// ════════════════════════════════════════════════════════════════
// 認証管理
// ════════════════════════════════════════════════════════════════
// トークンは sessionStorage 優先（タブを閉じると自動クリア）。
// 既存の localStorage トークンも引き続き読み込む（移行互換）。
// ログイン保持: localStorage に保存した fp_token_persist / fp_token_expire / fp_keep_login
// - fp_keep_login: 既定ON。明示的に "0"（チェックを外した）ときのみOFF。未設定/"1" はON。
//   （メール通知のTODOリンクは新規タブで開き sessionStorage が空 → 保持ONでないと再ログインになるため既定ON）
// - expire = 最終アクティビティから7日間（ローリング）
const FP_TOKEN_PERSIST_KEY = "fp_token_persist";
const FP_TOKEN_EXPIRE_KEY  = "fp_token_expire";
const FP_KEEP_LOGIN_KEY    = "fp_keep_login";
const FP_SESSION_DAYS      = 7;
/** ログイン保持が有効かどうか（既定ON。明示的に "0" のときだけOFF） */
function fpKeepLoginEnabled() {
  try { return localStorage.getItem(FP_KEEP_LOGIN_KEY) !== "0"; } catch { return true; }
}
function fpReadPersistedToken() {
  try {
    if (!fpKeepLoginEnabled()) return "";
    const t = localStorage.getItem(FP_TOKEN_PERSIST_KEY);
    const exp = Number(localStorage.getItem(FP_TOKEN_EXPIRE_KEY) || 0);
    if (!t || !exp) return "";
    if (Date.now() > exp) {
      fpClearPersistedToken();
      return "";
    }
    return t;
  } catch { return ""; }
}
/** トークンを7日ローリングで保存（ログイン保持ON時のみ） */
function fpPersistToken(token) {
  if (!fpKeepLoginEnabled()) return;
  try {
    const exp = Date.now() + FP_SESSION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(FP_TOKEN_PERSIST_KEY, token);
    localStorage.setItem(FP_TOKEN_EXPIRE_KEY, String(exp));
  } catch { /* noop */ }
}
/** 有効期限をリセット（ハートビート時に呼ぶ） */
function fpRollPersistedToken() {
  if (!fpKeepLoginEnabled()) return;
  try {
    const t = localStorage.getItem(FP_TOKEN_PERSIST_KEY);
    if (!t) return;
    const exp = Date.now() + FP_SESSION_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(FP_TOKEN_EXPIRE_KEY, String(exp));
  } catch { /* noop */ }
}
// 保持トークン本体のみ破棄。保持ON/OFFの選好（FP_KEEP_LOGIN_KEY）はログアウト後も引き継ぐ。
function fpClearPersistedToken() {
  try {
    localStorage.removeItem(FP_TOKEN_PERSIST_KEY);
    localStorage.removeItem(FP_TOKEN_EXPIRE_KEY);
  } catch { /* noop */ }
}
/** TenantHub「管理画面へ」からのSSOハンドオフ（#fp_sso=<token>）。
 *  TenantHubは別ドメイン(ob.digitaleyes.tech)で sessionStorage を共有できないため、
 *  Googleログインのコールバックと同じくURLハッシュで受け取る。読んだら即ハッシュを消し、
 *  トークンがアドレスバー・履歴・ブックマークに残らないようにする（保持トークンにはしない）。
 *  誰のトークンかを確かめてから受け入れる。以前は無条件に採用していたため、社員が自分のトークンを埋めた
 *  リンクを同僚に開かせると、同僚がその社員として操作してしまい監査ログの操作者もすり替わった。 */
function fpConsumeSsoTokenFromHash() {
  try {
    const m = String(location.hash || "").match(/^#fp_sso=([^&]+)$/);
    if (!m) return "";
    history.replaceState(null, "", `${location.pathname}${location.search}#dashboard`);
    const token = decodeURIComponent(m[1]);
    const incoming = fpJwtPayload(token);
    if (!incoming) return "";
    const current = fpJwtPayload(sessionStorage.getItem("fp_token") || fpReadPersistedToken() || "");
    const label = (p) => `${p.name || ""}（${p.email || "メール不明"}）`;
    if (current && String(current.userId) === String(incoming.userId)) return ""; // 同じ人でログイン中ならそのまま
    const msg = current
      ? `いま ${label(current)} でログインしています。TenantHub から ${label(incoming)} に切り替えますか？`
      : `TenantHub から ${label(incoming)} として Flow Pilot にログインします。よろしいですか？`;
    if (!window.confirm(`${msg}\n心当たりがない場合は「キャンセル」してください。`)) return "";
    sessionStorage.setItem("fp_token", token);
    return token;
  } catch { return ""; }
}
/** JWT の中身（署名は検証しない＝表示・比較用。正当性はサーバーが毎回検証する）。読めなければ null */
function fpJwtPayload(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0));
    const p = JSON.parse(new TextDecoder().decode(bytes));
    return p && typeof p === "object" && p.userId != null ? p : null;
  } catch { return null; }
}
let authToken = fpConsumeSsoTokenFromHash() || sessionStorage.getItem("fp_token") || fpReadPersistedToken() || "";
let authUser = null;
const LEGACY_DUMMY_DEPT_IDS = new Set(["dept-sales-hq", "dept-sales-1", "dept-admin"]);
const IS_LOCAL_DEBUG_HOST = location.hostname === "localhost" || location.hostname === "127.0.0.1";
const originalFetch = window.fetch.bind(window);

/** API 呼び出しの共通ヘルパー。**新規の API 呼び出しはこれを使う**（生 fetch を書かない）。
 *  認証ヘッダ付与・JSONボディ化・res.ok 判定・JSONパース・通信例外の吸収を一箇所にまとめ、
 *  「失敗が握りつぶされて成功トーストだけ出る」事故を構造的に防ぐ。
 *  戻り値: { ok, status, data } — ok=false は HTTPエラー or 通信エラー（status=0）。
 *  失敗時のトースト・巻き戻しは画面ごとの文脈があるため呼び出し側で行う。
 *  既存の生 fetch 呼び出しは動くまま残す（触るときにこちらへ寄せる）。 */
/** クライアント側で作る一意ID（原価取込の runId など）。crypto.randomUUID が無い環境は時刻＋乱数で代用 */
function newClientUuid() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function apiFetch(path, { method = "GET", body } = {}) {
  try {
    const res = await fetch(path, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: {} };
  }
}

/** 認証つきでファイルをダウンロードする（JSON でない応答用。apiFetch と同じく認証ヘッダを付けて res.ok を確かめる）。
 *  ファイル名は Content-Disposition の filename* を優先し、無ければ fallbackName。失敗は Error を投げる（文言はサーバの error） */
async function apiDownloadFile(path, fallbackName) {
  if (!authToken) throw new Error("ログインが必要です");
  const res = await fetch(path, { headers: { Authorization: `Bearer ${authToken}` } });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(typeof j.error === "string" ? j.error : "ファイルの取得に失敗しました");
  }
  const blob = await res.blob();
  const m = /filename\*=UTF-8''([^;]+)/i.exec(res.headers.get("Content-Disposition") || "");
  let name = fallbackName;
  if (m) {
    try {
      name = decodeURIComponent(m[1].trim());
    } catch {
      /* noop */
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 描画中に呼ぶ「未読込なら取得」を key ごとに1本へまとめる（読込完了まで render のたびに同じ GET を
 *  並列で撃たない）。保存後の明示的な再取得はこれを通さず直接呼ぶ（進行中の古い取得に吸収させない） */
const _renderFetchInFlight = new Set();
function fetchOnceInFlight(key, fn) {
  if (_renderFetchInFlight.has(key)) return;
  _renderFetchInFlight.add(key);
  Promise.resolve()
    .then(fn)
    .catch(() => {})
    .finally(() => _renderFetchInFlight.delete(key));
}
// ────────────────────────────────────────────────────────────────
// フロント／サーバ共有の列挙定数（単一定義元）
//
// 背景: 選択肢・許可リストがフロント(app-src)とサーバ(api)に二重定義されていると、
// 片方だけ直した時にサーバの normalize* が未知値を既定値へ丸める
// （実例: 職種「情シス」をフロントだけに足して「営業」へ化けた）。
// このファイルに一本化し、値の追加・変更・削除は必ずここだけで行う。
//
// 仕組み:
//   - ブラウザ: app-src/*.js の名前順連結で app.js の先頭側に入る（00-auth の次）。
//     ここで宣言した const はそのまま全フロントコードから参照できる。
//   - サーバ: 末尾の module.exports ガード経由で api/ から require する
//     （例: require('../app-src/00-shared-enums')）。サーバ側の別名
//     （JOB_ROLES / DOMAIN_STATUSES 等）は exports 側でマッピングする。
//
// 注意:
//   - 配列を in-place 変更（push/sort 等）しないこと。両環境で同一オブジェクトを
//     参照する前提なので、加工が要る場合はスプレッドでコピーしてから行う。
//   - 値だけの共有に限定する。normalize*/resolve* 関数はフォールバック既定値が
//     環境ごとに異なるため各側に置いたまま（この定数を参照させる）。
//     例外は環境依存の既定値を持たない純関数（帳票の空白行数・請求明細の金額計算とハッシュ・営業日）で、
//     両側で同じ結果でなければならないものはここに置く。
// ────────────────────────────────────────────────────────────────

/** システム権限（セキュリティ）。サーバ別名: SYSTEM_ROLES */
const SYSTEM_ROLE_OPTIONS = ["マスター", "管理者", "一般"];
/** 職種（業務ラベル・デフォルトメニュー初期値）。サーバ別名: JOB_ROLES */
const JOB_ROLE_OPTIONS = ["営業", "CS", "経理", "メディア", "クリエイティブ", "情シス", "開発", "その他"];
/** 案件のカテゴリ（基本情報「カテゴリ」・複数選択可。カテゴリごとに担当者を持つ）。サーバ別名: PROJECT_WORK_CATEGORIES */
const PROJECT_WORK_CATEGORY_OPTIONS = ["媒体", "制作", "システム", "その他"];

/** 全ナビキー（マスター/管理者の既定＝全部） */
const ALL_NAV_KEYS = [
  "dashboard",
  "progress-management",
  "workload",
  "projects",
  "development",
  "allowed-tools",
  "client-list",
  "partner-list",
  "cost-allocation",
  "budget-progress",
  "staff-assignment",
  "contract-management",
  "cost-management",
  "invoice-receipt",
  "application-management",
  "domain-list",
  "budget-admin",
  "account-admin"
];

/** 営業ロールのデフォルト nav 権限 */
const DEFAULT_SALES_NAV_KEYS = [
  "dashboard",
  "projects",
  "development",
  "allowed-tools",
  "client-list",
  "partner-list",
  "cost-allocation",
  "budget-progress",
  "progress-management",
  "workload"
];

/** 営業以外の一般ロールのデフォルト nav 権限 */
const DEFAULT_BASE_NAV_KEYS = ["dashboard", "development", "allowed-tools", "budget-progress", "progress-management", "workload"];

/** ドメイン管理: 状況（ステータス）。旧「停止」は廃止。サーバ別名: DOMAIN_STATUSES */
const DOMAIN_STATUS_OPTIONS = ["取得申請", "契約", "契約確認中", "契約更新可", "解約申請有", "解約"];

/** 課題（旧 ToDo）のステータス列 — ボードの列見出しに使用 */
const PM_TODO_STATUSES = ["未対応", "対応中", "完了", "保留", "対応不要"];
/** 制作・PJ進行テーマの既定ステータス列（工程）。テーマがカスタム状況を保存していない場合の
 * フォールバックとしてフロント・サーバ双方で使う（工程順の判定＝差し戻し検知・必須工程チェックの基準）。 */
const PM_SEISAKU_STATUSES = ["未対応", "ディレクター", "デザイナー", "コーダー", "完了", "保留", "対応不要"];
/** 課題の優先度 */
const PM_AGENDA_PRIORITIES = ["低", "中", "高", "最優先"];
/** ToDoグループ（プロジェクト）の分類。空 "" = 未分類。サーバ別名: TODO_GROUP_CATEGORIES */
const PM_TODO_GROUP_CATEGORY_OPTIONS = ["開発", "依頼", "制作", "その他"];
/** 上の分類の画面表示ラベル。保存値（DB・issues-to-tasks の --genre=開発 等）は従来のまま、表示だけ変える。
 *  サーバの文言（api/task-md.js 等）も同じラベルを使う */
const PM_TODO_GROUP_CATEGORY_LABELS = { "開発": "自社対応", "制作": "制作・PJ進行" };
/** タスクMD（tasks/*.md）を作るプロジェクトの種別。毎朝の自動生成（issues-to-tasks.yml の既定）と課題ボードの「タスクMD」ボタンが対象にする */
const PM_TASK_MD_GENRE = "開発";
/** 課題コメントへのリアクション絵文字（Slack風）。フロントのパレット表示と
 *  サーバの許可判定（toggleMessageReaction）が同じ一覧を使う。追加・変更はここだけ。 */
const PM_REACTION_EMOJIS = ["👍", "👀", "🙇", "❤️", "✅", "🎉", "😂", "🤔"];
/** 完了からこの日数が経過した（更新のない）課題を自動アーカイブする。フロントは案内文言、サーバは実処理で使用 */
const PM_DONE_AUTO_ARCHIVE_DAYS = 30;

/** 案件外（社内業務）時間の種別。サーバは未知値を既定へ丸めず 400 で弾く */
const INTERNAL_WORK_CATEGORIES = ["社内会議", "営業活動", "採用", "その他"];

/** 開発ページ: 自社ツール（own）の状況 */
const DEV_STATUS_OPTIONS = ["要件定義", "開発中", "社内利用", "販売中", "販売停止"];
/** 開発ページ: 受託開発（contract）の状況 */
const DEV_CONTRACT_STATUS_OPTIONS = ["提案中", "要件定義", "開発中", "リリース済"];
/** バッジ表示・正規化用の全許可値（両方の和集合） */
const DEV_ALL_STATUS_OPTIONS = [...new Set([...DEV_STATUS_OPTIONS, ...DEV_CONTRACT_STATUS_OPTIONS])];

/** 許可ツールの大区分。サーバ別名: CATEGORY_OPTIONS */
const ALLOWED_TOOL_CATEGORIES = ["AI", "開発ツール", "業務ツール", "その他"];
/** 旧区分名の後方互換（Claudeプラグイン→AI）。サーバ別名: CATEGORY_ALIASES */
const ALLOWED_TOOL_CATEGORY_ALIASES = { "Claudeプラグイン": "AI" };
/** 大区分ごとの小区分（列挙がある大区分だけ持つ）。サーバ別名: SUBCATEGORY_OPTIONS */
const ALLOWED_TOOL_SUBCATEGORIES = { AI: ["本体", "スキル", "プラグイン"] };

/** 書類テンプレート（設定 > 書類テンプレート）の書類種別・公開範囲・税率。
 *  画面（15z-doc-templates.js）と api/doc-templates.js の単一定義元 */
const DOC_TEMPLATE_DOC_TYPES = ["quote", "invoice", "order"];
const DOC_TEMPLATE_SCOPES = ["private", "shared"];
const DOC_TEMPLATE_TAX_RATES = [0.1, 0.08];

/** 捺印申請の対象書類（書類タブkey→表示名。表示順もこの並び）。
 *  フロント docTabHasSealWorkflow / docTabLabelForSealApplication と
 *  サーバ api/_docSeal.js・api/doc-seal-applications.js の対象判定の単一定義元。
 *  対象を増減する時はここだけを変える（承認フロー種別 seal:<key> も自動で追従する）。 */
const DOC_SEAL_TAB_LABELS = {
  quote: "見積書",
  order: "発注書",
  acceptance: "発注請書",
  delivery: "納品書",
  invoice: "請求書",
  receipt: "領収書"
};

/** 捺印申請の対象（DOC_SEAL_TAB_LABELS）のうち、未承認だと送付（メール/Slack/正式PDF発行）を
 *  実際にブロックする種別。2026-10 ユーザー要望で送付の制御をなくし、どの種別も未承認で送れる（空）。
 *  捺印申請の仕組み自体（申請・承認・承認済みなら社印付きで発行）は残す＝社印は承認済みの書類にだけ刷られる。
 *  フロント docTabBlocksSendWhenUnapproved とサーバ api/_docSeal.js の単一定義元。 */
const DOC_SEAL_SEND_BLOCK_TYPES = [];

/** 指定書類が「未承認だと送付できない」対象か。 */
function docTypeBlocksSendWhenUnapproved(docType) {
  return DOC_SEAL_SEND_BLOCK_TYPES.includes(String(docType || ""));
}

/** 会社印（社印）を刷る書類種別。捺印申請の対象（DOC_SEAL_TAB_LABELS）とは別集合で、
 *  発注書・検収書は宛名＝自社・右欄＝取引先の「印」欄という向き（DOC_CONF の right:"client"）の
 *  ため自社印を載せる場所がない。
 *  画面プレビュー（07-pdf-output.js）と正式PDF・メール添付（api/_docRender.js 経由）が
 *  同じ判定を使う単一定義元。片側だけ変えると「画面では押印されているのにPDFでは出ない」になる。 */
const DOC_SEAL_STAMP_TYPES = ["quote", "acceptance", "delivery", "invoice", "receipt"];

/** 指定書類が社印を刷る種別か。実際に押印するかは別途「捺印申請が承認済」であることが条件。 */
function docTypeShowsSealStamp(docType) {
  return DOC_SEAL_STAMP_TYPES.includes(String(docType || ""));
}

/** 書類タブごとの既定備考 */
const DOC_REMARK_DEFAULTS = { invoice: "＊お振込手数料はご負担願います。" };

/** 書類の備考。そのタブで未入力（undefined）なら既定文言。
 *  画面（07-pdf-output.js）と正式PDF・送付（doc-pdf / _docSend / doc-mail）で同じ規則にする */
function resolveDocRemark(remarkMap, docType) {
  const map = remarkMap && typeof remarkMap === "object" ? remarkMap : {};
  const t = String(docType || "");
  return map[t] !== undefined ? String(map[t] ?? "") : DOC_REMARK_DEFAULTS[t] || "";
}

/** 書類画面の「他の書類に反映」で備考を上書きする先（board と同じ。見積書→他の書類すべて、請求書→領収書） */
const DOC_REMARK_REFLECT_TARGETS = {
  quote: ["order", "acceptance", "delivery", "inspection", "invoice", "receipt"],
  invoice: ["receipt"]
};

/** 反映先に入れる備考。既定文言のある書類（請求書）は、反映元に無ければ既定文言を下に残す */
function reflectedDocRemark(sourceRemark, targetType) {
  const src = String(sourceRemark ?? "").trim();
  const def = DOC_REMARK_DEFAULTS[String(targetType || "")] || "";
  return def && !src.includes(def) ? [src, def].filter(Boolean).join("\n") : src;
}

/** 社内申請（ツール・備品・開発・案件ロック解除）の状況。取引先の承認（下の CLIENT_APPROVAL_STATUS_OPTIONS）とは別の値。 */
const APPLICATION_STATUS_OPTIONS = ["申請中", "承認", "却下"];

/** ログイン・招待・パスワード再設定を許すメールのドメイン（サーバだけが使う） */
const ALLOWED_EMAIL_DOMAINS = ["o-eighty.com", "o-eighty.co.jp"];

/** 取引先（クライアント/パートナー）の取引承認ステータス。
 *  未申請（仮登録・コメントで確認中）→ 申請中（承認フローに掲載）→ 承認済（顧客番号を自動発番）／却下（理由つき差し戻し）。
 *  旧値「未承認」は「未申請」の別名として扱う（CLIENT_APPROVAL_LEGACY_MAP）。
 *  正規化関数はフォールバックが両側で同じでも方針どおり各側に置く（サーバ api/_clientApprovalStatus.js ↔ フロント normalizeMasterApprovalStatus）。 */
const CLIENT_APPROVAL_STATUS_OPTIONS = ["未申請", "申請中", "承認済", "却下"];
const CLIENT_APPROVAL_LEGACY_MAP = { 未承認: "未申請" };

/** 原価のジャンル4択（付け先なしの「販管費」はジャンルでなく入れ先バケット） */
const COST_CATEGORIES = ["媒体費", "制作費", "開発費", "その他"];

/** 発注申請（外注先に頼む前の承認）が要る会計区分。制作費・開発費だけで、媒体費・その他（システム利用料など）は
 *  申請なしで登録できる（2026-10 ユーザー要望）。外注費は制作費の旧ラベル。
 *  フロント 15z-cost-order-applications.js とサーバ api/_costOrderApproval.js の単一定義元 */
const COST_ORDER_REQUIRED_CATEGORIES = ["制作費", "開発費", "外注費"];

function costCategoryNeedsOrderApproval(category) {
  return COST_ORDER_REQUIRED_CATEGORIES.includes(String(category ?? "").trim());
}

/** 原価の支払方法（空=未設定を許容） */
const COST_PAYMENT_METHODS = ["請求書", "カード", "口座振替", "その他"];

/** 源泉徴収区分（パートナーマスタの withholding_tax と同じ値。仮原価では区分を選び、源泉所得税は金額から計算する） */
const WITHHOLDING_TAX_KINDS = ["なし", "源泉徴収（復興特別所得税あり）", "源泉徴収（復興特別所得税なし）"];

/** 広告CSV取込（原価）の摘要末尾に付く媒体ラベル。
 *  摘要は「<アカウント名> <ラベル>」形式で保存されるため、この末尾一致で
 *  「同じアカウント（＝同じ案件に紐づく予定）の原価」を後からグループ化できる。
 *  フロント: 取込時の摘要組み立て（COST_IMPORT_CSV_SOURCE_META）と原価割当のグループ化。
 *  サーバ: 摘要の逆引きパース（予想案件の記憶・前月の案件から作成）。
 *  ラベル文言を変えると過去に取り込んだ原価の摘要とズレてグループ化から漏れるため変更禁止
 *  （変えたい場合は旧ラベルも残して後方互換にする） */
const AD_CSV_NOTE_LABELS = {
  google: "Google広告",
  ydn: "Yahooディスプレイ（YDN）",
  yss: "Yahoo検索（YSS）",
  line: "LINE広告",
  meta: "Meta広告"
};

/** 原価の発注ステータス（board の発注管理に準拠・空=未設定を許容）。
 *  当面はラベルのみで原価集計には影響しない（「見送り」も合計に含む）。
 *  未設定の表示は支払ステータスから推定する（フロント costOrderStatusDisplayLabel） */
const COST_ORDER_STATUSES = ["見積中(高)", "見積中(中)", "見積中(低)", "見積中(除)", "発注確定", "発注済", "見送り"];

/** 請求書メール受信箱の処理状態（DB値）。open=未処理 / check=要確認（AI読取不能・取引先未登録）/ done=登録済 / ignored=無視。
 *  表示ラベルはフロント INVOICE_MAIL_INBOX_STATUS_LABELS（15r-invoice-mail-inbox.js） */
const INVOICE_MAIL_INBOX_STATUSES = ["open", "check", "done", "ignored"];

/** カード決済チェックの「いつ登録できるか」の目安（DB値）。day=毎月N日に決済 / bizdays_after_month_end=月末からN営業日で請求書が出る / none=目安なし */
const CARD_COST_CHECK_RULE_TYPES = ["day", "bizdays_after_month_end", "none"];

/** A4帳票（請求書等）の明細テーブル本文（明細行＋空白行）が1ページに収まる上限行数。
 *  実測: 1行=約8.2mm・11行で印字可能高さ277mm（A4 297mm−@page余白10mm×2）を超える。
 *  空白行はこの上限まで埋め、明細だけでこれを超えるとPDFは2枚に分かれる（フロント/サーバ共用）。 */
const FORMAL_DOC_ONE_PAGE_BODY_ROWS = 10;
/** 明細が少ない時に合計欄の手前へ入れる空白行の最大本数（視覚的平衡用） */
const FORMAL_DOC_LINE_BLANK_PAD_MAX = 8;
/** A4帳票: 明細テーブルの本文行数（明細・「明細なし」・入力用ゴースト行込み）→合計欄手前の空白行本数。
 *  1ページ上限まで埋め、空白行が原因でPDFが2枚に分かれないようにする。フロント/サーバ共用の単一定義
 *  （片側だけ変えると画面プレビューと正式PDFの空白行数がズレるため、式はここ以外に書かない）。 */
function formalDocBlankPadCount(bodyRowCount) {
  const n = Number(bodyRowCount) || 0;
  return Math.min(FORMAL_DOC_LINE_BLANK_PAD_MAX, Math.max(0, FORMAL_DOC_ONE_PAGE_BODY_ROWS - n));
}

// ── 請求明細の金額計算（フロント/サーバ/運用スクリプト共用）────────────────
// 画面・正式PDF（api/_docRender.js）・照合スクリプト（scripts/_boardInvoiceLines.js）に式が
// コピーされていた結果、消費税の端数処理（画面=切り捨て／PDF=四捨五入）と数値の読み方がずれ、
// 顧客に届く金額と画面が1円違う・全角入力の行が0円になる、が起きていた。式はここ以外に書かない。

/** 明細の数量・単価の入力値→数値。全角は半角へ（NFKC）、桁区切り・¥・円・空白は無視、
 *  先頭の ▲ △ −（値引きの慣習表記）は負号とみなす。読めなければ NaN（呼び出し側で既定値を決める） */
function parseBillingNumber(v) {
  if (typeof v === "number") return v;
  const s = String(v ?? "").normalize("NFKC").replace(/[,\s¥円]/g, "").replace(/^[▲△−]/, "-");
  return parseFloat(s);
}

/** 請求明細・書類1行の税抜金額＝数量×単価。数量が空・不正なときのみ1。0 は 0×単価＝0。負の割合・小数数量も可。
 *  税抜行の円単位は端数切り捨て（負行も含めて一貫して floor） */
function billingLineAmountExTax(line) {
  if (!line) return 0;
  const qtyRaw = parseBillingNumber(line.qty);
  const price = parseBillingNumber(line.price) || 0;
  const qty = Number.isFinite(qtyRaw) ? qtyRaw : 1;
  const amt = qty * price;
  // 浮動小数の誤差（8.2×3000=24599.999…）で1円欠けないよう、極小の端数を丸めてから切り捨てる
  return Number.isFinite(amt) ? Math.floor(Math.round(amt * 1e6) / 1e6) : 0;
}

/** 請求書の消費税率（現状は10%のみ） */
const BILLING_TAX_RATE = 0.1;
/** 書類1枚の消費税額＝税抜合計×税率を1回だけ四捨五入（インボイス制度の「税率ごとに1回」）。
 *  顧客へ届く正式PDF・メール・郵送・発行履歴が以前からこの規則なので、画面側をこれに揃えた */
function billingTaxYen(subtotalExTax, taxRate = BILLING_TAX_RATE) {
  return Math.round((Number(subtotalExTax) || 0) * taxRate);
}

// ── 営業日（土日祝を除く平日）─────────────────────────────────────
// 案件ロック（解除3営業日・月次ロック初回シード）・REM取込日・稼働の業務時間換算が同じ定義を使う。
// 片側だけ変えると「画面では編集できるのに保存で403」やREM取込の404が起きるため、ここが唯一の定義元。

/** 祝日の基準判定（固定日・ハッピーマンデー・春分/秋分の日）。振替休日・国民の休日は含まない
 *  （isJapaneseHoliday 側で加味）。春分・秋分は近似式（1980〜2099年で有効・国立天文台の公表値と一致）。 */
function _jpNthMondayDate(year, month, nth) {
  const first = new Date(year, month - 1, 1);
  const offsetToMonday = (8 - first.getDay()) % 7;
  return 1 + offsetToMonday + (nth - 1) * 7;
}
const _JP_FIXED_HOLIDAYS = new Set(["1-1", "2-11", "2-23", "4-29", "5-3", "5-4", "5-5", "8-11", "11-3", "11-23"]);
function _jpFixedHoliday(date) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  if (_JP_FIXED_HOLIDAYS.has(`${m}-${d}`)) return true;
  if (m === 1 && d === _jpNthMondayDate(y, 1, 2)) return true; // 成人の日
  if (m === 7 && d === _jpNthMondayDate(y, 7, 3)) return true; // 海の日
  if (m === 9 && d === _jpNthMondayDate(y, 9, 3)) return true; // 敬老の日
  if (m === 10 && d === _jpNthMondayDate(y, 10, 2)) return true; // スポーツの日
  const shunbun = Math.floor(20.8431 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
  const shuubun = Math.floor(23.2488 + 0.242194 * (y - 1980) - Math.floor((y - 1980) / 4));
  if (m === 3 && d === shunbun) return true;
  if (m === 9 && d === shuubun) return true;
  return false;
}
/** 振替休日・国民の休日を加味した祝日判定（臨時の祝日は規則で計算できないため含まない） */
function isJapaneseHoliday(date) {
  if (_jpFixedHoliday(date)) return true;
  if (date.getDay() !== 0) {
    const prev = new Date(date); prev.setDate(date.getDate() - 1);
    const next = new Date(date); next.setDate(date.getDate() + 1);
    if (_jpFixedHoliday(prev) && _jpFixedHoliday(next)) return true; // 国民の休日
  }
  // 振替休日: 前日から連続する祝日を遡り、その起点が日曜日なら今日が振替休日
  const cursor = new Date(date);
  cursor.setDate(cursor.getDate() - 1);
  for (let guard = 0; guard < 10 && _jpFixedHoliday(cursor); guard++) {
    if (cursor.getDay() === 0) return true;
    cursor.setDate(cursor.getDate() - 1);
  }
  return false;
}

/** 営業日か（土日祝を除く平日） */
function isBusinessDay(date) {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) return false;
  return !isJapaneseHoliday(date);
}

/** 月の第N営業日（monthIndex は 0 始まり。12 を渡せば翌年1月として解釈される）。
 *  n が不正（NaN/0以下）でも無限ループしないよう 1 に丸め、反復にも上限を置く。 */
function getNthBusinessDayOfMonth(year, monthIndex, n) {
  const target = Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
  let count = 0;
  const d = new Date(year, monthIndex, 1);
  for (let guard = 0; guard < 366; guard++) {
    if (isBusinessDay(d)) {
      count++;
      if (count >= target) break;
    }
    d.setDate(d.getDate() + 1);
  }
  return new Date(d);
}

/** 原価の発注の証跡（2026-10-30 要望）の期限: 計上月の翌月の第5営業日（原価箱の締めアラートと同じ基準）。
 *  計上月は 'YYYY/M'・'YYYY-MM' のどちらでも読む。読めなければ ''。返り値は 'YYYY-MM-DD' */
const ORDER_EVIDENCE_DUE_BUSINESS_DAY = 5;
/** 証跡の対象は、この日時以降に発注申請が承認された原価だけ（それより前の承認分は証跡待ち・期限切れを出さない。2026-10-30 ユーザー決定） */
const ORDER_EVIDENCE_RULE_START = "2026-11-01T00:00:00+09:00";
function orderEvidenceDueDate(targetMonth) {
  const m = String(targetMonth || "").match(/^(\d{4})[/-](\d{1,2})/);
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return "";
  // monthIndex は 0 始まりなので、計上月の数字をそのまま渡すと翌月になる（12月なら翌年1月）
  const d = getNthBusinessDayOfMonth(Number(m[1]), Number(m[2]), ORDER_EVIDENCE_DUE_BUSINESS_DAY);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 在席まわりは2軸。混ぜると「手動と自動が同じ値を奪い合う」ので必ず別々に持つ。
 *  ① 稼働状態（自動）… PC の操作から判定する。自己申告できない。DB には保存せず読み出し時に計算する
 *  ② ステータス（自己申告）… 自由入力の短い文（Slack と同じ。プリセットは持たない）。
 *     稼働状態とは独立に併存する（「会議中」と書いていても操作していれば稼働中）。
 *  値を足すのはこのファイルだけ。サーバは require('../app-src/00-shared-enums') で同じ定義を読む。 */
const USER_ACTIVITY_STATES = ["稼働中", "離席中", "オフライン"];

/** ワンタッチで付けられる自己申告ステータス。左下メニューに並ぶ（押すと付く／もう一度押すと外れる）。
 *  それ以外は自由入力。「通勤途中」のような既定一覧は置かない（2026-09-09 ユーザー指示・打合せ中だけ残す。
 *  文言は「会議中」から「打合せ中」へ同日変更）。 */
const PRESENCE_QUICK_STATUSES = ["打合せ中"];

/** 在席一覧（全員の在席状況）に出さないアカウントの氏名。検証用アカウントが席に並ぶのを避ける
 *  （2026-09-09 ユーザー指示）。空白の全角/半角・連続は無視して比較する（presenceRosterHiddenName）。
 *  担当者プルダウン等には影響しない＝在席一覧だけの除外。 */
const PRESENCE_ROSTER_HIDDEN_NAMES = ["テスト テスト"];

/** 在席一覧から隠す氏名かどうか。空白の揺れ（全角・連続・前後）を吸収して比較する。 */
function presenceRosterHiddenName(name) {
  const norm = (s) => String(s || "").replace(/[\s　]+/g, " ").trim();
  const n = norm(name);
  return !!n && PRESENCE_ROSTER_HIDDEN_NAMES.some((h) => norm(h) === n);
}

/** 「ステータスを設定」モーダルのテンプレ（Slack と同じ並び・2026-09-09 ユーザー指示）。
 *  period: "1h" = 1時間後に自動で消える / "range" = 開始日・終了日を指定 / "none" = 期限なし。
 *  押すと入力欄に「絵文字＋文言」が入り、期間の初期値が選ばれる。文言は編集してよい。 */
const PRESENCE_STATUS_TEMPLATES = [
  { key: "meeting", emoji: "📅", label: "会議中", period: "1h", note: "1時間" },
  { key: "commute", emoji: "🚃", label: "通勤中", period: "1h", note: "1時間" },
  { key: "vacation", emoji: "🌴", label: "休暇中", period: "range", note: "開始・終了日を設定" },
  { key: "remote", emoji: "🏠", label: "リモート作業", period: "none", note: "" }
];

/** 自己申告ステータスの表示可否。開始前・期限切れは「ステータスなし」として扱う。
 *  DB に状態遷移を持たず読み出し時に判定する（cron 不要）。フロント（自分の表示）とサーバ（配信）で共用。
 *  @param {number|null} fromMs 表示開始（epoch ms・null=即時）
 *  @param {number|null} untilMs 表示終了（epoch ms・null=期限なし）
 *  @param {number} nowMs */
function presenceStatusVisible(fromMs, untilMs, nowMs) {
  const f = Number(fromMs) || 0;
  const u = Number(untilMs) || 0;
  if (f && nowMs < f) return false;
  if (u && nowMs > u) return false;
  return true;
}

/** 自己申告ステータスの上限文字数。入力欄の maxlength（app-src/15o-presence.js）と
 *  サーバの保存検証（api/heartbeat.js）で同じ値を使う。全員のカードの1行に収まる長さ。 */
const PRESENCE_STATUS_MAX_LEN = 40;

/** 自己申告ステータスの正規化。改行・制御文字は空白に、連続空白は1つに、前後の空白は落とし、上限で切る。
 *  フロントとサーバで同じ関数を使う（片側だけ緩いと、画面で弾いた値が API 直叩きで入る）。
 *  空なら "" を返す＝「ステータスなし」。 */
function normalizePresenceStatusText(raw) {
  if (raw === undefined || raw === null) return "";
  const s = String(raw)
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s.length > PRESENCE_STATUS_MAX_LEN ? s.slice(0, PRESENCE_STATUS_MAX_LEN) : s;
}

/** 最後の操作からこの分数を超えたら「離席中」。ハートビート間隔より短くてよい
 *  （クライアントが最終操作時刻そのものを送るので、間隔で精度が落ちない）。 */
const PRESENCE_AWAY_MINUTES = 10;
/** ハートビートがこの分数途絶えたら「オフライン」。既存のログイン状況バッジ
 *  （app-src/08-login-status-log.js の「🟢アクセス中＝15分未満」）と同じ基準。 */
const PRESENCE_OFFLINE_MINUTES = 15;

/** 稼働状態を計算する。フロント（受信した値の再計算）とサーバ（配信時）で同じ判定を使う。
 *  @param {number|null} lastSeenMs 最後にハートビートが届いた時刻
 *  @param {number|null} lastInputMs 最後に操作した時刻
 *  @param {number} nowMs 現在時刻 */
function resolveUserActivityState(lastSeenMs, lastInputMs, nowMs) {
  const seen = Number(lastSeenMs) || 0;
  if (!seen || nowMs - seen > PRESENCE_OFFLINE_MINUTES * 60 * 1000) return "オフライン";
  const input = Number(lastInputMs) || 0;
  if (!input || nowMs - input > PRESENCE_AWAY_MINUTES * 60 * 1000) return "離席中";
  return "稼働中";
}

/** アカウント設定のプロフィール項目の保存上限。
 *  入力欄の maxlength（app-src/11-cost-form.js）とサーバの保存検証（api/me.js）で同じ値を使う。 */
const MY_ACCOUNT_PROFILE_LIMITS = { displayName: 100, comment: 500, phone: 50 };

/** 電話番号として妥当か（未入力は可）。日本の固定/携帯（0始まり10〜11桁）と、
 *  +国番号 で始まる国際表記（8〜15桁）を許す。ハイフン・空白の有無は問わない。
 *  フロントの入力チェックとサーバの保存検証が同じ判定を使うため、ここに単一定義する
 *  （両側に置くと片方だけ緩くなり、画面で弾いた値が API 直叩きで入る）。 */
function isValidMyAccountPhone(value) {
  const s = String(value || "").trim();
  if (!s) return true;
  const noSpace = s.replace(/[\s　]/g, "");
  if (noSpace.startsWith("+")) {
    const digits = noSpace.slice(1).replace(/\D/g, "");
    return digits.length >= 8 && digits.length <= 15 && /^[1-9]/.test(digits);
  }
  const digitsOnly = noSpace.replace(/\D/g, "");
  if (digitsOnly.length < 10 || digitsOnly.length > 11) return false;
  if (!digitsOnly.startsWith("0")) return false;
  return /^0[1-9]\d+$/.test(digitsOnly);
}

/** 貸与品管理（左メニュー「貸与品」）: PC の OS。サーバ別名: EQUIPMENT_PC_OS */
const EQUIPMENT_PC_OS_OPTIONS = ["Mac", "Windows"];
/** 備品の貸与申請（社内申請）: 品目。サーバ別名: EQUIPMENT_ITEM_TYPES */
const EQUIPMENT_ITEM_TYPE_OPTIONS = ["PC", "携帯電話", "ENセキュリティーカード", "その他"];

// ── 請求項目の楽観的排他用ハッシュ ─────────────────────────────────
// 画面が最後にサーバーから受け取った請求項目（billing_lines_json）のハッシュを保存時に送り、
// サーバー（api/_billingLinesConflict.js）が現在値のハッシュと比べて食い違えば 409 で弾く。
// jsonb はオブジェクトのキー順を並べ替えて返すため、両側でキーを再帰的にソートした正規形で比べる。
// 片側だけ式を変えると常に「他のユーザーが更新しました」になるため、ここ以外に書かない。

/** 409（請求項目の競合）の文言。サーバーの応答と、画面が送信を止めたときのトーストで共用 */
const BILLING_LINES_CONFLICT_MESSAGE = "他のユーザーが請求項目を更新しました。再読み込みしてから編集してください";

/** JSON の正規形（キーを再帰的にソート）。先に JSON 往復して undefined・NaN・Date 等を JSON と同じ扱いにそろえる */
function canonicalJsonString(value) {
  const json = JSON.stringify(value);
  if (json === undefined) return "null";
  const walk = (v) => {
    if (Array.isArray(v)) return `[${v.map(walk).join(",")}]`;
    if (v && typeof v === "object") {
      return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${walk(v[k])}`).join(",")}}`;
    }
    return JSON.stringify(v);
  };
  return walk(JSON.parse(json));
}

/** FNV-1a 32bit（UTF-16 コード単位）→ 8桁16進 */
function fnv1a32Hex(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** 請求項目のハッシュ。DB の生値（配列・JSON文字列・null）も画面の配列も同じ値になる。配列以外は空配列扱い */
function billingLinesHash(raw) {
  let v = raw;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      v = null;
    }
  }
  return fnv1a32Hex(canonicalJsonString(Array.isArray(v) ? v : []));
}

// サーバ（Node/require）向けエクスポート。ブラウザでは typeof module が undefined なので素通り。
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    MY_ACCOUNT_PROFILE_LIMITS,
    isValidMyAccountPhone,
    USER_ACTIVITY_STATES,
    PRESENCE_QUICK_STATUSES,
    PRESENCE_STATUS_TEMPLATES,
    presenceStatusVisible,
    PRESENCE_ROSTER_HIDDEN_NAMES,
    presenceRosterHiddenName,
    PRESENCE_STATUS_MAX_LEN,
    normalizePresenceStatusText,
    PRESENCE_AWAY_MINUTES,
    PRESENCE_OFFLINE_MINUTES,
    resolveUserActivityState,
    SYSTEM_ROLES: SYSTEM_ROLE_OPTIONS,
    JOB_ROLES: JOB_ROLE_OPTIONS,
    PROJECT_WORK_CATEGORIES: PROJECT_WORK_CATEGORY_OPTIONS,
    ALL_NAV_KEYS,
    DEFAULT_SALES_NAV_KEYS,
    DEFAULT_BASE_NAV_KEYS,
    DOMAIN_STATUSES: DOMAIN_STATUS_OPTIONS,
    EQUIPMENT_PC_OS: EQUIPMENT_PC_OS_OPTIONS,
    EQUIPMENT_ITEM_TYPES: EQUIPMENT_ITEM_TYPE_OPTIONS,
    PM_TODO_STATUSES,
    PM_SEISAKU_STATUSES,
    PM_AGENDA_PRIORITIES,
    TODO_GROUP_CATEGORIES: PM_TODO_GROUP_CATEGORY_OPTIONS,
    PM_TASK_MD_GENRE,
    PM_TODO_GROUP_CATEGORY_LABELS,
    PM_REACTION_EMOJIS,
    PM_DONE_AUTO_ARCHIVE_DAYS,
    INTERNAL_WORK_CATEGORIES,
    DEV_STATUS_OPTIONS,
    DEV_CONTRACT_STATUS_OPTIONS,
    DEV_ALL_STATUS_OPTIONS,
    CATEGORY_OPTIONS: ALLOWED_TOOL_CATEGORIES,
    CATEGORY_ALIASES: ALLOWED_TOOL_CATEGORY_ALIASES,
    SUBCATEGORY_OPTIONS: ALLOWED_TOOL_SUBCATEGORIES,
    DOC_SEAL_TAB_LABELS,
    DOC_TEMPLATE_DOC_TYPES,
    DOC_TEMPLATE_SCOPES,
    DOC_TEMPLATE_TAX_RATES,
    DOC_SEAL_STAMP_TYPES,
    docTypeShowsSealStamp,
    resolveDocRemark,
    DOC_REMARK_REFLECT_TARGETS,
    reflectedDocRemark,
    DOC_SEAL_SEND_BLOCK_TYPES,
    docTypeBlocksSendWhenUnapproved,
    APPLICATION_STATUSES: APPLICATION_STATUS_OPTIONS,
    ALLOWED_EMAIL_DOMAINS,
    CLIENT_APPROVAL_STATUSES: CLIENT_APPROVAL_STATUS_OPTIONS,
    CLIENT_APPROVAL_LEGACY_MAP,
    COST_CATEGORIES,
    COST_ORDER_REQUIRED_CATEGORIES,
    costCategoryNeedsOrderApproval,
    COST_PAYMENT_METHODS,
    COST_ORDER_STATUSES,
    INVOICE_MAIL_INBOX_STATUSES,
    CARD_COST_CHECK_RULE_TYPES,
    AD_CSV_NOTE_LABELS,
    FORMAL_DOC_ONE_PAGE_BODY_ROWS,
    FORMAL_DOC_LINE_BLANK_PAD_MAX,
    formalDocBlankPadCount,
    parseBillingNumber,
    billingLineAmountExTax,
    BILLING_TAX_RATE,
    billingTaxYen,
    isJapaneseHoliday,
    isBusinessDay,
    getNthBusinessDayOfMonth,
    orderEvidenceDueDate,
    ORDER_EVIDENCE_RULE_START,
    canonicalJsonString,
    billingLinesHash,
    BILLING_LINES_CONFLICT_MESSAGE
  };
}
// 共有データは端末保存せず、常にサーバー基準で扱う
const DEPT_LOCAL_FALLBACK_KEY = "fp_dept_fallback_v1";
const approvalApproversMemoryStore = {};
const approvalOtherFlowsMemoryStore = [];
/** 申請種別ごとにどの申請フロー（申請者の所属フロー or 固定のフローID）を使うか。
 *  捺印対象の書類（見積書/発注書/発注請書/納品書/請求書/領収書）は運用上ずっと同じ承認先だった
 *  ため単一キー "seal:documents" に統合済み（旧: DOC_SEAL_TAB_LABELS から書類種別ごとに6個生成）。
 *  DOC_SEAL_TAB_LABELS 自体は他用途（社印表示・送付ブロック判定）で使うため変更しない。 */
const APPROVAL_APPLICATION_FLOW_KIND_META = [
  { key: "seal:documents", label: "請求書等の書類の捺印申請" },
  { key: "contract:client_partner", label: "契約（クライアント・パートナー）" },
  { key: "tool:permission", label: "ツールの許可申請" },
  { key: "project:unlock", label: "案件ロック解除申請" },
  { key: "development:application", label: "自社ツール開発の事前申請" },
  { key: "equipment:loan", label: "備品の貸与申請" },
  { key: "cost:order", label: "原価の発注申請（制作費・開発費）" }
];
const APPROVAL_FLOW_BINDING_DEPT_DEFAULT = "__dept__";
/** 捺印以外の申請種別（全員共通・承認者を種別ごとに直接設定する）。捺印申請だけが申請者（営業）の所属フローで回る */
const APPROVAL_SEAL_KIND_KEY = "seal:documents";
const APPROVAL_OTHER_KIND_META = APPROVAL_APPLICATION_FLOW_KIND_META.filter((m) => m.key !== APPROVAL_SEAL_KIND_KEY);
/** 種別ごとの承認者は approvalApprovers に "kind:<種別キー>" を ID とする暗黙のフローとして保存する
 *  （サーバ側の解決規則は固定フローと同じ＝ bindings[種別] → approvalApprovers[ID].steps）。 */
function approvalKindFlowId(kindKey) {
  return `kind:${String(kindKey || "")}`;
}
let budgetDraftMemoryStore = {};
/** 会計期ごとに「サーバーにある（と最後に確認した）内容」の JSON。保存は変わった期だけ送る
 *  （全期を送るとサーバーが無条件に上書きするため、他の管理者が別の期に入れた編集を古い内容で消していた） */
let budgetServerSnapshot = {};

function rememberBudgetServerPeriods(byPeriod) {
  if (!byPeriod || typeof byPeriod !== "object") return;
  Object.keys(byPeriod).forEach((id) => {
    if (id) budgetServerSnapshot[id] = JSON.stringify(byPeriod[id]);
  });
}
/** 予算マスタ全文のサーバー再起動間隔でバックグラウンド保存 */
let budgetMasterPersistTimer = null;
let budgetMasterRowsFetchPromises = {};

function schedulePersistBudgetMasterRowsToServer() {
  if (!authToken) return;
  clearTimeout(budgetMasterPersistTimer);
  budgetMasterPersistTimer = setTimeout(() => {
    persistBudgetMasterRowsToServer().then((ok) => {
      if (!ok) showToastError("予算データの保存に失敗しました（権限またはネットワークをご確認ください）");
    });
  }, 500);
}

async function persistBudgetMasterRowsToServer() {
  if (!authToken) return false;
  try {
    const store = readBudgetDraftStore();
    const changed = {};
    Object.keys(store).forEach((id) => {
      if (id && JSON.stringify(store[id]) !== budgetServerSnapshot[id]) changed[id] = store[id];
    });
    if (!Object.keys(changed).length) return true; // 送る変更なし
    const res = await fetch("/api/budget-master-rows", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ byPeriod: changed })
    });
    if (res.ok) rememberBudgetServerPeriods(changed);
    return !!res.ok;
  } catch {
    /* 通信失敗は黙認（再度編集時に宛先へ） */
    return false;
  }
}
const PAYMENT_TERMS_OPTIONS = [
  "月末締翌月末払",
  "月末締翌2ヶ月末払",
  "月末締当月末払",
  "月末締当月26日払",
  "月末締翌月10日払",
  "月末締翌月20日払",
  "月末締め翌25日払",
  "月末締翌々1日払",
  "月末締翌々月5日払",
  "10日締翌月10日払",
  "月末締翌々月10日",
  "月末締の翌々20日",
  "15日締め当月15日払",
  "1日締当月25日払",
  "20日締翌月10日",
  "20日締翌月20日払",
  "25日締翌月25日払",
  "カード翌々4日払",
  "カード翌々10日払",
  "カード払い15日締翌月10日",
  "カード払い翌月27日",
  "カード払い翌々月末",
  "月末締め翌27日払（口座振替）",
  "月末締め翌25日払（立替）"
];
/** headerAliases は旧・契約一覧「CSVダウンロード」形式（電話番号/振込手数料/送付方法/適格番号/登録日時）と
 *  board の顧客一覧エクスポート形式（顧客名/顧客略称名/市区町村・番地）の取込互換のため（現行DLは正式名で出力） */
const CLIENT_CSV_COLUMNS = [
  { header: "会社名", key: "name", fallback: "", headerAliases: ["顧客名"] },
  { header: "会社名（略）", key: "shortName", fallback: "", headerAliases: ["顧客略称名"] },
  { header: "顧客番号", key: "customerNo", fallback: "" },
  { header: "敬称", key: "honorific", fallback: "" },
  { header: "郵便番号", key: "postalCode", fallback: "" },
  { header: "都道府県", key: "prefecture", fallback: "東京都" },
  { header: "住所", key: "address", fallback: "", headerAliases: ["市区町村・番地"] },
  { header: "建物名", key: "building", fallback: "" },
  { header: "TEL", key: "tel", fallback: "", headerAliases: ["電話番号"] },
  { header: "FAX", key: "fax", fallback: "" },
  { header: "HP URL", key: "hpUrl", fallback: "" },
  { header: "担当者", key: "contactName", fallback: "" },
  { header: "メール", key: "email", fallback: "" },
  { header: "社内担当者", key: "contractOwner", fallback: "" },
  { header: "支払条件", key: "paymentTerms", fallback: "月末締翌月末払" },
  { header: "振込手数料負担", key: "transferFeeBurden", fallback: "先方", headerAliases: ["振込手数料"] },
  { header: "NDA", key: "ndaStatus", fallback: "未" },
  { header: "基本契約", key: "contractStatus", fallback: "未" },
  { header: "書類送付方法", key: "deliveryMethod", fallback: "メール(DL)", headerAliases: ["送付方法"] },
  { header: "備考", key: "notes", fallback: "" },
  { header: "初回登録日", key: "firstRegisteredAt", fallback: "", headerAliases: ["登録日時"] },
  { header: "法人番号", key: "corporateNumber", fallback: "" },
  { header: "適格請求書発行事業者", key: "invoiceIssuer", fallback: "未設定" },
  { header: "適格請求書発行事業者登録番号", key: "invoiceIssuerNumber", fallback: "", headerAliases: ["適格番号", "適格請求書発行事業者の登録番号"] }
];

/** ドメイン管理: 登録業者（旧「サービス」・単選択）の選択肢。値はそのまま保存（サーバ側ホワイトリストなし）。キーは service */
const DOMAIN_SERVICE_OPTIONS = ["お名前ドットコム", "さくらインターネット", "AWS", "その他"];

/** ドメイン管理: サービス（複数選択）の選択肢。"+" 連結で保存（書類送付方法と同方式）。キーは services。
 *  値はそのまま保存（サーバ側ホワイトリストなし）。表示・結合はこの順に固定。 */
const DOMAIN_SERVICE_KIND_OPTIONS = ["ドメイン", "サーバー", "メール", "SSL(有料)", "その他"];

// DOMAIN_STATUS_OPTIONS は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。

/** ドメイン一覧の既定の状況並び順（要対応・期限が迫るものを上位に）。一覧の既定ソートで使用。 */
const DOMAIN_STATUS_SORT_ORDER = ["取得申請", "契約更新可", "解約申請有", "契約確認中", "契約", "解約"];

/** 状況フィルタの初期表示（解約以外を表示）。 */
const DOMAIN_STATUS_FILTER_DEFAULT = DOMAIN_STATUS_OPTIONS.filter((s) => s !== "解約");

/** ドメイン管理 CSV（テンプレート／インポート／エクスポート共通） */
const DOMAIN_CSV_COLUMNS = [
  // 空欄は空のまま送る（新規はサーバーの normalizeStatus が「契約」に、既存は状況を変えない）。
  // 以前は「契約」で埋めて送っていたため、サーバーの「空欄なら既存値のまま」が効かず「契約確認中」が「契約」に戻っていた
  { header: "状況", key: "status", fallback: "" },
  { header: "登録業者", key: "service", fallback: "" },
  { header: "サブドメイン", key: "subdomain", fallback: "" },
  { header: "ドメイン", key: "domain", fallback: "" },
  { header: "サービス", key: "services", fallback: "" },
  { header: "ID", key: "registrarId", fallback: "" },
  { header: "パスワード", key: "registrarPassword", fallback: "" },
  { header: "クライアント", key: "clientName", fallback: "" },
  { header: "担当", key: "owner", fallback: "" },
  { header: "案件", key: "projectNote", fallback: "" },
  { header: "更新日", key: "renewedDate", fallback: "" },
  { header: "備考", key: "notes", fallback: "" }
];

/** 契約管理・クライアントの書類送付方法（複数は + で連結）。表示・結合はこの順に固定。 */
const CONTRACT_DELIVERY_METHOD_PARTS = ["メール(DL)", "郵送", "メール(添付)"];

const PARTNER_CSV_COLUMNS = [
  { header: "発注先名", key: "name", fallback: "", headerAliases: ["会社名"] },
  { header: "顧客番号", key: "customerNo", fallback: "" },
  // 「会社名（略）」は社名の略称であり支社ではないため alias 禁止（略称が支社に入り既存行と不一致→全行二重登録になった 2026-07 事故の原因）
  { header: "発注先支社", key: "branch", fallback: "" },
  { header: "敬称", key: "honorific", fallback: "" },
  { header: "発注先担当者", key: "contactName", fallback: "", headerAliases: ["担当者"] },
  { header: "社内担当者", key: "contractOwner", fallback: "", headerAliases: ["社内担当"] },
  { header: "メール", key: "contactEmail", fallback: "" },
  { header: "TEL", key: "contactTel", fallback: "", headerAliases: ["電話番号"] },
  { header: "FAX", key: "fax", fallback: "" },
  { header: "郵便番号", key: "postalCode", fallback: "" },
  { header: "都道府県", key: "prefecture", fallback: "" },
  { header: "住所", key: "address", fallback: "" },
  { header: "建物名", key: "building", fallback: "" },
  { header: "支払条件", key: "paymentTerms", fallback: "月末締翌月末払" },
  { header: "支払方法", key: "paymentMethod", fallback: "銀行振込" },
  { header: "振込手数料負担", key: "transferFeeBurden", fallback: "先方", headerAliases: ["振込手数料"] },
  { header: "書類送付方法", key: "deliveryMethod", fallback: "メール(DL)", headerAliases: ["送付方法"] },
  { header: "振込先口座", key: "bankInfo", fallback: "" },
  { header: "源泉徴収区分", key: "withholdingTax", fallback: "なし" },
  { header: "発注区分1", key: "category1", fallback: "" },
  { header: "発注区分2", key: "category2", fallback: "" },
  { header: "発注区分3", key: "category3", fallback: "" },
  { header: "社内メモ", key: "notes", fallback: "" },
  { header: "初回登録日", key: "firstRegisteredAt", fallback: "", headerAliases: ["登録日時"] },
  { header: "法人番号", key: "corporateNumber", fallback: "" },
  { header: "適格請求書発行事業者", key: "invoiceIssuer", fallback: "未設定" },
  { header: "適格請求書発行事業者登録番号", key: "invoiceIssuerNumber", fallback: "", headerAliases: ["適格番号", "適格請求書発行事業者の登録番号"] },
  { header: "NDA", key: "ndaStatus", fallback: "未" },
  { header: "業務委託契約", key: "contractStatus", fallback: "未", headerAliases: ["基本契約"] }
];
/** プロジェクト一覧マスター向けCSV（旧6列・後方互換） */
const PROJECT_LEGACY_CSV_COLUMNS = [
  { header: "案件名", key: "name", fallback: "" },
  { header: "顧客名", key: "clientName", fallback: "" },
  { header: "金額", key: "amount", fallback: "0" },
  { header: "請求月", key: "billingMonth", fallback: "" },
  { header: "自社担当", key: "ownerName", fallback: "" },
  { header: "受注ステータス", key: "orderStatus", fallback: "見積もり" },
  { header: "進捗状況", key: "progressStatus", fallback: "未着手" },
  { header: "タグ", key: "tags", fallback: "" }
];
/** プロジェクト取り込み用（見出し行／通常／小計行を同一案件noで束ねる）テンプレートのヘッダ行 */
const PROJECT_WIDE_CSV_HEADERS = [
  "案件no",
  "案件名",
  "顧客名",
  "小計",
  "消費税",
  "源泉徴収税",
  "合計",
  "摘要",
  "数量",
  "単位",
  "単価",
  "金額",
  "見出し行",
  "小計行",
  "行の種類",
  "請求日",
  "支払期限",
  "支払条件",
  "受注ステータス",
  "進捗状況",
  "請求ステータス",
  "自社担当",
  "タグ"
];
const LOCAL_ENDPOINT_PATTERN = /^https?:\/\/(127\.0\.0\.1|localhost|0\.0\.0\.0|\[::1\])(?::\d+)?\//i;
/** CSV・マスタ突き合わせ用（全角スペース・連続空白・不可視文字を揃える） */
function normalizeCsvLookupWhitespace(s) {
  return String(s ?? "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
function parseCsvRow(row) {
  const cells = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === "\"") {
      if (inQuotes && row[i + 1] === "\"") {
        current += "\"";
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current);
  // 末尾の replace は toCsvCell が数式インジェクション対策で付けた先頭 ' を剥がす（出力側と**対**）。
  // 「' の直後がトリガー文字」のときだけ剥がすので、データ本来の先頭 '（'24年 等）は壊さない
  return cells.map((v) => String(v || "").trim().replace(/^'(?=[=+\-@\t\r])/, ""));
}
/** CSV本文を1レコード＝1要素に分割する（各要素は parseCsvRow に渡す）。
 *  引用符の内側の改行はレコードの区切りにしない。素の text.split(/\r?\n/) だと、備考に改行を含む行が
 *  途中で切れ、後半が「会社名だけの別レコード」として登録される（board の顧客一覧CSV取込で実害）。
 *  空行（カンマだけの行も含む）は除く。 */
function splitCsvRecords(text) {
  const records = [];
  let current = "";
  let inQuotes = false;
  const src = String(text ?? "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === "\"") inQuotes = !inQuotes;
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      records.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  records.push(current);
  return records.filter((r) => r.trim() && !/^[,\s]*$/.test(r));
}
function normalizeCsvHeaderLabel(label) {
  return String(label ?? "")
    .normalize("NFKC")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, "")
    .trim();
}
function buildCsvHeaderMap(headerCells) {
  const map = {};
  headerCells.forEach((h, i) => {
    const norm = normalizeCsvHeaderLabel(h);
    if (norm && map[norm] === undefined) map[norm] = i;
  });
  return map;
}

/** 列定義（header/headerAliases）に対応するCSV列番号。無ければ undefined（位置フォールバックはしない） */
function csvHeaderIndexForColumn(headerMap, colDef) {
  let idx = headerMap[normalizeCsvHeaderLabel(colDef.header)];
  if (idx === undefined && Array.isArray(colDef.headerAliases)) {
    for (const alt of colDef.headerAliases) {
      idx = headerMap[normalizeCsvHeaderLabel(alt)];
      if (idx !== undefined) break;
    }
  }
  return idx;
}
/** CSVの1セル（クライアント/パートナー共用）。ヘッダ未一致時は位置フォールバックしない（別形式CSVの誤投入で列ずれしないよう） */
function csvRawCellForColumn(cols, headerMap, colDef) {
  const idx = csvHeaderIndexForColumn(headerMap, colDef);
  return idx !== undefined ? cols[idx] : undefined;
}

function toCsvCell(value) {
  let text = String(value ?? "");
  // CSV数式インジェクション対策: Excel/Sheets は先頭が = + - @ タブ CR のセルを数式として
  // 解釈する。原価CSVの発注先名・メモは外部CSV取込（REM/board）由来の値も含むため
  // 「社内の人しか書き込めない」前提が成り立たない。先頭に ' を足して無害化する。
  // 見た目が数値のセルは対象外（-1000 を文字列化すると Excel で合計できなくなる）。
  // 付けた ' は取込側 parseCsvRow が**対で**剥がす（自社エクスポートCSVの再取込が業務にある）。
  // 副次効果: +81-3-1234-5678 のような電話番号も、これまで Excel が -6870 と計算していたのを防ぐ
  if (/^[=+\-@\t\r]/.test(text) && !/^[+-]?[\d,]+(\.\d+)?$/.test(text)) {
    text = `'${text}`;
  }
  if (text.includes(",") || text.includes("\"") || text.includes("\n")) {
    return `"${text.replace(/"/g, "\"\"")}"`;
  }
  return text;
}
function decodeCsvArrayBuffer(buffer) {
  const utf8 = new TextDecoder("utf-8").decode(buffer).replace(/^\uFEFF/, "");
  const utf8ReplacementCount = (utf8.match(/\uFFFD/g) || []).length;
  // UTF-8 デコードで破損文字がなければそのまま採用
  if (utf8ReplacementCount === 0) return utf8;
  try {
    const sjis = new TextDecoder("shift-jis").decode(buffer).replace(/^\uFEFF/, "");
    const sjisReplacementCount = (sjis.match(/\uFFFD/g) || []).length;
    // 破損文字が少ない方を採用（Excel CSV の文字化け対策）
    return sjisReplacementCount < utf8ReplacementCount ? sjis : utf8;
  } catch {
    return utf8;
  }
}
function readCsvFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("failed to read csv file"));
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(decodeCsvArrayBuffer(reader.result));
        return;
      }
      if (typeof reader.result === "string") {
        resolve(reader.result.replace(/^\uFEFF/, ""));
        return;
      }
      resolve("");
    };
    reader.readAsArrayBuffer(file);
  });
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function uploadCostSourcePdfForImport(pdfBase64, filename) {
  if (!authToken) return null;
  const res = await fetch("/api/cost-source-pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ pdfBase64, filename: String(filename || "invoice.pdf").slice(0, 240) })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : "取込PDFの保存に失敗しました");
  const id = data.id;
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) throw new Error("取込PDFの保存応答が不正です");
  return n;
}

async function downloadCostSourcePdfDocument(docId) {
  const id = Number(docId);
  if (!Number.isFinite(id) || id <= 0) return;
  try {
    await apiDownloadFile(`/api/cost-source-pdf?id=${encodeURIComponent(String(id))}`, "cost-source.pdf");
  } catch (e) {
    showToastError(`PDFのダウンロードに失敗しました：${e?.message || ""}`);
  }
}

const PM_ATTACH_MAX_BYTES = 15 * 1024 * 1024;
const PM_ATTACH_EXT_HINT = "PDF・画像（PNG/JPEG/GIF/WebP）・CSV・Excel（.xls/.xlsx）・Word（.doc/.docx）・PowerPoint（.ppt/.pptx）・ZIP・HTML（.html）・テキスト（.txt/.md）";

function progressAttachmentClientMeta(fileName) {
  const name = String(fileName || "").trim();
  const low = name.toLowerCase();
  const m = low.match(/\.([a-z0-9]+)$/);
  const ext = m ? `.${m[1]}` : "";
  // サーバ側の許可表（api/progress-attachment.js EXT_TO_MIME）と accept= 属性4箇所と同じ集合。追加時は全部同時に
  const allow = new Set([".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".csv", ".xls", ".xlsx", ".doc", ".docx", ".ppt", ".pptx", ".zip", ".html", ".htm", ".txt", ".md"]);
  if (!allow.has(ext)) return null;
  return { ext, safeName: name.slice(0, 240) };
}

function formatProgressAttachmentSize(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "—";
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  return `${(x / (1024 * 1024)).toFixed(1)} MB`;
}

function readLocalFileArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("ファイルの読み込みに失敗しました"));
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error("ファイルの読み込みに失敗しました"));
    };
    reader.readAsArrayBuffer(file);
  });
}

// Vercel サーバーレスの本文上限 4.5MB を越えないよう、これを超えるファイルは分割転送する。
// サーバ側の許容値（api/progress-attachment.js の CHUNK_MAX_BYTES / CHUNK_MAX_COUNT）と対応。変更時は両側を同時に。
const PM_ATTACH_TRANSFER_CHUNK_BYTES = 3 * 1024 * 1024;

async function pmAttachApiPost(payload) {
  const res = await fetch("/api/progress-attachment", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(payload)
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.error === "string" ? j.error : "添付の保存に失敗しました");
  return j;
}

async function uploadProgressBinaryAttachment({ agendaId, messageId, file }) {
  if (!authToken) throw new Error("ログインが必要です");
  if (!progressAttachmentClientMeta(file.name)) {
    throw new Error(`対応形式のみ: ${PM_ATTACH_EXT_HINT}（各最大15MB）`);
  }
  if (file.size > PM_ATTACH_MAX_BYTES) throw new Error("ファイルサイズは15MB以内にしてください");
  const target = {};
  const aid = agendaId != null ? Number(agendaId) : NaN;
  const mid = messageId != null ? Number(messageId) : NaN;
  if (Number.isFinite(mid) && mid >= 1) target.messageId = mid;
  else if (Number.isFinite(aid) && aid >= 1) target.agendaId = aid;
  else throw new Error("添付先が不正です");
  const buf = await readLocalFileArrayBuffer(file);
  const bytes = new Uint8Array(buf);
  if (bytes.length <= PM_ATTACH_TRANSFER_CHUNK_BYTES) {
    await pmAttachApiPost({ filename: file.name, fileBase64: arrayBufferToBase64(buf), ...target });
    return;
  }
  // 分割アップロード: チャンクを順に送り、最後に finalize で合体させる
  const uploadId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const totalChunks = Math.ceil(bytes.length / PM_ATTACH_TRANSFER_CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const part = bytes.subarray(i * PM_ATTACH_TRANSFER_CHUNK_BYTES, (i + 1) * PM_ATTACH_TRANSFER_CHUNK_BYTES);
    await pmAttachApiPost({ uploadId, chunkIndex: i, fileBase64: arrayBufferToBase64(part) });
  }
  await pmAttachApiPost({ uploadId, finalize: true, totalChunks, filename: file.name, ...target });
}

/** 添付を Blob として取得（4.5MBのレスポンス上限を越えるファイルは分割ダウンロードして結合） */
async function fetchProgressAttachmentBlobWithMeta(attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) throw new Error("添付が不正です");
  if (!authToken) throw new Error("ログインが必要です");
  const headers = { Authorization: `Bearer ${authToken}` };
  const fetchOk = async (extraQuery) => {
    const res = await fetch(`/api/progress-attachment?id=${encodeURIComponent(String(id))}${extraQuery}`, { headers });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(typeof j.error === "string" ? j.error : "ファイルの取得に失敗しました");
    }
    return res;
  };
  // 添付は不変なので、読込済み一覧 state にメタがあれば meta リクエストを省く
  const local = findProgressAttachmentById(id);
  let size = local ? Number(local.sizeBytes) : NaN;
  let filename = local ? String(local.filename || "").trim() : "";
  let type = local ? String(local.mimeType || "") : "";
  if (!Number.isFinite(size) || size < 0 || !filename) {
    const meta = await (await fetchOk("&meta=1")).json().catch(() => ({}));
    size = Number(meta.sizeBytes) || 0;
    filename = String(meta.filename || "download");
    type = String(meta.mimeType || "");
  }
  if (!type) type = "application/octet-stream";
  if (size <= PM_ATTACH_TRANSFER_CHUNK_BYTES) {
    const res = await fetchOk("");
    return { blob: await res.blob(), filename, mimeType: type };
  }
  const parts = [];
  for (let off = 0; off < size; off += PM_ATTACH_TRANSFER_CHUNK_BYTES) {
    parts.push(await (await fetchOk(`&offset=${off}&length=${PM_ATTACH_TRANSFER_CHUNK_BYTES}`)).arrayBuffer());
  }
  return { blob: new Blob(parts, { type }), filename, mimeType: type };
}

async function downloadProgressAttachment(attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id <= 0) return;
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  try {
    const { blob, filename } = await fetchProgressAttachmentBlobWithMeta(id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    showToast(err instanceof Error && err.message ? err.message : "ダウンロードに失敗しました");
  }
}

function revokeProgressAttachPreviewUrls() {
  const map = uiState.progressAttachPreviewUrls;
  if (!map || typeof map !== "object") return;
  for (const url of Object.values(map)) {
    if (typeof url === "string" && url.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* noop */
      }
    }
  }
  uiState.progressAttachPreviewUrls = {};
}

async function fetchProgressAttachmentObjectUrl(attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) throw new Error("添付が不正です");
  if (!authToken) throw new Error("ログインが必要です");
  const cache = uiState.progressAttachPreviewUrls || {};
  if (cache[String(id)]) return cache[String(id)];
  const { blob } = await fetchProgressAttachmentBlobWithMeta(id);
  const url = URL.createObjectURL(blob);
  if (!uiState.progressAttachPreviewUrls) uiState.progressAttachPreviewUrls = {};
  uiState.progressAttachPreviewUrls[String(id)] = url;
  return url;
}

function progressAttachmentIsImage(att) {
  const mime = String(att?.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  const fn = String(att?.filename || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp)$/.test(fn);
}

function progressAttachmentPreviewIcon(att) {
  const fn = String(att?.filename || "").toLowerCase();
  if (/\.(xlsx?|csv)$/.test(fn)) return "\uD83D\uDCCA";
  if (/\.pptx?$/.test(fn)) return "\uD83D\uDCFD\uFE0F";
  if (/\.docx?$/.test(fn)) return "\uD83D\uDCC3";
  if (/\.zip$/.test(fn)) return "\uD83D\uDDDC\uFE0F";
  if (/\.html?$/.test(fn)) return "\uD83C\uDF10";
  if (/\.pdf$/.test(fn)) return "\uD83D\uDCC4";
  if (/\.txt$/.test(fn)) return "\uD83D\uDCDD";
  return "\uD83D\uDCCE";
}

function progressAgendaAttachPreviewLightboxHtml(previewAtt, previewUrl) {
  if (!previewAtt) return "";
  const idAttr = escapeHtmlAttr(String(previewAtt.id));
  const fn = escapeHtml(String(previewAtt.filename || ""));
  const fnAttr = escapeHtmlAttr(String(previewAtt.filename || "file"));
  const isImg = progressAttachmentIsImage(previewAtt);
  const sizeLbl = escapeHtml(formatProgressAttachmentSize(previewAtt.sizeBytes));
  const body = isImg
    ? previewUrl
      ? `<img class="pm-attach-preview-lightbox-img" src="${escapeHtmlAttr(previewUrl)}" alt="${fnAttr}" />`
      : `<p class="pm-attach-preview-lightbox-loading">\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026</p>`
    : `<div class="pm-attach-preview-lightbox-file">
        <span class="pm-attach-preview-lightbox-file-icon" aria-hidden="true">${progressAttachmentPreviewIcon(previewAtt)}</span>
        <p class="pm-attach-preview-lightbox-file-name">${fn}</p>
        <p class="pm-attach-preview-lightbox-file-meta">${sizeLbl}</p>
        <p class="pm-attach-preview-lightbox-file-hint">\u30D7\u30EC\u30D3\u30E5\u30FC\u306F\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3002\u30C0\u30A6\u30F3\u30ED\u30FC\u30C9\u3057\u3066\u3054\u78BA\u8A8D\u304F\u3060\u3055\u3044\u3002</p>
      </div>`;
  return `<div class="pm-attach-preview-layer" role="presentation">
    <div class="pm-attach-preview-backdrop" data-close-pm-attach-preview="1"></div>
    <div class="pm-attach-preview-lightbox" role="dialog" aria-modal="true" aria-label="\u30D5\u30A1\u30A4\u30EB\u30D7\u30EC\u30D3\u30E5\u30FC">
      <button type="button" class="pm-attach-preview-lightbox-close" data-close-pm-attach-preview="1" aria-label="\u9589\u3058\u308B">\u00D7</button>
      <div class="pm-attach-preview-lightbox-body">${body}</div>
      <p class="pm-attach-preview-lightbox-caption">${fn}</p>
      <div class="pm-attach-preview-lightbox-actions">
        <button type="button" class="inline-btn" data-pm-attach-download="${idAttr}">\u30C0\u30A6\u30F3\u30ED\u30FC\u30C9</button>
      </div>
    </div>
  </div>`;
}

function renderProgressAttachPreviewLayer() {
  const previewId = String(uiState.progressAgendaAttachPreviewId || "").trim();
  if (!previewId) return "";
  const previewAtt = findProgressAttachmentById(previewId);
  if (!previewAtt) return "";
  const previewUrl = progressAttachmentIsImage(previewAtt)
    ? (uiState.progressAttachPreviewUrls || {})[previewId] || ""
    : "";
  return progressAgendaAttachPreviewLightboxHtml(previewAtt, previewUrl);
}

function closeProgressAgendaAttachModal() {
  uiState.progressAgendaAttachModal = null;
  uiState.progressAgendaAttachPreviewId = "";
  revokeProgressAttachPreviewUrls();
}

function progressAgendaAttachmentsList(ag) {
  return Array.isArray(ag?.attachments) ? ag.attachments : [];
}

function progressAgendaAttachTriggerHtml(ag) {
  const n = progressAgendaAttachmentsList(ag).length;
  const aid = escapeHtmlAttr(String(ag?.id ?? ""));
  const badge =
    n > 0 ? `<span class="pm-attach-trigger-badge" aria-hidden="true">${n > 9 ? "9+" : n}</span>` : "";
  const label = n > 0 ? `添付ファイル（${n}件）` : "添付ファイル";
  return `<button type="button" class="pm-attach-trigger" data-pm-agenda-attach-open="${aid}" aria-label="${escapeHtmlAttr(label)}" title="${escapeHtmlAttr(label)}"><span class="pm-attach-trigger-ic" aria-hidden="true">${pmBoardIconSvg("attach", 17)}</span>${badge}</button>`;
}

function progressAddValidFilesToAgendaModal(fileList) {
  if (!uiState.progressShowIssueAddForm) return;
  const files = progressFilterValidPmFiles(fileList);
  if (!files.length) {
    showToast(`対応形式のみ追加できます（${PM_ATTACH_EXT_HINT}、各15MB以内）`);
    return;
  }
  const prev = Array.isArray(uiState.progressAgendaModalPendingFiles)
    ? [...uiState.progressAgendaModalPendingFiles]
    : [];
  uiState.progressAgendaModalPendingFiles = prev.concat(files);
  render();
}

function progressAgendaModalAttachFieldHtml(disabled) {
  const dis = Boolean(disabled);
  const disCls = dis ? " is-disabled" : "";
  const disAttr = dis ? " disabled" : "";
  const pending = progressAgendaModalPendingFilesHtml();
  return `<div class="pm-agenda-attach-field">
      <div class="pm-attach-inline-zone pm-file-drop-target${disCls}" data-pm-file-drop="modal:pending" role="group" aria-label="添付ファイル">
        <span class="pm-attach-inline-lead">ドラッグ＆ドロップ、Ctrl+V（Mac: Cmd+V）貼り付け、または</span>
        <label class="pm-attach-inline-pick${disCls}">
          ファイルを選択
          <input type="file" class="pm-attach-file-input" data-pm-modal-agenda-attach-file multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.zip,.html,.htm,.txt,.md"${disAttr} />
        </label>
      </div>
      ${pending}
      <p class="pm-agenda-attach-format-hint">${escapeHtml(PM_ATTACH_EXT_HINT)}（各15MB以内）</p>
    </div>`;
}

function progressAgendaModalPendingFilesHtml() {
  const files = uiState.progressAgendaModalPendingFiles || [];
  if (!files.length) return "";
  return `<div class="pm-modal-pending-files" aria-label="追加予定の添付">
    ${files
      .map((f, i) => {
        const name = escapeHtml(String(f?.name || "file"));
        const size = formatProgressAttachmentSize(f?.size);
        return `<span class="pm-modal-pending-file"><span class="pm-modal-pending-file-name" title="${escapeHtmlAttr(String(f?.name || ""))}">${name}</span><span class="pm-modal-pending-file-size">${escapeHtml(size)}</span><button type="button" class="pm-modal-pending-file-remove" data-pm-modal-pending-file-remove="${i}" aria-label="削除">×</button></span>`;
      })
      .join("")}
  </div>`;
}

async function uploadProgressAgendaPendingModalFiles(agendaId) {
  const aid = Number(agendaId);
  if (!Number.isFinite(aid) || aid < 1) return 0;
  const files = Array.isArray(uiState.progressAgendaModalPendingFiles) ? [...uiState.progressAgendaModalPendingFiles] : [];
  if (!files.length) return 0;
  let n = 0;
  for (const file of files) {
    await uploadProgressBinaryAttachment({ agendaId: aid, file });
    n += 1;
  }
  uiState.progressAgendaModalPendingFiles = [];
  return n;
}

function renderProgressAgendaAttachModal() {
  const modal = uiState.progressAgendaAttachModal;
  if (!modal?.agendaId) return "";
  const hit = findProgressAgendaById(modal.agendaId);
  const ag = hit?.agenda;
  if (!ag) return "";
  const list = progressAgendaAttachmentsList(ag);
  const previewId = String(uiState.progressAgendaAttachPreviewId || "").trim();
  const previewAtt = previewId ? list.find((a) => String(a.id) === previewId) : null;
  const previewUrl =
    previewAtt && progressAttachmentIsImage(previewAtt)
      ? (uiState.progressAttachPreviewUrls || {})[previewId] || ""
      : "";
  const images = list.filter((a) => progressAttachmentIsImage(a));
  const others = list.filter((a) => !progressAttachmentIsImage(a));
  const imageGrid = images.length
    ? `<div class="pm-agenda-attach-images" aria-label="画像">
        ${images
          .map((att) => {
            const idAttr = escapeHtmlAttr(String(att.id));
            const fn = escapeHtml(String(att.filename || "image"));
            const isPreview = previewId === String(att.id);
            const thumbUrl = (uiState.progressAttachPreviewUrls || {})[String(att.id)] || "";
            const thumbInner = thumbUrl
              ? `<img class="pm-agenda-attach-thumb-img" src="${escapeHtmlAttr(thumbUrl)}" alt="" loading="lazy" />`
              : `<span class="pm-agenda-attach-thumb-ph" aria-hidden="true">🖼</span>`;
            return `<div class="pm-agenda-attach-thumb-card${isPreview ? " is-active" : ""}">
              <button type="button" class="pm-agenda-attach-thumb" data-pm-attach-preview="${idAttr}" title="${escapeHtmlAttr(String(att.filename || ""))}">
                ${thumbInner}
                <span class="pm-agenda-attach-thumb-label">${fn}</span>
              </button>
              <div class="pm-agenda-attach-thumb-actions">
                <button type="button" class="pm-attach-btn pm-attach-btn--dl" data-pm-attach-download="${idAttr}" title="ダウンロード" aria-label="ダウンロード">DL</button>
              </div>
            </div>`;
          })
          .join("")}
      </div>`
    : "";
  const fileRows = others.length
    ? `<div class="pm-agenda-attach-files">${progressStandardAttachRowsHtml(others, { previewable: true })}</div>`
    : "";
  const empty = !list.length ? `<p class="pm-agenda-attach-empty">添付ファイルはありません</p>` : "";
  const previewOpen = Boolean(previewId);
  return `
${previewOpen ? "" : `<div class="composer-backdrop project-create-backdrop" data-close-pm-agenda-attach="1"></div>`}
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm pm-agenda-attach-modal" role="dialog" aria-modal="true" aria-labelledby="pm-agenda-attach-title">
      <div class="composer-head">
        <div><h3 id="pm-agenda-attach-title">添付ファイル</h3><p class="project-create-step2-sub">${list.length}件 · 削除はやりとりに履歴として残ります</p></div>
        <button type="button" class="inline-btn ghost" data-close-pm-agenda-attach="1">閉じる</button>
      </div>
      <div class="composer-body pm-agenda-attach-body">
        ${empty}
        ${imageGrid}
        ${fileRows}
      </div>
    </aside>
  `;
}

function progressFilterValidPmFiles(fileList) {
  const list = fileList instanceof FileList ? Array.from(fileList) : Array.isArray(fileList) ? [...fileList] : [];
  const out = [];
  for (const f of list) {
    if (!f || typeof f.name !== "string") continue;
    if (!progressAttachmentClientMeta(f.name)) continue;
    if (f.size > PM_ATTACH_MAX_BYTES) continue;
    out.push(f);
  }
  return out;
}

function progressAddValidFilesToCompose(agendaIdStr, fileList) {
  const id = String(agendaIdStr || "").trim();
  if (!id) return;
  const files = progressFilterValidPmFiles(fileList);
  if (!files.length) {
    showToastDirect(`対応形式のみ追加できます（${PM_ATTACH_EXT_HINT}、各15MB以内）`);
    return;
  }
  if (String(uiState.progressComposeAttach.agendaId || "") !== id) {
    uiState.progressComposeAttach = { agendaId: id, files: [] };
  }
  uiState.progressComposeAttach.files.push(...files);
  // render() を呼ぶとコメント入力中のテキストが消えるため、
  // チップエリアだけ DOM 差し替えして textarea の内容を維持する
  progressRefreshComposeChipsDom(id);
}

/** コメント入力を壊さずに添付チップだけ再描画する */
function progressRefreshComposeChipsDom(agendaIdStr) {
  const id = String(agendaIdStr || "").trim();
  // textarea の値を先に退避（render() がどこかで呼ばれても復元できるように）
  const ta = document.querySelector("[data-pm-msg-body]");
  const savedVal = ta instanceof HTMLTextAreaElement ? ta.value : null;
  const savedSel = ta instanceof HTMLTextAreaElement ? [ta.selectionStart, ta.selectionEnd] : null;
  const restoreTextarea = () => {
    if (savedVal === null) return;
    const taAfter = document.querySelector("[data-pm-msg-body]");
    if (taAfter instanceof HTMLTextAreaElement && taAfter.value !== savedVal) {
      taAfter.value = savedVal;
      try { taAfter.setSelectionRange(savedSel[0], savedSel[1]); } catch { /* noop */ }
    }
  };
  const compose = id ? document.querySelector(`.pm-compose[data-pm-file-drop="compose:${CSS.escape(id)}"]`) : null;
  if (!(compose instanceof HTMLElement)) {
    // フォールバック: コンポーズが見つからない場合のみ render
    render();
    requestAnimationFrame(restoreTextarea);
    return;
  }
  const chipsHtml = progressComposeAttachChipsHtml(id);
  let chipsEl = compose.querySelector(".pm-compose-attach-chips");
  if (chipsHtml) {
    if (chipsEl) {
      chipsEl.innerHTML = chipsHtml;
    } else {
      const div = document.createElement("div");
      div.className = "pm-compose-attach-chips";
      div.setAttribute("aria-label", "送信予定の添付");
      div.innerHTML = chipsHtml;
      // 描画時と同じ位置（左カラム先頭）へ。無ければ file input の直後にフォールバック
      const colLeft = compose.querySelector(".pm-compose-col-left");
      const fileInput = compose.querySelector(".pm-attach-file-input--compose");
      if (colLeft) {
        colLeft.insertAdjacentElement("afterbegin", div);
      } else if (fileInput) {
        fileInput.insertAdjacentElement("afterend", div);
      }
    }
    // 添付があるのにコンポーズが折り畳まれていると登録ボタン（展開ボディ内）が見えないため、必ず展開する
    compose.classList.add("pm-compose--open");
  } else if (chipsEl) {
    chipsEl.remove();
  }
  // DOMを直接操作した後もtextareaの値を念のため確認・復元
  restoreTextarea();
}

async function progressApplyPmFileDrop(dropSpec, fileList) {
  const files = progressFilterValidPmFiles(fileList);
  if (!files.length) {
    showToast(`対応形式のみ追加できます（${PM_ATTACH_EXT_HINT}、各15MB以内）`);
    return;
  }
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  if (dropSpec === "modal:pending") {
    progressAddValidFilesToAgendaModal(files);
    return;
  }
  const route = currentRoute();
  const issueParam = String(route.searchParams?.get("issue") || "").trim();
  const pid = String(route.searchParams?.get("project") || "").trim();
  if (!issueParam) return;
  if (dropSpec.startsWith("compose:")) {
    const agId = dropSpec.slice("compose:".length);
    if (String(agId) !== issueParam) return;
    progressAddValidFilesToCompose(issueParam, files);
    return;
  }
  if (dropSpec.startsWith("agenda:")) {
    const agendaId = Number(dropSpec.slice("agenda:".length));
    if (!Number.isFinite(agendaId) || String(agendaId) !== issueParam) return;
    uiState.progressAttachmentUploadAgendaId = String(agendaId);
    render();
    try {
      for (const file of files) {
        await uploadProgressBinaryAttachment({ agendaId, file });
      }
      await refreshProgressManagementFromServer();
      uiState.progressAttachmentUploadAgendaId = "";
      showToast("添付を登録しました");
      if (pid) setProgressManagementHash(null, pid, issueParam);
      else render();
    } catch (err) {
      uiState.progressAttachmentUploadAgendaId = "";
      showToastError(err instanceof Error ? err.message : "添付に失敗しました");
      render();
    }
    return;
  }
  if (dropSpec.startsWith("msg:")) {
    const messageId = Number(dropSpec.slice("msg:".length));
    if (!Number.isFinite(messageId) || messageId < 1) return;
    uiState.progressAttachmentUploadMessageId = String(messageId);
    render();
    try {
      for (const file of files) {
        await uploadProgressBinaryAttachment({ messageId, file });
      }
      await refreshProgressManagementFromServer();
      uiState.progressAttachmentUploadMessageId = "";
      showToast("添付を登録しました");
      if (pid) setProgressManagementHash(null, pid, issueParam);
      else render();
    } catch (err) {
      uiState.progressAttachmentUploadMessageId = "";
      showToastError(err instanceof Error ? err.message : "添付に失敗しました");
      render();
    }
  }
}

function findClientForProjectCsv(clientName) {
  const needle = normalizeCsvLookupWhitespace(clientName);
  if (!needle) return null;
  const byName = clients.find((c) => {
    const n = normalizeCsvLookupWhitespace(c.name || "");
    return n && n === needle;
  });
  if (byName) return byName;
  const byShort = clients.find((c) => {
    const sn = normalizeCsvLookupWhitespace(c.shortName || "");
    return sn && sn === needle;
  });
  return byShort || null;
}
/** 会社名（顧客名）の重複判定用。trim 済み文字列で一致 */
function findExistingClientByName(name, excludeClientUiId = "") {
  const s = String(name || "").trim();
  if (!s) return null;
  return clients.find((c) => String(c.name || "").trim() === s && c.id !== excludeClientUiId) || null;
}
function findUserForProjectCsv(ownerName) {
  const s = String(ownerName || "").trim();
  if (!s) return null;
  const exact = users.find((u) => isActiveUser(u) && u.name === s);
  if (exact) return exact;
  return users.find((u) => isActiveUser(u) && userNameWithRetiredLabel(u) === s) || null;
}
/** CSV の金額表記→数値。全角は半角へ（NFKC）、桁区切り・空白・¥・円は無視。会計の負数表記
 *  （先頭の ▲ △ −、全体を囲む括弧）は負数。読めなければ NaN（呼び出し側で既定値を決める） */
function parseAccountingAmount(raw) {
  let s = String(raw ?? "").normalize("NFKC").replace(/[,\s¥円]/g, "");
  let neg = false;
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    neg = true;
    s = paren[1];
  }
  if (/^[▲△−]/.test(s)) {
    neg = true;
    s = s.slice(1);
  }
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? (neg ? -Math.abs(n) : n) : NaN;
}

function parseProjectCsvAmount(raw) {
  // 「¥1,000」「1000円」「１０００」も読む（以前は NaN→黙って 0 円になっていた）
  const n = parseAccountingAmount(raw);
  return Number.isFinite(n) ? n : 0;
}

/** 原価取込プレビューの金額入力（カンマ可） */
function parseCostReviewYenInput(raw) {
  const s = String(raw ?? "")
    .replace(/[,，\s]/g, "")
    .trim();
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}
function normalizeProjectCsvOrderStatus(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "見積もり";
  if (s === "失注" || s === "除外" || s === "見積もり" || s === "受注確定" || s === "受注済") return s;
  if (s === "進行中" || s === "見積もり中" || s === "未受注") return "見積もり";
  if (s === "完了") return "受注済";
  if (s.startsWith("見積中")) return "見積もり";
  return s;
}
/** 進捗状況の表示・保存値を固定リストに揃える */
function normalizeProjectProgressStatus(raw) {
  const s = String(raw ?? "").trim();
  if (PROJECT_PROGRESS_STATUS_LIST.includes(s)) return s;
  if (s === "完了" || s === "納品" || s === "納品完了") return "納品済";
  if (s === "進行中" || s === "作業中") return "着手中";
  return "未着手";
}
/** 請求ステータス列 → fp_projects.invoice_status 向け */
function normalizeProjectCsvInvoiceStatus(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "未作成";
  if (s.includes("入金済") || s === "入金確認済み") return "入金確認済み";
  if (s.includes("入金待")) return "入金待ち";
  if (s.includes("送付済") || s === "請求OK" || s.includes("請求済")) return "送付済み";
  if (s.includes("送付準備") || s.includes("準備OK")) return "送付準備OK";
  if (s.includes("下書き")) return "下書きあり";
  return s;
}
/** 請求日・日付文字列 → 請求月（例 2026/4/1 → 2026/4） */
function parseBillingMonthFromCsvDate(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})[\/\-](\d{1,2})/);
  if (!m) return null;
  return `${m[1]}/${Number(m[2], 10)}`;
}
/** CSV の請求日・日付セル → 請求(予定)日用 YYYY-MM-DD */
function parseCsvDateToIsoYmd(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return "";
  const m = t.match(/^(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})/);
  if (!m) return "";
  const y = m[1];
  const mo = String(Number(m[2], 10)).padStart(2, "0");
  const d = String(Number(m[3], 10)).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}
/** ワイド形式CSVのヘッダ表記ゆれ → projectWideRowFromCols が参照する正規キー（未対応なら ""） */
function wideCsvCanonHeaderKey(headerLabel) {
  const raw = String(headerLabel ?? "").trim();
  if (!raw) return "";
  const t = raw.normalize("NFKC").replace(/\u3000/g, " ").trim();
  const compactLower = t.replace(/\s+/g, "").toLowerCase();
  if (compactLower === "案件no" || compactLower === "ケースno" || /^案件番号$/i.test(t)) return "案件no";
  return "";
}

/** 1行目のヘッダセルから列名→インデックス（同一ヘッダは先勝ち）。「案件No」等を案件noとしても解決 */
function buildProjectWideHeaderMap(headerCells) {
  const map = {};
  headerCells.forEach((h, i) => {
    const k = String(h || "").trim();
    if (k && map[k] === undefined) map[k] = i;
    const canon = wideCsvCanonHeaderKey(k);
    if (canon && map[canon] === undefined) map[canon] = i;
  });
  return map;
}
// 請求明細の行ごとの「分類」は 2026-10 要望で廃止（担当者割当・一覧CSVは案件のカテゴリを使う）。
// 保存済みの明細に残っている category キーは消さない（編集の衝突判定 billingLinesHash が変わるため）。読まないだけ。
/** CSV ワイド1案件グループ → 案件詳細「請求項目」用の明細行（行順維持） */
function billingDetailLinesFromWideGroup(groupRows) {
  const lines = [];
  const numCell = (raw) => String(raw ?? "").replace(/,/g, "").replace(/\s/g, "").trim();
  for (const r of groupRows) {
    const kind = String(r.rowKind || "").trim();
    if (kind === "見出し行") {
      const title = String(r.headingLabel || "").trim() || String(r.summary || "").trim();
      lines.push({ lineType: "heading", item: title });
      continue;
    }
    if (kind === "小計行") {
      const label = String(r.subtotalLabel || "").trim() || "小計";
      lines.push({ lineType: "subtotal", item: label });
      continue;
    }
    if (kind !== "通常" && kind !== "") continue;
    const item = String(r.summary || "").trim();
    const qtyStr = numCell(r.qty);
    const priceStr = numCell(r.unitPrice);
    const lineAmt = parseProjectCsvAmount(r.lineAmount);
    if (
      kind === "" &&
      !item &&
      !qtyStr &&
      !priceStr &&
      !lineAmt
    ) {
      continue;
    }
    // 数量が空でも単価か行金額があれば「1式」とみなす（"0" にすると明細が0円で取り込まれ、請求書の金額を誤る）
    const qty = qtyStr || (priceStr || lineAmt ? "1" : "0");
    const unit = String(r.unit || "").trim() || "式";
    let price = priceStr;
    if (!price) {
      const amt = parseProjectCsvAmount(r.lineAmount);
      const qn = parseFloat(qty.replace(/,/g, "")) || 0;
      if (qn > 0 && amt) price = String(Math.round(amt / qn));
      else price = "0";
    }
    lines.push({ lineType: "normal", item, qty, unit, price, assignedUserId: "" });
  }
  return lines;
}
function projectWideRowFromCols(cols, hMap) {
  const g = (ja) => {
    const i = hMap[ja];
    if (i === undefined || cols[i] === undefined) return "";
    return String(cols[i]).trim();
  };
  return {
    caseNo: g("案件no"),
    name: g("案件名"),
    clientName: g("顧客名"),
    subtotal: g("小計"),
    consumptionTax: g("消費税"),
    withholdingTax: g("源泉徴収税"),
    total: g("合計"),
    summary: g("摘要"),
    qty: g("数量"),
    unit: g("単位"),
    unitPrice: g("単価"),
    lineAmount: g("金額"),
    headingLabel: g("見出し行"),
    subtotalLabel: g("小計行"),
    rowKind: g("行の種類"),
    billingDate: g("請求日"),
    paymentDue: g("支払期限"),
    paymentTerms: g("支払条件"),
    orderStatus: g("受注ステータス"),
    progressStatus: g("進捗状況"),
    invoiceStatus: g("請求ステータス"),
    ownerName: g("自社担当"),
    tagsRaw: g("タグ")
  };
}
function isWideProjectImportCsv(headerLine) {
  const raw = String(headerLine || "").normalize("NFKC");
  const cells = parseCsvRow(raw).map((c) => String(c ?? "").trim());
  const hasCaseCol =
    cells.some((x) => wideCsvCanonHeaderKey(x) === "案件no") ||
    /案件\s*no|ケース\s*no/i.test(raw) ||
    raw.includes("案件番号");
  const hasRowKind =
    cells.some((x) => x === "行の種類" || x.includes("行の種類")) || raw.includes("行の種類");
  const hasClient =
    cells.some((x) => x === "顧客名" || /^顧客名/.test(x)) || raw.includes("顧客名");
  return hasCaseCol && hasRowKind && hasClient;
}
/** 同一案件の複数行を案件no＋案件名＋顧客名でグループ化 */
function groupWideProjectImportRows(rows) {
  const map = new Map();
  for (const r of rows) {
    const name = normalizeCsvLookupWhitespace(r.name || "");
    const client = normalizeCsvLookupWhitespace(r.clientName || "");
    if (!name || !client) continue;
    const no = String(r.caseNo || "").trim();
    const key = `${no}|||${name}|||${client}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return map;
}
/** グループから1件分のPOST用フィールドを組み立て（見出し行を優先） */
function projectPayloadFromWideGroup(groupRows) {
  if (!groupRows.length) return null;
  const heading =
    groupRows.find((r) => r.rowKind === "見出し行") ||
    groupRows.find((r) => r.rowKind === "小計行") ||
    groupRows[0];
  const name = normalizeCsvLookupWhitespace(heading.name || "");
  const clientName = normalizeCsvLookupWhitespace(heading.clientName || "");
  if (!name || !clientName) return null;
  let amount = parseProjectCsvAmount(heading.total);
  if (!amount) {
    const subRow = groupRows.find((r) => r.rowKind === "小計行");
    if (subRow) {
      amount =
        parseProjectCsvAmount(subRow.lineAmount) ||
        parseProjectCsvAmount(subRow.subtotalLabel) ||
        parseProjectCsvAmount(subRow.subtotal) ||
        parseProjectCsvAmount(subRow.total);
    }
  }
  if (!amount) amount = parseProjectCsvAmount(heading.subtotal);
  const plannedInvoiceDate = parseCsvDateToIsoYmd(heading.billingDate);
  const billingMonth = parseBillingMonthFromCsvDate(heading.billingDate);
  const nextAction = String(heading.summary || "").trim().slice(0, 500);
  const tagsJoined = parseProjectDetailTags(heading.tagsRaw || "").join(", ");
  return {
    name,
    clientName,
    amount,
    billingMonth,
    plannedInvoiceDate: plannedInvoiceDate || "",
    ownerName: heading.ownerName,
    orderStatus: heading.orderStatus,
    progressStatus: heading.progressStatus,
    invoiceStatus: heading.invoiceStatus,
    nextAction,
    tags: tagsJoined
  };
}
/** クライアントCSVの NDA／基本契約 を DB の「未」「済」へ正規化 */
function normalizeClientCsvDocStatus(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return "未";
  if (t === "済" || t === "済み") return "済";
  if (/締結済み?/.test(t)) return "済";
  const lc = t.toLowerCase();
  if (lc === "yes" || lc === "true" || lc === "1" || lc === "y") return "済";
  if (t === "有" || /^(完了|ok|〇|○)$/.test(t)) return "済";
  return "未";
}
function resolveRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input === "object") {
    if (typeof input.url === "string") return input.url;
    if (typeof input.href === "string") return input.href;
  }
  return "";
}
window.fetch = (input, init) => {
  let url = "";
  try {
    url = resolveRequestUrl(input);
    // 本番/ステージングでローカル端末宛アクセスを止める（Private Network 許可ポップアップ対策）
    if (!IS_LOCAL_DEBUG_HOST && LOCAL_ENDPOINT_PATTERN.test(url)) {
      return Promise.resolve(new Response(null, { status: 204, statusText: "No Content" }));
    }
  } catch {}
  return originalFetch(input, init).then((res) => {
    // セッション切れ（bad_token）は heartbeat 以外の通常API呼び出しでも検知して自動ログアウトへ誘導する。
    // 放置すると期限切れトークンで叩き続け、呼ぶたびサーバーに bad_token の401が積み上がってしまう。
    // ここで例外が起きても呼び出し元の fetch 結果自体は必ず返す（認証チェックの副作用でAPI呼び出しを壊さない）。
    if (res.status === 401 && /\/api\//.test(url)) {
      try { handleSessionExpired(); } catch {}
    }
    return res;
  });
};

/** JWT の exp（毎日 0:00 JST）をミリ秒で取得 */
function authTokenExpMs(token = authToken) {
  if (!token) return 0;
  try {
    const b64 = token.split(".")[1];
    if (!b64) return 0;
    const json = JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
    return json.exp && Number.isFinite(json.exp) ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

let fpSessionLogoutTimer = null;
const FP_MIDNIGHT_PROMPT_GRACE_MS = 90 * 1000;

function clearMidnightJstLogoutTimer() {
  if (fpSessionLogoutTimer != null) {
    clearTimeout(fpSessionLogoutTimer);
    fpSessionLogoutTimer = null;
  }
}

function authTokenPayload() {
  if (!authToken) return null;
  try {
    const b64 = authToken.split(".")[1];
    if (!b64) return null;
    return JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function getTokyoYmdKeyClient(dateMs = Date.now()) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return dtf.format(new Date(dateMs));
}

/** 当日の「続ける」（+2h）を使った延長セッションか */
function isExtendedSessionToken() {
  const p = authTokenPayload();
  const ymd = p?.sessionExtendYmd;
  return typeof ymd === "string" && ymd.length > 0 && ymd === getTokyoYmdKeyClient();
}

/** ログイン保持（7日有効）セッションか。exp が 0:00 ではなく発行から7日後なので深夜モーダルの対象外 */
function isPersistSessionToken() {
  return authTokenPayload()?.persistSession === true;
}

function isAuthSessionExpired() {
  const exp = authTokenExpMs();
  return exp > 0 && Date.now() >= exp;
}

function canShowMidnightBedtimePrompt(expMs = authTokenExpMs()) {
  if (!expMs) return false;
  const elapsed = Date.now() - expMs;
  return elapsed >= 0 && elapsed <= FP_MIDNIGHT_PROMPT_GRACE_MS && document.visibilityState === "visible";
}

function closeMidnightBedtimeModal() {
  const el = document.getElementById("fpMidnightBedtimeModal");
  if (el) el.remove();
  uiState.midnightBedtimeModalOpen = false;
}

function showMidnightBedtimeModal() {
  if (!authToken || uiState.midnightBedtimeModalOpen) return;
  if (isExtendedSessionToken()) {
    doLogout();
    return;
  }
  closeMidnightBedtimeModal();
  uiState.midnightBedtimeModalOpen = true;
  const overlay = document.createElement("div");
  overlay.id = "fpMidnightBedtimeModal";
  overlay.className = "fp-midnight-bedtime-overlay open";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "fpMidnightBedtimeTitle");
  overlay.innerHTML = `
    <div class="fp-midnight-bedtime-modal">
      <div class="fp-midnight-bedtime-head">
        <h2 id="fpMidnightBedtimeTitle" class="fp-midnight-bedtime-title">夜は寝ないとだめです！</h2>
      </div>
      <p class="fp-midnight-bedtime-lede">0:00になりました。ログアウトするか、残業用にあと2時間だけ続けるかを選んでください（延長は1日1回まで）。</p>
      <div class="fp-midnight-bedtime-foot">
        <button type="button" class="inline-btn ghost" data-midnight-bedtime-logout="1">ログアウト</button>
        <button type="button" class="inline-btn" data-midnight-bedtime-extend="1">あと2時間続ける</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  // 猶予時間（90秒）が過ぎても操作しなかった場合は強制ログアウト
  // （画面を開きっぱなしで離席しているケースに対応）
  setTimeout(() => {
    if (uiState.midnightBedtimeModalOpen) {
      doLogout();
    }
  }, FP_MIDNIGHT_PROMPT_GRACE_MS);
}

function handleAuthSessionExpiry(promptAtExpiry = false) {
  if (!authToken) return;
  if (isExtendedSessionToken()) {
    doLogout();
    return;
  }
  if (!promptAtExpiry || !canShowMidnightBedtimePrompt()) {
    doLogout();
    return;
  }
  showMidnightBedtimeModal();
}

async function handleMidnightSessionExtend() {
  const overlay = document.getElementById("fpMidnightBedtimeModal");
  const extendBtn = overlay?.querySelector("[data-midnight-bedtime-extend]");
  if (extendBtn) extendBtn.disabled = true;
  try {
    const res = await fetch("/api/session-extend", {
      method: "POST",
      headers: { Authorization: `Bearer ${authToken}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = typeof data.error === "string" ? data.error : "セッション延長に失敗しました";
      showToast(msg);
      if (extendBtn) extendBtn.disabled = false;
      return;
    }
    authToken = data.token;
    sessionStorage.setItem("fp_token", authToken);
    fpPersistToken(authToken);
    closeMidnightBedtimeModal();
    scheduleMidnightJstLogout();
    showToast("あと2時間だけセッションを延長しました");
  } catch {
    showToastError("セッション延長に失敗しました");
    if (extendBtn) extendBtn.disabled = false;
  }
}

/** 0:00 JST でモーダル → ログアウト or 1日1回 +2h 延長 */
function scheduleMidnightJstLogout() {
  clearMidnightJstLogoutTimer();
  if (!authToken) return;
  // ログイン保持セッションは exp が発行から7日後（0:00 ではない）。深夜モーダル/強制ログアウトの
  // 対象にすると、ログインした時刻（例: 13:54）に「0:00になりました」が誤発火するため対象外にする。
  // 保持セッションは JWT の自然失効（visibilitychange / 次回起動）で静かにログアウトされる。
  if (isPersistSessionToken()) return;
  const exp = authTokenExpMs();
  if (!exp) return;
  // 延長(+2h)セッションは満了時に深夜モーダルを再表示せず静かにログアウト（真夜中またぎで
  // isExtendedSessionToken の日付判定がずれ、モーダルが再度出る誤動作を防ぐ）。
  const promptAtExpiry = !isExtendedSessionToken();
  const remain = exp - Date.now();
  if (remain <= 0) {
    handleAuthSessionExpiry(false);
    return;
  }
  fpSessionLogoutTimer = setTimeout(() => handleAuthSessionExpiry(promptAtExpiry), remain);
}

function bindMidnightJstLogoutWatch() {
  if (window.__fpMidnightLogoutWatchBound) return;
  window.__fpMidnightLogoutWatchBound = true;
  document.addEventListener("visibilitychange", () => {
    if (!authToken) return;
    if (isAuthSessionExpired()) handleAuthSessionExpiry(false);
  });
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-midnight-bedtime-logout]")) {
      closeMidnightBedtimeModal();
      doLogout();
      return;
    }
    if (e.target.closest("[data-midnight-bedtime-extend]")) {
      e.preventDefault();
      void handleMidnightSessionExtend();
    }
  });
}

function isLoggedIn() {
  return !!authToken && !!authUser && !isAuthSessionExpired();
}

function normalizeDepartmentsForUi(list) {
  return (Array.isArray(list) ? list : [])
    .filter((d) => d && d.id && d.name)
    .map((d) => ({
      id: String(d.id),
      name: String(d.name || ""),
      createdAt: String(d.createdAt || ""),
      memberIds: Array.isArray(d.memberIds) ? d.memberIds.map((id) => String(id)) : [],
      leaderMemberId: String(d.leaderMemberId || "")
    }));
}

function saveDepartmentsFallbackLocal(list) {
  // 端末ローカルには保存しない（全アカウント共通データのみ利用）
  void list;
}

function readDepartmentsFallbackLocal() {
    return [];
}

let heavyDataLoadPromise = null;

/** 起動データ（/api/startup）の Response。自動ログインでは /api/me と同時に先に始める（tryAutoLogin） */
function fetchStartupResponse() {
  return fetch("/api/startup", { headers: { Authorization: `Bearer ${authToken}` } });
}

async function loadDataFromDB(options = {}) {
  if (!authToken) return;
  const mode = options.mode || "full"; // full | critical | heavy
  const skipLoadingUi = !!options.skipLoadingUi;
  const headers = { Authorization: `Bearer ${authToken}` };
  const applyCritical = async () => {
    // /api/startup は 20 秒タイムアウト（コールドスタート + DDL 初期化が長い）
    const _startupTimeout = new Promise((resolve) =>
      setTimeout(() => resolve({ ok: false, _timedOut: true }), 20000)
    );
    const startupRes = await Promise.race([
      options.startupPrefetch || fetchStartupResponse(),
      _startupTimeout
    ]).catch(() => ({ ok: false }));
    let data = {};
    if (startupRes.ok) {
      data = await startupRes.json().catch(() => ({}));
    }
    if (Array.isArray(data.users)) {
      replaceUsersFromApiPayload(data.users);
    } else {
      // startup.js の users クエリが失敗した場合は /api/users を直接取得（10 秒タイムアウト）
      const usersRes = await Promise.race([
        fetch("/api/users", { headers }).catch(() => null),
        new Promise((resolve) => setTimeout(() => resolve(null), 10000))
      ]);
      if (usersRes?.ok) {
        const ud = await usersRes.json().catch(() => ({}));
        if (Array.isArray(ud.users)) replaceUsersFromApiPayload(ud.users);
      }
    }
    if (Array.isArray(data.departments)) {
      const rawDepartments = data.departments;
      // 旧UIのダミー部署は表示対象から除外する（必要なら新規に追加し直す）
      const departments = rawDepartments
        .filter((d) => !LEGACY_DUMMY_DEPT_IDS.has(String(d.id || "")))
        .map((d) => ({
          id: String(d.id),
          name: String(d.name || ""),
          createdAt: String(d.createdAt || ""),
          memberIds: Array.isArray(d.memberIds) ? d.memberIds.map((id) => String(id)) : [],
          leaderMemberId: String(d.leaderMemberId || "")
        }));
      uiState.deptSettings = {
        departments,
        editingDeptId: String(departments[0]?.id || "")
      };
      saveDepartmentsFallbackLocal(departments);
    }
    // 承認設定（版トークン含む）は departments の有無と独立に取り込む
    // （関数内で各フィールドの有無を個別判定するため、無関係なペイロードには何もしない）
    applyApprovalSettingsPayload(data);
    if (Array.isArray(data.periods) && data.periods.length > 0) {
      uiState.budgetPeriods = data.periods.map(normalizeBudgetPeriodClient);
      sortBudgetPeriodsInPlace();
      if (!uiState.selectedBudgetPeriodId) {
        uiState.selectedBudgetPeriodId = latestBudgetPeriodIdFromSorted(uiState.budgetPeriods);
      }
    }
    if (data.byPeriod && typeof data.byPeriod === "object") {
      try {
        budgetDraftMemoryStore = JSON.parse(JSON.stringify(data.byPeriod));
        rememberBudgetServerPeriods(data.byPeriod);
        if (!uiState.budgetRowsLoadedByPeriod || typeof uiState.budgetRowsLoadedByPeriod !== "object") {
          uiState.budgetRowsLoadedByPeriod = {};
        }
        Object.keys(budgetDraftMemoryStore).forEach((id) => {
          if (id) uiState.budgetRowsLoadedByPeriod[id] = true;
        });
      } catch { /* ignore */ }
    } else if (!startupRes.ok) {
      budgetDraftMemoryStore = {};
    }
    if (userCanAccessPage("progress-management")) {
      void refreshProgressViewerCountsFromServer().then((ok) => {
        if (ok) {
          try {
            render();
          } catch {
            /* noop */
          }
        }
      });
    }
  };
  const applyHeavy = async () => {
    // tail fetchはheavyデータに依存しないため、heavyと並列で開始する
    const tailFetches = [];
    if (userCanAccessPage("application-management")) {
      tailFetches.push(
        fetch("/api/application-review-memos", { headers }).then(async (memRes) => {
          try {
            if (memRes.ok) {
              const md = await memRes.json().catch(() => ({}));
              if (md && md.memos && typeof md.memos === "object") {
                // 全種別を取り込む（以前は contract/seal だけで、ツール等の確認メモが再読込で消えて見えていた）
                uiState.appMgmtReviewMemos = {};
                for (const [k, v] of Object.entries(md.memos)) {
                  if (v && typeof v === "object") uiState.appMgmtReviewMemos[k] = { ...v };
                }
              }
            }
          } catch {
            /* オフライン等は既存メモを維持 */
          }
        })
      );
      // 申請承認のベル通知用: ツール許可・ロック解除・開発事前申請も起動時に読み込む
      // （ページを開かなくても「承認するまで残る」通知が出るように。承認/却下で再取得され件数が減る）
      if (!uiState.allowedToolsLoaded) tailFetches.push(fetchAllowedToolsData());
      if (uiState.projectUnlockApplications === null && !uiState.projectUnlockAppsLoading) {
        tailFetches.push(fetchProjectUnlockApplications());
      }
      if (!uiState.developmentApplicationsLoaded) tailFetches.push(fetchDevelopmentApplications());
      if (!uiState.equipmentApplicationsLoaded) tailFetches.push(fetchEquipmentApplications());
    }
    tailFetches.push(
      fetch("/api/doc-seal-applications", { headers }).then(async (sealRes) => {
        try {
          if (sealRes.ok) {
            const sd = await sealRes.json().catch(() => ({}));
            applyDocSealApplicationsPayload(sd);
            uiState.docSealAppsFetchedAt = Date.now();
          } else if (sealRes.status === 401 || sealRes.status === 403) {
            uiState.docSealStatus = {};
            uiState.docSealApprovalProgress = {};
          }
        } catch {
          /* オフライン等は既存の捺印状態を維持 */
        }
      })
    );
    tailFetches.push(
      fetch("/api/project-tag-presets", { headers }).then(async (tagRes) => {
        try {
          if (tagRes.ok) {
            const td = await tagRes.json().catch(() => ({}));
            applyProjectTagPresetsPayload(td);
          }
        } catch {
          /* オフライン等は localStorage の候補を維持 */
        }
      })
    );

    const routeAtHeavyStart = currentRoute();
    const partnersNeededBeforeRender =
      routeAtHeavyStart.page === "partner-list" ||
      routeAtHeavyStart.sidebarKey === "partner-list" ||
      routeAtHeavyStart.sidebarKey === "contract-management";
    const partnersFetchPromise = fetch("/api/partners", { headers }).catch(() => null);
    const applyPartnersPayload = async (partnersRes) => {
      if (partnersRes?.ok) {
        try {
          const pdata = await partnersRes.json();
          uiState.partnersCache = Array.isArray(pdata.partners) ? pdata.partners : [];
          return true;
        } catch {
          uiState.partnersCache = [];
          return true;
        }
      }
      uiState.partnersCache = [];
      return true;
    };
    // ドメイン管理は起動時に取得しない（/api/domains はレジストラ認証情報を含み閲覧監査ログが載るため、
    // domain-list ページを開いた時の遅延読み込みに変更。15-sp-backlog.js fetchDomainsData）
    // 媒体原価の「アカウント名→案件」記憶（非ブロッキング。取込プレビューの自動補完に使う）
    const costAcctMapFetchPromise = fetch("/api/cost-account-mappings", { headers }).catch(() => null);
    const applyCostAcctMapPayload = async (mapRes) => {
      let rows = [];
      if (mapRes?.ok) {
        try {
          const mdata = await mapRes.json();
          rows = Array.isArray(mdata.mappings) ? mdata.mappings : [];
        } catch { rows = []; }
      }
      uiState.costAccountProjectMap = rows.map((m) => ({
        sourceKey: String(m.sourceKey || ""),
        accountNameKey: String(m.accountNameKey || ""),
        projectId: m.projectId != null ? Number(m.projectId) : null
      }));
    };
    // メイン 3 API も 20 秒タイムアウト（コールドスタート対策）
    const _heavyMainTimeout = new Promise((resolve) =>
      setTimeout(() => resolve([{ ok: false }, { ok: false }, null]), 20000)
    );
    const [clientsRes, projectsRes, expensesRes] = await Promise.race([
      Promise.all([
        fetch("/api/clients", { headers }),
        fetch("/api/projects", { headers }),
        // 経費（人件費を含む）は月次P/L・経営ダッシュボードを開ける人だけが読める（API も同じ権限で 403）
        userCanAccessPage("monthly-pl") ? fetch("/api/expenses", { headers }).catch(() => null) : null
      ]),
      _heavyMainTimeout
    ]);
    if (clientsRes.ok) {
      const data = await clientsRes.json();
      if (data.clients) {
        clients.length = 0;
        data.clients.forEach((c) => clients.push({
          id: `c${c.id}`, dbId: c.id, name: c.name, shortName: c.short_name, honorific: c.honorific,
          postalCode: c.postal_code, prefecture: c.prefecture, address: c.address, building: c.building,
          tel: c.tel, fax: c.fax, email: c.email, contactName: c.contact_name, hpUrl: c.hp_url,
          contractOwner: c.contract_owner != null ? String(c.contract_owner).trim() : "",
          paymentTerms: c.payment_terms, transferFeeBurden: c.transfer_fee_burden,
          ndaStatus: c.nda_status, ndaDate: c.nda_date, ndaNote: c.nda_note,
          contractStatus: c.contract_status, contractDate: c.contract_date, contractNote: c.contract_note,
          approvalStatus: c.approval_status ? String(c.approval_status).trim() : "未申請",
          approvalRejectReason: c.approval_reject_reason || "",
          deliveryMethod: normalizeDeliveryMethodStored(c.delivery_method || ""),
          notes: c.notes, ndaPurpose: c.nda_purpose || "", tags: c.tags,
          firstRegisteredAt: c.first_registered_at || "",
          corporateNumber: c.corporate_number,
          customerNo: c.customer_no != null ? String(c.customer_no) : "",
          invoiceIssuer: c.invoice_issuer, invoiceIssuerNumber: c.invoice_issuer_number,
          salesHidden: c.sales_hidden === true,
          archived: c.archived === true,
          approvalApplyNote: c.approval_apply_note || "",
          expectedAmount: c.expected_amount != null ? Number(c.expected_amount) : null,
          capitalAmount: c.capital_amount != null ? Number(c.capital_amount) : null,
          establishedYm: c.established_ym || ""
        }));
      }
    }
    if (projectsRes.ok) {
      const data = await projectsRes.json();
      // 月次ロック済み月（isMonthLocked の参照元）。案件と同じ応答で届く
      if (Array.isArray(data.lockedMonths)) setLockedMonths(data.lockedMonths);
      if (Array.isArray(data.projects) && (data.projects.length > 0 || projects.length === 0)) {
        projects.length = 0;
        data.projects.forEach((p) => {
          const planYmd = pgDateFieldToYmd(p.planned_invoice_date);
          const dueYmd = pgDateFieldToYmd(p.due_date);
          projects.push({
          id: `p${p.id}`, dbId: p.id, clientId: `c${p.client_id}`, name: p.name,
          client: p.client_name, person: p.person, clientBranchName: p.client_branch_name || "",
          amount: p.amount,
          billingMonth: p.billing_month, currentStage: p.current_stage, nextAction: p.next_action,
          recurringBilling: p.recurring_billing === true,
          mailTo: p.mail_to || "",
          mailCc: p.mail_cc || "",
          // 最終更新日（ローカル日付 YYYY-MM-DD）。以前はマッピングが無く、入金ページ等で「更新: undefined」と出ていた
          updatedAt: p.updated_at ? localDateToYmd(new Date(p.updated_at)) : "",
          paymentTerm: p.payment_term || "",
          internalMemo: p.internal_memo || "",
          taxIncluded: p.tax_included === true,
          unlockUntil: p.unlock_until || "",
          plannedInvoiceDate: planYmd,
          dueDate: dueYmd,
          billingDate: isoYmdToSlashDisplay(planYmd) || "",
          stale: p.stale, projectType: p.project_type, useOrderFlow: p.use_order_flow,
          orderStatus: p.order_result_status || "",
          progressStatus: normalizeProjectProgressStatus(p.progress_status),
          tag: p.tags != null ? String(p.tags) : "",
          serviceCategory: p.service_category != null ? String(p.service_category) : "",
          contractDone: p.contract_done,
          salesOwnerId: p.sales_owner_id != null ? `u${p.sales_owner_id}` : "",
          accountingOwnerId: p.accounting_owner_id != null ? `u${p.accounting_owner_id}` : "",
          assignedUserId: p.assigned_user_id != null ? `u${p.assigned_user_id}` : "",
          currentOwnerRole: p.current_owner_role || "営業", invoiceStatus: p.invoice_status,
          handoffStatus: p.handoff_status || "未引き継ぎ", owner: "",
          handoffAt: p.handoff_at || "",
          handoffNote: p.handoff_note || "",
          handedBy: p.handed_by || "",
          previousAssignedUserId: p.previous_assigned_user_id != null ? `u${p.previous_assigned_user_id}` : "",
          paymentRecord: p.payment_record && typeof p.payment_record === "object" ? p.payment_record : null,
          salesSplit: normalizeClientSalesSplit(p.sales_split_json),
          nonSalesSplit: normalizeClientNonSalesInvolved(p.non_sales_split_json),
          workCategories: normalizeClientWorkCategories(p.work_categories_json),
          paymentConfirmedBy: p.payment_confirmed_by != null ? `u${p.payment_confirmed_by}` : "",
          paymentConfirmedAt: p.payment_confirmed_at || "",
          docs: { nda: p.nda_status, contract: p.contract_status, quote: p.quote_status, order: p.order_status, payment: p.payment_status },
          costs: (p.costs || []).map((c) => mapServerCostToClientShape(c))
        });
        });
        // 保存時に「変えた項目だけ」送るための基準（persistProjectDetailToServer）
        projects.forEach(rememberProjectServerBaseline);
        uiState.orphanCosts = [];
        if (Array.isArray(data.orphanCosts)) {
          const costIdsOnProjects = new Set();
          for (const p of projects) {
            for (const c of p.costs || []) {
              if (c?.id) costIdsOnProjects.add(String(c.id));
            }
          }
          data.orphanCosts.forEach((c) => {
            const mapped = mapServerCostToClientShape(c);
            if (costIdsOnProjects.has(String(mapped.id))) return;
            uiState.orphanCosts.push(mapped);
          });
        }
        // ローカルにあってサーバーにない請求項目（未保存として残す）の pid を収集
        const _pidsLocalOnlyBilling = [];
        for (const row of data.projects) {
          const pid = `p${row.id}`;
          const serverLines = normalizeServerBillingLines(row.billing_lines_json);
          const isDirty = !!(uiState.projectDocsDraftDirty && uiState.projectDocsDraftDirty[pid]);
          // サーバーが空配列で、この下書きの元になったサーバー状態（ベースハッシュ）から変わっている
          // ＝他の場所で明細が消された。古い下書きを送り返すと削除済みの明細が復活するため、
          // 同期せずサーバーの内容を採用する（編集中＝dirty の下書きは除く。保存時に 409 で知らせる）
          let staleDraft = false;
          // serverが null or 空配列([]) → サーバー未保存とみなしてローカルデータを同期候補に
          if (serverLines == null || serverLines.length === 0) {
            // サーバーの明細は空と分かっている＝「保存せずに移動」の戻し先は空（新規作成した案件など。
            // 控えが無いと案件詳細で足した明細を捨てられず、未保存のまま残り続ける）
            uiState.projectBillingLinesServerCopy[pid] = [];
            const localDraft = uiState.projectDocsDrafts[pid];
            const localLines = Array.isArray(localDraft?.lines) ? localDraft.lines : [];
            // 有効なアイテム（itemが空でない）が1件以上あれば同期対象
            const hasRealData = localLines.some((l) => l && String(l.item || "").trim().length > 0);
            const baseHash = billingLinesBaseHashFor(pid);
            staleDraft = hasRealData && !isDirty && serverLines != null && !!baseHash && baseHash !== billingLinesHash(serverLines);
            if (hasRealData && !staleDraft) _pidsLocalOnlyBilling.push(pid);
            if (serverLines == null) continue;
            // serverLines.length === 0 の場合はそのまま処理を続ける（空配列でドラフトを上書き）
          }
          if (isDirty) {
            // dirty（編集中）の場合はドラフトを上書きしない。ベースハッシュも進めない
            // （進めると、他の人の変更を知らないまま手元の編集で上書きできてしまう）
            if (serverLines.length > 0) rememberProjectBillingLinesServerSnapshot(pid, serverLines, { keepBase: true });
            continue;
          }
          const prev = uiState.projectDocsDrafts[pid];
          const prevLines = Array.isArray(prev?.lines) ? prev.lines : [];
          if (serverLines.length === 0 && prevLines.length > 0 && !staleDraft) {
            // サーバーが空を返したがローカルに有効なデータがある場合:
            // ドラフトを維持し、スナップショットも上書きしない（保護値を保持）
            continue;
          }
          uiState.projectDocsDrafts[pid] = {
            lines: serverLines.map((x) => (x && typeof x === "object" ? x : {})),
            meta: prev && typeof prev.meta === "object" ? prev.meta : {},
            remark: prev && typeof prev.remark === "object" ? prev.remark : {}
          };
          rememberProjectBillingLinesServerSnapshot(pid, serverLines);
        }
        try {
          persistProjectDocDraftsStore();
        } catch {
          /* ignore */
        }
        // 端末にだけ残っている請求項目（サーバーは空）は、以前は読込時に自動でサーバーへ送っていた。
        // 案件詳細は「保存」ボタンで確定する方式にしたため（2026-10）、送らずに「未保存」として残し、
        // 案件詳細で保存ボタンを押したときに送る（409・ロックの扱いは保存時の persistProjectDetailToServer と同じ）
        for (const pid of _pidsLocalOnlyBilling) {
          uiState.projectDocsDraftDirty[pid] = true;
          uiState.projectBillingManualDirty[pid] = true;
        }
      }
    }
    if (partnersNeededBeforeRender) {
      await applyPartnersPayload(await partnersFetchPromise);
    } else {
      void partnersFetchPromise
        .then(async (partnersRes) => {
          await applyPartnersPayload(partnersRes);
          syncContractMasterFromClients();
          const route = currentRoute();
          if (
            route.page === "client-list" ||
            route.page === "partner-list" ||
            route.sidebarKey === "client-list" ||
            route.sidebarKey === "contract-management"
          ) {
            render();
          }
        })
        .catch(() => {
          /* ignore */
        });
    }
    // 媒体原価の紐づけ記憶（非ブロッキング。取込ウィザード表示中なら反映後に再描画）
    void costAcctMapFetchPromise
      .then(async (mapRes) => {
        await applyCostAcctMapPayload(mapRes);
        if (uiState.costRegWizard) render();
      })
      .catch(() => {
        /* ignore */
      });
    // 経費データをサーバーから読み込む（メモリのみから脱却）
    if (expensesRes?.ok) {
      try {
        const edata = await expensesRes.json();
        if (Array.isArray(edata.expenses)) {
          expenses.length = 0;
          edata.expenses.forEach((e) => expenses.push({
            id: String(e.id), dbId: e.id,
            month: String(e.month || "").replace(/-/g, "/"),
            category: e.category || "",
            label: e.label || "",
            amount: Number(e.amount || 0),
            note: e.note || ""
          }));
        }
      } catch { /* ignore */ }
    }
    // tailFetches に 15 秒タイムアウトを設ける。
    // Vercel サーバーレス関数のコールドスタートで応答が遅延しても
    // applyHeavy() が無限待機にならないようにする。
    await Promise.race([
      Promise.all(tailFetches),
      new Promise((resolve) => setTimeout(resolve, 15000))
    ]);
    pruneProjectDocDraftsForExistingProjects();
    syncContractMasterFromClients();
    // localStorage に残っている書類メタをサーバーに一括アップロード（初回マイグレーション）
    void migrateDocMetaToServer();
  };
  const runHeavyWithLoadingUi = async () => {
    uiState.heavyDataLoading = true;
    queueMicrotask(() => {
      try {
        render();
      } catch {
        /* noop */
      }
    });
    // セーフティタイマー: 5 秒経過してもローディングが続く場合は強制解除する
    // rAF キューの詰まりを防ぐため renderScheduled を先にリセットしてから render()
    const _safetyTimer = setTimeout(() => {
      if (uiState.heavyDataLoading) {
        uiState.heavyDataLoading = false;
        // renderScheduled をリセットして rAF デバウンス by-pass
        renderScheduled = false;
        try { render(); } catch { /* noop */ }
      }
    }, 5000);
    try {
      await applyHeavy();
    } finally {
      clearTimeout(_safetyTimer);
      uiState.heavyDataLoading = false;
      renderScheduled = false;
      try {
        render();
      } catch {
        /* noop */
      }
    }
  };
  try {
    if (mode === "heavy") {
      /* 一覧を heavyDataLoading で置き換えると「空に見える」ため、直後の再同期だけ UI フラグを立てない */
      if (skipLoadingUi) {
        if (heavyDataLoadPromise) await heavyDataLoadPromise;
        await applyHeavy();
        return;
      }
      if (!heavyDataLoadPromise) {
        heavyDataLoadPromise = runHeavyWithLoadingUi().finally(() => {
          heavyDataLoadPromise = null;
        });
      }
      return heavyDataLoadPromise;
    }
    await applyCritical();
    if (mode === "critical") return;
    await runHeavyWithLoadingUi();
  } catch { /* ignore */ }
}

async function tryAutoLogin() {
  if (!authToken) return showLoginScreen();
  if (isAuthSessionExpired()) {
    doLogout();
    return;
  }
  // 認証そのものを拒否された（401/403）時だけ保持トークンを捨てる。タイムアウト・オフライン・5xx で
  // 捨てると、コールドスタートや電波の悪い場所で PWA を開いただけで「ログイン保持（7日）」が消えていた
  let authRejected = false;
  // /api/startup は /api/me の結果に依存しないので同時に取りに行く（以前は me → startup の順で待ち、
  // しばらく使っていないと起動待ちが2回分乗っていた）。me が失敗したら結果は使わずに捨てる
  const startupPrefetch = fetchStartupResponse().catch(() => ({ ok: false }));
  try {
    // /api/me に 15 秒タイムアウト（コールドスタート時に応答がない場合でもログイン画面へ遷移）
    const res = await Promise.race([
      fetch("/api/me", { headers: { Authorization: `Bearer ${authToken}` } }),
      new Promise((resolve) => setTimeout(() => resolve({ ok: false }), 15000))
    ]);
    if (!res.ok) {
      authRejected = res.status === 401 || res.status === 403;
      throw new Error();
    }
    const data = await res.json();
    authUser = data.user;
    Object.assign(currentUser, {
      id: `u${authUser.id}`,
      dbId: authUser.id,
      name: authUser.name,
      role: primaryJobRole(authUser),
      systemRole: systemRoleOfUser(authUser),
      email: authUser.email,
      roles: [primaryJobRole(authUser)],
      navPermissions: normalizeNavPermissions(authUser.navPermissions || authUser.nav_permissions),
      avatar: authUser.avatar != null && String(authUser.avatar).trim() ? String(authUser.avatar) : "",
      displayName: authUser.displayName || "",
      comment: authUser.comment || "",
      phone: authUser.phone || "",
      notifyEmail: authUser.notifyEmail !== false,
      notifySlack: authUser.notifySlack === true,
      notifyPush: authUser.notifyPush === true
    });
    uiState.auth.isLoggedIn = true;
    ensureProjectListFilterDefaultsOnce();
    uiState.auth.isLoading = true;
    hideLoginScreen();
    render();
    // セーフティタイマー: 15 秒経過しても critical load が終わらなければ強制的に画面を解放する
    // (loadDataFromDB は内部で全エラーをキャッチして throw しないため、これが最後の砦)
    const _criticalSafetyTimer = setTimeout(() => {
      if (uiState.auth.isLoading) {
        uiState.auth.isLoading = false;
        try { render(); } catch { /* noop */ }
      }
    }, 15000);
    await loadDataFromDB({ mode: "critical", startupPrefetch });
    clearTimeout(_criticalSafetyTimer);
    uiState.auth.isLoading = false;
    render();
    loadDataFromDB({ mode: "heavy" }).then(() => {
      render();
    });
    scheduleMidnightJstLogout();
    void syncTodoPushSubscriptionAfterLogin();
  } catch {
    uiState.auth.isLoading = false;
    if (authRejected) {
      sessionStorage.removeItem("fp_token"); localStorage.removeItem("fp_token");
      fpClearPersistedToken();
    }
    authToken = "";
    clearMidnightJstLogoutTimer();
    showLoginScreen();
    if (!authRejected) {
      const errEl = document.getElementById("loginError");
      const errText = document.getElementById("loginErrorText");
      if (errText) errText.textContent = "サーバーに接続できませんでした。通信状況を確認して再読み込みしてください（ログイン保持は残っています）";
      if (errEl) errEl.style.display = "block";
    }
  }
}

async function doLogin(email, password) {
  try {
    const res = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // ログイン保持チェックがONの場合、サーバー側で7日間有効なJWTを発行させる
      body: JSON.stringify({ email, password, keepLogin: fpKeepLoginEnabled() })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const base = typeof data.error === "string" ? data.error : "ログインに失敗しました";
      if (res.status === 503) return `${base}（サーバーが利用できない状態です。時間をおいて再度お試しください）`;
      if (res.status >= 500) return `${base}（HTTP ${res.status}・サーバー側エラー）`;
      if (res.status === 401) return base;
      return `${base}（HTTP ${res.status}）`;
    }
    authToken = data.token;
    authUser = data.user;
    sessionStorage.setItem("fp_token", authToken);
    fpPersistToken(authToken);
    Object.assign(currentUser, {
      id: `u${authUser.id}`,
      dbId: authUser.id,
      name: authUser.name,
      role: primaryJobRole(authUser),
      systemRole: systemRoleOfUser(authUser),
      email: authUser.email,
      roles: [primaryJobRole(authUser)],
      navPermissions: normalizeNavPermissions(authUser.navPermissions || authUser.nav_permissions),
      avatar: authUser.avatar != null && String(authUser.avatar).trim() ? String(authUser.avatar) : "",
      displayName: authUser.displayName || "",
      comment: authUser.comment || "",
      phone: authUser.phone || "",
      notifyEmail: authUser.notifyEmail !== false,
      notifySlack: authUser.notifySlack === true,
      notifyPush: authUser.notifyPush === true
    });
    uiState.auth.isLoggedIn = true;
    ensureProjectListFilterDefaultsOnce();
    uiState.auth.isLoading = true;
    hideLoginScreen();
    render();
    await loadDataFromDB({ mode: "critical" });
    uiState.auth.isLoading = false;
    render();
    loadDataFromDB({ mode: "heavy" }).then(() => render());
    scheduleMidnightJstLogout();
    void syncTodoPushSubscriptionAfterLogin();
    return null;
  } catch {
    uiState.auth.isLoading = false;
    return "通信エラーが発生しました";
  }
}

async function doDevLogin() {
  try {
    const res = await fetch("/api/dev-login", { method: "POST", headers: { "Content-Type": "application/json" } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const base = typeof data.error === "string" ? data.error : "開発ログインに失敗しました";
      return res.status ? `${base}（HTTP ${res.status}）` : base;
    }
    authToken = data.token;
    authUser = data.user;
    sessionStorage.setItem("fp_token", authToken);
    fpPersistToken(authToken);
    Object.assign(currentUser, {
      id: `u${authUser.id}`,
      dbId: authUser.id,
      name: authUser.name,
      role: primaryJobRole(authUser),
      systemRole: systemRoleOfUser(authUser),
      email: authUser.email,
      roles: [primaryJobRole(authUser)],
      navPermissions: normalizeNavPermissions(authUser.navPermissions || authUser.nav_permissions),
      avatar: authUser.avatar != null && String(authUser.avatar).trim() ? String(authUser.avatar) : "",
      displayName: authUser.displayName || "",
      comment: authUser.comment || "",
      phone: authUser.phone || "",
      notifyEmail: authUser.notifyEmail !== false,
      notifySlack: authUser.notifySlack === true,
      notifyPush: authUser.notifyPush === true
    });
    uiState.auth.isLoggedIn = true;
    ensureProjectListFilterDefaultsOnce();
    uiState.auth.isLoading = true;
    hideLoginScreen();
    render();
    await loadDataFromDB({ mode: "critical" });
    uiState.auth.isLoading = false;
    render();
    loadDataFromDB({ mode: "heavy" }).then(() => render());
    scheduleMidnightJstLogout();
    void syncTodoPushSubscriptionAfterLogin();
    return null;
  } catch {
    uiState.auth.isLoading = false;
    return "通信エラーが発生しました";
  }
}

async function doSetPassword(token, password) {
  try {
    const res = await fetch("/api/set-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const base = typeof data.error === "string" ? data.error : "設定に失敗しました";
      return res.status ? `${base}（HTTP ${res.status}）` : base;
    }
    return null;
  } catch {
    return "通信エラーが発生しました";
  }
}

/** ログアウトのたびに進む番号。取得の応答待ちの間にログアウトしたかを判定し、後着の結果で次の人の画面を汚さない
 *  （トークンはセッション延長でも変わるので使わない） */
let fpLogoutSeq = 0;

function doLogout() {
  fpLogoutSeq += 1;
  clearMidnightJstLogoutTimer();
  closeMidnightBedtimeModal();
  // 共有端末で前の人の ToDo 通知が届き続けないよう、この端末のプッシュ購読を外す
  // （サーバの購読行は本人のトークンでしか消せないので、authToken を消す前の値を渡す。
  //   同じ人が再ログインしたら syncTodoPushSubscriptionAfterLogin が購読し直す）
  void disableTodoPush(authToken);
  authToken = "";
  authUser = null;
  // 一覧の列幅・並び順（15z-table-prefs.js）も前の人の分を残さない
  fpTablePrefsReset();
  // 一覧の固定項目編集の編集中の選択も（15z-list-filter-fields.js）
  uiState.listFilterFieldsEdit = null;
  // 同一端末で別アカウントへ再ログインするときの残留情報を除去
  Object.assign(currentUser, {
    id: "",
    dbId: undefined,
    name: "",
    role: "営業",
    email: "",
    roles: ["営業"],
    navPermissions: [],
    displayName: "",
    comment: "",
    phone: "",
    avatar: "",
    password: ""
  });
  users.length = 0;
  clients.length = 0;
  projects.length = 0;
  // ドメイン一覧はレジストラ認証情報を含むため、ログアウトでメモリから確実に破棄する
  domains.length = 0;
  uiState.domainsLoaded = false;
  uiState.domainsLoading = false;
  uiState.domainsLoadError = false;
  uiState.domainsLightRequested = false;
  uiState.domainsLightPromise = null;
  uiState.auth.isLoading = false;
  uiState.heavyDataLoading = false;
  uiState.myAccountEditor = null;
  uiState.userEditor = null;
  // 貸与品（社員の貸与PC・携帯等）と編集中の下書きも別ユーザーへ持ち越さない
  uiState.userEquipment = null;
  uiState.equipEditor = null;
  uiState.myEquipment = null;
  uiState.equipmentApplications = [];
  uiState.equipmentApplicationsLoaded = false;
  uiState.equipApplyDraft = null;
  uiState.deptMemberModal = null;
  // このタブで既読にした課題の記録も次の人へ持ち越さない（12 reapplyProgressAgendaLocalReads）
  progressAgendaLocalReadAt.clear();
  // 顧客担当者・顧客支社キャッシュは権限（sales_hidden 除外範囲）依存なので別ユーザーへ持ち越さない
  uiState.clientContacts.rows = null;
  uiState.clientContacts.partnerRows = null;
  uiState.clientContacts.editor = null;
  uiState.clientBranches.rows = null;
  uiState.clientBranches.partnerRows = null;
  uiState.clientBranches.editor = null;
  // 郵送ステータスキャッシュも権限（案件閲覧範囲）依存なので別ユーザーへ持ち越さない
  uiState.docMailStatus = {};
  uiState.docMailStatusLoadedFor = {};
  uiState.sidebarUserMenuOpen = false;
  uiState.sidebarNotifyMenuOpen = false;
  sessionStorage.removeItem("fp_token"); localStorage.removeItem("fp_token");
  fpClearPersistedToken();
  uiState.projectDocsDraftDirty = {};
  uiState.projectBillingManualDirty = {};
  uiState.projectBillingLinesServerCopy = {};
  uiState.progressEditingMessageId = "";
  uiState.progressChallengeModalSubmitting = false;
  uiState.progressAgendaModalSubmitting = false;
  uiState.progressAttachmentUploadAgendaId = "";
  uiState.progressAttachmentUploadMessageId = "";
  uiState.projectListFiltersDefaultsApplied = false;
  // 一覧の選択・割当先の初期選択・取引先一覧の絞り込みも次のユーザーへ持ち越さない（2026-10 レビュー指摘）
  uiState.costAllocSelected = {};
  uiState.costAllocGroupPick = {};
  uiState.costAllocBulkProjectPick = "";
  uiState.costAllocBulkAssignBusy = false;
  if (uiState.projectTable) uiState.projectTable.selected = {};
  uiState.clientList.filters = { keyword: "", approval: "", contractOwner: "", sort: "" };
  uiState.clientList.page = 1;
  uiState.partnerList.filters = { keyword: "", approval: "", paymentMethod: "all", nda: "all", contract: "all", sort: "" };
  uiState.partnerList.page = 1;
  uiState.progressComposeAttach = { agendaId: "", files: [] };
  uiState.progressMessageSubmittingByAgenda = {};
  uiState.progressAgendaModalPendingFiles = [];
  closeProgressAgendaAttachModal();
  uiState.progressShowChallengeEditForm = false;
  uiState.progressChallengeEditDraft = { challengeId: "", title: "", body: "" };
  uiState.progressShowChallengeWiki = false;
  uiState.progressChallengeWikiDraft = { challengeId: "", wikiBody: "" };
  uiState.progressShowChallengeSettings = false;
  uiState.progressChallengeSettingsDraft = {
    challengeId: "",
    tab: "categories",
    statuses: [],
    categories: [],
    newStatus: "",
    newCategory: "",
    basicTitle: "",
    basicBody: "",
    projectId: "",
    clientPick: "",
    workKind: ""
  };
  uiState.progressChallengeWikiSubmitting = false;
  uiState.progressChallengeSettingsSubmitting = false;
  uiState.progressMgmtFetchInFlight = false;
  uiState.progressMgmtLoading = false;
  uiState.progressMgmtDetailLoading = false;
  uiState.progressMgmtSuppressEmptyRefetch = false;
  uiState.progressMgmtFetchFailCount = 0;
  uiState._pmIssueHydrateTried = "";
  uiState.budgetRowsLoadedByPeriod = {};
  uiState.budgetRowsLoadingByPeriod = {};
  uiState.budgetRowsLoadFailedByPeriod = {};
  budgetMasterRowsFetchPromises = {};
  // 未保存の予算マスタ編集はログアウトで失われる（次回ログインでサーバー値に上書きされる）ため、
  // 誤解を招く「未保存の変更があります」表示だけでも残さないよう消しておく
  uiState.budgetMasterDirty = false;
  budgetDraftMemoryStore = {};
  budgetServerSnapshot = {};
  uiState.progressMgmtLoadError = "";
  uiState.progressListFetchDone = { active: false, starred: false, archived: false };
  uiState.progressListCache = { active: null, starred: null, archived: null };
  uiState.progressAssignedListFetchDone = false;
  uiState.progressAssignedListCache = null;
  uiState.progressAssignedListCacheScope = "";
  // ToDoのプロジェクト一覧・表示中ツリー・ベル通知の対象は参加メンバー制限に依存するので別ユーザーへ持ち越さない
  // （残すと、同じタブで再ログインした人に前の人しか見えないプロジェクト名・課題名が出る）
  uiState.progressGroupListCache = { active: null, my_themes: null, starred: null, archived: null };
  uiState.progressGroupListFetchDone = { active: false, my_themes: false, starred: false, archived: false };
  uiState.progressChallenges = [];
  uiState.progressTodoGroups = [];
  uiState.progressMentionTargets = [];
  uiState.progressWatchTargets = [];
  uiState.progressAssignedToMeTargets = [];
  uiState.progressWatchUnreadCount = 0;
  uiState.progressMentionUnreadCount = 0;
  uiState.progressAssignedToMeCount = 0;
  uiState.progressAssignedActiveTotal = 0;
  uiState.progressCreatedActiveTotal = 0;
  uiState.progressMyThemeCount = null;
  uiState.cardCostChecklist = null; // カード決済チェックの一覧・開閉も別の人へ持ち越さない
  // 経費・未割当原価・確認メモ・捺印状態・各種申請も権限で取得範囲が変わるので持ち越さない。
  // 読込済みフラグが残ると次の人の権限で取り直さず、前の人の取得結果をそのまま表示していた
  expenses.length = 0;
  uiState.orphanCosts = [];
  uiState.partnersCache = [];
  uiState.appMgmtReviewMemos = {};
  uiState.docSealStatus = {};
  uiState.docSealApprovalProgress = {};
  uiState.projectUnlockApplications = null;
  uiState.allowedTools = [];
  uiState.allowedToolApplications = [];
  uiState.allowedToolsLoaded = false;
  uiState.developmentApplications = [];
  uiState.developmentApplicationsLoaded = false;
  uiState.developments = [];
  uiState.developmentsLoaded = false;
  // 請求明細の下書きと最後に見たサーバーのハッシュは利用者別でない localStorage にあるため、
  // 残すと前の人の未保存の下書きが次の人のトークンで自動同期される（金額入りの明細・操作者のすり替わり）
  uiState.projectDocsDrafts = {};
  uiState.projectBillingLinesServerSnapshot = {};
  billingLinesServerHashCache = null;
  // 書き込み待ちのタイマーが残ると、消した直後に前の人のハッシュを書き戻す
  if (billingLinesServerHashSaveTimer) clearTimeout(billingLinesServerHashSaveTimer);
  billingLinesServerHashSaveTimer = null;
  try {
    localStorage.removeItem(PROJECT_DOC_DRAFTS_LS_KEY);
    localStorage.removeItem(BILLING_LINES_SERVER_HASH_LS_KEY);
  } catch {
    /* ignore */
  }
  // 招待リンク（#set-password:<トークン>）のハッシュは消さない。
  // 生文字列比較だとトークン付きハッシュに一致せず設定画面に到達できなくなるため、ルート判定で行う
  if (currentRoute().page !== "set-password") {
    history.replaceState(null, "", `${location.pathname}${location.search}#dashboard`);
  }
  // 次のログインで /api/departments を必ず再取得しやすくする（全員同じ定義の共有DB）
  try {
    localStorage.removeItem(DEPT_LOCAL_FALLBACK_KEY);
  } catch {
    /* ignore */
  }
  showLoginScreen();
}

/**
 * セッション失効（JWTの期限切れ／「全デバイスからログアウト」による失効）を
 * バックグラウンド処理が 401 で検知したときの共通処理。
 * authToken をクリアして各種ポーリングを止め（`if (!authToken) return` で自然に停止）、
 * ログイン画面へ誘導する。`!authToken` ガードで二重実行を防ぎ、再ログインで自動的に再武装する。
 */
function handleSessionExpired() {
  if (!authToken) return; // 既にログアウト済みなら何もしない
  doLogout();
  const errEl = document.getElementById("loginError");
  const errText = document.getElementById("loginErrorText");
  if (errText) errText.textContent = "セッションの有効期限が切れています。再ログインしてください";
  if (errEl) errEl.style.display = "block";
}

/** 搭乗券ログイン: BOARDING の日付・時刻 */
function boardingPassMetaNow() {
  const now = new Date();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return {
    date: `${now.getDate()} ${months[now.getMonth()]}`,
    time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
  };
}

function boardingPassLoginErrorHtml() {
  return `<div class="login-error" id="loginError" style="display:none;" role="alert">
    <div class="login-error-row"><span id="loginErrorText"></span><button type="button" class="login-error-close" id="loginErrorClose">閉じる</button></div>
  </div>`;
}

// 飛行機シルエット（上向き）。絵文字✈はOSにより小さな単色グリフで描画され
// 虫に見えるため、SVGで描く。向きはCSSのrotateで進行方向に合わせる。
function loginPlaneSvg(cls) {
  return `<svg class="${cls}" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>`;
}

function boardingPassCardTopHtml() {
  const { date, time } = boardingPassMetaNow();
  return `
    <div class="pass-top">
      <div class="header-row">
        <div class="logo"><span class="on">On </span><span class="boarding">boarding</span></div>
        <div class="header-meta">
          <div>
            <div class="bp-label">搭乗日</div>
            <div class="bp-value" id="loginBoardingDate">${escapeHtml(date)}</div>
          </div>
          <div>
            <div class="bp-label">搭乗口</div>
            <div class="bp-value">OneEighty</div>
          </div>
        </div>
      </div>
      <div class="route">
        <div class="route-end">
          <div class="iata">NOW</div>
          <div class="route-place">現在地</div>
        </div>
        <div class="route-mid">
          <div class="route-dash"></div>
          ${loginPlaneSvg("plane")}
          <div class="route-dash"></div>
        </div>
        <div class="route-end">
          <div class="iata">NEXT</div>
          <div class="route-place">目的地</div>
        </div>
      </div>
      <div class="details-row">
        <div><div class="bp-label">FLIGHT</div><div class="bp-value-lg">GD-JOB</div></div>
        <div><div class="bp-label">BOARDING</div><div class="bp-value-lg" id="loginBoardingTime">${escapeHtml(time)}</div></div>
        <div><div class="bp-label">SEAT</div><div class="bp-value-lg">1A</div></div>
      </div>
      <div class="subtitle-line">ココロもシゴトも、旅するように。</div>
    </div>
    <div class="tear" aria-hidden="true">
      <div class="tear-dot"></div>
      <div class="tear-line"></div>
      <div class="tear-dot"></div>
    </div>`;
}

function boardingPassLoginShellHtml(passBottomHtml) {
  return `
    <h1 class="login-screen-visually-hidden">On boarding</h1>
    <div class="wrap">
      <div class="pass">
        ${boardingPassCardTopHtml()}
        <div class="pass-bottom">${passBottomHtml}</div>
      </div>
    </div>`;
}

function bindLoginErrorClose() {
  const closeBtn = document.getElementById("loginErrorClose");
  if (!closeBtn) return;
  closeBtn.addEventListener("click", () => {
    const errEl = document.getElementById("loginError");
    const errText = document.getElementById("loginErrorText");
    if (errEl) errEl.style.display = "none";
    if (errText) errText.textContent = "";
  });
}

function showLoginScreen() {
  const route = currentRoute();
  const isSetPassword = route.page === "set-password";
  const pwToken = isSetPassword ? route.id : "";

  document.title = ensureAppDocumentTitleBase();
  revokeFaviconBadgeObjectUrl();
  setDocumentFaviconHref("/favicon.png");
  document.querySelector(".app-layout").style.display = "none";
  let loginEl = document.getElementById("loginScreen");
  if (!loginEl) {
    loginEl = document.createElement("div");
    loginEl.id = "loginScreen";
    document.body.appendChild(loginEl);
  }
  loginEl.style.display = "flex";

  if (isSetPassword) {
    loginEl.innerHTML = boardingPassLoginShellHtml(`
        ${boardingPassLoginErrorHtml()}
        <div class="login-success" id="loginSuccess" style="display:none;"></div>
        <div class="field">
          <label for="newPass1">新しいパスワード（8文字以上）</label>
          <input type="password" id="newPass1" placeholder="パスワード" autocomplete="new-password" />
        </div>
        <div class="field">
          <label for="newPass2">パスワード確認</label>
          <input type="password" id="newPass2" placeholder="もう一度入力" autocomplete="new-password" />
        </div>
        <button type="button" class="btn" id="setPassBtn">パスワードを設定</button>
        <p class="footer-note"><a href="#dashboard">ログイン画面に戻る</a></p>
      `);
    bindLoginErrorClose();
    document.getElementById("setPassBtn").addEventListener("click", async (e) => {
      const btn = e.currentTarget;
      const p1 = document.getElementById("newPass1").value;
      const p2 = document.getElementById("newPass2").value;
      const errEl = document.getElementById("loginError");
      const errText = document.getElementById("loginErrorText");
      const sucEl = document.getElementById("loginSuccess");
      errEl.style.display = "none";
      sucEl.style.display = "none";
      if (errText) errText.textContent = "";
      if (p1 !== p2) {
        if (errText) errText.textContent = "パスワードが一致しません";
        errEl.style.display = "block";
        return;
      }
      if (p1.length < 8) {
        if (errText) errText.textContent = "8文字以上で設定してください";
        errEl.style.display = "block";
        return;
      }
      btn.disabled = true; // 送信中の二重送信防止
      const err = await doSetPassword(pwToken, p1);
      if (err) {
        btn.disabled = false;
        if (errText) errText.textContent = err;
        errEl.style.display = "block";
      } else {
        // 招待トークンは1回で失効するため、成功後は再送信させずログイン画面へ誘導する
        sucEl.textContent = "パスワードを設定しました。ログイン画面へ移動します…";
        sucEl.style.display = "block";
        // ユーザーが3秒以内に自分で移動していたら画面を奪わない
        const hashAtSuccess = location.hash;
        setTimeout(() => {
          if (location.hash === hashAtSuccess) location.hash = "#dashboard";
        }, 3000);
      }
    });
  } else {
    const isLocal = location.hostname === "localhost" || location.hostname === "127.0.0.1";
    loginEl.innerHTML = boardingPassLoginShellHtml(`
        ${boardingPassLoginErrorHtml()}
        <div class="field">
          <label for="loginEmail">メールアドレス</label>
          <input type="email" id="loginEmail" placeholder="name@o-eighty.com" value="" autocomplete="username" />
        </div>
        <div class="field">
          <label for="loginPass">パスワード</label>
          <input type="password" id="loginPass" placeholder="パスワード" value="" autocomplete="current-password" />
        </div>
        <label class="login-keep-label">
          <input type="checkbox" id="loginKeepCheck" ${fpKeepLoginEnabled() ? "checked" : ""} />
          <span>ログイン状態を保持する（${FP_SESSION_DAYS}日間）</span>
        </label>
        <button type="button" class="btn" id="loginBtn">ログイン</button>
        <p class="footer-note"><a href="#" id="forgotPassLink">パスワードをお忘れの方</a></p>
        ${isLocal ? `<button type="button" class="btn-dev" id="devLoginBtn">開発用ログイン</button>
        <p class="login-hint">ローカル開発: API に <code>ALLOW_LOCAL_DEV_LOGIN=1</code> と <code>LOCAL_DEV_EMAIL</code> / <code>LOCAL_DEV_PASSWORD</code> を設定</p>` : ""}
        <p class="footer-note">o-eighty.com / o-eighty.co.jp のアドレスでログイン可能</p>
      `);
    bindLoginErrorClose();
    const loginBtn = document.getElementById("loginBtn");
    loginBtn.addEventListener("click", async () => {
      // 送信中の二重送信防止（パスワード設定側と同じ）。スマホで応答が遅いと連打・Enter連打が
      // そのまま何回も POST /api/login に届き、成功後の初期読込が同時に何本も走って画面が固まる
      // （本番の監査ログで 2 秒間に 11 回のログイン成功が実際に記録された）。
      if (loginBtn.disabled) return;
      loginBtn.disabled = true;
      try {
        await submitLogin();
      } finally {
        loginBtn.disabled = false;
      }
    });
    async function submitLogin() {
      const email = document.getElementById("loginEmail").value;
      const pass = document.getElementById("loginPass").value;
      const keepCheck = document.getElementById("loginKeepCheck");
      // チェックボックスの状態を先に保存（doLogin内でfpPersistTokenが参照する）
      try {
        if (keepCheck?.checked) localStorage.setItem(FP_KEEP_LOGIN_KEY, "1");
        else { localStorage.setItem(FP_KEEP_LOGIN_KEY, "0"); fpClearPersistedToken(); }
      } catch { /* noop */ }
      const errEl = document.getElementById("loginError");
      const errText = document.getElementById("loginErrorText");
      errEl.style.display = "none";
      if (errText) errText.textContent = "";
      const err = await doLogin(email, pass);
      if (err) {
        if (errText) errText.textContent = err;
        errEl.style.display = "block";
      }
    }
    document.getElementById("loginPass").addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !loginBtn.disabled) loginBtn.click();
    });
    document.getElementById("forgotPassLink").addEventListener("click", (e) => {
      e.preventDefault();
      showForgotPasswordForm(loginEl, document.getElementById("loginEmail").value);
    });
    const devLoginBtn = document.getElementById("devLoginBtn");
    if (devLoginBtn) {
      devLoginBtn.addEventListener("click", async () => {
        const errEl = document.getElementById("loginError");
        const errText = document.getElementById("loginErrorText");
        errEl.style.display = "none";
        if (errText) errText.textContent = "";
        const err = await doDevLogin();
        if (err) {
          if (errText) errText.textContent = err;
          errEl.style.display = "block";
        }
      });
    }
  }
}

/** パスワードを忘れた人向け: メールアドレスを入れて再設定リンク（#set-password:<token>）をメールで受け取る */
function showForgotPasswordForm(loginEl, initialEmail) {
  loginEl.innerHTML = boardingPassLoginShellHtml(`
      ${boardingPassLoginErrorHtml()}
      <div class="login-success" id="loginSuccess" style="display:none;"></div>
      <p class="login-hint forgot-lead">登録済みのメールアドレスを入力してください。パスワード再設定用のリンクをお送りします（有効期限1時間）。</p>
      <div class="field">
        <label for="forgotEmail">メールアドレス</label>
        <input type="email" id="forgotEmail" placeholder="name@o-eighty.com" value="${escapeHtml(initialEmail || "")}" autocomplete="username" />
      </div>
      <button type="button" class="btn" id="forgotSendBtn">再設定メールを送る</button>
      <p class="footer-note"><a href="#" id="forgotBackLink">ログイン画面に戻る</a></p>
    `);
  bindLoginErrorClose();
  const sendBtn = document.getElementById("forgotSendBtn");
  const emailInput = document.getElementById("forgotEmail");
  sendBtn.addEventListener("click", async () => {
    const errEl = document.getElementById("loginError");
    const errText = document.getElementById("loginErrorText");
    const sucEl = document.getElementById("loginSuccess");
    errEl.style.display = "none";
    sucEl.style.display = "none";
    const email = emailInput.value.trim();
    if (!email) {
      errText.textContent = "メールアドレスを入力してください";
      errEl.style.display = "block";
      return;
    }
    sendBtn.disabled = true;
    const { ok, status, data } = await apiFetch("/api/forgot-password", { method: "POST", body: { email } });
    if (!ok) {
      sendBtn.disabled = false;
      errText.textContent = typeof data.error === "string" ? data.error : status ? `送信に失敗しました（HTTP ${status}）` : "通信エラーが発生しました";
      errEl.style.display = "block";
      return;
    }
    // 連打で何通も送らないよう、成功後はボタンを戻さない（送り直したい場合は画面を開き直す）
    sucEl.textContent = data.message || "再設定用のメールを送信しました";
    sucEl.style.display = "block";
  });
  emailInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !sendBtn.disabled) sendBtn.click();
  });
  document.getElementById("forgotBackLink").addEventListener("click", (e) => {
    e.preventDefault();
    showLoginScreen();
  });
  emailInput.focus();
}

function hideLoginScreen() {
  const loginEl = document.getElementById("loginScreen");
  if (loginEl) loginEl.style.display = "none";
  document.querySelector(".app-layout").style.display = "";
}

const appRoot = document.getElementById("appRoot");
const fpModalLayer = document.getElementById("fp-modal-layer");
const navPrimary = document.getElementById("navPrimary");
const navMgmt = document.getElementById("navMgmt");
const navStages = document.getElementById("navStages");
const navSettings = document.getElementById("navSettings");
const sidebarUserName = document.getElementById("sidebarUserName");
const sidebarUserAvatar = document.getElementById("sidebarUserAvatar");
const sidebarUserRoot = document.querySelector(".sidebar-user");
const sidebarUserToggle = document.querySelector("[data-sidebar-user-toggle]");
const sidebarNotifyToggle = document.querySelector("[data-sidebar-notify-toggle]");
const sidebarNotifyMenu = document.getElementById("sidebarNotifyMenu");
const sidebarNotifyBadge = document.getElementById("sidebarNotifyBadge");
const appLayout = document.querySelector(".app-layout");

const yen = new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY", maximumFractionDigits: 0 });
/** ￥記号なしの金額表記（書類など） */
function formatYenPlain(n) {
  return new Intl.NumberFormat("ja-JP", { maximumFractionDigits: 0 }).format(Number(n) || 0);
}

const LINE_STAFF_LS_KEY = "fp_staff_line_assign_v1";
/** 請求明細の割当先が「部署全体」のときのストレージ用プレフィックス（例: dept:abc123） */
const LINE_ASSIGNEE_DEPT_PREFIX = "dept:";

function parseLineAssigneeDeptId(token) {
  const s = String(token || "").trim();
  if (!s.startsWith(LINE_ASSIGNEE_DEPT_PREFIX)) return null;
  const rest = s.slice(LINE_ASSIGNEE_DEPT_PREFIX.length).trim();
  return rest || null;
}

function lineAssigneeDeptToken(deptId) {
  const id = String(deptId || "").trim();
  return id ? `${LINE_ASSIGNEE_DEPT_PREFIX}${id}` : "";
}

/** 担当者割当で選べる部署（名称ありのもの、名前順） */
function staffAssignableDepartments() {
  const list = (uiState.deptSettings?.departments || []).filter((d) => String(d.name || "").trim());
  return list.slice().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ja"));
}

/** 割当トークンを「案件担当の集計・一覧絞り込み」用にユーザー id へ広げる（部署はメンバー一覧） */
function expandSingleLineAssigneeToken(token) {
  const s = String(token || "").trim();
  if (!s) return [];
  const deptId = parseLineAssigneeDeptId(s);
  if (deptId != null) {
    const d = (uiState.deptSettings?.departments || []).find((x) => String(x.id) === String(deptId));
    if (d && Array.isArray(d.memberIds) && d.memberIds.length) return d.memberIds.map(String);
    return [];
  }
  return [s];
}

function lineAssigneeDisplayLabel(assigneeToken) {
  const s = String(assigneeToken || "").trim();
  if (!s) return "";
  const deptId = parseLineAssigneeDeptId(s);
  if (deptId != null) {
    const d = (uiState.deptSettings?.departments || []).find((x) => String(x.id) === String(deptId));
    return d ? `${String(d.name)}（部署全体）` : `部署(${deptId})`;
  }
  const u = users.find((x) => String(x.id) === s);
  return u ? String(u.name || "") : s;
}

/** 担当者割当の「担当者で絞り込み」: 直接割当 or 部署割当でメンバーに含まれる */
function lineAssigneesMatchStaffFilterUser(assignIds, filterUserId) {
  const fid = String(filterUserId || "").trim();
  if (!fid) return false;
  for (const aid of assignIds || []) {
    const expanded = expandSingleLineAssigneeToken(aid);
    if (expanded.some((u) => String(u) === fid)) return true;
  }
  return false;
}

/** normal 行の担当 user id 一覧（複数可）。assignedUserIds を優先し、無ければ assignedUserId を1件として扱う */
function normalizeLineAssigneeIds(line) {
  if (!line || (line.lineType || "normal") !== "normal") return [];
  if (Array.isArray(line.assignedUserIds) && line.assignedUserIds.length) {
    const dedup = [...new Set(line.assignedUserIds.map(String).map((s) => s.trim()).filter(Boolean))];
    line.assignedUserIds = dedup;
    line.assignedUserId = dedup[0] || "";
    return dedup;
  }
  const one = String(line.assignedUserId || "").trim();
  if (!one) {
    line.assignedUserIds = [];
    return [];
  }
  line.assignedUserIds = [one];
  return [one];
}

function setLineAssigneeIds(line, ids) {
  const clean = [...new Set((ids || []).map(String).map((s) => s.trim()).filter(Boolean))];
  line.assignedUserIds = clean;
  line.assignedUserId = clean[0] || "";
}

/** 請求明細の normal 行に付いた担当者 user id（メモリのドラフト優先、無ければ LS） */
function collectNormalLineAssignedUserIdsForProject(projectOrId) {
  const pid =
    typeof projectOrId === "object" && projectOrId != null && projectOrId.id != null
      ? String(projectOrId.id)
      : String(projectOrId || "");
  if (!pid) return new Set();
  const ids = new Set();
  const draft = uiState.projectDocsDrafts[pid];
  if (draft?.lines?.length) {
    for (const line of draft.lines) {
      if ((line.lineType || "normal") !== "normal") continue;
      for (const uid of normalizeLineAssigneeIds(line)) {
        if (!uid) continue;
        for (const x of expandSingleLineAssigneeToken(uid)) {
          if (x) ids.add(x);
        }
      }
    }
  }
  if (ids.size > 0) return ids;
  try {
    const o = JSON.parse(localStorage.getItem(LINE_STAFF_LS_KEY) || "{}");
    const arr = o[pid];
    if (Array.isArray(arr)) {
      for (const v of arr) {
        if (typeof v !== "string" || !v.trim()) continue;
        const s = v.trim();
        if (s.includes("|")) {
          s.split("|").forEach((x) => {
            const t = x.trim();
            if (t) ids.add(t);
          });
        } else ids.add(s);
      }
    }
  } catch {
    /* ignore */
  }
  return ids;
}

/** 明細行の担当が1名に確定したとき案件の assignedUserId と揃える（複数名のときは一覧フィルタ用にクリア） */
function syncProjectAssignedUserFromInvoiceLines(projectOrId) {
  const pid =
    typeof projectOrId === "object" && projectOrId != null && projectOrId.id != null
      ? String(projectOrId.id)
      : String(projectOrId || "");
  const proj = projects.find((x) => String(x.id) === pid);
  if (!proj) return;
  const ids = collectNormalLineAssignedUserIdsForProject(pid);
  if (ids.size === 1) proj.assignedUserId = [...ids][0];
  else if (ids.size > 1) proj.assignedUserId = "";
}

function persistAllLineStaffForProject(projectId) {
  const pid = String(projectId || "");
  if (!pid) return;
  // 担当者情報は billing_lines_json としてサーバーに保存済みのため
  // localStorage への書き込みは廃止。読み込みのみ移行期フォールバックとして維持。
  persistProjectDocDraftsStore();
  syncProjectAssignedUserFromInvoiceLines(pid);
}

function hydrateLineStaffForProject(projectId) {
  const pid = String(projectId || "");
  const d = uiState.projectDocsDrafts[pid];
  if (!pid || !d?.lines || !Array.isArray(d.lines)) return;
  let o = {};
  try {
    o = JSON.parse(localStorage.getItem(LINE_STAFF_LS_KEY) || "{}");
  } catch {
    return;
  }
  const arr = o[pid];
  if (!Array.isArray(arr)) return;
  const n = Math.min(d.lines.length, arr.length);
  for (let i = 0; i < n; i += 1) {
    const line = d.lines[i];
    if ((line.lineType || "normal") !== "normal") continue;
    // 旧データは書き込みを廃止して更新されないうえ行番号で対応するため、担当者が入っている行は上書きしない
    // （以前は開くたびに旧値へ戻し、並べ替え後は別の行の担当者が保存されていた）。空の行の補完だけに使う
    if (String(line.assignedUserId || "").trim() || (Array.isArray(line.assignedUserIds) && line.assignedUserIds.length)) continue;
    if (typeof arr[i] === "string") {
      const raw = arr[i].trim();
      if (!raw) {
        continue;
      } else if (raw.includes("|")) {
        const ids = raw.split("|").map((x) => x.trim()).filter(Boolean);
        line.assignedUserIds = ids;
        line.assignedUserId = ids[0] || "";
      } else {
        line.assignedUserId = raw;
        line.assignedUserIds = [raw];
      }
    }
  }
}

function ensureProjectDocDraft(projectId) {
  const pid = String(projectId || "");
  if (!pid) return null;
  if (!uiState.projectDocsDrafts[pid]) {
    uiState.projectDocsDrafts[pid] = { lines: [], meta: {}, remark: {} };
    hydrateLineStaffForProject(pid);
    return uiState.projectDocsDrafts[pid];
  }
  const d = uiState.projectDocsDrafts[pid];
  if (!Array.isArray(d.lines)) d.lines = [];
  if (!d.meta || typeof d.meta !== "object") d.meta = {};
  if (!d.remark || typeof d.remark !== "object") d.remark = {};
  d.lines.forEach((line) => {
    if ((line.lineType || "normal") === "normal") {
      if (line.assignedUserId === undefined || line.assignedUserId === null) line.assignedUserId = "";
      normalizeLineAssigneeIds(line);
    }
  });
  hydrateLineStaffForProject(pid);
  return d;
}

/** 請求項目 `lines` 内で `from` 行を `to` 行の位置へ移動（↑↓・DnD 共通） */
function moveProjectDetailLine(lines, from, to) {
  if (!Array.isArray(lines) || from === to) return;
  if (from < 0 || to < 0 || from >= lines.length || to >= lines.length) return;
  const [row] = lines.splice(from, 1);
  let insertAt = to;
  if (from < to) insertAt = to - 1;
  lines.splice(insertAt, 0, row);
}

const PROJECT_DOC_DRAFTS_LS_KEY = "fp_project_doc_drafts_v1";

function readProjectDocDraftsStore() {
  try {
    const text = localStorage.getItem(PROJECT_DOC_DRAFTS_LS_KEY) || "{}";
    // 破損・肥大化した下書きで初期表示が重くなるのを防ぐ
    if (text.length > 2_000_000) return {};
    const raw = JSON.parse(text);
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function persistProjectDocDraftsStore() {
  try {
    localStorage.setItem(PROJECT_DOC_DRAFTS_LS_KEY, JSON.stringify(uiState.projectDocsDrafts || {}));
  } catch {
    /* ignore quota */
  }
}

let persistProjectDocDraftsTimer = null;
function schedulePersistProjectDocDraftsStore() {
  if (persistProjectDocDraftsTimer) clearTimeout(persistProjectDocDraftsTimer);
  persistProjectDocDraftsTimer = setTimeout(() => {
    persistProjectDocDraftsTimer = null;
    persistProjectDocDraftsStore();
  }, 250);
}

// ── 書類メタ（支払期限・備考等）のサーバー保存・ロード ──

/** メタ保存タイマーキャッシュ（プロジェクトID → timer） */
const _docMetaSaveTimers = {};
/** 未送信の編集（プロジェクトID → { [書類タブ]: { fields: Set<metaキー>, remark: boolean } }）。
 *  保存は編集したタブだけを docType 指定で送り、他のタブ（他の人の編集を含む）を手元のコピーで上書きしない。
 *  サーバーからの読込でも、ここにある項目だけは手元の入力を残す。 */
const _docMetaPendingEdits = {};

/**
 * 書類メタ（meta/remark）をサーバーに保存。
 * 入力のたびに呼ばれるので 1.5秒デバウンスで実際の PUT を遅延。
 * tab と metaField で何を編集したかを記録する（metaField が null なら備考の編集）。
 * 書類画面では記録だけして送らず、「保存」ボタンで確定する（15z-doc-page-save.js）。
 */
function scheduleDocMetaSaveToServer(pid, tab, metaField) {
  if (!authToken) return;
  if (tab) {
    const byTab = _docMetaPendingEdits[pid] || (_docMetaPendingEdits[pid] = {});
    const ed = byTab[tab] || (byTab[tab] = { fields: new Set(), remark: false });
    if (metaField) ed.fields.add(String(metaField));
    else ed.remark = true;
    if (currentRoute().page === "project-docs") return;
  }
  if (_docMetaSaveTimers[pid]) clearTimeout(_docMetaSaveTimers[pid]);
  _docMetaSaveTimers[pid] = setTimeout(() => {
    delete _docMetaSaveTimers[pid];
    void persistDocMetaToServer(pid);
  }, 1500);
}

/**
 * 書類メタをタブ単位でサーバーへ保存する。tabs 省略時は未送信の編集があるタブ。
 * サーバーの保存済み内容を読み込む前には送らない（手元の不完全なコピーでそのタブを上書きしないため。
 * 未読込なら先に読み込み、編集した項目だけを読込結果に重ねてから送る）。
 * @returns {Promise<{ ok: boolean, error?: string }>} 失敗時は編集を未送信のまま残す（次の保存で再送）
 */
async function persistDocMetaToServer(pid, tabs = null) {
  if (!authToken) return { ok: true };
  const proj = projects.find((p) => p.id === pid);
  if (!proj?.dbId) return { ok: true };
  const pending = _docMetaPendingEdits[pid] || {};
  const sendTabs = [...new Set(tabs || Object.keys(pending))];
  if (!uiState.projectDocsDrafts?.[pid] || sendTabs.length === 0) return { ok: true };
  if (!uiState._docMetaServerLoaded?.[pid]) {
    await loadDocMetaFromServer(pid);
    if (!uiState._docMetaServerLoaded?.[pid]) {
      const error = "保存済みの書類設定を読み込めませんでした";
      showToastError(`支払期限・備考を保存できませんでした：${error}`);
      return { ok: false, error };
    }
  }
  const draft = uiState.projectDocsDrafts[pid];
  for (const tab of sendTabs) {
    const body = { projectId: proj.dbId, docType: tab };
    if (draft.meta?.[tab] && typeof draft.meta[tab] === "object") body.meta = draft.meta[tab];
    if (draft.remark && Object.prototype.hasOwnProperty.call(draft.remark, tab)) body.remark = String(draft.remark[tab] ?? "");
    if (body.meta === undefined && body.remark === undefined) continue;
    // 送信中の新しい編集は残す（送った時点までの編集だけを未送信から外す）
    const sentEdit = pending[tab];
    if (sentEdit) delete pending[tab];
    const { ok, status, data } = await apiFetch("/api/project-doc-meta", { method: "PUT", body });
    if (!ok) {
      if (sentEdit) {
        const byTab = _docMetaPendingEdits[pid] || (_docMetaPendingEdits[pid] = {});
        const cur = byTab[tab];
        byTab[tab] = cur
          ? { fields: new Set([...sentEdit.fields, ...cur.fields]), remark: sentEdit.remark || cur.remark }
          : sentEdit;
      }
      console.warn("persistDocMetaToServer failed: HTTP", status);
      const error = data?.error || (status ? `HTTP ${status}` : "通信エラー");
      showToastError(`支払期限・備考を保存できませんでした：${error}`);
      return { ok: false, error };
    }
    // 捺印対象の書類の内容が変わると、サーバーが捺印申請を未申請に戻す（api/project-doc-meta.js）
    if (data?.sealResetTabs?.length) applyServerDocSealReset(pid, data.sealResetTabs);
  }
  if (_docMetaPendingEdits[pid] && Object.keys(_docMetaPendingEdits[pid]).length === 0) delete _docMetaPendingEdits[pid];
  return { ok: true };
}

/**
 * 保留中のデバウンス保存タイマーがあれば即座に確定させる。
 * 正式PDF発行（サーバーがDBの保存済みデータから生成する）の直前など、
 * 「保存を待ってから叩く」ことを保証したいときに使う。
 * tabs（書類タブ1つ or 配列）を渡すと、そのタブは未編集でも送る（画面に出している初期値＝支払期限の
 * 自動計算等を正式PDF・メール・郵送と揃える）。ただしロック中の案件では未編集タブは送らない
 * （初期値が保存済みと違うだけで 403 になり発行・送付が止まるため。ロック中はサーバーの保存済み内容で出す）。
 * @returns {Promise<{ ok: boolean, error?: string }>} ok=false なら呼び出し側は発行・送付を止める
 */
async function flushDocMetaSaveToServer(pid, tabs = null) {
  if (_docMetaSaveTimers[pid]) {
    clearTimeout(_docMetaSaveTimers[pid]);
    delete _docMetaSaveTimers[pid];
  }
  const sendTabs = Object.keys(_docMetaPendingEdits[pid] || {});
  const proj = projects.find((p) => p.id === pid);
  if (tabs && !isProjectLocked(proj)) sendTabs.push(...(Array.isArray(tabs) ? tabs : [tabs]));
  return persistDocMetaToServer(pid, sendTabs);
}

/**
 * 正式PDF・メール送付・郵送の直前に、未保存の請求項目（明細）を保存する。書類はサーバーの保存済み内容から
 * 作られるため、自動保存（約1.6秒後）より先に送ると画面と違う古い金額の書類が出てしまう。
 * 保存すると内容変更で捺印申請が未申請に戻ることがある（サーバーの判定を applyServerDocSealReset で反映）ため、
 * 承認済みだったタブが戻っていたら一度止める。未承認でも送れる（2026-10 送付の制御をなくした）が、そのまま送ると
 * 社印付きのつもりで社印なしの書類が先方に届くため。知らせたあとにもう一度押せば社印なしで送れる。
 * @returns {Promise<{ ok: boolean, error?: string, sealReset?: boolean }>} ok=false なら呼び出し側は中止する
 */
async function flushDocBillingLinesBeforeSend(pid, tabs) {
  if (!uiState.projectDocsDraftDirty[pid]) return { ok: true };
  const proj = projects.find((p) => p.id === pid);
  if (!proj) return { ok: false, error: "案件が見つかりません" };
  const tabList = Array.isArray(tabs) ? tabs : [tabs];
  const approvedBefore = tabList.filter((t) => docTabHasSealWorkflow(t) && getDocSealStatus(pid, t) === "承認済");
  try {
    await persistProjectDetailToServer(proj, { includeBillingLines: true });
  } catch (e) {
    // 409（他の人が更新済み）・ロック等はサーバーの理由をそのまま出す
    return { ok: false, error: String(e?.serverMessage || "").trim() || "請求項目の保存に失敗しました。もう一度お試しください" };
  }
  if (approvedBefore.some((t) => getDocSealStatus(pid, t) !== "承認済")) {
    return { ok: false, sealReset: true, error: "請求項目の変更により捺印申請が未申請に戻りました（このまま送ると社印なしになります）。社印付きで送るには再度申請してください" };
  }
  return { ok: true };
}

/**
 * 初回マイグレーション: localStorage に残っている書類メタを全案件分まとめてサーバーへ送信。
 * 既にサーバーにデータがある案件は上書きしない（GET して空の場合のみ PUT）。
 * 1度実行したら localStorage フラグで以後スキップ。
 */
async function migrateDocMetaToServer() {
  if (!authToken) return;
  const FLAG = "fp_doc_meta_migrated_v1";
  if (localStorage.getItem(FLAG) === "1") return;
  const drafts = uiState.projectDocsDrafts || {};
  const pids = Object.keys(drafts).filter((pid) => {
    const d = drafts[pid];
    return (
      (d.meta && Object.keys(d.meta).some((t) => Object.keys(d.meta[t] || {}).length > 0)) ||
      (d.remark && Object.values(d.remark || {}).some((v) => String(v || "").trim()))
    );
  });
  let ok = 0;
  let failed = 0;
  for (const pid of pids) {
    const proj = projects.find((p) => p.id === pid);
    if (!proj?.dbId) continue;
    try {
      // GET して未保存（空）の場合のみ PUT
      const getRes = await fetch(
        `/api/project-doc-meta?projectId=${encodeURIComponent(proj.dbId)}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!getRes.ok) { failed++; continue; }
      const existing = await getRes.json().catch(() => ({}));
      const hasServerData =
        (existing.ok && Object.keys(existing.meta || {}).length > 0) ||
        Object.values(existing.remark || {}).some((v) => String(v || "").trim());
      if (hasServerData) continue; // サーバーに既にデータあり → スキップ
      const d = drafts[pid];
      const putRes = await fetch("/api/project-doc-meta", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ projectId: proj.dbId, meta: d.meta || {}, remark: d.remark || {} })
      });
      if (!putRes.ok) { failed++; continue; }
      ok++;
      // APIレート制限回避
      await new Promise((r) => setTimeout(r, 50));
    } catch {
      failed++;
    }
  }
  if (ok > 0) console.log(`[doc-meta-migration] ${ok}件をサーバーに移行しました`);
  // 失敗があった案件は次回セッションで再試行できるよう、フラグは全件成功時のみ立てる
  if (failed === 0) localStorage.setItem(FLAG, "1");
  else console.warn(`[doc-meta-migration] ${failed}件が失敗したため未完了として次回再試行します`);
}

/** 書類メタをサーバーからロードして uiState に反映（書類ページ初回表示時に呼ぶ） */
const _docMetaLoadFailedAt = {};

async function loadDocMetaFromServer(pid) {
  if (!authToken) return;
  const proj = projects.find((p) => p.id === pid);
  if (!proj?.dbId) return;
  // 既にロード済みならスキップ
  if (!uiState._docMetaServerLoaded) uiState._docMetaServerLoaded = {};
  if (uiState._docMetaServerLoaded[pid]) return;
  // 失敗直後は毎回の render() で叩き直さないよう15秒はスキップ（画面遷移せず再訪した場合の連打防止）
  if (_docMetaLoadFailedAt[pid] && Date.now() - _docMetaLoadFailedAt[pid] < 15000) return;
  try {
    const res = await fetch(
      `/api/project-doc-meta?projectId=${encodeURIComponent(proj.dbId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (!res.ok) { _docMetaLoadFailedAt[pid] = Date.now(); return; }
    const data = await res.json();
    if (!data.ok) { _docMetaLoadFailedAt[pid] = Date.now(); return; }
    uiState._docMetaServerLoaded[pid] = true;
    delete _docMetaLoadFailedAt[pid];
    // サーバーデータをマージ（タブ単位でサーバー優先・ローカル補完）。
    // ただし読込前に入力した未送信の項目は手元の値を残す（読込で入力が消えない・後の保存で送られる）
    const draft = uiState.projectDocsDrafts[pid] || (uiState.projectDocsDrafts[pid] = { lines: [], meta: {}, remark: {} });
    if (!draft.meta || typeof draft.meta !== "object") draft.meta = {};
    if (!draft.remark || typeof draft.remark !== "object") draft.remark = {};
    const pending = _docMetaPendingEdits[pid] || {};
    Object.entries(data.meta || {}).forEach(([tab, serverTab]) => {
      const merged = serverTab && typeof serverTab === "object" ? { ...serverTab } : {};
      const localTab = draft.meta[tab] || {};
      (pending[tab]?.fields || []).forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(localTab, f)) merged[f] = localTab[f];
      });
      draft.meta[tab] = merged;
    });
    const serverRemark = data.remark || {};
    Object.entries(serverRemark).forEach(([tab, v]) => {
      if (!pending[tab]?.remark) draft.remark[tab] = v;
    });
    // 端末にだけ残っている備考（書類画面で保存せずに再読み込みした等）は「未保存」として出し、保存ボタンで送る。
    // 既定文言と同じもの（以前の画面が開いただけで入れていた値）は数えない
    Object.keys(draft.remark).forEach((tab) => {
      if (Object.prototype.hasOwnProperty.call(serverRemark, tab) || pending[tab]?.remark) return;
      if (String(draft.remark[tab] ?? "") === resolveDocRemark({}, tab)) return;
      const byTab = _docMetaPendingEdits[pid] || (_docMetaPendingEdits[pid] = {});
      (byTab[tab] || (byTab[tab] = { fields: new Set(), remark: false })).remark = true;
    });
    render();
  } catch (e) {
    _docMetaLoadFailedAt[pid] = Date.now();
    console.warn("loadDocMetaFromServer failed:", e?.message || e);
  }
}

function pruneProjectDocDraftsForExistingProjects() {
  const alive = new Set((projects || []).map((p) => String(p.id || "")).filter(Boolean));
  /** GET /api/projects が一時的に空や通信失敗で一覧だけ空になると、ここですべての項目下書きが消えてしまうのを防ぐ */
  if (alive.size === 0) return;
  let changed = false;
  for (const key of Object.keys(uiState.projectDocsDrafts || {})) {
    if (!alive.has(String(key))) {
      // 未保存の変更がある下書きは削除しない（API が一時的に案件を返さない場合でも消えないよう保護）
      if (uiState.projectDocsDraftDirty?.[key]) continue;
      delete uiState.projectDocsDrafts[key];
      changed = true;
    }
  }
  if (changed) persistProjectDocDraftsStore();
}

const PROJECT_DETAIL_TAG_EXTRAS_STORAGE_KEY = "flowPilotProjectTagExtras";
const SIDEBAR_NAV_COLLAPSED_STORAGE_KEY = "flowPilotSidebarNavCollapsed";

function loadSidebarNavCollapsedFromStore() {
  try {
    return localStorage.getItem(SIDEBAR_NAV_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function loadProjectDetailExtraTagPresetsFromStore() {
  try {
    const raw = localStorage.getItem(PROJECT_DETAIL_TAG_EXTRAS_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr.map((t) => String(t).trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ja"));
  } catch {
    return [];
  }
}

const uiState = {
  composer: null,
  /** 開発ページ：一覧データ・取得状態・編集ドラフト・ToDoボード候補 */
  developments: [],
  developmentsLoaded: false,
  developmentsLoadError: false,
  developmentEditDraft: null,
  developmentTodoOptions: null,
  developmentSaving: false,
  /** 開発ページ：デモパスワードを表示中の development id → true */
  developmentRevealPass: {},
  /** 開発ページ：資料ダウンロードの中央モーダル。{ id } */
  developmentDocModal: null,
  /** 開発ページ：自社ツール開発の事前申請一覧（承認フロー連動）・取得状態・申請モーダル */
  developmentApplications: [],
  developmentApplicationsLoaded: false,
  developmentApplicationsLoadError: false,
  developmentApplyDraft: null,
  /** 申請管理：クライアント/パートナーの更新・削除申請（保留型フロー）の一覧・取得状態・処理中フラグ */
  masterChangeRequests: [],
  masterChangeRequestsLoaded: false,
  appMgmtChangeReqBusyId: "",
  /** 契約一覧：「削除したデータを表示」ON時に取得する削除済み（論理削除）行のキャッシュ・取得状態・復元中ID */
  deletedContractRows: null,
  deletedContractRowsLoading: false,
  contractRestoringId: "",
  /** 誤操作で閉じた事前申請フォームの入力内容の退避先（次に開いたとき復元、送信成功でクリア） */
  developmentApplyDraftSaved: null,
  developmentApplySaving: false,
  /** 申請承認ページ：自社ツール開発の事前申請の却下モーダル。{ id } */
  devAppRejectDraft: null,
  devAppApprovalSaving: false,
  /** 稼働状況ページ：表示タブ（mywork=自分の工数 / estimate=見積 / evaluation=評価サマリ） */
  workloadTab: "mywork",
  /** 稼働状況ページ：データ取得時刻（TTL自動再取得用。キー: mywork/estimate/eval） */
  wlFetchedAt: {},
  /** 案件単価・見積シミュレータ用データ（scope=estimate の { projects, defaults }） */
  workloadEstimate: null,
  workloadEstimateLoaded: false,
  workloadEstimateLoadError: false,
  /** 見積シミュレータの入力状態 */
  wlEstimator: { category: "", hours: "", rate: "" },
  /** マイ工数タブ：自分の直近工数ログ＋案件候補 */
  myWork: null,
  myWorkLoaded: false,
  myWorkLoadError: false,
  /** 評価サマリタブ：対象月（YYYY-MM・空=今月）とデータ・展開中メンバー */
  evalMonth: "",
  evalData: null,
  evalBusy: null,
  evalLoaded: false,
  evalLoadError: false,
  evalExpandUserId: null,
  /** 許可ツールページ：許可ツール一覧（承認済み）・申請一覧（承認フロー連動）・取得状態・フィルタ */
  allowedTools: [],
  allowedToolApplications: [],
  allowedToolsLoaded: false,
  allowedToolsLoadError: false,
  allowedToolsCategoryFilter: "all",
  allowedToolsSearch: "",
  /** 申請モーダル（登録/編集/削除のいずれか）。{ kind, targetToolId, title, category, approverDept, usageTerms, reason } */
  allowedToolApplyDraft: null,
  allowedToolApplySaving: false,
  /** 申請承認ページ：ツール許可申請の却下モーダル。{ id } */
  toolRejectDraft: null,
  toolApprovalSaving: false,
  docStart: null,
  templateEditor: null,
  /** 書類テンプレート（/api/doc-templates。null=未取得。15z-doc-templates.js） */
  docTemplates: null,
  docTemplatesLoading: false,
  docTemplatesError: "",
  docTemplateSaving: false,
  templateTab: "quote",
  orderFlowPromptProjectId: "",
  clientCreate: null,
  clientEditor: null,
  userEditor: null,
  projectCreate: null,
  projectCreateWarning: null,
  /** PROJECT一覧・一括複製の確認モーダル `{ selectedIds: string[] }` */
  projectBulkDupConfirm: null,
  /** 案件一覧の書類の一括出力（15z-project-bulk-docs.js）。{ selectedIds, types, processing, result } */
  projectBulkDocs: null,
  /** 捺印の申請・承認の確認 `{ kind, key?, pid?, tab?, selectedIds?, keys? }` */
  sealConfirmDialog: null,
  /** 申請承認（統一テーブル）で選択中の行 `"kind:key"` → true（全カテゴリ共通の一括承認用） */
  appMgmtSelected: {},
  /** 取引承認（個別・一括）の API 更新中 */
  appMgmtContractApproving: false,
  /** 通知ベルからの導線用（旧タブの名残。統一テーブル化後は遷移先の絞り込みには使わない） */
  appMgmtTab: "all",
  /** 申請承認の確認メモ（ユーザー単位で DB 永続化） kind → rowKey → text */
  appMgmtReviewMemos: {},
  /** 申請承認: 詳細モーダル `{ kind, key }`（kind: contract/seal/tool/unlock/dev/changereq） */
  appMgmtDetailModal: null,
  /** 申請承認: 契約関係（取引承認）の却下モーダル。{ id } */
  appMgmtContractRejectDraft: null,
  /** 申請承認: 請求書（捺印）の却下モーダル。{ key } */
  appMgmtSealRejectDraft: null,
  appMgmtSealRejectSaving: false,
  detailLineAddPicker: null,
  /** 案件詳細・請求項目 SP 編集モーダル `{ pid, index }` */
  detailLineMobileEdit: null,
  /** 案件詳細・請求項目のドラッグ並べ替え中 `{ pid, fromIndex }` */
  detailLineDrag: null,
  partnerCreate: null,
  /** 法人検索 UI（DB マスタ＋任意の法人インデックス JSON） */
  corporateLocalSearch: {
    context: null,
    query: "",
    results: null,
    error: "",
    loading: false,
    indexRowCount: null,
    searchHint: ""
  },
  partnersCache: [],
  /** 媒体原価の「アカウント名→案件」記憶（全員共有）。`[{ sourceKey, accountNameKey, projectId }]` */
  costAccountProjectMap: [],
  costForm: null,
  /** project_id が NULL の原価（案件未割当） */
  orphanCosts: [],
  /** 原価登録ウィザード（中央モーダル） `{ phase, targetMonth, csvSourceKey, ... }` */
  costRegWizard: null,
  costFilter: "all",
  /** 原価管理一覧: キーワード・対象月。種別は costMgmtTabKeys のタブで指定 */
  costMgmtTabKeys: ["all"],
  costMgmtFilter: { keyword: "", vendor: "", payStatus: "", paymentMethod: "", lock: "", vendorType: "", costKind: "", invFrom: "", invTo: "", payFrom: "", payTo: "", tmFrom: "", tmTo: "" },
  /** 原価管理一覧: ページネーション（行数が多いのでページ分割して描画を軽くする） */
  costMgmtTable: { page: 1, perPage: 50 },
  costMgmtSort: { key: "targetMonth", dir: "desc" },
  costMgmtColWidths: {},
  /** 原価管理一覧: 一括操作用 `projectId:costRowId` → true */
  costMgmtSelected: {},
  /** 原価割当一覧: 一括割当用（キー形式は原価管理と同じ `未割当UI:costId`） */
  costAllocSelected: {},
  /** 原価割当: まとめて割当の案件プルダウン選択値（案件 UI id） */
  costAllocBulkProjectPick: "",
  /** 原価割当（未割当キュー）: 支払先の絞り込みと並び替え（key は costRowSortValue のキー・default は既定順） */
  costAllocFilter: { vendor: "" },
  costAllocSort: { key: "default", dir: "desc" },
  /** 原価管理: 一括アクション用ミニモーダル `null` | `category` | `delegate` */
  costMgmtBulkModal: null,
  /** 原価登録: 検索バーの詳細条件（支払方法〜各月範囲）を開いているか */
  costMgmtFilterDetailOpen: false,
  /** 原価照合: 確認＋受領登録モーダル `null` | { rows, confirmOnlyIds, saving } */
  costReconcileReceiptModal: null,
  /** 原価割当（営業ビュー）: 照会フィルタ（実際の初期値は emptyCostReviewState / ensureCostReviewState が補う） */
  costReview: { month: "", vendor: "", projectIds: [], clientNames: [], projMenuOpen: false, clientMenuOpen: false, projQuery: "", sortKey: "targetMonth", sortDir: "desc" },
  /** 原価への異議申請モーダル */
  costDisputeModal: null,
  /** 原価の差し戻しモーダル（経理→営業。15e-cost-remand） */
  costRemandModal: null,
  /** 原価の付け替えモーダル（営業が自分の案件どうしで移す。15e-cost-move） */
  costMoveModal: null,
  paymentForm: null,
  /** 入金の一括記録: 選択中の案件ID集合と、一括登録する入金日 */
  paymentBulkSelected: {},
  paymentBulkDate: "",
  collapsedSections: {},
  // 予算進捗「経営」タブ・月次P/L の表示月。空＝未選択で、描画時に当月へ寄せる
  // （固定値を置くと時が経つほど過去月が既定になる）。年度は予算進捗の「期を選択」に一本化
  mgmtMonth: "",
  plMonth: "",
  // 貢献度タブの絞り込み。既定は両方空＝すべて表示（仕様確定）
  contribMonth: "",
  contribMember: "",
  expenseForm: null,
  /** @type {null | { message: string, sticky: boolean, variant: 'info'|'error', seq: number }} */
  toast: null,
  currentUserRole: "営業",
  dashboardMineOnly: true,  // ダッシュボード「今やること」を自分担当のみに絞るか
  projectListFilters: {
    keyword: "",
    clientNameQuery: "",
    /** 自社担当者複数選択（空＝絞り込みなし） */
    assignedUserIds: [],
    status: "all",
    stale: "all",
    completion: "active",
    billingMonthFrom: "",
    billingMonthTo: "",
    /** 入金日の範囲（YYYY-MM-DD。どちらか指定で、範囲内に入金日がある案件だけを表示） */
    paymentDateFrom: "",
    paymentDateTo: "",
    sort: "updated_desc",
    mineOnly: true,
    /** 受注ステータス複数選択（空＝失注以外すべて。失注は選んだときだけ出す） */
    orderStatuses: [],
    /** 「入金」列（simplifiedInvoiceStatus）の区分で絞り込み（空＝絞り込みなし）。ダッシュボードのカードから設定 */
    paymentSimple: "",
    /** タグ複数選択（空＝絞り込みなし／いずれか1つでも付いていれば一致） */
    filterTags: [],
    /** ロック状態（all=すべて / locked=ロック済み / unlocked=未ロック） */
    lockState: "all",
    /** 案件のカテゴリ（all=すべて / PROJECT_WORK_CATEGORY_OPTIONS の値 / unset=未設定） */
    workCategory: "all"
  },
  /** 案件詳細・基本情報の担当者プルダウンを開いている案件ID（再描画しても開いたままにする） */
  workCategoryPickerOpenPid: "",
  /** ログイン後にプロジェクト一覧の初期フィルターを1回だけ適用済みか */
  projectListFiltersDefaultsApplied: false,
  /** SP: フィルターの詳細欄を展開しているか */
  projectListFilterOpen: false,
  /** 案件一覧で開いている複数選択プルダウン（"assignee" / "order" / "tag"。チェックで再描画しても開いたままにする） */
  projectListOpenFilterDd: "",
  /** 一覧の検索欄の固定項目編集モード（15z-list-filter-fields.js）。{ id: 一覧ID, hidden, order } 。編集中でなければ null */
  listFilterFieldsEdit: null,
  projectTable: {
    sortKey: "no",
    sortDir: "desc",
    page: 1,
    perPage: 20,
    selected: {}
  },
  quickFilter: {
    contracts: "mine",  // 各ステージページも初期は自分担当のみ
    quotes: "mine",
    orders: "all",
    invoices: "mine",
    payments: "mine",
    invoiceBillingMonth: "all"
  },
  /** 予算進捗ページの表示状態（#budget-progress 再描画時に復元） */
  budgetProgress: { periodIdx: 12, tab: "all", collapse: { h1: false, h2: false } },
  settingsMenu: "account-settings",
  accountSettingsTab: "account",
  secretMenuTab: "login-status",
  /** ToDo SP バックログ風UI: 選択シート（null=非表示） */
  pmSpAttrSheet: null,
  /** ToDo SP バックログ風UI: コメント送信時に変更する状態（null=変更しない） */
  pmSpComposePendingStatus: null,
  /** ToDo SP バックログ風UI: コメント送信時に変更する担当者（null=変更しない） */
  pmSpComposePendingAssignee: null,
  /** ToDo SP バックログ風UI: コメント全件表示 */
  pmSpCommentsExpanded: false,
  /** ToDo SP バックログ風UI: コメント入力シートを開くか */
  pmSpComposeOpen: false,
  /** ToDo SP バックログ風UI: 上の入力シート・保留中の状況/担当者がどの課題のものか（別の課題へ移ったら捨てる） */
  pmSpComposeAgendaId: null,
  auditLogFilter: { userId: "", resourceType: "", action: "", dateFrom: "", dateTo: "" },
  loginStatusView: { items: null, loading: false },
  /** アカウント一覧: チェック時は退職ユーザーを非表示（デフォルト true） */
  accountListExcludeRetired: true,
  community: {
    tab: "org",
    dept: "すべて",
    tag: "すべて"
  },
  /** ToDo管理（プロジェクト・テーマ・課題）API 同期データ */
  progressTodoGroups: [],
  progressChallenges: [],
  progressSelectedTodoGroupId: "",
  progressSelectedChallengeId: "",
  progressSelectedAgendaId: "",
  /** ToDo管理・グループ一覧のキーワード絞り込み */
  progressGroupListKeyword: "",
  /** ToDo管理・テーマ一覧のキーワード絞り込み */
  progressProjectListKeyword: "",
  /** ToDo管理・課題ボード/自分担当一覧の課題検索（課題名＋内容の部分一致・両画面共有） */
  progressIssueSearch: "",
  /** ToDo管理・プロジェクト一覧: active | starred | archived */
  progressGroupListMode: "active",
  /** ToDo管理・テーマ一覧: active | starred | archived */
  progressListMode: "active",
  /** ToDo管理・グループ一覧で「グループを登録」モーダル */
  progressShowTodoGroupForm: false,
  progressTodoGroupCreateDraft: { title: "", body: "", category: "" },
  progressShowTodoGroupEditForm: false,
  progressTodoGroupEditDraft: { groupId: "", title: "", body: "", category: "", challengeIds: [], themeFilter: "" },
  progressTodoGroupModalSubmitting: false,
  /** ToDo管理・グループ一覧で選択中の分類フィルタ（"" = すべて） */
  progressGroupListCategory: "",
  /** ToDo管理・プロジェクト一覧で「★お気に入りだけ表示」トグル（旧お気に入りタブの置き換え） */
  progressProjListStarOnly: false,
  /** ToDo管理一覧で「テーマを登録」モーダルを開いているか */
  progressShowProjectForm: false,
  /** ToDo管理・テーマ登録モーダル内の下書き（再描画で保持） */
  progressChallengeCreateDraft: { title: "", body: "" },
  /** ToDo管理・テーマ編集モーダルを開いているか */
  progressShowChallengeEditForm: false,
  /** ToDo管理・テーマ編集モーダル内の下書き */
  progressChallengeEditDraft: { challengeId: "", title: "", body: "" },
  progressShowChallengeWiki: false,
  progressChallengeWikiDraft: { challengeId: "", wikiBody: "" },
  progressShowChallengeSettings: false,
  progressChallengeSettingsDraft: {
    challengeId: "",
    tab: "categories",
    statuses: [],
    categories: [],
    newStatus: "",
    newCategory: "",
    basicTitle: "",
    basicBody: "",
    projectId: ""
  },
  progressChallengeWikiSubmitting: false,
  progressChallengeSettingsSubmitting: false,
  /** 課題詳細「工程履歴」モーダルで表示中の課題ID（""=閉じている） */
  progressStageHistoryAgendaId: "",
  /** 差し戻し（工程順の後戻り）理由入力モーダルの下書き（null=閉じている） */
  progressRejectionPrompt: null,
  /** 課題詳細「実働工数の明細」モーダルで表示中の課題ID（""=閉じている） */
  progressWorkLogDetailAgendaId: "",
  /** 課題ボードで「課題を追加」モーダルを開いているか */
  progressShowIssueAddForm: false,
  /** 課題クイック追加: テーマ選択ピッカーを開いているか（どの画面からでも課題追加へ） */
  progressQuickAddPickerOpen: false,
  /** 課題編集モーダル対象の課題 id（空なら新規追加） */
  progressAgendaEditAgendaId: "",
  /** 課題追加モーダル内の下書き（再描画で保持） */
  progressAgendaCreateDraft: {
    title: "",
    body: "",
    status: "未対応",
    assignedUserId: "",
    assignedUserSearch: "",
    priority: "中",
    startDate: "",
    dueDate: ""
  },
  /** ToDo管理: GET 実行中（二重取得防止） */
  progressMgmtFetchInFlight: false,
  /** ToDo管理: 初回など一覧ツリー未取得のまま待っている間 true */
  progressMgmtLoading: false,
  /** ToDo管理: summary から詳細（やりとり）を full で取り直し中 */
  progressMgmtDetailLoading: false,
  /** ToDo管理: 一覧が空の状態で GET が失敗したあと、自動再試行を止める */
  progressMgmtSuppressEmptyRefetch: false,
  /** ToDo管理: 連続 GET 失敗回数（自動再試行の抑制用） */
  progressMgmtFetchFailCount: 0,
  /** ToDo管理: 直近の取得エラー文言（画面表示用） */
  progressMgmtLoadError: "",
  /** 一覧タブごとの初回取得済み（空配列でも true にして再取得ループを防ぐ） */
  progressListFetchDone: { active: false, starred: false, archived: false },
  /** テーマ一覧タブごとのキャッシュ（null＝未取得 / {groups,challenges}＝取得済み） */
  progressListCache: { active: null, starred: null, archived: null },
  /** グループ一覧タブごとのキャッシュ */
  progressGroupListCache: { active: null, my_themes: null, starred: null, archived: null },
  progressGroupListFetchDone: { active: false, my_themes: false, starred: false, archived: false },
  progressAssignedListCache: null,
  /** 表示中の担当課題ツリー・キャッシュがどのスコープ（me/created/involved）のものか。キャッシュ枠は1つなので、違うスコープの中身を流用しないために持つ */
  progressAssignedListCacheScope: "",
  progressAssignedListFetchDone: false,
  /** 自分の担当課題ビューの表示形式: talk（LINE風2ペイン・既定） | list（従来カード）。アカウントごとに localStorage 保存・PC限定 */
  progressAssignedViewMode: "talk",
  /** 担当課題トークビュー: 未読メンションがある課題だけに絞る（検索・保留を除く等は既存の担当課題一覧と共有） */
  /** 担当課題トークビュー: 会話を開いた時点の既読位置スナップショット
   * { agendaId, lastReadAt, anchored }。開くと既読化が走り lastReadAt が即座に更新されるため、
   * 「ここから未読」の区切りと初期スクロール位置はこのスナップショットから計算する */
  progressTalkUnreadAnchor: null,
  /** ウォッチ中課題の未読件数（API viewer.watchUnreadCount） */
  progressWatchUnreadCount: 0,
  /** 未読メンション件数（API viewer.mentionUnreadCount） */
  progressMentionUnreadCount: 0,
  /** 未読メンションの課題一覧（API viewer.mentionTargets） */
  progressMentionTargets: [],
  /** 未読ウォッチの課題一覧（API viewer.watchTargets） */
  progressWatchTargets: [],
  /** 担当になった未読件数（API viewer.assignedToMeCount） */
  progressAssignedToMeCount: 0,
  /** 自分が担当の未アーカイブ・未完了課題の総数（API viewer.assignedActiveTotal）。ベル「担当課題」件数はこれを表示（未読とは別軸） */
  progressAssignedActiveTotal: 0,
  /** 自分が起票した未アーカイブ・未完了課題の総数（API viewer.createdActiveTotal）。担当課題ページの「起票」チップの数字 */
  progressCreatedActiveTotal: 0,
  /** 担当になった未読課題一覧（API viewer.assignedToMeTargets） */
  progressAssignedToMeTargets: [],
  /** マイテーマ件数（API viewer.myThemeCount） */
  progressMyThemeCount: null,
  /** 直近に既読処理した課題 id（再描画の重複既読を防ぐ） */
  progressReadMarkedAgendaId: "",
  /** ウォッチ ON/OFF 送信中の agenda id（文字列） */
  progressWatchTogglingId: "",
  /** 課題ボードでウォッチ中のみ表示 */
  progressBoardWatchOnly: false,
  /** 課題ボードで担当が自分の課題のみ表示 */
  progressBoardAssigneeOnly: false,
  /** 「自分の担当課題」一覧のタブ: all または challengeId（文字列） */
  progressMyAssignListFilter: "all",
  /** 「自分の担当課題」テーマドロップダウン開閉状態 */
  progressMyAssignThemeDdOpen: false,
  /** 「自分の担当課題」サマリー絞り込み: all | overdue | high */
  progressMyAssignQuickFilter: "all",
  /** 「自分の担当課題」ウォッチ中のみ表示するトグル */
  progressMyAssignWatchOnly: false,
  /** 「自分の担当課題」並び替え: key = due | no ／ dir = asc | desc（既定は従来の期限順）。
   *  実際の値は localStorage からアカウント別に復元する（15m-agenda-sort.js）ので、ここは復元前の初期値 */
  progressMyAssignSort: { key: "due", dir: "asc" },
  /** 「自分の担当課題」保留の課題を除くトグル */
  progressMyAssignHideHold: false,
  /** 「自分の担当課題」一覧に入ったあと一覧APIを確実に再取得するフラグ（テーマのみ取得後の状態を避ける） */
  progressAssignedMeNeedRefresh: false,
  /** 課題ボード（?project=…）へ遷移したとき、他者による担当変更等を反映するため1回だけ再取得するフラグ */
  progressBoardNeedRefresh: false,
  /** ToDo の直前ハッシュ（担当一覧への遷移検知） */
  _fpPrevPmHash: "",
  /** ToDoやりとり: 編集中のメッセージ id（文字列） */
  progressEditingMessageId: "",
  /** ToDoやりとり: リアクション絵文字パレットを開いているメッセージ id（文字列・空=閉） */
  progressReactPickerMsgId: "",
  /** ToDo テーマ登録モーダル: 送信中（二重クリック防止） */
  progressChallengeModalSubmitting: false,
  /** ToDo 課題追加モーダル: 送信中（二重クリック防止） */
  progressAgendaModalSubmitting: false,
  /** ToDo プロジェクト設定「Backlog連携」: 保存・取り込み中（二重クリック防止） */
  progressBacklogImportBusy: false,
  /** ToDo 課題ボード「タスクMD」: 生成を依頼中の challenge id（文字列・空=なし。二重クリック防止） */
  progressTaskMdRequesting: "",
  /** ToDo プロジェクト設定「Backlog連携」: 接続先の名前一覧（null=未取得） */
  progressBacklogConnections: null,
  /** 次回 render で右ドロワーの入場アニメを抑止（課題追加完了直後など） */
  suppressComposerDrawerAnimation: false,
  /** ToDo 課題詳細: 属性フォーム保存中の agenda id（文字列） */
  progressIssueAttrSaving: "",
  /** ToDo 課題詳細: 添付アップロード中の agenda id（文字列） */
  progressAttachmentUploadAgendaId: "",
  /** ToDo コメント: 添付アップロード中の message id（文字列） */
  progressAttachmentUploadMessageId: "",
  /** ToDo 新規コメント送信前に添付するファイル（課題 id に紐づく） */
  progressComposeAttach: { agendaId: "", files: [] },
  progressMessageSubmittingByAgenda: {},
  /** 課題追加・編集モーダルで保存前にキューする添付（File[]） */
  progressAgendaModalPendingFiles: [],
  /** 課題添付一覧モーダル（{ agendaId }） */
  progressAgendaAttachModal: null,
  /** 課題添付モーダル内の画像プレビュー対象 id */
  progressAgendaAttachPreviewId: "",
  /** 課題添付プレビュー用 blob URL（id → url） */
  progressAttachPreviewUrls: {},
  deptSettings: null,
  deptDragMemberId: "",
  deptCreateModal: null,
  /** 承認フロー: 申請フローID → 各ステップの承認者（""=未設定、またはユーザーID） */
  approvalApprovers: null,
  /** 申請種別キー → フローID。捺印＝`__dept__`（申請者の所属フロー）、それ以外＝`kind:<種別>`（種別ごとの承認者）で固定 */
  approvalApplicationFlowBindings: null,
  /** 承認者ピッカー: { deptId, stepIndex } | null */
  approvalPicker: null,
  /** その他の申請の承認者を編集中の種別（"kind:<種別キー>"。空＝閉じている） */
  approvalKindEditId: "",
  /** 案件ロック解除申請の一覧（/api/project-unlock-applications。null=未取得） */
  projectUnlockApplications: null,
  projectUnlockAppsLoading: false,
  /** ロック解除申請モーダル { projectId } | null */
  projectUnlockRequestDraft: null,
  projectUnlockSaving: false,
  /** ロック解除申請の却下モーダル { id } | null */
  unlockRejectDraft: null,
  /** 請求書・見積書 発行履歴キャッシュ { "pid:docType": [entry,...] } */
  invoiceIssueHistory: {},
  invoiceIssueHistoryLoading: {},
  /** 発行履歴モーダル { projectId, docType } */
  invoiceIssueHistoryModal: null,
  /** 案件編集履歴モーダル { projectId, dbId, loading, history } */
  projectEditHistoryModal: null,
  /** 案件詳細「コメント」タブの状態 `{ projectId, loading, loaded, error, items, draft, editingId, editDraft, submitting }` | null */
  projectComments: null,
  /** 予算設定（#budget-admin）の会計期リスト */
  budgetPeriods: null,
  selectedBudgetPeriodId: "",
  budgetRowsLoadedByPeriod: {},
  budgetRowsLoadingByPeriod: {},
  budgetRowsLoadFailedByPeriod: {},
  sidebarSections: {
    sales: true,
    assistant: true,
    admin: true,
    backoffice: true
  },
  /** 左ナビを細いアイコンバーに畳む（main を広げる） */
  sidebarNavCollapsed: loadSidebarNavCollapsedFromStore(),
  /** 狭い画面: 左上ハンバーガーで開くサイドパネルの表示状態 */
  fpMobileNavOpen: false,
  /** 案件詳細の概要・実績その他タブ用の編集ドラフト */
  projectDetailForm: null,
  /** 基本情報フォームのサブタブ（billing | mail | class | memo）。値は draft に持つのでタブ切替で消えない */
  projectDetailFormSection: "billing",
  /** タグ新規作成モーダル `{ projectId, value }` | null（プルダウンの「＋新しいタグを作成…」で開く） */
  projectDetailTagModal: null,
  /** タグ欄で新規入力したタグ名（案件・クライアントに無いもの）をプルダウン候補へ残す */
  projectDetailExtraTagPresets: loadProjectDetailExtraTagPresetsFromStore(),
  /** 案件詳細ヘッダーで案件名をインライン編集中の案件 id（例 p1） */
  editingProjectNameId: "",
  /** 担当者割当ページの検索条件 */
  staffAssignmentFilters: {
    category: "all",
    billingFrom: "",
    billingTo: "",
    assignee: "all",
    /** true のとき未割当の請求明細行のみ一覧に出す（既定オン） */
    unassignedOnly: true
  },
  /** 担当者割当一覧の行選択・一括割当（key: `projectId:lineIndex`） */
  staffAssignmentTable: {
    selected: {},
    /** 一括適用で選ぶ担当者 id（複数可・選択行に順に割当） */
    bulkUserIds: [],
    /** 一括担当者パネルを開いたままにする（チェックで render しても閉じない） */
    bulkAssignPanelOpen: false,
    /** 表示する行数（「さらに表示」で STAFF_ASSIGN_PAGE_SIZE ずつ増やす） */
    shownCount: 0
  },
  /** 捺印申請状態: key `projectId:docTab` → `未申請` | `申請中` | `承認済` */
  docSealStatus: {},
  /** 郵送ステータス: key `projectId:docType` → 最新の fp_doc_mail_history.status（未取得なら undefined） */
  docMailStatus: {},
  /** 郵送ステータスを取得済み/取得中の案件id集合（多重フェッチ防止。取得後は毎回クリアして再取得させる） */
  docMailStatusLoadedFor: {},
  /** 案件一覧「押印申請」列で書類別パネルを開いている案件 id（"" = 閉） */
  pjSealPanelProjectId: "",
  /** 捺印の多段承認: key `projectId:docTab` → `{ approvedCount }`（済んだ承認者の人数＝次の承認者インデックス） */
  docSealApprovalProgress: {},
  /** 直近の却下情報: key `projectId:docTab` → `{ reason, reviewedAt }`（却下後は未申請扱いに戻るため、理由だけここに残す） */
  docSealRejectInfo: {},
  /** 捺印申請の「申請履歴」モーダル。{ mapKey } | null */
  docSealHistoryModal: null,
  projectDocsDrafts: readProjectDocDraftsStore(),
  /** 請求項目がローカルで編集中のとき true。サーバーからの billing_lines_json で行を上書きしない */
  projectDocsDraftDirty: {},
  /** 案件詳細で編集した（または端末にだけ残っていた）未保存の請求項目。自動では送らず「保存」ボタンで確定する */
  projectBillingManualDirty: {},
  /** 最後に保存・読込したサーバーの請求項目（「保存せずに移動」で入力を捨てて戻す先） */
  projectBillingLinesServerCopy: {},
  /** サーバー同期時点の明細行数（空配列での誤上書き防止用） */
  projectBillingLinesServerSnapshot: {},
  // 検索条件の初期値は 15k-client-list-fields.js の clientListDefaultFilters / partnerListDefaultFilters と同じ
  clientList: {
    filters: {
      keyword: "",
      approval: "",
      contractOwner: "",
      nda: "all",
      contract: "all",
      send: "",
      qualified: "",
      paymentTerms: "",
      prefecture: "",
      hidden: "",
      deleted: "",
      sort: ""
    },
    page: 1,
    perPage: 20
  },
  /** 先方担当者（取引先詳細の「担当者・支社」タブ・案件詳細の担当者プルダウン）。rows=null は未取得（遅延ロード） */
  clientContacts: {
    rows: null,
    loading: false,
    loadError: false,
    /** パートナーの担当者（取引先詳細の「担当者・支社」タブ）。rows と一緒に取得する */
    partnerRows: null,
    /** { id, clientId, partnerId（パートナーの担当者のとき）, lastName, firstName, honorific, title, dept, email, archived } | null */
    editor: null,
    saving: false
  },
  /** 支社（取引先詳細の「担当者・支社」タブ・案件詳細の支社プルダウン・書類の宛先）。rows=null は未取得（遅延ロード） */
  clientBranches: {
    rows: null,
    loading: false,
    loadError: false,
    /** パートナーの支社（取引先詳細の「担当者・支社」タブ）。rows と一緒に取得する */
    partnerRows: null,
    /** { id, clientId, partnerId（パートナーの支社のとき）, name, postalCode, prefecture, address, building, tel, fax, archived } | null */
    editor: null,
    saving: false
  },
  partnerList: {
    filters: {
      keyword: "",
      approval: "",
      paymentMethod: "all",
      nda: "all",
      contract: "all",
      send: "",
      qualified: "",
      paymentTerms: "",
      prefecture: "",
      hidden: "",
      deleted: "",
      sort: ""
    },
    page: 1,
    perPage: 20
  },
  domainList: {
    filters: {
      keyword: "",
      statuses: DOMAIN_STATUS_FILTER_DEFAULT.slice(),
      owner: "",
      clientName: ""
    },
    statusMenuOpen: false,
    sortKey: "",
    sortAsc: true,
    page: 1,
    perPage: 20,
    selected: {}
  },
  /** ドメイン一覧の遅延読み込み状態（認証情報を含むため domain-list を開いた時だけ取得する） */
  domainsLoaded: false,
  domainsLoading: false,
  domainsLoadError: false,
  /** 原価取込のドメイン照合用の軽量データ（認証情報なし）を取得済みか */
  domainsLightRequested: false,
  /** 同・取得中の Promise（お名前取込が担当営業を読む前に待つ） */
  domainsLightPromise: null,
  domainPwReveal: {},
  sidebarUserMenuOpen: false,
  sidebarNotifyMenuOpen: false,
  midnightBedtimeModalOpen: false,
  myAccountEditor: null,
  auth: {
    isLoggedIn: false,
    isLoading: false,
    step: "login", // login | otp
    email: "",
    password: "",
    otp: "",
    codeSentTo: "",
    generatedCode: ""
  },
  /** ダッシュボード検索バーのクエリ */
  dashboardSearch: "",
  /** GET /api/projects 等の heavy 取得中（一覧・ダッシュボードで now loading 表示用） */
  heavyDataLoading: false,
  /** 監査ログビュー（未初期化だと undefined になり直接アクセスで TypeError 発生） */
  auditLogView: { items: null, loading: false, hasMore: false, offset: 0 }
};

// localStorage の下書きから初期スナップショットを生成（サーバー応答前の誤消去防止）
(function seedBillingLinesSnapshotFromDrafts() {
  const drafts = uiState.projectDocsDrafts || {};
  const snap = uiState.projectBillingLinesServerSnapshot;
  for (const pid of Object.keys(drafts)) {
    const lines = drafts[pid]?.lines;
    if (Array.isArray(lines) && lines.length > 0 && !snap[pid]) {
      snap[pid] = { lineCount: lines.length, at: 0 };
    }
  }
})();

const salesMonths = ["4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月", "1月", "2月", "3月"];
const members = [];
const articles = [];

/** 案件の業務カテゴリ（見積シミュレータ・案件単価集計用）。「その他」は自由入力。
 *  サーバ側は自由入力を許容するためホワイトリスト正規化はしない（api/projects.js normalizeServiceCategory 参照） */
const SERVICE_CATEGORY_OPTIONS = ["ウェブ制作", "システム開発", "広告運用", "SNS運用", "SEO対策"];

// SYSTEM_ROLE_OPTIONS / JOB_ROLE_OPTIONS / ALL_NAV_KEYS / DEFAULT_SALES_NAV_KEYS /
// DEFAULT_BASE_NAV_KEYS は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。
/** @deprecated 後方互換。職種のみ */
const ROLE_OPTIONS = JOB_ROLE_OPTIONS;

function normalizeNavPermissions(perms) {
  const base = Array.isArray(perms) ? perms.map((p) => String(p).trim()).filter(Boolean) : [];
  const out = [];
  const seen = new Set();
  base.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  });
  if (out.includes("budget-progress") && !seen.has("progress-management")) {
    out.push("progress-management");
    seen.add("progress-management");
  }
  // 原価一覧（cost-management）を持つ人＝経理側なので請求書受取も含意（既存アカウントの権限を更新せずに新メニューを出す。サーバ normalizeNavPermissionsDb と対）
  if (out.includes("cost-management") && !seen.has("invoice-receipt")) {
    out.push("invoice-receipt");
    seen.add("invoice-receipt");
  }
  // 契約一覧は取引先一覧へ集約した（2026-10-28）。契約管理（contract-management）を持つ人＝経理側は取引先一覧も開ける
  // （経理の既定権限は client-list を持たないため。サーバ normalizeNavPermissionsDb と対）
  if (out.includes("contract-management") && !seen.has("client-list")) {
    out.push("client-list");
    seen.add("client-list");
  }
  // 取引先一覧（client-list）はパートナータブを含むので partner-list も含意（権限画面で付ける手段が無い。サーバ normalizeNavPermissionsDb と対）
  if (out.includes("client-list") && !seen.has("partner-list")) {
    out.push("partner-list");
    seen.add("partner-list");
  }
  const legacy = new Set(["hours-management", "other-admin"]);
  return out.filter((k) => !legacy.has(k));
}

function defaultNavPermissionsForJobRole(jobRole) {
  const r = String(jobRole || "").trim();
  if (r === "管理部" || r === "経理") {
    // 契約一覧（contract-management）が取引先一覧と同等の内容を含むため、取引先一覧（client-list/partner-list）は付与しない
    return normalizeNavPermissions([
      ...DEFAULT_SALES_NAV_KEYS.filter((k) => k !== "client-list" && k !== "partner-list"),
      "contract-management",
      "cost-management",
      "invoice-receipt",
      "application-management"
    ]);
  }
  if (r === "営業") return [...DEFAULT_SALES_NAV_KEYS];
  // CS: 原価割当（どの案件に割り当てるか）の作業担当。案件一覧＋原価割当を既定で持つ。
  // 原価登録（cost-management）は経理専用のため付与しない（登録・編集・削除・支払記録はサーバも拒否）。
  if (r === "CS") return normalizeNavPermissions([...DEFAULT_BASE_NAV_KEYS, "projects", "cost-allocation"]);
  if (r === "開発") return [...DEFAULT_BASE_NAV_KEYS];
  if (r === "情シス") return normalizeNavPermissions([...DEFAULT_BASE_NAV_KEYS, "domain-list"]);
  return [...DEFAULT_BASE_NAV_KEYS];
}

function defaultNavPermissionsForUser(user) {
  const sys = systemRoleOfUser(user);
  if (sys === "マスター" || sys === "管理者") return [...ALL_NAV_KEYS];
  return defaultNavPermissionsForJobRole(primaryJobRole(user));
}

/** @deprecated */
function defaultNavPermissionsForRole(role) {
  const r = String(role || "").trim();
  if (r === "マスター" || r === "管理者") return [...ALL_NAV_KEYS];
  return defaultNavPermissionsForJobRole(r);
}

/** アカウント一覧の表示メニュー並び（左サイドメニューと同順のみ）。
 *  masterOnly 項目（アカウント設定・操作ログ・予算進捗）は権限付与対象でないため除外する。 */
function accountNavPermKeysOrdered() {
  return LEFT_SIDEBAR_NAV_KEYS.filter((k) => {
    const m = menu.find((x) => x.key === k);
    return m && !m.masterOnly && !m.alwaysVisible;
  });
}

/** 職種ラベル（旧「管理部」→「経理」） */
function normalizeJobRoleLabel(role) {
  const r = String(role || "").trim();
  if (r === "管理部") return "経理";
  return r;
}

function isAccountingDepartmentJobRole(role) {
  const r = normalizeJobRoleLabel(role);
  return r === "経理";
}

function primaryJobRole(user) {
  const r = normalizeJobRoleLabel(user?.role || "");
  if (JOB_ROLE_OPTIONS.includes(r)) return r;
  if (r === "マスター" || r === "管理者") return "営業";
  return "営業";
}

/** @deprecated 職種。新規は primaryJobRole を使用 */
function primaryRole(user) {
  return primaryJobRole(user);
}

function systemRoleOfUser(user) {
  const s = String(user?.systemRole || user?.system_role || "").trim();
  if (SYSTEM_ROLE_OPTIONS.includes(s)) return s;
  const r = primaryJobRole(user);
  if (r === "マスター" || r === "管理者") return r;
  const list = Array.isArray(user?.roles) && user.roles.length ? user.roles.map(String) : [];
  if (list.includes("マスター")) return "マスター";
  if (list.includes("管理者")) return "管理者";
  const legacyRole = String(user?.role || "").trim();
  if (legacyRole === "マスター") return "マスター";
  if (legacyRole === "管理者") return "管理者";
  return "一般";
}

/** 担当者割当では選べない（主ロールが営業、またはロール一覧に「営業」を含む） */
function isStaffAssignmentExcludedSalesUser(u) {
  if (!u) return false;
  if (primaryJobRole(u) === "営業") return true;
  const rs = Array.isArray(u.roles) && u.roles.length ? u.roles.map(String) : [];
  return rs.includes("営業");
}

/** ナビとは別軸だがサーバー側 _navAccess.isMasterOrAdminRow と対応させる */
function isMasterOrAdminUser(u) {
  if (!u) return false;
  const s = systemRoleOfUser(u);
  return s === "マスター" || s === "管理者";
}

/** マスターロールのみ（管理者は含めない） */
function isMasterRoleUser(u) {
  if (!u) return false;
  return systemRoleOfUser(u) === "マスター";
}

/** クライアント/パートナーの新規登録・削除の許可（マスター または 経理職種）。変更(更新)は全員可。 */
function canManageClientPartnerMaster(u) {
  const user = u || currentUser;
  return isMasterRoleUser(user) || primaryJobRole(user) === "経理";
}

/** 案件の新規作成の許可。2026-08-18よりログイン中の全ユーザーに開放
 *  （旧: マスター・管理者 または 経理職種のみ。実際に新規案件が必要になるのは
 *  現場の営業なので、そこを止める意味が薄かった。押印申請・取引承認・外部送信には
 *  別途ゲートがあるため、案件作成自体を絞る必要はない）。
 *  サーバー側（api/projects.js POST）も元々このロールでは絞っていない。 */
function canCreateProject(u) {
  const user = u || currentUser;
  return !!user;
}

/** 画面上のページ id → メニュー権限キー（いずれかがあれば可） */
function requiredNavKeysForPage(page) {
  if (page === "account-settings") return [];
  const multi = {
    dashboard: ["dashboard"],
    development: ["development"],
    "allowed-tools": ["allowed-tools"],
    projects: ["projects"],
    community: ["community"],
    quotes: ["contract-management"],
    orders: ["contract-management"],
    invoices: ["contract-management"],
    payments: ["contract-management"],
    contracts: ["contract-management"],
    costs: ["cost-allocation", "cost-management"],
    "cost-allocation": ["cost-allocation"],
    "cost-management": ["cost-management"],
    // 原価照合（経理の月末チェック）: 原価登録のサブ画面（経理/マスター判定はページ側で追加）
    "cost-reconcile": ["cost-management"],
    // 請求書受取（受信箱）。経理/マスター/管理者の追加判定はページ側と API 側で行う
    "invoice-receipt": ["invoice-receipt"],
    // 取引先一覧はクライアント／パートナーのタブ統合。どちらの権限でも両タブを閲覧可。
    "client-list": ["client-list", "partner-list"],
    "partner-list": ["client-list", "partner-list"],
    // 取引先詳細（右スライドの置き換え）。契約一覧からも開くため contract-management も許可
    "client-detail": ["client-list", "partner-list", "contract-management", "projects"],
    // その他（営業非表示・管理部のみ）タブ。ページ側でも canManageClientPartnerMaster で絞る
    "client-others": ["client-list", "partner-list", "contract-management"],
    "domain-list": ["domain-list"],
    "equipment-admin": ["equipment-admin"],
    "project-detail": ["projects"],
    "project-docs": ["projects"],
    // メール作成画面（project-docs の「メール送信」から遷移）。ここが未定義だと
    // requiredNavKeysForPage が null を返し、projects 権限があっても userCanAccessPage が
    // 一律 false になる＝送信ボタンは活性なのに押すと権限エラーで弾かれる、という実害があった。
    "project-mail": ["projects"],
    "account-settings": ["account-admin", "approval-settings"],
    "account-admin": ["account-admin", "approval-settings"],
    "template-settings": ["contract-management"],
    "other-settings": ["account-admin", "budget-admin", "notice-admin"],
    "notice-admin": ["account-admin", "budget-admin", "notice-admin"],
    "other-admin": ["account-admin", "budget-admin", "notice-admin"],
    "approval-settings": ["account-admin", "approval-settings"],
    settings: ["contract-management"],
    "budget-progress": ["budget-progress"],
    "budget-admin": ["budget-admin"],
    // 経営ダッシュボード(#management)は MASTER_ONLY_PAGES で直接ゲートするためここには含めない
    "monthly-pl": ["budget-admin", "cost-management"],
    "staff-assignment": ["staff-assignment", "projects"],
    "application-management": ["application-management"],
    "progress-management": ["progress-management"],
    workload: ["workload"]
  };
  return multi[page] || null;
}

/** 秘密メニュー扱い（マスターロールのみ閲覧可。管理者も不可）。
 *  isMasterOrAdminUser の全許可より先に判定する。
 *  account-settings / approval-settings は account-admin と同じアカウント設定画面を開く別 hash のため同扱い。 */
const MASTER_ONLY_PAGES = new Set([
  "account-admin",
  "account-settings",
  "approval-settings",
  // 経営ダッシュボード。2026-08-24に予算進捗（budget-progress）から分離し、こちらだけマスター限定を継続
  // （外すと isMasterOrAdminUser の全許可で管理者に開いてしまう）。budget-progress は営業メニューへ復活済みのため
  // ここには含めない（nav権限ベースの requiredNavKeysForPage 判定に委ねる）
  "management"
]);

/** 貸与品を見て編集できるか。マスター、または権限画面で「貸与品」（equipment-admin）を付けた人のみ。
 *  管理者には自動で開かない（もとはマスター限定。サーバ canManageEquipmentRow と対） */
function canManageUserEquipment() {
  if (!currentUser) return false;
  return isMasterRoleUser(currentUser) || normalizeNavPermissions(currentUser.navPermissions || []).includes("equipment-admin");
}

function userCanAccessPage(page) {
  if (!currentUser) return false; // 未ログイン / ログアウト直後
  if (MASTER_ONLY_PAGES.has(page)) return isMasterRoleUser(currentUser);
  if (page === "equipment-admin") return canManageUserEquipment();
  if (isMasterOrAdminUser(currentUser)) return true;
  const need = requiredNavKeysForPage(page);
  if (!Array.isArray(need)) return false;
  if (need.length === 0) return true;
  const perms = normalizeNavPermissions(currentUser?.navPermissions || []);
  return need.some((k) => perms.includes(k));
}

async function refreshCurrentUserFromServer() {
  if (!authToken) return;
  try {
    const res = await fetch("/api/me", { headers: { Authorization: `Bearer ${authToken}` } });
    if (!res.ok) return;
    const data = await res.json();
    authUser = data.user;
    const u = authUser;
    Object.assign(currentUser, {
      id: `u${u.id}`,
      dbId: u.id,
      name: u.name,
      role: primaryJobRole(u),
      systemRole: systemRoleOfUser(u),
      email: u.email || "",
      roles: [primaryJobRole(u)],
      navPermissions: normalizeNavPermissions(u.navPermissions || u.nav_permissions),
      avatar: u.avatar != null && String(u.avatar).trim() ? String(u.avatar) : currentUser.avatar || "",
      displayName: u.displayName || "",
      comment: u.comment || "",
      phone: u.phone || ""
    });
  } catch {
    /* noop */
  }
}

function applyCurrentUserFromUsersList() {
  const sid = currentUser?.id || "";
  const sdb = currentUser?.dbId;
  const row = users.find((u) => u.id === sid || (sdb != null && u.dbId === sdb));
  if (!row) return;
  Object.assign(currentUser, {
    name: row.name ?? currentUser.name,
    email: row.email ?? currentUser.email,
    role: primaryJobRole(row),
    systemRole: systemRoleOfUser(row),
    roles: [primaryJobRole(row)],
    navPermissions: normalizeNavPermissions(row.navPermissions),
    avatar: (row.avatar && String(row.avatar).trim()) || currentUser.avatar || ""
  });
}

const users = [
  {
    id: "u1",
    name: "管理者",
    role: "営業",
    systemRole: "マスター",
    roles: ["営業"],
    navPermissions: normalizeNavPermissions([...ALL_NAV_KEYS]),
    avatar: "",
    email: ""
  }
];

// ログインユーザー（実際のアプリではセッション/認証から取得）
const currentUser = users[0]; // 田中（u1）

/** DB で is_active=false（ソフト削除）のユーザー。一覧には出すが案件担当の選択肢などからは除外する */
function isActiveUser(u) {
  if (!u) return false;
  if (u.isActive === false) return false;
  return true;
}

function activeUsers() {
  return users.filter(isActiveUser);
}

/** 案件・ToDo・社内担当など「担当者」プルダウン用（休職・退職は含めない） */
function assignableUsers() {
  return users.filter((u) => isActiveUser(u) && !u.onLeave && !u.retired);
}

/** 案件一覧の担当者絞り込み・案件の担当営業割当の候補（「担当者検索から除外」を除く）。
 *  除外はこの2箇所限定の仕様。メンション・ToDo担当・承認者などの候補には使わない */
function projectAssignCandidateUsers() {
  return assignableUsers().filter((u) => !u.searchExcluded);
}

/** GET /api/users の users 配列でグローバル users を置き換え（メンション・担当者一覧と同期） */
function replaceUsersFromApiPayload(dataUsers) {
  if (!Array.isArray(dataUsers)) return;
  users.length = 0;
  for (const u of dataUsers) {
    const mapped = {
      ...u,
      avatar: u.avatar != null ? String(u.avatar) : "",
      role: primaryJobRole(u),
      systemRole: systemRoleOfUser(u),
      roles: [primaryJobRole(u)],
      navPermissions: normalizeNavPermissions(
        Array.isArray(u.navPermissions) && u.navPermissions.length
          ? u.navPermissions
          : defaultNavPermissionsForUser(u)
      )
    };
    users.push(mapped);
  }
  applyCurrentUserFromUsersList();
}

const PM_MENTION_POPOVER_ID = "pmMentionPopover";

/** @type {null | { textareaId: string, atIndex: number, endQuery: number, list: { name: string }[], activeIndex: number, _taScrollHandler?: (e: Event) => void }} */
let progressMentionSession = null;
let progressMentionGlobalListenersBound = false;
/** メンション用 users の取り直しが並列しないようにする */
let mentionUserDirectoryFetchPromise = null;

/** users が1件・空のとき /api/users を再取得（初回 critical の欠落・キャッシュずれでメンションが自分だけになるのを防ぐ） */
function ensureMentionUserDirectoryLoaded() {
  if (!authToken) return Promise.resolve();
  const activeCount = activeUsers().filter(isActiveUser).length;
  if (activeCount > 1) return Promise.resolve();
  if (mentionUserDirectoryFetchPromise) return mentionUserDirectoryFetchPromise;
  mentionUserDirectoryFetchPromise = (async () => {
    try {
      const res = await fetch("/api/users", { headers: { Authorization: `Bearer ${authToken}` } });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (Array.isArray(data.users) && data.users.length > 0) {
        replaceUsersFromApiPayload(data.users);
      }
    } finally {
      mentionUserDirectoryFetchPromise = null;
    }
  })();
  return mentionUserDirectoryFetchPromise;
}

/** 非同期で users が増えたあと、開いているメンション UI を更新 */
function progressMentionTryRefreshPopoverAfterDirectoryLoad(ta, pinnedAtIndex) {
  if (!(ta instanceof HTMLTextAreaElement)) return;
  const pop = document.getElementById(PM_MENTION_POPOVER_ID);
  if (!pop || !progressMentionSession || progressMentionSession.textareaId !== ta.id) return;
  const ctx = progressComposeFindMentionAnchor(ta.value, ta.selectionStart);
  if (!ctx || ctx.atIndex !== pinnedAtIndex) return;
  const list = progressComposeFilteredMentionUsers(ctx.query);
  progressMentionSession.list = list;
  progressMentionSession.endQuery = ctx.endQuery;
  progressMentionSession.activeIndex = Math.min(progressMentionSession.activeIndex, Math.max(0, list.length - 1));
  progressMentionRenderPopoverList(pop, list, progressMentionSession.activeIndex);
  progressMentionPositionPopover(ta);
}

/**
 * textarea / input 内キャレットの座標（要素左上からの px）。component/textarea-caret-position 由来。
 * @param {HTMLTextAreaElement | HTMLInputElement} element
 * @param {number} position
 * @returns {{ top: number, left: number, height: number }}
 */
function pmGetCaretCoordinates(element, position) {
  const computed = window.getComputedStyle ? window.getComputedStyle(element) : element.currentStyle;
  const isInput = element.nodeName === "INPUT";
  const properties = [
    "direction",
    "boxSizing",
    "width",
    "height",
    "overflowX",
    "overflowY",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "borderStyle",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontStyle",
    "fontVariant",
    "fontWeight",
    "fontStretch",
    "fontSize",
    "fontSizeAdjust",
    "lineHeight",
    "fontFamily",
    "textAlign",
    "textTransform",
    "textIndent",
    "textDecoration",
    "letterSpacing",
    "wordSpacing",
    "tabSize",
    "MozTabSize"
  ];

  const div = document.createElement("div");
  div.id = "pm-caret-mirror-div";
  const old = document.getElementById("pm-caret-mirror-div");
  if (old) old.remove();
  document.body.appendChild(div);

  try {
    const style = div.style;
    style.whiteSpace = "pre-wrap";
    if (!isInput) style.wordWrap = "break-word";
    style.position = "absolute";
    style.visibility = "hidden";

    const isFirefox = window.mozInnerScreenX != null;
    for (let i = 0; i < properties.length; i += 1) {
      const prop = properties[i];
      if (isInput && prop === "lineHeight") {
        if (computed.boxSizing === "border-box") {
          const height = parseInt(computed.height, 10);
          const outerHeight =
            parseInt(computed.paddingTop, 10) +
            parseInt(computed.paddingBottom, 10) +
            parseInt(computed.borderTopWidth, 10) +
            parseInt(computed.borderBottomWidth, 10);
          const targetHeight = outerHeight + parseInt(computed.lineHeight, 10);
          if (height > targetHeight) style.lineHeight = `${height - outerHeight}px`;
          else if (height === targetHeight) style.lineHeight = computed.lineHeight;
          else style.lineHeight = "0";
        } else {
          style.lineHeight = computed.height;
        }
      } else {
        style[prop] = computed[prop];
      }
    }

    if (isFirefox) {
      if (element.scrollHeight > parseInt(computed.height, 10)) style.overflowY = "scroll";
    } else {
      style.overflow = "hidden";
    }

    div.textContent = element.value.substring(0, position);
    if (isInput) div.textContent = String(div.textContent || "").replace(/\s/g, "\u00a0");

    const span = document.createElement("span");
    span.textContent = element.value.substring(position) || ".";
    div.appendChild(span);

    return {
      top: span.offsetTop + parseInt(computed.borderTopWidth, 10),
      left: span.offsetLeft + parseInt(computed.borderLeftWidth, 10),
      height: parseInt(computed.lineHeight, 10)
    };
  } finally {
    if (div.parentNode) div.parentNode.removeChild(div);
  }
}

/** キャレット index に対応するビューポート座標（@ 付近配置用） */
function progressMentionCaretViewportAnchor(ta, index) {
  if (!(ta instanceof HTMLTextAreaElement)) return null;
  const pos = Math.max(0, Math.min(index, ta.value.length));
  try {
    const c = pmGetCaretCoordinates(ta, pos);
    const tr = ta.getBoundingClientRect();
    const lh = Number.isFinite(c.height) && c.height > 0 ? c.height : 18;
    return {
      left: tr.left + c.left,
      top: tr.top + c.top,
      bottom: tr.top + c.top + lh,
      height: lh
    };
  } catch {
    return null;
  }
}

function progressMentionDismissForDomRefresh() {
  const s = progressMentionSession;
  if (s?.textareaId && s._taScrollHandler) {
    const el = document.getElementById(s.textareaId);
    if (el) el.removeEventListener("scroll", s._taScrollHandler);
  }
  document.getElementById("pm-caret-mirror-div")?.remove();
  document.getElementById(PM_MENTION_POPOVER_ID)?.remove();
  progressMentionSession = null;
}

/** メンション照合用: NFKC・不可視文字除去（IME／全角スペース混在の差を吸収） */
function progressMentionNormalizeForMatch(s) {
  return String(s || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim()
    .toLowerCase();
}

/** 確定済みメンション（@氏名 の直後に区切りスペースがある）か */
function progressMentionIsCompletedAt(text, atIndex, caret) {
  const tail = text.slice(atIndex + 1, caret);
  let matched = false;
  for (const u of activeUsers()) {
    const name = String(u.name || "").trim();
    if (!name) continue;
    if (!tail.startsWith(name + " ")) continue;
    const after = tail.slice(name.length + 1);
    if (after.length === 0 || after.trim().length > 0) {
      matched = true;
      break;
    }
  }
  return matched;
}

/** キャレット左の行区間で、単独の @ 開始のメンション候補を探す（メールアドレス中の @ は除外） */
function progressComposeFindMentionAnchor(text, caret) {
  for (let j = caret - 1; j >= 0; j--) {
    const ch = text[j];
    if (ch === "\n" || ch === "\r") break;
    if (ch === "@" || ch === "＠") {
      if (j > 0 && !/[\s\n\r]/.test(text[j - 1])) continue;
      const query = text.slice(j + 1, caret);
      /* クエリ内の空白は「三浦 拓」のように氏名を入力するために必要 */
      if (/[\n\r@＠]/.test(query)) continue;
      if (progressMentionIsCompletedAt(text, j, caret)) continue;
      return { atIndex: j, endQuery: caret, query };
    }
  }
  return null;
}

/** メンション表示用: 氏名の長い順（部分一致の誤検出を防ぐ） */
function progressMentionDisplayNamesSorted() {
  return activeUsers()
    .map((u) => String(u.name || "").trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
}

/** text[i] が @氏名 メンション開始なら { len, raw }、でなければ null */
function progressMessageMentionAt(text, i) {
  if (i < 0 || i >= text.length) return null;
  const ch = text[i];
  if (ch !== "@" && ch !== "＠") return null;
  if (i > 0 && !/[\s\n\r]/.test(text[i - 1])) return null;
  const rest = text.slice(i + 1);
  for (const name of progressMentionDisplayNamesSorted()) {
    if (!rest.startsWith(name)) continue;
    const next = rest[name.length];
    if (
      next === undefined ||
      next === " " ||
      next === "\n" ||
      next === "\r" ||
      /[,.\u3001\u3002!?！？：:;；]/.test(next)
    ) {
      return { len: 1 + name.length, raw: ch + name };
    }
  }
  return null;
}

/** やりとり本文の HTML（改行・スペースを保持、@メンションを強調） */
function progressMessagePlainWithMentionsHtml(text) {
  if (!text) return "";
  if (!text.includes("@") && !text.includes("＠")) return escapeHtml(text);
  let out = "";
  let i = 0;
  while (i < text.length) {
    const hit = progressMessageMentionAt(text, i);
    if (hit) {
      out += `<span class="pm-mention-hl">${escapeHtml(hit.raw)}</span>`;
      i += hit.len;
      continue;
    }
    let j = i + 1;
    while (j < text.length && text[j] !== "@" && text[j] !== "＠") j++;
    out += escapeHtml(text.slice(i, j));
    i = j;
  }
  return out;
}

function progressMessageFindItalicClose(text, from) {
  for (let i = from; i < text.length; i += 1) {
    if (text[i] !== "/") continue;
    if (i === from) continue;
    if (/\s/.test(text[i - 1] || "") || /\s/.test(text[from] || "")) continue;
    return i;
  }
  return -1;
}

/** http/https の URL を位置 i から検出。見つからなければ "" を返す（末尾の句読点・閉じ括弧は除く）。 */
function progressMessageUrlAt(text, i) {
  if (!(text.startsWith("http://", i) || text.startsWith("https://", i))) return "";
  let j = i;
  while (j < text.length && !/[\s<>"'`　）】」』｝]/.test(text[j])) j += 1;
  let url = text.slice(i, j).replace(/[.,;:!?)\]、。」』）】]+$/, "");
  return url.length > "https://".length ? url : "";
}

function progressMessageFormattedInlineHtml(text) {
  let out = "";
  let i = 0;
  while (i < text.length) {
    if (text.startsWith("**", i)) {
      const close = text.indexOf("**", i + 2);
      if (close > i + 2) {
        out += `<strong>${progressMessageFormattedInlineHtml(text.slice(i + 2, close))}</strong>`;
        i = close + 2;
        continue;
      }
    }
    if (text.startsWith("~~", i)) {
      const close = text.indexOf("~~", i + 2);
      if (close > i + 2) {
        out += `<s>${progressMessageFormattedInlineHtml(text.slice(i + 2, close))}</s>`;
        i = close + 2;
        continue;
      }
    }
    // URL 自動リンク（マークダウン整形より先に URL 全体を1トークンとして消費）
    const urlHit = progressMessageUrlAt(text, i);
    if (urlHit) {
      out += `<a class="pm-msg-link" href="${escapeHtmlAttr(urlHit)}" target="_blank" rel="noopener noreferrer">${escapeHtml(urlHit)}</a>`;
      i += urlHit.length;
      continue;
    }
    if (
      text[i] === "/" &&
      (i === 0 || /[\s([{「『（]/.test(text[i - 1] || "")) &&
      i + 1 < text.length &&
      !/\s/.test(text[i + 1] || "")
    ) {
      const close = progressMessageFindItalicClose(text, i + 1);
      if (close > i + 1) {
        out += `<em>${progressMessageFormattedInlineHtml(text.slice(i + 1, close))}</em>`;
        i = close + 1;
        continue;
      }
    }
    let j = i + 1;
    while (
      j < text.length &&
      !text.startsWith("**", j) &&
      !text.startsWith("~~", j) &&
      text[j] !== "/" &&
      !progressMessageUrlAt(text, j)
    ) j += 1;
    out += progressMessagePlainWithMentionsHtml(text.slice(i, j));
    i = j;
  }
  return out;
}

/** 行頭 `> ` の連続行を引用（<blockquote>）にする（Slack と同じ記法）。
 *  コードブロックの外側だけに適用するため、progressMessageBodyDisplayHtml がフェンスで区切った
 *  区間ごとに呼ぶ。atLineStart=false は区間が行の途中から始まる（直前がインラインコード）場合。 */
function progressMessageQuoteBlocksHtml(seg, atLineStart) {
  if (!seg.includes(">")) return progressMessageFormattedInlineHtml(seg);
  const QUOTE_RE = /^>(?: |$)/;
  // 行を「引用 / 通常」の run に分ける
  const runs = [];
  seg.split("\n").forEach((line, idx) => {
    const isQuote = (idx > 0 || atLineStart) && QUOTE_RE.test(line);
    const last = runs[runs.length - 1];
    if (last && last.quote === isQuote) last.lines.push(line);
    else runs.push({ quote: isQuote, lines: [line] });
  });
  // 中身が空の引用（ボタンを押しただけ・「>」だけの行）は箱を出さず通常の文字に戻し、隣の通常 run とつなぐ
  const merged = [];
  runs.forEach((r) => {
    const quote = r.quote && r.lines.some((l) => l.replace(QUOTE_RE, "").trim());
    const last = merged[merged.length - 1];
    if (last && !last.quote && !quote) last.lines.push(...r.lines);
    else merged.push({ quote, lines: r.lines });
  });
  // 引用の前後の改行1つは <blockquote> のマージンと二重になるので、run の境界で飲み込む（join に含めない）
  return merged
    .map((r) =>
      r.quote
        ? `<blockquote class="pm-msg-quote">${progressMessageFormattedInlineHtml(r.lines.map((l) => l.replace(QUOTE_RE, "")).join("\n"))}</blockquote>`
        : progressMessageFormattedInlineHtml(r.lines.join("\n"))
    )
    .join("");
}

function progressMessageBodyDisplayHtml(body) {
  const text = String(body || "");
  if (!text) return "";
  // コードブロック（``` ... ```）をブロックレベルで先に処理し、その他の行は引用→インライン整形
  const FENCE = "```";
  if (!text.includes(FENCE)) return progressMessageQuoteBlocksHtml(text, true);
  let out = "";
  let i = 0;
  const atLineStart = (pos) => pos === 0 || text[pos - 1] === "\n";
  while (i < text.length) {
    const fenceStart = text.indexOf(FENCE, i);
    if (fenceStart === -1) {
      // フェンスなし：残り全体を引用→インライン処理
      out += progressMessageQuoteBlocksHtml(text.slice(i), atLineStart(i));
      break;
    }
    // 閉じフェンスを探す
    const fenceEnd = text.indexOf(FENCE, fenceStart + FENCE.length);
    if (fenceEnd === -1) {
      // 閉じなし：フェンス記号だけ落として残りをインライン処理（``` が生のまま画面に出るのを防ぐ）
      let tail = text.slice(fenceStart + FENCE.length);
      // 行頭のフェンスだった場合は直後の改行も落とす（記号の跡が空行として残らないように）
      if (tail.startsWith("\n") && (fenceStart === 0 || text[fenceStart - 1] === "\n")) tail = tail.slice(1);
      out += progressMessageQuoteBlocksHtml(text.slice(i, fenceStart) + tail, atLineStart(i));
      break;
    }
    const raw = text.slice(fenceStart + FENCE.length, fenceEnd);
    // 中身に改行があれば従来どおりブロック（<pre>）。1行で閉じた ```code``` は文中のインラインコードとして
    // 扱う（ブロックにすると文の途中で改行が入り、1文が3行に割れて読めなくなるため）
    const isBlock = raw.includes("\n");
    // フェンス前のテキスト。ブロックの直前の改行1つは <pre> の上下マージンと二重になるので飲み込む
    // （閉じフェンス側は従来から飲み込んでいて、上下の余白が非対称になっていた）
    let before = text.slice(i, fenceStart);
    if (isBlock) before = before.replace(/\n$/, "");
    if (before) out += progressMessageQuoteBlocksHtml(before, atLineStart(i));
    const codeContent = raw.replace(/^\n/, "").replace(/\n$/, "");
    // 中身が空（選択せずにボタンを押しただけ）なら箱を出さない
    if (codeContent) {
      out += isBlock
        ? `<pre class="pm-msg-code-block"><code>${escapeHtml(codeContent)}</code></pre>`
        : `<code class="pm-msg-code-inline">${escapeHtml(codeContent)}</code>`;
    }
    i = fenceEnd + FENCE.length;
    // ブロック直後の改行1つを飲み込む
    if (isBlock && text[i] === "\n") i += 1;
  }
  return out;
}

function progressMentionUserMatchesQuery(normName, normId, f) {
  if (!f) return true;
  if (normId.includes(f)) return true;
  if (normName.includes(f)) return true;
  const tokens = normName.split(/[\s\u3000]+/).filter(Boolean);
  return tokens.some((t) => t.startsWith(f) || t.includes(f));
}

/** 小さいほど上位（Slack 的に先頭一致を優先） */
function progressMentionUserRank(normName, f) {
  if (!f) return 0;
  if (normName.startsWith(f)) return 0;
  const tokens = normName.split(/[\s\u3000]+/).filter(Boolean);
  if (tokens[0] && tokens[0].startsWith(f)) return 1;
  if (tokens.some((t) => t.startsWith(f))) return 2;
  if (normName.includes(f)) return 3;
  return 99;
}

/** 通知用入力のトークン（名前・メールの一部など）からユーザーを一意に推定 */
function progressResolveNotifyTokenToUser(tok) {
  const t = String(tok || "").trim().toLowerCase();
  if (!t) return null;
  const pool = activeUsers();
  const exactName = pool.find((u) => String(u.name || "").trim().toLowerCase() === t);
  if (exactName) return exactName;
  const exactEmail = pool.find((u) => String(u.email || "").trim().toLowerCase() === t);
  if (exactEmail) return exactEmail;
  const partial = pool.filter((u) => {
    const n = String(u.name || "").trim().toLowerCase();
    if (!n) return false;
    return n.includes(t) || n.split(/[\s\u3000]+/).some((p) => p.startsWith(t));
  });
  if (partial.length === 1) return partial[0];
  return null;
}

/** お知らせ欄から @メンション（@名前 @名前 …）を抽出 */
function progressExtractNotifyMentionDisplays(notifyRaw) {
  const text = String(notifyRaw || "");
  const displays = [];
  const seen = new Set();
  let i = 0;
  while (i < text.length) {
    const hit = progressMessageMentionAt(text, i);
    if (hit) {
      const disp = hit.raw.replace(/^[＠@]/, "").trim();
      if (disp) {
        const key = disp.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          displays.push(disp);
        }
      }
      i += hit.len;
      continue;
    }
    i += 1;
  }
  if (!displays.length && text.trim()) {
    const tokens = text.split(/\s+/).map((x) => x.trim()).filter(Boolean);
    for (const tok of tokens) {
      const cleaned = tok.replace(/^[＠@]+/, "").trim();
      if (!cleaned) continue;
      const u = progressResolveNotifyTokenToUser(cleaned);
      if (!u) continue;
      const disp = String(u.name || "").trim() || String(u.email || "").trim();
      if (!disp) continue;
      const key = disp.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      displays.push(disp);
    }
  }
  return displays;
}

/** お知らせ欄（@名前 …）→ 送信用のユーザーID（DB id）。本文には @ を書き足さず notifyUserIds として送る */
function progressNotifyUserIdsFromRaw(notifyRaw) {
  const ids = [];
  for (const disp of progressExtractNotifyMentionDisplays(notifyRaw)) {
    const u = progressResolveNotifyTokenToUser(disp);
    const id = u?.dbId != null ? Number(u.dbId) : NaN;
    if (Number.isFinite(id) && id >= 1 && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

function progressComposeFilteredMentionUsers(query) {
  const f = progressMentionNormalizeForMatch(query);
  // メンション候補は担当者選択と同じく休職中・退職済を除外（assignableUsers）。
  // 制限付きプロジェクトのメンバー外は「担当外」として候補に残し、選べなくする（15t-challenge-members.js）
  const ch = progressRouteChallenge();
  const rows = assignableUsers().map((u) => {
    const name = String(u.name || "").trim();
    return { id: String(u.id != null ? u.id : ""), name: name || "（無名）", blocked: !progressChallengeUserAllowed(ch, u) };
  });
  let out = rows;
  if (f) {
    out = rows.filter((u) => {
      const normName = progressMentionNormalizeForMatch(u.name);
      const normId = progressMentionNormalizeForMatch(u.id);
      return progressMentionUserMatchesQuery(normName, normId, f);
    });
    out = out
      .map((u) => ({
        u,
        r: progressMentionUserRank(progressMentionNormalizeForMatch(u.name), f)
      }))
      .sort((a, b) => {
        if (a.r !== b.r) return a.r - b.r;
        return String(a.u.name || "").localeCompare(String(b.u.name || ""), "ja");
      })
      .map(({ u }) => u);
  }
  // id は所属部署の逆引き（userDepartmentName）に使う。挿入されるのは name のみ
  return out.slice(0, 25).map(({ id, name, blocked }) => ({ id, name, blocked }));
}

function progressMentionEnsureTextareaId(ta) {
  if (!ta.id) ta.id = `pm-mention-ta-${Math.random().toString(36).slice(2, 10)}`;
  return ta.id;
}

function progressMentionPositionPopover(ta) {
  const pop = document.getElementById(PM_MENTION_POPOVER_ID);
  if (!pop || !ta) return;
  const ctx = progressComposeFindMentionAnchor(ta.value, ta.selectionStart);
  if (!ctx) return;

  const margin = 8;
  const r = ta.getBoundingClientRect();
  const maxW = Math.min(360, Math.max(240, r.width));
  pop.style.width = `${maxW}px`;

  /* Slack に近い: 入力中キャレット付近（クエリ末尾）を基準。取れなければ @ 位置 */
  let anchor = progressMentionCaretViewportAnchor(ta, ctx.endQuery);
  if (!anchor || !Number.isFinite(anchor.left) || !Number.isFinite(anchor.top)) {
    anchor = progressMentionCaretViewportAnchor(ta, ctx.atIndex);
  }

  const placeFallback = () => {
    let left = r.left;
    if (left + maxW > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - margin - maxW);
    pop.style.left = `${Math.round(left)}px`;
    pop.style.top = `${Math.round(r.bottom + 4)}px`;
    pop.style.maxHeight = "";
    const le = pop.querySelector(".pm-mention-popover-list");
    if (le) le.style.maxHeight = "";
  };

  if (!anchor || !Number.isFinite(anchor.left) || !Number.isFinite(anchor.top)) {
    placeFallback();
    return;
  }

  pop.style.visibility = "hidden";
  pop.style.left = `${margin}px`;
  pop.style.top = `${margin}px`;
  pop.style.maxHeight = "";
  const listReset = pop.querySelector(".pm-mention-popover-list");
  if (listReset) listReset.style.maxHeight = "";

  void pop.offsetHeight;
  const ph = pop.offsetHeight;
  const pw = Math.min(maxW, pop.offsetWidth || maxW);

  let left = anchor.left;
  /* まずキャレット行の上に出す（Slack 風）。上に余裕がなければ下へ */
  let top = anchor.top - ph - 4;
  if (top < margin) {
    top = anchor.bottom + 4;
  }
  if (left + pw > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - margin - pw);
  if (left < margin) left = margin;
  if (top + ph > window.innerHeight - margin) {
    top = Math.max(margin, window.innerHeight - margin - ph);
  }
  if (top < margin) {
    top = margin;
    const maxH = Math.max(140, window.innerHeight - 2 * margin);
    pop.style.maxHeight = `${maxH}px`;
    const head = pop.querySelector(".pm-mention-popover-head");
    const listEl = pop.querySelector(".pm-mention-popover-list");
    const headH = head ? head.offsetHeight : 28;
    if (listEl) listEl.style.maxHeight = `${Math.max(72, maxH - headH - 12)}px`;
  }

  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;
  pop.style.visibility = "";
}

function progressMentionRenderPopoverList(pop, list, activeIndex) {
  const listEl = pop.querySelector(".pm-mention-popover-list");
  if (!listEl) return;
  if (!list.length) {
    listEl.innerHTML = `<div class="pm-mention-empty" role="status">一致するユーザーがいません</div>`;
    return;
  }
  listEl.innerHTML = list
    .map((u, i) => {
      const dept = userDepartmentName(u);
      const deptHtml = dept ? `<span class="pm-mention-item-dept">（${escapeHtml(dept)}）</span>` : "";
      const blockedHtml = u.blocked ? `<span class="pm-mention-item-blocked">担当外のユーザーです</span>` : "";
      return `<button type="button" class="pm-mention-item${i === activeIndex ? " is-active" : ""}${u.blocked ? " is-blocked" : ""}" role="option" data-pm-mention-idx="${i}" aria-selected="${i === activeIndex ? "true" : "false"}"${u.blocked ? ' aria-disabled="true"' : ""}><span class="pm-mention-item-name">${escapeHtml(u.name)}</span>${deptHtml}${blockedHtml}</button>`;
    })
    .join("");
}

function progressMentionOpenForTextarea(ta) {
  progressMentionDismissForDomRefresh();
  const ctx = progressComposeFindMentionAnchor(ta.value, ta.selectionStart);
  if (!ctx) return;
  const list = progressComposeFilteredMentionUsers(ctx.query);
  const taId = progressMentionEnsureTextareaId(ta);
  progressMentionSession = {
    textareaId: taId,
    atIndex: ctx.atIndex,
    endQuery: ctx.endQuery,
    list,
    activeIndex: 0
  };
  const pop = document.createElement("div");
  pop.id = PM_MENTION_POPOVER_ID;
  pop.className = "pm-mention-popover";
  pop.setAttribute("role", "listbox");
  pop.dataset.linkedTextareaId = taId;
  pop.innerHTML = `<div class="pm-mention-popover-head" aria-hidden="true">ユーザーを選択</div><div class="pm-mention-popover-list"></div>`;
  document.body.appendChild(pop);
  progressMentionRenderPopoverList(pop, progressMentionSession.list, progressMentionSession.activeIndex);
  const onTaScroll = () => progressMentionPositionPopover(ta);
  ta.addEventListener("scroll", onTaScroll, { passive: true });
  progressMentionSession._taScrollHandler = onTaScroll;
  progressMentionPositionPopover(ta);
  const pinnedAt = ctx.atIndex;
  void ensureMentionUserDirectoryLoaded().then(() => {
    progressMentionTryRefreshPopoverAfterDirectoryLoad(ta, pinnedAt);
  });
}

/** 書式ボタン（太字・斜体・打消・引用・コード）。編集欄用。[data-pm-format-scope] の中に textarea と一緒に置く */
function progressComposeFormatToolbarHtml() {
  return `<div class="pm-compose-toolbar">
    <button type="button" class="pm-compose-tool-btn" title="太字" data-pm-compose-format="bold">B</button>
    <button type="button" class="pm-compose-tool-btn pm-compose-tool-italic" title="斜体" data-pm-compose-format="italic"><i>/</i></button>
    <button type="button" class="pm-compose-tool-btn pm-compose-tool-strike" title="打ち消し線" data-pm-compose-format="strike"><s>S</s></button>
    <button type="button" class="pm-compose-tool-btn pm-compose-tool-quote" title="引用" data-pm-compose-format="quote" aria-label="引用"><span class="pm-compose-tool-ic" aria-hidden="true">${pmBoardIconSvg("quote", 14)}</span></button>
    <button type="button" class="pm-compose-tool-btn pm-compose-tool-code" title="コードブロック" data-pm-compose-format="code">&lt;/&gt;</button>
  </div>`;
}

function applyProgressComposeFormat(format, btn) {
  // PC は .pm-compose、SP はボトムシート .pm-sp-compose-sheet が入力欄の入れ物。
  // SP は片方しか DOM に存在しない（progressManagementIssueDetailHtml が _isSpView で分岐して return する）
  // 投稿済みコメントの編集欄・課題の詳細欄は [data-pm-format-scope] で囲む
  const wrap = btn?.closest?.(".pm-compose, .pm-sp-compose-sheet, [data-pm-format-scope]");
  const ta = wrap?.querySelector?.("[data-pm-msg-body], [data-pm-edit-message-body], [data-pm-modal-agenda-body]");
  if (!(ta instanceof HTMLTextAreaElement)) return false;
  const start = Number.isFinite(ta.selectionStart) ? ta.selectionStart : ta.value.length;
  const end = Number.isFinite(ta.selectionEnd) ? ta.selectionEnd : ta.value.length;
  if (format === "quote") {
    // 引用は行単位の記法（行頭 `> `）。選択範囲を行頭まで広げ、含まれる各行の先頭に付ける
    const lineStart = ta.value.lastIndexOf("\n", start - 1) + 1;
    // 行選択で選択が改行の直後で終わっている時、その改行は含めない（次の行まで引用化しない）
    const quoteEnd = end > start && ta.value[end - 1] === "\n" ? end - 1 : end;
    const quoted = ta.value
      .slice(lineStart, quoteEnd)
      .split("\n")
      .map((l) => `> ${l}`)
      .join("\n");
    ta.value = `${ta.value.slice(0, lineStart)}${quoted}${ta.value.slice(quoteEnd)}`;
    ta.focus();
    try {
      ta.setSelectionRange(lineStart + quoted.length, lineStart + quoted.length);
    } catch {
      /* noop */
    }
    ta.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  }
  const pair = format === "bold" ? ["**", "**"] : format === "italic" ? ["/", "/"] : format === "strike" ? ["~~", "~~"] : format === "code" ? ["```\n", "\n```"] : null;
  if (!pair) return false;
  const before = ta.value.slice(0, start);
  const selected = ta.value.slice(start, end);
  const after = ta.value.slice(end);
  ta.value = `${before}${pair[0]}${selected}${pair[1]}${after}`;
  const caretStart = start + pair[0].length;
  const caretEnd = caretStart + selected.length;
  ta.focus();
  try {
    ta.setSelectionRange(selected ? caretStart : caretEnd, caretEnd);
  } catch {
    /* noop */
  }
  ta.dispatchEvent(new Event("input", { bubbles: true }));
  return true;
}

function progressMentionSyncFromTextarea(ta) {
  if (!(ta instanceof HTMLTextAreaElement)) return;
  if (ta.dataset.pmMentionTextarea !== "1") return;
  const ctx = progressComposeFindMentionAnchor(ta.value, ta.selectionStart);
  if (!ctx) {
    progressMentionDismissForDomRefresh();
    return;
  }
  const list = progressComposeFilteredMentionUsers(ctx.query);
  progressMentionEnsureTextareaId(ta);
  const pop = document.getElementById(PM_MENTION_POPOVER_ID);
  if (
    pop &&
    progressMentionSession &&
    progressMentionSession.textareaId === ta.id &&
    progressMentionSession.atIndex === ctx.atIndex
  ) {
    progressMentionSession.endQuery = ctx.endQuery;
    progressMentionSession.list = list;
    progressMentionSession.activeIndex = Math.min(progressMentionSession.activeIndex, Math.max(0, list.length - 1));
    progressMentionRenderPopoverList(pop, list, progressMentionSession.activeIndex);
    progressMentionPositionPopover(ta);
    return;
  }
  progressMentionOpenForTextarea(ta);
}

function progressMentionApplyPick(name) {
  const s = progressMentionSession;
  if (s?.list?.find((r) => r.name === name)?.blocked) {
    showToast(`${name} さんは担当外のユーザーです（このプロジェクトのメンバーではありません）`);
    return;
  }
  const ta = s ? document.getElementById(s.textareaId) : null;
  if (!(ta instanceof HTMLTextAreaElement) || !s) {
    progressMentionDismissForDomRefresh();
    return;
  }
  const text = ta.value;
  const { atIndex, endQuery } = s;
  const before = text.slice(0, atIndex);
  const after = text.slice(endQuery);
  const ins = `@${name} `;
  ta.value = before + ins + after;
  const pos = before.length + ins.length;
  ta.selectionStart = ta.selectionEnd = pos;
  progressMentionDismissForDomRefresh();
  ta.focus();
  ta.dispatchEvent(new Event("input", { bubbles: true }));
}

function progressMentionMoveActive(delta) {
  const s = progressMentionSession;
  const pop = document.getElementById(PM_MENTION_POPOVER_ID);
  if (!s || !pop || !s.list.length) return;
  s.activeIndex = Math.max(0, Math.min(s.list.length - 1, s.activeIndex + delta));
  progressMentionRenderPopoverList(pop, s.list, s.activeIndex);
  const ta = document.getElementById(s.textareaId);
  if (ta instanceof HTMLTextAreaElement) progressMentionPositionPopover(ta);
}

function bindProgressMentionGlobalListenersOnce() {
  if (progressMentionGlobalListenersBound) return;
  progressMentionGlobalListenersBound = true;
  const onScrollOrResize = () => {
    if (!progressMentionSession) return;
    const ta = document.getElementById(progressMentionSession.textareaId);
    if (ta instanceof HTMLTextAreaElement) progressMentionPositionPopover(ta);
    else progressMentionDismissForDomRefresh();
  };
  window.addEventListener("scroll", onScrollOrResize, true);
  window.addEventListener("resize", onScrollOrResize);
}

/** 請求明細の行担当・担当者割当で選べるユーザー（営業ロール除外。休職・退職も除外） */
function staffAssignableUsers() {
  return users
    .filter((u) => isActiveUser(u) && !u.onLeave && !u.retired && !isStaffAssignmentExcludedSalesUser(u))
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ja"));
}

function canEditProject(project) {
  if (!project) return false;
  return project.salesOwnerId === currentUser.id || project.accountingOwnerId === currentUser.id
    || project.assignedUserId === currentUser.id;
}

function canEditQuote(p) {
  return salesOwnerUser(p)?.id === currentUser.id;
}

function canEditInvoice(p) {
  return salesOwnerUser(p)?.id === currentUser.id || accountingOwnerUser(p)?.id === currentUser.id;
}

/** 入金確認の権限: 職種「経理」（旧管理部は経理へ正規化）の全員とマスター/管理者は、案件の担当を問わず可。
 *  旧仕様は案件作成時に自動設定される隠し項目 accounting_owner_id の本人のみで、
 *  誰も意図して割り当てていない値で可否が決まっていたため緩和（2026-07-10）。 */
function canConfirmPayment(p) {
  return isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser);
}

function canTransferToAccounting(p) {
  return salesOwnerUser(p)?.id === currentUser.id;
}

function isReadOnlyProject(project) {
  return !canEditProject(project);
}

/** ダッシュボードの売上進捗を営業向け「個人・月次」にする。それ以外は会社全体の進捗 */
function isDashboardSalesPersonalView() {
  return primaryJobRole(currentUser) === "営業";
}

function readOnlyHint(project) {
  if (!project) return "この案件は閲覧専用です";
  const salesUser = salesOwnerUser(project);
  const assignedName = salesUser?.name || project.owner || "担当者";
  return `この案件は${assignedName}担当のため閲覧専用です`;
}

function defaultAccountingUserId() {
  return users.find((u) => isActiveUser(u) && isAccountingDepartmentJobRole(u.role))?.id || currentUser.id;
}

const clients = [
];

const domains = [];

const projects = [];

// ────────────────────────────────────────────────────────────────
// 月次経費データ
// project.costs[] は案件原価（変動費）
// expenses[] は会社全体の固定費・管理費（PLの販売費・一般管理費）
// ────────────────────────────────────────────────────────────────
const expenses = [];

const EXPENSE_CATEGORIES = ["人件費", "賃料", "広告宣伝費", "通信・システム費", "交際費", "その他経費"];

const menu = [
  { key: "dashboard",              label: "ダッシュボード",   group: "primary" },
  { key: "progress-management",    label: "ToDo管理",         group: "primary" },
  // 許可ツールに備品の貸与申請を足して「社内申請」に（2026-10-03。キーは nav 権限の互換のため allowed-tools のまま）。
  // 自社ツールはあまり使わないので下げた
  { key: "allowed-tools",          label: "社内申請",         group: "primary" },
  { key: "development",            label: "自社ツール",       group: "primary" },
  // 営業メニュー：プロジェクト一覧・取引先一覧・原価割当（営業は原価割当で自分の分だけ照会）
  // 取引先一覧は 2026-07-30 に左メニューへ復活（顧客担当者タブの導線）。
  // 予算進捗は2026-08-24に営業メニュー末尾へ復活（全体・部署タブ・個人別のみ。経営ダッシュボードとは別ページ）
  { type: "section", id: "sales",     label: "営業メニュー", group: "primary" },
  { key: "projects",               label: "案件一覧", group: "primary" },
  { key: "client-list",            label: "取引先一覧",        group: "primary" },
  { key: "cost-allocation",        label: "原価割当",          group: "primary" },
  { key: "budget-progress",        label: "予算進捗",          group: "primary" },
  { type: "section", id: "admin",     label: "管理者メニュー", group: "primary" },
  { key: "application-management", label: "申請承認",          group: "primary" },
  // 稼働状況（workload）はいったんメニューから撤去（2026-07-30 ユーザー指示・ページとコードは残置）
  { type: "section", id: "backoffice", label: "経理・情シスメニュー", group: "primary" },
  // 契約一覧は取引先一覧へ集約したので左メニューには出さない（2026-10-28）。権限キーとしては残す
  // （見積・書類テンプレート設定や、マスター・経理のみの取引先をサーバが返す範囲の判定に使っているため。権限画面の✓も残る）
  { key: "contract-management",    label: "契約管理",          group: "primary", sidebarHidden: true },
  { key: "cost-management",        label: "原価一覧",          group: "primary" },
  // 請求書受取: invoice@ 宛に届いた請求書メールの受信箱（バクラク「請求書受取」と同名・15r-invoice-mail-inbox.js）
  { key: "invoice-receipt",        label: "請求書受取",        group: "primary" },
  { key: "domain-list",            label: "ドメイン",          group: "primary" },
  // 貸与品（スタッフごとの貸与PC・携帯電話など）。マスター以外は権限画面で✓した人だけ（canManageUserEquipment）
  { key: "equipment-admin",        label: "貸与品",            group: "primary" },
  // 秘密メニュー（マスターロールのみ表示・navPermissionsに関わらず非表示対象）
  { type: "section", id: "secret", label: "秘密メニュー", group: "primary", masterOnly: true },
  { key: "account-admin", label: "アカウント設定", group: "primary", masterOnly: true },
  { key: "secret-menu", label: "操作ログ", group: "primary", masterOnly: true },
  // 経営ダッシュボード（推移・原価・月次P/L・予算設定）。マスター限定。予算進捗(全体/部署タブ/個人別)とは別ページ
  { key: "management", label: "経営ダッシュボード", group: "primary", masterOnly: true }
];

/** 左サイドバーに載せている項目のみ（ここに無いナビキーはマスターでもリンクに含めない） */
const LEFT_SIDEBAR_NAV_KEYS = menu.filter((m) => m.key).map((m) => m.key);

/** マスター・管理者ロールは DB の nav_permissions に関わらず、左メニュー定義の全項目を表示
 *  注意: 秘密メニューの表示は別途 isMasterRoleUser で制御されるため管理者は見えない */
function effectiveSidebarNavPermissions() {
  if (!currentUser) return ALL_NAV_KEYS; // ログアウト直後など currentUser が null の場合
  // マスター or 管理者はメニュー定義の全項目を表示（秘密メニューは別ガードで制御）
  // 貸与品は管理者でも✓が無ければ出さない（ページ・API ともマスター or 貸与品権限のみ）
  if (isMasterOrAdminUser(currentUser)) return LEFT_SIDEBAR_NAV_KEYS.filter((k) => k !== "equipment-admin" || canManageUserEquipment());
  // 空配列はフォールバック対象（"|| ALL_NAV_KEYS" だと [] が truthy でスキップされる）
  const p = currentUser.navPermissions;
  return Array.isArray(p) && p.length > 0 ? p : ALL_NAV_KEYS;
}

function tone(status) {
  if (status.includes("期限切れ") || status.includes("未")) return "attention";
  if (status.includes("済") || status.includes("OK")) return "safe";
  return "";
}

function detectStage(project) {
  if (project.currentStage) return project.currentStage;
  if (!project.contractDone) return "contracts";
  const t = project.nextAction;
  if (t.includes("入金")) return "payments";
  if (t.includes("請求")) return "invoices";
  if (t.includes("見積")) return "quotes";
  if ((t.includes("請書") || t.includes("発注")) && project.useOrderFlow) return "orders";
  return "projects";
}

function getClientById(id) {
  return clients.find((c) => c.id === id);
}

function getUserById(id) {
  return users.find((u) => u.id === id);
}

function clientName(project) {
  return getClientById(project.clientId)?.name || project.client || "未設定クライアント";
}

/** 書類の宛名担当者。案件詳細で選んだ担当者（案件ごと）を優先し、無ければクライアントマスタの担当者名 */
function clientContact(project) {
  return project.person || getClientById(project.clientId)?.contactName || "ご担当者様";
}

/**
 * 書類・郵送先の宛先住所。案件で選んだ支社（clientBranchName）を優先し、無ければクライアントマスタの住所。
 * サーバー側の同等ロジックは api/_docAddress.js resolveProjectRecipient（両側同時に直すこと）。
 * 支社・担当者マスタが未ロードなら ensureClientBranchesLoaded/ensureClientContactsLoaded がロードを開始し、
 * 完了時に render() されるので、いったんクライアントマスタの値で返して後続の再描画で解決される。
 */
function clientRecipientAddress(project) {
  const cli = getClientById(project.clientId);
  let addr = { postalCode: cli?.postalCode || "", prefecture: cli?.prefecture || "", address: cli?.address || "", building: cli?.building || "", source: "client" };
  const branchName = String(project.clientBranchName || "").trim();
  if (branchName) {
    ensureClientBranchesLoaded();
    const hit = (uiState.clientBranches.rows || []).find((r) => !r.archived && r.clientId === project.clientId && r.name === branchName);
    // 支社名が選択されていてもマスタに一致行が無ければ（改名・アーカイブ済み等）source は "client" のまま。
    // 一致しても住所（都道府県＋住所）が空なら同じくクライアントマスタの住所を使う（部署名だけを
    // 支社として登録しているケースがあり、そのまま採用すると宛先が空欄の書類・郵送物になる）。
    // 郵便番号だけの部分的な補完はしない。判定はサーバ側 api/_docAddress.js と同じ（両側同時に直すこと）。
    if (hit && `${hit.prefecture || ""}${hit.address || ""}`.trim()) {
      addr = { postalCode: hit.postalCode, prefecture: hit.prefecture, address: hit.address, building: hit.building, source: "branch" };
    }
  }
  return addr;
}

/** 郵便番号を〒XXX-XXXX形式に整形（7桁に満たない/崩れた値はそのまま出す。データのハイフン有無・空白混在を吸収） */
function formatPostalCodeDisplay(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 7) return `〒${digits.slice(0, 3)}-${digits.slice(3)}`;
  return raw ? `〒${String(raw).trim()}` : "";
}

/** 案件の宛先住所を「郵便番号\n住所\n建物名」の3行にする（改行区切り。空欄の行は詰める）。
 *  サーバー側の同等ロジックは api/_docRender.js buildDocSheet（両側同時に直すこと）。 */
function clientRecipientAddressLine(project) {
  const a = clientRecipientAddress(project);
  return [formatPostalCodeDisplay(a.postalCode), `${a.prefecture || ""}${a.address || ""}`.trim(), a.building]
    .filter(Boolean)
    .join("\n");
}

/** クライアント・パートナー編集 UI と同じ「済」のみ完了（未／済の2値。表記ゆれは想定しない） */
function clientPartnerMasterIsDone(raw) {
  return String(raw ?? "").trim() === "済";
}

/**
 * 案件に紐づくクライアントマスタ（fp_projects.client_id と clients[].id の形式差・
 * 一覧未同期時でも project.client 名でフォールバック）
 */
function resolveProjectClientMaster(project) {
  if (!project) return null;
  const byId = getClientById(project.clientId);
  if (byId) return byId;
  const cid = String(project.clientId || "").trim();
  const m = cid.match(/^c(\d+)$/i);
  if (m) {
    const db = Number(m[1]);
    if (Number.isFinite(db) && db > 0) {
      const hit = clients.find((c) => c.dbId != null && Number(c.dbId) === db);
      if (hit) return hit;
    }
  }
  const nm = String(project.client || "").trim();
  if (!nm) return null;
  return (
    clients.find(
      (c) =>
        String(c.name || "").trim() === nm ||
        String(c.shortName || "").trim() === nm
    ) || null
  );
}

/** 案件の請求先表示名に一致するパートナーマスタ（クライアント側と別マスタのときの名寄せ） */
function resolvePartnerMasterMatchingProjectClientName(project) {
  const nm = String(project?.client || "").trim();
  if (!nm) return null;
  const rows = uiState.partnersCache || [];
  for (const pt of rows) {
    if (!pt) continue;
    const nameOnly = String(pt.name ?? "").trim();
    const label = partnerMasterDisplayLabel(pt);
    if (nameOnly && nameOnly === nm) return pt;
    if (label && label === nm) return pt;
  }
  return null;
}

/** 契約一覧で同期済みのクライアント行（clients より先に更新されている場合の参照用） */
function contractMasterClientRowForProject(project) {
  if (!project?.clientId) return null;
  return (
    contractMaster.find(
      (r) =>
        r &&
        r.id === project.clientId &&
        (r.classification || "クライアント") === "クライアント"
    ) || null
  );
}

/** 一覧・ダッシュボード等: 明細行の担当が優先（複数なら「複数」） */
function projectOwnerDisplayName(project) {
  if (!project) return "未設定";
  const lineIds = collectNormalLineAssignedUserIdsForProject(project);
  if (lineIds.size > 1) return "複数";
  if (lineIds.size === 1) return getUserById([...lineIds][0])?.name || "未設定";
  return salesOwnerUser(project)?.name || project.owner || "未設定";
}

/** プロジェクト一覧の「自社担当」列: 営業を優先して表示。営業のみがいない案件はメディア等の担当も表示 */
function projectListOwnerDisplayName(project) {
  if (!project) return "未設定";
  const orderedIds = [];
  const pushId = (id) => {
    const s = String(id || "").trim();
    if (!s || orderedIds.includes(s)) return;
    orderedIds.push(s);
  };
  // 営業担当はひとり: salesOwnerId が実在ユーザーを指す限りそれだけを採用し、
  // assignedUserId は salesOwnerId が未設定/退職者等で解決できない時だけのフォールバックにする
  // （同期漏れの旧値や別担当への割当を、salesOwnerId が有効な間は別人として並べない）
  pushId(salesOwnerUser(project)?.id);
  for (const uid of collectNormalLineAssignedUserIdsForProject(project)) {
    pushId(uid);
  }
  const collectNames = (salesOnly) => {
    const out = [];
    const seen = new Set();
    for (const id of orderedIds) {
      const u = getUserById(id);
      if (!u) continue;
      if (salesOnly && !isStaffAssignmentExcludedSalesUser(u)) continue;
      const n = String(u.name || "").trim();
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    return out;
  };
  const salesFirst = collectNames(true);
  if (salesFirst.length) return salesFirst.join("、");
  const anyRole = collectNames(false);
  if (anyRole.length) return anyRole.join("、");
  return projectOwnerDisplayName(project);
}

/** 一覧表示と同じ基準で案件に紐づく自社担当 user id（有効なユーザーのみ） */
function collectProjectListAssigneeUserIds(project) {
  if (!project) return [];
  const orderedIds = [];
  const pushId = (id) => {
    const s = String(id || "").trim();
    if (!s || orderedIds.includes(s)) return;
    orderedIds.push(s);
  };
  pushId(salesOwnerUser(project)?.id);
  for (const uid of collectNormalLineAssignedUserIdsForProject(project)) {
    pushId(uid);
  }
  return orderedIds.filter((id) => getUserById(id));
}

function sanitizeProjectListAssigneeFilterIds(ids) {
  return (Array.isArray(ids) ? ids : [])
    .map((x) => String(x || "").trim())
    .filter((id) => id && id !== "__none__");
}

function projectMatchesAssigneeFilter(project, selectedIds) {
  const pick = sanitizeProjectListAssigneeFilterIds(selectedIds);
  if (!pick.length) return true;
  for (const id of pick) {
    if (collectProjectListAssigneeUserIds(project).includes(id)) return true;
  }
  return false;
}

function normalizeProjectListAssigneeFilters(f) {
  if (!f) return;
  if (Array.isArray(f.assignedUserIds)) {
    f.assignedUserIds = sanitizeProjectListAssigneeFilterIds(f.assignedUserIds);
    return;
  }
  const legacyId = String(f.assignedUserId || "all").trim();
  if (legacyId && legacyId !== "all") {
    f.assignedUserIds = sanitizeProjectListAssigneeFilterIds([legacyId]);
  } else {
    const q = normalizeProjectFilterLookupText(f.assignedUserNameQuery || "");
    if (q === "未設定" || q === "担当者なし" || q === "なし") {
      f.assignedUserIds = [];
    } else if (q) {
      const hit = assignableUsers().find((u) => normalizeProjectFilterLookupText(u.name) === q);
      f.assignedUserIds = hit ? sanitizeProjectListAssigneeFilterIds([hit.id]) : [];
    } else {
      f.assignedUserIds = [];
    }
  }
}

/** プロジェクト一覧・担当者フィルタのトリガー表示文言 */
function projectListAssigneeFilterTriggerLabel(selIds) {
  const ids = sanitizeProjectListAssigneeFilterIds(selIds);
  if (!ids.length) return { text: "担当者を選択", placeholder: true };
  const labels = [];
  for (const id of ids) {
    const u = getUserById(id);
    labels.push(String(u?.name || "").trim() || "（不明）");
  }
  let text = labels.join("、");
  if (labels.length > 2) {
    text = `${labels.slice(0, 2).join("、")} 他${labels.length - 2}件`;
  }
  return { text, placeholder: false };
}

function ownerName(project) {
  return projectOwnerDisplayName(project);
}

// ── 担当者ヘルパー ──
function salesOwnerUser(p) {
  return getUserById(p.salesOwnerId) || getUserById(p.assignedUserId);
}

function accountingOwnerUser(p) {
  return getUserById(p.accountingOwnerId) || users.find((u) => isAccountingDepartmentJobRole(u.role));
}

function currentAssigneeUser(p) {
  return p.currentOwnerRole === "経理" ? accountingOwnerUser(p) : salesOwnerUser(p);
}

function nextAssigneeUser(p) {
  if (p.currentStage === "done") return null;
  return p.currentOwnerRole === "経理" ? salesOwnerUser(p) : accountingOwnerUser(p);
}

function ownerWithRole(user) {
  if (!user) return "未設定";
  return `${user.name}（${user.role}）`;
}

function toDateNumber(s) {
  return Number((s || "").replace(/\//g, "")) || 0;
}

function staleRank(stale) {
  if (stale === "期限切れ") return 0;
  const m = /あと(\d+)日/.exec(stale || "");
  if (m) return Number(m[1]);
  return 99;
}

function stageRank(stage) {
  const order = ["contracts", "quotes", "orders", "invoices", "payments", "done"];
  const idx = order.indexOf(stage);
  return idx >= 0 ? idx : 99;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** 月次ロック済みの月（yyyymm 数値）。正本はサーバの fp_month_locks で、
 *  /api/projects・/api/month-locks 応答の lockedMonths から setLockedMonths で反映する。
 *  旧・自動ロック（月末後5営業日）は廃止＝経理・マスターが手動でかける。 */
const _lockedMonthsYyyymm = new Set();

/** lockedMonths（'YYYY/MM' の配列）でロック済み月集合を丸ごと入れ替える */
function setLockedMonths(list) {
  _lockedMonthsYyyymm.clear();
  (Array.isArray(list) ? list : []).forEach((ym) => {
    const yyyymm = parseProjectBillingMonthYyyymm(ym);
    if (yyyymm != null) _lockedMonthsYyyymm.add(yyyymm);
  });
}

/** 対象月が月次ロック済みか。サーバ api/_projectLock.js（fp_month_locks）と判定を揃えること。
 *  請求月の格納形式は非ゼロ埋め（"2026/4"）や "-" 区切りが混在するため、案件一覧の既定パーサ
 *  parseProjectBillingMonthYyyymm（\d{1,2} 許容）に揃えて解釈する。 */
function isMonthLocked(ym) {
  const yyyymm = parseProjectBillingMonthYyyymm(ym);
  return yyyymm != null && _lockedMonthsYyyymm.has(yyyymm);
}

/** 案件の一時解除（unlock_until）が現在有効か */
function projectUnlockActive(p) {
  if (!p || !p.unlockUntil) return false;
  const t = new Date(p.unlockUntil).getTime();
  return Number.isFinite(t) && t > Date.now();
}

/** 案件がロック中か（請求月が月次ロック済み かつ 解除が有効でない） */
function isProjectLocked(p) {
  if (!p) return false;
  return isMonthLocked(p.billingMonth) && !projectUnlockActive(p);
}

/** マスター（システム権限）・経理（旧管理部・職種）はロックを直接解除できる */
function canDirectUnlockProjects(user) {
  const u = user || currentUser;
  return isMasterRoleUser(u) || primaryJobRole(u) === "経理";
}

/** マスターはロック中案件も解除操作なしで編集できる（サーバ api/projects・api/costs・api/project-billing も同条件でバイパス）。
 *  ただし締め済みデータを黙って書き換えないよう、保存前に案件ごと1回だけ確認を出す。確認済み案件の ID を保持。 */
const _masterLockedEditOkIds = new Set();

/** マスターがロック中案件の編集を確認済みか（confirm は出さない。自動保存の可否判定用） */
function masterLockedProjectEditConfirmed(p) {
  return !!p && isMasterRoleUser(currentUser) && _masterLockedEditOkIds.has(String(p.id));
}

/** ロック中案件への保存前確認。非ロック・マスター以外は常に true（従来挙動のまま） */
function confirmMasterLockedProjectEdit(p) {
  if (!p || !isProjectLocked(p) || !isMasterRoleUser(currentUser)) return true;
  const key = String(p.id);
  if (_masterLockedEditOkIds.has(key)) return true;
  const ok = window.confirm(
    `🔒「${p.name || ""}」はロック中です（請求月 ${p.billingMonth || "—"} は月次ロック済み）。\nマスター権限でこのまま変更を保存しますか？（OKするとこの案件では以後この確認は出ません）`
  );
  if (ok) _masterLockedEditOkIds.add(key);
  return ok;
}

/** 解除期限の表示（M/D） */
function projectUnlockUntilDisplay(p) {
  if (!p || !p.unlockUntil) return "";
  const d = new Date(p.unlockUntil);
  if (!Number.isFinite(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
}


/** プロジェクト一覧の初期値を「当月＋自分担当」にする職種（営業・メディア） */
function projectListUsesSalesMediaDefaultFilters(user) {
  const role = primaryJobRole(user || currentUser);
  return role === "営業" || role === "メディア";
}

/** 案件一覧の請求月の既定＝当月（職種を問わず。一覧は当月の請求を見るのが主用途） */
function applyProjectListBillingMonthDefault(f) {
  const cm = currentMonth();
  f.billingMonthFrom = cm;
  f.billingMonthTo = cm;
}

/** プロジェクト一覧の初期フィルター（当月の請求月・ログインユーザーを担当者に設定）— 営業・メディア向け */
function applyProjectListFilterDefaults() {
  const f = uiState.projectListFilters;
  applyProjectListBillingMonthDefault(f);
  if (currentUser?.id) {
    f.assignedUserIds = [String(currentUser.id)];
  } else {
    f.assignedUserIds = [];
  }
  delete f.assignedUserId;
  delete f.assignedUserNameQuery;
  f.mineOnly = true;
}

/**
 * 案件一覧のページを変え、チェックした選択を外す。ページ移動・並べ替え・絞り込み・表示件数の変更で使う。
 * 選択を残すと画面に見えない行まで一括操作（受注状況・入金済・削除）の対象になり、誤操作を招くため
 */
function setProjectListPage(page) {
  uiState.projectTable.page = page;
  uiState.projectTable.selected = {};
}

/** プロジェクト一覧フィルターを未選択・空に戻す（リセット用。ログイン時の初期値は付けない） */
function resetProjectListFilters() {
  const f = uiState.projectListFilters;
  f.no = "";
  f.name = "";
  f.keyword = "";
  f.clientId = "all";
  f.clientNameQuery = "";
  f.orderStatuses = [];
  f.paymentSimple = "";
  f.filterTags = [];
  f.assignedUserIds = [];
  f.billingMonthFrom = "";
  f.billingMonthTo = "";
  f.paymentDateFrom = "";
  f.paymentDateTo = "";
  f.lockState = "all";
  f.workCategory = "all";
  f.mineOnly = false;
  delete f.orderStatus;
  delete f.tag;
  delete f.category;
  delete f.taxMode;
  delete f.assignedUserId;
  delete f.assignedUserNameQuery;
}

/** 案件一覧で効いている「既定で付く」絞り込み（請求月・担当・入金区分）の表示用ラベル。
 *  0件時に「なぜ出ないか」を示すために使う（例: ["請求月: 2026/10", "担当: 山田"]） */
function projectListDefaultFilterSummary(f) {
  const parts = [];
  const from = String(f?.billingMonthFrom || "").trim();
  const to = String(f?.billingMonthTo || "").trim();
  if (from || to) parts.push(`請求月: ${from === to ? from : `${from}〜${to}`}`);
  const ids = sanitizeProjectListAssigneeFilterIds(f?.assignedUserIds);
  if (ids.length) parts.push(`担当: ${projectListAssigneeFilterTriggerLabel(ids).text}`);
  if (f?.paymentSimple) parts.push(`入金: ${f.paymentSimple}`);
  if (projectListHidesLostByDefault(f)) parts.push("受注: 失注以外");
  return parts;
}

/** 受注状況を1つも選んでいないとき＝失注以外すべて。失注はプルダウンで選んだときだけ出す
 *  （2026-10 要望: 全ステータスにチェックが入った状態を既定にせず、「選ばないと失注は出ない」を既定にする） */
function projectListHidesLostByDefault(f) {
  const picked = Array.isArray(f?.orderStatuses) && f.orderStatuses.length > 0;
  const legacyPick = f?.orderStatus && f.orderStatus !== "all";
  return !picked && !legacyPick;
}

function ensureProjectListFilterDefaultsOnce() {
  if (uiState.projectListFiltersDefaultsApplied) return;
  if (projectListUsesSalesMediaDefaultFilters()) {
    applyProjectListFilterDefaults();
  } else {
    resetProjectListFilters();
    // 営業・メディア以外は担当者の初期絞り込みを付けないが、請求月だけは当月を既定にする
    applyProjectListBillingMonthDefault(uiState.projectListFilters);
  }
  uiState.projectListFilters.orderStatuses = [];
  uiState.projectListFiltersDefaultsApplied = true;
}

// "overdue": 今月以前かつ未完了 / "unset": 請求月未設定かつ未完了 / "upcoming": 来月以降 / "ok": 完了
function billingUrgency(p) {
  const s = p.invoiceStatus;
  // 回収不能は「回収を諦めた」終端状態。督促・要対応バッジの対象から外す（未入金の"ok"扱いとは別だが urgency の分岐上は同じ非表示扱いでよい）
  if (s === "入金確認済み" || s === "入金待ち" || s === "送付済み" || s === "請求OK" || s === "請求済" || s === "回収不能") return "ok";
  // 請求月はDBトリガーで非ゼロ埋め（"2026/9"）。"/" を消して数値化すると 20269 < 202610 となり
  // 未来月まで overdue になるため、ゼロ埋め・"-" 区切りを吸収するパーサで yyyymm に揃えて比べる
  const bm = parseProjectBillingMonthYyyymm(p.billingMonth);
  if (bm == null) return "unset";
  return bm <= parseProjectBillingMonthYyyymm(currentMonth()) ? "overdue" : "upcoming";
}

function billingMonthList() {
  // 請求月はゼロ埋めなし（"2026/9"）なので文字列順だと 2026/10 が 2026/9 より前に来る。年月の数値で並べる
  const key = (m) => parseProjectBillingMonthYyyymm(m) ?? Number.MAX_SAFE_INTEGER;
  return [...new Set(projects.map((p) => p.billingMonth).filter(Boolean))].sort((a, b) => key(a) - key(b) || String(a).localeCompare(String(b)));
}

// 表示用の4段階ステータス
// 「請求OK」「請求済」は案件CSV取込のパススルーで本番に実在する値（2026-07-11時点で計377件）。
// 請求済み扱いにしないと入金列で「未請求」に誤表示される。
// 「回収不能」は原価計上専用のダミー案件（売上0）などで入金見込みがない場合の終端状態。
function simplifiedInvoiceStatus(p) {
  const s = p.invoiceStatus;
  if (s === "入金確認済み") return "入金済み";
  if (s === "回収不能") return "回収不能";
  if (s === "送付済み" || s === "入金待ち" || s === "請求OK" || s === "請求済") return "請求済み";
  return "未請求";
}

/** プロジェクト一覧「入金」列のソート順（未請求 < 請求済み < 入金済み。回収不能は要対応が無いため末尾） */
function paymentSimpleSortRank(simplified) {
  if (simplified === "入金済み") return 2;
  if (simplified === "請求済み") return 1;
  if (simplified === "回収不能") return 3;
  return 0;
}

/** 案件一覧の行色分け（受注状況）。純関数（テスト用に引数は文字列のみ）:
 *  quote=見積もり(白/既定) / confirmed=受注確定(黄) / ordered=受注済(緑) / lost=失注・除外(グレー)。
 *  請求・入金の状態は「入金」列のバッジで示し、行色には使わない（原価登録側とは色の軸が違う） */
function projectRowOrderStateKey(orderStatusUi) {
  const os = String(orderStatusUi || "").trim();
  if (os === "受注済") return "ordered";
  if (os === "受注確定") return "confirmed";
  if (os === "失注" || os === "除外") return "lost";
  return "quote";
}

function invoiceStatusTone(simplified) {
  if (simplified === "未請求") return "attention";
  if (simplified === "入金済み") return "safe";
  if (simplified === "回収不能") return "muted";
  return "";
}

/**
 * invoiceStatus（案件の請求ステータス）と捺印承認状態（fp_doc_seal_applications 由来の getDocSealStatus）は
 * データモデル上ずっと別々の2つの真実源のまま――ここではそれを変えない。変えるのは表示だけ。
 * 一覧などで「作成済みだが未承認」「送付準備OKだが捺印未申請」のような複合状態が一目でわかるよう、
 * 両方の値から人間にわかる1つのラベル＋トーンにまとめて返す表示専用ヘルパー（tone()/invoiceStatusTone() と同じ形）。
 * 送付済み以降（送付済み・入金待ち・入金確認済み等）は捺印の意味が薄れるため、捺印状態に関わらず
 * 従来どおりの3段階表示（simplifiedInvoiceStatus）のまま返す。
 */
function combinedInvoiceStatusLabel(p) {
  const simplified = simplifiedInvoiceStatus(p);
  // 送付済み以降は捺印状態を見ない（そのまま3段階表示）
  if (simplified !== "未請求") return { label: simplified, tone: invoiceStatusTone(simplified) };

  const seal = getDocSealStatus(p.id, "invoice");
  if (seal === "承認済") return { label: "発行待ち（承認済み）", tone: "safe" };
  if (seal === "申請中") return { label: "捺印申請中", tone: "" };
  // 捺印未申請: 下書き段階なら作成中、送付準備OKまで進んでいれば捺印申請待ち、
  // まだ何も作成していなければ従来どおり「未請求」
  if (p.invoiceStatus === "下書きあり") return { label: "作成中", tone: "attention" };
  if (p.invoiceStatus === "送付準備OK") return { label: "捺印申請待ち", tone: "attention" };
  return { label: simplified, tone: invoiceStatusTone(simplified) };
}

function billingBadgeHtml(p, pageType) {
  const urgency = billingUrgency(p);
  if (urgency === "ok") return "";

  const simplified = simplifiedInvoiceStatus(p);

  if (pageType === "invoices" || pageType === "payments") {
    const monthLabel = p.billingMonth
      ? `<span class="flow-badge">請求月: ${escapeHtml(p.billingMonth)}</span>`
      : `<span class="flow-badge attention">請求月: 未設定</span>`;
    return monthLabel;
  }

  // プロジェクト一覧・ダッシュボード: 要対応のときだけバッジ表示
  if (urgency === "overdue" && simplified === "未請求") {
    return `<span class="flow-badge attention">請求: 未請求（今月要対応）</span>`;
  }
  if (urgency === "unset" && simplified === "未請求") {
    return `<span class="flow-badge attention">請求: 未請求（請求月未設定）</span>`;
  }
  return "";
}

/** 見積もり系（見積もり・見積もり(低/中/高)）か。ダッシュボード集計・クイックフィルタ共通 */
function isQuoteOrderStatus(s) {
  const v = String(s || "").trim();
  return v === "見積もり" || /^見積もり\((低|中|高)\)$/.test(v);
}

/** 受注が取れている状態か（受注済・受注確定）。売上見込み・請求明細の集計など「受注扱い」の判定はここを通す */
function isWonOrderStatus(s) {
  const v = String(s || "").trim();
  return v === "受注済" || v === "受注確定";
}

/** 一覧・詳細で共通の受注ステータス（見積もり(確度3種含む)／受注確定／受注済／除外／失注。旧値はここで正規化） */
function projectOrderStatusForUi(p) {
  if (!p) return "見積もり";
  // 現行の正準値（確度つき見積もり含む）はそのまま返す
  if (PROJECT_ORDER_STATUS_LIST.includes(p.orderStatus)) return p.orderStatus;
  if (p.orderStatus === "失注") return "失注";
  if (p.orderStatus === "除外") return "除外";
  if (p.orderStatus === "完了") return "受注済";
  if (p.orderStatus === "見積もり" || p.orderStatus === "見積もり中" || p.orderStatus === "未受注") return "見積もり";
  if (p.orderStatus === "進行中") return "見積もり";
  if (p.orderStatus && String(p.orderStatus).startsWith("見積中")) return "見積もり";
  if (p.orderStatus) return "見積もり";
  if (p.docs?.order === "送付済み") return "受注済";
  if (detectStage(p) === "done" || p.invoiceStatus === "入金確認済み") return "受注済";
  return "見積もり";
}

const PROJECT_ORDER_STATUS_LIST = ["見積もり", "見積もり(低)", "見積もり(中)", "見積もり(高)", "受注確定", "受注済", "除外", "失注"];
/** 案件一覧の一括「受注状況を変更」で選ばれている値。プルダウンの表示と適用値を同じ関数から取る（既定は受注済） */
function projectBulkOrderStatus() {
  const v = uiState.projectListFilters.bulkStatus;
  return PROJECT_ORDER_STATUS_LIST.includes(v) ? v : "受注済";
}
/** 案件の進捗状況（一覧・詳細・CSV共通） */
const PROJECT_PROGRESS_STATUS_LIST = ["未着手", "着手中", "納品済", "研修済"];

/** 選択中の案件IDのうち、現在のフィルターで表示されているものだけ（絞り込み外の選択を一括操作に含めない） */
function getSelectedProjectIdsInFilter() {
  const selected = uiState.projectTable.selected || {};
  return getFilteredSortedProjects().map((p) => p.id).filter((id) => selected[id] === true);
}

function getFilteredSortedProjects() {
  // 固定項目編集で隠した検索項目は絞り込みに使わない（見えない条件で案件が隠れないように。15z-project-filter-fields.js）
  const f = projectListEffectiveFilters(uiState.projectListFilters);
  const t = uiState.projectTable;

  normalizeProjectListAssigneeFilters(f);
  const assigneePick = (Array.isArray(f.assignedUserIds) ? f.assignedUserIds : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  // フィルターを先に適用（コスト計算などの重い処理はフィルター後のみ）
  const preFiltered = projects.filter((p) => {
    // 原価箱（営業ごとの0円の一時置き場）は既定で一覧に出さない
    if (projectHiddenAsCostBox(p, f)) return false;
    const no = Number(String(p.no ?? p.id).replace(/[^\d]/g, "")) || 0;
    if ((f.no || "").trim() && !String(no).includes(String(f.no).trim())) return false;
    if ((f.name || "").trim() && !p.name.toLowerCase().includes(String(f.name).trim().toLowerCase())) return false;
    const clientQuery = normalizeProjectFilterLookupText(f.clientNameQuery || "");
    const clientQueryCompact = normalizeProjectFilterLookupCompact(f.clientNameQuery || "");
    if (f.clientId && f.clientId !== "all") {
      if (p.clientId !== f.clientId) return false;
    } else if (clientQuery) {
      const cl = clientName(p);
      const clientNameNorm = normalizeProjectFilterLookupText(cl);
      const clientNameCompact = normalizeProjectFilterLookupCompact(cl);
      if (!clientNameNorm.includes(clientQuery) && !clientNameCompact.includes(clientQueryCompact)) return false;
    }
    if (assigneePick.length > 0 && !projectMatchesAssigneeFilter(p, assigneePick)) return false;
    const orderStatus = projectOrderStatusForUi(p);
    let orderPick = Array.isArray(f.orderStatuses) ? [...f.orderStatuses] : [];
    if (!orderPick.length && f.orderStatus && f.orderStatus !== "all") {
      const want =
        f.orderStatus === "進行中" || f.orderStatus === "未受注" || f.orderStatus === "見積もり中"
          ? "見積もり"
          : f.orderStatus === "完了"
            ? "受注済"
            : f.orderStatus;
      orderPick = [want];
    }
    if (orderPick.length > 0 ? !orderPick.includes(orderStatus) : orderStatus === "失注") return false;
    if (f.paymentSimple && simplifiedInvoiceStatus(p) !== f.paymentSimple) return false;
    const bmFromStr = String(f.billingMonthFrom || "").trim();
    const bmToStr = String(f.billingMonthTo || "").trim();
    if (bmFromStr || bmToStr) {
      let fromNum = bmFromStr ? parseProjectBillingMonthYyyymm(bmFromStr) : null;
      let toNum = bmToStr ? parseProjectBillingMonthYyyymm(bmToStr) : null;
      if (fromNum != null && toNum != null && fromNum > toNum) {
        const swap = fromNum; fromNum = toNum; toNum = swap;
      }
      const bmNum = parseProjectBillingMonthYyyymm(p.billingMonth);
      // 請求月が未設定のプロジェクトは期間フィルターの対象外として常に表示する
      if (bmNum != null) {
        if (fromNum != null && bmNum < fromNum) return false;
        if (toNum != null && bmNum > toNum) return false;
      }
    }
    // 入金日: 指定時は範囲内に入金日がある案件だけ（未入金は除外。経理の入金消込の確認用）
    const pdFrom = String(f.paymentDateFrom || "").trim();
    const pdTo = String(f.paymentDateTo || "").trim();
    if (pdFrom || pdTo) {
      const [lo, hi] = pdFrom && pdTo && pdFrom > pdTo ? [pdTo, pdFrom] : [pdFrom, pdTo];
      const payYmd = String(p.paymentRecord?.payDate || p.paymentConfirmedAt || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(payYmd)) return false;
      if (lo && payYmd < lo) return false;
      if (hi && payYmd > hi) return false;
    }
    const tagPick = Array.isArray(f.filterTags) ? f.filterTags.filter((x) => String(x || "").trim()) : [];
    if (tagPick.length > 0) {
      const pTags = parseProjectDetailTags(p.tag || "");
      if (!tagPick.some((t) => pTags.includes(t))) return false;
    }
    if (f.lockState === "locked" && !isProjectLocked(p)) return false;
    if (f.lockState === "unlocked" && isProjectLocked(p)) return false;
    if (f.workCategory && f.workCategory !== "all") {
      const cats = workCategoriesFromRaw(p.workCategories).categories;
      if (f.workCategory === "unset" ? cats.length > 0 : !cats.includes(f.workCategory)) return false;
    }
    return true;
  });

  // フィルター後のみコスト合計・粗利率など重い計算を実行
  const getOrder = (p) => projectDisplayedBillingExTaxYen(p);
  // 原価は税抜基準（予算進捗の差益と同基準）。売上（税抜）と揃え、粗利率・差益の混合基準を解消
  const getCost = (p) => totalProjectRegisteredCostExTaxYen(p);
  const getMargin = (p) => {
    const q = getOrder(p);
    if (!q) return Number(p.margin || 0);
    return Math.round(((q - getCost(p)) / q) * 100);
  };
  const normalized = preFiltered.map((p) => ({
    ...p,
    _no: Number(String(p.no ?? p.id).replace(/[^\d]/g, "")) || 0,
    _client: clientName(p),
    _clientPerson: String(p.person || getClientById(p.clientId)?.contactName || "").trim(),
    _ownerName: projectListOwnerDisplayName(p),
    _orderStatus: projectOrderStatusForUi(p),
    _progressStatus: normalizeProjectProgressStatus(p.progressStatus),
    _paymentSimple: simplifiedInvoiceStatus(p),
    _category: p.category || (p.projectType === "new-business" ? "Web制作" : "システム開発"),
    _tag: p.tag || "",
    _order: getOrder(p),
    _cost: getCost(p),
    _margin: getMargin(p),
    _workCategoriesLabel: workCategoriesFromRaw(p.workCategories).categories.join("、"),
    _sealAgg: (() => {
      // 押印の進捗順ソート用: 承認済数を主・申請中数を従（未申請だけ→申請中あり→全承認 の順に並ぶ）。
      // 列に丸で出している書類（見積書・請求書・領収書）で数える（見た目と並び順を合わせる）
      const agg = projectSealAggregate(p.id, PROJECT_LIST_SEAL_DOT_TABS);
      return agg.approved * 10 + agg.pending;
    })(),
    _billingDateDisplay: isoYmdToSlashDisplay(p.plannedInvoiceDate) || p.billingDate || ""
  }));

  const sorted = [...normalized].sort((a, b) => {
    const dir = t.sortDir === "asc" ? 1 : -1;
    const sortKeyEff = t.sortKey === "quote" ? "no" : t.sortKey;
    const kv = {
      no: [a._no, b._no],
      name: [a.name, b.name],
      client: [a._client, b._client],
      order: [a._order, b._order],
      cost: [a._cost, b._cost],
      margin: [a._margin, b._margin],
      status: [a._orderStatus, b._orderStatus],
      progress: [a._progressStatus, b._progressStatus],
      payment: [paymentSimpleSortRank(a._paymentSimple), paymentSimpleSortRank(b._paymentSimple)],
      billingDate: [a._billingDateDisplay || "", b._billingDateDisplay || ""],
      tag: [(a._tag || "").trim(), (b._tag || "").trim()],
      owner: [a._ownerName, b._ownerName],
      seal: [a._sealAgg, b._sealAgg]
    }[sortKeyEff] || [a._no, b._no];
    if (typeof kv[0] === "number") return (kv[0] - kv[1]) * dir;
    return String(kv[0]).localeCompare(String(kv[1]), "ja") * dir;
  });
  return sorted;
}
function downloadProjectListFilteredCsv() {
  const rows = getFilteredSortedProjects();
  // 捺印申請は書類ごとに1列（未申請／申請中／承認済）。1セルにまとめると Excel で絞り込めないため
  const sealTabs = Object.entries(DOC_SEAL_TAB_LABELS);
  const headers = [
    "案件No",
    "案件名",
    "顧客",
    "先方担当者",
    "担当",
    "カテゴリ",
    "関わった担当者",
    "請求額（税抜）",
    "原価（税抜）",
    "粗利率",
    "差益",
    "受注",
    "請求日",
    "タグ",
    ...sealTabs.map(([, label]) => `捺印申請（${label}）`)
  ];
  const body = rows.map((p) => {
    const tags = parseProjectDetailTags(p._tag || "").join("、");
    // 案件詳細でカテゴリの右に選ぶ担当者（貢献度の集計と同じ projectContributorIds）
    const involved = projectContributorIds(p).map((id) => getUserById(id)?.name || "").filter(Boolean).join("、");
    return [
      p._no,
      p.name,
      p._client,
      p._clientPerson,
      p._ownerName,
      p._workCategoriesLabel,
      involved,
      p._order,
      p._cost,
      `${p._margin}%`,
      p._order - p._cost,
      p._orderStatus,
      p._billingDateDisplay || "",
      tags,
      ...sealTabs.map(([tab]) => getDocSealStatus(p.id, tab))
    ]
      .map((v) => toCsvCell(v))
      .join(",");
  });
  const csv = "\uFEFF" + [headers.map((h) => toCsvCell(h)).join(","), ...body].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `案件一覧_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  showToast(rows.length ? `${rows.length}件をCSVでダウンロードしました` : "該当0件のCSVをダウンロードしました（見出しのみ）");
}

/** @param {{ name?: string, clientId?: string, amount?: number, billingMonth?: string, note?: string, pendingCostAssign?: { costIds: string[], composeInvoice?: boolean } }} [prefill] */
function initProjectCreateState(prefill = null) {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  uiState.projectDetailForm = null;
  uiState.projectCreateWarning = null;
  uiState.projectCreate = {
    mode: "existing-client",
    // 原価割当「まとめて請求書を作成」など、作成後に続けて行いたい処理がある時だけ設定
    pendingCostAssign: prefill?.pendingCostAssign || null,
    form: {
      name: prefill?.name || "",
      clientId: prefill?.clientId || "",
      clientQuery: "",
      clientHighlight: -1,
      assignedUserId: currentUser.id || "u1",
      amount: prefill?.amount != null ? String(prefill.amount) : "",
      startDate: todayStr,
      billingMonth: prefill?.billingMonth || "",
      contactName: "",
      billingCompany: "",
      billingAddress: "",
      note: prefill?.note || ""
    }
  };
}

// 作成モーダルの案件名。再描画すると入力中のフォーカスが外れるので state だけ更新する
document.addEventListener("input", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.dataset.projectCreateName || !uiState.projectCreate) return;
  uiState.projectCreate.form.name = el.value;
});

function openProjectCreate() {
  initProjectCreateState();
  render();
}

function openClientModal({ mode = "create", clientId = "", fromProject = false, centered = false, inline = false, silent = false } = {}) {
  const base = clientId ? clients.find((c) => c.id === clientId) : null;
  const now = new Date();
  const defaultDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const defaultTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const firstRegisteredParts = splitFirstRegisteredForForm(base?.firstRegisteredAt || "");
  uiState.clientCreate = {
    mode,
    fromProject,
    centered,
    // inline=true は取引先詳細ページの基本情報タブに埋め込み表示（モーダルは出さない）
    inline,
    id: base?.id || "",
    name: base?.name || "",
    shortName: base?.shortName || "",
    honorific: base?.honorific || "御中",
    postalCode: base?.postalCode || "",
    prefecture: base?.prefecture || "東京都",
    address: base?.address || "",
    building: base?.building || "",
    tel: base?.tel || "",
    fax: base?.fax || "",
    contractOwner: base ? String(base.contractOwner ?? "").trim() : "",
    paymentTerms: base?.paymentTerms || "月末締翌月末払",
    transferFeeBurden: base?.transferFeeBurden || "先方",
    ndaStatus: base?.ndaStatus || "未",
    contractStatus: base?.contractStatus || "未",
    deliveryMethod: normalizeDeliveryMethodStored(base?.deliveryMethod || "メール(DL)"),
    contactName: base?.contactName || "",
    email: base?.email || "",
    notes: base?.notes || "",
    ndaPurpose: base?.ndaPurpose || "",
    tags: base?.tags || "",
    firstRegisteredAt: base?.firstRegisteredAt || "",
    firstRegisteredDate: firstRegisteredParts.date || defaultDate,
    firstRegisteredTime: firstRegisteredParts.time || defaultTime,
    corporateNumber: base?.corporateNumber || "",
    customerNo: base?.customerNo || "",
    invoiceIssuer: base?.invoiceIssuer || "未設定",
    invoiceIssuerNumber: base?.invoiceIssuerNumber || "",
    expectedAmount: base?.expectedAmount != null ? String(base.expectedAmount) : "",
    capitalAmount: base?.capitalAmount != null ? String(base.capitalAmount) : "",
    establishedYm: base?.establishedYm || ""
  };
  resetCorporateLocalSearch();
  // silent=true は呼び出し側が部分更新する（取引先詳細の埋め込み編集。全画面renderのちらつき回避）
  if (!silent) render();
}

function closeProjectCreate() {
  uiState.projectCreate = null;
  uiState.projectCreateWarning = null;
  if (/^#projects\?new=1$/.test(location.hash)) {
    history.replaceState(null, "", `${location.pathname}${location.search}#projects`);
  }
  render();
}

function createProjectFromModal(forceProceed = false) {
  if (!uiState.projectCreate) return;
  // 二重送信防止
  if (uiState.projectCreate._submitting) return;
  const { mode, form } = uiState.projectCreate;
  // 案件名以外の詳細は作成後の案件詳細「基本情報」で入力する。案件名は必須
  // （作成ボタンで即サーバーに登録されるため、無名のまま作ると「(案件名未設定)」の案件が残っていた）
  const projectName = String(form.name || "").trim();
  if (!form.clientId) {
    showToast("クライアントを選択してください");
    return;
  }
  if (!projectName) {
    showToast("案件名を入力してください");
    document.querySelector("[data-project-create-name]")?.focus();
    return;
  }
  const isNewBusiness = mode === "new-business";
  const amount = Number(String(form.amount).replace(/,/g, "")) || 0;
  const selectedClient = getClientById(form.clientId);
  const selectedUser = getUserById(form.assignedUserId);
  const clientNdaDone = selectedClient?.ndaStatus === "済";
  const clientContractDone = selectedClient?.contractStatus === "済";
  // 基本契約（業務委託）が締結済みなら NDA 単独の未締結は警告しない
  // （NDA→基本契約へ進む運用のため、基本契約に秘密保持条項が含まれ NDA 相当はカバー済みという整理）
  const clientAgreementDone = clientContractDone;
  if (!isNewBusiness && !clientAgreementDone && !forceProceed) {
    uiState.projectCreateWarning = {
      title: "契約締結していないクライアントです",
      message: "必ず締結の上、見積もり作成・受注してください。",
      missingItems: [!clientNdaDone ? "NDA" : "", !clientContractDone ? "業務委託契約書" : ""].filter(Boolean)
    };
    render();
    return;
  }
  uiState.projectCreateWarning = null;
  uiState.projectCreate._submitting = true;
  render();
  // 業務カテゴリは入力欄を撤去済み（カテゴリと役目が重なるため）。新規作成は未設定で始める
  const serviceCategory = "";
  const project = {
    id: "", // サーバ採番後に p<dbId> を入れる
    clientId: form.clientId || clients[0]?.id || "",
    assignedUserId: form.assignedUserId || "u1",
    name: projectName,
    client: selectedClient?.name || "未設定クライアント",
    // 顧客担当者は案件詳細で担当者マスタから選ぶ（未選択なら空欄）。ここで既定値を入れると
    // マスタに無い値が選択肢に居座るので空のままにする
    person: "",
    clientBranchName: "",
    owner: selectedUser?.name || "田中",
    amount,
    stale: "あと3日",
    updatedAt: localDateToYmd(new Date()),
    nextAction: isNewBusiness ? "契約書を作成する" : "見積書を作成してください",
    currentStage: isNewBusiness ? "contracts" : "quotes",
    contractRequired: isNewBusiness,
    projectType: isNewBusiness ? "new-business" : "existing-client",
    useOrderFlow: false,
    orderStatus: "見積もり",
    progressStatus: "未着手",
    serviceCategory,
    contractDone: isNewBusiness ? false : clientAgreementDone,
    billingMonth: form.billingMonth || "",
    salesOwnerId: form.assignedUserId || currentUser.id,
    accountingOwnerId: defaultAccountingUserId(),
    salesSplit: [],
    nonSalesSplit: [],
    workCategories: { categories: [], members: [] },
    currentOwnerRole: "営業",
    handoffStatus: "未引き継ぎ",
    handoffAt: "",
    handedBy: "",
    handoffNote: form.note || "",
    docs: {
      nda: isNewBusiness ? "未作成" : (clientNdaDone ? "締結済み" : "未作成"),
      contract: isNewBusiness ? "未作成" : (clientContractDone ? "締結済み" : "未作成"),
      quote: "未作成",
      order: "未作成",
      payment: "未確認"
    }
  };
  // DB保存
  const selectedClientObj = selectedClient;
  const dbClientId = selectedClientObj?.dbId || null;
  // 担当営業は必須: 未選択時は画面表示（上の salesOwnerId）と同じく作成者にフォールバックして送る
  const dbSalesOwnerId = Number(String(form.assignedUserId || currentUser.id).replace("u", "")) || null;
  const dbAccountingOwnerId = Number(String(defaultAccountingUserId()).replace("u", "")) || null;

  fetch("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({
      clientId: dbClientId,
      name: projectName,
      amount,
      billingMonth: form.billingMonth || null,
      currentStage: isNewBusiness ? "contracts" : "quotes",
      nextAction: isNewBusiness ? "契約書を作成する" : "見積書を作成してください",
      projectType: isNewBusiness ? "new-business" : "existing-client",
      salesOwnerId: dbSalesOwnerId,
      accountingOwnerId: dbAccountingOwnerId,
      assignedUserId: dbSalesOwnerId,
      invoiceStatus: "未作成",
      orderStatus: "見積もり",
      progressStatus: "未着手",
      serviceCategory
    })
  }).then(r => r.json()).then(data => {
    if (data.ok && data.project) {
      const dbP = data.project;
      project.id = `p${dbP.id}`;
      project.dbId = dbP.id;
      projects.unshift(project);
      const pending = uiState.projectCreate?.pendingCostAssign || null;
      uiState.projectCreate = null;
      if (pending && Array.isArray(pending.costIds) && pending.costIds.length) {
        // 原価割当「まとめて請求書を作成」からの遷移: 作成した案件へその場で原価も割り当ててから、
        // 請求書作成画面まで続けて開く（前月案件が見つからず自動複製できなかった時の受け皿）。
        // 割当は複数件の逐次リクエストで数秒かかりうるため、モーダルはここで先に閉じる
        // （閉じずに待たせると「作成中…」のまま固まって見える）
        render();
        void (async () => {
          const { ok, fail } = await assignOrphanCostsToProject(pending.costIds, project.id);
          if (fail) showToastError(`案件を作成しましたが、${fail}件の割当に失敗しました（${ok}件は成功）`);
          else showToast(`案件を作成し、${ok}件を割り当てました`);
          location.hash = pending.composeInvoice
            ? `#project-detail:${project.id}?composeInvoice=1`
            : `#project-detail:${project.id}?tab=overview`;
        })();
      } else {
        showToast("案件を作成しました");
        // 作成直後は請求項目でなく基本情報タブから始める（tab未指定の既定は billing）
        location.hash = `#project-detail:${project.id}?tab=overview`;
      }
    } else {
      if (uiState.projectCreate) uiState.projectCreate._submitting = false;
      showToastError(data.error || "作成に失敗しました");
      render();
    }
  }).catch(() => {
    // 応答が読めない（通信断・タイムアウト・非JSON）。サーバ側では作成済みの可能性があるため、
    // ローカルに仮案件は作らず（id 衝突する未保存の幽霊案件になる）、サーバから再読込して実在するものだけ出す。
    // モーダルと入力は残し、作成されていなければそのまま再送できるようにする
    if (uiState.projectCreate) uiState.projectCreate._submitting = false;
    showToastError("案件の作成結果を確認できませんでした。案件一覧に作成されていないか確認してから再度お試しください");
    render();
    loadDataFromDB().then(() => render()).catch(() => {});
  });
}

function stageLabel(stage) {
  if (stage === "quotes") return "見積ページへ";
  if (stage === "invoices") return "請求ページへ";
  if (stage === "payments") return "入金ページへ";
  if (stage === "contracts") return "契約ページへ";
  if (stage === "orders") return "請書 / 発注ページへ";
  return "案件ページへ";
}

// ────────────────────────────────────────────────────────────────
// todoAction: 案件の現在状態から「今すぐやるべき1つのアクション」を返す
//
// ラベルと挙動の対応ルール（厳守）:
//   "作成する"    → 新規作成画面を開く（compose）
//   "編集する"    → 既存データを開く（compose with existing id）
//   "確認する"    → 内容を表示するだけ（state 変更なし・detail）
//   "〜済みにする" → ステータス更新（quick）
//   "引き継ぐ"    → 担当変更（quick）
//
// NG: "確認する" ラベルで data-set-doc を使わない
// ────────────────────────────────────────────────────────────────
function todoAction(p) {
  const stage = p.currentStage || detectStage(p);

  // 差し戻し中 → 内容確認のみ（状態変更しない）
  if (p.handoffStatus === "差し戻し中") {
    return { label: "差し戻し内容を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "要確認" };
  }

  // ── 入金ステージ ──
  // 「確認する」は状態変更しない。実際の記録は案件詳細から行う
  if (stage === "payments") {
    if (p.docs.payment === "入金確認済み") {
      return { label: "入金詳細を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "" };
    }
    if (p.handoffStatus === "経理確認待ち") {
      return { label: "入金内容を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "要確認" };
    }
    return { label: "入金ページで確認する", attr: `data-go="payments"`, type: "detail", typeLabel: "" };
  }

  // ── 請求ステージ ──
  if (stage === "invoices") {
    if (p.invoiceStatus === "送付済み") {
      // 送付済み・未引き継ぎ → 引き継ぎ処理（状態変更あり・ラベルが意図を明示）
      return { label: "経理へ引き継ぐ", attr: `data-handoff="to-accounting:${p.id}"`, type: "quick", typeLabel: "1クリックで完了" };
    }
    if (p.invoiceStatus === "送付準備OK") {
      return { label: "送付済みにする", attr: `data-set-doc="invoice:sent:${p.id}"`, type: "quick", typeLabel: "1クリックで完了" };
    }
    // 下書きあり → コンポーザーで編集（既存データを開く）
    // 未作成 → コンポーザーで新規作成
    return {
      label: p.invoiceStatus === "下書きあり" ? "請求書を編集する" : "請求書を作成する",
      attr: `data-compose="invoice" data-project="${p.id}"`,
      type: "compose",
      typeLabel: "書類作成"
    };
  }

  // ── 見積ステージ ──
  if (stage === "quotes") {
    if (p.docs.quote === "送付準備OK") {
      return { label: "送付済みにする", attr: `data-set-doc="quote:sent:${p.id}"`, type: "quick", typeLabel: "1クリックで完了" };
    }
    // 送付済み・確認待ち → クライアント待ちのため確認のみ（状態変更なし）
    if (p.docs.quote === "送付済み" || p.docs.quote === "確認待ち") {
      return { label: "見積状況を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "" };
    }
    return {
      label: p.docs.quote === "下書きあり" ? "見積書を編集する" : "見積書を作成する",
      attr: `data-compose="quote" data-project="${p.id}"`,
      type: "compose",
      typeLabel: "書類作成"
    };
  }

  // ── 請書 / 発注ステージ ──
  if (stage === "orders") {
    return { label: "請書 / 発注を送付済みにする", attr: `data-set-doc="order:sent:${p.id}"`, type: "quick", typeLabel: "1クリックで完了" };
  }

  // ── 契約ステージ ──
  // 注意: 契約書にはコンポーザーがないため、"作成する"/"編集する" ラベルは使わない
  // ステータス変更ボタンはラベルが状態遷移を明示している場合のみ使用
  if (stage === "contracts") {
    if (p.docs.contract === "送付準備OK") {
      return { label: "契約書を送付済みにする", attr: `data-set-doc="contract:sent:${p.id}"`, type: "quick", typeLabel: "1クリックで完了" };
    }
    // 下書きあり・未作成 → 詳細ページで確認してから次のアクションを取る
    return { label: "案件詳細を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "要確認" };
  }

  // その他
  return { label: "案件を確認する", attr: `data-open-detail="${p.id}"`, type: "detail", typeLabel: "" };
}

// ステージ名の日本語ラベル（カードのコンテキスト表示用）
const STAGE_NAME = { contracts: "契約", quotes: "見積", orders: "請書/発注", invoices: "請求", payments: "入金", done: "完了" };

function stageStatus(pageType, p) {
  if (pageType === "quotes") return p.docs.quote;
  if (pageType === "invoices") return p.invoiceStatus;
  if (pageType === "contracts") return p.docs.contract;
  if (pageType === "orders") return p.docs.order;
  if (pageType === "payments") return p.docs.payment;
  return p.nextAction;
}

function hasExistingDoc(p, docType) {
  if (!p) return false;
  if (p.savedDocs?.[docType]) return true;
  if (docType === "quote") return ["下書きあり", "確認待ち", "送付準備OK", "送付済み"].includes(p.docs?.quote);
  if (docType === "invoice") return ["下書きあり", "送付準備OK", "送付済み", "入金待ち", "入金確認済み"].includes(p.invoiceStatus);
  return false;
}

function docActionLabel(p, docType) {
  const exists = hasExistingDoc(p, docType);
  if (docType === "quote") {
    if (!exists) return "見積書を作成する";
    if (p.docs?.quote === "送付済み") return "見積書内容を確認する";
    return "見積書を編集する";
  }
  if (docType === "invoice") {
    if (!exists) return "請求書を作成する";
    if (["送付済み", "入金待ち", "入金確認済み"].includes(p.invoiceStatus)) return "請求書内容を確認する";
    return "請求書を編集する";
  }
  return "書類を確認する";
}

function handoffTone(status) {
  if (status === "差し戻し中") return "attention";
  if (status === "経理確認待ち" || status === "完了") return "safe";
  return "";
}

function nextActionText(pageType, p) {
  const s = stageStatus(pageType, p);
  if (pageType === "contracts") {
    if (s === "未作成") return "契約書を作成してください";
    if (s === "下書きあり") return "契約書を編集して確定してください";
    if (s === "送付準備OK") return "あと一歩です。クライアントへ送付してください";
    if (s === "送付済み") return "対応完了（必要に応じて確認）";
    return "契約対応を進めてください";
  }
  if (pageType === "quotes") {
    if (s === "未作成") return "見積書を作成してください";
    if (s === "下書きあり") return "見積書を編集して確定してください";
    if (s === "送付準備OK") return "あと一歩です。クライアントへ送付してください";
    if (s === "送付済み") return "対応完了（必要に応じて確認）";
    return "見積ステージの対応を進めてください";
  }
  if (pageType === "invoices") {
    if (s === "未作成") return "請求書を作成してください";
    if (s === "下書きあり") return "請求書を編集してください";
    if (s === "送付準備OK") return "あと一歩です。クライアントへ送付してください";
    if (s === "送付済み") return "経理へ引き継いでください";
    if (s === "入金待ち") return "入金確認を記録してください";
    if (s === "入金確認済み") return "対応完了（必要に応じて確認）";
    return "請求ステージの対応を進めてください";
  }
  if (pageType === "payments") {
    if (p.handoffStatus === "経理確認待ち") return "入金確認を記録してください";
    if (p.handoffStatus === "差し戻し中") return "差し戻し内容を確認してください";
    if (p.docs.payment === "入金確認済み") return "対応完了（必要に応じて確認）";
    return "入金状況を確認してください";
  }
  return p.nextAction;
}

function projectDetailLineType(line) {
  return line?.lineType || "normal";
}

/**
 * サーバーの請求項目の状態を覚える。hash は次の保存で baseBillingLinesHash として送り、
 * サーバーが「最後に見た内容から他の人が変えていないか」を照合する（api/_billingLinesConflict.js）。
 * options.hash: サーバーが返した保存後のハッシュ（無ければ lines から計算）。
 * options.keepBase: 編集中（dirty）に一覧を取り直した場合。ベースを最新へ進めると、他の人の変更を
 * 知らないまま手元の編集で上書きできてしまうため、行数だけ更新してハッシュ（と競合状態）は据え置く。
 */
function rememberProjectBillingLinesServerSnapshot(pid, lines, options = {}) {
  const k = String(pid || "");
  if (!k) return;
  const n = Array.isArray(lines) ? lines.length : 0;
  if (!uiState.projectBillingLinesServerSnapshot) uiState.projectBillingLinesServerSnapshot = {};
  const prev = uiState.projectBillingLinesServerSnapshot[k];
  if (options.keepBase) {
    uiState.projectBillingLinesServerSnapshot[k] = { ...prev, lineCount: n, at: Date.now() };
    return;
  }
  const hash = options.hash || billingLinesHash(lines);
  uiState.projectBillingLinesServerSnapshot[k] = { lineCount: n, at: Date.now(), hash };
  if (!uiState.projectBillingLinesServerCopy) uiState.projectBillingLinesServerCopy = {};
  uiState.projectBillingLinesServerCopy[k] = JSON.parse(JSON.stringify(Array.isArray(lines) ? lines : []));
  storeBillingLinesServerHash(k, hash);
}

/** 他の人の変更と競合（409）した案件に印を付ける。自動保存・自動同期はこの印がある間は送らない（再読み込みで解除） */
function markProjectBillingLinesConflict(pid) {
  const k = String(pid || "");
  if (!k) return;
  if (!uiState.projectBillingLinesServerSnapshot) uiState.projectBillingLinesServerSnapshot = {};
  uiState.projectBillingLinesServerSnapshot[k] = { ...uiState.projectBillingLinesServerSnapshot[k], conflict: true };
}

// 最後に見たサーバーの請求項目ハッシュは localStorage にも残す。前回のセッションの下書き（localStorage）を
// 自動同期するとき、その下書きがどのサーバー状態から作られたかを送れるようにするため
// （送れないと、他の場所で削除された明細を古い下書きが復活させる）。
const BILLING_LINES_SERVER_HASH_LS_KEY = "fp_billing_lines_server_hash_v1";
let billingLinesServerHashCache = null;
let billingLinesServerHashSaveTimer = null;

function billingLinesServerHashStore() {
  if (!billingLinesServerHashCache) {
    try {
      const raw = JSON.parse(localStorage.getItem(BILLING_LINES_SERVER_HASH_LS_KEY) || "{}");
      billingLinesServerHashCache = raw && typeof raw === "object" ? raw : {};
    } catch {
      billingLinesServerHashCache = {};
    }
  }
  return billingLinesServerHashCache;
}

function storeBillingLinesServerHash(pid, hash) {
  const all = billingLinesServerHashStore();
  if (all[pid] === hash) return;
  all[pid] = hash;
  // 一覧の読込で全案件分まとめて呼ばれるため、書き込みはまとめて1回にする
  if (billingLinesServerHashSaveTimer) return;
  billingLinesServerHashSaveTimer = setTimeout(() => {
    billingLinesServerHashSaveTimer = null;
    try {
      localStorage.setItem(BILLING_LINES_SERVER_HASH_LS_KEY, JSON.stringify(all));
    } catch {
      /* ignore quota */
    }
  }, 300);
}

/** 保存時に送るベースハッシュ（このセッションで見たもの → 前回セッションで見たもの）。不明なら undefined */
function billingLinesBaseHashFor(pid) {
  const k = String(pid || "");
  return uiState.projectBillingLinesServerSnapshot?.[k]?.hash || billingLinesServerHashStore()[k] || undefined;
}

/** 案件ごとの請求項目の書き込みを直列化する。並行して送ると後発が先発の保存前のハッシュを送り、
 *  自分自身の保存と競合（409）してしまうため。fn は前の書き込みの完了後に呼ぶ（ベースハッシュは fn の中で読む） */
const projectBillingWriteChains = {};
function serializeProjectBillingWrite(pid, fn) {
  const k = String(pid || "");
  const run = (projectBillingWriteChains[k] || Promise.resolve()).then(fn);
  const tail = run.catch(() => {});
  projectBillingWriteChains[k] = tail;
  void tail.then(() => {
    if (projectBillingWriteChains[k] === tail) delete projectBillingWriteChains[k];
  });
  return run;
}

/** 自動保存が失敗した案件ID（トースト表示済み・再編集まで連呼しない） */
const projectBillingLinesSaveFailWarned = new Set();

function markProjectDocsDraftDirty(pid) {
  const k = String(pid || "");
  if (!k) return;
  uiState.projectDocsDraftDirty[k] = true;
  projectBillingLinesSaveFailWarned.delete(k);
  // 案件詳細・書類画面では自動で送らず「保存」ボタンで確定する（試しに変えた金額がそのまま確定しないように）。
  // CSV取込などは従来どおり自動保存
  const page = currentRoute().page;
  if (page === "project-detail" || page === "project-docs") {
    uiState.projectBillingManualDirty[k] = true;
    return;
  }
  delete uiState.projectBillingManualDirty[k];
  schedulePersistProjectBillingLinesToServer(k);
}

/** 保存ボタンで確定する（自動では送らない）未保存の請求項目があるか */
function projectBillingLinesManualDirty(pid) {
  return !!uiState.projectBillingManualDirty?.[String(pid || "")];
}

let projectBillingLinesAutosaveTimer = null;
const projectBillingLinesAutosavePending = new Set();

function schedulePersistProjectBillingLinesToServer(projectId) {
  const pid = String(projectId || "");
  if (!pid || !authToken || uiState.projectBillingManualDirty?.[pid]) return;
  projectBillingLinesAutosavePending.add(pid);
  if (projectBillingLinesAutosaveTimer) clearTimeout(projectBillingLinesAutosaveTimer);
  projectBillingLinesAutosaveTimer = setTimeout(() => {
    projectBillingLinesAutosaveTimer = null;
    const ids = [...projectBillingLinesAutosavePending];
    projectBillingLinesAutosavePending.clear();
    void flushDirtyProjectBillingLines(ids);
  }, 1600);
}

/** 編集中の請求項目をサーバーへ（ページ離脱前・自動保存） */
async function flushDirtyProjectBillingLines(projectIds = null) {
  if (!authToken) return;
  const ids =
    projectIds != null
      ? projectIds.map((x) => String(x || "")).filter(Boolean)
      : Object.keys(uiState.projectDocsDraftDirty || {}).filter((k) => uiState.projectDocsDraftDirty[k]);
  for (const pid of ids) {
    if (!uiState.projectDocsDraftDirty[pid]) continue;
    // 案件詳細の未保存分は保存ボタンで確定する（画面遷移のたびに送ると「保存せずに移動」が効かない）
    if (uiState.projectBillingManualDirty?.[pid]) continue;
    const p = projects.find((x) => String(x.id) === pid);
    if (!p?.dbId) continue;
    try {
      // 他の人の変更と競合（409）済みの案件は、同じベースで送り直しても必ず弾かれるので自動では送らない
      // （手元の編集は dirty のまま残す。下の catch で競合の文言を知らせる。再読み込みで解除）
      if (uiState.projectBillingLinesServerSnapshot?.[pid]?.conflict) {
        throw Object.assign(new Error(BILLING_LINES_CONFLICT_MESSAGE), { serverMessage: BILLING_LINES_CONFLICT_MESSAGE });
      }
      await persistProjectDetailToServer(p, { includeBillingLines: true });
    } catch (e) {
      // dirty のまま残し次回再試行。再試行しても失敗し続けるケース（ロック・権限）があるため、
      // 無言のままだと「保存できたように見えて実は反映されていない」事故になる（同一案件では1回のみ通知）。
      // 理由はサーバーの文言をそのまま出す。推測で「ロック中」と決めつけると原因調査を誤らせる。
      if (!projectBillingLinesSaveFailWarned.has(pid)) {
        projectBillingLinesSaveFailWarned.add(pid);
        const reason = String(e?.serverMessage || e?.message || "").trim();
        showToast(
          `「${p.name || "案件"}」の請求項目を保存できませんでした${reason ? `：${reason}` : ""}`
        );
      }
    }
  }
}

function clearProjectDocsDraftDirty(pid) {
  const k = String(pid || "");
  if (!k) return;
  delete uiState.projectDocsDraftDirty[k];
  if (uiState.projectBillingManualDirty) delete uiState.projectBillingManualDirty[k];
}

/** GET /api/projects の billing_lines_json（JSONB・文字列どちらでも） */
function normalizeServerBillingLines(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * PUT 用の請求項目ペイロード。誤って空で上書きする場合は null（送信しない）。
 * @returns {{ billingLines: object[], allowClearBillingLines: boolean } | null}
 */
function resolveBillingLinesPayloadForPut(p, options = {}) {
  const pid = String(p?.id || "");
  const want =
    !!options.includeBillingLines || (pid && uiState.projectDocsDraftDirty[pid]);
  if (!want) return null;

  const route = currentRoute();
  // モバイル（640px以下）では doc-line-desktop-cell が非表示のため
  // DOM の input.value は初回レンダー時の旧値のまま。
  // ここで syncFromDom を呼ぶと、モバイル保存後に正しく更新されたドラフト値が
  // 古い DOM 値で上書きされてしまうため、モバイル時はスキップする。
  const isMobileViewport = typeof window !== "undefined" && window.innerWidth <= 640;
  if (route.page === "project-detail" && String(route.id) === pid && !isMobileViewport) {
    syncProjectDetailBillingTableFromDom(pid);
  }
  const d = ensureProjectDocDraft(pid);
  const lines = Array.isArray(d?.lines) ? d.lines : [];
  const snap = uiState.projectBillingLinesServerSnapshot?.[pid];
  const allowClearBillingLines =
    !!options.allowClearBillingLines ||
    (!!uiState.projectDocsDraftDirty[pid] && lines.length === 0);

  if (lines.length === 0 && snap && snap.lineCount > 0 && !allowClearBillingLines) {
    return null;
  }
  return {
    billingLines: lines,
    allowClearBillingLines: allowClearBillingLines && lines.length === 0
  };
}

/**
 * 案件詳細「項目」テーブルの入力値を projectDocsDrafts に反映する。
 * 「書類作成・編集へ」直前に呼び、未確定の DOM のみがソースのときでも書類プレビューと一致させる。
 */
function syncProjectDetailBillingTableFromDom(projectId) {
  const pid = String(projectId || "");
  if (!pid) return;
  let table = null;
  try {
    table = document.querySelector(`table.doc-lines[data-project-billing-table="${CSS.escape(pid)}"]`);
  } catch {
    table = document.querySelector(`table.doc-lines[data-project-billing-table="${pid}"]`);
  }
  if (!table) return;
  const draft = ensureProjectDocDraft(pid);
  const lines = draft?.lines;
  if (!Array.isArray(lines)) return;
  let changed = false;
  for (let i = 0; i < lines.length; i += 1) {
    const tr = table.querySelector(`tr.doc-detail-line-row[data-detail-line-drop="${i}"]`);
    if (!tr) continue;
    tr.querySelectorAll("[data-detail-line-field]").forEach((el) => {
      const field = el.dataset.detailLineField;
      const idx = Number(el.dataset.detailLineIndex);
      if (!field || idx !== i) return;
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
      let next = el.value;
      if (lines[i][field] !== next) {
        lines[i][field] = next;
        changed = true;
      }
    });
    if (projectDetailLineType(lines[i]) === "normal") {
      const nextAmt = String(billingLineAmountExTax(lines[i]));
      if (lines[i].amount !== nextAmt) {
        lines[i].amount = nextAmt;
        changed = true;
      }
    }
  }
  if (changed) {
    markProjectDocsDraftDirty(pid);
    schedulePersistProjectDocDraftsStore();
  }
}

function projectDetailNormalLineAmountExTax(line) {
  return projectDetailLineType(line) === "normal" ? billingLineAmountExTax(line) : 0;
}

/** 案件詳細「項目」タブ：行 rowIndex 直前まで（小計行でリセット後）の税抜合計 */
function projectDetailBillingSubtotalUntil(lines, rowIndex) {
  if (!Array.isArray(lines)) return 0;
  let sum = 0;
  for (let i = 0; i < rowIndex; i += 1) {
    const row = lines[i];
    if (projectDetailLineType(row) === "subtotal") {
      sum = 0;
      continue;
    }
    if (projectDetailLineType(row) === "normal") sum += billingLineAmountExTax(row);
  }
  return sum;
}

function projectDetailBillingFooterTotals(lines) {
  const subtotal = Array.isArray(lines)
    ? lines.reduce((s, l) => s + projectDetailNormalLineAmountExTax(l), 0)
    : 0;
  const tax = billingTaxYen(subtotal);
  return { subtotal, tax, total: subtotal + tax };
}

/** 数量・単価入力中は render せず、金額列・合計だけ DOM 更新（IME / 連続入力が壊れないようにする） */
function updateProjectDetailBillingDerivedCells(projectId) {
  const route = currentRoute();
  if (route.page !== "project-detail" || String(route.id) !== String(projectId)) return;
  const table = document.querySelector(
    `table.doc-lines[data-project-billing-table="${CSS.escape(String(projectId))}"]`
  );
  if (!table?.isConnected) return;
  const docDraft = ensureProjectDocDraft(projectId);
  const lines = docDraft?.lines;
  if (!Array.isArray(lines)) return;
  const tbody = table.querySelector("tbody");
  if (!tbody) return;
  const trs = tbody.querySelectorAll("tr.doc-detail-line-row");
  if (trs.length !== lines.length) {
    render();
    return;
  }
  lines.forEach((l, i) => {
    const row = tbody.querySelector(`tr.doc-detail-line-row[data-detail-line-drop="${i}"]`);
    if (!row) return;
    const type = projectDetailLineType(l);
    if (type === "heading") return;
    if (type === "subtotal") {
      const el = row.querySelector("[data-detail-billing-subtotal-value]");
      if (el) el.textContent = yen.format(projectDetailBillingSubtotalUntil(lines, i));
      return;
    }
    const el = row.querySelector("[data-detail-billing-line-amount]");
    if (el) el.textContent = yen.format(billingLineAmountExTax(l));
  });
  const { subtotal, tax, total } = projectDetailBillingFooterTotals(lines);
  // フッター合計は table 外（soft-card 内の兄弟 div）にあるため、親から探す
  const footRoot = table.parentElement;
  const footSub = footRoot?.querySelector("[data-detail-billing-foot-subtotal]") ?? null;
  const footTax = footRoot?.querySelector("[data-detail-billing-foot-tax]") ?? null;
  const footTot = footRoot?.querySelector("[data-detail-billing-foot-total]") ?? null;
  if (footSub) footSub.textContent = yen.format(subtotal);
  if (footTax) footTax.textContent = yen.format(tax);
  if (footTot) footTot.textContent = yen.format(total);
}

/**
 * 書類作成・編集ページ（project-docs）版。案件詳細と同じく render せず金額列・合計だけ DOM 更新する。
 * 明細セルは金額テキストの後ろに行ツールバー（rowbar）を持つため、textContent ではなく先頭テキストノードだけ書き換える。
 */
function updateProjectDocsBillingDerivedCells(projectId) {
  const route = currentRoute();
  if (route.page !== "project-docs" || String(route.id) !== String(projectId)) return;
  const table = document.querySelector(".doc-create-preview-slot table.doc-inv-lines-table");
  if (!table?.isConnected) return;
  const lines = ensureProjectDocDraft(projectId)?.lines;
  if (!Array.isArray(lines)) return;
  const setAmountText = (el, value) => {
    if (!el) return;
    const text = formatYenPlain(value);
    const textNode = Array.from(el.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
    if (textNode) textNode.nodeValue = text;
    else el.insertBefore(document.createTextNode(text), el.firstChild);
  };
  lines.forEach((l, i) => {
    const row = table.querySelector(`tr.doc-detail-line-row[data-detail-line-drop="${i}"]`);
    if (!row) return;
    const type = projectDetailLineType(l);
    if (type === "heading") return;
    if (type === "subtotal") {
      setAmountText(row.querySelector("[data-detail-billing-subtotal-value]"), projectDetailBillingSubtotalUntil(lines, i));
      return;
    }
    setAmountText(row.querySelector("[data-detail-billing-line-amount]"), billingLineAmountExTax(l));
  });
  const { subtotal, tax, total } = projectDetailBillingFooterTotals(lines);
  const sheet = table.closest(".doc-sheet");
  setAmountText(table.querySelector("[data-doc-foot-subtotal]"), subtotal);
  setAmountText(table.querySelector("[data-doc-foot-tax]"), tax);
  setAmountText(table.querySelector("[data-doc-foot-total]"), total);
  // 上部「合計金額 ○○ 円（税込）」も同じ合計を出しているので揃える
  setAmountText(sheet?.querySelector(".doc-inv-total-val strong"), total);
}

/**
 * プロジェクト一覧・CSV の「請求額（税抜）」の数値。
 * 請求項目の明細（下書き）があれば通常行のみ税抜合算、無ければ従来どおり order ／ 受注時の amount。
 */
function projectDisplayedBillingExTaxYen(p) {
  if (!p) return 0;
  const lines = uiState.projectDocsDrafts?.[p.id]?.lines;
  if (Array.isArray(lines) && lines.length > 0) {
    let sum = 0;
    for (const l of lines) {
      const t = l?.lineType || "normal";
      if (t === "normal") sum += billingLineAmountExTax(l);
    }
    return Math.round(sum);
  }
  return (
    Number(p.order ?? (isWonOrderStatus(projectOrderStatusForUi(p)) ? Number(p.amount || 0) : 0)) || 0
  );
}

function calc(lines, taxRate = BILLING_TAX_RATE) {
  const subtotal = lines.reduce((sum, l) => sum + billingLineAmountExTax(l), 0);
  const tax = billingTaxYen(subtotal, taxRate);
  return { subtotal, tax, total: subtotal + tax };
}

function getTemplatesByType(docType) {
  // 見えるもの（チーム共通＋自分専用）はサーバーが絞って返す。未取得なら取りに行く（15z-doc-templates.js）
  if (uiState.docTemplates === null) fetchOnceInFlight("doc-templates", fetchDocTemplates);
  return (uiState.docTemplates || []).filter((t) => t.docType === docType);
}

function openDocStart(type, projectId) {
  uiState.docStart = {
    type,
    projectId,
    step: 1,
    method: "blank"
  };
  render();
}

function closeDocStart() {
  uiState.docStart = null;
  render();
}

function openComposer(type, projectId, options = {}) {
  // 見つからなければ開かない（以前は一覧の先頭＝別の案件で開いていた）
  const p = projects.find((item) => item.id === projectId);
  if (!p) {
    showToastError("案件が見つかりません。一覧から開き直してください");
    return;
  }
  // 保存済みドキュメントがあればそちらを優先（テンプレート指定がない場合）
  const savedDoc = p.savedDocs?.[type] || null;
  const preset = options.preset || savedDoc || null;
  const issueDateLabel = type === "quote" ? "発行日" : "請求日";
  const dueDateLabel = type === "quote" ? "有効期限" : "支払期限";
  const title = type === "quote" ? "見積書作成" : type === "invoice" ? "請求書作成" : "請書作成";
  const primary = type === "quote" ? "送付準備OKにする" : "送付済みにする";
  const defaultDays = preset?.dueDays || (type === "quote" ? 14 : 30);
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + defaultDays);
  uiState.composer = {
    type,
    projectId: p.id,
    title,
    primary,
    issueDateLabel,
    dueDateLabel,
    company: savedDoc?.company || clientName(p),
    person: savedDoc?.person || clientContact(p),
    subject: preset?.subject || `${p.name} ${type === "quote" ? "見積" : "請求"}`,
    // 既定は今日（以前は開発時の固定日が入り、そのまま見積・請求書に印字された）
    issueDate: savedDoc?.issueDate || localDateToYmd(new Date()),
    dueDate: savedDoc?.dueDate || `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, "0")}-${String(dueDate.getDate()).padStart(2, "0")}`,
    note: preset?.note || "",
    // 既定は自社の振込先（以前はダミーの口座が入り、そのまま書類に印字された）
    bankMemo: preset?.bankMemo || formalIssuerConstants().BANK_DEF,
    taxRate: typeof preset?.taxRate === "number" ? preset.taxRate : 0.1,
    templateName: options.templateName || "",
    lines: preset?.lines?.length ? preset.lines.map((l) => ({ ...l })) : [{ item: "デザイン制作", qty: 1, price: p.amount }]
  };
  uiState.docStart = null;
  render();
}

function closeComposer() {
  uiState.composer = null;
  // 「まとめて請求書を作成」からの自動オープン用フラグが残っていると、次の render で
  // また開いてしまう（route.autoComposeInvoice がハッシュ由来のため）。閉じたら消す
  if (/[?&]composeInvoice=1(&|$)/.test(location.hash)) {
    const raw = location.hash.replace(/^#/, "");
    const qm = raw.indexOf("?");
    const path = qm >= 0 ? raw.slice(0, qm) : raw;
    const q = new URLSearchParams(qm >= 0 ? raw.slice(qm + 1) : "");
    q.delete("composeInvoice");
    const qs = q.toString();
    history.replaceState(null, "", `${location.pathname}${location.search}#${path}${qs ? `?${qs}` : ""}`);
  }
  render();
}

let toastHideTimer = null;
let toastSeq = 0;

/** @param {string} message @param {{ sticky?: boolean, variant?: 'info'|'error', durationMs?: number }} [opts] */
function showToast(message, opts = {}) {
  const sticky = opts.sticky === true;
  const variant = opts.variant === "error" ? "error" : "info";
  const durationMs =
    typeof opts.durationMs === "number" && Number.isFinite(opts.durationMs)
      ? Math.max(400, opts.durationMs)
      : 2500;
  if (toastHideTimer) {
    clearTimeout(toastHideTimer);
    toastHideTimer = null;
  }
  const seq = ++toastSeq;
  uiState.toast = { message: String(message ?? ""), sticky, variant, seq };
  render();
  if (!sticky) {
    toastHideTimer = setTimeout(() => {
      if (uiState.toast && uiState.toast.seq === seq) {
        uiState.toast = null;
        render();
      }
      toastHideTimer = null;
    }, durationMs);
  }
}

/** エラー用: 自動では消えず、閉じるまで残す */
function showToastError(message, opts = {}) {
  showToast(message, { ...opts, sticky: true, variant: "error" });
}

/**
 * render() を呼ばずにトーストをDOM直挿入する。
 * textarea 入力中などフル再描画を避けたい場面に使う。
 */
function showToastDirect(message, durationMs = 2500) {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();
  const div = document.createElement("div");
  div.className = "toast toast--info";
  div.setAttribute("role", "status");
  div.innerHTML = `<div class="toast-body">${escapeHtml(String(message ?? ""))}</div>`;
  document.body.appendChild(div);
  setTimeout(() => { if (div.isConnected) div.remove(); }, durationMs);
}

/** 自動で消えないトースト（長い処理の進捗用）。終わったら showToast / clearToast で上書き・解除 */
function showToastPersistent(message) {
  showToast(message, { sticky: true, variant: "info" });
}

/**
 * 進捗トーストの文面だけを差し替える（表示中のトーストが無ければ通常どおり出す）。
 * showToastPersistent は showToast 経由で render() を呼ぶため、ループで1件ごとに呼ぶと
 * 画面全体を毎回作り直すことになる。原価取込のプレビューを開いたままだと
 * 「129行 × 案件セレクトの全案件 option」を毎件再構築して極端に遅くなるため、
 * 件数カウンタのような更新はここで DOM を直接書き換える（showToastDirect と同じ方針）。
 */
function showToastProgress(message) {
  const msg = String(message ?? "");
  const body = uiState.toast && uiState.toast.sticky ? document.querySelector(".toast .toast-body") : null;
  if (!body) {
    showToastPersistent(msg);
    return;
  }
  uiState.toast.message = msg; // 次に render() されたときも同じ文面になるよう state も揃える
  body.textContent = msg;
}

function clearToast() {
  if (toastHideTimer) {
    clearTimeout(toastHideTimer);
    toastHideTimer = null;
  }
  uiState.toast = null;
  render();
}

/** CSV一括取り込みの失敗内容を短文でまとめる（先頭数件の例示） */
function summarizeCsvImportErrors(samples, lastErr, failCount) {
  const head = [];
  for (let i = 0; i < (samples || []).length && head.length < 3; i++) {
    const t = String(samples[i] || "").trim();
    if (t && !head.includes(t)) head.push(t);
  }
  const lines = [];
  if (head.length)
    lines.push(`内容の例:\n${head.map((x) => `・${x}`).join("\n")}${failCount > head.length ? `\n（ほか計${failCount}件が失敗）` : ""}`);
  const le = String(lastErr || "").trim();
  if (le && !head.some((x) => x.includes(le.slice(0, 60))))
    lines.push(`直近の応答: ${le}`);
  if (!lines.length && failCount)
    lines.push(
      `${failCount}件が失敗しました。通信エラー、またはサーバー負荷・認可エラーの可能性があります。しばらくしてから分割して再試行してください。`
    );
  return lines.join("\n\n");
}

function pushDistinctImportErr(arr, msg) {
  const t = String(msg || "").trim();
  if (!t || arr.includes(t)) return;
  if (arr.length < 8) arr.push(t);
}

// 書類のメール送信は「メール作成画面」（app-src/15h-doc-mail-compose.js）に移行した。
// 宛先・件名・本文を確認してから送るため、ここでの即時送信ヘルパは持たない。

/** 発行履歴をサーバーから取得してキャッシュ（書類ページ表示時に呼ぶ） */
async function loadInvoiceIssueHistory(proj, docType) {
  if (!authToken || !proj?.dbId) return;
  const key = `${proj.id}:${docType}`;
  if (!uiState.invoiceIssueHistory) uiState.invoiceIssueHistory = {};
  // 取得中フラグで多重取得を防ぐ
  if (!uiState.invoiceIssueHistoryLoading) uiState.invoiceIssueHistoryLoading = {};
  if (uiState.invoiceIssueHistoryLoading[key]) return;
  uiState.invoiceIssueHistoryLoading[key] = true;
  try {
    const res = await fetch(
      `/api/invoice-issue-history?projectId=${encodeURIComponent(proj.dbId)}&docType=${encodeURIComponent(docType)}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok && Array.isArray(data.history)) {
      uiState.invoiceIssueHistory[key] = data.history;
      if (currentRoute().page === "project-docs") render();
    }
  } catch (e) {
    console.warn("loadInvoiceIssueHistory failed:", e?.message || e);
  } finally {
    uiState.invoiceIssueHistoryLoading[key] = false;
  }
}

// ────────────────────────────────────────────────────────────────
// PDF出力: 見積書・請求書・請書を新ウィンドウで表示 → 印刷/PDF保存
// 今後: invoice / order も同じ関数で対応できる設計
// 今後: メール送付時も同じ doc オブジェクトを使って本文生成できる
// ────────────────────────────────────────────────────────────────

/**
 * 請求書の支払期限を、クライアントの支払条件（月末締の各パターン）と締め月から自動計算する。
 * 対応するのは「月末締」で始まる条件のみ（10日締・15日締など日締めは日情報が要るため対象外＝空を返す）。
 * 返り値は 'YYYY-MM-DD' または ""（自動計算できない/情報不足）。手修正できる前提のデフォルト値。
 * @param {string} terms 支払条件（例 "月末締翌月末払"）
 * @param {string} closingMonth 締め月 'YYYY-MM' または 'YYYY/MM'（＝請求月）
 */
function computeInvoiceDueFromClientTerms(terms, closingMonth) {
  const t = String(terms || "").replace(/[\s　]/g, "");
  if (!/^月末締め?/.test(t)) return ""; // 月末締のみ自動化
  const bm = String(closingMonth || "").match(/(\d{4})[-/](\d{1,2})/);
  if (!bm) return "";
  // 支払月オフセット（当月=0 / 翌=1 / 翌々=2 / 翌Nヶ月=N）
  let offset = 0;
  const mMonths = t.match(/翌(\d+)[ヶか]?月/);
  if (mMonths) offset = Number(mMonths[1]);
  else if (t.includes("翌々")) offset = 2;
  else if (t.includes("翌")) offset = 1;
  let y = Number(bm[1]);
  let mo = Number(bm[2]) + offset; // 1-based
  y += Math.floor((mo - 1) / 12);
  mo = ((mo - 1) % 12) + 1;
  const lastDay = new Date(y, mo, 0).getDate();
  // 支払日（"NN日"指定があればその日、無ければ末日）
  const dMatch = t.match(/(\d{1,2})日/);
  let day = dMatch ? Number(dMatch[1]) : lastDay;
  if (day > lastDay) day = lastDay;
  return `${y}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * 現在読み込み済みのstyle.cssの?v=値を、そのまま新規プレビュー画面のstyle.cssにも使う。
 * style.cssは Cache-Control: public, max-age=31536000, immutable で配信されているため、
 * 毎回変わる値(Date.now()等)を使うとキャッシュが一切効かず、PDFボタンを押すたびに
 * 無スタイルの画面が一瞬表示される(FOUC)。今アプリが読み込んでいる?v=をそのまま使えば、
 * 既にブラウザにキャッシュ済みの同じURLにヒットしつつ、リリースでindex.htmlの?v=が
 * 更新されれば自動的に最新を指すようになる。
 */
function currentStyleCssVersion() {
  const link = document.querySelector('link[href*="style.css"]');
  const m = link && link.getAttribute("href").match(/[?&]v=([^&]+)/);
  return m ? m[1] : Date.now();
}

/**
 * 帳票の印刷用/PDF化用 HTML を組み立てる（共通ロジック）。
 * 確認プレビュー（printDocument）専用の生成元。正式PDF発行（downloadOfficialDocumentPdf）は
 * サーバー（api/doc-pdf）がDBの保存済みデータから生成するため、ここは使わない。
 * @returns {{html: string, typeLabel: string}|null} doc が無ければ null（トースト表示済み）
 */
function buildDocPrintHtml(type, projectId, docOverride) {
  const p = projects.find((x) => x.id === projectId);
  if (!p) return null;
  // コンポーザーが開いていればその内容を優先、なければ保存済みドキュメントを使う（docOverride があれば最優先）
  const doc =
    docOverride ||
    (uiState.composer && uiState.composer.projectId === projectId && uiState.composer.type === type
      ? uiState.composer
      : p.savedDocs?.[type]);
  if (!doc) {
    showToast("先に書類を保存してください（下書き保存でもOK）");
    return null;
  }
  const typeLabel = type === "quote" ? "見積書" : type === "invoice" ? "請求書" : "請書";
  const dueDateLabel = type === "quote" ? "有効期限" : "支払期限";
  const docTaxRate = typeof doc.taxRate === "number" ? doc.taxRate : 0.1;
  const docTaxPct = Math.round(docTaxRate * 100);
  const totals = calc(doc.lines || [], docTaxRate);
  // 書類番号は画面・正式PDF（api/_docRender.js）と同じ「発行日の年-案件ID(3桁)」。以前は配列の並び順と
  // 開発時の固定日を使っていたため、確認プレビューだけ番号が違い、並び替えで変わった
  const docIdx = p.dbId || String(p.id || "").replace(/\D/g, "") || "00000";
  const docYear = String(doc.issueDate || localDateToYmd(new Date())).slice(0, 4);
  const docNumber = `${docYear}-${String(docIdx).padStart(3, "0")}`;
  const baseHref = location.href.replace(/[#?].*/, "").replace(/[^/]+$/, "");

  /** 請求・見積 PDF（共通書式。捺印申請バナーは含めない） */
  if (type === "invoice" || type === "quote") {
    const FI = formalIssuerConstants();
    const cli = clients.find((c) => c.id === p.clientId);

    const idParts = (doc.issueDate || "").split("-");
    const dateJp =
      idParts.length === 3
        ? `${idParts[0]}年${idParts[1]}月${idParts[2]}日`
        : (doc.issueDate || "").replace(/-/g, "/");

    let dueRaw = doc.dueDate || "";
    // 請求書で支払期限が未設定なら、クライアントの支払条件×請求月から自動計算（手修正可）
    if (type === "invoice" && !String(dueRaw).trim()) {
      const closingMonth = p.billingMonth || (p.plannedInvoiceDate ? String(p.plannedInvoiceDate).slice(0, 7) : (doc.issueDate ? String(doc.issueDate).slice(0, 7) : ""));
      dueRaw = computeInvoiceDueFromClientTerms(cli?.paymentTerms, closingMonth) || "";
    }
    const dueDisp = String(dueRaw).replace(/-/g, "/");
    const subjectLine = doc.subject || p.name || "";

    const subtotal = totals.subtotal;
    const taxAmt = totals.tax;
    const totalAmt = totals.total;
    const taxPct = Math.round(docTaxRate * 100);

    const printLineRows =
      (doc.lines || []).length === 0
        ? `<tr><td colspan="5" class="doc-inv-line-empty">明細なし</td></tr>`
        : (doc.lines || [])
            .map((l) => {
              const priceNum = Number(l.price || 0);
              const qtyNum = Number(l.qty || 0);
              const amt = billingLineAmountExTax({ qty: qtyNum, price: priceNum, unit: l.unit });
              return `<tr>
        <td class="doc-inv-td-sum">${escapeHtml(l.item || "")}</td>
        <td class="doc-inv-td-qty">${escapeHtml(String(l.qty ?? ""))}</td>
        <td class="doc-inv-td-unit">${escapeHtml(String(l.unit || ""))}</td>
        <td class="doc-inv-td-unitprice">${yen.format(priceNum)}</td>
        <td class="doc-inv-td-amt">${yen.format(amt)}</td>
      </tr>`;
            })
            .join("");

    const footRows = `
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">${type === "invoice" && docTaxRate > 0 ? `${taxPct}%対象（税抜）` : "小計（税抜）"}</td>
      <td class="doc-inv-foot-amt">${yen.format(subtotal)}</td>
    </tr>
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">${docTaxRate > 0 ? `消費税（${taxPct}%）` : "消費税"}</td>
      <td class="doc-inv-foot-amt">${yen.format(taxAmt)}</td>
    </tr>
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">合計（税込）</td>
      <td class="doc-inv-foot-amt doc-inv-foot-amt-strong">${yen.format(totalAmt)}</td>
    </tr>
    `;

    // 未保存の請求項目があるときは承認済みでも社印を出さない。プレビューは保存前の内容で組み立てるため、
    // 承認後に金額を書き換えて保存せず印刷すると、承認していない金額の書類に社印が付いていた
    // （保存すればサーバーが明細の変化を見て承認を取り消すが、プレビューは保存を通らない）
    const hasUnsavedBillingLines = !!uiState.projectDocsDraftDirty?.[String(p.id)];
    const pdfSealApproved = docTabHasSealWorkflow(type) && getDocSealStatus(p.id, type) === "承認済" && !hasUnsavedBillingLines;
    // 捺印申請が承認済みなら社印を自動表示（チェックボックス不要）。対象種別は docTypeShowsSealStamp
    const showSealOnPdf = docTypeShowsSealStamp(type) && pdfSealApproved;
    const issuerRightPdf = issuerOurBrandHtml(
      FI.CO,
      FI.ADDR_LINES,
      FI.TEL,
      FI.REG,
      salesOwnerUser(p)?.name || ownerName(p),
      showSealOnPdf
    );

    let titleSpread = "";
    let dateLabelPdf = "";
    let notePhrase = "";
    let metaInnerRowsPdf = "";
    let remarkEscaped = "";

    if (type === "invoice") {
      titleSpread = "請求書";
      dateLabelPdf = "請求日";
      notePhrase = "下記の通り、御請求申し上げます。";
      metaInnerRowsPdf = `
      <tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(subjectLine)}</td></tr>
      <tr><th class="doc-inv-meta-th">支払期限</th><td class="doc-inv-meta-td">${escapeHtml(dueDisp)}</td></tr>
      <tr><th class="doc-inv-meta-th">振込先</th><td class="doc-inv-meta-td">${escapeHtml(doc.bankMemo || FI.BANK_DEF)}</td></tr>`;
      remarkEscaped = escapeHtml(doc.note || "");
    } else {
      titleSpread = "見積書";
      dateLabelPdf = "見積日";
      notePhrase = "下記の通り、お見積り申し上げます。";
      metaInnerRowsPdf = `
      <tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(subjectLine)}</td></tr>
      <tr><th class="doc-inv-meta-th">有効期限</th><td class="doc-inv-meta-td">${escapeHtml(dueDisp)}</td></tr>`;
      remarkEscaped = escapeHtml(doc.note || "");
    }

    // 宛先担当者名。案件で選んだ担当者(p.person)→クライアントマスタの順（グローバル関数 clientContact(p) と
    // 同じ優先順位だが、その既定値「ご担当者様」は使わない）。既定値を使うと「ご担当者様 様」の二重敬称になり、
    // かつサーバの実PDF（api/_docRender.js の `client.contact_name &&` ガード）は担当者不在なら行ごと出さないため、
    // 確認プレビューと実PDFで宛名の行数が食い違う。projectDocsPage 側（下の recipientContactExtraHtml）と同じ形。
    // trim はサーバ（api/_docAddress.js の String(personName||'').trim() || clientContactName）と条件を揃えるため。
    // client_person は自由記入テキストの時期があり、空白だけの値だと片側だけ担当者行が出てしまう。
    const recipientContactName = String(p.person || "").trim() || String(cli?.contactName || "").trim();

    const innerSheet = formalSheetInnerHtml({
      titleSpread,
      notePhrase,
      docNoStr: docNumber,
      dateLabel: dateLabelPdf,
      dateStrVal: dateJp,
      // recipientName/recipientContactExtraHtml は住所（recipientAddrStr）と同じく常にライブ計算する。
      // 以前は doc.company/doc.person（書類を最後に保存した時点のスナップショット）を使っていたため、
      // 保存後に案件で支社・担当者を選び直しても確認プレビューに反映されなかった。この2項目は
      // コンポーザーに自由入力欄が無く案件・クライアントからしか値が入らないため、
      // ライブ化しても手動編集を上書きする心配はない。
      recipientName: clientName(p),
      recipientAddrStr: clientRecipientAddressLine(p),
      recipientHonorificSuffix: `　${resolveRecipientHonorific(cli?.honorific)}`,
      recipientContactExtraHtml:
        recipientContactName && !isIndividualHonorific(cli?.honorific)
          ? `<p class="doc-inv-addr-sub">${escapeHtml(recipientContactName)} 様</p>`
          : "",
      issuerRightHtml: issuerRightPdf,
      metaInnerRowsHtml: metaInnerRowsPdf,
      totalPlainYen: formatYenPlain(totalAmt),
      linesBodyHtml: printLineRows,
      linesFooterHtml: footRows,
      remarkHtmlEscaped: remarkEscaped,
      remarkEditable: false,
      remarkProjectId: "",
      remarkTab: "",
      remarkText: ""
    });

    const pdfWrapped = formalDocPdfWrap(p.id, type, innerSheet);

    const invHtml = `<!DOCTYPE html>
<html lang="ja"><head><meta charset="UTF-8" />
<title>${typeLabel} - ${escapeHtmlAttr(clientName(p))}</title>
<link rel="stylesheet" href="${escapeHtmlAttr(baseHref)}style.css?v=${currentStyleCssVersion()}" />
<style>
@page { size: A4 portrait; margin: 10mm 10mm; }
body.doc-print-root { margin: 0; background: #e4e4e4; }
@media print {
  body.doc-print-root { background: #fff !important; }
  .inv-no-print { display: none !important; }
  .doc-seal-banner,
  .doc-seal-widget { display: none !important; }
}
</style>
</head>
<body class="doc-print-root doc-print-root--formal">
${pdfWrapped}
<div class="inv-no-print" style="margin-top:24px;text-align:center;padding:20px;">
  <button type="button" onclick="window.print()" style="background:#111827;color:#fff;border:none;padding:12px 28px;font-size:14px;border-radius:6px;cursor:pointer;">印刷 / PDF保存</button>
  <p style="margin-top:12px;font-size:12px;color:#6b7280;max-width:36em;margin-left:auto;margin-right:auto;line-height:1.5;">1枚に収めやすくするには、印刷ダイアログで「ヘッダーとフッター」をオフにし、余白は「デフォルト」推奨です。</p>
</div>
</body></html>`;

    return { html: invHtml, typeLabel };
  }

  const linesHtml = (doc.lines || [])
    .map(
      (l) => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${escapeHtml(l.item || "")}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${Number(l.qty || 0)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatYenPlain(Number(l.price || 0))}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatYenPlain(billingLineAmountExTax(l))}</td>
      </tr>`
    )
    .join("");
  const bankHtml =
    type === "invoice" && doc.bankMemo
      ? `<div style="margin-top:24px;padding:14px 16px;background:#f9fafb;border-radius:6px;border-left:3px solid #d1d5db;">
          <div style="font-size:11px;color:#9ca3af;margin-bottom:6px;letter-spacing:.05em;">お振込先</div>
          <div style="font-size:13px;line-height:1.8;white-space:pre-wrap;">${escapeHtml(doc.bankMemo)}</div>
        </div>`
      : "";
  const noteHtml = doc.note
    ? `<div style="margin-top:24px;padding:14px 16px;background:#f9fafb;border-radius:6px;">
        <div style="font-size:11px;color:#9ca3af;margin-bottom:6px;letter-spacing:.05em;">備考</div>
        <div style="font-size:13px;line-height:1.7;white-space:pre-wrap;">${escapeHtml(doc.note)}</div>
      </div>`
    : "";
  const html = `<!doctype html>
<html lang="ja"><head><meta charset="UTF-8">
<title>${escapeHtml(typeLabel)} - ${escapeHtml(doc.company || "")} - ${escapeHtml(p.name || "")}</title>
<link rel="stylesheet" href="${escapeHtmlAttr(baseHref)}style.css?v=${currentStyleCssVersion()}" />
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Helvetica Neue',Arial,'Hiragino Kaku Gothic ProN','Hiragino Sans',Meiryo,sans-serif;color:#111827;background:#e4e4e4}
.page{max-width:210mm;margin:0 auto;padding:14mm 12mm;background:#fff;box-sizing:border-box;box-shadow:0 4px 24px rgba(0,0,0,.08)}
table{width:100%;border-collapse:collapse}
th{background:#f3f4f6;padding:8px 12px;font-size:12px;color:#6b7280;font-weight:600;border-bottom:2px solid #e5e7eb}
th:not(:first-child){text-align:right}
@page{size:A4 portrait;margin:14mm 12mm}
@media print{body,html{background:#fff!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-print{display:none!important}.page{max-width:none!important;box-shadow:none!important;margin:0!important;padding:0!important}}
</style></head>
<body class="doc-print-root">
<div class="page">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;">
    <h1 style="font-size:28px;font-weight:700;letter-spacing:.08em;">${typeLabel}</h1>
    <div style="text-align:right;font-size:12px;color:#6b7280;line-height:2.2;">
      <div>No. ${escapeHtml(docNumber)}</div>
      <div>発行日: ${escapeHtml(String(doc.issueDate || "").replace(/-/g, "/"))}</div>
      <div>${dueDateLabel}: ${escapeHtml(String(doc.dueDate || "").replace(/-/g, "/"))}</div>
    </div>
  </div>
  <div style="margin-bottom:32px;">
    <div style="font-size:18px;font-weight:600;margin-bottom:4px;">${escapeHtml(doc.company || "")} 御中</div>
    <div style="font-size:13px;color:#6b7280;">${escapeHtml(doc.person || "")}</div>
  </div>
  <div style="border-top:2px solid #111827;border-bottom:1px solid #e5e7eb;padding:12px 0;margin-bottom:24px;">
    <div style="font-size:11px;color:#9ca3af;margin-bottom:4px;letter-spacing:.05em;">件名</div>
    <div style="font-size:15px;font-weight:600;">${escapeHtml(doc.subject || "")}</div>
  </div>
  <table>
    <thead><tr>
      <th style="width:50%;text-align:left;">項目</th>
      <th style="width:12%;">数量</th>
      <th style="width:19%;">単価</th>
      <th style="width:19%;">金額</th>
    </tr></thead>
    <tbody>${linesHtml}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-top:16px;">
    <div style="width:280px;">
      <div style="display:flex;justify-content:space-between;padding:6px 12px;font-size:13px;border-bottom:1px solid #f3f4f6;">
        <span style="color:#6b7280;">小計</span><span>${yen.format(totals.subtotal)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;padding:6px 12px;font-size:13px;border-bottom:1px solid #f3f4f6;">
        <span style="color:#6b7280;">${docTaxPct > 0 ? `消費税（${docTaxPct}%）` : "非課税"}</span><span>${yen.format(totals.tax)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;padding:10px 12px;font-size:16px;font-weight:700;border-top:2px solid #111827;margin-top:4px;">
        <span>${type === "invoice" ? "請求金額" : "合計金額"}</span><span>${yen.format(totals.total)}</span>
      </div>
    </div>
  </div>
  ${noteHtml}
  ${bankHtml}
  <div style="margin-top:48px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:flex-end;">
    <div style="font-size:11px;color:#9ca3af;">${escapeHtml(p.name || "")}</div>
    <div style="text-align:right;">
      <div style="font-size:14px;font-weight:600;">On boarding</div>
      <div style="font-size:12px;color:#6b7280;margin-top:2px;">担当: ${escapeHtml(ownerName(p))}</div>
    </div>
  </div>
  <div class="no-print" style="margin-top:40px;text-align:center;padding:24px 0;border-top:1px solid #e5e7eb;">
    <button onclick="window.print()" style="background:#111827;color:white;border:none;padding:12px 32px;font-size:14px;border-radius:6px;cursor:pointer;font-family:inherit;font-weight:600;">印刷 / PDF保存</button>
    <p style="margin-top:10px;font-size:12px;color:#9ca3af;">「印刷」ダイアログで「送信先: PDFに保存」を選ぶとPDFとして保存できます</p>
  </div>
</div>
</body></html>`;
  return { html, typeLabel };
}

/**
 * 確認プレビュー：新規ウィンドウに帳票を表示し window.print() で印刷/PDF保存させる（従来どおり）。
 * 捺印承認状態を問わずいつでも押せる。発行履歴には記録しない。
 */
function printDocument(type, projectId, docOverride) {
  const built = buildDocPrintHtml(type, projectId, docOverride);
  if (!built) return;
  const win = window.open("", "_blank", "width=900,height=750");
  if (!win) {
    showToast("ポップアップがブロックされました。ブラウザの設定でポップアップを許可してください。");
    return;
  }
  win.document.write(built.html);
  win.document.close();
}

/**
 * 正式PDFを発行：サーバー（api/doc-pdf）がDBの保存済みデータ（billing_lines_json 等）から
 * 実際の PDF ファイルを生成してダウンロードさせる。クライアント側でHTMLは組み立てず送らない。
 * 発行履歴（fp_invoice_issue_history）はサーバー側で同じリクエスト内に記録済み
 * （呼び出し元が別途フロントから記録リクエストを送る必要はない）。
 */
/** 正式PDFのダウンロード名: 「案件番号_案件名_御見積書.pdf」の形（見積書は「御見積書」、他は書類名） */
function projectDocDownloadFilename(p, type) {
  // 検収書は捺印申請の対象外で DOC_SEAL_TAB_LABELS に無いので、ここで名前を付ける（一括出力で「書類」にならないように）
  const label = type === "quote" ? "御見積書" : type === "invoice" ? "請求書" : type === "inspection" ? "検収書" : (DOC_SEAL_TAB_LABELS[type] || "書類");
  const no = String(Number(String(p.no ?? p.dbId ?? "").replace(/[^\d]/g, "")) || p.dbId || "");
  return `${sanitizeClientDocDownloadBasename([no, String(p.name || "").trim()].filter(Boolean).join("_"))}_${label}.pdf`;
}

/** 正式PDFをサーバーで作って受け取る（発行履歴はサーバーが同じリクエストで記録）。
 *  1件の発行（downloadOfficialDocumentPdf）と案件一覧の一括出力（15z-project-bulk-docs.js）で共用。
 *  @returns {Promise<{ok:true, blob:Blob} | {ok:false, error:string}>} */
async function fetchOfficialDocumentPdf(p, type) {
  if (!p?.dbId) return { ok: false, error: "この案件はまだサーバーに保存されていません" };
  try {
    const res = await fetch("/api/doc-pdf", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({ projectId: p.dbId, docType: type })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: String(data.error || res.status) };
    }
    const blob = await res.blob();
    // 請求書を発行するとサーバーが受注状況を「受注済」へ進める（api/invoice-issue-history.js
    // advanceOrderStatusToWonOnInvoiceIssue と同じ条件）。画面の案件にも反映して、一覧が古い受注状況のまま残らないようにする
    if (type === "invoice" && !["受注済", "受注確定", "完了", "失注", "除外"].includes(String(p.orderStatus || ""))) {
      p.orderStatus = "受注済";
    }
    return { ok: true, blob };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

async function downloadOfficialDocumentPdf(type, projectId) {
  const p = projects.find((x) => x.id === projectId);
  if (!p?.dbId) {
    showToast("この案件はまだサーバーに保存されていません");
    return false;
  }
  try {
    const r = await fetchOfficialDocumentPdf(p, type);
    if (!r.ok) {
      showToastError(`PDF生成に失敗しました：${r.error}`);
      return false;
    }
    const blob = r.blob;
    const a = document.createElement("a");
    const href = URL.createObjectURL(blob);
    a.href = href;
    a.download = projectDocDownloadFilename(p, type);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
    // ダウンロードはブラウザ側の表示だけで画面に変化が無く、発行できたか分からないため明示する
    showToast(`正式PDFを発行しました（${a.download}）。発行履歴に記録されています`);
    return true;
  } catch (e) {
    showToastError(`PDF生成エラー：${e?.message || e}`);
    return false;
  }
}

// ────────────────────────────────────────────────────────────────
// 原価一覧 PDF 出力
// ────────────────────────────────────────────────────────────────
function printCostList(projectId) {
  const p = projects.find((x) => x.id === projectId);
  if (!p) return;
  const costs = p.costs || [];
  const totalCost = costs.reduce((s, c) => s + costTotalRegisteredYen(c), 0);
  const today = new Date();
  const dateStr = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}`;

  const rowsHtml = costs.length === 0
    ? `<tr><td colspan="5" style="padding:20px;text-align:center;color:#9ca3af;">原価がまだ登録されていません</td></tr>`
    : costs.map((c) => {
        const noteRaw = String(c.note || "").trim();
        const noteDisp = noteRaw ? escapeHtml(noteRaw.length > 240 ? `${noteRaw.slice(0, 240)}…` : noteRaw) : "—";
        return `
        <tr>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${escapeHtml(c.category)}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${escapeHtml(c.vendor || "—")}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${escapeHtml(c.targetMonth || "—")}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:11px;">${noteDisp}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:right;">${yen.format(c.amount)}${costConsumptionTaxYen(c) ? ` <span style="font-size:11px;color:#64748b;">(税 ${yen.format(costConsumptionTaxYen(c))})</span>` : ""}</td>
        </tr>`;
      }).join("");

  const baseHrefCost = location.href.replace(/[#?].*/, "").replace(/[^/]+$/, "");
  const html = `<!doctype html>
<html lang="ja"><head><meta charset="UTF-8">
<title>原価一覧 - ${escapeHtml(p.name || "")}</title>
<link rel="stylesheet" href="${escapeHtmlAttr(baseHrefCost)}style.css?v=${currentStyleCssVersion()}" />
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Helvetica Neue',Arial,'Hiragino Kaku Gothic ProN','Hiragino Sans',Meiryo,sans-serif;color:#111827;background:#e4e4e4}
.page{max-width:210mm;margin:0 auto;padding:14mm 12mm;background:#fff;box-sizing:border-box;box-shadow:0 4px 24px rgba(0,0,0,.08)}
table{width:100%;border-collapse:collapse}
th{background:#f3f4f6;padding:8px 12px;font-size:12px;color:#6b7280;font-weight:600;border-bottom:2px solid #e5e7eb}
@page{size:A4 portrait;margin:14mm 12mm}
@media print{body,html{background:#fff!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-print{display:none!important}.page{max-width:none!important;box-shadow:none!important;margin:0!important;padding:0!important}}
</style></head>
<body class="doc-print-root">
<div class="page">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;">
    <div>
      <h1 style="font-size:22px;font-weight:700;letter-spacing:.05em;">原価一覧</h1>
      <div style="margin-top:4px;font-size:14px;color:#6b7280;">${escapeHtml(p.name || "")} ／ ${escapeHtml(clientName(p))}</div>
    </div>
    <div style="text-align:right;font-size:12px;color:#6b7280;line-height:2.2;">
      <div>作成日: ${dateStr}</div>
      <div>担当: ${escapeHtml(ownerName(p))}</div>
    </div>
  </div>
  <table>
    <thead><tr>
      <th style="text-align:left;width:13%;">会計区分</th>
      <th style="text-align:left;width:26%;">発注先</th>
      <th style="text-align:left;width:11%;">計上月</th>
      <th style="text-align:left;width:28%;">備考</th>
      <th style="text-align:right;width:18%;">金額（税別）</th>
    </tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-top:16px;">
    <div style="width:260px;border-top:2px solid #111827;padding-top:10px;">
      <div style="display:flex;justify-content:space-between;padding:6px 12px;font-size:16px;font-weight:700;">
        <span>原価合計</span><span>${yen.format(totalCost)}</span>
      </div>
    </div>
  </div>
  <div style="margin-top:48px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">
    On boarding — ${dateStr} 出力
  </div>
  <div class="no-print" style="margin-top:20px;text-align:center;padding:24px 0;">
    <button onclick="window.print()" style="padding:10px 28px;background:#111827;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">印刷 / PDF保存</button>
    <p style="margin-top:10px;font-size:12px;color:#9ca3af;">「印刷」→「PDFに保存」でPDF出力できます</p>
  </div>
</div>
</body></html>`;

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) { showToast("ポップアップがブロックされました。許可してから再度お試しください。"); return; }
  win.document.write(html);
  win.document.close();
}

function header(title, subtitle, tools = "") {
  // 説明文が空のページでは <p> ごと出さない（空の <p> でも margin-top が付いて隙間になる）
  const sub = String(subtitle ?? "").trim() ? `<p>${subtitle}</p>` : "";
  return `<header class="page-head"><div><h2>${title}</h2>${sub}</div><div class="head-tools">${tools}</div></header>`;
}

function summaries(items) {
  return `<section class="section"><h3 class="section-title">重点サマリー</h3><div class="card-grid cols-4">${items
    .map((i) => `<article class="summary-card"><div class="label">${i.label}</div><div class="value ${i.tone || ""}">${i.value}</div></article>`)
    .join("")}</div></section>`;
}

function quickFilters() {
  return "";
}

function quickFiltersFor(pageType) {
  const options =
    pageType === "payments"
      ? [
          { id: "all", label: "すべて" },
          { id: "handoff_waiting", label: "経理確認待ち" },
          { id: "returned", label: "差し戻しあり" },
          { id: "today", label: "今日確認する" },
          { id: "mine", label: "自分担当のみ" }
        ]
      : pageType === "invoices"
        ? [
            { id: "all", label: "すべて" },
            { id: "inv_overdue", label: "今月要対応" },
            { id: "inv_unbilled", label: "未請求" },
            { id: "inv_billed", label: "請求済み" },
            { id: "inv_paid", label: "入金済み" }
          ]
        : [
    { id: "all", label: "すべて" },
    { id: "uncreated", label: "未作成のみ" },
    { id: "draft", label: "下書きあり" },
    { id: "ready", label: "送付準備OK" },
    { id: "sent", label: "送付済み" },
    { id: "expired", label: "期限切れ" },
    { id: "mine", label: "自分担当のみ" }
        ];
  const active = uiState.quickFilter[pageType] || "all";
  const chips = `<div class="toolbar">${options
    .map((o) => `<button class="chip-btn ${active === o.id ? "active" : ""}" data-quick-filter="${pageType}:${o.id}">${o.label}</button>`)
    .join("")}</div>`;
  if (pageType !== "invoices") return chips;
  const months = billingMonthList();
  const activeBm = uiState.quickFilter.invoiceBillingMonth || "all";
  const bmSelect = months.length > 0
    ? `<select class="select project-filter-select" data-billing-month-filter="1">
        <option value="all" ${activeBm === "all" ? "selected" : ""}>請求月: すべて</option>
        ${months.map((m) => `<option value="${m}" ${activeBm === m ? "selected" : ""}>${m}</option>`).join("")}
        <option value="unset" ${activeBm === "unset" ? "selected" : ""}>請求月: 未設定</option>
      </select>`
    : "";
  return chips + (bmSelect ? `<div class="toolbar" style="margin-top:6px;">${bmSelect}</div>` : "");
}

function filterItemsByQuickFilter(pageType, items) {
  const current = uiState.quickFilter[pageType] || "all";
  const bmf = pageType === "invoices" ? (uiState.quickFilter.invoiceBillingMonth || "all") : "all";
  if (current === "all" && bmf === "all") return items;

  let result = items.filter((p) => {
    if (current === "all") return true;
    const status = stageStatus(pageType, p);
    if (pageType === "payments" && current === "handoff_waiting") return p.handoffStatus === "経理確認待ち";
    if (pageType === "payments" && current === "returned") return p.handoffStatus === "差し戻し中";
    // 今日更新された案件（以前は開発時の固定日付と比べていて常に0件だった）
    if (pageType === "payments" && current === "today") return p.updatedAt === localDateToYmd(new Date());
    if (pageType === "invoices" && current === "inv_overdue") return billingUrgency(p) === "overdue" && simplifiedInvoiceStatus(p) === "未請求";
    if (pageType === "invoices" && current === "inv_unbilled") return simplifiedInvoiceStatus(p) === "未請求";
    if (pageType === "invoices" && current === "inv_billed") return simplifiedInvoiceStatus(p) === "請求済み";
    if (pageType === "invoices" && current === "inv_paid") return simplifiedInvoiceStatus(p) === "入金済み";
    if (current === "uncreated") return status === "未作成";
    if (current === "draft") return status === "下書きあり";
    if (current === "ready") return status === "送付準備OK";
    if (current === "sent") return status === "送付済み" || status === "締結済み" || status === "入金確認済み";
    if (current === "expired") return p.stale === "期限切れ";
    if (current === "mine") return p.salesOwnerId === currentUser.id || p.accountingOwnerId === currentUser.id || p.assignedUserId === currentUser.id;
    return true;
  });

  if (pageType === "invoices" && bmf !== "all") {
    result = result.filter((p) => bmf === "unset" ? !p.billingMonth : p.billingMonth === bmf);
  }

  return result;
}

function rowActions(p, pageType) {
  const s = stageStatus(pageType, p);
  const readOnly = isReadOnlyProject(p);
  const disabled = readOnly ? " disabled" : "";
  const readonlyTag = `<span class="readonly-note">閲覧専用</span>`;
  if (pageType === "contracts") {
    if (s === "未作成") return `${readOnly ? readonlyTag : `<button class="inline-btn" data-set-doc="contract:draft:${p.id}"${disabled}>下書きとしてマークする</button>`}<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
    if (s === "下書きあり") return `${readOnly ? readonlyTag : `<button class="inline-btn" data-set-doc="contract:edit:${p.id}"${disabled}>送付準備OKにする</button>`}<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
    if (s === "送付準備OK") return `${readOnly ? readonlyTag : `<button class="inline-btn" data-set-doc="contract:sent:${p.id}"${disabled}>送付済みにする</button>`}<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
    return `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
  }
  if (pageType === "quotes") {
    const label = docActionLabel(p, "quote");
    if (s === "未作成" || hasExistingDoc(p, "quote")) return `${readOnly ? readonlyTag : `<button class="inline-btn" data-compose="quote" data-project="${p.id}"${disabled}>${label}</button>`}${p.savedDocs?.quote ? `<button class="inline-btn ghost" data-print-saved="quote:${p.id}">PDF出力</button>` : ""}${s === "送付済み" || readOnly ? `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>` : ""}`;
    return `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
  }
  if (pageType === "invoices") {
    const label = docActionLabel(p, "invoice");
    if (s === "未作成" || s === "下書きあり" || ["送付済み", "入金待ち", "入金確認済み"].includes(s)) {
      return `${readOnly ? readonlyTag : `<button class="inline-btn" data-compose="invoice" data-project="${p.id}"${disabled}>${label}</button>`}${p.savedDocs?.invoice ? `<button class="inline-btn ghost" data-print-saved="invoice:${p.id}">PDF出力</button>` : ""}${["送付済み", "入金待ち", "入金確認済み"].includes(s) || readOnly ? `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>` : ""}`;
    }
    if (s === "送付準備OK") return `${readOnly ? readonlyTag : `<button class="inline-btn" data-set-doc="invoice:sent:${p.id}"${disabled}>送付する</button>`}${p.savedDocs?.invoice ? `<button class="inline-btn ghost" data-print-saved="invoice:${p.id}">PDF出力</button>` : ""}`;
    return `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
  }
  if (pageType === "orders") return `${readOnly ? readonlyTag : `<button class="inline-btn" data-set-doc="order:sent:${p.id}"${disabled}>請書 / 発注を処理する</button>`}<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
  if (pageType === "payments") {
    const canPay = canConfirmPayment(p);
    const canReturn = canTransferToAccounting(p) || salesOwnerUser(p)?.id === currentUser.id;
    if (p.handoffStatus === "差し戻し中") return `<button class="inline-btn ghost" data-open-detail="${p.id}">内容を確認する</button>${canReturn ? `<button class="inline-btn" data-handoff="to-sales:${p.id}">営業へ差し戻す</button>` : readonlyTag}`;
    if (p.docs.payment === "入金確認済み") return `<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>`;
    const bulkCheck = canPay
      ? `<label class="pay-bulk-check-wrap" title="一括入金の対象に選択"><input type="checkbox" class="pay-bulk-check" data-payment-bulk-check="${p.id}"${uiState.paymentBulkSelected?.[p.id] ? " checked" : ""} aria-label="一括入金の対象に選択" /></label>`
      : "";
    return `${bulkCheck}<button class="inline-btn ghost" data-open-detail="${p.id}">詳細を見る</button>${canPay ? `<button class="inline-btn" data-open-payment-form="${p.id}">入金確認を記録する</button>` : ""}`;
  }
  if (pageType === "dashboard") {
    const urgency = billingUrgency(p);
    if (!readOnly && (urgency === "overdue" || urgency === "unset") && simplifiedInvoiceStatus(p) === "未請求") {
      return `<button class="inline-btn" data-compose="invoice" data-project="${p.id}">請求書を作成する</button>`;
    }
    const stage = detectStage(p);
    return `<button class="inline-btn" data-go="${stage}">${stageLabel(stage)}</button>${readOnly ? readonlyTag : ""}`;
  }
  if (pageType === "projects") {
    const stage = detectStage(p);
    return `<button class="inline-btn" data-go="${stage}">${stageLabel(stage)}</button>${readOnly ? readonlyTag : ""}`;
  }
  return `<button class="inline-btn" data-open-detail="${p.id}">詳細を見る</button>`;
}

function row(p, pageType) {
  const state = stageStatus(pageType, p);
  // 請求ステージのみ invoiceStatus と捺印承認状態の複合ラベルにまとめる（他ステージは捺印の概念が無いため従来どおり）
  const combined = pageType === "invoices" ? combinedInvoiceStatusLabel(p) : null;
  const displayStatus = combined ? combined.label : state;
  const displayTone = combined ? combined.tone : tone(state);
  const openDetailAttr = ` data-open-detail="${p.id}"`;
  const titleHtml =
    pageType === "projects"
      ? `<button class="title title-link" type="button" data-open-detail="${p.id}">${escapeHtml(projectDisplayName(p))}</button>`
      : `<span class="title">${escapeHtml(projectDisplayName(p))}</span>`;
  const projectEditLink = "";
  return `
    <article class="row-item"${openDetailAttr}>
      <div class="row-main">
        <div class="row-top">
          ${titleHtml}
          <span class="status-badge ${displayTone}">${displayStatus}</span>
        </div>
        <div class="row-flow-badges">
          ${(() => {
            const su = salesOwnerUser(p);
            const au = accountingOwnerUser(p);
            const isSales = p.currentOwnerRole !== "経理";
            return `<span class="flow-badge${isSales ? " flow-badge-sales-active" : ""}">営業: ${escapeHtml(su?.name || "未設定")}</span><span class="flow-badge${!isSales ? " flow-badge-acct-active" : ""}">経理: ${escapeHtml(au?.name || "未設定")}</span>`;
          })()}
          <span class="flow-badge ${handoffTone(p.handoffStatus)}">引き継ぎ: ${escapeHtml(p.handoffStatus)}</span>
          ${billingBadgeHtml(p, pageType)}
        </div>
        <div class="row-action">${escapeHtml(nextActionText(pageType, p))}</div>
        <div class="meta">
          <span>${escapeHtml(clientName(p))}</span><span>担当: ${escapeHtml(ownerName(p))}</span><span>${yen.format(p.amount)}</span>${p.stale ? `<span>${escapeHtml(p.stale)}</span>` : ""}${p.updatedAt ? `<span>更新: ${escapeHtml(p.updatedAt)}</span>` : ""}
        </div>
        ${projectEditLink}
      </div>
      <div class="row-actions">${rowActions(p, pageType)}</div>
    </article>
  `;
}

/** Date → ローカルの YYYY-MM-DD。toISOString() は UTC 変換で JST では前日にずれる（8/31→8/30）ため使わない */
function localDateToYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function endOfMonthDate(offsetMonths = 0) {
  const now = new Date();
  return localDateToYmd(new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0));
}

function projectDrawerDraft(project) {
  const ownerId = project.assignedUserId || projectAssignCandidateUsers()[0]?.id || users[0]?.id || "";
  return {
    no: String(Number(String(project.no ?? project.id).replace(/[^\d]/g, "")) || 0),
    clientId: project.clientId || clients[0]?.id || "",
    clientPerson: project.person || "",
    clientBranchName: project.clientBranchName || "",
    projectName: project.name || "",
    ownerId,
    // 未設定は取引先マスタの支払条件（書類に載る値と同じ）。以前は選択肢に無い「月末締め翌月末」を既定にしており、
    // 他の項目だけ直して保存してもこの値が書き込まれていた
    paymentTerm: project.paymentTerm || projectDocPaymentTermsText(project),
    orderStatus: projectOrderStatusForUi(project),
    progressStatus: normalizeProjectProgressStatus(project.progressStatus),
    billingTiming: project.billingTiming || "一括請求",
    plannedInvoiceDate: project.plannedInvoiceDate || "",
    dueDate: project.dueDate || "",
    internalMemo: project.internalMemo || "",
    docDetailSetting: project.docDetailSetting || "デフォルト",
    tag: project.tag || "",
    serviceCategory: project.serviceCategory || "",
    taxIncluded: !!project.taxIncluded,
    mailTo: project.mailTo || "",
    mailCc: project.mailCc || "",
    // 以下は以前「選んだ時点で保存」だった欄。試しに変えても保存ボタンまで確定しないよう下書きに含める（2026-10 要望）
    salesOwnerId: String(project.salesOwnerId || project.assignedUserId || ""),
    uncollectible: project.invoiceStatus === "回収不能",
    workCategories: projectWorkCategoriesForEdit(project)
  };
}

function projectDetailFormDateShortcuts(field, mode = "invoice") {
  const btn = (label, value) => `<button class="dform-date-btn" type="button" data-pd-date-shortcut="${field}:${value}">${label}</button>`;
  return mode === "invoice"
    ? `${btn("今月末", "m0")}${btn("来月末", "m1")}${btn("再来月末", "m2")}`
    : `${btn("今月末", "m0")}${btn("来月末", "m1")}`;
}

/** 案件タグ（カンマ区切り）を配列へ */
function parseProjectDetailTags(s) {
  return String(s || "")
    .split(/[,、]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function persistProjectDetailExtraTagPresets() {
  try {
    localStorage.setItem(PROJECT_DETAIL_TAG_EXTRAS_STORAGE_KEY, JSON.stringify(uiState.projectDetailExtraTagPresets || []));
  } catch {
    /* noop */
  }
}

function rememberProjectDetailTagPreset(tag, opts = {}) {
  const t = String(tag ?? "").trim();
  if (!t) return;
  if (!Array.isArray(uiState.projectDetailExtraTagPresets)) uiState.projectDetailExtraTagPresets = [];
  if (uiState.projectDetailExtraTagPresets.includes(t)) return;
  uiState.projectDetailExtraTagPresets.push(t);
  uiState.projectDetailExtraTagPresets.sort((a, b) => a.localeCompare(b, "ja"));
  persistProjectDetailExtraTagPresets();
  // 全員共有: サーバーにも追記（既存案件タグの再登録などサーバー由来は除く）
  if (!opts.fromServer) void pushProjectTagPresetToServer(t);
}

/** タグ候補をサーバーへ追記（全員共有・追記専用）。失敗は無視（localStorage に残る）。 */
async function pushProjectTagPresetToServer(tag) {
  if (!authToken) return;
  try {
    const res = await fetch("/api/project-tag-presets", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ tag })
    });
    if (!res.ok) console.warn("pushProjectTagPresetToServer failed: HTTP", res.status);
  } catch {
    /* オフライン等は localStorage に残るため無視 */
  }
}

/** GET /api/project-tag-presets の応答を候補へ合算（サーバー由来なので再POSTしない） */
function applyProjectTagPresetsPayload(payload) {
  const tags = payload && Array.isArray(payload.tags) ? payload.tags : [];
  for (const t of tags) rememberProjectDetailTagPreset(t, { fromServer: true });
}

/** タグプルダウン用: 案件・クライアント・手動登録タグを候補に（重複除去・五十音順） */
function collectProjectDetailTagPresetOptions(f) {
  const set = new Set();
  const add = (raw) => {
    parseProjectDetailTags(raw).forEach((t) => set.add(t));
  };
  projects.forEach((p) => add(p.tag));
  clients.forEach((c) => add(c.tags));
  add(f.tag);
  for (const t of uiState.projectDetailExtraTagPresets || []) set.add(t);
  return [...set].sort((a, b) => a.localeCompare(b, "ja"));
}

/** プロジェクト一覧のタグ絞り込み用候補（案件・顧客・手動プリセット） */
function collectProjectListTagFilterOptions() {
  const set = new Set();
  const add = (raw) => {
    parseProjectDetailTags(raw).forEach((t) => set.add(t));
  };
  projects.forEach((p) => add(p.tag));
  clients.forEach((c) => add(c.tags));
  for (const t of uiState.projectDetailExtraTagPresets || []) {
    const s = String(t ?? "").trim();
    if (s) set.add(s);
  }
  return [...set].sort((a, b) => a.localeCompare(b, "ja"));
}

/**
 * 案件詳細の「顧客支社名」「顧客担当者名」は取引先一覧のマスタ（支社／担当者タブ）から選ぶ。
 * 手入力は不可。案件のクライアントに紐づく有効な行だけを候補にし、マスタ未取得なら遅延ロードを促す。
 * 既存データがマスタに無い場合は現在値を選択肢として残す（保存済みの値を勝手に消さない）。
 * 未選択（空欄）のまま運用してよい仕様なので、候補ゼロでも登録を促す文言は出さず「（未選択）」で揃える。
 */
function projectClientMasterSelectHtml(field, currentValue, project, kind) {
  const cur = String(currentValue || "").trim();
  const clientId = project ? project.clientId : "";
  const rows = kind === "支社"
    ? (ensureClientBranchesLoaded(), uiState.clientBranches.rows)
    : (ensureClientContactsLoaded(), uiState.clientContacts.rows);
  if (!Array.isArray(rows)) {
    return `<select class="dform-select" data-search-dropdown-off="1" disabled aria-label="顧客${kind}"><option>読み込み中…</option></select>`;
  }
  const names = rows
    .filter((r) => !r.archived && r.clientId === clientId)
    .map((r) => (kind === "支社" ? String(r.name || "").trim() : clientContactDisplayName(r)))
    .filter(Boolean);
  const opts = [...new Set(cur ? [cur, ...names] : names)];
  // ＋ボタン: この画面上でマスタ（支社／担当者）を登録し、登録後にそのままこの欄へ選択する
  // （15g-client-branches.js / 15e-client-contacts.js の data-pd-add-client-* が処理）
  const addAttr = kind === "支社" ? "data-pd-add-client-branch" : "data-pd-add-client-contact";
  const addBtn = clientId
    ? `<button type="button" class="inline-btn ghost pd-master-add-btn" ${addAttr}="${escapeHtmlAttr(clientId)}" title="${kind}をこの画面から登録" aria-label="${kind}を登録">＋</button>`
    : "";
  return `
    <div class="pd-master-select-row">
    <select class="dform-select" data-search-dropdown-off="1" data-pd-field="${field}" aria-label="顧客${kind}">
      <option value="" ${cur ? "" : "selected"}>（未選択）</option>
      ${opts.map((v) => `<option value="${escapeHtmlAttr(v)}" ${v === cur ? "selected" : ""}>${escapeHtml(v)}</option>`).join("")}
    </select>
    ${addBtn}
    </div>`;
}

/** 案件詳細・概要（NDA等の下）：識別情報＋営業・受注を同一カード内にまとめる */
function buildProjectDetailOverviewMetaCardHtml(f, p, salesPickId, orderStatusOptions, currentOrderStatus) {
  if (!f) return "";
  const noDisp = escapeHtml(String(f.no ?? ""));
  const overviewClientName = escapeHtml(clientName(p));
  const overviewClientKeyword = escapeHtmlAttr(clientName(p));
  const salesPool = projectAssignCandidateUsers();
  const salesPoolIds = new Set(salesPool.map((u) => String(u.id)));
  const salesPickStr = salesPickId != null ? String(salesPickId) : "";
  const salesExtra =
    salesPickStr && !salesPoolIds.has(salesPickStr) ? getUserById(salesPickId) : null;
  const salesOptionsHtml =
    salesPool
      .map(
        (u) =>
          `<option value="${escapeHtmlAttr(u.id)}" ${salesPickId === u.id ? "selected" : ""}>${escapeHtml(u.name)}</option>`
      )
      .join("") +
    (salesExtra
      ? `<option value="${escapeHtmlAttr(String(salesExtra.id))}" selected>${escapeHtml(userNameWithRetiredLabel(salesExtra))}</option>`
      : "");
  return `
    <section class="section" style="padding-top:0;padding-bottom:0;">
      <div class="soft-card pd-overview-meta-card" style="padding:16px 20px;">
        <div class="pd-overview-identity-row">
          <div class="pd-overview-identity-cell pd-overview-identity-cell--no">
            <div class="pd-overview-identity-label-row">
              <span class="pd-overview-identity-label-main">案件No</span>
            </div>
            <input class="dform-input" value="${noDisp}" disabled aria-label="案件No" />
          </div>
          <div class="pd-overview-identity-cell pd-overview-identity-cell--client">
            <div class="pd-overview-identity-label-row">
              <span class="pd-overview-identity-label-main">クライアント</span>
            </div>
            <div class="pd-overview-client-name">
              <button type="button" class="pd-overview-client-link" data-go-client-list-keyword="${overviewClientKeyword}">${overviewClientName}</button>
            </div>
          </div>
          <div class="pd-overview-identity-cell pd-overview-identity-cell--branch">
            <div class="pd-overview-identity-label-row">
              <span class="pd-overview-identity-label-main">顧客支社名</span>
            </div>
            ${projectClientMasterSelectHtml("clientBranchName", f.clientBranchName, p, "支社")}
          </div>
          <div class="pd-overview-identity-cell pd-overview-identity-cell--person">
            <div class="pd-overview-identity-label-row">
              <span class="pd-overview-identity-label-main">顧客担当者名</span>
            </div>
            ${projectClientMasterSelectHtml("clientPerson", f.clientPerson, p, "担当者")}
          </div>
        </div>
        <div class="pd-overview-meta-divider"></div>
        <div class="pd-overview-meta-secondary">
          <div class="pd-overview-sales-row">
            <label class="pd-overview-sales-label">
              <span>営業:</span>
              <select class="dform-select pd-overview-sales-select" data-search-dropdown-off="1" data-project-sales-owner="${escapeHtmlAttr(p.id)}">
                ${salesOptionsHtml}
              </select>
            </label>
          </div>
          ${buildProjectWorkCategoriesHtml(p, f.workCategories)}
          <div class="pd-overview-status-stack">
            <div class="pd-overview-order-row">
              <span class="pd-overview-order-heading">受注ステータス:</span>
              ${orderStatusOptions.map((s) => `
              <label class="pd-overview-order-check">
                <input type="checkbox" data-project-order-status="${s}" data-project-order-status-id="${p.id}" ${currentOrderStatus === s ? "checked" : ""} />
                ${s}
              </label>
            `).join("")}
            </div>
            <div class="pd-overview-order-row">
              <span class="pd-overview-order-heading">入金:</span>
              <label class="pd-overview-order-check" title="原価計上専用のダミー案件など、入金の見込みがない場合に立てます">
                <input type="checkbox" data-project-uncollectible-id="${p.id}" ${f.uncollectible ? "checked" : ""} />
                回収不能（入金見込みなし）
              </label>
            </div>
          </div>
        </div>
      </div>
    </section>
  `;
}

function buildProjectDetailFormResultFieldsHtml(f, p) {
  const tagsList = parseProjectDetailTags(f.tag);
  const tagPresetOpts = collectProjectDetailTagPresetOptions(f).filter((t) => !tagsList.includes(t));
  const selectedPaymentTerm = String(f.paymentTerm || "").trim();
  const paymentTermOptions = [...PAYMENT_TERMS_OPTIONS];
  if (selectedPaymentTerm && !paymentTermOptions.includes(selectedPaymentTerm)) {
    // 既存案件の過去値は選択肢先頭で保持して編集時の消失を防ぐ
    paymentTermOptions.unshift(selectedPaymentTerm);
  }
  const chipsHtml =
    tagsList.length === 0
      ? `<span class="dform-tag-empty">未設定</span>`
      : tagsList
          .map(
            (t, i) =>
              `<span class="dform-tag-chip">${escapeHtml(t)}<button type="button" class="dform-tag-chip-remove" data-pd-tag-remove="${i}" aria-label="削除">×</button></span>`
          )
          .join("");
  // 末尾の「＋新しいタグを作成…」を選ぶとモーダルが開く（change ハンドラは 15l-project-detail-tags.js）。
  // __new__ はタグ名として使えない内部値
  const tagPresetSelect = `<select class="dform-select dform-tag-preset" data-search-dropdown-off="1" data-pd-tag-preset><option value="">タグを選択</option>${tagPresetOpts
    .map((t) => `<option value="${escapeHtmlAttr(t)}">${escapeHtml(t)}</option>`)
    .join("")}<option value="__new__">＋ 新しいタグを作成…</option></select>`;
  // 「請求・納期」1見出しに全項目がぶら下がって見づらい、という指摘によりサブタブで区画分け。
  // どのタブで編集しても値は draft（uiState.projectDetailForm）に持つので、タブを切り替えても消えない
  const activeSec = ["billing", "mail", "class", "split", "memo"].includes(uiState.projectDetailFormSection)
    ? uiState.projectDetailFormSection
    : "billing";
  const secTab = (key, label) =>
    `<button type="button" class="pd-form-tab${activeSec === key ? " active" : ""}" data-pd-form-tab="${key}" role="tab" aria-selected="${activeSec === key ? "true" : "false"}">${label}</button>`;
  const sectionBilling = `
    <div class="dform-row dform-row--grid">
      <div class="dform-label">支払条件 <span class="dform-req">必須</span></div>
      <div class="dform-val"><select class="dform-select" data-search-dropdown-off="1" data-pd-field="paymentTerm">${paymentTermOptions
        .map((v) => `<option ${selectedPaymentTerm === v ? "selected" : ""}>${escapeHtml(v)}</option>`)
        .join("")}</select></div>
    </div>
    <div class="dform-row dform-row--grid dform-row--valign-center">
      <div class="dform-label">請求タイミング <span class="dform-req">必須</span></div>
      <div class="dform-val" style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;"><div class="dform-radio-group">${(() => {
        // 「毎月請求」= recurring_billing フラグ（請求書画面の毎月請求チェックと同じ値）。
        // 旧値「定期請求」「分割請求」は選択肢から廃止（billingTiming は未持続のドラフト値のため移行不要・非該当は一括請求へ寄せる）
        const timingChecked = (s) => (p.recurringBilling === true ? s === "毎月請求" : s === "一括請求");
        return ["一括請求", "毎月請求"].map((s) => `<label><input type="radio" name="pd-billing-timing" value="${s}" ${timingChecked(s) ? "checked" : ""} data-pd-field="billingTiming" />${s}</label>`).join("");
      })()}</div><span style="font-size:12px;color:var(--fp-text-subtle);">※ 毎月請求にすると、案件一覧の「自動作成設定」（毎月指定日に自動複製）や Slack への指示で、翌月分の請求書（案件）がまとめて作成されます</span></div>
    </div>
    <div class="dform-row dform-row--grid">
      <div class="dform-label">請求(予定)日 <span class="dform-req">必須</span></div>
      <div class="dform-val"><div class="dform-date-row"><input class="dform-input" type="date" data-pd-field="plannedInvoiceDate" value="${escapeHtmlAttr(f.plannedInvoiceDate)}" />${projectDetailFormDateShortcuts("plannedInvoiceDate", "invoice")}</div></div>
    </div>
    <div class="dform-row dform-row--grid">
      <div class="dform-label">納期</div>
      <div class="dform-val"><div class="dform-date-row"><input class="dform-input" type="date" data-pd-field="dueDate" value="${escapeHtmlAttr(f.dueDate)}" />${projectDetailFormDateShortcuts("dueDate", "due")}</div></div>
    </div>
    <div class="dform-row dform-row--grid dform-row--valign-center">
      <div class="dform-label">税額表示</div>
      <div class="dform-val"><label class="dform-tax-inline"><input type="checkbox" data-pd-check="taxIncluded" ${f.taxIncluded ? "checked" : ""} /><span>税込表示</span></label></div>
    </div>
  `;
  const sectionMail = `
    <div class="dform-row dform-row--grid">
      <div class="dform-label">送信先メール</div>
      <div class="dform-val"><input class="dform-input" type="text" inputmode="email" data-pd-field="mailTo" value="${escapeHtmlAttr(f.mailTo || "")}" placeholder="請求書などのメール送信の宛先（未入力ならクライアントのメールアドレス）" /></div>
    </div>
    <div class="dform-row dform-row--grid">
      <div class="dform-label">CCメール</div>
      <div class="dform-val"><input class="dform-input" type="text" inputmode="email" data-pd-field="mailCc" value="${escapeHtmlAttr(f.mailCc || "")}" placeholder="CCの宛先（複数はカンマ区切り）" /></div>
    </div>
  `;
  // 旧「業務カテゴリ」欄は、基本情報のカテゴリ（媒体／制作…）と役目が重なるため撤去した。
  // 保存済みの値はドラフト経由でそのまま送り返すので消えない（見積シミュレータの集計が既存値を使う）
  const sectionClass = `
    <div class="dform-row dform-row--grid dform-row--tags-block" data-pd-tags-wrap="1">
      <div class="dform-label">タグ</div>
      <div class="dform-val">
        <div class="dform-tags-chips">${chipsHtml}</div>
        <div class="dform-tags-toolbar">
          <div class="dform-tags-main-row">
            ${tagPresetSelect}
            <button type="button" class="inline-btn ghost" data-pd-tag-add-selected="1">追加</button>
          </div>
        </div>
      </div>
    </div>
  `;
  const sectionMemo = `
    <div class="dform-row dform-row--grid">
      <div class="dform-label">社内メモ</div>
      <div class="dform-val"><textarea class="dform-input" rows="4" data-pd-field="internalMemo">${escapeHtml(f.internalMemo)}</textarea></div>
    </div>
    ${buildProjectDetailPaymentRowsHtml(p)}
  `;
  // 「売上分配」タブは営業（売上分配）と非生産（貢献度）の2軸を軸見出しつきで並べる
  // （組み立ては 15g-sales-split.js の buildProjectSplitEditorsHtml に集約）。
  // 開いている時だけ組み立てる（候補の絞り込みで予算マスタを取りに行くため、
  // 分配タブを見ない利用者に余計な取得と再描画を起こさない）
  const activeBody =
    activeSec === "mail" ? sectionMail
    : activeSec === "class" ? sectionClass
    : activeSec === "split" ? buildProjectSplitEditorsHtml(p)
    : activeSec === "memo" ? sectionMemo
    : sectionBilling;
  return `
    <div class="pd-form-tabs" role="tablist" aria-label="基本情報の入力区分">
      ${secTab("billing", "請求・納期")}
      ${secTab("mail", "メール送付先")}
      ${secTab("class", "タグ")}
      ${secTab("split", "関連担当")}
      ${secTab("memo", "メモ・入金")}
    </div>
    ${activeBody}
  `;
}

/** 基本情報・請求・納期欄の末尾: 入金日（経理のみ編集可・即時保存）＋入金対応者（自動記録・表示のみ） */
function buildProjectDetailPaymentRowsHtml(p) {
  if (!p) return "";
  const payDate = String(p.paymentRecord?.payDate || "").slice(0, 10);
  const confirmedUser = p.paymentConfirmedBy ? getUserById(p.paymentConfirmedBy) : null;
  const confirmedName = confirmedUser ? userNameWithRetiredLabel(confirmedUser) : "";
  const payDateCell = canConfirmPayment(p)
    ? `<div class="dform-date-row"><input class="dform-input" type="date" data-pd-payment-date="${escapeHtmlAttr(p.id)}" value="${escapeHtmlAttr(payDate)}" /><span style="font-size:12px;color:var(--fp-text-subtle);">日付を入れると入金済み・クリアすると未入金に戻ります</span></div>`
    : `<span>${payDate ? escapeHtml(payDate) : "未入金"}</span>`;
  return `
    <div class="dform-row dform-row--grid">
      <div class="dform-label">入金日</div>
      <div class="dform-val">${payDateCell}</div>
    </div>
    <div class="dform-row dform-row--grid dform-row--valign-center">
      <div class="dform-label">入金対応者</div>
      <div class="dform-val" style="display:flex;align-items:center;gap:8px;"><span style="font-size:13px;">${confirmedName ? escapeHtml(confirmedName) : "—"}</span><span style="font-size:12px;color:var(--fp-text-subtle);">入金確認の記録時に自動で残ります</span></div>
    </div>
  `;
}

function ensureProjectDetailFormDraft(p) {
  if (!p) return null;
  if (!uiState.projectDetailForm || uiState.projectDetailForm.projectId !== p.id) {
    // base = 下書きを作った（最後に取り込んだ）時点の保存済みの値。触っていない欄の判定に使う
    uiState.projectDetailForm = { projectId: p.id, draft: projectDrawerDraft(p), base: projectDrawerDraft(p) };
    // 別案件を開いたらサブタブは先頭（請求・納期）へ戻す
    uiState.projectDetailFormSection = "billing";
  } else {
    rebaseProjectDetailFormDraft(p);
  }
  return uiState.projectDetailForm.draft;
}

/** 下書きのうち利用者が触っていない欄を、保存済みの最新値に追従させる。
 *  下書きは同じ案件なら画面を離れても残るため、一覧の一括変更・注文書の送付済み・再読込などで
 *  案件の値が変わると、触っていない欄に古い値が残り、保存のたびにそれを書き戻してしまう */
function rebaseProjectDetailFormDraft(p) {
  const form = uiState.projectDetailForm;
  const cur = projectDrawerDraft(p);
  if (form.base) {
    Object.keys(cur).forEach((k) => {
      if (projectDraftValueKey(form.draft[k]) === projectDraftValueKey(form.base[k])) form.draft[k] = cur[k];
    });
  }
  form.base = cur;
}

function projectDetailFormDraftActive() {
  const r = currentRoute();
  if (r.page !== "project-detail" || !r.id) return null;
  const d = uiState.projectDetailForm;
  if (!d || String(d.projectId) !== String(r.id)) return null;
  return d.draft;
}

/** applyProjectFormDraftToProject が書き換える案件の項目（保存失敗時に送信前へ戻す範囲） */
const PROJECT_DRAFT_APPLIED_KEYS = [
  "clientId", "person", "clientBranchName", "name", "assignedUserId", "paymentTerm", "billingTiming",
  "plannedInvoiceDate", "dueDate", "billingDate", "internalMemo", "docDetailSetting", "tag", "serviceCategory",
  "taxIncluded", "mailTo", "mailCc", "orderStatus", "progressStatus", "salesOwnerId", "invoiceStatus",
  "invoiceStatusBeforeUncollectible", "workCategories", "nonSalesSplit", "updatedAt"
];

function applyProjectFormDraftToProject(p, f) {
  // 営業の比較は担当(assignedUserId)を書き換える前の値で行う
  const savedSalesId = String(p.salesOwnerId || p.assignedUserId || "");
  p.clientId = f.clientId;
  p.person = f.clientPerson;
  p.clientBranchName = f.clientBranchName;
  p.name = f.projectName || p.name;
  p.assignedUserId = f.ownerId;
  p.paymentTerm = f.paymentTerm;
  p.billingTiming = f.billingTiming;
  p.plannedInvoiceDate = f.plannedInvoiceDate;
  p.dueDate = f.dueDate;
  p.billingDate = isoYmdToSlashDisplay(p.plannedInvoiceDate) || "";
  p.internalMemo = f.internalMemo;
  p.docDetailSetting = f.docDetailSetting;
  p.tag = f.tag;
  p.serviceCategory = f.serviceCategory;
  p.taxIncluded = f.taxIncluded;
  p.mailTo = f.mailTo;
  p.mailCc = f.mailCc;
  // 受注ステータス・営業・回収不能・カテゴリは、変えたときだけ反映する（読込時の値をそのまま書き戻して
  // 旧表記の受注ステータスや未設定の営業を、保存のたびに別の値へ置き換えないように）
  if (f.orderStatus && f.orderStatus !== projectOrderStatusForUi(p)) p.orderStatus = f.orderStatus;
  if (f.progressStatus && f.progressStatus !== normalizeProjectProgressStatus(p.progressStatus)) p.progressStatus = f.progressStatus;
  if (f.salesOwnerId && f.salesOwnerId !== savedSalesId) p.salesOwnerId = f.salesOwnerId;
  const wasUncollectible = p.invoiceStatus === "回収不能";
  if (f.uncollectible && !wasUncollectible) {
    // 外したときに戻す先（保存列が無いので画面を開いている間だけ覚える）
    p.invoiceStatusBeforeUncollectible = p.invoiceStatus || "";
    p.invoiceStatus = "回収不能";
  } else if (f.uncollectible === false && wasUncollectible) {
    const before = p.invoiceStatusBeforeUncollectible;
    p.invoiceStatus = before && before !== "回収不能" ? before : "未作成";
  }
  if (f.workCategories && JSON.stringify(f.workCategories) !== JSON.stringify(projectWorkCategoriesForEdit(p))) {
    p.workCategories = { categories: [...f.workCategories.categories], members: [...f.workCategories.members] };
    // 旧「その他（貢献度）」の担当者は編集の初期値に写しているので、同じ保存で旧列を空にする
    // （残すと担当者を全員外しても再描画で旧担当者が戻る）
    if (Array.isArray(p.nonSalesSplit) && p.nonSalesSplit.length > 0) p.nonSalesSplit = [];
  }
  p.updatedAt = localDateToYmd(new Date()); // toISOString は UTC で JST の0〜9時に前日になる
}

/** DB / API の日付値を YYYY-MM-DD に正規化 */
function pgDateFieldToYmd(v) {
  if (v == null || v === "") return "";
  const s = String(v);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  return s.length >= 10 ? s.slice(0, 10) : "";
}

/** ISO YYYY-MM-DD → 一覧の請求日表示（YYYY/MM/DD） */
function isoYmdToSlashDisplay(iso) {
  const m = String(iso || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[1]}/${m[2]}/${m[3]}`;
}

/** ISO YYYY-MM-DD → 書類見出し用「YYYY年M月D日」 */
function isoYmdToJapaneseCalendar(iso) {
  const m = String(iso || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${Number(m[1])}年${Number(m[2])}月${Number(m[3])}日`;
}

/** 書類タブに応じた発行日／請求日の見出し（案件の請求予定日・納期を優先） */
function docSheetJapaneseDateFromProject(p, activeTab, fallbackJa) {
  if (activeTab === "invoice" || activeTab === "receipt") {
    const j = isoYmdToJapaneseCalendar(p.plannedInvoiceDate);
    if (j) return j;
  }
  if (activeTab === "order" || activeTab === "delivery" || activeTab === "inspection") {
    const j = isoYmdToJapaneseCalendar(p.dueDate);
    if (j) return j;
  }
  return fallbackJa;
}

function navigateToProjectDetail(projectId, tab = "billing") {
  const t =
    tab === "cost"
      ? "cost"
      : tab === "billing"
        ? "billing"
        : tab === "comments"
          ? "comments"
          : tab === "result"
            ? "overview"
            : tab === "overview"
              ? "overview"
              : "billing";
  const q = t === "billing" ? "" : `?tab=${t}`;
  location.hash = `#project-detail:${projectId}${q}`;
}

/** 旧ドロワーは廃止（詳細ページタブに統合） */
function renderProjectDrawer() {
  return "";
}

/** ダッシュボード用：本日を含む会計年度の開始年（4月始まり） */
function dashboardFiscalYearStart() {
  const d = new Date();
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  return m >= 4 ? y : y - 1;
}

/** 0=4月 … 11=3月 */
function dashboardFiscalMonthIndexNow() {
  const m = new Date().getMonth() + 1;
  return m >= 4 ? m - 4 : m + 8;
}

function parseProjectBillingMonthYyyymm(billingMonth) {
  const m = String(billingMonth || "").trim().match(/^(\d{4})[\/-](\d{1,2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12) return null;
  return y * 100 + mo;
}

/** プロジェクト一覧フィルタ用: 保存値を input type=month の value（YYYY-MM）へ */
function billingMonthFilterToMonthInputValue(stored) {
  const s = String(stored || "").trim();
  if (!s) return "";
  const n = parseProjectBillingMonthYyyymm(s);
  if (n == null) return "";
  const y = Math.floor(n / 100);
  const mo = n % 100;
  return `${y}-${String(mo).padStart(2, "0")}`;
}

/** input type=month の値 → 保存・表示の YYYY/MM */
function billingMonthStoredFromMonthInput(monthInputVal) {
  const s = String(monthInputVal || "").trim();
  if (!s) return "";
  const n = parseProjectBillingMonthYyyymm(s.replace(/-/g, "/"));
  if (n == null) return "";
  const y = Math.floor(n / 100);
  const mo = n % 100;
  return `${y}/${String(mo).padStart(2, "0")}`;
}

/** 原価ウィザードの登録対象月（YYYY/MM／YYYY-MM 混在を YYYY/MM 表示へ） */
function costTargetMonthSlashDisplay(tm) {
  const s = String(tm || "").trim();
  if (!s) return "―";
  const m = s.match(/^(\d{4})[-/](\d{1,2})$/);
  if (m) return `${m[1]}/${String(Number(m[2])).padStart(2, "0")}`;
  return s;
}

function fiscalYearStartFromBillingYyyymm(yyyymm) {
  const mo = yyyymm % 100;
  const y = Math.floor(yyyymm / 100);
  return mo >= 4 ? y : y - 1;
}

function fiscalMonthIndexFromBillingYyyymm(yyyymm) {
  const mo = yyyymm % 100;
  if (mo >= 4) return mo - 4;
  return mo + 8;
}

/** 会計年度（4月始まりの年＝fyStart）における暦 (y, month) の予算月インデックス 0〜11。年度に合わない場合は null */
function fiscalCalendarMonthToBudgetIndex(fyStart, y, mo) {
  if (!Number.isFinite(fyStart) || !Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12) return null;
  const expectY = mo >= 4 ? fyStart : fyStart + 1;
  if (y !== expectY) return null;
  return mo >= 4 ? mo - 4 : mo + 8;
}

function projectIncludedInDashboardSalesView(p, personalOnly) {
  if (!personalOnly) return true;
  const uid = currentUser?.id;
  if (!uid) return false;
  if (salesOwnerUser(p)?.id === uid) return true;
  if (p.assignedUserId === uid) return true;
  return false;
}

/** 原価1件の消費税額（円） */
function costConsumptionTaxYen(c) {
  const t = Number(c?.consumptionTax ?? 0);
  return Number.isFinite(t) ? t : 0;
}

/** 原価の税込支払額相当（税抜＋消費税）。集計・粗利で使用 */
function costTotalRegisteredYen(c) {
  const ex = Number(c?.amount || 0);
  return ex + costConsumptionTaxYen(c);
}

/** 案件に紐づく登録原価合計（円・税抜）。予算進捗の差益は税抜原価基準（予算表Excelと同基準）。
 *  実原価が来た支払先×計上月の仮原価は外す（案件詳細・経営ダッシュボードの countedProjectCosts と同じ基準） */
function totalProjectRegisteredCostExTaxYen(p) {
  return countedProjectCosts(p?.costs).reduce((sum, c) => sum + Number(c?.amount || 0), 0);
}

/** 原価登録の検索フィルターの空形（キー一覧の単一定義元） */
function emptyCostMgmtFilter() {
  return {
    keyword: "", vendor: "", payStatus: "", paymentMethod: "", lock: "", vendorType: "",
    costKind: "",
    invFrom: "", invTo: "", payFrom: "", payTo: "", tmFrom: "", tmTo: ""
  };
}

/** 原価管理ページの検索バー入力を `uiState.costMgmtFilter` に反映（DOM が無いときは既存値を維持） */
function syncCostMgmtFilterFromDom() {
  const prev = uiState.costMgmtFilter || emptyCostMgmtFilter();
  const inputVal = (sel, fallback) => {
    const el = document.querySelector(sel);
    return el instanceof HTMLInputElement ? el.value.trim() : fallback;
  };
  const selectVal = (sel, fallback) => {
    const el = document.querySelector(sel);
    return el instanceof HTMLSelectElement ? el.value : fallback;
  };
  const next = {
    keyword: inputVal("[data-cost-mgmt-filter-keyword]", prev.keyword || ""),
    vendor: selectVal("[data-cost-mgmt-filter-vendor]", prev.vendor || ""),
    payStatus: prev.payStatus || "", // 支払ステータスは件数つきの切替ボタン（15j）で直接 state を変える
    paymentMethod: selectVal("[data-cost-mgmt-filter-pay-method]", prev.paymentMethod || ""),
    lock: selectVal("[data-cost-mgmt-filter-lock]", prev.lock || ""),
    vendorType: selectVal("[data-cost-mgmt-filter-vendor-type]", prev.vendorType || ""),
    costKind: selectVal("[data-cost-mgmt-filter-cost-kind]", prev.costKind || ""),
    invFrom: inputVal("[data-cost-mgmt-filter-inv-from]", prev.invFrom || ""),
    invTo: inputVal("[data-cost-mgmt-filter-inv-to]", prev.invTo || ""),
    payFrom: inputVal("[data-cost-mgmt-filter-pay-from]", prev.payFrom || ""),
    payTo: inputVal("[data-cost-mgmt-filter-pay-to]", prev.payTo || ""),
    tmFrom: inputVal("[data-cost-mgmt-filter-tm-from]", prev.tmFrom || ""),
    tmTo: inputVal("[data-cost-mgmt-filter-tm-to]", prev.tmTo || "")
  };
  // 絞り込み条件が変わったら1ページ目へ（前の位置に留まると空ページに見えることがある）
  if (JSON.stringify(next) !== JSON.stringify(prev) && uiState.costMgmtTable) uiState.costMgmtTable.page = 1;
  uiState.costMgmtFilter = next;
}

/** DATE 値（'YYYY-MM-DD' or ISO文字列）→ 'YYYY-MM'。空・不正は '' */
function costDateToMonthKey(v) {
  const m = String(v || "").match(/^(\d{4}-\d{2})/);
  return m ? m[1] : "";
}

/** 'YYYY-MM' キーが from〜to（いずれも 'YYYY-MM'・空は無制限）に入るか。逆転指定は入れ替えて解釈 */
function costMonthKeyInRange(key, from, to) {
  if (!from && !to) return true;
  if (!key) return false;
  let lo = from || "";
  let hi = to || "";
  if (lo && hi && lo > hi) {
    const t = lo; lo = hi; hi = t;
  }
  if (lo && key < lo) return false;
  if (hi && key > hi) return false;
  return true;
}

function flattenCostRowMatchesMgmtFilter(row, filt, ctx) {
  const f = filt || emptyCostMgmtFilter();
  const kw = String(f.keyword ?? "").trim().toLowerCase();
  if (kw) {
    const blob = `${row._projectNo || ""} ${row._projectName || ""} ${row.category || ""} ${row.vendor || ""} ${row.note || ""} ${row.invoiceNo || ""}`.toLowerCase();
    if (!blob.includes(kw)) return false;
  }
  const vendorSel = String(f.vendor ?? "").trim();
  if (vendorSel && String(row.vendor || "").trim() !== vendorSel) return false;
  const paySel = String(f.payStatus ?? "").trim();
  if (paySel && costPayStatusDisplayLabel(row) !== paySel) return false;
  const methodSel = String(f.paymentMethod ?? "").trim();
  if (methodSel) {
    const rowMethod = String(row.paymentMethod || "").trim();
    if (methodSel === "未設定" ? rowMethod !== "" : rowMethod !== methodSel) return false;
  }
  // 原価区分: 未割当のみ（割当依頼中も未割当と同義・販管費/調整用バケットは対象外）／仮原価のみ／販管費のみ
  const kindSel = String(f.costKind ?? "").trim();
  if (kindSel === "unassigned" && !isCostRowProjectUnassigned(row)) return false;
  if (kindSel === "provisional" && !row.isProvisional) return false;
  if (kindSel === "salesExpense" && !isSalesExpenseCostRow(row)) return false;
  // 支払先区分: マスタの敬称（様=個人）と支払先名を突合。マスタ未登録は法人・その他扱い
  const vtSel = String(f.vendorType ?? "").trim();
  if (vtSel && ctx?.indivSet) {
    const isIndiv = costVendorIsIndividual(row.vendor, ctx.indivSet);
    if (vtSel === "individual" && !isIndiv) return false;
    if (vtSel === "corporate" && isIndiv) return false;
  }
  // ロック: 計上月が月次ロック済み（経理の手動ロック）かどうか
  const lockSel = String(f.lock ?? "").trim();
  if (lockSel) {
    const locked = isMonthLocked(row.targetMonth);
    if (lockSel === "locked" && !locked) return false;
    if (lockSel === "unlocked" && locked) return false;
  }
  // 請求月＝請求日の月／支払月＝支払期限の月／計上月＝targetMonth。いずれも範囲（from〜to）
  if (!costMonthKeyInRange(costDateToMonthKey(row.invoiceDate), f.invFrom, f.invTo)) return false;
  if (!costMonthKeyInRange(costDateToMonthKey(row.dueDate), f.payFrom, f.payTo)) return false;
  const tmKey = String(row.targetMonth || "").trim().replace(/\//g, "-").replace(/^(\d{4})-(\d)$/, "$1-0$2");
  if (!costMonthKeyInRange(tmKey, f.tmFrom, f.tmTo)) return false;
  return true;
}

/** 会計期オブジェクト → その期の年度開始年（4月始まりの年）。rangeLabel「2026/04～…」または N 期から推定 */
function fiscalYearStartFromBudgetPeriod(period) {
  if (!period) return dashboardFiscalYearStart();
  const rangeText = String(period.rangeLabel || "");
  const rangeMatch = rangeText.match(/(\d{4})\s*[\/／]\s*(\d{1,2})/);
  if (rangeMatch) {
    const y = Number(rangeMatch[1]);
    const mo = Number(rangeMatch[2]);
    if (Number.isFinite(y) && Number.isFinite(mo)) return mo >= 4 ? y : y - 1;
  }
  const pn = Number(period.periodNumber) || parseBudgetFiscalPeriodNumberFromLabel(period.label);
  if (Number.isFinite(pn) && pn > 0) return 2013 + pn;
  return dashboardFiscalYearStart();
}

/** 予算マスタ行の担当者名と案件の帰属先を突き合わせる（表記ゆれのみ吸収） */
function budgetProgressMemberKeyForProject(p) {
  const u = salesOwnerUser(p);
  if (u) return userNameWithRetiredLabel(u);
  const au = p.assignedUserId ? getUserById(p.assignedUserId) : null;
  if (au) return userNameWithRetiredLabel(au);
  return String(p.owner || "").trim();
}

function budgetMasterMemberNameMatchesProject(projectKey, rowMemberName) {
  const a = String(projectKey || "").trim();
  const b = String(rowMemberName || "").trim();
  if (!a || !b) return false;
  if (a === b) return true;
  return a.replace(/\s/g, "") === b.replace(/\s/g, "");
}

/** 請求月が会計年度内の案件について、売上−登録原価（差益）を月別集計（予算進捗「推移」タブ）。
 *  注意: personalOnly は売上分配(salesSplit)を按分しない（現状呼び出しは全体=false のみ）。
 *  個人フィルタを配線する際は budgetProgressSalesSharesForProject 経由に変えること */
function buildDashboardActualAmountsByMonth(fyStart, personalOnly) {
  const arr = Array(12).fill(0);
  for (const p of projects) {
    if (!projectIncludedInDashboardSalesView(p, personalOnly)) continue;
    const yyyymm = parseProjectBillingMonthYyyymm(p.billingMonth);
    if (yyyymm == null || fiscalYearStartFromBillingYyyymm(yyyymm) !== fyStart) continue;
    const idx = fiscalMonthIndexFromBillingYyyymm(yyyymm);
    // 売上・原価とも予算進捗の表タブ（buildBudgetProgressGroupsForSelectedPeriod）と同基準＝税抜
    const salesAmt = projectDisplayedBillingExTaxYen(p);
    arr[idx] += salesAmt - totalProjectRegisteredCostExTaxYen(p);
  }
  return arr;
}

/** 予算マスタ（選択中会計期）の月次を集計。営業個人表示時は自分行のみ（行の自動シードはしない） */
function buildDashboardBudgetAmountsByMonth(personalOnly) {
  const arr = Array(12).fill(0);
  ensureBudgetPeriodsState();
  const period =
    uiState.budgetPeriods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(uiState.budgetPeriods);
  if (!period?.id) return arr;
  if (!budgetRowsLoadedForPeriod(period.id)) {
    void loadBudgetRowsForPeriod(period.id);
    return arr;
  }
  if (!uiState.budgetRowsByPeriod || typeof uiState.budgetRowsByPeriod !== "object") {
    uiState.budgetRowsByPeriod = readBudgetDraftStore();
  }
  const pkg = uiState.budgetRowsByPeriod[period.id];
  let rows = Array.isArray(pkg?.rows) ? pkg.rows.map(normalizeBudgetRow) : [];
  if (personalOnly) {
    const me = String(currentUser?.name || "").trim();
    rows = me ? rows.filter((r) => String(r.member || "").trim() === me) : [];
  }
  for (const row of rows) {
    BUDGET_MONTH_KEYS.forEach((k, i) => {
      arr[i] += parseBudgetNumberInput(row[k]);
    });
  }
  return arr;
}

/** 棒グラフ用の高さ係数（百万円単位、上限あり） */
function dashboardBarHeightFromYen(amountYen) {
  const mil = Number(amountYen || 0) / 1000000;
  if (!mil) return 0;
  return Math.min(12, Math.max(0.35, mil));
}

function dashboardRatePercent(actual, budget) {
  const a = Number(actual || 0);
  const b = Number(budget || 0);
  if (b <= 0) return null;
  return Math.max(0, Math.round((a / b) * 100));
}

function dashboardRateToneClass(ratePct) {
  if (ratePct == null) return "db-rate--none";
  if (ratePct >= 100) return "db-rate--over";
  if (ratePct >= 80) return "db-rate--high";
  if (ratePct >= 50) return "db-rate--mid";
  return "db-rate--low";
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "たった今";
  if (diffMin < 60) return `${diffMin}分前`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}時間前`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "昨日";
  if (diffD < 7) return `${diffD}日前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * 予算進捗と同じ集計（buildBudgetProgressGroupsForSelectedPeriod）から、担当者別の年間粗利率（差益÷売上）を算出。
 * 同一担当名が複数行に分かれている場合は売上・差益を合算してから率を出す。
 */
function buildDashboardBudgetProgressMarginRankingRows(limit = 10) {
  let groups;
  try {
    groups = buildBudgetProgressGroupsForSelectedPeriod();
  } catch {
    groups = [];
  }
  if (!Array.isArray(groups) || !groups.length) return [];
  const byName = new Map();
  for (const g of groups) {
    for (const m of g.members || []) {
      const name = String(m.name || "").trim();
      if (!name || name === "—") continue;
      const salesArr = Array.isArray(m.sales) ? m.sales : [];
      const profitArr = Array.isArray(m.profit) ? m.profit : [];
      let tsY = 0;
      let tpY = 0;
      for (let i = 0; i < 12; i++) {
        tsY += Number(salesArr[i] || 0);
        tpY += Number(profitArr[i] || 0);
      }
      if (!byName.has(name)) byName.set(name, { sales: 0, profit: 0 });
      const agg = byName.get(name);
      agg.sales += tsY;
      agg.profit += tpY;
    }
  }
  const rows = [...byName.entries()]
    .map(([name, { sales, profit }]) => {
      if (!(sales > 0)) return null;
      const pct = Math.round((profit / sales) * 100);
      return { name, pct, salesYen: sales };
    })
    .filter(Boolean)
    .sort((a, b) => (b.pct !== a.pct ? b.pct - a.pct : b.salesYen - a.salesYen))
    .slice(0, limit);
  return rows;
}

/** ランキング3種（クライアント別売上・営業別売上(差益)・粗利率）。予算進捗ページ「推移」タブで使用。 */
function dashboardRankingsSectionHtml() {
  const clientRankingHtml = (() => {
    const clientSales = {};
    projects.forEach((p) => {
      const cName = p.client || clients.find((c) => c.id === p.clientId)?.name || "不明";
      clientSales[cName] = (clientSales[cName] || 0) + projectDisplayedBillingExTaxYen(p);
    });
    const sorted = Object.entries(clientSales).sort((a, b) => b[1] - a[1]).slice(0, 10);
    if (!sorted.length) return '<div class="empty-note">データがありません</div>';
    return sorted.map(([name, amount], i) => `
      <div class="ranking-row">
        <span class="ranking-num">${i + 1}</span>
        <span class="ranking-name">${escapeHtml(name)}</span>
        <span class="ranking-value">${yen.format(amount)}</span>
      </div>`).join("");
  })();
  // 「営業別売上ランキング（差益）」は廃止。同じ担当別の集計を「経営」タブの営業別集計
  // （売上・原価・粗利・粗利率・件数）が上位互換で持っているため、そちらに一本化した。
  const marginRankingColHtml = `
        <div class="db-ranking-col">
          <h3 class="section-title">粗利率ランキング</h3>
          <div class="list-card">
            ${(() => {
              const rows = buildDashboardBudgetProgressMarginRankingRows(10);
              if (!rows.length) return '<div class="empty-note">データがありません</div>';
              return rows
                .map(
                  (row, i) => `
                <div class="ranking-row">
                  <span class="ranking-num">${i + 1}</span>
                  <span class="ranking-name">${escapeHtml(row.name)}</span>
                  <span class="ranking-value">${row.pct}%</span>
                </div>`
                )
                .join("");
            })()}
          </div>
        </div>`;
  return `
    <section class="section">
      <div class="db-rankings-grid db-rankings-grid--carousel-sp">
        <div class="db-rankings-track-sp">
        <div class="db-ranking-col">
          <h3 class="section-title">クライアント別売上ランキング</h3>
          <div class="list-card">
            ${clientRankingHtml}
          </div>
        </div>
        ${marginRankingColHtml}
        </div>
      </div>
    </section>
  `;
}

/** 会社全体の進捗（月次）グラフ。予算進捗ページ「推移」タブで使用。
 *  全社・選択中会計期の予算 × 当年度の実績（差益）。旧ダッシュボード下部のグラフを移設したもの。 */
function companyMonthlyProgressSectionHtml() {
  const fyStart = dashboardFiscalYearStart();
  const currentMonthIndex = dashboardFiscalMonthIndexNow();
  const actualByMonth = buildDashboardActualAmountsByMonth(fyStart, false);
  const budgetByMonth = buildDashboardBudgetAmountsByMonth(false);
  const monthActual = actualByMonth[currentMonthIndex] || 0;
  const monthBudget = budgetByMonth[currentMonthIndex] || 0;
  const yearActual = actualByMonth.reduce((s, n) => s + n, 0);
  const yearBudget = budgetByMonth.reduce((s, n) => s + n, 0);
  const monthProgressHtml =
    monthBudget > 0
      ? `進捗${Math.round((monthActual / monthBudget) * 100)}%`
      : monthActual > 0
        ? "予算未入力（実績あり）"
        : "進捗 —";
  const yearProgressHtml =
    yearBudget > 0
      ? `進捗${Math.round((yearActual / yearBudget) * 100)}%`
      : yearActual > 0
        ? "予算未入力（実績あり）"
        : "進捗 —";
  const barBudget = budgetByMonth.map((v) => dashboardBarHeightFromYen(v));
  const barActual = actualByMonth.map((v) => dashboardBarHeightFromYen(v));
  const monthTilesHtml = salesMonths
    .map((m, i) => {
      const ratePct = dashboardRatePercent(actualByMonth[i], budgetByMonth[i]);
      const rateClass = dashboardRateToneClass(ratePct);
      const rateLabel = ratePct == null ? "—" : `${Math.min(ratePct, 999)}%`;
      const act = actualByMonth[i] || 0;
      const bud = budgetByMonth[i] || 0;
      const cur = i === currentMonthIndex ? " db-month-tile--current" : "";
      return `
        <article class="db-month-tile${cur}">
          <div class="db-month-tile-head">
            <span class="db-month-tile-mon">${escapeHtml(m)}</span>
            <span class="db-month-tile-rate ${rateClass}">${escapeHtml(rateLabel)}</span>
          </div>
          <dl class="db-month-tile-kv">
            <div><dt>実績</dt><dd>${yen.format(act)}</dd></div>
            <div><dt>予算</dt><dd>${yen.format(bud)}</dd></div>
          </dl>
        </article>`;
    })
    .join("");
  return `
    <section class="section">
      <h3 class="section-title">会社全体の進捗（月次）</h3>
      <div class="db-sales-card">
        <div class="db-kpi-grid">
          <div class="db-kpi"><span>今月予算（全社・差益）</span><strong class="accent">${yen.format(monthBudget)}</strong></div>
          <div class="db-kpi"><span>今月実績（全社・差益）</span><strong class="ok">${yen.format(monthActual)}</strong><em>${escapeHtml(monthProgressHtml)}</em></div>
          <div class="db-kpi"><span>年度累計予算（全社）</span><strong class="accent">${yen.format(yearBudget)}</strong></div>
          <div class="db-kpi"><span>年度累計実績（全社）</span><strong class="ok">${yen.format(yearActual)}</strong><em>${escapeHtml(yearProgressHtml)}</em></div>
        </div>
        <div class="db-bars db-bars-desktop-only">
          ${salesMonths.map((m, i) => {
            // 器（予算）の高さは実績が上回る月でもクリップされないよう高い方に合わせる。
            // 予算未入力（=0）でも実績が潰れず見えるようにするため。
            const trackH = Math.max(14, barBudget[i] * 20, barActual[i] * 20);
            const actualH = barActual[i] > 0 ? Math.max(10, barActual[i] * 20) : 0;
            const ratePct = dashboardRatePercent(actualByMonth[i], budgetByMonth[i]);
            const rateClass = dashboardRateToneClass(ratePct);
            const fill =
              ratePct == null
                ? (i === currentMonthIndex ? "#1a1a2e" : i < currentMonthIndex ? "#cbd5e1" : "transparent")
                : ratePct >= 100
                  ? "#1a5fa8"
                  : ratePct >= 80
                    ? "#047857"
                    : ratePct >= 50
                      ? "#b54708"
                      : "#b42318";
            const rateLabel = ratePct == null ? "—" : `${Math.min(ratePct, 999)}%`;
            return `<div class="db-bar-col"><div class="db-bar-rate ${rateClass}">${rateLabel}</div><div class="db-bar-bg" style="height:${trackH}px;"><div class="db-bar-fill ${rateClass}" style="height:${actualH}px;background:${fill};"></div></div><span>${m}</span></div>`;
          }).join("")}
        </div>
        <div class="db-month-tiles-sp" role="region" aria-label="月次の実績と予算（一覧）">
          <div class="db-month-tiles-track-sp">${monthTilesHtml}</div>
        </div>
        <div class="db-sales-foot">
          <span>■ 実績　□ 予算（全社）</span>
        </div>
      </div>
    </section>
  `;
}

function dashboard() {
  const today = new Date();
  const todayLabel = `本日 ${today.getFullYear()}/${today.getMonth() + 1}/${today.getDate()}`;
  // ─── ステータスカード集計（proposal_stage カラム追加まで既存フィールドで代用） ───
  // 「自分の案件」はカードの遷移先（案件一覧の担当者絞り込み projectMatchesAssigneeFilter）と同じ基準で数え、
  // 原価箱（0円の一時置き場）も一覧の既定どおり除外する（カードの件数＝遷移先の件数にするため）
  const _uid = String(currentUser?.id || "");
  const _mine = _uid
    ? projects.filter((p) => !projectHiddenAsCostBox(p, null) && projectMatchesAssigneeFilter(p, [_uid]))
    : [];
  // 請求額は1回だけ計算して Map に格納（カードごとに重複計算しないため）
  const _billingYenById = new Map(_mine.map((p) => [p.id, projectDisplayedBillingExTaxYen(p) || 0]));
  const _cardYen = (arr) => arr.reduce((s, p) => s + (_billingYenById.get(p.id) || 0), 0);
  const _pjMitsumori = _mine.filter((p) => isQuoteOrderStatus(p.orderStatus));
  // 「確認中」カードは新ステータス（DBカラム追加後に実装）まで常に0件のため出さない
  const _pjSofuzumi  = _mine.filter((p) => p.docs?.quote === "送付済み");
  const _pjJuchu     = _mine.filter((p) => p.orderStatus === "受注済");
  // 請求・入金は案件一覧「入金」列と同じ区分（simplifiedInvoiceStatus）で数える
  const _pjSeikyu    = _mine.filter((p) => simplifiedInvoiceStatus(p) === "請求済み");
  const _pjNyukin    = _mine.filter((p) => simplifiedInvoiceStatus(p) === "入金済み");
  // アラート: 受注済で未請求
  const _alertJuchu  = _pjJuchu.filter((p) => simplifiedInvoiceStatus(p) === "未請求").length;

  // ─── 検索バー ───
  const _dbSearchQ = String(uiState.dashboardSearch || "").trim().toLowerCase();
  const _dbClientHits = _dbSearchQ.length >= 1
    ? clients
        .filter((c) => !c.salesHidden && [c.name, c.shortName, c.contactName].join(" ").toLowerCase().includes(_dbSearchQ))
        .slice(0, 6)
    : [];
  const _dbPartnerHits = _dbSearchQ.length >= 1
    ? (uiState.partnersCache || [])
        .filter((p) => !p.sales_hidden && [p.name || "", p.branch || "", p.contact_name || ""].join(" ").toLowerCase().includes(_dbSearchQ))
        .slice(0, 6)
    : [];
  const _dbSearchOpen = _dbSearchQ.length >= 1;
  const _dbSearchResultsHtml = _dbSearchOpen
    ? _dbClientHits.length === 0 && _dbPartnerHits.length === 0
      ? `<div class="db-search-empty">一致する結果がありません</div>`
      : [
          ..._dbClientHits.map(
            (c) => `<button class="db-search-result-item" type="button" data-go-client-list-keyword="${escapeHtmlAttr(c.name)}"><span class="db-search-result-type">クライアント</span><span class="db-search-result-name">${escapeHtml(c.name)}</span></button>`
          ),
          ..._dbPartnerHits.map(
            (p) => `<button class="db-search-result-item" type="button" data-go-partner-list-keyword="${escapeHtmlAttr(p.name || "")}"><span class="db-search-result-type">パートナー</span><span class="db-search-result-name">${escapeHtml(p.name || "")}</span></button>`
          )
        ].join("")
    : "";

  return `
    <section class="section db-top-section">
      <div class="db-head">
        <div>
          <h2>ダッシュボード</h2>
        </div>
        <span class="chip">${todayLabel}</span>
      </div>
    </section>${orderEvidencePendingBannerHtml()}

    <section class="section">
      <div class="db-search-wrap">
        <svg class="db-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input id="db-search-input" class="db-search-input" type="search" data-db-search="1" data-ime-search
               placeholder="クライアント・パートナーを検索..."
               value="${escapeHtmlAttr(uiState.dashboardSearch || "")}"
               autocomplete="off" />
        <div class="db-search-dropdown${_dbSearchOpen ? " db-search-dropdown--open" : ""}">
          ${_dbSearchResultsHtml}
        </div>
      </div>
    </section>

    <section class="section db-status-section">
      <div class="db-status-grid">
        <button class="db-status-card" type="button" style="--s-color:#6366f1" data-db-status-nav="見積もり中">
          <div class="db-status-card__label">見積もり中</div>
          <div class="db-status-card__count">${_pjMitsumori.length}<span>件</span></div>
          <div class="db-status-card__amount">${yen.format(_cardYen(_pjMitsumori))}</div>
        </button>
        <button class="db-status-card" type="button" style="--s-color:#3b82f6" data-db-status-nav="送付済">
          <div class="db-status-card__label">見積送付済</div>
          <div class="db-status-card__count">${_pjSofuzumi.length}<span>件</span></div>
          <div class="db-status-card__amount">${yen.format(_cardYen(_pjSofuzumi))}</div>
        </button>
        <button class="db-status-card" type="button" style="--s-color:#10b981" data-db-status-nav="受注済">
          <div class="db-status-card__label">受注済</div>
          <div class="db-status-card__count">${_pjJuchu.length}<span>件</span></div>
          <div class="db-status-card__amount">${yen.format(_cardYen(_pjJuchu))}</div>
          ${_alertJuchu > 0 ? `<div class="db-status-card__alert danger">未請求 ${_alertJuchu}件</div>` : ""}
        </button>
        <button class="db-status-card" type="button" style="--s-color:#f97316" data-db-status-nav="請求済み">
          <div class="db-status-card__label">請求済み</div>
          <div class="db-status-card__count">${_pjSeikyu.length}<span>件</span></div>
          <div class="db-status-card__amount">${yen.format(_cardYen(_pjSeikyu))}</div>
        </button>
        <button class="db-status-card" type="button" style="--s-color:#8b5cf6" data-db-status-nav="入金済み">
          <div class="db-status-card__label">入金済み</div>
          <div class="db-status-card__count">${_pjNyukin.length}<span>件</span></div>
          <div class="db-status-card__amount">${yen.format(_cardYen(_pjNyukin))}</div>
        </button>
      </div>
      <div class="db-status-footer">
        <button class="db-link-btn" type="button" data-go="projects">すべての案件をみる →</button>
      </div>
    </section>

    ${companyMonthlyProgressSectionHtml()}
  `;
}

function communityPage() {
  const comm = uiState.community || { tab: "org", dept: "すべて", tag: "すべて" };
  const tab = comm.tab || "org";
  const deptFilters = ["すべて", "社長室", "営業部", "管理部", "開発部", "デザイン部"];
  const articleTags = ["すべて", "お知らせ", "ノウハウ", "雑談", "イベント"];
  const filteredMembers = comm.dept === "すべて"
    ? members
    : members.filter((m) => (m.department || "") === comm.dept);
  const filteredArticles = comm.tag === "すべて"
    ? articles
    : articles.filter((a) => (a.tag || "") === comm.tag);
  const tagTone = (tag) => {
    if (tag === "お知らせ") return "notice";
    if (tag === "ノウハウ") return "knowhow";
    if (tag === "雑談") return "chat";
    if (tag === "イベント") return "event";
    return "default";
  };
  const avatarSvg = `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="4" fill="#9ca3af"></circle>
      <path d="M4 20c0-3.8 3.6-6 8-6s8 2.2 8 6" fill="#9ca3af"></path>
    </svg>
  `;
  return `
    <div class="page cm-page">
      <div class="cm-page-head">
        <h2>コミュニティ</h2>
        <p>社内メンバーの体制と記事をまとめて確認する</p>
      </div>
      <div class="cm-page-wrap">
        <div class="cm-tab-bar">
          <button class="cm-tab-btn ${tab === "org" ? "active" : ""}" data-community-tab="org" type="button">体制図</button>
          <button class="cm-tab-btn ${tab === "article" ? "active" : ""}" data-community-tab="article" type="button">記事</button>
        </div>

        <section class="cm-tab-pane ${tab === "org" ? "active" : ""}">
          <div class="cm-chip-row">
            ${deptFilters.map((d) => `<button class="cm-chip ${comm.dept === d ? "active" : ""}" data-community-dept="${d}" type="button">${d}</button>`).join("")}
          </div>
          ${filteredMembers.length === 0
            ? `<div class="cm-empty">メンバーが登録されていません</div>`
            : `<div class="cm-member-grid">
                ${filteredMembers.map((m) => `
                  <article class="cm-member-card">
                    <div class="cm-avatar">${m.avatar ? `<img src="${escapeHtmlAttr(m.avatar)}" alt="${escapeHtmlAttr(m.name || "")}" />` : avatarSvg}</div>
                    <span class="cm-member-dept">${escapeHtml(m.department || "未所属")}</span>
                    <h4>${escapeHtml(m.name || "—")}</h4>
                    ${m.comment ? `<p class="cm-member-comment">${escapeHtml(m.comment)}</p>` : ""}
                    <p class="cm-member-phone">📞 ${escapeHtml(m.phone || "—")}</p>
                    <button class="cm-outline-btn" type="button">投稿一覧へ</button>
                  </article>
                `).join("")}
              </div>`
          }
        </section>

        <section class="cm-tab-pane ${tab === "article" ? "active" : ""}">
          <div class="cm-article-toolbar">
            <div class="cm-chip-row">
              ${articleTags.map((t) => `<button class="cm-chip ${comm.tag === t ? "active" : ""}" data-community-tag="${t}" type="button">${t}</button>`).join("")}
            </div>
            <button class="cm-primary-btn" type="button" data-community-write="1">＋ 記事を書く</button>
          </div>
          ${filteredArticles.length === 0
            ? `<div class="cm-empty">記事がありません</div>`
            : `<div class="cm-article-grid">
                ${filteredArticles.map((a) => `
                  <article class="cm-article-card">
                    <div class="cm-article-thumb">${a.thumb ? `<img src="${escapeHtmlAttr(a.thumb)}" alt="">` : ""}</div>
                    <div class="cm-article-body">
                      <span class="cm-tag ${tagTone(a.tag)}">${escapeHtml(a.tag || "未分類")}</span>
                      <h4>${escapeHtml(a.title || "無題の記事")}</h4>
                      <div class="cm-article-meta">👤 ${escapeHtml(a.author || "—")} ・ ${escapeHtml(a.date || "—")}</div>
                    </div>
                  </article>
                `).join("")}
              </div>`
          }
        </section>
      </div>
    </div>
  `;
}

function docSealBadgeTone(st) {
  return st === "承認済" ? "safe" : st === "申請中" ? "info" : "muted";
}

/** 案件の捺印対象書類（DOC_SEAL_TAB_LABELS の全種別）の集約状況 */
function projectSealAggregate(projectId, tabs = Object.keys(DOC_SEAL_TAB_LABELS)) {
  let approved = 0;
  let pending = 0;
  for (const tab of tabs) {
    const st = getDocSealStatus(projectId, tab);
    if (st === "承認済") approved += 1;
    else if (st === "申請中") pending += 1;
  }
  return { total: tabs.length, approved, pending };
}

/** 案件一覧の「捺印申請」列に丸で出す書類（見積書・請求書・領収書。その他の書類は押すと開くパネルで見る） */
const PROJECT_LIST_SEAL_DOT_TABS = ["quote", "invoice", "receipt"];

/** 捺印状況の丸1つ（✓ 承認済・… 申請中・− 未申請。マウスを乗せると「請求書：承認済」） */
function projectSealDotHtml(projectId, tab) {
  const st = getDocSealStatus(projectId, tab);
  const kind = st === "承認済" ? "ok" : st === "申請中" ? "pending" : "none";
  const mark = kind === "ok" ? "✓" : kind === "pending" ? "…" : "−";
  const text = `${DOC_SEAL_TAB_LABELS[tab]}：${st}`;
  return `<span class="pj-seal-dot pj-seal-dot--${kind}" title="${escapeHtmlAttr(text)}" aria-label="${escapeHtmlAttr(text)}">${mark}</span>`;
}

/** プロジェクト一覧テーブル「押印申請」列: 見積書・請求書・領収書の丸＋クリックで書類別パネル（全書類の状況と申請）
 *  （開閉は 15g-doc-seal-list-panel.js、申請ボタンは既存 data-pj-seal-apply ハンドラが処理） */
function projectTableSealCellHtml(p) {
  const dots = PROJECT_LIST_SEAL_DOT_TABS.map((tab) => projectSealDotHtml(p.id, tab)).join("");
  const open = String(uiState.pjSealPanelProjectId || "") === String(p.id);
  let panel = "";
  if (open) {
    const rows = Object.entries(DOC_SEAL_TAB_LABELS)
      .map(([tab, docLabel]) => {
        const st = getDocSealStatus(p.id, tab);
        const key = escapeHtmlAttr(docSealStateKey(p.id, tab));
        const rejectInfo = st === "未申請" ? getDocSealRejectInfo(p.id, tab) : null;
        const action =
          st === "未申請"
            ? `<button type="button" class="chip-btn pj-seal-panel-apply" data-pj-seal-apply="${key}"${rejectInfo ? ` title="${escapeHtmlAttr(`前回却下されました：${rejectInfo.reason}`)}"` : ""}>申請する${rejectInfo ? '<span class="pj-badge danger" style="margin-left:4px;">却下</span>' : ""}</button>`
            : `<span class="pj-badge ${docSealBadgeTone(st)}">${st}</span>`;
        return `<div class="pj-seal-panel-row">
          <button type="button" class="pj-link pj-seal-panel-doc" data-project-doc-tab="${key}" title="${escapeHtmlAttr(docLabel)}の書類タブを開く">${docLabel}</button>
          ${action}
        </div>`;
      })
      .join("");
    // 表の下端に入りきらない行では上向きに開く（開いたときに 15g-doc-seal-list-panel.js が判定）
    panel = `<div class="pj-seal-panel${uiState.pjSealPanelUp ? " pj-seal-panel--up" : ""}" data-pj-seal-panel="1">${rows}</div>`;
  }
  return `<td class="pj-seal-cell"><button type="button" class="pj-seal-status-btn pj-seal-dots${open ? " pj-seal-agg-open" : ""}" data-pj-seal-panel-toggle="${escapeHtmlAttr(p.id)}" title="書類別の押印申請状況を開く">${dots}<span class="pj-seal-agg-caret" aria-hidden="true">▾</span></button>${panel}</td>`;
}


/** 案件一覧が0件のときの案内。絞り込みで隠れている場合は効いている既定の条件（請求月・担当など）と
 *  「すべて表示」（既存のリセット data-project-reset と同じ動作）を出す */
function projectListEmptyStateHtml(f, hiddenByFilterCount) {
  // 失注は受注を選ばない限り出ない（「すべて表示」でも出ない）ので、探している案件が失注のときのために一言添える
  const lostHint = projectListHidesLostByDefault(f)
    ? `<div class="pj-empty-lost-hint">失注の案件は「受注」で「失注」を選ぶと表示されます</div>`
    : "";
  if (!(hiddenByFilterCount > 0)) return lostHint ? `<div class="pj-empty-filtered"><div>該当案件がありません</div>${lostHint}</div>` : "該当案件がありません";
  const summary = projectListDefaultFilterSummary(f);
  const cond = summary.length ? `（${escapeHtml(summary.join(" ／ "))}）` : "";
  return `<div class="pj-empty-filtered">
      <div>絞り込み条件${cond}に該当する案件がありません</div>
      <button type="button" class="inline-btn ghost" data-project-reset="1">すべて表示</button>
      ${lostHint}
    </div>`;
}

function projectsPage() {
  ensureProjectListFilterDefaultsOnce();
  // ロック解除申請の状態（解除申請中バッジ用）を初回だけ取得
  if (uiState.projectUnlockApplications === null && !uiState.projectUnlockAppsLoading) {
    void fetchProjectUnlockApplications();
  }
  const f = uiState.projectListFilters;
  // 固定項目編集で隠した検索項目は絞り込みに使わない（15z-project-filter-fields.js）。条件数・0件時の案内もこれで数える
  const fe = projectListEffectiveFilters(f);
  // 適用中フィルター条件数（既定値からの逸脱をカウント）。バッジ・リセット強調に使用
  const activeFilterCount = (() => {
    let n = 0;
    if (String(fe.no || "").trim()) n++;
    if (String(fe.name || "").trim()) n++;
    if (String(fe.keyword || "").trim()) n++;
    if (String(fe.clientNameQuery || "").trim()) n++;
    if (String(fe.clientId || "") && fe.clientId !== "all") n++;
    if (Array.isArray(fe.assignedUserIds) && fe.assignedUserIds.length) n++;
    if (Array.isArray(fe.orderStatuses) && fe.orderStatuses.length) n++;
    if (fe.paymentSimple) n++;
    if (Array.isArray(fe.filterTags) && fe.filterTags.length) n++;
    if (String(fe.billingMonthFrom || "").trim()) n++;
    if (String(fe.billingMonthTo || "").trim()) n++;
    if (String(fe.paymentDateFrom || "").trim()) n++;
    if (String(fe.paymentDateTo || "").trim()) n++;
    if (String(fe.lockState || "all") !== "all") n++;
    if (String(fe.workCategory || "all") !== "all") n++;
    return n;
  })();
  const t = uiState.projectTable;
  if (t.sortKey === "quote") uiState.projectTable.sortKey = "no";
  if (t.sortKey === "progress") uiState.projectTable.sortKey = "no";
  const allRows = getFilteredSortedProjects();
  // 「フィルター条件により N件 が非表示です」の分母。原価箱は絞り込みではなく仕様上の常時非表示なので
  // 数えない（数えると「すべて表示」を押しても通知が消えず、押しても出てこない件数を指し続ける）。
  // 受注を選んでいないときの失注も同じ理由で数えない（失注は選んだときだけ出す既定の仕様）
  const hidesLost = projectListHidesLostByDefault(f);
  const hiddenByFilterCount =
    projects.filter((p) => !projectHiddenAsCostBox(p, f) && !(hidesLost && projectOrderStatusForUi(p) === "失注")).length -
    allRows.length;
  const filteredOrderTotal = allRows.reduce((sum, p) => sum + Number(p._order || 0), 0);
  const filteredCostTotal = allRows.reduce((sum, p) => sum + Number(p._cost || 0), 0);
  const filteredProfitTotal = filteredOrderTotal - filteredCostTotal;
  const total = allRows.length;
  const pageCount = Math.max(1, Math.ceil(total / t.perPage));
  const page = Math.min(Math.max(1, t.page), pageCount);
  // 補正したページを状態にも書き戻す（失注にしてページ数が減ったあと、見出しの全選択が
  // 画面に無い補正前のページの行を対象にしていた。2026-10 レビュー指摘）
  if (t.page !== page) t.page = page;
  const start = (page - 1) * t.perPage;
  const rows = allRows.slice(start, start + t.perPage);
  const end = Math.min(start + rows.length, total);
  // 一括操作の対象と一致させるため、絞り込みで表示中の案件のみを数える
  const selectedCount = allRows.filter((p) => t.selected[p.id] === true).length;
  const headChecked = rows.length > 0 && rows.every((p) => t.selected[p.id] === true);
  const headIndeterminate = rows.some((p) => t.selected[p.id] === true) && !headChecked;
  const sortClass = (k) => t.sortKey === k ? "active" : "";
  const sortMark = (k) => t.sortKey === k ? (t.sortDir === "asc" ? "▲" : "▼") : "";
  const marginTone = (m) => m >= 75 ? "safe" : m >= 50 ? "info" : "danger";
  const statusBadge = (s) =>
    s === "受注済" ? "safe" : s === "受注確定" ? "attention" : s === "失注" ? "danger" : isQuoteOrderStatus(s) ? "info" : "muted";
  const paymentBadge = (s) => s === "入金済み" ? "safe" : s === "請求済み" ? "info" : "muted";
  // 一括入金の既定入金日（今日・ローカル日付。toISOStringはUTCで日付がずれるため使わない）
  const bulkPayToday = new Date();
  const bulkPayDefaultDate = `${bulkPayToday.getFullYear()}-${String(bulkPayToday.getMonth() + 1).padStart(2, "0")}-${String(bulkPayToday.getDate()).padStart(2, "0")}`;
  // 入金確認（一括）は経理/マスター/管理者のみ。操作できない人にボタンごと見せない（canConfirmPayment はプロジェクト非依存）
  const canBulkConfirmPayment = canConfirmPayment(null);
  const tagFilterOpts = collectProjectListTagFilterOptions();
  const selFilterTags = Array.isArray(f.filterTags) ? f.filterTags.filter((x) => String(x || "").trim()) : [];
  const tagFilterPanelInner =
    tagFilterOpts.length > 0
      ? tagFilterOpts
          .map(
            (tag) =>
              `<label class="pj-tag-filter-opt"><span class="pj-tag-filter-opt__check"><input type="checkbox" data-project-tag-filter="${escapeHtmlAttr(tag)}" ${selFilterTags.includes(tag) ? "checked" : ""} /></span><span class="pj-tag-filter-opt__text">${escapeHtml(tag)}</span></label>`
          )
          .join("")
      : `<div class="pj-tag-filter-empty">タグがありません</div>`;
  const tagFilterChips = pjFilterDdChipsHtml("tag", selFilterTags.map((tag) => ({ value: tag, label: tag })));
  const clientFilterSelectedId = f.clientId || "all";
  normalizeProjectListAssigneeFilters(f);
  const selAssigneeIds = (Array.isArray(f.assignedUserIds) ? f.assignedUserIds : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const assigneeTrigger = projectListAssigneeFilterTriggerLabel(selAssigneeIds);
  const assigneeChips = pjFilterDdChipsHtml(
    "assignee",
    selAssigneeIds.map((id) => ({ value: id, label: String(getUserById(id)?.name || "").trim() || "（不明）" }))
  );
  // 受注ステータス絞り込み: チェックボックス並びだと選択肢が多く分かりにくいため、担当者と同じ複数選択プルダウン
  const selOrderStatuses = (Array.isArray(f.orderStatuses) ? f.orderStatuses : []).filter((s) =>
    PROJECT_ORDER_STATUS_LIST.includes(s)
  );
  // 未選択＝失注以外すべて（projectListHidesLostByDefault）。選んだものは表示欄にチップで出し、×で外せる
  const orderChips = pjFilterDdChipsHtml("order", selOrderStatuses.map((s) => ({ value: s, label: s })));
  const orderFilterPanelInner = PROJECT_ORDER_STATUS_LIST.map(
    (s) =>
      `<label class="pj-assignee-filter-opt"><span class="pj-assignee-filter-opt__check"><input type="checkbox" data-project-order-status-filter="${escapeHtmlAttr(s)}" ${selOrderStatuses.includes(s) ? "checked" : ""} /></span><span class="pj-assignee-filter-opt__text">${escapeHtml(s)}</span></label>`
  ).join("");
  // 除外中でも絞り込みに選択済みの人は外さない（チェックを外せなくなるため）
  // 「すべて外す」＝ assignedUserIds=[]＝絞り込みなし（全員表示）。getFilteredSortedProjects の assigneePick.length>0 判定と対
  const assigneeFilterPanelInner =
    pjFilterDdBulkHtml("assignee", "絞り込みを外して全員の案件を表示") +
    assignableUsers()
      .filter((u) => !u.searchExcluded || selAssigneeIds.includes(String(u.id)))
      .map(
        (u) =>
          `<label class="pj-assignee-filter-opt"><span class="pj-assignee-filter-opt__check"><input type="checkbox" data-project-assignee-filter="${escapeHtmlAttr(u.id)}" ${selAssigneeIds.includes(u.id) ? "checked" : ""} /></span><span class="pj-assignee-filter-opt__text">${escapeHtml(u.name)}</span></label>`
      )
      .join("");
  const csvExportBtnHtml = `<button class="pj-filter-csv-dl-btn inline-btn ghost" type="button" data-project-list-export-csv="1" title="検索結果をダウンロード" aria-label="検索結果をCSVでダウンロード">
                <svg class="pj-filter-csv-dl-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 13v8"/><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/><path d="m8 17 4 4 4-4"/></svg>
              </button>`;
  // マスターの一括削除は、選んだ案件に効く操作なので見出しではなく一括バー（選択中のみ表示）に置く
  // （2026-10 レビュー指摘: 見出しの「リセット」の隣に、選択が無くても常に出ていた）。
  // 一括バーでは「選択解除」と右端の1かたまり（pj-bulk-bar-end）にし、間を空けて押し間違いを防ぐ。
  // 入金の操作も1かたまり（pj-bulk-bar-group）で折り返し、区切り「|」は置かない（折り返すと行末に残るため）
  const masterCsvTools = csvExportBtnHtml;
  const projectTbodyRowsHtml = uiState.heavyDataLoading
    ? `<tr><td colspan="14" class="pj-td-heavy-loading">${flowPilotNowLoadingBlock({
        inline: true,
        subtitle: "案件・原価・クライアント情報を読み込んでいます"
      })}</td></tr>`
    : rows
        .map((p) => `
                <tr class="pj-row-order pj-row-order--${projectRowOrderStateKey(p._orderStatus)}">
                  <td class="pj-td-check"><input type="checkbox" data-project-select="${p.id}" ${t.selected[p.id] === true ? "checked" : ""} /></td>
                  <td class="pj-no">${p._no}</td>
                  <td class="pj-td-name"><button class="pj-link" type="button" data-open-detail="${p.id}" data-open-detail-tab="overview">${projectLockBadgeHtml(p)}${escapeHtml(projectDisplayName(p))}</button></td>
                  <td class="pj-td-client pj-muted">${escapeHtml(p._client)}</td>
                  <td class="pj-muted">${escapeHtml(p._ownerName)}</td>
                  <td class="pj-right">${yen.format(p._order)}</td>
                  <td class="pj-right pj-muted">${yen.format(p._cost)}</td>
                  <td class="pj-right"><span class="pj-margin ${marginTone(p._margin)}">${yen.format(p._order - p._cost)}</span></td>
                  <td class="pj-date pj-td-billing">${p._billingDateDisplay || "-"}</td>
                  <td class="pj-right"><span class="pj-margin ${marginTone(p._margin)}">${p._margin}%</span></td>
                  <td class="pj-td-status"><span class="pj-badge ${statusBadge(p._orderStatus)}">${p._orderStatus}</span></td>
                  <td class="pj-td-payment"><span class="pj-badge ${paymentBadge(p._paymentSimple)}">${p._paymentSimple.replace(/済み$/, "済")}</span></td>
                  ${projectTableSealCellHtml(p)}
                  <td class="pj-actions"><div class="pj-actions-inner"><button class="chip-btn" type="button" data-open-detail="${p.id}" data-open-detail-tab="overview">詳細</button><button class="chip-btn" type="button" data-open-project-docs="${p.id}">書類</button>${projectLockActionButtonHtml(p)}</div></td>
                </tr>
              `)
        .join("") || `<tr><td colspan="14" class="empty-note">${projectListEmptyStateHtml(fe, hiddenByFilterCount)}</td></tr>`;
  // 検索欄の各項目。並び順は固定項目編集で人ごとに変えられる（15z-list-filter-fields.js listFilterFieldsShownKeys）。
  // 請求月・入金日は見出しと期間をひとかたまり（pj-filter-range）にして、どこへ並べても見出しと離れないようにする
  const pjFilterFieldsHtml = {
    no: `<input class="pj-input pj-filter-field pj-filter-field--no" placeholder="案件No" data-project-filter="no" data-ime-search value="${escapeHtmlAttr(f.no || "")}" />`,
    name: `<input class="pj-input pj-filter-field pj-filter-field--name" placeholder="案件名" data-project-filter="name" data-ime-search value="${escapeHtmlAttr(f.name || "")}" />`,
    // 選択中は × で「すべて」に戻せる（15n-project-list-filter-dd.js）
    client: `<span class="pj-filter-client">
              <select class="pj-input pj-filter-field pj-filter-field--client" data-project-filter="clientId">
              <option value="all" ${clientFilterSelectedId === "all" ? "selected" : ""}>顧客：すべて</option>
              ${clients.filter((c) => !c.salesHidden || c.id === clientFilterSelectedId).map((c) => `<option value="${escapeHtmlAttr(c.id)}" ${clientFilterSelectedId === c.id ? "selected" : ""}>${escapeHtml(c.name)}</option>`).join("")}
              </select>
              ${clientFilterSelectedId === "all" ? "" : `<button type="button" class="pj-filter-client__clear" data-pj-filter-client-clear title="顧客の絞り込みを解除" aria-label="顧客の絞り込みを解除">×</button>`}
            </span>`,
    assignee: `<details class="pj-assignee-filter-details" role="group" aria-label="自社担当者で絞り込み"${pjFilterDdAttrs("assignee")}>
              <summary class="pj-assignee-filter-trigger${assigneeTrigger.placeholder ? " pj-assignee-filter-trigger--placeholder" : ""}" title="${escapeHtmlAttr(assigneeTrigger.text)}">
                ${assigneeTrigger.placeholder ? `<span class="pj-assignee-filter-trigger-label">${escapeHtml(assigneeTrigger.text)}</span>` : assigneeChips}
                <span class="pj-assignee-filter-caret" aria-hidden="true">▾</span>
              </summary>
              <div class="pj-assignee-filter-panel">${assigneeFilterPanelInner}</div>
            </details>`,
    order: `<details class="pj-assignee-filter-details pj-order-filter-details" role="group" aria-label="受注ステータスで絞り込み"${pjFilterDdAttrs("order")}>
              <summary class="pj-assignee-filter-trigger${selOrderStatuses.length ? "" : " pj-assignee-filter-trigger--placeholder"}" title="${escapeHtmlAttr(selOrderStatuses.length ? selOrderStatuses.join("、") : "受注（失注以外）")}">
                ${selOrderStatuses.length ? orderChips : `<span class="pj-assignee-filter-trigger-label">受注（失注以外）</span>`}
                <span class="pj-assignee-filter-caret" aria-hidden="true">▾</span>
              </summary>
              <div class="pj-assignee-filter-panel">${pjFilterDdBulkHtml("order", "未選択に戻す（失注以外をすべて表示）")}${orderFilterPanelInner}</div>
            </details>`,
    lock: `<select class="pj-input pj-filter-field pj-filter-field--lock" data-project-filter="lockState" data-search-dropdown-off="1" aria-label="ロック状況で絞り込み">
              <option value="all" ${(f.lockState || "all") === "all" ? "selected" : ""}>ロック：すべて</option>
              <option value="locked" ${f.lockState === "locked" ? "selected" : ""}>ロック済</option>
              <option value="unlocked" ${f.lockState === "unlocked" ? "selected" : ""}>未ロック</option>
            </select>`,
    category: `<select class="pj-input pj-filter-field pj-filter-field--category" data-project-filter="workCategory" data-search-dropdown-off="1" aria-label="カテゴリで絞り込み">
              <option value="all" ${(f.workCategory || "all") === "all" ? "selected" : ""}>カテゴリ：すべて</option>
              ${PROJECT_WORK_CATEGORY_OPTIONS.map((c) => `<option value="${escapeHtmlAttr(c)}" ${f.workCategory === c ? "selected" : ""}>${escapeHtml(c)}</option>`).join("")}
              <option value="unset" ${f.workCategory === "unset" ? "selected" : ""}>未設定</option>
            </select>`,
    billingMonth: `<div class="pj-filter-range"><span class="pj-filter-billing-label">請求月</span>
            <div class="pj-filter-billing-dates">
              <label class="pj-month-wrap">
                <input class="pj-input pj-input-month" type="month" aria-label="請求月 開始" data-show-picker="1" data-project-filter="billingMonthFrom" value="${escapeHtmlAttr(billingMonthFilterToMonthInputValue(f.billingMonthFrom))}" />
              </label>
              <span class="pj-filter-billing-sep" aria-hidden="true">〜</span>
              <label class="pj-month-wrap">
                <input class="pj-input pj-input-month" type="month" aria-label="請求月 終了" data-show-picker="1" data-project-filter="billingMonthTo" value="${escapeHtmlAttr(billingMonthFilterToMonthInputValue(f.billingMonthTo))}" />
              </label>
            </div></div>`,
    paymentDate: `<div class="pj-filter-range"><span class="pj-filter-billing-label">入金日</span>
            <div class="pj-filter-billing-dates">
              <label class="pj-month-wrap">
                <input class="pj-input pj-input-month" type="date" aria-label="入金日 開始" data-show-picker="1" data-project-filter="paymentDateFrom" value="${escapeHtmlAttr(f.paymentDateFrom || "")}" />
              </label>
              <span class="pj-filter-billing-sep" aria-hidden="true">〜</span>
              <label class="pj-month-wrap">
                <input class="pj-input pj-input-month" type="date" aria-label="入金日 終了" data-show-picker="1" data-project-filter="paymentDateTo" value="${escapeHtmlAttr(f.paymentDateTo || "")}" />
              </label>
            </div></div>`,
    tag: `<details class="pj-tag-filter-details" role="group" aria-label="タグで絞り込み"${pjFilterDdAttrs("tag")}>
              <summary class="pj-tag-filter-trigger"${selFilterTags.length ? ` title="${escapeHtmlAttr(selFilterTags.join("、"))}"` : ""}>
                <span class="pj-tag-filter-trigger-label">タグ</span>
                ${selFilterTags.length ? tagFilterChips : ""}
                <span class="pj-tag-filter-caret" aria-hidden="true">▾</span>
              </summary>
              <div class="pj-tag-filter-panel">${tagFilterOpts.length ? pjFilterDdBulkHtml("tag", "タグの絞り込みを外す") : ""}${tagFilterPanelInner}</div>
            </details>`
  };
  const filterNoticeHtml = hiddenByFilterCount > 0
    ? `<div class="pj-filter-notice" role="status">
        <span class="pj-filter-notice-text">フィルター条件${f.paymentSimple ? `（入金: ${escapeHtml(f.paymentSimple)}）` : ""}により <strong>${hiddenByFilterCount}件</strong> が非表示です</span>
        <button type="button" class="inline-btn ghost pj-filter-notice-clear" data-project-reset="1">すべて表示</button>
      </div>`
    : "";
  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <h2>案件一覧</h2>
          ${filterNoticeHtml}
          ${/* 固定項目編集はフィルター通知の右・検索結果DLの左に置く。ボタンを中身の幅にして（--compact）見出しを1行に収める */ ""}
          <div class="pj-head-actions pj-head-actions--compact">
            ${listFilterFieldsEditButtonHtml(PROJECT_FILTER_SPEC)}
            ${masterCsvTools}
            <button class="inline-btn ${activeFilterCount > 0 ? "pj-filter-reset-active" : "ghost"}" type="button" data-project-reset="1">リセット</button>
            <button class="inline-btn ghost" type="button" data-recurring-billing-auto-settings-open="1" title="「毎月請求」に設定した自分の担当案件を、毎月指定した日に自動で今月分として複製します">自動作成設定</button>
            <button class="inline-btn pj-head-create-btn" data-open-project-create="1" type="button">＋ 新規案件を登録</button>
          </div>
        </div>
        <div class="pj-filter-area${uiState.projectListFilterOpen ? " pj-filter-area--open" : ""}">
          <div class="pj-filter-row pj-filter-row--main">
            ${listFilterFieldsShownKeys(PROJECT_FILTER_SPEC).map((k) => pjFilterFieldsHtml[k]).join("")}
            <div class="pj-filter-actions" role="group" aria-label="検索操作">
              <button class="inline-btn ghost pj-filter-actions-btn pj-filter-toggle-btn" type="button" data-pj-filter-toggle="1" aria-expanded="${uiState.projectListFilterOpen ? "true" : "false"}">${uiState.projectListFilterOpen ? "絞り込み ▲" : "絞り込み ▼"}${activeFilterCount > 0 ? `<span class="pj-filter-active-badge">${activeFilterCount}</span>` : ""}</button>
            </div>
          </div>
        </div>
        ${listFilterFieldsEditorHtml(PROJECT_FILTER_SPEC)}
        <div class="pj-bulk-bar ${selectedCount > 0 ? "show" : ""}">
          <span>${selectedCount}件選択中</span><span class="sep">|</span>
          <button class="inline-btn ghost" data-project-bulk-dup="1">複製</button><span class="sep">|</span>
          <button type="button" class="inline-btn ghost" data-project-bulk-seal-tab="1">捺印申請</button><span class="sep">|</span>
          <button type="button" class="inline-btn ghost" data-project-bulk-docs="1">書類を出力</button>
          <span class="sep">|</span>
          <span>受注状況を変更：</span>
          <select class="pj-input" style="width:140px;" data-project-filter="bulkStatus">${PROJECT_ORDER_STATUS_LIST.map((s) => `<option value="${s}" ${s === projectBulkOrderStatus() ? "selected" : ""}>${s}</option>`).join("")}</select>
          <button class="inline-btn" data-project-bulk-apply="1">適用</button>
          ${canBulkConfirmPayment ? `
          <span class="pj-bulk-bar-group">
            <label class="pj-bulk-bar-field">入金日
              <input class="pj-input" type="date" style="width:150px;" data-project-bulk-payment-date value="${bulkPayDefaultDate}" aria-label="一括登録する入金日" />
            </label>
            <button class="inline-btn" data-project-bulk-payment-apply="1">入金済に変更</button>
            <button class="inline-btn ghost" data-project-bulk-payment-cancel="1" title="誤って入金済にした案件を未入金（請求済み）へ戻す">未入金に戻す</button>
          </span>
          ` : ""}
          <span class="pj-bulk-bar-end">
            <button class="inline-btn ghost" data-project-clear-selection="1">選択解除</button>
            ${isMasterRoleUser(currentUser) ? `<button class="inline-btn ghost pj-master-project-delete-btn" type="button" data-project-master-bulk-delete="1">選択した案件を削除</button>` : ""}
          </span>
        </div>
        <div class="pj-table-area fp-grid-scroll">
          <table class="pj-table pj-project-table" data-fp-table="projects" data-fp-table-columns>
            <thead>
              <tr>
                <th class="pj-th-check"><input type="checkbox" data-project-select-all="1" ${headChecked ? "checked" : ""} ${headIndeterminate ? 'data-indeterminate="1"' : ""} /></th>
                <th class="${sortClass("no")}" data-project-sort="no">No ${sortMark("no")}</th>
                <th class="${sortClass("name")}" data-project-sort="name" data-fp-col-fixed>案件名 ${sortMark("name")}</th>
                <th class="${sortClass("client")}" data-project-sort="client">顧客 ${sortMark("client")}</th>
                <th class="${sortClass("owner")}" data-project-sort="owner">担当 ${sortMark("owner")}</th>
                <th class="${sortClass("order")}" data-project-sort="order">請求額（税抜） ${sortMark("order")}</th>
                <th>原価（税抜）</th>
                <th>粗利</th>
                <th class="${sortClass("billingDate")}" data-project-sort="billingDate">請求日 ${sortMark("billingDate")}</th>
                <th class="${sortClass("margin")}" data-project-sort="margin">粗利率 ${sortMark("margin")}</th>
                <th class="${sortClass("status")}" data-project-sort="status">受注 ${sortMark("status")}</th>
                <th class="${sortClass("payment")}" data-project-sort="payment">入金 ${sortMark("payment")}</th>
                <th class="${sortClass("seal")}" data-project-sort="seal">捺印申請 ${sortMark("seal")}</th>
                <th class="pj-th-actions" aria-label="操作"></th>
              </tr>
            </thead>
            <tbody>
              ${projectTbodyRowsHtml}
            </tbody>
          </table>
        </div>
        <div class="pj-pagination">
          <div class="pj-page-left">
            <span>表示 ${total === 0 ? 0 : start + 1}〜${end} 件 / 合計 ${total} 件</span>
            <span class="pj-profit-summary">粗利 ${yen.format(filteredProfitTotal)}（請求額（税抜）合計 ${yen.format(filteredOrderTotal)} / 原価（税抜）合計 ${yen.format(filteredCostTotal)}）</span>
          </div>
          <div class="pj-page-right">
            <select class="pj-input" style="width:100px;" data-search-dropdown-off="1" data-project-per-page="1">
              <option value="10" ${t.perPage === 10 ? "selected" : ""}>10件</option>
              <option value="20" ${t.perPage === 20 ? "selected" : ""}>20件</option>
              <option value="50" ${t.perPage === 50 ? "selected" : ""}>50件</option>
              <option value="100" ${t.perPage === 100 ? "selected" : ""}>100件</option>
            </select>
            <button class="chip-btn" ${page <= 1 ? "disabled" : ""} data-project-page="${page - 1}">＜</button>
            ${pageNumberItemsWithEllipsis(page, pageCount).map((n) => n === "…"
              ? `<span class="cl-page-ellipsis" style="padding:0 6px;color:var(--fp-text-subtle);">…</span>`
              : `<button class="chip-btn ${n === page ? "active" : ""}" data-project-page="${n}">${n}</button>`).join("")}
            <button class="chip-btn" ${page >= pageCount ? "disabled" : ""} data-project-page="${page + 1}">＞</button>
          </div>
        </div>
      </div>
    </div>
    ${projectUnlockRequestModalHtml()}
  `;
}

function docSealStateKey(projectId, docTab) {
  return `${projectId}:${docTab}`;
}

function getDocSealStatus(projectId, docTab) {
  const raw = uiState.docSealStatus?.[docSealStateKey(projectId, docTab)];
  if (raw === "申請中" || raw === "承認済") return raw;
  return "未申請";
}

/** 直近の却下理由（却下後は getDocSealStatus が「未申請」に戻るため、理由表示だけこちらを別途参照する） */
function getDocSealRejectInfo(projectId, docTab) {
  const info = uiState.docSealRejectInfo?.[docSealStateKey(projectId, docTab)];
  return info && typeof info === "object" && info.reason ? info : null;
}

function setDocSealStatus(projectId, docTab, status) {
  if (!uiState.docSealStatus) uiState.docSealStatus = {};
  const k = docSealStateKey(projectId, docTab);
  uiState.docSealStatus[k] = status;
  if (status !== "申請中") clearSealApprovalProgress(k);
}

// 郵送中とみなすステータス（この間は「郵送する」ボタンを準備中表示にして誤って二重発送しないようにする）
const DOC_MAIL_PENDING_STATUSES = ["accepted", "dispatching", "dispatched"];

function getDocMailStatus(projectId, docType) {
  return uiState.docMailStatus?.[`${projectId}:${docType}`] || null;
}

const _docMailStatusFailedAt = {};

/** 案件の郵送ステータスを取得して uiState.docMailStatus に反映する（invoiceタブ表示時に呼ぶ）。
 *  取得済み/取得中はスキップ。submitDocPostal 成功時・モーダルを閉じた時に
 *  uiState.docMailStatusLoadedFor をクリアして再取得させる（画面操作起点の更新。ポーリングはしない）。 */
async function ensureDocMailStatusLoaded(p) {
  if (!p?.dbId || uiState.docMailStatusLoadedFor[p.id]) return;
  if (_docMailStatusFailedAt[p.id] && Date.now() - _docMailStatusFailedAt[p.id] < 15000) return;
  uiState.docMailStatusLoadedFor[p.id] = true;
  const { ok, data } = await apiFetch(`/api/doc-mail?projectId=${p.dbId}`);
  if (!ok) {
    delete uiState.docMailStatusLoadedFor[p.id];
    _docMailStatusFailedAt[p.id] = Date.now();
    return;
  }
  const latestInvoice = (data.items || []).find((it) => String(it.docType || "").split("+").includes("invoice"));
  uiState.docMailStatus[`${p.id}:invoice`] = latestInvoice ? latestInvoice.status : null;
  render();
}

/** 捺印申請を送信する（確認モーダルの OK から。extra は申請コメント・承認期日＝sealApplyExtraFromDialog） */
async function submitDocSealApplicationQuick(mapKey, extra = {}) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab || !docTabHasSealWorkflow(parsed.tab)) return false;
  if (getDocSealStatus(parsed.pid, parsed.tab) !== "未申請") return false;
  const mk = docSealStateKey(parsed.pid, parsed.tab);
  const chain = getSealApprovalChainForMapKey(mk);
  if (!chain.length) {
    showToast(
      "承認フローに承認者がいません。アカウント設定の承認設定で承認者を設定してから申請してください。"
    );
    return false;
  }
  const snap = snapshotDocSealState(mk);
  setDocSealStatus(parsed.pid, parsed.tab, "申請中");
  setSealApprovalProgress(mk, 0, {
    appliedAt: new Date().toISOString(),
    appliedByUserId: currentUser?.id != null ? String(currentUser.id) : "",
    stepApprovedAt: [],
    applicantComment: extra.applicantComment,
    dueDate: extra.dueDate
  });
  const ok = await persistDocSealApplicationToServer(mk, snap, extra);
  if (ok) showToast("捺印申請を送信しました");
  return ok;
}

function clearSealApprovalProgress(mapKey) {
  if (uiState.docSealApprovalProgress && mapKey != null && String(mapKey) in uiState.docSealApprovalProgress) {
    delete uiState.docSealApprovalProgress[String(mapKey)];
  }
}

function sealApprovalProgressRecord(mapKey) {
  const o = uiState.docSealApprovalProgress?.[String(mapKey)];
  return o && typeof o === "object" ? o : null;
}

function getSealApprovalProgressApprovedCount(mapKey) {
  const n = sealApprovalProgressRecord(mapKey)?.approvedCount;
  if (!Number.isFinite(Number(n)) || Number(n) < 0) return 0;
  return Math.floor(Number(n));
}

function setSealApprovalProgress(mapKey, approvedCount, patch) {
  if (!uiState.docSealApprovalProgress) uiState.docSealApprovalProgress = {};
  const prev = sealApprovalProgressRecord(mapKey) || {};
  const p = patch && typeof patch === "object" ? patch : {};
  const stepApprovedAt = Array.isArray(p.stepApprovedAt)
    ? p.stepApprovedAt
    : Array.isArray(prev.stepApprovedAt)
      ? [...prev.stepApprovedAt]
      : [];
  uiState.docSealApprovalProgress[String(mapKey)] = {
    approvedCount: Math.max(0, Math.floor(Number(approvedCount) || 0)),
    appliedAt: p.appliedAt != null ? String(p.appliedAt) : String(prev.appliedAt || ""),
    appliedByUserId:
      p.appliedByUserId != null ? String(p.appliedByUserId) : String(prev.appliedByUserId || ""),
    stepApprovedAt,
    applicantComment: p.applicantComment != null ? String(p.applicantComment) : String(prev.applicantComment || ""),
    dueDate: p.dueDate != null ? String(p.dueDate) : String(prev.dueDate || "")
  };
}

function recordSealApprovalStepApprovedAt(mapKey, stepIndex) {
  const rec = sealApprovalProgressRecord(mapKey) || {};
  const times = Array.isArray(rec.stepApprovedAt) ? [...rec.stepApprovedAt] : [];
  const idx = Math.max(0, Math.floor(Number(stepIndex) || 0));
  while (times.length <= idx) times.push("");
  times[idx] = new Date().toISOString();
  setSealApprovalProgress(mapKey, getSealApprovalProgressApprovedCount(mapKey), {
    appliedAt: rec.appliedAt,
    stepApprovedAt: times
  });
}

/** 捺印申請の状態をサーバから取り直す（ログイン時の一括読込とは別に、申請承認ページを開いた時に呼ぶ）。
 *  ログイン後に他の人が出した申請は、これを呼ばないと画面をリロードするまで見えない。
 *  短時間の連続 render で叩き続けないよう、前回取得から一定時間は再取得しない。 */
const DOC_SEAL_REFRESH_MIN_INTERVAL_MS = 15000;
async function refreshDocSealApplications() {
  if (!authToken) return false;
  const last = Number(uiState.docSealAppsFetchedAt || 0);
  if (uiState.docSealAppsRefreshing || Date.now() - last < DOC_SEAL_REFRESH_MIN_INTERVAL_MS) return false;
  uiState.docSealAppsRefreshing = true;
  try {
    const res = await apiFetch("/api/doc-seal-applications");
    if (!res.ok) return false;
    applyDocSealApplicationsPayload(res.data);
    return true;
  } finally {
    // 失敗時も時刻を進める（API 障害中に render のたびに叩き続けない）
    uiState.docSealAppsFetchedAt = Date.now();
    uiState.docSealAppsRefreshing = false;
  }
}

/** GET /api/doc-seal-applications の応答を uiState に反映 */
function applyDocSealApplicationsPayload(payload) {
  uiState.docSealStatus = {};
  uiState.docSealApprovalProgress = {};
  uiState.docSealRejectInfo = {};
  const statuses =
    payload && typeof payload.statusByKey === "object" && payload.statusByKey ? payload.statusByKey : {};
  const progress =
    payload && typeof payload.progressByKey === "object" && payload.progressByKey ? payload.progressByKey : {};
  for (const [k, v] of Object.entries(statuses)) {
    const st = String(v || "").trim();
    if (st === "申請中" || st === "承認済") uiState.docSealStatus[String(k)] = st;
    // 却下は「未申請」扱いに戻して再申請できるようにする（getDocSealStatus参照）。
    // 理由だけは docSealRejectInfo に別途残し、再申請ボタンの近くで表示する。
    else if (st === "却下") {
      const pv = progress[k];
      uiState.docSealRejectInfo[String(k)] = {
        reason: pv && typeof pv === "object" && pv.reviewComment ? String(pv.reviewComment) : "",
        reviewedAt: pv && typeof pv === "object" && pv.reviewedAt ? String(pv.reviewedAt) : ""
      };
    }
  }
  for (const [k, v] of Object.entries(progress)) {
    if (!v || typeof v !== "object") continue;
    const n = Number(v.approvedCount);
    const stepApprovedAt = Array.isArray(v.stepApprovedAt) ? v.stepApprovedAt.map((x) => String(x ?? "")) : [];
    uiState.docSealApprovalProgress[String(k)] = {
      approvedCount: Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0,
      appliedAt: v.appliedAt != null ? String(v.appliedAt) : "",
      appliedByUserId: v.appliedByUserId != null ? String(v.appliedByUserId) : "",
      stepApprovedAt,
      applicantComment: v.applicantComment != null ? String(v.applicantComment) : "",
      dueDate: v.dueDate != null ? String(v.dueDate) : ""
    };
  }
  applyDocSealRevokePayload(payload?.revokedByKey);
}

function snapshotDocSealState(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab) return null;
  const rec = sealApprovalProgressRecord(mapKey);
  return {
    status: getDocSealStatus(parsed.pid, parsed.tab),
    progress: rec
      ? {
          approvedCount: rec.approvedCount,
          appliedAt: rec.appliedAt,
          appliedByUserId: rec.appliedByUserId,
          stepApprovedAt: Array.isArray(rec.stepApprovedAt) ? [...rec.stepApprovedAt] : []
        }
      : null
  };
}

function restoreDocSealState(mapKey, snap) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab || !snap) return;
  setDocSealStatus(parsed.pid, parsed.tab, snap.status);
  // 進捗も状態に関係なく変更前へ戻す（申請の失敗で「未申請」へ戻す時に、付けた進捗を残さない）
  if (snap.progress) {
    setSealApprovalProgress(mapKey, snap.progress.approvedCount, {
      appliedAt: snap.progress.appliedAt,
      appliedByUserId: snap.progress.appliedByUserId,
      stepApprovedAt: snap.progress.stepApprovedAt
    });
  } else {
    clearSealApprovalProgress(mapKey);
  }
}

/** 捺印申請状態を API へ保存（失敗時は snapBefore へ復元）。
 *  呼び出し側は状態を書き換える「前」に snapshotDocSealState で控えを取り、snapBefore に渡すこと。
 *  以前はここで控えを取っていたため、控えが書き換え後の状態になり、失敗しても画面が戻らなかった
 *  （画面は承認済・DBは申請中、のような食い違い）。 */
async function persistDocSealApplicationToServer(mapKey, snapBefore = null, extra = null) {
  if (!authToken) return false;
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab || !docTabHasSealWorkflow(parsed.tab)) return false;
  const snap = snapBefore;
  const status = getDocSealStatus(parsed.pid, parsed.tab);
  const rec = sealApprovalProgressRecord(mapKey);
  const body = {
    key: String(mapKey),
    status,
    progress:
      status === "申請中" && rec
        ? {
            approvedCount: rec.approvedCount,
            appliedAt: rec.appliedAt || null,
            stepApprovedAt: Array.isArray(rec.stepApprovedAt) ? rec.stepApprovedAt : []
          }
        : undefined,
    // 申請時の入力（新規・再申請のときだけサーバーが保存する。承認の前進では送らない）
    ...(extra && status === "申請中" ? { applicantComment: extra.applicantComment || "", dueDate: extra.dueDate || "" } : {})
  };
  try {
    const res = await fetch("/api/doc-seal-applications", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      if (snap) restoreDocSealState(mapKey, snap);
      showToastError("捺印申請の保存に失敗しました");
      return false;
    }
    return true;
  } catch {
    if (snap) restoreDocSealState(mapKey, snap);
    showToastError("捺印申請の保存に失敗しました");
    return false;
  }
}

/** サーバーが書類の内容の変更で捺印申請を取り消した時（api/_docSeal.js resetDocSealApplicationsForProject。
 *  応答の sealResetTabs）に、画面の申請状態を未申請へ合わせて再申請を促す。
 *  変更点はサーバーに残るので、取り直して「何から何に変わったか」を出す */
function applyServerDocSealReset(projectId, resetTabs) {
  const tabs = (Array.isArray(resetTabs) ? resetTabs : []).filter((tab) => DOC_SEAL_TAB_LABELS[tab]);
  if (!tabs.length) return;
  if (!uiState.docSealRevokeInfo) uiState.docSealRevokeInfo = {};
  tabs.forEach((tab) => {
    const mk = docSealStateKey(projectId, tab);
    uiState.docSealRevokeInfo[mk] = { revokedAt: new Date().toISOString(), fromStatus: getDocSealStatus(projectId, tab), changes: [] };
    setDocSealStatus(projectId, tab, "未申請");
  });
  showToast(`書類の内容の変更により、捺印申請を未申請に戻しました（${tabs.length}件）。再申請してください`);
  uiState.docSealAppsFetchedAt = 0;
  void refreshDocSealApplications().then((ok) => {
    if (ok) render();
  });
  render();
}

function formatSealApprovalDateTime(iso) {
  const d = new Date(String(iso || ""));
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function getApprovalFlowDisplayName(flowId) {
  const fid = String(flowId || "").trim();
  if (!fid) return "—";
  const other = (uiState.approvalOtherFlows || []).find((f) => String(f.id) === fid);
  if (other?.name) return String(other.name).trim();
  return fid;
}

function getSealApprovalFlowLabelForMapKey(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab) return "—";
  const p = projects.find((x) => String(x.id) === String(parsed.pid));
  if (!p) return "—";
  ensureApprovalApproversState();
  ensureApprovalApplicationFlowBindingsState();
  const flowId = getResolvedApprovalFlowIdForSeal(parsed.tab, String(p.assignedUserId || "").trim());
  return getApprovalFlowDisplayName(flowId);
}

/** 捺印申請の申請者（申請を出した本人。旧データで不明なときは案件の担当者） */
function getSealApplicantUserForMapKey(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab) return null;
  const appliedBy = String(sealApprovalProgressRecord(mapKey)?.appliedByUserId || "").trim();
  if (appliedBy) {
    const applicant = getUserById(appliedBy);
    if (applicant) return applicant;
  }
  const p = projects.find((x) => String(x.id) === String(parsed.pid));
  if (!p) return null;
  const uid = String(p.assignedUserId || "").trim();
  return uid ? getUserById(uid) || null : null;
}

/**
 * 捺印ウィジェット表示用: 申請者 + 承認者（順序どおり）
 */
function getSealApprovalFullDisplayChain(mapKey) {
  const applicant = getSealApplicantUserForMapKey(mapKey);
  const approvers = getSealApprovalChainForMapKey(mapKey);
  const entries = [];
  if (applicant) entries.push({ role: "applicant", user: applicant });
  for (const a of approvers) entries.push({ role: "approver", user: a.user });
  return entries;
}

/**
 * 捺印に紐づく承認フロー上の承認者を、ステップ順に重複なく並べる（空ステップはスキップ）
 */
function getSealApprovalChainForMapKey(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab) return [];
  if (!docTabHasSealWorkflow(parsed.tab)) return [];
  const p = projects.find((x) => String(x.id) === String(parsed.pid));
  if (!p) return [];
  ensureApprovalApproversState();
  ensureApprovalApplicationFlowBindingsState();
  const applicantUserId = String(p.assignedUserId || "").trim();
  const flowId = getResolvedApprovalFlowIdForSeal(parsed.tab, applicantUserId);
  const deptLike = getDeptLikeObjectForApprovalFlowId(flowId);
  const steps = getApprovalStepsForDept(deptLike.id);
  const chain = [];
  for (let stepIdx = 0; stepIdx < steps.length; stepIdx += 1) {
    const u = resolveApprovalStepUser(deptLike, stepIdx);
    if (u && u.id != null && !chain.some((e) => String(e.user.id) === String(u.id))) chain.push({ stepIdx, user: u });
  }
  return chain;
}

/** いま承認待ちの承認者（null ＝フロー未設定で即完、またはキュー外） */
function getCurrentSealApproverUserForMapKey(mapKey) {
  const chain = getSealApprovalChainForMapKey(mapKey);
  const n = getSealApprovalProgressApprovedCount(mapKey);
  if (!chain.length) return null;
  if (n >= chain.length) return null;
  return chain[n]?.user || null;
}

/** 1段階承認を進める。true＝状態更新した */
function applyOneSealApprovalAdvance(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  if (!parsed?.pid || !parsed?.tab) return false;
  if (getDocSealStatus(parsed.pid, parsed.tab) !== "申請中") return false;
  const chain = getSealApprovalChainForMapKey(mapKey);
  if (!chain.length) {
    setDocSealStatus(parsed.pid, parsed.tab, "承認済");
    return true;
  }
  const n = getSealApprovalProgressApprovedCount(mapKey);
  if (n >= chain.length) return false;
  recordSealApprovalStepApprovedAt(mapKey, n);
  const next = n + 1;
  if (next >= chain.length) setDocSealStatus(parsed.pid, parsed.tab, "承認済");
  else setSealApprovalProgress(mapKey, next);
  return true;
}

/** 捺印・申請管理向け: 申請者 — 承認者 — … の横型ステッパー */
const DOC_SEAL_FLOW_CHECK_SVG =
  '<span class="doc-seal-flow-h-check" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7.2L5.4 10.1L11.5 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

function sealApprovalFlowHorizontalHtml(mapKey) {
  const entries = getSealApprovalFullDisplayChain(mapKey);
  if (!entries.length) {
    return `<span class="pj-muted">—（承認者未設定）</span>`;
  }
  const parsed = parseDocSealKey(mapKey);
  const st = parsed ? getDocSealStatus(parsed.pid, parsed.tab) : "未申請";
  const rec = sealApprovalProgressRecord(mapKey);
  const approverDone = getSealApprovalProgressApprovedCount(mapKey);
  const hasApplicant = entries[0]?.role === "applicant";
  let currentIdx;
  if (st === "承認済") currentIdx = entries.length;
  else if (st === "申請中") currentIdx = Math.min(entries.length, (hasApplicant ? 1 : 0) + approverDone);
  else currentIdx = 0;
  const steps = entries
    .map((entry, i) => {
      const name = escapeHtml(String(entry.user?.name || "—").trim() || "—");
      let tone = "later";
      let nodeInner = "";
      let timeHtml = "";
      if (i < currentIdx) {
        tone = "done";
        nodeInner = DOC_SEAL_FLOW_CHECK_SVG;
        if (entry.role === "applicant") {
          const t = rec?.appliedAt;
          if (t) timeHtml = `<div class="doc-seal-flow-h-time">${escapeHtml(formatSealApprovalDateTime(t))}</div>`;
        } else {
          const apprIdx = hasApplicant ? i - 1 : i;
          const t = Array.isArray(rec?.stepApprovedAt) ? rec.stepApprovedAt[apprIdx] : "";
          if (t) timeHtml = `<div class="doc-seal-flow-h-time">${escapeHtml(formatSealApprovalDateTime(t))}</div>`;
        }
      } else if (i === currentIdx && currentIdx < entries.length) {
        tone = "current";
        if (entry.role === "approver") {
          const apprIdx = hasApplicant ? i - 1 : i;
          nodeInner = `<span class="doc-seal-flow-h-num">${Math.max(1, apprIdx - approverDone + 1)}</span>`;
        } else {
          nodeInner = '<span class="doc-seal-flow-h-applicant" aria-hidden="true">申</span>';
        }
      } else if (entry.role === "approver") {
        const apprIdx = hasApplicant ? i - 1 : i;
        nodeInner = `<span class="doc-seal-flow-h-num">${Math.max(1, apprIdx - approverDone + 1)}</span>`;
      }
      const connector =
        i < entries.length - 1
          ? `<span class="doc-seal-flow-h-connector${i < currentIdx - 1 ? " is-done" : i === currentIdx - 1 ? " is-active" : ""}" aria-hidden="true"></span>`
          : "";
      return `<li class="doc-seal-flow-h-step doc-seal-flow-h-step--${tone}">
        <div class="doc-seal-flow-h-step-core">
          <span class="doc-seal-flow-h-node">${nodeInner}</span>
          <div class="doc-seal-flow-h-name">${name}</div>
          ${timeHtml}
        </div>
        ${connector}
      </li>`;
    })
    .join("");
  return `<ol class="doc-seal-flow-h" aria-label="承認フロー">${steps}</ol>`;
}

/** 書類プレビューの「申請履歴」モーダル。承認フロー上の各人（申請者→承認者…）と、
 *  それぞれが実際に処理した日時を並べる。個別の履歴ログテーブルは持たないため、現在の
 *  申請（fp_doc_seal_applications の1レコード）が持つ日時をそのまま一覧化した「現在の申請の
 *  経過」であり、取消・再申請をまたいだ複数回分のログではない点に留意（2026-08-18 実装）。 */
function renderDocSealHistoryModal() {
  const st = uiState.docSealHistoryModal;
  if (!st?.mapKey) return "";
  const parsed = parseDocSealKey(st.mapKey);
  if (!parsed?.pid || !parsed?.tab) return "";
  const docLabel = escapeHtml(docTabLabelForSealApplication(parsed.tab));
  const status = getDocSealStatus(parsed.pid, parsed.tab);
  const entries = getSealApprovalFullDisplayChain(st.mapKey);
  const rec = sealApprovalProgressRecord(st.mapKey);
  const approverDone = getSealApprovalProgressApprovedCount(st.mapKey);
  const hasApplicant = entries[0]?.role === "applicant";
  const rowsHtml = entries.length
    ? entries
        .map((entry, i) => {
          const name = escapeHtml(String(entry.user?.name || "—").trim() || "—");
          const roleLabel = entry.role === "applicant" ? "申請" : "承認";
          let when = "";
          if (entry.role === "applicant") {
            when = rec?.appliedAt ? formatSealApprovalDateTime(rec.appliedAt) : "";
          } else {
            const apprIdx = hasApplicant ? i - 1 : i;
            when = Array.isArray(rec?.stepApprovedAt) ? rec.stepApprovedAt[apprIdx] || "" : "";
            when = when ? formatSealApprovalDateTime(when) : "";
          }
          const done = entry.role === "applicant" ? !!rec?.appliedAt : (hasApplicant ? i - 1 : i) < approverDone;
          return `<li class="doc-seal-hist-row${done ? " doc-seal-hist-row--done" : ""}">
            <span class="doc-seal-hist-role">${roleLabel}</span>
            <span class="doc-seal-hist-name">${name}</span>
            <span class="doc-seal-hist-time">${when ? escapeHtml(when) : "未処理"}</span>
          </li>`;
        })
        .join("")
    : `<p class="doc-seal-flow-empty">承認者が未設定です</p>`;
  return `
    <div class="composer-backdrop" data-doc-seal-history-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm doc-seal-history-modal" role="dialog" aria-modal="true" aria-labelledby="doc-seal-history-title" style="grid-template-rows:auto auto auto;">
      <div class="composer-head">
        <div><h3 id="doc-seal-history-title">${docLabel}の申請履歴</h3></div>
      </div>
      <div class="composer-body">
        <p class="doc-seal-hist-status">現在のステータス: <strong>${escapeHtml(status)}</strong></p>
        <ul class="doc-seal-hist-list">${rowsHtml}</ul>
      </div>
      <div class="composer-foot" style="justify-content:flex-end;">
        <button class="inline-btn ghost" type="button" data-doc-seal-history-close="1">閉じる</button>
      </div>
    </aside>
  `;
}

/** 申請承認一覧の「承認者」列: 経過表示。`opts.vertical` で申請内容モーダル用の縦フロー */
function sealApprovalProgressDisplayHtml(mapKey, opts) {
  const vertical = !!(opts && opts.vertical);
  const horizontal = !!(opts && (opts.horizontal || opts.stepper));
  const n = getSealApprovalProgressApprovedCount(mapKey);
  if (horizontal) {
    return sealApprovalFlowHorizontalHtml(mapKey);
  }
  const chain = getSealApprovalChainForMapKey(mapKey);
  if (!chain.length) {
    return `<span class="pj-muted">—（承認者未設定）</span>`;
  }
  if (vertical) return approvalFlowVerticalHtmlFromChain(chain, n);
  const parts = chain.map((entry, i) => {
    const name = escapeHtml(String(entry.user?.name || "—").trim() || "—");
    if (i < n) return `<span class="app-mgmt-seal-appr app-mgmt-seal-appr--done">${name}（承認済）</span>`;
    if (i === n) return `<span class="app-mgmt-seal-appr app-mgmt-seal-appr--current">${name}（承認待ち）</span>`;
    return `<span class="app-mgmt-seal-appr app-mgmt-seal-appr--later">${name}（未）</span>`;
  });
  return `<div class="app-mgmt-seal-appr-row">${parts.join('<span class="app-mgmt-seal-appr-arrow" aria-hidden="true">→</span>')}</div>`;
}

/** `projectId:docTab` 形式のキーを分解（捺印 UI 共通） */
function parseDocSealKey(raw) {
  const s = String(raw || "");
  const sep = s.indexOf(":");
  if (sep < 1) return null;
  return { pid: s.slice(0, sep), tab: s.slice(sep + 1) };
}

/** 捺印申請・承認フローを持つ書類タブか（対象一覧は 00-shared-enums の DOC_SEAL_TAB_LABELS） */
function docTabHasSealWorkflow(docTab) {
  return Object.prototype.hasOwnProperty.call(DOC_SEAL_TAB_LABELS, docTab);
}

/** 書類に載せる支払条件。クライアントマスタの支払条件をそのまま使う（未設定ならマスタの既定値）。
 *  以前は発注書・納品書・検収書だけ「月末締翌々月末払」を直書きしていたが、
 *  この文言は支払条件マスタの選択肢に存在しない値だったため、全種別でマスタ参照に揃えた。 */
function projectDocPaymentTermsText(project) {
  const terms = String(resolveProjectClientMaster(project)?.paymentTerms || "").trim();
  return terms || PAYMENT_TERMS_OPTIONS[0];
}

/** 画面上部の捺印ステータスバー（捺印対象の書類タブのみ `formalDocOuterWrap` から利用） */
function formalSealBannerHtml(projectId, docTab) {
  const st = getDocSealStatus(projectId, docTab);
  const mapKey = docSealStateKey(projectId, docTab);
  const widgetTone = st === "承認済" ? "approved" : st === "申請中" ? "pending" : "draft";
  const dk = `${escapeHtmlAttr(projectId)}:${escapeHtmlAttr(docTab)}`;
  let headBtns = "";
  if (st === "承認済") {
    headBtns = `<button type="button" class="doc-seal-btn doc-seal-btn--history" data-doc-seal-history="${dk}">申請履歴</button>`;
  } else if (st === "申請中") {
    headBtns = `<button type="button" class="doc-seal-btn doc-seal-btn--outline" data-doc-seal-cancel="${dk}">申請取消</button><button type="button" class="doc-seal-btn doc-seal-btn--history" data-doc-seal-history="${dk}">申請履歴</button>`;
  } else {
    headBtns = `<button type="button" class="doc-seal-btn doc-seal-btn--apply" data-doc-seal-apply="${dk}">捺印申請</button>`;
  }
  const flowLabel = escapeHtml(getSealApprovalFlowLabelForMapKey(mapKey));
  const entries = getSealApprovalFullDisplayChain(mapKey);
  const flowHtml = entries.length
    ? sealApprovalFlowHorizontalHtml(mapKey)
    : `<p class="doc-seal-flow-empty">承認者が未設定です</p>`;
  const rejectInfo = st === "未申請" ? getDocSealRejectInfo(projectId, docTab) : null;
  const rejectNoticeHtml = rejectInfo
    ? `<p class="doc-seal-widget-flow-lbl" style="color:#b42318;">前回の申請は却下されました：${escapeHtml(rejectInfo.reason)}</p>`
    : "";
  // 請求書タブのみ: invoiceStatus と合わせた複合ラベルを併記し、案件一覧の状態表示と揃える（見積・領収書等には invoiceStatus 相当が無いため対象外）
  const combinedProject = docTab === "invoice" ? projects.find((x) => x.id === projectId) : null;
  const combinedSuffix = combinedProject
    ? `<span class="doc-seal-widget-head-combined">（請求ステータス: ${escapeHtml(combinedInvoiceStatusLabel(combinedProject).label)}）</span>`
    : "";
  return `
      <div class="doc-seal-widget doc-seal-widget--${widgetTone}" role="region" aria-label="捺印申請状況">
        <div class="doc-seal-widget-head">
          <span class="doc-seal-widget-head-label">捺印申請状況 - ${escapeHtml(st)}${combinedSuffix}</span>
          <div class="doc-seal-widget-head-right">
            <span class="doc-seal-widget-deadline">捺印期限：${escapeHtml(sealDeadlineText(mapKey))}</span>
            ${headBtns}
          </div>
        </div>
        <div class="doc-seal-widget-body">
          ${sealRevokeNoticeHtml(mapKey)}
          ${rejectNoticeHtml}
          <p class="doc-seal-widget-flow-lbl">申請フロー：${flowLabel}</p>
          <div class="doc-seal-widget-flow">${flowHtml}</div>
        </div>
      </div>`;
}

/** PDF 用（ボタンなし／状況表示のみ） */
function formalSealBannerPdfHtml(projectId, docTab) {
  const st = getDocSealStatus(projectId, docTab);
  const tone =
    st === "承認済"
      ? "doc-seal-banner--approved"
      : st === "申請中"
        ? "doc-seal-banner--pending"
        : "doc-seal-banner--draft";
  return `
      <div class="doc-seal-banner ${tone} doc-seal-banner--static">
        <span class="doc-seal-banner-label">捺印申請状況 - ${escapeHtml(st)}</span>
      </div>`;
}

function formalDocOuterWrap(projectId, docTab, sheetInnerHtml) {
  const banner = docTabHasSealWorkflow(docTab) ? formalSealBannerHtml(projectId, docTab) : "";
  return `<div class="doc-formal-wrap">${banner}${sheetInnerHtml}</div>`;
}

/** PDF／印刷ウィンドウ用。捺印申請バナーは印刷範囲に含めず、請求書タイトル（doc-sheet）先頭から出力する */
function formalDocPdfWrap(projectId, docTabKey, sheetInnerHtml) {
  void projectId;
  void docTabKey;
  return `<div class="doc-formal-wrap doc-formal-wrap--pdf-print">${sheetInnerHtml}</div>`;
}

/** 共通明細テーブル tbody 内で `linesFooterHtml` の直前に入れる空白行 HTML。
 *  本数は formalDocBlankPadCount（00-shared-enums.js・サーバと共用）で1ページ上限まで。 */
function formalDocLinesBlankPaddingHtml(bodyRowCount) {
  const td = '<td class="doc-inv-line-blank-pad-cell">&nbsp;</td>';
  let html = "";
  const count = formalDocBlankPadCount(bodyRowCount);
  for (let r = 0; r < count; r += 1) {
    html += `<tr class="doc-inv-line-blank-pad" aria-hidden="true">${td}${td}${td}${td}${td}</tr>`;
  }
  return html;
}

/** 請求〜領収など共通の書式ブロック本体（バナー以外） */
function formalSheetInnerHtml(payload) {
  const {
    titleSpread,
    notePhrase,
    docNoStr,
    dateLabel,
    dateStrVal,
    dateCellHtml = "",
    recipientName,
    recipientAddrStr,
    recipientContactExtraHtml,
    recipientHonorificSuffix = "　御中",
    issuerRightHtml,
    metaInnerRowsHtml,
    totalPlainYen,
    linesBodyHtml,
    linesFooterHtml,
    remarkHtmlEscaped,
    remarkEditable,
    remarkProjectId,
    remarkTab,
    remarkText
  } = payload;
  // 空白行の本数は「実際に描画する本文行数」から導出（呼び出し元の数え漏れで空白過多→2枚化する事故を防ぐ。
  // セル内テキストは全て escapeHtml 済みなので "<tr" がデータ由来で現れることはない）
  const linesBodyRowCount = (String(linesBodyHtml || "").match(/<tr[\s>]/g) || []).length;
  const remarkSection =
    remarkEditable && remarkProjectId && remarkTab != null && remarkTab !== ""
      ? `<div class="doc-inv-remark-box">
          <div class="doc-inv-remark-cap">備考</div>
          <textarea class="doc-inv-remark-field" rows="5" data-doc-remark-edit="1" data-project="${escapeHtmlAttr(String(remarkProjectId))}" data-doc-tab="${escapeHtmlAttr(String(remarkTab))}">${escapeHtml(remarkText || "")}</textarea>
        </div>`
      : `<div class="doc-inv-remark-box">
          <div class="doc-inv-remark-cap">備考</div>
          <div class="doc-inv-remark-text">${remarkHtmlEscaped || ""}</div>
        </div>`;
  return `
      <div class="doc-sheet doc-sheet--formal">
        <h1 class="doc-inv-page-title">${titleSpread}</h1>
        <div class="doc-inv-head">
          <div class="doc-inv-head-left${recipientAddrStr ? " doc-inv-head-left--recipient" : ""}">
            <div class="doc-inv-addr-block">
              <p class="doc-inv-customer">${escapeHtml(recipientName)}${recipientHonorificSuffix ? escapeHtml(recipientHonorificSuffix) : ""}</p>
              ${recipientAddrStr ? `<p class="doc-inv-addr-sub">${String(recipientAddrStr).split("\n").map(escapeHtml).join("<br/>")}</p>` : ""}
              ${recipientContactExtraHtml || ""}
            </div>
            <p class="doc-inv-lead">${escapeHtml(notePhrase)}</p>
          </div>
          <div class="doc-inv-head-right">
            <table class="doc-inv-no-date-table">
              <tbody>
                <tr><th>No</th><td>${escapeHtml(docNoStr)}</td></tr>
                <tr><th>${escapeHtml(dateLabel)}</th><td>${dateCellHtml || escapeHtml(dateStrVal)}</td></tr>
              </tbody>
            </table>
            ${issuerRightHtml}
          </div>
        </div>
        <table class="doc-inv-meta-total-table">
          <tbody>
            ${metaInnerRowsHtml}
            <tr class="doc-inv-meta-gap"><td colspan="2"></td></tr>
            <tr class="doc-inv-total-money-row">
              <th class="doc-inv-total-lbl">合計金額</th>
              <td class="doc-inv-total-val"><strong>${escapeHtml(String(totalPlainYen ?? ""))}</strong>　円（税込）</td>
            </tr>
          </tbody>
        </table>
        <table class="doc-inv-lines-table">
          <colgroup>
            <col class="doc-inv-col-sum" /><col class="doc-inv-col-qty" /><col class="doc-inv-col-unit" /><col class="doc-inv-col-price" /><col class="doc-inv-col-amt" />
          </colgroup>
          <thead>
            <tr><th>摘要</th><th>数量</th><th>単位</th><th>単価</th><th>金額</th></tr>
          </thead>
          <tbody>
            ${linesBodyHtml}
            ${formalDocLinesBlankPaddingHtml(linesBodyRowCount)}
            ${linesFooterHtml}
          </tbody>
        </table>
        ${remarkSection}
      </div>`;
}

/** 印刷/PDF と画面で共有できる自社ロゴブロック（登録番号の直上に担当者名）
 *  showSeal=true のとき、捺印申請が承認済みの書類に会社印（社印）を重ねて表示する。 */
function issuerOurBrandHtml(OUR_CO, ADDR_HTML, OUR_TEL, OUR_REG, staffName, showSeal) {
  const staff =
    staffName != null && String(staffName).trim()
      ? `<p class="doc-inv-brand-staff">担当者：${escapeHtml(String(staffName).trim())}</p>`
      : "";
  const sealImg = showSeal
    ? `<img src="icons/company-seal.png" alt="社印" class="doc-inv-seal-stamp" />`
    : "";
  return `
            <div class="doc-inv-brand-block doc-inv-brand-block--ours">
              <img src="icons/oelogo.png" alt="one." class="doc-inv-brand-logo-img" />
              <p class="doc-inv-brand-company">${escapeHtml(OUR_CO)}</p>
              <p class="doc-inv-brand-info">${ADDR_HTML}<br />${escapeHtml(OUR_TEL)}</p>
              ${staff}
              <p class="doc-inv-brand-info doc-inv-brand-regline">${escapeHtml(OUR_REG)}</p>
              ${sealImg}
            </div>`;
}

/** 見積書の有効期限の既定文言（自由文。サーバ側 api/_docRender.js QUOTE_VALID_THRU_DEFAULT と両側ペア） */
const QUOTE_VALID_THRU_DEFAULT = "お見積り後2週間";

/** 納期（YYYY-MM-DD）の初期値: 案件の納期 → 他の書類タブで入力済みの納期（見積→発注→納品→検収の順）。
 *  見積書で納期を入れたら納品書・検収書などにも自動で反映させるため。
 *  サーバ側 api/_docRender.js resolveDocDeliverFields の allMeta フォールバックと両側ペア */
const DOC_DELIVER_BY_FALLBACK_TABS = ["quote", "order", "delivery", "inspection"];
function projectDocDeliverByFallback(draft, p) {
  const due = String(p?.dueDate || "").trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(due)) return due;
  const meta = draft?.meta || {};
  for (const t of DOC_DELIVER_BY_FALLBACK_TABS) {
    const v = String(meta[t]?.deliverBy || "").trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  }
  return "";
}

/** 書類の支払条件セレクト（見積書・発注書）。初期値はクライアントマスタの支払条件、選択は書類メタ paymentTerms に保存。
 *  サーバ側（doc-pdf / _docSend / doc-mail）は meta.paymentTerms → クライアントマスタ の順で参照する */
function docMetaPaymentTermsSelectHtml(p, dm, projAttr, tabAttr) {
  const cur = String(dm?.paymentTerms || "").trim() || projectDocPaymentTermsText(p);
  const opts = PAYMENT_TERMS_OPTIONS.includes(cur) ? PAYMENT_TERMS_OPTIONS : [cur, ...PAYMENT_TERMS_OPTIONS];
  return `<select class="doc-line-input doc-inv-meta-input doc-inv-meta-select" data-search-dropdown-off="1" data-doc-meta="1" data-doc-meta-field="paymentTerms" data-project="${projAttr}" data-doc-tab="${tabAttr}" aria-label="支払条件">${opts
    .map((o) => `<option value="${escapeHtmlAttr(o)}" ${o === cur ? "selected" : ""}>${escapeHtml(o)}</option>`)
    .join("")}</select>`;
}

/** 書類種別ごと PDF でも使う共通定数ブロックを返す */
function formalIssuerConstants() {
  return {
    CO: `株式会社ワンエイティ`,
    ADDR_LINES: `〒107-0062<br/>東京都港区南青山3-3-3<br/>リビエラ南青山ビルA EN 202`,
    TEL: `TEL：03-6411-3387`,
    REG: `登録番号：T4010901039537`,
    BANK_DEF: `りそな銀行 渋谷支店 普通 3478199`
  };
}

/** 書類プレビュー画面のドラフトから、printDocument 用の doc オブジェクトを組み立てる */
function buildDocFromProjectDraft(p, tab) {
  const draft = ensureProjectDocDraft(p.id);
  const dm = draft.meta[tab] || {};
  // 正式PDF（api/_docRender.js printedDocLines）と同じく、摘要か金額のある通常行を印字する。
  // 数量が空なら1（画面・PDFと同じ）。旧実装は空数量を0にして確認プレビューだけ金額が0になっていた
  const lines = (draft.lines || [])
    .filter((l) => (l?.lineType || "normal") === "normal")
    .filter((l) => String(l.item || "").trim() || billingLineAmountExTax(l) !== 0)
    .map((l) => {
      const qty = parseBillingNumber(l.qty);
      return {
        item: String(l.item || "").trim() || "—",
        qty: Number.isFinite(qty) ? qty : 1,
        unit: String(l.unit || ""),
        price: parseBillingNumber(l.price) || 0
      };
    });
  const today = new Date();
  let issue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const pinv = String(p.plannedInvoiceDate || "").trim().slice(0, 10);
  const pdu = String(p.dueDate || "").trim().slice(0, 10);
  const qIssue = String(dm.issueDate || "").trim().slice(0, 10);
  if ((tab === "invoice" || tab === "receipt") && /^\d{4}-\d{2}-\d{2}$/.test(pinv)) issue = pinv;
  else if ((tab === "order" || tab === "delivery" || tab === "inspection") && /^\d{4}-\d{2}-\d{2}$/.test(pdu)) issue = pdu;
  else if (tab === "quote" && /^\d{4}-\d{2}-\d{2}$/.test(qIssue)) issue = qIssue;
  const due =
    tab === "quote"
      ? String(dm.validThru || "").trim() || QUOTE_VALID_THRU_DEFAULT
      : tab === "invoice"
        ? String(dm.due || "").trim() || issue
        : issue;
  const FI = formalIssuerConstants();
  return {
    company: clientName(p),
    person: clientContact(p),
    subject: p.name || "",
    issueDate: issue,
    dueDate: due,
    note: resolveDocRemark(draft.remark, tab),
    bankMemo: FI.BANK_DEF,
    taxRate: 0.1,
    lines: lines.length ? lines : [{ item: "（明細なし）", qty: 0, unit: "", price: 0 }]
  };
}

/** 書類プレビュー下部：送付状/社印・PDF・メール */
function projectDocPreviewFooterHtml(p, activeTab, dm) {
  const sealOk =
    !docTabHasSealWorkflow(activeTab) || getDocSealStatus(p.id, activeTab) === "承認済";
  // 送付（正式PDF発行・メール送信・郵送）をブロックするか。sealOk とは別軸：見積書は
  // 捺印申請の仕組み自体はあるが未承認でも送付できる（docTypeBlocksSendWhenUnapproved）ため、
  // 「社印が付くか」（sealOk）と「送れるか」（sendBlocked）を分けている。
  const sendBlocked = docTabHasSealWorkflow(activeTab) && docTypeBlocksSendWhenUnapproved(activeTab) && !sealOk;
  const projAttr = escapeHtmlAttr(p.id);
  const tabAttr = escapeHtmlAttr(activeTab);
  // 社印は承認済みなら自動で押される（07-pdf-output.js の showSealOnDoc / showSealOnPdf）ため、
  // チェックボックスは操作項目ではなく状態表示。誤って外して押印なしPDFが出る事故を防ぐため常に disabled。
  const sealTitle = sealOk
    ? ` title="捺印申請が承認済みのため、常に社印付きで出力されます"`
    : ` title="捺印申請未承認のため社印付きで出力できません。"`;
  const covChecked = dm.printIncludeCover ? " checked" : "";
  const sealChecked = sealOk ? " checked" : "";
  const pdfKey = escapeHtmlAttr(`${p.id}:${activeTab}`);
  // 書類種別ラベル（発行履歴ボタンのツールチップ用）。未定義参照を防ぐ。
  const label = ({ invoice: "請求書", quote: "見積書", order: "発注書", acceptance: "発注請書", delivery: "納品書", inspection: "検収書", receipt: "領収書", cover: "送付状" })[activeTab] || "書類";
  // 正式PDF発行/メール: 送付ブロック対象かつ捺印未承認時はグレーアウト（disabled）。確認プレビューは承認状態を問わずいつでも押せる
  const actionDisabled = sendBlocked ? " disabled" : "";
  const actionTitle = sendBlocked ? ` title="捺印申請が承認されると正式PDF発行・メール送信が可能になります"` : "";
  // 正式PDFの作成中（16-render-routing.js の data-doc-footer-official が uiState._docOfficialPdfInFlight に「案件:書類」を入れる）
  const pdfBusy = uiState._docOfficialPdfInFlight === `${p.id}:${activeTab}`;
  const actionHint = sendBlocked
    ? `<p class="doc-preview-footer__action-hint">捺印申請の承認後に正式PDF発行・メール送信が使えます</p>`
    : pdfBusy
      ? `<p class="doc-preview-footer__action-hint" role="status">PDFを作成しています。数秒〜十数秒かかることがあります</p>`
      : "";
  // 郵送ボタンの状態表示（invoiceタブのみ）。受付済み〜印刷手配中の間は準備中扱いで二重発送を防ぐ
  const mailStatus = activeTab === "invoice" ? getDocMailStatus(p.id, "invoice") : null;
  const mailPending = mailStatus && DOC_MAIL_PENDING_STATUSES.includes(mailStatus);
  const postalLabel = mailPending ? docPostalStatusLabel({ status: mailStatus }) : "郵送する";
  const postalDisabled = sendBlocked || mailPending ? " disabled" : "";
  const postalTitle = mailPending
    ? ` title="郵送準備中です（${escapeHtmlAttr(postalLabel)}）。発送が完了すると再度押せるようになります"`
    : actionTitle;
  return `
    <footer class="doc-preview-footer" role="contentinfo">
      ${docPageSaveBarHtml(p, activeTab)}
      <div class="doc-preview-footer__vsep" aria-hidden="true"></div>
      <div class="doc-preview-footer__checks">
        <label class="doc-preview-footer__check">
          <input type="checkbox" class="doc-preview-footer__chk"${covChecked} data-doc-meta="1" data-doc-meta-field="printIncludeCover" data-project="${projAttr}" data-doc-tab="${tabAttr}" />
          <span>送付状</span>
        </label>
        ${docTypeShowsSealStamp(activeTab) ? `<label class="doc-preview-footer__check${!sealOk ? " doc-preview-footer__check--disabled" : ""}">
          <input type="checkbox" class="doc-preview-footer__chk"${sealChecked} disabled${sealTitle} />
          <span>社印</span>
        </label>` : ""}
        ${activeTab === "invoice" ? `<label class="doc-preview-footer__check" title="チェックすると、翌月に案件一覧の「自動作成設定」（毎月指定日に自動複製）やSlackへの指示で、この請求書（案件・明細）が翌月分として一括複製されます">
          <input type="checkbox" class="doc-preview-footer__chk"${p.recurringBilling === true ? " checked" : ""} data-recurring-billing-toggle="${projAttr}" />
          <span>毎月請求</span>
        </label>` : ""}
      </div>
      <div class="doc-preview-footer__vsep" aria-hidden="true"></div>
      <div class="doc-preview-footer__actions">
        ${actionHint}
        <button type="button" class="doc-preview-footer-btn" data-doc-footer-preview="${pdfKey}" title="レイアウト確認用のプレビュー（発行履歴には記録されません）">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
          <span>確認プレビュー</span>
        </button>
        <span class="doc-preview-footer__btn-vsep" aria-hidden="true"></span>
        <button type="button" class="doc-preview-footer-btn${sendBlocked ? " doc-preview-footer-btn--disabled" : ""}${pdfBusy ? " doc-preview-footer-btn--busy" : ""}"${actionDisabled}${actionTitle}${pdfBusy ? ' aria-busy="true" aria-disabled="true"' : ""} data-doc-footer-official="${pdfKey}">
          ${pdfBusy
            ? `<span class="doc-pdf-spinner" aria-hidden="true"></span><span>PDFを作成中…</span>`
            : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          <span>正式PDFを発行</span>`}
        </button>
        <span class="doc-preview-footer__btn-vsep" aria-hidden="true"></span>
        <button type="button" class="doc-preview-footer-btn${sendBlocked ? " doc-preview-footer-btn--disabled" : ""}"${actionDisabled}${actionTitle} data-doc-footer-mail="${pdfKey}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="m22 6-10 7L2 6"/></svg>
          <span>メール送信</span>
        </button>
        ${activeTab === "invoice" ? `<span class="doc-preview-footer__btn-vsep" aria-hidden="true"></span>
        <button type="button" class="doc-preview-footer-btn${sendBlocked || mailPending ? " doc-preview-footer-btn--disabled" : ""}"${postalDisabled}${postalTitle} data-doc-footer-postal="${pdfKey}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.5 12H16c-.7 2-2 3-4 3s-3.3-1-4-3H2.5"/><path d="M5.5 5.1L2 12v6c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2v-6l-3.4-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.8 1.1z"/></svg>
          <span>${escapeHtml(postalLabel)}</span>
        </button>` : ""}
      </div>
      <button type="button" class="doc-preview-footer-hist-btn" data-open-invoice-issue-history="${pdfKey}" title="${escapeHtmlAttr(label)}の発行履歴">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <span>発行履歴</span>
      </button>
    </footer>`;
}

function projectDocsPage(id, tab = "quote") {
  const p = projects.find((x) => x.id === id);
  if (!p) return projectMissingPageHtml("書類作成・編集");
  const activeTab = tab || "quote";
  // 書類メタをサーバーからロード（初回のみ・非同期で差分マージ後再描画）
  void loadDocMetaFromServer(p.id);
  // 郵送ボタンの状態表示（invoiceタブのみ。初回・再取得トリガー時のみ・非同期で反映後再描画）
  if (activeTab === "invoice") void ensureDocMailStatusLoaded(p);
  const draft = ensureProjectDocDraft(p.id);
  const lines = draft.lines || [];
  const lineTypeOf = (line) => line?.lineType || "normal";
  const isNormalLine = (line) => lineTypeOf(line) === "normal";
  const calcAmt = (l) => (isNormalLine(l) ? billingLineAmountExTax(l) : 0);
  const subtotal = lines.reduce((s, l) => s + calcAmt(l), 0);
  const tax = billingTaxYen(subtotal);
  const total = subtotal + tax;
  const client = clients.find((c) => c.id === p.clientId);
  const clientTo = client?.name || "〇〇〇株式会社";
  // 案件で選んだ担当者(p.person)を優先し、無ければクライアントマスタの担当者名（clientContact(p)関数と同じ優先順位）。
  // 変数名がグローバル関数 clientContact(project) と同名（意図的なシャドウイングではない旧実装）。
  // trim はサーバ（api/_docAddress.js の String(personName||'').trim() || clientContactName）と条件を揃えるため。
  // client_person は自由記入テキストの時期があり、空白だけの値だと画面は空の担当者行＋「様」を出す一方、
  // 実PDFはクライアントマスタの担当者名を出す＝宛名の行が食い違う。PDF経路（上の recipientContactName）と同じ形。
  const clientContact = String(p.person || "").trim() || String(client?.contactName || "").trim();
  const clientIsIndividual = isIndividualHonorific(client?.honorific);
  const clientHonorificSuffix = `　${resolveRecipientHonorific(client?.honorific)}`;
  const today = new Date();
  const dateStrToday = `${today.getFullYear()}年${String(today.getMonth()+1).padStart(2,"0")}月${String(today.getDate()).padStart(2,"0")}日`;
  // 見積書の見積日の既定（quote 初期化ブロックで使う）。使う箇所より前＝ここで宣言する
  const todayIsoYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const docNo = p.dbId || p.id.replace(/\D/g, "") || "00000";

  const OUR_COMPANY = `株式会社ワンエイティ`;
  const OUR_ADDR = `〒107-0062<br/>東京都港区南青山3-3-3<br/>リビエラ南青山ビルA EN 202`;
  const OUR_TEL = `TEL：03-6411-3387`;
  const OUR_REG = `登録番号：T4010901039537`;
  const OUR_BANK = `りそな銀行 渋谷支店 普通 3478199`;
  const staffNameForDocs = salesOwnerUser(p)?.name || currentUser.name || "";

  // PC：行ホバーで行の真上に浮くツールバー（▼種別/≡移動/＋追加/🗑削除）
  // SP：⋮ボタン常時表示→タップでボトムシートを開く（横スクロール不要・直感的）
  const rowbar = (i, t) => `<div class="doc-inv-rowbar" contenteditable="false">
    <select class="doc-inv-rowbar-type" data-detail-line-type-change="${i}" data-detail-project="${p.id}" title="行の種別（通常／見出し／小計）" data-search-dropdown-off="1">
      <option value="normal"${t === "normal" ? " selected" : ""}>通常</option>
      <option value="heading"${t === "heading" ? " selected" : ""}>見出し</option>
      <option value="subtotal"${t === "subtotal" ? " selected" : ""}>小計</option>
    </select>
    <span class="doc-inv-rowbar-btn doc-inv-rowbar-btn--drag doc-line-drag-handle" draggable="true" data-detail-line-drag="${i}" data-detail-project="${p.id}" role="button" tabindex="0" title="ドラッグで上下移動" aria-label="移動">
      <span class="doc-line-drag-line" aria-hidden="true"></span><span class="doc-line-drag-line" aria-hidden="true"></span><span class="doc-line-drag-line" aria-hidden="true"></span>
    </span>
    <button type="button" class="doc-inv-rowbar-btn doc-inv-rowbar-btn--add" data-detail-line-insert-below="${i}" data-detail-project="${p.id}" title="下に行を追加" aria-label="下に行を追加">＋</button>
    <button type="button" class="doc-inv-rowbar-btn doc-inv-rowbar-btn--del" data-detail-line-remove="${i}" data-detail-project="${p.id}" title="この行を削除" aria-label="削除">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
    </button>
  </div>`;
  // SP用：摘要セルに ⋮ ボタン（PCでは display:none）
  const spMenuBtn = (i) => `<button type="button" class="doc-inv-sp-menu-btn" data-inv-line-menu="${i}" data-detail-project="${p.id}" aria-label="行の操作メニュー">⋮</button>`;

  // SP用ボトムシート（uiState.invDocLineMenu が設定された行のみ展開）
  const menuState = uiState.invDocLineMenu;
  const spMenuSheet = (menuState && menuState.pid === p.id) ? (() => {
    const mi = menuState.index;
    const ml = lines[mi];
    const mt = ml ? lineTypeOf(ml) : "normal";
    const isFirst = mi === 0;
    const isLast = mi === lines.length - 1;
    return `<div class="doc-inv-sp-menu-backdrop" data-inv-line-menu-close="1"></div>
    <div class="doc-inv-sp-menu-sheet" role="dialog" aria-modal="true" aria-label="行の操作">
      <div class="doc-inv-sp-menu-header">
        <span class="doc-inv-sp-menu-title">行の操作</span>
        <button type="button" class="doc-inv-sp-menu-close" data-inv-line-menu-close="1" aria-label="閉じる">✕</button>
      </div>
      <div class="doc-inv-sp-menu-body">
        <div class="doc-inv-sp-menu-row">
          <label class="doc-inv-sp-menu-label">行の種別</label>
          <select class="doc-inv-sp-menu-sel" data-detail-line-type-change="${mi}" data-detail-project="${p.id}" data-search-dropdown-off="1">
            <option value="normal"${mt === "normal" ? " selected" : ""}>通常</option>
            <option value="heading"${mt === "heading" ? " selected" : ""}>見出し</option>
            <option value="subtotal"${mt === "subtotal" ? " selected" : ""}>小計</option>
          </select>
        </div>
        <div class="doc-inv-sp-menu-actions">
          <button type="button" class="doc-inv-sp-menu-action" data-detail-line-move="-1" data-detail-line-index="${mi}" data-detail-project="${p.id}"${isFirst ? " disabled" : ""}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>上へ移動
          </button>
          <button type="button" class="doc-inv-sp-menu-action" data-detail-line-move="1" data-detail-line-index="${mi}" data-detail-project="${p.id}"${isLast ? " disabled" : ""}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>下へ移動
          </button>
          <button type="button" class="doc-inv-sp-menu-action" data-detail-line-insert-below="${mi}" data-detail-project="${p.id}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>下に行を追加
          </button>
          <button type="button" class="doc-inv-sp-menu-action doc-inv-sp-menu-action--del" data-detail-line-remove="${mi}" data-detail-project="${p.id}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>この行を削除
          </button>
        </div>
      </div>
    </div>`;
  })() : "";

  const invEditableRows = lines.map((l, i) => {
    const type = lineTypeOf(l);
    if (type === "heading") {
      return `<tr class="doc-detail-line-row doc-inv-edit-row" data-detail-line-drop="${i}" data-detail-project="${p.id}">
        <td colspan="5" class="doc-inv-line-heading doc-inv-rowbar-host">${spMenuBtn(i)}<input class="doc-inv-cell doc-inv-cell--heading" data-detail-line-field="item" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.item || "")}" placeholder="見出し" />${rowbar(i, "heading")}</td>
      </tr>`;
    }
    if (type === "subtotal") {
      return `<tr class="doc-detail-line-row doc-inv-edit-row" data-detail-line-drop="${i}" data-detail-project="${p.id}">
        <td colspan="4" class="doc-inv-line-subrow" style="text-align:right;font-weight:600;">小計</td>
        <td class="doc-inv-td-amt doc-inv-rowbar-host" data-detail-billing-subtotal-value="1">${formatYenPlain(projectDetailBillingSubtotalUntil(lines, i))}${rowbar(i, "subtotal")}</td>
      </tr>`;
    }
    return `<tr class="doc-detail-line-row doc-inv-edit-row" data-detail-line-drop="${i}" data-detail-project="${p.id}">
      <td class="doc-inv-td-sum" style="position:relative;">${spMenuBtn(i)}<input class="doc-inv-cell doc-inv-cell--sp-indent" data-detail-line-field="item" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.item || "")}" placeholder="摘要" /></td>
      <td class="doc-inv-td-qty"><input class="doc-inv-cell doc-inv-cell--c" data-detail-line-field="qty" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(String(l.qty ?? ""))}" /></td>
      <td class="doc-inv-td-unit"><input class="doc-inv-cell doc-inv-cell--c" data-detail-line-field="unit" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.unit || "")}" /></td>
      <td class="doc-inv-td-unitprice"><input class="doc-inv-cell doc-inv-cell--r" data-detail-line-field="price" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(String(l.price ?? ""))}" /></td>
      <td class="doc-inv-td-amt doc-inv-rowbar-host" data-detail-billing-line-amount="1">${formatYenPlain(calcAmt(l))}${rowbar(i, "normal")}</td>
    </tr>`;
  }).join("");
  // 入力すると行が増える「ゴースト行」（常に末尾に1行・5列のまま）
  const ghostRow = `<tr class="doc-inv-ghost-row">
    <td class="doc-inv-td-sum"><input class="doc-inv-cell doc-inv-cell--ghost" data-doc-ghost-line="${p.id}" data-ime-search value="" placeholder="＋ 摘要を入力して行を追加" /></td>
    <td class="doc-inv-td-qty"></td><td class="doc-inv-td-unit"></td><td class="doc-inv-td-unitprice"></td><td class="doc-inv-td-amt"></td>
  </tr>`;
  const invoiceLinesBody = invEditableRows + ghostRow;

  const invoiceLinesFooter = `
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">${activeTab === "invoice" ? "10%対象（税抜）" : "小計（税抜）"}</td>
      <td class="doc-inv-foot-amt" data-doc-foot-subtotal="1">${formatYenPlain(subtotal)}</td>
    </tr>
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">消費税（10%）</td>
      <td class="doc-inv-foot-amt" data-doc-foot-tax="1">${formatYenPlain(tax)}</td>
    </tr>
    <tr class="doc-inv-foot-row">
      <td colspan="2" class="doc-inv-foot-blank"></td>
      <td colspan="2" class="doc-inv-foot-label">合計（税込）</td>
      <td class="doc-inv-foot-amt doc-inv-foot-amt-strong" data-doc-foot-total="1">${formatYenPlain(total)}</td>
    </tr>
  `;

  // 表示順は業務の流れ（見積 → 発注 → 納品 → 請求）。既定で開くタブは従来どおり請求書
  const tabs = [
    { key: "quote", label: "見積書" },
    { key: "order", label: "発注書" },
    { key: "acceptance", label: "発注請書" },
    { key: "delivery", label: "納品書" },
    { key: "inspection", label: "検収書" },
    { key: "invoice", label: "請求書" },
    { key: "receipt", label: "領収書" },
    { key: "cover", label: "送付状" }
  ];

  if (!draft.meta[activeTab]) draft.meta[activeTab] = {};
  const dm = draft.meta[activeTab];
  if (dm.printIncludeCover === undefined) dm.printIncludeCover = false;
  const sealApproved = docTabHasSealWorkflow(activeTab) && getDocSealStatus(p.id, activeTab) === "承認済";
  if (activeTab === "cover" && dm.introText === undefined) {
    dm.introText = "下記の通り、書類を送付させて頂きますので、よろしくお願い申し上げます。";
  }
  if (activeTab === "invoice") {
    // 支払期限が未設定なら、クライアントの支払条件×請求月から自動計算（手修正可）。
    // 請求月が未設定な新規案件（案件作成時は請求月を聞かないため）は、
    // 今月を締め月とみなして計算する（手修正できる前提のデフォルト値なので今月基準で十分）。
    // 案件の納期は作業の期限なので、支払条件から計算できない時だけ使う（サーバ api/_docRender.js resolveDocDueDate と両側ペア）
    if (!String(dm.due || "").trim()) {
      const cl = resolveProjectClientMaster(p);
      const closingMonth = p.billingMonth || (p.plannedInvoiceDate ? String(p.plannedInvoiceDate).slice(0, 7) : "") || currentMonth();
      const autoDue = computeInvoiceDueFromClientTerms(cl?.paymentTerms, closingMonth);
      if (autoDue) dm.due = autoDue;
      else if (p.dueDate) dm.due = p.dueDate;
    }
  }
  // 納期の初期値: 案件の納期 → 他の書類タブで入力済みの納期（見積書で入れた納期を納品書等にも自動反映）。
  // サーバ側 api/_docRender.js resolveDocDeliverFields と両側ペア
  const deliverByFallback = projectDocDeliverByFallback(draft, p);
  if (activeTab === "order" || activeTab === "delivery" || activeTab === "inspection") {
    if (!String(dm.deliverBy || "").trim() && deliverByFallback) dm.deliverBy = deliverByFallback;
  } else if (activeTab === "quote") {
    // 見積日はカレンダーで変更可（初回表示の日付を保存し、正式PDFも同じ日付で出す）
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dm.issueDate || ""))) dm.issueDate = todayIsoYmd;
    if (!String(dm.deliverBy || "").trim() && deliverByFallback) dm.deliverBy = deliverByFallback;
    if (!String(dm.validThru || "").trim()) dm.validThru = QUOTE_VALID_THRU_DEFAULT;
  } else if (activeTab === "acceptance") {
    // 納期は自由文。未入力なら案件の納期（→他タブの納期）を初期値に
    if (!String(dm.deliverByText || "").trim() && deliverByFallback) dm.deliverByText = deliverByFallback.replace(/-/g, "/");
  }
  const projAttr = escapeHtmlAttr(p.id);
  const tabAttr = escapeHtmlAttr(activeTab);
  const docMetaDateInput = (field, storedVal) =>
    `<input type="date" class="doc-line-input doc-inv-meta-input doc-inv-meta-date-input" data-doc-meta="1" data-doc-meta-field="${escapeHtmlAttr(field)}" data-project="${projAttr}" data-doc-tab="${tabAttr}" value="${escapeHtmlAttr(storedVal || "")}" />`;

  // 見積書の日付欄はカレンダー入力（保存値 dm.issueDate。上の quote 初期化で今日を投入済み）。
  // projAttr/tabAttr の宣言より後に置くこと（前に置くと初期化前アクセスで画面が落ちる）
  const dateStr =
    activeTab === "quote"
      ? (isoYmdToJapaneseCalendar(dm.issueDate) || dateStrToday)
      : docSheetJapaneseDateFromProject(p, activeTab, dateStrToday);
  const dateCellHtml =
    activeTab === "quote"
      ? `<input type="date" class="doc-line-input doc-inv-no-date-input" data-doc-meta="1" data-doc-meta-field="issueDate" data-project="${projAttr}" data-doc-tab="${tabAttr}" value="${escapeHtmlAttr(dm.issueDate || "")}" aria-label="見積日" />`
      : "";

  // 未入力の備考は draft に書き込まない（既定文言を見せるだけ）。未入力のタブは保存時に送られず、
  // 表示・正式PDFは同じ resolveDocRemark で既定文言を補う。他の書類の備考は「他の書類に反映」で入れる（15z-doc-page-save.js）

  const DOC_CONF = {
    invoice: { title: "請求書", dateLabel: "請求日", note: "下記の通り、御請求申し上げます。", right: "our" },
    quote: { title: "御見積書", dateLabel: "見積日", note: "下記の通り、お見積り申し上げます。", right: "our" },
    order: { title: "発注書", dateLabel: "発注日", note: "下記の通り発注致します。", right: "client" },
    // 発注請書は自社が受注側として出す（宛名＝クライアント・右は自社）。発注書とは向きが逆
    acceptance: { title: "発注請書", dateLabel: "発行日", note: "下記のとおり御注文をお請け致しました。", right: "our" },
    delivery: { title: "納品書", dateLabel: "納品日", note: "下記の通り、納品致します。", right: "our" },
    inspection: { title: "検収書", dateLabel: "検収日", note: "下記に関し、検収致しました。", right: "client" },
    receipt: { title: "領収書", dateLabel: "発行日", note: "下記、正に領収いたしました。", right: "our" },
    cover: { title: "", dateLabel: "", note: "", right: "our" }
  };

  let metaInnerRowsHtml = "";
  if (activeTab === "invoice") {
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>
<tr><th class="doc-inv-meta-th">支払期限</th><td class="doc-inv-meta-td">${docMetaDateInput("due", dm.due)}</td></tr>
<tr><th class="doc-inv-meta-th">振込先</th><td class="doc-inv-meta-td">${escapeHtml(OUR_BANK)}</td></tr>`;
  } else if (activeTab === "quote") {
    // 有効期限は「お見積り後2週間」のような自由文（既定値は quote の初期化ブロックで投入）。
    // 納期・支払条件は発注書と同じ扱い。サーバ側 api/_docRender.js の quote メタ行と一致させること
    const validThruInput = `<input type="text" class="doc-line-input doc-inv-meta-input" data-doc-meta="1" data-doc-meta-field="validThru" data-project="${projAttr}" data-doc-tab="${tabAttr}" value="${escapeHtmlAttr(dm.validThru || "")}" placeholder="${escapeHtmlAttr(QUOTE_VALID_THRU_DEFAULT)}" />`;
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>
<tr><th class="doc-inv-meta-th">納期</th><td class="doc-inv-meta-td">${docMetaDateInput("deliverBy", dm.deliverBy)}</td></tr>
<tr><th class="doc-inv-meta-th">支払条件</th><td class="doc-inv-meta-td">${docMetaPaymentTermsSelectHtml(p, dm, projAttr, tabAttr)}</td></tr>
<tr><th class="doc-inv-meta-th">有効期限</th><td class="doc-inv-meta-td">${validThruInput}</td></tr>`;
  } else if (activeTab === "order") {
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>
<tr><th class="doc-inv-meta-th">納期</th><td class="doc-inv-meta-td">${docMetaDateInput("deliverBy", dm.deliverBy)}</td></tr>
<tr><th class="doc-inv-meta-th">支払条件</th><td class="doc-inv-meta-td">${docMetaPaymentTermsSelectHtml(p, dm, projAttr, tabAttr)}</td></tr>`;
  } else if (activeTab === "acceptance") {
    // 納期は「別途ご相談」のような自由文も入るため日付ではなくテキスト。
    // 支払条件は固定文言ではなくクライアントマスタの値を引く（他の種別も同じ扱いに揃えた）。
    const deliverTextInput = `<input type="text" class="doc-line-input doc-inv-meta-input" data-doc-meta="1" data-doc-meta-field="deliverByText" data-project="${projAttr}" data-doc-tab="${tabAttr}" value="${escapeHtmlAttr(dm.deliverByText || "")}" placeholder="別途ご相談" />`;
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>
<tr><th class="doc-inv-meta-th">納期</th><td class="doc-inv-meta-td">${deliverTextInput}</td></tr>
<tr><th class="doc-inv-meta-th">支払条件</th><td class="doc-inv-meta-td">${escapeHtml(projectDocPaymentTermsText(p))}</td></tr>`;
  } else if (activeTab === "delivery" || activeTab === "inspection") {
    const placeInput = `<input type="text" class="doc-line-input doc-inv-meta-input" data-doc-meta="1" data-doc-meta-field="placeHint" data-project="${projAttr}" data-doc-tab="${tabAttr}" value="${escapeHtmlAttr(dm.placeHint || "")}" placeholder="納品場所" />`;
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>
<tr><th class="doc-inv-meta-th">納期</th><td class="doc-inv-meta-td">${docMetaDateInput("deliverBy", dm.deliverBy)}</td></tr>
<tr><th class="doc-inv-meta-th">納品場所</th><td class="doc-inv-meta-td">${placeInput}</td></tr>
<tr><th class="doc-inv-meta-th">支払条件</th><td class="doc-inv-meta-td">${escapeHtml(projectDocPaymentTermsText(p))}</td></tr>`;
  } else if (activeTab === "receipt") {
    metaInnerRowsHtml = `<tr><th class="doc-inv-meta-th">件名</th><td class="doc-inv-meta-td">${escapeHtml(p.name)}</td></tr>`;
  }

  let sheetHtml = "";
  if (activeTab === "cover") {
    if (draft.remark.cover === undefined) draft.remark.cover = "";
    const coverRemark = draft.remark.cover;
    const coverIntro = String(dm.introText ?? "");
    const coverInner = `
      <div class="doc-sheet doc-sheet--formal doc-sheet-formal-cover">
        <div class="doc-formal-cover-our">${escapeHtml(OUR_COMPANY)}<br />${OUR_ADDR.replace(/<br\/>/g, "<br />")}<br />${escapeHtml(OUR_TEL)}<br />担当者：${escapeHtml(staffNameForDocs)}</div>
        <hr class="doc-formal-cover-hr" />
        <div class="doc-formal-cover-row">
          <p class="doc-formal-cover-to"><strong>${escapeHtml(clientTo)}</strong>${escapeHtml(clientHonorificSuffix)}</p>
          <p class="doc-formal-cover-date">${escapeHtml(dateStr)}</p>
        </div>
        <h1 class="doc-inv-page-title doc-formal-cover-title">書類送付のご案内</h1>
        <textarea class="doc-formal-cover-intro-field" rows="3" data-doc-meta="1" data-doc-meta-field="introText" data-project="${projAttr}" data-doc-tab="cover">${escapeHtml(coverIntro)}</textarea>
        <p class="doc-formal-cover-ki">記</p>
        <div class="doc-inv-remark-box doc-formal-cover-remark">
          <div class="doc-inv-remark-cap">備考</div>
          <textarea class="doc-inv-remark-field" rows="5" data-doc-remark-edit="1" data-project="${projAttr}" data-doc-tab="cover">${escapeHtml(coverRemark)}</textarea>
        </div>
      </div>`;
    sheetHtml = formalDocOuterWrap(p.id, "cover", coverInner);
  } else {
    const conf = DOC_CONF[activeTab];
    const addrBr = OUR_ADDR.replace(/<br\/>/g, "<br />");
    // 捺印申請が承認済みなら社印を自動表示（チェックボックス不要）。対象種別は docTypeShowsSealStamp
    const showSealOnDoc = docTypeShowsSealStamp(activeTab) && sealApproved;
    const issuerOurHtml = issuerOurBrandHtml(OUR_COMPANY, addrBr, OUR_TEL, OUR_REG, staffNameForDocs, showSealOnDoc);
    const issuerClientHtml =
      `
            <div class="doc-inv-brand-block doc-inv-brand-block--client">
              <p class="doc-inv-brand-company">${escapeHtml(clientTo)}</p>
              ${clientContact ? `<p class="doc-inv-brand-info">${escapeHtml(clientContact)} 様</p>` : ""}
              <p class="doc-inv-brand-inkan">印</p>
            </div>`;
    const issuerRightHtml = conf.right === "client" ? issuerClientHtml : issuerOurHtml;
    const recipientName = conf.right === "client" ? OUR_COMPANY : clientTo;
    // conf.right==="client" のときは宛先が自社（検収書等）なので住所は出さない。見積書も宛先住所は載せない
    const recipientAddrStr = conf.right === "client" || activeTab === "quote" ? "" : clientRecipientAddressLine(p);
    const recipientHonorificSuffix =
      conf.right === "client"
        ? ""
        : (activeTab === "order" || activeTab === "inspection" ? "" : clientHonorificSuffix);
    // 個人（宛名が様）の場合、担当者行は同じ人への「様」二重表示になるため出さない
    const recipientContactExtraHtml =
      conf.right === "client" ? "" : (clientContact && !clientIsIndividual ? `<p class="doc-inv-addr-sub">${escapeHtml(clientContact)} 様</p>` : "");

    sheetHtml = formalDocOuterWrap(
      p.id,
      activeTab,
      formalSheetInnerHtml({
        titleSpread: conf.title,
        notePhrase: conf.note,
        // 正式PDF（api/_docRender.js docNo）と同じ「発行日の年-案件ID(3桁)」
        docNoStr: `${String(dateStr).slice(0, 4)}-${String(docNo).padStart(3, "0")}`,
        dateLabel: conf.dateLabel,
        dateStrVal: dateStr,
        dateCellHtml,
        recipientName,
        recipientAddrStr,
        recipientHonorificSuffix,
        recipientContactExtraHtml,
        issuerRightHtml,
        metaInnerRowsHtml,
        totalPlainYen: formatYenPlain(total),
        linesBodyHtml: invoiceLinesBody,
        linesFooterHtml: invoiceLinesFooter,
        remarkHtmlEscaped: "",
        remarkEditable: true,
        remarkProjectId: p.id,
        remarkTab: activeTab,
        remarkText: resolveDocRemark(draft.remark, activeTab)
      })
    );
  }

  const footerHtml = projectDocPreviewFooterHtml(p, activeTab, dm);
  sheetHtml = `<div class="doc-create-preview-slot"><div class="doc-create-preview-slot__sheet">${sheetHtml}</div>${footerHtml}</div>`;

  // 明細が1ページ上限を超えるときだけ「2枚に分かれる」ことを先に知らせる（空白行は自動で減るため、
  // 分かれる原因は実明細の行数のみ）。行数は印刷経路（正式PDF=摘要あり行／確認プレビュー=通常行）の多い方で判定。
  // 対象はPDF出力がある請求書・見積書タブのみ（他タブの出力は準備中のため警告しない）。
  const printedLineCount = Math.max(
    lines.filter((l) => isNormalLine(l)).length,
    lines.filter((l) => String(l?.item || "").trim()).length
  );
  const pageSplitNoteHtml =
    (activeTab === "invoice" || activeTab === "quote") && printedLineCount > FORMAL_DOC_ONE_PAGE_BODY_ROWS
      ? `<div class="pj-page-inline-banner doc-margin-banner"><div class="cost-alert doc-page-split-note">明細が${printedLineCount}行あるため、PDF・印刷は2枚に分かれます（1枚に収まる目安は${FORMAL_DOC_ONE_PAGE_BODY_ROWS}行まで）。</div></div>`
      : "";

  // SP版UI（640px以下専用）
  const spViewHtml = projectDocSpViewHtml(p, activeTab, draft, lines, total, subtotal, tax, dm, tabs);

  return `
    <section class="section">
      <div class="doc-page-wrap">
      <div class="doc-page-head">
        <h2>書類作成・編集</h2>
        <button class="chip-btn" data-go-detail="${p.id}">← 案件に戻る</button>
      </div>
      <div class="doc-tabs-wrap">
        <div class="doc-tabs">
          ${tabs.map((t) => `<button class="doc-tab ${activeTab === t.key ? "active" : ""}" data-project-doc-tab="${p.id}:${t.key}" type="button">${t.label}</button>`).join("")}
        </div>
      </div>
      <div class="doc-gap-3lines"></div>
      ${pageSplitNoteHtml}
      ${spViewHtml}
      ${sheetHtml}
      </div>
    </section>
    ${spMenuSheet}
  `;
}

/**
 * SP版 書類エディタ本体（640px以下専用）
 * モックアップに合わせた compact カードUIを生成する
 */
function projectDocSpViewHtml(p, activeTab, draft, lines, total, subtotal, tax, dm, tabs) {
  const pid = p.id;
  const lineTypeOf = (l) => l?.lineType || "normal";
  const calcAmt = (l) => (lineTypeOf(l) === "normal" ? billingLineAmountExTax(l) : 0);
  const fmtY = (n) => `¥${Number(n || 0).toLocaleString("ja-JP")}`;

  // ── タブストリップ ──
  const tabsHtml = tabs.map((t) =>
    `<button class="doc-sp-tab${activeTab === t.key ? " active" : ""}" type="button"
       data-project-doc-tab="${escapeHtmlAttr(pid)}:${escapeHtmlAttr(t.key)}">${escapeHtml(t.label)}</button>`
  ).join("");

  // ── ヘッダーメタ（件名・期限など） ──
  const metaItems = [{ lbl: "件名", val: p.name || "—" }];
  if (activeTab === "invoice")  metaItems.push({ lbl: "支払期限", val: dm.due ? dm.due.replace(/-/g, "/") : "—" });
  else if (activeTab === "quote")  metaItems.push({ lbl: "有効期限", val: dm.validThru ? dm.validThru.replace(/-/g, "/") : "—" });
  else if (activeTab === "acceptance") metaItems.push({ lbl: "納期", val: String(dm.deliverByText || "").trim() || "—" });
  else if (["order","delivery","inspection"].includes(activeTab)) metaItems.push({ lbl: "納期", val: dm.deliverBy ? dm.deliverBy.replace(/-/g, "/") : "—" });
  const metaHtml = metaItems.map((c) =>
    `<div class="doc-sp-meta-chip">
      <span class="doc-sp-meta-chip__lbl">${escapeHtml(c.lbl)}</span>
      <span class="doc-sp-meta-chip__val">${escapeHtml(c.val)}</span>
    </div>`
  ).join("");

  // ── 明細行 ──
  const rowMenuAttrs = (i) => `data-inv-line-menu="${i}" data-detail-project="${escapeHtmlAttr(pid)}"`;
  const linesHtml = lines.length === 0
    ? `<tr><td colspan="4" class="doc-sp-empty">明細がありません</td></tr>`
    : lines.map((l, i) => {
        const type = lineTypeOf(l);
        if (type === "heading") {
          return `<tr class="doc-sp-row doc-sp-row--heading" ${rowMenuAttrs(i)}>
            <td class="doc-sp-drag-cell"><span class="doc-sp-drag-handle" aria-hidden="true"></span></td>
            <td colspan="3" class="doc-sp-name">${escapeHtml(l.item || "")}</td>
          </tr>`;
        }
        if (type === "subtotal") {
          const stAmt = projectDetailBillingSubtotalUntil(lines, i);
          return `<tr class="doc-sp-row doc-sp-row--subtotal" ${rowMenuAttrs(i)}>
            <td class="doc-sp-drag-cell"></td>
            <td colspan="2" class="doc-sp-name doc-sp-subtotal-lbl">小計</td>
            <td class="doc-sp-amt doc-sp-subtotal-amt">${fmtY(stAmt)}</td>
          </tr>`;
        }
        const amt = calcAmt(l);
        const qtyStr = `${escapeHtml(String(l.qty ?? ""))}${escapeHtml(l.unit || "")} × ¥${(parseBillingNumber(l.price) || 0).toLocaleString()}`;
        return `<tr class="doc-sp-row" ${rowMenuAttrs(i)}>
          <td class="doc-sp-drag-cell"><span class="doc-sp-drag-handle" aria-hidden="true"></span></td>
          <td class="doc-sp-name">${escapeHtml(l.item || "")}</td>
          <td class="doc-sp-qty-price">${qtyStr}</td>
          <td class="doc-sp-amt">${fmtY(amt)}</td>
        </tr>`;
      }).join("");

  const staffName = salesOwnerUser(p)?.name || currentUser.name || "";
  const remark = String(draft.remark?.[activeTab] ?? "").trim();
  const remarkHtml = remark ? `<div class="doc-sp-remark">${escapeHtml(remark)}</div>` : "";
  // 捺印申請（PC は書類シートの上に出す。SP ではシートごと非表示のため、ここに出さないと申請できず、
  // 承認待ちの正式PDF・メール・郵送が SP から使えなかった）
  const spSealHtml = docTabHasSealWorkflow(activeTab) ? `<div class="doc-sp-seal">${formalSealBannerHtml(p.id, activeTab)}</div>` : "";

  return `
<div class="doc-sp-view">
  <div class="doc-sp-tabs-strip"><div class="doc-sp-tabs-inner">${tabsHtml}</div></div>
  <div class="doc-sp-header">
    <div class="doc-sp-header-left">${metaHtml}</div>
    <div class="doc-sp-header-right">
      <div class="doc-sp-logo-badge" aria-hidden="true">OE</div>
      <div class="doc-sp-company-name">株式会社ワンエイティ</div>
      <div class="doc-sp-company-meta">03-6411-3387${staffName ? `<br>担当: ${escapeHtml(staffName)}` : ""}</div>
    </div>
  </div>
  <div class="doc-sp-total-bar">
    <span class="doc-sp-total-lbl">合計金額（税込）</span>
    <span class="doc-sp-total-amt">${fmtY(total)}</span>
  </div>
  ${spSealHtml}
  ${activeTab === "invoice" ? `<label class="doc-sp-recurring-toggle">
    <input type="checkbox"${p.recurringBilling === true ? " checked" : ""} data-recurring-billing-toggle="${escapeHtmlAttr(pid)}" />
    <span>毎月請求（翌月も複製する）</span>
  </label>` : ""}
  <div class="doc-sp-items">
    <div class="doc-sp-items-head">
      <span class="doc-sp-items-title">明細</span>
      <button type="button" class="doc-sp-add-row-btn" data-detail-line-add-picker-open="${escapeHtmlAttr(pid)}">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>行を追加
      </button>
    </div>
    <table class="doc-sp-table">
      <thead><tr>
        <th class="doc-sp-th-drag"></th>
        <th>品名</th>
        <th class="doc-sp-th-qty">数量</th>
        <th class="doc-sp-th-amt">金額</th>
      </tr></thead>
      <tbody>${linesHtml}</tbody>
    </table>
    <div class="doc-sp-foot-totals">
      <div class="doc-sp-foot-row"><span>${activeTab === "invoice" ? "10%対象（税抜）" : "小計（税抜）"}</span><span>${fmtY(subtotal)}</span></div>
      <div class="doc-sp-foot-row"><span>消費税（10%）</span><span>${fmtY(tax)}</span></div>
      <div class="doc-sp-foot-row doc-sp-foot-row--grand"><span>合計（税込）</span><span>${fmtY(total)}</span></div>
    </div>
  </div>
  ${remarkHtml}
</div>`;
}

/** 発行履歴モーダル（編集履歴と同スタイル） */
function renderInvoiceIssueHistoryModal() {
  const m = uiState.invoiceIssueHistoryModal;
  if (!m) return "";
  const { projectId, docType } = m;
  const proj = projects.find((x) => x.id === projectId);
  const projName = proj?.name || "案件";
  // 種別ラベルは書類タブと同じ表記に合わせる（以前は見積書以外をすべて「請求書」と出していた）
  const label = DOC_MAIL_TYPE_LABELS[docType] || "書類";
  const amtLabel = docType === "invoice" ? "請求金額" : docType === "quote" ? "見積金額" : "金額";
  const key = `${projectId}:${docType}`;
  const cache = uiState.invoiceIssueHistory?.[key];

  let body;
  if (cache == null) {
    body = `<p class="peh-empty">読み込み中…</p>`;
  } else if (cache.length === 0) {
    body = `<p class="peh-empty">まだ${label}の発行・変更はありません。「正式PDFを発行」で出力するか、メールで送付・保存すると記録されます。</p>`;
  } else {
    body = cache.map((h) => {
      if (h.entryType === "edited") {
        const dt = formatIssueHistoryDateTime(h.editedAt);
        const who = escapeHtml(h.editedByName || "—");
        const changeRows = (Array.isArray(h.changes) ? h.changes : []).map((c) => `
          <li class="peh-change">
            <span class="peh-change-label">${escapeHtml(c.label || "")}</span>
            <span class="peh-change-diff">
              <span class="peh-change-before">${escapeHtml(String(c.before ?? ""))}</span>
              <span class="peh-change-arrow" aria-hidden="true">→</span>
              <span class="peh-change-after">${escapeHtml(String(c.after ?? ""))}</span>
            </span>
          </li>`).join("");
        return `<div class="peh-entry">
          <div class="peh-entry-head">
            <span class="peh-entry-dt">${dt}</span>
            <span class="peh-entry-src peh-entry-src--edit">変更</span>
            <span class="peh-entry-who">${who}</span>
          </div>
          <ul class="peh-changes">${changeRows}</ul>
        </div>`;
      }
      const dt = formatIssueHistoryDateTime(h.issuedAt);
      const who = escapeHtml(h.issuedByName || "—");
      const amt = `¥${Number(h.totalAmount || 0).toLocaleString("ja-JP")}`;
      // 改ざん防止用チェックサム（発行時点の実HTMLのSHA-256）。追加前に発行された古い記録には無い。
      const hashHtml = h.contentSha256
        ? `<li class="peh-change"><span class="peh-change-label">改ざん検知用ハッシュ</span><span class="peh-change-diff"><span class="peh-change-after peh-hash" title="${escapeHtmlAttr(h.contentSha256)}">${escapeHtml(h.contentSha256.slice(0, 12))}…</span></span></li>`
        : "";
      // 発行経路（PDF発行 / メール送付）。列の追加前に発行された古い記録には無いので出さない
      const srcLabel = ISSUE_SOURCE_LABELS[h.issueSource];
      const srcHtml = srcLabel
        ? `<span class="peh-entry-src peh-entry-src--${escapeHtmlAttr(h.issueSource)}">${escapeHtml(srcLabel)}</span>`
        : "";
      return `<div class="peh-entry">
        <div class="peh-entry-head">
          <span class="peh-entry-dt">${dt}</span>
          ${srcHtml}
          <span class="peh-entry-who">${who}</span>
        </div>
        <ul class="peh-changes">
          <li class="peh-change">
            <span class="peh-change-label">${amtLabel}</span>
            <span class="peh-change-diff">
              <span class="peh-change-after">${escapeHtml(amt)}</span>
            </span>
          </li>
          ${hashHtml}
        </ul>
      </div>`;
    }).join("");
  }

  return `
    <div class="composer-backdrop" data-close-invoice-issue-history="1"></div>
    <aside class="composer-panel fp-centered-modal invoice-issue-history-modal${spNoEnter("invoice-issue-history-modal")}" role="dialog" aria-modal="true" aria-labelledby="iih-title">
      <div class="composer-head">
        <div>
          <h3 id="iih-title">${label}の発行履歴${cache && cache.length ? `（${cache.length}件）` : ""}</h3>
          <p>${escapeHtml(projName)}</p>
        </div>
        <button class="inline-btn ghost" type="button" data-close-invoice-issue-history="1">閉じる</button>
      </div>
      <div class="composer-body peh-body">
        ${body}
        ${docLinksSectionHtml(proj?.dbId, docType)}
      </div>
    </aside>
  `;
}

/** 履歴用の日時フォーマット: YYYY/MM/DD HH:mm */
function formatIssueHistoryDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return escapeHtml(String(iso));
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${y}/${mo}/${da} ${hh}:${mi}`;
  } catch {
    return escapeHtml(String(iso));
  }
}

/** 編集履歴の取得先。案件は専用テーブル、それ以外は監査ログ（fp_audit_log）から引く。
 *  返る形は同じなのでモーダルの描画は共通（app-src/15i-change-history.js から source を指定して開く）。 */
function editHistoryApiUrl(m) {
  const id = encodeURIComponent(m.dbId);
  return m.source && m.source !== "project"
    ? `/api/entity-change-history?resourceType=${encodeURIComponent(m.source)}&resourceId=${id}`
    : `/api/project-edit-history?projectId=${id}`;
}

/** 編集履歴をサーバーから取得（案件・クライアント・パートナー共通） */
async function loadProjectEditHistory(dbId) {
  const m = uiState.projectEditHistoryModal;
  if (!m || !authToken || !dbId) return;
  const { ok: apiOk, data } = await apiFetch(editHistoryApiUrl(m));
  // 応答待ちの間に別の履歴（別の取引先・案件）を開き直していたら捨てる。以前は dbId だけで照合しており、
  // 「パートナー5」→「クライアント5」のように種別違いの同じ番号だと前の応答が混ざった
  if (uiState.projectEditHistoryModal !== m) return;
  m.loading = false;
  const loaded = apiOk && data.ok && Array.isArray(data.history);
  m.history = loaded ? data.history : [];
  // 取得に失敗したときは「履歴なし」と見せない（監査の閲覧で誤認しないように）
  m.error = loaded ? "" : "編集履歴の取得に失敗しました";
  render();
}

/** 案件編集履歴モーダル */
function renderProjectEditHistoryModal() {
  const m = uiState.projectEditHistoryModal;
  if (!m) return "";
  const isProject = !m.source || m.source === "project";
  const projName = isProject ? (projects.find((x) => x.id === m.projectId)?.name || "案件") : (m.name || "");
  const modalTitle = isProject ? "編集履歴" : "変更履歴";

  let body;
  if (m.loading) {
    body = `<p class="peh-empty">読み込み中…</p>`;
  } else if (m.error) {
    body = `<p class="peh-empty peh-error">${escapeHtml(m.error)}</p>`;
  } else if (!Array.isArray(m.history) || m.history.length === 0) {
    body = `<p class="peh-empty">${isProject ? "編集履歴はまだありません。案件情報を保存すると記録されます。" : "変更履歴はまだありません。この画面から保存すると記録されます。"}</p>`;
  } else {
    body = m.history.map((h) => {
      const dt = formatIssueHistoryDateTime(h.editedAt);
      const who = escapeHtml(h.editedByName || "—");
      const changeRows = (Array.isArray(h.changes) ? h.changes : []).map((c) => `
        <li class="peh-change">
          <span class="peh-change-label">${escapeHtml(c.label || c.field || "")}</span>
          <span class="peh-change-diff">
            <span class="peh-change-before">${escapeHtml(String(c.before ?? ""))}</span>
            <span class="peh-change-arrow" aria-hidden="true">→</span>
            <span class="peh-change-after">${escapeHtml(String(c.after ?? ""))}</span>
          </span>
        </li>`).join("");
      // 差分が無い記録（差分化する前の古いログ・作成/削除など）は説明文をそのまま出す。
      // 空に見せるより、粗くても残っている事実を見せる
      const bodyHtml = changeRows
        ? `<ul class="peh-changes">${changeRows}</ul>`
        : `<p class="peh-entry-note">${escapeHtml(h.note || "（変更内容の記録なし）")}</p>`;
      return `<div class="peh-entry">
        <div class="peh-entry-head">
          <span class="peh-entry-dt">${dt}</span>
          <span class="peh-entry-who">${who}</span>
        </div>
        ${bodyHtml}
      </div>`;
    }).join("");
  }

  return `
    <div class="composer-backdrop" data-close-project-edit-history="1"></div>
    <aside class="composer-panel fp-centered-modal project-edit-history-modal${spNoEnter("project-edit-history-modal")}" role="dialog" aria-modal="true" aria-labelledby="peh-title">
      <div class="composer-head">
        <div><h3 id="peh-title">${escapeHtml(modalTitle)}</h3><p>${escapeHtml(projName)}</p></div>
        <button class="inline-btn ghost" type="button" data-close-project-edit-history="1">閉じる</button>
      </div>
      <div class="composer-body peh-body">
        ${body}
      </div>
    </aside>
  `;
}

function stagePage(type, title, subtitle, summary) {
  const quickCreate =
    type === "contracts" && canCreateProject(currentUser)
      ? `<button class="chip-btn" data-open-project-create="1">新規案件を作成</button>`
      : type === "quotes"
      ? `<button class="chip-btn" data-compose="quote" data-project="${projects[0].id}">新規見積作成</button>`
      : type === "invoices"
        ? `<button class="chip-btn" data-compose="invoice" data-project="${projects[0].id}">新規請求作成</button>`
        : "";
  const capabilityText =
    type === "contracts"
      ? "このページでできること: NDA登録、契約書送付、締結完了処理"
      : type === "quotes"
        ? "このページでできること: 見積書の作成、下書き編集、送付準備、送付済み管理"
        : type === "orders"
          ? "このページでできること: 請書/発注書の作成、送付、不要判定"
          : type === "invoices"
            ? "このページでできること: 請求書の作成、送付準備、送付済み管理"
            : "このページでできること: 入金待ち確認、未確認の解消、確認記録";
  const stageItems =
    type === "contracts"
      ? projects.filter((p) => !p.contractDone)
      : type === "orders"
        ? projects.filter((p) => p.useOrderFlow)
        : projects;
  const filteredItems = filterItemsByQuickFilter(type, stageItems);
  return `
    ${header(title, subtitle, quickCreate)}
    <div class="meta"><span>${capabilityText}</span></div>
    ${
      type === "invoices"
        ? `<section class="section"><h3 class="section-title">重点サマリー</h3><div class="card-grid cols-4">${summary
            .map(
              (i) =>
                `<button class="summary-card summary-action" data-quick-filter="invoices:${i.filterId || "all"}"><div class="label">${i.label}</div><div class="value ${i.tone || ""}">${i.value}</div></button>`
            )
            .join("")}</div></section>`
        : summaries(summary)
    }
    <section class="section"><h3 class="section-title">クイックフィルター</h3>${quickFiltersFor(type)}</section>
    ${type === "payments" ? paymentBulkBarHtml() : ""}
    <section class="section"><h3 class="section-title">まとめ処理リスト</h3><div class="list-card">${filteredItems.map((p) => row(p, type)).join("") || `<div class="empty-note">該当案件がありません</div>`}</div></section>
  `;
}

/** 入金の一括記録バー。選択案件に同じ入金日をまとめて記録する（未選択は案内だけ＝位置を確保して表をずらさない） */
function paymentBulkBarHtml() {
  const sel = uiState.paymentBulkSelected || {};
  const ids = Object.keys(sel).filter((k) => sel[k]);
  if (!ids.length) return `<section class="section"><div class="pj-bulk-bar cost-mgmt-bulk-bar"></div></section>`;
  const dateVal = escapeHtmlAttr(uiState.paymentBulkDate || "");
  return `
    <section class="section">
      <div class="pj-bulk-bar cost-mgmt-bulk-bar show">
        <span>${ids.length}件選択中</span>
        <span class="sep" aria-hidden="true">|</span>
        <label class="pj-bulk-bar-field">入金日
          <input class="pj-input" type="date" data-payment-bulk-date value="${dateVal}" aria-label="一括登録する入金日" />
        </label>
        <button type="button" class="inline-btn" data-payment-bulk-apply="1">選択した案件の入金を記録</button>
        <span class="sep" aria-hidden="true">|</span>
        <button type="button" class="inline-btn ghost" data-payment-bulk-clear="1">選択を解除</button>
      </div>
    </section>`;
}

function templateSection(docType, title) {
  const items = getTemplatesByType(docType);
  const emptyHtml =
    uiState.docTemplates === null
      ? `<p class="meta">読み込み中…</p>`
      : uiState.docTemplatesError
        ? `<p class="meta">${escapeHtml(uiState.docTemplatesError)}（ページを再読み込みしてください）</p>`
        : `<p class="meta">まだテンプレートがありません。「新規テンプレート作成」から作れます。</p>`;
  return `
    <article class="soft-card panel-pad section-card">
      <div class="section-head-row">
        <h3 class="mini-title">${title}</h3>
        <button class="chip-btn" data-open-template-editor="${docType}">新規テンプレート作成</button>
      </div>
      <div class="template-list">
        ${items.length ? "" : emptyHtml}
        ${items
          .map(
            (t) => `
          <div class="template-row">
            <div>
              <div class="template-title-row">
                <strong>${escapeHtml(t.name)}</strong>
                ${t.isDefault ? `<span class="flow-badge safe">デフォルト</span>` : ""}
              </div>
              <div class="meta">
                <span>${escapeHtml(t.description)}</span>
                <span>${t.scope === "private" ? "自分専用" : "チーム共通"}</span>
                <span>作成者: ${escapeHtml(t.owner || "—")}</span>
                <span>更新: ${docTemplateUpdatedLabel(t.updatedAt)}</span>
              </div>
            </div>
            <div class="template-actions">
              ${t.canEdit ? `<button class="inline-btn ghost" data-template-action="edit:${escapeHtmlAttr(t.id)}">編集</button>` : ""}
              <button class="inline-btn ghost" data-template-action="duplicate:${escapeHtmlAttr(t.id)}">複製</button>
              ${t.canEdit ? `<button class="inline-btn ghost" data-template-action="delete:${escapeHtmlAttr(t.id)}">削除</button>` : ""}
              ${t.scope === "shared" && !t.isDefault ? `<button class="inline-btn" data-template-action="default:${escapeHtmlAttr(t.id)}">デフォルトにする</button>` : ""}
            </div>
          </div>
        `
          )
          .join("")}
      </div>
    </article>
  `;
}

// ── 案件ロック解除（共通ルール: 経理が請求月単位で手動ロック＝月次ロック） ──────────
// マスター・経理（旧管理部）は直接解除／再ロック、それ以外はプロジェクト一覧から解除申請。
// 承認は承認設定の binding 'project:unlock' のフローに従う（/api/project-unlock-applications）。

async function fetchProjectUnlockApplications() {
  if (!authToken || uiState.projectUnlockAppsLoading) return;
  uiState.projectUnlockAppsLoading = true;
  try {
    const res = await fetch("/api/project-unlock-applications", {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    const j = await res.json().catch(() => ({}));
    uiState.projectUnlockApplications = res.ok && Array.isArray(j.applications) ? j.applications : [];
  } catch {
    uiState.projectUnlockApplications = [];
  }
  uiState.projectUnlockAppsLoading = false;
  render();
}

/** この案件の「申請中」のロック解除申請（なければ null） */
function pendingUnlockApplicationForProject(p) {
  if (!p?.dbId) return null;
  const list = uiState.projectUnlockApplications;
  if (!Array.isArray(list)) return null;
  return list.find((a) => a && a.status === "申請中" && String(a.projectId) === String(p.dbId)) || null;
}

/** プロジェクト一覧の案件名の横に出すロック状態バッジ */
function projectLockBadgeHtml(p) {
  if (projectUnlockActive(p) && isMonthLocked(p.billingMonth)) {
    return `<span class="pj-badge info pj-lock-badge" title="ロック解除中（〜${escapeHtmlAttr(projectUnlockUntilDisplay(p))} まで編集可）">🔓 〜${escapeHtml(projectUnlockUntilDisplay(p))}</span>`;
  }
  if (isProjectLocked(p)) {
    const masterNote = isMasterRoleUser(currentUser) ? "（マスター権限は確認の上そのまま編集可）" : "";
    return `<span class="pj-badge muted pj-lock-badge" title="請求月 ${escapeHtmlAttr(p.billingMonth || "")} は締め済みのためロック中${masterNote}">🔒</span>`;
  }
  return "";
}

/** プロジェクト一覧の操作セルに出すロック解除/申請/再ロックボタン */
function projectLockActionButtonHtml(p) {
  const canDirect = canDirectUnlockProjects(currentUser);
  if (projectUnlockActive(p) && isMonthLocked(p.billingMonth)) {
    return canDirect
      ? `<button class="chip-btn ghost" type="button" data-project-relock="${escapeHtmlAttr(p.id)}" title="解除を取り消して再ロック">🔒 再ロック</button>`
      : "";
  }
  if (!isProjectLocked(p)) return "";
  if (canDirect) {
    return `<button class="chip-btn" type="button" data-project-unlock-direct="${escapeHtmlAttr(p.id)}" title="ロックを解除（3営業日で自動再ロック）">🔓 解除</button>`;
  }
  const pending = pendingUnlockApplicationForProject(p);
  if (pending) {
    return `<span class="pj-badge info pj-lock-badge" title="ロック解除を申請中です">解除申請中</span>`;
  }
  return `<button class="chip-btn ghost" type="button" data-project-unlock-request="${escapeHtmlAttr(p.id)}" title="ロック解除を申請">🔓 解除申請</button>`;
}

/** マスター・経理: 一覧から直接解除（3営業日の期限つき） */
async function directUnlockProject(projectId) {
  if (uiState.projectUnlockSaving) return;
  const p = projects.find((x) => x.id === projectId);
  if (!p?.dbId) return;
  if (!(await fpConfirm({ title: `「${p.name}」のロックを解除しますか？`, message: "締め済みの案件を編集できるようになります。", note: "3営業日後に自動で再ロックされます（途中で再ロックもできます）。", confirmLabel: "ロックを解除する" }))) return;
  uiState.projectUnlockSaving = true;
  try {
    const { ok, data: j } = await apiFetch("/api/project-unlock-applications", { method: "POST", body: { projectId: p.dbId } });
    uiState.projectUnlockSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "解除に失敗しました");
      render();
      return;
    }
    p.unlockUntil = j.unlockUntil || "";
    showToast(`ロックを解除しました（〜${projectUnlockUntilDisplay(p) || "期限"} まで編集可）`);
    render();
  } catch {
    uiState.projectUnlockSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** マスター・経理: 解除の取り消し（再ロック） */
async function relockProject(projectId) {
  if (uiState.projectUnlockSaving) return;
  const p = projects.find((x) => x.id === projectId);
  if (!p?.dbId) return;
  if (!(await fpConfirm({ title: `「${p.name}」を再ロックしますか？`, message: "この案件は編集できなくなります。", confirmLabel: "再ロックする", danger: false }))) return;
  uiState.projectUnlockSaving = true;
  try {
    const { ok, data: j } = await apiFetch("/api/project-unlock-applications", { method: "POST", body: { projectId: p.dbId, action: "relock" } });
    uiState.projectUnlockSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "再ロックに失敗しました");
      render();
      return;
    }
    p.unlockUntil = "";
    showToast("再ロックしました");
    render();
  } catch {
    uiState.projectUnlockSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** 解除申請モーダルを送信 */
async function submitProjectUnlockRequest() {
  if (uiState.projectUnlockSaving) return;
  const draft = uiState.projectUnlockRequestDraft;
  if (!draft) return;
  const p = projects.find((x) => x.id === draft.projectId);
  if (!p?.dbId) return;
  const reason = (document.getElementById("pj-unlock-reason")?.value || "").trim();
  if (!reason) {
    showToastError("解除理由を入力してください");
    return;
  }
  // 送信中・失敗時の再描画で入力欄が空にならないよう、入力した理由を下書きに持つ
  draft.reason = reason;
  uiState.projectUnlockSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/project-unlock-applications", { method: "POST", body: { projectId: p.dbId, reason } });
    uiState.projectUnlockSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "申請に失敗しました");
      render();
      return;
    }
    uiState.projectUnlockRequestDraft = null;
    if (j.application) {
      if (!Array.isArray(uiState.projectUnlockApplications)) uiState.projectUnlockApplications = [];
      uiState.projectUnlockApplications.unshift(j.application);
    }
    showToast("ロック解除を申請しました。承認されると3営業日の間だけ編集できます");
    render();
  } catch {
    uiState.projectUnlockSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** 解除申請モーダル（プロジェクト一覧） */
function projectUnlockRequestModalHtml() {
  const draft = uiState.projectUnlockRequestDraft;
  if (!draft) return "";
  const p = projects.find((x) => x.id === draft.projectId);
  if (!p) return "";
  const saving = uiState.projectUnlockSaving;
  return `
    <div class="cl-mini-modal-overlay open" data-project-unlock-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="pj-unlock-h">
        <div class="cl-mini-modal-head">
          <h4 id="pj-unlock-h">🔓 ロック解除を申請</h4>
          <button class="cl-modal-close" type="button" data-project-unlock-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(p.name || "")}</div>
            <div class="at-app-meta"><span class="at-app-meta-item">請求月：${escapeHtml(p.billingMonth || "—")}（締め済み）</span></div>
          </div>
          <p class="pj-unlock-modal-note">承認されると<strong>3営業日</strong>の間だけ編集でき、期限を過ぎると自動で再ロックされます。</p>
          <label class="cl-form-group">
            <span class="cl-form-label">解除理由 <span class="cl-form-required">*</span></span>
            <textarea id="pj-unlock-reason" class="cl-form-input" rows="3" placeholder="例：請求金額の訂正が必要なため">${escapeHtml(draft.reason || "")}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-project-unlock-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-project-unlock-submit="1" ${saving ? "disabled" : ""}>${saving ? "申請中…" : "申請する"}</button>
        </div>
      </div>
    </div>`;
}

// ── 申請承認ページ: ロック解除申請の承認/却下 ──────────────────────────────
async function approveUnlockApplication(id) {
  if (uiState.projectUnlockSaving) return;
  if (!(await fpConfirm({ title: "この案件のロック解除を承認しますか？", note: "3営業日の期限つきで解除されます。", confirmLabel: "承認して解除する" }))) return;
  uiState.projectUnlockSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/project-unlock-applications", { method: "PUT", body: { id, action: "approve" } });
    uiState.projectUnlockSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "承認に失敗しました");
      render();
      return;
    }
    const app = j.application;
    if (app && app.status === "承認" && app.projectId) {
      // ローカルの案件にも解除期限を反映（リロード不要で🔓表示に）
      const p = projects.find((x) => String(x.dbId) === String(app.projectId));
      if (p && app.unlockUntil) p.unlockUntil = app.unlockUntil;
    }
    showToast(app && app.status === "承認" ? "承認しました（ロック解除済み）" : "承認しました");
    await fetchProjectUnlockApplications();
  } catch {
    uiState.projectUnlockSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

async function rejectUnlockApplication() {
  if (uiState.projectUnlockSaving) return;
  const draft = uiState.unlockRejectDraft;
  if (!draft) return;
  const comment = (document.getElementById("unlock-reject-comment")?.value || "").trim();
  if (!comment) {
    showToastError("却下理由を入力してください");
    return;
  }
  draft.comment = comment;
  uiState.projectUnlockSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/project-unlock-applications", { method: "PUT", body: { id: draft.id, action: "reject", reviewComment: comment } });
    uiState.projectUnlockSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "却下に失敗しました");
      render();
      return;
    }
    uiState.unlockRejectDraft = null;
    showToast("却下しました");
    await fetchProjectUnlockApplications();
  } catch {
    uiState.projectUnlockSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** ロック解除申請の却下モーダル（申請承認ページ） */
function unlockRejectModalHtml() {
  const draft = uiState.unlockRejectDraft;
  if (!draft) return "";
  const app = (uiState.projectUnlockApplications || []).find((a) => String(a.id) === String(draft.id));
  if (!app) return "";
  const saving = uiState.projectUnlockSaving;
  return `
    <div class="cl-mini-modal-overlay open" data-unlock-reject-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="unlock-reject-h">
        <div class="cl-mini-modal-head">
          <h4 id="unlock-reject-h">解除申請を却下</h4>
          <button class="cl-modal-close" type="button" data-unlock-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(app.projectName || "")}</div>
            <div class="at-app-meta">${app.applicantName ? `<span class="at-app-meta-item">申請者：${escapeHtml(app.applicantName)}</span>` : ""}<span class="at-app-meta-item">請求月：${escapeHtml(app.billingMonth || "—")}</span></div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="unlock-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由">${escapeHtml(draft.comment || "")}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-unlock-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-unlock-reject-submit="1" ${saving ? "disabled" : ""}>${saving ? "却下中…" : "却下する"}</button>
        </div>
      </div>
    </div>`;
}
/** 操作ログを取得して uiState に反映する（append=true: さかのぼり追記） */
// ── ログイン状況 ──────────────────────────────────────────────────

/** ログイン状況を取得して uiState に反映 */
async function loadLoginStatus() {
  if (!authToken) return;
  if (!uiState.loginStatusView) uiState.loginStatusView = { items: null, loading: false };
  uiState.loginStatusView.loading = true;
  render();
  try {
    const r = await fetch("/api/login-status", { headers: { Authorization: `Bearer ${authToken}` } });
    const data = await r.json().catch(() => ({}));
    uiState.loginStatusView.loading = false;
    uiState.loginStatusView.items = data.ok ? (data.users || []) : null;
    if (!data.ok) showToast(data.error || "取得に失敗しました", { variant: "error" });
  } catch {
    uiState.loginStatusView.loading = false;
    uiState.loginStatusView.items = [];
  }
  render();
}

/** ログイン状況タブの HTML */
function loginStatusTabHtml() {
  const ls = uiState.loginStatusView || { items: null, loading: false };
  const now = Date.now();

  /** ステータスバッジを返す（last_seen_at が基準、なければ last_activity_at で判定） */
  // JST（UTC+9）での日付文字列 "YYYY-MM-DD" を返す
  const toJSTDateStr = (ms) => {
    const d = new Date(ms + 9 * 60 * 60 * 1000);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  };
  const todayJST = toJSTDateStr(now);

  const statusBadge = (u) => {
    const seenMs = u.last_seen_at     ? new Date(u.last_seen_at).getTime()     : 0;
    const actMs  = u.last_activity_at ? new Date(u.last_activity_at).getTime() : 0;
    const latest = Math.max(seenMs, actMs);
    if (!latest) return `<span class="ls-badge ls-badge--off">未ログイン</span>`;
    const diffMin = (now - latest) / 60000;
    if (diffMin < 15) return `<span class="ls-badge ls-badge--on">🟢 アクセス中</span>`;
    if (diffMin < 60) return `<span class="ls-badge ls-badge--recent">🟡 1時間以内</span>`;
    // JST 日付で今日かどうかを比較（24時間以内判定だと昨日も「本日」になるため）
    if (toJSTDateStr(latest) === todayJST) return `<span class="ls-badge ls-badge--today">本日アクセス</span>`;
    return `<span class="ls-badge ls-badge--off">⚫ オフライン</span>`;
  };

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso).slice(0, 16).replace("T", " "); }
  };

  let body;
  if (ls.loading) {
    body = `<p class="project-lock-auto-preview">読み込み中…</p>`;
  } else if (!ls.items) {
    body = `<p class="project-lock-auto-preview">取得に失敗しました。</p>`;
  } else if (ls.items.length === 0) {
    body = `<p class="project-lock-auto-preview">ユーザーがいません。</p>`;
  } else {
    const rows = ls.items.map(u => `<tr>
      <td class="ls-td">${escapeHtml(u.name || "—")}</td>
      <td class="ls-td">${statusBadge(u)}</td>
      <td class="ls-td ls-td--time">${escapeHtml(fmtDt(u.last_seen_at))}</td>
      <td class="ls-td ls-td--time">${escapeHtml(fmtDt(u.last_login_at))}</td>
      <td class="ls-td">${escapeHtml(u.last_login_ip || "—")}</td>
      <td class="ls-td ls-td--time">${escapeHtml(fmtDt(u.last_activity_at))}</td>
      <td class="ls-td">${escapeHtml(u.last_activity_ip || "—")}</td>
    </tr>`).join("");
    body = `<div style="overflow-x:auto;">
      <table class="pj-table ls-table" data-fp-table="login-status">
        <thead><tr><th>名前</th><th>状態</th><th>最終アクセス</th><th>最終ログイン</th><th>ログインIP</th><th>最終操作</th><th>操作IP</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="ls-note">※ 最終アクセスはアプリ起動中に5分ごと更新。15分以内を「アクセス中」と表示します。JWTセッション方式のため厳密なログイン判定ではありません。IPは操作ログ記録時点のものです。</p>`;
  }

  return `<section class="section">
    <article class="soft-card panel-pad section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
        <h3 class="mini-title">ログイン状況</h3>
        <button type="button" class="inline-btn ghost" data-login-status-reload="1">最新に更新</button>
      </div>
      ${body}
    </article>
  </section>`;
}

async function loadAuditLog(append = false) {
  if (!authToken) return;
  if (!uiState.auditLogView) uiState.auditLogView = { items: null, loading: false, hasMore: false, offset: 0 };
  const al  = uiState.auditLogView;
  const flt = uiState.auditLogFilter || {};
  const LIMIT = 200;
  const offset = append ? (al.offset || 0) : 0;

  al.loading = true;
  if (!append) al.items = null;
  render();

  try {
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
    if (flt.action)       params.set("action",       flt.action);
    if (flt.resourceType) params.set("resourceType", flt.resourceType);
    if (flt.userId)       params.set("userId",       flt.userId);
    if (flt.dateFrom)     params.set("dateFrom",     flt.dateFrom);
    if (flt.dateTo)       params.set("dateTo",       flt.dateTo);

    const r    = await fetch(`/api/audit-log?${params}`, { headers: { Authorization: `Bearer ${authToken}` } });
    const data = await r.json().catch(() => ({}));
    al.loading = false;
    if (data.ok) {
      const logs = data.logs || [];
      al.items   = append ? [...(al.items || []), ...logs] : logs;
      al.offset  = offset + logs.length;
      al.hasMore = logs.length === LIMIT;
    } else {
      if (!al.items) al.items = [];
      showToast(data.error || "取得に失敗しました", { variant: "error" });
    }
  } catch {
    al.loading = false;
    if (!al.items) al.items = [];
    showToast("通信エラー", { variant: "error" });
  }
  render();
}

/** 秘密メニューページ（マスターロール専用：削除済みデータ復元・操作ログ） */
function secretMenuPage() {
  if (!isMasterRoleUser(currentUser)) {
    return `${header("アクセス拒否", "")}
      <section class="section"><article class="soft-card panel-pad">
        <p style="color:#dc2626;">このページはマスター権限のみアクセスできます。</p>
      </article></section>`;
  }
  const tab = uiState.secretMenuTab || "restore";
  // 操作ログタブが開かれているとき、未取得なら非同期で自動取得
  if (tab === "audit-log" && (!uiState.auditLogView || uiState.auditLogView.items === null) && !uiState.auditLogView?.loading) {
    requestAnimationFrame(() => { void loadAuditLog(false); });
  }
  // ログイン状況タブが開かれているとき、未取得なら自動取得
  if (tab === "login-status" && (!uiState.loginStatusView || uiState.loginStatusView.items === null) && !uiState.loginStatusView?.loading) {
    requestAnimationFrame(() => { void loadLoginStatus(); });
  }
  return `
    ${header("操作ログ", "マスター専用の管理機能")}
    <section class="section">
      <div class="tab-bar account-tab-bar">
        <button class="tab-btn ${tab === "login-status" ? "active" : ""}" data-secret-menu-tab="login-status">ログイン状況</button>
        <button class="tab-btn ${tab === "audit-log" ? "active" : ""}" data-secret-menu-tab="audit-log">操作ログ</button>
        <button class="tab-btn ${tab === "restore" ? "active" : ""}" data-secret-menu-tab="restore">削除済みデータの復元</button>
      </div>
    </section>
    ${tab === "restore" ? restoreDeletedTabHtml() : ""}
    ${tab === "audit-log" ? auditLogTabHtml() : ""}
    ${tab === "login-status" ? loginStatusTabHtml() : ""}
  `;
}

function settingsPage() {
  const mode = uiState.settingsMenu;
  const showAccount = mode === "account-settings";
  const showTemplate = mode === "template-settings";
  const showOther = mode === "other-settings";
  const isApprovalTab = showAccount &&
    (uiState.accountSettingsTab === "approval" || uiState.accountSettingsTab === "approval-bindings");
  const subtitle =
    isApprovalTab ? "申請フロー（チーム）と承認者・申請ごとの使用フローを設定する" :
    showAccount ? "アカウント情報と担当者情報を管理する" :
    showTemplate ? "テンプレートを運用し、書類作成を標準化する" :
    "通知・クライアントなどその他設定を管理する";
  const pageTitle = showAccount ? "アカウント設定" : "設定";
  return `
    ${header(pageTitle, subtitle)}
    ${showAccount ? accountSettingsSection() : ""}
    ${showTemplate ? `<section class="section">
      <h3 class="section-title">設定 > 書類テンプレート</h3>
      <div class="toolbar">
        <button class="chip-btn ${uiState.templateTab === "quote" ? "active" : ""}" data-template-tab="quote">見積テンプレート</button>
        <button class="chip-btn ${uiState.templateTab === "invoice" ? "active" : ""}" data-template-tab="invoice">請求テンプレート</button>
        <button class="chip-btn ${uiState.templateTab === "order" ? "active" : ""}" data-template-tab="order">請書 / 発注テンプレート</button>
        <button class="chip-btn ${uiState.templateTab === "common" ? "active" : ""}" data-template-tab="common">共通設定</button>
      </div>
    </section>
    <section class="section">
      ${uiState.templateTab === "quote" ? templateSection("quote", "見積テンプレート一覧") : ""}
      ${uiState.templateTab === "invoice" ? templateSection("invoice", "請求テンプレート一覧") : ""}
      ${uiState.templateTab === "order" ? templateSection("order", "請書 / 発注テンプレート一覧") : ""}
      ${
        uiState.templateTab === "common"
          ? `<article class="soft-card panel-pad section-card">
              <h3 class="mini-title">共通設定</h3>
              <div class="setting-item"><span>税率設定</span><span>10%</span></div>
              <div class="setting-item"><span>注意書きテンプレート</span><span>標準文面</span></div>
              <div class="setting-item"><span>フッター文言</span><span>On boarding</span></div>
            </article>`
          : ""
      }
    </section>` : ""}
    ${/* 通知設定の案内だけ。以前ここにあった「クライアント管理」は初期の仮画面で、削除しても保存されず
         リロードで戻っていた（取引先は取引先管理で扱う。2026-10 全体レビュー #712 で撤去） */ ""}
    ${showOther ? `<section class="section">
      <article class="soft-card panel-pad">
        <h3 class="mini-title">通知設定</h3>
        <p class="meta" style="line-height:1.6;">ToDo通知（メール／Slack／プッシュ）の受け取り設定は<strong>「マイアカウント」</strong>に移動しました。左下のユーザー名 →「アカウント設定」から変更できます。</p>
      </article>
    </section>` : ""}
  `;
}

function loadApprovalApproversStore() {
  return approvalApproversMemoryStore;
}

/** `/api/departments` の approval フィールドをメモリに反映して uiState を再構築 */
function applyApprovalSettingsPayload(data) {
  if (!data || typeof data !== "object") return;
  let touched = false;
  if (data.approvalApprovers !== undefined && data.approvalApprovers !== null && typeof data.approvalApprovers === "object") {
    touched = true;
    Object.keys(approvalApproversMemoryStore).forEach((k) => {
      delete approvalApproversMemoryStore[k];
    });
    for (const [k, v] of Object.entries(data.approvalApprovers)) {
      const uid = String(k);
      const steps = Array.isArray(v?.steps) ? v.steps.map((s) => (s == null || s === "inherit" ? "" : String(s))) : [];
      while (steps.length < 2) steps.push("");
      approvalApproversMemoryStore[uid] = { steps, ccMemberIds: approvalCcIdsOf(v) };
    }
  }
  if (Array.isArray(data.approvalOtherFlows)) {
    touched = true;
    approvalOtherFlowsMemoryStore.length = 0;
    data.approvalOtherFlows.forEach((f, idx) => {
      if (!f?.id) return;
      approvalOtherFlowsMemoryStore.push({
        id: String(f.id),
        name: String(f.name || "").trim() || `フロー${idx + 1}`,
        memberIds: Array.isArray(f.memberIds) ? f.memberIds.map(String) : [],
        ccMemberIds: Array.isArray(f.ccMemberIds) ? f.ccMemberIds.map(String) : [],
        selfOverrideFlowId: String(f.selfOverrideFlowId || "")
      });
    });
  }
  if (data.approvalApplicationFlowBindings !== undefined && data.approvalApplicationFlowBindings !== null && typeof data.approvalApplicationFlowBindings === "object" && !Array.isArray(data.approvalApplicationFlowBindings)) {
    touched = true;
    uiState.approvalApplicationFlowBindings = { ...data.approvalApplicationFlowBindings };
  }
  // 楽観ロック用の版トークン（保存時に approvalSettingsBaseVersion として送り返す）
  if (data.approvalSettingsVersion !== undefined) {
    uiState.approvalSettingsVersion = data.approvalSettingsVersion || null;
  }
  if (touched) {
    ensureApprovalApproversState();
    ensureApprovalApplicationFlowBindingsState();
  }
  // 読込・保存のたびに未保存ガードの基準を取り直す（次の承認フロー表示時に approvalSettingsSnapshot で再取得）
  uiState.approvalSettingsBaseline = null;
}

/** 承認者の設定（approvalApprovers の1件）の CC。「その他の申請の承認者」（kind:<種別>）だけが持つ（2026-10 要望） */
function approvalCcIdsOf(entry) {
  return Array.isArray(entry?.ccMemberIds) ? [...new Set(entry.ccMemberIds.map(String).filter(Boolean))] : [];
}

function persistApprovalApproversStore() {
  // サーバー送信前にセッション内メモリを uiState と整合（永続は persistDepartments 経由）
  const next = uiState.approvalApprovers && typeof uiState.approvalApprovers === "object" ? uiState.approvalApprovers : {};
  Object.keys(approvalApproversMemoryStore).forEach((k) => { delete approvalApproversMemoryStore[k]; });
  Object.entries(next).forEach(([k, v]) => {
    approvalApproversMemoryStore[k] = {
      steps: Array.isArray(v?.steps) ? v.steps.map((s) => (s == null ? "" : String(s))) : ["", ""],
      ccMemberIds: approvalCcIdsOf(v)
    };
  });
}

function loadApprovalOtherFlowsStore() {
  return approvalOtherFlowsMemoryStore;
}

function persistApprovalOtherFlowsStore() {
  // サーバー送信前にセッション内メモリを uiState と整合（永続は persistDepartments 経由）
  const next = Array.isArray(uiState.approvalOtherFlows) ? uiState.approvalOtherFlows : [];
  approvalOtherFlowsMemoryStore.length = 0;
  next.forEach((f) => {
    if (!f?.id) return;
    approvalOtherFlowsMemoryStore.push({
      id: String(f.id),
      name: String(f.name || "").trim() || "名称未設定",
      memberIds: Array.isArray(f.memberIds) ? f.memberIds.map(String) : [],
      ccMemberIds: Array.isArray(f.ccMemberIds) ? f.ccMemberIds.map(String) : [],
      selfOverrideFlowId: String(f.selfOverrideFlowId || "")
    });
  });
}

function ensureApprovalOtherFlowsState() {
  const storeRaw = loadApprovalOtherFlowsStore();
  const raw = (Array.isArray(storeRaw) && storeRaw.length)
    ? storeRaw
    : (Array.isArray(uiState.approvalOtherFlows) ? uiState.approvalOtherFlows : []);
  const normalized = raw
    .map((f, idx) => ({
      id: String(f?.id || `of-${Date.now()}-${idx}`),
      name: String(f?.name || "").trim() || `フロー${idx + 1}`,
      memberIds: Array.isArray(f?.memberIds) ? [...new Set(f.memberIds.map(String))] : [],
      // CC（承認権限なし・通知のみ）。フロー全体に対して1セット
      ccMemberIds: Array.isArray(f?.ccMemberIds) ? [...new Set(f.ccMemberIds.map(String))] : [],
      // 承認者本人が申請するときの承認先（自己承認の回避）。他のフローのIDのみ有効
      selfOverrideFlowId: String(f?.selfOverrideFlowId || "")
    }))
    .filter((f, i, arr) => f.id && arr.findIndex((x) => x.id === f.id) === i);
  const ids = new Set(normalized.map((f) => f.id));
  normalized.forEach((f) => {
    if (!f.selfOverrideFlowId || f.selfOverrideFlowId === f.id || !ids.has(f.selfOverrideFlowId)) f.selfOverrideFlowId = "";
  });
  uiState.approvalOtherFlows = normalized;
}

/** 申請種別 → 使うフローID。捺印申請＝申請者の所属フロー（__dept__）、それ以外＝種別ごとの承認者（kind:…）で固定。
 *  画面からは変えられない（運用で決まっている）ため、ここで常にこの形に揃える。 */
function ensureApprovalApplicationFlowBindingsState() {
  const merged = { [APPROVAL_SEAL_KIND_KEY]: APPROVAL_FLOW_BINDING_DEPT_DEFAULT };
  for (const { key } of APPROVAL_OTHER_KIND_META) merged[key] = approvalKindFlowId(key);
  uiState.approvalApplicationFlowBindings = merged;
}

/** ユーザーがメンバーとして所属する申請フロー（部署から独立した承認用の箱） */
function getApprovalFlowByMemberId(memberId) {
  const uid = String(memberId || "").trim();
  if (!uid) return null;
  return (uiState.approvalOtherFlows || []).find(
    (f) => Array.isArray(f.memberIds) && f.memberIds.map(String).includes(uid)
  ) || null;
}

/**
 * 申請者の所属する申請フローid。未所属なら空（部署設定へはフォールバックしない。
 * サーバ側 api/_approvalFlow.js resolveApproverUserIdsForBinding と同じ規則）。
 */
function getUserApprovalFlowId(userId) {
  const flow = getApprovalFlowByMemberId(userId);
  return flow ? String(flow.id) : "";
}

/**
 * 申請種別 kind と申請者から、実際に使う申請フローIDを解決する。
 * サーバ側 api/_approvalFlow.js resolveFlowContextForBinding と同じ規則:
 *   申請ごとの使用フロー（固定 or 申請者の所属フロー）。
 * 「承認者本人が申請するときの承認先」（selfOverrideFlowId）は廃止（2026-10 要望: 承認者本人も
 * 別の申請フローのメンバーに入れれば足りる）。保存済みの値は残っていても使わない。
 */
function getResolvedApprovalFlowIdForKind(kind, applicantUserId) {
  ensureApprovalApproversState();
  ensureApprovalApplicationFlowBindingsState();
  const bound = uiState.approvalApplicationFlowBindings?.[kind] || APPROVAL_FLOW_BINDING_DEPT_DEFAULT;
  const flowId = !bound || bound === APPROVAL_FLOW_BINDING_DEPT_DEFAULT ? getUserApprovalFlowId(applicantUserId) : String(bound);
  return flowId || "";
}

/** 捺印申請で参照する承認フローID。書類種別は単一キー "seal:documents" に統合済み。 */
function getResolvedApprovalFlowIdForSeal(docTab, applicantUserId) {
  return getResolvedApprovalFlowIdForKind("seal:documents", applicantUserId);
}

/** 契約（クライアント・パートナー）マスタの承認フロー ID */
function getResolvedApprovalFlowIdForContractMaster(applicantUserId) {
  return getResolvedApprovalFlowIdForKind("contract:client_partner", applicantUserId);
}

function costDueDisplayForReadonly(cost) {
  const d = String(cost?.dueDate || "").trim();
  if (!d) return "—";
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : escapeHtml(d);
}

function ensureApprovalApproversState() {
  ensureDeptSettingsState();
  ensureApprovalOtherFlowsState();
  const raw = loadApprovalApproversStore();
  const otherFlows = uiState.approvalOtherFlows || [];
  const next = {};
  // 旧値 "inherit"（部署責任者を引く）は廃止＝未設定として扱う
  const normalizeSteps = (cur) => {
    const s = cur && Array.isArray(cur.steps) ? cur.steps.map((x) => (x == null || x === "inherit" ? "" : String(x))) : [];
    while (s.length < 2) s.push("");
    return s;
  };
  for (const f of otherFlows) {
    next[f.id] = { steps: normalizeSteps(raw[f.id]) };
  }
  // 捺印以外の種別ごとの承認者。旧設定（申請ごとの使用フローで固定フローを選んでいた）からは
  // そのフローの承認ステップを初回だけ引き継ぐ（保存するまでは画面上の値）
  const oldBindings =
    uiState.approvalApplicationFlowBindings && typeof uiState.approvalApplicationFlowBindings === "object" ? uiState.approvalApplicationFlowBindings : {};
  for (const { key } of APPROVAL_OTHER_KIND_META) {
    const id = approvalKindFlowId(key);
    const legacyFlowId = String(oldBindings[key] || "");
    const src = raw[id] || (legacyFlowId && legacyFlowId !== id ? raw[legacyFlowId] : null);
    next[id] = { steps: normalizeSteps(src), ccMemberIds: approvalCcIdsOf(raw[id]) };
  }
  uiState.approvalApprovers = next;
}

function getApprovalStepsForDept(deptId) {
  const a = uiState.approvalApprovers?.[deptId];
  return Array.isArray(a?.steps) ? a.steps : ["", ""];
}

/** 表示用のユーザー（そのステップに設定された承認者。未設定なら null） */
function resolveApprovalStepUser(flowLike, stepIdx) {
  const steps = getApprovalStepsForDept(flowLike.id);
  const v = stepIdx < steps.length ? steps[stepIdx] : "";
  return v ? users.find((u) => u.id === v) || null : null;
}

function setApprovalStepValue(deptId, stepIdx, value) {
  ensureApprovalApproversState();
  const entry = uiState.approvalApprovers[deptId];
  if (!entry) return;
  const steps = [...entry.steps];
  while (steps.length <= stepIdx) steps.push("");
  steps[stepIdx] = value;
  uiState.approvalApprovers[deptId] = { ...entry, steps };
  persistApprovalApproversStore();
}

function renderApprovalApproverSlot(dept, stepIdx) {
  const u = resolveApprovalStepUser(dept, stepIdx);
  if (u) {
    const initial = (u.name || "?").slice(0, 1);
    return `
      <div class="approval-approver-slot">
        <div class="approval-approver-pill" title="${escapeHtmlAttr(u.name || "")}">
          <span class="approval-av">
            <span class="approval-av-char">${initial}</span>
          </span>
          <span class="approval-approver-pill-text">
            <span class="approval-approver-pill-name-row">
              <span class="approval-approver-pill-name">${escapeHtml(u.name || "")}</span>
              <button type="button" class="approval-name-clear" data-approval-approver-clear="1" data-approval-sid="${dept.id}" data-approval-idx="${stepIdx}" aria-label="承認者を解除">×</button>
            </span>
          </span>
        </div>
      </div>
    `;
  }
  return `
    <div class="approval-approver-slot">
      <button type="button" class="approval-approver-empty" data-approval-open-picker="1" data-approval-sid="${dept.id}" data-approval-idx="${stepIdx}" aria-label="承認者を選ぶ">
        <span class="approval-approver-empty-ico" aria-hidden="true">+</span>
        <span class="approval-approver-empty-label">担当を選ぶ</span>
      </button>
    </div>
  `;
}

function renderApprovalPickerModal() {
  if (!uiState.approvalPicker) return "";
  const { deptId } = uiState.approvalPicker;
  const d =
    (uiState.approvalOtherFlows || []).find((x) => x.id === deptId) ||
    APPROVAL_OTHER_KIND_META.filter((m) => approvalKindFlowId(m.key) === deptId).map((m) => ({ name: m.label }))[0];
  return `
    <div class="approval-picker-root" id="approvalPickerRoot">
      <div class="approval-picker-dlg" role="dialog" aria-modal="true" aria-labelledby="approvalPickerTitle">
        <div class="approval-picker-scrim" data-approval-scrim="1" aria-label="閉じる" tabindex="-1"></div>
        <div class="approval-picker-card">
          <div class="approval-picker-head">
            <h4 class="approval-picker-title" id="approvalPickerTitle">承認者を選ぶ</h4>
            <button type="button" class="approval-picker-x" data-approval-scrim="1" aria-label="閉じる">×</button>
          </div>
          <p class="approval-picker-hint">${d ? `${escapeHtml(d.name)} — ` : ""}アカウント一覧の担当者から選びます。</p>
          <ul class="approval-picker-list">
            ${assignableUsers()
              .map(
                (u) => {
                  const un = (u.name || "?").replace(/</g, "&lt;");
                  const em = String(u.email || "").replace(/</g, "&lt;");
                  return `<li>
              <button type="button" class="approval-picker-row" data-approval-pick-user="${u.id}">
                <span class="approval-picker-name">${un}</span>
                <span class="approval-picker-mail">${em}</span>
              </button>
            </li>`;
                }
              )
              .join("")}
          </ul>
          <div class="approval-picker-foot">
            <button type="button" class="cl-btn cl-btn-outline" data-approval-scrim="1">キャンセル</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function addApprovalStepForDept(deptId) {
  ensureApprovalApproversState();
  const s = [...getApprovalStepsForDept(deptId)];
  s.push("");
  uiState.approvalApprovers[deptId] = { ...uiState.approvalApprovers[deptId], steps: s };
  persistApprovalApproversStore();
}

function addApprovalOtherFlow() {
  const base = Array.isArray(uiState.approvalOtherFlows) ? uiState.approvalOtherFlows : [];
  const flows = [...base];
  const id = `of-${Date.now()}`;
  flows.push({ id, name: `フロー${flows.length + 1}`, memberIds: [], ccMemberIds: [], selfOverrideFlowId: "" });
  uiState.approvalOtherFlows = flows;
  uiState.approvalFlowOpenId = id; // 追加したフローをその場で開く
  persistApprovalOtherFlowsStore();
  if (!uiState.approvalApprovers || typeof uiState.approvalApprovers !== "object") {
    uiState.approvalApprovers = {};
  }
  if (!uiState.approvalApprovers[id]) {
    uiState.approvalApprovers[id] = { steps: ["", ""] };
  }
  persistApprovalApproversStore();
}

function removeApprovalOtherFlow(id) {
  const fid = String(id || "");
  if (!fid) return;
  const base = Array.isArray(uiState.approvalOtherFlows) ? uiState.approvalOtherFlows : [];
  uiState.approvalOtherFlows = base.filter((f) => String(f?.id) !== fid);
  persistApprovalOtherFlowsStore();
  if (uiState.approvalApprovers && typeof uiState.approvalApprovers === "object") {
    delete uiState.approvalApprovers[fid];
  }
  persistApprovalApproversStore();
}

/** 申請フローの承認ステップをミニ表示（一覧セル・プレビュー用） */
function approvalFlowStepsMiniHtml(flowLike) {
  const steps = getApprovalStepsForDept(flowLike.id);
  const pills = [];
  steps.forEach((__, i) => {
    const u = resolveApprovalStepUser(flowLike, i);
    if (!u) return;
    pills.push(`
      <span class="af-step-mini">
        <span class="approval-av af-av-sm"><span class="approval-av-char">${escapeHtml((u.name || "?").slice(0, 1))}</span></span>
        <span class="af-step-mini-name">${escapeHtml(u.name || "")}</span>
      </span>`);
  });
  if (!pills.length) return `<span class="af-none">未設定</span>`;
  return pills.join(`<span class="af-step-arrow" aria-hidden="true">▶</span>`);
}

/** 種別ごとの承認者の CC（ユーザー）。表示・編集用 */
function approvalKindCcUsers(id) {
  return approvalCcIdsOf(uiState.approvalApprovers?.[id]).map((uid) => users.find((u) => u.id === uid)).filter(Boolean);
}

function approvalKindCcMiniHtml(id) {
  const cc = approvalKindCcUsers(id);
  return cc.length ? `<div class="af-cc-mini">CC: ${cc.map((u) => escapeHtml(u.name || "")).join("、")}</div>` : "";
}

/** 捺印以外の申請種別ごとの承認者（全員共通）。1種別1行で承認者をミニ表示し、編集はドロワー（renderApprovalKindEditModal） */
function approvalOtherKindsSectionHtml() {
  const rows = APPROVAL_OTHER_KIND_META.map((meta) => {
    const id = approvalKindFlowId(meta.key);
    return `
      <tr class="af-flow-line">
        <td class="af-cell-name"><span class="af-flow-name">${escapeHtml(meta.label)}</span></td>
        <td class="af-cell-steps"><div class="af-steps-cell">${approvalFlowStepsMiniHtml({ id })}</div>${approvalKindCcMiniHtml(id)}</td>
        <td class="af-cell-act"><button class="cl-btn cl-btn-outline af-toggle-btn" type="button" data-approval-kind-edit="${escapeHtmlAttr(id)}">編集</button></td>
      </tr>`;
  }).join("");
  return `
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <div class="section-head-row">
          <h3 class="mini-title">その他の申請の承認者</h3>
        </div>
        <p class="approval-dept-lead">契約・ツール許可などは社員全員が同じ承認者で回ります。申請の種類ごとに承認者を上から順に設定してください。</p>
        <div class="af-table-scroll">
          <table class="acct-table af-flow-table approval-kind-table" data-fp-table="approval-kinds">
            <colgroup>
              <col class="af-col-name" />
              <col />
              <col class="af-col-act" />
            </colgroup>
            <thead><tr><th>申請</th><th>承認ステップ（上から順に承認）</th><th></th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </article>
    </section>`;
}

/** 種別ごとの承認者を編集するドロワー（uiState.approvalKindEditId = "kind:<種別>"）。
 *  承認者の選択は既存の担当者ピッカー（approvalPicker）をこの上に重ねて使う */
function renderApprovalKindEditModal() {
  const id = String(uiState.approvalKindEditId || "");
  if (!id) return "";
  const meta = APPROVAL_OTHER_KIND_META.find((m) => approvalKindFlowId(m.key) === id);
  if (!meta) return "";
  const flowLike = { id };
  const stepRows = getApprovalStepsForDept(id)
    .map(
      (__, i) => `
      <div class="approval-kind-step">
        <span class="approval-kind-step-no">${i + 1}</span>
        ${renderApprovalApproverSlot(flowLike, i)}
      </div>`
    )
    .join("");
  return `
    <div class="approval-picker-root" id="approvalKindEditRoot">
      <div class="approval-picker-dlg" role="dialog" aria-modal="true" aria-labelledby="approvalKindEditTitle">
        <div class="approval-picker-scrim" data-approval-kind-close="1" aria-label="閉じる" tabindex="-1"></div>
        <div class="approval-picker-card">
          <div class="approval-picker-head">
            <h4 class="approval-picker-title" id="approvalKindEditTitle">${escapeHtml(meta.label)}</h4>
            <button type="button" class="approval-picker-x" data-approval-kind-close="1" aria-label="閉じる">×</button>
          </div>
          <p class="approval-picker-hint">承認者を上から順に設定します。変更は「承認フローを登録」で保存されます。</p>
          <div class="approval-kind-steps">
            ${stepRows}
            <button class="chip-btn approval-kind-add" type="button" data-approval-dept-add-step="${escapeHtmlAttr(id)}">＋ ステップ追加</button>
            <div class="af-ed-field approval-kind-cc">
            <span class="af-ed-label">CC（承認権限なし・通知のみ）</span>
            <div class="af-chips-box">
              ${approvalKindCcUsers(id)
                .map((u) => `<span class="af-chip">${escapeHtml(u.name || "")} <button type="button" class="af-chip-x" data-af-cc-remove="${escapeHtmlAttr(`${id}:${u.id}`)}" aria-label="CCから外す">×</button></span>`)
                .join("")}
              <button type="button" class="af-chip-add" data-af-cc-add="${escapeHtmlAttr(id)}">＋ CC追加</button>
            </div>
            <p class="af-ed-hint">この申請が進行するたびに通知だけ受け取ります（承認はできません）。</p>
            </div>
          </div>
          <div class="approval-picker-foot">
            <button type="button" class="cl-btn cl-btn-primary" data-approval-kind-close="1">閉じる</button>
          </div>
        </div>
      </div>
    </div>`;
}

/** 申請フロー一覧の1行（開くと行内エディタを展開）。flow = uiState.approvalOtherFlows の要素 */
function approvalFlowRowHtml(flow) {
  const open = uiState.approvalFlowOpenId === flow.id;
  const memberUsers = (flow.memberIds || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const ccUsers = (flow.ccMemberIds || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const memberChips =
    memberUsers.map((u) => `<span class="af-chip">${escapeHtml(u.name || "")}</span>`).join("") || `<span class="af-none">未使用</span>`;
  const ccMini = ccUsers.length
    ? `<div class="af-cc-mini">CC: ${ccUsers.map((u) => escapeHtml(u.name || "")).join("、")}</div>`
    : "";
  const stepBlocks = getApprovalStepsForDept(flow.id)
    .map(
      (__, i) => `
        <div class="approval-dept-stage">
          <div class="approval-dept-stage-label">${i + 1}. 承認者</div>
          ${renderApprovalApproverSlot(flow, i)}
        </div>`
    )
    .join("");
  const nameField = `<input class="pj-input af-flow-name-input" data-approval-other-flow-name="${escapeHtmlAttr(flow.id)}" value="${escapeHtmlAttr(String(flow.name || ""))}" aria-label="フロー名" />`;
  const membersBox = `
      <div class="af-ed-field">
        <span class="af-ed-label">申請する営業</span>
        <div class="af-chips-box">
          ${memberUsers
            .map((u) => `<span class="af-chip">${escapeHtml(u.name || "")} <button type="button" class="af-chip-x" data-af-member-remove="${escapeHtmlAttr(`${flow.id}:${u.id}`)}" aria-label="メンバーから外す">×</button></span>`)
            .join("")}
          <button type="button" class="af-chip-add" data-af-member-add="${escapeHtmlAttr(flow.id)}">＋ メンバー追加</button>
        </div>
        <p class="af-ed-hint">所属できるフローは1人1つです。</p>
      </div>`;
  const ccBox = `
      <div class="af-ed-field">
        <span class="af-ed-label">CC（承認権限なし・通知のみ）</span>
        <div class="af-chips-box">
          ${ccUsers
            .map((u) => `<span class="af-chip">${escapeHtml(u.name || "")} <button type="button" class="af-chip-x" data-af-cc-remove="${escapeHtmlAttr(`${flow.id}:${u.id}`)}" aria-label="CCから外す">×</button></span>`)
            .join("")}
          <button type="button" class="af-chip-add" data-af-cc-add="${escapeHtmlAttr(flow.id)}">＋ CC追加</button>
        </div>
        <p class="af-ed-hint">このフローの申請が進行するたびに通知だけ受け取ります（承認はできません）。</p>
      </div>`;
  const deleteBtn = `<button class="cl-btn cl-btn-outline af-flow-delete-btn" type="button" data-approval-other-flow-remove="${escapeHtmlAttr(flow.id)}">このフローを削除</button>`;
  return `
    <tr class="af-flow-line${open ? " af-flow-line--open" : ""}" data-af-row-toggle="${escapeHtmlAttr(flow.id)}" aria-expanded="${open ? "true" : "false"}">
      <td class="af-cell-name"><span class="af-flow-name">${escapeHtml(flow.name || "（名称未設定）")}</span></td>
      <td class="af-cell-members"><div class="af-member-cell">${memberChips}${ccMini}</div></td>
      <td class="af-cell-steps"><div class="af-steps-cell">${approvalFlowStepsMiniHtml(flow)}</div></td>
      <td class="af-cell-act"><button class="cl-btn cl-btn-outline af-toggle-btn" type="button">${open ? "閉じる" : "開く"}</button></td>
    </tr>
    ${open ? `
    <tr class="af-editor-tr">
      <td colspan="4">
        <div class="af-editor">
          <div class="af-ed-grid">
            <div>
              <div class="af-ed-field">
                <span class="af-ed-label">フロー名</span>
                ${nameField}
              </div>
              ${membersBox}
              ${ccBox}
            </div>
            <div>
              <span class="af-ed-label">承認ステップ <span class="af-ed-opt">（上から順に承認）</span></span>
              <div class="approval-dept-stages">
                ${stepBlocks}
              </div>
              <div class="approval-dept-foot">
                <button class="chip-btn" type="button" data-approval-dept-add-step="${escapeHtmlAttr(flow.id)}">＋ ステップ追加</button>
              </div>
            </div>
          </div>
          <div class="af-ed-foot">
            ${deleteBtn}
          </div>
        </div>
      </td>
    </tr>` : ""}
  `;
}

/** メンバー選択モーダル（承認フロー統合タブ用）。kind: "member"（申請できるメンバー・1人1フロー排他）
 *  / "cc"（承認権限なし・通知のみ。排他制約なし＝他フローの担当と重複可）。 */
function renderAfPickerModal() {
  if (!uiState.afPicker) return "";
  const { flowId, kind } = uiState.afPicker;
  const isCc = kind === "cc";
  const kindMeta = APPROVAL_OTHER_KIND_META.find((m) => approvalKindFlowId(m.key) === flowId);
  const flow =
    (uiState.approvalOtherFlows || []).find((f) => f.id === flowId) ||
    (isCc && kindMeta ? { id: flowId, name: kindMeta.label, memberIds: [], ccMemberIds: approvalCcIdsOf(uiState.approvalApprovers?.[flowId]) } : null);
  if (!flow) return "";
  // 追加済みの人も一覧に残し、チェックを付ける（選ぶたびに一覧から消えると行がずれて画面がちらつくため。
  // 2026-10 要望）。もう一度押すと外れる。選んでも閉じず、「閉じる」で閉じる
  const pickedIdSet = new Set(((isCc ? flow.ccMemberIds : flow.memberIds) || []).map(String));
  const candidates = users.filter((u) => (isActiveUser(u) && !u.onLeave && !u.retired) || pickedIdSet.has(u.id));
  const rows = candidates
    .map((u) => {
      const owner = isCc ? null : getApprovalFlowByMemberId(u.id);
      const blocked = !!(owner && owner.id !== flow.id);
      const picked = pickedIdSet.has(u.id);
      const un = escapeHtml(u.name || "?");
      const note = blocked ? `<span class="af-picker-owned">「${escapeHtml(owner.name || "")}」に所属中</span>` : `<span class="approval-picker-mail">${escapeHtml(String(u.email || ""))}</span>`;
      return `<li>
        <button type="button" class="approval-picker-row${blocked ? " af-picker-row--blocked" : ""}${picked ? " is-picked" : ""}" aria-pressed="${picked ? "true" : "false"}" data-af-pick-user="${escapeHtmlAttr(u.id)}"${blocked ? ` data-af-pick-blocked="${escapeHtmlAttr(owner.name || "")}"` : ""}>
          <span class="af-picker-check" aria-hidden="true">✓</span>
          <span class="approval-picker-name">${un}</span>
          ${note}
        </button>
      </li>`;
    })
    .join("");
  return `
    <div class="approval-picker-root" id="afPickerRoot">
      <div class="approval-picker-dlg" role="dialog" aria-modal="true" aria-labelledby="afPickerTitle">
        <div class="approval-picker-scrim" data-af-scrim="1" aria-label="閉じる" tabindex="-1"></div>
        <div class="approval-picker-card">
          <div class="approval-picker-head">
            <h4 class="approval-picker-title" id="afPickerTitle">${isCc ? "CCを追加" : "メンバーを追加"}</h4>
            <button type="button" class="approval-picker-x" data-af-scrim="1" aria-label="閉じる">×</button>
          </div>
          <p class="approval-picker-hint">${escapeHtml(flow.name || "")} — アカウント一覧の担当者から選びます。続けて複数人を選べます（もう一度押すと外れます）。</p>
          <ul class="approval-picker-list">
            ${rows || `<li class="cl-empty" style="padding:12px;">追加できる担当者がいません。</li>`}
          </ul>
          <div class="approval-picker-foot">
            <button type="button" class="cl-btn cl-btn-primary" data-af-scrim="1">閉じる</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function approvalSettingsSectionHtml() {
  ensureApprovalApproversState();
  ensureApprovalApplicationFlowBindingsState();
  // 未保存ガードの基準（読込・保存直後の最初の表示で取る。15z-account-settings-ux.js）
  if (uiState.approvalSettingsBaseline == null) uiState.approvalSettingsBaseline = approvalSettingsSnapshot();
  const dirty = approvalSettingsDirty();
  const flows = uiState.approvalOtherFlows || [];
  const rows = flows.map((f) => approvalFlowRowHtml(f)).join("");
  return `
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <div class="section-head-row">
          <h3 class="mini-title">承認フロー（請求書等の書類の捺印申請）</h3>
        </div>
        <p class="approval-dept-lead">捺印申請は<strong>申請者（営業）の所属フロー</strong>で回ります。営業をフローのメンバーにし、承認ステップに上長を置いてください（所属できるフローは1人1つ）。変更は「承認フローを登録」で保存されます。</p>
        <div class="af-table-scroll">
          <table class="acct-table af-flow-table" data-fp-table="approval-flows">
            <colgroup>
              <col class="af-col-name" />
              <col />
              <col class="af-col-steps" />
              <col class="af-col-act" />
            </colgroup>
            <thead>
              <tr><th>フロー名</th><th>申請する営業</th><th>承認ステップ</th><th></th></tr>
            </thead>
            <tbody>${rows || `<tr><td colspan="4" class="cl-empty" style="padding:16px;">フローがまだありません。「＋ フローを追加」から作成してください。</td></tr>`}</tbody>
          </table>
        </div>
        <div class="af-list-foot">
          <button class="chip-btn" type="button" data-approval-add-other-flow="1">＋ フローを追加</button>
        </div>
      </article>
    </section>
    ${approvalOtherKindsSectionHtml()}
    <section class="section approval-save-bar${dirty ? " is-dirty" : ""}">
      <div class="approval-dept-actions">
        <span class="approval-save-status">${dirty ? "未保存の変更があります" : "変更は「承認フローを登録」で保存されます"}</span>
        <button class="cl-btn cl-btn-primary" type="button" data-approval-save-drafts="1">承認フローを登録</button>
      </div>
    </section>
    ${renderApprovalKindEditModal()}
    ${renderAfPickerModal()}
  `;
}

function accountSettingsSection() {
  ensureDeptSettingsState();
  const rawTab = uiState.accountSettingsTab || "account";
  const tab = rawTab === "budget" || rawTab === "lock"
    ? "account"
    // 旧「承認設定」タブは「承認フロー」に統合（旧URL互換）。部署設定は独立タブのまま
    : (rawTab === "approval-bindings" ? "approval" : rawTab);
  const menuLabelMap = {};
  menu.forEach((m) => { if (m.key) menuLabelMap[m.key] = m.label; });
  const fallbackNavLabels = { "progress-management": "ToDo管理" };
  const excludeRetired = uiState.accountListExcludeRetired !== false;
  const accountListUsers = (excludeRetired ? users.filter((u) => !u.retired) : users)
    .filter((u) => acctListMatchesQuery(u, uiState.accountListQuery))
    .filter((u) => acctListMatchesRoleFilter(u, uiState.accountListRoleFilter));
  const acctRows = accountListUsers.map((u) => {
    const sys = systemRoleOfUser(u);
    const systemRoleBadge = `<span class="role-badge role-tag">${escapeHtml(sys)}</span>`;
    const jobRoleBadge = `<span class="role-badge" style="background:#f1f5f9;color:#475569;">${escapeHtml(primaryJobRole(u))}</span>`;
    const navPerms = normalizeNavPermissions(u.navPermissions || []);
    const navChecks = accountNavPermKeysOrdered().map((key) => {
      const checked = navPerms.includes(key) ? "checked" : "";
      const label = menuLabelMap[key] || fallbackNavLabels[key] || key;
      return `<label class="nav-perm-check"><input type="checkbox" ${checked} data-nav-perm-toggle="${u.id}:${key}" />${label}${fpSaveStatusHtml(`navperm:${u.id}:${key}`)}</label>`;
    });
    const hasPassword = u.hasPassword || !!u.password_hash || !!u.password;
    const inactiveBadge = !isActiveUser(u)
      ? `<span class="role-badge" style="background:#fef3c7;color:#92400e;">ログイン不可</span>`
      : "";
    const statusBadge = hasPassword
      ? `<span class="role-badge" style="background:#ecfdf5;color:#047857;">設定済み</span>`
      : `<span class="role-badge" style="background:#fef2f2;color:#b91c1c;">未設定</span>`;
    return `
      <tr data-acct-row="${escapeHtmlAttr(u.id)}">
        <td><div class="acct-name">${escapeHtml(userNameWithRetiredLabel(u))}</div></td>
        <td data-label="メール"><span class="acct-email">${escapeHtml(u.email || "未登録")}</span></td>
        <td class="acct-col-role" data-label="システム権限">${systemRoleBadge}</td>
        <td class="acct-col-role" data-label="職種">${jobRoleBadge}</td>
        <td class="acct-col-pw" data-label="PW">${inactiveBadge}${statusBadge}</td>
        <td class="acct-perms" data-label="表示メニュー">${acctNavPermsCellHtml(u, navChecks)}</td>
        <td>
          <div class="acct-actions">
            <button class="cl-btn cl-btn-outline cl-btn-sm" type="button" data-user-invite="${u.id}"${u.email ? "" : ` disabled title="メールアドレスが未登録です"`}>${hasPassword ? "再招待" : "招待"}</button>
            <button class="cl-btn cl-btn-outline cl-btn-sm" type="button" data-user-action="edit:${u.id}">編集</button>
            <button class="cl-btn cl-btn-outline cl-btn-sm cl-btn-danger" type="button" data-user-action="delete:${u.id}">削除</button>
          </div>
        </td>
      </tr>
    `;
  }).join("");

  const deptCreateModal = renderDeptCreateModal();
  const accountListCount = accountListUsers.length;
  const excludeRetiredChecked = excludeRetired ? " checked" : "";
  return `
    <section class="section">
      <div class="tab-bar account-tab-bar">
        <button class="tab-btn ${tab === "account" ? "active" : ""}" data-account-settings-tab="account">アカウント一覧</button>
        <button class="tab-btn ${tab === "dept" ? "active" : ""}" data-account-settings-tab="dept">部署設定</button>
        <button class="tab-btn ${tab === "approval" ? "active" : ""}" data-account-settings-tab="approval">承認フロー</button>
      </div>
    </section>
    ${tab === "account" ? `
      <section class="section">
        <article class="soft-card panel-pad section-card">
          <div class="section-head-row">
            <div class="acct-list-head-main">
              <h3 class="mini-title">アカウント一覧<span class="acct-list-count" aria-label="表示件数" data-acct-list-count="1">（${accountListCount}件）</span></h3>
              <label class="acct-list-exclude-retired">
                <input type="checkbox" data-account-list-exclude-retired="1"${excludeRetiredChecked} />
                <span>退職のユーザーを除く</span>
              </label>
              ${acctRoleFilterDropdownHtml()}
              <input type="search" class="acct-search" data-acct-search="1" value="${escapeHtmlAttr(uiState.accountListQuery || "")}" placeholder="名前・メールで絞り込み" aria-label="名前・メールで絞り込み" />
            </div>
            <div class="head-tools acct-head-tools">
              <button class="chip-btn" data-csv-download-users="1">CSVテンプレート</button>
              <label class="chip-btn" style="cursor:pointer;">CSVインポート<input type="file" accept=".csv" data-csv-import-users="1" style="display:none;" /></label>
              <button class="chip-btn" data-open-user-manager="new">＋ 新規アカウント作成</button>
            </div>
          </div>
          <table class="acct-table acct-table--users fp-sp-card-table" data-fp-table="accounts">
            <colgroup>
              <col class="acct-col-name" />
              <col class="acct-col-email" />
              <col class="acct-col-sys" />
              <col class="acct-col-job" />
              <col class="acct-col-pw" />
              <col class="acct-col-perms" />
              <col class="acct-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>名前</th>
                <th>メールアドレス</th>
                <th>システム権限</th>
                <th>職種</th>
                <th>PW</th>
                <th>表示メニュー</th>
                <th></th>
              </tr>
            </thead>
            <tbody>${acctRows}</tbody>
          </table>
        </article>
      </section>
    ` : ""}
    ${tab === "dept" ? deptKanbanSection(uiState.deptSettings, deptCreateModal) : ""}
    ${tab === "approval" ? approvalSettingsSectionHtml() : ""}
  `;
}

/** 削除済みデータ復元タブ */
function restoreDeletedTabHtml() {
  const st = uiState.restoreDeleted || { type: "project", items: null, loading: false, msg: "" };
  const types = [
    { key: "project", label: "案件" },
    { key: "client",  label: "クライアント" },
    { key: "partner", label: "パートナー" },
  ];
  const typeSelect = types.map(t =>
    `<button type="button" class="chip-btn${st.type === t.key ? " active" : ""}" data-restore-type="${escapeHtmlAttr(t.key)}">${escapeHtml(t.label)}</button>`
  ).join("");

  let body = "";
  if (st.loading) {
    body = `<p class="project-lock-auto-preview">読み込み中…</p>`;
  } else if (st.items === null) {
    body = `<p class="project-lock-auto-preview">種別を選んで「一覧を取得」してください。</p>`;
  } else if (st.items.length === 0) {
    body = `<p class="project-lock-auto-preview project-lock-auto-preview--ok">✓ 削除済みデータはありません。</p>`;
  } else {
    const rows = st.items.map(item => `
      <tr>
        <td style="padding:8px 12px;">${escapeHtml(item.name || `id:${item.id}`)}</td>
        <td style="padding:8px 12px;color:#64748b;font-size:12px;">${escapeHtml(restoreDeletedDateLabel(item.updated_at))}</td>
        <td style="padding:8px 12px;">
          <button type="button" class="chip-btn" data-restore-item="${escapeHtmlAttr(st.type)}:${item.id}" data-restore-item-name="${escapeHtmlAttr(item.name || String(item.id))}">復元する</button>
        </td>
      </tr>`).join("");
    body = `<div style="overflow-x:auto;"><table class="pj-table" style="min-width:400px;" data-fp-table="restore-deleted">
      <thead><tr><th>名称</th><th>削除日時</th><th>操作</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
  }

  return `<section class="section">
    <article class="soft-card panel-pad section-card">
      <h3 class="mini-title">削除済みデータの復元</h3>
      <p class="project-lock-desc">論理削除されたデータを復元します。操作ログに記録されます。</p>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:12px;">
        ${typeSelect}
        <button type="button" class="inline-btn ghost" data-restore-load="1" style="margin-left:auto;">一覧を取得</button>
      </div>
      ${st.msg ? `<p style="color:#dc2626;font-size:13px;margin-bottom:8px;">${escapeHtml(st.msg)}</p>` : ""}
      ${body}
    </article>
  </section>`;
}


/** 削除済みデータ復元の「削除日時」。サーバーは timestamptz を ISO（UTC）で返すため、先頭10文字だと
 *  日本時間 0〜9時に削除したものが前日に見えた。端末の日付（JST）で表示する */
function restoreDeletedDateLabel(raw) {
  const d = new Date(raw);
  return raw && !Number.isNaN(d.getTime()) ? localDateToYmd(d) : String(raw || "").slice(0, 10);
}
// 操作ログの resource_type → 日本語ラベル
const AUDIT_RESOURCE_LABELS = {
  project: "案件", client: "クライアント", partner: "パートナー",
  cost: "原価", user: "ユーザー", auth: "認証",
  todo_project: "TODOプロジェクト", todo_issue: "TODO課題", todo_group: "TODOグループ"
};
// action → 日本語ラベル
const AUDIT_ACTION_LABELS = {
  create: "作成", update: "更新", delete: "削除", login: "ログイン",
  login_failed: "ログイン失敗", login_blocked: "ロック", restore: "復元",
  approve: "承認", reject: "却下",
  archive: "アーカイブ", auto_archive: "自動アーカイブ"
};
// 発行履歴の発行経路 → 表示ラベル。**キーはDBに入る値**なので、サーバの許可リスト
// （api/invoice-issue-history.js の ISSUE_SOURCES）と同じキーに揃えること（テストで固定）。
const ISSUE_SOURCE_LABELS = {
  pdf: "PDF発行", email: "メール送付", postal: "郵送"
};
// action → バッジカラー
const AUDIT_ACTION_COLORS = {
  create: "#16a34a", update: "#2563eb", delete: "#dc2626",
  restore: "#d97706", login: "#6366f1", login_failed: "#f59e0b",
  login_blocked: "#dc2626", approve: "#16a34a", reject: "#dc2626",
  archive: "#64748b", auto_archive: "#94a3b8"
};

/** 案件名の表示用。作成直後は未入力（クライアント選択だけで作るため）なので、
 *  一覧・詳細では空欄でなく未設定と分かる文言を出す。書類の「件名」には使わない。 */
function projectDisplayName(p) {
  const n = String(p?.name || "").trim();
  return n || "（案件名未設定）";
}

/** 操作ログの「対象」を人間が読める文字列に変換 */
function auditLogTargetLabel(resourceType, resourceId) {
  if (!resourceType) return "—";
  const typeLabel = AUDIT_RESOURCE_LABELS[resourceType] || resourceType;
  if (!resourceId || resourceId === "null") return typeLabel;
  // auth（認証）: 操作者列と同じになるため対象は「ログイン」のみ表示
  if (resourceType === "auth") return "ログイン";
  // 数値IDは対応する名前をメモリから引く
  const numId = Number(resourceId);
  if (resourceType === "project" && Number.isFinite(numId)) {
    const p = projects.find(x => x.id == numId || x.dbId == numId);
    if (p?.name) return `案件「${p.name}」`;
  }
  if (resourceType === "client" && Number.isFinite(numId)) {
    const c = (uiState.clients || []).find(x => x.id == numId || x.dbId == numId);
    if (c?.name) return `クライアント「${c.name}」`;
  }
  if (resourceType === "user" && Number.isFinite(numId)) {
    const u = users.find(x => x.id == numId || x.dbId == numId);
    if (u?.name) return `ユーザー「${u.name}」`;
  }
  // 複数IDのカンマ区切り（bulk delete等）
  if (resourceId.includes(",")) return `${typeLabel}（${resourceId.split(",").length}件）`;
  return `${typeLabel} #${resourceId}`;
}

/** 操作ログ閲覧タブ */
function auditLogTabHtml() {
  const al    = uiState.auditLogView   || { items: null, loading: false, hasMore: false, offset: 0 };
  const flt   = uiState.auditLogFilter || { userId: "", resourceType: "", action: "", dateFrom: "", dateTo: "" };

  // フィルターUI
  const actionOpts = ["", "create", "update", "delete", "archive", "auto_archive", "login", "login_failed", "login_blocked", "restore", "approve", "reject"]
    .map(a => `<option value="${escapeHtmlAttr(a)}"${flt.action === a ? " selected" : ""}>${a ? (AUDIT_ACTION_LABELS[a] || a) : "すべての操作"}</option>`).join("");
  const typeOpts = ["", "project", "client", "partner", "cost", "user", "auth", "todo_project", "todo_issue", "todo_group"]
    .map(t => `<option value="${escapeHtmlAttr(t)}"${flt.resourceType === t ? " selected" : ""}>${t ? (AUDIT_RESOURCE_LABELS[t] || t) : "すべての種別"}</option>`).join("");
  const userOpts = `<option value="">すべてのユーザー</option>` +
    activeUsers().map(u => `<option value="${escapeHtmlAttr(String(u.dbId || u.id))}"${flt.userId === String(u.dbId || u.id) ? " selected" : ""}>${escapeHtml(u.name)}</option>`).join("");

  const filterBar = `
    <div class="al-filter-bar">
      <select class="pj-input al-filter-sel" data-audit-filter="action" data-search-dropdown-off="1">${actionOpts}</select>
      <select class="pj-input al-filter-sel" data-audit-filter="resourceType" data-search-dropdown-off="1">${typeOpts}</select>
      <select class="pj-input al-filter-sel" data-audit-filter="userId" data-search-dropdown-off="1">${userOpts}</select>
      <input type="date" class="pj-input al-filter-date" data-audit-filter="dateFrom" value="${escapeHtmlAttr(flt.dateFrom)}" title="開始日" />
      <span style="color:var(--fp-text-muted);align-self:center;">〜</span>
      <input type="date" class="pj-input al-filter-date" data-audit-filter="dateTo" value="${escapeHtmlAttr(flt.dateTo)}" title="終了日" />
      <button type="button" class="inline-btn" data-audit-log-load="1" style="white-space:nowrap;">検索</button>
      <button type="button" class="inline-btn ghost" data-audit-log-reset="1" style="white-space:nowrap;">リセット</button>
    </div>`;

  let body = "";
  if (al.loading && (!al.items || al.items.length === 0)) {
    body = `<p class="project-lock-auto-preview">読み込み中…</p>`;
  } else if (al.items === null) {
    body = `<p class="project-lock-auto-preview">上の条件で検索するか、そのまま「検索」を押すと直近200件を表示します。</p>`;
  } else if (al.items.length === 0) {
    body = `<p class="project-lock-auto-preview project-lock-auto-preview--ok">条件に一致するログがありません。</p>`;
  } else {
    const rows = al.items.map(item => {
      let dt = "—";
      try {
        const d = new Date(item.created_at);
        if (!isNaN(d)) dt = d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
      } catch { dt = String(item.created_at || "").replace("T", " ").slice(0, 16); }
      const actionLabel = AUDIT_ACTION_LABELS[item.action] || item.action || "—";
      const actionColor = AUDIT_ACTION_COLORS[item.action] || "#64748b";
      const targetLabel = auditLogTargetLabel(item.resource_type, item.resource_id);
      return `<tr>
        <td class="al-td al-td--time">${escapeHtml(dt)}</td>
        <td class="al-td">${escapeHtml(item.user_name || "—")}</td>
        <td class="al-td"><span class="al-badge" style="background:${actionColor}1a;color:${actionColor};">${escapeHtml(actionLabel)}</span></td>
        <td class="al-td">${escapeHtml(targetLabel)}</td>
        <td class="al-td al-td--detail" title="${escapeHtmlAttr(item.detail || "")}">${escapeHtml(item.detail || "")}</td>
      </tr>`;
    }).join("");
    const moreBtn = al.hasMore
      ? `<div style="text-align:center;margin-top:12px;">
           <button type="button" class="inline-btn ghost" data-audit-log-more="1">${al.loading ? "読み込み中…" : "さらに過去のログを読み込む"}</button>
         </div>`
      : `<p style="text-align:center;font-size:12px;color:var(--fp-text-muted);margin-top:8px;">— ${al.items.length}件表示（これ以上なし） —</p>`;
    body = `<div style="overflow-x:auto;"><table class="pj-table al-table" data-fp-table="audit-log">
      <thead><tr><th>日時</th><th>操作者</th><th>操作</th><th>対象</th><th>詳細</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>${moreBtn}`;
  }

  return `<section class="section">
    <article class="soft-card panel-pad section-card">
      <h3 class="mini-title" style="margin-bottom:12px;">操作ログ</h3>
      ${filterBar}
      <div style="margin-top:14px;">${body}</div>
    </article>
  </section>`;
}

function parseBudgetFiscalPeriodNumberFromLabel(label) {
  const m = String(label || "").match(/(\d+)/);
  if (!m) return 0;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalizeBudgetPeriodClient(p) {
  const label = String(p?.label || "");
  const fromApi = Number(p?.periodNumber);
  const num = Number.isFinite(fromApi) && fromApi > 0
    ? fromApi
    : parseBudgetFiscalPeriodNumberFromLabel(label);
  return {
    id: String(p?.id || ""),
    label: label || (num ? `${num}期` : ""),
    rangeLabel: String(p?.rangeLabel || ""),
    entryMode: p?.entryMode === "past" ? "past" : "current_future",
    periodNumber: Number.isFinite(num) && num > 0 ? num : 0
  };
}

function sortBudgetPeriodsInPlace() {
  if (!Array.isArray(uiState.budgetPeriods)) return;
  uiState.budgetPeriods.sort((a, b) => {
    const an = Number(a.periodNumber) || parseBudgetFiscalPeriodNumberFromLabel(a.label) || 999999;
    const bn = Number(b.periodNumber) || parseBudgetFiscalPeriodNumberFromLabel(b.label) || 999999;
    if (an !== bn) return an - bn;
    return String(a.id).localeCompare(String(b.id));
  });
}

/** sortBudgetPeriodsInPlace 後の配列で末尾＝期番号が最大の会計期（予算設定・予算進捗のデフォルト選択用） */
function latestBudgetPeriodFromSorted(periods) {
  const arr = Array.isArray(periods) ? periods : [];
  if (!arr.length) return null;
  return arr[arr.length - 1];
}

function latestBudgetPeriodIdFromSorted(periods) {
  const p = latestBudgetPeriodFromSorted(periods);
  return p?.id ? String(p.id) : "";
}

function ensureBudgetPeriodsState() {
  if (!Array.isArray(uiState.budgetPeriods)) uiState.budgetPeriods = [];
  sortBudgetPeriodsInPlace();
  if (!uiState.selectedBudgetPeriodId || !uiState.budgetPeriods.some((p) => p.id === uiState.selectedBudgetPeriodId)) {
    uiState.selectedBudgetPeriodId = latestBudgetPeriodIdFromSorted(uiState.budgetPeriods);
  }
}

function sanitizeBudgetPeriodNumberInput(raw) {
  return String(raw || "").replace(/\D/g, "").slice(0, 4);
}

/** YYYY/MM（月は1〜2桁、保存時は2桁にそろえる） */
function normalizeYmStrict(raw) {
  const t = String(raw || "").trim();
  const m = t.match(/^(\d{4})[\/\-](\d{1,2})$/);
  if (!m) return "";
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return "";
  return `${m[1]}/${String(mo).padStart(2, "0")}`;
}

function readBudgetDraftStore() {
  return budgetDraftMemoryStore && typeof budgetDraftMemoryStore === "object" ? budgetDraftMemoryStore : {};
}

function saveBudgetDraftStore(store, opts) {
  budgetDraftMemoryStore = store && typeof store === "object" ? JSON.parse(JSON.stringify(store)) : {};
  if (!uiState.budgetRowsLoadedByPeriod || typeof uiState.budgetRowsLoadedByPeriod !== "object") {
    uiState.budgetRowsLoadedByPeriod = {};
  }
  Object.keys(readBudgetDraftStore()).forEach((id) => {
    if (id) uiState.budgetRowsLoadedByPeriod[id] = true;
  });
  // persistToServer:false は予算マスタの手編集用（「保存」ボタンでまとめてサーバーへ送る）
  if (!opts || opts.persistToServer !== false) schedulePersistBudgetMasterRowsToServer();
}

function budgetRowsLoadedForPeriod(periodId) {
  const id = String(periodId || "").trim();
  if (!id) return true;
  const store = readBudgetDraftStore();
  return Boolean(uiState.budgetRowsLoadedByPeriod?.[id]) || Object.prototype.hasOwnProperty.call(store, id);
}

function budgetRowsLoadFailedForPeriod(periodId) {
  const id = String(periodId || "").trim();
  return Boolean(id && uiState.budgetRowsLoadFailedByPeriod?.[id]);
}

async function loadBudgetRowsForPeriod(periodId, opts = {}) {
  const id = String(periodId || "").trim();
  if (!authToken || !id || budgetRowsLoadedForPeriod(id)) return true;
  if (!userCanAccessPage("budget-progress") && !userCanAccessPage("budget-admin")) return false;
  if (budgetRowsLoadFailedForPeriod(id)) return false;
  if (!uiState.budgetRowsLoadedByPeriod || typeof uiState.budgetRowsLoadedByPeriod !== "object") {
    uiState.budgetRowsLoadedByPeriod = {};
  }
  if (!uiState.budgetRowsLoadingByPeriod || typeof uiState.budgetRowsLoadingByPeriod !== "object") {
    uiState.budgetRowsLoadingByPeriod = {};
  }
  if (uiState.budgetRowsLoadingByPeriod[id] && budgetMasterRowsFetchPromises[id]) {
    return budgetMasterRowsFetchPromises[id];
  }
  uiState.budgetRowsLoadingByPeriod[id] = true;
  const renderOnComplete = opts.renderOnComplete !== false;
  const fetchPromise = (async () => {
    try {
      const q = new URLSearchParams({ periodKey: id });
      const res = await fetch(`/api/budget-master-rows?${q.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) {
        if (!uiState.budgetRowsLoadFailedByPeriod || typeof uiState.budgetRowsLoadFailedByPeriod !== "object") {
          uiState.budgetRowsLoadFailedByPeriod = {};
        }
        uiState.budgetRowsLoadFailedByPeriod[id] = true;
        return false;
      }
      const data = await res.json().catch(() => ({}));
      const byPeriod = data && typeof data.byPeriod === "object" && data.byPeriod ? data.byPeriod : {};
      const store = readBudgetDraftStore();
      const next = { ...store };
      if (Object.prototype.hasOwnProperty.call(byPeriod, id)) {
        next[id] = byPeriod[id];
        rememberBudgetServerPeriods({ [id]: byPeriod[id] });
      } else if (!Object.prototype.hasOwnProperty.call(next, id)) {
        next[id] = { rows: [], extendSales: true };
      }
      budgetDraftMemoryStore = JSON.parse(JSON.stringify(next));
      uiState.budgetRowsByPeriod = next;
      uiState.budgetRowsLoadedByPeriod[id] = true;
      if (uiState.budgetRowsLoadFailedByPeriod) delete uiState.budgetRowsLoadFailedByPeriod[id];
      return true;
    } catch {
      if (!uiState.budgetRowsLoadFailedByPeriod || typeof uiState.budgetRowsLoadFailedByPeriod !== "object") {
        uiState.budgetRowsLoadFailedByPeriod = {};
      }
      uiState.budgetRowsLoadFailedByPeriod[id] = true;
      return false;
    } finally {
      delete uiState.budgetRowsLoadingByPeriod[id];
      if (budgetMasterRowsFetchPromises[id] === fetchPromise) {
        delete budgetMasterRowsFetchPromises[id];
      }
      if (renderOnComplete) {
        try {
          render();
        } catch {
          /* noop */
        }
      }
    }
  })();
  budgetMasterRowsFetchPromises[id] = fetchPromise;
  return fetchPromise;
}

function ensureSelectedBudgetRowsRequested() {
  ensureBudgetPeriodsState();
  if (!userCanAccessPage("budget-progress") && !userCanAccessPage("budget-admin")) return false;
  const period =
    uiState.budgetPeriods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(uiState.budgetPeriods);
  if (!period?.id || budgetRowsLoadedForPeriod(period.id)) return false;
  if (budgetRowsLoadFailedForPeriod(period.id)) return false;
  void loadBudgetRowsForPeriod(period.id);
  return true;
}

const BUDGET_MONTH_KEYS = ["m04", "m05", "m06", "m07", "m08", "m09", "m10", "m11", "m12", "m01", "m02", "m03"];
const BUDGET_MONTH_LABELS = ["4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月", "1月", "2月", "3月"];

const BUDGET_MASTER_CSV_FIXED_COL_COUNT = 2 + BUDGET_MONTH_KEYS.length;

function escapeHtmlAttr(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * モーダルが既にDOMに存在する場合は fp-drawer-no-enter を返す。
 * スライドアップアニメーションの再実行（ちらつき）を防ぐ。
 * @param {string} uniqueClass モーダルを一意に識別するクラス名
 */
function spNoEnter(uniqueClass) {
  return typeof document !== "undefined" && document.querySelector(`aside.composer-panel.${uniqueClass}`)
    ? " fp-drawer-no-enter"
    : "";
}

/** インラインHTMLへの差し込み用（項目名など）。引用符もエスケープする
 *  （属性値に誤って使われても値から抜け出せないように。テキスト中では見た目は変わらない） */
function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** 書類の宛名敬称は2値：個人（honorific が「様」）なら様、それ以外は御中。
 *  サーバ側の同等ロジックは api/_docRender.js の resolveRecipientHonorific（両側同時に直すこと） */
function isIndividualHonorific(honorific) {
  return String(honorific || "").trim() === "様";
}

function resolveRecipientHonorific(honorific) {
  return isIndividualHonorific(honorific) ? "様" : "御中";
}

/** 郵便番号入力の整形: 数字以外を除去し、4桁目からハイフンを自動挿入（最大7桁）。
 *  クライアント/顧客支社/パートナーマスタの郵便番号欄で共通利用。 */
function formatPostalCodeAsTyped(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 7);
  return digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
}

// クライアント/顧客支社/パートナーマスタの郵便番号欄をタイプ中に整形する（数字以外を弾く）。
// 各フォームの state 同期リスナー（16-render-routing.js・15g-client-branches.js）より
// 先にファイル読込されるため、同じ input イベントで先に値を整形してから後続リスナーへ渡る。
document.addEventListener("input", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (
    target.dataset.clientField === "postalCode" ||
    target.dataset.clientBranchField === "postalCode" ||
    target.dataset.partnerField === "postalCode"
  ) {
    target.value = formatPostalCodeAsTyped(target.value);
  }
});

function parseDeliveryMethodParts(raw) {
  return String(raw ?? "")
    .trim()
    .split(/\s*\+\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeDeliveryMethodStored(raw) {
  const alias = { メール添付: "メール(添付)", メール: "メール(DL)" };
  const allowed = new Set(CONTRACT_DELIVERY_METHOD_PARTS);
  const seen = new Set();
  const out = [];
  for (let p of parseDeliveryMethodParts(raw)) {
    if (Object.prototype.hasOwnProperty.call(alias, p)) p = alias[p];
    if (!allowed.has(p)) continue;
    if (seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out.join("+");
}

/** mode: "client" | "partner" — 送付方法の複数選択UI */
function deliveryMethodCheckboxGroupHtml(rawValue, mode) {
  const normalized = normalizeDeliveryMethodStored(rawValue);
  const selected = new Set(parseDeliveryMethodParts(normalized));
  const dataAttr = mode === "partner" ? "data-partner-delivery-part" : "data-client-delivery-part";
  const boxes = CONTRACT_DELIVERY_METHOD_PARTS.map((opt) => {
    const checked = selected.has(opt) ? "checked" : "";
    return `<label class="cl-inline-check"><input type="checkbox" class="cl-delivery-method-checkbox" ${dataAttr}="${escapeHtmlAttr(opt)}" ${checked} /><span>${escapeHtml(opt)}</span></label>`;
  }).join("");
  const hint = mode === "contract" ? "複数可" : "複数可（+で保存）";
  return `<div class="cl-delivery-method-group">${boxes}<span class="cl-delivery-method-hint">${hint}</span></div>`;
}

/** ドメイン管理「サービス」の複数選択UI（"+"連結で保存。書類送付方法と同方式） */
function domainServiceCheckboxGroupHtml(rawValue) {
  const selected = new Set(parseDeliveryMethodParts(rawValue));
  const boxes = DOMAIN_SERVICE_KIND_OPTIONS.map((opt) => {
    const checked = selected.has(opt) ? "checked" : "";
    return `<label class="cl-inline-check"><input type="checkbox" class="cl-delivery-method-checkbox" data-domain-service-part="${escapeHtmlAttr(opt)}" ${checked} /><span>${escapeHtml(opt)}</span></label>`;
  }).join("");
  return `<div class="cl-delivery-method-group">${boxes}<span class="cl-delivery-method-hint">複数可</span></div>`;
}

/** 保存値（"+"連結）→ 一覧表示用テキスト（既定の順に並べ、未知値は末尾に保持） */
function domainServicesDisplay(rawValue) {
  const parts = parseDeliveryMethodParts(rawValue);
  if (parts.length === 0) return "";
  const selected = new Set(parts);
  const known = DOMAIN_SERVICE_KIND_OPTIONS.filter((p) => selected.has(p));
  const extras = parts.filter((p) => !DOMAIN_SERVICE_KIND_OPTIONS.includes(p));
  return [...known, ...extras].join("・");
}

/** Word XML テキストノード用エスケープ */
function escapeWordXmlText(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function getClientCreateModalRootEl() {
  const btn = document.querySelector('[data-save-client="1"]');
  return btn?.closest("aside.composer-panel") ?? null;
}

/** 画面上の入力値を uiState.clientCreate に取り込む（未保存の編集を出力に反映） */
function syncClientCreateFormIntoState() {
  const aside = getClientCreateModalRootEl();
  if (!aside || !uiState.clientCreate) return;
  aside.querySelectorAll("[data-client-field]").forEach((el) => {
    const key = el.dataset.clientField;
    if (!key) return;
    if (el instanceof HTMLInputElement && el.type === "radio") {
      if (el.checked) uiState.clientCreate[key] = el.value;
    } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      uiState.clientCreate[key] = el.value;
    }
  });
  const parts = new Set();
  aside.querySelectorAll("[data-client-delivery-part]").forEach((chk) => {
    if (chk instanceof HTMLInputElement && chk.type === "checkbox" && chk.checked) {
      parts.add(String(chk.dataset.clientDeliveryPart || ""));
    }
  });
  uiState.clientCreate.deliveryMethod = CONTRACT_DELIVERY_METHOD_PARTS.filter((p) => parts.has(p)).join("+");
  // 個人✓（敬称）は data-client-field ではないので明示的に取り込む
  const indivEl = aside.querySelector("[data-client-individual]");
  if (indivEl instanceof HTMLInputElement) uiState.clientCreate.honorific = indivEl.checked ? "様" : "御中";
}

let pizzipLoadPromise = null;
function ensurePizZipLoaded() {
  if (typeof window !== "undefined" && window.PizZip) return Promise.resolve();
  if (pizzipLoadPromise) return pizzipLoadPromise;
  pizzipLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/pizzip@3.1.7/dist/pizzip.min.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("PizZip の読み込みに失敗しました"));
    document.head.appendChild(s);
  });
  return pizzipLoadPromise;
}

const PDFJS_CDN_BASE = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build";
let pdfjsLoadPromise = null;

function getPdfJsLib() {
  if (typeof window === "undefined") return null;
  return window.pdfjsLib || window["pdfjs-dist/build/pdf"] || null;
}

function configurePdfJsWorker(lib) {
  if (!lib || !lib.GlobalWorkerOptions) return;
  lib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN_BASE}/pdf.worker.min.js`;
}

function ensurePdfJsLoaded() {
  const existing = getPdfJsLib();
  if (existing) {
    configurePdfJsWorker(existing);
    if (!window.pdfjsLib) window.pdfjsLib = existing;
    return Promise.resolve();
  }
  if (pdfjsLoadPromise) return pdfjsLoadPromise;
  pdfjsLoadPromise = new Promise((resolve, reject) => {
    const fail = (err) => {
      pdfjsLoadPromise = null;
      reject(err);
    };
    const s = document.createElement("script");
    s.src = `${PDFJS_CDN_BASE}/pdf.min.js`;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = () => {
      try {
        const lib = getPdfJsLib();
        if (!lib) {
          fail(new Error("PDF.js を初期化できませんでした（グローバルが見つかりません）"));
          return;
        }
        configurePdfJsWorker(lib);
        if (!window.pdfjsLib) window.pdfjsLib = lib;
      } catch (e) {
        fail(e instanceof Error ? e : new Error(String(e)));
        return;
      }
      resolve();
    };
    s.onerror = () => fail(new Error("PDF.js の読み込みに失敗しました"));
    document.head.appendChild(s);
  });
  return pdfjsLoadPromise;
}

function kabushikiKaishaSuffix(fullName) {
  const n = String(fullName || "").trim();
  if (n.startsWith("株式会社")) return n.slice(4).trim() || n;
  return n;
}

function buildNdaPurposeClauseText(c) {
  const purpose = String(c.ndaPurpose || "").trim().replace(/\r\n|\n|\r/g, " ");
  if (purpose) return purpose.slice(0, 800);
  return "（本契約の目的を記載してください）";
}

function formatContractWarekiLongDate() {
  const d = new Date();
  try {
    return new Intl.DateTimeFormat("ja-JP-u-ca-japanese", { era: "long", year: "numeric", month: "long", day: "numeric" }).format(d);
  } catch {
    return new Intl.DateTimeFormat("ja-JP", { year: "numeric", month: "long", day: "numeric" }).format(d);
  }
}

function sanitizeClientDocDownloadBasename(s) {
  const t = String(s || "client").replace(/[/\\?%*:|"<>]/g, "_").trim();
  return t.slice(0, 72) || "client";
}

const NDA_DOCX_PLACEHOLDER_COMPANY = "株式会社●●●";
const NDA_DOCX_PLACEHOLDER_PURPOSE =
  "【例】「ＷＥＢコンテンツ及びサービス事業の市場性の共同研究、企画、開発、制作」";
const GYOMU_DOCX_DATE_PLACEHOLDER = "令和●年●月●日";

/** .docx は ZIP（先頭が PK）。Content-Type が正しくても中身が index.html のときがあるため併用 */
function isDocxZipArrayBuffer(buf) {
  if (!buf || buf.byteLength < 4) return false;
  const a = new Uint8Array(buf.slice(0, 4));
  return a[0] === 0x50 && a[1] === 0x4b;
}

function responseLooksLikeDocx(res, buf) {
  const ct = String(res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("text/html")) return isDocxZipArrayBuffer(buf);
  if (ct.includes("wordprocessingml") || ct.includes("application/vnd.openxmlformats")) return true;
  if (ct.includes("application/zip") || ct.includes("application/x-zip")) return true;
  if (ct.includes("application/octet-stream")) return true;
  return isDocxZipArrayBuffer(buf);
}

/**
 * Word は <w:t> に xml:space 等を付けることがあるため、内側テキスト一致で初出のみ置換する。
 * @returns 置換後 XML、見つからなければ null
 */
function replaceDocxWTextInnerOnce(xml, innerText, newInnerForWT) {
  const esc = String(innerText).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`<w:t[^>]*>${esc}</w:t>`);
  if (!re.test(xml)) return null;
  return xml.replace(re, `<w:t>${newInnerForWT}</w:t>`);
}

/**
 * クライアント/パートナー編集モーダル用：テンプレ .docx の document.xml を置換してダウンロード。
 * テンプレは client-templates/nda-template.docx と gyomu-itaku-template.docx（リポジトリ同梱、両者共用）。
 * mode="partner" のときは uiState.partnerCreate から読む。
 */
async function downloadFilledClientContractDocx(kind, mode, stateOverride) {
  // stateOverride: 編集フォームを開かずに保存済みデータ({name, shortName, ndaPurpose})から出力する経路
  // （取引先詳細の表示ビューのヘッダーから使う。フォームが無いので sync は呼ばない）
  const isPartner = mode === "partner";
  const c = stateOverride || (isPartner ? uiState.partnerCreate : uiState.clientCreate);
  if (!c) return;
  if (!isPartner && !stateOverride) syncClientCreateFormIntoState(); // パートナー欄はdata-partner-fieldが常時stateへ反映済みのため不要
  await ensurePizZipLoaded();
  const PizZipCtor = window.PizZip;
  if (!PizZipCtor) {
    showToastError("ZIPライブラリの読み込みに失敗しました");
    return;
  }
  const path = kind === "nda" ? "client-templates/nda-template.docx" : "client-templates/gyomu-itaku-template.docx";
  const url = new URL(path, window.location.href).href;
  let res;
  try {
    res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
  } catch (netErr) {
    console.error(netErr);
    showToastError("テンプレートの取得に失敗しました（ネットワーク）");
    return;
  }
  if (!res.ok) {
    showToast(`テンプレートが見つかりません（${path}）`);
    return;
  }
  const buf = await res.arrayBuffer();
  if (!responseLooksLikeDocx(res, buf)) {
    showToast(
      "テンプレートの取得に失敗しました。HTML が返っている可能性があります。キャッシュを消すか、client-templates の .docx がその URL で配信されているか確認してください。"
    );
    return;
  }
  let xml;
  try {
    const zip = new PizZipCtor(buf);
    const f = zip.file("word/document.xml");
    if (!f) {
      showToast("テンプレートが不正です（document.xml なし）");
      return;
    }
    xml = f.asText();
    if (kind === "nda") {
      const nameInner = escapeWordXmlText(String(c.name || "").trim() || "（顧客名）");
      let next = replaceDocxWTextInnerOnce(xml, NDA_DOCX_PLACEHOLDER_COMPANY, nameInner);
      if (next == null) {
        showToast("NDAテンプレの差し込み位置が見つかりません（社名プレースホルダ変更済みの可能性）");
        return;
      }
      xml = next;
      if (xml.includes(NDA_DOCX_PLACEHOLDER_PURPOSE)) {
        const purposeInner = escapeWordXmlText(buildNdaPurposeClauseText(c));
        const nextPurpose = replaceDocxWTextInnerOnce(xml, NDA_DOCX_PLACEHOLDER_PURPOSE, purposeInner);
        if (nextPurpose != null) xml = nextPurpose;
        // 「本契約の目的」が空だと目的条項は仮文言のまま。気づかず送付しないよう明示する
        if (!String(c.ndaPurpose || "").trim()) {
          showToast("「本契約の目的」が未入力のため、NDAの目的条項は仮文言のままです。ダウンロード後に必ず記入してください");
        }
      }
    } else {
      const suffixInner = escapeWordXmlText(kabushikiKaishaSuffix(String(c.name || "").trim()));
      let next =
        replaceDocxWTextInnerOnce(xml, "●●●", suffixInner) ||
        replaceDocxWTextInnerOnce(xml, "\u25cf\u25cf\u25cf", suffixInner);
      if (next == null) {
        showToast(
          "業務委託テンプレの差し込み位置が見つかりません。社名後ろの「●●●」が1つの書式内にあるか確認してください。"
        );
        return;
      }
      xml = next;
      if (xml.includes(GYOMU_DOCX_DATE_PLACEHOLDER)) {
        const dateInner = escapeWordXmlText(formatContractWarekiLongDate());
        const nextDate = replaceDocxWTextInnerOnce(xml, GYOMU_DOCX_DATE_PLACEHOLDER, dateInner);
        if (nextDate != null) xml = nextDate;
      }
    }
    zip.file("word/document.xml", xml);
    let out;
    try {
      out = zip.generate({ type: "blob", compression: "DEFLATE" });
    } catch (genErr) {
      console.warn("PizZip blob generate failed, fallback uint8array", genErr);
      const u8 = zip.generate({ type: "uint8array", compression: "DEFLATE" });
      out = new Blob([u8], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });
    }
    const baseName = sanitizeClientDocDownloadBasename(c.shortName || c.name || (isPartner ? "partner" : "client"));
    const label = kind === "nda" ? "NDA" : "業務委託基本契約書";
    const a = document.createElement("a");
    const href = URL.createObjectURL(out);
    a.href = href;
    a.download = `${baseName}_${label}.docx`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
    showToast(`${label}をダウンロードしました`);
  } catch (err) {
    console.error(err);
    const msg = err instanceof Error ? err.message : String(err);
    showToast(msg ? `ドキュメントの生成に失敗しました（${msg.slice(0, 120)}）` : "ドキュメントの生成に失敗しました");
  }
}

/** ヘッダー名から列インデックスへのマッピング（見つからないときは -1）。 */
function mapBudgetMasterCsvHeaders(headerCells) {
  const norm = (s) =>
    String(s || "")
      .trim()
      .replace(/[０-９]/g, (ch) =>
        ch >= "０" && ch <= "９" ? String.fromCharCode(ch.charCodeAt(0) - 0xfee0) : ch
      );
  const cells = headerCells.map((h) => norm(h));
  const findOne = (want) => cells.findIndex((c) => want.includes(c));
  const map = {
    dept: findOne(["部署", "部門", "dept", "department", "Dept", "DEPARTMENT"]),
    member: findOne(["担当者", "member", "名前", "氏名", "担当", "担当名", "name"])
  };
  BUDGET_MONTH_KEYS.forEach((k, i) => {
    map[k] = findOne([BUDGET_MONTH_LABELS[i], k, k.toUpperCase()]);
  });
  return map;
}

/** 1行目がデータっぽく、列数がちょうどあるときの固定列マップ（部署・担当者・12ヶ月）。 */
function budgetMasterCsvFixedColumnMap() {
  const o = { dept: 0, member: 1 };
  BUDGET_MONTH_KEYS.forEach((k, i) => {
    o[k] = 2 + i;
  });
  return o;
}

function rowFromBudgetCsvCells(cells, colMap) {
  const getField = (field) => {
    const i = colMap[field];
    if (typeof i !== "number" || i < 0 || i >= cells.length) return "";
    return cells[i];
  };
  const raw = { dept: getField("dept"), member: getField("member") };
  BUDGET_MONTH_KEYS.forEach((k) => {
    raw[k] = getField(k);
  });
  return normalizeBudgetRow(raw);
}

/** スプレッドシート形式（指標行×月列）の月並び: 4月〜3月 */
const BUDGET_MATRIX_FISCAL_PLAIN_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

function findBudgetMatrixMemberColIndex(normCells, metricIdx) {
  const want = ["担当者", "担当", "氏名", "名前"];
  for (let i = 0; i < metricIdx; i++) {
    const c = normCells[i];
    if (!c) continue;
    for (const w of want) {
      if (c === w || c.includes(w)) return i;
    }
  }
  return metricIdx >= 1 ? metricIdx - 1 : -1;
}

function findBudgetMatrixDeptColIndex(normCells, metricIdx, memberIdx) {
  for (let i = 0; i < metricIdx; i++) {
    if (i === memberIdx) continue;
    const c = normCells[i];
    if (c === "部署" || c === "部門" || (c && c.includes("部署"))) return i;
  }
  return -1;
}

function parseBudgetMatrixYearMonthHeader(h) {
  const t = normalizeCsvLookupWhitespace(String(h || "")).normalize("NFKC");
  let m = t.match(/(\d{4})\s*年\s*(\d{1,2})\s*月/);
  if (!m) m = t.match(/(\d{4})\s*[\/\-.．]\s*(\d{1,2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || mo < 1 || mo > 12) return null;
  return { y, m: mo };
}

function parseBudgetMatrixPlainMonthHeader(h) {
  const t = normalizeCsvLookupWhitespace(String(h || "")).normalize("NFKC").replace(/\s/g, "");
  const m = t.match(/^(\d{1,2})月$/);
  if (!m) return null;
  const mo = Number(m[1]);
  return mo >= 1 && mo <= 12 ? mo : null;
}

/** @returns {{ col: number, fiscalIndex: number }[] | null} */
function budgetMatrixMonthColumnsFromHeaders(headerCells, fyStart, metricIdx) {
  const norm = headerCells.map((cell) =>
    normalizeCsvLookupWhitespace(String(cell || "")).normalize("NFKC")
  );
  const monthSlots = [];
  for (let ci = metricIdx + 1; ci < norm.length && monthSlots.length < 12; ci++) {
    const h = norm[ci];
    if (!h) continue;
    const compact = h.replace(/\s/g, "");
    if (/^(年度)?\d*合計$|月平均|\d+期合計|^合計$/i.test(compact)) break;
    const ym = parseBudgetMatrixYearMonthHeader(h);
    if (ym) {
      const fi = fiscalCalendarMonthToBudgetIndex(fyStart, ym.y, ym.m);
      if (fi == null) continue;
      monthSlots.push({ col: ci, fiscalIndex: fi });
    } else {
      const pm = parseBudgetMatrixPlainMonthHeader(h);
      if (pm != null) monthSlots.push({ col: ci, fiscalIndex: null, plainMonth: pm });
    }
  }
  if (monthSlots.length !== 12) {
    const fallback = [];
    for (let ci = metricIdx + 1; ci < norm.length && fallback.length < 12; ci++) {
      const h = norm[ci];
      if (!h) continue;
      if (/合計|平均|期合計/i.test(normalizeCsvLookupWhitespace(h))) break;
      fallback.push(ci);
    }
    if (fallback.length !== 12) return null;
    return fallback.map((col, fiscalIndex) => ({ col, fiscalIndex }));
  }
  const allKeyed = monthSlots.every((s) => typeof s.fiscalIndex === "number");
  if (allKeyed) {
    monthSlots.sort((a, b) => a.fiscalIndex - b.fiscalIndex);
    const seen = new Set();
    for (const s of monthSlots) {
      if (seen.has(s.fiscalIndex) || s.fiscalIndex < 0 || s.fiscalIndex > 11) return null;
      seen.add(s.fiscalIndex);
    }
    if (seen.size !== 12) return null;
    return monthSlots.map(({ col, fiscalIndex }) => ({ col, fiscalIndex }));
  }
  const allPlain = monthSlots.every((s) => s.plainMonth != null && s.fiscalIndex === null);
  if (allPlain) {
    for (let j = 0; j < 12; j++) {
      if (monthSlots[j].plainMonth !== BUDGET_MATRIX_FISCAL_PLAIN_ORDER[j]) return null;
    }
    return monthSlots.map((s, j) => ({ col: s.col, fiscalIndex: j }));
  }
  return null;
}

/** 1行目が「部署・担当・指標・月度…」形式なら列マップ。さもなければ null */
function tryBudgetMatrixCsvHeaderMap(headerCells, fyStart) {
  const norm = headerCells.map((h) =>
    normalizeCsvLookupWhitespace(String(h ?? "")).normalize("NFKC")
  );
  let metricIdx = -1;
  for (let i = 0; i < norm.length; i++) {
    const c = norm[i];
    if (c === "指標" || (c && c.includes("指標"))) {
      metricIdx = i;
      break;
    }
  }
  if (metricIdx <= 0) return null;
  const memberIdx = findBudgetMatrixMemberColIndex(norm, metricIdx);
  if (memberIdx < 0 || memberIdx >= metricIdx) return null;
  const deptIdx = findBudgetMatrixDeptColIndex(norm, metricIdx, memberIdx);
  const fy = Number(fyStart);
  if (!Number.isFinite(fy)) return null;
  const monthCols = budgetMatrixMonthColumnsFromHeaders(headerCells, fy, metricIdx);
  if (!monthCols) return null;
  return {
    deptIdx: deptIdx >= 0 ? deptIdx : -1,
    memberIdx,
    metricIdx,
    monthCols
  };
}

function classifyBudgetMatrixMetricKind(cell) {
  const t = normalizeCsvLookupWhitespace(String(cell ?? "")).normalize("NFKC").replace(/\s+/g, "");
  if (!t) return null;
  if (t === "合計" || t.includes("総合計")) return "__skip__";
  if (t.includes("粗利率")) return "__skip__";
  if (t.includes("進捗率")) return "__skip__";
  if ((t.includes("予算") && t.includes("差益")) || t === "予算(差益)" || t === "予算（差益）") return "budget";
  if (t === "予算") return "budget";
  if (t.includes("売上")) return "sales";
  if (t.includes("差益")) return "profit";
  return null;
}

function createEmptyBudgetMatrixAccumulator(dept, member) {
  const o = { dept: String(dept || ""), member: String(member || "") };
  BUDGET_MONTH_KEYS.forEach((k) => {
    o[k] = "";
    o[`sales_${k}`] = "";
    o[`profit_${k}`] = "";
  });
  return o;
}

function mergeBudgetMetricIntoAccumulator(acc, metricKind, monthCols, cells, periodTotalCol = -1, monthlyAvgCol = -1) {
  for (let j = 0; j < 12; j++) {
    const col = monthCols[j].col;
    const mk = BUDGET_MONTH_KEYS[j];
    const raw = col < cells.length ? cells[col] : "";
    const trimmed = normalizeCsvLookupWhitespace(raw);
    let targetField = mk;
    if (metricKind === "sales") targetField = `sales_${mk}`;
    else if (metricKind === "profit") targetField = `profit_${mk}`;
    if (metricKind === "budget") {
      acc[targetField] = String(raw ?? "").trim();
      continue;
    }
    if (!trimmed) continue;
    acc[targetField] = String(raw ?? "").trim();
  }
  const fields =
    metricKind === "sales"
      ? BUDGET_MONTH_KEYS.map((k) => `sales_${k}`)
      : metricKind === "profit"
        ? BUDGET_MONTH_KEYS.map((k) => `profit_${k}`)
        : BUDGET_MONTH_KEYS;
  const monthSum = fields.reduce((s, f) => s + parseBudgetNumberInput(acc[f]), 0);
  if (monthSum !== 0) return;
  if (monthlyAvgCol >= 0 && monthlyAvgCol < cells.length) {
    const avg = parseBudgetNumberInput(cells[monthlyAvgCol]);
    if (avg !== 0) {
      fields.forEach((f) => {
        acc[f] = String(avg);
      });
      return;
    }
  }
  if (periodTotalCol < 0 || periodTotalCol >= cells.length) return;
  const total = parseBudgetNumberInput(cells[periodTotalCol]);
  if (total === 0) return;
  const each = Math.round(total / 12);
  if (each === 0) return;
  fields.forEach((f) => {
    acc[f] = String(each);
  });
}

/** 社内予算表CSV: ヘッダ行（2025年4月…）を先頭から探索 */
function findBudgetMatrixHeaderRowIndex(lines) {
  let bestIdx = -1;
  let bestCount = 0;
  const limit = Math.min(lines.length, 30);
  for (let li = 0; li < limit; li++) {
    const cells = parseCsvRow(lines[li]);
    let count = 0;
    for (const cell of cells) {
      if (parseBudgetMatrixYearMonthHeader(cell)) count++;
    }
    if (count > bestCount) {
      bestCount = count;
      bestIdx = li;
    }
  }
  return bestCount >= 6 ? bestIdx : -1;
}

/** 「10期合計」「11期合計」など年度合計列（月次が空のときのフォールバック用） */
function findBudgetMatrixPeriodTotalColIndex(headerCells, metricIdx) {
  for (let ci = metricIdx + 1; ci < headerCells.length; ci++) {
    const h = normalizeCsvLookupWhitespace(String(headerCells[ci] || "")).normalize("NFKC");
    if (/^\d+期合計$/.test(h.replace(/\s/g, ""))) return ci;
  }
  return -1;
}

/** 「月平均」列（月次が空のとき、各月へその値を入れる） */
function findBudgetMatrixMonthlyAvgColIndex(headerCells, metricIdx) {
  for (let ci = metricIdx + 1; ci < headerCells.length; ci++) {
    const h = normalizeCsvLookupWhitespace(String(headerCells[ci] || "")).normalize("NFKC").replace(/\s/g, "");
    if (h === "月平均" || h.includes("月平均")) return ci;
  }
  return -1;
}

function isBudgetSheetSkippedMemberName(name) {
  const t = normalizeCsvLookupWhitespace(String(name || "")).normalize("NFKC");
  if (!t || t === "—" || t === "-" || /^#DIV\/0!$/i.test(t)) return true;
  if (/合計/.test(t) && (t.includes("予算") || t.includes("差益") || t === "合計")) return true;
  if (t === "合計" || t.endsWith("合計")) return true;
  return false;
}

function isBudgetSheetSkippedDeptName(dept) {
  const t = normalizeCsvLookupWhitespace(String(dept || "")).normalize("NFKC");
  if (!t) return false;
  if (t.startsWith("予算") && t.includes("差益")) return true;
  if (t === "差益" && t.includes("合計")) return true;
  return false;
}

function normalizeBudgetSheetMemberName(name) {
  return normalizeCsvLookupWhitespace(String(name || ""))
    .replace(/^[（(](.+)[）)]$/, "$1")
    .trim();
}

/**
 * 指標列ヘッダなしの社内予算表（1列目=部署・2列目=担当・3列目=指標・4列目〜=月）
 * @returns {object | null}
 */
function tryBudgetSheetLegacyColumnMap(headerCells, fyStart, lines, headerRowIndex) {
  const metricIdx = 3;
  const memberIdx = 2;
  const deptIdx = 1;
  const fy = Number(fyStart);
  if (!Number.isFinite(fy)) return null;
  const monthCols = budgetMatrixMonthColumnsFromHeaders(headerCells, fy, metricIdx);
  if (!monthCols) return null;
  let validMetricRows = 0;
  const bodyEnd = Math.min(lines.length, headerRowIndex + 50);
  for (let li = headerRowIndex + 1; li < bodyEnd; li++) {
    const cells = parseCsvRow(lines[li]);
    const kind = classifyBudgetMatrixMetricKind(cells[metricIdx]);
    if (kind === "budget" || kind === "sales" || kind === "profit") validMetricRows++;
  }
  if (validMetricRows < 2) return null;
  return { deptIdx, memberIdx, metricIdx, monthCols, sheetLegacy: true };
}

function resolveBudgetMatrixCsvMapStruct(lines, fyStart) {
  const fy = Number(fyStart);
  if (!Number.isFinite(fy)) return null;
  let headerRowIndex = 0;
  let mapStruct = tryBudgetMatrixCsvHeaderMap(parseCsvRow(lines[0]), fy);
  if (!mapStruct) {
    headerRowIndex = findBudgetMatrixHeaderRowIndex(lines);
    if (headerRowIndex < 0) return null;
    const headCells = parseCsvRow(lines[headerRowIndex]);
    mapStruct = tryBudgetMatrixCsvHeaderMap(headCells, fy);
    if (!mapStruct) mapStruct = tryBudgetSheetLegacyColumnMap(headCells, fy, lines, headerRowIndex);
  }
  if (!mapStruct) return null;
  const headCells = parseCsvRow(lines[headerRowIndex]);
  const periodTotalCol = findBudgetMatrixPeriodTotalColIndex(headCells, mapStruct.metricIdx);
  const monthlyAvgCol = findBudgetMatrixMonthlyAvgColIndex(headCells, mapStruct.metricIdx);
  return { ...mapStruct, headerRowIndex, periodTotalCol, monthlyAvgCol };
}

function budgetMatrixAccumulatorsToNormalizedRows(byKey) {
  return Array.from(byKey.values()).map((acc) => normalizeBudgetRow(acc));
}

/**
 * 予算進捗用スプレッドシートCSV（担当×指標×月）を解析。形式不一致時は null。
 * @returns {{ rows: object[] } | { empty: boolean, message: string } | null}
 */
function parseBudgetMatrixCsv(lines, fyStart) {
  if (!Array.isArray(lines) || lines.length < 2) return null;
  const resolved = resolveBudgetMatrixCsvMapStruct(lines, fyStart);
  if (!resolved) return null;
  const { headerRowIndex, periodTotalCol, monthlyAvgCol, ...mapStruct } = resolved;
  const accMap = new Map();
  let lastDept = "";
  let lastMember = "";
  for (let li = headerRowIndex + 1; li < lines.length; li++) {
    const cells = parseCsvRow(lines[li]);
    if (!cells.some((c) => String(c || "").trim())) continue;
    const metricKind = classifyBudgetMatrixMetricKind(cells[mapStruct.metricIdx]);
    if (metricKind === "__skip__" || metricKind === null) continue;
    let dept = "";
    if (mapStruct.deptIdx >= 0) {
      dept = normalizeCsvLookupWhitespace(String(cells[mapStruct.deptIdx] ?? ""));
      if (isBudgetSheetSkippedDeptName(dept)) continue;
      if (!dept) dept = lastDept;
      else lastDept = dept;
    }
    let member = normalizeBudgetSheetMemberName(cells[mapStruct.memberIdx] ?? "");
    if (!member) member = lastMember;
    else lastMember = member;
    if (isBudgetSheetSkippedMemberName(member)) continue;
    const key = `${dept}\n${member}`;
    if (!accMap.has(key)) accMap.set(key, createEmptyBudgetMatrixAccumulator(dept, member));
    mergeBudgetMetricIntoAccumulator(
      accMap.get(key),
      metricKind,
      mapStruct.monthCols,
      cells,
      periodTotalCol,
      monthlyAvgCol
    );
  }
  const rows = budgetMatrixAccumulatorsToNormalizedRows(accMap);
  if (!rows.length) {
    return {
      empty: true,
      message:
        "スプレッドシート形式の列として認識しましたが、取り込める行がありません。「予算(差益)」「売上」「差益」の指標行があるか確認してください（粗利率・進捗率・合計行は読み込みません）。"
    };
  }
  return { rows, sheetLegacy: !!mapStruct.sheetLegacy };
}

function budgetMatrixMonthHeaderLabelsForFy(fyStart) {
  const fy = Number(fyStart);
  if (!Number.isFinite(fy)) return [];
  return BUDGET_MATRIX_FISCAL_PLAIN_ORDER.map((mo) => {
    const y = mo >= 4 ? fy : fy + 1;
    return `${y}年${mo}月`;
  });
}

function budgetDeptDatalistHtml() {
  const names = (uiState.deptSettings?.departments || [])
    .map((d) => String(d?.name || "").trim())
    .filter(Boolean);
  const opts = names.map((n) => `<option value="${escapeHtmlAttr(n)}"></option>`).join("");
  return `<datalist id="budget-dept-datalist">${opts}</datalist>`;
}

function normalizeBudgetRow(row) {
  const base = {
    dept: String(row?.dept || ""),
    member: String(row?.member || "")
  };
  BUDGET_MONTH_KEYS.forEach((k) => {
    base[k] = String(row?.[k] ?? "");
    base[`sales_${k}`] = String(row?.[`sales_${k}`] ?? "");
    base[`profit_${k}`] = String(row?.[`profit_${k}`] ?? "");
  });
  // 旧データ（上期/下期）互換: 値を先頭月へ退避して編集可能にする
  if (!BUDGET_MONTH_KEYS.some((k) => String(row?.[k] || "").trim())) {
    if (row?.h1) base.m04 = String(row.h1);
    if (row?.h2) base.m10 = String(row.h2);
  }
  return base;
}

/** 部署設定の並び × 各部署の memberIds の順を、予算マスタの表示順に使う */
function buildBudgetDeptMemberPositions() {
  const map = new Map();
  (uiState.deptSettings?.departments || []).forEach((d, deptIdx) => {
    (Array.isArray(d.memberIds) ? d.memberIds : []).forEach((mid, memberIdx) => {
      map.set(String(mid), { deptIdx, memberIdx });
    });
  });
  return map;
}

function budgetRowSortKeyForUser(u, posMap) {
  const name = userNameWithRetiredLabel(u);
  if (posMap.has(String(u.id))) {
    const { deptIdx, memberIdx } = posMap.get(String(u.id));
    return { deptIdx, slot: memberIdx, tie: name };
  }
  return { deptIdx: 999997, slot: 0, tie: name };
}

/** CSV 等でも行の deployment 文言でグルーピング。部署設定の一覧順→未登録名は五十音順→部署空は最後。 */
function budgetDeptGroupKey(deptRaw) {
  const dname = String(deptRaw || "").trim();
  const departments = uiState.deptSettings?.departments || [];
  if (!dname) return { tier: 2 };
  const idx = departments.findIndex((d) => String(d.name || "").trim() === dname);
  if (idx >= 0) return { tier: 0, idx };
  return { tier: 1, label: dname };
}

function compareBudgetDeptGroupKey(a, b) {
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (a.tier === 0 && b.tier === 0) return a.idx - b.idx;
  if (a.tier === 1 && b.tier === 1) return (a.label || "").localeCompare(b.label || "", "ja");
  return 0;
}

/** 同一部署ブロック内: 設定のメンバー順→そのほかユーザー→名前のみそう */
function budgetMemberSortKeyWithinDept(row, posMap) {
  const name = String(row?.member || "").trim();
  const tie = name || "\uffff";
  const u = users.find((x) => isActiveUser(x) && userNameWithRetiredLabel(x) === name);
  if (u && posMap.has(String(u.id))) {
    const { memberIdx } = posMap.get(String(u.id));
    return { slot: memberIdx, tie };
  }
  if (u) return { slot: 999998, tie };
  return { slot: 999999, tie };
}

/** 予算マスタ表・進捗集計共通の並び（部署順を最優先）。 */
function compareBudgetMasterRowOrder(rowA, rowB, posMap) {
  const dk = compareBudgetDeptGroupKey(budgetDeptGroupKey(rowA?.dept), budgetDeptGroupKey(rowB?.dept));
  if (dk !== 0) return dk;
  const ma = budgetMemberSortKeyWithinDept(rowA, posMap);
  const mb = budgetMemberSortKeyWithinDept(rowB, posMap);
  if (ma.slot !== mb.slot) return ma.slot - mb.slot;
  return ma.tie.localeCompare(mb.tie, "ja");
}

function compareBudgetMasterSortKeys(ka, kb) {
  if (ka.deptIdx !== kb.deptIdx) return ka.deptIdx - kb.deptIdx;
  if (ka.slot !== kb.slot) return ka.slot - kb.slot;
  return ka.tie.localeCompare(kb.tie, "ja");
}

function seedBudgetRows() {
  const deptNames = (uiState.deptSettings?.departments || [])
    .map((d) => String(d?.name || "").trim())
    .filter(Boolean);
  const defaultDept = deptNames[0] || "";
  const deptByMemberId = new Map();
  (uiState.deptSettings?.departments || []).forEach((d) => {
    const name = String(d?.name || "").trim();
    if (!name) return;
    (Array.isArray(d?.memberIds) ? d.memberIds : []).forEach((mid) => {
      deptByMemberId.set(String(mid), name);
    });
  });
  const posMapForSeed = buildBudgetDeptMemberPositions();
  const seeded = users
    .filter((u) => isActiveUser(u) && !u.onLeave && !u.retired && u.role === "営業")
    .sort((a, b) =>
      compareBudgetMasterSortKeys(
        budgetRowSortKeyForUser(a, posMapForSeed),
        budgetRowSortKeyForUser(b, posMapForSeed)
      ))
    .map((u) => normalizeBudgetRow({
      dept: deptByMemberId.get(String(u.id)) || defaultDept,
      member: userNameWithRetiredLabel(u),
      ...Object.fromEntries(BUDGET_MONTH_KEYS.map((k) => [k, ""]))
    }));
  return seeded.length ? seeded : [normalizeBudgetRow({ dept: defaultDept, member: "", ...Object.fromEntries(BUDGET_MONTH_KEYS.map((k) => [k, ""])) })];
}

/**
 * 予算マスタに「営業ロールの有効ユーザー」を自動反映する。
 * - 既存行は維持（手入力値を壊さない）
 * - 未登録の営業担当者だけ追記
 */
function syncSalesUsersToBudgetRows(rows) {
  if (!Array.isArray(rows)) return [];
  const deptByMemberId = new Map();
  (uiState.deptSettings?.departments || []).forEach((d) => {
    const name = String(d?.name || "").trim();
    if (!name) return;
    (Array.isArray(d?.memberIds) ? d.memberIds : []).forEach((mid) => deptByMemberId.set(String(mid), name));
  });
  const defaultDept = String((uiState.deptSettings?.departments || [])[0]?.name || "").trim();
  const existingMembers = new Set(rows.map((r) => String(r?.member || "").trim()).filter(Boolean));
  const salesUsers = users.filter(
    (u) => isActiveUser(u) && !u.onLeave && !u.retired && u.role === "営業"
  );
  const additions = salesUsers
    .filter((u) => !existingMembers.has(userNameWithRetiredLabel(u)))
    .map((u) =>
      normalizeBudgetRow({
        dept: deptByMemberId.get(String(u.id)) || defaultDept,
        member: userNameWithRetiredLabel(u),
        ...Object.fromEntries(BUDGET_MONTH_KEYS.map((k) => [k, ""]))
      })
    );
  if (!additions.length) return rows;
  return [...rows, ...additions];
}

function ensureBudgetRowsForSelectedPeriod(periodOverride) {
  ensureBudgetPeriodsState();
  const period =
    periodOverride &&
    typeof periodOverride === "object" &&
    String(periodOverride.id ?? "").trim() !== ""
      ? uiState.budgetPeriods.find((p) => String(p.id) === String(periodOverride.id)) || periodOverride
      : uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) ||
        latestBudgetPeriodFromSorted(uiState.budgetPeriods);
  if (!period) return [];
  if (budgetRowsLoadFailedForPeriod(period.id)) return [];
  if (!budgetRowsLoadedForPeriod(period.id)) {
    void loadBudgetRowsForPeriod(period.id);
    return [];
  }
  if (!uiState.budgetRowsByPeriod || typeof uiState.budgetRowsByPeriod !== "object") {
    uiState.budgetRowsByPeriod = readBudgetDraftStore();
  }
  const pkg = uiState.budgetRowsByPeriod[period.id];
  const normalized = Array.isArray(pkg?.rows) ? pkg.rows.map(normalizeBudgetRow) : [];
  if (normalized.length) {
    // CSV取込や手編集後の内容をそのまま保持する（再描画時に営業ユーザーを自動混入させない）
    return normalized;
  }
  const seeded = seedBudgetRows();
  uiState.budgetRowsByPeriod[period.id] = { rows: seeded, extendSales: true };
  // 初期行の自動保存は予算を編集できる人だけ（閲覧のみの人は 403 で「保存に失敗」と出ていた）
  saveBudgetDraftStore(uiState.budgetRowsByPeriod, { persistToServer: userCanAccessPage("budget-admin") });
  return seeded;
}

/** 予算進捗グラフ／表用に、選択中会計期の予算マスタ行をグループ化し、案件の売上・差益（売上−原価）を請求月で合算 */
function buildBudgetProgressGroupsForSelectedPeriod() {
  ensureBudgetPeriodsState();
  const period =
    uiState.budgetPeriods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(uiState.budgetPeriods);
  if (!period || !period.id) return [];
  const rows = ensureBudgetRowsForSelectedPeriod(period);
  const posMap = buildBudgetDeptMemberPositions();
  const withIdx = rows.map((row, rowIdx) => ({ row, rowIdx }));
  withIdx.sort((a, b) => compareBudgetMasterRowOrder(a.row, b.row, posMap));
  const deptOrder = [];
  const byDept = new Map();
  for (const { row } of withIdx) {
    const dept = String(row.dept || "").trim() || "（部署未設定）";
    if (!byDept.has(dept)) {
      byDept.set(dept, []);
      deptOrder.push(dept);
    }
    const budget = BUDGET_MONTH_KEYS.map((k) => parseBudgetNumberInput(row[k]));
    const totalBudget = budget.reduce((sum, n) => sum + n, 0);
    const salesImported = BUDGET_MONTH_KEYS.map((k) =>
      String(row[`sales_${k}`] ?? "").trim() === "" ? null : parseBudgetNumberInput(row[`sales_${k}`])
    );
    const profitImported = BUDGET_MONTH_KEYS.map((k) =>
      String(row[`profit_${k}`] ?? "").trim() === "" ? null : parseBudgetNumberInput(row[`profit_${k}`])
    );
    const sales = salesImported.map((v) => (v != null ? v : 0));
    const profit = profitImported.map((v) => (v != null ? v : 0));
    const salesFromProjects = salesImported.map((v) => v == null);
    const profitFromProjects = profitImported.map((v) => v == null);
    byDept.get(dept).push({
      name: String(row.member || "").trim() || "—",
      budget,
      sales,
      profit,
      totalBudget,
      salesFromProjects,
      profitFromProjects
    });
  }
  const groups = deptOrder.map((dept) => ({ dept, members: byDept.get(dept) || [] }));
  const fyStart = fiscalYearStartFromBudgetPeriod(period);
  for (const p of projects) {
    const yyyymm = parseProjectBillingMonthYyyymm(p.billingMonth);
    if (yyyymm == null || fiscalYearStartFromBillingYyyymm(yyyymm) !== fyStart) continue;
    const idx = fiscalMonthIndexFromBillingYyyymm(yyyymm);
    // 案件一覧の「請求額（税抜）合計」と同一ロジック（請求明細の税抜合計→なければ受注済のみ amount）。
    // p.amount 直参照だと明細ありの案件で税込相当が混ざり、一覧と数字が合わない
    const salesAmt = projectDisplayedBillingExTaxYen(p);
    // 差益は税抜売上−税抜原価（予算表Excelと同基準）。原価管理の集計（税込）とは基準が異なる
    const profitAmt = salesAmt - totalProjectRegisteredCostExTaxYen(p);
    // 売上分配が設定されていれば複数営業へ比率按分（差益も同比率）。未設定は担当営業100%
    const shares = budgetProgressSalesSharesForProject(p, salesAmt, profitAmt);
    for (const sh of shares) {
      for (const g of groups) {
        for (const m of g.members) {
          if (!budgetMasterMemberNameMatchesProject(sh.memberKey, m.name)) continue;
          if (m.salesFromProjects[idx]) m.sales[idx] += sh.sales;
          if (m.profitFromProjects[idx]) m.profit[idx] += sh.profit;
        }
      }
    }
  }
  return groups;
}

async function persistBudgetPeriod(period) {
  if (!authToken || !period) return false;
  try {
    const payload = {
      id: period.id,
      label: period.label,
      periodNumber: period.periodNumber,
      rangeLabel: period.rangeLabel,
      rangeStart: period.rangeStart,
      rangeEnd: period.rangeEnd,
      entryMode: period.entryMode
    };
    const res = await fetch("/api/budget-periods", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(payload)
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function deleteBudgetPeriod(periodId) {
  if (!authToken || !periodId) return false;
  try {
    const res = await fetch("/api/budget-periods", {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ id: periodId })
    });
    return res.ok;
  } catch {
    return false;
  }
}

function parseBudgetNumberInput(value) {
  // △1,000・▲1,000・(1,000) を負数として読む（以前は数字以外を捨てて +1,000 になっていた）
  const acc = parseAccountingAmount(value);
  if (Number.isFinite(acc)) return acc;
  // 読めない表記は従来どおり数字以外を捨てて読む（既存の取込結果を変えない）
  let s = String(value ?? "")
    .trim()
    .replace(/\s/g, "");
  try {
    s = s.normalize("NFKC");
  } catch {
    /* ignore */
  }
  const digits = s.replace(/[^\d.-]/g, "");
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}

/** 案件作成モーダルの「クライアント名で検索」候補（会社名・略称名の両方が対象。プレースホルダの
 *  「会社名・略称名で検索」と一致させる）。全体render（11-cost-form.js renderProjectCreateModal）と
 *  部分更新（refreshProjectClientCandidates）の2箇所が同じ候補を出す必要があるため単一定義元にする
 *  （片方だけ直すと検索欄への入力中だけ挙動が戻る事故になる）。 */
function projectCreateClientCandidates(query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  // 取引承認が「承認済」のクライアントだけ案件に紐づけられる（未申請=仮登録・申請中・却下・
  // アーカイブ済みは候補に出さない。承認されるまで案件を作れないのが新フローの仕様）
  return clients
    .filter((c) => !c.salesHidden && c.archived !== true && isClientApproved(c) &&
      [c.name, c.shortName].join(" ").toLowerCase().includes(q))
    .slice(0, 8);
}

function projectClientCandidatesHtml(projectCreateState) {
  const form = projectCreateState?.form || {};
  const clientSearchTrim = String(form.clientQuery || "").trim();
  const clientCandidates = projectCreateClientCandidates(clientSearchTrim);
  if (!clientSearchTrim) {
    return `<p class="project-create-candidate-hint" role="status">文字を入力すると候補が表示されます</p>`;
  }
  if (clientCandidates.length === 0) {
    return `<p class="project-create-candidate-hint" role="status">該当するクライアントがありません</p>`;
  }
  return clientCandidates
    .map(
      (c, idx) =>
        `<button type="button" class="picker-option ${form.clientId === c.id ? "active" : ""} ${form.clientHighlight === idx ? "focus" : ""}" data-select-client="${c.id}" role="option" aria-selected="${form.clientId === c.id ? "true" : "false"}">
            <strong>${escapeHtml(c.name)}</strong>
            <small>${escapeHtml(c.contactName || "担当未設定")}${c.ndaStatus === "済" ? " ・ NDA済" : ""}${c.contractStatus === "済" ? " ・ 契約済" : ""}</small>
          </button>`
    )
    .join("");
}

function projectCreateSelectedCardHtml(selectedClient) {
  if (!selectedClient) return "";
  return `<div class="selected-client-card project-create-selected">
    <div class="project-create-selected-title">選択中</div>
    <div class="project-create-selected-name">${escapeHtml(selectedClient.name)}</div>
    <div class="meta"><span>${escapeHtml(selectedClient.contactName || "—")}</span><span>${escapeHtml(selectedClient.tel || "")}</span><span>${escapeHtml(selectedClient.email || "")}</span></div>
  </div>`;
}

function refreshProjectClientCandidates() {
  if (!uiState.projectCreate) return;
  const listEl = document.querySelector(".project-create-candidate-list");
  if (!(listEl instanceof HTMLElement)) return;
  listEl.innerHTML = projectClientCandidatesHtml(uiState.projectCreate);
}

function budgetMasterRowsHtml(period) {
  const rows = ensureBudgetRowsForSelectedPeriod(period);
  const withIdx = rows.map((row, rowIdx) => ({ row, rowIdx }));
  const posMap = buildBudgetDeptMemberPositions();
  withIdx.sort((a, b) => compareBudgetMasterRowOrder(a.row, b.row, posMap));
  const memberList = users
    .filter((u) => isActiveUser(u))
    .map((u) => ({ name: userNameWithRetiredLabel(u), role: u.role }));
  return withIdx.map(({ row, rowIdx }) => {
    const monthNums = BUDGET_MONTH_KEYS.map((k) => parseBudgetNumberInput(row[k]));
    const total = monthNums.reduce((sum, n) => sum + n, 0);
    const monthCells = BUDGET_MONTH_KEYS
      .map((k, i) => `<td><input class="pj-input budget-month-input" inputmode="numeric" data-budget-row-field="${k}" data-budget-row-idx="${rowIdx}" value="${escapeHtmlAttr(row[k])}" placeholder="${BUDGET_MONTH_LABELS[i]}" /></td>`)
      .join("");
    const memberValue = String(row.member || "").trim();
    const memberSelectOptions = memberList
      .map((m) => `<option value="${escapeHtmlAttr(m.name)}" ${m.name === memberValue ? "selected" : ""}>${escapeHtml(m.name)}（${escapeHtml(m.role)}）</option>`)
      .join("");
    const deptVal = escapeHtmlAttr(row.dept || "");
    return `
      <tr>
        <td><input class="pj-input budget-dept-input" type="text" list="budget-dept-datalist" maxlength="80" data-budget-row-field="dept" data-budget-row-idx="${rowIdx}" value="${deptVal}" placeholder="部署" autocomplete="off" /></td>
        <td><select class="pj-input budget-member-select" data-budget-row-field="member" data-budget-row-idx="${rowIdx}">${memberValue && !memberList.some((m) => m.name === memberValue) ? `<option value="${escapeHtmlAttr(memberValue)}" selected>${escapeHtml(memberValue)}</option>` : ""}${memberSelectOptions}</select></td>
        ${monthCells}
        <td class="acct-num acct-name" data-budget-row-total="1">¥${total.toLocaleString("ja-JP")}</td>
        <td><button class="cl-btn cl-btn-outline cl-btn-sm cl-btn-danger" type="button" data-budget-row-delete="${rowIdx}">削除</button></td>
      </tr>
    `;
  }).join("");
}

function budgetAdminInnerHtml() {
  ensureBudgetPeriodsState();
  const periods = uiState.budgetPeriods;
  const selected = periods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(periods);
  const selectedMasterTitle = !selected ? "予算マスタ" : (() => {
    const n = selected.periodNumber || parseBudgetFiscalPeriodNumberFromLabel(selected.label);
    return `${n ? `${n}期` : (selected.label || "会計期")} 予算マスタ`;
  })();
  const periodChips = periods
    .map((p) => {
      const n = p.periodNumber || parseBudgetFiscalPeriodNumberFromLabel(p.label);
      const chipTitle = n ? `${n}期` : p.label;
      return `
      <div class="budget-period-chip ${selected && p.id === selected.id ? "active" : ""}">
        <button type="button" class="chip-btn budget-period-chip__label" data-budget-period-select="${escapeHtmlAttr(p.id)}">${escapeHtml(chipTitle)}<span class="budget-period-chip__range" style="opacity:.65;font-size:11px;margin-left:6px;">${escapeHtml(p.rangeLabel || "")}</span></button>
        <button type="button" class="budget-period-delete-btn" data-budget-period-delete="${escapeHtmlAttr(p.id)}" aria-label="${escapeHtmlAttr(chipTitle)}を削除">×</button>
      </div>`;
    })
    .join("");
  const budgetRowsLoading = selected ? ensureSelectedBudgetRowsRequested() : false;
  const budgetRowsFailed = selected ? budgetRowsLoadFailedForPeriod(selected.id) : false;
  const budgetRows = selected && !budgetRowsLoading ? budgetMasterRowsHtml(selected) : "";
  const budgetRowsPanel = budgetRowsLoading
    ? `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "予算データを読み込んでいます" })}</div>`
    : budgetRowsFailed
      ? `<div class="cl-empty" style="padding:18px 20px;">予算データを取得できませんでした。ページを再読み込みしてください。</div>`
      : null;
  return `
        <div class="pj-filter-area budget-admin-period-block">
          <div class="budget-pj-block-head">
            <h3 class="pj-page-subheading budget-pj-block-title">会計期</h3>
            <p class="budget-pj-block-lede">表示・編集する期を選び、必要に応じて新しい期を追加します</p>
          </div>
        <div class="budget-period-toolbar" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:14px;">
          ${periodChips}
        </div>
        <div class="budget-period-add-row budget-period-add-form" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;margin-top:14px;padding-top:14px;border-top:1px solid var(--border);">
          <span class="budget-period-inline-label" style="color:var(--muted);font-size:13px;">期</span>
          <input class="pj-input budget-period-num-input" type="text" inputmode="numeric" autocomplete="off" data-budget-period-new-number maxlength="4" style="width:3.25rem;text-align:center;" />
          <span style="font-size:13px;">期</span>
          <span class="budget-period-add-gap" aria-hidden="true" style="width:8px;flex-shrink:0;"></span>
          <span class="budget-period-inline-label" style="color:var(--muted);font-size:13px;">期間</span>
          <input class="pj-input budget-period-ym-input" type="text" inputmode="numeric" autocomplete="off" data-budget-period-new-from placeholder="YYYY/MM" maxlength="7" style="width:6.5rem;" />
          <span class="budget-period-tilde" style="font-size:13px;padding:0 2px;">～</span>
          <input class="pj-input budget-period-ym-input" type="text" inputmode="numeric" autocomplete="off" data-budget-period-new-to placeholder="YYYY/MM" maxlength="7" style="width:6.5rem;" />
          <button type="button" class="chip-btn" data-budget-period-add="1">＋ 期を追加</button>
        </div>
        </div>
        <div class="pj-table-area budget-admin-master-block">
        <div class="budget-pj-master-head-inner">
          <h3 class="pj-page-subheading budget-pj-block-title">${selectedMasterTitle}</h3>
        </div>
        ${selected ? `
          ${budgetDeptDatalistHtml()}
          ${budgetRowsPanel || `
          <div class="budget-master-scroll">
            <table class="budget-table budget-master-table">
              <thead><tr><th>部署</th><th>担当者</th>${BUDGET_MONTH_LABELS.map((m) => `<th>${m}</th>`).join("")}<th>年度合計</th><th></th></tr></thead>
              <tbody>${budgetRows || `<tr><td colspan="16" class="cl-empty">行がありません。「＋ 行を追加」で追加できます。</td></tr>`}</tbody>
            </table>
          </div>
          <div style="margin-top:10px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;padding:0 20px 16px;">
            <button type="button" class="chip-btn" data-budget-row-add="1">＋ 行を追加</button>
            <button type="button" class="chip-btn" data-csv-download-budget-master="1">CSVテンプレート</button>
            <button type="button" class="chip-btn" data-csv-download-budget-matrix="1">スプレッドシート形式テンプレ</button>
            <label class="chip-btn" style="cursor:pointer;">CSVインポート<input type="file" accept=".csv,text/csv" data-csv-import-budget-master="1" style="display:none;" /></label>
            <span style="margin-left:auto;display:flex;align-items:center;gap:10px;">
              <span data-budget-master-dirty-note style="color:#b45309;font-size:12px;${uiState.budgetMasterDirty ? "" : "display:none;"}">未保存の変更があります</span>
              <button type="button" class="cl-btn cl-btn-primary" data-budget-master-save="1">保存</button>
            </span>
          </div>
          `}
        ` : `<div class="cl-empty" style="padding:12px 20px 24px;">会計期を追加してください</div>`}
        </div>
  `;
}

/** 予算設定の単独ページ（メニューからは外し、経営ダッシュボードの「予算設定」タブへ合体。ルートは後方互換で残置） */
function budgetAdminPage() {
  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>予算設定</h2>
            <p class="pj-page-lede">会計期の追加・切替と、選択中の期の予算マスタを管理する</p>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            ${isMasterRoleUser(currentUser) ? `<button class="chip-btn" type="button" data-go="management">経営ダッシュボードを見る</button>` : ""}
          </div>
        </div>
        ${budgetAdminInnerHtml()}
      </div>
    </div>
  `;
}

function deptKanbanSection(deptState, deptCreateModal) {
  const allAssigned = deptState.departments.flatMap((d) => d.memberIds);
  const unassigned = users.filter(
    (u) => isActiveUser(u) && !u.onLeave && !u.retired && !allAssigned.includes(u.id)
  );

  const columns = deptState.departments.map((d) => {
    const members = d.memberIds.map((id) => users.find((u) => u.id === id)).filter(Boolean);
    const cards = members.map((u) => memberCardHtml(u, d.name, d.leaderMemberId === u.id)).join("");
    return `
      <div class="kanban-col">
        <div class="kanban-col-head">
          <span class="kanban-col-title">${escapeHtml(d.name)}</span>
          <span class="kanban-col-count">${members.length}</span>
        </div>
        <div class="kanban-dropzone" data-dept-drop-zone="${d.id}">
          ${cards || `<div class="dept-empty-note">メンバーがいません<br>ドラッグで追加</div>`}
        </div>
        <div class="kanban-col-foot">
          <button class="cl-btn cl-btn-outline cl-btn-sm" type="button" data-dept-edit-toggle="${d.id}">名前変更</button>
          <button class="cl-btn cl-btn-outline cl-btn-sm cl-btn-danger" type="button" data-dept-delete="${d.id}">削除</button>
        </div>
      </div>`;
  }).join("");

  const unassignedCol = `
    <div class="kanban-col kanban-col-unassigned">
      <div class="kanban-col-head">
        <span class="kanban-col-title">未所属</span>
        <span class="kanban-col-count">${unassigned.length}</span>
      </div>
      <div class="kanban-dropzone" data-dept-drop-zone="__unassigned__">
        ${unassigned.map((u) => memberCardHtml(u, "未所属")).join("") || `<div class="dept-empty-note">全員所属済み</div>`}
      </div>
    </div>`;

  const leftArea = `
    <div class="kanban-left-area">
      <div class="kanban-left-head">
        <span class="kanban-left-title">部署一覧</span>
        <button class="chip-btn" data-dept-open-add="1" type="button">＋ 部署を追加</button>
      </div>
      <div class="kanban-left-grid">
        ${columns}
      </div>
    </div>`;

  return `
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <div class="section-head-row">
          <h3 class="mini-title">部署設定</h3>
          <p style="font-size:12px;color:var(--muted);margin:0;">カードの「⋯」から所属部署・責任者を変更できます（PCはドラッグで移動、ダブルクリックで責任者の設定・解除も可）。</p>
        </div>
        <div class="kanban-board">
          ${leftArea}
          ${unassignedCol}
        </div>
      </article>
      ${deptCreateModal}
      ${renderDeptMemberModal()}
    </section>`;
}

function ensureDeptSettingsState() {
  if (uiState.deptSettings) return;
  uiState.deptSettings = {
    departments: [],
    editingDeptId: ""
  };
}

function cloneDeptSettingsState() {
  const state = uiState.deptSettings;
  if (!state) return null;
  return {
    editingDeptId: String(state.editingDeptId || ""),
    departments: (state.departments || []).map((d) => ({
      id: String(d.id || ""),
      name: String(d.name || ""),
      createdAt: String(d.createdAt || ""),
      memberIds: Array.isArray(d.memberIds) ? d.memberIds.map((id) => String(id)) : [],
      leaderMemberId: String(d.leaderMemberId || "")
    }))
  };
}

function restoreDeptSettingsState(snapshot) {
  if (!snapshot) return;
  uiState.deptSettings = {
    editingDeptId: snapshot.editingDeptId,
    departments: snapshot.departments.map((d) => ({
      id: d.id,
      name: d.name,
      createdAt: d.createdAt,
      memberIds: [...d.memberIds],
      leaderMemberId: d.leaderMemberId
    }))
  };
}

/**
 * 部署のメンバー・責任者から、アカウント一覧（有効ユーザー）に居ない id を除く。
 * 削除済みユーザーが画面 state に残ったまま全部署 POST すると、サーバで外した人が書き戻され
 * （責任者なら承認フローが止まる）ため、送信前に必ず通す。users が空（読込失敗）なら何も外さない。
 */
function departmentsWithExistingMembers(departments, userList) {
  const list = Array.isArray(departments) ? departments : [];
  if (!Array.isArray(userList) || !userList.length) return list;
  const alive = new Set(userList.map((u) => String(u.id)));
  return list.map((d) => ({
    ...d,
    memberIds: (Array.isArray(d.memberIds) ? d.memberIds : []).filter((id) => alive.has(String(id))),
    leaderMemberId: d.leaderMemberId && alive.has(String(d.leaderMemberId)) ? d.leaderMemberId : ""
  }));
}

/** ユーザー削除成功後: 画面 state の部署から当人（メンバー・責任者）を外す（サーバ側の DELETE と揃える） */
function removeUserFromDeptSettings(userId) {
  const depts = uiState.deptSettings?.departments;
  if (!Array.isArray(depts)) return;
  const uid = String(userId);
  depts.forEach((d) => {
    if (Array.isArray(d.memberIds)) d.memberIds = d.memberIds.filter((id) => String(id) !== uid);
    if (String(d.leaderMemberId || "") === uid) d.leaderMemberId = "";
  });
}

/** @returns {Promise<{ ok: boolean, status?: number, serverCode?: string, serverDetail?: string }>} */
async function persistDepartments() {
  if (!authToken || !uiState.deptSettings?.departments) return { ok: true };
  try {
    const res = await fetch("/api/departments", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ departments: departmentsWithExistingMembers(uiState.deptSettings.departments, users) })
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let serverCode;
      let serverDetail;
      try {
        const j = JSON.parse(text);
        if (j && typeof j.code === "string") serverCode = j.code;
        if (j && typeof j.detail === "string") serverDetail = j.detail;
      } catch {
        /* ignore */
      }
      return { ok: false, status: res.status, serverCode, serverDetail };
    }
    let data;
    try {
      data = await res.json();
    } catch {
      return { ok: false, status: res.status };
    }
    if (Array.isArray(data.departments)) {
      uiState.deptSettings.departments = normalizeDepartmentsForUi(data.departments);
      const exists = uiState.deptSettings.departments.some((d) => d.id === uiState.deptSettings.editingDeptId);
      if (!exists) uiState.deptSettings.editingDeptId = String(uiState.deptSettings.departments[0]?.id || "");
      saveDepartmentsFallbackLocal(uiState.deptSettings.departments);
    }
    return { ok: true };
  } catch {
    return { ok: false, status: 0 };
  }
}

function formatDeptPersistFailureMessage(result, customFailText) {
  if (result.ok) return "";
  if (result.status === 401) return "セッションの有効期限が切れています。再ログインしてください";
  if (result.status === 0) return "通信に失敗しました。ネットワークを確認してください";
  if (result.serverCode && (result.status === 500 || result.status === 503)) {
    const tail =
      result.serverDetail && String(result.serverDetail).length > 0
        ? ` ${String(result.serverDetail).slice(0, 180)}`
        : "";
    return `部署の保存に失敗しました（${result.status}｜${result.serverCode}）。DB接続またはサーバー設定を確認してください${tail}`;
  }
  return (
    customFailText ||
    (result.status ? `部署の保存に失敗しました（${result.status}）` : "部署の保存に失敗しました")
  );
}

/** 承認ルールのみサーバーへ保存（リロード後も復元される）。
 *  他の人が先に保存していた場合はサーバーが 409 を返すので、最新を読み直して "conflict" を返す
 *  （呼び出し側で「先に保存された」旨を案内する。無警告の後勝ち上書きを防ぐ楽観ロック）。
 *  @returns {Promise<boolean | "conflict">} */
async function persistOrgApprovalSettings() {
  if (!authToken) return false;
  try {
    ensureApprovalApproversState();
    ensureApprovalApplicationFlowBindingsState();
    persistApprovalApproversStore();
    persistApprovalOtherFlowsStore();
    const res = await apiFetch("/api/org-approval-settings", {
      method: "POST",
      body: {
        approvalApprovers: uiState.approvalApprovers || {},
        approvalOtherFlows: Array.isArray(uiState.approvalOtherFlows) ? uiState.approvalOtherFlows : [],
        approvalApplicationFlowBindings: uiState.approvalApplicationFlowBindings || {},
        approvalSettingsBaseVersion: uiState.approvalSettingsVersion || null
      }
    });
    if (res.status === 409) {
      // 先に保存された内容（と新しい版トークン）を読み直し、画面に最新を反映してから案内する
      const latest = await apiFetch("/api/departments");
      if (latest.ok) applyApprovalSettingsPayload(latest.data);
      return "conflict";
    }
    if (!res.ok) return false;
    if (res.data && res.data.fallback) return false;
    applyApprovalSettingsPayload(res.data);
    return true;
  } catch {
    return false;
  }
}

function memberCardHtml(user, deptLabel, isLeader = false) {
  const inactiveNote = !isActiveUser(user) ? " · アカウント無効" : "";
  return `
    <div class="dept-member-card ${isLeader ? "is-leader" : ""}" draggable="true" data-dept-member-card="${user.id}">
      <div class="dept-member-avatar">${escapeHtml((user.name || "?").slice(0, 1))}</div>
      <div class="dept-member-main">
        <div class="dept-member-name">${escapeHtml(user.name)}${isLeader ? `<span class="dept-leader-badge">責任者</span>` : ""}</div>
        <div class="dept-member-meta">${escapeHtml(deptLabel)}${inactiveNote}</div>
      </div>
      <button type="button" class="dept-member-menu" data-dept-member-menu="${escapeHtmlAttr(user.id)}" title="所属部署・責任者を変更" aria-label="${escapeHtmlAttr(user.name)}の所属部署・責任者を変更">⋯</button>
    </div>
  `;
}

function getDepartmentByMemberId(memberId) {
  const departments = uiState.deptSettings?.departments || [];
  return departments.find((dept) => dept.memberIds.includes(memberId)) || null;
}

function departmentMemberEditorHtml(editingDept) {
  const editingMembers = editingDept.memberIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  const otherMembers = users.filter(
    (u) => isActiveUser(u) && !u.onLeave && !u.retired && !editingDept.memberIds.includes(u.id)
  );
  const leftCards = editingMembers.map((u) => memberCardHtml(u, editingDept.name, editingDept.leaderMemberId === u.id)).join("");
  const rightCards = otherMembers.map((u) => {
    const dept = getDepartmentByMemberId(u.id);
    return memberCardHtml(u, dept?.name || "未所属");
  }).join("");
  return `
    <div class="dept-member-editor">
      <div class="dept-member-editor-head">
        <h4>「${escapeHtml(editingDept.name)}」メンバー編集</h4>
        <p>カードをドラッグして部署間で移動できます。ダブルクリックで責任者の設定・解除（初期は責任者なし）</p>
      </div>
      <div class="dept-member-grid">
        <div class="dept-member-col">
          <div class="dept-member-col-title">${escapeHtml(editingDept.name)}（${editingMembers.length}名）</div>
          <div class="dept-member-dropzone" data-dept-drop-zone="${editingDept.id}">
            ${leftCards || `<div class="dept-empty-note">メンバーがいません</div>`}
          </div>
        </div>
        <div class="dept-member-col dept-member-col--pool">
          <div class="dept-member-col-title">その他のメンバー（${otherMembers.length}名）</div>
          <div class="dept-member-dropzone" data-dept-drop-pool="1" data-dept-drop-zone="${editingDept.id}">
            ${rightCards || `<div class="dept-empty-note">メンバーがいません</div>`}
          </div>
        </div>
      </div>
    </div>
  `;
}

function moveMemberToDepartment(memberId, deptId) {
  const state = uiState.deptSettings;
  if (!state) return;
  state.departments.forEach((d) => {
    d.memberIds = d.memberIds.filter((id) => id !== memberId);
    if (d.leaderMemberId === memberId) d.leaderMemberId = "";
  });
  const target = state.departments.find((d) => d.id === deptId);
  if (!target) return;
  target.memberIds.push(String(memberId));
}

function removeMemberFromDepartment(memberId, deptId) {
  const state = uiState.deptSettings;
  if (!state) return;
  const target = state.departments.find((d) => d.id === deptId);
  if (!target) return;
  target.memberIds = target.memberIds.filter((id) => id !== memberId);
  if (target.leaderMemberId === memberId) target.leaderMemberId = "";
}

function renderDeptCreateModal() {
  const modal = uiState.deptCreateModal;
  if (!modal) return "";
  return `
    <div class="cl-mini-modal-overlay open" data-dept-close-add="1">
      <div class="cl-mini-modal">
        <div class="cl-mini-modal-head">
          <h4>${modal.editId ? "部署名を変更" : "部署を追加"}</h4>
          <button class="cl-modal-close" data-dept-close-add="1" type="button">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <label class="cl-form-group">
            <span class="cl-form-label">部署名 <span class="cl-form-required">*</span></span>
            <input
              class="cl-form-input"
              data-dept-create-name="1"
              value="${escapeHtmlAttr(modal.name || "")}"
              placeholder="例：営業2部"
            />
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button class="cl-btn cl-btn-outline" data-dept-close-add="1" type="button">キャンセル</button>
          <button class="cl-btn cl-btn-primary" data-dept-save-add="1" type="button">${modal.editId ? "変更する" : "追加する"}</button>
        </div>
      </div>
    </div>
  `;
}

/** 原価管理: 一括「種別を変更」「割当を依頼」用ミニモーダル */
function renderCostMgmtBulkModal() {
  if (currentRoute().page !== "cost-management") return "";
  const m = uiState.costMgmtBulkModal;
  if (!m) return "";
  pruneCostMgmtSelectionToValid();
  const sel = uiState.costMgmtSelected || {};
  const selCount = Object.keys(sel).filter((k) => sel[k]).length;
  if (!selCount) {
    uiState.costMgmtBulkModal = null;
    return "";
  }
  const bulkCatOpts = COST_CATEGORIES.map(
    (cat) => `<option value="${escapeHtmlAttr(cat)}">${escapeHtml(cat)}</option>`
  ).join("");
  const bulkDelegateUserOpts = assignableUsers()
    .map((u) => `<option value="${escapeHtmlAttr(u.id)}">${escapeHtml(u.name || u.id)}</option>`)
    .join("");
  const canDelegate = userCanAccessPage("cost-management");

  if (m === "category") {
    return `
    <div class="cl-mini-modal-overlay open" data-cost-mgmt-bulk-modal-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal" role="dialog" aria-modal="true" aria-labelledby="cost-mgmt-bulk-cat-title">
        <div class="cl-mini-modal-head">
          <h4 id="cost-mgmt-bulk-cat-title">会計区分を変更</h4>
          <button class="cl-modal-close" type="button" data-cost-mgmt-bulk-modal-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="cost-mgmt-bulk-modal-lede">選択中の原価 <strong>${selCount}</strong> 件の会計区分を一括で変更します。</p>
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">会計区分</span>
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-cost-mgmt-bulk-category-pick="1" aria-label="一括変更する会計区分">${bulkCatOpts}</select>
          </label>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-cost-mgmt-bulk-modal-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn cl-btn-primary" data-cost-mgmt-bulk-apply-category="1">会計区分を反映</button>
        </div>
      </div>
    </div>`;
  }
  if (m === "delegate") {
    if (!canDelegate) {
      uiState.costMgmtBulkModal = null;
      return "";
    }
    return `
    <div class="cl-mini-modal-overlay open" data-cost-mgmt-bulk-modal-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal" role="dialog" aria-modal="true" aria-labelledby="cost-mgmt-bulk-delegate-title">
        <div class="cl-mini-modal-head">
          <h4 id="cost-mgmt-bulk-delegate-title">割当を依頼</h4>
          <button class="cl-modal-close" type="button" data-cost-mgmt-bulk-modal-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="cost-mgmt-bulk-modal-lede">原価の割当を依頼します。</p>
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">依頼先</span>
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-cost-mgmt-delegate-user-pick="1" aria-label="案件割当の依頼先">
              <option value="">（選択）</option>
              ${bulkDelegateUserOpts}
            </select>
          </label>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-cost-mgmt-bulk-modal-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="inline-btn ghost" data-cost-mgmt-bulk-delegate-clear="1">依頼を解除する</button>
          <button type="button" class="cl-btn cl-btn-primary" data-cost-mgmt-bulk-delegate="1">依頼する</button>
        </div>
      </div>
    </div>`;
  }
  if (m === "payStatus") {
    // 支払ステータスの一括変更（未受領/受領済/支払済）。経理/マスターのみ。適用は 15j の submitCostBulkPayStatus
    if (!userCanRegisterCostReceipt()) {
      uiState.costMgmtBulkModal = null;
      return "";
    }
    const pick = COST_BULK_PAY_STATUS_TARGETS[uiState.costMgmtBulkPayStatusPick]
      ? uiState.costMgmtBulkPayStatusPick
      : "received";
    const pickLabel = COST_BULK_PAY_STATUS_TARGETS[pick];
    const rows = costMgmtSelectedCostRows();
    // 支払済は支払日の訂正も兼ねるので、支払済の行も対象（支払日を指定日に上書き）
    const sameCount = pick === "paid" ? 0 : rows.filter((c) => costReceiptGroup(c) === pick).length;
    // 受領済にする未受領行のうち請求書No未入力の件数（旧「受領済にする」モーダルの注意を引き継ぐ）
    // 1件だけ未受領→受領済にするときは、その場で請求書Noを入れられるようにする（旧・行ごとの受領登録の代わり）
    const singleReceipt = pick === "received" && rows.length === 1 && costReceiptGroup(rows[0]) === "pending" ? rows[0] : null;
    const noInvoiceNo =
      pick === "received" && !singleReceipt
        ? rows.filter((c) => costReceiptGroup(c) === "pending" && !String(c.invoiceNo || "").trim()).length
        : 0;
    const pickOpts = Object.entries(COST_BULK_PAY_STATUS_TARGETS)
      .map(([k, label]) => `<option value="${k}" ${k === pick ? "selected" : ""}>${label}</option>`)
      .join("");
    const pickNote = {
      pending: "支払済の行は支払日をクリアします。",
      received: "支払済の行は支払日をクリアします。未受領の行の仮原価は実原価になります（＝消込）。",
      paid: "支払日を登録して「支払済」にします。支払済の行は支払日をこの日付に変更します。締め済みの案件でも登録できます。"
    }[pick];
    return `
    <div class="cl-mini-modal-overlay open" data-cost-mgmt-bulk-modal-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal" role="dialog" aria-modal="true" aria-labelledby="cost-mgmt-bulk-paystatus-title">
        <div class="cl-mini-modal-head">
          <h4 id="cost-mgmt-bulk-paystatus-title">支払ステータスを変更</h4>
          <button class="cl-modal-close" type="button" data-cost-mgmt-bulk-modal-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">変更後のステータス</span>
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-cost-mgmt-bulk-paystatus-pick="1" aria-label="一括変更する支払ステータス">${pickOpts}</select>
          </label>
          ${
            pick === "paid"
              ? `<label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">支払日</span>
            <input class="pj-input" type="date" data-cost-mgmt-bulk-paydate-pick="1" value="${escapeHtmlAttr(uiState.costMgmtBulkPayDate || "")}" aria-label="一括登録する支払日" />
          </label>`
              : ""
          }
          ${
            singleReceipt
              ? `<label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">請求書No（先方請求書の番号）</span>
            <input class="pj-input" data-cost-mgmt-bulk-invoice-no="1" value="${escapeHtmlAttr(String(uiState.costMgmtBulkInvoiceNo ?? singleReceipt.invoiceNo ?? ""))}" placeholder="INV-0001" autocomplete="off" />
          </label>`
              : ""
          }
          <p class="cost-mgmt-bulk-modal-lede">選択中の原価 <strong>${selCount}</strong> 件のうち <strong>${rows.length - sameCount}</strong> 件を「${pickLabel}」にします${sameCount ? `（すでに${pickLabel}の ${sameCount} 件はそのまま）` : ""}。${pickNote}</p>
          ${
            noInvoiceNo
              ? `<p class="cost-mgmt-bulk-modal-lede">うち<strong>請求書No未入力が ${noInvoiceNo} 件</strong>あります。未入力のまま受領済にします（あとから行の「編集」で入力できます）。</p>`
              : ""
          }
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-cost-mgmt-bulk-modal-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn cl-btn-primary" data-cost-mgmt-bulk-paystatus-apply="1" ${rows.length - sameCount ? "" : "disabled"}>「${pickLabel}」にする</button>
        </div>
      </div>
    </div>`;
  }
  uiState.costMgmtBulkModal = null;
  return "";
}

/** 原価割当のグループに出す「前月の割当先」ヒント（記憶の逆引き。CSビュー表示時に1回だけ取得）。
 *  hints=REM物件キー / adHints=広告CSVアカウントキー */
let remAllocHintsState = { loaded: false, loading: false, hints: {}, adHints: {} };

function ensureRemAllocHintsLoaded() {
  if (remAllocHintsState.loaded || remAllocHintsState.loading) return;
  remAllocHintsState.loading = true;
  apiFetch("/api/costs?remHints=1")
    .then((r) => {
      const ok = r.ok && r.data && typeof r.data.hints === "object";
      // 失敗時も loaded=true にする（この画面は1操作ごとに render() するため、
      // 失敗のまま loading だけ戻すと描画のたびに再フェッチが走り続ける）。
      // 再試行は invalidateRemAllocHints()（割当成功時）に任せる
      remAllocHintsState = {
        loaded: true,
        loading: false,
        hints: ok ? r.data.hints || {} : {},
        adHints: ok && r.data.adHints && typeof r.data.adHints === "object" ? r.data.adHints : {}
      };
      if (ok) render();
    })
    .catch(() => {
      remAllocHintsState = { loaded: true, loading: false, hints: {}, adHints: {} };
    });
}

/** 割当で記憶が変わった後に呼ぶ（次の表示で取り直す） */
function invalidateRemAllocHints() {
  remAllocHintsState = { loaded: false, loading: false, hints: {}, adHints: {} };
}

/** REM媒体原価取込のON/OFF・対象営業日（フェッチ結果をキャッシュし、原価登録ページ表示時に1回だけ取得） */
let remBillingSettingsState = { loaded: false, loading: false, enabled: true, targetBusinessDay: 4 };

/** REM取込設定を未取得なら取得する（結果はキャッシュ・完了時に再描画） */
function ensureRemBillingSettingsLoaded() {
  if (remBillingSettingsState.loaded || remBillingSettingsState.loading) return;
  remBillingSettingsState.loading = true;
  apiFetch("/api/rem-billing-settings")
    .then((r) => {
      if (r.ok) {
        remBillingSettingsState = {
          loaded: true,
          loading: false,
          enabled: r.data.enabled !== false,
          targetBusinessDay: Number(r.data.targetBusinessDay) || 4
        };
        render();
      } else {
        remBillingSettingsState.loading = false;
      }
    })
    .catch(() => {
      remBillingSettingsState.loading = false;
    });
}

/** 「⚙ 取込設定」ボタン（原価登録ページの登録ボタンと並べて表示。呼び出し元で経理/マスターのみに絞る） */
function remBillingSettingsButtonHtml() {
  ensureRemBillingSettingsLoaded();
  if (!remBillingSettingsState.loaded) return "";
  return `<button type="button" class="inline-btn ghost" data-rem-billing-settings-open="1"
    style="min-width:auto;padding:10px 20px;font-size:14px;"
    title="REM媒体原価の自動取込のON/OFFと対象営業日を設定します">⚙ 取込設定</button>`;
}

/** 取込設定モーダル（原価登録ページのみ）。開いた瞬間の値をドラフトとして編集し、保存時にまとめて送信する。 */
function renderRemBillingSettingsModal() {
  if (currentRoute().page !== "cost-management") return "";
  const m = uiState.remBillingSettingsModal;
  if (!m) return "";
  const dayMin = 1;
  const dayMax = 10;
  const r = m.importResult;
  const resultHtml = !r
    ? ""
    : r.ok
      ? `<p style="font-size:14px;color:#047857;margin:6px 0 0;">取込完了: 対象${r.totalRows}件 / 自動割当${r.autoAssigned}件 / 未割当${r.unassigned}件 / スキップ${r.skipped}件${r.settledProvisional ? ` / 仮原価の消込${r.settledProvisional}件` : ""}</p>`
      : `<p style="font-size:14px;color:#b42318;margin:6px 0 0;">取込に失敗しました: ${escapeHtml(r.message || "")}</p>`;
  return `
    <div class="cl-mini-modal-overlay open" data-rem-billing-settings-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal cost-modal--readable" role="dialog" aria-modal="true" aria-labelledby="rem-billing-settings-title">
        <div class="cl-mini-modal-head">
          <h4 id="rem-billing-settings-title">REM媒体原価 取込設定</h4>
          <button class="cl-modal-close" type="button" data-rem-billing-settings-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="cost-mgmt-bulk-modal-lede">広告請求CSVからの媒体原価の月次自動取込を設定します。停止中はCron・手動再取込とも実行されません。</p>
          <label class="cl-form-group cost-mgmt-bulk-modal-field" style="flex-direction:row;align-items:center;gap:8px;">
            <input type="checkbox" data-rem-billing-settings-enabled="1" ${m.enabled ? "checked" : ""} style="width:16px;height:16px;" />
            <span class="cl-form-label" style="margin:0;">自動取込を有効にする</span>
          </label>
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">対象営業日（前月分を第何営業日から取り込むか）</span>
            <input type="number" class="pj-input" min="${dayMin}" max="${dayMax}" step="1"
              data-rem-billing-settings-day="1" value="${escapeHtmlAttr(String(m.targetBusinessDay))}" style="max-width:120px;" />
            <span class="cost-modal-help">CSVは第3営業日に確定するため、第4営業日以降を推奨します（既定=4）。営業日は土日祝を除いて数えます。取り込めるまで翌日以降も自動で再試行します。</span>
          </label>
          <div class="cl-form-group cost-mgmt-bulk-modal-field" style="border-top:1px solid #e5e7eb;padding-top:14px;margin-top:4px;">
            <span class="cl-form-label">今すぐ取り込む</span>
            <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
              <input type="month" class="pj-input" data-rem-billing-import-month="1" value="${escapeHtmlAttr(m.importMonth)}" style="max-width:160px;" />
              <button type="button" class="inline-btn ghost" data-rem-billing-import-now="1" ${m.importing ? "disabled" : ""} style="min-width:auto;padding:8px 16px;">${m.importing ? "取込中…" : "今すぐ取り込む"}</button>
            </div>
            <span class="cost-modal-help">複製後の再回収や前倒し発行に。営業日判定を無視して即実行します（設定を保存していなくてもこの操作には影響しません）。</span>
            ${resultHtml}
          </div>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-rem-billing-settings-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn cl-btn-primary" data-rem-billing-settings-save="1" ${m.saving ? "disabled" : ""}>${m.saving ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>`;
}

/** 「🔒 月次ロック」ボタン（原価登録ページの登録ボタンと並べて表示。呼び出し元で経理/マスターのみに絞る） */
function monthLockButtonHtml() {
  return `<button type="button" class="inline-btn ghost" data-month-lock-open="1"
    style="min-width:auto;padding:10px 20px;font-size:14px;"
    title="月単位で案件・原価の編集をロック/解除します">🔒 月次ロック</button>`;
}

/** 月次ロックモーダル（原価登録ページのみ）。対象月を選んでロック/解除をトグルする。
 *  ロック状態はフロントの isMonthLocked（/api/projects の lockedMonths）と同じ集合を参照する。 */
function renderMonthLockModal() {
  const _mlModalPage = currentRoute().page;
  if (_mlModalPage !== "cost-management" && _mlModalPage !== "cost-reconcile") return "";
  const m = uiState.monthLockModal;
  if (!m) return "";
  const ymSlash = String(m.ym || "").replace("-", "/");
  const validYm = /^\d{4}\/\d{2}$/.test(ymSlash);
  const locked = validYm && isMonthLocked(ymSlash);
  // 照合の未確認件数: ロック前の最終チェック導線（警告のみ・ブロックはしない。媒体費=対象外は数えない）
  const unreconciled = validYm ? costReconcileUnreconciledCount(m.ym) : 0;
  const unreconciledHtml =
    validYm && !locked && unreconciled > 0
      ? `<p style="font-size:13px;color:#b45309;margin:6px 0 0;">⚠ この月に照合が「未確認」の原価が <strong>${unreconciled}件</strong> あります。<a href="#cost-reconcile">照合画面で確認する</a>（ロック自体は可能です）</p>`
      : "";
  const statusHtml = !validYm
    ? ""
    : (locked
        ? `<p style="font-size:13px;color:#b42318;margin:6px 0 0;">🔒 ${escapeHtml(ymSlash)} はロック済みです（この月の案件・原価の金額系は編集できません）</p>`
        : `<p style="font-size:13px;color:#047857;margin:6px 0 0;">${escapeHtml(ymSlash)} は未ロックです（編集可能）</p>`) + unreconciledHtml;
  return `
    <div class="cl-mini-modal-overlay open" data-month-lock-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal" role="dialog" aria-modal="true" aria-labelledby="month-lock-title">
        <div class="cl-mini-modal-head">
          <h4 id="month-lock-title">🔒 月次ロック</h4>
          <button class="cl-modal-close" type="button" data-month-lock-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="cost-mgmt-bulk-modal-lede">月単位で案件・原価の編集をロックします。その月の金額と内容を確認し終えたらロックしてください（自動ではかかりません）。</p>
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">計上月</span>
            <input type="month" class="pj-input" data-month-lock-month="1" value="${escapeHtmlAttr(m.ym || "")}" style="max-width:160px;" />
            ${statusHtml}
          </label>
          <span style="font-size:12px;color:#64748b;">ロック中の案件は、案件一覧からのロック解除申請（3営業日の期限つき）で個別に編集を許可できます。</span>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-month-lock-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn ${locked ? "cl-btn-outline" : "cl-btn-primary"}" data-month-lock-toggle="1" ${m.saving || !validYm ? "disabled" : ""}>${m.saving ? "変更中…" : locked ? "ロックを解除する" : "この月をロックする"}</button>
        </div>
      </div>
    </div>`;
}

/** 月次ロックのトグルを送信する（経理/マスターのみ操作可。サーバ /api/month-locks が正本） */
async function submitMonthLockToggle() {
  const m = uiState.monthLockModal;
  if (!m || m.saving) return;
  const ymSlash = String(m.ym || "").replace("-", "/");
  if (!/^\d{4}\/\d{2}$/.test(ymSlash)) {
    showToast("計上月を選択してください", { variant: "error" });
    return;
  }
  const nextLocked = !isMonthLocked(ymSlash);
  if (nextLocked ? !(await confirmOrderEvidencePendingForLock(ymSlash)) : !(await fpConfirm({ title: `${ymSlash} の月次ロックを解除しますか？`, message: "この月の案件・原価が再び編集できるようになります。", note: "締めをやり直すときは、もう一度ロックしてください。", confirmLabel: "ロックを解除する" }))) return;
  m.saving = true;
  render();
  const res = await apiFetch("/api/month-locks", { method: "POST", body: { ym: ymSlash, locked: nextLocked } });
  m.saving = false;
  if (!res.ok) {
    showToast(typeof res.data?.error === "string" ? res.data.error : "月次ロックの変更に失敗しました", { variant: "error" });
    render();
    return;
  }
  setLockedMonths(res.data.lockedMonths);
  showToast(nextLocked ? `${ymSlash} をロックしました` : `${ymSlash} のロックを解除しました`);
  render();
}

/** 現在時刻基準の前月を 'YYYY-MM' で返す（取込対象月ピッカーの既定値） */
function remBillingDefaultImportMonth() {
  const now = new Date();
  // setMonth(getMonth()-1) は 31日等で日付が溢れて当月に戻るため、1日固定で前月を作る
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * メニュー「原価割当」: 自分宛の案件未割当原価を一覧で表示し、案件へ紐づける（原価管理と同系のテーブル UI）。
 */
function costAllocationSectionHtml(opts) {
  pruneCostAllocSelectionToValid();
  const csView = userSeesCostAllocationCsView();
  const { flt, srt } = ensureCostAllocListState();
  const monthFilter = opts?.monthFilter || "";
  let rows = costAllocationVisibleRows();
  if (monthFilter) {
    const want = parseProjectBillingMonthYyyymm(monthFilter.replace(/-/g, "/"));
    rows = rows.filter((c) => parseProjectBillingMonthYyyymm(c.targetMonth || "") === want);
  }
  // 支払先の候補は、支払先で絞り込む前（計上月までを適用した状態）の行から作る
  const vendorOptionsHtml = costMgmtVendorFilterOptionsHtml(rows, flt.vendor);
  if (flt.vendor) rows = rows.filter((c) => String(c.vendor || "").trim() === flt.vendor);
  rows = sortCostRowsBy(rows, srt.key, srt.dir);
  const sortMark = (k) => (srt.key === k ? (srt.dir === "asc" ? " ▲" : " ▼") : "");
  const sortCls = (k) => (srt.key === k ? " cost-sort-active" : "");
  const n = rows.length;
  // CSビューは2つの表示形式を持つ: cards(既定)=3層構成（異議＝最上部フラット／物件グループ／その他フラット）、
  // table=グループ化せず全行フラット（複数グループ横断のチェック選択・金額順ソート等に使う）。営業ビューは従来のまま
  const viewMode = csView ? flt.viewMode : "cards";
  // ゾーニング自体は表示形式によらず計算する（table表示中もツールバーの異議件数などを正しく出すため）。
  // グループをカードとして描画するかどうかだけを viewMode で切り替える
  const zones = csView ? costAllocationZonedRows(rows) : null;
  const bukkenFilter = zones ? String(flt.bukken || "").trim().toLowerCase() : "";
  let groupEntries = [];
  if (zones && viewMode === "cards") {
    // REM物件グループと広告CSVアカウントグループを1つの並びに統合（見た目・操作は同系のカード）。
    // カード内の行も計上月の新しい順で固定（列ヘッダーの並び替えはフラット行にのみ効く）
    const byMonth = (rs) => sortCostRowsBy(rs, "targetMonth", "desc");
    groupEntries = [
      ...[...zones.groups.entries()].map(([key, rs]) => ({ kind: "rem", key, rows: byMonth(rs) })),
      ...[...zones.adGroups.entries()].map(([key, rs]) => ({ kind: "ad", key, rows: byMonth(rs) }))
    ];
    if (bukkenFilter) groupEntries = groupEntries.filter((g) => String(g.key).toLowerCase().includes(bukkenFilter));
    // グループは計上月の新しい順で固定（照会の案件カードと同じ）
    groupEntries.sort((a, b) => compareCostCardRowsNewestFirst(a.rows, b.rows));
    ensureRemAllocHintsLoaded();
  }
  const disputeRows = zones && viewMode === "cards" ? zones.disputes : [];
  const otherRows = zones && viewMode === "cards" ? zones.others : rows;
  const flatSelectableRows = zones && viewMode === "cards" ? [...disputeRows, ...otherRows] : rows;
  // 物件で絞り込んだグループの行数（件数のグループ数と同じ集合で数える）
  const groupRowN = groupEntries.reduce((s, g) => s + g.rows.length, 0);
  const disputeN = zones ? zones.disputes.length : 0;
  const sel = uiState.costAllocSelected || {};
  const selCount = Object.keys(sel).filter((k) => sel[k]).length;
  // チェック選択の対象はフラット行のみ（グループの行はヘッダーの一括操作で扱う）
  const allVisKeys = flatSelectableRows.map((c) => costMgmtSelectionKey(COST_UNASSIGNED_PROJECT_UI_ID, c.id));
  const allVisSelected = flatSelectableRows.length > 0 && allVisKeys.length > 0 && allVisKeys.every((k) => sel[k]);
  const bulkBarHtml =
    n === 0
      ? ""
      : selCount === 0
      ? `<div class="pj-bulk-bar cost-mgmt-bulk-bar cost-alloc-bulk-bar"></div>` // 未選択は案内だけ（CSS）。位置を確保して表をずらさない
      : `<div class="pj-bulk-bar cost-mgmt-bulk-bar cost-alloc-bulk-bar show">
          <span>${selCount}件選択中</span>
          <span class="sep" aria-hidden="true">|</span>
          <select class="pj-input cost-alloc-bulk-flat-select cost-alloc-project-pick-select" data-cost-alloc-bulk-project-pick="1" aria-label="まとめて割当する案件">${costAllocBulkProjectPickOptionsHtml()}</select>
          <button type="button" class="cl-btn cl-btn-primary cost-alloc-bulk-flat-btn" data-cost-alloc-bulk-assign="1"${uiState.costAllocBulkAssignBusy ? " disabled" : ""}>${uiState.costAllocBulkAssignBusy ? "割当中…" : "まとめて割当"}</button>
          ${csView ? costBoxAssignBarHtml() : ""}
        </div>`;
  const isFiltered = !!flt.vendor || !!(csView && flt.bukken) || srt.key !== "default";
  // 呼び出し元が自分のフィルタ（担当者・計上月など）を持つ場合は、絞り込み欄が2段に割れないよう
  // この1行に前置きし、「リセット」も呼び出し元のものに一本化する
  const leadingFiltersHtml = opts?.leadingFiltersHtml || "";
  const clearButtonHtml =
    opts?.clearButtonHtml ??
    (isFiltered ? `<button type="button" class="inline-btn ghost cost-review-clear" data-cost-alloc-filter-clear="1">リセット</button>` : "");
  const toolbarHtml = `
    <div class="pj-filter-area">
      <div class="cost-mgmt-search-bar pj-cost-mgmt-filter-row pj-filter-row--main cost-allocation-toolbar">
        <div class="cost-mgmt-search-row cost-mgmt-search-row--top">
          ${leadingFiltersHtml}
          ${
            // 物件名フィルタは予測グループの絞り込み専用。一覧（table）表示ではグループが無く意味を持たないため隠す
            csView && viewMode === "cards"
              ? `<label class="cost-mgmt-search-field cost-mgmt-search-field--bukken">
            <span class="cost-mgmt-search-label">物件</span>
            <input type="text" class="pj-input" data-cost-alloc-filter-bukken data-ime-search placeholder="物件名で絞り込み" value="${escapeHtmlAttr(flt.bukken || "")}" />
          </label>`
              : ""
          }
          <label class="cost-mgmt-search-field cost-mgmt-search-field--vendor">
            <span class="cost-mgmt-search-label">発注先</span>
            <select class="pj-input" data-cost-alloc-filter-vendor>${vendorOptionsHtml}</select>
          </label>
          ${csView && viewMode === "cards" ? "" : costSortControlHtml({ prefix: "cost-alloc", options: COST_ALLOC_SORT_OPTIONS, key: srt.key, dir: srt.dir })}
          ${
            csView
              ? `<div class="cost-review-view-seg" role="group" aria-label="表示形式">
            <button type="button" class="inline-btn ghost${viewMode === "cards" ? " cost-review-view-on" : ""}" data-cost-alloc-view="cards" title="物件・アカウント単位のカードで表示">物件グループ</button>
            <button type="button" class="inline-btn ghost${viewMode === "table" ? " cost-review-view-on" : ""}" data-cost-alloc-view="table" title="グループ化せず全行を表で表示">一覧</button>
          </div>`
              : ""
          }
          ${clearButtonHtml}
        </div>
        <div class="cost-mgmt-search-row cost-allocation-toolbar-row">
          <p class="cost-allocation-toolbar-summary">${
            csView
              ? `未割当の原価 <strong>${n}</strong> 件${
                  disputeN > 0 ? `（うち<strong class="cost-dispute-count">異議 ${disputeN} 件</strong>）` : ""
                }${groupEntries.length > 0 ? ` ／ 物件グループ <strong>${groupEntries.length}</strong>（${groupRowN}行）` : ""}。`
              : `あなた宛の案件割当依頼を <strong>${n}</strong> 件表示しています。`
          }<span class="cost-allocation-toolbar-hint">${
            csView
              ? viewMode === "cards"
                ? `物件グループは前月の割当先が分かっていれば<strong>まとめて請求書を作成</strong>で1回（前月案件が見つからない時はその場で新規案件を作って続けます）、既存案件へ入れたい時だけ開いて選んでください。それ以外はチェックで選んで<strong>まとめて割当</strong>か、行の<strong>編集</strong>から保存してください。計上先案件は<strong>計上月が異なる案件も</strong>選べます。`
                : `チェックで複数行を選び<strong>まとめて割当</strong>するか、行の<strong>編集</strong>から保存してください。計上先案件は<strong>計上月が異なる案件も</strong>選べます。`
              : `チェックで複数行を選び<strong>まとめて割当</strong>するか、行の<strong>編集</strong>から保存してください。計上先案件は<strong>計上月が異なる案件も</strong>選べます。`
          }</span></p>
        </div>
      </div>
    </div>`;
  const rowTr = (c) => {
    const taxY = costConsumptionTaxYen(c);
    const exY = Number(c.amount || 0);
    const totalIncl = exY + taxY;
    const amtInner =
      taxY > 0
        ? `<span class="cost-amt-total">${yen.format(exY)}</span><div class="cost-tax-sub">消費税 ${yen.format(
            taxY
          )} · 税込 ${yen.format(totalIncl)}</div>`
        : `<span class="cost-amt-total">${yen.format(exY)}</span>`;
    const pid = COST_UNASSIGNED_PROJECT_UI_ID;
    // 摘要・備考はその場で編集可（CS同士の状況共有用。原価登録一覧と同じインライン保存）
    const noteDisplay = stripCostBucketMarkers(String(c.note || "").trim());
    const noteHtml = `<textarea class="cost-table-note-inline" rows="1" readonly title="ダブルクリックで編集" data-cost-inline-note="${escapeHtmlAttr(`${pid}:${c.id}`)}" data-cost-inline-note-orig="${escapeHtmlAttr(noteDisplay)}" placeholder="発注名・メモ">${escapeHtml(noteDisplay)}</textarea>`;
    const rowKey = costMgmtSelectionKey(pid, c.id);
    const rowChecked = sel[rowKey] ? " checked" : "";
    // 異議（営業からの差し戻し）: 元案件・申請者・理由を表示
    const dispFrom = c.disputeProjectDbId != null ? projects.find((pp) => pp.dbId === c.disputeProjectDbId) : null;
    const dispBy = c.disputedByDbId != null ? getUserById(`u${c.disputedByDbId}`)?.name || "" : "";
    const disputeHtml = c.disputeNote
      ? `<div class="cost-dispute-badge" title="${escapeHtmlAttr(`${dispBy ? dispBy + "さん" : "担当営業"}より異議${dispFrom ? `（元: ${dispFrom.name}）` : ""}: ${c.disputeNote}`)}">異議: ${escapeHtml(String(c.disputeNote).slice(0, 30))}${String(c.disputeNote).length > 30 ? "…" : ""}${dispFrom ? `<span class="cost-dispute-from">元: ${escapeHtml(dispFrom.name)}</span>` : ""}</div>`
      : "";
    return `
          <tr class="cost-row-unassigned${c.disputeNote ? " cost-row-disputed" : ""}">
            <td class="cost-table-col-check">
              <input type="checkbox" class="cost-mgmt-row-check"${rowChecked} data-cost-alloc-row-check="${escapeHtmlAttr(rowKey)}" aria-label="行を選択" />
            </td>
            <td class="cost-td-category"><span class="cost-category-badge">${escapeHtml(c.category)}</span></td>
            <td class="cost-vendor">${escapeHtml(c.vendor || "—")}${disputeHtml}</td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
            <td class="cost-table-note-cell cost-table-note-cell--editable">${noteHtml}${costMemoMarkHtml(c)}</td>
            <td class="cost-actions">
              <button type="button" class="inline-btn ghost" data-edit-cost="${pid}:${escapeHtmlAttr(c.id)}">編集</button>${
                csView && c.importSource === "rem"
                  ? `<button type="button" class="inline-btn ghost" data-rem-create-from-prev="${escapeHtmlAttr(String(costDbNumericId(c) || ""))}" data-rem-create-from-prev-uiid="${escapeHtmlAttr(String(c.id))}" title="前月に割り当てた案件を月ずらしで複製し、この原価をその案件へ割り当てます。見つからない場合はその場で新規案件を作り請求書作成へ進みます">請求書を作成</button>`
                  : ""
              }
            </td>
          </tr>`;
  };
  // 従来のフラット表（thead＋tbody）。全選択チェックは表示中のフラット行（異議＋その他）を
  // まとめて対象にする1つの状態で、両テーブルのヘッダーに同じものが出る。
  // 「案件」列は置かない（この表は未割当の行だけなので常に「案件未割当」で情報にならず、金額が横スクロールの先に押し出されていた）。
  // 異議の元案件は発注先の下に出す
  const flatTableWrap = (tbodyHtml, withCheckAll) => `
    <div class="pj-table-area">
      <table class="cost-table cost-table--global cost-table--allocation-queue" data-fp-table="cost-alloc-queue">
        <thead>
          <tr>
            <th class="cost-table-col-check" scope="col">${
              withCheckAll
                ? `<input type="checkbox" class="cost-mgmt-row-check cost-mgmt-check-all"${allVisSelected ? " checked" : ""} data-cost-alloc-check-all="1" title="表示中の行をすべて選択" aria-label="表示中の行をすべて選択" />`
                : ""
            }</th>
            <th scope="col" class="cost-th-sortable${sortCls("category")}" data-cost-alloc-sort="category">会計区分${sortMark("category")}</th>
            <th class="cost-table-col-vendor cost-th-sortable${sortCls("vendor")}" scope="col" data-cost-alloc-sort="vendor">発注先${sortMark("vendor")}</th>
            <th scope="col" class="cost-th-sortable${sortCls("amount")}" data-cost-alloc-sort="amount">金額（税抜）${sortMark("amount")}</th>
            <th scope="col" class="cost-th-sortable${sortCls("targetMonth")}" data-cost-alloc-sort="targetMonth">計上月${sortMark("targetMonth")}</th>
            <th scope="col" data-fp-fill>発注名</th>
            <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
          </tr>
        </thead>
        <tbody>${tbodyHtml}</tbody>
      </table>
    </div>`;
  let bodyHtml;
  if (uiState.heavyDataLoading) {
    bodyHtml = flatTableWrap(
      `<tr><td colspan="7" class="cost-empty cost-empty--heavy-load">${flowPilotNowLoadingBlock({
        inline: true,
        subtitle: "原価データを読み込んでいます"
      })}</td></tr>`,
      true
    );
  } else if (!csView || n === 0 || viewMode === "table") {
    // 一覧（table）表示: グループ化せず全行フラット（複数グループ横断のチェック選択・金額順ソート用）
    bodyHtml = flatTableWrap(
      n === 0
        ? `<tr><td colspan="7" class="cost-empty">${isFiltered ? "該当する原価がありません。" : csView ? "未割当の原価はありません。" : "該当する案件割当依頼はありません。"}</td></tr>`
        : rows.map(rowTr).join(""),
      true
    );
  } else {
    // CSビュー: 異議（最上部）→ 物件グループ → その他（物件情報なし）の3層
    const disputeHtml = disputeRows.length
      ? `<p class="cost-alloc-zone-label cost-alloc-zone-label--dispute">営業からの異議 ${disputeRows.length}件</p>${flatTableWrap(disputeRows.map(rowTr).join(""), true)}`
      : "";
    const groupsHtml = groupEntries.length
      ? `<div class="cost-bkgrp-list">${groupEntries
          .map((g) => (g.kind === "ad" ? costAllocAdAccountGroupHtml(g.key, g.rows) : costAllocBukkenGroupHtml(g.key, g.rows)))
          .join("")}</div>`
      : bukkenFilter
        ? `<p class="cost-alloc-zone-label">物件名「${escapeHtml(flt.bukken)}」に一致するグループはありません。</p>`
        : "";
    const othersHtml = otherRows.length
      ? `<p class="cost-alloc-zone-label">その他の未割当（物件情報なし） ${otherRows.length}件</p>${flatTableWrap(otherRows.map(rowTr).join(""), true)}`
      : "";
    bodyHtml = disputeHtml + groupsHtml + othersHtml;
  }
  return `
    ${toolbarHtml}
    ${bulkBarHtml}
    ${bodyHtml}`;
}

/**
 * 原価割当（CSビュー）: 物件グループ1枚分のカード。
 * ヘッダー＝物件名・行数・カテゴリ別内訳・税抜合計（クリックで展開）。
 * メタ行＝前月の割当先ヒント（記憶の逆引き）とグループ単位の一括操作。
 * ボタンには描画時点の対象行IDを持たせ、クリック側はそれをそのまま使う（フィルタ状態の再現を不要にする）。
 */
function costAllocBukkenGroupHtml(bukken, rs) {
  const open = !!(uiState.costAllocOpenBukken && uiState.costAllocOpenBukken[bukken]);
  const total = rs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalIncl = rs.reduce((s, c) => s + Number(c.amount || 0) + costConsumptionTaxYen(c), 0);
  const byCat = new Map();
  for (const c of rs) byCat.set(c.category, (byCat.get(c.category) || 0) + Number(c.amount || 0));
  const catChips = [...byCat.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([cat, sum]) => `<span class="cost-bkgrp-cat"><span class="cost-category-badge">${escapeHtml(cat)}</span> ${yen.format(sum)}</span>`)
    .join("");
  const hint = remAllocHintsState.loaded ? remAllocHintsState.hints[bukken] : undefined;
  const NEW_BUKKEN_HINT_TEXT = "この物件は初回です。一度割り当てると記憶され、来月からは自動割当の対象になります。";
  const hintHtml =
    remAllocHintsState.loaded && hint
      ? `<div class="cost-bkgrp-hint">前月の割当先: <strong>${escapeHtml(hint.clientName || "")}</strong>${
          hint.exampleProjectName
            ? ` ／ 例: ${escapeHtml(hint.exampleProjectName)}${hint.exampleBillingMonth ? `（請求 ${escapeHtml(hint.exampleBillingMonth)}）` : ""}`
            : ""
        }<span class="cost-bkgrp-hint-auto">対象の案件が起票されると翌朝に自動割当されます</span></div>`
      : "";
  const uiIds = rs.map((c) => String(c.id)).join(",");
  const dbIds = rs.map((c) => String(costDbNumericId(c) || "")).filter(Boolean).join(",");
  const pick = (uiState.costAllocGroupPick && uiState.costAllocGroupPick[bukken]) || "";
  return `
    <div class="cost-bkgrp${open ? " cost-bkgrp--open" : ""}">
      <div class="cost-bkgrp-head" data-rem-group-toggle="${escapeHtmlAttr(bukken)}" role="button" tabindex="0" aria-expanded="${open ? "true" : "false"}">
        <span class="cost-bkgrp-caret" aria-hidden="true">▶</span>
        <div class="cost-bkgrp-title">
          <span class="cost-bkgrp-name">${escapeHtml(bukken)}</span>
          <span class="cost-bkgrp-src">自動取込・${rs.length}行</span>
          ${remAllocHintsState.loaded && !hint ? `<span class="cost-bkgrp-new" title="${escapeHtmlAttr(NEW_BUKKEN_HINT_TEXT)}">初回（記憶なし）</span>` : ""}
          <span class="cost-bkgrp-cats">${catChips}</span>
        </div>
        <div class="cost-bkgrp-total">${yen.format(total)}<small>税抜合計 · 税込 ${yen.format(totalIncl)}</small></div>
      </div>
      ${hintHtml}
      ${
        open
          ? // 開いた時だけ操作ボタンを出す（閉じた状態のボタンは撤去。ヘッダー押下で開いてから操作する動線に統一）。
            // 「まとめて請求書を作成」は記憶の有無に関わらず常に出す（記憶が無くても、その場で新規案件を作って
            // 請求書作成へ続けられるため＝openProjectCreateForCostRows のフォールバックで対応済み）
            `<div class="cost-bkgrp-actions">
        <select class="pj-input cost-alloc-project-pick-select cost-bkgrp-pick" data-rem-group-project-pick="${escapeHtmlAttr(bukken)}" aria-label="このグループの割当先案件">${costAllocBulkProjectPickOptionsHtml(pick)}</select>
        <button type="button" class="cl-btn cl-btn-primary cost-bkgrp-assign-btn" data-rem-group-assign="${escapeHtmlAttr(bukken)}" data-rem-group-cost-ids="${escapeHtmlAttr(uiIds)}">この案件へまとめて割当（${rs.length}行）</button>
        <button type="button" class="inline-btn ghost" data-rem-group-create-prev="${escapeHtmlAttr(bukken)}" data-rem-group-cost-dbids="${escapeHtmlAttr(dbIds)}" data-rem-group-cost-ids="${escapeHtmlAttr(uiIds)}" title="前月に割り当てた案件を月ずらしで複製してこのグループの原価をまとめて割り当て、請求書作成まで開きます。見つからない場合はその場で新規案件を作って続けます">まとめて請求書を作成</button>
      </div>`
          : ""
      }
      ${
        open
          ? `<div class="cost-bkgrp-body"><table class="cost-table cost-table--global cost-bkgrp-table" data-fp-table="cost-alloc-bukken">
        <thead><tr>
          <th scope="col">会計区分</th>
          <th scope="col">発注先</th>
          <th scope="col">グループ（キャンペーン等）</th>
          <th scope="col">金額（税抜）</th>
          <th scope="col">計上月</th>
          <th scope="col" data-fp-fill>発注名</th>
          <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
        </tr></thead>
        <tbody>${rs
          .map((c) => {
            const noteDisplay = stripCostBucketMarkers(String(c.note || "").trim());
            // 金額はフラット表と同じく税込・消費税のサブ行つき（請求書との金額突合に必要）
            const taxY = costConsumptionTaxYen(c);
            const exY = Number(c.amount || 0);
            const amtInner =
              taxY > 0
                ? `<span class="cost-amt-total">${yen.format(exY)}</span><div class="cost-tax-sub">消費税 ${yen.format(taxY)} · 税込 ${yen.format(exY + taxY)}</div>`
                : `<span class="cost-amt-total">${yen.format(exY)}</span>`;
            return `<tr class="cost-row-unassigned">
            <td class="cost-td-category"><span class="cost-category-badge">${escapeHtml(c.category)}</span></td>
            <td class="cost-vendor">${escapeHtml(c.vendor || "—")}</td>
            <td class="cost-bkgrp-td-group">${escapeHtml(c.remGroup && c.remGroup !== "-" ? c.remGroup : "—")}</td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
            <td class="cost-table-note-cell cost-table-note-cell--editable"><textarea class="cost-table-note-inline" rows="1" readonly title="ダブルクリックで編集" data-cost-inline-note="${escapeHtmlAttr(`${COST_UNASSIGNED_PROJECT_UI_ID}:${c.id}`)}" data-cost-inline-note-orig="${escapeHtmlAttr(noteDisplay)}" placeholder="発注名・メモ">${escapeHtml(noteDisplay)}</textarea></td>
            <td class="cost-actions"><button type="button" class="inline-btn ghost" data-edit-cost="${COST_UNASSIGNED_PROJECT_UI_ID}:${escapeHtmlAttr(c.id)}">編集</button><button type="button" class="inline-btn ghost" data-rem-create-from-prev="${escapeHtmlAttr(String(costDbNumericId(c) || ""))}" data-rem-create-from-prev-uiid="${escapeHtmlAttr(String(c.id))}" title="前月に割り当てた案件を月ずらしで複製し、この行だけをその案件へ割り当てて請求書作成まで開きます。見つからない場合はその場で新規案件を作って続けます">請求書を作成</button></td>
          </tr>`;
          })
          .join("")}</tbody>
      </table></div>`
          : ""
      }
    </div>`;
}

/**
 * 原価割当（CSビュー）: 広告CSV取込のアカウントグループ1枚分のカード。
 * Google/META/Yahoo/LINE と媒体が分かれていても同じ案件へ紐づく塊なので、
 * アカウント名（摘要から復元）単位に1枚へ集約する。REM物件カードと同じ意匠・同じ操作系。
 * 開閉・割当先セレクト・まとめて割当は REM 用の data-rem-group-* ハンドラを
 * `ad:` プレフィックスつきキーでそのまま共用する（15h-rem-cost-alloc.js）。
 */
function costAllocAdAccountGroupHtml(account, rs) {
  const groupKey = `ad:${account}`;
  const open = !!(uiState.costAllocOpenBukken && uiState.costAllocOpenBukken[groupKey]);
  const total = rs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalIncl = rs.reduce((s, c) => s + Number(c.amount || 0) + costConsumptionTaxYen(c), 0);
  const byCat = new Map();
  for (const c of rs) byCat.set(c.category, (byCat.get(c.category) || 0) + Number(c.amount || 0));
  const catChips = [...byCat.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([cat, sum]) => `<span class="cost-bkgrp-cat"><span class="cost-category-badge">${escapeHtml(cat)}</span> ${yen.format(sum)}</span>`)
    .join("");
  const hint = remAllocHintsState.loaded ? remAllocHintsState.adHints[account] : undefined;
  const NEW_AD_ACCOUNT_HINT_TEXT = "このアカウントは初回です。一度割り当てると記憶され、来月からは「まとめて請求書を作成」で前月案件を自動複製できるようになります（今回は新規案件の作成から）。";
  const hintHtml =
    remAllocHintsState.loaded && hint
      ? `<div class="cost-bkgrp-hint">前月の割当先: <strong>${escapeHtml(hint.clientName || "")}</strong> ／ ${escapeHtml(hint.projectName || "")}${hint.billingMonth ? `（請求 ${escapeHtml(hint.billingMonth)}）` : ""}<span class="cost-bkgrp-hint-auto">同名の翌月案件が起票されると翌朝に自動割当されます</span></div>`
      : "";
  const uiIds = rs.map((c) => String(c.id)).join(",");
  const dbIds = rs.map((c) => String(costDbNumericId(c) || "")).filter(Boolean).join(",");
  const pick = (uiState.costAllocGroupPick && uiState.costAllocGroupPick[groupKey]) || "";
  return `
    <div class="cost-bkgrp${open ? " cost-bkgrp--open" : ""}">
      <div class="cost-bkgrp-head" data-rem-group-toggle="${escapeHtmlAttr(groupKey)}" role="button" tabindex="0" aria-expanded="${open ? "true" : "false"}">
        <span class="cost-bkgrp-caret" aria-hidden="true">▶</span>
        <div class="cost-bkgrp-title">
          <span class="cost-bkgrp-name">${escapeHtml(account)}</span>
          <span class="cost-bkgrp-src">手動取込・${rs.length}行</span>
          ${remAllocHintsState.loaded && !hint ? `<span class="cost-bkgrp-new" title="${escapeHtmlAttr(NEW_AD_ACCOUNT_HINT_TEXT)}">初回（記憶なし）</span>` : ""}
          <span class="cost-bkgrp-cats">${catChips}</span>
        </div>
        <div class="cost-bkgrp-total">${yen.format(total)}<small>税抜合計 · 税込 ${yen.format(totalIncl)}</small></div>
      </div>
      ${hintHtml}
      ${
        open
          ? // 開いた時だけ操作ボタンを出す（閉じた状態のボタンは撤去。ヘッダー押下で開いてから操作する動線に統一）。
            // 「まとめて請求書を作成」は記憶の有無に関わらず常に出す（記憶が無くても、その場で新規案件を作って
            // 請求書作成へ続けられるため＝openProjectCreateForCostRows のフォールバックで対応済み）
            `<div class="cost-bkgrp-actions">
        <select class="pj-input cost-alloc-project-pick-select cost-bkgrp-pick" data-rem-group-project-pick="${escapeHtmlAttr(groupKey)}" aria-label="このグループの割当先案件">${costAllocBulkProjectPickOptionsHtml(pick)}</select>
        <button type="button" class="cl-btn cl-btn-primary cost-bkgrp-assign-btn" data-rem-group-assign="${escapeHtmlAttr(groupKey)}" data-rem-group-cost-ids="${escapeHtmlAttr(uiIds)}">この案件へまとめて割当（${rs.length}行）</button>
        <button type="button" class="inline-btn ghost" data-ad-group-create-prev="${escapeHtmlAttr(account)}" data-rem-group-cost-dbids="${escapeHtmlAttr(dbIds)}" data-rem-group-cost-ids="${escapeHtmlAttr(uiIds)}" title="前月に割り当てた案件を月ずらしで複製してこのグループの原価をまとめて割り当て、請求書作成まで開きます。見つからない場合はその場で新規案件を作って続けます">まとめて請求書を作成</button>
      </div>`
          : ""
      }
      ${
        open
          ? `<div class="cost-bkgrp-body"><table class="cost-table cost-table--global cost-bkgrp-table" data-fp-table="cost-alloc-ad">
        <thead><tr>
          <th scope="col">会計区分</th>
          <th scope="col">発注先</th>
          <th scope="col">媒体</th>
          <th scope="col">金額（税抜）</th>
          <th scope="col">計上月</th>
          <th scope="col" data-fp-fill>発注名</th>
          <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
        </tr></thead>
        <tbody>${rs
          .map((c) => {
            const noteDisplay = stripCostBucketMarkers(String(c.note || "").trim());
            const taxY = costConsumptionTaxYen(c);
            const exY = Number(c.amount || 0);
            const amtInner =
              taxY > 0
                ? `<span class="cost-amt-total">${yen.format(exY)}</span><div class="cost-tax-sub">消費税 ${yen.format(taxY)} · 税込 ${yen.format(exY + taxY)}</div>`
                : `<span class="cost-amt-total">${yen.format(exY)}</span>`;
            const srcParsed = adCsvNoteAccount(c.note);
            return `<tr class="cost-row-unassigned">
            <td class="cost-td-category"><span class="cost-category-badge">${escapeHtml(c.category)}</span></td>
            <td class="cost-vendor">${escapeHtml(c.vendor || "—")}</td>
            <td class="cost-bkgrp-td-group">${escapeHtml(srcParsed ? srcParsed.label : "—")}</td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
            <td class="cost-table-note-cell cost-table-note-cell--editable"><textarea class="cost-table-note-inline" rows="1" readonly title="ダブルクリックで編集" data-cost-inline-note="${escapeHtmlAttr(`${COST_UNASSIGNED_PROJECT_UI_ID}:${c.id}`)}" data-cost-inline-note-orig="${escapeHtmlAttr(noteDisplay)}" placeholder="発注名・メモ">${escapeHtml(noteDisplay)}</textarea></td>
            <td class="cost-actions"><button type="button" class="inline-btn ghost" data-edit-cost="${COST_UNASSIGNED_PROJECT_UI_ID}:${escapeHtmlAttr(c.id)}">編集</button></td>
          </tr>`;
          })
          .join("")}</tbody>
      </table></div>`
          : ""
      }
    </div>`;
}

/**
 * メニュー「原価管理」: 全案件の原価を一覧・編集・取り込み登録。
 * 支払期限・支払状況の列はこの一覧には含めない（編集フォームで管理）。
 */
function globalCostManagementSection() {
  restoreCostMgmtViewState(); // ログイン者ごとに最後の並び順・検索条件を戻す（15x）
  const { flatAll, scoped, filtered, tabKeys, mg } = getCostMgmtTableRows();
  pruneCostMgmtSelectionToValid();
  const sel = uiState.costMgmtSelected || {};
  const selCount = Object.keys(sel).filter((k) => sel[k]).length;
  const allVisKeys = filtered.map((c) => costMgmtSelectionKey(c._projectId, c.id));
  const allVisSelected = filtered.length > 0 && allVisKeys.length > 0 && allVisKeys.every((k) => sel[k]);
  const canBulkDelegate = userCanAccessPage("cost-management");
  // 支払日登録・削除は経理/マスター/管理者のみ（CS は原価の案件割当のみが役割のため対象外。api/cost-payments.js・api/costs.js と対）
  const isAccountingForCostMgmt = isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser);
  const selIncl =
    selCount > 0
      ? flatAll.reduce((sum, c) => (sel[costMgmtSelectionKey(c._projectId, c.id)] ? sum + costTotalRegisteredYen(c) : sum), 0)
      : 0;
  // 行ごとの受領・削除ボタンは無いので、未選択でも一括バーの位置に操作方法を出しておく（位置も固定されて表がずれない）
  const bulkBarHtml =
    flatAll.length === 0
      ? ""
      : selCount === 0
      ? `<div class="pj-bulk-bar cost-mgmt-bulk-bar cost-mgmt-bulk-bar--idle show">行にチェックを入れると、受領済・支払済（支払日）への変更・会計区分の変更・削除などをまとめて行えます</div>`
      : `<div class="pj-bulk-bar cost-mgmt-bulk-bar show">
          <span>${selCount}件選択中<span class="cost-mgmt-bulk-sum" title="選択中の支払額（税込）の合計">${yen.format(selIncl)}</span></span>
          <span class="sep" aria-hidden="true">|</span>
          <button type="button" class="inline-btn" data-cost-mgmt-open-bulk-modal="category">会計区分変更</button>
          ${
            canBulkDelegate
              ? `<span class="sep" aria-hidden="true">|</span><button type="button" class="inline-btn ghost" data-cost-mgmt-open-bulk-modal="delegate">割当依頼</button>`
              : ""
          }
          ${
            isAccountingForCostMgmt
              ? `<span class="sep" aria-hidden="true">|</span><button type="button" class="inline-btn ghost" data-cost-mgmt-open-bulk-modal="payStatus">支払ステータス変更</button>
                 <span class="pj-bulk-bar-end"><button type="button" class="inline-btn ghost cost-mgmt-bulk-del-bar" data-cost-mgmt-bulk-delete-bar="1">まとめて削除</button></span>`
              : ""
          }
        </div>`;
  // 詳細条件（支払方法〜各月範囲）は畳んでおき、表の表示領域を広く取る。畳んでいても条件は効いたまま（件数バッジで示す）
  const detailOpen = uiState.costMgmtFilterDetailOpen === true;
  const detailActiveN = ["paymentMethod", "lock", "vendorType", "costKind"].filter((k) => mg[k]).length +
    [["invFrom", "invTo"], ["payFrom", "payTo"], ["tmFrom", "tmTo"]].filter(([a, b]) => mg[a] || mg[b]).length;
  // 条件が1つでも入っていればリセットを強調する（案件一覧と同じ pj-filter-reset-active）
  const mgFilterActive = Object.values(mg).some((v) => String(v || "").trim()) || !(tabKeys.length === 1 && tabKeys[0] === "all");
  const mgSearchBarHtml = `
    <div class="cost-mgmt-search-bar pj-cost-mgmt-filter-row pj-filter-row--main">
      <div class="cost-mgmt-search-row cost-mgmt-search-row--top">
        <label class="cost-mgmt-search-field cost-mgmt-search-field--keyword">
          <span class="cost-mgmt-search-label">キーワード</span>
          <input class="pj-input" type="search" data-cost-mgmt-filter-keyword data-ime-search value="${escapeHtmlAttr(String(mg.keyword || ""))}" placeholder="案件・請求書No・備考など" autocomplete="off" />
        </label>
        <label class="cost-mgmt-search-field cost-mgmt-search-field--vendor">
          <span class="cost-mgmt-search-label">発注先</span>
          <select class="pj-input" data-cost-mgmt-filter-vendor>${costMgmtVendorFilterOptionsHtml(flatAll, mg.vendor)}</select>
        </label>
        ${costMgmtCategorySelectHtml(tabKeys)}
        <button type="button" class="inline-btn ghost cost-mgmt-detail-toggle" data-cost-mgmt-detail-toggle="1" aria-expanded="${detailOpen ? "true" : "false"}">
          詳細条件${detailActiveN > 0 ? `<span class="cost-mgmt-detail-count">${detailActiveN}</span>` : ""} ${detailOpen ? "▴" : "▾"}
        </button>
        ${/* リセットは詳細条件の右（以前は支払タブの行の右端で、検索欄から離れて見つけにくかった。2026-10 要望） */ ""}
        <button type="button" class="inline-btn ${mgFilterActive ? "pj-filter-reset-active" : "ghost"} cost-mgmt-filter-clear-all" data-cost-mgmt-filter-clear="1">リセット</button>
      </div>
      ${
        detailOpen
          ? `<div class="cost-mgmt-search-row cost-mgmt-search-row--top cost-mgmt-search-row--detail">
        <label class="cost-mgmt-search-field cost-mgmt-search-field--pay-status">
          <span class="cost-mgmt-search-label">支払方法</span>
          <select class="pj-input" data-search-dropdown-off="1" data-cost-mgmt-filter-pay-method>
            <option value="" ${!mg.paymentMethod ? "selected" : ""}>すべて</option>
            ${COST_PAYMENT_METHODS.map((m) => `<option value="${m}" ${mg.paymentMethod === m ? "selected" : ""}>${m}</option>`).join("")}
            <option value="未設定" ${mg.paymentMethod === "未設定" ? "selected" : ""}>未設定</option>
          </select>
        </label>
        <label class="cost-mgmt-search-field cost-mgmt-search-field--lock">
          <span class="cost-mgmt-search-label">ロック</span>
          <select class="pj-input" data-search-dropdown-off="1" data-cost-mgmt-filter-lock>
            <option value="" ${!mg.lock ? "selected" : ""}>すべて</option>
            <option value="locked" ${mg.lock === "locked" ? "selected" : ""}>ロック済のみ</option>
            <option value="unlocked" ${mg.lock === "unlocked" ? "selected" : ""}>未ロックのみ</option>
          </select>
        </label>
        <label class="cost-mgmt-search-field cost-mgmt-search-field--lock" title="パートナー/クライアントの敬称（様=個人）と支払先名を突合します。個人への支払いの源泉漏れチェック用">
          <span class="cost-mgmt-search-label">支払先区分</span>
          <select class="pj-input" data-search-dropdown-off="1" data-cost-mgmt-filter-vendor-type>
            <option value="" ${!mg.vendorType ? "selected" : ""}>すべて</option>
            <option value="individual" ${mg.vendorType === "individual" ? "selected" : ""}>個人のみ</option>
            <option value="corporate" ${mg.vendorType === "corporate" ? "selected" : ""}>法人・その他</option>
          </select>
        </label>
        <label class="cost-mgmt-search-field cost-mgmt-search-field--lock">
          <span class="cost-mgmt-search-label">原価区分</span>
          <select class="pj-input" data-search-dropdown-off="1" data-cost-mgmt-filter-cost-kind>
            <option value="" ${!mg.costKind ? "selected" : ""}>すべて</option>
            <option value="unassigned" ${mg.costKind === "unassigned" ? "selected" : ""}>未割当のみ</option>
            <option value="provisional" ${mg.costKind === "provisional" ? "selected" : ""}>仮原価のみ</option>
            <option value="salesExpense" ${mg.costKind === "salesExpense" ? "selected" : ""}>販管費のみ</option>
          </select>
        </label>
      </div>
      <div class="cost-mgmt-search-row cost-mgmt-search-row--months">
        ${[
          ["請求月", "請求日の月", "inv", mg.invFrom, mg.invTo],
          ["支払月", "支払期限の月", "pay", mg.payFrom, mg.payTo],
          ["計上月", "", "tm", mg.tmFrom, mg.tmTo]
        ]
          .map(
            ([label, title, key, from, to]) => `
        <label class="cost-mgmt-search-field cost-mgmt-search-field--range" title="${escapeHtmlAttr(title)}">
          <span class="cost-mgmt-search-label">${label}</span>
          <span class="cost-mgmt-range-row">
            <input class="pj-input pj-input-month" type="month" title="開始（含む）" data-show-picker="1" data-search-dropdown-off="1" data-cost-mgmt-filter-month data-cost-mgmt-filter-${key}-from value="${escapeHtmlAttr(String(from || ""))}" />
            <span class="cost-mgmt-range-sep" aria-hidden="true">〜</span>
            <input class="pj-input pj-input-month" type="month" title="終了（含む）" data-show-picker="1" data-search-dropdown-off="1" data-cost-mgmt-filter-month data-cost-mgmt-filter-${key}-to value="${escapeHtmlAttr(String(to || ""))}" />
          </span>
        </label>`
          )
          .join("")}
      </div>`
          : ""
      }
      <div class="cost-mgmt-search-row cost-mgmt-search-row--tabs">
        ${costMgmtPayStatusChipsHtml(flatAll, mg, tabKeys)}
      </div>
    </div>`;
  // 上部の未割当・調整・仮のお知らせバナーは廃止（未受領＝ピンクの行色で判別する運用に統一）
  const todayYmd = new Date().toLocaleDateString("sv-SE"); // 端末の日付で YYYY-MM-DD（支払期限切れの判定用）
  const rows =
    uiState.heavyDataLoading
      ? `<tr><td colspan="12" class="cost-empty cost-empty--heavy-load">${flowPilotNowLoadingBlock({
          inline: true,
          subtitle: "原価データを読み込んでいます"
        })}</td></tr>`
      : filtered.length === 0
      ? `<tr><td colspan="12" class="cost-empty">${flatAll.length === 0 ? "原価がまだ登録されていません" : "該当する原価がありません"}</td></tr>`
      : costMgmtPageRows(filtered)
          .map((c) => {
            const taxY = costConsumptionTaxYen(c);
            const pid = c._projectId;
            const pname = escapeHtml(c._projectName || "");
            const exYen = Number(c.amount || 0);
            const totalIncl = exYen + taxY;
            const whYen = Math.round(Number(c.withholdingTax || 0)) || 0;
            // 支払額（税込）だけを表示。税抜・消費税の内訳はホバーで確認できる
            const amtInner =
              `<span class="cost-amt-total" title="${escapeHtmlAttr(`税抜 ${yen.format(exYen)} · 消費税 ${yen.format(taxY)}`)}">${yen.format(totalIncl)}</span>` +
              (whYen > 0 ? `<div class="cost-tax-sub">源泉 ${yen.format(whYen)}</div>` : "");
            // 発注名は備考の1行目を出す（長い名前は折り返す・備考の全文はホバー）。押すと編集フォームを開く
            const noteDisplay = stripCostBucketMarkers(String(c.note || "").trim());
            const noteFirstLine = noteDisplay.split("\n")[0].trim();
            const noteHtml = `<button type="button" class="cost-mgmt-name-link${noteFirstLine ? "" : " cost-mgmt-name-link--empty"}" data-edit-cost="${pid}:${escapeHtmlAttr(c.id)}" title="${escapeHtmlAttr(noteDisplay || "クリックで編集")}">${escapeHtml(noteFirstLine || "（発注名なし）")}${costMemoMarkHtml(c)}</button>`;
            const rowKey = costMgmtSelectionKey(pid, c.id);
            const rowChecked = sel[rowKey] ? " checked" : "";
            // ③ 未割当の予想案件（記憶由来）を候補として提示（アシスタントが1クリックで割当）
            const suggestedUiId = isCostRowProjectUnassigned(c) ? costSuggestedProjectUiId(c) : "";
            const suggestedName = suggestedUiId ? (projects.find((pp) => pp.id === suggestedUiId)?.name || "") : "";
            // 候補がある未割当は「候補を見る」だけ出し、中身はモーダルで確認して割り当てる（15w）。
            // セルに候補名を並べると、割当済みの行と見た目が紛れるため
            const suggestHtml = suggestedName
              ? ` <button type="button" class="cost-suggest-open" data-cost-suggest-open="${pid}:${escapeHtmlAttr(c.id)}:${escapeHtmlAttr(suggestedUiId)}" title="${escapeHtmlAttr(`候補: ${suggestedName}`)}">候補を見る</button>`
              : "";
            // 行の操作は 編集・差戻 のみ（受領登録・削除などはチェックして一括バーから行う）
            const remandBtnHtml = !isAccountingForCostMgmt
              ? ""
              : c.remandNote
                ? `<button type="button" class="inline-btn ghost" data-cost-remand-resolve="${pid}:${escapeHtmlAttr(c.id)}" title="差し戻しを取り消します">差戻取消</button>`
                : projects.some((pp) => pp.id === pid) && costRemandApplicable(c)
                  ? `<button type="button" class="inline-btn ghost" data-cost-remand-open="${pid}:${escapeHtmlAttr(c.id)}" title="内容の相違を担当営業へ差し戻します">差戻</button>`
                  : "";
            const delegationRequested = isCostRowDelegationRequested(c);
            const delegateName =
              c.delegatedSalesUserDbId != null && Number.isFinite(Number(c.delegatedSalesUserDbId))
                ? getUserById(`u${c.delegatedSalesUserDbId}`)?.name || `ユーザー#${c.delegatedSalesUserDbId}`
                : "";
            const projTitle = `${c._projectName || ""}${delegationRequested && delegateName ? `（割当依頼: ${delegateName}）` : ""}`;
            // ロックは案件一覧と同じく行の先頭（No列の頭）に出す
            const lockHtml = isMonthLocked(c.targetMonth)
              ? `<span class="cost-row-locked-badge" title="計上月 ${escapeHtmlAttr(String(c.targetMonth || ""))} は締め済みのためロック中">🔒</span>`
              : "";
            const remandBadge = c.remandNote
              ? `<span class="cost-remand-badge" title="${escapeHtmlAttr(`差し戻し中: ${c.remandNote}`)}">差戻し中</span>`
              : "";
            const dbNo = costDbNumericId(c);
            const payLabel = costPayStatusDisplayLabel(c);
            // 支払済みは日付だけを済カラーで表示（ステータス文言＋日付の二段にしない）
            const payHtml = c.paidDate
              ? `<span class="cost-pay-status cs-paid">${escapeHtml(costDateShortDisplay(c.paidDate))}</span>`
              : `<span class="cost-pay-status ${costPayClass(payLabel)}">${escapeHtml(payLabel)}</span>`;
            const receiptGroup = costReceiptGroup(c);
            // 「支払」「発注」はセルを押して1クリックで変更（経理/マスター・月次ロック外の行。15v）
            const statusEditable = costInlineStatusEditable(c);
            // 支払期限切れ（未払いで期限が今日より前）は赤で目立たせる
            const dueOverdue = receiptGroup !== "paid" && /^\d{4}-\d{2}-\d{2}/.test(String(c.dueDate || "")) && String(c.dueDate).slice(0, 10) < todayYmd;
            return `
          <tr class="cost-row-recv--${receiptGroup}${delegationRequested ? " cost-row-unassigned cost-row-delegation-requested" : isCostRowProjectUnassigned(c) ? " cost-row-unassigned" : ""}${c.isProvisional ? " cost-row-provisional" : ""}">
            <td class="cost-mgmt-col-check">
              <input type="checkbox" class="cost-mgmt-row-check"${rowChecked} data-cost-mgmt-row-check="${escapeHtmlAttr(rowKey)}" aria-label="${escapeHtmlAttr(`選択: ${noteFirstLine || c.vendor || `原価 ${dbNo ?? ""}`}`)}" />
            </td>
            <td class="cost-mgmt-no-cell">${lockHtml}${dbNo != null ? dbNo : "—"}</td>
            <td class="cost-table-note-cell">${noteHtml}${remandBtnHtml ? `<span class="cost-sp-remand">${remandBtnHtml}</span>` : ""}</td>
            <td class="cost-vendor" title="${escapeHtmlAttr(c.vendor || "")}">${escapeHtml(c.vendor || "—")}</td>
            <td class="cost-table-project" title="${escapeHtmlAttr(projTitle)}">${pname}${remandBadge}${suggestHtml}</td>
            <td class="cost-mgmt-approval-cell">${costOrderApprovalCellHtml(c)}</td>
            <td class="cost-mgmt-tm-cell">${escapeHtml(String(c.targetMonth || "—"))}</td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-mgmt-due-cell">${escapeHtml(costDateShortDisplay(c.invoiceDate))}</td>
            <td class="cost-mgmt-due-cell${dueOverdue ? " cost-mgmt-due-cell--overdue" : ""}"${dueOverdue ? ' title="支払期限を過ぎています"' : ""}>${escapeHtml(costDateShortDisplay(c.dueDate))}</td>
            <td class="cost-mgmt-pay-cell">${
              statusEditable
                ? `<button type="button" class="cost-status-cell-btn" data-cost-pay-pick="${pid}:${escapeHtmlAttr(c.id)}" title="クリックで支払状況を変更" aria-haspopup="menu">${payHtml}</button>`
                : payHtml
            }</td>
            <td class="cost-mgmt-order-cell">${
              statusEditable
                ? `<button type="button" class="cost-status-cell-btn" data-cost-order-pick="${pid}:${escapeHtmlAttr(c.id)}" title="クリックで発注ステータスを変更" aria-haspopup="menu">${escapeHtml(costOrderStatusDisplayLabel(c) || "—")}</button>`
                : escapeHtml(costOrderStatusDisplayLabel(c) || "—")
            }</td>
            <td class="cost-actions">
              <button type="button" class="inline-btn ghost" data-edit-cost="${pid}:${c.id}">編集</button>
              <span class="cost-remand-slot">${remandBtnHtml}</span>
            </td>
          </tr>`;
          })
          .join("");

  // 合計表示: 絞り込み結果全体（全ページ）。選択中の合計は一括バー側に出す（チェックで合計行の高さが変わらないように）
  const totalsEx = filtered.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalsIncl = filtered.reduce((s, c) => s + Number(c.amount || 0) + costConsumptionTaxYen(c), 0);
  const totalsBarHtml = `
    <div class="cost-mgmt-totals">
      <span>絞り込み結果 <strong>${filtered.length}件</strong> ・ 税抜 <strong>${yen.format(totalsEx)}</strong> ・ 税込 <strong>${yen.format(totalsIncl)}</strong></span>
      <span class="cost-mgmt-legend" aria-label="行の色"><span class="cost-mgmt-legend-sw cost-mgmt-legend-sw--pending"></span>未受領<span class="cost-mgmt-legend-sw cost-mgmt-legend-sw--received"></span>受領済<span class="cost-mgmt-legend-sw cost-mgmt-legend-sw--paid"></span>支払済</span>
    </div>`;

  const srt = uiState.costMgmtSort || { key: "targetMonth", dir: "desc" };
  const sortMark = (k) => srt.key === k ? (srt.dir === "asc" ? " ▲" : " ▼") : "";
  const sortCls = (k) => srt.key === k ? " cost-sort-active" : "";
  const rh = (col) => `<span class="cost-col-resize-handle" data-cost-col-resize="${col}" aria-hidden="true"></span>`;
  const cw = (col) => `style="width:${getCostMgmtColWidth(col)}px"`;
  // 発注名の col は幅指定なし＝余った幅を全部受け取る。表の最小幅を全列の合計にして発注名の最小幅を保つ（style.css）
  const tableMinWidth = 34 + Object.keys(COST_MGMT_COL_DEFAULTS).reduce((sum, col) => sum + getCostMgmtColWidth(col), 0);

  const sectionHtml = `
      <div class="pj-filter-area">
      ${mgSearchBarHtml}
      </div>
      ${bulkBarHtml}
      ${totalsBarHtml}
      <div class="pj-table-area fp-grid-scroll">
        <table class="cost-table cost-table--global cost-table--mgmt cost-table--resizable" data-fp-table="cost-mgmt" data-fp-table-own-widths style="min-width:${tableMinWidth}px">
          <colgroup>
            <col data-cost-col="check" style="width:34px">
            <col data-cost-col="no" ${cw("no")}>
            <col data-cost-col="name">
            <col data-cost-col="vendor" ${cw("vendor")}>
            <col data-cost-col="project" ${cw("project")}>
            <col data-cost-col="approval" ${cw("approval")}>
            <col data-cost-col="tm" ${cw("tm")}>
            <col data-cost-col="amount" ${cw("amount")}>
            <col data-cost-col="invoice" ${cw("invoice")}>
            <col data-cost-col="due" ${cw("due")}>
            <col data-cost-col="pay" ${cw("pay")}>
            <col data-cost-col="order" ${cw("order")}>
            <col data-cost-col="actions" ${cw("actions")}>
          </colgroup>
          <thead>
            <tr>
              <th class="cost-mgmt-col-check" scope="col">
                <input type="checkbox" class="cost-mgmt-row-check cost-mgmt-check-all"${allVisSelected ? " checked" : ""} data-cost-mgmt-check-all="1" title="絞り込み結果をすべて選択（全ページ分）" aria-label="絞り込み結果をすべて選択（全ページ分）" />
              </th>
              <th scope="col" class="cost-th-sortable${sortCls("no")}" data-cost-sort="no">No${sortMark("no")}${rh("no")}</th>
              <th scope="col">発注名${rh("name")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("vendor")}" data-cost-sort="vendor">発注先${sortMark("vendor")}${rh("vendor")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("project")}" data-cost-sort="project">計上案件${sortMark("project")}${rh("project")}</th>
              <th scope="col" class="cost-mgmt-th-center" title="発注申請の承認（済＝承認済・未＝申請中／却下／申請なし・ー＝申請の対象外）">発注承認${rh("approval")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("targetMonth")}" data-cost-sort="targetMonth">計上月${sortMark("targetMonth")}${rh("tm")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("amount")}" data-cost-sort="amount">支払額（税込）${sortMark("amount")}${rh("amount")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("invoiceDate")}" data-cost-sort="invoiceDate">請求日${sortMark("invoiceDate")}${rh("invoice")}</th>
              <th scope="col" class="cost-th-sortable${sortCls("dueDate")}" data-cost-sort="dueDate">支払期限${sortMark("dueDate")}${rh("due")}</th>
              <th scope="col" class="cost-th-sortable cost-mgmt-th-center${sortCls("payStatus")}" data-cost-sort="payStatus">支払${sortMark("payStatus")}${rh("pay")}</th>
              <th scope="col" class="cost-th-sortable cost-mgmt-th-center${sortCls("orderStatus")}" data-cost-sort="orderStatus">発注${sortMark("orderStatus")}${rh("order")}</th>
              <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${costSuggestModalHtml()}
      ${costMgmtPaginationHtml(filtered.length)}`;
  // ページ番号は上のページ送り描画で件数に合わせて補正されるので、その後に保存する（15x）
  saveCostMgmtViewState();
  return sectionHtml;
}

function costsPage() {
  const route = currentRoute();
  if (route.page === "cost-allocation") {
    const csView = userSeesCostAllocationCsView();
    if (csView) {
      const isMaster = costReviewIsMasterView();
      if (isMaster) {
        return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>原価割当</h2>
            <p class="pj-page-lede">「未割当」で案件への割当作業ができます。担当者名・「販管費・調整用」を選ぶと割当済み原価を照会できます。</p>
          </div>
        </div>
        ${costReviewSectionHtml()}
      </div>
    </div>`;
      }
      return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>原価割当</h2>
            <p class="pj-page-lede">未割当の原価を案件へ割り当てます。営業からの異議（差し戻し）もここに届きます。</p>
          </div>
        </div>
        ${costAllocationSectionHtml()}
      </div>
    </div>`;
    }
    // 営業ビュー: 自分の案件の原価を全部照会＋異議申請。自分宛の割当依頼が残っていればその処理も先頭に出す
    // 原価の登録は案件詳細と同じ「＋ 原価を追加」フォーム（制作費・開発費は発注申請）。まとめて入力も全区分（制作費・開発費は行ごとに発注申請）
    // （2026-10 ユーザー決定: 営業が原価を入れる形をそろえる。以前の「請求書から原価を登録」＝営業の請求書取込は廃止）
    const delegatedN = costAllocationDelegatedQueueRows().length;
    return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>原価割当</h2>
            <p class="pj-page-lede">自分の案件の原価の照会・登録画面です。制作費・開発費は外注先に頼む前に発注申請してください。内容が違うものは「異議」から差し戻してください。</p>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            <button type="button" class="inline-btn ghost pj-head-action-btn" data-open-cost-bulk-entry>＋ 原価をまとめて入力</button>
            <button type="button" class="inline-btn pj-head-action-btn" data-add-provisional-cost="">＋ 原価を追加</button>
          </div>
        </div>
        ${costImportPendingRestoreBannerHtml()}
        ${delegatedN > 0 ? costAllocationSectionHtml() : ""}
        ${costReviewSectionHtml()}
      </div>
    </div>`;
  }
  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>原価一覧</h2>
          </div>
          ${
            isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser)
              ? `<div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            <button type="button" class="inline-btn ghost pj-head-action-btn" data-cost-mgmt-csv="1" title="絞り込み結果（全ページ分）をCSVでダウンロードします">CSV出力</button>
            <a class="inline-btn ghost pj-head-action-btn" href="#cost-reconcile" title="請求書と登録済み原価を突き合わせて月末チェックします">照合</a>
            ${monthLockButtonHtml()}
            ${remBillingSettingsButtonHtml()}
            <button type="button" class="inline-btn pj-head-action-btn" data-open-cost-mgmt-register="other">＋ 原価を登録</button>
          </div>`
              : ""
          }
        </div>
    ${costImportPendingRestoreBannerHtml()}
    ${globalCostManagementSection()}
      </div>
    </div>
  `;
}

function renderOrderFlowPromptModal() {
  if (!uiState.orderFlowPromptProjectId) return "";
  const p = projects.find((x) => x.id === uiState.orderFlowPromptProjectId);
  if (!p) return "";
  return `
    <div class="composer-backdrop" data-close-order-flow-prompt="1"></div>
    <aside class="composer-panel project-create-panel">
      <div class="composer-head">
        <div><h3>請書 / 発注書の利用確認</h3><p>${escapeHtml(p.name)}</p></div>
        <button class="inline-btn ghost" data-close-order-flow-prompt="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="prompt-copy">この案件で請書 / 発注書を使用しますか？</p>
        <div class="create-mode-grid">
          <button class="mode-card" data-order-flow-choice="use:${p.id}">
            <strong>使用する</strong>
            <span>請書 / 発注ステージをフローに含めます</span>
          </button>
          <button class="mode-card" data-order-flow-choice="skip:${p.id}">
            <strong>使用しない</strong>
            <span>通常フロー（請求ステージへ）で進めます</span>
          </button>
        </div>
      </div>
    </aside>
  `;
}

/** 取込設定モーダルのドラフトを保存する（経理/マスターのみ操作可） */
async function submitRemBillingSettings() {
  const m = uiState.remBillingSettingsModal;
  if (!m || m.saving) return;
  m.saving = true;
  render();
  const res = await apiFetch("/api/rem-billing-settings", {
    method: "POST",
    body: { enabled: m.enabled, targetBusinessDay: m.targetBusinessDay }
  });
  if (!res.ok) {
    m.saving = false;
    showToast(typeof res.data?.error === "string" ? res.data.error : "取込設定の変更に失敗しました", { variant: "error" });
    render();
    return;
  }
  remBillingSettingsState = {
    loaded: true,
    loading: false,
    enabled: res.data.enabled !== false,
    targetBusinessDay: Number(res.data.targetBusinessDay) || 4
  };
  uiState.remBillingSettingsModal = null;
  showToast("取込設定を保存しました");
  render();
}

/** 「今すぐ取り込む」: 選択月を営業日判定を無視して即実行する（経理/マスターのみ操作可） */
async function submitRemBillingManualImport() {
  const m = uiState.remBillingSettingsModal;
  if (!m || m.importing) return;
  if (!m.importMonth) {
    showToast("対象月を選択してください", { variant: "error" });
    return;
  }
  m.importing = true;
  m.importResult = null;
  render();
  const res = await apiFetch(`/api/rem-billing-import?month=${encodeURIComponent(m.importMonth)}`);
  m.importing = false;
  if (!res.ok) {
    // 経路によりエラー本文の形が異なる: 権限/設定系は{error}、REM API側の失敗（月未生成等）はrunImportの{message}
    const message =
      (typeof res.data?.error === "string" && res.data.error) ||
      (typeof res.data?.message === "string" && res.data.message) ||
      "取込に失敗しました";
    m.importResult = { ok: false, message };
    showToast(message, { variant: "error" });
    render();
    return;
  }
  m.importResult = { ok: true, ...res.data };
  showToast(`取込完了: 対象${res.data.totalRows}件・自動割当${res.data.autoAssigned}件`);
  render();
}

// ── イベント（独立リスナー・原価登録ページと原価照合ページ） ──
document.addEventListener("click", (e) => {
  const _mlPage = currentRoute().page;
  if (_mlPage !== "cost-management" && _mlPage !== "cost-reconcile") return;
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  if (t.closest("[data-month-lock-open]")) {
    uiState.monthLockModal = { ym: remBillingDefaultImportMonth(), saving: false };
    render();
    return;
  }
  const mlCloseEl = t.closest("[data-month-lock-close]");
  if (mlCloseEl) {
    // overlay自身がclose属性を持つため、モーダル内のクリックは閉じ扱いにしない（16の解除申請モーダルと同パターン）
    const insideDialog = mlCloseEl.classList.contains("cl-mini-modal-overlay") && t.closest(".cl-mini-modal");
    if (!insideDialog) {
      if (uiState.monthLockModal?.saving) return;
      uiState.monthLockModal = null;
      render();
      return;
    }
  }
  if (t.closest("[data-month-lock-toggle]")) {
    void submitMonthLockToggle();
    return;
  }
  if (t.closest("[data-rem-billing-settings-open]")) {
    uiState.remBillingSettingsModal = {
      enabled: remBillingSettingsState.enabled,
      targetBusinessDay: remBillingSettingsState.targetBusinessDay,
      saving: false,
      importMonth: remBillingDefaultImportMonth(),
      importing: false,
      importResult: null
    };
    render();
    return;
  }
  const remCloseEl = t.closest("[data-rem-billing-settings-close]");
  if (remCloseEl) {
    // overlay自身がclose属性を持つため、モーダル内のクリックは閉じ扱いにしない。
    // このガードが無いと「保存」「今すぐ取り込む」もclosestがoverlayに当たって発火せず閉じるだけになる
    const insideDialog = remCloseEl.classList.contains("cl-mini-modal-overlay") && t.closest(".cl-mini-modal");
    if (!insideDialog) {
      if (uiState.remBillingSettingsModal?.saving || uiState.remBillingSettingsModal?.importing) return;
      uiState.remBillingSettingsModal = null;
      render();
      return;
    }
  }
  if (t.closest("[data-rem-billing-settings-save]")) {
    void submitRemBillingSettings();
    return;
  }
  if (t.closest("[data-rem-billing-import-now]")) {
    void submitRemBillingManualImport();
  }
});

document.addEventListener("change", (e) => {
  const _mlChangePage = currentRoute().page;
  if (_mlChangePage !== "cost-management" && _mlChangePage !== "cost-reconcile") return;
  const t = e.target;
  if (t instanceof HTMLInputElement && t.hasAttribute("data-month-lock-month") && uiState.monthLockModal) {
    uiState.monthLockModal.ym = t.value || "";
    render(); // 選んだ月のロック状態表示とボタンラベルを更新する
    return;
  }
  const m = uiState.remBillingSettingsModal;
  if (!m) return;
  if (t instanceof HTMLInputElement && t.hasAttribute("data-rem-billing-settings-enabled")) {
    m.enabled = t.checked;
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-rem-billing-settings-day")) {
    const v = Math.round(Number(t.value));
    m.targetBusinessDay = Number.isFinite(v) ? Math.min(10, Math.max(1, v)) : m.targetBusinessDay;
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-rem-billing-import-month")) {
    m.importMonth = t.value || remBillingDefaultImportMonth();
  }
});

// ────────────────────────────────────────────────────────────────
// 折りたたみ状態ヘルパー
// defaultCollapsed=true → 初回は閉じた状態
// ────────────────────────────────────────────────────────────────
function isSectionCollapsed(id, defaultCollapsed = true) {
  if (id in uiState.collapsedSections) return uiState.collapsedSections[id];
  return defaultCollapsed;
}

// ────────────────────────────────────────────────────────────────
// projectToTasks: 案件データ → 「今やること」リストに変換
// 原価・請求・入金をバラバラに見せず、優先度付きタスクとして統合する
// ────────────────────────────────────────────────────────────────
function projectToTasks(p) {
  const tasks = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function daysUntil(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.ceil((d - today) / 86400000);
  }

  function datePriority(dateStr) {
    const days = daysUntil(dateStr);
    if (days === null) return "caution";
    if (days <= 3) return "urgent";
    if (days <= 10) return "caution";
    return "info";
  }

  const salesPfx = "営業対応：";
  const acctPfx  = "経理対応：";

  // ── 1. 原価タスク（未発注・支払待ち） ──
  (p.costs || []).forEach((c) => {
    if (c.payStatus === "未発注") {
      tasks.push({
        id: `cost-order-${c.id}`,
        priority: datePriority(c.dueDate),
        label: `${salesPfx}${c.vendor || c.category} を発注する`,
        detail: `${c.category} ／ ${yen.format(c.amount)}${c.dueDate ? ` ／ 期限 ${c.dueDate}` : ""}`,
        action: { label: "発注済みにする", attr: `data-cost-status="${p.id}:${c.id}:発注済み"` },
        sub: { label: "編集", attr: `data-edit-cost="${p.id}:${c.id}"` }
      });
    } else if (c.payStatus === "支払待ち") {
      const days = daysUntil(c.dueDate);
      const pri = days !== null && days <= 3 ? "urgent"
                : days !== null && days <= 10 ? "caution"
                : "info";
      const daysText = days !== null ? `（あと${days}日）` : "";
      tasks.push({
        id: `cost-pay-${c.id}`,
        priority: pri,
        label: `${salesPfx}${c.vendor || c.category} の支払いを記録する`,
        detail: `${c.category} ／ ${yen.format(c.amount)}${c.dueDate ? ` ／ 期限 ${c.dueDate}${daysText}` : ""}`,
        action: { label: "支払い完了を登録する", attr: `data-cost-status="${p.id}:${c.id}:支払済み"` },
        sub: { label: "編集", attr: `data-edit-cost="${p.id}:${c.id}"` }
      });
    }
  });

  // ── 2. 差し戻し中 ──
  if (p.handoffStatus === "差し戻し中") {
    tasks.push({
      id: "handoff-return",
      priority: "urgent",
      label: `${salesPfx}差し戻し内容を確認・修正する`,
      detail: p.handoffNote || "経理から差し戻しがあります",
      action: { label: "請求書を修正する", attr: `data-compose="invoice" data-project="${p.id}"` }
    });
  }

  // ── 3. 入金確認（経理担当時） ──
  if (p.handoffStatus === "経理確認待ち" && p.docs.payment !== "入金確認済み") {
    const canPay = canConfirmPayment(p);
    tasks.push({
      id: "payment-confirm",
      priority: "urgent",
      label: `${acctPfx}入金確認を記録する`,
      detail: canPay ? `${yen.format(p.amount)} の入金待ち` : "経理が入金確認を行います",
      action: canPay ? { label: "入金記録を入力する", attr: `data-open-payment-form="${p.id}"` } : null,
      sub: { label: "営業へ差し戻す", attr: `data-handoff="to-sales:${p.id}"` }
    });
  }

  // ── 4. 請求書タスク ──
  if (p.invoiceStatus !== "入金確認済み") {
    const cm = currentMonth();
    const bm = p.billingMonth ? Number(p.billingMonth.replace("/", "")) : null;
    const cmNum = Number(cm.replace("/", ""));
    const billingOverdue = bm !== null && bm <= cmNum;

    if (p.invoiceStatus === "送付準備OK") {
      tasks.push({
        id: "invoice-send",
        priority: "urgent",
        label: `${salesPfx}請求書を送付する`,
        detail: `${yen.format(p.amount)} ／ 送付準備完了`,
        action: { label: "送付済みにする", attr: `data-set-doc="invoice:sent:${p.id}"` }
      });
    } else if ((p.invoiceStatus === "送付済み" || p.invoiceStatus === "入金待ち") && p.handoffStatus === "未引き継ぎ") {
      tasks.push({
        id: "invoice-handoff",
        priority: "caution",
        label: `${salesPfx}経理へ引き継ぐ`,
        detail: "請求書送付済み・入金確認が必要",
        action: { label: "経理へ引き継ぐ", attr: `data-handoff="to-accounting:${p.id}"` }
      });
    } else if (p.invoiceStatus === "下書きあり") {
      tasks.push({
        id: "invoice-edit",
        priority: billingOverdue ? "urgent" : "caution",
        label: `${salesPfx}請求書を編集・確定する`,
        detail: p.billingMonth ? `請求月: ${p.billingMonth}` : "請求月: 未設定",
        action: { label: "請求書を編集する", attr: `data-compose="invoice" data-project="${p.id}"` }
      });
    } else if (p.invoiceStatus === "未作成" && (p.currentStage === "invoices" || billingOverdue)) {
      tasks.push({
        id: "invoice-create",
        priority: billingOverdue ? "urgent" : "caution",
        label: `${salesPfx}請求書を作成する`,
        detail: `${p.billingMonth ? `請求月: ${p.billingMonth}` : "請求月: 未設定"} ／ ${yen.format(p.amount)}`,
        action: { label: "請求書を作成する", attr: `data-compose="invoice" data-project="${p.id}"` }
      });
    }
  }

  // 優先度順にソート（同優先度内は追加順を維持）
  const priOrder = { urgent: 0, caution: 1, info: 2 };
  tasks.sort((a, b) => priOrder[a.priority] - priOrder[b.priority]);
  return tasks;
}

// ────────────────────────────────────────────────────────────────
// 原価一覧（要対応原価 + 全原価一覧折りたたみ）
// ────────────────────────────────────────────────────────────────
function buildCostsCollapseSection(p) {
  const costs = p.costs || [];
  const actionCosts = costs.filter(isCostActionNeeded);

  function costRow(c) {
    const needsAction = isCostActionNeeded(c);
    const noteRaw = String(c.note || "").trim();
    const noteHtml = noteRaw
      ? noteRaw.length > 120
        ? `<span class="cost-table-note" title="${escapeHtmlAttr(noteRaw)}">${escapeHtml(noteRaw.slice(0, 120))}…</span>`
        : `<span class="cost-table-note">${escapeHtml(noteRaw)}</span>`
      : "—";
    const memoMark = costMemoMarkHtml(c);
    return `
      <tr class="${needsAction ? "cost-row-action" : ""}">
        <td><span class="cost-category-badge">${escapeHtml(String(c.category || "その他"))}</span></td>
        <td class="cost-vendor">${escapeHtml(String(c.vendor || "—"))}</td>
        <td>${escapeHtml(String(c.targetMonth || "—"))}</td>
        <td class="cost-detail-note-cell">${noteHtml}${memoMark}</td>
        <td class="cost-amount">${yen.format(c.amount)}${costConsumptionTaxYen(c) ? `<span class="cost-tax-sub">　税 ${yen.format(costConsumptionTaxYen(c))}</span>` : ""}</td>
        <td class="cost-actions">
          <button class="inline-btn ghost" data-edit-cost="${p.id}:${c.id}">編集</button>
          <button class="inline-btn ghost" data-delete-cost="${p.id}:${c.id}">削除</button>
        </td>
      </tr>
    `;
  }

  const tableHead = `<tr><th>会計区分</th><th>発注先</th><th>計上月</th><th>発注名</th><th>金額（税抜）</th><th></th></tr>`;

  // ── 要対応原価（常に表示） ──
  const actionSection = actionCosts.length > 0 ? `
    <div class="soft-card" style="margin-top: 12px; border-left: 3px solid #f59e0b;">
      <div style="padding: 8px 12px 6px; font-size: 12px; font-weight: 600; color: #b45309; letter-spacing: 0.03em;">要対応原価 ${actionCosts.length}件</div>
      <table class="cost-table cost-table--project-readonly">
        <thead>${tableHead}</thead>
        <tbody>${actionCosts.map(costRow).join("")}</tbody>
      </table>
    </div>
  ` : "";

  // ── 全原価一覧（要対応分を除外して表示） ──
  const otherCosts = costs.filter((c) => !isCostActionNeeded(c));
  const allRows = costs.length === 0
    ? `<tr><td colspan="6" class="cost-empty">原価がまだ登録されていません</td></tr>`
    : otherCosts.length === 0
      ? `<tr><td colspan="6" class="cost-empty">すべての原価は上の「要対応」に表示されています</td></tr>`
      : otherCosts.map(costRow).join("");

  const allCostsTable = `
    <div class="soft-card" style="margin-top: 8px;">
      <table class="cost-table cost-table--project-readonly">
        <thead>${tableHead}</thead>
        <tbody>${allRows}</tbody>
      </table>
    </div>
  `;

  return `
    <section class="section">
      <div class="collapse-header">
        <h3 class="section-title">外注・原価管理 <span class="section-badge" style="background:#6b7280;">${costs.length}</span></h3>
        <div style="display:flex;gap:8px;">
          <button class="inline-btn ghost" data-print-costs="${p.id}">原価一覧 PDF</button>
        </div>
      </div>
      ${actionSection}
      ${allCostsTable}
    </section>
  `;
}

// ────────────────────────────────────────────────────────────────
// 請求・入金情報（折りたたみ）
// ────────────────────────────────────────────────────────────────
function buildBillingSection(p) {
  const readOnly = !canEditInvoice(p);
  const invoiceActionLabel = docActionLabel(p, "invoice");

  const contentHtml = `
    <div class="soft-card panel-pad" style="margin-top: 12px;">
      <div class="setting-item"><span>請求状態</span><span>${escapeHtml(p.invoiceStatus)}</span></div>
      <div class="setting-item"><span>請求月</span><span>${escapeHtml(p.billingMonth || "未設定")}</span></div>
      <div class="setting-item"><span>引き継ぎ状態</span><span>${escapeHtml(p.handoffStatus)}</span></div>
      ${p.handoffAt ? `<div class="setting-item"><span>引き継ぎ日</span><span>${escapeHtml(p.handoffAt)}</span></div>` : ""}
      ${p.handoffNote ? `<div class="setting-item"><span>引き継ぎメモ</span><span>${escapeHtml(p.handoffNote)}</span></div>` : ""}
      <div class="setting-item"><span>入金状態</span><span>${escapeHtml(p.docs.payment)}</span></div>
      ${readOnly ? `<div class="setting-item"><span>編集権限</span><span>${readOnlyHint(p)}</span></div>` : ""}
    </div>
    <div class="hero-actions" style="margin-top: 10px;">
      ${(p.invoiceStatus === "未作成" || hasExistingDoc(p, "invoice")) ? `<button class="inline-btn ghost" data-compose="invoice" data-project="${p.id}" ${readOnly ? "disabled" : ""}>${invoiceActionLabel}</button>` : ""}
      ${p.savedDocs?.invoice ? `<button class="inline-btn ghost" data-print-saved="invoice:${p.id}">請求書 PDF出力</button>` : ""}
      ${readOnly ? `<span class="readonly-note">閲覧専用</span>` : ""}
    </div>
  `;

  return `
    <section class="section">
      <div class="detail-section-head">
        <h3 class="section-title">請求・入金情報</h3>
      </div>
      ${contentHtml}
    </section>
  `;
}

// ────────────────────────────────────────────────────────────────
// 案件詳細・契約情報（折りたたみ）
// ────────────────────────────────────────────────────────────────
function buildProjectSection(p) {
  const stage = detectStage(p);
  const stepOrder = p.useOrderFlow
    ? ["proposal", "contracts", "quotes", "orders", "invoices", "payments", "done"]
    : ["proposal", "contracts", "quotes", "invoices", "payments", "done"];
  const currentIdx = Math.max(1, stepOrder.indexOf(p.currentStage || stage));
  const stepNames = p.useOrderFlow
    ? ["提案", "契約", "見積", "請書/発注", "請求", "入金", "完了"]
    : ["提案", "契約", "見積", "請求", "入金", "完了"];
  const steps = stepNames
    .map((name, i) => {
      if (i < currentIdx) return `<div class="step done">${name}</div>`;
      if (i === currentIdx) return `<div class="step current">${name}</div>`;
      return `<div class="step">${name}</div>`;
    })
    .join("");

  const contentHtml = `
    <div style="margin-top: 4px;">
      <div class="detail-steps">${steps}</div>
    </div>
    <div class="soft-card panel-pad" style="margin-top: 12px;">
      <div class="setting-item"><span>クライアント</span><span>${escapeHtml(clientName(p))}</span></div>
      <div class="setting-item"><span>営業担当</span><span>${escapeHtml(ownerWithRole(salesOwnerUser(p)))}</span></div>
      <div class="setting-item"><span>経理担当</span><span>${escapeHtml(ownerWithRole(accountingOwnerUser(p)))}</span></div>
      <div class="setting-item"><span>現在担当</span><span>${escapeHtml(p.currentOwnerRole)}</span></div>
      <div class="setting-item"><span>現在のフェーズ</span><span>${escapeHtml(STAGE_NAME[stage] || stage)}</span></div>
      <div class="setting-item"><span>売上金額</span><span>${yen.format(p.amount)}</span></div>
      <div class="setting-item"><span>見積状態</span><span>${escapeHtml(p.docs.quote)}</span></div>
      <div class="setting-item"><span>契約状態</span><span>${escapeHtml(p.docs.contract)}</span></div>
      <div class="setting-item"><span>請書 / 発注フロー</span>
        <span>${p.useOrderFlow ? "使用する" : "使用しない"}
          <button class="inline-btn ghost" style="margin-left:8px;" data-toggle-order-flow="${p.id}" ${isReadOnlyProject(p) ? "disabled" : ""}>${p.useOrderFlow ? "使用しないに変更" : "使用するに変更"}</button>
        </span>
      </div>
    </div>
    <div class="hero-actions" style="margin-top: 10px;">
      <button class="inline-btn ghost" data-compose="quote" data-project="${p.id}" ${!canEditQuote(p) ? "disabled" : ""}>${docActionLabel(p, "quote")}</button>
      ${p.savedDocs?.quote ? `<button class="inline-btn ghost" data-print-saved="quote:${p.id}">見積 PDF出力</button>` : ""}${!canEditQuote(p) ? `<span class="readonly-note">閲覧専用</span>` : ""}
    </div>
  `;

  return `
    <section class="section">
      <div class="detail-section-head">
        <h3 class="section-title">案件詳細・契約情報</h3>
      </div>
      ${contentHtml}
    </section>
  `;
}

/** 案件詳細の NDA／業務委託の未締結バナー用。内部の「未作成」と空はユーザー向けに「未締結」で統一する */
function detailDocConclusionAlertLabel(status) {
  const s = String(status ?? "").trim();
  if (!s || s === "未作成") return "未締結";
  return s;
}

/** 案件詳細ヘッダーの案件名（鉛筆で編集） */
function projectDetailPageHeadTitleHtml(p) {
  const pid = escapeHtmlAttr(p.id);
  const v = escapeHtmlAttr(p.name || "");
  const escName = escapeHtml(projectDisplayName(p));
  if (uiState.editingProjectNameId === p.id) {
    return `<span class="detail-head-title-edit-row"><span class="detail-head-title-edit-wrap"><input type="text" class="detail-head-title-input" data-project-detail-name-input="${pid}" value="${v}" autocomplete="off" /></span><button type="button" class="detail-head-title-btn detail-head-title-btn--save" data-project-detail-name-save="${pid}" aria-label="案件名を確定">確定</button><button type="button" class="detail-head-title-btn detail-head-title-btn--cancel" data-project-detail-name-cancel="${pid}" aria-label="編集をやめる">キャンセル</button></span>`;
  }
  return `<span class="detail-head-title-row"><span class="detail-head-title-text">${escName}</span><button type="button" class="detail-head-title-edit" data-project-detail-name-edit="${pid}" aria-label="案件名を編集"><span aria-hidden="true">✎</span></button></span>`;
}

// ────────────────────────────────────────────────────────────────
// detailPage: 案件詳細画面
// 「判断 → 行動 → 詳細確認」の順で使えるUIを構成する
// ────────────────────────────────────────────────────────────────
/** #project-detail の id（p123 / 123 / dbId）から案件を解決 */
function resolveProjectByRouteId(routeId) {
  const raw = String(routeId ?? "").trim();
  if (!raw) return null;
  const exact = projects.find((x) => String(x.id) === raw);
  if (exact) return exact;
  if (/^\d+$/.test(raw)) {
    const byUi = projects.find((x) => String(x.id) === `p${raw}`);
    if (byUi) return byUi;
    const n = Number(raw);
    if (Number.isFinite(n)) {
      return projects.find((x) => Number(x.dbId) === n) || null;
    }
  }
  return null;
}

function detailPageLoadingHtml(title = "案件詳細") {
  return `
    <header class="page-head">
      <div><h2>${escapeHtml(title)}</h2></div>
      <div class="head-tools"><button class="chip-btn" type="button" data-go="projects">← 一覧に戻る</button></div>
    </header>
    <section class="section"><article class="soft-card panel-pad section-card">${flowPilotNowLoadingBlock({
      subtitle: "案件情報を読み込んでいます"
    })}</article></section>`;
}

/** 案件が見つからないページ（読込中なら読込中表示）。案件詳細・書類画面で共有。
 *  別の案件（一覧の先頭など）で代用しない（その案件の書類が表示され、編集がその案件に保存されてしまう） */
function projectMissingPageHtml(title = "案件詳細") {
  if (uiState.auth.isLoading || uiState.heavyDataLoading) return detailPageLoadingHtml(title);
  return `
    <header class="page-head">
      <div><h2>${escapeHtml(title)}</h2></div>
      <div class="head-tools"><button class="chip-btn" type="button" data-go="projects">← 一覧に戻る</button></div>
    </header>
    <section class="section"><p class="empty-note">案件が見つかりません。一覧から案件を開き直してください。</p></section>`;
}

/**
 * サーバーが空で、この端末にだけ請求項目がある案件を「未保存」として扱う（render から呼ぶ）。
 * 以前はここで自動的にサーバーへ送っていたが、案件詳細は「保存」ボタンで確定する方式にしたため（2026-10）、
 * 送らずに未保存表示を出し、保存ボタンで送る。
 */
function markLocalOnlyProjectBillingLinesUnsaved(p) {
  if (!p?.dbId || uiState.heavyDataLoading) return;
  const pid = String(p.id);
  const snap = uiState.projectBillingLinesServerSnapshot?.[pid];
  if (snap && snap.lineCount > 0) return; // サーバーに既にデータあり
  if (snap?.conflict) return; // 他の人の変更と競合（409）済み（再読み込みで解除）
  const lines = uiState.projectDocsDrafts[pid]?.lines;
  if (!Array.isArray(lines) || !lines.some((l) => l && String(l.item || "").trim().length > 0)) return;
  uiState.projectDocsDraftDirty[pid] = true;
  uiState.projectBillingManualDirty[pid] = true;
}

function detailPage(id) {
  const p = resolveProjectByRouteId(id);
  if (!p) return projectMissingPageHtml("案件詳細");
  // 端末にだけある請求項目は未保存として出す（保存ボタンで送る）
  markLocalOnlyProjectBillingLinesUnsaved(p);
  const route = currentRoute();
  const tabMatch =
    route.page === "project-detail" && String(route.id) === String(p.id) ? route.detailTab : "overview";
  const mainTab =
    tabMatch === "cost" ? "cost"
      : tabMatch === "billing" ? "billing"
      : tabMatch === "comments" ? "comments"
      : "overview";
  const formDraft = ensureProjectDetailFormDraft(p);
  // 営業・受注ステータスは下書き（保存ボタンで確定）の値を出す
  const salesPickId = formDraft?.salesOwnerId || p.salesOwnerId || p.assignedUserId || currentUser.id;
  const readOnlyInvoice = !canEditInvoice(p);
  const readOnlyQuote = !canEditQuote(p);
  const invoiceActionLabel = docActionLabel(p, "invoice");
  const quoteActionLabel = docActionLabel(p, "quote");
  const orderStatusOptions = PROJECT_ORDER_STATUS_LIST;
  const currentOrderStatus = formDraft?.orderStatus || projectOrderStatusForUi(p);

  // ── NDA・契約ステータス ──
  // 案件 fp_projects の snapshot とマスタのずれを吸収（id／名前フォールバック、パートナー名寄せ、契約一覧フラグ）。
  // クライアント・パートナー編集の NDA／基本契約は「未」「済」のみ。完了判定はマスタで ===「済」。
  // 案件 docs（nda_status 等）は「未作成」「締結済み」など別表記のため従来どおり複数パターンで判定（バナー文言は detailDocConclusionAlertLabel で揃える）。
  const clientRow = resolveProjectClientMaster(p);
  const partnerRow = resolvePartnerMasterMatchingProjectClientName(p);
  const cmClient = contractMasterClientRowForProject(p);
  const clientNdaMasterDone =
    clientPartnerMasterIsDone(clientRow?.ndaStatus) ||
    clientPartnerMasterIsDone(partnerRow?.nda_status) ||
    cmClient?.nda === true;
  const clientContractMasterDone =
    clientPartnerMasterIsDone(clientRow?.contractStatus) ||
    clientPartnerMasterIsDone(partnerRow?.contract_status) ||
    cmClient?.baseContract === true;
  const ndaDocDone =
    p.docs.nda === "締結済み" || p.docs.nda === "送付済み" || p.docs.nda === "確認済み" || String(p.docs.nda ?? "").trim() === "済";
  const contractDocDone =
    p.docs.contract === "締結済み" ||
    p.docs.contract === "送付済み" ||
    p.docs.contract === "確認済み" ||
    String(p.docs.contract ?? "").trim() === "済";
  const ndaDone = ndaDocDone || clientNdaMasterDone;
  const contractDone = contractDocDone || p.contractDone || clientContractMasterDone;

  // 基本契約（業務委託）が締結済みなら NDA 未締結アラートは出さない
  // （NDA→基本契約へ進む運用のため NDA 相当はカバー済み。案件作成時アラートと同じルール）
  const ndaAlertHtml = ndaDone || contractDone
    ? ""
    : `<div style="display:flex;align-items:center;gap:8px;padding:10px 16px;border-radius:12px;font-size:13px;font-weight:600;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;">
          <span style="font-size:16px;">⚠</span>
          NDA ${escapeHtml(detailDocConclusionAlertLabel(p.docs.nda))}
        </div>`;
  const contractAlertHtml = contractDone
    ? ""
    : `<div style="display:flex;align-items:center;gap:8px;padding:10px 16px;border-radius:12px;font-size:13px;font-weight:600;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;">
          <span style="font-size:16px;">⚠</span>
          業務委託契約 ${escapeHtml(detailDocConclusionAlertLabel(p.docs.contract))}
        </div>`;
  // ── 案件ロック（請求月が締め済み） ──
  const lockAlertHtml = isProjectLocked(p)
    ? `<div style="display:flex;align-items:center;gap:8px;padding:10px 16px;border-radius:12px;font-size:13px;font-weight:600;background:#fffbeb;border:1px solid #fde68a;color:#92400e;">
          <span style="font-size:16px;">🔒</span>
          ${isMasterRoleUser(currentUser)
            ? `この案件はロック中です（請求月 ${escapeHtml(p.billingMonth || "—")} は月次ロック済み）。マスター権限のため編集できます（保存時に確認が出ます）`
            : `この案件はロック中です（請求月 ${escapeHtml(p.billingMonth || "—")} は月次ロック済み）。金額・請求月・請求項目の変更にはロック解除が必要です（原価はロック対象外）`}
        </div>`
    : projectUnlockActive(p) && isMonthLocked(p.billingMonth)
      ? `<div style="display:flex;align-items:center;gap:8px;padding:10px 16px;border-radius:12px;font-size:13px;font-weight:600;background:#eff6ff;border:1px solid #bfdbfe;color:#1e40af;">
          <span style="font-size:16px;">🔓</span>
          ロック解除中（〜${escapeHtml(projectUnlockUntilDisplay(p))} まで編集可。期限後は自動で再ロックされます）
        </div>`
      : "";
  const detailContractAlertsOnlyHtml =
    ndaAlertHtml || contractAlertHtml || lockAlertHtml
      ? `<div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:12px;">${lockAlertHtml}${ndaAlertHtml}${contractAlertHtml}</div>`
      : "";
  const overviewContractAlertsSection =
    ndaAlertHtml || contractAlertHtml || lockAlertHtml
      ? `<section class="section"><div style="display:flex;gap:12px;flex-wrap:wrap;">${lockAlertHtml}${ndaAlertHtml}${contractAlertHtml}</div></section>`
      : "";

  // ── 請求項目（共通明細行）──
  const docDraft = ensureProjectDocDraft(p.id);
  const lines = docDraft.lines || [];
  const lineTypeOf = projectDetailLineType;
  const isNormalLine = (line) => projectDetailLineType(line) === "normal";
  const calcAmount = (l) => projectDetailNormalLineAmountExTax(l);
  const calcSubtotalUntil = (rowIndex) => projectDetailBillingSubtotalUntil(lines, rowIndex);
  const { subtotal, tax, total } = projectDetailBillingFooterTotals(lines);

  const nLines = lines.length;
  /** 請求項目行の上移動・下移動・削除 */
  const detailLineRowAttrs = (i) =>
    `class="doc-detail-line-row" data-detail-line-drop="${i}" data-detail-project="${p.id}"`;
  const detailLineActionCell = (i) => {
    const pid = p.id;
    const upDisabled = i <= 0;
    const downDisabled = i >= nLines - 1;
    return `<td class="doc-line-actions doc-line-actions--detail"><span class="doc-line-action-btns">
      <span class="doc-line-drag-handle" draggable="true" role="button" tabindex="0" aria-label="ドラッグで順番を変更" title="ドラッグで移動" data-detail-line-drag="${i}" data-detail-project="${pid}">
        <span class="doc-line-drag-line" aria-hidden="true"></span><span class="doc-line-drag-line" aria-hidden="true"></span><span class="doc-line-drag-line" aria-hidden="true"></span>
      </span>
      <button type="button" class="doc-line-btn doc-line-btn-move" aria-label="上へ移動" title="上へ" data-detail-line-move="-1" data-detail-line-index="${i}" data-detail-project="${pid}"${upDisabled ? " disabled" : ""}>↑</button>
      <button type="button" class="doc-line-btn doc-line-btn-move" aria-label="下へ移動" title="下へ" data-detail-line-move="1" data-detail-line-index="${i}" data-detail-project="${pid}"${downDisabled ? " disabled" : ""}>↓</button>
      <button type="button" class="doc-line-btn" aria-label="下に行を追加" title="下に行を追加" data-detail-line-insert-below="${i}" data-detail-project="${pid}">＋</button>
      <button type="button" class="doc-line-btn" aria-label="行を削除" title="削除" data-detail-line-remove="${i}" data-detail-project="${pid}">×</button>
    </span></td>`;
  };
  /** 行の左端の ▼（通常／見出し／小計）。board と同じく行を足してから種別を選ぶ（書類画面の ▼ と同じハンドラ）。
   *  列を増やすと style.css の列番号（nth-child）指定がずれるため、1列目のセルの中に置く */
  const detailLineTypeSelect = (i, t) => `<select class="doc-inv-rowbar-type" data-detail-line-type-change="${i}" data-detail-project="${p.id}" title="行の種別（通常／見出し／小計）" aria-label="行の種別" data-search-dropdown-off="1">
      <option value="normal"${t === "normal" ? " selected" : ""}>通常</option>
      <option value="heading"${t === "heading" ? " selected" : ""}>見出し</option>
      <option value="subtotal"${t === "subtotal" ? " selected" : ""}>小計</option>
    </select>`;

  const linesHtml = lines.length === 0
    ? `<tr><td colspan="6" style="text-align:center;color:var(--fp-text-subtle);padding:24px 0;font-size:13px;">明細がありません。「＋ 項目を追加」で登録してください。</td></tr>`
    : lines.map((l, i) => {
      const type = lineTypeOf(l);
      if (type === "heading") {
        return `
          <tr ${detailLineRowAttrs(i)}>
            <td colspan="5"><span class="doc-line-typed">${detailLineTypeSelect(i, type)}<input class="doc-line-input" data-detail-line-field="item" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.item || "")}" placeholder="見出し" /></span></td>
            ${detailLineActionCell(i)}
          </tr>
        `;
      }
      if (type === "subtotal") {
        return `
          <tr ${detailLineRowAttrs(i)}>
            <td colspan="4" style="font-weight:700;color:#374151;"><span class="doc-line-typed">${detailLineTypeSelect(i, type)}<span class="doc-line-typed-label">小計</span></span></td>
            <td style="width:90px;text-align:right;font-weight:700;" data-detail-billing-subtotal-value="1">${yen.format(calcSubtotalUntil(i))}</td>
            ${detailLineActionCell(i)}
          </tr>
        `;
      }
      return `
      <tr ${detailLineRowAttrs(i)}>
        <td class="doc-line-desktop-cell"><span class="doc-line-typed">${detailLineTypeSelect(i, type)}<input class="doc-line-input" data-detail-line-field="item" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.item)}" placeholder="摘要" /></span></td>
        <td class="doc-line-desktop-cell" style="width:60px;"><input class="doc-line-input" data-detail-line-field="qty" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(String(l.qty ?? ""))}" style="text-align:right;" /></td>
        <td class="doc-line-desktop-cell" style="width:50px;"><input class="doc-line-input" data-detail-line-field="unit" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(l.unit ?? "")}" /></td>
        <td class="doc-line-desktop-cell" style="width:80px;"><input class="doc-line-input" data-detail-line-field="price" data-detail-line-index="${i}" data-detail-project="${p.id}" value="${escapeHtmlAttr(String(l.price ?? ""))}" style="text-align:right;" /></td>
        <td class="doc-line-desktop-cell" style="width:90px;text-align:right;font-weight:600;" data-detail-billing-line-amount="1">${yen.format(calcAmount(l))}</td>
        ${detailLineActionCell(i)}
        <td class="doc-line-mobile-cell">
          <div class="dlm-card-info">
            <div class="dlm-card-item">${escapeHtml(l.item || "(摘要なし)")}</div>
            <div class="dlm-card-meta">${escapeHtml(String(l.qty ?? 1))}${escapeHtml(l.unit || "式")} × ¥${Number(l.price || 0).toLocaleString()}</div>
          </div>
          <div class="dlm-card-right">
            <div class="dlm-card-amount">${yen.format(calcAmount(l))}</div>
            <div class="dlm-card-btns">
              <button type="button" class="dlm-edit-btn" data-detail-line-mobile-edit="${i}" data-detail-project="${p.id}">編集</button>
              <button type="button" class="dlm-delete-btn" data-detail-line-remove="${i}" data-detail-project="${p.id}" aria-label="行を削除">×</button>
            </div>
          </div>
        </td>
      </tr>
      `;
    }).join("");

  const detailMainTabs = `
    <section class="section" style="padding-bottom:0;">
      <div class="detail-main-tabs" role="tablist" aria-label="案件詳細の表示">
        <button type="button" class="detail-main-tab${mainTab === "overview" ? " active" : ""}" data-detail-main-tab="overview" role="tab" aria-selected="${mainTab === "overview" ? "true" : "false"}">基本情報</button>
        <button type="button" class="detail-main-tab${mainTab === "billing" ? " active" : ""}" data-detail-main-tab="billing" role="tab" aria-selected="${mainTab === "billing" ? "true" : "false"}">請求項目</button>
        <button type="button" class="detail-main-tab${mainTab === "cost" ? " active" : ""}" data-detail-main-tab="cost" role="tab" aria-selected="${mainTab === "cost" ? "true" : "false"}">原価</button>
        <button type="button" class="detail-main-tab${mainTab === "comments" ? " active" : ""}" data-detail-main-tab="comments" role="tab" aria-selected="${mainTab === "comments" ? "true" : "false"}">コメント</button>
      </div>
    </section>
  `;

  const overviewBody = `
    ${overviewContractAlertsSection}

    ${buildProjectDetailOverviewMetaCardHtml(formDraft, p, salesPickId, orderStatusOptions, currentOrderStatus)}
    ${uiState.clientBranches.editor?.fromProjectDetail ? clientBranchEditorModalHtml() : ""}
    ${uiState.clientContacts.editor?.fromProjectDetail ? clientContactEditorModalHtml() : ""}

    <section class="section">
      <div class="soft-card" style="padding:20px;">
        ${buildProjectDetailFormResultFieldsHtml(formDraft, p)}
      </div>
      <div class="pd-overview-save-row">
        <button class="inline-btn pd-overview-save-btn" type="button" data-pd-save-detail="stay">保存</button>
        <button class="inline-btn ghost pd-overview-docs-btn" type="button" data-pd-save-detail="list">保存して一覧へ</button>
        <button class="inline-btn ghost pd-overview-docs-btn" type="button" data-pd-save-detail="docs">保存して書類へ →</button>
        ${projectDetailSaveStatusHtml()}
        <button class="inline-btn ghost pd-overview-history-btn" type="button" data-open-project-edit-history="${p.id}">編集履歴</button>
      </div>
      ${/* 保存せずに移る道（2026-10 要望）。ボタンを増やさず控えめなリンクにする。未保存の変更があれば
          15k-project-detail-save.js の離脱確認（data-go / data-open-project-docs）が止める */ ""}
      <div class="pd-overview-leave-links">
        <span class="pd-overview-leave-label">保存せずに：</span>
        <button type="button" class="pd-overview-leave-link" data-go="projects">一覧へ戻る</button>
        <span class="pd-overview-leave-sep" aria-hidden="true">／</span>
        <button type="button" class="pd-overview-leave-link" data-open-project-docs="${p.id}">書類へ →</button>
      </div>
    </section>
  `;

  const billingTabBody = `
    <section class="section">
      ${detailContractAlertsOnlyHtml}
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
        <h3 class="section-title" style="margin:0;">項目</h3>
        ${/* 種別を選ぶ画面は出さず通常行を末尾に足す（種別は行の ▼ で変える）。末尾への挿入＝最後の行の「下に追加」と同じ処理 */ ""}
        <button class="inline-btn ghost" data-detail-line-insert-below="${nLines - 1}" data-detail-project="${p.id}" type="button" style="font-size:13px;">＋ 項目を追加</button>
      </div>
      <div class="soft-card" style="overflow-x:auto;">
        <table class="doc-lines" data-project-billing-table="${escapeHtmlAttr(p.id)}" style="width:100%;min-width:740px;table-layout:fixed;">
          <colgroup>
            <col style="width:284px;" />
            <col style="width:70px;" />
            <col style="width:60px;" />
            <col style="width:110px;" />
            <col style="width:120px;" />
            <col style="width:108px;" />
          </colgroup>
          <thead><tr><th>摘要</th><th>数量</th><th>単位</th><th>単価</th><th>金額</th><th class="doc-line-actions">操作</th></tr></thead>
          <tbody>${linesHtml}</tbody>
        </table>
        <div style="display:flex;justify-content:flex-end;gap:16px;padding:12px 16px;border-top:1px solid #f0f0f0;font-size:13px;">
          <span class="pd-billing-save" style="margin-right:auto;display:flex;align-items:center;gap:8px;">
            <button class="inline-btn pd-overview-save-btn" type="button" data-pd-save-detail="stay">保存</button>
            ${projectDetailSaveStatusHtml()}
          </span>
          <span style="color:var(--fp-text-subtle);">小計 <strong style="color:#111;" data-detail-billing-foot-subtotal>${yen.format(subtotal)}</strong></span>
          <span style="color:var(--fp-text-subtle);">消費税 <strong style="color:#111;" data-detail-billing-foot-tax>${yen.format(tax)}</strong></span>
          <span style="color:var(--fp-text-subtle);">合計 <strong style="color:#111;font-size:15px;" data-detail-billing-foot-total>${yen.format(total)}</strong></span>
        </div>
      </div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:10px;">
        ${orderEvidenceProjectButtonHtml(p)}
        <button class="inline-btn" type="button" data-open-project-docs="${p.id}">書類作成・編集へ →</button>
      </div>
    </section>
  `;

  const costTabBody = buildCostsReadonlySection(p);

  const detailHead = `<header class="page-head detail-page-head"><button class="detail-back-btn" type="button" data-go="projects" aria-label="一覧に戻る"><span class="detail-back-arrow" aria-hidden="true">←</span><span class="detail-back-label">一覧</span></button><div class="detail-head-title-wrap"><h2 class="detail-page-h2-with-edit">${projectDetailPageHeadTitleHtml(p)}</h2></div></header>`;

  const mainBody =
    mainTab === "overview" ? overviewBody
      : mainTab === "billing" ? billingTabBody
      : mainTab === "comments" ? projectCommentsTabHtml(p)
      : costTabBody;

  return `
    ${detailHead}
    ${remCostAllocProjectBannerHtml(p)}
    ${detailMainTabs}
    ${mainBody}
  `;
}

/**
 * 案件ごとに「サーバーにある（と画面が最後に確認した）値」を覚え、保存では変えた項目だけを送る。
 * 全項目を送ると、一覧を読み込んだ後に他の人が変えた金額・ステータス等を手元の古い値で戻してしまう
 * （サーバーの PUT は送られたキーの列だけ更新する）。覚えていない案件（新規作成直後など）は全項目を送る。
 */
const projectServerBaseline = new WeakMap();

function projectDetailServerBody(p) {
  return {
    id: p.dbId,
    name: p.name || "",
    clientBranchName: p.clientBranchName || "",
    clientPerson: p.person || "",
    amount: Number(p.amount || 0),
    billingMonth: p.billingMonth || null,
    currentStage: p.currentStage || "contracts",
    nextAction: p.nextAction || "",
    invoiceStatus: p.invoiceStatus || "未作成",
    ndaStatus: p.docs?.nda || "未作成",
    contractStatus: p.docs?.contract || "未作成",
    quoteStatus: p.docs?.quote || "未作成",
    paymentStatus: p.docs?.payment || "未確認",
    orderStatus: p.orderStatus || "",
    progressStatus: normalizeProjectProgressStatus(p.progressStatus),
    plannedInvoiceDate: p.plannedInvoiceDate || "",
    dueDate: p.dueDate || "",
    ...(() => {
      const eff = p.salesOwnerId || p.assignedUserId || currentUser.id;
      const n = Number(String(eff).replace(/^u/, ""));
      const idNum = Number.isFinite(n) && n > 0 ? n : null;
      return { salesOwnerId: idNum, assignedUserId: idNum };
    })(),
    // 売上分配（営業別集計の按分）。null で解除＝担当営業100%に戻す
    salesSplit: salesSplitToServerBody(p.salesSplit),
    // その他（貢献度）＝案件に関わった営業以外のメンバー（表示専用・比率なし）。null で解除＝未設定に戻す
    nonSalesSplit: nonSalesInvolvedToServerBody(p.nonSalesSplit),
    // カテゴリと担当者（基本情報）。null で解除＝未設定
    workCategories: workCategoriesToServerBody(p.workCategories),
    tags: parseProjectDetailTags(p.tag || "").join(", "),
    mailTo: p.mailTo || "",
    mailCc: p.mailCc || "",
    paymentTerm: p.paymentTerm || "",
    internalMemo: p.internalMemo || "",
    taxIncluded: p.taxIncluded === true,
    ...(p.serviceCategory !== undefined ? { serviceCategory: p.serviceCategory } : {})
  };
}

function rememberProjectServerBaseline(p) {
  if (p) projectServerBaseline.set(p, projectDetailServerBody(p));
}

function changedProjectDetailBody(p, fullBody) {
  const base = projectServerBaseline.get(p);
  if (!base) return { ...fullBody };
  const out = { id: fullBody.id };
  for (const [k, v] of Object.entries(fullBody)) {
    if (k !== "id" && JSON.stringify(v) !== JSON.stringify(base[k])) out[k] = v;
  }
  return out;
}

/** 案件の保存。同じ案件への請求項目の書き込みと直列化する（並行送信で自分の保存と 409 競合しないように） */
function persistProjectDetailToServer(p, options = {}) {
  return serializeProjectBillingWrite(p?.id, () => persistProjectDetailToServerNow(p, options));
}

async function persistProjectDetailToServerNow(p, options = {}) {
  if (!authToken || !p?.dbId) return { ok: true, skipped: true };
  // マスターのロック中編集は保存前に確認。キャンセルは throw して呼び出し元の
  // catch（ロールバック＋失敗トースト）に乗せる（黙って skip すると「保存しました」誤表示になる）
  if (!confirmMasterLockedProjectEdit(p)) throw new Error("保存をキャンセルしました（ロック中の案件）");
  const pid = String(p.id || "");
  const fullBody = projectDetailServerBody(p);
  const body = changedProjectDetailBody(p, fullBody);
  /** 請求項目を「ユーザーが実際に編集した」保存か（markProjectDocsDraftDirty が立てる）。
   *  includeBillingLines は概要保存でも同送するため、送ったこと自体は編集の証拠にならない。 */
  // 案件詳細で編集した未保存の明細（projectBillingManualDirty）は、保存ボタン・書類送付前など
  // includeBillingLines を明示した保存でだけ送る。案件名のインライン保存などに同乗させると、
  // 試しに変えた金額が保存ボタンを押さないまま確定してしまう
  const billingLinesEdited =
    !!uiState.projectDocsDraftDirty[pid] && (!!options.includeBillingLines || !uiState.projectBillingManualDirty?.[pid]);
  /** 項目未編集時に空配列を送るとサーバー上の明細が消える（受注状況・進捗のみ保存時など） */
  if (options.includeBillingLines || billingLinesEdited) {
    const route = currentRoute();
    // モバイル(≤640px)では doc-line-desktop-cell が非表示で el.value が旧値のため DOM 同期をスキップ
    const _isMobileVp = typeof window !== "undefined" && window.innerWidth <= 640;
    if (!_isMobileVp && route.page === "project-detail" && String(route.id) === pid) {
      syncProjectDetailBillingTableFromDom(pid);
    }
    const d = ensureProjectDocDraft(pid);
    const lines = Array.isArray(d?.lines) ? d.lines : [];
    // サーバースナップショットに明細があるのに空で上書きする誤消去を防ぐ
    // allowClearBillingLines を明示した場合のみ空での上書きを許可する
    const snap = uiState.projectBillingLinesServerSnapshot?.[pid];
    // 利用者が実際に全行を削除した保存（未保存フラグあり）で、最後に見たサーバーの明細のハッシュも
    // 分かっているなら空を送る。ハッシュがあればサーバーが他の人の変更と照合する（409）ので誤消去にならない。
    // 以前は空を一切送らず、全行削除して保存しても成功に見えてリロードで明細が戻っていた
    const userClearedLines = billingLinesEdited && !!billingLinesBaseHashFor(pid);
    const safeToSendEmpty = !!options.allowClearBillingLines || userClearedLines || !snap || snap.lineCount === 0;
    if (lines.length === 0 && !safeToSendEmpty) {
      // 空上書きを回避。dirty フラグを保持して次回正しいデータで保存させる
    } else {
      body.billingLines = lines;
      // サーバーは既存の明細がある案件への空配列を、この指定が無いと黙って無視する
      if (lines.length === 0 && snap && snap.lineCount > 0) body.allowClearBillingLines = true;
    }
  }
  // 請求項目を送るときは、最後に見たサーバーの請求項目のハッシュを添える（他の人の変更があれば 409）。
  // 保存後のハッシュはこの時点の内容で計算する（応答待ちの間に編集されても、送った内容とずれないように）
  let sentBillingLinesHash = "";
  if (Object.prototype.hasOwnProperty.call(body, "billingLines")) {
    const baseHash = billingLinesBaseHashFor(pid);
    if (baseHash) body.baseBillingLinesHash = baseHash;
    sentBillingLinesHash = billingLinesHash(body.billingLines);
  }
  // 実タイムアウト: 応答が無いとき fetch を実際に abort し、保存リクエストが
  // 裏で残り続けて遷移を詰まらせるのを防ぐ（hashchange の保存待ち対策と対）。
  const _ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const _to = _ctrl ? setTimeout(() => _ctrl.abort(), 10000) : null;
  let res;
  try {
    res = await fetch("/api/projects", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(body),
      ...(_ctrl ? { signal: _ctrl.signal } : {})
    });
  } finally {
    if (_to) clearTimeout(_to);
  }
  // サーバーの理由をそのまま載せる（呼び出し元のトーストが原因を推測で言わないようにするため）
  if (!res.ok) {
    let reason = "";
    try {
      reason = String((await res.json())?.error || "");
    } catch {
      reason = "";
    }
    // 409 = 他の人が請求項目を更新済み。手元の編集（dirty）は残したまま、自動での再送を止める
    if (res.status === 409) markProjectBillingLinesConflict(pid);
    const err = new Error(reason || `project update failed (${res.status})`);
    err.serverMessage = reason;
    throw err;
  }
  // 保存できた内容を次回の比較の基準にする（送らなかった項目も手元の値＝サーバーの値とみなせる）
  projectServerBaseline.set(p, fullBody);
  if (Object.prototype.hasOwnProperty.call(body, "billingLines")) {
    const saved = await res.json().catch(() => ({}));
    rememberProjectBillingLinesServerSnapshot(p.id, body.billingLines, {
      hash: saved?.billingLinesHash || sentBillingLinesHash
    });
    // 応答待ちの間に追加で編集されていたら未保存のまま残して送り直す。以前は無条件に未保存を消していたため、
    // その編集は保存されないまま「保存済み」扱いになり、直後の郵送・メールも古い金額で送られた
    const nowLines = ensureProjectDocDraft(p.id)?.lines;
    if (billingLinesHash(Array.isArray(nowLines) ? nowLines : []) === sentBillingLinesHash) {
      clearProjectDocsDraftDirty(p.id);
    } else {
      schedulePersistProjectBillingLinesToServer(p.id);
    }
    // 捺印申請の取り消しはサーバーが同じトランザクションで判定する（書類に印字される内容が変わったときだけ。
    // api/_docSeal.js billingLinesPrintedChanged）。以前はフロントも「請求項目を編集したら必ず」取り消していたため、
    // 担当者だけの変更（担当者割当の一括適用など）でも承認済みが未申請に戻っていた。サーバーの結果を画面に反映する
    applyServerDocSealReset(p.id, saved?.sealResetTabs);
  }
  return { ok: true, skipped: false };
}

/** 営業⇄経理の引き継ぎ状態を永続化（引き継ぎ専用列のみ。他フィールドは persistProjectDetailToServer 側）。失敗時は throw。
 *  入金記録の列は通常「値があるときだけ更新」のため、取り消しでクリアする場合は options.clearPayment を指定する。 */
async function persistProjectHandoffToServer(p, options = {}) {
  if (!authToken || !p?.dbId) return { ok: true, skipped: true };
  const res = await fetch("/api/project-handoff", {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({
      id: p.dbId,
      handoffStatus: p.handoffStatus || "",
      currentOwnerRole: p.currentOwnerRole || "",
      handoffAt: p.handoffAt || "",
      handoffNote: p.handoffNote || "",
      handedBy: p.handedBy || "",
      previousAssignedUserId: p.previousAssignedUserId || "",
      paymentRecord: p.paymentRecord || null,
      paymentConfirmedBy: p.paymentConfirmedBy || "",
      paymentConfirmedAt: p.paymentConfirmedAt || "",
      ...(options.clearPayment ? { clearPayment: true } : {})
    })
  });
  if (!res.ok) throw new Error("project handoff update failed");
  return { ok: true, skipped: false };
}

/** プロジェクト一覧の一括複製: サーバーに新規案件を作成し、見積・請求の項目（billing lines）もコピーする。
 *  書類ステータス・原価・引き継ぎ状態はコピーせず新規案件の既定値で開始する。失敗時は throw。
 *  options（毎月請求の複製用。省略時は従来どおり）:
 *   - keepName: true で「（複製）」を付けず同名のまま（毎月分の名前連鎖を防ぐ）
 *   - billingMonth / plannedInvoiceDate: 複製先の請求月・請求予定日の上書き
 *   - recurringBilling: true で複製先にも「毎月請求」を引き継ぐ
 *   - copyMode: "project"（案件情報のみ）／"quote"（項目＋見積書の設定・備考）／"all"（項目＋全書類の設定・備考）。
 *     省略時は従来どおり項目のみコピー（毎月請求の複製など既存呼び出しの挙動を変えない） */
async function duplicateProjectWithItemsToServer(src, options = {}) {
  const copyMode = options.copyMode || "";
  const copyLines = copyMode !== "project";
  const copyMetaTabs = copyMode === "all" ? null : copyMode === "quote" ? ["quote"] : [];
  if (!authToken) throw new Error("ログインが必要です");
  const toDbUserId = (uid) => {
    const n = Number(String(uid || "").replace(/^u/, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const clientDbId = (() => {
    const c = clients.find((x) => x.id === src.clientId);
    if (c?.dbId) return c.dbId;
    const n = Number(String(src.clientId || "").replace(/^c/, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const name = options.keepName ? src.name : `${src.name}（複製）`;
  const billingMonth =
    options.billingMonth !== undefined ? options.billingMonth : (src.billingMonth || null);
  const plannedInvoiceDate =
    options.plannedInvoiceDate !== undefined
      ? (options.plannedInvoiceDate || null)
      : (src.plannedInvoiceDate || null);
  const dueDate =
    options.dueDate !== undefined ? (options.dueDate || null) : (src.dueDate || null);
  const recurringBilling = options.recurringBilling === true;
  const { ok, data } = await apiFetch("/api/projects", {
    method: "POST",
    body: {
      clientId: clientDbId,
      name,
      amount: Number(src.amount || 0),
      billingMonth,
      plannedInvoiceDate,
      currentStage: src.currentStage || "contracts",
      nextAction: src.nextAction || "",
      projectType: src.projectType || "existing-client",
      salesOwnerId: toDbUserId(src.salesOwnerId),
      accountingOwnerId: toDbUserId(src.accountingOwnerId),
      assignedUserId: toDbUserId(src.assignedUserId || src.salesOwnerId),
      invoiceStatus: "未作成",
      orderStatus: src.orderStatus || "",
      progressStatus: normalizeProjectProgressStatus(src.progressStatus),
      tags: parseProjectDetailTags(src.tag || "").join(", "),
      serviceCategory: src.serviceCategory || "",
      // 基本情報の複製: 納期・支社名・顧客担当者・送信先/CCメールも引き継ぐ
      dueDate,
      clientBranchName: src.clientBranchName || "",
      clientPerson: src.person || "",
      mailTo: src.mailTo || "",
      mailCc: src.mailCc || "",
      // 支払条件・社内メモ・税込表示も引き継ぐ（以前は送らず、画面では引き継いで見えるのにリロードで消えた）
      paymentTerm: src.paymentTerm || "",
      internalMemo: src.internalMemo || "",
      taxIncluded: src.taxIncluded === true,
      recurringBilling,
      // 売上分配・その他（貢献度）・カテゴリ担当も引き継ぐ（毎月請求のサーバー側複製と同じ扱い）
      salesSplit: salesSplitToServerBody(src.salesSplit),
      nonSalesSplit: nonSalesInvolvedToServerBody(src.nonSalesSplit),
      workCategories: workCategoriesToServerBody(src.workCategories)
    }
  });
  if (!ok || data.project?.id == null) {
    throw new Error(typeof data.error === "string" ? data.error : "複製の作成に失敗しました");
  }
  const dbId = Number(data.project.id);
  const newPid = `p${dbId}`;
  projects.unshift({
    ...src,
    id: newPid,
    dbId,
    no: undefined,
    name,
    billingMonth,
    plannedInvoiceDate,
    dueDate,
    billingDate: isoYmdToSlashDisplay(plannedInvoiceDate || "") || "",
    recurringBilling,
    // 締め済み月の複製はサーバーが3営業日の一時解除つきで作成する（unlock_until を反映）
    unlockUntil: data.project.unlock_until || "",
    invoiceStatus: "未作成",
    docs: { nda: "未作成", contract: "未作成", quote: "未作成", order: "未作成", payment: "未確認" },
    costs: [],
    currentOwnerRole: "営業",
    handoffStatus: "未引き継ぎ",
    handoffAt: "",
    handoffNote: "",
    handedBy: "",
    previousAssignedUserId: "",
    // {...src} のシャローコピーだと src と同一配列を共有し、片方の分配編集が両案件に波及する
    salesSplit: Array.isArray(src.salesSplit) ? src.salesSplit.map((s) => ({ ...s })) : [],
    // nonSalesSplit は userId 文字列の配列（プリミティブ）。[...src.nonSalesSplit] で配列自体を切り離せば十分
    nonSalesSplit: Array.isArray(src.nonSalesSplit) ? [...src.nonSalesSplit] : [],
    workCategories: (({ categories, members }) => ({ categories: [...categories], members: [...members] }))(workCategoriesFromRaw(src.workCategories)),
    paymentRecord: null,
    paymentConfirmedBy: "",
    paymentConfirmedAt: ""
  });
  // 書類の設定・備考（meta/remark）のコピー。複製元の書類ページを開いていないとメモリに無いので先にサーバーから読む
  if (copyMetaTabs === null || copyMetaTabs.length > 0) {
    await loadDocMetaFromServer(src.id);
    const srcDraft = uiState.projectDocsDrafts[src.id] || {};
    const pick = (obj) => {
      const out = {};
      Object.entries(obj || {}).forEach(([tab, v]) => {
        if (copyMetaTabs === null || copyMetaTabs.includes(tab)) out[tab] = JSON.parse(JSON.stringify(v));
      });
      return out;
    };
    const meta = pick(srcDraft.meta);
    // 支払期限・見積日は複製元の発行時点の日付。コピーすると請求月を変えても前月の支払期限で請求書が出るため外し、
    // 書類を開いたときに複製先の請求月・今日から決め直させる（07-pdf-output.js の未設定時の既定値）
    Object.values(meta).forEach((m) => {
      if (m && typeof m === "object") {
        delete m.due;
        delete m.issueDate;
      }
    });
    const remark = pick(srcDraft.remark);
    if (Object.keys(meta).length > 0 || Object.keys(remark).length > 0) {
      uiState.projectDocsDrafts[newPid] = { lines: [], meta, remark };
      // 作成直後の案件なのでサーバーに書類メタは無い（読込を待たずに、コピーしたタブを1つずつ送る）
      if (!uiState._docMetaServerLoaded) uiState._docMetaServerLoaded = {};
      uiState._docMetaServerLoaded[newPid] = true;
      await persistDocMetaToServer(newPid, [...new Set([...Object.keys(meta), ...Object.keys(remark)])]);
    }
  }
  // 項目（billing lines）のコピー。サーバーに永続化されるのは lines のみ（billing_lines_json）
  const srcLines = copyLines
    ? (uiState.projectDocsDrafts[src.id]?.lines || []).filter((l) => l && typeof l === "object")
    : [];
  if (srcLines.length > 0) {
    const lines = JSON.parse(JSON.stringify(srcLines));
    const prevDraft = uiState.projectDocsDrafts[newPid];
    uiState.projectDocsDrafts[newPid] = { lines, meta: prevDraft?.meta || {}, remark: prevDraft?.remark || {} };
    const bres = await apiFetch("/api/project-billing", {
      method: "PUT",
      body: { projectId: dbId, billingLines: lines }
    });
    if (!bres.ok) {
      // 案件本体は作成済みだが項目コピーが未保存。成功扱いにするとリロードで明細が消えて見えるため失敗として知らせる
      throw new Error("案件は複製しましたが、項目のコピーに失敗しました（複製先の項目をご確認ください）");
    }
    rememberProjectBillingLinesServerSnapshot(newPid, lines);
  }
  return newPid;
}

/** プロジェクト一覧: マスターがチェックした案件のみ一括削除（サーバー DELETE + 関連原価削除） */
async function deleteSelectedProjectsBulkMaster() {
  if (!isMasterRoleUser(currentUser)) {
    showToast("この操作はマスター権限のみ利用できます");
    return;
  }
  const selectedUiIds = Object.entries(uiState.projectTable.selected || {})
    .filter(([, v]) => v === true)
    .map(([id]) => id);
  if (!selectedUiIds.length) {
    showToast("削除する案件にチェックを入れてください");
    return;
  }
  const meta = [];
  for (const uiId of selectedUiIds) {
    const idx = projects.findIndex((p) => p.id === uiId);
    if (idx < 0) continue;
    meta.push({ idx, uiId, dbId: projects[idx].dbId });
  }
  if (!meta.length) return;
  const lockedCount = meta.filter(({ idx }) => isProjectLocked(projects[idx])).length;
  const ok = await fpConfirm({
    title: `案件 ${meta.length} 件を削除しますか？`,
    message: `一覧から外れ、紐づく原価も一覧・集計から外れます。${lockedCount ? `\n⚠ ロック中（締め済み）の案件 ${lockedCount} 件を含みます。` : ""}`,
    note: "「削除済みデータの復元」で原価ごと戻せます。",
    confirmLabel: `${meta.length} 件を削除する`
  });
  if (!ok) return;
  const dbIds = [...new Set(meta.map((m) => Number(m.dbId)).filter((n) => Number.isFinite(n) && n > 0))];
  try {
    if (authToken && dbIds.length) {
      const { ok: delOk, data } = await apiFetch("/api/projects", { method: "DELETE", body: { ids: dbIds } });
      if (!delOk) throw new Error(typeof data.error === "string" ? data.error : "delete failed");
    }
    [...meta].sort((a, b) => b.idx - a.idx).forEach(({ idx }) => {
      projects.splice(idx, 1);
    });
    selectedUiIds.forEach((id) => {
      delete uiState.projectTable.selected[id];
      // 削除済み案件の下書き・スナップショットを即時クリーンアップ
      delete uiState.projectDocsDrafts[id];
      delete uiState.projectDocsDraftDirty[id];
      if (uiState.projectBillingLinesServerSnapshot) delete uiState.projectBillingLinesServerSnapshot[id];
    });
    try { persistProjectDocDraftsStore(); } catch { /* ignore */ }
    showToast(`${meta.length}件の案件を削除しました`);
    render();
  } catch (e) {
    showToastError(e instanceof Error && e.message ? e.message : "削除に失敗しました");
  }
}

/** 原価のうち集計に数えるもの＝実原価＋「同じ支払先×計上月の実原価がまだ無い」仮原価。
 *  実原価が入った支払先×月の仮原価まで足すと二重計上になる（粗利・外注先名・月次P/Lで共通の規則） */
function countedProjectCosts(costs) {
  const list = (costs || []).filter(Boolean);
  const key = (c) => `${compactVendorMatchKey(c.vendor || "")}|${String(c.targetMonth || "").trim().replace(/-/g, "/")}`;
  const realKeys = new Set(list.filter((c) => !c.isProvisional).map(key));
  return list.filter((c) => !c.isProvisional || !realKeys.has(key(c)));
}

// 案件の粗利サマリー（原価計・粗利・粗利率）。原価タブ・書類作成画面など複数箇所で参照するため一本化。
// 仮原価（見込み）と実原価を分けて集計。実原価が来た支払先×計上月の仮は集計から外す（二重計上回避）
// 原価は税抜（売上も税抜なので、案件一覧・予算進捗と同じ「税抜売上 − 税抜原価」。2026-07-30 ユーザー確定）
function projectGrossMarginSummary(p) {
  const costs = p.costs || [];
  const provVendorMonthKey = (c) =>
    `${compactVendorMatchKey(c.vendor || "")}|${String(c.targetMonth || "").trim().replace(/-/g, "/")}`;
  const realKeys = new Set(costs.filter((c) => !c.isProvisional).map(provVendorMonthKey));
  const exTax = (c) => Number(c.amount || 0);
  const realCost = costs.filter((c) => !c.isProvisional).reduce((s, c) => s + exTax(c), 0);
  const provisionalCost = costs
    .filter((c) => c.isProvisional && !realKeys.has(provVendorMonthKey(c)))
    .reduce((s, c) => s + exTax(c), 0);
  const hasProvisional = provisionalCost > 0;
  const totalCost = realCost + provisionalCost;
  // 売上は p.amount（受注金額の固定カラム。請求項目の変更に追従しない）ではなく、
  // 案件一覧の粗利率と同じ実際の請求項目合計（projectDisplayedBillingExTaxYen）を使う。
  // p.amount 参照のままだと、明細だけ更新された案件で売上ゼロ扱いになり粗利が実態と逆転する。
  const revenue = projectDisplayedBillingExTaxYen(p);
  const grossProfit = revenue - totalCost;
  const marginPct = revenue > 0 ? Math.round((grossProfit / revenue) * 100) : 0;
  return { realCost, provisionalCost, hasProvisional, totalCost, grossProfit, marginPct };
}

/** 営業が案件詳細から変更・削除できる原価か（サーバ api/costs.js salesMayTouchCost と同じ: 受領済・支払済より前。
 *  2026-10 要望で見込み（仮原価）だけから実原価にも広げた）。締めた月はマスター/管理者以外は触れない（月次ロック） */
function costEditableFromProjectDetail(c) {
  const received = Boolean(c.paidDate) || ["支払待ち", "支払済み"].includes(String(c.payStatus || ""));
  if (received) return false;
  return !isMonthLocked(c.targetMonth) || isMasterOrAdminUser(currentUser);
}

/** 今のユーザーに原価の編集・削除ボタンを出すか（案件詳細の原価表・原価割当の一覧/案件カードで共通）。
 *  仮原価は担当外の案件でも営業が追加・修正・削除できる（2026-07-31 緩和）。2026-10 要望で、受領済より前なら
 *  実原価も営業（原価割当）が修正・削除できる（サーバ api/costs.js salesMayTouchCost）。
 *  原価管理だけの人（CS）は実原価の金額等を変えられないので、実原価のボタンは原価割当を使える人にだけ出す */
function costEditDeleteAllowedForUser(c) {
  const canTouch = c.isProvisional
    ? userCanAccessPage("cost-allocation") || userCanAccessPage("cost-management")
    : userCanAccessPage("cost-allocation");
  return canTouch && costEditableFromProjectDetail(c);
}

function buildCostsReadonlySection(p) {
  const costs = p.costs || [];
  const { realCost, provisionalCost, hasProvisional, totalCost, grossProfit, marginPct: margin } =
    projectGrossMarginSummary(p);
  // 仮原価の追加は原価割当・原価管理の人（編集・削除の出し分けは costEditDeleteAllowedForUser）
  const canTouchProvisional = userCanAccessPage("cost-allocation") || userCanAccessPage("cost-management");
  const rows =
    costs.length === 0
      ? `<tr><td colspan="8" class="cost-empty">原価がまだ登録されていません</td></tr>`
      : costs.map((c) => {
          const noteRaw = String(c.note || "").trim();
          const noteHtml = noteRaw
            ? noteRaw.length > 200
              ? `<span class="cost-table-note" title="${escapeHtmlAttr(noteRaw)}">${escapeHtml(noteRaw.slice(0, 200))}…</span>`
              : `<span class="cost-table-note">${escapeHtml(noteRaw)}</span>`
            : "—";
          const memoMark = costMemoMarkHtml(c);
          const taxY = costConsumptionTaxYen(c);
          const amtCell =
            taxY > 0
              ? `${yen.format(c.amount || 0)}<span class="cost-tax-sub">　税 ${yen.format(taxY)}</span>`
              : yen.format(c.amount || 0);
          const paySt = costPayStatusDisplayLabel(c);
          // 承認額超過・承認なしの原価には「理由を入力」（15z-cost-order-applications.js）
          const reasonBtn = costOrderExceptionReasonBtnHtml(c, p.id);
          const opsCell =
            costEditDeleteAllowedForUser(c)
              ? `<td class="cost-actions"><div class="cost-ops-inline">
          <button type="button" class="inline-btn ghost" data-edit-cost="${p.id}:${c.id}">編集</button>
          <button type="button" class="inline-btn ghost" data-delete-cost="${p.id}:${c.id}">削除</button>
          ${reasonBtn}
        </div></td>`
              : reasonBtn
                ? `<td class="cost-actions"><div class="cost-ops-inline">${reasonBtn}</div></td>`
                : `<td></td>`;
          return `
      <tr class="${c.isProvisional ? "cost-row-provisional" : ""}">
        <td><span class="cost-category-badge">${escapeHtml(String(c.category || "その他"))}</span>${c.isProvisional ? '<span class="cost-provisional-badge">見込</span>' : ""}${costOrderStatusBadgeHtml(c)}</td>
        <td class="cost-vendor">${escapeHtml(String(c.vendor || "—"))}</td>
        <td class="cost-amount">${amtCell}</td>
        <td>${escapeHtml(String(c.targetMonth || "—"))}</td>
        <td>${costDueDisplayForReadonly(c)}</td>
        <td><span class="cost-pay-status ${costPayClass(paySt)}">${escapeHtml(paySt)}</span></td>
        <td class="cost-detail-note-cell">${noteHtml}${memoMark}</td>
        ${opsCell}
      </tr>`;
        }).join("");

  return `
    <section class="section">
      <div class="detail-section-head" style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <h3 class="section-title">原価一覧</h3>
        <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
          <div style="font-size:13px;color:#374151;">
            ${hasProvisional
              ? `実原価（税抜） <strong>${yen.format(realCost)}</strong> ＋ 見込 <strong style="color:#b45309;">${yen.format(provisionalCost)}</strong> ／ 見込粗利 <strong style="color:${grossProfit < 0 ? "#dc2626" : "#059669"};">${yen.format(grossProfit)}</strong>（${margin}%）`
              : `合計原価（税抜） <strong>${yen.format(totalCost)}</strong> ／ 粗利 <strong style="color:${grossProfit < 0 ? "#dc2626" : "#059669"};">${yen.format(grossProfit)}</strong>（${margin}%）`}
          </div>
          ${canTouchProvisional ? `<button type="button" class="inline-btn" data-add-provisional-cost="${p.id}">＋ 原価を追加</button>` : ""}
        </div>
      </div>
      <div class="soft-card" style="overflow-x:auto;">
        <table class="cost-table cost-table--project-readonly cost-table--project-cost-tab">
          <thead>
            <tr>
              <th>会計区分</th>
              <th>発注先</th>
              <th>金額（税抜）</th>
              <th>計上月</th>
              <th>支払期限</th>
              <th>支払状況</th>
              <th>発注名</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>
  `;
}

function renderComposer() {
  if (!uiState.composer) return "";
  const c = uiState.composer;
  const project = projects.find((p) => p.id === c.projectId);
  // 書類の現在のステータスを取得（ハードコードではなく実データから）
  const currentDocStatus = c.type === "invoice"
    ? project?.invoiceStatus || "作成中"
    : project?.docs?.[c.type] || "作成中";
  const taxRate = c.taxRate ?? 0.1;
  const taxPct = Math.round(taxRate * 100);
  const totals = calc(c.lines, taxRate);
  // 帳票画面（projectDocPreviewFooterHtml の sendBlocked）と同じ基準: 捺印フローがあり、かつ
  // 未承認だと送付をブロックする種別（docTypeBlocksSendWhenUnapproved。見積書は対象外）は
  // 承認済みでなければPDF出力不可。このコンポーザーはPDF出力時に printDocument へ渡す前段で
  // 帳票側と同じチェックを素通りしてしまっていたため、ここでも揃える。
  const composerSendBlocked =
    docTabHasSealWorkflow(c.type) &&
    docTypeBlocksSendWhenUnapproved(c.type) &&
    getDocSealStatus(c.projectId, c.type) !== "承認済";
  return `
    <div class="composer-backdrop" data-close-composer="1"></div>
    <aside class="composer-panel doc-composer-modal">
      <div class="composer-head">
        <div><h3>${c.title}</h3><p>下書き / 送付準備 / 送付済みまでここで完結</p></div>
        <button class="inline-btn ghost" data-close-composer="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="form-grid">
          <label>宛名（会社名）<input data-field="company" value="${escapeHtmlAttr(c.company)}" /></label>
          <label>担当者名<input data-field="person" value="${escapeHtmlAttr(c.person)}" /></label>
          <label>件名<input data-field="subject" value="${escapeHtmlAttr(c.subject)}" /></label>
          <label>${c.issueDateLabel}<input type="date" data-field="issueDate" value="${escapeHtmlAttr(c.issueDate)}" /></label>
          <label>${c.dueDateLabel}<input type="date" data-field="dueDate" value="${escapeHtmlAttr(c.dueDate)}" /></label>
          <label>税率<select data-field="taxRate">
            <option value="0.1" ${taxRate === 0.1 ? "selected" : ""}>10%（標準）</option>
            <option value="0.08" ${taxRate === 0.08 ? "selected" : ""}>8%（軽減税率）</option>
            <option value="0" ${taxRate === 0 ? "selected" : ""}>非課税（0%）</option>
          </select></label>
        </div>
        <div class="doc-meta">
          <span>状態: ${escapeHtml(currentDocStatus)}</span>
          <span>作成者: ${escapeHtml(ownerName(project || {}))}</span>
          <span>自動保存: ON</span>
          ${c.templateName ? `<span>テンプレート: ${escapeHtml(c.templateName)}</span>` : ""}
        </div>
        <h4>明細</h4>
        ${c.lines
          .map(
            (line, idx) => `
          <div class="line-item">
            <input data-line-index="${idx}" data-line-field="item" value="${escapeHtmlAttr(line.item)}" placeholder="項目名" />
            <input data-line-index="${idx}" data-line-field="qty" type="text" inputmode="numeric" value="${escapeHtmlAttr(line.qty)}" />
            <input data-line-index="${idx}" data-line-field="price" type="text" inputmode="numeric" value="${escapeHtmlAttr(line.price)}" />
            <div class="line-total">${formatYenPlain(billingLineAmountExTax(line))} 円</div>
            <button class="inline-btn ghost" data-remove-line="${idx}">削除</button>
          </div>
        `
          )
          .join("")}
        <button class="inline-btn" data-add-line="1">行を追加</button>
        <div class="totals">
          <div><span>小計</span><strong>${formatYenPlain(totals.subtotal)} 円</strong></div>
          <div><span>消費税（${taxPct}%）</span><strong>${formatYenPlain(totals.tax)} 円</strong></div>
          <div><span>${c.type === "quote" ? "合計" : "請求金額合計"}</span><strong>${formatYenPlain(totals.total)} 円</strong></div>
        </div>
        ${c.type === "invoice" ? `<label>振込先メモ<textarea data-field="bankMemo">${escapeHtml(c.bankMemo)}</textarea></label>` : ""}
        <label>備考<textarea data-field="note">${escapeHtml(c.note)}</textarea></label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-doc-save="draft">下書き保存</button>
        <button class="inline-btn ghost" data-print-doc="1"${composerSendBlocked ? ' disabled title="捺印申請が承認されるとPDF出力が可能になります"' : ""}>PDF出力</button>
        <button class="inline-btn" data-doc-save="ready">送付準備OKにする</button>
        ${c.type === "invoice" ? `<button class="inline-btn" data-doc-save="sent">送付済みにする</button>` : ""}
      </div>
    </aside>
  `;
}


// ────────────────────────────────────────────────────────────────
// 原価フォーム（サイドパネル）
// ────────────────────────────────────────────────────────────────
// COST_CATEGORIES は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。

/** 旧カテゴリ→新カテゴリの読み替え（広告費→媒体費・外注費→制作費）。表示・保存の両方で正規化し、
 *  DB 一括移行の有無に関わらず新4択で一貫させる。旧値のみ読み替え、それ以外は「その他」に丸めず
 *  そのまま返す（空だけ既定の媒体費）。※「販管費」はジャンルではなく“入れ先バケット”に一本化。 */
const COST_CATEGORY_LEGACY_MAP = { "広告費": "媒体費", "外注費": "制作費" };
function normalizeCostCategoryLabel(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "媒体費";
  return COST_CATEGORY_LEGACY_MAP[s] || s;
}

/** フォーム・一覧・data-* で「案件未割当」を表す UI 専用 ID（DB の案件とは無関係） */
const COST_UNASSIGNED_PROJECT_UI_ID = "__cost_unassigned__";
const COST_UNASSIGNED_PROJECT_LABEL = "（案件未割当）";

/** 旧タブキー（pending / thisMonth）を現行のフィルターに読み替える */
function normalizedUiCostFilterTab() {
  const raw = uiState.costFilter || "all";
  if (raw === "pending") return "unassigned";
  if (raw === "thisMonth") return "all";
  return raw;
}

/** 原価一覧のフラット行が「案件未割当」タブに含める対象か（販管費・調整用バケットは除外） */
function isCostRowProjectUnassigned(c) {
  if (String(c._projectId || "") !== COST_UNASSIGNED_PROJECT_UI_ID) return false;
  if (isSalesExpenseCostRow(c)) return false;
  if (isAdjustmentCostRow(c)) return false;
  return true;
}

/** 案件未割当かつ割当依頼先が設定済み（「割当依頼」タブ用） */
function isCostRowDelegationRequested(c) {
  if (!isCostRowProjectUnassigned(c)) return false;
  const d = c.delegatedSalesUserDbId;
  return d != null && Number.isFinite(Number(d)) && Number(d) > 0;
}

/** 「原価割当」一覧: 案件未割当かつ、割当依頼先がログインユーザー（DB id 一致）の原価のみ */
function costAllocationDelegatedQueueRows() {
  const orphans = ensureOrphanCosts();
  const myDb = currentUser?.dbId;
  if (myDb == null || !Number.isFinite(Number(myDb))) return [];
  return orphans.filter((c) => {
    const d = c.delegatedSalesUserDbId;
    if (d == null || !Number.isFinite(Number(d))) return false;
    return Number(d) === Number(myDb);
  });
}

/** 原価割当ページで「割当作業側（CSビュー）」を見るユーザーか。
 *  マスターは常に割当ビュー。それ以外は職種を優先:
 *  CS→割当ビュー／営業→照会ビュー（システム権限が管理者でも営業職なら照会側）。
 *  どちらでもない職種は管理者/経理のみ割当ビュー */
function userSeesCostAllocationCsView() {
  const sys = systemRoleOfUser(currentUser);
  if (sys === "マスター") return true;
  const roles =
    Array.isArray(currentUser?.roles) && currentUser.roles.length ? currentUser.roles : [currentUser?.role];
  if (roles.includes("CS")) return true;
  if (roles.includes("営業")) return false;
  if (sys === "管理者") return true;
  return roles.includes("経理");
}

/**
 * CSビューの未割当キューを4層に分ける:
 *   disputes = 営業からの異議（従来どおり最上部にフラット表示・グループに畳まない）
 *   groups   = REM取込の原価を物件名ごとに Map<remBukken, rows>
 *   adGroups = 広告CSV取込（Google/Yahoo/LINE/Meta）の原価をアカウント名ごとに Map<account, rows>
 *              （摘要の「<アカウント名> <媒体ラベル>」から復元。媒体が分かれていても
 *              同じ案件へ紐づく塊なので、ばらばらのフラット行にしない）
 *   others   = 紐づけ先の手がかりを持たない行（手動登録・PDF取込など。従来のフラット表のまま）
 * 営業ビューはこの関数を通らない（従来表示のまま）。
 */
function costAllocationZonedRows(rows) {
  const disputes = [];
  const others = [];
  const groups = new Map();
  const adGroups = new Map();
  for (const c of rows) {
    if (c.disputeNote) {
      disputes.push(c);
      continue;
    }
    if (c.importSource === "rem" && c.remBukken) {
      if (!groups.has(c.remBukken)) groups.set(c.remBukken, []);
      groups.get(c.remBukken).push(c);
      continue;
    }
    const ad = adCsvNoteAccount(c.note);
    if (ad) {
      if (!adGroups.has(ad.account)) adGroups.set(ad.account, []);
      adGroups.get(ad.account).push(c);
      continue;
    }
    others.push(c);
  }
  return { disputes, groups, adGroups, others };
}

/** 原価割当ページの割当キュー行: CSビュー=未割当すべて（異議を先頭）／営業=自分宛の割当依頼のみ */
function costAllocationVisibleRows() {
  if (!userSeesCostAllocationCsView()) return costAllocationDelegatedQueueRows();
  // 生の未割当配列には _projectId が無いため、販管費/調整用バケットだけ note マーカーで除外する
  const rows = ensureOrphanCosts().filter((c) => !isSalesExpenseCostRow(c) && !isAdjustmentCostRow(c));
  return [...rows].sort((a, b) => {
    const ad = a.disputeNote ? 0 : 1;
    const bd = b.disputeNote ? 0 : 1;
    if (ad !== bd) return ad - bd;
    return (costDbNumericId(b) || 0) - (costDbNumericId(a) || 0);
  });
}

// ── 原価割当ページの並び替え（未割当キュー・照会一覧で共用） ──

/** 原価行の並び替え比較値。既定順（未知キー）は null を返し、呼び出し側で元の並びを保つ */
function costRowSortValue(c, key) {
  switch (key) {
    case "no": return costDbNumericId(c) || 0;
    case "owner": return String(c._ownerName || "");
    case "note": return stripCostBucketMarkers(String(c.note || "")).trim();
    case "vendor": return String(c.vendor || "");
    case "project": return String(c._projectName || "");
    case "category": return String(c.category || "");
    case "amount": return Number(c.amount || 0);
    case "targetMonth": return parseProjectBillingMonthYyyymm(String(c.targetMonth || "")) || 0;
    case "payStatus": return costPayStatusDisplayLabel(c);
    default: return null;
  }
}

/** 原価行を key/dir（asc|desc）で並び替える。同値は原価ID降順で安定させる */
function sortCostRowsBy(rows, key, dir) {
  if (costRowSortValue({}, key) === null) return rows; // 既定順はそのまま
  const asc = dir !== "desc";
  return [...rows].sort((a, b) => {
    const av = costRowSortValue(a, key);
    const bv = costRowSortValue(b, key);
    const d = typeof av === "string" ? av.localeCompare(String(bv), "ja") : av < bv ? -1 : av > bv ? 1 : 0;
    if (d !== 0) return asc ? d : -d;
    return (costDbNumericId(b) || 0) - (costDbNumericId(a) || 0);
  });
}

/** 原価カード（案件・物件グループ）の並び: 計上月の新しい順、同じ月なら新しく登録された原価を含むカードが上。
 *  カードに複数月の原価があるときは最新の計上月で比べる */
function compareCostCardRowsNewestFirst(aRows, bRows) {
  const latestOf = (rs, key) => Math.max(0, ...rs.map((c) => Number(costRowSortValue(c, key)) || 0));
  return latestOf(bRows, "targetMonth") - latestOf(aRows, "targetMonth") || latestOf(bRows, "no") - latestOf(aRows, "no");
}

/** 並び替えUI（項目プルダウン＋昇順/降順トグル）。
 *  SP では未割当キューの表がカード化して列ヘッダーが消えるため、ヘッダークリックと同じ状態をここからも変えられるようにする。
 *  PC は列ヘッダーで並び替えられるため出さない（--sp-only） */
function costSortControlHtml({ prefix, options, key, dir }) {
  const canFlip = costRowSortValue({}, key) !== null;
  return `
    <div class="cost-mgmt-search-field cost-mgmt-search-field--sort cost-mgmt-search-field--sp-only">
      <span class="cost-mgmt-search-label">並び替え</span>
      <span class="cost-sort-control">
        <select class="pj-input" data-search-dropdown-off="1" data-${prefix}-sort-key="1" aria-label="並び替える項目">
          ${options
            .map(([v, label]) => `<option value="${escapeHtmlAttr(v)}" ${v === key ? "selected" : ""}>${escapeHtml(label)}</option>`)
            .join("")}
        </select>
        <button type="button" class="inline-btn ghost cost-sort-dir-btn" data-${prefix}-sort-dir="1" ${canFlip ? "" : "disabled"} title="昇順・降順を切り替え">${dir === "asc" ? "昇順 ▲" : "降順 ▼"}</button>
      </span>
    </div>`;
}

/** 原価割当（未割当キュー）の支払先フィルタ・並び替え状態 */
function ensureCostAllocListState() {
  if (!uiState.costAllocFilter || typeof uiState.costAllocFilter !== "object") uiState.costAllocFilter = { vendor: "" };
  if (typeof uiState.costAllocFilter.bukken !== "string") uiState.costAllocFilter.bukken = "";
  // 表示形式（CSビューのみ意味を持つ）: cards=予測グループ化（既定）／table=従来の全行フラット表。
  // 照会ページのcostReview.viewModeとは別状態（グループの単位が物件/アカウントで案件単位ではないため）
  if (uiState.costAllocFilter.viewMode !== "cards" && uiState.costAllocFilter.viewMode !== "table") {
    uiState.costAllocFilter.viewMode = "cards";
  }
  if (!uiState.costAllocSort || typeof uiState.costAllocSort !== "object") uiState.costAllocSort = { key: "default", dir: "desc" };
  return { flt: uiState.costAllocFilter, srt: uiState.costAllocSort };
}

/** 未割当キューの並び替え項目（既定＝異議を先頭にした新しい順） */
const COST_ALLOC_SORT_OPTIONS = [
  ["default", "既定"],
  ["targetMonth", "計上月"],
  ["vendor", "発注先"],
  ["amount", "金額"],
  ["category", "会計区分"]
];

/** チェックボックス選択の対象行。CSビュー×カード表示では物件グループに畳まれた行を除く
 *  （グループの行はヘッダーの一括操作で扱う。ここに含めると画面に無いチェックが
 *  一括割当バーから見えないまま実行されてしまう）。一覧表示（table）では全行が対象
 *  （画面どおりフラットに並ぶため、グループを除外する理由が無い＝複数グループ横断選択も可能）。 */
function costAllocationSelectableRows() {
  const rows = costAllocationVisibleRows();
  if (!userSeesCostAllocationCsView()) return rows;
  const { flt } = ensureCostAllocListState();
  if (flt.viewMode === "table") return rows;
  const { disputes, others } = costAllocationZonedRows(rows);
  return [...disputes, ...others];
}

/** ヘッダー「すべて選択」の対象行。表示と同じ絞り（15d経由の計上月・発注先フィルタ）を適用し、
 *  カード表示（既定）ではCSビューの物件グループに畳まれた行を除く。表示に無い行が選ばれて
 *  「見えないまま一括割当される」のを防ぐため、レンダラ側の flat 行と同じ集合にする。
 *  一覧表示ではグループも含め画面どおり全行が対象。 */
function costAllocationCheckAllRows() {
  const { flt } = ensureCostAllocListState();
  let rows = costAllocationVisibleRows();
  // 原価割当（マスター）の「未割当」ビュー（15d）は計上月フィルタを持つ。表示と同じ絞りを適用する。
  // 以前はページ名が cost-management のときだけ絞っていたが、このビューは cost-allocation にしか出ないため
  // 一度も効かず、計上月で絞っても他の月の行まで選ばれていた（2026-10 レビュー指摘。533件誤割当と同じ型）
  const m = costAllocationActiveMonthFilter();
  if (m) {
    const want = parseProjectBillingMonthYyyymm(m.replace(/-/g, "/"));
    rows = rows.filter((c) => parseProjectBillingMonthYyyymm(c.targetMonth || "") === want);
  }
  if (flt.vendor) rows = rows.filter((c) => String(c.vendor || "").trim() === flt.vendor);
  if (userSeesCostAllocationCsView() && flt.viewMode !== "table") {
    const { disputes, others } = costAllocationZonedRows(rows);
    rows = [...disputes, ...others];
  }
  return rows;
}

/** 未割当原価を案件へ割り当てる共通ループ（チェック一括バーと物件グループの共用）。
 *  送信フィールドの列挙をここへ一本化する（経路ごとにコピーすると、
 *  フィールド追加時に片側だけ直して「その経路だけ値が落ちる」事故になる） */
async function assignOrphanCostsToProject(costIdUis, assignPid) {
  let ok = 0;
  let fail = 0;
  for (const costIdUi of costIdUis) {
    const pl = findCostPlacementByUiId(costIdUi);
    const c = pl?.cost;
    if (!c || !pl.orphan) {
      fail++;
      continue;
    }
    try {
      await persistCostToServerUpdate(
        costIdUi,
        {
          category: c.category,
          vendor: c.vendor,
          amount: Number(c.amount || 0),
          consumptionTax: costConsumptionTaxYen(c),
          targetMonth: c.targetMonth,
          payStatus: c.payStatus,
          dueDate: c.dueDate || "",
          note: c.note || ""
        },
        assignPid
      );
      ok++;
    } catch {
      fail++;
    }
  }
  // REM原価の割当は記憶（前月の割当先ヒント）を変えるため、成功があれば取り直す
  if (ok) invalidateRemAllocHints();
  return { ok, fail };
}

function pruneCostAllocSelectionToValid() {
  if (!uiState.costAllocSelected || typeof uiState.costAllocSelected !== "object") {
    uiState.costAllocSelected = {};
    return;
  }
  // 今の絞り込み（計上月・発注先）で画面に出ている行だけを有効とする。絞り込みを変えたあとに
  // 見えなくなった選択が残ると、「まとめて割当」で見えない行まで割り当ててしまう
  const valid = new Set(
    costAllocationCheckAllRows().map((c) => costMgmtSelectionKey(COST_UNASSIGNED_PROJECT_UI_ID, c.id))
  );
  for (const k of Object.keys(uiState.costAllocSelected)) {
    if (!valid.has(k)) delete uiState.costAllocSelected[k];
  }
}

/** 原価割当のまとめて割当・編集フォーム用: 請求月で絞らず全案件 */
function projectsForCostAllocationAssignPick() {
  return projects;
}

function costAllocBulkProjectPickOptionsHtml(selectedOverride) {
  // selectedOverride: 物件グループの割当先セレクト用（グループごとに選択値を分けて保持する）。
  // 省略時は従来どおり一括割当バーの選択値（uiState.costAllocBulkProjectPick）を使う
  const sel = String(selectedOverride !== undefined ? selectedOverride : uiState.costAllocBulkProjectPick || "").trim();
  let html = `<option value="">${escapeHtml("（案件を選ぶ）")}</option>`;
  // 案件が決まらない原価の振り分け先として一番使う頻度が高いため、販管費を先頭に固定表示する
  html += `<option value="${COST_SALES_EXPENSE_PROJECT_UI_ID}" ${sel === COST_SALES_EXPENSE_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_SALES_EXPENSE_PROJECT_LABEL)}</option>`;
  for (const p of projectsForCostAllocationAssignPick()) {
    html += `<option value="${escapeHtmlAttr(p.id)}" ${p.id === sel ? "selected" : ""} data-option-search="${escapeHtmlAttr(
      projectAccountingPickSearchBlob(p)
    )}">${escapeHtml(projectAccountingPickLabel(p))}</option>`;
  }
  return html;
}

const COST_MGMT_TAB_KEY_SET = new Set(["all", ...COST_CATEGORIES]);

function normalizeCostMgmtTabKeys(raw) {
  const arr = (Array.isArray(raw) ? raw : ["all"]).map(String).filter((k) => COST_MGMT_TAB_KEY_SET.has(k));
  const uniq = [...new Set(arr)];
  if (!uniq.length || uniq.includes("all")) return ["all"];
  return uniq;
}

/** 原価管理: 会計区分の絞り込み（一覧はプルダウンで1つ選ぶ。配列は複数でもOR＝いずれかに該当）。
 *  未割当・仮原価は検索バーのプルダウン（costMgmtFilter）側 */
function costMgmtRowMatchesTabSelection(row, tabKeys) {
  const keys = normalizeCostMgmtTabKeys(tabKeys);
  if (keys.includes("all")) return true;
  return keys.some((k) => row.category === k);
}

/** 原価登録一覧: 会計区分の絞り込みプルダウン（値は costMgmtTabKeys に1つだけ入れる。旧タブの複数選択が残っていれば「すべて」表示） */
function costMgmtCategorySelectHtml(selectedKeys) {
  const keys = normalizeCostMgmtTabKeys(selectedKeys);
  const sel = keys.length === 1 ? keys[0] : "all";
  return `<label class="cost-mgmt-search-field cost-mgmt-search-field--lock">
          <span class="cost-mgmt-search-label">会計区分</span>
          <select class="pj-input" data-search-dropdown-off="1" data-cost-mgmt-filter-category>
            ${["all", ...COST_CATEGORIES]
              .map((k) => `<option value="${escapeHtmlAttr(k)}" ${k === sel ? "selected" : ""}>${escapeHtml(k === "all" ? "すべて" : k)}</option>`)
              .join("")}
          </select>
        </label>`;
}

/** 原価管理・案件詳細の種別タブ（すべて／未割当／各種別を網羅） */
function costFilterBarHtml(activeFilterKey) {
  const tabs = [
    { key: "all", label: "すべて" },
    { key: "unassigned", label: "未割当" },
    ...COST_CATEGORIES.map((cat) => ({ key: cat, label: cat }))
  ];
  return `
    <div class="cost-filter-bar">
      ${tabs
        .map(
          (t) =>
            `<button type="button" class="cost-filter-tab ${activeFilterKey === t.key ? "active" : ""}" data-cost-filter="${escapeHtmlAttr(String(t.key))}">${escapeHtml(t.label)}</button>`
        )
        .join("")}
    </div>`;
}

// 案件に紐づけない“入れ先バケット”（DBは project_id NULL・備考先頭のマーカーで種別を表す）。
// 「販管費」は旧「営業費」を改名したもの（内部識別子は SALES_EXPENSE のまま・データは後方互換）。
const COST_SALES_EXPENSE_PROJECT_UI_ID = "__cost_sales_expense__"; // 販管費バケット
const COST_SALES_EXPENSE_PROJECT_LABEL = "販管費";
const COST_SALES_EXPENSE_NOTE_PREFIX = "【販管費】";
const COST_SALES_EXPENSE_NOTE_PREFIX_LEGACY = "【営業費】"; // 旧マーカー（既存データ後方互換）

// 調整用バケット（案件に紐づかない、かつ販管費でもない原価の置き場。清算前提の一時置きではなく恒久）
const COST_ADJUSTMENT_PROJECT_UI_ID = "__cost_adjustment__";
const COST_ADJUSTMENT_PROJECT_LABEL = "調整用";
const COST_ADJUSTMENT_NOTE_PREFIX = "【調整】";

/** 案件を持たない入れ先バケット（未割当／販管費／調整用）の UI-ID か */
function isNoProjectBucketUiId(id) {
  return (
    id === COST_UNASSIGNED_PROJECT_UI_ID ||
    id === COST_SALES_EXPENSE_PROJECT_UI_ID ||
    id === COST_ADJUSTMENT_PROJECT_UI_ID
  );
}

/** 表示用: 先頭の入れ先マーカー（販管費/旧営業費/調整）を1つ除去 */
function stripCostBucketMarkers(note) {
  let n = String(note ?? "").trim();
  for (const pre of [
    COST_SALES_EXPENSE_NOTE_PREFIX,
    COST_SALES_EXPENSE_NOTE_PREFIX_LEGACY,
    COST_ADJUSTMENT_NOTE_PREFIX
  ]) {
    if (n.startsWith(pre)) {
      n = n.slice(pre.length).replace(/^\s+/, "").trim();
      break;
    }
  }
  return n;
}

function isSalesExpenseCostRow(c) {
  if (!c) return false;
  if (c._costSalesExpense) return true;
  const n = String(c.note || "").trimStart();
  return n.startsWith(COST_SALES_EXPENSE_NOTE_PREFIX) || n.startsWith(COST_SALES_EXPENSE_NOTE_PREFIX_LEGACY);
}

function ensureSalesExpenseNote(note, salesExpense) {
  const n = stripCostBucketMarkers(note);
  if (salesExpense) return n ? `${COST_SALES_EXPENSE_NOTE_PREFIX} ${n}` : COST_SALES_EXPENSE_NOTE_PREFIX;
  return n;
}

function isAdjustmentCostRow(c) {
  if (!c) return false;
  if (c._costAdjustment) return true;
  return String(c.note || "").trimStart().startsWith(COST_ADJUSTMENT_NOTE_PREFIX);
}

function ensureAdjustmentNote(note, adjustment) {
  const n = stripCostBucketMarkers(note);
  if (adjustment) return n ? `${COST_ADJUSTMENT_NOTE_PREFIX} ${n}` : COST_ADJUSTMENT_NOTE_PREFIX;
  return n;
}

function salesExpenseCostProjectProxy() {
  return {
    id: COST_SALES_EXPENSE_PROJECT_UI_ID,
    dbId: null,
    _costUnassignedBucket: true,
    _costSalesExpense: true
  };
}

function adjustmentCostProjectProxy() {
  return {
    id: COST_ADJUSTMENT_PROJECT_UI_ID,
    dbId: null,
    _costUnassignedBucket: true,
    _costAdjustment: true
  };
}

function isSalesExpenseCostBucketRef(project) {
  return Boolean(project?._costSalesExpense || project?.id === COST_SALES_EXPENSE_PROJECT_UI_ID);
}

function isAdjustmentCostBucketRef(project) {
  return Boolean(project?._costAdjustment || project?.id === COST_ADJUSTMENT_PROJECT_UI_ID);
}

function ensureOrphanCosts() {
  if (!uiState.orphanCosts) uiState.orphanCosts = [];
  return uiState.orphanCosts;
}

/** サーバー POST ／ フロガード用の未割当バケット参照 */
function unassignedCostProjectProxy() {
  return { id: COST_UNASSIGNED_PROJECT_UI_ID, dbId: null, _costUnassignedBucket: true };
}

function isUnassignedCostBucketRef(project) {
  return Boolean(project?._costUnassignedBucket || isNoProjectBucketUiId(project?.id));
}

// 原価箱: 「担当営業は分かっているが、まだ請求書等が無く正式案件に付けられない」原価の
// 一時置き場。実体は amount=0 の通常案件（api/cost-boxes.js が作成）。粗利には
// 担当営業の原価としてそのまま乗る点が「未割当」バケットと異なる（意図的な設計）。
const COST_BOX_NAME_PREFIX = "【原価箱】";

/** 4〜3月の年度区切りで、指定日が属する期の開始年（例: 2027/02→2026, 2027/04→2027）を返す */
function costBoxFiscalYearStartYear(d = new Date()) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? y : y - 1;
}

function isCostBoxProject(project) {
  return String(project?.name || "").startsWith(COST_BOX_NAME_PREFIX);
}

/** 原価箱に付く固定タグ（api/cost-boxes.js の INSERT と対。案件一覧のタグ絞り込みで拾える） */
const COST_BOX_TAG = "#原価箱";

/** 案件一覧で原価箱を隠すか。実案件ではない0円の置き場なので既定は非表示。
 *  ただしタグで #原価箱 を選択・案件名検索がヒットした場合は出す
 *  （原価の付け替え先である箱に辿り着けなくなるのを防ぐ）。集計・粗利は隠さない側の話なので
 *  ここでは一覧の行だけを対象にする。 */
function projectHiddenAsCostBox(project, filters) {
  if (!isCostBoxProject(project)) return false;
  if ((Array.isArray(filters?.filterTags) ? filters.filterTags : []).includes(COST_BOX_TAG)) return false;
  const nameQuery = String(filters?.name || "").trim().toLowerCase();
  if (nameQuery && String(project?.name || "").toLowerCase().includes(nameQuery)) return false;
  return true;
}

/** 指定営業の今期の原価箱案件を find-or-create（`POST /api/cost-boxes`、サーバー側で冪等）し、
 *  取得した案件をローカル `projects` へ反映して UI-ID を返す。原価配分ページの「箱に入れる」と
 *  お名前ドットコム取込の自動割当の両方から共通で使う。失敗時は { ok:false, error } を返す。 */
async function ensureCostBoxProjectUiId(salesOwnerUiId, fiscalYearStartYear) {
  const salesOwnerDbId = Number(String(salesOwnerUiId ?? "").replace("u", ""));
  if (!Number.isFinite(salesOwnerDbId) || salesOwnerDbId <= 0) {
    return { ok: false, error: "営業を選んでください" };
  }
  const res = await apiFetch("/api/cost-boxes", {
    method: "POST",
    body: { salesOwnerId: salesOwnerDbId, fiscalYearStartYear }
  });
  if (!res.ok || !res.data?.project) {
    return { ok: false, error: typeof res.data?.error === "string" ? res.data.error : "原価箱の作成に失敗しました" };
  }
  const boxRow = res.data.project;
  let boxUiId = projects.find((p) => p.dbId === boxRow.id)?.id;
  if (!boxUiId) {
    boxUiId = `p${boxRow.id}`;
    const boxClientId = boxRow.client_id != null ? `c${boxRow.client_id}` : "";
    const boxSalesOwnerId = boxRow.sales_owner_id != null ? `u${boxRow.sales_owner_id}` : "";
    // 通常の案件新規作成（05-cost-expense-data.js の project オブジェクト構築）と同じ形に揃える。
    // 一部フィールドが欠けたまま projects に push すると、案件一覧・予算進捗の描画が
    // 前提にしているプロパティが undefined になり、その行以降が描画されず「どこにも
    // 表示されない」ように見える不具合の原因になるため
    projects.unshift({
      id: boxUiId,
      dbId: boxRow.id,
      clientId: boxClientId,
      name: boxRow.name,
      client: getClientById(boxClientId)?.name || "未設定クライアント",
      person: "",
      clientBranchName: "",
      owner: getUserById(boxSalesOwnerId)?.name || "",
      amount: 0,
      billingMonth: boxRow.billing_month || "",
      stale: "",
      updatedAt: "",
      currentStage: boxRow.current_stage || "contracts",
      nextAction: boxRow.next_action || "",
      contractRequired: false,
      projectType: "existing-client",
      useOrderFlow: false,
      orderStatus: "見積もり",
      progressStatus: boxRow.progress_status || "未着手",
      serviceCategory: "",
      contractDone: false,
      recurringBilling: false,
      salesOwnerId: boxSalesOwnerId,
      accountingOwnerId: boxRow.accounting_owner_id != null ? `u${boxRow.accounting_owner_id}` : "",
      assignedUserId: boxRow.assigned_user_id != null ? `u${boxRow.assigned_user_id}` : "",
      currentOwnerRole: "営業",
      invoiceStatus: boxRow.invoice_status || "未作成",
      handoffStatus: "未引き継ぎ",
      handoffAt: "",
      handedBy: "",
      handoffNote: "",
      previousAssignedUserId: "",
      paymentRecord: null,
      paymentConfirmedBy: "",
      paymentConfirmedAt: "",
      mailTo: "",
      mailCc: "",
      unlockUntil: "",
      plannedInvoiceDate: "",
      dueDate: "",
      billingDate: "",
      tag: boxRow.tags || "",
      docs: { nda: "", contract: "", quote: "", order: "", payment: "" },
      costs: []
    });
  }
  return { ok: true, uiId: boxUiId };
}

/** 原価箱（案件未定・担当を選ぶ）の疑似選択肢ID。値は "<接頭辞><営業のUI-ID>"（例: __costbox_owner_u42）。
 *  実案件のUI-ID（p<dbId>）・入れ先バケットのUI-ID（__cost_unassigned__ 等）と衝突しない形にしてある。
 *  選んだ時点ではまだ実案件は存在せず、登録直前（wizard適用・原価フォーム保存）に
 *  ensureCostBoxProjectUiId で find-or-create して実際の案件IDへ解決する。 */
const COST_BOX_PICK_PREFIX = "__costbox_owner_";
function costBoxPickId(salesOwnerUiId) {
  return `${COST_BOX_PICK_PREFIX}${salesOwnerUiId}`;
}
function costBoxPickOwnerUiId(pickId) {
  const s = String(pickId ?? "");
  return s.startsWith(COST_BOX_PICK_PREFIX) ? s.slice(COST_BOX_PICK_PREFIX.length) : "";
}

/** 原価箱の候補となる営業一覧。他人名義の箱を選べるのは経理/CSティア・マスター・管理者のみ
 *  （api/cost-boxes.js の403判定と同じ基準）で、それ以外は自分（営業職）だけが対象になる。
 *  restrictToSelf=true（営業の請求書取込＝salesMode）は cost-management ティアでも自分限定にする
 *  （計上先の案件自体が costWizardSalesModeProjects で自分の担当分に絞られているのと揃える）。 */
function costBoxPickCandidateOwners(restrictToSelf) {
  const selfOnly = restrictToSelf === true || !userSeesCostAllocationCsView();
  if (!selfOnly) return costBoxSalesOwnerOptions();
  const myRoles =
    Array.isArray(currentUser?.roles) && currentUser.roles.length ? currentUser.roles : [currentUser?.role];
  if (!myRoles.includes("営業")) return [];
  const myDb = currentUser?.dbId;
  if (myDb == null || !Number.isFinite(Number(myDb))) return [];
  const me = getUserById(`u${myDb}`);
  return me ? [me] : [];
}

/** 計上先プルダウンに差し込む「原価箱」optgroup。案件一覧の手前（未割当等のバケットの直後）に置き、
 *  案件が大量にあってもすぐ見つかる位置にする。owners が空なら空文字（optgroup自体を出さない）。 */
function costBoxPickOptionsHtml(selectedPick, owners) {
  if (!owners.length) return "";
  const opts = owners
    .map((u) => {
      const val = costBoxPickId(u.id);
      return `<option value="${escapeHtmlAttr(val)}" ${selectedPick === val ? "selected" : ""}>${escapeHtml(u.name || "")}</option>`;
    })
    .join("");
  return `<optgroup label="原価箱（案件未定・担当を選ぶ）">${opts}</optgroup>`;
}

/** 通常の案件一覧も「原価箱」と同じく optgroup でまとめる。原価箱グループだけ担当者名が
 *  一段インデントされ、案件だけ地の文（インデント無し）になっていると階層が非対称で
 *  分かりにくいという指摘への対応。optionsHtml が空なら空文字（optgroup自体を出さない）。 */
function costProjectListOptgroupHtml(optionsHtml) {
  return optionsHtml ? `<optgroup label="案件一覧">${optionsHtml}</optgroup>` : "";
}

/** 原価箱グループの候補（owners）に該当する営業の箱は、すでに実体化済み（find-or-create済み）でも
 *  「原価箱」グループの選択肢と全く同じ行き先を指す。プレーンな案件一覧にも実名で出ていると、
 *  同じ相手への割当先が2箇所に重複して見え紛らわしいため、案件一覧側からはその分だけ間引く
 *  （owners に含まれない他人の原価箱は、それを選ぶ手段がグループ側に無いので一覧に残す）。
 *  keepPickId は現在の選択中の値（あれば）で、間引き対象でも必ず残す
 *  （消すと <select> がどのoptionにも一致しなくなり、選択済みの案件が見た目上「未割当」等に
 *  すり替わって見える＝実際の状態と表示がズレる事故になるため）。 */
function excludeCandidateCostBoxProjects(projectList, owners, keepPickId) {
  if (!owners.length) return projectList;
  const ownerIds = new Set(owners.map((u) => u.id));
  return projectList.filter((p) => p.id === keepPickId || !(isCostBoxProject(p) && ownerIds.has(p.salesOwnerId)));
}

/** 取込プレビューの計上先プルダウン「案件一覧」optgroup の中身を、描画1回につき1度だけ組み立てる。
 *  重い部分（案件ごとの表示ラベル・検索文字列・★判定・HTMLエスケープ）を案件ごとに1回だけ計算し、
 *  行ごとには excludeCandidateCostBoxProjects（選択中の原価箱案件は残す規則）で並べ直して selected を
 *  付けるだけにする。生成HTMLは行ごとに作った場合と同一。★はグローバル対象月で決まる。 */
function costImportProjectOptionListBuilder(projectList, owners, targetMonth) {
  const optionById = new Map(
    projectList.map((p) => {
      const marker = projectBillingMonthMatchesCostTargetMonth(p, targetMonth) ? "★ " : "　";
      const head = `<option value="${escapeHtmlAttr(p.id)}" `;
      const tail = ` data-option-search="${escapeHtmlAttr(projectAccountingPickSearchBlob(p))}">${marker}${escapeHtml(projectAccountingPickLabel(p))}</option>`;
      return [p.id, { html: head + tail, selectedHtml: head + "selected" + tail }];
    })
  );
  const htmlByPick = new Map();
  return {
    htmlFor(pidPick) {
      // 同じ選択値の行は同じ文字列（開いた行ごとに作り直さない）
      if (!htmlByPick.has(pidPick)) {
        htmlByPick.set(
          pidPick,
          excludeCandidateCostBoxProjects(projectList, owners, pidPick)
            .map((p) => (p.id === pidPick ? optionById.get(p.id).selectedHtml : optionById.get(p.id).html))
            .join("")
        );
      }
      return htmlByPick.get(pidPick);
    },
    /** 選択中の案件1件分だけ（遅延生成 select の初期表示用。案件でなければ空） */
    selectedOnlyHtmlFor(pidPick) {
      return optionById.get(pidPick)?.selectedHtml || "";
    }
  };
}

// ---- 取込プレビューの計上先プルダウン: 案件一覧の遅延生成 ----
// 129行×約2,400案件を全行の <select> に持たせると31万 option（約36MBのHTML）になり、描画・スクロール・
// 再描画のたびにブラウザが固まる。行の初期表示は「選択中の案件1件＋バケット＋原価箱」だけにし、
// 開いた行（検索付きプルダウンの sd-before-open／native select の focus・mousedown）で全案件を流し込む。
// 値を読む側（syncCostImportPreviewRowsFromDom・登録）は sel.value しか見ないので影響しない。
// 値を書く側（同アカウント名への一括反映）は書く前に costImportFillLazyProjectOptions を通す。
let costImportLazyProjectOptions = null;

function costImportFillLazyProjectOptions(sel) {
  if (!(sel instanceof HTMLSelectElement) || !("lazyProjectOptions" in sel.dataset)) return;
  if (!costImportLazyProjectOptions) return;
  const pidPick = sel.value;
  const group = sel.querySelector("optgroup[data-lazy-project-group]");
  if (!group) return;
  group.innerHTML = costImportLazyProjectOptions.htmlFor(pidPick);
  delete sel.dataset.lazyProjectOptions;
  sel.value = pidPick;
}

/** 値を書き込む前に、その値の option が無ければ1件だけ足す（全案件は流し込まない）。
 *  同アカウント名の一括反映は最大で全行に及ぶので、行ごとに全案件を入れると元の巨大DOMに戻ってしまう */
function costImportEnsureLazyProjectOption(sel, value) {
  if (!(sel instanceof HTMLSelectElement) || !("lazyProjectOptions" in sel.dataset)) return;
  if (!costImportLazyProjectOptions || !value) return;
  if ([...sel.options].some((o) => o.value === value)) return;
  const group = sel.querySelector("optgroup[data-lazy-project-group]");
  if (group) group.innerHTML = costImportLazyProjectOptions.selectedOnlyHtmlFor(value);
}

/** 候補を閉じたら「選択中の1件」に戻す（多数の行を順に開いても DOM が行数×案件数に膨らまない） */
function costImportReleaseLazyProjectOptions(sel) {
  if (!(sel instanceof HTMLSelectElement) || "lazyProjectOptions" in sel.dataset) return;
  const group = sel.querySelector("optgroup[data-lazy-project-group]");
  if (!group || !costImportLazyProjectOptions) return;
  const pidPick = sel.value;
  group.innerHTML = costImportLazyProjectOptions.selectedOnlyHtmlFor(pidPick);
  sel.dataset.lazyProjectOptions = "1";
  sel.value = pidPick;
}

document.addEventListener("sd-before-open", (e) => costImportFillLazyProjectOptions(e.target));
document.addEventListener("sd-after-close", (e) => costImportReleaseLazyProjectOptions(e.target));
// 検索付きプルダウンが当たっていない場合（native select）の保険
document.addEventListener("focusin", (e) => costImportFillLazyProjectOptions(e.target));
document.addEventListener("focusout", (e) => costImportReleaseLazyProjectOptions(e.target));

/** 計上先プルダウンで原価箱の疑似選択肢が選ばれている行を、実案件へ解決する（無ければ作成）。
 *  同じ営業への解決は1回にまとめる（複数行が同じ営業を指す取込CSVは珍しくない）。
 *  1件でも解決に失敗したら { ok:false, error } を返す（一部だけ登録して不整合になるのを避ける）。 */
async function resolveCostBoxPick(pickId, cache) {
  const ownerUiId = costBoxPickOwnerUiId(pickId);
  if (!ownerUiId) return { ok: true, uiId: pickId };
  if (cache.has(ownerUiId)) return cache.get(ownerUiId);
  const result = await ensureCostBoxProjectUiId(ownerUiId, costBoxFiscalYearStartYear());
  cache.set(ownerUiId, result);
  return result;
}

function findCostPlacementByUiId(costIdUi) {
  for (let pi = 0; pi < projects.length; pi++) {
    const p = projects[pi];
    const costs = p.costs || [];
    for (let ci = 0; ci < costs.length; ci++) {
      if (costs[ci].id === costIdUi) {
        return { cost: costs[ci], project: p, costIndex: ci, orphan: false };
      }
    }
  }
  const oc = ensureOrphanCosts();
  for (let i = 0; i < oc.length; i++) {
    if (oc[i].id === costIdUi) {
      return { cost: oc[i], project: null, costIndex: i, orphan: true };
    }
  }
  return null;
}

function removeCostPlacementAt(pl) {
  if (pl.orphan) {
    ensureOrphanCosts().splice(pl.costIndex, 1);
  } else if (pl.project?.costs) {
    pl.project.costs.splice(pl.costIndex, 1);
  }
}

/** 案件に紐づいている原価と同じ id が orphan に残っていると「未割当」に二重表示されるため除去する */
function pruneStaleOrphanCostDuplicates() {
  const attached = new Set();
  for (const p of projects) {
    for (const c of p.costs || []) {
      if (c?.id) attached.add(String(c.id));
    }
  }
  const oc = ensureOrphanCosts();
  for (let i = oc.length - 1; i >= 0; i--) {
    if (attached.has(String(oc[i]?.id || ""))) oc.splice(i, 1);
  }
}

function costAssignmentUiIdFromPlacement(pl) {
  if (pl.orphan && isSalesExpenseCostRow(pl.cost)) return COST_SALES_EXPENSE_PROJECT_UI_ID;
  if (pl.orphan && isAdjustmentCostRow(pl.cost)) return COST_ADJUSTMENT_PROJECT_UI_ID;
  return pl.orphan ? COST_UNASSIGNED_PROJECT_UI_ID : pl.project.id;
}

/** 原価の対象月（YYYY/MM または YYYY-MM）と案件の請求月が同じなら true（請求月未設定は一致しない） */
function projectBillingMonthMatchesCostTargetMonth(project, costTargetMonth) {
  const raw = String(costTargetMonth || "").trim().replace(/-/g, "/");
  if (!raw) return true;
  const tn = parseProjectBillingMonthYyyymm(raw);
  if (tn == null) return true;
  const bm = project?.billingMonth;
  if (!bm || !String(bm).trim()) return false;
  const pn = parseProjectBillingMonthYyyymm(String(bm).trim());
  if (pn == null) return false;
  return pn === tn;
}

/** 原価登録で選べる案件。対象月が有効なときは請求月が一致する案件のみ（未設定・不正な対象月は絞り込みなし） */
function projectsEligibleForCostTargetMonth(costTargetMonth) {
  const raw = String(costTargetMonth || "").trim().replace(/-/g, "/");
  if (!raw) return projects;
  if (parseProjectBillingMonthYyyymm(raw) == null) return projects;
  // 原価箱は色々な対象月の原価を受け入れる置き場なので、請求月一致フィルタの対象外にする
  return projects.filter((p) => isCostBoxProject(p) || projectBillingMonthMatchesCostTargetMonth(p, raw));
}

/** 対象月（YYYY/MM・YYYY-MM）の月末日を YYYY-MM-DD で返す。読めなければ空文字 */
function costTargetMonthEndYmd(targetMonth) {
  const m = String(targetMonth || "").trim().replace(/-/g, "/").match(/^(\d{4})\/(\d{1,2})$/);
  if (!m) return "";
  return localDateToYmd(new Date(Number(m[1]), Number(m[2]), 0));
}

/** 行個別の対象月オーバーライドを考慮した実効対象月 */
function rowEffectiveTargetMonth(row, wizardTargetMonth) {
  const ov = String(row?.targetMonthOverride || "").trim().replace(/-/g, "/");
  if (ov && parseProjectBillingMonthYyyymm(ov) != null) return ov;
  return String(wizardTargetMonth || "").trim().replace(/-/g, "/");
}

function costFormPruneProjectToEligibleMonth() {
  const f = uiState.costForm;
  if (!f) return;
  const pid = String(f.projectId || "").trim();
  if (!pid || isNoProjectBucketUiId(pid)) return;
  const elig = projectsEligibleForCostTargetMonth(f.targetMonth);
  if (!elig.some((p) => p.id === pid)) f.projectId = COST_UNASSIGNED_PROJECT_UI_ID;
}

function costImportPreviewPruneProjectPicksToTargetMonth(rev) {
  if (!rev || !Array.isArray(rev.rows)) return;
  const existingIds = new Set(projects.map((p) => p.id));
  for (const row of rev.rows) {
    if (row.importCaptionKind === "consumption_tax") continue;
    const pick = String(row.projectIdPick ?? "").trim();
    if (!pick || isNoProjectBucketUiId(pick) || costBoxPickOwnerUiId(pick)) continue;
    if (!existingIds.has(pick)) row.projectIdPick = "";
  }
}

function costWizardPruneManualProjectToTargetMonth() {
  const w = uiState.costRegWizard;
  if (!w || w.phase !== "manual") return;
  const pid = String(w.manualProjectId ?? "").trim();
  if (!pid || isNoProjectBucketUiId(pid) || costBoxPickOwnerUiId(pid)) return;
  const elig = projectsEligibleForCostTargetMonth(w.targetMonth);
  if (!elig.some((p) => p.id === pid)) w.manualProjectId = "";
}

/** assignTo が "keep" のときはサーバーで project を変更しない */
function resolveAssignToProjectDbPayload(assignTo) {
  if (isNoProjectBucketUiId(assignTo))
    return null;
  const p = projects.find((x) => x.id === assignTo);
  if (!p?.dbId) {
    throw new Error("案件が未取得です。一覧を再読み込みしてからやり直してください。");
  }
  return p.dbId;
}

function flattenAllCostsAcrossProjectsAndOrphans() {
  const fromProjects = projects.flatMap((p) =>
    (p.costs || []).map((c) => ({
      ...c,
      _projectId: p.id,
      _projectName: p.name,
      _projectNo: projectDisplayNoForUi(p)
    }))
  );
  const attachedIds = new Set(fromProjects.map((c) => String(c.id || "")));
  const orphans = ensureOrphanCosts()
    .filter((c) => !attachedIds.has(String(c.id || "")))
    .map((c) => ({
      ...c,
      _projectId: COST_UNASSIGNED_PROJECT_UI_ID,
      _projectName: isSalesExpenseCostRow(c)
        ? COST_SALES_EXPENSE_PROJECT_LABEL
        : isAdjustmentCostRow(c)
          ? COST_ADJUSTMENT_PROJECT_LABEL
          : COST_UNASSIGNED_PROJECT_LABEL,
      _projectNo: ""
    }));
  return fromProjects.concat(orphans);
}

/** 原価管理テーブル用: フィルター・タブ適用後の行と集計用フラット一覧 */
function getCostMgmtTableRows() {
  const flatAll = flattenAllCostsAcrossProjectsAndOrphans();
  const mg = uiState.costMgmtFilter || emptyCostMgmtFilter();
  // 支払先区分フィルタ用の個人名セットは1回だけ構築して全行で使い回す
  const mgCtx = { indivSet: mg.vendorType ? costVendorIndividualNameSet() : null };
  const scoped = flatAll.filter((row) => flattenCostRowMatchesMgmtFilter(row, mg, mgCtx));
  const tabKeys = normalizeCostMgmtTabKeys(uiState.costMgmtTabKeys);
  let filtered = scoped.filter((c) => costMgmtRowMatchesTabSelection(c, tabKeys));

  const srt = uiState.costMgmtSort || { key: "targetMonth", dir: "desc" };
  if (srt.key) {
    const asc = srt.dir !== "desc";
    filtered = [...filtered].sort((a, b) => {
      let av, bv;
      switch (srt.key) {
        case "targetMonth": av = String(a.targetMonth || ""); bv = String(b.targetMonth || ""); break;
        case "vendor": av = String(a.vendor || "").toLowerCase(); bv = String(b.vendor || "").toLowerCase(); break;
        case "project": av = String(a._projectName || "").toLowerCase(); bv = String(b._projectName || "").toLowerCase(); break;
        case "amount": av = costTotalRegisteredYen(a); bv = costTotalRegisteredYen(b); break; // 一覧の支払額（税込）と同じ基準
        case "dueDate": av = String(a.dueDate || ""); bv = String(b.dueDate || ""); break;
        case "paidDate": av = String(a.paidDate || ""); bv = String(b.paidDate || ""); break;
        case "invoiceDate": av = String(a.invoiceDate || ""); bv = String(b.invoiceDate || ""); break;
        case "no": av = costDbNumericId(a) || 0; bv = costDbNumericId(b) || 0; break;
        case "payStatus": av = costPayStatusDisplayLabel(a); bv = costPayStatusDisplayLabel(b); break;
        case "orderStatus": av = costOrderStatusDisplayLabel(a); bv = costOrderStatusDisplayLabel(b); break;
        case "paymentMethod": av = String(a.paymentMethod || ""); bv = String(b.paymentMethod || ""); break;
        default: return 0;
      }
      if (av < bv) return asc ? -1 : 1;
      if (av > bv) return asc ? 1 : -1;
      return 0;
    });
  }

  return { flatAll, scoped, filtered, tabKeys, mg };
}

// name は発注名の最小幅（実際は余った幅をすべて受け取る）。actions は「編集＋差戻取消」が収まる固定幅
// 発注承認（approval）は 2026-10 に計上案件の右へ追加。表の最小幅を変えないよう発注名の既定を同じだけ減らした（発注名は余った幅を受け取る）
const COST_MGMT_COL_DEFAULTS = { no: 56, name: 258, vendor: 140, project: 200, approval: 64, tm: 82, amount: 130, invoice: 78, due: 78, pay: 84, order: 84, actions: 124 };

/** 原価管理一覧のページネーション状態（page はここでクランプして常に有効値に保つ） */
function costMgmtTableState(totalRows) {
  if (!uiState.costMgmtTable || typeof uiState.costMgmtTable !== "object") {
    uiState.costMgmtTable = { page: 1, perPage: 50 };
  }
  const t = uiState.costMgmtTable;
  t.perPage = Number(t.perPage) || 50;
  const pageCount = Math.max(1, Math.ceil((Number(totalRows) || 0) / t.perPage));
  t.page = Math.min(Math.max(1, Number(t.page) || 1), pageCount);
  return { t, pageCount };
}

/** フィルタ・ソート済み行から現在ページ分だけ切り出す（描画行数を抑えて一覧を軽くする） */
function costMgmtPageRows(filtered) {
  const { t } = costMgmtTableState(filtered.length);
  const start = (t.page - 1) * t.perPage;
  return filtered.slice(start, start + t.perPage);
}

function costMgmtPaginationHtml(totalRows) {
  const { t, pageCount } = costMgmtTableState(totalRows);
  const start = (t.page - 1) * t.perPage;
  const end = Math.min(start + t.perPage, totalRows);
  const perPageOptions = [50, 100, 200, 500]
    .map((n) => `<option value="${n}" ${t.perPage === n ? "selected" : ""}>${n}件</option>`)
    .join("");
  return `
    <div class="pj-pagination cost-mgmt-pagination">
      <div class="pj-page-left">
        <span>表示 ${totalRows === 0 ? 0 : start + 1}〜${end} 件 / 合計 ${totalRows} 件</span>
      </div>
      <div class="pj-page-right">
        <select class="pj-input" style="width:100px;" data-search-dropdown-off="1" data-cost-mgmt-per-page="1">${perPageOptions}</select>
        <button type="button" class="inline-btn ghost" data-cost-mgmt-page="prev" ${t.page <= 1 ? "disabled" : ""}>前へ</button>
        <span class="cost-mgmt-page-indicator">${t.page} / ${pageCount}</span>
        <button type="button" class="inline-btn ghost" data-cost-mgmt-page="next" ${t.page >= pageCount ? "disabled" : ""}>次へ</button>
      </div>
    </div>`;
}

/** 発注名の最小幅。発注名の列は幅指定なし（表の最小幅から他の列を引いた残り）なので、表の最小幅にこの値以上を
 *  含めて潰れないようにする（狭い画面で 63px まで縮み、1行3〜4文字で縦に長く折り返していた） */
const COST_MGMT_NAME_MIN = 200;

function getCostMgmtColWidth(col) {
  const stored = uiState.costMgmtColWidths?.[col];
  const w = typeof stored === "number" ? stored : (COST_MGMT_COL_DEFAULTS[col] || 100);
  return col === "name" ? Math.max(COST_MGMT_NAME_MIN, w) : w;
}

function costMgmtSelectionKey(projectId, costId) {
  return `${String(projectId)}:${String(costId)}`;
}

function pruneCostMgmtSelectionToValid() {
  if (!uiState.costMgmtSelected || typeof uiState.costMgmtSelected !== "object") {
    uiState.costMgmtSelected = {};
    return;
  }
  const valid = new Set(
    flattenAllCostsAcrossProjectsAndOrphans().map((c) => costMgmtSelectionKey(c._projectId, c.id))
  );
  for (const k of Object.keys(uiState.costMgmtSelected)) {
    if (!valid.has(k)) delete uiState.costMgmtSelected[k];
  }
  const selLeft = Object.keys(uiState.costMgmtSelected).filter((k) => uiState.costMgmtSelected[k]).length;
  if (!selLeft) uiState.costMgmtBulkModal = null;
}

// 支払ステータスの6段階
const COST_PAY_STATUSES = ["未発注", "発注済み", "請求書待ち", "支払待ち", "支払済み"];

// 「あなたが動く必要がある」ステータス
function isCostActionNeeded(c) {
  return c.payStatus === "未発注" || c.payStatus === "支払待ち";
}

/** 一覧「支払」列の表示ラベル: 未発注→未受領→受領済→支払済 の4段階に読み替える。
 *  DB値は従来の5段階（未発注/発注済み/請求書待ち/支払待ち/支払済み）のまま・表示だけの対応。
 *  仮原価（実請求書が未着）と board 移行データの「請求書未受領」も「未受領」に含める */
function costPayStatusDisplayLabel(c) {
  if (c?.isProvisional) return "未受領";
  const s = String(c?.payStatus || "").trim();
  // 発注申請の最終承認で仮→実原価になった直後は支払状況が未設定（未登録）。請求書はまだ届いていないので未受領と同じ
  if ((!s || s === "未登録") && c?.orderApprovalStatus === "承認") return "未受領";
  // 「発注済」（み無し）は今のコードは書かないが本番に残る旧データの値。「発注済み」と同じく未受領に寄せる（DBは変えない）
  if (s === "発注済み" || s === "発注済" || s === "請求書待ち" || s === "請求書未受領") return "未受領";
  if (s === "支払待ち") return "受領済";
  if (s === "支払済み") return "支払済";
  return s || "未登録";
}

/** 一覧「発注」列の表示: 発注ステータス（board準拠）が未設定なら支払ステータスから推定
 *  （未発注→空欄・それ以外＝登録済みの原価は「発注済」とみなす）。DBは書き換えない表示だけの推定 */
function costOrderStatusDisplayLabel(c) {
  const s = String(c?.orderStatus || "").trim();
  if (s) return s;
  return String(c?.payStatus || "").trim() === "未発注" ? "" : "発注済";
}

/** 経理の請求書チェック用: 行の受領グループ（未受領=pending / 受領済=received / 支払済=paid）。
 *  原価登録一覧の行背景3色（ピンク/黄/白）に使う。
 *  DB値→表示の写像を二重に持たないよう、表示ラベル（costPayStatusDisplayLabel）から導出する */
function costReceiptGroup(c) {
  if (c?.paidDate) return "paid";
  const label = costPayStatusDisplayLabel(c);
  if (label === "支払済") return "paid";
  if (label === "受領済") return "received";
  return "pending";
}

/** 原価登録一覧の発注先フィルター選択肢（登録済み原価の発注先から重複排除） */
function costMgmtVendorFilterOptionsHtml(rows, selected) {
  const sel = String(selected ?? "").trim();
  const names = new Set();
  for (const c of rows || []) {
    const v = String(c.vendor || "").trim();
    if (v) names.add(v);
  }
  if (sel) names.add(sel);
  const sorted = [...names].sort((a, b) => a.localeCompare(b, "ja"));
  return (
    `<option value="" ${sel === "" ? "selected" : ""}>すべて</option>` +
    sorted.map((v) => `<option value="${escapeHtmlAttr(v)}" ${v === sel ? "selected" : ""}>${escapeHtml(v)}</option>`).join("")
  );
}

/** 原価登録一覧の支払ステータス切替（件数つきチップ）。件数は支払ステータス以外の絞り込み・種別タブを掛けた後の行数。
 *  選択肢は DB列挙（COST_PAY_STATUSES）を表示段階へ写して導出＝写像を変えたら選択肢も自動で追随する */
function costMgmtPayStatusChipsHtml(flatAll, mg, tabKeys) {
  const sel = String(mg.payStatus ?? "").trim();
  const mgNoPay = { ...mg, payStatus: "" };
  const mgCtx = { indivSet: mg.vendorType ? costVendorIndividualNameSet() : null };
  const counts = new Map();
  let total = 0;
  for (const row of flatAll) {
    if (!flattenCostRowMatchesMgmtFilter(row, mgNoPay, mgCtx) || !costMgmtRowMatchesTabSelection(row, tabKeys)) continue;
    const k = costPayStatusDisplayLabel(row);
    counts.set(k, (counts.get(k) || 0) + 1);
    total++;
  }
  const canonical = [...new Set(["未登録", ...COST_PAY_STATUSES].map((s) => costPayStatusDisplayLabel({ payStatus: s })))];
  const labels = canonical.filter((s) => counts.has(s) || s === sel);
  for (const s of counts.keys()) if (!labels.includes(s)) labels.push(s); // 想定外の値も選べるように
  const chip = (value, label, n) =>
    `<button type="button" class="cost-mgmt-status-chip${value === sel ? " active" : ""}" data-cost-mgmt-pay-chip="${escapeHtmlAttr(value)}" aria-pressed="${value === sel ? "true" : "false"}">${escapeHtml(label)}<span class="cost-mgmt-status-chip-n">${n}</span></button>`;
  return `<div class="cost-mgmt-status-chips" role="group" aria-label="支払ステータスで絞り込み"><span class="cost-mgmt-status-chips-label">支払</span>${chip("", "すべて", total)}${labels.map((s) => chip(s, s, counts.get(s) || 0)).join("")}</div>`;
}

/** 個人（敬称=様）のパートナー/クライアント名の正規化セット。支払先の個人/法人判定用（源泉漏れチェック） */
function costVendorIndividualNameSet() {
  const norm = (s) => String(s || "").normalize("NFKC").replace(/\s+/g, "").toLowerCase();
  const set = new Set();
  for (const pt of uiState.partnersCache || []) {
    if (String(pt?.honorific || "").trim() !== "様") continue;
    const disp = partnerMasterDisplayLabel(pt) || String(pt?.name ?? "").trim();
    if (disp) set.add(norm(disp));
    if (pt?.name) set.add(norm(pt.name));
  }
  for (const cl of clients || []) {
    if (String(cl?.honorific || "").trim() !== "様") continue;
    if (cl.name) set.add(norm(cl.name));
    if (cl.shortName) set.add(norm(cl.shortName));
  }
  return set;
}

/** 支払先名が個人（敬称=様のマスタ）に一致するか */
function costVendorIsIndividual(vendor, indivSet) {
  const key = String(vendor || "").normalize("NFKC").replace(/\s+/g, "").toLowerCase();
  return !!key && indivSet.has(key);
}

/** 源泉所得税の標準計算: 税抜報酬の10.21%、100万円を超える部分は20.42%（1円未満切捨て） */
function calcWithholdingTaxYen(amountExTax) {
  const a = Math.max(0, Math.round(Number(String(amountExTax ?? 0).toString().replace(/,/g, "")) || 0));
  if (a <= 1000000) return Math.floor(a * 0.1021);
  return Math.floor((a - 1000000) * 0.2042 + 102100);
}

/** 発注先名からパートナーマスタの行を名寄せで引く（空白・全角ゆれ吸収。不一致は null） */
function costPartnerByVendorName(vendor) {
  const norm = (s) => String(s || "").normalize("NFKC").replace(/\s+/g, "").toLowerCase();
  const key = norm(vendor);
  if (!key) return null;
  return (
    (uiState.partnersCache || []).find(
      (p) => key === norm(partnerMasterDisplayLabel(p)) || key === norm(p?.name)
    ) || null
  );
}

/** パートナーの支払条件から、原価の支払期限を「支払月と支払日だけ」で決める（2026-10 要望）。
 *  締め日は見ず、基準月（請求日の月。未入力なら登録した月）から 当月=0／翌=1／翌々=2／翌Nヶ月=N だけずらし、
 *  「末」は月末・「N日」はその日にする（例: 翌月末払 → 10月なら11月末）。締め日の違いでずれる分は手で直す前提。
 *  支払月が読めない条件（"翌"も"当月"も無い）は ""（自動計算せず手入力に任せる）。
 *  ※請求書（取引先＝クライアント側）の支払期限は書類に刷られるため、従来の computeInvoiceDueFromClientTerms のまま */
function computeCostDueFromPartnerTerms(terms, baseYm) {
  const t = String(terms || "").normalize("NFKC").replace(/\s/g, "");
  const bm = String(baseYm || "").match(/(\d{4})[-/](\d{1,2})/);
  if (!t || !bm) return "";
  // 支払側だけを見る（"20日締翌月10日払" の "20日" を支払日と取り違えない）
  const pay = t.includes("締") ? t.slice(t.lastIndexOf("締") + 1) : t;
  let offset;
  const mMonths = pay.match(/翌(\d+)[ヶか]?月/);
  if (mMonths) offset = Number(mMonths[1]);
  else if (pay.includes("翌々")) offset = 2;
  else if (pay.includes("翌")) offset = 1;
  else if (pay.includes("当月")) offset = 0;
  else return "";
  let y = Number(bm[1]);
  let mo = Number(bm[2]) + offset;
  y += Math.floor((mo - 1) / 12);
  mo = ((mo - 1) % 12) + 1;
  const lastDay = new Date(y, mo, 0).getDate();
  const dMatch = pay.replace(/翌\d+[ヶか]?月/, "").match(/(\d{1,2})日/);
  const day = dMatch ? Math.min(Number(dMatch[1]), lastDay) : lastDay;
  return `${y}-${String(mo).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** 発注先名→パートナーマスタの支払条件で支払期限を自動計算。基準月は請求日の月（未入力なら今月＝登録した月）。
 *  パートナー不一致・支払月が読めない支払条件は ""（自動計算せず手入力に任せる） */
function costAutoDueDateFromVendorTerms(vendor, invoiceDate) {
  const pt = costPartnerByVendorName(vendor);
  if (!pt) return "";
  const m = String(invoiceDate || "").trim().match(/^(\d{4})-(\d{1,2})/);
  const baseYm = m ? `${m[1]}-${m[2]}` : localDateToYmd(new Date()).slice(0, 7);
  return computeCostDueFromPartnerTerms(pt.payment_terms, baseYm);
}

/** 源泉徴収区分と税抜金額から源泉所得税を計算。源泉徴収の対象でない区分は null。
 *  復興特別所得税なしは 10%（100万円超の部分は20%）、ありは calcWithholdingTaxYen（10.21%／20.42%） */
function costWithholdingYenForKind(kind, amountExTax) {
  const k = String(kind || "").trim();
  if (!k.startsWith("源泉徴収")) return null;
  if (!k.includes("復興特別所得税なし")) return calcWithholdingTaxYen(amountExTax);
  const a = Math.max(0, Math.round(Number(String(amountExTax ?? 0).replace(/,/g, "")) || 0));
  return a <= 1000000 ? Math.floor(a * 0.1) : Math.floor((a - 1000000) * 0.2 + 100000);
}

/** 発注先名→パートナーマスタの源泉徴収区分（WITHHOLDING_TAX_KINDS の値）。未登録・区分なしは "" */
function costWithholdingKindFromVendor(vendor) {
  const kind = String(costPartnerByVendorName(vendor)?.withholding_tax || "").trim();
  return WITHHOLDING_TAX_KINDS.includes(kind) ? kind : "";
}

/** 登録済みの源泉所得税の金額から区分を推定する（原価には区分の列が無いため。仮原価の編集で区分の選択に戻す）。
 *  どちらの計算式とも一致しない金額（手入力・金額変更後に未更新など）は "" を返す＝区分を決めず、保存時も
 *  登録済みの金額をそのまま使う（触っていない源泉所得税を保存のたびに計算し直して書き換えない） */
function costWithholdingKindFromAmount(amountExTax, withholdingYen) {
  const w = Math.round(Number(withholdingYen) || 0);
  if (w <= 0) return "なし";
  return WITHHOLDING_TAX_KINDS.find((k) => costWithholdingYenForKind(k, amountExTax) === w) || "";
}

/** 発注先名→パートナーマスタの源泉徴収区分で源泉所得税を計算。源泉徴収の対象でない・不一致は null */
function costAutoWithholdingFromVendor(vendor, amountExTax) {
  return costWithholdingYenForKind(costPartnerByVendorName(vendor)?.withholding_tax, amountExTax);
}

/** 発注先名→パートナーマスタの支払方法を、原価の支払方法（COST_PAYMENT_METHODS）の語彙へ写像して返す。
 *  パートナー側の語彙: 銀行振込/口座振替/クレジットカード(+旧値 現金支払)。不一致・未登録は ""（手入力に任せる） */
function costAutoPaymentMethodFromVendor(vendor) {
  const pt = costPartnerByVendorName(vendor);
  if (!pt) return "";
  const map = {
    "銀行振込": "請求書",
    "口座振替": "口座振替",
    "クレジットカード": "カード",
    "現金支払": "その他"
  };
  return map[String(pt.payment_method || "").trim()] || "";
}

/** 発注先の下に出す、パートナー登録の支払条件などの案内（自動で入る値の出どころを見せる。2026-10 要望）。
 *  compact=true はまとめて入力の表用の短い1行 */
function costPartnerTermsHintHtml(vendor, compact = false) {
  if (!String(vendor || "").trim()) return "";
  const pt = costPartnerByVendorName(vendor);
  if (!pt) {
    return `<span class="cost-form-hint cost-partner-hint is-missing">${compact ? "パートナー未登録（自動では入りません）" : "パートナー未登録のため、支払期限・支払方法・源泉は自動では入りません"}</span>`;
  }
  const parts = [
    String(pt.payment_terms || "").trim() || "支払条件未登録",
    String(pt.payment_method || "").trim(),
    String(pt.withholding_tax || "").trim().startsWith("源泉徴収") ? "源泉徴収あり" : ""
  ].filter(Boolean);
  const text = compact ? parts.join("・") : `パートナー登録: ${parts.join("・")}（支払期限・支払方法・源泉を自動で入れています。違うときは直してください）`;
  return `<span class="cost-form-hint cost-partner-hint">${escapeHtml(text)}</span>`;
}

/** 原価の読み取り専用サマリ1項目（原価割当パネルで使用） */
function costReadonlySummaryItemHtml(label, val) {
  return `<div class="cost-alloc-ro-item"><span class="cost-alloc-ro-label">${escapeHtml(label)}</span><span class="cost-alloc-ro-value">${escapeHtml(val || "—")}</span></div>`;
}

// 支払ステータスに対応するCSSクラス（DB値の5段階と表示の4段階の両方を受ける）
function costPayClass(status) {
  if (status === "支払済み" || status === "支払済") return "cs-paid";
  if (status === "未発注") return "cs-unordered";
  if (status === "支払待ち" || status === "受領済") return "cs-pay-wait";
  if (status === "発注済み") return "cs-ordered";
  if (status === "請求書待ち" || status === "請求書未受領" || status === "未受領") return "cs-invoice-wait";
  return "cs-unregistered"; // 未登録
}

// 種別ごとのフォーム（支払期限の表示有無）
const CATEGORY_META = {
  "媒体費": { showDueDate: false },
  "制作費": { showDueDate: true },
  "開発費": { showDueDate: true },
  "その他": { showDueDate: true } // 外注以外の請求書払いもあるため支払期限を出し、発注先の支払条件から自動で入れる（2026-10 要望）
};

/** 原価フォームに支払期限の欄を出して自動で入れるか。仮原価（営業が入れる見込み）は会計区分に関係なく出す
 *  （2026-10 要望: パートナーの支払条件から入った支払期限を営業が見て直せるように）。それ以外は会計区分の規則どおり */
function costFormShowsDueDate(f) {
  return f?.isProvisional === true || CATEGORY_META[f?.category]?.showDueDate !== false;
}

/** fp_costs.id を UI の cost-123 と対応させる */
function costDbNumericId(cost) {
  if (!cost?.id) return null;
  const m = String(cost.id).match(/^cost-(\d+)$/);
  return m ? Number(m[1]) : null;
}

/** 原価フォーム保存用: 対象月を YYYY/MM に正規化 */
function normalizeCostTargetMonthForSave(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const fromMonthInput = billingMonthStoredFromMonthInput(s.replace(/\//g, "-"));
  if (fromMonthInput) return fromMonthInput;
  return s.replace(/-/g, "/");
}

/** 支払期限・支払日など DATE 値（'YYYY-MM-DD'）を一覧向けの短縮表示 'MM/DD' に。空は「—」 */
function costDateShortDisplay(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}` : s;
}

/** 営業の請求書取込（salesMode）: 計上先に出す案件をログイン中ユーザーが担当（営業/制作/経理）のものに絞る。
 *  サーバ側の実原価POST制限（api/costs.js callerOwnsProjectRow: 担当案件のみ）と対。担当外の請求書は
 *  その案件の担当営業か経理に回す運用。 */
function costWizardSalesModeProjects() {
  const myDb = currentUser?.dbId;
  if (myDb == null || !Number.isFinite(Number(myDb))) return [];
  const me = `u${myDb}`;
  return projects.filter(
    (p) => p.salesOwnerId === me || p.assignedUserId === me || p.accountingOwnerId === me
  );
}

/** 未割当原価の予想案件（fp_cost_account_project_map 由来）を UI-ID で返す。無ければ空。 */
function costSuggestedProjectUiId(c) {
  const dbId = c.suggestedProjectDbId;
  if (dbId == null || !Number.isFinite(Number(dbId))) return "";
  const p = projects.find((x) => Number(x.dbId) === Number(dbId));
  return p ? p.id : "";
}

/** 広告CSVのソース別ラベル（支払先に付ける） */
const COST_IMPORT_CSV_SOURCE_META = Object.freeze({
  // 広告5媒体のラベルは摘要「<アカウント名> <ラベル>」の一部として保存され、
  // サーバ側の逆引きパース（予想案件・前月の案件から作成）とペアなので
  // 00-shared-enums.js の AD_CSV_NOTE_LABELS だけで変更する
  google: { label: AD_CSV_NOTE_LABELS.google, vendor: "Google" },
  ydn: { label: AD_CSV_NOTE_LABELS.ydn, vendor: "Yahoo!" },
  yss: { label: AD_CSV_NOTE_LABELS.yss, vendor: "Yahoo!" },
  line: { label: AD_CSV_NOTE_LABELS.line, vendor: "LINE株式会社" },
  meta: { label: AD_CSV_NOTE_LABELS.meta, vendor: "Facebook" },
  generic: { label: "汎用（列名から自動判定）", vendor: "広告" },
  onamae: { label: "お名前ドットコム", vendor: "お名前.com" },
  upsider: { label: "UPSIDER", vendor: "UPSIDER" },
  tecolas: { label: "テコラス（AWS）", vendor: "テコラス" }
});

/** 広告CSV取込の摘要を「<アカウント名> <媒体ラベル>[ 計上月]」として分解する。合致しなければ null。
 *  保存形式: 1行目=アカウント名+媒体ラベル+計上月サフィックス（costImportSummaryTextForPersist）、
 *  2行目以降=取込原文など。サーバ api/costs.js の parseAdCsvNoteAccount と同じ規則（両側ペアで変更する） */
function adCsvNoteAccount(note) {
  let s = String(note || "").split("\n")[0].trim();
  if (!s) return null;
  // 末尾の計上月サフィックス（例: " 2026/05"）を剥がす
  s = s.replace(/\s+\d{4}\/\d{1,2}$/, "");
  for (const [sourceKey, label] of Object.entries(AD_CSV_NOTE_LABELS)) {
    if (s.length > label.length && s.endsWith(label)) {
      const account = s.slice(0, s.length - label.length).trim();
      if (account) return { account, sourceKey, label };
    }
  }
  return null;
}

const COST_IMPORT_CSV_SOURCE_KEYS = Object.keys(COST_IMPORT_CSV_SOURCE_META);

/** 広告プラットフォーム（その他＝generic は含めない） */
const COST_IMPORT_AD_PLATFORM_KEYS = ["google", "ydn", "yss", "line", "meta"];

const COST_IMPORT_AD_PLATFORM_ICON = Object.freeze({
  google: "G",
  ydn: "YD",
  yss: "YS",
  line: "L",
  meta: "M"
});

/** 取り込み選択の `csv:key` / `pdf:key` / `paste:key` / `excel:key` を分解。不正時は null */
function parseCostImportChoiceValue(raw) {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(csv|pdf|paste|excel):([a-z]+)$/);
  if (!m) return null;
  const kind = m[1];
  const sourceKey = m[2];
  if (!COST_IMPORT_CSV_SOURCE_META[sourceKey]) return null;
  return { kind, sourceKey };
}

function csvHeaderNormalizeForMatch(h) {
  return String(h ?? "")
    .replace(/\uFEFF/g, "")
    .trim()
    .toLowerCase()
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ");
}

function classifyCsvAmountColumnTaxMode(headerCell) {
  const h = String(headerCell ?? "").trim();
  if (!h) return "unknown";
  if (/税込|税額込|（税込|内税/.test(h)) return "inclusive";
  if (/税抜|税別|外税|課税対象|対象金額|excluding/i.test(h)) return "exclusive";
  return "unknown";
}

/** インポート金額を税抜・消費税に分割（標準税率10%想定／税込列は逆算）。
 *  集計・粗利は税込（amount + consumptionTax）で行うため、税抜/不明の列でも
 *  消費税を 0 にせず標準10%を付与する（税抜取込だけ税込合計が1割過少になるのを防ぐ）。
 *  税込/税抜トグルで再解釈できるよう、取り込んだ元の数値 rawImported も返す。 */
function splitImportedYenToExTaxAndTax(absYen, mode) {
  const n = Math.round(Number(absYen) || 0);
  if (n <= 0) return { amountExTax: 0, consumptionTax: 0, rawImported: n };
  if (mode === "inclusive") {
    const ex = Math.round(n / 1.1);
    return { amountExTax: ex, consumptionTax: n - ex, rawImported: n };
  }
  // 税抜（外税）／税区分不明: n を税抜とみなし、標準10%を消費税として付与する
  return { amountExTax: n, consumptionTax: Math.round(n * 0.1), rawImported: n };
}

/** 値引きなど負の金額も含めて分割する（負数は絶対値で分割して符号を戻す）。
 *  負数の消費税を0にすると、値引きのある請求書の税込合計が値引き額の1割だけ多くなる */
function splitSignedImportedYen(yen, mode) {
  const n = Math.round(Number(yen) || 0);
  if (n >= 0) return splitImportedYenToExTaxAndTax(n, mode);
  const s = splitImportedYenToExTaxAndTax(-n, mode);
  return { amountExTax: -s.amountExTax, consumptionTax: -s.consumptionTax, rawImported: n };
}

function inferPdfLineTaxMode(lineText) {
  const h = String(lineText ?? "");
  if (/税込|（税込|税額込/.test(h)) return "inclusive";
  if (/税抜|税別|外税/.test(h)) return "exclusive";
  return "exclusive";
}

/**
 * 請求書明細の「適用」欄の値を優先して取り出す（「振込先」列と誤結合したときの読み取り用）
 */
function extractCostImportTekiyouFromLine(lineText) {
  const s = String(lineText ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s || !/適用\s*[：:]/i.test(s)) return "";
  const m = s.match(/適用\s*[：:]\s*(.+)/i);
  if (!m) return "";
  let v = m[1]
    .replace(/(?:¥|￥)\s*[\d,]+(?:\.\d{1,2})?/g, " ")
    .replace(/[\d,]+(?:\.\d{1,2})?\s*円(?:\([^)]*\))?/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  v = v.replace(/[:：]\s*$/, "").trim();
  return v.slice(0, 500);
}

/** 請求明細の「摘要／品名／品目／項目」列（ラベル＋コロン付き）を取り出す */
function extractCostImportSummaryColumnFromLine(lineText) {
  const s = String(lineText ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  if (!/(?:摘要|品名|品目|項目)\s*[：:]/i.test(s)) return "";
  const m = s.match(/(?:摘要|品名|品目|項目)\s*[：:]\s*(.+)/i);
  if (!m) return "";
  let v = m[1]
    .replace(/(?:¥|￥)\s*[\d,]+(?:\.\d{1,2})?/g, " ")
    .replace(/[\d,]+(?:\.\d{1,2})?\s*円(?:\([^)]*\))?/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return v.replace(/[:：]\s*$/, "").trim().slice(0, 500);
}

/** 請求明細表の列見出しだけのチャンクか（本文と · で連結された先頭ブロック） */
function isInvoiceTableHeaderNoiseChunk(p) {
  const s0 = costImportHintNormalized(p);
  if (!s0) return false;
  const s = s0.replace(/\s/g, "");
  if (/^摘要数量単価明細金額/.test(s)) return true;
  if (/^(?:品名|品目|項目)数量単価(?:明細)?金額/.test(s)) return true;
  if (/^(?:品名|品目|項目)数量単価金額/.test(s)) return true;
  if (/^数量単価明細金額/.test(s) && s.length <= 16) return true;
  if (/^明細番号(?:商品名|品名|品目|摘要)/.test(s)) return true;
  if (/^明細番号.*単価.*数量.*金額/.test(s)) return true;
  if (/^(?:商品名|品名)単価数量金額(?:期間|利用月|備考)/.test(s)) return true;
  if (/^納品日(?:摘要|品名|品目|摘\s*要)/.test(s)) return true;
  if (/^納品日.*金額.*数量.*(?:小計|税区分)/.test(s)) return true;
  if (/^摘\s*要金額数量小計税区分/.test(s)) return true;
  return false;
}

/** 行末の日付・税区分ノイズ（利用月・期間・税率%）を除去 */
function stripTrailingInvoiceDateTokens(line) {
  let t = String(line ?? "").trim();
  for (let i = 0; i < 6; i++) {
    const prev = t;
    // 税区分コード: 10%, 8%, 0%, 軽減8% など
    t = t.replace(/\s+(?:軽減\s*)?\d+\s*[％%]\s*$/u, "").trim();
    // YYYY/MM 利用月
    t = t.replace(/\s+\d{4}[\/]\d{2}\s*$/u, "").trim();
    // YYYY/MM/DD〜YYYY/MM/DD 期間
    t = t.replace(/\s+\d{4}[\/]\d{1,2}[\/]\d{1,2}\s*[〜～]\s*\d{4}[\/]\d{1,2}[\/]\d{1,2}\s*$/u, "").trim();
    // 〜YYYY/MM/DD 末尾のみ
    t = t.replace(/\s+[〜～]\s*\d{4}[\/]\d{1,2}[\/]\d{1,2}\s*$/u, "").trim();
    if (t === prev) break;
  }
  return t;
}

/** 行頭の「YYYY/MM/DD 」形式の納品日プレフィックスを除去 */
function stripLeadingDeliveryDatePrefix(line) {
  return String(line ?? "").replace(/^\d{4}[\/年]\d{1,2}[\/月]\d{1,2}日?\s+/u, "").trim();
}

/**
 * PDFで「摘要 数量 単価 明細金額」と本文が1行にマージされたとき、
 * 見出し・末尾の数量・単価・明細金額の数列を除いた摘要本文だけ残す
 */
function stripInvoiceTableMergedNoise(text) {
  let t = costImportHintNormalized(String(text ?? ""));
  if (!t) return "";
  t = t.replace(/^摘要\s+数量\s+単価\s+明細金額\s*(?:[·・／/]\s*)?/i, "");
  t = t.replace(
    /^(?:品名|品目|項目)\s+数量\s+単価\s+(?:明細)?金額\s*(?:[·・／/]\s*)?/i,
    ""
  );
  t = t.replace(/^数量\s+単価\s+明細金額\s+摘要\s*(?:[·・／/]\s*)?/i, "");
  t = t.replace(/^数量\s+単価\s+(?:明細)?金額\s+(?:品名|品目|項目|摘要)\s*(?:[·・／/]\s*)?/i, "");
  let prev;
  for (let i = 0; i < 6; i++) {
    prev = t;
    t = t.replace(/\s+[\d０-９]{1,6}\s+[\d,，]{1,14}\s+[\d,，]{1,14}\s*$/u, "").trim();
    t = t.replace(/([）\)])\s*(\d{1,6})\s+([\d,，]+)\s+([\d,，]+)\s*$/u, "$1").trim();
    if (t === prev) break;
  }
  t = t.replace(/^摘要\s*数量\s*単価\s*明細金額\s*(?:[·・／/]\s*)?/i, "").trim();
  t = t
    .replace(/^(?:品名|品目|項目)\s*数量\s*単価\s*(?:明細)?金額\s*(?:[·・／/]\s*)?/i, "")
    .trim();
  return t;
}

/** 数量・単価・明細金額の末尾トークン（全角桁・全角カンマ対応） */
function parseInvoiceTableNumberToken(raw) {
  const s = String(raw ?? "")
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff10 + 0x30))
    .replace(/,/g, "")
    .replace(/，/g, "")
    .replace(/\s/g, "")
    .trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 行末が「… 数量 単価 明細金額」の請求明細表か。金額は右端（明細金額列）のみ採る（数量を金額と誤認しない）。
 */
function matchInvoiceTableQtyUnitPriceLineTail(lineText) {
  const t = String(lineText ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const tailPat = /\s+(\d{1,6})\s+([\d,，０-９]+)\s+([\d,，０-９]+)\s*$/u;
  const tm = t.match(tailPat);
  if (tm) {
    return {
      head: t.slice(0, t.length - tm[0].length).trim(),
      qty: parseInvoiceTableNumberToken(tm[1]),
      unitPrice: parseInvoiceTableNumberToken(tm[2]),
      lineAmount: parseInvoiceTableNumberToken(tm[3])
    };
  }
  // 「一式」等の日本語数量語も qty=1 として認識する
  const jpQtyPat = /\s+(一式|一ヶ所|一か所|一箇所|一個|式)\s+([\d,，０-９]+)\s+([\d,，０-９]+)\s*$/u;
  const jm = t.match(jpQtyPat);
  if (jm) {
    return {
      head: t.slice(0, t.length - jm[0].length).trim(),
      qty: 1,
      unitPrice: parseInvoiceTableNumberToken(jm[2]),
      lineAmount: parseInvoiceTableNumberToken(jm[3])
    };
  }

  // 末尾の日付ノイズ（利用月・期間）を除去してから再試行
  const tStripped = stripTrailingInvoiceDateTokens(t);
  if (tStripped !== t && tStripped.length > 0) {
    const reResult = matchInvoiceTableQtyUnitPriceLineTail(tStripped);
    if (reResult) return reResult;
  }

  // 「単価 数量 金額」形式（unitPrice qty lineAmount）の末尾パターン
  // 例: "入力業務_1 13,000 1 13,000" → unitPrice=13000, qty=1, lineAmount=13000
  const altTailPat = /\s+([\d,，０-９]+)\s+(\d{1,6})\s+([\d,，０-９]+)\s*$/u;
  const am = tStripped.match(altTailPat);
  if (am) {
    const unitPrice = parseInvoiceTableNumberToken(am[1]);
    const qty = parseInvoiceTableNumberToken(am[2]);
    const lineAmount = parseInvoiceTableNumberToken(am[3]);
    // qty × unitPrice ≈ lineAmount で妥当性確認（10%誤差許容）
    if (unitPrice >= 100 && qty >= 1 && lineAmount >= 100) {
      const expected = unitPrice * qty;
      if (Math.abs(expected - lineAmount) <= Math.max(1, expected * 0.1)) {
        const rawHead = tStripped.slice(0, tStripped.length - am[0].length).trim();
        return {
          head: stripLeadingDeliveryDatePrefix(rawHead),
          qty,
          unitPrice,
          lineAmount
        };
      }
    }
  }

  // 負の金額（値引き・インセンティブ）「説明 -金額」形式
  // 例: "Profile Passport パートナーインセンティブ -45,000" → lineAmount=-45000
  const negTailPat = /\s+-\s*([\d,，０-９]+)\s*$/u;
  const nm = tStripped.match(negTailPat);
  if (nm) {
    const lineAmount = -parseInvoiceTableNumberToken(nm[1]);
    if (Math.abs(lineAmount) >= 100) {
      return {
        head: stripLeadingDeliveryDatePrefix(tStripped.slice(0, tStripped.length - nm[0].length).trim()),
        qty: 1,
        unitPrice: lineAmount,
        lineAmount
      };
    }
  }

  return null;
}

/**
 * 明細表が1行に「品目・数量・単価・金額」で並んだとき、左側の説明文だけ抜く
 * （例: BOOKINGMNG-160 … 1 30,000 30,000）
 */
function extractInvoiceTableLeadingDescription(lineText) {
  let t = stripPdfLineMoneyArtifacts(String(lineText ?? ""))
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  t = stripInvoiceTableMergedNoise(t);
  if (t.length < 10) return "";
  const tailHit = matchInvoiceTableQtyUnitPriceLineTail(t);
  if (!tailHit) {
    if (
      t.length >= 8 &&
      /[A-Za-z\u3040-\u9faf]/.test(t) &&
      !/^\d/.test(t) &&
      !isPdfInvoiceFooterOrBankLikeChunk(t)
    )
      return t.slice(0, 500);
    return "";
  }
  const head = tailHit.head;
  if (head.length < 8) return "";
  let out = head.replace(/^件名\s*[：:]?\s*/i, "").trim();
  out = stripInvoiceTableMergedNoise(out);
  if (isPdfInvoiceFooterOrBankLikeChunk(out)) return "";
  if (/^[\d,.\sー−−]+$/.test(out)) return "";
  out = stripBankTransferNoiseSegmentsFromCostImportHint(out);
  if (!out.trim()) return "";
  return out.slice(0, 500);
}

/** 金額トークンを除いた文言（近傍行から摘要を推定するときに使用） */
function stripPdfLineMoneyArtifacts(lineText) {
  return String(lineText ?? "")
    .replace(/(?:¥|￥)\s*[\d,]+(?:\.\d{1,2})?/g, " ")
    .replace(/[\d,]+(?:\.\d{1,2})?\s*円(?:\([^)]*\))?/g, " ")
    .replace(
      /(?:合計|小計|税込(?:額)?|御請求額|請求(?:額|金額)?|ご請求(?:額)?|お支払(?:い)?(?:金額)?|金額)[:：\s　]*[\d,]+(?:\.\d{1,2})?/gi,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();
}

/** 口座番号・記号など、摘要に不要な「桁だけ」のチャンクか（· 区切り単位でも使う） */
function isLikelyAccountNumberOnlyChunk(chunk) {
  const lax = costImportHintNormalized(chunk);
  if (!lax || lax.length < 5) return false;
  const nospace = lax.replace(/\s/g, "").replace(/[－ー-]/g, "-");
  const zen2han = nospace.replace(/[０-９]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) - 0xff10 + 0x30)
  );
  if (/^[0-9]{7,14}$/.test(zen2han)) return true;
  if (/^[0-9]{3,5}-[0-9]{5,12}$/.test(zen2han)) return true;
  if (
    /^[0-9\-]{7,24}$/.test(zen2han) &&
    /[0-9]/.test(zen2han) &&
    !/[A-Za-z\u3040-\u9faf]/.test(lax)
  )
    return true;
  return false;
}

/** 摘要に混入した口座・店番・記号・銀行名ブロックを除去（PDFの列結合ノイズ対策） */
function stripBankTransferNoiseSegmentsFromCostImportHint(hint) {
  let t = costImportHintNormalized(hint);
  if (!t) return "";
  t = t.replace(/口座(?:種別|情報)?\s*[：:]\s*(?:普通|当座|貯蓄|別段)\b/gi, " ");
  t = t.replace(/口座番号\s*[：:]\s*[0-9０-９\-－ー\s]{4,36}/gi, " ");
  t = t.replace(
    /(?:ゆうちょ|ＵＳＳ|郵便(?:局|便)?|ゆうちょ銀行)\s*記号\s*[：:]\s*[0-9０-９]{2,14}/gi,
    " "
  );
  t = t.replace(
    /(?:ゆうちょ|ＵＳＳ|郵便(?:局|便)?|ゆうちょ銀行)\s*番号\s*[：:]\s*[0-9０-９\-－ー\s]{2,22}/gi,
    " "
  );
  t = t.replace(
    /記号\s*[：:]\s*[0-9０-９]{2,14}\s+番号\s*[：:]\s*[0-9０-９\-－ー\s]{2,22}/gi,
    " "
  );
  t = t.replace(
    /店番(?:号)?(?:\s*[（(]\s*支店(?:コード)?\s*[）)])?\s*[：:]\s*[0-9０-９]{3,9}/gi,
    " "
  );
  t = t.replace(/支店(?:コー?ド|コード)\s*[：:]\s*[0-9０-９]{3,9}/gi, " ");
  t = t.replace(/\b(?:SWIFT|BIC)\s*[：:]?\s*[A-Z0-9]{8,13}\b/gi, " ");
  t = t.replace(/\bIBAN\s*[：:]?\s*[A-Z0-9]{10,40}\b/gi, " ");
  t = t.replace(/\s{2,}/g, " ").trim();
  const parts = t.split(/\s*[·・]\s*/).map((p) => p.trim()).filter(Boolean);
  const kept = parts.filter(
    (p) =>
      !isLikelyAccountNumberOnlyChunk(p) &&
      !isPdfInvoiceFooterOrBankLikeChunk(p) &&
      !isInvoiceTableHeaderNoiseChunk(p)
  );
  t = kept.join(" · ").trim();
  const hasDesc = /[A-Za-z\u3040-\u9faf]{2,}/.test(t.replace(/\s/g, ""));
  if (hasDesc) {
    t = t
      .replace(/\s+[0-9０-９]{7,14}$/u, " ")
      .replace(/\s+[0-9０-９]{3,5}\s*[-－ー]\s*[0-9０-９]{5,14}$/u, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
  }
  return t;
}

/** 請求書PDFから得た請求元名が名称欄へ混入しないよう除去（一覧・マスタとは別ヘッダーで示す） */
function stripDocumentPayeeFromCostImportHint(hint, payeeDoc) {
  const h = String(hint || "")
    .replace(/\s+/g, " ")
    .trim();
  const p = String(payeeDoc || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!h) return "";
  if (!p || p.length < 2) return h;
  const pNorm = compactVendorMatchKey(p);
  if (!pNorm) return h;
  if (compactVendorMatchKey(h) === pNorm || h === p) return "";
  const chunks = h.split(/\s*[·・]\s*/).map((c) => c.trim()).filter(Boolean);
  if (chunks.length > 1) {
    const kept = chunks.filter((c) => {
      if (compactVendorMatchKey(c) === pNorm || c === p) return false;
      return !(p.length >= 6 && (c.includes(p) || p.includes(c)));
    });
    if (kept.length) return kept.join(" · ").trim().slice(0, 500);
  }
  try {
    let out = h.replace(new RegExp(p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), " ");
    out = out
      .replace(/\s+[·・]\s+/g, " · ")
      .replace(/^\s*[·・]\s*|\s*[·・]\s*$/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return out.slice(0, 500);
  } catch {
    return h;
  }
}

/**
 * 金額のみの行の前後から、請求書上のサービス名・明細語を拾う。
 * 「御中」付近は請求先（自社側）になりやすいため除外する。
 * excludePayeeStr は PDF から推定した請求発行元名（名称欄に二重表示しない）
 */
function inferNeighborPdfSummaryHint(rawLines, lineIndex, excludePayeeStr) {
  const exPay = String(excludePayeeStr ?? "")
    .replace(/\s+/g, " ")
    .trim();
  const exNorm = exPay.length >= 2 ? compactVendorMatchKey(exPay) : "";
  const deltas = [...Array.from({ length: 17 }, (_, k) => k - 8)].sort(
    (a, b) => Math.abs(a) - Math.abs(b)
  );
  for (const d of deltas) {
    if (d === 0) continue;
    const ixPre = lineIndex + d;
    if (ixPre < 0 || ixPre >= rawLines.length) continue;
    const rawPre = rawLines[ixPre].trim();
    if (!rawPre || rawPre.length < 2 || rawPre.length > 220) continue;
    const sumPick =
      extractCostImportSummaryColumnFromLine(rawPre) ||
      extractCostImportSummaryColumnFromLine(stripPdfLineMoneyArtifacts(rawPre));
    if (sumPick.length >= 2 && !isPdfInvoiceNonItemNameChunk(sumPick)) {
      return stripBankTransferNoiseSegmentsFromCostImportHint(sumPick).slice(0, 520);
    }
    const tekiyouPick =
      extractCostImportTekiyouFromLine(rawPre) ||
      extractCostImportTekiyouFromLine(stripPdfLineMoneyArtifacts(rawPre));
    if (tekiyouPick.length >= 2 && !isPdfInvoiceNonItemNameChunk(tekiyouPick)) {
      return stripBankTransferNoiseSegmentsFromCostImportHint(tekiyouPick).slice(0, 520);
    }
  }
  const chunks = [];
  const seen = new Set();
  for (const d of deltas) {
    if (d === 0) continue;
    const ix = lineIndex + d;
    if (ix < 0 || ix >= rawLines.length) continue;
    const raw = rawLines[ix].trim();
    if (!raw || raw.length < 2 || raw.length > 220) continue;
    let t = stripPdfLineMoneyArtifacts(raw);
    t = stripBankTransferNoiseSegmentsFromCostImportHint(t);
    if (t.length < 2 || t.length > 200) continue;
    if (isLikelyAccountNumberOnlyChunk(t)) continue;
    if (exPay && (t === exPay || compactVendorMatchKey(t) === exNorm)) continue;
    if (exPay && exPay.length >= 6 && (t.includes(exPay) || exPay.includes(t))) continue;
    if (isPdfInvoiceNonItemNameChunk(t)) continue;
    if (/振込(?:み)?(?:先|口座)\s*[：:]/i.test(t) && !/適用\s*[：:]/i.test(raw)) continue;
    if (/御中|T\d{13}|適格請求書|登録番号[:：]?/i.test(t)) continue;
    if (/^株式会社.+御中|^有限会社.+御中/.test(t.replace(/\s/g, ""))) continue;
    if (/^[\d,\s％%ー\-−・.．。]+$/.test(t)) continue;
    if (/請求書番号|^No\.|^№|^日付|^発行|^page\s*\d|^ページ/i.test(t)) continue;
    const head = t.slice(0, 140);
    if (seen.has(head)) continue;
    seen.add(head);
    chunks.push(t);
    if (chunks.length >= 4) break;
  }
  return stripBankTransferNoiseSegmentsFromCostImportHint(chunks.join(" · ")).slice(0, 520);
}

/** 振込先／口座ブロックから名義らしき行を抽出（請求先「御中」行は使わない） */
function extractPdfBankAccountHolderHint(lines) {
  const companyShape = /(株式会社|有限会社|合同会社|（株）|\(株\)|㈱)/;
  const accCtx =
    /口座(?:名義|情報|種別)|振込(?:み)?(?:先|口座)|振り?込(?:み)?先|ご入金(?:先)?|金融機関|ゆうちょ|普通(?:預金)?|当座(?:預金)?|請求.?記載.?振込|振込.?依頼|お振込|支払(?:方法)?/i;
  for (let i = 0; i < lines.length; i++) {
    if (!accCtx.test(lines[i])) continue;
    for (let j = i + 1; j < Math.min(i + 26, lines.length); j++) {
      const cand = lines[j];
      if (!cand || cand.length < 3 || cand.length > 140) continue;
      if (/御中/.test(cand)) continue;
      if (/株式会社.+御中|有限会社.+御中/.test(cand.replace(/\s/g, ""))) continue;
      if (/^※|^※※|備考[:：]?$/i.test(cand)) continue;
      if (/^[\d\s\-－ー・]{12,}$/.test(cand)) continue;
      if (/^[〒]?.{0,3}\d[\d\s\-－ー]{8,}$/.test(cand)) continue;
      if (/^(¥|￥)\s*\d/.test(cand)) continue;
      if (/店番|口座番号|記号|番号|支店.?コード|^口座種別\b/i.test(cand) && !companyShape.test(cand)) continue;
      if (companyShape.test(cand))
        return cleanInvoicePayeeDisplayText(cand.replace(/\s+/g, " ").trim().slice(0, 200));
      if (/（カ）|\(カ\)|カブシキ\s*ガイシャ/i.test(cand))
        return cleanInvoicePayeeDisplayText(cand.replace(/\s+/g, " ").trim().slice(0, 200));
      if (/^[ァ-ヿー／・\s\d（）()（）]+$/u.test(cand) && cand.replace(/\s/g, "").length >= 6)
        return cleanInvoicePayeeDisplayText(cand.replace(/\s+/g, " ").trim().slice(0, 200));
      if (/^[ァ-ヿ]{4,}(\s+[ァ-ヿー]+){2,}$/u.test(cand.trim()))
        return cleanInvoicePayeeDisplayText(cand.replace(/\s+/g, " ").trim().slice(0, 200));
    }
  }
  return "";
}

/** コンパクト比較用（会社名などのゆらぎ吸収） */
function compactVendorMatchKey(s) {
  return String(s ?? "")
    .normalize("NFKC")
    // NFKC で変換されないダッシュ類を半角ハイフンに統一（PDF抽出のカタカナ長音符・罫線等対策）
    .replace(/[\u30FC\u2015\u2014\u2013\u2012\u2010]/g, "-")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, "")
    .toLowerCase();
}

/** 「株式会社…」など法人名のかたまりをヒントから抜き出し（請求書PDFの支払先照合用） */
function extractCompanyLikeNameForVendorHint(hint) {
  const s = String(hint ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  const m = s.match(/((?:株式会社|有限会社|合同会社|（株）|\(株\)|㈱)[^\n，,]{1,52})/);
  if (m) return m[1].trim().slice(0, 80);
  return "";
}

/** 請求インポート用のゆらぎ正規化（PDFの細かい空白・異体字の隙間対策） */
function costImportHintNormalized(hint) {
  return String(hint ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[\uFEFF]/g, "")
    .trim();
}

/** 請求元・口座ブロックにつきがちな「口座名：」等を除き、表示／照合用文字列にする */
function cleanInvoicePayeeDisplayText(raw) {
  let t = costImportHintNormalized(raw);
  if (!t) return "";
  t = t.replace(
    /^(?:口座名|口座(?:名義|番号)|振込(?:先名義)?|お振込先|振込口座|名義(?:人)?|宛名|金融機関名)[：:=\s　]+/gi,
    ""
  ).trim();
  t = t.replace(/^(?:上記|下記|右記)[）\)]?\s*/,"").trim();
  // 挨拶文と会社名が2列マージで同一行になった場合、「。」区切りで会社名セグメントを抽出
  const companyShape = /株式会社|有限会社|合同会社|（株）|\(株\)|㈱/;
  if (companyShape.test(t) && /。/.test(t)) {
    const segs = t.split("。").map((s) => s.trim()).filter(Boolean);
    const compSeg = segs.find((s) => companyShape.test(s));
    if (compSeg && compSeg.length < t.length) t = compSeg;
  }
  // 挨拶・冒頭定型文が残っていれば除去（「毎度お引き立て…」「平素より…」等）
  t = t.replace(/^(?:毎度|いつも|平素より?|謹んで|拝啓|謹啓).{0,60}?\s/, "").trim();
  return t.slice(0, 200);
}

/** 名称・摘要ごとの先頭ラベルを除去（品目：〜 など） */
function costImportStripLeadingFieldLabels(t) {
  let s = costImportHintNormalized(t);
  s = s.replace(
    /^(?:口座名義?|振込先|品名|品目|項目|サービス名|内容|摘要|備考|説明|明細|適用)[：:=\s　]+/i,
    ""
  ).trim();
  return s;
}

/**
 * 通常明細行: 名称を短く、摘要へ読み取り全文／後段を載せる
 */
function costImportSplitNormalNameAndSummary(rawHint, docFallback) {
  const raw = costImportHintNormalized(rawHint);
  let body = costImportStripLeadingFieldLabels(
    stripDocumentPayeeFromCostImportHint(stripStructuralChunksFromCostImportHint(raw), docFallback)
  );
  if (!body) return { name: "—", summary: "", projectHint: raw };
  const sumLbl = body.match(/^(.{1,200}?)\s+(?:摘要|備考|説明|品名|品目|項目)[：:]\s*(.+)$/i);
  if (sumLbl) {
    const name = costImportStripLeadingFieldLabels(sumLbl[1]).trim() || "—";
    const summary = sumLbl[2].trim();
    return {
      name: name.slice(0, 240),
      summary: summary.slice(0, 500),
      projectHint: body
    };
  }
  const sepDot = body.search(/[·・／/]/);
  if (sepDot >= 10 && sepDot < body.length - 4) {
    const name = body.slice(0, sepDot).trim();
    const summary = body
      .slice(sepDot + 1)
      .replace(/^[·・/\s/]+/, "")
      .trim();
    return {
      name: (name || "—").slice(0, 240),
      summary: (summary || body).slice(0, 500),
      projectHint: body
    };
  }
  if (body.length > 42) {
    const take = 36;
    return {
      name: `${body.slice(0, take).trim()}…`,
      summary: body.slice(0, 520),
      projectHint: body
    };
  }
  return { name: body.slice(0, 240), summary: body.slice(0, 500), projectHint: body };
}

/** 「内訳10％対象」などは名称ではなく摘要（区分）側に寄せる。半角／全角カッコ・「内」「訳」分割にも対応 */
function isInvoiceBreakdownOrTaxCaptionHint(hint) {
  const lax = costImportHintNormalized(hint);
  if (!lax || isConsumptionTaxLineHint(hint)) return false;
  const s = lax.replace(/\s/g, "");
  const sc = s.replace(/[％]/g, "%");
  /** 「10%対象(税抜)」のように内訳見出し無しで出る請求小計行 */
  if (/10\s*[%％]\s*対象/.test(sc) && /税抜/.test(sc)) return true;
  if (/対象\s*[（(]?\s*税抜/.test(sc) && /10/.test(sc)) return true;
  /** 内訳 + 10 + 対象 + (税抜|税込) の典型的パターン（空白・半角括弧混在） */
  if (
    /内[￥¥/／]?訳/.test(sc) &&
    /10/.test(sc) &&
    /対[象向]/.test(sc) &&
    /税[抜込]/.test(sc)
  )
    return true;
  if (/内[￥¥/／]?訳/.test(sc) && /対[象向]/.test(sc) && /[（(]税[抜込][）)]/.test(lax)) return true;
  if (/\d{1,2}%対[象向][（(]?税[抜込][）)]?/.test(sc)) return true;
  if (/課税対象|適用税率|対象金額.*10|10%対[象向]|標準10/.test(sc)) return true;
  if (/内[￥¥/／]?訳\d/.test(sc) && /税[抜込]|%|対[象向]/.test(sc)) return true;
  return false;
}

/** 小計〜振込脚注・銀行名など、明細名称ではないブロックか（チャンク単位／行単位） */
function isPdfInvoiceFooterOrBankLikeChunk(txt) {
  const s = costImportHintNormalized(txt);
  if (!s) return true;
  if (/口座名義|振込(?:み)?先名義|お振込(?:み)?先|登録金融機関|金融機関コード|銀行コード/i.test(s))
    return true;
  if (/\bT\d{13}\b/.test(s)) return true;
  if (/適格請求書|請求書発行|インボイス|登録番号[:：\s]?T?\d+/i.test(s)) return true;
  if (
    /ご請求申し上げ|下記の通り\s*(?:、|，)?\s*ご請求|請求申し上げ|銀行\s*[（(]\s*\d{3,4}\s*[）)]/i.test(s)
  )
    return true;
  if (/沖縄県|那覇市|JP[-‐]?2.*ビル|おもろまち/i.test(s) && !/BOOKING|DB|バージョン|システム|サーバ|利用料/i.test(s))
    return true;
  if (/振込(?:先|銀行|金融機関)|振り込み(?:先)?|ご入金|お振込|支払(?:方法|条件)|振込.?手数料|総合.?振込|電信.?振替/i.test(
    s
  ))
    return true;
  if (
    /\b[A-Z]{2,}\s*銀行|琉球銀行|沖縄銀行|銀行\s*(?:東京|北海道|宮崎)?\b|銀\s*行\s*[（(]?本店|証券会社|証券株式会社|ジャパンネット銀行|楽天銀行|(?:みずほ|三菱|MUFJ|三菱UFJ|三井住友|SMB|りそな|野村|SBI)|信用金庫|信用組合|労働金庫|農林中央金庫|商工組合中央金庫/i.test(
      s
    )
  )
    return true;
  if (/支店\b|分店\b|(?:本店)?営\s*業部|口\s*座\s*(?:種別|番号)|店\s*番|記\s*号|普\s*通|当\s*座|ゆうちょ|金融機構名/i.test(s)) return true;
  if (/^〒|^郵便番号|住所[:：]?[都道府県]/i.test(s) && !/サービス|利用料|広告/i.test(s)) return true;
  return false;
}

/** 請求書の計・請求総額行など（明細サービス名ではない）か */
function isPdfInvoiceTotalsOrDemandCaptionChunk(txt) {
  const lax = costImportHintNormalized(txt);
  if (!lax || isConsumptionTaxLineHint(txt) || isInvoiceBreakdownOrTaxCaptionHint(txt)) return false;
  if (isTaxIncludedTotalHint(txt)) return true;
  if (isLikelyInvoiceSubtotalExTaxHint(txt)) return true;
  const trim = lax.trim();
  const sCompact = trim.replace(/\s/g, "");
  if (sCompact.length <= 52) {
    if (
      /^小計|^合計|^御計|^税抜|^税込|お支払い金額|ご請求金額|御請求額|^請求金額|請求書合計|^計$|^円$/i.test(trim) ||
      /^(計|済|済み)$/i.test(trim)
    )
      return true;
    if (/請求総額|総請求額|請求合計|ご請求総額|お支払総額|御請求総額/i.test(trim)) return true;
    if (/^(請求)?総額$/i.test(trim)) return true;
  }
  return false;
}

/** 名称欄へ入れない文字列チャンク（近傍結合や名称列から除去する） */
function isPdfInvoiceNonItemNameChunk(chunk) {
  const s = costImportHintNormalized(chunk);
  if (!s) return true;
  if (isConsumptionTaxLineHint(chunk)) return true;
  if (isInvoiceBreakdownOrTaxCaptionHint(chunk)) return true;
  if (isLikelyAccountNumberOnlyChunk(chunk)) return true;
  if (isPdfInvoiceFooterOrBankLikeChunk(chunk)) return true;
  if (isPdfInvoiceTotalsOrDemandCaptionChunk(chunk)) return true;
  return false;
}

/** 「 · 」で連結された名称から、請求定型・脚注・内訳行チャンクをはずす */
function stripStructuralChunksFromCostImportHint(hint) {
  const hFull = costImportHintNormalized(hint);
  if (!hFull) return "";
  const parts = hFull.split(/\s*[·・]\s*/).map((p) => p.trim()).filter(Boolean);
  const kept =
    parts.length > 1
      ? parts.filter(
          (p) => !isInvoiceTableHeaderNoiseChunk(p) && !isPdfInvoiceNonItemNameChunk(p)
        )
      : isPdfInvoiceNonItemNameChunk(hFull)
        ? []
        : [hFull];
  const joined = kept.join(" · ").trim();
  return stripBankTransferNoiseSegmentsFromCostImportHint(
    stripInvoiceTableMergedNoise(joined)
  );
}

/** 小計〜銀行情報など「名称」ではなく摘要側の請求脚注として扱う */
function isPdfInvoiceMetaCaptionHint(clientHint) {
  const lax = costImportHintNormalized(clientHint);
  if (!lax) return false;
  if (isConsumptionTaxLineHint(clientHint) || isInvoiceBreakdownOrTaxCaptionHint(clientHint)) return false;
  if (isPdfInvoiceFooterOrBankLikeChunk(clientHint)) return true;
  if (isPdfInvoiceTotalsOrDemandCaptionChunk(clientHint)) return true;
  return false;
}

/** 部分一致で満点（100）にしてよいか。「本社」「東京」のような短い名前は摘要に含まれるだけで別の取引先を
 *  選んでしまうため、4文字未満は英数字の略称（NTT・GMO 等）が単語として現れるときだけ認める */
function vendorSubstringMatchAllowed(shorter, longer) {
  if (shorter.length >= 4) return true;
  if (!/^[A-Za-z0-9]+$/.test(shorter)) return false;
  return new RegExp(`(^|[^A-Za-z0-9])${shorter}([^A-Za-z0-9]|$)`).test(longer);
}

function vendorMatchScoreAgainstLabel(haystackRaw, needleLabel) {
  const h = String(haystackRaw ?? "").trim();
  const c = String(needleLabel ?? "").trim();
  if (!h || !c) return 0;
  if (h === c) return 100;
  if (h.includes(c) && vendorSubstringMatchAllowed(c, h)) return 100;
  if (c.includes(h) && vendorSubstringMatchAllowed(h, c)) return 100;
  const tH = compactVendorMatchKey(h);
  const tC = compactVendorMatchKey(c);
  if (!tC) return 0;
  if (tC.length >= 6 && tH.includes(tC)) return 90;
  if (tH.length >= 6 && tC.includes(tH)) return 84;
  if (tC.length >= 4 && tH.includes(tC)) return 76;
  if (tH.length >= 4 && tC.includes(tH)) return 72;
  return 0;
}

/** PDFの本文から請求元（支払先）の会社名らしき文字列を推定。請求先「御中／様」側は請求発行元ではないため除外し、優先して振込名義などから読む */
function extractPdfDocumentPayeeNameHint(fullText) {
  const lines = String(fullText ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return "";
  const fromBank = extractPdfBankAccountHolderHint(lines);
  if (fromBank) return fromBank;

  const companyShape = /(株式会社|有限会社|合同会社|（株）|\(株\)|㈱)/;
  /** 「御中」や請求書宛は取引先（こちら）側の名前が並ぶことが多く、請求発行元（支払先）の手掛かりとしては使わない */
  const rejectIssuedToRecipient = (line) =>
    /御中/.test(line) ||
    /(?:請求書|請求.?書)\s*[：:●・]?\s*宛(?:先)?|^宛名[:：]?|請求.?宛(?:先)?|請求.?送付(?:先)|(?:ご|お)請求(?:先)?|送付(?:先)/i.test(line) ||
    /ご担当者|担当者\s*様/.test(line); // 担当者行は受取側の宛先であり発行者ではない
  const badStart =
    /^(〒|tel|fax|mail|https?:|登録番号|請求書|ご請求|請求金額|合計|小計|消費税|内訳|備考|振込|口座|適格)/i;
  let best = "";
  let bestSc = -1;
  const invReg = /\bT\d{13}\b/;
  lines.slice(0, 55).forEach((line, i) => {
    if (!line || line.length < 4 || line.length > 88) return;
    if (rejectIssuedToRecipient(line)) return;
    if (badStart.test(line)) return;
    if (/御中$|様$/.test(line) && !companyShape.test(line)) return;
    let sc = 0;
    if (companyShape.test(line)) sc += 58;
    if (/事業所|本社|支店/.test(line)) sc += 6;
    if (/〒|TEL|電話|FAX/.test(line)) sc += 5;
    if (invReg.test(line)) sc += 45;
    sc += Math.max(0, 22 - i) * 0.35;
    if (/請求|Invoice|納品|領収|明細/.test(line)) sc -= 22;
    // 挨拶文と会社名が混在する行は信頼性が低い（2列マージ由来）
    if (/毎度|いつも|平素より?|拝啓|謹啓|御引き立て|お引き立て|ご愛顧/.test(line)) sc -= 35;
    // 担当者・様 が混入している行は受取側とのマージ行として大幅減点
    if (/ご担当者|担当者\s*様|\s様\s/.test(line)) sc -= 50;
    if (sc > bestSc) {
      bestSc = sc;
      best = line;
    }
  });
  /** 登録番号は発行者側の手掛かり。直前段落に御中側が混入していないときだけ参照 */
  lines.forEach((line, idx) => {
    if (!invReg.test(line)) return;
    for (let j = Math.max(0, idx - 4); j < idx; j++) {
      const ln = lines[j];
      if (!ln || ln.length > 88 || badStart.test(ln)) continue;
      if (rejectIssuedToRecipient(ln)) continue;
      if (/御中$|様$/.test(ln) && !companyShape.test(ln)) continue;
      let sc = 40;
      if (companyShape.test(ln)) sc += 50;
      if (sc > bestSc) {
        bestSc = sc;
        best = ln;
      }
    }
  });
  lines.forEach((line, i) => {
    if (!line || line.length < 5 || line.length > 92) return;
    if (!companyShape.test(line)) return;
    if (rejectIssuedToRecipient(line)) return;
    if (badStart.test(line)) return;
    if (/請求書|請求一覧|請求一覧表|請求データ|適格請求書|領収|納品|明細一覧/i.test(line)) return;
    let sc = 32;
    if (i < 35) sc += 14;
    if (invReg.test(line)) sc += 28;
    if (/御中$|様$/.test(line)) sc -= 18;
    if (sc > bestSc) {
      bestSc = sc;
      best = line;
    }
  });
  return cleanInvoicePayeeDisplayText(best.replace(/\s+/g, " ").trim().slice(0, 200));
}

/** 取込ヒントから支払先表示名を決定（パートナーマスタ優先→クライアント） */
function resolveCostVendorFromPartnersAndClients(hint) {
  const rawFull = String(hint ?? "")
    .replace(/\u3000/g, " ")
    .trim();
  if (!rawFull) return { vendorLabel: "", unmatched: true, source: null };
  const core = extractCompanyLikeNameForVendorHint(rawFull);
  const scored = [];

  for (const pt of uiState.partnersCache || []) {
    if (pt?.sales_hidden) continue; // 営業に非表示のパートナーは自動照合の対象外（支払先候補と整合）
    const name = String(pt?.name ?? "").trim();
    const branch = String(pt?.branch ?? "").trim();
    const disp = partnerMasterDisplayLabel(pt) || name;
    if (!disp) continue;
    const sc = Math.max(
      vendorMatchScoreAgainstLabel(rawFull, name),
      vendorMatchScoreAgainstLabel(rawFull, branch),
      core ? vendorMatchScoreAgainstLabel(core, name) : 0,
      core ? vendorMatchScoreAgainstLabel(core, branch) : 0
    );
    if (sc > 0) scored.push({ sc, label: disp, source: "partner" });
  }

  for (const cl of clients) {
    const name = String(cl.name || "").trim();
    const sn = String(cl.shortName || "").trim();
    const tryLb = (lb) => {
      if (!lb) return 0;
      return Math.max(
        vendorMatchScoreAgainstLabel(rawFull, lb),
        core ? vendorMatchScoreAgainstLabel(core, lb) : 0
      );
    };
    const sc = Math.max(tryLb(name), tryLb(sn));
    if (sc > 0) scored.push({ sc, label: name || sn, source: "client" });
  }

  scored.sort((a, b) => b.sc - a.sc);
  if (!scored.length) return { vendorLabel: rawFull, unmatched: true, source: null };
  const top = scored[0];
  return { vendorLabel: top.label, unmatched: false, source: top.source };
}

function costImportResolveDocumentPayeeUiKeyFromMeta(cleanRaw, resolved) {
  const keyRaw = "__pdf_raw__";
  if (!resolved || resolved.unmatched) return keyRaw;
  if (resolved.source === "partner") {
    for (const pt of uiState.partnersCache || []) {
      if (pt?.sales_hidden) continue; // 営業に非表示のパートナーには解決しない
      const disp = partnerMasterDisplayLabel(pt) || String(pt?.name ?? "").trim();
      if (!disp || pt?.id == null) continue;
      if (
        disp === resolved.vendorLabel ||
        String(pt.name || "").trim() === resolved.vendorLabel
      )
        return `partner:${pt.id}`;
    }
  }
  if (resolved.source === "client") {
    for (const cl of clients) {
      const n = String(cl.name || "").trim();
      const sn = String(cl.shortName || "").trim();
      if (cl?.id != null && (n === resolved.vendorLabel || sn === resolved.vendorLabel)) {
        return `client:${cl.id}`;
      }
    }
  }
  return keyRaw;
}

function applyDocumentPayeeUiKeyToImportRows(w) {
  const rows = w?.rows;
  if (!rows?.length) return;
  const key = String(w.documentPayeeUiKey || "__pdf_raw__").trim() || "__pdf_raw__";
  let label = "";
  let unmatched = false;
  if (key.startsWith("partner:")) {
    const idStr = key.slice(8).trim();
    const pt = (uiState.partnersCache || []).find((x) => String(x?.id ?? "") === idStr);
    if (pt) {
      label = partnerMasterDisplayLabel(pt) || String(pt.name || "").trim();
      unmatched = false;
    } else {
      const r = resolveCostVendorFromPartnersAndClients(
        cleanInvoicePayeeDisplayText(w.documentPayeeRaw || "") || w.documentPayeeRaw || ""
      );
      label = r.vendorLabel;
      unmatched = r.unmatched;
    }
  } else if (key.startsWith("client:")) {
    const idStr = key.slice(7).trim();
    const cl = clients.find((x) => String(x?.id ?? "") === idStr);
    if (cl) {
      label = String(cl.name || "").trim() || "(クライアント)";
      unmatched = false;
    } else {
      const r = resolveCostVendorFromPartnersAndClients(
        cleanInvoicePayeeDisplayText(w.documentPayeeRaw || "") || w.documentPayeeRaw || ""
      );
      label = r.vendorLabel;
      unmatched = r.unmatched;
    }
  } else {
    const clean = cleanInvoicePayeeDisplayText(w.documentPayeeRaw || "");
    const hint = clean || String(w.documentPayeeRaw || "").trim();
    const r = resolveCostVendorFromPartnersAndClients(hint);
    label = r.vendorLabel || hint || "";
    unmatched = !!r.unmatched;
  }
  for (const row of rows) {
    row.vendorResolvedLabel = label;
    row.vendorMasterUnmatched = unmatched;
  }
}

/** Step2 プレビュー: DOM の入力を rev.rows に反映（再描画・手動行追加・登録前に使用） */
function syncCostImportPreviewRowsFromDom(rev) {
  if (!rev || rev.phase !== "preview" || !Array.isArray(rev.rows)) return;
  document.querySelectorAll("[data-cost-review-pick]").forEach((el) => {
    if (!(el instanceof HTMLSelectElement)) return;
    const idx = Number.parseInt(String(el.getAttribute("data-cost-review-pick") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !rev.rows[idx]) return;
    rev.rows[idx].projectIdPick = el.value;
  });
  document.querySelectorAll("[data-cost-review-amount-ex]").forEach((el) => {
    if (!(el instanceof HTMLInputElement)) return;
    const idx = Number.parseInt(String(el.getAttribute("data-cost-review-amount-ex") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !rev.rows[idx]) return;
    if (rev.rows[idx].importCaptionKind === "consumption_tax") return;
    // 入力欄は「税抜金額」。この sync は全行に対しトグル・登録前などに毎回走るため、
    // 実際に値が変わった行だけ更新する（未編集行を毎回書き換えると、税込取込の
    // 正確な税額が round(ex*0.1) に丸め直されたり、保持中の元取込値が壊れて
    // トグル往復が狂う）。編集時は税抜額基準で消費税10%と元取込値を揃える。
    const newEx = parseCostReviewYenInput(el.value);
    if (Number.isFinite(newEx) && newEx !== rev.rows[idx].amountExTax) {
      rev.rows[idx].amountExTax = newEx;
      rev.rows[idx].consumptionTax = costImportRecomputedTaxYen(rev.rows[idx], newEx);
      rev.rows[idx].importedRawAmount = newEx;
    }
  });
  document.querySelectorAll("[data-cost-review-note]").forEach((el) => {
    if (!(el instanceof HTMLTextAreaElement)) return;
    const idx = Number.parseInt(String(el.getAttribute("data-cost-review-note") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !rev.rows[idx]) return;
    rev.rows[idx].reviewNote = el.value;
  });
  document.querySelectorAll("[data-cost-review-manual-summary]").forEach((el) => {
    if (!(el instanceof HTMLTextAreaElement)) return;
    const idx = Number.parseInt(String(el.getAttribute("data-cost-review-manual-summary") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !rev.rows[idx]) return;
    rev.rows[idx].manualSummaryText = el.value;
  });
}

/** 原価の登録者の表示名。記録が無い（登録者を記録する前の原価・システム取込）／ユーザーが見つからないときはその旨 */
function costCreatorLabel(dbId) {
  if (dbId == null) return "不明（記録前の原価・自動取込）";
  return getUserById(`u${dbId}`)?.name || `ユーザー#${dbId}`;
}

/** 原価フォームの「支払状況」欄（経理/マスターのみ）。未受領/受領済を選ぶ。支払日が入っていれば支払済（支払日で決まる）。
 *  未受領の値は今の値（未発注・発注済み等）をそのまま使い、触らずに保存しても状態を書き換えない */
function costFormPayStatusFieldHtml(f) {
  if (!userCanRegisterCostReceipt()) return "";
  if (String(f.paidDate || "").trim()) {
    return `<label>支払状況
            <select class="pj-input" disabled title="支払日が入っているので支払済です（支払日を消すと選べます）"><option>支払済</option></select>
          </label>`;
  }
  const received = costReceiptGroup({ payStatus: f.payStatus, isProvisional: f.isProvisional }) === "received";
  const pendingValue = received ? "請求書待ち" : String(f.payStatus || "") || "請求書待ち";
  return `<label>支払状況
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="payStatus">
              <option value="${escapeHtmlAttr(pendingValue)}" ${received ? "" : "selected"}>未受領（請求書がまだ）</option>
              <option value="支払待ち" ${received ? "selected" : ""}>受領済（請求書あり）</option>
            </select>
          </label>`;
}

/** 原価登録時の既定の状態。経理の登録は請求書を受け取っていることが多いので 発注済・受領済（DB値=支払待ち）。
 *  営業の登録（原価割当ページの請求書取込＝salesMode）は経理の受領チェックを通すため従来どおり 未発注・未設定 */
function costRegisterDefaultStatuses(salesMode) {
  return salesMode ? { payStatus: "未発注", orderStatus: "" } : { payStatus: "支払待ち", orderStatus: "発注済" };
}

/** 取込の発注ステータス（全行共通）。未選択なら請求書受取は「発注済」、他は登録者に応じた既定（経理=発注済・営業=未設定） */
function costImportEffectiveOrderStatus(rev) {
  const picked = rev?.importOrderStatus;
  if (picked !== undefined) return COST_ORDER_STATUSES.includes(picked) ? picked : "";
  return rev?.inboxItemId ? "発注済" : costRegisterDefaultStatuses(rev?.salesMode === true).orderStatus;
}

function costImportOrderStatusOptionsHtml(rev) {
  const cur = costImportEffectiveOrderStatus(rev);
  return ["", ...COST_ORDER_STATUSES]
    .map((s) => `<option value="${escapeHtmlAttr(s)}" ${s === cur ? "selected" : ""}>${escapeHtml(s || "未設定")}</option>`)
    .join("");
}

function createManualCostImportPreviewRow() {
  return {
    key: `ir-manual-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
    clientHint: "",
    nameColumnText: "—",
    amountExTax: 0,
    consumptionTax: 0,
    importedRawAmount: 0,
    note: "",
    reviewNote: "",
    projectIdPick: "",
    vendorResolvedLabel: "",
    vendorMasterUnmatched: false,
    importCaptionKind: "normal",
    summaryCaptionRead: "",
    suggestedProjectExtras: "",
    isManualPreviewRow: true,
    manualSummaryText: "",
    targetMonthOverride: null
  };
}

/** PDFプレビュー用・請求元プルダウンの option HTML */
function costImportDocumentPayeeSelectOptionsHtml(wizard) {
  const cleanShown = cleanInvoicePayeeDisplayText(wizard.documentPayeeRaw || "");
  const pdfLine = wizard.documentPayeeRaw || "";
  const selKey = String(wizard.documentPayeeUiKey || "__pdf_raw__").trim() || "__pdf_raw__";
  const pdfLabel = cleanShown
    ? cleanShown
    : pdfLine.trim()
      ? pdfLine.trim().slice(0, 72)
      : "未取得";
  let html =
    `<option value="__pdf_raw__" ${selKey === "__pdf_raw__" ? "selected" : ""}>${escapeHtml(pdfLabel)}</option>` +
    `<optgroup label="${escapeHtml("パートナー")}">`;
  for (const pt of uiState.partnersCache || []) {
    const id = pt?.id;
    if (id == null || String(id).trim() === "") continue;
    if (pt?.sales_hidden && `partner:${id}` !== selKey) continue; // 営業に非表示は除外（選択中の値は残して既存の支払先を失わない）
    const vk = `partner:${id}`;
    const lab = partnerMasterDisplayLabel(pt) || String(pt?.name ?? "").trim() || vk;
    html += `<option value="${escapeHtmlAttr(vk)}" ${vk === selKey ? "selected" : ""}>${escapeHtml(lab)}</option>`;
  }
  html += `</optgroup><optgroup label="${escapeHtml("クライアント")}">`;
  for (const cl of clients) {
    const id = cl?.id;
    if (id == null || String(id).trim() === "") continue;
    if (cl?.salesHidden && `client:${id}` !== selKey) continue; // 営業に非表示は除外（選択中の値は残して既存の支払先を失わない）
    const vk = `client:${id}`;
    const lab = String(cl.name || "").trim() || vk;
    html += `<option value="${escapeHtmlAttr(vk)}" ${vk === selKey ? "selected" : ""}>${escapeHtml(lab)}</option>`;
  }
  html += `</optgroup>`;
  return html;
}

/** Google広告レポートのアカウント名から案件照合用テキストを抽出（A列） */
function googleAdsAccountNameToProjectMatchHint(accountName) {
  let s = String(accountName ?? "")
    .replace(/\u3000/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  // 先頭の中黒「・」「･」を除去（Meta等で箇条書きプレフィックスとして使われる）
  s = s.replace(/^[・･]+\s*/, "").trim();
  // 全体がワンエイティ自身を指す行は取り込み対象外として空文字を返す
  if (/^ワンエイティ$/i.test(s) || /^one\s*eighty$/i.test(s)) return "";
  // 末尾の「・ワンエイティ」「／ワンエイティ」を除去
  s = s.replace(/\s*[・･]\s*ワンエイティ\s*$/i, "").trim();
  s = s.replace(/\s*[・･]\s*one\s*eighty\s*$/i, "").trim();
  s = s.replace(/[／/]\s*ワンエイティ\s*$/i, "").trim();
  s = s.replace(/[／/]\s*one\s*eighty\s*$/i, "").trim();
  // 除去の結果ワンエイティ自身になった場合もスキップ
  if (!s || /^ワンエイティ$/i.test(s) || /^one\s*eighty$/i.test(s)) return "";
  // スラッシュ区切りは「案件名／会社名」形式なので最初に案件名部分を取り出す
  const slashIdx = s.search(/[／/]/);
  if (slashIdx > 3) s = s.slice(0, slashIdx).trim();
  s = s.replace(/[（(]\s*ワンエイティ\s*[）)]/i, "").trim();
  s = s.replace(/^【[^】]*】\s*/, "").trim();
  s = s.replace(/^[A-Z]{2,8}_/i, "").trim();
  s = s.replace(/[（(][A-Z]{0,4}\d{5,12}[）)]/gi, "").trim();
  s = s.replace(/\s+/g, " ").trim();
  const paren = s.match(/^(.+?)[（(]([^）)]{2,40})[）)]\s*$/);
  if (paren) {
    const projectPart = String(paren[1] || "").trim();
    const clientPart = String(paren[2] || "").trim();
    return `${projectPart} ${clientPart}`.trim() || s;
  }
  return s;
}

function advertisingCsvDetectNameAmountColumns(headerCells, sourceKey) {
  const heads = headerCells.map((h) => String(h ?? "").trim());
  const lows = heads.map(csvHeaderNormalizeForMatch);
  const NAME_KEYS = ["クライアント名", "クライアント", "顧客名", "顧客", "広告アカウント", "企業名", "広告主名", "広告主", "アカウント名", "アカウント", "キャンペーン名", "キャンペーン", "広告セット", "customer", "client", "account name", "account", "campaign name", "campaign", "広告セット名"];
  const AMT_KEYS = [
    { key: "当月利用額合計", score: 14 },
    { key: "利用額合計", score: 13 },
    { key: "請求額", score: 12 },
    { key: "請求金额", score: 12 },
    { key: "billed amount", score: 11 },
    { key: "invoice amount", score: 11 },
    { key: "金額", score: 8 },
    { key: "広告費", score: 8 },
    { key: "支払額", score: 8 },
    { key: "配信費用", score: 4 },
    { key: "delivery cost", score: 4 },
    { key: "クリック単価", score: 2 },
    { key: "費用", score: 2 },
    { key: "総費用", score: 3 },
    { key: "コスト", score: 3 },
    { key: "cost", score: 3 },
    { key: "amount", score: 3 },
    { key: "amount spent", score: 3 },
    { key: "spent", score: 3 }
  ];

  let nameIdx = -1;
  let nameScore = -1;
  lows.forEach((cell, i) => {
    if (!cell) return;
    let s = 0;
    NAME_KEYS.forEach((k) => {
      const kk = csvHeaderNormalizeForMatch(k);
      if (!kk) return;
      if (cell === kk) s += kk.length >= 8 ? 5 : 4;
      else if (cell.includes(kk)) s += kk.length >= 12 ? 3 : kk.length >= 6 ? 2 : 1;
    });
    if (sourceKey === "google" && (cell === "アカウント名" || cell.includes("アカウント名"))) s += 6;
    if (s > nameScore) {
      nameScore = s;
      nameIdx = i;
    }
  });
  let amtIdx = -1;
  let amtScore = -1;
  lows.forEach((cell, i) => {
    if (!cell) return;
    let s = 0;
    AMT_KEYS.forEach(({ key, score }) => {
      const kk = csvHeaderNormalizeForMatch(key);
      if (!kk) return;
      if (cell === kk) s += score;
      else if (cell.includes(kk)) s += Math.max(1, score - 2);
    });
    if (s > amtScore) {
      amtScore = s;
      amtIdx = i;
    }
  });
  if (sourceKey === "google" && heads.length >= 5) {
    const norm0 = normalizeCsvHeaderLabel(heads[0]);
    if (nameIdx < 0 && norm0.includes("アカウント名")) nameIdx = 0;
    const norm4 = normalizeCsvHeaderLabel(heads[4]);
    if (norm4.includes("請求額")) amtIdx = 4;
    else if (amtIdx < 0 && norm4.includes("請求")) amtIdx = 4;
  }
  // For Yahoo YDN/YSS: A列=アカウント名(摘要＆案件マッチング), L列=当月利用額合計(税込)
  // B列は「企業名（広告主）」= ワンエイティ自社名のため案件マッチングには使用しない
  let companyIdx = -1;
  if (sourceKey === "ydn" || sourceKey === "yss") {
    nameIdx = 0;    // A列: アカウント名（摘要＆マッチング）
    companyIdx = -1; // B列は自社名のため不使用
    if (heads.length > 11) amtIdx = 11; // L列: 当月利用額合計（税込）
    else if (heads.length > 10) amtIdx = 10; // フォールバック: K列
  }
  // For LINE広告: B列=広告アカウント名(摘要), C列=広告主名(案件マッチング), F列=ご請求金額合計(税抜)
  if (sourceKey === "line" && heads.length >= 6) {
    nameIdx = 1;    // B列: 広告アカウント名
    companyIdx = 2; // C列: 広告主名
    amtIdx = 5;     // F列: ご請求金額合計
  }
  // For Meta広告: A列=アカウント名(摘要), C列=消化金額JPY(金額)
  if (sourceKey === "meta") {
    if (nameIdx < 0) nameIdx = 0;
    if (amtIdx < 0 && heads.length >= 3) {
      // C列「消化金額（JPY）」を優先
      const norm2 = normalizeCsvHeaderLabel(heads[2] || "");
      if (norm2.includes("消化") || norm2.includes("金額") || norm2.includes("JPY")) amtIdx = 2;
    }
  }
  return { heads, nameIdx, amtIdx, companyIdx };
}

/** 広告プラットフォームCSV（一般形）からレビュー行用データ */
function parseAdvertisingCostCsvRows(csvText, sourceKey) {
  const lines = String(csvText ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l);
  if (lines.length < 2) return [];
  const meta = COST_IMPORT_CSV_SOURCE_META[sourceKey] || { label: "", vendor: "広告" };
  let headerLineIdx = 0;
  // 先頭にメタ行があるフォーマットはヘッダー行を明示的にスキャン（最大10行）
  if (sourceKey === "google" || sourceKey === "line") {
    const scanKey = sourceKey === "google" ? "アカウント名" : "広告アカウントID";
    for (let hi = 0; hi < Math.min(10, lines.length); hi++) {
      const row = parseCsvRow(lines[hi]);
      const norm0 = normalizeCsvHeaderLabel(String(row[0] || ""));
      if (norm0.startsWith(scanKey) || norm0 === scanKey) {
        headerLineIdx = hi;
        break;
      }
    }
  }
  // Meta広告: A列=アカウント名 の行をヘッダーとして探す（最大5行）
  if (sourceKey === "meta") {
    for (let hi = 0; hi < Math.min(5, lines.length); hi++) {
      const row = parseCsvRow(lines[hi]);
      const norm0 = normalizeCsvHeaderLabel(String(row[0] || ""));
      if (norm0 === "アカウント名" || norm0.startsWith("アカウント名")) {
        headerLineIdx = hi;
        break;
      }
    }
  }
  const headerRow = parseCsvRow(lines[headerLineIdx]);
  const detected = advertisingCsvDetectNameAmountColumns(headerRow, sourceKey);
  const { nameIdx, amtIdx, companyIdx, heads } = detected;
  if (nameIdx < 0 || amtIdx < 0) {
    throw new Error(
      sourceKey === "google"
        ? "CSVから「アカウント名」（A列）と「請求額」（E列）を特定できませんでした（1行目の見出しを確認してください）"
        : "CSVから名前列と金額列を特定できませんでした（ヘッダー行を確認してください）"
    );
  }
  let taxMode = classifyCsvAmountColumnTaxMode(heads[amtIdx]);
  if (taxMode === "unknown") taxMode = "exclusive";
  const isYahoo = sourceKey === "ydn" || sourceKey === "yss";
  if (isYahoo) taxMode = "inclusive";
  // LINE広告の「ご請求金額合計」は税抜
  // (taxMode はデフォルト exclusive のままでよい)
  const rows = [];
  for (let i = headerLineIdx + 1; i < lines.length; i += 1) {
    const cells = parseCsvRow(lines[i]);
    const hintRaw = cells[nameIdx] != null ? String(cells[nameIdx]).trim() : "";
    const amt = parseProjectCsvAmount(cells[amtIdx] != null ? cells[amtIdx] : "");
    if (!amt || amt <= 0) continue;
    // 小計・合計行（名前列が空）はスキップ
    if (!hintRaw) continue;
    let clientHint;
    let projectMatchHint;
    if (sourceKey === "google") {
      const cleaned = googleAdsAccountNameToProjectMatchHint(hintRaw);
      // ワンエイティ自身のアカウント行はスキップ（cleaned が空文字 = 自社名のみの行）
      if (!cleaned) continue;
      projectMatchHint = cleaned || hintRaw || `${meta.label} 行${i}`;
      clientHint = projectMatchHint ? `${projectMatchHint} Google広告` : `${meta.label} 行${i}`;
    } else if (isYahoo || sourceKey === "line" || sourceKey === "meta") {
      // YDN/YSS/LINE/Meta: アカウント名から摘要、広告主名/企業名で案件マッチング
      const cleanedAcct = googleAdsAccountNameToProjectMatchHint(hintRaw);
      // ワンエイティ自身のアカウント行はスキップ
      if (!cleanedAcct) continue;
      const acctLabel = cleanedAcct;
      clientHint = `${acctLabel} ${meta.label}`;
      const companyRaw = companyIdx >= 0 && cells[companyIdx] != null ? String(cells[companyIdx]).trim() : "";
      projectMatchHint = companyRaw || acctLabel;
    } else {
      clientHint = hintRaw || `${meta.label} 行${i}`;
      projectMatchHint = clientHint;
    }
    const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(amt, taxMode);
    const useAccountKey = sourceKey === "google" || isYahoo || sourceKey === "line" || sourceKey === "meta";
    rows.push({
      clientHint,
      projectMatchHint,
      accountNameKey: useAccountKey ? hintRaw : "",
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: `${meta.label} CSV取込`,
      skipStructuralStrip: true
    });
  }
  return rows;
}

/** お名前ドットコム管理画面の表コピペ（タブ/連続空白区切り）を原価行に変換 */
function parseOnamaePastedRows(pastedText) {
  const lines = String(pastedText ?? "")
    .split(/\r?\n/)
    .map((l) => l.replace(/　/g, " ").trimEnd())
    .filter((l) => l.trim());
  if (!lines.length) return [];
  const meta = COST_IMPORT_CSV_SOURCE_META.onamae;
  const datePat = /^\d{4}\/\d{1,2}\/\d{1,2}$/;
  const taxPat = /^\d{1,2}\s*[%％]$/;
  const amountPat = /^[\d,]+\s*円$/;
  const domainPat = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
  const rows = [];
  for (const line of lines) {
    // タブ優先、無ければ2連続以上の空白で分割
    let cells = line.includes("\t") ? line.split("\t") : line.split(/\s{2,}/);
    cells = cells.map((c) => c.trim()).filter(Boolean);
    if (cells.length < 2) continue;
    // ヘッダー行（金額セルが無い）はスキップ
    let domainHint = "";
    let amountCell = "";
    let descParts = [];
    for (const c of cells) {
      if (!amountCell && amountPat.test(c)) { amountCell = c; continue; }
      if (taxPat.test(c)) continue;
      if (datePat.test(c)) continue;
      if (/^\d+$/.test(c)) continue; // 数量列
      if (!domainHint && domainPat.test(c)) { domainHint = c; continue; }
      descParts.push(c);
    }
    if (!amountCell) continue;
    const amt = parseProjectCsvAmount(amountCell.replace(/円$/, ""));
    if (!amt || amt <= 0) continue; // 0円行はスキップ
    const desc = descParts.join(" ").replace(/\s+/g, " ").trim();
    // 税率列がある＝表示金額は税込。標準10%で税抜・消費税に分割
    const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(amt, "inclusive");
    const clientHint = [desc, domainHint].filter(Boolean).join(" ").trim() || `${meta.label} 行`;
    rows.push({
      clientHint,
      projectMatchHint: domainHint || desc,
      domainHint,
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: `${meta.label} 貼り付け取込`,
      skipStructuralStrip: true
    });
  }
  return rows;
}

// ドメイン管理で社内の内部ドメインに使っているクライアント名（fp_clients にも実在する正規値）。
// この行のサーバー費用は特定の営業の売上に対応しないため、原価箱ではなく販管費へ入れる
// （api/cost-boxes.js の「会社の箱は既存の販管費バケットで代用する」に合わせる）。
const COST_INTERNAL_DOMAIN_CLIENT_NAME = "社内処理";

/** お名前ドットコム貼付行のドメイン列から、ドメイン管理（fp_domains）経由で計上先を決める。
 *  案件名との照合が当てにならないため案件は推定せず、
 *  ・クライアントが「社内処理」の行 → 販管費バケット
 *  ・それ以外で owner から担当営業が判る行 → その営業の今期の原価箱（find-or-create）
 *  ・どちらでもない行 → 空欄（誤った案件へ付けない）
 *  戻り値: 貼付行の domainHint（元文字列）→ 計上先 UI-ID の Map（決まらなかった分は含まない）。 */
async function resolveOnamaeDomainCostAssignments(parsedRows) {
  // ドメイン管理の軽量データがまだ届いていないと全行「担当営業不明」になるので必ず待つ
  await fetchDomainsLightForCostImport();
  const fiscalYearStartYear = costBoxFiscalYearStartYear();
  const assignByHint = new Map();
  const ownerUiIdByHint = new Map();
  for (const r of Array.isArray(parsedRows) ? parsedRows : []) {
    const hint = String(r.domainHint || "").trim();
    if (!hint || assignByHint.has(hint) || ownerUiIdByHint.has(hint)) continue;
    const domainRows = findDomainRowsMatchingHint(hint);
    if (!domainRows.length) continue;
    if (domainRows.some((d) => String(d.clientName || "").trim() === COST_INTERNAL_DOMAIN_CLIENT_NAME)) {
      assignByHint.set(hint, COST_SALES_EXPENSE_PROJECT_UI_ID);
      continue;
    }
    const ownerName = domainRows.map((d) => String(d.owner || "").trim()).find(Boolean) || "";
    const ownerUser = ownerName ? findUserForProjectCsv(ownerName) : null;
    if (ownerUser) ownerUiIdByHint.set(hint, ownerUser.id);
  }
  const boxUiIdByOwnerUiId = new Map();
  for (const [hint, ownerUiId] of ownerUiIdByHint) {
    if (!boxUiIdByOwnerUiId.has(ownerUiId)) {
      const result = await ensureCostBoxProjectUiId(ownerUiId, fiscalYearStartYear);
      boxUiIdByOwnerUiId.set(ownerUiId, result.ok ? result.uiId : "");
    }
    const boxUiId = boxUiIdByOwnerUiId.get(ownerUiId);
    if (boxUiId) assignByHint.set(hint, boxUiId);
  }
  return assignByHint;
}

/** UPSIDERカード明細CSV（取引日・利用先・出金金額・カード名…）を原価行に変換。
 *  E列「出金金額」（税込）を原価、J列「カード名」（案件名で運用）を摘要・案件マッチングに使う。
 *  出金が無い行（チャージ等の入金行）は除外する。 */
function parseUpsiderCsvRows(csvText) {
  const lines = String(csvText ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l);
  if (lines.length < 2) return [];
  const meta = COST_IMPORT_CSV_SOURCE_META.upsider;
  // ヘッダー行から列位置を特定（先頭5行をスキャン）
  let headerLineIdx = -1;
  let idxDate = 0;
  let idxStore = 1;
  let idxOut = 4;
  let idxCard = 9;
  for (let hi = 0; hi < Math.min(5, lines.length); hi++) {
    const heads = parseCsvRow(lines[hi]).map(normalizeCsvHeaderLabel);
    const out = heads.indexOf("出金金額");
    const card = heads.indexOf("カード名");
    if (out >= 0 && card >= 0) {
      headerLineIdx = hi;
      idxOut = out;
      idxCard = card;
      const d = heads.findIndex((h) => h === "取引日" || h === "利用日");
      if (d >= 0) idxDate = d;
      const st = heads.indexOf("利用先");
      if (st >= 0) idxStore = st;
      break;
    }
  }
  if (headerLineIdx < 0) {
    throw new Error("CSVから「出金金額」「カード名」の列を特定できませんでした（UPSIDERの取引明細CSVか確認してください）");
  }
  const rows = [];
  for (let i = headerLineIdx + 1; i < lines.length; i += 1) {
    const cells = parseCsvRow(lines[i]);
    const amt = parseProjectCsvAmount(cells[idxOut] != null ? cells[idxOut] : "");
    if (!amt || amt <= 0) continue; // 入金（チャージ）行・金額なし行はスキップ
    const card = String(cells[idxCard] ?? "").trim();
    const store = String(cells[idxStore] ?? "").trim();
    const date = String(cells[idxDate] ?? "").trim();
    const hint = card || store;
    if (!hint) continue;
    // カード決済額は税込。標準10%で税抜・消費税に分割（プレビューで税抜へ切替可）
    const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(amt, "inclusive");
    rows.push({
      clientHint: [hint, date].filter(Boolean).join(" "),
      projectMatchHint: hint,
      accountNameKey: card,
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: `${meta.label} CSV取込`,
      skipStructuralStrip: true
    });
  }
  return rows;
}

/** テコラス（AWS）請求明細CSV（Cloud Service・アカウントID・商品名・合計価格（＄）・為替レート・税抜価格（￥））を原価行に変換。
 *  「税抜価格（￥）」を原価（税抜）とし、AWSアカウントIDを紐づけ記憶のキーにする（2回目以降は自動で同じ案件へ）。 */
function parseTecolasCsvRows(csvText) {
  const lines = String(csvText ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l);
  if (lines.length < 2) return [];
  const meta = COST_IMPORT_CSV_SOURCE_META.tecolas;
  let headerLineIdx = -1;
  let idxAccount = -1;
  let idxYen = -1;
  let idxItem = -1;
  let idxService = -1;
  let idxUsd = -1;
  let idxRate = -1;
  for (let hi = 0; hi < Math.min(5, lines.length); hi++) {
    const heads = parseCsvRow(lines[hi]).map(normalizeCsvHeaderLabel);
    const yen = heads.findIndex((h) => h.startsWith("税抜価格"));
    const account = heads.indexOf("アカウントID");
    if (yen >= 0 && account >= 0) {
      headerLineIdx = hi;
      idxYen = yen;
      idxAccount = account;
      idxItem = heads.indexOf("商品名");
      idxUsd = heads.findIndex((h) => h.startsWith("合計価格"));
      idxRate = heads.findIndex((h) => h.startsWith("為替レート"));
      idxService = heads.findIndex((h) => h.replace(/^\uFEFF/, "") === "CloudService"); // 正規化で空白が消える・先頭列なのでBOMも外す
      break;
    }
  }
  if (headerLineIdx < 0) {
    throw new Error("CSVから「アカウントID」「税抜価格（￥）」の列を特定できませんでした（テコラスの請求明細CSVか確認してください）");
  }
  const rows = [];
  for (let i = headerLineIdx + 1; i < lines.length; i += 1) {
    const cells = parseCsvRow(lines[i]);
    const amt = parseProjectCsvAmount(cells[idxYen] != null ? cells[idxYen] : "");
    if (!amt || amt <= 0) continue;
    const account = String(cells[idxAccount] ?? "").trim();
    const item = idxItem >= 0 ? String(cells[idxItem] ?? "").trim() : "";
    const service = idxService >= 0 ? String(cells[idxService] ?? "").trim() : "";
    const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(amt, "exclusive");
    // 円換算の根拠（ドル額・請求書記載レート）をプレビューの備考に残す
    const fxNote = idxUsd >= 0 && idxRate >= 0
      ? costImportFxNoteText(cells[idxUsd], { currency: "USD", rate: Number(cells[idxRate]), source: "invoice" })
      : "";
    rows.push({
      clientHint: [service, account, item].filter(Boolean).join(" ") || `${meta.label} 行`,
      projectMatchHint: account || item,
      accountNameKey: account,
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: `${meta.label} CSV取込`,
      ...(fxNote ? { fxNote } : {}),
      skipStructuralStrip: true
    });
  }
  return rows;
}

/** 汎用貼り付け（Excelコピー・メール本文等）のフォールバックパーサ（AI未設定・失敗時）。
 * タブ/連続空白区切りの行から金額セルを推定し、残りを摘要として原価行に変換する。 */
function parseGenericPastedRows(pastedText) {
  const lines = String(pastedText ?? "")
    .split(/\r?\n/)
    .map((l) => l.replace(/　/g, " ").trimEnd())
    .filter((l) => l.trim());
  const explicitAmountPat = /^[¥￥]?\s*\d{1,3}(?:,\d{3})+(?:\.\d+)?\s*円?$|^[¥￥]\s*\d+$|^\d+\s*円$/;
  const bareAmountPat = /^\d{3,}$/;
  const taxRatePat = /^\d{1,2}\s*[%％]$/;
  const datePat = /^\d{4}[/-]\d{1,2}([/-]\d{1,2})?$/;
  const totalPat = /^(小計|合計|総計|請求金額|ご請求金額|税込合計|お支払い?金額|振込|消費税)/;
  const rows = [];
  for (const line of lines) {
    let cells = line.includes("\t") ? line.split("\t") : line.split(/\s{2,}/);
    cells = cells.map((c) => c.trim()).filter(Boolean);
    if (cells.length < 2) continue;
    let explicitAmount = "";
    let bareAmount = "";
    const descParts = [];
    for (const c of cells) {
      if (taxRatePat.test(c)) continue;
      if (datePat.test(c)) continue;
      // 金額らしいセルは最後のものを採用（単価×数量＝金額 の表で右端を取る）
      if (explicitAmountPat.test(c)) { explicitAmount = c; continue; }
      if (bareAmountPat.test(c)) { bareAmount = c; continue; }
      if (/^\d+$/.test(c)) continue; // 数量列
      descParts.push(c);
    }
    const amountCell = explicitAmount || bareAmount;
    if (!amountCell) continue;
    const desc = descParts.join(" ").replace(/\s+/g, " ").trim();
    if (!desc || totalPat.test(desc)) continue; // 見出し・小計・合計・税のみの行は除外
    const amt = parseProjectCsvAmount(amountCell.replace(/[¥￥円]/g, ""));
    if (!amt || amt <= 0) continue;
    rows.push({
      clientHint: desc,
      projectMatchHint: desc,
      amountExTax: amt,
      consumptionTax: 0,
      note: "貼り付け取込",
      skipStructuralStrip: true
    });
  }
  return rows;
}

/** 摘要が消費税・内訳の税額行のみか（案件割当不要・税額だけ合算） */
function isConsumptionTaxLineHint(hint) {
  const s = String(hint || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return false;
  if (/^(消費税|内\s*消\s*費税|地方消費税)(?:\s|（|\(|の|$|[①-⑨])/i.test(s)) return true;
  if (/^※\s*うち消費税/i.test(s)) return true;
  if (/^うち\s*消費税\b/i.test(s)) return true;
  if (/^（内訳）\s*消費税/i.test(s)) return true;
  if (/^[（(]?\s*\d{1,2}\s*[％％%]\s*消費税/u.test(s)) return true;
  if (/^\d{1,2}\s*[％％%]\s*消費税/u.test(s)) return true;
  if (/^10\s*%\s*消費税/i.test(s)) return true;
  if (/10\s*[%％]\s*消費税|消費税\s*[（(]?\s*10\s*[%％]/i.test(s)) return true;
  return false;
}

function pickCostImportVendorResolutionHint(row, documentPayeeFallback) {
  const doc = String(row?.payeeFromDocument ?? "").trim() || String(documentPayeeFallback ?? "").trim();
  if (doc) return doc;
  const line = String(row?.clientHint ?? "").trim();
  return extractCompanyLikeNameForVendorHint(line) || line;
}

/** プレビュー「摘要」列: 税・内訳・請求脚注行は種別ラベル＋読み取りテキスト、通常は — */
function costImportPreviewCaptionKind(clientHint) {
  const h = clientHint;
  if (isConsumptionTaxLineHint(h)) return "consumption_tax";
  if (isInvoiceBreakdownOrTaxCaptionHint(h)) return "breakdown";
  if (isPdfInvoiceMetaCaptionHint(h)) return "invoice_meta";
  const s = String(h || "")
    .replace(/\s+/g, " ")
    .trim();
  // 長い行（実際の明細テキスト）が「消費税」と「%」を含むだけで消費税行扱いにならないよう長さ制限
  if (/消費税/.test(s) && /\d\s*[％%]/.test(s) && s.length <= 80) return "consumption_tax";
  return "normal";
}

/** 税込の請求合計など（Footer）。案件ごとの割当対象から外す */
function isTaxIncludedTotalHint(hint) {
  const s = String(hint || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return false;
  if (/税抜/.test(s) && !/税込/.test(s)) return false;
  return (
    /(?:御)?請求(?:額|金額)|お支払い(?:金額)?|ご請求(?:額)?/.test(s) && /税込|（税込/.test(s)
  );
}

/** 請求書の税抜「小計・対象金額」に相当する行（これだけ案件に割当） */
function isLikelyInvoiceSubtotalExTaxHint(hint) {
  const s = String(hint || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s || isConsumptionTaxLineHint(s)) return false;
  if (/消費税/.test(s) && !/税抜/.test(s)) return false;
  if (/(?:御)?請求(?:額|金額)|お支払い(?:金額)?|ご請求(?:額)?/i.test(s) && /税込|（税込/.test(s)) return false;
  if (/^小計\b/i.test(s)) return true;
  if (/^計\b$/i.test(s)) return true;
  if (/税抜|税別/.test(s) && /(小計|合計)/i.test(s)) return true;
  if (
    /^合計\b/i.test(s) &&
    !/税込/i.test(s) &&
    s.replace(/\s/g, "").length <= 12
  ) {
    return true;
  }
  if (/^対象金額\b/i.test(s)) return true;
  if (/10\s*[%％]\s*対象|対象\s*[（(]?\s*税抜|税抜\s*[）)]?\s*10\s*[%％]/i.test(s) && /税抜|税別/.test(s)) return true;
  return false;
}

/**
 * 税抜金額が同じ「明細」と「内訳10%対象(税抜)」が両方あるとき、摘要のある明細だけ残し内訳行を捨てる
 */
function dropDuplicateSubtotalWhenDetailSameAmount(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  return arr.filter((r) => {
    const hint = String(r.clientHint || "");
    const ex = Math.round(Number(r.amountExTax ?? 0)) || 0;
    if (!ex || !isCostImportConsolidateSubtotalHint(hint)) return true;
    const dupDetail = arr.some(
      (x) =>
        x !== r &&
        (Math.round(Number(x.amountExTax ?? 0)) || 0) === ex &&
        !isCostImportConsolidateSubtotalHint(String(x.clientHint || "")) &&
        String(x.clientHint || "").trim().length > 0
    );
    return !dupDetail;
  });
}

/** total（円）を weights の比で整数に按分する（端数は大きい余りの順に1円ずつ配り、合計は必ず total に一致） */
function allocateYenProportionally(total, weights) {
  const sumW = weights.reduce((a, w) => a + Math.max(0, w), 0);
  if (!sumW) return weights.map((_, i) => (i === 0 ? total : 0));
  const raw = weights.map((w) => (total * Math.max(0, w)) / sumW);
  const out = raw.map(Math.floor);
  let rest = total - out.reduce((a, v) => a + v, 0);
  const order = raw.map((v, i) => [v - Math.floor(v), i]).sort((x, y) => y[0] - x[0]);
  for (let k = 0; rest > 0 && k < order.length; k++, rest--) out[order[k][1]] += 1;
  return out;
}

/** consolidate: 税額を合算する「本命」の税抜小計行か（内訳ボックスの10%対象(税抜)を含む） */
function isCostImportConsolidateSubtotalHint(hint) {
  if (isLikelyInvoiceSubtotalExTaxHint(hint)) return true;
  const h = String(hint || "");
  if (isConsumptionTaxLineHint(h)) return false;
  const lax = costImportHintNormalized(hint);
  if (!/税抜|税別/i.test(lax)) return false;
  const sc = lax.replace(/\s/g, "").replace(/[％]/g, "%");
  if (/10\s*%?\s*対象/.test(sc) && /税抜/.test(sc)) return true;
  if (/対象\s*[（(]?\s*税抜/.test(sc) && /10/.test(sc)) return true;
  return isInvoiceBreakdownOrTaxCaptionHint(hint);
}

function normalizeTaxOnlyCostImportRow(row) {
  let ex = Math.round(Number(row.amountExTax ?? row.amount ?? 0)) || 0;
  let tx = Math.round(Number(row.consumptionTax ?? 0)) || 0;
  const hint = String(row.clientHint || "");
  if (isConsumptionTaxLineHint(hint)) {
    const sum = ex + tx;
    return { ...row, amountExTax: 0, consumptionTax: sum, clientHint: hint };
  }
  return { ...row, amountExTax: ex, consumptionTax: tx, clientHint: hint };
}

/**
 * CSV/PDF 取込後の結合処理: 消費税行はリストから除外し税額を税抜小計行に寄せる。
 * 請求書レイアウトに「小計」等があるときは、その行のみ案件割当対象とする。
 */
function consolidateCostImportParsedRows(rows) {
  const arrIn = Array.isArray(rows) ? rows : [];
  if (!arrIn.length) return arrIn;

  const normalized = arrIn.map(normalizeTaxOnlyCostImportRow);
  let taxSum = 0;
  let nonTax = [];
  for (const r of normalized) {
    if (isConsumptionTaxLineHint(String(r.clientHint || ""))) {
      taxSum += Math.round(Number(r.consumptionTax ?? 0)) || 0;
      continue;
    }
    nonTax.push({ ...r });
  }

  nonTax = dropDuplicateSubtotalWhenDetailSameAmount(nonTax);

  /** 税込合計フッタのみ除外。内訳「10%対象(税抜)」だけに絞らない（明細摘要の行が消えるため） */
  let merged = nonTax.filter((r) => !isTaxIncludedTotalHint(String(r.clientHint || "")));
  if (!merged.length) merged = nonTax.slice();

  // 明細が複数ある請求書は、消費税行の合計を明細の税抜金額で按分する（1行目に全額寄せると
  //  「税抜260,000・消費税59,000」のように1行だけ税が過大・他の行は0になる）
  // 按分先は「残る明細行」だけ（小計・合計・内訳キャプションなど最後に落とす行へ配ると税が消える）
  const isTaxAllocDetailRow = (r) => {
    const h = String(r.clientHint || "");
    return (
      (Math.round(Number(r.amountExTax ?? 0)) || 0) > 0 &&
      !isCostImportConsolidateSubtotalHint(h) &&
      !isInvoiceBreakdownOrTaxCaptionHint(h) &&
      !isPdfInvoiceTotalsOrDemandCaptionChunk(h)
    );
  };
  const detailIdx = taxSum > 0 ? merged.map((r, i) => (isTaxAllocDetailRow(r) ? i : -1)).filter((i) => i >= 0) : [];
  // 明細側に既に消費税が付いている入力（AI行は10%付与済み等）は按分しない＝従来どおり（二重計上を薄く広げて気づきにくくしない）
  if (detailIdx.length > 1 && detailIdx.every((i) => !(Math.round(Number(merged[i].consumptionTax ?? 0)) || 0))) {
    const shares = allocateYenProportionally(taxSum, detailIdx.map((i) => Math.round(Number(merged[i].amountExTax ?? 0)) || 0));
    detailIdx.forEach((i, k) => {
      merged[i].consumptionTax = (Math.round(Number(merged[i].consumptionTax ?? 0)) || 0) + shares[k];
    });
  } else if (taxSum > 0 && merged.length) {
    let targetIdx = merged.findIndex((r) => {
      const h = String(r.clientHint || "");
      return (
        (Math.round(Number(r.amountExTax ?? 0)) || 0) > 0 &&
        !isCostImportConsolidateSubtotalHint(h)
      );
    });
    if (targetIdx < 0) {
      targetIdx = merged.findIndex((r) =>
        isCostImportConsolidateSubtotalHint(String(r.clientHint || ""))
      );
    }
    if (targetIdx < 0) {
      targetIdx = merged.reduce((best, r, i, m) => {
        const v =
          (Math.round(Number(r.amountExTax ?? 0)) || 0) +
          (Math.round(Number(r.consumptionTax ?? 0)) || 0);
        const bv =
          (Math.round(Number(m[best].amountExTax ?? 0)) || 0) +
          (Math.round(Number(m[best].consumptionTax ?? 0)) || 0);
        return v > bv ? i : best;
      }, 0);
    }
    const target = merged[targetIdx];
    target.consumptionTax =
      (Math.round(Number(target.consumptionTax ?? 0)) || 0) + taxSum;
  }

  return merged.filter((r) => {
    const h = String(r.clientHint || "");
    if (isInvoiceBreakdownOrTaxCaptionHint(h)) return false;
    if (isPdfInvoiceTotalsOrDemandCaptionChunk(h)) return false;
    const ex = Math.round(Number(r.amountExTax ?? 0)) || 0;
    const tx = Math.round(Number(r.consumptionTax ?? 0)) || 0;
    // 値引き・インセンティブは負数で来る（AI の指示どおり）。0 円の行だけ落とす（以前は負数も落として原価が過大になった）
    return ex !== 0 || tx !== 0;
  });
}

/**
 * 同一行に「税抜明細」と「税込合計」が並ぶPDFで、税抜側だけを金額スキャンに使う。
 * 「お支払金額」「ご請求額（税込）」等より左の部分を返す。
 */
function pdfLineSegmentBeforeTaxInclusiveAmountLabels(line) {
  const s = String(line ?? "");
  if (!s.trim()) return "";
  const rxList = [
    /お支払(?:い)?金額/i,
    /(?:ご請求|御請求)(?:額)?/i,
    /請求金額\s*[（(]?\s*税込/i,
    /(?:合計|総計)金額\s*[（(]?\s*税込/i,
    /税込(?:み|額)?(?:\s*[（(]|$)/i,
    /[（(]\s*税込\s*[）)]/,
    /御請求額\s*[（(]\s*税込/i,
    /お支払.*税込/i
  ];
  let cut = s.length;
  for (const rx of rxList) {
    const m = rx.exec(s);
    if (m && m.index >= 0 && m.index < cut) {
      const pre = s.slice(Math.max(0, m.index - 12), m.index);
      if (/税抜\s*$/.test(pre)) continue;
      cut = Math.min(cut, m.index);
    }
  }
  return s.slice(0, cut).trim();
}

/**
 * 税込ラベル付きの金額は拾わず、残りで最大の円建て金額を取る（明細の税抜を優先するため）
 */
function normalizePdfInvoiceDetailLineForParsing(raw) {
  let t = costImportHintNormalized(String(raw ?? "").replace(/\u3000/g, " "));
  t = t.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff10 + 0x30));
  t = t.replace(/．/g, ".").replace(/，/g, ",");
  t = t.replace(/\s+/g, " ").trim();
  return t;
}

/**
 * 「PG 1.5人月 450,000 675,000 ・ SE 0.2人月 …」のように複数役務が1行に結合された請求明細を分割する。
 * 同一行に税込合計（918,500 等）が並ぶと末尾3桁パターンが誤判定されやすいため、人月が複数あるときは別処理と組み合わせる。
 */
function extractManmonthPdfDetailSegments(lineText) {
  const s = normalizePdfInvoiceDetailLineForParsing(lineText);
  if (!s || !/人月/.test(s)) return null;
  const re = /([^\s・･／]+)\s*(\d+(?:\.\d+)?)\s*人月\s+([\d,]+)\s+([\d,]+)/g;
  const out = [];
  let m;
  while ((m = re.exec(s)) !== null) {
    const role = m[1].trim();
    const man = m[2];
    const amt = Math.round(parseProjectCsvAmount(m[4])) || 0;
    if (!role || amt < 100) continue;
    out.push({
      hint: `${role} ${man}人月`.trim(),
      amountExTax: amt
    });
  }
  return out.length >= 2 ? out : null;
}

/**
 * 請求明細表（摘要・数量・単価・明細金額）が PDF 抽出で1行に連結されたときに行を復元する。
 * 「・」区切りや REM-チケット先頭、数字直後の REM- で分割し、各チャンク末尾の数量・単価・明細金額を採る。
 */
function extractInvoiceTableRowsFromPdfMergedLine(lineText) {
  const s0 = normalizePdfInvoiceDetailLineForParsing(lineText);
  if (!s0 || s0.length < 24) return null;
  let sDigits = s0.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xff10 + 0x30));
  /** 「金額REM-123」のように中黒なしで連結されたときも分割できるようにする */
  sDigits = sDigits.replace(/([\d,])(?=\s*REM-\d+)/gi, "$1 · ");

  function collectRowsFromChunks(chunks) {
    const rows = [];
    for (const rawChunk of chunks) {
      const norm = costImportHintNormalized(String(rawChunk ?? ""))
        .replace(/\u3000/g, " ")
        .replace(/\s+/g, " ")
        .replace(/\s*[·・]+\s*$/u, "")
        .trim();
      if (!norm || norm.length < 8) continue;
      if (isInvoiceTableHeaderNoiseChunk(norm)) continue;
      const hit = matchInvoiceTableQtyUnitPriceLineTail(norm);
      if (!hit || hit.lineAmount < 100 || hit.qty < 1 || hit.qty > 999999) continue;
      let desc = stripInvoiceTableMergedNoise(hit.head).trim();
      desc = stripBankTransferNoiseSegmentsFromCostImportHint(desc);
      if (desc.length < 6) continue;
      if (/^[\d,.\sー−−]+$/.test(desc)) continue;
      rows.push({ hint: desc.slice(0, 500), amountExTax: Math.round(hit.lineAmount) });
    }
    return rows;
  }

  let rows = collectRowsFromChunks(sDigits.split(/\s*[·・]\s*/).map((b) => b.trim()).filter(Boolean));
  if (rows.length >= 2) return rows;

  const sSplitRem = sDigits.replace(/([\d,])(?=REM-\d+)/gi, "$1 · ");
  rows = collectRowsFromChunks(sSplitRem.split(/(?=REM-\d+)/i).map((b) => b.trim()).filter(Boolean));
  if (rows.length >= 2) return rows;

  return null;
}

/**
 * 摘要のみの行の直後に「数量 単価 明細金額」だけが続くPDF向けに1行へ結合する。
 *（例: 「- REM-127 …」の次行が「1 30,000 30,000」／「REM-128 …」の次行が「1 4,626 4,626」）
 */
function tryMergeInvoiceDetailLineWithFollowingQtyLine(rawLines, li) {
  const raw = String(rawLines[li] ?? "").trim();
  if (!raw) return { mergedLine: raw, skipNext: false };
  const cur = normalizePdfInvoiceDetailLineForParsing(raw);
  if (!cur || cur.length < 6) return { mergedLine: raw, skipNext: false };
  if (isInvoiceTableHeaderNoiseChunk(cur)) return { mergedLine: raw, skipNext: false };
  if (matchInvoiceTableQtyUnitPriceLineTail(cur)) return { mergedLine: raw, skipNext: false };
  if (li + 1 >= rawLines.length) return { mergedLine: raw, skipNext: false };
  const nxt = normalizePdfInvoiceDetailLineForParsing(String(rawLines[li + 1] ?? ""));
  if (!nxt) return { mergedLine: raw, skipNext: false };
  const nxtTrim = nxt.trim();
  // 末尾の日付ノイズ（利用月・期間）を除去した次行も試験対象にする
  const nxtStripped = stripTrailingInvoiceDateTokens(nxtTrim);
  const tripleOnly = /^\d{1,6}\s+[\d,，０-９]+\s+[\d,，０-９]+$/u.test(nxtTrim)
    || /^(?:一式|一ヶ所|一か所|一箇所|一個|式)\s+[\d,，０-９]+\s+[\d,，０-９]+$/u.test(nxtTrim)
    || /^\d{1,6}\s+[\d,，０-９]+\s+[\d,，０-９]+$/u.test(nxtStripped)
    || /^[\d,，０-９]+\s+\d{1,6}\s+[\d,，０-９]+$/u.test(nxtStripped); // 単価 数量 金額 形式
  if (!tripleOnly) return { mergedLine: raw, skipNext: false };
  // REM-xxx パターンに加え、数字で終わらない説明行（一般的な「摘要 + 次行に数量/単価/金額」形式）も対応
  const hasRem = /REM-\d+/i.test(cur);
  const bulletRem = /^\s*[-－ー]\s*REM-\d+/i.test(cur);
  const looksLikeDescriptionLine = cur.trim().length >= 4 && !/[\d０-９]$/.test(cur.trimEnd());
  if (!hasRem && !bulletRem && !looksLikeDescriptionLine) return { mergedLine: raw, skipNext: false };
  const combined = `${cur} ${nxtStripped}`.trim();
  const hit = matchInvoiceTableQtyUnitPriceLineTail(combined);
  if (hit && hit.lineAmount >= 100 && hit.qty >= 1 && hit.qty < 1e6) {
    return { mergedLine: combined, skipNext: true };
  }
  return { mergedLine: raw, skipNext: false };
}

/** 行末に未完のテキスト（次行へ続く REM など）があるとき、最後の「数量 単価 明細金額」までで切り詰める */
function pdfLineTruncateAfterLastQtyUnitTotalTriple(lineText) {
  const t = normalizePdfInvoiceDetailLineForParsing(lineText);
  if (!t) return "";
  const cur = t.trim();
  const tailPat = /\s+(\d{1,6})\s+([\d,，０-９]{1,18})\s+([\d,，０-９]{1,18})\s*$/u;
  const m = cur.match(tailPat);
  if (!m || m.index == null) return cur;
  return cur.slice(0, m.index + m[0].length).trim();
}

/** 1件だけヒント化されたあとも、摘要文字列に複数の人月明細が残っていれば行を分割する */
function expandMergedManmonthPdfHints(hints) {
  const arr = Array.isArray(hints) ? hints : [];
  const out = [];
  for (const h of arr) {
    const seg = extractManmonthPdfDetailSegments(String(h.clientHint || ""));
    if (seg?.length >= 2) {
      for (const s of seg) {
        out.push({
          ...h,
          clientHint: s.hint.slice(0, 500),
          amountExTax: s.amountExTax,
          consumptionTax: 0,
          // 元の取込額は分割後の行の金額（元の連結行全体の額を引き継ぐと、税込/税抜の切替で各行が合計額になる）
          importedRawAmount: s.amountExTax
        });
      }
    } else {
      out.push(h);
    }
  }
  return out;
}

/** 摘要に複数明細が連結されたまま残っているヒントを、表形式パターンで行分割する */
function expandMergedInvoiceTablePdfHints(hints) {
  const arr = Array.isArray(hints) ? hints : [];
  const out = [];
  for (const h of arr) {
    const segs = extractInvoiceTableRowsFromPdfMergedLine(String(h.clientHint || ""));
    if (!segs || segs.length < 2) {
      out.push(h);
      continue;
    }
    let any = false;
    for (const s of segs) {
      let rest = costImportHintNormalized(s.hint);
      rest = stripStructuralChunksFromCostImportHint(rest);
      const payee = String(h.payeeFromDocument || "").trim();
      if (payee) {
        const wo = stripDocumentPayeeFromCostImportHint(rest, payee);
        if (String(wo || "").trim().length >= 1) rest = wo;
      }
      rest = costImportHintNormalized(rest);
      if (isPdfInvoiceTotalsOrDemandCaptionChunk(rest) || isPdfInvoiceFooterOrBankLikeChunk(rest)) continue;
      rest = stripInvoiceTableMergedNoise(rest);
      rest = stripBankTransferNoiseSegmentsFromCostImportHint(rest);
      if (!String(rest || "").trim()) continue;
      if (isPdfInvoiceTotalsOrDemandCaptionChunk(costImportHintNormalized(rest))) continue;
      any = true;
      out.push({
        ...h,
        clientHint: rest.slice(0, 500),
        amountExTax: s.amountExTax,
        consumptionTax: 0,
        // 元の取込額は分割後の行の金額（元の連結行全体の額を引き継ぐと、税込/税抜の切替で各行が合計額になる）
        importedRawAmount: s.amountExTax
      });
    }
    if (!any) out.push(h);
  }
  return out;
}

function maxYenAmountFromPdfLineDetailSegment(lineSeg) {
  const seg = String(lineSeg ?? "").trim();
  if (!seg) return 0;
  const yenPat =
    /(?:¥|￥)\s*([\d,]{1,16}(?:\.\d{1,2})?)|([\d,]{1,16}(?:\.\d{1,2})?)\s*円(?:\([^)]*\))?|(?:合計|小計|税込(?:額)?|御請求額|請求(?:額|金額)?|ご請求(?:額)?|お支払(?:い)?(?:金額)?|金額)[:：\s　]*([\d,]{1,16}(?:\.\d{1,2})?)/gi;
  const amts = [];
  yenPat.lastIndex = 0;
  let m;
  while ((m = yenPat.exec(seg)) !== null) {
    const whole = m[0] || "";
    if (m[3] != null && m[3] !== "" && /(?:税込|お支払|ご請求|御請求|合計|小計)/i.test(whole) && !/税抜/.test(whole)) {
      continue;
    }
    const num = parseProjectCsvAmount(m[1] || m[2] || m[3]);
    if (num && num >= 100) amts.push(Math.round(num));
  }
  if (!amts.length) {
    // フォールバック: ¥/円 なしの表形式金額（例: 「一式 10,000 10,000」最右カラム）
    const trailingAmtM = seg.match(/(?:^|\s)(\d{1,3}(?:,\d{3})+)\s*$/);
    if (trailingAmtM) {
      const n = Math.round(parseProjectCsvAmount(trailingAmtM[1]));
      if (n >= 1000) return n;
    }
    return 0;
  }
  // 同一行に2つの金額があり比率が約1.1倍（税抜/税込の並び）なら小さい方（税抜）を返す
  if (amts.length === 2) {
    const [lo, hi] = [...amts].sort((a, b) => a - b);
    if (hi / lo > 1.05 && hi / lo < 1.15) return lo;
  }
  return Math.max(...amts);
}

/** PDFのプレーンテキストから候補行を組み立て（媒体レポート共通の簡易推定）。sourceVendor が空または "PDF" のときは注釈を付けない（重複表示を避ける） */
function parseAdvertisingPdfTextHints(text, sourceVendor) {
  const docPayeeOne = extractPdfDocumentPayeeNameHint(text);
  const rawLines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const hints = [];
  /** 小口・広告単価なども拾う（下限を下げ過ぎるとノイズ増のため調整しやすい値） */
  const MIN_AMOUNT_FOR_HINT = 100;
  const noteLine = (() => {
    const s = String(sourceVendor ?? "").trim();
    if (!s || /^pdf$/i.test(s)) return "";
    return `${s}より取込`;
  })();

  for (let li = 0; li < rawLines.length; li++) {
    const baseLi = li;
    const { mergedLine: mergedRaw, skipNext } = tryMergeInvoiceDetailLineWithFollowingQtyLine(rawLines, li);
    if (skipNext) li += 1;
    const line = String(mergedRaw ?? "").trim();
    if (!line) continue;

    const mmSegments = extractManmonthPdfDetailSegments(line);
    if (mmSegments?.length >= 2) {
      for (const seg of mmSegments) {
        let rest = costImportHintNormalized(seg.hint);
        rest = stripStructuralChunksFromCostImportHint(rest);
        if (docPayeeOne) {
          const wo = stripDocumentPayeeFromCostImportHint(rest, docPayeeOne);
          if (String(wo || "").trim().length >= 1) rest = wo;
        }
        const restNorm = costImportHintNormalized(rest);
        if (isPdfInvoiceTotalsOrDemandCaptionChunk(restNorm)) continue;
        rest = stripInvoiceTableMergedNoise(restNorm);
        rest = stripBankTransferNoiseSegmentsFromCostImportHint(rest);
        if (!String(rest || "").trim()) continue;
        if (isPdfInvoiceTotalsOrDemandCaptionChunk(costImportHintNormalized(rest))) continue;
        const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(seg.amountExTax, "exclusive");
        hints.push({
          clientHint: rest.slice(0, 500),
          payeeFromDocument: docPayeeOne,
          amountExTax,
          consumptionTax,
          importedRawAmount: rawImported,
          note: noteLine
        });
      }
      continue;
    }

    const invTableSegs = extractInvoiceTableRowsFromPdfMergedLine(line);
    if (invTableSegs && invTableSegs.length >= 2) {
      for (const seg of invTableSegs) {
        let rest = costImportHintNormalized(seg.hint);
        rest = stripStructuralChunksFromCostImportHint(rest);
        if (docPayeeOne) {
          const wo = stripDocumentPayeeFromCostImportHint(rest, docPayeeOne);
          if (String(wo || "").trim().length >= 1) rest = wo;
        }
        const restNorm = costImportHintNormalized(rest);
        if (isPdfInvoiceTotalsOrDemandCaptionChunk(restNorm)) continue;
        rest = stripInvoiceTableMergedNoise(restNorm);
        rest = stripBankTransferNoiseSegmentsFromCostImportHint(rest);
        if (!String(rest || "").trim()) continue;
        if (isPdfInvoiceTotalsOrDemandCaptionChunk(costImportHintNormalized(rest))) continue;
        const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(seg.amountExTax, "exclusive");
        hints.push({
          clientHint: rest.slice(0, 500),
          payeeFromDocument: docPayeeOne,
          amountExTax,
          consumptionTax,
          importedRawAmount: rawImported,
          note: noteLine
        });
      }
      continue;
    }

    const lineForAmt = pdfLineTruncateAfterLastQtyUnitTotalTriple(line);

    let bestAmt = 0;
    let tableTail = matchInvoiceTableQtyUnitPriceLineTail(lineForAmt);
    let usedTableTail = false;
    if ((String(lineForAmt).match(/人月/g) || []).length >= 2) {
      tableTail = null;
    }
    // 税率行（「税率10%対象 小計 255,000」等）はスキップ
    if (/^税率\s*\d+\s*[%％]\s*(?:対象|軽減)/.test(lineForAmt)) continue;
    if (
      tableTail &&
      Math.abs(tableTail.lineAmount) >= MIN_AMOUNT_FOR_HINT &&
      tableTail.qty >= 0 &&
      tableTail.qty < 1e6
    ) {
      bestAmt = Math.round(tableTail.lineAmount);
      usedTableTail = true;
    } else {
      const detailSeg = pdfLineSegmentBeforeTaxInclusiveAmountLabels(lineForAmt);
      const scanSeg = detailSeg.length >= 2 ? detailSeg : lineForAmt;
      bestAmt = maxYenAmountFromPdfLineDetailSegment(scanSeg);
    }
    if (Math.abs(bestAmt) < MIN_AMOUNT_FOR_HINT) continue;
    // tableTailのheadに納品日プレフィックスが含まれる場合は除去
    const usedHead = usedTableTail && tableTail?.head
      ? stripLeadingDeliveryDatePrefix(tableTail.head)
      : null;
    let rest = usedHead && usedHead.length >= 2 ? usedHead : stripPdfLineMoneyArtifacts(lineForAmt);
    const tableDesc = usedHead && usedHead.length >= 6 ? usedHead : extractInvoiceTableLeadingDescription(lineForAmt);
    const summaryCol =
      extractCostImportSummaryColumnFromLine(lineForAmt) || extractCostImportSummaryColumnFromLine(rest);
    const tekiyouRest =
      extractCostImportTekiyouFromLine(lineForAmt) || extractCostImportTekiyouFromLine(rest);
    if (tableDesc.length >= 6) rest = tableDesc;
    else if (summaryCol.length >= 2 && !isPdfInvoiceFooterOrBankLikeChunk(summaryCol)) rest = summaryCol;
    else if (tekiyouRest.length >= 2 && !isPdfInvoiceFooterOrBankLikeChunk(tekiyouRest)) rest = tekiyouRest;
    const trivialNumeric = /^[\d.,\-\s％%ー\-−]+$/.test(rest);
    const bareSummary = /^(小計|合計|計)$/i.test(rest);
    if ((!rest || trivialNumeric || rest.length < 2) && !bareSummary) {
      const fromCtx = inferNeighborPdfSummaryHint(rawLines, baseLi, docPayeeOne);
      rest =
        fromCtx && fromCtx.replace(/\s+/g, " ").trim().length >= 2
          ? fromCtx.replace(/\s+/g, " ").trim()
          : `行${baseLi + 1}（金額のみ・近傍に摘要なし）`;
    }
    rest = stripStructuralChunksFromCostImportHint(costImportHintNormalized(rest));
    if (docPayeeOne) {
      const wo = stripDocumentPayeeFromCostImportHint(rest, docPayeeOne);
      if (String(wo || "").trim().length >= 1) rest = wo;
    }
    const restNorm = costImportHintNormalized(rest);
    if (isTaxIncludedTotalHint(restNorm) && !extractInvoiceTableLeadingDescription(lineForAmt)) continue;
    if (isPdfInvoiceFooterOrBankLikeChunk(restNorm)) {
      const salv = extractInvoiceTableLeadingDescription(lineForAmt);
      if (salv.length >= 6) rest = salv;
      else continue;
    }
    if (/^行\d+（金額のみ・近傍に摘要なし）$/.test(restNorm)) continue;
    rest = stripInvoiceTableMergedNoise(costImportHintNormalized(rest));
    rest = stripBankTransferNoiseSegmentsFromCostImportHint(rest);
    if (!String(rest || "").trim()) continue;
    if (isPdfInvoiceTotalsOrDemandCaptionChunk(costImportHintNormalized(rest))) continue;
    let tMode = inferPdfLineTaxMode(lineForAmt);
    if (usedTableTail) tMode = "exclusive";
    else if (pdfLineSegmentBeforeTaxInclusiveAmountLabels(lineForAmt).length < String(lineForAmt).trim().length) {
      tMode = "exclusive";
    }
    const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(bestAmt, tMode);
    hints.push({
      clientHint: rest.slice(0, 500),
      payeeFromDocument: docPayeeOne,
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: noteLine
    });
  }
  return expandMergedInvoiceTablePdfHints(expandMergedManmonthPdfHints(hints));
}

/**
 * pdf.js の getTextContent は並び順が崩れやすい（特に請求書系PDF）。
 * 座標で行クラスタしてから読み順に並べる。
 */
function pdfTextRunsFromPageContent(tc) {
  const runs = [];
  for (const it of tc.items) {
    if (!("str" in it)) continue;
    const s = String(it.str ?? "").replace(/\u00a0/g, " ");
    if (!s.trim()) continue;
    const tr = it.transform;
    if (!Array.isArray(tr) || tr.length < 6) continue;
    const x = Number(tr[4]);
    const y = Number(tr[5]);
    const fontH = Math.sqrt(tr[2] * tr[2] + tr[3] * tr[3]) || Math.abs(Number(tr[3])) || 12;
    const w = typeof it.width === "number" && Number.isFinite(it.width) ? it.width : 0;
    runs.push({ str: s, x, y, fontH: fontH || 12, width: w });
  }
  if (!runs.length) return [];
  const medianH = [...runs.map((r) => r.fontH)].sort((a, b) => a - b)[Math.floor(runs.length / 2)] || 12;
  const lineTol = Math.max(2.25, medianH * 0.35);
  runs.sort((a, b) => b.y - a.y || a.x - b.x);
  /** 既存ラインとの y が近ければその行へ */
  const lines = [];
  const findLineIdx = (y) =>
    lines.findIndex((ln) => Math.abs(ln.yAvg - y) <= lineTol);
  for (const r of runs) {
    let idx = findLineIdx(r.y);
    if (idx < 0) {
      lines.push({ yAvg: r.y, items: [r] });
    } else {
      const ln = lines[idx];
      ln.items.push(r);
      const n = ln.items.length;
      ln.yAvg = ln.items.reduce((s, x) => s + x.y, 0) / n;
    }
  }
  lines.sort((a, b) => b.yAvg - a.yAvg);
  return lines.map((ln) => {
    ln.items.sort((a, b) => a.x - b.x);
    let merged = "";
    for (let i = 0; i < ln.items.length; i += 1) {
      const cur = ln.items[i];
      if (i === 0) {
        merged += cur.str;
        continue;
      }
      const prev = ln.items[i - 1];
      const gap = cur.x - (prev.x + (prev.width > 0 ? prev.width : prev.fontH * String(prev.str).length * 0.55));
      // \u6570\u5b57\u672b\u5c3e\u2192\u6570\u5b57\u59cb\u307e\u308a\u306f\u9699\u9593\u304c\u30bc\u30ed\u3067\u3082\u5fc5\u305a\u30b9\u30da\u30fc\u30b9\u3092\u633f\u5165\uff08"199,000""64,000"\u306e\u9023\u7d50\u3092\u9632\u3050\uff09
      const prevEndsDigit = /\d$/.test(merged.trimEnd());
      const curStartsDigit = /^\d/.test(cur.str);
      const needSpace =
        (prevEndsDigit && curStartsDigit && gap > -2) ||
        (gap > prev.fontH * 0.35 &&
          /[\w\u3040-\u30ff\u4e00-\u9faf]$/.test(merged.trimEnd()) &&
          /^[\w\u3040-\u30ff\u4e00-\u9faf]/.test(cur.str));
      merged += `${needSpace ? " " : ""}${cur.str}`;
    }
    return merged.trim();
  });
}

async function extractPdfPlainTextPages(arrayBuffer) {
  try {
    await ensurePdfJsLoaded();
  } catch (e) {
    const msg = e && typeof e.message === "string" ? e.message : "PDF処理ライブラリを読み込めませんでした";
    throw new Error(msg);
  }
  const lib = getPdfJsLib();
  if (!lib) {
    throw new Error("PDF処理ライブラリを読み込めませんでした。ページを再読込してください");
  }
  configurePdfJsWorker(lib);
  /** PDF.js は内部で ArrayBuffer をワーカーへ転送し detach することがあるため、必ずコピーを渡す */
  const pdfData =
    arrayBuffer instanceof ArrayBuffer
      ? arrayBuffer.slice(0)
      : arrayBuffer instanceof Uint8Array
        ? new Uint8Array(arrayBuffer)
        : arrayBuffer;
  let pdf;
  try {
    pdf = await lib.getDocument({ data: pdfData }).promise;
  } catch (e) {
    const msg = String(e?.message ?? e ?? "");
    if (/password/i.test(msg) || /encrypt/i.test(msg)) {
      throw new Error("PDFがパスワード保護されているか開けません。保護解除後のファイルを試してください");
    }
    throw new Error(msg ? `PDFを開けませんでした（${msg.slice(0, 120)}）` : "PDFを開けませんでした");
  }
  let out = "";
  for (let p = 1; p <= pdf.numPages; p += 1) {
    const page = await pdf.getPage(p);
    const tc = await page.getTextContent({ disableCombineTextItems: false });
    const lines = pdfTextRunsFromPageContent(tc);
    out += lines.length ? `${lines.join("\n")}\n\n` : "\n";
  }
  const trimmed = out.trim();
  if (!trimmed) {
    throw new Error(
      "PDFから文字を読み取れませんでした。画像のみのPDF（スキャン）では自動読取できません。「その他CSV」で数値ファイルを試すか、文字が選択できるPDFかご確認ください"
    );
  }
  return out;
}

/**
 * pdfTextRunsFromPageContent の列座標保持版。
 * 各行をアイテムの x 座標ギャップで列分割し、{ line, cols } を返す。
 */
function pdfStructuredRunsFromPageContent(tc) {
  const runs = [];
  for (const it of tc.items) {
    if (!("str" in it)) continue;
    const s = String(it.str ?? "").replace(/ /g, " ");
    if (!s.trim()) continue;
    const tr = it.transform;
    if (!Array.isArray(tr) || tr.length < 6) continue;
    const x = Number(tr[4]);
    const y = Number(tr[5]);
    const fontH = Math.sqrt(tr[2] * tr[2] + tr[3] * tr[3]) || Math.abs(Number(tr[3])) || 12;
    const w = typeof it.width === "number" && Number.isFinite(it.width) ? it.width : 0;
    runs.push({ str: s, x, y, fontH: fontH || 12, width: w });
  }
  if (!runs.length) return [];
  const medianH = [...runs.map((r) => r.fontH)].sort((a, b) => a - b)[Math.floor(runs.length / 2)] || 12;
  const lineTol = Math.max(2.25, medianH * 0.35);
  const colGapThreshold = medianH * 2.5;
  runs.sort((a, b) => b.y - a.y || a.x - b.x);
  const lines = [];
  const findLineIdx = (y) => lines.findIndex((ln) => Math.abs(ln.yAvg - y) <= lineTol);
  for (const r of runs) {
    let idx = findLineIdx(r.y);
    if (idx < 0) {
      lines.push({ yAvg: r.y, items: [r] });
    } else {
      const ln = lines[idx];
      ln.items.push(r);
      const n = ln.items.length;
      ln.yAvg = ln.items.reduce((s, xi) => s + xi.y, 0) / n;
    }
  }
  lines.sort((a, b) => b.yAvg - a.yAvg);
  return lines.map((ln) => {
    ln.items.sort((a, b) => a.x - b.x);
    const cols = [];
    let lineStr = "";
    for (let i = 0; i < ln.items.length; i++) {
      const cur = ln.items[i];
      const curXEnd = cur.x + (cur.width > 0 ? cur.width : cur.fontH * String(cur.str).length * 0.55);
      if (i === 0) {
        cols.push({ text: cur.str, x: cur.x, xEnd: curXEnd });
        lineStr += cur.str;
      } else {
        const prev = ln.items[i - 1];
        const prevXEnd = prev.x + (prev.width > 0 ? prev.width : prev.fontH * String(prev.str).length * 0.55);
        const gap = cur.x - prevXEnd;
        if (gap > colGapThreshold) {
          cols.push({ text: cur.str, x: cur.x, xEnd: curXEnd });
        } else {
          const last = cols[cols.length - 1];
          last.text += cur.str;
          last.xEnd = Math.max(last.xEnd, curXEnd);
        }
        const needSpace =
          gap > prev.fontH * 0.35 &&
          /[\w぀-ヿ一-龯]$/.test(lineStr.trimEnd()) &&
          /^[\w぀-ヿ一-龯]/.test(cur.str);
        lineStr += `${needSpace ? " " : ""}${cur.str}`;
      }
    }
    return { line: lineStr.trim(), cols };
  });
}

/** PDF全ページを列座標付き構造データとして抽出。失敗時は空配列を返す（呼び出し元で fallback）。 */
async function extractPdfStructuredPages(arrayBuffer) {
  try {
    await ensurePdfJsLoaded();
    const lib = getPdfJsLib();
    if (!lib) return [];
    configurePdfJsWorker(lib);
    const pdfData =
      arrayBuffer instanceof ArrayBuffer
        ? arrayBuffer.slice(0)
        : arrayBuffer instanceof Uint8Array
          ? new Uint8Array(arrayBuffer)
          : arrayBuffer;
    const pdf = await lib.getDocument({ data: pdfData }).promise;
    const pages = [];
    for (let p = 1; p <= pdf.numPages; p++) {
      const page = await pdf.getPage(p);
      const tc = await page.getTextContent({ disableCombineTextItems: false });
      pages.push(pdfStructuredRunsFromPageContent(tc));
    }
    return pages;
  } catch (e) {
    return [];
  }
}

/**
 * 構造化ページデータからテーブルの列ヘッダーを検出する。
 * 「摘要」「税抜金額」等のヘッダーを持つ行を探し、列インデックスのマップを返す。
 */
function detectInvoiceTableColumns(rows) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.cols.length < 2) continue;
    if (!/摘要|品名|品目|項目|内容|サービス/i.test(row.line)) continue;
    if (!/税抜|税別|外税|税込|金額|単価|合計/i.test(row.line)) continue;
    const colMap = { descIdx: -1, taxExIdx: -1, taxInIdx: -1, amtIdx: -1, headerRowIdx: i };
    for (let c = 0; c < row.cols.length; c++) {
      const ct = row.cols[c].text;
      if (/摘要|品名|品目|項目|内容|サービス/i.test(ct) && colMap.descIdx < 0) colMap.descIdx = c;
      else if (/税抜|税別|外税/.test(ct) && colMap.taxExIdx < 0) colMap.taxExIdx = c;
      else if (/税込|税額込/.test(ct) && colMap.taxInIdx < 0) colMap.taxInIdx = c;
      else if (/金額|合計|小計/.test(ct) && colMap.amtIdx < 0) colMap.amtIdx = c;
    }
    if (colMap.descIdx >= 0 && (colMap.taxExIdx >= 0 || colMap.amtIdx >= 0)) {
      return colMap;
    }
  }
  return null;
}

/**
 * 列構造から摘要・税抜金額を直接抽出して hint 配列を返す。
 * 「税抜金額」列が存在する場合はその列を優先し、tax mode を "exclusive" とする。
 */
function parseInvoiceTableFromColumns(pages) {
  const hints = [];
  for (const rows of pages) {
    const colMap = detectInvoiceTableColumns(rows);
    if (!colMap) continue;
    const { descIdx, taxExIdx, amtIdx, headerRowIdx } = colMap;
    const amountColIdx = taxExIdx >= 0 ? taxExIdx : amtIdx;
    if (amountColIdx < 0) continue;
    const docPayeeOne = (() => {
      const allText = rows.map((r) => r.line).join("\n");
      return extractPdfDocumentPayeeNameHint(allText);
    })();
    for (let i = headerRowIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row.cols.length) continue;
      const descText = descIdx >= 0 && descIdx < row.cols.length ? row.cols[descIdx].text.trim() : "";
      const amtText = amountColIdx < row.cols.length ? row.cols[amountColIdx].text.trim() : "";
      if (!amtText) continue;
      const amt = parseProjectCsvAmount(amtText.replace(/[¥￥\s]/g, ""));
      if (!amt || amt < 100) continue;
      if (!descText || descText.length < 2) continue;
      const descNorm = costImportHintNormalized(descText);
      if (isTaxIncludedTotalHint(descNorm)) continue;
      if (isPdfInvoiceFooterOrBankLikeChunk(descNorm)) continue;
      const { amountExTax, consumptionTax, rawImported } = splitImportedYenToExTaxAndTax(Math.round(amt), "exclusive");
      hints.push({
        clientHint: descText.slice(0, 500),
        payeeFromDocument: docPayeeOne || null,
        amountExTax,
        consumptionTax,
        importedRawAmount: rawImported,
        note: ""
      });
    }
  }
  return hints;
}

// 広告媒体名・年月などノイズになるトークンを除外するパターン
const HINT_NOISE_TOKEN_RE = /^(Meta広告|Google広告|YDN|YSS|LINE広告|Yahoo広告|Twitter広告|TikTok広告|Instagram広告|Facebook広告|Meta|Google|LINE|Twitter|TikTok|Instagram|Facebook|広告|媒体費|運用費|管理費|代理店|手数料|\d{4}[\/年]\d{1,2}[月]?|\d{2}[\/]\d{2})$/i;
// 法人格サフィックスを除去して比較用の短縮形を作る
const CORP_PREFIX_RE = /^(株式会社|有限会社|合同会社|一般社団法人|公益財団法人|一般財団法人|社会福祉法人|医療法人|学校法人)\s*/;
const CORP_SUFFIX_RE = /\s*(株式会社|有限会社|合同会社|一般社団法人|公益財団法人|一般財団法人|社会福祉法人|医療法人|学校法人|（株）|\(株\)|㈱)$/;

function stripCorpSuffix(s) {
  return s.replace(CORP_PREFIX_RE, "").replace(CORP_SUFFIX_RE, "").trim();
}

function buildHintTryTexts(raw) {
  const t = String(raw ?? "")
    .replace(/　/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return [];
  const seen = new Set();
  const result = [];
  const push = (s) => {
    const v = s?.trim();
    if (v && v.length >= 2 && !seen.has(v)) { seen.add(v); result.push(v); }
  };
  // 1. 元テキストそのまま
  push(t);
  // 2. googleAds用クリーニング
  push(googleAdsAccountNameToProjectMatchHint(t));
  // 3. 括弧より前
  push(t.replace(/[（(].*$/, "").trim());
  // 4. アンダースコア区切りセグメント
  if (t.includes("_")) {
    for (const seg of t.split("_").map((s) => s.trim())) push(seg);
  }
  // 5. スペース区切りでノイズ除去後の各トークン
  //    例: "ローレルコート本厚木 近鉄不動産 Meta広告 2026/03" → ["ローレルコート本厚木", "近鉄不動産"]
  const spaceTokens = t.split(/[\s　]+/).map((s) => s.trim()).filter((s) => s.length >= 2 && !HINT_NOISE_TOKEN_RE.test(s));
  for (const tok of spaceTokens) push(tok);
  // 6. 法人格除去バリアント（"近鉄不動産株式会社" → "近鉄不動産"）
  const snapshot = [...result];
  for (const tok of snapshot) push(stripCorpSuffix(tok));
  return result;
}

/** ドメイン文字列を正規化（小文字・末尾ドット除去・前後空白除去） */
function normalizeDomainForMatch(s) {
  return String(s ?? "").trim().toLowerCase().replace(/\.+$/, "");
}

/** 2つのドメインがドット境界で一致 or 親子関係にあるか */
function domainHintMatchesCandidate(hintNorm, candNorm) {
  if (!hintNorm || !candNorm) return false;
  if (hintNorm === candNorm) return true;
  if (hintNorm.endsWith("." + candNorm)) return true; // hint は cand のサブドメイン
  if (candNorm.endsWith("." + hintNorm)) return true; // cand は hint のサブドメイン
  return false;
}

/** domainHint（お名前のドメイン列等）に domain/subdomain が一致するドメイン管理行を返す */
function findDomainRowsMatchingHint(domainHint) {
  const hintNorm = normalizeDomainForMatch(domainHint);
  if (!hintNorm || !hintNorm.includes(".")) return [];
  const rows = Array.isArray(typeof domains !== "undefined" ? domains : null) ? domains : [];
  return rows.filter((d) => {
    const cands = [normalizeDomainForMatch(d.domain), normalizeDomainForMatch(d.subdomain)].filter(Boolean);
    return cands.some((c) => domainHintMatchesCandidate(hintNorm, c));
  });
}

/** ドメイン管理行の「クライアント名／案件メモ」表示ラベル（プレビューの紐づけ根拠表示用） */
function domainRegistryRowsToLinkLabel(domainRows) {
  for (const d of Array.isArray(domainRows) ? domainRows : []) {
    const cl = String(d.clientName || "").trim();
    const pn = String(d.projectNote || "").trim();
    if (cl || pn) return [cl, pn].filter(Boolean).join("／");
  }
  return "";
}

/**
 * ドメイン列（お名前等）から案件を推定。グローバル `domains` を domain/subdomain で照合し、
 * ヒットした行の clientName・projectNote を案件名照合に流す（fp_domains に project_id が無いため）。
 */
function findProjectsMatchingDomainRows(domainRows, limit = 6) {
  const seenProj = new Set();
  const out = [];
  for (const d of Array.isArray(domainRows) ? domainRows : []) {
    const hints = [String(d.clientName || "").trim(), String(d.projectNote || "").trim()].filter(Boolean);
    for (const h of hints) {
      for (const p of findProjectsMatchingClientHint(h, limit)) {
        if (seenProj.has(p.id)) continue;
        seenProj.add(p.id);
        out.push(p);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}

function findProjectsMatchingDomainHint(domainHint, limit = 6) {
  return findProjectsMatchingDomainRows(findDomainRowsMatchingHint(domainHint), limit);
}

function findProjectsMatchingClientHint(hint, limit = 6) {
  const tryTexts = buildHintTryTexts(hint);
  if (!tryTexts.length) return [];
  const tFull = tryTexts[0];

  const out = [];
  for (const p of projects) {
    const cnRaw = clientName(p);
    const cn = cnRaw === "未設定クライアント" ? String(p.client || "").trim() : cnRaw;
    const pn = String(p.name || "").trim();
    if (!cn && !pn) continue;
    const cnStripped = stripCorpSuffix(cn);
    const pnStripped = stripCorpSuffix(pn);
    const cnC = cn.replace(/\s+/g, "");
    const cnSC = cnStripped.replace(/\s+/g, "");
    const pnC = pn.replace(/\s+/g, "");
    const pnSC = pnStripped.replace(/\s+/g, "");

    let matched = false;
    for (const tryT of tryTexts) {
      if (!tryT || tryT.length < 2) continue;
      const tryC = tryT.replace(/\s+/g, "");
      const tryS = stripCorpSuffix(tryT);
      const trySC = tryS.replace(/\s+/g, "");

      if (cn) {
        if (costHintIncludes(cn, tryT) || tryT.includes(cn)) { matched = true; break; }
        if (tryC.length >= 3 && cnC && costHintIncludes(cnC, tryC)) { matched = true; break; }
        if (cnStripped && tryS.length >= 2 && (costHintIncludes(cnStripped, tryS) || tryS.includes(cnStripped))) { matched = true; break; }
        if (cnSC && trySC.length >= 3 && costHintIncludes(cnSC, trySC)) { matched = true; break; }
      }
      if (pn) {
        if (costHintIncludes(pn, tryT) || (tryC.length >= 4 && pnC && pnC.includes(tryC))) { matched = true; break; }
        if (pnStripped && tryS.length >= 3 && (costHintIncludes(pnStripped, tryS) || (trySC.length >= 4 && pnSC && pnSC.includes(trySC)))) { matched = true; break; }
      }
    }
    if (!matched && tFull.length >= 3) {
      const tFullC = tFull.replace(/\s+/g, "");
      if (cn && (costHintIncludes(cn, tFull) || tFull.includes(cn))) matched = true;
      else if (cnC && costHintIncludes(cnC, tFullC)) matched = true;
      else if (pn && (costHintIncludes(pn, tFull) || (tFullC.length >= 4 && pnC && pnC.includes(tFullC)))) matched = true;
    }
    if (matched) out.push(p);
  }
  const seen = new Set();
  const uniq = [];
  for (const p of out) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    uniq.push(p);
    if (uniq.length >= limit) break;
  }
  return uniq;
}

function partnerMasterDisplayLabel(pt) {
  const n = String(pt?.name ?? "").trim();
  const b = String(pt?.branch ?? "").trim();
  if (!n && !b) return "";
  return b ? `${n}（${b}）` : n;
}

/** 取込テキストからパートナーマスタをざっくり照合（原価プレビュー表示用） */
function findPartnersMatchingHint(hint, limit = 8) {
  const t = String(hint ?? "").trim();
  if (!t) return [];
  const tNorm = t.replace(/\u3000/g, " ").replace(/\s+/g, "");
  const rows = uiState.partnersCache || [];
  const out = [];
  for (const pt of rows) {
    const name = String(pt?.name ?? "").trim();
    const branch = String(pt?.branch ?? "").trim();
    const combo = `${name}${branch}`.trim();
    if (!combo) continue;
    if (name && (t.includes(name) || name.includes(t))) {
      out.push(pt);
      continue;
    }
    if (branch && (t.includes(branch) || branch.includes(t))) {
      out.push(pt);
      continue;
    }
    const nameCompact = name.replace(/\s+/g, "");
    if (tNorm.length >= 2 && nameCompact && (nameCompact.includes(tNorm) || tNorm.includes(nameCompact))) {
      out.push(pt);
    }
  }
  const seen = new Set();
  const uniq = [];
  for (const pt of out) {
    const id = pt?.id;
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    uniq.push(pt);
    if (uniq.length >= limit) break;
  }
  return uniq;
}

/** AI(PDF)抽出の items を、取込プレビューの行（parsed row）形状へ変換 */
function aiItemsToCostImportParsedRows(items, payee, noteLabel = "AI読取（PDF）", fx = null, subject = "") {
  const arr = Array.isArray(items) ? items : [];
  const payeeStr = String(payee || "").trim(), subjectHint = String(subject || "").trim(); // 件名は案件の推定に使う
  const rows = [];
  for (const it of arr) {
    // 外貨建て請求書はサーバが円換算済み（amount）。換算元とレートを備考の既定値に残す
    const fxNote = fx ? costImportFxNoteText(it?.foreignAmount, fx) : "";
    const summary = String(it?.summary || "").trim();
    const amt = Math.round(Number(it?.amount) || 0);
    if (!summary && !amt) continue;
    let amountExTax;
    let consumptionTax;
    let rawImported = amt;
    let noAutoConsumptionTax = false;
    if (fx && it?.taxMode !== "inclusive" && it?.taxMode !== "exclusive") {
      // 外貨建てで税区分が読めないときは消費税なし（海外の請求には日本の消費税が付かないことが多い）。
      // 後段（プレビュー生成の安全網・金額編集・税込/税抜切替）で10%を補わないよう印を付ける
      amountExTax = amt;
      consumptionTax = 0;
      noAutoConsumptionTax = true;
    } else {
      const mode = it?.taxMode === "inclusive" ? "inclusive" : "exclusive";
      // 値引き・インセンティブ等の負数も同じ税区分で消費税を分ける
      const split = splitSignedImportedYen(amt, mode);
      amountExTax = split.amountExTax;
      consumptionTax = split.consumptionTax;
      rawImported = split.rawImported;
    }
    rows.push({
      clientHint: summary || payeeStr || "AI読取",
      payeeFromDocument: payeeStr,
      amountExTax,
      consumptionTax,
      importedRawAmount: rawImported,
      note: noteLabel,
      ...(fxNote ? { fxNote } : {}),
      ...(noAutoConsumptionTax ? { noAutoConsumptionTax } : {}), ...(subjectHint ? { subjectHint } : {}),
      skipStructuralStrip: true
    });
  }
  return rows;
}

/** 取込プレビューで税抜を打ち換えたときの消費税。通常は10%。消費税なしの外貨行は請求書の税行の比率（無ければ0） */
function costImportRecomputedTaxYen(row, ex) {
  if (!(ex > 0)) return 0;
  if (row?.noAutoConsumptionTax) return Math.round(ex * (Number(row.fxTaxRatio) || 0));
  return Math.round(ex * 0.1);
}

/** 外貨→円換算の記録（例: USD 210.00 を円換算（1USD=157.35円・2026-10-01・請求書記載レート）） */
function costImportFxNoteText(foreignAmount, fx) {
  const cur = String(fx?.currency || "").trim();
  const rate = Number(fx?.rate);
  if (!cur || !(rate > 0)) return "";
  const amt = Number(foreignAmount);
  const amtStr = Number.isFinite(amt) ? amt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
  const src = fx.source === "invoice" ? "請求書記載レート" : "請求日の参考レート(ECB)";
  const dateStr = String(fx.rateDate || "").trim();
  return `${cur} ${amtStr} を円換算（1${cur}=${rate}円・${dateStr ? `${dateStr}・` : ""}${src}）`;
}

/** ドル等の外貨建てらしいテキストか（従来パーサは通貨を判別できず外貨額を円として読むため、取込を止める判定に使う） */
function costImportTextLooksForeignCurrency(text) {
  const t = String(text || "");
  // 円の請求書に参考のドル表記（$5 等）が混ざるだけなら円建てとして従来パーサに任せる
  return /\bUSD\b|\$\s?\d/.test(t) && !/[¥￥円]/.test(t);
}

/** 登録時に確定した「アカウント名→案件」をサーバ保存＋ローカルキャッシュ即時更新 */
function persistAndCacheCostAccountMappings(list) {
  const arr = Array.isArray(list) ? list : [];
  if (!arr.length) return;
  // 同キーは最後の値を採用
  const uniq = new Map();
  for (const m of arr) {
    if (!m || !m.accountNameKey || !Number.isFinite(Number(m.projectId))) continue;
    uniq.set(`${m.sourceKey}|${m.accountNameKey}`, {
      sourceKey: String(m.sourceKey || "generic"),
      accountNameKey: String(m.accountNameKey),
      projectId: Number(m.projectId)
    });
  }
  if (!uniq.size) return;
  const cache = uiState.costAccountProjectMap || (uiState.costAccountProjectMap = []);
  for (const m of uniq.values()) {
    const i = cache.findIndex((x) => x.sourceKey === m.sourceKey && x.accountNameKey === m.accountNameKey);
    if (i >= 0) cache[i].projectId = m.projectId;
    else cache.push({ ...m });
  }
  if (typeof authToken === "undefined" || !authToken) return;
  void (async () => {
    for (const m of uniq.values()) {
      try {
        const res = await fetch("/api/cost-account-mappings", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(m)
        });
        if (!res.ok) console.warn("persistAndCacheCostAccountMappings failed: HTTP", res.status);
      } catch {
        /* 記憶の保存失敗は致命的でない（次回手当てに戻るだけ） */
      }
    }
  })();
}

/** 媒体原価の記憶（アカウント名→案件）から案件UI-IDを引く。無ければ空文字 */
function costAccountMappedProjectUiId(sourceKey, accountNameKey) {
  const acct = String(accountNameKey || "").trim();
  if (!acct) return "";
  const sk = String(sourceKey || "").trim();
  const arr = uiState.costAccountProjectMap || [];
  const hit = arr.find((m) => String(m.accountNameKey || "") === acct && (!sk || String(m.sourceKey || "") === sk));
  if (!hit || hit.projectId == null) return "";
  const p = projects.find((pp) => Number(pp.dbId) === Number(hit.projectId));
  return p ? p.id : "";
}

/** 原価の重複判定キー（対象月｜支払先｜税抜金額）。金額0は判定しない（""） */
function costDupKey(targetMonth, vendor, amount) {
  const amt = Math.round(Number(amount || 0)) || 0;
  if (!amt) return "";
  return `${String(targetMonth || "").trim().replace(/-/g, "/")}|${compactVendorMatchKey(vendor || "")}|${amt}`;
}

/** 取込プレビューの重複候補判定用: 既存原価から (対象月｜支払先｜税抜金額) の索引を作る */
function buildExistingCostDupIndex() {
  const idx = new Map();
  for (const c of flattenAllCostsAcrossProjectsAndOrphans()) {
    const key = costDupKey(c.targetMonth, c.vendor, c.amount);
    if (!key) continue;
    if (!idx.has(key)) idx.set(key, []);
    idx.get(key).push(compactVendorMatchKey(c.note || ""));
  }
  return idx;
}

/** 手入力の新規登録（単発・一括）の二重計上チェック: 対象月・支払先・税抜金額が同じ既存原価（案件は問わない）。
 *  画面に読み込んである原価が対象（取込プレビューの重複候補と同じ条件） */
function findExistingDuplicateCosts(targetMonth, vendor, amount) {
  const key = costDupKey(targetMonth, vendor, amount);
  if (!key) return [];
  return flattenAllCostsAcrossProjectsAndOrphans().filter((c) => costDupKey(c.targetMonth, c.vendor, c.amount) === key);
}

/** 重複候補があれば、どの案件に誰が登録したかを見せて確認する。続けてよければ true */
function confirmExistingCostDuplicates(dups) {
  if (!dups.length) return true;
  const lines = dups.slice(0, 5).map(
    (c) => `・${c.targetMonth} ${c.vendor} ${yen.format(Number(c.amount) || 0)}（${c._projectName || "—"}／登録者: ${costCreatorLabel(c.createdByUserDbId)}）`
  );
  if (dups.length > 5) lines.push(`ほか${dups.length - 5}件`);
  return window.confirm(
    `計上月・発注先・金額（税抜）が同じ原価がすでに登録されています。\n${lines.join("\n")}\n\n二重計上でないことを確認してください。それでも登録しますか？`
  );
}

/** レビュー行が既存原価の重複候補か（対象月・支払先・税抜金額が一致。ドメイン/摘要も既存noteに一致すれば確度が高い） */
function reviewRowIsDuplicateOfExisting(dupIndex, { targetMonth, vendor, amountExTax, domainHint, summary }) {
  const key = costDupKey(targetMonth, vendor, amountExTax);
  if (!key) return false;
  const candNotes = dupIndex.get(key);
  if (!candNotes || !candNotes.length) return false;
  const sig = compactVendorMatchKey(domainHint || summary || "");
  if (!sig || sig.length < 3) return true; // 月・支払先・金額の一致のみでも候補
  return candNotes.some((n) => n && (n.includes(sig) || sig.includes(n)));
}

/**
 * 取込プレビュー行を、選んだ案件の「仮原価」と突合する（案件＋支払先＋金額）。
 * projectIdPick が変わるたびに再計算するため、描画時にライブで呼ぶ。
 * 返り値: { status: "match"|"mismatch"|"none", provCostId, provAmount }
 */
function costImportRowProvisionalMatch(r) {
  const pid = String(r?.projectIdPick ?? "").trim();
  if (!pid || isNoProjectBucketUiId(pid)) return { status: "none" };
  const p = projects.find((x) => x.id === pid);
  if (!p) return { status: "none" };
  const vendorKey = compactVendorMatchKey(r.vendorResolvedLabel || "");
  if (!vendorKey) return { status: "none" };
  const amt = Math.round(Number(r.amountExTax ?? 0)) || 0;
  const cands = (p.costs || []).filter(
    (c) => c.isProvisional && compactVendorMatchKey(c.vendor || "") === vendorKey
  );
  if (!cands.length) return { status: "none" };
  // 発注申請中の原価がある＝承認前に発注して請求書が来た。仮を消して新規登録すると申請が消え、承認後に二重計上になるので、
  // 既定は登録せず原価照合でその原価と突き合わせる（15z-cost-order-applications.js・承認前発注）
  const pendingOrder = cands.find((c) => c.orderApprovalStatus === "申請中");
  if (pendingOrder) return { status: "order-pending", provCostId: pendingOrder.id, provAmount: Math.round(Number(pendingOrder.amount || 0)) || 0 };
  const exact = cands.find((c) => (Math.round(Number(c.amount || 0)) || 0) === amt);
  if (exact) return { status: "match", provCostId: exact.id, provAmount: Math.round(Number(exact.amount || 0)) || 0 };
  const c0 = cands[0];
  return { status: "mismatch", provCostId: c0.id, provAmount: Math.round(Number(c0.amount || 0)) || 0 };
}

/** 取込プレビュー: 仮突合の表示＋行アクション（合致=置換チェック／金額相違=処理選択）。 */
function costImportRowProvisionalMatchHtml(r, i) {
  const m = costImportRowProvisionalMatch(r);
  if (m.status === "match") {
    const checked = r.provReplace !== false; // 既定ON
    return `<div class="cost-import-prov cost-import-prov--match">
      <span class="cost-import-prov-dot cost-import-prov-dot--ok"></span>合致 <span class="cost-import-prov-sub">仮 ${yen.format(m.provAmount)} と一致</span>
      <label class="cost-import-prov-check"><input type="checkbox" data-cost-review-prov-replace="${i}" ${checked ? "checked" : ""} />仮を置換</label>
    </div>`;
  }
  if (m.status === "order-pending") {
    const act = String(r.provMismatchAction || "skip");
    const opt = (v, label) => `<option value="${v}" ${act === v ? "selected" : ""}>${label}</option>`;
    return `<div class="cost-import-prov cost-import-prov--mismatch">
      <span class="cost-import-prov-dot cost-import-prov-dot--warn"></span>発注申請中 <span class="cost-import-prov-sub">仮 ${yen.format(m.provAmount)}（承認前に発注）</span>
      <select class="cost-import-prov-select" data-cost-review-prov-action="${i}" aria-label="発注申請中の原価がある行の処理">
        ${opt("skip", "登録しない（原価照合で突き合わせる）")}${opt("register", "それでも新規登録（二重計上に注意）")}
      </select>
    </div>`;
  }
  if (m.status === "mismatch") {
    const act = String(r.provMismatchAction || "register");
    const opt = (v, label) => `<option value="${v}" ${act === v ? "selected" : ""}>${label}</option>`;
    return `<div class="cost-import-prov cost-import-prov--mismatch">
      <span class="cost-import-prov-dot cost-import-prov-dot--warn"></span>金額相違 <span class="cost-import-prov-sub">仮 ${yen.format(m.provAmount)}</span>
      <select class="cost-import-prov-select" data-cost-review-prov-action="${i}" aria-label="金額相違の処理">
        ${opt("register", "そのまま登録（仮は残す）")}${opt("replace", "この金額で確定（仮を置換）")}${opt("skip", "スキップ")}
      </select>
    </div>`;
  }
  return "";
}

/** 取込プレビュー: 指定行の仮突合インジケータを DOM 直接更新（pick/金額変更時・再描画せずに反映） */
function refreshCostImportProvMatchDom(idx) {
  const sel = document.querySelector(`[data-cost-review-pick="${idx}"]`);
  const wrap = sel && sel.closest(".cost-import-proj-cell-wrap");
  if (!wrap) return;
  const row = uiState.costRegWizard && uiState.costRegWizard.rows && uiState.costRegWizard.rows[idx];
  const existing = wrap.querySelector(".cost-import-prov");
  if (existing) existing.remove();
  const html = row ? costImportRowProvisionalMatchHtml(row, idx) : "";
  if (html) wrap.insertAdjacentHTML("beforeend", html);
}

/** レビューモーダル用の行データ */
function buildCostImportReviewRows(parsedRows, opts = {}) {
  const docFallback = String(opts.documentPayeeHint ?? "").trim();
  // 広告CSVは支払先を媒体固定値に設定（アカウント名等を読まない）
  const fixedVendor = String(opts.fixedVendor || "").trim();
  // 広告プラットフォームCSVは「制作」案件を予測から外し、広告・媒体関連案件を優先
  const isAdPlatformCsv = !!fixedVendor && fixedVendor !== "広告";
  const AD_TERMS = /広告|媒体|デジタル|リスティング|SNS|ディスプレイ|プロモーション/;
  const SEISAKU_TERMS = /制作/;
  // 同一アカウント名の件数を事前カウント（Google広告一括反映表示用）
  const accountNameCountMap = {};
  for (const r of parsedRows) {
    const k = String(r.accountNameKey || "");
    if (k) accountNameCountMap[k] = (accountNameCountMap[k] || 0) + 1;
  }
  const dupIndex = buildExistingCostDupIndex();
  return parsedRows.map((r, i) => {
    const rawHint = stripInvoiceTableMergedNoise(r.clientHint || "");
    const rawForCap = rawHint;
    const importCaptionKind = r.skipStructuralStrip ? "normal" : costImportPreviewCaptionKind(rawForCap);
    let nameColumnText;
    let summaryCaptionRead;
    let projectHintForMatch;
    if (importCaptionKind === "normal") {
      // CSV行（skipStructuralStrip=true）は ・ で分割しない — 地名や物件名に ・ が含まれるため
      const sp = r.skipStructuralStrip
        ? { name: rawForCap.slice(0, 240), summary: rawForCap.slice(0, 500), projectHint: rawForCap }
        : costImportSplitNormalNameAndSummary(rawForCap, docFallback);
      const fullCaptionForSummary = r.skipStructuralStrip ? rawForCap : costImportStripLeadingFieldLabels(
        stripDocumentPayeeFromCostImportHint(
          stripStructuralChunksFromCostImportHint(rawForCap),
          docFallback
        )
      ).trim();
      nameColumnText = (sp.name || "—").trim().slice(0, 520);
      summaryCaptionRead = stripInvoiceTableMergedNoise(
        (sp.summary || fullCaptionForSummary || rawForCap)
          .replace(/\s+/g, " ")
          .trim()
      ).slice(0, 520);
      projectHintForMatch = sp.projectHint || rawForCap;
    } else {
      nameColumnText = stripDocumentPayeeFromCostImportHint(
        stripStructuralChunksFromCostImportHint(rawHint),
        docFallback
      )
        .trim()
        .slice(0, 500);
      if (!nameColumnText) nameColumnText = "—";
      summaryCaptionRead = stripInvoiceTableMergedNoise(
        rawForCap.replace(/\s+/g, " ").trim()
      ).slice(0, 280);
      projectHintForMatch = rawForCap;
    }
    const matchSrc =
      nameColumnText === "—"
        ? projectHintForMatch || ""
        : `${nameColumnText} ${summaryCaptionRead || ""}`.trim();
    const projectMatchSeed = String(r.projectMatchHint || "").trim();
    // ドメイン列があれば登録ドメイン経由の案件照合を最優先（お名前等）。空なら従来の名前照合。
    const domainRows = r.domainHint ? findDomainRowsMatchingHint(r.domainHint) : [];
    // 原価箱・販管費に入る行は計上先の名前から顧客が判らなくなるため、
    // ドメイン管理の「クライアント名／案件メモ」を備考の既定値にする（プレビューで編集可）。
    // 下の domainRegistryLabel は お名前 経路で計上先の説明に差し替わるので別に持つ。
    const domainRegistryNote = domainRegistryRowsToLinkLabel(domainRows);
    let domainRegistryLabel = domainRegistryNote;
    let matches;
    if (opts.sourceKey === "onamae") {
      // お名前ドットコムは案件名との名前照合がほぼ当たらないため使わない。
      // 計上先は呼び出し側の resolveOnamaeDomainCostAssignments が解決済み
      // （社内処理の内部ドメイン＝販管費／それ以外は担当営業の原価箱）。
      // 決まらなかった行は空欄のままにする（誤った案件へ付けない）。
      const assignUiId = opts.domainAssignByHint instanceof Map
        ? opts.domainAssignByHint.get(String(r.domainHint || "").trim())
        : "";
      const assignProject =
        assignUiId === COST_SALES_EXPENSE_PROJECT_UI_ID
          ? salesExpenseCostProjectProxy()
          : assignUiId
            ? projects.find((p) => p.id === assignUiId)
            : null;
      matches = assignProject ? [assignProject] : [];
      if (assignUiId === COST_SALES_EXPENSE_PROJECT_UI_ID) {
        domainRegistryLabel = `${COST_INTERNAL_DOMAIN_CLIENT_NAME}のため${COST_SALES_EXPENSE_PROJECT_LABEL}へ自動計上`;
      } else if (assignProject) {
        domainRegistryLabel = `${assignProject.owner || "担当営業"}の原価箱へ自動計上`;
      } else if (domainRows.length) {
        domainRegistryLabel = "ドメイン管理はヒットしましたが担当営業の原価箱を特定できませんでした（案件を手動選択してください）";
      }
    } else {
      const domainMatches = domainRows.length ? findProjectsMatchingDomainRows(domainRows) : [];
      const subjectMatches = !domainMatches.length && r.subjectHint ? findProjectsMatchingClientHint(r.subjectHint) : []; // 件名（AI読取）で当たれば明細の品目より優先
      matches = domainMatches.length ? domainMatches : subjectMatches.length ? subjectMatches
        : findProjectsMatchingClientHint(
            projectMatchSeed || (matchSrc.trim() ? matchSrc : rawForCap)
          );
    }
    let sortedMatches = matches;
    if (isAdPlatformCsv && matches.length > 1) {
      const adProjects = matches.filter(p => AD_TERMS.test(String(p.name || "")));
      const neutralProjects = matches.filter(p => !AD_TERMS.test(String(p.name || "")) && !SEISAKU_TERMS.test(String(p.name || "")));
      const seisakuProjects = matches.filter(p => SEISAKU_TERMS.test(String(p.name || "")));
      sortedMatches = [...adProjects, ...neutralProjects, ...seisakuProjects];
    }
    const pick = sortedMatches[0] || null;
    // 記憶された紐づけ（アカウント名→案件）があれば最優先で採用
    const mappedUiId = r.accountNameKey
      ? costAccountMappedProjectUiId(opts.sourceKey || "", String(r.accountNameKey))
      : "";
    let suggestedProjectExtras = "";
    if (pick && sortedMatches.length > 1) {
      suggestedProjectExtras = sortedMatches
        .slice(1, 4)
        .map((p2) => `${clientName(p2)}／${String(p2.name || "").trim() || "—"}`)
        .join(" · ");
      if (sortedMatches.length > 4) suggestedProjectExtras += " …";
    }
    const ex = Math.round(Number(r.amountExTax ?? r.amount ?? 0)) || 0;
    let tax = Math.round(Number(r.consumptionTax ?? 0)) || 0;
    // 安全網: 課税行で消費税が0のまま残っていれば税抜とみなし標準10%を補完する。
    // 集計・粗利は税込（amount + consumptionTax）で行うため、分割経路を通らなかった
    // 行（PDF表の展開行など）で税が欠落すると税込合計が1割過少になるのを防ぐ。
    if (importCaptionKind !== "consumption_tax" && ex > 0 && tax === 0 && !r.noAutoConsumptionTax) {
      tax = Math.round(ex * 0.1);
    }
    // 税込/税抜トグルで元の取込値を再解釈できるよう保持（無ければ税抜の ex を元値とみなす）
    let importedRawAmount = Math.round(Number(r.importedRawAmount ?? 0)) || 0;
    if (importedRawAmount === 0) importedRawAmount = ex;
    let vendorResolvedLabel, vendorMasterUnmatched;
    if (fixedVendor) {
      vendorResolvedLabel = fixedVendor;
      vendorMasterUnmatched = false;
    } else {
      const vendorHint = pickCostImportVendorResolutionHint(r, docFallback);
      const payee = resolveCostVendorFromPartnersAndClients(vendorHint);
      vendorResolvedLabel = payee.vendorLabel;
      vendorMasterUnmatched = payee.unmatched;
    }
    const duplicateOfExisting = reviewRowIsDuplicateOfExisting(dupIndex, {
      targetMonth: opts.targetMonth,
      vendor: vendorResolvedLabel,
      amountExTax: ex,
      domainHint: r.domainHint,
      summary: summaryCaptionRead || nameColumnText
    });
    return {
      key: `ir-${i}-${Date.now()}`,
      clientHint: r.clientHint,
      accountNameKey: String(r.accountNameKey || ""),
      accountNameCount: accountNameCountMap[String(r.accountNameKey || "")] || 0,
      nameColumnText,
      amountExTax: ex,
      consumptionTax: tax,
      importedRawAmount,
      note: r.note || "",
      // ドメイン管理のクライアント名・換算根拠は発注名へ自動で追記（一覧で読める）。備考欄（reviewNote→memo）は人が書くメモだけ
      autoNote: [domainRegistryNote, r.fxNote].filter(Boolean).join(" / "),
      // 消費税なしの外貨行でも、請求書に税の行があって按分された税は、金額を打ち換えたとき同じ比率で付け直す
      ...(r.noAutoConsumptionTax ? { noAutoConsumptionTax: true, fxTaxRatio: ex > 0 && tax > 0 ? tax / ex : 0 } : {}),
      projectIdPick: mappedUiId || (pick ? pick.id : ""),
      vendorResolvedLabel,
      vendorMasterUnmatched,
      importCaptionKind,
      summaryCaptionRead,
      suggestedProjectExtras,
      domainRegistryLabel,
      domainRegistryMiss: !!r.domainHint && !domainRows.length,
      domainHint: String(r.domainHint || ""),
      duplicateOfExisting,
      ...(r.pdfSourceIndex != null ? { pdfSourceIndex: r.pdfSourceIndex } : {}),
      targetMonthOverride: null
    };
  });
}

/** 取込行から DB の note に保存する発注名テキスト（手動行は発注名欄の入力） */
function costImportSummaryTextForPersist(row, targetMonth) {
  if (!row) return "";
  // 手入力行・まとめ行・発注名を書き換えた読み取り行は、編集後の発注名（manualSummaryText）をそのまま使う
  if (row.isManualPreviewRow || row.isMergedPreviewRow || row.manualSummaryText != null) return String(row.manualSummaryText || "").trim();
  const sum = stripInvoiceTableMergedNoise(String(row.summaryCaptionRead || "").trim());
  const name = stripInvoiceTableMergedNoise(String(row.nameColumnText || "").trim());
  const nameUse = name && name !== "—" ? name : "";
  let cap = (sum || nameUse).replace(/\s+/g, " ").trim();
  if (!cap) {
    const hint = stripInvoiceTableMergedNoise(String(row.clientHint || "").trim());
    cap = hint.replace(/\s+/g, " ").trim();
  }
  const monthSuffix = String(targetMonth || "").trim().replace(/-/g, "/");
  if (monthSuffix) cap = `${cap} ${monthSuffix}`.trim();
  return cap.slice(0, 520);
}

/** 案件No の表示用（未設定時は id から数字部分のみ） */
function projectDisplayNoForUi(p) {
  if (!p) return "";
  const raw = String(p.no ?? "").trim();
  if (raw) return raw;
  return String(String(p.id ?? "").replace(/[^\d]/g, "")).trim();
}

/** 原価登録など: 計上先のプルダウン表示（検索は option 文言に番号を含める） */
function projectAccountingPickLabel(p) {
  const no = projectDisplayNoForUi(p);
  const name = String(p?.name ?? "").trim() || "—";
  return `${no || "—"} - ${name}`;
}

/** 検索用に付与する文字列（表示ラベルに無い語もヒットさせる） */
function projectAccountingPickSearchBlob(p) {
  const no = projectDisplayNoForUi(p);
  const name = String(p?.name ?? "").trim();
  const client = String(clientName(p) || "").trim();
  return [no, name, client].filter(Boolean).join(" ");
}

function costImportSelectedProjectLabel(projectIdUi) {
  const id = String(projectIdUi ?? "").trim();
  if (!id || id === COST_UNASSIGNED_PROJECT_UI_ID) return COST_UNASSIGNED_PROJECT_LABEL;
  if (id === COST_SALES_EXPENSE_PROJECT_UI_ID) return COST_SALES_EXPENSE_PROJECT_LABEL;
  if (id === COST_ADJUSTMENT_PROJECT_UI_ID) return COST_ADJUSTMENT_PROJECT_LABEL;
  const p = projects.find((x) => x.id === id);
  if (!p) return "（不明な案件）";
  return projectAccountingPickLabel(p);
}

function mapServerCostToClientShape(row) {
  const taxRaw = row.consumption_tax ?? row.consumptionTax;
  const tax = Number(taxRaw ?? 0);
  const note = row.note || "";
  const noteTrimmed = String(note).trimStart();
  const salesEx =
    noteTrimmed.startsWith(COST_SALES_EXPENSE_NOTE_PREFIX) ||
    noteTrimmed.startsWith(COST_SALES_EXPENSE_NOTE_PREFIX_LEGACY);
  const adjustment = noteTrimmed.startsWith(COST_ADJUSTMENT_NOTE_PREFIX);
  const pdfRaw = row.source_pdf_doc_id ?? row.sourcePdfDocId;
  const pdfN = pdfRaw != null && pdfRaw !== "" ? Number(pdfRaw) : NaN;
  const delRaw = row.delegated_sales_user_id ?? row.delegatedSalesUserId;
  const delN = delRaw != null && delRaw !== "" ? Number(delRaw) : NaN;
  const sugRaw = row.suggested_project_id ?? row.suggestedProjectId;
  const sugN = sugRaw != null && sugRaw !== "" ? Number(sugRaw) : NaN;
  return {
    id: `cost-${row.id}`,
    category: normalizeCostCategoryLabel(row.category),
    vendor: row.vendor || "",
    amount: Number(row.amount || 0),
    consumptionTax: Number.isFinite(tax) ? Math.round(tax) : 0,
    targetMonth: row.target_month || "",
    payStatus: row.pay_status || "未登録",
    dueDate: row.due_date || "",
    paidDate: row.paid_date || "",
    invoiceDate: row.invoice_date || "",
    paymentMethod: row.payment_method || "",
    invoiceNo: row.invoice_no || "",
    // 登録者（DB id）。null＝不明（この列の追加前の原価・システム取込）
    createdByUserDbId: row.created_by_user_id != null && Number.isFinite(Number(row.created_by_user_id)) ? Number(row.created_by_user_id) : null,
    withholdingTax: Math.round(Number(row.withholding_tax || 0)) || 0,
    orderStatus: row.order_status || "",
    note,
    // 備考（発注名＝note とは別の長文メモ。2026-10 追加。一覧の列には出さず 📝 の印だけ）
    memo: String(row.memo || ""),
    ...(row.is_provisional === true ? { isProvisional: true } : {}),
    // 発注申請（制作費・開発費・15z-cost-order-applications.js）。NULL＝申請の対象外（既存の原価・媒体費・経理の登録）
    ...(row.order_approval_status
      ? {
          orderApprovalStatus: String(row.order_approval_status),
          ...(row.order_review_comment ? { orderReviewComment: String(row.order_review_comment) } : {}),
          // 承認額超過・承認なしの判定と理由（15z-cost-order-applications.js costOrderExceptionOf）
          ...(row.order_approved_amount != null ? { orderApprovedAmount: Math.round(Number(row.order_approved_amount)) || 0 } : {}),
          ...(row.order_applied_by != null ? { orderAppliedByDbId: Number(row.order_applied_by) } : {}),
          ...(row.order_exception_reason ? { orderExceptionReason: String(row.order_exception_reason) } : {}),
          ...(row.order_pre_ordered_at ? { orderPreOrderedAt: String(row.order_pre_ordered_at) } : {})
        }
      : {}),
    ...(String(row.dispute_note || "").trim()
      ? {
          disputeNote: String(row.dispute_note).trim(),
          disputedByDbId: row.disputed_by != null ? Number(row.disputed_by) : null,
          disputedAt: row.disputed_at || "",
          disputeProjectDbId: row.dispute_project_id != null ? Number(row.dispute_project_id) : null
        }
      : {}),
    ...(String(row.remand_note || "").trim()
      ? {
          remandNote: String(row.remand_note).trim(),
          remandedByDbId: row.remanded_by != null ? Number(row.remanded_by) : null,
          remandedAt: row.remanded_at || ""
        }
      : {}),
    ...(row.reconciled_at
      ? {
          reconciledAt: row.reconciled_at || "",
          reconciledByDbId: row.reconciled_by != null ? Number(row.reconciled_by) : null
        }
      : {}),
    ...(row.import_source === "rem" || row.importSource === "rem"
      ? {
          importSource: "rem",
          remBukken: String(row.rem_bukken ?? row.remBukken ?? ""),
          remGroup: String(row.rem_group ?? row.remGroup ?? "")
        }
      : {}),
    ...(Number.isFinite(pdfN) && pdfN > 0 ? { sourcePdfDocId: pdfN } : {}),
    ...(Number.isFinite(delN) && delN > 0 ? { delegatedSalesUserDbId: delN } : {}),
    ...(Number.isFinite(sugN) && sugN > 0 ? { suggestedProjectDbId: sugN } : {}),
    ...(salesEx ? { _costSalesExpense: true } : {}),
    ...(adjustment ? { _costAdjustment: true } : {})
  };
}

async function persistCostToServerCreate(project, patch) {
  if (!authToken) {
    // スタブ運用（未ログイン）: サーバに送る形と同じ内容をローカルへ積む
    const { projectId: _pid, ...body } = costCreateRequestBody(project, patch);
    pushCreatedCostToLocalState(project, {
      id: `cost-${Date.now()}`,
      ...body,
      dueDate: body.dueDate || "",
      paidDate: body.paidDate || "",
      invoiceDate: body.invoiceDate || "",
      ...(isSalesExpenseCostBucketRef(project) ? { _costSalesExpense: true } : {}),
      ...(isAdjustmentCostBucketRef(project) ? { _costAdjustment: true } : {})
    });
    pruneStaleOrphanCostDuplicates();
    return true;
  }
  const { ok, data } = await apiFetch("/api/costs", { method: "POST", body: costCreateRequestBody(project, patch) });
  if (!ok) throw new Error(typeof data.error === "string" ? data.error : "原価の作成に失敗しました");
  if (!data.cost) throw new Error("サーバー応答が不正です");
  pushCreatedCostToLocalState(project, mapServerCostToClientShape(data.cost));
  pruneStaleOrphanCostDuplicates();
  return true;
}

/** POST /api/costs（新規登録）のリクエストボディ。単発登録と取込の一括登録で同じ形を送る。
 *  案件つきなのに dbId が無い（一覧未取得）場合は throw（送っても登録先が特定できない） */
function costCreateRequestBody(project, patch) {
  const bucket = isUnassignedCostBucketRef(project);
  if (!bucket && !project?.dbId) {
    throw new Error("案件が未取得です。一覧を再読み込みしてからやり直してください。");
  }
  const salesEx = isSalesExpenseCostBucketRef(project);
  const adjustment = isAdjustmentCostBucketRef(project);
  const noteOut = adjustment
    ? ensureAdjustmentNote(patch.note || "", true)
    : ensureSalesExpenseNote(patch.note || "", salesEx);
  const pdfDocId =
    patch.sourcePdfDocId != null && patch.sourcePdfDocId !== ""
      ? Number(patch.sourcePdfDocId)
      : null;
  return {
    projectId: bucket ? null : project.dbId,
    category: normalizeCostCategoryLabel(patch.category),
    vendor: patch.vendor || "",
    amount: patch.amount,
    consumptionTax: Math.round(Number(patch.consumptionTax ?? 0)) || 0,
    targetMonth: patch.targetMonth,
    payStatus: patch.payStatus,
    dueDate: patch.dueDate || null,
    paidDate: patch.paidDate || null,
    invoiceDate: patch.invoiceDate || null,
    paymentMethod: patch.paymentMethod || "",
    invoiceNo: patch.invoiceNo || "",
    withholdingTax: Math.round(Number(patch.withholdingTax || 0)) || 0,
    orderStatus: patch.orderStatus || "",
    isProvisional: patch.isProvisional === true,
    note: noteOut,
    ...(String(patch.memo || "").trim() ? { memo: String(patch.memo) } : {}),
    ...(Number.isFinite(pdfDocId) && pdfDocId > 0 ? { sourcePdfDocId: pdfDocId } : {}),
    // 発注申請（制作費・開発費・経理以外。サーバ api/_costOrderApproval.js normalizeOrderApplyInput が読むキー）
    ...(patch.orderApply ? costOrderApplyRequestFields(patch.orderApply) : {}),
    // 取込の冪等キー（一括登録だけが付ける。再送しても同じ行は1件しか作られない）
    ...(patch.importRunId && patch.importRowKey ? { importRunId: patch.importRunId, importRowKey: patch.importRowKey } : {})
  };
}

/** サーバで作成された原価をローカル state に載せる（バケットは未割当、案件つきはその案件の costs）。
 *  再送で同じ行が返ってきたときに二重に載せない（id で判定） */
function pushCreatedCostToLocalState(project, mapped) {
  const list = isUnassignedCostBucketRef(project) ? ensureOrphanCosts() : (project.costs || (project.costs = []));
  if (mapped?.id != null && list.some((c) => c?.id === mapped.id)) return;
  list.push(mapped);
}

// ---- 取込の冪等キー（importRunId / importRowKey）----
// 目的: 100行ずつの一括登録で応答を受け取れなかったとき、同じキーで再送しても二重登録にならないようにする
// （サーバ fp_costs の部分UNIQUE索引 uq_fp_costs_import_run_row と対）。
// importRunId はウィザード1回につき1つ（最初の登録時に作り、結果不明の行を再登録しても変えない）。
// importRowKey はプレビュー行の識別子 row.key（作成時刻入り・行ごとに一意）。内容を編集しても行を足しても
// 他の行のキーは変わらないので、「一部を直して再登録」でも登録済みの行は二重にならない。
// 登録開始〜完了の間はプレビュー（行・runId・保存済みPDFのID）を localStorage に控え、タブを閉じたり
// リロードしても同じ runId・行キーで続きから再登録できるようにする。控えは原価登録/原価割当ページのバナー
// （復元して続ける／破棄）と「＋ 原価を登録」を押したときの確認で使う。完了・閉じるで消す。
// PDF の base64 は控えない（保存済みIDがあれば再アップロードしない）。別ユーザーの控えは読まない。
const COST_IMPORT_RUN_STORAGE_KEY = "fp:costImportRun";
/** 控えの有効期限。古い控えは復元を提案しない（別日の取込に紛れ込ませない） */
const COST_IMPORT_RUN_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
/** 控えるウィザード項目（描画・登録が読むものだけ。一時値や大きな値を将来足しても勝手に控えない） */
const COST_IMPORT_RUN_SNAPSHOT_KEYS = [
  "mode", "salesMode", "targetMonth", "csvSourceKey", "costImportChoice", "previewKind", "importCategory",
  "importTaxMode", "documentPayeeUiKey", "documentPayeeRaw", "defaultVendor", "importPdfFileName",
  "uploadedPdfDocId", "sourceDocIdByIndex", "rows", "mergeBackupRows",
  // 請求書メール受信箱から来た取込。復元後の登録完了でも受信箱の行を「登録済」にできるよう残す（File 本体は残せない）
  "inboxItemId",
  // AIが読んだ請求書No（単一PDF。複数PDFは pdfSources の各要素に持つ）
  "importInvoiceNo",
  // プレビューで選んだ発注ステータス（全行共通）
  "importOrderStatus"
];

function costImportRunStateSave(rev) {
  if (!rev || !rev.importRunId) return;
  const wizard = {};
  for (const k of COST_IMPORT_RUN_SNAPSHOT_KEYS) if (rev[k] !== undefined) wizard[k] = rev[k];
  if (Array.isArray(rev.pdfSources)) wizard.pdfSources = rev.pdfSources.map((s) => ({ fileName: s?.fileName || "", invoiceNo: s?.invoiceNo || "" }));
  try {
    localStorage.setItem(
      COST_IMPORT_RUN_STORAGE_KEY,
      JSON.stringify({ importRunId: rev.importRunId, userId: currentUser?.dbId ?? null, at: Date.now(), wizard })
    );
  } catch (e) {
    console.warn("[原価取込] 控えを保存できませんでした（リロード後の復元はできません）:", e instanceof Error ? e.message : e);
  }
}

/** ログイン中ユーザーの有効な控えだけ返す（別ユーザー・期限切れ・壊れた値は null） */
function costImportRunStateLoad() {
  try {
    const raw = localStorage.getItem(COST_IMPORT_RUN_STORAGE_KEY);
    const st = raw ? JSON.parse(raw) : null;
    if (!st || typeof st.importRunId !== "string" || !st.wizard || !Array.isArray(st.wizard.rows)) return null;
    if (!Number.isFinite(Number(st.at)) || Date.now() - Number(st.at) > COST_IMPORT_RUN_STORAGE_TTL_MS) return null;
    if (st.userId !== (currentUser?.dbId ?? null)) return null;
    return st;
  } catch {
    return null;
  }
}

function costImportRunStateClear() {
  try {
    localStorage.removeItem(COST_IMPORT_RUN_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** 結果不明のまま残った取込があれば { st, count } を返す（登録対象になる行だけ数える） */
function costImportPendingRestore() {
  const st = costImportRunStateLoad();
  if (!st) return null;
  const count = st.wizard.rows.filter((r) => r && r.importCaptionKind !== "consumption_tax").length;
  return count ? { st, count } : null;
}

/** 控えからプレビューを復元する。案件一覧が未読込だと計上先の選択が消えるので、その間は待ってもらう。true=復元した */
function costImportRestoreFromStorage() {
  const pending = costImportPendingRestore();
  if (!pending) return false;
  if (!projects.length || uiState.heavyDataLoading) {
    showToast("案件データを読み込み中です。少し待ってからもう一度押してください");
    return false;
  }
  uiState.costRegWizard = { ...pending.st.wizard, phase: "preview", registering: false, importRunId: pending.st.importRunId };
  render();
  return true;
}

/** 原価登録／原価割当ページの先頭に出す案内（控えが無ければ空）。破棄は明示的に押したときだけ */
function costImportPendingRestoreBannerHtml() {
  const pending = costImportPendingRestore();
  if (!pending) return "";
  return `<div class="cost-import-dup-banner cost-import-restore-banner" role="status">
    <span class="cost-import-dup-banner-text">前回の原価取込で、登録結果を受け取れなかった行が <strong>${pending.count}件</strong> 残っています。同じ行は二重登録されないので、復元して登録を続けてください。</span>
    <button type="button" class="inline-btn" data-cost-import-restore="1">復元して続ける</button>
    <button type="button" class="inline-btn ghost" data-cost-import-discard="1">破棄</button>
  </div>`;
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (t.closest("[data-cost-import-restore]")) {
    costImportRestoreFromStorage();
    return;
  }
  if (t.closest("[data-cost-import-discard]")) {
    if (!window.confirm("前回の取込の控えを破棄します。登録されなかった行は取り込み直しが必要になります。よろしいですか？")) return;
    costImportRunStateClear();
    render();
  }
});

/** 再送で「登録済み」と返った行（サーバの既存行）をローカルに載せる先。既存行の案件で決める
 *  （結果不明のあと計上先を変えて再登録しても、サーバ側は初回の案件のままなので画面もそれに合わせる） */
function costImportProjectForServerRow(cost, fallbackProject) {
  const pid = cost?.project_id;
  if (pid == null) return unassignedCostProjectProxy();
  return projects.find((p) => Number(p.dbId) === Number(pid)) || fallbackProject;
}

/** 一括登録の1リクエストあたりの行数。サーバは行ごとにDB往復するので、1回の関数実行が長くなりすぎない
 *  （途中で切られて「登録されたのに失敗表示→再実行で二重登録」にならない）大きさにする。
 *  サーバ側上限 COST_BULK_CREATE_MAX_ROWS（api/costs.js）より小さいこと */
const COST_BULK_CREATE_CHUNK_ROWS = 100;

/** 通信断・ゲートウェイ側タイムアウトとみなして自動再送する HTTP ステータス（0=応答なし） */
const COST_BULK_CREATE_RETRY_STATUSES = new Set([0, 502, 503, 504]);
const COST_BULK_CREATE_MAX_RETRIES = 2;

/** apiFetch の結果が「結果不明」（応答なし／ゲートウェイ側の 502/503/504 で、サーバのエラー文が無い）か。
 *  サーバ自身が JSON で返した失敗（取込準備に失敗した 503 など）は確定した失敗として扱い、再送しない */
function costBulkCreateIsTransportUnknown(r) {
  return !r.ok && COST_BULK_CREATE_RETRY_STATUSES.has(r.status) && !(typeof r.data?.error === "string" && r.data.error);
}

/** 自動再送しても応答が得られなかったとき。行はプレビューに残り、同じ取込キーで再登録できる（二重登録にならない） */
const COST_BULK_CREATE_TRANSPORT_ERROR_MSG =
  "通信エラーで結果を受け取れませんでした。該当行はプレビューに残しています。通信が戻ってからもう一度「原価を登録する」を押してください（同じ行は二重登録されません）";

/**
 * 取込プレビューの一括登録。entries=[{ project, patch }] を COST_BULK_CREATE_CHUNK_ROWS 行ずつ
 * POST /api/costs（action:'bulk-create'）で送り、行ごとの結果 [{ ok, error }] を entries と同じ順で返す。
 * サーバは1行ずつ単発POSTと同じ検証を通す。1リクエストが丸ごと失敗しても、その分の行だけ失敗にして残りは続ける。
 * onProgress(done, total) は1リクエスト終わるごとに呼ぶ（進捗トースト用）。未ログイン（スタブ運用）時は1件ずつローカルへ積む。
 */
async function persistCostsToServerCreateBulk(entries, onProgress) {
  const results = entries.map(() => null);
  if (!authToken) {
    for (let i = 0; i < entries.length; i++) {
      await persistCostToServerCreate(entries[i].project, entries[i].patch);
      results[i] = { ok: true };
    }
    return results;
  }
  // 送れる行だけ集める（案件未取得の行はその行だけ失敗）
  const pending = [];
  entries.forEach((en, i) => {
    try {
      pending.push({ i, row: costCreateRequestBody(en.project, en.patch) });
    } catch (e) {
      results[i] = { ok: false, error: e instanceof Error ? e.message : "保存に失敗しました" };
    }
  });
  let done = 0;
  for (let at = 0; at < pending.length; at += COST_BULK_CREATE_CHUNK_ROWS) {
    const chunk = pending.slice(at, at + COST_BULK_CREATE_CHUNK_ROWS);
    const body = { action: "bulk-create", rows: chunk.map((x) => x.row) };
    // 通信断・ゲートウェイ側のタイムアウトだけ同じ内容で自動再送（最大2回）。行は冪等キー付きなので
    // サーバが先に登録済みでも二重にならない。4xx/5xxのサーバ応答（行エラー含む）は再送しない
    let res = await apiFetch("/api/costs", { method: "POST", body });
    for (let retry = 1; retry <= COST_BULK_CREATE_MAX_RETRIES && costBulkCreateIsTransportUnknown(res); retry++) {
      await new Promise((r) => setTimeout(r, 500 * retry));
      res = await apiFetch("/api/costs", { method: "POST", body });
    }
    const { ok, data } = res;
    const serverResults = ok && Array.isArray(data.results) && data.results.length === chunk.length ? data.results : null;
    const transportUnknown = !serverResults && costBulkCreateIsTransportUnknown(res);
    const requestErr = transportUnknown
      ? COST_BULK_CREATE_TRANSPORT_ERROR_MSG
      : typeof data?.error === "string" && data.error ? data.error : "原価の作成に失敗しました";
    chunk.forEach((x, k) => {
      const r = serverResults ? serverResults[k] : null;
      if (r && r.ok && r.cost) {
        const project = r.duplicate ? costImportProjectForServerRow(r.cost, entries[x.i].project) : entries[x.i].project;
        pushCreatedCostToLocalState(project, mapServerCostToClientShape(r.cost));
        results[x.i] = { ok: true, ...(r.duplicate ? { duplicate: true } : {}) };
      } else if (serverResults) {
        results[x.i] = { ok: false, error: typeof r?.error === "string" ? r.error : "原価の作成に失敗しました" };
      } else {
        results[x.i] = { ok: false, error: requestErr, ...(transportUnknown ? { unknown: true } : {}) };
      }
    });
    done += chunk.length;
    if (typeof onProgress === "function") onProgress(done, pending.length);
  }
  pruneStaleOrphanCostDuplicates();
  return results;
}

/** 原価フォームの「発注名」（note。一覧の発注名列）と「備考」（memo。長文メモ・一覧の列には出さない）。
 *  発注名と備考を分けたのは 2026-10 要望（以前は備考1つを発注名・摘要・備考と呼び分けていた） */
function costFormNoteMemoFieldsHtml(f) {
  return `
          <label class="cost-form-note-label">発注名（任意）
            <textarea data-cost-field="note" class="cost-form-note" rows="2" placeholder="例: 10月分 保守費（一覧の「発注名」に出ます）">${escapeHtml(f.note || "")}</textarea>
          </label>
          <label class="cost-form-note-label">備考（任意）
            <textarea data-cost-field="memo" class="cost-form-note" rows="4" placeholder="長文のメモ・申し送りなど（一覧の列には出ません）">${escapeHtml(f.memo || "")}</textarea>
          </label>`;
}

/** 一覧で、備考（memo）がある原価に付ける印。全文は押すと吹き出しで出る（列には出さない。15z-cost-memo-popover.js） */
function costMemoMarkHtml(c) {
  const memo = String(c?.memo || "").trim();
  return memo ? `<span class="cost-memo-mark" title="${escapeHtmlAttr(`備考: ${memo}`)}" aria-label="備考あり" data-cost-memo="${escapeHtmlAttr(memo)}">📝</span>` : "";
}

/** 既存原価の編集フォーム（uiState.costForm）を作る。通常の編集・差し戻しの修正で共有する
 *  （経路ごとに項目を写すと、項目追加時に片側だけ漏れて保存で値が消える） */
function costEditFormFromPlacement(placement, costUiId) {
  const cost = placement.cost;
  return {
    projectId: costAssignmentUiIdFromPlacement(placement),
    editingId: costUiId,
    category: cost.category,
    vendor: cost.vendor,
    amount: cost.amount,
    consumptionTax: costConsumptionTaxYen(cost),
    targetMonth: cost.targetMonth,
    // DATE はサーバから ISO 文字列で届くことがあるため date input 用に YYYY-MM-DD へ
    dueDate: String(cost.dueDate || "").slice(0, 10),
    paidDate: String(cost.paidDate || "").slice(0, 10),
    invoiceDate: String(cost.invoiceDate || "").slice(0, 10),
    paymentMethod: cost.paymentMethod || "",
    invoiceNo: cost.invoiceNo || "",
    withholdingTax: Math.round(Number(cost.withholdingTax || 0)) || 0,
    withholdingKind: costWithholdingKindFromAmount(cost.amount, cost.withholdingTax),
    orderStatus: cost.orderStatus || "",
    isProvisional: cost.isProvisional === true,
    createdByUserDbId: cost.createdByUserDbId ?? null, // 表示のみ（登録者は変更不可）
    payStatus: cost.payStatus,
    note: stripCostBucketMarkers(cost.note || ""),
    memo: cost.memo || "",
    sourcePdfDocId:
      cost.sourcePdfDocId != null && Number(cost.sourcePdfDocId) > 0 ? Number(cost.sourcePdfDocId) : null
  };
}

/**
 * @param {string} editingCostId
 * @param {object} patch
 * @param {string|'keep'} assignTo 「keep」または案件 UI ID、または COST_UNASSIGNED_PROJECT_UI_ID
 */
async function persistCostToServerUpdate(editingCostId, patch, assignTo = "keep") {
  const pl = findCostPlacementByUiId(editingCostId);
  if (!pl) return false;
  const keepBucket = assignTo === "keep" || assignTo === costAssignmentUiIdFromPlacement(pl);
  const merged = {
    ...pl.cost,
    category: normalizeCostCategoryLabel(patch.category),
    vendor: patch.vendor,
    amount: patch.amount,
    consumptionTax: Math.round(Number(patch.consumptionTax ?? 0)) || 0,
    targetMonth: patch.targetMonth,
    payStatus: patch.payStatus,
    dueDate: patch.dueDate || "",
    paidDate: patch.paidDate ?? pl.cost.paidDate ?? "",
    invoiceDate: patch.invoiceDate ?? pl.cost.invoiceDate ?? "",
    paymentMethod: patch.paymentMethod ?? pl.cost.paymentMethod ?? "",
    invoiceNo: patch.invoiceNo ?? pl.cost.invoiceNo ?? "",
    withholdingTax: patch.withholdingTax ?? pl.cost.withholdingTax ?? 0,
    orderStatus: patch.orderStatus ?? pl.cost.orderStatus ?? "",
    isProvisional: patch.isProvisional !== undefined ? patch.isProvisional === true : (pl.cost.isProvisional === true),
    note: "",
    memo: patch.memo !== undefined ? String(patch.memo || "") : pl.cost.memo || ""
  };
  if (assignTo === "keep") {
    merged.note = patch.note ?? pl.cost.note ?? "";
    merged._costSalesExpense = isSalesExpenseCostRow({ note: merged.note });
    merged._costAdjustment = isAdjustmentCostRow({ note: merged.note });
  } else {
    merged._costSalesExpense = assignTo === COST_SALES_EXPENSE_PROJECT_UI_ID;
    merged._costAdjustment = assignTo === COST_ADJUSTMENT_PROJECT_UI_ID;
    merged.note = merged._costAdjustment
      ? ensureAdjustmentNote(patch.note ?? "", true)
      : ensureSalesExpenseNote(patch.note ?? "", merged._costSalesExpense);
  }
  if (!keepBucket && !isNoProjectBucketUiId(assignTo)) {
    delete merged.delegatedSalesUserDbId;
  }
  // 案件 or 販管費/調整用へ割り当て直したら異議は解消（サーバ側クリアと同じ規則。リロード前の表示整合用）
  if (
    assignTo !== "keep" &&
    (!isNoProjectBucketUiId(assignTo) || assignTo === COST_SALES_EXPENSE_PROJECT_UI_ID || assignTo === COST_ADJUSTMENT_PROJECT_UI_ID)
  ) {
    delete merged.disputeNote;
    delete merged.disputedByDbId;
    delete merged.disputedAt;
    delete merged.disputeProjectDbId;
  }

  const n = costDbNumericId({ id: editingCostId });
  if (!authToken || n == null) {
    if (keepBucket) {
      Object.assign(pl.cost, merged);
      pruneStaleOrphanCostDuplicates();
      return true;
    }
    removeCostPlacementAt(pl);
    if (isNoProjectBucketUiId(assignTo)) {
      ensureOrphanCosts().push(merged);
      pruneStaleOrphanCostDuplicates();
      return true;
    }
    const tp = projects.find((x) => x.id === assignTo);
    if (!tp) return false;
    if (!tp.costs) tp.costs = [];
    tp.costs.push(merged);
    pruneStaleOrphanCostDuplicates();
    return true;
  }

  const body = {
    id: n,
    category: normalizeCostCategoryLabel(patch.category),
    vendor: patch.vendor,
    amount: patch.amount,
    consumptionTax: Math.round(Number(patch.consumptionTax ?? 0)) || 0,
    targetMonth: normalizeCostTargetMonthForSave(patch.targetMonth) || pl.cost.targetMonth || "",
    payStatus: patch.payStatus || pl.cost.payStatus || "未登録",
    dueDate: patch.dueDate || null,
    paidDate: patch.paidDate ?? pl.cost.paidDate ?? null,
    invoiceDate: patch.invoiceDate ?? pl.cost.invoiceDate ?? null,
    paymentMethod: patch.paymentMethod ?? pl.cost.paymentMethod ?? "",
    invoiceNo: patch.invoiceNo ?? pl.cost.invoiceNo ?? "",
    withholdingTax: patch.withholdingTax ?? pl.cost.withholdingTax ?? 0,
    orderStatus: patch.orderStatus ?? pl.cost.orderStatus ?? "",
    ...(patch.isProvisional !== undefined
      ? { isProvisional: patch.isProvisional === true }
      : (pl.cost.isProvisional ? { isProvisional: true } : {})),
    note: merged.note,
    // 備考は編集フォームから保存したときだけ送る（送らない更新ではサーバーが今の備考を残す）
    ...(patch.memo !== undefined ? { memo: String(patch.memo || "") } : {}),
    // 画面が最後に見た値。他の人がその後に金額・支払日を変えていればサーバーが 409 で止める（api/costs.js costBaseConflicts）
    base: { amount: pl.cost.amount, consumptionTax: pl.cost.consumptionTax, paidDate: pl.cost.paidDate || "" }
  };
  if (!keepBucket) {
    try {
      body.projectId = resolveAssignToProjectDbPayload(assignTo);
    } catch (err) {
      throw err instanceof Error ? err : new Error("計上先案件の指定が不正です");
    }
  }

  const res = await fetch("/api/costs", {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const base = typeof data.error === "string" ? data.error : "原価の更新に失敗しました";
    const pg = data.pgCode != null ? String(data.pgCode) : "";
    const pgm = typeof data.pgMessage === "string" && data.pgMessage.trim() ? data.pgMessage.trim() : "";
    const extra = pg && pgm ? ` [${pg}] ${pgm}` : pg ? ` [${pg}]` : "";
    throw new Error(base + extra);
  }
  // サーバーが計上月を寄せた（REM原価を案件へ割当）ときはその値を持つ。古い月のまま次の保存で送ると元に戻る
  if (typeof data.targetMonth === "string" && data.targetMonth) merged.targetMonth = data.targetMonth;
  // 発注申請中は承認まで仮原価のまま保存される／申請中に受領した＝承認前発注の印が付いた（api/costs.js PUT）
  if (data.isProvisional === true) merged.isProvisional = true;
  if (data.orderPreOrdered === true) merged.orderPreOrderedAt = new Date().toISOString();
  // 承認前（申請中・却下）の発注申請を直して承認をやり直した（申請中・承認0人へ戻った。api/costs.js PUT）
  if (data.orderApprovalStatus === "申請中") {
    merged.orderApprovalStatus = "申請中";
    merged.isProvisional = true;
  }

  if (keepBucket) {
    Object.assign(pl.cost, merged);
    pruneStaleOrphanCostDuplicates();
    return true;
  }
  removeCostPlacementAt(pl);
  if (isNoProjectBucketUiId(assignTo)) {
    ensureOrphanCosts().push(merged);
  } else {
    const tp = projects.find((x) => x.id === assignTo);
    if (!tp) throw new Error("案件が見つかりません。");
    if (!tp.costs) tp.costs = [];
    tp.costs.push(merged);
  }
  pruneStaleOrphanCostDuplicates();
  return true;
}

/** 案件未割当原価に案件割当の依頼先を付与／解除（POST /api/cost-delegations） */
async function persistCostDelegationsToServer(costDbIds, delegatedUserDbId) {
  if (!authToken) throw new Error("ログインが必要です");
  const res = await fetch("/api/cost-delegations", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ costIds: costDbIds, delegatedSalesUserId: delegatedUserDbId })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const base = typeof data.error === "string" ? data.error : "依頼の更新に失敗しました";
    const pg = data.pgCode != null ? String(data.pgCode) : "";
    const pgm = typeof data.pgMessage === "string" && data.pgMessage.trim() ? data.pgMessage.trim() : "";
    const extra = pg && pgm ? ` [${pg}] ${pgm}` : pg ? ` [${pg}]` : "";
    throw new Error(base + extra);
  }
}

async function persistCostToServerDelete(costIdUi) {
  const pl = findCostPlacementByUiId(costIdUi);
  if (!pl) return false;
  const n = costDbNumericId({ id: costIdUi });
  if (authToken != null && n != null) {
    const res = await fetch("/api/costs", {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ id: n })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : "原価の削除に失敗しました");
  }
  removeCostPlacementAt(pl);
  return true;
}

function renderCostRegistrationWizard() {
  const w = uiState.costRegWizard;
  // プレビュー以外では遅延生成用の案件一覧を持たない（閉じた後に数百KBの文字列を抱えない）
  if (!w || w.phase !== "preview") costImportLazyProjectOptions = null;
  if (!w) return "";
  // ドメイン列→案件照合（お名前等）用の軽量ドメインデータを先読み（CSV/PDF選択中に届く）
  void fetchDomainsLightForCostImport();

  const backdrop =
    `<div class="composer-backdrop project-create-backdrop" aria-hidden="true"></div>`;

  const headClose = `<button type="button" class="inline-btn ghost" data-close-cost-reg-wizard="1">閉じる</button>`;

  /** Step1とStep2で同じ2状態のみ使う: 現在=active（濃色・白文字）、それ以外=既定（薄グレー・グレー文字） */
  const stepIndicatorHtml = (wizStep) =>
    `
    <div class="step-indicator">
      <span class="step-dot ${wizStep === 1 ? "active" : ""}">1</span>
      <span class="step-line ${wizStep >= 2 ? "active" : ""}"></span>
      <span class="step-dot ${wizStep >= 2 ? "active" : ""}">2</span>
    </div>`;

  if (w.phase === "setup") {
    const isOtherMode = w.mode === "other";
    const isSalesMode = w.salesMode === true;
    const modalTitle = isSalesMode ? "請求書から原価を登録" : isOtherMode ? "原価を登録" : "広告原価を登録";
    const monthVal = billingMonthFilterToMonthInputValue(w.targetMonth || currentMonth());
    const fallbackChoice =
      typeof w.costImportChoice === "string" && parseCostImportChoiceValue(w.costImportChoice)
        ? w.costImportChoice
        : isSalesMode ? "pdf:generic" : isOtherMode ? "csv:generic" : `csv:${COST_IMPORT_CSV_SOURCE_META[w.csvSourceKey] ? w.csvSourceKey : "google"}`;
    const adCsvButtonsHtml = isOtherMode ? "" : COST_IMPORT_AD_PLATFORM_KEYS.map((k) => {
      const m = COST_IMPORT_CSV_SOURCE_META[k];
      const icon = COST_IMPORT_AD_PLATFORM_ICON[k] || "A";
      const isActive = fallbackChoice === `csv:${k}`;
      return `<button type="button" class="cost-import-icon-btn ${isActive ? "active" : ""}" data-cost-wizard-import="csv:${escapeHtmlAttr(k)}">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">${escapeHtml(icon)}</span>
        <span class="cost-import-icon-btn-title">${escapeHtml(m.label)}</span>
        <span class="cost-import-icon-btn-sub">CSV</span>
      </button>`;
    }).join("");
    const genericCsvActive = fallbackChoice === "csv:generic";
    const genericPdfActive = fallbackChoice === "pdf:generic";
    const miscImportButtonsHtml = !isOtherMode ? "" : `
      ${isSalesMode ? "" : `<button type="button" class="cost-import-icon-btn cost-import-icon-btn--misc-csv ${genericCsvActive ? "active" : ""}" data-cost-wizard-import="csv:generic">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">CSV</span>
        <span class="cost-import-icon-btn-title">その他CSV</span>
        <span class="cost-import-icon-btn-sub">汎用データ</span>
      </button>`}
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--misc-pdf ${genericPdfActive ? "active" : ""}" data-cost-wizard-import="pdf:generic">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">PDF</span>
        <span class="cost-import-icon-btn-title">PDF</span>
        <span class="cost-import-icon-btn-sub">請求書・明細</span>
      </button>
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--misc-excel ${fallbackChoice === "excel:generic" ? "active" : ""}" data-cost-wizard-import="excel:generic">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">XLSX</span>
        <span class="cost-import-icon-btn-title">Excel</span>
        <span class="cost-import-icon-btn-sub">請求書・明細</span>
      </button>
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--paste-ai ${fallbackChoice === "paste:generic" ? "active" : ""}" data-cost-wizard-import="paste:generic">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">貼</span>
        <span class="cost-import-icon-btn-title">コピペ取込</span>
        <span class="cost-import-icon-btn-sub">AIで読み取り</span>
      </button>
      ${isSalesMode ? "" : `<button type="button" class="cost-import-icon-btn cost-import-icon-btn--onamae ${fallbackChoice === "paste:onamae" ? "active" : ""}" data-cost-wizard-import="paste:onamae">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">お</span>
        <span class="cost-import-icon-btn-title">お名前.com</span>
        <span class="cost-import-icon-btn-sub">表を貼り付け</span>
      </button>
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--upsider ${fallbackChoice === "csv:upsider" ? "active" : ""}" data-cost-wizard-import="csv:upsider">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">U</span>
        <span class="cost-import-icon-btn-title">UPSIDER</span>
        <span class="cost-import-icon-btn-sub">カード明細CSV</span>
      </button>
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--tecolas ${fallbackChoice === "csv:tecolas" ? "active" : ""}" data-cost-wizard-import="csv:tecolas">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">AWS</span>
        <span class="cost-import-icon-btn-title">テコラス（AWS）</span>
        <span class="cost-import-icon-btn-sub">請求明細CSV</span>
      </button>
      <button type="button" class="cost-import-icon-btn cost-import-icon-btn--manual" data-cost-wizard-manual="1">
        <span class="cost-import-icon-btn-mark" aria-hidden="true">＋</span>
        <span class="cost-import-icon-btn-title">手入力</span>
        <span class="cost-import-icon-btn-sub">フォーム入力</span>
      </button>`}`;
    return `${backdrop}
    <aside class="composer-panel project-create-modal cost-reg-wizard-modal${spNoEnter("cost-reg-wizard-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-reg-wizard-title">
      <div class="composer-head">
        <div><h3 id="cost-reg-wizard-title">${escapeHtml(modalTitle)}</h3></div>
        ${headClose}
      </div>
      <div class="composer-body">
        ${stepIndicatorHtml(1)}
        <div class="project-create-step project-create-step1">
          <h3>Step 1: 取り込みの準備</h3>
          ${Array.isArray(w.inboxFiles) && w.inboxFiles.length
            ? `<p class="project-create-step1-lead">📥 受信箱のメール「${escapeHtml(w.inboxSubject || "")}」の添付PDF（${w.inboxFiles.length}件）を取り込みます。計上月を選んで「PDF」を押してください。</p>`
            : ""}
          ${isOtherMode ? "" : `<p class="project-create-step1-lead">
            広告媒体はすべて <strong>CSV</strong> で取り込みます。媒体アイコンのボタンを押すと、その媒体のCSV取込が始まります。
            案件の紐づけは Step 2 のプレビューで行います。
          </p>`}

          <div class="project-create-lane project-create-lane--search">
            <div class="project-create-lane-head">
              <span class="project-create-lane-eyebrow" aria-hidden="true">A</span>
              <div>
                <label class="project-create-lane-title cost-wizard-month-title" for="cost-wizard-month-input">${isOtherMode ? "計上月" : "媒体費の月（何月分）"}</label>
                <p class="project-create-lane-desc">${isOtherMode ? "この原価が発生した月" : "広告が配信された月を選択（例: 3月分の媒体費は4月に請求書が届いても「3月」を選択）"}</p>
              </div>
            </div>
            <div class="client-picker project-create-search-wrap">
              <input id="cost-wizard-month-input" class="search project-create-search-input pj-input" type="month" data-show-picker="1" data-cost-wizard-month value="${escapeHtmlAttr(monthVal)}" title="計上月" />
              ${isMonthLocked(w.targetMonth)
                ? '<p class="cost-lock-warning">🔒 この月は月次ロック済みです。新規の原価登録には管理者/マスター権限が必要です。</p>'
                : ""}
            </div>
          </div>

          <div class="project-create-form-block-title cost-reg-block-after-lane">${isOtherMode ? "取込方法" : "各広告媒体（CSV）"}</div>
          <div class="project-create-lane project-create-lane--cost-meta">
            <div class="project-create-lane-head">
              <span class="project-create-lane-eyebrow project-create-lane-eyebrow--teal" aria-hidden="true">B</span>
              <div>
                <div class="project-create-lane-title">取込方法を選択</div>
                <p class="project-create-lane-desc">${isSalesMode ? "受け取った請求書（PDF・Excel）をアップロードするか、内容をコピペで取り込みます。AIが明細を読み取ります。" : isOtherMode ? "CSV・PDF・Excel・コピペで取り込むか、手入力で登録してください。" : "各媒体のCSVをアップロードして取り込みます。"}</p>
              </div>
            </div>
            <div class="cost-import-icon-grid">
              ${adCsvButtonsHtml}${miscImportButtonsHtml}
            </div>
          </div>
        </div>
      </div>
      <div class="composer-foot">
        <button type="button" class="inline-btn ghost" data-close-cost-reg-wizard="1">キャンセル</button>
      </div>
    </aside>`;
  }

  if (w.phase === "paste") {
    const monthVal = billingMonthFilterToMonthInputValue(w.targetMonth || currentMonth());
    const pasteVal = escapeHtml(String(w.pasteText || ""));
    const isGenericPaste = w.costImportChoice === "paste:generic";
    const pasteTitle = isGenericPaste ? "コピペで原価を登録（AI読取）" : "お名前ドットコムを貼り付けて登録";
    const pasteStepTitle = isGenericPaste ? "Step 1: 請求内容を貼り付け" : "Step 1: 管理画面の表を貼り付け";
    const pasteLead = isGenericPaste
      ? `Excelのセル範囲・メール本文・PDFの文字など、請求内容をそのままコピーして貼り付けてください。
            AIが品目・金額・税区分を読み取り、明細行に変換します。案件の紐づけは Step 2 で確認できます。`
      : `お名前ドットコムの管理画面で明細表を選択してコピーし、下の欄に貼り付けてください。
            金額・税率・ドメインを自動で読み取り、ドメインから案件を推定します（0円の行は除外）。案件の紐づけは Step 2 で確認できます。`;
    const pastePlaceholder = isGenericPaste
      ? "請求書の明細をコピーして貼り付け（Excelの表・メール本文などそのままでOK。AIが品目と金額を読み取ります）"
      : "管理画面の表をそのままコピーして貼り付け（日付・品目・ドメイン・税率・金額の列を自動判定します）";
    return `${backdrop}
    <aside class="composer-panel project-create-modal cost-reg-wizard-modal${spNoEnter("cost-reg-wizard-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-reg-wizard-title">
      <div class="composer-head">
        <div><h3 id="cost-reg-wizard-title">${escapeHtml(pasteTitle)}</h3></div>
        ${headClose}
      </div>
      <div class="composer-body">
        ${stepIndicatorHtml(1)}
        <div class="project-create-step project-create-step1">
          <h3>${escapeHtml(pasteStepTitle)}</h3>
          <p class="project-create-step1-lead">
            ${pasteLead}
          </p>
          <div class="client-form-section project-create-step2-block">
            <h4 class="project-create-form-block-title">計上月</h4>
            <label class="client-form-row">計上月
              <input class="pj-input" type="month" data-show-picker="1" data-cost-wizard-paste-month value="${escapeHtmlAttr(monthVal)}" />
            </label>
          </div>
          <div class="client-form-section project-create-step2-block">
            <h4 class="project-create-form-block-title">明細を貼り付け</h4>
            <textarea class="pj-input cost-import-paste-textarea" data-cost-wizard-paste-text rows="10" placeholder="${escapeHtmlAttr(pastePlaceholder)}">${pasteVal}</textarea>
          </div>
        </div>
      </div>
      <div class="composer-foot">
        <button type="button" class="inline-btn ghost" data-cost-wizard-back-setup="1">← 戻る</button>
        <button type="button" class="inline-btn" data-cost-wizard-paste-parse="1">取り込む →</button>
      </div>
    </aside>`;
  }

  if (w.phase === "manual") {
    const monthVal = billingMonthFilterToMonthInputValue(w.targetMonth || currentMonth());
    const pidNorm = w.manualProjectId && w.manualProjectId !== "" ? w.manualProjectId : COST_UNASSIGNED_PROJECT_UI_ID;
    const manualBoxOwners = costBoxPickCandidateOwners(false);
    const eligManualSet = new Set(projectsEligibleForCostTargetMonth(w.targetMonth).map((p) => p.id));
    const eligManual = excludeCandidateCostBoxProjects([...projects], manualBoxOwners, pidNorm).sort((a, b) => {
      const aOk = eligManualSet.has(a.id) ? 0 : 1;
      const bOk = eligManualSet.has(b.id) ? 0 : 1;
      return aOk - bOk;
    });
    const opts =
      `<option value="${escapeHtmlAttr(COST_UNASSIGNED_PROJECT_UI_ID)}" ${pidNorm === COST_UNASSIGNED_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_UNASSIGNED_PROJECT_LABEL)}</option>` +
      `<option value="${escapeHtmlAttr(COST_SALES_EXPENSE_PROJECT_UI_ID)}" ${pidNorm === COST_SALES_EXPENSE_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_SALES_EXPENSE_PROJECT_LABEL)}</option>` +
      `<option value="${escapeHtmlAttr(COST_ADJUSTMENT_PROJECT_UI_ID)}" ${pidNorm === COST_ADJUSTMENT_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_ADJUSTMENT_PROJECT_LABEL)}</option>` +
      costBoxPickOptionsHtml(pidNorm, manualBoxOwners) +
      costProjectListOptgroupHtml(
        eligManual
          .map(
            (p) => {
              const isMatch = eligManualSet.has(p.id);
              const label = escapeHtml(projectAccountingPickLabel(p)) + (isMatch ? " ★" : "");
              return `<option value="${escapeHtmlAttr(p.id)}" ${p.id === pidNorm ? "selected" : ""} data-option-search="${escapeHtmlAttr(projectAccountingPickSearchBlob(p))}">${label}</option>`;
            }
          )
          .join("")
      );
    return `${backdrop}
    <aside class="composer-panel project-create-modal cost-reg-wizard-modal${spNoEnter("cost-reg-wizard-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-reg-wizard-title">
      <div class="composer-head">
        <div><h3 id="cost-reg-wizard-title">原価を登録</h3></div>
        ${headClose}
      </div>
      <div class="composer-body">
        ${stepIndicatorHtml(1)}
        <div class="project-create-step project-create-step2">
          <h3>手入力: 案件を選択</h3>
          <p class="project-create-step2-sub">計上月は前の画面で選んだ月が入っています（変更する場合のみ選び直してください）。登録する案件を選んで入力フォームへ進んでください。</p>
          <div class="client-form-section project-create-step2-block">
            <h4 class="project-create-form-block-title">計上月</h4>
            <label class="client-form-row">計上月（選択済み・変更可）
              <input class="pj-input" type="month" data-show-picker="1" data-cost-wizard-manual-month value="${escapeHtmlAttr(monthVal)}" />
            </label>
          </div>
          <div class="client-form-section project-create-step2-block">
            <h4 class="project-create-form-block-title">案件<span class="cost-form-hint">（未割当でも登録できます）</span></h4>
            <label class="client-form-row">一覧から選択<select class="pj-input" data-cost-wizard-manual-project>${opts}</select>
              <span class="cost-form-hint">★が対象月に一致する案件です。</span>
            </label>
          </div>
        </div>
      </div>
      <div class="composer-foot">
        <button type="button" class="inline-btn ghost" data-cost-wizard-back-setup="1">← 戻る</button>
        <button type="button" class="inline-btn" data-cost-wizard-open-manual-form="1">入力フォームを開く →</button>
      </div>
    </aside>`;
  }

  if (w.phase === "preview" && !w.rows?.length) {
    return `${backdrop}
    <aside class="composer-panel project-create-modal cost-reg-wizard-modal cost-import-review${spNoEnter("cost-reg-wizard-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-reg-wizard-title">
      <div class="composer-head">
        <div><h3 id="cost-reg-wizard-title">原価を登録</h3></div>
        ${headClose}
      </div>
      <div class="composer-body">
        ${stepIndicatorHtml(2)}
        <div class="project-create-step project-create-step1">
          <h3>Step 2: プレビューと案件の紐づけ</h3>
          <p class="project-create-step1-lead">
            プレビューに表示する明細がありません。行の<strong>×</strong>ですべて除外した場合は、Step 1 に戻ってファイルを取り込み直してください。
          </p>
        </div>
      </div>
      <div class="composer-foot">
        <button type="button" class="inline-btn ghost" data-cost-wizard-back-setup="1">← Step 1 に戻る</button>
      </div>
    </aside>`;
  }

  if (w.phase === "preview" && w.rows?.length) {
    const importCatDefault = w.mode === "other" ? "制作費" : "媒体費";
    const importCatNorm = w.importCategory ? normalizeCostCategoryLabel(w.importCategory) : "";
    const importCat =
      importCatNorm && COST_CATEGORIES.includes(importCatNorm) ? importCatNorm : importCatDefault;
    const importCatOptions = COST_CATEGORIES.map(
      (c) => `<option value="${escapeHtmlAttr(c)}" ${c === importCat ? "selected" : ""}>${escapeHtml(c)}</option>`
    ).join("");
    const hasUnmatchedVendor = w.rows.some((r) => r.vendorMasterUnmatched);
    const dupCount = w.rows.filter((r) => r.duplicateOfExisting).length;
    const isAdsCsvForTax = w.previewKind === "csv" && COST_IMPORT_AD_PLATFORM_KEYS.includes(w.csvSourceKey);
    // 税モードトグル（広告CSV以外）。未設定なら取込値から推定する。
    // 税込は税抜額 < 元の取込額（内税を控除）／税抜は両者が等しい、で判別する
    // （税抜取込も消費税10%を持つようになったため「税額>0=税込」では判別できない）。
    if (!isAdsCsvForTax && w.importTaxMode !== "inclusive" && w.importTaxMode !== "exclusive") {
      w.importTaxMode = w.rows.some((r) => {
        const ex = Math.round(Number(r.amountExTax ?? 0)) || 0;
        const raw = Math.round(Number(r.importedRawAmount ?? 0)) || 0;
        return ex > 0 && raw > 0 && ex < raw;
      })
        ? "inclusive"
        : "exclusive";
    }
    const taxToggleHtml = isAdsCsvForTax
      ? ""
      : `<p class="cost-import-preview-meta-line cost-import-tax-toggle-line">金額の税区分：
          <span class="cost-import-tax-toggle" role="group" aria-label="取り込んだ金額が税込か税抜か">
            <button type="button" class="cost-import-tax-toggle-btn ${w.importTaxMode === "inclusive" ? "active" : ""}" data-cost-import-tax-mode="inclusive">税込</button>
            <button type="button" class="cost-import-tax-toggle-btn ${w.importTaxMode === "exclusive" ? "active" : ""}" data-cost-import-tax-mode="exclusive">税抜</button>
          </span>
        </p>`;
    const dupBannerHtml = dupCount > 0
      ? `<div class="cost-import-dup-banner" role="status">
          <span class="cost-import-dup-banner-text">⚠ 既存の原価と重複の可能性がある行が <strong>${dupCount}件</strong> あります（対象月・発注先・金額が一致）。</span>
          <button type="button" class="inline-btn ghost cost-import-dup-exclude-btn" data-cost-import-exclude-dups="1">重複候補を一括除外</button>
        </div>`
      : "";
    const payeePdfUnregistered =
      w.previewKind === "pdf" && w.rows?.length ? w.rows.some((x) => x.vendorMasterUnmatched) : false;
    const targetMonthSlash = escapeHtml(costTargetMonthSlashDisplay(w.targetMonth));
    const payeeUnregHtml = payeePdfUnregistered
      ? `<span class="cost-import-payee-inline-warn cost-import-payee-warn cost-import-payee-warn--inline">（未登録の取引先です）</span>`
      : "";
    const isMultiPdf = w.previewKind === "pdf" && Array.isArray(w.pdfSources) && w.pdfSources.length > 1;
    const payeeRowHtml = isMultiPdf
      ? `<p class="cost-import-preview-meta-line">読み取った請求元：<span class="cost-import-payee-dash">PDFごとに自動判定（各行に表示）</span></p>`
      : w.previewKind === "pdf"
        ? `<p class="cost-import-preview-meta-line cost-import-preview-payee-row">
          <span class="cost-import-preview-payee-heading">読み取った請求元：</span>
          <label class="cost-import-payee-field">
            <span class="cost-import-visually-hidden">請求元の照会先（パートナー・クライアント／PDFから読み取った文言）</span>
            <select class="pj-input cost-import-payee-select" data-document-payee-select="1" aria-label="読み取った請求元" aria-describedby="cost-import-payee-help">${costImportDocumentPayeeSelectOptionsHtml(w)}</select>
          </label>
          ${payeeUnregHtml}
          <span id="cost-import-payee-help" class="cost-import-visually-hidden">PDFから推定した請求元。パートナー・クライアント／原文の読み取りを切り替えられます。</span>
        </p>`
        : `<p class="cost-import-preview-meta-line">読み取った請求元：<span class="cost-import-payee-dash">―</span></p>`;
    const isAdsCsv = w.previewKind === "csv" && COST_IMPORT_AD_PLATFORM_KEYS.includes(w.csvSourceKey);
    const rowCountLine = `<p class="cost-import-preview-meta-line"><strong>${w.rows.length}件</strong>を読み込みました${isMultiPdf ? `（PDF ${w.pdfSources.length}枚）` : ""}</p>`;
    const previewMetaBanner = isAdsCsv
      ? `<div class="cost-import-preview-meta" role="status">
          <p class="cost-import-preview-meta-line">媒体費の月（何月分）：${targetMonthSlash}</p>
          <p class="cost-import-preview-meta-line">会計区分：<strong>媒体費</strong></p>
          ${rowCountLine}
        </div>`
      : `<div class="cost-import-preview-meta" role="status">
          ${payeeRowHtml}
          <p class="cost-import-preview-meta-line">登録対象月：${targetMonthSlash}</p>
          ${rowCountLine}
          ${taxToggleHtml}
        </div>`;
    costImportPreviewPruneProjectPicksToTargetMonth(w);
    // 営業モード: 計上先は自分が担当の案件のみ（サーバの実原価POST制限と対）。未割当・販管費・調整バケットは出さない
    const isSalesMode = w.salesMode === true;
    const rowBoxOwners = costBoxPickCandidateOwners(isSalesMode);
    const pickableProjectsRaw = isSalesMode ? costWizardSalesModeProjects() : projects;
    const pickableIds = isSalesMode ? new Set(pickableProjectsRaw.map((p) => p.id)) : null;
    // 全案件を表示し、グローバル対象月と一致する案件に★マーカー。
    // 「原価箱」グループと重複する既存の原価箱案件は一覧から間引く（選択中のものは残す）。
    // 案件一覧の <option> は全行で同じなので描画1回につき1度だけ組み立てる（行ごとに作ると
    // 129行×約2,400案件の文字列生成とクライアント名の線形探索で描画が数秒止まる）
    const projectOptionList = costImportProjectOptionListBuilder(pickableProjectsRaw, rowBoxOwners, w.targetMonth);
    costImportLazyProjectOptions = projectOptionList;
    const tableRows = w.rows
      .map((r, i) => {
        let pidPick = String(r.projectIdPick ?? "").trim();
        if (isSalesMode && pidPick && !pickableIds.has(pidPick) && !costBoxPickOwnerUiId(pidPick)) pidPick = "";
        const headUn = isSalesMode
          ? `<option value="" ${pidPick === "" ? "selected" : ""}>（計上先の案件を選択）</option>`
          : `<option value="" ${pidPick === "" ? "selected" : ""}>${escapeHtml(COST_UNASSIGNED_PROJECT_LABEL)}</option>` +
            `<option value="${escapeHtmlAttr(COST_SALES_EXPENSE_PROJECT_UI_ID)}" ${pidPick === COST_SALES_EXPENSE_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_SALES_EXPENSE_PROJECT_LABEL)}</option>` +
            `<option value="${escapeHtmlAttr(COST_ADJUSTMENT_PROJECT_UI_ID)}" ${pidPick === COST_ADJUSTMENT_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_ADJUSTMENT_PROJECT_LABEL)}</option>`;
        // 案件一覧は遅延生成: 初期は選択中の1件だけ持ち、開いた時に全案件を流し込む（costImportFillLazyProjectOptions）
        const opts =
          headUn +
          costBoxPickOptionsHtml(pidPick, rowBoxOwners) +
          `<optgroup label="案件一覧" data-lazy-project-group="1">${projectOptionList.selectedOnlyHtmlFor(pidPick)}</optgroup>`;
        const noteHtml = (r.note ? `<div class="cost-import-hint-note">${escapeHtml(String(r.note))}</div>` : "") + (r.autoNote ? `<div class="cost-import-suggested-sub">発注名に追記: ${escapeHtml(String(r.autoNote))}</div>` : "");
        const extrasHtml = r.suggestedProjectExtras
          ? `<div class="cost-import-suggested-sub">ほかの案件候補: ${escapeHtml(r.suggestedProjectExtras)}</div>`
          : "";
        // ドメイン列がドメイン管理と照合できたか（サーバー請求の xxx.onamaeweb.jp 等の紐づけ根拠）
        const domainRegHtml = r.domainRegistryLabel
          ? `<div class="cost-import-suggested-sub cost-import-domain-reg">ドメイン管理: ${escapeHtml(r.domainRegistryLabel)}</div>`
          : r.domainRegistryMiss
            ? `<div class="cost-import-suggested-sub cost-import-domain-reg">ドメイン管理に未登録（${escapeHtml(String(r.domainHint || ""))}）</div>`
            : "";
        const capKind = r.importCaptionKind || "normal";
        const summaryDisplayBase = stripInvoiceTableMergedNoise(
          String(r.summaryCaptionRead || r.nameColumnText || r.clientHint || "").trim()
        );
        const effMonthDisp = rowEffectiveTargetMonth(r, w.targetMonth);
        const summaryDisplay = summaryDisplayBase
          ? `${summaryDisplayBase}${effMonthDisp ? ` ${effMonthDisp}` : ""}`
          : "";
        const capReadHtml = summaryDisplay
          ? `<div class="cost-import-cap-read">${escapeHtml(summaryDisplay)}</div>`
          : "";
        // 同一アカウント名が複数行ある場合、件数バッジと「→ 全行に反映」ヒントを表示
        const acctKey = String(r.accountNameKey || "");
        const acctCount = Number(r.accountNameCount) || 0;
        const acctBadgeHtml = acctKey && acctCount > 1
          ? `<div class="cost-import-acct-badge" title="${escapeHtmlAttr(acctKey)}">同アカウント計${acctCount}行　変更で全行反映</div>`
          : "";
        const summaryInner = capKind === "normal" && ["pdf", "excel", "paste"].includes(w.previewKind) ? `<textarea class="pj-input cost-import-manual-summary-input" data-cost-review-manual-summary="${i}" rows="2" placeholder="発注名（品名・項目など）">${escapeHtml(r.manualSummaryText ?? costImportSummaryTextForPersist(r, effMonthDisp))}</textarea>` : capReadHtml || `<span class="cost-import-cap-dash">—</span>`; // PDF・Excel・コピペの読み取り行は発注名を書き換えられる（広告CSV等は発注名の形を次回の照合に使うため固定）
        const dupBadgeHtml = r.duplicateOfExisting
          ? `<div class="cost-import-dup-badge" title="既存の原価と対象月・発注先・金額が一致します">⚠ 重複の可能性</div>`
          : "";
        // 複数PDF時は行ごとに請求元・元PDFが異なるので、どのPDF由来かを各行に表示
        const sourceHintHtml = (() => {
          if (!isMultiPdf || r.isManualPreviewRow || r.pdfSourceIndex == null) return "";
          const vendor = String(r.vendorResolvedLabel || "").trim();
          const fileName = String(w.pdfSources[r.pdfSourceIndex]?.fileName || "").trim();
          const parts = [];
          if (vendor) parts.push(vendor + (r.vendorMasterUnmatched ? "（未登録）" : ""));
          if (fileName) parts.push(fileName);
          if (!parts.length) return "";
          return `<div class="cost-import-suggested-sub cost-import-row-source">請求元: ${escapeHtml(parts.join(" · "))}</div>`;
        })();
        const manualSummaryVal = escapeHtml(String(r.manualSummaryText ?? ""));
        const isEditableSummary = r.isManualPreviewRow === true || r.isMergedPreviewRow === true;
        const summaryBlock = isEditableSummary
          ? `<textarea class="pj-input cost-import-manual-summary-input" data-cost-review-manual-summary="${i}" rows="1" placeholder="発注名（品名・項目など）">${manualSummaryVal}</textarea>${noteHtml}`
          : `<div class="cost-import-summary-main">${summaryInner}${dupBadgeHtml}${acctBadgeHtml}${sourceHintHtml}${domainRegHtml}${noteHtml}</div>`;
        const ex = Math.round(Number(r.amountExTax ?? 0)) || 0;
        const tx = Math.round(Number(r.consumptionTax ?? 0)) || 0;
        const isTaxAllocationSkip = capKind === "consumption_tax";
        const amtCell = isTaxAllocationSkip
          ? `${yen.format(ex)}${tx ? `<div class="cost-import-tax-sub">税 ${yen.format(tx)}</div>` : ""}`
          : `<div class="cost-import-amt-edit">
              <label class="cost-import-amt-field cost-import-amt-field--single">
                <input class="pj-input cost-import-amt-input" type="text" inputmode="numeric" autocomplete="off" data-cost-review-amount-ex="${i}" value="${ex}" aria-label="税抜金額（円）" title="税抜金額" />
              </label>
              ${tx ? `<div class="cost-import-tax-sub" title="消費税 ${yen.format(tx)}">税 ${yen.format(tx)}</div><div class="cost-import-tax-sub" title="税込 ${yen.format(ex + tx)}">税込 ${yen.format(ex + tx)}</div>` : ""}
            </div>`;
        // targetMonthOverride が手動設定済みの場合のみ緑チップで表示
        const overrideIsSet = !!String(r.targetMonthOverride || "").trim();
        let monthHintHtml = "";
        if (!isTaxAllocationSkip && overrideIsSet) {
          const dispMonth = costTargetMonthSlashDisplay(r.targetMonthOverride);
          monthHintHtml = `<div class="cost-import-month-override cost-import-month-override--set">` +
            `✓ 対象月: <strong>${escapeHtml(dispMonth)}</strong>で登録` +
            `<button type="button" class="cost-import-month-override-clear" data-cost-review-month-clear="${i}" aria-label="対象月のオーバーライドを解除">解除</button>` +
            `</div>`;
        }
        const provMatchHtml = isTaxAllocationSkip ? "" : costImportRowProvisionalMatchHtml(r, i);
        const projCell = isTaxAllocationSkip
          ? `<span class="cost-import-proj-placeholder" title="税額行は案件に割り当てません">―</span>`
          : `<div class="cost-import-proj-cell-wrap">` +
            `<select class="pj-input cost-import-proj-select" data-cost-review-pick="${i}" data-account-name-key="${escapeHtmlAttr(acctKey)}" data-placeholder-gray-off="1" data-lazy-project-options="1">${opts}</select>` +
            `${monthHintHtml}${provMatchHtml}${extrasHtml}</div>`; // ほかの案件候補は選ぶ欄の下に出す
        const reviewNoteVal = escapeHtml(String(r.reviewNote ?? ""));
        return `<tr${isTaxAllocationSkip ? ' class="cost-import-tax-row"' : ""}>
          <td class="cost-import-cap-cell cost-import-summary-merged">
            ${summaryBlock}
          </td>
          <td class="cost-import-amt">${amtCell}</td>
          <td class="cost-import-proj-td ${isTaxAllocationSkip ? "cost-import-proj-td--na" : ""}">${projCell}</td>
          <td class="cost-import-note-td cost-import-note-with-remove">
            <div class="cost-import-note-actions">
              ${isTaxAllocationSkip
                ? `<span class="cost-import-tax-label">（消費税・自動計算）</span>`
                : `<textarea class="pj-input cost-import-review-note" data-cost-review-note="${i}" rows="1" placeholder="一覧には出ません">${reviewNoteVal}</textarea>
              <button type="button" class="cost-import-row-remove" data-cost-review-remove="${i}" aria-label="この行をプレビューから除外">×</button>`}
            </div>
          </td>
        </tr>`;
      })
      .join("");
    const isRegistering = w.registering === true;
    const registerBtnLabel = isRegistering ? "登録中…" : hasUnmatchedVendor ? "それでも登録する" : "原価を登録する";
    const registerApplyVal = hasUnmatchedVendor ? "force" : "1";
    const registerBtnClass = hasUnmatchedVendor ? "inline-btn cost-import-force-btn" : "inline-btn";
    const mergeableCount = w.rows.filter((r) => r.importCaptionKind !== "consumption_tax").length;
    const mergeBtnHtml = Array.isArray(w.mergeBackupRows) && w.mergeBackupRows.length
      ? `<button type="button" class="inline-btn ghost" data-cost-preview-merge-undo="1"${isRegistering ? " disabled" : ""} title="まとめる前の明細行に戻します">まとめを解除</button>`
      : mergeableCount >= 2
        ? `<button type="button" class="inline-btn ghost" data-cost-preview-merge-rows="1"${isRegistering ? " disabled" : ""} title="明細行の金額を合算し、摘要をつなげて1行で登録します">ひとつにまとめる</button>`
        : "";
    const footApply = `
        <button type="button" class="inline-btn ghost" data-cost-wizard-back-setup="1"${isRegistering ? " disabled" : ""}>← 戻る</button>
        ${mergeBtnHtml}
        <button type="button" class="inline-btn ghost cost-import-manual-reg-btn" data-cost-preview-add-manual-row="1"${isRegistering ? " disabled" : ""}>手動で登録</button>
        <button type="button" class="${registerBtnClass}" data-cost-import-apply="${registerApplyVal}"${isRegistering ? ' disabled aria-busy="true"' : ""}>${escapeHtml(
      registerBtnLabel
    )}</button>
    `;
    return `${backdrop}
    <aside class="composer-panel project-create-modal cost-reg-wizard-modal cost-import-review${spNoEnter("cost-reg-wizard-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-reg-wizard-title">
      <div class="composer-head">
        <div><h3 id="cost-reg-wizard-title">原価を登録</h3></div>
        ${headClose}
      </div>
      <div class="composer-body">
        ${stepIndicatorHtml(2)}
        <div class="project-create-step project-create-step1">
          <h3>Step 2: プレビューと案件の紐づけ</h3>
          <p class="project-create-step1-lead">
            取り込んだ明細を確認し、<strong>登録する案件</strong>を各行で選んでください。取り込まない行は<strong>×</strong>で除外できます。会計区分は全行共通です。
          </p>
          ${previewMetaBanner}
          ${dupBannerHtml}
          ${isAdsCsv
            ? ``
            : `<label class="cost-import-category-bar">
                <span class="cost-import-category-bar-label">会計区分（全行共通）：</span>
                <select class="pj-input cost-import-category-select" data-cost-import-category-pick>${importCatOptions}</select>
              </label>`
          }
          <label class="cost-import-category-bar">
            <span class="cost-import-category-bar-label">発注ステータス（全行共通）：</span>
            <select class="pj-input cost-import-category-select" data-search-dropdown-off="1" data-cost-import-order-status-pick>${costImportOrderStatusOptionsHtml(w)}</select>
          </label>
          <div class="cost-reg-preview-table-wrap">
            <table class="cost-import-preview-table">
              <colgroup>
                <col class="cost-import-col-summary" />
                <col class="cost-import-col-amt" />
                <col class="cost-import-col-proj" />
                <col class="cost-import-col-note" />
              </colgroup>
              <thead><tr><th>発注名（品名・項目など）</th><th>金額（税抜）</th><th>登録する案件</th><th>備考（任意）</th></tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </div>
          ${
            hasUnmatchedVendor
              ? `<p class="cost-import-hint-msg">取引先がマスタ未登録です。内容を確認のうえ「それでも登録する」で登録できます。</p>`
              : ""
          }
        </div>
      </div>
      <div class="composer-foot">
        ${footApply}
      </div>
    </aside>`;
  }

  return "";
}

/** 原価の発注先プルダウンの候補＝パートナーマスタの会社だけ（2026-10 要望）。
 *  以前はクライアントと過去の原価の発注先名も出しており、それを選ぶとパートナーマスタと一致せず
 *  支払期限・支払方法・源泉の自動入力が効かなかった。未登録の発注先は先にパートナー登録する運用。
 *  既存の原価に入っている名前は costVendorSelectOptionsHtml が選択中の値として残す（編集で消えない） */
function costVendorCandidateLabels() {
  const labels = new Set();
  for (const pt of uiState.partnersCache || []) {
    if (pt?.sales_hidden) continue; // 営業に非表示のパートナーは候補から除外（選択中の値は下流で残す）
    const disp = partnerMasterDisplayLabel(pt) || String(pt?.name ?? "").trim();
    if (disp) labels.add(disp);
  }
  return [...labels].sort((a, b) => a.localeCompare(b, "ja"));
}

function costVendorSelectOptionsHtml(selectedVendor) {
  const sel = String(selectedVendor ?? "").trim();
  const base = costVendorCandidateLabels();
  if (sel && !base.includes(sel)) base.push(sel);
  base.sort((a, b) => a.localeCompare(b, "ja"));
  let html = `<option value="" ${sel === "" ? "selected" : ""}>選択または検索</option>`;
  for (const v of base) {
    html += `<option value="${escapeHtmlAttr(v)}" ${v === sel ? "selected" : ""}>${escapeHtml(v)}</option>`;
  }
  return html;
}

function renderCostForm() {
  if (!uiState.costForm) return "";
  const f = uiState.costForm;
  const isEdit = Boolean(f.editingId);

  const categoryOptions = COST_CATEGORIES
    .map((c) => `<option value="${c}" ${f.category === c ? "selected" : ""}>${c}</option>`)
    .join("");

  const projVal = f.requireProject ? f.projectId || "" : f.projectId || COST_UNASSIGNED_PROJECT_UI_ID;
  const formBoxOwners = costBoxPickCandidateOwners(false);
  const eligProjects = excludeCandidateCostBoxProjects(projectsForCostAllocationAssignPick(), formBoxOwners, projVal);
  // 原価割当（営業）から開いたフォームは計上先の案件が必須（未割当・販管費・調整の入れ先は出さない）
  const costProjectPickHead = f.requireProject
    ? `<option value="" ${projVal === "" ? "selected" : ""}>（計上先の案件を選択）</option>`
    : `<option value="${escapeHtmlAttr(COST_UNASSIGNED_PROJECT_UI_ID)}" ${projVal === COST_UNASSIGNED_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_UNASSIGNED_PROJECT_LABEL)}</option>` +
      `<option value="${escapeHtmlAttr(COST_SALES_EXPENSE_PROJECT_UI_ID)}" ${projVal === COST_SALES_EXPENSE_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_SALES_EXPENSE_PROJECT_LABEL)}</option>` +
      `<option value="${escapeHtmlAttr(COST_ADJUSTMENT_PROJECT_UI_ID)}" ${projVal === COST_ADJUSTMENT_PROJECT_UI_ID ? "selected" : ""}>${escapeHtml(COST_ADJUSTMENT_PROJECT_LABEL)}</option>`;
  const costProjectPickOptions =
    costProjectPickHead +
    costBoxPickOptionsHtml(projVal, formBoxOwners) +
    costProjectListOptgroupHtml(
      eligProjects
        .map(
          (p) =>
            `<option value="${escapeHtmlAttr(p.id)}" ${p.id === projVal ? "selected" : ""} data-option-search="${escapeHtmlAttr(projectAccountingPickSearchBlob(p))}">${escapeHtml(projectAccountingPickLabel(p))}</option>`
        )
        .join("")
    );
  // 仮原価（営業が入れる見込み）では、経理だけが扱う欄（支払状況・請求日・支払日・請求書No）を出さない（2026-10 要望）。
  // 支払期限・支払方法・源泉徴収区分はパートナーマスタから自動で入れ、営業が見て直せるように出す（2026-10 要望）。
  // 源泉は board と同じく区分を選び、源泉所得税は保存時に区分と金額から計算する
  const dueDateField = costFormShowsDueDate(f) ? `
    <label>支払期限（任意）
      <input data-cost-field="dueDate" type="date" value="${f.dueDate || ""}" />
    </label>
  ` : "";

  const pdfDlRaw = f.sourcePdfDocId;
  const pdfDlN = pdfDlRaw != null && pdfDlRaw !== "" ? Number(pdfDlRaw) : NaN;
  const pdfDlHtml =
    Number.isFinite(pdfDlN) && pdfDlN > 0 && authToken
      ? `<div class="cost-form-source-pdf">
          <span class="cost-form-source-pdf-label">取込PDF</span>
          <button type="button" class="inline-btn ghost" data-cost-download-source-pdf="${pdfDlN}">ダウンロード</button>
        </div>`
      : "";

  // 原価割当からの編集は 計上先案件・会計区分・備考 のみ変更可。
  // 金額・支払先・日付などは表示のみ（変更は原価登録から）。state は初期値のまま送るので値は変わらない
  // ※例外: 経理からの差し戻し修正（remandFix）は金額等を直すのが目的なのでフル編集フォームを出す
  if (isEdit && currentRoute().page === "cost-allocation" && !f.remandFix) {
    const taxYen = Math.round(Number(f.consumptionTax ?? 0)) || 0;
    const exYen = Number(String(f.amount ?? 0).toString().replace(/,/g, "")) || 0;
    const roItem = costReadonlySummaryItemHtml;
    return `
    <div class="composer-backdrop" data-close-cost-form="1"></div>
    <aside class="composer-panel project-create-panel cost-edit-side-panel">
      <div class="composer-head">
        <div><h3>原価を割り当て</h3><p>金額・発注先などの変更は「原価一覧」から行えます</p></div>
        <button class="inline-btn ghost" data-close-cost-form="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="cost-alloc-ro-summary">
          ${roItem("発注先", f.vendor)}
          ${roItem("金額（税抜）", yen.format(exYen))}
          ${roItem("消費税", yen.format(taxYen))}
          ${roItem("計上月", f.targetMonth)}
          ${roItem("請求日", f.invoiceDate)}
          ${roItem("支払期限", f.dueDate)}
          ${roItem("支払日", f.paidDate)}
        </div>
        <div class="form-grid cost-form-grid">
          <label>計上先案件
            <select class="pj-input" data-cost-field="projectId">${costProjectPickOptions}</select>
          </label>
          <label>会計区分
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="category">${categoryOptions}</select>
          </label>
          ${costFormNoteMemoFieldsHtml(f)}
          ${pdfDlHtml}
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-cost-form="1">キャンセル</button>
        <button class="inline-btn" data-save-cost="${f.projectId}" ${f._submitting ? "disabled" : ""}>${f._submitting ? "保存中…" : "更新する"}</button>
      </div>
    </aside>`;
  }

  return `
    <div class="composer-backdrop" data-close-cost-form="1"></div>
    <aside class="composer-panel project-create-panel cost-edit-side-panel">
      <div class="composer-head">
        <div><h3>${f.remandFix ? "差し戻された原価を修正" : isEdit ? "原価を編集" : costFormNeedsOrderApply(f) ? "原価の発注申請" : "原価を追加"}</h3><p>${f.remandFix ? "修正して保存すると差し戻しは解消され、経理に伝わります" : costFormNeedsOrderApply(f) ? "制作費・開発費は、外注先に頼む前に承認が必要です" : "売上とは別に原価を管理します"}</p></div>
        <button class="inline-btn ghost" data-close-cost-form="1">閉じる</button>
      </div>
      <div class="composer-body">
        ${f.remandFix && f.remandNote ? `<div class="cost-remand-banner">経理からの差し戻し理由: ${escapeHtml(f.remandNote)}</div>` : ""}
        <div class="form-grid cost-form-grid">
          ${(f.isProvisional || f.provisionalContext) && !costFormNeedsOrderApply(f) && costUserSkipsOrderApproval() ? `<label class="cost-form-provisional-row" style="grid-column:1/-1;display:flex;align-items:center;gap:8px;font-weight:500;">
            <input type="checkbox" data-cost-provisional-toggle="1" ${f.isProvisional ? "checked" : ""} style="width:16px;height:16px;" />
            仮原価（見込み）として登録
          </label>` : ""}
          <label>計上先案件
            <select class="pj-input" data-cost-field="projectId"${f.remandFix ? " disabled" : ""}>${costProjectPickOptions}</select>
          </label>
          <label>会計区分
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="category">${categoryOptions}</select>
          </label>
          <label>発注ステータス
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="orderStatus">
              <option value="" ${!f.orderStatus ? "selected" : ""}>未設定（支払状況から自動表示）</option>
              ${COST_ORDER_STATUSES.map((s) => `<option value="${s}" ${f.orderStatus === s ? "selected" : ""}>${s}</option>`).join("")}
            </select>
          </label>
          ${f.isProvisional ? "" : costFormPayStatusFieldHtml(f)}
          <label><span>発注先（会社名・サービス名） <span class="cl-form-required">*</span></span>
            <select class="pj-input" data-cost-field="vendor">${costVendorSelectOptionsHtml(f.vendor)}</select>
            ${costPartnerTermsHintHtml(f.vendor)}
            ${costOrderNewPartnerHtml(f)}
          </label>
          <label><span>金額（税抜） <span class="cl-form-required">*</span></span>
            <input data-cost-field="amount" inputmode="numeric" value="${costFormYenInputValue(f.amount, "")}" placeholder="例: 100,000" />
          </label>
          <label>消費税（金額から10%を自動計算・手修正可）
            <input data-cost-field="consumptionTax" inputmode="numeric" value="${costFormYenInputValue(f.consumptionTax, "0")}" placeholder="例: 10,000" />
          </label>
          <label><span>計上月 <span class="cl-form-required">*</span></span>
            <input class="pj-input pj-input-month" data-show-picker="1" data-search-dropdown-off="1" data-cost-field="targetMonth" type="month" value="${(f.targetMonth || "").replace("/", "-")}" title="計上月" />
          </label>
          ${f.isProvisional ? "" : `
          <label>請求日（任意）
            <input data-cost-field="invoiceDate" type="date" value="${f.invoiceDate || ""}" />
          </label>`}
          ${dueDateField}
          ${f.isProvisional ? "" : `
          <label>支払日（任意・入力すると支払済）
            <input data-cost-field="paidDate" type="date" value="${f.paidDate || ""}" />
          </label>`}
          <label>支払方法
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="paymentMethod">
              <option value="" ${!f.paymentMethod ? "selected" : ""}>未設定</option>
              ${COST_PAYMENT_METHODS.map((m) => `<option value="${m}" ${f.paymentMethod === m ? "selected" : ""}>${m}</option>`).join("")}
            </select>
          </label>
          ${f.isProvisional ? "" : `
          <label>請求書No（先方請求書の番号）
            <input data-cost-field="invoiceNo" value="${escapeHtmlAttr(f.invoiceNo || "")}" placeholder="INV-0001" autocomplete="off" />
          </label>`}
          ${f.isProvisional ? `
          <label>源泉徴収区分
            <select class="pj-input" data-search-dropdown-off="1" data-cost-field="withholdingKind">
              ${f.withholdingKind || !Math.round(Number(f.withholdingTax) || 0) ? "" : `<option value="" selected>登録済みの金額のまま（${escapeHtml(yen.format(Math.round(Number(f.withholdingTax) || 0)))}）</option>`}
              ${WITHHOLDING_TAX_KINDS.map((k) => `<option value="${k}" ${(f.withholdingKind || (Math.round(Number(f.withholdingTax) || 0) ? "" : "なし")) === k ? "selected" : ""}>${k}</option>`).join("")}
            </select>
          </label>` : `
          <label>源泉所得税（円）
            <span class="cost-withholding-row">
              <input data-cost-field="withholdingTax" inputmode="numeric" value="${f.withholdingTax || 0}" />
              <button type="button" class="inline-btn ghost cost-withholding-calc" data-cost-withholding-calc="1" title="税抜金額の10.21%（100万円超の部分は20.42%）を自動入力">自動計算</button>
            </span>
          </label>`}
          ${f.editingId ? `<label>登録者
            <input value="${escapeHtmlAttr(costCreatorLabel(f.createdByUserDbId))}" disabled title="この原価を登録した人（変更不可）" />
          </label>` : ""}
          ${/* 発注申請欄は発注先を選ぶと T番号欄が増減するので、上の入力欄がずれないよう最後（備考の上）に置く */ ""}
          ${costOrderApplyFieldsHtml(f)}
          ${costFormNoteMemoFieldsHtml(f)}
          ${pdfDlHtml}
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-cost-form="1">キャンセル</button>
        <button class="inline-btn" data-save-cost="${f.projectId}" ${f._submitting ? "disabled" : ""}>${f._submitting ? "保存中…" : (isEdit ? "更新する" : costFormNeedsOrderApply(f) ? "発注申請する" : "追加する")}</button>
      </div>
    </aside>
  `;
}

function renderToast() {
  const t = uiState.toast;
  if (!t || !String(t.message || "").trim()) return "";
  const esc = escapeHtml(t.message).replace(/\n/g, "<br>");
  const err = t.variant === "error";
  const cls = err ? "toast toast--error" : "toast toast--info";
  const dismiss = t.sticky
    ? `<button type="button" class="toast-dismiss" data-dismiss-toast="1" aria-label="閉じる">×</button>`
    : "";
  return `<div class="${cls}" role="${err ? "alert" : "status"}">${dismiss}<div class="toast-body">${esc}</div></div>`;
}

function renderProjectCreateModal() {
  if (!uiState.projectCreate) return "";
  const m = uiState.projectCreate;
  const selectedClient = m.form.clientId ? clients.find((c) => c.id === m.form.clientId) : null;
  const clientSearchTrim = (m.form.clientQuery || "").trim();
  const clientCandidates = projectCreateClientCandidates(clientSearchTrim);
  const clientListHtml = !clientSearchTrim
    ? `<p class="project-create-candidate-hint" role="status">文字を入力すると候補が表示されます</p>`
    : clientCandidates.length === 0
      ? `<p class="project-create-candidate-hint" role="status">該当するクライアントがありません</p>`
      : clientCandidates
          .map(
            (c, idx) =>
              `<button type="button" class="picker-option ${m.form.clientId === c.id ? "active" : ""} ${m.form.clientHighlight === idx ? "focus" : ""}" data-select-client="${escapeHtmlAttr(c.id)}" role="option" aria-selected="${m.form.clientId === c.id ? "true" : "false"}">
                  <strong>${escapeHtml(c.name)}</strong>
                  <small>${escapeHtml(c.contactName || "担当未設定")}${c.ndaStatus === "済" ? " ・ NDA済" : ""}${c.contractStatus === "済" ? " ・ 契約済" : ""}</small>
                </button>`
          )
          .join("");

  const step1 = `
    <div class="project-create-step project-create-step1">
      <h3>クライアントを選択</h3>
      <p class="project-create-step1-lead">既存クライアントを検索して選ぶか、未登録なら下の「新しいクライアントを登録」から追加してください。案件名を入れて作成し、その他の詳細は作成後に開く案件詳細の「基本情報」で入力します。</p>
      <div class="project-create-lane project-create-lane--search">
        <div class="project-create-lane-head">
          <span class="project-create-lane-eyebrow" aria-hidden="true">A</span>
          <div>
            <div class="project-create-lane-title">クライアント名で検索</div>
            <p class="project-create-lane-desc">1文字以上入力すると候補が一覧に出ます</p>
          </div>
        </div>
        <div class="client-picker project-create-search-wrap">
          <input data-client-search="1" value="${escapeHtmlAttr(m.form.clientQuery || "")}" placeholder="会社名・略称名で検索" class="search project-create-search-input" autocomplete="off" />
        </div>
        <div class="client-picker-list project-create-candidate-list" role="listbox" aria-label="クライアント候補">
          ${clientListHtml}
        </div>
      </div>
      <div class="project-create-lane project-create-lane--new">
        <div class="project-create-lane-head">
          <span class="project-create-lane-eyebrow project-create-lane-eyebrow--new" aria-hidden="true">B</span>
          <div>
            <div class="project-create-lane-title">新しいクライアントを追加</div>
            <p class="project-create-lane-desc">マスタにまだいない取引先の場合はここから登録</p>
          </div>
        </div>
        <button type="button" class="project-create-new-client-btn" data-open-client-create="1">
          <span class="project-create-new-client-btn-icon" aria-hidden="true">＋</span>
          <span>
            <span class="project-create-new-client-btn-label">新しいクライアントを登録</span>
            <span class="project-create-new-client-btn-sub">登録後、自動でこの案件用に選ばれます</span>
          </span>
        </button>
      </div>
      ${selectedClient ? `
        <div class="selected-client-card project-create-selected">
          <div class="project-create-selected-title">選択中</div>
          <div class="project-create-selected-name">${escapeHtml(selectedClient.name)}</div>
          <div class="meta"><span>${escapeHtml(selectedClient.contactName || "—")}</span><span>${escapeHtml(selectedClient.tel || "")}</span><span>${escapeHtml(selectedClient.email || "")}</span></div>
        </div>` : ""}
      ${/* 案件名はクライアントの選択と関係なく常に出す（選択時は選択カードだけ部分更新するため、選択時に出す作りだと表示されない） */ ""}
      <label class="project-create-name">
        <span>案件名 <span class="cl-form-required">*</span></span>
        <input class="pj-input" data-project-create-name="1" value="${escapeHtmlAttr(m.form.name || "")}" placeholder="例：〇〇サイト リニューアル" autocomplete="off" />
      </label>
    </div>
  `;

  // 既にモーダルがDOMに存在する場合はスライドアニメーションを再生しない
  const _alreadyOpen = !!document.querySelector("aside.composer-panel.project-create-modal");
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-project-create="1"></div>
    <aside class="composer-panel project-create-modal${_alreadyOpen ? " fp-drawer-no-enter" : ""}" role="dialog" aria-modal="true" aria-labelledby="project-create-title">
      <div class="composer-head">
        <div><h3 id="project-create-title">新しい案件を作成</h3></div>
        <button class="inline-btn ghost" data-close-project-create="1" type="button">閉じる</button>
      </div>
      <div class="composer-body">
        ${step1}
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-project-create="1">キャンセル</button>
        <button class="inline-btn" data-project-create="1"${!m.form.clientId || m._submitting ? " disabled" : ""}${m._submitting ? ' aria-busy="true"' : ""}>${m._submitting ? "作成中…" : "案件を作成して詳細へ"}</button>
      </div>
    </aside>
  `;
}

/** ToDo管理: グループ新規登録 */
function renderProgressTodoGroupCreateModal() {
  if (!uiState.progressShowTodoGroupForm) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  if (String(route.searchParams?.get("group") || "").trim() || String(route.searchParams?.get("project") || "").trim()) {
    return "";
  }
  const d = uiState.progressTodoGroupCreateDraft || { title: "", body: "" };
  const titleVal = escapeHtmlAttr(d.title || "");
  const bodyVal = escapeHtml(d.body || "");
  const busy = Boolean(uiState.progressTodoGroupModalSubmitting);
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-todo-group-create="1"${busy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-todo-group-create-modal${spNoEnter("pm-todo-group-create-modal")}" role="dialog" aria-modal="true" aria-labelledby="pm-todo-group-create-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-todo-group-create-title">${PM_WORD.project}を登録</h3>
          <p class="project-create-step2-sub">例: 新REM（${PM_WORD.theme}・${PM_WORD.issue}をまとめる単位）</p>
        </div>
        <button class="inline-btn ghost" data-close-pm-todo-group-create="1" type="button"${busy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body">
        <div class="client-form-section project-create-step2-block">
          <label class="client-form-row">
            <span class="client-form-row-label">${PM_WORD.project}名 <span class="client-required">（必須）</span></span>
            <input type="text" class="pj-input" data-pm-modal-todo-group-title value="${titleVal}" placeholder="例: 新REM" maxlength="500" />
          </label>
          <label class="client-form-row">
            <span class="client-form-row-label">種別（任意）</span>
            ${progressTodoGroupCategorySelectHtml(d.category, "data-pm-modal-todo-group-category")}
          </label>
          <label class="client-form-row">説明（任意）<textarea class="pj-input" data-pm-modal-todo-group-body rows="3" placeholder="プロジェクトの概要など">${bodyVal}</textarea></label>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-todo-group-create="1" type="button"${busy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-todo-group-modal="1" type="button"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "登録中…" : "登録する"}</button>
      </div>
    </aside>
  `;
}

function renderProgressTodoGroupEditModal() {
  if (!uiState.progressShowTodoGroupEditForm) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  const d = uiState.progressTodoGroupEditDraft || {
    groupId: "",
    title: "",
    body: "",
    challengeIds: [],
    themeFilter: ""
  };
  const titleVal = escapeHtmlAttr(d.title || "");
  const bodyVal = escapeHtml(d.body || "");
  const busy = Boolean(uiState.progressTodoGroupModalSubmitting);
  const themePicker = progressTodoGroupEditThemePickerHtml(d.groupId, d);
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-todo-group-edit="1"${busy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-todo-group-edit-modal" role="dialog" aria-modal="true" aria-labelledby="pm-todo-group-edit-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-todo-group-edit-title">${PM_WORD.project}を編集</h3>
        </div>
        <button class="inline-btn ghost" data-close-pm-todo-group-edit="1" type="button"${busy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body pm-todo-group-edit-body">
        <div class="client-form-section project-create-step2-block pm-todo-group-edit-fields">
          <label class="client-form-row">
            <span class="client-form-row-label">${PM_WORD.project}名 <span class="client-required">（必須）</span></span>
            <input type="text" class="pj-input" data-pm-modal-todo-group-edit-title value="${titleVal}" placeholder="例: 新REM" maxlength="500" />
          </label>
          <label class="client-form-row">
            <span class="client-form-row-label">種別（任意）</span>
            ${progressTodoGroupCategorySelectHtml(d.category, "data-pm-modal-todo-group-edit-category")}
          </label>
          <label class="client-form-row">説明（任意）<textarea class="pj-input" data-pm-modal-todo-group-edit-body rows="3" placeholder="説明・概要など">${bodyVal}</textarea></label>
        </div>
        ${themePicker}
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-todo-group-edit="1" type="button"${busy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-todo-group-edit="1" type="button"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "保存中…" : "保存する"}</button>
      </div>
    </aside>
  `;
}

/** ToDo管理: テーマ新規登録（案件作成モーダルと同系のパネル） */
function renderProgressChallengeCreateModal() {
  if (!uiState.progressShowProjectForm) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  if (String(route.searchParams?.get("project") || "").trim()) return "";
  const d = uiState.progressChallengeCreateDraft || { title: "", body: "", genre: "" };
  const titleVal = escapeHtmlAttr(d.title || "");
  const bodyVal = escapeHtml(d.body || "");
  const chBusy = Boolean(uiState.progressChallengeModalSubmitting);
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-challenge-create="1"${chBusy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-challenge-create-modal${spNoEnter("pm-challenge-create-modal")}" role="dialog" aria-modal="true" aria-labelledby="pm-challenge-create-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-challenge-create-title">${PM_WORD.theme}を登録</h3>
          <p class="project-create-step2-sub">${PM_WORD.issue}ボードで管理する単位です</p>
        </div>
        <button class="inline-btn ghost" data-close-pm-challenge-create="1" type="button"${chBusy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body">
        <div class="client-form-section project-create-step2-block">
          <label class="client-form-row">
            <span class="client-form-row-label">タイトル <span class="client-required">（必須）</span></span>
            <input type="text" class="pj-input" data-pm-modal-challenge-title value="${titleVal}" placeholder="例: 〇〇のサイト改修" maxlength="500" />
          </label>
          <label class="client-form-row">
            <span class="client-form-row-label">種別（任意）</span>
            ${progressTodoGroupCategorySelectHtml(d.genre, "data-pm-modal-challenge-genre")}
          </label>
          <label class="client-form-row">概要・ゴール（任意）<textarea class="pj-input" data-pm-modal-challenge-body rows="3" placeholder="概要・ゴールなど">${bodyVal}</textarea></label>
          ${progressChallengeMembersFieldHtml(d, "create")}
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-challenge-create="1" type="button"${chBusy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-challenge-modal="1" type="button"${chBusy ? " disabled aria-busy=\"true\"" : ""}>${chBusy ? "登録中…" : "登録する"}</button>
      </div>
    </aside>
  `;
}

/** ToDo管理: テーマ編集（一覧・課題ボードから） */
function renderProgressChallengeEditModal() {
  if (!uiState.progressShowChallengeEditForm) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  const d = uiState.progressChallengeEditDraft || { challengeId: "", title: "", body: "", genre: "" };
  const titleVal = escapeHtmlAttr(d.title || "");
  const bodyVal = escapeHtml(d.body || "");
  const busy = Boolean(uiState.progressChallengeModalSubmitting);
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-challenge-edit="1"${busy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-challenge-edit-modal${spNoEnter("pm-challenge-edit-modal")}" role="dialog" aria-modal="true" aria-labelledby="pm-challenge-edit-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-challenge-edit-title">${PM_WORD.theme}を編集</h3>
          <p class="project-create-step2-sub">保存すると一覧のタイトル・説明と${PM_WORD.issue}ボードの表示が更新されます</p>
        </div>
        <button class="inline-btn ghost" data-close-pm-challenge-edit="1" type="button"${busy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body">
        <div class="client-form-section project-create-step2-block">
          <label class="client-form-row">
            <span class="client-form-row-label">タイトル <span class="client-required">（必須）</span></span>
            <input type="text" class="pj-input" data-pm-modal-challenge-edit-title value="${titleVal}" placeholder="例: 〇〇のサイト改修" maxlength="500" />
          </label>
          <label class="client-form-row">
            <span class="client-form-row-label">種別（任意）</span>
            ${progressTodoGroupCategorySelectHtml(d.genre, "data-pm-modal-challenge-edit-genre")}
          </label>
          <label class="client-form-row">説明（任意）<textarea class="pj-input" data-pm-modal-challenge-edit-body rows="3" placeholder="説明・概要など">${bodyVal}</textarea></label>
          ${progressChallengeMembersFieldHtml(d, "edit")}
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-challenge-edit="1" type="button"${busy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-challenge-edit="1" type="button"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "保存中…" : "保存する"}</button>
      </div>
    </aside>
  `;
}

function closeProgressChallengeWikiModal() {
  uiState.progressShowChallengeWiki = false;
  uiState.progressChallengeWikiDraft = { challengeId: "", wikiBody: "" };
  uiState.progressChallengeWikiSubmitting = false;
}

function closeProgressChallengeSettingsModal() {
  uiState.progressShowChallengeSettings = false;
  uiState.progressChallengeSettingsDraft = {
    challengeId: "",
    tab: "categories",
    statuses: [],
    categories: [],
    newStatus: "",
    newCategory: "",
    basicTitle: "",
    basicBody: "",
    projectId: "",
    clientPick: "",
    workKind: ""
  };
  uiState.progressChallengeSettingsSubmitting = false;
}

function openProgressChallengeWikiModal(ch) {
  if (!ch) return;
  uiState.progressShowChallengeSettings = false;
  closeProgressChallengeSettingsModal();
  uiState.progressShowChallengeWiki = true;
  uiState.progressChallengeWikiDraft = {
    challengeId: String(ch.id),
    wikiBody: String(ch.wikiBody || "")
  };
}

function openProgressChallengeSettingsModal(ch, tab) {
  if (!ch) return;
  uiState.progressShowChallengeWiki = false;
  closeProgressChallengeWikiModal();
  uiState.progressShowChallengeSettings = true;
  uiState.progressChallengeSettingsDraft = {
    challengeId: String(ch.id),
    tab: PM_CHALLENGE_SETTINGS_TABS.includes(tab) ? tab : "categories",
    statuses: [...progressChallengeStatuses(ch)],
    // 工数を計測する状態（null=全状態を計測）
    trackedStatuses: Array.isArray(ch.timeTrackedStatuses)
      ? ch.timeTrackedStatuses.map((s) => String(s || "").trim()).filter(Boolean)
      : null,
    // 必須工程（前進時にスキップできない状態。null/空=必須指定なし）
    requiredSteps: Array.isArray(ch.requiredSteps)
      ? ch.requiredSteps.map((s) => String(s || "").trim()).filter(Boolean)
      : null,
    categories: [...progressChallengeCategories(ch)],
    agendaTemplates: progressAgendaTemplatesDraftFrom(ch),
    newStatus: "",
    newCategory: "",
    basicTitle: String(ch.title || ""),
    basicBody: String(ch.body || ""),
    projectId: ch.projectId != null ? String(ch.projectId) : "",
    // クライアント欄の初期値＝紐づけ済み案件のクライアント（アクティブ一覧から導出。無ければ空）
    clientPick: (() => {
      const p = (Array.isArray(projects) ? projects : []).find((x) => Number(x.dbId) === Number(ch.projectId));
      const cid = p ? Number(String(p.clientId || "").replace(/^c/, "")) : NaN;
      return Number.isFinite(cid) && cid >= 1 ? String(cid) : "";
    })(),
    workKind: ch.workKind === "internal" ? "internal" : "",
    // Backlog連携タブの入力中の接続先・キー（保存はタブ内の専用ボタン・15z-backlog-import.js）
    backlogConnection: String(ch.backlogConnection || ""),
    backlogProjectKey: String(ch.backlogProjectKey || ""),
    backlogNotifyUserIds: Array.isArray(ch.backlogNotifyUserIds) ? [...ch.backlogNotifyUserIds] : [],
    backlogNewIssueNotifyUserIds: Array.isArray(ch.backlogNewIssueNotifyUserIds) ? [...ch.backlogNewIssueNotifyUserIds] : [],
    // 参加メンバー（メンバータブで触った時だけ保存に載せる＝membersTouched）
    memberMode: Array.isArray(ch.memberUserIds) ? "selected" : "all",
    memberUserIds: Array.isArray(ch.memberUserIds) ? [...ch.memberUserIds] : null,
    membersTouched: false
  };
}


function progressChallengeSettingsListEditorHtml(kind, draft) {
  const isStatus = kind === "statuses";
  const key = isStatus ? "statuses" : "categories";
  const list = Array.isArray(draft?.[key]) ? draft[key].map((s) => String(s || "").trim()).filter(Boolean) : [];
  const ch = findProgressChallenge(draft?.challengeId);
  const counts = new Map();
  if (ch) {
    for (const a of ch.agendas || []) {
      if (a.archivedAt) continue;
      const v = isStatus
        ? normalizeProgressTodoStatusForChallenge(a.status, ch)
        : normalizeProgressAgendaCategory(a.category);
      counts.set(v, (counts.get(v) || 0) + 1);
    }
  }
  const rows = list.length
    ? list
        .map((name, i) => {
          const n = counts.get(name) || 0;
          const mod = isStatus ? progressKanColModifier(name) : "";
          const nameField = isStatus ? "data-pm-challenge-settings-status-name" : "data-pm-challenge-settings-category-name";
          const lockedDone = isStatus && (name === "完了" || name === "未対応" || name === "対応中");
          const statusNameCls = isStatus
            ? ` pm-challenge-settings-list-name--status${lockedDone ? " pm-challenge-settings-list-name--locked" : ""}`
            : "";
          const nameInputAttrs = `${nameField}="${i}" value="${escapeHtmlAttr(name)}" maxlength="80" aria-label="${isStatus ? "状況名" : "カテゴリ名"}"${
            lockedDone ? " readonly tabindex=\"-1\"" : ""
          }`;
          const namePart = `<input type="text" class="pj-input pm-challenge-settings-list-name${statusNameCls}" ${nameInputAttrs} />`;
          const nameCell = isStatus
            ? `<div class="pm-challenge-settings-list-status-cell pm-challenge-settings-list-status-cell--${mod}${lockedDone ? " is-locked-done" : ""}">${namePart}</div>`
            : namePart;
          const reorderHandle =
            `<span class="pm-challenge-settings-drag-handle" data-pm-challenge-settings-drag-handle="1" draggable="true" title="ドラッグして並べ替え" role="button" tabindex="0" aria-label="ドラッグして並べ替え">
            <span class="pm-challenge-settings-drag-handle__bars" aria-hidden="true"></span>
          </span>`;
          const canRemoveStatus = !(isStatus && (name === "完了" || name === "未対応" || name === "対応中"));
          const removeBtn = canRemoveStatus
            ? `<button type="button" class="pm-challenge-settings-list-remove" data-pm-challenge-settings-remove-${key}="${i}" aria-label="削除">×</button>`
            : `<span class="pm-challenge-settings-list-remove-slot" aria-hidden="true"></span>`;
          // 工数を数える状態のチェック（未設定 null は全状態計測＝チェック済み扱い）
          const trackedArr = Array.isArray(draft?.trackedStatuses) ? draft.trackedStatuses : null;
          const trackCell = isStatus
            ? `<label class="pm-challenge-settings-track" title="チェックした状況だけ、課題詳細の進捗バーで工数（経過時間）を計測します"><input type="checkbox" data-pm-challenge-settings-track="${i}"${!trackedArr || trackedArr.includes(name) ? " checked" : ""} />工数</label>`
            : "";
          // 必須工程のチェック（未設定 null/空は「必須指定なし」＝未チェック扱い）
          const requiredArr = Array.isArray(draft?.requiredSteps) ? draft.requiredSteps : null;
          const requiredCell = isStatus
            ? `<label class="pm-challenge-settings-required" title="チェックした状況は、前工程からスキップして進めなくなります（差し戻しは自由に戻れます）"><input type="checkbox" data-pm-challenge-settings-required="${i}"${requiredArr && requiredArr.includes(name) ? " checked" : ""} />必須</label>`
            : "";
          return `<li class="pm-challenge-settings-list-row" data-pm-challenge-settings-sort-row="${key}" data-pm-sort-index="${i}">
            ${reorderHandle}
            ${nameCell}
            <span class="pm-challenge-settings-list-count">（${n}）</span>
            ${trackCell}
            ${requiredCell}
            ${removeBtn}
          </li>`;
        })
        .join("")
    : `<li class="pm-challenge-settings-list-empty">まだ登録がありません（保存時はアプリ既定を使用します）</li>`;
  const newField = isStatus ? "newStatus" : "newCategory";
  const newVal = escapeHtmlAttr(String(draft?.[newField] || ""));
  const addLabel = isStatus ? "状況を追加" : "カテゴリを追加";
  const placeholder = isStatus ? "例: 確認待ち" : "例: バグ";
  return `
    <div class="pm-challenge-settings-list-editor">
      <ul class="pm-challenge-settings-list">${rows}</ul>
      <div class="pm-challenge-settings-add-row">
        <span class="pm-challenge-settings-add-spacer" aria-hidden="true"></span>
        <input type="text" class="pj-input" data-pm-challenge-settings-new="${key}" value="${newVal}" placeholder="${escapeHtmlAttr(placeholder)}" maxlength="80" />
        <button type="button" class="inline-btn" data-pm-challenge-settings-add="${key}">${addLabel}</button>
      </div>
      ${isStatus ? `<ul class="pm-challenge-settings-note pm-challenge-settings-note--list">
        <li>課題ボードの列は上から順に表示されます。左の「≡」をドラッグして順番を入れ替えられます。名称は直接編集できます。</li>
        <li>「未対応」「対応中」「完了」は名前の変更および削除ができません。</li>
        <li>リストから削除した状況の課題はボードに「未設定」列として表示されるので、他の列へドラッグして移してください。</li>
        <li>「工数」にチェックした状況だけ、課題詳細の進捗バーで経過時間（工数）を計測・表示します。</li>
        <li>「必須」にチェックした状況は、前工程からスキップして進めなくなります（差し戻しで戻るのは自由です）。</li>
        <li>種別が「制作・PJ進行」の${PM_WORD.theme}では、この状況リストがそのまま進捗ステップバーの工程になります。</li>
      </ul>` : `<p class="pm-challenge-settings-note">課題のカテゴリ選択肢として使われます。「その他」は一覧では末尾に並びます。左の「≡」をドラッグして順番を入れ替えられます。</p>`}
    </div>
  `;
}

function renderProgressChallengeWikiModal() {
  if (!uiState.progressShowChallengeWiki) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  const d = uiState.progressChallengeWikiDraft || { challengeId: "", wikiBody: "" };
  const ch = findProgressChallenge(d.challengeId);
  if (!ch) return "";
  const busy = Boolean(uiState.progressChallengeWikiSubmitting);
  const bodyVal = escapeHtml(d.wikiBody || "");
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-challenge-wiki="1"${busy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-challenge-wiki-modal${spNoEnter("pm-challenge-wiki-modal")}" role="dialog" aria-modal="true" aria-labelledby="pm-challenge-wiki-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-challenge-wiki-title">${PM_WORD.theme}詳細</h3>
          <p class="project-create-step2-sub">${escapeHtml(ch.title || "（無題）")}</p>
        </div>
        <button class="inline-btn ghost" type="button" data-close-pm-challenge-wiki="1"${busy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body">
        <textarea class="pj-input pm-challenge-wiki-ta" data-pm-challenge-wiki-body rows="18" aria-label="${PM_WORD.theme}詳細" placeholder="背景、要件、決定事項、参考URL など">${bodyVal}</textarea>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-challenge-wiki="1" type="button"${busy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-challenge-wiki="1" type="button"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "保存中…" : "保存する"}</button>
      </div>
    </aside>
  `;
}

/** テーマ設定「基本」タブ: クライアント選択肢（アクティブ案件を持つクライアントをprojectsから集約）。
 *  値は fp_clients の id。projects 側の clientId は "c<id>" 形式なので数値へ剥がして揃える。 */
function pmThemeClientChoices() {
  const map = new Map();
  for (const p of Array.isArray(projects) ? projects : []) {
    const cid = Number(String(p.clientId || "").replace(/^c/, ""));
    if (!Number.isFinite(cid) || cid < 1) continue;
    const name = String(p.client || "").trim();
    if (!map.has(cid)) map.set(cid, { id: cid, name: name || `クライアント #${cid}`, projects: [] });
    map.get(cid).projects.push(p);
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "ja"));
}

function renderProgressChallengeSettingsModal() {
  if (!uiState.progressShowChallengeSettings) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  const d = uiState.progressChallengeSettingsDraft || {};
  const ch = findProgressChallenge(d.challengeId);
  if (!ch) return "";
  const busy = Boolean(uiState.progressChallengeSettingsSubmitting);
  const archivedAgendaCount = (Array.isArray(ch.agendas) ? ch.agendas : []).filter((a) => a && a.archivedAt).length;
  const tab = PM_CHALLENGE_SETTINGS_TABS.includes(d.tab) ? d.tab : "categories";
  const tabBtn = (id, label) => {
    const on = tab === id;
    return `<button type="button" class="pm-challenge-settings-tab${on ? " is-active" : ""}" data-pm-cset-tab="${id}" data-pm-challenge-settings-tab="${id}" role="tab" aria-selected="${on ? "true" : "false"}">${escapeHtml(label)}</button>`;
  };
  // アーカイブ・Backlog連携タブは共通の「設定を保存」を出さない（Backlog連携はタブ内に専用ボタンがある）
  const ownSaveTab = tab === "archived" || tab === "backlog";
  let body = "";
  if (tab === "categories" || tab === "statuses") {
    body = progressChallengeSettingsListEditorHtml(tab, d);
  } else if (tab === "templates") {
    body = progressAgendaTemplatesTabHtml(d);
  } else if (tab === "members") {
    body = progressChallengeMembersFieldHtml(d, "settings");
  } else if (tab === "backlog") {
    body = progressChallengeBacklogTabHtml(ch, d);
  } else if (tab === "archived") {
    body = `<div class="pm-archived-agendas-tab">
      <p class="pm-challenge-settings-note">アーカイブした課題の一覧です。完了から${PM_DONE_AUTO_ARCHIVE_DAYS}日間更新がない課題も自動的にここへ移動します。復元は起票者のみ行えます。</p>
      ${progressArchivedAgendaListHtml(ch.agendas)}
    </div>`;
  } else {
    const titleVal = escapeHtmlAttr(String(d.basicTitle ?? ""));
    const bodyVal = escapeHtml(String(d.basicBody ?? ""));
    // 工数区分（'' = クライアント案件 / 'internal' = 社内工数）。社内工数ではクライアント欄を出さない
    const wk = String(d.workKind || "") === "internal" ? "internal" : "";
    // クライアントを選ぶと案件は自動で紐づく（そのクライアントの案件が複数ある時だけ案件の選択欄を出す）
    const selPid = String(d.projectId ?? "");
    const clientPick = String(d.clientPick ?? "");
    const clientChoices = pmThemeClientChoices();
    const clientOpts =
      `<option value="">（未設定）</option>` +
      clientChoices
        .map((c) => `<option value="${escapeHtmlAttr(c.id)}"${clientPick === String(c.id) ? " selected" : ""}>${escapeHtml(c.name)}</option>`)
        .join("");
    const clientProjects = clientChoices.find((c) => String(c.id) === clientPick)?.projects || [];
    const projPickRow =
      clientProjects.length > 1
        ? `<label class="client-form-row">案件（このクライアントの案件が複数あります）
          <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-challenge-settings-project>
            <option value=""${selPid === "" ? " selected" : ""}>案件を選択…</option>
            ${clientProjects
              .map((p) => {
                const pid = p.dbId != null ? String(p.dbId) : String(p.id).replace(/^p/, "");
                return `<option value="${escapeHtmlAttr(pid)}"${selPid === pid ? " selected" : ""}>${escapeHtml(p.name || pid)}</option>`;
              })
              .join("")}
          </select>
        </label>`
        : "";
    // 紐づけ済み案件がアクティブ一覧に無い（＝クライアントを導出できない）場合は、触らない限り維持されることを示す
    const keepHint =
      !clientPick && selPid
        ? `<p class="pm-challenge-settings-note">現在の関連案件はそのまま維持されています（クライアントを選び直すと変更されます）。</p>`
        : "";
    const projRow =
      wk === "internal"
        ? ""
        : `<label class="client-form-row">クライアント（任意）
          <select class="pj-input dform-select" data-pm-challenge-settings-client>${clientOpts}</select>
        </label>
        ${projPickRow}${keepHint}`;
    const workKindNote =
      wk === "internal"
        ? `社内工数の${PM_WORD.theme}では、ToDoから作ったカレンダー予定（[TODO:番号] 付き）が自動的に「社内業務」の工数として取り込まれます。`
        : `クライアントを選ぶと、この${PM_WORD.theme}のToDoで記録した工数がそのクライアントの案件の工数として集計され、ToDoから作るカレンダー予定にも [PJ:案件番号] が自動で入ります。`;
    body = `
      <div class="client-form-section project-create-step2-block">
        <label class="client-form-row">
          <span class="client-form-row-label">タイトル <span class="client-required">（必須）</span></span>
          <input type="text" class="pj-input" data-pm-challenge-settings-basic-title value="${titleVal}" placeholder="例: 〇〇のサイト改修" maxlength="500" />
        </label>
        <label class="client-form-row">説明（任意）<textarea class="pj-input" data-pm-challenge-settings-basic-body rows="4" placeholder="説明・概要など">${bodyVal}</textarea></label>
        <label class="client-form-row">工数の区分
          <select class="pj-input dform-select" data-search-dropdown-off="1" data-placeholder-gray-off="1" data-pm-challenge-settings-workkind>
            <option value=""${wk === "" ? " selected" : ""}>クライアント案件</option>
            <option value="internal"${wk === "internal" ? " selected" : ""}>社内工数（案件なし）</option>
          </select>
        </label>
        ${projRow}
        <p class="pm-challenge-settings-note">${workKindNote}</p>
      </div>`;
  }
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-challenge-settings="1"${busy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-challenge-settings-modal" role="dialog" aria-modal="true" aria-labelledby="pm-challenge-settings-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-challenge-settings-title">${PM_WORD.theme}設定</h3>
          <p class="project-create-step2-sub">${escapeHtml(ch.title || "（無題）")}</p>
        </div>
        <button class="inline-btn ghost" type="button" data-close-pm-challenge-settings="1"${busy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="pm-challenge-settings-layout">
        <nav class="pm-challenge-settings-nav" aria-label="${PM_WORD.theme}設定メニュー" role="tablist">
          ${tabBtn("categories", "カテゴリー")}
          ${tabBtn("statuses", "状況")}
          ${tabBtn("templates", "テンプレート")}
          ${tabBtn("basic", "基本")}
          ${tabBtn("members", "メンバー")}
          ${tabBtn("backlog", "Backlog連携")}
          ${tabBtn("archived", `アーカイブ${archivedAgendaCount > 0 ? `（${archivedAgendaCount}）` : ""}`)}
        </nav>
        <div class="pm-challenge-settings-body">${body}</div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-challenge-settings="1" type="button"${busy ? " disabled" : ""}>${ownSaveTab ? "閉じる" : "キャンセル"}</button>
        ${ownSaveTab ? "" : `<button class="inline-btn" data-pm-submit-challenge-settings="1" type="button"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "保存中…" : "設定を保存"}</button>`}
      </div>
    </aside>
  `;
}

/** 「最近使った担当者」として覚えておく人数の上限 */
const PM_RECENT_ASSIGNEE_LIMIT = 5;

/** 「最近使った担当者」の保存キー（ログインユーザーごと）。未ログインなら空＝保存しない */
function progressRecentAssigneeStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.recentAssignees.${me}` : "";
}

/** 担当者 dbId として妥当か（1以上の整数のみ。0・負数・小数・非数値は捨てる） */
function isProgressAssigneeDbId(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1;
}

/** 保存済みの担当者 dbId（新しい順・最大 PM_RECENT_ASSIGNEE_LIMIT 件） */
function progressRecentAssigneeDbIds() {
  const key = progressRecentAssigneeStorageKey();
  if (!key) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((v) => isProgressAssigneeDbId(v))
      .map((v) => String(Number(v)))
      .slice(0, PM_RECENT_ASSIGNEE_LIMIT);
  } catch {
    return [];
  }
}

/**
 * 課題の担当者を設定・変更したら呼ぶ（新規作成・編集・詳細での変更のすべて）。最新を先頭にした MRU で保存する。
 * サーバー（DB）には持たせないので、PC とスマホでは別々に貯まる。
 */
function rememberProgressRecentAssignee(assigneeDbId) {
  const key = progressRecentAssigneeStorageKey();
  if (!key || !isProgressAssigneeDbId(assigneeDbId)) return;
  const id = String(Number(assigneeDbId));
  const next = [id, ...progressRecentAssigneeDbIds().filter((x) => x !== id)].slice(0, PM_RECENT_ASSIGNEE_LIMIT);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* 保存できなくても担当者の選択そのものは動くので握りつぶす */
  }
}

/**
 * 最近使った担当者を返す（新しい順）。pool（assignableUsers）に居る人だけに絞るので、
 * 退職・休職した人が初期値や先頭候補に残り続けることはない。
 */
function progressRecentAssigneeUsers(pool, limit = PM_RECENT_ASSIGNEE_LIMIT) {
  const list = Array.isArray(pool) ? pool : [];
  if (!list.length) return [];
  const out = [];
  for (const dbKey of progressRecentAssigneeDbIds()) {
    if (out.length >= limit) break;
    const u = list.find((x) => x.dbId != null && String(Number(x.dbId)) === dbKey);
    if (u && !out.includes(u)) out.push(u);
  }
  return out;
}

// ===== 最近メンションした人（担当者「候補」に出すためだけのMRU。担当者の既定には使わない）=====
// メンション＝通知先であって担当ではないので、既定（自動選択）には絶対に使わない。
// 「候補として選びやすくする」目的でのみ担当者プルダウンに出す。端末ローカル（localStorage）。
function progressRecentMentionStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.recentMentions.${me}` : "";
}
function progressRecentMentionDbIds() {
  const key = progressRecentMentionStorageKey();
  if (!key) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((v) => isProgressAssigneeDbId(v))
      .map((v) => String(Number(v)))
      .slice(0, PM_RECENT_ASSIGNEE_LIMIT);
  } catch {
    return [];
  }
}
/** コメントで @メンションした人を最近メンション履歴に記録する（最新を先頭にしたMRU） */
function rememberProgressRecentMention(mentionDbId) {
  const key = progressRecentMentionStorageKey();
  if (!key || !isProgressAssigneeDbId(mentionDbId)) return;
  const id = String(Number(mentionDbId));
  const next = [id, ...progressRecentMentionDbIds().filter((x) => x !== id)].slice(0, PM_RECENT_ASSIGNEE_LIMIT);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* 保存できなくてもメンションそのものは動くので握りつぶす */
  }
}
/** コメント本文の @メンションを解決して、居るメンバーだけ最近メンション履歴に記録する */
function rememberProgressRecentMentionsFromBody(bodyText) {
  if (!progressRecentMentionStorageKey()) return;
  const displays = progressExtractNotifyMentionDisplays(bodyText);
  // 本文中の登場順で記録すると先頭が最古になるため、逆順に回して「最後に書いた人」を先頭へ寄せる
  for (let i = displays.length - 1; i >= 0; i -= 1) {
    const u = progressResolveNotifyTokenToUser(displays[i]);
    if (u && u.dbId != null && isProgressAssigneeDbId(u.dbId)) rememberProgressRecentMention(Number(u.dbId));
  }
}
/** 最近メンションした人を返す（新しい順・pool に居る人のみ） */
function progressRecentMentionUsers(pool, limit = PM_RECENT_ASSIGNEE_LIMIT) {
  const list = Array.isArray(pool) ? pool : [];
  if (!list.length) return [];
  const out = [];
  for (const dbKey of progressRecentMentionDbIds()) {
    if (out.length >= limit) break;
    const u = list.find((x) => x.dbId != null && String(Number(x.dbId)) === dbKey);
    if (u && !out.includes(u)) out.push(u);
  }
  return out;
}

/**
 * よく使う担当者を返す（読み込み済み課題の assignedUserId 出現頻度の上位）。
 * pool（assignableUsers）に含まれ、かつ実際に割当実績がある人のみを頻度降順で返す。
 * 永続化はせず、メモリ上の progressChallenges 配下の agendas から集計する。
 */
function progressFrequentAssigneeUsers(pool, limit = 5) {
  const list = Array.isArray(pool) ? pool : [];
  if (!list.length) return [];
  const counts = new Map();
  const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  for (const ch of chs) {
    const ags = Array.isArray(ch?.agendas) ? ch.agendas : [];
    for (const a of ags) {
      const id = a?.assignedUserId;
      if (id == null) continue;
      const key = String(Number(id));
      if (!/^\d+$/.test(key)) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  if (!counts.size) return [];
  return list
    .map((u) => {
      const dbKey = u.dbId != null ? String(Number(u.dbId)) : null;
      const c = dbKey && counts.has(dbKey) ? counts.get(dbKey) : 0;
      return { u, c };
    })
    .filter((x) => x.c > 0)
    .sort((a, b) => b.c - a.c)
    .slice(0, Math.max(0, limit))
    .map((x) => x.u);
}

/**
 * 課題ぐるみの担当候補: 登録者と、その課題のコメントで過去に@メンションされた人（新しい順）。
 * pool（assignableUsers）に居る人だけ返す＝退職・休職者は出さない。
 * messages 未ロード（threadDeferred）の課題ではメンション分は空になる（graceful）。
 */
/** 「この課題でメンションされた人」として出す最大人数（候補の先頭に出す少数精鋭でよい） */
const PM_AGENDA_MENTION_CANDIDATE_LIMIT = 5;

function progressAgendaContextCandidates(ag, pool) {
  const list = Array.isArray(pool) ? pool : [];
  const out = { creator: null, mentioned: [] };
  if (!ag || !list.length) return out;
  const creatorDb = Number(ag.createdByUserId);
  if (Number.isFinite(creatorDb) && creatorDb >= 1) {
    out.creator = list.find((u) => u.dbId != null && Number(u.dbId) === creatorDb) || null;
  }
  const msgs = Array.isArray(ag.messages) ? ag.messages : [];
  const poolIds = new Set(list.map((p) => String(p.id)));
  const seen = new Set();
  // 新しいコメントのメンションを優先（配列は古い順なので後ろから走査）
  for (let i = msgs.length - 1; i >= 0 && out.mentioned.length < PM_AGENDA_MENTION_CANDIDATE_LIMIT; i -= 1) {
    const m = msgs[i];
    if (!m || m.isSystem) continue;
    const body = String(m.body || "");
    // @の無い本文を progressExtractNotifyMentionDisplays に渡すと「全単語を人名として
    // 曖昧解決する」フォールバック（お知らせ欄専用）が走り、本文中の姓が誤って候補に
    // 昇格する。実メンションを含むコメントだけ解析する。
    if (!/[＠@]/.test(body)) continue;
    for (const disp of progressExtractNotifyMentionDisplays(body)) {
      if (out.mentioned.length >= PM_AGENDA_MENTION_CANDIDATE_LIMIT) break;
      const u = progressResolveNotifyTokenToUser(disp);
      if (!u) continue;
      const key = String(u.id);
      if (seen.has(key)) continue;
      seen.add(key);
      if (poolIds.has(key)) out.mentioned.push(u);
    }
  }
  return out;
}

/**
 * 担当者候補のグループ分け（[ラベル, users[]] の配列・重複なし）。
 * PCの optgroup 生成とSPの選択シートが同じ結果を消費する＝並び順の定義はここだけ。
 * 順序: 登録者 → この課題でメンションされた人 → 最近使った → 最近メンション → よく使う → すべて。
 */
function progressAssigneeCandidateGroups(pool, ag) {
  const list = Array.isArray(pool) ? pool : [];
  const ctx = progressAgendaContextCandidates(ag, list);
  const picked = new Set();
  const take = (users) => {
    const out = [];
    for (const u of users) {
      const key = String(u.id);
      if (picked.has(key)) continue;
      picked.add(key);
      out.push(u);
    }
    return out;
  };
  const groups = [
    ["登録者", take(ctx.creator ? [ctx.creator] : [])],
    ["この課題でメンションされた人", take(ctx.mentioned)],
    ["最近使った担当者", take(progressRecentAssigneeUsers(list))],
    ["最近メンションした人", take(progressRecentMentionUsers(list))],
    ["よく使う担当者", take(progressFrequentAssigneeUsers(list, 5))]
  ].filter(([, users]) => users.length);
  // 先頭候補が1人も居なければ（初回など）従来どおりグループ分けしない
  if (!groups.length) return [];
  const rest = list.filter((u) => !picked.has(String(u.id)));
  if (rest.length) groups.push(["すべてのメンバー", rest]);
  return groups;
}

/**
 * 担当者セレクトの optgroup 本体HTML（課題モーダル・課題詳細・コンポーズで共用）。
 * ag を渡すと「登録者」「この課題でメンションされた人」を先頭グループに出す（担当者設定ミス対策・2026-08-25要望）。
 * 既定（自動選択）はどのグループにも与えない＝呼び出し側の selected 判定だけで決まる。
 * @param {Array} pool assignableUsers()
 * @param {object|null} ag 対象の課題（新規作成など文脈が無ければ null）
 * @param {(u: object) => string} optionHtml 1人分の <option> を作る関数（selected 判定は呼び出し側）
 */
function progressAssigneeGroupedOptionsHtml(pool, ag, optionHtml) {
  const list = Array.isArray(pool) ? pool : [];
  const groups = progressAssigneeCandidateGroups(list, ag);
  if (!groups.length) return list.map(optionHtml).join("");
  return groups
    .map(([label, users]) => `<optgroup label="${label}">${users.map(optionHtml).join("")}</optgroup>`)
    .join("");
}

/** 課題クイック追加: どの画面からでも置ける「＋課題を追加」ボタン */
function progressQuickAddButtonHtml() {
  return `<button type="button" class="inline-btn pm-quick-add-btn" data-pm-quick-add-issue title="${PM_WORD.theme}を選んで${PM_WORD.issue}を追加">＋${PM_WORD.issue}を追加</button>`;
}

/** 課題クイック追加: テーマ選択ピッカー（モーダル）。一覧は各ページが既に読み込み済みの
 * uiState.progressChallenges をそのまま使う（別APIで取得し直すとキャッシュが古くなり、
 * 新規作成/アーカイブ直後のテーマがピッカーにだけ反映されない不整合が起きるため）。 */
function renderProgressQuickAddThemePicker() {
  if (!uiState.progressQuickAddPickerOpen) return "";
  const allChallenges = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  const challenges = allChallenges.filter((c) => !c.archivedAt);
  let body = "";
  if (challenges.length === 0) {
    body = `<div class="pm-qa-empty-state"><p class="pm-empty">追加できる${PM_WORD.theme}がありません。先に${PM_WORD.theme}を作成してください。</p></div>`;
  } else {
    const items = challenges
      .map((c) => {
        const search = normalizeProgressSearchText(String(c.title || ""));
        return `<button type="button" class="pm-qa-theme-item" data-pm-quick-add-pick="${escapeHtmlAttr(String(c.id))}" data-qa-search="${escapeHtmlAttr(search)}">
          <span class="pm-qa-theme-title">${escapeHtml(c.title || "（無題）")}</span>
        </button>`;
      })
      .join("");
    body = `<div class="pm-qa-list" data-pm-quick-add-list>${items}<div class="pm-qa-no-match" data-pm-quick-add-empty hidden>該当する${PM_WORD.theme}がありません</div></div>`;
  }
  return `
    <div class="composer-backdrop project-create-backdrop" data-pm-quick-add-close="1"></div>
    <aside class="composer-panel project-create-modal pm-quick-add-modal" role="dialog" aria-modal="true" aria-labelledby="pm-quick-add-title">
      <div class="composer-head">
        <div>
          <h3 id="pm-quick-add-title">課題を追加する${PM_WORD.theme}を選択</h3>
          <p class="project-create-step2-sub">${PM_WORD.theme}を選ぶと、その課題ボードで追加フォームが開きます</p>
        </div>
        <button class="inline-btn ghost" data-pm-quick-add-close="1" type="button">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="pm-qa-search">
          <span class="pm-qa-search-ic" aria-hidden="true">${pmBoardIconSvg("search", 16)}</span>
          <input type="search" class="pj-input pm-qa-search-input" data-pm-quick-add-search placeholder="${PM_WORD.theme}名で検索" autocomplete="off" aria-label="${PM_WORD.theme}を検索" />
        </div>
        ${body}
      </div>
    </aside>
  `;
}

/** ToDo管理: 課題追加・編集（テーマ登録モーダルと同系のパネル／課題ボードのみ） */
function renderProgressAgendaCreateModal() {
  if (!uiState.progressShowIssueAddForm) return "";
  const route = currentRoute();
  if (route.page !== "progress-management") return "";
  const editing = isProgressAgendaEditModal();
  const editAid = progressAgendaEditAgendaId();
  const editHit = editing ? findProgressAgendaById(editAid) : null;
  let projectId = String(route.searchParams?.get("project") || "").trim();
  if (!projectId && editHit?.agenda?.challengeId != null) {
    // 担当課題トークビュー等、URLに project= を持たないルート（assigned=me）から
    // 「課題の詳細」で編集モーダルを開いたときは、編集対象課題自身の challengeId で補う
    projectId = String(editHit.agenda.challengeId);
  }
  if (!projectId) return "";
  const onIssueDetail = progressManagementIssueDetailRouteActive(route);
  if (onIssueDetail && !editing) return "";
  const ch = findProgressChallenge(projectId);
  const d = mergedProgressAgendaCreateDraftForModal();
  const statusVal = ch ? normalizeProgressTodoStatusForChallenge(d.status, ch) : normalizeProgressTodoStatus(d.status);
  const priVal = normalizeProgressAgendaPriority(d.priority);
  const titleVal = escapeHtmlAttr(d.title || "");
  const bodyVal = escapeHtml(d.body || "");
  const dueVal = escapeHtmlAttr(d.dueDate || "");
  const challengeIdAttr = escapeHtmlAttr(projectId);
  const statusList = ch ? progressChallengeStatuses(ch) : PM_TODO_STATUSES;
  // 「完了」への変更は作成者のみ許可 — 編集時は作成者以外の選択肢から「完了」を除外（SP詳細シートと同ルール）
  const editSrcAg = editHit?.agenda || null;
  const editMyDbId = progressCurrentUserDbId();
  const editIsCreator =
    editSrcAg?.createdByUserId != null && editMyDbId != null && Number(editSrcAg.createdByUserId) === editMyDbId;
  const editCurStatus = editSrcAg
    ? (ch ? normalizeProgressTodoStatusForChallenge(editSrcAg.status, ch) : normalizeProgressTodoStatus(editSrcAg.status))
    : "";
  const visibleStatusList = editing
    ? statusList.filter((s) => s !== "完了" || editCurStatus === "完了" || editIsCreator)
    : statusList;
  const opt = visibleStatusList
    .map((s) => `<option value="${escapeHtmlAttr(s)}"${statusVal === s ? " selected" : ""}>${escapeHtml(s)}</option>`)
    .join("");
  const priOpt = PM_AGENDA_PRIORITIES.map(
    (p) => `<option value="${escapeHtmlAttr(p)}"${priVal === p ? " selected" : ""}>${escapeHtml(p)}</option>`
  ).join("");
  const catOpt = progressAgendaCategorySelectHtml(d.category || "その他", projectId);
  const selAssigneeRaw = String(d.assignedUserId || "").trim();
  const assigneePool = progressChallengeAssigneePool(ch);
  const assigneePoolPick = assigneePool.find(
    (u) => String(u.id) === selAssigneeRaw || (u.dbId != null && selAssigneeRaw === String(Number(u.dbId)))
  );
  const assigneeExtra =
    selAssigneeRaw && !assigneePoolPick
      ? users.find(
          (u) => String(u.id) === selAssigneeRaw || (u.dbId != null && selAssigneeRaw === String(Number(u.dbId)))
        )
      : null;
  const assigneeOptionHtml = (u) => {
    const uid = String(u.id);
    const selected =
      selAssigneeRaw === uid || (u.dbId != null && selAssigneeRaw === String(Number(u.dbId)));
    return `<option value="${escapeHtmlAttr(uid)}"${selected ? " selected" : ""}>${escapeHtml(userNameWithDept(u))}</option>`;
  };
  // 編集時は課題の文脈（登録者・課題内メンション）を先頭に。以降は 最近使った→最近メンション→よく使う の順
  const assigneeBodyOpts = progressAssigneeGroupedOptionsHtml(
    assigneePool,
    editing ? editSrcAg : null,
    assigneeOptionHtml
  );
  const assigneeOpts =
    `<option value="">（未設定）</option>` +
    assigneeBodyOpts +
    (assigneeExtra
      ? `<option value="${escapeHtmlAttr(String(assigneeExtra.id))}" selected>${escapeHtml(userNameWithRetiredLabel(assigneeExtra))}</option>`
      : "");
  const creatorName = editing
    ? progressAgendaCreatorDisplayName(editSrcAg)
    : String(currentUser?.name || authUser?.name || "").trim() || "（不明）";
  const agBusy = Boolean(uiState.progressAgendaModalSubmitting);
  const editIdAttr = editing ? ` data-pm-agenda-edit-id="${escapeHtmlAttr(editAid)}"` : "";
  return `
    <div class="composer-backdrop project-create-backdrop" data-close-pm-agenda-create="1"${agBusy ? " style=\"pointer-events:none;opacity:0.6\"" : ""}></div>
    <aside class="composer-panel project-create-modal pm-agenda-create-modal${spNoEnter("pm-agenda-create-modal")}" role="dialog" aria-modal="true" aria-labelledby="pm-agenda-create-title" data-pm-file-drop="modal:pending">
      <div class="composer-head">
        <div>
          <h3 id="pm-agenda-create-title">${editing ? "課題を編集" : "課題を追加"}</h3>
          <p class="project-create-step2-sub">${editing ? `${PM_WORD.issue}の内容を変更して保存します` : `この${PM_WORD.theme}の${PM_WORD.issue}ボードに新しい${PM_WORD.issue}を追加します`}</p>
        </div>
        <button class="inline-btn ghost" data-close-pm-agenda-create="1" type="button"${agBusy ? " disabled" : ""}>閉じる</button>
      </div>
      <div class="composer-body">
        <div class="client-form-section project-create-step2-block">
          ${progressAgendaTemplatePickerHtml(ch, editing)}
          <label class="client-form-row">
            <span class="client-form-row-label">タイトル <span class="client-required">（必須）</span></span>
            <input type="text" class="pj-input" data-pm-modal-agenda-title value="${titleVal}" placeholder="課題のタイトル" maxlength="500" />
          </label>
          <!-- for で textarea を指す（無いとラベル文字のクリックが先頭の書式ボタン＝太字を押してしまう） -->
          <label class="client-form-row pm-agenda-detail-row" for="pm-modal-agenda-body">
            <span class="client-form-row-label">詳細（任意）</span>
            <div class="pm-agenda-body-editor" data-pm-format-scope>
              ${progressComposeFormatToolbarHtml()}
              <textarea class="pj-input" id="pm-modal-agenda-body" data-pm-modal-agenda-body data-pm-mention-textarea="1" rows="3" placeholder="内容・受け入れ条件など">${bodyVal}</textarea>
            </div>
          </label>
          <label class="client-form-row">カテゴリ
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-modal-agenda-category>${catOpt}</select>
          </label>
          <label class="client-form-row pm-agenda-client-row">クライアント
            <select class="pj-input dform-select" data-pm-modal-agenda-client aria-label="クライアント">${developmentClientOptions(d.clientId)}</select>
          </label>
          <label class="client-form-row">状況
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-modal-agenda-status>${opt}</select>
          </label>
          <div class="client-form-row pm-agenda-creator-row">
            <span>登録者</span>
            <span class="pm-agenda-creator-val">${escapeHtml(creatorName)}</span>
          </div>
          <label class="client-form-row">
            <span class="client-form-row-label">担当者${editing ? "" : " <span class=\"client-required\">（必須）</span>"}</span>
            <select class="pj-input dform-select" data-pm-modal-agenda-assignee aria-label="担当者">${assigneeOpts}</select>
          </label>
          ${
            editing
              ? ""
              : `<label class="client-form-row">
            <span class="client-form-row-label">課題の追加をお知らせするユーザー（任意）</span>
            <textarea class="pj-input" data-pm-modal-agenda-notify data-pm-mention-textarea="1" rows="1" autocomplete="off" placeholder="@で選択（複数可）。開いたかどうか既読を確認できます" maxlength="500">${escapeHtml(String(d.notifyRaw || ""))}</textarea>
          </label>`
          }
          <label class="client-form-row">優先度
            <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-modal-agenda-priority>${priOpt}</select>
          </label>
          <!-- 開始日（＝登録日）は自動で当日が入り編集不可。入力欄は撤去（IMPROVE-380）。表示は各カードのタイトル横（TASK-389）。 -->
          <label class="client-form-row">期限日<input class="pj-input" data-pm-modal-agenda-due data-show-picker="1" type="date" value="${dueVal}" /></label>
          <label class="client-form-row pm-agenda-modal-attach-row">
            <span class="client-form-row-label">添付（任意）</span>
            ${progressAgendaModalAttachFieldHtml(agBusy)}
          </label>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-pm-agenda-create="1" type="button"${agBusy ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-pm-submit-agenda-modal="1" data-pm-agenda-challenge-id="${challengeIdAttr}"${editIdAttr} type="button"${agBusy ? " disabled aria-busy=\"true\"" : ""}>${agBusy ? (editing ? "保存中…" : "追加中…") : editing ? "保存する" : "追加する"}</button>
      </div>
    </aside>
  `;
}

function renderProjectCreateWarningModal() {
  if (!uiState.projectCreateWarning) return "";
  const m = uiState.projectCreateWarning;
  const missingHtml = (m.missingItems || []).map((item) => `<li>・${item}</li>`).join("");
  return `
    <div class="composer-backdrop project-create-warning-backdrop" data-project-create-cancel-warning="1"></div>
    <aside class="composer-panel project-create-warning-modal fp-centered-modal" role="dialog" aria-modal="true" aria-labelledby="project-create-warning-title" style="grid-template-rows:auto auto auto;border:1px solid #f1d0d0;">
      <div class="composer-head" style="border-bottom:1px solid #f5d5d5;">
        <div><h3 id="project-create-warning-title" style="color:#991b1b;">${m.title}</h3></div>
      </div>
      <div class="composer-body" style="padding:20px 20px 14px;">
        <p style="margin:0;color:#4b5563;line-height:1.7;">${m.message}</p>
        <p style="margin:12px 0 6px;color:#1f2937;font-weight:700;">【未締結】</p>
        <ul style="list-style:none;padding:0;margin:0;color:#374151;line-height:1.7;">
          ${missingHtml || "<li>・なし</li>"}
        </ul>
      </div>
      <div class="composer-foot" style="justify-content:flex-end;">
        <button class="inline-btn ghost" data-project-create-cancel-warning="1" type="button">戻る</button>
        <button class="inline-btn" data-project-create-force="1" type="button" style="background:#b91c1c;border-color:#991b1b;color:#fff;">それでも進める</button>
      </div>
    </aside>
  `;
}

/** 案件コピーで何を引き継ぐか（複製確認モーダルの選択肢）。all が従来どおりの挙動 */
const PROJECT_COPY_MODES = [
  { key: "project", label: "案件情報のみ", desc: "顧客・案件名・担当・ステータスなどの基本情報だけ" },
  { key: "quote", label: "案件情報・見積書のみ", desc: "基本情報に加えて、項目（明細）と見積書の設定・備考" },
  { key: "all", label: "全て", desc: "基本情報・項目（明細）・全書類の設定・備考" }
];

/** 原価フォームの欄が「まだ自動の値のまま」か（空欄、または前回自動で入れた値と同じ）。手で直した値は上書きしない */
function costFormFieldStillAuto(f, field) {
  const cur = String(f[field] ?? "").trim();
  // 「0」を未入力とみなすのは新規だけ。登録済みの原価の 0 は意図した値（源泉の対象外など）なので、
  // 金額や発注先を直したときに自動計算で上書きしない
  return !cur || (cur === "0" && !f.editingId) || cur === String(f._auto?.[field] ?? "");
}

/** 原価フォームの欄へ自動の値を入れ、自動で入れたことを覚える（発注先を変えたら入れ直せるように） */
function setCostFormAutoValue(f, field, value) {
  f[field] = value;
  f._auto = { ...(f._auto || {}), [field]: String(value) };
}

/** 原価フォーム: 発注先が源泉徴収の対象なら源泉所得税を自動で入れる（対象外の発注先に変えたら自動の値を消す）。
 *  手で直した値は上書きしない。金額の入力中に再描画しないよう、欄は直接書き換える */
function syncCostFormWithholdingFromVendor() {
  const f = uiState.costForm;
  if (!f || !costFormFieldStillAuto(f, "withholdingTax")) return false;
  const auto = costAutoWithholdingFromVendor(f.vendor, f.amount) ?? 0;
  if (Math.round(Number(f.withholdingTax) || 0) === auto) return false;
  setCostFormAutoValue(f, "withholdingTax", auto);
  const el = document.querySelector('[data-cost-field="withholdingTax"]');
  if (el instanceof HTMLInputElement && el !== document.activeElement) el.value = String(auto);
  return true;
}

/** 原価フォーム: 請求日・発注先の変更でパートナーマスタから自動補完する（変わったら true＝再描画）。
 *  支払期限=支払条件から計算（欄を出さないとき=仮原価でない媒体費等は対象外）／支払方法=マスタの支払方法を写像／
 *  源泉所得税=源泉徴収区分から。どれも空欄か自動の値のままの間だけ（発注先を変えたら入れ直す） */
function applyCostFormPartnerAutofill(key) {
  const f = uiState.costForm;
  if (!f) return false;
  let changed = false;
  if (costFormShowsDueDate(f) && costFormFieldStillAuto(f, "dueDate")) {
    const autoDue = costAutoDueDateFromVendorTerms(f.vendor, f.invoiceDate);
    if (autoDue && autoDue !== f.dueDate) {
      setCostFormAutoValue(f, "dueDate", autoDue);
      changed = true;
    }
  }
  if (key === "vendor" && costFormFieldStillAuto(f, "paymentMethod")) {
    const autoMethod = costAutoPaymentMethodFromVendor(f.vendor);
    if (autoMethod && autoMethod !== f.paymentMethod) {
      setCostFormAutoValue(f, "paymentMethod", autoMethod);
      changed = true;
    }
  }
  if (key === "vendor" && syncCostFormWithholdingFromVendor()) changed = true;
  // 仮原価の源泉徴収区分（源泉所得税は保存時に区分と金額から計算する）
  if (key === "vendor" && costFormFieldStillAuto(f, "withholdingKind")) {
    const autoKind = costWithholdingKindFromVendor(f.vendor);
    if (autoKind && autoKind !== f.withholdingKind) {
      setCostFormAutoValue(f, "withholdingKind", autoKind);
      changed = true;
    }
  }
  return changed;
}

/** 原価フォーム: 金額（税抜）から消費税10%を自動計算して state と画面の消費税欄を同期する。
 *  入力のたびに呼ばれるので render() はしない（フォーカスを失うため）。消費税欄は手修正可（軽減税率・非課税など） */
function syncCostFormTaxFromAmount() {
  const f = uiState.costForm;
  if (!f) return;
  const amt = Math.round(Number(f.amount) || 0);
  f.consumptionTax = amt > 0 ? Math.round(amt * 0.1) : 0;
  const taxEl = document.querySelector('[data-cost-field="consumptionTax"]');
  if (taxEl instanceof HTMLInputElement && taxEl !== document.activeElement) taxEl.value = costFormYenInputValue(f.consumptionTax, "0");
}

/** 原価フォームの金額欄の表示値（桁区切り）。入力の読み取り側はカンマを除いて数値にしている（16 の input/change） */
function costFormYenInputValue(n, emptyText) {
  const v = Math.round(Number(n) || 0);
  return v ? v.toLocaleString("ja-JP") : emptyText;
}

// 金額欄は入力中は打ったまま、欄を離れたら桁区切りで見せる（30000 → 30,000）
document.addEventListener("focusout", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !uiState.costForm) return;
  const key = el.dataset.costField;
  if (key === "amount") el.value = costFormYenInputValue(uiState.costForm.amount, "");
  else if (key === "consumptionTax") el.value = costFormYenInputValue(uiState.costForm.consumptionTax, "0");
});

function renderProjectBulkDupConfirmModal() {
  if (!uiState.projectBulkDupConfirm) return "";
  const ids = uiState.projectBulkDupConfirm.selectedIds || [];
  const n = ids.length;
  const processing = !!uiState.projectBulkDupConfirm.processing;
  const disabled = processing ? " disabled" : "";
  const mode = PROJECT_COPY_MODES.some((m) => m.key === uiState.projectBulkDupConfirm.mode)
    ? uiState.projectBulkDupConfirm.mode
    : "all";
  const bodyHtml = processing
    ? `<p style="margin:0;color:#4b5563;line-height:1.7;" role="status">複製しています…</p>`
    : `<p style="margin:0;color:#4b5563;line-height:1.7;">選択した<strong>${n}件</strong>の案件を複製します。コピーする内容を選んでください。</p>
       <div class="project-copy-mode-list" role="radiogroup" aria-label="コピーする内容">
         ${PROJECT_COPY_MODES.map((m) => `
         <label class="project-copy-mode-item">
           <input type="radio" name="project-bulk-dup-mode" value="${m.key}" ${m.key === mode ? "checked" : ""} />
           <span class="project-copy-mode-label">${m.label}</span>
           <span class="project-copy-mode-desc">${m.desc}</span>
         </label>`).join("")}
       </div>
       <p style="margin:10px 0 0;font-size:12px;color:#64748b;">書類の発行状況・捺印申請・原価はコピーされません。</p>`;
  return `
    <div class="composer-backdrop" data-project-bulk-dup-cancel="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm" role="dialog" aria-modal="true" aria-labelledby="project-bulk-dup-title" style="grid-template-rows:auto auto auto;">
      <div class="composer-head">
        <div><h3 id="project-bulk-dup-title">複製の確認</h3></div>
      </div>
      <div class="composer-body" style="padding:20px;">
        ${bodyHtml}
      </div>
      <div class="composer-foot" style="justify-content:flex-end;gap:8px;">
        <button class="inline-btn ghost" type="button" data-project-bulk-dup-cancel="1"${disabled}>キャンセル</button>
        <button class="inline-btn" type="button" data-project-bulk-dup-confirm="1"${disabled}>${processing ? "複製中…" : "複製する"}</button>
      </div>
    </aside>
  `;
}

function renderSealConfirmModal() {
  const d = uiState.sealConfirmDialog;
  if (!d) return "";
  let title = "ご確認";
  let bodyHtml = "";
  if (d.kind === "apply") {
    const parsed = parseDocSealKey(d.key);
    if (!parsed) return "";
    const proj = projects.find((x) => x.id === parsed.pid);
    const pname = escapeHtml(proj?.name || "案件");
    const dlabel = escapeHtml(docTabLabelForSealApplication(parsed.tab));
    title = sealRevokeInfo(d.key) ? "捺印申請の送信（再申請）" : "捺印申請の送信";
    bodyHtml = `<p style="margin:0;color:#4b5563;line-height:1.7;">「${pname}」の<strong>${dlabel}</strong>について、捺印申請を送信しますか？</p>${sealRevokeNoticeHtml(d.key)}${sealApplyInputsHtml(d)}`;
  } else if (d.kind === "approve") {
    const parsed = parseDocSealKey(d.key);
    if (!parsed) return "";
    const proj = projects.find((x) => x.id === parsed.pid);
    const pname = escapeHtml(proj?.name || "案件");
    const dlabel = escapeHtml(docTabLabelForSealApplication(parsed.tab));
    title = "捺印の承認";
    const mk = String(d.key || "");
    const cur = getCurrentSealApproverUserForMapKey(mk);
    const chain = getSealApprovalChainForMapKey(mk);
    const n = getSealApprovalProgressApprovedCount(mk);
    const curName = escapeHtml(String(cur?.name || "—"));
    let sub = "";
    if (chain.length > 0 && n + 1 < chain.length) {
      const nextName = escapeHtml(String(chain[n + 1]?.user?.name || "—"));
      sub = `<p style="margin:10px 0 0;font-size:12px;color:#64748b;">この承認のあと、次は<strong>${nextName}</strong>さんの承認待ちとなります。</p>`;
    } else if (chain.length > 0) {
      sub = `<p style="margin:10px 0 0;font-size:12px;color:#64748b;">この承認で<strong>承認済</strong>になります。</p>`;
    }
    bodyHtml = `<p style="margin:0;color:#4b5563;line-height:1.7;">「${pname}」の<strong>${dlabel}</strong>について、<strong>${curName}</strong>としてこの段階の捺印申請を承認しますか？</p>${sub}`;
  } else if (d.kind === "bulk") {
    title = "一括捺印申請";
    bodyHtml = `${sealBulkTargetsHtml(d)}${sealApplyInputsHtml(d)}`;
  } else if (d.kind === "app-mgmt-bulk-approve-all") {
    const n = (d.items || []).length;
    title = "申請の一括承認";
    bodyHtml = `<p style="margin:0;color:#4b5563;line-height:1.7;">選択した<strong>${n}件</strong>をまとめて承認します。カテゴリごとに承認経路へ振り分けて処理します（あなたが承認者でない、または承認順が来ていない申請はスキップされます）。よろしいですか？</p>`;
  } else {
    return "";
  }
  const processing = !!d.processing;
  const bodyWithStatus = processing
    ? `<p class="app-mgmt-approve-processing" role="status">承認を実行しています。件数が多いと時間がかかります…</p>`
    : bodyHtml;
  const okLabel = processing ? "承認中…" : "OK";
  const cancelDisabled = processing ? " disabled" : "";
  // 一括申請は書類を選ぶまで送信できない
  const okDisabled = processing || (d.kind === "bulk" && !d.tab) ? " disabled" : "";
  const backdropBusy = processing ? ' data-seal-confirm-busy="1"' : "";
  return `
    <div class="composer-backdrop" data-seal-confirm-cancel="1"${backdropBusy}></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm seal-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="seal-confirm-title" style="grid-template-rows:auto minmax(0, 1fr) auto;">
      <div class="composer-head">
        <div><h3 id="seal-confirm-title">${escapeHtml(title)}</h3></div>
      </div>
      <div class="composer-body" style="padding:20px;">
        ${bodyWithStatus}
      </div>
      <div class="composer-foot" style="justify-content:flex-end;gap:8px;">
        <button class="inline-btn ghost" type="button" data-seal-confirm-cancel="1"${cancelDisabled}>キャンセル</button>
        <button class="inline-btn" type="button" data-seal-confirm-ok="1"${okDisabled}>${escapeHtml(okLabel)}</button>
      </div>
    </aside>
  `;
}

function renderDetailLineAddPickerModal() {
  if (!uiState.detailLineAddPicker) return "";
  return `
    <div class="composer-backdrop" data-detail-line-add-picker-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--xs detail-line-add-picker-modal" role="dialog" aria-modal="true" aria-labelledby="detail-line-add-picker-title" style="grid-template-rows:auto auto auto;">
      <div class="composer-head">
        <div><h3 id="detail-line-add-picker-title">追加する項目を選択</h3></div>
      </div>
      <div class="composer-body" style="display:grid;gap:8px;padding:16px 20px;">
        <button class="inline-btn ghost" type="button" data-detail-line-add-type="normal">通常行</button>
        <button class="inline-btn ghost" type="button" data-detail-line-add-type="heading">見出し行</button>
        <button class="inline-btn ghost" type="button" data-detail-line-add-type="subtotal">小計行</button>
      </div>
      <div class="composer-foot" style="justify-content:flex-end;">
        <button class="inline-btn ghost" type="button" data-detail-line-add-picker-close="1">キャンセル</button>
      </div>
    </aside>
  `;
}

function renderDetailLineMobileEditModal() {
  const m = uiState.detailLineMobileEdit;
  if (!m) return "";
  const { pid, index } = m;
  const lines = uiState.projectDocsDrafts[pid]?.lines;
  if (!lines || index < 0 || index >= lines.length) return "";
  const l = lines[index];
  const type = projectDetailLineType(l);
  if (type !== "normal") return "";
  return `
    <div class="composer-backdrop" data-detail-line-mobile-close="1"></div>
    <aside class="dlm-sheet" role="dialog" aria-modal="true" aria-labelledby="dlm-title">
      <div class="dlm-sheet-head">
        <h3 id="dlm-title" class="dlm-sheet-title">明細を編集</h3>
        <button type="button" class="dlm-sheet-close" data-detail-line-mobile-close="1" aria-label="閉じる">×</button>
      </div>
      <div class="dlm-sheet-body">
        <label class="dlm-field">
          <span class="dlm-label">摘要</span>
          <input type="text" class="dlm-input" id="dlm-item" value="${escapeHtmlAttr(l.item || "")}" placeholder="摘要を入力" />
        </label>
        <div class="dlm-row2">
          <label class="dlm-field">
            <span class="dlm-label">数量</span>
            <input type="number" class="dlm-input dlm-input--right" id="dlm-qty" value="${escapeHtmlAttr(String(l.qty ?? "1"))}" step="any" />
          </label>
          <label class="dlm-field">
            <span class="dlm-label">単位</span>
            <input type="text" class="dlm-input" id="dlm-unit" value="${escapeHtmlAttr(l.unit || "")}" placeholder="式" />
          </label>
        </div>
        <label class="dlm-field">
          <span class="dlm-label">単価（円）</span>
          <input type="number" class="dlm-input dlm-input--right" id="dlm-price" value="${escapeHtmlAttr(String(l.price ?? "0"))}" step="any" />
        </label>
      </div>
      <div class="dlm-sheet-foot">
        <button type="button" class="inline-btn ghost dlm-cancel-btn" data-detail-line-mobile-close="1">キャンセル</button>
        <button type="button" class="inline-btn dlm-save-btn" data-detail-line-mobile-save="1" data-detail-project="${escapeHtmlAttr(String(pid))}" data-detail-line-index="${index}">保存</button>
      </div>
    </aside>
  `;
}

/** クライアントフォームの都道府県セレクトと法人検索の照合に共通利用 */
const JAPAN_PREFECTURE_NAMES = [
  "北海道","青森県","岩手県","宮城県","秋田県","山形県","福島県","茨城県","栃木県","群馬県","埼玉県","千葉県","東京都","神奈川県","新潟県","富山県","石川県","福井県","山梨県","長野県","岐阜県","静岡県","愛知県","三重県","滋賀県","京都府","大阪府","兵庫県","奈良県","和歌山県","鳥取県","島根県","岡山県","広島県","山口県","徳島県","香川県","愛媛県","高知県","福岡県","佐賀県","長崎県","熊本県","大分県","宮崎県","鹿児島県","沖縄県"
];

/** 都道府県セレクトの <option> 群（クライアント/顧客支社/パートナー/契約で共通）。
 *  先頭の空オプションは必須。これが無いとブラウザは保存値が空でも先頭「北海道」を選択済みとして
 *  表示し、①画面と実データが食い違う ②その状態で「北海道」を選んでも検索プルダウンの
 *  「値が変わっていない」判定（15-sp-backlog.js の sel.value !== next）でイベントが飛ばず
 *  state に入らない ③一度入れた都道府県を空へ戻せない、の3つが同時に起きる。 */
function prefectureOptionsHtml(selected) {
  const cur = String(selected || "");
  const opts = JAPAN_PREFECTURE_NAMES.map((p) => `<option ${cur === p ? "selected" : ""}>${p}</option>`).join("");
  return `<option value="" ${cur ? "" : "selected"}>未設定</option>${opts}`;
}

function resolveClientPrefectureFromRow(apiPref, addressLine) {
  const p = String(apiPref || "").trim();
  if (JAPAN_PREFECTURE_NAMES.includes(p)) return p;
  const hay = `${p}${String(addressLine || "")}`;
  for (const pr of JAPAN_PREFECTURE_NAMES) {
    if (hay.includes(pr)) return pr;
  }
  return "東京都";
}

function resetCorporateLocalSearch() {
  uiState.corporateLocalSearch = {
    context: null,
    query: "",
    results: null,
    error: "",
    loading: false,
    indexRowCount: null,
    searchHint: ""
  };
}

function corpLocalSearchPanelHtml(mode) {
  const ls = uiState.corporateLocalSearch || {
    context: null,
    query: "",
    results: null,
    error: "",
    loading: false,
    indexRowCount: null,
    searchHint: ""
  };
  const is = ls.context === mode;
  const q = is ? String(ls.query || "") : "";
  const loading = is && ls.loading;
  const err = is ? String(ls.error || "").trim() : "";
  const hint = is ? String(ls.searchHint || "").trim() : "";
  const results = is ? ls.results : null;
  let body = "";
  if (loading) body = '<div class="corp-local-status corp-local-status--muted">検索中…</div>';
  else if (err) body = `<div class="corp-local-status corp-local-status--err">${escapeHtml(err)}</div>`;
  else if (Array.isArray(results)) {
    if (results.length === 0) {
      const emptyIdx = is && ls.indexRowCount === 0;
      body = hint
        ? `<div class="corp-local-status corp-local-status--muted">${escapeHtml(hint)}</div>`
        : `<div class="corp-local-status corp-local-status--muted">${
            emptyIdx
              ? "インデックスが空です。新規は下のフォームへ入力してください。"
              : "該当なし。新規の法人は下のフォームへ直接入力してください。"
          }</div>`;
    } else {
      body = `<div class="corp-local-results" role="list">${results
        .map(
          (r, i) =>
            `<button type="button" class="corp-local-pick" role="listitem" data-corp-local-pick="${mode}:${i}">
          <span class="corp-local-pick-name">${escapeHtml(r.name)}</span>
          <span class="corp-local-pick-sub">${escapeHtml(`${r.prefecture || ""}${r.address || ""}`)}</span>
          <span class="corp-local-pick-num">${escapeHtml(r.corporateNumber)}</span>
        </button>`
        )
        .join("")}</div>`;
    }
  }
  return `
    <div class="client-form-section client-corp-local">
      <div class="client-form-row">
        <span>マスタ照会（任意）</span>
        <div style="display:flex;gap:6px;flex:1;min-width:0;">
          <input type="search" class="corp-local-q" placeholder="すでに登録した法人を商号・法人番号で検索" value="${escapeHtmlAttr(q)}" autocomplete="off" data-corp-local-q="${mode}" style="flex:1;min-width:0;" />
          <button type="button" class="inline-btn" data-corp-local-submit="${mode}" style="min-width:auto;padding:9px 16px;">検索</button>
        </div>
      </div>
      <div class="corp-local-help">国税庁の全法人データ（約512万件）および登録済み${mode === "partner" ? "パートナー" : "クライアント"}から検索できます。会社名または法人番号（13桁）で検索し、候補を選択するとフォームに自動入力されます。</div>
      ${body}
    </div>`;
}

function renderClientCreateModal(opts = {}) {
  if (!uiState.clientCreate) return "";
  const c = uiState.clientCreate;
  // inline は取引先詳細ページ側（clientDetailPage）が embed:true で描く。モーダルレイヤーには出さない
  if (c.inline && !opts.embed) return "";
  const isEdit = c.mode === "edit";
  const fromProject = !!c.fromProject;
  // 新規登録・削除はマスター/経理のみ。変更(更新申請)は全員可。案件作成中(fromProject)の登録は従来どおり残す。
  const canManageMaster = canManageClientPartnerMaster();
  const centered = !!c.centered;
  const useCenteredModal = fromProject || centered;
  const req = `<span class="client-required">（必須）</span>`;
  // 基本契約が締結済みなら NDA 未締結の注意表示は出さない（NDA→基本契約の運用でカバー済み扱い）
  const contractPending = String(c.contractStatus || "未").trim() !== "済";
  const ndaPending = String(c.ndaStatus || "未").trim() !== "済" && contractPending;
  const ndaExportBtnClass = `inline-btn ghost${ndaPending ? " client-export-doc-pending" : ""}`;
  const gyomuExportBtnClass = `inline-btn ghost${contractPending ? " client-export-doc-pending" : ""}`;
  // embed（取引先詳細のページ埋め込み）は fp-cldetail-embed を付け、右ドロワー強制の
  // !important ルール（aside.composer-panel[role="dialog"]）の除外対象にする
  const asideAttrs = useCenteredModal
    ? `class="composer-panel client-create-modal${opts.embed ? " fp-cldetail-embed" : ""}${spNoEnter("client-create-modal")}" role="dialog" aria-modal="true"`
    : `class="composer-panel fp-drawer-panel${spNoEnter("fp-drawer-panel")}"`;
  return `
    <div class="composer-backdrop${useCenteredModal ? " client-create-backdrop" : ""}" data-close-client-create="1"></div>
    <aside ${asideAttrs}>
      <div class="composer-head">
        <div><h3>${isEdit ? "クライアントを編集" : "新しいクライアントを登録"}</h3><p>${c.fromProject ? "作成後はそのまま選択されます" : "クライアントマスタに反映されます"}</p></div>
        <button class="inline-btn ghost" data-close-client-create="1">閉じる</button>
      </div>
      <div class="composer-body">
        ${corpLocalSearchPanelHtml("client")}
        <div class="client-form-divider"></div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>顧客名 ${req}</span><input data-client-field="name" value="${escapeHtmlAttr(c.name)}" placeholder="正式名称を入力" /></div>
          <div class="client-form-row"><span>顧客略称名 ${req}</span><input data-client-field="shortName" value="${escapeHtmlAttr(c.shortName)}" placeholder="略称（書類以外の画面表示用）" /></div>
          <div class="client-form-row"><span>個人</span><label class="cl-inline-check"><input type="checkbox" data-client-individual="1" ${isIndividualHonorific(c.honorific) ? "checked" : ""} /><span>個人（書類の宛名を「御中」ではなく「様」にする）</span></label></div>
          <label class="client-form-row"><span>おおよその取引予定金額${isEdit ? "" : ` ${req}`}</span><input data-client-field="expectedAmount" value="${escapeHtmlAttr(c.expectedAmount || "")}" inputmode="numeric" placeholder="${isEdit ? "円（例：500000）。分かる範囲でOK・未定なら空欄可" : "円（例：500000）。おおよそ・分かる範囲でOK"}" /></label>
          ${clientFormCreditRowsHtml("client", c, !isEdit)}
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>郵便番号</span><div style="display:flex;gap:6px;align-items:center;"><input data-client-field="postalCode" value="${escapeHtmlAttr(c.postalCode)}" placeholder="000-0000" inputmode="numeric" maxlength="8" style="width:140px;" /></div></div>
          <div class="client-form-row"><span>都道府県</span><select data-client-field="prefecture">${prefectureOptionsHtml(c.prefecture)}</select></div>
          <label class="client-form-row">市区町村・番地<input data-client-field="address" value="${escapeHtmlAttr(c.address)}" /></label>
          <label class="client-form-row">建物名<input data-client-field="building" value="${escapeHtmlAttr(c.building)}" /></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">TEL<input data-client-field="tel" value="${escapeHtmlAttr(c.tel)}" placeholder="000-0000-0000" /></label>
          <label class="client-form-row">FAX<input data-client-field="fax" value="${escapeHtmlAttr(c.fax)}" placeholder="000-0000-0000" /></label>
          <label class="client-form-row">メール<input data-client-field="email" value="${escapeHtmlAttr(c.email)}" placeholder="example@company.co.jp" /></label>
          <label class="client-form-row">担当者名<input data-client-field="contactName" value="${escapeHtmlAttr(c.contactName)}" /></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>社内担当者</span>
            <select data-client-field="contractOwner" aria-label="社内担当者">${internalStaffSelectOptionsHtml(c.contractOwner)}</select>
          </div>
          <div class="client-form-row"><span>デフォルト支払条件 ${req}</span>
            <select data-search-dropdown-off="1" data-client-field="paymentTerms">
              ${PAYMENT_TERMS_OPTIONS.map((v) => `<option ${c.paymentTerms === v ? "selected" : ""}>${v}</option>`).join("")}
            </select>
          </div>
          <div class="client-form-row"><span>振込手数料負担 ${req}</span>
            <div class="radio-group client-form-radio-group">
              <label><input type="radio" name="transferFee" value="先方" ${c.transferFeeBurden === "先方" ? "checked" : ""} data-client-field="transferFeeBurden" /><span>先方</span></label>
              <label><input type="radio" name="transferFee" value="当方" ${c.transferFeeBurden === "当方" ? "checked" : ""} data-client-field="transferFeeBurden" /><span>当方</span></label>
            </div>
          </div>
          ${clientFormDocStatusRowHtml("data-client-field", "ndaStatus", "NDA締結", c.ndaStatus, isEdit && canManageMaster)}
          ${clientFormDocStatusRowHtml("data-client-field", "contractStatus", "基本契約書締結", c.contractStatus, isEdit && canManageMaster)}
          <label class="client-form-row">本契約の目的<textarea data-client-field="ndaPurpose" rows="2" placeholder="NDA・業務委託基本契約書の目的条項に反映されます">${escapeHtml(c.ndaPurpose || "")}</textarea></label>
          <div class="client-form-row"><span>書類送付方法 ${req}</span>
            ${deliveryMethodCheckboxGroupHtml(c.deliveryMethod, "client")}
          </div>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">備考<textarea data-client-field="notes" rows="3">${escapeHtml(c.notes)}</textarea></label>
          <label class="client-form-row">タグ<input data-client-field="tags" value="${escapeHtmlAttr(c.tags)}" placeholder="カンマ区切りで入力" /></label>
          <div class="client-form-row"><span>初回登録日</span>
            <div class="client-date-time-row">
              <input type="date" data-client-field="firstRegisteredDate" value="${c.firstRegisteredDate || ""}" />
              <input type="time" data-client-field="firstRegisteredTime" value="${c.firstRegisteredTime || ""}" step="60" />
            </div>
          </div>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>適格請求書発行事業者</span>
            <div class="radio-group client-form-radio-group">
              <label><input type="radio" name="invoiceIssuer" value="未設定" ${c.invoiceIssuer === "未設定" ? "checked" : ""} data-client-field="invoiceIssuer" /><span>未設定</span></label>
              <label><input type="radio" name="invoiceIssuer" value="該当する" ${c.invoiceIssuer === "該当する" ? "checked" : ""} data-client-field="invoiceIssuer" /><span>該当する</span></label>
              <label><input type="radio" name="invoiceIssuer" value="該当しない" ${c.invoiceIssuer === "該当しない" ? "checked" : ""} data-client-field="invoiceIssuer" /><span>該当しない</span></label>
            </div>
          </div>
          <label class="client-form-row">適格請求書発行事業者の登録番号<input data-client-field="invoiceIssuerNumber" value="${escapeHtmlAttr(c.invoiceIssuerNumber)}" placeholder="T + 13桁の数字" /></label>
        </div>
      </div>
      <div class="composer-foot client-create-foot-actions">
        <button type="button" class="${ndaExportBtnClass}" data-client-export-doc="nda">NDAを出力（.docx）</button>
        <button type="button" class="${gyomuExportBtnClass}" data-client-export-doc="gyomu-itaku">業務委託基本契約書を出力（.docx）</button>
        ${isEdit && canManageMaster ? `<button type="button" class="inline-btn ghost" data-client-delete-request="1">削除申請</button>` : ""}
        ${isEdit || fromProject || canManageMaster ? `<button type="button" class="inline-btn" data-save-client="1">${isEdit ? "更新申請" : "登録"}</button>` : ""}
      </div>
    </aside>
  `;
}

/** ドメインのクライアント欄: 登録済みクライアント＋パートナーから選択（既存の任意値はフォールバック表示） */
function domainClientSelectOptionsHtml(selectedValue) {
  const t = selectedValue == null ? "" : String(selectedValue).trim();
  const seen = new Set();
  const clientNames = [];
  clients.forEach((c) => {
    if (c.salesHidden) return; // 営業に非表示のクライアントは候補から除外（選択中の任意値は下部フォールバックで保持）
    const n = String(c?.name ?? "").trim();
    if (!n || seen.has(n)) return;
    seen.add(n);
    clientNames.push(n);
  });
  const partnerSeen = new Set();
  const partnerNames = [];
  (uiState.partnersCache || []).forEach((p) => {
    if (p?.sales_hidden) return; // 営業に非表示のパートナーは候補から除外
    const n = partnerMasterDisplayLabel(p);
    if (!n || partnerSeen.has(n)) return;
    partnerSeen.add(n);
    partnerNames.push(n);
  });
  clientNames.sort((a, b) => a.localeCompare(b, "ja"));
  partnerNames.sort((a, b) => a.localeCompare(b, "ja"));
  const optionFor = (n) => `<option value="${escapeHtmlAttr(n)}" ${t === n ? "selected" : ""}>${escapeHtml(n)}</option>`;
  const parts = [`<option value="" ${t === "" ? "selected" : ""}>（未選択）</option>`];
  if (clientNames.length) {
    parts.push(`<optgroup label="クライアント">${clientNames.map(optionFor).join("")}</optgroup>`);
  }
  if (partnerNames.length) {
    parts.push(`<optgroup label="パートナー">${partnerNames.map(optionFor).join("")}</optgroup>`);
  }
  // 既存の任意値（"社内利用" 等、マスタ未登録）はフォールバックで保持
  if (t && !seen.has(t) && !partnerSeen.has(t)) {
    parts.push(`<optgroup label="その他">${optionFor(t)}</optgroup>`);
  }
  return parts.join("");
}

/** ドメイン日付欄の表示値: ISO(yyyy-mm-dd)ならスラッシュ表示、手入力途中など非ISOは素のまま残す */
function domainDateDisplayValue(v) {
  return isoYmdToSlashDisplay(v) || String(v || "");
}

/**
 * ドメイン編集モーダルの日付欄（ハイブリッド）。
 * - テキスト欄に「YYYY/MM/DD」を手入力（保存時/blur時に parseCsvDateToIsoYmd で ISO 正規化）
 * - 📅ボタンで隣接の非表示 type=date を showPicker、選択値は ISO で state へ
 */
function domainDateFieldHtml(field, label) {
  const d = uiState.domainCreate;
  const text = domainDateDisplayValue(d[field]);
  const iso = pgDateFieldToYmd(d[field]);
  return `
    <div class="client-form-row"><span>${label}</span>
      <div class="dm-date-input-wrap">
        <input class="dm-date-text" type="text" inputmode="numeric" data-domain-date-text="${field}" value="${escapeHtmlAttr(text)}" placeholder="YYYY/MM/DD" autocomplete="off" />
        <button type="button" class="dm-date-pick-btn" data-domain-date-open="${field}" aria-label="カレンダーを開く" title="カレンダーを開く">📅</button>
        <input class="dm-date-native" type="date" data-domain-date-picker="${field}" value="${escapeHtmlAttr(iso)}" tabindex="-1" aria-hidden="true" />
      </div>
    </div>`;
}

function renderDomainCreateModal() {
  if (!uiState.domainCreate) return "";
  const d = uiState.domainCreate;
  const isEdit = d.mode === "edit";
  const statusOptions = DOMAIN_STATUS_OPTIONS;
  const pwVisible = !!d.pwVisible;
  return `
    <div class="composer-backdrop client-create-backdrop" data-close-domain-create="1"></div>
    <aside class="composer-panel client-create-modal${spNoEnter("client-create-modal")}" role="dialog" aria-modal="true">
      <div class="composer-head">
        <div><h3>${isEdit ? "ドメインを編集" : "新しいドメインを登録"}</h3><p>ドメイン管理に反映されます</p></div>
        <button class="inline-btn ghost" data-close-domain-create="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="client-form-section">
          <div class="client-form-row"><span>状況</span>
            <select data-search-dropdown-off="1" data-domain-field="status">
              ${statusOptions.map((v) => `<option value="${v}" ${d.status === v ? "selected" : ""}>${v}</option>`).join("")}
            </select>
          </div>
          <div class="client-form-row"><span>登録業者</span>
            <select data-search-dropdown-off="1" data-domain-field="service" aria-label="登録業者">
              <option value="" ${!d.service ? "selected" : ""}>（未選択）</option>
              ${DOMAIN_SERVICE_OPTIONS.map((v) => `<option value="${escapeHtmlAttr(v)}" ${d.service === v ? "selected" : ""}>${escapeHtml(v)}</option>`).join("")}
              ${d.service && !DOMAIN_SERVICE_OPTIONS.includes(d.service) ? `<option value="${escapeHtmlAttr(d.service)}" selected>${escapeHtml(d.service)}</option>` : ""}
            </select>
          </div>
          <div class="client-form-row"><span>サービス</span>${domainServiceCheckboxGroupHtml(d.services)}</div>
          <label class="client-form-row">ドメイン<input data-domain-field="domain" value="${escapeHtmlAttr(d.domain)}" placeholder="example.com" /></label>
          <label class="client-form-row">サブドメイン<input data-domain-field="subdomain" value="${escapeHtmlAttr(d.subdomain)}" placeholder="xxxx.onamaeweb.jp など" /></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">ID（登録業者ログイン）<input data-domain-field="registrarId" value="${escapeHtmlAttr(d.registrarId)}" autocomplete="off" /></label>
          <div class="client-form-row"><span>パスワード</span>
            <div class="dm-pw-input-wrap">
              <input data-domain-field="registrarPassword" type="${pwVisible ? "text" : "password"}" value="${escapeHtmlAttr(d.registrarPassword)}" autocomplete="new-password" />
              <button type="button" class="dm-pw-toggle" data-domain-create-pw-toggle="1" title="${pwVisible ? "隠す" : "表示"}" aria-label="${pwVisible ? "パスワードを隠す" : "パスワードを表示"}">${pwVisible ? "🙈" : "👁"}</button>
            </div>
          </div>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>クライアント</span>
            <select data-domain-field="clientName" aria-label="クライアント">${domainClientSelectOptionsHtml(d.clientName)}</select>
          </div>
          <div class="client-form-row"><span>担当</span>
            <select data-domain-field="owner" aria-label="担当">${internalStaffSelectOptionsHtml(d.owner)}</select>
          </div>
          <label class="client-form-row">案件<input data-domain-field="projectNote" value="${escapeHtmlAttr(d.projectNote)}" placeholder="関連案件・用途メモ" /></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          ${domainDateFieldHtml("renewedDate", "更新日")}
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">備考 / メモ<textarea data-domain-field="notes" rows="3">${escapeHtml(d.notes)}</textarea></label>
        </div>
      </div>
      <div class="composer-foot client-create-foot-actions">
        ${isEdit ? `<button type="button" class="inline-btn ghost" data-domain-delete-request="1">削除</button>` : ""}
        <button type="button" class="inline-btn" data-save-domain="1">${isEdit ? "更新" : "登録"}</button>
      </div>
    </aside>
  `;
}

function renderUserEditorModal() {
  if (!uiState.userEditor) return "";
  const u = uiState.userEditor;
  const isEdit = Boolean(u.id);
  return `
    <div class="composer-backdrop" data-close-user-editor="1"></div>
    <aside class="composer-panel project-create-panel" data-user-editor-panel="1">
      <div class="composer-head">
        <div><h3>${isEdit ? "担当者を編集" : "新しい担当者を追加"}</h3><p>担当者マスタに反映されます</p></div>
        <button class="inline-btn ghost" data-close-user-editor="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="form-grid">
          <label>名字<input data-user-field="lastName" value="${escapeHtmlAttr(u.lastName || "")}" /></label>
          <label>名前<input data-user-field="firstName" value="${escapeHtmlAttr(u.firstName || "")}" /></label>
          <label>メールアドレス<input data-user-field="email" value="${escapeHtmlAttr(u.email || "")}" type="email" placeholder="example@company.co.jp" /></label>
          <label>システム権限<select data-search-dropdown-off="1" data-user-field="systemRole">${SYSTEM_ROLE_OPTIONS.map((r) => `<option value="${r}" ${systemRoleOfUser(u) === r ? "selected" : ""}>${r}</option>`).join("")}</select></label>
          <label>職種<select data-search-dropdown-off="1" data-user-field="role">${JOB_ROLE_OPTIONS.map((r) => `<option value="${r}" ${primaryJobRole(u) === r ? "selected" : ""}>${r}</option>`).join("")}</select></label>
        </div>
        <div style="margin-top:12px;">
          <label style="display:block;font-size:13px;color:#475569;margin-bottom:4px;">パスワード${isEdit ? "（変更する場合のみ）" : "（任意）"}</label>
          <div style="display:flex;gap:8px;align-items:stretch;">
            <input data-user-field="password" value="${escapeHtmlAttr(u.password || "")}" type="text" placeholder="${isEdit ? "変更する場合のみ入力" : "8文字以上 / 未入力なら招待で設定"}" autocomplete="new-password" style="flex:1;" />
            <button type="button" class="inline-btn ghost" data-generate-user-password="1" style="white-space:nowrap;">生成</button>
          </div>
          <p class="my-account-field-hint" style="margin-top:6px;">設定すると招待なしで「メール＋このパスワード」で直接ログインできます（8文字以上）。${isEdit ? "空欄なら現在のパスワードを維持します。" : ""}</p>
        </div>
        <div style="margin-top:12px;">
          <label style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <input type="checkbox" data-user-field="onLeave" ${u.onLeave ? "checked" : ""} />
            <span>休職</span>
          </label>
          <label style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <input type="checkbox" data-user-field="retired" ${u.retired ? "checked" : ""} />
            <span>退職</span>
          </label>
          <label style="display:flex;align-items:center;gap:8px;">
            <input type="checkbox" data-user-field="searchExcluded" ${u.searchExcluded ? "checked" : ""} />
            <span>担当者検索から除外（案件一覧の絞り込み・案件の担当営業候補に出さない）</span>
          </label>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-user-editor="1">キャンセル</button>
        <button class="inline-btn" data-save-user="1">${isEdit ? "更新する" : "作成する"}</button>
      </div>
    </aside>
  `;
}

function splitUserName(fullName) {
  const raw = String(fullName || "").trim();
  if (!raw) return { lastName: "", firstName: "" };
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return { lastName: parts[0], firstName: parts.slice(1).join(" ") };
  }
  return { lastName: raw, firstName: "" };
}

function isAllowedAccountEmail(email) {
  const norm = String(email || "").trim().toLowerCase();
  if (!norm) return true;
  const domain = norm.split("@")[1] || "";
  return domain === "o-eighty.com" || domain === "o-eighty.co.jp";
}

function userNameWithRetiredLabel(user) {
  const name = user?.name || "";
  if (!name) return "";
  const tags = [];
  if (user?.onLeave) tags.push("休職中のユーザー");
  if (user?.retired) tags.push("退職したユーザー");
  return tags.length ? `${name}（${tags.join("・")}）` : name;
}

/** ユーザーの所属部署名（部署設定の memberIds から逆引き）。未所属・未ロードなら空文字。
 *  memberIds には旧形式（"12"＝生DB id。api/users.js detachUserFromAllDepartments が両形式を
 *  掃除している）が残りうるため、"u12" で見つからなければ dbId でも引く。 */
function userDepartmentName(user) {
  const dept =
    getDepartmentByMemberId(String(user?.id || "")) ||
    (user?.dbId != null ? getDepartmentByMemberId(String(user.dbId)) : null);
  return String(dept?.name || "").trim();
}

/** 担当者・メンション候補の表示名「名前（部署名）」。同姓同名や似た名前の選び間違い防止用 */
function userNameWithDept(user) {
  const name = String(user?.name || "（無名）");
  const dname = userDepartmentName(user);
  return dname ? `${name}（${dname}）` : name;
}

/** 契約管理・クライアントマスタ共通の社内担当（value は表示ラベルと同一）。先頭に未選択可。 */
function internalStaffSelectOptionsHtml(selectedValue) {
  const t = selectedValue == null ? "" : String(selectedValue).trim();
  const au = assignableUsers();
  const opts = [`<option value="" ${t === "" ? "selected" : ""}>（未選択）</option>`];
  au.forEach((u) => {
    const label = userNameWithRetiredLabel(u);
    const sel = t === label || t === u.name ? "selected" : "";
    opts.push(`<option value="${escapeHtmlAttr(label)}" ${sel}>${escapeHtml(label)}</option>`);
  });
  if (t && !au.some((u) => t === userNameWithRetiredLabel(u) || t === u.name)) {
    opts.push(`<option value="${escapeHtmlAttr(t)}" selected>${escapeHtml(t)}</option>`);
  }
  return opts.join("");
}

// isValidMyAccountPhone は app-src/00-shared-enums.js へ移設した
// （サーバ api/me.js の保存検証と同じ判定を使うため。ここに再定義すると二重宣言で起動不能になる）

function renderMyAccountModal() {
  if (!uiState.myAccountEditor) return "";
  const m = uiState.myAccountEditor;
  // src は escapeHtmlAttr を通す（保存値がそのまま属性に入ると `x" onerror="..."` で属性を抜けられる）。
  // 他ユーザーのアイコンを描く経路（12-summary.js の sidebar / progressUserAvatarHtml）は既に同じ形。
  const avatarView = m.avatar
    ? `<img src="${escapeHtmlAttr(m.avatar)}" alt="avatar" class="my-account-avatar-img" />`
    : `<span class="my-account-avatar-fallback">🐱</span>`;
  const panelClass = "composer-panel fp-drawer-panel fp-my-account-panel";
  const backdropClass = "composer-backdrop fp-overlay-backdrop--sp";
  return `
    <div class="${backdropClass}" data-close-my-account="1"></div>
    <aside class="${panelClass}">
      <div class="composer-head">
        <div><h3>${escapeHtml(currentUser.name || "アカウント設定")}</h3><p>アイコン・表示名・コメント・電話番号・パスワードを変更できます</p></div>
        <button type="button" class="inline-btn ghost fp-modal-close" data-close-my-account="1" aria-label="閉じる">閉じる</button>
      </div>
      <div class="composer-body my-account-form">
        <div class="my-account-avatar-block">
          <div class="my-account-avatar">${avatarView}</div>
          <label class="my-account-image-btn" style="width:fit-content;">
            <span>+画像を選択</span>
            <input type="file" accept="image/*" data-my-account-avatar="1" style="display:none;" />
          </label>
        </div>
        <div class="my-account-gap-row"></div>
        <div class="my-account-row my-account-profile-row">
          <label>表示名
            <input data-my-account-field="displayName" value="${escapeHtmlAttr(m.displayName || "")}" maxlength="${MY_ACCOUNT_PROFILE_LIMITS.displayName}" placeholder="表示名を入力" />
          </label>
        </div>
        <div class="my-account-row">
          <label>コメント
            <input data-my-account-field="comment" value="${escapeHtmlAttr(m.comment || "")}" maxlength="${MY_ACCOUNT_PROFILE_LIMITS.comment}" placeholder="コメントを入力" />
          </label>
        </div>
        <div class="my-account-row">
          <label>電話番号
            <input type="tel" data-my-account-field="phone" value="${escapeHtmlAttr(m.phone || "")}" maxlength="${MY_ACCOUNT_PROFILE_LIMITS.phone}" placeholder="例：03-1234-5678、090-1234-5678" inputmode="tel" autocomplete="tel" />
          </label>
          <p class="my-account-field-hint">ハイフンあり／なしどちらでも可。未入力可。+81からの国際表記も可。</p>
        </div>
        <div class="my-account-row">
          <label>現在のパスワード
            <input type="password" data-my-account-field="currentPassword" value="${escapeHtmlAttr(m.currentPassword || "")}" placeholder="現在のパスワード" autocomplete="new-password" />
          </label>
        </div>
        <div class="form-grid my-account-row">
          <label>新しいパスワード
            <input type="password" data-my-account-field="newPassword" value="${escapeHtmlAttr(m.newPassword || "")}" placeholder="新しいパスワード" autocomplete="new-password" />
          </label>
          <label>新しいパスワード（確認）
            <input type="password" data-my-account-field="confirmPassword" value="${escapeHtmlAttr(m.confirmPassword || "")}" placeholder="もう一度入力" autocomplete="new-password" />
          </label>
        </div>
        <div class="my-account-notify-section">
          <div class="my-account-notify-title">通知の受け取り</div>
          <div class="pj-muted" style="font-size:12px;margin:-4px 0 8px;">自分宛のメンション・ToDoの担当者設定・自分宛の申請・申請の承認状況（承認/ステップ前進/却下）が届きます</div>
          <div class="my-account-notify-row">
            <span>メールで受け取る${fpSaveStatusHtml("notify:email")}</span>
            <input type="checkbox" class="fp-switch" data-notify-pref="email" ${currentUser.notifyEmail !== false ? "checked" : ""} />
          </div>
          <div class="my-account-notify-row">
            <span>Slackで受け取る${fpSaveStatusHtml("notify:slack")}</span>
            <input type="checkbox" class="fp-switch" data-notify-pref="slack" ${currentUser.notifySlack ? "checked" : ""} />
          </div>
          <!-- PWA push はいったん非表示（機能自体は残置） -->
          <div class="my-account-notify-row" style="display:none">
            <span class="my-account-notify-label">プッシュで受け取る（スマホ）<span class="fp-tip" tabindex="0" role="note" aria-label="プッシュはこの端末への通知許可が必要で、iPhoneは「ホーム画面に追加」したアプリでのみ利用できます。"><span class="fp-tip-mark">?</span><span class="fp-tip-bubble">プッシュはこの端末への通知許可が必要で、iPhoneは「ホーム画面に追加」したアプリでのみ利用できます。</span></span></span>
            <input type="checkbox" class="fp-switch" data-notify-pref="push" ${currentUser.notifyPush ? "checked" : ""} />
          </div>
        </div>
        <div class="my-account-notify-section">
          <div class="my-account-notify-title">Claude 連携（MCP）</div>
          ${renderMyAccountMcpSection(m)}
        </div>
      </div>
      <div class="my-account-security-section">
        <div class="my-account-security-title">セキュリティ</div>
        <div class="my-account-security-row">
          <div class="my-account-security-info">
            <div class="my-account-security-label">全デバイスからログアウト</div>
            <div class="my-account-security-desc">このアカウントでログイン中の全デバイスのセッションを無効にします。</div>
          </div>
          <button type="button" class="inline-btn ghost" data-revoke-all-sessions="1">実行する</button>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-my-account="1">キャンセル</button>
        <button class="inline-btn" data-save-my-account="1">保存する</button>
      </div>
    </aside>
  `;
}

function renderDocStartModal() {
  if (!uiState.docStart) return "";
  const d = uiState.docStart;
  const isQuote = d.type === "quote";
  // 「過去の見積/請求書を複製する」は選択後に実データを何もコピーしない未実装の入口だったため、
  // 嘘の成功トースト（「複製して開始しました」）だけが出る事故を避けて一旦外す
  const methods = isQuote
    ? [
        { id: "blank", label: "まっさらから作る" },
        { id: "template", label: "テンプレートから作る" }
      ]
    : [
        { id: "blank", label: "まっさらから作る" },
        { id: "template", label: "テンプレートから作る" },
        { id: "quote-generate", label: "見積から生成する" }
      ];
  const candidates = getTemplatesByType(d.type);
  const list = candidates
    .map(
      (t) => `
      <button class="mode-card ${d.templateId === t.id ? "active" : ""}" data-doc-template="${t.id}">
        <strong>${escapeHtml(t.name)}</strong>
        <span>${escapeHtml(t.description)}</span>
        <span>${t.scope === "private" ? "自分専用" : "共通"} / ${t.isDefault ? "デフォルト" : "通常"}</span>
      </button>
    `
    )
    .join("");
  return `
    <div class="composer-backdrop" data-close-doc-start="1"></div>
    <aside class="composer-panel project-create-panel">
      <div class="composer-head">
        <div><h3>${isQuote ? "見積作成方法を選択" : "請求作成方法を選択"}</h3><p>作成方法を選んでからフォームを開きます</p></div>
        <button class="inline-btn ghost" data-close-doc-start="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="create-mode-grid">
          ${methods
            .map((m) => `<button class="mode-card ${d.method === m.id ? "active" : ""}" data-doc-method="${m.id}"><strong>${m.label}</strong></button>`)
            .join("")}
        </div>
        ${d.method === "template" ? `<div class="section"><h4>テンプレートを選択</h4><div class="create-mode-grid">${list}</div></div>` : ""}
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-doc-start="1">キャンセル</button>
        <button class="inline-btn" data-doc-start-open="1">この方法で開始</button>
      </div>
    </aside>
  `;
}

function authPage() {
  return `
    <section class="auth-shell">
      <div class="auth-brand">
        <h1>On boarding</h1>
        <div class="ai-mode-badge">
          <span class="ai-mode-badge__icon">✦</span>
          <span class="ai-mode-badge__text">＋AI mode</span>
        </div>
      </div>
      <article class="auth-card">
        <h2>ログイン</h2>
        <button class="auth-submit" data-auth-action="login">ログイン</button>
        <p class="auth-hint">一時対応: ID/パスワードなしでログインできます。</p>
      </article>
    </section>
  `;
}

function renderTemplateEditorModal() {
  if (!uiState.templateEditor) return "";
  const t = uiState.templateEditor;
  return `
    <div class="composer-backdrop" data-close-template-editor="1"></div>
    <aside class="composer-panel project-create-panel">
      <div class="composer-head">
        <div><h3>${t.id ? "テンプレートを編集" : "新規テンプレート作成"}</h3><p>用途ごとに複数テンプレートを運用できます</p></div>
        <button class="inline-btn ghost" data-close-template-editor="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="form-grid">
          <label>テンプレート名<input data-template-field="name" value="${escapeHtmlAttr(t.name)}" /></label>
          <label>用途メモ<input data-template-field="description" value="${escapeHtmlAttr(t.description)}" /></label>
          <label>公開範囲<select data-template-field="scope"><option value="private" ${t.scope === "private" ? "selected" : ""}>自分専用</option><option value="shared" ${t.scope === "shared" ? "selected" : ""}>チーム共通</option></select></label>
          <label>デフォルト設定<select data-template-field="isDefault" ${t.scope === "shared" ? "" : `disabled title="デフォルトにできるのはチーム共通のテンプレートだけです"`}><option value="false" ${!t.isDefault ? "selected" : ""}>通常</option><option value="true" ${t.isDefault ? "selected" : ""}>デフォルト</option></select></label>
          <label>初期件名<input data-template-field="subject" value="${escapeHtmlAttr(t.preset.subject)}" /></label>
          <label>税設定<select data-template-field="taxRate"><option value="0.1" ${Number(t.preset.taxRate) === 0.1 ? "selected" : ""}>10%</option><option value="0.08" ${Number(t.preset.taxRate) === 0.08 ? "selected" : ""}>8%</option></select></label>
          <label>有効期限/支払期限(日)<input data-template-field="dueDays" value="${t.preset.dueDays}" /></label>
          <label>初期振込メモ<input data-template-field="bankMemo" value="${escapeHtmlAttr(t.preset.bankMemo)}" /></label>
        </div>
        <label>初期備考<textarea data-template-field="note">${escapeHtml(t.preset.note)}</textarea></label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-template-editor="1">キャンセル</button>
        <button class="inline-btn" data-template-save="1" ${uiState.docTemplateSaving ? "disabled" : ""}>保存する</button>
      </div>
    </aside>
  `;
}


// ────────────────────────────────────────────────────────────────
// 取込プレビュー「ひとつにまとめる」
// PDF等の摘要ごとの行出しはそのまま残し、ボタンで明細行（消費税キャプション行を除く）を
// 1行に合算して登録できる。金額は合算、摘要は「 ／ 」区切りで連結。
// まとめる前の行構成は mergeBackupRows に保持し「まとめを解除」で戻せる。
// ────────────────────────────────────────────────────────────────

function mergeCostImportPreviewRowsIntoOne(rev) {
  if (!rev || !Array.isArray(rev.rows)) return 0;
  const mergeable = rev.rows.filter((r) => r.importCaptionKind !== "consumption_tax");
  if (mergeable.length < 2) return 0;
  const taxRows = rev.rows.filter((r) => r.importCaptionKind === "consumption_tax");
  const sumEx = mergeable.reduce((a, r) => a + (Math.round(Number(r.amountExTax ?? 0)) || 0), 0);
  const sumTax = mergeable.reduce((a, r) => a + (Math.round(Number(r.consumptionTax ?? 0)) || 0), 0);
  const labels = mergeable
    .map((r) => {
      const t = r.isManualPreviewRow || r.manualSummaryText != null
        ? String(r.manualSummaryText || "").trim()
        : stripInvoiceTableMergedNoise(String(r.summaryCaptionRead || "").trim()) ||
          (String(r.nameColumnText || "").trim() !== "—" ? String(r.nameColumnText || "").trim() : "") ||
          String(r.clientHint || "").trim();
      return t.replace(/\s+/g, " ").trim();
    })
    .filter(Boolean);
  const joined = labels.join(" ／ ").slice(0, 520);
  const first = mergeable[0];
  const merged = {
    ...first,
    key: `ir-merged-${Date.now()}`,
    amountExTax: sumEx,
    consumptionTax: sumTax,
    // 元の取込額は現在の税区分での合計（税込なら税込合計）。税抜合計を入れると、まとめた後に税込/税抜を
    // 切り替えたとき税抜合計を基準に分け直し、金額が約1割ずれていた
    importedRawAmount: rev.importTaxMode === "exclusive" ? sumEx : sumEx + sumTax,
    summaryCaptionRead: joined,
    // まとめ行は摘要を手入力行と同様に編集可能にする（manualSummaryText を表示・保存の元にする）
    isMergedPreviewRow: true,
    manualSummaryText: joined,
    autoNote: mergeable.map((r) => String(r.autoNote || "").trim()).filter(Boolean).join(" / "),
    reviewNote: mergeable.map((r) => String(r.reviewNote || "").trim()).filter(Boolean).join(" / "),
    note: `明細${mergeable.length}件を合算`,
    // 先頭行由来のバッジが「まとめ行」に誤って残らないようクリア
    accountNameKey: "",
    accountNameCount: 0,
    duplicateOfExisting: false
  };
  rev.mergeBackupRows = rev.rows;
  rev.rows = [merged, ...taxRows];
  return mergeable.length;
}

// 新機能のイベントは 16-render-routing.js の巨大 click ハンドラに足さず独立リスナーで持つ
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  const rev = uiState.costRegWizard;
  if (!rev || rev.phase !== "preview" || rev.registering) return;
  if (target.closest("[data-cost-preview-merge-rows]")) {
    syncCostImportPreviewRowsFromDom(rev);
    const n = mergeCostImportPreviewRowsIntoOne(rev);
    if (!n) {
      showToast("まとめられる明細行が2行未満です");
      return;
    }
    showToast(`明細${n}行を1行にまとめました。摘要と金額を確認してください`);
    render();
    return;
  }
  if (target.closest("[data-cost-preview-merge-undo]")) {
    if (!Array.isArray(rev.mergeBackupRows) || !rev.mergeBackupRows.length) return;
    rev.rows = rev.mergeBackupRows;
    rev.mergeBackupRows = null;
    showToast("まとめを解除しました");
    render();
    return;
  }
});
// ────────────────────────────────────────────────────────────────
// サマリー計算: 各ステージページのサマリー数字を実データから算出
// ────────────────────────────────────────────────────────────────
function contractsSummary() {
  const notDone = projects.filter((p) => !p.contractDone).length;
  const ndaNotCreated = projects.filter((p) => p.docs.nda === "未作成").length;
  const waiting = projects.filter((p) => p.docs.contract === "確認待ち" || p.docs.nda === "確認待ち").length;
  const done = projects.filter((p) => p.contractDone).length;
  return [
    { label: "契約未対応", value: `${notDone}件`, tone: notDone > 0 ? "warning" : "" },
    { label: "NDA未登録", value: `${ndaNotCreated}件`, tone: ndaNotCreated > 0 ? "danger" : "" },
    { label: "確認待ち", value: `${waiting}件` },
    { label: "締結済み", value: `${done}件`, tone: done > 0 ? "safe" : "" }
  ];
}

function quotesSummary() {
  const notCreated = projects.filter((p) => p.docs.quote === "未作成").length;
  const draft = projects.filter((p) => p.docs.quote === "下書きあり").length;
  const ready = projects.filter((p) => p.docs.quote === "送付準備OK").length;
  const sent = projects.filter((p) => p.docs.quote === "送付済み" || p.docs.quote === "確認待ち").length;
  return [
    { label: "未作成", value: `${notCreated}件`, tone: notCreated > 0 ? "danger" : "" },
    { label: "下書きあり", value: `${draft}件`, tone: draft > 0 ? "warning" : "" },
    { label: "送付準備OK", value: `${ready}件` },
    { label: "送付済み", value: `${sent}件`, tone: sent > 0 ? "safe" : "" }
  ];
}

function ordersSummary() {
  const orderProjects = projects.filter((p) => p.useOrderFlow);
  const notCreated = orderProjects.filter((p) => p.docs.order === "未作成").length;
  const draft = orderProjects.filter((p) => p.docs.order === "下書きあり").length;
  const sent = orderProjects.filter((p) => p.docs.order === "送付済み").length;
  const skipped = projects.filter((p) => !p.useOrderFlow).length;
  return [
    { label: "未対応", value: `${notCreated}件`, tone: notCreated > 0 ? "warning" : "" },
    { label: "下書きあり", value: `${draft}件` },
    { label: "送付済み", value: `${sent}件`, tone: sent > 0 ? "safe" : "" },
    { label: "不要（スキップ）", value: `${skipped}件` }
  ];
}

function paymentsSummary() {
  const waiting = projects.filter((p) => p.invoiceStatus === "入金待ち");
  const waitingAmount = waiting.reduce((sum, p) => sum + p.amount, 0);
  const longPending = projects.filter((p) => p.handoffStatus === "経理確認待ち" && p.invoiceStatus === "入金待ち").length;
  const confirmed = projects.filter((p) => p.invoiceStatus === "入金確認済み").length;
  return [
    { label: "入金待ち", value: `${waiting.length}件`, tone: waiting.length > 0 ? "warning" : "" },
    { label: "入金待ち金額", value: yen.format(waitingAmount), tone: waitingAmount > 0 ? "danger" : "" },
    { label: "長期未確認", value: `${longPending}件`, tone: longPending > 0 ? "danger" : "" },
    { label: "入金確認済み", value: `${confirmed}件`, tone: confirmed > 0 ? "safe" : "" }
  ];
}

function currentRoute() {
  const raw = location.hash.replace(/^#/, "");
  if (!raw) return { page: "dashboard", sidebarKey: "dashboard", searchParams: new URLSearchParams() };
  const qm = raw.indexOf("?");
  const path = qm >= 0 ? raw.slice(0, qm) : raw;
  const q = new URLSearchParams(qm >= 0 ? raw.slice(qm + 1) : "");

  if (path.startsWith("set-password:")) return { page: "set-password", sidebarKey: "dashboard", id: path.split(":")[1], searchParams: q };
  if (path.startsWith("project-detail:")) {
    const id = path.split(":")[1] || "";
    const tabRaw = (q.get("tab") || "").trim().toLowerCase();
    let detailTab = "billing";
    if (tabRaw === "cost") detailTab = "cost";
    else if (tabRaw === "billing") detailTab = "billing";
    else if (tabRaw === "overview") detailTab = "overview";
    else if (tabRaw === "comments") detailTab = "comments";
    else if (tabRaw === "result") detailTab = "overview";
    // 原価割当の「まとめて請求書を作成」から遷移した直後だけ、書類画面の請求書タブへ進める（15-sp-backlog.js）
    const autoComposeInvoice = q.get("composeInvoice") === "1";
    return { page: "project-detail", sidebarKey: "projects", id, detailTab, autoComposeInvoice, searchParams: q };
  }
  if (path.startsWith("project-docs:")) {
    const [, id = "", tab = "quote"] = path.split(":");
    return { page: "project-docs", sidebarKey: "projects", id, tab, searchParams: q };
  }
  // メール作成画面（書類のメール送信）。docs= に送る書類をカンマ区切りで持つ
  if (path.startsWith("project-mail:")) {
    const [, id = ""] = path.split(":");
    return { page: "project-mail", sidebarKey: "projects", id, docs: q.get("docs") || "invoice", searchParams: q };
  }
  if (path === "projects" && q.get("new") === "1") {
    return { page: "projects", sidebarKey: "projects", openProjectCreate: true, searchParams: q };
  }
  // 契約管理メニューは内部的に contracts 画面へ寄せて同一ハンドラを使う（サイドバー強調は contract-management を維持）
  if (path === "contract-management") {
    return { page: "contracts", sidebarKey: "contract-management", searchParams: q };
  }
  if (path === "progress-management") {
    return { page: "progress-management", sidebarKey: "progress-management", searchParams: q };
  }
  // 原価照合（経理の月末チェック）: 原価登録ページのサブ画面。サイドバー強調は cost-management に寄せる
  if (path === "cost-reconcile") {
    return { page: "cost-reconcile", sidebarKey: "cost-management", searchParams: q };
  }
  // 取引先一覧（統合）：クライアント／パートナーは同一メニュー（client-list）配下のタブ。
  // ページIDは既存のまま（フィルタ等の data ハンドラが currentRoute().page で判定するため）残し、
  // サイドバー強調だけ client-list に寄せて 1 メニューがどちらのタブでもハイライトされるようにする。
  if (path === "partner-list") {
    return { page: "partner-list", sidebarKey: "client-list", searchParams: q };
  }
  // その他（営業非表示・管理部のみ）タブ。サイドバー強調は client-list に寄せる
  if (path === "client-others") {
    return { page: "client-others", sidebarKey: "client-list", searchParams: q };
  }
  // 取引先詳細ページ（右スライドの置き換え）: #client-detail:client:12?tab=docs
  // タブは info(基本情報・既定) / people(担当者・支社) / docs(契約書類) / other-docs(その他書類) / comments(コメント)。案件詳細の ?tab= と同じ作法
  if (path.startsWith("client-detail:")) {
    const [, typeRaw = "client", idRaw = ""] = path.split(":");
    const entityType = typeRaw === "partner" ? "partner" : "client";
    const tabRaw = (q.get("tab") || "").trim().toLowerCase();
    const detailTab = ["people", "orders", "docs", "other-docs", "comments"].includes(tabRaw) ? tabRaw : "info";
    return { page: "client-detail", sidebarKey: "client-list", entityType, id: idRaw, detailTab, searchParams: q };
  }
  return { page: path, sidebarKey: path, searchParams: q };
}

function isAccountSettingsRoute() {
  const page = currentRoute().page;
  return page === "account-settings" || page === "account-admin" || page === "approval-settings";
}

// N営業日計算（getNthBusinessDayOfMonth）は 00-shared-enums.js に移設。
// 土日祝を除く定義をサーバ（api/_projectLock.js）と共有するため、ここには再定義しないこと。

function applySidebarUserMenuState() {
  if (!sidebarUserRoot || !sidebarUserToggle) return;
  sidebarUserRoot.classList.toggle("open", uiState.sidebarUserMenuOpen);
  sidebarUserToggle.setAttribute("aria-expanded", String(uiState.sidebarUserMenuOpen));
}

function applySidebarNotifyMenuState() {
  if (!sidebarUserRoot) return;
  sidebarUserRoot.classList.toggle("notify-open", uiState.sidebarNotifyMenuOpen);
  if (sidebarNotifyToggle) {
    sidebarNotifyToggle.setAttribute("aria-expanded", String(uiState.sidebarNotifyMenuOpen));
  }
}

/** SP専用: ベルアイコン直下の通知パネルを表示・非表示 */
function applyMobileNotifyPanel() {
  if (typeof window === "undefined" || window.innerWidth > 920) return;
  let panel = document.getElementById("fpMobileNotifyPanel");
  if (!panel) {
    panel = document.createElement("div");
    panel.id = "fpMobileNotifyPanel";
    panel.className = "fp-mobile-notify-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "お知らせ");
    document.body.appendChild(panel);
  }
  const isOpen = !!uiState.sidebarNotifyMenuOpen;
  panel.hidden = !isOpen;
  if (!isOpen) return;

  // サイドバーと同じ通知アイテムをパネルに反映
  const items = collectSidebarNotificationItems();
  const headerHtml = `<div class="fp-mobile-notify-panel-head">
    <span class="fp-mobile-notify-panel-title">お知らせ</span>
    <button type="button" class="fp-mobile-notify-panel-close" data-fp-mobile-notify-close="1" aria-label="閉じる">×</button>
  </div>`;
  const bodyHtml = items.length === 0
    ? `<p class="fp-mobile-notify-panel-empty">新しい通知はありません</p>`
    : items.map((it) => notifyItemHtml(it, "mobile")).join("");
  panel.innerHTML = headerHtml + `<div class="fp-mobile-notify-panel-body">${bodyHtml}</div>`;
}

function updateNotifyBadgeElement(el, disp) {
  if (!el) return;
  const show = Boolean(disp);
  el.hidden = !show;
  el.setAttribute("aria-hidden", show ? "false" : "true");
  el.textContent = show ? disp : "";
}

function refreshSidebarUserChrome() {
  if (sidebarUserName) sidebarUserName.textContent = currentUser.name || "ユーザー";
  if (sidebarUserAvatar) {
    sidebarUserAvatar.innerHTML = currentUser.avatar
      ? `<img src="${escapeHtmlAttr(currentUser.avatar)}" alt="${escapeHtmlAttr(currentUser.name)}" class="sidebar-user-avatar-img" />`
      : "🐱";
  }
  // バッジとメニューを同一ソースで計算。バッジは未読のみ（informational＝担当課題総数は除外）で、0 のときは出さない
  const items = collectSidebarNotificationItems();
  const total = notifyBadgeCountFromItems(items);
  const hasNotify = total > 0;
  const disp = hasNotify ? formatNavBadgeDisplay(total) : "";
  updateNotifyBadgeElement(sidebarNotifyBadge, disp);
  updateNotifyBadgeElement(document.getElementById("fpTopbarNotifyBadge"), disp);
  if (sidebarNotifyToggle) {
    sidebarNotifyToggle.classList.toggle("has-notify", hasNotify);
    sidebarNotifyToggle.setAttribute(
      "aria-label",
      hasNotify ? `通知（未読 ${disp || total} 件）` : "通知"
    );
  }
  const topbarNotifyBtn = document.getElementById("fpTopbarNotifyBtn");
  if (topbarNotifyBtn) {
    topbarNotifyBtn.setAttribute("aria-expanded", String(!!uiState.sidebarNotifyMenuOpen));
    topbarNotifyBtn.setAttribute(
      "aria-label",
      hasNotify ? `通知（未読 ${disp || total} 件）` : "通知"
    );
  }
  if (sidebarNotifyMenu) {
    sidebarNotifyMenu.innerHTML = items.length
      ? items.map((it) => notifyItemHtml(it, "sidebar")).join("")
      : '<p class="sidebar-notify-empty">新しい通知はありません</p>';
  }
  // 在席ブロック（自分の状態＋ステータス選択＋全員の在席状況）。中身は 15o-presence.js が持つ
  if (typeof fpPresenceRenderSidebar === "function") fpPresenceRenderSidebar();
  applySidebarUserMenuState();
  applySidebarNotifyMenuState();
  applyBrowserTabNotificationTitle();
}

function applySidebarCollapsedClass() {
  if (!appLayout || !uiState.auth?.isLoggedIn || uiState.auth?.isLoading) return;
  const collapsed = !!uiState.sidebarNavCollapsed;
  appLayout.classList.toggle("sidebar-collapsed", collapsed);
  if (collapsed && (uiState.sidebarUserMenuOpen || uiState.sidebarNotifyMenuOpen)) {
    uiState.sidebarUserMenuOpen = false;
    uiState.sidebarNotifyMenuOpen = false;
    applySidebarUserMenuState();
    applySidebarNotifyMenuState();
  }
  document.querySelectorAll("[data-sidebar-collapse-toggle]").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const expanded = !uiState.sidebarNavCollapsed;
    el.setAttribute("aria-expanded", String(expanded));
    el.setAttribute("aria-label", expanded ? "メニューをしまう" : "メニューを広げる");
  });
}

/** 承認フロー ID に対応する申請フローオブジェクト（未知の ID のときは最小形。部署設定は参照しない） */
function getDeptLikeObjectForApprovalFlowId(flowId) {
  const fid = String(flowId || "").trim();
  const flow = (uiState.approvalOtherFlows || []).find((f) => String(f.id) === fid);
  return flow || { id: fid, memberIds: [] };
}

/** 捺印「申請中」1件について、現在ユーザーがこの段階の承認者か（順番承認） */
function isCurrentUserSealApproverForPendingKey(mapKey) {
  if (!currentUser?.id) return false;
  const colon = String(mapKey).indexOf(":");
  if (colon < 1) return false;
  const pid = String(mapKey).slice(0, colon);
  const tab = String(mapKey).slice(colon + 1);
  if (!docTabHasSealWorkflow(tab)) return false;
  if (getDocSealStatus(pid, tab) !== "申請中") return false;
  const cur = getCurrentSealApproverUserForMapKey(mapKey);
  if (!cur?.id) return false;
  if (String(cur.id) === String(currentUser.id)) return true;
  if (currentUser.dbId != null && cur.dbId != null && Number(cur.dbId) === Number(currentUser.dbId)) return true;
  return false;
}

/** 自分が承認対象となっている捺印申請中の件数（捺印対象の全書類） */
function countSealApplicationsAwaitingCurrentUserApproval() {
  let n = 0;
  for (const k of listPendingSealApplicationKeys()) {
    if (!isCurrentUserSealApproverForPendingKey(k)) continue;
    n += 1;
  }
  return n;
}

/** 通知種別アイコン（アクター不明＝アバター無しのときに色付き丸で表示） */
const NOTIFY_TYPE_ICONS = {
  approval: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0Zm3.25 5.5a.75.75 0 0 0-1.06 0L7 8.69 5.81 7.5a.75.75 0 0 0-1.06 1.06l1.75 1.75a.75.75 0 0 0 1.06 0l3.69-3.69a.75.75 0 0 0 0-1.06Z" fill="currentColor"/></svg>`,
  mention: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm5.46 4.25-.003.003-2.89 2.886a2.75 2.75 0 1 1-1.06-1.061l2.886-2.886.003-.003a.75.75 0 0 1 1.064 1.06ZM8 6.25a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z" fill="currentColor"/></svg>`,
  watch: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2C4.5 2 1.5 5.5 1.5 8S4.5 14 8 14s6.5-3.5 6.5-6S11.5 2 8 2ZM0 8c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8S0 12.418 0 8Zm8-2.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z" fill="currentColor"/></svg>`,
  assigned: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10.561 8.073a6.005 6.005 0 0 1 3.432 5.142.75.75 0 1 1-1.498.07 4.5 4.5 0 0 0-8.99 0 .75.75 0 0 1-1.498-.07 6.004 6.004 0 0 1 3.431-5.142 3.999 3.999 0 1 1 5.123 0ZM10.5 5a2.5 2.5 0 1 0-5 0 2.5 2.5 0 0 0 5 0Z" fill="currentColor"/></svg>`
};

function notifyItemKind(it) {
  if (it && it.kind) return it.kind;
  const id = String(it?.id || "");
  if (id.startsWith("app-mgmt")) return "approval";
  if (id.startsWith("todo-assigned")) return "assigned";
  if (id.startsWith("todo-mention")) return "mention";
  if (id.startsWith("todo-watch")) return "watch";
  return "other";
}

function notifyKindColor(kind) {
  if (kind === "assigned") return "#185fa5";
  if (kind === "mention") return "#534ab7";
  if (kind === "watch") return "#0f6e56";
  if (kind === "approval") return "#854f0b";
  return "#5f5e5a";
}

/** 通知のアクター（投稿者・更新者）に対応する users 行（アバター解決用） */
function notifyResolveActorUser(it) {
  const list = Array.isArray(users) ? users : [];
  const db = it?.actorUserId;
  if (db != null && Number.isFinite(Number(db))) {
    const n = Number(db);
    const byDb = list.find((u) => u && u.dbId != null && Number(u.dbId) === n);
    if (byDb) return byDb;
  }
  const nm = String(it?.actorName || "").trim();
  if (nm) {
    const byName = list.find((u) => u && String(u.name || "").trim() === nm);
    if (byName) return byName;
  }
  return null;
}

/** 通知1件のHTML（左下ベル / SPパネル共通）。variant: "sidebar" | "mobile" */
function notifyItemHtml(it, variant) {
  const kind = notifyItemKind(it);
  const color = notifyKindColor(kind);
  const isSidebar = variant === "sidebar";
  const btnClass = isSidebar ? "sidebar-notify-item" : "fp-mobile-notify-item";
  const tabAttr = it.appMgmtTab ? ` data-sidebar-notify-tab="${escapeHtmlAttr(it.appMgmtTab)}"` : "";
  const actorName = String(it.actorName || "").trim();
  let avatarHtml;
  if (actorName) {
    const actorUser = notifyResolveActorUser(it);
    avatarHtml = `<span class="fp-notify-avatar">${progressUserAvatarHtml(actorUser, "pm-assignee-avatar--notify", actorName)}</span>`;
  } else {
    avatarHtml = `<span class="fp-notify-avatar fp-notify-avatar--type" style="background:${color};color:#fff;">${NOTIFY_TYPE_ICONS[kind] || ""}</span>`;
  }
  const timeStr = it.at ? timeAgo(it.at) : (kind === "approval" ? "承認待ち" : "");
  const primary = actorName
    ? `<span class="fp-notify-actor">${escapeHtml(actorName)}</span> さんが${escapeHtml(it.action || "")}`
    : escapeHtml(it.label || "");
  const secondary = escapeHtml(it.context || it.detail || "");
  const countBadge = (!isSidebar && it.count > 1) ? `<span class="fp-mobile-notify-item-badge">${it.count}</span>` : "";
  return `<button type="button" class="${btnClass} fp-notify-item fp-notify-item--${kind}" data-sidebar-notify-go="${escapeHtmlAttr(it.hash)}"${tabAttr}>
    ${avatarHtml}
    <span class="fp-notify-text">
      <span class="fp-notify-line1"><span class="fp-notify-primary">${primary}</span>${timeStr ? `<span class="fp-notify-time">${escapeHtml(timeStr)}</span>` : ""}</span>
      ${secondary ? `<span class="fp-notify-secondary">${secondary}</span>` : ""}
    </span>
    ${countBadge}
  </button>`;
}

/** 左下ベル用: 通知一覧（種類と件数） */
function collectSidebarNotificationItems() {
  reconcileProgressNotifyCountsFromTree();
  const items = [];
  if (userCanAccessPage("application-management")) {
    // 契約承認の対象者はマスター/経理のみ（申請承認テーブル側の絞り＝「申請中」のみと揃える）
    const nContract = canManageClientPartnerMaster()
      ? contractMaster.filter((c) => c.approvalStatus === "申請中").length
      : 0;
    const nSeal = countSealApplicationsAwaitingCurrentUserApproval();
    if (nContract > 0) {
      items.push({
        id: "app-mgmt-contract",
        label: "取引承認（契約関係）",
        detail: `${nContract}件の承認待ち`,
        count: nContract,
        hash: "application-management",
        appMgmtTab: "contract"
      });
    }
    if (nSeal > 0) {
      items.push({
        id: "app-mgmt-seal",
        label: "捺印申請（書類）",
        detail: `${nSeal}件の承認待ち`,
        count: nSeal,
        hash: "application-management",
        appMgmtTab: "seal"
      });
    }
    // ツール許可・ロック解除・開発事前申請も、承認/却下するまでベルに残す（canApprove＝いま自分の承認番）
    const nTool = (uiState.allowedToolApplications || []).filter((a) => a && a.canApprove).length;
    if (nTool > 0) {
      items.push({
        id: "app-mgmt-tool",
        label: "ツールの許可申請",
        detail: `${nTool}件の承認待ち`,
        count: nTool,
        hash: "application-management",
        appMgmtTab: "tool"
      });
    }
    const nEquip = (uiState.equipmentApplications || []).filter((a) => a && a.canApprove).length;
    if (nEquip > 0) {
      items.push({
        id: "app-mgmt-equip",
        label: "備品の貸与申請",
        detail: `${nEquip}件の承認待ち`,
        count: nEquip,
        hash: "application-management",
        appMgmtTab: "equip"
      });
    }
    const nUnlock = (uiState.projectUnlockApplications || []).filter((a) => a && a.canApprove).length;
    if (nUnlock > 0) {
      items.push({
        id: "app-mgmt-unlock",
        label: "案件ロック解除申請",
        detail: `${nUnlock}件の承認待ち`,
        count: nUnlock,
        hash: "application-management",
        appMgmtTab: "unlock"
      });
    }
    const nDev = (uiState.developmentApplications || []).filter((a) => a && a.canApprove).length;
    if (nDev > 0) {
      items.push({
        id: "app-mgmt-dev",
        label: "自社ツール開発の事前申請",
        detail: `${nDev}件の承認待ち`,
        count: nDev,
        hash: "application-management",
        appMgmtTab: "development"
      });
    }
  }
  // 原価の異議（営業からの差し戻し）: 割当作業側（CS・マスター/管理者）に件数を通知。割当されると自動で消える
  if (userCanAccessPage("cost-allocation") && userSeesCostAllocationCsView()) {
    const nDispute = ensureOrphanCosts().filter((c) => c.disputeNote).length;
    if (nDispute > 0) {
      items.push({
        id: "cost-dispute",
        label: "原価への異議",
        detail: `${nDispute}件が営業から差し戻されています`,
        count: nDispute,
        hash: "cost-allocation"
      });
    }
  }
  // 原価の差し戻し（経理→営業）: 担当営業に件数を通知。修正して保存すると自動で消える
  if (userCanAccessPage("cost-allocation") && !userSeesCostAllocationCsView()) {
    let nRemand = 0;
    for (const p of projects) {
      if (!isMyProjectForCostReview(p)) continue;
      for (const c of p.costs || []) if (c.remandNote) nRemand++;
    }
    if (nRemand > 0) {
      items.push({
        id: "cost-remand",
        label: "原価の差し戻し",
        detail: `${nRemand}件が経理から差し戻されています`,
        count: nRemand,
        hash: "cost-allocation"
      });
    }
  }
  if (userCanAccessPage("progress-management")) {
    const treeLoaded = (uiState.progressChallenges || []).length > 0;
    // 新しく担当に設定されたら赤バッジ通知（未読・課題を開くと既読で消える）。
    const assignTargets = Array.isArray(uiState.progressAssignedToMeTargets) ? uiState.progressAssignedToMeTargets : [];
    for (const t of assignTargets) {
      const title = String(t.agendaTitle || "").trim() || "（無題）";
      items.push({
        id: `todo-assigned-${t.agendaId}`,
        kind: "assigned",
        actorName: t.actorName || "",
        actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
        action: "担当者に設定しました",
        context: title,
        at: t.assignedAt || null,
        label: "担当に設定",
        detail: `「${title}」の担当者になりました`,
        count: 1,
        hash: progressManagementIssueHash(t.challengeId, t.agendaId, t.todoGroupId)
      });
    }
    // 「担当課題」合計は総数を表示（未読とは別軸の情報表示・バッジ非加算）。
    // 閲覧で減る未読件数ではないため、課題を開いても件数は変わらず実数と一致する。
    const assignedTotal = normalizeSidebarNavBadgeCount(uiState.progressAssignedActiveTotal);
    if (assignedTotal > 0) {
      items.push({
        id: "todo-assigned-total",
        kind: "assigned",
        label: "担当課題（合計）",
        detail: `自分が担当の課題が${assignedTotal}件`,
        count: assignedTotal,
        informational: true,
        hash: "progress-management?assigned=me"
      });
    }
    const mentionTargets = listUnreadToDoMentionTargetsForNotify();
    if (mentionTargets.length) {
      for (const t of mentionTargets) {
        const title = String(t.agendaTitle || "").trim() || "（無題）";
        items.push({
          id: `todo-mention-${t.agendaId}`,
          kind: "mention",
          actorName: t.actorName || "",
          actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
          action: "あなたをメンションしました",
          context: title,
          at: t.latestAt || null,
          label: "メンション",
          detail: `「${title}」であなた宛`,
          count: 1,
          hash: progressManagementIssueHash(t.challengeId, t.agendaId)
        });
      }
    } else if (!treeLoaded) {
      const mentions = countToDoMentionsForCurrentUser();
      if (mentions > 0) {
        items.push({
          id: "todo-mention",
          label: "メンション",
          detail: `${mentions}件のコメントであなた宛`,
          count: mentions,
          hash: "progress-management"
        });
      }
    }
    const watchTargets = listUnreadWatchTargetsForNotify();
    if (watchTargets.length) {
      for (const t of watchTargets) {
        const title = String(t.agendaTitle || "").trim() || "（無題）";
        items.push({
          id: `todo-watch-${t.agendaId}`,
          kind: "watch",
          actorName: t.actorName || "",
          actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
          action: "課題を更新しました",
          context: title,
          at: t.latestAt || null,
          label: "ウォッチ中の更新",
          detail: `「${title}」に更新あり`,
          count: 1,
          hash: progressManagementIssueHash(t.challengeId, t.agendaId, t.todoGroupId)
        });
      }
    } else if (!treeLoaded) {
      const watch = normalizeSidebarNavBadgeCount(uiState.progressWatchUnreadCount);
      if (watch > 0) {
        items.push({
          id: "todo-watch",
          label: "ウォッチ中の更新",
          detail: `${watch}件のTODOに更新あり`,
          count: watch,
          hash: "progress-management"
        });
      }
    }
  }
  // 種類をまたいで新しい順に並べる（承認待ちは上・情報表示の「担当課題 合計」は下・それ以外は at 降順）。
  items.sort((a, b) => {
    const grp = (it) =>
      String(it.id || "").startsWith("app-mgmt-") ? 0 : it.informational ? 2 : 1;
    const ga = grp(a);
    const gb = grp(b);
    if (ga !== gb) return ga - gb;
    if (ga !== 1) return 0; // 承認待ち・情報表示はグループ内の順序を維持
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return tb - ta; // 新しいものが上
  });
  return items;
}

/** 未読バッジ用の合計。informational（担当課題の総数など・未読ではない情報表示）は除外する。 */
function notifyBadgeCountFromItems(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (sum, it) => (it && it.informational ? sum : sum + (Number(it.count) || 0)),
    0
  );
}

function sidebarNotificationTotalCount() {
  return notifyBadgeCountFromItems(collectSidebarNotificationItems());
}

function progressNotificationCount() {
  const assignTargets = Array.isArray(uiState.progressAssignedToMeTargets) ? uiState.progressAssignedToMeTargets : [];
  const assignN = assignTargets.length;
  const watch = normalizeSidebarNavBadgeCount(uiState.progressWatchUnreadCount);
  const mentions = countToDoMentionsForCurrentUser();
  return normalizeSidebarNavBadgeCount(assignN + watch + mentions);
}

/** 課題コメント本文に、自分宛の @メンション があるか（半角/全角 @） */
function progressMessageBodyMentionsCurrentUser(body) {
  const name = String(currentUser?.name || "").trim();
  if (!name) return false;
  const hay = String(body || "");
  if (!hay.includes("@") && !hay.includes("＠")) return false;
  const compact = name.replace(/\s+/g, "");
  const parts = name.split(/\s+/).filter(Boolean);
  const lows = hay.toLowerCase();
  const trials = [];
  for (const p of parts) {
    trials.push(("@" + p).toLowerCase());
    trials.push(("＠" + p).toLowerCase());
  }
  trials.push(("@" + compact).toLowerCase());
  trials.push(("＠" + compact).toLowerCase());
  return trials.some((t) => lows.includes(t));
}

function progressManagementIssueHash(challengeId, agendaId, groupId) {
  const sp = new URLSearchParams();
  const gid =
    groupId != null && String(groupId).trim() !== ""
      ? groupId
      : (uiState.progressChallenges || []).find((c) => String(c.id) === String(challengeId))?.todoGroupId;
  if (gid != null && String(gid).trim() !== "") sp.set("group", String(gid));
  sp.set("project", String(challengeId));
  sp.set("issue", String(agendaId));
  return `progress-management?${sp.toString()}`;
}

function applyProgressPayloadToState(payload) {
  const d = payload && typeof payload === "object" ? payload : {};
  const newChallenges = Array.isArray(d.challenges) ? d.challenges : [];
  reapplyProgressAgendaLocalReads(newChallenges);
  uiState.progressChallenges = newChallenges;
  uiState.progressTodoGroups = Array.isArray(d.groups) ? d.groups : [];
  // 新データで全キャッシュのチャレンジも同期（statuses/categories/テンプレート/agendas の巻き戻りを防止）
  for (const ch of newChallenges) {
    const id = Number(ch.id);
    if (!Number.isFinite(id)) continue;
    if (Array.isArray(ch.statuses)) {
      _syncChallengeSettingsInAllCaches(id, {
        statuses: ch.statuses,
        categories: ch.categories,
        timeTrackedStatuses: Array.isArray(ch.timeTrackedStatuses) ? ch.timeTrackedStatuses : null,
        hasTimeTrackedStatuses: true,
        requiredSteps: Array.isArray(ch.requiredSteps) ? ch.requiredSteps : null,
        hasRequiredSteps: true,
        workKind: typeof ch.workKind === "string" ? ch.workKind : ""
      });
      applyProgressAgendaTemplatesLocal(id, ch.agendaTemplates);
    }
    if (Array.isArray(ch.agendas)) {
      for (const ag of ch.agendas) {
        if (ag?.status == null) continue;
        _syncAgendaInAllCaches(ag.id, (a) => {
          a.status = ag.status;
          a.completedAt = ag.completedAt;
          a.updatedAt = ag.updatedAt;
        });
      }
    }
  }
}

function findProgressTodoGroup(groupId) {
  const gid = String(groupId || "").trim();
  if (!gid) return null;
  return (uiState.progressTodoGroups || []).find((g) => String(g.id) === gid) || null;
}

function progressChallengesInGroup(groupId) {
  const gid = Number(groupId);
  if (!Number.isFinite(gid) || gid < 1) return [];
  return (uiState.progressChallenges || []).filter((c) => Number(c.todoGroupId) === gid);
}

function progressViewerSliceFromPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.viewer && typeof payload.viewer === "object") return payload.viewer;
  if (
    payload.watchUnreadCount != null ||
    payload.mentionUnreadCount != null ||
    payload.myThemeCount != null ||
    Array.isArray(payload.mentionTargets) ||
    Array.isArray(payload.watchTargets)
  ) {
    return payload;
  }
  return null;
}

function applyProgressViewerFromPayload(payload) {
  const viewer = progressViewerSliceFromPayload(payload);
  if (!viewer) return;
  const w = viewer.watchUnreadCount;
  uiState.progressWatchUnreadCount =
    w != null && Number.isFinite(Number(w)) ? Math.max(0, Math.floor(Number(w))) : 0;
  const m = viewer.mentionUnreadCount;
  uiState.progressMentionUnreadCount =
    m != null && Number.isFinite(Number(m)) ? Math.max(0, Math.floor(Number(m))) : 0;
  const targets = viewer.mentionTargets;
  uiState.progressMentionTargets = Array.isArray(targets)
    ? targets
        .map((t) => ({
          challengeId: Number(t.challengeId),
          agendaId: Number(t.agendaId),
          agendaTitle: t.agendaTitle != null ? String(t.agendaTitle) : "",
          latestAt: t.latestAt || null,
          actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
          actorName: t.actorName != null ? String(t.actorName) : "",
          count: t.count != null && Number.isFinite(Number(t.count)) ? Math.max(1, Math.floor(Number(t.count))) : 1
        }))
        .filter((t) => Number.isFinite(t.challengeId) && t.challengeId >= 1 && Number.isFinite(t.agendaId) && t.agendaId >= 1)
    : [];
  const watchTargets = viewer.watchTargets;
  uiState.progressWatchTargets = Array.isArray(watchTargets)
    ? watchTargets
        .map((t) => ({
          challengeId: Number(t.challengeId),
          agendaId: Number(t.agendaId),
          todoGroupId: t.todoGroupId != null ? Number(t.todoGroupId) : null,
          agendaTitle: t.agendaTitle != null ? String(t.agendaTitle) : "",
          latestAt: t.latestAt || null,
          actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
          actorName: t.actorName != null ? String(t.actorName) : ""
        }))
        .filter((t) => Number.isFinite(t.challengeId) && t.challengeId >= 1 && Number.isFinite(t.agendaId) && t.agendaId >= 1)
    : [];
  const ac = viewer.assignedToMeCount;
  uiState.progressAssignedToMeCount =
    ac != null && Number.isFinite(Number(ac)) ? Math.max(0, Math.floor(Number(ac))) : 0;
  const aat = viewer.assignedActiveTotal;
  if (aat != null && Number.isFinite(Number(aat))) {
    uiState.progressAssignedActiveTotal = Math.max(0, Math.floor(Number(aat)));
  }
  const cat = viewer.createdActiveTotal;
  if (cat != null && Number.isFinite(Number(cat))) {
    uiState.progressCreatedActiveTotal = Math.max(0, Math.floor(Number(cat)));
  }
  const assignTargets = viewer.assignedToMeTargets;
  uiState.progressAssignedToMeTargets = Array.isArray(assignTargets)
    ? assignTargets
        .map((t) => ({
          challengeId: Number(t.challengeId),
          agendaId: Number(t.agendaId),
          todoGroupId: t.todoGroupId != null ? Number(t.todoGroupId) : null,
          agendaTitle: t.agendaTitle != null ? String(t.agendaTitle) : "",
          assignedAt: t.assignedAt || null,
          actorUserId: t.actorUserId != null ? Number(t.actorUserId) : null,
          actorName: t.actorName != null ? String(t.actorName) : ""
        }))
        .filter((t) => Number.isFinite(t.challengeId) && t.challengeId >= 1 && Number.isFinite(t.agendaId) && t.agendaId >= 1)
    : [];
  const mt = viewer.myThemeCount;
  uiState.progressMyThemeCount =
    mt != null && Number.isFinite(Number(mt)) ? Math.max(0, Math.floor(Number(mt))) : uiState.progressMyThemeCount;
}

function progressCurrentUserDbId() {
  const id = currentUser?.dbId != null ? Number(currentUser.dbId) : NaN;
  return Number.isFinite(id) && id >= 1 ? id : null;
}

/** ウォッチ未読（自分以外の更新のみ） */
function progressAgendaWatchHasUnreadLocal(ag) {
  if (!ag?.watchedByMe) return false;
  const viewer = progressCurrentUserDbId();
  const actor = ag.lastActivityUserId != null ? Number(ag.lastActivityUserId) : NaN;
  if (viewer != null && Number.isFinite(actor) && actor === viewer) return false;
  if (typeof ag.watchHasUnread === "boolean") return ag.watchHasUnread;
  const updatedAt = ag.updatedAt ? new Date(ag.updatedAt) : null;
  if (!updatedAt || Number.isNaN(updatedAt.getTime())) return false;
  const baseline = ag.lastReadAt ? new Date(ag.lastReadAt) : null;
  if (!baseline || Number.isNaN(baseline.getTime())) return true;
  return updatedAt > baseline;
}

function progressClearOwnWatchUnreadLocal(agendaId) {
  const myId = progressCurrentUserDbId();
  if (myId == null) return;
  syncProgressAgendaWatchFields(agendaId, { lastActivityUserId: myId, watchHasUnread: false });
  recalcProgressWatchUnreadFromTree();
  refreshSidebarUserChrome();
}

/** お知らせメニュー用: 未読ウォッチの課題（更新が新しい順） */
function listUnreadWatchTargetsForNotify() {
  const fromTree = [];
  const treeIds = new Set();
  let hasTree = false;
  for (const ch of uiState.progressChallenges || []) {
    const cid = Number(ch.id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    hasTree = true;
    for (const ag of ch.agendas || []) {
      const aid = Number(ag.id);
      if (!Number.isFinite(aid) || aid < 1) continue;
      treeIds.add(aid);
      if (!progressAgendaWatchHasUnreadLocal(ag)) continue;
      fromTree.push({
        challengeId: cid,
        agendaId: aid,
        todoGroupId: ch.todoGroupId != null ? Number(ch.todoGroupId) : null,
        agendaTitle: String(ag.title || "").trim(),
        latestAt: ag.updatedAt || null
      });
    }
  }
  const fromApi = Array.isArray(uiState.progressWatchTargets) ? uiState.progressWatchTargets : [];
  if (!hasTree) return fromApi;
  // 手元ツリーは表示中の範囲（1プロジェクト・担当課題など）だけなので、ツリーにある課題はツリーで判定し、
  // ツリーに無い課題はサーバー集計で補う（メンションと同じ。以前はツリーだけ返し、他プロジェクトのウォッチ通知が消えた）
  for (const t of fromApi) {
    if (!treeIds.has(Number(t.agendaId))) fromTree.push(t);
  }
  fromTree.sort((a, b) => {
    const ta = a.latestAt ? new Date(a.latestAt).getTime() : 0;
    const tb = b.latestAt ? new Date(b.latestAt).getTime() : 0;
    return tb - ta;
  });
  return fromTree;
}

/** お知らせメニュー用: 未読メンションの課題（新しい順） */
function listUnreadToDoMentionTargetsForNotify() {
  const myDb = currentUser?.dbId != null ? String(currentUser.dbId) : "";
  const byAgenda = new Map();
  const loadedIds = new Set();
  let anyLoaded = false;
  for (const ch of uiState.progressChallenges || []) {
    const cid = Number(ch.id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    for (const ag of ch.agendas || []) {
      if (!progressAgendaHasLoadedMessages(ag)) continue;
      anyLoaded = true;
      const aid = Number(ag.id);
      if (!Number.isFinite(aid) || aid < 1) continue;
      loadedIds.add(aid);
      let latestAt = null;
      let latestMsg = null;
      let mentionCnt = 0;
      for (const m of ag.messages || []) {
        if (m.isSystem) continue;
        const author = String(m.userId != null ? m.userId : "");
        if (myDb && author === myDb) continue;
        if (!progressMessageMentionIsUnread(ag, m)) continue;
        mentionCnt += 1;
        const t = m.createdAt ? new Date(m.createdAt).getTime() : NaN;
        if (Number.isFinite(t) && (!latestAt || t > latestAt)) {
          latestAt = t;
          latestMsg = m;
        }
      }
      if (mentionCnt > 0) {
        byAgenda.set(aid, {
          challengeId: cid,
          agendaId: aid,
          agendaTitle: String(ag.title || "").trim(),
          latestAt: latestAt ? new Date(latestAt).toISOString() : null,
          // サーバー集計由来の行と表示を揃える（誰からのメンションか）
          actorUserId: latestMsg?.userId != null ? Number(latestMsg.userId) : null,
          actorName: String(latestMsg?.userName || "").trim(),
          mentionCnt
        });
      }
    }
  }
  const fromApi = Array.isArray(uiState.progressMentionTargets) ? uiState.progressMentionTargets : [];
  if (!anyLoaded) return fromApi;
  // 手元ツリーは表示中の範囲（担当課題・1プロジェクト等）だけなので、スレッド読込済みの課題はツリーで判定し、
  // それ以外（担当外・他プロジェクト）はサーバー集計で補う。ツリーだけ返すと担当外のメンションが消える
  for (const t of fromApi) {
    const aid = Number(t.agendaId);
    if (!loadedIds.has(aid) && !byAgenda.has(aid)) byAgenda.set(aid, t);
  }
  return [...byAgenda.values()].sort((a, b) => {
    const ta = a.latestAt ? new Date(a.latestAt).getTime() : 0;
    const tb = b.latestAt ? new Date(b.latestAt).getTime() : 0;
    return tb - ta;
  });
}

/** 通知バッジ用の viewer 件数だけ再取得（ログイン直後・既読直後） */
async function refreshProgressViewerCountsFromServer() {
  if (!authToken) return false;
  // 取得中に課題を既読にした場合、この応答は既読前の集計なので捨てる（読んだ課題が未読メンションへ戻るのを防ぐ）
  const readSeq = progressAgendaReadSeq;
  const logoutSeqAtStart = fpLogoutSeq;
  try {
    const res = await fetch("/api/progress-management?scope=viewer", {
      headers: { Authorization: `Bearer ${authToken}` },
      cache: "no-store"
    });
    if (res.ok) {
      const d = await res.json().catch(() => ({}));
      if (fpLogoutSeq !== logoutSeqAtStart) return false;
      if (readSeq !== progressAgendaReadSeq) return false;
      applyProgressViewerFromPayload(d);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function progressAgendaHasLoadedMessages(ag) {
  if (!ag || ag.threadDeferred) return false;
  return Array.isArray(ag.messages);
}

/** コメントの宛先が自分か（本文の @メンション、または「お知らせするユーザー」に自分が含まれる） */
function progressMessageTargetsCurrentUser(msg) {
  const myDb = currentUser?.dbId != null ? Number(currentUser.dbId) : NaN;
  if (Number.isFinite(myDb) && Array.isArray(msg?.notifyUserIds) && msg.notifyUserIds.map(Number).includes(myDb)) return true;
  return progressMessageBodyMentionsCurrentUser(msg?.body);
}

function progressMessageMentionIsUnread(ag, msg) {
  if (!msg || msg.isSystem) return false;
  if (!progressMessageTargetsCurrentUser(msg)) return false;
  const lr = ag?.lastReadAt;
  if (!lr) return true;
  const mc = msg.createdAt ? new Date(msg.createdAt).getTime() : NaN;
  const rc = new Date(lr).getTime();
  if (!Number.isFinite(mc)) return true;
  if (!Number.isFinite(rc)) return true;
  return mc > rc;
}

/** 手元ツリーにスレッド読込済みの課題が1件でもあるか（無ければ @メンションはサーバー集計に頼る） */
function progressAnyAgendaThreadLoaded() {
  return (uiState.progressChallenges || []).some((ch) => (ch.agendas || []).some(progressAgendaHasLoadedMessages));
}

function recalcProgressMentionUnreadFromTree() {
  if (progressAnyAgendaThreadLoaded()) {
    uiState.progressMentionUnreadCount = countToDoMentionsForCurrentUser();
    return;
  }
  // メッセージ未読込（summary スコープ）ではツリーから @メンションを判定できない。
  // ここで 0/[] にするとサーバーが返した viewer.mentionTargets を消してしまい、
  // 担当変更なしのメンション通知の赤バッジが出なくなる（BUG249）。
  // 未読込時はサーバーの viewer 件数（mentionUnreadCount / mentionTargets）をそのまま信頼する。
  // 既読反映は markAgendaRead 時のサーバー再取得（applyProgressViewerFromPayload）で行われる。
}

/** ToDo ツリーと通知バッジの件数を揃える（summary 直後の stale viewer 対策） */
function reconcileProgressNotifyCountsFromTree() {
  if (!(uiState.progressChallenges || []).length) return;
  recalcProgressWatchUnreadFromTree();
  recalcProgressMentionUnreadFromTree();
}

/** 既読操作のたびに進める。viewer 取得の応答が既読前のものか判定する（refreshProgressViewerCountsFromServer） */
let progressAgendaReadSeq = 0;
/** このタブで既読にした課題 → 既読にした時刻。既読前に始まった一覧取得の応答で lastReadAt が巻き戻るのを防ぐ。
 *  課題を直接開くと「課題の取得 → 既読化」の順に走り、取得の応答（既読前）が後から届いてメンション通知が残っていた */
const progressAgendaLocalReadAt = new Map();

/** 取得した一覧の lastReadAt を、このタブで既読にした時刻より古ければ引き上げる（applyProgressPayloadToState から） */
function reapplyProgressAgendaLocalReads(challenges) {
  if (!progressAgendaLocalReadAt.size) return;
  for (const ch of challenges || []) {
    for (const ag of ch.agendas || []) {
      const localAt = progressAgendaLocalReadAt.get(Number(ag.id));
      if (!localAt) continue;
      const serverAt = ag.lastReadAt ? new Date(ag.lastReadAt).getTime() : NaN;
      if (Number.isFinite(serverAt) && serverAt >= new Date(localAt).getTime()) continue;
      ag.lastReadAt = localAt;
      ag.watchHasUnread = false;
    }
  }
}

function applyProgressAgendaReadLocal(agendaId) {
  progressAgendaReadSeq += 1;
  const now = new Date().toISOString();
  syncProgressAgendaWatchFields(agendaId, { watchHasUnread: false, lastReadAt: now });
  recalcProgressWatchUnreadFromTree();
  recalcProgressMentionUnreadFromTree();
  const aid = Number(agendaId);
  if (Number.isFinite(aid) && aid >= 1) {
    progressAgendaLocalReadAt.set(aid, now);
    uiState.progressWatchTargets = (uiState.progressWatchTargets || []).filter((t) => Number(t.agendaId) !== aid);
    // ツリー外（担当外）の課題はサーバー集計でしか判定できないので、ここで外さないと再取得まで残る
    uiState.progressMentionTargets = (uiState.progressMentionTargets || []).filter((t) => Number(t.agendaId) !== aid);
  }
  refreshSidebarUserChrome();
}

function syncProgressAgendaWatchFields(agendaId, patch) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda || !patch || typeof patch !== "object") return;
  Object.assign(hit.agenda, patch);
}

function recalcProgressWatchUnreadFromTree() {
  for (const ch of uiState.progressChallenges || []) {
    for (const ag of ch.agendas || []) {
      const unread = progressAgendaWatchHasUnreadLocal(ag);
      if (ag.watchedByMe) ag.watchHasUnread = unread;
    }
  }
  // 件数はお知らせの一覧と同じ集合（ツリー＋ツリー外のサーバー集計）で数える。ツリーだけで数えると
  // 表示中でないプロジェクトのウォッチ通知がバッジから消えていた
  uiState.progressWatchUnreadCount = listUnreadWatchTargetsForNotify().length;
}

function progressAgendaWatchSnapshot(ag) {
  return {
    watchedByMe: Boolean(ag?.watchedByMe),
    watchHasUnread: Boolean(ag?.watchHasUnread)
  };
}

/** ウォッチ状態をローカルに即反映（全件再取得しない） */
function applyProgressAgendaWatchLocal(agendaId, watched) {
  syncProgressAgendaWatchFields(agendaId, {
    watchedByMe: watched,
    watchHasUnread: false
  });
  recalcProgressWatchUnreadFromTree();
  refreshSidebarUserChrome();
}

async function progressMarkAgendaRead(agendaId) {
  if (!authToken) return;
  const id = Number(agendaId);
  if (!Number.isFinite(id) || id < 1) return;
  applyProgressAgendaReadLocal(id);
  const readSeq = progressAgendaReadSeq;
  try {
    const res = await fetch("/api/progress-management", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ action: "markAgendaRead", agendaId: id })
    });
    const j = await res.json().catch(() => ({}));
    if (res.ok) {
      // 応答待ちの間に別の課題も既読にしていたら、この応答の件数はその既読より前の集計なので反映しない
      // （反映すると、読んだばかりの課題のメンション通知が一時的に戻る。refreshProgressViewerCountsFromServer と同じ）
      if (readSeq === progressAgendaReadSeq) applyProgressViewerFromPayload(j);
      refreshSidebarUserChrome();
      syncProgressMentionInboxInPlace();
    }
  } catch {
    /* ignore */
  }
}

/** ToDo 課題ウォッチ（目アイコン・クリックで ON/OFF） */
function progressWatchEyeButtonHtml(ag, context = "head") {
  const aid = escapeHtmlAttr(String(ag.id));
  const watched = Boolean(ag.watchedByMe);
  const unread = watched && ag.watchHasUnread;
  const kanban = context === "kanban";
  const tip = watched
    ? unread
      ? "ウォッチ中（未読の更新あり）— クリックで解除"
      : "ウォッチ中 — クリックで解除"
    : "ウォッチする";
  const cls = [
    "pm-watch-eye",
    kanban ? "pm-watch-eye--kanban" : "pm-watch-eye--head",
    watched ? "pm-watch-eye--on" : "",
    unread ? "pm-watch-eye--unread" : ""
  ]
    .filter(Boolean)
    .join(" ");
  const svgSize = kanban ? 14 : 17;
  const glyph = `<span class="pm-watch-eye__glyph" aria-hidden="true">${pmBoardIconSvg("watch", svgSize)}</span>`;
  return `<button type="button" class="${cls}" data-pm-watch-toggle="${aid}" aria-pressed="${watched ? "true" : "false"}" aria-label="${escapeHtmlAttr(tip)}" title="${escapeHtmlAttr(tip)}"${kanban ? ' draggable="false"' : ""}>${glyph}</button>`;
}

/** ToDo 未読メンション件数（閲覧済み lastReadAt より後のコメントのみ。メッセージ未読込時は API 件数）。
 *  読込済み時もお知らせ一覧と同じ集合（ツリー＋ツリー外はサーバー集計）を数え、担当外のメンションを落とさない */
function countToDoMentionsForCurrentUser() {
  if (!progressAnyAgendaThreadLoaded()) return normalizeSidebarNavBadgeCount(uiState.progressMentionUnreadCount);
  return listUnreadToDoMentionTargetsForNotify().reduce((sum, t) => {
    const c = Number(t.mentionCnt != null ? t.mentionCnt : t.count);
    return sum + (Number.isFinite(c) && c >= 1 ? c : 1);
  }, 0);
}

/** 左ナビ通知の件数を数値に正規化（Unicode 囲み数字・全角数字の混入対策） */
function coerceNavBadgeNumber(n) {
  if (n == null || n === "") return NaN;
  if (typeof n === "number") return Number.isFinite(n) ? n : NaN;
  let s = String(n).trim();
  if (!s) return NaN;
  const cp0 = s.codePointAt(0);
  /* ① (U+2460) … ⑳ (U+2473) は 1 文字で 1…20 */
  if (s.length === 1 && cp0 >= 0x2460 && cp0 <= 0x2473) {
    return cp0 - 0x2460 + 1;
  }
  s = s.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30));
  const parsed = parseInt(s, 10);
  return Number.isFinite(parsed) ? parsed : NaN;
}

/** 左サイドバー用の通知件数表示（ASCII 数字のみ。99 超は 99+） */
function formatNavBadgeDisplay(n) {
  const num = coerceNavBadgeNumber(n);
  if (!Number.isFinite(num) || num < 1) return "";
  if (num > 99) return "99+";
  return String(Math.floor(num));
}

let appDocumentTitleBase = "";
const FAVICON_BADGE_RENDER_SIZES = [32, 64];
const faviconBadgeState = { baseImage: null, objectUrls: {}, drawToken: 0, lastBadgeKey: null };

function ensureAppDocumentTitleBase() {
  if (!appDocumentTitleBase) {
    appDocumentTitleBase = String(document.title || "").trim() || "On boarding";
  }
  return appDocumentTitleBase;
}

function setDocumentFaviconHref(href, opts) {
  const url = String(href || "/favicon.png");
  const sizes = opts && opts.sizes != null ? String(opts.sizes) : "";
  for (const rel of ["icon", "shortcut icon"]) {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = url;
    link.type = "image/png";
    if (sizes) link.sizes = sizes;
    else link.removeAttribute("sizes");
  }
}

function revokeFaviconBadgeObjectUrls() {
  const map = faviconBadgeState.objectUrls || {};
  for (const url of Object.values(map)) {
    if (url && String(url).startsWith("blob:")) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* noop */
      }
    }
  }
  faviconBadgeState.objectUrls = {};
  document.querySelectorAll("link[data-fp-favicon-badge]").forEach((el) => el.remove());
}

/** @deprecated alias */
function revokeFaviconBadgeObjectUrl() {
  revokeFaviconBadgeObjectUrls();
}

function clearFaviconBadgeLinkElements() {
  document.querySelectorAll("link[data-fp-favicon-badge]").forEach((el) => el.remove());
}

function applyFaviconBadgeLinkElements(urlBySize) {
  clearFaviconBadgeLinkElements();
  for (const size of FAVICON_BADGE_RENDER_SIZES) {
    const url = urlBySize[size];
    if (!url) continue;
    for (const rel of ["icon", "shortcut icon"]) {
      const link = document.createElement("link");
      link.rel = rel;
      link.type = "image/png";
      link.sizes = `${size}x${size}`;
      link.href = url;
      link.setAttribute("data-fp-favicon-badge", String(size));
      document.head.appendChild(link);
    }
  }
  const fallback = urlBySize[32] || urlBySize[16];
  if (fallback) setDocumentFaviconHref(fallback);
}

function loadFaviconBaseImage() {
  if (faviconBadgeState.baseImage) return Promise.resolve(faviconBadgeState.baseImage);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      faviconBadgeState.baseImage = img;
      resolve(img);
    };
    img.onerror = () => reject(new Error("favicon load failed"));
    img.src = "/favicon.png";
  });
}

/** 角丸長方形パス（roundRect 非対応ブラウザは arcTo フォールバック） */
function faviconBadgeRoundRect(ctx, x, y, w, h, r, fill) {
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
  ctx.fillStyle = fill;
  ctx.fill();
}

/** ブラウザタブ favicon 右上の未読バッジ。実フォント＋高解像度で数字を判読可能に描く。 */
function drawFaviconNotificationBadge(ctx, size, label) {
  const text = String(label || "1");
  const bg = "#ef4444";
  const fg = "#ffffff";
  const ring = "#ffffff";
  const multi = text.length > 1;
  const h = Math.round(size * (multi ? 0.60 : 0.66));
  const fontPx = Math.round(h * 0.74);
  ctx.font = `700 ${fontPx}px Arial, Helvetica, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const tw = multi ? ctx.measureText(text).width : 0;
  const w = multi ? Math.round(tw + h * 0.55) : h;
  const x0 = size - w;
  const y0 = 0;
  const ringW = Math.max(1, Math.round(size * 0.06));
  // 背景アイコンと分離する白フチ
  faviconBadgeRoundRect(ctx, x0 - ringW, y0, w + ringW, h + ringW, (h + ringW) / 2, ring);
  // 赤バッジ
  faviconBadgeRoundRect(ctx, x0, y0, w, h, h / 2, bg);
  // 数字（アンチエイリアスされた実フォント）
  ctx.fillStyle = fg;
  ctx.fillText(text, x0 + w / 2, y0 + h / 2 + Math.round(size * 0.02));
}

function renderFaviconWithBadgeCanvas(img, size, label) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, size, size);
  // ベース画像は滑らかに縮小（潰れ防止）。バッジ文字も実フォントで描画。
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, size, size);
  drawFaviconNotificationBadge(ctx, size, label);
  return canvas;
}

/** ブラウザタブの favicon に未読件数バッジを重ねる */
async function applyBrowserTabNotificationFavicon(count) {
  const num = coerceNavBadgeNumber(count);
  const show = uiState.auth?.isLoggedIn && Number.isFinite(num) && num >= 1;
  // 件数が変わっていなければ favicon を作り直さない。
  // （描画ごとに blob を生成/破棄すると ERR_FILE_NOT_FOUND が大量発生し、
  //   数回の画面遷移でタブが不安定になっていた。変化時だけ再生成する。）
  const badgeKey = show ? String(formatNavBadgeDisplay(num) || "1") : "none";
  if (badgeKey === faviconBadgeState.lastBadgeKey) return;
  faviconBadgeState.lastBadgeKey = badgeKey;
  const token = ++faviconBadgeState.drawToken;
  if (!show) {
    revokeFaviconBadgeObjectUrl();
    setDocumentFaviconHref("/favicon.png");
    return;
  }
  const label = formatNavBadgeDisplay(num) || "1";
  try {
    const img = await loadFaviconBaseImage();
    if (token !== faviconBadgeState.drawToken) return;
    const urlBySize = {};
    for (const size of FAVICON_BADGE_RENDER_SIZES) {
      const canvas = renderFaviconWithBadgeCanvas(img, size, label);
      if (!canvas) continue;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob || token !== faviconBadgeState.drawToken) return;
      urlBySize[size] = URL.createObjectURL(blob);
    }
    if (token !== faviconBadgeState.drawToken) {
      for (const url of Object.values(urlBySize)) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* noop */
        }
      }
      return;
    }
    revokeFaviconBadgeObjectUrls();
    faviconBadgeState.objectUrls = urlBySize;
    applyFaviconBadgeLinkElements(urlBySize);
  } catch {
    if (token === faviconBadgeState.drawToken) setDocumentFaviconHref("/favicon.png");
  }
}

function applyBrowserTabNotificationTitle() {
  const base = ensureAppDocumentTitleBase();
  const total = uiState.auth?.isLoggedIn ? sidebarNotificationTotalCount() : 0;
  document.title = total >= 1 ? `(${total > 99 ? "99+" : total}) ${base}` : base;
  // OS レベルのバッジ（Chrome / Edge デスクトップ）
  if (typeof navigator.setAppBadge === "function") {
    try {
      if (total >= 1) navigator.setAppBadge(total);
      else navigator.clearAppBadge();
    } catch { /* unsupported */ }
  }
  void applyBrowserTabNotificationFavicon(total);
}

/** 左ナビ件数を非負整数に（NaN や将来の文字列混入を吸収） */
function normalizeSidebarNavBadgeCount(raw) {
  const n =
    typeof raw === "number" ? (Number.isFinite(raw) ? raw : NaN) : coerceNavBadgeNumber(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/** 左サイドバー用の通知件数（ベル通知と同一ソース。未読のみ＝informational は除外。0 は表示しない） */
function getSidebarNavBadgeCount(navKey) {
  let sum = 0;
  for (const it of collectSidebarNotificationItems()) {
    if (it.informational) continue;
    if (navKey === "progress-management" && String(it.id || "").startsWith("todo-")) {
      sum += Number(it.count) || 0;
    } else if (navKey === "application-management" && String(it.id || "").startsWith("app-mgmt-")) {
      sum += Number(it.count) || 0;
    }
  }
  // 請求書受取: 未処理の受信メール件数（15r-invoice-mail-inbox.js。通知アイテムではなく受信箱の件数）
  if (navKey === "invoice-receipt") sum += invoiceReceiptOpenCountForBadge();
  return normalizeSidebarNavBadgeCount(sum);
}

/** サイドナビ・アイコン用 SVG path（共通） */
const NAV_SIDEBAR_ICON_PATHS = {
  dashboard:
    '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  projects:
    '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="15" y2="16"/>',
  "cost-allocation": '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  contracts:
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  "client-list":
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  "partner-list":
    '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/><circle cx="12" cy="14" r="2"/>',
  "contract-management": '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  "cost-management":
    '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
  // 請求書受取: 受信トレイ
  "invoice-receipt":
    '<polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>',
  "staff-assignment":
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>',
  "hours-management": '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  costs: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  "account-admin": '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  "budget-admin": '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
  "application-management":
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/>',
  // 貸与品: ノートPC
  "equipment-admin": '<rect x="4" y="4" width="16" height="11" rx="1"/><path d="M2 19h20"/>',
  "domain-list":
    '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  "notice-admin": '<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>',
  "other-admin":
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  "budget-progress": '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
  management: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
  "progress-management": '<path d="M12 20v-6M6 20V10M18 20V4"/>',
  workload: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  development: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>',
  "allowed-tools": '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 11.5 11 13.5 15 9.5"/>',
  "approval-settings":
    '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
  "secret-menu":
    '<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>'
};

function navIconSvg(inner, size) {
  const s = typeof size === "number" ? size : 16;
  return `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

/** 課題ボードヘッダー用アイコン（絵文字の重複を避け機能ごとにSVG） */
const PM_BOARD_ICON_PATHS = {
  search:
    '<circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
  wiki: '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>',
  watch:
    '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  assignee:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/>',
  editPen:
    '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>',
  attach:
    '<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
  quote: '<path d="M5 4v16M11 7h9M11 12h9M11 17h9"/>',
  maList:
    '<line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/>',
  maAlert:
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  maFlame:
    '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
  maCheckCircle:
    '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
  maTrash:
    '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  maCalendar:
    '<rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>',
  maFolder:
    '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  maClock:
    '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  link:
    '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>'
};

function pmBoardIconSvg(kind, size = 18) {
  const inner = PM_BOARD_ICON_PATHS[kind] || '<circle cx="12" cy="12" r="2"/>';
  return navIconSvg(inner, size);
}

function sidebarNavIconMarkup(navKey, size = 16) {
  const path = NAV_SIDEBAR_ICON_PATHS[navKey];
  const inner = path || '<circle cx="12" cy="12" r="2"/>';
  return navIconSvg(inner, size);
}

/** 下端クイックナビの既定キー（権限により穴埋め） */
const FP_MOBILE_QUICK_NAV_PREFERRED_KEYS = [
  "projects",
  "cost-allocation",
  "progress-management",
  "allowed-tools"
];
const FP_MOBILE_QUICK_NAV_FALLBACK_KEYS = [
  "dashboard",
  "progress-management",
  "staff-assignment",
  // 契約一覧は取引先一覧へ集約（2026-10-28）。経理は contract-management から client-list を含意で持つ
  "client-list",
  "cost-management"
];

const FP_MOBILE_QUICK_NAV_SHORT_LABEL = {
  dashboard: "ホーム",
  projects: "案件",
  "cost-allocation": "原価割当",
  "progress-management": "ToDo",
  "client-list": "取引先",
  "allowed-tools": "申請"
};

/** 狭い画面用の下端ナビ項目（権限順で最大 5） */
function resolveMobileQuickNavKeys(perms) {
  const pk = [...FP_MOBILE_QUICK_NAV_PREFERRED_KEYS].filter((k) => perms.includes(k));
  const list = [...pk];
  for (const k of FP_MOBILE_QUICK_NAV_FALLBACK_KEYS) {
    if (list.length >= 5) break;
    if (perms.includes(k) && !list.includes(k)) list.push(k);
  }
  return list.slice(0, 5);
}

function menuLabelForNavKey(navKey) {
  const m = menu.find((x) => x.key === navKey && !x.type);
  return m ? m.label : navKey;
}

function renderMobileQuickBottomNav(active) {
  const el = document.getElementById("fpMobileBottomNav");
  if (!el) return;
  if (!uiState.auth?.isLoggedIn || uiState.auth?.isLoading) {
    el.innerHTML = "";
    el.classList.add("fp-mobile-bottom-nav--empty");
    return;
  }
  el.classList.remove("fp-mobile-bottom-nav--empty");
  const perms = effectiveSidebarNavPermissions();
  const keys = resolveMobileQuickNavKeys(perms);
  if (keys.length === 0) {
    el.innerHTML = "";
    el.classList.add("fp-mobile-bottom-nav--empty");
    return;
  }
  const rows = keys.map((k) => {
    const isActive = active === k;
    let short = FP_MOBILE_QUICK_NAV_SHORT_LABEL[k];
    if (!short) short = menuLabelForNavKey(k);
    if (short.length > 6) {
      short = short.slice(0, 5) + "…";
    }
    const badgeN = getSidebarNavBadgeCount(k);
    const badgeDisp = badgeN > 0 ? formatNavBadgeDisplay(badgeN) : "";
    const badgeHtml =
      badgeN > 0
        ? `<span class="fp-mobile-quick-badge" aria-label="通知 ${badgeN}件">${escapeHtml(badgeDisp)}</span>`
        : "";
    const fullLabel = escapeHtml(menuLabelForNavKey(k));
    const navHash = k === "progress-management" ? progressManagementHomeHash() : k;
    return `<a class="fp-mobile-quick-link ${isActive ? "active" : ""}" href="#${escapeHtmlAttr(
      navHash
    )}"${isActive ? ' aria-current="page"' : ""}><span class="fp-mobile-quick-ic-w">${sidebarNavIconMarkup(
      k,
      22
    )}</span>${badgeHtml}<span class="fp-mobile-quick-label">${escapeHtml(short)}</span><span class="fp-mobile-quick-visually-hidden">${fullLabel}</span></a>`;
  });
  el.innerHTML = rows.join("");
}

function isFpMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 920px)").matches;
}

/** SP実機の月入力対策: ホイールピッカー操作中は change/input が段階的に発火し、
 *  そのたび render() で DOM を作り直すとピッカーが閉じて入力不能になる。
 *  タッチ端末では「月入力にフォーカスがある間は一切 render しない」を不変条件にし、
 *  変更は dirty として貯めて月入力から離れた 400ms 後にまとめて反映する
 *  （フォーカス判定を入力個別でなく activeElement の型で行うので、開始→終了と
 *  連続で触るケースやフォーカス確立前に change が届く端末でもピッカーを壊さない）。
 *  state 反映は呼び出し側で済ませておく */
let monthPickingFlushTimer = null;
let monthPickingDirty = false;
function flushMonthPicking(force) {
  monthPickingFlushTimer = null;
  if (!monthPickingDirty) return;
  const ae = document.activeElement;
  if (!force && ae instanceof HTMLInputElement && ae.type === "month") {
    // まだ月入力を操作中（ピッカーが開いている可能性）。blur か、操作が止まって
    // 1.2秒の保険タイマーで必ず反映する（iOS はピッカーを閉じてもフォーカスが
    // 残り blur が来ないことがあるため、無期限の blur 待ちにはしない）
    ae.addEventListener("blur", scheduleMonthPickingFlush, { once: true });
    monthPickingFlushTimer = setTimeout(() => flushMonthPicking(true), 1200);
    return;
  }
  monthPickingDirty = false;
  // 再構築前にフォーカスを解いておく（旧DOMに焦点が残ったまま消えるのを防ぐ）
  if (ae instanceof HTMLInputElement && ae.type === "month") ae.blur();
  render();
}
function scheduleMonthPickingFlush() {
  if (monthPickingFlushTimer != null) clearTimeout(monthPickingFlushTimer);
  monthPickingFlushTimer = setTimeout(() => flushMonthPicking(false), 400);
}
function deferRenderWhileMonthPicking(_input) {
  if (!window.matchMedia("(pointer: coarse)").matches) return false;
  monthPickingDirty = true;
  scheduleMonthPickingFlush();
  return true;
}

function routeNavigationKey(route) {
  const r = route || currentRoute();
  return [
    r.page,
    r.sidebarKey || "",
    r.id || "",
    r.detailTab || r.tab || "",
    r.openProjectCreate ? "1" : "",
    r.openPmChallengeForm ? "1" : "",
    r.openPmAgendaForm ? "1" : ""
  ].join("|");
}

/** ページ遷移時にアカウント設定などのオーバーレイを閉じる（SPでモーダルが残る問題の対策） */
function dismissTransientUiOnRouteChange(route) {
  const key = routeNavigationKey(route);
  const prev = uiState._fpLastRouteNavKey || "";
  if (prev && prev !== key) {
    uiState.myAccountEditor = null;
    uiState.userEditor = null;
    uiState.costMgmtBulkModal = null;
    uiState.costReconcileReceiptModal = null;
    uiState.clientApplyModal = null;
    uiState.clientAttachKindModal = null;
    // 埋め込み編集（取引先詳細）はページを離れたら破棄（モーダル抑止中の見えないゴースト状態を残さない）。
    // ただし同じ取引先の詳細ページ内での遷移（契約書類タブ→編集ボタンで基本情報タブへ寄せる等）では保持する
    const stayingOnSameDetail = (entityType, stateId) =>
      route.page === "client-detail" && route.entityType === entityType && String(stateId) === String(route.id);
    if (uiState.clientCreate?.inline && !stayingOnSameDetail("client", String(uiState.clientCreate.id || "").replace(/^c/, ""))) {
      uiState.clientCreate = null;
    }
    if (uiState.partnerCreate?.inline && !stayingOnSameDetail("partner", uiState.partnerCreate.id)) {
      uiState.partnerCreate = null;
    }
    uiState.fpMobileNavOpen = false;
    if (uiState.sidebarUserMenuOpen) {
      uiState.sidebarUserMenuOpen = false;
    }
    if (uiState.sidebarNotifyMenuOpen) {
      uiState.sidebarNotifyMenuOpen = false;
    }
    applySidebarUserMenuState();
    applySidebarNotifyMenuState();
    applyMobileNotifyPanel(); // SP通知パネル（#fpMobileNotifyPanel）はstateと別にDOM側のhiddenを持つため、ここで明示的に同期しないと次ページの上に残り続ける
    if (uiState.progressAgendaAttachModal) closeProgressAgendaAttachModal();
  }
  uiState._fpLastRouteNavKey = key;
}

function applyFpMobileDrawerState() {
  if (!appLayout) return;
  const chromed =
    uiState.auth?.isLoggedIn && !uiState.auth?.isLoading && !appLayout.classList.contains("auth-mode");
  const open = chromed && !!uiState.fpMobileNavOpen;
  appLayout.classList.toggle("fp-mobile-nav-open", open);
  const toggle = document.querySelector("[data-fp-mobile-nav-toggle]");
  const backdrop = document.querySelector("[data-fp-mobile-nav-backdrop]");
  if (toggle) {
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "メニューを閉じる" : "メニューを開く");
  }
  if (backdrop) backdrop.setAttribute("aria-hidden", String(!open));
}

/** サイドバー起点で全画面オーバーレイ（使い方ガイド・テンプレ設定・アカウント設定等）を開く前に呼ぶ。
 *  SPではハンバーガードロワー内から開くため、ドロワー自体も閉じないと裏に残ったまま重なって見える */
function closeSidebarFlyouts() {
  uiState.sidebarUserMenuOpen = false;
  uiState.sidebarNotifyMenuOpen = false;
  uiState.fpMobileNavOpen = false;
  applySidebarUserMenuState();
  applySidebarNotifyMenuState();
  applyMobileNotifyPanel();
  applyFpMobileDrawerState();
}

let fpMobileNavListenersBound = false;
function bindFpMobileNavChromeOnce() {
  if (fpMobileNavListenersBound) return;
  fpMobileNavListenersBound = true;
  document.body.addEventListener("click", (ev) => {
    const t = ev.target;
    if (!(t instanceof Element)) return;
    const toggler = t.closest("[data-fp-mobile-nav-toggle]");
    if (toggler) {
      uiState.fpMobileNavOpen = !uiState.fpMobileNavOpen;
      applyFpMobileDrawerState();
      return;
    }
    const backdrop = t.closest("[data-fp-mobile-nav-backdrop]");
    if (backdrop) {
      uiState.fpMobileNavOpen = false;
      applyFpMobileDrawerState();
      return;
    }
    const sideNav = t.closest(".sidebar a.nav-link[href^='#'], .sidebar a.brand-home-link[href^='#']");
    if (sideNav) {
      uiState.fpMobileNavOpen = false;
      applyFpMobileDrawerState();
    }
    const topbarNotify = t.closest("#fpTopbarNotifyBtn");
    if (topbarNotify) {
      uiState.sidebarNotifyMenuOpen = !uiState.sidebarNotifyMenuOpen;
      uiState.sidebarUserMenuOpen = false;
      if (uiState.sidebarNotifyMenuOpen) {
        void refreshProgressViewerCountsFromServer().then(() => {
          refreshSidebarUserChrome();
          applyMobileNotifyPanel();
        });
        // SP(≤920px): サイドバーではなく専用パネルで表示
        if (window.innerWidth <= 920) {
          applyMobileNotifyPanel();
        } else {
          // デスクトップは従来どおりサイドバー
        }
      } else {
        applyMobileNotifyPanel();
      }
      applySidebarUserMenuState();
      applySidebarNotifyMenuState();
      refreshSidebarUserChrome();
      return;
    }
    // SP通知パネルの「×」閉じるボタン
    const notifyClose = t.closest("[data-fp-mobile-notify-close]");
    if (notifyClose) {
      uiState.sidebarNotifyMenuOpen = false;
      applySidebarNotifyMenuState();
      applyMobileNotifyPanel();
      return;
    }
    const pwaBtn = t.closest("#fpPwaInstallBtn");
    if (pwaBtn) {
      triggerPwaInstall();
      return;
    }
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape") return;
    if (!uiState.fpMobileNavOpen) return;
    uiState.fpMobileNavOpen = false;
    applyFpMobileDrawerState();
  });
}

function navLinks(target, items, active) {
  if (!target) return;
  let html = "";
  let currentSection = "";
  for (const i of items) {
    if (i.type === "section") {
      currentSection = i.id || "";
      // masterOnly セクション：マスター以外には表示しない
      if (i.masterOnly && !isMasterRoleUser(currentUser)) { continue; }
      // セクション内にユーザーが閲覧可能なメニューが1つもなければセクション自体を非表示
      const perms = effectiveSidebarNavPermissions();
      const sectionItems = items.filter((x) => !x.type && x.key);
      const sectionStart = items.indexOf(i);
      const nextSectionIdx = items.findIndex((x, idx) => idx > sectionStart && x.type === "section");
      const sectionEnd = nextSectionIdx >= 0 ? nextSectionIdx : items.length;
      const sectionChildren = items.slice(sectionStart + 1, sectionEnd).filter((x) => x.key && !x.sidebarHidden);
      if (sectionChildren.length > 0 && sectionChildren.every((x) => !perms.includes(x.key) && !x.masterOnly)) continue;
      const open = !!uiState.sidebarSections[currentSection];
      html += `<button class="nav-section-toggle ${open ? "open" : ""}" data-nav-section-toggle="${currentSection}" type="button" title="${escapeHtmlAttr(i.label)}"><span class="nav-section-toggle-label">${escapeHtml(i.label)}</span><span class="nav-section-caret">▼</span></button>`;
      continue;
    }
    // masterOnly 項目：マスター以外は表示しない（navPermissions 判定もスキップ）
    if (i.masterOnly && !isMasterRoleUser(currentUser)) continue;
    // 権限キーとしてだけ残している項目（契約管理）は左メニューに出さない
    if (i.sidebarHidden) continue;
    // ユーザーのnavPermissionsに含まれないメニューは非表示（マスターは左メニュー定義の全項目）
    // alwaysVisible 項目は navPermissions に関わらず全員に表示（現状は該当なし）
    const perms = effectiveSidebarNavPermissions();
    if (i.key && !i.masterOnly && !i.alwaysVisible && !perms.includes(i.key)) continue;
    const hiddenClass = currentSection && !uiState.sidebarSections[currentSection] ? " hidden" : "";
    const subClass = currentSection ? " nav-link-sub" : "";
    const badgeN = getSidebarNavBadgeCount(i.key);
    const badgeDisp = badgeN > 0 ? formatNavBadgeDisplay(badgeN) : "";
    const badgeHtml =
      badgeN > 0
        ? `<span class="nav-badge" aria-label="通知 ${badgeN}件">${escapeHtml(badgeDisp)}</span>`
        : "";
    const titleAttr = `${escapeHtmlAttr(i.label)}${badgeN > 0 ? `（通知${badgeN}）` : ""}`;
    const navHash = i.key === "progress-management" ? progressManagementHomeHash() : i.key;
    html += `<a class="nav-link${subClass}${hiddenClass} ${i.key === active ? "active" : ""}" href="#${escapeHtmlAttr(navHash)}" title="${titleAttr}"><span class="nav-link-icon">${sidebarNavIconMarkup(i.key)}</span><span class="nav-link-main"><span class="nav-link-label">${escapeHtml(i.label)}</span>${badgeHtml}</span></a>`;
  }
  target.innerHTML = html;
}

// ════════════════════════════════════════════════════════════════
// 経営集計ヘルパー
// ════════════════════════════════════════════════════════════════

// 利用可能な月一覧（案件billingMonth + 経費month + 原価targetMonth の和集合・新しい順）。
// 3者は保存書式が違う（請求月は非ゼロ埋め "2026/7"／原価の計上月はゼロ埋め "2026/07"）ため、
// 必ず costTargetMonthKey で "YYYY/MM" に揃えてから重複排除する。生値のまま並べると同じ月が2つ出る。
function getAvailableMonths() {
  const keys = new Set([currentMonthKey()]);
  const add = (raw) => {
    const k = costTargetMonthKey(raw);
    if (k) keys.add(k);
  };
  projects.forEach((p) => {
    add(p.billingMonth);
    (p.costs || []).forEach((c) => add(c?.targetMonth));
  });
  expenses.forEach((e) => add(e.month));
  ensureOrphanCosts().forEach((c) => add(c?.targetMonth));
  return [...keys].sort((a, b) => b.localeCompare(a));
}

/** 当月を "YYYY/MM"（ゼロ埋め）で返す */
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// 月次 P/L を計算して返す。
//
// 月の比較は必ず costTargetMonthKey で "YYYY/MM" に揃えてから行う。案件の請求月は非ゼロ埋め
// （DBトリガーで "2026/7" 固定）、原価の計上月はゼロ埋め（"2026/07"）で保存されており、
// 生値の === で比べると 1〜9月は原価が1件も一致せず、粗利・営業利益が過大に出る。
//
// 金額は税抜で揃える。売上は projectDisplayedBillingExTaxYen（請求明細の税抜合計。無ければ
// 受注済のときだけ受注金額）で、予算進捗・ダッシュボード・案件一覧と同じ定義。以前は受注金額の
// 列(p.amount)を生で足していたため、請求明細で運用している案件の売上がここだけ抜けていた。
// 原価も税抜（amount）にする。売上が税抜・原価が税込だと粗利がその分だけ小さく出る。
function calcMonthlyPL(month) {
  const key = costTargetMonthKey(month);
  const sameMonth = (raw) => costTargetMonthKey(raw) === key;

  // 売上: billingMonth が一致 かつ invoiceStatus !== "未作成"
  const revProjects = projects.filter(
    (p) => sameMonth(p.billingMonth) && p.invoiceStatus !== "未作成"
  );
  const revenue = revProjects.reduce((s, p) => s + projectDisplayedBillingExTaxYen(p), 0);

  // 見込み売上（invoiceStatus 問わず billingMonth が一致するもの）
  const pipelineProjects = projects.filter((p) => sameMonth(p.billingMonth));
  const pipeline = pipelineProjects.reduce((s, p) => s + projectDisplayedBillingExTaxYen(p), 0);

  // 原価: cost.targetMonth が一致するコスト（案件未割当を含む）。実原価が入った支払先×月の仮原価は
  // 二重計上になるので除く（案件の粗利と同じ規則。以前は全件足しており粗利・営業利益が小さく出ていた）
  const allCosts = countedProjectCosts(ensureOrphanCosts()).concat(...projects.map((p) => countedProjectCosts(p.costs)));
  const monthCosts = allCosts.filter((c) => sameMonth(c.targetMonth));
  const costTotal = monthCosts.reduce((s, c) => s + Number(c?.amount || 0), 0);

  // 粗利
  const grossProfit = revenue - costTotal;
  const gpRate = revenue > 0 ? Math.round((grossProfit / revenue) * 100) : 0;

  // 経費
  const monthExpenses = expenses.filter((e) => sameMonth(e.month));
  const expenseTotal = monthExpenses.reduce((s, e) => s + e.amount, 0);

  // 営業利益
  const operatingIncome = grossProfit - expenseTotal;
  const opRate = revenue > 0 ? Math.round((operatingIncome / revenue) * 100) : 0;

  return {
    month, revProjects, pipelineProjects, revenue, pipeline,
    monthCosts, costTotal, grossProfit, gpRate,
    monthExpenses, expenseTotal, operatingIncome, opRate
  };
}

// ════════════════════════════════════════════════════════════════
// 原価カテゴリ別 月次推移
//   月＝計上月（fp_costs.target_month）／金額＝税抜（amount）
//   対象＝案件割当済・未割当・販管費・調整用の全原価（仮原価は除く）
// ════════════════════════════════════════════════════════════════

/** 会計年度（4月始まり）の12ヶ月を "YYYY/MM"（ゼロ埋め）で返す */
function fiscalYearMonthKeys(fyStart) {
  return Array.from({ length: 12 }, (_, i) => {
    const mo = ((3 + i) % 12) + 1; // 0→4月 … 11→3月
    const y = mo >= 4 ? fyStart : fyStart + 1;
    return `${y}/${String(mo).padStart(2, "0")}`;
  });
}

/** 原価の計上月（YYYY/M も YYYY/MM も来うる）を "YYYY/MM" に揃える */
function costTargetMonthKey(targetMonth) {
  const m = String(targetMonth || "").trim().match(/^(\d{4})[\/-](\d{1,2})$/);
  return m ? `${m[1]}/${String(Number(m[2])).padStart(2, "0")}` : "";
}

/** COST_CATEGORIES 外の値（旧データ・空）は「その他」へ寄せる */
function costTrendCategoryKey(category) {
  const c = normalizeCostCategoryLabel(category);
  return COST_CATEGORIES.includes(c) ? c : "その他";
}

/**
 * 会計年度 fyStart のカテゴリ別・月別の原価（税抜）を集計する。
 * 返り値: { months: [{ key, label, byCategory: {cat: yen}, total }], categoryTotals, grandTotal, maxMonthTotal }
 * maxMonthTotal はバーの高さの基準。値引き等でカテゴリ計がマイナスになる月があるため、
 * 実際に積む（＝正の）カテゴリだけの合計の最大値を使う（純額を基準にするとバーが枠を超える）。
 */
function buildCostCategoryMonthlyTrend(fyStart) {
  const keys = fiscalYearMonthKeys(fyStart);
  const months = keys.map((key) => ({
    key,
    label: `${Number(key.slice(5))}月`,
    byCategory: Object.fromEntries(COST_CATEGORIES.map((c) => [c, 0])),
    total: 0
  }));
  const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
  const categoryTotals = Object.fromEntries(COST_CATEGORIES.map((c) => [c, 0]));

  const allCosts = ensureOrphanCosts().concat(...projects.map((p) => p.costs || []));
  for (const c of allCosts) {
    if (!c || c.isProvisional) continue; // 仮原価は確定前なので数えない
    const row = byKey[costTargetMonthKey(c.targetMonth)];
    if (!row) continue; // 対象年度外／計上月なし
    const cat = costTrendCategoryKey(c.category);
    const amt = Number(c.amount || 0); // 税抜
    row.byCategory[cat] += amt;
    row.total += amt;
    categoryTotals[cat] += amt;
  }

  const grandTotal = COST_CATEGORIES.reduce((s, c) => s + categoryTotals[c], 0);
  const maxMonthTotal = Math.max(
    ...months.map((m) => COST_CATEGORIES.reduce((s, c) => s + Math.max(m.byCategory[c], 0), 0)),
    0
  );
  return { months, categoryTotals, grandTotal, maxMonthTotal };
}

/** 積み上げ棒グラフのカテゴリ色（凡例・バーで共用）。未定義のカテゴリが増えても灰色で描く */
const COST_TREND_CATEGORY_COLORS = {
  媒体費: "#3b82f6",
  制作費: "#10b981",
  開発費: "#8b5cf6",
  その他: "#94a3b8"
};
const costTrendCategoryColor = (cat) => COST_TREND_CATEGORY_COLORS[cat] || "#94a3b8";

function costCategoryTrendSectionHtml() {
  const fyStart = budgetProgressSelectedFiscalYearStart();
  const t = buildCostCategoryMonthlyTrend(fyStart);

  const legendHtml = COST_CATEGORIES.map(
    (cat) => `
      <span class="ctrend-legend-item">
        <span class="ctrend-dot" style="background:${costTrendCategoryColor(cat)};"></span>
        ${escapeHtml(cat)}
        <strong>${yen.format(t.categoryTotals[cat])}</strong>
        <span class="ctrend-legend-pct">${t.grandTotal > 0 ? Math.round((t.categoryTotals[cat] / t.grandTotal) * 100) : 0}%</span>
      </span>`
  ).join("");

  const barsHtml = t.months
    .map((m) => {
      const segments = COST_CATEGORIES.filter((cat) => m.byCategory[cat] > 0)
        .map((cat) => {
          const pct = t.maxMonthTotal > 0 ? (m.byCategory[cat] / t.maxMonthTotal) * 100 : 0;
          const title = `${m.key} ${cat} ${yen.format(m.byCategory[cat])}（税抜）`;
          return `<div class="ctrend-seg" style="height:${pct.toFixed(2)}%;background:${costTrendCategoryColor(cat)};" title="${escapeHtmlAttr(title)}"></div>`;
        })
        .join("");
      return `
        <div class="ctrend-col">
          <div class="ctrend-stack" title="${escapeHtmlAttr(`${m.key} 合計 ${yen.format(m.total)}（税抜）`)}">${segments}</div>
          <div class="ctrend-label">${escapeHtml(m.label)}</div>
        </div>`;
    })
    .join("");

  const rowsHtml = COST_CATEGORIES.map(
    (cat) => `
      <tr>
        <td class="mgmt-td-name"><span class="ctrend-dot" style="background:${costTrendCategoryColor(cat)};"></span>${escapeHtml(cat)}</td>
        ${t.months.map((m) => `<td class="mgmt-td-num">${m.byCategory[cat] ? yen.format(m.byCategory[cat]) : "—"}</td>`).join("")}
        <td class="mgmt-td-num"><strong>${yen.format(t.categoryTotals[cat])}</strong></td>
      </tr>`
  ).join("");

  return `
    <section class="section">
      <h3 class="section-title">原価カテゴリ別 月次推移（${fyStart}年度・税抜）</h3>
      <p class="ctrend-note">原価の計上月（対象月）ベース。案件未割当・販管費・調整用も含み、仮原価は除いています。
        同じ予算進捗の他タブは「案件の請求月」ベースのため、月の切り方が異なります。</p>
      <div class="ctrend-legend">${legendHtml}</div>
      <div class="soft-card ctrend-card">
        <div class="ctrend-scroll">
          <div class="ctrend-chart">${barsHtml}</div>
        </div>
        <div class="ctrend-scroll">
          <table class="mgmt-table ctrend-table">
            <thead>
              <tr><th>カテゴリ</th>${t.months.map((m) => `<th>${escapeHtml(m.label)}</th>`).join("")}<th>年度計</th></tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr>
                <th>合計</th>
                ${t.months.map((m) => `<th class="mgmt-td-num">${m.total ? yen.format(m.total) : "—"}</th>`).join("")}
                <th class="mgmt-td-num">${yen.format(t.grandTotal)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  `;
}

/** 案件の「営業（売上分配）」表示ラベル。分配設定があれば分配先の名前を並べ、
 *  無ければ担当営業1名を出す（案件一覧・請求書と同じ「担当営業」の考え方）。
 *  戻り値は escapeHtml 済みの断片を joinしたHTML文字列（呼び出し側で再エスケープしない）。 */
function projectSalesLabelHtml(p) {
  const valid = validSalesSplitEntries(p.salesSplit);
  if (valid.length > 0) {
    return valid.map((s) => escapeHtml(getUserById(s.userId)?.name || "?")).join(" / ");
  }
  const owner = salesOwnerUser(p);
  return owner ? escapeHtml(owner.name) : "—";
}

/** 案件に「関わった人」の userId 一覧（"uN" 形式）。営業は分配設定があればその全員、
 *  無ければ担当営業1名（salesOwnerUser フォールバック込み）。関わった制作陣を合わせて返す。
 *  貢献度タブの担当者フィルタは役割を問わず「この人の仕事」を見たいので、両方を対象にする */
function contributionRowPersonIds(p) {
  const salesValid = validSalesSplitEntries(p.salesSplit);
  const salesIds =
    salesValid.length > 0 ? salesValid.map((s) => String(s.userId)) : (() => {
      const owner = salesOwnerUser(p);
      return owner ? [String(owner.id)] : [];
    })();
  const involvedIds = projectContributorIds(p).filter((id) => getUserById(id));
  return [...new Set([...salesIds, ...involvedIds])];
}

/** 内勤実績タブ上部の「制作陣別 粗利推移」（全体の粗利＝縦棒・制作陣別＝折れ線の複合グラフ）。
 *  下の表と同じ allRows（年度内・人数で割らない満額）から月×制作陣の粗利を集計する。
 *  営業の粗利を見るためのものではないので、線は「関わった制作陣」（nonSalesSplit）だけ。
 *  灰色の縦棒は担当者の有無にかかわらず、請求月が年度内の全案件の粗利を月ごとに足した全体の推移
 *  （1案件1回だけ足すので、担当者の線を足した値とは一致しない）。全体と個人では桁が違い、同じ目盛りだと
 *  個人の線が0付近に張り付くため、棒は右・線は左の別目盛りにする。
 *  色は年度計の大きい順に8名まで。9人目以降は灰色の細線にまとめて描く。
 *  担当者を選ぶとその人の線だけを強調する（表示する月の絞り込みは年間推移なので効かせない）。 */
function contributionTrendChartHtml(allRows, fyStart, selectedMember) {
  // 担当者別の線の色（カテゴリ色の固定順。足りない分は作らず灰色の「その他」に寄せる）
  const CONTRIB_TREND_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
  const CONTRIB_TREND_OTHER_COLOR = "#c4c4c0";
  // 軸目盛りは「万」単位で短く出す（例: 1,250,000 → 125万）
  const contribTrendAxisLabel = (v) => (Math.abs(v) < 1 ? "0" : `${Math.round(v / 10000).toLocaleString("ja-JP")}万`);
  const keys = fiscalYearMonthKeys(fyStart);
  const idx = new Map(keys.map((k, i) => [k, i]));
  // 全体の粗利は担当者の設定がない案件も含める（金額の出し方は表と同じ projectGrossMarginSummary）
  const totals = keys.map(() => 0);
  for (const p of projects) {
    const i = idx.get(costTargetMonthKey(p.billingMonth));
    if (i != null) totals[i] += Math.round(Number(projectGrossMarginSummary(p).grossProfit) || 0);
  }
  const TOTAL_COLOR = "#d6d5cf";
  const byPerson = new Map();
  for (const row of allRows) {
    const i = idx.get(row.monthKey);
    if (i == null) continue;
    const creativeIds = new Set(projectContributorIds(row.p).filter((id) => getUserById(id)));
    for (const id of creativeIds) {
      if (!byPerson.has(id)) byPerson.set(id, keys.map(() => 0));
      byPerson.get(id)[i] += row.grossProfit;
    }
  }
  if (byPerson.size === 0) return "";

  const sum = (arr) => arr.reduce((s, v) => s + v, 0);
  const persons = [...byPerson.entries()]
    .map(([id, values]) => ({ id, name: userNameWithRetiredLabel(getUserById(id)) || "?", values, total: sum(values) }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, "ja"));
  persons.forEach((p, i) => {
    // 9人目以降は灰色。ただし担当者で選んだ人は灰色のままだと強調にならないので濃い色で個別に出す
    p.isOther = i >= CONTRIB_TREND_COLORS.length && p.id !== selectedMember;
    p.color = CONTRIB_TREND_COLORS[i] || (p.isOther ? CONTRIB_TREND_OTHER_COLOR : "#1f2937");
  });

  // 描画領域（viewBox 座標）。SVG は preserveAspectRatio="none" で高さ固定・横だけカード幅に伸ばす
  // （等倍拡大だと広い画面でグラフも文字も大きくなりすぎるため）。線・点は non-scaling-stroke で
  // 太さを保ち、軸の文字は伸びないよう SVG の外（HTML）に置く。
  // 目盛りは左（制作陣）・右（全体）とも0始まりの4等分で、刻みは 1/2/5×10^n のきりのいい値。
  // 等分数を揃えて目盛り線を共用する。原価だけ先に付いた月などのマイナスは0の位置に描く（金額はツールチップで見る）
  const W = 1200, H = 200, T = 8, B = 8, DIVS = 4;
  const niceAxisMax = (values) => {
    const rawStep = Math.max(...values, 0) / DIVS || 10000;
    const mag = 10 ** Math.floor(Math.log10(rawStep));
    return [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= rawStep) * DIVS;
  };
  const leftMax = niceAxisMax(persons.flatMap((p) => p.values));
  const rightMax = niceAxisMax(totals);
  const x = (i) => (W * (i + 0.5)) / keys.length;
  const yOf = (axisMax) => (v) => T + ((H - T - B) * (axisMax - Math.max(v, 0))) / axisMax;
  const y = yOf(leftMax);
  const yTotal = yOf(rightMax);
  const tickRatios = Array.from({ length: DIVS + 1 }, (_, n) => n / DIVS);
  const pointsOf = (values) => values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

  const NSS = 'vector-effect="non-scaling-stroke"';
  const hasSelection = persons.some((p) => p.id === selectedMember);
  const lineHtml = (p) => {
    const selected = p.id === selectedMember;
    const dim = hasSelection && !selected;
    const width = selected ? 3 : p.isOther ? 1.5 : 2;
    // 点は長さ0の線＋丸い線端で描く（伸縮する SVG でも円のまま）。0円の月は点を省く
    const size = selected ? 9 : 7;
    const dot = (i, v, stroke, w) => `<path d="M${x(i).toFixed(1)} ${y(v).toFixed(1)}h0" stroke="${stroke}" stroke-width="${w}" ${NSS}/>`;
    const dots = p.isOther
      ? ""
      : p.values.map((v, i) => (v === 0 ? "" : dot(i, v, "#fff", size + 3) + dot(i, v, p.color, size))).join("");
    return `<g opacity="${dim ? 0.18 : 1}" stroke-linecap="round"><polyline points="${pointsOf(p.values)}" fill="none" stroke="${p.color}" stroke-width="${width}" stroke-linejoin="round" ${NSS}/>${dots}</g>`;
  };
  // 全体の粗利の棒（列幅の半分）。0以下の月は描かない
  const barW = (W / keys.length) * 0.5;
  const barsHtml = totals
    .map((v, i) => (v <= 0 ? "" : `<rect x="${(x(i) - barW / 2).toFixed(1)}" y="${yTotal(v).toFixed(1)}" width="${barW.toFixed(1)}" height="${(yTotal(0) - yTotal(v)).toFixed(1)}"/>`))
    .join("");
  // 灰色→色付き→選択中の順に重ね、強調したい線が上に来るようにする
  const drawOrder = [...persons].reverse().sort((a, b) => (a.id === selectedMember) - (b.id === selectedMember));

  // 月ごとの透明な当たり判定。触れた月の合計と制作陣別（0円の人は省く・金額順）を
  // data-contrib-tip に持たせ、下の pointerover ハンドラがツールチップに出す（金額は凡例に出さない）。
  // 人数が多いと縦に伸びてグラフ枠（横スクロール用）に縦スクロールが出るため、TIP_ROWS 人ごとに列を増やして
  // グラフの高さに収める（縦に上から順に並べる）
  const TIP_ROWS = 9;
  const hoverHtml = keys
    .map((k, i) => {
      const monthPersons = persons.filter((p) => p.values[i] !== 0).sort((a, b) => b.values[i] - a.values[i]);
      const rows = monthPersons
        .map((p) => `<div class="contrib-tip-row"><span class="ctrend-dot" style="background:${p.color};"></span>${escapeHtml(p.name)}<strong>${yen.format(p.values[i])}</strong></div>`)
        .join("");
      const rowsPerCol = Math.ceil(monthPersons.length / Math.ceil(monthPersons.length / TIP_ROWS)) || 1;
      const tip = `<div class="contrib-tip-head">${Number(k.slice(5))}月 粗利<strong>${yen.format(totals[i])}</strong></div><div class="contrib-tip-rows" style="grid-template-rows:repeat(${rowsPerCol},auto)">${rows}</div>`;
      const w = W / keys.length;
      return `<rect class="contrib-trend-hit" x="${(x(i) - w / 2).toFixed(1)}" y="${T}" width="${w.toFixed(1)}" height="${H - T - B}" data-contrib-tip="${escapeHtmlAttr(tip)}"/>`;
    })
    .join("");

  const svg = `
    <svg class="contrib-trend-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="${escapeHtmlAttr(`${fyStart}年度 制作陣別 粗利推移`)}">
      ${tickRatios
        .map((r) => `<line x1="0" x2="${W}" y1="${y(leftMax * r).toFixed(1)}" y2="${y(leftMax * r).toFixed(1)}" stroke="${r === 0 ? "#c9c8c2" : "#ecebe7"}" stroke-width="1" ${NSS}/>`)
        .join("")}
      <g fill="${TOTAL_COLOR}" opacity="${hasSelection ? 0.5 : 1}">${barsHtml}</g>
      ${drawOrder.map(lineHtml).join("")}
      ${hoverHtml}
    </svg>`;
  // 軸の文字（HTML）。縦軸は目盛り線の高さに合わせて % で置き（左＝制作陣・右＝全体）、横軸は12等分の列に並べる
  const yLabelsOf = (axisMax) =>
    tickRatios
      .map((r) => `<span style="top:${((y(leftMax * r) / H) * 100).toFixed(2)}%">${escapeHtml(contribTrendAxisLabel(axisMax * r))}</span>`)
      .join("");
  const xLabelsHtml = keys.map((k) => `<span>${Number(k.slice(5))}月</span>`).join("");

  const colored = persons.filter((p) => !p.isOther);
  const others = persons.filter((p) => p.isOther);
  const legendHtml = `
    <span class="ctrend-legend-item${hasSelection ? " contrib-trend-dim" : ""}"><span class="ctrend-dot" style="background:${TOTAL_COLOR};"></span>全体の粗利（棒・右の目盛り）</span>
    ${colored
      .map(
        (p) => `<span class="ctrend-legend-item${hasSelection && p.id !== selectedMember ? " contrib-trend-dim" : ""}">
          <span class="ctrend-dot" style="background:${p.color};"></span>${escapeHtml(p.name)}
        </span>`
      )
      .join("")}
    ${
      others.length > 0
        ? `<span class="ctrend-legend-item" title="${escapeHtmlAttr(others.map((p) => p.name).join("\n"))}">
            <span class="ctrend-dot" style="background:${CONTRIB_TREND_OTHER_COLOR};"></span>ほか${others.length}名（灰色の線）
          </span>`
        : ""
    }`;

  return `
    <p class="ctrend-note">請求月ベース・税抜。複数人で関わった案件は各人に満額で計上。棒は担当者の設定がない案件も含む全体の粗利（右の目盛り）、線は制作陣別（左の目盛り）。</p>
    <div class="ctrend-legend">${legendHtml}</div>
    <div class="ctrend-scroll contrib-trend-wrap">
      <div class="contrib-trend-plot">
        <div class="contrib-trend-ylabels">${yLabelsOf(leftMax)}</div>
        <div class="contrib-trend-ylabels contrib-trend-ylabels--right">${yLabelsOf(rightMax)}</div>
        ${svg}
      </div>
      <div class="contrib-trend-xlabels">${xLabelsHtml}</div>
      <div class="contrib-trend-tip" hidden></div>
    </div>`;
}

// 制作陣別 粗利推移のツールチップ（触れた月の内訳）。グラフは render のたびに作り直されるので委譲で拾う
document.addEventListener("pointerover", (e) => {
  const wrap = e.target instanceof Element ? e.target.closest(".contrib-trend-wrap") : null;
  if (!wrap) return;
  const tipEl = wrap.querySelector(".contrib-trend-tip");
  const hit = e.target.closest("[data-contrib-tip]");
  if (!hit) {
    tipEl.hidden = true;
    return;
  }
  tipEl.innerHTML = hit.getAttribute("data-contrib-tip"); // 生成時に名前は escapeHtml 済み
  tipEl.hidden = false;
  // 触れた月の列の右隣に出す。右端で切れる場合は左隣へ
  const wr = wrap.getBoundingClientRect();
  const hr = hit.getBoundingClientRect();
  const left = hr.right - wr.left + wrap.scrollLeft + 8;
  tipEl.style.left = `${left + tipEl.offsetWidth > wrap.scrollWidth ? hr.left - wr.left + wrap.scrollLeft - tipEl.offsetWidth - 8 : left}px`;
});
document.addEventListener("pointerout", (e) => {
  const wrap = e.target instanceof Element ? e.target.closest(".contrib-trend-wrap") : null;
  if (wrap && !wrap.contains(e.relatedTarget)) wrap.querySelector(".contrib-trend-tip").hidden = true;
});

/** 経営ダッシュボード「貢献度」タブ: 選択中の会計年度でカテゴリの担当者（projectContributorIds。
 *  未移行の案件は旧「その他（貢献度）」）が設定された案件を、営業担当・関わった制作陣・粗利/粗利率で一覧表示する
 *  （請求・原価の列は見たいのが粗利なので出さない）。
 *  按分はせず案件の実額をそのまま出す（営業評価との重み付けは別軸で行う運用のため、
 *  営業・関わったメンバーとも同じ利益額でよい。人数で割らない・仕様確定）。
 *  金額は案件詳細・原価タブと同じ projectGrossMarginSummary を使い、数字がズレないようにする。
 *  月・担当者の絞り込みは既定「すべて表示」。プルダウンの選択肢は会計年度内の実データから作る。 */
function contributionSectionHtml() {
  const fyStart = budgetProgressSelectedFiscalYearStart();
  const allRows = projects
    .filter((p) => {
      if (projectContributorIds(p).length === 0) return false;
      const yyyymm = parseProjectBillingMonthYyyymm(p.billingMonth);
      return yyyymm != null && fiscalYearStartFromBillingYyyymm(yyyymm) === fyStart;
    })
    .map((p) => {
      const yyyymm = parseProjectBillingMonthYyyymm(p.billingMonth) || 0;
      const involvedNames = projectContributorIds(p)
        .map((userId) => getUserById(userId))
        .filter(Boolean)
        .map((u) => userNameWithRetiredLabel(u));
      const summary = projectGrossMarginSummary(p);
      return {
        p,
        yyyymm,
        // fiscalYearMonthKeys と同じ "YYYY/MM" 表記に揃えてグルーピングキーにする
        monthKey: `${Math.floor(yyyymm / 100)}/${String(yyyymm % 100).padStart(2, "0")}`,
        involvedNames,
        personIds: contributionRowPersonIds(p),
        grossProfit: Math.round(Number(summary.grossProfit) || 0),
        marginPct: summary.marginPct
      };
    });

  // プルダウンの選択肢は絞り込み前（会計年度内の全件）から作る。選んだ結果で選択肢自体が
  // 減ると「担当者を選んだら月が消えた」ように見えて使いにくいため
  const monthOptions = fiscalYearMonthKeys(fyStart).filter((k) => allRows.some((r) => r.monthKey === k));
  // 担当者は営業・クリエイティブで別グループにする（personIds は営業側=validSalesSplitEntries/
  // salesOwnerUser、クリエイティブ側=projectContributorIds（カテゴリ担当。メディア職もここ）から集めており、職種は固定なので
  // u.role === "営業" かどうかだけで正しく分けられる）
  const memberMap = new Map();
  for (const row of allRows) {
    for (const id of row.personIds) {
      if (!memberMap.has(id)) {
        const u = getUserById(id);
        if (u) memberMap.set(id, u);
      }
    }
  }
  const memberOptionsOf = (predicate) =>
    [...memberMap.entries()]
      .filter(([, u]) => predicate(u))
      .map(([id, u]) => [id, userNameWithRetiredLabel(u)])
      .sort((a, b) => a[1].localeCompare(b[1], "ja"));
  const salesMemberOptions = memberOptionsOf((u) => u.role === "営業");
  const creativeMemberOptions = memberOptionsOf((u) => u.role !== "営業");

  const selectedMonth = String(uiState.contribMonth || "");
  const selectedMember = String(uiState.contribMember || "");
  const rows = allRows.filter((row) => {
    if (selectedMonth && row.monthKey !== selectedMonth) return false;
    if (selectedMember && !row.personIds.includes(selectedMember)) return false;
    return true;
  });

  const filtersHtml = `
    <div class="bp-head-tools">
      <div class="bp-period-field pl-month-field">
        <label class="bp-period-field-label">表示する月</label>
        <select class="select bp-period-select" data-search-dropdown-off="1" aria-label="表示する月" data-contrib-month-select>
          <option value="">すべて</option>
          ${monthOptions.map((k) => `<option value="${escapeHtmlAttr(k)}" ${k === selectedMonth ? "selected" : ""}>${Number(k.slice(5))}月</option>`).join("")}
        </select>
      </div>
      <div class="bp-period-field pl-month-field">
        <label class="bp-period-field-label">担当者</label>
        <select class="select bp-period-select contrib-member-select" aria-label="担当者" data-contrib-member-select>
          <option value="">すべて</option>
          ${salesMemberOptions.length > 0 ? `<optgroup label="営業">${salesMemberOptions.map(([id, name]) => `<option value="${escapeHtmlAttr(id)}" ${id === selectedMember ? "selected" : ""}>${escapeHtml(name)}</option>`).join("")}</optgroup>` : ""}
          ${creativeMemberOptions.length > 0 ? `<optgroup label="クリエイティブ">${creativeMemberOptions.map(([id, name]) => `<option value="${escapeHtmlAttr(id)}" ${id === selectedMember ? "selected" : ""}>${escapeHtml(name)}</option>`).join("")}</optgroup>` : ""}
        </select>
      </div>
    </div>`;

  // 4月始まりの会計年度順（fiscalYearMonthKeys の並び）→ 同月内は案件名順
  const monthOrder = new Map(fiscalYearMonthKeys(fyStart).map((k, i) => [k, i]));
  const sortedRows = [...rows].sort((a, b) => {
    const byMonthOrder = (monthOrder.get(a.monthKey) ?? 0) - (monthOrder.get(b.monthKey) ?? 0);
    if (byMonthOrder !== 0) return byMonthOrder;
    return String(a.p.name || "").localeCompare(String(b.p.name || ""), "ja");
  });

  const trendHtml = contributionTrendChartHtml(allRows, fyStart, selectedMember);

  const COLS = 6;
  const rowHtml = ({ p, monthKey, involvedNames, grossProfit, marginPct }) => `
      <tr>
        <td>${Number(monthKey.slice(5))}月</td>
        <td class="mgmt-td-name">${escapeHtml(p.name || "")}</td>
        <td>${projectSalesLabelHtml(p)}</td>
        <td>${involvedNames.map((n) => `<span class="contrib-person">${escapeHtml(n)}</span>`).join("、")}</td>
        <td class="mgmt-td-num">${yen.format(grossProfit)}</td>
        <td class="mgmt-td-num">${marginPct}%</td>
      </tr>`;

  const emptyMessage =
    allRows.length === 0
      ? "カテゴリの担当者が設定された案件がまだありません"
      : "絞り込み条件に一致する案件がありません";
  const rowsHtml =
    rows.length === 0
      ? `<tr><td colspan="${COLS}" class="cost-empty">${escapeHtml(emptyMessage)}</td></tr>`
      : sortedRows.map(rowHtml).join("");

  return `
    <section class="section">
      <h3 class="section-title">内勤実績（${fyStart}年度）</h3>
      ${filtersHtml}
      ${trendHtml ? `<div class="soft-card ctrend-card contrib-trend-card">${trendHtml}</div>` : ""}
      <div class="soft-card ctrend-card">
        <div class="ctrend-scroll">
          <table class="mgmt-table ctrend-table contrib-table">
            <thead>
              <tr><th>月</th><th>案件</th><th>営業</th><th>関わった制作陣</th><th>粗利（税抜）</th><th>粗利率</th></tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
      </div>
    </section>
  `;
}

// ════════════════════════════════════════════════════════════════
// 期間の選択（予算進捗の「期」＝年度／各タブの表示月）
// ════════════════════════════════════════════════════════════════

/** 予算進捗ページで選択中の「期」に対応する会計年度（4月始まりの開始年）。
 *  年度は予算進捗ページ上部の「期を選択」プルダウンに一本化しており、タブ側には置かない。 */
function budgetProgressSelectedFiscalYearStart() {
  const periods = Array.isArray(uiState.budgetPeriods) ? uiState.budgetPeriods : [];
  const period =
    periods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(periods);
  return fiscalYearStartFromBudgetPeriod(period);
}

/** 選択中の月。未設定・書式違い・データに無い月は当月へ寄せる */
function selectedMonthKeyFrom(stored) {
  const months = getAvailableMonths();
  const cur = costTargetMonthKey(stored);
  if (months.includes(cur)) return cur;
  const now = currentMonthKey();
  return months.includes(now) ? now : months[0] || now;
}

/** 月選択のプルダウン（チップの横並びだと月が増えるほど探しにくいため） */
function monthSelectBarHtml(selected, attrName) {
  const options = getAvailableMonths()
    .map((m) => `<option value="${escapeHtmlAttr(m)}"${m === selected ? " selected" : ""}>${escapeHtml(m)}</option>`)
    .join("");
  // 見た目は同じページ上部の「期を選択」に合わせる（.bp-period-field / .bp-period-select）。
  // 原価管理の検索バー（pj-filter-area）を流用するとグレーの帯が横幅いっぱいに出て、
  // "2026/08" だけを選ぶ欄には大げさすぎた。data-search-dropdown-off は検索付きの
  // カスタムドロップダウンを使わない指定（12ヶ月なら素の select の方が軽く、幅も収まる）。
  return `
    <div class="bp-period-field pl-month-field">
      <label class="bp-period-field-label">表示する月</label>
      <select class="select bp-period-select" data-search-dropdown-off="1" aria-label="表示する月" ${attrName}>${options}</select>
    </div>`;
}

// 表示月の変更（既存の巨大 click ハンドラには足さず、この機能のイベントはここに置く）
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLSelectElement)) return;
  if (t.matches("[data-pl-month-select]")) {
    uiState.plMonth = t.value;
    render();
  }
  if (t.matches("[data-contrib-month-select]")) {
    uiState.contribMonth = t.value;
    render();
  }
  if (t.matches("[data-contrib-member-select]")) {
    uiState.contribMember = t.value;
    render();
  }
});

/** 予算進捗の「期」を切り替えた時に、app 側が中身を描いているタブを描き直す。
 *  budget-progress.js の redrawAll から呼ばれる（全体 render() を挟むと再入するため部分更新）。
 *  初回描画（budgetProgressPage のテンプレート）と同じ関数を使うこと。二重マークアップにすると
 *  期を切り替えた瞬間に見た目が変わる。 */
window.flowPilotRedrawBudgetAppViews = () => {
  const cost = document.getElementById("bp-viewCost");
  if (cost) cost.innerHTML = costCategoryTrendSectionHtml();
  const pl = document.getElementById("bp-viewPl");
  if (pl) pl.innerHTML = monthlyPlSectionsHtml();
  const contrib = document.getElementById("bp-viewContrib");
  if (contrib) {
    contrib.innerHTML = contributionSectionHtml();
    // 担当者セレクトは検索付き（sd-select）に enhance している。この innerHTML 差し替えは
    // renderNow() の外（期を切り替えた時の部分更新）を通るため、通常の enhance 呼び出しが
    // 効かない。ここで明示的に enhance しないと、期を切り替えた瞬間に素のnative selectへ戻る
    enhanceSearchableSelects(contrib);
  }
};

/** 月次推移（選択中の月を含む会計年度の12ヶ月＝4月〜3月）。
 *  売上・粗利・営業利益を1本ずつ並べた自前の棒グラフ。列は原価カテゴリ別推移と同じ4月始まりに
 *  揃えてあるので、タブを行き来しても同じ月が同じ位置に来る。
 *  高さの基準は12ヶ月の売上の最大値。売上0の月でも粗利・営業利益は描く
 *  （原価や経費だけある月で棒が消え、赤い最小バーだけが残って理由が読めなくなるため）。 */
function monthlyPlTrendSectionHtml(month) {
  const yyyymm = parseProjectBillingMonthYyyymm(month);
  const fyStart = yyyymm == null ? dashboardFiscalYearStart() : fiscalYearStartFromBillingYyyymm(yyyymm);
  const months = fiscalYearMonthKeys(fyStart);
  const trendData = months.map((m) => calcMonthlyPL(m));
  const maxRev = Math.max(...trendData.map((d) => d.revenue), 1);

  const trendBars = trendData
    .map((d) => {
      const revPct = Math.round((Math.max(d.revenue, 0) / maxRev) * 100);
      const gpPct = Math.round((Math.max(d.grossProfit, 0) / maxRev) * 100);
      const opPct = Math.round((Math.max(d.operatingIncome, 0) / maxRev) * 100);
      const gpNeg = d.grossProfit < 0;
      const opNeg = d.operatingIncome < 0;
      return `
      <div class="trend-col">
        <div class="trend-bar-wrap">
          <div class="trend-bar trend-rev"  style="height:${revPct}%"  title="${escapeHtmlAttr(`${d.month} 売上 ${yen.format(d.revenue)}`)}"></div>
          <div class="trend-bar trend-gp ${gpNeg ? "trend-neg" : ""}" style="height:${Math.max(gpPct, gpNeg ? 4 : 0)}%" title="${escapeHtmlAttr(`${d.month} 粗利 ${yen.format(d.grossProfit)}`)}"></div>
          <div class="trend-bar trend-oi ${opNeg ? "trend-neg" : ""}" style="height:${Math.max(opPct, opNeg ? 4 : 0)}%" title="${escapeHtmlAttr(`${d.month} 営業利益 ${yen.format(d.operatingIncome)}`)}"></div>
        </div>
        <div class="trend-label ${d.month === month ? "trend-label--current" : ""}">${escapeHtml(`${Number(d.month.slice(5))}月`)}</div>
      </div>`;
    })
    .join("");

  // グラフだけだと金額が読めない（マウスを乗せないと分からない）ため、原価タブと同じく内訳表を添える
  const rowSpec = [
    { label: "売上", pick: (d) => d.revenue, dot: "trend-rev" },
    { label: "原価", pick: (d) => d.costTotal, dot: "" },
    { label: "粗利", pick: (d) => d.grossProfit, dot: "trend-gp", sign: true },
    { label: "経費", pick: (d) => d.expenseTotal, dot: "" },
    { label: "営業利益", pick: (d) => d.operatingIncome, dot: "trend-oi", sign: true }
  ];
  const tableRowsHtml = rowSpec
    .map((r) => {
      const cells = trendData
        .map((d) => {
          const v = r.pick(d);
          const cls = r.sign && v < 0 ? "mgmt-td-num text-danger" : "mgmt-td-num";
          return `<td class="${cls}">${v ? yen.format(v) : "—"}</td>`;
        })
        .join("");
      const total = trendData.reduce((s, d) => s + r.pick(d), 0);
      const totalCls = r.sign && total < 0 ? "mgmt-td-num text-danger" : "mgmt-td-num";
      const dot = r.dot ? `<span class="trend-dot ${r.dot}"></span>` : "";
      return `<tr><td class="mgmt-td-name">${dot}${escapeHtml(r.label)}</td>${cells}<td class="${totalCls}"><strong>${yen.format(total)}</strong></td></tr>`;
    })
    .join("");

  return `
    <section class="section">
      <h3 class="section-title">月次推移（${fyStart}年度・税抜）</h3>
      <div class="trend-legend">
        <span class="trend-dot trend-rev"></span>売上
        <span class="trend-dot trend-gp"></span>粗利
        <span class="trend-dot trend-oi"></span>営業利益
      </div>
      <div class="soft-card ctrend-card">
        <div class="ctrend-scroll">
          <div class="trend-chart">${trendBars}</div>
        </div>
        <div class="ctrend-scroll">
          <table class="mgmt-table ctrend-table">
            <thead>
              <tr><th>項目</th>${months
                .map((m) => `<th${m === month ? ' class="trend-label--current"' : ""}>${escapeHtml(`${Number(m.slice(5))}月`)}</th>`)
                .join("")}<th>年度計</th></tr>
            </thead>
            <tbody>${tableRowsHtml}</tbody>
          </table>
        </div>
      </div>
    </section>
  `;
}

// ════════════════════════════════════════════════════════════════
// 月次 P/L
//   予算進捗の「月次P/L」タブと、単独ページ #monthly-pl の両方で同じ中身を使う。
//   単独ページを残しているのは、予算進捗がマスター専用なのに対し月次P/Lは
//   budget-admin / cost-management でも見られる必要があるため（経理の導線）。
// ════════════════════════════════════════════════════════════════
function monthlyPlSectionsHtml() {
  const month = selectedMonthKeyFrom(uiState.plMonth);
  const pl = calcMonthlyPL(month);
  const monthTabsHtml = monthSelectBarHtml(month, "data-pl-month-select");

  // ── PLサマリーカード ──
  const gpClass = pl.gpRate  < 0 ? "text-danger" : pl.gpRate  < 30 ? "text-warning" : "text-safe";
  const opClass = pl.opRate  < 0 ? "text-danger" : pl.opRate  < 10 ? "text-warning" : "text-safe";
  // 金額は右端で縦に揃える。以前は行ごとに要素数が違い（見込含の注記あり／率あり）、
  // space-between のせいで「売上高だけ中央、率のある行は率のぶん左」とバラバラだった。
  // 右側を pl-right でまとめ、率は空でも枠を出して桁位置を固定する。
  const plRow = (cls, label, valueHtml, { sub = "", rate = "" } = {}) => `
      <div class="pl-row ${cls}">
        <span class="pl-label">${escapeHtml(label)}</span>
        <span class="pl-right">
          ${sub ? `<span class="pl-sub">${sub}</span>` : ""}
          <span class="pl-value">${valueHtml}</span>
          <span class="pl-rate">${rate}</span>
        </span>
      </div>`;
  const plSummaryHtml = `
    <div class="pl-summary-card soft-card">
      ${plRow("pl-revenue", "売上高", yen.format(pl.revenue), {
        sub: pl.pipeline > pl.revenue ? `（見込含 ${yen.format(pl.pipeline)}）` : ""
      })}
      ${plRow("pl-deduct", "売上原価", yen.format(pl.costTotal))}
      ${plRow("pl-subtotal", "売上総利益（粗利）", `<span class="${gpClass}">${yen.format(pl.grossProfit)}</span>`, {
        rate: `${pl.gpRate}%`
      })}
      ${plRow("pl-deduct", "販売費及び一般管理費", yen.format(pl.expenseTotal))}
      ${plRow("pl-total", "営業利益", `<span class="${opClass}">${yen.format(pl.operatingIncome)}</span>`, {
        rate: `${pl.opRate}%`
      })}
    </div>
  `;

  // ── 折りたたみ: 売上明細 ──
  const revenueOpen = !isSectionCollapsed("pl-revenue", true);
  const revenueRows = pl.revProjects.length === 0
    ? `<p class="empty-note">この月に確定した売上はありません</p>`
    : pl.revProjects.map((p) => `
        <div class="pl-detail-row">
          <span class="pl-detail-name"><a href="#" data-open-detail="${p.id}" style="color:inherit;text-decoration:none;">${escapeHtml(p.name)}</a></span>
          <span class="pl-detail-sub">${escapeHtml(ownerName(p))} ／ ${escapeHtml(p.invoiceStatus)}</span>
          <span class="pl-detail-amount">${yen.format(projectDisplayedBillingExTaxYen(p))}</span>
        </div>
      `).join("");
  const revenueSectionHtml = `
    <section class="section">
      <div class="collapse-header">
        <h3 class="section-title">売上明細 <span class="section-badge" style="background:#3b82f6;">${pl.revProjects.length}</span></h3>
        <button class="collapse-trigger" data-collapse-toggle="pl-revenue">
          ${revenueOpen ? "閉じる" : "展開する"} <span class="collapse-arrow ${revenueOpen ? "open" : ""}">▾</span>
        </button>
      </div>
      ${revenueOpen ? `<div class="pl-detail-list soft-card" style="margin-top:10px;">${revenueRows}</div>` : ""}
    </section>
  `;

  // ── 折りたたみ: 原価明細 ──
  const costOpen = !isSectionCollapsed("pl-cost", true);
  const costRows = pl.monthCosts.length === 0
    ? `<p class="empty-note">この月の原価はありません</p>`
    : pl.monthCosts.map((c) => `
        <div class="pl-detail-row">
          <span class="pl-detail-name">${escapeHtml(c.vendor || c.category)}</span>
          <span class="pl-detail-sub"><span class="cost-category-badge">${escapeHtml(c.category)}</span> <span class="cost-pay-status ${costPayClass(costPayStatusDisplayLabel(c))}">${escapeHtml(costPayStatusDisplayLabel(c))}</span></span>
          <span class="pl-detail-amount">${yen.format(c.amount)}</span>
        </div>
      `).join("");
  const costSectionHtml = `
    <section class="section">
      <div class="collapse-header">
        <h3 class="section-title">原価明細 <span class="section-badge" style="background:#f59e0b;">${pl.monthCosts.length}</span></h3>
        <button class="collapse-trigger" data-collapse-toggle="pl-cost">
          ${costOpen ? "閉じる" : "展開する"} <span class="collapse-arrow ${costOpen ? "open" : ""}">▾</span>
        </button>
      </div>
      ${costOpen ? `<div class="pl-detail-list soft-card" style="margin-top:10px;">${costRows}</div>` : ""}
    </section>
  `;

  // ── 折りたたみ: 経費明細 ──
  const expOpen = !isSectionCollapsed("pl-expense", false); // デフォルト展開
  const expRows = pl.monthExpenses.length === 0
    ? `<p class="empty-note">この月の経費はありません</p>`
    : pl.monthExpenses.map((e) => `
        <div class="pl-detail-row">
          <span class="pl-detail-name">${escapeHtml(e.label)}</span>
          <span class="pl-detail-sub"><span class="cost-category-badge">${escapeHtml(e.category)}</span>${e.note ? ` — ${escapeHtml(e.note)}` : ""}</span>
          <div class="pl-detail-amount">
            ${yen.format(e.amount)}
            <button class="inline-btn ghost" style="margin-left:8px;" data-edit-expense="${e.id}">編集</button>
            <button class="inline-btn ghost" data-delete-expense="${e.id}">削除</button>
          </div>
        </div>
      `).join("");

  // 経費カテゴリ別小計
  const expByCategory = EXPENSE_CATEGORIES.map((cat) => {
    const catItems = pl.monthExpenses.filter((e) => e.category === cat);
    if (!catItems.length) return "";
    const total = catItems.reduce((s, e) => s + e.amount, 0);
    return `<div class="exp-cat-row"><span>${cat}</span><span>${yen.format(total)}</span></div>`;
  }).join("");

  const expenseSectionHtml = `
    <section class="section">
      <div class="collapse-header">
        <h3 class="section-title">経費明細 <span class="section-badge" style="background:#8b5cf6;">${pl.monthExpenses.length}</span></h3>
        <div style="display:flex;gap:8px;align-items:center;">
          <button class="inline-btn" data-add-expense="${month}">＋ 経費を追加</button>
          <button class="collapse-trigger" data-collapse-toggle="pl-expense">
            ${expOpen ? "閉じる" : "展開する"} <span class="collapse-arrow ${expOpen ? "open" : ""}">▾</span>
          </button>
        </div>
      </div>
      ${expOpen ? `
        <div class="pl-detail-list soft-card" style="margin-top:10px;">${expRows}</div>
        ${expByCategory ? `<div class="exp-cat-summary soft-card" style="margin-top:8px;">${expByCategory}</div>` : ""}
      ` : ""}
    </section>
  `;

  return `
    ${monthTabsHtml}
    <section class="section">
      <h3 class="section-title">${escapeHtml(month)} 損益計算書（税抜）</h3>
      ${plSummaryHtml}
    </section>
    ${monthlyPlTrendSectionHtml(month)}
    ${revenueSectionHtml}
    ${costSectionHtml}
    ${expenseSectionHtml}
  `;
}

/** 単独ページ版（#monthly-pl）。中身はタブと同じものを使う */
function monthlyPlPage() {
  return `
    ${header("月次 P/L", "売上・原価・経費・営業利益を月ごとに確認する")}
    ${monthlyPlSectionsHtml()}
  `;
}

// ════════════════════════════════════════════════════════════════
// 経費フォーム（サイドパネル）
// ════════════════════════════════════════════════════════════════
function renderExpenseForm() {
  if (!uiState.expenseForm) return "";
  const f = uiState.expenseForm;
  const isEdit = Boolean(f.editingId);
  const catOptions = EXPENSE_CATEGORIES
    .map((c) => `<option value="${c}" ${f.category === c ? "selected" : ""}>${c}</option>`)
    .join("");

  return `
    <div class="composer-backdrop" data-close-expense-form="1"></div>
    <aside class="composer-panel project-create-panel">
      <div class="composer-head">
        <div><h3>${isEdit ? "経費を編集" : "経費を追加"}</h3><p>月次の固定費・管理費を記録します</p></div>
        <button class="inline-btn ghost" data-close-expense-form="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="form-grid">
          <label>対象月
            <input data-expense-field="month" type="month" value="${(f.month || "").replace("/", "-")}" />
          </label>
          <label>カテゴリ
            <select data-expense-field="category">${catOptions}</select>
          </label>
          <label style="grid-column:1/-1;">内容
            <input data-expense-field="label" value="${escapeHtmlAttr(f.label || "")}" placeholder="例: スタッフ給与、オフィス賃料" />
          </label>
          <label>金額（円）
            <input data-expense-field="amount" inputmode="numeric" value="${escapeHtmlAttr(String(f.amount ?? ""))}" placeholder="100000" />
          </label>
          <label>メモ（任意）
            <input data-expense-field="note" value="${escapeHtmlAttr(f.note || "")}" placeholder="補足など" />
          </label>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-expense-form="1">キャンセル</button>
        <button class="inline-btn" data-save-expense="1" ${f._submitting ? "disabled" : ""}>${f._submitting ? "保存中…" : (isEdit ? "更新する" : "追加する")}</button>
      </div>
    </aside>
  `;
}

// ────────────────────────────────────────────────────────────────
// 入金記録フォーム（モーダル）
// 「入金確認を記録する」→ このフォームで日付・金額・備考を入力 → 保存後に状態更新
// ────────────────────────────────────────────────────────────────
function renderPaymentForm() {
  if (!uiState.paymentForm) return "";
  const f = uiState.paymentForm;
  const p = projects.find((x) => x.id === f.projectId);
  if (!p) return "";
  return `
    <div class="composer-backdrop" data-close-payment-form="1"></div>
    <aside class="composer-panel project-create-panel">
      <div class="composer-head">
        <div><h3>入金記録</h3><p>${escapeHtml(p.name)} の入金を確認・記録します</p></div>
        <button class="inline-btn ghost" data-close-payment-form="1">閉じる</button>
      </div>
      <div class="composer-body">
        <div class="soft-card panel-pad" style="margin-bottom:16px;">
          <div class="setting-item"><span>案件名</span><span>${escapeHtml(p.name)}</span></div>
          <div class="setting-item"><span>クライアント</span><span>${escapeHtml(clientName(p))}</span></div>
          <div class="setting-item"><span>請求金額</span><span>${yen.format(p.amount)}</span></div>
        </div>
        <div class="form-grid">
          <label>入金日
            <input data-payment-field="payDate" type="date" value="${f.payDate}" />
          </label>
          <label>入金額（円）
            <input data-payment-field="amount" inputmode="numeric" value="${f.amount}" placeholder="${p.amount}" />
          </label>
          <label style="grid-column:1/-1;">備考（任意）
            <input data-payment-field="note" value="${escapeHtmlAttr(f.note)}" placeholder="例: 4月分 入金確認済み" />
          </label>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-close-payment-form="1">キャンセル</button>
        <button class="inline-btn" data-save-payment="1">入金確認を完了にする</button>
      </div>
    </aside>
  `;
}

/**
 * 入金確認を案件に反映する（単票・一括で共通利用）。案件を「入金確認済み・完了」に遷移させ、
 * 巻き戻し用のスナップショットを返す。サーバ保存は呼び出し側が行う。
 */
function applyPaymentConfirmationToProject(p, { payDate, amount, note }) {
  const snap = {
    invoiceStatus: p.invoiceStatus,
    handoffStatus: p.handoffStatus,
    currentOwnerRole: p.currentOwnerRole,
    nextAction: p.nextAction,
    currentStage: p.currentStage,
    paymentRecord: p.paymentRecord,
    paymentConfirmedBy: p.paymentConfirmedBy,
    paymentConfirmedAt: p.paymentConfirmedAt,
    lastUpdatedBy: p.lastUpdatedBy,
    lastUpdatedAt: p.lastUpdatedAt,
    docs: { ...(p.docs || {}) }
  };
  if (!p.docs) p.docs = {};
  p.docs.payment = "入金確認済み";
  p.invoiceStatus = "入金確認済み";
  p.handoffStatus = "完了";
  p.currentOwnerRole = "経理";
  p.nextAction = "対応完了（必要に応じて確認）";
  p.currentStage = "done";
  // 入力欄は文字列（「50,000」・全角も来る）。Number() だと NaN になり黙って請求額で記録していたため、
  // 経費フォームと同じ parseBillingNumber で読む。空欄（または0）は従来どおり請求額。
  const amountNum = parseBillingNumber(amount);
  p.paymentRecord = { payDate, amount: Number.isFinite(amountNum) && amountNum !== 0 ? amountNum : p.amount, note: note || "" };
  p.paymentConfirmedBy = currentUser.id;
  p.paymentConfirmedAt = payDate;
  p.lastUpdatedBy = currentUser.id;
  p.lastUpdatedAt = payDate;
  return snap;
}

/**
 * 入金確認の取り消し（誤って入金済にした案件を未入金へ戻す）。applyPaymentConfirmationToProject の逆操作。
 * 元の請求ステータスは記録していないため「入金待ち」（表示は請求済み）へ戻す。
 * 巻き戻し用スナップショットを返し、サーバ保存は呼び出し側が行う（入金記録のクリアは persistProjectHandoffToServer の clearPayment で）。
 */
function applyPaymentCancellationToProject(p) {
  const snap = {
    invoiceStatus: p.invoiceStatus,
    handoffStatus: p.handoffStatus,
    currentOwnerRole: p.currentOwnerRole,
    nextAction: p.nextAction,
    currentStage: p.currentStage,
    paymentRecord: p.paymentRecord,
    paymentConfirmedBy: p.paymentConfirmedBy,
    paymentConfirmedAt: p.paymentConfirmedAt,
    lastUpdatedBy: p.lastUpdatedBy,
    lastUpdatedAt: p.lastUpdatedAt,
    docs: { ...(p.docs || {}) }
  };
  if (!p.docs) p.docs = {};
  p.docs.payment = "未確認";
  p.invoiceStatus = "入金待ち";
  p.handoffStatus = "経理確認待ち";
  p.currentOwnerRole = "経理";
  p.nextAction = "入金を確認してください";
  p.currentStage = "payments";
  p.paymentRecord = null;
  p.paymentConfirmedBy = "";
  p.paymentConfirmedAt = "";
  p.lastUpdatedBy = currentUser.id;
  p.lastUpdatedAt = localDateToYmd(new Date()); // toISOString は UTC で JST の0〜9時に前日になる
  return snap;
}

/**
 * 入金確認／取り消しの保存（入金ステータス等 → 入金記録 の2段階）。apply* のあと呼ぶ。
 * 2本目だけ失敗すると DB は入金確認済み・入金記録なしになり、画面だけ snap に戻すと食い違う。
 * 失敗したら手元を snap に戻し、1本目が通っていれば戻した内容で保存し直す。
 * @returns {Promise<{ ok: boolean, mismatch?: boolean }>} mismatch=true は保存し直しにも失敗（再読み込みが必要）
 */
async function persistPaymentChangeToServer(p, snap, handoffOptions = {}) {
  let detailSaved = false;
  try {
    await persistProjectDetailToServer(p);
    detailSaved = true;
    await persistProjectHandoffToServer(p, handoffOptions);
    return { ok: true };
  } catch {
    if (snap) Object.assign(p, snap);
    if (!detailSaved) return { ok: false };
    try {
      await persistProjectDetailToServer(p);
      return { ok: false };
    } catch {
      return { ok: false, mismatch: true };
    }
  }
}

/** persistPaymentChangeToServer が食い違いを残したときの案内（失敗トーストに足す） */
const PAYMENT_SAVE_MISMATCH_HINT = "入金ステータスだけ保存された可能性があります。画面を再読み込みして確認してください。";

function staffAssignPeriodYyyymm(p) {
  if (!p) return null;
  const n = parseProjectBillingMonthYyyymm(p.billingMonth);
  if (n != null) return n;
  const s = String(p.plannedInvoiceDate || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (m) return Number(m[1]) * 100 + Number(m[2]);
  return null;
}

function staffAssignBillingLabel(p) {
  if (!p) return "—";
  if (p.billingMonth && String(p.billingMonth).trim()) return String(p.billingMonth).trim();
  const s = String(p.plannedInvoiceDate || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})/);
  if (m) return `${m[1]}/${Number(m[2])}`;
  return "—";
}

function staffAssignRowKey(projectId, lineIndex) {
  return `${String(projectId)}:${Number(lineIndex)}`;
}

function parseStaffAssignRowKey(key) {
  const s = String(key || "");
  const i = s.lastIndexOf(":");
  if (i <= 0) return null;
  const pid = s.slice(0, i);
  const idx = Number(s.slice(i + 1));
  if (!pid || Number.isNaN(idx)) return null;
  return { pid, idx };
}

/** 一括担当: 選択済みチップ（1行スクロール用HTML） */
function staffBulkAssignChipsHtml(bulkIds) {
  const dedup = [...new Set((bulkIds || []).map(String).filter(Boolean))];
  if (!dedup.length) {
    return `<span class="sa-bulk-assign-placeholder">＋ クリックして追加</span>`;
  }
  return dedup
    .map((id) => {
      const deptId = parseLineAssigneeDeptId(id);
      let label;
      let ariaRm;
      if (deptId != null) {
        const d = (uiState.deptSettings?.departments || []).find((x) => String(x.id) === String(deptId));
        const dn = d?.name || "部署";
        label = `${escapeHtml(dn)}（部署全体）`;
        ariaRm = escapeHtmlAttr(`${dn}を外す`);
      } else {
        const u = users.find((x) => String(x.id) === String(id));
        label = u
          ? `${escapeHtml(userNameWithRetiredLabel(u))}（${escapeHtml(primaryRole(u))}）`
          : escapeHtml(String(id));
        ariaRm = escapeHtmlAttr(u?.name ? `${u.name}を外す` : "削除");
      }
      return `<span class="sa-bulk-chip"><span class="sa-bulk-chip-text">${label}</span><button type="button" class="sa-bulk-chip-remove" data-staff-bulk-chip-remove="${escapeHtmlAttr(id)}" aria-label="${ariaRm}">×</button></span>`;
    })
    .join("");
}

function pruneStaffAssignmentSelection(rows) {
  const valid = new Set(rows.map((r) => staffAssignRowKey(r.p.id, r.lineIndex)));
  const sel = uiState.staffAssignmentTable?.selected;
  if (!sel || typeof sel !== "object") return;
  Object.keys(sel).forEach((k) => {
    if (!valid.has(k)) delete sel[k];
  });
}

function collectStaffAssignmentRows() {
  const f = uiState.staffAssignmentFilters || {
    category: "all",
    billingFrom: "",
    billingTo: "",
    assignee: "all",
    unassignedOnly: true
  };
  const fromStr = String(f.billingFrom || "").trim();
  const toStr = String(f.billingTo || "").trim();
  const fromN = fromStr ? parseProjectBillingMonthYyyymm(fromStr.replace(/-/g, "/")) : null;
  const toN = toStr ? parseProjectBillingMonthYyyymm(toStr.replace(/-/g, "/")) : null;
  const out = [];
  // 行ごとの分類は廃止（2026-10 要望）。案件のカテゴリ（複数可）で絞り込む。"unset" はカテゴリ未設定の案件
  const catFilter =
    f.category === "unset" || PROJECT_WORK_CATEGORY_OPTIONS.includes(f.category) ? f.category : "all";
  for (const p of projects) {
    if (!isWonOrderStatus(projectOrderStatusForUi(p))) continue;
    const projectCats = workCategoriesFromRaw(p.workCategories).categories;
    if (catFilter === "unset" ? projectCats.length > 0 : catFilter !== "all" && !projectCats.includes(catFilter)) continue;
    const category = projectCats.join("、") || "未設定";
    const draft = ensureProjectDocDraft(p.id);
    const lines = draft?.lines || [];
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
      const line = lines[lineIndex];
      if ((line.lineType || "normal") !== "normal") continue;
      const item = String(line.item || "").trim();
      if (!item) continue;
      const assignIds = normalizeLineAssigneeIds(line);
      const assignId = assignIds[0] || "";
      if (f.unassignedOnly !== false && assignIds.length > 0) continue;
      if (f.assignee === "unassigned" && assignIds.length) continue;
      if (f.assignee !== "all" && f.assignee !== "unassigned" && !lineAssigneesMatchStaffFilterUser(assignIds, f.assignee)) {
        continue;
      }
      const period = staffAssignPeriodYyyymm(p);
      if (fromN != null || toN != null) {
        if (period == null) continue;
        if (fromN != null && period < fromN) continue;
        if (toN != null && period > toN) continue;
      }
      out.push({ p, line, lineIndex, assignId, assignIds: [...assignIds], item, category });
    }
  }
  return { rows: out, categories: PROJECT_WORK_CATEGORY_OPTIONS };
}

/** 一覧に一度に出す行数。本番は未割当だけで約1.4万行あり、全行を描くとチェック1回ごとに
 *  約3.5秒固まっていた（2026-10-08 実機計測。DOM 約19万要素）。 */
const STAFF_ASSIGN_PAGE_SIZE = 200;
function staffAssignmentShownCount() {
  return Math.max(STAFF_ASSIGN_PAGE_SIZE, Number(uiState.staffAssignmentTable?.shownCount) || 0);
}

document.addEventListener("click", (e) => {
  const btn = e.target instanceof Element ? e.target.closest("[data-staff-assign-show-more]") : null;
  if (!btn) return;
  uiState.staffAssignmentTable.shownCount = staffAssignmentShownCount() + STAFF_ASSIGN_PAGE_SIZE;
  render();
});

function staffAssignmentPage() {
  const { rows, categories } = collectStaffAssignmentRows();
  pruneStaffAssignmentSelection(rows);
  const f = uiState.staffAssignmentFilters;
  const tbl = uiState.staffAssignmentTable;
  const selMap = tbl.selected || {};
  const shownRows = rows.slice(0, staffAssignmentShownCount());
  const restCount = rows.length - shownRows.length;
  const au = staffAssignableUsers();
  /** 一覧の「担当者」絞り込み（休職・退職は含めない。営業含む） */
  const assigneeFilterUsers = assignableUsers()
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ja"));
  const allowedAssigneeFilterIds = new Set(assigneeFilterUsers.map((u) => String(u.id)));
  if (
    f.assignee !== "all" &&
    f.assignee !== "unassigned" &&
    !allowedAssigneeFilterIds.has(String(f.assignee))
  ) {
    uiState.staffAssignmentFilters.assignee = "all";
  }
  const allowedBulkPicks = new Set([
    ...au.map((u) => String(u.id)),
    ...staffAssignableDepartments().map((d) => lineAssigneeDeptToken(d.id))
  ]);
  if (Array.isArray(tbl.bulkUserIds) && tbl.bulkUserIds.length) {
    const prunedBulk = tbl.bulkUserIds.filter((id) => allowedBulkPicks.has(String(id)));
    if (prunedBulk.length !== tbl.bulkUserIds.length) tbl.bulkUserIds = prunedBulk;
  }
  const displayNo = (p) => Number(String(p.no ?? p.id).replace(/[^\d]/g, "")) || 0;
  const selectedCount = rows.reduce((n, r) => n + (selMap[staffAssignRowKey(r.p.id, r.lineIndex)] ? 1 : 0), 0);
  if (selectedCount === 0) tbl.bulkAssignPanelOpen = false;
  const headChecked = shownRows.length > 0 && shownRows.every((r) => selMap[staffAssignRowKey(r.p.id, r.lineIndex)]);
  const headIndeterminate = selectedCount > 0 && !headChecked;
  const bulkIds = Array.isArray(tbl.bulkUserIds) ? tbl.bulkUserIds : [];
  const bulkPickSelected = (pickId) => bulkIds.some((b) => String(b) === String(pickId));
  const assignDepts = staffAssignableDepartments();
  const rowsHtml = uiState.heavyDataLoading
    ? `<tr><td colspan="9" class="empty-note pj-td-heavy-loading">${flowPilotNowLoadingBlock({
        inline: true,
        subtitle: "案件・請求明細を読み込んでいます"
      })}</td></tr>`
    : rows.length === 0
      ? `<tr><td colspan="9" class="empty-note">条件に一致する請求項目がありません（受注済の案件で、案件詳細で摘要を入力した明細行が対象です）。</td></tr>`
      : shownRows
          .map((r) => {
            const pid = r.p.id;
            const rowKey = staffAssignRowKey(pid, r.lineIndex);
            const rowChecked = selMap[rowKey] === true;
            const aids = Array.isArray(r.assignIds) ? r.assignIds : normalizeLineAssigneeIds(r.line);
            const statusHtml = aids.length
              ? `<span class="sa-line-status sa-line-status--ok">割当済</span>`
              : `<span class="sa-line-status sa-line-status--na">未割当</span>`;
            const assigneeLabel =
              aids.length === 0
                ? `<span class="sa-line-assign-empty">未割当</span>`
                : `<span class="sa-line-assign-multi">${aids
                    .map((aid1) => {
                      const did = parseLineAssigneeDeptId(aid1);
                      if (did != null) {
                        const dRow = (uiState.deptSettings?.departments || []).find((x) => String(x.id) === String(did));
                        const dn = dRow?.name || "不明な部署";
                        return `<span class="sa-line-assign-name sa-line-assign-dept">${escapeHtml(dn)}（部署全体）</span>`;
                      }
                      const uRow = users.find((u) => String(u.id) === String(aid1));
                      return uRow
                        ? `<span class="sa-line-assign-name">${escapeHtml(userNameWithRetiredLabel(uRow))}（${escapeHtml(primaryRole(uRow))}）</span>`
                        : `<span class="sa-line-assign-unknown">${escapeHtml(aid1)}</span>`;
                    })
                    .join("、")}</span>`;
            return `<tr>
              <td class="sa-td-check"><input type="checkbox" data-staff-assign-select="${escapeHtmlAttr(rowKey)}" ${rowChecked ? "checked" : ""} aria-label="行を選択" /></td>
              <td class="sa-td-status">${statusHtml}</td>
              <td class="sa-td-no">${displayNo(r.p)}</td>
              <td class="sa-td-name">${escapeHtml(r.p.name || "")}</td>
              <td class="sa-td-client">${escapeHtml(clientName(r.p))}</td>
              <td>${escapeHtml(r.item)}</td>
              <td>${escapeHtml(r.category)}</td>
              <td class="sa-td-date">${escapeHtml(staffAssignBillingLabel(r.p))}</td>
              <td class="sa-td-assignee">${assigneeLabel}</td>
            </tr>`;
          })
          .join("");
  const catOpts = [`<option value="all"${f.category === "all" ? " selected" : ""}>すべて</option>`]
    .concat(categories.map((c) => `<option value="${escapeHtmlAttr(c)}"${f.category === c ? " selected" : ""}>${escapeHtml(c)}</option>`))
    .concat(`<option value="unset"${f.category === "unset" ? " selected" : ""}>未設定</option>`)
    .join("");
  const assigneeOpts = [
    `<option value="all"${f.assignee === "all" ? " selected" : ""}>すべて</option>`,
    `<option value="unassigned"${f.assignee === "unassigned" ? " selected" : ""}>未割当のみ</option>`
  ]
    .concat(
      assigneeFilterUsers.map(
        (u) =>
          `<option value="${escapeHtmlAttr(u.id)}"${f.assignee === u.id ? " selected" : ""}>${escapeHtml(u.name)}（${escapeHtml(primaryRole(u))}）</option>`
      )
    )
    .join("");
  return `
    ${header("担当者割当", "受注済の案件の請求項目（摘要あり）ごとに、担当者または部署（全体）を割り当てます")}
    <section class="section">
      <div class="sa-filter-bar soft-card panel-pad">
        <div class="sa-filter-row">
          <label class="sa-filter-field"><span class="sa-filter-label">カテゴリ</span>
            <select class="pj-input" data-staff-assign-filter="category" title="案件のカテゴリ（案件詳細の基本情報）で絞り込みます">${catOpts}</select>
          </label>
          <label class="sa-filter-field"><span class="sa-filter-label">請求月（開始）</span>
            <input class="pj-input pj-input-month" type="month" data-show-picker="1" data-staff-assign-filter="billingFrom" value="${escapeHtmlAttr(f.billingFrom || "")}" title="YYYY/MM 範囲の開始（含む）" />
          </label>
          <span class="sa-filter-sep" aria-hidden="true">～</span>
          <label class="sa-filter-field"><span class="sa-filter-label">請求月（終了）</span>
            <input class="pj-input pj-input-month" type="month" data-show-picker="1" data-staff-assign-filter="billingTo" value="${escapeHtmlAttr(f.billingTo || "")}" title="YYYY/MM 範囲の終了（含む）" />
          </label>
          <label class="sa-filter-field"><span class="sa-filter-label">担当者</span>
            <select class="pj-input" data-staff-assign-filter="assignee" style="min-width:160px;">${assigneeOpts}</select>
          </label>
          <button type="button" class="inline-btn ghost" data-staff-assign-reset="1">条件クリア</button>
        </div>
        <p class="sa-filter-hint">対象は受注ステータスが「受注済」の案件のみです。担当者の絞り込みは、その人が直接割り当てられた行に加え、その人が所属する部署へ「部署全体」割当された行も含みます。一覧は案件詳細の請求項目と連動し、カテゴリは案件のカテゴリ（基本情報）です。</p>
        <label class="sa-staff-unassigned-filter">
          <input type="checkbox" data-staff-assign-unassigned-only="1" ${f.unassignedOnly !== false ? "checked" : ""} />
          <span>未割当の案件のみ表示する</span>
        </label>
      </div>
      <div class="pj-bulk-bar sa-bulk-bar sa-bulk-bar-users ${selectedCount > 0 ? "show" : ""}">
        <span>${selectedCount}件選択中</span><span class="sep">|</span>
        <div class="sa-bulk-assign-dropdown-wrap">
          <span class="sa-bulk-assign-label">割当先を一括設定</span>
          <div class="sa-bulk-assign-combo${tbl.bulkAssignPanelOpen ? " is-open" : ""}">
            <div class="sa-bulk-assign-chips-row pj-input">
              <div class="sa-bulk-assign-chips-scroll" aria-label="選択中の割当先">${staffBulkAssignChipsHtml(bulkIds)}</div>
              <button type="button" class="sa-bulk-assign-clear-all" data-staff-bulk-assign-clear-all="1" ${bulkIds.length ? "" : "disabled"} title="すべて外す" aria-label="すべて外す">×</button>
              <button type="button" class="sa-bulk-assign-toggle" data-staff-bulk-assign-toggle="1" aria-expanded="${tbl.bulkAssignPanelOpen ? "true" : "false"}" aria-haspopup="listbox" aria-label="割当先を追加">▾</button>
            </div>
            <div class="sa-bulk-assign-panel" role="listbox" aria-label="割当先を追加">
              ${au
                .map(
                  (u) =>
                    `<label class="sa-bulk-assign-option"><input type="checkbox" data-staff-assign-bulk-pick="${escapeHtmlAttr(u.id)}" ${bulkPickSelected(u.id) ? "checked" : ""} />${escapeHtml(userNameWithRetiredLabel(u))}（${escapeHtml(primaryRole(u))}）</label>`
                )
                .join("")}
              ${
                assignDepts.length
                  ? `<div class="sa-bulk-assign-panel-sub" role="presentation">部署（所属メンバー全員の担当）</div>${assignDepts
                      .map((d) => {
                        const tok = lineAssigneeDeptToken(d.id);
                        return `<label class="sa-bulk-assign-option sa-bulk-assign-option--dept"><input type="checkbox" data-staff-assign-bulk-pick="${escapeHtmlAttr(tok)}" ${bulkPickSelected(tok) ? "checked" : ""} />${escapeHtml(d.name || "（無名）")}（部署全体）</label>`;
                      })
                      .join("")}`
                  : ""
              }
            </div>
          </div>
        </div>
        <div class="pj-bulk-bar-end sa-bulk-bar-actions">
          <button type="button" class="inline-btn" data-staff-assign-bulk-apply="1">適用</button>
          <button type="button" class="inline-btn ghost" data-staff-assign-clear-selection="1">選択解除</button>
        </div>
      </div>
      <div class="soft-card sa-table-wrap" style="overflow-x:auto;margin-top:14px;">
        <table class="sa-table pj-table" data-fp-table="staff-assignment">
          <thead>
            <tr>
              <th class="sa-th-check"><input type="checkbox" data-staff-assign-select-all="1" ${headChecked ? "checked" : ""} ${headIndeterminate ? 'data-indeterminate="1"' : ""} aria-label="表示中の行をすべて選択" /></th>
              <th class="sa-th-status" style="width:92px;">状態</th>
              <th>案件No</th>
              <th class="sa-th-name">案件名</th>
              <th class="sa-th-client">顧客</th>
              <th>摘要（請求項目）</th>
              <th>カテゴリ</th>
              <th>請求月</th>
              <th class="sa-th-name">担当者</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      ${
        restCount > 0 && !uiState.heavyDataLoading
          ? `<div class="sa-show-more"><span class="sa-show-more-count">全${rows.length.toLocaleString()}件中 ${shownRows.length.toLocaleString()}件を表示</span><button type="button" class="inline-btn ghost" data-staff-assign-show-more="1">さらに${Math.min(STAFF_ASSIGN_PAGE_SIZE, restCount)}件表示（残り${restCount.toLocaleString()}件）</button></div>`
          : ""
      }
    </section>
  `;
}

function hoursManagementPage() {
  return `
    ${header("工数管理", "作業時間の登録・集計（準備中）")}
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <p class="empty-note">この機能は準備中です。</p>
      </article>
    </section>
  `;
}

function docTabLabelForSealApplication(tab) {
  return DOC_SEAL_TAB_LABELS[tab] || tab || "—";
}

/** 申請管理に載る「申請中」の捺印キー一覧（`projectId:docTab`） */
function listPendingSealApplicationKeys() {
  const keys = [];
  const sealMap = uiState.docSealStatus || {};
  for (const [mapKey, st] of Object.entries(sealMap)) {
    if (st !== "申請中") continue;
    if (mapKey.indexOf(":") < 1) continue;
    keys.push(mapKey);
  }
  return keys;
}

function ensureAppMgmtReviewMemos() {
  if (!uiState.appMgmtReviewMemos || typeof uiState.appMgmtReviewMemos !== "object") {
    uiState.appMgmtReviewMemos = {};
  }
  for (const k of APP_MGMT_MEMO_KINDS) {
    if (!uiState.appMgmtReviewMemos[k] || typeof uiState.appMgmtReviewMemos[k] !== "object") {
      uiState.appMgmtReviewMemos[k] = {};
    }
  }
}

function getAppMgmtReviewMemo(kind, key) {
  ensureAppMgmtReviewMemos();
  return String(uiState.appMgmtReviewMemos[kind]?.[String(key)] ?? "");
}

function setAppMgmtReviewMemo(kind, key, text) {
  ensureAppMgmtReviewMemos();
  const kk = String(key);
  const t = String(text ?? "");
  if (!t.trim()) delete uiState.appMgmtReviewMemos[kind][kk];
  else uiState.appMgmtReviewMemos[kind][kk] = t;
}

/** 確認メモの対象カテゴリ（サーバ api/application-review-memos.js の KINDS と一致させる） */
const APP_MGMT_MEMO_KINDS = new Set(["contract", "seal", "tool", "unlock", "dev", "changereq", "equip", "costorder"]);

/** 確認メモを API へ保存（失敗時はメモを元に戻す） */
async function persistAppMgmtReviewMemoToServer(kind, key, text) {
  if (!authToken) return false;
  if (!APP_MGMT_MEMO_KINDS.has(kind)) return false;
  const prev = getAppMgmtReviewMemo(kind, key);
  setAppMgmtReviewMemo(kind, key, text);
  try {
    const res = await fetch("/api/application-review-memos", {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ kind, key, text })
    });
    if (!res.ok) {
      setAppMgmtReviewMemo(kind, key, prev);
      showToastError("確認メモを保存できませんでした");
      return false;
    }
    return true;
  } catch {
    setAppMgmtReviewMemo(kind, key, prev);
    showToastError("確認メモを保存できませんでした");
    return false;
  }
}

/** 契約マスタ行から承認フロー解決用の申請者ユーザーID（社内担当者名 → users） */
function applicantUserIdForContractRow(c) {
  const t = String(c?.tantou || "").trim();
  if (t) {
    const u = users.find((x) => String(x.name || "").trim() === t);
    if (u?.id != null) return String(u.id);
  }
  return String(currentUser?.id || users[0]?.id || "").trim();
}

/** 承認フローIDから承認者チェーン（重複除く・表示用） */
function buildReferenceApprovalChainForFlowId(flowId) {
  const fid = String(flowId || "").trim();
  if (!fid) return [];
  const deptLike = getDeptLikeObjectForApprovalFlowId(fid);
  const steps = getApprovalStepsForDept(deptLike.id);
  const chain = [];
  for (let stepIdx = 0; stepIdx < steps.length; stepIdx += 1) {
    const u = resolveApprovalStepUser(deptLike, stepIdx);
    if (u?.id != null && !chain.some((e) => String(e.user.id) === String(u.id))) chain.push({ user: u });
  }
  return chain;
}

/** 申請内容モーダル用: 承認者を縦に並べ、↓ でつなぐ */
function approvalFlowVerticalHtmlFromChain(chain, approvedCount) {
  const n = Math.max(0, Math.min(Math.floor(Number(approvedCount) || 0), chain.length));
  if (!chain.length) {
    return `<span class="pj-muted">—（承認者未設定）</span>`;
  }
  const bits = [];
  for (let i = 0; i < chain.length; i += 1) {
    const name = escapeHtml(String(chain[i].user?.name || "—").trim() || "—");
    let tone = "later";
    let statusLabel = "未承認";
    if (i < n) {
      tone = "done";
      statusLabel = "承認済";
    } else if (i === n) {
      tone = "current";
      statusLabel = "承認待ち";
    }
    bits.push(
      `<div class="app-mgmt-approval-flow-v-step app-mgmt-approval-flow-v-step--${tone}"><span class="app-mgmt-approval-flow-v-name">${name}</span><span class="app-mgmt-approval-flow-v-status">${escapeHtml(statusLabel)}</span></div>`
    );
    if (i < chain.length - 1) {
      bits.push(`<div class="app-mgmt-approval-flow-v-arrow" aria-hidden="true">↓</div>`);
    }
  }
  return `<div class="app-mgmt-approval-flow-v">${bits.join("")}</div>`;
}

/** 申請内容モーダル用の行ヘルパー（開発申請の旧モーダルの体裁を全カテゴリで共通利用） */
function appMgmtDetailRowHtml(label, valueHtml) {
  return `<div class="at-review-row"><div class="at-review-row-label">${escapeHtml(label)}</div><div class="at-review-row-value">${valueHtml}</div></div>`;
}
function appMgmtDetailTextRowHtml(label, value) {
  return appMgmtDetailRowHtml(label, value ? escapeHtml(value).replace(/\n/g, "<br>") : '<span class="pj-muted">—</span>');
}
function appMgmtDetailUrlRowHtml(label, url) {
  return url ? appMgmtDetailRowHtml(label, allowedToolUrlLink(url)) : "";
}
/** {userId,name}[] 形式の承認者チェーンを approvalFlowVerticalHtmlFromChain が読める形に変換 */
function appMgmtChainToFlowChain(approverChain) {
  return (Array.isArray(approverChain) ? approverChain : []).map((c) => ({ user: { name: c?.name || "" } }));
}
/**
 * 申請内容モーダル用: CC（承認権限なし・通知のみ）を承認フローの下に1行で表示。
 * 承認者と紛れないよう「CC（承認権限なし）」の見出し＋薄い文字色に留める（承認/却下ボタンは出さない）。
 */
function appMgmtCcLineHtml(ccChain) {
  const names = (Array.isArray(ccChain) ? ccChain : [])
    .map((c) => String(c?.name || "").trim())
    .filter(Boolean);
  if (!names.length) return "";
  return `<p class="pj-muted" style="margin:6px 0 0;font-size:12px;">CC（承認権限なし）：${escapeHtml(names.join("、"))}</p>`;
}

function renderApplicationMgmtDetailModal() {
  const m = uiState.appMgmtDetailModal;
  if (!m?.kind || m.key == null || String(m.key) === "") return "";
  const kind = m.kind;
  const key = String(m.key);
  let title = "申請の詳細";
  let bodyHtml = "";
  let linksHtml = "";
  /** 保存の右に置く操作（捺印申請の「承認」） */
  let actionHtml = "";
  const memoVal = escapeHtml(getAppMgmtReviewMemo(kind, key));

  if (kind === "contract") {
    const row = contractMaster.find((x) => String(x.id) === key);
    if (!row) return "";
    title = String(row.name || "—").trim() || "—";
    const applicantId = applicantUserIdForContractRow(row);
    ensureApprovalApproversState();
    ensureApprovalApplicationFlowBindingsState();
    const flowId = getResolvedApprovalFlowIdForContractMaster(applicantId);
    const fid = String(flowId || "").trim();
    let flowBlock = "";
    if (!fid) {
      flowBlock = `<span class="pj-muted">—（部署／フロー未設定）</span>`;
    } else {
      const chain = buildReferenceApprovalChainForFlowId(fid);
      if (!chain.length) {
        flowBlock = isClientApproved(row)
          ? `<p class="pj-muted" style="margin:0;font-size:13px;line-height:1.55;">承認フローに承認者が登録されていませんが、<strong>取引承認は承認済み</strong>です。</p>`
          : `<span class="pj-muted">—（承認者未設定）</span>`;
      } else {
        const apprCount = isClientApproved(row) ? chain.length : 0;
        flowBlock = approvalFlowVerticalHtmlFromChain(chain, apprCount);
      }
    }
    // 登録内容（承認者が判断に使うマスタの中身。空欄は「—」）
    const disp = (v) => {
      const s = String(v ?? "").trim();
      return s ? escapeHtml(s) : '<span class="pj-muted">—</span>';
    };
    const addrDisp = [row.zip ? `〒${row.zip}` : "", `${row.prefecture || ""}${row.addr || ""}`.trim()]
      .filter(Boolean)
      .join(" ");
    const issuerDisp =
      String(row.issuer || "").trim() && row.issuer !== "未設定"
        ? `${escapeHtml(row.issuer)}${row.regNo ? `（${escapeHtml(row.regNo)}）` : ""}`
        : '<span class="pj-muted">—</span>';
    // 申請時に入力された内容（取引予定金額・申請メモ）は contractMaster の行（upsertContractFrom* が正規化）から読む
    const applyNote = String(row.applyNote || "").trim();
    bodyHtml = `
      ${appMgmtDetailRowHtml("区分", disp(row.classification))}
      ${appMgmtDetailRowHtml("顧客番号", disp(row.customerNo))}
      ${appMgmtDetailRowHtml("取引予定金額（おおよそ）", row.expectedAmount != null ? escapeHtml(`¥${Number(row.expectedAmount).toLocaleString("ja-JP")}`) : '<span class="pj-muted">—</span>')}
      ${clientApplyCreditInfoRows(row.capitalAmount, row.establishedYm)
        .map((r) => appMgmtDetailRowHtml(r.label, r.value === "—" ? '<span class="pj-muted">—</span>' : escapeHtml(r.value)))
        .join("")}
      ${applyNote ? appMgmtDetailTextRowHtml("申請メモ", applyNote) : ""}
      ${appMgmtDetailRowHtml("住所", disp(addrDisp))}
      ${appMgmtDetailRowHtml("TEL", disp(row.tel))}
      ${appMgmtDetailRowHtml("Email", disp(row.email))}
      ${appMgmtDetailRowHtml("先方担当者", disp(row.contactPerson))}
      ${appMgmtDetailRowHtml("社内担当", disp(row.tantou))}
      ${appMgmtDetailRowHtml("支払条件", disp(row.payment))}
      ${appMgmtDetailRowHtml("振込手数料", disp(row.transferFeeBurden))}
      ${appMgmtDetailRowHtml("NDA", row.nda ? "締結済" : "未締結")}
      ${appMgmtDetailRowHtml("業務委託契約", row.baseContract ? "締結済" : "未締結")}
      ${appMgmtDetailRowHtml("適格請求書発行", issuerDisp)}
      ${appMgmtDetailRowHtml("初回登録日", disp(row.registeredAt))}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}</div>
    `;
    // 遷移先は取引先詳細ページ（申請時の添付・コメント・AIチェック履歴が見える。契約一覧ドロワーでは添付が見えない）
    const flowTarget = clientFlowBuildTarget(row);
    linksHtml = flowTarget
      ? `<button type="button" class="inline-btn ghost" data-client-flow-detail="${escapeHtmlAttr(flowTarget)}">取引先詳細</button>`
      : `<button type="button" class="inline-btn ghost" data-contract-open="${escapeHtmlAttr(row.id)}">契約詳細</button>`;
  } else if (kind === "seal") {
    const parsed = parseDocSealKey(key);
    if (!parsed?.pid || !parsed.tab) return "";
    if (!docTabHasSealWorkflow(parsed.tab)) return "";
    const p = projects.find((x) => String(x.id) === String(parsed.pid));
    const docLabel = docTabLabelForSealApplication(parsed.tab);
    title = `${docLabel}（捺印）`;
    const flowLabel = escapeHtml(getSealApprovalFlowLabelForMapKey(key));
    const appr = sealApprovalProgressDisplayHtml(key, { horizontal: true });
    // 金額・申請日を取得
    let sealModalAmount = "";
    try {
      if (p && docTabHasSealWorkflow(parsed.tab)) {
        const docForAmt2 = buildDocFromProjectDraft(p, parsed.tab);
        const totAmt2 = calc(docForAmt2.lines || [], docForAmt2.taxRate ?? 0.1).total;
        if (totAmt2 > 0) sealModalAmount = yen.format(totAmt2);
      }
    } catch { /* noop */ }
    const sealApplicantName = getSealApplicantUserForMapKey(key)?.name || "";
    const sealAppliedAt = sealApprovalProgressRecord(key)?.appliedAt;
    const sealAppliedDisp = sealAppliedAt
      ? (() => { try { const d = new Date(sealAppliedAt); return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}`; } catch { return ""; } })()
      : "";
    bodyHtml = `
      <p style="margin:0 0 8px;font-size:14px;color:#334155;line-height:1.5;">案件 <strong>${escapeHtml(p?.name || "—")}</strong></p>
      <div style="display:flex;gap:16px;margin:0 0 10px;font-size:13px;color:#475467;flex-wrap:wrap;">
        <span>書類: <strong style="color:#1e293b;">${escapeHtml(docLabel)}</strong></span>
        ${sealModalAmount ? `<span>金額（税込）: <strong style="color:#1e293b;">${escapeHtml(sealModalAmount)}</strong></span>` : ""}
        ${sealApplicantName ? `<span>申請者: <strong style="color:#1e293b;">${escapeHtml(sealApplicantName)}</strong></span>` : ""}
        ${sealAppliedDisp ? `<span>申請日: <strong style="color:#1e293b;">${escapeHtml(sealAppliedDisp)}</strong></span>` : ""}
        ${sealApprovalProgressRecord(key)?.dueDate ? `<span>承認期日: <strong style="color:#1e293b;">${escapeHtml(sealApprovalProgressRecord(key).dueDate.replace(/-/g, "/"))}</strong></span>` : ""}
      </div>
      ${sealApprovalProgressRecord(key)?.applicantComment ? `<p class="seal-apply-comment-view">${escapeHtml(sealApprovalProgressRecord(key).applicantComment)}</p>` : ""}
      ${sealRevokeNoticeHtml(key)}
      <p style="margin:0 0 6px;font-size:12px;font-weight:600;color:#334155;">申請フロー：${flowLabel}</p>
      <div style="margin-bottom:12px;">${appr}</div>
    `;
    const docSlug = docTabHasSealWorkflow(parsed.tab) ? parsed.tab : "invoice";
    // ボタンは「該当書類へ・保存・承認」の3つ（2026-10 要望で「案件（請求タブ）」は外した）
    linksHtml = `<button type="button" class="inline-btn ghost" data-app-mgmt-nav-project-docs="${escapeHtmlAttr(String(parsed.pid))}:${escapeHtmlAttr(docSlug)}">該当書類へ</button>`;
    if (getDocSealStatus(parsed.pid, parsed.tab) === "申請中" && isCurrentUserSealApproverForPendingKey(key)) {
      actionHtml = `<button type="button" class="cl-btn cl-btn-primary" data-app-mgmt-seal-approve="${escapeHtmlAttr(key)}">✓ 承認</button>`;
    }
  } else if (kind === "tool") {
    const a = (uiState.allowedToolApplications || []).find((x) => String(x.id) === key);
    if (!a) return "";
    const toolKindLabel = { register: "登録", edit: "編集", delete: "削除" };
    title = a.title || "—";
    const flowChain = appMgmtChainToFlowChain(a.approverChain);
    const flowBlock = flowChain.length
      ? approvalFlowVerticalHtmlFromChain(flowChain, a.approvedCount || 0)
      : `<span class="pj-muted">—（承認者未設定）</span>`;
    bodyHtml = `
      ${appMgmtDetailRowHtml("区分", escapeHtml(toolKindLabel[a.kind] || "登録"))}
      ${appMgmtDetailRowHtml("カテゴリ", escapeHtml(a.category || "—") + (a.subcategory ? ` / ${escapeHtml(a.subcategory)}` : ""))}
      ${appMgmtDetailRowHtml("申請者", escapeHtml(a.applicantName || "—"))}
      ${appMgmtDetailTextRowHtml("申請理由", a.reason)}
      ${a.vendor ? appMgmtDetailRowHtml("開発会社", escapeHtml(a.vendor)) : ""}
      ${appMgmtDetailUrlRowHtml("URL", a.url)}
      ${toolAppAttachmentLinksHtml(a) ? appMgmtDetailRowHtml("添付", toolAppAttachmentLinksHtml(a)) : ""}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}${appMgmtCcLineHtml(a.ccChain)}</div>
      ${a.status === "却下" && a.reviewComment ? appMgmtDetailRowHtml("却下理由", escapeHtml(a.reviewComment)) : ""}
    `;
  } else if (kind === "costorder") {
    // 原価の発注申請（15z-cost-order-applications.js）
    const d = costOrderAppDetail(key);
    if (!d) return "";
    title = d.title;
    bodyHtml = d.bodyHtml;
  } else if (kind === "equip") {
    const a = (uiState.equipmentApplications || []).find((x) => String(x.id) === key);
    if (!a) return "";
    title = a.itemType || "—";
    const flowChain = appMgmtChainToFlowChain(a.approverChain);
    const flowBlock = flowChain.length
      ? approvalFlowVerticalHtmlFromChain(flowChain, a.approvedCount || 0)
      : `<span class="pj-muted">—（承認者未設定）</span>`;
    bodyHtml = `
      ${appMgmtDetailRowHtml("品目", escapeHtml(a.itemType || "—"))}
      ${appMgmtDetailRowHtml("申請者", escapeHtml(a.applicantName || "—"))}
      ${appMgmtDetailTextRowHtml("希望内容", a.detail)}
      ${appMgmtDetailTextRowHtml("申請理由", a.reason)}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}${appMgmtCcLineHtml(a.ccChain)}</div>
    `;
  } else if (kind === "unlock") {
    const a = (uiState.projectUnlockApplications || []).find((x) => String(x.id) === key);
    if (!a) return "";
    title = a.projectName || "—";
    const flowChain = appMgmtChainToFlowChain(a.approverChain);
    const flowBlock = flowChain.length
      ? approvalFlowVerticalHtmlFromChain(flowChain, a.approvedCount || 0)
      : `<span class="pj-muted">—（承認者未設定）</span>`;
    bodyHtml = `
      ${appMgmtDetailRowHtml("請求月", escapeHtml(a.billingMonth || "—"))}
      ${appMgmtDetailRowHtml("申請者", escapeHtml(a.applicantName || "—"))}
      ${appMgmtDetailTextRowHtml("解除理由", a.reason)}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}${appMgmtCcLineHtml(a.ccChain)}</div>
      ${a.status === "却下" && a.reviewComment ? appMgmtDetailRowHtml("却下理由", escapeHtml(a.reviewComment)) : ""}
    `;
  } else if (kind === "dev") {
    const a = (uiState.developmentApplications || []).find((x) => String(x.id) === key);
    if (!a) return "";
    const yn = (b) => (b ? "あり" : "なし");
    title = a.title || "—";
    const flowChain = appMgmtChainToFlowChain(a.approverChain);
    const flowBlock = flowChain.length
      ? approvalFlowVerticalHtmlFromChain(flowChain, a.approvedCount || 0)
      : `<span class="pj-muted">—（承認者未設定）</span>`;
    bodyHtml = `
      ${appMgmtDetailRowHtml("申請者", escapeHtml(a.applicantName || "—"))}
      ${appMgmtDetailTextRowHtml("目的", a.purpose)}
      ${appMgmtDetailTextRowHtml("想定利用者", a.targetUsers)}
      ${appMgmtDetailTextRowHtml("既存ツールで代替できない理由", a.alternativeReason)}
      ${appMgmtDetailTextRowHtml("解決する課題・売上見込み", a.problemAndRevenue)}
      ${appMgmtDetailTextRowHtml("セキュリティの対策", a.securityMeasures)}
      ${appMgmtDetailTextRowHtml("利用するドメイン・サーバー環境", a.domainServerInfo)}
      ${appMgmtDetailRowHtml("担当責任者", a.maintainerName ? escapeHtml(a.maintainerName) : '<span class="pj-muted">—</span>')}
      ${appMgmtDetailTextRowHtml("保守対策（担当者退職時）", a.maintenanceContinuity)}
      ${appMgmtDetailRowHtml("個人情報・顧客データを扱う", yn(a.handlesPii))}
      ${appMgmtDetailRowHtml("外部AI・APIを利用する", yn(a.usesExternalAi))}
      ${appMgmtDetailUrlRowHtml("サービスURL", a.serviceUrl)}
      ${appMgmtDetailUrlRowHtml("GitHub", a.githubUrl)}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}${appMgmtCcLineHtml(a.ccChain)}</div>
      ${a.status === "却下" && a.reviewComment ? appMgmtDetailRowHtml("却下理由", escapeHtml(a.reviewComment)) : ""}
    `;
  } else if (kind === "changereq") {
    const cr = (uiState.masterChangeRequests || []).find((x) => String(x.id) === key);
    if (!cr) return "";
    const isDelete = cr.action === "delete";
    title = cr.entity_name_snapshot || "—";
    const changes = Array.isArray(cr.changes) ? cr.changes : [];
    const contentHtml = isDelete
      ? `<span class="pj-muted">この対象を削除します</span>`
      : changes.length === 0
        ? `<span class="pj-muted">—</span>`
        : changes.map((c) => `<div>${escapeHtml(c.l)}: ${escapeHtml(c.b || "（空）")} → ${escapeHtml(c.a || "（空）")}</div>`).join("");
    bodyHtml = `
      ${appMgmtDetailRowHtml("区分", escapeHtml(cr.entity_type === "partner" ? "パートナー" : "クライアント"))}
      ${appMgmtDetailRowHtml("種別", `<span class="pj-badge ${isDelete ? "danger" : "info"}">${isDelete ? "削除申請" : "更新申請"}</span>`)}
      ${appMgmtDetailRowHtml("申請者", escapeHtml(cr.requested_by_name || "—"))}
      ${appMgmtDetailRowHtml("内容", contentHtml)}
    `;
    // 遷移先は取引先詳細ページ（添付書類・コメントはここでしか見えない。契約承認と同じ導線）
    const crFlowTarget = cr.entity_id != null ? `${cr.entity_type === "partner" ? "partner" : "client"}:${cr.entity_id}` : "";
    linksHtml = crFlowTarget
      ? `<button type="button" class="inline-btn ghost" data-client-flow-detail="${escapeHtmlAttr(crFlowTarget)}">取引先詳細</button>`
      : "";
  } else {
    return "";
  }

  return `
    <div class="composer-backdrop" data-app-mgmt-detail-close="1"></div>
    <aside class="composer-panel app-mgmt-detail-panel fp-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="app-mgmt-detail-title" style="grid-template-rows:auto 1fr auto;">
      <div class="composer-head">
        <div><h3 id="app-mgmt-detail-title">${escapeHtml(title)}</h3></div>
        <button class="inline-btn ghost" type="button" data-app-mgmt-detail-close="1">閉じる</button>
      </div>
      <div class="composer-body" style="padding:16px 20px;">
        ${bodyHtml}
        <label style="display:block;font-size:12px;font-weight:600;color:#334155;margin-top:4px;">確認メモ
          <textarea class="pj-input app-mgmt-memo-ta" rows="3" style="width:100%;margin-top:6px;resize:vertical;" data-app-mgmt-memo data-app-mgmt-memo-kind="${escapeHtmlAttr(kind)}" data-app-mgmt-memo-key="${escapeHtmlAttr(key)}">${memoVal}</textarea>
        </label>
      </div>
      <div class="composer-foot" style="justify-content:flex-end;flex-wrap:wrap;gap:8px;">
        ${linksHtml}
        <button class="cl-btn ${actionHtml ? "cl-btn-outline" : "cl-btn-primary"}" type="button" data-app-mgmt-detail-save="1">保存</button>
        ${actionHtml}
      </div>
    </aside>
  `;
}

let appMgmtMemoBlurBound = false;
function bindAppMgmtMemoBlurListenersOnce() {
  if (appMgmtMemoBlurBound) return;
  appMgmtMemoBlurBound = true;
  document.addEventListener(
    "blur",
    (e) => {
      const ta = e.target;
      if (!(ta instanceof HTMLTextAreaElement)) return;
      if (!ta.matches("[data-app-mgmt-memo]")) return;
      const kind = ta.dataset.appMgmtMemoKind || "";
      const memoKey = ta.dataset.appMgmtMemoKey || "";
      if (!kind || !memoKey) return;
      if (!APP_MGMT_MEMO_KINDS.has(kind)) return;
      void persistAppMgmtReviewMemoToServer(kind, memoKey, ta.value).then((ok) => {
        if (!ok) ta.value = getAppMgmtReviewMemo(kind, memoKey);
        render();
      });
    },
    true
  );
}

/**
 * 申請承認用: 取引承認（approval_status のみ）を DB に反映。application-management 権限で可。
 * @param {{ clientDbId?: number, partnerDbId?: number }} payload
 * @returns {Promise<{ ok: boolean, updated?: number }>}
 */
async function persistTransactionApprovalApi(payload) {
  if (!authToken) return { ok: false };
  try {
    const res = await fetch("/api/transaction-approval", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, status: res.status, error: typeof data.error === "string" ? data.error : "" };
    return {
      ok: true,
      updated: Number(data.updated) || 0,
      customerNo: typeof data.customerNo === "string" ? data.customerNo : null,
      docStatus: data.docStatus && typeof data.docStatus === "object" ? data.docStatus : null
    };
  } catch {
    return { ok: false };
  }
}

/**
 * 申請承認（契約関係）: 取引承認を「承認済」に更新（API 失敗時は行を元に戻す）
 * @param {string[]} clientIds 契約マスター行の id（クライアントは c{n}、パートナーは partner-{n}）
 */
async function persistContractMasterApproveMany(clientIds) {
  let updated = 0;
  let fail = 0;
  for (const rawId of clientIds) {
    const id = String(rawId || "");
    if (!id) continue;
    const idx = contractMaster.findIndex((item) => String(item.id) === id);
    if (idx < 0) continue;
    const row = contractMaster[idx];
    if (isClientApproved(row)) continue;
    const prevStatus = row.approvalStatus;
    const isPartnerRow =
      String(row.classification || "").trim() === "パートナー" || id.startsWith("partner-");
    const partnerDbId =
      isPartnerRow && row.sourcePartnerId != null && Number.isFinite(Number(row.sourcePartnerId))
        ? Number(row.sourcePartnerId)
        : null;
    const list = uiState.partnersCache || [];
    const partnerIdx =
      partnerDbId != null && Number.isFinite(partnerDbId) ? list.findIndex((p) => Number(p.id) === partnerDbId) : -1;
    const prevPartnerRow = partnerIdx >= 0 ? { ...list[partnerIdx] } : null;

    contractMaster[idx].approvalStatus = "承認済";

    if (isPartnerRow) {
      if (partnerDbId == null || !Number.isFinite(partnerDbId) || partnerDbId <= 0) {
        contractMaster[idx].approvalStatus = prevStatus;
        fail += 1;
        continue;
      }
      if (partnerIdx >= 0) {
        list[partnerIdx] = { ...list[partnerIdx], approval_status: "承認済" };
        upsertContractFromPartner(list[partnerIdx]);
      }
      const api = await persistTransactionApprovalApi({ partnerDbId });
      if (!api.ok) {
        contractMaster[idx].approvalStatus = prevStatus;
        if (prevPartnerRow && partnerIdx >= 0) {
          list[partnerIdx] = prevPartnerRow;
          upsertContractFromPartner(prevPartnerRow);
        }
        fail += 1;
      } else {
        // 承認で自動反映された値（顧客番号の発番・添付からの締結状態）をリロード無しでローカルへ
        if (partnerIdx >= 0 && (api.customerNo || api.docStatus)) {
          const next = { ...list[partnerIdx] };
          if (api.customerNo) next.customer_no = api.customerNo;
          if (api.docStatus?.ndaStatus === "済") next.nda_status = "済";
          if (api.docStatus?.contractStatus === "済") next.contract_status = "済";
          list[partnerIdx] = next;
          upsertContractFromPartner(next);
        }
        updated += 1;
      }
      continue;
    }

    syncClientFromContractRow(contractMaster[idx]);
    const patched = clients.find((x) => x.id === contractMaster[idx].id);
    if (!patched?.dbId) {
      contractMaster[idx].approvalStatus = prevStatus;
      syncClientFromContractRow(contractMaster[idx]);
      fail += 1;
      continue;
    }
    const api = await persistTransactionApprovalApi({ clientDbId: patched.dbId });
    if (!api.ok) {
      contractMaster[idx].approvalStatus = prevStatus;
      syncClientFromContractRow(contractMaster[idx]);
      fail += 1;
    } else {
      // 承認で自動反映された値（顧客番号の発番・添付からの締結状態）をリロード無しでローカルへ
      if (api.customerNo) patched.customerNo = api.customerNo;
      if (api.docStatus?.ndaStatus === "済") patched.ndaStatus = "済";
      if (api.docStatus?.contractStatus === "済") patched.contractStatus = "済";
      if (api.customerNo || api.docStatus) upsertContractFromClient(patched);
      updated += 1;
    }
  }
  return { updated, fail };
}

/** 申請管理：クライアント/パートナーの更新・削除申請（保留型フロー）の一覧を取得 */
async function fetchMasterChangeRequests() {
  if (!authToken) return;
  const r = await apiFetch("/api/master-change-requests");
  if (r.ok) uiState.masterChangeRequests = Array.isArray(r.data.requests) ? r.data.requests : [];
  uiState.masterChangeRequestsLoaded = true;
  render();
}

/** 経理・申請管理（捺印などの承認キュー） */
/** 申請日表示: YYYY/MM/DD（不正・空は「—」） */
function appMgmtDateDisp(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "—";
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return "—";
  }
}

/** 申請承認（統一テーブル）: 全カテゴリ共通の行データを組み立てる */
function buildAppMgmtRows() {
  const rows = [];

  // 請求書（捺印）：いま自分が承認待ちのもの
  const sealMap = uiState.docSealStatus || {};
  for (const [mapKey, st] of Object.entries(sealMap)) {
    if (st !== "申請中") continue;
    const colon = mapKey.indexOf(":");
    if (colon < 1) continue;
    if (!isCurrentUserSealApproverForPendingKey(mapKey)) continue;
    const pid = mapKey.slice(0, colon);
    const tab = mapKey.slice(colon + 1);
    const p = projects.find((x) => x.id === pid);
    const docLabel = docTabLabelForSealApplication(tab);
    const docSlug = docTabHasSealWorkflow(tab) ? tab : "invoice";
    const chain = getSealApprovalChainForMapKey(mapKey);
    const approvedCount = getSealApprovalProgressApprovedCount(mapKey);
    rows.push({
      uid: `seal:${mapKey}`,
      kind: "seal",
      key: mapKey,
      categoryLabel: "捺印申請",
      subLabel: `${sealIsResubmission(mapKey) ? "【再申請】" : ""}${p?.name || "（案件不明）"}・${docLabel}`,
      // 承認期日は専用の列に出す（申請内容の後ろに付けていた「（期日 10/14）」は外した。2026-10）
      dueIso: sealApprovalProgressRecord(mapKey)?.dueDate || "",
      applicantName: getSealApplicantUserForMapKey(mapKey)?.name || "—",
      appliedAtIso: sealApprovalProgressRecord(mapKey)?.appliedAt || "",
      statusHtml: chain.length ? `${approvedCount}/${chain.length}` : "—",
      selectable: true,
      opHtml: `<button type="button" class="chip-btn ghost" data-app-mgmt-doc-window="${escapeHtmlAttr(String(pid))}:${escapeHtmlAttr(docSlug)}" title="書類を別ウィンドウで開く">書類</button>
        <button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-seal-approve="${escapeHtmlAttr(mapKey)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-seal-reject="${escapeHtmlAttr(mapKey)}">却下</button>`
    });
  }

  // 契約関係（取引承認）：申請ボタンで「申請中」になったものだけキューに載せる
  // （未申請＝仮登録は営業と管理部が確認中の段階なので、承認者には見せない。却下済みは再申請待ちとして除外）。
  // 承認権限者（マスター/経理）のみに表示。
  const contractQueue = canManageClientPartnerMaster()
    ? contractMaster.filter((c) => c.approvalStatus === "申請中")
    : [];
  const contractApproving = !!uiState.appMgmtContractApproving;
  for (const c of contractQueue) {
    const cid = String(c.id);
    // 申請内容リンクの飛び先＝取引先詳細ページ（申請時の添付・コメントが見えるのはこちら。ユーザー指摘）
    const flowTarget = clientFlowBuildTarget(c);
    rows.push({
      uid: `contract:${cid}`,
      kind: "contract",
      key: cid,
      flowTarget,
      categoryLabel: "新規登録申請",
      subLabel: `${c.name || "—"}（${c.classification || "—"}）`,
      applicantName: "—",
      appliedAtIso: c.registeredAt || "",
      statusHtml: `<span class="pj-badge warn">申請中</span>`,
      selectable: true,
      opHtml: `<button type="button" class="chip-btn" data-app-mgmt-detail-open="1" data-app-mgmt-detail-kind="contract" data-app-mgmt-detail-key="${escapeHtmlAttr(cid)}">詳細</button>
        <button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-contract-approve="${escapeHtmlAttr(c.id)}"${contractApproving ? " disabled" : ""}>${contractApproving ? "承認中…" : "✓ 承認"}</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-contract-reject="${escapeHtmlAttr(cid)}">却下</button>`
    });
  }

  // ツール許可申請：いま自分が承認待ちのもの（サーバが canApprove を付与）。
  // マスターは自分の番でない申請中も含め全件を見える（操作は自分の番のときのみ・自社ツール開発申請と同方式）。
  const isMasterToolViewer = isMasterRoleUser(currentUser);
  const pendingToolApps = (uiState.allowedToolApplications || []).filter(
    (a) => a && (a.canApprove || (isMasterToolViewer && a.status === "申請中"))
  );
  const toolKindLabel = { register: "登録", edit: "編集", delete: "削除" };
  for (const a of pendingToolApps) {
    const id = String(a.id);
    const currentApproverName = a.approverChain?.find((c) => c.userId === a.currentApproverUserId)?.name || "";
    rows.push({
      uid: `tool:${id}`,
      kind: "tool",
      key: id,
      categoryLabel: "ツール許可",
      subLabel: `${a.title || "—"}（${toolKindLabel[a.kind] || "登録"}）`,
      applicantName: a.applicantName || "—",
      appliedAtIso: a.createdAt || "",
      statusHtml: a.chainLength ? `${a.approvedCount}/${a.chainLength}` : "—",
      selectable: !!a.canApprove,
      opHtml: a.canApprove
        ? `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-tool-approve="${escapeHtmlAttr(id)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-tool-reject="${escapeHtmlAttr(id)}">却下</button>`
        : `<span class="pj-muted">${currentApproverName ? `承認待ち：${escapeHtml(currentApproverName)}` : "—"}</span>`
    });
  }

  // 備品の貸与申請：ツール許可申請と同じ見せ方（マスターは自分の番でない申請中も見える。操作は自分の番のときのみ）
  const pendingEquipApps = (uiState.equipmentApplications || []).filter(
    (a) => a && (a.canApprove || (isMasterToolViewer && a.status === "申請中"))
  );
  for (const a of pendingEquipApps) {
    const id = String(a.id);
    const currentApproverName = a.approverChain?.find((c) => c.userId === a.currentApproverUserId)?.name || "";
    rows.push({
      uid: `equip:${id}`,
      kind: "equip",
      key: id,
      categoryLabel: "備品貸与",
      subLabel: a.detail ? `${a.itemType}（${a.detail}）` : a.itemType || "—",
      applicantName: a.applicantName || "—",
      appliedAtIso: a.createdAt || "",
      statusHtml: a.chainLength ? `${a.approvedCount}/${a.chainLength}` : "—",
      selectable: !!a.canApprove,
      opHtml: a.canApprove
        ? `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-equip-approve="${escapeHtmlAttr(id)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-equip-reject="${escapeHtmlAttr(id)}">却下</button>`
        : `<span class="pj-muted">${currentApproverName ? `承認待ち：${escapeHtml(currentApproverName)}` : "—"}</span>`
    });
  }

  // 原価の発注申請（制作費・開発費・外注先に頼む前）：見せ方は備品と同じ（15z-cost-order-applications.js）
  rows.push(...costOrderAppMgmtRows());

  // 案件ロック解除申請：いま自分が承認待ちのもの（サーバが canApprove を付与）
  const pendingUnlockApps = (uiState.projectUnlockApplications || []).filter((a) => a && a.canApprove);
  for (const a of pendingUnlockApps) {
    const id = String(a.id);
    rows.push({
      uid: `unlock:${id}`,
      kind: "unlock",
      key: id,
      categoryLabel: "ロック解除",
      subLabel: `${a.projectName || "—"}（請求月：${a.billingMonth || "—"}）`,
      applicantName: a.applicantName || "—",
      appliedAtIso: a.createdAt || "",
      statusHtml: a.chainLength ? `${a.approvedCount}/${a.chainLength}` : "—",
      selectable: true,
      opHtml: `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-unlock-approve="${escapeHtmlAttr(id)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-unlock-reject="${escapeHtmlAttr(id)}">却下</button>`
    });
  }

  // 自社ツール開発の事前申請：いま自分が承認待ちのもの（サーバが canApprove を付与）。
  // マスターは自分の番でない申請中も含め全件を見える（操作は自分の番のときのみ）。
  const isMasterDevViewer = isMasterRoleUser(currentUser);
  const pendingDevApps = (uiState.developmentApplications || []).filter(
    (a) => a && (a.canApprove || (isMasterDevViewer && a.status === "申請中"))
  );
  for (const a of pendingDevApps) {
    const id = String(a.id);
    const currentApproverName = a.approverChain?.find((c) => c.userId === a.currentApproverUserId)?.name || "";
    rows.push({
      uid: `dev:${id}`,
      kind: "dev",
      key: id,
      categoryLabel: "開発申請",
      subLabel: a.title || "—",
      applicantName: a.applicantName || "—",
      appliedAtIso: a.createdAt || "",
      statusHtml: a.chainLength ? `${a.approvedCount}/${a.chainLength}` : "—",
      selectable: !!a.canApprove,
      opHtml: a.canApprove
        ? `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-dev-approve="${escapeHtmlAttr(id)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-dev-reject="${escapeHtmlAttr(id)}">却下</button>`
        : `<span class="pj-muted">${currentApproverName ? `承認待ち：${escapeHtml(currentApproverName)}` : "—"}</span>`
    });
  }

  // クライアント/パートナーの更新・削除申請（保留型フロー）
  const changeReqBusyId = uiState.appMgmtChangeReqBusyId || "";
  for (const cr of uiState.masterChangeRequests || []) {
    const id = String(cr.id);
    const isDelete = cr.action === "delete";
    const busy = changeReqBusyId === id;
    // 遷移先は取引先詳細ページ（契約承認と同じ。対象・添付・コメントがここで見える。ユーザー指摘）
    const flowTarget = cr.entity_id != null ? `${cr.entity_type === "partner" ? "partner" : "client"}:${cr.entity_id}` : "";
    rows.push({
      uid: `changereq:${id}`,
      kind: "changereq",
      key: id,
      flowTarget,
      isDelete,
      categoryLabel: isDelete ? "削除申請" : "更新申請",
      subLabel: `${cr.entity_name_snapshot || "—"}（${cr.entity_type === "partner" ? "パートナー" : "クライアント"}）`,
      applicantName: cr.requested_by_name || "—",
      appliedAtIso: cr.requested_at || "",
      statusHtml: `<span class="pj-badge info">審査中</span>`,
      selectable: true,
      opHtml: `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-changereq-approve="${escapeHtmlAttr(id)}"${busy ? " disabled" : ""}>✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-changereq-reject="${escapeHtmlAttr(id)}"${busy ? " disabled" : ""}>却下</button>`
    });
  }

  rows.sort((a, b) => {
    const ta = a.appliedAtIso ? new Date(a.appliedAtIso).getTime() : 0;
    const tb = b.appliedAtIso ? new Date(b.appliedAtIso).getTime() : 0;
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });
  return rows;
}

/** 経理・申請管理（捺印などの承認キュー） */
function applicationManagementPage() {
  // ツール許可申請（承認フロー連動）のデータを取得（許可ツールページと共有）。
  if (!uiState.allowedToolsLoaded) fetchOnceInFlight("allowedTools", fetchAllowedToolsData);
  // 備品の貸与申請（社内申請ページと共有）
  if (!uiState.equipmentApplicationsLoaded) fetchOnceInFlight("equipmentApplications", fetchEquipmentApplications);
  // 案件ロック解除申請のデータを取得
  if (uiState.projectUnlockApplications === null && !uiState.projectUnlockAppsLoading) {
    void fetchProjectUnlockApplications();
  }
  // 自社ツール開発の事前申請のデータを取得（開発ページと共有）。
  if (!uiState.developmentApplicationsLoaded) fetchOnceInFlight("developmentApplications", fetchDevelopmentApplications);
  // クライアント/パートナーの更新・削除申請（保留型フロー）のデータを取得
  if (!uiState.masterChangeRequestsLoaded) fetchOnceInFlight("masterChangeRequests", fetchMasterChangeRequests);
  // 捺印申請はログイン時にしか読み込まれず、他の人がその後に出した申請が見えなかった（2026-09-16 実害）。
  // 他の申請種別と同じく、このページを開いた時に取り直す（取得できたら再描画）。
  void refreshDocSealApplications()
    .then((updated) => {
      if (updated) render();
    })
    .catch(() => {});

  const rows = buildAppMgmtRows();
  const rowUidSet = new Set(rows.map((r) => r.uid));
  const sel = uiState.appMgmtSelected || {};
  for (const k of Object.keys(sel)) {
    if (!rowUidSet.has(k)) delete sel[k];
  }
  const selectableRows = rows.filter((r) => r.selectable);
  const selectedCount = selectableRows.filter((r) => sel[r.uid] === true).length;
  const headChecked = selectableRows.length > 0 && selectableRows.every((r) => sel[r.uid] === true);
  const headIndeterminate = selectableRows.some((r) => sel[r.uid] === true) && !headChecked;
  // 選べる行が無いタブでは一括バー（未選択時の案内）も出さない
  const bulkBarClass = !selectableRows.length
    ? "pj-bulk-bar app-mgmt-seal-bulk pj-bulk-bar--none"
    : selectedCount > 0
      ? "pj-bulk-bar app-mgmt-seal-bulk show"
      : "pj-bulk-bar app-mgmt-seal-bulk";

  const rowsHtml =
    rows.length === 0
      ? ""
      : rows
          .map((r) => {
            const checkCell = r.selectable
              ? `<input type="checkbox" data-app-mgmt-row-select="${escapeHtmlAttr(r.uid)}" ${sel[r.uid] === true ? "checked" : ""} aria-label="この行を選択" />`
              : "";
            // 申請内容はクリックで詳細へ（契約承認・削除申請は取引先詳細ページ＝対象と添付・コメントが見える。
            // 更新申請はフィールド差分が見える申請内容モーダルのまま＝モーダル内にも取引先詳細への導線を追加）
            const contentBtn =
              (r.kind === "contract" || (r.kind === "changereq" && r.isDelete)) && r.flowTarget
                ? `<button type="button" class="pj-link" data-client-flow-detail="${escapeHtmlAttr(r.flowTarget)}">${escapeHtml(r.subLabel)}</button>`
                : r.kind === "contract"
                  ? `<button type="button" class="pj-link" data-contract-open="${escapeHtmlAttr(r.key)}">${escapeHtml(r.subLabel)}</button>`
                  : `<button type="button" class="pj-link" data-app-mgmt-detail-open="1" data-app-mgmt-detail-kind="${escapeHtmlAttr(r.kind)}" data-app-mgmt-detail-key="${escapeHtmlAttr(r.key)}">${escapeHtml(r.subLabel)}</button>`;
            return `<tr>
          <td class="app-mgmt-seal-td-check">${checkCell}</td>
          <td class="app-mgmt-td-cat"><span class="pj-badge app-mgmt-cat app-mgmt-cat--${escapeHtmlAttr(r.kind)}${r.isDelete ? " is-delete" : ""}">${escapeHtml(r.categoryLabel)}</span></td>
          <td class="app-mgmt-td-name">${contentBtn}</td>
          <td class="app-mgmt-td-applicant pj-muted">${escapeHtml(r.applicantName)}</td>
          <td class="app-mgmt-td-applied-at pj-muted">${escapeHtml(appMgmtDateDisp(r.appliedAtIso))}</td>
          <td class="app-mgmt-td-due">${sealDueCellHtml(r.dueIso)}</td>
          <td class="app-mgmt-td-status">${r.statusHtml}</td>
          <td class="app-mgmt-op-cell"><div class="pj-actions-inner">${r.opHtml}</div></td>
        </tr>`;
          })
          .join("");

  return `
    <div class="page pj-page">
      <div class="pj-page-wrap pj-page-wrap--app-mgmt">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>申請承認</h2>
          </div>
        </div>
        ${
          uiState.heavyDataLoading
            ? `<section class="app-mgmt-tab-panel app-mgmt-heavy-load-only" aria-busy="true">${flowPilotNowLoadingBlock({
                subtitle: "案件・申請データを読み込んでいます"
              })}</section>`
            : rows.length === 0
              ? `<section class="app-mgmt-tab-panel"><p class="app-mgmt-all-empty">承認待ちの申請はありません ✓</p></section>`
              : `<section class="app-mgmt-tab-panel" aria-label="申請一覧">
        <div class="${bulkBarClass}">
          <span>${selectedCount}件選択中</span><span class="sep">|</span>
          <button type="button" class="inline-btn" data-app-mgmt-bulk-approve="1">一括承認</button>
          <button type="button" class="inline-btn ghost" data-app-mgmt-clear-selection="1">選択解除</button>
        </div>
        <div class="pj-table-area">
          <table class="pj-table app-mgmt-seal-table app-mgmt-unified-table" data-fp-table="applications">
            <thead>
              <tr>
                <th class="app-mgmt-seal-th-check"><input type="checkbox" data-app-mgmt-select-all="1" ${headChecked ? "checked" : ""} ${headIndeterminate ? 'data-indeterminate="1"' : ""} aria-label="選択可能な行をすべて選択" /></th>
                <th class="app-mgmt-th-cat">申請カテゴリ</th>
                <th class="app-mgmt-th-name">申請内容</th>
                <th class="app-mgmt-th-applicant">申請者</th>
                <th class="app-mgmt-th-applied-at">申請日</th>
                <th class="app-mgmt-th-due">承認期日</th>
                <th class="app-mgmt-th-status">承認状態</th>
                <th class="app-mgmt-seal-th-op">操作</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
      </section>`
        }
      </div>
    </div>
    ${contractRejectModalHtml()}
    ${sealRejectModalHtml()}
    ${toolRejectModalHtml()}
    ${equipRejectModalHtml()}
    ${costOrderRejectModalHtml()}
    ${unlockRejectModalHtml()}
    ${devAppRejectModalHtml()}
  `;
}

/** 契約関係（取引承認）の却下を送信（失敗時はトースト表示のみ、ローカル状態は変更しない） */
async function rejectContractApplication() {
  if (uiState.appMgmtContractApproving) return;
  const draft = uiState.appMgmtContractRejectDraft;
  if (!draft) return;
  const row = contractMaster.find((c) => String(c.id) === String(draft.id));
  if (!row) return;
  const reason = (document.getElementById("contract-reject-comment")?.value || "").trim();
  if (!reason) return showToastError("却下理由を入力してください");
  uiState.appMgmtContractApproving = true;
  render();
  try {
    const isPartnerRow = String(row.classification || "").trim() === "パートナー" || String(row.id).startsWith("partner-");
    let payload = null;
    if (isPartnerRow) {
      const partnerDbId = Number.isFinite(Number(row.sourcePartnerId)) ? Number(row.sourcePartnerId) : null;
      if (partnerDbId != null && partnerDbId > 0) payload = { partnerDbId, decision: "reject", reason };
    } else {
      const patched = clients.find((x) => x.id === row.id);
      if (patched?.dbId) payload = { clientDbId: patched.dbId, decision: "reject", reason };
    }
    if (!payload) {
      uiState.appMgmtContractApproving = false;
      showToastError("対象が見つかりませんでした");
      render();
      return;
    }
    const result = await persistTransactionApprovalApi(payload);
    uiState.appMgmtContractApproving = false;
    if (!result.ok) {
      showToastError(result.error || "却下に失敗しました");
      render();
      return;
    }
    uiState.appMgmtContractRejectDraft = null;
    showToast("却下しました");
    await loadDataFromDB();
  } catch {
    uiState.appMgmtContractApproving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** 契約関係（取引承認）の却下モーダル（申請承認ページ） */
function contractRejectModalHtml() {
  const draft = uiState.appMgmtContractRejectDraft;
  if (!draft) return "";
  const row = contractMaster.find((c) => String(c.id) === String(draft.id));
  if (!row) return "";
  const saving = uiState.appMgmtContractApproving;
  return `
    <div class="cl-mini-modal-overlay open" data-contract-reject-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="contract-reject-h">
        <div class="cl-mini-modal-head">
          <h4 id="contract-reject-h">取引承認を却下</h4>
          <button class="cl-modal-close" type="button" data-contract-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(row.name || "—")}</div>
            <div class="at-app-meta"><span class="at-app-meta-item">区分：${escapeHtml(row.classification || "—")}</span></div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="contract-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由">${escapeHtml(row.approvalRejectReason || "")}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-contract-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-contract-reject-submit="1" ${saving ? "disabled" : ""}>${saving ? "却下中…" : "却下する"}</button>
        </div>
      </div>
    </div>`;
}

/** 請求書（捺印）の却下を送信 */
async function rejectSealApplication() {
  const draft = uiState.appMgmtSealRejectDraft;
  if (!draft) return;
  const parsed = parseDocSealKey(draft.key);
  if (!parsed) return;
  const reason = (document.getElementById("seal-reject-comment")?.value || "").trim();
  if (!reason) return showToastError("却下理由を入力してください");
  if (uiState.appMgmtSealRejectSaving) return;
  uiState.appMgmtSealRejectSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/doc-seal-applications", { method: "PUT", body: { key: draft.key, status: "却下", reviewComment: reason } });
    uiState.appMgmtSealRejectSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "却下に失敗しました");
      render();
      return;
    }
    uiState.appMgmtSealRejectDraft = null;
    setDocSealStatus(parsed.pid, parsed.tab, "未申請");
    if (!uiState.docSealRejectInfo) uiState.docSealRejectInfo = {};
    uiState.docSealRejectInfo[draft.key] = { reason, reviewedAt: new Date().toISOString() };
    delete uiState.appMgmtSelected?.[`seal:${draft.key}`];
    showToast("却下しました");
    render();
  } catch {
    uiState.appMgmtSealRejectSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

/** 請求書（捺印）の却下モーダル（申請承認ページ） */
function sealRejectModalHtml() {
  const draft = uiState.appMgmtSealRejectDraft;
  if (!draft) return "";
  const parsed = parseDocSealKey(draft.key);
  if (!parsed) return "";
  const p = projects.find((x) => String(x.id) === String(parsed.pid));
  const docLabel = docTabLabelForSealApplication(parsed.tab);
  const saving = uiState.appMgmtSealRejectSaving;
  return `
    <div class="cl-mini-modal-overlay open" data-seal-reject-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="seal-reject-h">
        <div class="cl-mini-modal-head">
          <h4 id="seal-reject-h">捺印申請を却下</h4>
          <button class="cl-modal-close" type="button" data-seal-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(p?.name || "—")}</div>
            <div class="at-app-meta"><span class="at-app-meta-item">書類：${escapeHtml(docLabel)}</span></div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="seal-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由"></textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-seal-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-seal-reject-submit="1" ${saving ? "disabled" : ""}>${saving ? "却下中…" : "却下する"}</button>
        </div>
      </div>
    </div>`;
}

/** 自社ツール開発の事前申請の却下モーダル（申請承認ページ） */
function devAppRejectModalHtml() {
  const draft = uiState.devAppRejectDraft;
  if (!draft) return "";
  const app = (uiState.developmentApplications || []).find((a) => String(a.id) === String(draft.id));
  if (!app) return "";
  const saving = uiState.devAppApprovalSaving;
  return `
    <div class="cl-mini-modal-overlay open">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="dev-app-reject-h">
        <div class="cl-mini-modal-head">
          <h4 id="dev-app-reject-h">申請を却下</h4>
          <button class="cl-modal-close" type="button" data-dev-app-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(app.title)}</div>
            ${app.applicantName ? `<div class="at-app-meta"><span class="at-app-meta-item">申請者：${escapeHtml(app.applicantName)}</span></div>` : ""}
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="dev-app-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由">${escapeHtml(app.reviewComment || "")}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-dev-app-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-dev-app-reject-submit="1" ${saving ? "disabled" : ""}>${saving ? "却下中…" : "却下する"}</button>
        </div>
      </div>
    </div>`;
}

/** ツール許可申請の却下モーダル（申請承認ページ） */
function toolRejectModalHtml() {
  const draft = uiState.toolRejectDraft;
  if (!draft) return "";
  const app = (uiState.allowedToolApplications || []).find((a) => String(a.id) === String(draft.id));
  if (!app) return "";
  const saving = uiState.toolApprovalSaving;
  return `
    <div class="cl-mini-modal-overlay open" data-tool-reject-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="tool-reject-h">
        <div class="cl-mini-modal-head">
          <h4 id="tool-reject-h">申請を却下</h4>
          <button class="cl-modal-close" type="button" data-tool-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(app.title)}</div>
            <div class="at-app-meta"><span class="at-app-cat">${escapeHtml(app.category || "—")}${app.subcategory ? ` / ${escapeHtml(app.subcategory)}` : ""}</span>${app.applicantName ? `<span class="at-app-meta-item">申請者：${escapeHtml(app.applicantName)}</span>` : ""}</div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="tool-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由">${escapeHtml(app.reviewComment || "")}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-tool-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-tool-reject-submit="1" ${saving ? "disabled" : ""}>${saving ? "却下中…" : "却下する"}</button>
        </div>
      </div>
    </div>`;
}

// PM_TODO_STATUSES / PM_SEISAKU_STATUSES は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。
// （評価順が制作の工程順=差し戻し検知・必須工程チェックの基準になるため、サーバ側 evaluateAgendaStatusTransition
//  でも同じ既定リストが必要になり、共有ファイルへ移動した。）
/** 制作プロジェクトの既定カテゴリ（テーマ設定でカスタマイズしていない場合の初期値） */
const PM_SEISAKU_CATEGORIES = ["バナー作成", "デザインスポット対応", "LP作成", "その他"];

/** ToDo管理の画面用語。フォルダ1段化で group 層を廃止し、challenge を「プロジェクト」と呼ぶ。
 *  project（旧group名）は group UI 撤去に伴い表に出ない。theme＝challenge＝「プロジェクト」。 */
const PM_WORD = Object.freeze({
  project: "プロジェクト",
  theme: "プロジェクト",
  issue: "課題"
});
// PM_DONE_AUTO_ARCHIVE_DAYS（完了課題の自動アーカイブ日数）は
// app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。
/** ToDo API 自動再取得の上限（超えたら手動リロードまで停止） */
const PM_FETCH_MAX_AUTO_RETRIES = 3;
// PM_AGENDA_PRIORITIES は app-src/00-shared-enums.js を参照。
const PM_AGENDA_CATEGORIES = ["その他", "バグ", "改善", "運用", "機能", "要望", "調査"];
/** プロジェクト（テーマ）の種別が「制作」か（進捗ステップバー・クライアント欄の表示条件を1箇所にまとめる）。
 * 課題個別のカテゴリではなく、所属するプロジェクトの種別で判定する。 */
function isSeisakuProject(ch) {
  return String(ch?.genre || "").trim() === "制作";
}
// PM_TODO_GROUP_CATEGORY_OPTIONS（ToDoグループの分類。空 "" = 未分類）と表示ラベル
// PM_TODO_GROUP_CATEGORY_LABELS は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。
function progressTodoGroupCategoryLabel(v) {
  const s = String(v || "").trim();
  return PM_TODO_GROUP_CATEGORY_LABELS[s] || s;
}
/** 分類値 → CSS クラス用スラッグ（日本語をそのままクラスに使わない） */
const PM_TODO_GROUP_CATEGORY_SLUG = { "開発": "dev", "依頼": "request", "制作": "production", "その他": "other" };
function progressTodoGroupCategorySlug(v) {
  return PM_TODO_GROUP_CATEGORY_SLUG[String(v || "").trim()] || "none";
}
/** ToDoグループの分類プルダウン。dataAttr は data-* 属性（例 "data-pm-modal-todo-group-category"） */
function progressTodoGroupCategorySelectHtml(selected, dataAttr) {
  const cur = String(selected || "").trim();
  const opts = ['<option value="">（未分類）</option>']
    .concat(
      PM_TODO_GROUP_CATEGORY_OPTIONS.map((c) => {
        const v = escapeHtmlAttr(c);
        return `<option value="${v}"${c === cur ? " selected" : ""}>${escapeHtml(progressTodoGroupCategoryLabel(c))}</option>`;
      })
    )
    .join("");
  return `<select class="pj-input" ${dataAttr}>${opts}</select>`;
}
/** ToDoグループの分類チップ（一覧の行・フィルタ用）。空 "" は描画しない */
function progressTodoGroupCategoryChipHtml(category) {
  const c = String(category || "").trim();
  if (!c) return "";
  const slug = progressTodoGroupCategorySlug(c);
  return `<span class="pm-group-genre-chip pm-group-genre-chip--${slug}">${escapeHtml(progressTodoGroupCategoryLabel(c))}</span>`;
}
/** ToDoグループ一覧の分類フィルタチップ列。selCat="" = すべて、"__none__" = 未分類 */
function progressGroupCategoryFilterBarHtml(selCat) {
  const cur = String(selCat || "").trim();
  const chip = (value, label, slug) => {
    const active = cur === value ? " is-active" : "";
    const slugCls = slug ? ` pm-group-genre-filter--${slug}` : "";
    return `<button type="button" class="pm-group-genre-filter${slugCls}${active}" data-pm-group-category-filter="${escapeHtmlAttr(value)}" aria-pressed="${cur === value ? "true" : "false"}">${escapeHtml(label)}</button>`;
  };
  const chips = [chip("", "すべて", "")]
    .concat(PM_TODO_GROUP_CATEGORY_OPTIONS.map((c) => chip(c, progressTodoGroupCategoryLabel(c), progressTodoGroupCategorySlug(c))))
    .concat([chip("__none__", "未分類", "none")])
    .join("");
  // ★お気に入りは種別と別軸のトグル（旧お気に入りタブの置き換え。タブ切替でなく同一一覧の絞り込みに）
  const starOnly = !!uiState.progressProjListStarOnly;
  const starChip = `<button type="button" class="pm-group-genre-filter pm-proj-star-filter${starOnly ? " is-active" : ""}" data-pm-proj-star-filter="1" aria-pressed="${starOnly ? "true" : "false"}" title="お気に入り（★）のプロジェクトだけ表示">★ お気に入り</button>`;
  return `<div class="pm-group-genre-filter-bar" role="group" aria-label="種別で絞り込み">
    <span class="pm-group-genre-filter-label">種別</span>
    ${chips}
    <span class="pm-group-genre-filter-sep" aria-hidden="true"></span>
    ${starChip}
  </div>`;
}

function normalizeProgressTodoStatus(s) {
  const v = String(s || "").trim();
  if (!v) return "未対応";
  // 既定リストに含まれる → そのまま。カスタム状況（非空）もそのまま保持し「未対応」で上書きしない
  return v;
}

function findProgressChallenge(challengeId) {
  return (uiState.progressChallenges || []).find((c) => String(c.id) === String(challengeId)) || null;
}

/** テーマごとの状態列（未設定時はアプリ既定。制作プロジェクトは制作用の既定を使う）。
 * テーマ設定で保存済みのリストはそのまま尊重する（工程の削除・追加・並べ替えが反映される）。 */
function progressChallengeStatuses(ch) {
  const list = Array.isArray(ch?.statuses)
    ? ch.statuses.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  const base = list.length
    ? [...list]
    : isSeisakuProject(ch)
      ? [...PM_SEISAKU_STATUSES]
      : [...PM_TODO_STATUSES];
  if (!base.includes("完了")) base.push("完了");
  return base;
}

/** テーマごとのカテゴリ（未設定時はアプリ既定） */
function sortProgressChallengeCategoriesOtherLast(list) {
  const arr = Array.isArray(list) ? list.map((s) => String(s || "").trim()).filter(Boolean) : [];
  const head = [];
  const tail = [];
  for (const x of arr) {
    if (x === "その他") tail.push(x);
    else head.push(x);
  }
  return head.concat(tail);
}

function progressChallengeCategories(ch) {
  const list = Array.isArray(ch?.categories)
    ? ch.categories.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  const defaults = isSeisakuProject(ch) ? PM_SEISAKU_CATEGORIES : PM_AGENDA_CATEGORIES;
  const raw = list.length ? list : [...defaults];
  return sortProgressChallengeCategoriesOtherLast(raw);
}

function normalizeProgressTodoStatusForChallenge(s, ch) {
  const allowed = progressChallengeStatuses(ch);
  const v = String(s || "").trim();
  if (!v) return allowed[0] || "未対応";
  // カスタム状況リスト / 既定状態リストに含まれる → そのまま
  if (allowed.includes(v) || PM_TODO_STATUSES.includes(v)) return v;
  // どちらにも含まれない場合も非空の値はそのまま保持する
  // （ch.statuses が未ロードや空のときにカスタム状況を「未対応」で上書きしないため）
  return v;
}

function normalizeProgressAgendaPriority(p) {
  const v = String(p || "").trim();
  return PM_AGENDA_PRIORITIES.includes(v) ? v : "中";
}

function normalizeProgressAgendaCategory(raw) {
  const t = String(raw || "").trim().slice(0, 200);
  return t || "その他";
}

/** 課題カテゴリの選択肢（プリセット＋既存データに出てくる値） */
function collectProgressAgendaCategoryOptions(challengeId) {
  const ch = challengeId != null ? findProgressChallenge(challengeId) : null;
  const set = new Set(ch ? progressChallengeCategories(ch) : PM_AGENDA_CATEGORIES);
  const pool = ch ? [ch] : uiState.progressChallenges || [];
  for (const c of pool) {
    for (const a of c.agendas || []) {
      const cat = String(a.category || "").trim();
      if (cat) set.add(cat);
    }
  }
  return Array.from(set);
}

function progressAgendaCategorySelectHtml(selected, challengeId) {
  const sel = normalizeProgressAgendaCategory(selected);
  const opts = collectProgressAgendaCategoryOptions(challengeId);
  if (sel && !opts.includes(sel)) opts.unshift(sel);
  return opts
    .map((c) => `<option value="${escapeHtmlAttr(c)}"${sel === c ? " selected" : ""}>${escapeHtml(c)}</option>`)
    .join("");
}

/** ブラウザのローカル日付（開始日など） */
function isoLocalDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 課題の担当者として API に渡すユーザーID（fp_users.id／数値文字列） */
function progressAgendaAssignedUserIdForApi(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/^u(\d+)$/i);
  if (m) return m[1];
  const u = users.find((x) => String(x.id) === s);
  if (u?.dbId != null && Number.isFinite(Number(u.dbId))) return String(Number(u.dbId));
  return null;
}

/** 課題のクライアント（制作カテゴリ）として API に渡すクライアントID（fp_clients.id／数値文字列） */
function progressAgendaClientIdForApi(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/^c(\d+)$/i);
  if (m) return m[1];
  const c = clients.find((x) => String(x.id) === s);
  if (c?.dbId != null && Number.isFinite(Number(c.dbId))) return String(Number(c.dbId));
  return null;
}

/** サーバーの clientId（数値）から画面のクライアント選択値（"c123"）を求める。
 * ローカルの clients キャッシュに未解決でも（担当者IDの u${dbId} と同様）"c${dbId}" として保持し、
 * 保存時に無関係な編集で client_id が黙って null クリアされるのを防ぐ。 */
function progressAgendaClientFrontIdFromApi(dbId) {
  if (dbId == null) return "";
  const n = Number(dbId);
  if (!Number.isFinite(n) || n < 1) return "";
  const c = clients.find((x) => x.dbId != null && Number(x.dbId) === n);
  return c ? c.id : `c${n}`;
}

function progressIssueAttrFormEl(agendaId) {
  const id = String(agendaId ?? "");
  if (!id) return null;
  try {
    return document.querySelector(`[data-pm-issue-attr-form="${CSS.escape(id)}"]`);
  } catch {
    return document.querySelector(`[data-pm-issue-attr-form="${id}"]`);
  }
}

function progressIssueHeadStatusEl(agendaId) {
  const id = String(agendaId ?? "");
  if (!id) return null;
  try {
    return document.querySelector(`[data-pm-issue-head-status="${CSS.escape(id)}"]`);
  } catch {
    return document.querySelector(`[data-pm-issue-head-status="${id}"]`);
  }
}

function syncProgressIssueHeadStatus(agendaId, value) {
  const headStatus = progressIssueHeadStatusEl(agendaId);
  if (headStatus instanceof HTMLSelectElement) headStatus.value = value;
}

function buildProgressIssueAttrPayloadFromForm(agendaId) {
  const form = progressIssueAttrFormEl(agendaId);
  if (!form) return { error: "フォームが見つかりません" };
  const pick = (field) => form.querySelector(`[data-pm-issue-field="${field}"]`);
  const statusEl = pick("status");
  const categoryEl = pick("category");
  const assigneeEl = pick("assignee");
  const priorityEl = pick("priority");
  const startEl = pick("startDate");
  const dueEl = pick("dueDate");
  const assigneeVal = assigneeEl instanceof HTMLSelectElement ? String(assigneeEl.value || "").trim() : "";
  const apiAssignee = assigneeVal ? progressAgendaAssignedUserIdForApi(assigneeVal) : null;
  if (assigneeVal && !apiAssignee) return { error: "担当者の指定が不正です" };
  // status はチャレンジのカスタム状況を考慮して正規化（normalizeProgressTodoStatus だけだとカスタム状況が「未対応」になる）
  const found = findProgressAgendaById(agendaId);
  const normalizeStatus = (v) => found?.challenge
    ? normalizeProgressTodoStatusForChallenge(v, found.challenge)
    : normalizeProgressTodoStatus(v);
  return {
    payload: {
      action: "updateAgendaDetail",
      agendaId: Number(agendaId),
      status:
        statusEl instanceof HTMLSelectElement ? normalizeStatus(statusEl.value) : undefined,
      category:
        categoryEl instanceof HTMLSelectElement ? normalizeProgressAgendaCategory(categoryEl.value) : undefined,
      priority:
        priorityEl instanceof HTMLSelectElement ? normalizeProgressAgendaPriority(priorityEl.value) : undefined,
      startDate: startEl instanceof HTMLInputElement ? String(startEl.value || "").trim() : undefined,
      dueDate: dueEl instanceof HTMLInputElement ? String(dueEl.value || "").trim() : undefined,
      assignedUserId: apiAssignee
    }
  };
}

function setProgressIssueAttrSaveUi(agendaId, { dirty, saving } = {}) {
  const id = String(agendaId ?? "");
  if (!id) return;
  let btn;
  try {
    btn = document.querySelector(`[data-pm-save-issue-attrs="${CSS.escape(id)}"]`);
  } catch {
    btn = document.querySelector(`[data-pm-save-issue-attrs="${id}"]`);
  }
  if (!(btn instanceof HTMLButtonElement)) return;
  const isSaving = saving === true || String(uiState.progressIssueAttrSaving) === id;
  if (typeof dirty === "boolean") btn.disabled = isSaving || !dirty;
  btn.textContent = isSaving ? "保存中…" : "保存";
}

/** 課題の担当者がログイン中ユーザーか */
function isProgressAgendaAssignedToCurrentUser(ag) {
  const mine =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  if (mine == null) return false;
  const assignee = ag?.assignedUserId;
  if (assignee == null || !Number.isFinite(Number(assignee))) return false;
  return Number(assignee) === mine;
}

/** 課題ボード用: アクティブ課題にウォッチ／担当フィルタを適用 */
function filterProgressBoardAgendas(agendas) {
  let list = activeProgressAgendas(agendas);
  if (uiState.progressBoardWatchOnly) {
    list = list.filter((a) => Boolean(a.watchedByMe));
  }
  if (uiState.progressBoardAssigneeOnly) {
    list = list.filter((a) => isProgressAgendaAssignedToCurrentUser(a));
  }
  const q = normalizeProgressSearchText(uiState.progressIssueSearch);
  if (q) list = list.filter((a) => progressAgendaMatchesSearch(a, q));
  return list;
}

/** 作成・コピーした直後の課題が表示フィルタで隠れる場合、そのフィルタを解除する。
 *  新しい課題はウォッチ未登録で作られるため、「ウォッチ中のみ表示」ON のまま課題を足すと
 *  作った本人の画面に出てこず、登録に失敗したように見えるのを防ぐ。
 *  @returns {boolean} 解除したフィルタがあれば true */
function clearProgressBoardFiltersHidingAgenda(ag) {
  let cleared = false;
  if (uiState.progressBoardWatchOnly && !ag?.watchedByMe) {
    uiState.progressBoardWatchOnly = false;
    cleared = true;
  }
  if (uiState.progressBoardAssigneeOnly && !isProgressAgendaAssignedToCurrentUser(ag)) {
    uiState.progressBoardAssigneeOnly = false;
    cleared = true;
  }
  return cleared;
}

/** 課題検索用テキスト正規化（全角空白→半角・連続空白圧縮・小文字化） */
function normalizeProgressSearchText(v) {
  return String(v || "").replace(/　/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

/** 検索語が課題番号の指定（"475" / "#475"）ならその数値を返す。違えば null。
 *  # は任意。番号だけが入力されたときにしか効かないので、言葉での検索は巻き込まない。 */
function progressSearchIssueNumber(qNorm) {
  const m = /^#?(\d+)$/.exec(String(qNorm || "").trim());
  return m ? Number(m[1]) : null;
}

/** 課題が検索語（正規化済み）に課題名または本文で部分一致するか。
 *  "475" / "#475" のように番号だけを入力したときは課題番号の完全一致も拾う
 *  （番号を表示しているのに番号で探せなかったため）。部分一致にすると 47 で 475 も 147 も
 *  出てしまうので、番号は完全一致に限る。従来の本文一致は残すため既存の検索結果は変わらない。 */
function progressAgendaMatchesSearch(ag, qNorm) {
  if (!qNorm) return true;
  const wantNo = progressSearchIssueNumber(qNorm);
  if (wantNo !== null && Number(ag?.id) === wantNo) return true;
  const hay = normalizeProgressSearchText(`${ag?.title || ""} ${ag?.body || ""}`);
  return hay.includes(qNorm);
}

/** 課題ボード/自分担当一覧 共通: 課題検索ボックス（課題名＋内容） */
function progressIssueSearchInputHtml() {
  const v = String(uiState.progressIssueSearch || "");
  const hasV = v.trim() !== "";
  const ph = `${PM_WORD.issue}名・内容・#番号で検索`;
  return `<div class="pm-issue-search${hasV ? " is-filled" : ""}">
    <span class="pm-issue-search-ic" aria-hidden="true">${pmBoardIconSvg("search", 16)}</span>
    <input type="search" class="pj-input pm-issue-search-input" data-pm-issue-search data-ime-search placeholder="${escapeHtmlAttr(ph)}" value="${escapeHtmlAttr(v)}" maxlength="200" autocomplete="off" aria-label="${escapeHtmlAttr(ph)}" />
    ${hasV ? `<button type="button" class="pm-issue-search-clear" data-pm-issue-search-clear aria-label="検索をクリア" title="クリア">×</button>` : ""}
  </div>`;
}

/** 課題ボード: ウォッチ／担当フィルタ（アイコンのみトグル） */
function progressBoardFilterTogglesHtml() {
  const watchOn = Boolean(uiState.progressBoardWatchOnly);
  const assigneeOn = Boolean(uiState.progressBoardAssigneeOnly);
  const watchTip = watchOn ? "ウォッチ中のみ表示（ON）— クリックで解除" : "ウォッチ中のみ表示";
  const assigneeTip = assigneeOn ? "担当が自分の課題のみ（ON）— クリックで解除" : "担当が自分の課題のみ表示";
  return `<div class="pm-board-filters" role="group" aria-label="課題の表示フィルタ">
    <button type="button" class="pm-board-icon-btn pm-board-icon-btn--filter pm-board-icon-btn--watch${watchOn ? " is-active" : ""}" data-pm-board-watch-only aria-pressed="${watchOn ? "true" : "false"}" title="${escapeHtmlAttr(watchTip)}" aria-label="${escapeHtmlAttr(watchTip)}"><span class="pm-board-icon-btn__glyph" aria-hidden="true">${pmBoardIconSvg("watch")}</span></button>
    <button type="button" class="pm-board-icon-btn pm-board-icon-btn--filter pm-board-icon-btn--assignee${assigneeOn ? " is-active" : ""}" data-pm-board-assignee-only aria-pressed="${assigneeOn ? "true" : "false"}" title="${escapeHtmlAttr(assigneeTip)}" aria-label="${escapeHtmlAttr(assigneeTip)}"><span class="pm-board-icon-btn__glyph" aria-hidden="true">${pmBoardIconSvg("assignee")}</span></button>
  </div>`;
}

/** 課題の assigned_user_id に対応する users 行（アイコン解決用） */
function progressAgendaAssigneeUser(ag) {
  const list = Array.isArray(users) ? users : [];
  const db = ag?.assignedUserId;
  if (db != null && Number.isFinite(Number(db))) {
    const n = Number(db);
    const byDb = list.find((u) => u.dbId != null && Number(u.dbId) === n);
    if (byDb) return byDb;
    const byApi = list.find((u) => progressAgendaAssignedUserIdForApi(u.id) === String(n));
    if (byApi) return byApi;
  }
  const nm = String(ag?.assignedUserName || "").trim();
  if (nm) {
    const byName = list.find((u) => String(u.name || "").trim() === nm);
    if (byName) return byName;
  }
  return null;
}

/** やりとりメッセージが現在ユーザー自身の投稿か（システム履歴は除外） */
function progressMessageIsAuthor(m) {
  if (!m || m.isSystem) return false;
  const msgUid = m.userId;
  if (msgUid == null || !Number.isFinite(Number(msgUid))) return false;
  const n = Number(msgUid);
  if (currentUser?.dbId != null && Number(currentUser.dbId) === n) return true;
  if (authUser?.id != null && Number(authUser.id) === n) return true;
  const authorU = progressMessageAuthorUser(m);
  if (authorU && currentUser?.id && String(authorU.id) === String(currentUser.id)) return true;
  return false;
}

/** やりとりメッセージの userId（fp_users.id）に対応する users 行（アイコン・表示名用。退職者も users から解決） */
function progressMessageAuthorUser(m) {
  const list = Array.isArray(users) ? users : [];
  const db = m?.userId;
  if (db != null && Number.isFinite(Number(db))) {
    const n = Number(db);
    const byDb = list.find((u) => u && u.dbId != null && Number(u.dbId) === n);
    if (byDb) return byDb;
    const byApi = list.find((u) => u && progressAgendaAssignedUserIdForApi(u.id) === String(n));
    if (byApi) return byApi;
  }
  const nm = String(m?.userName || "").trim();
  if (nm && nm !== "（不明）") {
    const byName = list.find((u) => u && String(u.name || "").trim() === nm);
    if (byName) return byName;
  }
  return null;
}

/** やりとり1件のアイコン（API の userAvatar を優先） */
function progressMessageAvatarHtml(m, authorU) {
  const apiSrc = String(m?.userAvatar ?? "").trim();
  const nameOnly = String(m?.userName || "—").trim();
  if (apiSrc) {
    // 写真ありの人もドットを出す（progressUserAvatarHtml を通らないため、ここで足さないと写真の人だけ出ない）
    const dot = typeof fpPresenceDotHtml === "function" ? fpPresenceDotHtml(authorU) : "";
    return `<span class="pm-assignee-avatar pm-assignee-avatar--msg"><img src="${escapeHtmlAttr(apiSrc)}" alt="${escapeHtmlAttr(nameOnly)}" class="pm-assignee-avatar-img" />${dot}</span>`;
  }
  return progressUserAvatarHtml(authorU, "pm-assignee-avatar--msg", nameOnly);
}

/**
 * メンション・お知らせした相手の既読アイコン（自分が書いたコメントのみ・API の mentionReads）。
 * 既読＝相手がそのToDoを開いた（サーバー側 fp_progress_agenda_read で判定済み）。
 */
function progressMentionReadAvatarsHtml(m) {
  const list = Array.isArray(m?.mentionReads) ? m.mentionReads : [];
  if (!list.length) return "";
  const MAX_SHOWN = 3;
  const shown = list.slice(0, MAX_SHOWN);
  const rest = list.length - shown.length;
  const chips = shown
    .map((r) => {
      const name = String(r?.name || "").trim() || "（不明）";
      const isRead = Boolean(r?.readAt);
      const title = isRead ? `${name}: 既読 ${formatProgressMsgTime(r.readAt)}` : `${name}: 未読`;
      const src = String(r?.avatar || "").trim();
      const inner = src
        ? `<img src="${escapeHtmlAttr(src)}" alt="" class="pm-assignee-avatar-img" />`
        : escapeHtml(name.slice(0, 1));
      const fallbackCls = src ? "" : " pm-assignee-avatar--fallback";
      const check = isRead ? `<span class="pm-msg-read-check" aria-hidden="true">✓</span>` : "";
      return `<span class="pm-msg-read-chip${isRead ? " pm-msg-read-chip--read" : ""}" title="${escapeHtmlAttr(title)}"><span class="pm-assignee-avatar${fallbackCls}" aria-hidden="true">${inner}</span>${check}</span>`;
    })
    .join("");
  const more = rest > 0 ? `<span class="pm-msg-read-more">+${rest}</span>` : "";
  const readCount = list.filter((r) => r?.readAt).length;
  const label = `メンション・お知らせした${list.length}人中${readCount}人が既読`;
  return `<div class="pm-msg-reads" aria-label="${escapeHtmlAttr(label)}">${chips}${more}</div>`;
}

/** 担当者アイコン用の画像 URL（users[].avatar または同一ユーザーの currentUser.avatar） */
function resolveProgressUserAvatarSrc(u) {
  if (!u) return "";
  const fromU = String(u.avatar || "").trim();
  if (fromU) return fromU;
  if (currentUser?.id != null && String(u.id) === String(currentUser.id)) return String(currentUser.avatar || "").trim();
  if (currentUser?.dbId != null && u.dbId != null && Number(u.dbId) === Number(currentUser.dbId)) {
    return String(currentUser.avatar || "").trim();
  }
  return "";
}

/**
 * @param {object|null} u users 行（無い場合は nameOnly でイニシャルのみ）
 * @param {string} [nameOnly] API の assignedUserName（users に未登録のときの表示名）
 */
function progressUserAvatarHtml(u, extraClass = "", nameOnly = "") {
  const displayName = String((u && u.name) || nameOnly || "?").trim() || "?";
  const src = u ? resolveProgressUserAvatarSrc(u) : "";
  const initial = escapeHtml(displayName.slice(0, 1));
  const cls = `pm-assignee-avatar${extraClass ? ` ${extraClass}` : ""}`;
  // 在席ドット。ここ1箇所に足せば課題カード・カンバン・トークビュー・稼働状況の全アバターに出る。
  // 中身の更新は再描画ではなく app-src/15o-presence.js が data-presence-user を直接塗り替える。
  const dot = typeof fpPresenceDotHtml === "function" ? fpPresenceDotHtml(u) : "";
  if (src) {
    return `<span class="${cls}" aria-hidden="true"><img src="${escapeHtmlAttr(src)}" alt="" class="pm-assignee-avatar-img" />${dot}</span>`;
  }
  return `<span class="${cls} pm-assignee-avatar--fallback" aria-hidden="true">${initial}${dot}</span>`;
}

/** 課題カード・詳細ヘッダ用の期限表示（YYYY/MM/DD） */
function formatProgressAgendaDueDisplay(iso) {
  if (!iso) return "";
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso).trim();
  return `${m[1]}/${m[2]}/${m[3]}`;
}

function parseProgressAgendaDueYmd(iso) {
  const m = String(iso || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
}

function progressAgendaDueYmdNumber(ymd) {
  if (!ymd) return null;
  return ymd.y * 10000 + ymd.mo * 100 + ymd.d;
}

function isProgressAgendaDueOverdue(ag, ch) {
  const ymd = parseProgressAgendaDueYmd(ag?.dueDate);
  if (!ymd) return false;
  const st = ch
    ? normalizeProgressTodoStatusForChallenge(ag?.status, ch)
    : normalizeProgressTodoStatus(ag?.status);
  if (st === "完了" || st === "対応不要") return false;
  const now = new Date();
  const todayNum = progressAgendaDueYmdNumber({
    y: now.getFullYear(),
    mo: now.getMonth() + 1,
    d: now.getDate()
  });
  const dueNum = progressAgendaDueYmdNumber(ymd);
  return dueNum != null && todayNum != null && dueNum < todayNum;
}

/** 課題ボードカード右下: 期限日のみ（未設定は空欄・過ぎていれば赤） */
function progressKanbanDueDateHtml(ag) {
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);
  if (!dueDisp) return "";
  const overdue = isProgressAgendaDueOverdue(ag);
  const cls = overdue ? "pm-kan-card-bl-date pm-kan-card-bl-date--overdue" : "pm-kan-card-bl-date";
  return `<span class="${cls}"><span class="pm-kan-card-bl-date-lbl">期限日</span> ${escapeHtml(dueDisp)}</span>`;
}

/** 完了列カード: 完了日のみ */
function progressKanbanCompletedDateHtml(ag) {
  const disp = formatProgressAgendaDueDisplay(ag.completedAt);
  const val = disp ? escapeHtml(disp) : "—";
  return `<span class="pm-kan-card-bl-date"><span class="pm-kan-card-bl-date-lbl">完了日</span> ${val}</span>`;
}

function progressKanbanIsCompactColumn(columnStatus) {
  return columnStatus === "完了" || columnStatus === "対応不要";
}

/** 完了・対応不要列: ヘッダ左（ウォッチ左横）の完了日 */
function progressKanbanCompactHeadDateHtml(ag) {
  return progressKanbanCompletedDateHtml(ag).replace(
    'class="pm-kan-card-bl-date"',
    'class="pm-kan-card-bl-date pm-kan-card-head-date"'
  );
}

function progressKanbanCardActionsHtml(a) {
  const aid = escapeHtmlAttr(String(a?.id ?? ""));
  // アーカイブは起票者のみ（「完了」への変更と同じ作成者ゲート）
  const myDbId = progressCurrentUserDbId();
  const isCreator = a?.createdByUserId != null && myDbId != null && Number(a.createdByUserId) === myDbId;
  const archiveBtn = isCreator
    ? `<button type="button" class="pm-kan-card-dd-archive" draggable="false" data-pm-archive-agenda="${aid}">アーカイブ</button>`
    : "";
  return `<details class="pm-kan-card-dd" data-pm-kan-dd="1">
    <summary class="pm-kan-card-dd-sum" draggable="false" aria-label="その他の操作">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="2" cy="8" r="1.5" fill="currentColor"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/><circle cx="14" cy="8" r="1.5" fill="currentColor"/></svg>
    </summary>
    <div class="pm-kan-card-dd-panel">
      <button type="button" class="pm-kan-card-dd-status" draggable="false" data-pm-sp-open-attr="status" data-pm-sp-agenda="${aid}">状況を変更</button>
      <button type="button" class="pm-kan-card-dd-edit" draggable="false" data-pm-edit-agenda="${aid}">編集</button>
      <button type="button" class="pm-kan-card-dd-copy" draggable="false" data-pm-copy-agenda-titled="${aid}" title="課題キー（${PM_WORD.theme}名＋番号）と件名をコピーします。対応アプリでは課題キーにリンクが付きます。Shiftキーを押しながらクリックするとURLのみコピーします。">リンクをコピー</button>
      <button type="button" class="pm-kan-card-dd-duplicate" draggable="false" data-pm-duplicate-agenda="${aid}">課題を複製</button>
      ${archiveBtn}
    </div>
  </details>`;
}

/** 課題番号の表示（"#123"）。カード・詳細ヘッダーなど複数箇所で揃える */
function progressIssueNumberHtml(id) {
  return `#${escapeHtml(String(id ?? ""))}`;
}

/** 課題ボードのカンバンカード（完了・対応不要列はコンパクト表示） */
function progressKanbanCardHtml(a, columnStatus) {
  const aid = escapeHtmlAttr(String(a.id));
  const cat = normalizeProgressAgendaCategory(a.category);
  const catClass = progressAgendaCategoryBadgeClass(cat);
  const dd = progressKanbanCardActionsHtml(a);
  // 優先度バッジ（課題ボードのカードでも一目で優先度が分かるように・IMPROVE-381）
  const priVal = normalizeProgressAgendaPriority(a.priority);
  const priMod = priVal === "最優先" ? "urgent" : priVal === "高" ? "high" : priVal === "低" ? "low" : "mid";
  const priBadge = `<span class="pm-kan-card-pri pm-kan-card-pri--${priMod}" title="優先度: ${escapeHtmlAttr(priVal)}">${escapeHtml(priVal)}</span>`;
  // 登録日（開始日）は課題ボードのカードには表示しない（ユーザー指示 2026-07-23）。詳細画面・SP属性で表示する。
  if (progressKanbanIsCompactColumn(columnStatus)) {
    return `<div class="pm-kan-card pm-kan-card--backlog pm-kan-card--compact" draggable="true" data-pm-drag-agenda="${aid}">
              <div class="pm-kan-card-bl-inner">
                <div class="pm-kan-card-bl-head">
                  ${priBadge}
                  ${progressKanbanCompactHeadDateHtml(a)}
                  <span class="pm-kan-card-bl-head-actions">${progressWatchEyeButtonHtml(a, "kanban")}</span>
                </div>
                <button type="button" class="pm-kan-card-bl-open" draggable="false" data-pm-select-agenda="${aid}">
                  <div class="pm-kan-card-compact-line">
                    <span class="${catClass}">${escapeHtml(cat)}</span>
                    <span class="pm-kan-card-bl-title"><span class="pm-kan-card-key">${progressIssueNumberHtml(a.id)}</span>${escapeHtml(a.title || "（無題）")}</span>
                  </div>
                </button>
                ${dd}
              </div>
            </div>`;
  }
  const au = progressAgendaAssigneeUser(a);
  const nameFull = String(
    (a.assignedUserName && String(a.assignedUserName).trim()) || (au && String(au.name || "").trim()) || ""
  ).trim();
  const assigneeLabel = nameFull || "—";
  const assigneeTitle = escapeHtmlAttr(assigneeLabel);
  const requesterName = progressAgendaCreatorDisplayName(a);
  const hasRequester = requesterName && requesterName !== "—";
  const cu = hasRequester ? progressAgendaCreatorUser(a) : null;
  const requesterHtml = hasRequester
    ? `<span class="pm-kan-card-bl-person pm-kan-card-bl-person--requester">${progressUserAvatarHtml(cu, "pm-assignee-avatar--kanban", requesterName)}<span class="pm-kan-card-bl-requester" title="登録者 ${escapeHtmlAttr(requesterName)}">${escapeHtml(progressCardShortName(requesterName))}</span></span><span class="pm-kan-card-bl-flow-arrow" aria-hidden="true">≫</span>`
    : "";
  return `<div class="pm-kan-card pm-kan-card--backlog" draggable="true" data-pm-drag-agenda="${aid}">
              <div class="pm-kan-card-bl-inner">
                <div class="pm-kan-card-bl-head">
                  <span class="${catClass}">${escapeHtml(cat)}</span>
                  ${priBadge}
                  ${progressWatchEyeButtonHtml(a, "kanban")}
                </div>
                <button type="button" class="pm-kan-card-bl-open" draggable="false" data-pm-select-agenda="${aid}">
                  <span class="pm-kan-card-bl-title"><span class="pm-kan-card-key">${progressIssueNumberHtml(a.id)}</span>${escapeHtml(a.title || "（無題）")}</span>
                  <div class="pm-kan-card-bl-foot">
                    <span class="pm-kan-card-bl-flow${hasRequester ? "" : " pm-kan-card-bl-flow--single"}">
                      ${requesterHtml}
                      <span class="pm-kan-card-bl-person pm-kan-card-bl-person--assignee">${progressUserAvatarHtml(au, "pm-assignee-avatar--kanban", nameFull)}<span class="pm-kan-card-bl-assignee" title="担当者 ${assigneeTitle}">${escapeHtml(progressCardShortName(assigneeLabel))}</span></span>
                    </span>
                    ${progressKanbanDueDateHtml(a)}
                  </div>
                </button>
                ${dd}
              </div>
            </div>`;
}

/** 課題詳細: 担当者表示名のみ（アイコンなし） */
function progressIssueAssigneeDisplayName(ag) {
  const au = progressAgendaAssigneeUser(ag);
  return String((ag.assignedUserName && String(ag.assignedUserName).trim()) || (au && String(au.name || "").trim()) || "").trim() || "—";
}

/** 課題詳細: クライアント表示名（制作カテゴリの課題に紐づくクライアント。未設定は"—"） */
function progressIssueClientDisplayName(ag) {
  if (ag?.clientId == null) return "—";
  const n = Number(ag.clientId);
  if (!Number.isFinite(n) || n < 1) return "—";
  const c = clients.find((x) => x.dbId != null && Number(x.dbId) === n);
  return c && String(c.name || "").trim() ? String(c.name).trim() : "—";
}

/** 課題の登録者表示名（作成時のユーザー名・変更不可） */
function progressAgendaCreatorDisplayName(ag) {
  return String(ag?.createdByName || "").trim() || "—";
}

/** カード表示用に姓（名字）だけを取り出す。「姓 名」形式ならスペース前のみ、無ければそのまま */
function progressCardShortName(name) {
  const s = String(name || "").trim();
  if (!s || s === "—") return s;
  const parts = s.split(/[\s　]+/).filter(Boolean);
  return parts.length >= 2 ? parts[0] : s;
}

/** 課題の created_by_user_id に対応する users 行（アイコン解決用・退職者も解決） */
function progressAgendaCreatorUser(ag) {
  const list = Array.isArray(users) ? users : [];
  const db = ag?.createdByUserId;
  if (db != null && Number.isFinite(Number(db))) {
    const n = Number(db);
    const byDb = list.find((u) => u && u.dbId != null && Number(u.dbId) === n);
    if (byDb) return byDb;
    const byApi = list.find((u) => u && progressAgendaAssignedUserIdForApi(u.id) === String(n));
    if (byApi) return byApi;
  }
  const nm = String(ag?.createdByName || "").trim();
  if (nm) {
    const byName = list.find((u) => u && String(u.name || "").trim() === nm);
    if (byName) return byName;
  }
  return null;
}

/**
 * 新規課題の担当者の初期値。優先順位は「最近使った担当者 → 登録者（自分） → 未設定」。
 * あくまで初期値で、モーダル上で自由に変更できる（保存時の「担当者は必須」チェックは従来どおり）。
 */
function defaultProgressAgendaAssigneeId() {
  const pool = progressChallengeAssigneePool(progressRouteChallenge());
  const recent = progressRecentAssigneeUsers(pool, 1)[0];
  if (recent) return String(recent.id);
  const me = String(currentUser?.id || "").trim();
  return me && pool.some((u) => String(u.id) === me) ? me : "";
}

function defaultProgressAgendaCreateDraft() {
  return {
    title: "",
    body: "",
    category: "その他",
    status: "未対応",
    assignedUserId: defaultProgressAgendaAssigneeId(),
    assignedUserSearch: "",
    priority: "中",
    startDate: isoLocalDate(),
    dueDate: "",
    clientId: "",
    // 課題の追加をお知らせするユーザー（@名前 …）。新規作成時のみ送る
    notifyRaw: ""
  };
}

function mergedProgressAgendaCreateDraftForModal() {
  const base = defaultProgressAgendaCreateDraft();
  const raw = uiState.progressAgendaCreateDraft || {};
  // 開始日の「今日」既定は新規作成のときだけ。編集で開いた課題の「開始日なし」をここで
  // 今日に埋めると、状況だけ変えて保存しても開始日が一緒に書き換わる（保存経路は同じ
  // このドラフトを読む）ため、編集中は入力値をそのまま使う
  const startDate = isProgressAgendaEditModal()
    ? String(raw.startDate ?? "")
    : (raw.startDate && String(raw.startDate).trim()) || base.startDate;
  return {
    ...base,
    ...raw,
    startDate,
    assignedUserSearch: raw.assignedUserSearch != null ? String(raw.assignedUserSearch) : base.assignedUserSearch
  };
}

function progressAgendaEditAgendaId() {
  return String(uiState.progressAgendaEditAgendaId || "").trim();
}

function isProgressAgendaEditModal() {
  return Boolean(uiState.progressShowIssueAddForm && progressAgendaEditAgendaId());
}

function progressAgendaDraftFromAgenda(ag) {
  let assignedUserId = "";
  if (ag?.assignedUserId != null && Number.isFinite(Number(ag.assignedUserId))) {
    const dbId = Number(ag.assignedUserId);
    const u = users.find((x) => x.dbId != null && Number(x.dbId) === dbId);
    assignedUserId = u ? String(u.id) : `u${dbId}`;
  }
  return {
    title: String(ag?.title || ""),
    body: String(ag?.body || ""),
    category: normalizeProgressAgendaCategory(ag?.category),
    status: normalizeProgressTodoStatus(ag?.status),
    assignedUserId,
    assignedUserSearch: "",
    priority: normalizeProgressAgendaPriority(ag?.priority),
    startDate: ag?.startDate ? String(ag.startDate).slice(0, 10) : "",
    dueDate: ag?.dueDate ? String(ag.dueDate).slice(0, 10) : "",
    clientId: progressAgendaClientFrontIdFromApi(ag?.clientId)
  };
}

function closeProgressAgendaFormModal() {
  uiState.progressShowIssueAddForm = false;
  uiState.progressAgendaEditAgendaId = "";
  uiState.progressAgendaCreateDraft = defaultProgressAgendaCreateDraft();
  uiState.progressAgendaModalPendingFiles = [];
  uiState.progressAgendaModalSubmitting = false;
}

function closeProgressChallengeFormModal() {
  uiState.progressShowProjectForm = false;
  uiState.progressChallengeCreateDraft = { title: "", body: "" };
  uiState.progressChallengeModalSubmitting = false;
}

function closeProgressTodoGroupFormModal() {
  uiState.progressShowTodoGroupForm = false;
  uiState.progressTodoGroupCreateDraft = { title: "", body: "", category: "" };
  uiState.progressTodoGroupModalSubmitting = false;
}

function closeProgressTodoGroupEditModal() {
  uiState.progressShowTodoGroupEditForm = false;
  uiState.progressTodoGroupEditDraft = { groupId: "", title: "", body: "", category: "", challengeIds: [], themeFilter: "" };
  uiState.progressTodoGroupModalSubmitting = false;
}

function progressChallengesForGroupEditPicker() {
  return (uiState.progressChallenges || []).filter((c) => !c.archivedAt);
}

/** グループ編集モーダル: 絞り込み後のプロジェクト id 一覧 */
function progressTodoGroupEditFilteredChallengeIds(draft) {
  const kw = String(draft?.themeFilter || "")
    .trim()
    .toLowerCase();
  let list = progressChallengesForGroupEditPicker();
  if (kw) {
    list = list.filter((c) => `${c.title || ""} ${c.body || ""}`.toLowerCase().includes(kw));
  }
  return list.map((c) => String(c.id));
}

function progressTodoGroupEditThemePickerHtml(_groupId, draft) {
  const selected = new Set(
    (Array.isArray(draft?.challengeIds) ? draft.challengeIds : []).map((x) => String(x))
  );
  const kw = String(draft?.themeFilter || "")
    .trim()
    .toLowerCase();
  let list = progressChallengesForGroupEditPicker();
  const totalCount = list.length;
  if (kw) {
    list = list.filter((c) => `${c.title || ""} ${c.body || ""}`.toLowerCase().includes(kw));
  }
  list = [...list].sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "ja"));
  if (!list.length) {
    const msg = totalCount
      ? `キーワードに一致する${PM_WORD.theme}がありません。`
      : `登録済みの${PM_WORD.theme}がありません。先に${PM_WORD.theme}を作成してください。`;
    return `<div class="pm-todo-group-theme-picker"><p class="pm-todo-group-theme-empty">${escapeHtml(msg)}</p></div>`;
  }
  const rows = list
    .map((c) => {
      const cid = String(c.id);
      const checked = selected.has(cid);
      return `<button type="button" class="pm-theme-chip${checked ? " is-on" : ""}" data-pm-todo-group-edit-challenge="${escapeHtmlAttr(cid)}" aria-pressed="${checked ? "true" : "false"}">
        <span class="pm-theme-chip-label">${escapeHtml(c.title || "（無題）")}</span>
      </button>`;
    })
    .join("");
  const selN = selected.size;
  return `
    <div class="pm-todo-group-theme-picker">
      <div class="pm-todo-group-theme-picker-head">
        <span class="pm-todo-group-theme-picker-title">紐づける${PM_WORD.theme}</span>
        <span class="pm-todo-group-theme-badge" aria-live="polite"><strong>${selN}</strong><span class="pm-todo-group-theme-badge-sep">/</span>${list.length}</span>
      </div>
      <div class="pm-todo-group-theme-search-row">
        <input type="search" class="pj-input pm-todo-group-theme-filter" data-pm-todo-group-edit-theme-filter data-ime-search placeholder="${PM_WORD.theme}名で絞り込み" value="${escapeHtmlAttr(String(draft?.themeFilter || ""))}" maxlength="200" />
        <span class="pm-todo-group-theme-quick">
          <button type="button" class="pm-link-btn" data-pm-todo-group-edit-select-all="1">すべて</button>
          <span class="pm-todo-group-theme-quick-sep" aria-hidden="true">·</span>
          <button type="button" class="pm-link-btn" data-pm-todo-group-edit-clear-all="1">解除</button>
        </span>
      </div>
      <div class="pm-todo-group-theme-list-scroll" data-pm-todo-group-edit-theme-scroll>
        <div class="pm-todo-group-theme-list" role="group" aria-label="紐づける${PM_WORD.theme}">${rows}</div>
      </div>
    </div>
  `;
}

function buildProgressTodoGroupFromCreatePayload(groupId, fields) {
  const now = new Date().toISOString();
  const creatorDbId =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  const groups = uiState.progressTodoGroups || [];
  const maxSort = groups.reduce((m, g) => Math.max(m, Number(g.sortOrder) || 0), -1);
  return {
    id: Number(groupId),
    title: fields.title,
    body: fields.body,
    category: fields.category || "",
    sortOrder: maxSort + 1,
    createdAt: now,
    updatedAt: now,
    createdByUserId: creatorDbId,
    createdByName: String(currentUser?.name || authUser?.name || "").trim() || "（不明）",
    archivedAt: null,
    starredByMe: false,
    themeCount: 0,
    agendaCount: 0,
    openAgendaCount: 0
  };
}

function applyProgressTodoGroupCreatedLocal(group) {
  if (!group) return;
  if (!Array.isArray(uiState.progressTodoGroups)) uiState.progressTodoGroups = [];
  if (uiState.progressTodoGroups.some((g) => String(g.id) === String(group.id))) return;
  uiState.progressTodoGroups.push(group);
}

function buildProgressChallengeFromCreatePayload(challengeId, fields, todoGroupId) {
  const now = new Date().toISOString();
  const creatorDbId =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  const chs = uiState.progressChallenges || [];
  const maxSort = chs.reduce((m, c) => Math.max(m, Number(c.sortOrder) || 0), -1);
  return {
    id: Number(challengeId),
    todoGroupId: todoGroupId != null ? Number(todoGroupId) : null,
    title: fields.title,
    body: fields.body,
    genre: fields.genre || "",
    sortOrder: maxSort + 1,
    createdAt: now,
    updatedAt: now,
    createdByUserId: creatorDbId,
    createdByName: String(currentUser?.name || authUser?.name || "").trim() || "（不明）",
    archivedAt: null,
    starredByMe: false,
    wikiBody: "",
    members: [],
    memberUserIds: Array.isArray(fields.memberUserIds) ? fields.memberUserIds : null,
    statuses: [],
    categories: [],
    agendas: []
  };
}

function applyProgressChallengeWikiUpdatedLocal(challengeId, wikiBody) {
  const ch = findProgressChallenge(challengeId);
  if (!ch) return;
  ch.wikiBody = String(wikiBody || "");
  ch.updatedAt = new Date().toISOString();
}

function applyProgressChallengeSettingsUpdatedLocal(challengeId, patch) {
  const ch = findProgressChallenge(challengeId);
  const hasProjectId = Object.prototype.hasOwnProperty.call(patch, "projectId");
  const hasTracked = Object.prototype.hasOwnProperty.call(patch, "timeTrackedStatuses");
  const trackedVal = hasTracked && Array.isArray(patch.timeTrackedStatuses) ? [...patch.timeTrackedStatuses] : null;
  const hasRequired = Object.prototype.hasOwnProperty.call(patch, "requiredSteps");
  const requiredVal = hasRequired && Array.isArray(patch.requiredSteps) ? [...patch.requiredSteps] : null;
  const hasWorkKind = Object.prototype.hasOwnProperty.call(patch, "workKind");
  const workKindVal = hasWorkKind && patch.workKind === "internal" ? "internal" : "";
  if (ch) {
    if (patch.statuses != null) ch.statuses = [...patch.statuses];
    if (patch.categories != null) ch.categories = [...patch.categories];
    if (hasProjectId) ch.projectId = patch.projectId != null ? Number(patch.projectId) : null;
    if (hasTracked) ch.timeTrackedStatuses = trackedVal;
    if (hasRequired) ch.requiredSteps = requiredVal;
    if (hasWorkKind) ch.workKind = workKindVal;
    if (patch.memberUserIds !== undefined) ch.memberUserIds = patch.memberUserIds;
    ch.updatedAt = new Date().toISOString();
  }
  // ライブツリーだけでなく全リストキャッシュにも反映（render時の巻き戻り防止）
  _syncChallengeSettingsInAllCaches(challengeId, {
    statuses: patch.statuses != null ? patch.statuses : undefined,
    categories: patch.categories != null ? patch.categories : undefined,
    projectId: hasProjectId ? (patch.projectId != null ? Number(patch.projectId) : null) : undefined,
    timeTrackedStatuses: hasTracked ? trackedVal : undefined,
    hasTimeTrackedStatuses: hasTracked,
    requiredSteps: hasRequired ? requiredVal : undefined,
    hasRequiredSteps: hasRequired,
    workKind: hasWorkKind ? workKindVal : undefined
  });
}

function applyProgressChallengeCreatedLocal(challenge) {
  if (!challenge) return;
  if (!Array.isArray(uiState.progressChallenges)) uiState.progressChallenges = [];
  if (uiState.progressChallenges.some((c) => String(c.id) === String(challenge.id))) return;
  uiState.progressChallenges.push(challenge);
}

function applyProgressChallengeUpdatedLocal(challengeId, fields) {
  const ch = (uiState.progressChallenges || []).find((c) => Number(c.id) === Number(challengeId));
  if (!ch) return;
  ch.title = fields.title;
  ch.body = fields.body;
  if (fields.genre !== undefined) ch.genre = fields.genre || "";
  if (fields.memberUserIds !== undefined) ch.memberUserIds = fields.memberUserIds;
  ch.updatedAt = new Date().toISOString();
}

function setProgressChallengeModalSubmitBusy(busy, { edit = false } = {}) {
  const btn = document.querySelector(
    edit ? "[data-pm-submit-challenge-edit]" : "[data-pm-submit-challenge-modal]"
  );
  if (btn instanceof HTMLButtonElement) {
    btn.disabled = busy;
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    btn.textContent = busy ? (edit ? "保存中…" : "登録中…") : edit ? "保存する" : "登録する";
  }
  const closeSel = edit ? "[data-close-pm-challenge-edit]" : "[data-close-pm-challenge-create]";
  document.querySelectorAll(closeSel).forEach((el) => {
    if (el instanceof HTMLButtonElement) el.disabled = busy;
  });
  const backdrop = document.querySelector(
    `.composer-backdrop${edit ? "[data-close-pm-challenge-edit]" : "[data-close-pm-challenge-create]"}`
  );
  if (backdrop instanceof HTMLElement) {
    backdrop.style.pointerEvents = busy ? "none" : "";
    backdrop.style.opacity = busy ? "0.6" : "";
  }
}

function progressAgendaAssigneeNameForApiId(apiUserId) {
  if (apiUserId == null || String(apiUserId).trim() === "") return "";
  const n = Number(apiUserId);
  if (!Number.isFinite(n) || n < 1) return "";
  const u = users.find((x) => x.dbId != null && Number(x.dbId) === n);
  return u ? String(u.name || "").trim() : "";
}

function buildProgressAgendaFromCreatePayload(challengeId, agendaId, fields) {
  const now = new Date().toISOString();
  const creatorDbId =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  const assigneeApiId =
    fields.assignedUserId != null && String(fields.assignedUserId).trim() !== ""
      ? Number(fields.assignedUserId)
      : null;
  rememberProgressRecentAssignee(assigneeApiId);
  const ch = (uiState.progressChallenges || []).find((c) => String(c.id) === String(challengeId));
  const maxSort = (Array.isArray(ch?.agendas) ? ch.agendas : []).reduce(
    (m, a) => Math.max(m, Number(a.sortOrder) || 0),
    -1
  );
  return {
    id: Number(agendaId),
    challengeId: Number(challengeId),
    title: fields.title,
    body: fields.body,
    category: fields.category,
    status: fields.status,
    sortOrder: maxSort + 1,
    createdAt: now,
    updatedAt: now,
    createdByUserId: creatorDbId,
    createdByName: String(currentUser?.name || authUser?.name || "").trim() || "（不明）",
    assignedUserId: assigneeApiId,
    assignedUserName: progressAgendaAssigneeNameForApiId(assigneeApiId),
    priority: fields.priority,
    startDate: fields.startDate,
    dueDate: fields.dueDate,
    completedAt: fields.status === "完了" ? now.slice(0, 10) : null,
    clientId: fields.clientId != null ? Number(fields.clientId) : null,
    archivedAt: null,
    messages: [],
    attachments: [],
    threadDeferred: true,
    watchedByMe: false,
    watchHasUnread: false,
    lastReadAt: null
  };
}

function applyProgressAgendaCreatedLocal(challengeId, agenda) {
  const chs = uiState.progressChallenges || [];
  const ch = chs.find((c) => String(c.id) === String(challengeId));
  if (!ch || !agenda) return;
  if (!Array.isArray(ch.agendas)) ch.agendas = [];
  if (ch.agendas.some((a) => String(a.id) === String(agenda.id))) return;
  ch.agendas.push(agenda);
}

/** stepAssignees の値から fp_users.id を取り出す。値は2形式:
 * 素の数値＝工程を抜けた時の自動固定記録、{ id, manual: true }＝工数担当プルダウンでの手動設定。
 * 数値・文字列以外のプリミティブ（true 等）を Number() で 1 に化けさせない。 */
function progressStepAssigneeUidOf(value) {
  const raw = value && typeof value === "object" ? value.id : value;
  const n = typeof raw === "number" || typeof raw === "string" ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= 1 ? Math.round(n) : null;
}

/** stepAssignees の値がユーザーの明示的な手動設定か（再入場時の自動削除から保護される） */
function progressStepAssigneeIsManual(value) {
  return Boolean(value && typeof value === "object" && value.manual);
}

/** 状況変更に伴う工程担当（stepAssignees）の更新。サーバ freezeStepAssigneeOnStatusChange と同じ規則の即時反映用。
 * 抜けた工程: 未記録なら「変更前の担当」を固定記録（手動設定・既存記録は上書きしない）。
 * 入った工程: 自動固定記録を消す（差し戻しで戻ると古い記録が現在の担当より優先表示されるため。
 * 次に抜ける時に再記録）。手動設定はユーザーの明示入力なので消さない。 */
function applyProgressStepAssigneeFreezeLocal(ag, fromStatus, toStatus, prevAssigneeUid) {
  const from = String(fromStatus || "").trim();
  const to = String(toStatus || "").trim();
  if (from === to) return;
  const map = ag.stepAssignees && typeof ag.stepAssignees === "object" ? { ...ag.stepAssignees } : {};
  let changed = false;
  const aid = Number(prevAssigneeUid);
  if (from && map[from] == null && Number.isFinite(aid) && aid >= 1) {
    map[from] = Math.round(aid);
    changed = true;
  }
  if (to && map[to] != null && !progressStepAssigneeIsManual(map[to])) {
    delete map[to];
    changed = true;
  }
  if (changed) ag.stepAssignees = Object.keys(map).length ? map : null;
}

function applyProgressAgendaUpdatedLocal(agendaId, fields) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda) return;
  const ag = hit.agenda;
  applyProgressStepAssigneeFreezeLocal(ag, ag.status, fields.status, ag.assignedUserId);
  ag.title = fields.title;
  ag.body = fields.body;
  ag.category = fields.category;
  ag.status = fields.status;
  ag.priority = fields.priority;
  ag.startDate = fields.startDate;
  ag.dueDate = fields.dueDate;
  const assigneeApiId =
    fields.assignedUserId != null && String(fields.assignedUserId).trim() !== ""
      ? Number(fields.assignedUserId)
      : null;
  ag.assignedUserId = assigneeApiId;
  ag.assignedUserName = progressAgendaAssigneeNameForApiId(assigneeApiId);
  rememberProgressRecentAssignee(assigneeApiId);
  if (fields.clientId !== undefined) {
    ag.clientId = fields.clientId != null ? Number(fields.clientId) : null;
  }
  if (fields.status === "完了" && !ag.completedAt) {
    ag.completedAt = localDateToYmd(new Date());
  } else if (fields.status !== "完了") {
    ag.completedAt = null;
  }
  ag.updatedAt = new Date().toISOString();
  // 全リストキャッシュにも反映（render時の巻き戻り防止）
  _syncAgendaInAllCaches(agendaId, (a) => {
    a.status = ag.status;
    a.category = ag.category;
    a.priority = ag.priority;
    a.startDate = ag.startDate;
    a.dueDate = ag.dueDate;
    a.assignedUserId = ag.assignedUserId;
    a.assignedUserName = ag.assignedUserName;
    a.completedAt = ag.completedAt;
    a.updatedAt = ag.updatedAt;
    a.stepAssignees = ag.stepAssignees;
    if (fields.clientId !== undefined) a.clientId = ag.clientId;
  });
}

/** カンバン D&D 用: ステータスのみ即時反映。戻すときは返却値を revertProgressAgendaStatusLocal に渡す */
function applyProgressAgendaStatusLocal(agendaId, status) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda) return null;
  const ag = hit.agenda;
  const snapshot = {
    status: ag.status,
    completedAt: ag.completedAt,
    updatedAt: ag.updatedAt,
    stepAssignees: ag.stepAssignees && typeof ag.stepAssignees === "object" ? { ...ag.stepAssignees } : ag.stepAssignees ?? null
  };
  const norm = hit.challenge
    ? normalizeProgressTodoStatusForChallenge(status, hit.challenge)
    : normalizeProgressTodoStatus(status);
  applyProgressStepAssigneeFreezeLocal(ag, ag.status, norm, ag.assignedUserId);
  ag.status = norm;
  if (norm === "完了" && !ag.completedAt) {
    ag.completedAt = localDateToYmd(new Date());
  } else if (norm !== "完了") {
    ag.completedAt = null;
  }
  ag.updatedAt = new Date().toISOString();
  const myId = progressCurrentUserDbId();
  if (myId != null) {
    ag.lastActivityUserId = myId;
    if (ag.watchedByMe) ag.watchHasUnread = false;
  }
  // 全リストキャッシュにも状態変更を反映（render時の巻き戻り防止）
  _syncAgendaInAllCaches(agendaId, (a) => {
    a.status = ag.status;
    a.completedAt = ag.completedAt;
    a.updatedAt = ag.updatedAt;
    a.stepAssignees = ag.stepAssignees;
  });
  return snapshot;
}

function revertProgressAgendaStatusLocal(agendaId, snapshot) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda || !snapshot) return;
  hit.agenda.status = snapshot.status;
  hit.agenda.completedAt = snapshot.completedAt;
  hit.agenda.updatedAt = snapshot.updatedAt;
  hit.agenda.stepAssignees = snapshot.stepAssignees;
  _syncAgendaInAllCaches(agendaId, (a) => {
    a.status = snapshot.status;
    a.completedAt = snapshot.completedAt;
    a.updatedAt = snapshot.updatedAt;
    a.stepAssignees = snapshot.stepAssignees;
  });
}

function progressAgendaStatusChangeLogLine(actorName, fromStatus, toStatus) {
  const who = String(actorName || "").trim() || "ユーザー";
  // normalizeProgressTodoStatus だとカスタム状況が「未対応」になるためそのまま使う
  const from = String(fromStatus || "").trim() || "（不明）";
  const to = String(toStatus || "").trim() || "（不明）";
  return `${who} が 状況 を「${from}」から「${to}」に変更しました。`;
}

/**
 * 課題の状況変更をサーバーに保存し、保存後はサーバーから真実（status＋履歴）を
 * 取り直して画面を確定させる共通処理。
 * 楽観更新は呼び出し側で行い、ここでは API → サーバー再取得 → DOM 確定を担う。
 * カスタム状況・他人の課題・履歴表示すべてを一本化して扱う。
 * @returns {Promise<{ok:boolean, savedStatus?:string, error?:string}>}
 */
async function commitProgressAgendaStatusChange(agendaId, toStatus, { rejectionReason } = {}) {
  const numId = Number(agendaId);
  if (!authToken || !Number.isFinite(numId) || numId < 1) return { ok: false };
  let savedStatus = String(toStatus || "");
  try {
    const res = await fetch("/api/progress-management", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        action: "updateAgendaStatus",
        agendaId: numId,
        status: toStatus,
        ...(rejectionReason ? { rejectionReason } : {})
      })
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: typeof j.error === "string" ? j.error : "状況の変更に失敗しました" };
    }
    if (typeof j.savedStatus === "string" && j.savedStatus) savedStatus = j.savedStatus;
  } catch {
    return { ok: false, error: "状況の変更に失敗しました" };
  }
  // サーバーから真実を取り直して画面を確定（status・カスタム状況列・履歴をすべて整合）
  const route = currentRoute();
  const pid = String(route.searchParams?.get("project") || "").trim();
  const gid = String(route.searchParams?.get("group") || "").trim();
  try {
    const refOk = await refreshProgressManagementFromServer({
      scope: "summary",
      includeChallengeId: pid || undefined,
      includeGroupId: gid || undefined,
      hydrateAgendaId: numId
    });
    if (refOk) {
      const latest = currentRoute();
      if (latest.page === "progress-management") {
        if (progressManagementIssueDetailRouteActive(latest)) {
          refreshProgressIssueSummaryDom(numId);
          refreshProgressIssueThreadDom(numId);
        } else {
          render();
        }
      }
    } else {
      // 再取得失敗でも保存は成功しているため軽くフィードバック
      showToastDirect("変更は保存されましたが画面の更新に失敗しました。再読み込みしてください。");
    }
  } catch {
    showToastDirect("変更は保存されましたが画面の更新に失敗しました。再読み込みしてください。");
  }
  return { ok: true, savedStatus };
}

function appendProgressAgendaUserMessageLocal(agendaId, body, messageId) {
  const hit = findProgressAgendaById(agendaId);
  const ag = hit?.agenda;
  if (!ag) return null;
  if (!Array.isArray(ag.messages)) ag.messages = [];
  const uid =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  const mid =
    messageId != null && Number.isFinite(Number(messageId)) && Number(messageId) >= 1
      ? Number(messageId)
      : `local-msg-${Date.now()}`;
  const msg = {
    id: mid,
    body: String(body || ""),
    userId: uid,
    userName: String(currentUser?.name || authUser?.name || "").trim() || "ユーザー",
    userAvatar: String(currentUser?.avatar || "").trim(),
    createdAt: new Date().toISOString(),
    isSystem: false,
    attachments: []
  };
  ag.messages.push(msg);
  if (Object.prototype.hasOwnProperty.call(ag, "threadDeferred")) delete ag.threadDeferred;
  return msg;
}

function replaceProgressLocalMessageId(agendaId, localId, serverId) {
  if (localId == null || serverId == null) return false;
  const hit = findProgressAgendaById(agendaId);
  const msgs = Array.isArray(hit?.agenda?.messages) ? hit.agenda.messages : [];
  const msg = msgs.find((m) => String(m.id) === String(localId));
  const sid = Number(serverId);
  if (!msg || !Number.isFinite(sid) || sid < 1) return false;
  msg.id = sid;
  msg.sending = false;
  return true;
}

function removeProgressLocalMessage(agendaId, localId) {
  if (localId == null) return false;
  const hit = findProgressAgendaById(agendaId);
  const ag = hit?.agenda;
  const msgs = Array.isArray(ag?.messages) ? ag.messages : [];
  const idx = msgs.findIndex((m) => String(m.id) === String(localId));
  if (idx < 0) return false;
  msgs.splice(idx, 1);
  return true;
}

function appendProgressAgendaSystemMessageLocal(agendaId, body) {
  const hit = findProgressAgendaById(agendaId);
  const ag = hit?.agenda;
  if (!ag) return;
  // messages 未ロード（他人の課題で threadDeferred 等）でも履歴が消えないよう配列を初期化
  if (!Array.isArray(ag.messages)) ag.messages = [];
  const uid =
    currentUser?.dbId != null && Number.isFinite(Number(currentUser.dbId))
      ? Number(currentUser.dbId)
      : authUser?.id != null && Number.isFinite(Number(authUser.id))
        ? Number(authUser.id)
        : null;
  ag.messages.push({
    id: `local-sys-${Date.now()}`,
    body: String(body || ""),
    userId: uid,
    userName: String(currentUser?.name || authUser?.name || "").trim() || "ユーザー",
    createdAt: new Date().toISOString(),
    isSystem: true,
    attachments: []
  });
}

function applyProgressMessageUpdatedLocal(messageId, body) {
  const mid = Number(messageId);
  if (!Number.isFinite(mid) || mid < 1) return null;
  for (const ch of uiState.progressChallenges || []) {
    for (const ag of Array.isArray(ch.agendas) ? ch.agendas : []) {
      const msgs = Array.isArray(ag.messages) ? ag.messages : [];
      const msg = msgs.find((m) => Number(m.id) === mid);
      if (!msg) continue;
      msg.body = String(body || "");
      msg.updatedAt = new Date().toISOString();
      if (Object.prototype.hasOwnProperty.call(ag, "threadDeferred")) delete ag.threadDeferred;
      ag.updatedAt = new Date().toISOString();
      return Number(ag.id);
    }
  }
  return null;
}

function captureProgressAgendaDetailSnapshot(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda) return null;
  const ag = hit.agenda;
  const ch = hit.challenge;
  const agStatus = ch
    ? normalizeProgressTodoStatusForChallenge(ag.status, ch)
    : normalizeProgressTodoStatus(ag.status);
  return {
    status: agStatus,
    priority: normalizeProgressAgendaPriority(ag.priority),
    category: normalizeProgressAgendaCategory(ag.category),
    startDate: ag.startDate ? String(ag.startDate).slice(0, 10) : null,
    dueDate: ag.dueDate ? String(ag.dueDate).slice(0, 10) : null,
    assignedUserId:
      ag.assignedUserId != null && Number.isFinite(Number(ag.assignedUserId)) ? Number(ag.assignedUserId) : null,
    assigneeName: progressIssueAssigneeDisplayName(ag)
  };
}

function applyProgressAgendaDetailFromPayload(agendaId, payload) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda || !payload) return;
  const ag = hit.agenda;
  const ch = hit.challenge;
  if (payload.status !== undefined && payload.status !== null) {
    const norm = ch
      ? normalizeProgressTodoStatusForChallenge(payload.status, ch)
      : normalizeProgressTodoStatus(payload.status);
    ag.status = norm;
    if (norm === "完了" && !ag.completedAt) {
      ag.completedAt = localDateToYmd(new Date());
    } else if (norm !== "完了") {
      ag.completedAt = null;
    }
  }
  if (payload.priority !== undefined && payload.priority !== null) {
    ag.priority = normalizeProgressAgendaPriority(payload.priority);
  }
  if (payload.category !== undefined && payload.category !== null) {
    ag.category = normalizeProgressAgendaCategory(payload.category);
  }
  if (payload.startDate !== undefined) {
    ag.startDate = payload.startDate ? String(payload.startDate).slice(0, 10) : null;
  }
  if (payload.dueDate !== undefined) {
    ag.dueDate = payload.dueDate ? String(payload.dueDate).slice(0, 10) : null;
  }
  if (Object.prototype.hasOwnProperty.call(payload, "assignedUserId")) {
    const assigneeApiId =
      payload.assignedUserId != null && String(payload.assignedUserId).trim() !== ""
        ? Number(payload.assignedUserId)
        : null;
    ag.assignedUserId = Number.isFinite(assigneeApiId) ? assigneeApiId : null;
    ag.assignedUserName = progressAgendaAssigneeNameForApiId(ag.assignedUserId);
    rememberProgressRecentAssignee(ag.assignedUserId);
  }
  ag.updatedAt = new Date().toISOString();
}

function progressAgendaDetailChangeLogLines(actorName, before, payload) {
  if (!before || !payload) return [];
  const who = String(actorName || "").trim() || "ユーザー";
  const fmtDate = (iso) => {
    if (!iso) return "（なし）";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}/${m[2]}/${m[3]}` : String(iso);
  };
  const lines = [];
  if (payload.status !== undefined && payload.status !== null) {
    const next = normalizeProgressTodoStatus(payload.status);
    if (next !== before.status) {
      lines.push(`${who} が 状況 を「${before.status}」から「${next}」に変更しました。`);
    }
  }
  if (payload.priority !== undefined && payload.priority !== null) {
    const next = normalizeProgressAgendaPriority(payload.priority);
    if (next !== before.priority) {
      lines.push(`${who} が 優先度 を「${before.priority}」から「${next}」に変更しました。`);
    }
  }
  if (payload.category !== undefined && payload.category !== null) {
    const next = normalizeProgressAgendaCategory(payload.category);
    if (next !== before.category) {
      lines.push(`${who} が カテゴリ を「${before.category}」から「${next}」に変更しました。`);
    }
  }
  if (payload.startDate !== undefined) {
    const next = payload.startDate ? String(payload.startDate).slice(0, 10) : null;
    if (next !== before.startDate) {
      lines.push(
        `${who} が 開始日 を「${fmtDate(before.startDate)}」から「${fmtDate(next)}」に変更しました。`
      );
    }
  }
  if (payload.dueDate !== undefined) {
    const next = payload.dueDate ? String(payload.dueDate).slice(0, 10) : null;
    if (next !== before.dueDate) {
      lines.push(`${who} が 期限日 を「${fmtDate(before.dueDate)}」から「${fmtDate(next)}」に変更しました。`);
    }
  }
  if (Object.prototype.hasOwnProperty.call(payload, "assignedUserId")) {
    const aid =
      payload.assignedUserId != null && String(payload.assignedUserId).trim() !== ""
        ? Number(payload.assignedUserId)
        : null;
    const newLabel = Number.isFinite(aid) ? progressAgendaAssigneeNameForApiId(aid) || "（なし）" : "（なし）";
    const oldLabel = before.assigneeName || "（なし）";
    if (Number(before.assignedUserId || 0) !== Number(aid || 0) || oldLabel !== newLabel) {
      lines.push(`${who} が 担当者 を「${oldLabel}」から「${newLabel}」に変更しました。`);
    }
  }
  return lines;
}

function patchProgressKanbanMoveCard(agendaId, status) {
  const id = String(agendaId ?? "");
  if (!id || !status) return false;
  let card;
  try {
    card = document.querySelector(`[data-pm-drag-agenda="${CSS.escape(id)}"]`);
  } catch {
    card = document.querySelector(`[data-pm-drag-agenda="${id}"]`);
  }
  if (!(card instanceof HTMLElement)) return false;
  let col = null;
  document.querySelectorAll("[data-pm-kan-drop-status]").forEach((el) => {
    if (el.getAttribute("data-pm-kan-drop-status") === status) col = el;
  });
  if (!(col instanceof HTMLElement)) return false;
  col.appendChild(card);
  return true;
}

function syncProgressIssueCompletedNoteDom(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  const form = progressIssueAttrFormEl(agendaId);
  const quick = form?.querySelector("[data-pm-issue-attr-quick]");
  if (!hit?.agenda || !quick) return;
  const existing = quick.querySelector(".pm-issue-completed-note");
  if (hit.agenda.completedAt) {
    const text = `対応終了日: ${String(hit.agenda.completedAt)}`;
    if (existing instanceof HTMLElement) {
      existing.textContent = text;
    } else {
      const p = document.createElement("p");
      p.className = "pm-issue-completed-note";
      p.setAttribute("role", "note");
      p.textContent = text;
      quick.appendChild(p);
    }
  } else if (existing) {
    existing.remove();
  }
}

function resetProgressIssueAttrFormFromAgenda(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  const form = progressIssueAttrFormEl(agendaId);
  if (!hit?.agenda || !form) return false;
  const ag = hit.agenda;
  const ch = hit.challenge;
  const agStatus = ch
    ? normalizeProgressTodoStatusForChallenge(ag.status, ch)
    : normalizeProgressTodoStatus(ag.status);
  const statusEl = form.querySelector('[data-pm-issue-field="status"]');
  const assigneeEl = form.querySelector('[data-pm-issue-field="assignee"]');
  const dueEl = form.querySelector('[data-pm-issue-field="dueDate"]');
  if (statusEl instanceof HTMLSelectElement) statusEl.value = agStatus;
  syncProgressIssueHeadStatus(agendaId, agStatus);
  if (assigneeEl instanceof HTMLSelectElement) {
    const dbAssignee =
      ag.assignedUserId != null && Number.isFinite(Number(ag.assignedUserId)) ? Number(ag.assignedUserId) : null;
    const selAssigneeClientId =
      dbAssignee == null
        ? ""
        : (() => {
            const row = users.find((u) => u.dbId != null && Number(u.dbId) === dbAssignee);
            return row ? String(row.id) : "";
          })();
    assigneeEl.value = selAssigneeClientId;
  }
  if (dueEl instanceof HTMLInputElement) {
    dueEl.value = ag.dueDate ? String(ag.dueDate).slice(0, 10) : "";
  }
  syncProgressIssueCompletedNoteDom(agendaId);
  setProgressIssueAttrSaveUi(agendaId, { dirty: false });
  return true;
}

function refreshProgressIssueSummaryDom(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  const summary = document.querySelector(".pm-issue-summary");
  if (hit?.agenda && hit.challenge && summary instanceof HTMLElement) {
    const wrap = document.createElement("div");
    wrap.innerHTML = progressIssueSummaryGridHtml(hit.agenda, hit.challenge);
    const next = wrap.firstElementChild;
    if (next instanceof HTMLElement) summary.replaceWith(next);
  }
  // 工程バーも同時に作り直す（初回描画と同じ progressIssueStepBarHtml で。ここを外すと
  // 状況・担当を変えても ▼現在 の位置と担当アバターがリロードまで古いまま残る）
  if (hit?.agenda && hit.challenge) {
    const stepWrap = document.querySelector(".pm-step-bar-wrap");
    const stepHtml = progressIssueStepBarHtml(hit.agenda, hit.challenge);
    if (stepWrap instanceof HTMLElement) {
      if (stepHtml) {
        const w = document.createElement("div");
        w.innerHTML = stepHtml;
        const nextBar = w.firstElementChild;
        if (nextBar instanceof HTMLElement) stepWrap.replaceWith(nextBar);
      } else {
        // 保留・対応不要などフロー外の状況に変えた場合はバーごと消す
        stepWrap.remove();
      }
    } else if (stepHtml) {
      // フロー外→フロー内に戻った場合。置き場所は初回描画（progressIssueCollapsibleDetailHtml）と同じ末尾
      const memoBody = document.querySelector(".pm-memo-body");
      if (memoBody instanceof HTMLElement) memoBody.insertAdjacentHTML("beforeend", stepHtml);
    }
  }
  // ヘッダー属性バーも同時に更新（初回描画と同じ関数で作り直す。ここに項目を書き足さない）
  const bar = document.querySelector(".pm-issue-head-attr-bar");
  if (!hit?.agenda || !(bar instanceof HTMLElement)) return false;
  bar.innerHTML = progressIssueHeadAttrBarInnerHtml(hit.agenda, hit.challenge);
  return true;
}

/** コメントのリアクションチップ＋追加ボタン＋絵文字パレット（PC/SP共通・課題詳細スレッド用）。
 *  パレットの開閉状態は uiState.progressReactPickerMsgId（スレッド再描画をまたいで維持）。
 *  クリック処理は app-src/15n-message-reactions.js。 */
function progressMessageReactionsHtml(m) {
  if (m.isSystem || m.sending) return "";
  const list = Array.isArray(m.reactions) ? m.reactions : [];
  const myDbId = progressCurrentUserDbId();
  // 絵文字ごとにまとめる（Map は挿入順を保つ＝最初に押された順）
  const byEmoji = new Map();
  for (const r of list) {
    if (!r || !r.emoji) continue;
    if (!byEmoji.has(r.emoji)) byEmoji.set(r.emoji, { emoji: r.emoji, users: [] });
    byEmoji.get(r.emoji).users.push(r);
  }
  const msgIdAttr = escapeHtmlAttr(String(m.id));
  const chips = [...byEmoji.values()]
    .map((g) => {
      const mine = myDbId != null && g.users.some((r) => Number(r.userId) === myDbId);
      const names = g.users.map((r) => String(r.userName || "").trim()).filter(Boolean).join("、");
      return `<button type="button" class="pm-react-chip${mine ? " pm-react-chip--mine" : ""}" data-pm-react-toggle="${msgIdAttr}" data-pm-react-emoji="${escapeHtmlAttr(g.emoji)}" title="${escapeHtmlAttr(names)}" aria-label="${escapeHtmlAttr(`${g.emoji} ${names}`)}"><span class="pm-react-chip-emoji">${escapeHtml(g.emoji)}</span><span class="pm-react-chip-count">${g.users.length}</span></button>`;
    })
    .join("");
  const pickerOpen = String(uiState.progressReactPickerMsgId || "") === String(m.id);
  const palette = pickerOpen
    ? `<div class="pm-react-palette" role="menu" aria-label="リアクションを選択">${PM_REACTION_EMOJIS.map(
        (em) =>
          `<button type="button" class="pm-react-palette-btn" role="menuitem" data-pm-react-toggle="${msgIdAttr}" data-pm-react-emoji="${escapeHtmlAttr(em)}">${escapeHtml(em)}</button>`
      ).join("")}</div>`
    : "";
  const addBtn = `<button type="button" class="pm-react-add${pickerOpen ? " is-open" : ""}" data-pm-react-open="${msgIdAttr}" title="リアクションする" aria-label="リアクションする">🙂<span class="pm-react-add-plus">＋</span></button>`;
  return `<div class="pm-msg-reactions" data-pm-react-wrap="${msgIdAttr}">${chips}${addBtn}${palette}</div>`;
}

/** システム履歴「〜が 添付ファイル「X」を追加しました。」のXを、実際の添付ファイルの
 * プレビューへ開くリンクに変える。起票時に登録した添付（=課題直付き。コメントの添付とは別経路）は
 * 履歴の文中に埋もれるだけで開けなかった（ユーザー指摘 2026-08-26）。一致する添付が
 * 見つからない場合（削除済み等）は null を返しプレーンテキストのまま表示させる。 */
function progressSystemMessageAttachLinkHtml(m) {
  const body = String(m?.body || "");
  const match = body.match(/^(.*?が\s*添付ファイル「)([^」]+)(」を追加しました。?)$/);
  if (!match) return null;
  const [, pre, filename, post] = match;
  const ag = findProgressAgendaById(m.agendaId)?.agenda;
  if (!ag) return null;
  const att = progressAgendaAttachmentsList(ag).find(
    (a) => String(a.filename || "").trim() === filename.trim()
  );
  if (!att) return null;
  const idAttr = escapeHtmlAttr(String(att.id));
  return `${escapeHtml(pre)}<button type="button" class="pm-attach-name--preview" data-pm-attach-preview="${idAttr}" title="${escapeHtmlAttr(filename)}">${escapeHtml(filename)}</button>${escapeHtml(post)}`;
}

function progressIssueMessageRowHtml(m) {
  const sys = Boolean(m.isSystem);
  const mine = progressMessageIsAuthor(m);
  const editing = !sys && mine && String(uiState.progressEditingMessageId) === String(m.id);
  const authorU = progressMessageAuthorUser(m);
  const authorName = authorU ? userNameWithRetiredLabel(authorU) : String(m.userName || "—").trim();
  const avatarEl = progressMessageAvatarHtml(m, authorU);
  const timeStr = escapeHtml(m.sending ? "送信中..." : formatProgressMsgTime(m.createdAt));

  // システム履歴メッセージ: コンパクトな横並びスタイルを維持
  if (sys) {
    const sysTag = `<span class="pm-msg-tag pm-msg-tag--system" title="属性変更の履歴">履歴</span>`;
    const attachLink = progressSystemMessageAttachLinkHtml(m);
    return `<div class="pm-msg pm-msg--system">
      <div class="pm-msg-head">
        <div class="pm-msg-head-main">${avatarEl}<span class="pm-msg-author">${escapeHtml(authorName)}</span>${sysTag}</div>
        <div class="pm-msg-head-aside"><span class="pm-msg-time">${timeStr}</span></div>
      </div>
      <div class="pm-msg-body">${attachLink || progressMessageBodyDisplayHtml(m.body)}</div>
      ${progressMessageFilesBlockHtml(m, { allowUpload: false })}
    </div>`;
  }

  // 吹き出し: アバターは常に左・名前左+時刻右（モックアップ準拠）
  const del = mine && !m.sending
    ? `<button type="button" class="inline-btn ghost pm-msg-del" data-pm-delete-message="${escapeHtmlAttr(String(m.id))}">削除</button>`
    : "";
  const edit = (mine && !editing && !m.sending)
    ? `<button type="button" class="inline-btn ghost pm-msg-edit" data-pm-edit-message="${escapeHtmlAttr(String(m.id))}">編集</button>`
    : "";
  // 既読アイコンは編集・削除と同じ行に置く（自分のコメントには元からある行なので高さが増えない）
  const readsHtml = progressMentionReadAvatarsHtml(m);
  const actionsHtml = (readsHtml || edit || del)
    ? `<div class="pm-msg-bactions">${readsHtml}${edit}${del}</div>`
    : "";

  // 名前(左) + 時刻(右): mine/other 共通レイアウト
  const fromRow = `<div class="pm-msg-bfrom">
    <span class="pm-msg-bname">${escapeHtml(authorName)}</span>
    <span class="pm-msg-time">${timeStr}</span>
  </div>`;

  const bubbleContent = editing
    ? `${fromRow}
       <div data-pm-format-scope>
         ${progressComposeFormatToolbarHtml()}
         <textarea class="pj-input pm-textarea pm-msg-edit-textarea" data-pm-edit-message-body data-pm-mention-textarea="1" rows="5">${escapeHtml(m.body || "")}</textarea>
       </div>
       <div class="pm-msg-edit-actions">
         <button type="button" class="inline-btn ghost" data-pm-cancel-edit-message>キャンセル</button>
         <button type="button" class="inline-btn" data-pm-save-edit-message="${escapeHtmlAttr(String(m.id))}">保存</button>
       </div>
       ${progressMessageFilesBlockHtml(m, { allowUpload: true })}`
    : `${fromRow}
       <div class="pm-msg-body">${progressMessageBodyForDisplayHtml(m)}</div>
       ${progressMessageFilesBlockHtml(m, { allowUpload: false })}`;

  // bgroup = 吹き出し+リアクションの包み。自分のコメント（右寄せ）でもリアクションを
  // 「吹き出しの左端」に揃えるための基準枠（style.css .pm-msg-bgroup）。
  return `<div class="pm-msg pm-msg--bubble${mine ? " pm-msg--mine" : ""}${editing ? " pm-msg--editing" : ""}${m.sending ? " pm-msg--sending" : ""}">
    <div class="pm-msg-bav">${avatarEl}</div>
    <div class="pm-msg-bwrap">
      <div class="pm-msg-bgroup">
        <div class="pm-msg-bbubble">${bubbleContent}</div>
        ${progressMessageReactionsHtml(m)}
      </div>
      ${actionsHtml}
    </div>
  </div>`;
}


function refreshProgressIssueThreadDom(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  const thread = document.querySelector(".pm-thread--issue-detail");
  if (!hit?.agenda || !(thread instanceof HTMLElement)) return false;
  if (Boolean(hit.agenda.threadDeferred) || Boolean(uiState.progressMgmtDetailLoading)) return false;
  const msgs = progressIssueMessagesSortedOldestFirst(hit.agenda.messages);
  thread.innerHTML = msgs.map((m) => progressIssueMessageRowHtml(m)).join("");
  return true;
}

function refreshProgressIssueDetailAfterAttrs(agendaId) {
  if (!refreshProgressIssueSummaryDom(agendaId)) return false;
  if (!resetProgressIssueAttrFormFromAgenda(agendaId)) return false;
  refreshProgressIssueThreadDom(agendaId);
  return true;
}

function setProgressAgendaModalSubmitBusy(busy) {
  const editing = isProgressAgendaEditModal();
  const btn = document.querySelector("[data-pm-submit-agenda-modal]");
  if (btn instanceof HTMLButtonElement) {
    btn.disabled = busy;
    btn.setAttribute("aria-busy", busy ? "true" : "false");
    btn.textContent = busy ? (editing ? "保存中…" : "追加中…") : editing ? "保存する" : "追加する";
  }
  document.querySelectorAll("[data-close-pm-agenda-create]").forEach((el) => {
    if (el instanceof HTMLButtonElement) el.disabled = busy;
  });
  const backdrop = document.querySelector(".composer-backdrop[data-close-pm-agenda-create]");
  if (backdrop instanceof HTMLElement) {
    backdrop.style.pointerEvents = busy ? "none" : "";
    backdrop.style.opacity = busy ? "0.6" : "";
  }
}

function findProgressAgendaById(agendaId) {
  const n = Number(agendaId);
  if (!Number.isFinite(n) || n < 1) return null;
  for (const c of uiState.progressChallenges || []) {
    const agendas = Array.isArray(c.agendas) ? c.agendas : [];
    const ag = agendas.find((a) => Number(a.id) === n);
    if (ag) return { challenge: c, agenda: ag };
  }
  return null;
}

function findProgressAttachmentById(attId) {
  const n = Number(attId);
  if (!Number.isFinite(n) || n < 1) return null;
  for (const c of uiState.progressChallenges || []) {
    for (const ag of Array.isArray(c.agendas) ? c.agendas : []) {
      for (const att of progressAgendaAttachmentsList(ag)) {
        if (Number(att.id) === n) return att;
      }
      for (const m of Array.isArray(ag.messages) ? ag.messages : []) {
        for (const att of Array.isArray(m.attachments) ? m.attachments : []) {
          if (Number(att.id) === n) return att;
        }
      }
    }
  }
  return null;
}

function progressTodoStatusPillClass(status) {
  const s = normalizeProgressTodoStatus(status);
  if (s === "完了") return "pm-todo-pill pm-todo-pill--done";
  if (s === "対応中") return "pm-todo-pill pm-todo-pill--doing";
  if (s === "保留") return "pm-todo-pill pm-todo-pill--hold";
  if (s === "対応不要") return "pm-todo-pill pm-todo-pill--na";
  return "pm-todo-pill pm-todo-pill--open";
}

function stripProgressThreadDeferredFlags() {
  for (const c of uiState.progressChallenges || []) {
    const agendas = Array.isArray(c.agendas) ? c.agendas : [];
    for (const a of agendas) {
      if (a && Object.prototype.hasOwnProperty.call(a, "threadDeferred")) delete a.threadDeferred;
    }
  }
}

function normalizeProgressListMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  if (m === "archived" || m === "starred") return m;
  return "active";
}

function normalizeProgressGroupListMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  if (m === "archived" || m === "my_themes" || m === "starred") return m;
  return "active";
}

/** プロジェクト一覧（#progress-management?projects=1） */
function progressManagementProjectsListRouteActive(route) {
  const r = route || currentRoute();
  if (r.page !== "progress-management") return false;
  if (String(r.searchParams?.get("group") || "").trim()) return false;
  if (String(r.searchParams?.get("project") || "").trim()) return false;
  const v = String(r.searchParams?.get("projects") || "").trim().toLowerCase();
  return v === "1" || v === "all" || v === "true";
}

function progressManagementProjectsListHash() {
  return "progress-management?projects=1";
}

function normalizeProgressCacheEntry(entry) {
  if (Array.isArray(entry)) return { groups: [], challenges: entry };
  if (entry && typeof entry === "object") {
    return {
      groups: Array.isArray(entry.groups) ? entry.groups : [],
      challenges: Array.isArray(entry.challenges) ? entry.challenges : []
    };
  }
  return null;
}

function ensureProgressListFetchDoneState() {
  if (!uiState.progressListFetchDone || typeof uiState.progressListFetchDone !== "object") {
    uiState.progressListFetchDone = { active: false, starred: false, archived: false };
  }
  return uiState.progressListFetchDone;
}

function markProgressListFetched(listMode) {
  const mode = normalizeProgressListMode(listMode);
  ensureProgressListFetchDoneState()[mode] = true;
}

function ensureProgressListCacheState() {
  if (!uiState.progressListCache || typeof uiState.progressListCache !== "object") {
    uiState.progressListCache = { active: null, starred: null, archived: null };
  }
  return uiState.progressListCache;
}

function hasProgressListCache(listMode) {
  const mode = normalizeProgressListMode(listMode);
  const c = ensureProgressListCacheState();
  return normalizeProgressCacheEntry(c[mode]) != null;
}

function getProgressListCache(listMode) {
  const mode = normalizeProgressListMode(listMode);
  const c = ensureProgressListCacheState();
  return normalizeProgressCacheEntry(c[mode]);
}

function setProgressListCache(listMode, payload) {
  const mode = normalizeProgressListMode(listMode);
  const c = ensureProgressListCacheState();
  const entry = normalizeProgressCacheEntry(payload);
  c[mode] = entry || { groups: [], challenges: [] };
}

function applyProgressListCacheToView(listMode) {
  const cached = getProgressListCache(listMode);
  if (!cached) return false;
  applyProgressPayloadToState(cached);
  reconcileProgressNotifyCountsFromTree();
  refreshSidebarUserChrome();
  void refreshProgressViewerCountsFromServer().then(() => refreshSidebarUserChrome());
  return true;
}

function ensureProgressGroupListCacheState() {
  if (!uiState.progressGroupListCache || typeof uiState.progressGroupListCache !== "object") {
    uiState.progressGroupListCache = { active: null, my_themes: null, starred: null, archived: null };
  } else {
    if (!("my_themes" in uiState.progressGroupListCache)) uiState.progressGroupListCache.my_themes = null;
    if (!("starred" in uiState.progressGroupListCache)) uiState.progressGroupListCache.starred = null;
  }
  return uiState.progressGroupListCache;
}

function ensureProgressGroupListFetchDoneState() {
  if (!uiState.progressGroupListFetchDone || typeof uiState.progressGroupListFetchDone !== "object") {
    uiState.progressGroupListFetchDone = { active: false, my_themes: false, starred: false, archived: false };
  } else {
    if (!("my_themes" in uiState.progressGroupListFetchDone)) uiState.progressGroupListFetchDone.my_themes = false;
    if (!("starred" in uiState.progressGroupListFetchDone)) uiState.progressGroupListFetchDone.starred = false;
  }
  return uiState.progressGroupListFetchDone;
}

function hasProgressGroupListCache(listMode) {
  const mode = normalizeProgressGroupListMode(listMode);
  return normalizeProgressCacheEntry(ensureProgressGroupListCacheState()[mode]) != null;
}

function getProgressGroupListCache(listMode) {
  return normalizeProgressCacheEntry(ensureProgressGroupListCacheState()[normalizeProgressGroupListMode(listMode)]);
}

function setProgressGroupListCache(listMode, payload) {
  const entry = normalizeProgressCacheEntry(payload);
  ensureProgressGroupListCacheState()[normalizeProgressGroupListMode(listMode)] = entry || {
    groups: [],
    challenges: []
  };
}

function applyProgressGroupListCacheToView(listMode) {
  const cached = getProgressGroupListCache(listMode);
  if (!cached) return false;
  applyProgressPayloadToState(cached);
  reconcileProgressNotifyCountsFromTree();
  refreshSidebarUserChrome();
  void refreshProgressViewerCountsFromServer().then(() => refreshSidebarUserChrome());
  return true;
}

function hasProgressAssignedListCache() {
  return normalizeProgressCacheEntry(uiState.progressAssignedListCache) != null;
}

/** 「担当中」と「関わった」は別の絞り込み結果だが、キャッシュ枠は1つしかない。
 *  切り替え時はここで必ず捨てて取り直す（片方だけ消すと前のタブの中身が残る）。 */
function invalidateProgressAssignedListCache() {
  uiState.progressAssignedListCache = null;
  uiState.progressAssignedListCacheScope = "";
  uiState.progressAssignedListFetchDone = false;
  uiState.progressAssignedMeNeedRefresh = true;
}

function setProgressAssignedListCache(payload, assignScope) {
  const entry = normalizeProgressCacheEntry(payload);
  uiState.progressAssignedListCache = entry || { groups: [], challenges: [] };
  uiState.progressAssignedListCacheScope = assignScope;
}

function applyProgressAssignedListCacheToView() {
  const cached = normalizeProgressCacheEntry(uiState.progressAssignedListCache);
  if (!cached) return false;
  applyProgressPayloadToState(cached);
  reconcileProgressNotifyCountsFromTree();
  refreshSidebarUserChrome();
  void refreshProgressViewerCountsFromServer().then(() => refreshSidebarUserChrome());
  return true;
}

function markProgressGroupListFetched(listMode) {
  ensureProgressGroupListFetchDoneState()[normalizeProgressGroupListMode(listMode)] = true;
}

function _syncChallengeStarInAllCaches(challengeId, starred, previousStarred = null) {
  const id = Number(challengeId);
  let sourceChallenge = findProgressChallenge(id);
  let sourceGroup = null;
  for (const mode of ["active", "starred", "archived"]) {
    if (hasProgressGroupListCache(mode)) {
      const cache = getProgressGroupListCache(mode);
      if (cache) {
        const c = (cache.challenges || []).find((x) => Number(x.id) === id);
        if (c) {
          c.starredByMe = starred;
          if (!sourceChallenge) sourceChallenge = c;
        }
      }
    }
  }
  for (const mode of ["active", "starred", "archived"]) {
    if (hasProgressListCache(mode)) {
      const cache = getProgressListCache(mode);
      if (cache) {
        const c = (cache.challenges || []).find((x) => Number(x.id) === id);
        if (c) {
          c.starredByMe = starred;
          if (!sourceChallenge) sourceChallenge = c;
        }
      }
    }
  }
  const myThemesCache = getProgressGroupListCache("my_themes");
  if (myThemesCache) {
    const chs = Array.isArray(myThemesCache.challenges) ? myThemesCache.challenges : [];
    myThemesCache.challenges = chs;
    const idx = chs.findIndex((x) => Number(x.id) === id);
    if (!starred) {
      if (idx >= 0) chs.splice(idx, 1);
      return;
    }
    if (idx >= 0) {
      chs[idx].starredByMe = true;
      return;
    }
    if (!sourceChallenge || sourceChallenge.archivedAt) return;
    const next = { ...sourceChallenge, starredByMe: true };
    chs.push(next);
    const gid = next.todoGroupId != null ? String(next.todoGroupId).trim() : "";
    if (!gid) return;
    const groups = Array.isArray(myThemesCache.groups) ? myThemesCache.groups : [];
    myThemesCache.groups = groups;
    if (groups.some((g) => String(g.id) === gid)) return;
    sourceGroup = findProgressTodoGroup(gid);
    if (!sourceGroup) {
      for (const mode of ["active", "starred", "archived"]) {
        const cache = getProgressGroupListCache(mode);
        sourceGroup = (cache?.groups || []).find((g) => String(g.id) === gid) || null;
        if (sourceGroup) break;
      }
    }
    if (sourceGroup) groups.push(sourceGroup);
  }
  const currentCount = Number(uiState.progressMyThemeCount);
  if (
    uiState.progressMyThemeCount !== null && // null はまだ未ロード（0 と区別する）
    Number.isFinite(currentCount) &&
    previousStarred !== null &&
    Boolean(previousStarred) !== Boolean(starred)
  ) {
    uiState.progressMyThemeCount = Math.max(0, Math.floor(currentCount) + (starred ? 1 : -1));
  }
}

/** 全リストキャッシュ（active/starred/archived の group・list、my_themes、assigned）の
 *  該当チャレンジに対して mutator を適用する。
 *  キャッシュは fetch ごとに別オブジェクトで、ライブツリーとは参照が異なるため、
 *  状況/カテゴリ/状態などローカル変更を全キャッシュに反映しないと、
 *  次の render() の applyProgress*CacheToView で古い値に巻き戻る。 */
function _forEachCachedChallenge(challengeId, fn) {
  const id = Number(challengeId);
  if (!Number.isFinite(id)) return;
  const buckets = [];
  for (const mode of ["active", "starred", "archived"]) {
    if (hasProgressGroupListCache(mode)) buckets.push(getProgressGroupListCache(mode));
    if (hasProgressListCache(mode)) buckets.push(getProgressListCache(mode));
  }
  buckets.push(getProgressGroupListCache("my_themes"));
  buckets.push(normalizeProgressCacheEntry(uiState.progressAssignedListCache));
  for (const cache of buckets) {
    if (!cache || !Array.isArray(cache.challenges)) continue;
    const c = cache.challenges.find((x) => Number(x.id) === id);
    if (c) fn(c);
  }
}

/** チャレンジの状況/カテゴリ定義を全キャッシュに同期 */
function _syncChallengeSettingsInAllCaches(challengeId, { statuses, categories, projectId, timeTrackedStatuses, hasTimeTrackedStatuses, requiredSteps, hasRequiredSteps, workKind } = {}) {
  _forEachCachedChallenge(challengeId, (c) => {
    if (Array.isArray(statuses)) c.statuses = [...statuses];
    if (Array.isArray(categories)) c.categories = [...categories];
    if (projectId !== undefined) c.projectId = projectId;
    if (workKind !== undefined) c.workKind = workKind === "internal" ? "internal" : "";
    // null（全計測／必須指定なし）も有効値のため、キー有無フラグで判定する
    if (hasTimeTrackedStatuses) c.timeTrackedStatuses = Array.isArray(timeTrackedStatuses) ? [...timeTrackedStatuses] : null;
    if (hasRequiredSteps) c.requiredSteps = Array.isArray(requiredSteps) ? [...requiredSteps] : null;
  });
}

/** 課題（アジェンダ）を全キャッシュ内で探して mutator を適用 */
function _syncAgendaInAllCaches(agendaId, mutator) {
  const id = Number(agendaId);
  if (!Number.isFinite(id) || typeof mutator !== "function") return;
  const buckets = [];
  for (const mode of ["active", "starred", "archived"]) {
    if (hasProgressGroupListCache(mode)) buckets.push(getProgressGroupListCache(mode));
    if (hasProgressListCache(mode)) buckets.push(getProgressListCache(mode));
  }
  buckets.push(getProgressGroupListCache("my_themes"));
  buckets.push(normalizeProgressCacheEntry(uiState.progressAssignedListCache));
  for (const cache of buckets) {
    if (!cache || !Array.isArray(cache.challenges)) continue;
    for (const ch of cache.challenges) {
      const ag = Array.isArray(ch.agendas) ? ch.agendas.find((a) => Number(a.id) === id) : null;
      if (ag) mutator(ag);
    }
  }
}

/** GET 失敗時: 再取得ループを止め、一覧タブの「未取得」状態を解消する */
function markProgressFetchFailedForRoute(route, { onGroupList, onAssignedMeFlat, onThemeList, listMode, groupListMode }) {
  uiState.progressMgmtSuppressEmptyRefetch = true;
  if (onGroupList) markProgressGroupListFetched(groupListMode);
  if (onAssignedMeFlat) uiState.progressAssignedListFetchDone = true;
  if (onThemeList) markProgressListFetched(listMode);
}

function invalidateProgressGroupListCache(listMode) {
  const c = ensureProgressGroupListCacheState();
  const done = ensureProgressGroupListFetchDoneState();
  if (listMode == null || listMode === "") {
    c.active = null;
    c.my_themes = null;
    c.starred = null;
    c.archived = null;
    done.active = false;
    done.my_themes = false;
    done.starred = false;
    done.archived = false;
    return;
  }
  const mode = normalizeProgressGroupListMode(listMode);
  c[mode] = null;
  done[mode] = false;
}

function invalidateProgressListCache(listMode) {
  const c = ensureProgressListCacheState();
  if (listMode == null || listMode === "") {
    c.active = null;
    c.starred = null;
    c.archived = null;
    invalidateProgressGroupListCache();
    return;
  }
  c[normalizeProgressListMode(listMode)] = null;
}

function invalidateProgressListFetch(listMode) {
  const done = ensureProgressListFetchDoneState();
  if (listMode == null || listMode === "") {
    done.active = false;
    done.starred = false;
    done.archived = false;
    invalidateProgressListCache();
    return;
  }
  const mode = normalizeProgressListMode(listMode);
  done[mode] = false;
  invalidateProgressListCache(mode);
}

async function prefetchProgressGroupListModes(modes) {
  if (!authToken || currentRoute().page !== "progress-management") return;
  const route = currentRoute();
  if (String(route.searchParams?.get("group") || "").trim() || String(route.searchParams?.get("project") || "").trim()) {
    return;
  }
  if (progressManagementAssignedMeRouteActive(route)) return;
  const list = (Array.isArray(modes) ? modes : [])
    .map((m) => normalizeProgressGroupListMode(m))
    .filter((m, i, arr) => arr.indexOf(m) === i);
  for (const mode of list) {
    if (hasProgressGroupListCache(mode) || uiState.progressMgmtFetchInFlight) continue;
    await refreshProgressManagementFromServer({ groupListMode: mode, scope: "summary", prefetch: true });
  }
}

async function refreshProgressMyThemesCacheFromServer() {
  if (!authToken || currentRoute().page !== "progress-management") return false;
  const logoutSeqAtStart = fpLogoutSeq;
  try {
    const params = new URLSearchParams({ scope: "summary", listMode: "starred", myThemes: "1" });
    const res = await fetch(`/api/progress-management?${params.toString()}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    if (!res.ok) return false;
    const d = await res.json().catch(() => ({}));
    if (fpLogoutSeq !== logoutSeqAtStart) return false;
    setProgressGroupListCache("my_themes", {
      groups: Array.isArray(d.groups) ? d.groups : [],
      challenges: Array.isArray(d.challenges) ? d.challenges : []
    });
    markProgressGroupListFetched("my_themes");
    return true;
  } catch {
    return false;
  }
}

async function prefetchProgressListModes(modes) {
  if (!authToken || currentRoute().page !== "progress-management") return;
  const route = currentRoute();
  const projectParam = String(route.searchParams?.get("project") || "").trim();
  const groupParam = String(route.searchParams?.get("group") || "").trim();
  if (projectParam) return;
  if (!groupParam) return;
  const list = (Array.isArray(modes) ? modes : [])
    .map((m) => normalizeProgressListMode(m))
    .filter((m, i, arr) => arr.indexOf(m) === i);
  for (const mode of list) {
    if (hasProgressListCache(mode) || uiState.progressMgmtFetchInFlight) continue;
    await refreshProgressManagementFromServer({ listMode: mode, scope: "summary", prefetch: true });
  }
}

function activeProgressAgendas(agendas) {
  return (Array.isArray(agendas) ? agendas : []).filter((a) => !a.archivedAt);
}

async function refreshProgressManagementFromServer(opts) {
  if (!authToken) return false;
  // 応答待ちの間にログアウト／別アカウントへ切り替わったら、前の人のデータを state へ書き戻さない
  const logoutSeqAtStart = fpLogoutSeq;
  // 取得中に課題を既読にしたら、この応答の viewer（ベル・未読メンション）は既読前の集計なので使わない
  const readSeqAtStart = progressAgendaReadSeq;
  const o = opts && typeof opts === "object" ? opts : {};
  const prefetch = o.prefetch === true;
  const scope = o.scope === "summary" ? "summary" : "full";
  const route = currentRoute();
  const groupParam = String(route.searchParams?.get("group") || "").trim();
  const projectParam = String(route.searchParams?.get("project") || "").trim();
  const issueParam = String(route.searchParams?.get("issue") || "").trim();
  const assignedMeRoute = progressManagementAssignedMeRouteActive(route);
  const onGroupList =
    route.page === "progress-management" &&
    !groupParam &&
    !projectParam &&
    !assignedMeRoute;
  const onAssignedMeFlat = route.page === "progress-management" && !groupParam && !projectParam && assignedMeRoute;
  const onThemeList = route.page === "progress-management" && groupParam && !projectParam;
  let listMode = normalizeProgressListMode(o.listMode != null ? o.listMode : uiState.progressListMode);
  let groupListMode = normalizeProgressGroupListMode(
    o.groupListMode != null ? o.groupListMode : uiState.progressGroupListMode
  );
  if (!onGroupList && !onAssignedMeFlat && !onThemeList) listMode = "active";
  if (!onGroupList && !onAssignedMeFlat) groupListMode = "active";
  if (onAssignedMeFlat) groupListMode = "active";
  const requestListMode = onGroupList && groupListMode === "my_themes" ? "starred" : listMode;
  const requestGroupListMode = onGroupList && groupListMode === "my_themes" ? "active" : groupListMode;
  const params = new URLSearchParams();
  if (scope === "summary") params.set("scope", "summary");
  if (onAssignedMeFlat) params.set("assigned", progressManagementAssignScope(route));
  if (onGroupList && groupListMode === "my_themes") params.set("myThemes", "1");
  if ((onThemeList || onGroupList) && requestListMode !== "active") params.set("listMode", requestListMode);
  if ((onGroupList || onAssignedMeFlat) && requestGroupListMode !== "active") params.set("groupListMode", requestGroupListMode);
  const includeId =
    o.includeChallengeId != null
      ? String(o.includeChallengeId).trim()
      : projectParam;
  if (includeId) params.set("projectId", includeId);
  const includeGroupId =
    o.includeGroupId != null ? String(o.includeGroupId).trim() : groupParam || "";
  if (includeGroupId) params.set("groupId", includeGroupId);
  const hydrateAid =
    o.hydrateAgendaId != null
      ? Number(o.hydrateAgendaId)
      : issueParam
        ? Number(issueParam)
        : NaN;
  if (scope === "summary" && Number.isFinite(hydrateAid) && hydrateAid >= 1) {
    params.set("hydrateAgendaId", String(hydrateAid));
  }
  const q = params.toString() ? `?${params.toString()}` : "";
  try {
    const res = await Promise.race([
      fetch(`/api/progress-management${q}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        cache: "no-store"
      }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 20000))
    ]);
    if (fpLogoutSeq !== logoutSeqAtStart) return false;
    if (res.ok) {
      uiState.progressMgmtFetchFailCount = 0;
      uiState.progressMgmtLoadError = "";
      // 成功レスポンスを受けたら、どの分岐でも必ず失敗フラグを解除する。
      // （以前のコールドスタート失敗で立った suppress が残り、成功後も
      //   「データを取得できませんでした」が固定表示されるのを防ぐ）
      uiState.progressMgmtSuppressEmptyRefetch = false;
      const d = await res.json();
      if (fpLogoutSeq !== logoutSeqAtStart) return false;
      const payload = {
        groups: Array.isArray(d.groups) ? d.groups : [],
        challenges: Array.isArray(d.challenges) ? d.challenges : []
      };
      if (onGroupList) {
        setProgressGroupListCache(groupListMode, payload);
        markProgressGroupListFetched(groupListMode);
        if (!prefetch && normalizeProgressGroupListMode(uiState.progressGroupListMode) === groupListMode) {
          applyProgressPayloadToState(payload);
        }
      } else if (onAssignedMeFlat) {
        const reqScope = progressManagementAssignScope(route);
        setProgressAssignedListCache(payload, reqScope);
        uiState.progressAssignedListFetchDone = true;
        // 応答待ちの間に別スコープへ移っていたら画面へは流さない（次の描画でスコープ不一致として取り直す）
        if (!prefetch && reqScope === progressManagementAssignScope()) applyProgressPayloadToState(payload);
      } else if (onThemeList) {
        setProgressListCache(listMode, payload);
        markProgressListFetched(listMode);
        if (!prefetch && normalizeProgressListMode(uiState.progressListMode) === listMode) {
          applyProgressPayloadToState(payload);
        }
      } else if (!prefetch) {
        applyProgressPayloadToState(payload);
      }
      // 先読み/後着の prefetch GET でベル（未読・メンション・担当）の viewer 状態を上書きしない。
      // 課題詳細を開いて既読化した直後に prefetch の応答が届くと未読へ巻き戻る不具合の対策（IMPROVE-378）。
      // 既読反映は markAgendaRead 時の refreshProgressViewerCountsFromServer（非 prefetch）で行われる。
      if (!prefetch) {
        // 既読化の後に届いた既読前の集計は捨てる（既読反映は markAgendaRead の応答で行われる）
        if (readSeqAtStart === progressAgendaReadSeq) applyProgressViewerFromPayload(d);
        else reconcileProgressNotifyCountsFromTree();
        refreshSidebarUserChrome();
      }
      return true;
    }
    const errBody = await res.text().catch(() => "");
    uiState.progressMgmtFetchFailCount = Number(uiState.progressMgmtFetchFailCount || 0) + 1;
    uiState.progressMgmtLoadError =
      res.status >= 500
        ? "サーバーでデータ取得に失敗しました。しばらくしてから再読み込みしてください。"
        : `データを取得できませんでした（${res.status}）`;
    console.error("progress-management GET failed:", res.status, errBody.slice(0, 500));
    markProgressFetchFailedForRoute(route, { onGroupList, onAssignedMeFlat, onThemeList, listMode, groupListMode });
  } catch (e) {
    if (fpLogoutSeq !== logoutSeqAtStart) return false;
    uiState.progressMgmtFetchFailCount = Number(uiState.progressMgmtFetchFailCount || 0) + 1;
    uiState.progressMgmtLoadError = "通信エラーが発生しました。ネットワークを確認してください。";
    console.error("progress-management GET network error:", e);
    markProgressFetchFailedForRoute(route, { onGroupList, onAssignedMeFlat, onThemeList, listMode, groupListMode });
  }
  return false;
}

/** 現在の ToDo 管理ルートで、実在する課題の詳細画面を開いているか（URL に issue があっても無効なら false） */
function progressManagementIssueDetailRouteActive(route) {
  const r = route || currentRoute();
  if (r.page !== "progress-management") return false;
  const projectId = String(r.searchParams?.get("project") || "").trim();
  const issueParam = String(r.searchParams?.get("issue") || "").trim();
  if (!projectId || !issueParam) return false;
  const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  const ch = chs.find((c) => String(c.id) === projectId);
  const agendas = Array.isArray(ch?.agendas) ? ch.agendas : [];
  return agendas.some((a) => String(a.id) === issueParam);
}

/** ToDo管理のトップ（自分の担当課題） */
function progressManagementHomeHash() {
  return "progress-management?assigned=me";
}

/** 「自分の担当課題」一覧（グループ／テーマ横断）。デフォルト表示。
 *  URL: assigned=me（担当中）/ assigned=created（起票した）/ assigned=involved（関わった）/ クエリなし */
function progressManagementAssignedMeRouteActive(route) {
  const r = route || currentRoute();
  if (r.page !== "progress-management") return false;
  if (String(r.searchParams?.get("group") || "").trim()) return false;
  if (String(r.searchParams?.get("project") || "").trim()) return false;
  if (progressManagementProjectsListRouteActive(r)) return false;
  const v = String(r.searchParams?.get("assigned") || "").trim().toLowerCase();
  if (v === "me" || v === "mine" || v === "1" || v === "involved" || v === "created") return true;
  const qs = r.searchParams ? String(r.searchParams) : "";
  return !qs;
}

/** 担当課題ページのスコープ。"me"（担当中）/ "created"（起票した）/ "involved"（コメントした・担当でも起票者でもない）。
 *  絞り込みの本体はサーバ（api/progress-management.js の agendaViewerScopeSql）。担当課題ページ以外でも "me" を返す。 */
function progressManagementAssignScope(route) {
  const r = route || currentRoute();
  if (!progressManagementAssignedMeRouteActive(r)) return "me";
  const v = String(r.searchParams?.get("assigned") || "").trim().toLowerCase();
  return v === "involved" || v === "created" ? v : "me";
}

/** 「関わった」タブを表示中か。 */
function progressManagementInvolvedRouteActive(route) {
  return progressManagementAssignScope(route) === "involved";
}

/** 「起票した課題」（自分が起票・担当は問わない）を表示中か。 */
function progressManagementCreatedRouteActive(route) {
  return progressManagementAssignScope(route) === "created";
}

function progressMyAssignThemeTabKey(challengeId) {
  const id = String(challengeId ?? "").trim();
  return id || "_none";
}

function normalizeProgressMyAssignTab(raw) {
  const v = String(raw || "").trim();
  if (v === "all" || v === "_none" || /^\d+$/.test(v)) return v;
  return "all";
}

function progressMyAssignRowsForTab(rows, tabId) {
  if (tabId === "all") return rows;
  if (tabId === "_none") return rows.filter((x) => !String(x.ch?.id ?? "").trim());
  return rows.filter((x) => String(x.ch?.id ?? "") === tabId);
}

function normalizeProgressMyAssignQuickFilter(raw) {
  const v = String(raw || "").trim();
  if (v === "overdue") return v;
  return "all";
}

/** 担当課題ページの絞り込み（テーマ/期限超過/ウォッチ中/保留を除く）を全解除する。「合計」チップと
 *  スコープ切替（担当中/起票/関わった）で共用。切替先に該当が無い絞り込みを持ち越すと「件数はあるのに一覧が空」に見える */
function resetProgressMyAssignFilters() {
  uiState.progressMyAssignListFilter = "all";
  uiState.progressMyAssignQuickFilter = "all";
  uiState.progressMyAssignWatchOnly = false;
  uiState.progressMyAssignHideHold = false;
}

function progressMyAssignRowsForQuickFilter(rows, quickId) {
  if (quickId === "overdue") return rows.filter((x) => x.overdue);
  return rows;
}

function progressMyAssignThemeTabsFromRows(rows) {
  const map = new Map();
  for (const r of rows) {
    const tabKey = progressMyAssignThemeTabKey(r.ch?.id);
    const label = String(r.themeName || "").trim() || "（無題）";
    if (!map.has(tabKey)) map.set(tabKey, { tabKey, label, count: 0 });
    map.get(tabKey).count += 1;
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "ja"));
}

function progressMyAssignmentsTabsHtml(rows) {
  const tabs = progressMyAssignThemeTabsFromRows(rows);
  if (!tabs.length) return "";
  const cur = normalizeProgressMyAssignTab(uiState.progressMyAssignListFilter);
  const allCount = rows.length;
  const isAllActive = cur === "all";
  const isOpen = !!uiState.progressMyAssignThemeDdOpen;

  // 「すべて」タブ（pill型・既存デザインそのまま）
  const allBtn = `<button type="button" class="pm-proj-list-tab${isAllActive ? " is-active" : ""}" role="tab" aria-selected="${isAllActive ? "true" : "false"}" data-pm-my-assign-tab="all">すべて<span class="pm-my-assign-tab-count">${allCount}</span></button>`;

  // テーマ絞り込みカスタムドロップダウン（pill型タブと同じ外観）
  const selectedTab = tabs.find((t) => t.tabKey === cur);
  const triggerInner = selectedTab
    ? `${escapeHtml(selectedTab.label)}<span class="pm-my-assign-tab-count">${selectedTab.count}</span>`
    : `${PM_WORD.theme}`;
  const isThemeActive = !isAllActive;

  // ドロップダウン内アイテム
  const items = tabs.map((t) => {
    const isCur = t.tabKey === cur;
    return `<button type="button" class="pm-my-assign-theme-dd-item${isCur ? " is-active" : ""}" data-pm-my-assign-tab="${escapeHtmlAttr(t.tabKey)}" data-pm-my-assign-theme-dd-pick="1">
      <span class="pm-my-assign-theme-dd-item-label">${escapeHtml(t.label)}</span>
      <span class="pm-my-assign-tab-count">${t.count}</span>
    </button>`;
  }).join("");

  const themeDd = `<div class="pm-my-assign-theme-dd${isOpen ? " is-open" : ""}">
    <button type="button"
      class="pm-proj-list-tab pm-my-assign-theme-dd-trigger${isThemeActive ? " is-active" : ""}"
      data-pm-my-assign-theme-dd-toggle="1"
      aria-haspopup="listbox"
      aria-expanded="${isOpen ? "true" : "false"}"
    >${triggerInner}<span class="pm-my-assign-theme-dd-caret" aria-hidden="true"></span></button>
    <div class="pm-my-assign-theme-dd-panel" role="listbox" aria-label="${PM_WORD.theme}を選択">
      ${items}
    </div>
  </div>`;

  // ウォッチ中トグル（テーマの横）。ウォッチ中の担当課題がある時のみ表示。
  const watchOnly = !!uiState.progressMyAssignWatchOnly;
  const watchedCount = rows.filter((r) => Boolean(r.ag?.watchedByMe)).length;
  const watchPill =
    watchedCount > 0 || watchOnly
      ? `<button type="button" class="pm-proj-list-tab pm-my-assign-watch-tab${watchOnly ? " is-active" : ""}" data-pm-my-assign-watch-toggle="1" aria-pressed="${watchOnly ? "true" : "false"}" title="ウォッチ中の担当課題だけ表示">ウォッチ中<span class="pm-my-assign-tab-count">${watchedCount}</span></button>`
      : "";

  // 保留を除くトグル。保留の担当課題がある時のみ表示。
  const hideHold = !!uiState.progressMyAssignHideHold;
  const holdCount = rows.filter((r) => r.agStatus === "保留").length;
  const holdPill =
    holdCount > 0 || hideHold
      ? `<button type="button" class="pm-proj-list-tab pm-my-assign-hold-tab${hideHold ? " is-active" : ""}" data-pm-my-assign-hide-hold-toggle="1" aria-pressed="${hideHold ? "true" : "false"}" title="保留の担当課題を一覧から除く">保留を除く<span class="pm-my-assign-tab-count">${holdCount}</span></button>`
      : "";

  return `<div class="pm-my-assign-tabs-wrap pm-my-assign-tabs-wrap--dropdown">
    <div class="pm-my-assign-tab-row">
      ${allBtn}${themeDd}${watchPill}${holdPill}
    </div>
  </div>`;
}

function progressMyAssignPriPillMod(pri) {
  const v = normalizeProgressAgendaPriority(pri);
  if (v === "最優先") return "urgent";
  if (v === "高") return "high";
  if (v === "低") return "low";
  return "mid";
}

function progressCollectMyAssignmentsRows() {
  const out = [];
  // 絞り込みはサーバ側（api/progress-management.js の agendaViewerScopeSql）で済んでいる。
  // 「関わった」「起票した」タブは *担当ではない* 課題も出すので、ここで担当者チェックをかけると
  // 返ってきた行を全部捨ててしまう。担当中だけは念のため自分宛てかを再確認する。
  const assignScope = progressManagementAssignScope();
  const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  for (const ch of chs) {
    if (ch.archivedAt) continue;
    const cid = Number(ch.id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    const gid =
      ch.todoGroupId != null && String(ch.todoGroupId).trim() !== "" ? String(ch.todoGroupId).trim() : "";
    const grp = gid ? findProgressTodoGroup(gid) : null;
    const projectName = ((grp?.title && String(grp.title).trim()) || "").trim() || "（無題）";
    const themeName = (ch.title && String(ch.title).trim()) || "（無題）";
    for (const ag of activeProgressAgendas(ch.agendas)) {
      // サーバが「開いている課題」として同梱しただけのスコープ外の課題は一覧に出さない（右ペイン専用）
      if (ag.viewerScopeMatch === false) continue;
      if (assignScope === "me" && !isProgressAgendaAssignedToCurrentUser(ag)) continue;
      const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
      if (agStatus === "完了" || agStatus === "対応不要") continue;
      const dueRaw = ag.dueDate ? String(ag.dueDate).slice(0, 10) : "";
      let dueSort = Number.POSITIVE_INFINITY;
      if (dueRaw) {
        const dx = new Date(`${dueRaw}T12:00:00`).getTime();
        if (Number.isFinite(dx)) dueSort = dx;
      }
      let updatedTs = 0;
      if (ag.updatedAt) {
        const ux = new Date(ag.updatedAt).getTime();
        if (Number.isFinite(ux)) updatedTs = ux;
      }
      out.push({ ch, ag, projectName, themeName, gid, agStatus, dueSort, updatedTs });
    }
  }
  out.sort((a, b) => {
    if (a.dueSort !== b.dueSort) return a.dueSort - b.dueSort;
    return b.updatedTs - a.updatedTs;
  });
  return out;
}

/** ToDo：自分担当の課題をプロジェクトまたぎで一覧 (#progress-management?assigned=me) */
function progressManagementMyAssignmentsListHtml() {
  uiState.progressSelectedTodoGroupId = "";
  uiState.progressSelectedChallengeId = "";
  uiState.progressSelectedAgendaId = "";
  const groups = Array.isArray(uiState.progressTodoGroups) ? uiState.progressTodoGroups : [];
  const baseRows = progressCollectMyAssignmentsRows();
  const fetching = Boolean(uiState.progressMgmtFetchInFlight);
  const loadFailed =
    Boolean(uiState.progressMgmtSuppressEmptyRefetch && baseRows.length === 0 && groups.length === 0) && !fetching;
  const loadingBlock =
    fetching && baseRows.length === 0 && groups.length === 0
      ? `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "担当課題を読み込んでいます" })}</div>`
      : "";
  const failBlock = loadFailed
    ? `<div class="pm-my-assign-empty"><p class="pm-empty">データを取得できませんでした。ページを再読み込みしてください。</p></div>`
    : "";
  const noDataBlock =
    !fetching && !loadFailed && baseRows.length === 0
      ? `<div class="pm-my-assign-empty"><p class="pm-empty">自分が担当の${PM_WORD.issue}はありません。</p></div>`
      : "";

  const enriched = baseRows.map((r) => {
    const overdue = isProgressAgendaDueOverdue(r.ag, r.ch);
    const priority = normalizeProgressAgendaPriority(r.ag.priority);
    const category = normalizeProgressAgendaCategory(r.ag.category);
    return { ...r, overdue, priority, category };
  });
  const themeTabs = progressMyAssignThemeTabsFromRows(enriched);
  let tabId = normalizeProgressMyAssignTab(uiState.progressMyAssignListFilter);
  if (tabId !== "all" && !themeTabs.some((t) => t.tabKey === tabId)) tabId = "all";
  if (tabId !== uiState.progressMyAssignListFilter) uiState.progressMyAssignListFilter = tabId;
  let quickFilter = normalizeProgressMyAssignQuickFilter(uiState.progressMyAssignQuickFilter);
  if (quickFilter !== uiState.progressMyAssignQuickFilter) uiState.progressMyAssignQuickFilter = quickFilter;
  let filtered = progressMyAssignRowsForTab(enriched, tabId);
  filtered = progressMyAssignRowsForQuickFilter(filtered, quickFilter);
  const watchOnly = !!uiState.progressMyAssignWatchOnly;
  if (watchOnly) filtered = filtered.filter((r) => Boolean(r.ag?.watchedByMe));
  const hideHold = !!uiState.progressMyAssignHideHold;
  if (hideHold) filtered = filtered.filter((r) => r.agStatus !== "保留");
  const searchQ = normalizeProgressSearchText(uiState.progressIssueSearch);
  if (searchQ) filtered = filtered.filter((r) => progressAgendaMatchesSearch(r.ag, searchQ));
  const tabsBar =
    baseRows.length > 0 && !loadingBlock && !failBlock ? progressMyAssignmentsTabsHtml(enriched) : "";

  const total = enriched.length;
  const overdueN = enriched.filter((x) => x.overdue).length;

  const stat = (label, value, variant, filterKey) => {
    const vCls = variant ? ` pm-my-assign-stat--${variant}` : "";
    const inner = `<span class="pm-my-assign-stat__label">${escapeHtml(label)}</span><span class="pm-my-assign-stat__value">${escapeHtml(String(value))}</span>`;
    if (filterKey === "all") {
      const showAllActive = tabId === "all" && quickFilter === "all" && !watchOnly && !hideHold;
      return `<button type="button" class="pm-my-assign-stat pm-my-assign-stat--filter${vCls}${showAllActive ? " is-active" : ""}" data-pm-my-assign-show-all="1" aria-pressed="${showAllActive ? "true" : "false"}" title="すべての担当課題を表示">${inner}</button>`;
    }
    if (filterKey !== "overdue") {
      return `<div class="pm-my-assign-stat${vCls}">${inner}</div>`;
    }
    const active = quickFilter === filterKey;
    return `<button type="button" class="pm-my-assign-stat pm-my-assign-stat--filter${vCls}${active ? " is-active" : ""}" data-pm-my-assign-quick-filter="overdue" aria-pressed="${active ? "true" : "false"}" title="期限超過の課題だけ表示">${inner}</button>`;
  };
  // 0件でも出す（トークビューと同じ）。起票した課題への切替は上のタブ（progressGroupListModeTabsHtml）
  const statsBar =
    !loadingBlock && !failBlock
      ? `<div class="pm-my-assign-stats" aria-label="件数サマリー">
    ${stat("合計", total, "neutral", "all")}
    ${stat("期限超過", overdueN, overdueN > 0 ? "danger" : "muted", "overdue")}
  </div>`
      : "";

  const rowHtml = ({ ch, ag, projectName, themeName, gid }) => {
    const title = (ag.title && String(ag.title).trim()) || "（無題）";
    const dueDispRaw = formatProgressAgendaDueDisplay(ag.dueDate);
    const dueDisp = dueDispRaw || "";
    const overdueRow = isProgressAgendaDueOverdue(ag, ch);
    const gidEnc = gid ? encodeURIComponent(gid) : "";
    // from=assigned を付与することで、閉じるボタンが自分の担当課題に戻れるようにする
    const issueHref =
      gid && gidEnc
        ? `#progress-management?group=${gidEnc}&project=${encodeURIComponent(String(ch.id))}&issue=${encodeURIComponent(String(ag.id))}&from=assigned`
        : `#progress-management?project=${encodeURIComponent(String(ch.id))}&issue=${encodeURIComponent(String(ag.id))}&from=assigned`;
    const priMod = progressMyAssignPriPillMod(ag.priority);
    const priLbl = normalizeProgressAgendaPriority(ag.priority);
    const catLbl = normalizeProgressAgendaCategory(ag.category);
    const themePart = themeName || "—";
    const projectPart = projectName || "—";
    const dueBadge = dueDisp
      ? `<span class="pm-my-assign-card-due${overdueRow ? " pm-my-assign-card-due--overdue" : ""}">${escapeHtml(dueDisp)}</span>`
      : "";
    const catBadge = catLbl && catLbl !== "その他"
      ? `<span class="pm-my-assign-meta-divider" aria-hidden="true"></span><span class="pm-my-assign-card-cat">${escapeHtml(catLbl)}</span>`
      : "";
    const requesterName = progressAgendaCreatorDisplayName(ag);
    const hasRequester = requesterName && requesterName !== "—";
    const requesterUser = hasRequester ? progressAgendaCreatorUser(ag) : null;
    const requesterBadge = hasRequester
      ? `<span class="pm-my-assign-meta-divider" aria-hidden="true"></span><span class="pm-my-assign-card-requester" title="登録者 ${escapeHtmlAttr(requesterName)}"><span class="pm-my-assign-card-requester-label">登録者</span>${progressUserAvatarHtml(requesterUser, "pm-assignee-avatar--my-assign", requesterName)}<span class="pm-my-assign-card-requester-name">${escapeHtml(progressCardShortName(requesterName))}</span></span>`
      : "";
    // 「関わった」「起票した」タブは自分以外が担当の課題を出すので、いまの担当者が誰かを添える
    // （担当中タブでは常に自分なので出さない。未割り当ては「担当なし」と明示する）
    let assigneeBadge = "";
    if (progressManagementAssignScope() !== "me") {
      const assigneeName = progressIssueAssigneeDisplayName(ag);
      const assigneeUser = assigneeName !== "—" ? progressAgendaAssigneeUser(ag) : null;
      assigneeBadge = assigneeName !== "—"
        ? `<span class="pm-my-assign-meta-divider" aria-hidden="true"></span><span class="pm-my-assign-card-requester" title="担当 ${escapeHtmlAttr(assigneeName)}"><span class="pm-my-assign-card-requester-label">担当</span>${progressUserAvatarHtml(assigneeUser, "pm-assignee-avatar--my-assign", assigneeName)}<span class="pm-my-assign-card-requester-name">${escapeHtml(progressCardShortName(assigneeName))}</span></span>`
        : `<span class="pm-my-assign-meta-divider" aria-hidden="true"></span><span class="pm-my-assign-card-requester"><span class="pm-my-assign-card-requester-label">担当</span><span class="pm-my-assign-card-requester-name">なし</span></span>`;
    }
    const statusStr = normalizeProgressTodoStatusForChallenge(ag.status, ch);
    const statusMod = progressKanColModifier(statusStr);
    return `<div class="pm-my-assign-card${overdueRow ? " pm-my-assign-card--overdue" : ""}" role="listitem">
      <span class="pm-my-assign-pri pm-my-assign-pri--${priMod}" aria-label="優先度: ${escapeHtmlAttr(priLbl)}">${escapeHtml(priLbl)}</span>
      <div class="pm-my-assign-card-main">
        <div class="pm-my-assign-card-row1">
          <a class="pm-my-assign-card-name pm-proj-title-link" href="${issueHref}"><span class="pm-kan-card-key">${progressIssueNumberHtml(ag.id)}</span>${escapeHtml(title)}</a>
          ${dueBadge}
        </div>
        <div class="pm-my-assign-card-row2">
          <span class="pm-my-assign-status pm-my-assign-status--${statusMod}">${escapeHtml(statusStr)}</span>
          ${assigneeBadge}
          ${requesterBadge}
          <span class="pm-my-assign-meta-divider" aria-hidden="true"></span>
          <span class="pm-my-assign-card-ctx">${escapeHtml(themePart)}</span>
          ${catBadge}
        </div>
      </div>
    </div>`;
  };

  const { key: sortKey, dir: sortDir } = ensureProgressMyAssignSortState();
  const sortedRows = pinExactIssueNumberFirst(sortProgressMyAssignRows(filtered, sortKey, sortDir), searchQ);

  let listBody = "";
  if (loadingBlock) listBody = loadingBlock;
  else if (failBlock) listBody = failBlock;
  else if (noDataBlock) listBody = noDataBlock;
  else if (filtered.length === 0) {
    listBody = `<div class="pm-my-assign-empty"><p class="pm-empty">この条件に一致する${PM_WORD.issue}がありません。</p></div>`;
  } else {
    listBody = `<div class="pm-my-assign-cards" role="list">${sortedRows.map((row) => rowHtml(row)).join("")}</div>`;
  }

  return `
    <div class="page pj-page pm-page pm-page--proj-list pm-page--my-assign">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>${progressAssignScopeHeading()}</h2>
          </div>
          <div class="pj-head-actions">${progressAssignedViewToggleHtml()}</div>
        </div>
        ${progressGroupListModeTabsHtml()}
        ${progressMentionInboxHtml()}
        <div class="pm-my-assign-body">
          <div class="pm-my-assign-head-actions">
            ${baseRows.length > 0 && !loadingBlock ? `<div class="pm-my-assign-search">${progressIssueSearchInputHtml()}</div>` : ""}
            ${baseRows.length > 0 && !loadingBlock ? progressMyAssignSortControlHtml() : ""}
            ${progressQuickAddButtonHtml()}
          </div>
          ${statsBar}
          ${tabsBar}
          <div class="pm-my-assign-list list-card">${listBody}</div>
        </div>
      </div>
    </div>
  `;
}

function setProgressManagementHash(groupId, projectId, issueId) {
  if (issueId) {
    closeProgressAgendaFormModal();
  }
  let gid = groupId;
  if ((gid == null || String(gid).trim() === "") && projectId) {
    const ch = (uiState.progressChallenges || []).find((c) => String(c.id) === String(projectId));
    if (ch?.todoGroupId != null) gid = ch.todoGroupId;
  }
  const sp = new URLSearchParams();
  if (gid != null && String(gid).trim() !== "") sp.set("group", String(gid));
  if (projectId) sp.set("project", String(projectId));
  if (issueId) sp.set("issue", String(issueId));
  const q = sp.toString();
  const frag = q ? `progress-management?${q}` : "progress-management";
  if (location.hash.replace(/^#/, "") !== frag) location.hash = frag;
  else render();
}

function progressAgendaDetailUrl(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.challenge || !hit?.agenda) return "";
  const sp = new URLSearchParams();
  if (hit.challenge.todoGroupId != null && String(hit.challenge.todoGroupId).trim() !== "") {
    sp.set("group", String(hit.challenge.todoGroupId));
  }
  sp.set("project", String(hit.challenge.id));
  sp.set("issue", String(hit.agenda.id));
  const base = `${location.origin}${location.pathname}`;
  return `${base}#progress-management?${sp.toString()}`;
}

/** 課題のコピー用ペイロード（Backlog風「テーマ名 #番号」をリンク＋件名はプレーン）。
 *  リッチ対応先ではキーにリンクが付き、非対応先では「テーマ名 #番号 件名」のプレーンになる。 */
function progressAgendaTitledCopyPayload(agendaId) {
  const hit = findProgressAgendaById(agendaId);
  if (!hit?.agenda) return null;
  const url = progressAgendaDetailUrl(agendaId);
  if (!url) return null;
  const title = (hit.agenda.title && String(hit.agenda.title).trim()) || "（無題）";
  const themeName = (hit.challenge?.title && String(hit.challenge.title).trim()) || PM_WORD.theme;
  const key = `${themeName} #${hit.agenda.id}`;
  return {
    title,
    url,
    key,
    // プレーン側は末尾にURLを付ける（ToDoコメント等のURL自動リンク先でもリンクになるように）
    text: `${key} ${title} ${url}`,
    html: `<a href="${escapeHtmlAttr(url)}">${escapeHtml(key)}</a> ${escapeHtml(title)}`
  };
}

function progressKanColModifier(st) {
  if (st === "完了") return "done";
  if (st === "対応中") return "doing";
  if (st === "保留") return "hold";
  if (st === "対応不要") return "na";
  return "open";
}

function formatProgressDateShort(iso) {
  if (!iso) return "—";
  const m = String(iso).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "—";
  return `${m[1]}/${m[2]}/${m[3]}`;
}

/** 猫ローディング: 旧 2 コマ用インターバル掃除（互換のため配列は維持） */
const fpLcCatIntervals = [];

function disposeOnBoardingLoadingCatAnimations() {
  fpLcCatIntervals.forEach((tid) => clearInterval(tid));
  fpLcCatIntervals.length = 0;
}

function syncOnBoardingLoadingCatAfterRender() {
  disposeOnBoardingLoadingCatAnimations();
}

/** 共通 UI: CSS 描画の猫 + アニメ（app.js 組み込み HTML） */
function onBoardingNowLoadingCharSpans() {
  const s = "now loading...";
  const delayMs = 80;
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const safe = ch === " " ? "&nbsp;" : escapeHtml(ch);
    out += `<span class="fp-lc__char" style="animation-delay:${i * delayMs}ms">${safe}</span>`;
  }
  return out;
}

function flowPilotNowLoadingBlock(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const rawSub = o.subtitle != null ? String(o.subtitle).trim() : "";
  const sub = rawSub ? `<p class="fp-lc__sub">${escapeHtml(rawSub)}</p>` : "";
  const mod = o.inline ? " fp-lc--inline" : "";
  const catImg = `<div class="fp-lc__cat-motion" aria-hidden="true"><div class="fp-lc__cat-motion-scale"><div class="fp-lc-css-cat-wrap"><div class="fp-lc-css-shadow"></div><div class="fp-lc-css-body"><div class="fp-lc-css-head"></div><div class="fp-lc-css-ear fp-lc-css-ear--left"></div><div class="fp-lc-css-ear fp-lc-css-ear--right"></div><div class="fp-lc-css-tail"></div><div class="fp-lc-css-leg fp-lc-css-leg--front-a"></div><div class="fp-lc-css-leg fp-lc-css-leg--front-b"></div><div class="fp-lc-css-leg fp-lc-css-leg--back-a"></div><div class="fp-lc-css-leg fp-lc-css-leg--back-b"></div></div></div></div></div>`;
  const chars = onBoardingNowLoadingCharSpans();
  return `<div class="fp-lc${mod}" data-fp-lc="1" role="status" aria-live="polite">
    <div class="fp-lc__inner">
      <div class="fp-lc__row">
        ${catImg}
        <p class="fp-lc__text" aria-label="now loading...">${chars}</p>
      </div>
      ${sub}
    </div>
  </div>`;
}

/** ToDo管理: データ取得待ちの全画面プレースホルダ */
function progressManagementLoadingHtml(message) {
  const sub = String(message || "").trim() || "一覧を読み込んでいます";
  return `
    <div class="page pj-page pm-page pm-page--loading">
      <div class="pj-page-wrap">
        <div class="pm-loading-card pm-loading-card--fp-cat">
          ${flowPilotNowLoadingBlock({ subtitle: sub })}
        </div>
      </div>
    </div>
  `;
}

function progressChallengeStarButtonHtml(c) {
  const pid = escapeHtmlAttr(String(c.id));
  const starred = Boolean(c.starredByMe);
  const cls = starred ? "pm-proj-star-btn pm-proj-star-btn--on" : "pm-proj-star-btn";
  const tip = starred ? "お気に入りを解除" : "お気に入りに追加";
  return `<button type="button" class="${cls}" data-pm-toggle-challenge-star="${pid}" title="${escapeHtmlAttr(tip)}" aria-pressed="${starred ? "true" : "false"}" aria-label="${escapeHtmlAttr(tip)}">★</button>`;
}

function progressTodoGroupStarButtonHtml(g) {
  const gid = escapeHtmlAttr(String(g.id));
  const starred = Boolean(g.starredByMe);
  const cls = starred ? "pm-proj-star-btn pm-proj-star-btn--on" : "pm-proj-star-btn";
  const tip = starred ? "お気に入りを解除" : "お気に入りに追加";
  return `<button type="button" class="${cls}" data-pm-toggle-todo-group-star="${gid}" title="${escapeHtmlAttr(tip)}" aria-pressed="${starred ? "true" : "false"}" aria-label="${escapeHtmlAttr(tip)}">★</button>`;
}

function progressMyThemeChallengesForBadge() {
  const cached = getProgressGroupListCache("my_themes");
  const hasCache = cached && Array.isArray(cached.challenges);
  const source = hasCache ? cached.challenges : (Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : []);
  const seen = new Set();
  const out = [];
  for (const c of source) {
    if (!c || c.archivedAt || !c.starredByMe) continue;
    const id = String(c.id ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(c);
  }
  return out;
}

// progressMyThemeBadgeCount はお気に入りタブ廃止（★絞り込みチップへ統合）で呼び出しゼロになり削除

function progressMyThemesViewPayload() {
  const direct = getProgressGroupListCache("my_themes");
  if (direct) return { groups: direct.groups, challenges: direct.challenges, loading: false, failed: false };
  const done = Boolean(ensureProgressGroupListFetchDoneState().my_themes);
  const loading = !done || Boolean(uiState.progressMgmtLoading || uiState.progressMgmtFetchInFlight);
  return {
    groups: [],
    challenges: [],
    loading,
    failed: !loading && Boolean(uiState.progressMgmtSuppressEmptyRefetch)
  };
}

function progressGroupListModeTabsHtml() {
  // タブ廃止後に my_themes モードのまま来た場合（旧状態・ベル経由等）は
  // 「すべてのプロジェクト＋★絞り込みON」へ引き継ぐ（旧お気に入りタブと同じ見え方になる）
  if (normalizeProgressGroupListMode(uiState.progressGroupListMode) === "my_themes") {
    uiState.progressGroupListMode = "active";
    uiState.progressProjListStarOnly = true;
  }
  const mode = normalizeProgressGroupListMode(uiState.progressGroupListMode);
  const assignActive = progressManagementAssignedMeRouteActive();
  // 「お気に入り」は独立タブを廃止し、「すべてのプロジェクト」内の★絞り込みチップに統合
  // （2026-08-26 ユーザー要望。my_themes モード自体はルート互換のため非破壊で残置）
  const modeButtons = [
    { id: "active", label: "すべてのプロジェクト" },
    { id: "archived", label: "アーカイブ" }
  ]
    .map((t) => {
      const active = !assignActive && mode === t.id ? " is-active" : "";
      const badgeHtml = t.badge != null ? `<span class="pm-proj-list-tab-badge">${t.badge}</span>` : "";
      return `<button type="button" class="pm-proj-list-tab pm-proj-list-tab--secondary${active}" role="tab" aria-selected="${!assignActive && mode === t.id ? "true" : "false"}" data-pm-group-list-mode="${escapeHtmlAttr(t.id)}">${escapeHtml(t.label)}${badgeHtml}</button>`;
    })
    .join("");
  // 「関わった課題」は自分の課題ビューの片割れなので、このバーの担当課題側グループに並べる。
  // 見た目はバー全体の作法（アクティブだけ青ピル・他は右の一覧ナビと同じテイストのテキスト）に従う。
  // 切替ハンドラは 15h-involved-agendas.js
  const involvedActive = assignActive && progressManagementInvolvedRouteActive();
  const createdActive = assignActive && progressManagementCreatedRouteActive();
  const meActive = assignActive && progressManagementAssignScope() === "me";
  const scopeTab = (label, isActive, attrs, title) =>
    `<button type="button" class="pm-proj-list-tab ${isActive ? "pm-proj-list-tab--assign is-active" : "pm-proj-list-tab--secondary"}" ${attrs} aria-pressed="${isActive ? "true" : "false"}"${title ? ` title="${escapeHtmlAttr(title)}"` : ""}>${escapeHtml(label)}</button>`;
  return `<div class="pm-proj-list-tabs-bar">
    <div class="pm-proj-list-tabs pm-proj-list-tabs--scope">
      ${scopeTab("自分の担当課題", meActive, 'data-pm-go-my-assignments="1"')}
      ${scopeTab("起票した課題", createdActive, 'data-pm-assign-scope="created"', "自分が起票した課題（担当が他の人でも出ます）")}
      ${scopeTab("関わった課題", involvedActive, 'data-pm-assign-scope="involved"', "自分がコメントした課題のうち、起票者でも担当者でもないもの")}
    </div>
    <span class="pm-proj-list-tabs-bar__sep" aria-hidden="true"></span>
    <div class="pm-proj-list-tabs pm-proj-list-tabs--modes" role="tablist" aria-label="${PM_WORD.project}一覧の表示">${modeButtons}</div>
  </div>`;
}

function progressBreadcrumbHtml(parts) {
  const items = Array.isArray(parts) ? parts.filter(Boolean) : [];
  if (!items.length) return "";
  return `<nav class="pm-breadcrumb" aria-label="パンくず">${items
    .map((p, i) => {
      const last = i === items.length - 1;
      if (last || !p.href) {
        return `<span class="pm-breadcrumb__item${last ? " pm-breadcrumb__item--current" : ""}">${escapeHtml(p.label || "")}</span>`;
      }
      return `<a class="pm-breadcrumb__item pm-breadcrumb__link" href="${escapeHtmlAttr(p.href)}">${escapeHtml(p.label || "")}</a>`;
    })
    .join('<span class="pm-breadcrumb__sep" aria-hidden="true">›</span>')}</nav>`;
}

/** ToDo管理: グループ → テーマ → 課題のパンくず */
function progressManagementNavBreadcrumb(ch, groupId, opts) {
  // フォルダ1段化: group 階層は出さない（ToDo管理 > プロジェクト名 > 課題）。
  const o = opts && typeof opts === "object" ? opts : {};
  const themeLabel = (ch?.title && String(ch.title).trim()) || "（無題）";
  const issueLabel = o.issueTitle != null ? String(o.issueTitle).trim() : "";
  const parts = [{ label: "ToDo管理", href: "#progress-management" }];
  const boardHref = `#progress-management?project=${encodeURIComponent(String(ch.id))}`;
  if (issueLabel) {
    parts.push({ label: themeLabel, href: boardHref });
    parts.push({ label: issueLabel || "（無題）" });
  } else {
    parts.push({ label: themeLabel });
  }
  return progressBreadcrumbHtml(parts);
}

function progressListModeTabsHtml() {
  const mode = normalizeProgressListMode(uiState.progressListMode);
  const tabs = [
    { id: "active", label: "すべて" },
    { id: "archived", label: "アーカイブ" }
  ];
  return `<div class="pm-proj-list-tabs" role="tablist" aria-label="一覧の表示">
    ${tabs
      .map((t) => {
        const active = mode === t.id ? " is-active" : "";
        return `<button type="button" class="pm-proj-list-tab${active}" role="tab" aria-selected="${mode === t.id ? "true" : "false"}" data-pm-list-mode="${escapeHtmlAttr(t.id)}">${escapeHtml(t.label)}</button>`;
      })
      .join("")}
  </div>`;
}

/** ToDo管理: マイテーマ一覧（#progress-management、mode=my_themes） */
function progressManagementMyThemesHtml() {
  uiState.progressSelectedTodoGroupId = "";
  uiState.progressSelectedChallengeId = "";
  uiState.progressSelectedAgendaId = "";
  const payload = progressMyThemesViewPayload();
  const sourceChallenges = Array.isArray(payload.challenges) ? payload.challenges : [];
  const sourceGroups = Array.isArray(payload.groups) ? payload.groups : [];
  const groupById = new Map(sourceGroups.map((g) => [String(g.id), g]));
  const starredChs = sourceChallenges.filter((c) => c.starredByMe && !c.archivedAt);
  let bodyHtml;
  if (payload.loading) {
    bodyHtml = `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "お気に入りを読み込んでいます" })}</div>`;
  } else if (payload.failed) {
    bodyHtml = `<div class="pm-my-assign-empty"><p class="pm-empty">お気に入りを取得できませんでした。ページを再読み込みしてください。</p></div>`;
  } else if (starredChs.length === 0) {
    bodyHtml = `<div class="pm-my-assign-empty"><p class="pm-empty">★ ${PM_WORD.project}一覧でスターを付けると、ここにショートカットが表示されます。</p></div>`;
  } else {
    const cards = starredChs.map((c) => {
      const gid = c.todoGroupId != null ? String(c.todoGroupId).trim() : "";
      const grp = gid ? groupById.get(gid) || findProgressTodoGroup(gid) : null;
      const groupName = (grp?.title && String(grp.title).trim()) || "";
      const title = (c.title && String(c.title).trim()) || "（無題）";
      const agendas = activeProgressAgendas(c.agendas);
      const openCount = agendas.filter((ag) => {
        const st = normalizeProgressTodoStatusForChallenge(ag.status, c);
        return st !== "完了" && st !== "対応不要";
      }).length;
      const doingCount = agendas.filter((ag) => normalizeProgressTodoStatusForChallenge(ag.status, c) === "対応中").length;
      const boardHref = gid
        ? `#progress-management?group=${encodeURIComponent(gid)}&project=${encodeURIComponent(String(c.id))}`
        : `#progress-management?project=${encodeURIComponent(String(c.id))}`;
      const openBadge = `<span class="pm-my-theme-card-badge pm-my-theme-card-badge--open">${openCount} 未完了</span>`;
      const doingBadge = doingCount > 0 ? `<span class="pm-my-theme-card-badge pm-my-theme-card-badge--doing">${doingCount} 対応中</span>` : "";
      const breadcrumb = groupName ? `<span class="pm-my-theme-card-group">${escapeHtml(groupName)}</span><span class="pm-my-theme-card-sep" aria-hidden="true">/</span>` : "";
      return `<div class="pm-my-theme-card" role="listitem">
        <div class="pm-my-theme-card-main">
          <div class="pm-my-theme-card-breadcrumb">${breadcrumb}<a class="pm-my-theme-card-name pm-proj-title-link" href="${boardHref}">${escapeHtml(title)}</a></div>
          <div class="pm-my-theme-card-meta">${openBadge}${doingBadge}</div>
        </div>
        ${progressChallengeStarButtonHtml(c)}
      </div>`;
    }).join("");
    bodyHtml = `<div class="pm-my-theme-cards" role="list">${cards}</div>`;
  }
  return `
    <div class="page pj-page pm-page pm-page--proj-list pm-page--my-themes">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>お気に入り</h2>
          </div>
        </div>
        ${progressGroupListModeTabsHtml()}
        <div class="pm-my-assign-body">
          <div class="pm-my-assign-list list-card">${bodyHtml}</div>
        </div>
      </div>
    </div>
  `;
}

/** ToDo管理: プロジェクト（challenge）フラット一覧（#progress-management ランディング）。
 *  フォルダ1段化で group 層を廃止し、全プロジェクトをジャンルで絞り込む。 */
function progressManagementProjectListHtml() {
  uiState.progressSelectedTodoGroupId = "";
  uiState.progressSelectedChallengeId = "";
  uiState.progressSelectedAgendaId = "";
  const allChs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  // 前回の絞り込み（★・種別）を復元／保存（15n）
  syncProgressProjListViewState(allChs);
  const listMode = normalizeProgressListMode(uiState.progressListMode);
  const modeChs = allChs.filter((c) => (listMode === "archived" ? !!c.archivedAt : !c.archivedAt));
  const kw = String(uiState.progressProjectListKeyword || "").trim().toLowerCase();
  const selCat = String(uiState.progressGroupListCategory || "").trim();
  const matchesCat = (c) => {
    if (!selCat) return true;
    if (selCat === "__none__") return !String(c.genre || "").trim();
    return String(c.genre || "").trim() === selCat;
  };
  const starOnly = !!uiState.progressProjListStarOnly;
  const filtered = modeChs.filter((c) => {
    const kwOk = !kw || `${c.title || ""} ${c.body || ""}`.toLowerCase().includes(kw);
    return kwOk && matchesCat(c) && (!starOnly || Boolean(c.starredByMe));
  });
  const genreFilterBar = progressGroupCategoryFilterBarHtml(selCat);
  const modalOpen = !!uiState.progressShowProjectForm;
  const loadingList = Boolean(uiState.progressMgmtLoading && allChs.length === 0);
  const loadFailedList = Boolean(uiState.progressMgmtSuppressEmptyRefetch && allChs.length === 0 && !loadingList);
  const emptyMsg =
    listMode === "archived"
      ? `アーカイブした${PM_WORD.theme}はありません。`
      : `まだ${PM_WORD.theme}がありません。「${PM_WORD.theme}を登録」から追加できます。`;
  const rows = loadingList
    ? `<tr><td colspan="7" class="pm-proj-empty-cell pm-proj-empty-cell--loading">${flowPilotNowLoadingBlock({ inline: true, subtitle: "一覧を読み込んでいます" })}</td></tr>`
    : loadFailedList
      ? `<tr><td colspan="7" class="pm-proj-empty-cell"><p class="pm-empty">データを取得できませんでした。ページを再読み込みしてください。</p></td></tr>`
      : modeChs.length === 0
        ? `<tr><td colspan="7" class="pm-proj-empty-cell"><p class="pm-empty">${escapeHtml(emptyMsg)}</p></td></tr>`
        : filtered.length === 0
          ? `<tr><td colspan="7" class="pm-proj-empty-cell"><p class="pm-empty">条件に一致する${PM_WORD.theme}がありません。</p></td></tr>`
          : filtered
              .map((c) => {
                const list = activeProgressAgendas(c.agendas);
                const total = list.length;
                const openCnt = list.filter((a) => {
                  const st = normalizeProgressTodoStatus(a.status);
                  return st !== "完了" && st !== "対応不要";
                }).length;
                const updated = formatProgressDateShort(c.updatedAt || c.createdAt);
                const snip = c.body
                  ? `${escapeHtml(String(c.body).slice(0, 96))}${String(c.body).length > 96 ? "…" : ""}`
                  : "—";
                const pid = escapeHtmlAttr(String(c.id));
                const isArchived = Boolean(c.archivedAt);
                const genreCell =
                  progressTodoGroupCategoryChipHtml(c.genre) ||
                  `<span class="pm-group-genre-chip pm-group-genre-chip--none">未分類</span>`;
                const archiveBtn = isArchived
                  ? `<button type="button" class="pm-proj-action-btn" data-pm-unarchive-challenge="${pid}" title="${PM_WORD.theme}を復元">復元</button>`
                  : `<button type="button" class="pm-proj-action-btn" data-pm-archive-challenge="${pid}" title="${PM_WORD.theme}をアーカイブ">アーカイブ</button>`;
                const editBtn = isArchived
                  ? ""
                  : `<button type="button" class="pm-proj-action-btn" data-pm-open-edit-challenge="${pid}" title="タイトル・種別・説明を編集">編集</button>`;
                const deleteBtn =
                  isMasterRoleUser(currentUser) && !isArchived
                    ? `<button type="button" class="pm-proj-action-btn pm-proj-action-btn--danger" data-pm-delete-challenge="${pid}" title="${PM_WORD.theme}を完全に削除（マスターのみ）">削除</button>`
                    : "";
                return `<tr>
              <td class="pm-proj-td-title"><span class="pm-proj-title-cell">${progressChallengeStarButtonHtml(c)}<a class="pm-proj-title-link" href="#progress-management?project=${pid}">${escapeHtml(c.title || "（無題）")}</a></span></td>
              <td class="pm-proj-td-genre">${genreCell}</td>
              <td class="pm-proj-td-snip">${snip}</td>
              <td class="pm-proj-td-num">${total}</td>
              <td class="pm-proj-td-num pm-proj-td-num--open">${openCnt}</td>
              <td class="pm-proj-td-date">${escapeHtml(updated)}</td>
              <td class="pm-proj-td-actions">
                <div class="pm-proj-action-row">${editBtn}${archiveBtn}${deleteBtn}</div>
              </td>
            </tr>`;
              })
              .join("");
  return `
    <div class="page pj-page pm-page pm-page--proj-list pm-page--theme-list">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>ToDo管理</h2>
          </div>
        </div>
        ${progressGroupListModeTabsHtml()}
        ${genreFilterBar}
        <div class="pm-proj-toolbar">
          <input type="search" class="pj-input pm-proj-keyword" data-pm-project-keyword data-ime-search placeholder="${PM_WORD.theme}名で絞り込み" value="${escapeHtmlAttr(uiState.progressProjectListKeyword || "")}" maxlength="200" />
          ${listMode === "archived" ? "" : progressQuickAddButtonHtml()}
          ${listMode === "archived" ? "" : `<button type="button" class="inline-btn" data-pm-toggle-project-form>${modalOpen ? "登録を閉じる" : `${PM_WORD.theme}を登録`}</button>`}
          ${listMode === "archived" ? "" : `<span class="pm-proj-toolbar-note">※開発カテゴリの課題は、毎朝8時にGitHubにMDファイルが生成されます。</span>`}
        </div>
        <div class="pm-proj-table-wrap list-card">
          <table class="pm-proj-table pm-group-table pm-proj-table--stack-sp" data-fp-table="todo-group-themes" aria-label="ToDo管理の${PM_WORD.theme}一覧">
            <thead>
              <tr>
                <th>${PM_WORD.theme}</th>
                <th>種別</th>
                <th>説明</th>
                <th>${PM_WORD.issue}</th>
                <th>未完了</th>
                <th>更新</th>
                <th></th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

/** ToDo管理: グループ一覧（#progress-management） */
function progressManagementGroupListHtml() {
  uiState.progressSelectedTodoGroupId = "";
  uiState.progressSelectedChallengeId = "";
  uiState.progressSelectedAgendaId = "";
  const groups = Array.isArray(uiState.progressTodoGroups) ? uiState.progressTodoGroups : [];
  const listMode = normalizeProgressGroupListMode(uiState.progressGroupListMode);
  const kw = String(uiState.progressGroupListKeyword || "").trim().toLowerCase();
  const selCat = String(uiState.progressGroupListCategory || "").trim();
  const matchesCat = (g) => {
    if (!selCat) return true;
    if (selCat === "__none__") return !String(g.category || "").trim();
    return String(g.category || "").trim() === selCat;
  };
  const filtered = groups.filter((g) => {
    const kwOk = !kw || `${g.title || ""} ${g.body || ""}`.toLowerCase().includes(kw);
    return kwOk && matchesCat(g);
  });
  const genreFilterBar = progressGroupCategoryFilterBarHtml(selCat);
  const modalOpen = !!uiState.progressShowTodoGroupForm;
  const loadingList = Boolean(uiState.progressMgmtLoading && groups.length === 0);
  const loadFailedList = Boolean(uiState.progressMgmtSuppressEmptyRefetch && groups.length === 0 && !loadingList);
  const emptyMsg =
    listMode === "archived"
      ? `アーカイブした${PM_WORD.project}はありません。`
      : `まだ${PM_WORD.project}がありません。「${PM_WORD.project}を登録」から追加できます。`;
  const rows = loadingList
    ? `<tr><td colspan="8" class="pm-proj-empty-cell pm-proj-empty-cell--loading">${flowPilotNowLoadingBlock({ inline: true, subtitle: "一覧を読み込んでいます" })}</td></tr>`
    : loadFailedList
      ? `<tr><td colspan="8" class="pm-proj-empty-cell"><p class="pm-empty">データを取得できませんでした。ページを再読み込みしてください。</p></td></tr>`
      : groups.length === 0
        ? `<tr><td colspan="8" class="pm-proj-empty-cell"><p class="pm-empty">${escapeHtml(emptyMsg)}</p></td></tr>`
        : filtered.length === 0
          ? `<tr><td colspan="8" class="pm-proj-empty-cell"><p class="pm-empty">条件に一致する${PM_WORD.project}がありません。</p></td></tr>`
          : filtered
              .map((g) => {
                const gid = escapeHtmlAttr(String(g.id));
                const snip = g.body
                  ? `${escapeHtml(String(g.body).slice(0, 96))}${String(g.body).length > 96 ? "…" : ""}`
                  : "—";
                const updated = formatProgressDateShort(g.updatedAt || g.createdAt);
                const isArchived = Boolean(g.archivedAt);
                const archiveBtn = isArchived
                  ? `<button type="button" class="pm-proj-action-btn" data-pm-unarchive-todo-group="${gid}" title="${PM_WORD.project}を復元">復元</button>`
                  : `<button type="button" class="pm-proj-action-btn" data-pm-archive-todo-group="${gid}" title="${PM_WORD.project}をアーカイブ">アーカイブ</button>`;
                const editBtn = isArchived
                  ? ""
                  : `<button type="button" class="pm-proj-action-btn" data-pm-open-edit-todo-group="${gid}" title="${PM_WORD.project}名・説明を編集">編集</button>`;
                const deleteBtn =
                  isMasterRoleUser(currentUser) && !isArchived
                    ? `<button type="button" class="pm-proj-action-btn pm-proj-action-btn--danger" data-pm-delete-todo-group="${gid}" title="${PM_WORD.project}を完全に削除（${PM_WORD.theme}が無い場合のみ・マスター）">削除</button>`
                    : "";
                const genreCell =
                  progressTodoGroupCategoryChipHtml(g.category) ||
                  `<span class="pm-group-genre-chip pm-group-genre-chip--none">未分類</span>`;
                return `<tr class="pm-group-row">
              <td class="pm-proj-td-title"><span class="pm-proj-title-cell"><a class="pm-proj-title-link pm-group-title-link" href="#progress-management?group=${gid}">${escapeHtml(g.title || "（無題）")}</a></span></td>
              <td class="pm-proj-td-genre">${genreCell}</td>
              <td class="pm-proj-td-snip">${snip}</td>
              <td class="pm-proj-td-num">${Number(g.themeCount) || 0}</td>
              <td class="pm-proj-td-num">${Number(g.agendaCount) || 0}</td>
              <td class="pm-proj-td-num pm-proj-td-num--open">${Number(g.openAgendaCount) || 0}</td>
              <td class="pm-proj-td-date">${escapeHtml(updated)}</td>
              <td class="pm-proj-td-actions">
                <div class="pm-proj-action-row">${editBtn}${archiveBtn}${deleteBtn}</div>
              </td>
            </tr>`;
              })
              .join("");
  return `
    <div class="page pj-page pm-page pm-page--group-list">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>ToDo管理</h2>
          </div>
        </div>
        ${progressGroupListModeTabsHtml()}
        ${genreFilterBar}
        <div class="pm-proj-toolbar">
          <input type="search" class="pj-input pm-proj-keyword" data-pm-group-keyword data-ime-search placeholder="${PM_WORD.project}名で絞り込み" value="${escapeHtmlAttr(uiState.progressGroupListKeyword || "")}" maxlength="200" />
          ${progressQuickAddButtonHtml()}
          ${listMode === "archived" ? "" : `<button type="button" class="inline-btn" data-pm-toggle-todo-group-form>${modalOpen ? "登録を閉じる" : `${PM_WORD.project}を登録`}</button>`}
        </div>
        <div class="pm-proj-table-wrap list-card">
          <table class="pm-proj-table pm-group-table pm-proj-table--stack-sp" data-fp-table="todo-projects" aria-label="ToDo管理の${PM_WORD.project}一覧">
            <thead>
              <tr>
                <th>${PM_WORD.project}</th>
                <th>分類</th>
                <th>説明</th>
                <th>${PM_WORD.theme}</th>
                <th>${PM_WORD.issue}</th>
                <th>未完了</th>
                <th>更新</th>
                <th></th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>
      </div>
  `;
}

/** ToDo管理: プロジェクト一覧（#progress-management?group=） */
function progressManagementThemeListHtml(groupId) {
  const gid = String(groupId || "").trim();
  const grp = findProgressTodoGroup(gid);
  uiState.progressSelectedTodoGroupId = gid;
  uiState.progressSelectedChallengeId = "";
  uiState.progressSelectedAgendaId = "";
  if (!grp) {
    if (uiState.progressMgmtLoading || uiState.progressMgmtFetchInFlight) {
      return progressManagementLoadingHtml(`${PM_WORD.project}を読み込み中…`);
    }
    const loadFailed = Boolean(uiState.progressMgmtSuppressEmptyRefetch);
    const note = loadFailed
      ? "データを取得できませんでした。ページを再読み込みしてください。"
      : `${PM_WORD.project}が見つかりません。一覧から開き直してください。`;
    return `
    <div class="page pj-page pm-page pm-page--theme-list">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        ${progressBreadcrumbHtml([
          { label: "ToDo管理", href: "#progress-management" },
          { label: "（不明）" }
        ])}
        <p class="pm-empty">${escapeHtml(note)}</p>
      </div>
    </div>`;
  }
  const chs = progressChallengesInGroup(gid);
  const gidAttr = escapeHtmlAttr(gid);
  const listMode = normalizeProgressListMode(uiState.progressListMode);
  const kw = String(uiState.progressProjectListKeyword || "").trim().toLowerCase();
  const filtered = !kw
    ? chs
    : chs.filter((c) => `${c.title || ""} ${c.body || ""}`.toLowerCase().includes(kw));
  const modalOpen = !!uiState.progressShowProjectForm;
  const loadingList = Boolean(uiState.progressMgmtLoading && chs.length === 0);
  const loadFailedList = Boolean(uiState.progressMgmtSuppressEmptyRefetch && chs.length === 0 && !loadingList);
  const emptyMsg =
    listMode === "archived"
      ? `アーカイブした${PM_WORD.theme}はありません。`
      : `まだ${PM_WORD.theme}がありません。「${PM_WORD.theme}を登録」から追加できます。`;
  const rows = loadingList
    ? `<tr><td colspan="6" class="pm-proj-empty-cell pm-proj-empty-cell--loading">${flowPilotNowLoadingBlock({ inline: true, subtitle: "一覧を読み込んでいます" })}</td></tr>`
    : loadFailedList
      ? `<tr><td colspan="6" class="pm-proj-empty-cell"><p class="pm-empty">データを取得できませんでした。別の画面から戻るか、ページを再読み込みしてください。</p></td></tr>`
    : chs.length === 0
      ? `<tr><td colspan="6" class="pm-proj-empty-cell"><p class="pm-empty">${escapeHtml(emptyMsg)}</p></td></tr>`
        : filtered.length === 0
        ? `<tr><td colspan="6" class="pm-proj-empty-cell"><p class="pm-empty">キーワードに一致する項目がありません。</p></td></tr>`
        : filtered
            .map((c) => {
              const list = activeProgressAgendas(c.agendas);
              const total = list.length;
              const openCnt = list.filter((a) => {
                const st = normalizeProgressTodoStatus(a.status);
                return st !== "完了" && st !== "対応不要";
              }).length;
              const updated = formatProgressDateShort(c.updatedAt || c.createdAt);
              const snip = c.body
                ? `${escapeHtml(String(c.body).slice(0, 96))}${String(c.body).length > 96 ? "…" : ""}`
                : "—";
              const pid = escapeHtmlAttr(String(c.id));
              const isArchived = Boolean(c.archivedAt);
              const archiveBtn = isArchived
                ? `<button type="button" class="pm-proj-action-btn" data-pm-unarchive-challenge="${pid}" title="${PM_WORD.theme}を復元">復元</button>`
                : `<button type="button" class="pm-proj-action-btn" data-pm-archive-challenge="${pid}" title="${PM_WORD.theme}をアーカイブ">アーカイブ</button>`;
              const editBtn = isArchived
                ? ""
                : `<button type="button" class="pm-proj-action-btn" data-pm-open-edit-challenge="${pid}" title="タイトル・説明を編集">編集</button>`;
              const deleteBtn =
                isMasterRoleUser(currentUser) && !isArchived
                  ? `<button type="button" class="pm-proj-action-btn pm-proj-action-btn--danger" data-pm-delete-challenge="${pid}" title="${PM_WORD.theme}を完全に削除（マスターのみ）">削除</button>`
                  : "";
              return `<tr>
              <td class="pm-proj-td-title"><span class="pm-proj-title-cell">${progressChallengeStarButtonHtml(c)}<a class="pm-proj-title-link" href="#progress-management?group=${gidAttr}&project=${pid}">${escapeHtml(c.title || "（無題）")}</a></span></td>
              <td class="pm-proj-td-snip">${snip}</td>
              <td class="pm-proj-td-num">${total}</td>
              <td class="pm-proj-td-num">${openCnt}</td>
              <td class="pm-proj-td-date">${escapeHtml(updated)}</td>
              <td class="pm-proj-td-actions">
                <div class="pm-proj-action-row">
                  ${editBtn}
                  ${archiveBtn}
                  ${deleteBtn}
                </div>
              </td>
            </tr>`;
            })
            .join("");
  return `
    <div class="page pj-page pm-page pm-page--proj-list pm-page--theme-list">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        ${progressBreadcrumbHtml([
          { label: "ToDo管理", href: "#progress-management" },
          { label: grp.title || "（無題）" }
        ])}
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>${escapeHtml(grp.title || "（無題）")}</h2>
            ${grp.body ? `<p class="pm-theme-list-desc">${escapeHtml(String(grp.body)).replace(/\n/g, "<br />")}</p>` : ""}
          </div>
        </div>
        ${progressListModeTabsHtml()}
        <div class="pm-proj-toolbar">
          <input type="search" class="pj-input pm-proj-keyword" data-pm-project-keyword data-ime-search placeholder="${PM_WORD.theme}名で絞り込み" value="${escapeHtmlAttr(uiState.progressProjectListKeyword || "")}" maxlength="200" />
          ${progressQuickAddButtonHtml()}
          ${listMode === "archived" ? "" : `<button type="button" class="inline-btn" data-pm-toggle-project-form>${modalOpen ? "登録を閉じる" : `${PM_WORD.theme}を登録`}</button>`}
        </div>
        <div class="pm-proj-table-wrap list-card">
          <table class="pm-proj-table pm-proj-table--stack-sp" data-fp-table="todo-themes" aria-label="${PM_WORD.theme}一覧">
            <thead>
              <tr>
                <th>${PM_WORD.theme}</th>
                <th>説明</th>
                <th>${PM_WORD.issue}</th>
                <th>未完了</th>
                <th>更新</th>
                <th></th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

function progressAgendaCategoryBadgeClass(cat) {
  const c = normalizeProgressAgendaCategory(cat);
  if (c === "バグ") return "pm-kan-card-cat pm-kan-card-cat--urgent";
  if (c === "改善" || c === "機能" || c === "要望") return "pm-kan-card-cat pm-kan-card-cat--high";
  if (c === "調査" || c === "運用") return "pm-kan-card-cat pm-kan-card-cat--low";
  return "pm-kan-card-cat pm-kan-card-cat--mid";
}

/** 課題詳細: 作成時に添付したファイルのインライン表示 */
function progressIssueAttachmentsInlineHtml(ag) {
  const list = progressAgendaAttachmentsList(ag);
  if (!list.length) return "";
  const images = list.filter(progressAttachmentIsImage);
  const others = list.filter((a) => !progressAttachmentIsImage(a));
  const previewUrls = uiState.progressAttachPreviewUrls || {};

  // 未取得の画像URLを非同期でロードして再描画
  const needLoad = images.filter((a) => !previewUrls[String(a.id)]);
  if (needLoad.length) {
    requestAnimationFrame(() => {
      needLoad.forEach((att) => {
        void fetchProgressAttachmentObjectUrl(att.id).then(() => {
          // 課題詳細が開いていたら本文エリアだけ差し替える
          const el = document.querySelector(".pm-issue-attachments-inline");
          if (el) {
            const found = findProgressAgendaById(ag.id);
            if (found?.agenda) el.outerHTML = progressIssueAttachmentsInlineHtml(found.agenda);
          }
        }).catch(() => {/* サイレント */});
      });
    });
  }

  const imgItems = images.map((att) => {
    const idAttr = escapeHtmlAttr(String(att.id));
    const fn = escapeHtmlAttr(String(att.filename || "image"));
    const thumbUrl = previewUrls[String(att.id)] || "";
    const thumb = thumbUrl
      ? `<img class="pm-issue-attach-img" src="${escapeHtmlAttr(thumbUrl)}" alt="${fn}" loading="lazy" />`
      : `<span class="pm-issue-attach-img-ph" aria-hidden="true">🖼</span>`;
    return `<button type="button" class="pm-issue-attach-img-btn" data-pm-attach-preview="${idAttr}" title="${fn}">${thumb}</button>`;
  }).join("");

  const fileItems = others.map((att) => {
    const idAttr = escapeHtmlAttr(String(att.id));
    const fn = escapeHtml(String(att.filename || "ファイル"));
    const icon = progressAttachmentPreviewIcon(att);
    return `<div class="pm-issue-attach-file">
      <span class="pm-issue-attach-file-icon" aria-hidden="true">${icon}</span>
      <span class="pm-issue-attach-file-name" title="${escapeHtmlAttr(String(att.filename || ""))}">${fn}</span>
      <button type="button" class="pm-attach-btn pm-attach-btn--dl" data-pm-attach-download="${idAttr}" aria-label="ダウンロード">DL</button>
    </div>`;
  }).join("");

  return `<div class="pm-issue-attachments-inline">
    ${imgItems ? `<div class="pm-issue-attach-imgs">${imgItems}</div>` : ""}
    ${fileItems ? `<div class="pm-issue-attach-files">${fileItems}</div>` : ""}
  </div>`;
}

/** メモ欄の開閉状態（既定=展開＝現状維持。ユーザー・課題ごとに localStorage で記憶） */
function progressMemoExpanded(agendaId) {
  try {
    const stored = localStorage.getItem(`pm_memo_expanded_${agendaId}`);
    return stored == null ? true : stored === "1";
  } catch {
    return true;
  }
}

/** メモに未読の更新があるか（本文の最終更新時刻 vs 最後に開いた時刻の比較） */
function progressMemoHasUpdate(ag) {
  if (!ag?.bodyUpdatedAt) return false;
  try {
    const lastViewed = Number(localStorage.getItem(`pm_memo_last_viewed_${ag.id}`) || 0);
    return new Date(ag.bodyUpdatedAt).getTime() > lastViewed;
  } catch {
    return false;
  }
}

/** 課題本文（メモ）の表示HTML。やりとり（コメント）と同じ整形関数を通し、URL自動リンク・
 *  **太字**・~~打消~~・/斜体/・コードブロック・@メンション強調を本文でも効かせる。
 *  改行は .pm-issue-body-inline の white-space: pre-wrap で表現するため <br> 変換はしない。 */
function progressIssueBodyDisplayHtml(body) {
  return `<div id="pm-issue-detail-body" class="pm-issue-body-inline">${progressMessageBodyDisplayHtml(body)}</div>`;
}

/** 折りたたみ時のメモ プレビュー1行。本文がコードブロックで始まると 1行目が ``` になり、
 *  記号だけが見えてしまうため、フェンス行と空行は飛ばして最初の中身の行を採る。 */
function progressMemoPreviewLine(body) {
  const line = String(body || "")
    .split("\n")
    .find((l) => l.trim() && !l.trim().startsWith("```"));
  return (line || "").slice(0, 60);
}

/** 課題詳細: 作成時に登録した詳細本文（メモ）。開閉式（既定=展開）＋文字数・更新ありバッジ表示 */
function progressIssueBodyInlineHtml(ag) {
  const body = String(ag?.body || "").trim();
  const attachHtml = progressIssueAttachmentsInlineHtml(ag);
  if (!body && !attachHtml) return "";
  const bodyHtml = body ? progressIssueBodyDisplayHtml(body) : "";
  if (!body) return bodyHtml + attachHtml; // 本文なし（添付のみ）は開閉UI不要
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  const expanded = progressMemoExpanded(ag.id);
  const hasUpdate = progressMemoHasUpdate(ag);
  // 展開表示中は本文が実際に見えているため「閲覧済み」として記録する（既定=展開のため、
  // トグル操作を一度もしないケースでも更新ありバッジが永久に消えないのを防ぐ）。
  if (expanded && hasUpdate && ag?.bodyUpdatedAt) {
    try {
      localStorage.setItem(`pm_memo_last_viewed_${ag.id}`, String(Date.now()));
    } catch {
      /* localStorage不可は既読記録をスキップするだけ */
    }
  }
  const firstLine = progressMemoPreviewLine(body);
  const previewHtml = expanded ? "" : `<div class="pm-memo-preview">${escapeHtml(firstLine)}${firstLine.length < body.length ? "…" : ""}</div>`;
  return `<div class="pm-memo${expanded ? "" : " pm-memo--folded"}">
      <div class="pm-memo-head" data-pm-memo-toggle="${aidAttr}">
        <span class="pm-memo-head-left">📝 メモ${hasUpdate ? ` <span class="pm-memo-badge">更新あり</span>` : ""}</span>
        <span class="pm-memo-toggle-label">${expanded ? "閉じる ▲" : "開く ▾"}</span>
      </div>
      ${previewHtml}
      <div class="pm-memo-body">${bodyHtml}${attachHtml}</div>
    </div>`;
}

/** 課題詳細の「メモ＋担当者などの属性行＋工程バー」をまとめて1つの開閉ブロックとして描画する
 * （PC・SP共通）。トグルは既存のメモ開閉と同じ状態（progressMemoExpanded）を使い、開閉対象を
 * メモ本文だけでなく attrHtml・stepBarHtml まで広げる。閉じている間はタイトル行だけを残し、
 * 担当・期限・工程の詳細を隠して省スペース表示にできるようにするため。メモ本文が空でも
 * 属性行・工程バーは表示する対象なので、本文の有無に関わらずこのブロック自体は常に描画する。
 * @param {string} attrHtml 呼び出し側で組み立てた担当者などの属性行HTML
 * @param {string} stepBarHtml 呼び出し側で組み立てた工程バーHTML（progressIssueStepBarHtml の戻り値）
 */
function progressIssueCollapsibleDetailHtml(ag, attrHtml, stepBarHtml) {
  const body = String(ag?.body || "").trim();
  const attachHtml = progressIssueAttachmentsInlineHtml(ag);
  const hasMemo = Boolean(body || attachHtml);
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  const expanded = progressMemoExpanded(ag.id);
  const hasUpdate = hasMemo ? progressMemoHasUpdate(ag) : false;
  if (expanded && hasUpdate && ag?.bodyUpdatedAt) {
    try {
      localStorage.setItem(`pm_memo_last_viewed_${ag.id}`, String(Date.now()));
    } catch {
      /* localStorage不可は既読記録をスキップするだけ */
    }
  }
  const headerLabel = hasMemo ? `📝 メモ` : "詳細";
  const bodyHtml = body ? progressIssueBodyDisplayHtml(body) : "";
  const firstLine = progressMemoPreviewLine(body);
  const previewHtml = !expanded && hasMemo
    ? `<div class="pm-memo-preview">${escapeHtml(firstLine)}${firstLine.length < body.length ? "…" : ""}</div>`
    : "";
  return `<div class="pm-memo${expanded ? "" : " pm-memo--folded"}">
      <div class="pm-memo-head" data-pm-memo-toggle="${aidAttr}">
        <span class="pm-memo-head-left">${headerLabel}${hasUpdate ? ` <span class="pm-memo-badge">更新あり</span>` : ""}</span>
        <span class="pm-memo-toggle-label">${expanded ? "閉じる ▲" : "開く ▾"}</span>
      </div>
      ${previewHtml}
      <div class="pm-memo-body">
        ${bodyHtml}${attachHtml}
        ${attrHtml || ""}
        ${stepBarHtml || ""}
      </div>
    </div>`;
}

/** 状況変更メッセージ（既存の履歴ログ）から「いつ・どこから・どこへ変更したか」を抽出する。DB列は増やさず、
 * 既存の状況変更ログ（システムメッセージ）を再利用して各工程の所要時間を割り出すため。
 * @returns {{from:string, to:string, at:string}[]} 発生時刻の昇順
 */
function progressAgendaStatusChangeEvents(ag) {
  const msgs = Array.isArray(ag?.messages) ? ag.messages : [];
  const re = /が\s*状況\s*を「(.+?)」から「(.+?)」に変更しました。/;
  const events = [];
  for (const m of msgs) {
    if (!m?.isSystem || !m.createdAt) continue;
    const lines = String(m.body || "").split("\n");
    for (const line of lines) {
      const hit = line.match(re);
      if (hit) events.push({ from: hit[1], to: hit[2], at: m.createdAt });
    }
  }
  events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return events;
}

/** 課題の状況（＝工程）が切り替わった時系列（作成時の初期状況を先頭に含む）。昇順
 * 作成時の初期状況は、最初の状況変更ログの「から」（無ければ現在の状況＝一度も変更していない）から求める。
 */
function progressAgendaStepTimeline(ag) {
  // changes は既に昇順。作成時刻(先頭に足す初期状況)は必ず最初の変更より前なので、追加ソートは不要
  const changes = progressAgendaStatusChangeEvents(ag);
  const timeline = [];
  const initialStatus = changes.length ? changes[0].from : String(ag?.status || "");
  if (ag?.createdAt && initialStatus) timeline.push({ status: initialStatus, at: ag.createdAt });
  for (const c of changes) timeline.push({ status: c.to, at: c.at });
  return timeline;
}

/** 営業日判定（土日祝＋年末年始休業(12/29〜1/3)を除く）。経過時間の業務時間換算に使用。
 *  土日祝の判定は 00-shared-enums.js の isBusinessDay（フロント/サーバ共通の単一定義元）。
 *  年末年始休業は祝日ではないため、この換算だけの上乗せルールとしてここに置く。 */
function isProgressBusinessDay(date) {
  const m = date.getMonth() + 1, d = date.getDate();
  if ((m === 12 && d >= 29) || (m === 1 && d <= 3)) return false;
  return isBusinessDay(date);
}

/** コアタイム制で固定の勤務時間帯が無いため、営業日の暦上経過分を日割りで7.5時間に換算する
 * （土日・祝日・年末年始は0扱い）。1営業日 = 7.5時間。 */
const PROGRESS_BUSINESS_MS_PER_DAY = 7.5 * 60 * 60 * 1000;
const PROGRESS_DAY_MS = 24 * 60 * 60 * 1000;

/** 実時間の区間 [startMs, endMs) を、営業日分だけ7.5時間換算した「業務経過ミリ秒」に変換 */
function businessElapsedMs(startMs, endMs) {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  let total = 0;
  let dayCursor = new Date(startMs);
  dayCursor.setHours(0, 0, 0, 0);
  // 反復上限（保険）: 異常に長い区間でもタブが固まらないよう上限を設ける（約10年分）
  for (let guard = 0; guard < 3660 && dayCursor.getTime() < endMs; guard++) {
    const dayStart = dayCursor.getTime();
    const dayEnd = dayStart + PROGRESS_DAY_MS;
    const segStart = Math.max(startMs, dayStart);
    const segEnd = Math.min(endMs, dayEnd);
    if (segEnd > segStart && isProgressBusinessDay(dayCursor)) {
      total += ((segEnd - segStart) / PROGRESS_DAY_MS) * PROGRESS_BUSINESS_MS_PER_DAY;
    }
    dayCursor = new Date(dayEnd);
  }
  return total;
}

/**
 * 各工程（状況）に費やした合計時間（営業時間換算ミリ秒）。同じ工程を行き来した場合は合算。
 * 現在の工程（最後のタイムライン項目）は「今まで」までの経過分を加算する。
 * 土日・祝日・年末年始は除外し、営業日は1日=7.5時間で換算する（businessElapsedMs）。
 * @returns {Map<string, number>}
 */
function progressAgendaStepDurationsMs(ag) {
  const timeline = progressAgendaStepTimeline(ag);
  const out = new Map();
  const now = Date.now();
  for (let i = 0; i < timeline.length; i++) {
    const cur = timeline[i];
    const next = timeline[i + 1];
    const startMs = new Date(cur.at).getTime();
    const endMs = next ? new Date(next.at).getTime() : now;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) continue;
    out.set(cur.status, (out.get(cur.status) || 0) + businessElapsedMs(startMs, endMs));
  }
  return out;
}

/** 営業時間換算ミリ秒を「1日2時間」「7時間」のような日本語表記に整形（1日=7.5時間） */
function formatProgressStepDurationMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const totalMinutes = Math.floor(ms / 60000);
  const minutesPerBusinessDay = 7.5 * 60;
  const days = Math.floor(totalMinutes / minutesPerBusinessDay);
  const hours = Math.floor((totalMinutes % minutesPerBusinessDay) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days}日${hours}時間` : `${days}日`;
  if (hours > 0) return `${hours}時間`;
  return `${Math.max(minutes, 1)}分`;
}

/** 課題詳細: 制作プロジェクトの進捗ステップバー（状況＝工程の横型フロー＋工程ごとの所要時間・工数担当） */
function progressIssueStepBarHtml(ag, ch) {
  if (!ag || !ch || !isSeisakuProject(ch)) return "";
  // テーマ設定の状態列をそのまま工程として並べる。保留・対応不要はフロー外のためバーに出さない
  // （状況ドロップダウン側で扱う）。
  const steps = progressChallengeStatuses(ch).filter((s) => s !== "保留" && s !== "対応不要");
  const curStatus = String(ag.status || "").trim();
  const curIndex = steps.indexOf(curStatus);
  if (curIndex < 0) return "";
  const durations = progressAgendaStepDurationsHybrid(ag);
  const overrides =
    ag.stepDurationOverrides && typeof ag.stepDurationOverrides === "object" ? ag.stepDurationOverrides : {};
  // 工数（経過時間）を計測する工程（テーマ設定の「工数」チェック。null=全工程を計測）
  const trackedList = Array.isArray(ch.timeTrackedStatuses)
    ? ch.timeTrackedStatuses.map((s) => String(s || "").trim()).filter(Boolean)
    : null;
  // 工程ごとの工数担当（工程名 → fp_users.id）。未選択の工程は課題の担当者に従う
  const stepAssignees = ag.stepAssignees && typeof ag.stepAssignees === "object" ? ag.stepAssignees : {};
  const assigneeName = progressIssueAssigneeDisplayName(ag);
  const assigneeUser =
    ag.assignedUserId != null ? users.find((u) => u.dbId != null && Number(u.dbId) === Number(ag.assignedUserId)) : null;
  const stepAssigneePool = progressChallengeAssigneePool(ch).filter((u) => u.dbId != null);
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  const currentStepRejected = progressAgendaCurrentStepIsRejected(ag);
  const stepsHtml = steps.map((st, i) => {
    const mod = i < curIndex ? "done" : i === curIndex ? "current" : "later";
    const rejectedMod = mod === "current" && currentStepRejected ? " pm-step--rejected" : "";
    const overrideMin = overrides[st];
    const hasOverride = overrideMin != null && Number.isFinite(Number(overrideMin));
    const durMs = hasOverride ? Number(overrideMin) * 60000 : durations.get(st);
    let durHtml = "—";
    if (durMs != null) {
      const label = mod === "current" ? `経過 ${formatProgressStepDurationMs(durMs)}` : formatProgressStepDurationMs(durMs);
      durHtml = `⏱ ${escapeHtml(label)}${hasOverride ? " ✎" : ""}`;
    }
    // 手動設定（工数担当プルダウン）を優先し、無ければ工程履歴の直近通過時の担当にフォールバック。
    // ただし現在の工程では履歴を使わない（freeze が入場時に記録を消すのと同じ意図。差し戻しで戻ると
    // 過去の通過記録が現在の課題担当より優先表示され、計測対象外の工程は直すUIも無いため）。
    const manualUid = progressStepAssigneeUidOf(stepAssignees[st]);
    const stepUid = manualUid != null
      ? manualUid
      : mod === "current" ? null : progressStepAssigneeUidOf(progressAgendaStepAssigneeFromHistory(ag, st));
    const stepUser = stepUid != null ? users.find((u) => u.dbId != null && Number(u.dbId) === stepUid) : null;
    const dispUser = stepUser || assigneeUser;
    const dispName = stepUser ? String(stepUser.name || "") : assigneeName;
    const nodeInner = mod === "done"
      ? "✓"
      : mod === "current"
        ? progressUserAvatarHtml(dispUser, "pm-step-node-avatar", dispName)
        : "";
    const nodeCls = mod === "current" && dispUser ? "pm-step-node pm-step-node--avatar" : "pm-step-node";
    const curLabel = mod === "current" ? `<span class="pm-step-cur-label">▼ 現在</span>` : "";
    // 退職済み等でプールに居ない選択済み担当も、選択が見えるよう option を補う
    const extraOpt =
      stepUid != null && !stepAssigneePool.some((u) => Number(u.dbId) === stepUid)
        ? `<option value="${stepUid}" selected>${escapeHtml(stepUser ? userNameWithRetiredLabel(stepUser) : `ID:${stepUid}`)}</option>`
        : "";
    // 未選択のフォールバックは現在の工程だけ課題の担当を表示。未到達は担当未定、
    // 通過済みは工程を抜けた時点の担当が step_assignees に固定記録される（未記録の過去データは未定表示）
    const unsetLabel = mod === "current"
      ? (assigneeName ? `${assigneeName}（課題の担当）` : "（課題の担当）")
      : "担当を選択";
    const selOpts =
      `<option value=""${stepUid == null ? " selected" : ""}>${escapeHtml(unsetLabel)}</option>` +
      stepAssigneePool
        .map((u) => `<option value="${Number(u.dbId)}"${stepUid === Number(u.dbId) ? " selected" : ""}>${escapeHtml(u.name || "（無名）")}</option>`)
        .join("") +
      extraOpt;
    const assigneeHtml = `<select class="pm-step-assignee-sel" data-search-dropdown-off="1" data-pm-step-assignee="${aidAttr}:${escapeHtmlAttr(st)}" title="この工程の工数を数える担当" aria-label="「${escapeHtmlAttr(st)}」の工数担当">${selOpts}</select>`;
    // 通過済み・現在の工程は経過時間をクリックして手動修正できる（未到達の工程は記録が無いため対象外）
    const durEditable = mod !== "later";
    const durAttrs = durEditable
      ? ` data-pm-step-dur-edit="${aidAttr}:${escapeHtmlAttr(st)}" role="button" tabindex="0" title="クリックして経過時間を手動修正${hasOverride ? "（手動修正済み）" : ""}"`
      : "";
    // 工数を計測しない工程は経過時間・工数担当を出さない（フローの節としてだけ表示）
    const trackedStep = !trackedList || trackedList.includes(st);
    const assigneePart = trackedStep ? assigneeHtml : "";
    const durPart = trackedStep
      ? `<div class="pm-step-dur${durEditable ? " pm-step-dur--editable" : ""}"${durAttrs}>${durHtml}</div>`
      : "";
    return `<div class="pm-step pm-step--${mod}${rejectedMod}">
        <div class="pm-step-conn"></div>
        ${curLabel}
        <div class="${nodeCls}">${nodeInner}</div>
        <div class="pm-step-role">${escapeHtml(st)}</div>
        ${assigneePart}
        ${durPart}
      </div>`;
  }).join("");
  return `<div class="pm-step-bar-wrap" aria-label="進捗">
      ${progressIssueRejectionBannerHtml(currentStepRejected)}
      <div class="pm-step-bar">${stepsHtml}</div>
      <div class="pm-step-bar-links">
        <button type="button" class="pm-step-history-link" data-pm-stage-history-open="${aidAttr}">工程履歴を見る</button>
      </div>
      ${progressAgendaWorkLogSummaryHtml(ag)}
    </div>`;
}

/** 現在の工程が直前の差し戻しで入った工程の場合、専用の注意バナーを表示する（工程バー内・PC/SP共通）。
 * rejected は progressAgendaCurrentStepIsRejected(ag) の結果を呼び出し側から渡してもらう
 * （progressIssueStepBarHtml が既に計算済みのため、同じ描画内で二重計算しない）。 */
function progressIssueRejectionBannerHtml(rejected) {
  if (!rejected) return "";
  const actor = rejected.actorId != null ? users.find((u) => u.dbId != null && Number(u.dbId) === Number(rejected.actorId)) : null;
  const when = rejected.enteredAt ? new Date(rejected.enteredAt) : null;
  const whenLabel = when ? `${when.getMonth() + 1}/${when.getDate()} ${String(when.getHours()).padStart(2, "0")}:${String(when.getMinutes()).padStart(2, "0")}` : "";
  const headline = rejected.fromStepName
    ? `${escapeHtml(rejected.fromStepName)}から「${escapeHtml(rejected.stepName)}」へ差し戻し`
    : `「${escapeHtml(rejected.stepName)}」へ差し戻し`;
  return `<div class="pm-issue-reject-banner">
      <span class="pm-issue-reject-banner-icon" aria-hidden="true">↩</span>
      <div>
        <div class="pm-issue-reject-banner-headline">${headline}</div>
        ${rejected.rejectionReason ? `<div class="pm-issue-reject-banner-reason">理由：${escapeHtml(rejected.rejectionReason)}</div>` : ""}
        <div class="pm-issue-reject-banner-meta">${[whenLabel, actor ? `${escapeHtml(actor.name || "")}さん` : ""].filter(Boolean).join("　")}</div>
      </div>
    </div>`;
}

/** 課題詳細ヘッダーの属性バーの中身（起票・担当・期限・優先度・カレンダー・クライアント）。
 * 初回描画の progressIssueDetailHeadHtml と、担当・状況などを変えた後のDOM部分更新
 * refreshProgressIssueSummaryDom の**両方**がこれを使う。以前は同じマークアップを2箇所に
 * 書いていたため、片方にだけ項目を足すと「詳細を開いた直後は出るのに、担当を変えた瞬間に消える」
 * ことになっていた。属性バーに項目を足すときはこの関数だけを直す。 */
function progressIssueHeadAttrBarInnerHtml(ag, ch) {
  // 誰が起票したかは担当と並べて見えたほうが分かりやすい（担当の左隣に置く・変更不可）
  const creatorName = progressAgendaCreatorDisplayName(ag);
  const assigneeName = progressIssueAssigneeDisplayName(ag);
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);
  const priVal = normalizeProgressAgendaPriority(ag.priority);
  const priMod = priVal === "最優先" ? "urgent" : priVal === "高" ? "high" : priVal === "低" ? "low" : "mid";
  // クライアントは制作以外のプロジェクトでも登録できる（TASK-373）。設定済みのときだけ表示（未設定の「—」は出さない）。
  const clientDisp = progressIssueClientDisplayName(ag);
  const clientHtml = clientDisp && clientDisp !== "—"
    ? `<span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">クライアント</span><span class="pm-issue-head-attr-val">${escapeHtml(clientDisp)}</span></span>`
    : "";
  return `
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">課題番号</span><span class="pm-issue-head-attr-val">${progressIssueNumberHtml(ag?.id)}</span></span>
      <span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">起票</span><span class="pm-issue-head-attr-val">${escapeHtml(creatorName)}</span></span>
      <span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">担当</span><span class="pm-issue-head-attr-val">${escapeHtml(assigneeName)}</span></span>
      <span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">期限</span><span class="pm-issue-head-attr-val${isProgressAgendaDueOverdue(ag) ? " pm-issue-head-attr-val--overdue" : ""}">${dueDisp ? escapeHtml(dueDisp) : "—"}</span></span>
      <span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr"><span class="pm-issue-head-attr-lbl">優先度</span><span class="pm-issue-head-attr-val pm-issue-sum-pri-box pm-issue-sum-pri-box--${priMod}">${escapeHtml(priVal)}</span></span>
      <span class="pm-issue-head-attr-sep" aria-hidden="true"></span>
      <span class="pm-issue-head-attr">${progressCalendarCreateButtonHtml(ag, ch)}</span>
      ${clientHtml}`;
}

/** 課題詳細: ページ上部の課題名 */
function progressIssueDetailHeadHtml(ag, ch, statusOpt = "") {
  const title = escapeHtml((ag.title && String(ag.title).trim()) || "（無題）");
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  // 登録日時（＝起票の作成タイムスタンプ）を編集不可でタイトル横に出す（TASK-389・時刻まで表示）。
  // createdAt は時刻付き。無い場合のみ日付のみの startDate にフォールバック。
  const regDisp = formatProgressMsgTime(ag.createdAt) || formatProgressAgendaDueDisplay(ag.startDate);
  const regDateHtml = regDisp
    ? `<span class="pm-issue-detail-regdate" title="登録日時（起票）"><span class="pm-issue-detail-regdate-lbl">登録日時</span>${escapeHtml(regDisp)}</span>`
    : "";
  // 最終更新（コメント・属性変更を含む最後の動き）。登録日時と並べて、いつ触られたかを詳細画面で分かるようにする
  const updDisp = formatProgressMsgTime(ag.updatedAt);
  const updDateHtml =
    updDisp && updDisp !== formatProgressMsgTime(ag.createdAt)
      ? `<span class="pm-issue-detail-regdate" title="最終更新（コメント・属性の変更を含む）"><span class="pm-issue-detail-regdate-lbl">最終更新</span>${escapeHtml(updDisp)}</span>`
      : "";
  const attrBar = `<div class="pm-issue-head-attr-bar">${progressIssueHeadAttrBarInnerHtml(ag, ch)}
    </div>`;
  return `<div class="pm-issue-detail-head">
      <div class="pm-issue-detail-title-row">
        <div class="pm-issue-detail-title-lead">
          <h2 class="pm-issue-detail-title">${title}</h2>
          <button type="button" class="pm-issue-copy-titled-btn" data-pm-copy-agenda-titled="${aidAttr}" title="課題キー（${PM_WORD.theme}名＋番号）と件名をコピーします。対応アプリでは課題キーにリンクが付きます。Shiftキーを押しながらクリックするとURLのみコピーします。" aria-label="課題キーと件名をコピー"><span class="pm-issue-copy-titled-btn__ic" aria-hidden="true">${pmBoardIconSvg("link", 16)}</span></button>
          ${regDateHtml}
          ${updDateHtml}
        </div>
        <div class="pm-issue-detail-head-controls">
          ${statusOpt ? `<select class="dform-select pm-detail-status pm-detail-status--head" data-search-dropdown-off="1" data-pm-issue-head-status="${aidAttr}" aria-label="状況">${statusOpt}</select>${fpSaveStatusHtml(`agenda-status:${ag?.id ?? ""}`)}` : ""}
          <div class="pm-issue-detail-head-actions">
            ${progressWatchEyeButtonHtml(ag, "head")}
            ${progressAgendaAttachTriggerHtml(ag)}${progressTodoInvoiceButtonHtml(ag)}
            <button type="button" class="inline-btn ghost pm-issue-status-change-btn" data-pm-issue-status-change="${aidAttr}" title="タイトル・詳細・カテゴリ・優先度などを編集"><span class="pm-issue-status-change-btn__ic" aria-hidden="true">${pmBoardIconSvg("editPen", 16)}</span><span class="pm-issue-status-change-btn__label">課題を編集</span></button>
            <button type="button" class="inline-btn ghost pm-issue-close-btn" data-pm-close-issue-detail="1" aria-label="課題詳細を閉じてボードに戻る">閉じる</button>
          </div>
        </div>
      </div>
      <div class="pm-issue-detail-desc-inline">${progressIssueCollapsibleDetailHtml(ag, attrBar, progressIssueStepBarHtml(ag, ch))}</div>
    </div>`;
}

function progressIssuePanelBarDetailHtml(ag) {
  const bodyBlock = progressIssueBodyInlineHtml(ag);
  if (!bodyBlock) return "";
  return `<div class="pm-issue-bar-detail">${bodyBlock}</div>`;
}

function progressIssuePanelTitleBarHtml(ag) {
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  return `<div class="pm-panel-title-bar" aria-labelledby="pm-h-issue-detail">
    <div class="pm-panel-title-bar-row">
      <div class="pm-panel-title-bar-lead">
        <h3 id="pm-h-issue-detail" class="pm-panel-title pm-panel-title--issue-bar">課題</h3>
        ${progressIssuePanelBarDetailHtml(ag)}
      </div>
      <div class="pm-panel-title-bar-actions">
        ${progressWatchEyeButtonHtml(ag, "head")}
        ${progressAgendaAttachTriggerHtml(ag)}
        <button type="button" class="inline-btn ghost pm-issue-status-change-btn" data-pm-issue-status-change="${aidAttr}" title="カテゴリ・優先度・開始日・詳細を編集"><span class="pm-issue-status-change-btn__ic" aria-hidden="true">${pmBoardIconSvg("editPen", 16)}</span><span class="pm-issue-status-change-btn__label">状況の変更</span></button>
      </div>
    </div>
  </div>`;
}

/** 課題詳細: 上部2列サマリー（ラベル | 値） */
function progressIssueSummaryGridHtml(ag, ch) {
  const priVal = normalizeProgressAgendaPriority(ag.priority);
  const agStatus = ch
    ? normalizeProgressTodoStatusForChallenge(ag.status, ch)
    : normalizeProgressTodoStatus(ag.status);
  const assigneeName = progressIssueAssigneeDisplayName(ag);
  const creatorName = progressAgendaCreatorDisplayName(ag);
  const startDisp = formatProgressAgendaDueDisplay(ag.startDate);
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);
  const dash = "—";
  const catDisp = normalizeProgressAgendaCategory(ag.category);
  const priMod = priVal === "最優先" ? "urgent" : priVal === "高" ? "high" : priVal === "低" ? "low" : "mid";
  const priVisual = `<span class="pm-issue-sum-pri-box pm-issue-sum-pri-box--${priMod}">${escapeHtml(priVal)}</span>`;
  return `<div class="pm-issue-summary" aria-label="課題概要">
    <div class="pm-issue-sum-col">
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">優先度</span><span class="pm-issue-sum-val">${priVisual}</span></div>
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">カテゴリ</span><span class="pm-issue-sum-val pm-issue-sum-val--muted">${escapeHtml(catDisp)}</span></div>
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">状況</span><span class="pm-issue-sum-val">${escapeHtml(agStatus)}</span></div>
    </div>
    <div class="pm-issue-sum-col">
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">登録者</span><span class="pm-issue-sum-val pm-issue-sum-val--muted">${escapeHtml(creatorName)}</span></div>
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">担当者</span><span class="pm-issue-sum-val">${escapeHtml(assigneeName)}</span></div>
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">開始日</span><span class="pm-issue-sum-val">${escapeHtml(startDisp || dash)}</span></div>
      <div class="pm-issue-sum-row"><span class="pm-issue-sum-label">期限日</span><span class="pm-issue-sum-val${isProgressAgendaDueOverdue(ag) ? " pm-issue-sum-val--overdue" : ""}">${dueDisp ? escapeHtml(dueDisp) : dash}</span></div>
    </div>
  </div>`;
}

function progressComposeAttachChipsHtml(agendaId) {
  const st = uiState.progressComposeAttach || { agendaId: "", files: [] };
  if (String(st.agendaId) !== String(agendaId) || !st.files.length) return "";
  return st.files
    .map((f, i) => {
      const fn = escapeHtml(String(f.name || "file"));
      // title は属性値。escapeHtml は " を変換しないので属性用のエスケープを別に使う
      const fnAttr = escapeHtmlAttr(String(f.name || "file"));
      return `<span class="pm-compose-attach-chip"><span class="pm-compose-attach-chip-name" title="${fnAttr}">${fn}</span><button type="button" class="pm-compose-attach-chip-x" data-pm-compose-attach-remove="${i}" aria-label="添付を外す">×</button></span>`;
    })
    .join("");
}

function progressStandardAttachRowsHtml(list, opts) {
  const hideDelete = Boolean(opts && opts.hideDelete);
  const previewable = Boolean(opts && opts.previewable);
  const previewImagesOnly = Boolean(opts && opts.previewImagesOnly);
  return (Array.isArray(list) ? list : [])
    .map((att) => {
      const idAttr = escapeHtmlAttr(String(att.id));
      const fnRaw = String(att.filename || "（無題）").trim() || "（無題）";
      const fn = escapeHtml(fnRaw);
      const when = escapeHtml(formatProgressMsgTime(att.createdAt));
      const sizeRaw = formatProgressAttachmentSize(att.sizeBytes);
      const whoDisp = String(att.createdByName || "").trim();
      const hintParts = [sizeRaw];
      if (whoDisp) hintParts.push(whoDisp);
      const nameTitle = escapeHtmlAttr(hintParts.length ? `${fnRaw}（${hintParts.join(" · ")}）` : fnRaw);
      const delBtn = hideDelete
        ? ""
        : `<button type="button" class="pm-attach-btn pm-attach-btn--del" data-pm-attach-delete="${idAttr}" title="削除" aria-label="削除">削除</button>`;
      const canPreview = previewable && (!previewImagesOnly || progressAttachmentIsImage(att));
      const nameCell = canPreview
        ? `<button type="button" class="pm-attach-name pm-attach-name--preview" data-pm-attach-preview="${idAttr}" title="${nameTitle}">${fn}</button>`
        : `<span class="pm-attach-name" title="${nameTitle}">${fn}</span>`;
      return `<div class="pm-attach-row${canPreview ? " pm-attach-row--previewable" : ""}">
        ${nameCell}
        <span class="pm-attach-date">${when}</span>
        <button type="button" class="pm-attach-btn pm-attach-btn--dl" data-pm-attach-download="${idAttr}" title="ダウンロード" aria-label="ダウンロード">DL</button>${delBtn}
      </div>`;
    })
    .join("");
}

/** コメント本文の表示。ファイルだけで送ったコメント（サーバが本文を「（ファイルのみ）」で保存）の添付が
 *  全部削除されたら、空の吹き出しに「（ファイルのみ）」と出さず「削除したデータ」と見せる（DB の本文はそのまま）。
 *  添付が未読込（attachments が配列でない）・送信中・アップロード中は判断できないので元の本文を出す */
function progressMessageBodyForDisplayHtml(m) {
  const filesGone =
    Array.isArray(m.attachments) &&
    !m.attachments.length &&
    !m.sending &&
    String(uiState.progressAttachmentUploadMessageId || "") !== String(m.id);
  if (filesGone && String(m.body || "").trim() === "（ファイルのみ）") {
    return `<span class="pm-msg-deleted">削除したデータ</span>`;
  }
  return progressMessageBodyDisplayHtml(m.body);
}

function progressMessageFilesBlockHtml(m, opts) {
  const allowUpload = Boolean(opts && opts.allowUpload);
  const list = Array.isArray(m.attachments) ? m.attachments : [];
  const sys = Boolean(m.isSystem);
  const mid = String(m.id);
  const midAttr = escapeHtmlAttr(mid);
  if (sys && !list.length) return "";
  const uploading = String(uiState.progressAttachmentUploadMessageId || "") === mid;
  const rows = progressStandardAttachRowsHtml(list, { hideDelete: sys, previewable: true, previewImagesOnly: true });
  const uploadingEl = uploading ? `<p class="pm-attach-uploading" aria-live="polite">アップロード中…</p>` : "";
  const listEl = list.length ? `<div class="pm-attach-list pm-attach-list--msg">${rows}</div>` : "";
  if (sys) {
    return `<div class="pm-msg-files pm-msg-files--readonly">${listEl}</div>`;
  }
  if (!allowUpload) {
    if (!list.length && !uploading) return "";
    return `<div class="pm-msg-files pm-msg-files--readonly">${uploadingEl}${listEl}</div>`;
  }
  const fid = `pm-msg-file-${mid}`;
  const fidAttr = escapeHtmlAttr(fid);
  return `<div class="pm-msg-files pm-file-drop-target" data-pm-file-drop="msg:${midAttr}">${uploadingEl}${listEl}<div class="pm-msg-attach-actions"><input type="file" id="${fidAttr}" class="pm-attach-file-input pm-attach-file-input--msg-bind" aria-label="このコメントにファイルを添付" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.zip,.html,.htm,.txt,.md" data-pm-msg-attach-upload="${midAttr}" ${uploading ? "disabled" : ""} /><label for="${fidAttr}" class="inline-btn ghost pm-msg-attach-add">ファイルを追加</label></div></div>`;
}

function progressKanbanHtml(challenge, agendas) {
  const ch = challenge && typeof challenge === "object" ? challenge : {};
  const baseStatuses = progressChallengeStatuses(ch);
  // 課題が持つ状況のうち、列定義に無いもの（カスタム状況が ch.statuses に未反映等）も
  // 列として補い、カードが行き場を失って消えるのを防ぐ
  const statuses = [...baseStatuses];
  const orphanStatuses = new Set();
  const seen = new Set(baseStatuses);
  for (const a of agendas) {
    const s = normalizeProgressTodoStatusForChallenge(a.status, ch);
    if (s && !seen.has(s)) { seen.add(s); statuses.push(s); orphanStatuses.add(s); }
  }
  return statuses.map((st) => {
    const isOrphan = orphanStatuses.has(st);
    const inCol = agendas.filter((a) => normalizeProgressTodoStatusForChallenge(a.status, ch) === st);
    const mod = progressKanColModifier(st);
    const stAttr = escapeHtmlAttr(st);
    const cards = inCol.length ? inCol.map((a) => progressKanbanCardHtml(a, st)).join("") : `<p class="pm-empty pm-empty--tight">なし</p>`;
    // オーファン列（テーマ設定に存在しない状況）はドロップ不可にして視覚的に区別
    const orphanAttr = isOrphan ? ' data-pm-kan-orphan="1"' : '';
    const orphanNote = isOrphan ? `<span class="pm-kan-col-orphan-note" title="${PM_WORD.theme}設定の状況リストに無い状況です。カードは他の列へドラッグで移せます">未設定</span>` : "";
    // 完了列の見出しに自動アーカイブの案内
    const headTitle = st === "完了" ? ` title="完了から${PM_DONE_AUTO_ARCHIVE_DAYS}日間更新がない課題は自動的にアーカイブへ移動します"` : "";
    return `<section class="pm-kan-col${isOrphan ? " pm-kan-col--orphan" : ""}" aria-label="${stAttr}">
      <h4 class="pm-kan-col-head pm-kan-col-head--${mod}"${headTitle}>${orphanNote}<span class="pm-kan-col-dot"></span>${escapeHtml(st)} <span class="pm-kan-count">${inCol.length}</span></h4>
      <div class="pm-kan-col-body${isOrphan ? " pm-kan-col-body--no-drop" : ""}"${isOrphan ? "" : ` data-pm-kan-drop-status="${stAttr}"`}${orphanAttr}>${cards}</div>
    </section>`;
  }).join("");
}

/** アーカイブした課題の一覧本体（テーマ設定モーダルの「アーカイブ」タブで使用）。復元は起票者のみ。 */
function progressArchivedAgendaListHtml(agendas) {
  const archived = (Array.isArray(agendas) ? agendas : []).filter((a) => a && a.archivedAt);
  if (!archived.length) {
    return `<p class="pm-archived-agendas-empty">アーカイブした課題はありません。</p>`;
  }
  const myDbId = progressCurrentUserDbId();
  const rows = archived
    .map((a) => {
      const aid = escapeHtmlAttr(String(a.id));
      const title = escapeHtml((a.title && String(a.title).trim()) || "（無題）");
      const isCreator = a.createdByUserId != null && myDbId != null && Number(a.createdByUserId) === myDbId;
      const restoreBtn = isCreator
        ? `<button type="button" class="inline-btn ghost pm-archived-agenda-restore" data-pm-unarchive-agenda="${aid}">復元する</button>`
        : "";
      return `<div class="pm-archived-agenda-row"><span class="pm-archived-agenda-title">${title}</span>${restoreBtn}</div>`;
    })
    .join("");
  return `<div class="pm-archived-agendas-list">${rows}</div>`;
}

/** 課題ボードのみ（#progress-management?project=…）。詳細は課題クリックで ?issue= へ */
function progressManagementBoardHtml(ch, agendas, groupId) {
  const modalOpen = !!uiState.progressShowIssueAddForm;
  const boardAgendas = filterProgressBoardAgendas(agendas);
  const isArchived = Boolean(ch.archivedAt);
  const chId = escapeHtmlAttr(String(ch.id));
  const archivedBanner = isArchived
    ? `<p class="pm-board-archived-note" role="status">この${PM_WORD.theme}はアーカイブされています。<button type="button" class="inline-btn ghost" data-pm-unarchive-challenge="${chId}">復元する</button></p>`
    : "";
  return `
    <div class="page pj-page pm-page pm-page--issue-board">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        ${progressManagementNavBreadcrumb(ch, groupId)}
        <div class="pm-board-head">
          ${archivedBanner}
          <div class="pm-board-head-row">
            <div class="pm-board-head-main">
              <h2 class="pm-board-title">${escapeHtml(ch.title || "（無題）")}</h2>
              ${ch.body ? `<p class="pm-board-desc">${escapeHtml(ch.body).replace(/\n/g, "<br />")}</p>` : ""}
            </div>
            ${
              isArchived
                ? ""
                : `<div class="pm-board-head-actions">
              <div class="pm-board-tools" role="group" aria-label="${PM_WORD.theme}の操作">
              <button type="button" class="pm-board-icon-btn pm-board-icon-btn--wiki" data-pm-open-challenge-wiki="${chId}" title="${PM_WORD.theme}詳細" aria-label="${PM_WORD.theme}詳細"><span class="pm-board-icon-btn__glyph" aria-hidden="true">${pmBoardIconSvg("wiki")}</span></button>
              <button type="button" class="pm-board-icon-btn pm-board-icon-btn--settings" data-pm-open-challenge-settings="${chId}" title="${PM_WORD.theme}設定" aria-label="${PM_WORD.theme}設定"><span class="pm-board-icon-btn__glyph" aria-hidden="true">${pmBoardIconSvg("settings")}</span></button>
              </div>
              ${progressIssueSearchInputHtml()}
              ${progressBoardFilterTogglesHtml()}
              ${progressTaskMdButtonHtml(ch)}
              <button type="button" class="inline-btn" data-pm-toggle-issue-form>${modalOpen ? "追加を閉じる" : "課題を追加"}</button>
            </div>`
            }
          </div>
        </div>
        <div class="pm-kanban-outer pm-panel pm-kanban-outer--solo">
          <h3 class="pm-panel-title pm-kanban-title">課題ボード</h3>
          <div class="pm-kanban-scroll">
            <div class="pm-kanban">${progressKanbanHtml(ch, boardAgendas)}</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/** 課題やりとりの並び（古い順＝最新が末尾。日時不明は id で安定ソート） */
function progressIssueMessagesSortedOldestFirst(messages) {
  const arr = Array.isArray(messages) ? [...messages] : [];
  const ms = (m) => {
    const t = new Date(m?.createdAt || "").getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const idRank = (m) => {
    const n = Number(m?.id);
    return Number.isFinite(n) ? n : 0;
  };
  arr.sort((a, b) => {
    const dm = ms(a) - ms(b);
    if (dm !== 0) return dm;
    return idRank(a) - idRank(b);
  });
  return arr;
}

// ── SP バックログ風UI ヘルパー ──

/** ステータスバッジの色ペアを返す */
function progressAgendaStatusBadgeColor(status) {
  switch (status) {
    case "未対応":   return { bg: "#fee2e2", text: "#991b1b" };
    case "対応中":   return { bg: "#dbeafe", text: "#1e40af" };
    case "完了":     return { bg: "#dcfce7", text: "#166534" };
    case "保留":     return { bg: "#fef9c3", text: "#854d0e" };
    case "対応不要": return { bg: "#f3f4f6", text: "#374151" };
    default:         return { bg: "#ede9fe", text: "#5b21b6" };
  }
}

/** SP 属性行1行分の HTML */
function progressSpAttrRowHtml(label, valueHtml, fieldKey, agendaId, canEdit) {
  const tapAttr = canEdit
    ? ` data-pm-sp-open-attr="${escapeHtmlAttr(fieldKey)}" data-pm-sp-agenda="${escapeHtmlAttr(String(agendaId))}"`
    : "";
  const chevron = canEdit ? '<span class="pm-sp-attr-chevron">›</span>' : "";
  return `<div class="pm-sp-attr-row${canEdit ? " pm-sp-attr-row--tap" : ""}"${tapAttr}>
    <span class="pm-sp-attr-label">${escapeHtml(label)}</span>
    <span class="pm-sp-attr-value">${valueHtml}</span>
    ${chevron}
  </div>`;
}

/** SP 属性カード HTML */
function progressSpAttrCard(ag, ch, isCreator) {
  const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  const priVal = normalizeProgressAgendaPriority(ag.priority);
  const catDisp = normalizeProgressAgendaCategory(ag.category);
  const assigneeName = progressIssueAssigneeDisplayName(ag);
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);

  const stCol = progressAgendaStatusBadgeColor(agStatus);
  const statusBadge = `<span class="pm-sp-badge" style="background:${stCol.bg};color:${stCol.text};">${escapeHtml(agStatus)}</span>`;

  const catCol = progressAgendaStatusBadgeColor("_cat_" + catDisp);
  const catBadge = `<span class="pm-sp-badge" style="background:#f1f5f9;color:#475569;">${escapeHtml(catDisp)}</span>`;

  // 優先度バッジ色
  const priColors = { "最優先": { bg: "#fce7f3", text: "#9d174d" }, "高": { bg: "#fee2e2", text: "#991b1b" }, "中": { bg: "#fef9c3", text: "#854d0e" }, "低": { bg: "#f3f4f6", text: "#374151" } };
  const pCol = priColors[priVal] || { bg: "#ede9fe", text: "#5b21b6" };
  const priBadge = `<span class="pm-sp-badge" style="background:${pCol.bg};color:${pCol.text};">${escapeHtml(priVal)}</span>`;

  const assigneeBadge = `<span class="pm-sp-attr-plain">${escapeHtml(assigneeName)}</span>`;
  // 起票者は担当者のすぐ上に読み取り専用で並べる（PC詳細ヘッダーの「起票」と同じ情報）
  const creatorBadge = `<span class="pm-sp-attr-plain pm-sp-attr-plain--muted">${escapeHtml(progressAgendaCreatorDisplayName(ag))}</span>`;
  const dueBadge = dueDisp
    ? `<span class="pm-sp-attr-plain${isProgressAgendaDueOverdue(ag) ? " pm-sp-attr-plain--overdue" : ""}">${escapeHtml(dueDisp)}</span>`
    : `<span class="pm-sp-attr-plain pm-sp-attr-plain--muted">—</span>`;

  const agendaId = ag.id;
  // 登録日時（＝起票の作成タイムスタンプ・時刻付き）は編集不可の読み取り表示（IMPROVE-380 / TASK-389）
  const startDisp = formatProgressMsgTime(ag.createdAt) || formatProgressAgendaDueDisplay(ag.startDate);
  const startRow = `<div class="pm-sp-attr-row">
    <span class="pm-sp-attr-label">登録日時</span>
    <span class="pm-sp-attr-value"><span class="pm-sp-attr-plain${startDisp ? "" : " pm-sp-attr-plain--muted"}">${escapeHtml(startDisp || "—")}</span></span>
  </div>`;
  // 期限日はネイティブ日付ピッカーで直接編集（行全体に透明な date 入力を重ねてタップで開く）
  const dueVal = ag.dueDate ? String(ag.dueDate).slice(0, 10) : "";
  const dueRow = `<div class="pm-sp-attr-row pm-sp-attr-row--tap pm-sp-attr-row--due">
    <span class="pm-sp-attr-label">期限日</span>
    <span class="pm-sp-attr-value">${dueBadge}</span>
    <span class="pm-sp-attr-chevron">›</span>
    <input type="date" class="pm-sp-due-input" data-pm-sp-due-input data-pm-sp-agenda="${escapeHtmlAttr(String(agendaId))}" value="${escapeHtmlAttr(dueVal)}" aria-label="期限日を変更" />
  </div>`;
  // 状態・担当者はタップで直接変更可能、種別・優先度も変更可
  return `<div class="pm-sp-attr-card">
    ${progressSpAttrRowHtml("種別", catBadge, "category", agendaId, true)}
    ${progressSpAttrRowHtml("状況", statusBadge, "status", agendaId, true)}
    ${progressSpAttrRowHtml("起票者", creatorBadge, "", agendaId, false)}
    ${progressSpAttrRowHtml("担当者", assigneeBadge, "assignee", agendaId, true)}
    ${progressSpAttrRowHtml("優先度", priBadge, "priority", agendaId, true)}
    ${progressSpAttrRowHtml("カレンダー", progressCalendarCreateButtonHtml(ag, ch), "", agendaId, false)}
    ${startRow}
    ${dueRow}
  </div>`;
}

/** SP 編集シート HTML（状態・担当者の一覧からどれを変更するか選ぶ） */
function progressSpEditSheetHtml(ag, ch, isCreator) {
  if (!uiState.pmSpEditSheetOpen) return "";
  const agendaId = String(ag.id);
  const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  const assigneeName = progressIssueAssigneeDisplayName(ag);

  const stCol = progressAgendaStatusBadgeColor(agStatus);
  const statusBadge = `<span class="pm-sp-badge" style="background:${stCol.bg};color:${stCol.text};">${escapeHtml(agStatus)}</span>`;
  const assigneeBadge = `<span class="pm-sp-attr-plain">${escapeHtml(assigneeName)}</span>`;

  // 編集可能な行（状態・担当者）
  const editRows = [
    { label: "状況", valueHtml: statusBadge, field: "status" },
    { label: "担当者", valueHtml: assigneeBadge, field: "assignee" },
  ].map(({ label, valueHtml, field }) => `
    <div class="pm-sp-attr-row pm-sp-attr-row--tap" data-pm-sp-open-attr="${escapeHtmlAttr(field)}" data-pm-sp-agenda="${escapeHtmlAttr(agendaId)}">
      <span class="pm-sp-attr-label">${escapeHtml(label)}</span>
      <span class="pm-sp-attr-value">${valueHtml}</span>
      <span class="pm-sp-attr-chevron">›</span>
    </div>`).join("");

  return `
    <div class="pm-sp-sheet-backdrop" data-pm-sp-edit-close></div>
    <div class="pm-sp-sheet pm-sp-edit-sheet">
      <div class="pm-sp-sheet-head">
        <button type="button" class="pm-sp-sheet-cancel" data-pm-sp-edit-close>キャンセル</button>
        <span class="pm-sp-sheet-title">編集</span>
        <span style="min-width:56px;"></span>
      </div>
      <div class="pm-sp-sheet-body">${editRows}</div>
    </div>`;
}

/** SP 選択ボトムシート HTML */
/**
 * コンポーズフッターのチップ表示のみDOMで更新（render()不要）
 */
function progressRefreshComposeFooterDom(agendaId, ag, ch) {
  const footer = document.querySelector(".pm-sp-compose-footer");
  if (!footer) { render(); return; }
  const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  // 状態ボタン/チップを再生成
  const stPending = uiState.pmSpComposePendingStatus;
  const assPending = uiState.pmSpComposePendingAssignee;
  const stHtml = stPending
    ? (() => { const col = progressAgendaStatusBadgeColor(stPending);
        return `<span class="pm-sp-compose-attr-chip"><button type="button" class="pm-sp-compose-attr-chip-label" data-pm-sp-compose-select="status" data-pm-sp-compose-agenda="${escapeHtmlAttr(agendaId)}"><span class="pm-sp-badge" style="background:${col.bg};color:${col.text};font-size:11px;padding:3px 8px;">${escapeHtml(stPending)}</span></button><button type="button" class="pm-sp-compose-chip-x-btn" data-pm-sp-compose-clear="status" aria-label="状況変更をキャンセル">×</button></span>`; })()
    : `<button type="button" class="pm-sp-compose-attr-btn" data-pm-sp-compose-select="status" data-pm-sp-compose-agenda="${escapeHtmlAttr(agendaId)}" aria-label="状況を変更">状況</button>`;
  const assHtml = assPending
    ? (() => { const u = users.find((x) => String(x.id) === assPending); const name = u?.name || "担当者";
        return `<span class="pm-sp-compose-attr-chip"><button type="button" class="pm-sp-compose-attr-chip-label" data-pm-sp-compose-select="assignee" data-pm-sp-compose-agenda="${escapeHtmlAttr(agendaId)}"><span style="font-size:11px;color:#475569;white-space:nowrap;">${escapeHtml(name)}</span></button><button type="button" class="pm-sp-compose-chip-x-btn" data-pm-sp-compose-clear="assignee" aria-label="担当者変更をキャンセル">×</button></span>`; })()
    : `<button type="button" class="pm-sp-compose-attr-btn" data-pm-sp-compose-select="assignee" data-pm-sp-compose-agenda="${escapeHtmlAttr(agendaId)}" aria-label="担当者を変更">担当者</button>`;
  // 既存の状態/担当者ボタン・チップを差し替え（送信ボタンは残す）
  const sendBtn = footer.querySelector(".pm-sp-compose-send");
  const attachLabel = footer.querySelector(".pm-sp-compose-attach-btn");
  footer.innerHTML = "";
  if (attachLabel) footer.appendChild(attachLabel);
  const tmp = document.createElement("div");
  tmp.innerHTML = stHtml + assHtml;
  while (tmp.firstChild) footer.appendChild(tmp.firstChild);
  if (sendBtn) footer.appendChild(sendBtn);
}

/**
 * 選択シートをDOMに直接挿入する（全ページrender()なしでシートを表示）
 * SP課題詳細ページ内の既存シートを入れ替える
 */
/** SP 属性シートで「今の担当者が候補に無い」ことを表す選択値。このまま保存しても担当者を変えない */
const PM_SP_SHEET_KEEP_ASSIGNEE = "__keep_current_assignee__";

function progressSpInsertSelectionSheet(ag, ch) {
  // 既存シートを削除
  document.querySelectorAll(".pm-sp-sheet-backdrop, .pm-sp-sheet").forEach(el => el.remove());
  const html = progressSpSelectionSheetHtml(ag, ch);
  if (!html) return;
  const container = document.querySelector(".pm-page--issue-sp") || document.querySelector(".pj-page");
  if (!container) { render(); return; }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  while (tmp.firstChild) container.appendChild(tmp.firstChild);
  // 担当者シートは全社ユーザー一覧が必要。SP初回ロードでは users が自分だけのことがあり、
  // その場合だけ /api/users を取得してシートを開き直す（コメントで@しないと担当を変えられない問題の対策）。
  const sheet = uiState.pmSpAttrSheet;
  if (sheet && sheet.field === "assignee" && !sheet._dirLoaded && assignableUsers().length <= 1) {
    sheet._dirLoaded = true;
    void ensureMentionUserDirectoryLoaded().then(() => {
      const s = uiState.pmSpAttrSheet;
      if (s && s.field === "assignee" && String(s.agendaId) === String(ag.id)) {
        progressSpInsertSelectionSheet(ag, ch);
      }
    });
  }
}

function progressSpSelectionSheetHtml(ag, ch) {
  const sheet = uiState.pmSpAttrSheet;
  if (!sheet) return "";

  const field = String(sheet.field || "");
  const selected = String(sheet.selected != null ? sheet.selected : "");

  let titleLabel = "";
  let rows = "";
  let searchHtml = "";

  if (field === "status") {
    titleLabel = "状態";
    const myDbId = progressCurrentUserDbId();
    const isCreator = ag.createdByUserId != null && myDbId != null && Number(ag.createdByUserId) === myDbId;
    // 「完了」は作成者のみ選べるが、既に完了済みの課題では現在値として残す（PC側と挙動を揃える）
    const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
    const statusList = progressChallengeStatuses(ch).filter((s) => s !== "完了" || agStatus === "完了" || isCreator);
    rows = statusList.map((s) => {
      const col = progressAgendaStatusBadgeColor(s);
      const badge = `<span class="pm-sp-badge" style="background:${col.bg};color:${col.text};">${escapeHtml(s)}</span>`;
      const check = selected === s ? '<span class="pm-sp-sel-check">✓</span>' : "";
      return `<div class="pm-sp-sel-row" data-pm-sp-sel-opt="${escapeHtmlAttr(s)}">${badge}${check}</div>`;
    }).join("");

  } else if (field === "category") {
    titleLabel = "種別";
    const cats = progressChallengeCategories(ch);
    rows = cats.map((c) => {
      const badge = `<span class="pm-sp-badge" style="background:#f1f5f9;color:#475569;">${escapeHtml(c)}</span>`;
      const check = selected === c ? '<span class="pm-sp-sel-check">✓</span>' : "";
      return `<div class="pm-sp-sel-row" data-pm-sp-sel-opt="${escapeHtmlAttr(c)}">${badge}${check}</div>`;
    }).join("");

  } else if (field === "assignee") {
    titleLabel = "担当者";
    // 担当者は人数が多いので名前で絞り込めるようにする（TASK-344）。
    // クエリは uiState.pmSpAttrSheet.query に保持し、シートがどの経路で再構築されても
    // 入力値と絞り込み結果を復元する（ユーザー名簿の遅延ロードで再挿入されるため）。
    // autofocus は付けない（SPのタッチでキーボードが即出てシートが隠れるため）
    const filterQuery = String(sheet.query || "");
    const filterNorm = searchableSelectNorm(filterQuery);
    searchHtml = `<div class="pm-sp-sheet-search"><input type="search" class="pj-input pm-sp-sheet-search-input" data-pm-sp-assignee-filter data-ime-search placeholder="名前で検索" aria-label="担当者を名前で検索" autocomplete="off" value="${escapeHtmlAttr(filterQuery)}" /></div>`;
    const pool = progressChallengeAssigneePool(ch);
    // グループ分け（登録者→課題内メンション→…）はPCの optgroup と同じ定義を消費する（担当者設定ミス対策）
    const sheetGroups = progressAssigneeCandidateGroups(pool, ag);
    const userRowHtml = (u) => {
      const uid = String(u.id);
      const initial = escapeHtml((u.name || "？").slice(0, 1));
      const nm = escapeHtml(u.name || "（無名）");
      const dept = userDepartmentName(u);
      const deptHtml = dept ? `<span class="pm-sp-sel-row-muted">（${escapeHtml(dept)}）</span>` : "";
      const check = selected === uid ? '<span class="pm-sp-sel-check">✓</span>' : "";
      const hidden = filterNorm && !searchableSelectNorm(u.name || "").includes(filterNorm) ? ' style="display:none;"' : "";
      return `<div class="pm-sp-sel-row"${hidden} data-pm-sp-sel-opt="${escapeHtmlAttr(uid)}" data-pm-sp-sel-name="${escapeHtmlAttr(u.name || "")}"><span class="pm-sp-assignee-ic">${initial}</span><span>${nm}${deptHtml}</span>${check}</div>`;
    };
    // 先頭に「未設定（担当を外す）」を置く。空値で保存すると担当解除される。絞り込み中も常に表示。
    const noneCheck = selected === "" ? '<span class="pm-sp-sel-check">✓</span>' : "";
    const noneRow = `<div class="pm-sp-sel-row" data-pm-sp-sel-opt=""><span class="pm-sp-assignee-ic pm-sp-assignee-ic--none">—</span><span class="pm-sp-sel-row-muted">未設定（担当を外す）</span>${noneCheck}</div>`;
    // 見出し行は選択不可（data-pm-sp-sel-opt を持たない別クラス）。絞り込み中は非表示
    const groupLabelHtml = (label) =>
      `<div class="pm-sp-sel-group-label"${filterNorm ? ' style="display:none;"' : ""}>${escapeHtml(label)}</div>`;
    rows = noneRow + (sheetGroups.length
      ? sheetGroups.map(([label, users]) => groupLabelHtml(label) + users.map(userRowHtml).join("")).join("")
      : pool.map(userRowHtml).join(""));

  } else if (field === "priority") {
    titleLabel = "優先度";
    // 値の追加・変更は app-src/00-shared-enums.js だけで行う（ここで並べ直すと PC 側の優先度
    // プルダウン（11-cost-form.js）にだけ新しい優先度が出て SP に出ない片側漏れになる）
    const pris = PM_AGENDA_PRIORITIES;
    const priColors = { "最優先": { bg: "#fce7f3", text: "#9d174d" }, "高": { bg: "#fee2e2", text: "#991b1b" }, "中": { bg: "#fef9c3", text: "#854d0e" }, "低": { bg: "#f3f4f6", text: "#374151" } };
    rows = pris.map((p) => {
      const pCol = priColors[p] || { bg: "#ede9fe", text: "#5b21b6" };
      const badge = `<span class="pm-sp-badge" style="background:${pCol.bg};color:${pCol.text};">${escapeHtml(p)}</span>`;
      const check = selected === p ? '<span class="pm-sp-sel-check">✓</span>' : "";
      return `<div class="pm-sp-sel-row" data-pm-sp-sel-opt="${escapeHtmlAttr(p)}">${badge}${check}</div>`;
    }).join("");

  } else if (field === "dueDate") {
    // 期限日はシートではなくデートピッカーで処理 — シートは表示しない
    return "";
  }

  // 担当者シートは検索で行数が変わるたびに高さが content-fit で縮み、
  // position:fixed; bottom:0 のため上端（ヘッダー）が下にずれて見える（TASK-344追補）。
  // 高さを固定して絞り込み中も上端位置を変えない。
  const sheetHeightMod = field === "assignee" ? " pm-sp-sheet--fixed-height" : "";

  return `<div class="pm-sp-sheet-backdrop" data-pm-sp-sheet-backdrop></div>
  <div class="pm-sp-sheet${sheetHeightMod}" role="dialog" aria-modal="true" aria-label="${escapeHtmlAttr(titleLabel)}の選択">
    <div class="pm-sp-sheet-head">
      <button type="button" class="pm-sp-sheet-cancel" data-pm-sp-sel-cancel>キャンセル</button>
      <span class="pm-sp-sheet-title">${escapeHtml(titleLabel)}</span>
      <button type="button" class="pm-sp-sheet-save" data-pm-sp-sel-save>保存</button>
    </div>
    ${searchHtml}
    <div class="pm-sp-sheet-body">${rows}</div>
  </div>`;
}

/* SP 担当者選択シートの名前絞り込み（TASK-344）。
   DOM の行を隠すだけで render() は呼ばない＝IME 変換中の入力にも安全。
   クエリは uiState.pmSpAttrSheet.query にも保存し、シート再構築時の復元に使う。 */
document.addEventListener("input", (e) => {
  const t = e.target instanceof Element ? e.target.closest("[data-pm-sp-assignee-filter]") : null;
  if (!(t instanceof HTMLInputElement)) return;
  const sheetEl = t.closest(".pm-sp-sheet");
  if (!sheetEl) return;
  if (uiState.pmSpAttrSheet) uiState.pmSpAttrSheet.query = t.value;
  const qn = searchableSelectNorm(t.value);
  sheetEl.querySelectorAll(".pm-sp-sel-row").forEach((row) => {
    if (!(row instanceof HTMLElement)) return;
    // 「未設定（担当を外す）」行は常に表示。名前は data 属性で照合（頭文字・✓の混入を避ける）
    const isNone = row.getAttribute("data-pm-sp-sel-opt") === "";
    const hit = !qn || isNone || searchableSelectNorm(row.getAttribute("data-pm-sp-sel-name") || "").includes(qn);
    row.style.display = hit ? "" : "none";
  });
  // グループ見出し行は絞り込み中は隠す（ヒット行がグループをまたいで混ざるため）
  sheetEl.querySelectorAll(".pm-sp-sel-group-label").forEach((label) => {
    if (label instanceof HTMLElement) label.style.display = qn ? "none" : "";
  });
});

/** SP コメントセクション HTML */
function progressSpCommentSectionHtml(ag, msgs, expanded) {
  const total = msgs.length;
  let displayMsgs;
  let moreBtn = "";
  if (total > 3 && !expanded) {
    displayMsgs = msgs.slice(-3);
    moreBtn = `<button type="button" class="pm-sp-comments-more" data-pm-sp-expand-comments>コメントをもっと見る（全${total}件）</button>`;
  } else {
    displayMsgs = msgs;
  }
  const threadInner = displayMsgs.length
    ? displayMsgs.map((m) => progressSpMessageRowHtml(m)).join("")
    : `<p class="pm-sp-no-comments">まだやりとりはありません</p>`;

  const countBadge = total > 0 ? `<span class="pm-sp-comments-count">${total}</span>` : "";

  return `<div class="pm-sp-comments-section">
    <div class="pm-sp-comments-head">コメント${countBadge}</div>
    ${moreBtn}
    <div class="pm-sp-thread">${threadInner}</div>
  </div>`;
}

/** SP用シンプルなメッセージ行（添付崩れ対策・PC用の複雑なレイアウトを使わない） */
function progressSpMessageRowHtml(m) {
  const sys = Boolean(m.isSystem);
  const mine = progressMessageIsAuthor(m);
  const editing = !sys && mine && String(uiState.progressEditingMessageId) === String(m.id);
  const authorU = progressMessageAuthorUser(m);
  const authorName = authorU ? userNameWithRetiredLabel(authorU) : String(m.userName || "—").trim();
  const timeStr = escapeHtml(m.sending ? "送信中..." : formatProgressMsgTime(m.createdAt));
  const bodyHtml = progressMessageBodyForDisplayHtml(m);

  if (sys) {
    return `<div class="pm-sp-msg pm-sp-msg--sys">
      <span class="pm-sp-msg-sys-dot" aria-hidden="true">●</span>
      <span class="pm-sp-msg-sys-body">${bodyHtml}</span>
    </div>`;
  }

  const avatarEl = progressMessageAvatarHtml(m, authorU);
  const attachList = Array.isArray(m.attachments) && m.attachments.length
    ? `<div class="pm-sp-msg-files">${m.attachments.map((att) => {
        const fn = escapeHtml(String(att.filename || "ファイル"));
        const idAttr = escapeHtmlAttr(String(att.id));
        const isImg = progressAttachmentIsImage(att);
        const icon = isImg ? "🖼" : progressAttachmentPreviewIcon(att);
        return `<button type="button" class="pm-sp-msg-file-chip" data-pm-attach-preview="${idAttr}" title="${escapeHtmlAttr(String(att.filename || ""))}"><span aria-hidden="true">${icon}</span> ${fn}</button>`;
      }).join("")}</div>`
    : "";

  const editBtn = (mine && !editing && !m.sending)
    ? `<button type="button" class="pm-sp-msg-edit-btn" data-pm-edit-message="${escapeHtmlAttr(String(m.id))}">編集</button>`
    : "";
  const delBtn = (mine && !editing && !m.sending)
    ? `<button type="button" class="pm-sp-msg-edit-btn pm-sp-msg-del-btn" data-pm-delete-message="${escapeHtmlAttr(String(m.id))}">削除</button>`
    : "";
  const bodyBlock = editing
    ? `<div class="pm-sp-msg-edit" data-pm-format-scope>
         ${progressComposeFormatToolbarHtml()}
         <textarea class="pj-input pm-sp-msg-edit-textarea" data-pm-edit-message-body data-pm-mention-textarea="1" rows="6">${escapeHtml(m.body || "")}</textarea>
         <div class="pm-sp-msg-edit-actions">
           <button type="button" class="inline-btn ghost" data-pm-cancel-edit-message>キャンセル</button>
           <button type="button" class="inline-btn" data-pm-save-edit-message="${escapeHtmlAttr(String(m.id))}">保存</button>
         </div>
       </div>`
    : `<div class="pm-sp-msg-body">${bodyHtml}</div>`;

  return `<div class="pm-sp-msg${editing ? " pm-sp-msg--editing" : ""}">
    <div class="pm-sp-msg-avatar">${avatarEl}</div>
    <div class="pm-sp-msg-main">
      <div class="pm-sp-msg-header">
        <span class="pm-sp-msg-name">${escapeHtml(authorName)}</span>
        <span class="pm-sp-msg-time">${timeStr}</span>
        ${progressMentionReadAvatarsHtml(m)}
        ${(editBtn || delBtn) ? `<span class="pm-sp-msg-actions">${editBtn}${delBtn}</span>` : ""}
      </div>
      ${bodyBlock}
      ${attachList}
      ${progressMessageReactionsHtml(m)}
    </div>
  </div>`;
}

/** 課題コメント欄（状況/担当者/期限日を同時変更できる大型コンポーズ）。
 * 課題詳細ページ（プロジェクト経由）と担当課題トークビューの両方で使う共有部品。
 * threadPending 中は入力自体を無効化した読み込み中表示にする。 */
function progressIssueComposeBoxHtml(ch, ag) {
  const selAid = String(ag.id);
  if (String(uiState.progressComposeAttach.agendaId || "") !== selAid) {
    uiState.progressComposeAttach = { agendaId: selAid, files: [] };
  }
  const threadPending = Boolean(ag.threadDeferred) || Boolean(uiState.progressMgmtDetailLoading);
  if (threadPending) {
    return `<div class="pm-compose pm-compose--pending" aria-disabled="true">${flowPilotNowLoadingBlock({ inline: true, subtitle: "やりとりの読み込みが完了するまでお待ちください" })}</div>`;
  }
  // コンポーズ欄の担当者セレクト用：selected を付けず常に「変更しない」がデフォルト
  const dbAssignee =
    ag.assignedUserId != null && Number.isFinite(Number(ag.assignedUserId)) ? Number(ag.assignedUserId) : null;
  const selAssigneeClientId =
    dbAssignee == null
      ? ""
      : (() => {
          const row = users.find((u) => u.dbId != null && Number(u.dbId) === dbAssignee);
          return row ? String(row.id) : "";
        })();
  const assigneePool = progressChallengeAssigneePool(ch);
  const assigneePoolIds = new Set(assigneePool.map((u) => String(u.id)));
  const assigneeExtra =
    selAssigneeClientId && !assigneePoolIds.has(selAssigneeClientId)
      ? users.find((u) => String(u.id) === selAssigneeClientId)
      : null;
  // 「完了」への変更は作成者のみ許可 — 作成者以外のセレクトから「完了」を除外
  const myDbId = progressCurrentUserDbId();
  const isCreator = ag.createdByUserId != null && myDbId != null && Number(ag.createdByUserId) === myDbId;
  const composeAssigneeOpts =
    progressAssigneeGroupedOptionsHtml(assigneePool, ag, (u) =>
      `<option value="${escapeHtmlAttr(String(u.id))}">${escapeHtml(userNameWithDept(u))}</option>`
    ) +
    (assigneeExtra
      ? `<option value="${escapeHtmlAttr(String(assigneeExtra.id))}">${escapeHtml(userNameWithRetiredLabel(assigneeExtra))}</option>`
      : "");
  // コンポーズ欄の状況セレクト用（「完了」は作成者のみ表示）
  const composeStatusList = progressChallengeStatuses(ch).filter((s) => s !== "完了" || isCreator);
  const composeStatusOpts = composeStatusList.map(s => `<option value="${escapeHtmlAttr(s)}">${escapeHtml(s)}</option>`).join("");

  const composeChips = progressComposeAttachChipsHtml(ag.id);
  const composeFileId = `pm-compose-file-${selAid}`;
  const composeFileIdAttr = escapeHtmlAttr(composeFileId);
  const composeDueVal = escapeHtmlAttr(String(ag.dueDate || "").slice(0, 10));
  return `<div class="pm-compose pm-compose--backlog pm-compose--collapsible pm-file-drop-target" data-pm-file-drop="compose:${escapeHtmlAttr(selAid)}">
          <input type="file" id="${composeFileIdAttr}" class="pm-attach-file-input pm-attach-file-input--compose" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.zip,.html,.htm,.txt,.md" data-pm-compose-attach-file aria-label="画像・ファイルを添付" />
          <div class="pm-compose-trigger-bar">
            <label for="${composeFileIdAttr}" class="pm-compose-trigger-attach" title="ファイルを添付"><span class="pm-compose-tool-ic" aria-hidden="true">${pmBoardIconSvg("attach", 16)}</span></label>
            <div class="pm-compose-trigger-field" data-pm-compose-expand role="button" tabindex="0" aria-label="コメントを入力">コメント（@を入力してメンバーに通知）</div>
            <button type="button" class="inline-btn ghost pm-compose-trigger-status-btn" data-pm-issue-status-change="${escapeHtmlAttr(selAid)}" aria-label="状況の変更"><span class="pm-issue-status-change-btn__ic" aria-hidden="true">${pmBoardIconSvg("editPen", 14)}</span><span class="pm-issue-status-change-btn__label">状況の変更</span></button>
          </div>
          <div class="pm-compose-expandable-body">
            <div class="pm-compose-expand-header">
              <span class="pm-compose-expand-label">コメントを追加</span>
              <button type="button" class="pm-compose-collapse-btn" data-pm-compose-collapse aria-label="コンポーズを閉じる">×</button>
            </div>
            <div class="pm-compose-split">
              <div class="pm-compose-col-left">
                ${composeChips ? `<div class="pm-compose-attach-chips" aria-label="送信予定の添付">${composeChips}</div>` : ""}
                <div class="pm-compose-toolbar">
                  <button type="button" class="pm-compose-tool-btn" title="太字" data-pm-compose-format="bold">B</button>
                  <button type="button" class="pm-compose-tool-btn pm-compose-tool-italic" title="斜体" data-pm-compose-format="italic"><i>/</i></button>
                  <button type="button" class="pm-compose-tool-btn pm-compose-tool-strike" title="打ち消し線" data-pm-compose-format="strike"><s>S</s></button>
                  <button type="button" class="pm-compose-tool-btn pm-compose-tool-quote" title="引用" data-pm-compose-format="quote" aria-label="引用"><span class="pm-compose-tool-ic" aria-hidden="true">${pmBoardIconSvg("quote", 14)}</span></button>
                  <button type="button" class="pm-compose-tool-btn pm-compose-tool-code" title="コードブロック" data-pm-compose-format="code">&lt;/&gt;</button>
                  <label for="${composeFileIdAttr}" class="pm-compose-tool-btn" title="ファイルを添付"><span class="pm-compose-tool-ic" aria-hidden="true">${pmBoardIconSvg("attach", 16)}</span></label>
                </div>
                <textarea class="pj-input pm-textarea pm-compose-input" data-pm-msg-body data-pm-mention-textarea="1" rows="6" placeholder="コメントを入力...（@ でメンション）&#10;画像はCtrl+V（Mac: Cmd+V）で貼り付け可能"></textarea>
                <label class="pm-compose-notify-label"><span class="pm-compose-notify-label-text">お知らせするユーザー（任意）</span>
                  <textarea class="pj-input pm-textarea pm-compose-notify-users" data-pm-compose-notify-users data-pm-mention-textarea="1" rows="2" autocomplete="off" placeholder="@で選択（複数可）。本文には表示されず、既読を確認できます" maxlength="500"></textarea>
                </label>
              </div>
              <div class="pm-compose-col-right">
                <div class="pm-compose-attr-head">
                  <span class="pm-compose-attr-title">投稿と同時に変更</span>
                  <span class="pm-compose-attr-note">変更しない項目は現在のままです</span>
                </div>
                <label class="pm-compose-attr-field">
                  <span class="pm-compose-attr-lbl">状況</span>
                  <select class="pj-input dform-select" data-pm-compose-status aria-label="コメント登録と同時に状況を変更">
                    <option value="" selected>変更しない</option>
                    ${composeStatusOpts}
                  </select>
                </label>
                <label class="pm-compose-attr-field">
                  <span class="pm-compose-attr-lbl">担当者</span>
                  <select class="pj-input dform-select pm-compose-assignee-sel" id="pm-compose-assignee-${escapeHtmlAttr(selAid)}" data-pm-compose-assignee aria-label="コメント登録と同時に担当者を変更">
                    <option value="" selected>変更しない</option>
                    ${composeAssigneeOpts}
                  </select>
                </label>
                <label class="pm-compose-attr-field">
                  <span class="pm-compose-attr-lbl">期限日</span>
                  <input type="date" class="pj-input pm-issue-date-input" data-pm-compose-due data-show-picker="1" value="${composeDueVal}" aria-label="期限日を変更" />
                </label>
                <!-- 開始日（＝登録日）は編集不可のため入力欄を撤去（IMPROVE-380）。表示はカードのタイトル横（TASK-389）。 -->
                <!-- 閉じる/登録は右カラム内に置く。split の下の独立行にすると、その1行分だけ
                     コンポーズ全体が右カラムより高くなる（トークで会話が隠れる要因。2026-08-26） -->
                <div class="pm-compose-footer-row">
                  <div class="pm-compose-footer-actions">
                    <button type="button" class="inline-btn ghost" data-pm-compose-collapse>閉じる</button>
                    <button type="button" class="inline-btn pm-compose-submit" data-pm-send-message="${escapeHtmlAttr(selAid)}">登録</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>`;
}

/** 課題のやりとり画面のみ（#progress-management?project=…&issue=…） */
function progressManagementIssueDetailHtml(ch, ag, groupId) {
  const selAid = String(ag.id);
  const pid = escapeHtmlAttr(String(ch.id));
  const agendaIdAttr = escapeHtmlAttr(String(ag.id));
  const msgs = progressIssueMessagesSortedOldestFirst(ag.messages);
  // progressComposeAttach のリセットは共有部品 progressIssueComposeBoxHtml 側で行う（重複させない）
  if (uiState.progressEditingMessageId) {
    const mid = String(uiState.progressEditingMessageId);
    if (!msgs.some((m) => String(m.id) === mid)) uiState.progressEditingMessageId = "";
  }
  const agStatus = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  const statusList = progressChallengeStatuses(ch);
  // 「完了」への変更は作成者のみ許可 — 作成者以外のセレクトから「完了」を除外
  const myDbId = progressCurrentUserDbId();
  const isCreator = ag.createdByUserId != null && myDbId != null && Number(ag.createdByUserId) === myDbId;
  const visibleStatusList = statusList.filter((s) => s !== "完了" || agStatus === "完了" || isCreator);
  const statusOpt = visibleStatusList
    .map((s) => `<option value="${escapeHtmlAttr(s)}"${agStatus === s ? " selected" : ""}>${escapeHtml(s)}</option>`)
    .join("");
  const dueVal = escapeHtmlAttr(ag.dueDate ? String(ag.dueDate).slice(0, 10) : "");
  const completedNote = ag.completedAt
    ? `<p class="pm-issue-completed-note" role="note">対応終了日: ${escapeHtml(String(ag.completedAt))}</p>`
    : "";
  // グループ/テーマ名
  const gidRaw =
    groupId != null && String(groupId).trim() !== ""
      ? String(groupId).trim()
      : ch?.todoGroupId != null
        ? String(ch.todoGroupId)
        : "";
  const grp = gidRaw ? findProgressTodoGroup(gidRaw) : null;
  const groupLabel = (grp?.title && String(grp.title).trim()) || "";
  const themeLabel = (ch?.title && String(ch.title).trim()) || "（無題）";

  const threadPending = Boolean(ag.threadDeferred) || Boolean(uiState.progressMgmtDetailLoading);
  let threadMsgs;
  if (threadPending) {
    threadMsgs = `<div class="pm-thread-load-msg pm-thread-load-msg--cat">${flowPilotNowLoadingBlock({ inline: true, subtitle: "やりとりを読み込んでいます" })}</div>`;
  } else if (msgs.length === 0) {
    threadMsgs = "";
  } else {
    threadMsgs = msgs.map((m) => progressIssueMessageRowHtml(m)).join("");
  }
  const composeBlock = progressIssueComposeBoxHtml(ch, ag);
  // ── SP バックログ風レイアウト ──
  const _isSpView = (
    window.matchMedia?.("(pointer: coarse)")?.matches ||
    window.matchMedia?.("(max-width: 640px)")?.matches ||
    (typeof window !== "undefined" && window.innerWidth <= 640)
  );
  if (_isSpView) {
    // コメント入力シート・送信時に変える状況/担当者・コメント全件表示は、表示中の課題のもの。
    // 別の課題へ移ったら持ち越さない（以前は課題Aで付けた「完了」が、戻る操作や通知で開いた課題Bの送信に適用された）
    if (String(uiState.pmSpComposeAgendaId ?? "") !== String(ag.id)) {
      uiState.pmSpComposeAgendaId = ag.id;
      uiState.pmSpComposeOpen = false;
      uiState.pmSpComposePendingStatus = null;
      uiState.pmSpComposePendingAssignee = null;
      uiState.pmSpCommentsExpanded = false;
    }
    const spExpanded = Boolean(uiState.pmSpCommentsExpanded);
    const spComposeOpen = Boolean(uiState.pmSpComposeOpen);
    // シートの選択肢が agenda と一致しているかをチェックしてリセット
    if (uiState.pmSpAttrSheet && String(uiState.pmSpAttrSheet.agendaId) !== String(ag.id)) {
      uiState.pmSpAttrSheet = null;
    }
    const sheetHtml = progressSpSelectionSheetHtml(ag, ch);
    const spMsgs = threadPending ? [] : msgs;

    // SP専用コメント入力シート（シンプル：テキスト＋添付＋送信のみ）
    const spComposeChips = progressComposeAttachChipsHtml(selAid);
    const spComposeFileId = `pm-sp-compose-file-${escapeHtmlAttr(selAid)}`;
    const composeSheetHtml = spComposeOpen ? `
      <div class="pm-sp-compose-backdrop" data-pm-sp-compose-close></div>
      <div class="pm-sp-compose-sheet">
        <div class="pm-sp-compose-sheet-head">
          <button type="button" class="pm-sp-compose-cancel" data-pm-sp-compose-close>キャンセル</button>
          <span class="pm-sp-compose-sheet-title">コメント</span>
        </div>
        <div class="pm-sp-compose-sheet-body pm-file-drop-target" data-pm-file-drop="compose:${escapeHtmlAttr(selAid)}">
          <input type="file" id="${spComposeFileId}" class="pm-attach-file-input pm-attach-file-input--compose" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.zip,.html,.htm,.txt,.md" data-pm-compose-attach-file aria-label="ファイルを添付" style="display:none;" />
          ${spComposeChips ? `<div class="pm-compose-attach-chips pm-sp-compose-chips">${spComposeChips}</div>` : ""}
          <div class="pm-compose-toolbar pm-sp-compose-toolbar">
            <button type="button" class="pm-compose-tool-btn" title="太字" data-pm-compose-format="bold">B</button>
            <button type="button" class="pm-compose-tool-btn pm-compose-tool-italic" title="斜体" data-pm-compose-format="italic"><i>/</i></button>
            <button type="button" class="pm-compose-tool-btn pm-compose-tool-strike" title="打ち消し線" data-pm-compose-format="strike"><s>S</s></button>
            <button type="button" class="pm-compose-tool-btn pm-compose-tool-quote" title="引用" data-pm-compose-format="quote" aria-label="引用"><span class="pm-compose-tool-ic" aria-hidden="true">${pmBoardIconSvg("quote", 14)}</span></button>
            <button type="button" class="pm-compose-tool-btn pm-compose-tool-code" title="コードブロック" data-pm-compose-format="code">&lt;/&gt;</button>
          </div>
          <textarea class="pm-sp-compose-textarea" data-pm-msg-body data-pm-mention-textarea="1" placeholder="コメントを入力...（@ でメンション）" rows="4"></textarea>
        </div>
        <div class="pm-sp-compose-footer">
          <label class="pm-sp-compose-attach-btn" for="${spComposeFileId}" title="ファイルを添付" aria-label="ファイルを添付">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
          </label>
          ${(() => {
            // 状態ボタン（選択済みなら色付きチップ＋×、未選択ならグレーボタン）
            const stPending = uiState.pmSpComposePendingStatus;
            const stBtn = stPending
              ? (() => { const col = progressAgendaStatusBadgeColor(stPending);
                  return `<span class="pm-sp-compose-attr-chip">
                    <button type="button" class="pm-sp-compose-attr-chip-label" data-pm-sp-compose-select="status" data-pm-sp-compose-agenda="${escapeHtmlAttr(selAid)}">
                      <span class="pm-sp-badge" style="background:${col.bg};color:${col.text};font-size:11px;padding:3px 8px;">${escapeHtml(stPending)}</span>
                    </button>
                    <button type="button" class="pm-sp-compose-chip-x-btn" data-pm-sp-compose-clear="status" aria-label="状況変更をキャンセル">×</button>
                  </span>`; })()
              : `<button type="button" class="pm-sp-compose-attr-btn" data-pm-sp-compose-select="status" data-pm-sp-compose-agenda="${escapeHtmlAttr(selAid)}" aria-label="状況を変更">状況</button>`;
            // 担当者ボタン（選択済みなら名前チップ＋×、未選択ならグレーボタン）
            const assPending = uiState.pmSpComposePendingAssignee;
            const assBtn = assPending
              ? (() => { const u = users.find((x) => String(x.id) === assPending); const name = u?.name || "担当者";
                  return `<span class="pm-sp-compose-attr-chip">
                    <button type="button" class="pm-sp-compose-attr-chip-label" data-pm-sp-compose-select="assignee" data-pm-sp-compose-agenda="${escapeHtmlAttr(selAid)}">
                      <span style="font-size:11px;color:#475569;white-space:nowrap;">${escapeHtml(name)}</span>
                    </button>
                    <button type="button" class="pm-sp-compose-chip-x-btn" data-pm-sp-compose-clear="assignee" aria-label="担当者変更をキャンセル">×</button>
                  </span>`; })()
              : `<button type="button" class="pm-sp-compose-attr-btn" data-pm-sp-compose-select="assignee" data-pm-sp-compose-agenda="${escapeHtmlAttr(selAid)}" aria-label="担当者を変更">担当者</button>`;
            return stBtn + assBtn;
          })()}
          <button type="button" class="pm-sp-compose-send" data-pm-send-message="${escapeHtmlAttr(selAid)}" aria-label="送信">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>` : "";


    // 「コメントを追加」トリガー（鉛筆なし・シンプル）
    const composeTrigger = !spComposeOpen ? `
      <div class="pm-sp-compose-trigger" data-pm-sp-compose-open>
        <span class="pm-sp-compose-trigger-placeholder">コメントを追加...</span>
      </div>` : "";

    return `
    <div class="page pj-page pm-page pm-page--issue-detail pm-page--issue-sp">
      <div class="pm-sp-issue-wrap">
        ${progressManagementNavBreadcrumb(ch, groupId, { issueTitle: ag.title })}
        <div class="pm-sp-issue-title">${escapeHtml(ag.title || "（無題）")}</div>
        ${progressIssueCollapsibleDetailHtml(ag, progressSpAttrCard(ag, ch, isCreator), progressIssueStepBarHtml(ag, ch))}
        ${progressSpCommentSectionHtml(ag, spMsgs, spExpanded)}
        ${composeTrigger}
      </div>
      ${composeSheetHtml}
      ${sheetHtml}
    </div>
  `;
  }

  // 左カラム: やりとりをメイン
  const leftCol = `<div class="pm-issue-col-left pm-issue-col-left--thread">
        <div class="pm-issue-thread-stack">
          <div class="pm-thread pm-thread--issue-detail" role="log" aria-live="polite">${threadMsgs}</div>
          ${composeBlock}
        </div>
      </div>`;
  const detailHtml = `<div class="pm-panel-body pm-thread-wrap pm-backlog-detail pm-issue-detail-body">
      <div class="pm-issue-split">${leftCol}</div>
    </div>`;
  return `
    <div class="page pj-page pm-page pm-page--issue-detail">
      <div class="pj-page-wrap">
        ${spDesktopRecommendedNoticeHtml()}
        ${progressManagementNavBreadcrumb(ch, groupId, { issueTitle: ag.title })}
        ${progressIssueDetailHeadHtml(ag, ch, statusOpt)}
        <section class="pm-panel pm-panel--detail pm-panel--issue-full" aria-label="課題詳細">
          ${detailHtml}
        </section>
      </div>
    </div>
  `;
}

/** ToDo管理: 課題ボード or 課題詳細（?issue= で分岐） */
function progressManagementProjectDetailHtml(ch, agendas, ag, groupId) {
  if (ag) return progressManagementIssueDetailHtml(ch, ag, groupId);
  return progressManagementBoardHtml(ch, agendas, groupId);
}

/** ToDo管理: テーマ → 課題（URL: #progress-management?project=&issue=） */
function progressManagementPage() {
  const route = currentRoute();
  const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
  const groupParam = String(route.searchParams?.get("group") || "").trim();
  const projectParam = String(route.searchParams?.get("project") || "").trim();
  const assignedFlat = !projectParam && !groupParam && progressManagementAssignedMeRouteActive(route);
  if (projectParam) {
    uiState.progressShowProjectForm = false;
    uiState.progressChallengeCreateDraft = { title: "", body: "" };
  }
  const issueParam = String(route.searchParams?.get("issue") || "").trim();
  // project= の無いルートでは編集モーダルを開きっぱなしにしない、が既定の挙動
  // （別ページで開いたモーダルを持ち越さないためのガード）。
  // 例外は担当課題トークビューの「課題の詳細」だけ：project= 無しのまま編集モーダルを開くので、
  // 閉じると開いた直後の再描画で即座に閉じてしまう。ただし「今どの課題を編集中か」と
  // 「今URLのissue=で選択している課題」が一致するときだけに限定する。単に assignedFlat 中に
  // 開いていたというだけで残すと、他ページ（案件ボード等）で開いた別課題の編集モーダルを
  // サイドバーナビ等で assigned=me へ遷移したときに閉じ忘れて持ち越してしまう。
  const keepEditModalOpenForTalk =
    assignedFlat && isProgressAgendaEditModal() && issueParam && progressAgendaEditAgendaId() === issueParam;
  if (!projectParam && !keepEditModalOpenForTalk) {
    closeProgressAgendaFormModal();
  }
  if (assignedFlat) {
    if (progressAssignedTalkActive(route)) {
      return progressAssignedTalkViewHtml(issueParam);
    }
    return progressManagementMyAssignmentsListHtml();
  }
  const ch =
    projectParam && chs.some((c) => String(c.id) === projectParam)
      ? chs.find((c) => String(c.id) === projectParam) || null
      : null;
  if (ch) {
    const agendas = Array.isArray(ch.agendas) ? ch.agendas : [];
    let ag = null;
    if (issueParam && agendas.some((a) => String(a.id) === issueParam && !a.archivedAt)) {
      ag = agendas.find((a) => String(a.id) === issueParam && !a.archivedAt) || null;
    }
    // 対象の課題がまだ手元に無く取得中（ベル通知などからの遷移直後）は、
    // 誤ったフォーマットの空ボードを一瞬出さずローディングを表示する
    // （アーカイブ済み課題は手元に「ある」のでここを通らず、従来どおりボードへ）
    if (
      issueParam &&
      !agendas.some((a) => String(a.id) === issueParam) &&
      (uiState.progressMgmtFetchInFlight || uiState.progressMgmtLoading || uiState.progressMgmtDetailLoading)
    ) {
      return progressManagementLoadingHtml(`${PM_WORD.issue}を読み込み中…`);
    }
    uiState.progressSelectedTodoGroupId = String(ch.todoGroupId || groupParam || "");
    uiState.progressSelectedChallengeId = String(ch.id);
    uiState.progressSelectedAgendaId = ag ? String(ag.id) : "";
    return progressManagementProjectDetailHtml(ch, agendas, ag, groupParam || ch.todoGroupId);
  }
  if (projectParam && (uiState.progressMgmtLoading || uiState.progressMgmtFetchInFlight)) {
    return progressManagementLoadingHtml(`${PM_WORD.theme}・${PM_WORD.issue}を読み込み中…`);
  }
  // フォルダ1段化: group 層は廃止。?group= は無視してフラットなプロジェクト一覧へ。
  if (normalizeProgressGroupListMode(uiState.progressGroupListMode) === "my_themes") {
    return progressManagementMyThemesHtml();
  }
  if (uiState.progressMgmtLoading && (uiState.progressChallenges || []).length === 0) {
    return progressManagementLoadingHtml(`${PM_WORD.theme}一覧を読み込み中…`);
  }
  return progressManagementProjectListHtml();
}

function formatProgressMsgTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return String(iso);
  }
}

/** SP向け: PC推奨バナーは出さない（PWA・スマホ完結） */
function spDesktopRecommendedNoticeHtml() {
  return "";
}

function syncBudgetProgressUiState(patch) {
  if (patch && typeof patch === "object" && typeof patch.selectedBudgetPeriodId === "string") {
    uiState.selectedBudgetPeriodId = patch.selectedBudgetPeriodId;
  }
  Object.assign(uiState.budgetProgress, patch);
}

/** #budget-progress・#management は同じ budget-progress.js（#bp-mainTabs 等）を共有するため、
 *  そのタブ切替・期選択のクリック/変更ハンドラは両ルートで動く必要がある */
function isBudgetProgressSharedRoute() {
  const page = currentRoute().page;
  return page === "budget-progress" || page === "management";
}

/** mode: "management"（経営ダッシュボード＝推移/原価/月次P/L/予算設定・マスター限定）
 *      | "budget-progress"（予算進捗＝全体/部署タブ（動的）/個人別・営業メニュー）
 *  タブごとにHTML生成関数を呼び分けており、他モードのタブ用コンテナ自体をDOMに出さない
 *  （bp-hidden で隠すだけだと、非表示タブの中身が確定情報としてDOMに残って閲覧可能なロールに漏れるため） */
function budgetProgressPage(mode) {
  const isManagement = mode === "management";
  const budgetRowsLoading = ensureSelectedBudgetRowsRequested();
  const selectedPeriod =
    uiState.budgetPeriods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(uiState.budgetPeriods);
  const budgetRowsFailed = selectedPeriod?.id ? budgetRowsLoadFailedForPeriod(selectedPeriod.id) : false;
  const tools = `
    <div class="bp-head-tools">
      <div class="bp-legend" aria-label="進捗率の色凡例">
        <div class="bp-legend-item"><span class="bp-legend-dot" style="background:#c0392b"></span><span>50%未満</span></div>
        <div class="bp-legend-item"><span class="bp-legend-dot" style="background:#1d6840"></span><span>50〜80%</span></div>
        <div class="bp-legend-item"><span class="bp-legend-dot" style="background:#1a5fa8"></span><span>80%以上</span></div>
      </div>
      <div class="bp-period-field">
        <label class="bp-period-field-label" for="bp-periodSelect">期を選択</label>
        <select id="bp-periodSelect" class="select bp-period-select" data-search-dropdown-off="1" aria-label="会計期"></select>
      </div>
    </div>
  `;
  const views = isManagement
    ? `<div class="tabs-row" id="bp-mainTabs"></div>
       <div id="bp-viewTrend" class="bp-hidden">${companyMonthlyProgressSectionHtml()}${dashboardRankingsSectionHtml()}</div>
       <div id="bp-viewCost" class="bp-hidden">${costCategoryTrendSectionHtml()}</div>
       <div id="bp-viewPl" class="bp-hidden">${monthlyPlSectionsHtml()}</div>
       <div id="bp-viewContrib" class="bp-hidden">${contributionSectionHtml()}</div>
       <div id="bp-viewSettings" class="bp-hidden">${budgetAdminInnerHtml()}</div>`
    : `<div class="tabs-row" id="bp-mainTabs"></div>
       <div id="bp-viewAll"></div>
       <div id="bp-viewDept" class="bp-hidden"></div>
       <div id="bp-viewPerson" class="bp-hidden"></div>`;
  return `
    ${spDesktopRecommendedNoticeHtml()}
    <header class="page-head bp-page-head">
      <div>
        <h2>${isManagement ? "経営ダッシュボード" : "予算進捗"}</h2>
        <div class="bp-view-switch" aria-label="表示切替">
          <button type="button" class="chip-btn" data-bp-view="all" onclick="window.budgetProgressSetView && window.budgetProgressSetView('all')">全体</button>
          <button type="button" class="chip-btn" data-bp-view="h1" onclick="window.budgetProgressSetView && window.budgetProgressSetView('h1')">上期</button>
          <button type="button" class="chip-btn" data-bp-view="h2" onclick="window.budgetProgressSetView && window.budgetProgressSetView('h2')">下期</button>
        </div>
      </div>
      <div class="head-tools">${tools}</div>
    </header>
    <div class="budget-progress" id="budgetProgressRoot" data-bp-mode="${isManagement ? "management" : "budget-progress"}">
      ${
        budgetRowsLoading
          ? `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "予算データを読み込んでいます" })}</div>`
          : budgetRowsFailed
            ? `<div class="cl-empty" style="padding:18px 20px;">予算データを取得できませんでした。ページを再読み込みしてください。</div>`
            : views
      }
    </div>
  `;
}

/** マスター側の契約／取引の承認状態（NDA・業務委託書の締結「済」とは別管理）。
 *  許可値は 00-shared-enums.js の CLIENT_APPROVAL_STATUS_OPTIONS（未申請/申請中/承認済/却下）。
 *  旧値「未承認」は「未申請」へ読み替え。サーバ側の対は api/_clientApprovalStatus.js（既定値は両側とも「未申請」）。 */
function normalizeMasterApprovalStatus(raw) {
  const s = String(raw ?? "").trim();
  const mapped = CLIENT_APPROVAL_LEGACY_MAP[s] || s;
  return CLIENT_APPROVAL_STATUS_OPTIONS.includes(mapped) ? mapped : "未申請";
}

function isClientApproved(rowOrClient) {
  if (!rowOrClient) return false;
  const a = rowOrClient.approvalStatus ?? rowOrClient.approval_status;
  return normalizeMasterApprovalStatus(a) === "承認済";
}

const contractMaster = clients.map((client, idx) => {
  const p = projects.find((proj) => proj.clientId === client.id);
  const statusSeed = p?.docs?.contract || "";
  const status = statusSeed === "締結済み" ? "契約中" : statusSeed === "確認待ち" ? "交渉中" : (idx % 4 === 0 ? "終了" : "交渉中");
  return {
    id: client.id,
    no: `C-${String(idx + 1).padStart(3, "0")}`,
    customerNo: client.customerNo || "",
    shortName: client.name,
    name: client.name,
    tantou: p?.owner || users[idx % users.length]?.name || "",
    status,
    zip: client.postalCode || "",
    prefecture: client.prefecture || "東京都",
    addr: client.address || `東京都千代田区${idx + 1}-1-1`,
    tel: client.phone || `03-000${idx + 1}-000${idx + 2}`,
    payment: idx % 2 === 0 ? "月末締め翌月末" : "20日締め翌月末",
    transferFeeBurden: client.transferFeeBurden || "先方",
    nda: p?.docs?.nda === "締結済み" ? true : idx % 2 === 0,
    baseContract: status === "契約中",
    approvalStatus: normalizeMasterApprovalStatus(client.approvalStatus),
    approvalRejectReason: client.approvalRejectReason || "",
    sendMethod: idx % 3 === 0 ? "メール(DL)" : idx % 3 === 1 ? "郵送" : "メール(DL)+郵送",
    regNo: client.invoiceNo || (idx % 3 === 2 ? "" : `T${String(1000000000000 + idx)}`),
    qualified: idx % 3 === 2 ? "無" : "有",
    issuer: idx % 3 === 2 ? "該当しない" : "該当する",
    contactPerson: client.contactName || "",
    email: client.email || "",
    registeredAt: client.firstRegisteredAt || "",
    salesHidden: client.salesHidden === true,
    archived: client.archived === true,
    classification: "クライアント"
  };
});

function upsertContractFromClient(client) {
  if (!client || !client.id) return;
  const idx = contractMaster.findIndex((item) => item.id === client.id);
  const prev = idx >= 0 ? contractMaster[idx] : null;
  const next = {
    ...(prev || {}),
    id: client.id,
    no: prev?.no || `C-${String(contractMaster.length + 1).padStart(3, "0")}`,
    customerNo: client.customerNo != null && String(client.customerNo).trim() !== "" ? String(client.customerNo).trim() : (prev?.customerNo || ""),
    shortName: client.shortName || client.name || prev?.shortName || "",
    name: client.name || prev?.name || "",
    tantou:
      client.contractOwner != null && String(client.contractOwner).trim() !== ""
        ? String(client.contractOwner).trim()
        : prev != null && prev.tantou != null && String(prev.tantou).trim() !== ""
          ? String(prev.tantou).trim()
          : "",
    status: client.contractStatus === "済" ? "契約中" : (prev?.status || "交渉中"),
    // マスター側の値を正とする（空=クリア済みも尊重）。prev へ戻すとクリアした値が保存時にDBへ復活する
    zip: client.postalCode != null ? String(client.postalCode).trim() : "",
    prefecture: client.prefecture != null && String(client.prefecture).trim() !== "" ? String(client.prefecture).trim() : "東京都",
    addr: client.address || prev?.addr || "",
    tel: client.tel || client.phone || prev?.tel || "",
    payment: client.paymentTerms || prev?.payment || "",
    transferFeeBurden: client.transferFeeBurden != null && String(client.transferFeeBurden).trim() !== "" ? String(client.transferFeeBurden).trim() : "先方",
    nda: client.ndaStatus === "済",
    baseContract: client.contractStatus === "済",
    approvalStatus: normalizeMasterApprovalStatus(client.approvalStatus ?? prev?.approvalStatus ?? "未申請"),
    approvalRejectReason: client.approvalRejectReason ?? prev?.approvalRejectReason ?? "",
    sendMethod: normalizeDeliveryMethodStored(
      client.deliveryMethod != null && String(client.deliveryMethod).trim() !== ""
        ? client.deliveryMethod
        : prev?.sendMethod || "メール(DL)"
    ),
    regNo: client.invoiceIssuerNumber || prev?.regNo || "",
    qualified: client.invoiceIssuer === "該当する" ? "有" : client.invoiceIssuer === "該当しない" ? "無" : (prev?.qualified || "無"),
    issuer: client.invoiceIssuer || prev?.issuer || "未設定",
    contactPerson: client.contactName || prev?.contactPerson || "",
    email: client.email || prev?.email || "",
    honorific: client.honorific || "",
    building: client.building || "",
    fax: client.fax || "",
    notes: client.notes || "",
    tags: client.tags || "",
    registeredAt: client.firstRegisteredAt || "",
    salesHidden: client.salesHidden === true,
    archived: client.archived === true,
    classification: prev?.classification || "クライアント",
    // 取引承認の申請内容（申請承認ページの詳細ドロワーが行から直接読む）
    expectedAmount: client.expectedAmount != null ? Number(client.expectedAmount) : (prev?.expectedAmount ?? null),
    capitalAmount: client.capitalAmount != null ? Number(client.capitalAmount) : (prev?.capitalAmount ?? null),
    establishedYm: client.establishedYm || prev?.establishedYm || "",
    applyNote: client.approvalApplyNote != null ? String(client.approvalApplyNote) : (prev?.applyNote ?? "")
  };
  if (idx >= 0) contractMaster[idx] = next;
  else contractMaster.unshift(next);
}

function upsertContractFromPartner(partner) {
  if (!partner || partner.id == null) return;
  const rowId = `partner-${partner.id}`;
  const idx = contractMaster.findIndex((item) => item.id === rowId);
  const prev = idx >= 0 ? contractMaster[idx] : null;
  const ndaStatus = String(partner.nda_status || "未").trim();
  const contractStatus = String(partner.contract_status || "未").trim();
  const next = {
    ...(prev || {}),
    id: rowId,
    sourcePartnerId: partner.id,
    no: prev?.no || `P-${String(contractMaster.length + 1).padStart(3, "0")}`,
    customerNo: partner.customer_no != null && String(partner.customer_no).trim() !== "" ? String(partner.customer_no).trim() : (prev?.customerNo || ""),
    shortName: partner.branch ? `${partner.name} ${partner.branch}` : (partner.name || prev?.shortName || ""),
    name: partner.name || prev?.name || "",
    /** 契約一覧の「社内担当者」＝ fp_partners.contract_owner（先方担当 contact_name とは別） */
    tantou:
      partner.contract_owner != null && String(partner.contract_owner).trim() !== ""
        ? String(partner.contract_owner).trim()
        : prev != null && prev.tantou != null && String(prev.tantou).trim() !== ""
          ? String(prev.tantou).trim()
          : "",
    status: contractStatus === "済" ? "契約中" : (prev?.status || "交渉中"),
    // マスター側の値を正とする（空=クリア済みも尊重）。prev へ戻すとクリアした値が保存時にDBへ復活する
    zip: partner.postal_code != null ? String(partner.postal_code).trim() : "",
    prefecture: partner.prefecture != null ? String(partner.prefecture).trim() : "",
    addr: partner.address || prev?.addr || "",
    tel: partner.contact_tel || prev?.tel || "",
    payment: partner.payment_terms || prev?.payment || "",
    transferFeeBurden: partner.transfer_fee_burden != null && String(partner.transfer_fee_burden).trim() !== "" ? String(partner.transfer_fee_burden).trim() : "先方",
    nda: ndaStatus === "済",
    baseContract: contractStatus === "済",
    approvalStatus: normalizePartnerApprovalStatus(partner.approval_status, partner.nda_status, partner.contract_status),
    approvalRejectReason: partner.approval_reject_reason || "",
    sendMethod: normalizeDeliveryMethodStored(partner.delivery_method || prev?.sendMethod || "メール(DL)"),
    regNo: partner.invoice_issuer_number || prev?.regNo || "",
    qualified: partner.invoice_issuer === "該当する" ? "有" : partner.invoice_issuer === "該当しない" ? "無" : (prev?.qualified || "無"),
    issuer: partner.invoice_issuer || prev?.issuer || "未設定",
    contactPerson: partner.contact_name || prev?.contactPerson || "",
    email: partner.contact_email || prev?.email || "",
    honorific: partner.honorific || "",
    branch: partner.branch || "",
    building: partner.building || "",
    fax: partner.fax || "",
    notes: partner.notes || "",
    tags: partner.tags || "",
    paymentMethod: partner.payment_method || "銀行振込",
    withholdingTax: partner.withholding_tax || "なし",
    corporateNumber: partner.corporate_number || "",
    ...splitPartnerBankInfo(partner.bank_info),
    registeredAt: partner.first_registered_at || "",
    salesHidden: partner.sales_hidden === true,
    archived: partner.archived === true,
    classification: "パートナー",
    // 取引承認の申請内容（申請承認ページの詳細ドロワーが行から直接読む）
    expectedAmount: partner.expected_amount != null ? Number(partner.expected_amount) : null,
    capitalAmount: partner.capital_amount != null ? Number(partner.capital_amount) : null,
    establishedYm: partner.established_ym || "",
    applyNote: partner.approval_apply_note || ""
  };
  if (idx >= 0) contractMaster[idx] = next;
  else contractMaster.unshift(next);
}

/** 削除済み（論理削除）行を契約一覧のテーブル表示用に変換する軽量版。
 *  復元前提の一覧表示だけに使うため、upsertContractFromClient/Partner ほど多くの列は持たない
 *  （contractMaster には混ぜない＝他画面の走査に影響させないため別配列で保持する）。 */
function deletedContractRowFromApiRow(row, entityType) {
  const isPartner = entityType === "partner";
  return {
    id: `del-${entityType}-${row.id}`,
    sourceEntityId: row.id,
    entityType,
    isDeleted: true,
    no: "",
    customerNo: row.customer_no || "",
    name: row.name || "",
    addr: [row.prefecture, row.address].filter(Boolean).join(""),
    tel: isPartner ? (row.contact_tel || "") : (row.tel || ""),
    payment: row.payment_terms || "",
    nda: String(row.nda_status || "未").trim() === "済",
    baseContract: String(row.contract_status || "未").trim() === "済",
    approvalStatus: "",
    sendMethod: normalizeDeliveryMethodStored(row.delivery_method || ""),
    regNo: row.invoice_issuer_number || "",
    qualified: row.invoice_issuer === "該当する" ? "有" : row.invoice_issuer === "該当しない" ? "無" : "無",
    salesHidden: row.sales_hidden === true,
    archived: false,
    classification: isPartner ? "パートナー" : "クライアント"
  };
}

/** 契約一覧：「削除したデータを表示」ON時に取得する削除済み（論理削除）クライアント/パートナー */
async function fetchDeletedContractRows() {
  if (!authToken || uiState.deletedContractRowsLoading) return;
  uiState.deletedContractRowsLoading = true;
  render();
  try {
    const [cr, pr] = await Promise.all([
      apiFetch("/api/clients?includeDeleted=1"),
      apiFetch("/api/partners?includeDeleted=1")
    ]);
    const clientRows = cr.ok && Array.isArray(cr.data.clients) ? cr.data.clients.map((c) => deletedContractRowFromApiRow(c, "client")) : [];
    const partnerRows = pr.ok && Array.isArray(pr.data.partners) ? pr.data.partners.map((p) => deletedContractRowFromApiRow(p, "partner")) : [];
    uiState.deletedContractRows = [...clientRows, ...partnerRows];
  } finally {
    uiState.deletedContractRowsLoading = false;
    render();
  }
}

/** 削除済み行の復元。成功したら削除済みキャッシュから外し、通常データを再取得してcontractMasterへ反映する */
async function restoreContractRow(entityType, id) {
  if (uiState.contractRestoringId) return;
  uiState.contractRestoringId = `${entityType}:${id}`;
  render();
  try {
    const path = entityType === "partner" ? "/api/partners" : "/api/clients";
    const r = await apiFetch(path, { method: "PUT", body: { id, restore: true } });
    if (!r.ok) {
      showToastError(r.data?.error || "復元に失敗しました");
      return;
    }
    if (Array.isArray(uiState.deletedContractRows)) {
      uiState.deletedContractRows = uiState.deletedContractRows.filter(
        (row) => !(row.entityType === entityType && String(row.sourceEntityId) === String(id))
      );
    }
    showToast("復元しました");
    await loadDataFromDB();
  } finally {
    uiState.contractRestoringId = "";
    render();
  }
}

function syncContractMasterFromClients() {
  const clientIds = new Set(clients.map((c) => c.id));
  const partnerIds = new Set((uiState.partnersCache || []).map((p) => `partner-${p.id}`));
  for (let i = contractMaster.length - 1; i >= 0; i -= 1) {
    const row = contractMaster[i];
    const classification = row?.classification || "クライアント";
    if (classification === "クライアント" && !clientIds.has(row.id)) contractMaster.splice(i, 1);
    if (classification === "パートナー" && !partnerIds.has(row.id)) contractMaster.splice(i, 1);
  }
  clients.forEach((c) => upsertContractFromClient(c));
  (uiState.partnersCache || []).forEach((p) => upsertContractFromPartner(p));
}

/** 契約一覧の行 → クライアントマスタの形。既存の値を土台に、行の内容で上書きする。
 *  clients は書き換えない（更新申請のように「送るだけでローカルへは当てない」用途があるため）。 */
function clientFromContractRow(contractRow) {
  if (!contractRow || !contractRow.id) return null;
  if ((contractRow.classification || "クライアント") !== "クライアント") return null;
  const idx = clients.findIndex((c) => c.id === contractRow.id);
  const nextClient = {
    id: contractRow.id,
    ...(idx >= 0 ? clients[idx] : {}),
    name: contractRow.name || (idx >= 0 ? clients[idx].name : "新規クライアント"),
    shortName: contractRow.shortName || contractRow.name || (idx >= 0 ? clients[idx].shortName : ""),
    contractOwner: String(contractRow.tantou ?? "").trim(),
    postalCode: contractRow.zip != null ? contractRow.zip : (idx >= 0 ? clients[idx].postalCode : ""),
    prefecture: contractRow.prefecture || (idx >= 0 ? clients[idx].prefecture : "東京都"),
    // 空欄にした住所・電話・登録番号は空のまま保存する（|| だと前の値に戻り、消せなかった）
    address: contractRow.addr ?? (idx >= 0 ? clients[idx].address : ""),
    tel: contractRow.tel ?? (idx >= 0 ? clients[idx].tel : ""),
    paymentTerms: contractRow.payment || (idx >= 0 ? clients[idx].paymentTerms : "月末締翌月末払"),
    transferFeeBurden: contractRow.transferFeeBurden || (idx >= 0 ? clients[idx].transferFeeBurden : "先方"),
    deliveryMethod: normalizeDeliveryMethodStored(
      contractRow.sendMethod != null && String(contractRow.sendMethod).trim() !== ""
        ? contractRow.sendMethod
        : idx >= 0
          ? clients[idx].deliveryMethod
          : "メール(DL)"
    ),
    invoiceIssuerNumber: contractRow.regNo ?? (idx >= 0 ? clients[idx].invoiceIssuerNumber : ""),
    ndaStatus: contractRow.nda ? "済" : "未",
    contractStatus: contractRow.baseContract ? "済" : "未",
    approvalStatus: normalizeMasterApprovalStatus(
      contractRow.approvalStatus ?? (idx >= 0 ? clients[idx].approvalStatus : "未申請")
    ),
    customerNo: contractRow.customerNo ?? (idx >= 0 ? clients[idx].customerNo : ""),
    honorific: contractRow.honorific ?? (idx >= 0 ? clients[idx].honorific : ""),
    building: contractRow.building ?? (idx >= 0 ? clients[idx].building : ""),
    fax: contractRow.fax ?? (idx >= 0 ? clients[idx].fax : ""),
    email: contractRow.email ?? (idx >= 0 ? clients[idx].email : ""),
    contactName: contractRow.contactPerson ?? (idx >= 0 ? clients[idx].contactName : ""),
    notes: contractRow.notes ?? (idx >= 0 ? clients[idx].notes : ""),
    tags: contractRow.tags ?? (idx >= 0 ? clients[idx].tags : ""),
    invoiceIssuer: contractRow.issuer || (idx >= 0 ? clients[idx].invoiceIssuer : "未設定"),
    firstRegisteredAt: contractRow.registeredAt || (idx >= 0 ? clients[idx].firstRegisteredAt : ""),
    salesHidden: contractRow.salesHidden === true
  };
  return nextClient;
}

/** 契約一覧の行の内容を clients へ反映する（即時保存する経路用）。 */
function syncClientFromContractRow(contractRow) {
  const nextClient = clientFromContractRow(contractRow);
  if (!nextClient) return;
  const idx = clients.findIndex((c) => c.id === contractRow.id);
  if (idx >= 0) clients[idx] = nextClient;
  else clients.unshift(nextClient);
}

/** 契約一覧CSVの「登録日時」表示（初回登録日時 ISO → "YYYY-MM-DD HH:mm"。時刻無しは日付のみ） */
function contractRegisteredAtDisplay(raw) {
  const { date, time } = splitFirstRegisteredForForm(raw);
  if (!date) return "";
  return time ? `${date} ${time}` : date;
}

/** 契約一覧CSV（全件DL/選択分DLで共通）。列の並びはここだけで管理する。
 *  列名は CLIENT_CSV_COLUMNS の正式 header から引く（手書きコピーだと列定義側のリネームに取り残されるため）。
 *  パートナー行の取込互換は PARTNER_CSV_COLUMNS の header / headerAliases が担保（例:「基本契約」=業務委託契約の旧名alias）。 */
function contractCsvText(rows) {
  const h = (key) => (CLIENT_CSV_COLUMNS.find((col) => col.key === key) || { header: key }).header;
  const headers = ["分類", h("name"), h("postalCode"), h("prefecture"), h("address"), h("tel"), h("email"), h("paymentTerms"), h("transferFeeBurden"), h("ndaStatus"), h("contractStatus"), h("deliveryMethod"), h("invoiceIssuer"), h("invoiceIssuerNumber"), h("customerNo"), h("firstRegisteredAt")];
  const lines = rows.map((c) => [
    c.classification || "クライアント", c.name, c.zip || "", c.prefecture || "", c.addr, c.tel, c.email || "", c.payment,
    c.transferFeeBurden || "先方", c.nda ? "有" : "無", c.baseContract ? "有" : "無", c.sendMethod,
    c.issuer || "", c.regNo || "", c.customerNo || "", contractRegisteredAtDisplay(c.registeredAt)
  ].map(toCsvCell).join(","));
  return String.fromCharCode(0xFEFF) + `${headers.join(",")}\r\n${lines.join("\r\n")}`;
}

/** 申請状況フィルタ値 → 一致すべきステータス配列（旧 approved/pending 値も読み替える）。
 *  取引先一覧のクライアント/パートナー両方が同じ読み替えを使う単一定義 */
function approvalFilterStatuses(v) {
  return v === "approved" ? ["承認済"] : v === "pending" ? ["未申請", "申請中", "却下"] : [v];
}

function isDomainListRoute() {
  return currentRoute().page === "domain-list";
}

/** ドメイン一覧の選択をchangeで同期（契約一覧と同型）。解除時はキーを削除 */
function syncDomainListSelectionFromInput(input, isHeaderAll) {
  if (!(input instanceof HTMLInputElement) || !isDomainListRoute()) return;
  const sel = uiState.domainList.selected;
  if (!sel || typeof sel !== "object") uiState.domainList.selected = {};
  if (isHeaderAll) {
    const pageIds = domainListRows()
      .slice(
        (uiState.domainList.page - 1) * uiState.domainList.perPage,
        uiState.domainList.page * uiState.domainList.perPage
      )
      .map((r) => r.id);
    pageIds.forEach((id) => {
      if (input.checked) uiState.domainList.selected[id] = true;
      else delete uiState.domainList.selected[id];
    });
  } else {
    const id = input.getAttribute("data-domain-row-check") || "";
    if (!id) return;
    if (input.checked) uiState.domainList.selected[id] = true;
    else delete uiState.domainList.selected[id];
  }
  render();
}

function isProjectsListRoute() {
  return currentRoute().page === "projects";
}

function isStaffAssignmentRoute() {
  return currentRoute().page === "staff-assignment";
}

function syncStaffAssignmentSelectionFromInput(input, isHeaderAll) {
  if (!(input instanceof HTMLInputElement) || !isStaffAssignmentRoute()) return;
  const { rows } = collectStaffAssignmentRows();
  const tbl = uiState.staffAssignmentTable;
  if (!tbl.selected || typeof tbl.selected !== "object") tbl.selected = {};
  if (isHeaderAll) {
    // 見出しのチェックは「表示中の行」だけ（一覧は STAFF_ASSIGN_PAGE_SIZE 行ずつ表示）
    rows.slice(0, staffAssignmentShownCount()).forEach((r) => {
      const key = staffAssignRowKey(r.p.id, r.lineIndex);
      if (input.checked) tbl.selected[key] = true;
      else delete tbl.selected[key];
    });
  } else {
    const key = input.dataset.staffAssignSelect || "";
    if (!key) return;
    if (input.checked) tbl.selected[key] = true;
    else delete tbl.selected[key];
  }
  render();
}

function syncProjectListSelectionFromInput(input, isHeaderAll) {
  if (!(input instanceof HTMLInputElement) || !isProjectsListRoute()) return;
  if (isHeaderAll) {
    const visible = getFilteredSortedProjects();
    const start = (uiState.projectTable.page - 1) * uiState.projectTable.perPage;
    const rows = visible.slice(start, start + uiState.projectTable.perPage);
    rows.forEach((p) => {
      if (input.checked) uiState.projectTable.selected[p.id] = true;
      else delete uiState.projectTable.selected[p.id];
    });
  } else {
    const id = input.dataset.projectSelect || "";
    if (!id) return;
    if (input.checked) uiState.projectTable.selected[id] = true;
    else delete uiState.projectTable.selected[id];
  }
  render();
}

function clientTagsArray(client) {
  const raw = String(client?.tags || "");
  return raw.split(/[,、\s]+/).map((x) => x.trim()).filter(Boolean);
}

/** 一覧カード用: NDA / 基本契約（業務委託）の表示ラベル */
function clientNdaStatusLine(client) {
  const ok = client?.ndaStatus === "済";
  return ok ? "NDA 締結済" : "NDA 未締結";
}

function clientContractStatusLine(client) {
  const ok = client?.contractStatus === "済";
  return ok ? "業務委託 締結済" : "業務委託 未締結";
}

/** 取引先一覧カードの申請状況ピル（4状態＋アーカイブ）。クライアント/パートナー共通 */
function clientDirectoryApprovalPillHtml(status, archived) {
  if (archived === true) {
    return `<span class="cl-client-approval-pill cl-client-approval-pill--arch"><span class="cl-client-approval-dot cl-client-approval-dot--arch" aria-hidden="true"></span><span class="cl-client-approval-text">アーカイブ</span></span>`;
  }
  const mod = status === "承認済" ? "ok" : status === "申請中" ? "pending" : status === "却下" ? "warn" : "none";
  // 申請中は「何の申請か」をツールチップで補足（申請中＝取引承認。更新・削除の申請は申請承認ページに種別つきで並ぶ）
  const title = status === "申請中" ? ` title="取引承認（新規取引の承認）を申請中です"` : "";
  return `<span class="cl-client-approval-pill cl-client-approval-pill--${mod}"${title}><span class="cl-client-approval-dot cl-client-approval-dot--${mod}" aria-hidden="true"></span><span class="cl-client-approval-text">${escapeHtml(status)}</span></span>`;
}

/** 取引先一覧カードの操作ボタン（申請/再申請・詳細・アーカイブ/復元）。
 *  data-client-flow-* のクリック処理は app-src/15k-client-directory-flow.js の独立リスナー */
function clientDirectoryCardActionsHtml(entityType, dbId, status, archived, canApplyArchive) {
  const t = escapeHtmlAttr(`${entityType}:${dbId}`);
  const btns = [];
  if (canApplyArchive && archived !== true && (status === "未申請" || status === "却下")) {
    btns.push(`<button class="cl-btn cl-btn-primary cl-client-card-flow-btn" data-client-flow-apply-open="${t}" type="button">${status === "却下" ? "再申請" : "申請"}</button>`);
  }
  btns.push(`<button class="cl-client-card-edit" data-client-flow-detail="${t}" type="button">詳細</button>`);
  if (canApplyArchive) {
    if (archived === true) {
      btns.push(`<button class="cl-client-card-edit" data-client-flow-archive="${t}" data-client-flow-archive-value="false" type="button">復元</button>`);
    } else if (status === "未申請" || status === "却下") {
      btns.push(`<button class="cl-client-card-edit" data-client-flow-archive="${t}" data-client-flow-archive-value="true" type="button">アーカイブ</button>`);
    }
  }
  return btns.join("");
}

/** NDA/業務委託基本契約書バッジ。基本契約が締結済みなら NDA 無しはカバー済み＝色（緑破線）だけ変える
 *  （文言は変えない。「NDA（カバー済）」は意味が伝わらないというユーザー指摘） */
function clientDirectoryContractBadgesHtml(ndaOk, ctrOk) {
  const ndaBadge = ndaOk
    ? `<span class="cl-client-contract-badge cl-client-contract-badge--ok"><span class="cl-client-contract-mark">✓</span> NDA</span>`
    : ctrOk
      ? `<span class="cl-client-contract-badge cl-client-contract-badge--covered" title="基本契約が締結済みのためNDA相当はカバー済み（不足扱いにしない）"><span class="cl-client-contract-mark">—</span> NDA</span>`
      : `<span class="cl-client-contract-badge cl-client-contract-badge--ng"><span class="cl-client-contract-mark">—</span> NDA</span>`;
  return `${ndaBadge}<span class="cl-client-contract-badge ${ctrOk ? "cl-client-contract-badge--ok" : "cl-client-contract-badge--ng"}"><span class="cl-client-contract-mark">${ctrOk ? "✓" : "—"}</span> 業務委託基本契約書</span>`;
}

function splitFirstRegisteredForForm(isoOrRaw) {
  const raw = String(isoOrRaw || "").trim();
  if (!raw) return { date: "", time: "" };
  const slash = raw.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})[ \t]+(\d{1,2}):(\d{2})/);
  if (slash) {
    return {
      date: `${slash[1]}-${String(Number(slash[2])).padStart(2, "0")}-${String(Number(slash[3])).padStart(2, "0")}`,
      time: `${String(Number(slash[4])).padStart(2, "0")}:${String(Number(slash[5])).padStart(2, "0")}`
    };
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  return {
    date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
  };
}

function buildFirstRegisteredAtValue(date, time) {
  const d = String(date || "").trim();
  const t = String(time || "").trim();
  if (!d) return "";
  return `${d.replace(/-/g, "/")} ${t || "00:00"}`;
}

/** クライアント/パートナーの更新申請を送る（承認されるまでDBは変わらない＝ローカルへ当てないこと）。
 *  クライアント/パートナーの編集モーダルで共有する。 */
async function submitMasterChangeRequest(entityType, entityId, payload) {
  const r = await apiFetch("/api/master-change-requests", {
    method: "POST",
    body: { entityType, entityId, payload }
  });
  if (r.ok && r.data?.ok === true) return { ok: true, error: "" };
  return { ok: false, error: typeof r.data?.error === "string" ? r.data.error : "" };
}

/** clientId → 案件数（カードの「案件 N件」と同じ数え方） */
function clientProjectCountMap() {
  const counts = new Map();
  projects.forEach((p) => counts.set(p.clientId, (counts.get(p.clientId) || 0) + 1));
  return counts;
}

function clientListRows() {
  const rows = clientListFilteredRows();
  const sort = (uiState.clientList.filters || {}).sort;
  if (sort !== "projectsDesc" && sort !== "projectsAsc") return rows;
  // 同数は既定の並びを保つ（Array.prototype.sort は安定ソート）
  const counts = clientProjectCountMap();
  const dir = sort === "projectsDesc" ? -1 : 1;
  return rows.sort((a, b) => dir * ((counts.get(a.id) || 0) - (counts.get(b.id) || 0)));
}

function clientListFilteredRows() {
  const st = uiState.clientList;
  // 固定項目編集で隠した検索項目の条件は使わない（15z-list-filter-fields.js）
  const f = listFilterFieldsEffective(CLIENT_LIST_FILTER_SPEC, st.filters || {});
  return clients.filter((c) => {
    // 「マスター・経理のみ」（sales_hidden）のクライアントは、マスター・経理にだけグレーのカードで出す
    // （サーバも contract-management 権限の無い人には返していない）
    if (c.salesHidden && !canManageClientPartnerMaster()) return false;
    // 申請状況フィルタ。「アーカイブ」を選んだときだけアーカイブ済みを表示し、それ以外は常に除外
    if (f.approval === "archived") {
      if (c.archived !== true) return false;
    } else {
      if (c.archived === true) return false;
      if (f.approval && !approvalFilterStatuses(f.approval).includes(normalizeMasterApprovalStatus(c.approvalStatus))) {
        return false;
      }
    }
    if (f.contractOwner) {
      const o = String(c.contractOwner || "").trim();
      if (o !== String(f.contractOwner).trim()) return false;
    }
    if (!clientListExtraFilterMatch(f, {
      ndaOk: c.ndaStatus === "済", contractOk: c.contractStatus === "済", deliveryMethod: c.deliveryMethod,
      invoiceIssuer: c.invoiceIssuer, paymentTerms: c.paymentTerms, prefecture: c.prefecture, salesHidden: c.salesHidden === true
    })) return false;
    const tags = clientTagsArray(c);
    // タグはキーワード検索の対象に含める（専用のタグ検索欄は置かない）
    if (f.keyword) {
      const hay = [
        c.name, c.shortName, c.customerNo, c.contactName, c.contractOwner, c.email, c.tel, c.prefecture, c.address, c.building,
        c.notes, c.paymentTerms, c.deliveryMethod, c.invoiceIssuerNumber, tags.join(" ")
      ].join(" ").toLowerCase();
      if (!hay.includes(String(f.keyword).toLowerCase())) return false;
    }
    return true;
  });
}

/** ページ番号の並び（10ページ以上は先頭・末尾・現在の前後2つを残して「…」で省略）。
 *  案件一覧・クライアント一覧・パートナー一覧のページャで共用 */
function pageNumberItemsWithEllipsis(page, totalPages) {
  const items = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (totalPages > 9 && i !== 1 && i !== totalPages && Math.abs(i - page) > 2) {
      if (items[items.length - 1] !== "…") items.push("…");
      continue;
    }
    items.push(i);
  }
  return items;
}

function clientPageButtons(totalPages) {
  const page = uiState.clientList.page;
  if (totalPages <= 1) return "";
  const items = pageNumberItemsWithEllipsis(page, totalPages);
  return `
    <button class="cl-page-btn" data-client-list-page="${page - 1}" ${page === 1 ? "disabled" : ""}>＜</button>
    ${items.map((p) => p === "…"
      ? `<span class="cl-page-ellipsis">…</span>`
      : `<button class="cl-page-btn ${p === page ? "active" : ""}" data-client-list-page="${p}">${p}</button>`).join("")}
    <button class="cl-page-btn" data-client-list-page="${page + 1}" ${page === totalPages ? "disabled" : ""}>＞</button>
  `;
}

function partnerPageButtons(totalPages) {
  const page = uiState.partnerList.page;
  if (totalPages <= 1) return "";
  const items = pageNumberItemsWithEllipsis(page, totalPages);
  return `
    <button class="chip-btn" data-partner-list-page="${page - 1}" ${page === 1 ? "disabled" : ""}>＜</button>
    ${items.map((p) => p === "…"
      ? `<span class="cl-page-ellipsis" style="padding:0 6px;color:var(--fp-text-subtle);">…</span>`
      : `<button class="chip-btn ${p === page ? "active" : ""}" data-partner-list-page="${p}">${p}</button>`).join("")}
    <button class="chip-btn" data-partner-list-page="${page + 1}" ${page === totalPages ? "disabled" : ""}>＞</button>
  `;
}

function partnerTagsArray(partner) {
  const raw = String(partner?.tags || "");
  return raw.split(/[,、\s]+/).map((x) => x.trim()).filter(Boolean);
}

function normalizePartnerApprovalStatus(raw, ndaRaw, contractRaw) {
  const s = String(raw || "").trim();
  const mapped = CLIENT_APPROVAL_LEGACY_MAP[s] || s;
  if (CLIENT_APPROVAL_STATUS_OPTIONS.includes(mapped)) return mapped;
  // approval_status 列が無い時代の古いキャッシュ行だけの後方互換（自己申告のNDA/契約から導出）。
  // 新値「未申請」「申請中」は上で素通しされるため、ここに落ちて承認済へ化けることはない。
  const ndaOk = String(ndaRaw || "").trim() === "済";
  const contractOk = String(contractRaw || "").trim() === "済";
  return ndaOk && contractOk ? "承認済" : "未申請";
}

/** 取引先・パートナー一覧の「並び順」セレクト（filterAttr は data-client-list-filter 等） */
function projectCountSortSelectHtml(filterAttr, value) {
  const opts = [["", "既定"], ["projectsDesc", "案件数が多い順"], ["projectsAsc", "案件数が少ない順"]];
  return `<label class="cl-filter-group"><span>並び順</span><select class="cl-filter-input" data-search-dropdown-off="1" ${filterAttr}="sort">${opts.map(([v, label]) => `<option value="${v}" ${(value || "") === v ? "selected" : ""}>${label}</option>`).join("")}</select></label>`;
}

/** partnerId → 案件数（集計対象の原価で支払先にそのパートナーが入っている案件の数。支払先名で名寄せ） */
function partnerProjectCountMap() {
  const counts = new Map();
  const byVendor = new Map();
  projects.forEach((p) => {
    const ids = new Set();
    countedProjectCosts(p.costs).forEach((c) => {
      const v = String(c.vendor || "");
      if (!byVendor.has(v)) byVendor.set(v, costPartnerByVendorName(v));
      const pt = byVendor.get(v);
      if (pt) ids.add(pt.id);
    });
    ids.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
  });
  return counts;
}

function partnerListRows() {
  const rows = partnerListFilteredRows();
  const sort = (uiState.partnerList.filters || {}).sort;
  if (sort !== "projectsDesc" && sort !== "projectsAsc") return rows;
  // 同数は既定の並びを保つ（clientListRows と同じ）
  const counts = partnerProjectCountMap();
  const dir = sort === "projectsDesc" ? -1 : 1;
  return rows.sort((a, b) => dir * ((counts.get(a.id) || 0) - (counts.get(b.id) || 0)));
}

function partnerListFilteredRows() {
  const st = uiState.partnerList;
  const f = listFilterFieldsEffective(PARTNER_LIST_FILTER_SPEC, st.filters || {});
  return (uiState.partnersCache || []).filter((p) => {
    // 「マスター・経理のみ」のパートナーは、マスター・経理にだけグレーのカードで出す（clientListFilteredRows と同じ）
    if (p.sales_hidden && !canManageClientPartnerMaster()) return false;
    const tags = partnerTagsArray(p);
    // 申請状況フィルタ。「アーカイブ」を選んだときだけアーカイブ済みを表示し、それ以外は常に除外
    if (f.approval === "archived") {
      if (p.archived !== true) return false;
    } else {
      if (p.archived === true) return false;
      if (f.approval && !approvalFilterStatuses(f.approval).includes(normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status))) {
        return false;
      }
    }
    if (f.paymentMethod && f.paymentMethod !== "all") {
      const pm = String(p.payment_method || "").trim();
      if (pm !== String(f.paymentMethod).trim()) return false;
    }
    if (!clientListExtraFilterMatch(f, {
      ndaOk: String(p.nda_status || "").trim() === "済", contractOk: String(p.contract_status || "").trim() === "済",
      deliveryMethod: p.delivery_method, invoiceIssuer: p.invoice_issuer, paymentTerms: p.payment_terms,
      prefecture: p.prefecture, salesHidden: p.sales_hidden === true
    })) return false;
    // タグはキーワード検索の対象に含める（専用のタグ検索欄は置かない）
    if (f.keyword) {
      const hay = [
        p.name, p.branch, p.customer_no, p.contact_name, p.contact_email, p.contact_tel, p.prefecture, p.address, p.building, p.notes,
        p.payment_terms, p.payment_method, p.withholding_tax, p.nda_status, p.contract_status, p.delivery_method, p.invoice_issuer_number,
        normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status),
        tags.join(" ")
      ].join(" ").toLowerCase();
      if (!hay.includes(String(f.keyword).toLowerCase())) return false;
    }
    return true;
  });
}

/** 取引先CSVの取込ボタン。一括での新規登録・更新は申請フローを迂回するため**マスターのみ**
 *  （2026-09-04 経理を除外: 経理もその他含め取引先申請フローで登録する運用）。
 *  押せてしまうと権限の無い人が全社の取引先マスタを一括で書き換えられる。
 *  クリック側（16-render-routing.js の csvImportClients / csvImportPartners）でも同じ判定をする。 */
/** 取込用 CSV の雛形ダウンロード。使い道の取込（csvImportToolHtml）と同じくマスターだけに出す */
function csvTemplateToolHtml(attr) {
  if (!isMasterRoleUser(currentUser)) return "";
  return `<button class="cl-btn cl-btn-outline" ${attr}="1" type="button">CSV取込用テンプレート</button>`;
}

function csvImportToolHtml(attr) {
  if (!isMasterRoleUser(currentUser)) return "";
  return `<label class="cl-btn cl-btn-outline cl-client-list-csv-label">CSVインポート<input type="file" accept=".csv" ${attr}="1" style="display:none;" /></label>`;
}

function partnerDirectoryToolsHtml() {
  return `
    ${csvTemplateToolHtml("data-csv-download-partners")}
    ${csvImportToolHtml("data-csv-import-partners")}
    ${clientListCsvButtonHtml("partner")}
    ${canManageClientPartnerMaster() ? `<button class="cl-btn cl-btn-primary" data-open-partner-create="1" type="button">新規パートナー登録</button>` : ""}
  `;
}

function partnerDirectoryBodyHtml() {
  const st = uiState.partnerList;
  const fil = st.filters || {};
  const partners = partnerListRows();
  const projectCounts = partnerProjectCountMap();
  const payMethodLabel = { "銀行振込": "振込", "口座振替": "口振", "クレジットカード": "カード", "現金支払": "現金" };
  const total = partners.length;
  const totalPages = Math.max(1, Math.ceil(total / st.perPage));
  if (st.page > totalPages) st.page = totalPages;
  const start = (st.page - 1) * st.perPage;
  const pageRows = partners.slice(start, start + st.perPage);
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + st.perPage, total);
  // 検索欄の各項目。出す項目と並び順は固定項目編集で人ごとに変えられる（15z-list-filter-fields.js）
  const fieldsHtml = {
    keyword: `<label class="cl-filter-group cl-client-keyword-group"><span>キーワード検索</span><input type="search" class="cl-filter-input" placeholder="社名・担当・タグ・住所など" data-partner-list-filter="keyword" value="${escapeHtmlAttr(fil.keyword || "")}" /></label>`,
    paymentMethod: `<label class="cl-filter-group cl-client-category-group"><span>支払方法</span><select class="cl-filter-input" data-search-dropdown-off="1" data-partner-list-filter="paymentMethod">
              <option value="all">すべて</option>
              <option value="銀行振込" ${fil.paymentMethod === "銀行振込" ? "selected" : ""}>銀行振込</option>
              <option value="口座振替" ${fil.paymentMethod === "口座振替" ? "selected" : ""}>口座振替</option>
              <option value="クレジットカード" ${fil.paymentMethod === "クレジットカード" ? "selected" : ""}>クレジットカード</option>
              <option value="現金支払" ${fil.paymentMethod === "現金支払" ? "selected" : ""}>現金支払</option>
            </select></label>`,
    approval: `<label class="cl-filter-group cl-client-approval-group"><span>申請状況</span><select class="cl-filter-input" data-search-dropdown-off="1" data-partner-list-filter="approval">
              <option value="">すべて</option>
              ${CLIENT_APPROVAL_STATUS_OPTIONS.map((s) => `<option value="${escapeHtmlAttr(s)}" ${fil.approval === s ? "selected" : ""}>${escapeHtml(s)}</option>`).join("")}
              <option value="archived" ${fil.approval === "archived" ? "selected" : ""}>アーカイブ</option>
            </select></label>`,
    ...clientListExtraFilterFieldsHtml("data-partner-list-filter", fil, "業務委託"),
    sort: projectCountSortSelectHtml("data-partner-list-filter", fil.sort)
  };
  return `
        <div class="cl-filter-area" aria-label="検索条件">
          <div class="cl-filter-row cl-client-filter-row-single">
            ${listFilterFieldsShownKeys(PARTNER_LIST_FILTER_SPEC).map((k) => fieldsHtml[k]).join("")}
            <div class="cl-filter-actions cl-client-filter-reset-inline">${listFilterFieldsEditButtonHtml(PARTNER_LIST_FILTER_SPEC)}<button class="cl-btn cl-btn-outline" type="button" data-partner-list-reset="1">リセット</button></div>
          </div>
        </div>
        ${listFilterFieldsEditorHtml(PARTNER_LIST_FILTER_SPEC)}
        <div class="cl-content cl-client-list-cards">
          ${
            uiState.heavyDataLoading
              ? `<div class="cl-board-empty cl-board-empty--heavy-load">${flowPilotNowLoadingBlock({
                  inline: true,
                  subtitle: "パートナー情報を読み込んでいます"
                })}</div>`
              : pageRows.length === 0 && !clientListDeletedRows("partner", fil).length
                ? `<div class="cl-board-empty">該当するパートナーがありません</div>`
                : pageRows.map((p) => {
            const tags = partnerTagsArray(p).join(", ");
            const ndaOk = String(p.nda_status || "").trim() === "済";
            const ctrOk = String(p.contract_status || "").trim() === "済";
            const approvalLabel = normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status);
            return `
              <article class="cl-client-card${p.archived === true ? " cl-client-card--archived" : ""}${p.sales_hidden ? " cl-client-card--limited" : ""}" data-client-flow-detail="partner:${p.id}">
                <div class="cl-client-card-main">
                  <div class="cl-client-card-line">
                    ${clientDirectoryApprovalPillHtml(approvalLabel, p.archived === true)}
                    <button class="cl-client-card-name" data-client-flow-detail="partner:${p.id}" type="button">${escapeHtml(p.name)}</button>
                    ${clientListLimitedBadgeHtml(p.sales_hidden)}
                    <div class="cl-client-meta">
                      <span class="cl-client-meta-group">
                        <span class="cl-client-meta-item cl-client-meta-item--customer-no">顧客番号：${escapeHtml(p.customer_no || (approvalLabel === "承認済" ? "—" : "承認時に発番"))}</span>
                        <span class="cl-client-meta-item">案件 ${projectCounts.get(p.id) || 0}件</span>
                        <span class="cl-client-contracts">${clientDirectoryContractBadgesHtml(ndaOk, ctrOk)}
                        </span>
                      </span>
                      <span class="cl-client-meta-group">
                        <span class="cl-client-meta-item">支払条件：${escapeHtml(p.payment_terms || "—")}</span>
                        <span class="cl-client-meta-item">支払：${escapeHtml(payMethodLabel[p.payment_method] || p.payment_method || "—")}</span>
                        <span class="cl-client-meta-item">源泉：${escapeHtml(p.withholding_tax === "なし" ? "—" : (p.withholding_tax || "—"))}</span>
                        <span class="cl-client-meta-item">タグ：${escapeHtml(tags || "—")}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="cl-client-card-actions">
                  ${clientDirectoryCardActionsHtml("partner", p.id, approvalLabel, p.archived === true, canManageClientPartnerMaster())}
                </div>
              </article>`;
          }).join("")}
          ${uiState.heavyDataLoading || st.page !== totalPages ? "" : clientListDeletedCardsHtml("partner", fil)}
        </div>
        <div class="pj-pagination cl-pagination">
          <span>表示 ${from}〜${to} 件 / 合計 ${total} 件</span>
          <div class="pj-page-right">
            <select class="pj-input" style="width:100px;" data-search-dropdown-off="1" data-partner-list-per-page="1"><option value="20" ${st.perPage === 20 ? "selected" : ""}>20件</option><option value="50" ${st.perPage === 50 ? "selected" : ""}>50件</option><option value="100" ${st.perPage === 100 ? "selected" : ""}>100件</option></select>
            ${partnerPageButtons(totalPages)}
          </div>
        </div>
  `;
}

/** パートナー編集フォームの状態を開く（従来は 16-render-routing の data-partner-edit 分岐に
 *  インラインで書かれていた state 構築を関数化。埋め込み編集の部分更新から silent で呼べるように）。 */
function openPartnerEditModal(pid, { inline = false, silent = false } = {}) {
  const p = (uiState.partnersCache || []).find((x) => x.id === pid);
  if (!p) return false;
  const firstRegisteredParts = splitFirstRegisteredForForm(p.first_registered_at || "");
  const now = new Date();
  const defaultDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const defaultTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const bank = splitPartnerBankInfo(p.bank_info);
  uiState.partnerCreate = {
    id: p.id,
    name: p.name,
    branch: p.branch,
    honorific: p.honorific || "御中",
    contactName: p.contact_name,
    contractOwner: p.contract_owner != null ? String(p.contract_owner).trim() : "",
    contactEmail: p.contact_email,
    contactTel: p.contact_tel,
    fax: p.fax || "",
    hpUrl: p.hp_url || "",
    postalCode: p.postal_code, prefecture: p.prefecture, address: p.address, building: p.building,
    paymentTerms: p.payment_terms, paymentMethod: p.payment_method,
    transferFeeBurden: p.transfer_fee_burden || "先方",
    deliveryMethod: normalizeDeliveryMethodStored(p.delivery_method || ""),
    bankName: bank.bankName, branchName: bank.branchName, accountType: bank.accountType, accountNumber: bank.accountNumber, accountHolder: bank.accountHolder,
    withholdingTax: p.withholding_tax,
    ndaStatus: String(p.nda_status || "未").trim() === "済" ? "済" : "未",
    contractStatus: String(p.contract_status || "未").trim() === "済" ? "済" : "未",
    approvalStatus: normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status),
    notes: p.notes, ndaPurpose: p.nda_purpose || "", tags: p.tags,
    firstRegisteredAt: p.first_registered_at || "",
    firstRegisteredDate: firstRegisteredParts.date || defaultDate,
    firstRegisteredTime: firstRegisteredParts.time || defaultTime,
    corporateNumber: p.corporate_number || "",
    customerNo: p.customer_no != null ? String(p.customer_no) : "",
    invoiceIssuer: p.invoice_issuer || "未設定",
    invoiceIssuerNumber: p.invoice_issuer_number || "",
    expectedAmount: p.expected_amount != null ? String(p.expected_amount) : "",
    capitalAmount: p.capital_amount != null ? String(p.capital_amount) : "",
    establishedYm: p.established_ym || "",
    // salesHidden はここに載せない: 編集フォームに営業表示の欄は無く、載せると PUT が
    // フォームを開いた時点の古い値で他の管理者のトグル変更を上書きする（キー無し＝列を触らない）
    inline
  };
  resetCorporateLocalSearch();
  if (!silent) render();
  return true;
}

function renderPartnerModal(opts = {}) {
  if (!uiState.partnerCreate) return "";
  const p = uiState.partnerCreate;
  // inline は取引先詳細ページ側（clientDetailPage）が embed:true で描く。モーダルレイヤーには出さない
  if (p.inline && !opts.embed) return "";
  const isEdit = !!p.id;
  const req = '<span class="client-required">（必須）</span>';
  // 基本契約が締結済みなら NDA 未締結の注意表示は出さない（クライアント側 renderClientCreateModal と同じ整理）
  const contractPending = String(p.contractStatus || "未").trim() !== "済";
  const ndaPending = String(p.ndaStatus || "未").trim() !== "済" && contractPending;
  const ndaExportBtnClass = `inline-btn ghost${ndaPending ? " client-export-doc-pending" : ""}`;
  const gyomuExportBtnClass = `inline-btn ghost${contractPending ? " client-export-doc-pending" : ""}`;
  return `
    <div class="composer-backdrop client-create-backdrop" data-close-partner="1"></div>
    <aside class="composer-panel client-create-modal${opts.embed ? " fp-cldetail-embed" : ""}${spNoEnter("client-create-modal")}" role="dialog" aria-modal="true">
      <div class="composer-head">
        <div><h3>${isEdit ? "パートナーを編集" : !isEdit && p.salesHidden ? "その他の取引先を登録" : "新しいパートナーを登録"}</h3><p>${!isEdit && p.salesHidden ? "営業の画面には表示されません（自動で営業非表示・承認時はE採番）" : "パートナーマスタに反映されます"}</p></div>
        <button class="inline-btn ghost" data-close-partner="1">閉じる</button>
      </div>
      <div class="composer-body">
        ${corpLocalSearchPanelHtml("partner")}
        <div class="client-form-divider"></div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row"><span>発注先名 ${req}</span><input data-partner-field="name" value="${escapeHtmlAttr(p.name || "")}" placeholder="会社名・屋号・個人名" /></label>
          <label class="client-form-row">発注先支社<input data-partner-field="branch" value="${escapeHtmlAttr(p.branch || "")}" placeholder="支社・支店名" /></label>
          <div class="client-form-row"><span>個人</span><label class="cl-inline-check"><input type="checkbox" data-partner-individual="1" ${isIndividualHonorific(p.honorific) ? "checked" : ""} /><span>個人（発注先が個人の場合にチェック）</span></label></div>
          <label class="client-form-row"><span>おおよその取引予定金額${isEdit ? "" : ` ${req}`}</span><input data-partner-field="expectedAmount" value="${escapeHtmlAttr(p.expectedAmount || "")}" inputmode="numeric" placeholder="${isEdit ? "円（例：500000）。分かる範囲でOK・未定なら空欄可" : "円（例：500000）。おおよそ・分かる範囲でOK"}" /></label>
          ${clientFormCreditRowsHtml("partner", p, !isEdit)}
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">郵便番号<input data-partner-field="postalCode" value="${escapeHtmlAttr(p.postalCode || "")}" placeholder="000-0000" inputmode="numeric" maxlength="8" style="width:140px;" /></label>
          <label class="client-form-row">都道府県<select data-partner-field="prefecture" style="width:120px;">${prefectureOptionsHtml(p.prefecture)}</select></label>
          <label class="client-form-row">住所<input data-partner-field="address" value="${escapeHtmlAttr(p.address || "")}" style="width:100%;" /></label>
          <label class="client-form-row">建物名<input data-partner-field="building" value="${escapeHtmlAttr(p.building || "")}" /></label>
          <label class="client-form-row">TEL<input data-partner-field="contactTel" value="${escapeHtmlAttr(p.contactTel || "")}" placeholder="000-0000-0000" /></label>
          <label class="client-form-row">FAX<input data-partner-field="fax" value="${escapeHtmlAttr(p.fax || "")}" placeholder="000-0000-0000" /></label>
          <label class="client-form-row">メール<input data-partner-field="contactEmail" value="${escapeHtmlAttr(p.contactEmail || "")}" type="email" placeholder="example@company.co.jp" /></label>
          <label class="client-form-row">担当者名<input data-partner-field="contactName" value="${escapeHtmlAttr(p.contactName || "")}" /></label>
          <label class="client-form-row">社内担当者<select data-partner-field="contractOwner" class="pj-input" style="width:100%;">${internalStaffSelectOptionsHtml(p.contractOwner || "")}</select></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <div class="client-form-row"><span>支払条件 ${req}</span>
            <select data-search-dropdown-off="1" data-partner-field="paymentTerms">
              ${PAYMENT_TERMS_OPTIONS.map((v) => `<option ${p.paymentTerms === v ? "selected" : ""}>${v}</option>`).join("")}
            </select>
          </div>
          <div class="client-form-row"><span>支払方法 ${req}</span>
            <div class="radio-group client-form-radio-group">
              ${["銀行振込","口座振替","クレジットカード"].map((v) => `<label><input type="radio" name="partnerPayMethod" value="${v}" ${(p.paymentMethod || "銀行振込") === v ? "checked" : ""} data-partner-field="paymentMethod" /><span>${v}</span></label>`).join("")}
            </div>
          </div>
          <div class="client-form-row"><span>振込手数料負担 ${req}</span>
            <div class="radio-group client-form-radio-group">
              <label><input type="radio" name="partnerTransferFee" value="先方" ${p.transferFeeBurden === "先方" ? "checked" : ""} data-partner-field="transferFeeBurden" /><span>先方</span></label>
              <label><input type="radio" name="partnerTransferFee" value="当方" ${p.transferFeeBurden === "当方" ? "checked" : ""} data-partner-field="transferFeeBurden" /><span>当方</span></label>
            </div>
          </div>
          <div class="client-form-row"><span>書類送付方法 ${req}</span>
            ${deliveryMethodCheckboxGroupHtml(p.deliveryMethod, "partner")}
          </div>
          <div class="client-form-row"><span>振込先口座</span>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;width:100%;">
              <input data-partner-field="bankName" value="${escapeHtmlAttr(p.bankName || "")}" placeholder="銀行名" />
              <input data-partner-field="branchName" value="${escapeHtmlAttr(p.branchName || "")}" placeholder="支店名" />
              <input data-partner-field="accountType" value="${escapeHtmlAttr(p.accountType || "")}" placeholder="口座種別（普通/当座）" />
              <input data-partner-field="accountNumber" value="${escapeHtmlAttr(p.accountNumber || "")}" placeholder="口座番号" />
              <input data-partner-field="accountHolder" value="${escapeHtmlAttr(p.accountHolder || "")}" placeholder="口座名義" style="grid-column:1 / -1;" />
            </div>
          </div>
          <div class="client-form-row"><span>源泉徴収区分 ${req}</span>
            <div class="radio-group client-form-radio-group">
              ${["なし","源泉徴収（復興特別所得税あり）","源泉徴収（復興特別所得税なし）"].map((v) => `<label><input type="radio" name="partnerWithholding" value="${v}" ${(p.withholdingTax || "なし") === v ? "checked" : ""} data-partner-field="withholdingTax" />${v}</label>`).join("")}
            </div>
          </div>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          ${clientFormDocStatusRowHtml("data-partner-field", "ndaStatus", "NDA締結", p.ndaStatus, isEdit && canManageClientPartnerMaster())}
          ${clientFormDocStatusRowHtml("data-partner-field", "contractStatus", "業務委託契約書締結", p.contractStatus, isEdit && canManageClientPartnerMaster())}
          <label class="client-form-row">本契約の目的<textarea data-partner-field="ndaPurpose" rows="2" placeholder="NDA・業務委託基本契約書の目的条項に反映されます">${escapeHtml(p.ndaPurpose || "")}</textarea></label>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">社内メモ<textarea data-partner-field="notes" rows="3">${escapeHtml(p.notes || "")}</textarea></label>
          <label class="client-form-row">タグ<input data-partner-field="tags" value="${escapeHtmlAttr(p.tags || "")}" placeholder="カンマ区切り" /></label>
          <div class="client-form-row"><span>初回登録日</span>
            <div class="client-date-time-row">
              <input type="date" data-partner-field="firstRegisteredDate" value="${p.firstRegisteredDate || ""}" />
              <input type="time" data-partner-field="firstRegisteredTime" value="${p.firstRegisteredTime || ""}" step="60" />
            </div>
          </div>
        </div>
        <div class="client-form-divider"></div>
        <div class="client-form-section">
          <label class="client-form-row">法人番号<input data-partner-field="corporateNumber" value="${escapeHtmlAttr(p.corporateNumber || "")}" /></label>
          <div class="client-form-row"><span>適格請求書発行事業者 ${req}</span>
            <div class="radio-group client-form-radio-group">
              <label><input type="radio" name="partnerInvoiceIssuer" value="該当する" ${p.invoiceIssuer === "該当する" ? "checked" : ""} data-partner-field="invoiceIssuer" /><span>該当する</span></label>
              ${isEdit ? `<label><input type="radio" name="partnerInvoiceIssuer" value="該当しない" ${p.invoiceIssuer === "該当しない" ? "checked" : ""} data-partner-field="invoiceIssuer" /><span>該当しない</span></label>` : ""}
            </div>
          </div>
          <label class="client-form-row"><span>登録番号${isEdit ? "" : ` ${req}`}</span><input data-partner-field="invoiceIssuerNumber" value="${escapeHtmlAttr(p.invoiceIssuerNumber || "")}" placeholder="T + 13桁の数字" /></label>
        </div>
      </div>
      <div class="composer-foot client-create-foot-actions">
        <button type="button" class="${ndaExportBtnClass}" data-partner-export-doc="nda">NDAを出力（.docx）</button>
        <button type="button" class="${gyomuExportBtnClass}" data-partner-export-doc="gyomu-itaku">業務委託基本契約書を出力（.docx）</button>
        ${isEdit && canManageClientPartnerMaster() ? `<button type="button" class="inline-btn ghost" data-partner-delete-request="1">削除申請</button>` : ""}
        <button class="inline-btn ghost" data-close-partner="1"${p._submitting ? " disabled" : ""}>キャンセル</button>
        <button class="inline-btn" data-save-partner="1"${p._submitting ? ' disabled aria-busy="true"' : ""}>${p._submitting ? "登録中…" : (isEdit ? "更新申請" : "登録")}</button>
      </div>
    </aside>
  `;
}

/** 振込先は1列（bank_info）に5項目を入れる。通常は従来どおり空白区切り、空欄の項目や空白を含む名前が
 *  あるときは「｜」区切りで位置を保つ（空白区切りだと支店名が空のとき口座種別が支店名の位置にずれ、
 *  空白を含む銀行名・支店名も分解が崩れていた） */
const PARTNER_BANK_INFO_SEP = "｜";

function splitPartnerBankInfo(bankInfo) {
  const raw = String(bankInfo || "").trim();
  if (raw.includes(PARTNER_BANK_INFO_SEP)) {
    const p = raw.split(PARTNER_BANK_INFO_SEP).map((x) => x.trim());
    return { bankName: p[0] || "", branchName: p[1] || "", accountType: p[2] || "", accountNumber: p[3] || "", accountHolder: p.slice(4).join(PARTNER_BANK_INFO_SEP) };
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  return {
    bankName: parts[0] || "",
    branchName: parts[1] || "",
    accountType: parts[2] || "",
    accountNumber: parts[3] || "",
    accountHolder: parts.slice(4).join(" ")
  };
}

function composePartnerBankInfo(partnerDraft) {
  const parts = [
    partnerDraft.bankName,
    partnerDraft.branchName,
    partnerDraft.accountType,
    partnerDraft.accountNumber,
    partnerDraft.accountHolder
  ].map((x) => String(x || "").trim());
  if (!parts.some(Boolean)) return "";
  // 途中に空欄がある（後ろの項目がずれる）か、口座名義（末尾）以外に空白入りがあれば位置を保てる区切りにする。
  // 末尾側だけ空・名義の空白は従来形式でも正しく読めるので、従来どおり空白区切りのまま
  let last = parts.length - 1;
  while (last > 0 && !parts[last]) last -= 1;
  const needsSep =
    parts.slice(0, last).some((x) => !x) ||
    parts.slice(0, 4).some((x) => /\s/.test(x)) ||
    parts.some((x) => x.includes(PARTNER_BANK_INFO_SEP));
  return needsSep ? parts.join(PARTNER_BANK_INFO_SEP) : parts.filter(Boolean).join(" ");
}

function clientDirectoryToolsHtml() {
  return `
    ${csvTemplateToolHtml("data-csv-download-clients")}
    ${csvImportToolHtml("data-csv-import-clients")}
    ${clientListCsvButtonHtml("client")}
    ${canManageClientPartnerMaster() ? `<button class="cl-btn cl-btn-primary" data-open-client-manager="new" type="button">新規クライアント登録</button>` : ""}
  `;
}

function clientDirectoryBodyHtml() {
  const st = uiState.clientList;
  const ownerSeen = new Set();
  const contractOwners = [];
  clients.forEach((c) => {
    const co = String(c.contractOwner || "").trim();
    if (!co || ownerSeen.has(co)) return;
    ownerSeen.add(co);
    contractOwners.push(co);
  });
  contractOwners.sort((a, b) => a.localeCompare(b, "ja"));
  const list = clientListRows();
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / st.perPage));
  if (st.page > totalPages) st.page = totalPages;
  const start = (st.page - 1) * st.perPage;
  const pageRows = list.slice(start, start + st.perPage);
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + st.perPage, total);
  const projectCounts = clientProjectCountMap();
  const cardRowsHtml = pageRows.map((c) => {
    const pc = projectCounts.get(c.id) || 0;
    const tagsLine = clientTagsArray(c).join(", ");
    const status = normalizeMasterApprovalStatus(c.approvalStatus);
    const ndaOk = c.ndaStatus === "済";
    const ctrOk = c.contractStatus === "済";
    // 新規登録直後の楽観追加行（id="ctmp-…"）は dbId 未確定＝詳細ページへ飛べないため、
    // 従来どおり編集モーダルを開く導線にフォールバックする（POST 応答で dbId が付けば次の描画で詳細導線になる）
    const dbId = Number(c.dbId ?? String(c.id).replace(/^c/, ""));
    const hasDbId = Number.isFinite(dbId) && dbId > 0;
    const openAttr = hasDbId
      ? `data-client-flow-detail="${escapeHtmlAttr(`client:${dbId}`)}"`
      : `data-client-action="edit:${escapeHtmlAttr(c.id)}"`;
    return `
      <article class="cl-client-card${c.archived === true ? " cl-client-card--archived" : ""}${c.salesHidden ? " cl-client-card--limited" : ""}" ${openAttr}>
        <div class="cl-client-card-main">
          <div class="cl-client-card-line">
            ${clientDirectoryApprovalPillHtml(status, c.archived === true)}
            <button class="cl-client-card-name" ${openAttr} type="button">${escapeHtml(c.name)}</button>
            ${clientListLimitedBadgeHtml(c.salesHidden)}
            <div class="cl-client-meta">
              <span class="cl-client-meta-group">
                <span class="cl-client-meta-item cl-client-meta-item--customer-no">顧客番号：${escapeHtml(c.customerNo || (status === "承認済" ? "—" : "承認時に発番"))}</span>
                <span class="cl-client-meta-item">案件 ${pc}件</span>
              </span>
              <span class="cl-client-meta-group">
                <span class="cl-client-contracts">${clientDirectoryContractBadgesHtml(ndaOk, ctrOk)}</span>
              </span>
              <span class="cl-client-meta-group">
                <span class="cl-client-meta-item">社内担当者：${escapeHtml(c.contractOwner || "—")}</span>
                <span class="cl-client-meta-item">タグ：${escapeHtml(tagsLine || "—")}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="cl-client-card-actions">
          ${hasDbId
            ? clientDirectoryCardActionsHtml("client", dbId, status, c.archived === true, true)
            : `<button class="cl-client-card-edit" data-client-action="edit:${escapeHtmlAttr(c.id)}" type="button">編集</button>`}
        </div>
      </article>`;
  }).join("");
  // 検索欄の各項目。出す項目と並び順は固定項目編集で人ごとに変えられる（15z-list-filter-fields.js）
  const f = st.filters;
  const fieldsHtml = {
    keyword: `<label class="cl-filter-group cl-client-keyword-group"><span>キーワード検索</span><input type="search" class="cl-filter-input" data-client-list-filter="keyword" data-ime-search value="${escapeHtmlAttr(f.keyword || "")}" placeholder="社名・先方担当・社内担当・タグ・住所など" /></label>`,
    approval: `<label class="cl-filter-group cl-client-approval-group"><span>申請状況</span><select class="cl-filter-input" data-search-dropdown-off="1" data-client-list-filter="approval"><option value="">すべて</option>${CLIENT_APPROVAL_STATUS_OPTIONS.map((s) => `<option value="${escapeHtmlAttr(s)}" ${f.approval === s ? "selected" : ""}>${escapeHtml(s)}</option>`).join("")}<option value="archived" ${f.approval === "archived" ? "selected" : ""}>アーカイブ</option></select></label>`,
    contractOwner: `<label class="cl-filter-group cl-client-contract-owner-group"><span>社内担当者</span><select class="cl-filter-input" data-client-list-filter="contractOwner"><option value="">すべて</option>${contractOwners.map((o) => `<option value="${escapeHtmlAttr(o)}" ${f.contractOwner === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}</select></label>`,
    ...clientListExtraFilterFieldsHtml("data-client-list-filter", f, "基本契約"),
    sort: projectCountSortSelectHtml("data-client-list-filter", f.sort)
  };
  return `
        <div class="cl-filter-area" aria-label="検索条件">
          <div class="cl-filter-row cl-client-filter-row-single">
            ${listFilterFieldsShownKeys(CLIENT_LIST_FILTER_SPEC).map((k) => fieldsHtml[k]).join("")}
            <div class="cl-filter-actions cl-client-filter-reset-inline">${listFilterFieldsEditButtonHtml(CLIENT_LIST_FILTER_SPEC)}<button class="cl-btn cl-btn-outline" data-client-list-reset="1" type="button">リセット</button></div>
        </div>
        </div>
        ${listFilterFieldsEditorHtml(CLIENT_LIST_FILTER_SPEC)}
        <div class="cl-content cl-client-list-cards">
          ${
            uiState.heavyDataLoading
              ? `<div class="cl-board-empty cl-board-empty--heavy-load">${flowPilotNowLoadingBlock({
                  inline: true,
                  subtitle: "クライアント・案件情報を読み込んでいます"
                })}</div>`
              : pageRows.length === 0 && !clientListDeletedRows("client", f).length
                ? `<div class="cl-board-empty">該当するクライアントがありません</div>`
                : cardRowsHtml
          }
          ${uiState.heavyDataLoading || st.page !== totalPages ? "" : clientListDeletedCardsHtml("client", f)}
        </div>
        <div class="cl-pagination">
          <span>表示 ${from}〜${to} 件 / 合計 ${total} 件</span>
          <div class="cl-page-controls">
            <select class="cl-per-page" data-search-dropdown-off="1" data-client-list-per-page="1"><option value="20" ${st.perPage === 20 ? "selected" : ""}>20件</option><option value="50" ${st.perPage === 50 ? "selected" : ""}>50件</option><option value="100" ${st.perPage === 100 ? "selected" : ""}>100件</option></select>
            <div class="cl-page-btns">${clientPageButtons(totalPages)}</div>
          </div>
        </div>
  `;
}

/** 担当者・支社の遅延ロード完了時に再描画すべき画面か。
 *  project-docs も対象: 書類の宛先住所（clientRecipientAddress）が支社マスタを引くため、
 *  ここに含めないと「案件一覧の［書類］から直接開く／書類ページをリロードする」経路で
 *  支社マスタ未取得のまま描画され、支社を選んでいてもクライアントマスタの住所が出たきり
 *  ロード完了後も直らない（別の操作で render() が走るまで気づけない）。 */
function clientDirectoryLazyDataNeedsRender() {
  return ["client-list", "partner-list", "client-others", "client-detail", "project-detail", "project-docs"]
    .includes(currentRoute().page);
}

/** 営業向け「取引先一覧」統合ページ。クライアント／パートナー／その他をタブ切替（契約一覧の cl-class-tabs を流用）。
 *  tab = "client" | "partner" | "others"。各タブは #client-list / #partner-list / #client-others
 *  ハッシュそのままで、フィルタ・ページングの data 属性ハンドラ（data-client-list-* 等）を維持する。
 *  担当者・支社のタブは 2026-10-28 に廃止し、各取引先の詳細（担当者・支社タブ）へ移した。 */
function clientPartnerListPage(tab) {
  const canSeeOthers = canManageClientPartnerMaster();
  const active = ["partner", ...(canSeeOthers ? ["others"] : [])].includes(tab) ? tab : "client";
  const clientCount = clientListFilteredRows().length;
  const partnerCount = partnerListFilteredRows().length;
  const tools =
    active === "partner" ? partnerDirectoryToolsHtml()
    : active === "others" ? clientOthersToolsHtml()
    : clientDirectoryToolsHtml();
  const body =
    active === "partner" ? partnerDirectoryBodyHtml()
    : active === "others" ? clientOthersBodyHtml()
    : clientDirectoryBodyHtml();
  return `
    <div class="page cl-page">
      <div class="cl-page-wrap cl-page-wrap--alike-pj">
        <div class="cl-title-area cl-title-area-row">
          <h2>取引先一覧</h2>
          <div class="cl-client-list-tools">${tools}</div>
        </div>
        <div class="cl-class-tabs" role="tablist" aria-label="クライアント／パートナー切替">
          <a role="tab" class="cl-class-tab cl-class-tab--client ${active === "client" ? "active" : ""}" aria-selected="${active === "client" ? "true" : "false"}" href="#client-list">クライアント<span class="cl-class-tab-count">${clientCount}</span></a>
          <a role="tab" class="cl-class-tab cl-class-tab--partner ${active === "partner" ? "active" : ""}" aria-selected="${active === "partner" ? "true" : "false"}" href="#partner-list">パートナー<span class="cl-class-tab-count">${partnerCount}</span></a>
          ${canSeeOthers ? `<a role="tab" class="cl-class-tab ${active === "others" ? "active" : ""}" aria-selected="${active === "others" ? "true" : "false"}" href="#client-others" title="営業非表示（E区分・家賃など管理部のみが扱う取引先）">その他<span class="cl-class-tab-count">${clientOthersRows().length}</span></a>` : ""}
        </div>
        ${body}
      </div>
    </div>
  `;
}

/** 更新日（renewed_date＝次回更新日そのもの）までの残日数。解約・更新日不明は null。
 *  更新日は将来日として登録される運用（1ヶ月前通知Cronも同様に扱う）。+1年はしない。 */
function domainDaysRemaining(d) {
  if (!d || d.status === "解約") return null;
  const next = d.renewedDate ? new Date(`${String(d.renewedDate).slice(0, 10)}T00:00:00`) : null;
  if (!next || Number.isNaN(next.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((next.getTime() - today.getTime()) / 86400000);
  const y = next.getFullYear();
  const mo = String(next.getMonth() + 1).padStart(2, "0");
  const da = String(next.getDate()).padStart(2, "0");
  return { days, nextYmd: `${y}/${mo}/${da}` };
}

function domainListRows() {
  const st = uiState.domainList;
  const f = st.filters || {};
  const rows = domains.filter((d) => {
    // 状況は複数選択。選択が空なら全件（フィルタ無効）扱い。
    if (Array.isArray(f.statuses) && f.statuses.length > 0 && !f.statuses.includes(d.status || "契約")) return false;
    if (f.owner) {
      const o = String(d.owner || "").trim();
      if (o !== String(f.owner).trim()) return false;
    }
    if (f.clientName) {
      const c = String(d.clientName || "").trim();
      if (c !== String(f.clientName).trim()) return false;
    }
    if (f.keyword) {
      const hay = [
        d.domain, d.subdomain, d.registrarId, d.clientName, d.owner, d.projectNote, d.notes, d.status, d.service, d.services
      ].join(" ").toLowerCase();
      if (!hay.includes(String(f.keyword).toLowerCase())) return false;
    }
    return true;
  });
  return domainListSort(rows);
}

/** 状況の並び順インデックス（DOMAIN_STATUS_SORT_ORDER の順）。未知は末尾扱い。 */
function domainStatusRank(status) {
  const i = DOMAIN_STATUS_SORT_ORDER.indexOf(String(status || "契約"));
  return i < 0 ? DOMAIN_STATUS_SORT_ORDER.length : i;
}

/** ソート用の比較値。残日数・状況は数値、それ以外は文字列。値なしは "" を返し常に末尾へ送る */
function domainSortValue(d, key) {
  if (key === "remain") {
    const rem = domainDaysRemaining(d);
    return rem ? rem.days : "";
  }
  if (key === "status") return domainStatusRank(d.status);
  if (key === "domain") return String(d.domain || d.subdomain || "");
  if (key === "renewedDate") return String(d[key] || "");
  const map = {
    service: d.service, registrarId: d.registrarId,
    clientName: d.clientName, owner: d.owner, projectNote: d.projectNote
  };
  return String(map[key] ?? "");
}

/** 既定の並び（列未指定時）: 状況順（DOMAIN_STATUS_SORT_ORDER）→ 同状況内は残日数の少ない順。
 *  残日数なし（取得申請など更新日未設定）は同状況内の末尾へ。 */
function domainDefaultCompare(a, b) {
  const ra = domainStatusRank(a.status);
  const rb = domainStatusRank(b.status);
  if (ra !== rb) return ra - rb;
  const da = domainDaysRemaining(a);
  const db = domainDaysRemaining(b);
  const va = da ? da.days : null;
  const vb = db ? db.days : null;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  return va - vb;
}

/** domainList.sortKey/sortAsc に従って並べ替え（空値は方向に関わらず末尾）。
 *  sortKey 未指定なら既定ソート（状況順→残日数昇順）。 */
function domainListSort(rows) {
  const { sortKey, sortAsc } = uiState.domainList;
  if (!sortKey) return rows.slice().sort(domainDefaultCompare);
  const dir = sortAsc ? 1 : -1;
  const isEmpty = (v) => v === "" || v === null || v === undefined;
  return rows.slice().sort((a, b) => {
    const va = domainSortValue(a, sortKey);
    const vb = domainSortValue(b, sortKey);
    const ea = isEmpty(va);
    const eb = isEmpty(vb);
    if (ea && eb) return 0;
    if (ea) return 1;
    if (eb) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
    return String(va).localeCompare(String(vb), "ja", { numeric: true }) * dir;
  });
}

function domainPageButtons(totalPages) {
  const page = uiState.domainList.page;
  if (totalPages <= 1) return "";
  const items = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (totalPages > 9 && i !== 1 && i !== totalPages && Math.abs(i - page) > 2) {
      if (items[items.length - 1] !== "…") items.push("…");
      continue;
    }
    items.push(i);
  }
  return `
    <button class="cl-page-btn" data-domain-list-page="${page - 1}" ${page === 1 ? "disabled" : ""}>＜</button>
    ${items.map((p) => p === "…"
      ? `<span class="cl-page-ellipsis">…</span>`
      : `<button class="cl-page-btn ${p === page ? "active" : ""}" data-domain-list-page="${p}">${p}</button>`).join("")}
    <button class="cl-page-btn" data-domain-list-page="${page + 1}" ${page === totalPages ? "disabled" : ""}>＞</button>
  `;
}

function domainStatusPillClass(status) {
  if (status === "解約") return "dm-status--canceled";
  if (status === "解約申請有") return "dm-status--cancel-req";
  if (status === "契約確認中") return "dm-status--checking";
  if (status === "契約更新可") return "dm-status--renewable";
  if (status === "取得申請") return "dm-status--requested";
  return "dm-status--active";
}

/** 状況フィルタのトリガー表示ラベル（選択状況を要約）。 */
function domainStatusFilterSummary(statuses) {
  const all = DOMAIN_STATUS_OPTIONS;
  const sel = Array.isArray(statuses) ? statuses.filter((s) => all.includes(s)) : [];
  if (sel.length === 0 || sel.length === all.length) return "すべて";
  const excluded = all.filter((s) => !sel.includes(s));
  if (excluded.length === 1) return `${excluded[0]}以外`;
  if (sel.length <= 2) return sel.join("・");
  return `${sel.length}件選択`;
}

/** 状況フィルタ＝複数選択プルダウン（バックドロップで外側クリック閉じ）。 */
function domainStatusFilterDropdownHtml(st) {
  const open = !!st.statusMenuOpen;
  const statuses = Array.isArray(st.filters.statuses) ? st.filters.statuses : [];
  const summary = domainStatusFilterSummary(statuses);
  const boxes = DOMAIN_STATUS_OPTIONS.map((v) =>
    `<label class="cl-inline-check"><input type="checkbox" data-domain-status-filter="${escapeHtmlAttr(v)}" ${statuses.includes(v) ? "checked" : ""} /><span>${escapeHtml(v)}</span></label>`
  ).join("");
  return `
    <div class="cl-filter-group dm-status-filter-group"><span>状況（複数選択可）</span>
      <div class="dm-status-dd ${open ? "open" : ""}">
        <button type="button" class="cl-filter-input dm-status-dd-trigger" data-domain-status-menu-toggle="1" aria-haspopup="true" aria-expanded="${open ? "true" : "false"}"><span class="dm-status-dd-label">${escapeHtml(summary)}</span><span class="dm-status-dd-caret">▾</span></button>
        ${open ? `<div class="dm-status-dd-backdrop" data-domain-status-menu-close="1"></div><div class="dm-status-dd-panel">${boxes}</div>` : ""}
      </div>
    </div>`;
}

/**
 * ドメイン一覧の遅延読み込み。/api/domains（GET全件）はレジストラ認証情報を含み
 * 閲覧監査ログが記録されるため、起動時ではなく domain-list ページを開いた時だけ取得する。
 */
async function fetchDomainsData() {
  if (!authToken || uiState.domainsLoading) return;
  uiState.domainsLoading = true;
  const tokenAtStart = authToken;
  try {
    const res = await fetch("/api/domains", { headers: { Authorization: `Bearer ${tokenAtStart}` } });
    if (!res.ok) throw new Error(String(res.status));
    const ddata = await res.json();
    // 応答待ちの間にログアウト/再ログインしていたら書き戻さない（レジストラ認証情報入りの一覧が次の人のメモリに戻る）
    if (!authToken || authToken !== tokenAtStart) {
      uiState.domainsLoading = false;
      return;
    }
    const rows = Array.isArray(ddata.domains) ? ddata.domains : [];
    domains.length = 0;
    rows.forEach((d) => domains.push({
      id: `dm${d.id}`, dbId: d.id,
      status: d.status || "契約",
      service: d.service || "",
      services: d.services || "",
      subdomain: d.subdomain || "",
      domain: d.domain || "",
      registrarId: d.registrar_id || "",
      registrarPassword: d.registrar_password || "",
      clientName: d.client_name || "",
      owner: d.owner != null ? String(d.owner).trim() : "",
      projectNote: d.project_note || "",
      renewedDate: pgDateFieldToYmd(d.renewed_date) || "",
      notes: d.notes || ""
    }));
    uiState.domainsLoadError = false;
  } catch {
    uiState.domainsLoadError = true;
    // 原価取込用の軽量版（状況・ID・更新日・備考なし）が残っていると一覧に出て、編集保存の全項目 PUT で
    // 状況が「契約」・ID や備考が空に上書きされる。全件が取れなかったときは一覧に何も出さない
    domains.length = 0;
  }
  // 失敗時も loaded を立てて再描画ごとの無限リトライを防ぐ（allowed-tools と同方式）
  uiState.domainsLoaded = true;
  uiState.domainsLoading = false;
  render();
}

/**
 * 原価取込ウィザードのドメイン列→案件照合・担当営業（原価箱）解決用の軽量データ
 * （認証情報なし・監査ログなし）。全件を取得済み/取得中ならそちらを使う。
 * 失敗しても名前照合にフォールバックするだけなので握りつぶす。
 * 取得中の呼び出しには進行中の Promise を返す。お名前取込は貼り付け直後に `domains` を
 * 読むため、待たずに走ると全行「担当営業不明」になり原価箱への自動計上が丸ごと落ちる。
 */
function fetchDomainsLightForCostImport() {
  if (!authToken || uiState.domainsLoaded || uiState.domainsLoading) return Promise.resolve();
  if (uiState.domainsLightRequested) return uiState.domainsLightPromise || Promise.resolve();
  uiState.domainsLightRequested = true;
  const tokenAtStart = authToken;
  uiState.domainsLightPromise = (async () => {
    try {
      const res = await fetch("/api/domains?fields=link", { headers: { Authorization: `Bearer ${tokenAtStart}` } });
      if (!res.ok) return;
      const ddata = await res.json().catch(() => ({}));
      const rows = Array.isArray(ddata.domains) ? ddata.domains : [];
      if (uiState.domainsLoaded || uiState.domainsLoading) return; // 全件が先行していれば上書きしない
      // 応答待ちの間にログアウト/再ログインしていたら書き戻さない（doLogout が消した一覧が復活する）
      if (!authToken || authToken !== tokenAtStart) return;
      domains.length = 0;
      rows.forEach((d) => domains.push({
        id: `dm${d.id}`, dbId: d.id,
        subdomain: d.subdomain || "",
        domain: d.domain || "",
        clientName: d.client_name || "",
        projectNote: d.project_note || "",
        // お名前ドットコム取込が担当営業の原価箱を特定するのに使う（落とすと全行「担当営業不明」になる）
        owner: d.owner != null ? String(d.owner).trim() : ""
      }));
    } catch { /* 照合は案件名フォールバックで続行 */ }
  })();
  return uiState.domainsLightPromise;
}

function domainListPage() {
  if (!uiState.domainsLoaded && !uiState.domainsLoading) void fetchDomainsData();
  const st = uiState.domainList;
  const ownerSeen = new Set();
  const owners = [];
  domains.forEach((d) => {
    const o = String(d.owner || "").trim();
    if (!o || ownerSeen.has(o)) return;
    ownerSeen.add(o);
    owners.push(o);
  });
  owners.sort((a, b) => a.localeCompare(b, "ja"));
  const clientSeen = new Set();
  const clientNames = [];
  domains.forEach((d) => {
    const c = String(d.clientName || "").trim();
    if (!c || clientSeen.has(c)) return;
    clientSeen.add(c);
    clientNames.push(c);
  });
  clientNames.sort((a, b) => a.localeCompare(b, "ja"));
  const dsortIcon = (key) => st.sortKey === key
    ? `<span class="sort-icon">${st.sortAsc ? "▲" : "▼"}</span>`
    : `<span class="sort-icon">↕</span>`;
  const list = domainListRows();
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / st.perPage));
  if (st.page > totalPages) st.page = totalPages;
  const start = (st.page - 1) * st.perPage;
  const pageRows = list.slice(start, start + st.perPage);
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + st.perPage, total);
  if (!st.selected || typeof st.selected !== "object") st.selected = {};
  const selectedCount = Object.values(st.selected).filter(Boolean).length;
  const headChecked = pageRows.length > 0 && pageRows.every((r) => st.selected[r.id]);
  const headIndeterminate = pageRows.some((r) => st.selected[r.id]) && !headChecked;
  const rowsHtml = pageRows.map((d) => {
    const rem = domainDaysRemaining(d);
    let remCell = '<span class="dm-remain dm-remain--none">—</span>';
    if (rem) {
      const cls = rem.days < 0 ? "dm-remain--over" : rem.days <= 30 ? "dm-remain--soon" : "dm-remain--ok";
      const label = rem.days < 0 ? `期限切れ ${Math.abs(rem.days)}日` : `残${rem.days}日`;
      remCell = `<span class="dm-remain ${cls}" title="次回更新 ${rem.nextYmd}">${label}</span>`;
    }
    const fullDomain = d.subdomain
      ? `${escapeHtml(d.subdomain)}`
      : `<span class="dm-muted">—</span>`;
    const servicesText = domainServicesDisplay(d.services);
    return `
      <tr class="dm-row" data-domain-action="edit:${d.id}">
        <td class="td-check"><input type="checkbox" data-domain-row-check="${d.id}" ${st.selected[d.id] === true ? "checked" : ""} /></td>
        <td><span class="dm-status ${domainStatusPillClass(d.status)}">${escapeHtml(d.status || "契約")}</span></td>
        <td>${d.service ? escapeHtml(d.service) : '<span class="dm-muted">—</span>'}</td>
        <td class="dm-domain-cell">
          <span class="dm-domain-main">${d.domain ? escapeHtml(d.domain) : '<span class="dm-muted">（未設定）</span>'}</span>
          <span class="dm-domain-sub">${fullDomain}</span>
        </td>
        <td class="dm-services-cell">${servicesText ? escapeHtml(servicesText) : '<span class="dm-muted">—</span>'}</td>
        <td>${d.clientName ? escapeHtml(d.clientName) : '<span class="dm-muted">—</span>'}</td>
        <td>${d.owner ? escapeHtml(d.owner) : '<span class="dm-muted">—</span>'}</td>
        <td>${d.projectNote ? escapeHtml(d.projectNote) : '<span class="dm-muted">—</span>'}</td>
        <td class="dm-date-cell">${d.renewedDate ? isoYmdToSlashDisplay(d.renewedDate) : '<span class="dm-muted">—</span>'}</td>
        <td>${remCell}</td>
        <td class="dm-actions-cell"><button class="cl-client-card-edit" data-domain-action="edit:${d.id}" type="button">編集</button></td>
      </tr>`;
  }).join("");
  return `
    <div class="page cl-page">
      <div class="cl-page-wrap cl-page-wrap--alike-pj">
        <div class="cl-title-area cl-title-area-row">
          <h2>ドメイン</h2>
          <div class="cl-client-list-tools">
            <button class="cl-btn cl-btn-outline" data-domain-bulk-delete="1" type="button"${selectedCount === 0 ? " disabled" : ""} title="一覧で選択した行を削除">削除</button>
            <button class="cl-btn cl-btn-outline" data-domain-csv-export="1" type="button">CSVエクスポート</button>
            <button class="cl-btn cl-btn-outline" data-domain-csv-template="1" type="button">CSVテンプレート</button>
            <label class="cl-btn cl-btn-outline cl-client-list-csv-label">CSVインポート<input type="file" accept=".csv" data-domain-csv-import="1" style="display:none;" /></label>
            <button class="cl-btn cl-btn-primary" data-open-domain-manager="new" type="button">新規ドメイン登録</button>
          </div>
        </div>
        <div class="cl-filter-area" aria-label="検索条件">
          <div class="cl-filter-row cl-client-filter-row-single">
            <label class="cl-filter-group cl-grow-1"><span>キーワード検索</span><input type="search" class="cl-filter-input" data-domain-list-filter="keyword" value="${escapeHtmlAttr(st.filters.keyword || "")}" placeholder="ドメイン・サブドメ・ID・クライアント・案件・備考など" /></label>
            ${domainStatusFilterDropdownHtml(st)}
            <label class="cl-filter-group"><span>担当</span><select class="cl-filter-input" data-search-dropdown-off="1" data-domain-list-filter="owner"><option value="">すべて</option>${owners.map((o) => `<option value="${escapeHtmlAttr(o)}" ${st.filters.owner === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}</select></label>
            <label class="cl-filter-group dm-filter-client"><span>クライアント</span><select class="cl-filter-input" data-domain-list-filter="clientName"><option value="">すべて</option>${clientNames.map((c) => `<option value="${escapeHtmlAttr(c)}" ${st.filters.clientName === c ? "selected" : ""}>${escapeHtml(c)}</option>`).join("")}</select></label>
            <div class="cl-filter-actions cl-client-filter-reset-inline"><button class="cl-btn cl-btn-outline" data-domain-list-reset="1" type="button">リセット</button></div>
          </div>
        </div>
        <div class="pj-bulk-bar ${selectedCount > 0 ? "show" : ""}">
          <span>${selectedCount}件選択中</span>
        </div>
        <div class="cl-content dm-table-wrap fp-grid-scroll">
          ${
            !uiState.domainsLoaded
              ? `<div class="cl-board-empty cl-board-empty--heavy-load">${flowPilotNowLoadingBlock({ inline: true, subtitle: "ドメイン情報を読み込んでいます" })}</div>`
              : pageRows.length === 0
                ? `<div class="cl-board-empty">${uiState.domainsLoadError ? "ドメイン一覧の取得に失敗しました。再読み込みしてください" : "該当するドメインがありません"}</div>`
                : `<table class="dm-table" data-fp-table="domains">
                     <thead>
                       <tr>
                         <th class="th-check"><input type="checkbox" data-domain-check-all="1" ${headChecked ? "checked" : ""} ${headIndeterminate ? 'data-indeterminate="1"' : ""} /></th>
                         <th data-domain-sort="status">状況 ${dsortIcon("status")}</th>
                         <th data-domain-sort="service">登録業者 ${dsortIcon("service")}</th>
                         <th data-domain-sort="domain">ドメイン / サブドメ ${dsortIcon("domain")}</th>
                         <th>サービス</th>
                         <th data-domain-sort="clientName">クライアント ${dsortIcon("clientName")}</th>
                         <th data-domain-sort="owner">担当 ${dsortIcon("owner")}</th>
                         <th data-domain-sort="projectNote">案件 ${dsortIcon("projectNote")}</th>
                         <th data-domain-sort="renewedDate">更新日 ${dsortIcon("renewedDate")}</th>
                         <th data-domain-sort="remain">残日数 ${dsortIcon("remain")}</th>
                         <th></th>
                       </tr>
                     </thead>
                     <tbody>${rowsHtml}</tbody>
                   </table>`
          }
        </div>
        <div class="cl-pagination">
          <span>表示 ${from}〜${to} 件 / 合計 ${total} 件</span>
          <div class="cl-page-controls">
            <select class="cl-per-page" data-search-dropdown-off="1" data-domain-list-per-page="1"><option value="20" ${st.perPage === 20 ? "selected" : ""}>20件</option><option value="50" ${st.perPage === 50 ? "selected" : ""}>50件</option><option value="100" ${st.perPage === 100 ? "selected" : ""}>100件</option></select>
            <div class="cl-page-btns">${domainPageButtons(totalPages)}</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function pageHtml(route) {
  if (route.page === "project-detail") return detailPage(route.id);
  if (route.page === "project-docs") return projectDocsPage(route.id, route.tab);
  if (route.page === "project-mail") return projectMailComposePage(route.id, route.docs);
  if (route.page === "account-settings" || route.page === "account-admin") {
    uiState.settingsMenu = "account-settings";
    const t = (route.searchParams && route.searchParams.get("tab")) || "";
    // 旧「承認設定(approval-bindings)」URLは「承認フロー」に統合。部署設定(dept)は独立タブ
    if (t === "approval" || t === "approval-bindings") uiState.accountSettingsTab = "approval";
    else if (t === "dept" || t === "account") uiState.accountSettingsTab = t;
    else uiState.accountSettingsTab = "account";
    return settingsPage();
  }
  if (route.page === "secret-menu") { return secretMenuPage(); }
  if (route.page === "template-settings") { uiState.settingsMenu = "template-settings"; return settingsPage(); }
  if (route.page === "other-settings") { uiState.settingsMenu = "other-settings"; return settingsPage(); }
  if (route.page === "approval-settings") {
    uiState.settingsMenu = "account-settings";
    uiState.accountSettingsTab = "approval";
    return settingsPage();
  }
  if (route.page === "community") return communityPage();
  if (route.page === "projects") return projectsPage();
  if (route.page === "development") return developmentPage();
  if (route.page === "allowed-tools") return allowedToolsPage();
  if (route.page === "costs") return costsPage();
  if (route.page === "client-list") return clientPartnerListPage("client");
  if (route.page === "partner-list") return clientPartnerListPage("partner");
  if (route.page === "client-others") return clientPartnerListPage("others");
  if (route.page === "client-detail") return clientDetailPage(route);
  if (route.page === "domain-list") return domainListPage();
  if (route.page === "equipment-admin") return equipmentAdminPage();
  if (route.page === "cost-allocation") return costsPage();
  if (route.page === "cost-management") return costsPage();
  if (route.page === "cost-reconcile") return costReconcilePage();
  if (route.page === "invoice-receipt") return invoiceReceiptPage();
  if (route.page === "staff-assignment") return staffAssignmentPage();
  if (route.page === "hours-management") return hoursManagementPage();
  if (route.page === "application-management") return applicationManagementPage();
  if (route.page === "progress-management") return progressManagementPage();
  if (route.page === "workload") return workloadPage();
  if (route.page === "budget-admin") return budgetAdminPage();
  if (route.page === "notice-admin") { uiState.settingsMenu = "other-settings"; return settingsPage(); }
  if (route.page === "other-admin") { uiState.settingsMenu = "other-settings"; return settingsPage(); }
  if (route.page === "quotes") return stagePage("quotes", "見積", "見積書の作成・下書き・送付状況をまとめて管理する", quotesSummary());
  if (route.page === "orders") return stagePage("orders", "請書 / 発注", "請書と発注書だけをまとめて処理する", ordersSummary());
  if (route.page === "invoices") return stagePage("invoices", "請求", "請求書の作成・送付・請求漏れ防止に集中する", [
    { label: `今月要対応（${currentMonth()}以前）`, value: `${projects.filter((p) => billingUrgency(p) === "overdue" && simplifiedInvoiceStatus(p) === "未請求").length}件`, tone: "danger", filterId: "inv_overdue" },
    { label: "未請求", value: `${projects.filter((p) => simplifiedInvoiceStatus(p) === "未請求").length}件`, tone: "warning", filterId: "inv_unbilled" },
    { label: "請求済み", value: `${projects.filter((p) => simplifiedInvoiceStatus(p) === "請求済み").length}件`, filterId: "inv_billed" },
    { label: "入金済み", value: `${projects.filter((p) => simplifiedInvoiceStatus(p) === "入金済み").length}件`, filterId: "inv_paid" }
  ]);
  if (route.page === "payments") return stagePage("payments", "入金", "入金待ち・未確認・長期未確認をまとめて確認する", paymentsSummary());
  // #management = 経営ダッシュボード（推移・原価・月次P/L・予算設定。マスター限定）
  if (route.page === "management") return budgetProgressPage("management");
  if (route.page === "monthly-pl") return monthlyPlPage();
  // #budget-progress = 予算進捗（全体・部署タブ（動的）・個人別。営業メニュー）
  if (route.page === "budget-progress") return budgetProgressPage("budget-progress");
  if (route.page === "settings") { uiState.settingsMenu = "template-settings"; return settingsPage(); }
  return dashboard();
}

/** 一覧の表のスクロール枠（再描画の前後で位置を保つ対象）。pj-table-area=案件・原価・申請・契約など／dm=ドメイン／sa=担当割当 */
const LIST_TABLE_SCROLL_SELECTOR = ".pj-table-area, .dm-table-wrap, .sa-table-wrap";

function renderNow() {
  if (!uiState.auth.isLoggedIn) {
    if (appLayout) {
      appLayout.classList.add("auth-mode");
      uiState.fpMobileNavOpen = false;
    }
    appRoot.innerHTML = `${authPage()}${renderToast()}`;
    bindFpMobileNavChromeOnce();
    renderMobileQuickBottomNav("");
    applyFpMobileDrawerState();
    return;
  }
  if (appLayout) appLayout.classList.remove("auth-mode");
  if (uiState.auth.isLoading) {
    if (navPrimary) navPrimary.innerHTML = "";
    if (navSettings) navSettings.innerHTML = "";
    appRoot.innerHTML = `<section class="section section--fp-loading"><article class="soft-card panel-pad section-card section-card--fp-loading">${flowPilotNowLoadingBlock({ subtitle: "データを読み込んでいます" })}</article></section>${renderToast()}`;
    bindFpMobileNavChromeOnce();
    renderMobileQuickBottomNav("dashboard");
    applyFpMobileDrawerState();
    return;
  }
  let route = currentRoute();
  // 契約一覧は取引先一覧へ集約した（2026-10-28）。旧URL（Slack の通知・ブックマーク）は取引先一覧へ移す。
  // ?name= 付きは会社名をキーワードに入れ、パートナーならパートナーのタブを開く
  if (route.page === "contracts") {
    const nameFromUrl = String(route.searchParams?.get("name") || "").trim();
    const isPartner = !!nameFromUrl && contractMaster.some((c) => String(c.name || "").trim() === nameFromUrl && c.classification === "パートナー");
    if (nameFromUrl) {
      const list = isPartner ? uiState.partnerList : uiState.clientList;
      list.filters = { ...(isPartner ? partnerListDefaultFilters() : clientListDefaultFilters()), keyword: nameFromUrl };
      list.page = 1;
    }
    history.replaceState(null, "", `${location.pathname}${location.search}#${isPartner ? "partner-list" : "client-list"}`);
    route = currentRoute();
  }
  // 担当者・支社のタブは各取引先の詳細へ移した（2026-10-28）。旧URLは取引先一覧へ
  if (route.page === "client-contacts" || route.page === "client-branches") {
    history.replaceState(null, "", `${location.pathname}${location.search}#client-list`);
    route = currentRoute();
  }
  if (!userCanAccessPage(route.page)) {
    showToast("このページを表示する権限がありません");
    const base = `${location.pathname}${location.search}`;
    history.replaceState(null, "", `${base}#dashboard`);
    route = currentRoute();
  }
  dismissTransientUiOnRouteChange(route);
  if (route.page !== "progress-management") {
    uiState.progressMgmtSuppressEmptyRefetch = false;
    uiState.progressShowProjectForm = false;
    uiState.progressChallengeCreateDraft = { title: "", body: "" };
    closeProgressAgendaFormModal();
    uiState.progressEditingMessageId = "";
    uiState.progressChallengeModalSubmitting = false;
    uiState.progressAgendaModalSubmitting = false;
    uiState.progressAttachmentUploadAgendaId = "";
    uiState.progressAttachmentUploadMessageId = "";
    uiState.progressComposeAttach = { agendaId: "", files: [] };
    uiState.progressMessageSubmittingByAgenda = {};
    uiState.progressShowChallengeEditForm = false;
    uiState.progressChallengeEditDraft = { challengeId: "", title: "", body: "" };
    uiState.progressShowTodoGroupForm = false;
    uiState.progressTodoGroupCreateDraft = { title: "", body: "", category: "" };
    uiState.progressShowTodoGroupEditForm = false;
    uiState.progressTodoGroupEditDraft = { groupId: "", title: "", body: "", category: "", challengeIds: [], themeFilter: "" };
    uiState.progressTodoGroupModalSubmitting = false;
    uiState.progressQuickAddPickerOpen = false;
  } else if (userCanAccessPage("progress-management") && authToken) {
    const groupParam = String(route.searchParams?.get("group") || "").trim();
    const projectParam = String(route.searchParams?.get("project") || "").trim();
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    const assignedMeRoute = progressManagementAssignedMeRouteActive(route);
    const curPmFrag = location.hash.replace(/^#/, "");
    const prevPmFrag = String(uiState._fpPrevPmHash || "");
    if (prevPmFrag !== curPmFrag) {
      // involved/created（関わった・起票したタブ）も担当課題一覧の一種。ここに入れ忘れると遷移時に再取得されず古い中身が残る
      const wasAssigned = /\bassigned=(?:me|mine|involved|created|1)\b/i.test(prevPmFrag);
      const isAssigned = /\bassigned=(?:me|mine|involved|created|1)\b/i.test(curPmFrag);
      if (isAssigned && !wasAssigned) uiState.progressAssignedMeNeedRefresh = true;
      // 課題ボード（project あり・issue なし）へ遷移したら、他者による担当変更等を反映するため1回再取得する
      if (projectParam && !issueParam) uiState.progressBoardNeedRefresh = true;
      // ハッシュが変わったら「キャッシュに無い課題の取得を試した」記録をリセット（別課題への遷移で再度取得できるように）
      uiState._pmIssueHydrateTried = "";
      uiState._fpPrevPmHash = curPmFrag;
      uiState.progressMgmtFetchFailCount = 0;
      uiState.progressMgmtLoadError = "";
      uiState.progressMgmtSuppressEmptyRefetch = false;
    }
    const onGroupList =
      !groupParam && !projectParam && !assignedMeRoute;
    const onAssignedMeFlat = !groupParam && !projectParam && assignedMeRoute;
    const onThemeList = groupParam && !projectParam;
    const onBoard = Boolean(projectParam) && !issueParam;
    const listMode = normalizeProgressListMode(uiState.progressListMode);
    const groupListMode = normalizeProgressGroupListMode(uiState.progressGroupListMode);
    if (onGroupList && hasProgressGroupListCache(groupListMode)) {
      applyProgressGroupListCacheToView(groupListMode);
    }
    // 担当中/起票/関わったはキャッシュ枠が1つ。チップ以外の経路（戻る・サイドバー・ベル等）でスコープが変わっても
    // 前のスコープの中身を流用しないよう、食い違ったら捨てて表示中のツリーも空にしてから取り直す
    // （空にしないと、取り直しの間「起票」の中身を担当者で絞った一部だけが担当中として見える）
    const assignScopeNow = onAssignedMeFlat ? progressManagementAssignScope(route) : "";
    if (onAssignedMeFlat && uiState.progressAssignedListCacheScope !== assignScopeNow) {
      invalidateProgressAssignedListCache();
      applyProgressPayloadToState({ groups: [], challenges: [] });
      uiState.progressAssignedListCacheScope = assignScopeNow;
    }
    if (onAssignedMeFlat && hasProgressAssignedListCache()) {
      applyProgressAssignedListCacheToView();
    }
    if (onThemeList && hasProgressListCache(listMode)) {
      applyProgressListCacheToView(listMode);
    }
    const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
    const groups = Array.isArray(uiState.progressTodoGroups) ? uiState.progressTodoGroups : [];
    const emptyTree = onGroupList ? groups.length === 0 : onAssignedMeFlat ? groups.length === 0 && chs.length === 0 : chs.length === 0;
    let needHydrateThread = false;
    if (issueParam) {
      const n = Number(issueParam);
      if (Number.isFinite(n) && n >= 1) {
        const hit = findProgressAgendaById(n);
        if (hit?.agenda?.threadDeferred === true) needHydrateThread = true;
        // ベル通知など他画面からの遷移では、手元のツリーが別モードのキャッシュ
        // （担当課題のみ・古い一覧など）のことがあり、対象の課題が見つからない。
        // その場合も1回だけサーバから取得する（存在しない課題での再取得ループは
        // _pmIssueHydrateTried で防ぎ、従来どおりボード表示へフォールバック）。
        else if (!hit && String(uiState._pmIssueHydrateTried || "") !== issueParam) needHydrateThread = true;
      }
    }
    const listFetchDone = onGroupList
      ? Boolean(ensureProgressGroupListFetchDoneState()[groupListMode])
      : onAssignedMeFlat
        ? Boolean(uiState.progressAssignedListFetchDone)
        : onThemeList
          ? Boolean(ensureProgressListFetchDoneState()[listMode])
          : true;
    const fetchRetryBlocked = Number(uiState.progressMgmtFetchFailCount || 0) >= PM_FETCH_MAX_AUTO_RETRIES;
    const shouldFetch = !fetchRetryBlocked
      ? onGroupList
        ? !listFetchDone && !uiState.progressMgmtFetchInFlight && !uiState.progressMgmtSuppressEmptyRefetch
        : onAssignedMeFlat
          ? !uiState.progressMgmtFetchInFlight &&
            !uiState.progressMgmtSuppressEmptyRefetch &&
            (Boolean(uiState.progressAssignedMeNeedRefresh) || !Boolean(uiState.progressAssignedListFetchDone) || needHydrateThread)
          : onThemeList
            ? !listFetchDone && !uiState.progressMgmtFetchInFlight && !uiState.progressMgmtSuppressEmptyRefetch
            : (emptyTree || needHydrateThread || (onBoard && Boolean(uiState.progressBoardNeedRefresh))) &&
              !uiState.progressMgmtFetchInFlight &&
              !(emptyTree && uiState.progressMgmtSuppressEmptyRefetch)
      : false;
    if (shouldFetch) {
      uiState.progressMgmtFetchInFlight = true;
      // 課題単位の取得は1ハッシュにつき1回だけ（失敗時も threadDeferred 失敗時と同じく
      // 「トースト→フォールバック」で止め、リトライ連発とトースト連発を避ける）
      if (issueParam) uiState._pmIssueHydrateTried = issueParam;
      if (onGroupList || onThemeList || onAssignedMeFlat || emptyTree) uiState.progressMgmtLoading = true;
      if (needHydrateThread && !emptyTree) uiState.progressMgmtDetailLoading = true;
      const scope = "summary";
      const hydrateAgendaId =
        issueParam && Number.isFinite(Number(issueParam)) && Number(issueParam) >= 1
          ? Number(issueParam)
          : undefined;
      const logoutSeqAtStart = fpLogoutSeq;
      void (async () => {
        let ok = false;
        try {
          ok = await refreshProgressManagementFromServer({
            scope,
            listMode: onThemeList ? listMode : undefined,
            groupListMode: onGroupList ? groupListMode : undefined,
            includeGroupId: groupParam || undefined,
            includeChallengeId: projectParam || undefined,
            hydrateAgendaId: needHydrateThread || emptyTree ? hydrateAgendaId : undefined
          });
        } catch (err) {
          console.error("refreshProgressManagementFromServer failed:", err);
          ok = false;
        } finally {
          // ログアウト／別アカウントへ切り替わった後に届いた結果なら、次の人の状態（取得中フラグ・失敗表示）に触らない
          if (fpLogoutSeq !== logoutSeqAtStart) return;
          uiState.progressMgmtFetchInFlight = false;
          uiState.progressMgmtLoading = false;
          uiState.progressMgmtDetailLoading = false;
          if (onAssignedMeFlat) uiState.progressAssignedMeNeedRefresh = false;
          if (onBoard) uiState.progressBoardNeedRefresh = false;
          if (!ok && needHydrateThread && !emptyTree) {
            stripProgressThreadDeferredFlags();
            showToast(uiState.progressMgmtLoadError || "やりとりを取得できませんでした。時間をおいて再度お試しください。");
          } else if (!ok && fetchRetryBlocked) {
            showToast(uiState.progressMgmtLoadError || "データ取得に失敗しました。ページを再読み込みしてください。");
          }
          if (ok && !onGroupList && !onThemeList && !onAssignedMeFlat) uiState.progressMgmtSuppressEmptyRefetch = false;
          render();
        }
      })();
    }
  }
  if (route.openProjectCreate && !uiState.projectCreate) {
    initProjectCreateState();
  }
  // 原価割当「まとめて請求書を作成」からの遷移直後だけ、書類画面の請求書タブへ進める。
  // 書類画面は案件が見つからないと projects[0] へフォールバックするため、リロード直後の
  // ハッシュ駆動で projects がまだ空/未該当の間は遷移しない
  // （無ければ何もしない＝次の render で projects 読み込み後に再評価される）
  if (
    route.page === "project-detail" &&
    route.autoComposeInvoice &&
    route.id &&
    !uiState.composer &&
    projects.some((p) => p.id === route.id)
  ) {
    // 旧コンポーザー（openComposer）は請求日・振込先・明細が仮の既定値で、入力もサーバへ保存されない。
    // 前月から複製した実際の請求明細を編集・発行できる書類画面（請求書タブ）へ置き換えて遷移する
    location.replace(`${location.pathname}${location.search}#project-docs:${route.id}:invoice`);
    return;
  }
  let active = route.sidebarKey || route.page;
  if (route.page === "project-detail") active = "projects";
  if (active === "account-settings" || active === "approval-settings") active = "account-admin";
  if (active === "other-admin") active = "notice-admin";
  refreshSidebarUserChrome();
  let restoreClientListTextFilter = null;
  if (route.page === "client-list") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement) {
      const k = ae.dataset.clientListFilter;
      if (k === "keyword") {
        restoreClientListTextFilter = {
          key: k,
          start: ae.selectionStart,
          end: ae.selectionEnd,
          scrollTop: ae.scrollTop
        };
      }
    }
  }
  let restoreDomainListTextFilter = null;
  if (route.page === "domain-list") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.dataset.domainListFilter === "keyword") {
      restoreDomainListTextFilter = {
        start: ae.selectionStart,
        end: ae.selectionEnd,
        scrollTop: ae.scrollTop
      };
    }
  }
  let restoreCostMgmtKeyword = null;
  if (route.page === "cost-management") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.hasAttribute("data-cost-mgmt-filter-keyword")) {
      restoreCostMgmtKeyword = { start: ae.selectionStart, end: ae.selectionEnd };
    }
  }
  // 一覧: チェック等で表ごと描き直すと表の横/縦スクロールが先頭に戻って表ががたつくため、同じ画面の再描画なら
  // 描画前の位置を（スクロール枠ごと・出現順に）控えて戻す。別の画面から来た直後は戻さない
  const _tableScrollKey = location.hash;
  const _restoreTableScroll =
    appRoot.dataset.fpRenderKey === _tableScrollKey
      ? [...appRoot.querySelectorAll(LIST_TABLE_SCROLL_SELECTOR)].map((el) => [el.scrollLeft, el.scrollTop])
      : null;
  let _restoreDashboardSearch = null;
  if (route.page === "dashboard") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.dataset.dbSearch === "1") {
      _restoreDashboardSearch = { start: ae.selectionStart, end: ae.selectionEnd };
    }
  }
  let _restoreProgressIssueSearch = null;
  if (route.page === "progress-management") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.dataset.pmIssueSearch !== undefined) {
      _restoreProgressIssueSearch = { start: ae.selectionStart, end: ae.selectionEnd };
    }
  }
  let _restoreCostAllocBukken = null;
  if (route.page === "cost-allocation" || route.page === "cost-management") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.dataset.costAllocFilterBukken !== undefined) {
      _restoreCostAllocBukken = { start: ae.selectionStart, end: ae.selectionEnd };
    }
  }
  let _restoreAllowedToolsSearch = null;
  if (route.page === "allowed-tools") {
    const ae = document.activeElement;
    if (ae instanceof HTMLInputElement && ae.dataset.atSearch !== undefined) {
      _restoreAllowedToolsSearch = { start: ae.selectionStart, end: ae.selectionEnd };
    }
  }
  navLinks(navPrimary,   menu.filter((m) => m.group === "primary"),  active);
  if (navSettings) navSettings.innerHTML = "";
  bindFpMobileNavChromeOnce();
  renderMobileQuickBottomNav(active);
  progressMentionDismissForDomRefresh();
  capturePmComposeDrafts();
  appRoot.innerHTML = `${pageHtml(route)}${renderToast()}`;
  restorePmComposeDrafts();
  appRoot.dataset.fpRenderKey = _tableScrollKey;
  // 一覧の列幅・画面の高さの枠（15z-table-prefs.js）を先に当ててからスクロール位置を戻す。
  // 後から当てると、戻す時点では表の幅・枠の高さが違ってスクロール量が丸められ、チェックを付けるたびに
  // 枠が先頭へ跳ねてちらついて見えた（MutationObserver も走るが、当て済みの表は飛ばす）
  fpTableEnhanceAll();
  const _tableScrollBoxes = appRoot.querySelectorAll(LIST_TABLE_SCROLL_SELECTOR);
  // 表の数が変わった描き直し（読込中→表示・絞り込みで表が消える等）は別の表へ位置が移るので戻さない
  if (_restoreTableScroll && _restoreTableScroll.length === _tableScrollBoxes.length) {
    _tableScrollBoxes.forEach((el, i) => {
      [el.scrollLeft, el.scrollTop] = _restoreTableScroll[i];
    });
  }
  const _modalNowOpen = anyModalOpen();
  if (fpModalLayer && (_modalNowOpen || _prevModalOpen)) {
    fpModalLayer.innerHTML = `${renderComposer()}${renderCostForm()}${renderCostRegistrationWizard()}${renderCostBulkEntryModal()}${renderExpenseForm()}${renderPaymentForm()}${renderDocStartModal()}${renderTemplateEditorModal()}${renderUserEditorModal()}${renderMyAccountModal()}${renderOrderFlowPromptModal()}${renderProjectDrawer()}${renderProjectCreateModal()}${renderProgressTodoGroupCreateModal()}${renderProgressTodoGroupEditModal()}${renderProgressChallengeCreateModal()}${renderProgressChallengeEditModal()}${renderProgressChallengeWikiModal()}${renderProgressChallengeSettingsModal()}${renderProgressAgendaCreateModal()}${renderProgressQuickAddThemePicker()}${renderProgressAgendaAttachModal()}${renderProjectCreateWarningModal()}${renderProjectBulkDupConfirmModal()}${renderProjectBulkDocsModal()}${renderSealConfirmModal()}${renderDocSealHistoryModal()}${renderApplicationMgmtDetailModal()}${renderDetailLineAddPickerModal()}${renderDetailLineMobileEditModal()}${renderProjectDetailTagModal()}${renderClientCreateModal()}${renderPartnerModal()}${renderClientApplyModal()}${renderClientAttachKindModal()}${renderDomainCreateModal()}${renderApprovalPickerModal()}${renderCostMgmtBulkModal()}${renderCostReconcileReceiptModal()}${renderRemBillingSettingsModal()}${renderMonthLockModal()}${renderCostDisputeModal()}${renderCostMoveModal()}${renderCostNoteModal()}${renderCostRemandModal()}${renderCostOrderReasonModal()}${renderProjectEditHistoryModal()}${renderInvoiceIssueHistoryModal()}${renderProgressStageHistoryModal()}${renderProgressRejectionPromptModal()}${renderProgressWorkLogDetailModal()}${renderProgressAttachPreviewLayer()}`;
    _modalLayerUpdated = true;
  }
  _prevModalOpen = _modalNowOpen;
  if (route.page === "progress-management") {
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    const scrollActiveThreadToBottom = () => {
      const th = document.querySelector(".pm-thread--issue-detail, .pm-thread--assigned-talk");
      if (!(th instanceof HTMLElement)) return;
      // 「ここから未読」の区切りがあるあいだは最下部でなくそこから表示する（LINEと同じ・
      // 前回読んだ続き）。再描画のたびに位置を合わせ直すことで、開いた直後のバックグラウンド
      // 再取得による再描画でも読んでいる位置から最下部へ飛ばされない。
      // 自分がコメントを送ると区切りは解除され（15i側でスナップショットをクリア）、最下部追従に戻る
      const divider = th.querySelector("[data-pm-unread-divider]");
      if (divider instanceof HTMLElement) {
        th.scrollTop += divider.getBoundingClientRect().top - th.getBoundingClientRect().top - 12;
        return;
      }
      th.scrollTop = th.scrollHeight;
    };
    const onIssueThreadRoute =
      progressManagementIssueDetailRouteActive(route) || progressAssignedTalkActive(route);
    if (!issueParam) {
      uiState.progressReadMarkedAgendaId = "";
    } else if (uiState.progressReadMarkedAgendaId !== issueParam) {
      uiState.progressReadMarkedAgendaId = issueParam;
      requestAnimationFrame(() => {
        if (onIssueThreadRoute) scrollActiveThreadToBottom();
        void progressMarkAgendaRead(issueParam);
      });
    } else if (onIssueThreadRoute) {
      requestAnimationFrame(scrollActiveThreadToBottom);
    }
  }
  if (restoreClientListTextFilter) {
    const { key, start, end, scrollTop } = restoreClientListTextFilter;
    requestAnimationFrame(() => {
      const el = document.querySelector(`input[data-client-list-filter="${key}"]`);
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (typeof scrollTop === "number") el.scrollTop = scrollTop;
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try {
            el.setSelectionRange(start, end);
          } catch {
            /* noop */
          }
        }
      }
    });
  }
  if (restoreDomainListTextFilter) {
    const { start, end, scrollTop } = restoreDomainListTextFilter;
    requestAnimationFrame(() => {
      const el = document.querySelector('input[data-domain-list-filter="keyword"]');
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (typeof scrollTop === "number") el.scrollTop = scrollTop;
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (restoreCostMgmtKeyword) {
    const { start, end } = restoreCostMgmtKeyword;
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-cost-mgmt-filter-keyword]");
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (_restoreDashboardSearch) {
    const { start, end } = _restoreDashboardSearch;
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-db-search]");
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (_restoreProgressIssueSearch) {
    const { start, end } = _restoreProgressIssueSearch;
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-pm-issue-search]");
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (_restoreCostAllocBukken) {
    const { start, end } = _restoreCostAllocBukken;
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-cost-alloc-filter-bukken]");
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (_restoreAllowedToolsSearch) {
    const { start, end } = _restoreAllowedToolsSearch;
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-at-search]");
      if (el instanceof HTMLInputElement) {
        el.focus();
        if (start != null && end != null && typeof el.setSelectionRange === "function") {
          try { el.setSelectionRange(start, end); } catch { /* noop */ }
        }
      }
    });
  }
  if (route.page === "project-detail" && uiState.editingProjectNameId) {
    const eid = uiState.editingProjectNameId;
    requestAnimationFrame(() => {
      const el = document.querySelector(`input[data-project-detail-name-input="${eid}"]`);
      if (el instanceof HTMLInputElement) {
        el.focus();
        try {
          el.select();
        } catch {
          /* noop */
        }
      }
    });
  }
  const indeterminateEl = document.querySelector('input[data-indeterminate="1"]');
  if (indeterminateEl instanceof HTMLInputElement) indeterminateEl.indeterminate = true;
  if (uiState.suppressComposerDrawerAnimation) {
    uiState.suppressComposerDrawerAnimation = false;
    applyFpDrawerNoEnterAnimation();
  }
  applySidebarCollapsedClass();
  applyFpMobileDrawerState();
  if (
    (route.page === "budget-progress" || route.page === "management") &&
    typeof window.initBudgetProgressPage === "function"
  ) {
    ensureBudgetPeriodsState();
    const period =
      uiState.budgetPeriods.find((p) => String(p.id) === String(uiState.selectedBudgetPeriodId || "")) ||
      latestBudgetPeriodFromSorted(uiState.budgetPeriods);
    if (!period?.id || budgetRowsLoadedForPeriod(period.id)) {
      window.initBudgetProgressPage(
        uiState.budgetProgress,
        syncBudgetProgressUiState,
        {
          mode: route.page === "management" ? "management" : "budget-progress",
          periods: Array.isArray(uiState.budgetPeriods) ? uiState.budgetPeriods : [],
          selectedBudgetPeriodId: uiState.selectedBudgetPeriodId || "",
          resolveProgressGroups: buildBudgetProgressGroupsForSelectedPeriod
        }
      );
    }
  }
}

let renderScheduled = false;
let renderRunning = false;
let costMgmtKeywordDebounceTimer = null;
let searchableSelectGlobalBound = false;
let _prevModalOpen = false;
let _modalLayerUpdated = false;

function anyModalOpen() {
  return !!(
    uiState.composer || uiState.costForm || uiState.costRegWizard || uiState.costBulkEntry ||
    uiState.expenseForm || uiState.paymentForm || uiState.docStart ||
    uiState.templateEditor || uiState.userEditor || uiState.myAccountEditor ||
    uiState.orderFlowPromptProjectId || uiState.projectCreate ||
    uiState.projectCreateWarning || uiState.projectBulkDupConfirm || uiState.projectBulkDocs ||
    uiState.sealConfirmDialog || uiState.docSealHistoryModal ||
    uiState.appMgmtDetailModal?.kind || uiState.detailLineAddPicker ||
    uiState.detailLineMobileEdit ||
    uiState.projectDetailTagModal || uiState.clientCreate || uiState.partnerCreate ||
    uiState.clientApplyModal ||
    uiState.clientAttachKindModal ||
    uiState.domainCreate ||
    uiState.approvalPicker || uiState.costMgmtBulkModal || uiState.costReconcileReceiptModal || uiState.remBillingSettingsModal ||
    uiState.monthLockModal ||
    uiState.costDisputeModal || uiState.costMoveModal || uiState.costNoteModal || uiState.costRemandModal || uiState.costOrderReasonModal || uiState.projectEditHistoryModal ||
    uiState.invoiceIssueHistoryModal ||
    uiState.progressShowTodoGroupForm || uiState.progressShowTodoGroupEditForm ||
    uiState.progressShowProjectForm || uiState.progressShowChallengeEditForm ||
    uiState.progressShowChallengeWiki || uiState.progressShowChallengeSettings ||
    uiState.progressShowIssueAddForm || uiState.progressAgendaAttachModal?.agendaId ||
    uiState.progressAgendaAttachPreviewId || uiState.progressQuickAddPickerOpen ||
    uiState.progressStageHistoryAgendaId || uiState.progressRejectionPrompt ||
    uiState.progressWorkLogDetailAgendaId
  );
}

function normalizeProjectFilterLookupText(v) {
  return String(v || "").replace(/\u3000/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

function normalizeProjectFilterLookupCompact(v) {
  return normalizeProjectFilterLookupText(v).replace(/\s+/g, "");
}

function findProjectClientFilterIdByName(name) {
  const key = normalizeProjectFilterLookupText(name);
  if (!key) return "all";
  const hit = clients.find((c) => normalizeProjectFilterLookupText(c.name) === key);
  return hit ? hit.id : "all";
}

function findProjectAssigneeFilterIdByName(name) {
  const key = normalizeProjectFilterLookupText(name);
  if (!key) return "all";
  const hit = assignableUsers().find((u) => normalizeProjectFilterLookupText(u.name) === key);
  return hit ? hit.id : "all";
}

function searchableSelectNorm(v) {
  return String(v || "").replace(/\u3000/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

/** 候補パネルを閉じたときの後始末: 候補ボタンを捨て（開くたびに作り直す）、<select> 側へ通知する。
 *  候補を遅延生成する select はこの通知で option を「選択中の1件」に戻し、多数の行を順に開いても
 *  DOM が行数×候補数に膨らまないようにする（原価取込プレビューの計上先） */
function sdNotifyClosed(wrapEl) {
  const box = wrapEl.querySelector(".sd-options");
  if (box) box.innerHTML = "";
  const s = wrapEl.querySelector("select");
  if (s) s.dispatchEvent(new CustomEvent("sd-after-close", { bubbles: true }));
}

function closeAllSearchableSelects(exceptWrap = null) {
  document.querySelectorAll(".sd-select.sd-open").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (exceptWrap && el === exceptWrap) return;
    el.classList.remove("sd-open");
    sdNotifyClosed(el);
    const attr = el.closest(".pm-dsp-attr");
    const attrs = el.closest(".pm-dsp-attrs");
    const panel = el.closest(".pm-detail-side-panel");
    if (attr instanceof HTMLElement) attr.classList.remove("sd-host-open");
    if (attrs instanceof HTMLElement) attrs.classList.remove("sd-host-open");
    if (panel instanceof HTMLElement) panel.classList.remove("sd-host-open");
    if (el._sdPosListener) {
      const scrollEl = el._sdScrollEl || document.documentElement;
      scrollEl.removeEventListener("scroll", el._sdPosListener);
      window.removeEventListener("scroll", el._sdPosListener);
      el._sdPosListener = null;
      el._sdScrollEl = null;
    }
  });
}

/** 右ドロワー／モーダルが DOM 上で開いているか（render 直前の再入場アニメ抑止用） */
function fpRightDrawerOpenInDom() {
  return Boolean(
    document.querySelector(".composer-backdrop, aside.composer-panel") ||
      document.querySelector(".cl-modal-overlay.open, .cl-mini-modal-overlay.open") ||
      document.querySelector(".approval-picker-root")
  );
}

function markSuppressDrawerAnimationIfOpen() {
  if (fpRightDrawerOpenInDom()) uiState.suppressComposerDrawerAnimation = true;
}

function applyFpDrawerNoEnterAnimation() {
  document.querySelectorAll(".composer-panel, .composer-backdrop").forEach((el) => {
    if (el instanceof HTMLElement) el.classList.add("fp-drawer-no-enter");
  });
  document.querySelectorAll(".cl-modal-overlay.open, .cl-mini-modal-overlay.open").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.classList.add("fp-drawer-no-enter");
    const panel = el.querySelector(".cl-modal, .cl-mini-modal");
    if (panel instanceof HTMLElement) panel.classList.add("fp-drawer-no-enter");
  });
  document.querySelectorAll(".approval-picker-root").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const scrim = el.querySelector(".approval-picker-scrim");
    const card = el.querySelector(".approval-picker-card");
    if (scrim instanceof HTMLElement) scrim.classList.add("fp-drawer-no-enter");
    if (card instanceof HTMLElement) card.classList.add("fp-drawer-no-enter");
  });
}

function bindSearchableSelectGlobalHandlersOnce() {
  if (searchableSelectGlobalBound) return;
  searchableSelectGlobalBound = true;
  document.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t || t.closest(".sd-select")) return;
    closeAllSearchableSelects();
  });
}

/* ── @メンション入力欄の色付け ──────────────────────────────────
   textarea は仕様上「一部の文字だけ色付け」ができないため、背面に同じ文字を描く
   backdrop を重ね、確定済みメンション（既知ユーザー名）だけ薄黄背景を敷く。
   textarea 本体の文字は通常表示のまま（IME 変換中も見える）＝日本語入力を邪魔しない。
   投稿後の表示（progressMessagePlainWithMentionsHtml）と同じ判定関数を使い、
   ハイライト対象を投稿後と一致させる。 */
const PM_MENTION_BACKDROP_METRICS = [
  "fontFamily", "fontSize", "fontWeight", "fontStyle", "lineHeight", "letterSpacing",
  "wordSpacing", "textTransform", "textIndent", "tabSize",
  // 折返し規則は textarea の実測値をそのままコピーし、行の折返し位置を完全一致させる
  "whiteSpace", "overflowWrap", "wordBreak",
  "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth", "borderRadius"
];

function pmMentionBackdropHtml(text) {
  const s = String(text || "");
  let html = "";
  let i = 0;
  while (i < s.length) {
    const hit = progressMessageMentionAt(s, i);
    if (hit) {
      html += `<span class="pm-mention-hl">${escapeHtml(hit.raw)}</span>`;
      i += hit.len;
      continue;
    }
    let j = i + 1;
    while (j < s.length && s[j] !== "@" && s[j] !== "＠") j += 1;
    html += escapeHtml(s.slice(i, j));
    i = j;
  }
  // 末尾の改行ぶんの高さを textarea に合わせる（最後の空行を表現）
  return html + "\n";
}

function pmSyncMentionBackdrop(ta) {
  if (!(ta instanceof HTMLTextAreaElement)) return;
  const bd = ta.__pmMentionBackdrop;
  if (!bd) return;
  bd.innerHTML = pmMentionBackdropHtml(ta.value);
  bd.scrollTop = ta.scrollTop;
  bd.scrollLeft = ta.scrollLeft;
}

function enhanceMentionTextareaOverlays(rootEl) {
  const root = rootEl instanceof Element ? rootEl : document.body;
  root.querySelectorAll('textarea[data-pm-mention-textarea="1"]').forEach((ta) => {
    if (!(ta instanceof HTMLTextAreaElement)) return;
    if (ta.dataset.mentionOverlay === "1") { pmSyncMentionBackdrop(ta); return; }
    const parent = ta.parentNode;
    if (!parent) return;
    ta.dataset.mentionOverlay = "1";
    // textarea を透明化する前に実背景色・寸法を取得（この時点では pm-mention-editor 配下でないため素の値）
    const cs = getComputedStyle(ta);
    const realBg = cs.backgroundColor && cs.backgroundColor !== "rgba(0, 0, 0, 0)" ? cs.backgroundColor : "#fff";
    const wrap = document.createElement("div");
    wrap.className = "pm-mention-editor";
    const bd = document.createElement("div");
    bd.className = "pm-mention-hl-backdrop";
    bd.setAttribute("aria-hidden", "true");
    bd.style.background = realBg;
    for (const p of PM_MENTION_BACKDROP_METRICS) bd.style[p] = cs[p];
    parent.insertBefore(wrap, ta);
    wrap.appendChild(bd);
    wrap.appendChild(ta);
    ta.__pmMentionBackdrop = bd;
    ta.addEventListener("scroll", () => { bd.scrollTop = ta.scrollTop; bd.scrollLeft = ta.scrollLeft; });
    pmSyncMentionBackdrop(ta);
  });
}

function enhanceSearchableSelects(rootEl) {
  const root = rootEl instanceof Element ? rootEl : document.body;
  const selects = root.querySelectorAll("select:not([multiple]):not([data-search-dropdown-off='1'])");
  selects.forEach((sel) => {
    if (!(sel instanceof HTMLSelectElement)) return;
    // SP（タッチデバイス or 900px以下）では native select の方が使いやすいため
    // プロジェクトフィルター内・progress-management担当フィルターは enhance しない
    if (window.matchMedia?.("(max-width: 900px), (pointer: coarse)")?.matches) {
      if (sel.closest(".pj-filter-area")) return;
      if (
        currentRoute().page === "progress-management" &&
        (sel.matches('[data-pm-issue-field="assignee"]') ||
          sel.matches("[data-pm-modal-agenda-assignee]") ||
          sel.matches("[data-pm-modal-agenda-client]"))
      ) return;
    }
    if (sel.dataset.searchDropdownEnhanced === "1") return;
    sel.dataset.searchDropdownEnhanced = "1";

    const wrap = document.createElement("div");
    wrap.className = "sd-select";
    const styleText = sel.getAttribute("style");
    if (styleText) {
      wrap.setAttribute("style", styleText);
      sel.removeAttribute("style");
    }
    if (sel.className) {
      sel.className
        .split(/\s+/)
        .map((c) => c.trim())
        .filter(Boolean)
        .forEach((c) => wrap.classList.add(`sd-from-${c}`));
    }

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "sd-trigger";
    if (sel.classList.contains("pj-input")) trigger.classList.add("pj-input");
    if (sel.classList.contains("cl-filter-input")) trigger.classList.add("cl-filter-input");
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");

    const panel = document.createElement("div");
    panel.className = "sd-panel";
    const searchRow = document.createElement("div");
    searchRow.className = "sd-search-row";
    const searchIcon = document.createElement("span");
    searchIcon.className = "sd-search-icon";
    searchIcon.setAttribute("aria-hidden", "true");
    searchIcon.textContent = "⌕";
    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.className = "sd-search";
    searchInput.placeholder = "検索";
    searchRow.append(searchIcon, searchInput);
    const optionsBox = document.createElement("div");
    optionsBox.className = "sd-options";
    panel.append(searchRow, optionsBox);

    const getSelectedText = () => {
      const opt = sel.options[sel.selectedIndex];
      if (!opt) return "";
      return String(opt.text || "").trim();
    };

    const renderOptions = () => {
      const q = searchableSelectNorm(searchInput.value);
      optionsBox.innerHTML = "";
      let count = 0;
      // <optgroup> がある select は、絞り込み後も見出しを保つ（グループ名は検索対象にしない。
      // 例: 担当者を「営業」「クリエイティブ」等で分けている場合、検索中も所属がわかるように）
      let lastGroupLabel;
      Array.from(sel.options).forEach((opt) => {
        const label = String(opt.text || "");
        const value = String(opt.value || "");
        const extra = opt instanceof HTMLOptionElement ? String(opt.dataset.optionSearch || "") : "";
        const hay = `${label} ${extra}`;
        if (q && !searchableSelectNorm(hay).includes(q)) return;
        const groupLabel = opt.parentElement instanceof HTMLOptGroupElement ? opt.parentElement.label : "";
        if (groupLabel && groupLabel !== lastGroupLabel) {
          const groupEl = document.createElement("div");
          groupEl.className = "sd-option-group-label";
          groupEl.textContent = groupLabel;
          optionsBox.appendChild(groupEl);
        }
        lastGroupLabel = groupLabel;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "sd-option";
        if (opt.selected) btn.classList.add("is-selected");
        btn.dataset.value = value;
        const check = document.createElement("span");
        check.className = "sd-option-check";
        check.setAttribute("aria-hidden", "true");
        check.textContent = "✓";
        const text = document.createElement("span");
        text.className = "sd-option-label";
        text.textContent = label || "—";
        btn.setAttribute("aria-selected", opt.selected ? "true" : "false");
        btn.append(check, text);
        optionsBox.appendChild(btn);
        count += 1;
      });
      if (!count) {
        const empty = document.createElement("div");
        empty.className = "sd-option-empty";
        empty.textContent = "候補がありません";
        optionsBox.appendChild(empty);
      }
    };

    const syncTriggerLabel = () => {
      trigger.textContent = getSelectedText() || "選択してください";
    };

    trigger.addEventListener("click", (ev) => {
      ev.preventDefault();
      const open = !wrap.classList.contains("sd-open");
      closeAllSearchableSelects(wrap);
      wrap.classList.toggle("sd-open", open);
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      const attrHost = wrap.closest(".pm-dsp-attr");
      const attrsHost = wrap.closest(".pm-dsp-attrs");
      const panelHost = wrap.closest(".pm-detail-side-panel");
      [attrHost, attrsHost, panelHost].forEach((host) => {
        if (host instanceof HTMLElement) host.classList.toggle("sd-host-open", open);
      });
      if (!open) sdNotifyClosed(wrap);
      if (open) {
        searchInput.value = "";
        // 候補を開く直前に <select> 側へ通知する（候補を遅延生成する select がここで option を流し込む。
        // 例: 原価取込プレビューの計上先＝行ごとに全案件を持たず、開いた行だけ組み立てる）
        sel.dispatchEvent(new CustomEvent("sd-before-open", { bubbles: true }));
        renderOptions();
        // タッチ端末では自動フォーカスしない（ソフトキーボードがモーダルを覆い操作不能に見えるため）
        if (!window.matchMedia?.("(pointer: coarse)")?.matches) searchInput.focus();
        // 原価登録ウィザード内: composer-body の overflow:auto で切れないよう fixed 配置。
        // 原価割当の物件グループカード(.cost-bkgrp)も角丸クリップ用の overflow:hidden を持ち、
        // カードの残り高さより候補パネルが長いとパネル下端が切れる（候補が多い・行数が少ない物件で発生）ため同様に escape する
        // トークのコメント入力欄（.pm-assigned-talk-compose）も「登録」に届くよう overflow:auto にしたため、
        // 状況・担当者の候補が入力欄の上端で切れて見えない（2026-09-29 実機指摘）。同じく fixed で逃がす
        if (wrap.closest(".cost-reg-wizard-modal") || wrap.closest(".cost-bkgrp-actions") || wrap.closest(".pm-assigned-talk-compose")) {
          const scrollEl = wrap.closest(".composer-body") || wrap.closest(".pm-assigned-talk-compose") || document.documentElement;
          const positionPanel = () => {
            if (!wrap.classList.contains("sd-open")) {
              scrollEl.removeEventListener("scroll", positionPanel);
              window.removeEventListener("scroll", positionPanel);
              return;
            }
            const tRect = trigger.getBoundingClientRect();
            const panelW = Math.max(tRect.width, 220);
            const panelH = panel.offsetHeight > 0 ? panel.offsetHeight : 320;
            const spaceBelow = window.innerHeight - tRect.bottom - 8;
            const spaceAbove = tRect.top - 8;
            panel.style.left = tRect.left + "px";
            panel.style.width = panelW + "px";
            if (spaceBelow >= panelH || spaceBelow >= spaceAbove) {
              panel.style.top = tRect.bottom + 2 + "px";
              panel.style.bottom = "auto";
            } else {
              panel.style.top = "auto";
              panel.style.bottom = (window.innerHeight - tRect.top + 2) + "px";
            }
          };
          panel.style.position = "fixed";
          panel.style.right = "auto";
          panel.style.zIndex = "9999";
          positionPanel();
          // offsetHeight が確定してから再計算（初回は 0 の場合あり）
          requestAnimationFrame(positionPanel);
          if (wrap._sdPosListener) {
            scrollEl.removeEventListener("scroll", wrap._sdPosListener);
            window.removeEventListener("scroll", wrap._sdPosListener);
          }
          wrap._sdPosListener = positionPanel;
          wrap._sdScrollEl = scrollEl;
          scrollEl.addEventListener("scroll", positionPanel, { passive: true });
          window.addEventListener("scroll", positionPanel, { passive: true });
        } else if (!wrap.closest(".pm-dsp-attr-val")) {
          // 画面下端でパネルが見切れる場合は上方向に表示
          requestAnimationFrame(() => {
            if (!wrap.classList.contains("sd-open")) return;
            const tRect = trigger.getBoundingClientRect();
            const panelH = panel.offsetHeight || 240;
            const spaceBelow = window.innerHeight - tRect.bottom - 8;
            if (spaceBelow < panelH && tRect.top >= panelH) {
              panel.style.top = "auto";
              panel.style.bottom = "100%";
              panel.style.marginBottom = "2px";
              panel.style.marginTop = "";
            } else {
              panel.style.top = "";
              panel.style.bottom = "";
              panel.style.marginTop = "";
              panel.style.marginBottom = "";
            }
          });
        }
        if (wrap.closest(".pm-dsp-attr-val")) {
          const col = wrap.closest(".pm-issue-col-right--attrs");
          const scrollEl = wrap.closest(".pm-detail-side-panel") || col || document.documentElement;
          const positionPanel = () => {
            if (!wrap.classList.contains("sd-open")) {
              scrollEl.removeEventListener("scroll", positionPanel);
              window.removeEventListener("scroll", positionPanel);
              return;
            }
            const tRect = trigger.getBoundingClientRect();
            const colRect = col ? col.getBoundingClientRect() : tRect;
            const panelW = Math.max(colRect.width - 24, 160);
            const spaceBelow = window.innerHeight - tRect.bottom - 8;
            const spaceAbove = tRect.top - 8;
            panel.style.left = colRect.left + 12 + "px";
            panel.style.width = panelW + "px";
            if (spaceBelow >= Math.min(210, 160) || spaceBelow >= spaceAbove) {
              panel.style.top = tRect.bottom + 2 + "px";
              panel.style.bottom = "auto";
            } else {
              panel.style.top = "auto";
              panel.style.bottom = window.innerHeight - tRect.top + 2 + "px";
            }
          };
          panel.style.position = "fixed";
          panel.style.right = "auto";
          panel.style.zIndex = "9999";
          positionPanel();
          if (wrap._sdPosListener) {
            scrollEl.removeEventListener("scroll", wrap._sdPosListener);
            window.removeEventListener("scroll", wrap._sdPosListener);
          }
          wrap._sdPosListener = positionPanel;
          wrap._sdScrollEl = scrollEl;
          scrollEl.addEventListener("scroll", positionPanel, { passive: true });
          window.addEventListener("scroll", positionPanel, { passive: true });
        }
      }
    });

    searchInput.addEventListener("input", () => renderOptions());
    optionsBox.addEventListener("click", (ev) => {
      const btn = ev.target instanceof Element ? ev.target.closest(".sd-option") : null;
      if (!(btn instanceof HTMLElement)) return;
      const next = String(btn.dataset.value || "");
      if (sel.value !== next) {
        sel.value = next;
        sel.dispatchEvent(new Event("input", { bubbles: true }));
        sel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      syncTriggerLabel();
      wrap.classList.remove("sd-open");
      sdNotifyClosed(wrap);
      [wrap.closest(".pm-dsp-attr"), wrap.closest(".pm-dsp-attrs"), wrap.closest(".pm-detail-side-panel")].forEach((host) => {
        if (host instanceof HTMLElement) host.classList.remove("sd-host-open");
      });
      if (wrap._sdPosListener) {
        const scrollEl = wrap._sdScrollEl || document.documentElement;
        scrollEl.removeEventListener("scroll", wrap._sdPosListener);
        window.removeEventListener("scroll", wrap._sdPosListener);
        wrap._sdPosListener = null;
        wrap._sdScrollEl = null;
      }
      trigger.setAttribute("aria-expanded", "false");
    });

    sel.addEventListener("change", () => {
      syncTriggerLabel();
      // 閉じているときは候補を作り直さない（開くときに作る。閉じた直後の change で全候補を無駄に組み立てない）
      if (wrap.classList.contains("sd-open")) renderOptions();
    });

    sel.classList.add("sd-native");
    sel.setAttribute("aria-hidden", "true");
    sel.tabIndex = -1;

    const parent = sel.parentElement;
    if (!parent) return;
    parent.insertBefore(wrap, sel);
    wrap.append(trigger, panel, sel);
    syncTriggerLabel();
    renderOptions();
  });
}

// ────────────────────────────────────────────────────────────────
// 開発ページ：自社開発ツールの状況・販売状況を一覧管理
// 閲覧はログインユーザー全員、編集はマスター/管理者または職種「開発」のみ。
// データは API /api/development（新規テーブル fp_developments）。
// ────────────────────────────────────────────────────────────────

// DEV_STATUS_OPTIONS / DEV_CONTRACT_STATUS_OPTIONS / DEV_ALL_STATUS_OPTIONS は
// app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。

/** 状況→CSS用キー（バッジ色分け） */
const DEV_STATUS_KEY = {
  "提案中": "req",
  "要件定義": "req",
  "開発中": "dev",
  "社内利用": "rel",
  "販売中": "sale",
  "販売停止": "stop",
  "リリース済": "rel"
};

/** 種別ごとの状況選択肢 */
function developmentStatusOptionsFor(kind) {
  return kind === "contract" ? DEV_CONTRACT_STATUS_OPTIONS : DEV_STATUS_OPTIONS;
}

/** 編集権限：マスター/管理者 または 職種「開発」 */
function canEditDevelopment() {
  if (!currentUser) return false;
  return isMasterOrAdminUser(currentUser) || primaryJobRole(currentUser) === "開発";
}

/** 状況により「リリース予定」「リリース日」を出し分け */
function developmentReleaseLabel(status) {
  return status === "要件定義" || status === "開発中" ? "リリース予定" : "リリース日";
}

function developmentStatusBadge(status) {
  const s = DEV_ALL_STATUS_OPTIONS.includes(status) ? status : "要件定義";
  const key = DEV_STATUS_KEY[s] || "req";
  return `<span class="dev-status-badge dev-status--${key}">${escapeHtml(s)}</span>`;
}

/** 表示中のリスト種別。'own'=自社ツール / 'contract'=受託開発 */
function developmentCurrentView() {
  return uiState.developmentView === "contract" ? "contract" : "own";
}
function developmentKindOf(d) {
  return d && d.kind === "contract" ? "contract" : "own";
}

/** クライアント選択プルダウン（受託開発用）。clients グローバルから生成。 */
function developmentClientOptions(selectedId) {
  const sorted = (Array.isArray(clients) ? clients.slice() : [])
    // 営業に非表示のクライアントは除外（ただし選択中のものは残してフォーム値を失わない）
    .filter((c) => !c.salesHidden || String(c.id) === String(selectedId))
    .sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "ja")
    );
  return (
    `<option value="">未設定</option>` +
    sorted
      .map(
        (c) =>
          `<option value="${escapeHtmlAttr(c.id)}" ${String(c.id) === String(selectedId) ? "selected" : ""}>${escapeHtml(c.name || c.id)}</option>`
      )
      .join("")
  );
}
function developmentClientName(d) {
  const c = d.clientId ? getClientById(d.clientId) : null;
  return c ? c.name || c.id : "";
}

/** ToDoボードの選択値（"c:<gid>:<cid>" / "g:<gid>" / ""）を draft から組み立て */
function developmentTodoSelectValue(d) {
  if (d.todoChallengeId) return `c:${d.todoGroupId || 0}:${d.todoChallengeId}`;
  if (d.todoGroupId) return `g:${d.todoGroupId}`;
  return "";
}

/** 資料リスト（提案資料/マニュアル）を [{name,url}] に正規化。
 *  旧「資料URL」(docUrl) しか持たない既存データは提案資料1件として拾う（編集して保存すると移行される）。 */
function developmentDocsOf(d, key) {
  const raw = Array.isArray(d && d[key]) ? d[key] : [];
  const list = raw
    .map((x) => ({ name: String((x && x.name) || "").trim(), url: String((x && x.url) || "").trim() }))
    .filter((x) => x.url);
  if (key === "proposalDocs" && !list.length && d && d.docUrl) {
    return [{ name: "提案資料", url: String(d.docUrl).trim() }];
  }
  return list;
}

// ── 資料ファイル（提案資料/マニュアル）のアップロード・ダウンロード ─────────────
// PowerPoint の提案資料を Drive に置かずそのまま登録できるようにするための追加。
// Vercel は本文/応答とも 4.5MB 上限のため、課題添付・許可ツール申請の添付と同じ
// 3MB チャンク分割方式で 15MB まで扱う（サーバー側の正は api/development-attachment.js。
// チャンクサイズ・上限を変えるときは両側を同時に）。
const DEV_DOC_FILE_MAX_BYTES = 15 * 1024 * 1024;
const DEV_DOC_FILE_CHUNK_BYTES = 3 * 1024 * 1024;
const DEV_DOC_FILE_MAX_PER_KIND = 10;
const DEV_DOC_FILE_EXTS = [".ppt", ".pptx", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".zip", ".png", ".jpg", ".jpeg"];
const DEV_DOC_FILE_EXT_HINT = "PowerPoint（.ppt/.pptx）・PDF・Word（.doc/.docx）・Excel（.xls/.xlsx）・ZIP・画像（PNG/JPEG）";

function developmentDocFileExtOk(fileName) {
  const m = String(fileName || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return !!m && DEV_DOC_FILE_EXTS.includes(`.${m[1]}`);
}

/** バイト数を人が読める表記に（一覧・モーダルのファイルサイズ表示用） */
function developmentFileSizeLabel(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}

async function devDocFileApiPost(payload) {
  const r = await apiFetch("/api/development-attachment", { method: "POST", body: payload });
  if (!r.ok) throw new Error(typeof r.data.error === "string" ? r.data.error : "資料の保存に失敗しました");
  return r.data;
}

/** 資料ファイルをアップロードする（3MB 超は分割転送し finalize で合体）。 */
async function uploadDevelopmentDocFile(developmentId, kind, file) {
  if (!authToken) throw new Error("ログインが必要です");
  if (!developmentDocFileExtOk(file.name)) throw new Error(`対応形式のみ: ${DEV_DOC_FILE_EXT_HINT}`);
  if (file.size > DEV_DOC_FILE_MAX_BYTES) throw new Error(`「${file.name}」は15MBを超えています`);
  const buf = await readLocalFileArrayBuffer(file);
  const bytes = new Uint8Array(buf);
  if (bytes.length <= DEV_DOC_FILE_CHUNK_BYTES) {
    return devDocFileApiPost({ developmentId, kind, filename: file.name, fileBase64: arrayBufferToBase64(buf) });
  }
  const uploadId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const totalChunks = Math.ceil(bytes.length / DEV_DOC_FILE_CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const part = bytes.subarray(i * DEV_DOC_FILE_CHUNK_BYTES, (i + 1) * DEV_DOC_FILE_CHUNK_BYTES);
    await devDocFileApiPost({ uploadId, chunkIndex: i, fileBase64: arrayBufferToBase64(part) });
  }
  return devDocFileApiPost({ uploadId, finalize: true, totalChunks, developmentId, kind, filename: file.name });
}

/** 資料ファイルをダウンロードする（4.5MB 応答上限を越えるファイルは分割取得して結合）。
 *  認証ヘッダが要るので <a href> 直リンクにはできない。 */
async function downloadDevelopmentDocFile(attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) return;
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  try {
    const headers = { Authorization: `Bearer ${authToken}` };
    const fetchOk = async (extraQuery) => {
      const res = await fetch(`/api/development-attachment?id=${encodeURIComponent(String(id))}${extraQuery}`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.error === "string" ? j.error : "ファイルの取得に失敗しました");
      }
      return res;
    };
    const meta = await (await fetchOk("&meta=1")).json().catch(() => ({}));
    const size = Number(meta.sizeBytes) || 0;
    const filename = String(meta.filename || "download");
    const type = String(meta.mimeType || "") || "application/octet-stream";
    let blob;
    if (size <= DEV_DOC_FILE_CHUNK_BYTES) {
      blob = await (await fetchOk("")).blob();
    } else {
      const parts = [];
      for (let off = 0; off < size; off += DEV_DOC_FILE_CHUNK_BYTES) {
        parts.push(await (await fetchOk(`&offset=${off}&length=${DEV_DOC_FILE_CHUNK_BYTES}`)).arrayBuffer());
      }
      blob = new Blob(parts, { type });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    showToastError(err instanceof Error && err.message ? err.message : "ダウンロードに失敗しました");
  }
}

/** 保存済みの資料ファイル [{id,filename,sizeBytes}]。kind='proposal'|'manual' */
function developmentFilesOf(d, kind) {
  const key = kind === "manual" ? "manualFiles" : "proposalFiles";
  return Array.isArray(d && d[key]) ? d[key] : [];
}

/** ToDoボードへのリンク（一覧表示用）。未設定なら空文字。 */
function developmentTodoHref(d) {
  const g = d.todoGroupId ? encodeURIComponent(String(d.todoGroupId)) : "";
  const c = d.todoChallengeId ? encodeURIComponent(String(d.todoChallengeId)) : "";
  if (c) return g ? `#progress-management?group=${g}&project=${c}` : `#progress-management?project=${c}`;
  if (g) return `#progress-management?group=${g}`;
  return "";
}

// ── 自社ツール開発の事前申請（承認フロー連動） ────────────────────
// 情報セキュリティ管理規程 C-23 第18条第4号：着手前にシステム開発部へ申請し承認を得る。
// 全ユーザーが申請可能。承認は承認設定「自社ツール開発の事前申請」binding に従い、
// 申請承認ページで承認される。承認されると開発ページ（自社ツール）へ状況「要件定義」で反映される。

const DEV_APP_STATUS_KEY = { "申請中": "pending", "承認": "approved", "却下": "rejected" };

function devAppStatusBadge(status) {
  const s = status === "承認" || status === "却下" ? status : "申請中";
  const key = DEV_APP_STATUS_KEY[s] || "pending";
  return `<span class="at-status-badge at-status--${key}">${escapeHtml(s)}</span>`;
}

function devAppProgressText(a) {
  if (a.status !== "申請中") return "";
  if (!a.chainLength) return "承認者未設定（承認設定をご確認ください）";
  return `承認 ${a.approvedCount}/${a.chainLength}`;
}

async function fetchDevelopmentApplications() {
  if (!authToken) return;
  try {
    const r = await apiFetch("/api/development-applications");
    if (!r.ok) throw new Error(String(r.status));
    const j = r.data;
    uiState.developmentApplications = Array.isArray(j.applications) ? j.applications : [];
    uiState.developmentApplicationsLoaded = true;
    uiState.developmentApplicationsLoadError = false;
  } catch (e) {
    uiState.developmentApplicationsLoaded = true;
    uiState.developmentApplicationsLoadError = true;
  }
  render();
}

const DEV_APPLY_BLANK_DRAFT = {
  title: "",
  purpose: "",
  targetUsers: "",
  alternativeReason: "",
  maintainerUserId: "",
  problemAndRevenue: "",
  securityMeasures: "",
  maintenanceContinuity: "",
  domainServerInfo: "",
  handlesPii: false,
  usesExternalAi: false,
  serviceUrl: "",
  githubUrl: "",
  agree: false
};

// 誤操作で閉じても入力内容を失わないよう、閉じるときの内容を developmentApplyDraftSaved に
// 退避し、次に開くときはそこから復元する（送信成功時のみ空にする）。
function openDevApplyModal() {
  uiState.developmentApplyDraft = uiState.developmentApplyDraftSaved
    ? { ...uiState.developmentApplyDraftSaved }
    : { ...DEV_APPLY_BLANK_DRAFT };
  render();
}
function closeDevApplyModal() {
  if (uiState.developmentApplyDraft) {
    uiState.developmentApplyDraftSaved = { ...uiState.developmentApplyDraft, ...collectDevApplyForm() };
  }
  uiState.developmentApplyDraft = null;
  uiState.developmentApplySaving = false;
  render();
}

function collectDevApplyForm() {
  const byId = (id) => document.getElementById(id);
  return {
    title: (byId("dev-apply-title")?.value || "").trim(),
    purpose: (byId("dev-apply-purpose")?.value || "").trim(),
    targetUsers: (byId("dev-apply-target-users")?.value || "").trim(),
    alternativeReason: (byId("dev-apply-alt-reason")?.value || "").trim(),
    maintainerUserId: byId("dev-apply-maintainer")?.value || "",
    problemAndRevenue: (byId("dev-apply-problem-revenue")?.value || "").trim(),
    securityMeasures: (byId("dev-apply-security")?.value || "").trim(),
    maintenanceContinuity: (byId("dev-apply-continuity")?.value || "").trim(),
    domainServerInfo: (byId("dev-apply-domain-server")?.value || "").trim(),
    handlesPii: !!byId("dev-apply-pii")?.checked,
    usesExternalAi: !!byId("dev-apply-ai")?.checked,
    serviceUrl: (byId("dev-apply-service-url")?.value || "").trim(),
    githubUrl: (byId("dev-apply-github")?.value || "").trim(),
    agree: !!byId("dev-apply-agree")?.checked
  };
}

async function submitDevApplication() {
  if (uiState.developmentApplySaving) return;
  const v = collectDevApplyForm();
  const d = uiState.developmentApplyDraft;
  if (d) Object.assign(d, v);
  if (!v.title) return showToastError("ツール名（仮称）を入力してください");
  if (!v.purpose) return showToastError("目的を入力してください");
  if (!v.targetUsers) return showToastError("想定利用者を入力してください");
  if (!v.alternativeReason) return showToastError("既存ツールで代替できない理由を入力してください");
  if (!v.maintainerUserId) return showToastError("担当責任者を選択してください");
  if (!v.problemAndRevenue) return showToastError("このツールで解決する課題・売上見込みを入力してください");
  if (!v.securityMeasures) return showToastError("セキュリティの対策を入力してください");
  if (!v.domainServerInfo) return showToastError("利用するドメイン・サーバー環境を入力してください");
  if (!v.maintenanceContinuity) return showToastError("保守対策（担当者退職時）を入力してください");
  if (!v.agree) return showToastError("「情報セキュリティ管理規程を確認しました」にチェックしてください");

  uiState.developmentApplySaving = true;
  try {
    const { ok, data: j } = await apiFetch("/api/development-applications", { method: "POST", body: v });
    if (!ok) {
      uiState.developmentApplySaving = false;
      showToastError(typeof j.error === "string" ? j.error : "申請に失敗しました");
      return;
    }
    uiState.developmentApplyDraft = null;
    uiState.developmentApplyDraftSaved = null;
    uiState.developmentApplySaving = false;
    if (j.application) uiState.developmentApplications.unshift(j.application);
    showToast("申請しました");
  } catch (e) {
    uiState.developmentApplySaving = false;
    showToastError("通信に失敗しました");
  }
}

async function withdrawDevApplication(id) {
  const target = uiState.developmentApplications.find((a) => String(a.id) === String(id));
  if (!target) return;
  if (!window.confirm(`「${target.title}」の申請を取り下げます。よろしいですか？`)) return;
  try {
    const { ok, data: j } = await apiFetch("/api/development-applications", { method: "DELETE", body: { id } });
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "取り下げに失敗しました");
      return;
    }
    uiState.developmentApplications = uiState.developmentApplications.filter((a) => String(a.id) !== String(id));
    showToast("取り下げました");
  } catch (e) {
    showToastError("通信に失敗しました");
  }
}

// 却下済み申請の削除（申請者本人またはマスター。サーバー側でも同条件を検証）。
async function deleteRejectedDevApplication(id) {
  const target = uiState.developmentApplications.find((a) => String(a.id) === String(id));
  if (!target) return;
  if (!window.confirm(`却下済みの申請「${target.title}」を削除します。よろしいですか？`)) return;
  const { ok, data } = await apiFetch("/api/development-applications", { method: "DELETE", body: { id } });
  if (!ok) {
    showToastError(typeof data.error === "string" ? data.error : "削除に失敗しました");
    return;
  }
  uiState.developmentApplications = uiState.developmentApplications.filter((a) => String(a.id) !== String(id));
  showToast("削除しました");
  render();
}

// ── 申請承認ページ：承認/却下（承認者本人のみ・サーバで再検証） ──
async function approveDevApplication(id) {
  if (uiState.devAppApprovalSaving) return;
  if (!window.confirm("この申請を承認しますか？")) return;
  uiState.devAppApprovalSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/development-applications", { method: "PUT", body: { id, action: "approve" } });
    uiState.devAppApprovalSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "承認に失敗しました");
      render();
      return;
    }
    showToast(j.application && j.application.status === "承認" ? "承認しました（開発ページに反映）" : "承認しました");
    await fetchDevelopmentApplications();
  } catch (e) {
    uiState.devAppApprovalSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

async function rejectDevApplication() {
  if (uiState.devAppApprovalSaving) return;
  const draft = uiState.devAppRejectDraft;
  if (!draft) return;
  const comment = (document.getElementById("dev-app-reject-comment")?.value || "").trim();
  if (!comment) return showToastError("却下理由を入力してください");
  uiState.devAppApprovalSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/development-applications", { method: "PUT", body: { id: draft.id, action: "reject", reviewComment: comment } });
    uiState.devAppApprovalSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "却下に失敗しました");
      render();
      return;
    }
    uiState.devAppRejectDraft = null;
    showToast("却下しました");
    await fetchDevelopmentApplications();
  } catch (e) {
    uiState.devAppApprovalSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

function devApplicationCardHtml(a) {
  const reviewerLine =
    a.status === "却下" && a.reviewComment
      ? `<div class="at-app-reject">${escapeHtml(a.reviewComment)}</div>`
      : "";
  const progress = devAppProgressText(a);
  // マスターが他人の申請を見る場合のみ申請者名を表示（自分の申請では自明なので出さない）。
  const applicantLine = !a.isMine && a.applicantName ? `<span class="at-app-meta-item">申請者：${escapeHtml(a.applicantName)}</span>` : "";
  const maintainerLine = a.maintainerName ? `<span class="at-app-meta-item">担当責任者：${escapeHtml(a.maintainerName)}</span>` : "";
  const serviceUrlLine = a.serviceUrl ? `<span class="at-app-meta-item">サービスURL：${allowedToolUrlLink(a.serviceUrl)}</span>` : "";
  const githubLine = a.githubUrl ? `<span class="at-app-meta-item">GitHub：${allowedToolUrlLink(a.githubUrl)}</span>` : "";
  // 取り下げは申請者本人のみ／却下済みの削除は本人またはマスター（いずれもサーバー側でも再検証）。
  let actions = "";
  if (a.status === "申請中" && a.isMine) {
    actions = `<div class="at-app-actions"><button type="button" class="cl-btn cl-btn-outline" data-dev-app-withdraw="${escapeHtmlAttr(String(a.id))}">取り下げ</button></div>`;
  } else if (a.status === "却下" && (a.isMine || isMasterRoleUser(currentUser))) {
    actions = `<div class="at-app-actions"><button type="button" class="cl-btn cl-btn-outline" data-dev-app-delete="${escapeHtmlAttr(String(a.id))}">削除</button></div>`;
  }
  return `
    <div class="at-app-card">
      <div class="at-app-card-head">
        <div class="at-app-title">${escapeHtml(a.title)}</div>
        ${devAppStatusBadge(a.status)}
      </div>
      <div class="at-app-meta">
        ${applicantLine}
        ${maintainerLine}
        ${serviceUrlLine}
        ${githubLine}
        ${progress ? `<span class="at-app-meta-item">${escapeHtml(progress)}</span>` : ""}
      </div>
      ${reviewerLine}
      ${actions}
    </div>`;
}

function devApplyModalHtml() {
  const draft = uiState.developmentApplyDraft;
  if (!draft) return "";
  const saving = uiState.developmentApplySaving;
  const maintainerOpts =
    `<option value="">選択してください</option>` +
    users
      .map(
        (u) =>
          `<option value="${escapeHtmlAttr(u.id)}" ${String(u.id) === String(draft.maintainerUserId) ? "selected" : ""}>${escapeHtml(u.name || u.id)}</option>`
      )
      .join("");

  return `
    <div class="cl-mini-modal-overlay open">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="dev-apply-title-h">
        <div class="cl-mini-modal-head">
          <h4 id="dev-apply-title-h">自社ツール開発の事前申請</h4>
          <button class="cl-modal-close" type="button" data-dev-app-modal-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="at-apply-note">情報セキュリティ管理規程（C-23 第18条第4号）に基づく事前申請です。承認されると開発ページ（自社ツール）に「要件定義」として登録されます。</p>
          <label class="cl-form-group">
            <span class="cl-form-label">ツール名（仮称） <span class="cl-form-required">*</span></span>
            <input id="dev-apply-title" class="cl-form-input" value="${escapeHtmlAttr(draft.title)}" placeholder="例：原価突合アシスタント" />
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">目的 <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-purpose" class="cl-form-input" rows="2" placeholder="何のために作るか">${escapeHtml(draft.purpose)}</textarea>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">想定利用者 <span class="cl-form-required">*</span></span>
            <input id="dev-apply-target-users" class="cl-form-input" value="${escapeHtmlAttr(draft.targetUsers)}" placeholder="例：経理担当2名 / 全社員" />
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">既存のツール・サービスで代替できない理由 <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-alt-reason" class="cl-form-input" rows="2" placeholder="既存ツールで代替できない理由（代替できる場合は既存優先）">${escapeHtml(draft.alternativeReason)}</textarea>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">このツールで解決する課題・売上見込み <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-problem-revenue" class="cl-form-input" rows="2" placeholder="解決する課題、想定される売上・工数削減効果など">${escapeHtml(draft.problemAndRevenue)}</textarea>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">セキュリティの対策 <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-security" class="cl-form-input" rows="2" placeholder="認証・アクセス制御、個人情報の扱い、外部送信時の対策など">${escapeHtml(draft.securityMeasures)}</textarea>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">利用するドメイン・サーバー環境 <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-domain-server" class="cl-form-input" rows="2" placeholder="例：Vercel＋独自ドメイン取得予定 / 社内サーバーで完結し外部公開なし など">${escapeHtml(draft.domainServerInfo)}</textarea>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">担当責任者（問題があった場合の責任の所在） <span class="cl-form-required">*</span></span>
            <select id="dev-apply-maintainer" class="cl-form-input">${maintainerOpts}</select>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">保守対策（担当者が退職した場合） <span class="cl-form-required">*</span></span>
            <textarea id="dev-apply-continuity" class="cl-form-input" rows="2" placeholder="引き継ぎ・ドキュメント整備・後任の想定など">${escapeHtml(draft.maintenanceContinuity)}</textarea>
          </label>
          <label class="cl-form-group at-agree">
            <input id="dev-apply-pii" type="checkbox" ${draft.handlesPii ? "checked" : ""} />
            <span>個人情報・顧客データを扱う</span>
          </label>
          <label class="cl-form-group at-agree">
            <input id="dev-apply-ai" type="checkbox" ${draft.usesExternalAi ? "checked" : ""} />
            <span>外部AI・API（Claude API等）を利用する</span>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">サービスURL（任意）</span>
            <input id="dev-apply-service-url" class="cl-form-input" type="url" value="${escapeHtmlAttr(draft.serviceUrl || "")}" placeholder="例：https://xxxxx.oe-onboarding.site" />
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">GitHubのURL（OneEightyアカウントのみ）</span>
            <input id="dev-apply-github" class="cl-form-input" type="url" value="${escapeHtmlAttr(draft.githubUrl || "")}" placeholder="例：https://github.com/OneEightyGroup/xxxxx" />
          </label>
          <label class="cl-form-group at-agree">
            <input id="dev-apply-agree" type="checkbox" ${draft.agree ? "checked" : ""} />
            <span>情報セキュリティ管理規程を確認しました <span class="cl-form-required">*</span></span>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-dev-app-modal-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-dev-app-submit="1" ${saving ? "disabled" : ""}>${saving ? "申請中…" : "申請する"}</button>
        </div>
      </div>
    </div>`;
}

async function fetchDevelopments() {
  if (!authToken) return;
  try {
    const r = await apiFetch("/api/development");
    if (!r.ok) throw new Error(String(r.status));
    const j = r.data;
    uiState.developments = Array.isArray(j.developments) ? j.developments : [];
    uiState.developmentsLoaded = true;
    uiState.developmentsLoadError = false;
  } catch (e) {
    uiState.developmentsLoaded = true;
    uiState.developmentsLoadError = true;
  }
  render();
}

/** ToDoボード候補（テーマ＋課題）を取得してキャッシュ */
async function fetchDevelopmentTodoOptions() {
  if (!authToken) return;
  try {
    const r = await apiFetch("/api/progress-management?scope=summary");
    if (!r.ok) throw new Error(String(r.status));
    const j = r.data;
    uiState.developmentTodoOptions = {
      groups: Array.isArray(j.groups) ? j.groups : [],
      challenges: Array.isArray(j.challenges) ? j.challenges : []
    };
  } catch (e) {
    uiState.developmentTodoOptions = { groups: [], challenges: [], error: true };
  }
  if (uiState.developmentEditDraft) render();
}

function openDevelopmentEditor(id) {
  if (!canEditDevelopment()) return;
  const existing = id != null ? uiState.developments.find((d) => String(d.id) === String(id)) : null;
  uiState.developmentEditDraft = existing
    ? {
        ...existing,
        // 旧「資料URL」(docUrl) は提案資料の1件目として引き継ぎ、保存時に proposalDocs へ移す。
        // 資料欄を出さない受託開発では移行しないので docUrl は既存値のまま残す。
        proposalDocs: developmentDocsOf(existing, "proposalDocs"),
        manualDocs: developmentDocsOf(existing, "manualDocs"),
        docUrl: developmentKindOf(existing) === "own" ? "" : existing.docUrl || ""
      }
    : {
        id: null,
        kind: developmentCurrentView(),
        name: "",
        assigneeId: "",
        devAssigneeId: "",
        releaseDate: "",
        todoGroupId: null,
        todoChallengeId: null,
        todoLabel: "",
        status: developmentCurrentView() === "contract" ? "提案中" : "要件定義",
        notForSale: false,
        salesCount: 0,
        salesAmount: 0,
        clientId: "",
        orderAmount: 0,
        runningAmount: 0,
        genre: "",
        memo: "",
        demoUrl: "",
        demoId: "",
        demoPassword: "",
        demoNote: "",
        docUrl: "",
        proposalDocs: [],
        manualDocs: []
      };
  if (!uiState.developmentTodoOptions) void fetchDevelopmentTodoOptions();
  render();
}

function closeDevelopmentEditor() {
  uiState.developmentEditDraft = null;
  uiState.developmentSaving = false;
  render();
}

/** 資料リスト（提案資料/マニュアル）の1行。行の追加/削除は再描画せず DOM を直接いじる
 *  （再描画すると同じモーダル内の未保存入力が消えるため）。 */
function developmentDocEditRowHtml(doc) {
  const d = doc || { name: "", url: "" };
  return (
    `<div class="dev-doc-edit-row">` +
    `<input class="cl-form-input dev-doc-edit-name" value="${escapeHtmlAttr(d.name || "")}" placeholder="資料名（例：サービス紹介 2026年版）" />` +
    `<input class="cl-form-input dev-doc-edit-url" value="${escapeHtmlAttr(d.url || "")}" placeholder="https://… ダウンロードURL" />` +
    `<button type="button" class="dev-doc-edit-del" data-dev-doc-remove="1" aria-label="この資料を削除">✕</button>` +
    `</div>`
  );
}

/** 保存済みファイルの1行（✕で削除予定にする＝DOMから外し、保存時に差分でDELETEする） */
function developmentDocFileRowHtml(f) {
  return (
    `<div class="dev-doc-file-row" data-dev-file-id="${escapeHtmlAttr(String(f.id))}">` +
    `<span class="dev-doc-file-name">${escapeHtml(f.filename)}</span>` +
    `<span class="dev-doc-file-size">${escapeHtml(developmentFileSizeLabel(f.sizeBytes))}</span>` +
    `<button type="button" class="dev-doc-edit-del" data-dev-file-remove="1" aria-label="このファイルを削除">✕</button>` +
    `</div>`
  );
}

function developmentDocEditSectionHtml(kind, title, docs, files) {
  const k = escapeHtmlAttr(kind);
  return `
          <div class="dev-doc-fieldset">
            <div class="dev-doc-fieldset-head">
              <span class="dev-doc-fieldset-title">${escapeHtml(title)}</span>
              <button type="button" class="dev-doc-add-btn" data-dev-doc-add="${k}">＋ URLを追加</button>
            </div>
            <div class="dev-doc-rows" id="dev-f-docs-${k}">${docs.map((x) => developmentDocEditRowHtml(x)).join("")}</div>
            <div class="dev-doc-files" id="dev-f-files-${k}">${files.map((f) => developmentDocFileRowHtml(f)).join("")}</div>
            <label class="dev-doc-file-pick">
              <span class="dev-doc-file-pick-label">ファイルを追加（${escapeHtml(DEV_DOC_FILE_EXT_HINT)}／1件15MBまで）</span>
              <input type="file" id="dev-f-file-input-${k}" class="dev-doc-file-input" multiple accept="${escapeHtmlAttr(DEV_DOC_FILE_EXTS.join(","))}" data-dev-file-input="${k}" />
            </label>
            <div class="dev-doc-file-pending" id="dev-f-file-pending-${k}"></div>
            <p class="dev-doc-hint">複数登録できます。URLは入力した行だけ保存され、ファイルは保存時にアップロードされます。一覧の「資料」から選んでダウンロードできます。</p>
          </div>`;
}

/** 保存時に残すファイルID（✕で外された分は差分で削除する） */
function collectDevelopmentKeepFileIds(kind) {
  const wrap = document.getElementById(`dev-f-files-${kind}`);
  if (!wrap) return null;
  return Array.from(wrap.querySelectorAll("[data-dev-file-id]")).map((el) => String(el.dataset.devFileId));
}

/** 追加で選ばれたファイル（input が DOM に保持しているものをそのまま読む） */
function collectDevelopmentNewFiles(kind) {
  const input = document.getElementById(`dev-f-file-input-${kind}`);
  return input instanceof HTMLInputElement && input.files ? Array.from(input.files) : [];
}

/** 資料リストをモーダルの DOM から読み取る（URL未入力の行は捨てる）。
 *  受託開発の編集モーダルには資料欄が無い。そこで空配列を返すと保存時に既存の資料を
 *  消してしまうため、欄が描画されていないときは null を返して「未編集」を示す。 */
function collectDevelopmentDocs(kind) {
  const wrap = document.getElementById(`dev-f-docs-${kind}`);
  if (!wrap) return null;
  return Array.from(wrap.querySelectorAll(".dev-doc-edit-row"))
    .map((row) => ({
      name: (row.querySelector(".dev-doc-edit-name")?.value || "").trim(),
      url: (row.querySelector(".dev-doc-edit-url")?.value || "").trim()
    }))
    .filter((x) => x.url);
}

/** モーダルの DOM から保存値を読み取り、ToDo選択をパースする */
function collectDevelopmentForm() {
  const byId = (id) => document.getElementById(id);
  const name = (byId("dev-f-name")?.value || "").trim();
  const assigneeId = byId("dev-f-assignee")?.value || "";
  const devAssigneeId = byId("dev-f-dev-assignee")?.value || "";
  const status = byId("dev-f-status")?.value || "要件定義";
  const notForSale = !!byId("dev-f-not-for-sale")?.checked;
  const releaseDate = byId("dev-f-release")?.value || "";
  const salesCount = byId("dev-f-count")?.value || "0";
  const salesAmount = byId("dev-f-amount")?.value || "0";
  const clientId = byId("dev-f-client")?.value || "";
  const orderAmount = byId("dev-f-order-amount")?.value || "0";
  const runningAmount = byId("dev-f-running-amount")?.value || "0";
  const genre = byId("dev-f-genre")?.value || "";
  const memo = byId("dev-f-memo")?.value || "";
  const demoUrl = byId("dev-f-demo-url")?.value || "";
  const demoId = byId("dev-f-demo-id")?.value || "";
  const demoPassword = byId("dev-f-demo-pass")?.value || "";
  const demoNote = byId("dev-f-demo-note")?.value || "";
  const proposalDocs = collectDevelopmentDocs("proposal");
  const manualDocs = collectDevelopmentDocs("manual");
  const todoRaw = byId("dev-f-todo")?.value || "";

  let todoGroupId = null;
  let todoChallengeId = null;
  let todoLabel = "";
  const opts = uiState.developmentTodoOptions || { groups: [], challenges: [] };
  if (todoRaw.startsWith("c:")) {
    const parts = todoRaw.split(":");
    const g = Number(parts[1]);
    const c = Number(parts[2]);
    todoGroupId = Number.isFinite(g) && g > 0 ? g : null;
    todoChallengeId = Number.isFinite(c) && c > 0 ? c : null;
    const ch = opts.challenges.find((x) => String(x.id) === String(c));
    const grp = opts.groups.find((x) => String(x.id) === String(todoGroupId));
    todoLabel = [grp?.title, ch?.title].filter(Boolean).join(" / ");
  } else if (todoRaw.startsWith("g:")) {
    const g = Number(todoRaw.slice(2));
    todoGroupId = Number.isFinite(g) && g > 0 ? g : null;
    const grp = opts.groups.find((x) => String(x.id) === String(todoGroupId));
    todoLabel = grp?.title || "";
  }
  // 候補に無い保存済みの紐づけ先は、選択肢の表示名（保存済みのラベル）を引き継ぐ
  if (!todoLabel && todoRaw) todoLabel = String(byId("dev-f-todo")?.selectedOptions?.[0]?.textContent || "").trim();
  return { name, assigneeId, devAssigneeId, status, notForSale, releaseDate, salesCount, salesAmount, clientId, orderAmount, runningAmount, genre, memo, demoUrl, demoId, demoPassword, demoNote, proposalDocs, manualDocs, todoGroupId, todoChallengeId, todoLabel };
}

/** 本体保存後に資料ファイルの削除・追加を適用し、saved に最新のファイル一覧を載せる。
 *  失敗したものはエラー文言の配列で返す（本体の保存自体は成功しているため中断しない）。 */
async function applyDevelopmentDocFiles(saved, draft, filesState) {
  const errors = [];
  // 15MB のアップロードは数秒〜かかる。保存中は再描画しない（未保存入力を消さないため）ので
  // 無反応に見えないよう、再描画を伴わないトーストで進行中であることだけ伝える。
  // 完了トースト（showToast＝再描画側）と二重に出ないよう、終わったら必ず自分で外す。
  const addCount = ["proposal", "manual"].reduce((a, k) => a + ((filesState[k] && filesState[k].add.length) || 0), 0);
  let progressToast = null;
  if (addCount) {
    showToastDirect(`ファイルを${addCount}件アップロードしています…`, 60000);
    progressToast = document.querySelector(".toast");
  }
  try {
    for (const kind of ["proposal", "manual"]) {
      const listKey = kind === "manual" ? "manualFiles" : "proposalFiles";
      const existing = developmentFilesOf(draft, kind);
      const st = filesState[kind];
      // 資料欄を描画しない受託開発では keep が null。既存をそのまま残す。
      if (!st || st.keep == null) {
        saved[listKey] = existing;
        continue;
      }
      const keep = new Set(st.keep.map(String));
      const kept = existing.filter((f) => keep.has(String(f.id)));
      for (const f of existing.filter((x) => !keep.has(String(x.id)))) {
        const r = await apiFetch("/api/development-attachment", { method: "DELETE", body: { id: f.id } });
        if (r.ok) continue;
        // 消せていないものを消えたように見せない
        kept.push(f);
        errors.push(typeof r.data.error === "string" ? r.data.error : `「${f.filename}」の削除に失敗しました`);
      }
      for (const file of st.add) {
        if (kept.length >= DEV_DOC_FILE_MAX_PER_KIND) {
          errors.push(`ファイルは1種別につき${DEV_DOC_FILE_MAX_PER_KIND}件までです（「${file.name}」は追加されませんでした）`);
          continue;
        }
        try {
          const r = await uploadDevelopmentDocFile(saved.id, kind, file);
          if (r && r.attachment) kept.push(r.attachment);
        } catch (err) {
          errors.push(err instanceof Error && err.message ? err.message : `「${file.name}」のアップロードに失敗しました`);
        }
      }
      saved[listKey] = kept;
    }
  } finally {
    if (progressToast && progressToast.isConnected) progressToast.remove();
  }
  return errors;
}

async function saveDevelopment() {
  if (!canEditDevelopment() || uiState.developmentSaving) return;
  const draft = uiState.developmentEditDraft;
  if (!draft) return;
  const v = collectDevelopmentForm();
  if (!v.name) {
    showToastDirect("ツール名を入力してください");
    return;
  }
  // ファイルの状態はモーダルの DOM が生きているうちに読み切る（保存後は閉じてしまうため）。
  const filesState = {
    proposal: { keep: collectDevelopmentKeepFileIds("proposal"), add: collectDevelopmentNewFiles("proposal") },
    manual: { keep: collectDevelopmentKeepFileIds("manual"), add: collectDevelopmentNewFiles("manual") }
  };
  const isNew = draft.id == null;
  const body = {
    id: draft.id,
    kind: draft.kind === "contract" ? "contract" : "own",
    name: v.name,
    assigneeId: v.assigneeId,
    devAssigneeId: v.devAssigneeId,
    releaseDate: v.releaseDate,
    todoGroupId: v.todoGroupId,
    todoChallengeId: v.todoChallengeId,
    todoLabel: v.todoLabel,
    status: v.status,
    notForSale: v.notForSale,
    salesCount: v.salesCount,
    salesAmount: v.salesAmount,
    clientId: v.clientId,
    orderAmount: v.orderAmount,
    runningAmount: v.runningAmount,
    genre: v.genre,
    memo: v.memo,
    demoUrl: v.demoUrl,
    demoId: v.demoId,
    demoPassword: v.demoPassword,
    demoNote: v.demoNote,
    // 旧 docUrl は自社ツールの編集時のみ提案資料へ移行済み（draft.docUrl は空）。
    // 資料欄が無い受託開発では draft の値をそのまま返し、既存データを消さない。
    docUrl: draft.docUrl || "",
    proposalDocs: v.proposalDocs || (Array.isArray(draft.proposalDocs) ? draft.proposalDocs : []),
    manualDocs: v.manualDocs || (Array.isArray(draft.manualDocs) ? draft.manualDocs : [])
  };
  uiState.developmentSaving = true;
  try {
    const { ok, data: j } = await apiFetch("/api/development", { method: isNew ? "POST" : "PUT", body: body });
    if (!ok) {
      uiState.developmentSaving = false;
      showToastError(typeof j.error === "string" ? j.error : "保存に失敗しました");
      return;
    }
    const saved = j.development;
    let fileErrors = [];
    if (saved) {
      // 本体保存が通ってから資料ファイルを反映する（新規追加でも id が確定してから送れる）。
      fileErrors = await applyDevelopmentDocFiles(saved, draft, filesState);
      const i = uiState.developments.findIndex((d) => String(d.id) === String(saved.id));
      if (i >= 0) uiState.developments[i] = saved;
      else uiState.developments.push(saved);
    }
    uiState.developmentEditDraft = null;
    uiState.developmentSaving = false;
    if (fileErrors.length) showToastError(fileErrors[0]);
    else showToast(isNew ? "開発ツールを追加しました" : "保存しました");
  } catch (e) {
    uiState.developmentSaving = false;
    showToastError("通信に失敗しました");
  }
}

async function deleteDevelopment(id) {
  if (!canEditDevelopment()) return;
  const target = uiState.developments.find((d) => String(d.id) === String(id));
  if (!target) return;
  if (!window.confirm(`「${target.name}」を削除します。よろしいですか？`)) return;
  try {
    const { ok, data: j } = await apiFetch("/api/development", { method: "DELETE", body: { id } });
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "削除に失敗しました");
      return;
    }
    uiState.developments = uiState.developments.filter((d) => String(d.id) !== String(id));
    showToast("削除しました");
  } catch (e) {
    showToastError("通信に失敗しました");
  }
}

/** http(s) を補完した外部URL。スキーム無しは https を付与。 */
function developmentSafeUrl(u) {
  const s = String(u || "").trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

/** デモURLセル（全文折返し表示＋コピー付き） */
function developmentDemoUrlCell(d) {
  if (!d.demoUrl) return `<td class="dev-td-left"><span class="dev-muted">—</span></td>`;
  return `<td class="dev-td-left dev-td-wrap"><span class="dev-cell-copy"><a class="dev-todo-link dev-cell-copy-val" href="${escapeHtmlAttr(developmentSafeUrl(d.demoUrl))}" target="_blank" rel="noopener">${escapeHtml(d.demoUrl)}</a><button type="button" class="dev-copy-btn" data-dev-copy="${escapeHtmlAttr(d.demoUrl)}" aria-label="URLをコピー">コピー</button></span></td>`;
}

/** デモIDセル（全文折返し表示＋コピー付き） */
function developmentDemoIdCell(d) {
  if (!d.demoId) return `<td class="dev-td-left"><span class="dev-muted">—</span></td>`;
  return `<td class="dev-td-left dev-td-wrap"><span class="dev-cell-copy"><span class="dev-cell-copy-val">${escapeHtml(d.demoId)}</span><button type="button" class="dev-copy-btn" data-dev-copy="${escapeHtmlAttr(d.demoId)}" aria-label="IDをコピー">コピー</button></span></td>`;
}

/** 資料セル：提案資料・マニュアルの件数を、種別ごとに独立したチップとして常に出す
 *  （0件でも「—」にせず 0 と出す）。どちらを押しても中央モーダルが開き、
 *  0件のときは中身が「登録なし」と分かる。 */
function developmentDocsCell(d) {
  const id = escapeHtmlAttr(String(d.id));
  const count = (kind, docKey) => developmentDocsOf(d, docKey).length + developmentFilesOf(d, kind).length;
  const chip = (label, n) =>
    `<button type="button" class="dev-doc-chip${n ? "" : " dev-doc-chip--zero"}" data-dev-docs="${id}" ` +
    `title="${escapeHtmlAttr(`${label}：${n}件`)}">` +
    `<span class="dev-doc-chip-label">${escapeHtml(label)}</span>` +
    `<span class="dev-doc-chip-count">${n}</span></button>`;
  return (
    `<td class="dev-td-wrap"><span class="dev-doc-chips">` +
    chip("提案", count("proposal", "proposalDocs")) +
    chip("マニュアル", count("manual", "manualDocs")) +
    `</span></td>`
  );
}

/** デモパスワードセル（伏字＋表示トグル＋コピー） */
function developmentDemoPassCell(d) {
  if (!d.demoPassword) return `<td class="dev-td-left"><span class="dev-muted">—</span></td>`;
  const revealed = !!uiState.developmentRevealPass[String(d.id)];
  const shown = revealed ? escapeHtml(d.demoPassword) : "••••••••";
  return (
    `<td class="dev-td-left dev-td-wrap"><span class="dev-cell-copy"><span class="dev-cell-copy-val dev-demo-pass">${shown}</span>` +
    `<button type="button" class="dev-copy-btn" data-dev-pass-toggle="${escapeHtmlAttr(String(d.id))}">${revealed ? "隠す" : "表示"}</button>` +
    `<button type="button" class="dev-copy-btn" data-dev-copy="${escapeHtmlAttr(d.demoPassword)}" aria-label="パスワードをコピー">コピー</button></span></td>`
  );
}

function developmentRowHtml(d, canEdit) {
  const assignee = d.assigneeId ? getUserById(d.assigneeId) : null;
  const assigneeName = assignee ? escapeHtml(assignee.name || assignee.id) : `<span class="dev-muted">—</span>`;
  const releaseVal = d.releaseDate ? escapeHtml(d.releaseDate) : `<span class="dev-muted">未定</span>`;
  const href = developmentTodoHref(d);
  const todoVal = href
    ? `<a href="${escapeHtmlAttr(href)}" class="dev-todo-link">${escapeHtml(d.todoLabel || "開く")}</a>`
    : `<span class="dev-muted">—</span>`;
  const nameCell = canEdit
    ? `<button type="button" class="dev-name-btn" data-dev-edit="${escapeHtmlAttr(String(d.id))}">${escapeHtml(d.name)}</button>`
    : escapeHtml(d.name);
  return `
    <tr>
      <td class="pj-td-name">${nameCell}</td>
      <td>${developmentStatusBadge(d.status)}</td>
      <td>${assigneeName}</td>
      <td>${releaseVal}</td>
      ${developmentDocsCell(d)}
      <td class="dev-td-left dev-td-wrap">${todoVal}</td>
      ${developmentDemoUrlCell(d)}
      ${developmentDemoIdCell(d)}
      ${developmentDemoPassCell(d)}
    </tr>`;
}

/** 受託開発リストの行（営業/開発担当・クライアント・受注/見積もり額を表示。メモは長文で横伸びするため一覧では非表示、編集モーダルで確認/編集する） */
function developmentContractRowHtml(d, canEdit) {
  const salesUser = d.assigneeId ? getUserById(d.assigneeId) : null;
  const salesName = salesUser ? escapeHtml(salesUser.name || salesUser.id) : `<span class="dev-muted">—</span>`;
  const devUser = d.devAssigneeId ? getUserById(d.devAssigneeId) : null;
  const devName = devUser ? escapeHtml(devUser.name || devUser.id) : `<span class="dev-muted">—</span>`;
  // リリース済はリリース日を「—」表示にする
  const releaseVal = d.status === "リリース済"
    ? `<span class="dev-muted">—</span>`
    : d.releaseDate ? escapeHtml(d.releaseDate) : `<span class="dev-muted">未定</span>`;
  const genreCell = d.genre ? escapeHtml(d.genre) : `<span class="dev-muted">—</span>`;
  const clientNm = developmentClientName(d);
  const clientCell = clientNm ? escapeHtml(clientNm) : `<span class="dev-muted">—</span>`;
  const amountCell = d.orderAmount ? `¥${formatYenPlain(d.orderAmount)}` : `<span class="dev-muted">—</span>`;
  const runningCell = d.runningAmount ? `¥${formatYenPlain(d.runningAmount)}` : `<span class="dev-muted">—</span>`;
  const href = developmentTodoHref(d);
  const todoVal = href
    ? `<a href="${escapeHtmlAttr(href)}" class="dev-todo-link">${escapeHtml(d.todoLabel || "開く")}</a>`
    : `<span class="dev-muted">—</span>`;
  const nameCell = canEdit
    ? `<button type="button" class="dev-name-btn" data-dev-edit="${escapeHtmlAttr(String(d.id))}">${escapeHtml(d.name)}</button>`
    : escapeHtml(d.name);
  return `
    <tr>
      <td class="pj-td-name">${nameCell}</td>
      <td>${developmentStatusBadge(d.status)}</td>
      <td>${releaseVal}</td>
      <td>${salesName}</td>
      <td>${devName}</td>
      <td>${genreCell}</td>
      <td>${clientCell}</td>
      <td class="dev-td-num">${amountCell}</td>
      <td class="dev-td-num">${runningCell}</td>
      <td>${todoVal}</td>
    </tr>`;
}

function developmentEditorModalHtml() {
  const draft = uiState.developmentEditDraft;
  if (!draft) return "";
  const isNew = draft.id == null;
  const assigneeOptions = (selectedId) =>
    `<option value="">未設定</option>` +
    users
      .map(
        (u) =>
          `<option value="${escapeHtmlAttr(u.id)}" ${String(u.id) === String(selectedId) ? "selected" : ""}>${escapeHtml(u.name || u.id)}</option>`
      )
      .join("");
  const assigneeOpts = assigneeOptions(draft.assigneeId);
  const devAssigneeOpts = assigneeOptions(draft.devAssigneeId);
  // 種別ごとの選択肢。既存データの状況が選択肢外でも消えないよう、無ければ先頭に補う。
  const statusList = developmentStatusOptionsFor(draft.kind);
  const statusListForDraft = statusList.includes(draft.status) ? statusList : [draft.status, ...statusList];
  const statusOpts = statusListForDraft.map(
    (s) => `<option value="${escapeHtmlAttr(s)}" ${s === draft.status ? "selected" : ""}>${escapeHtml(s)}</option>`
  ).join("");

  const opts = uiState.developmentTodoOptions;
  const selVal = developmentTodoSelectValue(draft);
  let todoSelectInner = `<option value="">未設定</option>`;
  if (!opts) {
    todoSelectInner = `<option value="${escapeHtmlAttr(selVal)}" selected>読み込み中…</option>`;
  } else {
    // フォルダ1段化でテーマ層は廃止。開発ページではジャンル「開発」のプロジェクト名だけを
    // 候補にし、旧フォルダ分類（テーマ名プレフィックス・「（全体）」）は出さない。
    const groups = opts.groups || [];
    const challenges = opts.challenges || [];
    let html = `<option value="" ${selVal === "" ? "selected" : ""}>未設定</option>`;
    // 旧テーマ（グループ）への紐づけは新規候補に出さないが、既存の保存済み選択だけは
    // 編集時に消えないよう残す。
    groups.forEach((g) => {
      const val = `g:${String(g.id)}`;
      if (val !== selVal) return;
      html += `<option value="${escapeHtmlAttr(val)}" selected>${escapeHtml(g.title)}（全体）</option>`;
    });
    challenges.forEach((c) => {
      const gid = c.todoGroupId == null ? "0" : String(c.todoGroupId);
      const val = `c:${gid}:${c.id}`;
      // ジャンル「開発」のみ。ただし保存済み選択(selVal)は分類に関わらず残す。
      if ((c.genre || "") !== "開発" && val !== selVal) return;
      html += `<option value="${escapeHtmlAttr(val)}" ${val === selVal ? "selected" : ""}>${escapeHtml(c.title)}</option>`;
    });
    // 保存済みの紐づけ先が候補に無い（見られないプロジェクト・アーカイブ済み・候補の取得失敗）ときも選択肢に残す。
    // 残さないと「未設定」が選ばれ、開発ツールを保存しただけで ToDo の紐づけが消えていた
    if (selVal && !html.includes(`value="${escapeHtmlAttr(selVal)}"`)) {
      html += `<option value="${escapeHtmlAttr(selVal)}" selected>${escapeHtml(draft.todoLabel || "（表示できないプロジェクト）")}</option>`;
    }
    todoSelectInner = html;
  }

  const deleteBtn = isNew
    ? ""
    : `<button type="button" class="cl-btn cl-btn-outline dev-btn-danger" data-dev-delete="${escapeHtmlAttr(String(draft.id))}">削除</button>`;
  const saving = uiState.developmentSaving;

  const isContract = draft.kind === "contract";
  // 種別ごとに中盤のフィールドを出し分け（自社ツール=販売/デモ、受託=クライアント/受注額）
  const kindFields = isContract
    ? `
          <label class="cl-form-group">
            <span class="cl-form-label">クライアント</span>
            <select id="dev-f-client" class="cl-form-input">${developmentClientOptions(draft.clientId)}</select>
          </label>
          <div class="dev-form-row">
            <label class="cl-form-group">
              <span class="cl-form-label">初期費用</span>
              <input id="dev-f-order-amount" type="number" min="0" class="cl-form-input" value="${escapeHtmlAttr(String(draft.orderAmount || 0))}" />
            </label>
            <label class="cl-form-group">
              <span class="cl-form-label">ランニング</span>
              <input id="dev-f-running-amount" type="number" min="0" class="cl-form-input" value="${escapeHtmlAttr(String(draft.runningAmount || 0))}" />
            </label>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">メモ</span>
            <textarea id="dev-f-memo" class="cl-form-input" rows="2" placeholder="補足・進捗状況など">${escapeHtml(draft.memo || "")}</textarea>
          </label>`
    : `
          <label class="cl-inline-check dev-not-for-sale-check">
            <input id="dev-f-not-for-sale" type="checkbox" class="cl-delivery-method-checkbox" ${draft.notForSale ? "checked" : ""} />
            <span>販売しない（社内利用など）</span>
          </label>
          <div class="dev-form-row">
            <label class="cl-form-group">
              <span class="cl-form-label">販売件数</span>
              <input id="dev-f-count" type="number" min="0" class="cl-form-input" value="${escapeHtmlAttr(String(draft.salesCount || 0))}" />
            </label>
            <label class="cl-form-group">
              <span class="cl-form-label">販売額</span>
              <input id="dev-f-amount" type="number" min="0" class="cl-form-input" value="${escapeHtmlAttr(String(draft.salesAmount || 0))}" />
            </label>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">メモ</span>
            <textarea id="dev-f-memo" class="cl-form-input" rows="2" placeholder="補足・社内/社外の区別など">${escapeHtml(draft.memo || "")}</textarea>
          </label>
${developmentDocEditSectionHtml("proposal", "提案資料", Array.isArray(draft.proposalDocs) ? draft.proposalDocs : [], developmentFilesOf(draft, "proposal"))}
${developmentDocEditSectionHtml("manual", "マニュアル", Array.isArray(draft.manualDocs) ? draft.manualDocs : [], developmentFilesOf(draft, "manual"))}
          <div class="dev-demo-fieldset">
            <div class="dev-demo-fieldset-title">デモアカウント</div>
            <label class="cl-form-group">
              <span class="cl-form-label">ログインURL</span>
              <input id="dev-f-demo-url" class="cl-form-input" value="${escapeHtmlAttr(draft.demoUrl || "")}" placeholder="https://admin.example.com/login" />
            </label>
            <div class="dev-form-row">
              <label class="cl-form-group">
                <span class="cl-form-label">ID（メール等）</span>
                <input id="dev-f-demo-id" class="cl-form-input" value="${escapeHtmlAttr(draft.demoId || "")}" placeholder="demo@example.com" />
              </label>
              <label class="cl-form-group">
                <span class="cl-form-label">パスワード</span>
                <div class="dm-pw-input-wrap">
                  <input id="dev-f-demo-pass" type="password" class="cl-form-input" value="${escapeHtmlAttr(draft.demoPassword || "")}" placeholder="デモ用パスワード" autocomplete="new-password" />
                  <button type="button" class="dm-pw-toggle" data-dev-form-pw-toggle="1" title="表示" aria-label="パスワードを表示">👁</button>
                </div>
              </label>
            </div>
            <label class="cl-form-group">
              <span class="cl-form-label">その他メモ</span>
              <textarea id="dev-f-demo-note" class="cl-form-input" rows="2" placeholder="ログイン手順・注意点など">${escapeHtml(draft.demoNote || "")}</textarea>
            </label>
          </div>`;
  // 担当欄：受託は営業担当＋開発担当に分割、自社ツールは従来どおり単一の担当者。
  const assigneeStatusSection = isContract
    ? `
          <div class="dev-form-row">
            <label class="cl-form-group">
              <span class="cl-form-label">営業担当</span>
              <select id="dev-f-assignee" class="cl-form-input">${assigneeOpts}</select>
            </label>
            <label class="cl-form-group">
              <span class="cl-form-label">開発担当</span>
              <select id="dev-f-dev-assignee" class="cl-form-input">${devAssigneeOpts}</select>
            </label>
          </div>
          <div class="dev-form-row">
            <label class="cl-form-group">
              <span class="cl-form-label">ジャンル</span>
              <input id="dev-f-genre" class="cl-form-input" value="${escapeHtmlAttr(draft.genre || "")}" placeholder="例：コーポレートサイト / 業務システム" />
            </label>
            <label class="cl-form-group">
              <span class="cl-form-label">状況</span>
              <select id="dev-f-status" class="cl-form-input">${statusOpts}</select>
            </label>
          </div>`
    : `
          <div class="dev-form-row">
            <label class="cl-form-group">
              <span class="cl-form-label">担当者</span>
              <select id="dev-f-assignee" class="cl-form-input">${assigneeOpts}</select>
            </label>
            <label class="cl-form-group">
              <span class="cl-form-label">状況</span>
              <select id="dev-f-status" class="cl-form-input">${statusOpts}</select>
            </label>
          </div>`;
  const nameLabel = isContract ? "案件名" : "ツール名";
  const namePlaceholder = isContract ? "例：◯◯社 コーポレートサイト制作" : "例：デジペイ";
  const modalTitle = isContract
    ? (isNew ? "受託案件を追加" : "受託案件を編集")
    : (isNew ? "ツールを追加" : "ツールを編集");

  return `
    <div class="cl-mini-modal-overlay open" data-dev-modal-close="1">
      <div class="cl-mini-modal dev-modal" role="dialog" aria-modal="true" aria-labelledby="dev-modal-title">
        <div class="cl-mini-modal-head">
          <h4 id="dev-modal-title">${modalTitle}</h4>
          <button class="cl-modal-close" type="button" data-dev-modal-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <label class="cl-form-group">
            <span class="cl-form-label">${nameLabel} <span class="cl-form-required">*</span></span>
            <input id="dev-f-name" class="cl-form-input" value="${escapeHtmlAttr(draft.name)}" placeholder="${escapeHtmlAttr(namePlaceholder)}" />
          </label>
          ${assigneeStatusSection}
          <label class="cl-form-group">
            <span class="cl-form-label">リリース（予定）日</span>
            <input id="dev-f-release" type="date" class="cl-form-input" data-show-picker="1" value="${escapeHtmlAttr(draft.releaseDate || "")}" />
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">ToDo管理ボード</span>
            <select id="dev-f-todo" class="cl-form-input">${todoSelectInner}</select>
          </label>
          ${kindFields}
        </div>
        <div class="cl-mini-modal-foot dev-modal-foot">
          ${deleteBtn}
          <div class="dev-modal-foot-right">
            <button type="button" class="cl-btn cl-btn-outline" data-dev-modal-close="1">キャンセル</button>
            <button type="button" class="cl-btn cl-btn-primary" data-dev-save="1" ${saving ? "disabled" : ""}>${saving ? "保存中…" : "保存"}</button>
          </div>
        </div>
      </div>
    </div>`;
}

/** 資料ダウンロードのモーダル（編集ドロワーと区別できるよう中央に開く・閲覧のみ） */
function developmentDocModalHtml() {
  const st = uiState.developmentDocModal;
  if (!st) return "";
  const d = (Array.isArray(uiState.developments) ? uiState.developments : []).find(
    (x) => String(x.id) === String(st.id)
  );
  if (!d) return "";
  // アップロードされたファイルは認証ヘッダが要るので <a href> ではなくボタンで取得する。
  const groupHtml = (title, docs, files) => {
    const fileItems = files.map(
      (f) =>
        `<li><button type="button" class="dev-doc-item" data-dev-doc-file="${escapeHtmlAttr(String(f.id))}">` +
        `<span class="dev-doc-item-name">${escapeHtml(f.filename)}</span>` +
        `<span class="dev-doc-item-url">ファイル・${escapeHtml(developmentFileSizeLabel(f.sizeBytes))}</span></button></li>`
    );
    const urlItems = docs.map(
      (doc, i) =>
        `<li><a class="dev-doc-item" href="${escapeHtmlAttr(developmentSafeUrl(doc.url))}" target="_blank" rel="noopener" download>` +
        `<span class="dev-doc-item-name">${escapeHtml(doc.name || `${title}${i + 1}`)}</span>` +
        `<span class="dev-doc-item-url">${escapeHtml(doc.url)}</span></a></li>`
    );
    const items = fileItems.concat(urlItems);
    const inner = items.length ? items.join("") : `<li class="dev-doc-item-empty">登録なし</li>`;
    return `<div class="dev-doc-group"><div class="dev-doc-group-title">${escapeHtml(title)}</div><ul class="dev-doc-list">${inner}</ul></div>`;
  };
  return `
    <div class="dev-doc-overlay open" data-dev-doc-modal-close="1">
      <div class="dev-doc-modal" role="dialog" aria-modal="true" aria-labelledby="dev-doc-modal-title">
        <div class="cl-mini-modal-head">
          <h4 id="dev-doc-modal-title">資料をダウンロード</h4>
          <button class="cl-modal-close" type="button" data-dev-doc-modal-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="dev-doc-modal-body">
          <div class="dev-doc-modal-tool">${escapeHtml(d.name)}</div>
          ${groupHtml("提案資料", developmentDocsOf(d, "proposalDocs"), developmentFilesOf(d, "proposal"))}
          ${groupHtml("マニュアル", developmentDocsOf(d, "manualDocs"), developmentFilesOf(d, "manual"))}
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-dev-doc-modal-close="1">閉じる</button>
        </div>
      </div>
    </div>`;
}

function developmentPage() {
  if (!uiState.developmentsLoaded) void fetchDevelopments();
  if (!uiState.developmentApplicationsLoaded) fetchOnceInFlight("developmentApplications", fetchDevelopmentApplications);
  const canEdit = canEditDevelopment();
  const view = developmentCurrentView();
  const all = Array.isArray(uiState.developments) ? uiState.developments : [];
  const list = all.filter((d) => developmentKindOf(d) === view);
  const isContract = view === "contract";

  let summary, theadHtml, colgroupHtml, rowFn, addLabel, title, crossLink, emptyMsg;
  if (isContract) {
    const sumOrder = list.reduce((a, d) => a + (Number(d.orderAmount) || 0), 0);
    const sumRunning = list.reduce((a, d) => a + (Number(d.runningAmount) || 0), 0);
    summary = `
      <div class="dev-summary">
        <div class="dev-metric"><div class="dev-metric-label">受託案件</div><div class="dev-metric-value">${list.length}</div></div>
        <div class="dev-metric"><div class="dev-metric-label">初期費用 合計</div><div class="dev-metric-value">¥${formatYenPlain(sumOrder)}</div></div>
        <div class="dev-metric"><div class="dev-metric-label">ランニング 合計</div><div class="dev-metric-value">¥${formatYenPlain(sumRunning)}</div></div>
      </div>`;
    theadHtml = `
      <th class="dev-th-name">案件名</th>
      <th>状況</th>
      <th>リリース</th>
      <th>営業担当</th>
      <th>開発担当</th>
      <th>ジャンル</th>
      <th>クライアント</th>
      <th>初期費用</th>
      <th>ランニング</th>
      <th>ToDo</th>`;
    colgroupHtml = `
            <col style="width:22%">
            <col style="width:7%">
            <col style="width:9%">
            <col style="width:9%">
            <col style="width:7%">
            <col style="width:9%">
            <col style="width:6%">
            <col style="width:14%">
            <col style="width:9%">
            <col style="width:8%">`;
    rowFn = developmentContractRowHtml;
    addLabel = "受託案件を追加";
    title = "受託開発";
    crossLink = `<button type="button" class="dev-cross-link" data-dev-view="own">自社ツールの状況に戻る</button>`;
    emptyMsg = `登録された受託開発はまだありません。${canEdit ? "右上の「受託案件を追加」から登録できます。" : ""}`;
  } else {
    const totalCount = list.length;
    const sellingCount = list.filter((d) => d.status === "販売中").length;
    summary = `
      <div class="dev-summary">
        <div class="dev-metric"><div class="dev-metric-label">登録ツール</div><div class="dev-metric-value">${totalCount}</div></div>
        <div class="dev-metric"><div class="dev-metric-label">販売中</div><div class="dev-metric-value">${sellingCount}</div></div>
      </div>`;
    theadHtml = `
      <th class="dev-th-name">ツール名</th>
      <th>状況</th>
      <th>担当</th>
      <th>リリース</th>
      <th>資料</th>
      <th>ToDo</th>
      <th>デモURL</th>
      <th>デモID</th>
      <th>デモPW</th>`;
    colgroupHtml = `
            <col style="width:15%">
            <col style="width:8%">
            <col style="width:8%">
            <col style="width:9%">
            <col style="width:18%">
            <col style="width:10%">
            <col style="width:12%">
            <col style="width:11%">
            <col style="width:9%">`;
    rowFn = developmentRowHtml;
    addLabel = "ツールを追加";
    title = "自社ツール";
    crossLink = `<button type="button" class="dev-cross-link" data-dev-view="contract">その他受託開発中の状況を見る</button>`;
    emptyMsg = `登録された開発ツールはまだありません。${canEdit ? "右上の「ツールを追加」から登録できます。" : ""}`;
  }

  let body;
  if (!uiState.developmentsLoaded) {
    body = `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "開発データを読み込んでいます" })}</div>`;
  } else if (uiState.developmentsLoadError) {
    body = `<div class="cl-empty" style="padding:18px 20px;">開発データを取得できませんでした。ページを再読み込みしてください。</div>`;
  } else if (!list.length) {
    body = `<div class="cl-empty" style="padding:18px 20px;">${emptyMsg}</div>`;
  } else {
    body = `
      <div class="dev-table-wrap">
        <table class="pj-table dev-table" data-fp-table="development">
          <colgroup>${colgroupHtml}
          </colgroup>
          <thead>
            <tr>${theadHtml}</tr>
          </thead>
          <tbody>${list.map((d) => rowFn(d, canEdit)).join("")}</tbody>
        </table>
      </div>`;
  }

  const addBtn = canEdit
    ? `<button class="cl-btn cl-btn-primary" type="button" data-dev-add="1">${addLabel}</button>`
    : "";
  // 自社ツールの事前申請は全員が可能（受託開発はクライアント契約主導のため対象外）。
  const applyBtn = !isContract
    ? `<button class="cl-btn cl-btn-outline" type="button" data-dev-app-open="1">＋ 事前申請する</button>`
    : "";

  // 申請中のデータは申請者本人以外に見せない。マスターのみ全員分（自分にきていない分も）を見られる。
  const isMasterViewer = isMasterRoleUser(currentUser);
  const myApps = !isContract
    ? (Array.isArray(uiState.developmentApplications) ? uiState.developmentApplications : []).filter(
        (a) => a.status !== "承認" && (isMasterViewer || a.isMine)
      )
    : [];
  const myAppsBlock = myApps.length
    ? `
      <section class="at-section">
        <div class="at-section-head"><h3>${isMasterViewer ? "事前申請の状況（全員）" : "自分の事前申請"}</h3><span class="at-count">${myApps.length}件</span></div>
        <div class="at-app-grid">${myApps.map((a) => devApplicationCardHtml(a)).join("")}</div>
      </section>`
    : "";

  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <div class="dev-title-row">
              <h2>${title}</h2>
              ${crossLink}
            </div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            ${applyBtn}
            ${addBtn}
          </div>
        </div>
        <div class="dev-content">
          ${myAppsBlock}
          ${summary}
          ${body}
        </div>
      </div>
    </div>
    ${developmentEditorModalHtml()}
    ${devApplyModalHtml()}
    ${developmentDocModalHtml()}`;
}

// ── イベント委譲（開発ページ限定） ───────────────────────────────
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "development") return;

  const viewEl = target.closest("[data-dev-view]");
  if (viewEl instanceof HTMLElement) {
    uiState.developmentView = viewEl.dataset.devView === "contract" ? "contract" : "own";
    render();
    return;
  }
  const addEl = target.closest("[data-dev-add]");
  if (addEl instanceof HTMLElement) {
    openDevelopmentEditor(null);
    return;
  }
  if (target.closest("[data-dev-app-open]")) {
    openDevApplyModal();
    return;
  }
  if (target.closest("[data-dev-app-submit]")) {
    void submitDevApplication();
    return;
  }
  const devAppWithdrawEl = target.closest("[data-dev-app-withdraw]");
  if (devAppWithdrawEl instanceof HTMLElement) {
    void withdrawDevApplication(devAppWithdrawEl.dataset.devAppWithdraw);
    return;
  }
  const devAppDeleteEl = target.closest("[data-dev-app-delete]");
  if (devAppDeleteEl instanceof HTMLElement) {
    void deleteRejectedDevApplication(devAppDeleteEl.dataset.devAppDelete);
    return;
  }
  // 背景（オーバーレイ）クリックでは閉じない。誤操作防止のため✕/キャンセルのみで閉じる。
  if (target.closest("[data-dev-app-modal-close]")) {
    closeDevApplyModal();
    return;
  }
  // 資料モーダル（中央）：一覧の「資料」ボタンから開く。閲覧のみなので権限ゲートなし。
  const docsOpenEl = target.closest("[data-dev-docs]");
  if (docsOpenEl instanceof HTMLElement) {
    uiState.developmentDocModal = { id: docsOpenEl.dataset.devDocs || "" };
    render();
    return;
  }
  // ファイルのダウンロードは閉じる判定より先に見る（オーバーレイが
  // data-dev-doc-modal-close を持つため、後ろに置くと early-return に食われる）。
  const docFileEl = target.closest("[data-dev-doc-file]");
  if (docFileEl instanceof HTMLElement) {
    void downloadDevelopmentDocFile(docFileEl.dataset.devDocFile);
    return;
  }
  const docsCloseEl = target.closest("[data-dev-doc-modal-close]");
  if (docsCloseEl instanceof HTMLElement) {
    // 背景クリックで閉じる。ただしモーダル内のクリックは無視する。
    if (docsCloseEl.classList.contains("dev-doc-overlay") && target.closest(".dev-doc-modal")) return;
    uiState.developmentDocModal = null;
    render();
    return;
  }
  // 資料リストの行追加/削除は再描画せず DOM を直接いじる（他の未保存入力を消さないため）。
  const docAddEl = target.closest("[data-dev-doc-add]");
  if (docAddEl instanceof HTMLElement) {
    const kind = docAddEl.dataset.devDocAdd === "manual" ? "manual" : "proposal";
    const wrap = document.getElementById(`dev-f-docs-${kind}`);
    if (wrap) {
      wrap.insertAdjacentHTML("beforeend", developmentDocEditRowHtml(null));
      const added = wrap.lastElementChild;
      if (added instanceof HTMLElement) added.querySelector(".dev-doc-edit-name")?.focus();
    }
    return;
  }
  const docRemoveEl = target.closest("[data-dev-doc-remove]");
  if (docRemoveEl instanceof HTMLElement) {
    docRemoveEl.closest(".dev-doc-edit-row")?.remove();
    return;
  }
  // 保存済みファイルの✕は DOM から外すだけ。実際の削除は保存時に差分で行う。
  const fileRemoveEl = target.closest("[data-dev-file-remove]");
  if (fileRemoveEl instanceof HTMLElement) {
    fileRemoveEl.closest(".dev-doc-file-row")?.remove();
    return;
  }
  const editEl = target.closest("[data-dev-edit]");
  if (editEl instanceof HTMLElement) {
    openDevelopmentEditor(editEl.dataset.devEdit);
    return;
  }
  const delEl = target.closest("[data-dev-delete]");
  if (delEl instanceof HTMLElement) {
    void deleteDevelopment(delEl.dataset.devDelete);
    return;
  }
  const saveEl = target.closest("[data-dev-save]");
  if (saveEl instanceof HTMLElement) {
    void saveDevelopment();
    return;
  }
  const passToggleEl = target.closest("[data-dev-pass-toggle]");
  if (passToggleEl instanceof HTMLElement) {
    const id = String(passToggleEl.dataset.devPassToggle || "");
    uiState.developmentRevealPass[id] = !uiState.developmentRevealPass[id];
    render();
    return;
  }
  const formPassToggleEl = target.closest("[data-dev-form-pw-toggle]");
  if (formPassToggleEl instanceof HTMLElement) {
    const input = document.getElementById("dev-f-demo-pass");
    if (input instanceof HTMLInputElement) {
      const reveal = input.type === "password";
      input.type = reveal ? "text" : "password";
      formPassToggleEl.textContent = reveal ? "🙈" : "👁";
      formPassToggleEl.title = reveal ? "隠す" : "表示";
      formPassToggleEl.setAttribute("aria-label", reveal ? "パスワードを隠す" : "パスワードを表示");
    }
    return;
  }
  const copyEl = target.closest("[data-dev-copy]");
  if (copyEl instanceof HTMLElement) {
    const val = copyEl.dataset.devCopy || "";
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(val).then(
        () => showToastDirect("コピーしました"),
        () => showToastDirect("コピーできませんでした")
      );
    }
    return;
  }
  const closeEl = target.closest("[data-dev-modal-close]");
  if (closeEl instanceof HTMLElement) {
    const closeByOverlay = closeEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return;
    closeDevelopmentEditor();
    return;
  }
});

// 資料ファイルを選んだ直後に、対象外の形式・サイズ超過をその場で知らせる。
// 再描画すると同じモーダルの未保存入力が消えるため DOM を直接書き換える。
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (currentRoute().page !== "development") return;
  const kind = t.dataset.devFileInput;
  if (!kind) return;
  const list = document.getElementById(`dev-f-file-pending-${kind}`);
  if (!list) return;
  const files = Array.from(t.files || []);
  const bad = files.filter((f) => !developmentDocFileExtOk(f.name) || f.size > DEV_DOC_FILE_MAX_BYTES);
  if (bad.length) {
    // 1件でも不正なら選択ごと取り消す（file input は個別に外せないため）
    t.value = "";
    list.innerHTML = "";
    const f = bad[0];
    showToastError(
      !developmentDocFileExtOk(f.name)
        ? `「${f.name}」は対応形式ではありません（${DEV_DOC_FILE_EXT_HINT}）`
        : `「${f.name}」は15MBを超えています`
    );
    return;
  }
  list.innerHTML = files
    .map(
      (f) =>
        `<div class="dev-doc-file-row dev-doc-file-row--pending"><span class="dev-doc-file-name">${escapeHtml(f.name)}</span>` +
        `<span class="dev-doc-file-size">${escapeHtml(developmentFileSizeLabel(f.size))}</span>` +
        `<span class="dev-doc-file-new">保存時に追加</span></div>`
    )
    .join("");
});

// ── イベント委譲（申請承認ページ：自社ツール開発の事前申請の承認/却下） ─────
// 16-render-routing.js の巨大クリックハンドラに分岐を足さず、この機能ファイルで完結させる。
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "application-management") return;

  const approveEl = target.closest("[data-app-mgmt-dev-approve]");
  if (approveEl instanceof HTMLElement) {
    const id = approveEl.dataset.appMgmtDevApprove || "";
    if (id && !uiState.devAppApprovalSaving) void approveDevApplication(id);
    return;
  }
  const rejectEl = target.closest("[data-app-mgmt-dev-reject]");
  if (rejectEl instanceof HTMLElement) {
    const id = rejectEl.dataset.appMgmtDevReject || "";
    if (id) {
      uiState.devAppRejectDraft = { id };
      render();
    }
    return;
  }
  if (target.closest("[data-dev-app-reject-submit]")) {
    void rejectDevApplication();
    return;
  }
  // 背景（オーバーレイ）クリックでは閉じない。誤操作防止のため✕/キャンセルのみで閉じる。
  if (target.closest("[data-dev-app-reject-close]")) {
    uiState.devAppRejectDraft = null;
    uiState.devAppApprovalSaving = false;
    render();
    return;
  }
});
// ────────────────────────────────────────────────────────────────
// 許可ツールページ：会社が利用を許可しているツール・サービスの一覧（読み取り専用）と、
// その「許可申請（登録/編集/削除）」。基本は申請式で、承認は既存の承認フロー
// （承認設定の「ツールの許可申請」binding）に従い、申請承認ページで承認される。
// 承認されると一覧（fp_allowed_tools）に反映される。マスターも含め直接の追加・削除はできない
// （2026-10-03 廃止。すべて申請フローに載せる）。
// データは API /api/allowed-tools（一覧・読み取りのみ）と
// /api/tool-applications（申請）。
// ────────────────────────────────────────────────────────────────

// ALLOWED_TOOL_CATEGORIES / ALLOWED_TOOL_CATEGORY_ALIASES / ALLOWED_TOOL_SUBCATEGORIES は
// app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。
// 申請の種類。サーバ api/tool-applications.js の KIND_OPTIONS と一致。
const TOOL_APP_KIND_LABEL = { register: "登録", edit: "編集", delete: "削除" };

/** 区分→CSS用キー（チップ色分け） */
const ALLOWED_TOOL_CATEGORY_KEY = { AI: "ai", 開発ツール: "dev", 業務ツール: "biz", その他: "other" };
/** 申請状況→CSS用キー（バッジ色分け） */
const TOOL_APP_STATUS_KEY = { 申請中: "pending", 承認: "approved", 却下: "rejected" };

/** 未知・旧区分を正規化（許可リスト一致 or 旧名エイリアスのみ採用、それ以外は既定 AI）。 */
function normalizeAllowedToolCategory(category) {
  const s = String(category == null ? "" : category).trim();
  if (ALLOWED_TOOL_CATEGORIES.includes(s)) return s;
  if (ALLOWED_TOOL_CATEGORY_ALIASES[s]) return ALLOWED_TOOL_CATEGORY_ALIASES[s];
  return "AI";
}

/** 大区分に応じた小区分の許可値（列挙がなければ空配列）。 */
function allowedToolSubcategoryOptions(category) {
  return ALLOWED_TOOL_SUBCATEGORIES[normalizeAllowedToolCategory(category)] || [];
}

/** 小区分を正規化（大区分の許可値に一致すればそのまま、なければ空）。 */
function normalizeAllowedToolSubcategory(category, subcategory) {
  const opts = allowedToolSubcategoryOptions(category);
  const s = String(subcategory == null ? "" : subcategory).trim();
  return opts.includes(s) ? s : "";
}

function allowedToolCategoryChip(category, subcategory) {
  const c = normalizeAllowedToolCategory(category);
  const key = ALLOWED_TOOL_CATEGORY_KEY[c] || "ai";
  const sub = normalizeAllowedToolSubcategory(c, subcategory);
  const subChip = sub ? ` <span class="at-cat-sub">${escapeHtml(sub)}</span>` : "";
  return `<span class="at-cat-chip at-cat--${key}">${escapeHtml(c)}</span>${subChip}`;
}

function toolAppStatusBadge(status) {
  const s = APPLICATION_STATUS_OPTIONS.includes(status) ? status : "申請中";
  const key = TOOL_APP_STATUS_KEY[s] || "pending";
  return `<span class="at-status-badge at-status--${key}">${escapeHtml(s)}</span>`;
}

/** 承認進捗の文言（申請中のみ意味を持つ）。承認者未設定なら注意表示。 */
function toolAppProgressText(a) {
  if (a.status !== "申請中") return "";
  if (!a.chainLength) return "承認者未設定（承認設定をご確認ください）";
  return `承認 ${a.approvedCount}/${a.chainLength}`;
}

/** ISO 文字列（または空）を YYYY-MM-DD 表示にする。 */
function allowedToolDate(iso) {
  const s = String(iso || "");
  return s.length >= 10 ? s.slice(0, 10) : "";
}

/** ツールURLの表示。http/https のみリンク化（ホスト名表示）、それ以外はテキストのまま。 */
function allowedToolUrlLink(url) {
  const u = String(url || "").trim();
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) return escapeHtml(u);
  let label = u;
  try {
    label = new URL(u).hostname;
  } catch (e) {}
  return `<a href="${escapeHtmlAttr(u)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;
}

// ── 申請の添付ファイル（スキルファイル等の ZIP も可） ─────────────
// サーバ api/tool-application-attachment.js の EXT_TO_MIME / MAX_BYTES / CHUNK_MAX_* と対応。
// 変更時は両側を同時に見直すこと（3MB 分割転送は課題添付と同じ方式）。
const TOOL_APP_ATT_MAX_FILES = 5;
const TOOL_APP_ATT_MAX_BYTES = 15 * 1024 * 1024;
const TOOL_APP_ATT_CHUNK_BYTES = 3 * 1024 * 1024;
const TOOL_APP_ATT_EXTS = [".zip", ".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".csv", ".txt", ".md", ".xls", ".xlsx", ".ppt", ".pptx"];
const TOOL_APP_ATT_EXT_HINT = "ZIP・PDF・画像（PNG/JPEG/GIF/WebP）・CSV・Excel（.xls/.xlsx）・PowerPoint（.ppt/.pptx）・テキスト（.txt/.md）";

function toolAppAttachmentExtOk(fileName) {
  const m = String(fileName || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return !!m && TOOL_APP_ATT_EXTS.includes(`.${m[1]}`);
}

async function toolAppAttApiPost(payload) {
  const r = await apiFetch("/api/tool-application-attachment", { method: "POST", body: payload });
  if (!r.ok) throw new Error(typeof r.data.error === "string" ? r.data.error : "添付の保存に失敗しました");
  return r.data;
}

/** 申請作成後に添付をアップロードする（3MB 超は分割転送し finalize で合体）。 */
async function uploadToolApplicationAttachment(applicationId, file) {
  if (!authToken) throw new Error("ログインが必要です");
  if (!toolAppAttachmentExtOk(file.name)) throw new Error(`対応形式のみ: ${TOOL_APP_ATT_EXT_HINT}`);
  if (file.size > TOOL_APP_ATT_MAX_BYTES) throw new Error("ファイルサイズは15MB以内にしてください");
  const buf = await readLocalFileArrayBuffer(file);
  const bytes = new Uint8Array(buf);
  if (bytes.length <= TOOL_APP_ATT_CHUNK_BYTES) {
    await toolAppAttApiPost({ applicationId, filename: file.name, fileBase64: arrayBufferToBase64(buf) });
    return;
  }
  const uploadId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const totalChunks = Math.ceil(bytes.length / TOOL_APP_ATT_CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const part = bytes.subarray(i * TOOL_APP_ATT_CHUNK_BYTES, (i + 1) * TOOL_APP_ATT_CHUNK_BYTES);
    await toolAppAttApiPost({ uploadId, chunkIndex: i, fileBase64: arrayBufferToBase64(part) });
  }
  await toolAppAttApiPost({ uploadId, finalize: true, totalChunks, applicationId, filename: file.name });
}

/** 添付をダウンロードする（4.5MB レスポンス上限を越えるファイルは分割取得して結合）。 */
async function downloadToolApplicationAttachment(attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) return;
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  try {
    const headers = { Authorization: `Bearer ${authToken}` };
    const fetchOk = async (extraQuery) => {
      const res = await fetch(`/api/tool-application-attachment?id=${encodeURIComponent(String(id))}${extraQuery}`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.error === "string" ? j.error : "ファイルの取得に失敗しました");
      }
      return res;
    };
    const meta = await (await fetchOk("&meta=1")).json().catch(() => ({}));
    const size = Number(meta.sizeBytes) || 0;
    const filename = String(meta.filename || "download");
    const type = String(meta.mimeType || "") || "application/octet-stream";
    let blob;
    if (size <= TOOL_APP_ATT_CHUNK_BYTES) {
      blob = await (await fetchOk("")).blob();
    } else {
      const parts = [];
      for (let off = 0; off < size; off += TOOL_APP_ATT_CHUNK_BYTES) {
        parts.push(await (await fetchOk(`&offset=${off}&length=${TOOL_APP_ATT_CHUNK_BYTES}`)).arrayBuffer());
      }
      blob = new Blob(parts, { type });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    showToastError(err instanceof Error && err.message ? err.message : "ダウンロードに失敗しました");
  }
}

/** 申請の添付リンク列（自分の申請カード・申請承認ページ共用）。 */
function toolAppAttachmentLinksHtml(a) {
  const list = Array.isArray(a && a.attachments) ? a.attachments : [];
  if (!list.length) return "";
  return `<div class="at-app-atts">${list
    .map(
      (f) =>
        `<button type="button" class="at-app-att-link" data-tool-app-att-dl="${escapeHtmlAttr(String(f.id))}"><span class="at-app-att-ic" aria-hidden="true">${pmBoardIconSvg("attach", 13)}</span>${escapeHtml(f.filename)} <span class="pj-muted">(${formatProgressAttachmentSize(f.sizeBytes)})</span></button>`
    )
    .join("")}</div>`;
}

async function fetchAllowedToolsData() {
  if (!authToken) return;
  try {
    const [toolsRes, appsRes] = await Promise.all([
      fetch("/api/allowed-tools", { headers: { Authorization: `Bearer ${authToken}` } }),
      fetch("/api/tool-applications", { headers: { Authorization: `Bearer ${authToken}` } })
    ]);
    if (!toolsRes.ok || !appsRes.ok) throw new Error(`${toolsRes.status}/${appsRes.status}`);
    const tj = await toolsRes.json();
    const aj = await appsRes.json();
    uiState.allowedTools = Array.isArray(tj.tools) ? tj.tools : [];
    uiState.allowedToolApplications = Array.isArray(aj.applications) ? aj.applications : [];
    uiState.allowedToolsLoaded = true;
    uiState.allowedToolsLoadError = false;
  } catch (e) {
    uiState.allowedToolsLoaded = true;
    uiState.allowedToolsLoadError = true;
  }
  render();
}

// ── 申請モーダル（登録/編集/削除・全ユーザー） ─────────────────────
function openToolApplyModal(kind, toolId) {
  const k = kind === "edit" || kind === "delete" ? kind : "register";
  let draft = { kind: k, targetToolId: "", title: "", category: "AI", subcategory: "本体", vendor: "", url: "", usageTerms: "", reason: "", agree: false, files: [] };
  if ((k === "edit" || k === "delete") && toolId != null) {
    const t = (uiState.allowedTools || []).find((x) => String(x.id) === String(toolId));
    if (t) {
      const cat = normalizeAllowedToolCategory(t.category);
      draft = {
        kind: k,
        targetToolId: String(t.id),
        title: t.name || "",
        category: cat,
        subcategory: normalizeAllowedToolSubcategory(cat, t.subcategory) || (allowedToolSubcategoryOptions(cat)[0] || ""),
        vendor: t.vendor || "",
        url: t.url || "",
        usageTerms: t.usageTerms || "",
        // 編集では登録時の申請理由・用途を初期値にする（削除では削除理由の入力欄なので空のまま）
        reason: k === "edit" ? t.reason || "" : "",
        agree: false,
        files: []
      };
    }
  }
  uiState.allowedToolApplyDraft = draft;
  render();
}
function closeToolApplyModal() {
  uiState.allowedToolApplyDraft = null;
  uiState.allowedToolApplySaving = false;
  render();
}

function collectToolApplyForm() {
  const byId = (id) => document.getElementById(id);
  const draft = uiState.allowedToolApplyDraft || {};
  const category = normalizeAllowedToolCategory(byId("at-apply-category")?.value || draft.category);
  return {
    kind: draft.kind || "register",
    targetToolId: draft.targetToolId || "",
    title: (byId("at-apply-title")?.value || "").trim(),
    category,
    subcategory: normalizeAllowedToolSubcategory(category, byId("at-apply-subcategory")?.value || draft.subcategory),
    vendor: (byId("at-apply-vendor")?.value || "").trim(),
    url: (byId("at-apply-url")?.value || "").trim(),
    usageTerms: (byId("at-apply-terms")?.value || "").trim(),
    reason: (byId("at-apply-reason")?.value || "").trim(),
    agree: !!byId("at-apply-agree")?.checked
  };
}

async function submitToolApplication() {
  if (uiState.allowedToolApplySaving) return;
  const v = collectToolApplyForm();
  // 入力値を draft に保持（バリデーション失敗で再描画されても消えないように）。
  const d = uiState.allowedToolApplyDraft;
  if (d) {
    d.title = v.title;
    d.vendor = v.vendor;
    d.url = v.url;
    d.category = v.category;
    d.subcategory = v.subcategory;
    d.usageTerms = v.usageTerms;
    d.reason = v.reason;
    d.agree = v.agree;
  }
  if (v.kind !== "delete") {
    if (!v.title) {
      showToastError("ツール名・件名を入力してください");
      return;
    }
    if (!v.vendor) {
      showToastError("開発会社を入力してください");
      return;
    }
    if (!v.reason) {
      showToastError("申請理由・用途を入力してください");
      return;
    }
    if (!v.agree) {
      showToastError("「情報セキュリティ管理規定を確認しました」にチェックしてください");
      return;
    }
  }
  uiState.allowedToolApplySaving = true;
  try {
    const { ok, data: j } = await apiFetch("/api/tool-applications", { method: "POST", body: v });
    if (!ok) {
      uiState.allowedToolApplySaving = false;
      showToastError(typeof j.error === "string" ? j.error : "申請に失敗しました");
      return;
    }
    // 申請本体の作成後に添付をアップロード（3MB 超は分割転送）。
    const files = d && Array.isArray(d.files) ? d.files : [];
    const failed = [];
    if (j.application && files.length) {
      showToastPersistent("添付ファイルをアップロードしています…");
      for (const f of files) {
        try {
          await uploadToolApplicationAttachment(j.application.id, f);
        } catch (err) {
          failed.push(`${f.name}（${err instanceof Error && err.message ? err.message : "失敗"}）`);
        }
      }
    }
    uiState.allowedToolApplyDraft = null;
    uiState.allowedToolApplySaving = false;
    if (j.application) uiState.allowedToolApplications.unshift(j.application);
    if (failed.length) showToastError(`申請しましたが、添付のアップロードに失敗しました: ${failed.join(" / ")}`);
    else showToast("申請しました");
    // 添付メタ（ID・サイズ）はサーバ採番のため、アップロードした分がある時だけ再取得して反映する。
    if (files.length > failed.length) void fetchAllowedToolsData();
  } catch (e) {
    uiState.allowedToolApplySaving = false;
    showToastError("通信に失敗しました");
  }
}

async function withdrawToolApplication(id) {
  const target = uiState.allowedToolApplications.find((a) => String(a.id) === String(id));
  if (!target) return;
  if (!window.confirm(`「${target.title}」の申請を取り下げます。よろしいですか？`)) return;
  try {
    const { ok, data: j } = await apiFetch("/api/tool-applications", { method: "DELETE", body: { id } });
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "取り下げに失敗しました");
      return;
    }
    uiState.allowedToolApplications = uiState.allowedToolApplications.filter((a) => String(a.id) !== String(id));
    showToast("取り下げました");
  } catch (e) {
    showToastError("通信に失敗しました");
  }
}

// ── 申請承認ページ：承認/却下（承認者本人のみ・サーバで再検証） ──
async function approveToolApplication(id) {
  if (uiState.toolApprovalSaving) return;
  if (!window.confirm("この申請を承認しますか？")) return;
  uiState.toolApprovalSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/tool-applications", { method: "PUT", body: { id, action: "approve" } });
    uiState.toolApprovalSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "承認に失敗しました");
      render();
      return;
    }
    showToast(j.application && j.application.status === "承認" ? "承認しました（一覧に反映）" : "承認しました");
    await fetchAllowedToolsData(); // 一覧・申請を再取得（承認完了なら新規登録も反映）
  } catch (e) {
    uiState.toolApprovalSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

async function rejectToolApplication() {
  if (uiState.toolApprovalSaving) return;
  const draft = uiState.toolRejectDraft;
  if (!draft) return;
  const comment = (document.getElementById("tool-reject-comment")?.value || "").trim();
  if (!comment) {
    showToastError("却下理由を入力してください");
    return;
  }
  uiState.toolApprovalSaving = true;
  render();
  try {
    const { ok, data: j } = await apiFetch("/api/tool-applications", { method: "PUT", body: { id: draft.id, action: "reject", reviewComment: comment } });
    uiState.toolApprovalSaving = false;
    if (!ok) {
      showToastError(typeof j.error === "string" ? j.error : "却下に失敗しました");
      render();
      return;
    }
    uiState.toolRejectDraft = null;
    showToast("却下しました");
    await fetchAllowedToolsData();
  } catch (e) {
    uiState.toolApprovalSaving = false;
    showToastError("通信に失敗しました");
    render();
  }
}

// ── 自分の申請カード ──────────────────────────────────────────
function toolApplicationCardHtml(a) {
  const kindLabel = TOOL_APP_KIND_LABEL[a.kind] || "登録";
  const reviewerLine =
    a.status === "却下" && a.reviewComment
      ? `<div class="at-app-reject">${escapeHtml(a.reviewComment)}</div>`
      : "";
  const progress = toolAppProgressText(a);
  const vendorLine = a.vendor ? `<span class="at-app-meta-item">開発会社：${escapeHtml(a.vendor)}</span>` : "";
  const urlLine = a.url ? `<span class="at-app-meta-item">URL：${allowedToolUrlLink(a.url)}</span>` : "";
  const dateLine = allowedToolDate(a.createdAt);
  const withdraw =
    a.status === "申請中"
      ? `<div class="at-app-actions"><button type="button" class="cl-btn cl-btn-outline" data-at-withdraw="${escapeHtmlAttr(String(a.id))}">取り下げ</button></div>`
      : "";
  return `
    <div class="at-app-card">
      <div class="at-app-card-head">
        <div class="at-app-title"><span class="at-app-type">ツール</span>${escapeHtml(a.title)} <span class="at-app-kind">（${escapeHtml(kindLabel)}）</span></div>
        ${toolAppStatusBadge(a.status)}
      </div>
      <div class="at-app-meta">
        <span class="at-app-cat">${escapeHtml(a.category)}${a.subcategory ? ` / ${escapeHtml(a.subcategory)}` : ""}</span>
        ${vendorLine}
        ${urlLine}
        ${progress ? `<span class="at-app-meta-item">${escapeHtml(progress)}</span>` : ""}
        ${dateLine ? `<span class="at-app-meta-item">${escapeHtml(dateLine)}</span>` : ""}
      </div>
      ${toolAppAttachmentLinksHtml(a)}
      ${reviewerLine}
      ${withdraw}
    </div>`;
}

// ── マスタ一覧の行（読み取り専用＋編集/削除を申請） ───────────────
function allowedToolRowHtml(t) {
  const vendorCell = t.vendor
    ? `<span class="at-dept-cell">${escapeHtml(t.vendor)}</span>`
    : `<span class="dev-muted">—</span>`;
  const urlCell = t.url ? allowedToolUrlLink(t.url) : `<span class="dev-muted">—</span>`;
  const termsCell = t.usageTerms ? escapeHtml(t.usageTerms) : `<span class="dev-muted">—</span>`;
  // 削除もマスターを含め全員「申請→承認」（2026-10-03 直接追加・直接削除は廃止）
  const deleteAction = `<button type="button" class="chip-btn ghost" data-at-apply-delete="${escapeHtmlAttr(String(t.id))}">削除を申請</button>`;
  return `
    <tr>
      <td class="pj-td-name">${escapeHtml(t.name)}${toolAppAttachmentLinksHtml(t)}</td>
      <td>${allowedToolCategoryChip(t.category, t.subcategory)}</td>
      <td>${vendorCell}</td>
      <td>${urlCell}</td>
      <td class="at-terms-cell">${termsCell}</td>
      <td class="at-row-actions">
        <button type="button" class="chip-btn" data-at-apply-edit="${escapeHtmlAttr(String(t.id))}">編集を申請</button>
        ${deleteAction}
      </td>
    </tr>`;
}

// ── 申請モーダル ──────────────────────────────────────────────
function toolApplyModalHtml() {
  const draft = uiState.allowedToolApplyDraft;
  if (!draft) return "";
  const saving = uiState.allowedToolApplySaving;
  const isDelete = draft.kind === "delete";
  const titleMap = { register: "新しく申請する", edit: "ツールの編集を申請", delete: "ツールの削除を申請" };
  const submitMap = { register: "申請する", edit: "編集を申請", delete: "削除を申請" };
  const savingLabel = "申請中…";
  const catOpts = ALLOWED_TOOL_CATEGORIES.map(
    (c) => `<option value="${escapeHtmlAttr(c)}" ${c === draft.category ? "selected" : ""}>${escapeHtml(c)}</option>`
  ).join("");
  // 小区分は大区分に列挙がある場合（AI）のみ表示。大区分変更時は change ハンドラで再描画される。
  const subOptions = allowedToolSubcategoryOptions(draft.category);
  const subGroupHtml = subOptions.length
    ? `<label class="cl-form-group">
         <span class="cl-form-label">小区分</span>
         <select id="at-apply-subcategory" class="cl-form-input at-select">${subOptions
           .map((s) => `<option value="${escapeHtmlAttr(s)}" ${s === draft.subcategory ? "selected" : ""}>${escapeHtml(s)}</option>`)
           .join("")}</select>
       </label>`
    : "";

  const bodyHtml = isDelete
    ? `<p class="at-delete-note">「<strong>${escapeHtml(draft.title)}</strong>」を許可ツール一覧から削除する申請です。承認されると一覧から削除されます。</p>
       <label class="cl-form-group">
         <span class="cl-form-label">削除理由（任意）</span>
         <textarea id="at-apply-reason" class="cl-form-input" rows="3" placeholder="削除したい理由">${escapeHtml(draft.reason)}</textarea>
       </label>`
    : `<label class="cl-form-group">
         <span class="cl-form-label">ツール名・件名 <span class="cl-form-required">*</span></span>
         <input id="at-apply-title" class="cl-form-input" value="${escapeHtmlAttr(draft.title)}" placeholder="例：Cursor / HubSpot連携コネクター" />
       </label>
       <label class="cl-form-group">
         <span class="cl-form-label">大区分</span>
         <select id="at-apply-category" class="cl-form-input at-select" data-at-apply-category-select="1">${catOpts}</select>
       </label>
       ${subGroupHtml}
       <label class="cl-form-group">
         <span class="cl-form-label">開発会社 <span class="cl-form-required">*</span></span>
         <input id="at-apply-vendor" class="cl-form-input" value="${escapeHtmlAttr(draft.vendor)}" placeholder="例：Anthropic / HubSpot Japan" />
       </label>
       <label class="cl-form-group">
         <span class="cl-form-label">URL（任意）</span>
         <input id="at-apply-url" class="cl-form-input" type="url" value="${escapeHtmlAttr(draft.url)}" placeholder="例：https://claude.com" />
       </label>
       <label class="cl-form-group">
         <span class="cl-form-label">利用条件（任意）</span>
         <textarea id="at-apply-terms" class="cl-form-input" rows="2" placeholder="利用上の条件・規程など">${escapeHtml(draft.usageTerms)}</textarea>
       </label>
       <label class="cl-form-group">
         <span class="cl-form-label">申請理由・用途 <span class="cl-form-required">*</span></span>
         <textarea id="at-apply-reason" class="cl-form-input" rows="2" placeholder="何に使うか・導入したい背景など">${escapeHtml(draft.reason)}</textarea>
       </label>
       <label class="cl-form-group">
         <span class="cl-form-label">添付ファイル（任意・${TOOL_APP_ATT_MAX_FILES}件まで）</span>
         <label class="chip-btn at-apply-file-pick" style="cursor:pointer;display:inline-block;width:fit-content;">
           ファイルを選択
           <input id="at-apply-files" type="file" multiple data-at-apply-files="1" accept="${escapeHtmlAttr(TOOL_APP_ATT_EXTS.join(","))}" style="display:none;" />
         </label>
         <span class="at-att-hint">対応形式: ${escapeHtml(TOOL_APP_ATT_EXT_HINT)}（各最大15MB）。スキルファイル等は ZIP でまとめて添付できます。</span>
       </label>
       ${
         Array.isArray(draft.files) && draft.files.length
           ? `<div class="at-app-atts at-apply-file-list">${draft.files
               .map(
                 (f, i) =>
                   `<span class="at-app-att-item"><span class="at-app-att-ic" aria-hidden="true">${pmBoardIconSvg("attach", 13)}</span>${escapeHtml(f.name)} <span class="pj-muted">(${formatProgressAttachmentSize(f.size)})</span> <button type="button" class="cl-modal-close at-att-remove" data-at-apply-file-remove="${i}" aria-label="この添付を外す">✕</button></span>`
               )
               .join("")}</div>`
           : ""
       }
       <label class="cl-form-group at-agree">
         <input id="at-apply-agree" type="checkbox" ${draft.agree ? "checked" : ""} />
         <span>情報セキュリティ管理規定を確認しました <span class="cl-form-required">*</span></span>
       </label>`;

  return `
    <div class="cl-mini-modal-overlay open" data-at-modal-close="1">
      <div class="cl-mini-modal at-modal" role="dialog" aria-modal="true" aria-labelledby="at-apply-title-h">
        <div class="cl-mini-modal-head">
          <h4 id="at-apply-title-h">${titleMap[draft.kind] || "ツールの許可申請"}</h4>
          <button class="cl-modal-close" type="button" data-at-modal-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          ${draft.kind === "register" ? internalAppTypeSelectHtml("tool") : ""}
          <p class="at-apply-note">すべての変更は申請→承認で反映されます（承認は申請承認ページ）。</p>
          ${bodyHtml}
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-at-modal-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-at-apply-submit="1" ${saving ? "disabled" : ""}>${saving ? savingLabel : submitMap[draft.kind] || "申請する"}</button>
        </div>
      </div>
    </div>`;
}

// ── ページ本体 ────────────────────────────────────────────────
function allowedToolsPage() {
  if (!uiState.allowedToolsLoaded) fetchOnceInFlight("allowedTools", fetchAllowedToolsData);
  if (!uiState.equipmentApplicationsLoaded) fetchOnceInFlight("equipmentApplications", fetchEquipmentApplications);
  const apps = Array.isArray(uiState.allowedToolApplications) ? uiState.allowedToolApplications : [];
  // 自分の申請は種類をまたいで新しい順に1か所へ。
  // ツールの承認済みは一覧に反映済み、備品の貸与済みは「自分の貸与品」に出るため表示しない
  const myApps = [
    ...apps.filter((a) => a.isMine && a.status !== "承認").map((a) => ({ at: a.createdAt, html: toolApplicationCardHtml(a) })),
    ...(uiState.equipmentApplications || [])
      .filter((a) => a.isMine && !(a.status === "承認" && a.fulfilledAt))
      .map((a) => ({ at: a.createdAt, html: equipApplicationCardHtml(a) })),
    // 原価の発注申請（申請中）も「自分の申請」に出す（2026-10 本番操作テスト: 出した申請の状況がここで見えなかった）
    ...myCostOrderApplications().map((a) => ({ at: a.createdAt, html: costOrderApplicationCardHtml(a) }))
  ].sort((x, y) => String(y.at).localeCompare(String(x.at)));

  const allTools = Array.isArray(uiState.allowedTools) ? uiState.allowedTools : [];
  const catFilter = uiState.allowedToolsCategoryFilter || "all";
  const search = (uiState.allowedToolsSearch || "").trim().toLowerCase();
  const tools = allTools.filter((t) => {
    if (catFilter !== "all" && t.category !== catFilter) return false;
    if (search && !String(t.name || "").toLowerCase().includes(search)) return false;
    return true;
  });

  let myAppsBlock = "";
  if (myApps.length) {
    myAppsBlock = `
      <section class="at-section">
        <div class="at-section-head"><h3>自分の申請</h3><span class="at-count">${myApps.length}件</span></div>
        <div class="at-app-grid">${myApps.map((x) => x.html).join("")}</div>
      </section>`;
  }

  const filterChips = [{ key: "all", label: "すべて" }, ...ALLOWED_TOOL_CATEGORIES.map((c) => ({ key: c, label: c }))]
    .map(
      (f) =>
        `<button type="button" class="at-filter-chip ${catFilter === f.key ? "is-active" : ""}" data-at-filter="${escapeHtmlAttr(f.key)}">${escapeHtml(f.label)}</button>`
    )
    .join("");

  let listBody;
  if (!uiState.allowedToolsLoaded) {
    listBody = `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "許可ツールを読み込んでいます" })}</div>`;
  } else if (uiState.allowedToolsLoadError) {
    listBody = `<div class="cl-empty" style="padding:18px 20px;">許可ツールを取得できませんでした。ページを再読み込みしてください。</div>`;
  } else if (!tools.length) {
    const empty = allTools.length
      ? "条件に合うツールがありません。"
      : "許可ツールはまだ登録されていません。「＋申請する」から申請できます。";
    listBody = `<div class="cl-empty" style="padding:18px 20px;">${empty}</div>`;
  } else {
    listBody = `
      <div class="dev-table-wrap">
        <table class="pj-table at-table" data-fp-table="allowed-tools">
          <thead>
            <tr><th class="dev-th-name">ツール</th><th>区分</th><th>開発会社</th><th>URL</th><th>利用条件</th><th>申請</th></tr>
          </thead>
          <tbody>${tools.map((t) => allowedToolRowHtml(t)).join("")}</tbody>
        </table>
      </div>`;
  }

  return `
    <div class="page pj-page at-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>社内申請</h2>
            <p class="at-lead">ツールの利用許可や、PC・携帯電話などの備品の貸与はここから申請します。自分の申請（原価の発注申請を含む）の状況、貸与されている備品、会社が利用を許可しているツールの一覧も確認できます。</p>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            <button class="cl-btn cl-btn-primary" type="button" data-at-apply="1">＋ 申請する</button>
          </div>
        </div>
        <div class="dev-content at-content">
          ${myAppsBlock}
          <section class="at-section">
            <div class="at-section-head"><h3>自分の貸与品</h3></div>
            ${myEquipmentHtml()}
          </section>
          <section class="at-section">
            <div class="at-section-head"><h3>許可ツール一覧</h3><span class="at-count">${allTools.length}件</span></div>
            <div class="at-list-toolbar">
              <div class="at-filter-chips">${filterChips}</div>
              <div class="at-toolbar-right">
                <input type="search" class="at-search" data-at-search="1" data-ime-search value="${escapeHtmlAttr(uiState.allowedToolsSearch || "")}" placeholder="ツール名で検索" aria-label="ツール名で検索" />
              </div>
            </div>
            ${listBody}
          </section>
        </div>
      </div>
    </div>
    ${toolApplyModalHtml()}
    ${equipApplyModalHtml()}`;
}

// ── イベント委譲（許可ツールページ限定） ─────────────────────────
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "allowed-tools") return;

  const filterEl = target.closest("[data-at-filter]");
  if (filterEl instanceof HTMLElement) {
    uiState.allowedToolsCategoryFilter = filterEl.dataset.atFilter || "all";
    render();
    return;
  }
  if (target.closest("[data-at-apply]")) {
    // 社内申請の「＋ 申請する」。既定は備品の貸与（フォーム先頭の「申請の種類」でツールに切替。15z-equipment-applications.js）
    openEquipApplyModal();
    return;
  }
  const editEl = target.closest("[data-at-apply-edit]");
  if (editEl instanceof HTMLElement) {
    openToolApplyModal("edit", editEl.dataset.atApplyEdit);
    return;
  }
  const delEl = target.closest("[data-at-apply-delete]");
  if (delEl instanceof HTMLElement) {
    openToolApplyModal("delete", delEl.dataset.atApplyDelete);
    return;
  }
  if (target.closest("[data-at-apply-submit]")) {
    void submitToolApplication();
    return;
  }
  const fileRemoveEl = target.closest("[data-at-apply-file-remove]");
  if (fileRemoveEl instanceof HTMLElement) {
    const d = uiState.allowedToolApplyDraft;
    if (d && Array.isArray(d.files)) {
      // 入力途中の各欄を保持してから再描画（大区分変更ハンドラと同じ配慮）。
      const v = collectToolApplyForm();
      d.title = v.title;
      d.vendor = v.vendor;
      d.url = v.url;
      d.usageTerms = v.usageTerms;
      d.reason = v.reason;
      d.agree = v.agree;
      d.files.splice(Number(fileRemoveEl.dataset.atApplyFileRemove), 1);
      render();
    }
    return;
  }
  const withdrawEl = target.closest("[data-at-withdraw]");
  if (withdrawEl instanceof HTMLElement) {
    void withdrawToolApplication(withdrawEl.dataset.atWithdraw);
    return;
  }
  const closeEl = target.closest("[data-at-modal-close]");
  if (closeEl instanceof HTMLElement) {
    const closeByOverlay = closeEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return;
    closeToolApplyModal();
    return;
  }
});

// 検索入力（フォーカスは render 後に 15-sp-backlog の復元処理で維持）。
document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (currentRoute().page !== "allowed-tools") return;
  if (!t.matches("[data-at-search]")) return;
  // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
  if (e instanceof InputEvent && e.isComposing) return;
  uiState.allowedToolsSearch = t.value || "";
  render();
});

// 添付のダウンロード：許可ツールページ（自分の申請カード）と申請承認ページの両方に
// リンクが出るため、ページで絞らず data 属性だけで判定する独立リスナーにする。
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const dlEl = target.closest("[data-tool-app-att-dl]");
  if (!(dlEl instanceof HTMLElement)) return;
  void downloadToolApplicationAttachment(dlEl.dataset.toolAppAttDl);
});

// 添付ファイルの選択：形式・サイズ・件数を検査して draft に積む（実体は File のまま保持し、
// 申請POSTの成功後にアップロードする）。
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (currentRoute().page !== "allowed-tools") return;
  if (!t.matches("[data-at-apply-files]")) return;
  const d = uiState.allowedToolApplyDraft;
  if (!d) return;
  // 入力途中の各欄を保持してから再描画（大区分変更ハンドラと同じ配慮）。
  const v = collectToolApplyForm();
  d.title = v.title;
  d.vendor = v.vendor;
  d.url = v.url;
  d.usageTerms = v.usageTerms;
  d.reason = v.reason;
  d.agree = v.agree;
  if (!Array.isArray(d.files)) d.files = [];
  for (const f of Array.from(t.files || [])) {
    if (d.files.length >= TOOL_APP_ATT_MAX_FILES) {
      showToastError(`添付は${TOOL_APP_ATT_MAX_FILES}件までです`);
      break;
    }
    if (!toolAppAttachmentExtOk(f.name)) {
      showToastError(`「${f.name}」は対応形式ではありません（${TOOL_APP_ATT_EXT_HINT}）`);
      continue;
    }
    if (f.size > TOOL_APP_ATT_MAX_BYTES) {
      showToastError(`「${f.name}」は15MBを超えています`);
      continue;
    }
    d.files.push(f);
  }
  render();
});

// 大区分の変更：小区分の選択肢が変わるので、入力中の値を draft に退避してから再描画する。
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (currentRoute().page !== "allowed-tools") return;
  if (!t.matches("[data-at-apply-category-select]")) return;
  const d = uiState.allowedToolApplyDraft;
  if (!d) return;
  // 入力途中の各欄を保持（再描画で消えないように）。
  const v = collectToolApplyForm();
  d.title = v.title;
  d.vendor = v.vendor;
  d.url = v.url;
  d.usageTerms = v.usageTerms;
  d.reason = v.reason;
  d.agree = v.agree;
  // 大区分を切り替え、小区分は新しい大区分の先頭を既定にする。
  const nextCat = normalizeAllowedToolCategory(t.value);
  d.category = nextCat;
  d.subcategory = allowedToolSubcategoryOptions(nextCat)[0] || "";
  render();
});

// IME確定時の受け皿は 16-render-routing.js の compositionend が data-ime-search で
// 一括処理する（この検索窓の input にも data-ime-search を付与）。ここでの重複登録は廃止。
// ────────────────────────────────────────────────────────────────
// 稼働状況ページ：自分の工数（一般）／見積・評価サマリ（マスター/管理者）
// 閲覧はナビ権限 workload（既定で全員ON）。データは ToDo管理（progress-management）の
// scope=mywork / estimate / evaluation と、Googleカレンダー空き/埋まり（api/google-freebusy・未設定なら非表示）。
// ────────────────────────────────────────────────────────────────

/** 分 → 「1.5h」「45分」表示。null/0 は "—" */
function wlFmtMinutes(min) {
  const n = Number(min);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 60) return `${Math.round(n)}分`;
  const h = n / 60;
  return `${h % 1 === 0 ? h : h.toFixed(1)}h`;
}

/** ローカル日付の YYYY-MM-DD */
function wlIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 表示レンジ（week=今週月曜〜日曜 / month=今月）の {from,to}（YYYY-MM-DD 両端含む） */
function wlRangeBounds(range) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (range === "month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: wlIsoDate(from), to: wlIsoDate(to) };
  }
  const dow = today.getDay(); // 0=日
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: wlIsoDate(monday), to: wlIsoDate(sunday) };
}

/** タブを開いたときの自動再取得（更新ボタン廃止に伴うTTL）。取得開始時刻で判定し多重fetchを防ぐ */
const WL_TTL_MS = 5 * 60 * 1000;
function wlMarkFetched(key) {
  if (!uiState.wlFetchedAt) uiState.wlFetchedAt = {};
  uiState.wlFetchedAt[key] = Date.now();
}
function wlIsStale(key) {
  const t = uiState.wlFetchedAt?.[key] || 0;
  return Date.now() - t > WL_TTL_MS;
}

/** 案件単価・見積シミュレータ用データ（案件別の請求額×実績工数＋カテゴリ標準値） */
async function fetchWorkloadEstimate() {
  if (!authToken) return;
  wlMarkFetched("estimate");
  try {
    const r = await apiFetch("/api/progress-management?scope=estimate");
    if (!r.ok) throw new Error(String(r.status));
    const j = r.data;
    uiState.workloadEstimate = {
      projects: Array.isArray(j.projects) ? j.projects : [],
      defaults: Array.isArray(j.defaults) ? j.defaults : [],
      items: Array.isArray(j.items) ? j.items : []
    };
    uiState.workloadEstimateLoaded = true;
    uiState.workloadEstimateLoadError = false;
  } catch {
    uiState.workloadEstimateLoaded = true;
    uiState.workloadEstimateLoadError = true;
  }
  render();
}

/** マイ工数タブ：自分の直近工数ログ＋記録先案件の候補 */
async function fetchMyWork() {
  if (!authToken) return;
  wlMarkFetched("mywork");
  try {
    const r = await apiFetch("/api/progress-management?scope=mywork");
    if (!r.ok) throw new Error(String(r.status));
    const j = r.data;
    uiState.myWork = {
      logs: Array.isArray(j.logs) ? j.logs : [],
      projects: Array.isArray(j.projects) ? j.projects : []
    };
    uiState.myWorkLoaded = true;
    uiState.myWorkLoadError = false;
  } catch {
    uiState.myWorkLoaded = true;
    uiState.myWorkLoadError = true;
  }
  render();
}

/** 今月（YYYY-MM） */
function wlCurrentMonthIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function wlShiftMonth(month, delta) {
  const m = String(month || wlCurrentMonthIso()).match(/^(\d{4})-(\d{2})$/);
  const base = m ? new Date(Number(m[1]), Number(m[2]) - 1 + delta, 1) : new Date();
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}`;
}

/** 評価サマリタブ：月次集計＋カレンダー稼働（並列取得） */
async function fetchEvalData() {
  if (!authToken) return;
  wlMarkFetched("eval");
  const month = uiState.evalMonth || wlCurrentMonthIso();
  // 応答待ちの間に月を切り替えたら、この（前の月の）応答は捨てる。新しい月の見出しの下に
  // 前月の集計が出るのを防ぐ（新しい月の取得は workloadPage が別途走らせている）
  const isCurrentMonth = () => (uiState.evalMonth || wlCurrentMonthIso()) === month;
  try {
    const [evalRes, busyRes] = await Promise.all([
      apiFetch(`/api/progress-management?scope=evaluation&month=${encodeURIComponent(month)}`),
      apiFetch(`/api/google-freebusy?month=${encodeURIComponent(month)}`)
    ]);
    if (!evalRes.ok) throw new Error(String(evalRes.status));
    const evalData = evalRes.data;
    const evalBusy = busyRes.ok ? busyRes.data : { configured: false };
    if (!isCurrentMonth()) return;
    uiState.evalData = evalData;
    uiState.evalBusy = evalBusy;
    uiState.evalLoaded = true;
    uiState.evalLoadError = false;
  } catch {
    if (!isCurrentMonth()) return;
    uiState.evalLoaded = true;
    uiState.evalLoadError = true;
  }
  render();
}

// ── 共通ユーティリティ（見積・自分の工数） ──
/** 実効時間単価（円/h）。工数ゼロは null */
function wlHourlyRate(amount, minutes) {
  const a = Number(amount) || 0;
  const m = Number(minutes) || 0;
  if (a <= 0 || m <= 0) return null;
  return Math.round(a / (m / 60));
}

/** 記録一覧の案件セル：案件名＋クライアント名（あれば） */
function wlLogProjectCellHtml(l) {
  return `${escapeHtml(l.projectName)}${l.clientName ? `<span class="wl-log-client">${escapeHtml(l.clientName)}</span>` : ""}`;
}

/** 記録一覧のメモセル：ToDo由来（[TODO:]タグ取込）は課題名バッジを先頭に出す */
function wlLogNoteCellHtml(l) {
  const todo = l.agendaTitle
    ? `<span class="wl-log-todo"><span class="wl-cal-todo-badge">ToDo</span>${escapeHtml(l.agendaTitle)}</span>`
    : "";
  return `${todo}${escapeHtml(l.note || "")}`;
}

// ── 見積シミュレータタブ ────────────────────────────────────────
function wlMedian(nums) {
  const arr = nums.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (!arr.length) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : Math.round((arr[mid - 1] + arr[mid]) / 2);
}

/** カテゴリの参考値（実績があれば実績中央値、無ければ標準マスタ） */
function wlEstimateStatsFor(category) {
  const est = uiState.workloadEstimate;
  if (!est) return { source: "none", count: 0, hours: null, rate: null };
  const done = est.projects.filter(
    (p) => p.serviceCategory === category && (Number(p.totalActualMinutes) || 0) > 0 && (Number(p.amount) || 0) > 0
  );
  if (done.length) {
    const medMinutes = wlMedian(done.map((p) => Number(p.totalActualMinutes)));
    const medRate = wlMedian(done.map((p) => wlHourlyRate(p.amount, p.totalActualMinutes)).filter((r) => r != null));
    return { source: "actual", count: done.length, hours: medMinutes != null ? medMinutes / 60 : null, rate: medRate };
  }
  const def = est.defaults.find((d) => d.category === category);
  if (def && (def.defaultMinutes != null || def.defaultRate != null)) {
    return {
      source: "default",
      count: 0,
      hours: def.defaultMinutes != null ? def.defaultMinutes / 60 : null,
      rate: def.defaultRate != null ? def.defaultRate : null
    };
  }
  return { source: "none", count: 0, hours: null, rate: null };
}

/** シミュレータのカテゴリ候補（既定＋実績・標準・項目マスタに登場するカテゴリの和集合） */
function wlEstimatorCategoryChoices() {
  const set = new Set(SERVICE_CATEGORY_OPTIONS);
  const est = uiState.workloadEstimate;
  if (est) {
    est.projects.forEach((p) => { if (p.serviceCategory) set.add(p.serviceCategory); });
    est.defaults.forEach((d) => { if (d.category) set.add(d.category); });
    (est.items || []).forEach((i) => { if (i.category) set.add(i.category); });
  }
  return [...set];
}

/** カテゴリを選んだときの見積明細行（項目マスタ→無ければ実績/標準値の1行） */
function wlEstimateLinesFor(category) {
  const est = uiState.workloadEstimate;
  const items = (est?.items || []).filter((i) => i.category === category);
  if (items.length) {
    return items.map((i) => {
      const h = i.defaultMinutes != null ? i.defaultMinutes / 60 : null;
      return {
        name: i.itemName,
        hours: h != null ? String(h % 1 === 0 ? h : h.toFixed(1)) : "",
        rate: i.defaultRate != null ? String(i.defaultRate) : ""
      };
    });
  }
  const stats = wlEstimateStatsFor(category);
  return [{
    name: category,
    hours: stats.hours != null ? String(stats.hours % 1 === 0 ? stats.hours : stats.hours.toFixed(1)) : "",
    rate: stats.rate != null ? String(stats.rate) : ""
  }];
}

/** 明細1行の金額（工数h×単価。どちらか欠けたら null） */
function wlEstLineTotal(l) {
  const h = Number(l?.hours);
  const r = Number(l?.rate);
  return Number.isFinite(h) && Number.isFinite(r) && h > 0 && r > 0 ? Math.round(h * r) : null;
}

function wlEstLinesSum(lines) {
  return (Array.isArray(lines) ? lines : []).reduce((s, l) => s + (wlEstLineTotal(l) || 0), 0);
}

function workloadEstimateTabHtml() {
  if (!uiState.workloadEstimateLoaded) {
    return `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "実績データを読み込んでいます" })}</div>`;
  }
  if (uiState.workloadEstimateLoadError || !uiState.workloadEstimate) {
    return `<div class="cl-empty" style="padding:18px 20px;">実績データを取得できませんでした。ページを再読み込みしてください。</div>`;
  }
  const st = uiState.wlEstimator || { category: "", lines: [] };
  const cat = String(st.category || "");
  const choices = wlEstimatorCategoryChoices();
  const est = uiState.workloadEstimate;
  // 全カテゴリの自動見積一覧（項目マスタ→無ければ実績/標準値。行クリックで下に明細を展開）
  const autoRows = choices
    .map((c) => {
      const items = (est.items || []).filter((i) => i.category === c);
      let basis;
      let hoursTxt;
      let rateTxt;
      let totalTxt;
      if (items.length) {
        const totalH = items.reduce((s, i) => s + (i.defaultMinutes != null ? i.defaultMinutes / 60 : 0), 0);
        const total = items.reduce(
          (s, i) => s + (i.defaultMinutes != null && i.defaultRate != null ? Math.round((i.defaultMinutes / 60) * i.defaultRate) : 0),
          0
        );
        basis = `項目マスタ（${items.length}項目）`;
        hoursTxt = totalH > 0 ? `${totalH % 1 === 0 ? totalH : totalH.toFixed(1)}h` : "—";
        rateTxt = "—";
        totalTxt = total > 0 ? `¥${formatYenPlain(total)}` : "—";
      } else {
        const stats = wlEstimateStatsFor(c);
        const total = stats.hours != null && stats.rate != null ? Math.round(stats.hours * stats.rate) : null;
        basis = stats.source === "actual" ? `実績${stats.count}件の中央値` : stats.source === "default" ? "標準値" : "データなし";
        hoursTxt = stats.hours != null ? `${stats.hours % 1 === 0 ? stats.hours : stats.hours.toFixed(1)}h` : "—";
        rateTxt = stats.rate != null ? `¥${formatYenPlain(stats.rate)}/h` : "—";
        totalTxt = total != null ? `¥${formatYenPlain(total)}` : "—";
      }
      return `
        <tr class="wl-row ${cat === c ? "wl-row--expanded" : ""}" tabindex="0" role="button" aria-pressed="${cat === c ? "true" : "false"}" data-wl-est-pick="${escapeHtmlAttr(c)}">
          <td class="wl-proj-name">${escapeHtml(c)}</td>
          <td>${escapeHtml(basis)}</td>
          <td class="wl-num">${hoursTxt}</td>
          <td class="wl-num">${rateTxt}</td>
          <td class="wl-num wl-est-auto-total">${totalTxt}</td>
          <td class="wl-row-arrow" aria-hidden="true">›</td>
        </tr>`;
    })
    .join("");
  const autoTable = `
    <div class="wl-table-wrap">
      <table class="pj-table wl-table wl-est-auto-table" data-fp-table="workload-est-auto">
        <thead>
          <tr>
            <th class="wl-th-member">業務カテゴリ</th>
            <th>根拠</th>
            <th>標準工数</th>
            <th>時間単価</th>
            <th>自動見積額<span class="wl-th-note">（税抜）</span></th>
            <th class="wl-row-arrow-th" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>${autoRows}</tbody>
      </table>
    </div>
    <p class="wl-note">行クリックで明細（デザイン◯h・コーディング◯h…）を組み立てられます。</p>`;
  // 明細の組み立て（項目×工数×単価の積み上げ→見積書作成へ）
  let buildHtml = "";
  if (cat) {
    const lines = Array.isArray(st.lines) ? st.lines : [];
    const lineRows = lines
      .map((l, i) => {
        const amt = wlEstLineTotal(l);
        return `
          <tr>
            <td><input class="pj-input wl-est-name-input" data-wl-est-line="${i}" data-wl-est-field="name" value="${escapeHtmlAttr(l.name || "")}" placeholder="項目名（例：デザイン）" /></td>
            <td class="wl-num"><input type="number" class="pj-input wl-est-num-input" data-wl-est-line="${i}" data-wl-est-field="hours" value="${escapeHtmlAttr(l.hours ?? "")}" min="0" step="0.5" /></td>
            <td class="wl-num"><input type="number" class="pj-input wl-est-num-input" data-wl-est-line="${i}" data-wl-est-field="rate" value="${escapeHtmlAttr(l.rate ?? "")}" min="0" step="100" /></td>
            <td class="wl-num wl-est-line-amt" data-wl-est-amt="${i}">${amt != null ? `¥${formatYenPlain(amt)}` : "—"}</td>
            <td class="wl-num"><button type="button" class="chip-btn" data-wl-est-line-del="${i}">削除</button></td>
          </tr>`;
      })
      .join("");
    const sum = wlEstLinesSum(lines);
    const projOpts =
      `<option value="">案件を選択…</option>` +
      projects
        .slice()
        .sort((a, b) => String(a.name).localeCompare(String(b.name), "ja"))
        .map((p) => `<option value="${escapeHtmlAttr(p.id)}">${escapeHtml(p.name)}</option>`)
        .join("");
    buildHtml = `
      <div class="wl-est-wrap">
        <h3 class="wl-section-title">見積の組み立て：${escapeHtml(cat)}</h3>
        <div class="wl-table-wrap">
          <table class="pj-table wl-table wl-est-lines">
            <thead><tr><th class="wl-th-member">項目</th><th>工数（h）</th><th>単価（円/h）</th><th>金額</th><th></th></tr></thead>
            <tbody>
              ${lineRows}
              <tr class="wl-team-total"><td>合計（税抜）</td><td></td><td></td><td class="wl-num" id="wl-est-result">${sum > 0 ? `¥${formatYenPlain(sum)}` : "—"}</td><td></td></tr>
            </tbody>
          </table>
        </div>
        <div class="wl-est-actions">
          <button type="button" class="cl-btn" data-wl-est-line-add="1">＋項目を追加</button>
          <button type="button" class="cl-btn" data-wl-est-save-items="1">この項目構成を「${escapeHtml(cat)}」の標準として保存</button>
        </div>
        <div class="wl-est-actions">
          <select class="pj-input dform-select wl-est-project-select" data-search-dropdown-off="1" data-wl-est-project aria-label="見積書を作成する案件">${projOpts}</select>
          <button type="button" class="cl-btn wl-est-save-default" data-wl-est-compose="1">この内容で見積書を作成</button>
        </div>
      </div>`;
  }
  return `${autoTable}${buildHtml}`;
}

// ── 自分の工数（一般メンバー向け・閲覧のみ）───────────────────────
// 記録の振り分け・修正・確定はすべて上長がチーム工数タブで行う。
function workloadMyWorkTabHtml() {
  if (!uiState.myWorkLoaded) {
    return `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "自分の工数記録を読み込んでいます" })}</div>`;
  }
  if (uiState.myWorkLoadError || !uiState.myWork) {
    return `<div class="cl-empty" style="padding:18px 20px;">工数記録を取得できませんでした。ページを再読み込みしてください。</div>`;
  }
  const { logs } = uiState.myWork;
  const todayIso = wlIsoDate(new Date());
  const { from: weekFrom, to: weekTo } = wlRangeBounds("week");
  const monthPrefix = todayIso.slice(0, 7);
  const weekSum = logs.filter((l) => l.workDate >= weekFrom && l.workDate <= weekTo).reduce((s, l) => s + l.minutes, 0);
  const monthSum = logs.filter((l) => String(l.workDate || "").startsWith(monthPrefix)).reduce((s, l) => s + l.minutes, 0);
  const listRows = logs.length
    ? logs
        .map(
          (l) => `
          <tr>
            <td class="wl-num" style="text-align:left;">${escapeHtml(formatProgressAgendaDueDisplay(l.workDate) || "")}</td>
            <td>${wlLogProjectCellHtml(l)}</td>
            <td class="wl-num">${wlFmtMinutes(l.minutes)}</td>
            <td class="wl-mywork-note">${wlLogNoteCellHtml(l)}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="4" class="wl-expand-empty">直近35日の記録はまだありません</td></tr>`;
  return `
    <div class="dev-summary">
      <div class="dev-metric"><div class="dev-metric-label">今週の記録</div><div class="dev-metric-value">${wlFmtMinutes(weekSum)}</div></div>
      <div class="dev-metric"><div class="dev-metric-label">今月の記録</div><div class="dev-metric-value">${wlFmtMinutes(monthSum)}</div></div>
    </div>
    <p class="wl-note">カレンダーの予定から毎日23時に自動で集計されます。修正が必要な場合は上長へ連絡してください。</p>
    <h3 class="wl-section-title">直近35日の自分の記録</h3>
    <div class="wl-table-wrap">
      <table class="pj-table wl-table wl-mywork-table" data-fp-table="workload-mywork-list">
        <thead><tr><th>日付</th><th>案件</th><th>時間</th><th>メモ</th></tr></thead>
        <tbody>${listRows}</tbody>
      </table>
    </div>`;
}

// ── 評価サマリタブ（メンバー×月：質×量・稼働・案件工数を1枚に） ──
function workloadEvaluationTabHtml() {
  if (!uiState.evalLoaded) {
    return `<div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "評価サマリを集計しています" })}</div>`;
  }
  if (uiState.evalLoadError || !uiState.evalData) {
    return `<div class="cl-empty" style="padding:18px 20px;">評価サマリを取得できませんでした。ページを再読み込みしてください。</div>`;
  }
  const data = uiState.evalData;
  const busy = uiState.evalBusy || { configured: false };
  const busyConfigured = Boolean(busy.configured && !busy.error);
  const busyByUser = busyConfigured && busy.byUser && typeof busy.byUser === "object" ? busy.byUser : {};
  const month = data.month || uiState.evalMonth || wlCurrentMonthIso();
  const mm = month.match(/^(\d{4})-(\d{2})$/);
  const monthLabel = mm ? `${mm[1]}年${Number(mm[2])}月` : month;
  // 営業日数（土日祝以外・判定は 00-shared-enums.js の isBusinessDay）×8h をその月のキャパとして稼働率を出す
  let workdays = 0;
  if (mm) {
    const y = Number(mm[1]);
    const mo = Number(mm[2]);
    const dim = new Date(y, mo, 0).getDate();
    for (let d = 1; d <= dim; d++) {
      if (isBusinessDay(new Date(y, mo - 1, d))) workdays++;
    }
  }
  const capMin = workdays * 480;
  const projectTotals = data.projectTotals && typeof data.projectTotals === "object" ? data.projectTotals : {};
  // 按分売上 = 案件請求額 × 自分の工数 ÷ 案件の全期間総工数（全員×全月の合計が請求額を超えない配分）
  const attributedYen = (projectId, minutes) => {
    const tot = projectTotals[String(projectId)];
    if (!tot || !(tot.totalMinutes > 0) || !(tot.amount > 0)) return null;
    return Math.round((tot.amount * minutes) / tot.totalMinutes);
  };
  const rows = (Array.isArray(data.users) ? data.users : [])
    .map((u) => {
      const ag = data.agendaByUser?.[String(u.id)] || { doneCount: 0, doneWithDue: 0, onTime: 0, todoMinutes: 0 };
      const wk = data.workByUser?.[String(u.id)] || { directMinutes: 0, internalMinutes: 0, projectCount: 0 };
      const bd = Array.isArray(data.breakdownByUser?.[String(u.id)]) ? data.breakdownByUser[String(u.id)] : [];
      const onTimeRate = ag.doneWithDue > 0 ? Math.round((ag.onTime / ag.doneWithDue) * 100) : null;
      const busyRow = busyByUser[String(u.id)];
      const busyMin = busyRow ? Object.values(busyRow).reduce((s, v) => s + (Number(v) || 0), 0) : null;
      const utilization = busyMin != null && capMin > 0 ? Math.round((busyMin / capMin) * 100) : null;
      // 貢献売上・個人時間単価（案件に紐づく工数のみが対象）
      let revenueYen = 0;
      let projMinutes = 0;
      bd.forEach((row) => {
        if (row.projectId == null) return;
        projMinutes += row.minutes;
        const yen = attributedYen(row.projectId, row.minutes);
        if (yen != null) revenueYen += yen;
      });
      const personalRate = projMinutes > 0 && revenueYen > 0 ? Math.round(revenueYen / (projMinutes / 60)) : null;
      const expanded = String(uiState.evalExpandUserId ?? "") === String(u.id);
      const mainRow = `
        <tr class="wl-row ${expanded ? "wl-row--expanded" : ""}" data-wl-eval-expand="${escapeHtmlAttr(u.id)}">
          <td class="wl-member-cell">
            ${progressUserAvatarHtml(u, "pm-assignee-avatar--kanban", u.name)}
            <span>${escapeHtml(u.name)}</span>
            ${u.onLeave ? `<span class="wl-leave-badge">休職中</span>` : ""}
          </td>
          <td class="wl-num">${ag.doneCount || "—"}</td>
          <td class="wl-num">${onTimeRate == null ? "—" : `${onTimeRate}%`}</td>
          <td class="wl-num">${wlFmtMinutes(wk.directMinutes)}</td>
          <td class="wl-num">${wlFmtMinutes(wk.internalMinutes)}</td>
          <td class="wl-num">${wk.projectCount || "—"}</td>
          <td class="wl-num">${revenueYen > 0 ? `¥${formatYenPlain(revenueYen)}` : "—"}</td>
          <td class="wl-num">${personalRate != null ? `¥${formatYenPlain(personalRate)}/h` : "—"}</td>
          <td class="wl-num">${busyMin != null ? wlFmtMinutes(busyMin) : "—"}</td>
          <td class="wl-num">${utilization != null ? `${utilization}%` : "—"}</td>
        </tr>`;
      let detailRow = "";
      if (expanded) {
        const items = bd.length
          ? bd
              .map((row) => {
                const isInternal = row.projectId == null;
                const yen = isInternal ? null : attributedYen(row.projectId, row.minutes);
                const label = isInternal ? `社内業務（${row.internalCategory || "その他"}）` : row.projectName || `案件 #${row.projectId}`;
                return `
                  <li class="wl-task-item">
                    <span class="wl-task-link">
                      <span class="wl-task-title">${escapeHtml(label)}</span>
                      ${isInternal ? `<span class="wl-task-board">案件外</span>` : ""}
                      <span class="wl-task-due">${wlFmtMinutes(row.minutes)}</span>
                      <span class="wl-task-est">${yen != null ? `按分売上 ¥${formatYenPlain(yen)}` : ""}</span>
                    </span>
                  </li>`;
              })
              .join("")
          : `<li class="wl-expand-empty">この月の工数記録はありません</li>`;
        detailRow = `<tr class="wl-expand-row"><td colspan="10"><ul class="wl-task-list">${items}</ul></td></tr>`;
      }
      return mainRow + detailRow;
    })
    .join("");
  return `
    <div class="wl-eval-monthnav">
      <button type="button" class="cl-btn" data-wl-eval-shift="-1">‹ 前月</button>
      <span class="wl-eval-month">${escapeHtml(monthLabel)}</span>
      <button type="button" class="cl-btn" data-wl-eval-shift="1">翌月 ›</button>
    </div>
    <div class="wl-table-wrap">
      <table class="pj-table wl-table wl-eval-table" data-fp-table="workload-eval">
        <thead>
          <tr>
            <th class="wl-th-member">メンバー</th>
            <th>課題完了<span class="wl-th-note">（質×量）</span></th>
            <th>期限遵守率</th>
            <th>案件工数</th>
            <th>案件外時間</th>
            <th>関与案件</th>
            <th>貢献売上<span class="wl-th-note">（按分）</span></th>
            <th>個人単価</th>
            <th>カレンダー稼働</th>
            <th>平均稼働率<span class="wl-th-note">（平日8h基準）</span></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
`;
}

/** 見積・評価サマリ（請求額/評価データ）の閲覧可否＝マスター/管理者のみ。サーバ側も同条件でガード */
function wlCanViewAdminTabs() {
  return typeof isMasterOrAdminUser === "function" && currentUser && isMasterOrAdminUser(currentUser);
}

/** 現在のユーザーが見られるタブ集合（クリックハンドラと共用）。
 *  一般メンバー＝自分の工数（閲覧のみ）／管理層＝見積・評価サマリ */
function wlAllowedTabs() {
  if (wlCanViewAdminTabs()) return ["estimate", "evaluation"];
  return ["mywork"];
}

function workloadPage() {
  const validTabs = wlAllowedTabs();
  const tab = validTabs.includes(uiState.workloadTab) ? uiState.workloadTab : validTabs[0];
  // タブを開いた時に古いデータ（5分TTL）は自動で取り直す
  if (tab === "mywork") {
    if (uiState.myWorkLoaded && wlIsStale("mywork")) uiState.myWorkLoaded = false;
    if (!uiState.myWorkLoaded) fetchOnceInFlight("wl:mywork", fetchMyWork);
  } else if (tab === "evaluation") {
    if (uiState.evalLoaded && wlIsStale("eval")) uiState.evalLoaded = false;
    // 月ごとに1本（月切替時は新しい月を取りに行き、前月の遅延応答は fetchEvalData 側で破棄）
    if (!uiState.evalLoaded) fetchOnceInFlight(`wl:eval:${uiState.evalMonth || wlCurrentMonthIso()}`, fetchEvalData);
  } else {
    if (uiState.workloadEstimateLoaded && wlIsStale("estimate")) uiState.workloadEstimateLoaded = false;
    if (!uiState.workloadEstimateLoaded) fetchOnceInFlight("wl:estimate", fetchWorkloadEstimate);
  }
  const tabBtn = (id, label) => `<button type="button" class="wl-range-btn ${tab === id ? "is-active" : ""}" data-wl-tab="${id}">${label}</button>`;
  // 管理層＝2タブ／一般＝タブなし
  const tabBar = wlCanViewAdminTabs()
    ? `<div class="wl-range-toggle wl-tab-toggle" role="tablist">
        ${tabBtn("estimate", "見積")}
        ${tabBtn("evaluation", "評価サマリ")}
      </div>`
    : "";
  let body;
  if (tab === "estimate") body = workloadEstimateTabHtml();
  else if (tab === "evaluation") body = workloadEvaluationTabHtml();
  else body = workloadMyWorkTabHtml();
  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <div class="dev-title-row"><h2>稼働状況</h2></div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            ${tabBar}
          </div>
        </div>
        <div class="dev-content">
          ${body}
        </div>
      </div>
    </div>`;
}

// INTERNAL_WORK_CATEGORIES は app-src/00-shared-enums.js（フロント/サーバ共有の単一定義元）を参照。

// ── イベント委譲（稼働状況ページ限定） ─────────────────────────
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "workload") return;

  const tabEl = target.closest("[data-wl-tab]");
  if (tabEl instanceof HTMLElement) {
    const t = tabEl.dataset.wlTab;
    // workloadPage() 側と同じ集合（wlAllowedTabs）を許可（片方だけ更新しない）
    uiState.workloadTab = wlAllowedTabs().includes(t) ? t : "members";
    render();
    return;
  }
  const evalShiftEl = target.closest("[data-wl-eval-shift]");
  if (evalShiftEl instanceof HTMLElement) {
    const delta = Number(evalShiftEl.dataset.wlEvalShift) || 0;
    uiState.evalMonth = wlShiftMonth(uiState.evalMonth || wlCurrentMonthIso(), delta);
    uiState.evalLoaded = false;
    uiState.evalExpandUserId = null;
    render();
    return;
  }
  const evalExpandEl = target.closest("[data-wl-eval-expand]");
  if (evalExpandEl instanceof HTMLElement) {
    const id = String(evalExpandEl.dataset.wlEvalExpand || "");
    uiState.evalExpandUserId = String(uiState.evalExpandUserId ?? "") === id ? null : id;
    render();
    return;
  }
  // 見積タブ：自動見積一覧の行クリック → 明細の組み立てへロード
  const estPickEl = target.closest("[data-wl-est-pick]");
  if (estPickEl instanceof HTMLElement) {
    const cat = String(estPickEl.dataset.wlEstPick || "");
    uiState.wlEstimator = { category: cat, lines: cat ? wlEstimateLinesFor(cat) : [] };
    render();
    return;
  }
  const estLineAddEl = target.closest("[data-wl-est-line-add]");
  if (estLineAddEl instanceof HTMLElement) {
    const st = uiState.wlEstimator;
    if (!st) return;
    (st.lines || (st.lines = [])).push({ name: "", hours: "", rate: "" });
    render();
    return;
  }
  const estLineDelEl = target.closest("[data-wl-est-line-del]");
  if (estLineDelEl instanceof HTMLElement) {
    const st = uiState.wlEstimator;
    const i = Number(estLineDelEl.dataset.wlEstLineDel);
    if (st?.lines && Number.isFinite(i)) {
      st.lines.splice(i, 1);
      render();
    }
    return;
  }
  const saveItemsEl = target.closest("[data-wl-est-save-items]");
  if (saveItemsEl instanceof HTMLElement) {
    const st = uiState.wlEstimator || {};
    const cat = String(st.category || "").trim();
    if (!cat || !authToken) return;
    const items = (st.lines || [])
      .filter((l) => String(l.name || "").trim())
      .map((l) => {
        const h = Number(l.hours);
        const r = Number(l.rate);
        return {
          itemName: String(l.name).trim(),
          defaultMinutes: Number.isFinite(h) && h > 0 ? Math.round(h * 60) : null,
          defaultRate: Number.isFinite(r) && r > 0 ? Math.round(r) : null
        };
      });
    void (async () => {
      try {
        const { ok, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "saveEstimateItems", category: cat, items } });
        if (!ok) {
          showToastError(typeof j.error === "string" ? j.error : "項目マスタの保存に失敗しました");
          return;
        }
        // ローカルの items も該当カテゴリを置換
        const est = uiState.workloadEstimate;
        if (est) {
          est.items = (est.items || [])
            .filter((i) => i.category !== cat)
            .concat(items.map((i) => ({ category: cat, itemName: i.itemName, defaultMinutes: i.defaultMinutes, defaultRate: i.defaultRate })));
        }
        showToastDirect(`「${cat}」の見積項目を保存しました（${items.length}項目）`);
      } catch {
        showToastError("項目マスタの保存に失敗しました");
      }
    })();
    return;
  }
  // 見積書作成：組み立てた明細を書類コンポーザ（見積書）へプリフィル
  const estComposeEl = target.closest("[data-wl-est-compose]");
  if (estComposeEl instanceof HTMLElement) {
    const st = uiState.wlEstimator || {};
    const sel = document.querySelector("[data-wl-est-project]");
    const pidVal = sel instanceof HTMLSelectElement ? String(sel.value || "") : "";
    if (!pidVal) {
      showToastDirect("見積書を作成する案件を選択してください");
      return;
    }
    const proj = projects.find((p) => String(p.id) === pidVal);
    if (!proj) {
      showToastDirect("案件が見つかりません");
      return;
    }
    if (typeof canEditProject === "function" && !canEditProject(proj)) {
      showToastDirect(typeof readOnlyHint === "function" ? readOnlyHint(proj) : "この案件は編集できません");
      return;
    }
    const lines = (st.lines || [])
      .filter((l) => String(l.name || "").trim())
      .map((l) => ({
        item: String(l.name).trim(),
        qty: Number(l.hours) > 0 ? Number(l.hours) : 1,
        unit: "時間",
        price: Number(l.rate) > 0 ? Math.round(Number(l.rate)) : 0
      }));
    if (!lines.length) {
      showToastDirect("見積の項目を入力してください");
      return;
    }
    openComposer("quote", proj.id, { preset: { subject: `${proj.name} 見積`, lines } });
    return;
  }
});

// 見積タブ：明細行の入力で金額・合計を即時再計算（再描画せずDOM更新）
// 見積の自動見積行はキーボード操作（Tab移動＋Enter/Space）でも選択できるようにする
document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const row = e.target instanceof HTMLElement ? e.target.closest("[data-wl-est-pick]") : null;
  if (!row) return;
  e.preventDefault();
  row.click();
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (currentRoute().page !== "workload") return;
  if (!t.hasAttribute("data-wl-est-line")) return;
  const i = Number(t.getAttribute("data-wl-est-line"));
  const field = String(t.getAttribute("data-wl-est-field") || "");
  const line = uiState.wlEstimator?.lines?.[i];
  if (!line || !["name", "hours", "rate"].includes(field)) return;
  line[field] = t.value;
  const amtEl = document.querySelector(`[data-wl-est-amt="${i}"]`);
  const amt = wlEstLineTotal(line);
  if (amtEl) amtEl.textContent = amt != null ? `¥${formatYenPlain(amt)}` : "—";
  const sum = wlEstLinesSum(uiState.wlEstimator.lines);
  const out = document.getElementById("wl-est-result");
  if (out) out.textContent = sum > 0 ? `¥${formatYenPlain(sum)}` : "—";
});
// ── 承認フロー統合タブ（アカウント設定 > 承認フロー） ──
// 一覧行の開閉と、申請フロー（uiState.approvalOtherFlows・部署から独立）のメンバー編集を扱う。
// フローの追加削除・名前変更・承認ステップ系（data-approval-*）の既存ハンドラは 16-render-routing.js のまま。
// 変更はすべて下書き（メモリ）で、「承認フローを登録」(data-approval-save-drafts) が保存する。

function _onApprovalFlowTab() {
  return isAccountSettingsRoute() && uiState.accountSettingsTab === "approval";
}

/** 対象フローだけを差し替えて下書きへ反映。
 *  「その他の申請の承認者」（kind:<種別>）は申請フローではないので、CC を承認者の設定（approvalApprovers）に持つ */
function _afUpdateFlow(flowId, updateFn) {
  ensureApprovalOtherFlowsState();
  if (!(uiState.approvalOtherFlows || []).some((f) => f.id === flowId) && uiState.approvalApprovers?.[flowId]) {
    const entry = uiState.approvalApprovers[flowId];
    const next = updateFn({ id: flowId, memberIds: [], ccMemberIds: approvalCcIdsOf(entry) });
    uiState.approvalApprovers[flowId] = { ...entry, ccMemberIds: approvalCcIdsOf(next) };
    persistApprovalApproversStore();
    return;
  }
  uiState.approvalOtherFlows = (uiState.approvalOtherFlows || []).map((f) =>
    f.id === flowId ? updateFn({ ...f, memberIds: [...(f.memberIds || [])], ccMemberIds: [...(f.ccMemberIds || [])] }) : f
  );
  persistApprovalOtherFlowsStore();
}

/** "<flowId>:<userId>" を分ける。「その他の申請の承認者」の flowId は "kind:<種別>" とコロンを含むため最後のコロンで切る */
function _afSplitFlowUser(value) {
  const s = String(value || "");
  const i = s.lastIndexOf(":");
  return i < 0 ? ["", ""] : [s.slice(0, i), s.slice(i + 1)];
}

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !_onApprovalFlowTab()) return;

  // ピッカー内の選択（行の開閉より先に判定）
  const pickEl = target.closest("[data-af-pick-user]");
  if (pickEl instanceof HTMLElement && uiState.afPicker) {
    const { flowId, kind } = uiState.afPicker;
    const isCc = kind === "cc";
    const blockedOwner = !isCc ? (pickEl.dataset.afPickBlocked || "") : "";
    if (blockedOwner) {
      showToast(`「${blockedOwner}」に所属済みです。先にそちらのフローから外してください`);
      return;
    }
    const uid = String(pickEl.dataset.afPickUser || "");
    // 選んでも閉じず、押すたびに追加⇔解除を切り替える（続けて複数人を選び、「閉じる」で閉じる。2026-10 要望）。
    // 画面全体を描き直すとちらつくため、押した行のチェックだけ書き換え、後ろの画面は閉じるときに描き直す
    if (uid && flowId) {
      const field = isCc ? "ccMemberIds" : "memberIds";
      const toggle = (ids) => (ids.includes(uid) ? ids.filter((id) => id !== uid) : [...ids, uid]);
      _afUpdateFlow(flowId, (f) => ({ ...f, [field]: toggle((f[field] || []).map(String)) }));
      const picked = !pickEl.classList.contains("is-picked");
      pickEl.classList.toggle("is-picked", picked);
      pickEl.setAttribute("aria-pressed", picked ? "true" : "false");
    }
    return;
  }
  if (uiState.afPicker && target.closest("[data-af-scrim]")) {
    uiState.afPicker = null;
    render();
    return;
  }

  const memberAddEl = target.closest("[data-af-member-add]");
  if (memberAddEl instanceof HTMLElement) {
    uiState.afPicker = { flowId: memberAddEl.dataset.afMemberAdd || "", kind: "member" };
    render();
    return;
  }
  const memberRemoveEl = target.closest("[data-af-member-remove]");
  if (memberRemoveEl instanceof HTMLElement) {
    const [flowId, uid] = _afSplitFlowUser(memberRemoveEl.dataset.afMemberRemove);
    if (flowId && uid) {
      _afUpdateFlow(flowId, (f) => ({ ...f, memberIds: f.memberIds.filter((id) => id !== uid) }));
      showToast("メンバーから外しました");
      render();
    }
    return;
  }
  const ccAddEl = target.closest("[data-af-cc-add]");
  if (ccAddEl instanceof HTMLElement) {
    uiState.afPicker = { flowId: ccAddEl.dataset.afCcAdd || "", kind: "cc" };
    render();
    return;
  }
  const ccRemoveEl = target.closest("[data-af-cc-remove]");
  if (ccRemoveEl instanceof HTMLElement) {
    const [flowId, uid] = _afSplitFlowUser(ccRemoveEl.dataset.afCcRemove);
    if (flowId && uid) {
      _afUpdateFlow(flowId, (f) => ({ ...f, ccMemberIds: (f.ccMemberIds || []).filter((id) => id !== uid) }));
      showToast("CCから外しました");
      render();
    }
    return;
  }
  // 行の開閉（行内のボタン・チップ×を上で処理した後）
  const rowEl = target.closest("[data-af-row-toggle]");
  if (rowEl instanceof HTMLElement) {
    const id = rowEl.dataset.afRowToggle || "";
    uiState.approvalFlowOpenId = uiState.approvalFlowOpenId === id ? "" : id;
    render();
    return;
  }
});
// ─────────────────────────────────────────────────────────────
// 原価割当（営業ビュー）: 自分の案件に割り当てられた原価の照会＋異議申請
//   - 計上月（単月）・案件（複数選択）・顧客（複数選択）で絞り込み
//   - 「異議」で理由を添えて未割当へ差し戻し（CS が再割当する）
// イベントは独立リスナー（16 の巨大ハンドラには足さない）
// ─────────────────────────────────────────────────────────────

const COST_REVIEW_UNASSIGNED_VALUE = "unassigned";
/** 担当者プルダウン「販管費・調整用」: 実案件を持たない販管費・調整用バケットの原価を照会する */
const COST_REVIEW_OVERHEAD_VALUE = "overhead";
const COST_REVIEW_PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

/** 照会一覧の初期状態（「リセット」もこれに戻す。既定は計上月の新しい順） */
function emptyCostReviewState(perPage) {
  return {
    month: "",
    vendor: "",
    projectIds: [],
    clientNames: [],
    projMenuOpen: false,
    clientMenuOpen: false,
    projQuery: "",
    clientQuery: "",
    assigneeId: "",
    sortKey: "targetMonth",
    sortDir: "desc",
    page: 1,
    perPage: COST_REVIEW_PAGE_SIZE_OPTIONS.includes(Number(perPage)) ? Number(perPage) : 50
  };
}

function ensureCostReviewState() {
  if (!uiState.costReview || typeof uiState.costReview !== "object") {
    uiState.costReview = emptyCostReviewState();
  }
  const st = uiState.costReview;
  if (!Array.isArray(st.projectIds)) st.projectIds = [];
  if (!Array.isArray(st.clientNames)) st.clientNames = [];
  if (typeof st.assigneeId !== "string") st.assigneeId = "";
  // マスター/CS の担当者プルダウンに「すべて」は無く、既定は割当作業（未割当）
  if (!st.assigneeId && costReviewIsMasterView()) st.assigneeId = COST_REVIEW_UNASSIGNED_VALUE;
  if (typeof st.vendor !== "string") st.vendor = "";
  if (typeof st.sortKey !== "string" || !st.sortKey) st.sortKey = "targetMonth";
  if (st.sortDir !== "asc" && st.sortDir !== "desc") st.sortDir = "desc";
  // 表示形式: 営業は「どの案件にどの原価がついたか」を見る用途なのでカードが既定。
  // マスターは担当者横断の一覧照会が主なので表が既定（どちらもトグルで切替可）
  if (st.viewMode !== "cards" && st.viewMode !== "table") {
    st.viewMode = costReviewIsMasterView() ? "table" : "cards";
  }
  if (!Number.isFinite(Number(st.page)) || Number(st.page) < 1) st.page = 1;
  if (!COST_REVIEW_PAGE_SIZE_OPTIONS.includes(Number(st.perPage))) st.perPage = 50;
  return st;
}

/** ページング状態をクランプして返す（フィルタ後の件数が変わっても常に有効なページに収める） */
function costReviewPagedState(totalRows) {
  const st = ensureCostReviewState();
  const pageCount = Math.max(1, Math.ceil((Number(totalRows) || 0) / st.perPage));
  st.page = Math.min(Math.max(1, Number(st.page) || 1), pageCount);
  return { st, pageCount };
}

function costReviewPaginationHtml(totalRows, pagedState) {
  const { st, pageCount } = pagedState || costReviewPagedState(totalRows);
  const start = (st.page - 1) * st.perPage;
  const end = Math.min(start + st.perPage, totalRows);
  const perPageOptions = COST_REVIEW_PAGE_SIZE_OPTIONS.map(
    (n) => `<option value="${n}" ${st.perPage === n ? "selected" : ""}>${n}件</option>`
  ).join("");
  return `
    <div class="pj-pagination cost-mgmt-pagination">
      <div class="pj-page-left">
        <span>表示 ${totalRows === 0 ? 0 : start + 1}〜${end} 件 / 合計 ${totalRows} 件</span>
      </div>
      <div class="pj-page-right">
        <select class="pj-input" style="width:100px;" data-search-dropdown-off="1" data-cost-review-per-page="1">${perPageOptions}</select>
        <button type="button" class="inline-btn ghost" data-cost-review-page="prev" ${st.page <= 1 ? "disabled" : ""}>前へ</button>
        <span class="cost-mgmt-page-indicator">${st.page} / ${pageCount}</span>
        <button type="button" class="inline-btn ghost" data-cost-review-page="next" ${st.page >= pageCount ? "disabled" : ""}>次へ</button>
      </div>
    </div>`;
}

/** マスターは全員分を担当者プルダウンで絞り込んで照会できる（それ以外は自分固定）。
 *  CS職種はマスターと同じ割当担当なので、この照会・割当ビューもマスターと同じ見え方にする
 *  （userSeesCostAllocationCsView() が既に両者を同じ「CSビュー」に振り分けているのと揃える）。 */
function costReviewIsMasterView() {
  if (systemRoleOfUser(currentUser) === "マスター") return true;
  const roles =
    Array.isArray(currentUser?.roles) && currentUser.roles.length ? currentUser.roles : [currentUser?.role];
  return roles.includes("CS");
}

/** 自分（担当営業。未設定なら担当者）の案件か — 予算進捗の帰属と同じ salesOwnerUser 基準 */
function isMyProjectForCostReview(p) {
  const u = salesOwnerUser(p);
  return !!u && u.id === currentUser.id;
}

/** マスタービュー用の担当者プルダウン選択肢: 案件を持つ担当営業を名前順で列挙 */
function costReviewOwnerOptions() {
  const seen = new Map();
  for (const p of projects) {
    const owner = salesOwnerUser(p);
    if (owner && !seen.has(owner.id)) seen.set(owner.id, owner.name || String(owner.id));
  }
  return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], "ja"));
}

/** 原価箱の割当先候補: 在籍中の営業を名前順で列挙（案件をまだ持たない新人も選べるよう projects 経由にしない） */
function costBoxSalesOwnerOptions() {
  return users
    .filter((u) => isActiveUser(u) && !u.onLeave && !u.retired && u.role === "営業")
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ja"));
}

function ensureCostBoxPickState() {
  if (!uiState.costBoxPick || typeof uiState.costBoxPick !== "object") {
    uiState.costBoxPick = { salesOwnerId: "", saving: false };
  }
  return uiState.costBoxPick;
}

/** 原価割当（未割当キュー）の「まとめて割当」バーに添える、営業の原価箱への一括投入ミニフォーム。
 *  期は常に今期固定（過去期に付けられると粗利が遡って動くため選ばせない）。 */
function costBoxAssignBarHtml() {
  const st = ensureCostBoxPickState();
  const ownerOptions = costBoxSalesOwnerOptions();
  return `
    <span class="sep" aria-hidden="true">|</span>
    <select class="pj-input cost-alloc-bulk-flat-select" data-cost-box-owner-pick="1" aria-label="原価箱の担当営業">
      <option value="">営業の箱に割当</option>
      ${ownerOptions.map((u) => `<option value="${escapeHtmlAttr(u.id)}" ${st.salesOwnerId === u.id ? "selected" : ""}>${escapeHtml(u.name || "")}</option>`).join("")}
    </select>
    <button type="button" class="cl-btn cl-btn-primary cost-alloc-bulk-flat-btn" data-cost-box-assign="1" ${st.saving ? "disabled" : ""}>${st.saving ? "処理中…" : "箱に入れる"}</button>`;
}

/** 営業ビューの行: 自分の案件（マスターは選択した担当者、未選択なら全員）に紐づく原価すべて（案件名・顧客名付き） */
function costReviewAllRows() {
  const st = ensureCostReviewState();
  const isMaster = costReviewIsMasterView();
  const out = [];
  for (const p of projects) {
    const owner = salesOwnerUser(p);
    if (isMaster) {
      if (!owner || owner.id !== st.assigneeId) continue;
    } else if (!isMyProjectForCostReview(p)) {
      continue;
    }
    for (const c of p.costs || []) {
      out.push({
        ...c,
        _projectId: p.id,
        _projectName: p.name,
        _clientName: clientName(p) || String(p.client || ""),
        _ownerName: owner?.name || ""
      });
    }
  }
  // 販管費・調整用に割り当てた原価は実案件を持たず p.costs に乗らないため、上のループでは拾えない。
  // マスターが担当者プルダウンで「販管費・調整用」を選んだときにだけ出す
  if (isMaster && st.assigneeId === COST_REVIEW_OVERHEAD_VALUE) {
    for (const c of ensureOrphanCosts()) {
      const isSalesExpense = isSalesExpenseCostRow(c);
      const isAdjustment = isAdjustmentCostRow(c);
      if (!isSalesExpense && !isAdjustment) continue;
      out.push({
        ...c,
        _projectId: isSalesExpense ? COST_SALES_EXPENSE_PROJECT_UI_ID : COST_ADJUSTMENT_PROJECT_UI_ID,
        _projectName: isSalesExpense ? COST_SALES_EXPENSE_PROJECT_LABEL : COST_ADJUSTMENT_PROJECT_LABEL,
        _clientName: "",
        _ownerName: ""
      });
    }
  }
  return out;
}

function costReviewFilteredRows() {
  const st = ensureCostReviewState();
  let rows = costReviewAllRows();
  if (st.month) {
    const want = parseProjectBillingMonthYyyymm(st.month.replace(/-/g, "/"));
    rows = rows.filter((c) => parseProjectBillingMonthYyyymm(c.targetMonth || "") === want);
  }
  if (st.projectIds.length) {
    const pick = new Set(st.projectIds);
    rows = rows.filter((c) => pick.has(c._projectId));
  }
  if (st.clientNames.length) {
    const pick = new Set(st.clientNames);
    rows = rows.filter((c) => pick.has(c._clientName));
  }
  if (st.vendor) {
    rows = rows.filter((c) => String(c.vendor || "").trim() === st.vendor);
  }
  // 既定は計上月の新しい順 → 原価ID降順（sortCostRowsBy が同値を ID 降順で安定させる）
  return sortCostRowsBy(rows, st.sortKey, st.sortDir);
}

function costReviewMultiSummary(selected, allLabel) {
  if (!selected.length) return allLabel;
  if (selected.length <= 2) return selected.join("・");
  return `${selected.length}件選択`;
}

/** 複数選択プルダウン（ドメイン一覧の状況フィルタと同方式・案件用はパネル内検索付き） */
function costReviewMultiDdHtml({ kind, label, options, selected, open, query }) {
  const summary = costReviewMultiSummary(selected.map((o) => o.label ?? o), "すべて");
  const q = String(query || "").trim().toLowerCase();
  const boxes = options
    .filter((o) => !q || String(o.label).toLowerCase().includes(q))
    .map(
      (o) =>
        `<label class="cl-inline-check"><input type="checkbox" data-cost-review-pick="${escapeHtmlAttr(kind)}" value="${escapeHtmlAttr(o.value)}" ${o.checked ? "checked" : ""} /><span>${escapeHtml(o.label)}</span></label>`
    )
    .join("");
  const searchHtml = `<input class="pj-input cost-review-dd-search" type="search" data-cost-review-dd-search="${escapeHtmlAttr(kind)}" placeholder="${kind === "project" ? "案件名" : "顧客名"}で絞り込み" value="${escapeHtmlAttr(String(query || ""))}" autocomplete="off" />`;
  return `
    <div class="cost-mgmt-search-field cost-review-dd-field">
      <span class="cost-mgmt-search-label">${escapeHtml(label)}（複数選択可）</span>
      <div class="dm-status-dd cost-review-dd ${open ? "open" : ""}">
        <button type="button" class="cl-filter-input dm-status-dd-trigger" data-cost-review-dd-toggle="${escapeHtmlAttr(kind)}" aria-haspopup="true" aria-expanded="${open ? "true" : "false"}"><span class="dm-status-dd-label">${escapeHtml(summary)}</span><span class="dm-status-dd-caret">▾</span></button>
        ${open ? `<div class="dm-status-dd-backdrop" data-cost-review-dd-close="1"></div><div class="dm-status-dd-panel cost-review-dd-panel">${searchHtml}${boxes || '<p class="cost-review-dd-empty">該当なし</p>'}</div>` : ""}
      </div>
    </div>`;
}

/** 原価の登録方法の表記（原価割当のグループ見出し「自動取込・N行」（REM）「手動取込・N行」（広告CSV）とそろえる。
 *  以前は広告CSVの行も「手動」で、手入力と区別できなかった） */
function costImportSourceLabel(c) {
  if (c.importSource === "rem") return "自動取込";
  return adCsvNoteAccount(c.note) ? "手動取込" : "手入力";
}

/** 原価割当の未割当ビューに効いている計上月（"YYYY-MM"。効いていなければ ""）。
 *  マスターが担当者「未割当」を選んだときだけ、計上月の絞り込みが割当作業の一覧に掛かる。
 *  表示（costReviewSectionHtml）と「すべて選択」・選択の整理（11-cost-form.js）の両方がこれを使う */
function costAllocationActiveMonthFilter() {
  if (currentRoute().page !== "cost-allocation") return "";
  const st = ensureCostReviewState();
  return costReviewIsMasterView() && st.assigneeId === COST_REVIEW_UNASSIGNED_VALUE ? String(st.month || "") : "";
}

function costReviewSectionHtml() {
  const st = ensureCostReviewState();
  const isMaster = costReviewIsMasterView();

  const ownerOptions = isMaster ? costReviewOwnerOptions() : [];
  const assigneeDdHtml = isMaster
    ? `
          <label class="cost-mgmt-search-field cost-review-owner-field">
            <span class="cost-mgmt-search-label">担当者</span>
            <select class="pj-input" data-cost-review-assignee>
              <option value="${COST_REVIEW_UNASSIGNED_VALUE}" ${st.assigneeId === COST_REVIEW_UNASSIGNED_VALUE ? "selected" : ""}>未割当</option>
              <option value="${COST_REVIEW_OVERHEAD_VALUE}" ${st.assigneeId === COST_REVIEW_OVERHEAD_VALUE ? "selected" : ""}>販管費・調整用</option>
              ${ownerOptions.map(([id, name]) => `<option value="${escapeHtmlAttr(id)}" ${st.assigneeId === id ? "selected" : ""}>${escapeHtml(name)}</option>`).join("")}
            </select>
          </label>`
    : "";

  // マスターが「未割当」を選んだ場合は、割当作業（案件への紐づけ）画面を出す。
  // 案件・顧客フィルタは未割当行に案件が無いため意味を持たないが、計上月フィルタは
  // 担当者を選んだ時と同じく使えるようにする（絞り込みが減ったように見える不具合の修正）
  if (isMaster && st.assigneeId === COST_REVIEW_UNASSIGNED_VALUE) {
    // 担当者・計上月は割当作業側のツールバー（発注先・並び替え）へ差し込み、絞り込みを1行にまとめる
    return costAllocationSectionHtml({
      // 表示と「すべて選択」（11-cost-form.js costAllocationCheckAllRows）で同じ判定を使う
      monthFilter: costAllocationActiveMonthFilter(),
      leadingFiltersHtml: `
          ${assigneeDdHtml}
          <label class="cost-mgmt-search-field cost-mgmt-search-field--month">
            <span class="cost-mgmt-search-label">計上月</span>
            <input class="pj-input pj-input-month" type="month" data-show-picker="1" data-search-dropdown-off="1" data-cost-review-month value="${escapeHtmlAttr(st.month || "")}" />
          </label>`,
      clearButtonHtml: `<button type="button" class="inline-btn ghost cost-review-clear" data-cost-review-clear="1">リセット</button>`
    });
  }

  const all = costReviewAllRows();
  const filteredRows = costReviewFilteredRows();
  const paged = costReviewPagedState(filteredRows.length);
  const pageSt = paged.st;
  const rows = filteredRows.slice((pageSt.page - 1) * pageSt.perPage, (pageSt.page - 1) * pageSt.perPage + pageSt.perPage);

  // 選択肢は自分の案件（原価があるもの）から生成
  const projSeen = new Map();
  const clientSeen = new Set();
  for (const r of all) {
    if (!projSeen.has(r._projectId)) projSeen.set(r._projectId, r._projectName);
    if (r._clientName) clientSeen.add(r._clientName);
  }
  const projOptions = [...projSeen.entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "ja"))
    .map(([id, name]) => ({ value: id, label: name, checked: st.projectIds.includes(id) }));
  const clientOptions = [...clientSeen]
    .sort((a, b) => a.localeCompare(b, "ja"))
    .map((nm) => ({ value: nm, label: nm, checked: st.clientNames.includes(nm) }));
  const selProjLabels = projOptions.filter((o) => o.checked).map((o) => o.label);
  const selClientLabels = clientOptions.filter((o) => o.checked).map((o) => o.label);

  const totalIncl = filteredRows.reduce((s, c) => s + Number(c.amount || 0) + costConsumptionTaxYen(c), 0);

  // 一覧は列ヘッダークリックで並び替える（カード表示は計上月の新しい順で固定のため並び替えUIは持たない）
  const sortTh = (key, label) =>
    `<th scope="col" class="cost-th-sortable${st.sortKey === key ? " cost-sort-active" : ""}" data-cost-review-sort="${key}">${escapeHtml(label)}${st.sortKey === key ? (st.sortDir === "asc" ? " ▲" : " ▼") : ""}</th>`;

  const colCount = isMaster ? 10 : 9;
  const emptyMessage = isMaster
    ? st.assigneeId === COST_REVIEW_OVERHEAD_VALUE
      ? "販管費・調整用に割り当てられた原価はありません"
      : "この担当者に割り当てられた原価はありません"
    : "あなたの案件に割り当てられた原価はありません";

  const filterBar = `
    <div class="pj-filter-area">
      <div class="cost-mgmt-search-bar pj-cost-mgmt-filter-row pj-filter-row--main">
        <div class="cost-mgmt-search-row cost-mgmt-search-row--top">
          ${assigneeDdHtml}
          <label class="cost-mgmt-search-field cost-mgmt-search-field--month">
            <span class="cost-mgmt-search-label">計上月</span>
            <input class="pj-input pj-input-month" type="month" data-show-picker="1" data-search-dropdown-off="1" data-cost-review-month value="${escapeHtmlAttr(st.month || "")}" />
          </label>
          <label class="cost-mgmt-search-field cost-mgmt-search-field--vendor">
            <span class="cost-mgmt-search-label">発注先</span>
            <select class="pj-input" data-cost-review-vendor>${costMgmtVendorFilterOptionsHtml(all, st.vendor)}</select>
          </label>
          ${costReviewMultiDdHtml({ kind: "project", label: "案件", options: projOptions, selected: selProjLabels, open: !!st.projMenuOpen, query: st.projQuery })}
          ${costReviewMultiDdHtml({ kind: "client", label: "顧客", options: clientOptions, selected: selClientLabels, open: !!st.clientMenuOpen, query: st.clientQuery })}
          <div class="cost-review-view-seg" role="group" aria-label="表示形式">
            <button type="button" class="inline-btn ghost${st.viewMode === "cards" ? " cost-review-view-on" : ""}" data-cost-review-view="cards" title="案件ごとのカードで表示">案件カード</button>
            <button type="button" class="inline-btn ghost${st.viewMode === "table" ? " cost-review-view-on" : ""}" data-cost-review-view="table" title="従来の一覧表で表示">一覧</button>
          </div>
          <button type="button" class="inline-btn ghost cost-review-clear" data-cost-review-clear="1">リセット</button>
        </div>
      </div>
    </div>`;

  const tbody = uiState.heavyDataLoading
    ? `<tr><td colspan="${colCount}" class="cost-empty cost-empty--heavy-load">${flowPilotNowLoadingBlock({ inline: true, subtitle: "原価データを読み込んでいます" })}</td></tr>`
    : filteredRows.length === 0
      ? `<tr><td colspan="${colCount}" class="cost-empty">${all.length === 0 ? emptyMessage : "該当する原価がありません"}</td></tr>`
      : rows
          .map((c) => {
            const taxY = costConsumptionTaxYen(c);
            const exY = Number(c.amount || 0);
            const amtInner =
              taxY > 0
                ? `<span class="cost-amt-total">${yen.format(exY)}</span><div class="cost-tax-sub">税込 ${yen.format(exY + taxY)}</div>`
                : `<span class="cost-amt-total">${yen.format(exY)}</span>`;
            const noteRaw = stripCostBucketMarkers(String(c.note || "").trim());
            const dbNo = costDbNumericId(c);
            // 発注名・備考は80字で省略し、長い場合は「全文」でモーダル表示（一覧で切れて読めないため）
            const noteAttr = escapeHtmlAttr(noteRaw);
            const noteCellInner = noteRaw
              ? `${escapeHtml(noteRaw.slice(0, 80))}${noteRaw.length > 80 ? `…<button type="button" class="cost-note-more-btn" data-cost-note-open="1" data-cost-note-text="${noteAttr}" data-cost-note-subtitle="${escapeHtmlAttr(`No.${dbNo != null ? dbNo : "—"} / ${c._projectName || ""}`)}">全文</button>` : ""}`
              : "—";
            const payLabel = costPayStatusDisplayLabel(c);
            const payHtml = c.paidDate
              ? `<span class="cost-pay-status cs-paid">${escapeHtml(costDateShortDisplay(c.paidDate))}</span>`
              : `<span class="cost-pay-status ${costPayClass(payLabel)}">${escapeHtml(payLabel)}</span>`;
            // 経理からの差し戻し: 理由バッジ＋「修正する」（フル編集フォーム。保存で解消）
            const remandHtml = c.remandNote
              ? `<div class="cost-remand-badge-wrap"><span class="cost-remand-badge" title="${escapeHtmlAttr(`経理より差し戻し: ${c.remandNote}`)}">差戻し: ${escapeHtml(String(c.remandNote).slice(0, 30))}${String(c.remandNote).length > 30 ? "…" : ""}</span></div>`
              : "";
            return `
          <tr${c.remandNote ? ' class="cost-row-remanded"' : ""}>
            <td class="cost-mgmt-no-cell">${dbNo != null ? dbNo : "—"}</td>
            ${isMaster ? `<td class="cost-review-owner-cell" title="${escapeHtmlAttr(c._ownerName || "")}">${escapeHtml(c._ownerName || "—")}</td>` : ""}
            <td class="cost-table-note-cell" title="${noteAttr}">${noteCellInner}</td>
            <td class="cost-vendor" title="${escapeHtmlAttr(c.vendor || "")}">${escapeHtml(c.vendor || "—")}</td>
            <td class="cost-table-project" title="${escapeHtmlAttr(c._projectName)}">${escapeHtml(c._projectName)}<div class="cost-review-client-sub">${escapeHtml(c._clientName || "")}</div>${remandHtml}</td>
            <td class="cost-td-category"><span class="cost-category-badge">${escapeHtml(c.category)}</span></td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
            <td class="cost-mgmt-pay-cell">${payHtml}</td>
            <td class="cost-actions">
              ${c.remandNote ? `<button type="button" class="inline-btn cost-remand-fix-btn" data-cost-remand-fix="${escapeHtmlAttr(c.id)}" title="差し戻された内容を修正します（保存すると差し戻しは解消）">修正する</button>` : ""}
              ${isNoProjectBucketUiId(c._projectId) || costOrderProjectLocked(c) ? "" : `<button type="button" class="inline-btn ghost cost-review-dispute-btn" data-cost-review-dispute="${escapeHtmlAttr(c.id)}" title="この原価は担当案件ではない等、内容が違うときに未割当へ差し戻します">異議</button>`}
              ${costReviewEditDeleteBtnsHtml(c)}
              ${costMoveButtonHtml(c)}
            </td>
          </tr>`;
          })
          .join("");

  // 案件カード表示（営業の既定）: どの案件にどの原価がついたかを1枚ずつ確認する
  if (st.viewMode === "cards" && !uiState.heavyDataLoading) {
    return `${filterBar}${costReviewProjectCardsHtml(filteredRows, { totalIncl, emptyMessage, hasAny: all.length > 0 })}`;
  }
  return `
    ${filterBar}
    <div class="cost-review-summary">
      <span>表示中 <strong>${filteredRows.length}</strong> 件</span>
      <span class="cost-review-summary-total">原価合計（税込） <strong>${yen.format(totalIncl)}</strong></span>
    </div>
    <div class="pj-table-area">
      <table class="cost-table cost-table--global cost-table--review" data-fp-table="cost-review">
        <thead>
          <tr>
            ${sortTh("no", "No")}
            ${isMaster ? sortTh("owner", "担当者") : ""}
            ${sortTh("note", "発注名")}
            ${sortTh("vendor", "発注先")}
            ${sortTh("project", "計上案件")}
            ${sortTh("category", "カテゴリ")}
            ${sortTh("amount", "金額（税抜）")}
            ${sortTh("targetMonth", "計上月")}
            ${sortTh("payStatus", "支払")}
            <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
          </tr>
        </thead>
        <tbody>${tbody}</tbody>
      </table>
    </div>
    ${costReviewPaginationHtml(filteredRows.length, paged)}`;
}

/**
 * 照会の案件カード表示: フィルタ済みの原価行を案件ごとに1枚へ集約する（意匠は原価割当の物件グループと共通の .cost-bkgrp）。
 * カードは計上月の新しい順（カード内の最新の計上月）で固定し、同じ月なら新しく登録された原価を含む案件を上にする。
 * カード内の行も計上月の新しい順で固定（一覧の列ヘッダー並び替えは引き継がない）。
 * ページングは「カード数」に対して既存のページャをそのまま使う。
 */
function costReviewProjectCardsHtml(filteredRows, { totalIncl, emptyMessage, hasAny }) {
  const byProj = new Map();
  for (const c of sortCostRowsBy(filteredRows, "targetMonth", "desc")) {
    if (!byProj.has(c._projectId)) byProj.set(c._projectId, []);
    byProj.get(c._projectId).push(c);
  }
  const inclSum = (rs) => rs.reduce((s, c) => s + Number(c.amount || 0) + costConsumptionTaxYen(c), 0);
  const cards = [...byProj.entries()].sort((a, b) => compareCostCardRowsNewestFirst(a[1], b[1]));
  const paged = costReviewPagedState(cards.length);
  const pageSt = paged.st;
  const pageCards = cards.slice((pageSt.page - 1) * pageSt.perPage, (pageSt.page - 1) * pageSt.perPage + pageSt.perPage);
  const open = uiState.costReviewOpenProj && typeof uiState.costReviewOpenProj === "object" ? uiState.costReviewOpenProj : (uiState.costReviewOpenProj = {});

  const cardHtml = ([projectId, rs]) => {
    const proj = projects.find((pp) => pp.id === projectId);
    const first = rs[0];
    const isOpen = !!open[projectId];
    const exTotal = rs.reduce((s, c) => s + Number(c.amount || 0), 0);
    const byCat = new Map();
    for (const c of rs) byCat.set(c.category, (byCat.get(c.category) || 0) + Number(c.amount || 0));
    const catChips = [...byCat.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([cat, sum]) => `<span class="cost-bkgrp-cat"><span class="cost-category-badge">${escapeHtml(cat)}</span> ${yen.format(sum)}</span>`)
      .join("");
    const metaBits = [
      proj?.dbId != null ? `#${proj.dbId}` : "",
      proj?.billingMonth ? `請求 ${proj.billingMonth}` : "",
      first?._clientName || ""
    ].filter(Boolean).join(" ・ ");
    const remandN = rs.filter((c) => c.remandNote).length;
    const bodyHtml = !isOpen
      ? ""
      : `<div class="cost-bkgrp-body"><table class="cost-table cost-table--global cost-bkgrp-table" data-fp-table="cost-review-cards">
        <thead><tr>
          <th scope="col">発注名</th>
          <th scope="col">発注先</th>
          <th scope="col">カテゴリ</th>
          <th scope="col">金額（税抜）</th>
          <th scope="col">計上月</th>
          <th scope="col">支払</th>
          <th scope="col">登録方法</th>
          <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
        </tr></thead>
        <tbody>${rs
          .map((c) => {
            const taxY = costConsumptionTaxYen(c);
            const exY = Number(c.amount || 0);
            const amtInner =
              taxY > 0
                ? `<span class="cost-amt-total">${yen.format(exY)}</span><div class="cost-tax-sub">税込 ${yen.format(exY + taxY)}</div>`
                : `<span class="cost-amt-total">${yen.format(exY)}</span>`;
            const noteRaw = stripCostBucketMarkers(String(c.note || "").trim());
            const dbNo = costDbNumericId(c);
            const noteAttr = escapeHtmlAttr(noteRaw);
            const noteCellInner = noteRaw
              ? `${escapeHtml(noteRaw.slice(0, 60))}${noteRaw.length > 60 ? `…<button type="button" class="cost-note-more-btn" data-cost-note-open="1" data-cost-note-text="${noteAttr}" data-cost-note-subtitle="${escapeHtmlAttr(`No.${dbNo != null ? dbNo : "—"} / ${c._projectName || ""}`)}">全文</button>` : ""}`
              : "—";
            const payLabel = costPayStatusDisplayLabel(c);
            const payHtml = c.paidDate
              ? `<span class="cost-pay-status cs-paid">${escapeHtml(costDateShortDisplay(c.paidDate))}</span>`
              : `<span class="cost-pay-status ${costPayClass(payLabel)}">${escapeHtml(payLabel)}</span>`;
            const remandHtml = c.remandNote
              ? `<div class="cost-remand-badge-wrap"><span class="cost-remand-badge" title="${escapeHtmlAttr(`経理より差し戻し: ${c.remandNote}`)}">差戻し: ${escapeHtml(String(c.remandNote).slice(0, 30))}${String(c.remandNote).length > 30 ? "…" : ""}</span></div>`
              : "";
            return `<tr${c.remandNote ? ' class="cost-row-remanded"' : ""}>
            <td class="cost-table-note-cell" title="${noteAttr}">${noteCellInner}${remandHtml}</td>
            <td class="cost-vendor" title="${escapeHtmlAttr(c.vendor || "")}">${escapeHtml(c.vendor || "—")}</td>
            <td class="cost-td-category"><span class="cost-category-badge">${escapeHtml(c.category)}</span></td>
            <td class="cost-amount cost-amount--tax-view">${amtInner}</td>
            <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
            <td class="cost-mgmt-pay-cell">${payHtml}</td>
            <td class="cost-bkgrp-td-group">${costImportSourceLabel(c)}</td>
            <td class="cost-actions">
              ${c.remandNote ? `<button type="button" class="inline-btn cost-remand-fix-btn" data-cost-remand-fix="${escapeHtmlAttr(c.id)}" title="差し戻された内容を修正します（保存すると差し戻しは解消）">修正する</button>` : ""}
              ${isNoProjectBucketUiId(c._projectId) || costOrderProjectLocked(c) ? "" : `<button type="button" class="inline-btn ghost cost-review-dispute-btn" data-cost-review-dispute="${escapeHtmlAttr(c.id)}" title="この原価は担当案件ではない等、内容が違うときに未割当へ差し戻します">異議</button>`}
              ${costReviewEditDeleteBtnsHtml(c)}
              ${costMoveButtonHtml(c)}
            </td>
          </tr>`;
          })
          .join("")}</tbody>
      </table></div>`;
    return `
    <div class="cost-bkgrp${isOpen ? " cost-bkgrp--open" : ""}">
      <div class="cost-bkgrp-head" data-cost-review-card-toggle="${escapeHtmlAttr(projectId)}" role="button" tabindex="0" aria-expanded="${isOpen ? "true" : "false"}">
        <span class="cost-bkgrp-caret" aria-hidden="true">▶</span>
        <div class="cost-bkgrp-title">
          <span class="cost-bkgrp-name">${escapeHtml(first?._projectName || "")}</span>
          ${metaBits ? `<span class="cost-bkgrp-src">${escapeHtml(metaBits)}</span>` : ""}
          ${remandN ? `<span class="cost-bkgrp-new">差戻し ${remandN}件</span>` : ""}
          <span class="cost-bkgrp-cats">${catChips}</span>
        </div>
        <div class="cost-bkgrp-total">${yen.format(exTotal)}<small>税抜合計 · 税込 ${yen.format(inclSum(rs))} · ${rs.length}行</small></div>
      </div>
      ${bodyHtml}
    </div>`;
  };

  const body =
    cards.length === 0
      ? `<div class="pj-table-area"><table class="cost-table cost-table--global"><tbody><tr><td class="cost-empty">${hasAny ? "該当する原価がありません" : emptyMessage}</td></tr></tbody></table></div>`
      : `<div class="cost-bkgrp-list">${pageCards.map(cardHtml).join("")}</div>`;
  return `
    <div class="cost-review-summary">
      <span>案件 <strong>${cards.length}</strong> 件・原価 <strong>${filteredRows.length}</strong> 行</span>
      <span class="cost-review-summary-total">原価合計（税込） <strong>${yen.format(totalIncl)}</strong></span>
    </div>
    ${body}
    ${cards.length ? costReviewPaginationHtml(cards.length, paged) : ""}`;
}

/** 一覧・案件カードの「編集」「削除」。案件詳細の原価表と同じ条件（10-project-detail.js costEditDeleteAllowedForUser）。
 *  案件に紐づかない原価（販管費/調整用）はサーバが営業の変更を通さないので出さない。差戻し中は「修正する」があるので編集は出さない */
function costReviewEditDeleteBtnsHtml(c) {
  if (isNoProjectBucketUiId(c._projectId) || !costEditDeleteAllowedForUser(c)) return "";
  const key = escapeHtmlAttr(`${c._projectId}:${c.id}`);
  return `${c.remandNote ? "" : `<button type="button" class="inline-btn ghost" data-edit-cost="${key}">編集</button>`}<button type="button" class="inline-btn ghost" data-delete-cost="${key}">削除</button>`;
}

/** 異議申請モーダル */
function renderCostDisputeModal() {
  const m = uiState.costDisputeModal;
  if (!m) return "";
  return `
    <div class="composer-backdrop" data-cost-dispute-close="1"></div>
    <aside class="composer-panel project-create-panel cost-dispute-panel" role="dialog" aria-modal="true" aria-labelledby="cost-dispute-title">
      <div class="composer-head">
        <div><h3 id="cost-dispute-title">原価への異議</h3><p>${escapeHtml(m.projectName || "")}</p></div>
        <button class="inline-btn ghost" data-cost-dispute-close="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="prompt-copy">「${escapeHtml(m.costLabel || "")}」を案件から外して未割当へ差し戻します。理由を入力してください（CS に届きます）。</p>
        <label class="cl-form-group">
          <span class="cl-form-label">理由（必須）</span>
          <textarea class="pj-input cost-dispute-reason" rows="3" data-cost-dispute-reason placeholder="例）この案件の費用ではない／金額が違う">${escapeHtml(m.reason || "")}</textarea>
        </label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-cost-dispute-close="1">キャンセル</button>
        <button class="inline-btn" data-cost-dispute-submit="1" ${m.saving ? "disabled" : ""}>${m.saving ? "送信中…" : "異議を申請する"}</button>
      </div>
    </aside>`;
}

/** 発注名・備考の全文モーダル（一覧で切れて読めない長文を開いて確認する） */
function renderCostNoteModal() {
  const m = uiState.costNoteModal;
  if (!m) return "";
  return `
    <div class="composer-backdrop" data-cost-note-close="1"></div>
    <aside class="composer-panel project-create-panel cost-note-modal" role="dialog" aria-modal="true" aria-labelledby="cost-note-title">
      <div class="composer-head">
        <div><h3 id="cost-note-title">発注名・備考</h3>${m.subtitle ? `<p>${escapeHtml(m.subtitle)}</p>` : ""}</div>
        <button class="inline-btn ghost" data-cost-note-close="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="cost-note-modal-text">${escapeHtml(m.text || "")}</p>
      </div>
    </aside>`;
}

/** 異議申請の送信: サーバーで未割当へ差し戻し → ローカル state も同期 */
async function submitCostDispute() {
  const m = uiState.costDisputeModal;
  if (!m || m.saving) return;
  const reason = String(m.reason || "").trim();
  if (!reason) {
    showToast("理由を入力してください", { variant: "error" });
    return;
  }
  const pl = findCostPlacementByUiId(m.costUiId);
  const dbId = costDbNumericId({ id: m.costUiId });
  if (!pl || dbId == null) {
    showToast("原価が見つかりません。再読み込みしてください", { variant: "error" });
    return;
  }
  m.saving = true;
  render();
  const res = await apiFetch("/api/cost-disputes", { method: "POST", body: { costId: dbId, reason } });
  if (!res.ok) {
    m.saving = false;
    showToast(typeof res.data?.error === "string" ? res.data.error : "異議の申請に失敗しました", { variant: "error" });
    render();
    return;
  }
  // ローカル同期: 案件から外して未割当（異議付き）へ
  const cost = pl.cost;
  const fromProject = pl.project || null;
  if (fromProject && Array.isArray(fromProject.costs)) {
    fromProject.costs = fromProject.costs.filter((c) => c !== cost);
  }
  cost.disputeNote = reason;
  cost.disputedByDbId = currentUser?.dbId ?? null;
  cost.disputedAt = new Date().toISOString();
  if (fromProject?.dbId != null) cost.disputeProjectDbId = fromProject.dbId;
  delete cost.delegatedSalesUserDbId;
  ensureOrphanCosts().push(cost);
  uiState.costDisputeModal = null;
  showToast("異議を申請しました。未割当へ差し戻し、CS に通知されます");
  render();
}

// ── イベント（独立リスナー・原価割当ページのみ） ──
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (currentRoute().page !== "cost-allocation" && !uiState.costDisputeModal && !uiState.costNoteModal) return;

  const noteOpen = t.closest("[data-cost-note-open]");
  if (noteOpen instanceof HTMLElement) {
    uiState.costNoteModal = {
      text: noteOpen.getAttribute("data-cost-note-text") || "",
      subtitle: noteOpen.getAttribute("data-cost-note-subtitle") || ""
    };
    render();
    return;
  }
  if (t.closest("[data-cost-note-close]")) {
    uiState.costNoteModal = null;
    render();
    return;
  }

  const ddToggle = t.closest("[data-cost-review-dd-toggle]");
  if (ddToggle instanceof HTMLElement) {
    const st = ensureCostReviewState();
    const kind = ddToggle.getAttribute("data-cost-review-dd-toggle");
    if (kind === "project") {
      st.projMenuOpen = !st.projMenuOpen;
      st.clientMenuOpen = false;
    } else {
      st.clientMenuOpen = !st.clientMenuOpen;
      st.projMenuOpen = false;
    }
    render();
    return;
  }
  if (t.closest("[data-cost-review-dd-close]")) {
    const st = ensureCostReviewState();
    st.projMenuOpen = false;
    st.clientMenuOpen = false;
    render();
    return;
  }
  // 表示形式トグル（案件カード⇄一覧）
  const viewBtn = t.closest("[data-cost-review-view]");
  if (viewBtn instanceof HTMLElement) {
    const st = ensureCostReviewState();
    const mode = viewBtn.getAttribute("data-cost-review-view");
    if ((mode === "cards" || mode === "table") && st.viewMode !== mode) {
      st.viewMode = mode;
      st.page = 1; // ページの意味（行⇄カード）が変わるため先頭へ
      render();
    }
    return;
  }
  // 案件カードの開閉（カード内のボタン・リンク操作では開閉しない）
  const cardHead = t.closest("[data-cost-review-card-toggle]");
  if (cardHead instanceof HTMLElement) {
    if (t.closest("button, select, a, input, textarea, .sd-select")) return;
    const pid = cardHead.getAttribute("data-cost-review-card-toggle") || "";
    if (!uiState.costReviewOpenProj || typeof uiState.costReviewOpenProj !== "object") uiState.costReviewOpenProj = {};
    uiState.costReviewOpenProj[pid] = !uiState.costReviewOpenProj[pid];
    render();
    return;
  }
  if (t.closest("[data-cost-review-clear]")) {
    const cur = ensureCostReviewState();
    // 未割当ビューでは発注先・並び替えも同じ1行に並ぶためまとめてクリアする。
    // 照会ビュー（営業ページの割当依頼キューなど）では別ツールバーなので触らない
    if (cur.assigneeId === COST_REVIEW_UNASSIGNED_VALUE) {
      // 表示形式（物件グループ／一覧）は絞り込みではないので残す
      uiState.costAllocFilter = { vendor: "", viewMode: ensureCostAllocListState().flt.viewMode };
      uiState.costAllocSort = { key: "default", dir: "desc" };
    }
    uiState.costReview = emptyCostReviewState(cur.perPage);
    render();
    return;
  }
  // 並び替え（照会一覧）: 同じ列をもう一度押すと昇順⇄降順、別の列なら昇順から
  const reviewSortTh = t.closest("[data-cost-review-sort]");
  if (reviewSortTh instanceof HTMLElement) {
    const st = ensureCostReviewState();
    const key = reviewSortTh.getAttribute("data-cost-review-sort") || "";
    if (st.sortKey === key) st.sortDir = st.sortDir === "asc" ? "desc" : "asc";
    else {
      st.sortKey = key;
      st.sortDir = "asc";
    }
    st.page = 1;
    render();
    return;
  }
  // 並び替え（未割当キュー）
  const allocSortTh = t.closest("[data-cost-alloc-sort]");
  if (allocSortTh instanceof HTMLElement) {
    const { srt } = ensureCostAllocListState();
    const key = allocSortTh.getAttribute("data-cost-alloc-sort") || "";
    if (srt.key === key) srt.dir = srt.dir === "asc" ? "desc" : "asc";
    else {
      srt.key = key;
      srt.dir = "asc";
    }
    render();
    return;
  }
  if (t.closest("[data-cost-alloc-sort-dir]")) {
    const { srt } = ensureCostAllocListState();
    srt.dir = srt.dir === "asc" ? "desc" : "asc";
    render();
    return;
  }
  if (t.closest("[data-cost-alloc-filter-clear]")) {
    // 表示形式（物件グループ／一覧）は絞り込みではないので残す
    uiState.costAllocFilter = { vendor: "", viewMode: ensureCostAllocListState().flt.viewMode };
    uiState.costAllocSort = { key: "default", dir: "desc" };
    render();
    return;
  }
  const pageBtn = t.closest("[data-cost-review-page]");
  if (pageBtn instanceof HTMLElement) {
    const st = ensureCostReviewState();
    const dir = pageBtn.getAttribute("data-cost-review-page");
    if (dir === "prev") st.page = Math.max(1, (Number(st.page) || 1) - 1);
    else if (dir === "next") st.page = (Number(st.page) || 1) + 1;
    render();
    return;
  }
  const dispBtn = t.closest("[data-cost-review-dispute]");
  if (dispBtn instanceof HTMLElement) {
    const costUiId = dispBtn.getAttribute("data-cost-review-dispute") || "";
    const pl = findCostPlacementByUiId(costUiId);
    if (!pl) return;
    uiState.costDisputeModal = {
      costUiId,
      projectName: pl.project?.name || "",
      costLabel: `${stripCostBucketMarkers(String(pl.cost.note || "")).slice(0, 40) || pl.cost.vendor || "原価"}（${yen.format(Number(pl.cost.amount || 0))}）`,
      reason: "",
      saving: false
    };
    render();
    return;
  }
  if (t.closest("[data-cost-dispute-close]")) {
    if (uiState.costDisputeModal?.saving) return;
    uiState.costDisputeModal = null;
    render();
    return;
  }
  if (t.closest("[data-cost-dispute-submit]")) {
    void submitCostDispute();
    return;
  }
  if (t.closest("[data-cost-box-assign]")) {
    void submitCostBoxAssign();
    return;
  }
});

document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) && !(t instanceof HTMLSelectElement)) return;
  if (currentRoute().page !== "cost-allocation") return;
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-review-month")) {
    const st = ensureCostReviewState();
    st.month = t.value || "";
    st.page = 1;
    if (deferRenderWhileMonthPicking(t)) return;
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-review-assignee")) {
    const st = ensureCostReviewState();
    st.assigneeId = t.value || "";
    // 案件・顧客の絞り込みは担当者ごとのスコープなので、担当者を変えたら一緒にクリアする
    // （残したままだと選択肢から消えて見えなくなるのに絞り込みだけ効き続け、0件の原因が分からなくなる）
    st.projectIds = [];
    st.clientNames = [];
    st.page = 1;
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-review-vendor")) {
    const st = ensureCostReviewState();
    st.vendor = t.value || "";
    st.page = 1;
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-alloc-filter-vendor")) {
    ensureCostAllocListState().flt.vendor = t.value || "";
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-alloc-sort-key")) {
    ensureCostAllocListState().srt.key = t.value || "default";
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-review-per-page")) {
    const st = ensureCostReviewState();
    st.perPage = Number(t.value) || 50;
    st.page = 1;
    render();
    return;
  }
  if (t instanceof HTMLInputElement && t.dataset.costReviewPick) {
    const st = ensureCostReviewState();
    const kind = t.dataset.costReviewPick;
    const key = kind === "project" ? "projectIds" : "clientNames";
    const list = st[key];
    const v = t.value;
    const idx = list.indexOf(v);
    if (t.checked && idx < 0) list.push(v);
    if (!t.checked && idx >= 0) list.splice(idx, 1);
    st.page = 1;
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-box-owner-pick")) {
    ensureCostBoxPickState().salesOwnerId = t.value || "";
    render();
    return;
  }
});

/** 選択中の未割当原価を、指定営業の今期の原価箱へまとめて割当（箱が無ければ find-or-create）。
 *  期は常に今期固定（過去期に付けると粗利が遡って動くため選ばせない）。 */
async function submitCostBoxAssign() {
  const st = ensureCostBoxPickState();
  if (st.saving) return;
  if (!st.salesOwnerId) {
    showToast("原価箱の担当営業を選んでください", { variant: "error" });
    return;
  }
  pruneCostAllocSelectionToValid();
  const sel = uiState.costAllocSelected || {};
  const keys = Object.keys(sel).filter((k) => sel[k]);
  if (!keys.length) {
    showToast("行を選択してください", { variant: "error" });
    return;
  }
  st.saving = true;
  render();
  const boxResult = await ensureCostBoxProjectUiId(st.salesOwnerId, costBoxFiscalYearStartYear());
  if (!boxResult.ok) {
    st.saving = false;
    showToast(boxResult.error, { variant: "error" });
    render();
    return;
  }
  const boxUiId = boxResult.uiId;
  let ok = 0;
  let fail = 0;
  for (const rowKey of keys) {
    const colon = rowKey.lastIndexOf(":");
    const costIdUi = colon >= 0 ? rowKey.slice(colon + 1) : "";
    if (!costIdUi) continue;
    const pl = findCostPlacementByUiId(costIdUi);
    const c = pl?.cost;
    if (!c || !pl.orphan) {
      fail++;
      continue;
    }
    try {
      await persistCostToServerUpdate(
        costIdUi,
        {
          category: c.category,
          vendor: c.vendor,
          amount: Number(c.amount || 0),
          consumptionTax: costConsumptionTaxYen(c),
          targetMonth: c.targetMonth,
          payStatus: c.payStatus,
          dueDate: c.dueDate || "",
          note: c.note || ""
        },
        boxUiId
      );
      ok++;
    } catch {
      fail++;
    }
  }
  uiState.costAllocSelected = {};
  pruneCostAllocSelectionToValid();
  st.saving = false;
  if (fail && ok) showToastError(`原価箱へ割当: 成功 ${ok} 件・失敗 ${fail} 件`);
  else if (fail) showToastError(`原価箱への割当に失敗しました（${fail} 件）`);
  else showToast(ok > 1 ? `原価箱に入れました（${ok}件）。` : "原価箱に入れました。");
  render();
}

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (t instanceof HTMLTextAreaElement && t.hasAttribute("data-cost-dispute-reason") && uiState.costDisputeModal) {
    uiState.costDisputeModal.reason = t.value;
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-review-dd-search")) {
    // 再描画せずパネル内の選択肢だけを絞る（フォーカス・IME を壊さない）。値は再描画時の復元用に保持
    const st = ensureCostReviewState();
    if (t.getAttribute("data-cost-review-dd-search") === "project") st.projQuery = t.value;
    else st.clientQuery = t.value;
    const q = t.value.trim().toLowerCase();
    const panel = t.closest(".cost-review-dd-panel");
    if (panel) {
      panel.querySelectorAll("label.cl-inline-check").forEach((l) => {
        l.style.display = !q || l.textContent.toLowerCase().includes(q) ? "" : "none";
      });
    }
    return;
  }
});
// ────────────────────────────────────────────────────────────────
// 請求書の郵送（CPaaS NOW 印刷発送）
// 書類フッターの「郵送する」ボタン → 宛先確認モーダル → /api/doc-mail へ依頼。
// 宛先・PDFはすべてサーバー側（クライアントマスタ＋保存済み明細）から組み立てるため、
// ここでは表示と確認だけを行う。イベントは 16 の巨大ハンドラに足さず独立リスナーで処理。
// ────────────────────────────────────────────────────────────────

const DOC_POSTAL_STATUS_LABEL = {
  // サーバーが郵送サービスへ依頼する直前に確保する予約行（接続失敗で受付可否が不明な場合もこのまま残る）
  pending: "依頼中（受付確認待ち）",
  accepted: "受付済み",
  dispatching: "印刷手配中",
  dispatched: "印刷手配中",
  shipped: "発送済み",
  non_delivered: "不達（返送）",
  failed: "失敗",
};

function docPostalStatusLabel(item) {
  const base = DOC_POSTAL_STATUS_LABEL[item.status] || item.status || "—";
  if (item.status === "shipped" && item.shippedDate) return `${base}（${item.shippedDate}）`;
  if (item.status === "non_delivered" && item.nonDeliveryDate) return `${base}（${item.nonDeliveryDate}）`;
  return base;
}

let _docPostalModalProjectId = "";

/** モーダルを閉じた時、フッターの郵送ボタン状態を最新化する（郵送準備中→送信直後の表示反映）。 */
function closeDocPostalModal() {
  document.getElementById("docPostalOverlay")?.remove();
  if (!_docPostalModalProjectId) return;
  const pid = _docPostalModalProjectId;
  _docPostalModalProjectId = "";
  delete uiState.docMailStatusLoadedFor[pid];
  const p = projects.find((x) => x.id === pid);
  if (p) void ensureDocMailStatusLoaded(p);
}

async function loadDocPostalHistory(dbId) {
  const listEl = document.getElementById("docPostalHistory");
  if (!listEl) return;
  const { ok, data } = await apiFetch(`/api/doc-mail?projectId=${dbId}`);
  const el = document.getElementById("docPostalHistory");
  if (!el) return; // 取得中に閉じられた
  if (!ok) {
    el.innerHTML = `<p class="doc-postal-empty">郵送履歴を取得できませんでした</p>`;
    return;
  }
  const items = data.items || [];
  if (!items.length) {
    el.innerHTML = `<p class="doc-postal-empty">この案件の郵送履歴はまだありません</p>`;
    return;
  }
  el.innerHTML = items.map((it) => {
    const d = it.createdAt ? new Date(it.createdAt) : null;
    const dateStr = d ? `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}` : "—";
    const failed = it.status === "failed" || it.status === "non_delivered";
    return `<div class="doc-postal-hist-row${failed ? " doc-postal-hist-row--failed" : ""}">
      <span class="doc-postal-hist-date">${dateStr}</span>
      <span class="doc-postal-hist-status">${escapeHtml(docPostalStatusLabel(it))}</span>
      <span class="doc-postal-hist-amount">${it.totalYen != null ? yen.format(it.totalYen) : ""}</span>
      ${it.errorMessage ? `<span class="doc-postal-hist-error" title="${escapeHtmlAttr(it.errorMessage)}">${escapeHtml(it.errorMessage)}</span>` : ""}
    </div>`;
  }).join("");
}

/** 郵送の確認モーダル。docTypes は同封する書類（未指定なら請求書1枚）。 */
function openDocPostalModal(projectId, docTypes) {
  const p = projects.find((x) => x.id === projectId);
  if (!p) return;
  const types = (Array.isArray(docTypes) && docTypes.length ? docTypes : ["invoice"]).filter(
    (t) => Object.prototype.hasOwnProperty.call(DOC_MAIL_TYPE_LABELS, t)
  );
  if (!types.length) types.push("invoice");
  const typesLabel = docMailTypesLabel(types);
  if (!p.dbId) {
    showToast("この案件はまだサーバーに保存されていません");
    return;
  }
  const client = clients.find((c) => c.id === p.clientId);
  // 郵送先は案件で選んだ支社があればその住所を優先（実際にサーバーが郵送する宛先と同じロジック）
  const branchName = String(p.clientBranchName || "").trim();
  const recipientAddr = clientRecipientAddress(p);
  const zipRaw = String(recipientAddr.postalCode || "").replace(/[-\s]/g, "");
  const addr = `${recipientAddr.prefecture || ""}${recipientAddr.address || ""}`.trim();
  const building = String(recipientAddr.building || "").trim();
  const clientName = client?.name || "";
  const honorific = resolveRecipientHonorific(client?.honorific);
  const addressOk = /^\d{7}$/.test(zipRaw) && addr && clientName;
  // 支社が選択されていてもマスタに一致行が無ければ（改名・アーカイブ済み等）クライアントマスタへフォールバックしているので、
  // ラベルも実際に使う住所の出どころ（source）で判定する（誤って「支社の住所」と表示しない）
  const addrLabel = recipientAddr.source === "branch" ? `宛先（支社「${escapeHtml(branchName)}」の住所）` : "宛先（クライアントマスタの住所）";

  closeDocPostalModal();
  _docPostalModalProjectId = projectId;
  const overlay = document.createElement("div");
  overlay.id = "docPostalOverlay";
  overlay.className = "doc-postal-overlay";
  overlay.innerHTML = `
    <div class="doc-postal-modal" role="dialog" aria-label="書類を郵送">
      <div class="doc-postal-head">
        <h3>${escapeHtml(typesLabel)}を郵送する</h3>
        <button type="button" class="doc-postal-close" data-doc-postal-close="1" aria-label="閉じる">×</button>
      </div>
      <div class="doc-postal-body">
        <p class="doc-postal-lead">保存済みの${escapeHtml(typesLabel)}をA4カラー印刷し、${types.length > 1 ? "1通に同封して" : ""}封書（普通郵便）で下記宛先へ発送します。</p>
        <div class="doc-postal-addr">
          <div class="doc-postal-addr-label">${addrLabel}</div>
          ${addressOk ? `
            <div class="doc-postal-addr-zip">〒${escapeHtml(zipRaw.slice(0, 3))}-${escapeHtml(zipRaw.slice(3))}</div>
            <div class="doc-postal-addr-line">${escapeHtml(addr)}${building ? `<br/>${escapeHtml(building)}` : ""}</div>
            <div class="doc-postal-addr-name">${escapeHtml(clientName)} ${escapeHtml(honorific)}</div>
          ` : `
            <p class="doc-postal-warn">クライアントの郵便番号・住所が未登録のため郵送できません。<br/>取引先一覧でクライアントの住所を登録してから再度お試しください。</p>
          `}
        </div>
        <ul class="doc-postal-notes doc-postal-notes--warn">
          <li>当日19時までの受付分が翌営業日に発送されます（日祝・長期休暇を除く）。</li>
          <li><strong>一度依頼すると取り消せません。</strong>宛先と請求内容（確認プレビュー）をご確認ください。</li>
        </ul>
        <label class="doc-postal-confirm">
          <input type="checkbox" data-doc-postal-confirm="1" />
          内容を確認しました
        </label>
        <div class="doc-postal-hist">
          <div class="doc-postal-addr-label">この案件の郵送履歴</div>
          <div id="docPostalHistory"><p class="doc-postal-empty">読み込み中…</p></div>
        </div>
      </div>
      <div class="doc-postal-foot">
        <button type="button" class="doc-preview-footer-btn" data-doc-postal-close="1">キャンセル</button>
        <button type="button" class="doc-preview-footer-btn doc-postal-send-btn" data-doc-postal-send="${escapeHtmlAttr(projectId)}" data-doc-postal-types="${escapeHtmlAttr(types.join(","))}" data-doc-postal-address-ok="${addressOk ? "1" : "0"}" disabled>郵送を依頼する</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  void loadDocPostalHistory(p.dbId);
}

async function submitDocPostal(projectId, btn, docTypes) {
  const p = projects.find((x) => x.id === projectId);
  if (!p?.dbId) return;
  btn.disabled = true;
  const prevText = btn.textContent;
  btn.textContent = "依頼中…";
  // 書類はサーバーの保存済み内容から作られるため、未保存の請求項目・支払期限・備考を先に確定させる
  // （郵送は取り消せないので、古い金額で発送しない）
  const linesSave = await flushDocBillingLinesBeforeSend(p.id, docTypes.length ? docTypes : ["invoice"]);
  if (!linesSave.ok) {
    showToastError(linesSave.sealReset ? linesSave.error : `請求項目を保存できなかったため、郵送依頼を中止しました：${linesSave.error}`);
    btn.disabled = false;
    btn.textContent = prevText;
    return;
  }
  const metaSave = await flushDocMetaSaveToServer(p.id, docTypes.length ? docTypes : ["invoice"]);
  if (!metaSave.ok) {
    showToastError(`支払期限・備考を保存できなかったため、郵送依頼を中止しました：${metaSave.error}`);
    btn.disabled = false;
    btn.textContent = prevText;
    return;
  }
  const { ok, data } = await apiFetch("/api/doc-mail", {
    method: "POST",
    body: { projectId: p.dbId, docTypes: docTypes.length ? docTypes : ["invoice"] },
  });
  if (ok) {
    // 受付後は確認チェックを付け直しても再度押せないようにする（二重発送防止。change ハンドラが参照）
    btn.dataset.docPostalSent = "1";
    // サーバーが請求ステータスを進めたら手元の案件にも反映する（メール送付と同じ）
    if (data.invoiceStatus) p.invoiceStatus = data.invoiceStatus;
    if (Array.isArray(data.issueHistoryErrors) && data.issueHistoryErrors.length) {
      showToast(`郵送は受け付けましたが、発行履歴に記録できませんでした（${data.issueHistoryErrors.join(" / ")}）`);
    } else {
      showToast(`郵送を受け付けました（受付番号 ${data.deliveryOrderId ?? "—"}）`);
    }
    btn.textContent = "受付済み";
    void loadDocPostalHistory(p.dbId);
    // フッターの「郵送する」ボタンにも受付状態を反映（準備中→発送完了で自動的に戻る）
    delete uiState.docMailStatusLoadedFor[p.id];
    void ensureDocMailStatusLoaded(p);
  } else {
    showToastError(`郵送依頼に失敗しました：${data.error || "エラー"}`);
    btn.disabled = false;
    btn.textContent = prevText;
  }
}

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || target.dataset.docPostalConfirm !== "1") return;
  const overlay = document.getElementById("docPostalOverlay");
  const sendBtn = overlay?.querySelector(".doc-postal-send-btn");
  if (sendBtn instanceof HTMLButtonElement) {
    sendBtn.disabled = !target.checked || sendBtn.dataset.docPostalAddressOk !== "1" || sendBtn.dataset.docPostalSent === "1";
  }
});

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  // 「郵送する」ボタン自体の処理は 15h-doc-mail-compose.js（書類選択メニュー）が持つ。
  // ここは選択後に開くモーダル内の操作だけを扱う。
  const sendEl = target.closest("[data-doc-postal-send]");
  if (sendEl instanceof HTMLElement) {
    e.preventDefault();
    const types = (sendEl.getAttribute("data-doc-postal-types") || "invoice").split(",").filter(Boolean);
    void submitDocPostal(sendEl.getAttribute("data-doc-postal-send") || "", sendEl, types);
    return;
  }
  if (target.closest("[data-doc-postal-close]") || (target instanceof HTMLElement && target.id === "docPostalOverlay")) {
    closeDocPostalModal();
  }
});
// ────────────────────────────────────────────────────────────────
// 毎月請求の請求書（案件）複製:
//  - 「毎月請求」チェック（data-recurring-billing-toggle）: 案件詳細の基本情報（請求・納期）と
//    書類作成・編集（請求書タブ）の2箇所に置く。チェックした案件は自動作成の対象になる
//    （fp_projects.recurring_billing。即時保存＝基本情報の「保存」ボタンとは独立）。
//  - 案件一覧の「自動作成設定」ボタン（data-recurring-billing-auto-settings-open）
//    → ログインユーザーごとに「毎月◯日に自動作成するか」を設定するモーダルを開く
//      （api/recurring-billing-settings.js に保存）。実際の複製は Vercel Cron
//      （api/recurring-billing-auto.js、毎日1回）が担当営業（sales_owner_id）本人の分だけ実行する。
//      手動一括コピーの導線は廃止（Slack からの実行は api/_slackActions.js 側に残る）。
// 新機能のイベントは 16 の巨大ハンドラに足さず、本ファイルの独立リスナーで処理する
// （15a〜15c と同方式）。モーダルは 15i-mail-template-settings.js と同じく render()/uiState の
// 描画サイクルには乗らず自前のDOMで完結させる（fp-mts-* の既存スタイルを流用）。
// ────────────────────────────────────────────────────────────────

let recurringBillingAutoState = null;
let recurringBillingAutoOverlayEl = null;
let recurringBillingAutoWantOpen = false;

function recurringBillingAutoDayOptionsHtml(selectedDay) {
  const opts = [];
  for (let d = 1; d <= 28; d += 1) {
    opts.push(`<option value="${d}"${d === selectedDay ? " selected" : ""}>${d}日</option>`);
  }
  return opts.join("");
}

function renderRecurringBillingAutoOverlay() {
  const st = recurringBillingAutoState;
  if (!recurringBillingAutoOverlayEl || !st) return;
  const disabled = st.saving ? " disabled" : "";
  recurringBillingAutoOverlayEl.innerHTML = `
    <div class="fp-mts-backdrop" data-rba-close="1"></div>
    <div class="fp-mts-panel" style="max-width:420px;">
      <div class="fp-mts-head">
        <div>
          <p class="fp-mts-eyebrow">Recurring billing</p>
          <h2>毎月請求 自動作成設定</h2>
        </div>
        <button type="button" class="fp-mts-close" data-rba-close="1" aria-label="閉じる">×</button>
      </div>
      <p class="fp-mts-lead">「毎月請求」にチェックした自分の担当案件を、毎月指定した日に自動で今月分としてコピーします。</p>
      ${st.loading
        ? `<p class="fp-mts-empty" style="padding:0 22px 20px;">読み込み中…</p>`
        : `<div class="fp-mts-form" style="padding:16px 22px 20px;">
            <label class="fp-mts-field" style="flex-direction:row;align-items:center;gap:8px;">
              <input type="checkbox" data-rba-field="enabled"${st.draft.enabled ? " checked" : ""}${disabled} />
              <span style="font-size:13px;font-weight:600;color:var(--text);">自動作成を有効にする</span>
            </label>
            <label class="fp-mts-field">
              <span>毎月</span>
              <select class="fp-mts-input" data-search-dropdown-off="1" data-rba-field="dayOfMonth"${disabled}>
                ${recurringBillingAutoDayOptionsHtml(st.draft.dayOfMonth)}
              </select>
              <small>29〜31日は無い月があるため選べません（28日までで指定してください）。</small>
            </label>
            <div class="fp-mts-actions">
              <button type="button" class="fp-mts-save"${disabled} data-rba-save="1">${st.saving ? "保存中…" : "保存"}</button>
            </div>
          </div>`}
    </div>`;
}

async function loadRecurringBillingAutoSettings() {
  const st = recurringBillingAutoState;
  if (!st) return;
  st.loading = true;
  renderRecurringBillingAutoOverlay();
  const { ok, data } = await apiFetch("/api/recurring-billing-settings");
  st.loading = false;
  st.draft = ok && data.ok ? { enabled: data.enabled === true, dayOfMonth: Number(data.dayOfMonth) || 1 } : { enabled: false, dayOfMonth: 1 };
  if (!ok || !data.ok) showToast("自動作成設定を読み込めませんでした");
  renderRecurringBillingAutoOverlay();
}

function openRecurringBillingAutoSettings() {
  recurringBillingAutoWantOpen = true;
  if (!recurringBillingAutoOverlayEl) {
    recurringBillingAutoOverlayEl = document.createElement("div");
    recurringBillingAutoOverlayEl.className = "fp-mts-overlay";
    recurringBillingAutoOverlayEl.setAttribute("role", "dialog");
    recurringBillingAutoOverlayEl.setAttribute("aria-modal", "true");
    recurringBillingAutoOverlayEl.setAttribute("aria-label", "毎月請求 自動作成設定");
    recurringBillingAutoOverlayEl.hidden = true;
    document.body.appendChild(recurringBillingAutoOverlayEl);
  }
  recurringBillingAutoState = { loading: true, saving: false, draft: { enabled: false, dayOfMonth: 1 } };
  renderRecurringBillingAutoOverlay();
  recurringBillingAutoOverlayEl.hidden = false;
  requestAnimationFrame(() => recurringBillingAutoOverlayEl && recurringBillingAutoOverlayEl.classList.add("is-open"));
  document.body.classList.add("fp-mts-lock-scroll");
  void loadRecurringBillingAutoSettings();
}

function closeRecurringBillingAutoSettings() {
  if (!recurringBillingAutoOverlayEl) return;
  recurringBillingAutoWantOpen = false;
  recurringBillingAutoOverlayEl.classList.remove("is-open");
  document.body.classList.remove("fp-mts-lock-scroll");
  window.setTimeout(() => {
    if (recurringBillingAutoOverlayEl && !recurringBillingAutoWantOpen) recurringBillingAutoOverlayEl.hidden = true;
  }, 160);
}

async function saveRecurringBillingAutoSettings() {
  const st = recurringBillingAutoState;
  if (!st || st.saving) return;
  st.saving = true;
  renderRecurringBillingAutoOverlay();
  const { ok, data } = await apiFetch("/api/recurring-billing-settings", {
    method: "PUT",
    body: { enabled: st.draft.enabled, dayOfMonth: st.draft.dayOfMonth }
  });
  st.saving = false;
  if (!ok || !data.ok) {
    renderRecurringBillingAutoOverlay();
    showToastError(`保存できませんでした：${data.error || "エラー"}`);
    return;
  }
  renderRecurringBillingAutoOverlay();
  showToast("自動作成設定を保存しました");
}

// ── 案件一覧: 「自動作成設定」ボタン ──
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;

  if (target.closest("[data-recurring-billing-auto-settings-open]")) {
    openRecurringBillingAutoSettings();
    return;
  }

  if (!recurringBillingAutoOverlayEl || recurringBillingAutoOverlayEl.hidden) return;
  if (target.closest("[data-rba-close]")) {
    closeRecurringBillingAutoSettings();
    return;
  }
  if (target.closest("[data-rba-save]")) {
    void saveRecurringBillingAutoSettings();
  }
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const field = target.getAttribute("data-rba-field");
  if (!field || !recurringBillingAutoState?.draft) return;
  if (field === "enabled") {
    recurringBillingAutoState.draft.enabled = target.checked === true;
  } else if (field === "dayOfMonth") {
    recurringBillingAutoState.draft.dayOfMonth = Number(target.value) || 1;
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && recurringBillingAutoOverlayEl && !recurringBillingAutoOverlayEl.hidden) {
    closeRecurringBillingAutoSettings();
  }
});

// ── 書類作成・編集（請求書タブ）: 「毎月請求」チェックの保存 ──
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (!t.hasAttribute("data-recurring-billing-toggle")) return;
  const pid = t.getAttribute("data-recurring-billing-toggle") || "";
  const p = projects.find((x) => x.id === pid);
  if (!p) return;
  const next = t.checked === true;
  const prev = p.recurringBilling === true;
  p.recurringBilling = next;
  void (async () => {
    const { ok } = await apiFetch("/api/projects", {
      method: "PUT",
      body: { id: p.dbId, recurringBilling: next }
    });
    if (!ok) {
      p.recurringBilling = prev;
      showToastError("毎月請求の設定を保存できませんでした");
      render();
      return;
    }
    showToast(next ? "毎月請求にしました" : "毎月請求を解除しました");
  })();
});

// ── 案件詳細・基本情報: 請求タイミングの「毎月請求」⇔ recurring_billing の同期 ──
// 「毎月請求」を選ぶと毎月請求フラグON（翌月の一括複製対象）、他へ変えるとOFF。
// billingTiming 自体は従来どおり data-pd-field の汎用ハンドラがドラフトへ取り込む。
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (t.name !== "pd-billing-timing") return;
  const r = currentRoute();
  if (r.page !== "project-detail") return;
  const p = projects.find((x) => String(x.id) === String(r.id));
  if (!p) return;
  const next = t.value === "毎月請求";
  const prev = p.recurringBilling === true;
  if (prev === next) return;
  p.recurringBilling = next;
  void (async () => {
    const { ok } = await apiFetch("/api/projects", {
      method: "PUT",
      body: { id: p.dbId, recurringBilling: next }
    });
    if (!ok) {
      p.recurringBilling = prev;
      showToastError("毎月請求の設定を保存できませんでした");
      render();
      return;
    }
    showToast(next ? "毎月請求にしました" : "毎月請求を解除しました");
  })();
});
// ────────────────────────────────────────────────────────────────
// 予算マスタ（予算設定タブ・budget-admin）：明示保存まわりの独立ハンドラ
// セル編集のたびに render()（全再描画）すると blur でフォーカスが失われ、
// Tab移動不能・行追加/削除ボタンの click 不発になるため、編集はメモリ上の
// ドラフト（budgetDraftMemoryStore）に溜め、「保存」ボタンでサーバーへ送る。
// ────────────────────────────────────────────────────────────────

/** 未保存フラグを立て、再描画なしで「未保存の変更があります」表示を出す */
function markBudgetMasterDirty() {
  uiState.budgetMasterDirty = true;
  document.querySelectorAll("[data-budget-master-dirty-note]").forEach((el) => {
    if (el instanceof HTMLElement) el.style.display = "";
  });
}

/** 編集したセルが属する行の「年度合計」セルを再描画なしで更新する */
function updateBudgetMasterRowTotalCell(inputEl) {
  const tr = inputEl instanceof HTMLElement ? inputEl.closest("tr") : null;
  const totalCell = tr ? tr.querySelector("[data-budget-row-total]") : null;
  if (!totalCell) return;
  let sum = 0;
  tr.querySelectorAll("input[data-budget-row-field]").forEach((inp) => {
    if (BUDGET_MONTH_KEYS.includes(inp.dataset.budgetRowField)) sum += parseBudgetNumberInput(inp.value);
  });
  totalCell.textContent = `¥${sum.toLocaleString("ja-JP")}`;
}

let budgetMasterSaveInFlight = false;

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target.closest("[data-budget-master-save]") : null;
  if (!target) return;
  const page = currentRoute().page;
  // 予算設定タブは2026-08-24に予算進捗(budget-progress)から経営ダッシュボード(management)へ移った
  if (page !== "budget-admin" && page !== "management") return;
  if (budgetMasterSaveInFlight) return;
  budgetMasterSaveInFlight = true;
  if (target instanceof HTMLButtonElement) target.disabled = true;
  void (async () => {
    try {
      const ok = await persistBudgetMasterRowsToServer();
      if (ok) {
        uiState.budgetMasterDirty = false;
        showToast("予算マスタを保存しました");
        render();
      } else {
        showToastError("保存に失敗しました。通信状態を確認して再度お試しください");
      }
    } finally {
      budgetMasterSaveInFlight = false;
      if (target instanceof HTMLButtonElement) target.disabled = false;
    }
  })();
});

// 未保存のまま閉じる/リロードでドラフトが消えるのを防ぐ確認ダイアログ
window.addEventListener("beforeunload", (e) => {
  if (!uiState.budgetMasterDirty) return;
  e.preventDefault();
  e.returnValue = "";
});
// ────────────────────────────────────────────────────────────────
// 先方担当者（クライアント・パートナー）のマスタ。登録・編集は取引先詳細の「担当者・支社」タブ
// （15k-client-detail-people.js）と案件詳細の＋から行う。取引先一覧の「担当者」タブは 2026-10-28 に廃止。
// 登録・編集・削除は申請不要（クライアント本体の新規/削除と違い営業も可）。
// データは /api/client-contacts（fp_client_contacts / fp_partner_contacts）。必要になった時に遅延ロード。
// ────────────────────────────────────────────────────────────────

/** 初回表示時に一覧を取得（取得済み・取得中は何もしない） */
function ensureClientContactsLoaded() {
  const st = uiState.clientContacts;
  if (st.rows !== null || st.loading || !authToken) return;
  st.loading = true;
  st.loadError = false;
  void apiFetch("/api/client-contacts").then(({ ok, data }) => {
    st.loading = false;
    if (ok && Array.isArray(data.contacts)) {
      st.rows = data.contacts;
      // パートナーの担当者（取引先詳細の「担当者・支社」タブで使う。パートナー一覧の権限が無ければ空）
      st.partnerRows = Array.isArray(data.partnerContacts) ? data.partnerContacts : [];
    } else {
      st.rows = [];
      st.partnerRows = [];
      st.loadError = true;
    }
    // 取引先一覧はどのタブでもバッジ件数を出すため全タブ対象。案件詳細も担当者プルダウンの候補に使う
    if (clientDirectoryLazyDataNeedsRender()) render();
  });
}

/** 所属先（"client" / "partner"）の担当者キャッシュ */
function clientContactRowsOf(owner) {
  const st = uiState.clientContacts;
  return (owner === "partner" ? st.partnerRows : st.rows) || [];
}

function clientContactDisplayName(r) {
  return [r.lastName, r.firstName].filter(Boolean).join("　");
}

/** 追加・編集モーダル（cl-mini-modal 中央表示） */
function clientContactEditorModalHtml() {
  const st = uiState.clientContacts;
  const ed = st.editor;
  if (!ed) return "";
  const isEdit = !!ed.id;
  const ownerLabel = ed.partnerId ? "パートナー" : "顧客";
  const clientOpts = clients
    .filter((c) => !c.salesHidden || c.id === ed.clientId)
    .map((c) => `<option value="${escapeHtmlAttr(c.id)}" ${ed.clientId === c.id ? "selected" : ""}>${escapeHtml(c.name)}</option>`)
    .join("");
  // パートナーの担当者は取引先詳細からだけ登録する＝所属先は固定表示（付け替えはしない）
  const ownerFieldHtml = ed.partnerId
    ? clientSubRecordPartnerFieldHtml(ed.partnerId)
    : `<label class="cl-form-group">
            <span class="cl-form-label">顧客 <span class="cl-form-required">*</span></span>
            <select class="cl-form-input" data-client-contact-field="clientId">
              <option value="" ${ed.clientId ? "" : "selected"}>選択してください</option>${clientOpts}
            </select>
          </label>`;
  return `
    <div class="cl-mini-modal-overlay open" data-client-contact-overlay="1">
      <div class="cl-mini-modal at-modal cl-contact-modal" role="dialog" aria-modal="true" aria-labelledby="cl-contact-h">
        <div class="cl-mini-modal-head">
          <h4 id="cl-contact-h">${ownerLabel}担当者を${isEdit ? "編集" : "登録"}</h4>
          <button class="cl-modal-close" type="button" data-client-contact-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          ${ownerFieldHtml}
          <div class="cl-contact-form-2col">
            <label class="cl-form-group"><span class="cl-form-label">姓 <span class="cl-form-required">*</span></span><input class="cl-form-input" data-client-contact-field="lastName" value="${escapeHtmlAttr(ed.lastName || "")}" /></label>
            <label class="cl-form-group"><span class="cl-form-label">名</span><input class="cl-form-input" data-client-contact-field="firstName" value="${escapeHtmlAttr(ed.firstName || "")}" /></label>
          </div>
          <div class="cl-contact-form-2col">
            <label class="cl-form-group"><span class="cl-form-label">敬称</span><input class="cl-form-input" data-client-contact-field="honorific" value="${escapeHtmlAttr(ed.honorific || "様")}" /></label>
            <label class="cl-form-group"><span class="cl-form-label">役職</span><input class="cl-form-input" data-client-contact-field="title" value="${escapeHtmlAttr(ed.title || "")}" /></label>
          </div>
          <label class="cl-form-group"><span class="cl-form-label">部署</span><input class="cl-form-input" data-client-contact-field="dept" value="${escapeHtmlAttr(ed.dept || "")}" /></label>
          <label class="cl-form-group"><span class="cl-form-label">email</span><input class="cl-form-input" data-client-contact-field="email" type="email" value="${escapeHtmlAttr(ed.email || "")}" /></label>
          <label class="cl-contact-archive-check"><input type="checkbox" data-client-contact-field="archived" ${ed.archived ? "checked" : ""} /><span>アーカイブ（一覧の既定表示から外す）</span></label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-client-contact-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-client-contact-save="1" ${st.saving ? "disabled" : ""}>${st.saving ? "保存中…" : isEdit ? "更新する" : "登録する"}</button>
        </div>
      </div>
    </div>`;
}

async function saveClientContactEditor() {
  const st = uiState.clientContacts;
  const ed = st.editor;
  if (!ed || st.saving) return;
  if (!ed.clientId && !ed.partnerId) {
    showToastError("顧客を選択してください");
    return;
  }
  if (!String(ed.lastName || "").trim()) {
    showToastError("姓を入力してください");
    return;
  }
  st.saving = true;
  render();
  const owner = ed.partnerId ? "partner" : "client";
  const bodyPayload = {
    ...(ed.partnerId ? { partnerId: ed.partnerId } : { clientId: ed.clientId }),
    owner,
    lastName: String(ed.lastName || "").trim(),
    firstName: String(ed.firstName || "").trim(),
    honorific: String(ed.honorific || "様").trim() || "様",
    title: String(ed.title || "").trim(),
    dept: String(ed.dept || "").trim(),
    email: String(ed.email || "").trim(),
    archived: !!ed.archived
  };
  const { ok, data } = ed.id
    ? await apiFetch("/api/client-contacts", { method: "PUT", body: { id: ed.id, ...bodyPayload } })
    : await apiFetch("/api/client-contacts", { method: "POST", body: bodyPayload });
  st.saving = false;
  if (!ok || !data.contact) {
    showToastError(typeof data.error === "string" ? data.error : "担当者の保存に失敗しました");
    render();
    return;
  }
  const rowsKey = owner === "partner" ? "partnerRows" : "rows";
  if (!Array.isArray(st[rowsKey])) st[rowsKey] = [];
  const idx = st[rowsKey].findIndex((r) => r.id === data.contact.id);
  if (idx >= 0) st[rowsKey][idx] = data.contact;
  else st[rowsKey].unshift(data.contact);
  // 案件詳細の＋から登録した場合は、その案件の「顧客担当者名」に登録した担当者をそのまま選択する
  // （案件のクライアントと同じ顧客で登録した時のみ。選択肢は案件のクライアントで絞られるため）
  const pdDraft = ed.fromProjectDetail ? projectDetailFormDraftActive() : null;
  if (pdDraft && String(pdDraft.clientId) === String(data.contact.clientId)) {
    pdDraft.clientPerson = clientContactDisplayName(data.contact);
  }
  st.editor = null;
  showToast(ed.id ? "担当者を更新しました" : "担当者を登録しました");
  render();
}

async function deleteClientContact(id, owner = "client") {
  const st = uiState.clientContacts;
  const row = clientContactRowsOf(owner).find((r) => r.id === id);
  if (!row) return;
  const name = clientContactDisplayName(row) || "この担当者";
  if (!window.confirm(`「${row.clientName || row.partnerName} / ${name}」を削除します。よろしいですか？\n（一覧から外すだけならアーカイブをおすすめします）`)) return;
  const { ok, data } = await apiFetch("/api/client-contacts", { method: "DELETE", body: { id, owner } });
  if (!ok) {
    showToastError(typeof data.error === "string" ? data.error : "削除に失敗しました");
    return;
  }
  st[owner === "partner" ? "partnerRows" : "rows"] = clientContactRowsOf(owner).filter((r) => r.id !== id);
  showToast("担当者を削除しました");
  render();
}

/** 担当者モーダルを扱ってよい画面か: 取引先詳細（担当者・支社タブ）、または案件詳細からの＋登録中 */
function clientContactEditorRouteActive() {
  const page = currentRoute().page;
  return page === "client-detail" || (page === "project-detail" && !!uiState.clientContacts.editor?.fromProjectDetail);
}

// ── 案件詳細「顧客担当者名」の＋ボタン: 案件のクライアントを選択済みにして登録モーダルを開く ──
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  const addEl = target?.closest("[data-pd-add-client-contact]");
  if (!(addEl instanceof HTMLElement) || currentRoute().page !== "project-detail") return;
  ensureClientContactsLoaded();
  uiState.clientContacts.editor = {
    id: 0, clientId: addEl.dataset.pdAddClientContact || "", lastName: "", firstName: "", honorific: "様", title: "", dept: "", email: "", archived: false,
    fromProjectDetail: true
  };
  render();
});

// ── イベント（機能別ファイルに独立の listener を置く規約に従う） ──
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !clientContactEditorRouteActive()) return;
  const st = uiState.clientContacts;

  // 削除はカード全体の data-client-contact-open より先に判定する（カード内ボタンが開くに食われる）
  // 取引先詳細のボタンは data-client-contact-owner（"client" / "partner"）と所属先の ID を持つ
  const deleteEl = target.closest("[data-client-contact-delete]");
  if (deleteEl instanceof HTMLElement) {
    void deleteClientContact(Number(deleteEl.dataset.clientContactDelete), deleteEl.dataset.clientContactOwner || "client");
    return;
  }
  const openEl = target.closest("[data-client-contact-open]");
  if (openEl instanceof HTMLElement) {
    const key = openEl.dataset.clientContactOpen;
    const owner = openEl.dataset.clientContactOwner || "client";
    if (key === "new") {
      const ownerId = openEl.dataset.clientContactOwnerId || "";
      st.editor = {
        id: 0, clientId: owner === "client" ? ownerId : "", partnerId: owner === "partner" ? Number(ownerId) || 0 : 0,
        lastName: "", firstName: "", honorific: "様", title: "", dept: "", email: "", archived: false
      };
    } else {
      const row = clientContactRowsOf(owner).find((r) => String(r.id) === key);
      if (!row) return;
      st.editor = { id: row.id, clientId: row.clientId || "", partnerId: row.partnerId || 0, lastName: row.lastName, firstName: row.firstName, honorific: row.honorific, title: row.title, dept: row.dept, email: row.email, archived: !!row.archived };
    }
    render();
    return;
  }
  const saveEl = target.closest("[data-client-contact-save]");
  if (saveEl instanceof HTMLElement) {
    void saveClientContactEditor();
    return;
  }
  // 閉じる: ✕/キャンセル、またはオーバーレイ自身のクリック（モーダル内側クリックでは閉じない）
  const closeEl = target.closest("[data-client-contact-close]");
  if (closeEl instanceof HTMLElement || (st.editor && target === target.closest("[data-client-contact-overlay]"))) {
    if (st.editor && !st.saving) {
      st.editor = null;
      render();
    }
    return;
  }
});

document.addEventListener("change", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !clientContactEditorRouteActive()) return;
  const st = uiState.clientContacts;

  const fieldKey = target instanceof HTMLElement ? target.dataset.clientContactField : "";
  if (fieldKey && st.editor) {
    st.editor[fieldKey] =
      target instanceof HTMLInputElement && target.type === "checkbox" ? target.checked : target.value;
    // モーダル内はフォーカス維持のため再描画しない（保存時に state から読む）
    return;
  }
});
// ───────────────────────────────────────────────────────────────
// 原価割当（照会）: 案件割当済みの原価を別の案件へ付け替える（POST /api/cost-moves）
//   条件（サーバと同じ。最終判定はサーバ）:
//   移せる範囲 = 営業: 自分の案件どうし／CS: 同じ担当営業の案件どうし／マスター: どの案件どうしでも
//   かつ 原価の計上月と両案件の請求月が当年度（4月〜翌3月）・月次ロックされていない
//   営業には他人の案件の行にボタン自体を出さない
// ───────────────────────────────────────────────────────────────

/** 'YYYY/MM' 系の月が当年度（4月始まり）か */
function costMoveInCurrentFiscalYear(ym) {
  const yyyymm = parseProjectBillingMonthYyyymm(String(ym || "").replace(/-/g, "/"));
  return yyyymm != null && fiscalYearStartFromBillingYyyymm(yyyymm) === dashboardFiscalYearStart();
}

/** 原価の月次ロック判定（サーバ isCostMonthLocked と同じ: 締め月にかかっても、両案件が一時解除中なら通す） */
function costMoveMonthsLocked(months, from, to) {
  return months.some((ym) => isMonthLocked(ym)) && !(projectUnlockActive(from) && projectUnlockActive(to));
}

/** ログイン者の権限で from → to へ移せる範囲か（サーバ moveScopeError と対） */
function costMoveScopeAllows(from, to) {
  if (systemRoleOfUser(currentUser) === "マスター") return true;
  if (costReviewIsMasterView()) {
    const owner = salesOwnerUser(from);
    return !!owner && owner.id === salesOwnerUser(to)?.id;
  }
  return isMyProjectForCostReview(from) && isMyProjectForCostReview(to);
}

/** 付け替え先として選べる範囲の説明（モーダル・ツールチップ用） */
function costMoveScopeLabel() {
  if (systemRoleOfUser(currentUser) === "マスター") return "別の案件";
  return costReviewIsMasterView() ? "同じ担当営業の別案件" : "自分の別の案件";
}

/** cost を from → to へ付け替えられるか（サーバ api/cost-moves.js と同じ条件。REMは計上月が移動先の請求月になる） */
function costMoveAllowed(cost, from, to) {
  const toTm = (cost.remBukken && to.billingMonth) || cost.targetMonth;
  return (
    costMoveScopeAllows(from, to) &&
    [cost.targetMonth, toTm, from.billingMonth, to.billingMonth].every(costMoveInCurrentFiscalYear) &&
    !isProjectLocked(from) &&
    !isProjectLocked(to) &&
    !costMoveMonthsLocked([cost.targetMonth, toTm], from, to)
  );
}

/** 付け替え先の候補: 移せる範囲の別案件のうち、この原価を移せるもの。名前順 */
function costMoveTargetProjects(cost, fromProject) {
  return projects
    .filter((p) => p !== fromProject && costMoveAllowed(cost, fromProject, p))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ja"));
}

/** 照会の行（c._projectId 付き）の付け替え可否。ボタンを出さない行は null（営業にとっての他人の案件・販管費等） */
function costMoveEligibility(c) {
  const project = projects.find((p) => p.id === c._projectId);
  if (!project || costDbNumericId(c) == null) return null;
  if (!costReviewIsMasterView() && !isMyProjectForCostReview(project)) return null;
  if (costOrderProjectLocked(c)) return { ok: false, reason: "発注申請した原価は付け替えできません（変える場合は削除して申請し直してください）" };
  if (!costMoveInCurrentFiscalYear(c.targetMonth) || !costMoveInCurrentFiscalYear(project.billingMonth)) {
    return { ok: false, reason: "付け替えできるのは当年度（4月〜翌3月）の原価・案件のみです" };
  }
  if (isProjectLocked(project) || costMoveMonthsLocked([c.targetMonth], project, project)) {
    return { ok: false, reason: "月次ロック済みのため付け替えできません" };
  }
  if (costMoveTargetProjects(c, project).length === 0) {
    return { ok: false, reason: `付け替え先にできる${costMoveScopeLabel()}（当年度・未ロック）がありません` };
  }
  return { ok: true, reason: `${costMoveScopeLabel()}へこの原価を移します` };
}

/** 照会の行に出す「付け替え」ボタン（条件外は理由つきで無効表示） */
function costMoveButtonHtml(c) {
  const el = costMoveEligibility(c);
  if (!el) return "";
  return `<button type="button" class="inline-btn ghost cost-review-dispute-btn" data-cost-move-open="${escapeHtmlAttr(c.id)}" title="${escapeHtmlAttr(el.reason)}" ${el.ok ? "" : "disabled"}>付け替え</button>`;
}

function renderCostMoveModal() {
  const m = uiState.costMoveModal;
  if (!m) return "";
  const pl = findCostPlacementByUiId(m.costUiId);
  const targets = pl?.project ? costMoveTargetProjects(pl.cost, pl.project) : [];
  // マスターは担当営業をまたいで選べるため、候補に担当営業名を添える
  const showOwner = systemRoleOfUser(currentUser) === "マスター";
  const optionLabel = (p) =>
    [showOwner ? salesOwnerUser(p)?.name : "", p.billingMonth ? `請求 ${p.billingMonth}` : ""].filter(Boolean).join(" / ");
  return `
    <div class="composer-backdrop" data-cost-move-close="1"></div>
    <aside class="composer-panel project-create-panel cost-dispute-panel" role="dialog" aria-modal="true" aria-labelledby="cost-move-title">
      <div class="composer-head">
        <div><h3 id="cost-move-title">原価の付け替え</h3><p>${escapeHtml(pl?.project?.name || "")}</p></div>
        <button class="inline-btn ghost" data-cost-move-close="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="prompt-copy">「${escapeHtml(m.costLabel || "")}」を、${escapeHtml(costMoveScopeLabel())}へ移します（当年度・未ロックの案件のみ選べます）。</p>
        <label class="cl-form-group">
          <span class="cl-form-label">付け替え先の案件</span>
          <select class="pj-input" data-cost-move-target>
            <option value="">選択してください</option>
            ${targets
              .map((p) => `<option value="${escapeHtmlAttr(p.id)}" ${m.targetId === p.id ? "selected" : ""}>${escapeHtml(p.name || "")}${optionLabel(p) ? `（${escapeHtml(optionLabel(p))}）` : ""}</option>`)
              .join("")}
          </select>
        </label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-cost-move-close="1">キャンセル</button>
        <button class="inline-btn" data-cost-move-submit="1" ${m.saving || !m.targetId ? "disabled" : ""}>${m.saving ? "付け替え中…" : "付け替える"}</button>
      </div>
    </aside>`;
}

/** 付け替えの送信: サーバで移動 → ローカル state も元案件から外して移動先へ（REMは計上月も揃う） */
async function submitCostMove() {
  const m = uiState.costMoveModal;
  if (!m || m.saving || !m.targetId) return;
  const pl = findCostPlacementByUiId(m.costUiId);
  const to = projects.find((p) => p.id === m.targetId);
  const dbId = costDbNumericId({ id: m.costUiId });
  if (!pl?.project || !to || to.dbId == null || dbId == null) {
    showToast("原価または案件が見つかりません。再読み込みしてください", { variant: "error" });
    return;
  }
  m.saving = true;
  render();
  const res = await apiFetch("/api/cost-moves", { method: "POST", body: { costId: dbId, projectId: to.dbId } });
  if (!res.ok) {
    m.saving = false;
    showToast(typeof res.data?.error === "string" ? res.data.error : "付け替えに失敗しました", { variant: "error" });
    render();
    return;
  }
  const cost = pl.cost;
  pl.project.costs = (pl.project.costs || []).filter((c) => c !== cost);
  if (typeof res.data?.targetMonth === "string" && res.data.targetMonth) cost.targetMonth = res.data.targetMonth;
  if (!to.costs) to.costs = [];
  to.costs.push(cost);
  uiState.costMoveModal = null;
  showToast(`「${to.name || ""}」へ付け替えました`);
  render();
}

// ── イベント（独立リスナー・原価割当ページのみ） ──
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (currentRoute().page !== "cost-allocation" && !uiState.costMoveModal) return;

  const openBtn = t.closest("[data-cost-move-open]");
  if (openBtn instanceof HTMLButtonElement && !openBtn.disabled) {
    const costUiId = openBtn.getAttribute("data-cost-move-open") || "";
    const pl = findCostPlacementByUiId(costUiId);
    if (!pl?.project) return;
    uiState.costMoveModal = {
      costUiId,
      costLabel: `${stripCostBucketMarkers(String(pl.cost.note || "")).slice(0, 40) || pl.cost.vendor || "原価"}（${yen.format(Number(pl.cost.amount || 0))}）`,
      targetId: "",
      saving: false
    };
    render();
    return;
  }
  if (t.closest("[data-cost-move-close]")) {
    if (uiState.costMoveModal?.saving) return;
    uiState.costMoveModal = null;
    render();
    return;
  }
  if (t.closest("[data-cost-move-submit]")) {
    void submitCostMove();
  }
});

document.addEventListener("change", (e) => {
  const t = e.target;
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-move-target") && uiState.costMoveModal) {
    uiState.costMoveModal.targetId = t.value || "";
    render();
  }
});
// ─────────────────────────────────────────────────────────────
// 原価の差し戻し（経理→営業）: 「この原価、内容が違う」を理由付きで返す
//   - 経理: 原価登録一覧の行から理由を添えて差し戻し（/api/cost-remands）
//   - 営業: 原価割当ページで差し戻しを確認 → フル編集フォームで修正
//     （保存すると差し戻しはサーバ側で自動解消 = api/costs.js clearRemand）
// 異議（15d: 営業→CS）の鏡像。イベントは独立リスナー（16 の巨大ハンドラには足さない）
// ─────────────────────────────────────────────────────────────

/** 差し戻せる原価か: 差し戻しは「営業に直してもらう」操作なので、営業が登録した原価だけ。
 *  登録者が分からない原価（登録者を記録する前の原価・システム取込）は従来どおり差し戻せる。
 *  登録者のユーザーが見つからない（削除済み等）ときも従来どおり */
function costRemandApplicable(c) {
  const by = c?.createdByUserDbId;
  if (by == null) return true;
  const u = getUserById(`u${by}`);
  if (!u) return true;
  const roles = (Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role]).map(normalizeJobRoleLabel);
  return roles.includes("営業");
}

/** 差し戻し理由入力モーダル（経理） */
function renderCostRemandModal() {
  const m = uiState.costRemandModal;
  if (!m) return "";
  return `
    <div class="composer-backdrop" data-cost-remand-close="1"></div>
    <aside class="composer-panel project-create-panel cost-dispute-panel" role="dialog" aria-modal="true" aria-labelledby="cost-remand-title">
      <div class="composer-head">
        <div><h3 id="cost-remand-title">原価の差し戻し</h3><p>${escapeHtml(m.projectName || "")}</p></div>
        <button class="inline-btn ghost" data-cost-remand-close="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="prompt-copy">「${escapeHtml(m.costLabel || "")}」を担当営業へ差し戻します。理由を入力してください（担当営業に届き、修正・保存されると解消します）。</p>
        <label class="cl-form-group">
          <span class="cl-form-label">理由（必須）</span>
          <textarea class="pj-input cost-dispute-reason" rows="3" data-cost-remand-reason placeholder="例）請求書の金額と一致しない／原本が添付されていない">${escapeHtml(m.reason || "")}</textarea>
        </label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-cost-remand-close="1">キャンセル</button>
        <button class="inline-btn" data-cost-remand-submit="1" ${m.saving ? "disabled" : ""}>${m.saving ? "送信中…" : "差し戻す"}</button>
      </div>
    </aside>`;
}

/** 差し戻しの送信（経理）: サーバーでフラグ付与 → ローカル state も同期 */
async function submitCostRemand() {
  const m = uiState.costRemandModal;
  if (!m || m.saving) return;
  const reason = String(m.reason || "").trim();
  if (!reason) {
    showToast("理由を入力してください", { variant: "error" });
    return;
  }
  const pl = findCostPlacementByUiId(m.costUiId);
  const dbId = costDbNumericId({ id: m.costUiId });
  if (!pl || dbId == null) {
    showToast("原価が見つかりません。再読み込みしてください", { variant: "error" });
    return;
  }
  m.saving = true;
  render();
  const res = await apiFetch("/api/cost-remands", { method: "POST", body: { costId: dbId, reason } });
  if (!res.ok) {
    m.saving = false;
    showToast(typeof res.data?.error === "string" ? res.data.error : "差し戻しに失敗しました", { variant: "error" });
    render();
    return;
  }
  pl.cost.remandNote = reason;
  pl.cost.remandedByDbId = currentUser?.dbId ?? null;
  pl.cost.remandedAt = new Date().toISOString();
  uiState.costRemandModal = null;
  showToast("差し戻しました。担当営業に通知されます");
  render();
}

/** 差し戻しの取消（経理・誤操作の是正用） */
async function resolveCostRemand(costUiId) {
  const pl = findCostPlacementByUiId(costUiId);
  const dbId = costDbNumericId({ id: costUiId });
  if (!pl || dbId == null) return;
  const res = await apiFetch("/api/cost-remands", { method: "POST", body: { costId: dbId, resolve: true } });
  if (!res.ok) {
    showToast(typeof res.data?.error === "string" ? res.data.error : "差し戻しの取消に失敗しました", { variant: "error" });
    return;
  }
  delete pl.cost.remandNote;
  delete pl.cost.remandedByDbId;
  delete pl.cost.remandedAt;
  showToast("差し戻しを取り消しました");
  render();
}

/** 営業の修正フォームを開く: 通常の編集（data-edit-cost）と同じ costForm に remandFix を付ける。
 *  以前は項目を写していたため発注ステータスが漏れ、修正保存で発注が「未設定」に上書きされた */
function openCostRemandFixForm(costUiId) {
  const placement = findCostPlacementByUiId(costUiId);
  if (!placement) return;
  uiState.costForm = {
    ...costEditFormFromPlacement(placement, costUiId),
    remandFix: true,
    remandNote: placement.cost.remandNote || ""
  };
  render();
}

// ── イベント（独立リスナー・原価登録/原価割当ページのみ） ──
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const page = currentRoute().page;
  // 原価登録ページは "#cost-management"（ナビ）と "#costs" の両ハッシュで開けるため両方を許可する
  const remandPages = page === "costs" || page === "cost-management" || page === "cost-allocation" || page === "cost-reconcile";
  if (!remandPages && !uiState.costRemandModal) return;

  const openBtn = t.closest("[data-cost-remand-open]");
  if (openBtn instanceof HTMLElement) {
    const costUiId = (openBtn.getAttribute("data-cost-remand-open") || "").split(":")[1] || "";
    const pl = findCostPlacementByUiId(costUiId);
    if (!pl) return;
    uiState.costRemandModal = {
      costUiId,
      projectName: pl.project?.name || "",
      costLabel: `${stripCostBucketMarkers(String(pl.cost.note || "")).slice(0, 40) || pl.cost.vendor || "原価"}（${yen.format(Number(pl.cost.amount || 0))}）`,
      reason: "",
      saving: false
    };
    render();
    return;
  }
  const resolveBtn = t.closest("[data-cost-remand-resolve]");
  if (resolveBtn instanceof HTMLElement) {
    const costUiId = (resolveBtn.getAttribute("data-cost-remand-resolve") || "").split(":")[1] || "";
    void resolveCostRemand(costUiId);
    return;
  }
  const fixBtn = t.closest("[data-cost-remand-fix]");
  if (fixBtn instanceof HTMLElement) {
    openCostRemandFixForm(fixBtn.getAttribute("data-cost-remand-fix") || "");
    return;
  }
  if (t.closest("[data-cost-remand-close]")) {
    if (uiState.costRemandModal?.saving) return;
    uiState.costRemandModal = null;
    render();
    return;
  }
  if (t.closest("[data-cost-remand-submit]")) {
    void submitCostRemand();
    return;
  }
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (t instanceof HTMLTextAreaElement && t.hasAttribute("data-cost-remand-reason") && uiState.costRemandModal) {
    uiState.costRemandModal.reason = t.value;
  }
});
// ────────────────────────────────────────────────────────────────
// 使い方ガイド（サイドバーのユーザーメニュー「使い方ガイド」から開く）
// 既存の render()/uiState 描画サイクルには乗らず、自前のDOM生成・表示切替で完結させる
// 独立UI（development/allowed-tools/workload と同様に本ファイル単独で完結させる方針）。
// 内容は静的テキストのみで、API呼び出し・保存は一切行わない。
// ────────────────────────────────────────────────────────────────

const HELP_GUIDE_SECTIONS = [
  {
    id: "todo",
    label: "TODO",
    where: "サイドナビ「ToDo管理」",
    roles: ["全員が利用可"],
    icon: '<rect x="4" y="3" width="16" height="18" rx="2"></rect><path d="M8 8h8M8 12h8M8 16h5"></path>',
    html: `
      <p>「ToDo管理」トップは<b>プロジェクト</b>（案件のくくり）の一覧です。プロジェクトを開くと、そのプロジェクト専用のカンバン式<b>課題ボード</b>に入ります。</p>
      <h3>基本の流れ</h3>
      <ol class="fp-hg-steps">
        <li>ToDo管理トップで <span class="fp-hg-ui">プロジェクトを登録</span>、または既存のプロジェクトを選択</li>
        <li>ボード内の <span class="fp-hg-ui">課題を追加</span> から新規タスクを起票（必須はタイトルと担当者）</li>
        <li>詳細・カテゴリ・状況・優先度・開始日/期限日・添付ファイルは任意で入力</li>
        <li>カードをドラッグ&ドロップで列（状況）間を移動</li>
        <li>詳細画面のコメント欄で進捗共有。<span class="fp-hg-ui">@</span> でメンション、画像は貼り付け（Ctrl+V）で添付可</li>
      </ol>
      <p>列（状況）はボード右上の歯車アイコン（<span class="fp-hg-ui">プロジェクト設定</span>）の「状況」タブで自由に追加・並び替えできます。未設定の場合は既定の<span class="fp-hg-ui">未対応 / 対応中 / 完了 / 保留 / 対応不要</span>が使われます。</p>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">つまずきやすい点</span><p>「完了」への変更は<b>その課題を作成した本人のみ</b>可能です。</p></div></div>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">つまずきやすい点</span><p>完了した課題は、<b>完了後30日間更新がない</b>と自動的に「アーカイブ」へ移動します（プロジェクト設定の「アーカイブ」タブから確認）。復元できるのは<b>起票者のみ</b>です。</p></div></div>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">通知の受け取り方</span><p>アカウント設定の「ToDo通知の受け取り」で、メール・Slack通知のON/OFFを個人ごとに切り替えられます。</p></div></div>
    `
  },
  {
    id: "dev",
    label: "自社ツール",
    where: "サイドナビ「自社ツール」",
    roles: ["閲覧: 全員", "編集: マスター/管理者・職種「開発」"],
    icon: '<path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5z"></path>',
    html: `
      <p>自社で開発中・運用中のツールと、受託開発案件の進捗を一覧管理するページです。上部のタブで <span class="fp-hg-ui">自社ツール</span> / <span class="fp-hg-ui">受託開発</span> を切り替えます。</p>
      <div class="fp-hg-table-wrap"><table>
        <thead><tr><th>種別</th><th>ステータス</th><th>担当</th></tr></thead>
        <tbody>
          <tr><td>自社ツール</td><td>要件定義 → 開発中 → 社内利用 → 販売中 / 販売停止</td><td>単一担当者</td></tr>
          <tr><td>受託開発</td><td>提案中 → 要件定義 → 開発中 → リリース済</td><td>営業担当・開発担当を別々に登録</td></tr>
        </tbody>
      </table></div>
      <p>状況が「要件定義」「開発中」のときは日付欄が「リリース予定」、それ以降は「リリース日」に自動で切り替わります。各ツールの行から、紐づけたToDo管理のプロジェクト（課題ボード）へ直接ジャンプできます。</p>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">つまずきやすい点</span><p>編集できるのは<b>マスター/管理者、または職種が「開発」の人だけ</b>です。それ以外の職種は閲覧のみです。</p></div></div>
    `
  },
  {
    id: "allowed",
    label: "社内申請",
    where: "サイドナビ「社内申請」",
    roles: ["閲覧: 全員", "変更: 申請制（マスターも含め全員）"],
    icon: '<path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"></path><path d="M9.5 12l1.8 1.8L15 10"></path>',
    html: `
      <p>業務で使うSaaS・ツールの利用可否を管理する一覧です。<b>一覧は誰でも閲覧できますが、登録・編集・削除はすべて「申請」を出し、承認されて初めて反映</b>されます（マスター権限の人も同じです）。</p>
      <h3>新規に使いたいツールを申請する</h3>
      <ol class="fp-hg-steps">
        <li><span class="fp-hg-ui">申請する</span> ボタンからモーダルを開き、「申請の種類」で <b>ツールの利用許可</b> を選ぶ（開いた直後は備品の貸与）</li>
        <li>ツール名・開発会社（vendor）・申請理由/用途を入力</li>
        <li>「情報セキュリティ管理規定を確認しました」にチェック</li>
        <li>任意でURL・利用規約メモ・カテゴリ（AI / 開発ツール / 業務ツール / その他）を設定</li>
        <li>送信後は「申請中」状態になり、承認されると一覧に反映</li>
      </ol>
      <p>自分の申請が「申請中」の間は<span class="fp-hg-ui">取り下げ</span>で取消可能です。却下された場合はレビューコメントが表示されます。</p>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">つまずきやすい点</span><p>既存ツールの<span class="fp-hg-ui">編集を申請</span>を開くと、登録時の申請理由がそのまま入力欄に残っています。使い回さず編集内容に合わせて書き直してください。削除申請では理由欄は空欄からのスタートです。</p></div></div>
      <h3>備品（PC・携帯電話・ENカードなど）の貸与を申請する</h3>
      <ol class="fp-hg-steps">
        <li><span class="fp-hg-ui">申請する</span> を押し、「申請の種類」で <b>備品の貸与</b> を選んで、品目・希望内容・申請理由を入力して申請</li>
        <li>承認フロー（承認設定の「備品の貸与申請」）に沿って申請承認ページで承認される</li>
        <li>管理部が在庫から、または購入して手配し、左メニュー「貸与品」の「手配待ち」から登録すると「貸与済み」になる</li>
      </ol>
      <p>自分の申請はツール・備品をまとめて「自分の申請」に、貸与されている備品は「自分の貸与品」に表示されます。</p>
    `
  },
  {
    id: "projects",
    label: "案件一覧",
    where: "サイドナビ「案件一覧」（営業メニュー配下）",
    roles: ["新規登録: マスター/管理者のみ"],
    icon: '<path d="M4 6h16M4 12h16M4 18h10"></path>',
    html: `
      <h3>検索・絞り込み</h3>
      <p>案件No・案件名・顧客（検索）・自社担当者・受注ステータスに加え、<b>請求月</b>の期間指定（from〜to）で絞り込みできます。絞り込み中はバッジで件数表示され、<span class="fp-hg-ui">リセット</span>で解除できます。</p>
      <h3>行から使える操作</h3>
      <ul class="fp-hg-plain">
        <li><span class="fp-hg-ui">詳細</span> — 案件詳細画面へ</li>
        <li><span class="fp-hg-ui">書類</span> — 見積書・請求書などの帳票へ</li>
        <li>複数選択して、受注状況の一括変更・入金日の一括登録/取消・CSVダウンロード</li>
        <li>上部の <span class="fp-hg-ui">自動作成設定</span> — 「毎月請求」にした自分の担当案件を、毎月指定した日に自動で今月分として複製（Slack で「継続分を複製して」と頼んでも同じことができます）</li>
      </ul>
      <p>初期表示は<b>請求月＝当月</b>（営業・メディアは<b>担当＝自分</b>も）で絞り込まれています。案件が見つからないときは <span class="fp-hg-ui">すべて表示</span> または <span class="fp-hg-ui">リセット</span> を押してください。</p>
      <h3>案件のロック（締め）</h3>
      <p>経理がその月の金額・内容を確認し終えると、原価一覧ページの<span class="fp-hg-ui">🔒 月次ロック</span>から月単位でロックをかけます（自動ではかかりません）。ロックされた月の案件には一覧に🔒バッジが表示されます。マスター・経理は<span class="fp-hg-ui">🔓 解除</span>（3営業日後に自動で再ロック）を直接実行できますが、それ以外の職種は<span class="fp-hg-ui">🔓 解除申請</span>から申請し、承認を経て一時的に編集できるようになります。</p>
    `
  },
  {
    id: "invoice",
    label: "請求書の送付",
    where: "案件一覧の行の「書類」→「請求書」タブ",
    roles: ["作成・捺印申請: 案件の担当者", "承認: 承認設定の承認者（申請承認ページ）"],
    icon: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6M16 13H8M16 17H8"></path>',
    html: `
      <h3>請求書の作成〜送付の流れ</h3>
      <ol class="fp-hg-steps">
        <li>案件一覧の行の <span class="fp-hg-ui">書類</span> を押し、<b>請求書</b>タブで明細・請求日などを確認・編集して、画面下部の <span class="fp-hg-ui">保存</span> で確定（見積書の備考は「備考を他の書類に反映」を選んで保存すると、他の書類にも入ります）</li>
        <li>画面上部の <span class="fp-hg-ui">捺印申請</span> を押す（状況は「捺印申請状況」に表示。申請中は <span class="fp-hg-ui">申請取消</span> で取り消せます。複数の案件は案件一覧で選択して一括で <span class="fp-hg-ui">捺印申請</span> も可能）</li>
        <li>承認者が <span class="fp-hg-ui">申請承認</span> ページで承認すると「承認済」になり、社印が自動で入ります</li>
        <li>画面下部の <span class="fp-hg-ui">正式PDFを発行</span> ／ <span class="fp-hg-ui">メール送信</span> ／ <span class="fp-hg-ui">郵送する</span> で送付</li>
      </ol>
      <p>発行・送付の記録は下部の <span class="fp-hg-ui">発行履歴</span> から確認できます。レイアウトだけ確かめたいときは <span class="fp-hg-ui">確認プレビュー</span>（承認前でも使え、発行履歴には残りません）を使ってください。</p>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">つまずきやすい点</span><p>請求書は捺印申請が未承認でも正式PDF発行・メール送信・郵送ができますが、<b>社印が入るのは承認済みの書類だけ</b>です。社印付きで送るときは先に「捺印申請状況」が承認済みか確認してください。</p></div></div>
    `
  },
  {
    id: "cost",
    label: "原価割当",
    where: "サイドナビ「原価割当」／登録は「原価一覧」／届いた請求書メールは「請求書受取」",
    roles: ["登録: 原価一覧ページ・営業は原価割当ページの「＋ 原価を追加」「＋ 原価をまとめて入力」", "割当作業: マスター・職種CS", "照会・異議: 営業"],
    icon: '<circle cx="12" cy="12" r="8.5"></circle><path d="M12 7v10M9.5 9.3c0-1.3 1.1-2 2.5-2s2.5.8 2.5 2c0 2.6-5 1.6-5 4.2 0 1.2 1.1 2 2.5 2s2.5-.7 2.5-2"></path>',
    html: `
      <h3>原価を登録する（原価一覧ページ）</h3>
      <ol class="fp-hg-steps">
        <li><span class="fp-hg-ui">＋ 原価を登録</span> を押し、計上月を選ぶ</li>
        <li>CSV・PDF・Excel・コピペで取り込むか、<span class="fp-hg-ui">手入力</span> を選ぶ（手入力は計上先の案件を選んで <span class="fp-hg-ui">入力フォームを開く</span>。未割当・販管費・調整用バケットも選択可）</li>
        <li>会計区分（媒体費 / 制作費 / 開発費 / その他）・発注先・金額（税抜）・消費税を入力</li>
        <li>計上月・請求日・支払期限・支払方法・請求書Noを入力</li>
        <li>源泉所得税は <span class="fp-hg-ui">自動計算</span> ボタンで算出可能</li>
        <li>支払日を入力すると自動的に「支払済」扱いになる</li>
      </ol>
      <p>一覧の行色は状況で変わります（<b>ピンク＝未受領／黄＝受領済／白＝支払済</b>）。経理は届いた請求書を確認し、ピンク行の<span class="fp-hg-ui">受領登録</span>から請求書No・計上月・会計区分を入れて「受領済」にします。</p>
      <p>案件詳細・原価割当の <span class="fp-hg-ui">＋ 原価を追加</span> から入れた原価は見込みとして登録され、請求書が届いたら経理が照合して確定します。</p>
      <p>営業は「原価割当」ページの<span class="fp-hg-ui">＋ 原価を追加</span>（1件ずつ）か<span class="fp-hg-ui">＋ 原価をまとめて入力</span>（複数行）から、自分が担当する案件に原価を登録します。制作費・開発費は発注申請になります。</p>
      <p>どの登録画面でも、<b>発注名</b>は一覧の「発注名」列に出る名前、<b>備考</b>は長文のメモ・申し送り（一覧の列には出ず 📝 の印だけ）です。</p>
      <h3>届いた請求書メールを取り込む（請求書受取ページ）</h3>
      <p>取引先に <span class="fp-hg-ui">invoice@oe-onboarding.site</span> 宛で請求書を送ってもらうと、サイドナビ「請求書受取」に溜まります（未処理件数がバッジで出ます）。行を開くと添付PDF・本文・原本のハッシュ・変更履歴が見られ、<span class="fp-hg-ui">原価として登録</span> で添付PDFがそのまま取込ウィザードに渡ります。登録が終わると「登録済」に移り、原本メールは削除されずに保存されます（電子帳簿保存法対応）。</p>
      <h3>案件へ割り当てる（原価割当ページ）</h3>
      <div class="fp-hg-table-wrap"><table>
        <thead><tr><th>役割</th><th>見え方</th></tr></thead>
        <tbody>
          <tr><td>マスター／職種CS</td><td>未割当の原価すべてを一覧表示。チェックして<span class="fp-hg-ui">まとめて割当</span>、または行の<span class="fp-hg-ui">編集</span>で個別割当</td></tr>
          <tr><td>営業</td><td>自分宛に割当依頼された原価のみを表示。編集できるのは計上先案件・会計区分・備考のみ</td></tr>
        </tbody>
      </table></div>
      <h3>割当に異議がある場合（営業）</h3>
      <p>原価照会画面「原価割当変更」の各行にある<span class="fp-hg-ui">異議</span>ボタンから理由を入力し<span class="fp-hg-ui">異議を申請する</span>を押すと、その原価は案件から外れて未割当に差し戻され、CS側に通知されます。</p>
      <h3>経理からの差し戻し</h3>
      <p>経理が内容の相違（金額の不一致・原本なし等）を見つけた原価は、理由付きで担当営業へ<span class="fp-hg-ui">差し戻し</span>されます。営業は原価割当ページの<span class="fp-hg-ui">修正する</span>から内容を直して保存すると差し戻しが解消し、経理側の一覧からも「差戻し中」表示が消えます。</p>
      <h3>照合（経理の月末チェック）</h3>
      <p>原価一覧ページの<span class="fp-hg-ui">照合</span>から、対象月の原価ごとに<b>請求書（あり／なし／相違）</b>と<b>確認状態（確認済み／未確認）</b>を棚卸しできます。届いた請求書（PDF・Excel・コピペ）をAIで読み取ると、登録済み原価と業者名＋金額で突合され、結果が一覧の請求書列にも反映されます。<b>一致</b>は1クリックで確認済み（仮原価は実原価へ昇格）、<b>金額相違</b>はその場修正か差し戻し、<b>該当なし</b>は新規登録できます。媒体費（広告CSV・自動連携）は照合対象外として表示され、トグルで非表示にもできます。月次ロック時に未確認が残っていると警告が出ます（ロック自体は可能）。</p>
      <div class="fp-hg-callout tip"><span class="fp-hg-mark">!</span><div><span class="fp-hg-lbl">検索のコツ</span><p>「原価一覧」ページの月フィルタは<b>請求月・支払月（支払期限の月）・計上月</b>の3種類あります。探している原価が見つからないときは、どの月区分で絞り込んでいるか確認してください。</p></div></div>
    `
  }
];

let helpGuideActiveTab = HELP_GUIDE_SECTIONS[0].id;
let helpGuideOverlayEl = null;

function buildHelpGuideOverlay() {
  const el = document.createElement("div");
  el.className = "fp-help-guide-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-label", "使い方ガイド");
  el.hidden = true;
  document.body.appendChild(el);
  return el;
}

function renderHelpGuideOverlay() {
  if (!helpGuideOverlayEl) return;
  const active = HELP_GUIDE_SECTIONS.find((s) => s.id === helpGuideActiveTab) || HELP_GUIDE_SECTIONS[0];
  const tabs = HELP_GUIDE_SECTIONS.map(
    (s) => `<button type="button" class="fp-hg-tab${s.id === active.id ? " is-active" : ""}" data-help-guide-tab="${s.id}">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">${s.icon}</svg>
      <span>${s.label}</span>
    </button>`
  ).join("");
  helpGuideOverlayEl.innerHTML = `
    <div class="fp-hg-backdrop" data-help-guide-close="1"></div>
    <div class="fp-hg-panel">
      <div class="fp-hg-head">
        <div>
          <p class="fp-hg-eyebrow">Onboarding</p>
          <h2>使い方ガイド</h2>
        </div>
        <button type="button" class="fp-hg-close" data-help-guide-close="1" aria-label="閉じる">×</button>
      </div>
      <div class="fp-hg-body">
        <nav class="fp-hg-tabs" aria-label="機能を選択">${tabs}</nav>
        <div class="fp-hg-content">
          <p class="fp-hg-where">📍 ${active.where}</p>
          ${
            Array.isArray(active.roles) && active.roles.length
              ? `<div class="fp-hg-roles">${active.roles.map((r) => `<span class="fp-hg-role">${r}</span>`).join("")}</div>`
              : ""
          }
          ${active.html}
        </div>
      </div>
    </div>
  `;
}

// closeHelpGuide の非表示タイマーが、閉じている間に再度開かれたケースを誤って
// 隠さないようにするための意図フラグ（is-open クラスは rAF 頼みで判定が遅れうるため使わない）
let helpGuideWantOpen = false;

function openHelpGuide() {
  helpGuideWantOpen = true;
  if (!helpGuideOverlayEl) helpGuideOverlayEl = buildHelpGuideOverlay();
  helpGuideActiveTab = HELP_GUIDE_SECTIONS[0].id;
  renderHelpGuideOverlay();
  helpGuideOverlayEl.hidden = false;
  requestAnimationFrame(() => helpGuideOverlayEl && helpGuideOverlayEl.classList.add("is-open"));
  document.body.classList.add("fp-hg-lock-scroll");
}

function closeHelpGuide() {
  if (!helpGuideOverlayEl) return;
  helpGuideWantOpen = false;
  helpGuideOverlayEl.classList.remove("is-open");
  document.body.classList.remove("fp-hg-lock-scroll");
  window.setTimeout(() => {
    if (helpGuideOverlayEl && !helpGuideWantOpen) helpGuideOverlayEl.hidden = true;
  }, 160);
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest("[data-help-guide-trigger]");
  if (trigger) {
    e.preventDefault();
    if (typeof closeSidebarFlyouts === "function") closeSidebarFlyouts();
    openHelpGuide();
    return;
  }

  if (!helpGuideOverlayEl || helpGuideOverlayEl.hidden) return;

  const tabBtn = target.closest("[data-help-guide-tab]");
  if (tabBtn) {
    helpGuideActiveTab = tabBtn.dataset.helpGuideTab;
    renderHelpGuideOverlay();
    return;
  }
  const closeBtn = target.closest("[data-help-guide-close]");
  if (closeBtn) closeHelpGuide();
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && helpGuideOverlayEl && !helpGuideOverlayEl.hidden) closeHelpGuide();
});
// ────────────────────────────────────────────────────────────────
// 支社（クライアント・パートナー）のマスタ。登録・編集は取引先詳細の「担当者・支社」タブと案件詳細の＋から行う
// （取引先一覧の「支社」タブは 2026-10-28 に廃止）。クライアントの支社は案件詳細の
// 「顧客支社名」プルダウンの選択肢になる。実装は 15e-client-contacts.js と同型。
// 登録・編集・削除は申請不要（営業も可）。データは /api/client-branches。
// ────────────────────────────────────────────────────────────────

/** 初回表示時に一覧を取得（取得済み・取得中は何もしない） */
function ensureClientBranchesLoaded() {
  const st = uiState.clientBranches;
  if (st.rows !== null || st.loading || !authToken) return;
  st.loading = true;
  st.loadError = false;
  void apiFetch("/api/client-branches").then(({ ok, data }) => {
    st.loading = false;
    if (ok && Array.isArray(data.branches)) {
      st.rows = data.branches;
      // パートナーの支社（取引先詳細の「担当者・支社」タブで使う。パートナー一覧の権限が無ければ空）
      st.partnerRows = Array.isArray(data.partnerBranches) ? data.partnerBranches : [];
    } else {
      st.rows = [];
      st.partnerRows = [];
      st.loadError = true;
    }
    // 取引先一覧はどのタブでもバッジ件数を出すため全タブ対象。案件詳細も支社プルダウンの候補に使う
    if (clientDirectoryLazyDataNeedsRender()) render();
  });
}

/** 所属先（"client" / "partner"）の支社キャッシュ */
function clientBranchRowsOf(owner) {
  const st = uiState.clientBranches;
  return (owner === "partner" ? st.partnerRows : st.rows) || [];
}

/** 支社の住所表示（一覧カードのメタ用） */
function clientBranchAddressLine(r) {
  return [r.prefecture, r.address, r.building].filter(Boolean).join(" ");
}

/** 追加・編集モーダル（cl-mini-modal 中央表示） */
function clientBranchEditorModalHtml() {
  const st = uiState.clientBranches;
  const ed = st.editor;
  if (!ed) return "";
  const isEdit = !!ed.id;
  const clientOpts = clients
    .filter((c) => !c.salesHidden || c.id === ed.clientId)
    .map((c) => `<option value="${escapeHtmlAttr(c.id)}" ${ed.clientId === c.id ? "selected" : ""}>${escapeHtml(c.name)}</option>`)
    .join("");
  // パートナーの支社は取引先詳細からだけ登録する＝所属先は固定表示（付け替えはしない）
  const ownerFieldHtml = ed.partnerId
    ? clientSubRecordPartnerFieldHtml(ed.partnerId)
    : `<label class="cl-form-group">
            <span class="cl-form-label">顧客 <span class="cl-form-required">*</span></span>
            <select class="cl-form-input" data-client-branch-field="clientId">
              <option value="" ${ed.clientId ? "" : "selected"}>選択してください</option>${clientOpts}
            </select>
          </label>`;
  return `
    <div class="cl-mini-modal-overlay open" data-client-branch-overlay="1">
      <div class="cl-mini-modal at-modal cl-contact-modal" role="dialog" aria-modal="true" aria-labelledby="cl-branch-h">
        <div class="cl-mini-modal-head">
          <h4 id="cl-branch-h">${isEdit ? "支社を編集" : "支社を登録"}</h4>
          <button class="cl-modal-close" type="button" data-client-branch-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          ${ownerFieldHtml}
          <label class="cl-form-group"><span class="cl-form-label">支社名 <span class="cl-form-required">*</span></span><input class="cl-form-input" data-client-branch-field="name" value="${escapeHtmlAttr(ed.name || "")}" placeholder="例：関西支社" /></label>
          <div class="cl-contact-form-2col">
            <label class="cl-form-group"><span class="cl-form-label">郵便番号</span><input class="cl-form-input" data-client-branch-field="postalCode" value="${escapeHtmlAttr(ed.postalCode || "")}" placeholder="000-0000" inputmode="numeric" maxlength="8" /></label>
            <label class="cl-form-group"><span class="cl-form-label">都道府県</span><select class="cl-form-input" data-client-branch-field="prefecture">${prefectureOptionsHtml(ed.prefecture)}</select></label>
          </div>
          <label class="cl-form-group"><span class="cl-form-label">住所</span><input class="cl-form-input" data-client-branch-field="address" value="${escapeHtmlAttr(ed.address || "")}" placeholder="市区町村・番地" /></label>
          <label class="cl-form-group"><span class="cl-form-label">建物名</span><input class="cl-form-input" data-client-branch-field="building" value="${escapeHtmlAttr(ed.building || "")}" /></label>
          <div class="cl-contact-form-2col">
            <label class="cl-form-group"><span class="cl-form-label">TEL</span><input class="cl-form-input" data-client-branch-field="tel" value="${escapeHtmlAttr(ed.tel || "")}" placeholder="00-0000-0000" /></label>
            <label class="cl-form-group"><span class="cl-form-label">FAX</span><input class="cl-form-input" data-client-branch-field="fax" value="${escapeHtmlAttr(ed.fax || "")}" placeholder="00-0000-0000" /></label>
          </div>
          <label class="cl-contact-archive-check"><input type="checkbox" data-client-branch-field="archived" ${ed.archived ? "checked" : ""} /><span>アーカイブ（一覧の既定表示から外す）</span></label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-client-branch-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-client-branch-save="1" ${st.saving ? "disabled" : ""}>${st.saving ? "保存中…" : isEdit ? "更新する" : "登録する"}</button>
        </div>
      </div>
    </div>`;
}

async function saveClientBranchEditor() {
  const st = uiState.clientBranches;
  const ed = st.editor;
  if (!ed || st.saving) return;
  if (!ed.clientId && !ed.partnerId) {
    showToastError("顧客を選択してください");
    return;
  }
  if (!String(ed.name || "").trim()) {
    showToastError("支社名を入力してください");
    return;
  }
  // 案件は支社を名前で持っており、書類・郵送の宛先は顧客＋支社名で引く。改名すると既存案件の宛先が
  // 黙って本社の住所に切り替わるため、使っている案件があれば件数を出して確認する（既存案件は書き換えない）
  const before = ed.id && !ed.partnerId ? (st.rows || []).find((r) => r.id === ed.id) : null;
  if (before && String(before.name || "").trim() !== String(ed.name || "").trim()) {
    const cid = String(before.clientId ?? "").replace(/^c/, "");
    const usedBy = projects.filter(
      (p) => String(p.clientId ?? "").replace(/^c/, "") === cid && String(p.clientBranchName || "").trim() === String(before.name || "").trim()
    ).length;
    if (
      usedBy > 0 &&
      !window.confirm(
        `支社「${before.name}」を宛先にしている案件が ${usedBy} 件あります。\n名前を変えると、それらの案件の書類・郵送の宛先は本社の住所になります（案件側の支社名は変わりません）。\n変更してよろしいですか？`
      )
    ) {
      return;
    }
  }
  st.saving = true;
  render();
  const owner = ed.partnerId ? "partner" : "client";
  const bodyPayload = {
    ...(ed.partnerId ? { partnerId: ed.partnerId } : { clientId: ed.clientId }),
    owner,
    name: String(ed.name || "").trim(),
    postalCode: String(ed.postalCode || "").trim(),
    prefecture: String(ed.prefecture || "").trim(),
    address: String(ed.address || "").trim(),
    building: String(ed.building || "").trim(),
    tel: String(ed.tel || "").trim(),
    fax: String(ed.fax || "").trim(),
    archived: !!ed.archived
  };
  const { ok, data } = ed.id
    ? await apiFetch("/api/client-branches", { method: "PUT", body: { id: ed.id, ...bodyPayload } })
    : await apiFetch("/api/client-branches", { method: "POST", body: bodyPayload });
  st.saving = false;
  if (!ok || !data.branch) {
    showToastError(typeof data.error === "string" ? data.error : "支社の保存に失敗しました");
    render();
    return;
  }
  const rowsKey = owner === "partner" ? "partnerRows" : "rows";
  if (!Array.isArray(st[rowsKey])) st[rowsKey] = [];
  const idx = st[rowsKey].findIndex((r) => r.id === data.branch.id);
  if (idx >= 0) st[rowsKey][idx] = data.branch;
  else st[rowsKey].unshift(data.branch);
  // 案件詳細の＋から登録した場合は、その案件の「顧客支社名」に登録した支社をそのまま選択する
  // （案件のクライアントと同じ顧客で登録した時のみ。選択肢は案件のクライアントで絞られるため）
  const pdDraft = ed.fromProjectDetail ? projectDetailFormDraftActive() : null;
  if (pdDraft && String(pdDraft.clientId) === String(data.branch.clientId)) {
    pdDraft.clientBranchName = String(data.branch.name || "");
  }
  st.editor = null;
  showToast(ed.id ? "支社を更新しました" : "支社を登録しました");
  render();
}

async function deleteClientBranch(id, owner = "client") {
  const st = uiState.clientBranches;
  const row = clientBranchRowsOf(owner).find((r) => r.id === id);
  if (!row) return;
  if (!window.confirm(`「${row.clientName || row.partnerName} / ${row.name}」を削除します。よろしいですか？\n（一覧から外すだけならアーカイブをおすすめします）`)) return;
  const { ok, data } = await apiFetch("/api/client-branches", { method: "DELETE", body: { id, owner } });
  if (!ok) {
    showToastError(typeof data.error === "string" ? data.error : "削除に失敗しました");
    return;
  }
  st[owner === "partner" ? "partnerRows" : "rows"] = clientBranchRowsOf(owner).filter((r) => r.id !== id);
  showToast("支社を削除しました");
  render();
}

/** 支社モーダルを扱ってよい画面か: 取引先詳細（担当者・支社タブ）、または案件詳細からの＋登録中 */
function clientBranchEditorRouteActive() {
  const page = currentRoute().page;
  return page === "client-detail" || (page === "project-detail" && !!uiState.clientBranches.editor?.fromProjectDetail);
}

// ── 案件詳細「顧客支社名」の＋ボタン: 案件のクライアントを選択済みにして登録モーダルを開く ──
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  const addEl = target?.closest("[data-pd-add-client-branch]");
  if (!(addEl instanceof HTMLElement) || currentRoute().page !== "project-detail") return;
  ensureClientBranchesLoaded();
  uiState.clientBranches.editor = {
    id: 0, clientId: addEl.dataset.pdAddClientBranch || "", name: "", postalCode: "", prefecture: "", address: "", building: "", tel: "", fax: "", archived: false,
    fromProjectDetail: true
  };
  render();
});

// ── イベント（機能別ファイルに独立の listener を置く規約に従う） ──
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !clientBranchEditorRouteActive()) return;
  const st = uiState.clientBranches;

  // 削除はカード全体の data-client-branch-open より先に判定する（カード内ボタンが開くに食われる）
  // 取引先詳細のボタンは data-client-branch-owner（"client" / "partner"）と所属先の ID を持つ
  const deleteEl = target.closest("[data-client-branch-delete]");
  if (deleteEl instanceof HTMLElement) {
    void deleteClientBranch(Number(deleteEl.dataset.clientBranchDelete), deleteEl.dataset.clientBranchOwner || "client");
    return;
  }
  const openEl = target.closest("[data-client-branch-open]");
  if (openEl instanceof HTMLElement) {
    const key = openEl.dataset.clientBranchOpen;
    const owner = openEl.dataset.clientBranchOwner || "client";
    if (key === "new") {
      const ownerId = openEl.dataset.clientBranchOwnerId || "";
      st.editor = {
        id: 0, clientId: owner === "client" ? ownerId : "", partnerId: owner === "partner" ? Number(ownerId) || 0 : 0,
        name: "", postalCode: "", prefecture: "", address: "", building: "", tel: "", fax: "", archived: false
      };
    } else {
      const row = clientBranchRowsOf(owner).find((r) => String(r.id) === key);
      if (!row) return;
      st.editor = { id: row.id, clientId: row.clientId || "", partnerId: row.partnerId || 0, name: row.name, postalCode: row.postalCode, prefecture: row.prefecture, address: row.address, building: row.building, tel: row.tel, fax: row.fax, archived: !!row.archived };
    }
    render();
    return;
  }
  const saveEl = target.closest("[data-client-branch-save]");
  if (saveEl instanceof HTMLElement) {
    void saveClientBranchEditor();
    return;
  }
  // 閉じる: ✕/キャンセル、またはオーバーレイ自身のクリック（モーダル内側クリックでは閉じない）
  const closeEl = target.closest("[data-client-branch-close]");
  if (closeEl instanceof HTMLElement || (st.editor && target === target.closest("[data-client-branch-overlay]"))) {
    if (st.editor && !st.saving) {
      st.editor = null;
      render();
    }
    return;
  }
});

document.addEventListener("change", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !clientBranchEditorRouteActive()) return;
  const st = uiState.clientBranches;

  const fieldKey = target instanceof HTMLElement ? target.dataset.clientBranchField : "";
  if (fieldKey && st.editor) {
    st.editor[fieldKey] =
      target instanceof HTMLInputElement && target.type === "checkbox" ? target.checked : target.value;
    // モーダル内はフォーカス維持のため再描画しない（保存時に state から読む）
    return;
  }
});
// ─────────────────────────────────────────────────────────────
// 原価照合（経理の月末チェック・#cost-reconcile）
//   - 対象月の原価を「確認済み／未確認」「原本あり／なし」で棚卸しする
//   - 届いた請求書（PDF/Excel/コピペ）をAIで読み、登録済み原価と
//     業者名＋金額（＋月）で突合 → 一致／金額相違／該当なし の3分類
//     一致=確認して受領済へ（請求書No等を入れて受領登録＋確認済みを同時に。仮原価は実原価へ昇格）
//     相違=その場修正 or 差し戻し（15e）／該当なし=新規登録
//   - 月次ロックモーダルの「未確認N件」警告からも遷移してくる
// 2026-07-30 確定仕様（照合モード）。イベントは独立リスナー
// ─────────────────────────────────────────────────────────────

function ensureCostReconcileState() {
  if (!uiState.costReconcile || typeof uiState.costReconcile !== "object") {
    uiState.costReconcile = { month: remBillingDefaultImportMonth(), filter: "all", vendor: "", matches: null, matchSourceLabel: "", matching: false, hideMedia: false, rowMatch: {}, selected: {}, sortKey: "", sortDir: "asc", importOpen: false, importMode: "", pasteText: "" };
  }
  if (!uiState.costReconcile.rowMatch || typeof uiState.costReconcile.rowMatch !== "object") uiState.costReconcile.rowMatch = {};
  if (!uiState.costReconcile.rowInvoiceNo || typeof uiState.costReconcile.rowInvoiceNo !== "object") uiState.costReconcile.rowInvoiceNo = {};
  if (!uiState.costReconcile.selected || typeof uiState.costReconcile.selected !== "object") uiState.costReconcile.selected = {};
  return uiState.costReconcile;
}

/** 行配列を発注先で絞り込む（未指定なら素通し）。チップ件数・一覧・媒体費トグルで発注先の絞り込みを揃えるため共用 */
function costReconcileScopeByVendor(rows, vendor) {
  return vendor ? rows.filter((c) => String(c.vendor || "").trim() === vendor) : rows;
}

/** 現在の月・絞り込みチップ条件での照合対象行（媒体費は対象外）。一覧表示と一括選択で共用 */
function costReconcileFilteredTargetRows() {
  const st = ensureCostReconcileState();
  const monthRows = costReconcileMonthRows(st.month).filter((c) => !costReconcileIsMediaRow(c));
  const targetRows = costReconcileScopeByVendor(monthRows, st.vendor);
  const invStatus = (c) => costReconcileInvoiceStatus(c, st.rowMatch);
  if (st.filter === "unchecked") return targetRows.filter((c) => !c.reconciledAt);
  if (st.filter === "noinv") return targetRows.filter((c) => invStatus(c) === "no");
  if (st.filter === "mismatch") return targetRows.filter((c) => invStatus(c) === "mismatch");
  return targetRows;
}

/** 一括確認の対象にできる行（表示中の照合対象のうち未確認）のUI id */
function costReconcileSelectableRowIds() {
  return costReconcileFilteredTargetRows()
    .filter((c) => !c.reconciledAt)
    .map((c) => c.id);
}

/** 媒体原価（広告CSV・REM等の自動連携。カテゴリ=媒体費）は請求書照合の対象外として扱う */
function costReconcileIsMediaRow(c) {
  return normalizeCostCategoryLabel(c.category) === "媒体費";
}

/** 行ごとの請求書ステータス: "mismatch"（突合で金額相違）/ "yes"（原本あり or 突合で一致）/ "no" */
function costReconcileInvoiceStatus(c, rowMatch) {
  const rm = rowMatch ? rowMatch[c.id] : undefined;
  if (rm === "mismatch") return "mismatch";
  if (Number(c.sourcePdfDocId) > 0 || rm === "match") return "yes";
  return "no";
}

/** 月次ロックモーダルの警告用: 照合が未確認の件数（媒体費=対象外は数えない） */
function costReconcileUnreconciledCount(monthInput) {
  return costReconcileMonthRows(monthInput).filter((c) => !costReconcileIsMediaRow(c) && !c.reconciledAt).length;
}

function costReconcileUserAllowed() {
  return (
    userCanAccessPage("cost-management") &&
    (isAccountingDepartmentJobRole(currentUser?.role) || isMasterOrAdminUser(currentUser))
  );
}

/** 対象月（YYYY-MM）の原価行（案件付き＋未割当・販管費・調整含む） */
function costReconcileMonthRows(monthInput) {
  const want = parseProjectBillingMonthYyyymm(String(monthInput || "").replace(/-/g, "/"));
  if (!want) return [];
  return flattenAllCostsAcrossProjectsAndOrphans().filter(
    (c) => parseProjectBillingMonthYyyymm(c.targetMonth || "") === want
  );
}

/**
 * 抽出金額と登録済み原価の金額が一致するか。
 * 請求書の税区分（税込/税抜/不明）に応じて、原価の税抜額・税込額と突き合わせる。
 */
function costReconcileAmountMatches(extAmount, taxMode, costExYen, costInclYen) {
  const a = Math.round(Number(extAmount || 0));
  if (!a) return false;
  if (taxMode === "inclusive") return a === costInclYen;
  if (taxMode === "exclusive") return a === costExYen;
  return a === costExYen || a === costInclYen;
}

/** AI抽出結果（items+payee）を対象月の原価と突合し、一致/金額相違/該当なしに分類する */
function costReconcileClassifyItems(items, payee, monthRows) {
  const resolvedMeta = resolveCostVendorFromPartnersAndClients(String(payee || ""));
  const vendorLabel = String(resolvedMeta?.vendorLabel || payee || "").trim();
  const vendorKey = compactVendorMatchKey(vendorLabel) || compactVendorMatchKey(String(payee || ""));
  // 媒体費（自動連携）は照合対象外のため突合候補からも除く
  const cands = vendorKey
    ? monthRows.filter((c) => !costReconcileIsMediaRow(c) && compactVendorMatchKey(c.vendor || "") === vendorKey)
    : [];
  // 同一請求書内の複数明細が同じ原価行に重複で紐づかないよう、突合済みの候補は後続の明細から除く
  const usedIds = new Set();
  return (items || []).map((it) => {
    const amt = Math.round(Number(it.amount || 0));
    const base = {
      summary: String(it.summary || "").trim(),
      amount: amt,
      taxMode: it.taxMode === "inclusive" || it.taxMode === "exclusive" ? it.taxMode : "unknown",
      date: String(it.date || "").trim(),
      payee: String(payee || "").trim(),
      vendorLabel
    };
    if (!cands.length) return { ...base, status: "none" };
    const available = cands.filter((c) => !usedIds.has(c.id));
    const pool = available.length ? available : cands;
    const exact = pool.find((c) => {
      const ex = Math.round(Number(c.amount || 0)) || 0;
      return costReconcileAmountMatches(amt, base.taxMode, ex, ex + costConsumptionTaxYen(c));
    });
    if (exact) {
      usedIds.add(exact.id);
      return {
        ...base,
        status: "match",
        costUiId: exact.id,
        costAmount: Math.round(Number(exact.amount || 0)) || 0,
        costIsProvisional: exact.isProvisional === true,
        costReconciled: !!exact.reconciledAt
      };
    }
    // 金額相違: 税抜・税込のどちらで見ても金額が最も近い候補に紐づける
    const near = pool.reduce((best, c) => {
      const ex = Math.round(Number(c.amount || 0)) || 0;
      const d = Math.min(Math.abs(amt - ex), Math.abs(amt - (ex + costConsumptionTaxYen(c))));
      return !best || d < best.d ? { c, d } : best;
    }, null).c;
    usedIds.add(near.id);
    return {
      ...base,
      status: "mismatch",
      costUiId: near.id,
      costAmount: Math.round(Number(near.amount || 0)) || 0,
      costIncl: (Math.round(Number(near.amount || 0)) || 0) + costConsumptionTaxYen(near)
    };
  });
}

/** 確認フラグの送信（promote=true で仮→実昇格も同時に） */
async function submitCostReconcile(costUiId, checked, promote) {
  const pl = findCostPlacementByUiId(costUiId);
  const dbId = costDbNumericId({ id: costUiId });
  if (!pl || dbId == null) {
    showToast("原価が見つかりません。再読み込みしてください", { variant: "error" });
    return false;
  }
  const res = await apiFetch("/api/cost-reconcile", {
    method: "POST",
    body: { costId: dbId, checked: checked !== false, ...(promote ? { promote: true } : {}) }
  });
  if (!res.ok) {
    showToast(typeof res.data?.error === "string" ? res.data.error : "確認の保存に失敗しました", { variant: "error" });
    return false;
  }
  if (checked !== false) {
    pl.cost.reconciledAt = new Date().toISOString();
    pl.cost.reconciledByDbId = currentUser?.dbId ?? null;
    // 発注申請中の原価は照合しても実原価にしない（承認で実原価になる）。代わりに承認前発注の印が付く
    if (promote && res.data?.promoted !== false) pl.cost.isProvisional = false;
    if (res.data?.preOrdered === true && !pl.cost.orderPreOrderedAt) pl.cost.orderPreOrderedAt = new Date().toISOString();
  } else {
    delete pl.cost.reconciledAt;
    delete pl.cost.reconciledByDbId;
  }
  return true;
}

/** 確認＋受領登録モーダルの編集可能フィールド（input ハンドラと「⇩コピー」で共用） */
const COST_RECONCILE_RECEIPT_FIELDS = ["invoiceNo", "targetMonth", "category", "dueDate"];

/** 確認＋受領登録モーダルを開く。uiIds のうち未確認行が対象で、
 *  未受領行=入力つき（請求書No・計上月・会計区分・支払期限）／受領済・支払済の行=入力なしで確認のみ */
function openCostReconcileReceiptModal(uiIds) {
  const st = ensureCostReconcileState();
  const rows = [];
  const confirmOnlyIds = [];
  const seen = new Set();
  for (const uiId of uiIds) {
    if (!uiId || seen.has(uiId)) continue;
    seen.add(uiId);
    const c = findCostPlacementByUiId(uiId)?.cost;
    if (!c || c.reconciledAt || costReconcileIsMediaRow(c)) continue;
    // DB未採番の行は persistCostToServerUpdate がローカルにだけ書いて成功扱いになるため除外（15jの一括受領と同じガード）
    if (costDbNumericId({ id: uiId }) == null) continue;
    if (costReceiptGroup(c) !== "pending") {
      confirmOnlyIds.push(uiId);
      continue;
    }
    rows.push({
      costId: uiId,
      vendor: String(c.vendor || "").trim(),
      note: stripCostBucketMarkers(String(c.note || "").trim()),
      amountIncl: (Math.round(Number(c.amount || 0)) || 0) + costConsumptionTaxYen(c),
      // 請求書Noは 既存値 → AI突合で読めた番号 の順で埋める（無ければ手入力）
      invoiceNo: c.invoiceNo || st.rowInvoiceNo[uiId] || "",
      targetMonth: billingMonthFilterToMonthInputValue(c.targetMonth) || st.month || "",
      category: c.category || "",
      // 既存値を優先し、空なら 請求日×パートナーの支払条件 で自動計算（一括の受領済変更と同じ規則・修正可）
      dueDate: c.dueDate || costAutoDueDateFromVendorTerms(c.vendor, c.invoiceDate)
    });
  }
  if (!rows.length && !confirmOnlyIds.length) {
    showToast("確認して受領済にできる行（未確認）を選択してください");
    return;
  }
  uiState.costReconcileReceiptModal = { rows, confirmOnlyIds, saving: false };
  render();
}

/** 確認＋受領登録の一括保存: 各行を受領済（請求書No・計上月・会計区分・支払期限を反映、仮原価は実原価化）にし、
 *  続けて確認済みフラグを付ける。失敗した行はモーダルに残るので、原因（月次ロック等）解消後にそのまま再実行できる */
async function submitCostReconcileReceiptModal() {
  const m = uiState.costReconcileReceiptModal;
  if (!m || m.saving) return;
  const st = ensureCostReconcileState();
  for (const r of m.rows) {
    const rowLabel = r.vendor || r.note || "支払先不明の行";
    if (!String(r.invoiceNo || "").trim()) {
      showToast(`請求書Noを入力してください（${rowLabel}）`, { variant: "error" });
      return;
    }
    if (!normalizeCostTargetMonthForSave(r.targetMonth)) {
      showToast(`計上月を入力してください（${rowLabel}）`, { variant: "error" });
      return;
    }
  }
  m.saving = true;
  render();
  const total = m.rows.length + m.confirmOnlyIds.length;
  let done = 0;
  let ok = 0;
  let lastErr = "";
  const failedRows = [];
  const failedConfirmIds = [];
  const markDone = (uiId) => {
    delete st.selected[uiId];
    // 相違マークの行を確認した＝相違は解決済み扱い／突合結果パネルの該当行も処理済みへ（従来の確認と同じ規則）
    if (st.rowMatch[uiId] === "mismatch") delete st.rowMatch[uiId];
    if (Array.isArray(st.matches)) for (const mm of st.matches) if (mm.costUiId === uiId) mm.done = true;
  };
  for (const r of m.rows) {
    showToastProgress(`受領済＋確認済みに変更中… ${++done} / ${total}件`);
    const cost = findCostPlacementByUiId(r.costId)?.cost;
    if (!cost) {
      failedRows.push(r);
      lastErr = "原価が見つかりません。再読み込みしてください";
      continue;
    }
    try {
      await persistCostToServerUpdate(
        cost.id,
        costReceiptUpdatePatch(cost, {
          invoiceNo: String(r.invoiceNo || "").trim(),
          targetMonth: normalizeCostTargetMonthForSave(r.targetMonth),
          category: r.category,
          dueDate: String(r.dueDate || "").trim()
        }),
        "keep"
      );
    } catch (e) {
      failedRows.push(r);
      lastErr = e instanceof Error && e.message ? e.message : "";
      continue;
    }
    // 受領は反映済み＝確認フラグだけ失敗しても、再実行は同じ値の上書きで安全
    const flagged = await submitCostReconcile(r.costId, true, false).catch(() => false);
    if (!flagged) {
      failedRows.push(r);
      continue;
    }
    ok++;
    markDone(r.costId);
  }
  for (const uiId of m.confirmOnlyIds) {
    showToastProgress(`確認済みに変更中… ${++done} / ${total}件`);
    const c = findCostPlacementByUiId(uiId)?.cost;
    const flagged = c ? await submitCostReconcile(uiId, true, c.isProvisional === true).catch(() => false) : false;
    if (!flagged) {
      failedConfirmIds.push(uiId);
      continue;
    }
    ok++;
    markDone(uiId);
  }
  if (failedRows.length || failedConfirmIds.length) {
    m.rows = failedRows;
    m.confirmOnlyIds = failedConfirmIds;
    m.saving = false;
    showToast(
      `確認して受領済: 成功 ${ok}件・失敗 ${failedRows.length + failedConfirmIds.length}件${lastErr ? `。${lastErr}` : ""}。失敗分はモーダルに残したので、原因解消後にそのまま再実行できます`,
      { variant: "error" }
    );
  } else {
    uiState.costReconcileReceiptModal = null;
    showToast(`確認して受領済にしました（${ok}件）`);
  }
  render();
}

/** 確認のみ（受領登録なし）: 請求書Noが無い原価や月次ロック済みの月のための従来経路。
 *  モーダルの全対象行に確認済みフラグだけ付ける（仮原価は従来どおり実原価へ昇格） */
async function submitCostReconcileConfirmOnly() {
  const m = uiState.costReconcileReceiptModal;
  if (!m || m.saving) return;
  const st = ensureCostReconcileState();
  m.saving = true;
  render();
  const targetIds = [...m.rows.map((r) => r.costId), ...m.confirmOnlyIds];
  let done = 0;
  let ok = 0;
  const failedIds = new Set();
  for (const uiId of targetIds) {
    showToastProgress(`確認済みに変更中… ${++done} / ${targetIds.length}件`);
    const c = findCostPlacementByUiId(uiId)?.cost;
    const flagged = c ? await submitCostReconcile(uiId, true, c.isProvisional === true).catch(() => false) : false;
    if (!flagged) {
      failedIds.add(uiId);
      continue;
    }
    ok++;
    delete st.selected[uiId];
    if (st.rowMatch[uiId] === "mismatch") delete st.rowMatch[uiId];
    if (Array.isArray(st.matches)) for (const mm of st.matches) if (mm.costUiId === uiId) mm.done = true;
  }
  if (failedIds.size) {
    m.rows = m.rows.filter((r) => failedIds.has(r.costId));
    m.confirmOnlyIds = m.confirmOnlyIds.filter((id) => failedIds.has(id));
    m.saving = false;
    showToast(`確認済みへの変更: 成功 ${ok}件・失敗 ${failedIds.size}件。失敗分はモーダルに残したので、原因解消後にそのまま再実行できます`, { variant: "error" });
  } else {
    uiState.costReconcileReceiptModal = null;
    showToast(`確認済みにしました（${ok}件・受領登録なし）`);
  }
  render();
}

/** 確認＋受領登録モーダル（原価照合ページ・行ごとの請求書No等を入力して一括で受領済＋確認済みへ） */
function renderCostReconcileReceiptModal() {
  const m = uiState.costReconcileReceiptModal;
  if (!m) return "";
  if (currentRoute().page !== "cost-reconcile") return "";
  // 列挙外の旧カテゴリ値も表示が実値とずれないよう選択肢に含める（保存時はそのまま送りサーバの正規化に任せる）
  const catOptsFor = (sel) =>
    (sel && !COST_CATEGORIES.includes(sel) ? [sel, ...COST_CATEGORIES] : COST_CATEGORIES)
      .map((cat) => `<option value="${escapeHtmlAttr(cat)}" ${sel === cat ? "selected" : ""}>${escapeHtml(cat)}</option>`)
      .join("");
  const dis = m.saving ? "disabled" : "";
  const vendorCounts = new Map();
  for (const r of m.rows) if (r.vendor) vendorCounts.set(r.vendor, (vendorCounts.get(r.vendor) || 0) + 1);
  // 「⇩」= その値を同じ支払先の行へコピー。同じ支払先が2行以上あるときだけ出す（発注先が空の行同士は束ねない）
  const copyBtn = (i, field) =>
    m.rows[i].vendor && vendorCounts.get(m.rows[i].vendor) > 1
      ? `<button type="button" class="cost-reconcile-copy-btn" data-cost-reconcile-copy-vendor="${i}:${field}" title="この値を同じ支払先（${escapeHtmlAttr(m.rows[i].vendor)}）の行へコピー" ${dis}>⇩</button>`
      : "";
  const rowsHtml = m.rows
    .map(
      (r, i) => `
      <tr>
        <td class="cost-reconcile-receipt-ro" title="${escapeHtmlAttr(r.note)}"><strong>${escapeHtml(r.vendor || "—")}</strong><div class="cost-reconcile-receipt-note">${escapeHtml(r.note.slice(0, 40) || "—")}</div></td>
        <td class="cost-reconcile-receipt-ro cost-reconcile-receipt-amt">${yen.format(r.amountIncl)}</td>
        <td><div class="cost-reconcile-receipt-field"><input class="pj-input" data-cost-reconcile-receipt-field="${i}:invoiceNo" value="${escapeHtmlAttr(String(r.invoiceNo || ""))}" placeholder="INV-0001" autocomplete="off" ${dis} />${copyBtn(i, "invoiceNo")}</div></td>
        <td><div class="cost-reconcile-receipt-field"><input class="pj-input pj-input-month" type="month" data-show-picker="1" data-search-dropdown-off="1" data-cost-reconcile-receipt-field="${i}:targetMonth" value="${escapeHtmlAttr(String(r.targetMonth || ""))}" ${dis} />${copyBtn(i, "targetMonth")}</div></td>
        <td><div class="cost-reconcile-receipt-field"><select class="pj-input dform-select" data-search-dropdown-off="1" data-cost-reconcile-receipt-field="${i}:category" ${dis}>${catOptsFor(r.category)}</select>${copyBtn(i, "category")}</div></td>
        <td><div class="cost-reconcile-receipt-field"><input class="pj-input" type="date" data-cost-reconcile-receipt-field="${i}:dueDate" value="${escapeHtmlAttr(String(r.dueDate || ""))}" ${dis} />${copyBtn(i, "dueDate")}</div></td>
      </tr>`
    )
    .join("");
  return `
    <div class="composer-backdrop" data-cost-reconcile-receipt-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--lg cost-reconcile-receipt-modal" role="dialog" aria-modal="true" aria-labelledby="cost-reconcile-receipt-title" style="grid-template-rows:auto auto auto;">
      <div class="composer-head">
        <div><h3 id="cost-reconcile-receipt-title">確認して受領済にする</h3>
        <p>請求書の内容を確認し、請求書No・計上月を入れて「受領済＋確認済み」にします。仮原価は実原価になります（＝消込）。</p></div>
      </div>
      <div class="composer-body">
        ${m.rows.length
          ? `<div class="cost-reconcile-receipt-table-wrap">
          <table class="cost-reconcile-receipt-table">
            <thead><tr><th>発注先／摘要</th><th>税込額</th><th>請求書No</th><th>計上月</th><th>会計区分</th><th>支払期限</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
        <p class="cost-reconcile-receipt-hint">⇩ … その値を同じ支払先の行へコピーします</p>`
          : ""}
        ${m.confirmOnlyIds.length ? `<p class="cost-reconcile-receipt-hint">受領済・支払済の ${m.confirmOnlyIds.length}件 は入力なしで確認済みだけ付けます。</p>` : ""}
      </div>
      <div class="composer-foot" style="justify-content:flex-end;gap:8px;">
        <button class="inline-btn ghost" type="button" data-cost-reconcile-receipt-close="1" ${m.saving ? "disabled" : ""}>キャンセル</button>
        ${m.rows.length
          ? `<button class="inline-btn ghost" type="button" data-cost-reconcile-receipt-confirm-only="1" ${m.saving ? "disabled" : ""} title="請求書Noが無い原価（口座振替等）や月次ロック済みの月はこちら。受領登録はせず確認済みフラグだけ付けます（従来の確認と同じ）">確認のみにする</button>`
          : ""}
        <button class="inline-btn" type="button" data-cost-reconcile-receipt-save="1" ${m.saving ? "disabled" : ""}>${m.saving ? "保存中…" : `受領済＋確認済みにする（${m.rows.length + m.confirmOnlyIds.length}件）`}</button>
      </div>
    </aside>`;
}

/** 突合結果の1行分HTML */
function costReconcileMatchRowHtml(m, i) {
  const amtLabel = `${yen.format(m.amount)}${m.taxMode === "inclusive" ? "（税込）" : m.taxMode === "exclusive" ? "（税抜）" : ""}`;
  const head = `<span class="cost-reconcile-match-summary">${escapeHtml(m.summary || "（摘要なし）")}</span>
    <span class="cost-reconcile-match-amt">${escapeHtml(amtLabel)}</span>
    <span class="cost-reconcile-match-payee">${escapeHtml(m.vendorLabel || m.payee || "請求元不明")}</span>`;
  if (m.done) {
    return `<div class="cost-reconcile-match-row cost-reconcile-match-row--done">${head}<span class="cost-reconcile-chip cost-reconcile-chip--ok">処理済み</span></div>`;
  }
  if (m.status === "match") {
    const cost = findCostPlacementByUiId(m.costUiId)?.cost;
    const already = !!cost?.reconciledAt;
    const isProv = cost?.isProvisional === true;
    const pending = cost ? costReceiptGroup(cost) === "pending" : false;
    return `<div class="cost-reconcile-match-row">${head}
      <span class="cost-reconcile-chip cost-reconcile-chip--ok">一致（登録額 ${yen.format(m.costAmount)}）${isProv ? "・仮原価" : ""}</span>
      ${already
        ? '<span class="cost-reconcile-chip">確認済み</span>'
        : `<button type="button" class="inline-btn" data-cost-reconcile-match-confirm="${i}">${pending ? "✓ 確認して受領済へ" : isProv ? "✓ 確認して実原価へ" : "✓ 確認済みにする"}</button>`}
    </div>`;
  }
  if (m.status === "mismatch") {
    return `<div class="cost-reconcile-match-row">${head}
      <span class="cost-reconcile-chip cost-reconcile-chip--warn">金額相違（登録額 税抜${yen.format(m.costAmount)}・税込${yen.format(m.costIncl)}）</span>
      <button type="button" class="inline-btn ghost" data-edit-cost="x:${escapeHtmlAttr(m.costUiId)}">編集</button>
      <button type="button" class="inline-btn ghost" data-cost-reconcile-remand="${i}">差し戻し</button>
    </div>`;
  }
  return `<div class="cost-reconcile-match-row">${head}
    <span class="cost-reconcile-chip cost-reconcile-chip--none">該当なし（未登録）</span>
    <button type="button" class="inline-btn ghost" data-cost-reconcile-register="${i}">原価を登録</button>
  </div>`;
}

/** ZIP内のPDFファイル名: 請求日(MMDD)_支払先_税抜額_案件。Windowsで開けない文字は _ に置換 */
function costReconcileZipEntryName(c) {
  const inv = String(c.invoiceDate || "").slice(0, 10);
  const mmdd = /^\d{4}-\d{2}-\d{2}$/.test(inv) ? inv.slice(5).replace("-", "") : String(c.targetMonth || "").replace(/[^0-9]/g, "").slice(0, 6);
  const parts = [mmdd, String(c.vendor || "支払先不明"), String(Math.round(Number(c.amount || 0)) || 0), String(c._projectName || "")];
  const base = parts
    .filter(Boolean)
    .join("_")
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${(base || "cost").slice(0, 80)}.pdf`;
}

/** CSVフィールドのエスケープ（カンマ・改行・引用符を含む場合は二重引用符で囲む） */
function costReconcileCsvField(v) {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** 対象月の原本PDFをまとめてZIPでダウンロード（対応表CSV付き・原本なしも明記） */
async function costReconcileDownloadOriginalsZip() {
  const st = ensureCostReconcileState();
  if (st.zipping) return;
  const rows = costReconcileMonthRows(st.month);
  if (!rows.length) {
    showToast("この月の原価がありません");
    return;
  }
  st.zipping = true;
  render();
  try {
    await ensurePizZipLoaded();
    const PizZipCtor = window.PizZip;
    if (!PizZipCtor) throw new Error("ZIPライブラリの読み込みに失敗しました");
    const zip = new PizZipCtor();
    // 同じ原本PDFに複数原価が紐づく（1枚の請求書→複数行）ため docId 単位で1回だけ取得する
    const docIdToName = new Map();
    const usedNames = new Set();
    const failedDocIds = new Set();
    const docIds = [];
    for (const c of rows) {
      const docId = Number(c.sourcePdfDocId);
      if (docId > 0 && !docIdToName.has(docId)) {
        docIdToName.set(docId, null);
        docIds.push({ docId, cost: c });
      }
    }
    let fetched = 0;
    for (const { docId, cost } of docIds) {
      showToastPersistent(`原本を取得しています… ${++fetched} / ${docIds.length}件`);
      try {
        const res = await fetch(`/api/cost-source-pdf?id=${encodeURIComponent(String(docId))}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (!res.ok) throw new Error(String(res.status));
        const buf = await res.arrayBuffer();
        let name = costReconcileZipEntryName(cost);
        for (let n = 2; usedNames.has(name); n++) name = costReconcileZipEntryName(cost).replace(/\.pdf$/, `_${n}.pdf`);
        usedNames.add(name);
        docIdToName.set(docId, name);
        zip.file(name, buf);
      } catch {
        failedDocIds.add(docId);
      }
    }
    // 対応表CSV: 原本なし・取得失敗も明記（月末チェックの道具を兼ねる）
    const header = ["No", "支払先", "案件", "摘要", "税抜", "消費税", "税込", "計上月", "確認状態", "差し戻し", "原本ファイル"];
    const lines = [header.join(",")];
    for (const c of rows) {
      const taxY = costConsumptionTaxYen(c);
      const exY = Math.round(Number(c.amount || 0)) || 0;
      const docId = Number(c.sourcePdfDocId);
      const docName =
        docId > 0
          ? failedDocIds.has(docId)
            ? "（取得失敗）"
            : docIdToName.get(docId) || "（取得失敗）"
          : "（原本なし）";
      lines.push(
        [
          costDbNumericId(c) ?? "",
          costReconcileCsvField(c.vendor || ""),
          costReconcileCsvField(c._projectName || ""),
          costReconcileCsvField(stripCostBucketMarkers(String(c.note || "").trim()).slice(0, 100)),
          exY,
          taxY,
          exY + taxY,
          costReconcileCsvField(c.targetMonth || ""),
          costReconcileIsMediaRow(c) ? "対象外（媒体費）" : c.reconciledAt ? "確認済み" : "未確認",
          c.remandNote ? "差し戻し中" : "",
          costReconcileCsvField(docName)
        ].join(",")
      );
    }
    // Excelで文字化けしないよう BOM 付き UTF-8 / CRLF
    zip.file("_一覧.csv", "﻿" + lines.join("\r\n"));
    let blob;
    try {
      blob = zip.generate({ type: "blob", mimeType: "application/zip" });
    } catch {
      blob = new Blob([zip.generate({ type: "uint8array" })], { type: "application/zip" });
    }
    clearToast();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `原価原本_${String(st.month || "").trim() || "all"}.zip`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    const nNoDoc = rows.filter((c) => !(Number(c.sourcePdfDocId) > 0)).length;
    showToast(
      `原本 ${usedNames.size}件をZIPにまとめました${nNoDoc ? `（原本なし ${nNoDoc}件はCSVに明記）` : ""}${failedDocIds.size ? `・取得失敗 ${failedDocIds.size}件` : ""}`
    );
  } catch (e) {
    clearToast();
    showToast(e instanceof Error ? e.message : "ZIPの作成に失敗しました", { variant: "error" });
  }
  st.zipping = false;
  render();
}

function costReconcilePage() {
  if (!costReconcileUserAllowed()) {
    return `
    <div class="page pj-page"><div class="pj-page-wrap">
      <div class="pj-head"><div class="pj-head-stack"><h2>原価照合</h2>
        <p class="pj-page-lede">このページは経理・マスター/管理者のみ利用できます。</p></div></div>
    </div></div>`;
  }
  const st = ensureCostReconcileState();
  const rows = costReconcileMonthRows(st.month);
  // 媒体費（自動連携）は照合対象外: 件数に数えず、一覧では末尾に「対象外」として表示（トグルで非表示可）
  const targetRows = rows.filter((c) => !costReconcileIsMediaRow(c));
  // 発注先フィルタが効いている間は、チップの件数・一覧末尾の媒体費もその発注先だけの内訳にする（一覧の絞り込みと揃える）
  const vendorScopedTargetRows = costReconcileScopeByVendor(targetRows, st.vendor);
  const mediaRows = costReconcileScopeByVendor(rows.filter((c) => costReconcileIsMediaRow(c)), st.vendor);
  const nChecked = vendorScopedTargetRows.filter((c) => c.reconciledAt).length;
  const nUnchecked = vendorScopedTargetRows.length - nChecked;
  const invStatus = (c) => costReconcileInvoiceStatus(c, st.rowMatch);
  const nNoInvoice = vendorScopedTargetRows.filter((c) => invStatus(c) === "no").length;
  const nMismatch = vendorScopedTargetRows.filter((c) => invStatus(c) === "mismatch").length;
  const filteredTargets = costReconcileFilteredTargetRows();
  // 並び替え: 既定は「未確認を先頭に・同状態内はNo順」。摘要/発注先ヘッダーをクリックしたらその列で並べる。
  // 媒体費は「すべて」表示のときだけ末尾にまとめる
  const sortKey = st.sortKey || "";
  const sortedTargets = [...filteredTargets].sort((a, b) => {
    if (sortKey === "note" || sortKey === "vendor") {
      const av =
        sortKey === "note"
          ? stripCostBucketMarkers(String(a.note || "").trim())
          : String(a.vendor || "").trim();
      const bv =
        sortKey === "note"
          ? stripCostBucketMarkers(String(b.note || "").trim())
          : String(b.vendor || "").trim();
      const cmp = av.localeCompare(bv, "ja");
      if (cmp !== 0) return st.sortDir === "desc" ? -cmp : cmp;
      return (costDbNumericId(a) || 0) - (costDbNumericId(b) || 0);
    }
    const ar = a.reconciledAt ? 1 : 0;
    const br = b.reconciledAt ? 1 : 0;
    if (ar !== br) return ar - br;
    return (costDbNumericId(a) || 0) - (costDbNumericId(b) || 0);
  });
  // 一括確認の選択状態: 表示条件から外れた行・確認済みになった行は選択から自動で外す
  const selectableIdSet = new Set(costReconcileSelectableRowIds());
  for (const k of Object.keys(st.selected)) if (!selectableIdSet.has(k)) delete st.selected[k];
  const selCount = Object.keys(st.selected).length;
  const allSelectableSelected = selectableIdSet.size > 0 && selCount === selectableIdSet.size;
  // 選択中の合計額（まとめて確認する前に、請求書の合計額と一致するか一目で見比べられるように）
  const selectedCosts = Object.keys(st.selected)
    .filter((k) => st.selected[k])
    .map((id) => findCostPlacementByUiId(id)?.cost)
    .filter(Boolean);
  const selSums = selectedCosts.reduce(
    (s, c) => {
      const ex = Number(c.amount) || 0;
      return { ex: s.ex + ex, incl: s.incl + ex + costConsumptionTaxYen(c) };
    },
    { ex: 0, incl: 0 }
  );
  const selSumEx = selSums.ex;
  const selSumIncl = selSums.incl;
  const showMedia = st.filter === "all" && !st.hideMedia;
  const sorted = showMedia
    ? sortedTargets.concat([...mediaRows].sort((a, b) => (costDbNumericId(a) || 0) - (costDbNumericId(b) || 0)))
    : sortedTargets;
  const chip = (key, label, n) =>
    `<button type="button" class="cost-reconcile-filter-chip ${st.filter === key ? "active" : ""}" data-cost-reconcile-filter="${key}">${label} <strong>${n}</strong></button>`;
  const matchesHtml = !st.matches
    ? ""
    : `<div class="cost-reconcile-matches soft-card">
        <div class="cost-reconcile-matches-head">
          <strong>突合結果</strong><span class="cost-reconcile-matches-src">${escapeHtml(st.matchSourceLabel || "")}</span>
          <button type="button" class="inline-btn ghost" data-cost-reconcile-clear-matches="1">結果をクリア</button>
        </div>
        ${st.matches.length ? st.matches.map((m, i) => costReconcileMatchRowHtml(m, i)).join("") : '<p class="cost-reconcile-empty">明細を読み取れませんでした</p>'}
      </div>`;
  const tbody = sorted.length === 0
    ? `<tr><td colspan="10" class="cost-empty">${rows.length === 0 ? "この月の原価はありません" : "該当する原価がありません"}</td></tr>`
    : sorted
        .map((c) => {
          const taxY = costConsumptionTaxYen(c);
          const exY = Number(c.amount || 0);
          const noteDisplay = stripCostBucketMarkers(String(c.note || "").trim());
          const dbNo = costDbNumericId(c);
          const hasDoc = Number(c.sourcePdfDocId) > 0;
          const isMedia = costReconcileIsMediaRow(c);
          const inv = isMedia ? null : invStatus(c);
          const isRealProject = projects.some((pp) => pp.id === c._projectId);
          const dlBtnHtml = hasDoc
            ? `<button type="button" class="inline-btn ghost" data-cost-download-source-pdf="${Number(c.sourcePdfDocId)}">請求書</button>`
            : "";
          const invoiceHtml = isMedia
            ? '<span class="cost-reconcile-chip">—</span>'
            : inv === "mismatch"
              ? `<span class="cost-reconcile-chip cost-reconcile-chip--warn">相違</span>${dlBtnHtml}`
              : inv === "yes"
                ? dlBtnHtml || '<span class="cost-reconcile-chip cost-reconcile-chip--ok">一致</span>'
                : '<span class="cost-reconcile-chip cost-reconcile-chip--none">なし</span>';
          const stateHtml = isMedia
            ? '<span class="cost-reconcile-chip cost-reconcile-chip--media" title="広告CSV・自動取込の媒体費は請求書照合の対象外です">対象外（自動連携）</span>'
            : `${c.reconciledAt
                ? '<span class="cost-reconcile-chip cost-reconcile-chip--ok">✓確認済</span>'
                : '<span class="cost-reconcile-chip cost-reconcile-chip--pending">未確認</span>'}${
                c.remandNote ? `<span class="cost-remand-badge" title="${escapeHtmlAttr(`差し戻し中: ${c.remandNote}`)}">差戻し中</span>` : ""}`;
          return `
        <tr class="${isMedia ? "cost-reconcile-row--media" : c.reconciledAt ? "cost-reconcile-row--checked" : ""}${c.isProvisional ? " cost-row-provisional" : ""}">
          <td class="cost-table-col-check">${
            isMedia || c.reconciledAt
              ? ""
              : `<input type="checkbox" class="cost-mgmt-row-check"${st.selected[c.id] ? " checked" : ""} data-cost-reconcile-row-check="${escapeHtmlAttr(c.id)}" aria-label="行を選択" />`
          }</td>
          <td class="cost-mgmt-no-cell">${dbNo != null ? dbNo : "—"}</td>
          <td class="cost-table-note-cell" title="${escapeHtmlAttr(noteDisplay)}">${noteDisplay ? escapeHtml(noteDisplay.slice(0, 60)) : "—"}</td>
          <td class="cost-vendor" title="${escapeHtmlAttr(c.vendor || "")}">${escapeHtml(c.vendor || "—")}</td>
          <td class="cost-table-project" title="${escapeHtmlAttr(c._projectName || "")}">${escapeHtml(c._projectName || "—")}${costOrderStatusBadgeHtml(c)}</td>
          <td class="cost-amount cost-amount--tax-view"><span class="cost-amt-total">${yen.format(exY)}</span>${taxY > 0 ? `<div class="cost-tax-sub">税込 ${yen.format(exY + taxY)}</div>` : ""}</td>
          <td class="cost-td-month">${escapeHtml(c.targetMonth || "—")}</td>
          <td class="cost-reconcile-doc-cell">${invoiceHtml}</td>
          <td class="cost-reconcile-state-cell">${stateHtml}</td>
          <td class="cost-actions">
            ${isMedia
              ? `<button type="button" class="inline-btn ghost" data-edit-cost="${escapeHtmlAttr(c._projectId)}:${escapeHtmlAttr(c.id)}">編集</button>`
              : `${c.reconciledAt
                  ? `<button type="button" class="inline-btn ghost" data-cost-reconcile-check="${escapeHtmlAttr(c.id)}:0">確認解除</button>`
                  : costReceiptGroup(c) === "pending"
                    ? `<button type="button" class="inline-btn" data-cost-reconcile-receipt-open="${escapeHtmlAttr(c.id)}">✓ 確認して受領済へ</button>`
                    : `<button type="button" class="inline-btn" data-cost-reconcile-check="${escapeHtmlAttr(c.id)}:1">${c.orderApprovalStatus === "申請中" ? "✓ 確認（承認前発注）" : c.orderApprovalStatus === "却下" ? "✓ 確認（発注却下・仮のまま）" : c.isProvisional ? "✓ 確認して実原価へ" : "✓ 確認"}</button>`}
            <button type="button" class="inline-btn ghost" data-edit-cost="${escapeHtmlAttr(c._projectId)}:${escapeHtmlAttr(c.id)}">編集</button>
            ${isRealProject && !c.remandNote
              ? `<button type="button" class="inline-btn ghost" data-cost-remand-open="${escapeHtmlAttr(c._projectId)}:${escapeHtmlAttr(c.id)}">差し戻し</button>`
              : ""}`}
          </td>
        </tr>`;
        })
        .join("");
  return `
    <div class="page pj-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>原価照合</h2>
            <p class="pj-page-lede"><a href="#cost-management">← 原価一覧へ戻る</a></p>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;">計上月
              <input type="month" class="pj-input" data-show-picker="1" data-cost-reconcile-month value="${escapeHtmlAttr(st.month || "")}" style="max-width:160px;" />
            </label>
            <button type="button" class="inline-btn" data-cost-reconcile-import-open="1" ${st.matching ? "disabled" : ""}>${st.matching ? "読み取り中…" : "📄 請求書と突合"}</button>
            <input type="file" accept=".pdf,.xlsx,.csv" multiple data-cost-reconcile-file style="display:none;" />
            <button type="button" class="inline-btn ghost" data-cost-reconcile-zip="1" ${st.zipping || rows.length === 0 ? "disabled" : ""} title="この月の原本PDFを1つのZIPにまとめてダウンロードします（対応表CSV付き・原本なしも明記）">${st.zipping ? "作成中…" : "原本まとめてDL"}</button>
          </div>
        </div>
        ${cardCostChecklistSectionHtml(st.month)}
        ${costOrderExceptionSummaryHtml(st.month)}
        <div class="pj-filter-area cost-reconcile-filter-area">
          ${matchesHtml}
          <div class="cost-reconcile-chips">
            ${chip("all", "すべて", vendorScopedTargetRows.length)}${chip("unchecked", "未確認", nUnchecked)}${chip("noinv", "請求書なし", nNoInvoice)}${chip("mismatch", "相違", nMismatch)}
            <label class="cost-reconcile-vendor-filter">発注先
              <select class="pj-input" data-cost-reconcile-vendor>${costMgmtVendorFilterOptionsHtml(targetRows, st.vendor)}</select>
            </label>
            <label class="cost-reconcile-media-toggle" title="広告CSV・自動取込の媒体費（照合対象外）の行を隠します">
              <input type="checkbox" data-cost-reconcile-hide-media ${st.hideMedia ? "checked" : ""} />媒体費を隠す（対象外・${mediaRows.length}件）
            </label>
            <span class="cost-reconcile-chips-note">確認済み ${nChecked} / ${vendorScopedTargetRows.length} 件</span>
          </div>
        </div>
        ${
          selCount > 0
            ? `<div class="pj-bulk-bar cost-mgmt-bulk-bar show">
          <span>${selCount}件選択中・合計 税抜${yen.format(selSumEx)}${selSumIncl !== selSumEx ? `（税込 ${yen.format(selSumIncl)}）` : ""}</span>
          <span class="sep" aria-hidden="true">|</span>
          <button type="button" class="inline-btn" data-cost-reconcile-bulk-receipt="1">✓ まとめて確認して受領済にする</button>
        </div>`
            : `<div class="pj-bulk-bar cost-mgmt-bulk-bar"></div>` /* 未選択は案内だけ（CSS）。位置を確保して表をずらさない */
        }
        <div class="pj-table-area">
          <table class="cost-table cost-table--global cost-table--reconcile" data-fp-table="cost-reconcile">
            <thead><tr>
              <th class="cost-table-col-check" scope="col">
                <input type="checkbox" class="cost-mgmt-row-check"${allSelectableSelected ? " checked" : ""} data-cost-reconcile-check-all="1" title="表示中の未確認行をすべて選択" aria-label="表示中の未確認行をすべて選択" />
              </th>
              <th scope="col">No</th>
              <th scope="col" class="cost-th-sortable${sortKey === "note" ? " cost-sort-active" : ""}" data-cost-reconcile-sort="note">摘要${sortKey === "note" ? (st.sortDir === "desc" ? " ▼" : " ▲") : ""}</th>
              <th scope="col" class="cost-th-sortable${sortKey === "vendor" ? " cost-sort-active" : ""}" data-cost-reconcile-sort="vendor">発注先${sortKey === "vendor" ? (st.sortDir === "desc" ? " ▼" : " ▲") : ""}</th>
              <th scope="col">案件</th>
              <th scope="col">金額（税抜）</th><th scope="col">計上月</th><th scope="col">請求書</th><th scope="col">状態</th>
              <th class="cost-table-col-actions" scope="col"><span class="cost-import-visually-hidden">操作</span></th>
            </tr></thead>
            <tbody>${tbody}</tbody>
          </table>
        </div>
      </div>
      ${costReconcileImportModalHtml(st)}
    </div>`;
}

/** 請求書と突合モーダル（取込方法の選び方を原価登録ウィザードと同じアイコンボタン式に。同じ原本を使うことが多いため） */
function costReconcileImportModalHtml(st) {
  if (!st.importOpen) return "";
  const fileBtn = (kind, mark, title, sub, cls) =>
    `<button type="button" class="cost-import-icon-btn ${cls}" data-cost-reconcile-import="${kind}" ${st.matching ? "disabled" : ""}>
      <span class="cost-import-icon-btn-mark" aria-hidden="true">${mark}</span>
      <span class="cost-import-icon-btn-title">${title}</span>
      <span class="cost-import-icon-btn-sub">${sub}</span>
    </button>`;
  return `
    <div class="composer-backdrop" data-cost-reconcile-import-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--lg cost-reconcile-paste-modal" role="dialog" aria-modal="true" aria-labelledby="cost-reconcile-import-title" style="grid-template-rows:auto auto auto;">
      <div class="composer-head">
        <div><h3 id="cost-reconcile-import-title">請求書と突合</h3><p>AIで読み取り、この月の原価と 業者名＋金額 で突き合わせます。</p></div>
      </div>
      <div class="composer-body">
        <div class="cost-import-icon-grid">
          ${fileBtn("pdf", "PDF", "PDF", "請求書・明細", "cost-import-icon-btn--misc-pdf")}
          ${fileBtn("excel", "XLSX", "Excel", "請求書・明細", "cost-import-icon-btn--misc-excel")}
          ${fileBtn("csv", "CSV", "CSV", "汎用データ", "cost-import-icon-btn--misc-csv")}
          ${fileBtn("paste", "貼", "コピペ取込", "AIで読み取り", `cost-import-icon-btn--paste-ai ${st.importMode === "paste" ? "active" : ""}`)}
        </div>
        ${st.importMode === "paste"
          ? `<textarea class="pj-input" rows="12" data-cost-reconcile-paste-text placeholder="請求書の内容を貼り付け" style="margin-top:12px;">${escapeHtml(st.pasteText || "")}</textarea>`
          : ""}
      </div>
      <div class="composer-foot" style="justify-content:flex-end;gap:8px;">
        <button class="inline-btn ghost" type="button" data-cost-reconcile-import-close="1">キャンセル</button>
        ${st.importMode === "paste" ? `<button class="inline-btn" type="button" data-cost-reconcile-paste-run="1">読み取って突合</button>` : ""}
      </div>
    </aside>`;
}

/** PDF/Excel/テキストをAIで抽出して突合結果を state に入れる */
async function costReconcileRunExtraction(bodyObj, sourceLabel) {
  const st = ensureCostReconcileState();
  if (st.matching) return;
  st.matching = true;
  const startMonth = st.month; // 読み取り中に計上月を変えたら、その結果は捨てる
  render();
  showToastPersistent("AIで請求書を読み取り中…");
  try {
    const res = await apiFetch("/api/cost-extract-pdf-ai", { method: "POST", body: bodyObj });
    clearToast();
    const aj = res.ok ? res.data : null;
    if (!aj || !aj.ok || !Array.isArray(aj.items) || !aj.items.length) {
      showToast(typeof aj?.error === "string" ? aj.error : "明細を読み取れませんでした", { variant: "error" });
      st.matching = false;
      render();
      return;
    }
    if (st.month !== startMonth) {
      // 前の月の請求書を新しい月の原価と突き合わせると、別の月の行に一致/相違や請求書Noが付いてしまう
      showToast("読み取り中に計上月が変わったため、突合結果を破棄しました。もう一度読み取ってください", { variant: "error" });
      st.matching = false;
      render();
      return;
    }
    const monthRows = costReconcileMonthRows(st.month);
    const classified = costReconcileClassifyItems(aj.items, aj.payee, monthRows);
    st.matches = Array.isArray(st.matches) ? st.matches.concat(classified) : classified;
    st.matchSourceLabel = [st.matchSourceLabel, sourceLabel].filter(Boolean).join("・");
    // 突合結果を原価行側にも反映（一覧の請求書列: 一致=あり／金額相違=相違。相違が優先）
    // 読み取れた請求書Noは行ごとに控え、受領登録モーダルの請求書No欄へプリフィルする
    const invNo = String(aj.invoiceNo || "").trim();
    for (const m of classified) {
      if (m.status === "match" && st.rowMatch[m.costUiId] !== "mismatch") st.rowMatch[m.costUiId] = "match";
      if (m.status === "mismatch") st.rowMatch[m.costUiId] = "mismatch";
      // 金額一致した行だけに控える（相違行は差し戻し・修正が先で、番号だけ正しく見える状態を作らない）
      if (invNo && m.status === "match") st.rowInvoiceNo[m.costUiId] = invNo;
    }
  } catch (e) {
    clearToast();
    showToast(e instanceof Error ? e.message : "読み取りに失敗しました", { variant: "error" });
  }
  st.matching = false;
  render();
}

// ── イベント（独立リスナー・原価照合ページのみ） ──
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (currentRoute().page !== "cost-reconcile") return;
  const st = ensureCostReconcileState();

  const filterBtn = t.closest("[data-cost-reconcile-filter]");
  if (filterBtn instanceof HTMLElement) {
    st.filter = filterBtn.getAttribute("data-cost-reconcile-filter") || "all";
    render();
    return;
  }
  if (t.closest("[data-cost-reconcile-import-open]")) {
    st.importOpen = true;
    st.importMode = "";
    st.pasteText = "";
    render();
    return;
  }
  if (t.closest("[data-cost-reconcile-import-close]")) {
    st.importOpen = false;
    render();
    return;
  }
  const importKindBtn = t.closest("[data-cost-reconcile-import]");
  if (importKindBtn instanceof HTMLElement) {
    const kind = importKindBtn.getAttribute("data-cost-reconcile-import") || "";
    if (kind === "paste") {
      st.importMode = "paste";
      render();
      return;
    }
    const inp = document.querySelector("[data-cost-reconcile-file]");
    if (inp instanceof HTMLInputElement) {
      // 選んだ種類に絞ってファイルピッカーを開く（読み分け自体は change ハンドラの拡張子判定のまま）
      inp.setAttribute("accept", kind === "pdf" ? ".pdf" : kind === "excel" ? ".xlsx" : ".csv");
      inp.click();
    }
    return;
  }
  if (t.closest("[data-cost-reconcile-zip]")) {
    void costReconcileDownloadOriginalsZip();
    return;
  }
  if (t.closest("[data-cost-reconcile-bulk-receipt]")) {
    openCostReconcileReceiptModal(Object.keys(st.selected).filter((k) => st.selected[k]));
    return;
  }
  const receiptOpenBtn = t.closest("[data-cost-reconcile-receipt-open]");
  if (receiptOpenBtn instanceof HTMLElement) {
    openCostReconcileReceiptModal([receiptOpenBtn.getAttribute("data-cost-reconcile-receipt-open") || ""]);
    return;
  }
  if (t.closest("[data-cost-reconcile-receipt-save]")) {
    void submitCostReconcileReceiptModal();
    return;
  }
  if (t.closest("[data-cost-reconcile-receipt-confirm-only]")) {
    void submitCostReconcileConfirmOnly();
    return;
  }
  if (t.closest("[data-cost-reconcile-receipt-close]")) {
    if (uiState.costReconcileReceiptModal?.saving) return;
    uiState.costReconcileReceiptModal = null;
    render();
    return;
  }
  const copyVendorBtn = t.closest("[data-cost-reconcile-copy-vendor]");
  if (copyVendorBtn instanceof HTMLElement) {
    const mm = uiState.costReconcileReceiptModal;
    if (mm?.saving) return;
    const raw = copyVendorBtn.getAttribute("data-cost-reconcile-copy-vendor") || "";
    const colon = raw.indexOf(":");
    const src = mm?.rows?.[Number(raw.slice(0, colon))];
    const field = raw.slice(colon + 1);
    if (!src || !COST_RECONCILE_RECEIPT_FIELDS.includes(field)) return;
    // 発注先が空の行同士を束ねない・空の値で入力済みの行を潰さない
    if (!src.vendor) return;
    if (!String(src[field] || "").trim()) {
      showToastDirect("コピー元の値が空です");
      return;
    }
    let n = 0;
    for (let j = 0; j < mm.rows.length; j++) {
      const r = mm.rows[j];
      if (r === src || r.vendor !== src.vendor) continue;
      r[field] = src[field];
      // フル再描画するとフォーカス・キャレットが飛ぶため、該当 input の値だけ直接書き換える（showToastDirect と同じ方針）
      const el = document.querySelector(`[data-cost-reconcile-receipt-field="${j}:${field}"]`);
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.value = r[field];
      n++;
    }
    showToastDirect(n ? `同じ支払先の${n}行にコピーしました` : "同じ支払先の行がありません");
    return;
  }
  // 摘要・発注先の並び替え（同じ列の再クリックで昇順⇄降順、3回目で既定順に戻る）
  const sortEl = t.closest("[data-cost-reconcile-sort]");
  if (sortEl instanceof HTMLElement) {
    const key = sortEl.getAttribute("data-cost-reconcile-sort") || "";
    if (st.sortKey !== key) {
      st.sortKey = key;
      st.sortDir = "asc";
    } else if (st.sortDir === "asc") {
      st.sortDir = "desc";
    } else {
      st.sortKey = ""; // 既定順（未確認先頭・No順）へ戻す
      st.sortDir = "asc";
    }
    render();
    return;
  }
  if (t.closest("[data-cost-reconcile-clear-matches]")) {
    st.matches = null;
    st.matchSourceLabel = "";
    st.rowInvoiceNo = {}; // 破棄した読み取り結果の請求書Noを後からプリフィルしない
    render();
    return;
  }
  if (t.closest("[data-cost-reconcile-paste-run]")) {
    const text = String(st.pasteText || "");
    if (!text.trim()) {
      showToast("貼り付け内容が空です");
      return;
    }
    st.importOpen = false;
    void costReconcileRunExtraction({ text }, "コピペ");
    return;
  }
  const checkBtn = t.closest("[data-cost-reconcile-check]");
  if (checkBtn instanceof HTMLElement) {
    const raw = checkBtn.getAttribute("data-cost-reconcile-check") || "";
    const colon = raw.lastIndexOf(":");
    const uiId = raw.slice(0, colon);
    const checked = raw.slice(colon + 1) === "1";
    const pl = findCostPlacementByUiId(uiId);
    const promote = checked && pl?.cost?.isProvisional === true;
    void submitCostReconcile(uiId, checked, promote).then((ok) => {
      if (ok) {
        // 相違マークが付いた行を経理が確認済みにした＝相違は解決済み扱いで消す
        if (checked && st.rowMatch[uiId] === "mismatch") delete st.rowMatch[uiId];
        showToast(checked ? (promote ? "確認し、実原価へ昇格しました" : "確認済みにしました") : "確認を解除しました");
        render();
      }
    });
    return;
  }
  const matchConfirmBtn = t.closest("[data-cost-reconcile-match-confirm]");
  if (matchConfirmBtn instanceof HTMLElement) {
    const idx = Number(matchConfirmBtn.getAttribute("data-cost-reconcile-match-confirm"));
    const m = st.matches && st.matches[idx];
    if (!m || m.status !== "match") return;
    const pl = findCostPlacementByUiId(m.costUiId);
    if (pl && costReceiptGroup(pl.cost) === "pending") {
      // 未受領なら受領登録つきモーダルへ（保存成功時にこの突合行も処理済みになる）
      openCostReconcileReceiptModal([m.costUiId]);
      return;
    }
    const promote = pl?.cost?.isProvisional === true;
    void submitCostReconcile(m.costUiId, true, promote).then((ok) => {
      if (ok) {
        m.done = true;
        showToast(promote ? "確認し、実原価へ昇格しました" : "確認済みにしました");
        render();
      }
    });
    return;
  }
  const remandBtn = t.closest("[data-cost-reconcile-remand]");
  if (remandBtn instanceof HTMLElement) {
    const idx = Number(remandBtn.getAttribute("data-cost-reconcile-remand"));
    const m = st.matches && st.matches[idx];
    if (!m || m.status !== "mismatch") return;
    const pl = findCostPlacementByUiId(m.costUiId);
    if (!pl) return;
    m.done = true;
    uiState.costRemandModal = {
      costUiId: m.costUiId,
      projectName: pl.project?.name || "",
      costLabel: `${stripCostBucketMarkers(String(pl.cost.note || "")).slice(0, 40) || pl.cost.vendor || "原価"}（${yen.format(Number(pl.cost.amount || 0))}）`,
      reason: `請求書の金額（${yen.format(m.amount)}${m.taxMode === "inclusive" ? " 税込" : m.taxMode === "exclusive" ? " 税抜" : ""}）と登録額（税抜${yen.format(m.costAmount)}）が一致しません`,
      saving: false
    };
    render();
    return;
  }
  const regBtn = t.closest("[data-cost-reconcile-register]");
  if (regBtn instanceof HTMLElement) {
    const idx = Number(regBtn.getAttribute("data-cost-reconcile-register"));
    const m = st.matches && st.matches[idx];
    if (!m || m.status !== "none") return;
    const a = Math.round(Number(m.amount || 0));
    const inclusive = m.taxMode === "inclusive";
    const ex = inclusive ? Math.round(a / 1.1) : a;
    const tax = inclusive ? a - ex : 0;
    m.done = true;
    uiState.costForm = {
      projectId: COST_UNASSIGNED_PROJECT_UI_ID,
      editingId: null,
      category: "制作費",
      vendor: m.vendorLabel || m.payee || "",
      amount: ex,
      consumptionTax: tax,
      targetMonth: String(st.month || "").replace("-", "/"),
      dueDate: "",
      paidDate: "",
      invoiceDate: "",
      paymentMethod: "",
      invoiceNo: "",
      withholdingTax: 0,
      // 照合は請求書を見ながらの登録なので 発注済・受領済 が既定（フォームで変更可）
      ...costRegisterDefaultStatuses(false),
      note: m.summary || "",
      sourcePdfDocId: null
    };
    render();
    return;
  }
});

document.addEventListener("change", (e) => {
  const t = e.target;
  if (currentRoute().page !== "cost-reconcile") return;
  const st = ensureCostReconcileState();
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-reconcile-month")) {
    st.month = t.value || "";
    st.matches = null;
    st.matchSourceLabel = "";
    st.rowMatch = {};
    st.rowInvoiceNo = {};
    st.selected = {};
    render();
    return;
  }
  // 一括確認のチェックボックス（行・全選択）
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-reconcile-row-check")) {
    const uiId = t.getAttribute("data-cost-reconcile-row-check") || "";
    if (t.checked) st.selected[uiId] = true;
    else delete st.selected[uiId];
    render(); // 選択件数バーの表示を更新
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-reconcile-check-all")) {
    if (t.checked) for (const id of costReconcileSelectableRowIds()) st.selected[id] = true;
    else st.selected = {};
    render();
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-reconcile-hide-media")) {
    st.hideMedia = t.checked;
    render();
    return;
  }
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-reconcile-vendor")) {
    st.vendor = t.value || "";
    render();
    return;
  }
  if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-reconcile-file")) {
    const files = Array.from(t.files || []);
    t.value = "";
    if (!files.length) return;
    st.importOpen = false; // ファイルを選んだら突合モーダルは閉じて読み取りへ
    void (async () => {
      for (const file of files) {
        if (file.size > 4 * 1024 * 1024) {
          showToast(`${file.name} が大きすぎます（4MBまで）`, { variant: "error" });
          continue;
        }
        if (/\.csv$/i.test(file.name || "")) {
          // CSVはExcelのSJIS書き出しがあるため readCsvFileText の文字コード自動判定に乗せる
          const text = await readCsvFileText(file);
          await costReconcileRunExtraction({ text }, file.name || "CSV");
          continue;
        }
        const buf = await file.arrayBuffer();
        const base64 = arrayBufferToBase64(buf);
        const isXlsx = /\.xlsx$/i.test(file.name || "");
        await costReconcileRunExtraction(
          isXlsx ? { xlsxBase64: base64, fileName: file.name || "invoice.xlsx" } : { pdfBase64: base64, fileName: file.name || "invoice.pdf" },
          file.name || (isXlsx ? "Excel" : "PDF")
        );
      }
    })();
  }
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (currentRoute().page !== "cost-reconcile") return;
  if (t instanceof HTMLTextAreaElement && t.hasAttribute("data-cost-reconcile-paste-text")) {
    ensureCostReconcileState().pasteText = t.value;
    return;
  }
  // 受領登録モーダルの入力値は state にだけ持つ（保存ボタンでまとめて反映。select・type=month も input を発火する）
  if ((t instanceof HTMLInputElement || t instanceof HTMLSelectElement) && t.hasAttribute("data-cost-reconcile-receipt-field")) {
    const m = uiState.costReconcileReceiptModal;
    const raw = t.getAttribute("data-cost-reconcile-receipt-field") || "";
    const colon = raw.indexOf(":");
    const r = m?.rows?.[Number(raw.slice(0, colon))];
    const field = raw.slice(colon + 1);
    if (r && COST_RECONCILE_RECEIPT_FIELDS.includes(field)) r[field] = t.value;
  }
});
// ────────────────────────────────────────────────────────────────
// 案件一覧「押印申請」列の書類別パネル（開閉のみを担当）
// 集約バッジ（07-pdf-output.js projectTableSealCellHtml）をクリックすると
// 捺印対象書類（DOC_SEAL_TAB_LABELS）の状態一覧パネルを開く。
// パネル内の「申請する」(data-pj-seal-apply) と書類タブ遷移 (data-project-doc-tab) は
// 16-render-routing.js の既存ハンドラが処理する。
// ────────────────────────────────────────────────────────────────

/** パネルを上向きに開くか。表の枠（縦スクロールする .pj-table-area）の下端までにパネル1枚分の高さが無く、
 *  上のほうが広いときは上向き（下の行で開くとパネルが枠の下で切れて見えなかった。2026-10 指摘）。
 *  パネルの高さは行数（捺印対象の書類数）から見積もる */
function pjSealPanelShouldOpenUp(toggleEl) {
  const box = toggleEl.closest(".pj-table-area");
  if (!box) return false;
  const b = toggleEl.getBoundingClientRect();
  const r = box.getBoundingClientRect();
  const panelH = Object.keys(DOC_SEAL_TAB_LABELS).length * 46 + 12;
  const below = r.bottom - b.bottom;
  const above = b.top - r.top;
  return below < panelH && above > below;
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "projects") return;

  const toggleEl = target.closest("[data-pj-seal-panel-toggle]");
  if (toggleEl instanceof HTMLElement) {
    const pid = toggleEl.getAttribute("data-pj-seal-panel-toggle") || "";
    uiState.pjSealPanelProjectId = String(uiState.pjSealPanelProjectId || "") === pid ? "" : pid;
    if (uiState.pjSealPanelProjectId) uiState.pjSealPanelUp = pjSealPanelShouldOpenUp(toggleEl);
    render();
    return;
  }
  // パネル外クリックで閉じる（パネル内の申請ボタン・書類リンクは開いたまま既存ハンドラに任せる）
  if (!uiState.pjSealPanelProjectId) return;
  if (target.closest("[data-pj-seal-panel]")) return;
  uiState.pjSealPanelProjectId = "";
  render();
});
// ───────────────────────────────────────────────
// 売上分配（営業別集計の按分）
// 案件の売上を複数営業に比率で分配する設定。あくまで予算進捗の営業別集計
// （売上・差益）の按分にだけ使い、請求金額・書類・担当営業(salesOwnerId)・
// 通知・原価データには一切影響しない。
// 未設定＝担当営業100%（既存の挙動そのまま）。
// ───────────────────────────────────────────────

/** GET 応答の sales_split_json → フロント形式 [{userId:"u5", ratio:3}] */
function normalizeClientSalesSplit(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e) => e && typeof e === "object" && Number(e.userId) > 0 && Number(e.ratio) > 0)
    .map((e) => ({ userId: `u${Number(e.userId)}`, ratio: Number(e.ratio) }));
}

/** PUT/POST body 用: フロント形式 → サーバー形式 [{userId:5, ratio:3}]。空なら null（＝解除） */
function salesSplitToServerBody(split) {
  if (!Array.isArray(split)) return null;
  const out = split
    .map((e) => ({
      userId: Number(String(e?.userId || "").replace(/^u/, "")),
      ratio: Number(e?.ratio)
    }))
    .filter((e) => Number.isFinite(e.userId) && e.userId > 0 && Number.isFinite(e.ratio) && e.ratio > 0);
  return out.length > 0 ? out : null;
}

/** 案件の請求月が属する会計期の予算マスタ期。見つからなければ予算進捗で選択中（無ければ最新）の期。
 *  按分の突合相手は「その期の予算マスタ行」なので、候補もその期に合わせる */
function salesSplitBudgetPeriodForProject(p) {
  ensureBudgetPeriodsState();
  const periods = Array.isArray(uiState.budgetPeriods) ? uiState.budgetPeriods : [];
  if (periods.length === 0) return null;
  const yyyymm = parseProjectBillingMonthYyyymm(p?.billingMonth);
  if (yyyymm != null) {
    const fyStart = fiscalYearStartFromBillingYyyymm(yyyymm);
    const hit = periods.find((period) => fiscalYearStartFromBudgetPeriod(period) === fyStart);
    if (hit) return hit;
  }
  return (
    periods.find((period) => String(period.id) === String(uiState.selectedBudgetPeriodId || "")) ||
    latestBudgetPeriodFromSorted(periods)
  );
}

/** 対象期の予算マスタ行の担当者名。未取得なら取得だけ促して空を返す（完了時に render() されて出直す）。
 *  ensureBudgetRowsForSelectedPeriod は未取得時に行を seed してサーバーへ保存しに行くため、
 *  案件詳細の描画からは呼ばない。ここは読むだけに徹する */
function salesSplitBudgetMemberNames(p) {
  const period = salesSplitBudgetPeriodForProject(p);
  if (!period?.id) return [];
  if (budgetRowsLoadFailedForPeriod(period.id)) return [];
  if (!budgetRowsLoadedForPeriod(period.id)) {
    void loadBudgetRowsForPeriod(period.id);
    return [];
  }
  const rows = readBudgetDraftStore()[period.id]?.rows;
  return (Array.isArray(rows) ? rows : [])
    .map((r) => String(r?.member || "").trim())
    .filter(Boolean);
}

/** 分配先の候補と、予算マスタで絞り込めたかどうか。
 *  予算進捗の集計（buildBudgetProgressGroupsForSelectedPeriod）は予算マスタ行の担当者名と
 *  一致した分しか加算しないため、マスタに居ない人へ配ると按分分が全体合計から黙って消える。
 *  突合は集計と同じ budgetMasterMemberNameMatchesProject を使い、候補と集計を必ず一致させる。
 *  マスタが未取得・取得失敗・権限なし・0件のときは restricted:false で絞り込まない
 *  （選択肢ゼロで詰ませない。従来どおり全候補が出る） */
function salesSplitCandidateInfo(p) {
  const pool = projectAssignCandidateUsers();
  const memberNames = salesSplitBudgetMemberNames(p);
  if (memberNames.length === 0) {
    return { pool, restricted: false, inBudgetMaster: () => true };
  }
  const inBudgetMaster = (u) => {
    const key = userNameWithRetiredLabel(u);
    return memberNames.some((name) => budgetMasterMemberNameMatchesProject(key, name));
  };
  return { pool: pool.filter(inBudgetMaster), restricted: true, inBudgetMaster };
}

/** 集計・プレビューが実際に使う「有効な分配先」。比率が正で、実在ユーザーの行だけ。
 *  ここを一本化しないと、画面のプレビュー額と予算進捗の集計額が別ルールでズレる */
function validSalesSplitEntries(split) {
  return Array.isArray(split)
    ? split.filter((s) => s && Number(s.ratio) > 0 && getUserById(s.userId))
    : [];
}

/** 総額(円)を比率で按分する。円未満の端数は筆頭（担当営業。分配に居なければ先頭）へ寄せ、
 *  按分合計＝総額を厳守する。マイナス額（赤字の差益）でも合計は保存される。 */
function apportionSalesSplitYen(totalYen, split, leadUserId) {
  const total = Math.round(Number(totalYen) || 0);
  const ratios = split.map((s) => Number(s.ratio) || 0);
  const ratioSum = ratios.reduce((a, b) => a + b, 0);
  if (!(ratioSum > 0)) return split.map(() => 0);
  const shares = ratios.map((r) => Math.trunc((total * r) / ratioSum));
  const distributed = shares.reduce((a, b) => a + b, 0);
  let leadIdx = split.findIndex((s) => String(s.userId) === String(leadUserId));
  if (leadIdx < 0) leadIdx = 0;
  shares[leadIdx] += total - distributed;
  return shares;
}

/** 予算進捗の営業別集計用: 案件1件を「担当者キー×売上×差益」の配分に展開する。
 *  分配設定があれば比率按分（原価・差益も同比率）、無ければ従来どおり担当営業100%。 */
function budgetProgressSalesSharesForProject(p, salesAmt, profitAmt) {
  const split = validSalesSplitEntries(p.salesSplit);
  if (split.length === 0) {
    const memberKey = budgetProgressMemberKeyForProject(p);
    return memberKey ? [{ memberKey, sales: salesAmt, profit: profitAmt }] : [];
  }
  const salesShares = apportionSalesSplitYen(salesAmt, split, p.salesOwnerId);
  const profitShares = apportionSalesSplitYen(profitAmt, split, p.salesOwnerId);
  return split.map((s, i) => ({
    memberKey: userNameWithRetiredLabel(getUserById(s.userId)),
    sales: salesShares[i],
    profit: profitShares[i]
  }));
}

/** 案件詳細・概要カード内の「売上分配」編集ブロック */
function buildProjectSalesSplitEditorHtml(p) {
  const split = Array.isArray(p.salesSplit) ? p.salesSplit : [];
  const { pool, inBudgetMaster } = salesSplitCandidateInfo(p);
  const poolIds = new Set(pool.map((u) => String(u.id)));
  const userOptionsHtml = (selectedId) => {
    let html = pool
      .map(
        (u) =>
          `<option value="${escapeHtmlAttr(u.id)}" ${String(selectedId) === String(u.id) ? "selected" : ""}>${escapeHtml(u.name)}</option>`
      )
      .join("");
    // 分配済みだが候補プールに居ない（退職・予算マスタ未登録）ユーザーも選択肢として残す。
    // 消すと既存の設定を勝手に書き換えることになるので、外さずに理由を添えて見せる
    if (selectedId && !poolIds.has(String(selectedId))) {
      const extra = getUserById(selectedId);
      if (extra) {
        const note = inBudgetMaster(extra) ? "" : "（予算マスタ未登録・予算進捗に反映されません）";
        html += `<option value="${escapeHtmlAttr(String(extra.id))}" selected>${escapeHtml(userNameWithRetiredLabel(extra) + note)}</option>`;
      }
    }
    return html;
  };
  const rowsHtml = split
    .map(
      (s, i) => `
      <div class="pd-sales-split-row">
        <select class="dform-select pd-sales-split-user" data-search-dropdown-off="1" data-sales-split-user="${escapeHtmlAttr(p.id)}" data-split-index="${i}">
          ${userOptionsHtml(s.userId)}
        </select>
        <input class="dform-input pd-sales-split-ratio" type="number" min="1" step="1" inputmode="numeric"
               value="${escapeHtmlAttr(String(s.ratio ?? ""))}" data-sales-split-ratio="${escapeHtmlAttr(p.id)}" data-split-index="${i}" aria-label="分配比率" />
        <button type="button" class="dform-tag-chip-remove" data-sales-split-remove="${escapeHtmlAttr(p.id)}" data-split-index="${i}" title="この分配先を外す">×</button>
      </div>`
    )
    .join("");
  const previewHtml = (() => {
    const valid = validSalesSplitEntries(split);
    if (valid.length === 0) return "";
    const totalYen = projectDisplayedBillingExTaxYen(p);
    const ratioLabel = valid.map((s) => Number(s.ratio)).join(":");
    if (Math.round(Number(totalYen) || 0) === 0) {
      return `<div class="pd-sales-split-preview">比率 ${escapeHtml(ratioLabel)}（税抜請求額が0円のため配分額はありません）</div>`;
    }
    const shares = apportionSalesSplitYen(totalYen, valid, p.salesOwnerId);
    const parts = valid.map((s, i) => `${escapeHtml(getUserById(s.userId)?.name || "?")} ${yen.format(shares[i])}`);
    return `<div class="pd-sales-split-preview">税抜請求 ${yen.format(totalYen)} を ${escapeHtml(ratioLabel)} で按分 → ${parts.join(" / ")}<span class="pd-sales-split-preview-note">（端数は担当営業へ）</span></div>`;
  })();
  return `
    <div class="pd-sales-split" data-sales-split-box="${escapeHtmlAttr(p.id)}">
      ${split.length === 0 ? `<div class="dform-tag-empty">未設定（担当営業100%）</div>` : rowsHtml}
      ${previewHtml}
      <button type="button" class="inline-btn ghost" data-sales-split-add="${escapeHtmlAttr(p.id)}">＋ 分配先を追加</button>
    </div>`;
}

/** 分配編集イベント（案件詳細）。既存の巨大 click ハンドラには足さず、ここで独立して拾う */
document.addEventListener("click", (e) => {
  const addBtn = e.target.closest?.("[data-sales-split-add]");
  if (addBtn) {
    const proj = projects.find((x) => x.id === addBtn.dataset.salesSplitAdd);
    if (!proj) return;
    if (!Array.isArray(proj.salesSplit)) proj.salesSplit = [];
    const used = new Set(proj.salesSplit.map((s) => String(s.userId)));
    // 1行目は担当営業から。以降は候補プールの未使用ユーザーを先頭から
    let nextId = !used.has(String(proj.salesOwnerId)) && getUserById(proj.salesOwnerId) ? proj.salesOwnerId : "";
    if (!nextId) {
      // プルダウンと同じ候補から採る。ここだけ絞らないと、選び直さない限り
      // 予算マスタに居ない相手が既定で入り、按分分が集計から消える
      const { pool: candidates, restricted } = salesSplitCandidateInfo(proj);
      const candidate = candidates.find((u) => !used.has(String(u.id)));
      if (!candidate) {
        showToast(
          restricted
            ? "追加できる分配先がいません（予算マスタに登録されている担当者のみ選べます）"
            : "追加できる分配先がいません",
          { variant: "error" }
        );
        return;
      }
      nextId = candidate.id;
    }
    proj.salesSplit = [...proj.salesSplit, { userId: nextId, ratio: 1 }];
    render();
    return;
  }
  const removeBtn = e.target.closest?.("[data-sales-split-remove]");
  if (removeBtn) {
    const proj = projects.find((x) => x.id === removeBtn.dataset.salesSplitRemove);
    if (!proj || !Array.isArray(proj.salesSplit)) return;
    const idx = Number(removeBtn.dataset.splitIndex);
    proj.salesSplit = proj.salesSplit.filter((_, i) => i !== idx);
    render();
  }
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.dataset.salesSplitUser) {
    const proj = projects.find((x) => x.id === target.dataset.salesSplitUser);
    const idx = Number(target.dataset.splitIndex);
    if (!proj || !Array.isArray(proj.salesSplit) || !proj.salesSplit[idx]) return;
    // 同一ユーザーの重複行は作らせない（保存時にサーバーが先勝ちで落とすため、先に見た目で防ぐ）
    const dup = proj.salesSplit.some((s, i) => i !== idx && String(s.userId) === String(target.value));
    if (dup) {
      showToast("同じ担当者が既に分配先にいます", { variant: "error" });
      render();
      return;
    }
    proj.salesSplit[idx] = { ...proj.salesSplit[idx], userId: target.value };
    render();
    return;
  }
  if (target.dataset.salesSplitRatio) {
    const proj = projects.find((x) => x.id === target.dataset.salesSplitRatio);
    const idx = Number(target.dataset.splitIndex);
    if (!proj || !Array.isArray(proj.salesSplit) || !proj.salesSplit[idx]) return;
    const ratio = Number(target.value);
    proj.salesSplit[idx] = {
      ...proj.salesSplit[idx],
      ratio: Number.isFinite(ratio) && ratio > 0 ? ratio : 0
    };
    render();
  }
});

// ───────────────────────────────────────────────
// その他（貢献度）＝案件に関わったクリエイティブ職のメンバー（旧データ。編集欄は基本情報の
// カテゴリ担当（15l-project-work-categories.js）へ移した。読込・保存の変換と候補の絞り込みは共用）
// 比率は持たず「誰が関わったか」だけを記録する表示専用の設定（予算マスタ・予算進捗の
// 集計には一切影響しない）。金額は経営ダッシュボード「クリエイティブ実績」タブで、
// 案件の粗利（projectGrossMarginSummary と同値）をそのまま案件単位で表示する（人数で割らない）。
// 編集は基本情報のカテゴリ担当（15l-project-work-categories.js）で行う。
// ───────────────────────────────────────────────

/** GET 応答の non_sales_split_json（userId の配列）→ フロント形式 ["u5","u12"]。
 *  要素は素の数値のほか { userId, ratio } 形式（本機能が比率式だった 2026-08-31 の
 *  短時間、本番がこの形で書いていた）も受け付け userId だけ取り出す。サーバー側の
 *  normalizeNonSalesInvolvedIds が同じ両形状を受けるのと**対**。ここを素の Number(e)
 *  だけにすると旧形状の行が NaN で全件落ち、画面は「未設定」に見えたまま次の保存で
 *  null 上書き＝既存の貢献度データが無言で消える */
function normalizeClientNonSalesInvolved(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const e of raw) {
    const userId = Number(e && typeof e === "object" ? e.userId : e);
    if (!Number.isInteger(userId) || userId <= 0 || seen.has(userId)) continue;
    seen.add(userId);
    out.push(`u${userId}`);
  }
  return out;
}

/** PUT/POST body 用: フロント形式 → サーバー形式 [5,12]。空なら null（＝解除） */
function nonSalesInvolvedToServerBody(list) {
  if (!Array.isArray(list)) return null;
  const seen = new Set();
  const out = [];
  for (const e of list) {
    const userId = Number(String(e ?? "").replace(/^u/, ""));
    if (!Number.isInteger(userId) || userId <= 0 || seen.has(userId)) continue;
    seen.add(userId);
    out.push(userId);
  }
  return out.length > 0 ? out : null;
}

/** 関連担当タブの中身: 営業（売上分配）。旧「その他（貢献度）」は基本情報のカテゴリ担当へ移した
 *  （15l-project-work-categories.js） */
function buildProjectSplitEditorsHtml(p) {
  return `
    <div class="pd-split-axes">
      <div class="pd-split-axis-label">営業（売上分配）</div>
      ${buildProjectSalesSplitEditorHtml(p)}
    </div>`;
}
// ────────────────────────────────────────────────────────────────
// 書類のメール送信（メール作成画面）と、送るボタンの書類選択メニュー。
//
// 書類プレビュー下部の「メール送信」「郵送する」を押すと、まず何を送るかを選ぶメニューが出る。
// メールを選ぶと専用ページ（#project-mail:<案件ID>?docs=invoice,delivery）へ遷移し、
// 宛先・件名・本文・送付方法（ダウンロードページ／メール添付）・送信タイミングを確認して送る。
// 実送信とガードはサーバー（api/_docSend.js）が持つ。ここは入力と確認だけ。
//
// イベントは 16-render-routing.js の巨大ハンドラに足さず、このファイルの独立リスナーで処理する。
// ────────────────────────────────────────────────────────────────

/** メールで送れる書類種別（送付状は単体で送るものではないので含めない）。
 *  サーバー側の許可リストは api/_docRender.js DOC_LABEL。値を増やすときは両方を見る。 */
const DOC_MAIL_TYPE_LABELS = {
  invoice: "請求書",
  quote: "見積書",
  order: "発注書",
  acceptance: "発注請書",
  delivery: "納品書",
  inspection: "検収書",
  receipt: "領収書",
};

/** 「送る」メニューによく出る組み合わせ。先頭は必ず「いま開いている書類」単体。 */
const DOC_MAIL_PRESETS = {
  invoice: [["invoice", "delivery"], ["invoice", "delivery", "inspection"]],
  quote: [["quote", "order"]],
  delivery: [["delivery", "inspection"]],
  order: [["order", "acceptance"]],
};

// 任意ファイル添付（サーバー側の上限は api/mail-attachment.js。チャンクサイズは両側で揃えること）
const MAIL_ATTACH_CHUNK_BYTES = 3 * 1024 * 1024;
const MAIL_ATTACH_MAX_BYTES = 15 * 1024 * 1024;
const MAIL_ATTACH_MAX_FILES = 5;
const MAIL_ATTACH_EXTS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".csv", ".txt", ".zip", ".png", ".jpg", ".jpeg"];
const MAIL_ATTACH_EXT_HINT = "PDF・Word・Excel・PowerPoint・CSV・テキスト・ZIP・画像（PNG/JPEG）";

/** 送信予約できる先の上限（サーバー api/doc-mail-queue.js の MAX_SCHEDULE_DAYS と揃えること）。
 *  無期限に仕込めると、退職・権限剥奪の後にも書類メールが客先へ出てしまう。 */
const DOC_MAIL_MAX_SCHEDULE_DAYS = 30;

/** datetime-local の max 属性用（ローカル時刻の "YYYY-MM-DDTHH:mm"）。 */
function docMailMaxScheduleValue() {
  const d = new Date(Date.now() + DOC_MAIL_MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 書類送付メールの差出人名（サーバー api/_docSend.js の DOC_MAIL_FROM_NAME と一致させること）。
 *  実際に差出人名を決めるのはサーバー側で、ここは画面の「送信元」表示用。 */
const DOC_MAIL_FROM_NAME = "株式会社ワンエイティ";

/** 本文・件名に使える差込タグ（サーバー api/_docSend.js の vars と一致させること） */
const DOC_MAIL_MERGE_TAGS = [
  { tag: "last_name", label: "担当者名" },
  { tag: "client_name", label: "顧客名" },
  { tag: "project_name", label: "案件名" },
  { tag: "doc_names", label: "書類名" },
  { tag: "download_url", label: "ダウンロードURL" },
];

function docMailTypeLabel(t) {
  return DOC_MAIL_TYPE_LABELS[t] || "書類";
}

function docMailTypesLabel(types) {
  return (types || []).map(docMailTypeLabel).join("＆");
}

// ── 「送る」メニュー（メール送信 / 郵送する） ──

/** メニューの中身。kind は "mail" | "postal"。郵送は請求書系のみ（封書に入れて送る前提）。 */
function docSendMenuOptions(activeTab) {
  const base = Object.prototype.hasOwnProperty.call(DOC_MAIL_TYPE_LABELS, activeTab) ? activeTab : "invoice";
  return [[base], ...(DOC_MAIL_PRESETS[base] || [])];
}

// 位置の再計算に使う（「その他」を開いて高さが変わったとき・画面サイズが変わったとき）
let docSendMenuAnchorEl = null;

function closeDocSendMenu() {
  document.getElementById("docSendMenu")?.remove();
  docSendMenuAnchorEl = null;
  document.removeEventListener("keydown", docSendMenuEscHandler);
  window.removeEventListener("resize", positionDocSendMenu);
}

function docSendMenuEscHandler(e) {
  if (e.key === "Escape") closeDocSendMenu();
}

/**
 * メニューをボタンの上（入らなければ下）に置き、画面に収まる高さへ制限する。
 * 「その他」を開くと高さが伸びるため、開閉のたびに呼び直すこと
 * （開いた時点の高さで固定すると、伸びた分が画面外にはみ出して見切れる）。
 */
function positionDocSendMenu() {
  const menu = document.getElementById("docSendMenu");
  if (!menu || !docSendMenuAnchorEl) return;
  const MARGIN = 8; // 画面端との余白
  const GAP = 8;    // ボタンとの隙間
  const vh = window.innerHeight;
  const r = docSendMenuAnchorEl.getBoundingClientRect();
  // ボタンが画面外にあることもある（下にスクロールしていない等）ので、余白は可視領域に丸めて測る
  const spaceAbove = Math.min(r.top, vh) - GAP - MARGIN;
  const spaceBelow = vh - Math.max(r.bottom, 0) - GAP - MARGIN;

  menu.style.maxHeight = ""; // 自然な高さを測るため一旦解除
  const natural = menu.offsetHeight;
  // 書類フッターは画面下端にあるので上に出すのが基本。入りきらなければ広いほうへ逃がす
  const above = natural <= spaceAbove || spaceAbove >= spaceBelow;
  // どちらに出しても、メニュー自体が画面の高さを超えないようにする
  const maxHeight = Math.min(vh - MARGIN * 2, Math.max(120, above ? spaceAbove : spaceBelow));
  menu.style.maxHeight = `${maxHeight}px`;

  const height = Math.min(natural, maxHeight);
  const top = above ? r.top - GAP - height : r.bottom + GAP;
  // 最後に必ず画面内へ収める（ボタンの位置に関係なく上下がはみ出さない）
  menu.style.top = `${Math.min(Math.max(MARGIN, top), vh - MARGIN - height)}px`;
  const width = menu.offsetWidth || 220;
  menu.style.left = `${Math.max(MARGIN, Math.min(r.left, window.innerWidth - width - MARGIN))}px`;
}

/** 送るボタンの上にメニューを出す（位置と高さは positionDocSendMenu が画面内に収める）。 */
function openDocSendMenu(anchorEl, kind, projectId, activeTab) {
  closeDocSendMenu();
  const options = docSendMenuOptions(activeTab);
  const title = kind === "postal" ? "郵送" : "メール送信";
  const menu = document.createElement("div");
  menu.id = "docSendMenu";
  menu.className = "doc-send-menu";
  menu.setAttribute("role", "menu");
  menu.dataset.docSendKind = kind;
  menu.dataset.docSendProject = projectId;
  menu.innerHTML = `
    <div class="doc-send-menu__head">${escapeHtml(title)}</div>
    ${options.map((types) => `
      <button type="button" class="doc-send-menu__item" role="menuitem" data-doc-send-pick="${escapeHtmlAttr(types.join(","))}">
        ${escapeHtml(docMailTypesLabel(types))}
      </button>`).join("")}
    <button type="button" class="doc-send-menu__item doc-send-menu__item--more" role="menuitem" data-doc-send-more="1">
      その他<span class="doc-send-menu__caret" aria-hidden="true">▸</span>
    </button>
    <div class="doc-send-menu__others" hidden>
      <p class="doc-send-menu__others-lead">送る書類を選ぶ</p>
      ${Object.keys(DOC_MAIL_TYPE_LABELS).map((t) => `
        <label class="doc-send-menu__check">
          <input type="checkbox" value="${escapeHtmlAttr(t)}"${t === activeTab ? " checked" : ""} data-doc-send-other-check="1" />
          <span>${escapeHtml(DOC_MAIL_TYPE_LABELS[t])}</span>
        </label>`).join("")}
      <button type="button" class="doc-send-menu__go" data-doc-send-others-go="1">この組み合わせで進む</button>
    </div>`;
  document.body.appendChild(menu);

  docSendMenuAnchorEl = anchorEl;
  positionDocSendMenu();
  document.addEventListener("keydown", docSendMenuEscHandler);
  window.addEventListener("resize", positionDocSendMenu);
}

/** 選んだ書類でメール作成画面へ／郵送モーダルへ */
function startDocSend(kind, projectId, types) {
  closeDocSendMenu();
  if (!types.length) {
    showToast("送る書類を選んでください");
    return;
  }
  if (kind === "postal") {
    openDocPostalModal(projectId, types);
    return;
  }
  location.hash = `project-mail:${projectId}?docs=${types.join(",")}`;
}

// ── メール作成画面の状態 ──

/** 案件×書類の組み合わせごとに入力内容を保持する（別の書類を選び直したら作り直す）。 */
function docMailComposeState(projectId, docTypes) {
  const key = `${projectId}:${docTypes.join(",")}`;
  const cur = uiState.docMailCompose;
  if (cur && cur.key === key) return cur;

  const p = projects.find((x) => x.id === projectId);
  const client = p ? resolveProjectClientMaster(p) : null;
  uiState.docMailCompose = {
    key,
    projectId,
    docTypes,
    tab: "edit",
    to: String(p?.mailTo || client?.email || ""),
    cc: String(p?.mailCc || ""),
    bcc: "",
    showCc: Boolean(p?.mailCc),
    showBcc: false,
    templateKind: "personal",
    templateId: "",
    subject: "",
    body: "",
    subjectTouched: false,
    bodyTouched: false,
    sendMode: "attach",
    timing: "now",
    scheduledAt: "",
    updateStatus: true,
    attachments: [],
    uploading: false,
    sending: false,
  };
  void loadDocMailTemplates();
  return uiState.docMailCompose;
}

/** テンプレ一覧（共有＋個人）を1回だけ読む。読めたら既定テンプレを本文に流し込む。 */
async function loadDocMailTemplates() {
  if (uiState.docMailTemplates?.loading) return;
  // 取得済みなら通信せず、いま作ったばかりの入力欄にテンプレを流し込むだけ
  // （ここを素通りすると、2件目以降のメール作成で件名・本文が空のままになる）
  if (uiState.docMailTemplates?.items) {
    applyDocMailDefaultTemplate();
    return;
  }
  uiState.docMailTemplates = { loading: true, items: null, personal: [] };
  const { ok, data } = await apiFetch("/api/mail-templates");
  uiState.docMailTemplates = {
    loading: false,
    items: ok && data.ok ? data.templates || [] : [],
    personal: ok && data.ok ? data.personalTemplates || [] : [],
  };
  applyDocMailDefaultTemplate();
  if (currentRoute().page === "project-mail") render();
}

/** docType に対応する共有テンプレの既定キー（サーバー api/_docSend.js DOC_TEMPLATE_KEY と一致させること） */
const DOC_MAIL_TEMPLATE_KEY = {
  invoice: "invoice",
  quote: "quote",
  order: "order",
  acceptance: "acceptance",
  receipt: "receipt",
  delivery: "delivery_inspection",
  inspection: "delivery_inspection",
};

/** 画面に出すテンプレ一覧。ユーザーメニュー「テンプレ設定」で管理する個人テンプレを使う。
 *  （初回ログイン時にサーバーが共有テンプレをコピーして配る。万一0件なら共有テンプレで代替する） */
function docMailTemplateList() {
  const tpls = uiState.docMailTemplates || {};
  const personal = tpls.personal || [];
  return personal.length ? { kind: "personal", items: personal } : { kind: "shared", items: tpls.items || [] };
}

function docMailTemplateValue(tpl, kind) {
  return kind === "personal" ? String(tpl.id) : String(tpl.template_key);
}

/** テンプレの対象書類キー（個人は doc_key、共有は template_key そのもの） */
function docMailTemplateDocKey(tpl, kind) {
  return kind === "personal" ? tpl.doc_key || "" : tpl.template_key || "";
}

/** 選択中のテンプレを決め、ユーザーが件名・本文をまだ触っていなければ流し込む。
 *  未選択のときは、送る書類に対応するテンプレ→一覧の先頭 の順で選ぶ。 */
function applyDocMailDefaultTemplate() {
  const st = uiState.docMailCompose;
  const tpls = uiState.docMailTemplates;
  if (!st || !tpls || tpls.loading) return;
  const { kind, items } = docMailTemplateList();
  let tpl = items.find((t) => kind === st.templateKind && docMailTemplateValue(t, kind) === String(st.templateId));
  if (!tpl) {
    const key = DOC_MAIL_TEMPLATE_KEY[st.docTypes[0]] || "invoice";
    tpl = items.find((t) => docMailTemplateDocKey(t, kind) === key) || items[0] || null;
    if (!tpl) return;
    st.templateKind = kind;
    st.templateId = docMailTemplateValue(tpl, kind);
  }
  if (!st.subjectTouched) st.subject = tpl.subject || "";
  if (!st.bodyTouched) st.body = docMailBodyForSendMode(tpl.body || "", st.sendMode);
}

/** 送付方法に合わせて本文のダウンロード案内を書き換える（画面で編集できるので、あくまで初期値の調整）。 */
function docMailBodyForSendMode(body, mode) {
  const text = String(body || "");
  const attachSentence = "書類は本メールに添付しております。";
  const linkSentence = "下記URLよりダウンロードしてください。\n##download_url##";
  if (mode === "attach") {
    return text.replace(/[^\n]*(?:下記URL|下記のURL)[^\n]*\n?##download_url##/g, attachSentence);
  }
  return text.includes("##download_url##") ? text : text.replace(attachSentence, linkSentence);
}

/** 差込タグを実データで置換したプレビュー文字列 */
function docMailApplyTags(text, st) {
  const p = projects.find((x) => x.id === st.projectId);
  const client = p ? resolveProjectClientMaster(p) : null;
  const vars = {
    last_name: client?.contactName || "ご担当者",
    client_name: client?.name || "",
    project_name: p?.name || "",
    doc_names: st.docTypes.map(docMailTypeLabel).join("・"),
    download_url: st.sendMode === "attach" ? "" : "（送信時に発行されるダウンロードURL）",
  };
  return String(text || "").replace(/##(\w+)##/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k] ?? "") : m
  );
}

// ── 画面 ──

function docMailMergeTagSelectHtml(field) {
  return `<select class="dmc-tag-select" data-search-dropdown-off="1" data-doc-mail-tag="${escapeHtmlAttr(field)}" aria-label="差込タグを挿入">
    <option value="">差込タグ</option>
    ${DOC_MAIL_MERGE_TAGS.map((t) => `<option value="${escapeHtmlAttr(t.tag)}">${escapeHtml(t.label)}</option>`).join("")}
  </select>`;
}

function docMailAttachmentsHtml(st) {
  const rows = st.attachments.map((f) => `
    <li class="dmc-file">
      <span class="dmc-file-name" title="${escapeHtmlAttr(f.filename)}">${escapeHtml(f.filename)}</span>
      <span class="dmc-file-size">${escapeHtml(developmentFileSizeLabel(f.sizeBytes))}</span>
      <button type="button" class="dmc-file-del" data-doc-mail-file-del="${f.id}" aria-label="削除">×</button>
    </li>`).join("");
  return `
    <label class="dmc-file-pick${st.uploading ? " is-busy" : ""}">
      <input type="file" data-doc-mail-file-input="1" ${st.uploading ? "disabled" : ""} multiple hidden />
      <span>${st.uploading ? "アップロード中…" : "ファイル選択"}</span>
    </label>
    ${st.attachments.length ? `<ul class="dmc-file-list">${rows}</ul>` : `<span class="dmc-file-empty">未選択</span>`}`;
}

function docMailEditHtml(st, client) {
  const tplList = docMailTemplateList();
  const deliveryMethod = String(client?.deliveryMethod || "");
  const isPostalClient = deliveryMethod.includes("郵送");
  return `
    <table class="dmc-top-table">
      <thead>
        <tr>
          <th>宛先（複数の場合はカンマ区切り）</th>
          <th>顧客 / 送付方法</th>
          <th>書類</th>
          <th>書類以外の添付</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="dmc-to-cell">
            <div class="dmc-addr-row">
              <span class="dmc-addr-label">To</span>
              <input type="text" class="dmc-input" value="${escapeHtmlAttr(st.to)}" data-doc-mail-field="to" placeholder="example@example.com" />
              <button type="button" class="dmc-addr-toggle${st.showCc ? " is-on" : ""}" data-doc-mail-toggle="showCc">Cc</button>
              <button type="button" class="dmc-addr-toggle${st.showBcc ? " is-on" : ""}" data-doc-mail-toggle="showBcc">Bcc</button>
            </div>
            ${st.showCc ? `<div class="dmc-addr-row">
              <span class="dmc-addr-label">Cc</span>
              <input type="text" class="dmc-input" value="${escapeHtmlAttr(st.cc)}" data-doc-mail-field="cc" />
            </div>` : ""}
            ${st.showBcc ? `<div class="dmc-addr-row">
              <span class="dmc-addr-label">Bcc</span>
              <input type="text" class="dmc-input" value="${escapeHtmlAttr(st.bcc)}" data-doc-mail-field="bcc" />
            </div>` : ""}
          </td>
          <td class="dmc-client-cell">
            <div class="dmc-client-name">${escapeHtml(client?.name || "（顧客未設定）")}</div>
            <div class="dmc-client-method">メール（${st.sendMode === "attach" ? "添付" : "DLページ"}）</div>
            ${isPostalClient ? `<div class="dmc-warn">このお客様は送付方法が「${escapeHtml(deliveryMethod)}」です。メールで送る場合はご確認ください。</div>` : ""}
          </td>
          <td class="dmc-docs-cell">
            <ul class="dmc-doc-list">
              ${st.docTypes.map((t) => `<li><a href="#project-docs:${escapeHtmlAttr(st.projectId)}:${escapeHtmlAttr(t)}">${escapeHtml(docMailTypeLabel(t))}</a></li>`).join("")}
            </ul>
          </td>
          <td class="dmc-files-cell">${docMailAttachmentsHtml(st)}</td>
        </tr>
      </tbody>
    </table>

    <div class="dmc-template-row">
      <label class="dmc-template-field">
        <span>メールテンプレート</span>
        <select class="dmc-input" data-search-dropdown-off="1" data-doc-mail-template="${escapeHtmlAttr(tplList.kind)}">
          <option value="">選択してください</option>
          ${tplList.items.map((t) => {
            const v = docMailTemplateValue(t, tplList.kind);
            return `<option value="${escapeHtmlAttr(v)}"${st.templateKind === tplList.kind && String(st.templateId) === v ? " selected" : ""}>${escapeHtml(t.name)}</option>`;
          }).join("")}
        </select>
      </label>
      <button type="button" class="dmc-template-manage" data-mail-tpl-settings-trigger="1">テンプレ設定を開く</button>
      <button type="button" class="dmc-template-save" data-doc-mail-save-template="1">いまの件名・本文を新しいテンプレに保存</button>
    </div>

    <div class="dmc-form">
      <div class="dmc-form-row">
        <div class="dmc-form-label">件名 <span class="dmc-req">必須</span></div>
        <div class="dmc-form-body">
          <div class="dmc-form-tools">${docMailMergeTagSelectHtml("subject")}</div>
          <input type="text" class="dmc-input dmc-subject" value="${escapeHtmlAttr(st.subject)}" data-doc-mail-field="subject" />
        </div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">本文 <span class="dmc-req">必須</span></div>
        <div class="dmc-form-body">
          <div class="dmc-form-tools">${docMailMergeTagSelectHtml("body")}</div>
          <textarea class="dmc-input dmc-body" rows="14" data-doc-mail-field="body">${escapeHtml(st.body)}</textarea>
        </div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">送信する書類</div>
        <div class="dmc-form-body dmc-form-body--plain">${escapeHtml(docMailTypesLabel(st.docTypes))}</div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">書類送付方法 <span class="dmc-req">必須</span></div>
        <div class="dmc-form-body dmc-form-body--plain">
          <label class="dmc-radio"><input type="radio" name="dmcSendMode" value="link"${st.sendMode === "link" ? " checked" : ""} data-doc-mail-field="sendMode" /><span>ダウンロードページ</span></label>
          <label class="dmc-radio"><input type="radio" name="dmcSendMode" value="attach"${st.sendMode === "attach" ? " checked" : ""} data-doc-mail-field="sendMode" /><span>メール添付</span></label>
          <span class="dmc-hint">ダウンロードページは90日間有効なURLを本文に差し込みます。メール添付はPDFを直接添付します。</span>
        </div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">送信タイミング <span class="dmc-req">必須</span></div>
        <div class="dmc-form-body dmc-form-body--plain">
          <label class="dmc-radio"><input type="radio" name="dmcTiming" value="now"${st.timing === "now" ? " checked" : ""} data-doc-mail-field="timing" /><span>即時</span></label>
          <label class="dmc-radio"><input type="radio" name="dmcTiming" value="schedule"${st.timing === "schedule" ? " checked" : ""} data-doc-mail-field="timing" /><span>送信予約</span></label>
          ${st.timing === "schedule" ? `<input type="datetime-local" class="dmc-input dmc-datetime" value="${escapeHtmlAttr(st.scheduledAt)}" max="${escapeHtmlAttr(docMailMaxScheduleValue())}" data-doc-mail-field="scheduledAt" />
          <span class="dmc-hint">予約は5分間隔で送信されます（指定時刻から最大5分遅れます）。${DOC_MAIL_MAX_SCHEDULE_DAYS}日先まで指定できます。</span>` : ""}
        </div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">ステータス変更</div>
        <div class="dmc-form-body dmc-form-body--plain">
          <label class="dmc-switch">
            <input type="checkbox"${st.updateStatus ? " checked" : ""} data-doc-mail-field="updateStatus" />
            <span class="dmc-switch-ui" aria-hidden="true"></span>
            <span>送信完了後に請求ステータスを自動的に「送付済み」に変更する</span>
          </label>
          ${st.docTypes.includes("invoice") ? "" : `<span class="dmc-hint">請求書を含まないため、この設定は使われません。</span>`}
        </div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">送信元</div>
        <div class="dmc-form-body dmc-form-body--plain">${escapeHtml(DOC_MAIL_FROM_NAME)}（システムの送信用アドレス）</div>
      </div>
      <div class="dmc-form-row">
        <div class="dmc-form-label">返信先</div>
        <div class="dmc-form-body dmc-form-body--plain">${escapeHtml(currentUser?.email || "—")}</div>
      </div>
    </div>`;
}

function docMailPreviewHtml(st) {
  return `
    <div class="dmc-preview">
      <dl class="dmc-preview-head">
        <dt>宛先</dt><dd>${escapeHtml(st.to || "（未入力）")}</dd>
        ${st.cc ? `<dt>Cc</dt><dd>${escapeHtml(st.cc)}</dd>` : ""}
        ${st.bcc ? `<dt>Bcc</dt><dd>${escapeHtml(st.bcc)}</dd>` : ""}
        <dt>件名</dt><dd>${escapeHtml(docMailApplyTags(st.subject, st) || "（未入力）")}</dd>
        <dt>書類</dt><dd>${escapeHtml(docMailTypesLabel(st.docTypes))}（${st.sendMode === "attach" ? "PDFを添付" : "ダウンロードページ"}）</dd>
        ${st.attachments.length ? `<dt>添付</dt><dd>${st.attachments.map((f) => escapeHtml(f.filename)).join("、")}</dd>` : ""}
      </dl>
      <pre class="dmc-preview-body">${escapeHtml(docMailApplyTags(st.body, st) || "（未入力）")}</pre>
    </div>`;
}

/** メール作成ページ（#project-mail:<案件ID>?docs=invoice,delivery） */
function projectMailComposePage(projectId, docTypesRaw) {
  const p = projects.find((x) => x.id === projectId);
  if (!p) {
    return `<section class="dmc-page"><div class="soft-card" style="padding:24px;">案件が見つかりません。<a href="#projects">案件一覧へ戻る</a></div></section>`;
  }
  const docTypes = String(docTypesRaw || "invoice")
    .split(",")
    .map((s) => s.trim())
    .filter((t) => Object.prototype.hasOwnProperty.call(DOC_MAIL_TYPE_LABELS, t));
  const st = docMailComposeState(projectId, docTypes.length ? docTypes : ["invoice"]);
  const client = resolveProjectClientMaster(p);

  return `
    <section class="dmc-page">
      <header class="dmc-head">
        <h2 class="dmc-title">メール作成</h2>
        <div class="dmc-head-right">${escapeHtml(p.name || "")}${client?.name ? ` ／ ${escapeHtml(client.name)}` : ""}</div>
      </header>
      <div class="dmc-tabs" role="tablist">
        <button type="button" class="dmc-tab${st.tab === "edit" ? " is-active" : ""}" role="tab" data-doc-mail-tab="edit">編集</button>
        <button type="button" class="dmc-tab${st.tab === "preview" ? " is-active" : ""}" role="tab" data-doc-mail-tab="preview">プレビュー</button>
      </div>
      <div class="dmc-panel">
        ${st.tab === "edit" ? docMailEditHtml(st, client) : docMailPreviewHtml(st)}
      </div>
      <footer class="dmc-foot">
        <button type="button" class="dmc-send-btn"${st.sending || st.uploading ? " disabled" : ""} data-doc-mail-send="1">
          ${st.sending ? "送信中…" : st.uploading ? "添付をアップロード中…" : st.timing === "schedule" ? "送信を予約" : "送信"}
        </button>
        <p class="dmc-foot-note">送信前に、書類のリンクからPDFの内容と、プレビュータブで差込タグの置換結果をご確認ください。</p>
        <button type="button" class="dmc-back-btn" data-doc-mail-back="1">戻る</button>
      </footer>
    </section>`;
}

// ── 添付ファイル ──

function docMailAttachExtOk(name) {
  const m = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return !!m && MAIL_ATTACH_EXTS.includes(`.${m[1]}`);
}

async function mailAttachApiPost(payload) {
  const r = await apiFetch("/api/mail-attachment", { method: "POST", body: payload });
  if (!r.ok) throw new Error(typeof r.data.error === "string" ? r.data.error : "添付の保存に失敗しました");
  return r.data;
}

/** 添付を1件アップロードする（3MB 超は分割転送し finalize で合体）。 */
async function uploadMailAttachment(file) {
  if (!docMailAttachExtOk(file.name)) throw new Error(`対応形式のみ: ${MAIL_ATTACH_EXT_HINT}`);
  if (file.size > MAIL_ATTACH_MAX_BYTES) throw new Error(`「${file.name}」は15MBを超えています`);
  const buf = await readLocalFileArrayBuffer(file);
  const bytes = new Uint8Array(buf);
  if (bytes.length <= MAIL_ATTACH_CHUNK_BYTES) {
    return mailAttachApiPost({ filename: file.name, fileBase64: arrayBufferToBase64(buf) });
  }
  const uploadId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const totalChunks = Math.ceil(bytes.length / MAIL_ATTACH_CHUNK_BYTES);
  for (let i = 0; i < totalChunks; i++) {
    const part = bytes.subarray(i * MAIL_ATTACH_CHUNK_BYTES, (i + 1) * MAIL_ATTACH_CHUNK_BYTES);
    await mailAttachApiPost({ uploadId, chunkIndex: i, fileBase64: arrayBufferToBase64(part) });
  }
  return mailAttachApiPost({ uploadId, finalize: true, totalChunks, filename: file.name });
}

async function handleDocMailFiles(files) {
  const st = uiState.docMailCompose;
  if (!st) return;
  if (st.attachments.length + files.length > MAIL_ATTACH_MAX_FILES) {
    showToast(`添付は${MAIL_ATTACH_MAX_FILES}件までです`);
    return;
  }
  st.uploading = true;
  render();
  try {
    for (const f of files) {
      const r = await uploadMailAttachment(f);
      st.attachments.push({ id: r.id, filename: r.filename, sizeBytes: r.sizeBytes });
    }
  } catch (e) {
    showToastError(e instanceof Error ? e.message : "添付のアップロードに失敗しました");
  } finally {
    st.uploading = false;
    render();
  }
}

// ── 送信 ──

async function submitDocMailSend() {
  const st = uiState.docMailCompose;
  if (!st || st.sending) return;
  // アップロード中の添付は attachments にまだ入っていない。送ると添付が抜けたまま客先へ届く
  if (st.uploading) {
    showToast("添付ファイルのアップロードが終わってから送信してください");
    return;
  }
  const p = projects.find((x) => x.id === st.projectId);
  if (!p?.dbId) {
    showToast("保存後に送信できます");
    return;
  }
  if (!String(st.to).trim()) {
    showToast("宛先を入力してください");
    return;
  }
  if (!String(st.subject).trim() || !String(st.body).trim()) {
    showToast("件名と本文は必須です");
    return;
  }
  if (st.timing === "schedule" && !st.scheduledAt) {
    showToast("送信予約の日時を指定してください");
    return;
  }
  if (st.timing === "schedule" && st.scheduledAt > docMailMaxScheduleValue()) {
    showToast(`送信予約は${DOC_MAIL_MAX_SCHEDULE_DAYS}日先までです`);
    return;
  }
  const label = docMailTypesLabel(st.docTypes);
  const scheduled = st.timing === "schedule";
  const ok0 = await fpConfirm({
    title: scheduled ? "送信を予約しますか？" : "メールを送信しますか？",
    message: `宛先：${st.to}\n書類：${label}\n${scheduled ? `送信日時：${st.scheduledAt.replace("T", " ")}` : "いますぐ送信します"}`,
    note: scheduled ? "送信日時になると自動で送られます（画面から予約は取り消せません）。" : "送信したメールは取り消せません。",
    confirmLabel: scheduled ? "予約する" : "送信する",
    danger: false
  });
  if (!ok0 || st.sending) return;

  st.sending = true;
  render();
  // 書類はサーバーの保存済み内容から作られるため、未保存の請求項目・支払期限・備考を先に確定させる
  // （保存できなければ古い内容で送らない）
  const linesSave = await flushDocBillingLinesBeforeSend(p.id, st.docTypes);
  if (!linesSave.ok) {
    st.sending = false;
    render();
    showToastError(linesSave.sealReset ? linesSave.error : `請求項目を保存できなかったため、送信を中止しました：${linesSave.error}`);
    return;
  }
  const metaSave = await flushDocMetaSaveToServer(p.id, st.docTypes);
  if (!metaSave.ok) {
    st.sending = false;
    render();
    showToastError(`支払期限・備考を保存できなかったため、送信を中止しました：${metaSave.error}`);
    return;
  }
  const { ok, status, data } = await apiFetch("/api/send-document-email", {
    method: "POST",
    body: {
      projectId: p.dbId,
      docTypes: st.docTypes,
      to: st.to,
      cc: st.cc,
      bcc: st.bcc,
      subject: st.subject,
      body: st.body,
      sendMode: st.sendMode,
      attachmentIds: st.attachments.map((f) => f.id),
      updateInvoiceStatus: st.updateStatus,
      // 画面で送付方法を表示したうえでの送信なので、郵送設定の顧客でもここで止めない
      allowPostalClient: true,
      ...(st.timing === "schedule" ? { scheduledAt: new Date(st.scheduledAt).toISOString() } : {}),
    },
  });
  st.sending = false;
  if (ok && data.ok) {
    // サーバーが請求ステータスを進めたら手元の案件にも反映する。反映しないと
    // 案件一覧の入金列がリロードするまで「未請求」のまま残る（送ったのに変わらないように見える）。
    if (data.invoice_status) p.invoiceStatus = data.invoice_status;
    // 送信済みの内容を残さない（同じ案件を開き直したときはテンプレから作り直す）
    uiState.docMailCompose = null;
    // メールは送れたが発行履歴に残せなかったときは黙って成功にしない（後で履歴を見て慌てないため）
    if (Array.isArray(data.issue_history_errors) && data.issue_history_errors.length) {
      showToast(`${label}は送信しましたが、発行履歴に記録できませんでした（${data.issue_history_errors.join(" / ")}）`);
    } else {
      showToast(data.scheduled ? `${label}の送信を予約しました` : `${label}を ${data.to} に送信しました`);
    }
    location.hash = `project-docs:${st.projectId}:${st.docTypes[0]}`;
    return;
  }
  render();
  showToast(`送信できませんでした：${data.message || data.error || status || "通信エラー"}`);
}

async function saveDocMailPersonalTemplate() {
  const st = uiState.docMailCompose;
  if (!st) return;
  if (!String(st.subject).trim() || !String(st.body).trim()) {
    showToast("件名と本文を入力してから保存してください");
    return;
  }
  const name = window.prompt("テンプレート名を入力してください", docMailTypesLabel(st.docTypes) + "送付");
  if (!name) return;
  const { ok, data } = await apiFetch("/api/mail-templates", {
    method: "POST",
    // 対象書類を入れておくと、次に同じ書類を送るとき最初から選ばれる
    body: { name, subject: st.subject, body: st.body, docKey: DOC_MAIL_TEMPLATE_KEY[st.docTypes[0]] || "" },
  });
  if (!ok || !data.ok) {
    showToastError(`保存できませんでした：${data.error || "エラー"}`);
    return;
  }
  uiState.docMailTemplates = null;
  st.templateKind = "personal";
  st.templateId = String(data.id);
  await loadDocMailTemplates();
  showToast("テンプレートに保存しました");
}

// ── イベント ──

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  // 送るボタン（メール／郵送）→ 書類選択メニュー
  const mailBtn = target.closest("[data-doc-footer-mail]");
  if (mailBtn instanceof HTMLElement && !mailBtn.disabled) {
    e.preventDefault();
    const [pid, tab] = (mailBtn.getAttribute("data-doc-footer-mail") || "").split(":");
    openDocSendMenu(mailBtn, "mail", pid, tab);
    return;
  }
  const postalBtn = target.closest("[data-doc-footer-postal]");
  if (postalBtn instanceof HTMLElement && !postalBtn.disabled) {
    e.preventDefault();
    const [pid, tab] = (postalBtn.getAttribute("data-doc-footer-postal") || "").split(":");
    openDocSendMenu(postalBtn, "postal", pid, tab);
    return;
  }

  const menu = document.getElementById("docSendMenu");
  if (menu) {
    const pick = target.closest("[data-doc-send-pick]");
    if (pick instanceof HTMLElement) {
      e.preventDefault();
      startDocSend(menu.dataset.docSendKind, menu.dataset.docSendProject, (pick.getAttribute("data-doc-send-pick") || "").split(","));
      return;
    }
    if (target.closest("[data-doc-send-more]")) {
      e.preventDefault();
      const others = menu.querySelector(".doc-send-menu__others");
      if (others) others.hidden = !others.hidden;
      positionDocSendMenu(); // 高さが変わるので置き直す（開いた分が画面外に出るのを防ぐ）
      if (others && !others.hidden) others.scrollIntoView({ block: "nearest" });
      return;
    }
    if (target.closest("[data-doc-send-others-go]")) {
      e.preventDefault();
      const checked = [...menu.querySelectorAll("[data-doc-send-other-check]:checked")].map((el) => el.value);
      startDocSend(menu.dataset.docSendKind, menu.dataset.docSendProject, checked);
      return;
    }
    if (!menu.contains(target)) closeDocSendMenu();
  }

  if (currentRoute().page !== "project-mail") return;
  const st = uiState.docMailCompose;
  if (!st) return;

  const tabBtn = target.closest("[data-doc-mail-tab]");
  if (tabBtn instanceof HTMLElement) {
    st.tab = tabBtn.getAttribute("data-doc-mail-tab") === "preview" ? "preview" : "edit";
    render();
    return;
  }
  const toggle = target.closest("[data-doc-mail-toggle]");
  if (toggle instanceof HTMLElement) {
    const field = toggle.getAttribute("data-doc-mail-toggle");
    st[field] = !st[field];
    render();
    return;
  }
  if (target.closest("[data-doc-mail-file-input]")) return; // ラベル経由のファイル選択はそのまま通す
  const fileDel = target.closest("[data-doc-mail-file-del]");
  if (fileDel instanceof HTMLElement) {
    const id = Number(fileDel.getAttribute("data-doc-mail-file-del"));
    st.attachments = st.attachments.filter((f) => f.id !== id);
    render();
    void apiFetch(`/api/mail-attachment?id=${id}`, { method: "DELETE" });
    return;
  }
  if (target.closest("[data-doc-mail-save-template]")) {
    void saveDocMailPersonalTemplate();
    return;
  }
  if (target.closest("[data-doc-mail-send]")) {
    void submitDocMailSend();
    return;
  }
  if (target.closest("[data-doc-mail-back]")) {
    location.hash = `project-docs:${st.projectId}:${st.docTypes[0]}`;
  }
});

// テキスト入力は state に取り込むだけで再描画しない（描画するとカーソルが飛び、IME変換も壊れる）
document.addEventListener("input", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const field = target.getAttribute("data-doc-mail-field");
  if (!field) return;
  const st = uiState.docMailCompose;
  if (!st) return;
  if (field === "subject" || field === "body") {
    st[field] = target.value;
    st[field === "subject" ? "subjectTouched" : "bodyTouched"] = true;
    return;
  }
  if (field === "to" || field === "cc" || field === "bcc" || field === "scheduledAt") st[field] = target.value;
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const st = uiState.docMailCompose;

  if (target.getAttribute("data-doc-mail-file-input") === "1" && target.files?.length) {
    // FileList は input の生の参照なので、value を空にする前に配列へ写す（写さないと選択が消える）
    const files = [...target.files];
    target.value = "";
    void handleDocMailFiles(files);
    return;
  }

  const tagField = target.getAttribute("data-doc-mail-tag");
  if (tagField && st) {
    const tag = target.value;
    target.value = "";
    if (!tag) return;
    const input = document.querySelector(`[data-doc-mail-field="${tagField}"]`);
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) return;
    const pos = input.selectionStart ?? input.value.length;
    const next = `${input.value.slice(0, pos)}##${tag}##${input.value.slice(input.selectionEnd ?? pos)}`;
    st[tagField] = next;
    st[tagField === "subject" ? "subjectTouched" : "bodyTouched"] = true;
    input.value = next;
    input.focus();
    input.setSelectionRange(pos + tag.length + 4, pos + tag.length + 4);
    return;
  }

  const tplKind = target.getAttribute("data-doc-mail-template");
  if (tplKind && st) {
    st.templateKind = tplKind;
    st.templateId = target.value;
    // テンプレを選び直したときは、編集済みでも新しいテンプレの内容に差し替える
    st.subjectTouched = false;
    st.bodyTouched = false;
    applyDocMailDefaultTemplate();
    render();
    return;
  }

  const field = target.getAttribute("data-doc-mail-field");
  if (!field || !st) return;
  if (field === "sendMode") {
    st.sendMode = target.value;
    if (!st.bodyTouched) applyDocMailDefaultTemplate();
    else st.body = docMailBodyForSendMode(st.body, st.sendMode);
    render();
    return;
  }
  if (field === "timing") {
    st.timing = target.value;
    render();
    return;
  }
  if (field === "updateStatus") {
    st.updateStatus = target.checked;
  }
});
// ────────────────────────────────────────────────────────────────
// ToDo管理トップ（自分の担当課題）の「担当中／起票した／関わった」切り替え。
//
// 担当者が入れ替わると、それまで関わっていた人が課題を見失う（追記したくても探せない）。
// ウォッチ機能は明示的な設定が要り発見されにくいため、設定不要で自動的に効く導線として
// 「起票した」＝**自分が起票した課題（担当は問わない）**、
// 「関わった」＝**自分がコメントしたが、起票者でも担当者でもない課題** を出す。
// 起票した課題は他の人が担当になっていることが多く、担当中タブでは見つからない（IMPROVE-558）。
//
// 絞り込みの本体はサーバ側（api/progress-management.js の agendaViewerScopeSql）。
// ここは URL の assigned= を切り替えるだけで、一覧の取得経路は担当中と同じものに相乗りする。
// ────────────────────────────────────────────────────────────────

// 「起票した課題」「関わった課題」ピルは progressGroupListModeTabsHtml（14-expense-payment-forms.js）の
// バー内に置く（件数チップ列に置くと、押した後どのタブも選択中にならず現在地が分からなくなる）。ここは切替ハンドラ。
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;

  const scopeEl = target.closest("[data-pm-assign-scope]");
  if (!(scopeEl instanceof HTMLElement)) return;

  const raw = scopeEl.dataset.pmAssignScope;
  const next = raw === "involved" || raw === "created" ? raw : "me";
  const cur = progressManagementAssignScope();
  if (next === cur) return;

  invalidateProgressAssignedListCache();
  // 期限超過などの絞り込みを持ち越すと、切替先に該当が無いとき「起票13件なのに一覧が空」に見える
  resetProgressMyAssignFilters();
  location.hash = `progress-management?assigned=${next}`;
});
/* ============================================================
 * Claude 連携（MCP）（アカウント設定内）
 * - claude.ai のコネクタ: 接続先 URL の表示と、OAuth で連携中の数・解除（サーバ側 /api/oauth・_mcpOAuth.js）
 * - Claude Code 用の個人トークンの発行・再発行・失効（サーバ側 /api/mcp-token。1人1本・ハッシュ保存）
 * イベントは 16-render-routing.js の巨大 click ハンドラに足さず、
 * このファイルに独立して置く（15a/15b/15c と同じ流儀）。
 * ============================================================ */

/** アカウント設定モーダルの「MCP連携トークン」セクション本体（renderMyAccountModal から呼ばれる）。
 *  状態が未取得ならここで取得を蹴る（15a developmentPage / 15b allowedToolsPage と同じ
 *  「描画関数の先頭で自分のデータを取りに行く」パターン。hydrate は同期で loading を立てるので再入しない）。 */
/** Claude Code に設定する1行コマンド（表示とコピーの単一定義元）。
 *  ホストは www 付きが正（裸ドメインは 307 リダイレクトで POST が化ける）。 */
/** MCP の接続先（コネクタに貼る URL・Claude Code の設定コマンドの単一定義元）。
 *  ホストは www 付きが正（裸ドメインは 307 リダイレクトで POST が化ける）。 */
const MCP_SERVER_URL = "https://www.oe-onboarding.site/api/mcp";

function mcpSetupCommand(token) {
  return `claude mcp add --transport http onboarding ${MCP_SERVER_URL} --header "Authorization: Bearer ${token}"`;
}

function renderMyAccountMcpSection(m) {
  if (!m.mcpStatus) void hydrateMyAccountMcpStatus();
  // 個人トークンは claude.ai にログインせずに使う人向け（ほぼ使わない）なので折りたたむ。
  // 発行直後はコマンドを見せるため開く。開閉は再描画で戻らないよう m に持つ（下の toggle で同期）
  const open = m.mcpTokenOpen || m.mcpIssuedToken ? " open" : "";
  return `${renderMyAccountMcpConnector(m.mcpStatus)}
      <details class="my-account-advanced" data-mcp-token-details="1"${open}>
        <summary>上級者向け：Claude Code 用トークン（通常は不要）</summary>
        ${renderMyAccountMcpToken(m)}
      </details>`;
}

/** claude.ai（ブラウザ・デスクトップ・スマホ）のコネクタで使う案内と、連携中の数・解除 */
function renderMyAccountMcpConnector(st) {
  const n = st && Number.isFinite(st.oauthConnections) ? st.oauthConnections : 0;
  return `
      <div class="my-account-row">
        <label>Claude のコネクタに追加する URL（クリックでコピー）
          <input readonly value="${escapeHtmlAttr(MCP_SERVER_URL)}" data-mcp-command-value="1" />
        </label>
      </div>
      <p class="my-account-field-hint">claude.ai の「設定 → コネクタ → カスタムコネクタを追加」にこの URL を貼り、「連携」を押して On boarding で許可すると、Claude のチャットから課題の確認・コメントなどができます。</p>
      ${n > 0 ? `<div class="my-account-notify-row">
        <span>コネクタで連携中（${n}件）</span>
        <button type="button" class="inline-btn ghost" data-mcp-oauth-revoke="1">解除</button>
      </div>` : ""}`;
}

/** Claude Code 用の個人トークン */
function renderMyAccountMcpToken(m) {
  if (m.mcpIssuedToken) {
    // トークン単体の欄は置かない（設定コマンドにトークンが入っているため1本で足りる）
    return `
      <div class="my-account-row">
        <label>Claude の設定コマンド（クリックでコピー・表示はこの1回だけ）
          <input readonly value="${escapeHtmlAttr(mcpSetupCommand(m.mcpIssuedToken))}" data-mcp-command-value="1" />
        </label>
      </div>
      <p class="my-account-field-hint">この設定は Claude Code用です。コピーした1行を Claude Code を動かすターミナルで実行すると、Claude Code から Onboarding を使えるようになります（トークン入りなので、チャット欄には貼らないでください）。接続先 www.oe-onboarding.site は 自社サーバーです。</p>`;
  }
  const st = m.mcpStatus;
  if (!st || st.loading) {
    return `<div class="my-account-notify-row"><span>状態を読み込み中…</span></div>`;
  }
  if (st.error) {
    return `<div class="my-account-notify-row"><span>状態を取得できませんでした</span><button type="button" class="inline-btn ghost" data-mcp-token-reload="1">再読み込み</button></div>`;
  }
  if (st.hasToken) {
    const issuedDate = st.issuedAt ? new Date(st.issuedAt) : null;
    const issued = issuedDate && !Number.isNaN(issuedDate.getTime()) ? issuedDate.toLocaleDateString("ja-JP") : "";
    return `
      <div class="my-account-notify-row">
        <span>Claude Code 用トークン：発行済み${issued ? `（${issued}）` : ""}</span>
        <button type="button" class="inline-btn ghost" data-mcp-token-issue="1">再発行</button>
      </div>
      <div class="my-account-notify-row">
        <span>このトークンを使えなくする（コネクタの連携はそのまま）</span>
        <button type="button" class="inline-btn ghost" data-mcp-token-revoke="1">失効する</button>
      </div>
      <p class="my-account-field-hint">トークンは発行時にしか表示されません。控えを失くしたら再発行してください（今のトークンは使えなくなります）。「全デバイスからログアウト」ではこのトークンとコネクタの連携の両方が切れます。</p>`;
  }
  return `
    <div class="my-account-notify-row">
      <span>Claude Code 用トークン：未発行</span>
      <button type="button" class="inline-btn ghost" data-mcp-token-issue="1">発行する</button>
    </div>
    <p class="my-account-field-hint">Claude Code（ターミナル）から使うときの、あなた専用の鍵です。claude.ai のコネクタで使うだけなら発行は不要です。操作はあなたの名義・権限で記録されます。</p>`;
}

/** 発行/失効の実行中フラグ（連打で POST が2本飛ぶと、後着レスポンスが先に描画された場合に
 *  DBに残っていない方のトークンを「この1回だけ」として見せてしまうため）。 */
let mcpTokenRequestBusy = false;

async function hydrateMyAccountMcpStatus() {
  const m = uiState.myAccountEditor;
  if (!m || m.mcpStatus) return;
  m.mcpStatus = { loading: true };
  const r = await apiFetch("/api/mcp-token");
  const cur = uiState.myAccountEditor;
  if (!cur) return; // 応答前にモーダルが閉じられた
  cur.mcpStatus = r.ok
    ? { hasToken: !!r.data.hasToken, issuedAt: r.data.issuedAt || null, oauthConnections: r.data.oauthConnections }
    : { error: true };
  render();
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;
  if (!uiState.myAccountEditor) return; // モーダルが開いていなければ全ボタンが存在しない

  const reloadEl = target.closest("[data-mcp-token-reload]");
  if (reloadEl instanceof HTMLElement) {
    uiState.myAccountEditor.mcpStatus = null;
    void hydrateMyAccountMcpStatus();
    render();
    return;
  }

  const issueEl = target.closest("[data-mcp-token-issue]");
  if (issueEl instanceof HTMLElement) {
    const m = uiState.myAccountEditor;
    const reissue = !!(m.mcpStatus && m.mcpStatus.hasToken);
    if (
      reissue &&
      !window.confirm("再発行すると今のトークンは使えなくなります。\n（Claude 側に設定済みのトークンも差し替えが必要です）\n再発行しますか？")
    )
      return;
    if (mcpTokenRequestBusy) return;
    mcpTokenRequestBusy = true;
    void (async () => {
      const r = await apiFetch("/api/mcp-token", { method: "POST" });
      mcpTokenRequestBusy = false;
      const cur = uiState.myAccountEditor;
      if (!cur) return;
      // 200 でも token が読めなければ失敗扱い（サーバ側は既に上書き済みの可能性があるため状態は取り直す）
      if (!r.ok || !r.data.token) {
        cur.mcpStatus = null;
        showToast(r.data.error || "トークンを発行できませんでした。もう一度お試しください", { variant: "error" });
        render();
        return;
      }
      cur.mcpIssuedToken = r.data.token;
      cur.mcpStatus = { ...cur.mcpStatus, hasToken: true, issuedAt: r.data.issuedAt || null };
      render();
    })();
    return;
  }

  const revokeEl = target.closest("[data-mcp-token-revoke]");
  if (revokeEl instanceof HTMLElement) {
    if (!window.confirm("Claude Code 用トークンを失効します。\nこのトークンを設定した Claude Code からは接続できなくなります（claude.ai のコネクタの連携はそのまま使えます）。よろしいですか？")) return;
    if (mcpTokenRequestBusy) return;
    mcpTokenRequestBusy = true;
    void (async () => {
      const r = await apiFetch("/api/mcp-token", { method: "DELETE" });
      mcpTokenRequestBusy = false;
      const cur = uiState.myAccountEditor;
      if (!cur) return;
      if (!r.ok) {
        showToast(r.data.error || "失効に失敗しました", { variant: "error" });
        return;
      }
      cur.mcpIssuedToken = null;
      cur.mcpStatus = { ...cur.mcpStatus, hasToken: false, issuedAt: null };
      showToast("Claude Code 用トークンを失効しました");
      render();
    })();
    return;
  }

  const oauthRevokeEl = target.closest("[data-mcp-oauth-revoke]");
  if (oauthRevokeEl instanceof HTMLElement) {
    if (!window.confirm("Claude のコネクタとの連携を解除します。\nもう一度使うときは Claude 側で「連携」からやり直してください。よろしいですか？")) return;
    if (mcpTokenRequestBusy) return;
    mcpTokenRequestBusy = true;
    void (async () => {
      const r = await apiFetch("/api/mcp-token?target=oauth", { method: "DELETE" });
      mcpTokenRequestBusy = false;
      const cur = uiState.myAccountEditor;
      if (!cur) return;
      if (!r.ok) {
        showToast(r.data.error || "解除に失敗しました", { variant: "error" });
        return;
      }
      cur.mcpStatus = { ...cur.mcpStatus, oauthConnections: 0 };
      showToast("Claude のコネクタとの連携を解除しました");
      render();
    })();
    return;
  }

  // コマンド欄・URL 欄はクリック（タップ）だけで全選択＋コピー（コピーボタンは置かない）
  // ※選択済みテキストをマウスでなぞり直すと「選択テキストのドラッグ」が始まり、添付ドロップの
  //   オーバーレイ（グローバル dragover リスナー）が出て邪魔になるため、下の dragstart で止めている
  // ※トーストは showToastDirect（DOM直挿入）を使う。showToast はフル再描画で入力欄が作り直され、
  //   選択が消えて画面がちらつく（このモーダルではそれが「コピーの挙動が変」に見えた実例あり）
  const copyFieldEl = target.closest("[data-mcp-command-value]");
  if (copyFieldEl instanceof HTMLInputElement) {
    copyFieldEl.focus();
    copyFieldEl.select();
    const selectedHint = () => showToastDirect("全選択しました。Ctrl+C（⌘C）でコピーしてください");
    // 非https等では navigator.clipboard 自体が undefined（既存のコピー処理と同じ存在チェックが必須）。
    // その場合も全選択は済んでいるので手動コピーの案内だけ出す
    if (!navigator.clipboard?.writeText) {
      selectedHint();
      return;
    }
    void navigator.clipboard
      .writeText(copyFieldEl.value)
      .then(() => showToastDirect("コピーしました"))
      .catch(selectedHint);
    return;
  }
});

// トークン欄の開閉を覚える（<details> の toggle はバブリングしないので capture で拾う）
document.addEventListener(
  "toggle",
  (e) => {
    const t = e.target;
    if (!(t instanceof HTMLDetailsElement) || !t.matches("[data-mcp-token-details]")) return;
    if (uiState.myAccountEditor) uiState.myAccountEditor.mcpTokenOpen = t.open;
  },
  true
);

// クリックで全選択されたコマンドをなぞり直すと選択テキストのドラッグが始まり、
// 添付用ドロップオーバーレイが出て「コピーしようとしたら添付が出る」状態になるため、
// この欄からのドラッグ開始そのものを止める（コピーはクリック時の自動コピーで完了している）
document.addEventListener("dragstart", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  if (t.closest("[data-mcp-command-value]")) e.preventDefault();
});
// ────────────────────────────────────────────────────────────────
// 原価割当: REM取込由来の未割当原価の「まとめて請求書を作成」ボタン
//  - 前月に確定した案件（fp_rem_billing_assignments の記憶が指す語幹＋前月利用月表記）を
//    サーバー側で月ずらし複製し、この原価をその案件へ割り当てる
//    （POST /api/costs { action: 'rem-create-project-from-prev', costId }）。
//  - 成功後は案件・原価の両方が変わるため、ページを再読込して最新状態に揃える
//    （新規案件をローカル状態へ部分反映すると原価管理側と食い違うため、素直にリロード）。
//    リロード後は #project-detail:pXXX?composeInvoice=1 で請求書作成画面まで自動オープン。
//  - 前月案件が見つからない等で自動複製できない時は行き止まりにせず、その場で
//    openProjectCreateForCostRows() が新規案件作成モーダルを開く（作成後に割当→請求書作成へ続く）。
// 新機能のイベントは 16 の巨大ハンドラに足さず、本ファイルの独立リスナーで処理する（15a〜15d と同方式）。
// ────────────────────────────────────────────────────────────────

let remCreateFromPrevBusy = false;

/** 「まとめて請求書を作成」/「請求書を作成」の失敗時受け皿（前月案件が見つからない・記憶がない等で
 *  自動複製できない時）。対象行の情報（物件名・合計金額・計上月・記憶があればクライアント）を
 *  引き継いで新規案件作成モーダルを開く。作成後はその場でこの行/グループを割り当て、
 *  続けて請求書作成画面まで開く（createProjectFromModal の pendingCostAssign 経由）。
 *  @param {object[]} rows 対象の原価（ensureOrphanCosts の要素）
 *  @param {{ name?: string, clientName?: string }} [opts] 物件名で決められない場合（広告CSVアカウント等）に上書き */
function openProjectCreateForCostRows(rows, opts = {}) {
  const list = (rows || []).filter(Boolean);
  if (!list.length) return;
  const uiIds = list.map((c) => String(c.id));
  const total = list.reduce((s, c) => s + Number(c.amount || 0), 0);
  const bukken = list[0].remBukken || "";
  const name = opts.name || bukken;
  const hint = opts.clientName ? null : bukken && remAllocHintsState.loaded ? remAllocHintsState.hints[bukken] : undefined;
  const clientNameHint = opts.clientName || hint?.clientName || "";
  // REM/広告CSV由来の名寄せは表記ゆれ（全角スペース等）があり得るため、CSV取込と同じ正規化つき検索を使う
  const client = clientNameHint ? findClientForProjectCsv(clientNameHint) : null;
  initProjectCreateState({
    name,
    clientId: client?.id || "",
    amount: total,
    billingMonth: list[0].targetMonth || "",
    note: name,
    pendingCostAssign: { costIds: uiIds, composeInvoice: true }
  });
  showToast("前月の案件が見つからなかったため、新規に案件を作成してください");
  render();
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-rem-create-from-prev]");
  if (!(btn instanceof HTMLElement)) return;
  if (currentRoute().page !== "cost-allocation") return;
  if (remCreateFromPrevBusy || remGroupActionBusy) return;
  const costId = Number(btn.getAttribute("data-rem-create-from-prev"));
  if (!Number.isFinite(costId) || costId < 1) return;
  const uiId = btn.getAttribute("data-rem-create-from-prev-uiid") || "";
  remCreateFromPrevBusy = true;
  btn.setAttribute("disabled", "disabled");
  void (async () => {
    let willReload = false;
    try {
      const { ok, data } = await apiFetch("/api/costs", {
        method: "POST",
        body: { action: "rem-create-project-from-prev", costId }
      });
      if (!ok || !data.ok) {
        // 行き止まりにせず、この場で新規案件を作って請求書作成へ続ける
        const pl = uiId ? findCostPlacementByUiId(uiId) : null;
        if (pl?.cost && pl.orphan) {
          openProjectCreateForCostRows([pl.cost]);
        } else {
          showToastError(data.error || "案件の作成に失敗しました");
        }
        return;
      }
      showToast(
        data.createdNew
          ? `案件を作成しました: ${data.projectName}（請求 ${data.billingMonth}）`
          : `既存の案件へ割り当てました: ${data.projectName}`
      );
      // サーバー側で案件作成＋原価更新が済んでいるので、最新状態を取り直してから請求書作成画面を開く。
      // リロードまでボタンを押せないままにする（戻すと再押下が 409→新規案件フォームに流れ、重複案件を作りうる）
      willReload = true;
      window.setTimeout(() => {
        location.hash = `#project-detail:p${data.projectId}?composeInvoice=1`;
        window.location.reload();
      }, 900);
    } finally {
      if (!willReload) {
        remCreateFromPrevBusy = false;
        btn.removeAttribute("disabled");
      }
    }
  })();
});

// ── 物件グループ（CSビュー）: 開閉・グループ単位のまとめて割当・前月の案件から作成 ──
// ボタンの data 属性に描画時点の対象行IDが入っているため、ここではフィルタ状態を再現しない。

const COST_ALLOC_GROUP_PAGES = new Set(["cost-allocation", "cost-management"]);

/** 物件名フィルタ（IME変換中は再描画しない。フォーカスは 15-sp-backlog.js の復元パターンが保持する） */
document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  if (t.dataset.costAllocFilterBukken === undefined) return;
  if (e instanceof InputEvent && e.isComposing) return;
  ensureCostAllocListState();
  uiState.costAllocFilter.bukken = t.value || "";
  render();
});

/** グループヘッダーのクリック/Enter/Spaceで開閉 */
function toggleCostAllocBukkenGroup(head) {
  const bukken = head.getAttribute("data-rem-group-toggle") || "";
  if (!bukken) return;
  if (!uiState.costAllocOpenBukken || typeof uiState.costAllocOpenBukken !== "object") uiState.costAllocOpenBukken = {};
  uiState.costAllocOpenBukken[bukken] = !uiState.costAllocOpenBukken[bukken];
  render();
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  const head = target.closest("[data-rem-group-toggle]");
  if (!(head instanceof HTMLElement)) return;
  // ヘッダー内の操作要素は開閉させない（ただし「開いて割当先を選ぶ」ボタン自体が toggle の場合は通す）
  const interactive = target.closest("button, select, a, input, textarea, .sd-select");
  if (interactive && interactive !== head) return;
  toggleCostAllocBukkenGroup(head);
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const t = e.target;
  if (!(t instanceof HTMLElement) || t.getAttribute("data-rem-group-toggle") == null) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  e.preventDefault();
  toggleCostAllocBukkenGroup(t);
});

/** グループの割当先セレクト: 選択値をグループ別に保持（再描画で消えないように） */
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLSelectElement)) return;
  const bukken = t.getAttribute("data-rem-group-project-pick");
  if (!bukken) return;
  if (!uiState.costAllocGroupPick || typeof uiState.costAllocGroupPick !== "object") uiState.costAllocGroupPick = {};
  uiState.costAllocGroupPick[bukken] = String(t.value || "");
});

let remGroupActionBusy = false;

/** グループ単位「この案件へまとめて割当」: 一括割当バーと同じ更新経路（persistCostToServerUpdate）を行数分回す */
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-rem-group-assign]");
  if (!(btn instanceof HTMLElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  if (remGroupActionBusy) return;
  const bukken = btn.getAttribute("data-rem-group-assign") || "";
  const costIds = String(btn.getAttribute("data-rem-group-cost-ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!costIds.length) return;
  const assignPid = String((uiState.costAllocGroupPick && uiState.costAllocGroupPick[bukken]) || "").trim();
  if (!assignPid || assignPid === COST_UNASSIGNED_PROJECT_UI_ID) {
    showToast("割当先の案件を選んでください");
    return;
  }
  if (!isNoProjectBucketUiId(assignPid) && !projects.some((p) => p.id === assignPid)) {
    showToast("案件が見つかりません");
    return;
  }
  // 記憶（自動割当）は物件×グループ単位。複数グループが同居する物件を1案件へまとめると
  // グループ別に育てた記憶がこの案件で上書きされるため、確認を挟む（例: 三井ホームのセレクト/オーダー）
  const groupNames = new Set(
    costIds
      .map((id) => findCostPlacementByUiId(id)?.cost?.remGroup)
      .filter((g) => g && g !== "-")
  );
  if (groupNames.size > 1) {
    const okGo = window.confirm(
      `「${bukken}」には ${groupNames.size} 種類のキャンペーングループが含まれます。全行を同じ案件へ割り当て、グループ別の割当記憶もこの案件で上書きされます。よろしいですか？\n（グループごとに分けたい場合は、展開して行の「編集」から個別に割り当ててください）`
    );
    if (!okGo) return;
  }
  remGroupActionBusy = true;
  btn.setAttribute("disabled", "disabled");
  void (async () => {
    try {
      // 送信フィールドの組み立てはチェック一括バーと共通（assignOrphanCostsToProject）
      const { ok, fail } = await assignOrphanCostsToProject(costIds, assignPid);
      const groupLabel = bukken.replace(/^ad:/, "");
      if (fail && ok) showToastError(`まとめて割当: 成功 ${ok} 件・失敗 ${fail} 件`);
      else if (fail) showToastError(`まとめて割当に失敗しました（${fail} 件）`);
      else showToast(`「${groupLabel}」の ${ok} 件を割り当てました。`);
      render();
    } finally {
      remGroupActionBusy = false;
      btn.removeAttribute("disabled");
    }
  })();
});

/** グループ単位「まとめて請求書を作成」: サーバー側アクションを行数分回す。
 *  1行目で案件が作成され、2行目以降は「同名あれば割当のみ」の冪等動作で同じ案件へ入る。
 *  前月案件が見つからず1件目から失敗した場合は、行き止まりにせずその場で新規案件作成へ回す */
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-rem-group-create-prev]");
  if (!(btn instanceof HTMLElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  if (remGroupActionBusy || remCreateFromPrevBusy) return;
  const dbIds = String(btn.getAttribute("data-rem-group-cost-dbids") || "")
    .split(",")
    .map((s) => Number(s))
    .filter((v) => Number.isFinite(v) && v >= 1);
  if (!dbIds.length) return;
  const uiIds = String(btn.getAttribute("data-rem-group-cost-ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  remGroupActionBusy = true;
  remCreateFromPrevBusy = true; // 行単位ボタンとの相互排他（片側だけだと同一物件の案件が二重作成されうる）
  btn.setAttribute("disabled", "disabled");
  void (async () => {
    let ok = 0;
    let fail = 0;
    let firstError = "";
    const projectNames = new Set(); // グループ別記憶で行ごとに割当先が分かれることがある
    const projectIds = new Set();
    let billingMonth = "";
    let willReload = false;
    try {
      for (const costId of dbIds) {
        const { ok: httpOk, data } = await apiFetch("/api/costs", {
          method: "POST",
          body: { action: "rem-create-project-from-prev", costId }
        });
        if (httpOk && data.ok) {
          ok++;
          if (data.projectName) projectNames.add(String(data.projectName));
          if (data.projectId) projectIds.add(Number(data.projectId));
          billingMonth = data.billingMonth || billingMonth;
        } else {
          fail++;
          if (!firstError) firstError = String(data.error || "案件の作成に失敗しました");
          if (!ok) {
            // 1件目から失敗（記憶なし・候補が一意でない等）は行き止まりにせず、
            // その場で新規案件を作って請求書作成へ続ける
            const rows = uiIds
              .map((id) => findCostPlacementByUiId(id))
              .filter((pl) => pl?.cost && pl.orphan)
              .map((pl) => pl.cost);
            if (rows.length) openProjectCreateForCostRows(rows, { name: btn.getAttribute("data-rem-group-create-prev") || "" });
            else showToastError(firstError);
            return;
          }
        }
      }
      const names = [...projectNames].join(" / ");
      if (fail) {
        // 途中失敗も理由ごと見せる（成功文言だけ出すと失敗に気づけない）
        showToastError(`成功 ${ok} 件・失敗 ${fail} 件（${firstError}）。割当先: ${names}`);
        willReload = true;
        window.setTimeout(() => window.location.reload(), 1400);
      } else {
        // グループ内の行がグループ別記憶で複数の案件に分かれた稀なケースは、
        // どちらの請求書を開くべきか決められないため自動オープンをスキップし理由を伝える
        showToast(
          projectIds.size === 1
            ? `${names}（請求 ${billingMonth}）へ ${ok} 件を割り当てました`
            : `${names}（請求 ${billingMonth}）へ ${ok} 件を割り当てました（複数案件のため請求書作成は開きません）`
        );
        willReload = true;
        window.setTimeout(() => {
          if (projectIds.size === 1) {
            location.hash = `#project-detail:p${[...projectIds][0]}?composeInvoice=1`;
          }
          window.location.reload();
        }, 1400);
      }
    } finally {
      if (!willReload) {
        remGroupActionBusy = false;
        remCreateFromPrevBusy = false;
        btn.removeAttribute("disabled");
      }
    }
  })();
});

// ── 案件詳細: 「この案件に紐づきそうな未割当のREM原価」バナー（CS/経理ビューのみ） ──
// 判定はヒント（記憶の逆引き）の exampleProjectId ＝「記憶の語幹に一致する直近の案件」。
// 請求書を起票した瞬間に気づける導線で、放置しても翌朝の日次Cronが同じ判定で自動割当する。

function remCostAllocProjectBannerHtml(p) {
  if (!p || p.dbId == null) return "";
  if (!userSeesCostAllocationCsView()) return ""; // 営業には未割当データ自体が配信されない
  ensureRemAllocHintsLoaded();
  if (!remAllocHintsState.loaded) return "";
  const hints = remAllocHintsState.hints || {};
  const rows = ensureOrphanCosts().filter(
    (c) =>
      c.importSource === "rem" &&
      c.remBukken &&
      !c.disputeNote &&
      hints[c.remBukken] &&
      Number(hints[c.remBukken].exampleProjectId) === Number(p.dbId)
  );
  if (!rows.length) return "";
  const exTotal = rows.reduce((s, c) => s + Number(c.amount || 0), 0);
  const byCat = new Map();
  for (const c of rows) byCat.set(c.category, (byCat.get(c.category) || 0) + Number(c.amount || 0));
  const catText = [...byCat.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([cat, sum]) => `${cat} ${yen.format(sum)}`)
    .join(" ・ ");
  const bukkens = [...new Set(rows.map((c) => c.remBukken))];
  const uiIds = rows.map((c) => String(c.id)).join(",");
  return `
    <div class="rem-pj-banner">
      <div class="rem-pj-banner-txt">
        <strong>この案件に紐づきそうな未割当の原価が ${rows.length} 件（税抜 ${yen.format(exTotal)}）あります</strong>
        <div class="rem-pj-banner-sub">物件「${escapeHtml(bukkens.join("・"))}」 ─ ${escapeHtml(catText)}（放置しても翌朝に自動割当されます）</div>
      </div>
      <button type="button" class="cl-btn cl-btn-primary" data-rem-banner-assign="${escapeHtmlAttr(p.id)}" data-rem-group-cost-ids="${escapeHtmlAttr(uiIds)}">${rows.length}件をこの案件へ割当</button>
      <button type="button" class="inline-btn ghost" data-rem-banner-review="${escapeHtmlAttr(bukkens[0] || "")}">明細を確認</button>
    </div>`;
}

let remBannerAssignBusy = false;

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-rem-banner-assign]");
  if (!(btn instanceof HTMLElement)) return;
  if (currentRoute().page !== "project-detail") return;
  if (remBannerAssignBusy) return;
  const assignPid = String(btn.getAttribute("data-rem-banner-assign") || "");
  const costIds = String(btn.getAttribute("data-rem-group-cost-ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!assignPid || !costIds.length) return;
  if (!projects.some((p) => p.id === assignPid)) {
    showToast("案件が見つかりません");
    return;
  }
  remBannerAssignBusy = true;
  btn.setAttribute("disabled", "disabled");
  void (async () => {
    try {
      const { ok, fail } = await assignOrphanCostsToProject(costIds, assignPid);
      if (fail && ok) showToastError(`割当: 成功 ${ok} 件・失敗 ${fail} 件`);
      else if (fail) showToastError(`割当に失敗しました（${fail} 件）`);
      else showToast(`${ok} 件をこの案件へ割り当てました。`);
      render();
    } finally {
      remBannerAssignBusy = false;
      btn.removeAttribute("disabled");
    }
  })();
});

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-rem-banner-review]");
  if (!(btn instanceof HTMLElement)) return;
  if (currentRoute().page !== "project-detail") return;
  const bukken = btn.getAttribute("data-rem-banner-review") || "";
  ensureCostAllocListState();
  uiState.costAllocFilter.bukken = bukken;
  if (bukken) uiState.costAllocOpenBukken = { [bukken]: true };
  location.hash = "#cost-allocation";
});

// 照会の案件カード: Enter/Spaceでも開閉（クリックハンドラは 15d 側）
document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const t = e.target;
  if (!(t instanceof HTMLElement) || t.getAttribute("data-cost-review-card-toggle") == null) return;
  e.preventDefault();
  const pid = t.getAttribute("data-cost-review-card-toggle") || "";
  if (!uiState.costReviewOpenProj || typeof uiState.costReviewOpenProj !== "object") uiState.costReviewOpenProj = {};
  uiState.costReviewOpenProj[pid] = !uiState.costReviewOpenProj[pid];
  render();
});

/** 広告CSVグループ版「まとめて請求書を作成」: サーバー側で一括処理（1リクエスト）。
 *  REM版と違い記憶がアカウント→案件IDを直接指すため、行ごとの解決ループは不要。
 *  記憶先の前月案件が見つからない場合は、行き止まりにせずその場で新規案件作成へ回す */
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-ad-group-create-prev]");
  if (!(btn instanceof HTMLElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  if (remGroupActionBusy || remCreateFromPrevBusy) return;
  const account = btn.getAttribute("data-ad-group-create-prev") || "";
  const costIds = String(btn.getAttribute("data-rem-group-cost-dbids") || "")
    .split(",")
    .map((s) => Number(s))
    .filter((v) => Number.isFinite(v) && v >= 1);
  if (!costIds.length) return;
  const uiIds = String(btn.getAttribute("data-rem-group-cost-ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  remGroupActionBusy = true;
  remCreateFromPrevBusy = true;
  btn.setAttribute("disabled", "disabled");
  void (async () => {
    let willReload = false;
    try {
      const { ok, data } = await apiFetch("/api/costs", {
        method: "POST",
        body: { action: "ad-create-project-from-prev", costIds }
      });
      if (!ok || !data.ok) {
        const rows = uiIds
          .map((id) => findCostPlacementByUiId(id))
          .filter((pl) => pl?.cost && pl.orphan)
          .map((pl) => pl.cost);
        if (rows.length) openProjectCreateForCostRows(rows, { name: account, clientName: remAllocHintsState.loaded ? remAllocHintsState.adHints[account]?.clientName : "" });
        else showToastError(data.error || "案件の作成に失敗しました");
        return;
      }
      showToast(
        `${data.projectName}（請求 ${data.billingMonth}）へ「${account}」の ${data.assigned} 件を割り当てました`
      );
      // サーバー側で案件作成＋原価更新が済んでいるので、最新状態を取り直してから請求書作成画面を開く
      willReload = true;
      window.setTimeout(() => {
        if (data.projectId) location.hash = `#project-detail:p${data.projectId}?composeInvoice=1`;
        window.location.reload();
      }, 1400);
    } finally {
      if (!willReload) {
        remGroupActionBusy = false;
        remCreateFromPrevBusy = false;
        btn.removeAttribute("disabled");
      }
    }
  })();
});

/** 原価割当（未割当ビュー）の表示形式トグル: 予測グループ⇄一覧 */
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const btn = target.closest("[data-cost-alloc-view]");
  if (!(btn instanceof HTMLElement)) return;
  if (!COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) return;
  const mode = btn.getAttribute("data-cost-alloc-view");
  if (mode !== "cards" && mode !== "table") return;
  const { flt, srt } = ensureCostAllocListState();
  if (flt.viewMode === mode) return;
  flt.viewMode = mode;
  // カード表示には並び替えUIが無いため、一覧で選んだ並び順を持ち込まない（見えない並び替えを残さない）
  if (mode === "cards") Object.assign(srt, { key: "default", dir: "desc" });
  render();
});
// ─────────────────────────────────────────────────────────────
// 自分の担当課題／関わった課題：LINE風2ペイン表示（トークビュー）
//   - 左=課題一覧（最新コメントのプレビュー・未読メンションバッジ）／右=選択中課題の会話
//   - 既定は従来のカード一覧のまま。ヘッダーの表示切替トグルで選ぶ（アカウントごとに記憶・PC限定）
//   - メッセージ表示・コンポーズ・既読化・スレッド取得は既存の課題詳細ページと同じ仕組みを再利用する
//   - イベントは独立リスナー（16 の巨大 click ハンドラには足さない）
// ─────────────────────────────────────────────────────────────

/** 表示切替の保存先キー。アカウントごとに分ける（並び替え設定と同じ方針） */
function progressAssignedViewModeStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.assignedViewMode.${me}` : "";
}

let pmAssignedViewModeLoadedKey = null;

/** 表示形式（list|talk）。初回（とアカウント切替後）は保存値を復元する。
 * 既定はトーク（ユーザー確定仕様 2026-08-26）。明示的に「一覧」を選んだ人だけ一覧のまま。 */
function progressAssignedViewMode() {
  const storeKey = progressAssignedViewModeStorageKey();
  if (storeKey && pmAssignedViewModeLoadedKey !== storeKey) {
    pmAssignedViewModeLoadedKey = storeKey;
    let stored = "";
    try {
      stored = localStorage.getItem(storeKey) || "";
    } catch {
      /* 壊れていても既定(talk)へ丸めるだけでよい */
    }
    uiState.progressAssignedViewMode = stored === "list" ? "list" : "talk";
  }
  return uiState.progressAssignedViewMode === "list" ? "list" : "talk";
}

function setProgressAssignedViewMode(mode) {
  const next = mode === "talk" ? "talk" : "list";
  uiState.progressAssignedViewMode = next;
  pmAssignedViewModeLoadedKey = progressAssignedViewModeStorageKey();
  const storeKey = progressAssignedViewModeStorageKey();
  if (storeKey) {
    try {
      localStorage.setItem(storeKey, next);
    } catch {
      /* 保存できなくても表示切替そのものは動くので握りつぶす */
    }
  }
}

/** スマホ幅・タッチ端末では常に従来のカード一覧（トークビューはPC専用機能） */
function progressAssignedTalkViewAvailable() {
  return !(
    window.matchMedia?.("(pointer: coarse)")?.matches ||
    window.matchMedia?.("(max-width: 900px)")?.matches ||
    (typeof window !== "undefined" && window.innerWidth <= 900)
  );
}

/** 自分の担当課題ビューがトーク表示中か。一覧描画(progressManagementPage)・renderNow()の
 * スクロール制御など、判定が要る場所すべてがこの1関数を参照する（条件がずれて片方だけ
 * 更新漏れ、を防ぐ） */
function progressAssignedTalkActive(route) {
  return (
    progressManagementAssignedMeRouteActive(route) &&
    progressAssignedTalkViewAvailable() &&
    progressAssignedViewMode() === "talk"
  );
}

/** 未読メンション件数を課題IDで引けるように（未読=自分宛メンション、のユーザー確定仕様）。
 * 既存のお知らせメニュー用ロジック（メッセージ読込済みならツリーから実数、未読込ならAPI集計）をそのまま利用する。 */
function progressAssignedTalkUnreadMentionMap() {
  const map = new Map();
  for (const t of listUnreadToDoMentionTargetsForNotify()) {
    const aid = Number(t.agendaId);
    if (!Number.isFinite(aid) || aid < 1) continue;
    const c = Number(t.mentionCnt != null ? t.mentionCnt : t.count);
    map.set(aid, Number.isFinite(c) && c >= 1 ? c : 1);
  }
  return map;
}

/** 左一覧の1行に出す時刻。今日は時刻のみ、それ以前は月/日 */
function progressAssignedTalkTimeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.getFullYear() === y.getFullYear() && d.getMonth() === y.getMonth() && d.getDate() === y.getDate();
  if (yesterday) return "昨日";
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** 表示切替トグル（担当課題一覧の見出し行に添える）。トークビューが使えない画面幅では出さない */
function progressAssignedViewToggleHtml() {
  if (!progressAssignedTalkViewAvailable()) return "";
  const mode = progressAssignedViewMode();
  return `<div class="pm-assigned-view-toggle" role="group" aria-label="表示形式">
    <button type="button" class="pm-assigned-view-toggle-btn${mode === "list" ? " is-active" : ""}" data-pm-assigned-view-mode="list" aria-pressed="${mode === "list" ? "true" : "false"}">一覧</button>
    <button type="button" class="pm-assigned-view-toggle-btn${mode === "talk" ? " is-active" : ""}" data-pm-assigned-view-mode="talk" aria-pressed="${mode === "talk" ? "true" : "false"}">トーク</button>
  </div>`;
}

/** トークビュー左一覧の1行 */
function progressAssignedTalkItemHtml(row, opts) {
  const { ch, ag, themeName, overdue, unread } = row;
  const involvedMode = Boolean(opts?.involvedMode);
  const selected = String(opts?.selectedAgendaId || "") === String(ag.id);
  const priMod = progressMyAssignPriPillMod(ag.priority);
  const priLbl = normalizeProgressAgendaPriority(ag.priority);
  const statusStr = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  const statusMod = progressKanColModifier(statusStr);
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);
  const lm = ag.lastMessage;
  const previewName = lm && !lm.isSystem ? progressCardShortName(lm.userName || "") : "";
  const previewBody = lm ? String(lm.body || "").replace(/\s+/g, " ").trim() : "";
  const preview = lm
    ? `${previewName ? escapeHtml(previewName) + ": " : ""}${escapeHtml(previewBody)}`
    : `<span class="pm-assigned-talk-preview-empty">コメントはまだありません</span>`;
  const timeLabel = progressAssignedTalkTimeLabel(lm?.createdAt || ag.updatedAt);
  let assigneeBadge = "";
  if (involvedMode) {
    const assigneeName = progressIssueAssigneeDisplayName(ag);
    assigneeBadge = `<span class="pm-assigned-talk-item-assignee">担当 ${escapeHtml(progressCardShortName(assigneeName))}</span>`;
  }
  return `<div class="pm-assigned-talk-item${selected ? " is-selected" : ""}${overdue ? " pm-assigned-talk-item--overdue" : ""}" data-pm-assigned-talk-open="${escapeHtmlAttr(String(ag.id))}" role="listitem">
    <span class="pm-my-assign-pri pm-my-assign-pri--${priMod}" aria-label="優先度: ${escapeHtmlAttr(priLbl)}">${escapeHtml(priLbl)}</span>
    <div class="pm-assigned-talk-item-main">
      <div class="pm-assigned-talk-item-row1">
        <span class="pm-kan-card-key">${progressIssueNumberHtml(ag.id)}</span>
        <span class="pm-assigned-talk-item-title">${escapeHtml((ag.title && String(ag.title).trim()) || "（無題）")}</span>
      </div>
      <div class="pm-assigned-talk-item-preview">${preview}</div>
      <div class="pm-assigned-talk-item-row3">
        <span class="pm-my-assign-status pm-my-assign-status--${statusMod}">${escapeHtml(statusStr)}</span>
        ${assigneeBadge}
        <span class="pm-assigned-talk-item-theme">${escapeHtml(themeName || "—")}</span>
      </div>
    </div>
    <div class="pm-assigned-talk-item-side">
      ${timeLabel ? `<span class="pm-assigned-talk-item-time">${escapeHtml(timeLabel)}</span>` : ""}
      ${dueDisp ? `<span class="pm-my-assign-card-due${overdue ? " pm-my-assign-card-due--overdue" : ""}">${escapeHtml(dueDisp)}</span>` : ""}
      ${unread > 0 ? `<span class="pm-assigned-talk-unread">${unread > 99 ? "99+" : unread}</span>` : ""}
    </div>
  </div>`;
}

/** 会話を開いた時点の既読位置スナップショット。開いた直後に既読化（markAgendaRead）が走って
 * ag.lastReadAt が「今」に更新されるため、更新前の値をここで確保しておく。
 * 最初の描画は markAgendaRead（renderNow の rAF 内）より先に走るので、初回描画時の確保で間に合う。 */
function progressTalkEnsureUnreadAnchor(ag) {
  const cur = uiState.progressTalkUnreadAnchor;
  if (cur && String(cur.agendaId) === String(ag.id)) return cur;
  const anchor = { agendaId: String(ag.id), lastReadAt: ag.lastReadAt || "" };
  uiState.progressTalkUnreadAnchor = anchor;
  return anchor;
}

/** スナップショット基準で「最初の未読メッセージ」の添字を返す（無ければ -1）。
 * 自分の発言・自分が起こしたシステム履歴は未読に数えない（自分の操作は見えているため） */
function progressTalkFirstUnreadIndex(msgs, anchor) {
  const myDb = progressCurrentUserDbId();
  const readTs = anchor.lastReadAt ? new Date(anchor.lastReadAt).getTime() : 0;
  if (anchor.lastReadAt && !Number.isFinite(readTs)) return -1;
  for (let i = 0; i < msgs.length; i++) {
    const m = msgs[i];
    if (myDb != null && Number(m.userId) === myDb) continue;
    const ts = m.createdAt ? new Date(m.createdAt).getTime() : NaN;
    if (!Number.isFinite(ts)) continue;
    if (ts > readTs) return i;
  }
  return -1;
}

/** トークのメモ（説明文・添付）に表示するものがあるか */
function progressAssignedTalkMemoHasContent(ag) {
  return Boolean(String(ag?.body || "").trim() || progressAgendaAttachmentsList(ag).length);
}

/** トークでの詳細開閉状態。既定=閉（ユーザー確定仕様 2026-08-26。会話を主役にする）。
 * 保存キーは課題詳細ページのメモ欄と共通（pm_memo_expanded_*）なので、
 * 一度開閉すればその状態は両ページで引き継がれる。既定だけがページで異なる */
function progressTalkMemoExpanded(agendaId) {
  try {
    const stored = localStorage.getItem(`pm_memo_expanded_${agendaId}`);
    return stored == null ? false : stored === "1";
  } catch {
    return false;
  }
}

/** ヘッダーの詳細開閉トグル。見た目は普通の課題詳細ページの「開く ▾／閉じる ▲」と同じ
 * 青いテキストリンク（ユーザー指定）。中身が無い課題では出さない。
 * 既定が課題詳細ページ（=展開）と異なるため、共有の data-pm-memo-toggle ではなく
 * トーク専用の属性＋下のリスナーで開閉する（共有ハンドラだと初回クリックが空振りする） */
function progressAssignedTalkMemoToggleHtml(ag) {
  if (!progressAssignedTalkMemoHasContent(ag)) return "";
  const expanded = progressTalkMemoExpanded(ag.id);
  const hasUpdate = progressMemoHasUpdate(ag);
  return `<button type="button" class="pm-assigned-talk-memo-toggle" data-pm-talk-memo-toggle="${escapeHtmlAttr(String(ag.id))}" aria-expanded="${expanded ? "true" : "false"}">${hasUpdate ? `<span class="pm-memo-badge">更新あり</span>` : ""}<span class="pm-memo-toggle-label">${expanded ? "閉じる ▲" : "開く ▾"}</span></button>`;
}

/** ヘッダー下のメモ本体（説明文＋添付＋工程バー）。開いているときだけ描画し、
 * 閉じているときは帯ごと消える（常設の区切り帯は変、との実機指摘 2026-08-26） */
function progressAssignedTalkMemoBodyHtml(ag, ch) {
  if (!progressAssignedTalkMemoHasContent(ag)) return "";
  if (!progressTalkMemoExpanded(ag.id)) return "";
  // 開いて見えた時点で「閲覧済み」にして更新ありバッジを消す（課題詳細ページのメモ欄と同じ挙動）
  if (progressMemoHasUpdate(ag) && ag?.bodyUpdatedAt) {
    try {
      localStorage.setItem(`pm_memo_last_viewed_${ag.id}`, String(Date.now()));
    } catch {
      /* localStorage不可は既読記録をスキップするだけ */
    }
  }
  const body = String(ag.body || "").trim();
  return `<div class="pm-assigned-talk-memo">
    ${body ? progressIssueBodyDisplayHtml(body) : ""}
    ${progressIssueAttachmentsInlineHtml(ag)}
    ${progressIssueStepBarHtml(ag, ch) || ""}
  </div>`;
}

/** 右ペイン：課題ヘッダー＋スレッド＋コンポーズ（メッセージ表示・コンポーズは課題詳細ページと共有部品） */
function progressAssignedTalkChatHtml(ch, ag) {
  const statusStr = normalizeProgressTodoStatusForChallenge(ag.status, ch);
  const statusMod = progressKanColModifier(statusStr);
  const overdue = isProgressAgendaDueOverdue(ag, ch);
  const dueDisp = formatProgressAgendaDueDisplay(ag.dueDate);
  const assigneeName = progressIssueAssigneeDisplayName(ag);
  const assigneeUser = assigneeName !== "—" ? progressAgendaAssigneeUser(ag) : null;
  const requesterName = progressAgendaCreatorDisplayName(ag);
  const themeLabel = (ch?.title && String(ch.title).trim()) || "（無題）";
  const threadPending = Boolean(ag.threadDeferred) || Boolean(uiState.progressMgmtDetailLoading);
  const msgs = progressIssueMessagesSortedOldestFirst(ag.messages);
  // 別の課題を開いたときに前の課題の「編集中」状態を持ち越さない（課題詳細ページと同じガード）
  if (uiState.progressEditingMessageId) {
    const mid = String(uiState.progressEditingMessageId);
    if (!msgs.some((m) => String(m.id) === mid)) uiState.progressEditingMessageId = "";
  }
  // 「前回読んだ続き」から表示するための既読位置スナップショット（LINEの「ここから未読」相当）
  const unreadAnchor = progressTalkEnsureUnreadAnchor(ag);
  const firstUnreadIdx = threadPending || msgs.length === 0 ? -1 : progressTalkFirstUnreadIndex(msgs, unreadAnchor);
  const unreadDividerHtml = `<div class="pm-talk-unread-divider" data-pm-unread-divider="1" role="separator">ここから未読</div>`;
  const threadMsgs = threadPending
    ? `<div class="pm-thread-load-msg pm-thread-load-msg--cat">${flowPilotNowLoadingBlock({ inline: true, subtitle: "やりとりを読み込んでいます" })}</div>`
    : msgs.length === 0
      ? `<p class="pm-empty pm-assigned-talk-thread-empty">まだやりとりがありません。最初のコメントを送ってみましょう。</p>`
      : msgs
          .map((m, i) => `${i === firstUnreadIdx ? unreadDividerHtml : ""}${progressIssueMessageRowHtml(m)}`)
          .join("");
  return `<section class="pm-assigned-talk-chat">
    <header class="pm-assigned-talk-chat-head">
      <div class="pm-assigned-talk-chat-head-main">
        <div class="pm-assigned-talk-chat-title">
          <span class="pm-kan-card-key">${progressIssueNumberHtml(ag.id)}</span>
          <h2>${escapeHtml((ag.title && String(ag.title).trim()) || "（無題）")}</h2>
        </div>
        <div class="pm-assigned-talk-chat-meta">
          <span class="pm-my-assign-status pm-my-assign-status--${statusMod}">${escapeHtml(statusStr)}</span>
          ${dueDisp ? `<span>期限 <b class="${overdue ? "pm-assigned-talk-overdue-text" : ""}">${escapeHtml(dueDisp)}${overdue ? "（超過）" : ""}</b></span>` : ""}
          <span>担当 ${assigneeUser ? progressUserAvatarHtml(assigneeUser, "pm-assignee-avatar--talk", assigneeName) : ""}${escapeHtml(progressCardShortName(assigneeName))}</span>
          <span>登録者 ${escapeHtml(progressCardShortName(requesterName))}</span>
          <span>プロジェクト: ${escapeHtml(themeLabel)}</span>
        </div>
      </div>
      <div class="pm-assigned-talk-chat-head-actions">
        ${progressAssignedTalkMemoToggleHtml(ag)}
        <button type="button" class="inline-btn ghost" data-pm-issue-status-change="${escapeHtmlAttr(String(ag.id))}">課題の詳細</button>
      </div>
    </header>
    ${progressAssignedTalkMemoBodyHtml(ag, ch)}
    <div class="pm-assigned-talk-thread pm-thread--assigned-talk">${threadMsgs}</div>
    <div class="pm-assigned-talk-compose">${progressIssueComposeBoxHtml(ch, ag)}</div>
  </section>`;
}

/** トークビュー本体。issueParam があればその課題を右ペインに開く（未指定/見つからない時は先頭の課題） */
function progressAssignedTalkViewHtml(issueParam) {
  // 担当中以外（起票した・関わった）は担当者が自分ではないので、行に担当者を添える
  const involvedMode = progressManagementAssignScope() !== "me";
  const baseRows = progressCollectMyAssignmentsRows();
  const fetching = Boolean(uiState.progressMgmtFetchInFlight);
  const loadFailed =
    Boolean(uiState.progressMgmtSuppressEmptyRefetch && baseRows.length === 0) && !fetching;

  if (fetching && baseRows.length === 0) {
    return `<div class="page pj-page pm-page pm-page--assigned-talk">
      <div class="pj-page-wrap">${progressAssignedViewToggleHeaderHtml()}
        <div class="pm-my-assign-loading">${flowPilotNowLoadingBlock({ subtitle: "担当課題を読み込んでいます" })}</div>
      </div>
    </div>`;
  }
  if (loadFailed) {
    return `<div class="page pj-page pm-page pm-page--assigned-talk">
      <div class="pj-page-wrap">${progressAssignedViewToggleHeaderHtml()}
        <div class="pm-my-assign-empty"><p class="pm-empty">データを取得できませんでした。ページを再読み込みしてください。</p></div>
      </div>
    </div>`;
  }

  const searchQ = normalizeProgressSearchText(uiState.progressIssueSearch);
  const quickFilter = normalizeProgressMyAssignQuickFilter(uiState.progressMyAssignQuickFilter);
  // ウォッチ中・テーマ絞り込みはトークに切替UIが無いので効かせない（一覧で付けたまま来ると、
  // 見えない絞り込みで課題が消えたように見えるため）。保留を除くはトークにもチップがあるので一覧と共有して効かせる
  const hideHold = !!uiState.progressMyAssignHideHold;
  const unreadMap = progressAssignedTalkUnreadMentionMap();

  // 各行の overdue/unread は一度だけ計算し、以降はこの値を使い回す（行ごとの再計算をしない）
  const enriched = baseRows.map((r) => ({
    ...r,
    overdue: isProgressAgendaDueOverdue(r.ag, r.ch),
    unread: unreadMap.get(Number(r.ag.id)) || 0
  }));

  let filtered = progressMyAssignRowsForQuickFilter(enriched, quickFilter);
  if (hideHold) filtered = filtered.filter((r) => r.agStatus !== "保留");
  if (searchQ) filtered = filtered.filter((r) => progressAgendaMatchesSearch(r.ag, searchQ));
  // 既定の並びは「更新が新しい順」（ユーザー確定仕様）。updatedTs は課題本体＋コメント投稿の両方で touchAgendaActivity 済み
  const sortedRows = [...filtered].sort((a, b) => b.updatedTs - a.updatedTs);

  const total = baseRows.length;
  const overdueN = enriched.filter((r) => r.overdue).length;
  const holdN = enriched.filter((r) => r.agStatus === "保留").length;
  const allActive = quickFilter === "all" && !hideHold;
  // 一覧と同じく、保留の課題がある時（またはトグル中）だけ出す
  const holdChip =
    holdN > 0 || hideHold
      ? `<button type="button" class="pm-my-assign-stat pm-my-assign-stat--filter pm-my-assign-stat--muted${hideHold ? " is-active" : ""}" data-pm-my-assign-hide-hold-toggle="1" aria-pressed="${hideHold ? "true" : "false"}" title="保留の担当課題を一覧から除く"><span class="pm-my-assign-stat__label">保留を除く</span><span class="pm-my-assign-stat__value">${holdN}</span></button>`
      : "";

  // 右ペインで開く課題は issue= の指定のみで決める（一覧の並びで暗黙に決めると、
  // URLと表示中の課題がズレて再読込のたびに違う課題が開く事故になるため自動選択はしない）。
  // 検索・絞り込みで一覧から一時的に外れても、開いている会話は閉じない（enriched=未絞り込みから探す）。
  // 担当を他の人へ変えた直後など一覧の対象から外れた課題も、サーバが hydrateAgendaId として
  // 同梱していれば右ペインには残す（一覧から消えても「変更できた」と分かるように）
  let selectedRow = issueParam ? enriched.find((r) => String(r.ag.id) === String(issueParam)) || null : null;
  if (!selectedRow && issueParam) {
    const hit = findProgressAgendaById(issueParam);
    if (hit) selectedRow = { ch: hit.challenge, ag: hit.agenda };
  }

  const listBody =
    baseRows.length === 0
      ? `<div class="pm-my-assign-empty"><p class="pm-empty">自分が担当の${PM_WORD.issue}はありません。</p></div>`
      : sortedRows.length === 0
        ? `<div class="pm-my-assign-empty"><p class="pm-empty">この条件に一致する${PM_WORD.issue}がありません。</p></div>`
        : `<div class="pm-assigned-talk-list-scroll" role="list">${sortedRows
            .map((r) => progressAssignedTalkItemHtml(r, { involvedMode, selectedAgendaId: selectedRow?.ag?.id }))
            .join("")}</div>`;

  const chatPane = selectedRow
    ? progressAssignedTalkChatHtml(selectedRow.ch, selectedRow.ag)
    : `<section class="pm-assigned-talk-chat pm-assigned-talk-chat--empty"><p class="pm-empty">左の一覧から課題を選んでください</p></section>`;

  return `<div class="page pj-page pm-page pm-page--assigned-talk">
    <div class="pj-page-wrap">
      ${progressAssignedViewToggleHeaderHtml()}
      <div class="pm-assigned-talk-split">
        <section class="pm-assigned-talk-list">
          <div class="pm-my-assign-search">${progressIssueSearchInputHtml()}</div>
          <div class="pm-my-assign-stats" aria-label="件数サマリー">
            <button type="button" class="pm-my-assign-stat pm-my-assign-stat--filter pm-my-assign-stat--neutral${allActive ? " is-active" : ""}" data-pm-my-assign-show-all="1" aria-pressed="${allActive ? "true" : "false"}" title="すべての担当課題を表示"><span class="pm-my-assign-stat__label">合計</span><span class="pm-my-assign-stat__value">${total}</span></button>
            <button type="button" class="pm-my-assign-stat pm-my-assign-stat--filter${overdueN > 0 ? " pm-my-assign-stat--danger" : " pm-my-assign-stat--muted"}${quickFilter === "overdue" ? " is-active" : ""}" data-pm-my-assign-quick-filter="overdue" aria-pressed="${quickFilter === "overdue" ? "true" : "false"}" title="期限超過の課題だけ表示"><span class="pm-my-assign-stat__label">期限超過</span><span class="pm-my-assign-stat__value">${overdueN}</span></button>
            ${holdChip}
          </div>
          ${listBody}
        </section>
        ${chatPane}
      </div>
    </div>
  </div>`;
}

/** 担当課題ページの見出し。スコープ（担当中/起票した/関わった）で変わる。従来一覧・トークビュー共通 */
function progressAssignScopeHeading() {
  const scope = progressManagementAssignScope();
  if (scope === "created") return "起票した課題";
  if (scope === "involved") return "関わった課題";
  return "自分の担当課題";
}

/** ヘッダー（見出し＋現行のスコープ/プロジェクトタブ＋表示切替トグル）。従来一覧と同じ構造・タブバーを流用する */
function progressAssignedViewToggleHeaderHtml() {
  return `${spDesktopRecommendedNoticeHtml()}
    <div class="pj-head">
      <div class="pj-head-stack">
        <h2>${progressAssignScopeHeading()}</h2>
      </div>
      <div class="pj-head-actions">${progressQuickAddButtonHtml()}${progressAssignedViewToggleHtml()}</div>
    </div>
    ${progressGroupListModeTabsHtml()}
    ${progressMentionInboxHtml()}`;
}

document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;

  // トークビューで自分がコメントを送ったら「ここから未読」の区切りを解除し、最下部追従に戻す
  // （送信処理本体は 16-render-routing の既存ハンドラ。ここでは区切りの解除だけを行う）
  if (t.closest("[data-pm-send-message]") && progressAssignedTalkActive()) {
    uiState.progressTalkUnreadAnchor = null;
    // 送信ハンドラ側の処理を止めない（return しない）
  }

  const modeBtn = t.closest("[data-pm-assigned-view-mode]");
  if (modeBtn instanceof HTMLElement && currentRoute().page === "progress-management") {
    const mode = String(modeBtn.dataset.pmAssignedViewMode || "");
    if (mode !== progressAssignedViewMode()) {
      setProgressAssignedViewMode(mode);
      render();
    }
    return;
  }

  // トークの詳細開閉（既定=閉のためトーク専用ハンドラ。保存キーは課題詳細ページと共通）
  const memoToggle = t.closest("[data-pm-talk-memo-toggle]");
  if (memoToggle instanceof HTMLElement && currentRoute().page === "progress-management") {
    const aid = String(memoToggle.dataset.pmTalkMemoToggle || "").trim();
    if (!aid) return;
    const next = !progressTalkMemoExpanded(aid);
    try {
      localStorage.setItem(`pm_memo_expanded_${aid}`, next ? "1" : "0");
      // 開いた瞬間に「閲覧済み」として更新ありバッジを消す（課題詳細ページのメモ欄と同じ）
      if (next) localStorage.setItem(`pm_memo_last_viewed_${aid}`, String(Date.now()));
    } catch {
      /* localStorage不可は開閉状態を記憶しないだけで動作は継続 */
    }
    render();
    return;
  }

  const openItem = t.closest("[data-pm-assigned-talk-open]");
  if (openItem instanceof HTMLElement && currentRoute().page === "progress-management") {
    const id = String(openItem.dataset.pmAssignedTalkOpen || "").trim();
    if (!id) return;
    const route = currentRoute();
    const sp = new URLSearchParams(route.searchParams ? String(route.searchParams) : "");
    // assigned= が無いまま issue= だけ足すと、このビューへの入口が「クエリなしの
    // 既定表示」だった場合に assignedFlat の判定から外れてしまう（プロジェクト一覧へ飛ばされる）。
    // 明示的にスコープを固定してから issue= を足す。
    sp.set("assigned", progressManagementAssignScope(route));
    sp.set("issue", id);
    const frag = `progress-management?${sp.toString()}`;
    if (location.hash.replace(/^#/, "") !== frag) location.hash = frag;
    return;
  }
});
// ────────────────────────────────────────────────────────────────
// クライアント・パートナーの「変更履歴」（誰がいつ何を変えたか）。
//
// 案件は以前から専用テーブル（fp_project_edit_history）＋基本情報タブの「編集履歴」ボタンで
// 見られたが、取引先には入口が無く「変更履歴：なし」の固定表示だけだった。
// 記録自体は監査ログ（fp_audit_log）に resource_type + resource_id 付きで残っているので、
// そこから引いて案件と同じモーダルで表示する（新規テーブルなし）。
//
// 取得は /api/entity-change-history、表示は 07-pdf-output.js の
// renderProjectEditHistoryModal を source 付きで共用する。
// ────────────────────────────────────────────────────────────────

/** 取引先詳細のヘッダーのボタン列に置く「変更履歴」ボタン。契約一覧を取引先一覧へ集約したため、
 *  入口を契約一覧の詳細から取引先詳細へ移した（2026-10-28） */
function clientChangeHistoryButtonHtml(entityType, dbId, name) {
  return `<button type="button" class="cl-btn cl-btn-outline" data-open-change-history="${escapeHtmlAttr(entityType)}:${escapeHtmlAttr(String(dbId))}" data-change-history-name="${escapeHtmlAttr(name || "")}">変更履歴</button>`;
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const el = target.closest("[data-open-change-history]");
  if (!(el instanceof HTMLElement)) return;

  const raw = String(el.dataset.openChangeHistory || "");
  const sep = raw.indexOf(":");
  if (sep < 1) return;
  const source = raw.slice(0, sep);
  const dbId = Number(raw.slice(sep + 1));
  if (!Number.isFinite(dbId) || dbId < 1) return;

  // 案件の編集履歴モーダルを流用する（source 付きで開くと取得先が切り替わる）
  uiState.projectEditHistoryModal = {
    source,
    dbId,
    name: el.dataset.changeHistoryName || "",
    loading: true,
    history: [],
    error: ""
  };
  render();
  void loadProjectEditHistory(dbId);
});
// ────────────────────────────────────────────────────────────────
// テンプレ設定（サイドバーのユーザーメニュー「テンプレ設定」から開く）
//
// 書類送付メールのテンプレートを一人ひとりが自分で管理する画面。ここに登録したものが、
// メール作成画面（15h-doc-mail-compose.js）のテンプレート選択にそのまま出る。
// 初回は共有テンプレ（api/_mailTemplates.js の既定7種）がコピーされて配られる。
//
// 使い方ガイド（15f）と同じく、render()/uiState の描画サイクルには乗らず自前のDOMで完結させる。
// イベントは 16-render-routing.js の巨大ハンドラに足さず、このファイルの独立リスナーで処理する。
// ────────────────────────────────────────────────────────────────

/** 画面の状態。overlay を開いている間だけ持つ。 */
let mailTplSettings = null;
let mailTplOverlayEl = null;
let mailTplWantOpen = false;

function mailTplBlankDraft() {
  return { id: null, name: "", docKey: "", subject: "", body: "" };
}

function mailTplDraftFrom(item) {
  return item
    ? { id: item.id, name: item.name || "", docKey: item.doc_key || "", subject: item.subject || "", body: item.body || "" }
    : mailTplBlankDraft();
}

/** 編集中の内容が保存済みの中身と違うか（切り替え・閉じるときの確認に使う） */
function mailTplDraftDirty() {
  const st = mailTplSettings;
  if (!st || !st.draft) return false;
  const src = mailTplDraftFrom((st.items || []).find((t) => t.id === st.draft.id));
  return ["name", "docKey", "subject", "body"].some((k) => String(st.draft[k] || "") !== String(src[k] || ""));
}

function mailTplConfirmDiscard() {
  return !mailTplDraftDirty() || window.confirm("編集中の内容は保存されていません。破棄してよろしいですか？");
}

/** 対象書類の選択肢。共有テンプレの名前（見積書送付・請求書送付…）をそのまま使う。 */
function mailTplDocOptions() {
  return (mailTplSettings?.shared || []).map((t) => ({ key: t.template_key, label: t.name || t.template_key }));
}

function mailTplDocLabel(docKey) {
  const hit = mailTplDocOptions().find((o) => o.key === docKey);
  return hit ? hit.label : "";
}

async function loadMailTplSettings(selectId) {
  const st = mailTplSettings;
  if (!st) return;
  st.loading = true;
  renderMailTplOverlay();
  const { ok, data } = await apiFetch("/api/mail-templates");
  st.loading = false;
  st.items = ok && data.ok ? data.personalTemplates || [] : [];
  st.shared = ok && data.ok ? data.templates || [] : [];
  if (!ok || !data.ok) showToast("テンプレートを読み込めませんでした");
  const keep = selectId != null ? Number(selectId) : st.selectedId;
  const found = st.items.find((t) => t.id === keep) || st.items[0] || null;
  st.selectedId = found ? found.id : null;
  st.draft = mailTplDraftFrom(found);
  renderMailTplOverlay();
}

function mailTplEditorHtml(st) {
  const d = st.draft || mailTplBlankDraft();
  const tags = (typeof DOC_MAIL_MERGE_TAGS !== "undefined" ? DOC_MAIL_MERGE_TAGS : [])
    .map((t) => `<code>##${escapeHtml(t.tag)}##</code>${escapeHtml(t.label)}`)
    .join("　");
  return `
    <div class="fp-mts-form">
      <label class="fp-mts-field">
        <span>テンプレート名 <em>必須</em></span>
        <input type="text" class="fp-mts-input" data-mail-tpl-field="name" value="${escapeHtmlAttr(d.name)}" placeholder="例：請求書送付（丁寧版）" />
      </label>
      <label class="fp-mts-field">
        <span>対象書類</span>
        <select class="fp-mts-input" data-search-dropdown-off="1" data-mail-tpl-field="docKey">
          <option value="">指定しない</option>
          ${mailTplDocOptions().map((o) => `<option value="${escapeHtmlAttr(o.key)}"${d.docKey === o.key ? " selected" : ""}>${escapeHtml(o.label)}</option>`).join("")}
        </select>
        <small>メール作成画面で、その書類を送るときに最初から選ばれます。</small>
      </label>
      <label class="fp-mts-field">
        <span>件名 <em>必須</em></span>
        <input type="text" class="fp-mts-input" data-mail-tpl-field="subject" value="${escapeHtmlAttr(d.subject)}" />
      </label>
      <label class="fp-mts-field fp-mts-field--grow">
        <span>本文 <em>必須</em></span>
        <textarea class="fp-mts-input fp-mts-body" data-mail-tpl-field="body" rows="10">${escapeHtml(d.body)}</textarea>
      </label>
      <p class="fp-mts-tags">差込タグ：${tags}</p>
      <div class="fp-mts-actions">
        ${d.id ? `<button type="button" class="fp-mts-del" data-mail-tpl-delete="1">削除</button>` : ""}
        <button type="button" class="fp-mts-save"${st.saving ? " disabled" : ""} data-mail-tpl-save="1">${st.saving ? "保存中…" : "保存"}</button>
      </div>
    </div>`;
}

function renderMailTplOverlay() {
  const st = mailTplSettings;
  if (!mailTplOverlayEl || !st) return;
  const list = (st.items || []).map((t) => {
    // 初回コピー直後はテンプレ名＝対象書類名なので、同じ文字を2行に出さない
    const label = mailTplDocLabel(t.doc_key);
    const sub = label && label !== t.name ? label : "";
    return `<button type="button" class="fp-mts-item${t.id === st.selectedId ? " is-active" : ""}" data-mail-tpl-pick="${t.id}">
      <span class="fp-mts-item-name">${escapeHtml(t.name || "（無題）")}</span>
      ${sub ? `<span class="fp-mts-item-doc">${escapeHtml(sub)}</span>` : ""}
    </button>`;
  }).join("");
  mailTplOverlayEl.innerHTML = `
    <div class="fp-mts-backdrop" data-mail-tpl-close="1"></div>
    <div class="fp-mts-panel">
      <div class="fp-mts-head">
        <div>
          <p class="fp-mts-eyebrow">Mail template</p>
          <h2>テンプレ設定</h2>
        </div>
        <button type="button" class="fp-mts-close" data-mail-tpl-close="1" aria-label="閉じる">×</button>
      </div>
      <p class="fp-mts-lead">書類をメールで送るときのテンプレートです。ここに登録したものが、メール作成画面のテンプレート選択に出ます。</p>
      <div class="fp-mts-body-wrap">
        <nav class="fp-mts-list" aria-label="テンプレート一覧">
          ${st.loading ? `<p class="fp-mts-empty">読み込み中…</p>` : list || `<p class="fp-mts-empty">まだテンプレートがありません。</p>`}
          <button type="button" class="fp-mts-new" data-mail-tpl-new="1">＋ 新しいテンプレート</button>
        </nav>
        <div class="fp-mts-editor">
          ${st.loading ? "" : mailTplEditorHtml(st)}
        </div>
      </div>
    </div>`;
}

function openMailTplSettings() {
  mailTplWantOpen = true;
  if (!mailTplOverlayEl) {
    mailTplOverlayEl = document.createElement("div");
    mailTplOverlayEl.className = "fp-mts-overlay";
    mailTplOverlayEl.setAttribute("role", "dialog");
    mailTplOverlayEl.setAttribute("aria-modal", "true");
    mailTplOverlayEl.setAttribute("aria-label", "テンプレ設定");
    mailTplOverlayEl.hidden = true;
    document.body.appendChild(mailTplOverlayEl);
  }
  mailTplSettings = { loading: true, saving: false, items: [], shared: [], selectedId: null, draft: mailTplBlankDraft() };
  renderMailTplOverlay();
  mailTplOverlayEl.hidden = false;
  requestAnimationFrame(() => mailTplOverlayEl && mailTplOverlayEl.classList.add("is-open"));
  document.body.classList.add("fp-mts-lock-scroll");
  void loadMailTplSettings();
}

function closeMailTplSettings() {
  if (!mailTplOverlayEl) return;
  mailTplWantOpen = false;
  mailTplOverlayEl.classList.remove("is-open");
  document.body.classList.remove("fp-mts-lock-scroll");
  window.setTimeout(() => {
    if (mailTplOverlayEl && !mailTplWantOpen) mailTplOverlayEl.hidden = true;
  }, 160);
}

async function saveMailTplDraft() {
  const st = mailTplSettings;
  if (!st || st.saving) return;
  const d = st.draft || mailTplBlankDraft();
  if (!String(d.name).trim()) { showToast("テンプレート名を入力してください"); return; }
  if (!String(d.subject).trim() || !String(d.body).trim()) { showToast("件名と本文は必須です"); return; }
  st.saving = true;
  renderMailTplOverlay();
  const { ok, data } = await apiFetch("/api/mail-templates", {
    method: "POST",
    body: { id: d.id || undefined, name: d.name, subject: d.subject, body: d.body, docKey: d.docKey },
  });
  st.saving = false;
  if (!ok || !data.ok) {
    renderMailTplOverlay();
    showToastError(`保存できませんでした：${data.error || "エラー"}`);
    return;
  }
  invalidateDocMailTemplateCache();
  await loadMailTplSettings(data.id || d.id);
  showToast("テンプレートを保存しました");
}

async function deleteMailTplDraft() {
  const st = mailTplSettings;
  if (!st || !st.draft?.id) return;
  if (!window.confirm(`「${st.draft.name || "無題"}」を削除します。よろしいですか？`)) return;
  const { ok, data } = await apiFetch(`/api/mail-templates?id=${encodeURIComponent(st.draft.id)}`, { method: "DELETE" });
  if (!ok || !data.ok) {
    showToastError(`削除できませんでした：${data.error || "エラー"}`);
    return;
  }
  invalidateDocMailTemplateCache();
  st.selectedId = null;
  await loadMailTplSettings();
  showToast("テンプレートを削除しました");
}

/** メール作成画面が持っているテンプレのキャッシュを捨てる。
 *  裏でメール作成画面を開いたまま編集されることがあるので、その場で読み直させる。 */
function invalidateDocMailTemplateCache() {
  if (typeof uiState === "undefined") return;
  uiState.docMailTemplates = null;
  if (uiState.docMailCompose && typeof loadDocMailTemplates === "function") void loadDocMailTemplates();
}

// ── イベント ──

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest("[data-mail-tpl-settings-trigger]");
  if (trigger) {
    e.preventDefault();
    if (typeof closeSidebarFlyouts === "function") closeSidebarFlyouts();
    openMailTplSettings();
    return;
  }

  if (!mailTplOverlayEl || mailTplOverlayEl.hidden) return;
  const st = mailTplSettings;
  if (!st) return;

  const pick = target.closest("[data-mail-tpl-pick]");
  if (pick instanceof HTMLElement) {
    const id = Number(pick.getAttribute("data-mail-tpl-pick"));
    if (id === st.selectedId || !mailTplConfirmDiscard()) return;
    st.selectedId = id;
    st.draft = mailTplDraftFrom((st.items || []).find((t) => t.id === id));
    renderMailTplOverlay();
    return;
  }
  if (target.closest("[data-mail-tpl-new]")) {
    if (!mailTplConfirmDiscard()) return;
    st.selectedId = null;
    st.draft = mailTplBlankDraft();
    renderMailTplOverlay();
    mailTplOverlayEl.querySelector('[data-mail-tpl-field="name"]')?.focus();
    return;
  }
  if (target.closest("[data-mail-tpl-save]")) {
    void saveMailTplDraft();
    return;
  }
  if (target.closest("[data-mail-tpl-delete]")) {
    void deleteMailTplDraft();
    return;
  }
  if (target.closest("[data-mail-tpl-close]")) {
    if (mailTplConfirmDiscard()) closeMailTplSettings();
  }
});

// テキスト入力は draft に取り込むだけで再描画しない（描画するとカーソルが飛び、IME変換も壊れる）
document.addEventListener("input", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const field = target.getAttribute("data-mail-tpl-field");
  if (!field || !mailTplSettings?.draft) return;
  mailTplSettings.draft[field] = target.value;
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.getAttribute("data-mail-tpl-field") !== "docKey" || !mailTplSettings?.draft) return;
  mailTplSettings.draft.docKey = target.value;
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && mailTplOverlayEl && !mailTplOverlayEl.hidden && mailTplConfirmDiscard()) closeMailTplSettings();
});
// ── 原価登録: 請求書の受領登録（経理チェック。一覧でチェックして一括バーの「支払ステータスを変更」から行う） ──
// 月初の経理フロー: 届いた請求書と原価を突合し、問題なければ 請求書No・計上月・会計区分 を入れて
// 状況を「受領済」にする。行はピンク（未受領）→黄（受領済）→白（支払済）で判別する。
// DBの支払ステータスは従来の5段階のまま（「受領済」= DB値「支払待ち」）。仮原価は受領登録で実原価化する。

/** 受領登録の操作可否（描画側のボタン出し分けと click ハンドラの再チェックで共用。16の一括操作ガードと同基準） */
function userCanRegisterCostReceipt() {
  return isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser);
}

/** 受領登録の更新ペイロード（単発=15j / 一括=15j / 照合=15g の3経路で共用。片方だけ仕様変更されるのを防ぐ）。
 *  vendor/amount/consumptionTax は persistCostToServerUpdate がフォールバックしない必須キーのため現値を渡す。
 *  支払方法が未設定ならパートナーマスタの支払方法から補完（設定済みは触らない） */
function costReceiptUpdatePatch(cost, { invoiceNo, targetMonth, category, dueDate }) {
  const autoMethod = String(cost.paymentMethod || "").trim() ? "" : costAutoPaymentMethodFromVendor(cost.vendor);
  return {
    category: category || cost.category,
    vendor: cost.vendor,
    amount: Number(cost.amount || 0),
    consumptionTax: costConsumptionTaxYen(cost),
    targetMonth,
    payStatus: "支払待ち",
    dueDate,
    invoiceNo,
    isProvisional: false,
    ...(autoMethod ? { paymentMethod: autoMethod } : {})
  };
}

/** 未受領行を1件ずつ受領登録（支払ステータス一括変更の「受領済」で使用）。請求書Noは既存値のまま（1件だけの時は
 *  モーダルで入力した invoiceNo を使う）・支払期限は空なら支払条件から自動計算 */
async function runCostReceiptUpdates(targets, { invoiceNo = null } = {}) {
  let ok = 0;
  let fail = 0;
  let lastErr = "";
  for (const cost of targets) {
    showToastProgress(`受領済に変更中… ${ok + fail + 1} / ${targets.length}件`);
    try {
      await persistCostToServerUpdate(
        cost.id,
        costReceiptUpdatePatch(cost, {
          invoiceNo: invoiceNo ?? (cost.invoiceNo || ""),
          targetMonth: cost.targetMonth,
          category: cost.category,
          dueDate: cost.dueDate || costAutoDueDateFromVendorTerms(cost.vendor, cost.invoiceDate) || ""
        }),
        "keep"
      );
      ok++;
    } catch (e) {
      fail++;
      lastErr = e instanceof Error && e.message ? e.message : "";
    }
  }
  return { ok, fail, lastErr };
}

/** 支払ステータス一括変更の選択肢（キー = costReceiptGroup の値）。DB値は 未受領=請求書待ち / 受領済=支払待ち / 支払済=支払済み */
const COST_BULK_PAY_STATUS_TARGETS = { pending: "未受領", received: "受領済", paid: "支払済" };

/** 原価登録一覧で選択中の原価（DB未保存の行は除く） */
function costMgmtSelectedCostRows() {
  pruneCostMgmtSelectionToValid();
  const sel = uiState.costMgmtSelected || {};
  return Object.keys(sel)
    .filter((k) => sel[k])
    .map((k) => findCostPlacementByUiId(k.slice(k.lastIndexOf(":") + 1))?.cost || null)
    .filter((c) => c && costDbNumericId(c) != null);
}

/** /api/cost-payments で支払日・支払ステータスを一括更新（表示中の支払日で楽観ロック） */
async function postCostPaymentsBulk(costs, { paidDate, payStatus }) {
  const costIds = costs.map((c) => costDbNumericId(c));
  const expectedPaidDates = {};
  costs.forEach((c, i) => {
    expectedPaidDates[costIds[i]] = c.paidDate ? String(c.paidDate).slice(0, 10) : null;
  });
  const { ok, data } = await apiFetch("/api/cost-payments", {
    method: "POST",
    body: { costIds, paidDate, payStatus, expectedPaidDates }
  });
  if (!ok) throw new Error(typeof data.error === "string" ? data.error : "支払ステータスの変更に失敗しました");
  return { updated: data.updated ?? costIds.length, conflicts: Array.isArray(data.conflicts) ? data.conflicts.length : 0 };
}

/** 支払ステータス一括変更（未受領/受領済/支払済）。すでに変更後と同じ状態の行はスキップ。
 *  受領済: 未受領の行は受領登録と同じ経路（仮原価の実原価化・支払期限の自動計算を1か所に保つ）、支払済の行は支払日クリア。
 *  未受領: 支払日をクリアして「請求書待ち」へ。支払済: 支払日を登録（既存の「支払日を登録」と同じAPI）。
 *  targets を渡すとその原価だけ（一覧の「支払」セルからの1件変更・15v）。省略時は一覧で選択中の行 */
let _costBulkPayStatusRunning = false;
async function submitCostBulkPayStatus(pick, paidDate, invoiceNo = null, targets = null) {
  if (_costBulkPayStatusRunning || !COST_BULK_PAY_STATUS_TARGETS[pick]) return;
  const label = COST_BULK_PAY_STATUS_TARGETS[pick];
  // 支払済は「支払日の訂正」も兼ねる: 支払済の行も、指定日と違えば支払日を上書きする（廃止した一括「支払日登録」の代わり）
  const rows = (targets || costMgmtSelectedCostRows()).filter(
    (c) => costReceiptGroup(c) !== pick || (pick === "paid" && String(c.paidDate || "").slice(0, 10) !== String(paidDate || ""))
  );
  if (!rows.length) {
    showToast(`「${label}」にする行がありません`);
    return;
  }
  if (pick === "paid" && !paidDate) {
    showToast("支払日を選んでください");
    return;
  }
  const receiptRows = pick === "received" ? rows.filter((c) => costReceiptGroup(c) === "pending") : [];
  const paymentRows = rows.filter((c) => !receiptRows.includes(c));
  _costBulkPayStatusRunning = true;
  if (!targets) uiState.costMgmtBulkModal = null; // セルからの1件変更では一括操作の入力中モーダルを閉じない
  render();
  let updated = 0;
  let conflicts = 0;
  let fail = 0;
  let lastErr = "";
  try {
    if (paymentRows.length) {
      try {
        const r = await postCostPaymentsBulk(paymentRows, {
          paidDate: pick === "paid" ? paidDate : null,
          payStatus: pick === "pending" ? "請求書待ち" : "支払待ち"
        });
        updated += r.updated;
        conflicts += r.conflicts;
      } catch (e) {
        fail += paymentRows.length;
        lastErr = e instanceof Error && e.message ? e.message : "";
      }
    }
    if (receiptRows.length) {
      // 請求書Noの入力欄は1件だけ受領済にする時しか出さない（複数件に同じNoを入れない）
      const r = await runCostReceiptUpdates(receiptRows, { invoiceNo: receiptRows.length === 1 ? invoiceNo : null });
      updated += r.ok;
      fail += r.fail;
      lastErr = r.lastErr || lastErr;
    }
    await loadDataFromDB({ mode: "heavy", skipLoadingUi: true });
  } finally {
    _costBulkPayStatusRunning = false;
  }
  if (pick === "paid" && !targets) uiState.costMgmtBulkPayDate = "";
  if (!fail && !conflicts) {
    if (!targets) uiState.costMgmtSelected = {};
    showToast(`「${label}」にしました（${updated}件）`);
  } else {
    const parts = [`「${label}」に変更: 成功 ${updated}件`];
    if (conflicts) parts.push(`${conflicts}件は他の担当者が先に変更（または削除）していたため更新せず、最新の内容を表示しました`);
    if (fail) parts.push(`失敗 ${fail}件${lastErr ? `（${lastErr}）` : ""}${targets ? "" : "。選択を残したので、そのまま再実行できます"}`);
    showToast(parts.join("。"), { variant: "error" });
  }
  render();
}

/** 割合の表示（分母0は空欄。案件一覧CSVの粗利率と同じく「%」付き） */
function costCsvRatioCell(num, den) {
  return den ? `${Math.round((num / den) * 1000) / 10}%` : "";
}

/** 月（YYYY-MM / YYYY/MM）を yyyy/mm の文字列として出すCSVセル。素の 2026-09 や 2026/09 は Excel が日付に変えて
 *  「Sep-26」と表示するため ="2026/09" の形で書く（自前で組んだ値なので toCsvCell の数式対策は通さない） */
function costCsvMonthCell(ym) {
  const m = String(ym || "").match(/^(\d{4})[-/](\d{1,2})$/);
  return m ? `"=""${m[1]}/${m[2].padStart(2, "0")}"""` : toCsvCell(ym);
}

/** 原価登録一覧のCSVダウンロード（絞り込み結果全体＝全ページ分。画面の列＋金額内訳・支払方法・請求書No＋計上案件の情報を含む） */
function downloadCostMgmtCsv() {
  const { filtered } = getCostMgmtTableRows();
  // 案件の列・備考は既存列の後ろに足す（既存の列位置で取り込んでいる表を崩さない）
  const headers = ["No", "発注名", "発注先", "計上案件", "計上月", "会計区分", "金額（税抜）", "消費税", "税込", "源泉所得税", "請求日", "支払期限", "支払日", "支払方法", "支払", "発注", "請求書No",
    "案件No", "顧客", "案件担当", "案件の請求月", "案件の売上（税抜）", "案件の原価合計（税抜）", "案件原価に占める割合", "案件売上に対する割合", "備考"];
  const projectById = new Map(projects.map((p) => [String(p.id), p]));
  const projectCols = new Map(); // 同じ案件の行が続くので案件ごとに1回だけ計算する
  const projectColsOf = (pid) => {
    if (!projectCols.has(pid)) {
      const p = projectById.get(pid);
      projectCols.set(pid, p ? {
        no: projectDisplayNoForUi(p), client: clientName(p), owner: projectListOwnerDisplayName(p), billingMonth: p.billingMonth || "",
        // 案件一覧CSVの「請求額（税抜）」「原価（税抜）」と同じ基準
        sales: projectDisplayedBillingExTaxYen(p), costTotal: totalProjectRegisteredCostExTaxYen(p)
      } : null);
    }
    return projectCols.get(pid);
  };
  const body = filtered.map((c) => {
    const ex = Number(c.amount || 0);
    const tax = costConsumptionTaxYen(c);
    const pj = projectColsOf(String(c._projectId || ""));
    const month = (ym) => ({ csvCell: costCsvMonthCell(ym) }); // 月の列だけ toCsvCell を通さない
    return [
      costDbNumericId(c) ?? "",
      stripCostBucketMarkers(String(c.note || "")),
      c.vendor || "",
      c._projectName || "",
      month(c.targetMonth),
      c.category || "",
      ex,
      tax,
      ex + tax,
      Math.round(Number(c.withholdingTax || 0)) || 0,
      String(c.invoiceDate || "").slice(0, 10),
      String(c.dueDate || "").slice(0, 10),
      String(c.paidDate || "").slice(0, 10),
      c.paymentMethod || "",
      costPayStatusDisplayLabel(c),
      costOrderStatusDisplayLabel(c),
      c.invoiceNo || "",
      ...(pj
        ? [pj.no, pj.client, pj.owner, month(pj.billingMonth), pj.sales, pj.costTotal, costCsvRatioCell(ex, pj.costTotal), costCsvRatioCell(ex, pj.sales)]
        : ["", "", "", "", "", "", "", ""]),
      c.memo || ""
    ]
      .map((v) => (v && v.csvCell !== undefined ? v.csvCell : toCsvCell(v)))
      .join(",");
  });
  const csv = "﻿" + [headers.map((h) => toCsvCell(h)).join(","), ...body].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `原価一覧_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  showToast(filtered.length ? `${filtered.length}件をCSVでダウンロードしました` : "該当0件のCSVをダウンロードしました（見出しのみ）");
}

// ── イベント（独立リスナー・原価登録ページのみ） ──
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const hit = t.closest("[data-cost-mgmt-bulk-paystatus-apply],[data-cost-mgmt-csv],[data-cost-mgmt-detail-toggle],[data-cost-mgmt-pay-chip]");
  if (!(hit instanceof HTMLElement)) return;
  if (currentRoute().page !== "cost-management") return;

  if (hit.hasAttribute("data-cost-mgmt-bulk-paystatus-apply")) {
    if (!userCanRegisterCostReceipt()) return;
    const dateEl = hit.closest(".cost-mgmt-bulk-modal")?.querySelector("input[data-cost-mgmt-bulk-paydate-pick]");
    const paidDate = dateEl instanceof HTMLInputElement ? String(dateEl.value || "").trim() : "";
    // 空欄は「入力なし」＝既存の請求書Noを残す（空で上書きして消さない）
    const invoiceNo = String(uiState.costMgmtBulkInvoiceNo ?? "").trim() || null;
    void submitCostBulkPayStatus(uiState.costMgmtBulkPayStatusPick, paidDate, invoiceNo);
    return;
  }

  if (hit.hasAttribute("data-cost-mgmt-csv")) {
    downloadCostMgmtCsv();
    return;
  }

  if (hit.hasAttribute("data-cost-mgmt-detail-toggle")) {
    syncCostMgmtFilterFromDom(); // 入力途中のキーワード等を畳む前に取り込む
    uiState.costMgmtFilterDetailOpen = !uiState.costMgmtFilterDetailOpen;
    render();
    return;
  }

  if (hit.hasAttribute("data-cost-mgmt-pay-chip")) {
    syncCostMgmtFilterFromDom();
    uiState.costMgmtFilter = { ...uiState.costMgmtFilter, payStatus: String(hit.getAttribute("data-cost-mgmt-pay-chip") || "") };
    if (uiState.costMgmtTable) uiState.costMgmtTable.page = 1;
    render();
  }
});

// 行チェックの Shift+クリックで範囲選択（直前にクリックした行〜今回の行を同じ状態に）。
// click はチェック状態が切り替わった後・change より前に来るので、ここでは範囲だけ state に入れ、
// クリックした行自身の反映と再描画は既存の change ハンドラ（16-render-routing）に任せる
let _costMgmtLastCheckedKey = "";
document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || t.dataset.costMgmtRowCheck == null) return;
  if (currentRoute().page !== "cost-management") return;
  const key = String(t.dataset.costMgmtRowCheck || "");
  if (e.shiftKey && _costMgmtLastCheckedKey && _costMgmtLastCheckedKey !== key) {
    const keys = [...document.querySelectorAll("input[data-cost-mgmt-row-check]")].map((el) => String(el.getAttribute("data-cost-mgmt-row-check") || ""));
    const a = keys.indexOf(_costMgmtLastCheckedKey);
    const b = keys.indexOf(key);
    if (a >= 0 && b >= 0) {
      if (!uiState.costMgmtSelected || typeof uiState.costMgmtSelected !== "object") uiState.costMgmtSelected = {};
      for (const k of keys.slice(Math.min(a, b), Math.max(a, b) + 1)) {
        if (t.checked) uiState.costMgmtSelected[k] = true;
        else delete uiState.costMgmtSelected[k];
      }
    }
  }
  _costMgmtLastCheckedKey = key;
});

// 会計区分の絞り込み（プルダウン）。選択は絞り込みが変わるので解除して1ページ目へ
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLSelectElement) || !t.hasAttribute("data-cost-mgmt-filter-category")) return;
  if (currentRoute().page !== "cost-management") return;
  syncCostMgmtFilterFromDom();
  uiState.costMgmtTabKeys = normalizeCostMgmtTabKeys([t.value]);
  uiState.costMgmtSelected = {};
  uiState.costMgmtBulkModal = null;
  if (uiState.costMgmtTable) uiState.costMgmtTable.page = 1;
  render();
});

// 1件受領の請求書No: 通知の再描画などで入力途中の値が消えないよう、打つたびに state に保持する
document.addEventListener("input", (e) => {
  const t = e.target;
  if (uiState.costMgmtBulkModal !== "payStatus" || !(t instanceof HTMLInputElement) || !t.hasAttribute("data-cost-mgmt-bulk-invoice-no")) return;
  uiState.costMgmtBulkInvoiceNo = t.value;
});

// 支払ステータス一括変更: 変更後ステータスの選択で再描画（支払日欄・対象件数を切替）。支払日は再描画で消えないよう state に保持
document.addEventListener("change", (e) => {
  if (uiState.costMgmtBulkModal !== "payStatus") return;
  const t = e.target;
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-cost-mgmt-bulk-paystatus-pick")) {
    uiState.costMgmtBulkPayStatusPick = t.value;
    render();
  } else if (t instanceof HTMLInputElement && t.hasAttribute("data-cost-mgmt-bulk-paydate-pick")) {
    uiState.costMgmtBulkPayDate = t.value;
  }
});
// ────────────────────────────────────────────────────────────────
// 案件（＝請求書）詳細の「コメント」タブ。営業と経理のやりとりを残す場所。
//
// 既存の社内メモ（基本情報タブの1枚のテキスト欄）は、誰がいつ書いたかが残らず上書きされるため、
// やりとりが追えなかった。こちらは ToDo のやりとりと同じ吹き出し形式で、投稿者・時刻が残り、
// 本人だけが編集・削除できる。編集履歴は持たず「編集済み」の表示のみ（ToDo と同じ挙動）。
//
// 表示は ToDo のやりとりの CSS（pm-msg 系）と本文整形・アバター・時刻の各ヘルパーを流用する
// ＝見た目が揃い、新規CSSもほぼ増えない。データは /api/project-comments。
// ────────────────────────────────────────────────────────────────

/** 案件ID（フロントの "p123"）→ サーバのDB id */
function projectCommentsDbId(p) {
  const n = Number(p?.dbId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** コメントの状態。案件ごとにキャッシュし、タブを開くたびに取り直さない */
function ensureProjectCommentsState(pid) {
  if (!uiState.projectComments || uiState.projectComments.projectId !== pid) {
    uiState.projectComments = {
      projectId: pid,
      loading: false,
      loaded: false,
      error: "",
      items: [],
      draft: "",
      editingId: null,
      editDraft: "",
      submitting: false
    };
  }
  return uiState.projectComments;
}

async function loadProjectComments(p) {
  const pid = p?.id;
  const dbId = projectCommentsDbId(p);
  const st = ensureProjectCommentsState(pid);
  if (!dbId) {
    st.loading = false;
    st.loaded = true;
    st.error = "保存後にコメントできます";
    return;
  }
  if (st.loading) return;
  st.loading = true;
  st.error = "";
  const res = await apiFetch(`/api/project-comments?projectId=${encodeURIComponent(dbId)}`);
  const cur = uiState.projectComments;
  if (!cur || cur.projectId !== pid) return; // 別案件へ移った後の応答は捨てる
  cur.loading = false;
  cur.loaded = true;
  if (res.ok && res.data?.ok && Array.isArray(res.data.comments)) {
    cur.items = res.data.comments;
  } else {
    cur.error = res.data?.error || "コメントの取得に失敗しました";
  }
  render();
}

/** コメント1件（ToDo のやりとりと同じ吹き出し） */
function projectCommentItemHtml(c, st) {
  // 投稿者判定・投稿者の解決は ToDo のやりとりと同じものを使う
  // （currentUser.dbId / authUser.id / users 突合の3経路を1箇所に持たせておく）
  const mine = progressMessageIsAuthor(c);
  const editing = st.editingId != null && String(st.editingId) === String(c.id);
  const authorU = progressMessageAuthorUser(c);
  const authorName = authorU ? userNameWithRetiredLabel(authorU) : String(c.userName || "—").trim();
  const avatarEl = progressUserAvatarHtml(authorU, "pm-assignee-avatar--msg", authorName);
  const timeStr = escapeHtml(formatProgressMsgTime(c.createdAt));
  const editedTag = c.edited ? `<span class="pm-msg-tag pm-msg-tag--edited">編集済み</span>` : "";

  const actions = mine && !editing
    ? `<div class="pm-msg-bactions">
         <button type="button" class="inline-btn ghost pm-msg-edit" data-pc-edit="${escapeHtmlAttr(String(c.id))}">編集</button>
         <button type="button" class="inline-btn ghost pm-msg-del" data-pc-delete="${escapeHtmlAttr(String(c.id))}">削除</button>
       </div>`
    : "";

  const fromRow = `<div class="pm-msg-bfrom">
    <span class="pm-msg-bname">${escapeHtml(authorName)}</span>
    <span class="pm-msg-time">${timeStr}${editedTag}</span>
  </div>`;

  const inner = editing
    ? `${fromRow}
       <textarea class="pj-input pm-textarea pm-msg-edit-textarea" data-pc-edit-body data-pm-mention-textarea="1" rows="5">${escapeHtml(st.editDraft)}</textarea>
       <div class="pm-msg-edit-actions">
         <button type="button" class="inline-btn ghost" data-pc-edit-cancel="1">キャンセル</button>
         <button type="button" class="inline-btn" data-pc-edit-save="${escapeHtmlAttr(String(c.id))}"${st.submitting ? " disabled" : ""}>保存</button>
       </div>`
    : `${fromRow}
       <div class="pm-msg-body">${progressMessageBodyDisplayHtml(c.body)}</div>`;

  return `<div class="pm-msg pm-msg--bubble${mine ? " pm-msg--mine" : ""}${editing ? " pm-msg--editing" : ""}">
    <div class="pm-msg-bav">${avatarEl}</div>
    <div class="pm-msg-bwrap">
      <div class="pm-msg-bbubble">${inner}</div>
      ${actions}
    </div>
  </div>`;
}

/** コメントタブ本体（案件詳細から呼ばれる） */
function projectCommentsTabHtml(p) {
  const st = ensureProjectCommentsState(p.id);
  const dbId = projectCommentsDbId(p);
  // タブを開いた最初の描画で取得を始める（URL 直開きでもクリックでも同じ経路になる）。
  // loadProjectComments は await の前に loading を立てるので二重取得にならない
  if (dbId && !st.loaded && !st.loading) void loadProjectComments(p);

  let listHtml;
  if (!dbId) {
    listHtml = `<p class="pc-empty">案件を保存するとコメントできるようになります。</p>`;
  } else if (st.loading && !st.loaded) {
    listHtml = `<p class="pc-empty">読み込み中…</p>`;
  } else if (st.error) {
    listHtml = `<p class="pc-empty pc-error">${escapeHtml(st.error)}</p>`;
  } else if (st.items.length === 0) {
    listHtml = `<p class="pc-empty">まだコメントはありません。請求内容の確認や依頼をここに残せます。</p>`;
  } else {
    listHtml = st.items.map((c) => projectCommentItemHtml(c, st)).join("");
  }

  const composer = dbId
    ? `<div class="pc-composer">
        <textarea class="pj-input pm-textarea" data-pc-draft data-pm-mention-textarea="1" rows="3" placeholder="コメントを入力（@で担当者にメンション）">${escapeHtml(st.draft)}</textarea>
        <div class="pc-composer-actions">
          <button type="button" class="inline-btn" data-pc-post="1"${st.submitting || !st.draft.trim() ? " disabled" : ""}>投稿</button>
        </div>
      </div>`
    : "";

  return `
    <section class="section">
      <div class="soft-card" style="padding:20px;">
        <div class="pc-list">${listHtml}</div>
        ${composer}
      </div>
    </section>
  `;
}

/** 現在のコメント状態と対象案件をまとめて取り出す（ハンドラ共通の前処理） */
function projectCommentsContext() {
  const r = currentRoute();
  if (r.page !== "project-detail" || !r.id) return null;
  const p = projects.find((x) => x.id === r.id);
  if (!p) return null;
  const st = ensureProjectCommentsState(p.id);
  const dbId = projectCommentsDbId(p);
  return dbId ? { p, st, dbId } : null;
}

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLTextAreaElement)) return;
  const ctx = projectCommentsContext();
  if (!ctx) return;
  if (t.matches("[data-pc-draft]")) {
    const wasEmpty = !ctx.st.draft.trim();
    ctx.st.draft = t.value;
    // 投稿ボタンの活性だけ更新する（毎入力の再描画で変換中の文字が消えるのを避ける）
    const btn = document.querySelector("[data-pc-post]");
    if (btn instanceof HTMLButtonElement) btn.disabled = ctx.st.submitting || !t.value.trim();
    void wasEmpty;
    return;
  }
  if (t.matches("[data-pc-edit-body]")) {
    ctx.st.editDraft = t.value;
  }
});

document.addEventListener("click", async (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const ctx = projectCommentsContext();
  if (!ctx) return;
  const { st, dbId } = ctx;

  const postEl = target.closest("[data-pc-post]");
  if (postEl instanceof HTMLElement) {
    const body = String(st.draft || "").trim();
    if (!body || st.submitting) return;
    st.submitting = true;
    const res = await apiFetch("/api/project-comments", { method: "POST", body: { projectId: dbId, body } });
    st.submitting = false;
    if (res.ok && res.data?.ok && res.data.comment) {
      st.items = [...st.items, res.data.comment];
      st.draft = "";
      render();
    } else {
      showToastError(res.data?.error || "コメントの投稿に失敗しました");
    }
    return;
  }

  const editEl = target.closest("[data-pc-edit]");
  if (editEl instanceof HTMLElement) {
    const id = editEl.dataset.pcEdit;
    const cur = st.items.find((c) => String(c.id) === String(id));
    st.editingId = id;
    st.editDraft = cur ? String(cur.body || "") : "";
    render();
    return;
  }

  const cancelEl = target.closest("[data-pc-edit-cancel]");
  if (cancelEl instanceof HTMLElement) {
    st.editingId = null;
    st.editDraft = "";
    render();
    return;
  }

  const saveEl = target.closest("[data-pc-edit-save]");
  if (saveEl instanceof HTMLElement) {
    const id = Number(saveEl.dataset.pcEditSave);
    const body = String(st.editDraft || "").trim();
    if (!body || st.submitting) return;
    st.submitting = true;
    const res = await apiFetch("/api/project-comments", { method: "PUT", body: { id, body } });
    st.submitting = false;
    if (res.ok && res.data?.ok && res.data.comment) {
      st.items = st.items.map((c) => (String(c.id) === String(id) ? res.data.comment : c));
      st.editingId = null;
      st.editDraft = "";
      render();
    } else {
      showToastError(res.data?.error || "コメントの更新に失敗しました");
    }
    return;
  }

  const delEl = target.closest("[data-pc-delete]");
  if (delEl instanceof HTMLElement) {
    const id = Number(delEl.dataset.pcDelete);
    if (!window.confirm("このコメントを削除しますか？")) return;
    const res = await apiFetch("/api/project-comments", { method: "DELETE", body: { id } });
    if (res.ok && res.data?.ok) {
      st.items = st.items.filter((c) => String(c.id) !== String(id));
      render();
    } else {
      showToastError(res.data?.error || "コメントの削除に失敗しました");
    }
  }
});
// ────────────────────────────────────────────────────────────────
// 取引承認の申請（契約申請）で入力する資本金・設立年月と、与信ルールの確認（2026-10-28 要望）。
//
// ルール: 資本金100万円以下、設立1年未満の取引先は原則NG（特に受注先は未入金リスクを考慮して慎重に判断）。
// 信用面で問題ないと判断できる場合は個別に検討する＝申請自体は止めず、該当すれば確認を出して進むか選ばせる。
// 値は fp_clients / fp_partners の capital_amount・established_ym に保存（api/client-approval-apply.js）。
// 取引先の登録・編集フォームでも入力できる（新規登録は必須。申請モーダルはその値を初期表示する）。
// ────────────────────────────────────────────────────────────────

const CLIENT_APPLY_CREDIT_RULE =
  "資本金100万円以下、設立1年未満の取引先は、原則NGとします。特に受注先については、未入金リスクを考慮し、慎重に判断します。" +
  "ただし、会社規模の拡大や業務実績が確認できるなど、信用面で問題がないと判断できる場合は、個別に検討します。";
const CLIENT_APPLY_CAPITAL_MIN = 1000000; // この金額「以下」が原則NG
const CLIENT_APPLY_ESTABLISHED_MIN_MONTHS = 12; // これ「未満」が原則NG

/** 設立年月の入力（"2020-04" / "2020/4" / "2020年4月"）→ "YYYY-MM"。読めなければ "" */
function clientApplyNormalizeYm(raw) {
  const m = String(raw ?? "").trim().replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .match(/^(\d{4})\s*[-/年.]\s*(\d{1,2})\s*月?$/);
  if (!m) return "";
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? `${m[1]}-${String(month).padStart(2, "0")}` : "";
}

/** 資本金の入力 → 0以上の整数。読めなければ null（取引予定金額と違い 0 円もありうる） */
function clientApplyCapitalParse(raw) {
  const s = String(raw ?? "").trim().replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  if (!/^[\d,，]+$/.test(s)) return null;
  const n = Number(s.replace(/[,，]/g, ""));
  return Number.isSafeInteger(n) ? n : null;
}

/** 今月の "YYYY-MM"（日本時間。設立年月の上限に使う） */
function clientApplyCurrentYm(now) {
  const jst = new Date((now || new Date()).getTime() + 9 * 3600 * 1000);
  return `${jst.getUTCFullYear()}-${String(jst.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** 設立からの経過月数（満）。未入力・未来の年月は null */
function clientApplyEstablishedMonths(ym, now) {
  const norm = clientApplyNormalizeYm(ym);
  if (!norm) return null;
  const [y, m] = norm.split("-").map(Number);
  const [cy, cm] = clientApplyCurrentYm(now).split("-").map(Number);
  const months = (cy - y) * 12 + (cm - m);
  return months >= 0 ? months : null;
}

/** 経過月数の表示（「設立から3年2か月」） */
function clientApplyEstablishedAgeText(ym) {
  if (!String(ym || "").trim()) return "";
  const months = clientApplyEstablishedMonths(ym);
  if (months == null) return "年月を正しく入力してください（未来の年月は不可）";
  const y = Math.floor(months / 12);
  const m = months % 12;
  return `設立から${y ? `${y}年` : ""}${m || !y ? `${m}か月` : ""}`;
}

/** 原則NGの基準に当たる項目（無ければ空配列） */
function clientApplyCreditReasons(capital, months) {
  const reasons = [];
  if (capital != null && capital <= CLIENT_APPLY_CAPITAL_MIN) reasons.push("資本金100万円以下");
  if (months != null && months < CLIENT_APPLY_ESTABLISHED_MIN_MONTHS) reasons.push("設立1年未満");
  return reasons;
}

/** 取引先詳細の基本情報・申請承認ページの詳細に出す行（{ label, value }。未入力は "—"） */
function clientApplyCreditInfoRows(capital, ym) {
  const norm = clientApplyNormalizeYm(ym);
  const [y, m] = norm ? norm.split("-").map(Number) : [];
  const age = norm ? clientApplyEstablishedAgeText(norm) : "";
  return [
    { label: "資本金", value: capital != null ? `¥${Number(capital).toLocaleString("ja-JP")}` : "—" },
    { label: "設立年月", value: norm ? `${y}年${m}月${age ? `（${age}）` : ""}` : "—" }
  ];
}

function clientApplyCreditWarnText(capitalRaw, establishedRaw) {
  const reasons = clientApplyCreditReasons(clientApplyCapitalParse(capitalRaw), clientApplyEstablishedMonths(establishedRaw));
  return reasons.length ? `原則NGの基準に該当します（${reasons.join("・")}）` : "";
}

/** 申請モーダルの資本金・設立年月の欄 */
function clientApplyCreditFieldsHtml(m) {
  const warn = clientApplyCreditWarnText(m.capital, m.established);
  return `
        <div class="fp-clapply-section fp-clapply-credit">
          <div class="fp-clapply-credit-col">
            <div class="fp-clapply-label">資本金（円・必須）</div>
            <input class="pj-input" data-clapply-capital type="text" inputmode="numeric" autocomplete="off" maxlength="15"
              value="${escapeHtmlAttr(m.capital || "")}" placeholder="例：3000000（数値のみ）" />
          </div>
          <div class="fp-clapply-credit-col">
            <div class="fp-clapply-label">設立年月（必須）</div>
            <div class="fp-clapply-est-row">
              <input class="pj-input" data-clapply-established type="month" max="${clientApplyCurrentYm()}" value="${escapeHtmlAttr(m.established || "")}" placeholder="例：2020-04" />
              <span class="fp-clapply-est-age" data-clapply-est-age>${escapeHtml(clientApplyEstablishedAgeText(m.established))}</span>
            </div>
          </div>
          <div class="fp-clapply-credit-warn" data-clapply-credit-warn${warn ? "" : " hidden"}>⚠ ${escapeHtml(warn)}</div>
        </div>`;
}

/** 入力中は再描画せず（IME・フォーカスを保つ）、経過年数と警告の表示だけ差し替える */
function clientApplyRefreshCreditHints() {
  const m = uiState.clientApplyModal;
  if (!m) return;
  const age = document.querySelector("[data-clapply-est-age]");
  if (age) age.textContent = clientApplyEstablishedAgeText(m.established);
  const warnEl = document.querySelector("[data-clapply-credit-warn]");
  if (warnEl instanceof HTMLElement) {
    const warn = clientApplyCreditWarnText(m.capital, m.established);
    warnEl.hidden = !warn;
    warnEl.textContent = warn ? `⚠ ${warn}` : "";
  }
}

/** 申請前のチェック。不備はトーストで止め（false）、原則NGの基準に当たれば確認を出して OK のときだけ進む。
 *  戻り値: 進んでよければ { capital, establishedYm }、やめるなら null */
async function clientApplyCreditCheck(m) {
  const capital = clientApplyCapitalParse(m.capital);
  if (capital == null) {
    showToast("資本金を数値で入力してください");
    return null;
  }
  const establishedYm = clientApplyNormalizeYm(m.established);
  const months = clientApplyEstablishedMonths(establishedYm);
  if (months == null) {
    showToast("設立年月を入力してください（未来の年月は不可）");
    return null;
  }
  const reasons = clientApplyCreditReasons(capital, months);
  if (reasons.length) {
    const ok = await fpConfirm({
      title: "それでも申請に進みますか？",
      message: `原則NGの基準に該当します（${reasons.join("・")}）`,
      note: CLIENT_APPLY_CREDIT_RULE,
      confirmLabel: "申請に進む",
      cancelLabel: "やめる"
    });
    if (!ok) return null;
  }
  return { capital, establishedYm };
}

/** 取引先の登録・編集フォームの資本金・設立年月の行（kind: "client" | "partner"。値は data-<kind>-field で state に入る） */
function clientFormCreditRowsHtml(kind, f, required) {
  const req = required ? ' <span class="client-required">（必須）</span>' : "";
  const warn = clientApplyCreditWarnText(f.capitalAmount, f.establishedYm);
  return `
          <label class="client-form-row"><span>資本金${req}</span><input data-${kind}-field="capitalAmount" data-credit-capital value="${escapeHtmlAttr(f.capitalAmount || "")}" inputmode="numeric" maxlength="15" placeholder="円（例：3000000）" /></label>
          <div class="client-form-row"><span>設立年月${req}</span><div class="fp-clapply-est-row"><input type="month" data-${kind}-field="establishedYm" data-credit-established max="${clientApplyCurrentYm()}" value="${escapeHtmlAttr(f.establishedYm || "")}" /><span class="fp-clapply-est-age" data-credit-age>${escapeHtml(clientApplyEstablishedAgeText(f.establishedYm))}</span></div></div>
          <div class="fp-clapply-credit-warn" data-credit-warn${warn ? "" : " hidden"}>⚠ ${escapeHtml(warn)}</div>`;
}

/** 登録・編集フォームの資本金・設立年月を送る値にする。不備は { error, selector }（required は新規登録のとき） */
function clientFormCreditValues(kind, f, required) {
  const capRaw = String(f.capitalAmount ?? "").trim();
  const ymRaw = String(f.establishedYm ?? "").trim();
  const sel = (key) => `[data-${kind}-field="${key}"]`;
  const capitalAmount = clientApplyCapitalParse(capRaw);
  if (capRaw ? capitalAmount == null : required) {
    return { error: capRaw ? "資本金は数値で入力してください" : "資本金を入力してください", selector: sel("capitalAmount") };
  }
  const establishedYm = clientApplyNormalizeYm(ymRaw);
  if (ymRaw ? clientApplyEstablishedMonths(establishedYm) == null : required) {
    return { error: ymRaw ? "設立年月を正しく入力してください（未来の年月は不可）" : "設立年月を入力してください", selector: sel("establishedYm") };
  }
  return { capitalAmount, establishedYm };
}

document.addEventListener("input", (e) => {
  const t = e.target;
  // 登録・編集フォーム: 経過年数と警告の表示だけ差し替える（値は既存の data-*-field の処理が state に入れる）
  if (t instanceof HTMLInputElement && t.matches("[data-credit-capital],[data-credit-established]")) {
    const box = t.closest(".client-form-section");
    const cap = box?.querySelector("[data-credit-capital]");
    const est = box?.querySelector("[data-credit-established]");
    if (!(cap instanceof HTMLInputElement) || !(est instanceof HTMLInputElement)) return;
    const age = box.querySelector("[data-credit-age]");
    if (age) age.textContent = clientApplyEstablishedAgeText(est.value);
    const warnEl = box.querySelector("[data-credit-warn]");
    if (warnEl instanceof HTMLElement) {
      const warn = clientApplyCreditWarnText(cap.value, est.value);
      warnEl.hidden = !warn;
      warnEl.textContent = warn ? `⚠ ${warn}` : "";
    }
    return;
  }
  if (!(t instanceof HTMLInputElement) || !uiState.clientApplyModal) return;
  if (t.matches("[data-clapply-capital]")) {
    // 取引予定金額と同じく数値以外は入れさせない（IME確定後に正規化）
    if (e.isComposing) return;
    const digits = t.value.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)).replace(/[^\d]/g, "");
    if (t.value !== digits) t.value = digits;
    uiState.clientApplyModal.capital = digits;
    clientApplyRefreshCreditHints();
    return;
  }
  if (t.matches("[data-clapply-established]")) {
    uiState.clientApplyModal.established = t.value;
    clientApplyRefreshCreditHints();
  }
});
// ────────────────────────────────────────────────────────────────
// 取引先詳細の「担当者・支社」タブ（2026-10-28 要望: 先方の担当者と支社を各クライアント・パートナーに持たせる）。
//
// クライアントの担当者・支社は取引先一覧の「担当者」「支社」タブ（15e / 15g）と同じデータで、
// ここではその取引先の分だけを出す。パートナーの分は同じ API の partnerContacts / partnerBranches。
// 登録・編集は 15e / 15g のモーダルとハンドラをそのまま使う（ボタンに所属先の data 属性を付けるだけ）。
// ────────────────────────────────────────────────────────────────

/** 担当者・支社モーダルの所属先欄（パートナーは付け替えないので固定表示） */
function clientSubRecordPartnerFieldHtml(partnerId) {
  return `<div class="cl-form-group"><span class="cl-form-label">パートナー</span><div class="fp-cldetail-people-owner">${escapeHtml(clientFlowFindPartner(partnerId)?.name || "—")}</div></div>`;
}

/** この取引先の担当者・支社（v = clientFlowEntityView の戻り値） */
function clientDetailPeopleRows(v) {
  const owner = v.entityType === "partner" ? "partner" : "client";
  const mine = (r) => (owner === "partner" ? Number(r.partnerId) : Number(r.clientDbId)) === Number(v.dbId);
  return {
    owner,
    // ボタンの所属先 ID。クライアントは "c123" 形式（モーダルの顧客選択の値と同じ）
    ownerId: owner === "partner" ? String(v.dbId) : clientFlowFindClient(v.dbId)?.id || "",
    contacts: clientContactRowsOf(owner).filter(mine),
    branches: clientBranchRowsOf(owner).filter(mine)
  };
}

/** タブの件数。どちらかが読み込み中なら "…" */
function clientDetailPeopleCount(v) {
  ensureClientContactsLoaded();
  ensureClientBranchesLoaded();
  if (uiState.clientContacts.rows === null || uiState.clientBranches.rows === null) return "…";
  const { contacts, branches } = clientDetailPeopleRows(v);
  return String(contacts.length + branches.length);
}

function clientDetailPeopleArchivedHtml(r) {
  return r.archived ? `<span class="fp-cldetail-people-archived">アーカイブ</span>` : "";
}

function clientDetailPeopleHtml(v) {
  const { owner, ownerId, contacts, branches } = clientDetailPeopleRows(v);
  const loading = (st) => (st.loading || st.rows === null ? `<p class="fp-cldetail-people-empty">読み込み中…</p>` : "");
  const ownerAttrs = (kind) => `data-client-${kind}-owner="${owner}" data-client-${kind}-owner-id="${escapeHtmlAttr(ownerId)}"`;
  const rowActions = (kind, r) => `
    <button type="button" class="cl-client-card-edit" data-client-${kind}-open="${r.id}" ${ownerAttrs(kind)}>編集</button>
    <button type="button" class="cl-client-card-edit" data-client-${kind}-delete="${r.id}" ${ownerAttrs(kind)}>削除</button>`;

  // 1行＝名前の下に「項目名：値」を並べる2段組み（列の表だと値が横に離れ、どの列が何か分からなかった 2026-10-28 指摘）。
  // 未入力の項目は出さない（「—」が並ぶと読みにくいため）
  const rowHtml = (kind, r, nameHtml, items) => {
    const meta = items.filter(([, val]) => val).map(([label, val]) =>
      `<span class="fp-cldetail-people-meta-item"><span class="fp-cldetail-people-meta-label">${label}</span>${escapeHtml(val)}</span>`).join("");
    return `
        <div class="fp-cldetail-people-row">
          <div class="fp-cldetail-people-main">
            <div class="fp-cldetail-people-name">${nameHtml}${clientDetailPeopleArchivedHtml(r)}</div>
            <div class="fp-cldetail-people-meta">${meta || `<span class="fp-cldetail-people-meta-none">詳細は未入力</span>`}</div>
          </div>
          <div class="fp-cldetail-people-actions">${rowActions(kind, r)}</div>
        </div>`;
  };

  const contactsHtml = loading(uiState.clientContacts) || (contacts.length === 0
    ? `<p class="fp-cldetail-people-empty">担当者は登録されていません</p>`
    : contacts.map((r) => rowHtml("contact", r,
      `${escapeHtml(clientContactDisplayName(r) || "—")}${r.honorific ? ` ${escapeHtml(r.honorific)}` : ""}`,
      [["部署", r.dept], ["役職", r.title], ["メール", r.email]])).join(""));

  const branchesHtml = loading(uiState.clientBranches) || (branches.length === 0
    ? `<p class="fp-cldetail-people-empty">支社は登録されていません</p>`
    : branches.map((r) => rowHtml("branch", r, escapeHtml(r.name || "—"),
      [["〒", r.postalCode], ["住所", clientBranchAddressLine(r)], ["TEL", r.tel], ["FAX", r.fax]])).join(""));

  // 1枚のカードに担当者・支社の2区画（基本情報タブと同じ1カード構成。追加ボタンはヘッダーのボタンと同じ枠線つき）
  return `
    <section class="soft-card fp-cldetail-card">
      <div class="fp-cldetail-people-sec">
        <div class="fp-cldetail-card-head"><b>担当者</b>
          <button type="button" class="cl-btn cl-btn-outline" data-client-contact-open="new" ${ownerAttrs("contact")}>＋ 担当者を追加</button>
        </div>
        ${contactsHtml}
      </div>
      <div class="fp-cldetail-people-sec">
        <div class="fp-cldetail-card-head"><b>支社</b>
          <button type="button" class="cl-btn cl-btn-outline" data-client-branch-open="new" ${ownerAttrs("branch")}>＋ 支社を追加</button>
        </div>
        ${branchesHtml}
      </div>
    </section>
    ${clientContactEditorModalHtml()}
    ${clientBranchEditorModalHtml()}`;
}
// ────────────────────────────────────────────────────────────────
// 取引先の申請フロー（2026-09 リニューアル）
//   - 取引先詳細ページ（#client-detail:client:12 / :partner:34。右スライド編集パネルの置き換え）
//     基本情報 / 契約書類（添付・/api/client-attachment）/ その他書類（同API・doc_kind=other）/
//     コメント（営業⇔管理部・/api/client-comments）
//   - 取引承認の申請モーダル（/api/client-approval-apply。添付一覧を確認して申請中へ）
//   - アーカイブ／復元（未申請・却下のみ。PUT /api/clients・/api/partners の archived）
//   - 取引先一覧「その他」タブ（営業非表示＝E区分。管理部のみ・置くだけ）
//
// イベント処理は 16-render-routing の巨大ハンドラに足さず、このファイルの独立リスナーで行う
// （15a-development / 15j-project-comments が手本）。コメントの吹き出しは ToDo の pm-msg 系
// CSS と progressMessage* ヘルパーを流用し、添付は 15a の 3MB チャンク分割と同じ作法。
// ────────────────────────────────────────────────────────────────

const CLIENT_DOC_FILE_MAX_BYTES = 15 * 1024 * 1024;
const CLIENT_DOC_FILE_CHUNK_BYTES = 3 * 1024 * 1024; // サーバ側 api/client-attachment.js の CHUNK_MAX_BYTES(3.5MB) より小さく保つ
const CLIENT_DOC_FILE_EXTS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".zip", ".png", ".jpg", ".jpeg", ".txt"];
const CLIENT_DOC_FILE_EXT_HINT = "PDF・Word（.doc/.docx）・Excel（.xls/.xlsx）・PowerPoint・ZIP・画像（PNG/JPEG）・テキスト";

/** "client:12" / "partner:34" → { entityType, dbId }。不正は null */
function clientFlowParseTarget(raw) {
  const [typeRaw = "", idRaw = ""] = String(raw || "").split(":");
  const entityType = typeRaw === "partner" ? "partner" : typeRaw === "client" ? "client" : "";
  const dbId = Number(idRaw);
  if (!entityType || !Number.isFinite(dbId) || dbId < 1) return null;
  return { entityType, dbId };
}

/** 契約一覧の行（contractMaster）→ 取引先詳細のターゲット文字列（"partner:34" / "client:12"）。
 *  解決できない行（新規行・マスタ未読込）は ""。clientFlowParseTarget と対の単一定義。 */
function clientFlowBuildTarget(row) {
  if (!row) return "";
  if (row.sourcePartnerId != null) return `partner:${row.sourcePartnerId}`;
  const cl = clients.find((x) => x.id === row.id);
  return cl?.dbId != null ? `client:${cl.dbId}` : "";
}

function clientFlowFindClient(dbId) {
  return clients.find((c) => Number(c.dbId) === Number(dbId)) || null;
}

function clientFlowFindPartner(dbId) {
  return (uiState.partnersCache || []).find((p) => Number(p.id) === Number(dbId)) || null;
}

/** 締結状態の表示（済＋締結日／未）。日付は YYYY-MM-DD 部分だけに丸める */
function clientFlowDocStatusText(status, date, note) {
  const s = String(status || "未").trim() === "済" ? "済" : "未";
  const d = String(date || "").slice(0, 10);
  const n = String(note || "").trim();
  return [s === "済" ? `済${d ? `（${d}）` : ""}` : "未", n].filter(Boolean).join(" ／ ");
}

/** 詳細ページの共通ビュー（クライアント/パートナーの形の差を吸収）。
 *  infoRows は基本情報タブの表示項目＝登録フォーム（renderClientCreateModal / renderPartnerModal）と同じ範囲を出す */
function clientFlowEntityView(entityType, dbId) {
  if (entityType === "partner") {
    const p = clientFlowFindPartner(dbId);
    if (!p) return null;
    const ndaOk = String(p.nda_status || "").trim() === "済";
    const ctrOk = String(p.contract_status || "").trim() === "済";
    return {
      entityType, dbId,
      name: p.name || "",
      status: normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status),
      archived: p.archived === true,
      salesHidden: p.sales_hidden === true,
      customerNo: p.customer_no || "",
      rejectReason: p.approval_reject_reason || "",
      applyNote: p.approval_apply_note || "",
      owner: p.contract_owner || "",
      registeredAt: p.first_registered_at || "",
      ndaOk, ctrOk,
      invoiceIssuer: String(p.invoice_issuer || "").trim(),
      invoiceIssuerNumber: String(p.invoice_issuer_number || "").trim(),
      expectedAmount: p.expected_amount != null ? Number(p.expected_amount) : null,
      capitalAmount: p.capital_amount != null ? Number(p.capital_amount) : null,
      establishedYm: p.established_ym || "",
      canApplyArchive: canManageClientPartnerMaster(),
      // inline=1: 右スライド/モーダルではなく基本情報タブに埋め込んで編集（2026-09-03 ユーザー要望）
      editBtnHtml: `<button class="cl-btn cl-btn-outline" data-partner-edit="${p.id}" data-partner-edit-inline="1" type="button">編集</button>`,
      infoRows: [
        { label: "名称", value: `${p.name || ""}${p.branch ? ` ${p.branch}` : ""}（${p.honorific || "御中"}）` },
        { label: "顧客番号", value: p.customer_no || "承認時に自動発番" },
        { label: "法人番号", value: p.corporate_number || "—" },
        { label: "住所", value: [p.postal_code, p.prefecture, p.address, p.building].filter(Boolean).join(" ") || "—" },
        { label: "TEL / FAX", value: [p.contact_tel, p.fax].filter(Boolean).join(" / ") || "—" },
        { label: "メール", value: p.contact_email || "—" },
        { label: "HP", value: p.hp_url || "—" },
        { label: "先方担当者", value: p.contact_name || "—" },
        { label: "社内担当者", value: p.contract_owner || "—" },
        { label: "支払条件", value: p.payment_terms || "—" },
        { label: "支払方法", value: p.payment_method || "—" },
        { label: "振込手数料負担", value: p.transfer_fee_burden || "—" },
        { label: "源泉徴収", value: p.withholding_tax || "—" },
        { label: "振込先", value: p.bank_info || "—" },
        { label: "NDA締結", value: clientFlowDocStatusText(p.nda_status) },
        { label: "基本契約締結", value: clientFlowDocStatusText(p.contract_status) },
        { label: "本契約の目的", value: p.nda_purpose || "—" },
        { label: "書類送付方法", value: p.delivery_method || "—" },
        { label: "分類", value: [p.category1, p.category2, p.category3].filter(Boolean).join(" / ") || "—" },
        { label: "取引予定金額（おおよそ）", value: p.expected_amount != null ? `¥${Number(p.expected_amount).toLocaleString("ja-JP")}` : "—" },
        ...clientApplyCreditInfoRows(p.capital_amount, p.established_ym),
        { label: "適格請求書発行事業者", value: [p.invoice_issuer, p.invoice_issuer_number].filter(Boolean).join(" ") || "—" },
        { label: "タグ", value: p.tags || "—" },
        { label: "備考", value: p.notes || "—" },
        { label: "初回登録日", value: contractRegisteredAtDisplay(p.first_registered_at) || "—" }
      ]
    };
  }
  const c = clientFlowFindClient(dbId);
  if (!c) return null;
  return {
    entityType, dbId,
    name: c.name || "",
    status: normalizeMasterApprovalStatus(c.approvalStatus),
    archived: c.archived === true,
    salesHidden: c.salesHidden === true,
    customerNo: c.customerNo || "",
    rejectReason: c.approvalRejectReason || "",
    applyNote: c.approvalApplyNote || "",
    owner: c.contractOwner || "",
    registeredAt: c.firstRegisteredAt || "",
    ndaOk: c.ndaStatus === "済",
    ctrOk: c.contractStatus === "済",
    invoiceIssuer: String(c.invoiceIssuer || "").trim(),
    invoiceIssuerNumber: String(c.invoiceIssuerNumber || "").trim(),
    expectedAmount: c.expectedAmount != null ? Number(c.expectedAmount) : null,
    capitalAmount: c.capitalAmount != null ? Number(c.capitalAmount) : null,
    establishedYm: c.establishedYm || "",
    canApplyArchive: true,
    // inline=1: 右スライド/モーダルではなく基本情報タブに埋め込んで編集（2026-09-03 ユーザー要望）
    editBtnHtml: `<button class="cl-btn cl-btn-outline" data-client-action="edit:${escapeHtmlAttr(c.id)}" data-client-edit-inline="1" type="button">編集</button>`,
    infoRows: [
      { label: "顧客名", value: `${c.name || ""}（${c.honorific || "御中"}）` },
      { label: "顧客略称名", value: c.shortName || "—" },
      { label: "顧客番号", value: c.customerNo || "承認時に自動発番" },
      { label: "法人番号", value: c.corporateNumber || "—" },
      { label: "住所", value: [c.postalCode, c.prefecture, c.address, c.building].filter(Boolean).join(" ") || "—" },
      { label: "TEL / FAX", value: [c.tel, c.fax].filter(Boolean).join(" / ") || "—" },
      { label: "メール", value: c.email || "—" },
      { label: "HP", value: c.hpUrl || "—" },
      { label: "先方担当者", value: c.contactName || "—" },
      { label: "社内担当者", value: c.contractOwner || "—" },
      { label: "支払条件", value: c.paymentTerms || "—" },
      { label: "振込手数料負担", value: c.transferFeeBurden || "—" },
      { label: "NDA締結", value: clientFlowDocStatusText(c.ndaStatus, c.ndaDate, c.ndaNote) },
      { label: "基本契約締結", value: clientFlowDocStatusText(c.contractStatus, c.contractDate, c.contractNote) },
      { label: "本契約の目的", value: c.ndaPurpose || "—" },
      { label: "書類送付方法", value: c.deliveryMethod || "—" },
      { label: "取引予定金額（おおよそ）", value: c.expectedAmount != null ? `¥${Number(c.expectedAmount).toLocaleString("ja-JP")}` : "—" },
      ...clientApplyCreditInfoRows(c.capitalAmount, c.establishedYm),
      { label: "適格請求書発行事業者", value: [c.invoiceIssuer, c.invoiceIssuerNumber].filter(Boolean).join(" ") || "—" },
      { label: "タグ", value: c.tags || "—" },
      { label: "備考", value: c.notes || "—" },
      { label: "初回登録日", value: contractRegisteredAtDisplay(c.firstRegisteredAt) || "—" }
    ]
  };
}

/** 詳細ページの取得キャッシュ（別の取引先へ移ったら取り直す） */
function ensureClientDetailState(entityType, dbId) {
  const key = `${entityType}:${dbId}`;
  if (!uiState.clientDetail || uiState.clientDetail.key !== key) {
    uiState.clientDetail = {
      key, entityType, dbId,
      comments: { loading: false, loaded: false, error: "", items: [], draft: "", submitting: false, editingId: null, editDraft: "" },
      attachments: { loading: false, loaded: false, error: "", items: [], uploading: false },
      aiCheck: null,
      aiHistory: { loading: false, loaded: false, error: "", items: [], openId: null }
    };
  }
  return uiState.clientDetail;
}

/** コメント・添付の両方が着地してから1回だけ全画面再描画する
 *  （別々に render すると初回表示でフル再描画が2回走り、入力中のIME変換も巻き込むため） */
function renderClientDetailWhenSettled(st) {
  if (uiState.clientDetail !== st) return; // 別の取引先へ移った後の応答は捨てる
  if (st.comments.loading || st.attachments.loading) return;
  // 申請モーダルの入力中（金額・ひとこと）にこの全再描画が走るとフォーカスとキャレットが失われ、
  // 続きの打鍵が消える（添付一覧の先読み完了が入力開始直後に着地するため）。描画後に同じ欄へ戻す
  const active = document.activeElement;
  const refocusSel = active instanceof HTMLElement
    ? ["[data-clapply-amount]", "[data-clapply-capital]", "[data-clapply-established]", "[data-clapply-note]"].find((sel) => active.matches(sel)) || ""
    : "";
  render();
  if (refocusSel) {
    // render は rAF 遅延のため、描画後のフレームでフォーカスを当て直す（data-aicheck-to-comment と同じ作法）
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.querySelector(refocusSel);
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.focus();
        // 年月の入力欄（type=month）は setSelectionRange できない
        if (el instanceof HTMLTextAreaElement || el.type === "text") el.setSelectionRange(el.value.length, el.value.length);
      }
    }));
  }
}

async function loadClientDetailComments(st) {
  if (st.comments.loading) return;
  st.comments.loading = true;
  const res = await apiFetch(`/api/client-comments?entityType=${st.entityType}&entityId=${st.dbId}`);
  if (uiState.clientDetail !== st) return;
  st.comments.loading = false;
  st.comments.loaded = true;
  if (res.ok && res.data?.ok && Array.isArray(res.data.comments)) {
    st.comments.items = res.data.comments;
    st.comments.error = "";
  } else {
    st.comments.error = res.data?.error || "コメントの取得に失敗しました";
  }
  renderClientDetailWhenSettled(st);
}

async function loadClientDetailAttachments(st) {
  if (st.attachments.loading) return;
  st.attachments.loading = true;
  const res = await apiFetch(`/api/client-attachment?entityType=${st.entityType}&entityId=${st.dbId}`);
  if (uiState.clientDetail !== st) return;
  st.attachments.loading = false;
  st.attachments.loaded = true;
  if (res.ok && res.data?.ok && Array.isArray(res.data.attachments)) {
    st.attachments.items = res.data.attachments;
    st.attachments.error = "";
  } else {
    st.attachments.error = res.data?.error || "契約書類の取得に失敗しました";
  }
  renderClientDetailWhenSettled(st);
}

/** AIチェック履歴（取引先を見られる人なら誰でも閲覧可。履歴は添付を消しても残る） */
async function loadClientDetailAiHistory(st) {
  if (st.aiHistory.loading) return;
  st.aiHistory.loading = true;
  const res = await apiFetch(`/api/contract-check-ai?entityType=${st.entityType}&id=${st.dbId}`);
  if (uiState.clientDetail !== st) return;
  st.aiHistory.loading = false;
  st.aiHistory.loaded = true;
  if (res.ok && res.data?.ok && Array.isArray(res.data.items)) {
    st.aiHistory.items = res.data.items;
    st.aiHistory.error = "";
  } else {
    st.aiHistory.error = res.data?.error || "AIチェック履歴の取得に失敗しました";
  }
  render();
}

/** コメント1件（ToDo・案件コメントと同じ吹き出し。操作は data-ccm-*） */
function clientCommentItemHtml(c, st) {
  const mine = progressMessageIsAuthor(c);
  const editing = st.comments.editingId != null && String(st.comments.editingId) === String(c.id);
  const authorU = progressMessageAuthorUser(c);
  const authorName = authorU ? userNameWithRetiredLabel(authorU) : String(c.userName || "—").trim();
  const avatarEl = progressUserAvatarHtml(authorU, "pm-assignee-avatar--msg", authorName);
  const timeStr = escapeHtml(formatProgressMsgTime(c.createdAt));
  const editedTag = c.edited ? `<span class="pm-msg-tag pm-msg-tag--edited">編集済み</span>` : "";
  const actions = mine && !editing
    ? `<div class="pm-msg-bactions">
         <button type="button" class="inline-btn ghost" data-ccm-edit="${escapeHtmlAttr(String(c.id))}">編集</button>
         <button type="button" class="inline-btn ghost" data-ccm-delete="${escapeHtmlAttr(String(c.id))}">削除</button>
       </div>`
    : "";
  const fromRow = `<div class="pm-msg-bfrom"><span class="pm-msg-bname">${escapeHtml(authorName)}</span><span class="pm-msg-time">${timeStr}${editedTag}</span></div>`;
  const inner = editing
    ? `${fromRow}
       <textarea class="pj-input pm-textarea pm-msg-edit-textarea" data-ccm-edit-body data-pm-mention-textarea="1" rows="4">${escapeHtml(st.comments.editDraft)}</textarea>
       <div class="pm-msg-edit-actions">
         <button type="button" class="inline-btn ghost" data-ccm-edit-cancel="1">キャンセル</button>
         <button type="button" class="inline-btn" data-ccm-edit-save="${escapeHtmlAttr(String(c.id))}"${st.comments.submitting ? " disabled" : ""}>保存</button>
       </div>`
    : `${fromRow}<div class="pm-msg-body">${progressMessageBodyDisplayHtml(c.body)}</div>`;
  return `<div class="pm-msg pm-msg--bubble${mine ? " pm-msg--mine" : ""}${editing ? " pm-msg--editing" : ""}">
    <div class="pm-msg-bav">${avatarEl}</div>
    <div class="pm-msg-bwrap"><div class="pm-msg-bbubble">${inner}</div>${actions}</div>
  </div>`;
}

/** AIチェック（過不足の洗い出し）にかけられるファイルか（PDF / Word(docx) / テキスト） */
function clientAiCheckSupported(filename) {
  return /\.(pdf|docx|txt)$/i.test(String(filename || ""));
}

/** 添付を契約書類／その他書類（doc_kind=other・引っ越しのお知らせ等）に分ける。
 *  書類提出ピル・申請モーダル・AIチェックは契約書類だけを対象にする */
function clientDetailContractDocs(st) {
  return st.attachments.items.filter((f) => f.docKind !== "other");
}
function clientDetailOtherDocs(st) {
  return st.attachments.items.filter((f) => f.docKind === "other");
}

/** ルートの detailTab → 表示タブ（不正値は基本情報） */
function clientDetailActiveTab(route) {
  return ["people", "orders", "docs", "other-docs", "comments"].includes(route.detailTab) ? route.detailTab : "info";
}

/** 書類1行（契約書類・その他書類で共通の見た目）。actionsHtml は右側の操作。f.rejected なら却下理由も出す */
function clientDocFileRowHtml(f, actionsHtml) {
  return `
    <div class="fp-cldetail-file${f.rejected ? " fp-cldetail-file--rejected" : ""}">
      <span class="fp-cldetail-file-ic">${escapeHtml((String(f.filename).match(/\.([a-z0-9]+)$/i) || [, "file"])[1].toUpperCase().slice(0, 4))}</span>
      <span class="fp-cldetail-file-main"><span class="fp-cldetail-file-name">${escapeHtml(f.filename)}</span>
        <span class="fp-cldetail-file-by">${escapeHtml(f.createdByName || "—")} ・ ${escapeHtml(formatProgressMsgTime(f.createdAt))} ・ ${developmentFileSizeLabel(f.sizeBytes)}</span>
        ${f.rejected ? `<span class="fp-cldetail-file-rejected">却下：${escapeHtml(f.rejectReason)}${f.rejectedByName ? `（${escapeHtml(f.rejectedByName)}）` : ""}</span>` : ""}</span>
      <span class="fp-cldetail-file-actions">${actionsHtml}</span>
    </div>`;
}

/** その他書類の一覧（開く・削除・契約書類へ移動のみ。AIチェック・却下は契約書類専用） */
function clientDetailOtherDocsHtml(st) {
  const a = st.attachments;
  if (a.loading && !a.loaded) return `<p class="pc-empty">読み込み中…</p>`;
  if (a.error) return `<p class="pc-empty pc-error">${escapeHtml(a.error)}</p>`;
  const items = clientDetailOtherDocs(st);
  if (!items.length) return `<p class="pc-empty">その他書類はまだありません。引っ越し（移転）のお知らせなど、取引先から届いた書類をここに保管できます。</p>`;
  return items.map((f) => clientDocFileRowHtml(f, `
        <select class="cl-filter-input fp-clatt-kind-sel" data-search-dropdown-off="1" data-clatt-kind="${f.id}" title="契約書類だった場合は契約書類タブへ移せます">
          <option value="other" selected>その他書類</option>
          <option value="">契約書類へ移動</option>
        </select>
        <button type="button" class="inline-btn ghost" data-clatt-download="${f.id}">開く</button>
        <button type="button" class="inline-btn ghost" data-clatt-delete="${f.id}">削除</button>`)).join("");
}

function clientDetailAttachmentsHtml(st) {
  const a = st.attachments;
  if (a.loading && !a.loaded) return `<p class="pc-empty">読み込み中…</p>`;
  if (a.error) return `<p class="pc-empty pc-error">${escapeHtml(a.error)}</p>`;
  const items = clientDetailContractDocs(st);
  if (!items.length) return `<p class="pc-empty">契約書類はまだありません。NDA・業務委託契約書（自社/他社フォーマット）をここに添付できます。</p>`;
  // AIチェックは取引先を見られる人なら誰でも実行可（2026-09-04 マスター/経理限定を撤廃）
  const canAi = true;
  // 却下（差し戻し）はマスター/経理のみ操作可（api/client-attachment.js PUT action:reject と同じ基準）
  const canReject = canManageClientPartnerMaster();
  return items.map((f) => clientDocFileRowHtml(f, `
        <select class="cl-filter-input fp-clatt-kind-sel" data-search-dropdown-off="1" data-clatt-kind="${f.id}" title="カテゴリ（承認時にNDA/基本契約の締結状態へ自動反映）">
          <option value="" ${!f.docKind ? "selected" : ""}>その他の契約書類</option>
          <option value="nda" ${f.docKind === "nda" ? "selected" : ""}>NDA</option>
          <option value="gyomu-itaku" ${f.docKind === "gyomu-itaku" ? "selected" : ""}>業務委託基本契約書</option>
          ${f.rejected ? "" : `<option value="other">その他書類へ移動</option>`}
        </select>
        <button type="button" class="inline-btn ghost" data-clatt-download="${f.id}">開く</button>
        ${canAi && clientAiCheckSupported(f.filename)
          ? `<button type="button" class="inline-btn" data-clatt-aicheck="${f.id}">AIチェック</button>`
          : ""}
        ${canReject
          ? f.rejected
            ? `<button type="button" class="inline-btn ghost" data-clatt-unreject="${f.id}">却下を取消</button>`
            : `<button type="button" class="inline-btn ghost" data-clatt-reject="${f.id}">却下</button>`
          : ""}
        <button type="button" class="inline-btn ghost" data-clatt-delete="${f.id}">削除</button>`)).join("");
}

/** 《《…》》で囲まれた特に重要な文言を赤字にする（エスケープ後にマーカーだけ置換＝XSS安全） */
function clientAiCheckEmphasize(text) {
  return escapeHtml(String(text || "")).replace(/《《(.*?)》》/g, '<span class="fp-aicheck-crit">$1</span>');
}

/** チェック結果本体（不足/過剰・相違/所見）のマークアップ。実行直後パネルと履歴の展開で共有 */
function clientAiCheckResultBodyHtml(result) {
  const MARK_GLYPHS = { ng: "✕", warn: "⚠", info: "ｉ" };
  const rowsHtml = (items, markOf) =>
    (items || []).length
      ? `<ul class="fp-aicheck-list">${items
          .map((it) => {
            const m = markOf(it);
            return `<li><span class="fp-aicheck-mark fp-aicheck-mark--${m}">${MARK_GLYPHS[m]}</span>
              <span><b>${escapeHtml(it.clause)}</b> — ${clientAiCheckEmphasize(it.detail)}${it.quote ? `<span class="fp-aicheck-quote">${escapeHtml(it.quote)}</span>` : ""}</span></li>`;
          })
          .join("")}</ul>`
      : `<p class="fp-aicheck-none">該当なし</p>`;
  return `
      <div class="fp-aicheck-cols">
        <div><div class="fp-aicheck-h fp-aicheck-h--ng">不足：自社フォーマットにあるのに先方書式に無い</div>${rowsHtml(result.missing, () => "ng")}</div>
        <div><div class="fp-aicheck-h fp-aicheck-h--warn">過剰・相違：先方書式にだけある／内容が違う</div>${rowsHtml(result.extraOrDifferent, (it) => (it.severity === "info" ? "info" : "warn"))}</div>
      </div>
      ${result.summary ? `<div class="fp-aicheck-summary">所見：${clientAiCheckEmphasize(result.summary)}</div>` : ""}`;
}

/** AIチェックの結果パネル（取引先詳細の契約書類カードの下に出す） */
function clientAiCheckPanelHtml(st) {
  const ai = st.aiCheck;
  if (!ai) return "";
  let body;
  if (ai.running) {
    body = `<p class="pc-empty">AIが自社フォーマットと突き合わせています…（30秒ほどかかることがあります）</p>`;
  } else if (ai.error) {
    body = `<p class="pc-empty pc-error">${escapeHtml(ai.error)}</p>`;
  } else if (ai.result) {
    body = `${clientAiCheckResultBodyHtml(ai.result)}
      <div class="fp-aicheck-actions"><button type="button" class="inline-btn ghost" data-aicheck-to-comment="1">結果をコメント欄に貼る</button></div>
      <div class="fp-aicheck-note">AIによるチェックの補助です。締結判断は原文をご確認ください。</div>`;
  } else {
    body = `<p class="pc-empty">比較の基準（自社フォーマット）を選んで「実行」を押してください。</p>`;
  }
  return `
    <div class="fp-aicheck">
      <div class="fp-aicheck-head">
        <b>AIチェック：${escapeHtml(ai.filename)}</b>
        <span class="fp-aicheck-head-tools">
          <select class="cl-filter-input fp-aicheck-kind" data-search-dropdown-off="1" data-aicheck-kind>
            <option value="nda" ${ai.templateKind === "nda" ? "selected" : ""}>自社NDAと比較</option>
            <option value="gyomu-itaku" ${ai.templateKind === "gyomu-itaku" ? "selected" : ""}>自社業務委託契約と比較</option>
          </select>
          <button type="button" class="inline-btn" data-aicheck-run="1"${ai.running ? " disabled" : ""}>${ai.running ? "実行中…" : ai.result ? "再実行" : "実行"}</button>
          <button type="button" class="inline-btn ghost" data-aicheck-close="1">閉じる</button>
        </span>
      </div>
      ${body}
    </div>`;
}

/** AIチェック履歴カード（契約書類タブの下）。行クリックで過去の結果を展開する */
function clientAiHistoryHtml(st) {
  const h = st.aiHistory;
  let body;
  if (h.loading && !h.loaded) {
    body = `<p class="pc-empty">読み込み中…</p>`;
  } else if (h.error) {
    body = `<p class="pc-empty pc-error">${escapeHtml(h.error)}</p>`;
  } else if (!h.items.length) {
    body = `<p class="pc-empty">まだAIチェックの履歴はありません。</p>`;
  } else {
    body = h.items.map((it) => {
      const open = h.openId != null && String(h.openId) === String(it.id);
      const kindLabel = it.templateKind === "gyomu-itaku" ? "自社業務委託契約と比較" : "自社NDAと比較";
      return `
        <div class="fp-aihist-item${open ? " fp-aihist-item--open" : ""}">
          <button type="button" class="fp-aihist-row" data-aihist-toggle="${escapeHtmlAttr(String(it.id))}">
            <span class="fp-aihist-time">${escapeHtml(formatProgressMsgTime(it.createdAt))}</span>
            <span class="fp-aihist-file">${escapeHtml(it.filename)}</span>
            <span class="fp-aihist-kind">${kindLabel}</span>
            <span class="fp-aihist-by">${escapeHtml(it.createdByName || "—")}</span>
            <span class="fp-aihist-caret">${open ? "▲" : "▼"}</span>
          </button>
          ${open ? `<div class="fp-aihist-result">${clientAiCheckResultBodyHtml(it.result || {})}</div>` : ""}
        </div>`;
    }).join("");
  }
  return `
    <section class="soft-card fp-cldetail-card fp-aihist">
      <div class="fp-cldetail-card-head"><b>AIチェック履歴</b><span class="fp-aihist-count">${h.loaded ? h.items.length : "…"}件</span></div>
      ${body}
    </section>`;
}

/** docx(ZIP) の word/document.xml から本文テキストを取り出す（Word出力と同じ PizZip を利用） */
async function clientFlowExtractDocxText(arrayBuffer) {
  await ensurePizZipLoaded();
  const PizZipCtor = window.PizZip;
  if (!PizZipCtor) throw new Error("ZIPライブラリの読み込みに失敗しました");
  const zip = new PizZipCtor(arrayBuffer);
  const f = zip.file("word/document.xml");
  if (!f) throw new Error("Wordファイルの本文を読み取れませんでした");
  return f
    .asText()
    // 表のセル/行・タブ・改行は区切り文字にしてから全タグを除去（区切りなしで潰すと表組みの条項が1行に連結され、条項単位の照合が壊れる）
    .replace(/<w:tab\b[^>]*\/>/g, "\t")
    .replace(/<w:br\b[^>]*\/>/g, "\n")
    .replace(/<\/w:tc>/g, "\t")
    .replace(/<\/w:tr>/g, "\n")
    .replace(/<\/w:p>/g, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** 自社テンプレ本文（Word出力に使う client-templates/*.docx と同一ファイル＝単一定義元）。
 *  テンプレはリリース単位で不変なので抽出結果はセッション内キャッシュ（再実行のたびのDL＋展開を避ける） */
const _clientAiTemplateTextCache = {};
async function clientFlowFetchTemplateText(templateKind) {
  if (_clientAiTemplateTextCache[templateKind]) return _clientAiTemplateTextCache[templateKind];
  const path = templateKind === "gyomu-itaku" ? "client-templates/gyomu-itaku-template.docx" : "client-templates/nda-template.docx";
  const res = await fetch(new URL(path, window.location.href).href, { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) throw new Error(`自社テンプレートが見つかりません（${path}）`);
  const buf = await res.arrayBuffer();
  // SPAのrewriteで存在しないパスにも200+index.htmlが返るため、docx実体か必ず検査する（09のWord出力と同じガード）
  if (!responseLooksLikeDocx(res, buf)) {
    throw new Error("自社テンプレートの取得に失敗しました（HTMLが返っています。キャッシュを消して再試行してください）");
  }
  const text = await clientFlowExtractDocxText(buf);
  _clientAiTemplateTextCache[templateKind] = text;
  return text;
}

/** 添付の本文バイト列＋メタを取得（4.5MB応答上限を越えるものは分割を並列取得して結合）。
 *  メタは一覧キャッシュ優先で1回だけ解決し、「開く」「AIチェック」の両方がこの1本を使う */
async function clientFlowFetchAttachmentBuffer(st, attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) throw new Error("ファイルの指定が不正です");
  if (!authToken) throw new Error("ログインが必要です");
  const headers = { Authorization: `Bearer ${authToken}` };
  const fetchOk = async (extraQuery) => {
    const res = await fetch(`/api/client-attachment?id=${encodeURIComponent(String(id))}${extraQuery}`, { headers });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(typeof j.error === "string" ? j.error : "ファイルの取得に失敗しました");
    }
    return res;
  };
  const cached = st?.attachments?.items?.find((f) => String(f.id) === String(id));
  const meta = cached || (await (await fetchOk("&meta=1")).json().catch(() => ({})));
  const size = Number(meta.sizeBytes) || 0;
  if (size <= CLIENT_DOC_FILE_CHUNK_BYTES) {
    return { buffer: await (await fetchOk("")).arrayBuffer(), meta };
  }
  const offsets = [];
  for (let off = 0; off < size; off += CLIENT_DOC_FILE_CHUNK_BYTES) offsets.push(off);
  const parts = await Promise.all(
    offsets.map((off) =>
      fetchOk(`&offset=${off}&length=${Math.min(CLIENT_DOC_FILE_CHUNK_BYTES, size - off)}`).then((r) => r.arrayBuffer())
    )
  );
  const total = parts.reduce((n, p) => n + p.byteLength, 0);
  const buf = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) {
    buf.set(new Uint8Array(p), pos);
    pos += p.byteLength;
  }
  return { buffer: buf.buffer, meta };
}

async function clientFlowRunAiCheck(st) {
  const ai = st.aiCheck;
  if (!ai || ai.running) return;
  ai.running = true;
  ai.error = "";
  render();
  try {
    const templateText = await clientFlowFetchTemplateText(ai.templateKind);
    const body = { attachmentId: ai.attachmentId, templateKind: ai.templateKind, templateText };
    if (/\.docx$/i.test(ai.filename)) {
      const { buffer } = await clientFlowFetchAttachmentBuffer(st, ai.attachmentId);
      body.targetText = await clientFlowExtractDocxText(buffer);
    }
    const res = await apiFetch("/api/contract-check-ai", { method: "POST", body });
    if (uiState.clientDetail !== st || st.aiCheck !== ai) return;
    ai.running = false;
    if (res.ok && res.data?.ok) {
      ai.result = { missing: res.data.missing || [], extraOrDifferent: res.data.extraOrDifferent || [], summary: res.data.summary || "" };
      // 履歴の先頭にも足す（サーバ保存に成功したときだけ history が返る）
      if (res.data.history && st.aiHistory) {
        st.aiHistory.items.unshift({
          id: res.data.history.id,
          attachmentId: ai.attachmentId,
          filename: ai.filename,
          templateKind: ai.templateKind,
          result: ai.result,
          createdByName: res.data.history.createdByName || "",
          createdAt: res.data.history.createdAt
        });
      }
    } else if (res.data?.reason === "ai_unconfigured") {
      ai.error = "AIチェックは未設定です（ANTHROPIC_API_KEY を設定すると使えます）";
    } else {
      // サーバの定型エラー（サイズ超過・文字コード等）はそのまま。生のSDK例外文言は返ってこない
      ai.error = res.data?.error || "AIチェックに失敗しました。時間をおいて再実行してください";
    }
  } catch (e) {
    if (uiState.clientDetail !== st || st.aiCheck !== ai) return;
    ai.running = false;
    ai.error = e?.message || "AIチェックに失敗しました";
  }
  render();
}

/** チェック結果 → コメント欄に貼るテキスト。
 *  全文は長すぎて誰も読まない（ユーザー指摘）ため、所見＋件数の要約だけ貼り、詳細はAIチェック履歴へ誘導する */
function clientAiCheckResultToText(ai) {
  const r = ai.result;
  const kind = ai.templateKind === "gyomu-itaku" ? "自社業務委託契約" : "自社NDA";
  const summary = String(r.summary || "").replace(/《《|》》/g, "").trim();
  const lines = [
    `先方の契約書（${ai.filename}）を${kind}と比較しました（不足${r.missing.length}件・過剰/相違${r.extraOrDifferent.length}件）。`
  ];
  if (summary) lines.push(summary);
  lines.push(`詳細は契約書類タブの「AIチェック履歴」（${ai.filename}）をご確認ください。`);
  return lines.join("\n");
}

/** 取引先詳細ページ本体 */
/** 詳細ページの埋め込み編集中か（"client" / "partner" / null）。表示とヘッダーの出し分けに使う */
function clientDetailInlineEditingKind(v) {
  if (
    v.entityType === "client" && uiState.clientCreate?.inline && uiState.clientCreate.mode === "edit" &&
    String(uiState.clientCreate.id) === `c${v.dbId}`
  ) return "client";
  if (v.entityType === "partner" && uiState.partnerCreate?.inline && Number(uiState.partnerCreate.id) === Number(v.dbId)) {
    return "partner";
  }
  return null;
}

/** ヘッダー右上の操作ボタン。編集中は「Word出力・削除申請・キャンセル・更新申請」に切り替える
 *  （モーダル用フッターはCSSで隠すため、保存系の操作はここに一本化。ユーザー要望＝右上に更新ボタン） */
function clientDetailHeaderActionsHtml(v) {
  const editing = clientDetailInlineEditingKind(v);
  if (editing) {
    const isClient = editing === "client";
    const state = isClient ? uiState.clientCreate : uiState.partnerCreate;
    const submitting = !!state?._submitting;
    const attr = {
      exportDoc: isClient ? "data-client-export-doc" : "data-partner-export-doc",
      del: isClient ? "data-client-delete-request" : "data-partner-delete-request",
      close: isClient ? "data-close-client-create" : "data-close-partner",
      save: isClient ? "data-save-client" : "data-save-partner"
    };
    // 未締結のWord出力ボタンは元どおり注意色。ただし基本契約が締結済みならNDAは不要扱い＝色なし
    // （モーダルのフッターにあった ndaPending/contractPending と同じ判定）
    const contractPending = String(state?.contractStatus || "未").trim() !== "済";
    const ndaPending = String(state?.ndaStatus || "未").trim() !== "済" && contractPending;
    // 並び順はユーザー指定: NDA → 業務委託基本契約書（フルネーム）→ 更新申請 → 削除申請（キャンセルは左端）。
    // 未申請の取引先は削除申請ではなくアーカイブを出す（取引にならなかったものは削除でなく退避）
    const dangerBtn =
      v.status === "未申請"
        ? v.archived
          ? "" // アーカイブ済みはこれ以上退避できない（復元は表示ビューのヘッダーにある）
          : `<button class="cl-btn cl-btn-outline" data-client-flow-archive="${v.entityType}:${v.dbId}" data-client-flow-archive-value="true" type="button">アーカイブ</button>`
        : canManageClientPartnerMaster()
          ? `<button class="cl-btn cl-btn-outline" ${attr.del}="1" type="button">削除申請</button>`
          : "";
    return [
      `<button class="cl-btn cl-btn-outline" ${attr.close}="1" type="button"${submitting ? " disabled" : ""}>キャンセル</button>`,
      `<button class="cl-btn cl-btn-outline${ndaPending ? " client-export-doc-pending" : ""}" ${attr.exportDoc}="nda" type="button">NDAを出力</button>`,
      `<button class="cl-btn cl-btn-outline${contractPending ? " client-export-doc-pending" : ""}" ${attr.exportDoc}="gyomu-itaku" type="button">業務委託基本契約書を出力</button>`,
      // 申請が通る前（未申請・申請中・却下）の編集は申請なしの直接保存＝「保存」、
      // 承認済の内容変更だけ変更申請フロー＝「更新申請」（登録申請は一覧/詳細の「申請する」ボタンから別途行う）
      `<button class="cl-btn cl-btn-primary" ${attr.save}="1" type="button"${submitting ? ' disabled aria-busy="true"' : ""}>${submitting ? (v.status === "承認済" ? "申請中…" : "保存中…") : v.status === "承認済" ? "更新申請" : "保存"}</button>`,
      dangerBtn
    ].filter(Boolean).join("");
  }
  // 申請可否は一覧カード（clientDirectoryCardActionsHtml）と同じ述語。
  // アーカイブは詳細ページには置かない（一覧カード側で行う。ユーザー指摘）。
  // 復元だけは残す＝アーカイブ済みの詳細を開いたとき、ここ以外に戻す導線が無いため
  const applyable = v.canApplyArchive && !v.archived && (v.status === "未申請" || v.status === "却下");
  // Word出力は編集前も出す（ユーザー要望＝編集ボタンの横）。保存済みデータから差し込むので編集状態は不要。
  // 注意色は編集時と同じ判定（基本契約が締結済みならNDAは不要扱い＝色なし）
  const contractPending = !v.ctrOk;
  const ndaPending = !v.ndaOk && contractPending;
  const exportBtn = (kind, label, pending) =>
    `<button class="cl-btn cl-btn-outline${pending ? " client-export-doc-pending" : ""}" data-client-flow-export-doc="${v.entityType}:${v.dbId}:${kind}" type="button">${label}</button>`;
  return [
    v.editBtnHtml,
    exportBtn("nda", "NDAを出力", ndaPending),
    exportBtn("gyomu-itaku", "業務委託基本契約書を出力", contractPending),
    // 表示範囲（マスター・経理のみ／全員）の切替は一覧カードでなくここに置く（一覧では誤って押しやすい。2026-10-28 ユーザー指摘）
    clientListLimitedToggleHtml(v.entityType, v.dbId, v.salesHidden),
    // 誰がいつ何を変えたか（監査ログ）。取得は /api/entity-change-history（取引先一覧・契約管理・案件のいずれかの権限で見られる）
    clientChangeHistoryButtonHtml(v.entityType, v.dbId, v.name),
    v.canApplyArchive && v.archived
      ? `<button class="cl-btn cl-btn-outline" data-client-flow-archive="${v.entityType}:${v.dbId}" data-client-flow-archive-value="false" type="button">復元</button>` : "",
    applyable
      ? `<button class="cl-btn cl-btn-primary" data-client-flow-apply-open="${v.entityType}:${v.dbId}" type="button">${v.status === "却下" ? "再申請する" : "申請する"}</button>` : ""
  ].filter(Boolean).join("");
}

/** タブバー＋タブ本体（[data-cldetail-main] の中身）。編集切替の部分更新でも同じこの関数で作る
 *  ＝初回描画と部分更新のマークアップ二重化を避ける（既知の「片方だけ直す」罠の回避） */
function clientDetailMainHtml(v, st, activeTab) {
  const commentsList = st.comments.loading && !st.comments.loaded
    ? `<p class="pc-empty">読み込み中…</p>`
    : st.comments.error
      ? `<p class="pc-empty pc-error">${escapeHtml(st.comments.error)}</p>`
      : st.comments.items.length === 0
        ? `<p class="pc-empty">まだコメントはありません。書式の確認依頼や「原本を郵送してください」等のやりとりをここに残せます。</p>`
        : st.comments.items.map((c) => clientCommentItemHtml(c, st)).join("");

  // タブ（基本情報/担当者・支社/契約書類/その他書類/コメント）。案件詳細と同じ ?tab= 遷移＝ハッシュリンクで再描画される
  const tabHref = (tab) => `#client-detail:${v.entityType}:${v.dbId}${tab === "info" ? "" : `?tab=${tab}`}`;
  const docsCount = st.attachments.loaded ? String(clientDetailContractDocs(st).length) : "…";
  const otherDocsCount = st.attachments.loaded ? String(clientDetailOtherDocs(st).length) : "…";
  const commentsCount = st.comments.loaded ? String(st.comments.items.length) : "…";
  const tabsHtml = `
    <div class="cl-class-tabs fp-cldetail-tabs" role="tablist" aria-label="取引先詳細のタブ切替">
      <a role="tab" class="cl-class-tab ${activeTab === "info" ? "active" : ""}" aria-selected="${activeTab === "info"}" href="${tabHref("info")}">基本情報</a>
      <a role="tab" class="cl-class-tab ${activeTab === "people" ? "active" : ""}" aria-selected="${activeTab === "people"}" href="${tabHref("people")}">担当者・支社<span class="cl-class-tab-count">${clientDetailPeopleCount(v)}</span></a>${clientDetailOrdersTabLinkHtml(v, activeTab, tabHref)}
      <a role="tab" class="cl-class-tab ${activeTab === "docs" ? "active" : ""}" aria-selected="${activeTab === "docs"}" href="${tabHref("docs")}">契約書類<span class="cl-class-tab-count">${docsCount}</span></a>
      <a role="tab" class="cl-class-tab ${activeTab === "other-docs" ? "active" : ""}" aria-selected="${activeTab === "other-docs"}" href="${tabHref("other-docs")}">その他書類<span class="cl-class-tab-count">${otherDocsCount}</span></a>
      <a role="tab" class="cl-class-tab ${activeTab === "comments" ? "active" : ""}" aria-selected="${activeTab === "comments"}" href="${tabHref("comments")}">コメント<span class="cl-class-tab-count">${commentsCount}</span></a>
    </div>`;

  let tabBody;
  if (activeTab === "orders") { tabBody = clientDetailOrdersHtml(v); } else if (activeTab === "people") { // 発注書は 15k-order-evidence.js
    tabBody = clientDetailPeopleHtml(v);
  } else if (activeTab === "docs") {
    tabBody = `
      <section class="soft-card fp-cldetail-card">
        <div class="fp-cldetail-card-head"><b>契約書類</b>
          <label class="inline-btn fp-cldetail-addfile-btn">＋ ファイル追加<input type="file" data-clatt-file-input="1" multiple accept="${CLIENT_DOC_FILE_EXTS.join(",")}" hidden /></label>
        </div>
        ${st.attachments.uploading ? `<p class="pc-empty">アップロード中…</p>` : ""}
        ${clientDetailAttachmentsHtml(st)}
        ${clientAiCheckPanelHtml(st)}
      </section>
      ${clientAiHistoryHtml(st)}`;
  } else if (activeTab === "other-docs") {
    // 契約書類以外の受領書類（引っ越しのお知らせ等）。カテゴリ選択は挟まず other で直接添付する
    tabBody = `
      <section class="soft-card fp-cldetail-card">
        <div class="fp-cldetail-card-head"><b>その他書類</b>
          <label class="inline-btn fp-cldetail-addfile-btn">＋ ファイル追加<input type="file" data-clatt-file-input="other" multiple accept="${CLIENT_DOC_FILE_EXTS.join(",")}" hidden /></label>
        </div>
        ${st.attachments.uploading ? `<p class="pc-empty">アップロード中…</p>` : ""}
        ${clientDetailOtherDocsHtml(st)}
      </section>`;
  } else if (activeTab === "comments") {
    tabBody = `
      <section class="soft-card fp-cldetail-card fp-cldetail-comments">
        <div class="fp-cldetail-card-head"><b>コメント（営業 ⇔ 管理部）</b></div>
        <div class="pc-list">${commentsList}</div>
        <div class="pc-composer">
          <textarea class="pj-input pm-textarea" data-ccm-draft data-pm-mention-textarea="1" rows="3" placeholder="コメントを入力（@で担当者にメンション）">${escapeHtml(st.comments.draft)}</textarea>
          <div class="pc-composer-actions">
            <button type="button" class="inline-btn" data-ccm-post="1"${st.comments.submitting || !st.comments.draft.trim() ? " disabled" : ""}>投稿</button>
          </div>
        </div>
      </section>`;
  } else {
    // 基本情報: 編集中はページ埋め込みでフォーム全体を表示（右スライド/モーダルは出さない。
    // 表示ビューと同じ見た目・右いっぱい。保存/Word出力/削除申請/キャンセルはヘッダー右上に集約し、
    // モーダル用のヘッダー（閉じる）とフッターはCSSで隠す＝フォームが下部のボタンと被らない）
    const editing = clientDetailInlineEditingKind(v);
    if (editing) {
      tabBody = `<div class="fp-cldetail-inline-edit">${
        editing === "client" ? renderClientCreateModal({ embed: true }) : renderPartnerModal({ embed: true })
      }</div>`;
    } else {
      // 表示モード: 登録フォームと同じ全項目を縦1列で表示（編集はヘッダーの「編集」で上のフォームに切替）
      const rows = v.infoRows || [];
      tabBody = `
        <section class="soft-card fp-cldetail-card">
          <div class="fp-cldetail-card-head"><b>基本情報</b><span class="fp-cldetail-nda-badges">${clientDirectoryContractBadgesHtml(v.ndaOk, v.ctrOk)}</span></div>
          <table class="fp-cldetail-kv fp-cldetail-kv--single">${rows
            .map((r) => `<tr><th>${escapeHtml(r.label)}</th><td>${escapeHtml(r.value)}</td></tr>`)
            .join("")}</table>
        </section>`;
    }
  }
  return `${tabsHtml}${tabBody}`;
}

/** 書類提出状況ピル（申請状況ピルとは別軸の表示。2026-09-07 ユーザー指摘: 申請後に書類を
 *  提出したかどうかが画面から分からなかった）。契約書類タブの添付有無・却下有無で判定する。
 *  却下中の書類が1件でもあれば「差し戻し中」を優先表示（承認済み後の追加提出でも起こり得る。
 *  ユーザー指摘: 「NDAだけ提出済だったパートナーが正式契約時に業務委託契約書を提出→中身差し戻し」）。
 *  読み込み中・取得失敗時は出さない（false陽性で「未提出」と誤表示しないため） */
function clientFlowDocsPillHtml(st) {
  if (!st.attachments.loaded || st.attachments.error) return "";
  // その他書類（お知らせ等）は提出状況に数えない（clientDetailContractDocs と同じ条件）
  const items = st.attachments.items.filter((f) => f.docKind !== "other");
  const hasRejected = items.some((f) => f.rejected);
  const submitted = items.length > 0;
  const mod = hasRejected ? "warn" : submitted ? "ok" : "none";
  const text = hasRejected ? "差し戻し中" : submitted ? "提出済" : "未提出";
  return `<span class="cl-client-approval-pill cl-client-approval-pill--${mod}" title="契約書類タブへの添付ファイルの有無・却下有無"><span class="cl-client-approval-dot cl-client-approval-dot--${mod}" aria-hidden="true"></span><span class="cl-client-approval-text">書類：${text}</span></span>`;
}

function clientDetailPage(route) {
  const target = clientFlowParseTarget(`${route.entityType}:${route.id}`);
  if (!target) return `<div class="page cl-page"><div class="cl-page-wrap cl-page-wrap--alike-pj"><div class="cl-empty" style="padding:24px;">取引先の指定が不正です。<a href="#client-list">取引先一覧へ戻る</a></div></div></div>`;
  const v = clientFlowEntityView(target.entityType, target.dbId);
  if (!v) {
    const loading = uiState.heavyDataLoading || (target.entityType === "partner" && !Array.isArray(uiState.partnersCache));
    return `<div class="page cl-page"><div class="cl-page-wrap cl-page-wrap--alike-pj"><div class="cl-empty" style="padding:24px;">${
      loading ? "取引先情報を読み込んでいます…" : `対象の取引先が見つかりません。<a href="#client-list">取引先一覧へ戻る</a>`
    }</div></div></div>`;
  }
  const st = ensureClientDetailState(target.entityType, target.dbId);
  if (!st.comments.loaded && !st.comments.loading) void loadClientDetailComments(st);
  if (!st.attachments.loaded && !st.attachments.loading) void loadClientDetailAttachments(st);
  if (!st.aiHistory.loaded && !st.aiHistory.loading) void loadClientDetailAiHistory(st);

  const rejectRow = v.status === "却下" && v.rejectReason
    ? `<div class="fp-cldetail-reject">却下理由：${escapeHtml(v.rejectReason)}</div>` : "";
  // 申請中は「何を申請しているのか」（取引承認＝新規取引の承認）と申請内容を明示する
  const applyNoteRow = v.status === "申請中"
    ? `<div class="fp-cldetail-applynote">取引承認（新規取引の承認）を申請中です${
        v.expectedAmount != null ? `／取引予定金額（おおよそ）：¥${Number(v.expectedAmount).toLocaleString("ja-JP")}` : ""
      }${v.applyNote ? `<br>申請メモ：${escapeHtml(v.applyNote)}` : ""}</div>` : "";
  const activeTab = clientDetailActiveTab(route);

  return `
    <div class="page cl-page">
      <div class="cl-page-wrap cl-page-wrap--alike-pj fp-cldetail">
        <div class="fp-cldetail-head">
          <div class="fp-cldetail-head-main">
            <a class="fp-cldetail-back" href="${v.entityType === "partner" ? "#partner-list" : "#client-list"}">← 取引先一覧</a>
            <h2 class="fp-cldetail-title">${escapeHtml(v.name)} ${clientDirectoryApprovalPillHtml(v.status, v.archived)}${clientFlowDocsPillHtml(st)}</h2>
            <div class="fp-cldetail-meta">
              <span>顧客番号：<b>${escapeHtml(v.customerNo || (v.status === "承認済" ? "—" : "承認時に自動発番"))}</b></span>
              <span>区分：${v.entityType === "partner" ? "パートナー" : "クライアント"}</span>
              <span>社内担当者：${escapeHtml(v.owner || "—")}</span>
              <span>登録日：${escapeHtml(contractRegisteredAtDisplay(v.registeredAt) || "—")}</span>
            </div>
            ${rejectRow}${applyNoteRow}
          </div>
          <div class="fp-cldetail-head-actions" data-cldetail-actions>${clientDetailHeaderActionsHtml(v)}</div>
        </div>
        <div data-cldetail-main>${clientDetailMainHtml(v, st, activeTab)}</div>
      </div>
    </div>
  `;
}

/** 編集⇄表示の切替を、全画面 render ではなくヘッダー操作＋タブ本体だけ差し替えて行う
 *  （全画面 innerHTML 再構築の「ちらつき」を出さない。DOMが無ければ通常の render に落とす） */
function clientDetailPartialRefresh() {
  const r = currentRoute();
  if (r.page !== "client-detail") {
    render();
    return;
  }
  const target = clientFlowParseTarget(`${r.entityType}:${r.id}`);
  const v = target && clientFlowEntityView(target.entityType, target.dbId);
  const actionsEl = document.querySelector("[data-cldetail-actions]");
  const mainEl = document.querySelector("[data-cldetail-main]");
  if (!v || !actionsEl || !mainEl) {
    render();
    return;
  }
  const st = ensureClientDetailState(target.entityType, target.dbId);
  const activeTab = clientDetailActiveTab(r);
  actionsEl.innerHTML = clientDetailHeaderActionsHtml(v);
  mainEl.innerHTML = clientDetailMainHtml(v, st, activeTab);
}

/** 申請モーダルの金額入力を整数に（全角数字→半角、桁区切りカンマは許容）。
 *  数字と桁区切り以外が混ざる入力は丸めずに弾く（「約50万円」を 50 と解釈しない）。不正/空/0以下は null */
function clientApplyAmountParse(raw) {
  const s = String(raw ?? "").trim().replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
  if (!/^[\d,，]+$/.test(s)) return null;
  const n = Number(s.replace(/[,，]/g, ""));
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** パートナー申請時のインボイス必須チェック（サーバ側 client-approval-apply と同じルール）。
 *  発注先は「該当する／該当しない」を必ず選び、該当する場合は登録番号（T+13桁）も必須。 */
function clientApplyInvoiceError(v) {
  if (v.entityType !== "partner") return "";
  if (v.invoiceIssuer !== "該当する" && v.invoiceIssuer !== "該当しない") {
    return "パートナーの申請には、適格請求書発行事業者（インボイス）の「該当する／該当しない」の設定が必要です。編集から設定してください";
  }
  if (v.invoiceIssuer === "該当する" && !/^T\d{13}$/.test(v.invoiceIssuerNumber)) {
    return "適格請求書発行事業者の登録番号（T + 13桁の数字）を編集から入力してください";
  }
  return "";
}

/** 取引承認の申請モーダル */
function renderClientApplyModal() {
  const m = uiState.clientApplyModal;
  if (!m) return "";
  const v = clientFlowEntityView(m.entityType, m.dbId);
  if (!v) return "";
  const invoiceErr = clientApplyInvoiceError(v);
  const det = uiState.clientDetail && uiState.clientDetail.key === `${m.entityType}:${m.dbId}` ? uiState.clientDetail : null;
  // 「取得中」「取得失敗」を「添付なし」と混同させない（添付済みなのに一瞬「なし」と出ると再アップロードを誘発する）
  const att = det?.attachments;
  const files = det ? clientDetailContractDocs(det) : [];
  const fileList = !att || (att.loading && !att.loaded)
    ? `<li class="fp-clapply-nofile">契約書類を読み込んでいます…</li>`
    : att.error
      ? `<li class="fp-clapply-nofile">契約書類を取得できませんでした（${escapeHtml(att.error)}）</li>`
      : files.length
        ? files.map((f) => `<li>${escapeHtml(f.filename)}（${developmentFileSizeLabel(f.sizeBytes)}）</li>`).join("")
        : `<li class="fp-clapply-nofile">添付なし（詳細ページの「契約書類」からNDA・業務委託契約書を添付できます）</li>`;
  return `
    <div class="composer-backdrop project-create-backdrop" data-clapply-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm fp-clapply-modal" role="dialog" aria-modal="true" aria-labelledby="fp-clapply-title">
      <div class="composer-head">
        <div><h3 id="fp-clapply-title">取引承認を申請 — ${escapeHtml(v.name)}</h3>
        <p class="project-create-step2-sub">申請すると申請承認ページ（マスター/経理）に「取引承認」として並び、契約一覧に「申請中」で表示されます</p></div>
        <button type="button" class="inline-btn ghost" data-clapply-close="1">閉じる</button>
      </div>
      <div class="composer-body fp-clapply-body">
        <div class="fp-clapply-section">
          <div class="fp-clapply-label">契約書類（承認者が確認できます）</div>
          <ul class="fp-clapply-files">${fileList}</ul>
        </div>
        <div class="fp-clapply-section">
          <div class="fp-clapply-label">おおよその取引予定金額（円・必須）</div>
          <input class="pj-input" data-clapply-amount type="text" inputmode="numeric" autocomplete="off" maxlength="13"
            value="${escapeHtmlAttr(m.amount || "")}" placeholder="例：500000（数値のみ）" />
        </div>
        ${clientApplyCreditFieldsHtml(m)}
        ${invoiceErr ? `<div class="fp-clapply-section"><div class="fp-clapply-invoice-warn">⚠ ${escapeHtml(invoiceErr)}</div></div>` : ""}
        <div class="fp-clapply-section">
          <div class="fp-clapply-label">承認者へのひとこと（任意）</div>
          <textarea class="pj-input pm-textarea" data-clapply-note rows="3" maxlength="2000" placeholder="例：NDAは先方書式です。反社条項は覚書で追記済み。経緯はコメント欄をご確認ください。">${escapeHtml(m.note || "")}</textarea>
        </div>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-clapply-close="1" type="button">キャンセル</button>
        <button class="inline-btn" data-clapply-submit="1" type="button"${m.submitting ? " disabled" : ""}>${m.submitting ? "申請中…" : "申請する"}</button>
      </div>
    </aside>
  `;
}

/** 取引先一覧「その他」タブに出す営業非表示の取引先（契約一覧の「その他」タブと同じ集合＝
 *  パートナーだけでなく非表示クライアントも含める。件数バッジもこの関数を使って揃える） */
function clientOthersRows() {
  const partnerRows = (uiState.partnersCache || [])
    .filter((p) => p.sales_hidden === true)
    .map((p) => ({
      entityType: "partner",
      dbId: p.id,
      name: p.name || "",
      status: normalizePartnerApprovalStatus(p.approval_status, p.nda_status, p.contract_status),
      archived: p.archived === true,
      customerNo: p.customer_no || "",
      meta: `支払条件：${p.payment_terms || "—"}`
    }));
  const clientRows = clients
    .filter((c) => c.salesHidden === true && Number.isFinite(Number(c.dbId)))
    .map((c) => ({
      entityType: "client",
      dbId: Number(c.dbId),
      name: c.name || "",
      status: normalizeMasterApprovalStatus(c.approvalStatus),
      archived: c.archived === true,
      customerNo: c.customerNo || "",
      meta: `社内担当者：${c.contractOwner || "—"}`
    }));
  return [...clientRows, ...partnerRows];
}

/** 「その他」タブの新規登録ボタン（マスター/経理のみ）。
 *  「その他」＝クライアントでもパートナーでもない取引先（家賃など販管費対象）で、
 *  登録時に自動で営業非表示になる（実体はパートナーマスタ×sales_hidden＝承認時はE採番）。 */
function clientOthersToolsHtml() {
  if (!canManageClientPartnerMaster()) return "";
  return `
    <button class="cl-btn cl-btn-primary" data-open-partner-create="hidden" type="button" title="その他の取引先（事務所家賃など）を登録します。営業の画面には表示されず、承認時はE採番になります">＋ 新規登録（その他）</button>
  `;
}

/** 取引先一覧「その他」タブ（営業非表示＝E区分。管理部のみ・一覧表示だけの置き場） */
function clientOthersBodyHtml() {
  if (!canManageClientPartnerMaster()) {
    return `<div class="cl-content"><div class="cl-board-empty">このタブはマスター／経理のみ表示できます</div></div>`;
  }
  const rows = clientOthersRows();
  const cards = rows.map((r) => `
      <article class="cl-client-card${r.archived ? " cl-client-card--archived" : ""}" data-client-flow-detail="${r.entityType}:${r.dbId}">
        <div class="cl-client-card-main">
          <div class="cl-client-card-line">
            ${clientDirectoryApprovalPillHtml(r.status, r.archived)}
            <button class="cl-client-card-name" data-client-flow-detail="${r.entityType}:${r.dbId}" type="button">${escapeHtml(r.name)}</button>
            <div class="cl-client-meta">
              <span class="cl-client-meta-item cl-client-meta-item--customer-no">顧客番号：${escapeHtml(r.customerNo || (r.status === "承認済" ? "—" : "承認時に発番"))}</span>
              <span class="cl-client-meta-item">区分：${r.entityType === "partner" ? "パートナー" : "クライアント"}</span>
              <span class="cl-client-meta-item">${escapeHtml(r.meta)}</span>
            </div>
          </div>
        </div>
        <div class="cl-client-card-actions">
          ${clientDirectoryCardActionsHtml(r.entityType, r.dbId, r.status, r.archived, true)}
        </div>
      </article>`).join("");
  return `
    <div class="cl-content cl-client-list-cards">
      <div class="fp-clothers-note">営業の画面には表示されない取引先（事務所家賃など販管費対象＝E区分）の置き場です。このタブの「＋ 新規登録（その他）」から登録すると、自動で営業非表示になります。</div>
      ${rows.length ? cards : `<div class="cl-board-empty">その他の取引先はまだありません</div>`}
    </div>
  `;
}

// ───────── 操作（独立リスナー） ─────────

async function clientFlowSetArchived(entityType, dbId, archived) {
  const url = entityType === "partner" ? "/api/partners" : "/api/clients";
  const res = await apiFetch(url, { method: "PUT", body: { id: dbId, archived } });
  if (!res.ok || res.data?.ok === false) {
    showToastError(res.data?.error || "アーカイブの更新に失敗しました");
    return false;
  }
  // ローカルへ反映（リロード無しで一覧・契約一覧から消える／戻る）
  if (entityType === "partner") {
    const p = clientFlowFindPartner(dbId);
    if (p) { p.archived = archived; upsertContractFromPartner(p); }
    // 埋め込み編集中の相手をアーカイブしたら編集は閉じる（編集フォームだけ残る宙吊りを防ぐ）
    if (archived && uiState.partnerCreate?.inline && Number(uiState.partnerCreate.id) === Number(dbId)) {
      uiState.partnerCreate = null;
    }
  } else {
    const c = clientFlowFindClient(dbId);
    if (c) { c.archived = archived; upsertContractFromClient(c); }
    if (archived && uiState.clientCreate?.inline && String(uiState.clientCreate.id) === `c${dbId}`) {
      uiState.clientCreate = null;
    }
  }
  showToast(archived ? "アーカイブしました（絞り込み「アーカイブ」で見返せます）" : "復元しました");
  render();
  return true;
}

async function clientFlowSubmitApply() {
  const m = uiState.clientApplyModal;
  if (!m || m.submitting) return;
  // 必須チェック: 取引予定金額（数値）と、パートナーのインボイス設定（サーバ側でも同じ検証をする）
  const amount = clientApplyAmountParse(m.amount);
  if (amount == null) {
    showToast("おおよその取引予定金額を数値で入力してください");
    return;
  }
  const v = clientFlowEntityView(m.entityType, m.dbId);
  const invoiceErr = v ? clientApplyInvoiceError(v) : "";
  if (invoiceErr) {
    showToast(invoiceErr);
    return;
  }
  // 資本金・設立年月（必須）と与信ルールの確認（15k-client-apply-credit.js）。確認中の二重押しも防ぐ
  m.submitting = true;
  const credit = await clientApplyCreditCheck(m);
  if (uiState.clientApplyModal !== m) return;
  if (!credit) {
    m.submitting = false;
    return;
  }
  render();
  const res = await apiFetch("/api/client-approval-apply", {
    method: "POST",
    body: {
      entityType: m.entityType, id: m.dbId, note: String(m.note || "").trim(), expectedAmount: amount,
      capitalAmount: credit.capital, establishedYm: credit.establishedYm
    }
  });
  const cur = uiState.clientApplyModal;
  if (!cur || cur !== m) return;
  m.submitting = false;
  if (!res.ok || res.data?.ok === false) {
    showToastError(res.data?.error || "申請に失敗しました");
    render();
    return;
  }
  // ローカルへ反映
  if (m.entityType === "partner") {
    const p = clientFlowFindPartner(m.dbId);
    if (p) {
      p.approval_status = "申請中";
      p.approval_reject_reason = null;
      p.expected_amount = amount;
      p.capital_amount = credit.capital;
      p.established_ym = credit.establishedYm;
      p.approval_apply_note = String(m.note || "").trim() || null;
      upsertContractFromPartner(p);
    }
  } else {
    const c = clientFlowFindClient(m.dbId);
    if (c) {
      c.approvalStatus = "申請中";
      c.approvalRejectReason = "";
      c.expectedAmount = amount;
      c.capitalAmount = credit.capital;
      c.establishedYm = credit.establishedYm;
      c.approvalApplyNote = String(m.note || "").trim();
      upsertContractFromClient(c);
    }
  }
  uiState.clientApplyModal = null;
  showToast("取引承認を申請しました。承認されると顧客番号が自動発番されます。続けて契約書類を提出してください");
  // 申請後に書類（NDA・業務委託契約書等）を提出する導線が無く気づけないという指摘（2026-09-07）を受け、
  // 詳細ページの「契約書類」タブへそのまま誘導する（一覧カードからの申請でも遷移する）
  location.hash = `#client-detail:${m.entityType}:${m.dbId}?tab=docs`;
  render();
}

/** 契約書類カテゴリのラベル（表示用・単一定義） */
const CLIENT_DOC_KIND_LABELS = { nda: "NDA", "gyomu-itaku": "業務委託基本契約書" };

/** ファイル名からカテゴリを推定（カテゴリ選択モーダルの初期値用。確定は人が選ぶ） */
function clientDocKindGuess(filename) {
  const f = String(filename || "");
  if (/nda|秘密保持/i.test(f)) return "nda";
  if (/業務委託|基本契約/i.test(f)) return "gyomu-itaku";
  return "";
}

/** ファイル添付前のカテゴリ選択モーダル（NDA/業務委託/その他）。
 *  ファイル名が整っていなくても承認時の締結状態の自動反映が効くように、添付時に人が選ぶ */
function renderClientAttachKindModal() {
  const m = uiState.clientAttachKindModal;
  if (!m) return "";
  const opt = (v, label, hint) => `
    <label class="fp-clattkind-opt${m.kind === v ? " fp-clattkind-opt--on" : ""}">
      <input type="radio" name="fp-clattkind" value="${v}" ${m.kind === v ? "checked" : ""} data-clattkind-radio="1" />
      <span><b>${label}</b>${hint ? `<span class="fp-clattkind-hint">${hint}</span>` : ""}</span>
    </label>`;
  return `
    <div class="composer-backdrop project-create-backdrop" data-clattkind-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm fp-clattkind-modal" role="dialog" aria-modal="true" aria-labelledby="fp-clattkind-title">
      <div class="composer-head">
        <div><h3 id="fp-clattkind-title">契約書類のカテゴリ</h3>
        <p class="project-create-step2-sub">取引承認が承認されると、選んだカテゴリの締結状態（NDA/基本契約）が自動で「済」になります</p></div>
        <button type="button" class="inline-btn ghost" data-clattkind-close="1">閉じる</button>
      </div>
      <div class="composer-body fp-clattkind-body">
        <ul class="fp-clapply-files">${m.files.map((f) => `<li>${escapeHtml(f.name)}</li>`).join("")}</ul>
        ${opt("nda", "NDA（秘密保持契約書）", "")}
        ${opt("gyomu-itaku", "業務委託基本契約書", "")}
        ${opt("", "その他の契約書類", "締結状態には反映しません。お知らせ等は「その他書類」タブへ")}
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-clattkind-close="1" type="button">キャンセル</button>
        <button class="inline-btn" data-clattkind-submit="1" type="button">添付する</button>
      </div>
    </aside>`;
}

async function clientFlowUploadFiles(st, files, docKind) {
  if (!files.length) return;
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  st.attachments.uploading = true;
  render();
  let okCount = 0;
  for (const file of files) {
    try {
      const extOk = CLIENT_DOC_FILE_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext));
      if (!extOk) throw new Error(`対応形式のみ: ${CLIENT_DOC_FILE_EXT_HINT}`);
      if (file.size > CLIENT_DOC_FILE_MAX_BYTES) throw new Error(`「${file.name}」は15MBを超えています`);
      const buf = await readLocalFileArrayBuffer(file);
      const bytes = new Uint8Array(buf);
      const post = async (payload) => {
        const r = await apiFetch("/api/client-attachment", { method: "POST", body: payload });
        if (!r.ok || r.data?.ok === false) throw new Error(r.data?.error || "契約書類の保存に失敗しました");
        return r.data;
      };
      if (bytes.length <= CLIENT_DOC_FILE_CHUNK_BYTES) {
        await post({ entityType: st.entityType, entityId: st.dbId, filename: file.name, fileBase64: arrayBufferToBase64(buf), docKind: docKind || "" });
      } else {
        const uploadId =
          typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
        const totalChunks = Math.ceil(bytes.length / CLIENT_DOC_FILE_CHUNK_BYTES);
        for (let i = 0; i < totalChunks; i++) {
          const part = bytes.subarray(i * CLIENT_DOC_FILE_CHUNK_BYTES, (i + 1) * CLIENT_DOC_FILE_CHUNK_BYTES);
          await post({ uploadId, chunkIndex: i, fileBase64: arrayBufferToBase64(part) });
        }
        await post({ uploadId, finalize: true, totalChunks, entityType: st.entityType, entityId: st.dbId, filename: file.name, docKind: docKind || "" });
      }
      okCount++;
    } catch (e) {
      showToast(e?.message || `「${file.name}」のアップロードに失敗しました`);
    }
  }
  st.attachments.uploading = false;
  st.attachments.loaded = false; // 一覧を取り直す
  if (okCount) showToast(`${okCount}件のファイルを添付しました`);
  render();
}

async function clientFlowDownloadAttachment(st, attId) {
  const id = Number(attId);
  if (!Number.isFinite(id) || id < 1) return;
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  try {
    // 認証ヘッダが要るので <a href> 直リンクにはできない（apiFetch は JSON 前提のためバイナリDLは生 fetch。
    // 15a-development の downloadDevelopmentDocFile と同じ作法）。取得は AIチェックと共通の1本
    const { buffer, meta } = await clientFlowFetchAttachmentBuffer(st, id);
    const filename = String(meta.filename || "download");
    const type = String(meta.mimeType || "") || "application/octet-stream";
    const blob = new Blob([buffer], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) {
    showToast(e?.message || "ファイルの取得に失敗しました");
  }
}

function clientDetailContext() {
  const r = currentRoute();
  if (r.page !== "client-detail") return null;
  const target = clientFlowParseTarget(`${r.entityType}:${r.id}`);
  if (!target) return null;
  return ensureClientDetailState(target.entityType, target.dbId);
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  // 埋め込み編集の起動: 全画面 render を走らせず（ちらつき防止）、状態を silent で作って
  // ヘッダー操作＋タブ本体だけを差し替える。後続の既存編集ハンドラ（16-render-routing）は止める
  const inlineEditBtn = target.closest("[data-client-edit-inline],[data-partner-edit-inline]");
  if (inlineEditBtn instanceof HTMLElement) {
    e.stopImmediatePropagation();
    const r = currentRoute();
    if (r.page === "client-detail" && r.detailTab && r.detailTab !== "info") {
      // フォームは基本情報タブに描くため、契約書類/コメントタブからは基本情報へ寄せる（履歴は汚さない）
      history.replaceState(null, "", `${location.pathname}${location.search}#client-detail:${r.entityType}:${r.id}`);
    }
    if (inlineEditBtn.dataset.clientEditInline === "1") {
      const [, cid] = String(inlineEditBtn.dataset.clientAction || "").split(":");
      openClientModal({ mode: "edit", clientId: cid, fromProject: false, centered: true, inline: true, silent: true });
    } else {
      openPartnerEditModal(Number(inlineEditBtn.dataset.partnerEdit), { inline: true, silent: true });
    }
    clientDetailPartialRefresh();
    return;
  }

  // 埋め込み編集のキャンセル: 同じく部分更新で表示モードへ戻す（全画面 render のちらつき回避）
  const inlineCloseBtn = target.closest("[data-close-client-create],[data-close-partner]");
  if (inlineCloseBtn instanceof HTMLElement && currentRoute().page === "client-detail") {
    const isClientClose = inlineCloseBtn.hasAttribute("data-close-client-create");
    if (isClientClose ? uiState.clientCreate?.inline : uiState.partnerCreate?.inline) {
      e.stopImmediatePropagation();
      if (isClientClose) uiState.clientCreate = null;
      else uiState.partnerCreate = null;
      resetCorporateLocalSearch();
      clientDetailPartialRefresh();
      return;
    }
  }

  // 申請モーダルを開く（一覧カード・詳細ページ共通）
  const applyOpenEl = target.closest("[data-client-flow-apply-open]");
  if (applyOpenEl instanceof HTMLElement) {
    const t = clientFlowParseTarget(applyOpenEl.dataset.clientFlowApplyOpen);
    if (t) {
      const v = clientFlowEntityView(t.entityType, t.dbId);
      uiState.clientApplyModal = {
        entityType: t.entityType,
        dbId: t.dbId,
        note: "",
        // 取引予定金額（必須）。再申請では前回の値を初期表示する
        amount: v?.expectedAmount != null ? String(v.expectedAmount) : "",
        // 資本金・設立年月（必須）。前回の値があれば初期表示する
        capital: v?.capitalAmount != null ? String(v.capitalAmount) : "",
        established: v?.establishedYm || "",
        submitting: false
      };
      // 添付一覧をモーダルに出すため、詳細キャッシュを対象取引先に合わせて先読み
      const st = ensureClientDetailState(t.entityType, t.dbId);
      if (!st.attachments.loaded && !st.attachments.loading) void loadClientDetailAttachments(st);
      render();
    }
    return;
  }
  if (target.closest("[data-clapply-close]")) {
    if (uiState.clientApplyModal) {
      uiState.clientApplyModal = null;
      render();
    }
    return;
  }
  // 契約書類カテゴリ選択モーダル（閉じる=添付せず破棄 / 添付する=選んだカテゴリでアップロード）
  if (target.closest("[data-clattkind-close]")) {
    if (uiState.clientAttachKindModal) {
      uiState.clientAttachKindModal = null;
      render();
    }
    return;
  }
  if (target.closest("[data-clattkind-submit]")) {
    const m = uiState.clientAttachKindModal;
    const stKind = clientDetailContext();
    uiState.clientAttachKindModal = null;
    if (m && stKind) void clientFlowUploadFiles(stKind, m.files, m.kind);
    else render();
    return;
  }
  if (target.closest("[data-clapply-submit]")) {
    void clientFlowSubmitApply();
    return;
  }

  // アーカイブ／復元
  const archiveEl = target.closest("[data-client-flow-archive]");
  if (archiveEl instanceof HTMLElement) {
    const t = clientFlowParseTarget(archiveEl.dataset.clientFlowArchive);
    const value = archiveEl.dataset.clientFlowArchiveValue === "true";
    if (t) {
      if (value && !window.confirm("この取引先をアーカイブします。よろしいですか？（絞り込み「アーカイブ」から復元できます）")) return;
      void clientFlowSetArchived(t.entityType, t.dbId, value);
    }
    return;
  }

  // Word出力（表示ビューのヘッダー）。編集フォームを開かず保存済みデータから差し込む
  const exportDocEl = target.closest("[data-client-flow-export-doc]");
  if (exportDocEl instanceof HTMLElement) {
    const [entityType, idRaw, kind] = String(exportDocEl.dataset.clientFlowExportDoc || "").split(":");
    if (kind !== "nda" && kind !== "gyomu-itaku") return;
    let override = null;
    let mode;
    if (entityType === "partner") {
      const p = clientFlowFindPartner(idRaw);
      if (p) { override = { name: p.name, ndaPurpose: p.nda_purpose || "" }; mode = "partner"; }
    } else {
      const c = clientFlowFindClient(idRaw);
      if (c) override = { name: c.name, shortName: c.shortName, ndaPurpose: c.ndaPurpose || "" };
    }
    if (override) {
      void downloadFilledClientContractDocx(kind, mode, override).catch((e) => {
        console.error(e);
        showToastError("出力に失敗しました");
      });
    }
    return;
  }

  // 詳細ページへ（カード全体・名前ボタン）。ボタン類より後に判定する
  const detailEl = target.closest("[data-client-flow-detail]");
  if (detailEl instanceof HTMLElement) {
    const t = clientFlowParseTarget(detailEl.dataset.clientFlowDetail);
    if (t) {
      // 申請承認の詳細ドロワーから飛ぶ場合はドロワーを閉じてから遷移（開いたまま裏でページだけ変わるのを防ぐ）
      uiState.appMgmtDetailModal = null;
      location.hash = `#client-detail:${t.entityType}:${t.dbId}`;
    }
    return;
  }

  // ── 詳細ページ内の操作 ──
  // アプリ全体のクリックがここまで来るので、対象要素でないときは currentRoute() 解析より先に抜ける
  const detailOp = target.closest(
    "[data-clatt-download],[data-clatt-delete],[data-clatt-reject],[data-clatt-unreject],[data-clatt-aicheck],[data-aicheck-run],[data-aicheck-close],[data-aicheck-to-comment],[data-aihist-toggle],[data-ccm-post],[data-ccm-edit],[data-ccm-edit-cancel],[data-ccm-edit-save],[data-ccm-delete]"
  );
  if (!detailOp) return;
  const st = clientDetailContext();
  if (!st) return;

  const dlEl = target.closest("[data-clatt-download]");
  if (dlEl instanceof HTMLElement) {
    void clientFlowDownloadAttachment(st, dlEl.dataset.clattDownload);
    return;
  }
  const rejectEl = target.closest("[data-clatt-reject]");
  if (rejectEl instanceof HTMLElement) {
    const id = Number(rejectEl.dataset.clattReject);
    const reason = String(window.prompt("却下理由を入力してください（提出者にSlackで通知されます）") || "").trim();
    if (!Number.isFinite(id) || !reason) return;
    void (async () => {
      const res = await apiFetch("/api/client-attachment", { method: "PUT", body: { id, action: "reject", reason } });
      if (!res.ok || res.data?.ok === false) {
        showToastError(res.data?.error || "却下に失敗しました");
        return;
      }
      const f = st.attachments.items.find((x) => Number(x.id) === id);
      if (f) { f.rejected = true; f.rejectReason = reason; f.rejectedByName = currentUser?.name || ""; }
      showToast("却下しました");
      render();
    })();
    return;
  }
  const unrejectEl = target.closest("[data-clatt-unreject]");
  if (unrejectEl instanceof HTMLElement) {
    const id = Number(unrejectEl.dataset.clattUnreject);
    if (!Number.isFinite(id)) return;
    void (async () => {
      const res = await apiFetch("/api/client-attachment", { method: "PUT", body: { id, action: "unreject" } });
      if (!res.ok || res.data?.ok === false) {
        showToastError(res.data?.error || "却下の取消に失敗しました");
        return;
      }
      const f = st.attachments.items.find((x) => Number(x.id) === id);
      if (f) { f.rejected = false; f.rejectReason = ""; f.rejectedByName = ""; }
      showToast("却下を取り消しました");
      render();
    })();
    return;
  }
  const aiOpenEl = target.closest("[data-clatt-aicheck]");
  if (aiOpenEl instanceof HTMLElement) {
    const id = Number(aiOpenEl.dataset.clattAicheck);
    const f = st.attachments.items.find((x) => String(x.id) === String(id));
    if (!f) return;
    // 添付時に選んだカテゴリを最優先、未分類はファイル名から推定（NDA/秘密保持→NDA、それ以外は業務委託）
    const guessKind =
      f.docKind === "nda" || f.docKind === "gyomu-itaku"
        ? f.docKind
        : /nda|秘密保持/i.test(String(f.filename)) ? "nda" : "gyomu-itaku";
    st.aiCheck = { attachmentId: id, filename: f.filename, templateKind: guessKind, running: false, result: null, error: "" };
    render();
    return;
  }
  if (target.closest("[data-aicheck-run]")) {
    void clientFlowRunAiCheck(st);
    return;
  }
  if (target.closest("[data-aicheck-close]")) {
    st.aiCheck = null;
    render();
    return;
  }
  const aihistEl = target.closest("[data-aihist-toggle]");
  if (aihistEl instanceof HTMLElement) {
    const id = aihistEl.dataset.aihistToggle;
    st.aiHistory.openId = st.aiHistory.openId != null && String(st.aiHistory.openId) === String(id) ? null : id;
    render();
    return;
  }
  if (target.closest("[data-aicheck-to-comment]")) {
    if (st.aiCheck?.result) {
      const text = clientAiCheckResultToText(st.aiCheck);
      st.comments.draft = st.comments.draft ? `${st.comments.draft}\n${text}` : text;
      render();
      // render は rAF 遅延＝直後の focus は破棄される旧DOMに当たるため、描画後のフレームで当てる
      requestAnimationFrame(() => requestAnimationFrame(() => document.querySelector("[data-ccm-draft]")?.focus()));
    }
    return;
  }
  const delEl = target.closest("[data-clatt-delete]");
  if (delEl instanceof HTMLElement) {
    const id = Number(delEl.dataset.clattDelete);
    if (!Number.isFinite(id) || !window.confirm("このファイルを削除します。よろしいですか？")) return;
    void (async () => {
      const res = await apiFetch("/api/client-attachment", { method: "DELETE", body: { id } });
      if (!res.ok || res.data?.ok === false) {
        showToastError(res.data?.error || "削除に失敗しました");
        return;
      }
      st.attachments.items = st.attachments.items.filter((f) => String(f.id) !== String(id));
      // 消したファイルのAIチェックパネルは残さない（「再実行」が404になる宙吊りを防ぐ）
      if (st.aiCheck && String(st.aiCheck.attachmentId) === String(id)) st.aiCheck = null;
      render();
    })();
    return;
  }

  if (target.closest("[data-ccm-post]")) {
    const body = String(st.comments.draft || "").trim();
    if (!body || st.comments.submitting) return;
    st.comments.submitting = true;
    render();
    void (async () => {
      const res = await apiFetch("/api/client-comments", {
        method: "POST",
        body: { entityType: st.entityType, entityId: st.dbId, body }
      });
      st.comments.submitting = false;
      if (res.ok && res.data?.ok && res.data.comment) {
        st.comments.items.push(res.data.comment);
        st.comments.draft = "";
      } else {
        showToastError(res.data?.error || "コメントの投稿に失敗しました");
      }
      render();
    })();
    return;
  }
  const editEl = target.closest("[data-ccm-edit]");
  if (editEl instanceof HTMLElement) {
    const id = editEl.dataset.ccmEdit;
    const item = st.comments.items.find((c) => String(c.id) === String(id));
    st.comments.editingId = id;
    st.comments.editDraft = item?.body || "";
    render();
    return;
  }
  if (target.closest("[data-ccm-edit-cancel]")) {
    st.comments.editingId = null;
    st.comments.editDraft = "";
    render();
    return;
  }
  const saveEl = target.closest("[data-ccm-edit-save]");
  if (saveEl instanceof HTMLElement) {
    const id = Number(saveEl.dataset.ccmEditSave);
    const body = String(st.comments.editDraft || "").trim();
    if (!body || st.comments.submitting) return;
    st.comments.submitting = true;
    render();
    void (async () => {
      const res = await apiFetch("/api/client-comments", { method: "PUT", body: { id, body } });
      st.comments.submitting = false;
      if (res.ok && res.data?.ok && res.data.comment) {
        const idx = st.comments.items.findIndex((c) => String(c.id) === String(id));
        if (idx >= 0) st.comments.items[idx] = res.data.comment;
        st.comments.editingId = null;
        st.comments.editDraft = "";
      } else {
        showToastError(res.data?.error || "コメントの編集に失敗しました");
      }
      render();
    })();
    return;
  }
  const cDelEl = target.closest("[data-ccm-delete]");
  if (cDelEl instanceof HTMLElement) {
    const id = Number(cDelEl.dataset.ccmDelete);
    if (!window.confirm("このコメントを削除します。よろしいですか？")) return;
    void (async () => {
      const res = await apiFetch("/api/client-comments", { method: "DELETE", body: { id } });
      if (res.ok && res.data?.ok) {
        st.comments.items = st.comments.items.filter((c) => String(c.id) !== String(id));
        render();
      } else {
        showToastError(res.data?.error || "コメントの削除に失敗しました");
      }
    })();
    return;
  }
});

// 入力（再描画せず state だけ更新＝IME変換中の文字が消えないように。投稿ボタンの活性だけ触る）
document.addEventListener("input", (e) => {
  const t = e.target;
  // 取引予定金額: 数値以外は入力させない（全角数字は半角へ、その他の文字は除去）。
  // IME変換中に value を書き換えると変換範囲が壊れて数字が欠ける／重複するため、確定後の input で正規化する
  if (t instanceof HTMLInputElement && t.matches("[data-clapply-amount]")) {
    if (e.isComposing) return;
    const digits = t.value
      .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
      .replace(/[^\d]/g, "");
    if (t.value !== digits) t.value = digits;
    if (uiState.clientApplyModal) uiState.clientApplyModal.amount = digits;
    return;
  }
  if (!(t instanceof HTMLTextAreaElement)) return;
  if (t.matches("[data-clapply-note]")) {
    if (uiState.clientApplyModal) uiState.clientApplyModal.note = t.value;
    return;
  }
  // アプリ内すべての textarea の打鍵がここへ来るので、対象でなければ currentRoute() 解析より先に抜ける
  if (!t.matches("[data-ccm-draft],[data-ccm-edit-body]")) return;
  const st = clientDetailContext();
  if (!st) return;
  if (t.matches("[data-ccm-draft]")) {
    const wasEmpty = !String(st.comments.draft || "").trim();
    st.comments.draft = t.value;
    const nowEmpty = !t.value.trim();
    if (wasEmpty !== nowEmpty) {
      const btn = document.querySelector("[data-ccm-post]");
      if (btn instanceof HTMLButtonElement) btn.disabled = nowEmpty || st.comments.submitting;
    }
  } else if (t.matches("[data-ccm-edit-body]")) {
    st.comments.editDraft = t.value;
  }
});

// ファイル選択（詳細ページの「＋ ファイル追加」）
document.addEventListener("change", (e) => {
  const t = e.target;
  if (t instanceof HTMLSelectElement && t.matches("[data-aicheck-kind]")) {
    const st = clientDetailContext();
    if (st?.aiCheck) {
      st.aiCheck.templateKind = t.value === "gyomu-itaku" ? "gyomu-itaku" : "nda";
      // 基準を変えたら前の結果は無効（残すと別基準のラベルで古い指摘をコメントに貼れてしまう）
      st.aiCheck.result = null;
      st.aiCheck.error = "";
      render();
    }
    return;
  }
  // カテゴリ選択モーダルのラジオ
  if (t instanceof HTMLInputElement && t.matches("[data-clattkind-radio]")) {
    if (uiState.clientAttachKindModal) uiState.clientAttachKindModal.kind = t.value;
    return;
  }
  // 添付済みファイルのカテゴリ変更（PUTで保存し、ローカルにも反映）
  if (t instanceof HTMLSelectElement && t.matches("[data-clatt-kind]")) {
    const st2 = clientDetailContext();
    if (!st2) return;
    const id = Number(t.dataset.clattKind);
    const docKind = t.value;
    void apiFetch("/api/client-attachment", { method: "PUT", body: { id, docKind } }).then((r) => {
      if (uiState.clientDetail !== st2) return;
      if (r.ok && r.data?.ok) {
        const f = st2.attachments.items.find((x) => Number(x.id) === id);
        if (f) f.docKind = docKind;
        // その他書類へ移した書類のAIチェック欄は残さない（契約書類タブに対象外の結果が宙吊りになる）
        if (docKind === "other" && st2.aiCheck && Number(st2.aiCheck.attachmentId) === id) st2.aiCheck = null;
        showToast(docKind === "other" ? "その他書類へ移動しました" : "カテゴリを変更しました");
        render(); // 契約書類⇔その他書類の移動はタブの中身・件数が変わる
      } else {
        showToastError(r.data?.error || "カテゴリの変更に失敗しました");
        render(); // 失敗時は表示を実状態へ戻す
      }
    });
    return;
  }
  if (!(t instanceof HTMLInputElement) || !t.matches("[data-clatt-file-input]")) return;
  const st = clientDetailContext();
  if (!st) return;
  // value="" は FileList を空にし得るため、必ず先にスナップショットを取る（15a-development と同じ作法）
  const files = Array.from(t.files || []);
  const isOtherDocs = t.dataset.clattFileInput === "other";
  t.value = "";
  if (!files.length) return;
  // その他書類（お知らせ等）は締結状態に関係しないのでカテゴリ選択を挟まず添付する
  if (isOtherDocs) {
    void clientFlowUploadFiles(st, files, "other");
    return;
  }
  // 即アップロードせず、カテゴリ（NDA/業務委託/その他）を選んでから添付する（初期値はファイル名から推定）
  uiState.clientAttachKindModal = { files, kind: clientDocKindGuess(files[0].name) };
  render();
});
// ── 取引先一覧: 契約一覧の統合で増えた検索項目・マスター/経理向けの操作（2026-10-28 要望） ──
// 契約一覧（#contract-management）は取引先一覧へ集約した（メニューは廃止・開くと取引先一覧へ移る）。
// - カードの表示は今までどおり（住所・電話などの2行目は表示が崩れるので出さない。2026-10-28 ユーザー指摘）
// - マスター・経理（canManageClientPartnerMaster）だけ: 「マスター・経理のみ」の取引先（sales_hidden）を
//   グレーのカードで出す（切り替えは取引先詳細のボタン）、CSVダウンロード、削除済みの表示と復元
// - 検索欄は固定項目編集（15z-list-filter-fields.js）で人ごとに出す項目と並び順を選ぶ。増えた項目は最初は隠しておく
// イベントは独立リスナー（16 の巨大 click ハンドラには足さない）。

/** マスター・経理向けの操作を出すか。サーバは「マスター・経理のみ」の取引先の閲覧・切り替えを契約管理
 *  （contract-management）権限で許すので、それを持たない経理には出さない（押しても黙って無視されるため） */
function clientListManagerOnly() {
  return canManageClientPartnerMaster()
    && (isMasterOrAdminUser(currentUser) || normalizeNavPermissions(currentUser?.navPermissions || []).includes("contract-management"));
}

/** クライアント・パートナーで共通の追加検索項目（契約一覧から移したもの） */
function clientListExtraFilterFields(contractLabel) {
  return [
    { key: "nda", label: "NDA", clear: { nda: "all" } },
    { key: "contract", label: contractLabel, clear: { contract: "all" } },
    { key: "send", label: "書類送付方法", clear: { send: "" }, defaultHidden: true },
    { key: "qualified", label: "適格請求書発行事業者", clear: { qualified: "" }, defaultHidden: true },
    { key: "paymentTerms", label: "支払条件", clear: { paymentTerms: "" }, defaultHidden: true },
    { key: "prefecture", label: "都道府県", clear: { prefecture: "" }, defaultHidden: true },
    { key: "hidden", label: "表示範囲", clear: { hidden: "" }, defaultHidden: true, available: clientListManagerOnly },
    { key: "deleted", label: "削除済み", clear: { deleted: "" }, defaultHidden: true, available: clientListManagerOnly }
  ];
}

const CLIENT_LIST_FILTER_SPEC = {
  id: "client-list-filter",
  pages: ["client-list"],
  fields: [
    { key: "keyword", label: "キーワード検索", clear: { keyword: "" } },
    { key: "approval", label: "申請状況", clear: { approval: "" } },
    { key: "contractOwner", label: "社内担当者", clear: { contractOwner: "" } },
    ...clientListExtraFilterFields("基本契約"),
    { key: "sort", label: "並び順", clear: { sort: "" } }
  ],
  onChange: () => { uiState.clientList.page = 1; }
};

const PARTNER_LIST_FILTER_SPEC = {
  id: "partner-list-filter",
  pages: ["partner-list"],
  fields: [
    { key: "keyword", label: "キーワード検索", clear: { keyword: "" } },
    { key: "paymentMethod", label: "支払方法", clear: { paymentMethod: "all" } },
    { key: "approval", label: "申請状況", clear: { approval: "" } },
    ...clientListExtraFilterFields("業務委託"),
    { key: "sort", label: "並び順", clear: { sort: "" } }
  ],
  onChange: () => { uiState.partnerList.page = 1; }
};

/** 検索条件の初期値（リセットもこれに戻す） */
function clientListDefaultFilters() {
  return { keyword: "", approval: "", contractOwner: "", nda: "all", contract: "all", send: "", qualified: "", paymentTerms: "", prefecture: "", hidden: "", deleted: "", sort: "" };
}

function partnerListDefaultFilters() {
  return { keyword: "", approval: "", paymentMethod: "all", nda: "all", contract: "all", send: "", qualified: "", paymentTerms: "", prefecture: "", hidden: "", deleted: "", sort: "" };
}

/** 検索欄のプルダウン1つ。options は [値, 表示] の配列 */
function clientListFilterSelectHtml(attr, key, label, value, options) {
  const opts = options.map(([v, t]) => `<option value="${escapeHtmlAttr(v)}" ${String(value ?? "") === v ? "selected" : ""}>${escapeHtml(t)}</option>`).join("");
  return `<label class="cl-filter-group cl-client-approval-group"><span>${escapeHtml(label)}</span><select class="cl-filter-input" data-search-dropdown-off="1" ${attr}="${key}">${opts}</select></label>`;
}

/** 追加検索項目の部品（キー → HTML）。attr は data-client-list-filter / data-partner-list-filter */
function clientListExtraFilterFieldsHtml(attr, f, contractLabel) {
  const sel = (key, label, options) => clientListFilterSelectHtml(attr, key, label, f[key], options);
  const doneOpts = [["all", "すべて"], ["済", "済"], ["未", "未"]];
  return {
    nda: sel("nda", "NDA", doneOpts),
    contract: sel("contract", contractLabel, doneOpts),
    send: sel("send", "書類送付方法", [["", "すべて"], ...CONTRACT_DELIVERY_METHOD_PARTS.map((v) => [v, v])]),
    qualified: sel("qualified", "適格請求書発行事業者", [["", "すべて"], ["有", "該当する"], ["無", "該当しない・未設定"]]),
    paymentTerms: sel("paymentTerms", "支払条件", [["", "すべて"], ...PAYMENT_TERMS_OPTIONS.map((v) => [v, v])]),
    prefecture: sel("prefecture", "都道府県", [["", "すべて"], ...JAPAN_PREFECTURE_NAMES.map((v) => [v, v])]),
    hidden: sel("hidden", "表示範囲", [["", "すべて"], ["visible", "全員に表示"], ["hidden", "マスター・経理のみ"]]),
    deleted: sel("deleted", "削除済み", [["", "表示しない"], ["show", "表示する"]])
  };
}

/** 追加検索項目での絞り込み（クライアント・パートナー共通）。row は両者の値をそろえたもの */
function clientListExtraFilterMatch(f, row) {
  if (f.nda && f.nda !== "all" && (f.nda === "済") !== row.ndaOk) return false;
  if (f.contract && f.contract !== "all" && (f.contract === "済") !== row.contractOk) return false;
  if (f.send && !parseDeliveryMethodParts(normalizeDeliveryMethodStored(row.deliveryMethod || "")).includes(f.send)) return false;
  if (f.qualified && (f.qualified === "有") !== (row.invoiceIssuer === "該当する")) return false;
  if (f.paymentTerms && String(row.paymentTerms || "").trim() !== f.paymentTerms) return false;
  if (f.prefecture && String(row.prefecture || "").trim() !== f.prefecture) return false;
  if (f.hidden && (f.hidden === "hidden") !== row.salesHidden) return false;
  return true;
}

/** マスター・経理のみの取引先の目印（名前の横） */
function clientListLimitedBadgeHtml(salesHidden) {
  return salesHidden ? `<span class="cl-client-limited-badge" title="マスター・経理以外の人には表示されません（ダッシュボード検索・案件選択・Slack も同じ）">マスター・経理のみ</span>` : "";
}

/** 表示範囲の切り替えボタン（マスター・経理のみ）。取引先詳細のヘッダーのボタン列に置く */
function clientListLimitedToggleHtml(entityType, dbId, salesHidden) {
  if (!clientListManagerOnly()) return "";
  return `<button class="cl-btn cl-btn-outline" data-client-list-limited="${escapeHtmlAttr(`${entityType}:${dbId}`)}" data-client-list-limited-value="${salesHidden ? "false" : "true"}" type="button" title="${salesHidden ? "全員に表示します" : "マスター・経理以外の人に表示しないようにします"}">${salesHidden ? "全員に表示" : "非表示にする"}</button>`;
}

/** 編集フォームの「NDA締結」「基本契約書締結」の行。契約一覧の編集画面にあった手動の切り替えをここへ移した:
 *  マスター・経理の編集時だけ選べる（新規登録は「未」固定・それ以外の人は表示のみ）。attr は data-client-field 等 */
function clientFormDocStatusRowHtml(attr, key, label, value, editable) {
  const v = String(value || "").trim() === "済" ? "済" : "未";
  if (!editable) return `<div class="client-form-row"><span>${label}</span><strong>${v}</strong></div>`;
  return `<div class="client-form-row"><span>${label}</span><select data-search-dropdown-off="1" ${attr}="${key}">${["未", "済"].map((o) => `<option value="${o}" ${v === o ? "selected" : ""}>${o}</option>`).join("")}</select></div>`;
}

/** 削除済み（論理削除）の取引先。「削除済み：表示する」のときだけ一覧の末尾に出す（マスター・経理のみ） */
function clientListDeletedRows(entityType, f) {
  if (!clientListManagerOnly() || f.deleted !== "show" || !Array.isArray(uiState.deletedContractRows)) return [];
  const kw = String(f.keyword || "").toLowerCase();
  return uiState.deletedContractRows.filter((r) => r.entityType === entityType && (!kw || String(r.name || "").toLowerCase().includes(kw)));
}

function clientListDeletedCardsHtml(entityType, f) {
  if (clientListManagerOnly() && f.deleted === "show" && uiState.deletedContractRowsLoading) {
    return `<div class="cl-board-empty">削除済みの取引先を読み込んでいます…</div>`;
  }
  return clientListDeletedRows(entityType, f).map((r) => {
    const key = `${r.entityType}:${r.sourceEntityId}`;
    const restoring = uiState.contractRestoringId === key;
    return `
      <article class="cl-client-card cl-client-card--deleted">
        <div class="cl-client-card-main">
          <div class="cl-client-card-line">
            <span class="cl-client-approval-pill cl-client-approval-pill--arch"><span class="cl-client-approval-dot cl-client-approval-dot--arch" aria-hidden="true"></span><span class="cl-client-approval-text">削除済み</span></span>
            <span class="cl-client-card-name">${escapeHtml(r.name)}</span>
          </div>
        </div>
        <div class="cl-client-card-actions">
          <button class="cl-client-card-edit" data-client-list-restore="${escapeHtmlAttr(key)}" type="button" ${restoring ? "disabled" : ""}>${restoring ? "復元中…" : "復元"}</button>
        </div>
      </article>`;
  }).join("");
}

/** CSVダウンロード（マスター・経理のみ）。いま一覧に出ている行を、契約一覧と同じ列で出す */
function clientListCsvButtonHtml(entityType) {
  if (!clientListManagerOnly()) return "";
  return `<button class="cl-btn cl-btn-outline" data-client-list-csv="${entityType}" type="button" title="いま一覧に出ている取引先をCSVで保存します">CSVダウンロード</button>`;
}

function clientListDownloadCsv(entityType) {
  const ids = new Set(entityType === "partner" ? partnerListRows().map((p) => `partner-${p.id}`) : clientListRows().map((c) => c.id));
  const csv = contractCsvText(contractMaster.filter((r) => ids.has(r.id)));
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `取引先一覧_${entityType === "partner" ? "パートナー" : "クライアント"}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** 表示範囲（マスター・経理のみ／全員）を切り替える。PUT は部分更新（salesHidden だけ送る） */
async function clientListSetLimited(entityType, dbId, hidden) {
  const isPartner = entityType === "partner";
  const row = isPartner ? clientFlowFindPartner(dbId) : clientFlowFindClient(dbId);
  const name = String(row?.name || "").trim() || "この取引先";
  const ok = await fpConfirm({
    title: hidden ? `${name} を非表示にしますか？` : `${name} を全員に表示しますか？`,
    message: hidden ? "マスター・経理以外の人の一覧・ダッシュボード検索・案件選択・Slack に出なくなります。" : "全員の一覧・ダッシュボード検索・案件選択・Slack に出るようになります。",
    note: "あとから切り替えて戻せます。",
    confirmLabel: hidden ? "非表示にする" : "全員に表示する",
    danger: false
  });
  if (!ok) return;
  const res = await apiFetch(isPartner ? "/api/partners" : "/api/clients", { method: "PUT", body: { id: dbId, salesHidden: hidden } });
  if (!res.ok || res.data?.ok === false) {
    showToastError(res.data?.error || "表示範囲の変更に失敗しました");
    return;
  }
  if (row && isPartner) { row.sales_hidden = hidden; upsertContractFromPartner(row); }
  if (row && !isPartner) { row.salesHidden = hidden; upsertContractFromClient(row); }
  showToast(hidden ? "マスター・経理のみの表示にしました" : "全員に表示するようにしました");
  render();
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const limitedEl = t.closest("[data-client-list-limited]");
  if (limitedEl) {
    if (!clientListManagerOnly()) return;
    const [entityType, id] = String(limitedEl.dataset.clientListLimited || "").split(":");
    if (entityType && id) void clientListSetLimited(entityType, Number(id), limitedEl.dataset.clientListLimitedValue === "true");
    return;
  }
  const restoreEl = t.closest("[data-client-list-restore]");
  if (restoreEl) {
    if (!clientListManagerOnly()) return;
    const [entityType, id] = String(restoreEl.dataset.clientListRestore || "").split(":");
    if (entityType && id) void restoreContractRow(entityType, id);
    return;
  }
  const csvEl = t.closest("[data-client-list-csv]");
  if (csvEl && clientListManagerOnly()) clientListDownloadCsv(csvEl.dataset.clientListCsv === "partner" ? "partner" : "client");
}, true);

// 「削除済み：表示する」を選んだら、削除済みの取引先をまだ取っていなければ取りに行く
document.addEventListener("change", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLSelectElement) || el.value !== "show") return;
  if (el.dataset.clientListFilter !== "deleted" && el.dataset.partnerListFilter !== "deleted") return;
  if (clientListManagerOnly() && uiState.deletedContractRows === null) void fetchDeletedContractRows();
});
// 発注・受注の証跡（2026-10-30 要望・サーバ api/order-evidence.js）
// - 原価（制作費・開発費の外注）: 発注申請が承認されたあと、発注の証跡（依頼メールのスクショ等）を添付すると「登録完了」。
//   期限は計上月の翌月第5営業日（00-shared-enums.js orderEvidenceDueDate）。月次ロック後も添付できる。
//   証跡待ちは担当者のダッシュボードに出し続け、月次ロックの前に経理へ一覧で知らせる（ロックは止めない）
// - 案件: 受注の証跡（クライアントの発注書・注文メールのスクショ）を添付する
// - 取引先詳細の「発注書」タブ: パートナーは発注の証跡、クライアントは受注の証跡を件名・日付で探す
// 呼び出し: 原価のバッジ（15z-cost-order-applications.js）／案件詳細（10-project-detail.js）／取引先詳細（15k-client-directory-flow.js）／
//          ダッシュボード（07-pdf-output.js）／月次ロック（09-log-labels-misc.js）

const ORDER_EVIDENCE_MAX_BYTES = 3 * 1024 * 1024; // api/order-evidence.js MAX_BYTES と同じ
const ORDER_EVIDENCE_ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.eml,.msg,.doc,.docx,.xls,.xlsx";

function orderEvidenceState() {
  if (!uiState.orderEvidence) uiState.orderEvidence = { counts: null, countsFailed: false, pendingMine: null, tab: null };
  return uiState.orderEvidence;
}

/** 原価の DB id（画面の id は 'cost-123'） */
function orderEvidenceCostDbId(c) {
  const n = Number(String(c?.id || "").replace(/^cost-/, ""));
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function orderEvidenceTodayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 期限 'YYYY-MM-DD' → 表示（あと◯日／期限切れ） */
function orderEvidenceDueLabel(due) {
  if (!due) return { text: "証跡待ち", overdue: false };
  const days = Math.round((Date.parse(`${due}T00:00:00`) - Date.parse(`${orderEvidenceTodayYmd()}T00:00:00`)) / 86400000);
  const md = `${Number(due.slice(5, 7))}/${Number(due.slice(8, 10))}`;
  return days < 0
    ? { text: `証跡 期限切れ（${md}）`, overdue: true }
    : { text: `証跡待ち（${md}まで・あと${days}日）`, overdue: false };
}

/** 証跡の件数（原価 id → 件数）を1回だけ読む。読めなければバッジを出さない */
function ensureOrderEvidenceCountsLoaded() {
  const st = orderEvidenceState();
  if (st.counts || st.countsFailed) return;
  fetchOnceInFlight("order-evidence-counts", async () => {
    const { ok, data } = await apiFetch("/api/order-evidence?summary=cost");
    if (ok) {
      st.counts = data.counts || {};
      st.targets = new Set((data.targets || []).map(Number));
    } else {
      st.countsFailed = true;
    }
    render();
  });
}

/** 発注承認済みの原価の証跡バッジ（登録完了／証跡待ち／期限切れ）。押すと証跡の画面を開く */
function orderEvidenceBadgeHtml(c) {
  const id = orderEvidenceCostDbId(c);
  const st = orderEvidenceState();
  if (!id) return "";
  ensureOrderEvidenceCountsLoaded();
  if (!st.counts) return "";
  const n = Number(st.counts[id]) || 0;
  const title = [c.note, c.vendor].map((s) => String(s || "").trim()).filter(Boolean).join(" / ");
  const attrs = `type="button" data-order-evidence-cost="${id}" data-order-evidence-title="${escapeHtmlAttr(title)}"`;
  if (n > 0) return `<button ${attrs} class="cost-order-badge is-approved" title="発注の証跡 ${n}件（押すと確認・追加できます）">登録完了</button>`;
  // 2026-11-01 より前に承認された原価は証跡の対象外（ORDER_EVIDENCE_RULE_START）
  if (!st.targets?.has(id)) return "";
  const label = orderEvidenceDueLabel(orderEvidenceDueDate(c.targetMonth));
  return `<button ${attrs} class="cost-order-badge ${label.overdue ? "is-rejected" : "is-pending"}" title="外注先に頼んだ証拠（依頼メールのスクショなど）を添付すると登録完了になります">${escapeHtml(label.text)}</button>`;
}

/** 案件詳細の「受注の証跡」ボタン */
function orderEvidenceProjectButtonHtml(p) {
  if (!p?.dbId) return "";
  return `<button type="button" class="inline-btn ghost" data-order-evidence-project="${escapeHtmlAttr(String(p.dbId))}" data-order-evidence-title="${escapeHtmlAttr(p.name || "")}">受注の証跡（発注書）</button>`;
}

// ── 証跡の画面（原価・案件で共通） ──

function orderEvidenceOverlay() {
  return document.getElementById("orderEvidenceOverlay");
}

function closeOrderEvidenceModal() {
  orderEvidenceOverlay()?.remove();
}

async function openOrderEvidenceModal(side, targetId, title) {
  if (!Number.isInteger(targetId) || targetId < 1) return;
  closeOrderEvidenceModal();
  const isCost = side === "cost";
  const overlay = document.createElement("div");
  overlay.id = "orderEvidenceOverlay";
  overlay.className = "fp-oe-root";
  overlay.dataset.side = side;
  overlay.dataset.targetId = String(targetId);
  overlay.innerHTML = `
    <div class="fp-oe-backdrop" data-oe-close="1"></div>
    <div class="fp-oe-panel" role="dialog" aria-modal="true" aria-labelledby="fpOeTitle">
      <div class="fp-oe-head">
        <h3 id="fpOeTitle">${isCost ? "発注の証跡" : "受注の証跡（クライアントの発注書）"}</h3>
        <button type="button" class="inline-btn ghost" data-oe-close="1">閉じる</button>
      </div>
      <p class="fp-oe-lead">${isCost
        ? "外注先に頼んだ証拠（依頼メールのスクショ・先方の請書など）を添付してください。添付すると「登録完了」になります。月次ロックの後でも添付できます。"
        : "クライアントから受注した証拠（発注書・注文メールのスクショなど）を添付してください。"}</p>
      <div class="fp-oe-list" data-oe-list><p class="pc-empty">読み込み中…</p></div>
      <div class="fp-oe-form">
        <label>件名<input type="text" class="pj-input" data-oe-title maxlength="200" value="${escapeHtmlAttr(title || "")}" /></label>
        <label>${isCost ? "発注した日" : "受注した日"}<input type="date" class="pj-input" data-oe-date value="${orderEvidenceTodayYmd()}" /></label>
        <label>ファイル（3MBまで）<input type="file" data-oe-file accept="${ORDER_EVIDENCE_ACCEPT}" /></label>
        <button type="button" class="inline-btn" data-oe-upload="1">添付する</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  await loadOrderEvidenceModalList();
}

/** 証跡1行（画面・タブで共通） */
function orderEvidenceRowHtml(x, { withProject = false, withDelete = false } = {}) {
  return `<tr>
    <td>${escapeHtml(x.orderedOn || "—")}</td>
    <td>${escapeHtml(x.title || "")}</td>
    ${withProject ? `<td>${escapeHtml(x.projectName || "—")}${x.vendor ? `<br><small>${escapeHtml(x.vendor)}</small>` : ""}</td>` : ""}
    <td><button type="button" class="fp-oe-link" data-oe-download="${x.id}">${escapeHtml(x.filename)}</button></td>
    <td>${escapeHtml(x.createdByName || "")}</td>
    ${withDelete ? `<td><button type="button" class="inline-btn ghost" data-oe-delete="${x.id}">削除</button></td>` : ""}
  </tr>`;
}

async function loadOrderEvidenceModalList() {
  const overlay = orderEvidenceOverlay();
  if (!overlay) return;
  const { side, targetId } = overlay.dataset;
  const { ok, data } = await apiFetch(`/api/order-evidence?${side === "cost" ? "costId" : "projectId"}=${encodeURIComponent(targetId)}`);
  const box = orderEvidenceOverlay()?.querySelector("[data-oe-list]");
  if (!box) return;
  if (!ok) {
    box.innerHTML = `<p class="pc-empty pc-error">${escapeHtml(data.error || "読み込めませんでした")}</p>`;
    return;
  }
  const items = data.evidence || [];
  box.innerHTML = items.length
    ? `<table class="fp-oe-table"><thead><tr><th>日付</th><th>件名</th><th>ファイル</th><th>登録者</th><th></th></tr></thead>
        <tbody>${items.map((x) => orderEvidenceRowHtml(x, { withDelete: true })).join("")}</tbody></table>`
    : `<p class="pc-empty">まだ添付されていません。</p>`;
}

/** 添付・削除のあと: 件数・ダッシュボード・タブを読み直す */
function orderEvidenceAfterChange(side, targetId, delta) {
  const st = orderEvidenceState();
  if (side === "cost" && st.counts) st.counts[targetId] = Math.max(0, (Number(st.counts[targetId]) || 0) + delta);
  st.pendingMine = null;
  st.tab = null;
  render();
}

/** 添付する。root は添付画面か、取引先詳細の「発注書」タブ（案件を選んで添付する） */
async function submitOrderEvidenceUpload(root) {
  if (!root) return;
  const projectSelect = root.querySelector("[data-oe-project]");
  const side = projectSelect ? "project" : root.dataset.side;
  const targetId = Number(projectSelect ? projectSelect.value : root.dataset.targetId);
  const btn = root.querySelector("[data-oe-upload]");
  const fileInput = root.querySelector("[data-oe-file]");
  const title = String(root.querySelector("[data-oe-title]")?.value || "").trim();
  const orderedOn = String(root.querySelector("[data-oe-date]")?.value || "");
  // 「発注書」タブは「＋ ファイル追加」で選んだファイルを state に持つ（再描画で消えないように）
  const file = fileInput ? fileInput.files?.[0] : orderEvidenceState().tab?.form?.file;
  if (!Number.isInteger(targetId) || targetId < 1) return showToast("案件を選んでください");
  if (!title) return showToast("件名を入力してください");
  if (!orderedOn) return showToast(side === "cost" ? "発注した日を入力してください" : "受注した日を入力してください");
  if (!file) return showToast("ファイルを選んでください");
  if (file.size > ORDER_EVIDENCE_MAX_BYTES) return showToast("ファイルは3MB以内にしてください（PDFは圧縮するか、画面のスクショにしてください）");
  btn.disabled = true;
  btn.textContent = "添付中…";
  const fileBase64 = arrayBufferToBase64(await file.arrayBuffer());
  const { ok, data } = await apiFetch("/api/order-evidence", {
    method: "POST",
    body: { side, [side === "cost" ? "costId" : "projectId"]: targetId, title, orderedOn, filename: file.name, fileBase64 }
  });
  btn.disabled = false;
  btn.textContent = "添付する";
  if (!ok) {
    showToastError(`添付できませんでした：${data.error || "通信エラー"}`);
    return;
  }
  if (fileInput) fileInput.value = "";
  showToast(side === "cost" ? "発注の証跡を添付しました" : "受注の証跡を添付しました");
  orderEvidenceAfterChange(side, targetId, 1);
  if (root === orderEvidenceOverlay()) await loadOrderEvidenceModalList();
}

async function deleteOrderEvidence(id) {
  if (!(await fpConfirm({ title: "この証跡を削除しますか？", message: "削除したファイルは元に戻せません。", confirmLabel: "削除する" }))) return;
  const { ok, data } = await apiFetch("/api/order-evidence", { method: "DELETE", body: { id } });
  if (!ok) {
    showToastError(`削除できませんでした：${data.error || "通信エラー"}`);
    return;
  }
  showToast("証跡を削除しました");
  const overlay = orderEvidenceOverlay();
  if (overlay) {
    orderEvidenceAfterChange(overlay.dataset.side, Number(overlay.dataset.targetId), -1);
    await loadOrderEvidenceModalList();
  } else {
    orderEvidenceAfterChange("", 0, 0);
  }
}

// ── 取引先詳細の「発注書」タブ ──

function orderEvidenceTabState(v) {
  const st = orderEvidenceState();
  const key = `${v.entityType}:${v.dbId}`;
  if (!st.tab || st.tab.key !== key) st.tab = { key, q: "", from: "", to: "", items: [], loaded: false, error: "" };
  const t = st.tab;
  if (!t.loaded && !t.error) {
    fetchOnceInFlight(`order-evidence-tab:${key}:${t.q}:${t.from}:${t.to}`, async () => {
      const qs = new URLSearchParams({ entityType: v.entityType, entityId: String(v.dbId) });
      if (t.q) qs.set("q", t.q);
      if (t.from) qs.set("from", t.from);
      if (t.to) qs.set("to", t.to);
      const { ok, data } = await apiFetch(`/api/order-evidence?${qs}`);
      if (orderEvidenceState().tab !== t) return;
      if (ok) {
        t.items = data.evidence || [];
        t.loaded = true;
        if (!t.q && !t.from && !t.to) t.total = t.items.length;
      } else {
        t.error = data.error || "読み込めませんでした";
      }
      render();
    });
  }
  return t;
}

/** タブバーの「発注書」 */
function clientDetailOrdersTabLinkHtml(v, activeTab, tabHref) {
  const t = orderEvidenceTabState(v);
  return `<a role="tab" class="cl-class-tab ${activeTab === "orders" ? "active" : ""}" aria-selected="${activeTab === "orders"}" href="${tabHref("orders")}">発注書<span class="cl-class-tab-count">${t.total ?? "…"}</span></a>`;
}

/** この取引先の案件（新しい順）。受注の証跡は案件に付くので、添付するときに選ぶ */
function clientOrdersProjects(v) {
  return projects.filter((p) => p.clientId === `c${v.dbId}` && p.dbId).sort((a, b) => b.dbId - a.dbId);
}

/** クライアントの「発注書」タブの添付欄。他の書類タブと同じく「＋ ファイル追加」で選び、案件・件名・日付を決めて添付する（申請は無い）。
 *  入力は t.form に持つ（再描画で消えないように） */
function clientOrdersUploadFormHtml(v, t) {
  const f = t.form;
  if (!f) return "";
  const options = clientOrdersProjects(v)
    .map((p) => `<option value="${escapeHtmlAttr(String(p.dbId))}"${String(p.dbId) === f.projectId ? " selected" : ""}>${escapeHtml(p.name || "（案件名なし）")}</option>`)
    .join("");
  return `
    <div class="fp-oe-form fp-oe-tabform" data-oe-tab-form="1">
      <div class="fp-oe-tabform-file">添付するファイル：<b>${escapeHtml(f.file.name)}</b>（${Math.max(1, Math.round(f.file.size / 1024)).toLocaleString()}KB）</div>
      <label>案件<select class="pj-input" data-oe-project data-oe-tab-field="projectId">${options}</select></label>
      <label>件名<input type="text" class="pj-input" data-oe-title data-oe-tab-field="title" maxlength="200" value="${escapeHtmlAttr(f.title)}" /></label>
      <label>受注した日<input type="date" class="pj-input" data-oe-date data-oe-tab-field="date" value="${escapeHtmlAttr(f.date)}" /></label>
      <div class="fp-oe-tabform-actions">
        <button type="button" class="inline-btn ghost" data-oe-tab-cancel="1">キャンセル</button>
        <button type="button" class="inline-btn" data-oe-upload="1">添付する</button>
      </div>
    </div>`;
}

/** 「＋ ファイル追加」でファイルを選んだ: 件名はファイル名（拡張子なし）、日付は今日、案件は一番新しいものを初期値にする */
function startClientOrdersUpload(file) {
  const t = orderEvidenceState().tab;
  if (!t || !file) return;
  if (file.size > ORDER_EVIDENCE_MAX_BYTES) return showToast("ファイルは3MB以内にしてください（PDFは圧縮するか、画面のスクショにしてください）");
  const first = clientOrdersProjects({ dbId: Number(t.key.split(":")[1]) })[0];
  t.form = { file, title: file.name.replace(/\.[^.]+$/, ""), date: orderEvidenceTodayYmd(), projectId: first ? String(first.dbId) : "" };
  render();
}

/** 「発注書」タブの本体 */
function clientDetailOrdersHtml(v) {
  const t = orderEvidenceTabState(v);
  const isPartner = v.entityType === "partner";
  const list = t.error
    ? `<p class="pc-empty pc-error">${escapeHtml(t.error)}</p>`
    : !t.loaded
      ? `<p class="pc-empty">読み込み中…</p>`
      : t.items.length
        ? `<table class="fp-oe-table"><thead><tr><th>${isPartner ? "発注日" : "受注日"}</th><th>件名</th><th>案件</th><th>ファイル</th><th>登録者</th>${isPartner ? "" : "<th></th>"}</tr></thead>
            <tbody>${t.items.map((x) => orderEvidenceRowHtml(x, { withProject: true, withDelete: !isPartner })).join("")}</tbody></table>`
        : `<p class="pc-empty">${t.q || t.from || t.to ? "条件に合う発注書はありません。" : "まだ発注書はありません。"}</p>`;
  const canAdd = !isPartner && clientOrdersProjects(v).length > 0;
  const addBtn = canAdd
    ? `<label class="inline-btn fp-cldetail-addfile-btn">＋ ファイル追加<input type="file" data-oe-tab-file="1" accept="${ORDER_EVIDENCE_ACCEPT}" hidden /></label>`
    : "";
  const lead = isPartner
    ? "この取引先に外注した原価の証跡です。添付は案件の原価タブの「証跡待ち」バッジから行います。"
    : canAdd ? "" : "この取引先の案件がまだ無いため、発注書を添付できません。";
  return `
    <section class="soft-card fp-cldetail-card">
      <div class="fp-cldetail-card-head"><b>${isPartner ? "発注書（外注の証跡）" : "発注書（受注の証跡）"}</b>${addBtn}</div>
      ${lead ? `<p class="fp-oe-lead">${lead}</p>` : ""}
      ${canAdd ? clientOrdersUploadFormHtml(v, t) : ""}
      <div class="fp-oe-search">
        <input type="search" class="pj-input" data-oe-tab-q placeholder="件名・ファイル名・案件名で探す" value="${escapeHtmlAttr(t.q)}" />
        <input type="date" class="pj-input" data-oe-tab-from value="${escapeHtmlAttr(t.from)}" aria-label="日付（から）" />
        <span>〜</span>
        <input type="date" class="pj-input" data-oe-tab-to value="${escapeHtmlAttr(t.to)}" aria-label="日付（まで）" />
        <button type="button" class="inline-btn ghost" data-oe-tab-search="1">検索</button>
      </div>
      ${list}
    </section>`;
}

function runOrderEvidenceTabSearch(root) {
  const t = orderEvidenceState().tab;
  if (!t) return;
  t.q = String(root.querySelector("[data-oe-tab-q]")?.value || "").trim();
  t.from = String(root.querySelector("[data-oe-tab-from]")?.value || "");
  t.to = String(root.querySelector("[data-oe-tab-to]")?.value || "");
  t.loaded = false;
  t.error = "";
  render();
}

// ── ダッシュボード・月次ロック ──

/** ダッシュボード: 自分の担当で証跡待ちの外注費（添付するまで出し続ける） */
function orderEvidencePendingBannerHtml() {
  const st = orderEvidenceState();
  if (st.pendingMine == null) {
    fetchOnceInFlight("order-evidence-pending", async () => {
      const { ok, data } = await apiFetch("/api/order-evidence?pending=mine");
      st.pendingMine = ok ? data.pending || [] : [];
      if (st.pendingMine.length) render();
    });
    return "";
  }
  const items = st.pendingMine;
  if (!items.length) return "";
  const overdue = items.filter((x) => x.overdue).length;
  const rows = items.slice(0, 5).map((x) => {
    const label = orderEvidenceDueLabel(x.dueDate);
    return `<li><button type="button" class="fp-oe-link" data-order-evidence-cost="${x.costId}" data-order-evidence-title="${escapeHtmlAttr(x.vendor)}">${escapeHtml(x.projectName || "（案件なし）")} / ${escapeHtml(x.vendor || "—")}（${yen.format(x.amount)}）</button>
      <span class="fp-oe-due${label.overdue ? " is-overdue" : ""}">${escapeHtml(label.text)}</span></li>`;
  }).join("");
  return `
    <section class="section">
      <div class="soft-card panel-pad fp-oe-banner${overdue ? " is-overdue" : ""}">
        <b>発注の証跡が未登録の外注費が ${items.length} 件あります${overdue ? `（うち期限切れ ${overdue} 件）` : ""}</b>
        <p class="fp-oe-lead">外注先に頼んだ証拠（依頼メールのスクショなど）を添付してください。月が締まった後でも添付できます。</p>
        <ul class="fp-oe-pending">${rows}</ul>
        ${items.length > 5 ? `<p class="fp-oe-lead">ほか ${items.length - 5} 件</p>` : ""}
      </div>
    </section>`;
}

/** 月次ロックの前に、その月で証跡待ちの外注費を知らせる（ロックは止めない）。進めてよければ true */
async function confirmOrderEvidencePendingForLock(ymSlash) {
  const { ok, data } = await apiFetch(`/api/order-evidence?pending=month&ym=${encodeURIComponent(ymSlash)}`);
  const items = ok ? data.pending || [] : [];
  if (!items.length) return true;
  const lines = items.slice(0, 10).map((x) => `・${x.projectName || "（案件なし）"} / ${x.vendor || "—"}（${yen.format(x.amount)}）`);
  if (items.length > 10) lines.push(`ほか ${items.length - 10} 件`);
  return fpConfirm({
    title: `${ymSlash} に発注の証跡が未登録の外注費が ${items.length} 件あります`,
    message: lines.join("\n"),
    note: "ロックしても証跡はあとから添付できます。担当者のダッシュボードには、添付するまで残り続けます。",
    confirmLabel: "このままロックする",
    danger: false
  });
}

// ── イベント（他の click 処理より先に受け、行のクリック等に伝えない） ──
document.addEventListener(
  "click",
  (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const hit = t.closest("[data-order-evidence-cost],[data-order-evidence-project],[data-oe-download],[data-oe-delete],[data-oe-close],[data-oe-upload],[data-oe-tab-search],[data-oe-tab-cancel]");
    if (!(hit instanceof HTMLElement)) return;
    e.preventDefault();
    e.stopPropagation();
    const d = hit.dataset;
    if (d.orderEvidenceCost) void openOrderEvidenceModal("cost", Number(d.orderEvidenceCost), d.orderEvidenceTitle || "");
    else if (d.orderEvidenceProject) void openOrderEvidenceModal("project", Number(d.orderEvidenceProject), d.orderEvidenceTitle || "");
    else if (d.oeDownload) {
      void apiDownloadFile(`/api/order-evidence?id=${encodeURIComponent(d.oeDownload)}`, "evidence").catch((err) =>
        showToastError(`ダウンロードできませんでした：${err?.message || ""}`)
      );
    } else if (d.oeDelete) void deleteOrderEvidence(Number(d.oeDelete));
    else if (d.oeClose) closeOrderEvidenceModal();
    else if (d.oeUpload) void submitOrderEvidenceUpload(hit.closest("[data-oe-tab-form]") || orderEvidenceOverlay());
    else if (d.oeTabSearch) runOrderEvidenceTabSearch(hit.closest(".fp-cldetail-card") || document);
    else if (d.oeTabCancel) {
      const tab = orderEvidenceState().tab;
      if (tab) tab.form = null;
      render();
    }
  },
  true
);

// 「発注書」タブ: ファイルを選んだら添付欄を開き、入力は state に写す（再描画で消えないように）
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || !t.matches("[data-oe-tab-file]")) return;
  startClientOrdersUpload(t.files?.[0]);
  t.value = "";
});
document.addEventListener("input", (e) => {
  const t = e.target;
  const key = t instanceof HTMLElement ? t.dataset.oeTabField : "";
  const form = orderEvidenceState().tab?.form;
  if (key && form) form[key] = String(t.value ?? "");
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && orderEvidenceOverlay() && !document.querySelector(".fp-confirm-root")) {
    closeOrderEvidenceModal();
    return;
  }
  const t = e.target;
  if (e.key === "Enter" && !e.isComposing && t instanceof HTMLInputElement && t.closest(".fp-oe-search")) {
    e.preventDefault();
    runOrderEvidenceTabSearch(t.closest(".fp-cldetail-card") || document);
  }
});
// ────────────────────────────────────────────────────────────────
// 案件詳細・基本情報フォームのサブタブ（請求・納期／メール送付先／分類・タグ／メモ・入金）。
//
// 「請求・納期」1見出しの下に全項目がぶら下がって見づらい、という指摘への対応。
// 入力値は draft（uiState.projectDetailForm）に持っているため、タブを切り替えても
// 未保存の編集は消えない。保存は従来どおりフォーム全体の draft を送る。
// 描画本体は app-src/07-pdf-output.js の buildProjectDetailFormResultFieldsHtml。
// ────────────────────────────────────────────────────────────────

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "project-detail") return;

  const tabEl = target.closest("[data-pd-form-tab]");
  if (!(tabEl instanceof HTMLElement)) return;

  const key = String(tabEl.dataset.pdFormTab || "");
  if (!["billing", "mail", "class", "split", "memo"].includes(key)) return;
  if (uiState.projectDetailFormSection === key) return;
  uiState.projectDetailFormSection = key;
  render();
});
// ── 案件詳細（基本情報）の保存: 「保存」「保存して一覧へ」「保存して書類へ」と未保存表示・離脱確認 ──
// 基本情報は uiState.projectDetailForm.draft に溜めて「保存」で送る方式。保存したかが画面で分からず、
// 「書類作成・編集へ」で未保存のまま移れてしまっていたため（2026-10 要望）、
// 保存と遷移を1ボタンにまとめ、未保存の変更があるときは表示と離脱確認を出す。
// 営業・カテゴリ・受注ステータス・回収不能も以前は選んだ時点で保存していたが、「試しに変えてみた」が
// そのまま確定してしまうため、同じ下書きに入れて保存ボタンで確定する（2026-10 要望）。
// 請求項目も案件詳細では自動で送らず、同じ「保存」で確定する（請求項目タブにも保存ボタンを置く）。
// 未保存の請求項目は uiState.projectBillingManualDirty（markProjectDocsDraftDirty が案件詳細で立てる）。

/** 開いている案件の下書き（その案件の詳細画面でなければ null） */
function projectDetailDraftFor(proj) {
  const draft = projectDetailFormDraftActive();
  return draft && proj && String(uiState.projectDetailForm.projectId) === String(proj.id) ? draft : null;
}

/** 下書きの値の比較用キー。入力欄経由で数値・真偽が文字列になることがあるので文字列で比べる
 *  （カテゴリはオブジェクトなので JSON で） */
function projectDraftValueKey(v) {
  return v && typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
}

/** 開いている案件に未保存の変更があるか（基本情報は下書きと保存済みの値を比べる。請求項目は未保存の印）。
 *  送信に失敗したときは saveFailed でも未保存扱いにする */
function projectDetailFormIsDirty() {
  const draft = projectDetailFormDraftActive();
  if (!draft) return false;
  if (uiState.projectDetailForm.saveFailed) return true;
  if (uiState.projectBillingManualDirty?.[String(uiState.projectDetailForm.projectId)]) return true;
  const proj = projects.find((x) => String(x.id) === String(uiState.projectDetailForm.projectId));
  if (!proj) return false;
  const saved = projectDrawerDraft(proj);
  return Object.keys(saved).some((k) => projectDraftValueKey(draft[k]) !== projectDraftValueKey(saved[k]));
}

function projectDetailSaveStatusText() {
  if (uiState.projectDetailForm?.saving) return { text: "保存中…", cls: "is-saving" };
  if (projectDetailFormDraftActive() && uiState.projectDetailForm.saveFailed) {
    return { text: "保存に失敗しました（もう一度保存してください）", cls: "is-dirty" };
  }
  if (projectDetailFormIsDirty()) {
    // 次に変更が無くなっても「保存済み」と出さない（保存したのは前の内容）
    if (uiState.projectDetailForm) uiState.projectDetailForm.justSaved = false;
    return { text: "未保存の変更があります", cls: "is-dirty" };
  }
  // 「保存済み」は保存した直後だけ出す。開いただけ（何も変えていない）のときは何も出さない（2026-10 要望）
  return uiState.projectDetailForm?.justSaved ? { text: "保存済み", cls: "is-clean" } : { text: "", cls: "is-idle" };
}

/** 保存行の状態表示（入力のたびに refreshProjectDetailSaveStatus で書き換える） */
function projectDetailSaveStatusHtml() {
  const st = projectDetailSaveStatusText();
  return `<span class="pd-save-status ${st.cls}" data-pd-save-status role="status" aria-live="polite">${escapeHtml(st.text)}</span>`;
}

function refreshProjectDetailSaveStatus() {
  const el = document.querySelector("[data-pd-save-status]");
  if (!el) return;
  const st = projectDetailSaveStatusText();
  el.textContent = st.text;
  el.className = `pd-save-status ${st.cls}`;
}

/** 基本情報を保存する。成功で true（失敗・キャンセルはトーストを出して false） */
async function saveProjectDetailOverview(proj) {
  const d = uiState.projectDetailForm;
  if (!proj || !d?.draft || String(d.projectId) !== String(proj.id) || d.saving) return false;
  // 送信に失敗したら案件の値を送信前へ戻す（反映したままだと、一覧・集計に保存されていない
  // 受注ステータスや回収不能が出る）。下書きは利用者の入力に戻して未保存のまま残す
  // 戻すのは下書きの反映で書き換える項目だけ（送信中に請求項目の自動保存などが変えた項目は戻さない）
  const before = Object.fromEntries(PROJECT_DRAFT_APPLIED_KEYS.map((k) => [k, proj[k]]));
  const userDraft = d.draft;
  applyProjectFormDraftToProject(proj, d.draft);
  const applied = Object.fromEntries(PROJECT_DRAFT_APPLIED_KEYS.map((k) => [k, proj[k]]));
  const sent = projectDrawerDraft(proj);
  d.draft = { ...sent };
  d.base = sent;
  d.saving = true;
  refreshProjectDetailSaveStatus();
  try {
    // includeBillingLines: true — 請求項目も一緒にサーバーへ送信（案件詳細では請求項目も保存ボタンで確定する）
    await persistProjectDetailToServer(proj, { includeBillingLines: true });
    d.saveFailed = false;
    showToast("保存しました");
    d.justSaved = true;
    return true;
  } catch (err) {
    // 送信中に別の保存（案件名のインライン保存など）が変えた項目は戻さない（戻すとその保存が消える）
    PROJECT_DRAFT_APPLIED_KEYS.forEach((k) => {
      if (proj[k] === applied[k]) proj[k] = before[k];
    });
    // 送信中に触った欄はその値を残し、触っていない欄だけ送ろうとした入力へ戻す
    Object.keys(sent).forEach((k) => {
      if (projectDraftValueKey(d.draft[k]) === projectDraftValueKey(sent[k])) d.draft[k] = userDraft[k];
    });
    d.base = projectDrawerDraft(proj);
    d.saveFailed = true;
    // 他の人の変更との競合（409）・ロックなどはサーバーの文言を添える（請求項目は再読み込みで最新を取り直せる）
    const reason = String(err?.serverMessage || "").trim();
    showToastError(reason ? `保存に失敗しました：${reason}` : "保存に失敗しました");
    return false;
  } finally {
    d.saving = false;
    // 保存後に遷移しない経路（案件名が空で書類へ進めない等）でも「保存中…」を残さない
    refreshProjectDetailSaveStatus();
  }
}

/** 案件の書類画面（見積書）を開く。案件名が空なら止める */
function openProjectDocsForProject(pid) {
  const proj = projects.find((x) => x.id === pid);
  // 案件名が空のまま書類作成へ進むと、書類が既定名で発行されてしまうため、
  // 書類作成画面へ進む前に必ず入力させる（2026-08-18 ユーザー要望。作成モーダルでも必須にしたが、
  // 既存の案件名未設定の案件や、基本情報で消した場合のためにここでも止める）。
  if (proj && !String(proj.name || "").trim()) {
    showToastError("案件名を入力してください（基本情報の「案件名」）");
    return;
  }
  if (!(typeof window !== "undefined" && window.innerWidth <= 640)) syncProjectDetailBillingTableFromDom(pid);
  uiState.appMgmtDetailModal = null; // アプリ管理モーダルを閉じてから遷移
  // 受注ステータスによらず見積書を最初に開く（2026-10 要望。請求書へはタブで切り替える）
  const nextHash = `#project-docs:${pid}:quote`;
  if (location.hash === nextHash) render();
  else location.hash = nextHash;
}

// 入力のたびに「未保存の変更があります／保存済み」を更新する。
// 下書きへの反映は既存の input/change ハンドラ（後から登録）が行うので、その後に判定する
["input", "change"].forEach((type) => {
  document.addEventListener(type, () => {
    if (currentRoute().page !== "project-detail") return;
    setTimeout(refreshProjectDetailSaveStatus, 0);
  });
});

// 未保存のまま案件詳細から離れる前に確認する（capture で既存の遷移処理より先に止める）。
// 保存ボタン（保存して一覧へ・保存して書類へ）は保存後に遷移するのでここでは止めない
document.addEventListener(
  "click",
  (e) => {
    if (currentRoute().page !== "project-detail" || !projectDetailFormIsDirty()) return;
    const target = e.target instanceof Element ? e.target : null;
    const leave = target?.closest('a[href^="#"], [data-go], [data-open-project-docs]');
    if (!leave) return;
    if (confirm("保存していない変更があります。保存せずに移動しますか？\n（請求項目の入力は保存済みの内容に戻ります）")) {
      discardProjectDetailBillingLines(uiState.projectDetailForm.projectId);
      // 続く hashchange で同じ確認を2回出さない（移動が起きなかったときに残らないよう短時間で解除）
      projectDetailLeaveConfirmed = true;
      setTimeout(() => {
        projectDetailLeaveConfirmed = false;
      }, 1000);
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
  },
  true
);

/** 「保存せずに移動」: 案件詳細で編集した請求項目を捨てて、最後に保存・読込したサーバーの内容へ戻す。
 *  サーバーの内容が分からない（読込前など）ときは捨てずに未保存のまま残す（次に開いたとき保存できる） */
function discardProjectDetailBillingLines(pid) {
  const k = String(pid || "");
  if (!projectBillingLinesManualDirty(k)) return;
  const copy = uiState.projectBillingLinesServerCopy?.[k];
  const snap = uiState.projectBillingLinesServerSnapshot?.[k];
  const lines = copy || (snap && snap.lineCount === 0 ? [] : null);
  if (!lines) return;
  ensureProjectDocDraft(k).lines = JSON.parse(JSON.stringify(lines));
  clearProjectDocsDraftDirty(k);
  persistProjectDocDraftsStore();
}

// ブラウザの「戻る」など、クリックを通らない移動で案件詳細を離れたときも、未保存の請求項目を確かめる
// （上のクリック確認だけだと、試しに変えた明細が残ったまま書類画面などで保存・送付されうる）。
// 移動後なので、キャンセルは案件詳細へ戻す
let projectDetailLastHash = currentRoute().page === "project-detail" ? location.hash : "";
/** クリックの離脱確認で「保存せずに移動」を選んだ直後か（hashchange 側の確認を省く） */
let projectDetailLeaveConfirmed = false;
window.addEventListener("hashchange", () => {
  const r = currentRoute();
  const prev = projectDetailLastHash;
  // 下書きはまだ前の案件のもの（新しい画面の描画はこの後）
  const pid = uiState.projectDetailForm?.projectId;
  projectDetailLastHash = r.page === "project-detail" ? location.hash : "";
  // 「保存済み」は保存した直後だけ。詳細から離れたら消す（開き直したときに出さない）
  if (r.page !== "project-detail" && uiState.projectDetailForm) uiState.projectDetailForm.justSaved = false;
  // 同じ案件の中の移動（タブ切替など）は確認しない。別の案件の詳細へ移るときは確認する
  // （確認しないと前の案件の明細が未保存のまま、どこにも表示されずに残る）
  const sameProject =
    r.page === "project-detail" && String(resolveProjectByRouteId(r.id)?.id ?? r.id) === String(pid);
  if (sameProject || !prev || !projectBillingLinesManualDirty(pid)) return;
  if (projectDetailLeaveConfirmed) {
    projectDetailLeaveConfirmed = false;
    return;
  }
  if (confirm("保存していない請求項目の変更があります。破棄して移動しますか？\n（キャンセルすると案件詳細に戻ります）")) {
    discardProjectDetailBillingLines(pid);
  } else {
    location.hash = prev;
  }
});

window.addEventListener("beforeunload", (e) => {
  if (currentRoute().page !== "project-detail" || !projectDetailFormIsDirty()) return;
  e.preventDefault();
  e.returnValue = "";
});
// ────────────────────────────────────────────────────────────────
// 案件詳細・タグの新規作成モーダル。
//
// タグのプルダウン末尾の「＋ 新しいタグを作成…」を選ぶと開く（ユーザー指定のフロー）。
// 追加を押すと、候補への登録（全員共有）と今の案件への適用を同時に行い、閉じる。
// 旧実装と違いモーダルは中央のコンパクト表示（fp-centered-modal--xs）で、
// 「登録してからプルダウンで選び直す」二度手間はない。
// ────────────────────────────────────────────────────────────────

/** タグ新規作成モーダル。モーダルレイヤー（15-sp-backlog の renderNow）から呼ばれる */
function renderProjectDetailTagModal() {
  const m = uiState.projectDetailTagModal;
  const route = currentRoute();
  if (!m || route.page !== "project-detail" || String(route.id || "") !== String(m.projectId || "")) return "";
  if (!projectDetailFormDraftActive()) return "";
  return `
    <div class="composer-backdrop" data-pd-tag-modal-close="1"></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--xs pd-tag-modal" role="dialog" aria-modal="true" aria-labelledby="pd-tag-modal-title">
      <div class="composer-head">
        <div><h3 id="pd-tag-modal-title">新しいタグを作成</h3></div>
        <button type="button" class="inline-btn ghost" data-pd-tag-modal-close="1">閉じる</button>
      </div>
      <div class="composer-body pd-tag-modal-body">
        <input class="dform-input" type="text" data-pd-tag-modal-input value="${escapeHtmlAttr(String(m.value ?? ""))}" placeholder="例: 重要顧客" maxlength="120" />
        <p class="pd-tag-modal-hint">追加すると、この案件に付き、プルダウンの候補にも残ります。</p>
        <div class="pd-tag-modal-actions">
          <button type="button" class="inline-btn ghost" data-pd-tag-modal-close="1">キャンセル</button>
          <button type="button" class="inline-btn" data-pd-tag-modal-add="1">追加</button>
        </div>
      </div>
    </aside>
  `;
}

/** 入力中のタグ名を検証して案件へ適用。成功時 true（モーダルを閉じてよい） */
function applyNewProjectDetailTag() {
  const inp = document.querySelector("[data-pd-tag-modal-input]");
  const draft = projectDetailFormDraftActive();
  if (!draft || !(inp instanceof HTMLInputElement)) return false;
  const v = inp.value.trim();
  if (!v) {
    showToast("タグ名を入力してください");
    return false;
  }
  if (v === "__new__") {
    showToast("この名前は使えません");
    return false;
  }
  const arr = parseProjectDetailTags(draft.tag);
  if (arr.includes(v)) {
    showToast("すでに追加されています");
    return false;
  }
  rememberProjectDetailTagPreset(v);
  arr.push(v);
  draft.tag = arr.join(", ");
  return true;
}

document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLSelectElement)) return;
  if (t.dataset.pdTagPreset === undefined) return;
  if (t.value !== "__new__") return;
  // 「＋ 新しいタグを作成…」自体は選択状態にしない（追加ボタンが内部値を拾わないように戻す）
  t.value = "";
  if (currentRoute().page !== "project-detail") return;
  uiState.projectDetailTagModal = { projectId: String(currentRoute().id || ""), value: "" };
  render();
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (t.dataset.pdTagModalInput === undefined || !uiState.projectDetailTagModal) return;
  // 再描画で入力が消えないよう state に写すだけ（renderは呼ばない）
  uiState.projectDetailTagModal.value = t.value;
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter") return;
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || t.dataset.pdTagModalInput === undefined) return;
  if (e.isComposing) return; // IME変換確定のEnterでは送信しない
  e.preventDefault();
  if (applyNewProjectDetailTag()) {
    uiState.projectDetailTagModal = null;
    render();
  }
});

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (!uiState.projectDetailTagModal) return;

  if (target.closest("[data-pd-tag-modal-add]")) {
    if (applyNewProjectDetailTag()) {
      uiState.projectDetailTagModal = null;
      render();
    }
    return;
  }
  if (target.closest("[data-pd-tag-modal-close]")) {
    uiState.projectDetailTagModal = null;
    render();
  }
});
// ───────────────────────────────────────────────
// 案件のカテゴリと担当者（基本情報・営業の下）
// カテゴリ（媒体／制作／システム／その他）を複数選び、その右のプルダウン1つで担当者を複数選ぶ
// （担当者はカテゴリごとに分けない）。旧「関連担当」タブの「その他（貢献度）」の置き換え。
// 経営ダッシュボードの貢献度タブは projectContributorIds で担当者を集計する（比率なし・表示専用）。
// 形: p.workCategories = { categories: ["媒体", "制作"], members: ["u5"] }。
// 保存先は fp_projects.work_categories_json（サーバー normalizeWorkCategoriesJson と対）。
// ───────────────────────────────────────────────

/** 担当者候補: 退職者を除く全員（休職中は名前に印が付く）。人事異動が多く、職種やカテゴリで絞ると
 *  実際の担当者が候補から漏れるため絞らない。同じ id は1回だけ出す */
function workCategoryCandidateUsers() {
  const seen = new Set();
  return users
    .filter((u) => isActiveUser(u) && !u.retired && !seen.has(String(u.id)) && seen.add(String(u.id)))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ja"));
}

/** 候補に同姓同名（別アカウント）がいるときは、見分けられるようメールを添える */
function workCategoryUserLabel(u, dupNames) {
  const label = userNameWithRetiredLabel(u) || "?";
  return dupNames.has(u.name) && u.email ? `${label}（${u.email}）` : label;
}

/** { categories, members } に正規化する。カテゴリごとに担当者を持っていた旧形式
 *  （{ "媒体": [5], "制作": [] }。develop だけで使っていた）も、カテゴリ＝キー・担当者＝和集合として読む */
function workCategoriesFromRaw(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { categories: [], members: [] };
  if (Array.isArray(raw.categories) || Array.isArray(raw.members)) {
    const cats = Array.isArray(raw.categories) ? raw.categories : [];
    return { categories: PROJECT_WORK_CATEGORY_OPTIONS.filter((c) => cats.includes(c)), members: raw.members || [] };
  }
  const categories = PROJECT_WORK_CATEGORY_OPTIONS.filter((c) => Object.prototype.hasOwnProperty.call(raw, c));
  return { categories, members: categories.flatMap((c) => (Array.isArray(raw[c]) ? raw[c] : [])) };
}

/** 画面に出す・編集の元にする値。カテゴリも担当者も未設定の案件は、旧「その他（貢献度）」の担当者を
 *  初期値にする（そのまま初めてカテゴリを選んで保存しても、旧担当者が消えて貢献度の集計から外れないように） */
function projectWorkCategoriesForEdit(p) {
  const wc = workCategoriesFromRaw(p?.workCategories);
  if (wc.categories.length === 0 && wc.members.length === 0 && Array.isArray(p?.nonSalesSplit)) {
    return { categories: [], members: [...p.nonSalesSplit] };
  }
  return wc;
}

/** GET 応答の work_categories_json → フロント形式（担当者は "u5" 形式） */
function normalizeClientWorkCategories(raw) {
  const wc = workCategoriesFromRaw(raw);
  return { categories: wc.categories, members: normalizeClientNonSalesInvolved(wc.members) };
}

/** PUT/POST body 用: フロント形式 → { categories, members: [5] }。両方空なら null（＝未設定） */
function workCategoriesToServerBody(wc) {
  const { categories, members } = workCategoriesFromRaw(wc);
  const ids = nonSalesInvolvedToServerBody(members) || [];
  return categories.length > 0 || ids.length > 0 ? { categories, members: ids } : null;
}

/** 案件に関わった担当者。カテゴリ・担当者とも未設定の案件は旧「その他（貢献度）」を使う
 *  （既存データを新形式へ移すまで、貢献度の集計から消えないようにする） */
function projectContributorIds(p) {
  const wc = p?.workCategories;
  if (wc && ((wc.categories || []).length > 0 || (wc.members || []).length > 0)) {
    return [...new Set((wc.members || []).map(String))];
  }
  return Array.isArray(p?.nonSalesSplit) ? p.nonSalesSplit.map(String) : [];
}

/** 案件のカテゴリ → 原価フォームの会計区分の初期値。媒体を含む（または未設定）なら従来どおり媒体費、
 *  媒体以外だけなら先頭のカテゴリに対応する会計区分（制作の案件で毎回「媒体費」から直していた） */
const WORK_CATEGORY_TO_COST_CATEGORY = { 制作: "制作費", システム: "開発費", その他: "その他" };
function defaultCostCategoryForProject(p) {
  const { categories } = workCategoriesFromRaw(p?.workCategories);
  if (categories.length === 0 || categories.includes("媒体")) return "媒体費";
  return WORK_CATEGORY_TO_COST_CATEGORY[categories[0]] || "媒体費";
}

/** 媒体にチェックがあるのに担当者が0人か。保存は止めず、プルダウンを赤枠にして入れ忘れに気づかせるだけ */
function workCategoriesMissingMediaMember(wc) {
  return wc.categories.includes("媒体") && wc.members.length === 0;
}

/** 基本情報・営業の下に出す「カテゴリ」チェックと、その右の担当者プルダウン（複数選択）。
 *  wc は保存前の下書き（基本情報の保存ボタンで確定する）。省略時は保存済みの値 */
function buildProjectWorkCategoriesHtml(p, wc) {
  const { categories, members } = wc || projectWorkCategoriesForEdit(p);
  const missingMember = workCategoriesMissingMediaMember({ categories, members });
  const pid = escapeHtmlAttr(p.id);
  const checksHtml = PROJECT_WORK_CATEGORY_OPTIONS.map(
    (cat) => `
      <label class="pd-overview-order-check">
        <input type="checkbox" data-work-category-toggle="${escapeHtmlAttr(cat)}" data-work-category-pid="${pid}" ${categories.includes(cat) ? "checked" : ""} />
        ${escapeHtml(cat)}
      </label>`
  ).join("");
  // 候補外になった選択済みの人（退職など）も外せるよう一覧に残す
  const pool = workCategoryCandidateUsers();
  const poolIds = new Set(pool.map((u) => String(u.id)));
  const extra = [...new Set(members.map(String))].filter((id) => !poolIds.has(id)).map((id) => getUserById(id) || { id, name: "?" });
  const opts = [...pool, ...extra];
  const nameCount = new Map();
  opts.forEach((u) => nameCount.set(u.name, (nameCount.get(u.name) || 0) + 1));
  const dupNames = new Set([...nameCount].filter(([, n]) => n > 1).map(([name]) => name));
  // 候補が多いので名前で絞り込めるようにする。チェックのたびに再描画するため、絞り込み語は uiState に持って描画時にも当てる
  const q = String(uiState.workCategorySearch || "").trim().toLowerCase();
  const optsHtml = opts
    .map(
      (u) =>
        `<label class="pj-assignee-filter-opt" data-work-category-opt-name="${escapeHtmlAttr(String(u.name || "").toLowerCase())}"${q && !String(u.name || "").toLowerCase().includes(q) ? " hidden" : ""}><span class="pj-assignee-filter-opt__check"><input type="checkbox" data-work-category-member="${escapeHtmlAttr(String(u.id))}" data-work-category-pid="${pid}" ${members.some((id) => String(id) === String(u.id)) ? "checked" : ""} /></span><span class="pj-assignee-filter-opt__text">${escapeHtml(workCategoryUserLabel(u, dupNames))}</span></label>`
    )
    .join("");
  const first = members.length === 1 ? getUserById(members[0]) : null;
  const label = members.length === 0 ? "担当を選択" : first ? userNameWithRetiredLabel(first) : `${members.length}人選択中`;
  const open = uiState.workCategoryPickerOpenPid === p.id ? " open" : "";
  const pickerHtml = `
    <details class="pj-assignee-filter-details pd-work-cat-picker${missingMember ? " pd-work-cat-picker--missing" : ""}" data-work-category-picker="${pid}" role="group" aria-label="担当者を選択"${open}>
      <summary class="pj-assignee-filter-trigger${members.length === 0 ? " pj-assignee-filter-trigger--placeholder" : ""}">
        <span class="pj-assignee-filter-trigger-label">${escapeHtml(label)}</span>
        <span class="pj-assignee-filter-caret" aria-hidden="true">▾</span>
      </summary>
      <div class="pj-assignee-filter-panel">${optsHtml ? `<input type="search" class="pj-input pd-work-cat-search" data-work-category-search="${pid}" value="${escapeHtmlAttr(uiState.workCategorySearch || "")}" placeholder="名前で絞り込み" aria-label="担当者を名前で絞り込み" /><div class="pd-work-cat-list">${optsHtml}</div>` : `<div class="pj-tag-filter-empty">候補がいません</div>`}</div>
    </details>`;
  return `
    <div class="pd-overview-order-row pd-work-cat-row">
      <span class="pd-overview-order-heading">カテゴリ:</span>
      ${checksHtml}
      ${pickerHtml}
      ${missingMember ? `<span class="pd-work-cat-warning">媒体の担当者を選択してください</span>` : ""}
    </div>`;
}

/** 担当者プルダウンのスクロールする部分（候補の一覧。絞り込み欄は上に固定） */
function workCategoryPickerPanel(pid) {
  return document.querySelector(`[data-work-category-picker="${CSS.escape(String(pid))}"] .pd-work-cat-list`);
}

/** 変更を基本情報の下書きに入れる（保存ボタンで確定。旧「その他（貢献度）」の旧列は
 *  保存時に applyProjectFormDraftToProject が空にする） */
function setProjectWorkCategoriesDraft(proj, next) {
  const draft = projectDetailDraftFor(proj);
  if (!draft) return;
  draft.workCategories = next;
  // 再描画でプルダウンの一覧が先頭に戻らないよう、スクロール位置を引き継ぐ
  const scrollTop = workCategoryPickerPanel(proj.id)?.scrollTop || 0;
  render();
  const panel = workCategoryPickerPanel(proj.id);
  if (panel) panel.scrollTop = scrollTop;
}

/** 編集中の値（下書き）。下書きが無い画面では保存済みの値 */
function workCategoriesDraftOrSaved(proj) {
  return projectDetailDraftFor(proj)?.workCategories || projectWorkCategoriesForEdit(proj);
}

function workCategoriesTargetProject(el) {
  return projects.find((x) => x.id === el.dataset.workCategoryPid) || null;
}

// 担当者プルダウンの開閉を覚える（チェックのたびに再描画しても開いたままにする）。
// 外側クリックで閉じる処理は 15n-filter-dropdown-close.js（同じ pj-assignee-filter-details）に任せる。
// toggle はバブリングしないのでキャプチャで拾う
document.addEventListener(
  "toggle",
  (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.dataset.workCategoryPicker) return;
    if (d.open) uiState.workCategoryPickerOpenPid = d.dataset.workCategoryPicker;
    else if (uiState.workCategoryPickerOpenPid === d.dataset.workCategoryPicker) {
      uiState.workCategoryPickerOpenPid = "";
      uiState.workCategorySearch = ""; // 閉じたら絞り込みも解除（次に開いたとき全員が見えるように）
    }
  },
  true
);

// 担当者の絞り込み。再描画すると入力中のフォーカスが外れるので、表示中の候補の hidden だけを切り替える
document.addEventListener("input", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.dataset.workCategorySearch) return;
  uiState.workCategorySearch = el.value;
  const q = el.value.trim().toLowerCase();
  el.closest(".pj-assignee-filter-panel")?.querySelectorAll("[data-work-category-opt-name]").forEach((opt) => {
    opt.hidden = !!q && !opt.dataset.workCategoryOptName.includes(q);
  });
});

// 巨大 change ハンドラには足さず独立して拾う
document.addEventListener("change", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement)) return;
  if (el.dataset.workCategoryToggle) {
    const proj = workCategoriesTargetProject(el);
    if (!proj) return;
    const wc = workCategoriesDraftOrSaved(proj);
    const cat = el.dataset.workCategoryToggle;
    // チェックはプルダウンの外側。15n の外側クリックで閉じた後の toggle は再描画で外れた古い要素に届くため
    // 開閉の記憶が残り、閉じたはずのプルダウンが開いて描かれる。ここで明示的に閉じる
    uiState.workCategoryPickerOpenPid = "";
    const categories = el.checked ? [...wc.categories, cat] : wc.categories.filter((c) => c !== cat);
    setProjectWorkCategoriesDraft(proj, {
      categories: PROJECT_WORK_CATEGORY_OPTIONS.filter((c) => categories.includes(c)),
      members: wc.members
    });
    return;
  }
  if (el.dataset.workCategoryMember) {
    const proj = workCategoriesTargetProject(el);
    if (!proj) return;
    const wc = workCategoriesDraftOrSaved(proj);
    const userId = el.dataset.workCategoryMember;
    const others = wc.members.filter((id) => String(id) !== String(userId));
    // サーバー側の上限（20件）と揃える。超えた分が保存後リロードで無言で消えないように
    if (el.checked && others.length >= 20) {
      el.checked = false;
      showToast("これ以上追加できません（最大20人）", { variant: "error" });
      return;
    }
    setProjectWorkCategoriesDraft(proj, { categories: wc.categories, members: el.checked ? [...others, userId] : others });
  }
});
// ─────────────────────────────────────────────────────────────
// 担当課題一覧（自分の担当課題／関わった課題）の並び替え
//   - 期限順（従来の並び・既定）と課題番号順を切り替える
//   - イベントは独立リスナー（16 の巨大 click ハンドラには足さない）
// ─────────────────────────────────────────────────────────────

/** 並び替え項目。既定は期限（従来どおり「期限が近い順 → 更新が新しい順」） */
const PM_MY_ASSIGN_SORT_OPTIONS = [
  ["due", "期限"],
  ["no", `${PM_WORD.issue}番号`],
];

/** 並び替え設定の保存先キー。アカウントごとに分ける（共用PCで他人の設定と混ざらないように）。
 *  サーバー（DB）には持たせないので、PC とスマホでは別々に貯まる
 *  ＝最近使った担当者（fp.pm.recentAssignees.*）と同じ方針。 */
function progressMyAssignSortStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.myAssignSort.${me}` : "";
}

/** 並び替え状態を正規化する。未知値・壊れた保存値は既定（期限・昇順）へ丸める */
function normalizeProgressMyAssignSort(raw) {
  const key = PM_MY_ASSIGN_SORT_OPTIONS.some(([v]) => v === raw?.key) ? raw.key : "due";
  const dir = raw?.dir === "desc" ? "desc" : "asc";
  return { key, dir };
}

/** localStorage から読み込み済みの保存先キー。別アカウントでログインし直したら読み直す */
let pmMyAssignSortLoadedKey = null;

/** 並び替え状態。初回（とアカウント切替後）は保存値を復元する */
function ensureProgressMyAssignSortState() {
  const storeKey = progressMyAssignSortStorageKey();
  if (storeKey && pmMyAssignSortLoadedKey !== storeKey) {
    pmMyAssignSortLoadedKey = storeKey;
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(storeKey) || "null");
    } catch {
      /* 壊れた値は既定へ丸めるだけでよい */
    }
    uiState.progressMyAssignSort = normalizeProgressMyAssignSort(stored);
    return uiState.progressMyAssignSort;
  }
  const cur = uiState.progressMyAssignSort;
  const next = normalizeProgressMyAssignSort(cur);
  if (!cur || cur.key !== next.key || cur.dir !== next.dir) uiState.progressMyAssignSort = next;
  return uiState.progressMyAssignSort;
}

/** 選んだ並び順をこのアカウント用に保存する。次回ログイン時もこの並びで開く */
function saveProgressMyAssignSort() {
  const storeKey = progressMyAssignSortStorageKey();
  if (!storeKey) return;
  try {
    localStorage.setItem(storeKey, JSON.stringify(normalizeProgressMyAssignSort(uiState.progressMyAssignSort)));
  } catch {
    /* 保存できなくても並び替えそのものは動くので握りつぶす */
  }
}

/** 課題番号（表示上の "#123"）。並び替えの比較に使う */
function progressAgendaSortNumber(ag) {
  const n = Number(ag?.id);
  return Number.isFinite(n) ? n : 0;
}

/** 担当課題の行を key/dir で並び替える。
 *  - 同値は「更新が新しい順」で安定させる（従来のタイブレークをそのまま踏襲）
 *  - 期限なしは向きに関わらず末尾に置く。「期限が遠い順」で先頭に来ると探しにくいため */
function sortProgressMyAssignRows(rows, key, dir) {
  const asc = dir !== "desc";
  return [...rows].sort((a, b) => {
    if (key === "no") {
      const d = progressAgendaSortNumber(a.ag) - progressAgendaSortNumber(b.ag);
      if (d !== 0) return asc ? d : -d;
      return b.updatedTs - a.updatedTs;
    }
    const aNoDue = !Number.isFinite(a.dueSort);
    const bNoDue = !Number.isFinite(b.dueSort);
    if (aNoDue !== bNoDue) return aNoDue ? 1 : -1;
    if (!aNoDue && a.dueSort !== b.dueSort) {
      const d = a.dueSort - b.dueSort;
      return asc ? d : -d;
    }
    return b.updatedTs - a.updatedTs;
  });
}

/** 番号で検索したとき（"475" / "#475"）、その課題を一覧の先頭へ出す。
 *  番号で探す人は「その課題を開きたい」のであって一覧を眺めたいわけではないので、
 *  選択中の並び替えに埋もれさせない。並び替えそのものは変えず、完全一致の行だけを前に出す。 */
function pinExactIssueNumberFirst(rows, qNorm) {
  const wantNo = progressSearchIssueNumber(qNorm);
  if (wantNo === null) return rows;
  const hit = [];
  const rest = [];
  for (const r of rows) (Number(r?.ag?.id) === wantNo ? hit : rest).push(r);
  return hit.length ? [...hit, ...rest] : rows;
}

/** 並び替えUI（項目プルダウン＋昇順/降順トグル）。
 *  カードは PC/SP 共通で列ヘッダーが無いため、プルダウンだけで両方から操作できるようにする */
function progressMyAssignSortControlHtml() {
  const { key, dir } = ensureProgressMyAssignSortState();
  const opts = PM_MY_ASSIGN_SORT_OPTIONS.map(
    ([v, label]) =>
      `<option value="${escapeHtmlAttr(v)}" ${v === key ? "selected" : ""}>${escapeHtml(label)}</option>`
  ).join("");
  return `<div class="pm-my-assign-sort">
    <span class="pm-my-assign-sort-label">並び替え</span>
    <select class="pj-input pm-my-assign-sort-select" data-search-dropdown-off="1" data-pm-my-assign-sort-key="1" aria-label="並び替える項目">${opts}</select>
    <button type="button" class="inline-btn ghost pm-my-assign-sort-dir" data-pm-my-assign-sort-dir="1" title="昇順・降順を切り替え">${dir === "asc" ? "昇順 ▲" : "降順 ▼"}</button>
  </div>`;
}

document.addEventListener("change", (e) => {
  const t = e.target;
  if (t instanceof HTMLSelectElement && t.hasAttribute("data-pm-my-assign-sort-key")) {
    ensureProgressMyAssignSortState().key = t.value || "due";
    saveProgressMyAssignSort();
    render();
  }
});

document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (t.closest("[data-pm-my-assign-sort-dir]")) {
    const srt = ensureProgressMyAssignSortState();
    srt.dir = srt.dir === "asc" ? "desc" : "asc";
    saveProgressMyAssignSort();
    render();
  }
});
// ─────────────────────────────────────────────────────────────
// 一覧の複数選択プルダウン（担当者・受注・タグ等の <details>）を外側クリックで閉じる
//   - 従来は ▲ を押し直さないと開いたまま残り、他のプルダウンと重なっていた
//   - 別のプルダウンを開いたときも、開いていた方を閉じる
//   - イベントは独立リスナー（16 の巨大 click ハンドラには足さない）
// ─────────────────────────────────────────────────────────────

document.addEventListener("click", (e) => {
  const sel = "details.pj-assignee-filter-details[open], details.pj-tag-filter-details[open]";
  if (!document.querySelector(sel)) return;
  const own =
    e.target instanceof Element ? e.target.closest(".pj-assignee-filter-details, .pj-tag-filter-details") : null;
  document.querySelectorAll(sel).forEach((d) => {
    if (d === own) return;
    d.open = false;
    // 案件一覧のプルダウンは開閉を uiState に持つ（15n-project-list-filter-dd.js）。toggle は後から非同期で届くため、
    // 同じクリックで再描画が走ると旧要素ごと消えて届かず、閉じたはずのプルダウンが開いた状態で描き直される。ここで同期で消す
    if (d.dataset.pjFilterDd && uiState.projectListOpenFilterDd === d.dataset.pjFilterDd) uiState.projectListOpenFilterDd = "";
  });
});
// ────────────────────────────────────────────────────────────────
// ToDo課題コメントのリアクション（👍👀🙇…・Slack風）
//
// 描画は 14-expense-payment-forms.js progressMessageReactionsHtml（課題詳細スレッド
// progressIssueMessageRowHtml と SPカードのコメント一覧 progressSpMessageRowHtml の両方が呼ぶ）。
// ここはクリック処理だけを持つ。16-render-routing.js の巨大 click ハンドラには足さず、
// 機能別に独立リスナーを置く（CLAUDE.md）。
//
// 挙動:
//   - 🙂＋ … 絵文字パレットを開閉（開閉状態は uiState.progressReactPickerMsgId）
//   - チップ / パレットの絵文字 … 自分のリアクションをトグル。楽観更新→API失敗時は巻き戻し
//   - パレット外クリック … パレットを閉じる
// 再描画はメッセージ単位（[data-pm-react-wrap] の outerHTML 差し替え）のみ。
// スレッド全体を innerHTML で作り直すと、別コメントの編集中テキストが消えるためやらない。
// リアクションは通知・未読には数えない（サーバ側も touchAgendaActivity を呼ばない）。
// ────────────────────────────────────────────────────────────────

/** メッセージidから {agenda, msg} を引く（読み込み済みの全テーマを走査） */
function pmReactFindMessageById(msgId) {
  const n = Number(msgId);
  if (!Number.isFinite(n) || n < 1) return null;
  for (const c of uiState.progressChallenges || []) {
    for (const ag of Array.isArray(c.agendas) ? c.agendas : []) {
      const msg = (Array.isArray(ag.messages) ? ag.messages : []).find((x) => Number(x.id) === n);
      if (msg) return { agenda: ag, msg };
    }
  }
  return null;
}

/** 該当メッセージのリアクション行だけを描き直す（スレッド全体は触らない） */
function pmReactRefreshMsgDom(msgId) {
  const hit = pmReactFindMessageById(msgId);
  if (!hit) return;
  let wrap;
  try {
    wrap = document.querySelector(`[data-pm-react-wrap="${CSS.escape(String(msgId))}"]`);
  } catch {
    wrap = document.querySelector(`[data-pm-react-wrap="${msgId}"]`);
  }
  if (wrap instanceof HTMLElement) wrap.outerHTML = progressMessageReactionsHtml(hit.msg);
}

/** パレットを閉じ、開いていたメッセージのリアクション行を描き直す */
function pmReactClosePalette() {
  const msgId = uiState.progressReactPickerMsgId;
  uiState.progressReactPickerMsgId = "";
  if (msgId) pmReactRefreshMsgDom(msgId);
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;

  // タッチ端末向け: コメントをタップしたら🙂＋を表示（hover の代替。表示制御は
  // style.css の @media (hover: none) が担い、ホバー端末ではこのクラスは何もしない）
  const revealRow = t?.closest(".pm-msg, .pm-sp-msg");
  document.querySelectorAll(".pm-react-reveal").forEach((el) => {
    if (el !== revealRow) el.classList.remove("pm-react-reveal");
  });
  if (revealRow) revealRow.classList.add("pm-react-reveal");

  // 🙂＋ … パレット開閉
  const openBtn = t?.closest("[data-pm-react-open]");
  if (openBtn) {
    const mid = openBtn.getAttribute("data-pm-react-open") || "";
    const prev = String(uiState.progressReactPickerMsgId || "");
    uiState.progressReactPickerMsgId = prev === mid ? "" : mid;
    if (prev && prev !== mid) pmReactRefreshMsgDom(prev);
    pmReactRefreshMsgDom(mid);
    return;
  }

  // チップ / パレットの絵文字 … トグル
  const toggleBtn = t?.closest("[data-pm-react-toggle]");
  if (toggleBtn) {
    void pmReactToggle(toggleBtn);
    return;
  }

  // パレットの外側クリックで閉じる
  if (uiState.progressReactPickerMsgId && !t?.closest("[data-pm-react-wrap]")) {
    pmReactClosePalette();
  }
});

/** リアクションのトグル本体（楽観更新→API→失敗時は該当1件だけ巻き戻し） */
async function pmReactToggle(btn) {
  const msgId = Number(btn.getAttribute("data-pm-react-toggle"));
  const emoji = btn.getAttribute("data-pm-react-emoji") || "";
  const myDbId = progressCurrentUserDbId();
  if (!Number.isFinite(msgId) || !emoji || myDbId == null) return;
  const hit = pmReactFindMessageById(msgId);
  if (!hit) return;
  const msg = hit.msg;

  // パレットから選んだ場合はパレットを閉じてからトグル
  if (String(uiState.progressReactPickerMsgId || "") === String(msgId)) {
    uiState.progressReactPickerMsgId = "";
  }

  if (!Array.isArray(msg.reactions)) msg.reactions = [];
  const idx = msg.reactions.findIndex((r) => Number(r.userId) === myDbId && r.emoji === emoji);
  const removed = idx >= 0;
  if (removed) {
    msg.reactions.splice(idx, 1);
  } else {
    msg.reactions.push({
      userId: myDbId,
      userName: String(currentUser?.name || "").trim(),
      emoji
    });
  }
  pmReactRefreshMsgDom(msgId);

  const res = await apiFetch("/api/progress-management", {
    method: "POST",
    body: { action: "toggleMessageReaction", messageId: msgId, emoji }
  });
  if (!res.ok) {
    // 巻き戻しは自分のこの1件だけ（配列まるごと差し戻すと、並行した別のトグルまで消える）
    const j = msg.reactions.findIndex((r) => Number(r.userId) === myDbId && r.emoji === emoji);
    if (removed) {
      if (j < 0) msg.reactions.push({ userId: myDbId, userName: String(currentUser?.name || "").trim(), emoji });
    } else if (j >= 0) {
      msg.reactions.splice(j, 1);
    }
    pmReactRefreshMsgDom(msgId);
    showToast(String(res.data?.error || "リアクションに失敗しました"), { variant: "error" });
  }
}
// ─────────────────────────────────────────────────────────────
// プロジェクト一覧の「★お気に入り」絞り込みチップ
//   - 旧「お気に入り」タブの置き換え（タブ切替でなく同一一覧のトグル絞り込みに。2026-08-26）
//   - イベントは独立リスナー（16 の巨大 click ハンドラには足さない）
//   - 絞り込み（★・種別）はアカウントごとに保存し、次に開いたとき前回の状態で表示する。
//     保存が無い初回は「★を付けたプロジェクトがあれば★絞り込みON」を既定にする
// ─────────────────────────────────────────────────────────────

document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  const chip = t.closest("[data-pm-proj-star-filter]");
  if (!(chip instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;
  uiState.progressProjListStarOnly = !uiState.progressProjListStarOnly;
  render();
});

/** 保存先キー。アカウントごとに分ける（共用PCで他人の設定と混ざらないように）。DB には持たせない＝端末ごと */
function progressProjListViewStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.projListView.${me}` : "";
}

/** 種別の保存値を正規化する。未知値（選択肢から消えた種別など）は「すべて」へ丸める */
function normalizeProgressProjListCategory(v) {
  const s = typeof v === "string" ? v.trim() : "";
  return s === "__none__" || PM_TODO_GROUP_CATEGORY_OPTIONS.includes(s) ? s : "";
}

/** 復元済みの保存先キーと、最後に書いた値（描画のたびに同じ値を書かない） */
let pmProjListViewLoadedKey = null;
let pmProjListViewSavedJson = null;

/** プロジェクト一覧の描画ごとに呼ぶ。初回（とアカウント切替後）は保存値を復元し、以降は変化を保存する */
function syncProgressProjListViewState(challenges) {
  const storeKey = progressProjListViewStorageKey();
  if (!storeKey) return;
  if (pmProjListViewLoadedKey !== storeKey) {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(storeKey) || "null");
    } catch {
      /* 壊れた値は保存なし扱い */
    }
    if (saved && typeof saved === "object") {
      uiState.progressProjListStarOnly = saved.starOnly === true;
      uiState.progressGroupListCategory = normalizeProgressProjListCategory(saved.category);
    } else {
      // 保存なし: ★の有無で既定を決めるので、通常一覧が読み込まれるまで判定を待つ
      const list = Array.isArray(challenges) ? challenges : [];
      if (normalizeProgressListMode(uiState.progressListMode) !== "active" || list.length === 0) return;
      // 前のアカウントの状態は引き継がない
      uiState.progressProjListStarOnly = list.some((c) => c.starredByMe && !c.archivedAt);
      uiState.progressGroupListCategory = "";
    }
    pmProjListViewLoadedKey = storeKey;
    pmProjListViewSavedJson = null;
  }
  const json = JSON.stringify({
    starOnly: !!uiState.progressProjListStarOnly,
    category: normalizeProgressProjListCategory(uiState.progressGroupListCategory)
  });
  if (pmProjListViewSavedJson === json) return;
  pmProjListViewSavedJson = json;
  try {
    localStorage.setItem(storeKey, json);
  } catch {
    /* 保存できなくても絞り込み自体はこのタブの中で動く */
  }
}
// ─────────────────────────────────────────────────────────────
// 案件一覧の複数選択プルダウン（担当者・受注・タグの <details>）
//   - チェックのたびに再描画するので、開いているプルダウンを uiState に持ち、開いたまま続けて選べるようにする
//   - パネル先頭に「すべて選択／すべて外す」
//   - 表示欄に選んだ値をすべてチップで出し、×で外せる（2026-10 要望）
//   - イベントは独立リスナー（16 の巨大 click ハンドラには足さない）。外側クリックで閉じるのは 15n-filter-dropdown-close.js
// ─────────────────────────────────────────────────────────────

/** プルダウンの種類ごとの、選択中の値・書き戻し・「すべて選択」で入れる値 */
function pjFilterDdState(kind) {
  const f = uiState.projectListFilters;
  if (kind === "assignee") {
    return {
      get: () => sanitizeProjectListAssigneeFilterIds(f.assignedUserIds),
      set: (arr) => {
        f.assignedUserIds = sanitizeProjectListAssigneeFilterIds(arr);
        delete f.assignedUserId;
        delete f.assignedUserNameQuery;
      },
      all: () => assignableUsers().filter((u) => !u.searchExcluded).map((u) => String(u.id))
    };
  }
  if (kind === "order") {
    return {
      get: () => (Array.isArray(f.orderStatuses) ? f.orderStatuses : []),
      // 並びは選択肢の順に揃える（チップの並びが選んだ順で揺れないように）
      set: (arr) => {
        f.orderStatuses = PROJECT_ORDER_STATUS_LIST.filter((s) => arr.includes(s));
      },
      all: () => [...PROJECT_ORDER_STATUS_LIST]
    };
  }
  if (kind === "tag") {
    return {
      get: () => (Array.isArray(f.filterTags) ? f.filterTags : []),
      set: (arr) => {
        f.filterTags = arr;
      },
      all: () => collectProjectListTagFilterOptions()
    };
  }
  return null;
}

/** details に付ける属性（種類と、再描画前に開いていたら open） */
function pjFilterDdAttrs(kind) {
  return ` data-pj-filter-dd="${kind}"${uiState.projectListOpenFilterDd === kind ? " open" : ""}`;
}

/** パネル先頭の「すべて選択／すべて外す」。noneTitle で「外すと何が出るか」を添える（担当者は全員・受注は失注以外） */
function pjFilterDdBulkHtml(kind, noneTitle) {
  return `<div class="pj-filter-dd-bulk">
    <button type="button" class="pj-filter-dd-bulk-btn" data-pj-filter-dd-bulk="all" data-pj-filter-dd-kind="${kind}">すべて選択</button>
    <button type="button" class="pj-filter-dd-bulk-btn" data-pj-filter-dd-bulk="none" data-pj-filter-dd-kind="${kind}" title="${escapeHtmlAttr(noneTitle)}">すべて外す</button>
  </div>`;
}

/** 表示欄のチップ。選んだものはどれも×で外せるよう全件出し、表示欄の中で折り返す。
 *  × は summary の中なので button でなく role=button の span にする */
function pjFilterDdChipsHtml(kind, items) {
  const chips = items
    .map(
      (it) =>
        `<span class="pj-filter-dd-chip"><span class="pj-filter-dd-chip__text">${escapeHtml(it.label)}</span><span class="pj-filter-dd-chip__x" role="button" tabindex="0" aria-label="${escapeHtmlAttr(it.label)}を外す" data-pj-filter-dd-chip-remove="${escapeHtmlAttr(it.value)}" data-pj-filter-dd-kind="${kind}">×</span></span>`
    )
    .join("");
  return `<span class="pj-filter-dd-chips">${chips}</span>`;
}

// <details> の toggle はバブリングしないので capture で拾う
document.addEventListener(
  "toggle",
  (e) => {
    const el = e.target;
    if (!(el instanceof HTMLDetailsElement) || !el.dataset.pjFilterDd) return;
    const kind = el.dataset.pjFilterDd;
    if (el.open) uiState.projectListOpenFilterDd = kind;
    else if (uiState.projectListOpenFilterDd === kind) uiState.projectListOpenFilterDd = "";
  },
  true
);

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || currentRoute().page !== "projects") return;
  const bulk = t.closest("[data-pj-filter-dd-bulk]");
  const chipX = bulk ? null : t.closest("[data-pj-filter-dd-chip-remove]");
  if (!bulk && !chipX) return;
  // チップの × は summary の中にあるので、既定動作（プルダウンの開閉）を止める
  e.preventDefault();
  const st = pjFilterDdState((bulk || chipX).dataset.pjFilterDdKind);
  if (!st) return;
  if (bulk) {
    st.set(bulk.dataset.pjFilterDdBulk === "all" ? st.all() : []);
  } else {
    const v = chipX.dataset.pjFilterDdChipRemove;
    st.set(st.get().filter((x) => String(x) !== v));
  }
  setProjectListPage(1);
  render();
});

// 顧客の × で「顧客：すべて」に戻す（隠したとき・リセットと同じ値に）
document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || !t.closest("[data-pj-filter-client-clear]") || currentRoute().page !== "projects") return;
  Object.assign(uiState.projectListFilters, PROJECT_FILTER_SPEC.fields.find((d) => d.key === "client").clear);
  setProjectListPage(1);
  render();
});

// チップの × をキーボードでも外せるように（Enter / Space）
document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const t = e.target;
  if (!(t instanceof HTMLElement) || !t.matches("[data-pj-filter-dd-chip-remove]")) return;
  e.preventDefault();
  t.click();
});
// ────────────────────────────────────────────────────────────────
// 在席ステータス（左下ユーザーメニューの自分の設定 ＋「全員の在席状況」モーダル ＋ アバターのドット）
//
// 2軸で持つ。混ぜると「手動と自動が同じ値を奪い合う」ので必ず分ける（定義は 00-shared-enums.js）。
//   ① 稼働状態（自動）… PC の操作から判定。自己申告できない。稼働中 / 離席中 / オフライン
//   ② ステータス（自己申告）… ワンタッチ（打合せ中）または「ステータスを設定」モーダルの自由入力。
//      モーダルには Slack 風のテンプレ（絵文字＋期限）を置く。期限（開始/終了）は読み出し時に判定し、
//      過ぎていれば「なし」として扱う（DB に状態遷移を持たない＝cron 不要）。
//
// 読み書きの窓口は /api/heartbeat だけ（5分ごとに全員が叩くので、専用のポーリングを増やさない）。
// render()/uiState の描画サイクルには乗せない（15f-help-guide.js と同じ独立UI方式）。
// 🔴 ハートビート応答で render() を呼ぶと5分ごとに全画面フル再描画になるため、
//    反映は「前回と変化した要素だけ」を書き換える。
// ────────────────────────────────────────────────────────────────

/** 最後に操作した時刻。pointerdown / keydown / wheel だけを見る。
 *  scroll イベントやマウス移動は「席にいる」証拠にならない（プログラムのスクロールや机の振動でも発火する）。
 *  wheel はホイール/トラックパッドを人が触ったときだけ出るので、読むだけの閲覧中も稼働中にできる。 */
let fpPresenceLastInput = Date.now();
function fpPresenceLastInputAt() {
  return fpPresenceLastInput;
}
function fpPresenceOnInput() {
  fpPresenceLastInput = Date.now();
  fpPresenceRefreshSelf();
}
document.addEventListener("pointerdown", fpPresenceOnInput, { capture: true, passive: true });
document.addEventListener("keydown", fpPresenceOnInput, { capture: true, passive: true });
document.addEventListener("wheel", fpPresenceOnInput, { capture: true, passive: true });

/** DBユーザーID -> { state, status } */
const fpPresenceMap = new Map();
/** 自分の自己申告ステータス（"" = なし）と、その表示期間（0 = 指定なし） */
let fpPresenceSelfStatus = "";
let fpPresenceSelfFrom = 0;
let fpPresenceSelfUntil = 0;

/** 自分の DB ユーザーID（users 行の dbId と同じ体系） */
function fpPresenceMyDbId() {
  const v = typeof currentUser !== "undefined" && currentUser ? currentUser.dbId : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** 自分の稼働状態はサーバの往復を待たずローカルで出す（自分の操作は自分が一番よく知っている） */
function fpPresenceSelfState() {
  const now = Date.now();
  return resolveUserActivityState(now, fpPresenceLastInput, now);
}

/** 自分のステータスのうち、いま表示してよいもの（開始前・期限切れは ""） */
function fpPresenceSelfVisibleStatus() {
  return presenceStatusVisible(fpPresenceSelfFrom, fpPresenceSelfUntil, Date.now()) ? fpPresenceSelfStatus : "";
}

function fpPresenceStateClass(state) {
  if (state === "稼働中") return "is-active";
  if (state === "離席中") return "is-away";
  return "";
}

/** アバターのドットに出す状態。自分だけはローカル判定（左下メニューの「稼働中（自動）」と同じ値）。
 *  サーバの値は最大5分前のスナップショットなので、自分に使うと「操作中なのに離席中」とズレる（実報告 2026-10-06）。 */
function fpPresenceDotState(id) {
  if (id === fpPresenceMyDbId()) return fpPresenceSelfState();
  const p = fpPresenceMap.get(id);
  return p ? p.state : "";
}

/** アバターに差し込む状態ドット。状態が取れない/オフラインのときは CSS で非表示になる */
function fpPresenceDotHtml(u, extraClass = "") {
  const id = u && u.dbId != null ? Number(u.dbId) : NaN;
  if (!Number.isFinite(id)) return "";
  const cls = fpPresenceStateClass(fpPresenceDotState(id));
  return `<span class="fp-presence-dot${extraClass ? ` ${extraClass}` : ""}${cls ? ` ${cls}` : ""}" data-presence-user="${id}"></span>`;
}

/** 既に描かれているドットの状態クラスだけ差し替える（className ごと上書きすると
 *  サイズ違いの修飾クラスが消えるので、状態クラスだけを付け外しする）。selector で対象を絞れる */
function fpPresencePaintDots(selector = "[data-presence-user]") {
  document.querySelectorAll(selector).forEach((el) => {
    const cls = fpPresenceStateClass(fpPresenceDotState(Number(el.dataset.presenceUser)));
    el.classList.remove("is-active", "is-away");
    if (cls) el.classList.add(cls);
  });
}

/** 自分の稼働状態が変わったときだけ、自分のドットと左下メニューを描き直す（入力のたびに DOM を触らない）。
 *  離席から戻った瞬間はハートビートも送り、他の人の画面にも次の定期送信（最大5分）を待たず反映させる。 */
let fpPresenceSelfPainted = "";
let fpPresenceLastNudgeAt = 0;
function fpPresenceRefreshSelf() {
  const state = fpPresenceSelfState();
  if (state === fpPresenceSelfPainted) return;
  fpPresenceSelfPainted = state;
  const myId = fpPresenceMyDbId();
  if (myId != null) fpPresencePaintDots(`[data-presence-user="${myId}"]`);
  fpPresenceRenderSidebar();
  fpPresenceRenderRoster();
  if (state === "稼働中") fpPresenceNudgeHeartbeat();
}
/** 定期送信とは別に今すぐ送る。連打しないよう1分に1回まで */
function fpPresenceNudgeHeartbeat() {
  const now = Date.now();
  if (now - fpPresenceLastNudgeAt < 60 * 1000) return;
  fpPresenceLastNudgeAt = now;
  if (typeof fpSendHeartbeat === "function") fpSendHeartbeat();
}
// 稼働中→離席中は操作が無いことで起きるので、入力イベントでは拾えない。1分ごとに見る
setInterval(fpPresenceRefreshSelf, 60 * 1000);
// スリープ復帰・タブ復帰時は他の人の状態も古いので取り直す
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") fpPresenceNudgeHeartbeat();
});

/** ハートビート応答の反映。差分が無ければ一切描かない（5分ごとの再描画を防ぐ）。
 *  self は自分の行の生の値（期間つき）。一覧の status はサーバが期間を判定済みの表示値。 */
function fpApplyPresencePayload(list, self) {
  if (!Array.isArray(list)) return;
  let changed = false;
  const seen = new Set();
  for (const it of list) {
    const id = Number(it && it.userId);
    if (!Number.isFinite(id)) continue;
    seen.add(id);
    const state = String((it && it.state) || "");
    const status = String((it && it.status) || "");
    const cur = fpPresenceMap.get(id);
    if (!cur || cur.state !== state || cur.status !== status) {
      fpPresenceMap.set(id, { state, status });
      changed = true;
    }
  }
  for (const id of Array.from(fpPresenceMap.keys())) {
    if (!seen.has(id)) {
      fpPresenceMap.delete(id);
      changed = true;
    }
  }
  if (self && typeof self === "object") {
    fpPresenceSelfStatus = String(self.status || "");
    fpPresenceSelfFrom = Number(self.fromMs) || 0;
    fpPresenceSelfUntil = Number(self.untilMs) || 0;
  } else {
    const myId = fpPresenceMyDbId();
    const mine = myId != null ? fpPresenceMap.get(myId) : null;
    fpPresenceSelfStatus = mine ? mine.status : "";
    fpPresenceSelfFrom = 0;
    fpPresenceSelfUntil = 0;
  }
  if (!changed) return;
  fpPresencePaintDots();
  fpPresenceRenderSidebar();
  fpPresenceRenderRoster();
}

/** 左下メニューを閉じる（モーダルを開くときは二重表示にしない）。
 *  SPではハンバーガードロワー内から開くため、ドロワーごと閉じないとモーダルがドロワーの裏に出る */
function fpPresenceCloseSidebarMenus() {
  if (typeof closeSidebarFlyouts === "function") closeSidebarFlyouts();
}

/** モーダルを開く/閉じる共通手順。opacity のトランジションが終わってから hidden にする
 *  （閉じている間に再度開かれたら隠さない。wantOpen はそのためのフラグ）。 */
function fpPresenceShowOverlay(el) {
  el.hidden = false;
  requestAnimationFrame(() => el.classList.add("is-open"));
  document.body.classList.add("fp-presence-lock-scroll");
}
function fpPresenceHideOverlay(el, stillWantOpen) {
  el.classList.remove("is-open");
  document.body.classList.remove("fp-presence-lock-scroll");
  window.setTimeout(() => {
    if (!stillWantOpen()) el.hidden = true;
  }, 160);
}

// ── 左下ユーザーメニューの自分の設定 ────────────────────────────

/** index.html の #sidebarPresence に描く。メニューが閉じていても中身は保つ（開いた瞬間に正しい） */
function fpPresenceRenderSidebar() {
  const host = document.getElementById("sidebarPresence");
  if (!host) return;
  const state = fpPresenceSelfState();
  const status = fpPresenceSelfVisibleStatus();
  // ワンタッチ（打合せ中）と「ステータスを設定」の2つを等幅で横並び。設定側の欄には、ワンタッチ以外の現在値だけを映す
  const isQuick = PRESENCE_QUICK_STATUSES.includes(status);
  const quick = PRESENCE_QUICK_STATUSES.map((v) => {
    const on = status === v;
    return `<button type="button" class="fp-presence-chip${on ? " is-on" : ""}" data-presence-set="${escapeHtmlAttr(v)}" aria-pressed="${on ? "true" : "false"}" title="${on ? "押すと解除" : "押すと設定"}">${escapeHtml(v)}</button>`;
  }).join("");
  const freeText = !isQuick && status ? status : "";
  host.innerHTML = `
    <div class="fp-presence-self">
      <span class="fp-presence-dot ${fpPresenceStateClass(state)}"></span>
      <span class="fp-presence-self-txt">${escapeHtml(state)}（自動）</span>
    </div>
    <div class="fp-presence-row">
      ${quick}
      <button type="button" class="fp-presence-status-btn" data-presence-status-edit="1" title="ステータスを設定">
        <span class="fp-presence-status-txt${freeText ? "" : " is-empty"}">${freeText ? escapeHtml(freeText) : "ステータスを設定"}</span>
      </button>
    </div>
    <button type="button" class="sidebar-user-btn" data-presence-roster="1">
      <span class="sidebar-user-btn-icon" aria-hidden="true">👥</span>
      <span class="sidebar-user-btn-label">全員の在席状況</span>
    </button>
  `;
}

/** 自己申告ステータスを送る。押した瞬間に見た目を変え、応答で全員分を取り直す。
 *  opts.fromMs / opts.untilMs は表示期間（epoch ms・0 or 省略 = 指定なし）。 */
function fpPresenceSetStatus(next, opts) {
  const value = normalizePresenceStatusText(next);
  const fromMs = value && opts && Number(opts.fromMs) > 0 ? Number(opts.fromMs) : 0;
  const untilMs = value && opts && Number(opts.untilMs) > 0 ? Number(opts.untilMs) : 0;
  if (value === fpPresenceSelfStatus && fromMs === fpPresenceSelfFrom && untilMs === fpPresenceSelfUntil) return;
  fpPresenceSelfStatus = value;
  fpPresenceSelfFrom = fromMs;
  fpPresenceSelfUntil = untilMs;
  const myId = fpPresenceMyDbId();
  if (myId != null) {
    const cur = fpPresenceMap.get(myId);
    fpPresenceMap.set(myId, { state: cur ? cur.state : fpPresenceSelfState(), status: fpPresenceSelfVisibleStatus() });
  }
  fpPresenceRenderSidebar();
  fpPresenceRenderRoster();
  if (typeof fpSendHeartbeat === "function") {
    fpSendHeartbeat({ setStatus: value, setStatusFrom: fromMs || null, setStatusUntil: untilMs || null });
  }
}

// ── 「ステータスを設定」モーダル（自由入力＋テンプレ＋期間） ─────────

let fpPresenceStatusEl = null;
let fpPresenceStatusWantOpen = false;

function fpPresenceBuildStatusModal() {
  const el = document.createElement("div");
  el.className = "fp-presence-overlay fp-presence-overlay--status";
  el.id = "fpPresenceStatusOverlay";
  el.hidden = true;
  document.body.appendChild(el);
  return el;
}

/** <input type="date"> 用の YYYY-MM-DD（端末のローカル日付） */
function fpPresenceDateInputValue(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/** YYYY-MM-DD → その日のローカル 00:00 / 23:59:59.999 の epoch ms。不正なら 0 */
function fpPresenceDayBounds(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return { start: 0, end: 0 };
  const start = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).getTime();
  const end = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999).getTime();
  return Number.isFinite(start) ? { start, end } : { start: 0, end: 0 };
}

/** いまの自分の期間指定を、モーダルの選択肢に戻す（1h は復元できないので「日時まで」表示に丸める）。
 *  「今日中」は開始なし・終了がその日の 23:59（以前は 1時間 に戻り、そのまま保存すると1時間後に消えていた） */
function fpPresenceCurrentPeriod() {
  if (!fpPresenceSelfStatus || !fpPresenceSelfUntil) return "none";
  if (fpPresenceSelfFrom) return "range";
  const until = new Date(fpPresenceSelfUntil);
  return until.getHours() === 23 && until.getMinutes() === 59 ? "today" : "1h";
}

function fpPresenceRenderStatusModal() {
  if (!fpPresenceStatusEl || fpPresenceStatusEl.hidden) return;
  const current = fpPresenceSelfStatus;
  const period = fpPresenceCurrentPeriod();
  const now = Date.now();
  const rangeFrom = fpPresenceDateInputValue(fpPresenceSelfFrom || now);
  const rangeUntil = fpPresenceDateInputValue(fpPresenceSelfUntil || now + 24 * 60 * 60 * 1000);
  const templates = PRESENCE_STATUS_TEMPLATES.map(
    (t) => `
        <button type="button" class="fp-presence-tpl-btn" data-presence-tpl="${escapeHtmlAttr(t.key)}">
          <span class="fp-presence-tpl-emoji" aria-hidden="true">${t.emoji}</span>
          <span class="fp-presence-tpl-label">${escapeHtml(t.label)}</span>
          ${t.note ? `<span class="fp-presence-tpl-note">— ${escapeHtml(t.note)}</span>` : ""}
        </button>`
  ).join("");
  fpPresenceStatusEl.innerHTML = `
    <div class="fp-presence-backdrop" data-presence-status-close="1"></div>
    <div class="fp-presence-panel fp-presence-panel--status" role="dialog" aria-modal="true" aria-label="ステータスを設定">
      <div class="fp-presence-head">
        <h2>ステータスを設定</h2>
        <button type="button" class="fp-presence-close" data-presence-status-close="1" aria-label="閉じる">×</button>
      </div>
      <div class="fp-presence-status-body">
        <input type="text" class="fp-presence-status-input" data-presence-status-input="1"
               value="${escapeHtmlAttr(current)}" maxlength="${PRESENCE_STATUS_MAX_LEN}"
               placeholder="ステータスを入力" aria-label="ステータス" autocomplete="off" />
        <p class="fp-presence-status-hint">全員の在席状況に表示されます。</p>
        <div class="fp-presence-tpl" role="list">${templates}</div>
        <div class="fp-presence-period">
          <label class="fp-presence-period-head" for="fpPresencePeriodSelect">期間</label>
          <select id="fpPresencePeriodSelect" class="fp-presence-period-select" data-presence-period="1">
            <option value="none"${period === "none" ? " selected" : ""}>期限なし</option>
            <option value="1h"${period === "1h" ? " selected" : ""}>1時間後に消す</option>
            <option value="today"${period === "today" ? " selected" : ""}>今日中</option>
            <option value="range"${period === "range" ? " selected" : ""}>開始日と終了日を指定</option>
          </select>
          <div class="fp-presence-range" data-presence-range="1"${period === "range" ? "" : " hidden"}>
            <label class="fp-presence-range-item"><span>開始</span><input type="date" data-presence-range-from="1" value="${rangeFrom}" /></label>
            <label class="fp-presence-range-item"><span>終了</span><input type="date" data-presence-range-until="1" value="${rangeUntil}" /></label>
          </div>
        </div>
      </div>
      <div class="fp-presence-status-foot">
        ${current ? `<button type="button" class="inline-btn ghost" data-presence-status-clear="1">クリア</button>` : ""}
        <span class="fp-presence-status-foot-spacer"></span>
        <button type="button" class="inline-btn ghost" data-presence-status-close="1">キャンセル</button>
        <button type="button" class="inline-btn" data-presence-status-save="1">保存する</button>
      </div>
    </div>
  `;
}

function fpPresenceOpenStatusModal() {
  fpPresenceStatusWantOpen = true;
  if (!fpPresenceStatusEl) fpPresenceStatusEl = fpPresenceBuildStatusModal();
  fpPresenceStatusEl.hidden = false;
  fpPresenceRenderStatusModal();
  fpPresenceShowOverlay(fpPresenceStatusEl);
  const input = fpPresenceStatusEl.querySelector("[data-presence-status-input]");
  if (input instanceof HTMLInputElement) {
    input.focus();
    input.select();
  }
}

function fpPresenceCloseStatusModal() {
  if (!fpPresenceStatusEl) return;
  fpPresenceStatusWantOpen = false;
  fpPresenceHideOverlay(fpPresenceStatusEl, () => fpPresenceStatusWantOpen);
}

/** 期間セレクトに合わせて開始/終了日の欄を出し入れする（再描画せず DOM だけ切り替える＝入力中の文言を消さない） */
function fpPresenceSyncRangeVisibility() {
  const sel = fpPresenceStatusEl && fpPresenceStatusEl.querySelector("[data-presence-period]");
  const range = fpPresenceStatusEl && fpPresenceStatusEl.querySelector("[data-presence-range]");
  if (sel instanceof HTMLSelectElement && range instanceof HTMLElement) range.hidden = sel.value !== "range";
}

/** テンプレを押す: 入力欄に「絵文字＋文言」、期間の初期値（1時間 / 期間指定 / なし）を選ぶ。文言は編集してよい */
function fpPresenceApplyTemplate(key) {
  const t = PRESENCE_STATUS_TEMPLATES.find((x) => x.key === key);
  if (!t || !fpPresenceStatusEl) return;
  const input = fpPresenceStatusEl.querySelector("[data-presence-status-input]");
  const sel = fpPresenceStatusEl.querySelector("[data-presence-period]");
  if (input instanceof HTMLInputElement) input.value = `${t.emoji} ${t.label}`;
  if (sel instanceof HTMLSelectElement) sel.value = t.period;
  if (t.period === "range") {
    const from = fpPresenceStatusEl.querySelector("[data-presence-range-from]");
    const until = fpPresenceStatusEl.querySelector("[data-presence-range-until]");
    const now = Date.now();
    if (from instanceof HTMLInputElement && !from.value) from.value = fpPresenceDateInputValue(now);
    if (until instanceof HTMLInputElement && !until.value) until.value = fpPresenceDateInputValue(now + 24 * 60 * 60 * 1000);
  }
  fpPresenceSyncRangeVisibility();
  if (input instanceof HTMLInputElement) input.focus();
}

/** モーダルの入力を読んで保存する。期間は保存時刻を基準に epoch ms へ変換する */
function fpPresenceSaveStatusFromModal() {
  if (!fpPresenceStatusEl) return;
  const input = fpPresenceStatusEl.querySelector("[data-presence-status-input]");
  const sel = fpPresenceStatusEl.querySelector("[data-presence-period]");
  const text = input instanceof HTMLInputElement ? input.value : "";
  const period = sel instanceof HTMLSelectElement ? sel.value : "none";
  const now = Date.now();
  let fromMs = 0;
  let untilMs = 0;
  if (period === "1h") {
    untilMs = now + 60 * 60 * 1000;
  } else if (period === "today") {
    untilMs = fpPresenceDayBounds(fpPresenceDateInputValue(now)).end;
  } else if (period === "range") {
    const f = fpPresenceStatusEl.querySelector("[data-presence-range-from]");
    const u = fpPresenceStatusEl.querySelector("[data-presence-range-until]");
    const fb = fpPresenceDayBounds(f instanceof HTMLInputElement ? f.value : "");
    const ub = fpPresenceDayBounds(u instanceof HTMLInputElement ? u.value : "");
    fromMs = fb.start;
    untilMs = ub.end || fb.end;
    // 終了が開始より前なら入れ替える（入力ミスで永久に表示されない/されるのを防ぐ）
    if (fromMs && untilMs && untilMs < fromMs) {
      const t = fromMs;
      fromMs = fpPresenceDayBounds(u instanceof HTMLInputElement ? u.value : "").start;
      untilMs = fpPresenceDayBounds(f instanceof HTMLInputElement ? f.value : "").end || t;
    }
  }
  fpPresenceSetStatus(text, { fromMs, untilMs });
  fpPresenceCloseStatusModal();
}

// ── 「全員の在席状況」モーダル ──────────────────────────────────

let fpPresenceRosterEl = null;
let fpPresenceRosterWantOpen = false;
let fpPresenceRosterQuery = "";

function fpPresenceBuildRoster() {
  const el = document.createElement("div");
  el.className = "fp-presence-overlay";
  el.id = "fpPresenceOverlay";
  el.hidden = true;
  document.body.appendChild(el);
  return el;
}

/** 並びは「自分 → 稼働中 → 離席中 → オフライン」、同じ状態のなかは名前順。
 *  席の一覧なので、いま話しかけられる人が上に来るのが自然。 */
function fpPresenceRosterRows() {
  const myId = fpPresenceMyDbId();
  const order = { 稼働中: 0, 離席中: 1, オフライン: 2 };
  const q = fpPresenceRosterQuery.trim().toLowerCase();
  return assignableUsers()
    // 検証用アカウントは席に並べない（名簿は 00-shared-enums の PRESENCE_ROSTER_HIDDEN_NAMES）
    .filter((u) => !presenceRosterHiddenName(u.name))
    .filter((u) => !q || String(u.name || "").toLowerCase().includes(q))
    .map((u) => {
      const id = u.dbId != null ? Number(u.dbId) : NaN;
      const p = Number.isFinite(id) ? fpPresenceMap.get(id) : null;
      const isMe = myId != null && id === myId;
      return {
        u,
        isMe,
        state: isMe ? fpPresenceSelfState() : (p ? p.state : "オフライン"),
        status: isMe ? fpPresenceSelfVisibleStatus() : (p ? p.status : "")
      };
    })
    .sort((a, b) => {
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
      const d = (order[a.state] ?? 3) - (order[b.state] ?? 3);
      if (d !== 0) return d;
      return String(a.u.name || "").localeCompare(String(b.u.name || ""), "ja");
    });
}

function fpPresenceRenderRoster() {
  if (!fpPresenceRosterEl || fpPresenceRosterEl.hidden) return;
  const rows = fpPresenceRosterRows();
  const activeCount = rows.filter((r) => r.state === "稼働中").length;
  const cards = rows
    .map((r) => {
      // サブ行は 自己申告＞稼働状態 の優先で1行だけ出す（情報を積まない）。
      // 自分のカードも同じ規則＝自分で設定したステータスがここに見える。「自分」は名前横のバッジで示す
      // （以前はサブ行を「自分」固定にしていて、自分のステータスだけ在席状況で見えなかった）。
      const sub = r.status || r.state;
      const subCls = r.status ? " is-status" : "";
      return `
        <div class="fp-presence-card${r.state === "オフライン" ? " is-offline" : ""}${r.isMe ? " is-me" : ""}">
          ${progressUserAvatarHtml(r.u, "fp-presence-card-avatar", r.u.name)}
          <div class="fp-presence-card-body">
            <div class="fp-presence-card-name">
              <span>${escapeHtml(r.u.name || "—")}</span>
              ${r.isMe ? `<span class="fp-presence-card-me">自分</span>` : ""}
              <span class="fp-presence-dot fp-presence-dot--lg ${fpPresenceStateClass(r.state)}"></span>
            </div>
            <div class="fp-presence-card-sub${subCls}" title="${escapeHtmlAttr(sub)}">${escapeHtml(sub)}</div>
          </div>
        </div>`;
    })
    .join("");
  fpPresenceRosterEl.innerHTML = `
    <div class="fp-presence-backdrop" data-presence-roster-close="1"></div>
    <div class="fp-presence-panel" role="dialog" aria-modal="true" aria-label="在席状況">
      <div class="fp-presence-head">
        <h2>在席状況</h2>
        <button type="button" class="fp-presence-close" data-presence-roster-close="1" aria-label="閉じる">×</button>
      </div>
      <div class="fp-presence-tools">
        <input type="search" class="fp-presence-search" data-presence-search="1" placeholder="名前で検索"
               value="${escapeHtmlAttr(fpPresenceRosterQuery)}" aria-label="名前で検索" data-ime-search="1" />
        <span class="fp-presence-count">稼働中 ${activeCount}</span>
      </div>
      <div class="fp-presence-grid">${cards || `<p class="fp-presence-empty">該当する人がいません。</p>`}</div>
    </div>
  `;
  fpPresencePaintDots();
}

function fpPresenceOpenRoster() {
  fpPresenceRosterWantOpen = true;
  fpPresenceRosterQuery = "";
  if (!fpPresenceRosterEl) fpPresenceRosterEl = fpPresenceBuildRoster();
  fpPresenceRosterEl.hidden = false;
  fpPresenceRenderRoster();
  fpPresenceShowOverlay(fpPresenceRosterEl);
  // 開いた時点の状態を取りに行く（最大5分古いままにしない）
  if (typeof fpSendHeartbeat === "function") fpSendHeartbeat();
}

function fpPresenceCloseRoster() {
  if (!fpPresenceRosterEl) return;
  fpPresenceRosterWantOpen = false;
  fpPresenceHideOverlay(fpPresenceRosterEl, () => fpPresenceRosterWantOpen);
}

// ── イベント（16-render-routing.js の巨大ハンドラには足さない） ──

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const quick = target.closest("[data-presence-set]");
  if (quick instanceof HTMLElement) {
    e.preventDefault();
    // 同じものをもう一度押したら解除（トグル）。メニューは開いたままにして、間違えたらもう一度押して戻せるようにする。
    // 🔴 ここで同期に描き直すと押したボタンが DOM から外れ、後続の共通クリック処理（16-render-routing.js）が
    //    target.closest(".sidebar-user") を取れず「メニューの外をクリックした」と誤判定してメニューを閉じてしまう。
    //    そのため描き直しは1フレーム遅らせる（実報告 2026-09-09）。
    const v = quick.dataset.presenceSet || "";
    requestAnimationFrame(() => {
      fpPresenceSetStatus(fpPresenceSelfStatus === v ? "" : v);
    });
    return;
  }

  if (target.closest("[data-presence-status-edit]")) {
    e.preventDefault();
    fpPresenceCloseSidebarMenus();
    fpPresenceOpenStatusModal();
    return;
  }

  if (target.closest("[data-presence-roster]")) {
    e.preventDefault();
    fpPresenceCloseSidebarMenus();
    fpPresenceOpenRoster();
    return;
  }

  if (fpPresenceStatusEl && !fpPresenceStatusEl.hidden) {
    const tpl = target.closest("[data-presence-tpl]");
    if (tpl instanceof HTMLElement) { fpPresenceApplyTemplate(tpl.dataset.presenceTpl || ""); return; }
    if (target.closest("[data-presence-status-save]")) { fpPresenceSaveStatusFromModal(); return; }
    if (target.closest("[data-presence-status-clear]")) { fpPresenceSetStatus(""); fpPresenceCloseStatusModal(); return; }
    if (target.closest("[data-presence-status-close]")) { fpPresenceCloseStatusModal(); return; }
  }

  if (!fpPresenceRosterEl || fpPresenceRosterEl.hidden) return;
  if (target.closest("[data-presence-roster-close]")) fpPresenceCloseRoster();
});

document.addEventListener("change", (e) => {
  const el = e.target;
  if (el instanceof HTMLSelectElement && el.dataset.presencePeriod) fpPresenceSyncRangeVisibility();
});

// 検索は入力のたびに描き直す。IME 変換中は確定前の文字で絞らない
// （isComposing を見ないと「かいぎ」の途中で0件になる）
document.addEventListener("input", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.dataset.presenceSearch) return;
  if (e.isComposing) return;
  fpPresenceRosterQuery = el.value || "";
  const pos = el.selectionStart;
  fpPresenceRenderRoster();
  const next = fpPresenceRosterEl && fpPresenceRosterEl.querySelector("[data-presence-search]");
  if (next instanceof HTMLInputElement) {
    next.focus();
    try { next.setSelectionRange(pos, pos); } catch { /* type=search で失敗する環境は無視 */ }
  }
});

document.addEventListener("compositionend", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.dataset.presenceSearch) return;
  fpPresenceRosterQuery = el.value || "";
  fpPresenceRenderRoster();
  const next = fpPresenceRosterEl && fpPresenceRosterEl.querySelector("[data-presence-search]");
  if (next instanceof HTMLInputElement) next.focus();
});

document.addEventListener("keydown", (e) => {
  const statusOpen = fpPresenceStatusEl && !fpPresenceStatusEl.hidden;
  if (statusOpen) {
    // IME 変換確定の Enter で保存してしまわない（isComposing のガード）
    if (e.key === "Enter" && !e.isComposing && e.target instanceof HTMLInputElement && e.target.dataset.presenceStatusInput) {
      e.preventDefault();
      fpPresenceSaveStatusFromModal();
      return;
    }
    if (e.key === "Escape") fpPresenceCloseStatusModal();
    return;
  }
  if (e.key === "Escape" && fpPresenceRosterEl && !fpPresenceRosterEl.hidden) fpPresenceCloseRoster();
});
// ────────────────────────────────────────────────────────────────
// コメント入力欄の引用（行頭「> 」）のキー操作（Slack 風）
//
// 対象: 課題コメントの textarea（新規 [data-pm-msg-body]＝PC .pm-compose / SP .pm-sp-compose-sheet 共通、
// 投稿済みコメントの編集 [data-pm-edit-message-body]、課題の追加/編集モーダルの詳細 [data-pm-modal-agenda-body]）。
// 16-render-routing.js の巨大ハンドラには足さず、機能別に独立リスナーを置く（CLAUDE.md）。
//
// 挙動:
//   - 引用行で改行 … 次の行にも「> 」を引き継ぐ
//   - 「> 」だけの行で改行 … 「> 」を消して引用を抜ける（改行はしない）
//   - 「> 」の直後で Backspace … 「> 」だけを消す（引用を解除）
// keydown ではなく beforeinput（inputType）で見る。Android のソフトキーボードや IME 確定後は
// keydown が keyCode 229 / key "Unidentified" になり Enter/Backspace を判別できないため。
// @メンション候補の確定 Enter は 16-render-routing.js の keydown が preventDefault するので、
// その場合 beforeinput は発火せず、ここと二重に処理されることはない。
// 記法や描画は 04-user-nav-data.js（applyProgressComposeFormat / progressMessageQuoteBlocksHtml）。
// ────────────────────────────────────────────────────────────────

const PM_COMPOSE_QUOTE_MARKER = "> ";

/** 改行/Backspace を textarea の値に反映する。処理したら true（呼び出し側が preventDefault する）。
 *  action は "linebreak" | "backspace"。 */
function progressComposeQuoteEdit(ta, action) {
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
  const value = ta.value;
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const lineHead = value.slice(lineStart, start);
  const applyEdit = (from, to, text) => {
    ta.value = value.slice(0, from) + text + value.slice(to);
    const caret = from + text.length;
    try {
      ta.setSelectionRange(caret, caret);
    } catch {
      /* noop */
    }
    ta.dispatchEvent(new Event("input", { bubbles: true }));
    return true;
  };
  if (action === "linebreak") {
    if (!lineHead.startsWith(PM_COMPOSE_QUOTE_MARKER)) return false;
    // 複数行にまたがる選択は既定の動作（選択を改行で置換）に任せる
    if (value.slice(start, end).includes("\n")) return false;
    const lineEnd = value.indexOf("\n", end);
    const restOfLine = value.slice(end, lineEnd === -1 ? value.length : lineEnd);
    // 「> 」だけの行で改行 → 引用を抜ける（マーカーを消すだけで改行しない）
    if (start === end && lineHead === PM_COMPOSE_QUOTE_MARKER && restOfLine === "") {
      return applyEdit(lineStart, end, "");
    }
    return applyEdit(start, end, `\n${PM_COMPOSE_QUOTE_MARKER}`);
  }
  if (action === "backspace") {
    if (start !== end || lineHead !== PM_COMPOSE_QUOTE_MARKER) return false;
    return applyEdit(lineStart, start, "");
  }
  return false;
}

document.addEventListener("beforeinput", (e) => {
  const ta = e.target;
  if (!(ta instanceof HTMLTextAreaElement)) return;
  if (!ta.hasAttribute("data-pm-msg-body") && !ta.hasAttribute("data-pm-edit-message-body") && !ta.hasAttribute("data-pm-modal-agenda-body")) return;
  const type = e.inputType;
  const action = type === "insertLineBreak" || type === "insertParagraph" ? "linebreak" : type === "deleteContentBackward" ? "backspace" : "";
  if (!action || e.isComposing) return;
  if (progressComposeQuoteEdit(ta, action)) e.preventDefault();
});
// ── 原価登録ウィザード: PDFファイル群 → AI読取（失敗時は従来パーサ）→ プレビュー ──
// 16-render-routing.js の「取り込む」（ファイル選択）と 15r-invoice-mail-inbox.js（受信済み添付）の共用。
// inbox: 受信箱から来た場合 { itemId, files, docIds, subject }。docIds は fp_cost_source_documents の id（files と同じ順）で
//   登録時の再アップロードを省く。itemId は登録完了時に「登録済」へ更新する。プレビュー state にも載せ、「戻る」で持ち越せるようにする。
async function costImportPdfFilesToWizard(files, { sourceKey, targetMonth, choiceStr, inbox = null }) {
  const meta = COST_IMPORT_CSV_SOURCE_META[sourceKey];
  const sourceDocIds = inbox ? inbox.docIds : null;
  const inboxState = inbox
    ? { inboxItemId: inbox.itemId, inboxFiles: inbox.files, inboxDocIds: inbox.docIds, inboxSubject: inbox.subject }
    : {};
  const isGeneric = sourceKey === "generic";
  // 1ファイル分を AI（→失敗時は従来パーサ）で抽出する共通処理。
  // 複数PDF時は行ごとに請求元を解決するため、各行へ請求元(payeeFromDocument)を持たせる。
  const extractOnePdf = async (buf, base64, fileName) => {
    let parsedPdf = null;
    let docPayeeLine = "";
    let docInvoiceNo = ""; // AIが読んだ請求書No（請求書受取からの取込は受領済で入れるので、これも保存する）
    let aiUsed = false;
    let fxUnavailable = "";
    try {
      const aiResp = await fetch("/api/cost-extract-pdf-ai", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({ pdfBase64: base64, fileName: fileName || "invoice.pdf" })
      });
      if (aiResp.ok) {
        const aj = await aiResp.json();
        if (aj && aj.reason === "fx_unavailable") fxUnavailable = String(aj.currency || "外貨");
        if (aj && aj.ok && Array.isArray(aj.items) && aj.items.length) {
          const aiRows = aiItemsToCostImportParsedRows(aj.items, aj.payee, undefined, aj.fx, aj.subject);
          if (aiRows.length) {
            parsedPdf = consolidateCostImportParsedRows(aiRows);
            docPayeeLine = String(aj.payee || "");
            docInvoiceNo = String(aj.invoiceNo || "").trim();
            aiUsed = true;
          }
        }
      }
    } catch {
      /* フォールバックへ */
    }
    // 外貨建てでレートが取れなかった: 従来パーサは外貨額を円として読むのでフォールバックしない
    if (fxUnavailable) throw new Error(`${fxUnavailable}建ての請求書の円換算レートを取得できませんでした。時間をおいて再度お試しください`);
    if (!parsedPdf || !parsedPdf.length) {
      const txt = await extractPdfPlainTextPages(buf);
      if (costImportTextLooksForeignCurrency(txt)) {
        throw new Error("外貨建て（ドル等）の請求書はAI読取でのみ円換算できます。AI読取に失敗したため、時間をおいて再度お試しください");
      }
      docPayeeLine = extractPdfDocumentPayeeNameHint(txt);
      const fb = parseAdvertisingPdfTextHints(txt).map((r) =>
        r.payeeFromDocument ? r : { ...r, payeeFromDocument: docPayeeLine }
      );
      parsedPdf = consolidateCostImportParsedRows(fb);
      aiUsed = false;
    }
    return { parsedPdf: parsedPdf || [], docPayeeLine, docInvoiceNo, aiUsed };
  };
  try {
    // ── 単一PDF: 従来どおり（請求元プルダウンで一括補正できる） ──
    if (files.length === 1) {
      const file = files[0];
      const buf = await file.arrayBuffer();
      const base64 = arrayBufferToBase64(buf);
      showToastPersistent("AIでPDFを読み取り中…");
      const { parsedPdf, docPayeeLine, docInvoiceNo, aiUsed } = await extractOnePdf(buf, base64, file.name || "invoice.pdf");
      clearToast();
      if (!parsedPdf.length) {
        showToastError("金額を検出できませんでした。別のPDFか、別の取込方法をお試しください");
        render();
        return;
      }
      const docResolvedMeta = resolveCostVendorFromPartnersAndClients(docPayeeLine || "");
      const docUiKey = costImportResolveDocumentPayeeUiKeyFromMeta(docPayeeLine || "", docResolvedMeta);
      const pdfTag = aiUsed ? "AI · PDF" : "PDF";
      const wizPdf = {
        phase: "preview",
        mode: "other", // 旧「広告原価を登録」画面は廃止。戻るで媒体CSV専用画面に落ちないように常に「原価を登録」
        salesMode: uiState.costRegWizard?.salesMode === true,
        targetMonth,
        csvSourceKey: sourceKey,
        costImportChoice: choiceStr,
        previewKind: "pdf",
        sourceLabel: isGeneric ? `その他原価 · ${pdfTag}` : `${meta.label} · ${pdfTag}`,
        defaultVendor: isGeneric ? "PDF（その他原価）" : `${meta.vendor}（PDF）`,
        documentPayeeRaw: docPayeeLine,
        documentPayeeResolved: docResolvedMeta,
        documentPayeeUiKey: docUiKey,
        importCategory: isGeneric ? "外注費" : "広告費",
        importPdfBase64: base64,
        importPdfFileName: file.name || "invoice.pdf",
        importInvoiceNo: docInvoiceNo,
        // 受信箱からの取込: 添付PDFは受信時に保存済みなので再アップロードせずその docId を使う
        ...(sourceDocIds && sourceDocIds[0] ? { uploadedPdfDocId: sourceDocIds[0] } : {}),
        ...inboxState,
        rows: buildCostImportReviewRows(parsedPdf, { documentPayeeHint: docPayeeLine, targetMonth, sourceKey })
      };
      applyDocumentPayeeUiKeyToImportRows(wizPdf);
      uiState.costRegWizard = wizPdf;
      render();
      return;
    }
    // ── 複数PDF: 各ファイルを読み取り1つのプレビューに合算（請求元・元PDFは行ごと） ──
    const pdfSources = [];
    const combinedParsed = [];
    let anyAi = false;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      showToastPersistent(`AIでPDFを読み取り中… ${i + 1}/${files.length}枚`);
      const buf = await file.arrayBuffer();
      const base64 = arrayBufferToBase64(buf);
      const { parsedPdf, docInvoiceNo, aiUsed } = await extractOnePdf(buf, base64, file.name || `invoice-${i + 1}.pdf`);
      if (aiUsed) anyAi = true;
      const srcIndex = pdfSources.length;
      pdfSources.push({ base64, fileName: file.name || `invoice-${i + 1}.pdf`, invoiceNo: docInvoiceNo });
      for (const r of parsedPdf) combinedParsed.push({ ...r, pdfSourceIndex: srcIndex });
    }
    clearToast();
    if (!combinedParsed.length) {
      showToastError("金額を検出できませんでした。別のPDFか、別の取込方法をお試しください");
      render();
      return;
    }
    const pdfTag = anyAi ? "AI · PDF" : "PDF";
    uiState.costRegWizard = {
      phase: "preview",
      mode: "other", // 旧「広告原価を登録」画面は廃止。戻るで媒体CSV専用画面に落ちないように常に「原価を登録」
      salesMode: uiState.costRegWizard?.salesMode === true,
      targetMonth,
      csvSourceKey: sourceKey,
      costImportChoice: choiceStr,
      previewKind: "pdf",
      sourceLabel: isGeneric ? `その他原価 · ${pdfTag}×${files.length}` : `${meta.label} · ${pdfTag}×${files.length}`,
      defaultVendor: isGeneric ? "PDF（その他原価）" : `${meta.vendor}（PDF）`,
      importCategory: isGeneric ? "外注費" : "広告費",
      pdfSources,
      ...(sourceDocIds ? { sourceDocIdByIndex: sourceDocIds.slice() } : {}),
      ...inboxState,
      rows: buildCostImportReviewRows(combinedParsed, { targetMonth, sourceKey })
    };
    render();
  } catch (e) {
    clearToast();
    showToast(e instanceof Error ? e.message : "PDFの読み込みに失敗しました");
    render();
  }
}

// 取込プレビュー: 発注ステータス（全行共通）の選択（登録時の patch は 16 の costImportEffectiveOrderStatus が読む）
document.addEventListener("change", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLSelectElement) || t.dataset.costImportOrderStatusPick === undefined) return;
  const w = uiState.costRegWizard;
  if (!w || w.phase !== "preview") return;
  w.importOrderStatus = COST_ORDER_STATUSES.includes(t.value) ? t.value : "";
});
// ── 請求書受取（#invoice-receipt・経理/マスター/管理者） ──
// invoice@oe-onboarding.site に届いた請求書メールは SES → S3 → /api/invoice-mail-receive で
// fp_invoice_mail_inbox に溜まる（添付 PDF/画像は fp_cost_source_documents）。このページは
//   ・状態タブ（未処理/要確認/登録済/無視）＋絞り込み（キーワード・受信日の範囲）＋一覧
//   ・行を押すと右からドロワー（添付・本文・原本のハッシュ・紐づいた原価・変更履歴・操作）
//   ・「原価として登録」＝受信済みの添付PDFを既存の取込ウィザード（15q costImportPdfFilesToWizard）へ渡す
//   ・無視 / 未処理に戻す（状態の更新。サーバ側で履歴に残る）
// を扱う。登録完了時の「登録済」化は 16-render-routing.js の登録ハンドラから markInvoiceMailInboxItem を呼ぶ。
// バクラク「請求書受取」と同じ名称（ユーザー確定・2026-09-19）。

const INVOICE_MAIL_INBOX_STATUS_LABELS = { open: "未処理", check: "要確認", done: "登録済", ignored: "無視" };
/** 受付アドレス（表示用の既定）。実際の値は API（環境変数 INVOICE_MAIL_INBOX_ADDRESS。本番 invoice@ / ステージング invoice-staging@）が返す */
const INVOICE_MAIL_INBOX_ADDRESS = "invoice@oe-onboarding.site";
function invoiceReceiptAddress() {
  return String(uiState.invoiceReceipt?.address || "") || INVOICE_MAIL_INBOX_ADDRESS;
}

function invoiceReceiptState() {
  if (!uiState.invoiceReceipt || typeof uiState.invoiceReceipt !== "object") {
    uiState.invoiceReceipt = {
      status: "open",
      items: [],
      countByStatus: {},
      loaded: false,
      loading: false,
      error: "",
      q: "",
      from: "",
      to: "",
      detailId: null,
      detail: null,
      detailLoading: false
    };
  }
  return uiState.invoiceReceipt;
}

/** 請求書受取の利用可否（原価一覧の受領・支払登録と同じ基準。サーバ api/invoice-mail-inbox.js isAccountingOrAdminRow と対） */
function userCanUseInvoiceReceipt() {
  return isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser);
}

async function loadInvoiceReceipt() {
  const st = invoiceReceiptState();
  // 読込中の呼び出しは捨てずに「終わったらもう一度読む」にする。以前は捨てていたため、バッジ用の読込中に
  // 「無視」等を押すと取り直しが抜けて一覧・件数が古いまま残り、素早いタブ切替では前のタブの一覧が残った
  if (st.loading) {
    st.reloadPending = true;
    return st.loadingPromise;
  }
  st.loading = true;
  st.loadingPromise = (async () => {
    let res;
    let status;
    do {
      st.reloadPending = false;
      status = st.status;
      res = await apiFetch(`/api/invoice-mail-inbox?status=${encodeURIComponent(status)}`);
    } while (st.reloadPending || st.status !== status); // 応答待ちの間にタブを変えた・再読込を頼まれた
    st.loading = false;
    st.loaded = true;
    if (!res.ok) {
      st.error = typeof res.data?.error === "string" ? res.data.error : "請求書受取の読み込みに失敗しました";
    } else {
      st.error = "";
      st.items = Array.isArray(res.data.items) ? res.data.items : [];
      st.countByStatus = res.data.countByStatus && typeof res.data.countByStatus === "object" ? res.data.countByStatus : {};
      if (typeof res.data.inboxAddress === "string" && res.data.inboxAddress) st.address = res.data.inboxAddress;
    }
    render();
  })();
  return st.loadingPromise;
}

/** サイドナビのバッジ用: 未処理件数。未読込なら次のティックで読み込む（render 中に fetch を始めない） */
function invoiceReceiptOpenCountForBadge() {
  if (!currentUser || !userCanUseInvoiceReceipt()) return 0;
  const st = invoiceReceiptState();
  if (!st.loaded && !st.loading && !st.loadScheduled) {
    st.loadScheduled = true;
    setTimeout(() => {
      st.loadScheduled = false;
      void loadInvoiceReceipt();
    }, 0);
  }
  return Number(st.countByStatus?.open || 0);
}

/** 状態更新（open/check/done/ignored）。silent=true は登録完了時の自動更新（ウィザード側がトーストを出す） */
async function markInvoiceMailInboxItem(id, status, { silent = false } = {}) {
  const res = await apiFetch("/api/invoice-mail-inbox", { method: "PATCH", body: { id, status } });
  if (!res.ok) {
    if (!silent) showToastError(typeof res.data?.error === "string" ? res.data.error : "請求書受取の更新に失敗しました");
    return false;
  }
  if (!silent) showToast(`「${INVOICE_MAIL_INBOX_STATUS_LABELS[status] || status}」にしました`);
  const st = invoiceReceiptState();
  // ドロワーで開いている行なら詳細も取り直す（履歴に状態変更が増える）
  if (st.detailId === Number(id)) void openInvoiceReceiptDetail(id);
  // 一覧と件数はサーバから取り直す（手元で増減を計算すると、応答前のタブ切替や他の人の操作でずれる）
  await loadInvoiceReceipt();
  return true;
}

function invoiceReceiptItemById(st, id) {
  return st.detail?.item && Number(st.detail.item.id) === Number(id) ? st.detail.item : st.items.find((x) => Number(x.id) === Number(id));
}

function invoiceReceiptLinkedCostIdsLabel(linked) {
  return linked.map((c) => `#${Number(c.id)}`).join(", ");
}

/** 「無視」: このメールから登録した原価があれば、確認のうえ原価一覧からも削除してから無視にする（無視したのに原価が残る／
 *  未処理に戻して再登録すると二重になるのを防ぐ）。支払済みの原価は消さずに止める。途中で失敗したら無視にはしない */
async function ignoreInvoiceMailInboxItem(item) {
  const linked = Array.isArray(item.linkedCosts) ? item.linkedCosts : [];
  if (!linked.length) return markInvoiceMailInboxItem(item.id, "ignored");
  const paid = linked.filter((c) => c.paidDate || c.payStatus === "支払済み");
  if (paid.length) {
    showToast(`支払済みの原価（${invoiceReceiptLinkedCostIdsLabel(paid)}）が紐づいているため無視にできません`);
    return false;
  }
  // 原価の削除は原価一覧/原価割当の権限が要る（api/costs.js の DELETE と同じゲート）。権限が無ければ消さずに止める
  if (!userCanAccessPage("cost-management") && !userCanAccessPage("cost-allocation")) {
    showToast("このメールから登録した原価があります。無視にするには原価の削除が必要なため、経理・マスターに依頼してください");
    return false;
  }
  const receivedN = linked.filter((c) => c.payStatus === "支払待ち").length;
  const receivedNote = receivedN ? `\nうち ${receivedN} 件は受領済（請求書チェック済み）の原価です。` : "";
  if (!window.confirm(`このメールから登録した原価 ${linked.length}件（${invoiceReceiptLinkedCostIdsLabel(linked)}）も原価一覧から削除して「無視」にします。${receivedNote}\nよろしいですか？`)) return false;
  for (const c of linked) {
    // 読込後に他の人が支払済みにした原価はサーバーが 409 で止める（上の paid 判定は読込時点の値のため）
    const res = await apiFetch("/api/costs", { method: "DELETE", body: { id: Number(c.id), refuseIfPaid: true } });
    if (!res.ok && res.status !== 404) {
      showToastError(`原価 #${Number(c.id)} を削除できませんでした（${typeof res.data?.error === "string" ? res.data.error : "削除に失敗しました"}）`);
      await loadInvoiceReceipt(); // 途中まで消えた分を紐づき表示に反映
      return false;
    }
    const pl = findCostPlacementByUiId(`cost-${Number(c.id)}`);
    if (pl) removeCostPlacementAt(pl);
  }
  return markInvoiceMailInboxItem(item.id, "ignored");
}

async function openInvoiceReceiptDetail(id) {
  const st = invoiceReceiptState();
  st.detailId = Number(id);
  st.detailLoading = true;
  render();
  const res = await apiFetch(`/api/invoice-mail-inbox?id=${encodeURIComponent(String(id))}`);
  if (st.detailId !== Number(id)) return; // 読み込み中に別の行へ移った
  st.detailLoading = false;
  if (!res.ok) {
    showToast(typeof res.data?.error === "string" ? res.data.error : "詳細の読み込みに失敗しました");
    st.detailId = null;
    st.detail = null;
  } else {
    st.detail = { item: res.data.item, history: Array.isArray(res.data.history) ? res.data.history : [] };
  }
  render();
}

function invoiceMailAttachmentIsPdf(a) {
  // 画像（image/*）以外は PDF 扱い（受信 API は PDF と画像しか保存しない。content_type 無しの旧行も PDF）
  return !/^image\//i.test(String(a.contentType || ""));
}

/**
 * 受信箱の行から原価登録へ。添付PDFは保存済みなので取得してファイルとして渡し、docId も添える。
 * 計上月の選択画面（setup）は経由せず、そのまま AI 読取 → プレビューへ進む（計上月は今月。プレビューで行ごとに変更可、
 * 「戻る」で setup に戻れる）。読取に失敗した時だけ setup 画面に落ちる。
 */
async function startCostImportFromInboxItem(item, { salesExpense = false } = {}) {
  // 取込ウィザードに渡すのは PDF のみ（画像添付の AI 読取は受信時の自動読取で対応する）
  const docs = (item.attachments || []).filter((a) => a && a.stored && Number(a.docId) > 0 && invoiceMailAttachmentIsPdf(a));
  if (!docs.length) {
    showToast("このメールには取り込める添付PDFがありません");
    return;
  }
  if (uiState.costRegWizard?.phase === "preview") {
    showToast("取込プレビューが開いています。先に登録するか閉じてください");
    return;
  }
  if (!authToken) {
    showToast("ログインが必要です");
    return;
  }
  showToastPersistent("添付PDFを取得中…");
  try {
    // バイナリ取得なので apiFetch（JSON前提）は使わない（downloadCostSourcePdfDocument と同じ）。各PDFは独立なので並列に取る
    const files = await Promise.all(
      docs.map(async (a) => {
        const res = await fetch(`/api/cost-source-pdf?id=${encodeURIComponent(String(a.docId))}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (!res.ok) throw new Error("添付PDFの取得に失敗しました");
        return new File([await res.blob()], a.filename || "invoice.pdf", { type: "application/pdf" });
      })
    );
    const docIds = docs.map((a) => Number(a.docId));
    clearToast();
    const targetMonth = currentMonth();
    const inbox = { itemId: Number(item.id), files, docIds, subject: String(item.subject || "") };
    // 読取中は phase を "loading"（未知の phase はモーダルを描画しない）にして、途中の render() で計上月画面が
    // 一瞬出ないようにする。mode/salesMode はウィザードが参照する
    uiState.costRegWizard = {
      phase: "loading",
      mode: "other",
      salesMode: false,
      targetMonth,
      csvSourceKey: "generic",
      costImportChoice: "pdf:generic",
      inboxItemId: inbox.itemId,
      inboxFiles: files,
      inboxDocIds: docIds,
      inboxSubject: inbox.subject
    };
    await costImportPdfFilesToWizard(files, { sourceKey: "generic", targetMonth, choiceStr: "pdf:generic", inbox });
    // 「販管費」から開いた時は全行の計上先を販管費にしておく（案件の自動照合より優先。プレビューで行ごとに変更可）
    if (salesExpense && uiState.costRegWizard?.phase === "preview") {
      for (const row of uiState.costRegWizard.rows || []) row.projectIdPick = COST_SALES_EXPENSE_PROJECT_UI_ID;
      render();
    }
    // プレビューまで進めなかった（金額を検出できない等。トーストは出ている）→ 従来の計上月画面に落として手動でやり直せるようにする
    if (uiState.costRegWizard?.phase === "loading") {
      uiState.costRegWizard.phase = "setup";
      render();
    }
  } catch (e) {
    clearToast();
    showToast(e instanceof Error ? e.message : "添付PDFの取得に失敗しました");
  }
}

// ── 描画 ──

function invoiceReceiptStatusPillHtml(status) {
  const s = INVOICE_MAIL_INBOX_STATUSES.includes(status) ? status : "open";
  return `<span class="ir-pill ir-pill--${s}">${escapeHtml(INVOICE_MAIL_INBOX_STATUS_LABELS[s] || s)}</span>`;
}

/** プレビューできる添付（受信時に保存済みのもの） */
function invoiceReceiptPreviewableAttachments(item) {
  return (item?.attachments || []).filter((a) => a && a.stored && Number(a.docId) > 0);
}

/** 添付プレビューを開く👁ボタン（一覧の件名横・ドロワーの添付横で共用） */
function invoiceReceiptPreviewBtnHtml(item, a) {
  const label = escapeHtmlAttr(`${a.filename || "添付"} を表示`);
  return `<button type="button" class="inline-btn ghost ir-attach-preview" data-invoice-receipt-preview="${Number(a.docId)}" data-invoice-receipt-preview-item="${Number(item.id)}" title="${label}" aria-label="${label}">👁</button>`;
}

function invoiceReceiptAttachmentsHtml(item, { full = false } = {}) {
  const list = Array.isArray(item.attachments) ? item.attachments : [];
  if (!list.length) return `<span class="ir-muted">添付なし</span>`;
  const reasonLabel = { unsupported: "PDF・画像以外のため未保存", not_pdf: "PDF以外のため未保存", too_large: "15MB超のため未保存", too_many: "上限超のため未保存" };
  return list
    .map((a) => {
      const name = escapeHtml(String(a.filename || "attachment"));
      if (a.stored && Number(a.docId) > 0) {
        const isPdf = invoiceMailAttachmentIsPdf(a);
        // ドロワー（full）だけ中身を見るボタンを添える。原価か販管費かをその場で判断するため
        const previewBtn = full ? invoiceReceiptPreviewBtnHtml(item, a) : "";
        return `${previewBtn}<button type="button" class="inline-btn ghost ir-attach" data-cost-download-source-pdf="${Number(a.docId)}" title="${isPdf ? "PDF" : "画像"}をダウンロード">${isPdf ? "📄" : "🖼"} ${name}${full && a.size ? ` <span class="ir-muted">${escapeHtml(invoiceReceiptBytesLabel(a.size))}</span>` : ""}</button>`;
      }
      return `<span class="ir-muted ir-attach" title="${escapeHtmlAttr(reasonLabel[a.reason] || "未保存")}">${name}（${escapeHtml(reasonLabel[a.reason] || "未保存")}）</span>`;
    })
    .join("");
}

function invoiceReceiptBytesLabel(n) {
  const b = Number(n) || 0;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)}MB`;
  if (b >= 1024) return `${Math.round(b / 1024)}KB`;
  return `${b}B`;
}

function invoiceReceiptOpsHtml(item) {
  const hasPdf = (item.attachments || []).some((a) => a && a.stored && Number(a.docId) > 0 && invoiceMailAttachmentIsPdf(a));
  if (item.status === "open" || item.status === "check") {
    const disabledAttr = hasPdf ? "" : 'disabled title="添付PDFがないため取り込めません"';
    // 販管費も原価と同じ取込ウィザードで登録する（計上先を最初から「販管費」にするだけ）
    return `<button type="button" class="inline-btn ir-op" data-invoice-inbox-register="${Number(item.id)}" data-invoice-inbox-sales-expense="1" ${disabledAttr}>販管費</button>
      <button type="button" class="inline-btn ir-op" data-invoice-inbox-register="${Number(item.id)}" ${disabledAttr}>原価</button>
      <button type="button" class="inline-btn ghost ir-op" data-invoice-inbox-status="ignored" data-invoice-inbox-id="${Number(item.id)}">無視</button>`;
  }
  return `<button type="button" class="inline-btn ghost ir-op" data-invoice-inbox-status="open" data-invoice-inbox-id="${Number(item.id)}">未処理に戻す</button>`;
}

function invoiceReceiptLinkedCostsHtml(item, { full = false } = {}) {
  const list = Array.isArray(item.linkedCosts) ? item.linkedCosts : [];
  if (!list.length) return `<span class="ir-muted">—</span>`;
  return list
    .map((c) => {
      const label = `原価 #${Number(c.id)}`;
      const extra = full ? ` <span class="ir-muted">${escapeHtml(String(c.vendor || ""))} ${yen.format(Number(c.amount || 0))}</span>` : "";
      return `<span class="ir-linked">${escapeHtml(label)}${extra}</span>`;
    })
    .join("");
}

/** 絞り込み（キーワード＝件名・差出人・添付名、受信日の範囲）。一覧は状態ごとに最大 300 件なので手元で絞る */
function invoiceReceiptFilteredItems(st) {
  const q = String(st.q || "").trim().toLowerCase();
  const from = st.from ? new Date(`${st.from}T00:00:00`) : null;
  const to = st.to ? new Date(`${st.to}T23:59:59`) : null;
  return st.items.filter((it) => {
    const d = new Date(it.mailDate || it.receivedAt || 0);
    if (from && d < from) return false;
    if (to && d > to) return false;
    if (!q) return true;
    const hay = [it.subject, it.fromName, it.fromAddress, ...(it.attachments || []).map((a) => a.filename)]
      .map((x) => String(x || "").toLowerCase())
      .join(" ");
    return hay.includes(q);
  });
}

function invoiceReceiptRowHtml(item, selected) {
  // 差出人は「名前（アドレス）」の1行。添付名は一覧には出さない（ドロワーで見る）
  const from = item.fromName ? `${escapeHtml(item.fromName)}（${escapeHtml(item.fromAddress)}）` : escapeHtml(item.fromAddress);
  // 件名の先頭に👁（ドロワーを開かずに請求書を確認できる）。複数添付はプレビュー内で切替
  const firstAtt = invoiceReceiptPreviewableAttachments(item)[0];
  const previewBtn = firstAtt ? invoiceReceiptPreviewBtnHtml(item, firstAtt) : "";
  return `
    <tr class="ir-row ir-row--${escapeHtmlAttr(item.status)}${selected ? " ir-row--selected" : ""}" data-invoice-receipt-open="${Number(item.id)}" tabindex="0">
      <td class="ir-col-date">${escapeHtml(formatProgressMsgTime(item.mailDate || item.receivedAt))}</td>
      <td class="ir-col-from" title="${escapeHtmlAttr(item.fromAddress || "")}">${from}</td>
      <td class="ir-col-subject" title="${escapeHtmlAttr(item.subject || "")}">${previewBtn}${escapeHtml(item.subject || "（件名なし）")}</td>
      <td class="ir-col-status">${invoiceReceiptStatusPillHtml(item.status)}</td>
      <td class="ir-col-linked">${invoiceReceiptLinkedCostsHtml(item)}</td>
      <td class="ir-col-ops">${invoiceReceiptOpsHtml(item)}</td>
    </tr>`;
}

function invoiceReceiptHistoryLabel(h) {
  const d = h.detail || {};
  if (h.action === "received") return `受信（S3 保存・ハッシュ記録・添付 ${Number(d.stored || 0)} 件）`;
  if (h.action === "status") {
    return `状態を「${INVOICE_MAIL_INBOX_STATUS_LABELS[d.from] || d.from || "—"}」→「${INVOICE_MAIL_INBOX_STATUS_LABELS[d.to] || d.to || "—"}」に変更`;
  }
  if (h.action === "extract") return "AI 読取";
  if (h.action === "edit") return "読取結果を修正";
  return String(h.action || "");
}

function renderInvoiceReceiptDrawer(st) {
  if (!st.detailId) return "";
  const item = st.detail?.item || st.items.find((x) => Number(x.id) === st.detailId) || null;
  const history = st.detail?.history || [];
  const body = String(item?.bodyText || "").trim();
  const head = item
    ? `<div><h3 id="ir-drawer-title">${escapeHtml(item.subject || "（件名なし）")}</h3>
         <div class="ir-muted">${escapeHtml(item.fromName ? `${item.fromName} <${item.fromAddress}>` : item.fromAddress)} · ${escapeHtml(formatProgressMsgTime(item.mailDate || item.receivedAt))} 受信 · ${invoiceReceiptStatusPillHtml(item.status)}</div></div>`
    : `<div><h3 id="ir-drawer-title">読み込み中…</h3></div>`;
  const sections = item
    ? `
      <section class="ir-sec">
        <h4>添付<span class="ir-sec-r">${(item.attachments || []).length} 件</span></h4>
        <div>${invoiceReceiptAttachmentsHtml(item, { full: true })}</div>
      </section>
      <section class="ir-sec">
        <h4>本文</h4>
        ${body ? `<pre class="ir-body">${escapeHtml(body)}</pre>` : `<span class="ir-muted">本文なし</span>`}
      </section>
      <section class="ir-sec">
        <h4>原本<span class="ir-sec-r">受信時のまま保存・削除不可</span></h4>
        <div class="ir-kv">
          <span class="ir-k">宛先</span><span>${escapeHtml(item.toAddress || "")}</span>
          <span class="ir-k">SHA-256</span><span class="ir-mono">${escapeHtml(item.rawSha256 || "—")}</span>
          <span class="ir-k">サイズ</span><span>${escapeHtml(item.rawBytes != null ? invoiceReceiptBytesLabel(item.rawBytes) : "—")}</span>
          <span class="ir-k">保管先</span><span class="ir-mono">${escapeHtml(item.s3Key || "—")}</span>
        </div>
      </section>
      <section class="ir-sec">
        <h4>紐づいた原価</h4>
        <div>${invoiceReceiptLinkedCostsHtml(item, { full: true })}</div>
      </section>
      <section class="ir-sec">
        <h4>変更履歴<span class="ir-sec-r">訂正・削除の記録</span></h4>
        ${
          st.detailLoading
            ? `<span class="ir-muted">読み込み中…</span>`
            : history.length
              ? `<ul class="ir-hist">${history
                  .map(
                    (h) => `<li><span class="ir-hist-t">${escapeHtml(formatProgressMsgTime(h.createdAt))}</span><span>${escapeHtml(h.userName || "システム")}: ${escapeHtml(invoiceReceiptHistoryLabel(h))}</span></li>`
                  )
                  .join("")}</ul>`
              : `<span class="ir-muted">履歴なし</span>`
        }
      </section>`
    : "";
  return `
    <div class="ir-drawer-overlay" data-invoice-receipt-close="1"></div>
    <aside class="ir-drawer" role="dialog" aria-modal="true" aria-labelledby="ir-drawer-title">
      <div class="ir-drawer-head">
        ${head}
        <button type="button" class="inline-btn ghost ir-drawer-x" data-invoice-receipt-close="1" aria-label="閉じる">✕</button>
      </div>
      <div class="ir-drawer-body">${sections}</div>
      <div class="ir-drawer-foot">${item ? invoiceReceiptOpsHtml(item) : ""}</div>
    </aside>`;
}

// ── 添付プレビュー ──
// PDF は CSP（frame-src 無し＝default-src 'self'）で iframe 表示できないため、pdf.js でページごとに画像化して <img> で出す
// （img-src は blob: 可）。画像は blob URL をそのまま表示。blob URL は閉じる時に解放する。

function revokeInvoiceReceiptPreviewUrls(p) {
  for (const url of p?.pageUrls || []) URL.revokeObjectURL(url);
}

function closeInvoiceReceiptPreview() {
  const st = invoiceReceiptState();
  revokeInvoiceReceiptPreviewUrls(st.preview);
  st.preview = null;
}

async function pdfBlobToPageImageUrls(blob) {
  await ensurePdfJsLoaded();
  const lib = getPdfJsLib();
  const pdf = await lib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
  const urls = [];
  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      // 請求書の細かい文字が読めるよう、表示幅(最大900px)の約2倍の解像度で描く
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: Math.min(3, 1800 / base.width) });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (pngBlob) urls.push(URL.createObjectURL(pngBlob));
    }
  } finally {
    pdf.destroy();
  }
  return urls;
}

async function openInvoiceReceiptPreview(docId, itemId) {
  const st = invoiceReceiptState();
  const item =
    st.detail?.item && Number(st.detail.item.id) === itemId ? st.detail.item : st.items.find((x) => Number(x.id) === itemId);
  const attachments = invoiceReceiptPreviewableAttachments(item);
  const att = attachments.find((a) => Number(a.docId) === docId);
  if (!att || !authToken) return;
  closeInvoiceReceiptPreview();
  const preview = { itemId, attachments, docId, filename: String(att.filename || "attachment"), loading: true, error: "", pageUrls: [] };
  st.preview = preview;
  render();
  try {
    // バイナリ取得なので apiFetch（JSON前提）は使わない（startCostImportFromInboxItem と同じ）
    const res = await fetch(`/api/cost-source-pdf?id=${encodeURIComponent(String(docId))}`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });
    if (!res.ok) throw new Error("添付ファイルの取得に失敗しました");
    const blob = await res.blob();
    const urls = invoiceMailAttachmentIsPdf(att) ? await pdfBlobToPageImageUrls(blob) : [URL.createObjectURL(blob)];
    if (st.preview !== preview) {
      // 読み込み中に閉じた／別の添付を開いた
      revokeInvoiceReceiptPreviewUrls({ pageUrls: urls });
      return;
    }
    preview.pageUrls = urls;
  } catch (e) {
    if (st.preview !== preview) return;
    preview.error = e instanceof Error && e.message ? e.message : "プレビューを表示できませんでした";
  }
  preview.loading = false;
  render();
}

function renderInvoiceReceiptPreview(st) {
  const p = st.preview;
  if (!p) return "";
  const body = p.loading
    ? `<div class="ir-preview-msg">読み込み中…</div>`
    : p.error
      ? `<div class="ir-preview-msg">${escapeHtml(p.error)}。ダウンロードしてご確認ください。</div>`
      : p.pageUrls
          .map((url, i) => `<img class="ir-preview-page" src="${escapeHtmlAttr(url)}" alt="${escapeHtmlAttr(`${p.filename} ${i + 1}ページ目`)}" />`)
          .join("");
  // 添付が複数あるメールはプレビュー内で切り替える
  const switcher =
    p.attachments.length > 1
      ? `<div class="ir-preview-tabs">${p.attachments
          .map(
            (a) =>
              `<button type="button" class="inline-btn ghost${Number(a.docId) === p.docId ? " active" : ""}" data-invoice-receipt-preview="${Number(a.docId)}" data-invoice-receipt-preview-item="${Number(p.itemId)}">${escapeHtml(String(a.filename || "attachment"))}</button>`
          )
          .join("")}</div>`
      : "";
  return `
    <div class="ir-preview-overlay" data-invoice-receipt-preview-close="1"></div>
    <div class="ir-preview" role="dialog" aria-modal="true" aria-labelledby="ir-preview-title">
      <div class="ir-preview-head">
        <h3 id="ir-preview-title">${escapeHtml(p.filename)}</h3>
        ${switcher}
        <button type="button" class="inline-btn ghost" data-cost-download-source-pdf="${Number(p.docId)}">ダウンロード</button>
        <button type="button" class="inline-btn ghost" data-invoice-receipt-preview-close="1" aria-label="閉じる">✕</button>
      </div>
      <div class="ir-preview-body">${body}</div>
    </div>`;
}

function invoiceReceiptPage() {
  if (!userCanUseInvoiceReceipt()) {
    return `<div class="page pj-page"><div class="pj-page-wrap"><div class="pj-head"><div class="pj-head-stack"><h2>請求書受取</h2><p class="pj-page-lede">このページは経理・マスター・管理者のみ利用できます。</p></div></div></div></div>`;
  }
  const st = invoiceReceiptState();
  if (!st.loaded && !st.loading && !st.loadScheduled) {
    st.loadScheduled = true;
    setTimeout(() => {
      st.loadScheduled = false;
      void loadInvoiceReceipt();
    }, 0);
  }
  const tabs = INVOICE_MAIL_INBOX_STATUSES.map((s) => {
    const n = Number(st.countByStatus?.[s] || 0);
    return `<button type="button" class="cost-filter-tab${st.status === s ? " active" : ""}" data-invoice-receipt-tab="${s}">${INVOICE_MAIL_INBOX_STATUS_LABELS[s]}${n > 0 ? ` (${n})` : ""}</button>`;
  }).join("");
  const filtered = invoiceReceiptFilteredItems(st);
  const rows =
    st.loading && !st.items.length
      ? `<tr><td colspan="6" class="cost-empty">読み込み中…</td></tr>`
      : st.error
        ? `<tr><td colspan="6" class="cost-empty">${escapeHtml(st.error)}</td></tr>`
        : filtered.length === 0
          ? `<tr><td colspan="6" class="cost-empty">${st.items.length ? "絞り込みに該当するメールはありません" : st.status === "open" ? "未処理の請求書メールはありません" : "該当するメールはありません"}</td></tr>`
          : filtered.map((it) => invoiceReceiptRowHtml(it, Number(it.id) === st.detailId)).join("");
  return `
    <div class="page pj-page ir-page">
      <div class="pj-page-wrap">
        <div class="pj-head">
          <div class="pj-head-stack">
            <h2>請求書受取<span class="fp-tip fp-tip--below" tabindex="0" role="note" aria-label="${escapeHtmlAttr(invoiceReceiptAddress())} 宛に届いた請求書メールが溜まります（電子帳簿保存法対応）"><span class="fp-tip-mark">?</span><span class="fp-tip-bubble">${escapeHtml(invoiceReceiptAddress())} 宛に届いた請求書メールが溜まります（電子帳簿保存法対応）</span></span></h2>
            <p class="pj-page-lede ir-address">受付アドレス <button type="button" class="ir-address-value" data-invoice-receipt-copy-address="${escapeHtmlAttr(invoiceReceiptAddress())}" title="クリックでコピー">${escapeHtml(invoiceReceiptAddress())}</button></p>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-left:auto;">
            <button type="button" class="inline-btn ghost" data-invoice-receipt-reload="1" style="min-width:auto;padding:10px 20px;font-size:14px;">更新</button>
          </div>
        </div>
        <div class="ir-content">
        <div class="ir-toolbar">
          <div class="ir-tabs">${tabs}</div>
          <div class="ir-filters">
            <label class="ir-filter"><span class="ir-filter-label">キーワード</span><input class="pj-input" type="search" data-invoice-receipt-q data-ime-search value="${escapeHtmlAttr(st.q)}" placeholder="件名・差出人・添付名" autocomplete="off" /></label>
            <label class="ir-filter"><span class="ir-filter-label">受信日</span><span class="ir-range"><input class="pj-input" type="date" data-invoice-receipt-from value="${escapeHtmlAttr(st.from)}" /><span class="ir-range-sep">〜</span><input class="pj-input" type="date" data-invoice-receipt-to value="${escapeHtmlAttr(st.to)}" /></span></label>
            ${st.q || st.from || st.to ? `<button type="button" class="inline-btn ghost" data-invoice-receipt-clear="1">クリア</button>` : ""}
          </div>
        </div>
        <div class="ir-table-area">
          <table class="ir-table" data-fp-table="invoice-receipt">
            <thead>
              <tr><th>受信日時</th><th>差出人</th><th>件名</th><th>状態</th><th>紐づいた原価</th><th>操作</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <p class="ir-foot ir-muted">${filtered.length} 件${st.items.length !== filtered.length ? ` / ${st.items.length} 件` : ""}</p>
        </div>
      </div>
      ${renderInvoiceReceiptDrawer(st)}
      ${renderInvoiceReceiptPreview(st)}
    </div>`;
}

// ── イベント（独立リスナー） ──

document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const hit = t.closest(
    "[data-invoice-receipt-tab],[data-invoice-receipt-reload],[data-invoice-receipt-clear],[data-invoice-receipt-close],[data-invoice-receipt-copy-address],[data-invoice-inbox-status],[data-invoice-inbox-register],[data-invoice-receipt-open],[data-invoice-receipt-preview],[data-invoice-receipt-preview-close]"
  );
  if (!(hit instanceof HTMLElement)) return;
  if (!userCanUseInvoiceReceipt()) return;
  const st = invoiceReceiptState();
  if (hit.hasAttribute("data-invoice-receipt-preview")) {
    const docId = Number(hit.getAttribute("data-invoice-receipt-preview"));
    if (docId > 0) void openInvoiceReceiptPreview(docId, Number(hit.getAttribute("data-invoice-receipt-preview-item")));
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-preview-close")) {
    closeInvoiceReceiptPreview();
    render();
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-copy-address")) {
    const addr = String(hit.getAttribute("data-invoice-receipt-copy-address") || "");
    // 非https等では navigator.clipboard 自体が undefined（既存のコピー処理と同じ存在チェック）。その場合は prompt で手動コピー
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(addr).then(() => showToast("受付アドレスをコピーしました")).catch(() => prompt("受付アドレス", addr));
    } else {
      prompt("受付アドレス", addr);
    }
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-tab")) {
    const s = String(hit.getAttribute("data-invoice-receipt-tab") || "");
    if (!INVOICE_MAIL_INBOX_STATUSES.includes(s)) return;
    st.status = s;
    st.items = [];
    closeInvoiceReceiptPreview();
    st.detailId = null;
    st.detail = null;
    void loadInvoiceReceipt();
    render();
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-reload")) {
    void loadInvoiceReceipt();
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-clear")) {
    st.q = "";
    st.from = "";
    st.to = "";
    render();
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-close")) {
    closeInvoiceReceiptPreview();
    st.detailId = null;
    st.detail = null;
    render();
    return;
  }
  // 行内の操作ボタンは行クリック（ドロワーを開く）より先に判定する
  if (hit.hasAttribute("data-invoice-inbox-status")) {
    const s = String(hit.getAttribute("data-invoice-inbox-status") || "");
    const id = Number(hit.getAttribute("data-invoice-inbox-id"));
    if (!INVOICE_MAIL_INBOX_STATUSES.includes(s) || !(id > 0)) return;
    if (s === "ignored") void ignoreInvoiceMailInboxItem(invoiceReceiptItemById(st, id) || { id });
    else void markInvoiceMailInboxItem(id, s);
    return;
  }
  if (hit.hasAttribute("data-invoice-inbox-register")) {
    const id = Number(hit.getAttribute("data-invoice-inbox-register"));
    const item = invoiceReceiptItemById(st, id);
    if (!item) return;
    // 「未処理に戻す」→再登録で原価が二重にならないよう、登録済みの原価があれば確認する
    const linked = Array.isArray(item.linkedCosts) ? item.linkedCosts : [];
    if (linked.length && !window.confirm(`このメールから登録済みの原価があります（${invoiceReceiptLinkedCostIdsLabel(linked)}）。\nもう一度登録すると重複します。続けますか？`)) return;
    void startCostImportFromInboxItem(item, { salesExpense: hit.hasAttribute("data-invoice-inbox-sales-expense") });
    return;
  }
  if (hit.hasAttribute("data-invoice-receipt-open")) {
    // 添付のダウンロードボタン（16 の共通ハンドラ）は行クリック扱いにしない
    if (t.closest("[data-cost-download-source-pdf]")) return;
    const id = Number(hit.getAttribute("data-invoice-receipt-open"));
    if (id > 0) void openInvoiceReceiptDetail(id);
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const st = uiState.invoiceReceipt;
  if (!st) return;
  // プレビューが開いていればそれだけ閉じる（ドロワーは残す。一覧の件名横から開いた時はドロワー無し）
  if (st.preview) {
    closeInvoiceReceiptPreview();
    render();
    return;
  }
  if (!st.detailId) return;
  st.detailId = null;
  st.detail = null;
  render();
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (currentRoute().page !== "invoice-receipt") return;
  const st = invoiceReceiptState();
  if (t.hasAttribute("data-invoice-receipt-q")) {
    if (e.isComposing) return; // IME 変換中は確定まで待つ（compositionend で再発火する）
    st.q = t.value;
  } else if (t.hasAttribute("data-invoice-receipt-from")) {
    st.from = t.value;
  } else if (t.hasAttribute("data-invoice-receipt-to")) {
    st.to = t.value;
  } else {
    return;
  }
  render();
});
// ────────────────────────────────────────────────────────────────
// 書類作成・編集の「＋ 摘要を入力して行を追加」（ゴースト行）。
//  - 入力中（input）: 1文字目で state に行を作り、以降は摘要を追従させるだけ。再描画しない
//    （以前は1文字ごとに render() していて画面全体がちらついた）
//  - 確定（change = Enter / 欄を離れる）: 1回だけ再描画し、追加した行の摘要末尾へフォーカス。
//    確定時に摘要が空なら作った行を取り消す（空行を残さない）
// input 側の分岐は 16-render-routing.js（既存）から updateDocGhostLineFromInput を呼ぶ。
// 確定のイベントは 16 の巨大ハンドラに足さず、本ファイルの独立リスナーで処理する（15a〜15c と同方式）。
// ────────────────────────────────────────────────────────────────

/** ゴースト行の input: 未作成なら行を追加（index を data-doc-ghost-line-index に記憶）、作成済みなら摘要を更新 */
function updateDocGhostLineFromInput(target) {
  const pid = target.dataset.docGhostLine || "";
  const val = target.value;
  ensureProjectDocDraft(pid);
  const lines = uiState.projectDocsDrafts[pid]?.lines;
  if (!lines) return;
  const idxAttr = target.dataset.docGhostLineIndex;
  const idx = idxAttr != null && idxAttr !== "" ? Number(idxAttr) : -1;
  if (idx >= 0 && lines[idx]) {
    lines[idx].item = val;
    markProjectDocsDraftDirty(pid);
    schedulePersistProjectDocDraftsStore();
    return;
  }
  if (!val.trim()) return;
  lines.push({ lineType: "normal", item: val, qty: "1", unit: "式", price: "0", amount: "0", assignedUserId: "", assignedUserIds: [] });
  target.dataset.docGhostLineIndex = String(lines.length - 1);
  markProjectDocsDraftDirty(pid);
  persistAllLineStaffForProject(pid);
}

/** ゴースト行の確定: 空なら行を取り消し、そうでなければ再描画して追加行へフォーカス */
function commitDocGhostLine(target) {
  const pid = target.dataset.docGhostLine || "";
  const idxAttr = target.dataset.docGhostLineIndex;
  const idx = idxAttr != null && idxAttr !== "" ? Number(idxAttr) : -1;
  const lines = uiState.projectDocsDrafts[pid]?.lines;
  if (idx < 0 || !lines || !lines[idx]) return;
  if (!String(target.value || "").trim()) {
    lines.splice(idx, 1);
    delete target.dataset.docGhostLineIndex;
    target.value = "";
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    render();
    return;
  }
  delete target.dataset.docGhostLineIndex;
  render();
  // 追加した行の摘要末尾にフォーカス（preventScroll: 再描画後のスクロール復元と競合して画面が飛ばないように）
  requestAnimationFrame(() => {
    const el = document.querySelector(`input[data-detail-line-field="item"][data-detail-line-index="${idx}"][data-detail-project="${CSS.escape(pid)}"]`);
    if (el instanceof HTMLInputElement) { el.focus({ preventScroll: true }); el.setSelectionRange(el.value.length, el.value.length); }
  });
}

// Enter で確定（実測: この入力欄では Enter で change が発火しないため明示的に確定する。IME 変換中の Enter は対象外）
document.addEventListener("keydown", (e) => {
  if (e.key !== "Enter" || e.isComposing || e.keyCode === 229) return;
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || target.dataset.docGhostLine == null) return;
  if (currentRoute().page !== "project-docs" && currentRoute().page !== "project-detail") return;
  e.preventDefault();
  commitDocGhostLine(target);
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || target.dataset.docGhostLine == null) return;
  if (currentRoute().page !== "project-docs" && currentRoute().page !== "project-detail") return;
  commitDocGhostLine(target);
});
// ────────────────────────────────────────────────────────────────
// ToDo管理: プロジェクト（テーマ）の参加メンバー
// memberUserIds が null＝全員に公開。配列＝そのメンバー（＋マスター/管理者）だけが
// 一覧・課題ボード・課題を見られる（制限の本体はサーバ api/progress-management.js）。
// 登録モーダル（progressChallengeCreateDraft）と編集モーダル（progressChallengeEditDraft）で共用する。
// ────────────────────────────────────────────────────────────────

/** 参加メンバーの候補（退職者を除く在籍ユーザー） */
function progressChallengeMemberCandidates() {
  return activeUsers().filter((u) => !u.retired && u.dbId != null);
}

/** そのユーザーがプロジェクトに入れるか（担当者・メンションに指定できるか）。全員公開なら常に true。
 *  サーバ api/_challengeAccess.js userSeesChallengeMembers と同じ規則（メンバー or マスター/管理者） */
function progressChallengeUserAllowed(ch, u) {
  if (!Array.isArray(ch?.memberUserIds)) return true;
  if (u?.dbId != null && ch.memberUserIds.map(Number).includes(Number(u.dbId))) return true;
  return isMasterOrAdminUser(u);
}

/** 担当者・工程担当の選択肢（在籍・休職退職除外の assignableUsers をプロジェクトのメンバーで絞る） */
function progressChallengeAssigneePool(ch) {
  return assignableUsers().filter((u) => progressChallengeUserAllowed(ch, u));
}

/** いま開いている ToDo 画面のプロジェクト（URL の project= / issue= から。ToDo管理以外の画面＝null＝制限なし）。
 *  メンション候補・新規課題の担当者初期値のように、呼び出し側がプロジェクトを持っていない所で使う */
function progressRouteChallenge() {
  const route = currentRoute();
  if (route.page !== "progress-management") return null;
  const pid = String(route.searchParams?.get("project") || "").trim();
  if (pid) return findProgressChallenge(pid);
  const issue = String(route.searchParams?.get("issue") || "").trim();
  return (issue && findProgressAgendaById(issue)?.challenge) || null;
}

function progressChallengeMembersDraft(kind) {
  if (kind === "settings") return uiState.progressChallengeSettingsDraft;
  return kind === "edit" ? uiState.progressChallengeEditDraft : uiState.progressChallengeCreateDraft;
}

/** 保存リクエストに載せる値（null＝全員 / 数値ID配列＝選択したメンバー） */
function progressChallengeMembersPayload(d) {
  if (!d || d.memberMode !== "selected") return null;
  return (Array.isArray(d.memberUserIds) ? d.memberUserIds : []).map(Number).filter((n) => Number.isFinite(n) && n >= 1);
}

/** モーダル内の「参加メンバー」欄。kind = "create"（登録）| "edit"（一覧の編集）| "settings"（プロジェクト設定のメンバータブ） */
function progressChallengeMembersFieldHtml(d, kind) {
  const selected = d?.memberMode === "selected";
  const picked = new Set((Array.isArray(d?.memberUserIds) ? d.memberUserIds : []).map(String));
  const checks = progressChallengeMemberCandidates()
    .map(
      (u) => `<label class="cl-inline-check pm-ch-member-check">
        <input type="checkbox" data-pm-ch-member="${escapeHtmlAttr(String(u.dbId))}" data-pm-ch-members-kind="${kind}"${picked.has(String(u.dbId)) ? " checked" : ""} />
        <span>${escapeHtml(u.name || "")}</span>
      </label>`
    )
    .join("");
  const radio = (mode, label) =>
    `<label class="cl-inline-check pm-ch-members-mode"><input type="radio" name="pm-ch-members-mode-${kind}" value="${mode}" data-pm-ch-members-mode data-pm-ch-members-kind="${kind}"${(mode === "selected") === selected ? " checked" : ""} />${label}</label>`;
  return `
    <div class="client-form-row pm-ch-members">
      <span class="client-form-row-label">参加メンバー</span>
      <div class="pm-ch-members-body">
        <div class="pm-ch-members-modes">${radio("all", "全員")}${radio("selected", "メンバーを選択")}</div>
        <div class="pm-ch-members-list"${selected ? "" : " hidden"} data-pm-ch-members-list="${kind}">
          <p class="pm-ch-members-note">この${PM_WORD.theme}に参加するメンバーを選択します</p>
          <div class="pm-ch-members-checks">${checks}</div>
        </div>
      </div>
    </div>`;
}

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLInputElement)) return;
  const kind = target.dataset.pmChMembersKind;
  if (kind !== "create" && kind !== "edit" && kind !== "settings") return;
  const d = progressChallengeMembersDraft(kind);
  if (!d) return;
  d.membersTouched = true;
  if (target.dataset.pmChMembersMode !== undefined) {
    d.memberMode = target.value === "selected" ? "selected" : "all";
    if (d.memberMode === "selected" && !Array.isArray(d.memberUserIds)) {
      // 初回は自分をチェック済みにする（自分を外すと保存後に自分も見られなくなる）
      const me = Number(currentUser?.dbId ?? authUser?.id);
      d.memberUserIds = Number.isFinite(me) ? [me] : [];
      const mine = document.querySelector(`[data-pm-ch-member="${me}"][data-pm-ch-members-kind="${kind}"]`);
      if (mine instanceof HTMLInputElement) mine.checked = true;
    }
    // 再描画せず表示だけ切り替える（入力中のタイトル等のフォーカスを奪わない）
    const list = document.querySelector(`[data-pm-ch-members-list="${kind}"]`);
    if (list instanceof HTMLElement) list.hidden = d.memberMode !== "selected";
    return;
  }
  if (target.dataset.pmChMember !== undefined) {
    const id = Number(target.dataset.pmChMember);
    const ids = new Set((Array.isArray(d.memberUserIds) ? d.memberUserIds : []).map(Number));
    if (target.checked) ids.add(id);
    else ids.delete(id);
    d.memberUserIds = [...ids];
  }
});
// ─────────────────────────────────────────────────────────────
// 自分宛の未読メンション（担当課題ページ上部の常設ボックス）
//   - 担当者が自分でない課題でメンションされると「自分の担当課題」に出ず気づけない、への対策
//   - データはお知らせ（ベル）と同じ listUnreadToDoMentionTargetsForNotify を使う（判定を二重に持たない）
//   - 課題を開くと既読になり（progressMarkAgendaRead）、このボックスからも消える（既読化の直後に枠も差し替える）
//   - 見出しクリックで開閉（アカウントごとに記憶・既定は開）。開いている時は最新3件＋「もっと見る」で全件
//   - 開閉・もっと見るは枠だけ差し替える（全体 render しない）。課題へのリンクは通常の <a href> 遷移
//   - 開きっぱなしでも新着メンションに気づけるよう、表示中のタブだけ定期的に件数を取り直す
// ─────────────────────────────────────────────────────────────

/** 開いている時に最初から見せる件数（残りは「もっと見る」） */
const PM_MENTION_INBOX_PREVIEW_ROWS = 3;
/** 「もっと見る」で全件表示中か（画面内だけの一時状態。再読み込みで3件表示に戻る） */
let pmMentionInboxShowAll = false;

function pmMentionInboxOpenStorageKey() {
  const me = String(currentUser?.id || "").trim();
  return me ? `fp.pm.mentionInboxOpen.${me}` : "";
}

/** 開閉状態。既定は開（気づかせるための枠なので、閉じるのは本人の選択に任せる） */
function pmMentionInboxOpen() {
  const key = pmMentionInboxOpenStorageKey();
  if (!key) return true;
  try {
    return localStorage.getItem(key) !== "0";
  } catch {
    return true;
  }
}

function setPmMentionInboxOpen(open) {
  const key = pmMentionInboxOpenStorageKey();
  if (!key) return;
  try {
    localStorage.setItem(key, open ? "1" : "0");
  } catch {
    /* 保存できなくても今回の開閉は枠の差し替えで反映される */
  }
}
/** 通知件数の定期取り直し間隔。viewer 集計はメッセージ全件の ILIKE を含むので、短くしすぎない */
const PM_NOTIFY_POLL_INTERVAL_MS = 2 * 60 * 1000;
/** タブへ戻った時の即時取り直しの最短間隔（行き来の連打でAPIを叩きすぎない） */
const PM_NOTIFY_POLL_MIN_GAP_MS = 30 * 1000;
let pmNotifyPollLastAt = 0;

/** 担当課題ページ（一覧・トーク共通）の見出し下に出す未読メンション一覧。0件なら何も出さない */
function progressMentionInboxHtml() {
  if (!userCanAccessPage("progress-management")) return "";
  const targets = listUnreadToDoMentionTargetsForNotify();
  if (!targets.length) return "";
  const open = pmMentionInboxOpen();
  const shown = pmMentionInboxShowAll ? targets : targets.slice(0, PM_MENTION_INBOX_PREVIEW_ROWS);
  const rows = shown.map((t) => {
    const title = String(t.agendaTitle || "").trim() || "（無題）";
    const actor = String(t.actorName || "").trim();
    const time = progressAssignedTalkTimeLabel(t.latestAt);
    // from=assigned で、課題詳細の「閉じる」が担当課題ページへ戻る（一覧カードのリンクと同じ）
    const href = `#${progressManagementIssueHash(t.challengeId, t.agendaId)}&from=assigned`;
    return `<li><a class="pm-mention-inbox-item" href="${escapeHtmlAttr(href)}">
      <span class="pm-kan-card-key">${progressIssueNumberHtml(t.agendaId)}</span>
      <span class="pm-mention-inbox-title">${escapeHtml(title)}</span>
      <span class="pm-mention-inbox-meta">${actor ? `${escapeHtml(progressCardShortName(actor))}さん` : ""}${actor && time ? " · " : ""}${escapeHtml(time)}</span>
    </a></li>`;
  });
  const rest = targets.length - PM_MENTION_INBOX_PREVIEW_ROWS;
  const moreBtn =
    rest > 0
      ? `<button type="button" class="pm-mention-inbox-more" data-pm-mention-inbox-more="1">${pmMentionInboxShowAll ? "少なく表示" : `もっと見る（残り${rest}件）`}</button>`
      : "";
  return `<section class="pm-mention-inbox${open ? "" : " is-collapsed"}" aria-label="あなた宛の未読メンション">
    <button type="button" class="pm-mention-inbox-head" data-pm-mention-inbox-toggle="1" aria-expanded="${open ? "true" : "false"}">
      <span class="pm-mention-inbox-icon" aria-hidden="true">@</span>
      あなた宛の未読メンション <b>${targets.length}件</b>
      <span class="pm-mention-inbox-hint">担当外の課題も含みます。開くと既読になります</span>
      <span class="pm-mention-inbox-caret" aria-hidden="true">${open ? "▲" : "▼"}</span>
    </button>
    ${open ? `<ul class="pm-mention-inbox-list">${rows.join("")}</ul>${moreBtn}` : ""}
  </section>`;
}

document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  if (t.closest("[data-pm-mention-inbox-toggle]")) {
    setPmMentionInboxOpen(!pmMentionInboxOpen());
    syncProgressMentionInboxInPlace();
    return;
  }
  if (t.closest("[data-pm-mention-inbox-more]")) {
    pmMentionInboxShowAll = !pmMentionInboxShowAll;
    syncProgressMentionInboxInPlace();
  }
});

/** 担当課題ページ表示中なら、未読メンション枠だけを差し替える（全体 render は入力中のコメントを消しうるので避ける） */
function syncProgressMentionInboxInPlace() {
  if (!progressManagementAssignedMeRouteActive()) return;
  const html = progressMentionInboxHtml();
  const cur = document.querySelector(".pm-mention-inbox");
  if (cur) {
    if (html) cur.outerHTML = html;
    else {
      cur.remove();
      pmMentionInboxShowAll = false; // 全件読み終えたら、次の新着は3件表示から始める
    }
    return;
  }
  if (!html) return;
  document.querySelector(".pm-page .pm-proj-list-tabs-bar")?.insertAdjacentHTML("afterend", html);
}

/** 通知件数（ベル・左メニュー・未読メンション枠）をサーバーから取り直して反映する */
async function pollProgressNotifyCounts() {
  if (!authToken || document.visibilityState !== "visible") return;
  if (!userCanAccessPage("progress-management")) return;
  pmNotifyPollLastAt = Date.now();
  if (!(await refreshProgressViewerCountsFromServer())) return;
  refreshSidebarUserChrome();
  syncProgressMentionInboxInPlace();
}

setInterval(() => void pollProgressNotifyCounts(), PM_NOTIFY_POLL_INTERVAL_MS);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  if (Date.now() - pmNotifyPollLastAt < PM_NOTIFY_POLL_MIN_GAP_MS) return;
  void pollProgressNotifyCounts();
});
// ─────────────────────────────────────────────────────────────
// 課題コメント入力の下書き保持
//   - render() は画面を丸ごと作り直すため、詳細の開閉・一覧の切替・通知の更新などのたびに
//     入力中のコメントが消えていた（2026-09-29 実機指摘）。本人が消す／送信するまで残す
//   - renderNow() が DOM を差し替える直前に退避（capture）し、直後に戻す（restore）。課題ごとに保持するので、
//     別の課題やページへ移って戻ってきても残る。メモリ上のみ（再読み込みで消える）
//   - 送信時は既存ハンドラが入力欄を空にしてから描画するため、空＝下書きなしとして自然に消える
// ─────────────────────────────────────────────────────────────

/** agendaId -> { body, notify, status, assignee, due, open } */
const pmComposeDrafts = new Map();

const PM_COMPOSE_DRAFT_FIELDS = {
  body: "[data-pm-msg-body]",
  notify: "[data-pm-compose-notify-users]",
  status: "[data-pm-compose-status]",
  assignee: "[data-pm-compose-assignee]",
  due: "[data-pm-compose-due]"
};

/** 画面上のコメント入力欄（PC の .pm-compose・SP のシート本体とも data-pm-file-drop="compose:<課題ID>" を持つ） */
function pmComposeDraftContainers() {
  return [...document.querySelectorAll('[data-pm-file-drop^="compose:"]')].filter((el) => el instanceof HTMLElement);
}

function pmComposeDraftAgendaId(el) {
  return String(el.dataset.pmFileDrop || "").slice("compose:".length).trim();
}

/** 描画前: 入力欄の中身を課題ごとに退避。どの欄も未入力・未変更なら下書きを消す（本人が消した扱い） */
let pmComposeDraftFocus = null;
function capturePmComposeDrafts() {
  pmComposeDraftFocus = null;
  for (const box of pmComposeDraftContainers()) {
    const aid = pmComposeDraftAgendaId(box);
    if (!aid) continue;
    const d = {};
    for (const [key, sel] of Object.entries(PM_COMPOSE_DRAFT_FIELDS)) {
      const el = box.querySelector(sel);
      if (!(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) continue;
      // 期限日は現在の期限が初期値で入っているので、変えたときだけ下書きとして持つ
      const changed = el instanceof HTMLInputElement ? el.value !== el.defaultValue : el.value !== "";
      if (changed) d[key] = el.value;
      if (el === document.activeElement && "selectionStart" in el && el.selectionStart != null) {
        pmComposeDraftFocus = { aid, key, start: el.selectionStart, end: el.selectionEnd };
      }
    }
    if (Object.keys(d).length === 0) {
      pmComposeDrafts.delete(aid);
      continue;
    }
    d.open = box.classList.contains("pm-compose--open");
    pmComposeDrafts.set(aid, d);
  }
}

/** 描画後: 同じ課題の入力欄があれば退避した中身を戻す（開いていた入力欄は開いたまま・フォーカスも戻す） */
function restorePmComposeDrafts() {
  for (const box of pmComposeDraftContainers()) {
    const aid = pmComposeDraftAgendaId(box);
    const d = pmComposeDrafts.get(aid);
    if (!d) continue;
    for (const [key, sel] of Object.entries(PM_COMPOSE_DRAFT_FIELDS)) {
      if (d[key] == null) continue;
      const el = box.querySelector(sel);
      // @メンションの色付けは、この後 render() の enhanceMentionTextareaOverlays が値に合わせて作る
      if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.value = d[key];
    }
    // 開閉は退避時のまま（本人が「閉じる」で畳んだ入力欄を勝手に開かない）
    if (box.classList.contains("pm-compose--collapsible") && d.open) box.classList.add("pm-compose--open");
  }
}

/** render() がメンション装飾で textarea を包み直した後に呼ぶ。入力中だった欄へフォーカスとカーソル位置を戻す */
function restorePmComposeDraftFocus() {
  const f = pmComposeDraftFocus;
  pmComposeDraftFocus = null;
  if (!f) return;
  const box = pmComposeDraftContainers().find((b) => pmComposeDraftAgendaId(b) === f.aid);
  const el = box?.querySelector(PM_COMPOSE_DRAFT_FIELDS[f.key]);
  if (!(el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement)) return;
  el.focus({ preventScroll: true });
  try {
    el.setSelectionRange(f.start, f.end);
  } catch {
    /* type=date 等は選択範囲を持たないので無視 */
  }
}

// SP コメントシートの「キャンセル」ボタンは明示的な破棄なので、入力欄を空にしてから既存の閉じる処理
// （16 の data-pm-sp-compose-close → render）に任せる＝退避時に空と判定され下書きも消える。
// 背景タップ（同じ属性の backdrop）は誤操作のことが多いので下書きを残す。
// このファイルは 16 より前に連結されるので、同じ click で既存ハンドラより先に走る。
document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof Element) || !t.closest(".pm-sp-compose-cancel")) return;
  const box = t.closest(".pm-sp-compose-sheet")?.querySelector('[data-pm-file-drop^="compose:"]');
  if (!(box instanceof HTMLElement)) return;
  for (const sel of Object.values(PM_COMPOSE_DRAFT_FIELDS)) {
    const el = box.querySelector(sel);
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
      el.value = el instanceof HTMLInputElement ? el.defaultValue : "";
    }
  }
  pmComposeDrafts.delete(pmComposeDraftAgendaId(box));
});
// ── 原価一覧: 「支払」「発注」セルを押して状態を1クリックで変える（経理/マスター・月次ロック外の行のみ） ──
// セル（09 globalCostManagementSection の data-cost-pay-pick / data-cost-order-pick）を押すと選択肢が出る。
//   支払: 未受領／受領済／支払済（支払済だけ支払日を聞く）。保存は一括の支払ステータス変更（15j submitCostBulkPayStatus）を1件で使う
//   発注: 未設定＋発注ステータス。支払関連の項目を送らない部分更新（PUT /api/costs）で発注ステータスだけ変える
// 表の overflow で切れないよう body 直下に fixed 配置する（旧「⋯」メニューと同じ手法）。

const COST_INLINE_PAY_OPTIONS = [
  ["pending", "未受領"],
  ["received", "受領済"],
  ["paid", "支払済"]
];

let _costStatusPopEl = null;

function closeCostStatusPop() {
  if (_costStatusPopEl) {
    _costStatusPopEl.remove();
    _costStatusPopEl = null;
  }
  window.removeEventListener("scroll", closeCostStatusPop, true);
  window.removeEventListener("resize", closeCostStatusPop);
}

/** 一覧のセルを押せるか（描画側のボタン出し分けとクリック時の再チェックで共用） */
function costInlineStatusEditable(c) {
  return !!c && userCanRegisterCostReceipt() && !isMonthLocked(c.targetMonth) && costDbNumericId(c) != null;
}

function todayYmdLocal() {
  return new Date().toLocaleDateString("sv-SE");
}

function openCostStatusPop(anchor, kind, costId) {
  closeCostStatusPop();
  const c = findCostPlacementByUiId(costId)?.cost;
  if (!costInlineStatusEditable(c)) return;
  const pop = document.createElement("div");
  pop.className = "cost-status-pop";
  pop.setAttribute("role", "menu");
  pop.dataset.costId = costId;
  pop.dataset.kind = kind;
  const addBtn = (label, value, active) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `cost-status-pop-item${active ? " active" : ""}`;
    b.setAttribute("role", "menuitemradio");
    b.setAttribute("aria-checked", active ? "true" : "false");
    b.dataset.value = value;
    b.textContent = label;
    pop.appendChild(b);
  };
  if (kind === "pay") {
    const cur = costReceiptGroup(c);
    for (const [key, label] of COST_INLINE_PAY_OPTIONS) addBtn(label, key, key === cur);
    // 支払済は支払日が要るので、選んだら日付欄を出してから確定する
    const dateRow = document.createElement("div");
    dateRow.className = "cost-status-pop-date";
    dateRow.hidden = true;
    const input = document.createElement("input");
    input.type = "date";
    input.className = "pj-input";
    input.value = String(c.paidDate || "").slice(0, 10) || todayYmdLocal();
    input.setAttribute("aria-label", "支払日");
    const ok = document.createElement("button");
    ok.type = "button";
    ok.className = "inline-btn";
    ok.dataset.payApply = "1";
    ok.textContent = "支払済にする";
    dateRow.append(input, ok);
    pop.appendChild(dateRow);
  } else {
    const cur = String(c.orderStatus || "");
    addBtn("未設定", "", cur === "");
    for (const s of COST_ORDER_STATUSES) addBtn(s, s, s === cur);
  }
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  const mw = Math.max(pop.offsetWidth, 140);
  const mh = pop.offsetHeight;
  pop.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - mw - 8))}px`;
  const spaceBelow = window.innerHeight - r.bottom - 8;
  pop.style.top = spaceBelow >= mh || spaceBelow >= r.top - 8 ? `${r.bottom + 4}px` : `${Math.max(8, r.top - mh - 4)}px`;
  _costStatusPopEl = pop;
  window.addEventListener("scroll", closeCostStatusPop, true);
  window.addEventListener("resize", closeCostStatusPop);
}

/** 発注ステータスだけを変える。支払状況・支払日・支払期限などはボディに入れない＝サーバ（api/costs.js PUT）が
 *  既存値を保持する（画面のキャッシュが古くても、他の人が直前に付けた「支払済」等を巻き戻さない）。
 *  category/vendor/amount/消費税/計上月/備考はサーバが必ずボディから読むので現値を送る */
let _costOrderInlineSaving = false;
async function saveCostOrderStatusInline(c, orderStatus) {
  if (_costOrderInlineSaving) return showToast("発注ステータスを保存中です。少し待ってからもう一度お試しください");
  const n = costDbNumericId(c);
  if (n == null) return;
  _costOrderInlineSaving = true;
  try {
    const res = await apiFetch("/api/costs", {
      method: "PUT",
      body: {
        id: n,
        category: normalizeCostCategoryLabel(c.category),
        vendor: c.vendor,
        amount: Number(c.amount || 0),
        consumptionTax: costConsumptionTaxYen(c),
        targetMonth: normalizeCostTargetMonthForSave(c.targetMonth) || c.targetMonth || "",
        note: c.note || "",
        orderStatus
      }
    });
    if (!res.ok) throw new Error(typeof res.data?.error === "string" ? res.data.error : "発注ステータスの変更に失敗しました");
    c.orderStatus = orderStatus;
    showToast(`発注を「${orderStatus || "未設定"}」にしました`);
  } catch (e) {
    showToast(e instanceof Error && e.message ? e.message : "発注ステータスの変更に失敗しました", { variant: "error" });
  } finally {
    _costOrderInlineSaving = false;
  }
  render();
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (_costStatusPopEl && t.closest(".cost-status-pop")) {
    const pop = _costStatusPopEl;
    const c = findCostPlacementByUiId(pop.dataset.costId)?.cost;
    if (!costInlineStatusEditable(c)) return closeCostStatusPop();
    const applyBtn = t.closest("[data-pay-apply]");
    if (applyBtn) {
      const paidDate = String(pop.querySelector(".cost-status-pop-date input")?.value || "").trim();
      if (!paidDate) return showToast("支払日を選んでください");
      closeCostStatusPop();
      if (_costBulkPayStatusRunning) return showToast("支払状況を変更中です。少し待ってからもう一度お試しください");
      void submitCostBulkPayStatus("paid", paidDate, null, [c]);
      return;
    }
    const item = t.closest(".cost-status-pop-item");
    if (!(item instanceof HTMLElement)) return;
    const value = String(item.dataset.value || "");
    if (pop.dataset.kind === "pay") {
      if (value === "paid") {
        // 日付欄を出して確定を待つ（支払日なしで支払済にしない）
        pop.querySelector(".cost-status-pop-date").hidden = false;
        pop.querySelector(".cost-status-pop-date input")?.focus();
        return;
      }
      closeCostStatusPop();
      if (value === costReceiptGroup(c)) return;
      if (_costBulkPayStatusRunning) return showToast("支払状況を変更中です。少し待ってからもう一度お試しください");
      void submitCostBulkPayStatus(value, "", null, [c]);
      return;
    }
    closeCostStatusPop();
    if (value === String(c.orderStatus || "")) return;
    if (value && !COST_ORDER_STATUSES.includes(value)) return;
    void saveCostOrderStatusInline(c, value);
    return;
  }
  const cell = t.closest("[data-cost-pay-pick],[data-cost-order-pick]");
  if (cell instanceof HTMLElement && currentRoute().page === "cost-management") {
    const kind = cell.hasAttribute("data-cost-pay-pick") ? "pay" : "order";
    const key = String(cell.getAttribute(kind === "pay" ? "data-cost-pay-pick" : "data-cost-order-pick") || "");
    const costId = key.slice(key.lastIndexOf(":") + 1);
    if (_costStatusPopEl && _costStatusPopEl.dataset.costId === costId && _costStatusPopEl.dataset.kind === kind) {
      closeCostStatusPop();
      return;
    }
    openCostStatusPop(cell, kind, costId);
    return;
  }
  if (_costStatusPopEl) closeCostStatusPop(); // 外側クリックで閉じる
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && _costStatusPopEl) closeCostStatusPop();
});
// ── 原価一覧: 案件未割当の原価の「候補（記憶由来の予想案件）」をモーダルで確認して割り当てる ──
// 一覧の計上案件セルは「（案件未割当）」＋「候補を見る」だけにし（候補の有無で見た目が紛れないように）、
// 押すとこのモーダルで原価の内容と候補の案件を見比べて「この案件にする」。
// 割当の保存は既存の [data-cost-apply-suggested] ハンドラ（16-render-routing）にそのまま任せる。

/** モーダル本体（原価一覧の描画の末尾に差し込む）。状態は uiState.costSuggestModal = { pid, costId, suggestedUiId } */
function costSuggestModalHtml() {
  const m = uiState.costSuggestModal;
  if (!m) return "";
  const c = findCostPlacementByUiId(m.costId)?.cost;
  const p = projects.find((pp) => pp.id === m.suggestedUiId);
  if (!c || !p) {
    uiState.costSuggestModal = null;
    return "";
  }
  const name = stripCostBucketMarkers(String(c.note || "").trim()).split("\n")[0].trim() || "（発注名なし）";
  const item = (label, val) => `<div class="cost-suggest-modal-item"><span>${escapeHtml(label)}</span><strong>${escapeHtml(val)}</strong></div>`;
  return `
    <div class="cl-mini-modal-overlay open" data-cost-suggest-close="1">
      <div class="cl-mini-modal cost-suggest-modal" role="dialog" aria-modal="true" aria-labelledby="cost-suggest-title">
        <div class="cl-mini-modal-head">
          <h4 id="cost-suggest-title">候補の案件</h4>
          <button class="cl-modal-close" type="button" data-cost-suggest-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="cost-mgmt-bulk-modal-lede">前に同じ発注先の原価を割り当てた案件から、計上先の候補を出しています。内容が合っていれば割り当ててください。</p>
          <div class="cost-suggest-modal-cost">
            ${item("発注名", name)}
            ${item("発注先", c.vendor || "—")}
            ${item("計上月", String(c.targetMonth || "—"))}
            ${item("支払額（税込）", yen.format(costTotalRegisteredYen(c)))}
          </div>
          <div class="cost-suggest-modal-project">
            <span>候補</span>
            <strong>${escapeHtml(projectAccountingPickLabel(p))}</strong>
          </div>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          <button type="button" class="cl-btn cl-btn-outline" data-cost-suggest-close="1">閉じる</button>
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn cl-btn-primary" data-cost-apply-suggested="${escapeHtmlAttr(`${m.pid}:${m.costId}:${m.suggestedUiId}`)}">この案件にする</button>
        </div>
      </div>
    </div>`;
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || currentRoute().page !== "cost-management") return;
  const open = t.closest("[data-cost-suggest-open]");
  if (open instanceof HTMLElement) {
    const parts = String(open.getAttribute("data-cost-suggest-open") || "").split(":");
    if (parts.length < 3) return;
    const suggestedUiId = parts[parts.length - 1];
    const costId = parts[parts.length - 2];
    const pid = parts.slice(0, parts.length - 2).join(":");
    uiState.costSuggestModal = { pid, costId, suggestedUiId };
    render();
    return;
  }
  if (!uiState.costSuggestModal) return;
  // 「この案件にする」: 割当は 16 の既存ハンドラが続けて処理する（同じクリックがバブリングする）。ここではモーダルを閉じるだけ
  if (t.closest(".cost-suggest-modal [data-cost-apply-suggested]")) {
    uiState.costSuggestModal = null;
    return;
  }
  const close = t.closest("[data-cost-suggest-close]");
  if (close) {
    // オーバーレイ自身も close 属性を持つので、ダイアログ内のクリックは閉じ扱いにしない
    if (close.classList.contains("cl-mini-modal-overlay") && t.closest(".cl-mini-modal")) return;
    uiState.costSuggestModal = null;
    render();
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && uiState.costSuggestModal) {
    uiState.costSuggestModal = null;
    render();
  }
});
// ── 原価一覧: 並び順・検索条件・表示件数などを「ログイン者ごと」にブラウザへ保存し、次に開いたとき戻す ──
// 他ページへ移って戻ったときもリロードしても、その人が最後に触った状態のまま表示する。
// 保存先は localStorage（この端末・このブラウザのみ。ユーザーの DB id ごとにキーを分ける）。
// 原価一覧の描画（09 globalCostManagementSection）の先頭で復元、末尾で保存する。

const COST_MGMT_VIEW_STORAGE_PREFIX = "fp:costMgmtView:";

function costMgmtViewStorageKey() {
  const id = currentUser?.dbId;
  return id == null ? "" : `${COST_MGMT_VIEW_STORAGE_PREFIX}${id}`;
}

/** 保存対象（表示の状態だけ。選択中の行やモーダルは含めない） */
function costMgmtViewSnapshot() {
  return {
    sort: uiState.costMgmtSort || null,
    filter: uiState.costMgmtFilter || null,
    tabKeys: uiState.costMgmtTabKeys || null,
    detailOpen: uiState.costMgmtFilterDetailOpen === true,
    page: Number(uiState.costMgmtTable?.page) || 1,
    perPage: Number(uiState.costMgmtTable?.perPage) || 50
  };
}

/** ログイン者が変わった（または初回）なら、その人の保存状態を復元する。保存が無ければ既定に戻す（前の人の状態を引き継がない） */
function restoreCostMgmtViewState() {
  const key = costMgmtViewStorageKey();
  if (!key || uiState._costMgmtViewRestoredKey === key) return;
  uiState._costMgmtViewRestoredKey = key;
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    saved = null;
  }
  const base = emptyCostMgmtFilter();
  const filter = { ...base };
  if (saved && saved.filter && typeof saved.filter === "object") {
    for (const k of Object.keys(base)) if (typeof saved.filter[k] === "string") filter[k] = saved.filter[k];
  }
  uiState.costMgmtFilter = filter;
  uiState.costMgmtSort =
    saved && saved.sort && typeof saved.sort.key === "string"
      ? { key: saved.sort.key, dir: saved.sort.dir === "asc" ? "asc" : "desc" }
      : { key: "targetMonth", dir: "desc" };
  uiState.costMgmtTabKeys = normalizeCostMgmtTabKeys(saved?.tabKeys);
  uiState.costMgmtFilterDetailOpen = saved?.detailOpen === true;
  const perPage = [50, 100, 200, 500].includes(Number(saved?.perPage)) ? Number(saved.perPage) : 50;
  // page は描画時に件数でクランプされる（costMgmtTableState）ので、範囲外でも安全
  uiState.costMgmtTable = { page: Math.max(1, Number(saved?.page) || 1), perPage };
}

/** 状態が変わったときだけ書く（描画のたびに同じ値を書かない） */
function saveCostMgmtViewState() {
  const key = costMgmtViewStorageKey();
  if (!key || uiState._costMgmtViewRestoredKey !== key) return;
  const json = JSON.stringify(costMgmtViewSnapshot());
  if (uiState._costMgmtViewSavedJson === json) return;
  uiState._costMgmtViewSavedJson = json;
  try {
    localStorage.setItem(key, json);
  } catch {
    /* 保存できない（プライベートモード・容量超過）ときは、このタブの中だけ維持される */
  }
}
// ── 原価照合: クレジットカード決済の原価を、月ごとに登録したかを手動でチェックするリスト ──
// 例: さくらサーバー（毎月11日決済）／お名前ドットコム（月末から5営業日で請求書がサイトに出る）。
// 照合ページで選んでいる計上月について「登録した」をチェックする。目安日を過ぎても未チェックなら赤く出す。
// 項目とチェックは /api/card-cost-checklist（経理・マスター/管理者のみ）。

function ensureCardCostChecklistState() {
  if (!uiState.cardCostChecklist) {
    uiState.cardCostChecklist = { items: null, loading: false, error: "", editing: null, saving: false, busy: {}, reqSeq: 0, appliedSeq: 0 };
  }
  return uiState.cardCostChecklist;
}

function loadCardCostChecklistOnce() {
  const s = ensureCardCostChecklistState();
  if (s.items || s.loading || s.error) return;
  s.loading = true;
  apiFetch("/api/card-cost-checklist").then((r) => {
    s.loading = false;
    if (r.ok) s.items = Array.isArray(r.data.items) ? r.data.items : [];
    else s.error = typeof r.data?.error === "string" ? r.data.error : "カード決済チェックを読み込めませんでした";
    render();
  });
}

/** 目安日。day=計上月のN日（その月に無い日は月末）／bizdays_after_month_end=翌月の第N営業日 */
function cardCostChecklistDueDate(item, month) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month || ""));
  const n = Number(item?.ruleValue);
  if (!m || !(n >= 1)) return null;
  const y = Number(m[1]);
  const mi = Number(m[2]) - 1;
  if (item.ruleType === "day") return new Date(y, mi, Math.min(n, new Date(y, mi + 1, 0).getDate()));
  if (item.ruleType === "bizdays_after_month_end") return getNthBusinessDayOfMonth(y, mi + 1, n);
  return null;
}

function cardCostChecklistRuleLabel(item) {
  if (item.ruleType === "day") return `毎月${item.ruleValue}日決済`;
  if (item.ruleType === "bizdays_after_month_end") return `月末から${item.ruleValue}営業日で請求書`;
  return "";
}

/** 1項目の状態（並び順と表示に使う） */
function cardCostChecklistItemState(item, month, endOfYesterday) {
  const mark = item.checks && item.checks[month];
  const due = cardCostChecklistDueDate(item, month);
  return { mark, due, overdue: !mark && !!due && endOfYesterday > due };
}

/** 10件以上並ぶ前提で、1項目を小さなカードにする（名前＋1行の状態。決済日の区分・メモはツールチップ） */
function cardCostChecklistRowHtml(item, month, st) {
  const s = ensureCardCostChecklistState();
  const { mark, due, overdue } = st;
  const dueLabel = due ? `${due.getMonth() + 1}/${due.getDate()}` : "";
  const status = mark
    ? `<span class="card-cost-check-done">✓ ${escapeHtml(mark.byName || "")} ${escapeHtml(formatMdFromIso(mark.at))}</span>`
    : overdue
      ? `<span class="card-cost-check-overdue">目安 ${dueLabel} 超過</span>`
      : due
        ? `<span class="card-cost-check-due">目安 ${dueLabel}</span>`
        : `<span class="card-cost-check-due">未チェック</span>`;
  const tip = [item.name, cardCostChecklistRuleLabel(item), item.memo].filter(Boolean).join("\n");
  return `
    <li class="card-cost-check-row${mark ? " is-done" : ""}${overdue ? " is-overdue" : ""}" title="${escapeHtmlAttr(tip)}">
      <label class="card-cost-check-main">
        <input type="checkbox" data-card-cost-check-toggle="${item.id}" ${mark ? "checked" : ""} ${s.busy[item.id] || !month ? "disabled" : ""} />
        <span class="card-cost-check-name">${escapeHtml(item.name)}</span>
      </label>
      <button type="button" class="card-cost-check-edit" data-card-cost-check-edit="${item.id}" aria-label="${escapeHtmlAttr(item.name)}を編集">編集</button>
      <div class="card-cost-check-sub">${status}</div>
    </li>`;
}

/** ISO日時 → 「9/11」 */
function formatMdFromIso(iso) {
  const d = new Date(String(iso || ""));
  return Number.isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}/${d.getDate()}`;
}

/** 開閉はログイン者ごとにブラウザへ記憶（既定は開く） */
function cardCostChecklistOpenKey() {
  return currentUser?.dbId == null ? "" : `fp:cardCostCheckOpen:${currentUser.dbId}`;
}
function cardCostChecklistIsOpen() {
  const s = ensureCardCostChecklistState();
  const key = cardCostChecklistOpenKey();
  // 同じタブで別の人に替わったら、その人の開閉状態を読み直す
  if (typeof s.open === "boolean" && s.openKey === key) return s.open;
  s.openKey = key;
  try {
    s.open = key ? localStorage.getItem(key) !== "0" : true;
  } catch {
    s.open = true;
  }
  return s.open;
}

/** 照合ページに差し込むセクション（計上月 YYYY-MM） */
function cardCostChecklistSectionHtml(month) {
  loadCardCostChecklistOnce();
  const s = ensureCardCostChecklistState();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endOfYesterday = new Date(today.getTime() - 1); // 目安日の当日中は「過ぎた」にしない
  // 目安超過 → 未チェック（目安日の早い順）→ チェック済み の順に並べる
  const rank = (x) => (x.st.overdue ? 0 : x.st.mark ? 2 : 1);
  const rows = (s.items || [])
    .map((it) => ({ it, st: cardCostChecklistItemState(it, month, endOfYesterday) }))
    .sort((a, b) => rank(a) - rank(b) || (a.st.due?.getTime() ?? Infinity) - (b.st.due?.getTime() ?? Infinity) || a.it.id - b.it.id);
  const nOpen = rows.filter((x) => !x.st.mark).length;
  const nOverdue = rows.filter((x) => x.st.overdue).length;
  const open = cardCostChecklistIsOpen();
  const ym = /^(\d{4})-(\d{2})$/.exec(String(month || ""));
  const monthLabel = ym ? `${ym[1]}年${Number(ym[2])}月計上分` : "";
  const body = !open
    ? ""
    : s.loading
      ? `<p class="card-cost-check-empty">読み込み中…</p>`
      : s.error
        ? `<p class="card-cost-check-empty">${escapeHtml(s.error)} <button type="button" class="inline-btn ghost" data-card-cost-check-retry="1">再読み込み</button></p>`
        : !rows.length
          ? `<p class="card-cost-check-empty">カード決済の原価（さくらサーバー・お名前ドットコム等）を「＋ 項目を追加」で登録すると、月ごとに原価を登録したかをチェックできます。</p>`
          : `<ul class="card-cost-check-list">${rows.map((x) => cardCostChecklistRowHtml(x.it, month, x.st)).join("")}</ul>`;
  return `
    <section class="card-cost-check soft-card${open ? "" : " is-collapsed"}">
      <div class="card-cost-check-head">
        <button type="button" class="card-cost-check-toggle-open" data-card-cost-check-collapse="1" aria-expanded="${open ? "true" : "false"}">
          <span aria-hidden="true">${open ? "▾" : "▸"}</span><strong>カード決済の登録チェック</strong>
        </button>
        <span class="card-cost-check-month">${escapeHtml(monthLabel)}</span>
        ${rows.length ? `<span class="card-cost-check-count">${nOpen ? `未チェック ${nOpen}件` : "すべてチェック済み"}${nOverdue ? `（目安超過 <strong>${nOverdue}件</strong>）` : ""} / 全${rows.length}件</span>` : ""}
        <button type="button" class="inline-btn ghost card-cost-check-add" data-card-cost-check-edit="new">＋ 項目を追加</button>
      </div>
      ${body}
    </section>
    ${cardCostChecklistEditModalHtml()}`;
}

function cardCostChecklistEditModalHtml() {
  const e = ensureCardCostChecklistState().editing;
  if (!e) return "";
  const s = ensureCardCostChecklistState();
  const opt = (v, label) => `<option value="${v}" ${e.ruleType === v ? "selected" : ""}>${label}</option>`;
  return `
    <div class="cl-mini-modal-overlay open" data-card-cost-check-close="1">
      <div class="cl-mini-modal cost-mgmt-bulk-modal cost-modal--readable" role="dialog" aria-modal="true" aria-labelledby="card-cost-check-title">
        <div class="cl-mini-modal-head">
          <h4 id="card-cost-check-title">${e.id ? "項目を編集" : "項目を追加"}</h4>
          <button class="cl-modal-close" type="button" data-card-cost-check-close="1">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">名前</span>
            <input type="text" class="pj-input" maxlength="100" data-card-cost-check-field="name" value="${escapeHtmlAttr(e.name)}" placeholder="例: さくらサーバー" />
          </label>
          <div class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">登録できる目安</span>
            <div class="card-cost-check-rule-input">
              <select class="pj-input" data-card-cost-check-field="ruleType" data-search-dropdown-off="1">
                ${opt("day", "毎月 N 日に決済")}${opt("bizdays_after_month_end", "月末から N 営業日で請求書が出る")}${opt("none", "目安なし")}
              </select>
              ${e.ruleType === "none" ? "" : `<label>N = <input type="number" class="pj-input" min="1" max="${e.ruleType === "day" ? 31 : 20}" step="1" data-card-cost-check-field="ruleValue" value="${escapeHtmlAttr(String(e.ruleValue ?? ""))}" /></label>`}
            </div>
            <span class="cost-modal-help">目安日を過ぎても未チェックの項目は赤く表示します（毎月N日＝その計上月のN日／N営業日＝翌月の第N営業日・土日祝を除く）。</span>
          </div>
          <label class="cl-form-group cost-mgmt-bulk-modal-field">
            <span class="cl-form-label">メモ</span>
            <textarea class="pj-input" rows="2" maxlength="500" data-card-cost-check-field="memo" placeholder="例: 請求書は会員メニューからPDFをダウンロード">${escapeHtml(e.memo)}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot cost-mgmt-bulk-modal-foot--split">
          ${e.id ? `<button type="button" class="cl-btn cl-btn-outline" data-card-cost-check-archive="${e.id}" ${s.saving ? "disabled" : ""}>リストから外す</button>` : `<button type="button" class="cl-btn cl-btn-outline" data-card-cost-check-close="1">キャンセル</button>`}
          <span class="cost-mgmt-bulk-modal-foot-spacer" aria-hidden="true"></span>
          <button type="button" class="cl-btn cl-btn-primary" data-card-cost-check-save="1" ${s.saving ? "disabled" : ""}>${s.saving ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>`;
}

async function postCardCostChecklist(body, failMsg) {
  const s = ensureCardCostChecklistState();
  const seq = ++s.reqSeq;
  const r = await apiFetch("/api/card-cost-checklist", { method: "POST", body });
  if (!r.ok) {
    showToast(typeof r.data?.error === "string" ? r.data.error : failMsg, { variant: "error" });
    return false;
  }
  // 続けて操作したとき、先に出した要求の応答が後から届いても、適用済みより古い一覧では上書きしない
  // （最新の要求が失敗しても、それより前の成功分は反映される）
  if (seq > s.appliedSeq && Array.isArray(r.data.items)) {
    s.items = r.data.items;
    s.appliedSeq = seq;
  }
  return true;
}

document.addEventListener("click", async (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t || currentRoute().page !== "cost-reconcile") return;
  const s = ensureCardCostChecklistState();
  if (t.closest("[data-card-cost-check-retry]")) {
    s.error = "";
    loadCardCostChecklistOnce();
    render();
    return;
  }
  if (t.closest("[data-card-cost-check-collapse]")) {
    s.open = !cardCostChecklistIsOpen();
    try {
      const key = cardCostChecklistOpenKey();
      if (key) localStorage.setItem(key, s.open ? "1" : "0");
    } catch {
      /* 保存できなくてもこのタブの中では開閉が効く */
    }
    render();
    return;
  }
  const editBtn = t.closest("[data-card-cost-check-edit]");
  if (editBtn) {
    const key = editBtn.getAttribute("data-card-cost-check-edit");
    const it = (s.items || []).find((x) => String(x.id) === key);
    s.editing = it
      ? { id: it.id, name: it.name, ruleType: it.ruleType, ruleValue: it.ruleValue, memo: it.memo }
      : { id: 0, name: "", ruleType: "day", ruleValue: "", memo: "" };
    render();
    return;
  }
  const closeEl = t.closest("[data-card-cost-check-close]");
  // オーバーレイは中身のクリックで閉じない（自分自身を押したときだけ）
  if (closeEl && (closeEl === t || !closeEl.classList.contains("cl-mini-modal-overlay"))) {
    s.editing = null;
    render();
    return;
  }
  if (t.closest("[data-card-cost-check-save]") && s.editing && !s.saving) {
    s.saving = true;
    render();
    const ed = s.editing;
    const ok = await postCardCostChecklist(
      { action: "save", id: ed.id || undefined, name: ed.name, ruleType: ed.ruleType, ruleValue: ed.ruleValue, memo: ed.memo },
      "項目を保存できませんでした"
    );
    s.saving = false;
    if (ok) {
      s.editing = null;
      showToast("保存しました");
    }
    render();
    return;
  }
  const archiveBtn = t.closest("[data-card-cost-check-archive]");
  if (archiveBtn && !s.saving) {
    const id = Number(archiveBtn.getAttribute("data-card-cost-check-archive"));
    if (!window.confirm("この項目をリストから外しますか？（これまでのチェック記録は残ります）")) return;
    s.saving = true;
    render();
    const ok = await postCardCostChecklist({ action: "archive", id }, "リストから外せませんでした");
    s.saving = false;
    if (ok) s.editing = null;
    render();
  }
});

document.addEventListener("change", async (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || currentRoute().page !== "cost-reconcile") return;
  const id = Number(t.getAttribute("data-card-cost-check-toggle"));
  if (!id) return;
  const s = ensureCardCostChecklistState();
  const month = ensureCostReconcileState().month;
  s.busy[id] = true;
  render();
  await postCardCostChecklist({ action: "check", id, month, checked: t.checked }, "チェックを保存できませんでした");
  delete s.busy[id];
  render();
});

// モーダルの入力はドラフトに書くだけ（区分を切り替えたときだけ N 欄の出し分けのため再描画）
function onCardCostChecklistFieldInput(e) {
  const t = e.target;
  if (!(t instanceof HTMLElement) || currentRoute().page !== "cost-reconcile") return;
  const field = t.getAttribute("data-card-cost-check-field");
  const ed = ensureCardCostChecklistState().editing;
  if (!field || !ed || !("value" in t)) return;
  ed[field] = t.value;
  if (field === "ruleType" && e.type === "change") render();
}
document.addEventListener("input", onCardCostChecklistFieldInput);
document.addEventListener("change", onCardCostChecklistFieldInput);
// ────────────────────────────────────────────────────────────────
// アカウント設定の操作性まわり（アカウント一覧の表示メニュー欄・絞り込み／承認フローの未保存ガード／
// 部署設定のタップ操作用メンバーモーダル）。描画本体は 08-login-status-log.js / 09-log-labels-misc.js。
// ────────────────────────────────────────────────────────────────

// ── アカウント一覧: 表示メニュー欄 ──
/** マスター/管理者は userCanAccessPage で全画面が許可されるため、チェックは効かない。誤解を招くので出さない。
 *  ただし貸与品は管理者にも自動で開かないので、管理者には貸与品のチェックだけ出す（canManageUserEquipment）。
 *  それ以外は件数サマリの折りたたみにして行の高さを抑える（開閉状態は再描画をまたいで保持）。
 *  checks は accountNavPermKeysOrdered() と同じ並びのチェック HTML 配列 */
function acctNavPermsCellHtml(u, checks) {
  const keys = accountNavPermKeysOrdered();
  if (isMasterOrAdminUser(u)) {
    const all = `<span class="acct-perms-all" title="${escapeHtmlAttr(systemRoleOfUser(u))}は常に全画面を表示できます">全メニュー</span>`;
    return isMasterRoleUser(u) ? all : `${all}${checks[keys.indexOf("equipment-admin")] || ""}`;
  }
  const checksHtml = checks.join("");
  const total = keys.length;
  const on = normalizeNavPermissions(u.navPermissions || []).filter((k) => accountNavPermKeysOrdered().includes(k)).length;
  const open = uiState.acctPermsOpen?.[u.id] ? " open" : "";
  return `
    <details class="acct-perms-details" data-acct-perms="${escapeHtmlAttr(u.id)}"${open}>
      <summary>${on} / ${total} メニュー</summary>
      <div class="nav-perm-grid">${checksHtml}</div>
    </details>`;
}

/** 名前・メールの部分一致（大文字小文字・前後空白を無視） */
function acctListMatchesQuery(u, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q);
}

/** 職種の複数選択絞り込み（未選択=すべて）。判定は一覧の職種バッジと同じ primaryJobRole */
function acctListMatchesRoleFilter(u, roles) {
  if (!Array.isArray(roles) || roles.length === 0) return true;
  return roles.includes(primaryJobRole(u));
}

/** 契約一覧の複数選択フィルタと同型の details プルダウン（外側クリックで閉じるのは 15n-filter-dropdown-close.js） */
function acctRoleFilterDropdownHtml() {
  const selected = uiState.accountListRoleFilter || [];
  const placeholder = selected.length === 0;
  const opts = JOB_ROLE_OPTIONS.map((r) =>
    `<label class="pj-assignee-filter-opt"><span class="pj-assignee-filter-opt__check"><input type="checkbox" data-acct-role-filter="1" value="${escapeHtmlAttr(r)}" ${selected.includes(r) ? "checked" : ""} /></span><span class="pj-assignee-filter-opt__text">${escapeHtml(r)}</span></label>`
  ).join("");
  return `
    <details class="pj-assignee-filter-details acct-role-filter" data-acct-role-filter-dd="1" role="group" aria-label="職種で絞り込み"${uiState.acctRoleFilterOpen ? " open" : ""}>
      <summary class="pj-assignee-filter-trigger${placeholder ? " pj-assignee-filter-trigger--placeholder" : ""}">
        <span class="pj-assignee-filter-trigger-label">${escapeHtml(placeholder ? "職種：すべて" : selected.join("、"))}</span>
        <span class="pj-assignee-filter-caret" aria-hidden="true">▾</span>
      </summary>
      <div class="pj-assignee-filter-panel">${opts}</div>
    </details>`;
}

document.addEventListener("change", (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.matches("[data-acct-role-filter]")) return;
  const cur = uiState.accountListRoleFilter || [];
  // 並びは選択肢の順に揃える（トリガー表示が選んだ順で揺れないように）
  uiState.accountListRoleFilter = JOB_ROLE_OPTIONS.filter((r) =>
    r === input.value ? input.checked : cur.includes(r)
  );
  render();
});

// <details> の toggle はバブリングしないので capture で拾う
document.addEventListener("toggle", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLDetailsElement)) return;
  // チェックのたびに再描画するので、職種プルダウンの開閉も保持して連続で選べるようにする
  if (el.dataset.acctRoleFilterDd) {
    uiState.acctRoleFilterOpen = el.open;
    return;
  }
  if (!el.dataset.acctPerms) return;
  if (!uiState.acctPermsOpen) uiState.acctPermsOpen = {};
  uiState.acctPermsOpen[el.dataset.acctPerms] = el.open;
}, true);

// 絞り込みは入力ごとに再描画するとフォーカスが外れるため、行の表示切替だけで済ませる（値は再描画時にも使う）
document.addEventListener("input", (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.matches("[data-acct-search]")) return;
  uiState.accountListQuery = input.value;
  let shown = 0;
  document.querySelectorAll("tr[data-acct-row]").forEach((tr) => {
    const u = users.find((x) => x.id === tr.dataset.acctRow);
    const hit = !!u && acctListMatchesQuery(u, input.value);
    tr.hidden = !hit;
    if (hit) shown += 1;
  });
  const count = document.querySelector("[data-acct-list-count]");
  if (count) count.textContent = `（${shown}件）`;
});

// ── 承認フロー: 未保存の変更ガード ──
// 保存は「承認フローを登録」ボタンのみ。基準は表示時（読込・保存のたびに applyApprovalSettingsPayload が null に戻す）
function approvalSettingsSnapshot() {
  return JSON.stringify([
    uiState.approvalApprovers || {},
    uiState.approvalOtherFlows || [],
    uiState.approvalApplicationFlowBindings || {}
  ]);
}

function approvalSettingsDirty() {
  return uiState.approvalSettingsBaseline != null && approvalSettingsSnapshot() !== uiState.approvalSettingsBaseline;
}

function isApprovalTabOpen() {
  return isAccountSettingsRoute() && uiState.accountSettingsTab === "approval";
}

// 未保存のまま別タブ・別メニューへ移る前に確認する（capture で既存の遷移処理より先に止める）
document.addEventListener("click", (e) => {
  if (!isApprovalTabOpen() || !approvalSettingsDirty()) return;
  const target = e.target instanceof Element ? e.target : null;
  const leave = target?.closest('a[href^="#"], [data-account-settings-tab]:not([data-account-settings-tab="approval"])');
  if (!leave) return;
  if (confirm("承認フローに未保存の変更があります。保存せずに移動しますか？\n（保存するには「承認フローを登録」を押してください）")) return;
  e.preventDefault();
  e.stopImmediatePropagation();
}, true);

window.addEventListener("beforeunload", (e) => {
  if (!isApprovalTabOpen() || !approvalSettingsDirty()) return;
  e.preventDefault();
  e.returnValue = "";
});

// ── 部署設定: メンバーの所属・責任者を変更するモーダル（ドラッグ・ダブルクリックが使えない SP 向け。PC でも使える） ──
function renderDeptMemberModal() {
  const memberId = uiState.deptMemberModal?.memberId;
  const user = memberId && users.find((u) => u.id === memberId);
  if (!user) return "";
  const departments = uiState.deptSettings?.departments || [];
  const current = departments.find((d) => d.memberIds.includes(memberId));
  const isLeader = !!current && current.leaderMemberId === memberId;
  const options = [`<option value="__unassigned__"${current ? "" : " selected"}>未所属</option>`]
    .concat(departments.map((d) => `<option value="${escapeHtmlAttr(d.id)}"${current?.id === d.id ? " selected" : ""}>${escapeHtml(d.name)}</option>`))
    .join("");
  return `
    <div class="cl-mini-modal-overlay open" data-dept-member-close="1">
      <div class="cl-mini-modal">
        <div class="cl-mini-modal-head">
          <h4>${escapeHtml(user.name)}</h4>
          <button class="cl-modal-close" data-dept-member-close="1" type="button" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <label class="cl-form-group">
            <span class="cl-form-label">所属部署</span>
            <select class="cl-form-input" data-dept-member-dept="1" data-search-dropdown-off="1">${options}</select>
          </label>
          <label class="dept-member-leader-check">
            <input type="checkbox" data-dept-member-leader="1"${isLeader ? " checked" : ""}${current ? "" : " disabled"} />
            <span>この部署の責任者にする</span>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button class="cl-btn cl-btn-outline" data-dept-member-close="1" type="button">キャンセル</button>
          <button class="cl-btn cl-btn-primary" data-dept-member-save="1" type="button">変更する</button>
        </div>
      </div>
    </div>`;
}

// 別タブ・別ページへ移ったらモーダルは閉じる（戻ったときに開きっぱなしで再表示されないように）
window.addEventListener("hashchange", () => {
  uiState.deptMemberModal = null;
});

async function saveDeptMemberModal() {
  const memberId = uiState.deptMemberModal?.memberId;
  const deptId = document.querySelector("[data-dept-member-dept]")?.value || "__unassigned__";
  const wantLeader = !!document.querySelector("[data-dept-member-leader]")?.checked;
  const snapshot = cloneDeptSettingsState();
  // 既存のドラッグ移動と同じ関数で動かす（責任者だった部署から外れる時は責任者も解除される）
  const current = uiState.deptSettings.departments.find((d) => d.memberIds.includes(memberId));
  if (deptId === "__unassigned__") {
    if (current) removeMemberFromDepartment(memberId, current.id);
  } else if (current?.id !== deptId) {
    moveMemberToDepartment(memberId, deptId);
  }
  const target = uiState.deptSettings.departments.find((d) => d.id === deptId);
  if (target) {
    if (wantLeader) target.leaderMemberId = memberId;
    else if (target.leaderMemberId === memberId) target.leaderMemberId = "";
  }
  const persist = await persistDepartments();
  if (!persist.ok) restoreDeptSettingsState(snapshot);
  uiState.deptMemberModal = null;
  showToast(persist.ok ? "所属を変更しました" : formatDeptPersistFailureMessage(persist));
}

document.addEventListener("change", (e) => {
  const sel = e.target;
  if (!(sel instanceof HTMLSelectElement) || !sel.matches("[data-dept-member-dept]")) return;
  // 未所属では責任者にできない
  const leader = document.querySelector("[data-dept-member-leader]");
  if (leader instanceof HTMLInputElement) {
    leader.disabled = sel.value === "__unassigned__";
    if (leader.disabled) leader.checked = false;
  }
});

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !isAccountSettingsRoute()) return;
  const menuBtn = target.closest("[data-dept-member-menu]");
  if (menuBtn instanceof HTMLElement) {
    uiState.deptMemberModal = { memberId: menuBtn.dataset.deptMemberMenu };
    render();
    return;
  }
  if (target.closest("[data-dept-member-save]")) {
    void saveDeptMemberModal();
    return;
  }
  const closeEl = target.closest("[data-dept-member-close]");
  if (closeEl) {
    // オーバーレイ自体のクリックだけで閉じる（モーダル内のクリックは無視）
    if (closeEl.classList.contains("cl-mini-modal-overlay") && target.closest(".cl-mini-modal")) return;
    uiState.deptMemberModal = null;
    render();
  }
});

// ── 承認フロー: その他の申請の承認者（種別ごと）の編集ドロワー ──
document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !isApprovalTabOpen()) return;
  const editEl = target.closest("[data-approval-kind-edit]");
  if (editEl instanceof HTMLElement) {
    uiState.approvalKindEditId = String(editEl.dataset.approvalKindEdit || "");
    render();
    return;
  }
  if (target.closest("[data-approval-kind-close]") && uiState.approvalKindEditId) {
    uiState.approvalKindEditId = "";
    render();
  }
});

document.addEventListener("keydown", (e) => {
  // 担当者ピッカーが上に開いているときはそちらが先に閉じる（16-render-routing.js）
  if (e.key !== "Escape" || !uiState.approvalKindEditId || uiState.approvalPicker) return;
  uiState.approvalKindEditId = "";
  render();
});
// ── 課題テンプレート（プロジェクトごと） ──
// プロジェクト設定の「テンプレート」タブで [{ name, title, body }] を登録し、課題の追加モーダルで選ぶと
// タイトル・詳細に入る。保存はプロジェクト設定の「設定を保存」（updateChallengeSettings の agendaTemplates）。
// サーバ側の正規化は api/_progressValues.js の normalizeAgendaTemplates（上限はそちらと揃える）。
// テンプレートは一覧 API で全プロジェクト分が届くので、上限は小さく保つ（一覧の転送量を増やさない）。

/** プロジェクト設定モーダルのタブ（11-cost-form.js の設定モーダルと 16-render-routing.js のタブ切替が参照）。
 *  タブを足すときはここにも足す（無いと「カテゴリー」へ戻される） */
const PM_CHALLENGE_SETTINGS_TABS = ["categories", "statuses", "templates", "basic", "members", "backlog", "archived"];
const PM_AGENDA_TEMPLATE_MAX = 10;
const PM_AGENDA_TEMPLATE_BODY_MAX = 5000;

function progressAgendaTemplatesOf(ch) {
  return Array.isArray(ch?.agendaTemplates) ? ch.agendaTemplates : [];
}

/** 設定モーダルの下書き用（編集しても保存するまでプロジェクトの値を変えないよう複製する） */
function progressAgendaTemplatesDraftFrom(ch) {
  return progressAgendaTemplatesOf(ch).map((t) => ({
    name: String(t.name || ""),
    title: String(t.title || ""),
    body: String(t.body || "")
  }));
}

function progressAgendaTemplatesTabHtml(d) {
  const list = Array.isArray(d?.agendaTemplates) ? d.agendaTemplates : [];
  const rows = list.length
    ? list
        .map(
          (t, i) => `<li class="pm-agenda-tpl-row">
          <div class="pm-agenda-tpl-row-head">
            <input type="text" class="pj-input" data-pm-agenda-tpl-field="name" data-pm-agenda-tpl-index="${i}" value="${escapeHtmlAttr(t.name)}" placeholder="テンプレート名（必須）例: 不具合報告" maxlength="80" aria-label="テンプレート名" />
            <button type="button" class="pm-challenge-settings-list-remove" data-pm-agenda-tpl-remove="${i}" aria-label="このテンプレートを削除">×</button>
          </div>
          <input type="text" class="pj-input" data-pm-agenda-tpl-field="title" data-pm-agenda-tpl-index="${i}" value="${escapeHtmlAttr(t.title)}" placeholder="タイトルの初期値（任意）例: 【不具合】" maxlength="500" aria-label="タイトルの初期値" />
          <textarea class="pj-input" data-pm-agenda-tpl-field="body" data-pm-agenda-tpl-index="${i}" rows="6" placeholder="詳細の初期値（任意）" maxlength="${PM_AGENDA_TEMPLATE_BODY_MAX}" aria-label="詳細の初期値">${escapeHtml(t.body)}</textarea>
        </li>`
        )
        .join("")
    : `<li class="pm-challenge-settings-list-empty">まだテンプレートがありません</li>`;
  const canAdd = list.length < PM_AGENDA_TEMPLATE_MAX;
  return `<div class="pm-agenda-tpl-editor">
    <ul class="pm-agenda-tpl-list">${rows}</ul>
    <button type="button" class="inline-btn" data-pm-agenda-tpl-add="1"${canAdd ? "" : " disabled"}>テンプレートを追加</button>
    <p class="pm-challenge-settings-note">課題を追加するときに選ぶと、タイトルと詳細に入ります。最大${PM_AGENDA_TEMPLATE_MAX}件・詳細は${PM_AGENDA_TEMPLATE_BODY_MAX}文字まで。</p>
  </div>`;
}

/**
 * 保存に載せるテンプレート。テンプレートタブで触っていなければ templates は undefined（キーを送らない）。
 * 開いた時点の古い一覧で、他の人がその後に変えたテンプレートを上書きしないため（メンバーの membersTouched と同じ考え方）。
 * 名前の無いテンプレートに中身があれば { ok: false, index }
 */
function progressAgendaTemplatesPayload(d) {
  if (!d?.agendaTemplatesTouched || !Array.isArray(d.agendaTemplates)) return { ok: true, templates: undefined };
  const templates = [];
  for (let i = 0; i < d.agendaTemplates.length; i++) {
    const t = d.agendaTemplates[i];
    const name = String(t.name || "").trim();
    const title = String(t.title || "").trim();
    const body = String(t.body || "");
    if (!name) {
      if (title || body.trim()) return { ok: false, index: i };
      continue;
    }
    templates.push({ name, title, body });
  }
  return { ok: true, templates };
}

/** 名前の無いテンプレートで保存を止めたとき、テンプレートタブを開いてその欄を示す */
function showProgressAgendaTemplateNameError(index) {
  const d = uiState.progressChallengeSettingsDraft;
  if (d) d.tab = "templates";
  render();
  showToast("テンプレート名を入力してください");
  const el = document.querySelector(`[data-pm-agenda-tpl-field="name"][data-pm-agenda-tpl-index="${index}"]`);
  if (el instanceof HTMLInputElement) el.focus();
}

/** ライブツリーと全キャッシュへ反映（保存後・サーバから読み直した後。render で古い値に巻き戻らないように） */
function applyProgressAgendaTemplatesLocal(challengeId, templates) {
  if (!Array.isArray(templates)) return;
  const copy = () => templates.map((t) => ({ ...t }));
  const ch = findProgressChallenge(String(challengeId));
  if (ch) ch.agendaTemplates = copy();
  _forEachCachedChallenge(challengeId, (c) => {
    c.agendaTemplates = copy();
  });
}

/** 課題の追加モーダルのテンプレート選択欄（テンプレートが無い・編集時は出さない）。選んだものを選択状態で残す */
function progressAgendaTemplatePickerHtml(ch, editing) {
  const list = progressAgendaTemplatesOf(ch);
  if (editing || !list.length) return "";
  const picked = String(uiState.progressAgendaCreateDraft?.templateIndex ?? "");
  const opts = list
    .map((t, i) => `<option value="${i}"${picked === String(i) ? " selected" : ""}>${escapeHtml(t.name)}</option>`)
    .join("");
  return `<label class="client-form-row">テンプレート
    <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-agenda-template-pick aria-label="テンプレート">
      <option value="">（使わない）</option>${opts}
    </select>
  </label>`;
}

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || currentRoute().page !== "progress-management") return;
  const d = uiState.progressChallengeSettingsDraft;
  if (!d || !Array.isArray(d.agendaTemplates)) return;
  if (target.closest("[data-pm-agenda-tpl-add]")) {
    if (d.agendaTemplates.length >= PM_AGENDA_TEMPLATE_MAX) return;
    d.agendaTemplates.push({ name: "", title: "", body: "" });
    d.agendaTemplatesTouched = true;
    render();
    const inputs = document.querySelectorAll('[data-pm-agenda-tpl-field="name"]');
    const last = inputs[inputs.length - 1];
    if (last instanceof HTMLInputElement) last.focus();
    return;
  }
  const rm = target.closest("[data-pm-agenda-tpl-remove]");
  if (rm) {
    const i = Number(rm.getAttribute("data-pm-agenda-tpl-remove"));
    d.agendaTemplates = d.agendaTemplates.filter((_, idx) => idx !== i);
    d.agendaTemplatesTouched = true;
    render();
  }
});

// 入力は下書きへ直接書く（タブを切り替えても入力中の内容が残るように。再描画はしない）
document.addEventListener("input", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
  const field = el.getAttribute("data-pm-agenda-tpl-field");
  if (field !== "name" && field !== "title" && field !== "body") return;
  const d = uiState.progressChallengeSettingsDraft;
  const t = d?.agendaTemplates?.[Number(el.getAttribute("data-pm-agenda-tpl-index"))];
  if (!t) return;
  t[field] = el.value;
  d.agendaTemplatesTouched = true;
});

// 課題の追加モーダルでテンプレートを選んだら、タイトル・詳細へ入れる（書きかけの内容は確認してから上書き）
document.addEventListener("change", (e) => {
  const sel = e.target;
  if (!(sel instanceof HTMLSelectElement) || !sel.hasAttribute("data-pm-agenda-template-pick")) return;
  const draft = mergedProgressAgendaCreateDraftForModal();
  const prevIndex = String(draft.templateIndex ?? "");
  if (sel.value === "") {
    uiState.progressAgendaCreateDraft = { ...draft, templateIndex: "" };
    return;
  }
  const challengeId = sel.closest(".pm-agenda-create-modal")?.querySelector("[data-pm-agenda-challenge-id]")
    ?.getAttribute("data-pm-agenda-challenge-id");
  const tpl = progressAgendaTemplatesOf(findProgressChallenge(String(challengeId || "")))[Number(sel.value)];
  if (!tpl) return;
  const curTitle = String(draft.title || "").trim();
  const curBody = String(draft.body || "").trim();
  const overwrites = [
    curTitle && tpl.title && curTitle !== tpl.title ? "タイトル" : "",
    curBody && curBody !== tpl.body.trim() ? "詳細" : ""
  ].filter(Boolean);
  const templateIndex = sel.value;
  void (async () => {
    if (overwrites.length) {
      const ok = await fpConfirm({
        title: `テンプレート「${tpl.name}」を使いますか？`,
        message: `入力中の${overwrites.join("・")}がテンプレートの内容に置き換わります。`,
        confirmLabel: "置き換える",
        danger: false
      });
      if (!ok) {
        sel.value = prevIndex;
        return;
      }
    }
    uiState.progressAgendaCreateDraft = {
      ...draft,
      title: tpl.title || draft.title,
      body: tpl.body,
      templateIndex
    };
    render();
  })();
});
// ────────────────────────────────────────────────────────────────
// ToDo管理: プロジェクト設定「Backlog連携」タブ
// 接続先（自社・クライアントの Backlog など）と Backlog のプロジェクトキーを設定すると、その課題がこのプロジェクトへ毎時取り込まれる
// （取り込み本体・cron はサーバ api/backlog-import.js）。共通の「設定を保存」とは別に、
// タブ内の専用ボタンで保存・取り込みする（保存時に Backlog 側の存在確認と初回取り込みまで行うため）。
// ────────────────────────────────────────────────────────────────

/** 接続先の名前一覧（サーバの環境変数 BACKLOG_CONNECTIONS 由来。URL・キーは返らない）。未取得なら null */
function loadBacklogConnectionsOnce() {
  if (Array.isArray(uiState.progressBacklogConnections)) return;
  fetchOnceInFlight("backlog-connections", async () => {
    const { ok, data } = await apiFetch("/api/backlog-import", { method: "POST", body: { action: "listConnections" } });
    uiState.progressBacklogConnections = ok && Array.isArray(data.connections) ? data.connections : [];
    if (uiState.progressShowChallengeSettings) render();
  });
}

/** 通知先の選択欄（kind = "failure" 取り込み失敗 / "newIssue" 新しい課題）。連絡手段は各人の通知設定（メール・Slack・プッシュ） */
// 社員が数十人いるため、チェックを並べず複数選択プルダウンにする（一覧の担当者絞り込みと同じ部品。
// 外側クリックで閉じる処理は 15n-filter-dropdown-close.js が .pj-assignee-filter-details を見ている）
function backlogNotifyPickerHtml(kind, label, note, users, picked) {
  const opts = users
    .map(
      (u) => `<label class="pj-assignee-filter-opt"><span class="pj-assignee-filter-opt__check"><input type="checkbox" data-pm-backlog-notify="${escapeHtmlAttr(String(u.dbId))}" data-pm-backlog-notify-kind="${kind}" data-pm-backlog-notify-name="${escapeHtmlAttr(u.name || "")}"${picked.has(Number(u.dbId)) ? " checked" : ""} /></span><span class="pj-assignee-filter-opt__text">${escapeHtml(u.name || "")}</span></label>`
    )
    .join("");
  const names = users.filter((u) => picked.has(Number(u.dbId))).map((u) => String(u.name || ""));
  return `<div class="client-form-row">
      <span class="client-form-row-label">${escapeHtml(label)}</span>
      <div>
        <details class="pj-assignee-filter-details pm-backlog-notify-dd" role="group" aria-label="${escapeHtmlAttr(label)}">
          <summary class="pj-assignee-filter-trigger${names.length ? "" : " pj-assignee-filter-trigger--placeholder"}">
            <span class="pj-assignee-filter-trigger-label" data-pm-backlog-notify-label="${kind}">${escapeHtml(backlogNotifySummary(names))}</span>
            <span class="pj-assignee-filter-caret" aria-hidden="true">▾</span>
          </summary>
          <div class="pj-assignee-filter-panel">${opts}</div>
        </details>
        <p class="pm-challenge-settings-note">${escapeHtml(note)}</p>
      </div>
    </div>`;
}

/** プルダウンを閉じている時の表示（「田頭 歩 ほか2名」） */
function backlogNotifySummary(names) {
  if (!names.length) return "選択してください";
  return names.length === 1 ? names[0] : `${names[0]} ほか${names.length - 1}名`;
}

/** 画面で今チェックされている通知先（kind ごと） */
function backlogCheckedNotifyIds(kind) {
  return [...document.querySelectorAll(`[data-pm-backlog-notify-kind="${kind}"]`)]
    .filter((el) => el instanceof HTMLInputElement && el.checked)
    .map((el) => Number(el.dataset.pmBacklogNotify));
}

/** 「Backlog連携」タブの中身。d = progressChallengeSettingsDraft */
function progressChallengeBacklogTabHtml(ch, d) {
  loadBacklogConnectionsOnce();
  const conns = uiState.progressBacklogConnections;
  const busy = Boolean(uiState.progressBacklogImportBusy);
  const linkedKey = String(ch.backlogProjectKey || "");
  // 接続先が1つだけなら最初から選んでおく
  const selConn = String(d.backlogConnection || (Array.isArray(conns) && conns.length === 1 ? conns[0] : ""));
  const connOptions = (Array.isArray(conns) ? conns : [])
    .map((n) => `<option value="${escapeHtmlAttr(n)}"${n === selConn ? " selected" : ""}>${escapeHtml(n)}</option>`)
    .join("");
  // 環境変数から外された接続先も、選択中なら残して見せる（黙って別の接続先に化けさせない）
  const missingOpt = selConn && Array.isArray(conns) && !conns.includes(selConn)
    ? `<option value="${escapeHtmlAttr(selConn)}" selected>${escapeHtml(selConn)}（設定がありません）</option>`
    : "";
  const connRow = conns === undefined || conns === null
    ? `<p class="pm-challenge-settings-note">接続先を読み込み中…</p>`
    : !conns.length && !selConn
      ? `<p class="pm-challenge-settings-note pm-backlog-sync-error">Backlog の接続先がまだ設定されていません（管理者が環境変数 BACKLOG_CONNECTIONS に登録します）。</p>`
      : `<label class="client-form-row">接続先（Backlog のスペース）
          <select class="pj-input dform-select" data-search-dropdown-off="1" data-pm-backlog-connection>
            <option value=""${selConn ? "" : " selected"}>選択してください</option>${connOptions}${missingOpt}
          </select>
        </label>`;
  // 失敗の通知先: 選べるのは担当者にできる人（在籍・休職/退職を除く・参加メンバー制限に従う）。
  // 未設定（新規連携）の時は自分を選んだ状態で始める。退職等で選択済みの人が候補から消えたら警告を出す
  const notifyPool = progressChallengeAssigneePool(ch);
  const me = Number(currentUser?.dbId ?? authUser?.id);
  const picked = new Set(
    (Array.isArray(d.backlogNotifyUserIds) && d.backlogNotifyUserIds.length
      ? d.backlogNotifyUserIds
      : Number.isFinite(me) ? [me] : []
    ).map(Number)
  );
  const validPicked = notifyPool.filter((u) => picked.has(Number(u.dbId)));
  const notifyWarn = linkedKey && !validPicked.length
    ? `<p class="pm-challenge-settings-note pm-backlog-sync-error">有効な通知先がいません（退職・休職などで外れました）。通知先を選び直して保存してください。それまでは管理者にだけ通知されます。</p>`
    : "";
  // 新しい課題が届いた時の通知先（任意・空なら通知しない）
  const newIssuePicked = new Set((Array.isArray(d.backlogNewIssueNotifyUserIds) ? d.backlogNewIssueNotifyUserIds : []).map(Number));
  const notifyRow = `${backlogNotifyPickerHtml(
    "newIssue",
    "新しい課題の通知（任意）",
    "新しい課題が届いたら知らせます（1回の取り込みにつき1通）。",
    notifyPool,
    newIssuePicked
  )}${backlogNotifyPickerHtml(
    "failure",
    "失敗時の通知",
    "取り込みに失敗したら知らせます。複数人にしておくと退職・異動時も安心です。",
    notifyPool,
    picked
  )}${notifyWarn}`;
  const status = linkedKey
    ? `<p class="pm-challenge-settings-note">連携中: <strong>${escapeHtml(ch.backlogConnection || "")} / ${escapeHtml(linkedKey)}</strong>
        ／ 最終取り込み: ${ch.backlogSyncedAt ? escapeHtml(formatProgressMsgTime(ch.backlogSyncedAt)) : "まだありません"}</p>
       ${ch.backlogSyncError ? `<p class="pm-challenge-settings-note pm-backlog-sync-error">前回の取り込みでエラー: ${escapeHtml(ch.backlogSyncError)}</p>` : ""}`
    : `<p class="pm-challenge-settings-note">未連携です。</p>`;
  return `
    <div class="client-form-section project-create-step2-block">
      ${connRow}
      <label class="client-form-row">Backlog のプロジェクトキー
        <input type="text" class="pj-input" data-pm-backlog-project-key value="${escapeHtmlAttr(String(d.backlogProjectKey ?? ""))}"
          placeholder="例: WEB_SITE（空にして保存すると連携を解除）" maxlength="50" autocomplete="off" />
      </label>
      ${notifyRow}
      ${status}
      <div class="pm-backlog-actions">
        <button type="button" class="inline-btn" data-pm-backlog-save${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "処理中…" : "保存して取り込む"}</button>
        ${linkedKey ? `<button type="button" class="inline-btn ghost" data-pm-backlog-sync${busy ? " disabled" : ""}>いますぐ取り込む</button>` : ""}
      </div>
      <details class="pm-backlog-rules">
        <summary>取り込みのルール</summary>
        <ul class="pm-challenge-settings-note pm-challenge-settings-note--list">
          <li>毎時、Backlog → On boarding へ自動で取り込み（一方通行）</li>
          <li>Backlog で変わった項目だけ上書き（On boarding で変えた担当者・状況などは、Backlog でその項目が変わるまで残る）</li>
          <li>Backlog のコメント（新しい順）と添付ファイル名は本文の下に入る</li>
          <li>担当者はメール（無ければ氏名）が一致する人。いなければ担当者なし</li>
          <li>完了済みの課題は新しく取り込まない。Backlog で削除した課題はアーカイブへ</li>
          <li>種別「${PM_TODO_GROUP_CATEGORY_LABELS[PM_TASK_MD_GENRE] || PM_TASK_MD_GENRE}」なら、タスクMDも自動で更新</li>
          <li>通知は各人の通知設定（メール・Slack・プッシュ）で届く</li>
        </ul>
      </details>
    </div>`;
}

document.addEventListener("change", (e) => {
  const t = e.target;
  const d = uiState.progressChallengeSettingsDraft;
  if (!d) return;
  if (t instanceof HTMLSelectElement && t.dataset.pmBacklogConnection !== undefined) {
    d.backlogConnection = t.value;
    return;
  }
  if (t instanceof HTMLInputElement && t.dataset.pmBacklogNotify !== undefined) {
    // 画面に出ているチェックの状態をそのまま通知先にする（初期表示の「自分」も含めて取り込む）
    const kind = t.dataset.pmBacklogNotifyKind === "newIssue" ? "newIssue" : "failure";
    if (kind === "newIssue") d.backlogNewIssueNotifyUserIds = backlogCheckedNotifyIds("newIssue");
    else d.backlogNotifyUserIds = backlogCheckedNotifyIds("failure");
    // 再描画せずにプルダウンの表示だけ更新する（開いたまま続けて選べるように）
    const names = [...document.querySelectorAll(`[data-pm-backlog-notify-kind="${kind}"]`)]
      .filter((el) => el instanceof HTMLInputElement && el.checked)
      .map((el) => el.dataset.pmBacklogNotifyName || "");
    const labelEl = document.querySelector(`[data-pm-backlog-notify-label="${kind}"]`);
    if (labelEl) {
      labelEl.textContent = backlogNotifySummary(names);
      labelEl.closest("summary")?.classList.toggle("pj-assignee-filter-trigger--placeholder", !names.length);
    }
  }
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || t.dataset.pmBacklogProjectKey === undefined) return;
  const d = uiState.progressChallengeSettingsDraft;
  if (d) d.backlogProjectKey = t.value;
});

document.addEventListener("click", (e) => {
  const btn = e.target instanceof Element ? e.target.closest("[data-pm-backlog-save], [data-pm-backlog-sync]") : null;
  if (!(btn instanceof HTMLElement) || uiState.progressBacklogImportBusy) return;
  const d = uiState.progressChallengeSettingsDraft;
  const challengeId = Number(d?.challengeId);
  if (!Number.isFinite(challengeId) || challengeId < 1) return;
  const isSave = btn.dataset.pmBacklogSave !== undefined;
  const projectKey = String(d.backlogProjectKey || "").trim().toUpperCase();
  // 画面で選択中の値（1件だけで自動選択されている場合は draft に未反映のため DOM から読む）
  const connSel = document.querySelector("[data-pm-backlog-connection]");
  const connection = connSel instanceof HTMLSelectElement ? connSel.value : String(d.backlogConnection || "");
  // 通知先も画面のチェック状態から読む（初期表示で自分にチェックが入っているだけの時も送る）
  const notifyUserIds = backlogCheckedNotifyIds("failure");
  const newIssueNotifyUserIds = backlogCheckedNotifyIds("newIssue");
  void (async () => {
    uiState.progressBacklogImportBusy = true;
    render();
    try {
      const { ok, data } = await apiFetch("/api/backlog-import", {
        method: "POST",
        body: isSave ? { action: "saveSettings", challengeId, connection, projectKey, notifyUserIds, newIssueNotifyUserIds } : { action: "syncNow", challengeId }
      });
      if (!ok) {
        showToastError(typeof data.error === "string" ? data.error : "Backlog の取り込みに失敗しました");
        return;
      }
      if (isSave) d.backlogProjectKey = String(data.backlogProjectKey || "");
      showToast(
        isSave && !data.backlogProjectKey
          ? "Backlog 連携を解除しました"
          : `Backlog から取り込みました（新規 ${data.created || 0} 件・更新 ${data.updated || 0} 件${data.archived ? `・削除済み ${data.archived} 件をアーカイブ` : ""}${data.more ? "・残りは次回の自動取り込みで続きます" : ""}）${data.taskMdRequested ? "。タスクMDも自動で作り直します（数分で develop の tasks/ に反映）" : ""}`
      );
      await refreshProgressManagementFromServer({
        listMode: normalizeProgressListMode(uiState.progressListMode),
        scope: "full",
        includeChallengeId: challengeId
      });
    } finally {
      uiState.progressBacklogImportBusy = false;
      render();
    }
  })();
});
// ── 取り消せない操作の確認モーダル（window.confirm の置き換え）と二度押し対策 ──
// window.confirm は OS 標準の見た目で、ボタンが「OK／キャンセル」だけ＝何が起きるかがボタンから分からず、
// スマホでは誤タップしやすかった（2026-10 コード全体レビュー・UI/UX）。fpConfirm はボタンに動作名
// （「削除する」など）を書き、元に戻せるかを1行添え、初期フォーカスを「やめる」に置く。
//
// render() のたびに #appRoot / #fp-modal-layer は作り直されるので、モーダルは document.body に直接置く。
// 別のモーダルの上に重ねて開くことがあるため、Esc・Tab はこのモーダルの中で受け止めて下へ伝えない
// （16a-modal-focus.js の共通処理が下のモーダルを閉じたり、フォーカスを下へ移したりしないように）。

let fpConfirmIsOpen = false;

/**
 * @param {{ title: string, message?: string, note?: string, confirmLabel?: string, cancelLabel?: string, danger?: boolean }} opts
 *   note: 元に戻せるか等の補足（1行）。danger: 実行ボタンを赤にする（既定 true）
 * @returns {Promise<boolean>} 実行するなら true
 */
function fpConfirm({ title, message = "", note = "", confirmLabel = "実行する", cancelLabel = "やめる", danger = true } = {}) {
  if (fpConfirmIsOpen) return Promise.resolve(false);
  fpConfirmIsOpen = true;
  return new Promise((resolve) => {
    const returnFocus = document.activeElement;
    const root = document.createElement("div");
    root.className = "fp-confirm-root";
    root.innerHTML = `
      <div class="fp-confirm-backdrop" data-fp-confirm-answer="no"></div>
      <div class="fp-confirm-panel" role="alertdialog" aria-modal="true" aria-labelledby="fp-confirm-title"${message ? ' aria-describedby="fp-confirm-message"' : ""}>
        <h3 id="fp-confirm-title" class="fp-confirm-title">${escapeHtml(title)}</h3>
        ${message ? `<p id="fp-confirm-message" class="fp-confirm-message">${escapeHtml(message)}</p>` : ""}
        ${note ? `<p class="fp-confirm-note">${escapeHtml(note)}</p>` : ""}
        <div class="fp-confirm-actions">
          <button type="button" class="inline-btn ghost" data-fp-confirm-answer="no">${escapeHtml(cancelLabel)}</button>
          <button type="button" class="inline-btn ${danger ? "fp-confirm-danger" : "primary"}" data-fp-confirm-answer="yes">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>`;
    const finish = (ok) => {
      root.remove();
      fpConfirmIsOpen = false;
      if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
      resolve(ok);
    };
    root.addEventListener("click", (e) => {
      const answer = e.target instanceof Element ? e.target.closest("[data-fp-confirm-answer]") : null;
      if (answer) finish(answer.getAttribute("data-fp-confirm-answer") === "yes");
    });
    root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (e.isComposing || e.keyCode === 229) return;
        e.preventDefault();
        e.stopPropagation();
        finish(false);
      } else if (e.key === "Tab") {
        // ボタン2つの間だけを行き来する
        e.preventDefault();
        e.stopPropagation();
        const [no, yes] = root.querySelectorAll("[data-fp-confirm-answer]:not(.fp-confirm-backdrop)");
        (document.activeElement === no ? yes : no).focus();
      }
    });
    document.body.appendChild(root);
    root.querySelector('button[data-fp-confirm-answer="no"]').focus({ preventScroll: true });
  });
}

const fpExclusiveRunning = new Set();

/**
 * 取り消せない操作を、同じ key では同時に1つしか走らせない（二度押し・連打で二重に送らない）。
 * render() でボタンが作り直されても効くよう、ボタンでなく key で覚える。ボタンがあれば実行中は押せなくする。
 * @returns {Promise<any>} fn の戻り値。実行中に呼ばれたら何もせず undefined
 */
async function runExclusive(key, fn, button = null) {
  if (fpExclusiveRunning.has(key)) return undefined;
  fpExclusiveRunning.add(key);
  const btn = button instanceof HTMLButtonElement ? button : null;
  if (btn) {
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
  }
  try {
    return await fn();
  } finally {
    fpExclusiveRunning.delete(key);
    if (btn) {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
    }
  }
}
// ── 原価のまとめて入力（営業・原価割当ページ。2026-10 要望） ──
// 営業は「案件を作る → 原価をまとめて入れる → 案件に紐づける」をしており、前月の原価を複製して入れることも多い。
// 1画面の表で複数行を入れ、行ごとに計上先の案件（自分の担当案件）を選んで一括登録する。
// 会計区分はすべて入れられる。制作費・開発費の行は発注申請になり、行の下に申請理由（と取引先マスタに無ければ T番号）を入れる
// （2026-10 要望: 1件ずつでなくまとめて入れたい。申請の規則は案件詳細の「＋ 原価を追加」と同じ 15z-cost-order-applications.js）。
// 「前月の原価を複製」は、選んだ案件の前月の原価を、当月の同じ案件（同名・同顧客で請求月が計上月）に複製する。
// 当月の案件が無ければ複製しない（2026-10 要望: 案件から選んで原価だけコピー。コピー先に案件が無ければコピーできない）。
// 案件詳細の「＋ 原価を追加」と同じく、営業が入れる見込み（is_provisional）として登録し、入力欄も営業が入れるものだけにする
// （2026-10 要望: 触る人が同じなので統一）。支払期限・支払方法・源泉徴収区分はパートナーマスタから自動で入れ、営業が直せるように出す。
// 登録は取込ウィザードと同じ一括登録（persistCostsToServerCreateBulk・冪等キー付き）。
// サーバは営業の実原価を担当案件にしか登録させないため（api/costs.js）、計上先は自分の担当案件だけを出し、全行で必須にする。

/** 1行分の初期値 */
function costBulkEntryEmptyRow(seed = {}) {
  return {
    key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    projectId: "",
    vendor: "",
    // 発注名（原価一覧の「発注名」列＝ note）。経理が入れる原価と同じく、何の原価か分かる名前を入れる
    note: "",
    // 既定の会計区分・発注ステータスは「＋ 原価を追加」（16-render-routing.js data-add-provisional-cost）と同じ
    category: "媒体費",
    orderStatus: "",
    amount: "",
    dueDate: "",
    paymentMethod: "",
    withholdingKind: "",
    // 制作費・開発費の行の発注申請（15z-cost-order-applications.js costOrderApplyFromForm が読むキー）
    orderApplyReason: "",
    orderInvoiceIssuerNumber: "",
    error: "",
    ...seed
  };
}

/** 'YYYY/MM' → 前月の 'YYYY/MM' */
function costBulkPrevMonth(ym) {
  const m = String(ym || "").match(/^(\d{4})\/(\d{2})$/);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 2, 1);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** 行の支払期限の自動の値（計上月を基準月にする。見込みなので会計区分に関係なく入れる＝単票の costFormShowsDueDate と同じ） */
function costBulkAutoDue(row, targetMonth) {
  return costAutoDueDateFromVendorTerms(row.vendor, `${String(targetMonth).replace("/", "-")}-01`);
}

/** 発注先・会計区分・計上月から、支払期限・支払方法・源泉徴収区分を入れ直す。
 *  単票の原価フォームと同じく、空欄か自動の値のままの欄だけ（手で直した欄はそのまま） */
function costBulkApplyPartnerAuto(row, targetMonth) {
  const auto = {
    dueDate: costBulkAutoDue(row, targetMonth),
    paymentMethod: costAutoPaymentMethodFromVendor(row.vendor),
    withholdingKind: costWithholdingKindFromVendor(row.vendor)
  };
  for (const [field, value] of Object.entries(auto)) {
    if (costFormFieldStillAuto(row, field)) setCostFormAutoValue(row, field, value);
  }
  return row;
}

function openCostBulkEntry() {
  uiState.costBulkEntry = {
    targetMonth: currentMonth(),
    rows: [costBulkEntryEmptyRow()],
    copySourceId: "",
    runId: newClientUuid(),
    saving: false
  };
}

/** 前月の原価がある自分の案件（「前月の原価を複製」の複製元の候補） */
function costBulkCopySourceProjects(state) {
  const prev = costBulkPrevMonth(state.targetMonth);
  return costWizardSalesModeProjects().filter((p) => (p.costs || []).some((c) => String(c.targetMonth || "") === prev));
}

/** 選んだ案件の前月の原価（見込みも含む）を、当月の同じ案件（同名・同顧客で請求月が計上月）の行にする。
 *  当月の案件が無ければ null（複製しない。計上先の無い原価を作らない） */
function costBulkCopyPrevMonthRows(state, sourceId) {
  const prev = costBulkPrevMonth(state.targetMonth);
  const target = parseProjectBillingMonthYyyymm(state.targetMonth);
  const mine = costWizardSalesModeProjects();
  const src = mine.find((p) => p.id === sourceId);
  if (!src) return null;
  const dest = mine.find(
    (q) => q.name === src.name && q.clientId === src.clientId && parseProjectBillingMonthYyyymm(q.billingMonth) === target
  );
  if (!dest) return null;
  return (src.costs || [])
    .filter((c) => String(c.targetMonth || "") === prev)
    .map((c) =>
      costBulkApplyPartnerAuto(
        costBulkEntryEmptyRow({
          projectId: dest.id,
          vendor: c.vendor || "",
          note: c.note || "",
          category: normalizeCostCategoryLabel(c.category) || "媒体費",
          amount: String(Math.round(Number(c.amount) || 0))
        }),
        state.targetMonth
      )
    );
}

/** 行の入力チェック（登録前）。問題が無ければ "" */
function costBulkRowError(row) {
  if (!row.projectId) return "計上先の案件を選んでください";
  if (!String(row.vendor || "").trim()) return "発注先を選んでください";
  const n = Number(String(row.amount).replace(/,/g, ""));
  if (!Number.isFinite(n) || n === 0) return "金額を入れてください";
  // 制作費・開発費は発注申請（取引先マスタの発注先・T番号・申請理由。単票と同じ検証）
  return costOrderApplyFormError(row);
}

/** 空行（何も入れていない行）は登録しない */
function costBulkRowIsBlank(row) {
  return !row.projectId && !String(row.vendor || "").trim() && !String(row.amount || "").trim();
}

/** 制作費・開発費の行の下に出す発注申請の欄（申請理由と、取引先マスタに T番号が無ければ T番号）。対象外なら空。
 *  取引先マスタに無い・免税事業者で申請できない発注先は、登録を押す前にここで知らせる（文言は単票の発注申請欄と同じ） */
function costBulkApplyRowHtml(r, i, dis) {
  if (!costFormNeedsOrderApply(r)) return "";
  const hasVendor = !!String(r.vendor || "").trim();
  const pt = hasVendor ? costPartnerByVendorName(r.vendor) : null;
  const needsInvoiceNo = pt && !costPartnerInvoiceIsSet(pt) && !costPartnerIsTaxExempt(pt);
  const warn = hasVendor && !pt
    ? "取引先マスタに無い発注先です。先に取引先（パートナー）を登録してから申請してください。"
    : pt && costPartnerIsTaxExempt(pt)
      ? "この発注先は免税事業者（T番号なし）のため発注申請できません。T番号のある発注先を選んでください。"
      : "";
  return `
      <tr class="cost-bulk-apply-row">
        <td colspan="10">
          <span class="cost-bulk-apply-label">発注申請</span>
          <input class="pj-input cost-bulk-apply-reason" data-cost-bulk-field="${i}:orderApplyReason" value="${escapeHtmlAttr(r.orderApplyReason)}" placeholder="申請理由（なぜ外注するか・相見積の有無など）"${dis} />
          ${needsInvoiceNo ? `<input class="pj-input cost-bulk-apply-invoice" data-cost-bulk-field="${i}:orderInvoiceIssuerNumber" value="${escapeHtmlAttr(r.orderInvoiceIssuerNumber)}" placeholder="T番号（T＋13桁）" autocomplete="off"${dis} />` : ""}
          ${warn ? `<span class="cost-bulk-apply-warn">${escapeHtml(warn)}</span>` : ""}
        </td>
      </tr>`;
}

function renderCostBulkEntryModal() {
  const st = uiState.costBulkEntry;
  if (!st) return "";
  const mine = costWizardSalesModeProjects();
  const projectLabel = (p) => `${p.name || "（案件名なし）"}（${clientName(p)}・${p.billingMonth || "請求月未設定"}）`;
  const projectOptions = (sel) =>
    `<option value="">（案件を選択）</option>` +
    mine.map((p) => `<option value="${escapeHtmlAttr(p.id)}"${p.id === sel ? " selected" : ""}>${escapeHtml(projectLabel(p))}</option>`).join("");
  const copySources = costBulkCopySourceProjects(st);
  const copySourceOptions =
    `<option value="">（複製元の案件を選択）</option>` +
    copySources.map((p) => `<option value="${escapeHtmlAttr(p.id)}"${p.id === st.copySourceId ? " selected" : ""}>${escapeHtml(projectLabel(p))}</option>`).join("");
  const categoryOptions = (sel) => COST_CATEGORIES.map((c) => `<option value="${c}"${c === sel ? " selected" : ""}>${c}</option>`).join("");
  const orderStatusOptions = (sel) =>
    `<option value=""${sel ? "" : " selected"}>未設定</option>` +
    COST_ORDER_STATUSES.map((v) => `<option value="${v}"${v === sel ? " selected" : ""}>${v}</option>`).join("");
  const paymentMethodOptions = (sel) =>
    `<option value=""${sel ? "" : " selected"}>未設定</option>` +
    COST_PAYMENT_METHODS.map((v) => `<option value="${v}"${v === sel ? " selected" : ""}>${v}</option>`).join("");
  // 表が狭いので表示は短く（値はパートナーマスタと同じ区分）
  const withholdingKindOptions = (sel) =>
    WITHHOLDING_TAX_KINDS.map((k) => `<option value="${k}"${k === (sel || "なし") ? " selected" : ""}>${k.replace("源泉徴収（復興特別所得税", "源泉（復興税")}</option>`).join("");
  const dis = st.saving ? " disabled" : "";
  const rowsHtml = st.rows
    .map(
      (r, i) => `
      <tr class="cost-bulk-row${r.error ? " has-error" : ""}">
        <td><select class="pj-input" data-cost-bulk-field="${i}:projectId"${dis}>${projectOptions(r.projectId)}</select></td>
        <td><select class="pj-input" data-cost-bulk-field="${i}:vendor"${dis}>${costVendorSelectOptionsHtml(r.vendor)}</select>${costPartnerTermsHintHtml(r.vendor, true)}</td>
        <td><input class="pj-input" data-cost-bulk-field="${i}:note" value="${escapeHtmlAttr(r.note)}" placeholder="例: 10月分 保守費"${dis} /></td>
        <td><select class="pj-input" data-search-dropdown-off="1" data-cost-bulk-field="${i}:category"${dis}>${categoryOptions(r.category)}</select></td>
        <td><select class="pj-input" data-search-dropdown-off="1" data-cost-bulk-field="${i}:orderStatus"${dis}>${orderStatusOptions(r.orderStatus)}</select></td>
        <td><input class="pj-input cost-bulk-amount" inputmode="numeric" data-cost-bulk-field="${i}:amount" value="${escapeHtmlAttr(r.amount)}" placeholder="税抜"${dis} /></td>
        <td><input class="pj-input" type="date" data-cost-bulk-field="${i}:dueDate" value="${escapeHtmlAttr(r.dueDate)}"${dis} /></td>
        <td><select class="pj-input" data-search-dropdown-off="1" data-cost-bulk-field="${i}:paymentMethod"${dis}>${paymentMethodOptions(r.paymentMethod)}</select></td>
        <td><select class="pj-input" data-search-dropdown-off="1" data-cost-bulk-field="${i}:withholdingKind"${dis}>${withholdingKindOptions(r.withholdingKind)}</select></td>
        <td class="cost-bulk-act"><button type="button" class="chip-btn ghost" data-cost-bulk-remove="${i}" aria-label="${i + 1}行目を削除"${dis}>削除</button></td>
      </tr>
      ${costBulkApplyRowHtml(r, i, dis)}
      ${r.error ? `<tr class="cost-bulk-error-row"><td colspan="10">${escapeHtml(r.error)}</td></tr>` : ""}`
    )
    .join("");
  const total = st.rows.reduce((s, r) => s + (Math.round(Number(String(r.amount).replace(/,/g, ""))) || 0), 0);
  const filled = st.rows.filter((r) => !costBulkRowIsBlank(r)).length;
  const prev = costBulkPrevMonth(st.targetMonth);
  return `
    <div class="composer-backdrop" data-cost-bulk-close="1"></div>
    <aside class="composer-panel project-create-modal cost-bulk-modal${spNoEnter("cost-bulk-modal")}" role="dialog" aria-modal="true" aria-labelledby="cost-bulk-title">
      <div class="composer-head">
        <div>
          <h3 id="cost-bulk-title">原価をまとめて入力</h3>
          <p class="cost-bulk-lede">行ごとに計上先の案件（自分の担当案件）を選んで、原価をまとめて登録します。<br />制作費・開発費の行は発注申請が必要です。申請理由を入れてください。</p>
        </div>
      </div>
      <div class="composer-body cost-bulk-body">
        <div class="cost-bulk-toolbar">
          <label class="cost-bulk-month">計上月 <input class="pj-input" type="month" data-cost-bulk-month value="${escapeHtmlAttr(String(st.targetMonth).replace("/", "-"))}"${dis} /></label>
          ${copySources.length
            ? `<select class="pj-input cost-bulk-copy-source" data-cost-bulk-copy-source aria-label="前月の原価の複製元の案件"${dis}>${copySourceOptions}</select>
          <button type="button" class="inline-btn ghost" data-cost-bulk-copy-prev${dis}>前月（${escapeHtml(prev)}）の原価を複製</button>`
            : `<span class="cost-bulk-hint">前月（${escapeHtml(prev)}）の原価がある担当案件はありません</span>`}
          ${mine.length ? "" : `<span class="cost-bulk-warn">担当の案件がありません。先に案件を作成してください。</span>`}
        </div>
        <div class="cost-bulk-table-wrap">
          <table class="cost-bulk-table">
            <thead><tr><th>計上先の案件</th><th>発注先</th><th>発注名</th><th>会計区分</th><th>発注ステータス</th><th>金額（税抜）</th><th>支払期限</th><th>支払方法</th><th>源泉徴収区分</th><th></th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
        <button type="button" class="inline-btn ghost cost-bulk-add" data-cost-bulk-add${dis}>＋ 行を追加</button>
      </div>
      <div class="composer-foot" style="justify-content:space-between;gap:8px;">
        <span class="cost-bulk-total">${filled}件・合計 ${escapeHtml(yen.format(total))}（税抜）</span>
        <span style="display:flex;gap:8px;">
          <button class="inline-btn ghost" type="button" data-cost-bulk-close="1"${dis}>キャンセル</button>
          <button class="inline-btn" type="button" data-cost-bulk-save${dis || (filled ? "" : " disabled")}>${st.saving ? "登録中…" : `${filled}件を登録する`}</button>
        </span>
      </div>
    </aside>`;
}

async function saveCostBulkEntry() {
  const st = uiState.costBulkEntry;
  if (!st || st.saving) return;
  st.rows = st.rows.filter((r) => !costBulkRowIsBlank(r));
  st.rows.forEach((r) => {
    r.error = costBulkRowError(r);
  });
  if (!st.rows.length || st.rows.some((r) => r.error)) {
    render();
    if (st.rows.some((r) => r.error)) showToastError("入力が足りない行があります");
    return;
  }
  const entries = st.rows.map((r) => {
    const amount = Math.round(Number(String(r.amount).replace(/,/g, ""))) || 0;
    return {
      row: r,
      project: projects.find((p) => p.id === r.projectId),
      patch: {
        category: r.category,
        vendor: r.vendor,
        note: String(r.note || "").trim(),
        amount,
        consumptionTax: Math.round(amount * 0.1),
        targetMonth: st.targetMonth,
        // 「＋ 原価を追加」と同じ: 見込み（is_provisional）・支払状況は未登録・発注ステータスは行で選んだ値（未設定可）
        isProvisional: true,
        payStatus: "未登録",
        orderStatus: COST_ORDER_STATUSES.includes(r.orderStatus) ? r.orderStatus : "",
        // 支払期限・支払方法・源泉は行の値（パートナーマスタからの自動の値か、営業が直した値）
        dueDate: r.dueDate || "",
        paymentMethod: COST_PAYMENT_METHODS.includes(r.paymentMethod) ? r.paymentMethod : "",
        withholdingTax: costWithholdingYenForKind(r.withholdingKind, amount) || 0,
        // 制作費・開発費の行は発注申請（対象外の行は null）
        orderApply: costOrderApplyFromForm(r),
        importRunId: st.runId,
        importRowKey: r.key
      }
    };
  });
  // 計上月・発注先・税抜金額が同じ原価がすでにある行（二重計上の疑い）は、まとめて1回だけ確認する
  const dups = entries.flatMap((e) => findExistingDuplicateCosts(e.patch.targetMonth, e.patch.vendor, e.patch.amount));
  if (!confirmExistingCostDuplicates(dups)) return;
  st.saving = true;
  render();
  let results = [];
  try {
    results = await persistCostsToServerCreateBulk(entries);
  } finally {
    st.saving = false;
  }
  const failed = [];
  results.forEach((res, i) => {
    if (res?.ok) return;
    entries[i].row.error = res?.error || "登録に失敗しました";
    failed.push(entries[i].row);
  });
  const okCount = entries.length - failed.length;
  const appliedCount = entries.filter((e, i) => e.patch.orderApply && results[i]?.ok).length;
  if (appliedCount) void fetchCostOrderApplications();
  if (!failed.length) {
    uiState.costBulkEntry = null;
    showToast(`${okCount}件の原価を登録しました${appliedCount ? `（うち${appliedCount}件は発注申請。承認されるまで外注先への発注は待ってください）` : ""}`);
  } else {
    st.rows = failed;
    showToastError(`${failed.length}件を登録できませんでした${okCount ? `（${okCount}件は登録済み）` : ""}。行の内容を確認してください`);
  }
  render();
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (t.closest("[data-open-cost-bulk-entry]")) {
    openCostBulkEntry();
    render();
    return;
  }
  const st = uiState.costBulkEntry;
  if (!st || st.saving) return;
  if (t.closest("[data-cost-bulk-close]")) {
    const dirty = st.rows.some((r) => !costBulkRowIsBlank(r));
    if (dirty && !confirm("入力中の内容を破棄して閉じますか？")) return;
    uiState.costBulkEntry = null;
    render();
    return;
  }
  if (t.closest("[data-cost-bulk-add]")) {
    st.rows.push(costBulkEntryEmptyRow());
    render();
    return;
  }
  const rm = t.closest("[data-cost-bulk-remove]");
  if (rm) {
    st.rows.splice(Number(rm.getAttribute("data-cost-bulk-remove")), 1);
    if (!st.rows.length) st.rows.push(costBulkEntryEmptyRow());
    render();
    return;
  }
  if (t.closest("[data-cost-bulk-copy-prev]")) {
    if (!st.copySourceId) {
      showToastError("複製元の案件を選んでください");
      return;
    }
    const copied = costBulkCopyPrevMonthRows(st, st.copySourceId);
    if (!copied) {
      showToastError(`請求月が ${st.targetMonth} の同じ案件がないため複製できません。先に案件を作成してから複製してください`);
      return;
    }
    if (!copied.length) {
      showToast(`前月（${costBulkPrevMonth(st.targetMonth)}）のこの案件の原価はありません`);
      return;
    }
    st.rows = [...st.rows.filter((r) => !costBulkRowIsBlank(r)), ...copied];
    showToast(`${copied.length}件を複製しました。金額を確認してください`);
    render();
    return;
  }
  if (t.closest("[data-cost-bulk-save]")) void saveCostBulkEntry();
});

// 入力の取り込み。テキスト（金額・支払期限・発注名・申請理由）は再描画しない（入力中のフォーカスを保つ）。選択と計上月は再描画する
function onCostBulkInput(e) {
  const st = uiState.costBulkEntry;
  const el = e.target instanceof Element ? e.target : null;
  if (!st || st.saving || !el) return;
  if (el.matches("[data-cost-bulk-month]") && e.type === "change") {
    const v = String(el.value || "").replace("-", "/");
    if (!/^\d{4}\/\d{2}$/.test(v)) return;
    st.targetMonth = v;
    st.copySourceId = "";
    st.rows.forEach((r) => costBulkApplyPartnerAuto(r, v));
    render();
    return;
  }
  if (el.matches("[data-cost-bulk-copy-source]")) {
    st.copySourceId = el.value;
    return;
  }
  const spec = el.getAttribute("data-cost-bulk-field");
  if (!spec) return;
  const [idx, field] = spec.split(":");
  const row = st.rows[Number(idx)];
  if (!row) return;
  row[field] = el.value;
  row.error = "";
  if (field === "vendor" || field === "category") costBulkApplyPartnerAuto(row, st.targetMonth);
  if (el instanceof HTMLSelectElement && e.type === "change") {
    render();
  } else if (field === "amount") {
    // 合計表示だけ更新（再描画しない）
    const totalEl = document.querySelector(".cost-bulk-total");
    if (totalEl) {
      const total = st.rows.reduce((s, r) => s + (Math.round(Number(String(r.amount).replace(/,/g, ""))) || 0), 0);
      const filled = st.rows.filter((r) => !costBulkRowIsBlank(r)).length;
      totalEl.textContent = `${filled}件・合計 ${yen.format(total)}（税抜）`;
    }
  }
}
document.addEventListener("input", onCostBulkInput);
document.addEventListener("change", onCostBulkInput);
// 原価取込の案件照合（11-cost-form.js findProjectsMatchingClientHint）で使う部分一致の判定。

/** hay に needle が含まれるか。3文字以下の英数字（S3・EC2 など）は単語の途中に一致させない
 *  （明細「CloudFront + S3」の「S3」が案件名「HOME'S3月分」に当たり、無関係の案件が推定された・2026-10） */
function costHintIncludes(hay, needle) {
  const h = String(hay || "");
  const n = String(needle || "");
  if (!n || !h.includes(n)) return false;
  if (!/^[\x21-\x7e]{1,3}$/.test(n)) return true;
  const esc = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^A-Za-z0-9'’])${esc}(?![A-Za-z0-9])`).test(h);
}
// ── 原価一覧の備考の印（📝）を押すと備考の全文を吹き出しで出す（2026-11 要望） ──
// 印（11-cost-form.js costMemoMarkHtml）は title だけだと、印の上で1秒止めないと出ず、タッチでは出ない。
// 発注一覧では印を発注名のボタンの中に置くので、ボタンの「編集を開く」より先に止めるため capture で受ける。

function closeCostMemoPopover() {
  document.getElementById("cost-memo-popover")?.remove();
}

function openCostMemoPopover(mark) {
  closeCostMemoPopover();
  const memo = String(mark.dataset.costMemo || "");
  if (!memo) return;
  const pop = document.createElement("div");
  pop.id = "cost-memo-popover";
  pop.className = "cost-memo-popover";
  pop.setAttribute("role", "dialog");
  pop.setAttribute("aria-label", "備考");
  pop.innerHTML = `<div class="cost-memo-popover-head">備考</div><div class="cost-memo-popover-body">${escapeHtml(memo)}</div>`;
  document.body.appendChild(pop);
  // 印の下に出し、画面からはみ出す分は内側へ寄せる
  const r = mark.getBoundingClientRect();
  const w = pop.offsetWidth;
  const h = pop.offsetHeight;
  const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
  const top = r.bottom + 6 + h > window.innerHeight ? Math.max(8, r.top - h - 6) : r.bottom + 6;
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
}

document.addEventListener(
  "click",
  (e) => {
    const mark = e.target.closest?.(".cost-memo-mark");
    if (mark) {
      e.preventDefault();
      e.stopPropagation();
      openCostMemoPopover(mark);
      return;
    }
    if (!e.target.closest?.("#cost-memo-popover")) closeCostMemoPopover();
  },
  true
);
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeCostMemoPopover();
});
// 位置は開いた時の印に合わせているので、スクロール・リサイズでずれたら閉じる
window.addEventListener(
  "scroll",
  (e) => {
    if (!e.target.closest?.("#cost-memo-popover")) closeCostMemoPopover();
  },
  true
);
window.addEventListener("resize", closeCostMemoPopover);
// ────────────────────────────────────────────────────────────────
// 原価の発注申請（2026-10 ユーザー決定。外注費を減らすため、外注先に頼む前に判断する）。流れ:
//   営業が制作費・開発費の原価を入れる（＝発注申請。取引先マスタの発注先・申請理由・インボイスが必須）
//   → 承認フロー（binding 'cost:order'）で承認者が申請承認ページで承認／却下 → 承認されたら外注先に発注
// 対象は制作費・開発費の新規登録（媒体費・その他・既存の原価は対象外。2026-10 要望で媒体費以外→制作費・開発費に絞った）。案件詳細・原価割当から入れたら権限にかかわらず全員が申請し、
// 経理/マスター/管理者が原価一覧など他の画面から入れたときだけ申請しない（2026-10 ユーザー決定）。
// T番号は必須（免税事業者＝取引先マスタが「該当しない」の発注先には申請できない）。
// 取引先マスタの T番号が未登録なら申請時に入力し、同時に取引先の更新申請が出る（マスタへの反映は取引先の承認者が承認したとき）。
// 申請中・承認・却下の原価は金額・発注先・会計区分・案件を営業が変えられない（変えるなら削除して申請し直す）。
// 申請は仮原価で登録し、最終承認で実原価になる（請求書との突き合わせは経理の原価照合）。
// 承認額超過（承認後に経理が請求書の金額へ直して超えた）・承認前発注（申請中のまま請求書が届いた。承認まで仮原価のまま）・
// 承認なし（申請を通さずに入った外注費）は止めずに印を付け、
// 営業が案件の原価タブで理由を入れ、原価照合ページで月ごと・人ごとに集計する。
// 規則の正本はサーバ api/_costOrderApproval.js（対象の会計区分・入力の検証）と api/costs.js（編集の制限）。
// API: /api/cost-order-applications（申請中の一覧・承認・却下）。申請の登録は /api/costs の POST。
// ────────────────────────────────────────────────────────────────

/** 権限にかかわらず発注申請を通す画面（案件詳細・原価割当）。経理が請求書から入れる原価一覧は含めない */
const COST_ORDER_APPLY_PAGES = ["project-detail", "cost-allocation"];

/** 経理/マスター/管理者（サーバ isAccountingOrAdminRow と同じ判定）。COST_ORDER_APPLY_PAGES 以外では申請しない */
function costUserSkipsOrderApproval() {
  return isAccountingDepartmentJobRole(currentUser?.role) || isMasterOrAdminUser(currentUser);
}

/** この原価フォームの保存が発注申請になるか（新規・差し戻し修正でない・制作費・開発費で、
 *  経理/マスター/管理者以外か、案件詳細・原価割当からの登録） */
function costFormNeedsOrderApply(f) {
  if (!f || f.editingId || f.remandFix) return false;
  if (!costCategoryNeedsOrderApproval(f.category)) return false;
  return !costUserSkipsOrderApproval() || COST_ORDER_APPLY_PAGES.includes(currentRoute().page);
}

/** 取引先マスタに T番号（該当する＋T＋13桁）が入っているか（サーバ partnerInvoiceIsSet と同じ。発注申請は T番号必須） */
function costPartnerInvoiceIsSet(pt) {
  return String(pt?.invoice_issuer || "").trim() === "該当する" && /^T\d{13}$/.test(String(pt?.invoice_issuer_number || "").trim());
}

/** 免税事業者（取引先マスタが「該当しない」）。発注申請できない（2026-10 ユーザー決定: T番号必須） */
function costPartnerIsTaxExempt(pt) {
  return String(pt?.invoice_issuer || "").trim() === "該当しない";
}

function costOrderInvoiceNumberInput(f) {
  return String(f.orderInvoiceIssuerNumber || "").normalize("NFKC").trim().toUpperCase();
}

/** 原価フォームの発注申請欄（入力欄の最後・備考の上）。対象外なら空 */
function costOrderApplyFieldsHtml(f) {
  if (!costFormNeedsOrderApply(f)) return "";
  const pt = String(f.vendor || "").trim() ? costPartnerByVendorName(f.vendor) : null;
  let invoiceHtml = "";
  if (String(f.vendor || "").trim() && !pt) {
    invoiceHtml = `<p class="cost-order-apply-warn">取引先マスタに無い発注先です。先に取引先（パートナー）を登録してから申請してください。</p>`;
  } else if (pt && costPartnerInvoiceIsSet(pt)) {
    invoiceHtml = `<p class="cost-order-apply-invoice">T番号：${escapeHtml(pt.invoice_issuer_number)}<span class="pj-muted">（取引先マスタの登録）</span></p>`;
  } else if (pt && costPartnerIsTaxExempt(pt)) {
    invoiceHtml = `<p class="cost-order-apply-warn">この発注先は免税事業者（T番号なし）のため発注申請できません。T番号のある発注先を選んでください。</p>`;
  } else if (pt) {
    invoiceHtml = `
      <div class="cost-order-apply-invoice-edit">
        <label><span>T番号（取引先マスタに未登録） <span class="cl-form-required">*</span></span>
          <input data-cost-field="orderInvoiceIssuerNumber" value="${escapeHtmlAttr(f.orderInvoiceIssuerNumber || "")}" placeholder="T + 13桁の数字" autocomplete="off" />
        </label>
        <p class="cost-order-apply-warn">申請すると、取引先マスタ「${escapeHtml(pt.name || f.vendor)}」の更新申請（T番号の登録）も同時に出ます。番号は発注先の請求書・適格請求書発行事業者公表サイトで確かめてください。</p>
      </div>`;
  }
  // 取引承認が済んでいない発注先は警告だけ出す（申請はできる。承認画面にも出る。2026-10 ユーザー決定）
  const approvalWarnHtml = pt && !isClientApproved(pt)
    ? `<p class="cost-order-apply-warn">この取引先は取引承認が済んでいません（${escapeHtml(normalizeMasterApprovalStatus(pt.approval_status))}）。申請はできますが、承認者に表示されます。取引先の取引承認の申請もしてください。</p>`
    : "";
  return `
    <div class="cost-order-apply">
      <p class="cost-order-apply-lead">発注申請：承認されると原価として登録されます。承認されるまで外注先には発注しないでください。</p>
      ${approvalWarnHtml}
      ${invoiceHtml}
      <label><span>申請理由 <span class="cl-form-required">*</span></span>
        <textarea data-cost-field="orderApplyReason" rows="3" placeholder="なぜ外注するか（社内でできない理由）・相見積の有無など">${escapeHtml(f.orderApplyReason || "")}</textarea>
      </label>
    </div>`;
}

/** 発注申請の発注先プルダウンの下に出す「候補にない発注先」の入口。登録できる人（マスター／経理）は
 *  既存の新規パートナー登録モーダルを開く（原価フォームの上に重なる）。それ以外の人には依頼先を案内する */
function costOrderNewPartnerHtml(f) {
  if (!costFormNeedsOrderApply(f)) return "";
  return canManageClientPartnerMaster()
    ? `<span class="cost-form-hint">候補にない発注先は <button type="button" class="inline-btn ghost" data-open-partner-create="1">＋ 新規パートナー登録</button></span>`
    : `<span class="cost-form-hint">候補にない発注先は、マスター／経理に取引先（パートナー）の登録を依頼してください</span>`;
}

/** 保存前の検証（サーバ normalizeOrderApplyInput と同じ規則）。問題なければ "" */
function costOrderApplyFormError(f) {
  if (!costFormNeedsOrderApply(f)) return "";
  const pt = costPartnerByVendorName(f.vendor);
  if (!pt) return "制作費・開発費の原価は、取引先マスタにある発注先でしか申請できません。先に取引先（パートナー）を登録してください";
  if (costPartnerIsTaxExempt(pt)) return "この発注先は免税事業者（T番号なし）のため発注申請できません。T番号のある発注先を選んでください";
  if (!costPartnerInvoiceIsSet(pt) && !/^T\d{13}$/.test(costOrderInvoiceNumberInput(f))) {
    return "この発注先は T番号が未登録です。登録番号（T＋13桁の数字）を入力してください";
  }
  if (!String(f.orderApplyReason || "").trim()) return "申請理由（なぜ外注するか・相見積の有無など）を入力してください";
  return "";
}

/** 原価フォーム → 発注申請の内容（対象外なら null） */
function costOrderApplyFromForm(f) {
  if (!costFormNeedsOrderApply(f)) return null;
  const pt = costPartnerByVendorName(f.vendor);
  return {
    partnerId: pt ? Number(pt.id) : null,
    reason: String(f.orderApplyReason || "").trim(),
    // マスタに T番号が無いときだけ申請で入れた番号を送る（承認でマスタへ入る）
    invoiceIssuerNumber: pt && !costPartnerInvoiceIsSet(pt) ? costOrderInvoiceNumberInput(f) : ""
  };
}

/** POST /api/costs に載せる発注申請のキー */
function costOrderApplyRequestFields(o) {
  return {
    // 経理/マスター/管理者が案件詳細・原価割当から入れるときも申請にする合図（サーバ api/costs.js createOneCost）
    orderApply: true,
    orderPartnerId: o.partnerId,
    orderApplyReason: o.reason,
    orderInvoiceIssuerNumber: o.invoiceIssuerNumber
  };
}

/** 発注申請した原価（申請中・承認・却下）は案件を変えられない（付け替え・異議を出さない。サーバ api/_costOrderApproval.js と同じ） */
function costOrderProjectLocked(c) {
  return ["申請中", "承認", "却下"].includes(c?.orderApprovalStatus);
}

/** 原価一覧の発注申請バッジ（申請の対象外は空） */
function costOrderStatusBadgeHtml(c) {
  const st = c?.orderApprovalStatus;
  const ex = costOrderExceptionOf(c);
  // 承認後は発注の証跡（登録完了／証跡待ち／期限切れ。15k-order-evidence.js）
  const ev = st === "承認" ? orderEvidenceBadgeHtml(c) : "";
  if (ex) {
    const why = c.orderExceptionReason ? `理由: ${c.orderExceptionReason}` : "理由が未入力です（案件の原価タブで入力）";
    if (ex.kind === "overrun") {
      return `<span class="cost-order-badge is-rejected" title="${escapeHtmlAttr(`承認額 ${yen.format(c.orderApprovedAmount)} を超えています。${why}`)}">承認額超過 +${escapeHtml(yen.format(ex.yen))}</span>${ev}`;
    }
    if (ex.kind === "preordered") {
      return `<span class="cost-order-badge is-unapproved" title="${escapeHtmlAttr(`発注申請が承認される前に請求書が届きました（承認前に発注）。${why}`)}">承認前発注${st === "申請中" ? "（申請中）" : ""}</span>${ev}`;
    }
    return `<span class="cost-order-badge is-unapproved" title="${escapeHtmlAttr(`発注申請を通さずに登録された外注費です。${why}`)}">承認なし</span>`;
  }
  if (st === "申請中") return `<span class="cost-order-badge is-pending" title="承認されるまで外注先には発注しないでください">発注申請中</span>`;
  if (st === "承認") return `<span class="cost-order-badge is-approved">発注承認済</span>${ev}`;
  if (st === "却下") {
    const why = c.orderReviewComment ? `却下理由: ${c.orderReviewComment}` : "発注申請は却下されました";
    return `<span class="cost-order-badge is-rejected" title="${escapeHtmlAttr(why)}">発注却下</span>`;
  }
  return "";
}

/** 原価一覧の「発注承認」列（2026-10 要望: 計上案件のセルから分けて 済・未・ー の3つで出す）。
 *  済＝承認／未＝申請中・却下・承認なし（申請を通さずに入った制作費・開発費）／ー＝発注申請の対象外（媒体費・その他・既存データ）。
 *  詳しい状態は title に出し、見落とせない承認額超過・承認前発注は下に印を残す */
function costOrderApprovalCellHtml(c) {
  const st = c?.orderApprovalStatus;
  const [mark, cls, title] =
    st === "承認"
      ? ["済", "done", "発注承認済"]
      : st === "申請中"
        ? ["未", "pending", "発注申請中（承認待ち）"]
        : st === "却下"
          ? ["未", "pending", c.orderReviewComment ? `発注却下（理由: ${c.orderReviewComment}）` : "発注却下"]
          : st === "承認なし"
            ? ["未", "pending", "発注申請を通さずに登録された外注費（承認なし）"]
            : ["ー", "none", "発注申請の対象外"];
  const ex = costOrderExceptionOf(c);
  const exBadge = ex && ex.kind !== "unapproved" ? costOrderStatusBadgeHtml(c) : st === "承認" ? orderEvidenceBadgeHtml(c) : "";
  return `<span class="cost-order-approval cost-order-approval--${cls}" title="${escapeHtmlAttr(title)}">${mark}</span>${exBadge}`;
}

// ── 承認額超過・承認なし・承認前発注（止めずに印を付けて理由を残す。2026-10 ユーザー決定） ──
/** 承認額超過（承認後に請求書の金額へ直して承認額を超えた）・承認前発注（申請中のまま請求書が届いた）・
 *  承認なし（申請を通さずに入った外注費）。該当しなければ null。超過の判定はサーバ api/_costOrderApproval.js costOrderOverrunYen と同じ */
function costOrderExceptionOf(c) {
  if (c?.orderApprovalStatus === "承認なし") return { kind: "unapproved", yen: Math.round(Number(c.amount) || 0) };
  if (c?.orderApprovalStatus === "承認" && c.orderApprovedAmount != null) {
    const over = (Math.round(Number(c.amount) || 0)) - c.orderApprovedAmount;
    if (over > 0) return { kind: "overrun", yen: over };
  }
  if (c?.orderPreOrderedAt) return { kind: "preordered", yen: Math.round(Number(c.amount) || 0) };
  return null;
}

/** 経理の新規登録で、同じ案件・発注先に発注申請中の原価があれば確認する（承認前に発注して請求書が来た＝新規登録すると
 *  申請が承認されたときに二重計上になる）。続けてよければ true */
function confirmCostOrderPendingDuplicate(projectUiId, vendor) {
  const p = projects.find((x) => x.id === projectUiId);
  const key = compactVendorMatchKey(vendor || "");
  const pending = p && key ? (p.costs || []).find((c) => c.orderApprovalStatus === "申請中" && compactVendorMatchKey(c.vendor || "") === key) : null;
  if (!pending) return true;
  return window.confirm(
    `この案件には同じ発注先の発注申請中の原価（${yen.format(Number(pending.amount) || 0)}）があります。\n` +
      "承認前に発注して請求書が届いた場合は、新規登録せずに原価照合でその原価と突き合わせてください（新規登録すると、申請が承認されたときに二重計上になります）。\n\nそれでも新規登録しますか？"
  );
}

/** 案件の原価一覧の「理由を入力」ボタン（超過・承認なしの原価だけ） */
function costOrderExceptionReasonBtnHtml(c, projectUiId) {
  if (!costOrderExceptionOf(c)) return "";
  return `<button type="button" class="inline-btn ${c.orderExceptionReason ? "ghost" : ""}" data-cost-order-reason-open="${escapeHtmlAttr(`${projectUiId}:${c.id}`)}">${c.orderExceptionReason ? "理由を直す" : "理由を入力"}</button>`;
}

function renderCostOrderReasonModal() {
  const m = uiState.costOrderReasonModal;
  if (!m) return "";
  return `
    <div class="composer-backdrop" data-cost-order-reason-close="1"></div>
    <aside class="composer-panel project-create-panel cost-dispute-panel" role="dialog" aria-modal="true" aria-labelledby="cost-order-reason-title">
      <div class="composer-head">
        <div><h3 id="cost-order-reason-title">${escapeHtml(m.title)}の理由</h3><p>${escapeHtml(m.costLabel)}</p></div>
        <button class="inline-btn ghost" data-cost-order-reason-close="1">閉じる</button>
      </div>
      <div class="composer-body">
        <p class="prompt-copy">${escapeHtml(m.lead)}</p>
        <label class="cl-form-group">
          <span class="cl-form-label">理由</span>
          <textarea class="pj-input cost-dispute-reason" rows="3" data-cost-order-reason-text placeholder="例）先方の追加修正が2回発生したため／急ぎで申請が間に合わなかった">${escapeHtml(m.reason || "")}</textarea>
        </label>
      </div>
      <div class="composer-foot">
        <button class="inline-btn ghost" data-cost-order-reason-close="1">キャンセル</button>
        <button class="inline-btn" data-cost-order-reason-submit="1" ${m.saving ? "disabled" : ""}>${m.saving ? "保存中…" : "保存する"}</button>
      </div>
    </aside>`;
}

function openCostOrderReasonModal(key) {
  const [, costUiId] = String(key).split(":");
  const c = findCostPlacementByUiId(costUiId)?.cost;
  const ex = costOrderExceptionOf(c);
  if (!c || !ex) return;
  uiState.costOrderReasonModal = {
    costUiId,
    reason: c.orderExceptionReason || "",
    title: ex.kind === "overrun" ? "承認額超過" : ex.kind === "preordered" ? "承認前発注" : "承認なし",
    costLabel: `${c.vendor || "—"} ${yen.format(Number(c.amount) || 0)}`,
    lead: ex.kind === "overrun"
      ? `発注申請の承認額 ${yen.format(c.orderApprovedAmount)} を ${yen.format(ex.yen)} 超えています。なぜ増えたかを残してください（集計で上長・経理が確認します）。`
      : ex.kind === "preordered"
        ? "発注申請が承認される前に請求書が届きました（承認前に発注）。なぜ承認を待てなかったかを残してください（集計で上長・経理が確認します）。"
        : "発注申請を通さずに登録された外注費です。申請しなかった理由を残してください（集計で上長・経理が確認します）。",
    saving: false
  };
  render();
}

async function submitCostOrderReason() {
  const m = uiState.costOrderReasonModal;
  if (!m || m.saving) return;
  const c = findCostPlacementByUiId(m.costUiId)?.cost;
  const dbId = costDbNumericId({ id: m.costUiId });
  if (!c || dbId == null) {
    showToastError("原価が見つかりません。再読み込みしてください");
    return;
  }
  m.saving = true;
  render();
  const { ok, data } = await apiFetch("/api/cost-order-applications", {
    method: "PUT",
    body: { id: dbId, action: "exception-reason", reason: String(m.reason || "").trim() }
  });
  m.saving = false;
  if (!ok) {
    showToastError(data.error || "理由を保存できませんでした");
    render();
    return;
  }
  if (data.reason) c.orderExceptionReason = data.reason;
  else delete c.orderExceptionReason;
  uiState.costOrderReasonModal = null;
  showToast("理由を保存しました");
  render();
}

/** 原価照合ページの集計（選んだ計上月の承認額超過・承認なしを人ごとに）。超過は申請者、承認なしは案件の担当営業に付ける */
function costOrderExceptionSummaryHtml(monthInput) {
  const rows = costReconcileMonthRows(monthInput)
    .map((c) => ({ c, ex: costOrderExceptionOf(c) }))
    .filter((r) => r.ex);
  if (!rows.length) return "";
  const byPerson = new Map();
  for (const { c, ex } of rows) {
    const pj = projects.find((p) => p.id === c._projectId);
    const uid = ex.kind === "unapproved" ? pj?.salesOwnerId || "" : c.orderAppliedByDbId != null ? `u${c.orderAppliedByDbId}` : "";
    const key = uid || "-";
    if (!byPerson.has(key)) {
      byPerson.set(key, { name: getUserById(uid)?.name || (uid ? uid : "担当未設定・案件未割当"), overN: 0, overYen: 0, preN: 0, preYen: 0, unN: 0, unYen: 0, noReason: 0 });
    }
    const s = byPerson.get(key);
    if (ex.kind === "overrun") {
      s.overN += 1;
      s.overYen += ex.yen;
    } else if (ex.kind === "preordered") {
      s.preN += 1;
      s.preYen += ex.yen;
    } else {
      s.unN += 1;
      s.unYen += ex.yen;
    }
    if (!c.orderExceptionReason) s.noReason += 1;
  }
  const trs = [...byPerson.values()]
    .sort((a, b) => b.overYen + b.preYen + b.unYen - (a.overYen + a.preYen + a.unYen))
    .map(
      (s) => `<tr>
        <td>${escapeHtml(s.name)}</td>
        <td class="cost-amount">${s.overN ? `${s.overN}件・+${escapeHtml(yen.format(s.overYen))}` : "—"}</td>
        <td class="cost-amount">${s.preN ? `${s.preN}件・${escapeHtml(yen.format(s.preYen))}` : "—"}</td>
        <td class="cost-amount">${s.unN ? `${s.unN}件・${escapeHtml(yen.format(s.unYen))}` : "—"}</td>
        <td class="cost-amount">${s.noReason ? `<strong style="color:#b91c1c;">${s.noReason}件</strong>` : "0件"}</td>
      </tr>`
    )
    .join("");
  return `
    <section class="cost-order-exception-summary soft-card" aria-label="発注申請の例外">
      <div class="cost-order-exception-summary-head">
        <strong>発注申請の例外（この月）</strong>
        <span class="pj-muted">承認額超過・承認前発注は申請者、承認なしは案件の担当営業ごと。止めずに記録し、理由は案件の原価タブで入力します</span>
      </div>
      <table class="cost-table">
        <thead><tr><th scope="col">担当</th><th scope="col">承認額超過</th><th scope="col">承認前発注</th><th scope="col">承認なし</th><th scope="col">理由未入力</th></tr></thead>
        <tbody>${trs}</tbody>
      </table>
    </section>`;
}

document.addEventListener("input", (e) => {
  const el = e.target;
  if (el instanceof HTMLTextAreaElement && el.matches("[data-cost-order-reason-text]") && uiState.costOrderReasonModal) {
    uiState.costOrderReasonModal.reason = el.value;
  }
});

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  const openEl = target.closest("[data-cost-order-reason-open]");
  if (openEl instanceof HTMLElement) {
    openCostOrderReasonModal(openEl.dataset.costOrderReasonOpen);
    return;
  }
  if (target.closest("[data-cost-order-reason-submit]")) {
    void submitCostOrderReason();
    return;
  }
  if (target.closest("[data-cost-order-reason-close]") && uiState.costOrderReasonModal) {
    uiState.costOrderReasonModal = null;
    render();
  }
});

// ── 申請承認ページ ──
async function fetchCostOrderApplications() {
  if (!authToken) return;
  const { ok, data } = await apiFetch("/api/cost-order-applications");
  uiState.costOrderApplications = ok && Array.isArray(data.applications) ? data.applications : [];
  uiState.costOrderApplicationsLoaded = true;
  render();
}

/** 申請承認ページの行（自分の番の申請。マスターは自分の番でない申請中も見える＝操作は自分の番のときのみ） */
function costOrderAppMgmtRows() {
  if (!uiState.costOrderApplicationsLoaded) fetchOnceInFlight("costOrderApplications", fetchCostOrderApplications);
  const isMaster = isMasterRoleUser(currentUser);
  return (uiState.costOrderApplications || [])
    .filter((a) => a && (a.canApprove || (isMaster && a.status === "申請中")))
    .map((a) => {
      const id = String(a.id);
      const currentApproverName = a.approverChain?.find((c) => c.userId === a.currentApproverUserId)?.name || "";
      return {
        uid: `costorder:${id}`,
        kind: "costorder",
        key: id,
        categoryLabel: "発注申請",
        subLabel: `${a.preOrderedAt ? "⚠承認前発注 " : ""}${a.vendor || "—"} ${yen.format(a.amount || 0)}（${a.projectName || "案件なし"}）`,
        applicantName: a.applicantName || "—",
        appliedAtIso: a.createdAt || "",
        statusHtml: a.chainLength ? `${a.approvedCount}/${a.chainLength}` : "—",
        // 金額・粗利を見て判断する申請なので一括承認の対象にしない
        selectable: false,
        opHtml: a.canApprove
          ? `<button type="button" class="chip-btn chip-btn--approve" data-app-mgmt-costorder-approve="${escapeHtmlAttr(id)}">✓ 承認</button>
        <button type="button" class="chip-btn ghost" data-app-mgmt-costorder-reject="${escapeHtmlAttr(id)}">却下</button>`
          : `<span class="pj-muted">${currentApproverName ? `承認待ち：${escapeHtml(currentApproverName)}` : "—"}</span>`
      };
    });
}

/** 社内申請ページ「自分の申請」に出す、自分の申請中の発注申請（API は申請中だけ返す。承認・却下は案件の原価タブと通知で分かる） */
function myCostOrderApplications() {
  if (!uiState.costOrderApplicationsLoaded) fetchOnceInFlight("costOrderApplications", fetchCostOrderApplications);
  const me = String(currentUser?.dbId ?? "");
  return (uiState.costOrderApplications || []).filter((a) => me && a.applicantUserId === me);
}

/** 自分の申請カード（発注申請）。取り下げは原価の削除で行うため、ボタンは案件の原価タブへの導線にする */
function costOrderApplicationCardHtml(a) {
  const approver = a.approverChain?.find((c) => c.userId === a.currentApproverUserId)?.name || "";
  const progress = toolAppProgressText(a);
  const date = allowedToolDate(a.createdAt);
  return `
    <div class="at-app-card">
      <div class="at-app-card-head">
        <div class="at-app-title"><span class="at-app-type">発注</span>${escapeHtml(a.vendor || "—")} ${escapeHtml(yen.format(a.amount || 0))}</div>
        ${toolAppStatusBadge(a.status)}
      </div>
      <div class="at-app-meta">
        <span class="at-app-meta-item">案件：${escapeHtml(a.projectName || "—")}</span>
        ${progress ? `<span class="at-app-meta-item">${escapeHtml(progress)}${approver ? `（承認待ち：${escapeHtml(approver)}）` : ""}</span>` : ""}
        ${date ? `<span class="at-app-meta-item">${escapeHtml(date)}</span>` : ""}
      </div>
      ${a.projectDbId ? `<div class="at-app-actions"><a class="cl-btn cl-btn-outline" href="#project-detail:p${escapeHtmlAttr(String(a.projectDbId))}?tab=cost">案件の原価を開く</a></div>` : ""}
    </div>`;
}

/** 判断材料: 案件の売上・原価計（この申請を含む）・見込粗利率と、同じ発注先への今年の原価合計（見える案件の範囲） */
function costOrderJudgeRowsHtml(a) {
  const p = projects.find((x) => Number(x.dbId) === Number(a.projectDbId));
  let marginHtml = '<span class="pj-muted">—（案件を表示できません）</span>';
  if (p) {
    const s = projectGrossMarginSummary(p);
    const revenue = projectDisplayedBillingExTaxYen(p);
    marginHtml = `売上 ${escapeHtml(yen.format(revenue))} ／ 原価計（税抜） ${escapeHtml(yen.format(s.totalCost))}（この申請を含む） ／ 見込粗利 <strong style="color:${s.grossProfit < 0 ? "#dc2626" : "#059669"};">${escapeHtml(yen.format(s.grossProfit))}（${s.marginPct}%）</strong>`;
  }
  const year = String(a.targetMonth || "").slice(0, 4);
  const key = compactVendorMatchKey(a.vendor || "");
  let vendorYearTotal = 0;
  for (const pj of projects) {
    for (const c of countedProjectCosts(pj.costs)) {
      if (compactVendorMatchKey(c.vendor || "") === key && String(c.targetMonth || "").startsWith(year)) vendorYearTotal += Number(c.amount || 0);
    }
  }
  return `
    ${appMgmtDetailRowHtml("案件の粗利", marginHtml)}
    ${year ? appMgmtDetailRowHtml(`${year}年の同じ発注先`, `${escapeHtml(yen.format(vendorYearTotal))}（この申請を含む・税抜）`) : ""}`;
}

/** 申請承認ページの詳細（14-expense-payment-forms.js の詳細モーダルから呼ぶ） */
function costOrderAppDetail(key) {
  const a = (uiState.costOrderApplications || []).find((x) => String(x.id) === String(key));
  if (!a) return null;
  const flowChain = appMgmtChainToFlowChain(a.approverChain);
  const flowBlock = flowChain.length
    ? approvalFlowVerticalHtmlFromChain(flowChain, a.approvedCount || 0)
    : `<span class="pj-muted">—（承認者未設定）</span>`;
  const invoice =
    a.invoiceIssuer === "該当する"
      ? `適格請求書発行事業者（${escapeHtml(a.invoiceIssuerNumber || "—")}）`
      : a.invoiceIssuer === "該当しない"
        ? '<strong style="color:#b45309;">該当しない（免税事業者）</strong>'
        : '<span class="pj-muted">—</span>';
  return {
    title: `${a.vendor || "—"}への発注`,
    bodyHtml: `
      ${a.preOrderedAt ? appMgmtDetailRowHtml("承認前発注", '<strong style="color:#b45309;">承認前に発注済み（請求書が届いています）。事後承認です</strong>') : ""}
      ${appMgmtDetailRowHtml("案件", escapeHtml(a.projectName || "—"))}
      ${appMgmtDetailRowHtml("発注先", escapeHtml(a.vendor || "—"))}
      ${appMgmtDetailRowHtml("取引承認", isClientApproved({ approvalStatus: a.partnerApprovalStatus }) ? "承認済" : `<strong style="color:#b45309;">${escapeHtml(normalizeMasterApprovalStatus(a.partnerApprovalStatus))}（取引承認が済んでいない取引先）</strong>`)}
      ${appMgmtDetailRowHtml("会計区分", escapeHtml(a.category || "—"))}
      ${appMgmtDetailRowHtml("金額（税抜）", escapeHtml(yen.format(a.amount || 0)))}
      ${appMgmtDetailRowHtml("計上月", escapeHtml(a.targetMonth || "—"))}
      ${appMgmtDetailRowHtml("インボイス", invoice + (a.invoiceFromApplication ? '<span class="pj-muted">（申請で入力・取引先の更新申請中）</span>' : ""))}
      ${appMgmtDetailRowHtml("申請者", escapeHtml(a.applicantName || "—"))}
      ${appMgmtDetailTextRowHtml("申請理由", a.reason)}
      ${costOrderJudgeRowsHtml(a)}
      <p style="margin:12px 0 8px;font-size:12px;font-weight:600;color:#334155;">承認フロー</p>
      <div style="margin-bottom:12px;">${flowBlock}</div>`
  };
}

async function approveCostOrderApplication(id) {
  if (uiState.costOrderApprovalSaving) return;
  const seenApp = (uiState.costOrderApplications || []).find((a) => String(a?.id) === String(id));
  const ok0 = await fpConfirm({
    title: "この発注申請を承認しますか？",
    message: seenApp ? `${seenApp.vendor || "—"}　${yen.format(Number(seenApp.amount) || 0)}（税抜）` : "",
    note: "承認すると原価として登録され、申請者が外注先に発注します。",
    confirmLabel: "承認する",
    danger: false
  });
  if (!ok0) return;
  uiState.costOrderApprovalSaving = true;
  // 承認者が画面で見た金額を送る（申請者が承認中に金額を変えていたら、サーバが 409 で止める）
  const body = { id, action: "approve", ...(seenApp && Number.isFinite(Number(seenApp.amount)) ? { expectedAmount: Number(seenApp.amount) } : {}) };
  const { ok, data } = await apiFetch("/api/cost-order-applications", { method: "PUT", body });
  uiState.costOrderApprovalSaving = false;
  if (!ok) {
    showToastError(data.error || "承認に失敗しました");
    return;
  }
  showToast(data.application?.status === "承認" ? "承認しました（原価として登録し、申請者に発注OKを通知しました）" : "承認しました（次の承認者へ回しました）");
  await fetchCostOrderApplications();
}

function costOrderRejectModalHtml() {
  const draft = uiState.costOrderRejectDraft;
  const a = draft && (uiState.costOrderApplications || []).find((x) => String(x.id) === String(draft.id));
  if (!a) return "";
  return `
    <div class="cl-mini-modal-overlay open" data-costorder-reject-close="1">
      <div class="cl-mini-modal at-modal">
        <div class="cl-mini-modal-head">
          <h4>発注申請を却下</h4>
          <button class="cl-modal-close" type="button" data-costorder-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(a.vendor || "—")} ${escapeHtml(yen.format(a.amount || 0))}</div>
            <div class="at-app-meta"><span class="at-app-meta-item">申請者：${escapeHtml(a.applicantName || "—")}</span><span class="at-app-meta-item">案件：${escapeHtml(a.projectName || "—")}</span></div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="costorder-reject-comment" class="cl-form-input" rows="3" placeholder="例: 社内で対応してください／相見積を取ってください"></textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-costorder-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-costorder-reject-submit="1"${uiState.costOrderApprovalSaving ? " disabled" : ""}>却下する</button>
        </div>
      </div>
    </div>`;
}

async function rejectCostOrderApplication() {
  const draft = uiState.costOrderRejectDraft;
  if (!draft || uiState.costOrderApprovalSaving) return;
  const comment = (document.getElementById("costorder-reject-comment")?.value || "").trim();
  if (!comment) {
    showToastError("却下理由を入力してください");
    return;
  }
  uiState.costOrderApprovalSaving = true;
  const { ok, data } = await apiFetch("/api/cost-order-applications", { method: "PUT", body: { id: draft.id, action: "reject", reviewComment: comment } });
  uiState.costOrderApprovalSaving = false;
  if (!ok) {
    showToastError(data.error || "却下に失敗しました");
    return;
  }
  uiState.costOrderRejectDraft = null;
  showToast("却下しました");
  await fetchCostOrderApplications();
}

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || currentRoute().page !== "application-management") return;
  const approveEl = target.closest("[data-app-mgmt-costorder-approve]");
  if (approveEl instanceof HTMLElement) {
    void approveCostOrderApplication(approveEl.dataset.appMgmtCostorderApprove);
    return;
  }
  const rejectEl = target.closest("[data-app-mgmt-costorder-reject]");
  if (rejectEl instanceof HTMLElement) {
    uiState.costOrderRejectDraft = { id: rejectEl.dataset.appMgmtCostorderReject };
    render();
    return;
  }
  if (target.closest("[data-costorder-reject-submit]")) {
    void rejectCostOrderApplication();
    return;
  }
  const rejectClose = target.closest("[data-costorder-reject-close]");
  const onOverlayInside = rejectClose?.classList.contains("cl-mini-modal-overlay") && target.closest(".cl-mini-modal");
  if (rejectClose instanceof HTMLElement && !onOverlayInside) {
    uiState.costOrderRejectDraft = null;
    render();
  }
});

// 画面を移ったら却下モーダルは閉じ、次に開いたとき最新の申請を取り直す
window.addEventListener("hashchange", () => {
  uiState.costOrderRejectDraft = null;
  uiState.costOrderApplicationsLoaded = false;
});
// ── 送付した書類ダウンロードリンクの一覧と無効化（発行履歴モーダルの下段） ──
// メール送付（ダウンロードページ方式）のリンクはログイン不要・90日有効。誤った宛先に送ったときに止める手段として、
// 発行履歴モーダルに一覧と「無効化」ボタンを出す（2026-10 コード全体レビュー）。
// API: /api/doc-links（GET 一覧・POST 失効）。リンク本体（トークン）は画面に来ない。

const DOC_LINKS_REFRESH_MS = 30000;

async function fetchDocLinks(projectDbId, docType) {
  const key = `${projectDbId}:${docType}`;
  const { ok, data } = await apiFetch(`/api/doc-links?projectId=${encodeURIComponent(projectDbId)}&docType=${encodeURIComponent(docType)}`);
  if (!uiState.docLinks) uiState.docLinks = {};
  uiState.docLinks[key] = { at: Date.now(), links: ok && Array.isArray(data.links) ? data.links : null, error: ok ? "" : data.error || "読み込みに失敗しました" };
  render();
}

/** 発行履歴モーダルの下段。モーダルを開いている間は 30 秒ごとに読み直す（閲覧回数・他の人の無効化を反映） */
function docLinksSectionHtml(projectDbId, docType) {
  if (!projectDbId) return "";
  const key = `${projectDbId}:${docType}`;
  const cache = uiState.docLinks?.[key];
  if (!cache || Date.now() - cache.at > DOC_LINKS_REFRESH_MS) fetchOnceInFlight(`docLinks:${key}`, () => fetchDocLinks(projectDbId, docType));

  let body;
  if (!cache) body = `<p class="peh-empty">読み込み中…</p>`;
  else if (cache.error) body = `<p class="peh-empty">${escapeHtml(cache.error)}</p>`;
  else if (!cache.links.length) body = `<p class="peh-empty">メールで送ったダウンロードリンクはありません。</p>`;
  else {
    const now = Date.now();
    body = cache.links.map((l) => {
      const expired = l.expiresAt && new Date(l.expiresAt).getTime() < now;
      const state = l.revokedAt
        ? `無効化済み（${formatIssueHistoryDateTime(l.revokedAt)}）`
        : expired ? "期限切れ" : `有効（${formatIssueHistoryDateTime(l.expiresAt)} まで）`;
      const revokeBtn = !l.revokedAt && !expired
        ? `<button class="inline-btn ghost danger" type="button" data-doc-link-revoke="${escapeHtmlAttr(l.id)}" data-doc-link-project="${escapeHtmlAttr(projectDbId)}" data-doc-link-doctype="${escapeHtmlAttr(docType)}">無効化</button>`
        : "";
      return `<div class="peh-entry">
        <div class="peh-entry-head">
          <span class="peh-entry-dt">${formatIssueHistoryDateTime(l.createdAt)}</span>
          <span class="peh-entry-who">${escapeHtml(l.createdByName || "—")}</span>
          ${revokeBtn}
        </div>
        <ul class="peh-changes">
          <li class="peh-change"><span class="peh-change-label">状態</span><span class="peh-change-diff"><span class="peh-change-after">${escapeHtml(state)}</span></span></li>
          <li class="peh-change"><span class="peh-change-label">閲覧</span><span class="peh-change-diff"><span class="peh-change-after">${escapeHtml(`${l.accessCount}回${l.lastAccessAt ? `（最終 ${formatIssueHistoryDateTime(l.lastAccessAt)}）` : ""}`)}</span></span></li>
        </ul>
      </div>`;
    }).join("");
  }
  return `<h4 class="peh-section-title">送付したダウンロードリンク</h4>${body}`;
}

document.addEventListener("click", async (event) => {
  const btn = event.target instanceof Element ? event.target.closest("[data-doc-link-revoke]") : null;
  if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
  const projectDbId = btn.dataset.docLinkProject;
  const docType = btn.dataset.docLinkDoctype;
  const ok0 = await fpConfirm({
    title: "このダウンロードリンクを無効化しますか？",
    message: "メールを受け取った人は、このリンクから書類を開けなくなります。",
    note: "元に戻せません（必要ならもう一度送付してください）。",
    confirmLabel: "無効化する"
  });
  if (!ok0) return;
  btn.disabled = true;
  const { ok, data } = await apiFetch("/api/doc-links", { method: "POST", body: { projectId: Number(projectDbId), linkId: btn.dataset.docLinkRevoke } });
  if (!ok) {
    btn.disabled = false;
    showToastError(data.error || "リンクの無効化に失敗しました");
    return;
  }
  showToast("リンクを無効化しました");
  await fetchDocLinks(projectDbId, docType);
});
// ── 書類作成・編集画面の保存: 下部バーの「保存」と未保存表示・離脱確認（2026-10 要望） ──
// 書類画面は入力のたびに自動保存していたが、保存されたかが画面で分からなかったため、案件詳細と同じく
// 「保存」ボタンで確定する（board と同じ）。編集は次の2つに溜まる:
//  - 明細: uiState.projectBillingManualDirty（markProjectDocsDraftDirty が書類画面でも立てる）
//  - 支払期限・備考など: _docMetaPendingEdits（scheduleDocMetaSaveToServer が書類画面では送らずに記録だけする）
// 保存のしかたは「単体で保存」か「他の書類に反映」（備考を反映先へ上書き。00-shared-enums.js DOC_REMARK_REFLECT_TARGETS）。
// 明細はもともと全書類で共通なので反映するものは無い。

/** 案件ごとの保存状態（保存中・失敗・保存した直後） */
const _docPageSaveState = {};

function docPageSaveState(pid) {
  const k = String(pid || "");
  return _docPageSaveState[k] || (_docPageSaveState[k] = { saving: false, saveFailed: false, justSaved: false });
}

/** 書類画面で未保存の変更があるか（明細・支払期限・備考など） */
function docPageIsDirty(pid) {
  const k = String(pid || "");
  return projectBillingLinesManualDirty(k) || Object.keys(_docMetaPendingEdits[k] || {}).length > 0;
}

function docPageSaveStatusText(pid) {
  const st = docPageSaveState(pid);
  if (st.saving) return { text: "保存中…", cls: "is-saving" };
  if (docPageIsDirty(pid)) {
    st.justSaved = false;
    return { text: st.saveFailed ? "保存に失敗しました（もう一度保存してください）" : "未保存の変更があります", cls: "is-dirty" };
  }
  return st.justSaved ? { text: "保存済み", cls: "is-clean" } : { text: "", cls: "is-idle" };
}

function refreshDocPageSaveStatus() {
  const el = document.querySelector("[data-doc-save-status]");
  if (!el) return;
  const st = docPageSaveStatusText(el.dataset.docSaveStatus);
  el.textContent = st.text;
  el.className = `doc-save-status ${st.cls}`;
}

/** 下部バーの保存欄（状態・保存のしかた・保存ボタン） */
function docPageSaveBarHtml(p, activeTab) {
  const st = docPageSaveStatusText(p.id);
  const label = DOC_SEAL_TAB_LABELS[activeTab] || (activeTab === "cover" ? "送付状" : "書類");
  const reflectLabel = activeTab === "quote" ? "備考を他の書類に反映" : activeTab === "invoice" ? "領収書へ備考を反映" : "";
  const projAttr = escapeHtmlAttr(p.id);
  return `<div class="doc-save-bar">
        <span class="doc-save-status ${st.cls}" data-doc-save-status="${projAttr}" role="status" aria-live="polite">${escapeHtml(st.text)}</span>
        ${reflectLabel ? `<select class="doc-save-mode" data-doc-save-mode data-search-dropdown-off="1" data-placeholder-gray-off aria-label="保存のしかた">
          <option value="">${escapeHtml(label)}単体で保存</option>
          <option value="reflect">${escapeHtml(reflectLabel)}</option>
        </select>` : ""}
        <button type="button" class="doc-save-btn" data-doc-save="${projAttr}" data-doc-tab="${escapeHtmlAttr(activeTab)}"${st.cls === "is-saving" ? " disabled" : ""}>保存</button>
      </div>`;
}

/** 表示中の書類の備考を反映先へ上書きし、反映先も保存対象にする */
function reflectDocRemark(pid, tab) {
  const draft = ensureProjectDocDraft(pid);
  if (!draft.remark || typeof draft.remark !== "object") draft.remark = {};
  const src = resolveDocRemark(draft.remark, tab);
  const targets = DOC_REMARK_REFLECT_TARGETS[tab] || [];
  // 反映先で個別に書いた備考（既定文言のままでも空でもないもの）は消える。黙って消さずに確かめる
  const overwritten = targets.filter((t) => {
    const cur = String(draft.remark[t] ?? "").trim();
    return draft.remark[t] !== undefined && cur && cur !== resolveDocRemark({}, t) && cur !== reflectedDocRemark(src, t);
  });
  if (
    overwritten.length &&
    !confirm(`${overwritten.map((t) => DOC_SEAL_TAB_LABELS[t] || t).join("・")}の備考は個別に入力されています。\n${DOC_SEAL_TAB_LABELS[tab] || ""}の備考で上書きしますか？`)
  ) {
    return false;
  }
  for (const t of targets) {
    draft.remark[t] = reflectedDocRemark(src, t);
    scheduleDocMetaSaveToServer(pid, t, null);
  }
  return true;
}

/** 書類画面の変更を保存する（明細 → 支払期限・備考の順）。成功で true */
async function saveDocPage(pid, tab, reflect) {
  const k = String(pid || "");
  const st = docPageSaveState(k);
  const p = projects.find((x) => String(x.id) === k);
  if (!p || st.saving) return false;
  // サーバーへ送れない状態（未ログイン・案件がまだ DB に無い）で「保存しました」と出さない
  if (!authToken || !p.dbId) {
    showToastError("この案件はまだ保存できません。画面を再読み込みしてからもう一度お試しください");
    return false;
  }
  if (reflect && !reflectDocRemark(k, tab)) return false;
  if (!docPageIsDirty(k)) {
    showToast("保存する変更はありません");
    return true;
  }
  st.saving = true;
  refreshDocPageSaveStatus();
  try {
    if (projectBillingLinesManualDirty(k)) await persistProjectDetailToServer(p, { includeBillingLines: true });
    // 失敗時は persistDocMetaToServer がトーストを出し、編集を未送信のまま残す
    const meta = await persistDocMetaToServer(k);
    st.saveFailed = !meta.ok;
    if (!meta.ok) return false;
    st.justSaved = true;
    showToast(reflect ? "保存して他の書類に反映しました" : "保存しました");
    return true;
  } catch (err) {
    st.saveFailed = true;
    const reason = String(err?.serverMessage || "").trim();
    showToastError(reason ? `保存に失敗しました：${reason}` : "保存に失敗しました");
    return false;
  } finally {
    st.saving = false;
    render();
  }
}

/** 「保存せずに移動」: 書類画面の未保存の変更を捨てる。支払期限・備考は次に開いたときサーバーから読み直す */
function discardDocPageEdits(pid) {
  const k = String(pid || "");
  discardProjectDetailBillingLines(k);
  if (!_docMetaPendingEdits[k]) return;
  delete _docMetaPendingEdits[k];
  const draft = uiState.projectDocsDrafts?.[k];
  if (draft) {
    draft.meta = {};
    draft.remark = {};
  }
  if (uiState._docMetaServerLoaded) delete uiState._docMetaServerLoaded[k];
  persistProjectDocDraftsStore();
}

document.addEventListener("click", (e) => {
  const btn = e.target instanceof Element ? e.target.closest("[data-doc-save]") : null;
  if (!btn || currentRoute().page !== "project-docs") return;
  const mode = btn.closest(".doc-save-bar")?.querySelector("[data-doc-save-mode]");
  void saveDocPage(btn.dataset.docSave, btn.dataset.docTab, mode?.value === "reflect");
});

// 入力のたびに「未保存の変更があります」を更新する（下書きへの反映は既存の input/change ハンドラが先に行う）
["input", "change"].forEach((type) => {
  document.addEventListener(type, () => {
    if (currentRoute().page !== "project-docs") return;
    setTimeout(refreshDocPageSaveStatus, 0);
  });
});

/** クリックの離脱確認で「保存せずに移動」を選んだ直後か（hashchange 側の確認を省く） */
let docPageLeaveConfirmed = false;

// 未保存のまま発行・送付・捺印申請しない（書類はサーバーの保存済み内容から作るので、画面と違う書類が出る）。
// 未保存のまま書類画面を離れる前に確認する。どちらも既存の処理より先に止める（capture）
document.addEventListener(
  "click",
  (e) => {
    const r = currentRoute();
    if (r.page !== "project-docs" || !docPageIsDirty(r.id)) return;
    const target = e.target instanceof Element ? e.target : null;
    // 捺印申請も止める（承認者が見るのは保存済みの内容で、画面と違う書類を承認してしまう）
    if (target?.closest("[data-doc-footer-official], [data-doc-footer-mail], [data-doc-footer-postal], [data-doc-seal-apply]")) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showToastError("保存していない変更があります。先に「保存」を押してください");
      return;
    }
    if (!target?.closest('a[href^="#"], [data-go], [data-go-detail], [data-open-project-docs]')) return;
    if (confirm("保存していない変更があります。保存せずに移動しますか？\n（変更は保存済みの内容に戻ります）")) {
      discardDocPageEdits(r.id);
      docPageLeaveConfirmed = true;
      setTimeout(() => {
        docPageLeaveConfirmed = false;
      }, 1000);
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
  },
  true
);

// ブラウザの「戻る」など、クリックを通らない移動で書類画面を離れたとき。移動後なので、キャンセルは書類画面へ戻す。
// 同じ案件の書類タブの切り替えは確認しない（未保存のまま別の書類を見て、まとめて保存できる）
let docPageLastHash = currentRoute().page === "project-docs" ? location.hash : "";
let docPageLastPid = currentRoute().page === "project-docs" ? String(currentRoute().id || "") : "";
window.addEventListener("hashchange", () => {
  const r = currentRoute();
  const prev = docPageLastHash;
  const pid = docPageLastPid;
  docPageLastHash = r.page === "project-docs" ? location.hash : "";
  docPageLastPid = r.page === "project-docs" ? String(r.id || "") : "";
  if (!prev || (r.page === "project-docs" && String(r.id) === pid) || !docPageIsDirty(pid)) return;
  if (docPageLeaveConfirmed) {
    docPageLeaveConfirmed = false;
    return;
  }
  if (confirm("書類画面に保存していない変更があります。破棄して移動しますか？\n（キャンセルすると書類画面に戻ります）")) {
    discardDocPageEdits(pid);
  } else {
    location.hash = prev;
  }
});

window.addEventListener("beforeunload", (e) => {
  const r = currentRoute();
  if (r.page !== "project-docs" || !docPageIsDirty(r.id)) return;
  e.preventDefault();
  e.returnValue = "";
});
// ────────────────────────────────────────────────────────────────
// 書類テンプレート（設定 > 書類テンプレート）：見積・請求・請書/発注の作成時の初期値（件名・備考・振込先・税率・期限日数）。
// 作成・編集・複製・削除・デフォルトは /api/doc-templates に保存する。書類作成の「テンプレートから作る」
// （06-todo-action.js getTemplatesByType → openComposer の preset）で使う。
// 以前は画面の配列だけを書き換える仮実装で、リロードで消えていた（2026-10 全体レビュー #714）。
// ────────────────────────────────────────────────────────────────

let docTemplatesFetchSeq = 0;

/** 見えるテンプレート（チーム共通＋自分専用）を取り直す。保存後の取り直しは進行中の取得を待たずに撃ち、
 *  後から始めた取得の結果だけを使う（先の取得で上書きして保存結果が消えないように）。
 *  失敗したら読み込みを繰り返さず、「無い」ではなく「読み込めなかった」と出す */
async function fetchDocTemplates() {
  const seq = ++docTemplatesFetchSeq;
  uiState.docTemplatesLoading = true;
  const { ok, data } = await apiFetch("/api/doc-templates");
  if (seq !== docTemplatesFetchSeq) return;
  uiState.docTemplatesLoading = false;
  if (ok && data.ok && Array.isArray(data.templates)) {
    uiState.docTemplates = data.templates;
    uiState.docTemplatesError = "";
  } else {
    uiState.docTemplates = uiState.docTemplates || [];
    uiState.docTemplatesError = data.error || "書類テンプレートを読み込めませんでした";
    showToastError(uiState.docTemplatesError);
  }
  render();
}

/** 新規作成の下書き */
function newDocTemplateDraft(docType) {
  return {
    id: "",
    docType,
    name: "",
    description: "",
    scope: "private",
    isDefault: false,
    preset: { subject: "", note: "", bankMemo: "", taxRate: 0.1, dueDays: docType === "quote" ? 14 : 30 }
  };
}

/** 保存ボディ（サーバーが値を検証・丸める。api/doc-templates.js normalizeTemplateInput） */
function docTemplateBody(t) {
  return {
    docType: t.docType,
    name: t.name,
    description: t.description,
    scope: t.scope,
    isDefault: t.scope === "shared" && t.isDefault === true,
    preset: { ...t.preset }
  };
}

/** 書き込み1回分（二重送信を止め、成功したら一覧を取り直す） */
async function docTemplateWrite(path, method, body, successMsg) {
  if (uiState.docTemplateSaving) return false;
  uiState.docTemplateSaving = true;
  const { ok, data } = await apiFetch(path, { method, ...(body ? { body } : {}) });
  uiState.docTemplateSaving = false;
  if (!ok || !data.ok) {
    showToastError(data.error || "書類テンプレートを保存できませんでした");
    return false;
  }
  showToast(successMsg);
  await fetchDocTemplates();
  return true;
}

/** 更新日（ISO）→ 2026/10/26 */
function docTemplateUpdatedLabel(iso) {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}` : "—";
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const openEl = target.closest("[data-open-template-editor]");
  if (openEl instanceof HTMLElement) {
    uiState.templateEditor = newDocTemplateDraft(openEl.dataset.openTemplateEditor);
    render();
    return;
  }

  if (target.closest("[data-close-template-editor]")) {
    uiState.templateEditor = null;
    render();
    return;
  }

  const actionEl = target.closest("[data-template-action]");
  if (actionEl instanceof HTMLElement) {
    const [action, id] = String(actionEl.dataset.templateAction || "").split(":");
    const src = (uiState.docTemplates || []).find((t) => t.id === id);
    if (!src) return;
    if (action === "edit") {
      uiState.templateEditor = JSON.parse(JSON.stringify(src));
      render();
      return;
    }
    if (action === "duplicate") {
      // 複製は自分専用・通常で作る（共通のデフォルトを勝手に増やさない）
      void docTemplateWrite(
        "/api/doc-templates",
        "POST",
        docTemplateBody({ ...src, name: `${src.name}（複製）`, scope: "private", isDefault: false }),
        "テンプレートを複製しました"
      );
      return;
    }
    if (action === "delete") {
      if (!window.confirm(`テンプレート「${src.name}」を削除しますか？`)) return;
      void docTemplateWrite(`/api/doc-templates?id=${encodeURIComponent(id)}`, "DELETE", null, "テンプレートを削除しました");
      return;
    }
    if (action === "default") {
      void docTemplateWrite("/api/doc-templates", "PUT", { id, action: "default" }, "デフォルトを変更しました");
      return;
    }
    return;
  }

  if (target.closest("[data-template-save]") && uiState.templateEditor) {
    const t = uiState.templateEditor;
    if (!String(t.name || "").trim()) {
      showToastError("テンプレート名を入力してください");
      return;
    }
    void (async () => {
      const saved = await docTemplateWrite(
        "/api/doc-templates",
        t.id ? "PUT" : "POST",
        t.id ? { id: t.id, ...docTemplateBody(t) } : docTemplateBody(t),
        "テンプレートを保存しました"
      );
      if (saved) {
        uiState.templateEditor = null;
        render();
      }
    })();
  }
});
// ────────────────────────────────────────────────────────────────
// 備品の貸与申請（社内申請ページ＝#allowed-tools。申請フォームの「申請の種類」で ツール／備品 を切り替える）。流れ:
//   本人が申請（品目・希望内容・理由）→ 承認フロー（binding 'equipment:loan'）で管理部が承認（申請承認ページ）
//   → 管理部が在庫から、または購入して手配 → 左メニュー「貸与品」の「手配待ち」から貸与品一覧へ登録＝貸与済み
// 購入か在庫からかは申請では分けない（在庫が無ければ管理部が購入して貸す）。返却は申請にしない。
// API: /api/equipment-applications（申請）、/api/user-equipment?mine=1（自分の貸与品）。
// ────────────────────────────────────────────────────────────────

async function fetchEquipmentApplications() {
  if (!authToken) return;
  const { ok, data } = await apiFetch("/api/equipment-applications");
  uiState.equipmentApplications = ok && Array.isArray(data.applications) ? data.applications : [];
  uiState.equipmentApplicationsLoaded = true;
  uiState.equipmentApplicationsLoadError = !ok;
  render();
}

async function fetchMyEquipment() {
  if (!authToken) return;
  uiState.myEquipment = { loading: true, item: null };
  const { ok, data } = await apiFetch("/api/user-equipment?mine=1");
  uiState.myEquipment = { loading: false, item: ok ? data.item : null, error: !ok };
  render();
}

/** 申請の表示状況（承認後は「手配中」→ 貸与品一覧に登録されたら「貸与済み」） */
function equipAppStatusLabel(a) {
  if (a.status === "承認") return a.fulfilledAt ? "貸与済み" : "手配中";
  return a.status;
}

function equipAppStatusBadge(a) {
  const label = equipAppStatusLabel(a);
  const key = { 申請中: "pending", 手配中: "pending", 貸与済み: "approved", 却下: "rejected" }[label] || "pending";
  return `<span class="at-status-badge at-status--${key}">${escapeHtml(label)}</span>`;
}

/** 社内申請フォームの「申請の種類」プルダウン。ツール（15b の申請モーダル）／備品（下の申請モーダル）の新規申請で共通に出す */
// 「＋ 申請する」の既定は備品の貸与（openEquipApplyModal）。並びも既定を先頭にする
const INTERNAL_APP_TYPES = [
  { key: "equipment", label: "備品の貸与（PC・携帯電話など）" },
  { key: "tool", label: "ツールの利用許可" }
];
function internalAppTypeSelectHtml(current) {
  const opts = INTERNAL_APP_TYPES.map((t) => `<option value="${t.key}"${t.key === current ? " selected" : ""}>${escapeHtml(t.label)}</option>`).join("");
  return `
    <label class="cl-form-group">
      <span class="cl-form-label">申請の種類</span>
      <select class="cl-form-input" data-internal-app-type="1" data-search-dropdown-off="1">${opts}</select>
    </label>`;
}

function myEquipmentHtml() {
  const st = uiState.myEquipment;
  if (!st) void fetchMyEquipment();
  if (!st || st.loading) return `<p class="pj-muted">読み込み中…</p>`;
  if (st.error) return `<div class="cl-empty" style="padding:14px 16px;">貸与品を取得できませんでした。ページを再読み込みしてください。</div>`;
  const it = st.item;
  const rows = [];
  (it?.pcs || []).forEach((p) => rows.push(["PC", `${p.no || "（番号なし）"}${p.os ? `（${p.os}）` : ""}`]));
  if (it?.phone) rows.push(["携帯電話", it.phone]);
  if (it?.enCard) rows.push(["ENセキュリティーカード", it.enCard]);
  if (it?.otherItems) rows.push(["その他", it.otherItems]);
  if (!rows.length) return `<div class="cl-empty" style="padding:14px 16px;">貸与されている備品はありません。</div>`;
  return `<dl class="equip-mine">${rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join("")}</dl>`;
}

function equipApplicationCardHtml(a) {
  const progress = toolAppProgressText(a);
  const date = allowedToolDate(a.createdAt);
  return `
    <div class="at-app-card">
      <div class="at-app-card-head">
        <div class="at-app-title"><span class="at-app-type">備品</span>${escapeHtml(a.itemType)}</div>
        ${equipAppStatusBadge(a)}
      </div>
      <div class="at-app-meta">
        ${a.detail ? `<span class="at-app-meta-item">希望：${escapeHtml(a.detail)}</span>` : ""}
        ${progress ? `<span class="at-app-meta-item">${escapeHtml(progress)}</span>` : ""}
        ${date ? `<span class="at-app-meta-item">${escapeHtml(date)}</span>` : ""}
      </div>
      ${a.status === "却下" && a.reviewComment ? `<div class="at-app-reject">${escapeHtml(a.reviewComment)}</div>` : ""}
      ${a.status === "申請中" ? `<div class="at-app-actions"><button type="button" class="cl-btn cl-btn-outline" data-equip-app-withdraw="${escapeHtmlAttr(String(a.id))}">取り下げ</button></div>` : ""}
    </div>`;
}

function equipApplyModalHtml() {
  const d = uiState.equipApplyDraft;
  if (!d) return "";
  const opts = EQUIPMENT_ITEM_TYPE_OPTIONS.map((t) => `<option value="${escapeHtmlAttr(t)}"${d.itemType === t ? " selected" : ""}>${escapeHtml(t)}</option>`).join("");
  return `
    <div class="cl-mini-modal-overlay open" data-equip-apply-close="1">
      <div class="cl-mini-modal at-modal">
        <div class="cl-mini-modal-head">
          <h4>新しく申請する</h4>
          <button class="cl-modal-close" type="button" data-equip-apply-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          ${internalAppTypeSelectHtml("equipment")}
          <p class="at-apply-note">承認されると管理部が手配します（在庫が無い場合は購入して貸与）。</p>
          <label class="cl-form-group">
            <span class="cl-form-label">品目 <span class="cl-form-required">*</span></span>
            <select class="cl-form-input" data-equip-apply-field="itemType" data-search-dropdown-off="1">${opts}</select>
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">希望内容（任意）</span>
            <input class="cl-form-input" data-equip-apply-field="detail" value="${escapeHtmlAttr(d.detail)}" maxlength="500" placeholder="例：Mac希望 / iPhone / 交換（故障のため）" />
          </label>
          <label class="cl-form-group">
            <span class="cl-form-label">申請理由 <span class="cl-form-required">*</span></span>
            <textarea class="cl-form-input" data-equip-apply-field="reason" rows="3" maxlength="2000" placeholder="例：入社に伴い業務用PCが必要">${escapeHtml(d.reason)}</textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-equip-apply-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-equip-apply-submit="1"${d.saving ? " disabled" : ""}>${d.saving ? "申請中…" : "申請する"}</button>
        </div>
      </div>
    </div>`;
}

/** 社内申請の「＋ 申請する」から開く（既定の申請の種類＝備品の貸与） */
function openEquipApplyModal() {
  uiState.equipApplyDraft = { itemType: EQUIPMENT_ITEM_TYPE_OPTIONS[0], detail: "", reason: "", saving: false };
  render();
}

async function submitEquipApplication() {
  const d = uiState.equipApplyDraft;
  if (!d || d.saving) return;
  if (!d.reason.trim()) {
    showToastError("申請理由を入力してください");
    return;
  }
  d.saving = true;
  render();
  const { ok, data } = await apiFetch("/api/equipment-applications", {
    method: "POST",
    body: { itemType: d.itemType, detail: d.detail, reason: d.reason }
  });
  if (!ok || !data.ok) {
    d.saving = false;
    showToastError(`申請できませんでした：${data.error || "通信エラー"}`);
    return;
  }
  uiState.equipApplyDraft = null;
  uiState.equipmentApplications = [data.application, ...(uiState.equipmentApplications || [])];
  showToast("申請しました");
}

async function withdrawEquipApplication(id) {
  const a = (uiState.equipmentApplications || []).find((x) => String(x.id) === String(id));
  if (!a || !confirm(`「${a.itemType}」の申請を取り下げます。よろしいですか？`)) return;
  const { ok, data } = await apiFetch("/api/equipment-applications", { method: "DELETE", body: { id } });
  if (!ok) {
    showToastError(data.error || "取り下げに失敗しました");
    return;
  }
  uiState.equipmentApplications = uiState.equipmentApplications.filter((x) => String(x.id) !== String(id));
  showToast("取り下げました");
}

// ── 申請承認ページ：承認／却下 ──
async function approveEquipApplication(id) {
  if (uiState.equipApprovalSaving || !confirm("この申請を承認しますか？")) return;
  uiState.equipApprovalSaving = true;
  const { ok, data } = await apiFetch("/api/equipment-applications", { method: "PUT", body: { id, action: "approve" } });
  uiState.equipApprovalSaving = false;
  if (!ok) {
    showToastError(data.error || "承認に失敗しました");
    return;
  }
  showToast(data.application?.status === "承認" ? "承認しました（管理部の手配待ちへ）" : "承認しました");
  await fetchEquipmentApplications();
}

function equipRejectModalHtml() {
  const draft = uiState.equipRejectDraft;
  const a = draft && (uiState.equipmentApplications || []).find((x) => String(x.id) === String(draft.id));
  if (!a) return "";
  return `
    <div class="cl-mini-modal-overlay open" data-equip-reject-close="1">
      <div class="cl-mini-modal at-modal">
        <div class="cl-mini-modal-head">
          <h4>申請を却下</h4>
          <button class="cl-modal-close" type="button" data-equip-reject-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <div class="at-review-target">
            <div class="at-app-title">${escapeHtml(a.itemType)}${a.detail ? `（${escapeHtml(a.detail)}）` : ""}</div>
            <div class="at-app-meta"><span class="at-app-meta-item">申請者：${escapeHtml(a.applicantName || "—")}</span></div>
          </div>
          <label class="cl-form-group">
            <span class="cl-form-label">却下理由 <span class="cl-form-required">*</span></span>
            <textarea id="equip-reject-comment" class="cl-form-input" rows="3" placeholder="却下の理由"></textarea>
          </label>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-equip-reject-close="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-equip-reject-submit="1"${uiState.equipApprovalSaving ? " disabled" : ""}>却下する</button>
        </div>
      </div>
    </div>`;
}

async function rejectEquipApplication() {
  const draft = uiState.equipRejectDraft;
  if (!draft || uiState.equipApprovalSaving) return;
  const comment = (document.getElementById("equip-reject-comment")?.value || "").trim();
  if (!comment) {
    showToastError("却下理由を入力してください");
    return;
  }
  uiState.equipApprovalSaving = true;
  const { ok, data } = await apiFetch("/api/equipment-applications", { method: "PUT", body: { id: draft.id, action: "reject", reviewComment: comment } });
  uiState.equipApprovalSaving = false;
  if (!ok) {
    showToastError(data.error || "却下に失敗しました");
    return;
  }
  uiState.equipRejectDraft = null;
  showToast("却下しました");
  await fetchEquipmentApplications();
}

// ── 左メニュー「貸与品」：承認済みで未貸与（手配待ち）の申請。登録すると貸与済みになる ──
function equipFulfillQueueHtml() {
  if (!uiState.equipmentApplicationsLoaded) fetchOnceInFlight("equipmentApplications", fetchEquipmentApplications);
  const queue = (uiState.equipmentApplications || []).filter((a) => a.status === "承認" && !a.fulfilledAt);
  if (!queue.length) return "";
  const rows = queue.map((a) => `
    <tr>
      <td><div class="acct-name">${escapeHtml(a.applicantName || "—")}</div></td>
      <td data-label="品目">${escapeHtml(a.itemType)}</td>
      <td data-label="希望内容">${a.detail ? escapeHtml(a.detail) : `<span class="equip-none">—</span>`}</td>
      <td data-label="承認日">${escapeHtml(allowedToolDate(a.reviewedAt))}</td>
      <td><div class="acct-actions">
        <button type="button" class="cl-btn cl-btn-outline cl-btn-sm" data-equip-fulfill="${escapeHtmlAttr(String(a.id))}">貸与品に登録</button>
        <button type="button" class="cl-btn cl-btn-outline cl-btn-sm cl-btn-danger" data-equip-cancel="${escapeHtmlAttr(String(a.id))}">取消</button>
      </div></td>
    </tr>`).join("");
  return `
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <div class="section-head-row">
          <h3 class="mini-title">手配待ち（承認済み）<span class="acct-list-count">（${queue.length}件）</span></h3>
        </div>
        <p class="equip-queue-lead">在庫から、または購入して用意できたら「貸与品に登録」で管理番号などを入力してください。保存すると申請は「貸与済み」になります。手配が不要になった場合は「取消」。</p>
        <table class="acct-table fp-sp-card-table" data-fp-table="equipment-fulfill">
          <thead><tr><th>申請者</th><th>品目</th><th>希望内容</th><th>承認日</th><th></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </article>
    </section>`;
}

/** 手配待ちの申請から貸与品の編集モーダルを開く（PC の申請なら空の PC 行を足しておく）。保存後に fulfill する */
function openEquipFulfill(appId) {
  const a = (uiState.equipmentApplications || []).find((x) => String(x.id) === String(appId));
  const userId = Number(a?.applicantUserId);
  if (!a || !users.some((u) => u.dbId === userId)) {
    showToastError("申請者がアカウント一覧に見つかりません");
    return;
  }
  // 貸与品が読めていない時に空の下書きで開くと、保存で既存の貸与品を消してしまう
  if (!uiState.userEquipment?.byUserId) {
    void loadUserEquipment();
    showToastError("貸与品を読み込み中です。少し待ってからもう一度押してください");
    return;
  }
  const it = uiState.userEquipment.byUserId[userId] || equipEmpty();
  const pcs = it.pcs.map((p) => ({ ...p }));
  if (a.itemType === "PC" || !pcs.length) pcs.push({ no: "", os: "" });
  uiState.equipEditor = {
    userId,
    fulfillAppId: a.id,
    fulfillLabel: `${a.itemType}${a.detail ? `（${a.detail}）` : ""}`,
    draft: { ...equipEmpty(), ...it, pcs }
  };
  render();
}

/** 手配不要・申請者が退職済みなどで、承認済みの申請を手配せずに閉じる（申請者には「取り消し」と通知） */
async function cancelEquipApplication(appId) {
  const a = (uiState.equipmentApplications || []).find((x) => String(x.id) === String(appId));
  if (!a || !confirm(`${a.applicantName || "申請者"} さんの「${a.itemType}」の手配を取り消しますか？
申請者には取り消しが通知されます。`)) return;
  const { ok, data } = await apiFetch("/api/equipment-applications", { method: "PUT", body: { id: a.id, action: "cancel" } });
  if (!ok) {
    showToastError(data.error || "取り消しに失敗しました");
    return;
  }
  showToast("手配を取り消しました");
  await fetchEquipmentApplications();
}

/** 貸与品の保存後に、元の申請を貸与済みにする（15z-user-equipment.js の保存処理から呼ぶ） */
async function fulfillEquipApplication(appId) {
  const { ok, data } = await apiFetch("/api/equipment-applications", { method: "PUT", body: { id: appId, action: "fulfill" } });
  if (!ok) {
    showToastError(`貸与品は保存しましたが、申請を貸与済みにできませんでした：${data.error || "通信エラー"}`);
    return;
  }
  await fetchEquipmentApplications();
}

// ── イベント ──
document.addEventListener("input", (e) => {
  const el = e.target;
  const d = uiState.equipApplyDraft;
  if (d && el instanceof HTMLElement && el.dataset.equipApplyField) d[el.dataset.equipApplyField] = el.value;
});
document.addEventListener("change", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLSelectElement)) return;
  // 申請の種類を切り替えたら、もう一方の申請モーダルに入れ替える（入力途中の内容は種類ごとに別物なので引き継がない）
  if (el.dataset.internalAppType) {
    if (el.value === "equipment") {
      uiState.allowedToolApplyDraft = null;
      openEquipApplyModal();
    } else {
      uiState.equipApplyDraft = null;
      openToolApplyModal("register");
    }
    return;
  }
  const d = uiState.equipApplyDraft;
  if (d && el.dataset.equipApplyField) d[el.dataset.equipApplyField] = el.value;
});

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  const page = currentRoute().page;
  const closeOnOverlay = (el) => el.classList.contains("cl-mini-modal-overlay") && target.closest(".cl-mini-modal");

  if (page === "allowed-tools") {
    if (target.closest("[data-equip-apply-submit]")) {
      void submitEquipApplication();
      return;
    }
    const applyClose = target.closest("[data-equip-apply-close]");
    if (applyClose instanceof HTMLElement && !closeOnOverlay(applyClose)) {
      uiState.equipApplyDraft = null;
      render();
      return;
    }
    const withdrawEl = target.closest("[data-equip-app-withdraw]");
    if (withdrawEl instanceof HTMLElement) void withdrawEquipApplication(withdrawEl.dataset.equipAppWithdraw);
    return;
  }

  if (page === "application-management") {
    const approveEl = target.closest("[data-app-mgmt-equip-approve]");
    if (approveEl instanceof HTMLElement) {
      void approveEquipApplication(approveEl.dataset.appMgmtEquipApprove);
      return;
    }
    const rejectEl = target.closest("[data-app-mgmt-equip-reject]");
    if (rejectEl instanceof HTMLElement) {
      uiState.equipRejectDraft = { id: rejectEl.dataset.appMgmtEquipReject };
      render();
      return;
    }
    if (target.closest("[data-equip-reject-submit]")) {
      void rejectEquipApplication();
      return;
    }
    const rejectClose = target.closest("[data-equip-reject-close]");
    if (rejectClose instanceof HTMLElement && !closeOnOverlay(rejectClose)) {
      uiState.equipRejectDraft = null;
      render();
    }
    return;
  }

  if (!isUserEquipmentRoute()) return;
  const fulfillEl = target.closest("[data-equip-fulfill]");
  if (fulfillEl instanceof HTMLElement) {
    openEquipFulfill(fulfillEl.dataset.equipFulfill);
    return;
  }
  const cancelEl = target.closest("[data-equip-cancel]");
  if (cancelEl instanceof HTMLElement) void cancelEquipApplication(cancelEl.dataset.equipCancel);
});

// 画面を移ったら申請モーダル・却下モーダルは閉じる（戻ったときに開きっぱなしで再表示されないように）
window.addEventListener("hashchange", () => {
  uiState.equipApplyDraft = null;
  uiState.equipRejectDraft = null;
  uiState.myEquipment = null; // 次に開いたとき最新を取り直す
});
// ── 一覧の検索欄: 出す項目と並び順を人ごとに保存する「固定項目編集」の共通部品（案件一覧・取引先一覧） ──
// 「固定項目編集」で編集モードに入り、検索項目ごとのチェックで出す／出さないを選び、つまみ（⠿）をドラッグして
// 並べ替えて「保存」する（ログインユーザーごと）。保存先は一覧の列幅と同じ個人設定（fp_users.ui_prefs ／
// 15z-table-prefs.js）で、一覧ごとの ID（spec.id）に { hidden, order } を持つ。
// 隠した項目に値が残っていても絞り込みには使わない（見えない条件で行が隠れないように。listFilterFieldsEffective）。
// 編集中はチェック・並べ替えをその場で検索欄に映す（保存しないで「終了」すると元に戻る）。
// イベントは独立リスナー（16 の巨大 click ハンドラには足さない）。
//
// spec = {
//   id: 保存先の一覧ID, pages: 編集を続けてよいページ（離れたら編集中の選択を捨てる）,
//   fields: [{ key, label, clear: 隠したときに絞り込みから外す値（その項目の「すべて」の値）,
//              defaultHidden?: 未保存のとき最初は隠す, available?: () => この人が使える項目か }],
//   onChange: 絞り込みが変わったとき（ページを1へ戻す等）
// }

/** いま編集中の一覧（{ id, hidden, order } / null）。一度に1つの一覧しか編集しない */
function listFilterFieldsEdit(spec) {
  const edit = uiState.listFilterFieldsEdit;
  return edit && edit.id === spec.id ? edit : null;
}

/** この人が使える項目（available が false の項目は検索欄にも編集モードにも出さない） */
function listFilterFieldsAvailable(spec) {
  return spec.fields.filter((d) => !d.available || d.available());
}

/** 保存済みの「隠す項目」。未保存は defaultHidden の項目、設定の読込前・未ログインも同じ */
function listFilterFieldsSavedHidden(spec) {
  const hidden = fpTablePrefsReady() ? fpTablePrefs.tables[spec.id]?.hidden : null;
  if (!Array.isArray(hidden)) return spec.fields.filter((d) => d.defaultHidden).map((d) => d.key);
  return hidden.filter((k) => spec.fields.some((d) => d.key === k));
}

/** いま隠している項目（編集中は編集中の選択）。使えない項目も隠している扱い */
function listFilterFieldsHidden(spec) {
  const edit = listFilterFieldsEdit(spec);
  const unavailable = spec.fields.filter((d) => d.available && !d.available()).map((d) => d.key);
  return [...(edit ? edit.hidden : listFilterFieldsSavedHidden(spec)), ...unavailable];
}

function listFilterFieldShown(spec, key) {
  return !listFilterFieldsHidden(spec).includes(key);
}

/** 並び順を正規化する。知らないキー・重複は捨て、足りないキー（後から増えた項目など）は既定の順で末尾に足す */
function listFilterFieldsNormalizeOrder(spec, order) {
  const keys = spec.fields.map((d) => d.key);
  const out = (Array.isArray(order) ? order : []).filter((k, i, a) => keys.includes(k) && a.indexOf(k) === i);
  return [...out, ...keys.filter((k) => !out.includes(k))];
}

/** 保存済みの並び順。設定の読込前・未保存は既定の順 */
function listFilterFieldsSavedOrder(spec) {
  return listFilterFieldsNormalizeOrder(spec, fpTablePrefsReady() ? fpTablePrefs.tables[spec.id]?.order : null);
}

/** 検索欄に出す項目のキー（いまの並び順で、隠した項目は除く）。編集中は編集中の並び・選択 */
function listFilterFieldsShownKeys(spec) {
  const edit = listFilterFieldsEdit(spec);
  const hidden = new Set(listFilterFieldsHidden(spec));
  return (edit ? edit.order : listFilterFieldsSavedOrder(spec)).filter((k) => !hidden.has(k));
}

/** 隠した項目の条件を「すべて」にした絞り込み条件（隠していなければ元のまま）。一覧の絞り込み・条件数で使う */
function listFilterFieldsEffective(spec, f) {
  const hidden = listFilterFieldsHidden(spec);
  if (!hidden.length) return f;
  const out = { ...f };
  for (const d of spec.fields) {
    if (hidden.includes(d.key)) Object.assign(out, d.clear);
  }
  return out;
}

/** 「固定項目編集」ボタン（編集中は押下状態） */
function listFilterFieldsEditButtonHtml(spec) {
  return `<button class="inline-btn ghost${listFilterFieldsEdit(spec) ? " is-active" : ""}" type="button" data-list-filter-fields-edit="${escapeHtmlAttr(spec.id)}" title="検索欄に出す検索条件と並び順を選んで保存します（あなたのアカウントだけ）">固定項目編集</button>`;
}

/** 固定項目編集モードの帯（board と同じく、検索欄の下に案内と保存／終了を出す）。項目は検索欄と同じ並び */
function listFilterFieldsEditorHtml(spec) {
  const edit = listFilterFieldsEdit(spec);
  if (!edit) return "";
  const available = listFilterFieldsAvailable(spec);
  const boxes = edit.order
    .map((key) => available.find((d) => d.key === key))
    .filter(Boolean)
    .map(
      (d) => `<label class="pj-filter-fields-opt" data-list-filter-field-opt="${d.key}"><span class="pj-filter-fields-handle" data-list-filter-field-handle="1" title="ドラッグして並べ替え" aria-hidden="true">⠿</span><input type="checkbox" data-list-filter-field-toggle="${d.key}"${edit.hidden.includes(d.key) ? "" : " checked"} /> ${d.label}</label>`
    )
    .join("");
  return `
    <div class="pj-filter-fields-edit" role="region" aria-label="検索条件の固定表示の編集">
      <p class="pj-filter-fields-edit-title">検索条件の固定表示編集モード中です</p>
      <p class="pj-filter-fields-edit-desc">表示する検索条件にチェックを入れ、左のつまみ（⠿）をドラッグして並べ替えてから保存してください。チェックを外した条件は検索欄に出なくなり、絞り込みにも使いません。設定はあなたのアカウントだけに保存されます。</p>
      <div class="pj-filter-fields-opts">${boxes}</div>
      <div class="pj-filter-fields-edit-actions">
        <button type="button" class="inline-btn" data-list-filter-fields-save="1">保存</button>
        <button type="button" class="inline-btn ghost" data-list-filter-fields-end="1">終了</button>
      </div>
    </div>`;
}

/** 固定項目編集を使う一覧。spec は各一覧のファイルにある（app.js の連結順に左右されないよう、読込時でなく使う時に集める） */
function listFilterFieldsSpecById(id) {
  return [PROJECT_FILTER_SPEC, CLIENT_LIST_FILTER_SPEC, PARTNER_LIST_FILTER_SPEC].find((s) => s.id === id) || null;
}

function listFilterFieldsEditingSpec() {
  const edit = uiState.listFilterFieldsEdit;
  return edit ? listFilterFieldsSpecById(edit.id) : null;
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const editBtn = t.closest("[data-list-filter-fields-edit]");
  if (editBtn) {
    const spec = listFilterFieldsSpecById(editBtn.dataset.listFilterFieldsEdit);
    if (!spec) return;
    if (!fpTablePrefsReady()) {
      showToastError("設定を読み込んでいます。少し待ってからもう一度押してください");
      return;
    }
    uiState.listFilterFieldsEdit = listFilterFieldsEdit(spec)
      ? null
      : { id: spec.id, hidden: [...listFilterFieldsSavedHidden(spec)], order: listFilterFieldsSavedOrder(spec) };
    render();
    return;
  }
  const spec = listFilterFieldsEditingSpec();
  if (!spec) return;
  const edit = uiState.listFilterFieldsEdit;
  if (t.closest("[data-list-filter-fields-end]")) {
    uiState.listFilterFieldsEdit = null;
    render();
    return;
  }
  if (t.closest("[data-list-filter-fields-save]")) {
    if (!fpTablePrefsReady()) return;
    Object.assign(fpTablePrefsOf(spec.id), { hidden: [...edit.hidden], order: [...edit.order] });
    fpTablePrefsSave(spec.id);
    uiState.listFilterFieldsEdit = null;
    spec.onChange();
    showToast("検索条件の表示と並び順を保存しました");
    render();
  }
});

// 一覧から離れたら編集中の選択を捨てる（ログアウトでは doLogout が捨てる）。保存していない選択を次に開いたときに持ち越さない
window.addEventListener("hashchange", () => {
  const spec = listFilterFieldsEditingSpec();
  if (uiState.listFilterFieldsEdit && (!spec || !spec.pages.includes(currentRoute().page))) uiState.listFilterFieldsEdit = null;
});

document.addEventListener("change", (e) => {
  const el = e.target;
  const spec = listFilterFieldsEditingSpec();
  if (!spec || !(el instanceof HTMLInputElement) || !el.matches("[data-list-filter-field-toggle]")) return;
  const edit = uiState.listFilterFieldsEdit;
  const key = el.dataset.listFilterFieldToggle;
  const others = edit.hidden.filter((k) => k !== key);
  edit.hidden = el.checked ? others : [...others, key];
  spec.onChange();
  render();
});

// 並べ替え: つまみ（⠿）を押したまま動かすと、その項目を指の下の項目の前後へ移す（マウス・指とも。pointer イベント）。
// 動かしている間は DOM だけ入れ替え、離したときに並びを状態へ書き戻して描画し直す（検索欄にも映る）
document.addEventListener("pointerdown", (e) => {
  const handle = e.target instanceof Element ? e.target.closest("[data-list-filter-field-handle]") : null;
  const opt = handle?.closest("[data-list-filter-field-opt]");
  const box = opt?.parentElement;
  const spec = listFilterFieldsEditingSpec();
  const edit = uiState.listFilterFieldsEdit;
  if (!opt || !box || !spec) return;
  e.preventDefault();
  opt.classList.add("is-dragging");
  const move = (ev) => {
    const hit = document.elementFromPoint(ev.clientX, ev.clientY)?.closest("[data-list-filter-field-opt]");
    if (!hit || hit === opt || hit.parentElement !== box) return;
    const r = hit.getBoundingClientRect();
    box.insertBefore(opt, ev.clientX < r.left + r.width / 2 ? hit : hit.nextSibling);
  };
  const end = (ev) => {
    document.removeEventListener("pointermove", move);
    document.removeEventListener("pointerup", end);
    document.removeEventListener("pointercancel", end);
    if (ev.type === "pointerup" && uiState.listFilterFieldsEdit === edit) {
      // 編集モードに出していない項目（使えない項目）は元の位置のまま残す
      const shown = [...box.querySelectorAll("[data-list-filter-field-opt]")].map((el) => el.dataset.listFilterFieldOpt);
      let i = 0;
      edit.order = listFilterFieldsNormalizeOrder(spec, edit.order.map((k) => (shown.includes(k) ? shown[i++] : k)));
    }
    render();
  };
  document.addEventListener("pointermove", move);
  document.addEventListener("pointerup", end);
  document.addEventListener("pointercancel", end);
});

// つまみはチェックと同じ label の中にあるので、押して離す（クリック）だけでチェックが切り替わってしまう。つまみでは label を働かせない
document.addEventListener("click", (e) => {
  if (e.target instanceof Element && e.target.closest("[data-list-filter-field-handle]")) e.preventDefault();
});
// ── 案件一覧: チェックした案件の書類をまとめて出す（2026-10 要望） ──
// 一括操作バーの「書類を出力」→ 出す書類（見積書・請求書など）を選ぶ → 案件×書類ごとの正式PDFを ZIP にまとめてダウンロード。
// PDF は1件ずつ「正式PDFを発行」と同じサーバー（api/doc-pdf）で作る＝発行履歴に記録され、請求書は受注状況が受注済になる
// （2026-10 ユーザー決定: 確認用でなく正式な発行として扱う）。サーバーは1件ごとに PDF を作るので、まとめて1回では送らない。
// 捺印が未承認などでサーバーが断った書類は飛ばし、最後に理由つきで一覧にする。
// イベントは独立リスナー（16 の巨大 click ハンドラには足さない）。

/** 出せる書類（サーバー api/_docRender.js DOC_LABEL と同じ7種類。送付状はサーバーで作れないので対象外）。並びは書類タブと同じ */
const PROJECT_BULK_DOC_TYPES = [
  { key: "quote", label: "見積書" },
  { key: "order", label: "発注書" },
  { key: "acceptance", label: "発注請書" },
  { key: "delivery", label: "納品書" },
  { key: "inspection", label: "検収書" },
  { key: "invoice", label: "請求書" },
  { key: "receipt", label: "領収書" }
];

/** 1回に出せる件数（案件×書類）の上限。1件ずつサーバーで PDF を作るので、多いと時間がかかりすぎる */
const PROJECT_BULK_DOCS_MAX = 100;

function openProjectBulkDocs() {
  const ids = getSelectedProjectIdsInFilter();
  if (!ids.length) {
    showToast("案件を選択してください");
    return;
  }
  uiState.projectBulkDocs = { selectedIds: ids, types: ["invoice"], processing: false, result: null };
}

function projectBulkDocsJobCount(st) {
  return st.selectedIds.length * st.types.length;
}

function renderProjectBulkDocsModal() {
  const st = uiState.projectBulkDocs;
  if (!st) return "";
  const n = st.selectedIds.length;
  const jobs = projectBulkDocsJobCount(st);
  const dis = st.processing ? " disabled" : "";
  let bodyHtml;
  let footHtml;
  if (st.result) {
    const failedHtml = st.result.failed.length
      ? `<p class="pj-bulk-docs-failed-lead">出せなかった書類（${st.result.failed.length}件）</p>
         <ul class="pj-bulk-docs-failed">${st.result.failed
           .map((f) => `<li><strong>${escapeHtml(f.name)}</strong>：${escapeHtml(f.error)}</li>`)
           .join("")}</ul>`
      : "";
    bodyHtml = `<p class="pj-bulk-docs-lead">${st.result.ok ? `<strong>${st.result.ok}件</strong>の書類を ZIP にまとめてダウンロードしました。発行履歴に記録されています。` : "出せた書類はありませんでした。"}</p>${failedHtml}`;
    footHtml = `<button class="inline-btn" type="button" data-project-bulk-docs-close="1">閉じる</button>`;
  } else {
    const over = jobs > PROJECT_BULK_DOCS_MAX;
    bodyHtml = `
      <p class="pj-bulk-docs-lead">選択した<strong>${n}件</strong>の案件の書類を、正式PDFとして ZIP にまとめて出します。出す書類を選んでください。</p>
      <div class="pj-bulk-docs-types" role="group" aria-label="出す書類">
        ${PROJECT_BULK_DOC_TYPES.map(
          (t) => `<label class="pj-bulk-docs-type"><input type="checkbox" data-project-bulk-docs-type="${t.key}"${st.types.includes(t.key) ? " checked" : ""}${dis} /> ${t.label}</label>`
        ).join("")}
      </div>
      <ul class="pj-bulk-docs-notes">
        <li>1件ずつ「正式PDFを発行」したのと同じ扱いで、発行履歴に記録されます。請求書を出すと受注状況が「受注済」になります。</li>
        <li>捺印申請が承認されていない書類など、出せなかったものは最後に一覧で知らせます。</li>
        <li>保存していない請求項目の変更がある案件は飛ばします（案件を開いて保存してから出してください）。</li>
      </ul>
      <p class="pj-bulk-docs-count${over ? " is-over" : ""}">${jobs}件（${n}案件 × ${st.types.length}種類）${over ? `。一度に出せるのは${PROJECT_BULK_DOCS_MAX}件までです。案件か書類を減らしてください` : ""}</p>`;
    footHtml = `
      <button class="inline-btn ghost" type="button" data-project-bulk-docs-close="1"${dis}>キャンセル</button>
      <button class="inline-btn" type="button" data-project-bulk-docs-run="1"${dis || (jobs && !over ? "" : " disabled")}>${st.processing ? "作成中…" : `${jobs}件を出力する`}</button>`;
  }
  return `
    <div class="composer-backdrop"${st.processing ? "" : ' data-project-bulk-docs-close="1"'}></div>
    <aside class="composer-panel fp-centered-modal fp-centered-modal--sm pj-bulk-docs-modal" role="dialog" aria-modal="true" aria-labelledby="pj-bulk-docs-title" style="grid-template-rows:auto minmax(0, 1fr) auto;">
      <div class="composer-head"><div><h3 id="pj-bulk-docs-title">書類をまとめて出力</h3></div></div>
      <div class="composer-body" style="padding:20px;">${bodyHtml}</div>
      <div class="composer-foot" style="justify-content:flex-end;gap:8px;">${footHtml}</div>
    </aside>`;
}

/** ZIP の中のファイル名（案件番号_案件名_書類名.pdf）。同じ名前が続いたら _2, _3 を付ける */
function projectBulkDocsEntryName(p, type, usedNames) {
  const base = projectDocDownloadFilename(p, type);
  let name = base;
  for (let i = 2; usedNames.has(name); i++) name = base.replace(/\.pdf$/, `_${i}.pdf`);
  usedNames.add(name);
  return name;
}

async function runProjectBulkDocs() {
  const st = uiState.projectBulkDocs;
  if (!st || st.processing) return;
  const targets = st.selectedIds.map((id) => projects.find((p) => p.id === id)).filter(Boolean);
  const types = PROJECT_BULK_DOC_TYPES.filter((t) => st.types.includes(t.key));
  const jobs = targets.flatMap((p) => types.map((t) => ({ p, type: t.key, label: t.label })));
  if (!jobs.length || jobs.length > PROJECT_BULK_DOCS_MAX) return;
  st.processing = true;
  render();
  const failed = [];
  let ok = 0;
  try {
    await ensurePizZipLoaded();
    const PizZipCtor = window.PizZip;
    if (!PizZipCtor) throw new Error("ZIPライブラリの読み込みに失敗しました");
    const zip = new PizZipCtor();
    const usedNames = new Set();
    let done = 0;
    for (const j of jobs) {
      showToastPersistent(`書類を作成しています… ${++done} / ${jobs.length}件`);
      const name = `${String(j.p.name || "（案件名なし）").trim()}の${j.label}`;
      // 画面で直して保存していない明細があると、保存済みの内容で古い書類ができてしまうので出さない
      if (uiState.projectDocsDraftDirty?.[j.p.id]) {
        failed.push({ name, error: "保存していない請求項目の変更があります。案件を開いて保存してから出してください" });
        continue;
      }
      const r = await fetchOfficialDocumentPdf(j.p, j.type);
      if (!r.ok) {
        failed.push({ name, error: r.error });
        continue;
      }
      zip.file(projectBulkDocsEntryName(j.p, j.type, usedNames), await r.blob.arrayBuffer());
      ok += 1;
    }
    if (ok) {
      let blob;
      try {
        blob = zip.generate({ type: "blob", mimeType: "application/zip" });
      } catch {
        blob = new Blob([zip.generate({ type: "uint8array" })], { type: "application/zip" });
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `書類_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}_${ok}件.zip`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    }
  } catch (e) {
    failed.push({ name: "ZIPの作成", error: String(e?.message || e) });
  } finally {
    clearToast();
    st.processing = false;
  }
  st.result = { ok, failed };
  render();
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  if (t.closest("[data-project-bulk-docs]") && currentRoute().page === "projects") {
    openProjectBulkDocs();
    render();
    return;
  }
  const st = uiState.projectBulkDocs;
  if (!st || st.processing) return;
  if (t.closest("[data-project-bulk-docs-close]")) {
    uiState.projectBulkDocs = null;
    render();
    return;
  }
  if (t.closest("[data-project-bulk-docs-run]")) void runProjectBulkDocs();
});

document.addEventListener("change", (e) => {
  const el = e.target;
  const st = uiState.projectBulkDocs;
  if (!st || st.processing || !(el instanceof HTMLInputElement) || !el.matches("[data-project-bulk-docs-type]")) return;
  const key = el.dataset.projectBulkDocsType;
  st.types = PROJECT_BULK_DOC_TYPES.map((x) => x.key).filter((k) => (k === key ? el.checked : st.types.includes(k)));
  render();
});
// ── 案件一覧: 検索に使う項目の表示・非表示と並び順（固定項目編集。2026-10 要望・board の「固定項目編集」に合わせる） ──
// 仕組みは取引先一覧と共通（15z-list-filter-fields.js）。ここは案件一覧の検索項目の定義だけ。
// 並び順は検索欄全体で自由（請求月・入金日・タグも1行目へ混ぜられる）。保存先の一覧ID は "projects-filter"。

/** 検索項目と、隠したときに絞り込みから外す値（その項目の「すべて」の値）。並びは既定の表示順 */
const PROJECT_FILTER_SPEC = {
  id: "projects-filter",
  pages: ["projects"],
  fields: [
    { key: "no", label: "案件No", clear: { no: "" } },
    { key: "name", label: "案件名", clear: { name: "" } },
    { key: "client", label: "顧客", clear: { clientId: "all", clientNameQuery: "" } },
    { key: "lock", label: "ロック", clear: { lockState: "all" } },
    { key: "category", label: "カテゴリ", clear: { workCategory: "all" } },
    { key: "assignee", label: "担当者", clear: { assignedUserIds: [], assignedUserId: "all", assignedUserNameQuery: "" } },
    { key: "order", label: "受注", clear: { orderStatuses: [], orderStatus: "all" } },
    { key: "billingMonth", label: "請求月", clear: { billingMonthFrom: "", billingMonthTo: "" } },
    { key: "paymentDate", label: "入金日", clear: { paymentDateFrom: "", paymentDateTo: "" } },
    { key: "tag", label: "タグ", clear: { filterTags: [] } }
  ],
  onChange: () => setProjectListPage(1)
};

/** 隠した項目の条件を「すべて」にした絞り込み条件。一覧の絞り込み・条件数・0件時の案内で使う */
function projectListEffectiveFilters(f) {
  return listFilterFieldsEffective(PROJECT_FILTER_SPEC, f);
}
// ── 入力エラーを「どの欄か」分かる形で知らせる（取引先・パートナー登録） ──
// 以前は window.alert("必須項目が入力されていません") などで、どの欄が悪いか分からず、
// スマホでは OS 標準のダイアログが不意に出ていた（2026-10 コード全体レビュー・UI/UX）。
// 該当欄の行に赤枠（.fp-field-missing）と aria-invalid を付け、最初の欄へフォーカスを移し、
// エラートースト（閉じるまで残る・role="alert"）で理由を出す。入力し直すとその行の印は消える。

/**
 * @param {string} message トーストに出す理由
 * @param {string[]} selectors 印を付ける入力要素のセレクタ
 */
function reportFieldError(message, selectors) {
  clearFieldErrorMarks();
  let first = null;
  for (const selector of selectors) {
    document.querySelectorAll(selector).forEach((el) => {
      el.setAttribute("aria-invalid", "true");
      el.closest(".client-form-row")?.classList.add("fp-field-missing");
      if (!first) first = el;
    });
  }
  showToastError(message);
  if (first instanceof HTMLElement) {
    first.scrollIntoView({ block: "center" });
    first.focus({ preventScroll: true });
  }
}

/** @param {{ label: string, selector: string }[]} missing 未入力の欄（画面の項目名と入力要素のセレクタ） */
function reportMissingRequiredFields(missing) {
  reportFieldError(`必須項目が未入力です：${missing.map((m) => m.label).join("・")}`, missing.map((m) => m.selector));
}

function clearFieldErrorMarks() {
  document.querySelectorAll(".fp-field-missing").forEach((row) => row.classList.remove("fp-field-missing"));
  document.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute("aria-invalid"));
}

/** 印の付いた行に入力・選択があったら、その行の印だけ消す */
function clearFieldErrorMarkOnEdit(event) {
  const row = event.target instanceof Element ? event.target.closest(".fp-field-missing") : null;
  if (!row) return;
  row.classList.remove("fp-field-missing");
  row.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute("aria-invalid"));
}
document.addEventListener("input", clearFieldErrorMarkOnEdit);
document.addEventListener("change", clearFieldErrorMarkOnEdit);
// ── 即保存（欄を変えた瞬間に送る）の欄の横に出す保存状態 ──
// 「保存中… → ✓ 保存済み 14:32 ／ ✗ 保存できませんでした」。トーストは2.5秒で消えるため、
// 見逃しても欄の横を見れば保存されたか分かるようにする（docs/save-patterns-inventory.md）。
// 使い方: 描画側で fpSaveStatusHtml(key) を欄の横に置き、保存処理を fpSaveTrack(key, async () => ok) で包む。
// 状態は画面を切り替えるまで残り、再描画しても消えない。保存方式がボタン保存の欄には使わない
// （そちらは案件詳細の基本情報 15k-project-detail-save.js のように「未保存の変更があります」を出す）。

const fpSaveStatuses = {};

function fpSaveStatusLabel(st) {
  if (!st) return "";
  if (st.state === "saving") return "保存中…";
  if (st.state === "failed") return "保存できませんでした";
  return st.at ? `保存済み ${st.at}` : "保存済み";
}

function fpSaveStatusClockText() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** 欄の横に置く状態表示。key は欄ごとに一意な文字列（例: "notify:email"）。まだ保存していない欄は空 */
function fpSaveStatusHtml(key) {
  const st = fpSaveStatuses[key];
  const cls = st ? ` is-${st.state}` : "";
  return `<span class="fp-save-status${cls}" data-fp-save-status="${escapeHtmlAttr(key)}" role="status" aria-live="polite">${escapeHtml(fpSaveStatusLabel(st))}</span>`;
}

function fpSaveStatusSet(key, state) {
  fpSaveStatuses[key] = { state, at: state === "saved" ? fpSaveStatusClockText() : "" };
  // 再描画を待たず、いま画面にある表示も書き換える（フル再描画しない保存経路でも見える）
  document.querySelectorAll("[data-fp-save-status]").forEach((el) => {
    if (el.getAttribute("data-fp-save-status") !== key) return;
    el.textContent = fpSaveStatusLabel(fpSaveStatuses[key]);
    el.className = `fp-save-status is-${state}`;
  });
}

/** 保存処理 run（成功なら true を返す関数。例外は失敗扱い）を実行し、状態を保存中→保存済み／失敗にする。
 *  run の成否をそのまま返す（呼び出し側が続きの処理に使える）。トーストは呼び出し側が従来どおり出す */
async function fpSaveTrack(key, run) {
  fpSaveStatusSet(key, "saving");
  let ok = false;
  try {
    ok = (await run()) !== false;
  } catch {
    ok = false;
  }
  fpSaveStatusSet(key, ok ? "saved" : "failed");
  return ok;
}

// 別の画面へ移ったら、前の画面の「保存済み」は持ち越さない
window.addEventListener("hashchange", () => {
  for (const key of Object.keys(fpSaveStatuses)) delete fpSaveStatuses[key];
});
// ── 捺印申請の送信モーダル: 申請する書類の選択（一括）・コメント・承認期日（2026-10 要望） ──
// 確認モーダル本体は renderSealConfirmModal（11-cost-form.js）、OK の処理は 16-render-routing.js。
// ここは入力欄の HTML・入力の取り込み・一括申請1件分の処理だけを持つ。
// 期日を過ぎても承認されない申請は api/seal-due-remind.js が承認者へ毎朝リマインドする。

/** モーダルの入力から申請APIへ渡す追加項目を作る（空は送らない側で null 扱い） */
function sealApplyExtraFromDialog(d) {
  return {
    applicantComment: String(d?.comment || "").trim(),
    dueDate: /^\d{4}-\d{2}-\d{2}$/.test(String(d?.dueDate || "")) ? String(d.dueDate) : ""
  };
}

/** 申請承認一覧の「承認期日」セル。期日なし（捺印申請以外・未入力）は —。過ぎた期日は赤・当日は黄で目立たせる */
function sealDueCellHtml(dueIso) {
  const due = String(dueIso || "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return `<span class="pj-muted">—</span>`;
  const today = localDateToYmd(new Date());
  const state = due < today ? " is-overdue" : due === today ? " is-today" : "";
  const title = due < today ? "期日を過ぎています" : due === today ? "本日が期日です" : "";
  return `<span class="app-mgmt-due${state}"${title ? ` title="${title}"` : ""}>${escapeHtml(due.replace(/-/g, "/"))}</span>`;
}

/** コメント・承認期日の入力欄（単体申請・一括申請で共通） */
function sealApplyInputsHtml(d) {
  const today = localDateToYmd(new Date());
  return `
    <div class="seal-apply-inputs">
      <label class="seal-apply-field">
        <span class="seal-apply-label">承認期日（任意）</span>
        <input class="dform-input seal-apply-date" type="date" min="${escapeHtmlAttr(today)}" data-seal-apply-field="dueDate" value="${escapeHtmlAttr(d.dueDate || "")}" />
        <span class="seal-apply-hint">この日までに承認されないと、承認者へリマインドが届きます</span>
      </label>
      <label class="seal-apply-field">
        <span class="seal-apply-label">コメント（任意）</span>
        <textarea class="dform-input seal-apply-comment" rows="2" maxlength="2000" data-seal-apply-field="comment" placeholder="承認者への補足（急ぎの理由・確認してほしい点など）">${escapeHtml(d.comment || "")}</textarea>
      </label>
    </div>`;
}

/** 一括申請で、その案件のその書類が申請できるか（できないときは理由） */
function sealBulkTargetState(pid, tab) {
  const st = getDocSealStatus(pid, tab);
  if (st !== "未申請") return { ok: false, reason: st === "承認済" ? "承認済" : "申請中" };
  if (!getSealApprovalChainForMapKey(docSealStateKey(pid, tab)).length) return { ok: false, reason: "承認者なし" };
  return { ok: true, reason: "" };
}

/** 一括申請: 申請する書類の選択（案件ごとの申請可否の一覧は出さない＝選んだときに画面が動かないように。
 *  申請済み・承認者なしの案件は送信後のトーストで件数を知らせる） */
function sealBulkTargetsHtml(d) {
  const tab = String(d.tab || "");
  const radios = Object.entries(DOC_SEAL_TAB_LABELS)
    .map(
      ([key, label]) =>
        `<label class="seal-bulk-kind cl-inline-check"><input type="radio" name="seal-bulk-kind" value="${escapeHtmlAttr(key)}" data-seal-bulk-kind${tab === key ? " checked" : ""} /><span>${escapeHtml(label)}</span></label>`
    )
    .join("");
  return `
    <div class="seal-apply-field">
      <span class="seal-apply-label">申請する書類（${(d.selectedIds || []).length}件）</span>
      <div class="seal-bulk-kinds" role="radiogroup">${radios}</div>
    </div>`;
}

/** 一括申請の1件分。結果は "applied" / "skipped" / "failed" */
async function applyDocSealForBulk(pid, tab, extra) {
  if (!sealBulkTargetState(pid, tab).ok) return "skipped";
  const mk = docSealStateKey(pid, tab);
  const snap = snapshotDocSealState(mk);
  setDocSealStatus(pid, tab, "申請中");
  setSealApprovalProgress(mk, 0, {
    appliedAt: new Date().toISOString(),
    appliedByUserId: currentUser?.id != null ? String(currentUser.id) : "",
    stepApprovedAt: [],
    applicantComment: extra.applicantComment,
    dueDate: extra.dueDate
  });
  return (await persistDocSealApplicationToServer(mk, snap, extra)) ? "applied" : "failed";
}

// 入力の取り込み。テキストは再描画しない（入力中のフォーカスを保つ）。書類の選択は一覧を出し直す
document.addEventListener("input", (e) => {
  const el = e.target instanceof Element ? e.target.closest("[data-seal-apply-field]") : null;
  const d = uiState.sealConfirmDialog;
  if (!el || !d || d.processing) return;
  d[el.getAttribute("data-seal-apply-field")] = el.value;
});

document.addEventListener("change", (e) => {
  const d = uiState.sealConfirmDialog;
  if (!d || d.processing || !(e.target instanceof HTMLInputElement)) return;
  if (e.target.matches("[data-seal-apply-field]")) {
    d[e.target.getAttribute("data-seal-apply-field")] = e.target.value;
    return;
  }
  if (e.target.matches("[data-seal-bulk-kind]") && d.kind === "bulk") {
    // 再描画しない（入力中の期日・コメントや画面の位置を動かさない）。送信ボタンだけ有効にする
    d.tab = docTabHasSealWorkflow(e.target.value) ? e.target.value : "";
    const ok = document.querySelector("[data-seal-confirm-ok]");
    if (ok instanceof HTMLButtonElement) ok.disabled = !d.tab;
  }
});
// ── 捺印申請: 書類の内容変更で承認が外れた記録と【再申請】表示（2026-10 要望） ──
// サーバー（api/_docSeal.js resetDocSealApplicationsForProject）は、書類に載る内容（明細・案件名・宛先・
// 日付・支払期限・備考など）が変わると申請を未申請へ戻し、外れた日時・元の状態・変更点を残す。
// GET /api/doc-seal-applications の revokedByKey を uiState.docSealRevokeInfo に持ち、
// 申請者には「前回の承認は内容の変更で外れました」、承認者には【再申請】と「何から何に変わったか」を見せる。

/** 承認が外れた記録（なければ null） */
function sealRevokeInfo(mapKey) {
  const info = uiState.docSealRevokeInfo?.[String(mapKey)];
  return info && typeof info === "object" && info.revokedAt ? info : null;
}

/** 内容変更で外れた後の申請（＝再申請）か。申請中の行だけを対象にする */
function sealIsResubmission(mapKey) {
  const parsed = parseDocSealKey(mapKey);
  return !!parsed && getDocSealStatus(parsed.pid, parsed.tab) === "申請中" && !!sealRevokeInfo(mapKey);
}

/** GET の revokedByKey を取り込む（applyDocSealApplicationsPayload から呼ぶ） */
function applyDocSealRevokePayload(revokedByKey) {
  uiState.docSealRevokeInfo = {};
  const src = revokedByKey && typeof revokedByKey === "object" ? revokedByKey : {};
  for (const [k, v] of Object.entries(src)) {
    if (!v || typeof v !== "object" || !v.revokedAt) continue;
    uiState.docSealRevokeInfo[String(k)] = {
      revokedAt: String(v.revokedAt),
      fromStatus: String(v.fromStatus || ""),
      changes: (Array.isArray(v.changes) ? v.changes : []).map((c) => ({
        label: String(c?.label || ""),
        before: String(c?.before ?? ""),
        after: String(c?.after ?? "")
      }))
    };
  }
}

/** 変更点の一覧（「項目: 変更前 → 変更後」） */
function sealRevokeChangesHtml(info) {
  const changes = info?.changes || [];
  if (!changes.length) return `<p class="seal-revoke-empty">変更点の記録はありません</p>`;
  const rows = changes
    .map(
      (c) =>
        `<li><span class="seal-revoke-label">${escapeHtml(c.label)}</span><span class="seal-revoke-before">${escapeHtml(c.before || "（なし）")}</span><span class="seal-revoke-arrow" aria-hidden="true">→</span><span class="seal-revoke-after">${escapeHtml(c.after || "（なし）")}</span></li>`
    )
    .join("");
  return `<ul class="seal-revoke-changes">${rows}</ul>`;
}

/** 申請者向け（未申請）・承認者向け（申請中）の案内。記録が無ければ空 */
function sealRevokeNoticeHtml(mapKey) {
  const info = sealRevokeInfo(mapKey);
  if (!info) return "";
  const parsed = parseDocSealKey(mapKey);
  const st = parsed ? getDocSealStatus(parsed.pid, parsed.tab) : "";
  if (st === "承認済") return "";
  const when = formatSealApprovalDateTime(info.revokedAt).slice(0, 16);
  const from = info.fromStatus ? `（${escapeHtml(info.fromStatus)}）` : "";
  const head =
    st === "申請中"
      ? `<strong>【再申請】</strong>前回の申請${from}は書類の内容の変更で外れました（${escapeHtml(when)}）`
      : `前回の申請${from}は書類の内容の変更で外れました（${escapeHtml(when)}）。再申請してください`;
  return `<details class="seal-revoke-notice"><summary>${head}・変更点 ${info.changes.length}件</summary>${sealRevokeChangesHtml(info)}</details>`;
}

/** 書類画面の捺印ステータスバーの「捺印期限」（申請時の承認期日。未入力・未申請は未設定） */
function sealDeadlineText(mapKey) {
  const due = String(sealApprovalProgressRecord(mapKey)?.dueDate || "");
  return /^\d{4}-\d{2}-\d{2}$/.test(due) ? due.replace(/-/g, "/") : "未設定";
}
// ─────────────────────────────────────────────────────────────
// 一覧テーブルの列幅・並び替えを、ログインユーザーごとにサーバー保存する（fp_users.ui_prefs ／ PATCH /api/me）
//   - 対象は <table data-fp-table="<一覧ID>"> を付けたテーブルだけ（単一行の thead に限る）。
//     同じIDのテーブルが1画面に複数あっても（原価割当のグループ表など）同じ列幅を共有する
//   - 列は th の data-*-sort の値 → 見出し文字 → 列番号 の順で識別する（並び矢印 ▲▼ は除く）
//   - 列幅: 見出し右端のハンドルをドラッグ（ダブルクリックで既定の幅に戻す）。Excel・スプレッドシートと同じく
//     ドラッグした列だけが変わり、他の列は動かない（広げれば表が右へ伸びて横スクロール、狭めれば右が空く）。
//     画面は render() のたびに innerHTML ごと作り直されるので、描画のたびに保存値を当て直す
//   - 並び替え: FP_TABLE_SORT_STATE に登録した一覧の状態が既定から変わったら保存し、ログイン後に1回だけ復元する
//   - 列の表示設定: <table data-fp-table-columns> の一覧だけ、案内バーの「⚙ 表示する列」で列を隠せる（prefs.hidden＝列キーの配列）。
//     セルは消さずに fp-col-hidden を付けて隠す（style.css が列番号 nth-child で幅を決めている一覧があるため）
//   - スマホ幅（テーブルがカード表示になる ≤920px）では列幅・列の表示設定を扱わない
// ─────────────────────────────────────────────────────────────

const FP_TABLE_SP_QUERY = "(max-width: 920px)";
const FP_TABLE_COL_MIN = 40;

/** 一覧ごとの並び替え状態の読み書き（状態の形が一覧ごとに違うため {key, dir} に揃えて扱う） */
const FP_TABLE_SORT_STATE = {
  projects: {
    get: () => uiState.projectTable && { key: uiState.projectTable.sortKey, dir: uiState.projectTable.sortDir },
    set: (s) => Object.assign(uiState.projectTable, { sortKey: s.key, sortDir: s.dir })
  },
  "cost-mgmt": {
    get: () => uiState.costMgmtSort && { key: uiState.costMgmtSort.key, dir: uiState.costMgmtSort.dir },
    set: (s) => Object.assign(uiState.costMgmtSort, { key: s.key, dir: s.dir })
  },
  "cost-alloc-queue": {
    get: () => uiState.costAllocSort && { key: uiState.costAllocSort.key, dir: uiState.costAllocSort.dir },
    set: (s) => Object.assign(uiState.costAllocSort, { key: s.key, dir: s.dir })
  },
  "cost-review": {
    get: () => uiState.costReview && { key: uiState.costReview.sortKey, dir: uiState.costReview.sortDir },
    set: (s) => Object.assign(uiState.costReview, { sortKey: s.key, sortDir: s.dir })
  },
  "cost-reconcile": {
    get: () => uiState.costReconcile && { key: uiState.costReconcile.sortKey, dir: uiState.costReconcile.sortDir },
    set: (s) => Object.assign(uiState.costReconcile, { sortKey: s.key, sortDir: s.dir })
  },
  domains: {
    get: () => uiState.domainList && { key: uiState.domainList.sortKey, dir: uiState.domainList.sortAsc ? "asc" : "desc" },
    set: (s) => Object.assign(uiState.domainList, { sortKey: s.key, sortAsc: s.dir === "asc" })
  }
};

function fpTableSortNorm(s) {
  return s ? { key: String(s.key ?? ""), dir: s.dir === "asc" ? "asc" : "desc" } : null;
}

const fpTableSortEq = (a, b) => Boolean(a && b && a.key === b.key && a.dir === b.dir);

/** 各一覧の並び替えの既定（起動時の uiState の値）。原価消込の状態は開いたときに作られるので固定値 */
const fpTableSortDefaults = Object.fromEntries(
  Object.entries(FP_TABLE_SORT_STATE).map(([id, reg]) => [id, fpTableSortNorm(reg.get())])
);
fpTableSortDefaults["cost-reconcile"] = { key: "", dir: "asc" };

/** 読込済みの設定 { tables: { 一覧ID: { widths: {列キー: px}, sort: {key, dir} } } }。未読込は null */
let fpTablePrefs = null;
let fpTablePrefsUserId = null;
let fpTablePrefsFetchFailedAt = 0;
/** 並び替えを復元済みの一覧（ユーザーが変わったら空に戻す） */
const fpTableSortRestored = new Set();
const fpTablePrefsSaveTimers = {};
let fpTablePrefsSaveErrorShown = false;

function fpTablePrefsSet(userId, prefs) {
  fpTablePrefsUserId = userId;
  fpTablePrefs = prefs && typeof prefs === "object" && !Array.isArray(prefs) ? prefs : {};
  if (!fpTablePrefs.tables || typeof fpTablePrefs.tables !== "object") fpTablePrefs.tables = {};
  fpTableSortRestored.clear();
  // 原価一覧は独自の列幅リサイズ（17-todo-kanban-pwa-boot.js）を持つので、保存値を状態へ流し込む
  // （保存が無い人は空に戻す＝同じタブで前にログインしていた人の幅を引き継がない）
  const costWidths = fpTablePrefs.tables["cost-mgmt"]?.widths;
  uiState.costMgmtColWidths = costWidths && typeof costWidths === "object" ? { ...costWidths } : {};
}

/** ログアウト時（doLogout）に呼ぶ。前の人の設定・並び順・保存待ちを次の人へ持ち越さない */
function fpTablePrefsReset() {
  for (const id of Object.keys(fpTablePrefsSaveTimers)) {
    clearTimeout(fpTablePrefsSaveTimers[id]);
    delete fpTablePrefsSaveTimers[id];
  }
  fpTablePrefs = null;
  fpTablePrefsUserId = null;
  fpTableSortRestored.clear();
  uiState.costMgmtColWidths = {};
  for (const [id, reg] of Object.entries(FP_TABLE_SORT_STATE)) {
    if (reg.get() && fpTableSortDefaults[id]) reg.set(fpTableSortDefaults[id]);
  }
}

/** ログイン中ユーザーの設定が読込済みか。未読込なら /api/me から取得を始める（取得後に再描画） */
function fpTablePrefsReady() {
  const uid = currentUser?.dbId != null ? Number(currentUser.dbId) : NaN;
  if (!Number.isFinite(uid) || !authToken) return false;
  if (fpTablePrefs && fpTablePrefsUserId === uid) return true;
  // 自動ログインは /api/me の応答（authUser）に設定が載っている。原価一覧の列幅は描画時に
  // 状態から読むので、流し込んだら描き直す
  if (authUser && Number(authUser.id) === uid && authUser.uiPrefs) {
    fpTablePrefsSet(uid, authUser.uiPrefs);
    render();
    return true;
  }
  // 取得に失敗したら 30 秒は再試行しない（描画のたびに /api/me を撃たない）
  if (Date.now() - fpTablePrefsFetchFailedAt < 30000) return false;
  fetchOnceInFlight(`fp-table-prefs:${uid}`, async () => {
    const r = await apiFetch("/api/me");
    if (Number(currentUser?.dbId) !== uid) return;
    if (!r.ok) {
      fpTablePrefsFetchFailedAt = Date.now();
      return;
    }
    fpTablePrefsSet(uid, r.data?.user?.uiPrefs);
    render();
  });
  return false;
}

function fpTablePrefsOf(id) {
  if (!fpTablePrefs.tables[id] || typeof fpTablePrefs.tables[id] !== "object") fpTablePrefs.tables[id] = {};
  return fpTablePrefs.tables[id];
}

/** 一覧1つ分の設定をサーバーへ保存（連続操作はまとめて1回にする） */
function fpTablePrefsSave(id) {
  clearTimeout(fpTablePrefsSaveTimers[id]);
  fpTablePrefsSaveTimers[id] = setTimeout(async () => {
    delete fpTablePrefsSaveTimers[id];
    if (!fpTablePrefs) return;
    const r = await apiFetch("/api/me", { method: "PATCH", body: { uiPrefsTable: { id, prefs: fpTablePrefsOf(id) } } });
    if (r.ok) fpTablePrefsSaveErrorShown = false;
    if (!r.ok && !fpTablePrefsSaveErrorShown) {
      fpTablePrefsSaveErrorShown = true;
      showToastError(typeof r.data?.error === "string" ? r.data.error : "列幅・並び順の保存に失敗しました");
    }
  }, 600);
}

// 保存待ち（まとめ待ちの 600ms 中）にタブを閉じた・再読込した場合も送り切る。
// ページ破棄後も届くよう keepalive 付きの fetch を使う（apiFetch は keepalive を渡せないため生 fetch）
window.addEventListener("pagehide", () => {
  if (!fpTablePrefs || !authToken) return;
  for (const id of Object.keys(fpTablePrefsSaveTimers)) {
    clearTimeout(fpTablePrefsSaveTimers[id]);
    delete fpTablePrefsSaveTimers[id];
    fetch("/api/me", {
      method: "PATCH",
      keepalive: true,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ uiPrefsTable: { id, prefs: fpTablePrefsOf(id) } })
    }).catch(() => {});
  }
});

/** 原価一覧の独自リサイズから呼ぶ（列幅の保存だけをこの仕組みに相乗りさせる） */
function fpTablePrefsSaveWidths(id, widths) {
  if (!fpTablePrefsReady()) return;
  fpTablePrefsOf(id).widths = { ...widths };
  fpTablePrefsSave(id);
  fpTableUpdateAllTools();
}

function fpTableHeaderCells(table) {
  const head = table.tHead;
  if (!head || head.rows.length !== 1) return null;
  const cells = [...head.rows[0].cells];
  if (!cells.length || cells.some((c) => c.colSpan > 1)) return null;
  return cells;
}

function fpTableSortAttrValue(th) {
  for (const a of th.attributes) {
    if (/^data-[a-z-]+-sort$/.test(a.name) && a.value) return a.value;
  }
  return "";
}

function fpTableColKey(th, index) {
  const sortVal = fpTableSortAttrValue(th);
  if (sortVal) return sortVal;
  const text = th.textContent.replace(/[▲▼↑↓⇅]/g, "").replace(/\s+/g, "").slice(0, 40);
  return text || `#${index}`;
}

/** 隠す列のキー（列の表示設定）。設定が無ければ空 */
function fpTableHiddenKeys(id) {
  const h = fpTablePrefs?.tables?.[id]?.hidden;
  return new Set(Array.isArray(h) ? h.map(String) : []);
}

function fpTableIsHidden(th) {
  return Boolean(th.classList?.contains("fp-col-hidden"));
}

/** 表示設定で隠せる列か（チェック欄・操作列など見出しが空の列、data-fp-col-fixed の列は常に出す） */
function fpTableColToggleable(cells, th) {
  return !fpTableIsActionCol(cells, th) && !th.hasAttribute("data-fp-col-fixed") && fpTableColLabel(th) !== "";
}

/** 見出しの表示名（並びの矢印・空白を除く） */
function fpTableColLabel(th) {
  return th.textContent.replace(/[▲▼↑↓⇅]/g, "").replace(/\s+/g, " ").trim();
}

/** 隠す列の見出し・本文セルに fp-col-hidden を付け外す（列をまたぐ行は対象外） */
function fpTableApplyHidden(table, cells, id) {
  if (table.dataset.fpTableColumns === undefined) return;
  const hidden = fpTableHiddenKeys(id);
  const flags = cells.map((th, i) => fpTableColToggleable(cells, th) && hidden.has(fpTableColKey(th, i)));
  for (const row of [table.tHead.rows[0], ...[...table.tBodies].flatMap((b) => [...b.rows])]) {
    if (row.cells.length !== cells.length) continue;
    flags.forEach((f, i) => row.cells[i].classList.toggle("fp-col-hidden", f));
  }
}

/**
 * <colgroup> の <col>（見出しの列と1対1のときだけ）。固定レイアウトでは col の幅が th より優先されるため、
 * col がある表は col にも同じ幅を入れる。span 付き等で数が合わない表は null（列幅を扱わない）
 */
function fpTableCols(table, cells) {
  const cols = [...table.querySelectorAll(":scope > colgroup > col")];
  if (!cols.length) return [];
  return cols.length === cells.length && cols.every((c) => c.span === 1) ? cols : null;
}

/** 列の幅（＝保存する本来の幅。data-fp-w にも持つ）を入れる。余白を吸収した表示幅は fpTableFitSlack が上書きする */
function fpTableSetColWidth(th, col, w) {
  th.dataset.fpW = String(w);
  th.style.width = `${w}px`;
  if (col) col.style.width = `${w}px`;
}

/**
 * 表を枠いっぱいに広げる。列幅の合計が枠より狭いときは、余りを「余白を埋める列」に足して表示する（保存値は変えない）。
 * 埋める列は見出しに data-fp-fill を付けた列（摘要・備考など中身の長い列）、無ければ最後の列（操作列があればそれ）。
 * 最後の列が操作列だと余りがボタンの左の空白になり、摘要が保存幅のまま狭く折り返して見えたため（2026-10 指摘・原価割当）。
 * 表が枠より狭いと右側に見出しの帯や罫線の無い空白ができ、段差に見えるため。枠より広いときは合計幅のまま（横スクロール）。
 * skipIndex: ドラッグ中の列。その列が埋める列のときは余白を足さない（足すと狭めても見た目が変わらないため）
 */
function fpTableFitSlack(table, cells, cols, skipIndex = -1) {
  const ws = cells.map((th) => (fpTableIsHidden(th) ? 0 : Number(th.dataset.fpW) || Math.round(th.getBoundingClientRect().width)));
  const total = ws.reduce((a, b) => a + b, 0);
  const box = fpTableScroller(table) || table.parentElement;
  const slack = Math.floor((box?.clientWidth || 0) - total);
  // 余白を埋める列は表示中の列から選ぶ（隠した列に足しても見えないため）
  const marked = cells.findIndex((th) => th.hasAttribute("data-fp-fill") && !fpTableIsHidden(th));
  let lastShown = cells.length - 1;
  while (lastShown > 0 && fpTableIsHidden(cells[lastShown])) lastShown -= 1;
  const target = marked >= 0 && marked !== skipIndex ? marked : lastShown;
  const fill = slack > 0 && target !== skipIndex ? target : -1;
  cells.forEach((th, i) => {
    const w = i === fill ? ws[i] + slack : ws[i];
    th.style.width = `${w}px`;
    if (cols[i]) cols[i].style.width = `${w}px`;
  });
  table.style.width = `${fill >= 0 ? total + slack : total}px`;
}

/**
 * 保存した列幅が今の全列（操作列を除く）をカバーしているか。後から列が増えた一覧では、古い保存幅のまま当てると
 * 合計が枠を超えて横スクロールになり、増えた列も狭く押し込まれる（2026-10: 申請承認に承認期日を足した際の指摘）。
 * カバーしていなければ保存幅は当てず既定のレイアウトで出す（保存値は消さない＝「列幅を元に戻す」で消せる）
 */
function fpTableSavedWidthsCoverAll(cells, widths) {
  return cells.every((th, i) => fpTableIsActionCol(cells, th) || fpTableIsHidden(th) || Number(widths[fpTableColKey(th, i)]) > 0);
}

/**
 * 列幅を当てる。保存に無い列（後から増えた列）は今の表示幅のまま固定する。
 * 列幅の合計が枠より狭いときは、data-fp-fill の列（無ければ最後の列）が余りを吸収して表を枠いっぱいにする（fpTableFitSlack）
 */
function fpTableApplyWidths(table, cells, widths) {
  const cols = fpTableCols(table, cells) || [];
  const measured = cells.map((c) => c.getBoundingClientRect().width);
  const ws = cells.map((th, i) => {
    if (fpTableIsHidden(th)) return 0;
    const saved = Number(widths[fpTableColKey(th, i)]);
    // 操作列は幅を変えられない列なので保存値を使わず今の幅のまま（以前ハンドルがあった頃に広げた値が残っていても戻せないため）
    const useSaved = Number.isFinite(saved) && saved > 0 && !fpTableIsActionCol(cells, th);
    const w = Math.round(Math.max(fpTableColMin(th), useSaved ? saved : measured[i]));
    fpTableSetColWidth(th, cols[i], w);
    return w;
  });
  table.classList.add("fp-table--sized");
  table.style.width = `${ws.reduce((a, b) => a + b, 0)}px`;
  table.style.minWidth = "0";
  // 保存幅では中身（バッジ等）が収まらない列は、その分だけ広げて表示する（保存値は変えない）。
  // 狭く保存した後に長いバッジの行が増えた場合などに、隣の列へはみ出して重ならないように
  const over = fpTableColsOverflow(table, cells.length);
  cells.forEach((th, i) => {
    if (over[i] > 0) fpTableSetColWidth(th, cols[i], (ws[i] += over[i]));
  });
  fpTableFitSlack(table, cells, cols);
}

/**
 * 列の最小幅。見出しに文字がある列は 64px（日本語4〜5文字。これより狭いと1〜2文字ずつ折り返して読めない）、
 * チェック欄など見出しが空の列は 40px。これとは別に、折り返せない中身が収まる幅も確保する（fpTableColsOverflow）
 */
function fpTableColMin(th) {
  return th.textContent.replace(/[▲▼↑↓⇅\s]/g, "") ? 64 : FP_TABLE_COL_MIN;
}

/** 原価一覧（独自の列幅リサイズ・17-todo-kanban-pwa-boot.js）のドラッグ用: 最小幅と中身が収まる幅を下回らないよう col に幅を入れ、入れた幅を返す */
function fpTableClampOwnColWidth(th, col, w) {
  let width = Math.round(Math.max(fpTableColMin(th), w));
  col.style.width = `${width}px`;
  const table = th.closest("table");
  const cells = table ? fpTableHeaderCells(table) : null;
  const index = cells ? cells.indexOf(th) : -1;
  const over = index >= 0 ? fpTableColsOverflow(table, cells.length, index)[index] : 0;
  if (over > 0) col.style.width = `${(width += over)}px`;
  return width;
}

/**
 * 原価一覧の表示時: 保存幅が最小幅・中身の幅を下回る列を広げる（保存値は変えない）。幅指定の無い col（残り幅を取る発注名）は対象外。
 * 広げた分は表の最小幅にも足す（足さないと、残り幅で決まる発注名がその分だけ削られて潰れる）
 */
function fpTableFitOwnCols(table, cells) {
  const cols = fpTableCols(table, cells);
  if (!cols || !cols.length) return;
  const over = fpTableColsOverflow(table, cells.length);
  let added = 0;
  cells.forEach((th, i) => {
    const cur = parseFloat(cols[i].style.width);
    if (!cur) return;
    const w = Math.round(Math.max(fpTableColMin(th), cur + Math.max(0, over[i])));
    if (w <= cur) return;
    cols[i].style.width = `${w}px`;
    added += w - cur;
  });
  const minW = parseFloat(table.style.minWidth);
  if (added > 0 && minW) table.style.minWidth = `${Math.round(minW + added)}px`;
}

/**
 * 各列の本文セルで、折り返せない中身（バッジ・ボタン・入力欄など）がセルからはみ出している幅の最大値。
 * 文字は折り返す（overflow-wrap: anywhere）ので、はみ出すのは折り返せない部品だけ。重くならないよう先頭100行だけ見る。
 * only を渡すとその列だけ調べる（ドラッグ中）
 */
function fpTableColsOverflow(table, cellsLen, only = -1) {
  const over = new Array(cellsLen).fill(0);
  for (const body of table.tBodies) {
    for (const row of [...body.rows].slice(0, 100)) {
      if (row.cells.length !== cellsLen) continue; // 列をまたぐ行（colspan）は対象外
      for (let i = only >= 0 ? only : 0; i < (only >= 0 ? only + 1 : cellsLen); i++) {
        const td = row.cells[i];
        over[i] = Math.max(over[i], td.scrollWidth - td.clientWidth);
      }
    }
  }
  return over;
}

/**
 * 最後の列が操作列か（見出しが「操作」または空＝ボタン・矢印の列）。操作列は幅を変えさせず（ハンドルなし）、
 * 列幅を変えて表が枠より広くなっても右端に固定して常に押せるようにする（fp-table--fixed-last・style.css）
 */
function fpTableIsActionCol(cells, th) {
  return th === cells[cells.length - 1] && /^(操作)?$/.test(th.textContent.replace(/\s+/g, ""));
}

function fpTableEnhance(table) {
  const id = table.dataset.fpTable;
  const cells = fpTableHeaderCells(table);
  if (!id || !cells) return;
  fpTableApplyHidden(table, cells, id);
  const lastTh = cells[cells.length - 1];
  // 案件一覧のように最初から操作列を右端固定（sticky）している表は、その表のスタイルに任せる。
  // 判定は本文のセルで行う（見出しは縦スクロールで上に固定するために sticky のことがあり、右端固定とは限らない）
  const firstRow = [...table.tBodies].flatMap((b) => [...b.rows]).find((r) => r.cells.length === cells.length);
  const lastTd = firstRow?.cells[cells.length - 1];
  if (fpTableIsActionCol(cells, lastTh) && !(lastTd && getComputedStyle(lastTd).position === "sticky")) {
    table.classList.add("fp-table--fixed-last");
    // 見出しに背景色が無いと、固定した見出しの下を他の列が透けて流れる
    if (getComputedStyle(lastTh).backgroundColor === "rgba(0, 0, 0, 0)") lastTh.style.backgroundColor = "#fff";
  }
  // 原価一覧は独自の列幅リサイズがあるので、ここでは狭すぎる列の補正と並び替えの保存だけ扱う
  if (table.dataset.fpTableOwnWidths !== undefined && !window.matchMedia(FP_TABLE_SP_QUERY).matches) fpTableFitOwnCols(table, cells);
  if (table.dataset.fpTableOwnWidths === undefined && fpTableCols(table, cells) && !window.matchMedia(FP_TABLE_SP_QUERY).matches) {
    cells.forEach((th) => {
      if (fpTableIsActionCol(cells, th) || th.querySelector(":scope > .fp-col-resize")) return;
      if (getComputedStyle(th).position === "static") th.style.position = "relative";
      th.insertAdjacentHTML(
        "beforeend",
        `<span class="fp-col-resize" data-fp-col-resize="1" title="ドラッグで列幅を変更（ダブルクリックで元に戻す）" aria-hidden="true"></span>`
      );
    });
    const widths = fpTablePrefsOf(id).widths;
    if (widths && Object.keys(widths).length && fpTableSavedWidthsCoverAll(cells, widths)) fpTableApplyWidths(table, cells, widths);
  }
  if (!window.matchMedia(FP_TABLE_SP_QUERY).matches) {
    fpTableFitGrid(table);
    fpTableEnsureTools(table, id);
  }
  fpTableSyncSort(table, id);
}

/** 表の横スクロール枠（overflow-x が auto/scroll の最も近い祖先）。無ければ null */
function fpTableScroller(table) {
  for (let el = table.parentElement; el && el !== appRoot; el = el.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(el).overflowX)) return el;
  }
  return null;
}

/**
 * 画面の高さに収める枠（テンプレートで fp-grid-scroll を付けた枠だけ・試験導入）。枠の上端の位置と、枠の下にある
 * ページ送り等の高さを CSS 変数に入れ、ページ最上部で枠全体（＝横スクロールバー）が画面内に収まる高さにする。
 * 枠の中で縦スクロールするので、見出し（sticky）も付いてくる
 */
function fpTableFitGrid(table) {
  const sc = fpTableScroller(table);
  if (!sc?.classList.contains("fp-grid-scroll")) return;
  let below = 0;
  for (let el = sc.nextElementSibling; el; el = el.nextElementSibling) below += el.offsetHeight;
  sc.style.setProperty("--fp-grid-top", `${Math.round(sc.getBoundingClientRect().top + window.scrollY)}px`);
  sc.style.setProperty("--fp-grid-bottom", `${Math.min(160, below)}px`);
}

/**
 * 表の上の案内バー。横に隠れた列があるときは「◀ 右にあと N 列 ▶」（ボタンで横移動）、列幅を変えた一覧には
 * 「列幅を元に戻す」を出す。横スクロールバーは表の下端にしか無く、縦に長い表では画面外にあって
 * 隠れた列に気付けないため。同じ一覧IDの表が1画面に複数ある場合（原価割当のグループ表）は最初の1つだけ
 */
function fpTableEnsureTools(table, id) {
  const scroller = fpTableScroller(table);
  const anchor = scroller || table;
  let bar = anchor.previousElementSibling;
  if (!bar?.classList.contains("fp-table-tools")) {
    if (appRoot.querySelector(`.fp-table-tools[data-fp-tools-for="${id}"]`)) return;
    anchor.insertAdjacentHTML(
      "beforebegin",
      `<div class="fp-table-tools" data-fp-tools-for="${id}">` +
        `<span class="fp-table-tools-scroll" hidden>` +
        `<button type="button" class="fp-table-tools-btn" data-fp-scroll="-1" aria-label="左へスクロール">◀</button>` +
        `<span class="fp-table-tools-hint"></span>` +
        `<button type="button" class="fp-table-tools-btn" data-fp-scroll="1" aria-label="右へスクロール">▶</button>` +
        `</span>` +
        `<button type="button" class="fp-table-tools-reset" data-fp-reset-widths="${id}" hidden>列幅を元に戻す</button>` +
        (table.dataset.fpTableColumns !== undefined
          ? `<span class="fp-table-cols"><button type="button" class="fp-table-tools-reset" data-fp-cols-toggle="${id}" aria-expanded="false">⚙ 表示する列</button>` +
            `<span class="fp-table-cols-panel" data-fp-cols-panel="${id}" hidden></span></span>`
          : "") +
        `</div>`
    );
    bar = anchor.previousElementSibling;
  }
  bar._fpScroller = scroller;
  bar._fpTable = table;
  if (scroller && !scroller._fpToolsBound) {
    scroller._fpToolsBound = true;
    scroller.addEventListener("scroll", () => fpTableUpdateTools(bar), { passive: true });
  }
  fpTableUpdateTools(bar);
}

/** 案内バーの表示を今のスクロール位置・保存幅に合わせる（隠れている列の数は、右端固定の操作列を除いて数える） */
function fpTableUpdateTools(bar) {
  const sc = bar._fpScroller;
  const table = bar._fpTable;
  const id = bar.dataset.fpToolsFor;
  const widths = fpTablePrefs?.tables?.[id]?.widths;
  bar.querySelector("[data-fp-reset-widths]").hidden = !(widths && Object.keys(widths).length);
  const scrollWrap = bar.querySelector(".fp-table-tools-scroll");
  const over = sc && table?.isConnected ? sc.scrollWidth - sc.clientWidth : 0;
  scrollWrap.hidden = over <= 2;
  if (over <= 2) return;
  const box = sc.getBoundingClientRect();
  const cells = fpTableHeaderCells(table) || [];
  const last = cells[cells.length - 1];
  // 右端に固定された見出し（sticky かつ right:0。上に固定するだけの sticky は right が auto）
  const pinnedLast = Boolean(last) && getComputedStyle(last).position === "sticky" && getComputedStyle(last).right === "0px";
  const visRight = box.right - (pinnedLast ? last.getBoundingClientRect().width : 0);
  const cols = (pinnedLast ? cells.slice(0, -1) : cells).filter((c) => !fpTableIsHidden(c));
  const hiddenRight = cols.filter((c) => c.getBoundingClientRect().right > visRight + 4).length;
  const hiddenLeft = cols.filter((c) => c.getBoundingClientRect().left < box.left - 4).length;
  const parts = [];
  if (hiddenLeft) parts.push(`左に ${hiddenLeft} 列`);
  if (hiddenRight) parts.push(`右にあと ${hiddenRight} 列`);
  bar.querySelector(".fp-table-tools-hint").textContent = parts.length ? `${parts.join("・")}（横にスクロールできます）` : "横にスクロールできます";
  bar.querySelector('[data-fp-scroll="-1"]').disabled = sc.scrollLeft <= 1;
  bar.querySelector('[data-fp-scroll="1"]').disabled = sc.scrollLeft >= over - 1;
}

function fpTableUpdateAllTools() {
  appRoot.querySelectorAll(".fp-table-tools").forEach(fpTableUpdateTools);
}

/** 一覧の列幅を初期に戻す（「列幅を元に戻す」・見出しハンドルのダブルクリック）。原価一覧は独自リサイズの状態も消す */
function fpTableResetWidths(id) {
  if (!fpTablePrefsReady()) return;
  delete fpTablePrefsOf(id).widths;
  if (id === "cost-mgmt") uiState.costMgmtColWidths = {};
  fpTablePrefsSave(id);
  render();
}

/** 並び替え: 初回は保存値を復元、以降は既定から変わったら保存する（既定のままなら保存しない） */
function fpTableSyncSort(table, id) {
  const reg = FP_TABLE_SORT_STATE[id];
  const cur = fpTableSortNorm(reg?.get());
  if (!cur) return;
  const def = fpTableSortDefaults[id];
  const prefs = fpTablePrefsOf(id);
  if (!fpTableSortRestored.has(id)) {
    fpTableSortRestored.add(id);
    const saved = fpTableSortNorm(prefs.sort);
    const validKey =
      saved &&
      (saved.key === "" || saved.key === def?.key || fpTableHeaderCells(table)?.some((th) => fpTableSortAttrValue(th) === saved.key));
    // 設定の読込前に自分で並び替えていた場合（既定から変わっている）は、その操作を優先する
    if (validKey && fpTableSortEq(cur, def) && !fpTableSortEq(saved, cur)) {
      reg.set(saved);
      // 既定の並びが画面に出る前に描き直す（render() だと1フレーム既定の並びが見えてちらつく）
      renderBeforePaint();
    }
    return;
  }
  if (fpTableSortEq(cur, def)) {
    if (!prefs.sort) return;
    delete prefs.sort;
  } else {
    if (fpTableSortEq(cur, fpTableSortNorm(prefs.sort))) return;
    prefs.sort = cur;
  }
  fpTablePrefsSave(id);
}

function fpTableEnhanceAll() {
  const tables = appRoot.querySelectorAll("table[data-fp-table]");
  if (!tables.length || !fpTablePrefsReady()) return;
  tables.forEach((t) => {
    // 処理済みの印は見出し行に付ける（テーブルはそのままで thead だけ差し替える部分更新でも当て直すため）
    const headRow = t.tHead?.rows[0];
    if (!headRow || headRow._fpTableEnhanced) return;
    headRow._fpTableEnhanced = true;
    fpTableEnhance(t);
  });
}

// 画面の作り直し（innerHTML 置換）を検知して当て直す。MutationObserver は描画前に走るのでちらつかない
new MutationObserver(fpTableEnhanceAll).observe(appRoot, { childList: true, subtree: true });

// ── 列幅ドラッグ ──
document.addEventListener("pointerdown", (e) => {
  const handle = e.target instanceof Element ? e.target.closest("[data-fp-col-resize]") : null;
  if (!(handle instanceof HTMLElement) || e.button !== 0) return;
  const th = handle.closest("th");
  const table = th?.closest("table[data-fp-table]");
  const cells = table ? fpTableHeaderCells(table) : null;
  if (!th || !table || !cells || !fpTablePrefsReady()) return;
  e.preventDefault();
  e.stopPropagation();
  const id = table.dataset.fpTable;
  const index = cells.indexOf(th);
  // 開始時の幅は本来の幅（data-fp-w）を使う。表示幅だと、余白を吸収して広がった最後の列の幅を本来の幅として保存してしまう
  const startWidths = cells.map((c) => Number(c.dataset.fpW) || Math.round(c.getBoundingClientRect().width));
  const startX = e.clientX;
  let moved = false;
  const cols = fpTableCols(table, cells) || [];
  const keys = cells.map((c, i) => fpTableColKey(c, i));
  // 隠している列は幅を測れない（0）ので、保存済みの幅をそのまま残す（表示に戻したときに使う）
  const prevWidths = fpTablePrefsOf(id).widths || {};
  const widthsNow = () =>
    Object.fromEntries(
      cells
        .map((c, i) => [keys[i], fpTableIsHidden(c) ? Number(prevWidths[keys[i]]) : Number(c.dataset.fpW) || startWidths[i]])
        .filter(([, w]) => w > 0)
    );
  const onMove = (ev) => {
    if (!moved) {
      moved = true;
      handle.classList.add("is-active");
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      // 今の表示幅で全列を固定する（ドラッグ中の列以外が動かないように）
      fpTableApplyWidths(table, cells, Object.fromEntries(keys.map((k, i) => [k, startWidths[i]])));
    }
    const w = Math.round(Math.max(fpTableColMin(th), startWidths[index] + ev.clientX - startX));
    fpTableSetColWidth(th, cols[index], w);
    fpTableFitSlack(table, cells, cols, index);
    // 中身（バッジ等）が収まる幅より狭くはしない（はみ出した分だけ戻す）
    const over = fpTableColsOverflow(table, cells.length, index)[index];
    if (over > 0) {
      fpTableSetColWidth(th, cols[index], w + over);
      fpTableFitSlack(table, cells, cols, index);
    }
  };
  const onUp = () => {
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.removeEventListener("pointercancel", onUp);
    if (!moved) return;
    handle.classList.remove("is-active");
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    // 離した直後の見出しクリック（並び替え）を止める。click は pointerup と同じタスク内で届くので、
    // 次のタスクで外す（クリックが起きなかった場合に、無関係な次のクリックを飲み込まないように）
    const swallow = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
    document.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => document.removeEventListener("click", swallow, { capture: true }), 0);
    const widths = widthsNow();
    fpTablePrefsOf(id).widths = widths;
    fpTableFitSlack(table, cells, cols);
    // 同じ一覧IDの他のテーブル（同じ画面に複数ある場合）にもその場で反映する
    appRoot.querySelectorAll(`table[data-fp-table="${id}"]`).forEach((t) => {
      const c = t !== table ? fpTableHeaderCells(t) : null;
      if (c) fpTableApplyWidths(t, c, widths);
    });
    fpTablePrefsSave(id);
    fpTableUpdateAllTools();
  };
  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onUp);
});

// ハンドル上のクリック（ドラッグせずに離した場合）で並び替えが走らないようにする
document.addEventListener(
  "click",
  (e) => {
    if (e.target instanceof Element && e.target.closest("[data-fp-col-resize]")) {
      e.stopPropagation();
      e.preventDefault();
    }
  },
  true
);

// ダブルクリックで、その一覧の列幅を既定に戻す（原価一覧の独自ハンドルも対象）
document.addEventListener("dblclick", (e) => {
  const handle = e.target instanceof Element ? e.target.closest("[data-fp-col-resize], [data-cost-col-resize]") : null;
  const table = handle?.closest("table[data-fp-table]");
  if (!table) return;
  e.preventDefault();
  fpTableResetWidths(table.dataset.fpTable);
});

// 案内バーの操作（横スクロールボタン・列幅を元に戻す）
document.addEventListener("click", (e) => {
  const el = e.target instanceof Element ? e.target.closest("[data-fp-scroll], [data-fp-reset-widths]") : null;
  if (!el) return;
  if (el.dataset.fpResetWidths) {
    fpTableResetWidths(el.dataset.fpResetWidths);
    return;
  }
  const sc = el.closest(".fp-table-tools")?._fpScroller;
  if (sc) sc.scrollBy({ left: Number(el.dataset.fpScroll) * Math.round(sc.clientWidth * 0.7), behavior: "smooth" });
});

// ── 列の表示設定（⚙ 表示する列）──
/** パネルの中身（見出しの列ごとのチェック）を今の表から作る */
function fpTableColsPanelHtml(table, id) {
  const cells = fpTableHeaderCells(table) || [];
  const hidden = fpTableHiddenKeys(id);
  const items = cells
    .map((th, i) => ({ th, key: fpTableColKey(th, i) }))
    .filter(({ th }) => fpTableColToggleable(cells, th))
    .map(
      ({ th, key }) =>
        `<label class="fp-table-cols-item"><input type="checkbox" data-fp-col-key="${escapeHtmlAttr(key)}" data-fp-cols-for="${escapeHtmlAttr(id)}"${hidden.has(key) ? "" : " checked"} />${escapeHtml(fpTableColLabel(th))}</label>`
    )
    .join("");
  return `${items}<button type="button" class="fp-table-cols-showall" data-fp-cols-showall="${escapeHtmlAttr(id)}">すべて表示</button>`;
}

/** 隠す列を保存し、開いている同じ一覧の表へその場で当てる（再描画しない＝パネルを開いたまま続けて選べる） */
function fpTableSetHidden(id, keys) {
  if (!fpTablePrefsReady()) return;
  const prefs = fpTablePrefsOf(id);
  if (keys.length) prefs.hidden = keys;
  else delete prefs.hidden;
  fpTablePrefsSave(id);
  appRoot.querySelectorAll(`table[data-fp-table="${id}"]`).forEach((t) => {
    const cells = fpTableHeaderCells(t);
    if (!cells) return;
    fpTableApplyHidden(t, cells, id);
    const widths = prefs.widths;
    if (!widths || !Object.keys(widths).length || window.matchMedia(FP_TABLE_SP_QUERY).matches) return;
    // 表示に戻した列に保存幅が無ければ今の幅を足す（無いと全列の保存幅がそろわず、次回から保存幅が当たらなくなる）
    cells.forEach((th, i) => {
      const k = fpTableColKey(th, i);
      if (!fpTableIsHidden(th) && !fpTableIsActionCol(cells, th) && !(Number(widths[k]) > 0)) widths[k] = Math.round(th.getBoundingClientRect().width);
    });
    fpTableApplyWidths(t, cells, widths);
  });
  fpTableUpdateAllTools();
}

function fpTableCloseColsPanels(except = null, scrolled = null) {
  appRoot.querySelectorAll("[data-fp-cols-panel]").forEach((panel) => {
    if (panel === except) return;
    // スクロールで閉じるのは、ボタンの位置が動くスクロール（ページ・ボタンを含む枠）のときだけ
    if (scrolled && scrolled !== document && !scrolled.contains?.(panel)) return;
    panel.hidden = true;
    panel.parentElement?.querySelector("[data-fp-cols-toggle]")?.setAttribute("aria-expanded", "false");
  });
}

document.addEventListener("click", (e) => {
  const t = e.target instanceof Element ? e.target : null;
  const toggle = t?.closest("[data-fp-cols-toggle]");
  if (toggle) {
    const panel = toggle.parentElement?.querySelector("[data-fp-cols-panel]");
    const table = toggle.closest(".fp-table-tools")?._fpTable;
    if (!panel || !table) return;
    fpTableCloseColsPanels(panel);
    const open = panel.hidden;
    if (open) {
      panel.innerHTML = fpTableColsPanelHtml(table, toggle.dataset.fpColsToggle);
      // 一覧の枠（overflow:hidden）で切れないよう、ボタンの位置から画面に固定して出す（スクロールしたら閉じる）
      const r = toggle.getBoundingClientRect();
      panel.style.top = `${Math.round(r.bottom + 4)}px`;
      panel.style.right = `${Math.round(window.innerWidth - r.right)}px`;
      panel.style.maxHeight = `${Math.max(160, Math.round(window.innerHeight - r.bottom - 16))}px`;
    }
    panel.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    return;
  }
  const showAll = t?.closest("[data-fp-cols-showall]");
  if (showAll) {
    const id = showAll.dataset.fpColsShowall;
    fpTableSetHidden(id, []);
    showAll.closest("[data-fp-cols-panel]")?.querySelectorAll("[data-fp-col-key]").forEach((cb) => (cb.checked = true));
    return;
  }
  // パネルの外を押したら閉じる
  if (!t?.closest(".fp-table-cols")) fpTableCloseColsPanels();
});

window.addEventListener(
  "scroll",
  (e) => {
    if (!(e.target instanceof Element && e.target.closest("[data-fp-cols-panel]"))) fpTableCloseColsPanels(null, e.target);
  },
  { capture: true, passive: true }
);

document.addEventListener("change", (e) => {
  const cb = e.target instanceof HTMLInputElement && e.target.matches("[data-fp-col-key]") ? e.target : null;
  if (!cb) return;
  const panel = cb.closest("[data-fp-cols-panel]");
  const keys = [...(panel?.querySelectorAll("[data-fp-col-key]") || [])].filter((x) => !x.checked).map((x) => x.dataset.fpColKey);
  fpTableSetHidden(cb.dataset.fpColsFor, keys);
});

// 画面幅が変わると隠れる列の数も変わる
let fpTableResizeRaf = 0;
window.addEventListener("resize", () => {
  cancelAnimationFrame(fpTableResizeRaf);
  fpTableResizeRaf = requestAnimationFrame(() => {
    appRoot.querySelectorAll("table.fp-table--sized[data-fp-table]").forEach((t) => {
      const cells = fpTableHeaderCells(t);
      if (cells) fpTableFitSlack(t, cells, fpTableCols(t, cells) || []);
    });
    fpTableUpdateAllTools();
  });
});
// ────────────────────────────────────────────────────────────────
// ToDo管理: 課題ボードの「タスクMD」ボタン
// 押すと、毎朝のタスクMD生成（GitHub Actions）をこのプロジェクトだけ対象にいますぐ起動する
// （サーバ api/task-md.js）。既にある MD も作り直して上書きする。書き出し先・形式は毎朝と同じで、
// 数分で develop の tasks/ に反映される。
// ────────────────────────────────────────────────────────────────

function progressTaskMdButtonHtml(ch) {
  // 毎朝の自動生成と同じく、種別「開発」のプロジェクトだけ（サーバ api/task-md.js も同じ判定で断る）
  if (String(ch?.genre || "") !== PM_TASK_MD_GENRE) return "";
  const cid = String(ch?.id ?? "");
  const busy = String(uiState.progressTaskMdRequesting || "") === cid;
  return `<button type="button" class="inline-btn ghost pm-board-task-md-btn" data-pm-task-md="${escapeHtmlAttr(cid)}"
    title="この${PM_WORD.theme}の課題のタスクMDをいますぐまとめて生成します（既存のMDも上書き。毎朝の自動生成と同じ形式で develop の tasks/ に出力）"${busy ? " disabled aria-busy=\"true\"" : ""}>${busy ? "依頼中…" : "タスクMD"}</button>`;
}

document.addEventListener("click", (e) => {
  const btn = e.target instanceof Element ? e.target.closest("[data-pm-task-md]") : null;
  if (!(btn instanceof HTMLElement) || uiState.progressTaskMdRequesting) return;
  const challengeId = Number(btn.dataset.pmTaskMd);
  if (!Number.isInteger(challengeId) || challengeId < 1) return;
  void (async () => {
    uiState.progressTaskMdRequesting = String(challengeId);
    render();
    try {
      const { ok, data } = await apiFetch("/api/task-md", { method: "POST", body: { challengeId } });
      if (!ok) {
        showToastError(typeof data.error === "string" ? data.error : "タスクMDの生成を開始できませんでした");
        return;
      }
      showToast("タスクMDの生成を開始しました（数分で develop の tasks/ に反映されます）");
    } finally {
      uiState.progressTaskMdRequesting = "";
      render();
    }
  })();
});
// ────────────────────────────────────────────────────────────────
// 課題ボード: 完了した課題から請求用の案件を作る（課題詳細の「請求書を作る」）
//  - 課題詳細の右上にボタン／「請求書の案件を開く」／作れない理由つきの無効ボタンを出す（progressIssueDetailHeadHtml から呼ぶ）
//  - ボタンでモーダルを開き、同じクライアント・同じ完了月の未作成の完了課題を候補に並べる
//    （候補とそれぞれに関わった人はサーバー api/_progressInvoice.js が返す）。1件ずつ／まとめて1件を選べる
//  - 案件名: 「261102_ルジェンテ中野_web広告用バナー制作_依頼」→「ルジェンテ中野　web広告用バナー制作_11月分」
//  - 作成は既存の POST /api/projects（権限・月次ロック・履歴はそちら）→ 明細（課題名を1行ずつ）→
//    課題へ作成済みの印（二重作成を防ぐ）の順
// モーダルは 15d-recurring-billing.js と同じく render() の描画サイクルに乗らず自前のDOMで完結させる（fp-mts-* を流用）。
// ────────────────────────────────────────────────────────────────

let todoInvoiceState = null;
let todoInvoiceOverlayEl = null;

/** 課題名 → 案件名の元（先頭の日付6桁と末尾の「_依頼」を外し、最初の「_」を全角スペースに） */
function todoInvoiceBaseName(title) {
  return String(title || "").trim().replace(/^\d{6}_/, "").replace(/_依頼$/, "").replace("_", "　");
}

function todoInvoiceClient(clientDbId) {
  const n = Number(clientDbId);
  if (!Number.isFinite(n) || n < 1) return null;
  return clients.find((c) => c.dbId != null && Number(c.dbId) === n) || null;
}

function todoInvoiceUserName(uid) {
  return users.find((u) => String(u.id) === String(uid))?.name || "?";
}

/** 課題詳細の右上に置くボタン（作る / 作成済みの案件を開く / 作れない理由つきの無効ボタン）。完了した課題だけ。
 *  一覧のカードに置くと幅を取るため詳細に置く（アーカイブ済みの課題も詳細から使える） */
function progressTodoInvoiceButtonHtml(a) {
  if (String(a?.status || "") !== "完了" || !canCreateProject(currentUser)) return "";
  const cls = "inline-btn ghost pm-issue-status-change-btn pm-issue-invoice-btn";
  if (a.invoiceProjectId != null) {
    const p = projects.find((x) => Number(x.dbId) === Number(a.invoiceProjectId));
    const title = p ? `作成済み：${p.name}` : "作成済みの案件を開く";
    return `<a class="${cls} pm-issue-invoice-btn--done" href="#project-detail:p${Number(a.invoiceProjectId)}" title="${escapeHtmlAttr(title)}">請求書の案件を開く</a>`;
  }
  const reason =
    a.clientId == null ? "クライアント未設定のため作れません"
    : !todoInvoiceClient(a.clientId) ? "クライアントが見つからないため作れません"
    : "";
  // disabled にするとマウスを乗せても説明が出ない・押しても無反応になるため、押せない見た目にして押したら理由を出す
  if (reason) {
    return `<button type="button" class="${cls} is-disabled" aria-disabled="true" data-todo-invoice-why="${escapeHtmlAttr(reason)}" title="${escapeHtmlAttr(reason)}">請求書を作る</button>`;
  }
  return `<button type="button" class="${cls}" data-todo-invoice-open="${escapeHtmlAttr(String(a.id))}" title="この課題から請求用の案件を作ります">請求書を作る</button>`;
}

// ── 状態から作る値 ──

function todoInvoiceMonthLabel(st) {
  return `${Number(String(st.billingMonth).slice(5, 7)) || ""}月分`;
}

function todoInvoiceItemName(st, it) {
  return it.nameEdited ? it.name : `${todoInvoiceBaseName(it.title)}_${todoInvoiceMonthLabel(st)}`;
}

function todoInvoiceBundleName(st) {
  return st.bundle.nameEdited ? st.bundle.name : `${st.clientName}_${todoInvoiceMonthLabel(st)}`;
}

function todoInvoiceBundleMembers(st) {
  const fromItems = st.items.filter((it) => it.include).flatMap((it) => it.members);
  return [...new Set([...fromItems, ...st.bundle.added])].filter((uid) => !st.bundle.removed.includes(uid));
}

/** 作る案件の一覧 [{ name, categories, members, agendas }]。まとめて1件なら1つ */
function todoInvoiceGroups(st) {
  const included = st.items.filter((it) => it.include);
  if (st.mode === "bundle") {
    return [{ name: todoInvoiceBundleName(st).trim(), categories: st.bundle.categories, members: todoInvoiceBundleMembers(st), agendas: included }];
  }
  return included.map((it) => ({ name: todoInvoiceItemName(st, it).trim(), categories: it.categories, members: it.members, agendas: [it] }));
}

function todoInvoiceGroupProblem(g) {
  if (!g.name) return "案件名がありません";
  if (!g.categories.length) return "カテゴリが選ばれていません";
  if (!g.members.length) return "担当者がいません";
  return "";
}

// ── 描画 ──

function todoInvoiceCategoriesHtml(selected, key) {
  return PROJECT_WORK_CATEGORY_OPTIONS.map(
    (c) => `<label class="ti-inv-cat"><input type="checkbox" data-ti-cat="${escapeHtmlAttr(key)}" value="${escapeHtmlAttr(c)}"${selected.includes(c) ? " checked" : ""} /> ${escapeHtml(c)}</label>`
  ).join("");
}

function todoInvoiceMembersHtml(members, key) {
  const chips = members
    .map(
      (uid) => `<span class="ti-inv-chip">${escapeHtml(todoInvoiceUserName(uid))}<button type="button" data-ti-member-rm="${escapeHtmlAttr(key)}" data-ti-uid="${escapeHtmlAttr(uid)}" aria-label="${escapeHtmlAttr(todoInvoiceUserName(uid))}を外す">×</button></span>`
    )
    .join("");
  const addable = workCategoryCandidateUsers().filter((u) => !members.includes(String(u.id)));
  const add = `<select class="ti-inv-add" data-search-dropdown-off="1" data-ti-member-add="${escapeHtmlAttr(key)}" aria-label="担当者を追加">
      <option value="">＋ 追加</option>${addable.map((u) => `<option value="${escapeHtmlAttr(String(u.id))}">${escapeHtml(u.name || "")}</option>`).join("")}
    </select>`;
  return `<div class="ti-inv-members">${chips}${add}</div>`;
}

function todoInvoiceEditorHtml(name, nameKey, categories, members, key) {
  return `<div class="ti-inv-editor">
      <input type="text" class="fp-mts-input" data-ti-name="${escapeHtmlAttr(nameKey)}" value="${escapeHtmlAttr(name)}" aria-label="案件名" />
      <div class="ti-inv-cats">${todoInvoiceCategoriesHtml(categories, key)}</div>
      ${todoInvoiceMembersHtml(members, key)}
    </div>`;
}

function todoInvoiceIncludeHtml(it) {
  return `<input type="checkbox" data-ti-include="${it.id}"${it.include ? " checked" : ""}${it.fixed ? " disabled" : ""} aria-label="作成の対象にする" />`;
}

function todoInvoiceListHtml(st) {
  if (st.mode === "bundle") {
    const g = todoInvoiceGroups(st)[0];
    return `<div class="ti-inv-bundle">
        <ul class="ti-inv-bundle-list">${st.items
          .map((it) => `<li class="${it.include ? "" : "is-off"}"><label>${todoInvoiceIncludeHtml(it)} ${escapeHtml(it.title)}</label></li>`)
          .join("")}</ul>
        ${todoInvoiceEditorHtml(todoInvoiceBundleName(st), "bundle", g.categories, g.members, "bundle")}
        <p class="ti-inv-note">明細には課題ごとの名前を1行ずつ入れます。担当者は対象の課題に関わった人を合わせています。</p>
      </div>`;
  }
  const head = `<div class="ti-inv-row ti-inv-row--head"><span></span><span>案件名</span><span>カテゴリ</span><span>担当者</span><span></span></div>`;
  const rows = st.items
    .map((it) => {
      const name = todoInvoiceItemName(st, it);
      const g = { name: name.trim(), categories: it.categories, members: it.members };
      const ng = it.include && todoInvoiceGroupProblem(g);
      const who = it.members.length
        ? `${escapeHtml(todoInvoiceUserName(it.members[0]))}${it.members.length > 1 ? ` 他${it.members.length - 1}` : ""}`
        : `<span class="ti-inv-ng">なし</span>`;
      const cats = it.categories.length ? escapeHtml(it.categories.join("・")) : `<span class="ti-inv-ng">未選択</span>`;
      const row = `<div class="ti-inv-row${it.include ? "" : " is-off"}${ng ? " is-ng" : ""}" data-ti-toggle="${it.id}" title="${escapeHtmlAttr(it.title)}">
          ${todoInvoiceIncludeHtml(it)}
          <span class="ti-inv-ell">${escapeHtml(name)}</span>
          <span class="ti-inv-ell">${cats}</span>
          <span class="ti-inv-ell">${who}</span>
          <span class="ti-inv-chev" aria-hidden="true">${it.open ? "▴" : "▾"}</span>
        </div>`;
      return row + (it.open && it.include ? todoInvoiceEditorHtml(name, String(it.id), it.categories, it.members, String(it.id)) : "");
    })
    .join("");
  return `${head}<div class="ti-inv-rows">${rows}</div>`;
}

function renderTodoInvoiceOverlay() {
  const st = todoInvoiceState;
  if (!todoInvoiceOverlayEl || !st) return;
  let body;
  if (st.loading) body = `<p class="fp-mts-empty ti-inv-pad">読み込み中…</p>`;
  else if (st.error) body = `<p class="fp-mts-empty ti-inv-pad">${escapeHtml(st.error)}</p>`;
  else {
    const groups = todoInvoiceGroups(st);
    const ngCount = groups.filter((g) => todoInvoiceGroupProblem(g)).length;
    const disabled = st.saving ? " disabled" : "";
    const modeSeg =
      st.items.length > 1
        ? `<span class="ti-inv-seg" role="group" aria-label="作り方">
            <button type="button" data-ti-mode="each" class="${st.mode === "each" ? "is-on" : ""}">1件ずつ</button>
            <button type="button" data-ti-mode="bundle" class="${st.mode === "bundle" ? "is-on" : ""}">まとめて1件</button>
          </span>`
        : "";
    const contractWarn = st.contractOk
      ? ""
      : `<p class="ti-inv-warn">契約締結していないクライアントです。必ず締結の上、請求してください。</p>`;
    body = `<div class="ti-inv-meta">
        <span class="ti-inv-client">${escapeHtml(st.clientName)}</span>
        <label class="ti-inv-month">請求月 <input type="month" class="fp-mts-input" data-ti-month value="${escapeHtmlAttr(st.billingMonth)}"${disabled} /></label>
        ${modeSeg}
      </div>
      ${contractWarn}
      <div class="ti-inv-list">${todoInvoiceListHtml(st)}</div>
      <div class="ti-inv-foot">
        <span class="ti-inv-sum">${groups.length}件作成${ngCount ? ` · <span class="ti-inv-ng">要確認 ${ngCount}件</span>` : ""}<br /><small>担当営業：${escapeHtml(currentUser?.name || "")}（作成した人）</small></span>
        <span class="ti-inv-foot-actions">
          <button type="button" class="fp-mts-del ti-inv-cancel" data-ti-close="1"${disabled}>キャンセル</button>
          <button type="button" class="fp-mts-save" data-ti-create="1"${disabled || !groups.length ? " disabled" : ""}>${st.saving ? "作成中…" : "作成する"}</button>
        </span>
      </div>`;
  }
  todoInvoiceOverlayEl.innerHTML = `
    <div class="fp-mts-backdrop" data-ti-close="1"></div>
    <div class="fp-mts-panel ti-inv-panel">
      <div class="fp-mts-head">
        <div><p class="fp-mts-eyebrow">Invoice</p><h2>請求書（案件）を作る</h2></div>
        <button type="button" class="fp-mts-close" data-ti-close="1" aria-label="閉じる">×</button>
      </div>
      ${body}
    </div>`;
}

// ── 開く・閉じる ──

async function openTodoInvoiceModal(agendaId) {
  if (!todoInvoiceOverlayEl) {
    todoInvoiceOverlayEl = document.createElement("div");
    todoInvoiceOverlayEl.className = "fp-mts-overlay";
    todoInvoiceOverlayEl.setAttribute("role", "dialog");
    todoInvoiceOverlayEl.setAttribute("aria-modal", "true");
    todoInvoiceOverlayEl.setAttribute("aria-label", "請求書（案件）を作る");
    todoInvoiceOverlayEl.hidden = true;
    document.body.appendChild(todoInvoiceOverlayEl);
  }
  todoInvoiceState = { loading: true, saving: false, error: "", agendaId: Number(agendaId) };
  renderTodoInvoiceOverlay();
  todoInvoiceOverlayEl.hidden = false;
  requestAnimationFrame(() => todoInvoiceOverlayEl && todoInvoiceOverlayEl.classList.add("is-open"));
  document.body.classList.add("fp-mts-lock-scroll");

  const { ok, data } = await apiFetch(`/api/progress-management?scope=invoiceCandidates&agendaId=${Number(agendaId)}`);
  const st = todoInvoiceState;
  if (!st || st.agendaId !== Number(agendaId)) return;
  st.loading = false;
  const client = ok ? todoInvoiceClient(data.clientId) : null;
  if (!ok || !Array.isArray(data.agendas) || !data.agendas.length || !client) {
    st.error = (typeof data.error === "string" && data.error) || "候補を読み込めませんでした";
    renderTodoInvoiceOverlay();
    return;
  }
  Object.assign(st, {
    clientDbId: Number(client.dbId),
    clientName: String(client.name || ""),
    // 案件作成モーダルと同じ判定（基本契約が締結済みなら NDA 単独の未締結は警告しない）
    contractOk: client.contractStatus === "済",
    billingMonth: data.completedMonth,
    mode: "each",
    items: data.agendas.map((ag, i) => ({
      id: Number(ag.id),
      title: String(ag.title || ""),
      include: true,
      fixed: i === 0,
      open: false,
      name: "",
      nameEdited: false,
      categories: [PROJECT_WORK_CATEGORY_OPTIONS.includes(ag.category) ? ag.category : "制作"],
      members: (ag.memberIds || []).map((n) => users.find((u) => Number(u.dbId) === Number(n))?.id).filter(Boolean).map(String)
    })),
    bundle: { name: "", nameEdited: false, categories: ["制作"], added: [], removed: [] }
  });
  renderTodoInvoiceOverlay();
}

function closeTodoInvoiceModal() {
  if (!todoInvoiceOverlayEl || todoInvoiceState?.saving) return;
  todoInvoiceState = null;
  todoInvoiceOverlayEl.classList.remove("is-open");
  document.body.classList.remove("fp-mts-lock-scroll");
  window.setTimeout(() => {
    if (todoInvoiceOverlayEl && !todoInvoiceState) todoInvoiceOverlayEl.hidden = true;
  }, 160);
}

// ── 作成 ──

async function createTodoInvoiceProjects() {
  const st = todoInvoiceState;
  if (!st || st.saving || st.loading || st.error) return;
  const groups = todoInvoiceGroups(st);
  if (!groups.length) return;
  const ng = groups.find((g) => todoInvoiceGroupProblem(g));
  if (ng) {
    showToastError(`要確認の案件があります（${todoInvoiceGroupProblem(ng)}）`);
    return;
  }
  // 案件作成モーダル（createProjectFromModal）と同じく、契約未締結のクライアントは確認してから作る
  if (!st.contractOk && !(await fpConfirm({
    title: "契約締結していないクライアントです",
    message: "必ず締結の上、請求してください。",
    confirmLabel: "このまま作成する"
  }))) return;
  if (st.saving || todoInvoiceState !== st) return;
  st.saving = true;
  renderTodoInvoiceOverlay();
  // 開いている間に他の人が同じ課題から作っていないか、作る直前に確かめる（作ってからでは案件が二重に残る）
  const fresh = await apiFetch(`/api/progress-management?scope=invoiceCandidates&agendaId=${st.agendaId}`);
  const stillOpen = new Set((fresh.ok && Array.isArray(fresh.data.agendas) ? fresh.data.agendas : []).map((a) => Number(a.id)));
  if (!groups.every((g) => g.agendas.every((it) => stillOpen.has(it.id)))) {
    st.saving = false;
    renderTodoInvoiceOverlay();
    showToastError(fresh.ok ? "他の人が先に作成した課題があります。開き直してください" : (fresh.data.error || "作成済みか確認できませんでした"));
    return;
  }
  const toDbUserId = (uid) => Number(String(uid || "").replace(/^u/, "")) || null;
  const salesOwnerId = toDbUserId(currentUser.id);
  const billingMonth = st.billingMonth.replace("-", "/");
  const created = [];
  let failure = "";
  for (const g of groups) {
    const res = await apiFetch("/api/projects", {
      method: "POST",
      body: {
        clientId: st.clientDbId,
        name: g.name,
        amount: 0,
        billingMonth,
        currentStage: "invoices",
        nextAction: "請求書を作成してください",
        projectType: "existing-client",
        salesOwnerId,
        accountingOwnerId: toDbUserId(defaultAccountingUserId()),
        assignedUserId: salesOwnerId,
        invoiceStatus: "未作成",
        orderStatus: "見積もり",
        progressStatus: "未着手",
        workCategories: workCategoriesToServerBody({ categories: g.categories, members: g.members })
      }
    });
    const dbId = Number(res.data?.project?.id);
    if (!res.ok || !dbId) {
      failure = `「${g.name}」を作成できませんでした：${res.data?.error || "エラー"}`;
      break;
    }
    created.push(dbId);
    // 明細は課題ごとの名前を1行ずつ（金額は請求書の画面で入れる）
    const billingLines = g.agendas.map((it) => ({
      lineType: "normal", item: todoInvoiceBaseName(it.title), qty: "1", unit: "式", price: "0", amount: "0", assignedUserId: "", assignedUserIds: []
    }));
    const lineRes = await apiFetch("/api/project-billing", { method: "PUT", body: { projectId: dbId, billingLines } });
    const agendaIds = g.agendas.map((it) => it.id);
    const linkRes = await apiFetch("/api/progress-management", {
      method: "POST",
      body: { action: "linkInvoiceProject", projectId: dbId, agendaIds }
    });
    const linkedIds = linkRes.ok && Array.isArray(linkRes.data.linkedIds) ? linkRes.data.linkedIds : [];
    linkedIds.forEach((aid) => {
      const found = findProgressAgendaById(aid);
      if (found) found.agenda.invoiceProjectId = dbId;
    });
    // 記録できた課題が足りない＝直前の確認の後に他の人が作った（この案件は課題と結びつかない）
    const linkOk = linkedIds.length === agendaIds.length;
    if (!lineRes.ok || !linkOk) {
      failure = `「${g.name}」は作成しましたが、${!lineRes.ok ? "明細の保存" : "課題への作成済みの記録（他の人が先に作成した可能性があります）"}に失敗しました。案件をご確認ください`;
      break;
    }
  }
  st.saving = false;
  // 新しい案件を一覧に入れる（作成済みリンクの案件名・案件詳細を開くのに要る）
  if (created.length) await loadDataFromDB({ mode: "heavy", skipLoadingUi: true }).catch(() => {});
  if (failure) {
    showToastError(created.length ? `${created.length}件作成しました。${failure}` : failure);
    if (created.length) closeTodoInvoiceModal();
    else renderTodoInvoiceOverlay();
    render();
    return;
  }
  closeTodoInvoiceModal();
  showToast(`案件を${created.length}件作成しました`);
  if (created.length === 1) location.hash = `#project-detail:p${created[0]}?composeInvoice=1`;
  else render();
}

// ── イベント ──

function todoInvoiceTarget(key) {
  const st = todoInvoiceState;
  if (key === "bundle") return st.bundle;
  return st.items.find((it) => String(it.id) === String(key)) || null;
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const whyBtn = target.closest("[data-todo-invoice-why]");
  if (whyBtn) {
    showToast(whyBtn.getAttribute("data-todo-invoice-why") || "");
    return;
  }
  const openBtn = target.closest("[data-todo-invoice-open]");
  if (openBtn) {
    e.preventDefault();
    e.stopPropagation();
    void openTodoInvoiceModal(openBtn.getAttribute("data-todo-invoice-open"));
    return;
  }
  const st = todoInvoiceState;
  if (!todoInvoiceOverlayEl || todoInvoiceOverlayEl.hidden || !st) return;
  if (target.closest("[data-ti-close]")) return closeTodoInvoiceModal();
  if (st.saving) return;
  if (target.closest("[data-ti-create]")) return void createTodoInvoiceProjects();
  const modeBtn = target.closest("[data-ti-mode]");
  if (modeBtn) {
    st.mode = modeBtn.getAttribute("data-ti-mode") === "bundle" ? "bundle" : "each";
    return renderTodoInvoiceOverlay();
  }
  const rm = target.closest("[data-ti-member-rm]");
  if (rm) {
    const key = rm.getAttribute("data-ti-member-rm");
    const uid = rm.getAttribute("data-ti-uid");
    if (key === "bundle") {
      st.bundle.added = st.bundle.added.filter((x) => x !== uid);
      if (!st.bundle.removed.includes(uid)) st.bundle.removed.push(uid);
    } else {
      const it = todoInvoiceTarget(key);
      if (it) it.members = it.members.filter((x) => x !== uid);
    }
    return renderTodoInvoiceOverlay();
  }
  // 行のクリックで開閉（チェックボックスは除く）
  const row = target.closest("[data-ti-toggle]");
  if (row && !target.closest("input")) {
    const it = todoInvoiceTarget(row.getAttribute("data-ti-toggle"));
    if (it && it.include) {
      it.open = !it.open;
      renderTodoInvoiceOverlay();
    }
  }
});

document.addEventListener("change", (e) => {
  const t = e.target;
  const st = todoInvoiceState;
  if (!(t instanceof HTMLElement) || !st || !todoInvoiceOverlayEl?.contains(t)) return;
  if (t.hasAttribute("data-ti-month")) {
    if (/^\d{4}-\d{2}$/.test(t.value)) st.billingMonth = t.value;
  } else if (t.hasAttribute("data-ti-include")) {
    const it = todoInvoiceTarget(t.getAttribute("data-ti-include"));
    if (it && !it.fixed) it.include = t.checked;
  } else if (t.hasAttribute("data-ti-cat")) {
    const obj = todoInvoiceTarget(t.getAttribute("data-ti-cat"));
    if (obj) {
      const rest = obj.categories.filter((c) => c !== t.value);
      obj.categories = PROJECT_WORK_CATEGORY_OPTIONS.filter((c) => rest.includes(c) || (t.checked && c === t.value));
    }
  } else if (t.hasAttribute("data-ti-member-add")) {
    const key = t.getAttribute("data-ti-member-add");
    const uid = t.value;
    if (!uid) return;
    if (key === "bundle") {
      st.bundle.removed = st.bundle.removed.filter((x) => x !== uid);
      if (!st.bundle.added.includes(uid)) st.bundle.added.push(uid);
    } else {
      const it = todoInvoiceTarget(key);
      if (it && !it.members.includes(uid)) it.members.push(uid);
    }
  } else return;
  renderTodoInvoiceOverlay();
});

// 案件名はモーダルを描き直さず、値と行の表示だけ更新する（描き直すと入力中はカーソルが飛び、
// 入力欄から離れた change で描き直すと、そのまま押した「作成する」等のクリックが消える）
document.addEventListener("input", (e) => {
  const t = e.target;
  const st = todoInvoiceState;
  if (!(t instanceof HTMLInputElement) || !st || !t.hasAttribute("data-ti-name")) return;
  const key = t.getAttribute("data-ti-name");
  const obj = todoInvoiceTarget(key);
  if (!obj) return;
  obj.name = t.value;
  obj.nameEdited = true;
  const label = todoInvoiceOverlayEl.querySelector(`[data-ti-toggle="${CSS.escape(key)}"] .ti-inv-ell`);
  if (label) label.textContent = t.value;
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && todoInvoiceOverlayEl && !todoInvoiceOverlayEl.hidden) closeTodoInvoiceModal();
});
// ────────────────────────────────────────────────────────────────
// 左メニュー「貸与品」（#equipment-admin）：スタッフ1人＝1行で、貸与PC（管理番号＋OS、複数可）・携帯電話・
// ENセキュリティーカード・その他貸与品・備考を管理する。マスター、または権限画面で「貸与品」を付けた人のみ
// （api/user-equipment.js）。
// 一覧は表示のみ。行の「編集」でミニモーダル（部署設定と同じ cl-mini-modal）を開き、「保存」で1人分を PUT する。
// ────────────────────────────────────────────────────────────────

const EQUIP_PHONE_SUGGESTIONS = ["スマホ", "ガラケー", "端末なし（eSIM）"];
const EQUIP_TEXT_FIELDS = [
  { key: "phone", label: "携帯電話", placeholder: "スマホ / ガラケー など", list: "equip-phone-suggestions" },
  { key: "enCard", label: "ENセキュリティーカード", placeholder: "en0000000" },
  { key: "otherItems", label: "その他貸与品", placeholder: "" }
];

async function loadUserEquipment() {
  if (uiState.userEquipment && uiState.userEquipment.loading) return;
  uiState.userEquipment = { loading: true, byUserId: uiState.userEquipment?.byUserId || null };
  const { ok, data } = await apiFetch("/api/user-equipment");
  if (ok && data.ok) {
    const byUserId = {};
    data.items.forEach((it) => { byUserId[it.userId] = it; });
    uiState.userEquipment = { loading: false, byUserId };
  } else {
    // 失敗を「全員貸与品なし（{}）」にしない: その状態で編集→保存すると、1人分を丸ごと上書きする PUT が
    // 空の下書きで既存の PC 管理番号・ENカード等を消していた。読めるまで一覧も編集も出さない
    const error = data.error || "通信エラー";
    showToastError(`貸与品を読み込めませんでした：${error}`);
    uiState.userEquipment = { loading: false, byUserId: null, error };
  }
  if (isUserEquipmentRoute()) render();
}

/** 貸与品を表示している画面か（#equipment-admin） */
function isUserEquipmentRoute() {
  return currentRoute().page === "equipment-admin";
}

/** #equipment-admin（左メニュー「貸与品」）。ページの出入りは userCanAccessPage → canManageUserEquipment で絞る */
function equipmentAdminPage() {
  return `${header("貸与品", "スタッフごとの貸与PC・携帯電話・ENセキュリティーカードなどを管理する")}${userEquipmentSectionHtml()}`;
}

function equipEmpty() {
  return { pcs: [], phone: "", enCard: "", otherItems: "", note: "" };
}

function equipHasData(it) {
  return !!it && (it.pcs.length > 0 || !!(it.phone || it.enCard || it.otherItems || it.note));
}

function equipText(v) {
  return v ? escapeHtml(v) : `<span class="equip-none">—</span>`;
}

function userEquipmentSectionHtml() {
  const st = uiState.userEquipment;
  if (!st || (st.byUserId === null && !st.loading && !st.error)) void loadUserEquipment();
  if (st && st.error) {
    return `<section class="section"><article class="soft-card panel-pad section-card">
      <p class="muted">貸与品を読み込めませんでした（${escapeHtml(st.error)}）。データ保護のため、読み込めるまで編集できません。</p>
      <button type="button" class="inline-btn" data-equip-reload>再読み込み</button>
    </article></section>`;
  }
  if (!st || st.byUserId === null) {
    return `<section class="section"><article class="soft-card panel-pad section-card"><p class="muted">読み込み中…</p></article></section>`;
  }
  // 退職者は貸与品が残っている（未返却の）人だけ出す
  const rows = users
    // 行キーは DB の ID（u.dbId）。u.id は "u12" 形式の画面用 ID で、API の user_id とは一致しない
    .filter((u) => u.dbId && (!u.retired || equipHasData(st.byUserId[u.dbId])))
    .map((u) => {
      const it = st.byUserId[u.dbId] || equipEmpty();
      const pcs = it.pcs.length
        ? it.pcs.map((p) => `<div class="equip-pc-line">${escapeHtml(p.no || "（番号なし）")}${p.os ? `<span class="equip-pc-os-tag">${escapeHtml(p.os)}</span>` : ""}</div>`).join("")
        : equipText("");
      return `
        <tr data-equip-row="${u.dbId}">
          <td><div class="acct-name">${escapeHtml(userNameWithRetiredLabel(u))}</div></td>
          <td data-label="PC"><div>${pcs}</div></td>
          <td data-label="携帯電話">${equipText(it.phone)}</td>
          <td data-label="ENカード">${equipText(it.enCard)}</td>
          <td data-label="その他">${equipText(it.otherItems)}</td>
          <td data-label="備考" class="equip-note-cell">${equipText(it.note)}</td>
          <td><div class="acct-actions"><button type="button" class="cl-btn cl-btn-outline cl-btn-sm" data-equip-edit="${u.dbId}">編集</button></div></td>
        </tr>`;
    }).join("");
  return `
    ${equipFulfillQueueHtml()}
    <section class="section">
      <article class="soft-card panel-pad section-card">
        <div class="section-head-row">
          <h3 class="mini-title">貸与品・PC管理番号</h3>
        </div>
        <div class="equip-table-wrap">
          <table class="acct-table equip-table fp-sp-card-table" data-fp-table="user-equipment">
            <thead>
              <tr>
                <th>氏名</th>
                <th>PC（管理番号・OS）</th>
                <th>携帯電話</th>
                <th>ENセキュリティーカード</th>
                <th>その他貸与品</th>
                <th>備考</th>
                <th></th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </article>
      ${renderUserEquipmentEditor()}
    </section>`;
}

// ── 編集モーダル（uiState.equipEditor = { userId, draft }。入力は draft へ即反映し、保存で PUT） ──
function equipEditorPcRowHtml(pc, idx) {
  const osOptions = ["", ...EQUIPMENT_PC_OS_OPTIONS].map((os) =>
    `<option value="${escapeHtmlAttr(os)}"${pc.os === os ? " selected" : ""}>${os ? escapeHtml(os) : "OS —"}</option>`
  ).join("");
  return `
    <div class="equip-pc" data-equip-ed-pc="${idx}">
      <input type="text" class="cl-form-input equip-pc-no" data-equip-ed-pc-no="${idx}" value="${escapeHtmlAttr(pc.no || "")}" placeholder="OE000" maxlength="200" aria-label="PC管理番号" />
      <select class="cl-form-input equip-pc-os" data-equip-ed-pc-os="${idx}" data-search-dropdown-off="1" aria-label="OS">${osOptions}</select>
      <button type="button" class="equip-pc-remove" data-equip-ed-pc-remove="${idx}" title="このPCを外す" aria-label="このPCを外す">×</button>
    </div>`;
}

function renderUserEquipmentEditor() {
  const ed = uiState.equipEditor;
  const user = ed && users.find((u) => u.dbId === ed.userId);
  if (!user) return "";
  const d = ed.draft;
  const textFields = EQUIP_TEXT_FIELDS.map((f) => `
    <label class="cl-form-group">
      <span class="cl-form-label">${f.label}</span>
      <input type="text" class="cl-form-input" data-equip-ed-field="${f.key}" value="${escapeHtmlAttr(d[f.key] || "")}" placeholder="${escapeHtmlAttr(f.placeholder)}" maxlength="200"${f.list ? ` list="${f.list}"` : ""} />
    </label>`).join("");
  return `
    <div class="cl-mini-modal-overlay open" data-equip-ed-close="1">
      <div class="cl-mini-modal">
        <div class="cl-mini-modal-head">
          <h4>${escapeHtml(user.name)} の貸与品</h4>
          <button class="cl-modal-close" data-equip-ed-close="1" type="button" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          ${ed.fulfillLabel ? `<p class="at-apply-note">申請：${escapeHtml(ed.fulfillLabel)}。保存すると申請は「貸与済み」になります。</p>` : ""}
          <div class="cl-form-group">
            <span class="cl-form-label">PC（管理番号・OS）</span>
            <div class="equip-pcs">${d.pcs.map(equipEditorPcRowHtml).join("")}</div>
            <button type="button" class="chip-btn equip-pc-add" data-equip-ed-pc-add="1">＋ PCを追加</button>
          </div>
          ${textFields}
          <label class="cl-form-group">
            <span class="cl-form-label">備考</span>
            <textarea class="cl-form-input equip-note" data-equip-ed-field="note" rows="3" maxlength="2000">${escapeHtml(d.note || "")}</textarea>
          </label>
          <datalist id="equip-phone-suggestions">${EQUIP_PHONE_SUGGESTIONS.map((s) => `<option value="${escapeHtmlAttr(s)}"></option>`).join("")}</datalist>
        </div>
        <div class="cl-mini-modal-foot">
          <button class="cl-btn cl-btn-outline" data-equip-ed-close="1" type="button">キャンセル</button>
          <button class="cl-btn cl-btn-primary" data-equip-ed-save="1" type="button">保存</button>
        </div>
      </div>
    </div>`;
}

async function saveUserEquipmentEditor() {
  const ed = uiState.equipEditor;
  if (!ed || ed.saving) return;
  ed.saving = true;
  const { ok, data } = await apiFetch("/api/user-equipment", { method: "PUT", body: { ...ed.draft, userId: ed.userId } });
  ed.saving = false;
  if (!ok || !data.ok) {
    showToastError(`貸与品を保存できませんでした：${data.error || "通信エラー"}`);
    return;
  }
  if (uiState.userEquipment?.byUserId) uiState.userEquipment.byUserId[ed.userId] = data.item;
  // 保存中に閉じて別の人を開き直していたら、そちらは閉じない
  if (uiState.equipEditor === ed) uiState.equipEditor = null;
  showToast("貸与品を保存しました");
  // 手配待ちの申請から開いた場合は、その申請を貸与済みにする（15z-equipment-applications.js）
  if (ed.fulfillAppId) await fulfillEquipApplication(ed.fulfillAppId);
}

// 別タブ・別ページへ移ったら編集中の下書きは破棄する（戻ったときに古い下書きのモーダルが再表示されないように）
window.addEventListener("hashchange", () => {
  uiState.equipEditor = null;
});

// 入力は draft へ反映するだけ（再描画しないのでフォーカスが外れない）
document.addEventListener("input", (e) => {
  const el = e.target;
  const d = uiState.equipEditor?.draft;
  if (!d || !(el instanceof HTMLElement)) return;
  if (el.dataset.equipEdField) d[el.dataset.equipEdField] = el.value;
  else if (el.dataset.equipEdPcNo != null) d.pcs[Number(el.dataset.equipEdPcNo)].no = el.value;
});

document.addEventListener("change", (e) => {
  const el = e.target;
  const d = uiState.equipEditor?.draft;
  if (d && el instanceof HTMLSelectElement && el.dataset.equipEdPcOs != null) d.pcs[Number(el.dataset.equipEdPcOs)].os = el.value;
});

document.addEventListener("click", (e) => {
  const target = e.target instanceof Element ? e.target : null;
  if (!target) return;
  if (target.closest("[data-equip-reload]")) {
    uiState.userEquipment = null;
    void loadUserEquipment();
    render();
    return;
  }
  const editBtn = target.closest("[data-equip-edit]");
  if (editBtn instanceof HTMLElement) {
    if (!uiState.userEquipment?.byUserId) {
      showToastError("貸与品を読み込めていないため編集できません。再読み込みしてください");
      return;
    }
    const userId = Number(editBtn.dataset.equipEdit);
    const it = uiState.userEquipment?.byUserId?.[userId] || equipEmpty();
    uiState.equipEditor = {
      userId,
      draft: { ...equipEmpty(), ...it, pcs: it.pcs.length ? it.pcs.map((p) => ({ ...p })) : [{ no: "", os: "" }] }
    };
    render();
    return;
  }
  const d = uiState.equipEditor?.draft;
  if (!d) return;
  if (target.closest("[data-equip-ed-pc-add]")) {
    d.pcs.push({ no: "", os: "" });
    render();
    // 追加した行の番号欄へフォーカス（render は次フレームで描画される）
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.querySelector(`[data-equip-ed-pc-no="${d.pcs.length - 1}"]`)?.focus();
    }));
    return;
  }
  const removeBtn = target.closest("[data-equip-ed-pc-remove]");
  if (removeBtn instanceof HTMLElement) {
    d.pcs.splice(Number(removeBtn.dataset.equipEdPcRemove), 1);
    render();
    return;
  }
  if (target.closest("[data-equip-ed-save]")) {
    void saveUserEquipmentEditor();
    return;
  }
  const closeEl = target.closest("[data-equip-ed-close]");
  if (closeEl) {
    // オーバーレイ自体のクリックだけで閉じる（モーダル内のクリックは無視）
    if (closeEl.classList.contains("cl-mini-modal-overlay") && target.closest(".cl-mini-modal")) return;
    uiState.equipEditor = null;
    render();
  }
});
/** 描画中に renderBeforePaint() が呼ばれた（描画が終わったら次のフレームを待たずに描き直す） */
let renderAgainBeforePaint = false;

function render() {
  if (renderRunning) {
    renderScheduled = true;
    return;
  }
  if (renderScheduled) return;
  renderScheduled = true;
  window.requestAnimationFrame(renderFrame);
}

/** renderFrame が記録した内側スクロール位置を、作り直した同じ枠（セレクタと出現順で対応付け）へ戻す */
function restoreScrollSnapshots(snapshots) {
  for (const { sel, idx, top } of snapshots) {
    const el = document.querySelectorAll(sel)[idx];
    if (el) el.scrollTop = top;
  }
}

/** 1フレーム分の描画（render() が requestAnimationFrame で呼ぶ） */
function renderFrame() {
  renderScheduled = false;
  // 描画ループのサーキットブレーカ:
  // 短時間に過剰な再描画が続いたら一旦停止して原因ページを警告（タブのフリーズを防ぐ）。
  // 通常操作では到達しない高めの閾値（1秒に60回超）。
  const __now = Date.now();
  if (!Array.isArray(window.__fpRenderTimes)) window.__fpRenderTimes = [];
  window.__fpRenderTimes.push(__now);
  if (window.__fpRenderTimes.length > 120) window.__fpRenderTimes = window.__fpRenderTimes.slice(-120);
  const __recent = window.__fpRenderTimes.filter((t) => __now - t < 1000).length;
  if (__recent > 60) {
    console.error(
      `[fp] 描画ループを検知して停止しました。route=${currentRoute().page}（直近1秒で${__recent}回の再描画）。` +
      `この route 名を開発者に伝えてください。`
    );
    window.__fpRenderTimes = [];
    renderScheduled = false;
    return; // 自動再実行を止めてフリーズを回避
  }
  renderRunning = true;
  // スクロール位置を保持（renderNow 前に要素と位置を記録）
  // querySelectorAll でモーダル内の .composer-body も全件取得し、インデックスで対応付けて復元
  const scrollSnapshots = [];
  // ページ自体（window）のスクロール位置も保持する。書類作成・編集の「＋摘要を入力して行を追加」など、
  // 入力のたびに再描画する画面で innerHTML 差し替えの瞬間に文書が短くなり、ブラウザが先頭へ丸めてしまう
  // （＝入力するたびに最上部へ戻される）のを防ぐ。画面遷移時の先頭リセットは hashchange 側（二重 rAF）が
  // この復元より後に走るので従来どおり効く
  const windowScrollY = window.scrollY || document.documentElement.scrollTop || 0;
  // .doc-create-preview-slot: 書類作成・編集のプレビュー枠（内側でスクロールする）。摘要の追加など入力のたびの
  // 再描画で枠ごと作り直されると中のスクロールが 0 に戻り「入力すると最上部へ戻される」ため復元対象に含める
  // [data-pj-filter-dd] > div: 案件一覧の複数選択プルダウンのパネル（開いたままチェックを続けるので位置を保つ）
  for (const sel of [".composer-body", ".main-surface", ".cost-reg-preview-table-wrap", ".doc-create-preview-slot", "[data-pj-filter-dd] > div"]) {
    const els = [...document.querySelectorAll(sel)];
    for (let i = 0; i < els.length; i++) {
      if (els[i].scrollTop > 0) scrollSnapshots.push({ sel, idx: i, top: els[i].scrollTop });
    }
  }
  try {
    markSuppressDrawerAnimationIfOpen();
    renderNow();
    if (windowScrollY > 0) window.scrollTo(0, windowScrollY);
    // 作り直した枠は scrollTop が 0 なので、描画したこのフレームのうちに戻す。rAF の復元だけだと
    // 1フレームだけ枠の先頭が映り、書類明細の＋で行を足すたびに画面が「ちかっ」とちらついた
    restoreScrollSnapshots(scrollSnapshots);
    // レイアウト確定後に復元（enhanceSearchableSelects のフォーカス移動より後）
    if (scrollSnapshots.length || windowScrollY > 0) {
      requestAnimationFrame(() => {
        if (windowScrollY > 0 && Math.abs((window.scrollY || 0) - windowScrollY) > 1) window.scrollTo(0, windowScrollY);
        restoreScrollSnapshots(scrollSnapshots);
      });
    }
    bindSearchableSelectGlobalHandlersOnce();
    bindProgressMentionGlobalListenersOnce();
    bindAppMgmtMemoBlurListenersOnce();
    enhanceSearchableSelects(
      currentRoute().page === "progress-management"
        ? appRoot?.querySelector(".pm-page") || appRoot
        : appRoot
    );
    enhanceMentionTextareaOverlays(appRoot);
    // メンション装飾が textarea を包み直した後でないとフォーカスが外れるため、ここで戻す（15v-compose-draft.js）
    restorePmComposeDraftFocus();
    if (fpModalLayer && _modalLayerUpdated) {
      enhanceSearchableSelects(fpModalLayer);
      enhanceMentionTextareaOverlays(fpModalLayer);
      _modalLayerUpdated = false;
    }
    syncOnBoardingLoadingCatAfterRender();
  } catch (err) {
    console.warn("renderNow failed:", err instanceof Error ? err.message : String(err));
    if (appRoot) {
      appRoot.innerHTML = `<div class="fp-boot-error" style="padding:24px;font-family:sans-serif"><p style="font-weight:700">画面の表示に失敗しました</p><p style="color:#64748b;font-size:13px">${escapeHtml(err instanceof Error ? err.message : String(err))}</p></div>`;
    }
  } finally {
    renderRunning = false;
    // 診断（opt-in）: localStorage.fpRenderDebug='1' のときだけ、描画回数とDOMノード数・
    // ヒープを出力。描画ループ（回数が暴走）かDOM/ヒープ肥大（リーク）かを切り分ける用。
    // 通常は無効＝本番影響なし。
    try {
      if (window.localStorage && localStorage.getItem("fpRenderDebug") === "1") {
        window.__fpRenderCount = (window.__fpRenderCount || 0) + 1;
        const dom = document.getElementsByTagName("*").length;
        const heap = performance && performance.memory
          ? Math.round(performance.memory.usedJSHeapSize / 1048576) + "MB"
          : "n/a";
        console.log(`[fpRender #${window.__fpRenderCount}] route=${currentRoute().page} DOM=${dom} heap=${heap}`);
      }
    } catch (_) { /* noop */ }
    // 描画中に renderBeforePaint() で頼まれた描き直しは、画面に出る前に同じフレームで行う
    if (renderAgainBeforePaint) {
      renderAgainBeforePaint = false;
      renderScheduled = false;
      renderFrame();
      return;
    }
    // 描画中に再入で予約された分をフラッシュ。先に false に戻さないと、再帰した
    // render() が冒頭の `if (renderScheduled) return;` で弾かれ、rAF 未予約のまま
    // renderScheduled=true が残って以後の描画が完全停止する（遷移できなくなる）。
    if (renderScheduled) {
      renderScheduled = false;
      render();
    }
  }
}

/** 画面に出る前に描き直す（描画中なら、その描画が終わった直後に同じフレームで）。
 *  render() は次のフレームまで待つので、その間に古い描画が1フレーム見えてちらつく
 *  （例: 一覧の保存済みの並び順を復元すると、既定の並びが一瞬見えてから並び替わっていた。2026-10）。
 *  呼ぶ側は1回きりの描き直しに限ること（毎回呼ぶと描画が終わらない） */
function renderBeforePaint() {
  if (renderRunning) {
    renderAgainBeforePaint = true;
    return;
  }
  renderFrame();
}

document.addEventListener("dragstart", (e) => {
  const h = e.target instanceof Element ? e.target.closest("[data-detail-line-drag]") : null;
  if (!(h instanceof HTMLElement)) return;
  // 案件詳細の「項目」表と、書類作成・編集（project-docs）の明細行ツールバー ≡ の両方で使う
  const page = currentRoute().page;
  if (page !== "project-detail" && page !== "project-docs") return;
  const pid = h.dataset.detailProject || "";
  const from = Number(h.dataset.detailLineDrag);
  if (!pid || Number.isNaN(from)) return;
  uiState.detailLineDrag = { pid, fromIndex: from };
  try {
    e.dataTransfer.setData("text/plain", `${pid}:${from}`);
    e.dataTransfer.effectAllowed = "move";
  } catch {
    /* ignore */
  }
  h.classList.add("is-dragging");
});

document.addEventListener("dragend", (e) => {
  const h = e.target instanceof Element ? e.target.closest("[data-detail-line-drag]") : null;
  if (h instanceof HTMLElement) h.classList.remove("is-dragging");
  document.querySelectorAll(".doc-detail-line-row.is-detail-drop-target").forEach((el) => {
    el.classList.remove("is-detail-drop-target");
  });
  uiState.detailLineDrag = null;
});

document.addEventListener("dragover", (e) => {
  if (!uiState.detailLineDrag) return;
  const tr = e.target instanceof Element ? e.target.closest("tr[data-detail-line-drop]") : null;
  if (!(tr instanceof HTMLElement)) return;
  if (tr.dataset.detailProject !== uiState.detailLineDrag.pid) return;
  e.preventDefault();
  try {
    e.dataTransfer.dropEffect = "move";
  } catch {
    /* ignore */
  }
  document.querySelectorAll(".doc-detail-line-row.is-detail-drop-target").forEach((el) => {
    el.classList.remove("is-detail-drop-target");
  });
  tr.classList.add("is-detail-drop-target");
});

document.addEventListener("drop", (e) => {
  const tr = e.target instanceof Element ? e.target.closest("tr[data-detail-line-drop]") : null;
  if (!(tr instanceof HTMLElement) || !uiState.detailLineDrag) return;
  const drag = uiState.detailLineDrag;
  const pid = tr.dataset.detailProject || "";
  const to = Number(tr.dataset.detailLineDrop);
  if (pid !== drag.pid || Number.isNaN(to)) return;
  e.preventDefault();
  ensureProjectDocDraft(pid);
  const lines = uiState.projectDocsDrafts[pid]?.lines;
  if (!lines) return;
  moveProjectDetailLine(lines, drag.fromIndex, to);
  markProjectDocsDraftDirty(pid);
  persistAllLineStaffForProject(pid);
  uiState.detailLineDrag = null;
  tr.classList.remove("is-detail-drop-target");
  render();
});

/** ToDo 課題詳細: 添付・コメントへのファイルドラッグ＆ドロップ */
let pmFileDropHoverEl = null;
document.addEventListener("dragover", (e) => {
  if (currentRoute().page !== "progress-management") return;
  const z = e.target instanceof Element ? e.target.closest("[data-pm-file-drop]") : null;
  if (!(z instanceof HTMLElement)) {
    if (pmFileDropHoverEl) {
      pmFileDropHoverEl.classList.remove("pm-file-drop-hover");
      pmFileDropHoverEl = null;
    }
    return;
  }
  e.preventDefault();
  try {
    e.dataTransfer.dropEffect = "copy";
  } catch {
    /* noop */
  }
  if (pmFileDropHoverEl && pmFileDropHoverEl !== z) {
    pmFileDropHoverEl.classList.remove("pm-file-drop-hover");
  }
  pmFileDropHoverEl = z;
  z.classList.add("pm-file-drop-hover");
});

document.addEventListener("drop", (e) => {
  if (currentRoute().page !== "progress-management") return;
  const z = e.target instanceof Element ? e.target.closest("[data-pm-file-drop]") : null;
  if (!(z instanceof HTMLElement)) return;
  const spec = String(z.dataset.pmFileDrop || "").trim();
  if (!spec) return;
  e.preventDefault();
  e.stopPropagation();
  z.classList.remove("pm-file-drop-hover");
  pmFileDropHoverEl = null;
  const files = e.dataTransfer?.files;
  if (!files || !files.length) return;
  void progressApplyPmFileDrop(spec, files);
});

/**
 * TODO課題: Ctrl+V（Windows）/ Cmd+V（Mac）での画像ペースト
 * - 課題追加・編集フォームが開いている → 添付ファイルに追加
 * - 課題詳細閲覧中 → コンポーズ（コメント）エリアに追加
 * - スクリーンショットや画像のコピーに対応（clipboardData.items から取得）
 */
document.addEventListener("paste", (e) => {
  if (currentRoute().page !== "progress-management") return;
  const cd = e.clipboardData;
  if (!cd) return;

  // clipboard の items から画像ファイルを収集（スクリーンショット / ファイルコピー 両対応）
  // サポート形式（PNG/JPEG/GIF/WebP）のみ受け付ける。
  // image/heic 等の未対応形式は PNG に偽装せずスキップする（表示壊れ防止）
  const extMap = { "image/png": "png", "image/jpeg": "jpg", "image/jpg": "jpg", "image/gif": "gif", "image/webp": "webp" };
  const imageFiles = [];
  if (cd.items) {
    for (let i = 0; i < cd.items.length; i++) {
      const item = cd.items[i];
      if (item.kind !== "file") continue;
      if (!item.type.startsWith("image/")) continue;
      const ext = extMap[item.type];
      if (!ext) continue; // image/heic 等の非対応形式はスキップ（iOS カメラ写真はクリップボード経由で通常 JPEG に変換される）
      const raw = item.getAsFile();
      if (!raw) continue;
      // ペースト画像はファイル名が空になる場合があるため、拡張子付きの名前を確実に設定
      const named = new File([raw], `paste-${Date.now()}-${i}.${ext}`, { type: item.type });
      imageFiles.push(named);
    }
  }
  // cd.items が未対応の場合の fallback（古い Android 等）
  if (!imageFiles.length && cd.files && cd.files.length) {
    for (let i = 0; i < cd.files.length; i++) {
      const f = cd.files[i];
      if (!f || !f.type.startsWith("image/")) continue;
      const ext = extMap[f.type];
      if (!ext) continue;
      const named = f.name && f.name.includes(".") ? f : new File([f], `paste-${Date.now()}-${i}.${ext}`, { type: f.type });
      imageFiles.push(named);
    }
  }

  if (!imageFiles.length) return;
  // 画像がある場合のみデフォルト貼り付けを抑制（テキストペーストは通常通り動作）
  e.preventDefault();

  const route = currentRoute();
  const issueParam = String(route.searchParams?.get("issue") || "").trim();

  // 優先順位1: 課題追加・編集フォームが開いている → 添付ファイルに追加
  if (uiState.progressShowIssueAddForm) {
    progressAddValidFilesToAgendaModal(imageFiles);
    showToast("画像を添付しました（Ctrl+V / Cmd+V）");
    return;
  }

  // 優先順位2: 課題詳細表示中（コンポーズエリアに追加）
  // render() を避けるため showToastDirect を使用（showToast は render() を呼ぶためテキストが消える）
  if (issueParam) {
    progressAddValidFilesToCompose(issueParam, imageFiles);
    showToastDirect("画像をコメントエリアに追加しました（Ctrl+V / Cmd+V）");
    return;
  }

  showToastDirect("課題を開いてから画像を貼り付けてください");
});

/** テーマ設定モーダル: タブはキャプチャで処理（バブル側のハンドラより先に確実に反応） */
document.addEventListener(
  "click",
  (e) => {
    const tab =
      e.target instanceof Element
        ? e.target.closest("[data-pm-cset-tab], [data-pm-challenge-settings-tab]")
        : null;
    if (!(tab instanceof HTMLElement)) return;
    if (!tab.closest(".pm-challenge-settings-modal")) return;
    if (currentRoute().page !== "progress-management") return;
    const d = uiState.progressChallengeSettingsDraft;
    if (!String(d?.challengeId || "").trim()) return;
    const raw = String(
      tab.getAttribute("data-pm-cset-tab") || tab.getAttribute("data-pm-challenge-settings-tab") || "categories"
    ).trim();
    const nextTab = PM_CHALLENGE_SETTINGS_TABS.includes(raw) ? raw : "categories";
    if (d.tab === nextTab) return;
    e.preventDefault();
    e.stopPropagation();
    d.tab = nextTab;
    render();
  },
  true
);

/** テーマ設定モーダル：カテゴリ／状態リストのドラッグ並べ替え */
const pmChallengeSettingsSortDragState = { active: false, from: -1, key: "" };
function pmChallengeSettingsSortClearVisual() {
  document
    .querySelectorAll(
      ".pm-challenge-settings-list-row.pm-challenge-settings-list-row--dragging, .pm-challenge-settings-list-row.is-pm-sort-over"
    )
    .forEach((el) => {
      el.classList.remove("pm-challenge-settings-list-row--dragging", "is-pm-sort-over");
    });
}
document.addEventListener("dragstart", (e) => {
  const h =
    e.target instanceof Element ? e.target.closest("[data-pm-challenge-settings-drag-handle]") : null;
  if (!h || !(h instanceof HTMLElement)) return;
  const modal = document.querySelector(".pm-challenge-settings-modal");
  if (!modal || !modal.contains(h)) return;
  if (uiState.progressChallengeSettingsSubmitting) return;
  const row = h.closest("[data-pm-challenge-settings-sort-row]");
  if (!row || !(row instanceof HTMLElement)) return;
  const key = String(row.getAttribute("data-pm-challenge-settings-sort-row") || "");
  if (key !== "categories" && key !== "statuses") return;
  const from = Number(row.getAttribute("data-pm-sort-index"));
  if (!Number.isFinite(from) || from < 0) return;
  pmChallengeSettingsSortDragState.active = true;
  pmChallengeSettingsSortDragState.from = from;
  pmChallengeSettingsSortDragState.key = key;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", `${key}:${from}`);
    } catch (_) {}
  }
  row.classList.add("pm-challenge-settings-list-row--dragging");
});
document.addEventListener("dragend", (e) => {
  const h =
    e.target instanceof Element ? e.target.closest("[data-pm-challenge-settings-drag-handle]") : null;
  if (!h) return;
  pmChallengeSettingsSortDragState.active = false;
  pmChallengeSettingsSortDragState.from = -1;
  pmChallengeSettingsSortDragState.key = "";
  pmChallengeSettingsSortClearVisual();
});
document.addEventListener("dragover", (e) => {
  if (!pmChallengeSettingsSortDragState.active) return;
  const modal = document.querySelector(".pm-challenge-settings-modal");
  const t = e.target;
  const el = t instanceof Element ? t : null;
  if (!modal || !el || !modal.contains(el)) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
  const row = el.closest("[data-pm-challenge-settings-sort-row]");
  document.querySelectorAll(".pm-challenge-settings-list-row.is-pm-sort-over").forEach((r) => {
    r.classList.remove("is-pm-sort-over");
  });
  if (row) row.classList.add("is-pm-sort-over");
});
document.addEventListener("drop", (e) => {
  if (!pmChallengeSettingsSortDragState.active) return;
  const modal = document.querySelector(".pm-challenge-settings-modal");
  const tEl = e.target instanceof Element ? e.target : null;
  if (!modal || !tEl || !modal.contains(tEl)) return;
  const row = tEl.closest("[data-pm-challenge-settings-sort-row]");
  if (!row) {
    e.preventDefault();
    pmChallengeSettingsSortClearVisual();
    pmChallengeSettingsSortDragState.active = false;
    pmChallengeSettingsSortDragState.from = -1;
    pmChallengeSettingsSortDragState.key = "";
    return;
  }
  const keyAttr = String(row.getAttribute("data-pm-challenge-settings-sort-row") || "");
  if (keyAttr !== pmChallengeSettingsSortDragState.key) {
    e.preventDefault();
    pmChallengeSettingsSortClearVisual();
    pmChallengeSettingsSortDragState.active = false;
    pmChallengeSettingsSortDragState.from = -1;
    pmChallengeSettingsSortDragState.key = "";
    return;
  }
  pmChallengeSettingsSortClearVisual();
  const from = pmChallengeSettingsSortDragState.from;
  const to = Number(row.getAttribute("data-pm-sort-index"));
  pmChallengeSettingsSortDragState.active = false;
  pmChallengeSettingsSortDragState.from = -1;
  pmChallengeSettingsSortDragState.key = "";
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return;
  if (currentRoute().page !== "progress-management") return;
  const d = uiState.progressChallengeSettingsDraft;
  if (!d?.challengeId) return;
  if (keyAttr !== "statuses" && keyAttr !== "categories") return;
  const arr = Array.isArray(d[keyAttr]) ? [...d[keyAttr]].map((s) => String(s ?? "").trim()) : [];
  if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return;
  const [moved] = arr.splice(from, 1);
  arr.splice(to, 0, moved);
  d[keyAttr] = arr;
  render();
});


document.addEventListener("click", async (e) => {
  let target = e.target;
  if (target instanceof Text) target = target.parentElement;
  if (!(target instanceof Element)) return;
  if (handleProjectDetailClick(e, target)) return;
  if (handleApplicationMgmtClick(e, target)) return;
  if (handleProgressFormsClick(e, target)) return;
  if (handleProgressBoardClick(e, target)) return;
  if (handleProgressListClick(e, target)) return;
  if (handleSidebarAdminClick(e, target)) return;
  const budgetPeriodDeleteEl = target.closest("[data-budget-period-delete]");
  if (budgetPeriodDeleteEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const id = budgetPeriodDeleteEl.dataset.budgetPeriodDelete || "";
    const idx = uiState.budgetPeriods.findIndex((p) => p.id === id);
    if (idx < 0) return;
    if (uiState.budgetPeriods.length <= 1) {
      showToast("最低1つの期が必要です");
      return;
    }
    if (!window.confirm("本当に削除しますか？")) return;
    const prevPeriods = uiState.budgetPeriods.map((p) => ({ ...p }));
    const prevSelectedBudgetPeriodId = uiState.selectedBudgetPeriodId;
    const prevRowsByPeriod = uiState.budgetRowsByPeriod && typeof uiState.budgetRowsByPeriod === "object"
      ? JSON.parse(JSON.stringify(uiState.budgetRowsByPeriod))
      : uiState.budgetRowsByPeriod;
    uiState.budgetPeriods.splice(idx, 1);
    if (uiState.selectedBudgetPeriodId === id) {
      uiState.selectedBudgetPeriodId = latestBudgetPeriodIdFromSorted(uiState.budgetPeriods);
    }
    if (uiState.budgetRowsByPeriod && typeof uiState.budgetRowsByPeriod === "object") {
      delete uiState.budgetRowsByPeriod[id];
      saveBudgetDraftStore(uiState.budgetRowsByPeriod);
    }
    const ok = await deleteBudgetPeriod(id);
    if (!ok) {
      uiState.budgetPeriods = prevPeriods;
      uiState.selectedBudgetPeriodId = prevSelectedBudgetPeriodId;
      uiState.budgetRowsByPeriod = prevRowsByPeriod;
      if (uiState.budgetRowsByPeriod && typeof uiState.budgetRowsByPeriod === "object") {
        saveBudgetDraftStore(uiState.budgetRowsByPeriod);
      }
      showToastError("会計期の削除に失敗しました（サーバー反映できませんでした）");
      render();
      return;
    }
    showToast("会計期を削除しました");
    render();
    return;
  }
  if (handleBudgetRowClick(e, target)) return;
  const deptDeleteEl = target.closest("[data-dept-delete]");
  if (deptDeleteEl instanceof HTMLElement && isAccountSettingsRoute()) {
    ensureDeptSettingsState();
    const snapshot = cloneDeptSettingsState();
    const deptId = deptDeleteEl.dataset.deptDelete || "";
    const idx = uiState.deptSettings.departments.findIndex((d) => d.id === deptId);
    const dept = uiState.deptSettings.departments[idx];
    if (dept && !confirm(`部署「${dept.name}」を削除しますか？\n所属メンバー${dept.memberIds.length}名は未所属になり、責任者の設定も消えます。`)) return;
    if (idx >= 0) {
      uiState.deptSettings.departments.splice(idx, 1);
      const persist = await persistDepartments();
      if (!persist.ok) restoreDeptSettingsState(snapshot);
      showToast(persist.ok ? "部署を削除しました" : formatDeptPersistFailureMessage(persist));
    }
    return;
  }
  if (handleDeptEditClick(e, target)) return;
  const deptSaveAddEl = target.closest("[data-dept-save-add]");
  if (deptSaveAddEl instanceof HTMLElement && isAccountSettingsRoute()) {
    ensureDeptSettingsState();
    const snapshot = cloneDeptSettingsState();
    const name = (uiState.deptCreateModal?.name || "").trim();
    if (!name) {
      showToast("部署名を入力してください");
      return;
    }
    const editingDept = uiState.deptCreateModal?.editId
      ? uiState.deptSettings.departments.find((d) => d.id === uiState.deptCreateModal.editId)
      : null;
    if (editingDept) {
      editingDept.name = name;
      const persist = await persistDepartments();
      if (!persist.ok) restoreDeptSettingsState(snapshot);
      showToast(persist.ok ? "部署名を変更しました" : formatDeptPersistFailureMessage(persist));
      uiState.deptCreateModal = null;
      render();
      return;
    }
    const id = `dept-${Date.now()}`;
    const today = new Date();
    const yy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    uiState.deptSettings.departments.push({
      id,
      name,
      createdAt: `${yy}/${mm}/${dd}`,
      memberIds: [],
      leaderMemberId: ""
    });
    uiState.deptSettings.editingDeptId = id;
    const persist = await persistDepartments();
    if (!persist.ok) restoreDeptSettingsState(snapshot);
    showToast(persist.ok ? "部署を追加しました" : formatDeptPersistFailureMessage(persist));
    uiState.deptCreateModal = null;
    render();
    return;
  }
  if (handleDeptAndDomainClick(e, target)) return;
  const domainBulkDeleteEl = target.closest("[data-domain-bulk-delete]");
  if (domainBulkDeleteEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    const selectedIds = Object.entries(uiState.domainList.selected || {}).filter(([, v]) => v === true).map(([id]) => id);
    if (!selectedIds.length) {
      showToast("削除対象を選択してください");
      return;
    }
    if (!window.confirm(`選択した${selectedIds.length}件を削除しますか？`)) return;
    // 楽観的に一覧から外し、サーバ削除に失敗した行は元の位置へ戻す（単体削除と同じ扱い）
    const removed = []; // { origIdx, row }（origIdx 昇順）
    for (let i = domains.length - 1; i >= 0; i -= 1) {
      if (selectedIds.includes(domains[i].id)) {
        removed.unshift({ origIdx: i, row: domains[i] });
        domains.splice(i, 1);
      }
    }
    uiState.domainList.selected = {};
    render();
    const failed = [];
    for (const item of removed) {
      if (!item.row.dbId) continue;
      const res = await apiFetch("/api/domains", { method: "DELETE", body: { id: item.row.dbId } });
      if (!res.ok) failed.push(item);
    }
    const failedCount = failed.length;
    if (failedCount > 0) {
      // 戻す位置 = 元の位置 − それより前で実際に消えた行数
      const failedSet = new Set(failed);
      failed.forEach((item) => {
        const deletedBefore = removed.filter((r) => r.origIdx < item.origIdx && !failedSet.has(r)).length;
        domains.splice(Math.min(item.origIdx - deletedBefore, domains.length), 0, item.row);
      });
      render();
      showToastError(`${selectedIds.length - failedCount}件を削除しました（${failedCount}件はサーバー反映に失敗したため一覧に戻しました）`);
    } else {
      showToast(`${selectedIds.length}件を削除しました`);
    }
    return;
  }
  if (handleMasterDataClick(e, target)) return;
  if (handleUsersAndProjectsClick(e, target)) return;
  if (handleCostClick(e, target)) return;
  if (handleCostEditClick(e, target)) return;
});

let _keywordRenderTimer = null;
const _debouncedRender = () => {
  clearTimeout(_keywordRenderTimer);
  _keywordRenderTimer = setTimeout(() => {
    // IME変換中に予約済みの描画が発火すると、変換途中の文字で絞り込まれ入力欄も作り直される。
    // 確定時は compositionend が input を再発火して改めて予約するので、ここでは捨ててよい
    if (isImeComposingSearch(document.activeElement)) return;
    render();
  }, 200);
};
/** data-ime-search の検索欄が IME 変換中か。InputEvent.isComposing は IME/ブラウザによって
 *  変換中でも false で届くことがあるため、compositionstart〜end で立てるフラグでも判定する */
function isImeComposingSearch(target, e) {
  if (e instanceof InputEvent && e.isComposing) return true;
  return target instanceof HTMLElement && target.dataset.imeComposing === "1";
}

document.addEventListener("input", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (handleProgressInput(e, target)) return;
  if (handleDocCostDomainInput(e, target)) return;
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (handleProgressCostChange(e, target)) return;
  if (handleCsvImportChange(e, target)) return;
  if (handleProjectCostChange(e, target)) return;
});

document.addEventListener("change", (e) => {
  const target = e.target;
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.staffAssignBulkPick &&
    currentRoute().page === "staff-assignment"
  ) {
    const id = String(target.dataset.staffAssignBulkPick || "");
    const arr = uiState.staffAssignmentTable.bulkUserIds || (uiState.staffAssignmentTable.bulkUserIds = []);
    if (target.checked) {
      if (id && !arr.includes(id)) arr.push(id);
    } else {
      uiState.staffAssignmentTable.bulkUserIds = arr.filter((x) => x !== id);
    }
    uiState.staffAssignmentTable.bulkAssignPanelOpen = true;
    render();
    return;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.projectOrderStatusFilter != null &&
    currentRoute().page === "projects"
  ) {
    const key = String(target.dataset.projectOrderStatusFilter || "");
    let arr = [...(uiState.projectListFilters.orderStatuses || [])];
    if (target.checked) {
      if (key && !arr.includes(key)) arr.push(key);
    } else {
      arr = arr.filter((x) => x !== key);
    }
    uiState.projectListFilters.orderStatuses = PROJECT_ORDER_STATUS_LIST.filter((s) => arr.includes(s));
    setProjectListPage(1);
    render();
    return;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.projectTagFilter != null &&
    currentRoute().page === "projects"
  ) {
    const key = String(target.dataset.projectTagFilter || "").trim();
    let arr = [...(uiState.projectListFilters.filterTags || [])];
    if (target.checked) {
      if (key && !arr.includes(key)) arr.push(key);
    } else {
      arr = arr.filter((x) => x !== key);
    }
    uiState.projectListFilters.filterTags = arr;
    setProjectListPage(1);
    render();
    return;
  }
  if (target instanceof HTMLSelectElement && target.dataset.partnerListFilter && currentRoute().page === "partner-list") {
    uiState.partnerList.filters[target.dataset.partnerListFilter] = target.value;
    uiState.partnerList.page = 1;
    render();
    return;
  }
  if (
    target instanceof HTMLInputElement &&
    target.dataset.clientDeliveryPart &&
    uiState.clientCreate
  ) {
    const opt = String(target.dataset.clientDeliveryPart || "");
    const cur = normalizeDeliveryMethodStored(uiState.clientCreate.deliveryMethod || "");
    const parts = new Set(parseDeliveryMethodParts(cur));
    if (target.checked) parts.add(opt);
    else parts.delete(opt);
    uiState.clientCreate.deliveryMethod = CONTRACT_DELIVERY_METHOD_PARTS.filter((p) => parts.has(p)).join("+");
    render();
    return;
  }
  if (
    target instanceof HTMLInputElement &&
    target.dataset.partnerDeliveryPart &&
    uiState.partnerCreate
  ) {
    const opt = String(target.dataset.partnerDeliveryPart || "");
    const cur = normalizeDeliveryMethodStored(uiState.partnerCreate.deliveryMethod || "");
    const parts = new Set(parseDeliveryMethodParts(cur));
    if (target.checked) parts.add(opt);
    else parts.delete(opt);
    uiState.partnerCreate.deliveryMethod = CONTRACT_DELIVERY_METHOD_PARTS.filter((p) => parts.has(p)).join("+");
    render();
    return;
  }
  if (target instanceof HTMLInputElement && (target.dataset.clientIndividual !== undefined || target.dataset.partnerIndividual !== undefined)) {
    // 個人✓＝宛名の敬称を「様」、外すと「御中」。既存の honorific 列にそのまま保存する
    const createState = target.dataset.clientIndividual !== undefined ? uiState.clientCreate : uiState.partnerCreate;
    if (createState) createState.honorific = target.checked ? "様" : "御中";
    return;
  }
  if (
    target instanceof HTMLInputElement &&
    target.dataset.domainServicePart &&
    uiState.domainCreate
  ) {
    const opt = String(target.dataset.domainServicePart || "");
    const parts = new Set(parseDeliveryMethodParts(uiState.domainCreate.services || ""));
    if (target.checked) parts.add(opt);
    else parts.delete(opt);
    uiState.domainCreate.services = DOMAIN_SERVICE_KIND_OPTIONS.filter((p) => parts.has(p)).join("+");
    render();
    return;
  }
  if (!(target instanceof HTMLInputElement)) return;
  if (target.matches("[data-domain-check-all]")) {
    syncDomainListSelectionFromInput(target, true);
    return;
  }
  if (target.matches("[data-domain-row-check]")) {
    syncDomainListSelectionFromInput(target, false);
    return;
  }
  if (target.matches("[data-project-select-all]")) {
    syncProjectListSelectionFromInput(target, true);
    return;
  }
  if (target.matches("[data-project-select]")) {
    syncProjectListSelectionFromInput(target, false);
    return;
  }
  if (target.matches("[data-staff-assign-select-all]")) {
    syncStaffAssignmentSelectionFromInput(target, true);
    return;
  }
  if (target.matches("[data-staff-assign-select]")) {
    syncStaffAssignmentSelectionFromInput(target, false);
    return;
  }
});

document.addEventListener("dragstart", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const card = target.closest("[data-dept-member-card]");
  if (!(card instanceof HTMLElement)) return;
  if (!isAccountSettingsRoute() || uiState.accountSettingsTab !== "dept") return;
  const memberId = card.dataset.deptMemberCard || "";
  if (!memberId) return;
  uiState.deptDragMemberId = memberId;
  card.classList.add("is-dragging");
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", memberId);
  }
});

document.addEventListener("dragend", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const card = target.closest("[data-dept-member-card]");
  if (card instanceof HTMLElement) card.classList.remove("is-dragging");
  uiState.deptDragMemberId = "";
  document.querySelectorAll(".kanban-dropzone.is-drop-target, .dept-member-dropzone.is-drop-target").forEach((el) => {
    el.classList.remove("is-drop-target");
  });
});

document.addEventListener("dragover", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const zone = target.closest("[data-dept-drop-zone]");
  if (!(zone instanceof HTMLElement)) return;
  if (!uiState.deptDragMemberId) return;
  e.preventDefault();
  zone.classList.add("is-drop-target");
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
});

document.addEventListener("dragleave", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const zone = target.closest("[data-dept-drop-zone]");
  if (!(zone instanceof HTMLElement)) return;
  zone.classList.remove("is-drop-target");
});

document.addEventListener("drop", async (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const zone = target.closest("[data-dept-drop-zone]");
  if (!(zone instanceof HTMLElement)) return;
  if (!isAccountSettingsRoute() || uiState.accountSettingsTab !== "dept") return;
  const memberId = uiState.deptDragMemberId || e.dataTransfer?.getData("text/plain") || "";
  if (!memberId) return;
  e.preventDefault();
  const deptId = zone.dataset.deptDropZone || "";
  const snapshot = cloneDeptSettingsState();
  if (deptId === "__unassigned__") {
    // 全部署から除外
    const state = uiState.deptSettings;
    if (state) state.departments.forEach((d) => {
      d.memberIds = d.memberIds.filter((id) => id !== memberId);
      if (d.leaderMemberId === memberId) d.leaderMemberId = "";
    });
  } else {
    moveMemberToDepartment(memberId, deptId);
  }
  const persist = await persistDepartments();
  if (!persist.ok) restoreDeptSettingsState(snapshot);
  if (!persist.ok) showToast(formatDeptPersistFailureMessage(persist));
  uiState.deptDragMemberId = "";
  render();
});

document.addEventListener("dblclick", async (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (!isAccountSettingsRoute() || uiState.accountSettingsTab !== "dept") return;
  // 「⋯」ボタンの連打で責任者が切り替わらないようにする（ボタンはモーダルを開くだけ）
  if (target.closest("[data-dept-member-menu]")) return;
  const card = target.closest("[data-dept-member-card]");
  if (!(card instanceof HTMLElement)) return;
  const memberId = String(card.dataset.deptMemberCard || "");
  if (!memberId) return;
  const state = uiState.deptSettings;
  if (!state) return;
  const dept = state.departments.find((d) => Array.isArray(d.memberIds) && d.memberIds.includes(memberId));
  if (!dept) {
    showToast("未所属メンバーは責任者に設定できません");
    return;
  }
  const snapshot = cloneDeptSettingsState();
  if (dept.leaderMemberId === memberId) {
    dept.leaderMemberId = "";
  } else {
  dept.leaderMemberId = memberId;
  }
  const persist = await persistDepartments();
  if (!persist.ok) restoreDeptSettingsState(snapshot);
  if (!persist.ok)
    showToastError(formatDeptPersistFailureMessage(persist, "責任者設定の保存に失敗しました"));
  else showToast(dept.leaderMemberId ? "責任者を設定しました" : "責任者を解除しました");
  render();
});

// ブラウザ既定のスクロール自動復元（戻る/進む相当の履歴操作で前回位置へ戻す挙動）が
// ハッシュ遷移のスクロールリセットと競合しないよう、自前で制御する。
if ("scrollRestoration" in history) {
  try { history.scrollRestoration = "manual"; } catch (_e) { /* noop */ }
}

// 画面遷移は同期で即時に処理する（render は内部で rAF デバウンスされるため再入は安全）。
// 保存待ちで遷移をブロックしない／遷移を捨てない（最後のクリック先へ必ず遷移する）。
// SPA全般: メニュー等でページ（route.page）自体が切り替わった直後にスクロール位置が
// 残ったまま表示され、新しい見出しが固定ヘッダーの上へ見切れる不具合対策。
// ToDo管理は route.page が一覧/詳細とも "progress-management" で同一のため、
// ?issue= の有無・変化も別途チェックして同一ページ内遷移も検知する。
// render() 自体が内部で rAF デバウンス＋スクロール位置の保存/復元(16-render-routing.js冒頭)を
// 行うため、その後始末より後に確実に効かせるよう二重 rAF で最後に実行する。
let __fpLastRoutePage = currentRoute().page;
let __fpLastIssueParam = String(currentRoute().searchParams?.get("issue") || "").trim();
function __fpResetScrollToTop() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const ms = document.querySelector(".main-surface");
  if (ms) ms.scrollTop = 0;
}
window.addEventListener("hashchange", () => {
  uiState.fpMobileNavOpen = false;
  // 編集中の請求項目はバックグラウンドで保存（遷移はブロックしない）。
  // 保存失敗時は dirty を残し次回再試行。fetch 側は AbortController で実タイムアウト。
  void flushDirtyProjectBillingLines();
  try {
    dismissTransientUiOnRouteChange(currentRoute());
    const route = currentRoute();
    uiState.editingProjectNameId = "";
    const nextIssueParam = String(route.searchParams?.get("issue") || "").trim();
    const pageChanged = route.page !== __fpLastRoutePage;
    const issueOpened = nextIssueParam && nextIssueParam !== __fpLastIssueParam;
    if (pageChanged || issueOpened) {
      __fpResetScrollToTop();
      requestAnimationFrame(() => requestAnimationFrame(__fpResetScrollToTop));
    }
    __fpLastRoutePage = route.page;
    __fpLastIssueParam = nextIssueParam;
    if (route.page === "set-password") {
      showLoginScreen();
      render();
      return;
    }
    if (!isLoggedIn()) {
      showLoginScreen();
      render();
      return;
    }
    if (uiState.projectCreate && !route.openProjectCreate) {
      uiState.projectCreate = null;
    }
    render();
  } catch (err) {
    // 例外時でも render で最低限の画面復旧を行う
    console.error("hashchange handler error:", err);
    render();
  }
});

document.addEventListener("compositionstart", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.dataset.clientSearch && uiState.projectCreate) {
    target.dataset.imeComposing = "1";
    return;
  }
  // 検索欄は変換中フラグを立て、変換前に予約済みの絞り込み描画も取り消す（isImeComposingSearch）
  if (target.matches("[data-ime-search]")) {
    target.dataset.imeComposing = "1";
    clearTimeout(_keywordRenderTimer);
  }
});

document.addEventListener("compositionend", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (target instanceof HTMLTextAreaElement && target.dataset.pmMentionTextarea === "1") {
    pmSyncMentionBackdrop(target);
    progressMentionSyncFromTextarea(target);
    return;
  }
  if (!(target instanceof HTMLInputElement)) return;
  if (target.dataset.clientSearch && uiState.projectCreate) {
    delete target.dataset.imeComposing;
    uiState.projectCreate.form.clientQuery = target.value;
    uiState.projectCreate.form.clientHighlight = -1;
    render();
    return;
  }
  // IME確定時の受け皿: 検索窓等は input(isComposing中) の描画をガードしているため、
  // 変換確定後に input が発火しない/isComposing のままのIMEだと確定値が反映されず検索が走らない。
  // 確定値で改めて input を発火させて検索を実行する（synthetic Event は isComposing 判定に掛からず通る）。
  // 対象は data-ime-search を持つ検索/絞り込み入力。列挙ではなく属性で判定するため、
  // 新しい検索窓を足すときは入力欄に data-ime-search を付けるだけで再発火が効く
  // （旧実装のようにここへ dataset キーを追記し忘れて「確定しても検索が走らない」再発を防ぐ）。
  if (target.matches("[data-ime-search]")) {
    delete target.dataset.imeComposing;
    target.dispatchEvent(new Event("input", { bubbles: true }));
    return;
  }
});

/** クリックでカレンダー（ピッカー）を開く入力欄か。日付・月・日時の欄は全画面で対象（2026-10 要望:
 *  文字部分や見出しを押しても開いてほしい。以前は data-show-picker="1" を付けた欄だけ）。
 *  無効・読み取り専用の欄と、data-show-picker="0" で明示的に外した欄は対象外 */
function isClickToOpenPickerInput(n) {
  if (!(n instanceof HTMLInputElement) || n.disabled || n.readOnly) return false;
  const opt = n.getAttribute("data-show-picker");
  if (opt === "1") return true;
  if (opt === "0") return false;
  return n.type === "date" || n.type === "month" || n.type === "datetime-local";
}

document.addEventListener("pointerdown", (e) => {
  // タッチはネイティブのタップでピッカーが開き、かつ入力にフォーカスが入る。
  // ここで preventDefault すると iOS でフォーカスが入らず、ピッカー中の再描画保留
  // （deferRenderWhileMonthPicking のフォーカス判定）も効かなくなるため介入しない
  if (e.pointerType === "touch" || e.pointerType === "pen") return;
  /** type=month/date 等は内部がシャドウDOMのため、文字列部分クリックでは closest が input に届かない。composedPath でホスト input を拾う */
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];
  let input = null;
  for (const n of path) {
    if (isClickToOpenPickerInput(n)) {
      input = n;
      break;
    }
  }
  // 見出し（label）の文字をクリックしても開く。label 内の別の入力欄・ボタンを押したときは開かない
  if (!(input instanceof HTMLInputElement) && e.target instanceof Element && !e.target.closest("input, select, textarea, button, a")) {
    const lab = e.target.closest("label");
    const inner = lab ? [...lab.querySelectorAll("input")].find(isClickToOpenPickerInput) : null;
    if (inner instanceof HTMLInputElement) input = inner;
  }
  if (!(input instanceof HTMLInputElement)) return;
  if (typeof input.showPicker !== "function") return;
  e.preventDefault();
  try {
    input.showPicker();
  } catch {
    /* noop */
  }
});

document.addEventListener("keydown", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (!e.isComposing && e.keyCode !== 229) {
    const pop = document.getElementById(PM_MENTION_POPOVER_ID);
    if (
      pop &&
      target instanceof HTMLTextAreaElement &&
      pop.dataset.linkedTextareaId === target.id &&
      progressMentionSession &&
      target.dataset.pmMentionTextarea === "1"
    ) {
      if (e.key === "Escape") {
        e.preventDefault();
        progressMentionDismissForDomRefresh();
        return;
      }
      if (e.key === "Tab") {
        progressMentionDismissForDomRefresh();
      } else if (e.key === "ArrowDown" && progressMentionSession.list.length) {
        e.preventDefault();
        progressMentionMoveActive(1);
        return;
      } else if (e.key === "ArrowUp" && progressMentionSession.list.length) {
        e.preventDefault();
        progressMentionMoveActive(-1);
        return;
      } else if (e.key === "Enter" && !e.shiftKey && progressMentionSession.list.length) {
        const ctx = progressComposeFindMentionAnchor(target.value, target.selectionStart);
        if (!ctx) {
          progressMentionDismissForDomRefresh();
          return;
        }
        e.preventDefault();
        const u = progressMentionSession.list[progressMentionSession.activeIndex];
        if (u) progressMentionApplyPick(u.name);
        return;
      }
    }
  }
  if (
    e.key === "Enter" &&
    target instanceof HTMLInputElement &&
    target.getAttribute("data-project-detail-name-input") &&
    currentRoute().page === "project-detail" &&
    !e.isComposing
  ) {
    e.preventDefault();
    const pid = target.getAttribute("data-project-detail-name-input") || "";
    const p = projects.find((x) => x.id === pid);
    if (p) {
      const raw = target.value.trim();
      p.name = raw || p.name;
      const d = uiState.projectDetailForm;
      if (d?.draft && String(d.projectId) === String(p.id)) d.draft.projectName = p.name;
      uiState.editingProjectNameId = "";
      void (async () => {
        try {
          await persistProjectDetailToServer(p);
          showToast("案件名を更新しました");
        } catch {
          showToastError("案件名の保存に失敗しました");
        }
        render();
      })();
    }
    return;
  }
  if (
    e.key === "Enter" &&
    !e.isComposing &&
    target instanceof HTMLInputElement &&
    target.type !== "file"
  ) {
    const rPage = currentRoute().page;
    if (rPage === "cost-management" && target.hasAttribute("data-cost-mgmt-filter-keyword")) {
      e.preventDefault();
      if (costMgmtKeywordDebounceTimer != null) { clearTimeout(costMgmtKeywordDebounceTimer); costMgmtKeywordDebounceTimer = null; }
      syncCostMgmtFilterFromDom();
      render();
      return;
    }
    const clf = target.dataset.clientListFilter;
    if (rPage === "client-list" && clf === "keyword") {
      e.preventDefault();
      render();
      return;
    }
    const plf = target.dataset.partnerListFilter;
    if (rPage === "partner-list" && plf === "keyword") {
      e.preventDefault();
      render();
      return;
    }
    const dlf = target.dataset.domainListFilter;
    if (rPage === "domain-list" && dlf === "keyword") {
      e.preventDefault();
      render();
      return;
    }
    const pjf = target.dataset.projectFilter;
    if (rPage === "projects" && pjf && (pjf === "name" || pjf === "no")) {
      e.preventDefault();
      render();
      return;
    }
  }
  if (!uiState.projectCreate || !target.dataset.clientSearch) return;
  if (e.isComposing || e.keyCode === 229) return;
  const q = (uiState.projectCreate.form.clientQuery || "").trim();
  if (!q) return;
  const candidates = projectCreateClientCandidates(q);
  if (!candidates.length) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    uiState.projectCreate.form.clientHighlight = Math.min((uiState.projectCreate.form.clientHighlight ?? -1) + 1, candidates.length - 1);
    render();
  }
  if (e.key === "ArrowUp") {
    e.preventDefault();
    uiState.projectCreate.form.clientHighlight = Math.max((uiState.projectCreate.form.clientHighlight ?? 0) - 1, 0);
    render();
  }
  if (e.key === "Enter") {
    e.preventDefault();
    const idx = uiState.projectCreate.form.clientHighlight >= 0 ? uiState.projectCreate.form.clientHighlight : 0;
    const selected = candidates[idx];
    uiState.projectCreate.form.clientId = selected.id;
    uiState.projectCreate.form.clientQuery = selected.name;
    if (selected.contactName) uiState.projectCreate.form.contactName = selected.contactName;
    render();
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && currentRoute().page === "progress-management") {
    const openCompose = document.querySelector(".pm-compose--collapsible.pm-compose--open");
    if (openCompose instanceof HTMLElement) {
      const ta = openCompose.querySelector("[data-pm-msg-body]");
      // テキストが空のときだけ Escape で閉じる（入力中は誤操作防止）
      if (!ta || !(ta instanceof HTMLTextAreaElement) || ta.value.trim() === "") {
        e.preventDefault();
        openCompose.classList.remove("pm-compose--open");
        return;
      }
    }
  }
  if (e.key === "Escape" && uiState.editingProjectNameId && currentRoute().page === "project-detail") {
    e.preventDefault();
    uiState.editingProjectNameId = "";
    render();
    return;
  }
  if (e.key === "Escape" && uiState.approvalPicker) {
    e.preventDefault();
    uiState.approvalPicker = null;
    render();
  }
});

// ─────────────────────────────────────────────────────────────
// モーダル共通の Esc クローズ・フォーカス管理（16 の巨大ハンドラには足さない・独立ファイル）
//   - aria-modal="true" を宣言している全パネルを対象に、開いたら中へフォーカス・
//     閉じたら元の位置へ戻す・Tab でパネル外へ漏れないようにする（宣言と実装の不一致を解消）
//   - 各モーダル固有の閉じ方（uiState のどのキーを null にするか）は関知せず、
//     既存の data-*-close 系属性を持つバックドロップ/スクリムへ合成クリックを送るだけに留める
//   - 16-render-routing.js より後に読み込まれる必要がある（メンション候補などの
//     既存の個別 Escape 処理が e.preventDefault 済みなら二重に閉じないよう
//     e.defaultPrevented を見るため）
// ─────────────────────────────────────────────────────────────

const FP_MODAL_BACKDROP_SELECTOR =
  ".composer-backdrop, .pm-attach-preview-backdrop, .pm-sp-compose-backdrop, .pm-sp-sheet-backdrop, " +
  ".cl-mini-modal-overlay.open, .cl-modal-overlay.open, .doc-inv-sp-menu-backdrop, .approval-picker-scrim";

const FP_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function fpIsFocusable(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.hidden || el.closest("[hidden]")) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

/** data-*close* 系属性を持つ要素（各モーダルの「閉じる」系ボタン）か。初期フォーカスの対象から外すために使う */
function fpIsCloseControl(el) {
  return Array.from(el.attributes).some((a) => a.name.startsWith("data-") && /close/i.test(a.name));
}

/** selector に一致する要素のうち最も後（DOM順で最後＝最も新しく開いた＝最前面）のものを返す。
 *  fpModalLayer（オーバーレイ層）があれば、その中を優先して探す。 */
function fpTopmostMatch(selector) {
  const layerEl = typeof fpModalLayer !== "undefined" ? fpModalLayer : null;
  if (layerEl instanceof HTMLElement) {
    const layerMatches = layerEl.querySelectorAll(selector);
    if (layerMatches.length) return layerMatches[layerMatches.length - 1];
  }
  const all = document.querySelectorAll(selector);
  return all.length ? all[all.length - 1] : null;
}

/** 現在開いている中で最も手前のモーダルパネル */
function fpTopmostModalEl() {
  return fpTopmostMatch('[aria-modal="true"]');
}

function fpTopmostModalBackdrop() {
  return fpTopmostMatch(FP_MODAL_BACKDROP_SELECTOR);
}

// ── Esc でモーダルを閉じる ──
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape" || e.defaultPrevented) return;
  // IME の変換確定前に押された Escape（変換候補のキャンセル）はモーダルを閉じる操作ではない。
  // e.isComposing に加え、一部ブラウザは isComposing を落とすため keyCode 229 も見る
  // （このリポジトリで繰り返し踏んでいるIME関連の既知の罠）。
  if (e.isComposing || e.keyCode === 229) return;
  const backdrop = fpTopmostModalBackdrop();
  if (backdrop instanceof HTMLElement) {
    e.preventDefault();
    backdrop.click();
  }
});

// ── 入力途中のフォームを閉じるときの破棄確認 ──
// バックドロップのクリック・Esc（上の Esc 処理もバックドロップのクリックに変換される）・キャンセルで
// 入力内容が黙って消えないよう、パネル内で input/change が起きたフォームだけ閉じる前に確認する。
// 各フォームの state 形状には踏み込まず「パネル内でユーザーが何か入力したか」だけを見る
// （パネルが DOM から消えたら下の監視 tick で未入力に戻る＝次に開いたフォームへ持ち越さない）。
const FP_DIRTY_FORM_PANELS = {
  costForm: "aside.cost-edit-side-panel",
  projectCreate: 'aside[aria-labelledby="project-create-title"]',
  agendaForm: "aside.pm-agenda-create-modal",
  userEditor: "aside[data-user-editor-panel]"
};
const fpDirtyForms = new Set();

function fpMarkDirtyFormFromEvent(e) {
  const t = e.target;
  if (!(t instanceof Element)) return;
  for (const [key, selector] of Object.entries(FP_DIRTY_FORM_PANELS)) {
    if (t.closest(selector)) fpDirtyForms.add(key);
  }
}
document.addEventListener("input", fpMarkDirtyFormFromEvent, true);
document.addEventListener("change", fpMarkDirtyFormFromEvent, true);

function fpForgetClosedDirtyForms() {
  for (const key of [...fpDirtyForms]) {
    if (!document.querySelector(FP_DIRTY_FORM_PANELS[key])) fpDirtyForms.delete(key);
  }
}

/** key のフォームに入力があれば破棄してよいか確認する。閉じてよいなら true（未入力なら確認なしで true）。
 *  extraDirty: input イベントを伴わない入力（ドロップした添付など）があるとき true を渡す */
function fpConfirmDiscardDirtyForm(key, extraDirty = false) {
  if (!fpDirtyForms.has(key) && !extraDirty) return true;
  if (!window.confirm("入力中の内容を破棄して閉じますか？")) return false;
  fpDirtyForms.delete(key);
  return true;
}

// ── Tab でモーダル外へフォーカスが漏れないようにする（簡易フォーカストラップ） ──
document.addEventListener("keydown", (e) => {
  if (e.key !== "Tab") return;
  const modal = fpTopmostModalEl();
  if (!modal) return;
  const focusables = [...modal.querySelectorAll(FP_FOCUSABLE_SELECTOR)].filter(fpIsFocusable);
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !modal.contains(active)) {
    e.preventDefault();
    first.focus();
    return;
  }
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
});

/** 要素を再クエリできる程度に安定したセレクタへ変換（id優先・無ければ data-* 属性の組み合わせ） */
function fpDescribeFocusTarget(el) {
  if (!(el instanceof HTMLElement)) return null;
  if (el.id) return `#${CSS.escape(el.id)}`;
  const parts = Array.from(el.attributes)
    .filter((a) => a.name.startsWith("data-") && a.value)
    .map((a) => `[${a.name}="${CSS.escape(a.value)}"]`);
  if (!parts.length) return null;
  return el.tagName.toLowerCase() + parts.join("");
}

// モーダルを開くトリガー要素へフォーカスが移った瞬間（クリック時の既定動作でのフォーカスは
// click ハンドラで state を書き換えるより前に起きる）を継続的に記録しておく。
// MutationObserver のコールバックは DOM 差し替え「後」にしか呼べず、その時点では
// document.activeElement は既に古いノードの除去で body に落ちてしまっているため、
// 「今開こうとしている直前にどこにフォーカスがあったか」は afterの視点では取得できない。
let fpLastNonModalFocusSelector = null;
document.addEventListener(
  "focusin",
  (e) => {
    const t = e.target;
    if (t instanceof HTMLElement && !t.closest('[aria-modal="true"]') && !t.matches(FP_MODAL_BACKDROP_SELECTOR)) {
      fpLastNonModalFocusSelector = fpDescribeFocusTarget(t);
    }
  },
  true
);

// ── モーダルの開閉を監視し、開いたら中へ・閉じたら元の位置へフォーカスを移す ──
// render() のたびに innerHTML ごと再生成されるため、DOM ノードの同一性ではなく
// 「直前に aria-modal パネルが存在したか」の真偽値だけで開閉の切り替わりを判定する
// （モーダルを開いたまま無関係な再描画が起きても毎回フォーカスを奪わないため）。
let fpModalWasOpen = false;
let fpModalReturnFocusSelector = null;

function fpModalFocusObserverTick() {
  if (fpDirtyForms.size) fpForgetClosedDirtyForms();
  const modal = fpTopmostModalEl();
  const isOpenNow = !!modal;
  if (isOpenNow && !fpModalWasOpen) {
    fpModalReturnFocusSelector = fpLastNonModalFocusSelector;
    const focusables = [...modal.querySelectorAll(FP_FOCUSABLE_SELECTOR)].filter(fpIsFocusable);
    // 「閉じる」系ボタンが先頭にあっても初期フォーカスには選ばない
    // （開いた直後に Enter/Space を押す癖でモーダルが即閉じてしまうのを防ぐ）
    const preferred = focusables.filter((el) => !fpIsCloseControl(el));
    const target = preferred[0] || focusables[0] || modal;
    if (target === modal && !modal.hasAttribute("tabindex")) modal.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  } else if (!isOpenNow && fpModalWasOpen) {
    const selector = fpModalReturnFocusSelector;
    fpModalReturnFocusSelector = null;
    if (selector) {
      try {
        document.querySelector(selector)?.focus({ preventScroll: true });
      } catch {
        /* セレクタが不正なら諦める（フォーカスはbodyのまま） */
      }
    }
  }
  fpModalWasOpen = isOpenNow;
}

function fpInitModalFocusObserver() {
  const observer = new MutationObserver(fpModalFocusObserverTick);
  observer.observe(document.body, { childList: true, subtree: true });
}

fpInitModalFocusObserver();
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 447〜831 行）。処理したら true（16 側はそこで止める） */
function handleProjectDetailClick(e, target) {
  // 課題カードの［…］メニュー: 外側クリックで閉じる（別カードのメニューを開いたときも既存を閉じる）
  if (document.querySelector("details[data-pm-kan-dd][open]")) {
    const ownDd = target.closest("details[data-pm-kan-dd]");
    document.querySelectorAll("details[data-pm-kan-dd][open]").forEach((d) => {
      if (d instanceof HTMLDetailsElement && d !== ownDd) d.open = false;
    });
  }
  if (target.closest("[data-dismiss-toast]")) {
    e.preventDefault();
    clearToast();
    return true;
  }
  const mentionPick = target.closest("[data-pm-mention-idx]");
  if (mentionPick instanceof HTMLElement && mentionPick.closest(`#${PM_MENTION_POPOVER_ID}`)) {
    e.preventDefault();
    const idx = Number(mentionPick.dataset.pmMentionIdx);
    const row = progressMentionSession?.list?.[idx];
    if (row?.name) progressMentionApplyPick(row.name);
    return true;
  }
  {
    const popOpen = document.getElementById(PM_MENTION_POPOVER_ID);
    if (popOpen instanceof HTMLElement) {
      if (!target.closest(`#${PM_MENTION_POPOVER_ID}`)) {
        const lid = popOpen.dataset.linkedTextareaId || "";
        const taHit = target instanceof HTMLTextAreaElement ? target : target.closest("textarea");
        const isSameTa =
          !!lid &&
          taHit instanceof HTMLTextAreaElement &&
          taHit.id === lid &&
          taHit.dataset.pmMentionTextarea === "1";
        if (!isSameTa) progressMentionDismissForDomRefresh();
      }
    }
  }
  // 一覧の詳細/書類遷移は最優先で処理して、体感遅延を抑える
  // 案件編集履歴を開く
  const openIssueHistEl = target.closest("[data-open-invoice-issue-history]");
  if (openIssueHistEl instanceof HTMLElement) {
    e.preventDefault();
    const raw = openIssueHistEl.getAttribute("data-open-invoice-issue-history") || "";
    const sep = raw.indexOf(":");
    const pid = sep >= 0 ? raw.slice(0, sep) : raw;
    const docType = sep >= 0 ? raw.slice(sep + 1) : "";
    const proj = projects.find((x) => x.id === pid);
    if (!proj) return true;
    uiState.invoiceIssueHistoryModal = { projectId: pid, docType };
    render();
    if (proj.dbId) void loadInvoiceIssueHistory(proj, docType);
    return true;
  }
  const closeIssueHistEl = target.closest("[data-close-invoice-issue-history]");
  if (closeIssueHistEl instanceof HTMLElement) {
    e.preventDefault();
    uiState.invoiceIssueHistoryModal = null;
    render();
    return true;
  }
  const openEditHistEl = target.closest("[data-open-project-edit-history]");
  if (openEditHistEl instanceof HTMLElement) {
    e.preventDefault();
    const pid = openEditHistEl.getAttribute("data-open-project-edit-history") || "";
    const proj = projects.find((x) => x.id === pid);
    if (!proj) return true;
    if (!proj.dbId) {
      showToast("この案件はまだサーバーに保存されていません");
      return true;
    }
    uiState.projectEditHistoryModal = { projectId: pid, dbId: proj.dbId, loading: true, history: null, error: "" };
    render();
    void loadProjectEditHistory(proj.dbId);
    return true;
  }
  const closeEditHistEl = target.closest("[data-close-project-edit-history]");
  if (closeEditHistEl instanceof HTMLElement) {
    e.preventDefault();
    uiState.projectEditHistoryModal = null;
    render();
    return true;
  }
  const openProjectDocsFastEl = target.closest("[data-open-project-docs]");
  if (openProjectDocsFastEl instanceof HTMLElement) {
    e.preventDefault();
    const pid = openProjectDocsFastEl.dataset.openProjectDocs || "";
    if (pid) openProjectDocsForProject(pid);
    return true;
  }
  // 入金の一括記録: 行チェックの選択トグル（カード内クリックのため open-detail ガードより前で処理）
  const payBulkCheckEl = target.closest("[data-payment-bulk-check]");
  if (payBulkCheckEl instanceof HTMLInputElement) {
    const id = payBulkCheckEl.dataset.paymentBulkCheck || "";
    if (!id) return true;
    if (!uiState.paymentBulkSelected || typeof uiState.paymentBulkSelected !== "object") {
      uiState.paymentBulkSelected = {};
    }
    if (payBulkCheckEl.checked) uiState.paymentBulkSelected[id] = true;
    else delete uiState.paymentBulkSelected[id];
    if (!uiState.paymentBulkDate) {
      const t = new Date();
      uiState.paymentBulkDate = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(
        t.getDate()
      ).padStart(2, "0")}`;
    }
    render();
    return true;
  }

  const openDetailFastEl = target.closest("[data-open-detail]");
  if (openDetailFastEl instanceof HTMLElement) {
    // .row-actions 内のボタンは先にリターン（e.preventDefault() より前）。
    // ガード後にリターンすると preventDefault 済みで下流ハンドラが全滅するため順序厳守。
    if (openDetailFastEl.classList.contains("row-item") && target.closest(".row-actions")) return true;
    e.preventDefault();
    // data-open-detail-tab があればそのタブで開く（案件一覧は基本情報。未指定は従来どおり請求項目）
    const openTab = String(openDetailFastEl.dataset.openDetailTab || "").trim();
    const nextHash = `#project-detail:${openDetailFastEl.dataset.openDetail || ""}${openTab ? `?tab=${encodeURIComponent(openTab)}` : ""}`;
    if (location.hash === nextHash) render();
    else location.hash = nextHash;
    return true;
  }
  if (currentRoute().page === "staff-assignment" && uiState.staffAssignmentTable.bulkAssignPanelOpen) {
    const openCombo = document.querySelector(".sa-bulk-bar-users .sa-bulk-assign-combo");
    if (openCombo instanceof HTMLElement && !openCombo.contains(target)) {
      uiState.staffAssignmentTable.bulkAssignPanelOpen = false;
      render();
    }
  }
  const staffBulkPlaceholderEl = target.closest(".sa-bulk-assign-placeholder");
  if (staffBulkPlaceholderEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentTable.bulkAssignPanelOpen = true;
    render();
    return true;
  }
  const staffBulkToggleEl = target.closest("[data-staff-bulk-assign-toggle]");
  if (staffBulkToggleEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    const tbl = uiState.staffAssignmentTable;
    tbl.bulkAssignPanelOpen = !tbl.bulkAssignPanelOpen;
    render();
    return true;
  }
  const staffBulkClearAllEl = target.closest("[data-staff-bulk-assign-clear-all]");
  if (staffBulkClearAllEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentTable.bulkUserIds = [];
    render();
    return true;
  }
  const staffBulkChipRmEl = target.closest("[data-staff-bulk-chip-remove]");
  if (staffBulkChipRmEl instanceof HTMLButtonElement && currentRoute().page === "staff-assignment") {
    const rid = String(staffBulkChipRmEl.dataset.staffBulkChipRemove || "").trim();
    const arr = uiState.staffAssignmentTable.bulkUserIds || [];
    uiState.staffAssignmentTable.bulkUserIds = arr.filter((x) => String(x) !== rid);
    render();
    return true;
  }
  const staffAssignResetEl = target.closest("[data-staff-assign-reset]");
  if (staffAssignResetEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentFilters = { category: "all", billingFrom: "", billingTo: "", assignee: "all", unassignedOnly: true };
    uiState.staffAssignmentTable.selected = {};
    uiState.staffAssignmentTable.bulkUserIds = [];
    uiState.staffAssignmentTable.bulkAssignPanelOpen = false;
    render();
    return true;
  }
  const staffBulkApplyEl = target.closest("[data-staff-assign-bulk-apply]");
  if (staffBulkApplyEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentTable.bulkAssignPanelOpen = false;
    const allowedBulk = new Set([
      ...staffAssignableUsers().map((u) => String(u.id)),
      ...staffAssignableDepartments().map((d) => lineAssigneeDeptToken(d.id))
    ]);
    const assignIds = [...(uiState.staffAssignmentTable.bulkUserIds || [])]
      .map(String)
      .filter((id) => id && allowedBulk.has(id));
    const selectedKeys = Object.entries(uiState.staffAssignmentTable.selected || {})
      .filter(([, v]) => v === true)
      .map(([k]) => k);
    if (!selectedKeys.length) {
      showToast("行を選択してください");
      return true;
    }
    if (!assignIds.length) {
      showToast("一括で割り当てる担当者または部署を1件以上選択してください");
      return true;
    }
    const projectIds = new Set();
    let applied = 0;
    let ri = 0;
    const multiRow = selectedKeys.length > 1;
    const multiBulk = assignIds.length > 1;
    for (const key of selectedKeys) {
      const parsed = parseStaffAssignRowKey(key);
      if (!parsed) continue;
      const lines = ensureProjectDocDraft(parsed.pid)?.lines;
      if (!lines || !lines[parsed.idx]) continue;
      const line = lines[parsed.idx];
      if ((line.lineType || "normal") !== "normal") continue;
      if (!multiBulk || !multiRow) {
        setLineAssigneeIds(line, assignIds);
      } else {
        const uid = assignIds[ri % assignIds.length];
        ri += 1;
        setLineAssigneeIds(line, [uid]);
      }
      projectIds.add(parsed.pid);
      applied += 1;
    }
    // 担当者は請求項目（billing_lines_json）に保存するため、案件詳細の割当と同じく未保存の印を付けて自動保存させる。
    // 以前は印を付けておらず、一括適用した割当がサーバーに送られないまま再読み込みで消えていた
    projectIds.forEach((pid) => {
      markProjectDocsDraftDirty(pid);
      persistAllLineStaffForProject(pid);
    });
    uiState.staffAssignmentTable.selected = {};
    uiState.staffAssignmentTable.bulkUserIds = [...assignIds];
    showToast(
      applied
        ? multiBulk && !multiRow
          ? `${applied}件に${assignIds.length}名を割り当てました`
          : multiBulk && multiRow
            ? `${applied}件へ${assignIds.length}名を順に割り当てました`
            : `${applied}件に担当者を割り当てました`
        : "対象行がありませんでした"
    );
    render();
    return true;
  }
  const staffClearSelEl = target.closest("[data-staff-assign-clear-selection]");
  if (staffClearSelEl instanceof HTMLElement && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentTable.selected = {};
    uiState.staffAssignmentTable.bulkAssignPanelOpen = false;
    render();
    return true;
  }
  const detailMainTabEl = target.closest("[data-detail-main-tab]");
  if (detailMainTabEl instanceof HTMLElement && currentRoute().page === "project-detail") {
    const r = currentRoute();
    const pid = r.id;
    const m = detailMainTabEl.dataset.detailMainTab || "billing";
    const t = m === "cost" ? "cost" : m === "billing" ? "billing" : m === "comments" ? "comments" : "overview";
    if (!pid) return true;
    navigateToProjectDetail(pid, t);
    return true;
  }
  const projectNameEditEl = target.closest(
    "[data-project-detail-name-edit], [data-project-detail-name-save], [data-project-detail-name-cancel]"
  );
  if (projectNameEditEl instanceof HTMLElement && currentRoute().page === "project-detail") {
    const r = currentRoute();
    const pid = r.id;
    if (!pid) return true;
    const p = projects.find((x) => x.id === pid);
    if (!p) return true;
    if (projectNameEditEl.hasAttribute("data-project-detail-name-edit")) {
      uiState.editingProjectNameId = p.id;
      render();
      return true;
    }
    if (projectNameEditEl.hasAttribute("data-project-detail-name-cancel")) {
      uiState.editingProjectNameId = "";
      render();
      return true;
    }
    if (projectNameEditEl.hasAttribute("data-project-detail-name-save")) {
      const inp = document.querySelector(`input[data-project-detail-name-input="${p.id}"]`);
      const raw = inp instanceof HTMLInputElement ? inp.value.trim() : "";
      const prevName = p.name; // ロールバック用に保存
      p.name = raw || p.name;
      const d = uiState.projectDetailForm;
      if (d?.draft && String(d.projectId) === String(p.id)) d.draft.projectName = p.name;
      uiState.editingProjectNameId = "";
      void (async () => {
        try {
          await persistProjectDetailToServer(p);
          showToast("案件名を更新しました");
        } catch {
          // サーバー保存失敗: in-memory の名前を元に戻す
          p.name = prevName;
          if (d?.draft && String(d.projectId) === String(p.id)) d.draft.projectName = prevName;
          showToastError("案件名の保存に失敗しました。もう一度お試しください。");
        }
        render();
      })();
      return true;
    }
  }
  const pdTagRemove = target.closest("[data-pd-tag-remove]");
  if (pdTagRemove instanceof HTMLElement && currentRoute().page === "project-detail") {
    const draft = projectDetailFormDraftActive();
    if (!draft) return true;
    const i = Number(pdTagRemove.dataset.pdTagRemove);
    const arr = parseProjectDetailTags(draft.tag);
    if (Number.isFinite(i) && i >= 0 && i < arr.length) {
      arr.splice(i, 1);
      draft.tag = arr.join(", ");
      render();
    }
    return true;
  }
  const pdTagAddSelected = target.closest("[data-pd-tag-add-selected]");
  if (pdTagAddSelected instanceof HTMLElement && currentRoute().page === "project-detail") {
    const wrap = pdTagAddSelected.closest("[data-pd-tags-wrap]");
    const sel = wrap?.querySelector("[data-pd-tag-preset]");
    const draft = projectDetailFormDraftActive();
    if (!draft || !(sel instanceof HTMLSelectElement)) return true;
    const v = (sel.value || "").trim();
    // __new__ は「＋ 新しいタグを作成…」の内部値（15l-project-detail-tags.js がモーダルを開く）
    if (!v || v === "__new__") {
      showToast("タグを選択してください");
      return true;
    }
    const arr = parseProjectDetailTags(draft.tag);
    if (arr.includes(v)) {
      showToast("すでに追加されています");
      return true;
    }
    rememberProjectDetailTagPreset(v);
    arr.push(v);
    draft.tag = arr.join(", ");
    sel.value = "";
    render();
    return true;
  }
  const openDetailTabEl = target.closest("[data-open-detail-tab-id]");
  if (openDetailTabEl instanceof HTMLElement) {
    const pid = openDetailTabEl.dataset.openDetailTabId || "";
    const tr = openDetailTabEl.dataset.openDetailTab || "overview";
    const tab =
      tr === "cost" ? "cost" : tr === "billing" ? "billing" : tr === "result" ? "overview" : "overview";
    if (!pid) return true;
    if (openDetailTabEl.classList.contains("row-item") && target.closest(".row-actions")) return true;
    uiState.appMgmtDetailModal = null;
    navigateToProjectDetail(pid, tab);
    return true;
  }
  const projectDocTabEl = target.closest("[data-project-doc-tab]");
  if (projectDocTabEl instanceof HTMLElement) {
    const [pid, tab] = (projectDocTabEl.dataset.projectDocTab || "").split(":");
    if (pid && tab) location.hash = `#project-docs:${pid}:${tab}`;
    return true;
  }
  const sealHist = target.closest("[data-doc-seal-history]");
  if (sealHist instanceof HTMLElement) {
    const mk = sealHist.getAttribute("data-doc-seal-history") || "";
    if (mk) uiState.docSealHistoryModal = { mapKey: mk };
    render();
    return true;
  }
  if (target.closest("[data-doc-seal-history-close]")) {
    uiState.docSealHistoryModal = null;
    render();
    return true;
  }
  const pjSealApplyEl = target.closest("[data-pj-seal-apply]");
  if (pjSealApplyEl instanceof HTMLElement) {
    // 一覧からの申請もコメント・承認期日を入れられるよう確認モーダルを通す
    const key = pjSealApplyEl.getAttribute("data-pj-seal-apply") || "";
    uiState.sealConfirmDialog = { kind: "apply", key };
    render();
    return true;
  }
  const sealApplyEl = target.closest("[data-doc-seal-apply]");
  if (sealApplyEl instanceof HTMLElement) {
    const key = sealApplyEl.getAttribute("data-doc-seal-apply") || sealApplyEl.dataset.docSealApply || "";
    const parsed = parseDocSealKey(key);
    if (!parsed?.pid || !parsed?.tab) return true;
    if (getDocSealStatus(parsed.pid, parsed.tab) === "未申請") {
      uiState.sealConfirmDialog = { kind: "apply", key };
      render();
    }
    return true;
  }
  const sealCancelEl = target.closest("[data-doc-seal-cancel]");
  if (sealCancelEl instanceof HTMLElement) {
    const key = sealCancelEl.getAttribute("data-doc-seal-cancel") || "";
    const parsed = parseDocSealKey(key);
    if (!parsed?.pid || !parsed?.tab) return true;
    const mk = docSealStateKey(parsed.pid, parsed.tab);
    const snap = snapshotDocSealState(mk);
    setDocSealStatus(parsed.pid, parsed.tab, "未申請");
    void (async () => {
      const ok = await persistDocSealApplicationToServer(mk, snap);
      if (ok) showToast("申請を取り消しました");
      render();
    })();
    return true;
  }
  return false;
}

/** 16-render-routing.js の click から移した分岐（元の 833〜1102 行）。処理したら true（16 側はそこで止める） */
function handleApplicationMgmtClick(e, target) {
  // 契約関係（取引承認）：却下モーダル
  const appMgmtContractReject = target.closest("[data-app-mgmt-contract-reject]");
  if (appMgmtContractReject instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtContractReject.getAttribute("data-app-mgmt-contract-reject") || "";
    if (id) { uiState.appMgmtContractRejectDraft = { id }; render(); }
    return true;
  }
  if (target.closest("[data-contract-reject-submit]")) {
    void rejectContractApplication();
    return true;
  }
  const contractRejectClose = target.closest("[data-contract-reject-close]");
  if (contractRejectClose instanceof HTMLElement) {
    const closeByOverlay = contractRejectClose.classList.contains("cl-mini-modal-overlay");
    if (closeByOverlay && target.closest(".cl-mini-modal")) return true;
    uiState.appMgmtContractRejectDraft = null;
    uiState.appMgmtContractApproving = false;
    render();
    return true;
  }
  // 請求書（捺印）：却下モーダル
  const appMgmtSealReject = target.closest("[data-app-mgmt-seal-reject]");
  if (appMgmtSealReject instanceof HTMLElement && currentRoute().page === "application-management") {
    const key = appMgmtSealReject.getAttribute("data-app-mgmt-seal-reject") || "";
    if (key) { uiState.appMgmtSealRejectDraft = { key }; render(); }
    return true;
  }
  if (target.closest("[data-seal-reject-submit]")) {
    void rejectSealApplication();
    return true;
  }
  const sealRejectClose = target.closest("[data-seal-reject-close]");
  if (sealRejectClose instanceof HTMLElement) {
    const closeByOverlay = sealRejectClose.classList.contains("cl-mini-modal-overlay");
    if (closeByOverlay && target.closest(".cl-mini-modal")) return true;
    uiState.appMgmtSealRejectDraft = null;
    uiState.appMgmtSealRejectSaving = false;
    render();
    return true;
  }
  // ツール許可申請：承認（確認のうえ PUT）
  const appMgmtToolApprove = target.closest("[data-app-mgmt-tool-approve]");
  if (appMgmtToolApprove instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtToolApprove.getAttribute("data-app-mgmt-tool-approve") || "";
    if (id && !uiState.toolApprovalSaving) void approveToolApplication(id);
    return true;
  }
  // ツール許可申請：却下モーダルを開く
  const appMgmtToolReject = target.closest("[data-app-mgmt-tool-reject]");
  if (appMgmtToolReject instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtToolReject.getAttribute("data-app-mgmt-tool-reject") || "";
    if (id) { uiState.toolRejectDraft = { id }; render(); }
    return true;
  }
  if (target.closest("[data-tool-reject-submit]")) {
    void rejectToolApplication();
    return true;
  }
  const toolRejectClose = target.closest("[data-tool-reject-close]");
  if (toolRejectClose instanceof HTMLElement) {
    const closeByOverlay = toolRejectClose.classList.contains("cl-mini-modal-overlay");
    if (closeByOverlay && target.closest(".cl-mini-modal")) return true;
    uiState.toolRejectDraft = null;
    uiState.toolApprovalSaving = false;
    render();
    return true;
  }
  // 案件ロック解除申請：承認／却下モーダル
  const appMgmtUnlockApprove = target.closest("[data-app-mgmt-unlock-approve]");
  if (appMgmtUnlockApprove instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtUnlockApprove.getAttribute("data-app-mgmt-unlock-approve") || "";
    if (id && !uiState.projectUnlockSaving) void approveUnlockApplication(id);
    return true;
  }
  const appMgmtUnlockReject = target.closest("[data-app-mgmt-unlock-reject]");
  if (appMgmtUnlockReject instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtUnlockReject.getAttribute("data-app-mgmt-unlock-reject") || "";
    if (id) { uiState.unlockRejectDraft = { id }; render(); }
    return true;
  }
  if (target.closest("[data-unlock-reject-submit]")) {
    void rejectUnlockApplication();
    return true;
  }
  const unlockRejectClose = target.closest("[data-unlock-reject-close]");
  if (unlockRejectClose instanceof HTMLElement) {
    const closeByOverlay = unlockRejectClose.classList.contains("cl-mini-modal-overlay");
    if (closeByOverlay && target.closest(".cl-mini-modal")) return true;
    uiState.unlockRejectDraft = null;
    uiState.projectUnlockSaving = false;
    render();
    return true;
  }
  const appMgmtDetailOpenBtn = target.closest("[data-app-mgmt-detail-open]");
  if (appMgmtDetailOpenBtn instanceof HTMLElement && currentRoute().page === "application-management") {
    const kind = appMgmtDetailOpenBtn.getAttribute("data-app-mgmt-detail-kind") || "";
    const memoKey = appMgmtDetailOpenBtn.getAttribute("data-app-mgmt-detail-key") || "";
    if (APP_MGMT_MEMO_KINDS.has(kind) && memoKey) {
      uiState.appMgmtDetailModal = { kind, key: memoKey };
      render();
    }
    return true;
  }
  const appMgmtDetailSaveEl = target.closest("[data-app-mgmt-detail-save]");
  if (appMgmtDetailSaveEl instanceof HTMLElement && uiState.appMgmtDetailModal) {
    const mod = uiState.appMgmtDetailModal;
    const kind = mod.kind;
    const memoKey = String(mod.key || "");
    if (APP_MGMT_MEMO_KINDS.has(kind) && memoKey) {
      const panel = appMgmtDetailSaveEl.closest(".app-mgmt-detail-panel");
      const ta = panel?.querySelector("textarea[data-app-mgmt-memo]");
      const text = ta instanceof HTMLTextAreaElement ? ta.value : getAppMgmtReviewMemo(kind, memoKey);
      void (async () => {
        if (authToken) {
          const ok = await persistAppMgmtReviewMemoToServer(kind, memoKey, text);
          if (!ok) return;
        } else {
          setAppMgmtReviewMemo(kind, memoKey, text);
        }
        uiState.appMgmtDetailModal = null;
        showToast("確認メモを保存しました");
        render();
      })();
    }
    return true;
  }
  const appMgmtDetailCloseEl = target.closest("[data-app-mgmt-detail-close]");
  if (appMgmtDetailCloseEl instanceof HTMLElement) {
    uiState.appMgmtDetailModal = null;
    render();
    return true;
  }
  const appMgmtNavProjDocsEl = target.closest("[data-app-mgmt-nav-project-docs]");
  if (appMgmtNavProjDocsEl instanceof HTMLElement) {
    const raw = appMgmtNavProjDocsEl.getAttribute("data-app-mgmt-nav-project-docs") || "";
    const colon = raw.indexOf(":");
    if (colon >= 1) {
      const pid = raw.slice(0, colon);
      const docTab = raw.slice(colon + 1);
      uiState.appMgmtDetailModal = null;
      location.hash = `#project-docs:${pid}:${docTab}`;
    }
    return true;
  }
  // 申請承認: 書類を別ウィンドウで開く（見積書・請求書は帳票プレビューのポップアップ、
  // プレビュー未対応の書類種別はアプリの書類ページを新規タブで）
  const appMgmtDocWindowEl = target.closest("[data-app-mgmt-doc-window]");
  if (appMgmtDocWindowEl instanceof HTMLElement && currentRoute().page === "application-management") {
    const raw = appMgmtDocWindowEl.getAttribute("data-app-mgmt-doc-window") || "";
    const colon = raw.indexOf(":");
    if (colon >= 1) {
      const pid = raw.slice(0, colon);
      const docTab = raw.slice(colon + 1);
      const proj = projects.find((x) => x.id === pid);
      if (!proj) return true;
      if (docTab === "invoice" || docTab === "quote") {
        printDocument(docTab, pid, buildDocFromProjectDraft(proj, docTab));
      } else {
        window.open(`${location.pathname}#project-docs:${pid}:${docTab}`, "_blank", "noopener");
      }
    }
    return true;
  }
  const appMgmtOpenProjectCostEl = target.closest("[data-app-mgmt-open-project-cost]");
  if (appMgmtOpenProjectCostEl instanceof HTMLElement) {
    const pid = appMgmtOpenProjectCostEl.getAttribute("data-app-mgmt-open-project-cost") || "";
    if (pid) {
      uiState.appMgmtDetailModal = null;
      navigateToProjectDetail(pid, "cost");
    }
    return true;
  }
  // 申請承認（統一テーブル）：選択解除・全選択・行選択・一括承認
  const appMgmtClearSelEl = target.closest("[data-app-mgmt-clear-selection]");
  if (appMgmtClearSelEl instanceof HTMLElement && currentRoute().page === "application-management") {
    uiState.appMgmtSelected = {};
    render();
    return true;
  }
  const appMgmtSelectAllEl = target.closest("[data-app-mgmt-select-all]");
  if (appMgmtSelectAllEl instanceof HTMLElement && currentRoute().page === "application-management") {
    const uids = buildAppMgmtRows().filter((r) => r.selectable).map((r) => r.uid);
    const sel = uiState.appMgmtSelected || {};
    const allOn = uids.length > 0 && uids.every((u) => sel[u] === true);
    if (allOn) uids.forEach((u) => delete sel[u]);
    else uids.forEach((u) => { sel[u] = true; });
    uiState.appMgmtSelected = sel;
    render();
    return true;
  }
  const appMgmtRowSelectEl = target.closest("[data-app-mgmt-row-select]");
  if (appMgmtRowSelectEl instanceof HTMLElement && currentRoute().page === "application-management") {
    const uid = appMgmtRowSelectEl.getAttribute("data-app-mgmt-row-select") || "";
    if (!uid) return true;
    const sel = uiState.appMgmtSelected || {};
    sel[uid] = !sel[uid];
    uiState.appMgmtSelected = sel;
    render();
    return true;
  }
  const appMgmtBulkApproveBtn = target.closest("[data-app-mgmt-bulk-approve]");
  if (appMgmtBulkApproveBtn instanceof HTMLElement && currentRoute().page === "application-management") {
    const items = Object.entries(uiState.appMgmtSelected || {})
      .filter(([, v]) => v === true)
      .map(([uid]) => {
        const i = uid.indexOf(":");
        return i < 0 ? null : { kind: uid.slice(0, i), key: uid.slice(i + 1) };
      })
      .filter(Boolean);
    if (!items.length) {
      showToast("承認する行にチェックを入れてください");
      return true;
    }
    uiState.sealConfirmDialog = { kind: "app-mgmt-bulk-approve-all", items };
    render();
    return true;
  }
  const appMgmtContractAppr = target.closest("[data-app-mgmt-contract-approve]");
  if (appMgmtContractAppr instanceof HTMLElement && currentRoute().page === "application-management") {
    const id = appMgmtContractAppr.getAttribute("data-app-mgmt-contract-approve") || "";
    if (!id || uiState.appMgmtContractApproving) return true;
    uiState.appMgmtContractApproving = true;
    render();
    void (async () => {
      try {
        const { updated, fail } = await persistContractMasterApproveMany([id]);
        if (fail > 0) showToastError("承認の更新に失敗しました");
        else if (updated > 0) showToast("取引承認を承認済にしました");
        else showToast("既に承認済みです");
      } finally {
        uiState.appMgmtContractApproving = false;
        render();
      }
    })();
    return true;
  }
  const appMgmtChangeReqEl = target.closest("[data-app-mgmt-changereq-approve], [data-app-mgmt-changereq-reject]");
  if (appMgmtChangeReqEl instanceof HTMLElement && currentRoute().page === "application-management") {
    const isApprove = appMgmtChangeReqEl.hasAttribute("data-app-mgmt-changereq-approve");
    const id = appMgmtChangeReqEl.getAttribute(isApprove ? "data-app-mgmt-changereq-approve" : "data-app-mgmt-changereq-reject") || "";
    const decision = isApprove ? "approve" : "reject";
    if (!id || uiState.appMgmtChangeReqBusyId) return true;
    if (decision === "reject" && !window.confirm("この申請を却下しますか？（内容は反映されません）")) return true;
    uiState.appMgmtChangeReqBusyId = id;
    render();
    void (async () => {
      const result = await persistTransactionApprovalApi({ changeRequestId: Number(id), decision });
      uiState.appMgmtChangeReqBusyId = "";
      if (result.ok) {
        showToast(decision === "approve" ? "承認しました" : "却下しました");
        await Promise.all([fetchMasterChangeRequests(), loadDataFromDB()]);
      } else {
        showToastError(result.error || "処理に失敗しました");
        render();
      }
    })();
    return true;
  }
  const appMgmtSealAppr = target.closest("[data-app-mgmt-seal-approve]");
  if (appMgmtSealAppr instanceof HTMLElement && currentRoute().page === "application-management") {
    const raw = appMgmtSealAppr.getAttribute("data-app-mgmt-seal-approve") || "";
    const parsed = parseDocSealKey(raw);
    if (!parsed?.pid || !parsed?.tab) return true;
    if (getDocSealStatus(parsed.pid, parsed.tab) === "申請中") {
      // 詳細パネルの「承認」から来たときはパネルを閉じて確認ダイアログを前に出す（確認メモは入力欄を離れた時点で保存済み）
      uiState.appMgmtDetailModal = null;
      uiState.sealConfirmDialog = { kind: "approve", key: raw };
      render();
    }
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 1103〜1996 行）。処理したら true（16 側はそこで止める） */
function handleProgressFormsClick(e, target) {
  const pmToggleTodoGroupForm = target.closest("[data-pm-toggle-todo-group-form]");
  if (pmToggleTodoGroupForm instanceof HTMLElement && currentRoute().page === "progress-management") {
    const route = currentRoute();
    if (String(route.searchParams?.get("group") || "").trim() || String(route.searchParams?.get("project") || "").trim()) {
      return true;
    }
    const next = !uiState.progressShowTodoGroupForm;
    uiState.progressShowTodoGroupForm = next;
    uiState.progressTodoGroupCreateDraft = { title: "", body: "", category: "" };
    if (next) closeProgressTodoGroupEditModal();
    render();
    return true;
  }
  const closePmTodoGroupCreate = target.closest("[data-close-pm-todo-group-create]");
  if (closePmTodoGroupCreate instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressTodoGroupModalSubmitting) return true;
    closeProgressTodoGroupFormModal();
    render();
    return true;
  }
  const pmSubTodoGroupModal = target.closest("[data-pm-submit-todo-group-modal]");
  if (pmSubTodoGroupModal instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken || uiState.progressTodoGroupModalSubmitting) return true;
    const d = uiState.progressTodoGroupCreateDraft || { title: "", body: "", category: "" };
    const title = String(d.title || "").trim();
    const body = String(d.body || "").trim();
    const category = PM_TODO_GROUP_CATEGORY_OPTIONS.includes(String(d.category || "").trim())
      ? String(d.category).trim()
      : "";
    if (!title) {
      showToast(`${PM_WORD.project}名を入力してください`);
      return true;
    }
    uiState.progressTodoGroupModalSubmitting = true;
    void (async () => {
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "createTodoGroup", title, body, category } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "登録に失敗しました");
          return;
        }
        closeProgressTodoGroupFormModal();
        invalidateProgressGroupListCache();
        invalidateProgressListCache();
        showToast(`${PM_WORD.project}を登録しました`);
        const newId = j.id != null ? Number(j.id) : NaN;
        if (Number.isFinite(newId) && newId >= 1) {
          const groupListMode = normalizeProgressGroupListMode(uiState.progressGroupListMode);
          if (groupListMode === "active") {
            applyProgressTodoGroupCreatedLocal(
              buildProgressTodoGroupFromCreatePayload(newId, { title, body, category })
            );
            if (hasProgressGroupListCache("active")) {
              setProgressGroupListCache("active", {
                groups: uiState.progressTodoGroups,
                challenges: uiState.progressChallenges
              });
            }
          }
          markProgressListFetched("active");
          uiState.suppressComposerDrawerAnimation = true;
          location.hash = `progress-management?group=${newId}`;
          await refreshProgressManagementFromServer({
            groupListMode: "active",
            scope: "summary",
            includeGroupId: String(newId)
          });
        } else {
          render();
        }
      } finally {
        uiState.progressTodoGroupModalSubmitting = false;
        render();
      }
    })();
    return true;
  }
  const pmOpenEditTodoGroup = target.closest("[data-pm-open-edit-todo-group]");
  if (pmOpenEditTodoGroup instanceof HTMLElement && currentRoute().page === "progress-management") {
    const rawId = String(pmOpenEditTodoGroup.dataset.pmOpenEditTodoGroup || "").trim();
    const grp = findProgressTodoGroup(rawId);
    if (!grp) {
      showToast(`${PM_WORD.project}が見つかりません`);
      return true;
    }
    closeProgressTodoGroupFormModal();
    uiState.progressShowTodoGroupEditForm = true;
    const inGroup = progressChallengesInGroup(grp.id).map((c) => String(c.id));
    uiState.progressTodoGroupEditDraft = {
      groupId: String(grp.id),
      title: String(grp.title || ""),
      body: String(grp.body || ""),
      category: String(grp.category || ""),
      challengeIds: inGroup,
      themeFilter: ""
    };
    if (progressChallengesForGroupEditPicker().length === 0 && authToken) {
      void refreshProgressManagementFromServer({ scope: "summary" });
    }
    render();
    return true;
  }
  const closePmTodoGroupEdit = target.closest("[data-close-pm-todo-group-edit]");
  if (closePmTodoGroupEdit instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressTodoGroupModalSubmitting) return true;
    closeProgressTodoGroupEditModal();
    render();
    return true;
  }
  const pmEditGroupSelectAll = target.closest("[data-pm-todo-group-edit-select-all]");
  if (pmEditGroupSelectAll instanceof HTMLElement && currentRoute().page === "progress-management") {
    const d = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    const ids = new Set((Array.isArray(d.challengeIds) ? d.challengeIds : []).map(String));
    progressTodoGroupEditFilteredChallengeIds(d).forEach((id) => ids.add(id));
    d.challengeIds = [...ids];
    uiState.progressTodoGroupEditDraft = d;
    render();
    return true;
  }
  const pmEditGroupClearAll = target.closest("[data-pm-todo-group-edit-clear-all]");
  if (pmEditGroupClearAll instanceof HTMLElement && currentRoute().page === "progress-management") {
    const d = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    d.challengeIds = [];
    uiState.progressTodoGroupEditDraft = d;
    render();
    return true;
  }
  const pmEditGroupCh = target.closest("[data-pm-todo-group-edit-challenge]");
  if (pmEditGroupCh instanceof HTMLElement && currentRoute().page === "progress-management") {
    const cid = String(pmEditGroupCh.dataset.pmTodoGroupEditChallenge || "");
    if (!cid) return true;
    const d = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    const ids = new Set((Array.isArray(d.challengeIds) ? d.challengeIds : []).map(String));
    if (ids.has(cid)) ids.delete(cid);
    else ids.add(cid);
    d.challengeIds = [...ids];
    uiState.progressTodoGroupEditDraft = d;
    render();
    return true;
  }
  const pmSubTodoGroupEdit = target.closest("[data-pm-submit-todo-group-edit]");
  if (pmSubTodoGroupEdit instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken || uiState.progressTodoGroupModalSubmitting) return true;
    const d = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    const groupId = Number(d.groupId);
    const title = String(d.title || "").trim();
    const body = String(d.body || "").trim();
    const category = PM_TODO_GROUP_CATEGORY_OPTIONS.includes(String(d.category || "").trim())
      ? String(d.category).trim()
      : "";
    const challengeIds = (Array.isArray(d.challengeIds) ? d.challengeIds : [])
      .map((x) => Number(x))
      .filter((id) => Number.isFinite(id) && id >= 1);
    if (!Number.isFinite(groupId) || groupId < 1) return true;
    if (!title) {
      showToast(`${PM_WORD.project}名を入力してください`);
      return true;
    }
    uiState.progressTodoGroupModalSubmitting = true;
    void (async () => {
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "updateTodoGroup", groupId, title, body, category, challengeIds } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "保存に失敗しました");
          return;
        }
        const grp = findProgressTodoGroup(groupId);
        if (grp) {
          grp.title = title;
          grp.body = body;
          grp.category = category;
        }
        const idSet = new Set(challengeIds.map(String));
        const uncl = (uiState.progressTodoGroups || []).find((g) => String(g.title) === "未分類");
        const fallbackGid = uncl ? Number(uncl.id) : groupId;
        for (const c of uiState.progressChallenges || []) {
          if (c.archivedAt) continue;
          if (idSet.has(String(c.id))) c.todoGroupId = groupId;
          else if (Number(c.todoGroupId) === groupId) c.todoGroupId = fallbackGid;
        }
        closeProgressTodoGroupEditModal();
        invalidateProgressGroupListCache();
        invalidateProgressListCache();
        showToast(`${PM_WORD.project}を保存しました`);
        render();
        void refreshProgressManagementFromServer({ scope: "summary" });
      } finally {
        uiState.progressTodoGroupModalSubmitting = false;
        render();
      }
    })();
    return true;
  }
  const pmToggleProjForm = target.closest("[data-pm-toggle-project-form]");
  if (pmToggleProjForm instanceof HTMLElement && currentRoute().page === "progress-management") {
    const route = currentRoute();
    if (String(route.searchParams?.get("project") || "").trim()) return true;
    const next = !uiState.progressShowProjectForm;
    uiState.progressShowProjectForm = next;
    uiState.progressChallengeCreateDraft = { title: "", body: "", genre: "" };
    if (next) {
      uiState.progressShowChallengeEditForm = false;
      uiState.progressChallengeEditDraft = { challengeId: "", title: "", body: "", genre: "" };
    }
    render();
    return true;
  }
  const closePmChallengeCreate = target.closest("[data-close-pm-challenge-create]");
  if (closePmChallengeCreate instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressChallengeModalSubmitting) return true;
    closeProgressChallengeFormModal();
    render();
    return true;
  }
  const pmOpenEditChallenge = target.closest("[data-pm-open-edit-challenge]");
  if (pmOpenEditChallenge instanceof HTMLElement && currentRoute().page === "progress-management") {
    const rawId = String(pmOpenEditChallenge.dataset.pmOpenEditChallenge || "").trim();
    const chs = Array.isArray(uiState.progressChallenges) ? uiState.progressChallenges : [];
    const ch = chs.find((x) => String(x.id) === rawId);
    if (!ch) {
      showToast(`${PM_WORD.theme}が見つかりません`);
      return true;
    }
    uiState.progressShowProjectForm = false;
    uiState.progressChallengeCreateDraft = { title: "", body: "" };
    closeProgressChallengeWikiModal();
    closeProgressChallengeSettingsModal();
    uiState.progressShowChallengeEditForm = true;
    uiState.progressChallengeEditDraft = {
      challengeId: String(ch.id),
      title: String(ch.title || ""),
      body: String(ch.body || ""),
      genre: String(ch.genre || ""),
      memberMode: Array.isArray(ch.memberUserIds) ? "selected" : "all",
      memberUserIds: Array.isArray(ch.memberUserIds) ? [...ch.memberUserIds] : null
    };
    render();
    return true;
  }
  const closePmChallengeEdit = target.closest("[data-close-pm-challenge-edit]");
  if (closePmChallengeEdit instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressChallengeModalSubmitting) return true;
    uiState.progressShowChallengeEditForm = false;
    uiState.progressChallengeEditDraft = { challengeId: "", title: "", body: "" };
    uiState.progressChallengeModalSubmitting = false;
    render();
    return true;
  }
  const pmOpenChallengeWiki = target.closest("[data-pm-open-challenge-wiki]");
  if (pmOpenChallengeWiki instanceof HTMLElement && currentRoute().page === "progress-management") {
    const ch = findProgressChallenge(String(pmOpenChallengeWiki.dataset.pmOpenChallengeWiki || ""));
    if (!ch) {
      showToast(`${PM_WORD.theme}が見つかりません`);
      return true;
    }
    openProgressChallengeWikiModal(ch);
    render();
    return true;
  }
  const pmOpenChallengeSettings = target.closest("[data-pm-open-challenge-settings]");
  if (pmOpenChallengeSettings instanceof HTMLElement && currentRoute().page === "progress-management") {
    const ch = findProgressChallenge(String(pmOpenChallengeSettings.dataset.pmOpenChallengeSettings || ""));
    if (!ch) {
      showToast(`${PM_WORD.theme}が見つかりません`);
      return true;
    }
    openProgressChallengeSettingsModal(ch, "categories");
    render();
    return true;
  }
  const closePmChallengeWiki = target.closest("[data-close-pm-challenge-wiki]");
  if (closePmChallengeWiki instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressChallengeWikiSubmitting) return true;
    closeProgressChallengeWikiModal();
    render();
    return true;
  }
  const closePmChallengeSettings = target.closest("[data-close-pm-challenge-settings]");
  if (closePmChallengeSettings instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressChallengeSettingsSubmitting) return true;
    closeProgressChallengeSettingsModal();
    render();
    return true;
  }
  const pmChallengeSettingsAdd = target.closest("[data-pm-challenge-settings-add]");
  if (pmChallengeSettingsAdd instanceof HTMLElement && currentRoute().page === "progress-management") {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d?.challengeId) return true;
    const key = String(pmChallengeSettingsAdd.getAttribute("data-pm-challenge-settings-add") || "");
    if (key !== "statuses" && key !== "categories") return true;
    const newField = key === "statuses" ? "newStatus" : "newCategory";
    const name = String(d[newField] || "").trim();
    if (!name) return true;
    const list = Array.isArray(d[key]) ? [...d[key]] : [];
    if (!list.includes(name)) list.push(name);
    d[key] = list;
    d[newField] = "";
    render();
    return true;
  }
  const pmChallengeSettingsRemoveSt = target.closest("[data-pm-challenge-settings-remove-statuses]");
  if (pmChallengeSettingsRemoveSt instanceof HTMLElement && currentRoute().page === "progress-management") {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d?.challengeId) return true;
    const i = Number(pmChallengeSettingsRemoveSt.getAttribute("data-pm-challenge-settings-remove-statuses"));
    if (!Number.isFinite(i) || i < 0) return true;
    const statuses = Array.isArray(d.statuses) ? d.statuses : [];
    const label = String(statuses[i] || "").trim();
    if (label === "完了") {
      showToast("状況「完了」は削除できません");
      return true;
    }
    d.statuses = statuses.filter((_, idx) => idx !== i);
    render();
    return true;
  }
  const pmChallengeSettingsRemoveCat = target.closest("[data-pm-challenge-settings-remove-categories]");
  if (pmChallengeSettingsRemoveCat instanceof HTMLElement && currentRoute().page === "progress-management") {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d?.challengeId) return true;
    const i = Number(pmChallengeSettingsRemoveCat.getAttribute("data-pm-challenge-settings-remove-categories"));
    if (!Number.isFinite(i) || i < 0) return true;
    d.categories = (Array.isArray(d.categories) ? d.categories : []).filter((_, idx) => idx !== i);
    render();
    return true;
  }
  const pmSubmitChallengeWiki = target.closest("[data-pm-submit-challenge-wiki]");
  if (pmSubmitChallengeWiki instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken || uiState.progressChallengeWikiSubmitting) return true;
    const d = uiState.progressChallengeWikiDraft || {};
    const challengeId = Number(d.challengeId);
    if (!Number.isFinite(challengeId) || challengeId < 1) return true;
    const ta = document.querySelector("[data-pm-challenge-wiki-body]");
    const wikiBody = ta instanceof HTMLTextAreaElement ? ta.value : String(d.wikiBody || "");
    uiState.progressChallengeWikiSubmitting = true;
    render();
    void (async () => {
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "updateChallengeWiki", challengeId, wikiBody } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "保存に失敗しました");
          return;
        }
        applyProgressChallengeWikiUpdatedLocal(challengeId, wikiBody);
        closeProgressChallengeWikiModal();
        showToast(`${PM_WORD.theme}詳細を保存しました`);
        render();
        void refreshProgressManagementFromServer({
          listMode: normalizeProgressListMode(uiState.progressListMode),
          scope: "summary",
          includeChallengeId: challengeId
        });
      } finally {
        uiState.progressChallengeWikiSubmitting = false;
        render();
      }
    })();
    return true;
  }
  const pmSubmitChallengeSettings = target.closest("[data-pm-submit-challenge-settings]");
  if (pmSubmitChallengeSettings instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken || uiState.progressChallengeSettingsSubmitting) return true;
    const d = uiState.progressChallengeSettingsDraft || {};
    const challengeId = Number(d.challengeId);
    if (!Number.isFinite(challengeId) || challengeId < 1) return true;
    // 入力欄DOMの最新値も取り込む（render前で d.statuses 未反映の可能性に備える）
    const settingsModalEl = document.querySelector("[data-pm-challenge-settings-modal], .pm-challenge-settings-modal");
    let statuses = Array.isArray(d.statuses) ? d.statuses.map((s) => String(s || "").trim()).filter(Boolean) : [];
    let categories = Array.isArray(d.categories) ? d.categories.map((s) => String(s || "").trim()).filter(Boolean) : [];
    if (settingsModalEl) {
      // 既存行の編集中テキストを反映
      const stInputs = settingsModalEl.querySelectorAll("[data-pm-challenge-settings-status-name]");
      if (stInputs.length) {
        statuses = Array.from(stInputs).map((el) => String(el.value || "").trim()).filter(Boolean);
      }
      const catInputs = settingsModalEl.querySelectorAll("[data-pm-challenge-settings-category-name]");
      if (catInputs.length) {
        categories = Array.from(catInputs).map((el) => String(el.value || "").trim()).filter(Boolean);
      }
    }
    // 「追加」ボタン未押下で入力欄に残った新規状況/カテゴリも保存に含める（押し忘れ対策）
    let pendingStatus = String(d.newStatus || "").trim();
    let pendingCategory = String(d.newCategory || "").trim();
    if (settingsModalEl) {
      const newStEl = settingsModalEl.querySelector('[data-pm-challenge-settings-new="statuses"]');
      if (newStEl && String(newStEl.value || "").trim()) pendingStatus = String(newStEl.value).trim();
      const newCatEl = settingsModalEl.querySelector('[data-pm-challenge-settings-new="categories"]');
      if (newCatEl && String(newCatEl.value || "").trim()) pendingCategory = String(newCatEl.value).trim();
    }
    const autoAddedItems = [];
    if (pendingStatus && !statuses.includes(pendingStatus)) { statuses.push(pendingStatus); autoAddedItems.push(`状況「${pendingStatus}」`); }
    if (pendingCategory && !categories.includes(pendingCategory)) { categories.push(pendingCategory); autoAddedItems.push(`カテゴリ「${pendingCategory}」`); }
    if (!statuses.includes("完了")) statuses = statuses.concat("完了");
    // 「工数を数える」チェックをDOMから収集（全部チェック=null で保存し、後から足した状態も既定で計測対象にする）
    let timeTrackedStatuses = Array.isArray(d.trackedStatuses)
      ? d.trackedStatuses.map((s) => String(s || "").trim()).filter(Boolean)
      : null;
    if (settingsModalEl) {
      const trackRows = settingsModalEl.querySelectorAll('[data-pm-challenge-settings-sort-row="statuses"]');
      if (trackRows.length) {
        const arr = [];
        let sawUnchecked = false;
        trackRows.forEach((row) => {
          const nameEl = row.querySelector("[data-pm-challenge-settings-status-name]");
          const cb = row.querySelector("[data-pm-challenge-settings-track]");
          const nm = nameEl instanceof HTMLInputElement ? String(nameEl.value || "").trim() : "";
          if (!nm || !(cb instanceof HTMLInputElement)) return;
          if (cb.checked) arr.push(nm);
          else sawUnchecked = true;
        });
        timeTrackedStatuses = sawUnchecked ? arr : null;
      }
    }
    if (Array.isArray(timeTrackedStatuses)) {
      // 押し忘れ救済で自動追加した状態は既定で計測対象。保存後の状態リストに無い名前は落とす
      if (pendingStatus && statuses.includes(pendingStatus) && !timeTrackedStatuses.includes(pendingStatus)) {
        timeTrackedStatuses.push(pendingStatus);
      }
      timeTrackedStatuses = timeTrackedStatuses.filter((s) => statuses.includes(s));
    }
    // 「必須」チェックをDOMから収集（未チェックのみ=必須指定なし=null で保存）
    let requiredSteps = Array.isArray(d.requiredSteps)
      ? d.requiredSteps.map((s) => String(s || "").trim()).filter(Boolean)
      : null;
    if (settingsModalEl) {
      const requiredRows = settingsModalEl.querySelectorAll('[data-pm-challenge-settings-sort-row="statuses"]');
      if (requiredRows.length) {
        const arr = [];
        requiredRows.forEach((row) => {
          const nameEl = row.querySelector("[data-pm-challenge-settings-status-name]");
          const cb = row.querySelector("[data-pm-challenge-settings-required]");
          const nm = nameEl instanceof HTMLInputElement ? String(nameEl.value || "").trim() : "";
          if (!nm || !(cb instanceof HTMLInputElement)) return;
          if (cb.checked) arr.push(nm);
        });
        requiredSteps = arr.length ? arr : null;
      }
    }
    if (Array.isArray(requiredSteps)) {
      requiredSteps = requiredSteps.filter((s) => statuses.includes(s));
      if (!requiredSteps.length) requiredSteps = null;
    }
    // 参加メンバーはメンバータブで触った時だけ送る（未操作で「全員」に戻してしまわないため）
    const memberUserIds = d.membersTouched ? progressChallengeMembersPayload(d) : undefined;
    if (memberUserIds && !memberUserIds.length) {
      showToast("参加メンバーを1人以上選んでください");
      return true;
    }
    const tplPayload = progressAgendaTemplatesPayload(d);
    if (!tplPayload.ok) {
      showProgressAgendaTemplateNameError(tplPayload.index);
      return true;
    }
    uiState.progressChallengeSettingsSubmitting = true;
    render();
    void (async () => {
      try {
        const chBefore = findProgressChallenge(String(challengeId));
        const basicTitle = String(d.basicTitle ?? "").trim();
        const basicBody = String(d.basicBody ?? "").trim();
        if (!basicTitle) {
          showToast("タイトルを入力してください");
          return;
        }
        if (
          chBefore &&
          (basicTitle !== String(chBefore.title ?? "").trim() ||
            basicBody !== String(chBefore.body ?? "").trim())
        ) {
          const resCh = await fetch("/api/progress-management", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({
              action: "updateChallenge",
              challengeId,
              title: basicTitle,
              body: basicBody
            })
          });
          const jCh = await resCh.json().catch(() => ({}));
          if (!resCh.ok) {
            showToastError(typeof jCh.error === "string" ? jCh.error : "タイトル・説明の保存に失敗しました");
            return;
          }
          applyProgressChallengeUpdatedLocal(challengeId, { title: basicTitle, body: basicBody });
        }

        // 工数区分・関連案件はモーダルDOMの最新値を優先（draft 未反映の可能性に備える）
        let workKindVal = String(d.workKind || "") === "internal" ? "internal" : "";
        const wkSel = settingsModalEl?.querySelector("[data-pm-challenge-settings-workkind]");
        if (wkSel instanceof HTMLSelectElement) workKindVal = wkSel.value === "internal" ? "internal" : "";
        let projectIdVal = String(d.projectId ?? "");
        const projSel = settingsModalEl?.querySelector("[data-pm-challenge-settings-project]");
        if (projSel instanceof HTMLSelectElement) projectIdVal = String(projSel.value || "");
        if (workKindVal === "internal") projectIdVal = ""; // 社内工数は関連案件を持たない（サーバも解除する）
        const res = await fetch("/api/progress-management", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            action: "updateChallengeSettings",
            challengeId,
            statuses,
            categories,
            timeTrackedStatuses,
            requiredSteps,
            projectId: projectIdVal,
            workKind: workKindVal,
            ...(memberUserIds !== undefined ? { memberUserIds } : {}),
            ...(tplPayload.templates !== undefined ? { agendaTemplates: tplPayload.templates } : {})
          })
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToastError(typeof j.error === "string" ? j.error : "保存に失敗しました");
          return;
        }
        // サーバーが実際にDBへ保存した値で同期（永続化の真実）
        const savedStatuses = Array.isArray(j.statuses) && j.statuses.length ? j.statuses : statuses;
        const savedCategories = Array.isArray(j.categories) ? j.categories : categories;
        // 送ったのに保存されていない状況があれば永続化失敗として警告
        const missing = statuses.filter((s) => !savedStatuses.includes(s));
        applyProgressChallengeSettingsUpdatedLocal(challengeId, {
          statuses: savedStatuses,
          categories: savedCategories,
          timeTrackedStatuses: j.timeTrackedStatuses !== undefined ? j.timeTrackedStatuses : timeTrackedStatuses,
          requiredSteps: j.requiredSteps !== undefined ? j.requiredSteps : requiredSteps,
          projectId: j.projectId !== undefined ? j.projectId : (projectIdVal ? Number(projectIdVal) : null),
          workKind: j.workKind !== undefined ? j.workKind : workKindVal,
          memberUserIds: j.memberUserIds
        });
        applyProgressAgendaTemplatesLocal(challengeId, j.agendaTemplates);
        if (missing.length) {
          // エラーがある場合はモーダルを閉じずにメッセージを表示してユーザーが確認できるようにする
          showToast(`保存に問題があります（${missing.join("・")} が保存されませんでした）`, { variant: "error" });
          // モーダルを閉じない（ユーザーが再試行できる状態を維持）
        } else {
          // 保存後も閉じずに同じタブのまま開いておく（続けて他の設定を触れるように）。
          // 下書きは保存済みの値で作り直す（押し忘れ救済で足した項目・メンバー変更済みフラグ等を持ち越さない）
          const savedCh = findProgressChallenge(String(challengeId));
          if (savedCh) openProgressChallengeSettingsModal(savedCh, uiState.progressChallengeSettingsDraft?.tab);
          const autoMsg = autoAddedItems.length ? `（${autoAddedItems.join("・")}も自動追加しました）` : "";
          showToast(`${PM_WORD.theme}設定を保存しました${autoMsg}`);
        }
        render();
        void refreshProgressManagementFromServer({
          listMode: normalizeProgressListMode(uiState.progressListMode),
          scope: "full",
          includeChallengeId: challengeId
        });
      } finally {
        uiState.progressChallengeSettingsSubmitting = false;
        if (uiState.progressShowChallengeSettings) render();
      }
    })();
    return true;
  }
  const pmSubmitChallengeEdit = target.closest("[data-pm-submit-challenge-edit]");
  if (pmSubmitChallengeEdit instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    if (uiState.progressChallengeModalSubmitting) return true;
    const d = uiState.progressChallengeEditDraft || { challengeId: "", title: "", body: "", genre: "" };
    const challengeId = Number(d.challengeId);
    const title = String(d.title || "").trim();
    const body = String(d.body || "").trim();
    const genre = PM_TODO_GROUP_CATEGORY_OPTIONS.includes(String(d.genre || "").trim())
      ? String(d.genre).trim()
      : "";
    if (!Number.isFinite(challengeId) || challengeId < 1) return true;
    if (!title) {
      showToast("タイトルを入力してください");
      return true;
    }
    const memberUserIds = progressChallengeMembersPayload(d);
    if (memberUserIds && !memberUserIds.length) {
      showToast("参加メンバーを1人以上選んでください");
      return true;
    }
    uiState.progressChallengeModalSubmitting = true;
    setProgressChallengeModalSubmitBusy(true, { edit: true });
    const fields = { title, body, genre, memberUserIds };
    void (async () => {
      let ok = false;
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "updateChallenge", challengeId, title, body, genre, memberUserIds } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "更新に失敗しました");
          return;
        }
        if (j.memberUserIds !== undefined) fields.memberUserIds = j.memberUserIds;
        applyProgressChallengeUpdatedLocal(challengeId, fields);
        uiState.progressShowChallengeEditForm = false;
        uiState.progressChallengeEditDraft = { challengeId: "", title: "", body: "" };
        uiState.progressChallengeModalSubmitting = false;
        uiState.suppressComposerDrawerAnimation = true;
        showToast(`${PM_WORD.theme}を更新しました`);
        render();
        ok = true;
        void refreshProgressManagementFromServer({
          listMode: normalizeProgressListMode(uiState.progressListMode),
          scope: "summary",
          includeChallengeId: challengeId
        });
      } finally {
        uiState.progressChallengeModalSubmitting = false;
        if (!ok) {
          setProgressChallengeModalSubmitBusy(false, { edit: true });
          render();
        }
      }
    })();
    return true;
  }
  const pmQuickAddOpen = target.closest("[data-pm-quick-add-issue]");
  if (pmQuickAddOpen instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    uiState.progressQuickAddPickerOpen = true;
    render();
    return true;
  }
  const pmQuickAddClose = target.closest("[data-pm-quick-add-close]");
  if (pmQuickAddClose instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    uiState.progressQuickAddPickerOpen = false;
    render();
    return true;
  }
  const pmQuickAddPick = target.closest("[data-pm-quick-add-pick]");
  if (pmQuickAddPick instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const chId = String(pmQuickAddPick.getAttribute("data-pm-quick-add-pick") || "").trim();
    if (!chId) return true;
    uiState.progressQuickAddPickerOpen = false;
    uiState.progressShowIssueAddForm = true;
    uiState.progressAgendaEditAgendaId = "";
    uiState.progressAgendaCreateDraft = defaultProgressAgendaCreateDraft();
    setProgressManagementHash(undefined, chId, undefined);
    return true;
  }
  const pmToggleIssueForm = target.closest("[data-pm-toggle-issue-form]");
  if (pmToggleIssueForm instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (progressManagementIssueDetailRouteActive()) return true;
    const next = !uiState.progressShowIssueAddForm;
    if (next) {
      uiState.progressShowIssueAddForm = true;
      uiState.progressAgendaEditAgendaId = "";
      uiState.progressAgendaCreateDraft = defaultProgressAgendaCreateDraft();
    } else {
      closeProgressAgendaFormModal();
    }
    render();
    return true;
  }
  const closePmAgendaCreate = target.closest("[data-close-pm-agenda-create]");
  if (closePmAgendaCreate instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (uiState.progressAgendaModalSubmitting) return true;
    // ドロップで追加した添付は input イベントを伴わないため、保留中の添付があれば入力ありとみなす
    const hasPendingFiles = (uiState.progressAgendaModalPendingFiles || []).length > 0;
    if (!fpConfirmDiscardDirtyForm("agendaForm", hasPendingFiles)) return true;
    closeProgressAgendaFormModal();
    render();
    return true;
  }
  const pmEditAgenda = target.closest("[data-pm-edit-agenda]");
  if (pmEditAgenda instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    if (progressManagementIssueDetailRouteActive()) return true;
    document.querySelectorAll("details[data-pm-kan-dd]").forEach((d) => {
      if (d instanceof HTMLDetailsElement) d.open = false;
    });
    const id = Number(pmEditAgenda.dataset.pmEditAgenda);
    if (!Number.isFinite(id) || id < 1) return true;
    const hit = findProgressAgendaById(id);
    if (!hit?.agenda) {
      showToast("課題が見つかりません");
      return true;
    }
    uiState.progressShowIssueAddForm = true;
    uiState.progressAgendaEditAgendaId = String(id);
    uiState.progressAgendaCreateDraft = progressAgendaDraftFromAgenda(hit.agenda);
    render();
    return true;
  }
  // ── 課題キー＋件名をコピー（詳細ページのリンクボタン／ボードのカードメニュー。Shift+クリックでURLのみ） ──
  const pmCopyAgendaTitled = target.closest("[data-pm-copy-agenda-titled]");
  if (pmCopyAgendaTitled instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    // ボードのカードメニューから押されたときは、開いたままのメニューを閉じる
    document.querySelectorAll("details[data-pm-kan-dd]").forEach((d) => {
      if (d instanceof HTMLDetailsElement) d.open = false;
    });
    const id = Number(pmCopyAgendaTitled.dataset.pmCopyAgendaTitled);
    const payload = progressAgendaTitledCopyPayload(id);
    if (!payload) {
      showToastError("課題のURLを作成できませんでした");
      return true;
    }
    // Shift+クリックはURLのみ（Backlogの「課題キーのみコピー」に相当）
    if (e.shiftKey) {
      const okMsg = "URLをコピーしました";
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(payload.url).then(() => showToast(okMsg)).catch(() => prompt("URL", payload.url));
      } else {
        prompt("URL", payload.url);
      }
      return true;
    }
    const done = () => showToast("課題キーと件名をコピーしました");
    const fallback = () => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(payload.text).then(done).catch(() => prompt("課題キーと件名", payload.text));
      } else {
        prompt("課題キーと件名", payload.text);
      }
    };
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      try {
        const item = new ClipboardItem({
          "text/html": new Blob([payload.html], { type: "text/html" }),
          "text/plain": new Blob([payload.text], { type: "text/plain" })
        });
        navigator.clipboard.write([item]).then(done).catch(fallback);
      } catch {
        fallback();
      }
    } else {
      fallback();
    }
    return true;
  }
  // ── 課題を複製（同じテーマに新規作成。「リンクをコピー」と紛らわしいので文言は「複製」で統一） ──
  const pmDuplicateAgenda = target.closest("[data-pm-duplicate-agenda]");
  if (pmDuplicateAgenda instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    document.querySelectorAll("details[data-pm-kan-dd]").forEach((d) => {
      if (d instanceof HTMLDetailsElement) d.open = false;
    });
    const id = Number(pmDuplicateAgenda.dataset.pmDuplicateAgenda);
    const hit = findProgressAgendaById(id);
    if (!hit?.agenda || !hit?.challenge) { showToast("課題が見つかりません"); return true; }
    const ag = hit.agenda;
    const challengeId = Number(hit.challenge.id);
    if (!authToken) return true;
    // 二重送信防止（連打で課題が多重複製されるのを防ぐ）。確認ダイアログより先に判定する —
    // 後ろに置くと、送信中の2回目で「確認したのに何も起こらない」状態になる
    if (uiState._pmDuplicateAgendaInFlight) return true;
    const srcTitle = String(ag.title || "").trim();
    const themeName = String(hit.challenge.title || "").trim() || PM_WORD.theme;
    // 複製は押した瞬間に課題が増えるため、誤操作を取り消せるよう確認する
    if (!window.confirm(`「${srcTitle || "（無題）"}」を複製して、${PM_WORD.theme}「${themeName}」に新しい課題を作成します。よろしいですか？`)) return true;
    uiState._pmDuplicateAgendaInFlight = true;
    void (async () => {
      try {
        const assignedUserId = ag.assignedUserId
          ? progressAgendaAssignedUserIdForApi(String(ag.assignedUserId))
          : null;
        // 送信本文とローカル即時反映で同じ値を使う（片方だけ変えると画面とDBがズレる）。
        // 状況はテーマの先頭状況にする — 固定値だとテーマの状況リストに無い値になり、
        // ボードのどの列にも入らず「複製したのに出てこない」状態になる
        const fields = {
          title: `${srcTitle}（複製）`,
          body: String(ag.body || ""),
          category: normalizeProgressAgendaCategory(ag.category),
          status: normalizeProgressTodoStatusForChallenge("", hit.challenge),
          priority: normalizeProgressAgendaPriority(ag.priority),
          startDate: null,
          dueDate: null,
          assignedUserId,
          clientId: ag.clientId ?? null
        };
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "createAgenda", challengeId, ...fields } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "複製に失敗しました");
          return;
        }
        let filterCleared = false;
        if (j.id != null) {
          const newAgenda = buildProgressAgendaFromCreatePayload(challengeId, j.id, fields);
          applyProgressAgendaCreatedLocal(challengeId, newAgenda);
          filterCleared = clearProgressBoardFiltersHidingAgenda(newAgenda);
        }
        showToast(filterCleared ? "課題を複製しました（表示フィルタを解除しました）" : "課題を複製しました");
        render();
        void refreshProgressManagementFromServer({
          listMode: normalizeProgressListMode(uiState.progressListMode),
          scope: "summary",
          includeChallengeId: String(challengeId)
        });
      } catch {
        showToastError("複製に失敗しました");
      } finally {
        uiState._pmDuplicateAgendaInFlight = false;
      }
    })();
    return true;
  }
  const pmSelAg = target.closest("[data-pm-select-agenda]");
  if (pmSelAg instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (typeof window !== "undefined" && window.__pmKanbanSuppressAgendaClickUntil && Date.now() < window.__pmKanbanSuppressAgendaClickUntil) {
      return true;
    }
    document.querySelectorAll("details[data-pm-kan-dd]").forEach((d) => {
      if (d instanceof HTMLDetailsElement) d.open = false;
    });
    const id = String(pmSelAg.dataset.pmSelectAgenda || "");
    const route = currentRoute();
    const pid = String(route.searchParams?.get("project") || "").trim();
    uiState.progressSelectedAgendaId = id;
    if (pid) setProgressManagementHash(null, pid, id);
    else render();
    return true;
  }
  const pmSubChModal = target.closest("[data-pm-submit-challenge-modal]");
  if (pmSubChModal instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    if (uiState.progressChallengeModalSubmitting) return true;
    const d = uiState.progressChallengeCreateDraft || { title: "", body: "", genre: "" };
    const title = String(d.title || "").trim();
    const body = String(d.body || "").trim();
    const genre = PM_TODO_GROUP_CATEGORY_OPTIONS.includes(String(d.genre || "").trim())
      ? String(d.genre).trim()
      : "";
    if (!title) {
      showToast("タイトルを入力してください");
      return true;
    }
    const memberUserIds = progressChallengeMembersPayload(d);
    if (memberUserIds && !memberUserIds.length) {
      showToast("参加メンバーを1人以上選んでください");
      return true;
    }
    uiState.progressChallengeModalSubmitting = true;
    setProgressChallengeModalSubmitBusy(true);
    // フォルダ1段化: 所属グループはサーバ側で既定（"未分類"）に解決する
    const fields = { title, body, genre, memberUserIds };
    void (async () => {
      let ok = false;
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "createChallenge", title, body, genre, memberUserIds } });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "登録に失敗しました");
          return;
        }
        const listMode = normalizeProgressListMode(uiState.progressListMode);
        const newTodoGroupId = j.todoGroupId != null ? Number(j.todoGroupId) : null;
        if (j.memberUserIds !== undefined) fields.memberUserIds = j.memberUserIds; // サーバが保存した値（本人の自動追加を含む）
        if (j.id != null && listMode === "active") {
          applyProgressChallengeCreatedLocal(
            buildProgressChallengeFromCreatePayload(j.id, fields, newTodoGroupId)
          );
          if (hasProgressListCache("active")) {
            setProgressListCache("active", {
              groups: uiState.progressTodoGroups,
              challenges: uiState.progressChallenges
            });
          }
          invalidateProgressGroupListCache();
        }
        closeProgressChallengeFormModal();
        markProgressListFetched(listMode);
        uiState.suppressComposerDrawerAnimation = true;
        showToast(`${PM_WORD.theme}を登録しました`);
        render();
        ok = true;
        void refreshProgressManagementFromServer({ listMode, scope: "summary" });
      } finally {
        uiState.progressChallengeModalSubmitting = false;
        if (!ok) {
          setProgressChallengeModalSubmitBusy(false);
          render();
        }
      }
    })();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 1997〜3096 行）。処理したら true（16 側はそこで止める） */
function handleProgressBoardClick(e, target) {
  const pmSubAgModal = target.closest("[data-pm-submit-agenda-modal]");
  if (pmSubAgModal instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    if (uiState.progressAgendaModalSubmitting) return true;
    const challengeId = Number(pmSubAgModal.dataset.pmAgendaChallengeId);
    const editAgendaId = Number(pmSubAgModal.dataset.pmAgendaEditId);
    const isEdit = Number.isFinite(editAgendaId) && editAgendaId >= 1;
    if (!isEdit && (!Number.isFinite(challengeId) || challengeId < 1)) return true;
    const d = mergedProgressAgendaCreateDraftForModal();
    const title = String(d.title || "").trim();
    const body = String(d.body || "").trim();
    const category = normalizeProgressAgendaCategory(d.category);
    const status = normalizeProgressTodoStatus(d.status);
    const priority = normalizeProgressAgendaPriority(d.priority);
    const startDate = String(d.startDate || "").trim() || null;
    const dueDate = String(d.dueDate || "").trim() || null;
    const assignedUserIdRaw =
      d.assignedUserId != null && String(d.assignedUserId).trim() !== ""
        ? String(d.assignedUserId).trim()
        : null;
    const assignedUserId = assignedUserIdRaw ? progressAgendaAssignedUserIdForApi(assignedUserIdRaw) : null;
    if (!title) {
      showToast("課題のタイトルを入力してください");
      return true;
    }
    if (!isEdit && !assignedUserId) {
      showToast("担当者を選択してください");
      return true;
    }
    // クライアントは制作以外のプロジェクトでも登録できる（TASK-373）。入力欄の値をそのまま送る。
    const clientId = progressAgendaClientIdForApi(d.clientId);
    // 編集で状況を変更する場合、工程順の後戻り（差し戻し）なら理由入力ゲートを通す
    // （この保存経路だけ未配線だと、サーバーの evaluateAgendaStatusTransition が理由必須で
    // 400 を返すだけで、この画面には理由を入力する欄が無いため保存が永久にできなくなる）。
    const editGateFound = isEdit ? findProgressAgendaById(editAgendaId) : null;
    void (async () => {
      let ok = false;
      let rejectionReason = "";
      try {
        if (isEdit && editGateFound?.agenda && String(editGateFound.agenda.status || "").trim() !== status) {
          const gate = await requestProgressAgendaStatusChange(editGateFound.agenda, editGateFound.challenge, status);
          if (!gate.proceed) return;
          rejectionReason = gate.rejectionReason;
        }
        uiState.progressAgendaModalSubmitting = true;
        setProgressAgendaModalSubmitBusy(true);
        const agendaFields = {
          title,
          body,
          category,
          status,
          priority,
          startDate,
          dueDate,
          assignedUserId,
          ...(clientId !== undefined ? { clientId } : {})
        };
        const payload = isEdit
          ? {
              action: "updateAgenda",
              agendaId: editAgendaId,
              title,
              body,
              category,
              status,
              priority,
              startDate,
              dueDate,
              assignedUserId,
              ...(clientId !== undefined ? { clientId } : {}),
              ...(rejectionReason ? { rejectionReason } : {})
            }
          : {
              action: "createAgenda",
              challengeId,
              title,
              body,
              category,
              status,
              priority,
              startDate,
              dueDate,
              assignedUserId,
              notifyUserIds: progressNotifyUserIdsFromRaw(d.notifyRaw),
              ...(clientId !== undefined ? { clientId } : {})
            };
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: payload });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : isEdit ? "保存に失敗しました" : "登録に失敗しました");
          return;
        }
        const targetAgendaId = isEdit ? editAgendaId : j.id != null ? Number(j.id) : NaN;
        let filterCleared = false;
        if (isEdit) {
          applyProgressAgendaUpdatedLocal(editAgendaId, agendaFields);
        } else if (j.id != null) {
          const newAgenda = buildProgressAgendaFromCreatePayload(challengeId, j.id, agendaFields);
          applyProgressAgendaCreatedLocal(challengeId, newAgenda);
          filterCleared = clearProgressBoardFiltersHidingAgenda(newAgenda);
        }
        let attachN = 0;
        if (Number.isFinite(targetAgendaId) && targetAgendaId >= 1) {
          try {
            attachN = await uploadProgressAgendaPendingModalFiles(targetAgendaId);
          } catch (err) {
            showToastError(err instanceof Error ? err.message : "添付の保存に失敗しました");
          }
        }
        closeProgressAgendaFormModal();
        uiState.suppressComposerDrawerAnimation = true;
        const doneMsg = isEdit
          ? attachN > 0
            ? `課題を更新し、添付を${attachN}件登録しました`
            : "課題を更新しました"
          : attachN > 0
            ? `課題を追加し、添付を${attachN}件登録しました`
            : "課題を追加しました";
        showToast(filterCleared ? `${doneMsg}（表示フィルタを解除しました）` : doneMsg);
        render();
        ok = true;
        const syncChallengeId = isEdit
          ? String(findProgressAgendaById(editAgendaId)?.challenge?.id || challengeId)
          : String(challengeId);
        void refreshProgressManagementFromServer({ includeChallengeId: syncChallengeId });
      } finally {
        uiState.progressAgendaModalSubmitting = false;
        if (!ok) {
          setProgressAgendaModalSubmitBusy(false);
          render();
        }
      }
    })();
    return true;
  }
  const pmIssueCloseBoard = target.closest("[data-pm-issue-close-board]");
  if (pmIssueCloseBoard instanceof HTMLElement && currentRoute().page === "progress-management") {
    uiState.progressEditingMessageId = "";
    const closeRoute = currentRoute();
    const closeSp = new URLSearchParams(closeRoute.searchParams);
    const closeFromAssigned = closeSp.get("from") === "assigned";
    if (closeFromAssigned) {
      // 自分の担当課題から来た → 自分の担当課題へ戻る
      location.hash = "progress-management?assigned=me";
    } else {
      // 課題ボードから来た → ボードへ戻る
      const boardPid = String(pmIssueCloseBoard.dataset.pmIssueCloseBoard || "").trim();
      if (boardPid) setProgressManagementHash(null, boardPid, "");
      else location.hash = "progress-management?assigned=me";
    }
    return true;
  }
  const pmEditMsg = target.closest("[data-pm-edit-message]");
  if (pmEditMsg instanceof HTMLElement && currentRoute().page === "progress-management") {
    const id = String(pmEditMsg.dataset.pmEditMessage || "").trim();
    if (id) {
      uiState.progressEditingMessageId = id;
      render();
    }
    return true;
  }
  const pmCancelEditMsg = target.closest("[data-pm-cancel-edit-message]");
  if (pmCancelEditMsg instanceof HTMLElement && currentRoute().page === "progress-management") {
    uiState.progressEditingMessageId = "";
    render();
    return true;
  }
  const pmWatchToggle = target.closest("[data-pm-watch-toggle]");
  if (pmWatchToggle instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.stopPropagation();
    e.preventDefault();
    if (!authToken) return true;
    const agendaId = Number(pmWatchToggle.dataset.pmWatchToggle);
    if (!Number.isFinite(agendaId) || agendaId < 1) return true;
    if (String(uiState.progressWatchTogglingId) === String(agendaId)) return true;
    const hit = findProgressAgendaById(agendaId);
    if (!hit?.agenda) return true;
    const watched = pmWatchToggle.getAttribute("aria-pressed") === "true";
    const nextWatched = !watched;
    const action = watched ? "unwatchAgenda" : "watchAgenda";
    const snapshot = progressAgendaWatchSnapshot(hit.agenda);
    uiState.progressWatchTogglingId = String(agendaId);
    applyProgressAgendaWatchLocal(agendaId, nextWatched);
    render();
    void (async () => {
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action, agendaId } });
        if (!apiOk) {
          syncProgressAgendaWatchFields(agendaId, snapshot);
          recalcProgressWatchUnreadFromTree();
          refreshSidebarUserChrome();
          render();
          showToastError(typeof j.error === "string" ? j.error : "ウォッチの更新に失敗しました");
          return;
        }
        showToast(watched ? "ウォッチを解除しました" : "ウォッチしました");
      } catch {
        syncProgressAgendaWatchFields(agendaId, snapshot);
        recalcProgressWatchUnreadFromTree();
        refreshSidebarUserChrome();
        render();
        showToastError("通信エラーが発生しました");
      } finally {
        uiState.progressWatchTogglingId = "";
      }
    })();
    return true;
  }
  const pmBoardWatchOnlyBtn = target.closest("[data-pm-board-watch-only]");
  if (pmBoardWatchOnlyBtn instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    uiState.progressBoardWatchOnly = !uiState.progressBoardWatchOnly;
    render();
    return true;
  }
  const pmBoardAssigneeOnlyBtn = target.closest("[data-pm-board-assignee-only]");
  if (pmBoardAssigneeOnlyBtn instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    uiState.progressBoardAssigneeOnly = !uiState.progressBoardAssigneeOnly;
    render();
    return true;
  }
  const pmIssueSearchClearBtn = target.closest("[data-pm-issue-search-clear]");
  if (pmIssueSearchClearBtn instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    uiState.progressIssueSearch = "";
    render();
    requestAnimationFrame(() => {
      const el = document.querySelector("input[data-pm-issue-search]");
      if (el instanceof HTMLInputElement) el.focus();
    });
    return true;
  }
  // テーマドロップダウン: トリガーボタン（開閉）
  const pmMyAssignThemeDdToggle = target.closest("[data-pm-my-assign-theme-dd-toggle]");
  if (pmMyAssignThemeDdToggle instanceof HTMLElement && currentRoute().page === "progress-management") {
    uiState.progressMyAssignThemeDdOpen = !uiState.progressMyAssignThemeDdOpen;
    render();
    return true;
  }
  // テーマドロップダウン: アイテム選択
  const pmMyAssignThemeDdPick = target.closest("[data-pm-my-assign-theme-dd-pick]");
  if (pmMyAssignThemeDdPick instanceof HTMLElement && currentRoute().page === "progress-management") {
    const tab = normalizeProgressMyAssignTab(pmMyAssignThemeDdPick.getAttribute("data-pm-my-assign-tab"));
    uiState.progressMyAssignListFilter = tab;
    uiState.progressMyAssignThemeDdOpen = false;
    render();
    return true;
  }
  // ウォッチ中トグル（テーマの横）
  const pmMyAssignWatchToggle = target.closest("[data-pm-my-assign-watch-toggle]");
  if (pmMyAssignWatchToggle instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!progressManagementAssignedMeRouteActive()) return true;
    uiState.progressMyAssignWatchOnly = !uiState.progressMyAssignWatchOnly;
    uiState.progressMyAssignThemeDdOpen = false;
    render();
    return true;
  }
  const pmMyAssignHideHoldToggle = target.closest("[data-pm-my-assign-hide-hold-toggle]");
  if (pmMyAssignHideHoldToggle instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!progressManagementAssignedMeRouteActive()) return true;
    uiState.progressMyAssignHideHold = !uiState.progressMyAssignHideHold;
    uiState.progressMyAssignThemeDdOpen = false;
    render();
    return true;
  }
  // テーマドロップダウン: 外側クリックで閉じる
  if (uiState.progressMyAssignThemeDdOpen && !target.closest(".pm-my-assign-theme-dd")) {
    uiState.progressMyAssignThemeDdOpen = false;
    render();
  }
  const pmMyAssignTab = target.closest("[data-pm-my-assign-tab]");
  if (pmMyAssignTab instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!progressManagementAssignedMeRouteActive()) return true;
    const tab = normalizeProgressMyAssignTab(pmMyAssignTab.getAttribute("data-pm-my-assign-tab"));
    if (normalizeProgressMyAssignTab(uiState.progressMyAssignListFilter) === tab) return true;
    uiState.progressMyAssignListFilter = tab;
    render();
    return true;
  }
  const pmMyAssignShowAll = target.closest("[data-pm-my-assign-show-all]");
  if (pmMyAssignShowAll instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!progressManagementAssignedMeRouteActive()) return true;
    if (
      normalizeProgressMyAssignTab(uiState.progressMyAssignListFilter) === "all" &&
      normalizeProgressMyAssignQuickFilter(uiState.progressMyAssignQuickFilter) === "all" &&
      !uiState.progressMyAssignWatchOnly &&
      !uiState.progressMyAssignHideHold
    ) {
      return true;
    }
    resetProgressMyAssignFilters();
    render();
    return true;
  }
  const pmMyAssignQuickFilter = target.closest("[data-pm-my-assign-quick-filter]");
  if (pmMyAssignQuickFilter instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!progressManagementAssignedMeRouteActive()) return true;
    const f = normalizeProgressMyAssignQuickFilter(
      pmMyAssignQuickFilter.getAttribute("data-pm-my-assign-quick-filter")
    );
    const cur = normalizeProgressMyAssignQuickFilter(uiState.progressMyAssignQuickFilter);
    uiState.progressMyAssignQuickFilter = cur === f ? "all" : f;
    render();
    return true;
  }
  // ── SP バックログ風UI ──
  // 属性行タップ → シートを開く
  const spOpenAttr = target.closest("[data-pm-sp-open-attr]");
  if (spOpenAttr instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const field = String(spOpenAttr.dataset.pmSpOpenAttr || "");
    const agendaId = String(spOpenAttr.dataset.pmSpAgenda || "");
    if (!field || !agendaId) return true;
    const found = findProgressAgendaById(Number(agendaId));
    if (!found?.agenda) return true;
    const ag = found.agenda;
    const ch = found.challenge;
    // 期限日はデートピッカーで対応するためシートは使わない
    if (field === "dueDate") return true;
    // 現在値を selected にセット
    let currentVal = "";
    if (field === "status") {
      currentVal = normalizeProgressTodoStatusForChallenge(ag.status, ch);
    } else if (field === "category") {
      currentVal = normalizeProgressAgendaCategory(ag.category);
    } else if (field === "assignee") {
      const pool = assignableUsers();
      const dbAssignee = ag.assignedUserId != null && Number.isFinite(Number(ag.assignedUserId)) ? Number(ag.assignedUserId) : null;
      if (dbAssignee != null) {
        // 候補（SP の初回は自分だけのこともある）に今の担当者がいなくても「未設定」扱いにしない。
        // 以前は「未設定」にチェックが付き、そのまま保存すると担当者が外れていた
        const row =
          pool.find((u) => u.dbId != null && Number(u.dbId) === dbAssignee) ||
          users.find((u) => u.dbId != null && Number(u.dbId) === dbAssignee);
        currentVal = row ? String(row.id) : PM_SP_SHEET_KEEP_ASSIGNEE;
      }
    } else if (field === "priority") {
      currentVal = normalizeProgressAgendaPriority(ag.priority);
    }
    uiState.pmSpAttrSheet = { agendaId, field, selected: currentVal };
    progressSpInsertSelectionSheet(ag, ch);
    return true;
  }
  // シートのオプション選択（選択だけ変えてDOM更新）
  const spSelOpt = target.closest("[data-pm-sp-sel-opt]");
  if (spSelOpt instanceof HTMLElement && uiState.pmSpAttrSheet) {
    const newSelected = String(spSelOpt.dataset.pmSpSelOpt ?? "");
    uiState.pmSpAttrSheet = { ...uiState.pmSpAttrSheet, selected: newSelected };
    // 既存シートのチェックマークのみ更新（再レンダリングなし）
    const sheetBody = document.querySelector(".pm-sp-sheet .pm-sp-sheet-body");
    if (sheetBody) {
      sheetBody.querySelectorAll(".pm-sp-sel-check").forEach(el => el.remove());
      const optRow = sheetBody.querySelector(`[data-pm-sp-sel-opt="${CSS.escape(newSelected)}"]`);
      if (optRow) {
        const check = document.createElement("span");
        check.className = "pm-sp-sel-check";
        check.textContent = "✓";
        optRow.appendChild(check);
      }
    } else {
      render();
    }
    return true;
  }
  // シート保存
  if (target.closest("[data-pm-sp-sel-save]") && uiState.pmSpAttrSheet) {
    if (!authToken) return true;
    const sheet = { ...uiState.pmSpAttrSheet };
    uiState.pmSpAttrSheet = null;
    const numId = Number(sheet.agendaId);
    if (!Number.isFinite(numId) || numId < 1) { render(); return true; }
    const found = findProgressAgendaById(numId);
    if (!found?.agenda) { render(); return true; }
    const ag = found.agenda;
    const ch = found.challenge;
    const selectedVal = String(sheet.selected != null ? sheet.selected : "");
    // 候補に無い今の担当者のまま保存した＝変更なし（未設定へ外さない）
    if (sheet.field === "assignee" && selectedVal === PM_SP_SHEET_KEEP_ASSIGNEE) {
      document.querySelectorAll(".pm-sp-sheet-backdrop, .pm-sp-sheet").forEach((el) => el.remove());
      return true;
    }
    // コンポーズモード：直接保存せず pending に保持してコメント送信時に一緒に保存
    if (sheet.composeMode) {
      if (sheet.field === "status") uiState.pmSpComposePendingStatus = selectedVal || null;
      if (sheet.field === "assignee") uiState.pmSpComposePendingAssignee = selectedVal || null;
      // シートを閉じてコンポーズフッターのチップだけ更新
      document.querySelectorAll(".pm-sp-sheet-backdrop, .pm-sp-sheet").forEach(el => el.remove());
      progressRefreshComposeFooterDom(String(sheet.agendaId), ag, ch);
      return true;
    }
    // 属性カードのタップ→保存：シートを閉じて属性カードのみ更新
    document.querySelectorAll(".pm-sp-sheet-backdrop, .pm-sp-sheet").forEach(el => el.remove());
    void (async () => {
      if (sheet.field === "status") {
        const gate = await requestProgressAgendaStatusChange(ag, ch, selectedVal);
        if (!gate.proceed) return;
        const r = await commitProgressAgendaStatusChange(numId, selectedVal, { rejectionReason: gate.rejectionReason });
        if (!r.ok) showToastError(r.error || "状況の変更に失敗しました");
        else showToast(`状況を「${r.savedStatus || selectedVal}」に変更しました`);
      } else {
        const before = captureProgressAgendaDetailSnapshot(numId);
        const payload = { action: "updateAgendaDetail", agendaId: numId };
        if (sheet.field === "category") {
          payload.category = normalizeProgressAgendaCategory(selectedVal);
        } else if (sheet.field === "priority") {
          payload.priority = normalizeProgressAgendaPriority(selectedVal);
        } else if (sheet.field === "assignee") {
          const apiId = selectedVal ? progressAgendaAssignedUserIdForApi(selectedVal) : null;
          payload.assignedUserId = apiId;
        }
        try {
          const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: payload });
          if (!apiOk) {
            showToastError(typeof j.error === "string" ? j.error : "更新に失敗しました");
            return;
          }
          applyProgressAgendaDetailFromPayload(numId, payload);
          const lines = progressAgendaDetailChangeLogLines(
            currentUser?.name || authUser?.name,
            before,
            payload
          );
          if (lines.length) appendProgressAgendaSystemMessageLocal(numId, lines.join("\n"));
          showToast("属性を保存しました");
          const route = currentRoute();
          const pid = String(route.searchParams?.get("project") || "").trim();
          if (pid) setProgressManagementHash(null, pid, String(numId));
          else render();
        } catch {
          showToastError("更新に失敗しました");
        }
      }
    })();
    return true;
  }
  // シートキャンセル / バックドロップタップ
  if ((target.closest("[data-pm-sp-sel-cancel]") || target.closest("[data-pm-sp-sheet-backdrop]")) && uiState.pmSpAttrSheet) {
    uiState.pmSpAttrSheet = null;
    // DOMからシートを直接削除（render()不要）
    document.querySelectorAll(".pm-sp-sheet-backdrop, .pm-sp-sheet").forEach(el => el.remove());
    return true;
  }
  // コメントをもっと見る
  if (target.closest("[data-pm-sp-expand-comments]") && currentRoute().page === "progress-management") {
    uiState.pmSpCommentsExpanded = true;
    render();
    return true;
  }
  // SP コメント入力シートを開く
  if (target.closest("[data-pm-sp-compose-open]") && currentRoute().page === "progress-management") {
    uiState.pmSpComposeOpen = true;
    render();
    requestAnimationFrame(() => {
      const ta = document.querySelector(".pm-sp-compose-sheet [data-pm-msg-body]");
      if (ta instanceof HTMLTextAreaElement) ta.focus();
    });
    return true;
  }
  // SP コメント入力シートを閉じる
  if ((target.closest("[data-pm-sp-compose-close]") || target.closest(".pm-sp-compose-backdrop")) && currentRoute().page === "progress-management") {
    uiState.pmSpComposeOpen = false;
    uiState.pmSpComposePendingStatus = null;
    uiState.pmSpComposePendingAssignee = null;
    render();
    return true;
  }
  // SP コンポーズチップの × → pending をクリア（✏️ボタンより先に処理）
  const spComposeClear = target.closest("[data-pm-sp-compose-clear]");
  if (spComposeClear instanceof HTMLElement && currentRoute().page === "progress-management") {
    const which = spComposeClear.dataset.pmSpComposeClear;
    if (which === "status") uiState.pmSpComposePendingStatus = null;
    if (which === "assignee") uiState.pmSpComposePendingAssignee = null;
    render();
    return true;
  }
  // SP 状態/担当者ボタンを直接タップ → 選択シートをコンポーズモードで開く
  const spComposeSelect = target.closest("[data-pm-sp-compose-select]");
  if (spComposeSelect instanceof HTMLElement && currentRoute().page === "progress-management") {
    const field = spComposeSelect.dataset.pmSpComposeSelect;
    const agendaId = spComposeSelect.dataset.pmSpComposeAgenda;
    const found = findProgressAgendaById(Number(agendaId));
    if (!found?.agenda) return true;
    const ag = found.agenda;
    const ch = found.challenge;
    let currentVal = "";
    if (field === "status") {
      currentVal = uiState.pmSpComposePendingStatus || normalizeProgressTodoStatusForChallenge(ag.status, ch);
    } else if (field === "assignee") {
      const dbAssignee = ag.assignedUserId != null ? Number(ag.assignedUserId) : null;
      if (uiState.pmSpComposePendingAssignee) {
        currentVal = uiState.pmSpComposePendingAssignee;
      } else if (dbAssignee != null) {
        const row = users.find((u) => u.dbId != null && Number(u.dbId) === dbAssignee);
        currentVal = row ? String(row.id) : "";
      }
    }
    uiState.pmSpAttrSheet = { agendaId, field, selected: currentVal, composeMode: true };
    // render()は重いので選択シートのみDOM直接挿入（全ページ再描画なし）
    progressSpInsertSelectionSheet(ag, ch);
    return true;
  }

  // ── 課題詳細「閉じる」→ 直前のページ（ボード or 担当課題）に戻る ──
  const pmCloseIssueDetail = target.closest("[data-pm-close-issue-detail]");
  if (pmCloseIssueDetail instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const route = currentRoute();
    const sp = new URLSearchParams(route.searchParams);
    const fromAssigned = sp.get("from") === "assigned";
    if (fromAssigned || !sp.has("project")) {
      // 自分の担当課題から来た、またはプロジェクト文脈なし → 自分の担当課題へ
      location.hash = "progress-management?assigned=me";
    } else {
      // 課題ボードから来た → issue・from を除いてボードに戻る
      sp.delete("issue");
      sp.delete("from");
      const newFrag = sp.toString() ? `progress-management?${sp.toString()}` : "progress-management";
      if (location.hash.replace(/^#/, "") !== newFrag) {
        location.hash = newFrag;
      } else {
        render();
      }
    }
    return true;
  }
  const pmIssueStatusChange = target.closest("[data-pm-issue-status-change]");
  if (pmIssueStatusChange instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const rawId = String(pmIssueStatusChange.getAttribute("data-pm-issue-status-change") || "").trim();
    const id = Number(rawId);
    if (!Number.isFinite(id) || id < 1) return true;
    const hit = findProgressAgendaById(id);
    if (!hit?.agenda) {
      showToast("課題が見つかりません");
      return true;
    }
    uiState.progressShowIssueAddForm = true;
    uiState.progressAgendaEditAgendaId = String(id);
    uiState.progressAgendaCreateDraft = progressAgendaDraftFromAgenda(hit.agenda);
    render();
    return true;
  }
  // ── 進捗ステップバー: 経過時間を手動修正 ──
  const pmStepDurEdit = target.closest("[data-pm-step-dur-edit]");
  if (pmStepDurEdit instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const [rawId, stepName] = String(pmStepDurEdit.getAttribute("data-pm-step-dur-edit") || "").split(":");
    const agendaId = Number(rawId);
    if (!Number.isFinite(agendaId) || agendaId < 1 || !stepName) return true;
    const hit = findProgressAgendaById(agendaId);
    if (!hit?.agenda) {
      showToast("課題が見つかりません");
      return true;
    }
    const overrides =
      hit.agenda.stepDurationOverrides && typeof hit.agenda.stepDurationOverrides === "object"
        ? hit.agenda.stepDurationOverrides
        : {};
    const curMin = overrides[stepName];
    const defaultInput = curMin != null ? String(Math.round((Number(curMin) / 60) * 100) / 100) : "";
    const input = prompt(
      `「${stepName}」の経過時間を時間単位で入力してください（例: 7.5）。\n空にすると自動計算に戻ります。`,
      defaultInput
    );
    if (input === null) return true; // キャンセル
    const trimmed = input.trim();
    let minutes = null;
    if (trimmed !== "") {
      const hours = Number(trimmed);
      if (!Number.isFinite(hours) || hours < 0 || hours > 99999) {
        showToast("経過時間の値が不正です");
        return true;
      }
      minutes = Math.round(hours * 60);
    }
    void (async () => {
      try {
        const res = await fetch("/api/progress-management", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ action: "updateAgendaStepDurationOverride", agendaId, stepName, minutes })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToastError(data?.error || "経過時間の修正に失敗しました");
          return;
        }
        hit.agenda.stepDurationOverrides = data.stepDurationOverrides || null;
        _syncAgendaInAllCaches(agendaId, (a) => {
          a.stepDurationOverrides = data.stepDurationOverrides || null;
        });
        showToast(minutes === null ? "経過時間を自動計算に戻しました" : "経過時間を修正しました");
        render();
      } catch (_) {
        showToastError("経過時間の修正に失敗しました");
      }
    })();
    return true;
  }
  const pmSaveIssueAttrs = target.closest("[data-pm-save-issue-attrs]");
  if (pmSaveIssueAttrs instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const agendaId = Number(pmSaveIssueAttrs.dataset.pmSaveIssueAttrs);
    if (!Number.isFinite(agendaId) || agendaId < 1) return true;
    const route = currentRoute();
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    if (String(agendaId) !== issueParam) return true;
    const built = buildProgressIssueAttrPayloadFromForm(agendaId);
    if (built.error) {
      showToast(built.error);
      return true;
    }
    const attrGateFound = findProgressAgendaById(agendaId);
    const attrNewStatus = built.payload.status;
    const attrStatusChanging = attrNewStatus !== undefined && attrGateFound?.agenda &&
      String(attrGateFound.agenda.status || "").trim() !== String(attrNewStatus || "").trim();
    uiState.progressIssueAttrSaving = String(agendaId);
    setProgressIssueAttrSaveUi(agendaId, { saving: true });
    void (async () => {
      let needFullRender = false;
      try {
        if (attrStatusChanging) {
          const gate = await requestProgressAgendaStatusChange(attrGateFound.agenda, attrGateFound.challenge, attrNewStatus);
          if (!gate.proceed) return;
          built.payload.rejectionReason = gate.rejectionReason;
        }
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: built.payload });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "更新に失敗しました");
          await refreshProgressManagementFromServer();
          needFullRender = true;
          return;
        }
        const before = captureProgressAgendaDetailSnapshot(agendaId);
        applyProgressAgendaDetailFromPayload(agendaId, built.payload);
        if (!j.noop) {
          const lines = progressAgendaDetailChangeLogLines(
            currentUser?.name || authUser?.name,
            before,
            built.payload
          );
          if (lines.length) appendProgressAgendaSystemMessageLocal(agendaId, lines.join("\n"));
        }
        uiState.progressSelectedAgendaId = String(agendaId);
        if (!refreshProgressIssueDetailAfterAttrs(agendaId)) {
          const pid = String(route.searchParams?.get("project") || "").trim();
          if (pid) setProgressManagementHash(null, pid, String(agendaId));
          else needFullRender = true;
        } else {
          setProgressIssueAttrSaveUi(agendaId, { dirty: false });
        }
        if (!j.noop) showToast("属性を保存しました");
      } finally {
        uiState.progressIssueAttrSaving = "";
        setProgressIssueAttrSaveUi(agendaId, { saving: false });
        if (needFullRender) render();
      }
    })();
    return true;
  }
  const pmCancelIssueAttrs = target.closest("[data-pm-issue-attr-cancel]");
  if (pmCancelIssueAttrs instanceof HTMLElement && currentRoute().page === "progress-management") {
    const cancelAid = Number(pmCancelIssueAttrs.dataset.pmIssueAttrCancel);
    if (Number.isFinite(cancelAid) && cancelAid >= 1 && resetProgressIssueAttrFormFromAgenda(cancelAid)) return true;
    render();
    return true;
  }
  const pmSaveEditMsg = target.closest("[data-pm-save-edit-message]");
  if (pmSaveEditMsg instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmSaveEditMsg.dataset.pmSaveEditMessage);
    if (!Number.isFinite(id) || id < 1) return true;
    const wrap = pmSaveEditMsg.closest(".pm-msg-bbubble") || pmSaveEditMsg.closest(".pm-msg") || pmSaveEditMsg.closest(".pm-sp-msg");
    const ta = wrap?.querySelector("[data-pm-edit-message-body]");
    const body = ta instanceof HTMLTextAreaElement ? ta.value.trim() : "";
    if (!body) {
      showToast("本文を入力してください");
      return true;
    }
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "updateMessage", messageId: id, body } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "更新に失敗しました");
        return;
      }
      uiState.progressEditingMessageId = "";
      const updatedAgendaId = applyProgressMessageUpdatedLocal(id, body);
      const route = currentRoute();
      const pid = String(route.searchParams?.get("project") || "").trim();
      const gid = String(route.searchParams?.get("group") || "").trim();
      const issue = String(route.searchParams?.get("issue") || "").trim();
      const hydrateAgendaId =
        updatedAgendaId != null && Number.isFinite(Number(updatedAgendaId)) && Number(updatedAgendaId) >= 1
          ? Number(updatedAgendaId)
          : Number(issue);
      const onIssueDetail = progressManagementIssueDetailRouteActive(route);
      if (onIssueDetail && updatedAgendaId != null && refreshProgressIssueThreadDom(updatedAgendaId)) {
        showToast("更新しました");
        void refreshProgressManagementFromServer({
          scope: "summary",
          includeChallengeId: pid || undefined,
          includeGroupId: gid || undefined,
          hydrateAgendaId
        });
        return;
      }
      render();
      void refreshProgressManagementFromServer({
        scope: "summary",
        includeChallengeId: pid || undefined,
        includeGroupId: gid || undefined,
        hydrateAgendaId
      });
      showToast("更新しました");
    })();
    return true;
  }
  const pmComposeExpand = target.closest("[data-pm-compose-expand]");
  if (pmComposeExpand instanceof HTMLElement && currentRoute().page === "progress-management") {
    const compose = pmComposeExpand.closest(".pm-compose--collapsible");
    if (compose instanceof HTMLElement) {
      compose.classList.add("pm-compose--open");
      const ta = compose.querySelector("[data-pm-msg-body]");
      if (ta instanceof HTMLTextAreaElement) ta.focus();
    }
    return true;
  }
  const pmComposeCollapse = target.closest("[data-pm-compose-collapse]");
  if (pmComposeCollapse instanceof HTMLElement && currentRoute().page === "progress-management") {
    const compose = pmComposeCollapse.closest(".pm-compose--collapsible");
    if (compose instanceof HTMLElement) compose.classList.remove("pm-compose--open");
    return true;
  }
  const pmComposeFormat = target.closest("[data-pm-compose-format]");
  if (pmComposeFormat instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const format = String(pmComposeFormat.dataset.pmComposeFormat || "").trim();
    applyProgressComposeFormat(format, pmComposeFormat);
    return true;
  }
  const pmComposeAttachRm = target.closest("[data-pm-compose-attach-remove]");
  if (pmComposeAttachRm instanceof HTMLElement && currentRoute().page === "progress-management") {
    const idx = Number(pmComposeAttachRm.dataset.pmComposeAttachRemove);
    if (!Number.isFinite(idx) || idx < 0) return true;
    const arr = uiState.progressComposeAttach?.files || [];
    if (idx >= arr.length) return true;
    arr.splice(idx, 1);
    // render() はコメント入力中のテキストを消すため、チップだけ差し替える
    const agendaId = String(uiState.progressComposeAttach?.agendaId || "").trim();
    progressRefreshComposeChipsDom(agendaId);
    return true;
  }
  const pmSend = target.closest("[data-pm-send-message]");
  if (pmSend instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const agendaId = Number(pmSend.dataset.pmSendMessage);
    if (!Number.isFinite(agendaId) || agendaId < 1) {
      showToast("課題を選択してください");
      return true;
    }
    const wrap = pmSend.closest(".pm-backlog-detail") || pmSend.closest(".pm-page");
    const bodyEl = wrap?.querySelector("[data-pm-msg-body]");
    const notifyEl = wrap?.querySelector("[data-pm-compose-notify-users]");
    const assigneeChangeEl = wrap?.querySelector("[data-pm-compose-assignee]");
    const statusChangeEl = wrap?.querySelector("[data-pm-compose-status]");
    const startDateEl = wrap?.querySelector("[data-pm-compose-start]");
    const dueDateEl = wrap?.querySelector("[data-pm-compose-due]");
    const rawBody = bodyEl instanceof HTMLTextAreaElement ? bodyEl.value.trim() : "";
    const notifyRaw =
      notifyEl instanceof HTMLTextAreaElement || notifyEl instanceof HTMLInputElement ? notifyEl.value : "";
    // お知らせ欄の人は本文に @ を書き足さず、宛先IDとして別に送る（通知・既読確認はメンションと同じ）
    const notifyUserIds = progressNotifyUserIdsFromRaw(notifyRaw);
    // "変更しない"(value="") → スキップ、それ以外 → 指定ユーザーに変更
    const rawAssigneeVal = assigneeChangeEl instanceof HTMLSelectElement ? assigneeChangeEl.value.trim() : "";
    const newAssigneeId = rawAssigneeVal;
    const doChangeAssignee = rawAssigneeVal !== "";
    // 状況変更
    const rawStatusVal = statusChangeEl instanceof HTMLSelectElement ? statusChangeEl.value.trim() : "";
    const doChangeStatus = rawStatusVal !== "";
    // 日付: 入力値を取得（pre-fill値との比較はupdate時に行う）
    const rawStartDate = startDateEl instanceof HTMLInputElement ? startDateEl.value.trim() : null;
    const rawDueDate = dueDateEl instanceof HTMLInputElement ? dueDateEl.value.trim() : null;
    const submitKey = String(agendaId);
    if (!uiState.progressMessageSubmittingByAgenda) uiState.progressMessageSubmittingByAgenda = {};
    if (uiState.progressMessageSubmittingByAgenda[submitKey]) return true;
    const pending = Array.isArray(uiState.progressComposeAttach?.files)
      ? [...uiState.progressComposeAttach.files]
      : [];
    const hasFiles = pending.length > 0;
    // PC コンポーズの状況・担当者・日付の変更（日付は課題の現在値と違うときだけ変更扱い）
    const agNow = findProgressAgendaById(agendaId)?.agenda;
    const dateChanged = (v, orig) => v !== null && v !== String(orig || "").slice(0, 10);
    const hasAttrChange =
      doChangeAssignee ||
      doChangeStatus ||
      Boolean(agNow && (dateChanged(rawStartDate, agNow.startDate) || dateChanged(rawDueDate, agNow.dueDate)));
    // 本文が空でも、お知らせ先があればコメントとして送る（宛先を載せる入れ物が要るため短い定型文にする）。
    // 属性変更だけならコメントは作らず変更のみ反映する（変更履歴はサーバーのシステムメッセージに残る）
    const postComment = Boolean(rawBody || hasFiles || notifyUserIds.length);
    const postBody = rawBody || (notifyUserIds.length && !hasFiles ? (hasAttrChange ? "更新しました" : "お知らせします") : "");
    if (!postComment && !hasAttrChange) {
      // SPコンポーズで状態・担当者チップだけ設定されている場合は、本文なしでも属性変更のみ実行して閉じる
      // （本文必須で弾くと「担当者を変えただけでは保存できない」デッドエンドになるため）
      const spPendingStatus = uiState.pmSpComposePendingStatus;
      const spPendingAssignee = uiState.pmSpComposePendingAssignee;
      if (spPendingStatus || spPendingAssignee) {
        const foundForGate = findProgressAgendaById(agendaId);
        void (async () => {
          let rejectionReason = "";
          if (spPendingStatus && foundForGate?.agenda && foundForGate?.challenge) {
            const gate = await requestProgressAgendaStatusChange(foundForGate.agenda, foundForGate.challenge, spPendingStatus);
            if (!gate.proceed) return;
            rejectionReason = gate.rejectionReason;
          }
          uiState.pmSpComposePendingStatus = null;
          uiState.pmSpComposePendingAssignee = null;
          uiState.pmSpComposeOpen = false;
          render();
          if (spPendingStatus) {
            const r = await commitProgressAgendaStatusChange(agendaId, spPendingStatus, { rejectionReason });
            if (!r.ok) showToastError(r.error || "状態の変更に失敗しました");
            else showToast(`状況を「${r.savedStatus || spPendingStatus}」に変更しました`);
          }
          if (spPendingAssignee) {
            const apiId = progressAgendaAssignedUserIdForApi(spPendingAssignee);
            if (apiId) {
              // 担当者のみ送る（status等を同時に送ると並走する状態変更を古い値で上書きするため）
              const p = { action: "updateAgendaDetail", agendaId, assignedUserId: apiId };
              try {
                const r = await fetch("/api/progress-management", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify(p)
                });
                if (!r.ok) {
                  const j = await r.json().catch(() => ({}));
                  showToastError(j.error || "担当者の変更に失敗しました");
                } else {
                  applyProgressAgendaDetailFromPayload(agendaId, p);
                  showToast("担当者を変更しました");
                  render();
                }
              } catch {
                showToastError("担当者の変更に失敗しました");
              }
            }
          }
        })();
        return true;
      }
      showToast("メッセージを入力するか、ファイルを添付してください");
      return true;
    }

    const route = currentRoute();
    const pid = String(route.searchParams?.get("project") || "").trim();
    const gid = String(route.searchParams?.get("group") || "").trim();
    const onIssueDetail = progressManagementIssueDetailRouteActive(route);
    const scrollIssueThreadToBottom = () => {
      requestAnimationFrame(() => {
        const th = document.querySelector(".pm-thread--issue-detail");
        if (th instanceof HTMLElement) th.scrollTop = th.scrollHeight;
      });
    };
    uiState.progressMessageSubmittingByAgenda[submitKey] = true;
    pmSend.setAttribute("disabled", "disabled");
    pmSend.setAttribute("aria-busy", "true");
    uiState.progressComposeAttach = { agendaId: submitKey, files: [] };
    if (bodyEl instanceof HTMLTextAreaElement) bodyEl.value = "";
    if (notifyEl instanceof HTMLTextAreaElement || notifyEl instanceof HTMLInputElement) notifyEl.value = "";
    if (assigneeChangeEl instanceof HTMLSelectElement) assigneeChangeEl.value = "";
    if (statusChangeEl instanceof HTMLSelectElement) statusChangeEl.value = "";
    // 期限日も初期値へ戻す（残すと送信後の再描画で「下書きあり」扱いになり入力欄が開いたままになる）
    if (dueDateEl instanceof HTMLInputElement) dueDateEl.value = dueDateEl.defaultValue;
    uiState.progressSelectedAgendaId = submitKey;

    // 担当者変更：担当者の表示だけ先に楽観的更新（チラつき防止）
    // 履歴システムメッセージは楽観追加しない →サーバー確定後に refreshProgressManagementFromServer が自然に持ってくる
    if (doChangeAssignee) {
      const agHitOpt = findProgressAgendaById(agendaId);
      if (agHitOpt?.agenda) {
        const ag2 = agHitOpt.agenda;
        const assignedUserIdForApiOpt = newAssigneeId !== ""
          ? progressAgendaAssignedUserIdForApi(newAssigneeId)
          : null;
        applyProgressAgendaUpdatedLocal(agendaId, {
          title: ag2.title ?? "",
          body: ag2.body ?? "",
          category: ag2.category ?? "",
          // 状況はここでは変えない（下の差し戻し確認が「今の状況→新しい状況」で判定するため。先に変えると
          // 差し戻し理由の入力が出ず、サーバーが理由なしの差し戻しを 400 で拒んで担当者も状況も保存されなかった）
          status: ag2.status ?? "",
          priority: ag2.priority ?? "",
          startDate: ag2.startDate ?? null,
          dueDate: ag2.dueDate ?? null,
          assignedUserId: assignedUserIdForApiOpt
        });
      }
    }

    // コメントをローカル追加（属性変更だけの送信ではコメントを作らない）
    const localMsg = postComment ? appendProgressAgendaUserMessageLocal(agendaId, postBody) : null;
    if (localMsg) {
      localMsg.sending = true;
      localMsg.notifyUserIds = notifyUserIds;
    }
    const localId = localMsg?.id;

    progressMentionDismissForDomRefresh();
    if (onIssueDetail) {
      refreshProgressIssueThreadDom(agendaId);
      scrollIssueThreadToBottom();
    } else {
      render();
    }

    void (async () => {
      try {
        if (postComment) {
          const res = await fetch("/api/progress-management", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({
              action: "postMessage",
              agendaId,
              body: postBody,
              notifyUserIds,
              hasAttachments: hasFiles
            })
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(typeof j.error === "string" ? j.error : "送信に失敗しました");
          }
          const newMid = j.messageId != null ? Number(j.messageId) : NaN;
          if (Number.isFinite(newMid) && newMid >= 1) {
            replaceProgressLocalMessageId(agendaId, localId, newMid);
          } else if (localMsg) {
            localMsg.sending = false;
          }
          if (Number.isFinite(newMid) && newMid >= 1 && pending.length) {
            try {
              for (const file of pending) {
                await uploadProgressBinaryAttachment({ messageId: newMid, file });
              }
            } catch (err) {
              showToastError(err instanceof Error ? err.message : "コメントは保存済みですが、添付の一部に失敗しました");
            }
          }
        }
        showToast(postComment ? "送信しました" : "更新しました");
        // コメントで @メンション・お知らせした人を「最近メンションした人」に記録（担当者候補に出すため。既定には使わない）
        rememberProgressRecentMentionsFromBody(`${rawBody}\n${notifyRaw}`);
        // SP コメント入力シートを閉じる
        if (uiState.pmSpComposeOpen) uiState.pmSpComposeOpen = false;
        // SP コンポーズ内 ✏️ で選択した状態・担当者変更をコメント送信と同時に実行
        const pendingStatus = uiState.pmSpComposePendingStatus;
        const pendingAssignee = uiState.pmSpComposePendingAssignee;
        uiState.pmSpComposePendingStatus = null;
        uiState.pmSpComposePendingAssignee = null;
        if (pendingStatus) {
          const foundForStatusGate = findProgressAgendaById(agendaId);
          void (async () => {
            let rejectionReason = "";
            if (foundForStatusGate?.agenda && foundForStatusGate?.challenge) {
              const gate = await requestProgressAgendaStatusChange(foundForStatusGate.agenda, foundForStatusGate.challenge, pendingStatus);
              if (!gate.proceed) return;
              rejectionReason = gate.rejectionReason;
            }
            const r = await commitProgressAgendaStatusChange(agendaId, pendingStatus, { rejectionReason });
            if (!r.ok) showToastError(r.error || "状態の変更に失敗しました");
          })();
        }
        if (pendingAssignee) {
          const apiId = progressAgendaAssignedUserIdForApi(pendingAssignee);
          if (apiId) {
            const foundAg = findProgressAgendaById(agendaId);
            if (foundAg?.agenda) {
              // 担当者のみ送る。status/category 等を一緒に送ると、同時に走る状態変更(updateAgendaStatus)を
              // 古い値で上書きしてしまう（サーバは未指定フィールドを既存値で保持する）。
              const p = { action: "updateAgendaDetail", agendaId, assignedUserId: apiId };
              void fetch("/api/progress-management", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                body: JSON.stringify(p)
              }).then(async (r) => {
                if (!r.ok) { const j = await r.json().catch(() => ({})); showToastError(j.error || "担当者の変更に失敗しました"); }
                else { applyProgressAgendaDetailFromPayload(agendaId, p); }
              }).catch(() => showToastError("通信エラーのため担当者の変更に失敗しました"));
            }
          }
        }
        // 担当者変更 / 状況変更 / 日付変更 API（楽観的更新済み。まとめて1回の updateAgenda で送る）
        // postMessage後すぐに再描画せず、updateAgenda完了後にまとめて1回だけ描画する
        const agHit2 = findProgressAgendaById(agendaId);
        const ag2 = agHit2?.agenda;
        const origStartDate = ag2 ? String(ag2.startDate || "").slice(0, 10) : "";
        const origDueDate = ag2 ? String(ag2.dueDate || "").slice(0, 10) : "";
        const doChangeStart = rawStartDate !== null && rawStartDate !== origStartDate;
        const doChangeDue = rawDueDate !== null && rawDueDate !== origDueDate;
        const doChangeDate = doChangeStart || doChangeDue;
        if (ag2 && (doChangeAssignee || doChangeStatus || doChangeDate)) {
          try {
            let composeRejectionReason = "";
            // 差し戻しをキャンセルした場合も投稿済みコメントの反映（後続の refreshProgressManagementFromServer）は
            // 必ず実行する必要があるため、ここでは早期returnせず「状況変更だけ取り消す」フラグに倒す
            let effectiveDoChangeStatus = doChangeStatus;
            if (doChangeStatus) {
              const gate = await requestProgressAgendaStatusChange(ag2, agHit2?.challenge, rawStatusVal);
              if (!gate.proceed) {
                effectiveDoChangeStatus = false;
              } else {
                composeRejectionReason = gate.rejectionReason;
              }
            }
            if (effectiveDoChangeStatus || doChangeAssignee || doChangeDate) {
              const assignedUserIdForApi = doChangeAssignee && newAssigneeId !== ""
                ? progressAgendaAssignedUserIdForApi(newAssigneeId)
                : (ag2.assignedUserId ?? null);
              // effectiveDoChangeStatus のとき rawStatusVal を使い、そうでなければ現在の status を保持
              // category が null/undefined/空の場合は既存値を確実に引き継ぐ（空送信でサーバーが先頭カテゴリにリセットするのを防ぐ）
              const newStatus = effectiveDoChangeStatus ? rawStatusVal : (ag2.status ?? "未対応");
              const currentCategory = String(ag2.category || "").trim() || undefined;
              const updFields = {
                title: ag2.title ?? "",
                body: ag2.body ?? "",
                ...(currentCategory !== undefined ? { category: currentCategory } : {}),
                status: newStatus,
                priority: ag2.priority ?? "",
                startDate: doChangeStart ? (rawStartDate || null) : (ag2.startDate ?? null),
                dueDate: doChangeDue ? (rawDueDate || null) : (ag2.dueDate ?? null),
                assignedUserId: assignedUserIdForApi,
                ...(composeRejectionReason ? { rejectionReason: composeRejectionReason } : {})
              };
              const updRes = await fetch("/api/progress-management", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                body: JSON.stringify({ action: "updateAgenda", agendaId, ...updFields })
              });
              if (!updRes.ok) {
                const errMsg = [];
                if (doChangeAssignee) errMsg.push("担当者");
                if (effectiveDoChangeStatus) errMsg.push("状況");
                if (doChangeDate) errMsg.push("日付");
                showToastError(`${errMsg.join("・")}の変更に失敗しました`);
                void refreshProgressManagementFromServer({ scope: "summary", hydrateAgendaId: agendaId });
              } else if (doChangeAssignee) {
                // コメント横の担当者プルダウンで担当者を変えた場合も「最近使った担当者」履歴に記録する（TASK-372の記録漏れ修正）。
                // 他の経路（作成/編集モーダル・担当者のみ変更）は既に記録済みだが、この一括 updateAgenda 経路だけ抜けていた。
                rememberProgressRecentAssignee(Number(assignedUserIdForApi));
              }
            }
          } catch {
            showToastError(postComment ? "コメントは登録しましたが属性の変更に失敗しました" : "属性の変更に失敗しました");
          }
          // 変更ありの場合は updateAgenda完了後の手動描画をしない
          // → refreshProgressManagementFromServer の .then() で履歴込みの状態を1回だけ描画する
        } else {
          // 変更なし：従来どおり postMessage 後すぐ描画
          if (onIssueDetail) {
            refreshProgressIssueThreadDom(agendaId);
            scrollIssueThreadToBottom();
          }
        }
        void refreshProgressManagementFromServer({
          scope: "summary",
          includeChallengeId: pid || undefined,
          includeGroupId: gid || undefined,
          hydrateAgendaId: agendaId
        }).then((ok) => {
          if (!ok) return;
          const latestRoute = currentRoute();
          if (latestRoute.page !== "progress-management") return;
          if (progressManagementIssueDetailRouteActive(latestRoute)) {
            const latestIssueId = Number(latestRoute.searchParams?.get("issue"));
            if (Number.isFinite(latestIssueId) && latestIssueId === agendaId) {
              refreshProgressIssueSummaryDom(agendaId);
              refreshProgressIssueThreadDom(agendaId);
              scrollIssueThreadToBottom();
            }
          } else if (!onIssueDetail) {
            render();
          }
        });
      } catch (err) {
        removeProgressLocalMessage(agendaId, localId);
        if (bodyEl instanceof HTMLTextAreaElement) bodyEl.value = rawBody;
        if (notifyEl instanceof HTMLTextAreaElement || notifyEl instanceof HTMLInputElement) notifyEl.value = notifyRaw;
        uiState.progressComposeAttach = { agendaId: submitKey, files: pending };
        if (onIssueDetail) refreshProgressIssueThreadDom(agendaId);
        else render();
        showToastError(err instanceof Error ? err.message : "送信に失敗しました");
      } finally {
        delete uiState.progressMessageSubmittingByAgenda[submitKey];
        pmSend.removeAttribute("disabled");
        pmSend.removeAttribute("aria-busy");
      }
    })();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 3097〜3749 行）。処理したら true（16 側はそこで止める） */
function handleProgressListClick(e, target) {
  const pmGoMyAssignments = target.closest("[data-pm-go-my-assignments]");
  if (pmGoMyAssignments instanceof HTMLElement && currentRoute().page === "progress-management") {
    // 「起票した」「関わった」から押されたときは担当中へ戻す（同じ担当課題ページ扱いで素通りさせない）
    if (progressManagementAssignScope() !== "me") {
      invalidateProgressAssignedListCache();
      resetProgressMyAssignFilters(); // 絞り込みの持ち越し防止（15h のスコープ切替と同じ）
    } else if (progressManagementAssignedMeRouteActive()) return true;
    location.hash = "progress-management?assigned=me";
    return true;
  }
  const pmGroupCategoryFilter = target.closest("[data-pm-group-category-filter]");
  if (pmGroupCategoryFilter instanceof HTMLElement && currentRoute().page === "progress-management") {
    const val = String(pmGroupCategoryFilter.dataset.pmGroupCategoryFilter || "").trim();
    // クライアント側の絞り込みのみ（読込済みグループを絞る）。トグルで解除も可能。
    uiState.progressGroupListCategory = uiState.progressGroupListCategory === val ? "" : val;
    render();
    return true;
  }
  const pmGroupListMode = target.closest("[data-pm-group-list-mode]");
  if (pmGroupListMode instanceof HTMLElement && currentRoute().page === "progress-management") {
    const mode = normalizeProgressGroupListMode(pmGroupListMode.dataset.pmGroupListMode);
    // フォルダ1段化: 「すべて/アーカイブ」はプロジェクト(challenge)の active/archived を表す。
    // challenge 側の listMode も同期させ、フラット一覧が正しい集合を表示できるようにする。
    if (mode !== "my_themes") uiState.progressListMode = normalizeProgressListMode(mode);
    if (progressManagementAssignedMeRouteActive()) {
      uiState.progressGroupListMode = mode;
      uiState.progressMgmtSuppressEmptyRefetch = false;
      location.hash = progressManagementProjectsListHash();
      return true;
    }
    if (mode === normalizeProgressGroupListMode(uiState.progressGroupListMode)) return true;
    uiState.progressGroupListMode = mode;
    uiState.progressMgmtSuppressEmptyRefetch = false;
    if (applyProgressGroupListCacheToView(mode)) {
      uiState.progressMgmtLoading = false;
      render();
      return true;
    }
    uiState.progressTodoGroups = [];
    uiState.progressChallenges = [];
    uiState.progressMgmtLoading = true;
    uiState.progressMgmtFetchInFlight = true;
    render();
    const logoutSeqAtStart = fpLogoutSeq;
    void (async () => {
      let ok = false;
      try {
        ok = await refreshProgressManagementFromServer({
          groupListMode: mode,
          listMode: mode !== "my_themes" ? normalizeProgressListMode(mode) : undefined,
          scope: "summary"
        });
      } finally {
        // ログアウト／別アカウントへ切り替わった後に届いた結果なら、次の人の状態（取得中フラグ・失敗表示）に触らない
        if (fpLogoutSeq !== logoutSeqAtStart) return;
        uiState.progressMgmtFetchInFlight = false;
        uiState.progressMgmtLoading = false;
        if (!ok) {
          invalidateProgressGroupListCache(mode);
          uiState.progressMgmtSuppressEmptyRefetch = true;
          showToastError("一覧の取得に失敗しました");
        }
        render();
      }
    })();
    return true;
  }
  const pmListMode = target.closest("[data-pm-list-mode]");
  if (pmListMode instanceof HTMLElement && currentRoute().page === "progress-management") {
    const mode = normalizeProgressListMode(pmListMode.dataset.pmListMode);
    if (mode === normalizeProgressListMode(uiState.progressListMode)) return true;
    uiState.progressListMode = mode;
    uiState.progressMgmtSuppressEmptyRefetch = false;
    if (applyProgressListCacheToView(mode)) {
      uiState.progressMgmtLoading = false;
      render();
      return true;
    }
    uiState.progressChallenges = [];
    uiState.progressMgmtLoading = true;
    uiState.progressMgmtFetchInFlight = true;
    render();
    const logoutSeqAtStart = fpLogoutSeq;
    void (async () => {
      let ok = false;
      try {
        ok = await refreshProgressManagementFromServer({ listMode: mode, scope: "summary" });
      } finally {
        // ログアウト／別アカウントへ切り替わった後に届いた結果なら、次の人の状態（取得中フラグ・失敗表示）に触らない
        if (fpLogoutSeq !== logoutSeqAtStart) return;
        uiState.progressMgmtFetchInFlight = false;
        uiState.progressMgmtLoading = false;
        if (!ok) {
          invalidateProgressListFetch(mode);
          uiState.progressMgmtSuppressEmptyRefetch = true;
          showToastError("一覧の取得に失敗しました");
        }
        render();
      }
    })();
    return true;
  }
  const pmToggleGroupStar = target.closest("[data-pm-toggle-todo-group-star]");
  if (pmToggleGroupStar instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken) return true;
    const id = Number(pmToggleGroupStar.dataset.pmToggleTodoGroupStar);
    if (!Number.isFinite(id) || id < 1) return true;
    void (async () => {
      const groups = uiState.progressTodoGroups || [];
      const g = groups.find((x) => Number(x.id) === id);
      const prevStarred = g?.starredByMe;
      const optimisticStarred = !prevStarred;
      const activeCache = getProgressGroupListCache("active");
      const activeGroup = (activeCache?.groups || []).find((x) => Number(x.id) === id);
      if (g) g.starredByMe = optimisticStarred;
      if (activeGroup) activeGroup.starredByMe = optimisticStarred;
      render();
      let res, j;
      try {
        res = await fetch("/api/progress-management", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ action: "toggleTodoGroupStar", groupId: id })
        });
        j = await res.json().catch(() => ({}));
      } catch (_e) {
        // ネットワークエラー: 楽観的更新を元に戻す
        if (g) g.starredByMe = prevStarred;
        if (activeGroup) activeGroup.starredByMe = prevStarred;
        showToastError("お気に入りの更新に失敗しました（通信エラー）");
        render();
        return;
      }
      if (!res.ok) {
        if (g) g.starredByMe = prevStarred;
        if (activeGroup) activeGroup.starredByMe = prevStarred;
        showToastError(typeof j.error === "string" ? j.error : "お気に入りの更新に失敗しました");
        render();
        return;
      }
      const starred = Boolean(j.starred);
      if (g) g.starredByMe = starred;
      if (activeGroup) activeGroup.starredByMe = starred;
      if (normalizeProgressGroupListMode(uiState.progressGroupListMode) === "starred" && !starred) {
        uiState.progressTodoGroups = groups.filter((x) => Number(x.id) !== id);
      }
      if (hasProgressGroupListCache("starred")) {
        setProgressGroupListCache("starred", {
          groups: uiState.progressTodoGroups,
          challenges: uiState.progressChallenges || []
        });
      }
      if (hasProgressGroupListCache("active")) {
        const activeCache = getProgressGroupListCache("active");
        if (activeCache) {
          const ag = (activeCache.groups || []).find((x) => Number(x.id) === id);
          if (ag) ag.starredByMe = starred;
        }
      }
      render();
    })();
    return true;
  }
  const pmToggleStar = target.closest("[data-pm-toggle-challenge-star]");
  if (pmToggleStar instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    if (!authToken) return true;
    const id = Number(pmToggleStar.dataset.pmToggleChallengeStar);
    if (!Number.isFinite(id) || id < 1) return true;
    void (async () => {
      // Optimistic update: toggle immediately, revert on failure
      const chs = uiState.progressChallenges || [];
      const ch = chs.find((c) => Number(c.id) === id);
      const prevStarred = ch?.starredByMe;
      const optimisticStarred = !prevStarred;
      if (ch) ch.starredByMe = optimisticStarred;
      _syncChallengeStarInAllCaches(id, optimisticStarred, prevStarred);
      render();

      let res, j;
      try {
        res = await fetch("/api/progress-management", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ action: "toggleChallengeStar", challengeId: id })
        });
        j = await res.json().catch(() => ({}));
      } catch (_e) {
        // ネットワークエラー: 楽観的更新を元に戻す
        if (ch) ch.starredByMe = prevStarred;
        _syncChallengeStarInAllCaches(id, prevStarred, optimisticStarred);
        showToastError("お気に入りの更新に失敗しました（通信エラー）");
        render();
        return;
      }
      if (!res.ok) {
        // Revert optimistic update
        if (ch) ch.starredByMe = prevStarred;
        _syncChallengeStarInAllCaches(id, prevStarred, optimisticStarred);
        render();
        showToastError(typeof j.error === "string" ? j.error : "お気に入りの更新に失敗しました");
        return;
      }
      const starred = Boolean(j.starred);
      if (ch) ch.starredByMe = starred;
      if (normalizeProgressListMode(uiState.progressListMode) === "starred" && !starred) {
        uiState.progressChallenges = chs.filter((c) => Number(c.id) !== id);
      }
      _syncChallengeStarInAllCaches(id, starred, optimisticStarred);
      render();
    })();
    return true;
  }
  const pmDelCh = target.closest("[data-pm-delete-challenge]");
  if (pmDelCh instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    if (!isMasterRoleUser(currentUser)) {
      showToast("この操作はマスター権限のみ利用できます");
      return true;
    }
    const id = Number(pmDelCh.dataset.pmDeleteChallenge);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!window.confirm(`この${PM_WORD.theme}と配下の${PM_WORD.issue}・やり取りをすべて削除します。取り消せません。よろしいですか？`)) return true;
    void (async () => {
      let res, j;
      try {
        res = await fetch("/api/progress-management", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ kind: "challengeDelete", id })
        });
        j = await res.json().catch(() => ({}));
      } catch (_e) {
        showToastError("削除に失敗しました（通信エラー）");
        return;
      }
      if (!res.ok) {
        showToastError(typeof j.error === "string" ? j.error : "削除に失敗しました");
        return;
      }
      const route = currentRoute();
      const projectParam = String(route.searchParams?.get("project") || "").trim();
      if (projectParam === String(id)) {
        uiState.progressSelectedChallengeId = "";
        uiState.progressSelectedAgendaId = "";
        const ch = (uiState.progressChallenges || []).find((c) => Number(c.id) === id);
        const gid = ch?.todoGroupId ?? route.searchParams?.get("group");
        location.hash =
          gid != null && String(gid).trim() !== ""
            ? `#progress-management?group=${encodeURIComponent(String(gid))}`
            : `#${progressManagementProjectsListHash()}`;
      }
      invalidateProgressListFetch("active");
      invalidateProgressListFetch("archived");
      await refreshProgressManagementFromServer({ listMode: normalizeProgressListMode(uiState.progressListMode), scope: "summary" });
      showToast(`${PM_WORD.theme}を削除しました`);
      render();
    })();
    return true;
  }
  const pmDeleteTodoGroup = target.closest("[data-pm-delete-todo-group]");
  if (pmDeleteTodoGroup instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken || !isMasterRoleUser(currentUser)) return true;
    const id = Number(pmDeleteTodoGroup.dataset.pmDeleteTodoGroup);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!window.confirm(`この${PM_WORD.project}を完全に削除します。${PM_WORD.theme}が1件でもある場合は削除できません。よろしいですか？`)) {
      return true;
    }
    void (async () => {
      let res, j;
      try {
        res = await fetch("/api/progress-management", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ kind: "todoGroupDelete", id })
        });
        j = await res.json().catch(() => ({}));
      } catch (_e) {
        showToastError("削除に失敗しました（通信エラー）");
        return;
      }
      if (!res.ok) {
        showToastError(typeof j.error === "string" ? j.error : "削除に失敗しました");
        return;
      }
      invalidateProgressGroupListCache();
      await refreshProgressManagementFromServer({ groupListMode: normalizeProgressGroupListMode(uiState.progressGroupListMode), scope: "summary" });
      showToast(`${PM_WORD.project}を削除しました`);
      render();
    })();
    return true;
  }
  const pmArchiveTodoGroup = target.closest("[data-pm-archive-todo-group]");
  if (pmArchiveTodoGroup instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmArchiveTodoGroup.dataset.pmArchiveTodoGroup);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!window.confirm(`この${PM_WORD.project}をアーカイブします。よろしいですか？`)) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "DELETE", body: { kind: "todoGroup", id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "アーカイブに失敗しました");
        return;
      }
      uiState.progressGroupListMode = "archived";
      invalidateProgressGroupListCache();
      await refreshProgressManagementFromServer({ groupListMode: "archived", scope: "summary" });
      location.hash = progressManagementProjectsListHash();
      showToast(`${PM_WORD.project}をアーカイブしました`);
    })();
    return true;
  }
  const pmUnarchiveTodoGroup = target.closest("[data-pm-unarchive-todo-group]");
  if (pmUnarchiveTodoGroup instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmUnarchiveTodoGroup.dataset.pmUnarchiveTodoGroup);
    if (!Number.isFinite(id) || id < 1) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "unarchiveTodoGroup", groupId: id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "復元に失敗しました");
        return;
      }
      uiState.progressGroupListMode = "active";
      invalidateProgressGroupListCache("archived");
      await refreshProgressManagementFromServer({ groupListMode: "active", scope: "summary", includeGroupId: id });
      location.hash = `progress-management?group=${id}`;
      showToast(`${PM_WORD.project}を復元しました`);
    })();
    return true;
  }
  const pmArchiveCh = target.closest("[data-pm-archive-challenge]");
  if (pmArchiveCh instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmArchiveCh.dataset.pmArchiveChallenge);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!window.confirm(`この${PM_WORD.theme}と配下の${PM_WORD.issue}をアーカイブします。よろしいですか？`)) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "DELETE", body: { kind: "challenge", id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "アーカイブに失敗しました");
        return;
      }
      uiState.progressListMode = "archived";
      invalidateProgressListFetch("active");
      await refreshProgressManagementFromServer({ listMode: "archived", scope: "summary" });
      uiState.progressSelectedChallengeId = "";
      uiState.progressSelectedAgendaId = "";
      const ch = (uiState.progressChallenges || []).find((c) => Number(c.id) === id);
      const gid = ch?.todoGroupId ?? currentRoute().searchParams?.get("group");
      location.hash =
        gid != null && String(gid).trim() !== ""
          ? `#progress-management?group=${encodeURIComponent(String(gid))}`
          : `#${progressManagementProjectsListHash()}`;
      showToast(`${PM_WORD.theme}をアーカイブしました`);
    })();
    return true;
  }
  const pmUnarchiveCh = target.closest("[data-pm-unarchive-challenge]");
  if (pmUnarchiveCh instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmUnarchiveCh.dataset.pmUnarchiveChallenge);
    if (!Number.isFinite(id) || id < 1) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "unarchiveChallenge", challengeId: id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "復元に失敗しました");
        return;
      }
      uiState.progressListMode = "active";
      invalidateProgressListFetch("archived");
      await refreshProgressManagementFromServer({
        listMode: "active",
        scope: "summary",
        includeChallengeId: id
      });
      const route = currentRoute();
      const onBoard = String(route.searchParams?.get("project") || "").trim() === String(id);
      if (onBoard) setProgressManagementHash(null, String(id), "");
      else {
        uiState.progressSelectedChallengeId = "";
        uiState.progressSelectedAgendaId = "";
        location.hash = progressManagementProjectsListHash();
      }
      showToast(`${PM_WORD.theme}を復元しました`);
    })();
    return true;
  }
  const pmArchiveAg = target.closest("[data-pm-archive-agenda]");
  if (pmArchiveAg instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmArchiveAg.dataset.pmArchiveAgenda);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!window.confirm("この課題をアーカイブします。よろしいですか？")) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "DELETE", body: { kind: "agenda", id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "アーカイブに失敗しました");
        return;
      }
      await refreshProgressManagementFromServer();
      const route = currentRoute();
      const pjs = String(route.searchParams?.get("project") || "").trim();
      uiState.progressSelectedAgendaId = "";
      if (pjs) setProgressManagementHash(null, pjs, "");
      else render();
      showToast("課題をアーカイブしました");
    })();
    return true;
  }
  const pmUnarchiveAg = target.closest("[data-pm-unarchive-agenda]");
  if (pmUnarchiveAg instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmUnarchiveAg.dataset.pmUnarchiveAgenda);
    if (!Number.isFinite(id) || id < 1) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: { action: "unarchiveAgenda", agendaId: id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "復元に失敗しました");
        return;
      }
      await refreshProgressManagementFromServer();
      showToast("課題を復元しました");
      render();
    })();
    return true;
  }
  const pmDelMsg = target.closest("[data-pm-delete-message]");
  if (pmDelMsg instanceof HTMLElement && currentRoute().page === "progress-management") {
    if (!authToken) return true;
    const id = Number(pmDelMsg.dataset.pmDeleteMessage);
    if (!Number.isFinite(id) || id < 1) return true;
    let delTarget = null;
    for (const c of uiState.progressChallenges || []) {
      for (const a of c.agendas || []) {
        const found = (a.messages || []).find((m) => Number(m.id) === id);
        if (found) {
          delTarget = found;
          break;
        }
      }
      if (delTarget) break;
    }
    if (!delTarget || !progressMessageIsAuthor(delTarget)) {
      showToast("自分のメッセージのみ削除できます");
      return true;
    }
    if (!window.confirm("このメッセージを削除しますか？")) return true;
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "DELETE", body: { kind: "message", id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "削除に失敗しました");
        return;
      }
      await refreshProgressManagementFromServer();
      showToast("削除しました");
      render();
    })();
    return true;
  }
  const pmAgendaAttachOpen = target.closest("[data-pm-agenda-attach-open]");
  if (pmAgendaAttachOpen instanceof HTMLElement && currentRoute().page === "progress-management") {
    const aid = String(pmAgendaAttachOpen.dataset.pmAgendaAttachOpen || "").trim();
    if (!aid) return true;
    uiState.progressAgendaAttachModal = { agendaId: aid };
    uiState.progressAgendaAttachPreviewId = "";
    revokeProgressAttachPreviewUrls();
    render();
    void (async () => {
      const hit = findProgressAgendaById(aid);
      const ag = hit?.agenda;
      if (!ag) return;
      for (const att of progressAgendaAttachmentsList(ag).filter((a) => progressAttachmentIsImage(a))) {
        try {
          await fetchProgressAttachmentObjectUrl(att.id);
        } catch {
          /* サムネ失敗は無視 */
        }
      }
      if (uiState.progressAgendaAttachModal?.agendaId === aid) render();
    })();
    return true;
  }
  const pmAgendaAttachClose = target.closest("[data-close-pm-agenda-attach]");
  if (pmAgendaAttachClose instanceof HTMLElement && uiState.progressAgendaAttachModal) {
    closeProgressAgendaAttachModal();
    render();
    return true;
  }
  const pmAttachPreviewClose = target.closest("[data-close-pm-attach-preview]");
  if (pmAttachPreviewClose instanceof HTMLElement && uiState.progressAgendaAttachPreviewId) {
    uiState.progressAgendaAttachPreviewId = "";
    render();
    return true;
  }
  const pmAttachPreview = target.closest("[data-pm-attach-preview]");
  if (pmAttachPreview instanceof HTMLElement && currentRoute().page === "progress-management") {
    const id = Number(pmAttachPreview.dataset.pmAttachPreview);
    if (!Number.isFinite(id) || id < 1) return true;
    const previewAtt = findProgressAttachmentById(id);
    if (!previewAtt) return true;
    uiState.progressAgendaAttachPreviewId = String(id);
    render();
    if (previewAtt && progressAttachmentIsImage(previewAtt)) {
      void (async () => {
        try {
          await fetchProgressAttachmentObjectUrl(id);
          if (String(uiState.progressAgendaAttachPreviewId) === String(id)) render();
        } catch (err) {
          showToast(err instanceof Error ? err.message : "プレビューの取得に失敗しました");
        }
      })();
    }
    return true;
  }
  const pmPendingFileRemove = target.closest("[data-pm-modal-pending-file-remove]");
  if (pmPendingFileRemove instanceof HTMLElement && currentRoute().page === "progress-management") {
    const idx = Number(pmPendingFileRemove.dataset.pmModalPendingFileRemove);
    const list = Array.isArray(uiState.progressAgendaModalPendingFiles)
      ? [...uiState.progressAgendaModalPendingFiles]
      : [];
    if (Number.isFinite(idx) && idx >= 0 && idx < list.length) {
      list.splice(idx, 1);
      uiState.progressAgendaModalPendingFiles = list;
      render();
    }
    return true;
  }
  const pmAttachDl = target.closest("[data-pm-attach-download]");
  if (pmAttachDl instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    e.stopPropagation();
    const id = Number(pmAttachDl.dataset.pmAttachDownload);
    if (!Number.isFinite(id) || id < 1) return true;
    void downloadProgressAttachment(id);
    return true;
  }
  const pmAttachDel = target.closest("[data-pm-attach-delete]");
  if (pmAttachDel instanceof HTMLElement && currentRoute().page === "progress-management") {
    e.preventDefault();
    const id = Number(pmAttachDel.dataset.pmAttachDelete);
    if (!Number.isFinite(id) || id < 1) return true;
    if (!authToken) return true;
    if (!window.confirm("この添付ファイルを削除しますか？\n（やりとりに削除履歴が残ります）")) return true;
    const keepAttachModal = uiState.progressAgendaAttachModal;
    const keepAgendaId = keepAttachModal?.agendaId ? String(keepAttachModal.agendaId) : "";
    if (String(uiState.progressAgendaAttachPreviewId) === String(id)) {
      uiState.progressAgendaAttachPreviewId = "";
    }
    void (async () => {
      const { ok: apiOk, data: j } = await apiFetch("/api/progress-attachment", { method: "DELETE", body: { id } });
      if (!apiOk) {
        showToastError(typeof j.error === "string" ? j.error : "削除に失敗しました");
        return;
      }
      await refreshProgressManagementFromServer();
      showToast("添付を削除しました");
      if (keepAgendaId) {
        uiState.progressAgendaAttachModal = { agendaId: keepAgendaId };
        revokeProgressAttachPreviewUrls();
        render();
        void (async () => {
          const hit = findProgressAgendaById(keepAgendaId);
          const ag = hit?.agenda;
          if (!ag) return;
          for (const att of progressAgendaAttachmentsList(ag).filter((a) => progressAttachmentIsImage(a))) {
            try {
              await fetchProgressAttachmentObjectUrl(att.id);
            } catch {
              /* noop */
            }
          }
          if (uiState.progressAgendaAttachModal?.agendaId === keepAgendaId) render();
        })();
        return;
      }
      const route = currentRoute();
      const pid = String(route.searchParams?.get("project") || "").trim();
      const issue = String(route.searchParams?.get("issue") || "").trim();
      if (pid && issue) setProgressManagementHash(null, pid, issue);
      else render();
    })();
    return true;
  }
  // 確認プレビュー: いつでも押せる（捺印承認状態を問わない）。発行履歴には記録しない
  const docFooterPreview = target.closest("[data-doc-footer-preview]");
  if (docFooterPreview instanceof HTMLElement && currentRoute().page === "project-docs") {
    const raw = docFooterPreview.getAttribute("data-doc-footer-preview") || "";
    const sep = raw.indexOf(":");
    const pid = sep >= 0 ? raw.slice(0, sep) : "";
    const tab = sep >= 0 ? raw.slice(sep + 1) : "";
    const proj = projects.find((x) => x.id === pid);
    if (!proj) return true;
    if (tab === "invoice" || tab === "quote") {
      printDocument(tab, pid, buildDocFromProjectDraft(proj, tab));
    } else {
      showToast("この書類のプレビューは準備中です");
    }
    return true;
  }
  // 正式PDFを発行: 捺印承認フローがある書類種別は承認済みでなければ発行不可
  // （サーバー側 api/doc-pdf でも同じ基準を再確認する。ここはUI上の早期リターンに過ぎない）。
  // サーバーがDBの保存済みデータからPDFを生成するため、送信前に未保存の編集があれば保存を待つ。
  // 発行履歴（fp_invoice_issue_history）はサーバー側で同じリクエスト内に記録済み。
  const docFooterOfficial = target.closest("[data-doc-footer-official]");
  if (docFooterOfficial instanceof HTMLElement && currentRoute().page === "project-docs") {
    const raw = docFooterOfficial.getAttribute("data-doc-footer-official") || "";
    const sep = raw.indexOf(":");
    const pid = sep >= 0 ? raw.slice(0, sep) : "";
    const tab = sep >= 0 ? raw.slice(sep + 1) : "";
    const proj = projects.find((x) => x.id === pid);
    if (!proj) return true;
    if (tab !== "invoice" && tab !== "quote") {
      showToast("この書類のPDF出力は準備中です");
      return true;
    }
    if (docTabHasSealWorkflow(tab) && docTypeBlocksSendWhenUnapproved(tab) && getDocSealStatus(pid, tab) !== "承認済") {
      showToast("捺印申請が承認されると正式PDFを発行できます");
      return true;
    }
    // 二重送信防止（連打するとPDF生成の間に2回リクエストが飛び、発行履歴が二重に記録される）
    if (uiState._docOfficialPdfInFlight) {
      showToast("別の書類のPDFを作成中です。終わってからもう一度押してください");
      return true;
    }
    // 作成中はボタンを「PDFを作成中…」にする（数秒〜十数秒かかり、押しても反応が無いように見えていた）
    uiState._docOfficialPdfInFlight = raw;
    render();
    void (async () => {
      try {
        const linesSave = await flushDocBillingLinesBeforeSend(pid, tab);
        if (!linesSave.ok) {
          showToastError(linesSave.sealReset ? linesSave.error : `請求項目を保存できないため発行を中止しました：${linesSave.error}`);
          return;
        }
        // 支払期限・備考の保存に失敗したまま発行すると、サーバーの古い内容で正式PDFが出てしまうため止める。
        // 発行するタブは未編集でも送る（画面の初期値＝支払期限の自動計算等を正式PDFと揃える。従来の全タブ保存と同じ）
        const metaSave = await flushDocMetaSaveToServer(pid, tab);
        if (!metaSave.ok) {
          showToastError(`支払期限・備考を保存できなかったため、正式PDFの発行を中止しました：${metaSave.error}`);
          return;
        }
        await downloadOfficialDocumentPdf(tab, pid);
      } finally {
        uiState._docOfficialPdfInFlight = "";
        render();
      }
    })();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 3753〜4513 行）。処理したら true（16 側はそこで止める） */
function handleSidebarAdminClick(e, target) {
  // 「メール送信」「郵送する」は書類選択メニュー→メール作成画面。
  // 処理は app-src/15h-doc-mail-compose.js の独立リスナーが持つ（ここには足さない）。
  // ── 詳細ページの請求項目 CRUD ──
  const openDetailLineAddPickerEl = target.closest("[data-detail-line-add-picker-open]");
  if (openDetailLineAddPickerEl instanceof HTMLElement) {
    const pid = openDetailLineAddPickerEl.dataset.detailLineAddPickerOpen;
    if (!pid) return true;
    uiState.detailLineAddPicker = { pid };
    render();
    return true;
  }
  const closeDetailLineAddPickerEl = target.closest("[data-detail-line-add-picker-close]");
  if (closeDetailLineAddPickerEl instanceof HTMLElement) {
    uiState.detailLineAddPicker = null;
    render();
    return true;
  }
  const detailLineAddTypeEl = target.closest("[data-detail-line-add-type]");
  if (detailLineAddTypeEl instanceof HTMLElement) {
    const picker = uiState.detailLineAddPicker;
    if (!picker?.pid) return true;
    ensureProjectDocDraft(picker.pid);
    const type = String(detailLineAddTypeEl.dataset.detailLineAddType || "normal");
    const newLine = type === "heading"
      ? { lineType: "heading", item: "" }
      : type === "subtotal"
        ? { lineType: "subtotal", item: "小計" }
        : { lineType: "normal", item: "", qty: "1", unit: "式", price: "0", amount: "0", assignedUserId: "", assignedUserIds: [] };
    uiState.projectDocsDrafts[picker.pid].lines.push(newLine);
    markProjectDocsDraftDirty(picker.pid);
    persistAllLineStaffForProject(picker.pid);
    uiState.detailLineAddPicker = null;
    render();
    // 追加した行を画面内にスクロール（スマホで追加行が画面外になる問題への対処）
    requestAnimationFrame(() => {
      const lastRow = document.querySelector("table.doc-lines tbody tr.doc-detail-line-row:last-of-type");
      if (lastRow) lastRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return true;
  }
  const detailLineMoveEl = target.closest("[data-detail-line-move]");
  if (detailLineMoveEl instanceof HTMLElement) {
    if (detailLineMoveEl.disabled) return true;
    const dir = Number(detailLineMoveEl.getAttribute("data-detail-line-move"));
    const idx = Number(detailLineMoveEl.dataset.detailLineIndex);
    const pid = detailLineMoveEl.dataset.detailProject || "";
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (!lines || !pid || Number.isNaN(idx) || !dir) return true;
    const j = idx + dir;
    if (j < 0 || j >= lines.length) return true;
    // ↑↓は隣と入れ替える。moveProjectDetailLine は DnD 用の「対象行の前に入れる」意味なので、
    // ↓（from<to）に使うと元の位置へ戻って動かなかった
    [lines[idx], lines[j]] = [lines[j], lines[idx]];
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    uiState.invDocLineMenu = null;
    render();
    return true;
  }
  // ── 書類board: その行の下に新しい行を挿入 ──
  const insertBelowEl = target.closest("[data-detail-line-insert-below]");
  if (insertBelowEl instanceof HTMLElement) {
    const idx = Number(insertBelowEl.dataset.detailLineInsertBelow);
    const pid = insertBelowEl.dataset.detailProject;
    if (!pid || isNaN(idx)) return true;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (!lines) return true;
    const newLine = { lineType: "normal", item: "", qty: "1", unit: "式", price: "0", amount: "0", assignedUserId: "", assignedUserIds: [] };
    lines.splice(idx + 1, 0, newLine);
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    uiState.invDocLineMenu = null;
    render();
    // 新しい行の摘要欄にフォーカス（preventScroll: 復元したスクロールをフォーカスが動かして画面が飛ばないように）
    requestAnimationFrame(() => {
      const newRow = document.querySelector(`tr.doc-detail-line-row[data-detail-line-drop="${idx + 1}"][data-detail-project="${pid}"] input[data-detail-line-field="item"]`);
      if (newRow) newRow.focus({ preventScroll: true });
    });
    return true;
  }
  const detailLineRemoveEl = target.closest("[data-detail-line-remove]");
  if (detailLineRemoveEl instanceof HTMLElement) {
    const idx = Number(detailLineRemoveEl.dataset.detailLineRemove);
    const pid = detailLineRemoveEl.dataset.detailProject;
    if (!pid || isNaN(idx)) return true;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (!lines || idx < 0) return true;
    lines.splice(idx, 1);
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    uiState.invDocLineMenu = null;
    render();
    return true;
  }
  // ── 書類プレビュー SP：⋮ ボタン → ボトムシートを開く ──
  const invLineMenuOpen = target.closest("[data-inv-line-menu]");
  if (invLineMenuOpen instanceof HTMLElement) {
    const idx = Number(invLineMenuOpen.dataset.invLineMenu);
    const pid = invLineMenuOpen.dataset.detailProject || "";
    if (!pid || isNaN(idx)) return true;
    uiState.invDocLineMenu = { pid, index: idx };
    render();
    return true;
  }
  // ── 書類プレビュー SP：ボトムシートを閉じる（バックドロップ・✕） ──
  if (target.closest("[data-inv-line-menu-close]") instanceof HTMLElement) {
    uiState.invDocLineMenu = null;
    render();
    return true;
  }
  // ── 詳細ページ・請求項目 SP 編集モーダル ──
  const openLineMobileEditEl = target.closest("[data-detail-line-mobile-edit]");
  if (openLineMobileEditEl instanceof HTMLElement) {
    const idx = Number(openLineMobileEditEl.dataset.detailLineMobileEdit);
    const pid = openLineMobileEditEl.dataset.detailProject || "";
    if (!pid || isNaN(idx)) return true;
    uiState.detailLineMobileEdit = { pid, index: idx };
    render();
    return true;
  }
  const closeLineMobileEditEl = target.closest("[data-detail-line-mobile-close]");
  if (closeLineMobileEditEl instanceof HTMLElement) {
    uiState.detailLineMobileEdit = null;
    render();
    return true;
  }
  const saveLineMobileEl = target.closest("[data-detail-line-mobile-save]");
  if (saveLineMobileEl instanceof HTMLElement) {
    const idx = Number(saveLineMobileEl.dataset.detailLineIndex);
    const pid = saveLineMobileEl.dataset.detailProject || "";
    if (!pid || isNaN(idx)) return true;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (!lines || idx < 0 || idx >= lines.length) return true;
    const sheet = document.querySelector(".dlm-sheet");
    if (!sheet) return true;
    const itemEl = sheet.querySelector("#dlm-item");
    const qtyEl  = sheet.querySelector("#dlm-qty");
    const unitEl = sheet.querySelector("#dlm-unit");
    const priceEl = sheet.querySelector("#dlm-price");
    const line = lines[idx];
    if (itemEl  instanceof HTMLInputElement)  line.item  = itemEl.value;
    if (qtyEl   instanceof HTMLInputElement)  line.qty   = qtyEl.value;
    if (unitEl  instanceof HTMLInputElement)  line.unit  = unitEl.value;
    if (priceEl instanceof HTMLInputElement)  line.price = priceEl.value;
    // 金額は billingLineAmountExTax と同じロジック（Math.floor）で計算
    line.amount = String(billingLineAmountExTax(line));
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    uiState.detailLineMobileEdit = null;
    render();
    return true;
  }
  const projectDrawerDateEl = target.closest("[data-pd-date-shortcut]");
  if (projectDrawerDateEl instanceof HTMLElement) {
    const draft = projectDetailFormDraftActive();
    if (!draft) return true;
    const [field, token] = projectDrawerDateEl.dataset.pdDateShortcut.split(":");
    if (token === "today") draft[field] = localDateToYmd(new Date());
    if (token === "m0") draft[field] = endOfMonthDate(0);
    if (token === "m1") draft[field] = endOfMonthDate(1);
    if (token === "m2") draft[field] = endOfMonthDate(2);
    render();
    return true;
  }
  const savePdDetailEl = target.closest("[data-pd-save-detail]");
  if (savePdDetailEl instanceof HTMLElement && currentRoute().page === "project-detail") {
    const r = currentRoute();
    // 請求項目タブにも同じ「保存」を置く（基本情報と請求項目をまとめて保存する）
    if (r.detailTab !== "overview" && r.detailTab !== "billing") return true;
    const proj = projects.find((x) => x.id === r.id);
    // 保存後の行き先: stay=その場 / list=案件一覧 / docs=書類（見積書）
    const after = String(savePdDetailEl.dataset.pdSaveDetail || "stay");
    void (async () => {
      const ok = await saveProjectDetailOverview(proj);
      if (ok && after === "list") location.hash = "#projects";
      else if (ok && after === "docs") openProjectDocsForProject(proj.id);
      else render();
    })();
    return true;
  }
  const authActionEl = target.closest("[data-auth-action]");
  if (authActionEl instanceof HTMLElement) {
    const action = authActionEl.dataset.authAction;
    if (action === "login") {
      uiState.auth.isLoggedIn = true;
      uiState.auth.step = "login";
      uiState.auth.password = "";
      uiState.auth.otp = "";
      showToast("ログインしました");
      render();
      return true;
    }
  }
  const navSectionToggleEl = target.closest("[data-nav-section-toggle]");
  if (navSectionToggleEl instanceof HTMLElement) {
    const id = navSectionToggleEl.dataset.navSectionToggle || "";
    if (id) {
      uiState.sidebarSections[id] = !uiState.sidebarSections[id];
      render();
    }
    return true;
  }
  const clientListPageEl = target.closest("[data-client-list-page]");
  if (clientListPageEl instanceof HTMLElement && currentRoute().page === "client-list") {
    const page = Number(clientListPageEl.dataset.clientListPage || "1");
    if (Number.isFinite(page) && page > 0) {
      uiState.clientList.page = page;
      render();
    }
    return true;
  }
  const partnerListPageEl = target.closest("[data-partner-list-page]");
  if (partnerListPageEl instanceof HTMLElement && currentRoute().page === "partner-list") {
    const page = Number(partnerListPageEl.dataset.partnerListPage || "1");
    if (Number.isFinite(page) && page > 0) {
      uiState.partnerList.page = page;
      render();
    }
    return true;
  }
  const domainStatusMenuToggleEl = target.closest("[data-domain-status-menu-toggle]");
  if (domainStatusMenuToggleEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    uiState.domainList.statusMenuOpen = !uiState.domainList.statusMenuOpen;
    render();
    return true;
  }
  const domainStatusMenuCloseEl = target.closest("[data-domain-status-menu-close]");
  if (domainStatusMenuCloseEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    uiState.domainList.statusMenuOpen = false;
    render();
    return true;
  }
  const domainSortEl = target.closest("[data-domain-sort]");
  if (domainSortEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    const key = domainSortEl.dataset.domainSort;
    if (!key) return true;
    if (uiState.domainList.sortKey === key) {
      uiState.domainList.sortAsc = !uiState.domainList.sortAsc;
    } else {
      uiState.domainList.sortKey = key;
      uiState.domainList.sortAsc = true;
    }
    uiState.domainList.page = 1;
    render();
    return true;
  }
  const domainListPageEl = target.closest("[data-domain-list-page]");
  if (domainListPageEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    const page = Number(domainListPageEl.dataset.domainListPage || "1");
    if (Number.isFinite(page) && page > 0) {
      uiState.domainList.page = page;
      render();
    }
    return true;
  }
  const clientListResetEl = target.closest("[data-client-list-reset]");
  if (clientListResetEl instanceof HTMLElement && currentRoute().page === "client-list") {
    uiState.clientList.filters = clientListDefaultFilters();
    uiState.clientList.page = 1;
    render();
    return true;
  }
  const partnerListResetEl = target.closest("[data-partner-list-reset]");
  if (partnerListResetEl instanceof HTMLElement && currentRoute().page === "partner-list") {
    uiState.partnerList.filters = partnerListDefaultFilters();
    uiState.partnerList.page = 1;
    render();
    return true;
  }
  const domainListResetEl = target.closest("[data-domain-list-reset]");
  if (domainListResetEl instanceof HTMLElement && currentRoute().page === "domain-list") {
    uiState.domainList.filters = { keyword: "", statuses: DOMAIN_STATUS_FILTER_DEFAULT.slice(), owner: "", clientName: "" };
    uiState.domainList.statusMenuOpen = false;
    uiState.domainList.sortKey = "";
    uiState.domainList.sortAsc = true;
    uiState.domainList.page = 1;
    render();
    return true;
  }
  const domainPwToggleEl = target.closest("[data-domain-pw-toggle]");
  if (domainPwToggleEl instanceof HTMLElement) {
    const id = domainPwToggleEl.dataset.domainPwToggle;
    uiState.domainPwReveal[id] = !uiState.domainPwReveal[id];
    render();
    return true;
  }
  const partnerListSearchEl = target.closest("[data-partner-list-search]");
  if (partnerListSearchEl instanceof HTMLElement && currentRoute().page === "partner-list") {
    uiState.partnerList.page = 1;
    render();
    return true;
  }
  const contractOpenEl = target.closest("[data-contract-open]");
  if (contractOpenEl instanceof HTMLElement && currentRoute().page === "application-management") {
    // 申請管理の「契約詳細」。旧契約一覧の URL へ飛ばし、renderNow が取引先一覧（会社名で絞り込み）へ移す
    const id = contractOpenEl.dataset.contractOpen || "";
    const row = contractMaster.find((item) => item.id === id);
    if (!row) return true;
    uiState.appMgmtDetailModal = null;
    const nm = String(row.name || "").trim();
    const frag = nm
      ? `contract-management?name=${encodeURIComponent(nm)}`
      : "contract-management";
    if (location.hash.replace(/^#/, "") === frag) render();
    else location.hash = `#${frag}`;
    return true;
  }
  const sidebarCollapseToggleEl = target.closest("[data-sidebar-collapse-toggle]");
  if (sidebarCollapseToggleEl instanceof HTMLElement && uiState.auth.isLoggedIn) {
    uiState.sidebarNavCollapsed = !uiState.sidebarNavCollapsed;
    try {
      localStorage.setItem(SIDEBAR_NAV_COLLAPSED_STORAGE_KEY, uiState.sidebarNavCollapsed ? "1" : "0");
    } catch {
      /* noop */
    }
    applySidebarCollapsedClass();
    return true;
  }
  const sidebarNotifyGo = target.closest("[data-sidebar-notify-go]");
  if (sidebarNotifyGo instanceof HTMLElement) {
    const hash = String(sidebarNotifyGo.dataset.sidebarNotifyGo || "").trim();
    const tab = String(sidebarNotifyGo.dataset.sidebarNotifyTab || "").trim();
    if (tab === "all" || tab === "contract" || tab === "seal" || tab === "tool" || tab === "equip" || tab === "unlock" || tab === "development") uiState.appMgmtTab = tab;
    uiState.sidebarNotifyMenuOpen = false;
    uiState.sidebarUserMenuOpen = false;
    uiState.fpMobileNavOpen = false;
    applyFpMobileDrawerState();
    applySidebarUserMenuState();
    applySidebarNotifyMenuState();
    applyMobileNotifyPanel();  // SP通知パネルも閉じる
    if (hash) {
      try {
        const qs = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
        const sp = new URLSearchParams(qs);
        const issueId = sp.get("issue");
        const projectId = sp.get("project");
        const groupId = sp.get("group");
        if (issueId) void progressMarkAgendaRead(issueId);
        if (projectId || issueId) {
          setProgressManagementHash(groupId, projectId, issueId);
        } else {
          const next = `#${hash}`;
          if (location.hash === next) render();
          else location.hash = next;
        }
      } catch {
        const next = `#${hash}`;
        if (location.hash === next) render();
        else location.hash = next;
      }
    }
    return true;
  }
  const sidebarNotifyToggleEl = target.closest("[data-sidebar-notify-toggle]");
  if (sidebarNotifyToggleEl instanceof HTMLElement) {
    e.preventDefault();
    uiState.sidebarNotifyMenuOpen = !uiState.sidebarNotifyMenuOpen;
    if (uiState.sidebarNotifyMenuOpen) {
      uiState.sidebarUserMenuOpen = false;
      void refreshProgressViewerCountsFromServer().then(() => refreshSidebarUserChrome());
    }
    applySidebarUserMenuState();
    applySidebarNotifyMenuState();
    refreshSidebarUserChrome();
    return true;
  }
  const sidebarUserActionEl = target.closest("[data-sidebar-user-menu-action]");
  if (sidebarUserActionEl instanceof HTMLElement) {
    const action = sidebarUserActionEl.dataset.sidebarUserMenuAction;
    closeSidebarFlyouts();
    if (action === "account") {
      uiState.myAccountEditor = {
        name: currentUser.name || "",
        displayName: currentUser.displayName || "",
        comment: currentUser.comment || "",
        phone: currentUser.phone || "",
        avatar: currentUser.avatar || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      };
      render();
      return true;
    }
    if (action === "logout") {
      doLogout();
      return true;
    }
  }
  const sidebarUserToggleEl = target.closest("[data-sidebar-user-toggle]");
  if (sidebarUserToggleEl instanceof HTMLElement) {
    e.preventDefault();
    uiState.sidebarUserMenuOpen = !uiState.sidebarUserMenuOpen;
    if (uiState.sidebarUserMenuOpen) uiState.sidebarNotifyMenuOpen = false;
    applySidebarUserMenuState();
    applySidebarNotifyMenuState();
    return true;
  }
  // sidebar-user 外クリックで各メニューを閉じる
  // ただしベルボタン・SPパネル自体はここで閉じない（それぞれの専用ハンドラーで管理）
  if (!target.closest(".sidebar-user") && !target.closest("#fpTopbarNotifyBtn") && !target.closest("#fpMobileNotifyPanel")) {
    if (uiState.sidebarUserMenuOpen) {
      uiState.sidebarUserMenuOpen = false;
      applySidebarUserMenuState();
    }
    if (uiState.sidebarNotifyMenuOpen) {
      uiState.sidebarNotifyMenuOpen = false;
      applySidebarNotifyMenuState();
      if (typeof applyMobileNotifyPanel === "function") applyMobileNotifyPanel();
    }
  }
  const bpTabEl = target.closest("[data-bp-tab]");
  if (bpTabEl instanceof HTMLElement && isBudgetProgressSharedRoute()) {
    e.preventDefault();
    window.budgetProgressSwitchTab?.(bpTabEl.dataset.bpTab || "");
    return true;
  }
  const bpPersonEl = target.closest("[data-bp-person]");
  if (bpPersonEl instanceof HTMLElement && currentRoute().page === "budget-progress") {
    e.preventDefault();
    const idx = Number.parseInt(bpPersonEl.dataset.bpPerson || "0", 10);
    window.budgetProgressSelectPerson?.(idx);
    return true;
  }
  const communityTabEl = target.closest("[data-community-tab]");
  if (communityTabEl instanceof HTMLElement && currentRoute().page === "community") {
    uiState.community.tab = communityTabEl.dataset.communityTab || "org";
    render();
    return true;
  }
  const communityDeptEl = target.closest("[data-community-dept]");
  if (communityDeptEl instanceof HTMLElement && currentRoute().page === "community") {
    uiState.community.dept = communityDeptEl.dataset.communityDept || "すべて";
    render();
    return true;
  }
  const communityTagEl = target.closest("[data-community-tag]");
  if (communityTagEl instanceof HTMLElement && currentRoute().page === "community") {
    uiState.community.tag = communityTagEl.dataset.communityTag || "すべて";
    render();
    return true;
  }
  const communityWriteEl = target.closest("[data-community-write]");
  if (communityWriteEl instanceof HTMLElement && currentRoute().page === "community") {
    showToast("記事投稿機能は準備中です");
    return true;
  }
  const accountTabEl = target.closest("[data-account-settings-tab]");
  if (accountTabEl instanceof HTMLElement && isAccountSettingsRoute()) {
    const t = accountTabEl.dataset.accountSettingsTab || "account";
    if (t === "account" || t === "dept" || t === "approval" || t === "approval-bindings" || t === "lock") {
      location.hash = t === "account" ? "#account-admin" : `#account-admin?tab=${t}`;
    }
    return true;
  }
  // ── 秘密メニュー タブ切り替え ──
  const secretTabEl = target.closest("[data-secret-menu-tab]");
  if (secretTabEl instanceof HTMLElement && currentRoute().page === "secret-menu") {
    const newTab = secretTabEl.dataset.secretMenuTab || "restore";
    uiState.secretMenuTab = newTab;
    render();
    // 操作ログタブに切り替えたら自動取得
    if (newTab === "audit-log" && (!uiState.auditLogView || uiState.auditLogView.items === null)) {
      void loadAuditLog(false);
    }
    // ログイン状況タブに切り替えたら自動取得
    if (newTab === "login-status" && (!uiState.loginStatusView || uiState.loginStatusView.items === null)) {
      void loadLoginStatus();
    }
    return true;
  }
  // ── 削除済みデータ復元 / 操作ログ（秘密メニューページ） ──
  const onSecretMenu = currentRoute().page === "secret-menu";
  const restoreTypeEl = target.closest("[data-restore-type]");
  if (restoreTypeEl instanceof HTMLElement && onSecretMenu) {
    if (!uiState.restoreDeleted) uiState.restoreDeleted = { type: "project", items: null, loading: false, msg: "" };
    uiState.restoreDeleted.type = restoreTypeEl.dataset.restoreType || "project";
    uiState.restoreDeleted.items = null;
    uiState.restoreDeleted.loading = false; // 前の種別の取得中でも、その応答は捨てる（下の data-restore-load）
    uiState.restoreDeleted.msg = "";
    render();
    return true;
  }
  const restoreLoadEl = target.closest("[data-restore-load]");
  if (restoreLoadEl instanceof HTMLElement && onSecretMenu) {
    if (!uiState.restoreDeleted) uiState.restoreDeleted = { type: "project", items: null, loading: false, msg: "" };
    const type = uiState.restoreDeleted.type || "project";
    uiState.restoreDeleted.loading = true;
    uiState.restoreDeleted.msg = "";
    render();
    void (async () => {
      const { ok: apiOk, status, data } = await apiFetch(`/api/restore-deleted?type=${encodeURIComponent(type)}`);
      // 取得中に種別を切り替えていたら捨てる（案件の一覧が「クライアント」の画面に出て、
      // 同じ id の削除済みクライアントを復元してしまう）
      if (uiState.restoreDeleted.type !== type) return;
      uiState.restoreDeleted.loading = false;
      const loaded = apiOk && data.ok;
      uiState.restoreDeleted.items = loaded ? (data.items || []) : null;
      uiState.restoreDeleted.msg = loaded ? "" : status === 0 ? "通信エラー" : (data.error || "取得に失敗しました");
      render();
    })();
    return true;
  }
  const restoreItemEl = target.closest("[data-restore-item]");
  if (restoreItemEl instanceof HTMLElement && onSecretMenu) {
    const raw = restoreItemEl.dataset.restoreItem || "";
    const [rType, rId] = raw.split(":");
    if (!rType || !rId) return true;
    const itemName = restoreItemEl.dataset.restoreItemName || `ID:${rId}`;
    if (!window.confirm(`「${itemName}」を復元しますか？\n復元すると再び一覧に表示されます。`)) return true;
    void (async () => {
      try {
        const r = await fetch("/api/restore-deleted", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ type: rType, id: Number(rId) })
        });
        const data = await r.json().catch(() => ({}));
        if (data.ok) {
          showToast(`「${data.label || itemName}」を復元しました`);
          if (uiState.restoreDeleted?.items) {
            uiState.restoreDeleted.items = uiState.restoreDeleted.items.filter(x => String(x.id) !== String(rId));
          }
          // 復元した案件・取引先等を各一覧に出す（再読込しないとリロードまで見えない）
          loadDataFromDB().then(() => render()).catch(() => {});
        } else {
          showToast(data.error || "復元に失敗しました", { variant: "error" });
        }
      } catch { showToast("通信エラー", { variant: "error" }); }
      render();
    })();
    return true;
  }
  // ── ログイン状況: 再読み込み ──
  if (target.closest("[data-login-status-reload]") instanceof HTMLElement && onSecretMenu) {
    uiState.loginStatusView = { items: null, loading: false };
    void loadLoginStatus();
    return true;
  }
  // ── 操作ログ: フィルター変更 ──
  const auditFilterEl = target.closest("[data-audit-filter]");
  if (auditFilterEl instanceof HTMLElement && onSecretMenu) {
    const key = auditFilterEl.dataset.auditFilter;
    const val = auditFilterEl instanceof HTMLSelectElement || auditFilterEl instanceof HTMLInputElement
      ? auditFilterEl.value : "";
    if (!uiState.auditLogFilter) uiState.auditLogFilter = { userId: "", resourceType: "", action: "", dateFrom: "", dateTo: "" };
    uiState.auditLogFilter[key] = val;
    // セレクト変更時は自動で再検索しない（「検索」ボタン押下まで待つ）
    return true;
  }
  // ── 操作ログ: リセット ──
  if (target.closest("[data-audit-log-reset]") instanceof HTMLElement && onSecretMenu) {
    uiState.auditLogFilter = { userId: "", resourceType: "", action: "", dateFrom: "", dateTo: "" };
    uiState.auditLogView = { items: null, loading: false, hasMore: false, offset: 0 };
    render();
    // リセット後は自動取得
    void loadAuditLog(false);
    return true;
  }
  // ── 操作ログ: 検索（新規） ──
  const auditLogLoadEl = target.closest("[data-audit-log-load]");
  if (auditLogLoadEl instanceof HTMLElement && onSecretMenu) {
    // フィルター値をDOMから再取得（入力途中も反映）
    const bar = document.querySelector(".al-filter-bar");
    if (bar) {
      if (!uiState.auditLogFilter) uiState.auditLogFilter = { userId: "", resourceType: "", action: "", dateFrom: "", dateTo: "" };
      bar.querySelectorAll("[data-audit-filter]").forEach(el => {
        const k = el.dataset.auditFilter;
        if (k && (el instanceof HTMLSelectElement || el instanceof HTMLInputElement)) {
          uiState.auditLogFilter[k] = el.value;
        }
      });
    }
    uiState.auditLogView = { items: null, loading: false, hasMore: false, offset: 0 };
    render();
    void loadAuditLog(false);
    return true;
  }
  // ── 操作ログ: さかのぼり読み込み ──
  if (target.closest("[data-audit-log-more]") instanceof HTMLElement && onSecretMenu) {
    void loadAuditLog(true);
    return true;
  }
  if (uiState.approvalPicker && target.getAttribute("data-approval-scrim") === "1") {
    uiState.approvalPicker = null;
    render();
    return true;
  }
  const pickUserRow = target.closest("[data-approval-pick-user]");
  if (pickUserRow instanceof HTMLElement && uiState.approvalPicker && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    const uid = pickUserRow.dataset.approvalPickUser || "";
    const { deptId, stepIndex } = uiState.approvalPicker;
    if (uid && deptId) {
      setApprovalStepValue(deptId, stepIndex, uid);
      showToast("承認者を設定しました");
    }
    uiState.approvalPicker = null;
    render();
    return true;
  }
  const openApprPicker = target.closest("[data-approval-open-picker]");
  if (openApprPicker instanceof HTMLElement && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    const sid = openApprPicker.dataset.approvalSid || "";
    const idx = Number.parseInt(String(openApprPicker.dataset.approvalIdx ?? "0"), 10) || 0;
    if (sid) {
      uiState.approvalPicker = { deptId: sid, stepIndex: idx };
      render();
    }
    return true;
  }
  const clearAppr = target.closest("[data-approval-approver-clear]");
  if (clearAppr instanceof HTMLElement && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    const sid = clearAppr.dataset.approvalSid || "";
    const idx = Number.parseInt(String(clearAppr.dataset.approvalIdx ?? "0"), 10) || 0;
    if (sid) {
      setApprovalStepValue(sid, idx, "");
      showToast("承認者を解除しました");
      render();
    }
    return true;
  }
  const addStepEl = target.closest("[data-approval-dept-add-step]");
  if (addStepEl instanceof HTMLElement && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    const id = addStepEl.dataset.approvalDeptAddStep || "";
    if (id) {
      addApprovalStepForDept(id);
      showToast("承認ステップを追加しました");
      render();
    }
    return true;
  }
  const addOtherFlowEl = target.closest("[data-approval-add-other-flow]");
  if (addOtherFlowEl instanceof HTMLElement && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    addApprovalOtherFlow();
    showToast("フローを追加しました（「承認フローを登録」で保存されます）");
    render();
    return true;
  }
  const removeOtherFlowEl = target.closest("[data-approval-other-flow-remove]");
  if (removeOtherFlowEl instanceof HTMLElement && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    const id = removeOtherFlowEl.dataset.approvalOtherFlowRemove || "";
    if (id) {
      const flow = (uiState.approvalOtherFlows || []).find((f) => String(f.id) === id);
      const name = flow?.name || "このフロー";
      if (window.confirm(`${name}を削除しますか？`)) {
        removeApprovalOtherFlow(id);
        if (uiState.approvalFlowOpenId === id) uiState.approvalFlowOpenId = "";
        showToast("フローを削除しました");
        render();
      }
    }
    return true;
  }
  if (target.closest("[data-approval-save-drafts]") instanceof HTMLElement && isAccountSettingsRoute() && (uiState.accountSettingsTab === "approval" || uiState.accountSettingsTab === "approval-bindings")) {
    void (async () => {
      const ok = await persistOrgApprovalSettings();
      if (ok === "conflict") {
        showToastError("他の担当者が先に承認ルールを保存したため登録できませんでした。最新の内容を確認のうえ、再度編集・保存してください");
        render();
        return;
      }
      // 成功は通常のトースト（自動で消える）。エラー用は × を押すまで残るため、成功に使うと出たままになる
      if (ok) {
        showToast("承認フローを登録しました");
        render();
      } else {
        showToastError("登録に失敗しました。通信を確認して再度お試しください");
      }
    })();
    return true;
  }
  // 案件ロック解除（プロジェクト一覧）: 直接解除・再ロック・解除申請モーダル
  const unlockDirectEl = target.closest("[data-project-unlock-direct]");
  if (unlockDirectEl instanceof HTMLElement) {
    void directUnlockProject(unlockDirectEl.getAttribute("data-project-unlock-direct") || "");
    return true;
  }
  const relockEl = target.closest("[data-project-relock]");
  if (relockEl instanceof HTMLElement) {
    void relockProject(relockEl.getAttribute("data-project-relock") || "");
    return true;
  }
  const unlockRequestEl = target.closest("[data-project-unlock-request]");
  if (unlockRequestEl instanceof HTMLElement) {
    uiState.projectUnlockRequestDraft = { projectId: unlockRequestEl.getAttribute("data-project-unlock-request") || "" };
    render();
    return true;
  }
  if (target.closest("[data-project-unlock-submit]")) {
    void submitProjectUnlockRequest();
    return true;
  }
  const unlockCloseEl = target.closest("[data-project-unlock-close]");
  if (unlockCloseEl instanceof HTMLElement) {
    const closeByOverlay = unlockCloseEl.classList.contains("cl-mini-modal-overlay");
    if (closeByOverlay && target.closest(".cl-mini-modal")) return true;
    uiState.projectUnlockRequestDraft = null;
    uiState.projectUnlockSaving = false;
    render();
    return true;
  }
  const budgetPeriodSelectEl = target.closest("[data-budget-period-select]");
  if (budgetPeriodSelectEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    const id = budgetPeriodSelectEl.dataset.budgetPeriodSelect || "";
    if (id) {
      uiState.selectedBudgetPeriodId = id;
      render();
    }
    return true;
  }
  const budgetPeriodAddEl = target.closest("[data-budget-period-add]");
  if (budgetPeriodAddEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    const numIn = document.querySelector("[data-budget-period-new-number]");
    const fromIn = document.querySelector("[data-budget-period-new-from]");
    const toIn = document.querySelector("[data-budget-period-new-to]");
    const periodNumStr = numIn instanceof HTMLInputElement ? sanitizeBudgetPeriodNumberInput(numIn.value) : "";
    const periodNumber = Number.parseInt(periodNumStr, 10);
    const rangeStart = fromIn instanceof HTMLInputElement ? normalizeYmStrict(fromIn.value) : "";
    const rangeEnd = toIn instanceof HTMLInputElement ? normalizeYmStrict(toIn.value) : "";
    if (!periodNumStr || !Number.isFinite(periodNumber) || periodNumber < 1) {
      showToast("期は1以上の数値で入力してください");
      return true;
    }
    if (!rangeStart || !rangeEnd) {
      showToast("期間の開始・終了を YYYY/MM 形式で入力してください（例：2026/04）");
      return true;
    }
    ensureBudgetPeriodsState();
    if (uiState.budgetPeriods.some((p) => (Number(p.periodNumber) || parseBudgetFiscalPeriodNumberFromLabel(p.label)) === periodNumber)) {
      showToast("この期番号は既に登録されています");
      return true;
    }
    const id = `fy_${Date.now()}`;
    const label = `${periodNumber}期`;
    const rangeLabel = `${rangeStart}～${rangeEnd}`;
    const period = {
      id,
      label,
      periodNumber,
      rangeLabel,
      rangeStart,
      rangeEnd,
      entryMode: "current_future"
    };
    const prevSelectedBudgetPeriodId = uiState.selectedBudgetPeriodId;
    uiState.budgetPeriods.push(period);
    sortBudgetPeriodsInPlace();
    uiState.selectedBudgetPeriodId = id;
    if (numIn instanceof HTMLInputElement) numIn.value = "";
    if (fromIn instanceof HTMLInputElement) fromIn.value = "";
    if (toIn instanceof HTMLInputElement) toIn.value = "";
    render();
    persistBudgetPeriod(period).then((ok) => {
      if (!ok) {
        const idx = uiState.budgetPeriods.findIndex((p) => p.id === id);
        if (idx >= 0) uiState.budgetPeriods.splice(idx, 1);
        uiState.selectedBudgetPeriodId = prevSelectedBudgetPeriodId;
        showToastError(`${periodNumber}期の追加に失敗しました`);
        return;
      }
      showToast(`${periodNumber}期を追加しました`);
    });
    return true;
  }
  return false;
}

/** 16-render-routing.js の click から移した分岐（元の 4554〜4609 行）。処理したら true（16 側はそこで止める） */
function handleBudgetRowClick(e, target) {
  const budgetRowAddEl = target.closest("[data-budget-row-add]");
  if (budgetRowAddEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const period = uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(uiState.budgetPeriods);
    if (!period) {
      showToast("先に会計期を追加してください");
      return true;
    }
    if (!budgetRowsLoadedForPeriod(period.id) || budgetRowsLoadFailedForPeriod(period.id)) {
      // 読込前に追加すると空配列に1行だけ入った状態で既存行を上書きしてしまう
      showToast("予算データを読み込み中です。少し待ってからお試しください");
      return true;
    }
    const rows = ensureBudgetRowsForSelectedPeriod();
    const deptByMemberId = new Map();
    (uiState.deptSettings?.departments || []).forEach((d) => {
      const name = String(d?.name || "").trim();
      if (!name) return;
      (Array.isArray(d?.memberIds) ? d.memberIds : []).forEach((mid) => deptByMemberId.set(String(mid), name));
    });
    const defaultDept = String((uiState.deptSettings?.departments || [])[0]?.name || "").trim();
    const takenMembers = new Set(rows.map((r) => String(r.member || "").trim()));
    const nextSales = users.find((u) => isActiveUser(u) && u.role === "営業" && !takenMembers.has(userNameWithRetiredLabel(u)));
    const nextAny = users.find((u) => isActiveUser(u) && !takenMembers.has(userNameWithRetiredLabel(u)));
    const picked = nextSales || nextAny || null;
    rows.push(normalizeBudgetRow({
      dept: picked ? (deptByMemberId.get(String(picked.id)) || defaultDept) : defaultDept,
      member: picked ? userNameWithRetiredLabel(picked) : "",
      ...Object.fromEntries(BUDGET_MONTH_KEYS.map((k) => [k, ""]))
    }));
    if (!uiState.budgetRowsByPeriod) uiState.budgetRowsByPeriod = {};
    uiState.budgetRowsByPeriod[period.id] = { rows, extendSales: true };
    saveBudgetDraftStore(uiState.budgetRowsByPeriod, { persistToServer: false });
    markBudgetMasterDirty();
    render();
    return true;
  }
  const budgetRowDeleteEl = target.closest("[data-budget-row-delete]");
  if (budgetRowDeleteEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const period = uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(uiState.budgetPeriods);
    if (!period) return true;
    const idx = Number.parseInt(budgetRowDeleteEl.dataset.budgetRowDelete || "-1", 10);
    const rows = ensureBudgetRowsForSelectedPeriod();
    if (rows.length <= 1) {
      showToast("最低1行は必要です");
      return true;
    }
    if (idx >= 0 && idx < rows.length) rows.splice(idx, 1);
    if (!uiState.budgetRowsByPeriod) uiState.budgetRowsByPeriod = {};
    uiState.budgetRowsByPeriod[period.id] = { rows, extendSales: true };
    saveBudgetDraftStore(uiState.budgetRowsByPeriod, { persistToServer: false });
    markBudgetMasterDirty();
    render();
    return true;
  }
  return false;
}

/** 16-render-routing.js の click から移した分岐（元の 4626〜4643 行）。処理したら true（16 側はそこで止める） */
function handleDeptEditClick(e, target) {
  const deptEditToggleEl = target.closest("[data-dept-edit-toggle]");
  if (deptEditToggleEl instanceof HTMLElement && isAccountSettingsRoute()) {
    ensureDeptSettingsState();
    const deptId = deptEditToggleEl.dataset.deptEditToggle || "";
    const dept = uiState.deptSettings.departments.find((d) => d.id === deptId);
    // 部署追加と同じミニモーダルで名前を変更する（保存は data-dept-save-add 側）
    if (dept) {
      uiState.deptCreateModal = { name: dept.name, editId: dept.id };
      render();
    }
    return true;
  }
  const deptOpenAddEl = target.closest("[data-dept-open-add]");
  if (deptOpenAddEl instanceof HTMLElement && isAccountSettingsRoute()) {
    uiState.deptCreateModal = { name: "" };
    render();
    return true;
  }
  return false;
}

/** 16-render-routing.js の click から移した分岐（元の 4685〜4874 行）。処理したら true（16 側はそこで止める） */
function handleDeptAndDomainClick(e, target) {
  const deptCloseAddEl = target.closest("[data-dept-close-add]");
  if (deptCloseAddEl instanceof HTMLElement && isAccountSettingsRoute()) {
    const closeByOverlay = deptCloseAddEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return true;
    uiState.deptCreateModal = null;
    render();
    return true;
  }
  const templateTabEl = target.closest("[data-template-tab]");
  if (templateTabEl instanceof HTMLElement) {
    uiState.templateTab = templateTabEl.dataset.templateTab;
    render();
    return true;
  }
  // 書類テンプレートの作成・編集・複製・削除・デフォルトは 15z-doc-templates.js（API で保存）
  const closeOrderFlowPromptEl = target.closest("[data-close-order-flow-prompt]");
  if (closeOrderFlowPromptEl instanceof HTMLElement) {
    uiState.orderFlowPromptProjectId = "";
    render();
    return true;
  }
  const orderFlowChoiceEl = target.closest("[data-order-flow-choice]");
  if (orderFlowChoiceEl instanceof HTMLElement) {
    const [choice, projectId] = orderFlowChoiceEl.dataset.orderFlowChoice.split(":");
    const p = projects.find((x) => x.id === projectId);
    if (!p) return true;
    p.useOrderFlow = choice === "use";
    p.currentStage = p.useOrderFlow ? "orders" : "invoices";
    p.nextAction = p.useOrderFlow ? "請書 / 発注の対応を進める" : "請求ステージの対応を進める";
    uiState.orderFlowPromptProjectId = "";
    showToast(p.useOrderFlow ? "請書 / 発注フローを有効化しました" : "請書 / 発注フローをスキップしました");
    return true;
  }
  const toggleOrderFlowEl = target.closest("[data-toggle-order-flow]");
  if (toggleOrderFlowEl instanceof HTMLElement) {
    const p = projects.find((x) => x.id === toggleOrderFlowEl.dataset.toggleOrderFlow);
    if (!p) return true;
    if (!canEditProject(p)) {
      showToast(readOnlyHint(p));
      return true;
    }
    p.useOrderFlow = !p.useOrderFlow;
    showToast(p.useOrderFlow ? "請書 / 発注を使用するに変更しました" : "請書 / 発注を使用しないに変更しました");
    render();
    return true;
  }
  const closeDocStartEl = target.closest("[data-close-doc-start]");
  if (closeDocStartEl instanceof HTMLElement) {
    closeDocStart();
    return true;
  }
  const docMethodEl = target.closest("[data-doc-method]");
  if (docMethodEl instanceof HTMLElement && uiState.docStart) {
    uiState.docStart.method = docMethodEl.dataset.docMethod;
    if (uiState.docStart.method !== "template") uiState.docStart.templateId = "";
    render();
    return true;
  }
  const docTemplateEl = target.closest("[data-doc-template]");
  if (docTemplateEl instanceof HTMLElement && uiState.docStart) {
    uiState.docStart.templateId = docTemplateEl.dataset.docTemplate;
    render();
    return true;
  }
  const docStartOpenEl = target.closest("[data-doc-start-open]");
  if (docStartOpenEl instanceof HTMLElement && uiState.docStart) {
    const d = uiState.docStart;
    const project = projects.find((p) => p.id === d.projectId);
    const defaultTemplate = getTemplatesByType(d.type).find((t) => t.isDefault);
    const chosen = d.method === "template" ? getTemplatesByType(d.type).find((t) => t.id === d.templateId) || defaultTemplate : null;
    if (d.method === "template" && !chosen) {
      showToast("テンプレートを選択してください");
      return true;
    }
    // 「見積から生成する」は保存済み見積書の明細（工数×単価の行）をそのまま請求書へコピー
    let quotePreset = null;
    if (d.method === "quote-generate") {
      const savedQuote = project?.savedDocs?.quote;
      if (!savedQuote?.lines?.length) {
        showToast("保存済みの見積書がありません。先に見積書を作成してください");
        return true;
      }
      quotePreset = {
        subject: `${project.name} 請求`,
        lines: savedQuote.lines.map((l) => ({ ...l })),
        note: savedQuote.note || "",
        taxRate: savedQuote.taxRate
      };
    }
    openComposer(d.type, d.projectId, {
      preset: chosen?.preset || quotePreset,
      templateName: chosen?.name
    });
    if (d.type === "invoice" && project) {
      project.invoiceStatus = "下書きあり";
      project.nextAction = "請求書を編集してください";
      project.currentStage = "invoices";
    }
    if (d.method === "quote-generate") showToast("見積情報をもとに請求書を生成しました");
    return true;
  }
  const openProjectCreateEl = target.closest("[data-open-project-create]");
  if (openProjectCreateEl instanceof HTMLElement) {
    openProjectCreate();
    return true;
  }
  const closeProjectCreateEl = target.closest("[data-close-project-create]");
  if (closeProjectCreateEl instanceof HTMLElement) {
    if (!fpConfirmDiscardDirtyForm("projectCreate")) return true;
    closeProjectCreate();
    return true;
  }
  const createModeEl = target.closest("[data-create-mode]");
  if (createModeEl instanceof HTMLElement && uiState.projectCreate) {
    uiState.projectCreate.mode = createModeEl.dataset.createMode;
    render();
    return true;
  }
  const projectCreateEl = target.closest("[data-project-create]");
  if (projectCreateEl instanceof HTMLElement) {
    // 作成後は案件詳細へ遷移するため、他のフォーム状態を先に畳んでおく
    uiState.projectDetailForm = null;
    uiState.composer = null;
    uiState.costForm = null;
    uiState.expenseForm = null;
    uiState.paymentForm = null;
    uiState.docStart = null;
    uiState.templateEditor = null;
    uiState.orderFlowPromptProjectId = "";
    createProjectFromModal();
    return true;
  }
  const projectCreateForceEl = target.closest("[data-project-create-force]");
  if (projectCreateForceEl instanceof HTMLElement) {
    createProjectFromModal(true);
    return true;
  }
  const projectCreateCancelWarningEl = target.closest("[data-project-create-cancel-warning]");
  if (projectCreateCancelWarningEl instanceof HTMLElement) {
    uiState.projectCreateWarning = null;
    render();
    return true;
  }
  const projectSaveEl = target.closest("[data-project-save]");
  if (projectSaveEl instanceof HTMLElement) {
    showToast("新規案件の下書きを保存しました");
    return true;
  }
  const openClientCreateEl = target.closest("[data-open-client-create]");
  if (openClientCreateEl instanceof HTMLElement) {
    openClientModal({ mode: "create", fromProject: true });
    return true;
  }
  const openClientManagerEl = target.closest("[data-open-client-manager]");
  if (openClientManagerEl instanceof HTMLElement) {
    if (!canManageClientPartnerMaster()) {
      showToast("新規登録はマスター／経理のみ可能です");
      return true;
    }
    openClientModal({ mode: "create", fromProject: false, centered: true });
    return true;
  }
  const clientActionEl = target.closest("[data-client-action]");
  if (clientActionEl instanceof HTMLElement) {
    const [action, id] = clientActionEl.dataset.clientAction.split(":");
    if (action === "edit") {
      // data-client-edit-inline="1" 付き（取引先詳細の編集ボタン）はページ埋め込みで編集する
      openClientModal({
        mode: "edit",
        clientId: id,
        fromProject: false,
        centered: true,
        inline: clientActionEl.dataset.clientEditInline === "1"
      });
      return true;
    }
  }
  // ── ドメイン管理 ──
  const openDomainManagerEl = target.closest("[data-open-domain-manager]");
  if (openDomainManagerEl instanceof HTMLElement) {
    uiState.domainCreate = {
      mode: "create",
      status: "契約", service: "", services: "", subdomain: "", domain: "", registrarId: "", registrarPassword: "",
      clientName: "", owner: "", projectNote: "", renewedDate: "", notes: "",
      pwVisible: false
    };
    render();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 4914〜6244 行）。処理したら true（16 側はそこで止める） */
function handleMasterDataClick(e, target) {
  const domainActionEl = target.closest("[data-domain-action]");
  if (domainActionEl instanceof HTMLElement && !target.closest(".td-check")) {
    const [action, id] = domainActionEl.dataset.domainAction.split(":");
    if (action === "edit") {
      const d = domains.find((x) => x.id === id);
      if (d) {
        uiState.domainCreate = {
          mode: "edit", id: d.id, dbId: d.dbId,
          status: d.status || "契約",
          service: d.service || "",
          services: d.services || "",
          subdomain: d.subdomain || "", domain: d.domain || "",
          registrarId: d.registrarId || "", registrarPassword: d.registrarPassword || "",
          clientName: d.clientName || "", owner: d.owner || "", projectNote: d.projectNote || "",
          renewedDate: d.renewedDate || "",
          notes: d.notes || "", pwVisible: false
        };
        render();
      }
    }
    return true;
  }
  const closeDomainCreateEl = target.closest("[data-close-domain-create]");
  if (closeDomainCreateEl instanceof HTMLElement) {
    uiState.domainCreate = null;
    render();
    return true;
  }
  const domainCreatePwToggleEl = target.closest("[data-domain-create-pw-toggle]");
  if (domainCreatePwToggleEl instanceof HTMLElement && uiState.domainCreate) {
    uiState.domainCreate.pwVisible = !uiState.domainCreate.pwVisible;
    render();
    return true;
  }
  const domainDeleteRequestEl = target.closest("[data-domain-delete-request]");
  if (domainDeleteRequestEl instanceof HTMLElement && uiState.domainCreate?.mode === "edit" && uiState.domainCreate?.id) {
    if (!window.confirm("このドメインを削除しますか？")) return true;
    const d = uiState.domainCreate;
    const idx = domains.findIndex((x) => x.id === d.id);
    const prevDomain = idx >= 0 ? domains[idx] : null;
    if (idx >= 0) domains.splice(idx, 1);
    uiState.domainCreate = null;
    render();
    if (d.dbId) {
      fetch("/api/domains", { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ id: d.dbId }) })
        .then((r) => {
          if (!r.ok) {
            if (prevDomain) domains.splice(idx, 0, prevDomain);
            showToastError("ドメインの削除に失敗しました");
            render();
            return;
          }
          showToast("ドメインを削除しました");
        })
        .catch(() => {
          if (prevDomain) domains.splice(idx, 0, prevDomain);
          showToastError("通信エラーのため削除できませんでした（画面を元に戻しました）");
          render();
        });
    } else {
      showToast("ドメインを削除しました");
    }
    return true;
  }
  // 📅ボタン or 日付テキスト欄クリックでカレンダーを開く（テキスト欄はフォーカス維持で手入力も可）
  const domainDateTriggerEl = target.closest("[data-domain-date-open], [data-domain-date-text]");
  if (domainDateTriggerEl instanceof HTMLElement) {
    const field = domainDateTriggerEl.dataset.domainDateOpen || domainDateTriggerEl.dataset.domainDateText;
    const native = domainDateTriggerEl.closest(".dm-date-input-wrap")?.querySelector(`input[data-domain-date-picker="${field}"]`);
    if (native instanceof HTMLInputElement && typeof native.showPicker === "function") {
      try { native.showPicker(); } catch { /* noop */ }
    }
    // テキスト欄クリック時は return せず、フォーカス/キャレット配置（手入力）はそのまま通す
    if (domainDateTriggerEl.dataset.domainDateOpen) return true;
  }
  const saveDomainEl = target.closest("[data-save-domain]");
  if (saveDomainEl instanceof HTMLElement && uiState.domainCreate) {
    const d = uiState.domainCreate;
    if (!String(d.domain || "").trim() && !String(d.subdomain || "").trim()) {
      window.alert("ドメインまたはサブドメインを入力してください");
      return true;
    }
    const fields = {
      status: d.status || "契約",
      service: String(d.service || "").trim(),
      services: String(d.services || "").trim(),
      subdomain: String(d.subdomain || "").trim(),
      domain: String(d.domain || "").trim(),
      registrarId: String(d.registrarId || "").trim(),
      registrarPassword: String(d.registrarPassword || ""),
      clientName: String(d.clientName || "").trim(),
      owner: String(d.owner || "").trim(),
      projectNote: String(d.projectNote || "").trim(),
      renewedDate: parseCsvDateToIsoYmd(d.renewedDate),
      notes: String(d.notes || "")
    };
    if (d.mode === "edit" && d.id) {
      const idx = domains.findIndex((x) => x.id === d.id);
      const prevDomain = idx >= 0 ? domains[idx] : null;
      if (idx >= 0) domains[idx] = { ...domains[idx], ...fields };
      const dbId = d.dbId;
      uiState.domainCreate = null;
      render();
      if (dbId) {
        fetch("/api/domains", { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ ...fields, id: dbId }) })
          .then(async (r) => {
            if (!r.ok) {
              let resBody = {};
              try { resBody = await r.json(); } catch { resBody = {}; }
              if (idx >= 0 && prevDomain) domains[idx] = prevDomain;
              showToastError(typeof resBody.error === "string" ? resBody.error : "ドメインの更新に失敗しました");
              render();
              return;
            }
            showToast("ドメインを更新しました");
          })
          .catch(() => {
            if (idx >= 0 && prevDomain) domains[idx] = prevDomain;
            showToastError("通信エラーのため更新できませんでした（画面を元に戻しました）");
            render();
          });
      } else {
        showToast("ドメインを更新しました");
      }
      return true;
    }
    const tempId = `dmtmp-${Date.now()}`;
    domains.unshift({ id: tempId, dbId: null, ...fields });
    uiState.domainCreate = null;
    render();
    fetch("/api/domains", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify(fields) })
      .then(async (r) => {
        let data = {};
        try { data = await r.json(); } catch { data = {}; }
        if (!r.ok || !(data.ok && data.domain)) {
          const idx = domains.findIndex((x) => x.id === tempId);
          if (idx >= 0) domains.splice(idx, 1);
          showToastError(typeof data.error === "string" ? data.error : "ドメインの登録に失敗しました");
          render();
          return;
        }
        const nd = data.domain;
        const idx = domains.findIndex((x) => x.id === tempId);
        if (idx >= 0) domains[idx] = { ...domains[idx], id: `dm${nd.id}`, dbId: nd.id };
        showToast("ドメインを登録しました");
      })
      .catch(() => {
        const idx = domains.findIndex((x) => x.id === tempId);
        if (idx >= 0) domains.splice(idx, 1);
        showToastError("通信エラーのため登録できませんでした（画面を元に戻しました）");
        render();
      });
    return true;
  }
  // ── パートナー ──
  const openPartnerCreateEl = target.closest("[data-open-partner-create]");
  if (openPartnerCreateEl instanceof HTMLElement) {
    if (!canManageClientPartnerMaster()) {
      showToast("新規登録はマスター／経理のみ可能です");
      return true;
    }
    const now = new Date();
    const defaultDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const defaultTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    uiState.partnerCreate = {
      name: "", branch: "", honorific: "御中", contactName: "", contractOwner: "", contactEmail: "", contactTel: "", fax: "",
      hpUrl: "",
      postalCode: "", prefecture: "", address: "", building: "",
      paymentTerms: "月末締翌月末払", paymentMethod: "銀行振込",
      transferFeeBurden: "先方", deliveryMethod: "メール(DL)",
      bankName: "", branchName: "", accountType: "", accountNumber: "", accountHolder: "",
      withholdingTax: "なし",
      ndaStatus: "未", contractStatus: "未",
      approvalStatus: "未申請",
      notes: "", ndaPurpose: "", tags: "",
      firstRegisteredAt: "",
      firstRegisteredDate: defaultDate,
      firstRegisteredTime: defaultTime,
      corporateNumber: "", customerNo: "", invoiceIssuer: "", invoiceIssuerNumber: "", expectedAmount: "",
      // data-open-partner-create="hidden"（その他タブの登録ボタン）は営業表示=非表示で始める
      salesHidden: openPartnerCreateEl.dataset.openPartnerCreate === "hidden"
    };
    resetCorporateLocalSearch();
    render();
    return true;
  }
  const partnerEditEl = target.closest("[data-partner-edit]");
  if (partnerEditEl instanceof HTMLElement) {
    // state 構築は openPartnerEditModal（15-sp-backlog）に一本化。
    // data-partner-edit-inline="1" 付き（取引先詳細の編集ボタン）はページ埋め込みで編集する
    openPartnerEditModal(Number(partnerEditEl.dataset.partnerEdit), {
      inline: partnerEditEl.dataset.partnerEditInline === "1"
    });
    return true;
  }
  const closePartnerEl = target.closest("[data-close-partner]");
  if (closePartnerEl instanceof HTMLElement) {
    uiState.partnerCreate = null;
    resetCorporateLocalSearch();
    render();
    return true;
  }
  const partnerExportDocEl = target.closest("[data-partner-export-doc]");
  if (partnerExportDocEl instanceof HTMLElement && uiState.partnerCreate) {
    const kind = String(partnerExportDocEl.dataset.partnerExportDoc || "");
    if (kind === "nda" || kind === "gyomu-itaku") {
      void (async () => {
        try {
          await downloadFilledClientContractDocx(kind, "partner");
        } catch (e) {
          console.error(e);
          showToastError("出力に失敗しました");
        }
      })();
    }
    return true;
  }
  const savePartnerEl = target.closest("[data-save-partner]");
  if (savePartnerEl instanceof HTMLElement && uiState.partnerCreate) {
    // 新規登録(idなし)はマスター/経理のみ。変更(更新)は全員可。
    if (!uiState.partnerCreate.id && !canManageClientPartnerMaster()) {
      showToast("新規登録はマスター／経理のみ可能です");
      return true;
    }
    // 画面上「（必須）」表示の項目が未入力でも保存できていた（クライアント側の requiredFilled と同型のチェックが
    // パートナー側だけ抜けていた。2026-09-04 ユーザー報告）
    {
      const pc = uiState.partnerCreate;
      const missing = [
        [String(pc.name || "").trim(), "発注先名", '[data-partner-field="name"]'],
        [String(pc.paymentTerms || "").trim(), "支払条件", '[data-partner-field="paymentTerms"]'],
        [String(pc.paymentMethod || "").trim(), "支払方法", 'input[name="partnerPayMethod"]'],
        [String(pc.transferFeeBurden || "").trim(), "振込手数料負担", '[data-partner-field="transferFeeBurden"]'],
        [normalizeDeliveryMethodStored(pc.deliveryMethod || "").length > 0, "書類送付方法", "[data-partner-delivery-part]"],
        [String(pc.withholdingTax || "").trim(), "源泉徴収区分", 'input[name="partnerWithholding"]']
      ].filter(([filled]) => !filled).map(([, label, selector]) => ({ label, selector }));
      if (missing.length) {
        reportMissingRequiredFields(missing);
        return true;
      }
      // 適格請求書発行事業者「未設定」のまま新規登録できていた（2026-09-07 ユーザー指摘）。
      // 既存パートナーの更新（idあり）は既に「未設定」で登録済みの行を触れなくなるため対象外にする。
      // 文言は clientApplyInvoiceError（取引承認の申請時チェック向け）を流用せず新規登録専用にする
      // （「編集から入力してください」は新規登録画面では意味が通らない。2026-09-07 ユーザー指摘）
      if (!pc.id) {
        let invoiceErr = "";
        if (pc.invoiceIssuer !== "該当する") {
          invoiceErr = "パートナーの申請には、適格請求書発行事業者（インボイス）が必須です。該当するボタンのチェックと、登録番号の入力を行ってください。";
        } else if (!/^T\d{13}$/.test(String(pc.invoiceIssuerNumber || "").trim())) {
          invoiceErr = "適格請求書発行事業者の登録番号（T + 13桁の数字）の形式になっていません。確認して再度入力してください。";
        }
        if (invoiceErr) {
          reportFieldError(invoiceErr, ['[data-partner-field="invoiceIssuer"]', '[data-partner-field="invoiceIssuerNumber"]']);
          return true;
        }
      }
    }
    // おおよその取引予定金額は新規登録時のみ必須（2026-09-07 ユーザー要望：登録画面で入力させる。
    // 正確な額でなく「おおよそ・分かる範囲」でよい）。既存パートナーの更新（idあり）は
    // 未入力のまま保存できていた既存行を触れなくなるため対象外にし、従来どおり任意のまま
    let registerExpectedAmount = null;
    if (String(uiState.partnerCreate.expectedAmount ?? "").trim() !== "") {
      registerExpectedAmount = clientApplyAmountParse(uiState.partnerCreate.expectedAmount);
      if (registerExpectedAmount == null) {
        reportFieldError("おおよその取引予定金額は数値で入力してください（空欄も可）", ['[data-partner-field="expectedAmount"]']);
        return true;
      }
    } else if (!uiState.partnerCreate.id) {
      reportFieldError("おおよその取引予定金額を入力してください（おおよそ・分かる範囲でOK）", ['[data-partner-field="expectedAmount"]']);
      return true;
    }
    // 資本金・設立年月（与信ルールの判断材料。新規登録のみ必須・15k-client-apply-credit.js）
    const partnerCredit = clientFormCreditValues("partner", uiState.partnerCreate, !uiState.partnerCreate.id);
    if (partnerCredit.error) {
      reportFieldError(partnerCredit.error, [partnerCredit.selector]);
      return true;
    }
    // 顧客番号は入力させない（承認時に自動発番。board移行済みの既存番号は state 経由でそのまま保持）
    // 申請が通る前（未申請・申請中・却下）は申請なしで直接保存。承認済のマスタだけ変更申請フロー
    // （クライアント側の保存分岐と同じ整理。ユーザー要望「登録申請はまだしたくないけど保存したい」）
    const prowForSave = (uiState.partnersCache || []).find((x) => Number(x.id) === Number(uiState.partnerCreate.id));
    // パートナーの直接更新（PUT）はサーバーがマスター/経理に限っているため、それ以外の人の編集は
    // 未承認でも変更申請で送る（以前は直接 PUT して必ず 403 になり、営業は保存できなかった）
    const isApprovedPartner =
      !!uiState.partnerCreate.id &&
      (normalizePartnerApprovalStatus(prowForSave?.approval_status, prowForSave?.nda_status, prowForSave?.contract_status) === "承認済" ||
        !canManageClientPartnerMaster());
    // 更新は保留型の申請フロー（承認まで反映されない）。うっかり送信を防ぐため確認を挟む（新規登録・直接保存は従来どおり）
    if (isApprovedPartner && !window.confirm("変更の申請をしますか？（承認されると内容が反映されます）")) return true;
    // 二重送信防止
    if (uiState.partnerCreate._submitting) return true;
    uiState.partnerCreate._submitting = true;
    render();
    const p = uiState.partnerCreate;
    const payload = {
      ...p,
      firstRegisteredAt: buildFirstRegisteredAtValue(p.firstRegisteredDate, p.firstRegisteredTime),
      bankInfo: composePartnerBankInfo(p),
      expectedAmount: registerExpectedAmount,
      capitalAmount: partnerCredit.capitalAmount,
      establishedYm: partnerCredit.establishedYm
    };
    // 承認済の更新のみ保留型の申請フロー（application-management 承認まではDB未反映）。それ以外は即時。
    void (async () => {
      const sent = p.id
        ? isApprovedPartner
          ? await submitMasterChangeRequest("partner", p.id, payload)
          : await (async () => {
              const r = await apiFetch("/api/partners", { method: "PUT", body: { ...payload, id: p.id } });
              return { ok: r.ok && r.data?.ok === true, error: typeof r.data?.error === "string" ? r.data.error : "" };
            })()
        : await (async () => {
            const r = await apiFetch("/api/partners", { method: "POST", body: payload });
            return { ok: r.ok && r.data?.ok === true, error: typeof r.data?.error === "string" ? r.data.error : "" };
          })();
      if (sent.ok) {
        showToast(p.id ? (isApprovedPartner ? "更新申請を送信しました。承認されると反映されます" : "保存しました") : "パートナーを登録しました");
        uiState.partnerCreate = null;
        resetCorporateLocalSearch();
        if (p.id && isApprovedPartner) render();
        else loadDataFromDB().then(() => render());
      } else {
        if (uiState.partnerCreate) uiState.partnerCreate._submitting = false;
        showToastError(sent.error || (p.id && isApprovedPartner ? "申請に失敗しました" : "保存に失敗しました"));
        render();
      }
    })();
    return true;
  }
  const partnerDeleteRequestEl = target.closest("[data-partner-delete-request]");
  if (partnerDeleteRequestEl instanceof HTMLElement && uiState.partnerCreate?.id) {
    if (!canManageClientPartnerMaster()) {
      showToast("削除はマスター／経理のみ可能です");
      return true;
    }
    if (!window.confirm("このパートナーの削除を申請しますか？（承認されるまで削除されません）")) return true;
    const id = uiState.partnerCreate.id;
    fetch("/api/partners", { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ id }) })
      .then(r => r.json()).then(data => {
        if (data.ok) {
          uiState.partnerCreate = null;
          resetCorporateLocalSearch();
          showToast("削除を申請しました。承認されると削除されます");
          render();
        } else {
          showToastError(typeof data.error === "string" ? data.error : "削除の申請に失敗しました");
        }
      }).catch(() => {
        showToastError("通信エラーのため申請できませんでした。再度お試しください。");
      });
    return true;
  }
  const corpSubmitEl = target.closest("[data-corp-local-submit]");
  if (corpSubmitEl instanceof HTMLElement && corpSubmitEl.dataset.corpLocalSubmit) {
    const mode = String(corpSubmitEl.dataset.corpLocalSubmit || "");
    if (mode !== "client" && mode !== "partner") return true;
    if (mode === "client" && !uiState.clientCreate) return true;
    if (mode === "partner" && !uiState.partnerCreate) return true;
    const aside = corpSubmitEl.closest("aside");
    const inp = aside?.querySelector(`[data-corp-local-q="${mode}"]`);
    const q = inp instanceof HTMLInputElement ? inp.value.trim() : "";
    if (!q) {
      showToast("検索語を入力してください");
      return true;
    }
    if (!uiState.corporateLocalSearch) resetCorporateLocalSearch();
    uiState.corporateLocalSearch.context = mode;
    uiState.corporateLocalSearch.query = q;
    uiState.corporateLocalSearch.loading = true;
    uiState.corporateLocalSearch.error = "";
    uiState.corporateLocalSearch.results = null;
    uiState.corporateLocalSearch.indexRowCount = null;
    uiState.corporateLocalSearch.searchHint = "";
    const reqSeq = (uiState.corporateLocalSearch.reqSeq || 0) + 1;
    uiState.corporateLocalSearch.reqSeq = reqSeq;
    render();
    fetch("/api/corporate-search-local", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({ q, mode })
    })
      .then((r) => r.json())
      .then((data) => {
        if (!uiState.corporateLocalSearch || uiState.corporateLocalSearch.context !== mode || uiState.corporateLocalSearch.reqSeq !== reqSeq) return; // 後から検索し直していたら古い結果は捨てる
        uiState.corporateLocalSearch.loading = false;
        if (!data.ok) {
          uiState.corporateLocalSearch.error = typeof data.error === "string" ? data.error : "検索に失敗しました";
          uiState.corporateLocalSearch.results = null;
        } else {
          uiState.corporateLocalSearch.error = "";
          uiState.corporateLocalSearch.results = Array.isArray(data.results) ? data.results : [];
          uiState.corporateLocalSearch.indexRowCount =
            typeof data.indexRowCount === "number" ? data.indexRowCount : null;
          uiState.corporateLocalSearch.searchHint =
            typeof data.searchHint === "string" ? data.searchHint : "";
        }
        render();
      })
      .catch(() => {
        if (!uiState.corporateLocalSearch || uiState.corporateLocalSearch.context !== mode || uiState.corporateLocalSearch.reqSeq !== reqSeq) return;
        uiState.corporateLocalSearch.loading = false;
        uiState.corporateLocalSearch.error = "通信エラー";
        uiState.corporateLocalSearch.results = null;
        uiState.corporateLocalSearch.searchHint = "";
        render();
      });
    return true;
  }
  const corpPickEl = target.closest("[data-corp-local-pick]");
  if (corpPickEl instanceof HTMLElement && corpPickEl.dataset.corpLocalPick) {
    const raw = String(corpPickEl.dataset.corpLocalPick || "");
    const colon = raw.indexOf(":");
    const mode = colon >= 0 ? raw.slice(0, colon) : "";
    const idx = colon >= 0 ? Number(raw.slice(colon + 1)) : NaN;
    if (mode !== "client" && mode !== "partner") return true;
    const ls = uiState.corporateLocalSearch;
    const row = ls && Array.isArray(ls.results) && Number.isFinite(idx) ? ls.results[idx] : null;
    if (!row) return true;
    // 候補を明示的に選んだ＝その法人の値で上書きする。空欄のみ補完にすると、
    // 一度反映したあと別候補を選び直しても会社名が変わらない（再照会が無反応に見える）
    if (mode === "client" && uiState.clientCreate) {
      syncClientCreateFormIntoState();
      const c = uiState.clientCreate;
      if (row.corporateNumber) c.corporateNumber = row.corporateNumber;
      if (row.name) c.name = row.name;
      if (row.postalCode) c.postalCode = row.postalCode;
      const pref = resolveClientPrefectureFromRow(row.prefecture, row.address);
      if (pref) c.prefecture = pref;
      if (row.address) c.address = row.address;
    } else if (mode === "partner" && uiState.partnerCreate) {
      const p = uiState.partnerCreate;
      if (row.corporateNumber) p.corporateNumber = row.corporateNumber;
      if (row.name) p.name = row.name;
      if (row.postalCode) p.postalCode = row.postalCode;
      const pref = resolveClientPrefectureFromRow(row.prefecture, row.address);
      if (pref) p.prefecture = pref;
      if (row.address) p.address = row.address;
    }
    if (uiState.corporateLocalSearch) {
      uiState.corporateLocalSearch.results = null;
      uiState.corporateLocalSearch.error = "";
      uiState.corporateLocalSearch.loading = false;
      uiState.corporateLocalSearch.searchHint = "";
    }
    showToast("反映しました");
    render();
    return true;
  }
  const closeClientCreateEl = target.closest("[data-close-client-create]");
  if (closeClientCreateEl instanceof HTMLElement) {
    uiState.clientCreate = null;
    resetCorporateLocalSearch();
    render();
    return true;
  }
  const clientDeleteRequestEl = target.closest("[data-client-delete-request]");
  if (clientDeleteRequestEl instanceof HTMLElement && uiState.clientCreate?.mode === "edit" && uiState.clientCreate?.id) {
    if (!canManageClientPartnerMaster()) {
      showToast("削除はマスター／経理のみ可能です");
      return true;
    }
    if (!window.confirm("このクライアントの削除を申請しますか？（承認されるまで削除されません）")) return true;
    const id = uiState.clientCreate.id;
    const dbId = clients.find((x) => x.id === id)?.dbId;
    if (!dbId) return true;
    // 削除は保留型の申請フロー（application-management 承認で is_active=false）。
    // まだ削除されていないので、成功してもローカルの一覧から消さない
    fetch("/api/clients", { method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ id: dbId }) })
      .then(r => r.json()).then(data => {
        if (data.ok) {
          uiState.clientCreate = null;
          resetCorporateLocalSearch();
          showToast("削除を申請しました。承認されると削除されます");
          render();
        } else {
          showToastError(typeof data.error === "string" ? data.error : "削除の申請に失敗しました");
        }
      }).catch(() => {
        showToastError("通信エラーのため申請できませんでした。再度お試しください。");
      });
    return true;
  }
  const clientExportDocEl = target.closest("[data-client-export-doc]");
  if (clientExportDocEl instanceof HTMLElement && uiState.clientCreate) {
    const kind = String(clientExportDocEl.dataset.clientExportDoc || "");
    if (kind === "nda" || kind === "gyomu-itaku") {
      void (async () => {
        try {
          await downloadFilledClientContractDocx(kind);
        } catch (e) {
          console.error(e);
          showToastError("出力に失敗しました");
        }
      })();
    }
    return true;
  }
  const saveClientEl = target.closest("[data-save-client]");
  if (saveClientEl instanceof HTMLElement && uiState.clientCreate) {
    const c = uiState.clientCreate;
    // 新規登録はマスター/経理のみ（案件作成中 fromProject は営業も可）。変更(更新)は全員可。
    if (c.mode !== "edit" && !c.fromProject && !canManageClientPartnerMaster()) {
      showToast("新規登録はマスター／経理のみ可能です");
      return true;
    }
    const missing = [
      [String(c.name || "").trim(), "顧客名", '[data-client-field="name"]'],
      [String(c.shortName || "").trim(), "顧客略称名", '[data-client-field="shortName"]'],
      [String(c.paymentTerms || "").trim(), "デフォルト支払条件", '[data-client-field="paymentTerms"]'],
      [String(c.transferFeeBurden || "").trim(), "振込手数料負担", '[data-client-field="transferFeeBurden"]'],
      [normalizeDeliveryMethodStored(c.deliveryMethod || "").length > 0, "書類送付方法", "[data-client-delivery-part]"]
    ].filter(([filled]) => !filled).map(([, label, selector]) => ({ label, selector }));
    if (missing.length) {
      reportMissingRequiredFields(missing);
      return true;
    }
    // おおよその取引予定金額は新規登録時のみ必須（2026-09-07 ユーザー要望：登録画面で入力させる。
    // 正確な額でなく「おおよそ・分かる範囲」でよい）。既存クライアントの更新（edit）は
    // 未入力のまま保存できていた既存行を触れなくなるため対象外にし、従来どおり任意のまま
    let registerExpectedAmount = null;
    if (String(c.expectedAmount ?? "").trim() !== "") {
      registerExpectedAmount = clientApplyAmountParse(c.expectedAmount);
      if (registerExpectedAmount == null) {
        reportFieldError("おおよその取引予定金額は数値で入力してください（空欄も可）", ['[data-client-field="expectedAmount"]']);
        return true;
      }
    } else if (c.mode !== "edit") {
      reportFieldError("おおよその取引予定金額を入力してください（おおよそ・分かる範囲でOK）", ['[data-client-field="expectedAmount"]']);
      return true;
    }
    // 資本金・設立年月（与信ルールの判断材料。新規登録のみ必須・15k-client-apply-credit.js）
    const clientCredit = clientFormCreditValues("client", c, c.mode !== "edit");
    if (clientCredit.error) {
      reportFieldError(clientCredit.error, [clientCredit.selector]);
      return true;
    }
    // 顧客番号は入力させない（承認時に自動発番。board移行済みの既存番号は state 経由でそのまま保持）
    const excludeId = c.mode === "edit" && c.id ? c.id : "";
    if (findExistingClientByName(c.name, excludeId)) {
      reportFieldError("この顧客名は既に登録があります", ['[data-client-field="name"]']);
      return true;
    }
    const editingClient = c.mode === "edit" && c.id ? clients.find((x) => x.id === c.id) : null;
    // 締結状態を編集フォームで変えられるのはマスター・経理だけ（15k-client-list-fields.js clientFormDocStatusRowHtml）。
    // それ以外の人・新規登録はフォームの値を使わない（新規は「未」固定）
    const docStatusFrom = canManageClientPartnerMaster() ? c : editingClient;
    const ndaStatus = c.mode === "edit" ? (docStatusFrom?.ndaStatus || "未") : "未";
    const contractStatus = c.mode === "edit" ? (docStatusFrom?.contractStatus || "未") : "未";
    const approvalStatus = c.mode === "edit" ? normalizeMasterApprovalStatus(editingClient?.approvalStatus ?? "未申請") : "未申請";
    const clientFields = {
      name: c.name,
      shortName: c.shortName,
      honorific: c.honorific,
      postalCode: c.postalCode,
      prefecture: c.prefecture,
      address: c.address,
      building: c.building,
      tel: c.tel,
      fax: c.fax,
      hpUrl: c.hpUrl,
      contractOwner: String(c.contractOwner ?? "").trim(),
      paymentTerms: c.paymentTerms,
      transferFeeBurden: c.transferFeeBurden,
      ndaStatus,
      contractStatus,
      approvalStatus,
      deliveryMethod: normalizeDeliveryMethodStored(c.deliveryMethod || ""),
      contactName: c.contactName,
      email: c.email,
      notes: c.notes,
      ndaPurpose: c.ndaPurpose,
      tags: c.tags,
      firstRegisteredAt: buildFirstRegisteredAtValue(c.firstRegisteredDate, c.firstRegisteredTime),
      corporateNumber: c.corporateNumber,
      customerNo: String(c.customerNo ?? "").trim(),
      invoiceIssuer: c.invoiceIssuer,
      invoiceIssuerNumber: c.invoiceIssuerNumber,
      expectedAmount: registerExpectedAmount,
      // 編集で空欄のときはサーバが既存値を残すので、ローカルの行も既存値のままにする
      capitalAmount: clientCredit.capitalAmount ?? editingClient?.capitalAmount ?? null,
      establishedYm: clientCredit.establishedYm || editingClient?.establishedYm || ""
    };
    if (c.mode === "edit" && c.id) {
      const existing = clients.find((x) => x.id === c.id);
      const dbId = existing?.dbId;
      const applyEditLocal = () => {
        const idx = clients.findIndex((x) => x.id === c.id);
        if (idx >= 0) {
          clients[idx] = { ...clients[idx], ...clientFields };
          upsertContractFromClient(clients[idx]);
        }
        // 応答待ちの間にユーザーが別フォームを開いていたら、そちらは閉じない
        if (uiState.clientCreate === c) {
          uiState.clientCreate = null;
          resetCorporateLocalSearch();
        }
        showToast("クライアントを更新しました");
      };
      if (!dbId) {
        applyEditLocal();
        return true;
      }
      // 申請が通る前（未申請・申請中・却下）は申請なしで直接保存する
      // （仮登録の基本情報を直すだけで承認往復を要求しない。ユーザー要望「登録申請はまだしたくないけど保存したい」）。
      // 承認済のマスタだけ従来どおり変更申請フローに乗せる
      const isApprovedEntity = normalizeMasterApprovalStatus(existing?.approvalStatus) === "承認済";
      if (!isApprovedEntity) {
        if (c._submitting) return true;
        c._submitting = true;
        render();
        fetch("/api/clients", { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ id: dbId, ...clientFields }) })
          .then((r) => r.json())
          .then((data) => {
            c._submitting = false;
            if (data.ok) {
              applyEditLocal();
              render();
            } else {
              showToastError(typeof data.error === "string" ? data.error : "保存に失敗しました");
              render();
            }
          })
          .catch(() => {
            c._submitting = false;
            showToastError("通信エラーのため保存できませんでした。再度お試しください。");
            render();
          });
        return true;
      }
      // 更新は保留型の申請フロー（承認まで反映されない）。うっかり送信を防ぐため確認を挟む
      if (!window.confirm("変更の申請をしますか？（承認されると内容が反映されます）")) return true;
      // 二重送信防止。更新は保留型の申請フロー（application-management 承認まではDB未反映）のため、
      // 成功してもローカルの clients[idx] へは反映しない（=applyEditLocal は呼ばない）
      if (c._submitting) return true;
      c._submitting = true;
      render();
      void (async () => {
        const sent = await submitMasterChangeRequest("client", dbId, clientFields);
        c._submitting = false;
        if (sent.ok) {
          if (uiState.clientCreate === c) {
            uiState.clientCreate = null;
            resetCorporateLocalSearch();
          }
          showToast("更新申請を送信しました。承認されると反映されます");
          render();
        } else {
          showToastError(sent.error || "申請に失敗しました。再度お試しください。");
        }
      })();
      return true;
    } else {
      const tempId = `ctmp-${Date.now()}`;
      const optimisticClient = {
        id: tempId,
        ...clientFields,
        name: c.name || "新規クライアント",
        firstRegisteredAt: clientFields.firstRegisteredAt || ""
      };
      clients.unshift(optimisticClient);
      upsertContractFromClient(optimisticClient);
      if (uiState.projectCreate && c.fromProject) {
        uiState.projectCreate.form.clientId = optimisticClient.id;
        uiState.projectCreate.form.clientQuery = optimisticClient.name;
        uiState.projectCreate.form.contactName = optimisticClient.contactName || uiState.projectCreate.form.contactName;
      }
      uiState.clientCreate = null;
      resetCorporateLocalSearch();
      render();
      // サーバー登録に失敗したら楽観追加した行を取り除く（リロードで消える幽霊クライアントを残さない）。
      // 「INSERT 成功後にレスポンスだけ失敗」した場合もあるため、再取得でサーバーの実状態に合わせる
      // （成功していれば行が戻る＝再登録による二重登録を防ぐ）
      const rollbackOptimisticClient = (message) => {
        const cidx = clients.findIndex((x) => x.id === tempId);
        if (cidx >= 0) clients.splice(cidx, 1);
        const midx = contractMaster.findIndex((x) => x.id === tempId);
        if (midx >= 0) contractMaster.splice(midx, 1);
        if (uiState.projectCreate && uiState.projectCreate.form.clientId === tempId) {
          uiState.projectCreate.form.clientId = "";
          uiState.projectCreate.form.clientQuery = "";
        }
        showToastError(message);
        loadDataFromDB().then(() => render()).catch(() => {});
      };
      fetch("/api/clients", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` }, body: JSON.stringify(clientFields) })
        .then(r => r.json()).then(data => {
          if (!(data.ok && data.client)) {
            rollbackOptimisticClient(typeof data.error === "string" ? data.error : "クライアントの登録に失敗しました");
            return;
          }
            const nc = data.client;
          const realId = `c${nc.id}`;
          const cidx = clients.findIndex((x) => x.id === tempId);
          if (cidx >= 0) {
            clients[cidx] = {
              ...clients[cidx],
              id: realId,
              dbId: nc.id,
              firstRegisteredAt: nc.first_registered_at || clients[cidx].firstRegisteredAt || "",
              approvalStatus: nc.approval_status ? String(nc.approval_status).trim() : "未申請"
            };
          }
          const midx = contractMaster.findIndex((x) => x.id === tempId);
          if (midx >= 0) contractMaster[midx] = { ...contractMaster[midx], id: realId };
          if (uiState.projectCreate && c.fromProject && uiState.projectCreate.form.clientId === tempId) {
            uiState.projectCreate.form.clientId = realId;
          }
          // 成功トーストはサーバ登録の確定後に出す（失敗時は rollback 側のエラートーストのみ）
          showToast("クライアントを追加しました");
        })
        .catch(() => rollbackOptimisticClient("通信エラーのためクライアントを登録できませんでした。再度お試しください。"));
    }
    if (uiState.clientCreate) {
      uiState.clientCreate = null;
      resetCorporateLocalSearch();
      render();
    }
    return true;
  }
  const selectClientEl = target.closest("[data-select-client]");
  if (selectClientEl instanceof HTMLElement && uiState.projectCreate) {
    const selected = getClientById(selectClientEl.dataset.selectClient);
    uiState.projectCreate.form.clientId = selected?.id || "";
    uiState.projectCreate.form.clientQuery = selected?.name || "";
    if (selected?.contactName) uiState.projectCreate.form.contactName = selected.contactName;
    // 部分更新: 候補リスト・選択カード・作成ボタンのみ更新（full render 回避でちらつき防止）
    refreshProjectClientCandidates();
    const selectedWrap = document.querySelector(".project-create-lane--search");
    if (selectedWrap instanceof HTMLElement) {
      const existing = selectedWrap.querySelector(".project-create-selected");
      const newCard = projectCreateSelectedCardHtml(selected);
      if (existing) existing.outerHTML = newCard;
      else if (newCard) selectedWrap.insertAdjacentHTML("beforeend", newCard);
    }
    // 「案件を作成」ボタンの活性状態を更新
    const createBtn = document.querySelector("[data-project-create]");
    if (createBtn instanceof HTMLButtonElement) {
      createBtn.disabled = !uiState.projectCreate.form.clientId;
    }
    return true;
  }
  const csvDownloadUsersEl = target.closest("[data-csv-download-users]");
  if (csvDownloadUsersEl instanceof HTMLElement) {
    const header = "名前,メールアドレス,ロール";
    const rows = users.map((u) => `${u.name},${u.email || ""},${(u.roles || [u.role]).join("/")}`);
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "accounts_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("CSVテンプレートをダウンロードしました");
    return true;
  }
  const csvDownloadClientsEl = target.closest("[data-csv-download-clients]");
  if (csvDownloadClientsEl instanceof HTMLElement) {
    const header = CLIENT_CSV_COLUMNS.map((col) => toCsvCell(col.header)).join(",");
    const sampleMap = {
      name: "株式会社サンプル",
      shortName: "サンプル",
      honorific: "御中",
      postalCode: "100-0001",
      prefecture: "東京都",
      address: "千代田区千代田1-1",
      building: "サンプルビル 3F",
      tel: "03-0000-0000",
      fax: "03-0000-0001",
      hpUrl: "https://example.co.jp",
      contactName: "山田 太郎",
      email: "sample@example.com",
      contractOwner: currentUser?.name || "",
      paymentTerms: "月末締翌月末払",
      transferFeeBurden: "先方",
      ndaStatus: "未",
      contractStatus: "未",
      deliveryMethod: "メール(DL)",
      notes: "初回取引",
      firstRegisteredAt: "2020/11/11 20:38",
      corporateNumber: "1234567890123",
      invoiceIssuer: "該当する",
      invoiceIssuerNumber: "T1234567890123"
    };
    const sample = CLIENT_CSV_COLUMNS.map((col) => toCsvCell(sampleMap[col.key] ?? col.fallback ?? "")).join(",");
    const csv = "\uFEFF" + [header, sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "clients_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("クライアントCSVテンプレートをダウンロードしました");
    return true;
  }
  const domainCsvTemplateEl = target.closest("[data-domain-csv-template]");
  if (domainCsvTemplateEl instanceof HTMLElement) {
    const header = DOMAIN_CSV_COLUMNS.map((col) => toCsvCell(col.header)).join(",");
    const sampleMap = {
      status: "契約",
      service: "お名前ドットコム",
      subdomain: "20220606161058162364.onamaeweb.jp",
      domain: "example.com",
      services: "ドメイン+サーバー+SSL(有料)",
      registrarId: "rs1716136",
      registrarPassword: "password123",
      clientName: "社内利用",
      owner: "三浦",
      projectNote: "原価入力不要",
      renewedDate: "2025/06/06",
      notes: ""
    };
    const sample = DOMAIN_CSV_COLUMNS.map((col) => toCsvCell(sampleMap[col.key] ?? col.fallback ?? "")).join(",");
    const csv = "﻿" + [header, sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "domains_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("ドメインCSVテンプレートをダウンロードしました");
    return true;
  }
  const domainCsvExportEl = target.closest("[data-domain-csv-export]");
  if (domainCsvExportEl instanceof HTMLElement) {
    const header = DOMAIN_CSV_COLUMNS.map((col) => toCsvCell(col.header)).join(",");
    const body = domains.map((d) => DOMAIN_CSV_COLUMNS.map((col) => {
      let v = d[col.key] ?? "";
      if (col.key === "renewedDate" && v) v = isoYmdToSlashDisplay(v);
      return toCsvCell(v);
    }).join(",")).join("\n");
    const csv = "﻿" + [header, body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "domains.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast(`ドメイン ${domains.length}件 をCSV出力しました`);
    return true;
  }
  const csvDownloadPartnersEl = target.closest("[data-csv-download-partners]");
  if (csvDownloadPartnersEl instanceof HTMLElement) {
    const header = PARTNER_CSV_COLUMNS.map((col) => toCsvCell(col.header)).join(",");
    const sampleMap = {
      name: "株式会社サンプルパートナー",
      branch: "東京支社",
      honorific: "御中",
      contactName: "佐藤 花子",
      contractOwner: "田中 太郎",
      contactEmail: "partner@example.com",
      contactTel: "03-0000-0000",
      fax: "03-0000-0001",
      postalCode: "100-0001",
      prefecture: "東京都",
      address: "千代田区千代田1-1",
      building: "サンプルビル 5F",
      paymentTerms: "月末締翌月末払",
      paymentMethod: "銀行振込",
      transferFeeBurden: "先方",
      deliveryMethod: "メール(DL)",
      bankInfo: "○○銀行 △△支店 普通 1234567 サンプルカブシキガイシャ",
      withholdingTax: "なし",
      category1: "制作",
      category2: "動画",
      category3: "編集",
      notes: "初回取引",
      firstRegisteredAt: "2020/11/11 20:38",
      corporateNumber: "1234567890123",
      invoiceIssuer: "該当する",
      invoiceIssuerNumber: "T1234567890123",
      ndaStatus: "未",
      contractStatus: "未"
    };
    const sample = PARTNER_CSV_COLUMNS.map((col) => toCsvCell(sampleMap[col.key] ?? col.fallback ?? "")).join(",");
    const csv = "\uFEFF" + [header, sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "partners_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("パートナーCSVテンプレートをダウンロードしました");
    return true;
  }
  const projectMasterBulkDeleteEl = target.closest("[data-project-master-bulk-delete]");
  if (projectMasterBulkDeleteEl instanceof HTMLElement && currentRoute().page === "projects") {
    void runExclusive("projects:bulk-delete", deleteSelectedProjectsBulkMaster, projectMasterBulkDeleteEl);
    return true;
  }
  const csvDownloadProjectsEl = target.closest("[data-csv-download-projects]");
  if (csvDownloadProjectsEl instanceof HTMLElement) {
    if (!isMasterRoleUser(currentUser)) {
      showToast("マスター権限が必要です");
      return true;
    }
    const header = PROJECT_WIDE_CSV_HEADERS.map((h) => toCsvCell(h)).join(",");
    const clientSample = clients[0]?.name || "株式会社サンプル";
    const ownerSample = currentUser?.name || "山田太郎";
    const sampleRows = [
      [1, "サンプルWebサイトリニューアル", clientSample, 1597500, 159750, 0, 1200000, "（見出しと併記する摘要など）", "", "", "", "", "構成案作成に伴う取材作業", "", "見出し行", "2026/4/1", "2026/5/31", "月末締翌月末払", "受注済", "着手中", "入金済", ownerSample, "Web制作, VIP"],
      [1, "サンプルWebサイトリニューアル", clientSample, 1597500, 159750, 0, 1200000, "・制作明細の摘要例", 60, "ページ", 12000, 720000, "", "", "通常", "2026/4/1", "2026/5/31", "月末締翌月末払", "受注済", "着手中", "入金済", ownerSample, ""],
      [1, "サンプルWebサイトリニューアル", clientSample, 1597500, 159750, 0, 1200000, "", "", "", "", 1410000, "", "小計", "小計行", "2026/4/1", "2026/5/31", "月末締翌月末払", "受注済", "着手中", "入金済", ownerSample, ""]
    ].map((cells) => cells.map((c) => toCsvCell(c)).join(","));
    const csv = "\uFEFF" + [header, ...sampleRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "案件取り込み用.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("案件CSVテンプレートをダウンロードしました");
    return true;
  }
  const csvDownloadBudgetMasterEl = target.closest("[data-csv-download-budget-master]");
  if (csvDownloadBudgetMasterEl instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const header = ["部署", "担当者", ...BUDGET_MONTH_LABELS].map((h) => toCsvCell(h)).join(",");
    const sampleDept = String((uiState.deptSettings?.departments || [])[0]?.name || "").trim() || "営業部";
    const sampleMember = users.find((u) => isActiveUser(u) && u.role === "営業")?.name || "山田太郎";
    const sample = [sampleDept, sampleMember, ...Array(12).fill("1000000")].map((c) => toCsvCell(c)).join(",");
    const csv = "\uFEFF" + [header, sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "budget_master_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("予算マスタCSVテンプレートをダウンロードしました");
    return true;
  }
  if (target.closest("[data-csv-download-budget-matrix]") instanceof HTMLElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const period = uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(uiState.budgetPeriods);
    if (!period) {
      showToast("会計期を選択してください");
      return true;
    }
    const fyStart = fiscalYearStartFromBudgetPeriod(period);
    const monthLabels = budgetMatrixMonthHeaderLabelsForFy(fyStart);
    if (monthLabels.length !== 12) {
      showToast("会計期から年度開始年を決められませんでした");
      return true;
    }
    const deptH = "部署";
    const nameH = "氏名";
    const metricH = "指標";
    const metrics = ["予算(差益)", "売上", "差益", "粗利率", "進捗率"];
    const headerCells = [deptH, nameH, metricH, ...monthLabels].map((h) => toCsvCell(h)).join(",");
    const sampleDept = String((uiState.deptSettings?.departments || [])[0]?.name || "").trim() || "営業部";
    const sampleMember = users.find((u) => isActiveUser(u) && u.role === "営業")?.name || "山田太郎";
    const sampleRows = metrics.map((metric) => {
      const vals = metric === "予算(差益)" ? Array(12).fill("1000000") : Array(12).fill("");
      return [sampleDept, sampleMember, metric, ...vals].map((c) => toCsvCell(c)).join(",");
    });
    const csv = "\uFEFF" + [headerCells, ...sampleRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `budget_matrix_template_fy_${fyStart}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("スプレッドシート形式のCSVテンプレートをダウンロードしました（粗利率・進捗率は参考行です。取込時は予算・売上・差益のみ使用されます）");
    return true;
  }
  const openUserManagerEl = target.closest("[data-open-user-manager]");
  if (openUserManagerEl instanceof HTMLElement) {
    uiState.userEditor = { id: "", lastName: "", firstName: "", email: "", role: "営業", systemRole: "一般", onLeave: false, retired: false, searchExcluded: false, password: "" };
    render();
    return true;
  }
  const navPermToggleEl = target.closest("[data-nav-perm-toggle]");
  if (navPermToggleEl instanceof HTMLElement) {
    const [uid, navKey] = navPermToggleEl.dataset.navPermToggle.split(":");
    const user = users.find((u) => u.id === uid);
    if (user) {
      if (!user.navPermissions) user.navPermissions = [...ALL_NAV_KEYS];
      const prevNavPermissions = [...user.navPermissions];
      const idx = user.navPermissions.indexOf(navKey);
      if (idx >= 0) user.navPermissions.splice(idx, 1);
      else user.navPermissions.push(navKey);
      // partner-list は client-list の含意（権限画面に項目が無い）。取引先一覧を外したら一緒に外さないと
      // 読込時に含意された partner-list が保存に残り、URL 直打ちでパートナー一覧が見えたままになる
      if (navKey === "client-list" && idx >= 0) {
        user.navPermissions = user.navPermissions.filter((k) => k !== "partner-list");
      }
      if (authToken && user.dbId) {
        void fpSaveTrack(`navperm:${uid}:${navKey}`, async () => {
          try {
            const r = await fetch("/api/users", {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({
                id: user.dbId,
                name: user.name,
                email: user.email || null,
                role: user.role,
                roles: [user.role],
                navPermissions: user.navPermissions || [],
                retired: !!user.retired
              })
            });
            if (r.ok) return true;
            user.navPermissions = prevNavPermissions;
            showToastError("画面権限の更新に失敗しました");
          } catch {
            user.navPermissions = prevNavPermissions;
            showToastError("通信に失敗しました（画面権限の更新）");
          }
          return false;
        });
      }
      render();
    }
    return true;
  }
  const userInviteEl = target.closest("[data-user-invite]");
  if (userInviteEl instanceof HTMLElement) {
    const uid = userInviteEl.dataset.userInvite;
    const user = users.find((u) => u.id === uid);
    if (!user || !user.email) { showToast("メールアドレスが設定されていません"); return true; }
    const inviteLabel = userInviteEl.textContent; // 「招待」/「再招待」を送信後に戻す
    userInviteEl.disabled = true;
    userInviteEl.textContent = "送信中...";
    fetch("/api/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        name: user.name,
        email: user.email,
        role: primaryJobRole(user),
        systemRole: systemRoleOfUser(user),
        roles: [primaryJobRole(user)],
        // 既存ユーザーの再招待では権限を送らない（サーバーが現在値を保つ）。画面の古い値を送ると、
        // 読込後に他の管理者が変えた権限を戻していた
        ...(user.dbId ? {} : { navPermissions: user.navPermissions })
      })
    }).then(r => r.json()).then(data => {
      if (data.ok) {
        if (data.setupUrl) {
          showToast("アカウント作成完了。メール未設定のためURLを共有してください");
          prompt("パスワード設定URL（共有してください）", data.setupUrl);
        } else {
          showToast(data.message || "招待メールを送信しました");
        }
      } else {
        showToastError(data.error || "招待に失敗しました");
      }
      userInviteEl.disabled = false;
      userInviteEl.textContent = inviteLabel;
    }).catch(() => {
      showToastError("通信エラー");
      userInviteEl.disabled = false;
      userInviteEl.textContent = inviteLabel;
    });
    return true;
  }
  const userActionEl = target.closest("[data-user-action]");
  if (userActionEl instanceof HTMLElement) {
    const [action, id] = userActionEl.dataset.userAction.split(":");
    if (action === "edit") {
      const base = users.find((u) => u.id === id);
      const splitName = splitUserName(base?.name || "");
      uiState.userEditor = {
        id: base?.id || "",
        lastName: splitName.lastName,
        firstName: splitName.firstName,
        email: base?.email || "",
        role: primaryJobRole(base) || "営業",
        systemRole: systemRoleOfUser(base) || "一般",
        onLeave: !!base?.onLeave,
        retired: !!base?.retired,
        searchExcluded: !!base?.searchExcluded,
        password: ""
      };
      render();
      return true;
    }
    if (action === "delete") {
      const idx = users.findIndex((u) => u.id === id);
      const victim = idx >= 0 ? users[idx] : null;
      if (!victim) return true;
      // 実体は論理削除（ログイン不可＋全部署から外す。api/users.js DELETE）。画面から戻す手段が無いので必ず確認する
      void runExclusive(`user:delete:${id}`, async () => {
        const ok0 = await fpConfirm({
          title: `「${victim.name}」を削除しますか？`,
          message: "・ログインできなくなり、アカウント一覧から消えます\n・すべての部署から外れます（責任者の場合は解除され、承認フローの承認者もいなくなります）",
          note: "画面からは元に戻せません。",
          confirmLabel: "アカウントを削除する"
        });
        if (!ok0) return;
        const at = users.findIndex((u) => u.id === id);
        if (at >= 0) users.splice(at, 1);
        if (authToken && victim.dbId) {
          const { ok, data } = await apiFetch("/api/users", { method: "DELETE", body: { id: victim.dbId } });
          if (!ok) {
            if (at >= 0) users.splice(at, 0, victim);
            showToastError(typeof data.error === "string" ? data.error : "担当者の削除に失敗しました");
            render();
            return;
          }
        }
        // サーバは全部署から外している。画面 state にも反映しないと次の部署保存で書き戻される
        removeUserFromDeptSettings(victim.id);
        showToast("担当者を削除しました");
        render();
      }, userActionEl);
      return true;
    }
  }
  const closeUserEditorEl = target.closest("[data-close-user-editor]");
  if (closeUserEditorEl instanceof HTMLElement) {
    if (!fpConfirmDiscardDirtyForm("userEditor")) return true;
    uiState.userEditor = null;
    render();
    return true;
  }
  const genUserPwEl = target.closest("[data-generate-user-password]");
  if (genUserPwEl instanceof HTMLElement && uiState.userEditor) {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const buf = new Uint32Array(12);
    (window.crypto || window).getRandomValues(buf);
    let pw = "";
    for (let i = 0; i < buf.length; i++) pw += chars[buf[i] % chars.length];
    uiState.userEditor.password = pw;
    render();
    showToast("パスワードを生成しました（コピーして共有してください）");
    return true;
  }
  const saveUserEl = target.closest("[data-save-user]");
  if (saveUserEl instanceof HTMLElement && uiState.userEditor) {
    const u = uiState.userEditor;
    const lastName = (u.lastName || "").trim();
    const firstName = (u.firstName || "").trim();
    const fullName = [lastName, firstName].filter(Boolean).join(" ");
    if (!fullName) {
      showToast("名字または名前を入力してください");
      return true;
    }
    if (!isAllowedAccountEmail(u.email)) {
      showToast("o-eighty.com または o-eighty.co.jp のメールアドレスのみ登録できます");
      return true;
    }
    if (u.id) {
      const idx = users.findIndex((x) => x.id === u.id);
      const prevRow = idx >= 0 ? { ...users[idx] } : null;
      if (idx >= 0) users[idx] = {
        ...users[idx],
        name: fullName || users[idx].name,
        email: u.email || users[idx].email,
        role: u.role || users[idx].role,
        systemRole: u.systemRole || users[idx].systemRole || "一般",
        roles: [u.role || users[idx].role],
        onLeave: !!u.onLeave,
        retired: !!u.retired,
        searchExcluded: !!u.searchExcluded
      };
      const updated = idx >= 0 ? users[idx] : null;
      const passwordSet = !!(u.password || "").trim();
      if (authToken && updated?.dbId) {
        fetch("/api/users", {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            id: updated.dbId,
            name: updated.name,
            email: updated.email || null,
            role: updated.role,
            systemRole: updated.systemRole || "一般",
            roles: [updated.role],
            // 権限はこのフォームでは編集しないので送らない（サーバーが現在値を保つ）。画面の古い値を送ると、
            // 読込後に他の管理者が変えた権限を戻していた
            onLeave: !!updated.onLeave,
            retired: !!updated.retired,
            searchExcluded: !!updated.searchExcluded,
            password: (u.password || "").trim() || undefined
          })
        })
          .then(async (r) => {
            let resBody = {};
            try { resBody = await r.json(); } catch { resBody = {}; }
            if (!r.ok) {
              // 失敗（ドメイン制約・重複・サーバエラー等）: 楽観更新を巻き戻して実エラーを表示
              if (idx >= 0 && prevRow) users[idx] = prevRow;
              showToastError(typeof resBody.error === "string" ? resBody.error : "担当者の更新に失敗しました");
              render();
              return;
            }
            if (passwordSet && idx >= 0) users[idx].hasPassword = true;
            if (updated && currentUser && (updated.id === currentUser.id || updated.dbId === currentUser.dbId)) {
              await refreshCurrentUserFromServer();
            }
            showToast(passwordSet ? "担当者を更新しました（パスワード設定済み）" : "担当者を更新しました");
            render();
          })
          .catch(() => {
            if (idx >= 0 && prevRow) users[idx] = prevRow;
            showToastError("通信に失敗しました（更新）");
            render();
          });
      } else {
        if (updated && currentUser && (updated.id === currentUser.id || updated.dbId === currentUser.dbId)) {
          Object.assign(currentUser, { role: updated.role, systemRole: updated.systemRole, roles: updated.roles });
        }
        showToast("担当者を更新しました");
      }
    } else {
      const newUser = {
        id: `u${users.length + 1}`,
        name: fullName || "新規担当者",
        email: u.email || "",
        role: u.role || "営業",
        systemRole: u.systemRole || "一般",
        roles: [u.role || "営業"],
        navPermissions: defaultNavPermissionsForUser({ role: u.role || "営業", systemRole: u.systemRole || "一般" }),
        onLeave: !!u.onLeave,
        retired: !!u.retired,
        searchExcluded: !!u.searchExcluded
      };
      users.unshift(newUser);
      const passwordSetOnCreate = !!(u.password || "").trim();
      if (authToken) {
        fetch("/api/users", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            name: newUser.name,
            email: newUser.email || null,
            role: newUser.role,
            systemRole: newUser.systemRole,
            roles: newUser.roles,
            navPermissions: newUser.navPermissions,
            onLeave: !!newUser.onLeave,
            retired: !!newUser.retired,
            searchExcluded: !!newUser.searchExcluded,
            password: (u.password || "").trim() || undefined
          })
        }).then(async (r) => {
          let body = {};
          try {
            body = await r.json();
          } catch {
            body = {};
          }
          if (!r.ok) {
            users.shift();
            {
              const base =
                typeof body.error === "string" ? body.error : "担当者の保存に失敗しました";
              const hint =
                body.column ? `（列: ${body.column}）` :
                body.detail ? `（${String(body.detail).slice(0, 160)}）` : "";
              showToast(base + hint);
            }
            render();
            return;
          }
          if (body.reactivated) {
            showToast("削除済み（無効）だったアカウントを復活させました");
          }
          const created = body.user && body.user.id ? body.user : null;
          if (created) {
            users.shift();
            users.unshift(created);
          }
          await loadDataFromDB();
          // 他API失敗などで GET が空・不整合でも、作成済みユーザーだけは消さない
          if (created && created.id) {
            const still = users.some((x) => x.id === created.id || (created.dbId != null && x.dbId === created.dbId));
            if (!still) users.unshift(created);
          }
          // パスワードを設定して作成した場合は、招待（パスワード再設定）を呼ばず直接ログイン可能にする
          if (passwordSetOnCreate) {
            const ci = users.findIndex((x) => created && (x.id === created.id || (created.dbId != null && x.dbId === created.dbId)));
            if (ci >= 0) users[ci].hasPassword = true;
            render();
            showToast("アカウントを作成しました（このメール＋パスワードでログインできます）");
            return;
          }
          render();
          if (!newUser.email) {
            showToast("担当者を追加しました");
            return;
          }
          // 追加保存は成功させた上で、招待送信のみ別処理にする
          fetch("/api/invite", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({
              name: newUser.name,
              email: newUser.email,
              role: newUser.role,
              systemRole: newUser.systemRole,
              roles: newUser.roles,
              navPermissions: newUser.navPermissions
            })
          }).then((inviteRes) => inviteRes.json()).then((data) => {
            if (data.ok && data.setupUrl) {
              showToast("アカウントを追加しました。メール未設定のためURLを共有してください");
              prompt("パスワード設定URL（共有してください）", data.setupUrl);
            } else if (data.ok) {
              showToast("アカウントを追加し、招待メールを送信しました");
            } else {
              showToastError("アカウントは追加済みです（招待送信失敗: " + (data.error || "不明なエラー") + "）");
            }
          }).catch(() => {
            showToastError("アカウントは追加済みです（招待メール送信エラー）");
          });
        }).catch(() => {
          users.shift();
          showToastError("通信エラーのため担当者を保存できませんでした");
          render();
        });
      } else {
        showToast("担当者を追加しました");
      }
    }
    uiState.userEditor = null;
    render();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 6245〜7382 行）。処理したら true（16 側はそこで止める） */
function handleUsersAndProjectsClick(e, target) {
  const revokeAllEl = target.closest("[data-revoke-all-sessions]");
  if (revokeAllEl instanceof HTMLElement) {
    if (!authToken) return true;
    if (!window.confirm("このアカウントでログイン中の全デバイスのセッションを無効にします。\n（このデバイスを含む全ての端末が次のAPI通信時にログアウトされます）\n実行しますか？")) return true;
    void (async () => {
      try {
        const r = await fetch("/api/revoke-sessions", {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok) {
          showToast("全デバイスのセッションを無効にしました。再ログインしてください。");
          // 自分自身もログアウト
          setTimeout(() => {
            sessionStorage.removeItem("fp_token");
            fpClearPersistedToken();
            location.reload();
          }, 1500);
        } else {
          showToast(j.error || "処理に失敗しました", { variant: "error" });
        }
      } catch {
        showToast("通信エラーが発生しました", { variant: "error" });
      }
    })();
    return true;
  }
  const closeMyAccountEl = target.closest("[data-close-my-account]");
  if (closeMyAccountEl instanceof HTMLElement) {
    uiState.myAccountEditor = null;
    render();
    return true;
  }
  const saveMyAccountEl = target.closest("[data-save-my-account]");
  if (saveMyAccountEl instanceof HTMLElement && uiState.myAccountEditor) {
    const m = uiState.myAccountEditor;
    if (!isValidMyAccountPhone(m.phone)) {
      showToast("電話番号の形式が正しくありません（例：03-1234-5678、090-1234-5678、+81-90-1234-5678）");
      return true;
    }
    const wantsPasswordChange = !!(m.newPassword || m.confirmPassword || m.currentPassword);
    if (wantsPasswordChange) {
      if (!m.currentPassword) {
        showToast("現在のパスワードを入力してください");
        return true;
      }
      if (!m.newPassword || m.newPassword.length < 8) {
        showToast("新しいパスワードは8文字以上で入力してください");
        return true;
      }
      if (m.newPassword !== m.confirmPassword) {
        showToast("新しいパスワード（確認）が一致しません");
        return true;
      }
    }
    if (!authToken) {
      showToast("未認証のため保存できません");
      return true;
    }
    void (async () => {
      // プロフィール項目もサーバへ送る（ここに足し忘れると画面だけ変わってリロードで消える）
      const body = {
        avatar: m.avatar && String(m.avatar).trim() ? String(m.avatar) : "",
        displayName: (m.displayName || "").trim(),
        comment: (m.comment || "").trim(),
        phone: (m.phone || "").trim()
      };
      if (wantsPasswordChange) {
        body.currentPassword = m.currentPassword;
        body.newPassword = m.newPassword;
      }
      if (m.saving) return; // 連打で PATCH（パスワード変更を含む）が多重に飛ばないように
      m.saving = true;
      // apiFetch: 通信例外も { ok:false, status:0 } で返る（生 fetch だと未処理の reject で無反応だった）
      const r = await apiFetch("/api/me", { method: "PATCH", body });
      m.saving = false;
      if (!r.ok) {
        const msg = typeof r.data.error === "string" ? r.data.error : "";
        showToastError(msg || (r.status === 0 ? "通信に失敗しました。接続を確認してもう一度お試しください" : "保存に失敗しました"));
        return;
      }
      // パスワード変更時はサーバが他端末のセッションを失効させ、この端末用の新しいトークンを返す
      if (r.data && typeof r.data.token === "string" && r.data.token) {
        authToken = r.data.token;
        sessionStorage.setItem("fp_token", authToken);
        fpPersistToken(authToken);
      }
      currentUser.displayName = (m.displayName || "").trim();
      currentUser.comment = (m.comment || "").trim();
      currentUser.phone = (m.phone || "").trim();
      currentUser.avatar = m.avatar || "";
      const syncRow = users.find(
        (u) => String(u.id) === String(currentUser.id) || (currentUser.dbId != null && u.dbId === currentUser.dbId)
      );
      if (syncRow) syncRow.avatar = currentUser.avatar || "";
      uiState.myAccountEditor = null;
      showToast("アカウント設定を更新しました");
      render();
    })();
    return true;
  }
  const roleEl = target.closest("[data-role]");
  if (roleEl instanceof HTMLElement) {
    uiState.currentUserRole = roleEl.dataset.role;
    render();
    return true;
  }
  const dashMineEl = target.closest("[data-dashboard-mine-only]");
  if (dashMineEl instanceof HTMLElement) {
    uiState.dashboardMineOnly = dashMineEl.dataset.dashboardMineOnly === "1";
    render();
    return true;
  }
  const projectFilterButtonEl = target.closest("[data-project-filter]");
  if (projectFilterButtonEl instanceof HTMLElement && projectFilterButtonEl.dataset.projectFilter === "mine") {
    uiState.projectListFilters.mineOnly = !uiState.projectListFilters.mineOnly;
    render();
    return true;
  }
  const costSortEl = target.closest("[data-cost-sort]");
  if (costSortEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    const key = costSortEl.dataset.costSort;
    if (!uiState.costMgmtSort) uiState.costMgmtSort = { key: "targetMonth", dir: "desc" };
    if (uiState.costMgmtSort.key === key) {
      uiState.costMgmtSort.dir = uiState.costMgmtSort.dir === "asc" ? "desc" : "asc";
    } else {
      uiState.costMgmtSort.key = key;
      uiState.costMgmtSort.dir = "asc";
    }
    if (uiState.costMgmtTable) uiState.costMgmtTable.page = 1;
    render();
    return true;
  }

  const projectSortEl = target.closest("[data-project-sort]");
  if (projectSortEl instanceof HTMLElement) {
    const key = projectSortEl.dataset.projectSort;
    if (uiState.projectTable.sortKey === key) {
      uiState.projectTable.sortDir = uiState.projectTable.sortDir === "asc" ? "desc" : "asc";
    } else {
      uiState.projectTable.sortKey = key;
      uiState.projectTable.sortDir = "asc";
    }
    setProjectListPage(1);
    render();
    return true;
  }
  const projectPageEl = target.closest("[data-project-page]");
  if (projectPageEl instanceof HTMLElement) {
    setProjectListPage(Number(projectPageEl.dataset.projectPage) || 1);
    render();
    return true;
  }
  const projectListExportCsvEl = target.closest("[data-project-list-export-csv]");
  if (projectListExportCsvEl instanceof HTMLElement && currentRoute().page === "projects") {
    downloadProjectListFilteredCsv();
    return true;
  }
  const filterToggleEl = target.closest("[data-pj-filter-toggle]");
  if (filterToggleEl instanceof HTMLElement) {
    uiState.projectListFilterOpen = !uiState.projectListFilterOpen;
    render();
    return true;
  }
  const resetProjectEl = target.closest("[data-project-reset]");
  if (resetProjectEl instanceof HTMLElement) {
    resetProjectListFilters();
    setProjectListPage(1);
    render();
    return true;
  }
  const clearSelectionEl = target.closest("[data-project-clear-selection]");
  if (clearSelectionEl instanceof HTMLElement) {
    uiState.projectTable.selected = {};
    render();
    return true;
  }
  const bulkDupCancelEl = target.closest("[data-project-bulk-dup-cancel]");
  if (bulkDupCancelEl instanceof HTMLElement) {
    if (uiState.projectBulkDupConfirm?.processing) return true;
    uiState.projectBulkDupConfirm = null;
    render();
    return true;
  }
  const bulkDupConfirmEl = target.closest("[data-project-bulk-dup-confirm]");
  if (bulkDupConfirmEl instanceof HTMLElement && uiState.projectBulkDupConfirm) {
    const d = uiState.projectBulkDupConfirm;
    if (d.processing) return true;
    const selectedIds = d.selectedIds || [];
    if (!selectedIds.length) {
      uiState.projectBulkDupConfirm = null;
      render();
      return true;
    }
    // コピー内容（案件情報のみ／見積書まで／全て）はモーダルのラジオから読む（再描画で消える前に取得）
    const modeInput = document.querySelector('input[name="project-bulk-dup-mode"]:checked');
    const copyMode = modeInput instanceof HTMLInputElement && modeInput.value ? modeInput.value : "all";
    uiState.projectBulkDupConfirm = { ...d, mode: copyMode, processing: true };
    render();
    void (async () => {
      let ok = 0;
      let fail = 0;
      let lastErr = "";
      for (const uiId of selectedIds) {
        const src = projects.find((p) => p.id === uiId);
        if (!src) {
          fail += 1;
          continue;
        }
        try {
          await duplicateProjectWithItemsToServer(src, { copyMode });
          ok += 1;
        } catch (e) {
          fail += 1;
          lastErr = e instanceof Error && e.message ? e.message : "";
        }
      }
      try { persistProjectDocDraftsStore(); } catch { /* ignore */ }
      uiState.projectBulkDupConfirm = null;
      if (ok > 0) uiState.projectTable.selected = {};
      if (fail > 0) {
        showToastError(`${ok}件を複製しました。${fail}件は失敗しました${lastErr ? `（${lastErr}）` : ""}`);
      } else {
        showToast(`${ok}件を複製しました（${copyMode === "project" ? "案件情報のみ" : copyMode === "quote" ? "項目・見積書までコピー済み" : "項目・書類設定もコピー済み"}）`);
      }
      render();
    })();
    return true;
  }
  const sealConfirmCancelEl = target.closest("[data-seal-confirm-cancel]");
  if (sealConfirmCancelEl instanceof HTMLElement) {
    if (uiState.sealConfirmDialog?.processing) return true;
    uiState.sealConfirmDialog = null;
    render();
    return true;
  }
  const sealConfirmOkEl = target.closest("[data-seal-confirm-ok]");
  if (sealConfirmOkEl instanceof HTMLElement && uiState.sealConfirmDialog) {
    const d = uiState.sealConfirmDialog;
    if (d.processing) return true;
    if (d.kind === "app-mgmt-bulk-approve-all") {
      const items = Array.isArray(d.items) ? d.items : [];
      uiState.sealConfirmDialog = { ...d, processing: true };
      uiState.appMgmtContractApproving = true;
      render();
      void (async () => {
        let n = 0;
        let skip = 0;
        let fail = 0;
        const contractIds = [];
        let touchedTool = false;
        let touchedEquip = false;
        let touchedUnlock = false;
        let touchedDev = false;
        let touchedChangereq = false;
        try {
          for (const it of items) {
            const kind = it?.kind;
            const key = it?.key;
            if (!kind || key == null) continue;
            if (kind === "contract") {
              contractIds.push(key);
              continue;
            }
            if (kind === "seal") {
              const k = String(key);
              const parsed = parseDocSealKey(k);
              if (!parsed || getDocSealStatus(parsed.pid, parsed.tab) !== "申請中") continue;
              if (!isCurrentUserSealApproverForPendingKey(k)) {
                skip += 1;
                continue;
              }
              const snap = snapshotDocSealState(k);
              if (!applyOneSealApprovalAdvance(k)) continue;
              if (await persistDocSealApplicationToServer(k, snap)) n += 1;
              else fail += 1;
              continue;
            }
            if (kind === "tool" || kind === "unlock" || kind === "dev" || kind === "equip") {
              const url =
                kind === "tool" ? "/api/tool-applications"
                  : kind === "unlock" ? "/api/project-unlock-applications"
                    : kind === "equip" ? "/api/equipment-applications"
                      : "/api/development-applications";
              try {
                const res = await fetch(url, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify({ id: key, action: "approve" })
                });
                if (res.ok) {
                  n += 1;
                  if (kind === "tool") touchedTool = true;
                  else if (kind === "equip") touchedEquip = true;
                  else if (kind === "unlock") touchedUnlock = true;
                  else touchedDev = true;
                } else {
                  fail += 1;
                }
              } catch {
                fail += 1;
              }
              continue;
            }
            if (kind === "changereq") {
              const result = await persistTransactionApprovalApi({ changeRequestId: Number(key), decision: "approve" });
              if (result.ok) {
                n += 1;
                touchedChangereq = true;
              } else {
                fail += 1;
              }
            }
          }
          if (contractIds.length) {
            const { updated, fail: cFail } = await persistContractMasterApproveMany(contractIds);
            n += updated;
            fail += cFail;
          }
          uiState.appMgmtSelected = {};
          const refreshes = [];
          if (touchedTool) refreshes.push(fetchAllowedToolsData());
          if (touchedEquip) refreshes.push(fetchEquipmentApplications());
          if (touchedUnlock) refreshes.push(fetchProjectUnlockApplications());
          if (touchedDev) refreshes.push(fetchDevelopmentApplications());
          if (touchedChangereq) refreshes.push(fetchMasterChangeRequests(), loadDataFromDB());
          if (refreshes.length) await Promise.all(refreshes);
          if (n > 0) {
            showToast(
              `${n}件を承認しました` +
                (skip > 0 ? `（${skip}件は承認順のためスキップ）` : "") +
                (fail > 0 ? `（${fail}件は保存に失敗）` : "")
            );
          } else if (fail > 0) {
            showToastError("承認の保存に失敗しました");
          } else if (skip > 0) {
            showToast("選択した件は、いまのあなたの承認順ではありませんでした");
          } else {
            showToast("承認できた件がありませんでした（既に承認済み、または対象外です）");
          }
        } finally {
          uiState.sealConfirmDialog = null;
          uiState.appMgmtContractApproving = false;
          render();
        }
      })();
      return true;
    }
    const sealPersistKinds = new Set(["apply", "approve", "bulk"]);
    if (sealPersistKinds.has(d.kind)) {
      uiState.sealConfirmDialog = { ...d, processing: true };
      render();
      void (async () => {
        try {
          if (d.kind === "apply") {
            await submitDocSealApplicationQuick(d.key, sealApplyExtraFromDialog(d));
          } else if (d.kind === "approve") {
            const parsed = parseDocSealKey(d.key);
            const mk = String(d.key || "");
            if (parsed && getDocSealStatus(parsed.pid, parsed.tab) === "申請中") {
              if (!isCurrentUserSealApproverForPendingKey(mk)) {
                showToast("この申請は、いまのあなたの承認順ではありません");
              } else {
                const snap = snapshotDocSealState(mk);
                if (applyOneSealApprovalAdvance(mk) && (await persistDocSealApplicationToServer(mk, snap))) {
                  delete uiState.appMgmtSelected?.[`seal:${mk}`];
                  const st = getDocSealStatus(parsed.pid, parsed.tab);
                  showToast(
                    st === "承認済"
                      ? "捺印を承認済に更新しました"
                      : "承認しました。次の承認者の申請承認に表示されます"
                  );
                }
              }
            }
          } else if (d.kind === "bulk") {
            const tab = d.tab;
            const selectedIds = d.selectedIds || [];
            if (docTabHasSealWorkflow(tab)) {
              const extra = sealApplyExtraFromDialog(d);
              let applied = 0;
              let skipped = 0;
              let fail = 0;
              for (const pid of selectedIds) {
                const result = await applyDocSealForBulk(pid, tab, extra);
                if (result === "applied") applied += 1;
                else if (result === "failed") fail += 1;
                else skipped += 1;
              }
              if (applied > 0) {
                showToast(
                  `${applied}件の捺印申請を送信しました` +
                    (skipped > 0 ? `（${skipped}件は申請済み・承認者なしのため除外）` : "") +
                    (fail > 0 ? `（${fail}件は保存に失敗）` : "")
                );
              } else if (fail > 0) {
                showToastError("捺印申請の保存に失敗しました");
              } else {
                showToast(
                  "選択中の案件は、未申請以外、または承認フローに承認者がいないため送信できませんでした"
                );
              }
            }
          }
        } finally {
          uiState.sealConfirmDialog = null;
          render();
        }
      })();
      return true;
    }
    uiState.sealConfirmDialog = null;
    render();
    return true;
  }
  const bulkDupEl = target.closest("[data-project-bulk-dup]");
  if (bulkDupEl instanceof HTMLElement) {
    const selectedIds = getSelectedProjectIdsInFilter();
    if (selectedIds.length === 0) {
      showToast("案件を選択してください");
      return true;
    }
    uiState.projectBulkDupConfirm = { selectedIds };
    render();
    return true;
  }
  const bulkSealTabEl = target.closest("[data-project-bulk-seal-tab]");
  if (bulkSealTabEl instanceof HTMLElement && currentRoute().page === "projects") {
    const selectedIds = getSelectedProjectIdsInFilter();
    if (selectedIds.length === 0) {
      showToast("案件を選択してください");
      return true;
    }
    // 申請する書類はモーダルで選ぶ（以前は請求書固定で、何を申請するのか分からなかった）
    uiState.sealConfirmDialog = { kind: "bulk", tab: "", selectedIds };
    render();
    return true;
  }
  const bulkApplyEl = target.closest("[data-project-bulk-apply]");
  if (bulkApplyEl instanceof HTMLElement) {
    // 旧実装はローカルの orderStatus を書き換えるだけで保存せず、リロードで戻っていた。
    // 一括入金と同じく「反映→保存→失敗分は巻き戻し」にする
    const status = projectBulkOrderStatus();
    const selectedIds = getSelectedProjectIdsInFilter();
    if (selectedIds.length === 0) {
      showToast("案件を選択してください");
      return true;
    }
    const selected = projects.filter((p) => selectedIds.includes(p.id));
    const targets = selected.filter((p) => !isProjectLocked(p) && p.orderStatus !== status);
    const lockedCount = selected.filter((p) => isProjectLocked(p)).length;
    const lockedNote = lockedCount ? `（ロック中の ${lockedCount} 件は対象外）` : "";
    if (!targets.length) {
      showToast(`受注状況を変更できる案件がありません${lockedNote}`);
      return true;
    }
    if (!window.confirm(`選択した ${targets.length} 件の受注状況を「${status}」に変更します${lockedNote}。よろしいですか？`)) {
      return true;
    }
    void (async () => {
      const prev = new Map(targets.map((p) => [p.id, p.orderStatus]));
      targets.forEach((p) => { p.orderStatus = status; });
      render();
      let ok = 0;
      const failed = [];
      for (const p of targets) {
        try {
          await persistProjectDetailToServer(p);
          ok += 1;
        } catch {
          p.orderStatus = prev.get(p.id);
          failed.push(p.name || p.id);
        }
      }
      uiState.projectTable.selected = {};
      if (failed.length) {
        showToastError(`受注状況の変更: ${ok}件成功 / ${failed.length}件失敗（${failed.slice(0, 3).join("、")}${failed.length > 3 ? "…" : ""}）`);
      } else {
        showToast(`受注状況を「${status}」に変更しました（${ok}件）`);
      }
      render();
    })();
    return true;
  }
  // ── プロジェクト一覧: 選択案件を一括で入金済に変更（入金管理の一括記録と同じ反映・保存・巻き戻し） ──
  const bulkPaymentApplyEl = target.closest("[data-project-bulk-payment-apply]");
  if (bulkPaymentApplyEl instanceof HTMLElement && currentRoute().page === "projects") {
    const selectedIds = getSelectedProjectIdsInFilter();
    if (selectedIds.length === 0) {
      showToast("案件を選択してください");
      return true;
    }
    const dateEl = document.querySelector("input[data-project-bulk-payment-date]");
    const payDate = dateEl instanceof HTMLInputElement ? String(dateEl.value || "").trim() : "";
    if (!payDate) {
      showToast("入金日を選んでください");
      return true;
    }
    const targets = projects.filter(
      (p) => selectedIds.includes(p.id) && canConfirmPayment(p) && p.docs?.payment !== "入金確認済み"
    );
    if (!targets.length) {
      showToast("入金済にできる案件がありません（経理またはマスター/管理者のみ変更できます。入金済の案件は対象外）");
      return true;
    }
    if (!window.confirm(`選択した ${targets.length} 件を入金済にします（入金日 ${payDate}）。よろしいですか？`)) {
      return true;
    }
    void (async () => {
      const snaps = new Map();
      for (const p of targets) {
        snaps.set(p.id, applyPaymentConfirmationToProject(p, { payDate, amount: p.amount, note: "" }));
      }
      render();
      let ok = 0;
      const failed = [];
      let mismatch = false;
      for (const p of targets) {
        if (!(p.dbId && authToken)) {
          ok += 1;
          continue;
        }
        const r = await persistPaymentChangeToServer(p, snaps.get(p.id));
        if (r.ok) ok += 1;
        else {
          failed.push(p.name || p.id);
          if (r.mismatch) mismatch = true;
        }
      }
      uiState.projectTable.selected = {};
      if (failed.length) {
        showToastError(`入金済への変更: ${ok}件成功 / ${failed.length}件失敗（${failed.slice(0, 3).join("、")}${failed.length > 3 ? "…" : ""}）${mismatch ? PAYMENT_SAVE_MISMATCH_HINT : ""}`);
      } else {
        showToast(`入金済に変更しました（${ok}件・入金日 ${payDate}）`);
      }
      render();
    })();
    return true;
  }
  // ── プロジェクト一覧: 選択案件の入金確認を取り消して未入金（入金待ち＝請求済み表示）へ戻す ──
  const bulkPaymentCancelEl = target.closest("[data-project-bulk-payment-cancel]");
  if (bulkPaymentCancelEl instanceof HTMLElement && currentRoute().page === "projects") {
    const selectedIds = getSelectedProjectIdsInFilter();
    if (selectedIds.length === 0) {
      showToast("案件を選択してください");
      return true;
    }
    const targets = projects.filter(
      (p) => selectedIds.includes(p.id) && canConfirmPayment(p) && simplifiedInvoiceStatus(p) === "入金済み"
    );
    if (!targets.length) {
      showToast("未入金に戻せる案件がありません（入金済みの案件のみ・経理またはマスター/管理者が対象）");
      return true;
    }
    void runExclusive("projects:bulk-payment-cancel", async () => {
      const ok0 = await fpConfirm({
        title: `${targets.length} 件の入金確認を取り消しますか？`,
        message: "未入金（請求済み）に戻ります。",
        note: "入金日の記録も消えます（もう一度入金確認すれば付け直せます）。",
        confirmLabel: "入金確認を取り消す"
      });
      if (!ok0) return;
      const snaps = new Map();
      for (const p of targets) {
        snaps.set(p.id, applyPaymentCancellationToProject(p));
      }
      render();
      let ok = 0;
      const failed = [];
      let mismatch = false;
      for (const p of targets) {
        if (!(p.dbId && authToken)) {
          ok += 1;
          continue;
        }
        const r = await persistPaymentChangeToServer(p, snaps.get(p.id), { clearPayment: true });
        if (r.ok) ok += 1;
        else {
          failed.push(p.name || p.id);
          if (r.mismatch) mismatch = true;
        }
      }
      uiState.projectTable.selected = {};
      if (failed.length) {
        showToastError(`未入金への取り消し: ${ok}件成功 / ${failed.length}件失敗（${failed.slice(0, 3).join("、")}${failed.length > 3 ? "…" : ""}）${mismatch ? PAYMENT_SAVE_MISMATCH_HINT : ""}`);
      } else {
        showToast(`入金確認を取り消しました（${ok}件・請求済みに戻りました）`);
      }
      render();
    }, bulkPaymentCancelEl);
    return true;
  }
  const projectQuickEl = target.closest("[data-project-quick]");
  if (projectQuickEl instanceof HTMLElement) {
    const mode = projectQuickEl.dataset.projectQuick;
    if (mode === "todo") {
      uiState.projectListFilters.status = "invoices";
      uiState.projectListFilters.sort = "updated_desc";
    }
    if (mode === "expired") {
      uiState.projectListFilters.stale = "expired";
      uiState.projectListFilters.sort = "stale_priority";
    }
    if (mode === "handoff") {
      uiState.projectListFilters.status = "payments";
      uiState.projectListFilters.sort = "updated_desc";
    }
    render();
    return true;
  }
  const composeEl = target.closest("[data-compose]");
  const composeType = composeEl instanceof HTMLElement ? composeEl.dataset.compose : undefined;
  if (composeType) {
    const pid = composeEl.dataset.project;
    const proj = projects.find((x) => x.id === pid);
    if (proj && !canEditProject(proj)) {
      showToast(readOnlyHint(proj));
      return true;
    }
    if (hasExistingDoc(proj, composeType)) {
      openComposer(composeType, pid);
    } else {
      openDocStart(composeType, pid);
    }
    return true;
  }
  // ── 入金記録フォームを開く ──
  const openPaymentFormEl = target.closest("[data-open-payment-form]");
  if (openPaymentFormEl instanceof HTMLElement) {
    const p = projects.find((x) => x.id === openPaymentFormEl.dataset.openPaymentForm);
    if (!p) return true;
    if (!canConfirmPayment(p)) {
      showToast("入金確認は経理またはマスター/管理者が行えます");
      return true;
    }
    const today = new Date();
    uiState.paymentForm = {
      projectId: p.id,
      payDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
      amount: p.amount,
      note: ""
    };
    render();
    return true;
  }
  // ── 入金記録フォームを閉じる ──
  const closePaymentFormEl = target.closest("[data-close-payment-form]");
  if (closePaymentFormEl instanceof HTMLElement) {
    uiState.paymentForm = null;
    render();
    return true;
  }
  // ── 入金記録を保存して状態更新 ──
  const savePaymentEl = target.closest("[data-save-payment]");
  if (savePaymentEl instanceof HTMLElement && uiState.paymentForm) {
    const f = uiState.paymentForm;
    const p = projects.find((x) => x.id === f.projectId);
    if (p && !canConfirmPayment(p)) {
      showToast(readOnlyHint(p));
      return true;
    }
    // 入金額は空欄なら請求額。入力があるのに数値として読めない時は保存しない（黙って請求額にしない）
    if (String(f.amount ?? "").trim() && !Number.isFinite(parseBillingNumber(f.amount))) {
      showToastError("入金額を数値で入力してください（例: 50,000）");
      return true;
    }
    let paymentSnap = null;
    if (p) {
      paymentSnap = applyPaymentConfirmationToProject(p, {
        payDate: f.payDate,
        amount: f.amount,
        note: f.note
      });
    }
    uiState.paymentForm = null;
    render();
    if (p && p.dbId && authToken) {
      void (async () => {
        const r = await persistPaymentChangeToServer(p, paymentSnap);
        if (r.ok) {
          showToast(`入金確認を記録しました（${f.payDate}）`);
          return;
        }
        showToastError(`入金記録の保存に失敗しました。もう一度お試しください。${r.mismatch ? PAYMENT_SAVE_MISMATCH_HINT : ""}`);
        render();
      })();
    } else {
      showToast(`入金確認を記録しました（${f.payDate}）`);
    }
    return true;
  }

  // ── 入金の一括記録: 選択解除 ──
  const payBulkClearEl = target.closest("[data-payment-bulk-clear]");
  if (payBulkClearEl instanceof HTMLElement) {
    uiState.paymentBulkSelected = {};
    render();
    return true;
  }

  // ── 入金の一括記録: 選択案件に同じ入金日をまとめて記録 ──
  const payBulkApplyEl = target.closest("[data-payment-bulk-apply]");
  if (payBulkApplyEl instanceof HTMLElement) {
    const sel = uiState.paymentBulkSelected || {};
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) {
      showToast("案件を選択してください");
      return true;
    }
    const dateEl = document.querySelector("input[data-payment-bulk-date]");
    const payDate = dateEl instanceof HTMLInputElement ? String(dateEl.value || "").trim() : "";
    if (!payDate) {
      showToast("入金日を選んでください");
      return true;
    }
    // 対象＝選択かつ入金確認可能な案件のみ（未確認・権限あり）
    const targets = projects.filter(
      (p) => sel[p.id] && canConfirmPayment(p) && p.docs?.payment !== "入金確認済み"
    );
    if (!targets.length) {
      showToast("記録できる案件がありません（権限・状態をご確認ください）");
      return true;
    }
    if (!window.confirm(`選択した ${targets.length} 件を入金確認済みにします（入金日 ${payDate}）。よろしいですか？`)) {
      return true;
    }
    void (async () => {
      const snaps = new Map();
      for (const p of targets) {
        snaps.set(p.id, applyPaymentConfirmationToProject(p, { payDate, amount: p.amount, note: "" }));
      }
      render();
      let ok = 0;
      const failed = [];
      let mismatch = false;
      for (const p of targets) {
        if (!(p.dbId && authToken)) {
          ok += 1;
          continue;
        }
        const r = await persistPaymentChangeToServer(p, snaps.get(p.id));
        if (r.ok) ok += 1;
        else {
          failed.push(p.name || p.id);
          if (r.mismatch) mismatch = true;
        }
      }
      uiState.paymentBulkSelected = {};
      if (failed.length) {
        showToastError(`入金記録: ${ok}件成功 / ${failed.length}件失敗（${failed.slice(0, 3).join("、")}${failed.length > 3 ? "…" : ""}）${mismatch ? PAYMENT_SAVE_MISMATCH_HINT : ""}`);
      } else {
        showToast(`入金を記録しました（${ok}件・${payDate}）`);
      }
      render();
    })();
    return true;
  }

  const shortcutQuoteEl = target.closest("[data-shortcut-quote]");
  if (shortcutQuoteEl instanceof HTMLElement) {
    const firstProject = projects.find((p) => p.currentStage !== "done" && p.invoiceStatus !== "入金確認済み");
    if (firstProject) {
      openDocStart("quote", firstProject.id);
    } else {
      showToast("見積対象の案件がありません。先に案件を作成してください");
    }
    return true;
  }
  const goDetailEl = target.closest("[data-go-detail]");
  if (goDetailEl instanceof HTMLElement) {
    // 書類画面の「← 案件に戻る」は基本情報タブへ（タブ未指定だと既定の請求項目タブになる。2026-10 要望）
    const nextHash = `#project-detail:${goDetailEl.dataset.goDetail || ""}?tab=overview`;
    if (location.hash === nextHash) render();
    else location.hash = nextHash;
    return true;
  }
  const goClientListKeywordEl = target.closest("[data-go-client-list-keyword]");
  if (goClientListKeywordEl instanceof HTMLElement) {
    const keyword = String(goClientListKeywordEl.dataset.goClientListKeyword || "").trim();
    uiState.clientList.filters = {
      ...uiState.clientList.filters,
      keyword,
      approval: "",
      contractOwner: ""
    };
    uiState.clientList.page = 1;
    uiState.dashboardSearch = "";
    location.hash = "#client-list";
    return true;
  }
  // ダッシュボード: パートナー検索から一覧に遷移
  const goPartnerListKeywordEl = target.closest("[data-go-partner-list-keyword]");
  if (goPartnerListKeywordEl instanceof HTMLElement) {
    const keyword = String(goPartnerListKeywordEl.dataset.goPartnerListKeyword || "").trim();
    uiState.partnerList.filters = {
      ...uiState.partnerList.filters,
      keyword,
      approval: "",
      paymentMethod: "all",
      nda: "all",
      contract: "all"
    };
    if (typeof uiState.partnerList.page === "number") uiState.partnerList.page = 1;
    uiState.dashboardSearch = "";
    location.hash = "#partner-list";
    return true;
  }
  // ダッシュボード: ステータスカード → プロジェクト一覧に遷移
  const dbStatusNavEl = target.closest("[data-db-status-nav]");
  if (dbStatusNavEl instanceof HTMLElement) {
    const status = String(dbStatusNavEl.dataset.dbStatusNav || "").trim();
    // カードから遷移するときは全フィルターをリセット（月・クライアント等の残留を防ぐ）
    uiState.projectListFilters.keyword = "";
    uiState.projectListFilters.orderStatuses = [];
    uiState.projectListFilters.status = "all";
    uiState.projectListFilters.mineOnly = true;
    uiState.projectListFilters.completion = "active";
    uiState.projectListFilters.billingMonthFrom = "";
    uiState.projectListFilters.billingMonthTo = "";
    uiState.projectListFilters.paymentDateFrom = "";
    uiState.projectListFilters.paymentDateTo = "";
    uiState.projectListFilters.clientId = "all";
    uiState.projectListFilters.clientNameQuery = "";
    // カードは「自分の案件」を数えているので、遷移先も自分担当に絞る（件数を一致させる）
    uiState.projectListFilters.assignedUserIds = currentUser?.id ? [String(currentUser.id)] : [];
    uiState.projectListFilters.paymentSimple = "";
    uiState.projectListFilters.filterTags = [];
    // 案件番号・案件名・ロック状況・分類も戻す（残るとカードと一覧の件数が合わない。2026-10 レビュー指摘）
    uiState.projectListFilters.no = "";
    uiState.projectListFilters.name = "";
    uiState.projectListFilters.lockState = "all";
    uiState.projectListFilters.workCategory = "all";
    // 初回表示時の既定フィルター（当月の請求月など）でカードの条件を上書きさせない
    uiState.projectListFiltersDefaultsApplied = true;
    if (status === "見積もり中") {
      uiState.projectListFilters.orderStatuses = PROJECT_ORDER_STATUS_LIST.filter(isQuoteOrderStatus);
    } else if (status === "受注済") {
      uiState.projectListFilters.orderStatuses = ["受注済"];
    } else {
      // 送付済・請求済・入金済のカードは失注も含めて数えている。受注を未選択にすると失注が隠れて件数が合わないので全ステータスにする
      uiState.projectListFilters.orderStatuses = [...PROJECT_ORDER_STATUS_LIST];
      if (status === "請求済み" || status === "入金済み") {
        uiState.projectListFilters.status = "invoices";
        uiState.projectListFilters.paymentSimple = status;
      }
    }
    location.hash = "#projects";
    return true;
  }
  const goEl = target.closest("[data-go]");
  if (goEl instanceof HTMLElement) {
    const goKey = String(goEl.dataset.go || "").trim();
    location.hash = goKey === "progress-management" ? `#${progressManagementHomeHash()}` : `#${goKey}`;
    return true;
  }
  const closeComposerEl = target.closest("[data-close-composer]");
  if (closeComposerEl instanceof HTMLElement) {
    closeComposer();
    return true;
  }
  const quickFilterEl = target.closest("[data-quick-filter]");
  if (quickFilterEl instanceof HTMLElement) {
    const [pageType, value] = quickFilterEl.dataset.quickFilter.split(":");
    uiState.quickFilter[pageType] = value;
    render();
    return true;
  }
  // コンポーザー内「PDF出力」ボタン
  const printDocEl = target.closest("[data-print-doc]");
  if (printDocEl instanceof HTMLElement && uiState.composer) {
    // 捺印フローがあり、かつ未承認だと送付をブロックする種別（見積書は対象外）は承認済みでなければ
    // 出力不可（帳票画面 projectDocPreviewFooterHtml の sendBlocked と同じ基準）
    if (
      docTabHasSealWorkflow(uiState.composer.type) &&
      docTypeBlocksSendWhenUnapproved(uiState.composer.type) &&
      getDocSealStatus(uiState.composer.projectId, uiState.composer.type) !== "承認済"
    ) {
      showToast("捺印申請が承認されるとPDF出力できます");
      return true;
    }
    // 現在のコンポーザー内容を一時的に savedDocs に書き込んでから印刷
    const p = projects.find((x) => x.id === uiState.composer.projectId);
    if (p) {
      if (!p.savedDocs) p.savedDocs = {};
      p.savedDocs[uiState.composer.type] = {
        company: uiState.composer.company,
        person: uiState.composer.person,
        subject: uiState.composer.subject,
        issueDate: uiState.composer.issueDate,
        dueDate: uiState.composer.dueDate,
        lines: uiState.composer.lines.map((l) => ({ ...l })),
        note: uiState.composer.note,
        bankMemo: uiState.composer.bankMemo,
        taxRate: uiState.composer.taxRate ?? 0.1
      };
    }
    printDocument(uiState.composer.type, uiState.composer.projectId);
    return true;
  }
  // 一覧・詳細ページの「PDF出力」ボタン（保存済みドキュメントから印刷）
  const printSavedEl = target.closest("[data-print-saved]");
  if (printSavedEl instanceof HTMLElement) {
    const [docType, projectId] = printSavedEl.dataset.printSaved.split(":");
    printDocument(docType, projectId);
    return true;
  }
  // 原価一覧 PDF 出力
  const printCostsEl = target.closest("[data-print-costs]");
  if (printCostsEl instanceof HTMLElement) {
    printCostList(printCostsEl.dataset.printCosts);
    return true;
  }
  const addLineEl = target.closest("[data-add-line]");
  if (addLineEl instanceof HTMLElement) {
    uiState.composer.lines.push({ item: "新規項目", qty: 1, price: 0 });
    render();
    return true;
  }
  const removeLineEl = target.closest("[data-remove-line]");
  if (removeLineEl instanceof HTMLElement) {
    uiState.composer.lines.splice(Number(removeLineEl.dataset.removeLine), 1);
    if (uiState.composer.lines.length === 0) uiState.composer.lines.push({ item: "新規項目", qty: 1, price: 0 });
    render();
    return true;
  }
  const docSaveEl = target.closest("[data-doc-save]");
  if (docSaveEl instanceof HTMLElement) {
    const p = projects.find((x) => x.id === uiState.composer.projectId);
    const t = uiState.composer.type;
    // 書類の中身を案件に紐付けて保存（quote/invoice/order 共通）
    if (!p.savedDocs) p.savedDocs = {};
    p.savedDocs[t] = {
      company: uiState.composer.company,
      person: uiState.composer.person,
      subject: uiState.composer.subject,
      issueDate: uiState.composer.issueDate,
      dueDate: uiState.composer.dueDate,
      lines: uiState.composer.lines.map((l) => ({ ...l })),
      note: uiState.composer.note,
      bankMemo: uiState.composer.bankMemo,
      taxRate: uiState.composer.taxRate ?? 0.1,
      savedAt: new Date().toLocaleDateString("ja-JP")
    };
    // ステータス更新前のスナップショット（サーバー保存失敗時にロールバック用）
    const prevInvoiceStatus = p.invoiceStatus;
    const prevDocStatus = p.docs ? p.docs[t] : undefined;
    const prevAmount = p.amount;
    if (t === "invoice") {
      if (docSaveEl.dataset.docSave === "draft") p.invoiceStatus = "下書きあり";
      if (docSaveEl.dataset.docSave === "save") p.invoiceStatus = "下書きあり";
      if (docSaveEl.dataset.docSave === "ready") p.invoiceStatus = "送付準備OK";
      if (docSaveEl.dataset.docSave === "sent") p.invoiceStatus = "送付済み";
    } else {
      if (docSaveEl.dataset.docSave === "draft") p.docs[t] = "下書きあり";
      if (docSaveEl.dataset.docSave === "save") p.docs[t] = "確認待ち";
      if (docSaveEl.dataset.docSave === "ready") p.docs[t] = "送付準備OK";
      if (docSaveEl.dataset.docSave === "sent") p.docs[t] = "送付済み";
    }
    // 見積書を送付準備OK/送付済みにしたら、見積合計（税抜）を受注金額へ反映
    // （受注金額は稼働状況の時間単価・按分売上の分子。見積フローで自動的に最新へ保つ）
    let amountSynced = false;
    if (t === "quote" && ["ready", "sent"].includes(docSaveEl.dataset.docSave)) {
      const sub = calc(uiState.composer.lines, uiState.composer.taxRate ?? 0.1).subtotal;
      if (sub > 0 && sub !== p.amount) {
        p.amount = sub;
        amountSynced = true;
      }
    }
    closeComposer();
    showToast(amountSynced ? `書類状態を更新し、受注金額を見積額（¥${Number(p.amount).toLocaleString()}・税抜）に合わせました` : "書類状態を更新しました");
    // ステータス変更をサーバーに永続化（失敗したらロールバック）
    if (p.dbId && authToken) {
      void (async () => {
        try {
          await persistProjectDetailToServer(p);
        } catch {
          // 保存失敗: in-memory を戻してユーザーに通知
          if (t === "invoice") p.invoiceStatus = prevInvoiceStatus;
          else if (p.docs) p.docs[t] = prevDocStatus;
          p.amount = prevAmount;
          showToastError("書類状態の保存に失敗しました。もう一度お試しください。");
          render();
        }
      })();
    }
    return true;
  }
  const setDocEl = target.closest("[data-set-doc]");
  if (setDocEl instanceof HTMLElement) {
    const [docType, action, projectId] = setDocEl.dataset.setDoc.split(":");
    const p = projects.find((x) => x.id === projectId);
    if (!p) return true;
    if (!canEditProject(p)) {
      showToast(readOnlyHint(p));
      return true;
    }
    // 「送付済み」は実際の送付（メール・郵送・PDF）と同じく、捺印申請が承認済みの書類だけ（見積書は対象外）。
    // 以前は未承認でも状態だけ送付済みにでき、承認されていない書類が送られた記録になっていた
    if (action === "sent" && docTabHasSealWorkflow(docType) && docTypeBlocksSendWhenUnapproved(docType) && getDocSealStatus(p.id, docType) !== "承認済") {
      showToast("捺印申請が承認されるまで送付済みにできません");
      return true;
    }
    const prevSnap = {
      invoiceStatus: p.invoiceStatus,
      orderStatus: p.orderStatus,
      currentStage: p.currentStage,
      nextAction: p.nextAction,
      contractDone: p.contractDone,
      handoffStatus: p.handoffStatus,
      currentOwnerRole: p.currentOwnerRole,
      docs: { ...(p.docs || {}) }
    };
    let handoffTouched = false;
    if (docType === "quote") {
      if (action === "sent") p.docs.quote = "送付済み";
      if (action === "ready") p.docs.quote = "送付準備OK";
      p.currentStage = "quotes";
      if (action === "sent") uiState.orderFlowPromptProjectId = p.id;
    }
    if (docType === "invoice") {
      if (action === "sent") p.invoiceStatus = "送付済み";
      if (action === "ready") p.invoiceStatus = "送付準備OK";
      if (action === "sent") p.nextAction = "経理へ引き継ぐ";
      p.currentStage = "invoices";
    }
    if (docType === "contract") {
      if (action === "draft") p.docs.contract = "下書きあり";
      if (action === "edit") p.docs.contract = "送付準備OK";
      if (action === "sent") p.docs.contract = "送付済み";
      if (action === "sent") p.contractDone = true;
      p.currentStage = "quotes";
    }
    if (docType === "order") {
      if (action === "sent") {
        p.docs.order = "送付済み";
        p.orderStatus = "受注済";
      }
    }
    if (docType === "nda") {
      if (action === "review") p.docs.nda = "確認待ち";
      if (action === "sent") p.docs.nda = "送付済み";
    }
    if (docType === "payment") {
      if (action === "done" && !canConfirmPayment(p)) {
        showToast("入金確認は経理またはマスター/管理者が行えます");
        return true;
      }
      if (action === "done") {
        p.docs.payment = "入金確認済み";
        p.invoiceStatus = "入金確認済み";
        p.handoffStatus = "完了";
        p.currentOwnerRole = "経理";
        p.nextAction = "対応完了（必要に応じて確認）";
        p.currentStage = "done";
        handoffTouched = true;
      }
    }
    showToast("処理を反映しました");
    if (p.dbId && authToken) {
      void (async () => {
        try {
          await persistProjectDetailToServer(p);
          if (handoffTouched) await persistProjectHandoffToServer(p);
        } catch {
          Object.assign(p, prevSnap);
          showToastError("処理の保存に失敗しました。もう一度お試しください。");
          render();
        }
      })();
    }
    return true;
  }
  const handoffEl = target.closest("[data-handoff]");
  if (handoffEl instanceof HTMLElement) {
    const [direction, projectId] = handoffEl.dataset.handoff.split(":");
    const p = projects.find((x) => x.id === projectId);
    if (!p) return true;
    if (!canEditProject(p)) {
      showToast(readOnlyHint(p));
      return true;
    }
    const prevSnap = {
      assignedUserId: p.assignedUserId,
      previousAssignedUserId: p.previousAssignedUserId,
      handoffStatus: p.handoffStatus,
      currentOwnerRole: p.currentOwnerRole,
      handoffAt: p.handoffAt,
      handedBy: p.handedBy,
      handoffNote: p.handoffNote,
      nextAction: p.nextAction,
      invoiceStatus: p.invoiceStatus,
      currentStage: p.currentStage
    };
    const todayStr = new Date().toLocaleDateString("ja-JP");
    let handoffMsg = "";
    if (direction === "to-accounting") {
      p.previousAssignedUserId = p.assignedUserId;
      p.handoffStatus = "経理確認待ち";
      p.currentOwnerRole = "経理";
      p.assignedUserId = defaultAccountingUserId();
      p.handoffAt = todayStr;
      p.handedBy = p.owner;
      p.handoffNote = "請求送付済み。入金確認をお願いします。";
      p.nextAction = "入金確認を記録する";
      p.invoiceStatus = "入金待ち";
      p.currentStage = "payments";
      p.lastUpdatedBy = currentUser.id;
      p.lastUpdatedAt = todayStr;
      handoffMsg = "経理へ引き継ぎました";
    } else if (direction === "to-sales") {
      p.handoffStatus = "差し戻し中";
      p.currentOwnerRole = "営業";
      if (p.previousAssignedUserId) p.assignedUserId = p.previousAssignedUserId;
      p.handoffNote = "差し戻し: 内容をご確認ください。";
      p.nextAction = "再請求の対応を進める";
      p.currentStage = "invoices";
      p.lastUpdatedBy = currentUser.id;
      p.lastUpdatedAt = todayStr;
      handoffMsg = "営業へ差し戻しました";
    } else {
      return true;
    }
    showToast(handoffMsg);
    if (p.dbId && authToken) {
      void (async () => {
        try {
          await persistProjectDetailToServer(p);
          await persistProjectHandoffToServer(p);
        } catch {
          Object.assign(p, prevSnap);
          showToastError("引き継ぎの保存に失敗しました。もう一度お試しください。");
          render();
        }
      })();
    }
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 7385〜8183 行）。処理したら true（16 側はそこで止める） */
function handleCostClick(e, target) {

  // ── 原価: フィルタータブ（案件詳細）／原価管理は costMgmtTabKeys ──
  const costFilterEl = target.closest("[data-cost-filter]");
  if (costFilterEl instanceof HTMLElement) {
    const key = String(costFilterEl.dataset.costFilter || "all");
    if (currentRoute().page === "cost-management") {
      uiState.costMgmtTabKeys = key ? [key] : ["all"];
      uiState.costMgmtSelected = {};
      uiState.costMgmtBulkModal = null;
    } else {
      uiState.costFilter = key;
    }
    render();
    return true;
  }

  if (target.closest("[data-cost-mgmt-filter-clear]") && currentRoute().page === "cost-management") {
    uiState.costMgmtFilter = emptyCostMgmtFilter();
    uiState.costMgmtTabKeys = ["all"];
    uiState.costMgmtSelected = {};
    uiState.costMgmtBulkModal = null;
    if (uiState.costMgmtTable) uiState.costMgmtTable.page = 1;
    render();
    return true;
  }

  // ── 原価管理: 登録ウィザード（中央モーダル） ──
  const openCostRegEl = target.closest("[data-open-cost-mgmt-register]");
  if (openCostRegEl instanceof HTMLElement) {
    // 前回の取込で結果不明の行が残っていれば、新規取込の前にどちらかに決めてもらう
    // （残したまま同じファイルを取り込み直すと二重登録になる。OK=復元・キャンセル=破棄して新規へ）
    const pendingImport = costImportPendingRestore();
    if (pendingImport) {
      if (
        window.confirm(
          `前回の原価取込で、登録結果を受け取れなかった行が${pendingImport.count}件残っています。\n復元して登録を続けますか？（同じ行は二重登録されません）\nキャンセルすると前回分は破棄して新規の取込を始めます。`
        )
      ) {
        costImportRestoreFromStorage();
        return true;
      }
      costImportRunStateClear();
    }
    const attr = openCostRegEl.getAttribute("data-open-cost-mgmt-register");
    // "sales" = 営業の請求書取込（原価割当ページから起動。PDF/Excel/コピペのみ・計上先は担当案件に限定）
    const salesMode = attr === "sales";
    const mode = "other"; // 旧「広告原価を登録」（媒体CSV専用）は廃止
    const defaultChoice = salesMode ? "pdf:generic" : "csv:generic";
    const defaultSourceKey = "generic";
    uiState.costRegWizard = {
      phase: "setup",
      mode,
      salesMode,
      targetMonth: currentMonth(),
      csvSourceKey: defaultSourceKey,
      costImportChoice: defaultChoice
    };
    render();
    return true;
  }
  if (target.closest("[data-close-cost-reg-wizard]")) {
    const wz = uiState.costRegWizard;
    if (wz && wz.phase === "preview" && Array.isArray(wz.rows) && wz.rows.length > 0) {
      if (!window.confirm("取り込んだデータがすべて破棄されます。閉じてもよいですか？")) return true;
    }
    costImportRunStateClear();
    uiState.costRegWizard = null;
    render();
    return true;
  }
  if (target.closest("[data-cost-wizard-back-setup]")) {
    const cur = uiState.costRegWizard;
    if (!cur || cur.phase === "setup") return true;
    // 戻る先は常に「原価を登録」の取込方法選択（旧「広告原価を登録」＝媒体CSV専用画面は廃止）
    const backMode = "other";
    const backFallbackKey = "generic";
    uiState.costRegWizard = {
      phase: "setup",
      mode: backMode,
      salesMode: cur.salesMode === true,
      targetMonth: cur.targetMonth,
      csvSourceKey: COST_IMPORT_CSV_SOURCE_META[cur.csvSourceKey] ? cur.csvSourceKey : backFallbackKey,
      costImportChoice:
        cur.previewKind && cur.csvSourceKey && parseCostImportChoiceValue(`${cur.previewKind}:${cur.csvSourceKey}`)
          ? `${cur.previewKind}:${cur.csvSourceKey}`
          : `csv:${COST_IMPORT_CSV_SOURCE_META[cur.csvSourceKey] ? cur.csvSourceKey : backFallbackKey}`,
      // 請求書メール受信箱から来た取込は、戻っても同じ添付PDFで再取込できるよう受信箱の情報を持ち越す
      ...(cur.inboxItemId
        ? { inboxItemId: cur.inboxItemId, inboxFiles: cur.inboxFiles, inboxDocIds: cur.inboxDocIds, inboxSubject: cur.inboxSubject }
        : {})
    };
    render();
    return true;
  }
  if (target.closest("[data-cost-wizard-paste-parse]") && uiState.costRegWizard?.phase === "paste") {
    const monthIn = document.querySelector("[data-cost-wizard-paste-month]");
    const targetMonth = billingMonthStoredFromMonthInput(monthIn instanceof HTMLInputElement ? monthIn.value : "");
    if (!targetMonth) {
      showToast("計上月を選択してください");
      return true;
    }
    const textEl = document.querySelector("[data-cost-wizard-paste-text]");
    const pasteText = textEl instanceof HTMLTextAreaElement ? textEl.value : "";
    uiState.costRegWizard.pasteText = pasteText;
    uiState.costRegWizard.targetMonth = targetMonth;
    // ── 汎用コピペ取込: AIで読み取り、失敗時は簡易パーサにフォールバック ──
    if (uiState.costRegWizard.costImportChoice === "paste:generic") {
      if (!pasteText.trim()) {
        showToast("貼り付け内容が空です");
        return true;
      }
      void (async () => {
        let parsed = null;
        let payee = "";
        let aiUsed = false;
        try {
          showToastPersistent("AIで貼り付け内容を読み取り中…");
          const aiResp = await fetch("/api/cost-extract-pdf-ai", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
            },
            body: JSON.stringify({ text: pasteText })
          });
          if (aiResp.ok) {
            const aj = await aiResp.json();
            // 外貨建てで円換算できなかった: 従来パーサは外貨額を円として読むのでフォールバックしない
            if (aj && aj.reason === "fx_unavailable") {
              clearToast();
              showToast(`${aj.currency || "外貨"}建ての円換算レートを取得できませんでした。時間をおいて再度お試しください`);
              return;
            }
            if (aj && aj.ok && Array.isArray(aj.items) && aj.items.length) {
              const aiRows = aiItemsToCostImportParsedRows(aj.items, aj.payee, "AI読取（貼り付け）", aj.fx, aj.subject);
              if (aiRows.length) {
                parsed = aiRows;
                payee = String(aj.payee || "");
                aiUsed = true;
              }
            }
          }
        } catch {
          /* フォールバックへ */
        }
        clearToast();
        if ((!parsed || !parsed.length) && costImportTextLooksForeignCurrency(pasteText)) {
          showToastError("外貨建て（ドル等）の明細はAI読取でのみ円換算できます。AI読取に失敗したため、時間をおいて再度お試しください");
          return;
        }
        if (!parsed || !parsed.length) parsed = parseGenericPastedRows(pasteText);
        if (!parsed.length) {
          showToast("明細を読み取れませんでした。品目と金額が含まれる行を貼り付けてください");
          return;
        }
        uiState.costRegWizard = {
          phase: "preview",
          mode: "other",
          salesMode: uiState.costRegWizard?.salesMode === true,
          targetMonth,
          csvSourceKey: "generic",
          costImportChoice: "paste:generic",
          previewKind: "paste",
          sourceLabel: aiUsed ? "貼り付け · AI" : "貼り付け",
          importCategory: "外注費",
          rows: buildCostImportReviewRows(consolidateCostImportParsedRows(parsed), { documentPayeeHint: payee, targetMonth, sourceKey: "generic" })
        };
        render();
      })();
      return true;
    }
    const meta = COST_IMPORT_CSV_SOURCE_META.onamae;
    let parsedPaste;
    try {
      parsedPaste = parseOnamaePastedRows(pasteText);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "貼り付けデータの読み取りに失敗しました");
      return true;
    }
    if (!parsedPaste.length) {
      showToastError("金額を検出できませんでした。表の列（日付・品目・ドメイン・税率・金額）が含まれているか確認してください");
      return true;
    }
    void (async () => {
      showToastPersistent("ドメイン管理から計上先（担当営業の原価箱・販管費）を確認しています…");
      const domainAssignByHint = await resolveOnamaeDomainCostAssignments(parsedPaste);
      clearToast();
      uiState.costRegWizard = {
        phase: "preview",
        mode: "other",
        targetMonth,
        csvSourceKey: "onamae",
        costImportChoice: "paste:onamae",
        previewKind: "paste",
        sourceLabel: `${meta.label} 貼り付け`,
        defaultVendor: meta.vendor,
        importCategory: "その他",
        importTaxMode: "inclusive",
        rows: buildCostImportReviewRows(consolidateCostImportParsedRows(parsedPaste), {
          fixedVendor: meta.vendor,
          targetMonth,
          sourceKey: "onamae",
          domainAssignByHint
        })
      };
      render();
    })();
    return true;
  }
  const costImportExcludeDupsEl = target.closest("[data-cost-import-exclude-dups]");
  if (costImportExcludeDupsEl instanceof HTMLElement && uiState.costRegWizard?.phase === "preview") {
    const rev = uiState.costRegWizard;
    if (!Array.isArray(rev.rows)) return true;
    syncCostImportPreviewRowsFromDom(rev);
    const before = rev.rows.length;
    rev.rows = rev.rows.filter((r) => !r.duplicateOfExisting);
    const removed = before - rev.rows.length;
    showToast(removed > 0 ? `重複候補 ${removed}件 を除外しました` : "除外対象がありません");
    render();
    return true;
  }
  const costImportTaxModeEl = target.closest("[data-cost-import-tax-mode]");
  if (costImportTaxModeEl instanceof HTMLElement && uiState.costRegWizard?.phase === "preview") {
    const rev = uiState.costRegWizard;
    if (!Array.isArray(rev.rows)) return true;
    const mode = costImportTaxModeEl.getAttribute("data-cost-import-tax-mode") === "exclusive" ? "exclusive" : "inclusive";
    if (rev.importTaxMode === mode) return true;
    syncCostImportPreviewRowsFromDom(rev);
    const oldMode = rev.importTaxMode;
    for (const r of rev.rows) {
      // 消費税なしの外貨行は日本の税込/税抜の解釈を当てない
      if (r.importCaptionKind === "consumption_tax" || r.noAutoConsumptionTax) continue;
      const ex = Math.round(Number(r.amountExTax ?? 0)) || 0;
      const tx = Math.round(Number(r.consumptionTax ?? 0)) || 0;
      // 元の取込金額を再解釈する。保持していればそれを、無ければ現在の税区分から復元
      // （税込なら ex+tx が元値、税抜なら ex が元値）。gross 固定の旧実装だと
      // 税抜へ切替えるたびに税が二重に乗るため、必ず「元の取込値」を基準に再分割する。
      let raw = Math.round(Number(r.importedRawAmount ?? 0)) || 0;
      if (raw === 0) raw = oldMode === "inclusive" ? ex + tx : ex;
      if (raw === 0) continue;
      // 値引き等の負数も符号を保ったまま分け直す（以前は負数行を飛ばし、税区分を切り替えても消費税0のままだった）
      const split = splitSignedImportedYen(raw, mode);
      r.amountExTax = split.amountExTax;
      r.consumptionTax = split.consumptionTax;
      r.importedRawAmount = split.rawImported;
    }
    rev.importTaxMode = mode;
    render();
    return true;
  }
  const costReviewRemoveEl = target.closest("[data-cost-review-remove]");
  if (costReviewRemoveEl instanceof HTMLElement && uiState.costRegWizard?.phase === "preview") {
    const rev = uiState.costRegWizard;
    if (!Array.isArray(rev.rows)) return true;
    const idx = Number.parseInt(String(costReviewRemoveEl.getAttribute("data-cost-review-remove") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || idx >= rev.rows.length) return true;
    syncCostImportPreviewRowsFromDom(rev);
    rev.rows.splice(idx, 1);
    render();
    return true;
  }
  const costPreviewAddManualEl = target.closest("[data-cost-preview-add-manual-row]");
  if (costPreviewAddManualEl instanceof HTMLElement && uiState.costRegWizard?.phase === "preview") {
    const rev = uiState.costRegWizard;
    if (!Array.isArray(rev.rows)) return true;
    syncCostImportPreviewRowsFromDom(rev);
    const manual = createManualCostImportPreviewRow();
    const isMultiPdfWiz = Array.isArray(rev.pdfSources) && rev.pdfSources.length > 1;
    if (rev.previewKind === "pdf" && !isMultiPdfWiz) {
      // 単一PDFは請求元プルダウンの値を全行へ反映（手動行含む）
      rev.rows.push(manual);
      applyDocumentPayeeUiKeyToImportRows(rev);
    } else {
      // 複数PDF・CSVは行ごとに請求元が決まるため一括反映しない
      manual.vendorResolvedLabel = String(rev.defaultVendor || "").trim();
      rev.rows.push(manual);
    }
    showToast("手入力の行を追加しました。金額・案件を入力して反映してください。");
    render();
    return true;
  }
  const costWizardManualEl = target.closest("[data-cost-wizard-manual]");
  if (costWizardManualEl instanceof HTMLElement && uiState.costRegWizard?.phase === "setup") {
    const monthIn = document.querySelector("[data-cost-wizard-month]");
    const tm = billingMonthStoredFromMonthInput(monthIn instanceof HTMLInputElement ? monthIn.value : "");
    if (!tm) {
      showToast("対象月を選択してください");
      return true;
    }
    const selectedChoice =
      parseCostImportChoiceValue(uiState.costRegWizard.costImportChoice || "")
        ? uiState.costRegWizard.costImportChoice
        : "csv:google";
    const pv = parseCostImportChoiceValue(selectedChoice);
    const sk = pv?.sourceKey || uiState.costRegWizard.csvSourceKey || "google";
    const cic = pv ? `${pv.kind}:${pv.sourceKey}` : `csv:${sk}`;
    uiState.costRegWizard = {
      phase: "manual",
      mode: uiState.costRegWizard.mode || "other",
      targetMonth: tm,
      csvSourceKey: sk,
      costImportChoice: cic,
      manualProjectId: ""
    };
    render();
    return true;
  }
  const costWizardOpenFormEl = target.closest("[data-cost-wizard-open-manual-form]");
  if (costWizardOpenFormEl instanceof HTMLElement && !costWizardOpenFormEl.hasAttribute("disabled")) {
    const wz = uiState.costRegWizard;
    if (!wz || wz.phase !== "manual") return true;
    const monthIn = document.querySelector("[data-cost-wizard-manual-month]");
    const tm =
      billingMonthStoredFromMonthInput(monthIn instanceof HTMLInputElement ? monthIn.value : "") || wz.targetMonth;
    const sel = document.querySelector("[data-cost-wizard-manual-project]");
    const pid = sel instanceof HTMLSelectElement ? String(sel.value || "").trim() : "";
    const projectIdResolved = pid || COST_UNASSIGNED_PROJECT_UI_ID;
    uiState.costRegWizard = null;
    uiState.costForm = {
      projectId: projectIdResolved,
      editingId: null,
      category: "制作費",
      vendor: "",
      amount: 0,
      consumptionTax: 0,
      targetMonth: tm || currentMonth(),
      dueDate: "",
      paidDate: "",
      invoiceDate: "",
      paymentMethod: "",
      invoiceNo: "",
      withholdingTax: 0,
      // 経理の登録は 発注済・受領済 が既定（営業の手入力は従来どおり）。フォームで変更可
      ...costRegisterDefaultStatuses(wz.salesMode === true),
      note: "",
      sourcePdfDocId: null
    };
    render();
    return true;
  }

  const costWizardImportEl = target.closest("[data-cost-wizard-import]");
  if (costWizardImportEl instanceof HTMLElement && uiState.costRegWizard?.phase === "setup") {
    const monthIn = document.querySelector("[data-cost-wizard-month]");
    const targetMonth = billingMonthStoredFromMonthInput(monthIn instanceof HTMLInputElement ? monthIn.value : "");
    if (!targetMonth) {
      showToast("対象月を選択してください");
      return true;
    }
    const parsed = parseCostImportChoiceValue(costWizardImportEl.getAttribute("data-cost-wizard-import") || "");
    if (!parsed) {
      showToast("取り込み方法を選択してください");
      return true;
    }
    const { kind, sourceKey } = parsed;
    const meta = COST_IMPORT_CSV_SOURCE_META[sourceKey];
    const choiceStr = `${kind}:${sourceKey}`;
    uiState.costRegWizard.targetMonth = targetMonth;
    uiState.costRegWizard.csvSourceKey = sourceKey;
    uiState.costRegWizard.costImportChoice = choiceStr;

    if (kind === "paste") {
      uiState.costRegWizard.phase = "paste";
      if (typeof uiState.costRegWizard.pasteText !== "string") uiState.costRegWizard.pasteText = "";
      render();
      return true;
    }

    if (kind === "excel") {
      const inp = document.createElement("input");
      inp.type = "file";
      inp.accept = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      inp.addEventListener("change", () => {
        const file = inp.files && inp.files[0];
        if (!file) return;
        void (async () => {
          try {
            // Vercel のリクエストボディ上限（約4.5MB）を超えないよう事前チェック
            if (file.size > 4 * 1024 * 1024) {
              showToast("Excelファイルが大きすぎます（4MBまで）。内容をコピーして「コピペ取込」をお試しください");
              return;
            }
            showToastPersistent("AIでExcelを読み取り中…");
            const buf = await file.arrayBuffer();
            const base64 = arrayBufferToBase64(buf);
            const aiResp = await fetch("/api/cost-extract-pdf-ai", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
              },
              body: JSON.stringify({ xlsxBase64: base64, fileName: file.name || "invoice.xlsx" })
            });
            clearToast();
            const aj = aiResp.ok ? await aiResp.json() : null;
            if (!aj || !aj.ok || !Array.isArray(aj.items) || !aj.items.length) {
              showToast(
                aj && aj.reason === "ai_unconfigured"
                  ? "AI読取が未設定のためExcel取込を利用できません。内容をコピーして「コピペ取込」をお試しください"
                  : "Excelから明細を読み取れませんでした。内容をコピーして「コピペ取込」をお試しください"
              );
              render();
              return;
            }
            const aiRows = aiItemsToCostImportParsedRows(aj.items, aj.payee, "AI読取（Excel）", aj.fx, aj.subject);
            if (!aiRows.length) {
              showToast("Excelから明細を読み取れませんでした。内容をコピーして「コピペ取込」をお試しください");
              render();
              return;
            }
            uiState.costRegWizard = {
              phase: "preview",
              mode: uiState.costRegWizard?.mode || "other",
              salesMode: uiState.costRegWizard?.salesMode === true,
              targetMonth,
              csvSourceKey: sourceKey,
              costImportChoice: choiceStr,
              previewKind: "excel",
              sourceLabel: `Excel · AI（${file.name || "invoice.xlsx"}）`,
              importCategory: "外注費",
              rows: buildCostImportReviewRows(consolidateCostImportParsedRows(aiRows), {
                documentPayeeHint: String(aj.payee || ""),
                targetMonth,
                sourceKey
              })
            };
            render();
          } catch (e) {
            clearToast();
            showToast(e instanceof Error ? e.message : "Excelの読み込みに失敗しました");
            render();
          }
        })();
      });
      inp.click();
      return true;
    }

    if (kind === "csv") {
      const inp = document.createElement("input");
      inp.type = "file";
      inp.accept = ".csv,text/csv";
      inp.addEventListener("change", () => {
        const file = inp.files && inp.files[0];
        if (!file) return;
        void (async () => {
          try {
            showToastPersistent("CSVを取り込み中…");
            const text = await readCsvFileText(file);
            const parsedCsv = sourceKey === "upsider"
              ? parseUpsiderCsvRows(text)
              : sourceKey === "tecolas"
                ? parseTecolasCsvRows(text)
                : parseAdvertisingCostCsvRows(text, sourceKey);
            clearToast();
            if (!parsedCsv.length) {
              showToast("取り込めるデータ行がありません");
              return;
            }
            uiState.costRegWizard = {
              phase: "preview",
              mode: "other", // 旧「広告原価を登録」画面は廃止（戻るで媒体CSV専用画面に落ちないように）
              salesMode: uiState.costRegWizard?.salesMode === true,
              targetMonth,
              csvSourceKey: sourceKey,
              costImportChoice: choiceStr,
              previewKind: "csv",
              sourceLabel: `${meta.label} CSV`,
              defaultVendor: meta.vendor,
              importCategory: sourceKey === "upsider" || sourceKey === "tecolas" ? "その他" : uiState.costRegWizard?.mode === "other" ? "外注費" : "広告費",
              ...(sourceKey === "upsider" ? { importTaxMode: "inclusive" } : sourceKey === "tecolas" ? { importTaxMode: "exclusive" } : {}),
              rows: buildCostImportReviewRows(consolidateCostImportParsedRows(parsedCsv), { fixedVendor: meta.vendor, targetMonth, sourceKey })
            };
            render();
          } catch (e) {
            showToast(e instanceof Error ? e.message : "CSVの読み込みに失敗しました");
            render();
          }
        })();
      });
      inp.click();
      return true;
    }
    const inboxFiles = uiState.costRegWizard.inboxFiles;
    if (Array.isArray(inboxFiles) && inboxFiles.length) {
      // 請求書メール受信箱から: 受信済みの添付PDFをファイル選択の代わりに使う（15r-invoice-mail-inbox.js）
      const wz = uiState.costRegWizard;
      void costImportPdfFilesToWizard(inboxFiles, {
        sourceKey,
        targetMonth,
        choiceStr,
        inbox: { itemId: wz.inboxItemId, files: inboxFiles, docIds: wz.inboxDocIds, subject: wz.inboxSubject }
      });
      return true;
    }
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = ".pdf,application/pdf";
    inp.multiple = true;
    inp.addEventListener("change", () => {
      const files = Array.from(inp.files || []);
      if (!files.length) return;
      void costImportPdfFilesToWizard(files, { sourceKey, targetMonth, choiceStr });
    });
    inp.click();
    return true;
  }

  const applyImportEl = target.closest("[data-cost-import-apply]");
  if (applyImportEl instanceof HTMLElement && uiState.costRegWizard?.phase === "preview" && uiState.costRegWizard.rows?.length) {
    const forceApply = applyImportEl.dataset.costImportApply === "force";
    // 二重クリック防止
    if (uiState.costRegWizard.registering) return true;
    void (async () => {
      const rev = uiState.costRegWizard;
      if (!rev?.rows?.length || rev.phase !== "preview") return;
      if (rev.registering) return;
      syncCostImportPreviewRowsFromDom(rev);
      // 原価箱（案件未定）ピックの行を、登録前に実案件へ解決する（無ければ作成）。
      // 同じ営業を指す行は1回のAPI呼び出しにまとめ、1件でも失敗したら丸ごと中断する
      // （一部の行だけ実案件に化けて残り未解決のまま登録が進む、という不整合を避ける）
      const boxResolveCache = new Map();
      for (const row of rev.rows) {
        const pick = String(row.projectIdPick ?? "").trim();
        if (!costBoxPickOwnerUiId(pick)) continue;
        const result = await resolveCostBoxPick(pick, boxResolveCache);
        if (!result.ok) {
          showToastError(result.error);
          return;
        }
        row.projectIdPick = result.uiId;
      }
      const payeeSel = document.querySelector("[data-document-payee-select]");
      if (payeeSel instanceof HTMLSelectElement && rev.previewKind === "pdf") {
        rev.documentPayeeUiKey = payeeSel.value;
        applyDocumentPayeeUiKeyToImportRows(rev);
      }
      const catPickEl = document.querySelector("[data-cost-import-category-pick]");
      const catPickNorm =
        catPickEl instanceof HTMLSelectElement ? normalizeCostCategoryLabel(catPickEl.value) : "";
      const revCatNorm = rev.importCategory ? normalizeCostCategoryLabel(rev.importCategory) : "";
      const importCategory =
        catPickNorm && COST_CATEGORIES.includes(catPickNorm)
          ? catPickNorm
          : revCatNorm && COST_CATEGORIES.includes(revCatNorm)
            ? revCatNorm
            : rev.mode === "other"
              ? "制作費"
              : "媒体費";
      // 営業モード: 未割当・バケットへの登録はサーバが拒否するため、全行に計上先案件の選択を必須にする
      // （選択肢は担当案件に絞ってある。スキップ指定の行と税キャプション行は登録されないので対象外）
      if (rev.salesMode === true) {
        const missingProject = rev.rows.some((r) => {
          if (r.importCaptionKind === "consumption_tax") return false;
          const pm = costImportRowProvisionalMatch(r);
          if (pm.status === "mismatch" && String(r.provMismatchAction || "register") === "skip") return false;
          return !String(r.projectIdPick ?? "").trim();
        });
        if (missingProject) {
          showToastError("計上先の案件が未選択の行があります。すべての行で案件を選択してください。");
          return;
        }
      }
      const hasUnmatchedVendor = rev.rows.some((r) => r.vendorMasterUnmatched);
      if (hasUnmatchedVendor && !forceApply) {
        showToastError("マスタ未登録の取引先が含まれます。問題なければ「それでも登録する」で続行してください。");
        return;
      }
      // 元PDFの保存はウィザード1回につき1度だけ（結果不明の行を再登録するときに再アップロードしない。
      // 保存済みIDは rev に控える）
      let uploadedPdfDocId = rev.uploadedPdfDocId ?? null;
      let sourceDocIdByIndex = Array.isArray(rev.sourceDocIdByIndex) ? rev.sourceDocIdByIndex : null;
      if (rev.previewKind === "pdf" && authToken && uploadedPdfDocId == null && !sourceDocIdByIndex) {
        try {
          if (Array.isArray(rev.pdfSources) && rev.pdfSources.length) {
            // 複数PDF: 各元PDFを1回ずつ保存し、行ごとに紐づける
            sourceDocIdByIndex = [];
            for (let i = 0; i < rev.pdfSources.length; i++) {
              showToastProgress(`取込PDFを保存しています… ${i + 1}/${rev.pdfSources.length}枚`);
              const s = rev.pdfSources[i];
              sourceDocIdByIndex[i] = await uploadCostSourcePdfForImport(s.base64, s.fileName || `invoice-${i + 1}.pdf`);
            }
            rev.sourceDocIdByIndex = sourceDocIdByIndex;
            clearToast();
          } else if (rev.importPdfBase64) {
            showToastPersistent("取込PDFを保存しています…");
            uploadedPdfDocId = await uploadCostSourcePdfForImport(rev.importPdfBase64, rev.importPdfFileName || "invoice.pdf");
            rev.uploadedPdfDocId = uploadedPdfDocId;
            clearToast();
          }
        } catch (e) {
          clearToast();
          showToastError(e instanceof Error ? e.message : "取込PDFの保存に失敗しました");
          return;
        }
      }
      // 登録フラグを立ててボタンを無効化・進捗表示開始
      rev.registering = true;
      const totalRows = rev.rows.filter((r) => r.importCaptionKind !== "consumption_tax").length;
      // showToastPersistent が render() を呼ぶので、ここで別途 render() は不要
      showToastPersistent(`登録中… 0 / ${totalRows}件`);
      // お名前ドットコムはカード払いの自動決済で取込時点の支払いが確定している取込元
      const isCardAutoSettledImport = rev.csvSourceKey === "onamae";
      // 発注ステータス（全行共通）。プレビューで選んだ値、未選択なら請求書受取は「発注済」・他は未設定
      const importOrderStatus = costImportEffectiveOrderStatus(rev);
      let ok = 0;
      let failMsg = "";
      const learnedMappings = [];
      // 登録する行を先に組み立て、1回の一括APIで送る（129行を129回POSTすると認証・ロック確認の
      // 往復だけで数十秒かかる）。行ごとの検証・登録規則はサーバ側で単発POSTと同じものを通す。
      const batch = [];
      // 送信前に弾いた行（金額不足・一覧にない案件）。登録されなかった行として失敗行と同じくプレビューに残す
      const skippedRows = new Set();
      for (const row of rev.rows) {
        if (row.importCaptionKind === "consumption_tax") continue;
        const pick = String(row.projectIdPick ?? "").trim();
        if (
          pick &&
          !isNoProjectBucketUiId(pick) &&
          !projects.some((x) => x.id === pick)
        ) {
          failMsg = "一覧にない案件が選ばれた行があります";
          skippedRows.add(row);
          continue;
        }
        const proj =
          pick === COST_SALES_EXPENSE_PROJECT_UI_ID
            ? salesExpenseCostProjectProxy()
            : pick === COST_ADJUSTMENT_PROJECT_UI_ID
              ? adjustmentCostProjectProxy()
              : pick && !isNoProjectBucketUiId(pick)
                ? projects.find((x) => x.id === pick)
                : unassignedCostProjectProxy();
        const ex = Math.round(Number(row.amountExTax ?? row.amount ?? 0)) || 0;
        const tx = Math.round(Number(row.consumptionTax ?? 0)) || 0;
        if (ex === 0 && tx === 0) { // 値引き（負数）の行は登録する
          failMsg = "金額が不足した行があります";
          skippedRows.add(row);
          continue;
        }
        // 仮原価との突合（案件＋支払先＋金額）。金額相違で「スキップ」選択なら登録しない
        const provMatch = costImportRowProvisionalMatch(row);
        if (provMatch.status === "mismatch" && String(row.provMismatchAction || "register") === "skip") {
          continue;
        }
        // 発注申請中の原価がある行は既定で登録しない（原価照合で突き合わせる。承認前発注・15z-cost-order-applications.js）
        if (provMatch.status === "order-pending" && String(row.provMismatchAction || "skip") === "skip") {
          continue;
        }
        const shouldReplaceProv =
          (provMatch.status === "match" && row.provReplace !== false) ||
          (provMatch.status === "mismatch" && String(row.provMismatchAction || "register") === "replace");
        const vendorStr = (() => {
          // CSV取込は媒体ごとに支払先を固定値に設定（generic・PDF除く）
          if (rev.previewKind !== "pdf" && rev.csvSourceKey && rev.csvSourceKey !== "generic") {
            const fixed = COST_IMPORT_CSV_SOURCE_META[rev.csvSourceKey]?.vendor;
            if (fixed) return fixed;
          }
          return String(row.vendorResolvedLabel || row.clientHint || "").trim() || "（支払先不明）";
        })();
        const noteExtra = row.vendorMasterUnmatched
          ? `取込原文: ${String(row.clientHint || "").slice(0, 200)}`
          : "";
        const effTargetMonth = rowEffectiveTargetMonth(row, rev.targetMonth);
        const summaryLine = costImportSummaryTextForPersist(row, effTargetMonth);
        // 発注名（note）= 発注名欄＋自動の追記（ドメイン管理・換算根拠）。備考欄は memo（原価フォームの「備考」と同じ）
        const autoNote = String(row.autoNote ?? "").trim();
        const parts = [];
        if (summaryLine) parts.push(summaryLine);
        if (autoNote && autoNote !== summaryLine) parts.push(autoNote);
        if (noteExtra) parts.push(noteExtra);
        const noteCombined = parts.join("\n");
        const rowPdfDocId =
          sourceDocIdByIndex && row.pdfSourceIndex != null
            ? sourceDocIdByIndex[row.pdfSourceIndex]
            : uploadedPdfDocId;
        // PDF取込（＋原価を登録・請求書受取とも）は、AIが読んだ請求書No（PDFごと）も一緒に保存する（読めなければ空）
        const rowInvoiceNo =
          rev.previewKind === "pdf"
            ? String((row.pdfSourceIndex != null ? rev.pdfSources?.[row.pdfSourceIndex]?.invoiceNo : rev.importInvoiceNo) || "").trim()
            : "";
        batch.push({
          row,
          // 合致（または金額相違で「確定して置換」）なら、登録後に突合した仮原価を消す（実で置換）
          replaceProvCostId: shouldReplaceProv && provMatch.provCostId ? provMatch.provCostId : null,
          project: proj,
          patch: {
            category: importCategory,
            vendor: vendorStr,
            amount: ex,
            consumptionTax: tx,
            targetMonth: effTargetMonth,
            // お名前ドットコムはカード払いの自動決済なので、取込月の月末が支払期限で支払済み。
            // 請求書受取（受信メール）からの取込は請求書が届いている＝発注済・受領済で入れる。
            // 他の取込元も経理の登録は請求書を受け取っていることが多いので受領済（営業の取込は未発注のまま）。
            payStatus: isCardAutoSettledImport
              ? "支払済み"
              : rev.inboxItemId
                ? "支払待ち"
                : costRegisterDefaultStatuses(rev.salesMode === true).payStatus,
            ...(importOrderStatus ? { orderStatus: importOrderStatus } : {}),
            // パートナーマスタの支払条件・支払方法・源泉徴収区分から補完（単票フォームと同じ規則・登録後に手修正可）
            dueDate: isCardAutoSettledImport
              ? costTargetMonthEndYmd(effTargetMonth)
              : CATEGORY_META[importCategory]?.showDueDate !== false
                ? costAutoDueDateFromVendorTerms(vendorStr, "")
                : "",
            paymentMethod: isCardAutoSettledImport ? "カード" : costAutoPaymentMethodFromVendor(vendorStr),
            withholdingTax: costAutoWithholdingFromVendor(vendorStr, ex) || 0,
            note: noteCombined,
            memo: String(row.reviewNote ?? "").trim(),
            ...(rowPdfDocId ? { sourcePdfDocId: rowPdfDocId } : {}),
            ...(rowInvoiceNo ? { invoiceNo: rowInvoiceNo } : {})
          }
        });
      }
      // 冪等キー: runId はウィザード1回につき1つ（結果不明の行を再登録しても同じ）、行キーはプレビュー行の識別子
      if (!rev.importRunId) rev.importRunId = newClientUuid();
      for (const en of batch) {
        en.patch.importRunId = rev.importRunId;
        en.patch.importRowKey = String(en.row.key);
      }
      // 送信前にプレビューを控える（PDF保存後なので保存済みIDも入る）。途中でリロードしても同じキーで続きから再登録できる
      costImportRunStateSave(rev);
      let results = [];
      try {
        // 進捗は DOM 直更新（render() を通すとプレビュー全体を作り直して極端に遅い）
        results = await persistCostsToServerCreateBulk(batch, (done, total) =>
          showToastProgress(`登録中… ${done} / ${total}件`)
        );
      } catch (e) {
        failMsg = e instanceof Error ? e.message : "保存に失敗しました";
      }
      const registeredRows = new Set();
      let unknownCount = 0;
      let duplicateCount = 0;
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        const { row, project, replaceProvCostId } = batch[i];
        if (!r?.ok) {
          if (r?.unknown) unknownCount += 1;
          else failMsg = r?.error || "保存に失敗しました";
          continue;
        }
        ok += 1;
        registeredRows.add(row);
        if (r.duplicate) duplicateCount += 1;
        // 媒体原価の紐づけを記憶（アカウント名→実在案件のみ。バケットは dbId が無い）
        if (row.accountNameKey && project?.dbId) {
          learnedMappings.push({
            sourceKey: rev.csvSourceKey || "generic",
            accountNameKey: String(row.accountNameKey),
            projectId: Number(project.dbId)
          });
        }
        if (replaceProvCostId) {
          try {
            await persistCostToServerDelete(replaceProvCostId);
          } catch (_) {
            /* 置換の失敗は致命的でない（仮が残るだけ） */
          }
        }
      }
      persistAndCacheCostAccountMappings(learnedMappings);
      clearToast();
      // 再登録で「前回の送信で登録済み」と返った行があれば、今回の編集は反映されていないことを伝える
      const dupNote = duplicateCount ? `（うち${duplicateCount}件は前回の送信で登録済みでした。今回の変更内容は反映されていません）` : "";
      // 応答を受け取れなかった行がある、または1件も登録できなかった（取込準備の失敗など）ときは
      // プレビューを閉じずに残す（登録済みの行だけ除く）。結果不明の行は同じ runId・行キーで再登録できるので、
      // サーバに先に入っていても二重登録にならない。確定した失敗は直してから再登録するか閉じてもらう
      // 登録されなかった行（結果不明・確定失敗・送信前に弾いた行）が1件でもあれば、ウィザードを閉じずに
      // その行だけ残し、同じ runId・行キーのまま控える。閉じてファイル全体を取り込み直すと新しい runId になり、
      // 登録済みの行まで二重登録できてしまうため（部分成功でも失敗行だけを同じキーで再登録させる）
      const unregisteredCount = batch.length - registeredRows.size + skippedRows.size;
      if (unregisteredCount > 0) {
        rev.rows = rev.rows.filter((r) => !registeredRows.has(r));
        rev.registering = false;
        costImportRunStateSave(rev);
        const failNote = failMsg ? `　他に失敗: ${failMsg}` : "";
        const okNote = ok ? `${ok}件を登録しました${dupNote}。` : "";
        showToast(
          unknownCount
            ? `${okNote}${unknownCount}件は${COST_BULK_CREATE_TRANSPORT_ERROR_MSG}${failNote}`
            : `${okNote}${unregisteredCount}件は登録できませんでした（${failMsg || "保存に失敗しました"}）。行はプレビューに残しています。直してからもう一度登録するか、閉じてください`
        );
        render();
        return;
      }
      costImportRunStateClear();
      uiState.costRegWizard = null;
      // 請求書メール受信箱から来た取込は、全行登録できたら受信箱の行を「登録済」にする（15r-invoice-mail-inbox.js）
      if (rev.inboxItemId && ok && !failMsg) void markInvoiceMailInboxItem(rev.inboxItemId, "done", { silent: true });
      if (ok && !failMsg) {
        showToast(`${ok}件の原価を登録しました${dupNote}`);
      } else if (ok) {
        showToast(`${ok}件を登録しました${dupNote}（${failMsg}）`);
      } else {
        showToast(failMsg || "登録できませんでした");
      }
      render();
    })();
    return true;
  }
  return false;
}
// 画面のクリック処理（16-render-routing.js の document の click ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxxClick(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能のクリック処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の click から移した分岐（元の 8185〜8938 行）。処理したら true（16 側はそこで止める） */
function handleCostEditClick(e, target) {

  const costSourcePdfDlEl = target.closest("[data-cost-download-source-pdf]");
  if (costSourcePdfDlEl instanceof HTMLElement) {
    const rawId = costSourcePdfDlEl.getAttribute("data-cost-download-source-pdf");
    if (rawId) void downloadCostSourcePdfDocument(rawId);
    return true;
  }

  // ── 原価: 案件詳細から「仮原価を追加」（この案件に仮原価フラグ付きで新規） ──
  // 原価割当（営業）の「＋ 仮原価を追加」も同じフォーム（案件を決めずに開き、フォームで計上先の案件を選ぶ。
  // 2026-10 ユーザー決定: 営業が原価を入れる形をそろえる）
  const addProvEl = target.closest("[data-add-provisional-cost]");
  if (addProvEl instanceof HTMLElement) {
    const pid = addProvEl.dataset.addProvisionalCost || "";
    if (pid && !projects.some((x) => x.id === pid)) return true;
    uiState.costForm = {
      projectId: pid,
      requireProject: !pid,
      editingId: null,
      category: defaultCostCategoryForProject(projects.find((x) => x.id === pid)),
      vendor: "",
      amount: 0,
      consumptionTax: 0,
      targetMonth: currentMonth(),
      dueDate: "",
      paidDate: "",
      invoiceDate: "",
      paymentMethod: "",
      invoiceNo: "",
      withholdingTax: 0,
      orderStatus: "",
      isProvisional: true,
      provisionalContext: true,
      payStatus: "未登録",
      note: "",
      sourcePdfDocId: null
    };
    render();
    return true;
  }

  // ── 原価: 編集フォームを開く ──
  const editCostEl = target.closest("[data-edit-cost]");
  if (editCostEl instanceof HTMLElement) {
    const [, costId] = editCostEl.dataset.editCost.split(":");
    const placement = findCostPlacementByUiId(costId);
    if (!placement) return true;
    uiState.costForm = costEditFormFromPlacement(placement, costId);
    render();
    return true;
  }

  // ── 原価: 保存（新規 or 更新）──
  const saveCostEl = target.closest("[data-save-cost]");
  if (saveCostEl instanceof HTMLElement && uiState.costForm) {
    const f = uiState.costForm;
    // 二重送信防止（連打で同じ原価が二重登録されるのを防ぐ。経費保存と同じ _submitting パターン）
    if (f._submitting) return true;
    f._submitting = true;
    render();
    void (async () => {
    try {
    // 原価箱（案件未定）ピックは保存直前に実案件へ解決する（無ければ作成。既存なら再利用）
    const boxOwnerUiId = costBoxPickOwnerUiId(f.projectId);
    if (boxOwnerUiId) {
      const boxResult = await ensureCostBoxProjectUiId(boxOwnerUiId, costBoxFiscalYearStartYear());
      if (!boxResult.ok) {
        showToast(boxResult.error, { variant: "error" });
        return;
      }
      f.projectId = boxResult.uiId;
    }
    const assignPid = f.projectId && f.projectId !== "" ? f.projectId : COST_UNASSIGNED_PROJECT_UI_ID;
    const projForCreate =
      assignPid === COST_UNASSIGNED_PROJECT_UI_ID
        ? unassignedCostProjectProxy()
        : assignPid === COST_SALES_EXPENSE_PROJECT_UI_ID
          ? salesExpenseCostProjectProxy()
          : assignPid === COST_ADJUSTMENT_PROJECT_UI_ID
            ? adjustmentCostProjectProxy()
            : projects.find((x) => x.id === assignPid);
    const prevPlForSaveMsg = f.editingId ? findCostPlacementByUiId(f.editingId) : null;
    const prevAssignPidForSaveMsg = prevPlForSaveMsg ? costAssignmentUiIdFromPlacement(prevPlForSaveMsg) : null;
    // 必須項目バリデーション（支払先・金額・計上月）
    if (!String(f.vendor || "").trim()) {
      showToast("発注先を入力してください", { variant: "error" });
      return;
    }
    const amountCheck = Number(String(f.amount).replace(/,/g, ""));
    if (!Number.isFinite(amountCheck) || amountCheck === 0) {
      showToast("金額（税抜）を入力してください", { variant: "error" });
      return;
    }
    if (!String(f.targetMonth || "").trim()) {
      showToast("計上月を入力してください", { variant: "error" });
      return;
    }
    // 原価割当（営業）から開いた「仮原価を追加」は計上先の案件が必須
    if (f.requireProject && !String(f.projectId || "").trim()) {
      showToast("計上先の案件を選んでください", { variant: "error" });
      return;
    }
    // 発注申請（制作費・開発費・経理以外の新規）: 取引先マスタ・申請理由・インボイス（15z-cost-order-applications.js）
    const orderApplyErr = costOrderApplyFormError(f);
    if (orderApplyErr) {
      showToast(orderApplyErr, { variant: "error" });
      return;
    }
    // 経理の新規登録で、同じ案件・発注先に発注申請中の原価がある＝承認前に発注して請求書が来た（二重登録の防止）
    if (!f.editingId && !costFormNeedsOrderApply(f) && !confirmCostOrderPendingDuplicate(f.projectId, f.vendor)) return;
    // 新規登録で、計上月・発注先・税抜金額が同じ原価がすでにある（案件は問わない）＝二重計上の疑い
    if (!f.editingId && !confirmExistingCostDuplicates(findExistingDuplicateCosts(normalizeCostTargetMonthForSave(f.targetMonth), f.vendor, amountCheck))) return;
    if (
      !isNoProjectBucketUiId(assignPid) &&
      !projForCreate
    ) {
      showToast("案件が見つかりません");
      return;
    }
    const amount = Number(String(f.amount).replace(/,/g, "")) || 0;
    const consumptionTax = Math.round(Number(String(f.consumptionTax ?? 0).replace(/,/g, ""))) || 0;
    const patch = {
      category: f.category,
      vendor: f.vendor,
      amount,
      consumptionTax,
      targetMonth: normalizeCostTargetMonthForSave(f.targetMonth),
      dueDate: f.dueDate || "",
      paidDate: f.paidDate || "",
      invoiceDate: f.invoiceDate || "",
      paymentMethod: f.paymentMethod || "",
      invoiceNo: f.invoiceNo || "",
      // 仮原価は源泉徴収区分を選ぶので、源泉所得税は区分と金額から計算する
      withholdingTax: f.isProvisional && f.withholdingKind
        ? costWithholdingYenForKind(f.withholdingKind, amount) || 0
        : Math.round(Number(String(f.withholdingTax ?? 0).replace(/,/g, ""))) || 0,
      orderStatus: f.orderStatus || "",
      // 発注申請は承認まで仮原価（最終承認で実原価になる）
      isProvisional: f.isProvisional === true || costFormNeedsOrderApply(f),
      payStatus: f.payStatus || (f.editingId && findCostPlacementByUiId(f.editingId)?.cost?.payStatus) || "未登録",
      note: f.note || "",
      memo: f.memo || "",
      orderApply: costOrderApplyFromForm(f)
    };
    // 差し戻し解消のローカル同期用: 保存前の値を控える（サーバ clearRemand の「実質的変更」判定と対）
    const preRemandCost = (() => {
      if (!f.editingId) return null;
      const pl0 = findCostPlacementByUiId(f.editingId);
      return pl0?.cost?.remandNote ? { ...pl0.cost } : null;
    })();
    await (async () => {
      try {
        if (f.editingId) {
          await persistCostToServerUpdate(f.editingId, patch, assignPid);
          // 差し戻し中の原価の自動解消（サーバ側 clearRemand と同条件でローカルも同期）:
          //  - 営業の「修正する」（remandFix）＝保存自体が対応の意思表示なので変更が無くても解消
          //  - 経理/マスターは金額系（note以外）が実際に変わったときだけ解消
          //    （摘要だけの編集・無変更保存ではサーバも解消しないため、ここで消すと表示がズレる）
          const savedPl = findCostPlacementByUiId(f.editingId);
          const clientIsAccounting =
            isAccountingDepartmentJobRole(currentUser.role) || isMasterOrAdminUser(currentUser);
          const substantiveChange =
            preRemandCost != null &&
            (String(patch.category || "") !== String(preRemandCost.category || "") ||
              String(patch.vendor || "").trim() !== String(preRemandCost.vendor || "").trim() ||
              (Math.round(Number(patch.amount || 0)) || 0) !== (Math.round(Number(preRemandCost.amount || 0)) || 0) ||
              (Math.round(Number(patch.consumptionTax || 0)) || 0) !== (Math.round(Number(preRemandCost.consumptionTax || 0)) || 0) ||
              String(patch.targetMonth || "").replace(/-/g, "/") !== String(preRemandCost.targetMonth || "").replace(/-/g, "/") ||
              String(patch.payStatus || "") !== String(preRemandCost.payStatus || "") ||
              String(patch.dueDate || "").slice(0, 10) !== String(preRemandCost.dueDate || "").slice(0, 10) ||
              String(patch.paidDate || "").slice(0, 10) !== String(preRemandCost.paidDate || "").slice(0, 10) ||
              String(patch.invoiceDate || "").slice(0, 10) !== String(preRemandCost.invoiceDate || "").slice(0, 10) ||
              (patch.isProvisional === true) !== (preRemandCost.isProvisional === true) ||
              String(patch.paymentMethod || "") !== String(preRemandCost.paymentMethod || "") ||
              String(patch.invoiceNo || "") !== String(preRemandCost.invoiceNo || "") ||
              (Math.round(Number(patch.withholdingTax || 0)) || 0) !== (Math.round(Number(preRemandCost.withholdingTax || 0)) || 0));
          let remandCleared = false;
          if (
            savedPl?.cost?.remandNote &&
            (f.remandFix === true || (clientIsAccounting && substantiveChange))
          ) {
            delete savedPl.cost.remandNote;
            delete savedPl.cost.remandedByDbId;
            delete savedPl.cost.remandedAt;
            remandCleared = true;
          }
          const assignedToProject = !isNoProjectBucketUiId(assignPid);
          const wasUnassignedBucket = isNoProjectBucketUiId(prevAssignPidForSaveMsg);
          if (remandCleared) {
            showToast("修正を保存し、差し戻しを解消しました");
          } else if (
            currentRoute().page === "cost-allocation" &&
            assignedToProject &&
            wasUnassignedBucket
          ) {
            showToast("担当に割り当てました。");
          } else {
            showToast("原価を更新しました");
          }
        } else {
          await persistCostToServerCreate(projForCreate, patch);
          showToast(patch.orderApply ? "発注申請を出しました。承認されるまで外注先への発注は待ってください" : "原価を追加しました");
          if (patch.orderApply) void fetchCostOrderApplications();
        }
        uiState.costForm = null;
        render();
      } catch (e) {
        showToastError(e instanceof Error ? e.message : "原価の保存に失敗しました");
      }
    })();
    } finally {
      // 失敗・バリデーションで戻った時はフォームを残したまま再度押せるようにする
      // （保存中にフォーム state が差し替えられていても固まらないよう、現行の costForm も解除する）
      f._submitting = false;
      if (uiState.costForm) {
        uiState.costForm._submitting = false;
        render();
      }
    }
    })();
    return true;
  }

  // ── 原価: フォームを閉じる ──
  const closeCostFormEl = target.closest("[data-close-cost-form]");
  if (closeCostFormEl instanceof HTMLElement) {
    if (!fpConfirmDiscardDirtyForm("costForm")) return true;
    uiState.costForm = null;
    render();
    return true;
  }

  // ── 原価: 削除 ──
  const deleteCostEl = target.closest("[data-delete-cost]");
  if (deleteCostEl instanceof HTMLElement) {
    const delKey = deleteCostEl.dataset.deleteCost || "";
    const [, costId] = delKey.split(":");
    void runExclusive(`cost:delete:${costId}`, async () => {
      if (!(await fpConfirm({ title: "この原価を削除しますか？", note: "削除した原価は元に戻せません。", confirmLabel: "削除する" }))) return;
      try {
        await persistCostToServerDelete(costId);
        if (delKey && uiState.costMgmtSelected) delete uiState.costMgmtSelected[delKey];
        showToast("原価を削除しました");
        render();
      } catch (e) {
        showToastError(e instanceof Error ? e.message : "原価の削除に失敗しました");
      }
    }, deleteCostEl);
    return true;
  }

  // ③ 予想案件（記憶由来）を1クリックで割り当て
  const applySuggestEl = target.closest("[data-cost-apply-suggested]");
  if (applySuggestEl instanceof HTMLElement) {
    const parts = String(applySuggestEl.dataset.costApplySuggested || "").split(":");
    if (parts.length < 3) return true;
    const suggestedUiId = parts[parts.length - 1];
    const costIdUi = parts[parts.length - 2];
    const pl = findCostPlacementByUiId(costIdUi);
    if (!pl?.cost || !suggestedUiId) return true;
    const c = pl.cost;
    const patch = {
      category: c.category,
      vendor: c.vendor,
      amount: c.amount,
      consumptionTax: costConsumptionTaxYen(c),
      targetMonth: c.targetMonth,
      dueDate: c.dueDate || "",
      paidDate: c.paidDate || "",
      payStatus: c.payStatus,
      note: c.note || ""
    };
    void (async () => {
      try {
        await persistCostToServerUpdate(costIdUi, patch, suggestedUiId);
        await loadDataFromDB({ mode: "heavy", skipLoadingUi: true });
        showToast("候補の案件に割り当てました");
        render();
      } catch (e) {
        showToastError(e instanceof Error ? e.message : "割り当てに失敗しました");
      }
    })();
    return true;
  }

  const costMgmtOpenBulkModalEl = target.closest("[data-cost-mgmt-open-bulk-modal]");
  if (costMgmtOpenBulkModalEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    const kind = String(costMgmtOpenBulkModalEl.dataset.costMgmtOpenBulkModal || "").trim();
    if (["category", "delegate", "payStatus"].includes(kind)) {
      pruneCostMgmtSelectionToValid();
      const sel = uiState.costMgmtSelected || {};
      const keys = Object.keys(sel).filter((k) => sel[k]);
      if (!keys.length) {
        showToast("行を選択してください");
        return true;
      }
      if (kind === "delegate" && !userCanAccessPage("cost-management")) return true;
      if (kind === "payStatus") {
        uiState.costMgmtBulkPayStatusPick = "received";
        uiState.costMgmtBulkInvoiceNo = null; // 1件受領の請求書No（入力中の値。null=未入力＝既存値を表示）
      }
      if (kind === "payStatus" && !uiState.costMgmtBulkPayDate) {
        const t = new Date();
        uiState.costMgmtBulkPayDate = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(
          t.getDate()
        ).padStart(2, "0")}`;
      }
      uiState.costMgmtBulkModal = kind;
      render();
      return true;
    }
  }

  const costMgmtBulkModalCloseEl = target.closest("[data-cost-mgmt-bulk-modal-close]");
  if (costMgmtBulkModalCloseEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    const closeByOverlay = costMgmtBulkModalCloseEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cost-mgmt-bulk-modal");
    /* オーバーレイは子の .cost-mgmt-bulk-modal より上に data-close があるため、モーダル内クリックでも closest がオーバーレイに当たる。
       その場合は閉じないだけにし、依頼する／種別を反映など下位のハンドラへ進める。 */
    if (!(closeByOverlay && clickedInsideModal)) {
      uiState.costMgmtBulkModal = null;
      render();
      return true;
    }
  }

  const bulkCostCatEl = target.closest("[data-cost-mgmt-bulk-apply-category]");
  if (bulkCostCatEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    pruneCostMgmtSelectionToValid();
    const sel = uiState.costMgmtSelected || {};
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) {
      showToast("行を選択してください");
      return true;
    }
    const modalRoot = bulkCostCatEl.closest(".cost-mgmt-bulk-modal");
    const pick = modalRoot?.querySelector("select[data-cost-mgmt-bulk-category-pick]");
    const nextCat =
      pick instanceof HTMLSelectElement && COST_CATEGORIES.includes(pick.value) ? pick.value : "";
    if (!nextCat) {
      showToast("種別を選んでください");
      return true;
    }
    void (async () => {
      let ok = 0;
      let fail = 0;
      for (const rowKey of keys) {
        const colon = rowKey.lastIndexOf(":");
        const costIdUi = colon >= 0 ? rowKey.slice(colon + 1) : "";
        if (!costIdUi) continue;
        const pl = findCostPlacementByUiId(costIdUi);
        if (!pl?.cost) {
          fail++;
          continue;
        }
        const c = pl.cost;
        if (String(c.category || "") === nextCat) {
          ok++;
          continue;
        }
        try {
          await persistCostToServerUpdate(
            c.id,
            {
              category: nextCat,
              vendor: c.vendor,
              amount: Number(c.amount || 0),
              consumptionTax: costConsumptionTaxYen(c),
              targetMonth: c.targetMonth,
              payStatus: c.payStatus,
              dueDate: c.dueDate || "",
              note: c.note || ""
            },
            "keep"
          );
          ok++;
        } catch {
          fail++;
        }
      }
      uiState.costMgmtSelected = {};
      uiState.costMgmtBulkModal = null;
      showToast(fail ? `種別を更新しました（成功 ${ok} 件・失敗 ${fail} 件）` : `種別を更新しました（${ok} 件）`);
      render();
    })();
    return true;
  }

  const bulkCostDelEl = target.closest("[data-cost-mgmt-bulk-delete-bar]");
  if (bulkCostDelEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    pruneCostMgmtSelectionToValid();
    const sel = uiState.costMgmtSelected || {};
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) {
      showToast("行を選択してください");
      return true;
    }
    void runExclusive("cost:bulk-delete", async () => {
      if (!(await fpConfirm({ title: `原価 ${keys.length} 件を削除しますか？`, note: "削除した原価は元に戻せません。", confirmLabel: `${keys.length} 件を削除する` }))) return;
      let ok = 0;
      let fail = 0;
      for (const rowKey of keys) {
        const colon = rowKey.lastIndexOf(":");
        const costIdUi = colon >= 0 ? rowKey.slice(colon + 1) : "";
        if (!costIdUi) continue;
        try {
          await persistCostToServerDelete(costIdUi);
          if (uiState.costMgmtSelected) delete uiState.costMgmtSelected[rowKey];
          ok++;
        } catch {
          fail++;
        }
      }
      uiState.costMgmtSelected = {};
      uiState.costMgmtBulkModal = null;
      showToast(fail ? `削除しました（成功 ${ok} 件・失敗 ${fail} 件）` : `削除しました（${ok} 件）`);
      render();
    });
    return true;
  }

  const bulkCostDelegEl = target.closest("[data-cost-mgmt-bulk-delegate]");
  if (bulkCostDelegEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    pruneCostMgmtSelectionToValid();
    const sel = uiState.costMgmtSelected || {};
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) {
      showToast("行を選択してください");
      return true;
    }
    const modalRoot = bulkCostDelegEl.closest(".cost-mgmt-bulk-modal");
    const userPick = modalRoot?.querySelector("select[data-cost-mgmt-delegate-user-pick]");
    const uidRaw = userPick instanceof HTMLSelectElement ? String(userPick.value || "").trim() : "";
    const uidM = uidRaw.match(/^u(\d+)$/i);
    const delegatedDb = uidM ? Number(uidM[1]) : null;
    if (!delegatedDb) {
      showToast("依頼先を選んでください");
      return true;
    }
    const costDbIds = [];
    for (const rowKey of keys) {
      const colon = rowKey.lastIndexOf(":");
      const costIdUi = colon >= 0 ? rowKey.slice(colon + 1) : "";
      if (!costIdUi) continue;
      const pl = findCostPlacementByUiId(costIdUi);
      if (!pl?.cost || !pl.orphan || !isCostRowProjectUnassigned({ ...pl.cost, _projectId: COST_UNASSIGNED_PROJECT_UI_ID }))
        continue;
      const n = costDbNumericId({ id: costIdUi });
      if (n != null) costDbIds.push(n);
    }
    if (!costDbIds.length) {
      showToast("案件未割当の原価のみ依頼できます");
      return true;
    }
    void (async () => {
      try {
        await persistCostDelegationsToServer(costDbIds, delegatedDb);
        await loadDataFromDB({ mode: "heavy", skipLoadingUi: true });
        uiState.costMgmtSelected = {};
        uiState.costMgmtBulkModal = null;
        // 依頼した行が見えるよう「未割当のみ」に切り替える（割当依頼中も未割当扱い）
        uiState.costMgmtTabKeys = ["all"];
        uiState.costMgmtFilter = { ...(uiState.costMgmtFilter || emptyCostMgmtFilter()), costKind: "unassigned" };
        showToast(
          costDbIds.length > 1
            ? `担当に割り当てました（${costDbIds.length}件）。`
            : "担当に割り当てました。"
        );
        render();
      } catch (e) {
        showToastError(e instanceof Error ? e.message : "依頼に失敗しました");
      }
    })();
    return true;
  }

  const bulkCostDelegClearEl = target.closest("[data-cost-mgmt-bulk-delegate-clear]");
  if (bulkCostDelegClearEl instanceof HTMLElement && currentRoute().page === "cost-management") {
    pruneCostMgmtSelectionToValid();
    const sel = uiState.costMgmtSelected || {};
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) {
      showToast("行を選択してください");
      return true;
    }
    const costDbIds = [];
    for (const rowKey of keys) {
      const colon = rowKey.lastIndexOf(":");
      const costIdUi = colon >= 0 ? rowKey.slice(colon + 1) : "";
      if (!costIdUi) continue;
      const pl = findCostPlacementByUiId(costIdUi);
      const c = pl?.cost;
      if (!c || !pl.orphan || !isCostRowProjectUnassigned({ ...c, _projectId: COST_UNASSIGNED_PROJECT_UI_ID })) continue;
      if (c.delegatedSalesUserDbId == null) continue;
      const n = costDbNumericId({ id: costIdUi });
      if (n != null) costDbIds.push(n);
    }
    if (!costDbIds.length) {
      showToast("依頼が付いている案件未割当の行を選んでください");
      return true;
    }
    void (async () => {
      try {
        await persistCostDelegationsToServer(costDbIds, null);
        await loadDataFromDB({ mode: "heavy", skipLoadingUi: true });
        uiState.costMgmtSelected = {};
        uiState.costMgmtBulkModal = null;
        showToast(`依頼を解除しました（${costDbIds.length} 件）`);
        render();
      } catch (e) {
        showToastError(e instanceof Error ? e.message : "解除に失敗しました");
      }
    })();
    return true;
  }

  const costAllocBulkAssignEl = target.closest("[data-cost-alloc-bulk-assign]");
  // 原価管理→未割当ビュー（15d）にも同じセクションが描画されるため、両ページで受ける
  if (costAllocBulkAssignEl instanceof HTMLElement && COST_ALLOC_GROUP_PAGES.has(currentRoute().page)) {
    // 実行中の再押下で同じ行へ二重に PUT しない（グループ割当の remGroupActionBusy と同じ）
    if (uiState.costAllocBulkAssignBusy) return true;
    pruneCostAllocSelectionToValid();
    const sel = uiState.costAllocSelected || {};
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) {
      showToast("行を選択してください");
      return true;
    }
    const pickEl = document.querySelector("select[data-cost-alloc-bulk-project-pick]");
    const assignPid =
      pickEl instanceof HTMLSelectElement ? String(pickEl.value || "").trim() : "";
    // 販管費・調整用への一括振り分けは許可する（個別編集と同じ選択肢）。
    // 「未割当」自体への割当は無意味なので引き続き弾く
    if (!assignPid || assignPid === COST_UNASSIGNED_PROJECT_UI_ID) {
      showToast("割当先を選んでください");
      return true;
    }
    if (!isNoProjectBucketUiId(assignPid) && !projects.some((p) => p.id === assignPid)) {
      showToast("案件が見つかりません");
      return true;
    }
    // 「すべて選択」は表示中（絞り込み後）の未割当行を丸ごと選ぶため、母数が多いと気づかず
    // 大量の原価が意図しない案件へ一括で付け替わる事故になる（2026-09-08に533件誤割当）。
    // 送信直前に件数と割当先名を明示して確認を挟む
    const destLabel = pickEl instanceof HTMLSelectElement ? String(pickEl.selectedOptions?.[0]?.text || assignPid).trim() : assignPid;
    if (
      !window.confirm(
        `選択中の${keys.length}件を「${destLabel}」へまとめて割り当てます。よろしいですか？\n（「すべて選択」は現在の絞り込み条件で表示されている未割当行が対象です）`
      )
    ) {
      return true;
    }
    uiState.costAllocBulkAssignBusy = true;
    render();
    void (async () => {
      // 送信フィールドの組み立ては assignOrphanCostsToProject（11-cost-form.js）に一本化
      const costIdUis = keys
        .map((rowKey) => {
          const colon = rowKey.lastIndexOf(":");
          return colon >= 0 ? rowKey.slice(colon + 1) : "";
        })
        .filter(Boolean);
      let ok = 0;
      let fail = 0;
      try {
        ({ ok, fail } = await assignOrphanCostsToProject(costIdUis, assignPid));
      } finally {
        uiState.costAllocBulkAssignBusy = false;
      }
      uiState.costAllocSelected = {};
      pruneCostAllocSelectionToValid();
      if (fail && ok) showToastError(`まとめて割当: 成功 ${ok} 件・失敗 ${fail} 件`);
      else if (fail) showToastError(`まとめて割当に失敗しました（${fail} 件）`);
      else showToast(ok > 1 ? `担当に割り当てました（${ok}件）。` : "担当に割り当てました。");
      render();
    })();
    return true;
  }

  // ── 原価: ステータスをワンクリック変更（要対応エリアのボタンから） ──
  const costStatusEl = target.closest("[data-cost-status]");
  if (costStatusEl instanceof HTMLElement) {
    const [projectId, costId, newStatus] = costStatusEl.dataset.costStatus.split(":");
    const proj = projects.find((x) => x.id === projectId);
    const cost =
      proj?.costs?.find((c) => c.id === costId) ||
      (projectId === COST_UNASSIGNED_PROJECT_UI_ID ? ensureOrphanCosts().find((c) => c.id === costId) : null);
    if (!cost || (projectId !== COST_UNASSIGNED_PROJECT_UI_ID && !proj)) return true;
    const prevPayStatus = cost.payStatus; // ロールバック用に保存
    cost.payStatus = newStatus;
    const statusDoneMsg = `「${escapeHtml(cost.vendor || cost.category)}」を ${newStatus} に更新しました`;
    // 成功の知らせはサーバーへ保存できてから出す（送る前に出すと、失敗しても「更新しました」が一度見える）
    const statusSavesToServer = costDbNumericId(cost) != null && authToken;
    if (!statusSavesToServer) showToast(statusDoneMsg);
    if (statusSavesToServer) {
      void persistCostToServerUpdate(
        cost.id,
        {
          category: cost.category,
          vendor: cost.vendor,
          amount: Number(cost.amount || 0),
          consumptionTax: costConsumptionTaxYen(cost),
          targetMonth: cost.targetMonth,
          payStatus: newStatus,
          dueDate: cost.dueDate || "",
          note: cost.note || ""
        },
        "keep"
      ).then(() => showToast(statusDoneMsg)).catch(() => {
        // サーバー保存失敗: 楽観的更新を元に戻す
        cost.payStatus = prevPayStatus;
        showToastError("保存に失敗しました。状態を元に戻しました。");
        render();
      });
    }
    render();
    return true;
  }

  // ── 折りたたみセクション: トグル ──
  const collapseToggleEl = target.closest("[data-collapse-toggle]");
  if (collapseToggleEl instanceof HTMLElement) {
    const sid = collapseToggleEl.dataset.collapseToggle;
    uiState.collapsedSections[sid] = !isSectionCollapsed(sid);
    render();
    return true;
  }

  // 経営ダッシュボード・月次P/L の月選択はチップからプルダウンへ移行したため、
  // イベントは 13-management-dashboard.js の change ハンドラが受ける（ここには置かない）

  // ── 経費: 追加フォームを開く ──
  const addExpenseEl = target.closest("[data-add-expense]");
  if (addExpenseEl instanceof HTMLElement) {
    uiState.expenseForm = {
      editingId: null,
      month: addExpenseEl.dataset.addExpense,
      category: "人件費",
      label: "",
      amount: 0,
      note: ""
    };
    render();
    return true;
  }

  // ── 経費: 編集フォームを開く ──
  const editExpenseEl = target.closest("[data-edit-expense]");
  if (editExpenseEl instanceof HTMLElement) {
    const exp = expenses.find((e) => e.id === editExpenseEl.dataset.editExpense);
    if (!exp) return true;
    uiState.expenseForm = {
      editingId: exp.id,
      month: exp.month,
      category: exp.category,
      label: exp.label,
      amount: exp.amount,
      note: exp.note || ""
    };
    render();
    return true;
  }

  // ── 経費: フォームを閉じる ──
  const closeExpenseFormEl = target.closest("[data-close-expense-form]");
  if (closeExpenseFormEl instanceof HTMLElement) {
    uiState.expenseForm = null;
    render();
    return true;
  }

  // ── 経費: 保存（新規 or 更新）→ サーバーに保存 ──
  const saveExpenseEl = target.closest("[data-save-expense]");
  if (saveExpenseEl instanceof HTMLElement && uiState.expenseForm) {
    const f = uiState.expenseForm;
    // 二重送信防止（連打で経費が二重計上されるのを防ぐ）
    if (f._submitting) return true;
    // 全角数字・¥・桁区切りも読む（以前は NaN→0円で保存され成功トーストが出ていた）
    const amount = parseBillingNumber(f.amount);
    // マイナス（返金・訂正）は従来どおり登録できる。弾くのは数値でないものと0円だけ
    if (!Number.isFinite(amount) || amount === 0) {
      showToastError("金額を数値で入力してください（0円は登録できません）");
      return true;
    }
    f._submitting = true;
    render();
    const monthStr = (f.month || "").replace("-", "/").replace("/", "-");  // API は YYYY-MM 形式
    void (async () => {
      // 失敗時はフォームを閉じない（入力を失わせない）。成功時のみ閉じる
      let saved = false;
      if (f.editingId) {
        const exp = expenses.find((e) => e.id === f.editingId);
        const dbId = exp?.dbId;
        if (!dbId) {
          f._submitting = false;
          return;
        }
        const res = await apiFetch("/api/expenses", {
          method: "PUT",
          body: { id: dbId, month: monthStr, category: f.category, label: f.label, amount, note: f.note || "" }
        });
        if (res.ok) {
          const idx = expenses.findIndex((e) => e.id === f.editingId);
          if (idx >= 0) expenses[idx] = { ...expenses[idx], month: monthStr.replace("-", "/"), category: f.category, label: f.label, amount, note: f.note || "" };
          showToast("経費を更新しました");
          saved = true;
        } else {
          showToastError(res.status === 0 ? "通信エラーで経費を更新できませんでした" : (res.data?.error || "経費の更新に失敗しました"));
        }
      } else {
        const res = await apiFetch("/api/expenses", {
          method: "POST",
          body: { month: monthStr, category: f.category, label: f.label, amount, note: f.note || "" }
        });
        const e = res.data?.expense;
        if (res.ok && e) {
          expenses.push({ id: String(e.id), dbId: e.id, month: String(e.month || "").replace(/-/g, "/"), category: e.category || "", label: e.label || "", amount: Number(e.amount || 0), note: e.note || "" });
          showToast("経費を追加しました");
          saved = true;
        } else if (res.ok) {
          // 保存はされたが応答が読めない: 二重登録を避けるためフォームは閉じ、一覧はサーバから取り直す
          showToastError("経費の追加結果を確認できませんでした。一覧を再読み込みします");
          saved = true;
          loadDataFromDB().then(() => render()).catch(() => {});
        } else {
          showToastError(res.status === 0 ? "通信エラーで経費を追加できませんでした" : (res.data?.error || "経費の追加に失敗しました"));
        }
      }
      if (saved) uiState.expenseForm = null;
      else f._submitting = false;
      render();
    })();
    return true;
  }

  // ── 経費: 削除 → サーバーから削除 ──
  const deleteExpenseEl = target.closest("[data-delete-expense]");
  if (deleteExpenseEl instanceof HTMLElement) {
    const eid = deleteExpenseEl.dataset.deleteExpense;
    const exp = expenses.find((e) => e.id === eid);
    if (!exp?.dbId) return true;
    void runExclusive(`expense:delete:${eid}`, async () => {
      if (!(await fpConfirm({ title: "この経費を削除しますか？", note: "削除した経費は元に戻せません。", confirmLabel: "削除する" }))) return;
      const { ok, data } = await apiFetch("/api/expenses", { method: "DELETE", body: { id: exp.dbId } });
      if (ok) {
        const idx = expenses.findIndex((e) => e.id === eid);
        if (idx >= 0) expenses.splice(idx, 1);
        showToast("経費を削除しました");
      } else {
        showToastError(data.error || "削除に失敗しました");
      }
      render();
    }, deleteExpenseEl);
    return true;
  }
  return false;
}
// 画面の input 処理（16-render-routing.js の document の input ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxx(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能の input 処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の input から移した分岐（元の 617〜1072 行）。処理したら true（16 側はそこで止める） */
function handleProgressInput(e, target) {
  if (target.dataset.pmMentionTextarea === "1" && target instanceof HTMLTextAreaElement) {
    // メンションの色付けは IME 変換中も含めて常に同期（背景ハイライトなので変換を邪魔しない）
    pmSyncMentionBackdrop(target);
  }
  if (
    target.dataset.pmMentionTextarea === "1" &&
    target instanceof HTMLTextAreaElement &&
    (!("isComposing" in e) || !/** @type {InputEvent} */ (e).isComposing)
  ) {
    progressMentionSyncFromTextarea(target);
  }
  if (
    target.dataset.pmGroupKeyword !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    uiState.progressGroupListKeyword = target.value;
    _debouncedRender();
    return true;
  }
  if (
    target.dataset.pmProjectKeyword !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    uiState.progressProjectListKeyword = target.value;
    _debouncedRender();
    return true;
  }
  if (
    target.dataset.pmIssueSearch !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    uiState.progressIssueSearch = target.value;
    _debouncedRender();
    return true;
  }
  if (
    target.dataset.pmQuickAddSearch !== undefined &&
    target instanceof HTMLInputElement
  ) {
    // 再描画せず DOM を直接フィルタ（入力フォーカスを保つ）
    const q = normalizeProgressSearchText(target.value);
    const list = target.closest(".pm-quick-add-modal")?.querySelector("[data-pm-quick-add-list]");
    if (list instanceof HTMLElement) {
      let visible = 0;
      list.querySelectorAll(".pm-qa-theme-item").forEach((item) => {
        const hay = item instanceof HTMLElement ? String(item.dataset.qaSearch || "") : "";
        const show = !q || hay.includes(q);
        if (item instanceof HTMLElement) item.hidden = !show;
        if (show) visible += 1;
      });
      const empty = list.querySelector("[data-pm-quick-add-empty]");
      if (empty instanceof HTMLElement) empty.hidden = visible !== 0;
    }
    return true;
  }
  if (
    target.dataset.dbSearch === "1" &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "dashboard"
  ) {
    // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    uiState.dashboardSearch = target.value;
    _debouncedRender();
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupTitle !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupCreateDraft = uiState.progressTodoGroupCreateDraft || { title: "", body: "" };
    uiState.progressTodoGroupCreateDraft.title = target.value;
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupCreateDraft = uiState.progressTodoGroupCreateDraft || { title: "", body: "" };
    uiState.progressTodoGroupCreateDraft.body = target.value;
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupCategory !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupCreateDraft = uiState.progressTodoGroupCreateDraft || { title: "", body: "", category: "" };
    uiState.progressTodoGroupCreateDraft.category = target.value;
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupEditCategory !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupEditDraft = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      category: "",
      challengeIds: [],
      themeFilter: ""
    };
    uiState.progressTodoGroupEditDraft.category = target.value;
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupEditTitle !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupEditDraft = uiState.progressTodoGroupEditDraft || { groupId: "", title: "", body: "" };
    uiState.progressTodoGroupEditDraft.title = target.value;
    return true;
  }
  if (
    target.dataset.pmModalTodoGroupEditBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressTodoGroupEditDraft = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    uiState.progressTodoGroupEditDraft.body = target.value;
    return true;
  }
  if (
    target.dataset.pmTodoGroupEditThemeFilter !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    // IME変換中は再描画しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    const d = uiState.progressTodoGroupEditDraft || {
      groupId: "",
      title: "",
      body: "",
      challengeIds: [],
      themeFilter: ""
    };
    d.themeFilter = target.value;
    uiState.progressTodoGroupEditDraft = d;
    render();
    return true;
  }
  if (
    target.dataset.pmModalChallengeTitle !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeCreateDraft =
      uiState.progressChallengeCreateDraft || { title: "", body: "" };
    uiState.progressChallengeCreateDraft.title = target.value;
    return true;
  }
  if (
    target.dataset.pmModalChallengeBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeCreateDraft =
      uiState.progressChallengeCreateDraft || { title: "", body: "" };
    uiState.progressChallengeCreateDraft.body = target.value;
    return true;
  }
  if (
    target.dataset.pmModalChallengeGenre !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeCreateDraft =
      uiState.progressChallengeCreateDraft || { title: "", body: "", genre: "" };
    uiState.progressChallengeCreateDraft.genre = target.value;
    return true;
  }
  if (
    target.dataset.pmModalChallengeEditGenre !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeEditDraft =
      uiState.progressChallengeEditDraft || { challengeId: "", title: "", body: "", genre: "" };
    uiState.progressChallengeEditDraft.genre = target.value;
    return true;
  }
  if (
    target.dataset.pmModalChallengeEditTitle !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeEditDraft =
      uiState.progressChallengeEditDraft || { challengeId: "", title: "", body: "" };
    uiState.progressChallengeEditDraft.title = target.value;
    return true;
  }
  if (
    target.dataset.pmModalChallengeEditBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeEditDraft =
      uiState.progressChallengeEditDraft || { challengeId: "", title: "", body: "" };
    uiState.progressChallengeEditDraft.body = target.value;
    return true;
  }
  if (
    target.dataset.pmChallengeWikiBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressChallengeWikiDraft = uiState.progressChallengeWikiDraft || { challengeId: "", wikiBody: "" };
    uiState.progressChallengeWikiDraft.wikiBody = target.value;
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-basic-title") &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    d.basicTitle = target.value;
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-basic-body") &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    d.basicBody = target.value;
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-project") &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    d.projectId = target.value;
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-client") &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    d.clientPick = String(target.value || "");
    // 案件が1件ならそのまま自動紐づけ。複数なら下に出る案件選択で選ぶ（それまで未紐づけ）。未設定なら解除
    const hit = pmThemeClientChoices().find((c) => String(c.id) === d.clientPick);
    d.projectId = hit && hit.projects.length === 1 && hit.projects[0].dbId != null ? String(hit.projects[0].dbId) : "";
    render(); // 案件選択欄の表示切替
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-workkind") &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    d.workKind = target.value === "internal" ? "internal" : "";
    if (d.workKind === "internal") {
      d.projectId = ""; // 社内工数は関連案件を持たない
      d.clientPick = "";
    }
    render(); // クライアント・案件欄の表示/非表示を切り替える
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-status-name") &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    if (target.readOnly) return true;
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    const i = Number(target.getAttribute("data-pm-challenge-settings-status-name"));
    if (!Number.isFinite(i) || i < 0) return true;
    const list = Array.isArray(d.statuses) ? [...d.statuses] : [];
    if (String(list[i] || "").trim() === "完了") return true;
    const prevName = String(list[i] || "").trim();
    list[i] = target.value;
    d.statuses = list;
    // 「工数を数える」リストも改名に追随させる（旧名のまま残ると保存時に落ちるため）
    if (Array.isArray(d.trackedStatuses) && prevName) {
      const ti = d.trackedStatuses.indexOf(prevName);
      if (ti >= 0) d.trackedStatuses[ti] = String(target.value || "").trim();
    }
    return true;
  }
  if (
    target.hasAttribute("data-pm-challenge-settings-category-name") &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    const i = Number(target.getAttribute("data-pm-challenge-settings-category-name"));
    if (!Number.isFinite(i) || i < 0) return true;
    const list = Array.isArray(d.categories) ? [...d.categories] : [];
    list[i] = target.value;
    d.categories = list;
    return true;
  }
  if (
    target.dataset.pmChallengeSettingsNew !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    const key = String(target.getAttribute("data-pm-challenge-settings-new") || "");
    if (key === "statuses") d.newStatus = target.value;
    else if (key === "categories") d.newCategory = target.value;
    return true;
  }
  if (
    target.dataset.pmModalAgendaTitle !== undefined &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      title: target.value
    };
    return true;
  }
  if (
    target.dataset.pmModalAgendaCategory !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      category: normalizeProgressAgendaCategory(target.value)
    };
    return true;
  }
  if (
    target.dataset.pmModalAgendaClient !== undefined &&
    target instanceof HTMLSelectElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      clientId: target.value
    };
    return true;
  }
  if (
    target.dataset.pmModalAgendaBody !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      body: target.value
    };
    return true;
  }
  if (
    target.dataset.pmModalAgendaNotify !== undefined &&
    target instanceof HTMLTextAreaElement &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      notifyRaw: target.value
    };
    return true;
  }
  if (
    (target.dataset.pmModalAgendaStart !== undefined || target.dataset.pmModalAgendaDue !== undefined) &&
    target instanceof HTMLInputElement &&
    currentRoute().page === "progress-management"
  ) {
    const base = { ...defaultProgressAgendaCreateDraft(), ...(uiState.progressAgendaCreateDraft || {}) };
    if (target.dataset.pmModalAgendaStart !== undefined) base.startDate = target.value;
    if (target.dataset.pmModalAgendaDue !== undefined) base.dueDate = target.value;
    uiState.progressAgendaCreateDraft = base;
    return true;
  }
  if (target.dataset.budgetPeriodNewNumber !== undefined && target instanceof HTMLInputElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    const next = sanitizeBudgetPeriodNumberInput(target.value);
    if (next !== target.value) target.value = next;
    return true;
  }
  if ((target.dataset.budgetPeriodNewFrom !== undefined || target.dataset.budgetPeriodNewTo !== undefined) && target instanceof HTMLInputElement && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    let v = String(target.value || "").replace(/[^\d/]/g, "");
    if (v.length > 4 && v[4] !== "/") v = `${v.slice(0, 4)}/${v.slice(4)}`;
    v = v.slice(0, 7);
    if (v !== target.value) target.value = v;
    return true;
  }
  if (target.dataset.docLineField && target.dataset.docLineIndex && currentRoute().page === "project-docs") {
    const route = currentRoute();
    const pid = route.id;
    const idx = Number(target.dataset.docLineIndex);
    const field = target.dataset.docLineField;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (lines?.[idx]) {
      lines[idx][field] = target.value;
      schedulePersistProjectDocDraftsStore();
      markProjectDocsDraftDirty(pid);
    }
    return true;
  }
  if (target.dataset.docMeta === "1" && target.dataset.docMetaField && target.dataset.project && target.dataset.docTab && currentRoute().page === "project-docs") {
    if (target instanceof HTMLInputElement && target.type === "checkbox") return true;
    const pid = target.dataset.project;
    const tab = target.dataset.docTab;
    const field = target.dataset.docMetaField;
    const dmeta = ensureProjectDocDraft(pid);
    if (dmeta) {
      if (!dmeta.meta[tab]) dmeta.meta[tab] = {};
      dmeta.meta[tab][field] = target.value;
      schedulePersistProjectDocDraftsStore();
      scheduleDocMetaSaveToServer(pid, tab, field); // サーバーにも保存（編集したタブだけ）
    }
    return true;
  }
  if (target.dataset.docRemarkEdit === "1" && target.dataset.project && target.dataset.docTab && currentRoute().page === "project-docs") {
    const dr = ensureProjectDocDraft(target.dataset.project);
    if (dr) {
      dr.remark[target.dataset.docTab] = target.value;
      schedulePersistProjectDocDraftsStore();
      scheduleDocMetaSaveToServer(target.dataset.project, target.dataset.docTab, null); // サーバーにも保存（備考）
    }
    return true;
  }
  return false;
}

/** 16-render-routing.js の input から移した分岐（元の 1075〜1500 行）。処理したら true（16 側はそこで止める） */
function handleDocCostDomainInput(e, target) {
  // 詳細ページの請求項目編集
  // ── 書類board: ▼ 行種別の変更 ──
  if (target.dataset.detailLineTypeChange != null && target.dataset.detailProject) {
    const idx = Number(target.dataset.detailLineTypeChange);
    const pid = target.dataset.detailProject;
    const newType = target.value; // "normal" | "heading" | "subtotal"
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (!lines || !lines[idx]) return true;
    const prev = lines[idx];
    if (newType === "heading") {
      lines[idx] = { lineType: "heading", item: prev.item || "" };
    } else if (newType === "subtotal") {
      lines[idx] = { lineType: "subtotal", item: "小計" };
    } else {
      lines[idx] = { lineType: "normal", item: prev.item || "", qty: prev.qty ?? "1", unit: prev.unit || "式", price: prev.price ?? "0", amount: "0", assignedUserId: "", assignedUserIds: [] };
    }
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    render();
    return true;
  }
  // ── 書類: ゴースト行に入力したら新規明細を追加 ──
  if (target.dataset.docGhostLine != null) {
    // IME変換中は行追加しない（変換途中でinputが作り直されて確定してしまうのを防ぐ）
    if (e instanceof InputEvent && e.isComposing) return true;
    // 入力中は再描画しない（1文字ごとに画面全体を描き直すとちらつく）。state に行を作って摘要だけ追従させ、
    // 確定（Enter・欄を離れる＝change）時に 15s-doc-ghost-line.js の commitDocGhostLine が1回だけ再描画する
    updateDocGhostLineFromInput(target);
    return true;
  }
  if (target.dataset.detailLineField != null && target.dataset.detailLineIndex != null && target.dataset.detailProject) {
    const pid = target.dataset.detailProject;
    const idx = Number(target.dataset.detailLineIndex);
    const field = target.dataset.detailLineField;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (lines?.[idx]) {
      lines[idx][field] = target.value;
      // 金額自動計算（数量×単価。単位が「式」も同様）
      lines[idx].amount = String(billingLineAmountExTax(lines[idx]));
      markProjectDocsDraftDirty(pid);
      schedulePersistProjectDocDraftsStore();
      // 数量・単価は render しない（差し替えで IME・連続入力が壊れる）。金額列・合計のみ DOM 更新
      if (field === "qty" || field === "price") {
        if (currentRoute().page === "project-detail") updateProjectDetailBillingDerivedCells(pid);
        else if (currentRoute().page === "project-docs") updateProjectDocsBillingDerivedCells(pid);
      }
    }
    return true;
  }
  if (target.dataset.pdField && projectDetailFormDraftActive()) {
    projectDetailFormDraftActive()[target.dataset.pdField] = target.value;
    return true;
  }
  // 対象月オーバーライドを案件の請求月に合わせる
  if (
    target instanceof HTMLButtonElement &&
    target.getAttribute("data-cost-review-month-apply") != null &&
    uiState.costRegWizard?.phase === "preview"
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-month-apply") ?? ""), 10);
    const val = String(target.getAttribute("data-cost-review-month-value") ?? "").trim();
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    syncCostImportPreviewRowsFromDom(uiState.costRegWizard);
    uiState.costRegWizard.rows[idx].targetMonthOverride = val || null;
    render();
    return true;
  }
  // 対象月オーバーライドを解除してグローバル月に戻す
  if (
    target instanceof HTMLButtonElement &&
    target.getAttribute("data-cost-review-month-clear") != null &&
    uiState.costRegWizard?.phase === "preview"
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-month-clear") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    syncCostImportPreviewRowsFromDom(uiState.costRegWizard);
    uiState.costRegWizard.rows[idx].targetMonthOverride = null;
    uiState.costRegWizard.rows[idx].projectIdPick = "";
    render();
    return true;
  }
  // 案件プルダウン変更 → 同一アカウント名の全行に一括反映（Google広告など）
  // render() を呼ばずDOM直接更新してリロード感を防ぐ
  if (
    target instanceof HTMLSelectElement &&
    target.getAttribute("data-cost-review-pick") != null &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-pick") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    const newVal = target.value;
    syncCostImportPreviewRowsFromDom(uiState.costRegWizard);
    uiState.costRegWizard.rows[idx].projectIdPick = newVal;
    // sd-selectのトリガーラベルをDOM直接更新
    const updateTriggerLabel = (selEl) => {
      const triggerBtn = selEl.closest(".sd-select")?.querySelector(".sd-trigger");
      if (!triggerBtn) return;
      const opt = selEl.options[selEl.selectedIndex];
      triggerBtn.textContent = opt ? (String(opt.text || "").trim() || "選択してください") : "選択してください";
    };
    updateTriggerLabel(target);
    const acctKey = target.getAttribute("data-account-name-key") || "";
    if (acctKey) {
      let syncCount = 0;
      document.querySelectorAll("[data-cost-review-pick][data-account-name-key]").forEach((el) => {
        if (!(el instanceof HTMLSelectElement)) return;
        if (el.getAttribute("data-account-name-key") !== acctKey) return;
        const elIdx = Number.parseInt(String(el.getAttribute("data-cost-review-pick") ?? ""), 10);
        if (elIdx === idx || !Number.isFinite(elIdx) || !uiState.costRegWizard.rows[elIdx]) return;
        // 案件一覧は遅延生成なので、値を書く前にその値の option だけ足す（無いと value が空に落ちる。
        // 全案件を流し込むと同アカウントの行数×全案件で元の巨大DOMに戻る）
        costImportEnsureLazyProjectOption(el, newVal);
        el.value = newVal;
        uiState.costRegWizard.rows[elIdx].projectIdPick = newVal;
        updateTriggerLabel(el);
        refreshCostImportProvMatchDom(elIdx);
        syncCount++;
      });
      if (syncCount > 0) showToast(`同じアカウント名の ${syncCount} 行にも反映しました`);
    }
    // 案件が変わると仮突合も変わるのでインジケータだけ更新（再描画はしない）
    refreshCostImportProvMatchDom(idx);
    // render() を呼ばない → モーダルが再生成されずスクロール位置・フォーカスが維持される
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.getAttribute("data-cost-review-amount-ex") != null &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-amount-ex") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    const row = uiState.costRegWizard.rows[idx];
    if (row.importCaptionKind === "consumption_tax") return true;
    // 税抜金額を打ち換えたら消費税（10%）・元取込値も揃え、下の「税／税込」表示もその場で更新する
    // （登録前の同期は「値が変わった行だけ」再計算するので、ここで揃えないと古い税額のまま登録される）
    const ex = parseCostReviewYenInput(target.value);
    // 取込時の値を初回編集時に控え、元の金額に戻したら取込元（PDF等）の正確な税額を復元する（軽減税率・端数差を守る）
    if (row._importedEx === undefined) {
      row._importedEx = row.amountExTax;
      row._importedTax = row.consumptionTax;
      row._importedRaw = row.importedRawAmount;
    }
    row.amountExTax = ex;
    if (Number.isFinite(ex)) {
      const back = ex === row._importedEx;
      row.consumptionTax = back ? row._importedTax : costImportRecomputedTaxYen(row, ex);
      row.importedRawAmount = back ? row._importedRaw : ex;
      const box = target.closest(".cost-import-amt-edit");
      let sub = box?.querySelector(".cost-import-tax-sub");
      if (!sub && box) {
        sub = document.createElement("div");
        sub.className = "cost-import-tax-sub";
        box.appendChild(sub);
      }
      if (sub) {
        sub.textContent = `税 ${yen.format(row.consumptionTax)}／税込 ${yen.format(ex + row.consumptionTax)}`;
        sub.title = sub.textContent;
      }
    }
    refreshCostImportProvMatchDom(idx);
    return true;
  }
  // 取込プレビュー: 仮突合の行アクション（合致=置換チェック／金額相違=処理選択）
  if (
    (target.getAttribute("data-cost-review-prov-replace") != null ||
      target.getAttribute("data-cost-review-prov-action") != null) &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    const isReplace = target.getAttribute("data-cost-review-prov-replace") != null;
    const attr = isReplace ? "data-cost-review-prov-replace" : "data-cost-review-prov-action";
    const idx = Number.parseInt(String(target.getAttribute(attr) ?? ""), 10);
    const row = uiState.costRegWizard.rows[idx];
    if (!row) return true;
    if (isReplace && target instanceof HTMLInputElement) row.provReplace = target.checked;
    else if (target instanceof HTMLSelectElement) row.provMismatchAction = target.value;
    return true;
  }
  if (
    target instanceof HTMLTextAreaElement &&
    target.getAttribute("data-cost-review-note") != null &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-note") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    uiState.costRegWizard.rows[idx].reviewNote = target.value;
    return true;
  }
  if (
    target instanceof HTMLTextAreaElement &&
    target.getAttribute("data-cost-review-manual-summary") != null &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    const idx = Number.parseInt(String(target.getAttribute("data-cost-review-manual-summary") ?? ""), 10);
    if (!Number.isFinite(idx) || idx < 0 || !uiState.costRegWizard.rows[idx]) return true;
    uiState.costRegWizard.rows[idx].manualSummaryText = target.value;
    return true;
  }
  if (target.dataset.authField) {
    uiState.auth[target.dataset.authField] = target.value;
  }
  if (target.dataset.approvalOtherFlowName && isAccountSettingsRoute() && uiState.accountSettingsTab === "approval") {
    ensureApprovalOtherFlowsState();
    const id = target.dataset.approvalOtherFlowName;
    const name = String(target.value || "").trim();
    uiState.approvalOtherFlows = (uiState.approvalOtherFlows || []).map((f) => (f.id === id ? { ...f, name: name || "名称未設定" } : f));
    persistApprovalOtherFlowsStore();
    return true;
  }
  if (target.dataset.clientListFilter && currentRoute().page === "client-list") {
    const key = target.dataset.clientListFilter;
    // IME変換中は state も書かない（他の描画が走ったとき変換途中の文字で絞り込まれるのを防ぐ）
    if (key === "keyword" && isImeComposingSearch(target, e)) return true;
    uiState.clientList.filters[key] = target.value;
    uiState.clientList.page = 1;
    if (key === "keyword") {
      _debouncedRender();
      return true;
    }
    render();
    return true;
  }
  if (target.dataset.domainListFilter && currentRoute().page === "domain-list") {
    const dk = target.dataset.domainListFilter;
    uiState.domainList.filters[dk] = target.value;
    uiState.domainList.page = 1;
    if (dk === "keyword") return true;
    render();
    return true;
  }
  if (target.dataset.domainStatusFilter && currentRoute().page === "domain-list" && target instanceof HTMLInputElement) {
    const v = target.dataset.domainStatusFilter;
    const cur = Array.isArray(uiState.domainList.filters.statuses) ? uiState.domainList.filters.statuses.slice() : [];
    const idx = cur.indexOf(v);
    if (target.checked && idx < 0) cur.push(v);
    else if (!target.checked && idx >= 0) cur.splice(idx, 1);
    uiState.domainList.filters.statuses = cur;
    uiState.domainList.page = 1;
    render();
    return true;
  }
  if (
    target.dataset.partnerListFilter &&
    currentRoute().page === "partner-list" &&
    (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
  ) {
    const pk = target.dataset.partnerListFilter;
    uiState.partnerList.filters[pk] = target.value;
    uiState.partnerList.page = 1;
    if (pk === "keyword") return true;
    render();
    return true;
  }
  if (target.dataset.field && uiState.composer) {
    uiState.composer[target.dataset.field] = target.value;
    return true;
  }
  if (target.dataset.projectField && uiState.projectCreate) {
    uiState.projectCreate.form[target.dataset.projectField] = target.value;
    return true;
  }
  if (target.dataset.projectFilterLookup) {
    const key = String(target.dataset.projectFilterLookup || "");
    if (key === "clientId") {
      uiState.projectListFilters.clientNameQuery = target.value;
      uiState.projectListFilters.clientId = findProjectClientFilterIdByName(target.value);
      setProjectListPage(1);
      return true;
    }
  }
  if (target.dataset.projectFilter) {
    const pfKey = String(target.dataset.projectFilter || "");
    const isKeyword = pfKey === "name" || pfKey === "no";
    // IME変換中は state も書かない（他の描画が走ったとき変換途中の文字で絞り込まれるのを防ぐ）。
    // 確定時は compositionend（data-ime-search）が input を再発火してここを通る
    if (isKeyword && isImeComposingSearch(target, e)) return true;
    uiState.projectListFilters[pfKey] = target.value;
    // 一括変更の「受注状況」は絞り込みではないので、ページ位置を戻さない
    if (pfKey !== "bulkStatus") setProjectListPage(1);
    if (isKeyword) {
      _debouncedRender();
      return true;
    }
    // 入金日（type=date）も SP ではピッカー操作中に再描画しない（月と同じ扱い）
    if (target instanceof HTMLInputElement && (target.type === "month" || target.type === "date") && deferRenderWhileMonthPicking(target)) return true;
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "month" &&
    currentRoute().page === "cost-management" &&
    target.hasAttribute("data-cost-mgmt-filter-month")
  ) {
    syncCostMgmtFilterFromDom();
    if (deferRenderWhileMonthPicking(target)) return true;
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.hasAttribute("data-cost-mgmt-filter-keyword") &&
    currentRoute().page === "cost-management"
  ) {
    if (isImeComposingSearch(target, e)) return true;
    if (costMgmtKeywordDebounceTimer != null) clearTimeout(costMgmtKeywordDebounceTimer);
    costMgmtKeywordDebounceTimer = setTimeout(() => {
      costMgmtKeywordDebounceTimer = null;
      // 待機中に変換が始まったら描画しない（DOM の変換途中の文字を拾ってしまう。確定時に再予約される）
      if (isImeComposingSearch(document.activeElement)) return;
      syncCostMgmtFilterFromDom();
      render();
    }, 400);
    return true;
  }
  if (target.dataset.clientSearch && uiState.projectCreate) {
    uiState.projectCreate.form.clientQuery = target.value;
    uiState.projectCreate.form.clientHighlight = -1;
    const isComposing = target instanceof HTMLInputElement && target.dataset.imeComposing === "1";
    if (!isComposing) refreshProjectClientCandidates();
    return true;
  }
  if (target.dataset.corpLocalQ && (uiState.clientCreate || uiState.partnerCreate)) {
    const ctx = String(target.dataset.corpLocalQ || "");
    if ((ctx === "client" && uiState.clientCreate) || (ctx === "partner" && uiState.partnerCreate)) {
      if (!uiState.corporateLocalSearch) resetCorporateLocalSearch();
      uiState.corporateLocalSearch.context = ctx;
      uiState.corporateLocalSearch.query = target instanceof HTMLInputElement ? target.value : "";
    }
    return true;
  }
  if (
    (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) &&
    target.dataset.partnerField &&
    uiState.partnerCreate
  ) {
    uiState.partnerCreate[target.dataset.partnerField] = target.value;
    return true;
  }
  if (target.dataset.clientField && uiState.clientCreate) {
    uiState.clientCreate[target.dataset.clientField] = target.value;
    return true;
  }
  if (target.dataset.domainDateText && uiState.domainCreate) {
    // 手入力中は再描画しない（キャレット維持）。ISO 正規化は blur(change)/保存時に行う
    uiState.domainCreate[target.dataset.domainDateText] = target.value;
    return true;
  }
  if (target.dataset.domainDatePicker && uiState.domainCreate) {
    // カレンダー選択値（ISO）を取り込み、テキスト欄へスラッシュ表示で反映
    uiState.domainCreate[target.dataset.domainDatePicker] = target.value;
    render();
    return true;
  }
  if (target.dataset.domainField && uiState.domainCreate) {
    uiState.domainCreate[target.dataset.domainField] = target.value;
    return true;
  }
  if (target.dataset.userField && uiState.userEditor) {
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      uiState.userEditor[target.dataset.userField] = target.checked;
    } else {
      uiState.userEditor[target.dataset.userField] = target.value;
    }
    return true;
  }
  if (target.dataset.templateField && uiState.templateEditor) {
    const key = target.dataset.templateField;
    if (key === "name" || key === "description") uiState.templateEditor[key] = target.value;
    if (key === "scope") {
      // デフォルトにできるのはチーム共通だけ（api/doc-templates.js）。自分専用にしたらデフォルトを外して欄を切り替える
      uiState.templateEditor.scope = target.value;
      if (target.value !== "shared") uiState.templateEditor.isDefault = false;
      render();
    }
    if (key === "isDefault") uiState.templateEditor.isDefault = target.value === "true";
    if (key === "subject" || key === "note" || key === "bankMemo") uiState.templateEditor.preset[key] = target.value;
    if (key === "taxRate") uiState.templateEditor.preset.taxRate = Number(target.value);
    if (key === "dueDays") uiState.templateEditor.preset.dueDays = Number(target.value || 0);
    return true;
  }
  if (target.dataset.lineIndex && target.dataset.lineField && uiState.composer) {
    const idx = Number(target.dataset.lineIndex);
    const field = target.dataset.lineField;
    if (field === "item") {
      uiState.composer.lines[idx][field] = target.value;
      return true;
    }
    const normalized = String(target.value).normalize("NFKC").replace(/,/g, "").replace(/[^\d.-]/g, "");
    uiState.composer.lines[idx][field] = normalized === "" ? 0 : Number(normalized);
  }
  if (target.dataset.costField && uiState.costForm) {
    const key = target.dataset.costField;
    if (key === "targetMonth") {
      uiState.costForm[key] = String(target.value || "").replace(/-/g, "/");
      render();
      return true;
    }
    if (key === "amount" || key === "consumptionTax") {
      const n = String(target.value).normalize("NFKC").replace(/,/g, "").replace(/[^\d.-]/g, "");
      uiState.costForm[key] = n === "" ? 0 : Number(n);
      // 金額は税抜入力なので消費税は自動計算（消費税欄の手修正はそのまま活かす）
      if (key === "amount") {
        syncCostFormTaxFromAmount();
        syncCostFormWithholdingFromVendor();
      }
    } else {
      uiState.costForm[key] = target.value;
    }
  }
  if (target.dataset.expenseField && uiState.expenseForm) {
    uiState.expenseForm[target.dataset.expenseField] = target.value;
  }
  if (target.dataset.paymentField && uiState.paymentForm) {
    uiState.paymentForm[target.dataset.paymentField] = target.value;
  }
  if (target.dataset.myAccountField && uiState.myAccountEditor) {
    uiState.myAccountEditor[target.dataset.myAccountField] = target.value;
    return true;
  }
  if (target.dataset.deptCreateName && uiState.deptCreateModal) {
    uiState.deptCreateModal.name = target.value;
  }
  return false;
}
// 画面の change 処理（16-render-routing.js の document の change ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxx(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能の change 処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の change から移した分岐（元の 1507〜2115 行）。処理したら true（16 側はそこで止める） */
function handleProgressCostChange(e, target) {
  // 入金一括記録: 入金日の変更を保持（再描画で消えないように）
  if (target instanceof HTMLInputElement && target.dataset.paymentBulkDate !== undefined) {
    uiState.paymentBulkDate = String(target.value || "").trim();
    return true;
  }
  // 案件詳細・基本情報: 入金日のインライン変更（経理のみ）。日付を入れる→入金済み、クリア→未入金へ戻す
  if (target instanceof HTMLInputElement && target.dataset.pdPaymentDate !== undefined) {
    const p = projects.find((x) => x.id === target.dataset.pdPaymentDate);
    if (!p) return true;
    if (!canConfirmPayment(p)) {
      showToast("入金確認は経理またはマスター/管理者が行えます");
      render();
      return true;
    }
    const newDate = String(target.value || "").trim();
    const oldDate = String(p.paymentRecord?.payDate || "").slice(0, 10);
    if (newDate === oldDate) return true;
    void runExclusive(`payment:date:${p.id}`, async () => {
      const confirmed = newDate
        ? await fpConfirm({ title: "入金確認を記録しますか？", message: `入金日 ${newDate} で入金済みにします。`, confirmLabel: "入金済みにする", danger: false })
        : await fpConfirm({
            title: "入金確認を取り消しますか？",
            message: "入金日をクリアして、未入金（請求済み）に戻します。",
            note: "もう一度入金日を入れれば付け直せます。",
            confirmLabel: "入金確認を取り消す"
          });
      if (!confirmed) {
        render();
        return;
      }
      const snap = newDate
        ? applyPaymentConfirmationToProject(p, {
            payDate: newDate,
            amount: p.paymentRecord?.amount ?? p.amount,
            note: p.paymentRecord?.note || ""
          })
        : applyPaymentCancellationToProject(p);
      const doneMsg = newDate ? `入金確認を記録しました（${newDate}）` : "入金確認を取り消しました（請求済みに戻りました）";
      render();
      if (!(p.dbId && authToken)) {
        showToast(doneMsg);
        return;
      }
      const r = await persistPaymentChangeToServer(p, snap, newDate ? {} : { clearPayment: true });
      if (r.ok) {
        showToast(doneMsg);
        return;
      }
      showToastError(`入金記録の保存に失敗しました。もう一度お試しください。${r.mismatch ? PAYMENT_SAVE_MISMATCH_HINT : ""}`);
      render();
    });
    return true;
  }
  // 原価フォーム: 仮原価トグル
  if (target instanceof HTMLInputElement && target.dataset.costProvisionalToggle !== undefined && uiState.costForm) {
    uiState.costForm.isProvisional = target.checked;
    // 仮原価では源泉を区分で選ぶ。いまの源泉所得税（手入力を含む）から区分を決め直す
    // （決まらない金額は区分なし＝保存時もその金額のまま）
    if (target.checked) {
      uiState.costForm.withholdingKind = costWithholdingKindFromAmount(uiState.costForm.amount, uiState.costForm.withholdingTax);
    }
    // 仮原価にすると支払期限の欄が出る会計区分があるので、パートナーの支払条件から入れ直す
    applyCostFormPartnerAutofill("isProvisional");
    render();
    return true;
  }
  // SP 課題詳細: 期限日のネイティブ日付ピッカー変更 → 直接保存（空選択で期限クリア）
  if (
    target instanceof HTMLInputElement &&
    target.dataset.pmSpDueInput !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    if (!authToken) return true;
    const agendaId = Number(target.dataset.pmSpAgenda);
    if (!Number.isFinite(agendaId) || agendaId < 1) return true;
    const found = findProgressAgendaById(agendaId);
    if (!found?.agenda) return true;
    const newDue = String(target.value || "").trim(); // "YYYY-MM-DD" or ""
    const before = captureProgressAgendaDetailSnapshot(agendaId);
    const payload = { action: "updateAgendaDetail", agendaId, dueDate: newDue };
    void (async () => {
      try {
        const { ok: apiOk, data: j } = await apiFetch("/api/progress-management", { method: "POST", body: payload });
        if (!apiOk) {
          showToastError(typeof j.error === "string" ? j.error : "期限日の更新に失敗しました");
          return;
        }
        applyProgressAgendaDetailFromPayload(agendaId, payload);
        const lines = progressAgendaDetailChangeLogLines(currentUser?.name || authUser?.name, before, payload);
        if (lines.length) appendProgressAgendaSystemMessageLocal(agendaId, lines.join("\n"));
        showToast(newDue ? "期限日を変更しました" : "期限日をクリアしました");
        const route = currentRoute();
        const pid = String(route.searchParams?.get("project") || "").trim();
        if (pid) setProgressManagementHash(null, pid, String(agendaId));
        else render();
      } catch {
        showToastError("期限日の更新に失敗しました");
      }
    })();
    return true;
  }
  // テーマ設定: 状態ごとの「工数を数える」チェックを draft に同期（再描画で失われないように）
  if (
    target instanceof HTMLInputElement &&
    target.dataset.pmChallengeSettingsTrack !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    const d = uiState.progressChallengeSettingsDraft;
    if (!d) return true;
    const i = Number(target.dataset.pmChallengeSettingsTrack);
    const name = String((Array.isArray(d.statuses) ? d.statuses : [])[i] || "").trim();
    if (!Number.isFinite(i) || !name) return true;
    // null（全計測）から初めて外す時は、現在の状態リストで具体化してから増減する
    let arr = Array.isArray(d.trackedStatuses)
      ? [...d.trackedStatuses]
      : (Array.isArray(d.statuses) ? d.statuses : []).map((s) => String(s || "").trim()).filter(Boolean);
    if (target.checked) {
      if (!arr.includes(name)) arr.push(name);
    } else {
      arr = arr.filter((x) => x !== name);
    }
    d.trackedStatuses = arr;
    return true;
  }
  // 進捗ステップバー: 工程ごとの工数担当を変更（空=課題の担当者に従う）
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.pmStepAssignee !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    if (!authToken) return true;
    const raw = String(target.dataset.pmStepAssignee || "");
    const sep = raw.indexOf(":");
    const agendaId = Number(sep >= 0 ? raw.slice(0, sep) : "");
    const stepName = sep >= 0 ? raw.slice(sep + 1) : "";
    if (!Number.isFinite(agendaId) || agendaId < 1 || !stepName) return true;
    const hit = findProgressAgendaById(agendaId);
    if (!hit?.agenda) return true;
    const val = String(target.value || "").trim();
    const userId = val ? Number(val) : null;
    if (userId !== null && (!Number.isFinite(userId) || userId < 1)) return true;
    void (async () => {
      try {
        const res = await fetch("/api/progress-management", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ action: "updateAgendaStepAssignee", agendaId, stepName, userId })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showToastError(data?.error || "工数担当の変更に失敗しました");
          render();
          return;
        }
        hit.agenda.stepAssignees = data.stepAssignees || null;
        _syncAgendaInAllCaches(agendaId, (a) => {
          a.stepAssignees = data.stepAssignees || null;
        });
        showToast(userId === null ? "工数担当を課題の担当に戻しました" : "工数担当を変更しました");
        render();
      } catch (_) {
        showToastError("工数担当の変更に失敗しました");
        // 通信例外でもプルダウンを保存済みの値へ戻す（選んだままだと保存されたと誤認する）
        render();
      }
    })();
    return true;
  }
  // ドメイン日付の手入力欄: blur 時に「YYYY/MM/DD」等を ISO へ正規化（パース成功時のみ）
  if (target.dataset.domainDateText && uiState.domainCreate) {
    const f = target.dataset.domainDateText;
    const iso = parseCsvDateToIsoYmd(target.value);
    if (iso || !String(target.value || "").trim()) {
      uiState.domainCreate[f] = iso;
      render();
    }
    return true;
  }
  // ToDo通知の受け取りチャネル（メール / Slack / プッシュ）トグル
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.notifyPref != null
  ) {
    const pref = target.dataset.notifyPref; // "email" | "slack" | "push"
    const checked = target.checked;
    // プッシュは購読（許可取得 + PushManager）が必要なので専用フローに委譲する
    if (pref === "push") {
      void setTodoPushEnabled(checked, target);
      return true;
    }
    const fieldKey = pref === "slack" ? "notifySlack" : "notifyEmail";
    const prev = currentUser[fieldKey];
    currentUser[fieldKey] = checked;
    if (!authToken) return true;
    void fpSaveTrack(`notify:${pref}`, async () => {
      try {
        const res = await fetch("/api/me", {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ [fieldKey]: checked })
        });
        if (!res.ok) throw new Error();
      } catch {
        // 失敗時は元に戻す
        currentUser[fieldKey] = prev;
        target.checked = !checked;
        showToastError("通知設定の保存に失敗しました");
        return false;
      }
      showToast(`${pref === "slack" ? "Slack" : "メール"}通知を${checked ? "ON" : "OFF"}にしました`);
      return true;
    });
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.accountListExcludeRetired != null &&
    isAccountSettingsRoute()
  ) {
    uiState.accountListExcludeRetired = target.checked;
    render();
    return true;
  }
  if (
    (target instanceof HTMLSelectElement || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
    target.dataset.partnerField &&
    uiState.partnerCreate
  ) {
    uiState.partnerCreate[target.dataset.partnerField] = target.value;
    return true;
  }
  if (
    currentRoute().page === "cost-management" &&
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.costMgmtRowCheck != null
  ) {
    if (!uiState.costMgmtSelected || typeof uiState.costMgmtSelected !== "object") uiState.costMgmtSelected = {};
    const raw = String(target.dataset.costMgmtRowCheck || "");
    if (target.checked) uiState.costMgmtSelected[raw] = true;
    else delete uiState.costMgmtSelected[raw];
    const left = Object.keys(uiState.costMgmtSelected).filter((k) => uiState.costMgmtSelected[k]).length;
    if (!left) uiState.costMgmtBulkModal = null;
    render();
    return true;
  }
  if (
    currentRoute().page === "cost-management" &&
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.costMgmtCheckAll != null
  ) {
    if (!uiState.costMgmtSelected || typeof uiState.costMgmtSelected !== "object") uiState.costMgmtSelected = {};
    const { filtered } = getCostMgmtTableRows();
    if (target.checked) {
      for (const c of filtered) {
        uiState.costMgmtSelected[costMgmtSelectionKey(c._projectId, c.id)] = true;
      }
    } else {
      for (const c of filtered) {
        delete uiState.costMgmtSelected[costMgmtSelectionKey(c._projectId, c.id)];
      }
    }
    const left = Object.keys(uiState.costMgmtSelected).filter((k) => uiState.costMgmtSelected[k]).length;
    if (!left) uiState.costMgmtBulkModal = null;
    render();
    return true;
  }
  if (
    COST_ALLOC_GROUP_PAGES.has(currentRoute().page) &&
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.costAllocRowCheck != null
  ) {
    if (!uiState.costAllocSelected || typeof uiState.costAllocSelected !== "object") uiState.costAllocSelected = {};
    const raw = String(target.dataset.costAllocRowCheck || "");
    if (target.checked) uiState.costAllocSelected[raw] = true;
    else delete uiState.costAllocSelected[raw];
    render();
    return true;
  }
  if (
    COST_ALLOC_GROUP_PAGES.has(currentRoute().page) &&
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.costAllocCheckAll != null
  ) {
    if (!uiState.costAllocSelected || typeof uiState.costAllocSelected !== "object") uiState.costAllocSelected = {};
    // 表示と同じ絞り（計上月・発注先）を適用し、グループに畳まれた行は対象外
    const rows = costAllocationCheckAllRows();
    if (target.checked) {
      for (const c of rows) {
        uiState.costAllocSelected[costMgmtSelectionKey(COST_UNASSIGNED_PROJECT_UI_ID, c.id)] = true;
      }
    } else {
      for (const c of rows) {
        delete uiState.costAllocSelected[costMgmtSelectionKey(COST_UNASSIGNED_PROJECT_UI_ID, c.id)];
      }
    }
    render();
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.costAllocBulkProjectPick != null &&
    COST_ALLOC_GROUP_PAGES.has(currentRoute().page)
  ) {
    uiState.costAllocBulkProjectPick = String(target.value || "").trim();
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.pmModalAgendaStatus !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      status: normalizeProgressTodoStatus(target.value)
    };
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.pmModalAgendaPriority !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      priority: normalizeProgressAgendaPriority(target.value)
    };
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.pmModalAgendaAssignee !== undefined &&
    currentRoute().page === "progress-management"
  ) {
    const id = String(target.value || "").trim();
    const u = users.find((x) => String(x.id) === id);
    uiState.progressAgendaCreateDraft = {
      ...defaultProgressAgendaCreateDraft(),
      ...(uiState.progressAgendaCreateDraft || {}),
      assignedUserId: id,
      assignedUserSearch: u ? String(u.name || "") : ""
    };
    return true;
  }
  if (
    currentRoute().page === "progress-management" &&
    target instanceof HTMLInputElement &&
    target.type === "file" &&
    target.dataset.pmComposeAttachFile != null
  ) {
    const route = currentRoute();
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    if (!issueParam) return true;
    progressAddValidFilesToCompose(issueParam, target.files);
    target.value = "";
    return true;
  }
  if (
    currentRoute().page === "progress-management" &&
    target instanceof HTMLInputElement &&
    target.type === "file" &&
    target.dataset.pmModalAgendaAttachFile != null
  ) {
    progressAddValidFilesToAgendaModal(target.files);
    target.value = "";
    return true;
  }
  if (
    currentRoute().page === "progress-management" &&
    target instanceof HTMLInputElement &&
    target.type === "file" &&
    target.dataset.pmMsgAttachUpload != null
  ) {
    const messageId = Number(target.dataset.pmMsgAttachUpload);
    const file = target.files && target.files[0] ? target.files[0] : null;
    target.value = "";
    if (!authToken) {
      showToast("ログインが必要です");
      return true;
    }
    if (!Number.isFinite(messageId) || messageId < 1 || !file) return true;
    const route = currentRoute();
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    if (!issueParam) return true;
    uiState.progressAttachmentUploadMessageId = String(messageId);
    render();
    void (async () => {
      try {
        await uploadProgressBinaryAttachment({ messageId, file });
        await refreshProgressManagementFromServer();
        uiState.progressAttachmentUploadMessageId = "";
        showToast("添付を登録しました");
        const pid = String(route.searchParams?.get("project") || "").trim();
        if (pid) setProgressManagementHash(null, pid, issueParam);
        else render();
      } catch (err) {
        uiState.progressAttachmentUploadMessageId = "";
        showToastError(err instanceof Error ? err.message : "添付に失敗しました");
        render();
      }
    })();
    return true;
  }
  if (
    currentRoute().page === "progress-management" &&
    target instanceof HTMLSelectElement &&
    target.dataset.pmIssueHeadStatus != null
  ) {
    const agendaId = String(target.dataset.pmIssueHeadStatus || "").trim();
    if (!agendaId || !authToken) return true;
    const numId = Number(agendaId);
    if (!Number.isFinite(numId) || numId < 1) return true;
    const found = findProgressAgendaById(numId);
    if (!found?.agenda) return true;
    const nextStatus = String(target.value || "").trim();
    if (!nextStatus) return true;
    const curStatus = String(found.agenda.status || "").trim() || "未対応";
    if (curStatus === nextStatus) return true;
    // 保存中はセレクトを無効化（連打防止。差し戻し理由の入力待ちの間も含む）
    target.disabled = true;
    void (async () => {
      const gate = await requestProgressAgendaStatusChange(found.agenda, found.challenge, nextStatus);
      if (!gate.proceed) {
        target.value = curStatus;
        target.disabled = false;
        return;
      }
      // 楽観更新（即時フィードバック）。確定はサーバー再取得で行う。
      const snapshot = applyProgressAgendaStatusLocal(numId, nextStatus);
      syncProgressIssueHeadStatus(agendaId, nextStatus);
      refreshProgressIssueSummaryDom(numId);
      try {
        let savedStatus = nextStatus;
        const saved = await fpSaveTrack(`agenda-status:${agendaId}`, async () => {
          const r = await commitProgressAgendaStatusChange(numId, nextStatus, { rejectionReason: gate.rejectionReason });
          if (!r.ok) {
            if (snapshot) revertProgressAgendaStatusLocal(numId, snapshot);
            refreshProgressIssueSummaryDom(numId);
            resetProgressIssueAttrFormFromAgenda(numId);
            showToastError(r.error || "状況の変更に失敗しました");
            return false;
          }
          savedStatus = r.savedStatus || nextStatus;
          return true;
        });
        if (saved) showToast(`状況を「${savedStatus}」に変更しました`);
      } finally {
        target.disabled = false;
      }
    })();
    return true;
  }
  if (
    currentRoute().page === "progress-management" &&
    (target instanceof HTMLSelectElement || target instanceof HTMLInputElement) &&
    target.dataset.pmIssueField != null &&
    target.closest("[data-pm-issue-attr-form]")
  ) {
    const agendaId = String(target.dataset.pmIssueAgenda || "").trim();
    if (target instanceof HTMLSelectElement && target.dataset.pmIssueField === "status") {
      syncProgressIssueHeadStatus(agendaId, target.value);
    }
    if (agendaId) setProgressIssueAttrSaveUi(agendaId, { dirty: true });
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.detailLineField &&
    target.dataset.detailLineIndex != null &&
    target.dataset.detailProject
  ) {
    const pid = target.dataset.detailProject;
    const idx = Number(target.dataset.detailLineIndex);
    const field = target.dataset.detailLineField;
    const lines = uiState.projectDocsDrafts[pid]?.lines;
    if (lines?.[idx]) {
      lines[idx][field] = target.value;
      lines[idx].amount = String(billingLineAmountExTax(lines[idx]));
      markProjectDocsDraftDirty(pid);
      schedulePersistProjectDocDraftsStore();
    }
    return true;
  }
  if (target instanceof HTMLSelectElement && target.dataset.staffLineProject != null && target.dataset.staffLineIndex != null) {
    const pid = target.dataset.staffLineProject || "";
    const idx = Number(target.dataset.staffLineIndex);
    if (!pid || Number.isNaN(idx)) return true;
    const lines = ensureProjectDocDraft(pid)?.lines;
    if (!lines || !lines[idx]) return true;
    const line = lines[idx];
    if ((line.lineType || "normal") !== "normal") return true;
    setLineAssigneeIds(line, target.value ? [target.value] : []);
    markProjectDocsDraftDirty(pid);
    persistAllLineStaffForProject(pid);
    render();
    return true;
  }
  if (target instanceof HTMLSelectElement && target.dataset.staffAssignFilter === "category" && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentFilters.category = target.value;
    render();
    return true;
  }
  if (target instanceof HTMLSelectElement && target.dataset.staffAssignFilter === "assignee" && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentFilters.assignee = target.value;
    render();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.staffAssignFilter === "billingFrom" && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentFilters.billingFrom = target.value;
    if (deferRenderWhileMonthPicking(target)) return true;
    render();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.staffAssignFilter === "billingTo" && currentRoute().page === "staff-assignment") {
    uiState.staffAssignmentFilters.billingTo = target.value;
    if (deferRenderWhileMonthPicking(target)) return true;
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.staffAssignUnassignedOnly !== undefined &&
    currentRoute().page === "staff-assignment"
  ) {
    uiState.staffAssignmentFilters.unassignedOnly = target.checked;
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.docMeta === "1" &&
    target.dataset.docMetaField &&
    target.dataset.project &&
    target.dataset.docTab &&
    currentRoute().page === "project-docs"
  ) {
    const pid = target.dataset.project;
    const tab = target.dataset.docTab;
    const field = target.dataset.docMetaField;
    const dmeta = ensureProjectDocDraft(pid);
    if (dmeta) {
      if (!dmeta.meta[tab]) dmeta.meta[tab] = {};
      dmeta.meta[tab][field] = target.checked;
      schedulePersistProjectDocDraftsStore();
      scheduleDocMetaSaveToServer(pid, tab, field); // チェック状態もサーバーに保存
    }
    // チェック状態は input 要素自身が保持するため render() は不要（ちらつき・上部スクロール防止）
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.projectOrderStatusId && currentRoute().page === "project-detail") {
    const p = projects.find((x) => x.id === target.dataset.projectOrderStatusId);
    if (!p) return true;
    const nextStatus = String(target.dataset.projectOrderStatus || "").trim();
    if (!nextStatus) return true;
    if (!target.checked) {
      target.checked = true;
      return true;
    }
    // 下書きに入れるだけ（基本情報の「保存」で確定。試しに変えても確定しないように）
    const draft = projectDetailDraftFor(p);
    if (!draft) return true;
    draft.orderStatus = nextStatus;
    render();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.projectUncollectibleId && currentRoute().page === "project-detail") {
    const p = projects.find((x) => x.id === target.dataset.projectUncollectibleId);
    if (!p) return true;
    const draft = projectDetailDraftFor(p);
    if (!draft) return true;
    const nextChecked = target.checked;
    // オフに戻すときは付ける前の状態へ戻す（保存時に applyProjectFormDraftToProject が戻す）。無条件に「未作成」へ
    // 戻すと、送付済み・入金待ちの案件が未請求に見えて二重請求の原因になる。付ける前の状態は保存列が無いので
    // 画面を開いている間だけ覚え、分からない（リロード後など）ときは確認してから「未作成」にする。
    // 保存済みが回収不能でない（このページで付けて未保存のまま外す）なら元の状態のままなので確認は要らない
    const knownBefore = p.invoiceStatusBeforeUncollectible && p.invoiceStatusBeforeUncollectible !== "回収不能";
    if (!nextChecked && p.invoiceStatus === "回収不能" && !knownBefore &&
        !window.confirm("回収不能を外すと入金ステータスは「未作成」になります。\n請求書を送付済み・入金待ちの案件は、外したあと請求書の状態を設定し直してください。\n外してよろしいですか？")) {
      target.checked = true;
      return true;
    }
    draft.uncollectible = nextChecked;
    render();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.projectProgressStatusId && currentRoute().page === "project-detail") {
    const p = projects.find((x) => x.id === target.dataset.projectProgressStatusId);
    if (!p) return true;
    const nextProg = String(target.dataset.projectProgressStatus || "").trim();
    if (!nextProg) return true;
    if (!target.checked) {
      target.checked = true;
      return true;
    }
    // 受注ステータスと同じく下書きに入れ、基本情報の「保存」で確定する
    const draft = projectDetailDraftFor(p);
    if (!draft) return true;
    draft.progressStatus = normalizeProjectProgressStatus(nextProg);
    render();
    return true;
  }
  return false;
}
// 画面の change 処理（16-render-routing.js の document の change ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxx(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能の change 処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の change から移した分岐（元の 2116〜2875 行）。処理したら true（16 側はそこで止める） */
function handleCsvImportChange(e, target) {
  if (target instanceof HTMLSelectElement && target.dataset.projectSalesOwner && currentRoute().page === "project-detail") {
    const proj = projects.find((x) => x.id === target.dataset.projectSalesOwner);
    if (!proj) return true;
    // 下書きに入れて基本情報の「保存」で確定する（未保存表示と離脱確認が出るので、保存し忘れて
    // リロードで戻る事故は起きない）。担当(ownerId)も営業と同じ人にする（従来の即保存と同じ）
    const draft = projectDetailDraftFor(proj);
    if (!draft) return true;
    draft.salesOwnerId = target.value;
    draft.ownerId = target.value;
    render();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.csvImportUsers !== undefined && target.files && target.files[0]) {
    const file = target.files[0];
      void (async () => {
        let text = "";
        try {
          text = await readCsvFileText(file);
        } catch {
          showToastError("CSVの読み込みに失敗しました");
          target.value = "";
          return;
        }
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        if (!authToken) {
          showToast("ログインが必要です");
          target.value = "";
          return;
        }
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(",").map((s) => s.trim());
          if (!cols[0]) continue;
          const name = cols[0];
          const email = cols[1] || "";
          const roleStr = cols[2] || "営業";
          const roles = roleStr.split("/").map((r) => normalizeJobRoleLabel(r.trim())).filter(Boolean);
          const first = roles[0] || "営業";
          let systemRole = "一般";
          let role = first;
          if (first === "マスター" || first === "管理者") {
            systemRole = first;
            role = roles[1] && JOB_ROLE_OPTIONS.includes(roles[1]) ? roles[1] : "営業";
          } else if (!JOB_ROLE_OPTIONS.includes(first)) {
            role = "営業";
          }
          rows.push({ name, email, role, systemRole, roles: [role] });
        }
        if (!rows.length) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        const total = rows.length;
        let ok = 0;
        let fail = 0;
        let lastErr = "";
        const importErrSamples = [];
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          showToastPersistent(`登録中…（${i + 1}/${total}）`);
          try {
            const res = await fetch("/api/users", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({
                name: r.name,
                email: r.email || null,
                role: r.role,
                systemRole: r.systemRole || "一般",
                roles: r.roles,
                navPermissions: defaultNavPermissionsForUser(r),
                onLeave: false,
                retired: false
              })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
              fail++;
              let msg = typeof body.error === "string" ? body.error : "登録エラー";
              msg += `（HTTP ${res.status}）`;
              const hint =
                body.column ? ` — 列: ${body.column}` :
                body.detail ? ` — ${String(body.detail).slice(0, 120)}` : "";
              lastErr = msg + hint;
              pushDistinctImportErr(importErrSamples, lastErr);
              continue;
            }
            ok++;
          } catch {
            fail++;
            lastErr = lastErr || "通信に失敗しました（ネットワークまたはサーバー）";
            pushDistinctImportErr(importErrSamples, "通信に失敗しました（ネットワークまたはサーバー）");
          }
        }
        clearToast();
        await loadDataFromDB();
        render();
        if (ok && !fail) {
          showToast(`CSVから${ok}件をデータベースに登録しました`);
        } else if (ok) {
          showToastError(
            `ユーザーCSV: ${ok}件は登録しました。${fail}件はスキップまたは失敗しました。\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`
          );
        } else {
          showToastError(
            `ユーザーCSV: ${fail}件の登録に失敗しました。\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`
          );
        }
        target.value = "";
      })();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.csvImportProjects !== undefined && target.files && target.files[0]) {
    const file = target.files[0];
    void (async () => {
        if (!isMasterRoleUser(currentUser)) {
          showToast("マスター権限が必要です");
          target.value = "";
          return;
        }
        let text = "";
        try {
          text = await readCsvFileText(file);
        } catch {
          showToastError("CSVの読み込みに失敗しました");
          target.value = "";
          return;
        }
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        if (!authToken) {
          showToast("ログインが必要です");
          target.value = "";
          return;
        }
        const accDbId = Number(String(defaultAccountingUserId()).replace("u", "")) || null;
        const postProjectRow = async (payload) => {
          const nameNorm = normalizeCsvLookupWhitespace(payload.name || "");
          const clientNorm = normalizeCsvLookupWhitespace(payload.clientName || "");
          const client = findClientForProjectCsv(payload.clientName);
          if (!client) {
            if (!clientNorm) {
              return {
                ok: false,
                err:
                  "顧客名が空です。ワイド形式では先頭行に顧客名が無い行があると列がずれたように見えます。続き行へ顧客名をコピーするか、Excelで値のコピー下方向をしてください。"
              };
            }
            const sameAsProject = nameNorm && nameNorm === clientNorm;
            const hint = sameAsProject
              ? "案件名と顧客名が同じ文字列として読まれています。カンマを含むセルはCSVでダブルクォートで囲むか、列が1つずれていないか確認してください。"
              : "顧客マスタの正式名称または略称と一致する必要があります（前後空白・連続スペースの違いは吸収します）。";
            return { ok: false, err: `顧客が見つかりません: 「${payload.clientName}」${hint}` };
          }
          const owner = findUserForProjectCsv(payload.ownerName);
          const salesUid = owner ? owner.id : currentUser.id;
          const salesNum = Number(String(salesUid).replace("u", "")) || null;
          const res = await fetch("/api/projects", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({
              clientId: client.dbId,
              name: nameNorm || String(payload.name || "").trim(),
              amount: payload.amount,
              billingMonth: payload.billingMonth || null,
              plannedInvoiceDate:
                payload.plannedInvoiceDate != null && String(payload.plannedInvoiceDate).trim() !== ""
                  ? String(payload.plannedInvoiceDate).trim().slice(0, 10)
                  : null,
              currentStage: "contracts",
              nextAction: payload.nextAction || "",
              projectType: "existing-client",
              salesOwnerId: salesNum,
              accountingOwnerId: accDbId,
              assignedUserId: salesNum,
              invoiceStatus: normalizeProjectCsvInvoiceStatus(payload.invoiceStatus),
              orderStatus: normalizeProjectCsvOrderStatus(payload.orderStatus),
              progressStatus: normalizeProjectProgressStatus(payload.progressStatus),
              tags: parseProjectDetailTags(payload.tags || "").join(", ")
            })
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) {
            const base = typeof body.error === "string" ? body.error : "登録エラー";
            return { ok: false, err: `${base}（HTTP ${res.status}）` };
          }
          const nc = body.project && body.project.id;
          if (nc == null || !Number.isFinite(Number(nc))) return { ok: false, err: "登録応答が不正です" };
          return { ok: true, projectId: `p${nc}` };
        };

        let ok = 0;
        let fail = 0;
        let lastErr = "";
        const importErrSamples = [];

        if (isWideProjectImportCsv(lines[0])) {
          const hMap = buildProjectWideHeaderMap(parseCsvRow(lines[0]));
          const rawRows = [];
          let wideCarry = { caseNo: "", name: "", clientName: "" };
          for (let i = 1; i < lines.length; i++) {
            const cols = parseCsvRow(lines[i]);
            const rec = projectWideRowFromCols(cols, hMap);
            const rowNo = String(rec.caseNo || "").trim();
            const rowName = String(rec.name || "").trim();
            const rowClient = String(rec.clientName || "").trim();
            if (rowNo && rowNo !== wideCarry.caseNo) {
              wideCarry = { caseNo: rowNo, name: rowName, clientName: rowClient };
            } else {
              wideCarry = {
                caseNo: rowNo || wideCarry.caseNo,
                name: rowName || wideCarry.name,
                clientName: rowClient || wideCarry.clientName
              };
            }
            const merged = {
              ...rec,
              caseNo: wideCarry.caseNo,
              name: wideCarry.name,
              clientName: wideCarry.clientName
            };
            if (!String(merged.name || "").trim()) continue;
            rawRows.push(merged);
          }
          const groups = groupWideProjectImportRows(rawRows);
          const groupList = [...groups.values()];
          const total = groupList.length;
          if (!total) {
            showToast("CSVに有効な案件がありません");
            target.value = "";
            return;
          }
          for (let i = 0; i < groupList.length; i++) {
            showToastPersistent(`案件CSVインポート中…（${i + 1}/${total}）`);
            const payload = projectPayloadFromWideGroup(groupList[i]);
            if (!payload) {
              fail++;
              lastErr = "案件名・顧客名が不足";
              pushDistinctImportErr(importErrSamples, lastErr);
              continue;
            }
            try {
              const r = await postProjectRow(payload);
              if (r.ok && r.projectId) {
                ok++;
                const draft = ensureProjectDocDraft(r.projectId);
                draft.lines = billingDetailLinesFromWideGroup(groupList[i]);
                markProjectDocsDraftDirty(r.projectId); // 明細は作成の POST に含まれないため自動保存で送る
                persistAllLineStaffForProject(r.projectId);
              } else {
                fail++;
                if (r.err) lastErr = r.err;
                if (r.ok && !r.projectId) lastErr = lastErr || "登録応答が不正です";
                pushDistinctImportErr(importErrSamples, lastErr);
              }
            } catch {
              fail++;
              lastErr = lastErr || "通信に失敗しました（ネットワークまたはサーバー）";
              pushDistinctImportErr(importErrSamples, "通信に失敗しました（ネットワークまたはサーバー）");
            }
          }
        } else {
          const rows = [];
          for (let i = 1; i < lines.length; i++) {
            const cols = parseCsvRow(lines[i]);
            const entry = {};
            PROJECT_LEGACY_CSV_COLUMNS.forEach((col, idx) => {
              const raw = cols[idx];
              entry[col.key] = raw !== undefined && raw !== "" ? raw : (col.fallback || "");
            });
            const name = String(entry.name || "").trim();
            if (!name) continue;
            rows.push(entry);
          }
          if (!rows.length) {
            showToast("CSVにデータがありません");
            target.value = "";
            return;
          }
          const total = rows.length;
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            showToastPersistent(`案件CSVインポート中…（${i + 1}/${total}）`);
            try {
              const pr = await postProjectRow({
                name: String(r.name || "").trim(),
                clientName: r.clientName,
                amount: parseProjectCsvAmount(r.amount),
                billingMonth: String(r.billingMonth || "").trim() || null,
                ownerName: r.ownerName,
                orderStatus: r.orderStatus,
                progressStatus: normalizeProjectProgressStatus(r.progressStatus),
                invoiceStatus: "未作成",
                nextAction: "",
                tags: parseProjectDetailTags(r.tags || "").join(", ")
              });
              if (pr.ok) {
                ok++;
                parseProjectDetailTags(r.tags || "").forEach((t) => rememberProjectDetailTagPreset(t, { fromServer: true }));
              }
              else {
                fail++;
                if (pr.err) lastErr = pr.err;
                pushDistinctImportErr(importErrSamples, pr.err || lastErr);
              }
            } catch {
              fail++;
              lastErr = lastErr || "通信に失敗しました（ネットワークまたはサーバー）";
              pushDistinctImportErr(importErrSamples, "通信に失敗しました（ネットワークまたはサーバー）");
            }
          }
        }
        clearToast();
        await loadDataFromDB({ mode: "heavy" });
        render();
        if (ok && !fail) {
          showToast(`CSVから${ok}件の案件を登録しました`);
        } else if (ok) {
          showToastError(
            `案件CSV: ${ok}件は登録しました。${fail}件はスキップまたは失敗しました。\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`
          );
        } else {
          showToastError(`案件CSV: 登録に失敗しました。\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`);
        }
        target.value = "";
    })();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.domainCsvImport !== undefined && target.files && target.files[0]) {
    const file = target.files[0];
    void (async () => {
      let text = "";
      try {
        text = await readCsvFileText(file);
      } catch {
        showToastError("CSVの読み込みに失敗しました");
        target.value = "";
        return;
      }
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        showToast("CSVにデータがありません");
        target.value = "";
        return;
      }
      if (!authToken) {
        showToast("ログインが必要です");
        target.value = "";
        return;
      }
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvRow(lines[i]);
        const entry = {};
        DOMAIN_CSV_COLUMNS.forEach((col, idx) => {
          const raw = cols[idx];
          entry[col.key] = raw !== undefined && raw !== "" ? raw : (col.fallback || "");
        });
        if (!String(entry.domain || "").trim() && !String(entry.subdomain || "").trim()) continue;
        rows.push(entry);
      }
      if (!rows.length) {
        showToast("CSVに有効なデータがありません");
        target.value = "";
        return;
      }
      const total = rows.length;
      let ok = 0;
      let fail = 0;
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        showToastPersistent(`ドメイン登録中…（${i + 1}/${total}）`);
        try {
          const res = await fetch("/api/domains", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ ...r, upsert: true })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.ok && data.domain) {
            const nd = data.domain;
            const mapped = {
              id: `dm${nd.id}`, dbId: nd.id,
              status: nd.status || "契約",
              service: nd.service || "",
              services: nd.services || "",
              subdomain: nd.subdomain || "", domain: nd.domain || "",
              registrarId: nd.registrar_id || "", registrarPassword: nd.registrar_password || "",
              clientName: nd.client_name || "", owner: nd.owner != null ? String(nd.owner).trim() : "",
              projectNote: nd.project_note || "",
              renewedDate: pgDateFieldToYmd(nd.renewed_date) || "",
              notes: nd.notes || ""
            };
            // 上書き(upsert)時は既存行を置き換え、新規時のみ先頭に追加
            const existingIdx = domains.findIndex((x) => x.dbId === nd.id);
            if (existingIdx >= 0) domains[existingIdx] = mapped;
            else domains.unshift(mapped);
            ok++;
          } else {
            fail++;
          }
        } catch {
          fail++;
        }
      }
      clearToast();
      render();
      showToast(`ドメインCSV取込: 成功 ${ok}件${fail ? ` / 失敗 ${fail}件` : ""}`);
      target.value = "";
    })();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.csvImportClients !== undefined && target.files && target.files[0]) {
    // 描画側（csvImportToolHtml）と対で権限を見る。ボタンを隠すだけだと、
    // 開いたままの古いタブや DOM 直叩きで取込が走る
    if (!isMasterRoleUser(currentUser)) {
      showToast("CSVインポートはマスターのみ可能です（経理の登録は取引先申請から）");
      target.value = "";
      return true;
    }
    const file = target.files[0];
    void (async () => {
        let text = "";
        try {
          text = await readCsvFileText(file);
        } catch {
          showToastError("CSVの読み込みに失敗しました");
          target.value = "";
          return;
        }
        const lines = splitCsvRecords(text);
        if (lines.length < 2) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        if (!authToken) {
          showToast("ログインが必要です");
          target.value = "";
          return;
        }
        // ヘッダー名で列を特定する（列順固定だと契約一覧のダウンロードCSV等で列ずれ事故になるため）
        const clientHeaderCells = parseCsvRow(lines[0]);
        const clientHeaderMap = buildCsvHeaderMap(clientHeaderCells);
        if (csvHeaderIndexForColumn(clientHeaderMap, CLIENT_CSV_COLUMNS[0]) === undefined) {
          showToastError("CSVの1行目に「会社名」（または「顧客名」）列が見つかりません。ヘッダー行つきのCSV（クライアントCSVテンプレート形式）を使ってください");
          target.value = "";
          return;
        }
        // CSVにヘッダーが存在する列だけを更新対象にする（無い列を既定値で潰さない）
        const clientPresentKeys = new Set(
          CLIENT_CSV_COLUMNS.filter((col) => csvHeaderIndexForColumn(clientHeaderMap, col) !== undefined).map((col) => col.key)
        );
        const clientClassIdx = clientHeaderMap[normalizeCsvHeaderLabel("分類")];
        let skippedPartnerRows = 0;
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          if (/^[,\s]*$/.test(lines[i])) continue; // Excel由来のカンマだけの行
          const cols = parseCsvRow(lines[i]);
          // 契約一覧CSV（クライアント/パートナー混在）対応: 分類=パートナーの行は取り込まない
          if (clientClassIdx !== undefined && String(cols[clientClassIdx] ?? "").normalize("NFKC").trim() === "パートナー") {
            skippedPartnerRows++;
            continue;
          }
          const entry = {};
          CLIENT_CSV_COLUMNS.forEach((col) => {
            const raw = csvRawCellForColumn(cols, clientHeaderMap, col);
            entry[col.key] = raw !== undefined && raw !== "" ? raw : (col.fallback || "");
          });
          const name = entry.name || "";
          if (!name) continue;
          if (!entry.shortName) entry.shortName = name;
          rows.push(entry);
        }
        if (!rows.length) {
          showToast(skippedPartnerRows ? "取り込めるクライアント行がありません（分類=パートナーの行のみでした）" : "CSVにデータがありません");
          target.value = "";
          return;
        }
        const total = rows.length;
        let ok = 0;
        let fail = 0;
        let lastErr = "";
        const importErrSamples = [];
        /** 同一CSV内の重複行や、POST直後でまだ clients に載っていない id 解決用 */
        const csvImportDbIdByCompanyName = new Map();
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          showToastPersistent(`クライアント登録中…（${i + 1}/${total}）`);
          try {
            const nameKey = String(r.name || "").trim();
            let dbIdKnown = csvImportDbIdByCompanyName.get(nameKey);
            let existing = dbIdKnown != null ? clients.find((x) => x.dbId === dbIdKnown) : null;
            if (dbIdKnown == null) {
              existing = findClientForProjectCsv(r.name);
              dbIdKnown = existing?.dbId;
            }
            const shared = {
              name: r.name,
              shortName: r.shortName,
              honorific: r.honorific || "",
              postalCode: r.postalCode || "",
              prefecture: r.prefecture || "東京都",
              address: r.address || "",
              building: r.building || "",
              tel: r.tel || "",
              fax: r.fax || "",
              hpUrl: r.hpUrl || "",
              contactName: r.contactName,
              email: r.email || null,
              contractOwner: r.contractOwner != null && String(r.contractOwner).trim() !== "" ? String(r.contractOwner).trim() : null,
              paymentTerms: r.paymentTerms,
              transferFeeBurden: r.transferFeeBurden,
              ndaStatus: normalizeClientCsvDocStatus(r.ndaStatus),
              contractStatus: normalizeClientCsvDocStatus(r.contractStatus),
              deliveryMethod: normalizeDeliveryMethodStored(r.deliveryMethod || ""),
              notes: r.notes,
              corporateNumber: r.corporateNumber || "",
              customerNo: r.customerNo || "",
              invoiceIssuer: r.invoiceIssuer || "未設定",
              invoiceIssuerNumber: r.invoiceIssuerNumber || "",
              // サーバは解釈できる値が来たときだけ更新（空・不正は既存値維持＝クリア不可）なので更新時も送ってよい
              firstRegisteredAt: r.firstRegisteredAt || "",
              // CSVに取引予定金額の列が無いため、新規登録時必須チェックの対象外にする（パートナーのupsertと同趣旨）
              csvImport: true
            };
            // 更新時はCSVにヘッダーが存在する列だけ送る（サーバは部分更新＝送らないキーは既存値を維持）
            const putBody = {
              ...shared,
              id: dbIdKnown,
              ndaDate: existing?.ndaDate || null,
              ndaNote: existing?.ndaNote || "",
              contractDate: existing?.contractDate || null,
              contractNote: existing?.contractNote || "",
              approvalStatus: normalizeMasterApprovalStatus(existing?.approvalStatus ?? "未申請")
            };
            CLIENT_CSV_COLUMNS.forEach((col) => {
              if (!clientPresentKeys.has(col.key) && col.key in putBody) delete putBody[col.key];
            });
            const res = dbIdKnown != null
              ? await fetch("/api/clients", {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify(putBody)
                })
              : await fetch("/api/clients", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify(shared)
                });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
              fail++;
              let msg = typeof body.error === "string" ? body.error : "登録／更新エラー";
              msg += `（HTTP ${res.status}）`;
              lastErr = msg;
              pushDistinctImportErr(importErrSamples, lastErr);
              continue;
            }
            ok++;
            if (dbIdKnown != null) {
              csvImportDbIdByCompanyName.set(nameKey, dbIdKnown);
            } else if (body.client && body.client.id != null) {
              csvImportDbIdByCompanyName.set(nameKey, body.client.id);
            }
          } catch {
            fail++;
            lastErr = lastErr || "通信に失敗しました（ネットワークまたはサーバー）";
            pushDistinctImportErr(importErrSamples, "通信に失敗しました（ネットワークまたはサーバー）");
          }
        }
        clearToast();
        await loadDataFromDB();
        render();
        const skippedNote = skippedPartnerRows ? `（分類=パートナーの${skippedPartnerRows}行は対象外）` : "";
        if (ok && !fail) showToast(`CSVから${ok}件のクライアントを登録しました${skippedNote}`);
        else if (ok)
          showToastError(
            `クライアントCSV: ${ok}件は登録しました。${fail}件はスキップまたは失敗しました。${skippedNote}\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`
          );
        else
          showToastError(`クライアントCSV: 登録に失敗しました。${skippedNote}\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`);
        target.value = "";
    })();
    return true;
  }
  if (target instanceof HTMLInputElement && target.dataset.csvImportPartners !== undefined && target.files && target.files[0]) {
    // 描画側（csvImportToolHtml）と対で権限を見る。ボタンを隠すだけだと、
    // 開いたままの古いタブや DOM 直叩きで取込が走る
    if (!isMasterRoleUser(currentUser)) {
      showToast("CSVインポートはマスターのみ可能です（経理の登録は取引先申請から）");
      target.value = "";
      return true;
    }
    const file = target.files[0];
    void (async () => {
        let text = "";
        try {
          text = await readCsvFileText(file);
        } catch {
          showToastError("CSVの読み込みに失敗しました");
          target.value = "";
          return;
        }
        const lines = splitCsvRecords(text);
        if (lines.length < 2) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        if (!authToken) {
          showToast("ログインが必要です");
          target.value = "";
          return;
        }
        const partnerHeaderCells = parseCsvRow(lines[0]);
        const partnerHeaderMap = buildCsvHeaderMap(partnerHeaderCells);
        if (csvHeaderIndexForColumn(partnerHeaderMap, PARTNER_CSV_COLUMNS[0]) === undefined) {
          showToastError("CSVの1行目に「発注先名」（または「会社名」）列が見つかりません。ヘッダー行つきのCSV（パートナーCSVテンプレート形式）を使ってください");
          target.value = "";
          return;
        }
        // CSVにヘッダーが存在する列だけを更新対象にする（無い列を既定値で潰さない）
        const partnerPresentKeys = new Set(
          PARTNER_CSV_COLUMNS.filter((col) => csvHeaderIndexForColumn(partnerHeaderMap, col) !== undefined).map((col) => col.key)
        );
        const partnerClassIdx = partnerHeaderMap[normalizeCsvHeaderLabel("分類")];
        let skippedClientRows = 0;
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          if (/^[,\s]*$/.test(lines[i])) continue; // Excel由来のカンマだけの行
          const cols = parseCsvRow(lines[i]);
          // 契約一覧CSV（クライアント/パートナー混在）対応: 分類=クライアントの行は取り込まない
          if (partnerClassIdx !== undefined && String(cols[partnerClassIdx] ?? "").normalize("NFKC").trim() === "クライアント") {
            skippedClientRows++;
            continue;
          }
          const entry = {};
          PARTNER_CSV_COLUMNS.forEach((col) => {
            const raw = csvRawCellForColumn(cols, partnerHeaderMap, col);
            entry[col.key] = raw !== undefined && raw !== "" ? raw : (col.fallback || "");
          });
          if (!entry.name) continue;
          entry.__line = i + 1; // 元CSVの行番号（1始まり・ヘッダー行=1）
          rows.push(entry);
        }
        if (!rows.length) {
          showToast("CSVにデータがありません");
          target.value = "";
          return;
        }
        const total = rows.length;
        let ok = 0;
        let fail = 0;
        let revived = 0;
        let lastErr = "";
        const importErrSamples = [];
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          const rowNo = r.__line || i + 2; // スキップ行があってもCSVの実行番号を指す
          const rowLabel = `${rowNo}行目（${String(r.name || "").trim() || "名称未設定"}）`;
          showToastPersistent(`パートナー登録中…（${i + 1}/${total}）`);
          try {
            // CSVにヘッダーが存在する列だけ送る（サーバのupsert更新は送らないキーの列を維持）
            const partnerBody = {
                upsert: true,
                name: r.name,
                branch: r.branch || "",
                honorific: r.honorific || "",
                contactName: r.contactName || "",
                contactEmail: r.contactEmail || null,
                contactTel: r.contactTel || "",
                fax: r.fax || "",
                postalCode: r.postalCode || "",
                prefecture: r.prefecture || "",
                address: r.address || "",
                building: r.building || "",
                paymentTerms: r.paymentTerms || "月末締翌月末払",
                paymentMethod: r.paymentMethod || "銀行振込",
                transferFeeBurden: r.transferFeeBurden || "先方",
                deliveryMethod: normalizeDeliveryMethodStored(r.deliveryMethod || "メール(DL)"),
                bankInfo: r.bankInfo || "",
                withholdingTax: r.withholdingTax || "なし",
                category1: r.category1 || "",
                category2: r.category2 || "",
                category3: r.category3 || "",
                ndaStatus: normalizeClientCsvDocStatus(r.ndaStatus),
                contractStatus: normalizeClientCsvDocStatus(r.contractStatus),
                notes: r.notes || "",
                firstRegisteredAt: r.firstRegisteredAt || "",
                corporateNumber: r.corporateNumber || "",
                customerNo: r.customerNo || "",
                invoiceIssuer: r.invoiceIssuer || "未設定",
                invoiceIssuerNumber: r.invoiceIssuerNumber || "",
                contractOwner: r.contractOwner || ""
            };
            PARTNER_CSV_COLUMNS.forEach((col) => {
              if (!partnerPresentKeys.has(col.key) && col.key in partnerBody) delete partnerBody[col.key];
            });
            // 空セルは既定値に置換して送らず、キーごと省いて既存値を維持する
            // （upsert 再取込の空セルが支払条件等を既定へ巻き戻す事故の防止。新規行はサーバー側で同じ既定が入る）
            [
              ["paymentTerms", r.paymentTerms],
              ["paymentMethod", r.paymentMethod],
              ["transferFeeBurden", r.transferFeeBurden],
              ["deliveryMethod", r.deliveryMethod],
              ["withholdingTax", r.withholdingTax],
              ["invoiceIssuer", r.invoiceIssuer]
            ].forEach(([key, raw]) => {
              if (key in partnerBody && String(raw ?? "").trim() === "") delete partnerBody[key];
            });
            const res = await fetch("/api/partners", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              body: JSON.stringify(partnerBody)
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
              fail++;
              let msg = typeof body.error === "string" ? body.error : "登録エラー";
              msg += `（HTTP ${res.status}）`;
              lastErr = `${rowLabel}: ${msg}`;
              pushDistinctImportErr(importErrSamples, lastErr);
              continue;
            }
            ok++;
            if (body && body.revived) revived++;
          } catch {
            fail++;
            lastErr = `${rowLabel}: 通信に失敗しました（ネットワークまたはサーバー）`;
            pushDistinctImportErr(importErrSamples, lastErr);
          }
        }
        clearToast();
        await loadDataFromDB();
        render();
        const partnerNoteItems = [];
        if (revived) partnerNoteItems.push(`うち${revived}件は削除済みを復活して更新`);
        if (skippedClientRows) partnerNoteItems.push(`分類=クライアントの${skippedClientRows}行は対象外`);
        const partnerNotes = partnerNoteItems.length ? `（${partnerNoteItems.join("／")}）` : "";
        if (ok && !fail) showToast(`CSVから${ok}件のパートナーを登録しました${partnerNotes}`);
        else if (ok)
          showToastError(
            `パートナーCSV: ${ok}件は登録しました。${fail}件はスキップまたは失敗しました。${partnerNotes}\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`
          );
        else showToastError(`パートナーCSV: 登録に失敗しました。\n${summarizeCsvImportErrors(importErrSamples, lastErr, fail)}`);
        target.value = "";
    })();
    return true;
  }
  return false;
}
// 画面の change 処理（16-render-routing.js の document の change ハンドラから機能ごとに移したもの・2026-10）。
// 16 側は元の位置で上から順に `if (handleXxx(e, target)) return;` と呼ぶ（呼ぶ順番＝従来の分岐の順番）。
// 新しい機能の change 処理はここに足さず、機能別ファイルに独立の document.addEventListener を置く（CLAUDE.md）。

/** 16-render-routing.js の change から移した分岐（元の 2876〜3217 行）。処理したら true（16 側はそこで止める） */
function handleProjectCostChange(e, target) {
  if (target instanceof HTMLInputElement && target.dataset.csvImportBudgetMaster !== undefined && target.files && target.files[0]) {
    const file = target.files[0];
    void (async () => {
      let text = "";
      try {
        text = await readCsvFileText(file);
      } catch {
        showToastError("CSVの読み込みに失敗しました");
        target.value = "";
        return;
      }
      ensureBudgetPeriodsState();
      const period = uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(uiState.budgetPeriods);
      if (!period) {
        showToast("会計期を選択してください");
        target.value = "";
        return;
      }
      const pn = Number(period.periodNumber) || parseBudgetFiscalPeriodNumberFromLabel(period.label);
      const periodLabel = pn ? `${pn}期` : String(period.label || "").trim() || "この会計期";
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) {
        showToast("CSVにデータがありません");
        target.value = "";
        return;
      }
      const fyStart = fiscalYearStartFromBudgetPeriod(period);
      const matrixResult = parseBudgetMatrixCsv(lines, fyStart);
      let parsed = [];
      if (matrixResult) {
        if (matrixResult.empty) {
          showToast(matrixResult.message);
          target.value = "";
          return;
        }
        parsed = matrixResult.rows.slice(0, 500);
      } else {
        const headCells = parseCsvRow(lines[0]);
        const headerMap = mapBudgetMasterCsvHeaders(headCells);
        const monthsOk = BUDGET_MONTH_KEYS.every((k) => headerMap[k] >= 0);
        let colMap;
        let bodyStart;
        if (monthsOk && headerMap.member >= 0) {
          colMap = headerMap;
          bodyStart = 1;
        } else {
          const c0 = parseCsvRow(lines[0]);
          if (c0.length >= BUDGET_MASTER_CSV_FIXED_COL_COUNT) {
            colMap = budgetMasterCsvFixedColumnMap();
            bodyStart = 0;
          } else {
            showToast(
              "CSVの列が足りません。1行目に「部署,担当者,4月,…,3月」のヘッダーを付けるか、列順でそろえるか、「指標」列付きスプレッドシート形式にしてください。"
            );
            target.value = "";
            return;
          }
        }
        const maxRows = 500;
        for (let i = bodyStart; i < lines.length; i++) {
          if (parsed.length >= maxRows) break;
          const cells = parseCsvRow(lines[i]);
          if (cells.every((c) => !String(c || "").trim())) continue;
          const row = rowFromBudgetCsvCells(cells, colMap);
          const member = String(row.member || "").trim();
          const hasMonth = BUDGET_MONTH_KEYS.some((k) => parseBudgetNumberInput(row[k]) !== 0);
          if (!member && !hasMonth && !String(row.dept || "").trim()) continue;
          if (!member) continue;
          parsed.push(row);
        }
      }
      if (!parsed.length) {
        showToast("取り込める行がありません（担当者名と数値列を確認してください）");
        target.value = "";
        return;
      }
      const matrixNote =
        matrixResult && matrixResult.rows
          ? matrixResult.sheetLegacy
            ? "（社内予算表形式: 月次が空の行は「月平均」または「N期合計」列から各月へ展開します）"
            : "（スプレッドシート形式: 取り込んだ売上・差益は予算進捗で優先されます）"
          : "";
      if (!window.confirm(`「${periodLabel}」の予算行を、CSVの${parsed.length}件で置き換えます${matrixNote}。よろしいですか？`)) {
        target.value = "";
        return;
      }
      if (!uiState.budgetRowsByPeriod) uiState.budgetRowsByPeriod = {};
      uiState.budgetRowsByPeriod[period.id] = { rows: parsed, extendSales: false };
      saveBudgetDraftStore(uiState.budgetRowsByPeriod);
      const saved = await persistBudgetMasterRowsToServer();
      if (saved) {
        uiState.budgetMasterDirty = false;
        showToast(
          matrixResult && matrixResult.rows
            ? matrixResult.sheetLegacy
              ? `社内予算表形式のCSVから${parsed.length}件（担当単位）を取り込みました`
              : `スプレッドシート形式のCSVから${parsed.length}件（担当単位の行）を取り込みました`
            : `CSVから${parsed.length}件を取り込みました`
        );
      } else {
        showToastError("CSVは読み込みましたが、サーバー保存に失敗しました。通信状態を確認して再度保存してください。");
      }
      target.value = "";
      render();
    })();
    return true;
  }
  if (target.dataset.budgetRowField && (currentRoute().page === "budget-admin" || currentRoute().page === "management")) {
    ensureBudgetPeriodsState();
    const period = uiState.budgetPeriods.find((p) => p.id === uiState.selectedBudgetPeriodId) || latestBudgetPeriodFromSorted(uiState.budgetPeriods);
    if (!period) return true;
    const idx = Number.parseInt(target.dataset.budgetRowIdx || "-1", 10);
    const rows = ensureBudgetRowsForSelectedPeriod();
    if (idx < 0 || idx >= rows.length) return true;
    const field = target.dataset.budgetRowField;
    if (!["dept", "member", ...BUDGET_MONTH_KEYS].includes(field)) return true;
    rows[idx][field] = target.value;
    if (!uiState.budgetRowsByPeriod) uiState.budgetRowsByPeriod = {};
    uiState.budgetRowsByPeriod[period.id] = { rows, extendSales: true };
    // ここで render() すると blur のたびに全再描画されてフォーカスが失われ、
    // Tab移動不能・行追加/削除ボタンの click 不発の原因になる。編集はドラフトに溜め、
    // サーバー保存は「保存」ボタン（15e-budget-master.js）で明示的に行う。
    saveBudgetDraftStore(uiState.budgetRowsByPeriod, { persistToServer: false });
    markBudgetMasterDirty();
    updateBudgetMasterRowTotalCell(target);
    return true;
  }
  if (target.dataset.pdField && projectDetailFormDraftActive()) {
    projectDetailFormDraftActive()[target.dataset.pdField] = target.value;
    return true;
  }
  if (target.dataset.pdCheck && projectDetailFormDraftActive() && target instanceof HTMLInputElement) {
    projectDetailFormDraftActive()[target.dataset.pdCheck] = target.checked;
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.costReviewPick !== undefined &&
    uiState.costRegWizard?.phase === "preview" &&
    Array.isArray(uiState.costRegWizard.rows)
  ) {
    // 状態更新のみ。render() は不要（スクロール位置リセット防止）。
    // 表示更新は sd-select の change リスナー（enhanceSearchableSelects 内）が担う。
    const idx = Number.parseInt(target.dataset.costReviewPick || "-1", 10);
    if (!Number.isNaN(idx) && uiState.costRegWizard.rows[idx]) {
      uiState.costRegWizard.rows[idx].projectIdPick = target.value;
    }
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.costWizardManualProject !== undefined &&
    uiState.costRegWizard?.phase === "manual"
  ) {
    uiState.costRegWizard.manualProjectId = target.value;
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "month" &&
    target.dataset.costWizardManualMonth != null &&
    uiState.costRegWizard?.phase === "manual"
  ) {
    const tm = billingMonthStoredFromMonthInput(target.value);
    if (tm) uiState.costRegWizard.targetMonth = tm;
    costWizardPruneManualProjectToTargetMonth();
    render();
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.costImportCategoryPick !== undefined &&
    uiState.costRegWizard?.phase === "preview"
  ) {
    if (COST_CATEGORIES.includes(target.value)) {
      uiState.costRegWizard.importCategory = target.value;
    }
    return true;
  }
  if (
    target instanceof HTMLSelectElement &&
    target.dataset.documentPayeeSelect !== undefined &&
    uiState.costRegWizard?.phase === "preview" &&
    uiState.costRegWizard.previewKind === "pdf"
  ) {
    uiState.costRegWizard.documentPayeeUiKey = target.value;
    applyDocumentPayeeUiKeyToImportRows(uiState.costRegWizard);
    render();
    return true;
  }
  if (target.dataset.clientListPerPage && currentRoute().page === "client-list") {
    uiState.clientList.perPage = Number.parseInt(target.value || "20", 10) || 20;
    uiState.clientList.page = 1;
    render();
    return true;
  }
  if (target.dataset.partnerListPerPage && currentRoute().page === "partner-list") {
    uiState.partnerList.perPage = Number.parseInt(target.value || "20", 10) || 20;
    uiState.partnerList.page = 1;
    render();
    return true;
  }
  if (target.dataset.domainListPerPage && currentRoute().page === "domain-list") {
    uiState.domainList.perPage = Number.parseInt(target.value || "20", 10) || 20;
    uiState.domainList.page = 1;
    render();
    return true;
  }
  if (target.dataset.myAccountAvatar && uiState.myAccountEditor && target instanceof HTMLInputElement && target.files?.[0]) {
    const file = target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      uiState.myAccountEditor.avatar = typeof reader.result === "string" ? reader.result : "";
      render();
    };
    reader.readAsDataURL(file);
    return true;
  }
  if (target.id === "bp-periodSelect" && isBudgetProgressSharedRoute()) {
    window.budgetProgressOnPeriodChange?.(target);
    if (ensureSelectedBudgetRowsRequested()) render();
    return true;
  }
  if (target.dataset.billingMonthFilter) {
    uiState.quickFilter.invoiceBillingMonth = target.value;
    render();
    return true;
  }
  if (target.dataset.projectFilterLookup) {
    const key = String(target.dataset.projectFilterLookup || "");
    if (key === "clientId") {
      uiState.projectListFilters.clientNameQuery = target.value;
      uiState.projectListFilters.clientId = findProjectClientFilterIdByName(target.value);
      setProjectListPage(1);
      render();
      return true;
    }
  }
  if (target.dataset.projectFilter) {
    const cpf = String(target.dataset.projectFilter || "");
    uiState.projectListFilters[cpf] = target.value;
    // 一括変更の「受注状況」は絞り込みではないので、ページ位置を戻さない
    if (cpf !== "bulkStatus") setProjectListPage(1);
    // pj-month-wrap: 値がある場合に has-value クラスを付与（::after ヒントを隠す）
    const wrap = target.closest(".pj-month-wrap");
    if (wrap instanceof HTMLElement) {
      wrap.classList.toggle("has-value", String(target.value || "").trim() !== "");
    }
    // name/no はキーワード欄。input側で_debouncedRender()により入力中に自動反映されるが、
    // ブラウザのオートフィル等でinputが発火せずchangeだけ来るケースに備え、ここは即render()する。
    // input リスナー側と同じガード。change はここも通るため、片側だけだとSPでピッカーを壊す
    // 入金日（type=date）も SP ではピッカー操作中に再描画しない（月と同じ扱い）
    if (target instanceof HTMLInputElement && (target.type === "month" || target.type === "date") && deferRenderWhileMonthPicking(target)) return true;
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "checkbox" &&
    target.dataset.projectAssigneeFilter != null &&
    currentRoute().page === "projects"
  ) {
    const key = String(target.dataset.projectAssigneeFilter || "").trim();
    normalizeProjectListAssigneeFilters(uiState.projectListFilters);
    let arr = [...(uiState.projectListFilters.assignedUserIds || [])];
    if (target.checked) {
      if (key && !arr.includes(key)) arr.push(key);
    } else {
      arr = arr.filter((x) => x !== key);
    }
    uiState.projectListFilters.assignedUserIds = sanitizeProjectListAssigneeFilterIds(arr);
    delete uiState.projectListFilters.assignedUserId;
    delete uiState.projectListFilters.assignedUserNameQuery;
    setProjectListPage(1);
    render();
    return true;
  }
  if (
    target instanceof HTMLInputElement &&
    target.type === "month" &&
    currentRoute().page === "cost-management" &&
    target.hasAttribute("data-cost-mgmt-filter-month")
  ) {
    syncCostMgmtFilterFromDom();
    render();
    return true;
  }
  // 原価登録: 発注先・支払方法・ロック等のプルダウン変更で即絞り込み
  if (
    target instanceof HTMLSelectElement &&
    currentRoute().page === "cost-management" &&
    (target.hasAttribute("data-cost-mgmt-filter-vendor") ||
      target.hasAttribute("data-cost-mgmt-filter-pay-method") ||
      target.hasAttribute("data-cost-mgmt-filter-lock") ||
      target.hasAttribute("data-cost-mgmt-filter-vendor-type") ||
      target.hasAttribute("data-cost-mgmt-filter-cost-kind"))
  ) {
    syncCostMgmtFilterFromDom();
    render();
    return true;
  }
  if (target.dataset.projectPerPage) {
    uiState.projectTable.perPage = Number.parseInt(target.value || "20", 10) || 20;
    setProjectListPage(1);
    render();
    return true;
  }
  // 税率セレクタ変更時: 合計表示をリアルタイム更新
  if (target.dataset.field === "taxRate" && uiState.composer) {
    uiState.composer.taxRate = Number(target.value);
    render();
    return true;
  }
  // 原価フォームのセレクト変更（categoryはrender()して補助テキスト・支払期限フィールドを更新）
  if (target.dataset.costField && uiState.costForm) {
    const key = target.dataset.costField;
    if (key === "amount" || key === "consumptionTax") {
      const n = String(target.value).normalize("NFKC").replace(/,/g, "").replace(/[^\d.-]/g, "");
      uiState.costForm[key] = n === "" ? 0 : Number(n);
      if (key === "amount") {
        syncCostFormTaxFromAmount();
        syncCostFormWithholdingFromVendor();
      }
    } else if (key === "targetMonth") {
      uiState.costForm[key] = String(target.value || "").replace(/-/g, "/");
      render();
      return true;
    } else {
      uiState.costForm[key] = target.value;
    }
    // 請求日・発注先が決まったら、パートナーマスタから支払期限・支払方法・源泉所得税を自動補完（手修正可）
    if ((key === "invoiceDate" || key === "vendor" || key === "category") && applyCostFormPartnerAutofill(key)) {
      render();
      return true;
    }
    // 発注先の選択は発注申請欄（T番号の入力有無）の出し分けに効くので描き直す
    if (key === "category" || key === "vendor") render();
    return true;
  }
  if (target.dataset.lineIndex && target.dataset.lineField && uiState.composer) {
    render();
  }
  return false;
}
// ToDo管理: カンバン列間ドラッグでステータス変更
// ネイティブDnDは入れ子の overflow-x コンテナ（.pm-kanban-scroll）を自動スクロールしないため、
// 端に寄せたら手動でスクロールさせる（Backlog等の外部ツールと同等の挙動に合わせる）
let pmKanbanAutoScrollEl = null;
let pmKanbanAutoScrollDir = 0;
let pmKanbanAutoScrollRAF = null;
const PM_KANBAN_AUTOSCROLL_EDGE = 60;
const PM_KANBAN_AUTOSCROLL_MAX_SPEED = 18;

function pmKanbanAutoScrollStep() {
  if (!pmKanbanAutoScrollEl || !pmKanbanAutoScrollDir) {
    pmKanbanAutoScrollRAF = null;
    return;
  }
  pmKanbanAutoScrollEl.scrollLeft += pmKanbanAutoScrollDir;
  pmKanbanAutoScrollRAF = requestAnimationFrame(pmKanbanAutoScrollStep);
}

function pmKanbanSetAutoScroll(el, dir) {
  pmKanbanAutoScrollEl = dir ? el : null;
  pmKanbanAutoScrollDir = dir;
  if (dir && !pmKanbanAutoScrollRAF) {
    pmKanbanAutoScrollRAF = requestAnimationFrame(pmKanbanAutoScrollStep);
  } else if (!dir && pmKanbanAutoScrollRAF) {
    cancelAnimationFrame(pmKanbanAutoScrollRAF);
    pmKanbanAutoScrollRAF = null;
  }
}

document.addEventListener("dragstart", (e) => {
  const card = e.target?.closest?.("[data-pm-drag-agenda]");
  if (!(card instanceof HTMLElement) || currentRoute().page !== "progress-management") return;
  if (!authToken) {
    e.preventDefault();
    return;
  }
  const id = card.getAttribute("data-pm-drag-agenda") || "";
  e.dataTransfer.setData("text/plain", id);
  e.dataTransfer.effectAllowed = "move";
  card.classList.add("pm-kan-card--dragging");
});

document.addEventListener("dragend", (e) => {
  const card = e.target?.closest?.("[data-pm-drag-agenda]");
  if (card instanceof HTMLElement) {
    card.classList.remove("pm-kan-card--dragging");
    window.__pmKanbanSuppressAgendaClickUntil = Date.now() + 280;
  }
  document.querySelectorAll(".pm-kan-col-body--drag-over").forEach((el) => el.classList.remove("pm-kan-col-body--drag-over"));
  pmKanbanSetAutoScroll(null, 0);
});

document.addEventListener("dragover", (e) => {
  if (currentRoute().page !== "progress-management") return;
  const scrollEl = e.target instanceof Element ? e.target.closest(".pm-kanban-scroll") : null;
  if (scrollEl instanceof HTMLElement) {
    const rect = scrollEl.getBoundingClientRect();
    const x = e.clientX;
    let dir = 0;
    if (x < rect.left + PM_KANBAN_AUTOSCROLL_EDGE) {
      const ratio = 1 - Math.max(0, x - rect.left) / PM_KANBAN_AUTOSCROLL_EDGE;
      dir = -Math.max(4, Math.round(PM_KANBAN_AUTOSCROLL_MAX_SPEED * ratio));
    } else if (x > rect.right - PM_KANBAN_AUTOSCROLL_EDGE) {
      const ratio = 1 - Math.max(0, rect.right - x) / PM_KANBAN_AUTOSCROLL_EDGE;
      dir = Math.max(4, Math.round(PM_KANBAN_AUTOSCROLL_MAX_SPEED * ratio));
    }
    pmKanbanSetAutoScroll(scrollEl, dir);
  } else {
    pmKanbanSetAutoScroll(null, 0);
  }
  const dz = e.target?.closest?.("[data-pm-kan-drop-status]");
  if (!(dz instanceof HTMLElement)) return;
  e.preventDefault();
  e.dataTransfer.dropEffect = "move";
  document.querySelectorAll(".pm-kan-col-body--drag-over").forEach((el) => {
    if (el !== dz) el.classList.remove("pm-kan-col-body--drag-over");
  });
  dz.classList.add("pm-kan-col-body--drag-over");
});

document.addEventListener("drop", (e) => {
  const dz = e.target?.closest?.("[data-pm-kan-drop-status]");
  if (!(dz instanceof HTMLElement) || currentRoute().page !== "progress-management") return;
  e.preventDefault();
  dz.classList.remove("pm-kan-col-body--drag-over");
  pmKanbanSetAutoScroll(null, 0);
  if (!authToken) return;
  const agendaId = Number(e.dataTransfer.getData("text/plain") || "");
  const nextStatusRaw = dz.getAttribute("data-pm-kan-drop-status") || "";
  if (!Number.isFinite(agendaId) || agendaId < 1 || !nextStatusRaw.trim()) return;
  const found = findProgressAgendaById(agendaId);
  const norm = found?.challenge
    ? normalizeProgressTodoStatusForChallenge(nextStatusRaw, found.challenge)
    : normalizeProgressTodoStatus(nextStatusRaw);
  const cur = found
    ? found.challenge
      ? normalizeProgressTodoStatusForChallenge(found.agenda.status, found.challenge)
      : normalizeProgressTodoStatus(found.agenda.status)
    : "";
  if (cur === norm) return;
  // 「完了」への変更は作成者のみ許可（クライアント側チェック）
  if (norm === "完了" && cur !== "完了") {
    const myDbId = progressCurrentUserDbId();
    const creatorId = found?.agenda?.createdByUserId;
    if (creatorId != null && myDbId != null && Number(creatorId) !== myDbId) {
      showToast("「完了」への変更は課題を作成した本人のみ行えます");
      return;
    }
  }
  void (async () => {
    const gate = await requestProgressAgendaStatusChange(found?.agenda, found?.challenge, norm);
    if (!gate.proceed) return;
    const snapshot = applyProgressAgendaStatusLocal(agendaId, norm);
    if (!snapshot) return;
    uiState.progressSelectedAgendaId = String(agendaId);
    const route = currentRoute();
    const issueParam = String(route.searchParams?.get("issue") || "").trim();
    const onIssueDetail = issueParam === String(agendaId);
    // 楽観更新（カードを即移動）。確定はサーバー再取得で行う。
    if (!onIssueDetail) patchProgressKanbanMoveCard(agendaId, norm);
    progressClearOwnWatchUnreadLocal(agendaId);
    const r = await commitProgressAgendaStatusChange(agendaId, norm, { rejectionReason: gate.rejectionReason });
    if (!r.ok) {
      revertProgressAgendaStatusLocal(agendaId, snapshot);
      if (onIssueDetail) {
        if (!refreshProgressIssueDetailAfterAttrs(agendaId)) render();
      } else {
        patchProgressKanbanMoveCard(agendaId, snapshot.status);
      }
      showToastError(r.error || "移動に失敗しました");
      return;
    }
    const confirmedStatus = r.savedStatus || norm;
    if (confirmedStatus === "完了") showToast("状態を「完了」にしました（対応終了日を記録しました）");
    else showToast(`「${confirmedStatus}」に移動しました`);
  })();
});

// ===== Web Push（ToDo通知のプッシュ受信）=====
function fpPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** VAPID 公開鍵（base64url）を Uint8Array に変換 */
function fpUrlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** iPhone/iPad の Safari で「ホーム画面に追加」せずに開いているか（この状態では Web Push が使えない） */
function fpIsIosBrowserNotStandalone() {
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!isIos) return false;
  const standalone = navigator.standalone === true || (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  return !standalone;
}

// この端末で最後にプッシュを ON にしたユーザー（dbId）。ログアウトで購読を外した後、
// 同じ人が再ログインしたときだけ自動で購読し直すために使う（別の人の端末に勝手に購読を作らない）
const FP_PUSH_OWNER_KEY = "fp_push_owner";
function fpPushOwnerGet() {
  try {
    return localStorage.getItem(FP_PUSH_OWNER_KEY) || "";
  } catch {
    return "";
  }
}
function fpPushOwnerSet(dbId) {
  try {
    if (dbId) localStorage.setItem(FP_PUSH_OWNER_KEY, String(dbId));
    else localStorage.removeItem(FP_PUSH_OWNER_KEY);
  } catch {
    /* noop */
  }
}

/** サーバから VAPID 公開鍵を取得（未設定・失敗なら ""） */
async function fpFetchPushPublicKey() {
  const r = await apiFetch("/api/push-subscribe");
  return (r.ok && r.data && r.data.publicKey) || "";
}

/** この端末の購読を作成（既存があれば再利用）してサーバへ登録。成功で true。
 *  サーバが 409（＝この endpoint は別ユーザーの購読）を返したら、前の人の購読を捨てる。
 *  recreate=true なら続けて自分用に作り直す（本人が ON にした操作・本人の端末での復元時のみ） */
async function fpRegisterTodoPushSubscription(publicKey, { recreate = true } = {}) {
  const reg = await navigator.serviceWorker.ready;
  for (let attempt = 0; attempt < (recreate ? 2 : 1); attempt++) {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: fpUrlBase64ToUint8Array(publicKey)
      });
    }
    const r = await apiFetch("/api/push-subscribe", {
      method: "POST",
      body: { endpoint: sub.endpoint, keys: sub.toJSON().keys }
    });
    if (r.ok) {
      fpPushOwnerSet(currentUser.dbId);
      return true;
    }
    if (r.status !== 409) return false;
    await sub.unsubscribe().catch(() => {});
  }
  return false;
}

/** プッシュ購読を有効化。成功で true、失敗/非対応で false（理由はトースト表示） */
async function enableTodoPush() {
  if (!fpPushSupported()) {
    showToastError(
      fpIsIosBrowserNotStandalone()
        ? "iPhone/iPad では、共有メニューから「ホーム画面に追加」したアプリで開くとプッシュ通知を使えます"
        : "この端末はプッシュ通知に対応していません"
    );
    return false;
  }
  const publicKey = await fpFetchPushPublicKey();
  if (!publicKey) {
    showToastError("プッシュ通知が未設定です（サーバー側のVAPID鍵が必要です）");
    return false;
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    showToastError("通知が許可されませんでした");
    return false;
  }
  if (!(await fpRegisterTodoPushSubscription(publicKey))) {
    showToastError("購読の登録に失敗しました");
    return false;
  }
  return true;
}

/** この端末のプッシュ購読を解除する。
 *  token: ログアウト時は authToken を消す前の値を渡す（サーバの購読行は本人の分しか消せない） */
async function disableTodoPush(token = authToken) {
  try {
    if (fpPushSupported()) {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        if (token) {
          await fetch("/api/push-subscribe", {
            method: "DELETE",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ endpoint: sub.endpoint })
          }).catch(() => {});
        }
        await sub.unsubscribe().catch(() => {});
      }
    }
  } catch (e) {
    /* noop */
  }
  return true;
}

/** ログイン完了後に呼ぶ: この端末の購読をログイン者に合わせる。
 *  - 本人がこの端末で ON にしていた（owner 一致）か、owner 記録前からの購読 → 購読し直してサーバへ登録
 *    （ログアウトで外した購読の復元／pushsubscriptionchange で endpoint が変わった分の再登録を兼ねる）
 *  - それ以外の購読（前に使っていた別の人の分など）→ 解除する */
async function syncTodoPushSubscriptionAfterLogin() {
  if (!authToken || !fpPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    const owner = fpPushOwnerGet();
    const me = String(currentUser.dbId || "");
    const wantMine =
      currentUser.notifyPush === true &&
      Notification.permission === "granted" &&
      (owner === me || (!owner && Boolean(sub)));
    if (!wantMine) {
      if (sub) await disableTodoPush();
      return;
    }
    const publicKey = await fpFetchPushPublicKey();
    // owner 記録前からの購読は、サーバ上で本人の行だった場合だけ引き継ぐ（別人の行なら 409 → 解除のみ）
    if (publicKey) await fpRegisterTodoPushSubscription(publicKey, { recreate: owner === me });
  } catch (e) {
    /* noop: 通知の復元に失敗しても画面操作は続けられる */
  }
}

/** トグルから呼ばれる: 購読の有効/無効化 → サーバの notify_push を更新。失敗時はトグルを戻す */
async function setTodoPushEnabled(enabled, inputEl) {
  if (!authToken) return;
  let ok = false;
  try {
    ok = enabled ? await enableTodoPush() : await disableTodoPush();
  } catch (e) {
    ok = false;
  }
  if (!ok) {
    if (inputEl) inputEl.checked = !enabled;
    return;
  }
  if (!enabled) fpPushOwnerSet("");
  currentUser.notifyPush = enabled;
  fetch("/api/me", {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ notifyPush: enabled })
  })
    .then((res) => {
      if (!res.ok) throw new Error();
      showToast(`プッシュ通知を${enabled ? "ON" : "OFF"}にしました`);
    })
    .catch(() => {
      // 保存失敗時は購読フラグ・トグルを元に戻す（メール/Slack と同じ非対称解消）
      currentUser.notifyPush = !enabled;
      if (inputEl) inputEl.checked = !enabled;
      showToastError("通知設定の保存に失敗しました");
    });
}

let pendingVersionReload = false;

// 開きっぱなしのタブ/PWA でも新しい sw.js を拾うための更新確認（ブラウザ任せだと起動時やナビゲーション時しか確認しない）
const SW_UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const SW_UPDATE_CHECK_MIN_GAP_MS = 5 * 60 * 1000;

function watchServiceWorkerUpdates(reg) {
  let lastCheckAt = Date.now();
  const checkForUpdate = () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastCheckAt < SW_UPDATE_CHECK_MIN_GAP_MS) return;
    lastCheckAt = Date.now();
    reg.update().catch(() => {});
  };
  document.addEventListener("visibilitychange", checkForUpdate);
  setInterval(checkForUpdate, SW_UPDATE_CHECK_INTERVAL_MS);
}

function registerOnBoardingServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then(watchServiceWorkerUpdates)
      .catch(() => {});
  });
  // SW が更新（skipWaiting → clients.claim）されたら自動でページをリロードし
  // 古い app.js がメモリに残り続けるのを防ぐ。
  // ただし初回インストール（強制リロード含む・それまで controller が無い）でも clients.claim で
  // controllerchange が発火する。これは更新ではないので（ログイン中にリロードされないよう）見送る
  let hadServiceWorkerController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadServiceWorkerController) {
      hadServiceWorkerController = true;
      return;
    }
    reloadForNewVersionWhenIdle();
  });
  // 入力中でリロードを見送った分は、画面を移った時点（＝書きかけが無い時点）で読み込み直す
  window.addEventListener("hashchange", () => {
    if (pendingVersionReload) reloadForNewVersionWhenIdle();
  });
}

/** 書きかけの入力を巻き込むリロードかどうか（モーダル表示中・入力欄にフォーカス中） */
function isEditingSomethingNow() {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  return Boolean(document.querySelector('[role="dialog"], .composer-backdrop'));
}

/** 新しい配信物を取り込むためのリロード。入力中は保留し、案内だけ出す */
function reloadForNewVersionWhenIdle() {
  if (isEditingSomethingNow()) {
    if (!pendingVersionReload) showToast("新しいバージョンがあります。保存したらページを再読み込みしてください");
    pendingVersionReload = true;
    return;
  }
  window.location.reload();
}

// PWA インストールプロンプト
let pwaInstallPrompt = null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  pwaInstallPrompt = e;
  updatePwaInstallButton();
});
window.addEventListener("appinstalled", () => {
  pwaInstallPrompt = null;
  updatePwaInstallButton();
});
function updatePwaInstallButton() {
  const btn = document.getElementById("fpPwaInstallBtn");
  if (!btn) return;
  // iOS非対応のため常に非表示
  btn.hidden = true;
}
function triggerPwaInstall() {
  if (!pwaInstallPrompt) return;
  pwaInstallPrompt.prompt();
  pwaInstallPrompt.userChoice.then(() => {
    pwaInstallPrompt = null;
    updatePwaInstallButton();
  });
}

// 原価管理一覧のメモ: 誤入力防止のため通常時は読み取り専用。ダブルクリックで編集モードへ
document.addEventListener("dblclick", (e) => {
  const ta = e.target;
  if (!(ta instanceof HTMLTextAreaElement) || !ta.dataset.costInlineNote) return;
  if (!ta.readOnly) return;
  ta.readOnly = false;
  ta.focus();
  ta.select();
});

// 原価管理一覧のメモをインラインで編集・保存
document.addEventListener("focusout", (e) => {
  const ta = e.target;
  if (!(ta instanceof HTMLTextAreaElement) || !ta.dataset.costInlineNote) return;
  ta.readOnly = true;
  const newVal = ta.value.trim();
  const orig = String(ta.dataset.costInlineNoteOrig || "").trim();
  if (newVal === orig) return;
  const noteKey = ta.dataset.costInlineNote;
  const colon = noteKey.lastIndexOf(":");
  const costIdUi = colon >= 0 ? noteKey.slice(colon + 1) : noteKey;
  const pl = findCostPlacementByUiId(costIdUi);
  if (!pl) return;
  const cost = pl.cost;
  const noteToSave = isAdjustmentCostRow(cost)
    ? ensureAdjustmentNote(newVal, true)
    : isSalesExpenseCostRow(cost)
      ? ensureSalesExpenseNote(newVal, true)
      : newVal;
  ta.dataset.costInlineNoteOrig = newVal;
  cost.note = noteToSave;
  void persistCostToServerUpdate(costIdUi, {
    category: cost.category,
    vendor: cost.vendor,
    amount: Number(cost.amount || 0),
    consumptionTax: costConsumptionTaxYen(cost),
    targetMonth: cost.targetMonth,
    payStatus: cost.payStatus || "",
    dueDate: cost.dueDate || "",
    note: noteToSave
  }, "keep").then(() => {
    showToast("メモを更新しました");
  }).catch(() => {
    showToastError("メモの更新に失敗しました");
  });
});

// 原価フォーム: 源泉所得税の自動計算（税抜金額から標準ルールで算出して欄に入れる）
document.addEventListener("click", (e) => {
  const el = e.target instanceof Element ? e.target.closest("[data-cost-withholding-calc]") : null;
  if (!(el instanceof HTMLElement) || !uiState.costForm) return;
  uiState.costForm.withholdingTax = calcWithholdingTaxYen(uiState.costForm.amount);
  render();
});

// 原価登録一覧: ページ送り・表示件数（上限クランプは描画側 costMgmtTableState が行う）
document.addEventListener("click", (e) => {
  const el = e.target instanceof Element ? e.target.closest("[data-cost-mgmt-page]") : null;
  if (!(el instanceof HTMLElement) || currentRoute().page !== "cost-management") return;
  if (!uiState.costMgmtTable) uiState.costMgmtTable = { page: 1, perPage: 50 };
  const t = uiState.costMgmtTable;
  const dir = el.getAttribute("data-cost-mgmt-page");
  if (dir === "prev") t.page = Math.max(1, (Number(t.page) || 1) - 1);
  else if (dir === "next") t.page = (Number(t.page) || 1) + 1;
  render();
});
document.addEventListener("change", (e) => {
  const el = e.target;
  if (!(el instanceof HTMLSelectElement) || !el.hasAttribute("data-cost-mgmt-per-page")) return;
  if (currentRoute().page !== "cost-management") return;
  if (!uiState.costMgmtTable) uiState.costMgmtTable = { page: 1, perPage: 50 };
  uiState.costMgmtTable.perPage = Number(el.value) || 50;
  uiState.costMgmtTable.page = 1;
  render();
});

// 原価管理テーブル: 列幅リサイズ
document.addEventListener("mousedown", (e) => {
  const handle = e.target instanceof Element ? e.target.closest("[data-cost-col-resize]") : null;
  if (!(handle instanceof HTMLElement)) return;
  const colName = handle.dataset.costColResize;
  const th = handle.closest("th");
  if (!th) return;
  e.preventDefault();
  const startX = e.clientX;
  const startWidth = th.getBoundingClientRect().width;
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";

  // 最小幅（見出し文字の有無）と、折り返せない中身（候補ボタン・🔒付きNo等）が収まる幅より狭くしない（15z-table-prefs.js）
  const col = th.closest("table")?.querySelector(`col[data-cost-col="${colName}"]`);
  let newWidth = startWidth;
  const onMove = (ev) => {
    const minW = colName === "name" ? COST_MGMT_NAME_MIN : 0;
    if (col) newWidth = fpTableClampOwnColWidth(th, col, Math.max(minW, startWidth + ev.clientX - startX));
  };

  const onUp = () => {
    if (!uiState.costMgmtColWidths) uiState.costMgmtColWidths = {};
    uiState.costMgmtColWidths[colName] = newWidth;
    // ログインユーザーごとにサーバーへ保存（15z-table-prefs.js）
    fpTablePrefsSaveWidths("cost-mgmt", uiState.costMgmtColWidths);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
  };

  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
});

// 初回起動: ログインチェック
bindFpMobileNavChromeOnce();
registerOnBoardingServiceWorker();
bindMidnightJstLogoutWatch();
tryAutoLogin();

// ハートビート: 5分ごとに last_seen_at を更新し、全員の在席状態を受け取る。
// 送信内容と受信後の反映は app-src/15o-presence.js（在席まわりは1ファイルに寄せる）。
const FP_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
function fpSendHeartbeat(extraBody) {
  if (!authToken) return;
  // ログイン保持ONの場合はアクティビティごとに有効期限をローリング延長
  fpRollPersistedToken();
  // 最終操作時刻は「タブが開いている時刻」と別物。これを送らないと、PCを開いたまま
  // 席を立っても永久に稼働中のままになる（last_seen_at だけでは離席を検出できない）。
  const body = { lastInputAt: fpPresenceLastInputAt(), ...(extraBody || {}) };
  // 401時の再ログイン誘導は window.fetch 側で全API共通に処理する（app-src/01-state-data.js）
  fetch("/api/heartbeat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(body)
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (data && data.ok) fpApplyPresencePayload(data.presence, data.self);
    })
    .catch(() => {/* 通信エラー（オフライン等）はサイレント */});
}
// 初回は30秒後（ログイン直後に確実に一度送る）
setTimeout(fpSendHeartbeat, 30 * 1000);
setInterval(fpSendHeartbeat, FP_HEARTBEAT_INTERVAL_MS);
// ────────────────────────────────────────────────────────────────
// 制作・PJ進行 ToDo: 工程通過履歴（fp_progress_stage_history）まわり。
// サーバの recordStageHistoryTransition（api/progress-management.js）が
// 状況変更のたびに1行追加する構造化ログを、既存のチャット文言パース表示
// （progressAgendaStepDurationsMs 等・app-src/14）と併用するハイブリッド表示、
// および簡易「工程履歴」モーダルを担当する。
// このテーブル導入（2026-07-17）以前の課題は stageHistory が空のまま
// チャット文言パースにフォールバックする（過去分の遡及はしない）。
// ────────────────────────────────────────────────────────────────

/**
 * 各工程の合計滞留時間（営業時間換算ミリ秒）。工程ごとに、工程通過履歴（ag.stageHistory）に
 * 行があればそれを集計し（構造化ログ・複数回通過は各行を合算）、無ければ既存のチャット文言パース
 * （progressAgendaStepDurationsMs, app-src/14）の値にフォールバックする。
 * 「履歴が1件でもあれば以降の全工程を履歴だけで見る」方式にはしない: この機能のリリースを
 * またいで進行中だったToDoは、次の状況変更で「新しい工程の履歴行」が1件だけ増える一方、
 * それ以前に通過済みの工程は履歴行が無いまま（過去分の遡及はしない設計）なので、
 * 全面切り替えだと通過済み工程の表示が軒並み「—」に落ちてしまう。工程名ごとに履歴の有無を見て
 * 個別にフォールバックすることで、リリースをまたいだ課題でも既存工程の表示を保つ。
 * @returns {Map<string, number>}
 */
function progressAgendaStepDurationsHybrid(ag) {
  const rows = Array.isArray(ag?.stageHistory) ? ag.stageHistory : [];
  const out = progressAgendaStepDurationsMs(ag);
  if (!rows.length) return out;
  const now = Date.now();
  const historyStepNames = new Set(rows.map((row) => row.stepName));
  for (const stepName of historyStepNames) out.delete(stepName);
  for (const row of rows) {
    const startMs = new Date(row.enteredAt).getTime();
    const endMs = row.exitedAt ? new Date(row.exitedAt).getTime() : now;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) continue;
    out.set(row.stepName, (out.get(row.stepName) || 0) + businessElapsedMs(startMs, endMs));
  }
  return out;
}

/** 工程履歴から、指定工程を直近に通過した回の担当者を返す（行の担当は入場時点の記録。複数回通っていれば最新の回）。無ければ null。
 * 手動設定の ag.stepAssignees[stepName] はこれより優先される（呼び出し側で先にチェックする）。
 * 過去の通過記録なので「現在開いている工程」には使わないこと（差し戻しで戻った工程に古い担当が
 * 居座り、現在の課題担当が表示されなくなる。工程バーは mod === "current" でガードしている）。 */
function progressAgendaStepAssigneeFromHistory(ag, stepName) {
  const rows = Array.isArray(ag?.stageHistory) ? ag.stageHistory : [];
  let found = null;
  for (const row of rows) {
    if (row.stepName !== stepName || row.exitedAt == null) continue;
    found = row;
  }
  return found ? found.assigneeId : null;
}

/** 現在の工程が、直前の状況変更で「差し戻し」により入った工程かどうか。
 * stageHistory の最後の行（現在開いている行）の is_rejection を見る。差し戻し元の工程名
 * （1つ前に閉じた行）を fromStepName として付与して返す（バナー表示用）。無ければ null。 */
function progressAgendaCurrentStepIsRejected(ag) {
  const rows = Array.isArray(ag?.stageHistory) ? ag.stageHistory : [];
  if (!rows.length) return null;
  const last = rows[rows.length - 1];
  if (!last || last.exitedAt != null || !last.isRejection) return null;
  const prev = rows.length > 1 ? rows[rows.length - 2] : null;
  return { ...last, fromStepName: prev ? prev.stepName : "" };
}

// ── 工程履歴モーダル ─────────────────────────────────────────

function openProgressStageHistoryModal(agendaId) {
  const id = String(agendaId || "").trim();
  if (!id) return;
  uiState.progressStageHistoryAgendaId = id;
}

function closeProgressStageHistoryModal() {
  uiState.progressStageHistoryAgendaId = "";
}

function progressStageHistoryRowLabel(row) {
  const enter = row.enteredAt ? new Date(row.enteredAt) : null;
  const exit = row.exitedAt ? new Date(row.exitedAt) : null;
  const fmt = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "");
  return { enterLabel: fmt(enter), exitLabel: exit ? fmt(exit) : "進行中" };
}

function renderProgressStageHistoryModal() {
  const agendaId = uiState.progressStageHistoryAgendaId;
  if (!agendaId) return "";
  const ag = findProgressAgendaById(agendaId)?.agenda;
  if (!ag) return "";
  const rows = Array.isArray(ag.stageHistory) ? ag.stageHistory : [];
  const now = Date.now();
  const rowsHtml = rows.length
    ? rows
        .slice()
        .reverse()
        .map((row) => {
          const { enterLabel, exitLabel } = progressStageHistoryRowLabel(row);
          const startMs = new Date(row.enteredAt).getTime();
          const endMs = row.exitedAt ? new Date(row.exitedAt).getTime() : now;
          const durMs = businessElapsedMs(startMs, endMs);
          const assignee = row.assigneeId != null ? users.find((u) => u.dbId != null && Number(u.dbId) === Number(row.assigneeId)) : null;
          const actor = row.actorId != null ? users.find((u) => u.dbId != null && Number(u.dbId) === Number(row.actorId)) : null;
          const passLabel = row.passNo > 1 ? `（${row.passNo}回目）` : "";
          const rejectHtml = row.isRejection
            ? `<div class="pm-stage-history-reject">↩ 差し戻し${row.rejectionReason ? `：${escapeHtml(row.rejectionReason)}` : ""}</div>`
            : "";
          return `<li class="pm-stage-history-row">
            <div class="pm-stage-history-row-head">
              <span class="pm-stage-history-step">${escapeHtml(row.stepName)}${passLabel}</span>
              <span class="pm-stage-history-dur">${escapeHtml(formatProgressStepDurationMs(durMs))}</span>
            </div>
            <div class="pm-stage-history-row-meta">
              ${escapeHtml(enterLabel)} 〜 ${escapeHtml(exitLabel)}
              ${assignee ? `・担当 ${escapeHtml(assignee.name || "")}` : ""}
              ${actor ? `・操作 ${escapeHtml(actor.name || "")}` : ""}
            </div>
            ${rejectHtml}
          </li>`;
        })
        .join("")
    : `<li class="pm-stage-history-empty">この機能の導入前の課題のため、工程履歴の記録がありません。</li>`;
  return `
    <div class="cl-mini-modal-overlay open" data-pm-stage-history-close="1">
      <div class="cl-mini-modal" role="dialog" aria-modal="true" aria-labelledby="pm-stage-history-title">
        <div class="cl-mini-modal-head">
          <h4 id="pm-stage-history-title">工程履歴</h4>
          <button class="cl-modal-close" type="button" data-pm-stage-history-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <ul class="pm-stage-history-list">${rowsHtml}</ul>
        </div>
      </div>
    </div>`;
}

// ── 実働工数サマリ（ToDoに紐づいた分のみ。案件単位のみの記録は出てこない） ─────

/** 分を「2時間30分」のような日本語表記に整形（滞留時間の営業日換算とは別物・単純な実時間） */
function formatWorkLogMinutesLabel(totalMinutes) {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h > 0 && mm > 0) return `${h}時間${mm}分`;
  if (h > 0) return `${h}時間`;
  return `${mm}分`;
}

/** ToDo詳細の実働工数サマリ行（合計＋役割別内訳を一言＋詳細モーダルへのリンク）。
 * 工程滞留時間とは別データなので、ここに滞留時間は混ぜない。 */
function progressAgendaWorkLogSummaryHtml(ag) {
  const rows = Array.isArray(ag?.workLog) ? ag.workLog : [];
  const totalMinutes = rows.reduce((sum, r) => sum + (Number(r.minutes) || 0), 0);
  if (!rows.length || totalMinutes <= 0) return "";
  const byRole = new Map();
  for (const r of rows) {
    const key = String(r.role || "").trim() || "未分類";
    byRole.set(key, (byRole.get(key) || 0) + (Number(r.minutes) || 0));
  }
  const breakdown = [...byRole.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([role, mins]) => `${escapeHtml(role)}${formatWorkLogMinutesLabel(mins)}`)
    .join("・");
  const aidAttr = escapeHtmlAttr(String(ag?.id ?? ""));
  return `<div class="pm-worklog-line">
      <span class="pm-worklog-total">実働工数 合計 ${escapeHtml(formatWorkLogMinutesLabel(totalMinutes))}</span>
      <span class="pm-worklog-breakdown">${breakdown}</span>
      <button type="button" class="pm-worklog-link" data-pm-worklog-detail-open="${aidAttr}">詳細を見る ›</button>
    </div>`;
}

function openProgressWorkLogDetailModal(agendaId) {
  const id = String(agendaId || "").trim();
  if (!id) return;
  uiState.progressWorkLogDetailAgendaId = id;
}

function closeProgressWorkLogDetailModal() {
  uiState.progressWorkLogDetailAgendaId = "";
}

function renderProgressWorkLogDetailModal() {
  const agendaId = uiState.progressWorkLogDetailAgendaId;
  if (!agendaId) return "";
  const ag = findProgressAgendaById(agendaId)?.agenda;
  if (!ag) return "";
  const rows = Array.isArray(ag.workLog) ? ag.workLog : [];
  const rowsHtml = rows.length
    ? rows
        .slice()
        .reverse()
        .map((r) => {
          const dateLabel = r.workDate ? r.workDate.replace(/^\d{4}-/, "").replace("-", "/") : "";
          return `<li class="pm-worklog-detail-row">
            <span class="pm-worklog-detail-date">${escapeHtml(dateLabel)}</span>
            <span class="pm-worklog-detail-name">${escapeHtml(r.userName || "（不明）")}</span>
            <span class="pm-worklog-detail-role">${escapeHtml(r.role || "未分類")}</span>
            <span class="pm-worklog-detail-min">${escapeHtml(formatWorkLogMinutesLabel(r.minutes))}</span>
          </li>`;
        })
        .join("")
    : `<li class="pm-stage-history-empty">実働工数の記録がありません。</li>`;
  return `
    <div class="cl-mini-modal-overlay open" data-pm-worklog-detail-close="1">
      <div class="cl-mini-modal" role="dialog" aria-modal="true" aria-labelledby="pm-worklog-detail-title">
        <div class="cl-mini-modal-head">
          <h4 id="pm-worklog-detail-title">実働工数の明細</h4>
          <button class="cl-modal-close" type="button" data-pm-worklog-detail-close="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <ul class="pm-worklog-detail-list">${rowsHtml}</ul>
        </div>
      </div>
    </div>`;
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;

  const wlOpenEl = target.closest("[data-pm-worklog-detail-open]");
  if (wlOpenEl instanceof HTMLElement) {
    openProgressWorkLogDetailModal(wlOpenEl.dataset.pmWorklogDetailOpen);
    render();
    return;
  }
  const wlCloseEl = target.closest("[data-pm-worklog-detail-close]");
  if (wlCloseEl instanceof HTMLElement) {
    const closeByOverlay = wlCloseEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return;
    closeProgressWorkLogDetailModal();
    render();
    return;
  }
});

// ── 差し戻し（工程順で後戻り）検知 + 理由入力ゲート ─────────────────
// 既存の状況ドロップダウン／SPシート／かんばんドラッグはそのまま。呼び出し側は
// commitProgressAgendaStatusChange の直前でこのゲートを await し、proceed=false なら中断するだけ。

let _progressRejectionPromptResolve = null;

/** 状況変更が工程順で「後戻り」かどうか。保留・対応不要など工程順（テーマ設定の状態リスト）に
 * 含まれない状態が絡む変更は順序を持たないため対象外（差し戻し扱いにしない）。
 * 理由入力ゲートは「制作・PJ進行」（genre=制作）プロジェクト限定（サーバ側
 * evaluateAgendaStatusTransition と対）。 */
function progressStatusChangeIsRejection(ch, fromStatus, toStatus) {
  if (!ch) return false;
  if (!isSeisakuProject(ch)) return false;
  const steps = progressChallengeStatuses(ch);
  const oldIdx = steps.indexOf(String(fromStatus || "").trim());
  const newIdx = steps.indexOf(String(toStatus || "").trim());
  if (oldIdx < 0 || newIdx < 0) return false;
  return newIdx < oldIdx;
}

/**
 * 状況変更の実行前ゲート。差し戻しと判定した場合のみ理由入力必須の小モーダルを表示し、
 * 確定/キャンセルされるまで待つ。前進・非順序の変更はそのまま即通す。
 * @returns {Promise<{proceed:boolean, rejectionReason:string}>}
 */
function requestProgressAgendaStatusChange(ag, ch, toStatus) {
  const fromStatus = String(ag?.status || "").trim();
  if (!progressStatusChangeIsRejection(ch, fromStatus, toStatus)) {
    return Promise.resolve({ proceed: true, rejectionReason: "" });
  }
  return new Promise((resolve) => {
    _progressRejectionPromptResolve = resolve;
    uiState.progressRejectionPrompt = {
      agendaId: String(ag?.id ?? ""),
      fromStatus,
      toStatus: String(toStatus || "").trim(),
      reason: ""
    };
    render();
  });
}

function resolveProgressRejectionPrompt(result) {
  const resolveFn = _progressRejectionPromptResolve;
  _progressRejectionPromptResolve = null;
  uiState.progressRejectionPrompt = null;
  if (resolveFn) resolveFn(result);
}

function renderProgressRejectionPromptModal() {
  const d = uiState.progressRejectionPrompt;
  if (!d) return "";
  return `
    <div class="cl-mini-modal-overlay open" data-pm-rejection-cancel="1">
      <div class="cl-mini-modal" role="dialog" aria-modal="true" aria-labelledby="pm-rejection-title">
        <div class="cl-mini-modal-head">
          <h4 id="pm-rejection-title">差し戻し理由</h4>
          <button class="cl-modal-close" type="button" data-pm-rejection-cancel="1" aria-label="閉じる">✕</button>
        </div>
        <div class="cl-mini-modal-body">
          <p class="pm-rejection-prompt-desc">「${escapeHtml(d.fromStatus)}」から「${escapeHtml(d.toStatus)}」への変更は工程の後戻りです。差し戻す理由を入力してください。</p>
          <textarea class="pj-input pm-rejection-reason-ta" data-pm-rejection-reason-input rows="4" placeholder="例：配信内容の修正が必要" autofocus>${escapeHtml(d.reason || "")}</textarea>
        </div>
        <div class="cl-mini-modal-foot">
          <button type="button" class="cl-btn cl-btn-outline" data-pm-rejection-cancel="1">キャンセル</button>
          <button type="button" class="cl-btn cl-btn-primary" data-pm-rejection-confirm="1">差し戻す</button>
        </div>
      </div>
    </div>`;
}

// ── イベント委譲（ToDo管理ページ限定） ───────────────────────────────
document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;

  const openEl = target.closest("[data-pm-stage-history-open]");
  if (openEl instanceof HTMLElement) {
    openProgressStageHistoryModal(openEl.dataset.pmStageHistoryOpen);
    render();
    return;
  }
  const closeEl = target.closest("[data-pm-stage-history-close]");
  if (closeEl instanceof HTMLElement) {
    const closeByOverlay = closeEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return;
    closeProgressStageHistoryModal();
    render();
    return;
  }
  // 「差し戻す」ボタンの判定を先に行う。data-pm-rejection-cancel はオーバーレイ全体（モーダルの
  // 祖先要素）にも付いているため、closest() はモーダル内のどこをクリックしても祖先のオーバーレイに
  // ヒットしてしまう。cancel 判定を先に置くと「差し戻す」ボタン自身の click が常にここで早期returnし、
  // 下の rejConfirmEl 分岐に到達できず「差し戻す」が反応しないバグになる（実機で発見）。
  const rejConfirmEl = target.closest("[data-pm-rejection-confirm]");
  if (rejConfirmEl instanceof HTMLElement) {
    const reason = String(uiState.progressRejectionPrompt?.reason || "").trim();
    if (!reason) {
      showToastDirect("差し戻す理由を入力してください");
      return;
    }
    resolveProgressRejectionPrompt({ proceed: true, rejectionReason: reason });
    render();
    return;
  }
  const rejCancelEl = target.closest("[data-pm-rejection-cancel]");
  if (rejCancelEl instanceof HTMLElement) {
    const closeByOverlay = rejCancelEl.classList.contains("cl-mini-modal-overlay");
    const clickedInsideModal = !!target.closest(".cl-mini-modal");
    if (closeByOverlay && clickedInsideModal) return;
    resolveProgressRejectionPrompt({ proceed: false, rejectionReason: "" });
    render();
    return;
  }
});

document.addEventListener("input", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;
  if (t.dataset.pmRejectionReasonInput === undefined) return;
  if (!uiState.progressRejectionPrompt) return;
  uiState.progressRejectionPrompt.reason = t.value;
});
// ────────────────────────────────────────────────────────────────
// 全ToDo共通: Googleカレンダー予定作成ボタン（課題詳細ヘッダーの優先度の隣に表示）。
// カレンダー側の取込・振り分け自体は既存の稼働状況ページ（app-src/15c-workload.js）が
// 担う（そちらで未分類予定は自動表示される。ToDo管理ボード側に別バナーは重ねて出さない）。
// ────────────────────────────────────────────────────────────────

/** ToDo詳細から「このToDo用の予定を作成」する Google カレンダーの作成インテントURL。
 * OAuth・API呼び出し不要（画面遷移するだけ）。予定名の先頭にIDタグを入れておくことで、
 * 後で本人がカレンダー上でこの予定を確認取込した際にIDタグ経由で自動的に
 * このToDo・案件へ紐づく（api/_calendarCore.js の classifyEvent）。
 * 予定名の形式: `[PJ:案件ID/TODO:ToDo ID] タイトル`。テーマに関連案件が無ければ
 * `[TODO:ToDo ID] タイトル`（社内工数テーマ・未設定テーマ）。この場合は取込時点で
 * テーマの関連案件・工数区分を都度解決する（api/_workLogSchema.js の resolveAgendaWorkTarget）
 * ので、後からテーマ設定を変えても追従する。
 * クライアント名などの文字は足さない（予定名が長くなりカレンダーが見づらくなるため。
 * どのクライアントかはIDタグから工数管理側で逆引きして表示する）。 */
function progressCalendarCreateUrl(ag, ch) {
  const title = String(ag?.title || "").trim();
  const aid = ag?.id != null ? Number(ag.id) : null;
  const pid = ch?.projectId != null && Number(ch.projectId) >= 1 ? Number(ch.projectId) : null;
  const idPart = aid != null ? (pid != null ? `[PJ:${pid}/TODO:${aid}]` : `[TODO:${aid}]`) : "";
  const text = encodeURIComponent(`${idPart} ${title}`.trim());
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}`;
}

function progressCalendarCreateButtonHtml(ag, ch) {
  if (!ag?.id) return "";
  const url = progressCalendarCreateUrl(ag, ch);
  return `<a class="pm-cal-create-btn" href="${escapeHtmlAttr(url)}" target="_blank" rel="noopener noreferrer" title="この${PM_WORD.issue}のタグ入りでGoogleカレンダーの予定作成画面を開きます">📅 カレンダーへ追加</a>`;
}

document.addEventListener("click", (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentRoute().page !== "progress-management") return;

  const memoToggleEl = target.closest("[data-pm-memo-toggle]");
  if (memoToggleEl instanceof HTMLElement) {
    const aid = memoToggleEl.dataset.pmMemoToggle || "";
    if (!aid) return;
    const expanded = progressMemoExpanded(aid);
    const next = !expanded;
    try {
      localStorage.setItem(`pm_memo_expanded_${aid}`, next ? "1" : "0");
      // 開いた瞬間に「閲覧済み」として更新ありバッジを消す
      if (next) localStorage.setItem(`pm_memo_last_viewed_${aid}`, String(Date.now()));
    } catch {
      /* localStorage不可（プライベートモード等）は開閉状態を記憶しないだけで動作は継続 */
    }
    render();
    return;
  }
});
